// src/components/QuotationApp.js
import { workspaceBootstrapRole } from "../utils/accountMemberPermissions";
import { repeatCutHeight, horizontalPatternRules, cuttingDropMetres, applyRepeatEditToRows, patternCuttingInstructions } from "../utils/patternRepeat";
import { createQuoteRepositoryPager, quoteRepositoryKey, resolveQuoteListingDate } from "../utils/quoteRepository";
import FabricTextInput from "./FabricTextInput";
import { replaceGeneralQuoteItemImages } from "../utils/generalQuoteImageState";
import EmployeeActivityHistory from "./EmployeeActivityHistory";
import QuoteConstructionControls from "./QuoteConstructionControls";
import { DEFAULT_STITCHING, calculateCurtainTrackConfig, applyBendedTrackToCalcResult, normalizeCurtainRowLining, calculateCurtainRowLining, normalizeStitchingSettingForState } from "../utils/curtainFinancialRuntime";
import { getQuoteFabricIdentity, getQuoteConstructionKey, normalizeQuoteCalculationSettings, resolveQuoteStitchingConfig, captureQuoteCalculationSettings, preserveFrozenConstructionRow, getQuoteCuttingInputSignature, rebaseQuoteConstructionIdentities } from "../utils/quoteCalculationSettings";
import { buildSrDraftPatch, buildManualFabricDraftPatch, buildCatalogueInputDraftPatch, isAutomaticFabricRate, clearCatalogueSelection, hasManualFabricRate, hasManualCatalogueRate, buildCatalogueRatePatch, fabricGroupingKey, srDisplayLabel } from "../utils/fabricTextEditing";
import { buildSrPricingCommitPatch, hasUnresolvedLiveFabricPricing } from "../utils/fabricSrEditing";
import { resolveQuotePdfComponents, addDirectFabricPdfSummaries } from "../utils/quotePdfComponents";
import { buildCurtainRowFinancialAliasesForSave } from "../utils/curtainRowFinancialAliases";
import { consumeEmptyEditorRecoveryCheck, resolveGeneralDraftEditingIdentity } from "../utils/draftRecoverySession";
import { getOptimizerFabricOptions, optimizerFabricIdentity, selectOptimizerSource, buildOptimizerDemandInput } from "../utils/rollOptimizerInput";
import { optimizeWithRollAlignment, rollAlignmentSummaryLines } from "../utils/rollAlignmentAllowance";
import { QUOTE_READER_VERSION, QUOTE_UPDATE_REQUIRED, QUOTE_RECOVERY_PAUSED, QUOTE_MAINTENANCE_PAUSED, assertCompatibleQuote, readCompatibleQuoteDocument, requiresPatternWriter, quoteFailureNotice, createQuotationDocumentReader, canonicalQuotationIdentity, assertLegacyRepairSupported, assertQuoteDocumentIdentity } from "../utils/quoteReaderCompatibility";
import { num, round2, isCurtainAccessoryRowStatic, CURTAIN_GST_RATE_OPTIONS, CURTAIN_GST_MODE_FLAT, CURTAIN_GST_MODE_FABRIC_5_OTHER_18, normalizeCurtainGstRate, normalizeCurtainGstMode, getCurtainGstModeLabel, resolveCurtainAccessoryRowGstRate, resolveCurtainSavedRowGstRate, calculateCurtainQuoteTotals } from "../utils/curtainQuoteTotals";
import { resolveTaskTarget, isUnavailableTaskTarget } from "./crmTaskTargets";
import { buildDealOwnerAssignment } from "./crmAssignmentUtils";
import { buildCrmMemberRoster } from "./crmMemberRoster";
import QuotationAttribution from "./QuotationAttribution";
import { isCrmRecordUnavailable } from "./crmRecordLifecycle";
import { buildCrmLeadListQueries } from "../utils/listQuery";
import { buildLegacyQuoteIndexPageQuery, mergeQuoteIndexPages } from "./quoteIndexPagination";
import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import "./MobileResponsive.css";
import {
  BellRing,
  CalendarDays,
  Clock3,
  Save,
  Scissors,
  Download,
  Trash2,
  Plus,
  Copy,
  RotateCcw,
  Check,
  Menu,
  ChevronDown,
  ChevronUp,
  Columns3,
  GripVertical,
  LayoutGrid,
  Mail,
  ExternalLink,
  FileArchive,
  FileImage,
  FileText,
  Link2,
  Phone,
  Paperclip,
  Settings2,
  Users,
  UserCog,
  Unlink,
  UploadCloud,
  X,
  CheckCircle2,
  Smartphone,
} from "lucide-react";
import Sidebar from "./Sidebar";
import Button from "./ui/Button";
import GeneralQuotationEditor from "./GeneralQuotationEditor";
import QrCodeSvg from "./QrCodeSvg";
import { applyProductToRow, resolveDefaultRateForProduct, EDITABLE_PRODUCT_RATE_FIELDS } from "../utils/productRowTransitions";
import { isFirestoreSentinel, isLiveFirestoreSentinel, stripFirestoreSentinelsForState, normalizeIndexDateForWrite, toSortableMillis as safeDateMillis } from "../utils/firestoreDates";
import { staffPasswordSetupError } from "../utils/staffPasswordSetup";
import CrmRepairConsole, { RepairDataBadge } from "./CrmRepairConsole";
import { confirmGuidedRepair, verifyRepairTargets, REPAIR_VERIFY_WARNING, buildActivityDateProposal, canRepairActivityDateRow, assertFreshActivityDateProposal, ACTIVITY_SORT_WARNING } from "./crmRepairConsoleUtils";
import { QUOTE_INDEX_REPAIR_MODES, buildQuoteIndexRepairPreview, repairValueText, quoteIndexRepairConfirmation } from "./quoteIndexRepairUtils";
import { readRepairTestMode, setRepairTestMode, canUseRepairFixtures, createRepairFixtureBatch } from "./crmRepairLockdownUtils";
import useViewport from "./useViewport";
import { useAccount } from "../context/AccountContext";
import {
  amountToWords,
  buildBusinessDisplayName,
  calcGeneralGrandTotal,
  calcGeneralSubtotal,
  calculateGeneralQuoteTotals,
  createGeneralQuoteImage,
  createClientId,
  createDefaultGeneralQuote,
  CURTAIN_QUOTE_TYPE,
  GENERAL_QUOTE_IMAGE_TTL_DAYS,
  getGeneralItemBaseAmount,
  getGeneralItemAmount,
  getGeneralItemTaxAmount,
  GENERAL_QUOTE_TYPE,
  normalizeGeneralDiscount,
  normalizeGeneralDiscountType,
  normalizeGeneralQuoteItemSpecifications,
  normalizeGeneralQuoteItemImages,
  normalizeGeneralQuote,
  extractFirebaseStorageFullPathFromUrl,
  hasFirebaseStorageDownloadToken,
  isFirebaseStorageRestUrl,
  isProtectedFirebaseStorageRestUrl,
  getTodayLocalDateString,
  syncGeneralQuoteItemPrimaryImageFields,
  resolveQuoteDateForDisplay,
  resolveQuoteDateForInput,
  todayISO,
} from "./generalQuotationUtils";
import {
  buildOwnerWorkspaceProfile,
  DEFAULT_STAFF_LIMIT,
  DEFAULT_TEAM_PERMISSIONS,
  OWNER_TEAM_PERMISSIONS,
  TEAM_PERMISSION_DEFINITIONS,
  countOccupiedStaffSlots,
  createTeamMemberDraft,
  findMatchingTeamMember,
  getTeamMemberLoginStatusLabel,
  getTeamMemberStatusLabel,
  hasFeatureAccess,
  isActiveTeamMember,
  normalizeStaffLimit,
  normalizeEmailValue,
  normalizePermissionSet,
  normalizeTeamMembers,
  normalizeWorkspaceProfile,
} from "../utils/teamAccess";
import {
  buildCrmContactFromQuote,
  cloneChecklistTree,
  CRM_REMINDER_REPEAT_OPTIONS,
  CRM_REMINDER_STATUS_ACTIVE,
  CRM_REMINDER_STATUS_DISABLED,
  CRM_REMINDER_STATUS_DONE,
  CRM_REMINDER_TYPE_INSTALLATION,
  CRM_REMINDER_TYPE_TASK_DUE,
  CRM_FOLLOW_UP_PRIORITY_HIGH,
  CRM_FOLLOW_UP_PRIORITY_MEDIUM,
  CRM_FOLLOW_UP_PRIORITY_OPTIONS,
  CRM_FOLLOW_UP_REPEAT_OPTIONS,
  CRM_FOLLOW_UP_STATUS_DONE,
  CRM_FOLLOW_UP_STATUS_PENDING,
  CRM_OPPORTUNITY_STATUS_LOST,
  CRM_OPPORTUNITY_STATUS_NURTURE,
  CRM_OPPORTUNITY_STATUS_OPEN,
  CRM_OPPORTUNITY_STATUS_WON,
  CRM_OPPORTUNITY_STAGES,
  TRACKING_STATUSES,
  TRACKING_ITEM_TYPES,
  CRM_TASK_PRIORITIES,
  CRM_TASK_STATUS_CANCELLED,
  CRM_TASK_STATUS_COMPLETED,
  CRM_TASK_STATUS_OPEN,
  ORDER_TASK_TEMPLATES,
  PROJECT_HANDOVER_CHECKLIST_KEYS,
  PROJECT_HANDOVER_CHECKLIST_LABELS,
  PROJECT_PAYMENT_STATUS,
  PROJECT_PAYMENT_STATUS_ADVANCE_PENDING,
  PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED,
  PROJECT_PAYMENT_STATUS_CANCELLED,
  PROJECT_PAYMENT_STATUS_FULLY_PAID,
  PROJECT_PAYMENT_STATUS_OVERDUE,
  PROJECT_PAYMENT_STATUS_PART_PAID,
  PROJECT_EXECUTION_CHECKLIST_GROUPS,
  PROJECT_EXECUTION_CHECKLIST_LABELS,
  PROJECT_INSTALLATION_STATUS_COMPLETED,
  PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED,
  PROJECT_INSTALLATION_STATUS_SCHEDULED,
  PROJECT_REVIEW_STATUS_COMPLETED,
  PROJECT_REVIEW_STATUS_PENDING,
  PROJECT_REVIEW_STATUS_SKIPPED,
  PROJECT_STAGE_HANDOVER_PENDING,
  PROJECT_STAGE_CANCELLED,
  PROJECT_STAGE_FINAL_PAYMENT_PENDING,
  PROJECT_STAGE_INSTALLATION_COMPLETED,
  PROJECT_STAGE_INSTALLATION_SCHEDULED,
  PROJECT_STAGE_MATERIAL_RECEIVED,
  PROJECT_STAGE_PRODUCTION_STITCHING,
  PROJECT_STAGE_PROJECT_CLOSED,
  PROJECT_STAGE_QC,
  PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED,
  PROJECT_STAGE_SNAG_CORRECTION,
  STANDARD_ORDER_TASK_TEMPLATE_KEY,
  buildActivityLogEntry,
  buildCrmTaskTitle,
  buildDailyTrackingTaskId,
  buildProjectTrackingSummary,
  buildTrackingItemDisplayTitle,
  buildProjectFromOpportunityAndQuote,
  buildProjectTitle,
  buildOpportunityDisplayTitle,
  calculateBalanceAmount,
  calculateProjectChecklistSummary,
  calculateProjectPaymentSummary,
  createDefaultProjectExecutionChecklist,
  createDefaultProjectHandoverChecklist,
  DEFAULT_CRM_SETTINGS,
  DEFAULT_CRM_REMINDER_TIME,
  DEFAULT_ORDER_PIPELINE,
  createChecklistChildDraft,
  createChecklistFromStage,
  createDealChecklistItem,
  createExecutionSpecItem,
  createExecutionTemplateFieldDraft,
  createExecutionTemplateDraft,
  createChecklistTemplateDraft,
  createStageDraft,
  CRM_CHECKLIST_MAX_DEPTH,
  CRM_PIPELINE_TYPES,
  DEFAULT_SALES_PIPELINE,
  DEFAULT_CRM_PIPELINES,
  createOrderPipelineStageDraft,
  createSalesPipelineStageDraft,
  deleteChecklistNodeById,
  getActiveOrderPipelineStages,
  getActiveSalesPipelineStages,
  getChecklistProgress,
  getCrmFollowUpRepeatMinutes,
  getPrimaryPendingCrmFollowUp,
  getCrmReminderRepeatMinutes,
  getCrmReminderTypeLabel,
  formatCrmDate,
  calculateNextTrackingCheckTime,
  formatTrackingInfo,
  calculateTrackingPendingQty,
  getNextActionStatus,
  getTrackingStatusLabel,
  getTrackingItemTypeLabel,
  isTrackingItemDelayed,
  isTrackingItemOpen,
  getOpportunityStageLabel,
  getPaymentStatusLabel,
  getPaymentModeLabel,
  getPaymentTypeLabel,
  getProjectInstallationStatusLabel,
  getProjectReviewStatusLabel,
  getProjectStageLabel,
  getOrderPipelineStage,
  getOrderPipelineStageLabel,
  getChecklistNodeProgress,
  getCrmPipeline,
  getCrmStage,
  getSalesPipelineStage,
  getChecklistSummary,
  getDefaultCrmStageId,
  getFollowUpSummary,
  getInstallationSummary,
  getQuoteTypeLabel,
  insertChecklistChildNode,
  moveChecklistNodeById,
  normalizeChecklistTree,
  normalizeCrmAttachments,
  normalizeCrmDate,
  normalizeCrmSettings,
  normalizeCrmFollowUps,
  normalizePaymentMode,
  normalizePaymentStatus,
  normalizePaymentType,
  normalizeOpportunityStage,
  normalizeOpportunityStatus,
  normalizeProjectInstallationStatus,
  normalizeProjectReviewSummary,
  normalizeProjectReviewStatus,
  normalizeProjectSnagSummary,
  normalizeProjectStage,
  mapOrderPipelineStageToLegacyProjectStage,
  normalizeCrmReminderConfigs,
  normalizeExecutionSpecs,
  normalizeExecutionSpecAttachments,
  normalizeExecutionTemplateDrafts,
  normalizeExecutionTemplateItemType,
  normalizeExecutionTemplates,
  preserveChecklistDraftTree,
  getStageFilterOptions,
  getStageSelectOptions,
  normalizeCrmPipelines,
  normalizeOrderPipeline,
  normalizeSalesPipeline,
  resolveProjectPipelineStage,
  normalizeTrackingStatus,
  normalizeTrackingItemType,
  PAYMENT_MODES,
  PAYMENT_TYPES,
  normalizeDealChecklist,
  toggleChecklistNodeById,
  updateChecklistNodeById,
  validateProjectHandoverInput,
} from "./crmUtils";


import { ref, getDownloadURL, deleteObject, getMetadata, getBlob } from "firebase/storage";
import {
  app as firebaseApp,
  auth,
  db,
  functions,
  storage,
} from "../firebase";
import {
  clearFirestoreReadBackoffState,
  getDocCached as getDocCachedRaw,
  getDocsCached as getDocsCachedRaw,
  invalidateFirestoreReadCache,
  invalidateFirestoreReadCacheByPrefix,
  isFirestoreResourceExhausted,
  recordFirestoreRead,
  trackedGetDoc,
  trackedGetDocs,
  trackedOnSnapshot,
} from "../utils/firestoreReadCache";
import {
  buildFabricCatalogueDisplayCode,
  buildFabricCatalogueRowPatch,
  buildSrPriceRuleDocId,
  buildSrPriceRulePayload,
  canChangeCataloguePriceUnit,
  getSrPriceRuleLabel,
  getSrPricingMatchKey,
  numericSrValue,
  normalizeCatalogueKey,
  normalizeFabricCatalogue,
  normalizeFabricWidthIn,
  normalizeSrNumber,
  normalizeSrPriceRule,
  parseFabricCatalogueInput,
  resolveFabricCatalogueRrp,
  sanitizeFabricForDisplayAgainstSettings,
  stripStaleFabricBrandPrefix,
  validateQuoteFabricAgainstSettings,
} from "../utils/fabricCatalogue";
import {
  buildCustomerSearchIndexRecord,
  buildCustomerSearchIndexId,
  searchCrmLeads as globalSearchCrmLeads,
  searchCustomerPeople as globalSearchCustomerPeople,
  searchCustomers as globalSearchCustomers,
  searchQuotations as globalSearchQuotations,
  searchTaskHistory as globalSearchTaskHistory,
} from "../utils/globalSearch";
import {
  archiveManagedStorageFile,
  formatStorageBytes,
  uploadManagedImage,
} from "../utils/managedImageUpload";
import { IS_DEV_UI_ENABLED, devError, devInfo, devLog, devTable, devWarn } from "../utils/devUi";
import {
  getChunkLoadDebugContext,
  handleChunkLoadErrorRecovery,
  isChunkLoadError,
  resetChunkLoadReloadGuard,
} from "../utils/chunkLoadRecovery";
import {
  DEFAULT_PRODUCT_CONFIGS,
  PRODUCT_CALCULATION_LABELS,
  PRODUCT_CALCULATION_TYPES,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
  PRODUCT_CONFIG_COLLECTION,
  CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
  buildDefaultProductConfigs,
  buildProductConfigSnapshot,
  calculateBlindSqft,
  calculateCurtainPieces,
  calculateRomanBlindPanels,
  calculateProductRow,
  getProductPricingRequirements,
  convertRepeatToInches,
  createProductConfigDraft,
  formatRepeatMeasurement,
  mergeProductConfigsWithDefaults,
  isAccessoryProductConfig,
  isAccessoryCalculationType,
  isBlindSqftCalculationProduct,
  normalizeRepeatMeasurement,
  normalizeProductConfig,
  normalizeBlindSqftRoundingMode,
  normalizeProductName,
  normalizeRepeatApplyMode,
  normalizeRepeatProductTypeScope,
  normalizeRepeatUnit,
  normalizeStitchingKey,
  REPEAT_APPLY_MODES,
  resolveBlindSqftRoundingMode,
  getRowDiscountScope,
  getRepeatProductType,
  isRepeatApplicableProduct,
  resolveProductRepeatSettings,
  resolveCurtainFullness,
  resolveFabricRulesForRow,
  resolveProductConfig,
  resolveProductMinSqft,
  resolveSelectedStitchingStyle,
  productSupportsLining,
  shouldUseLegacyProductCalculation,
  validateProductConfig,
} from "../utils/productConfigs";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signOut,
  updatePassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { loadOperationalSettingsSnapshot, clearOperationalSettingsCache } from '../utils/operationalSettings';
import QuotationHistoryButton from './QuotationHistoryButton';
import {
  collection as sdkCollection,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  doc,
  arrayUnion,
  query as sdkQuery,
  limit as firestoreLimit,
  where as firestoreWhere,
  orderBy as firestoreOrderBy,
  startAfter as firestoreStartAfter,
  documentId as firestoreDocumentId,
  serverTimestamp,
  Timestamp,
  waitForPendingWrites,
  increment,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import {
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import CountUp from "react-countup";

const SAVED_QUOTES_PAGE_SIZE = 25;
const SAVED_QUOTES_DELETED_READ_LIMIT = 25;
const CRM_LEADS_PAGE_SIZE = 20;
const CRM_DEALS_PAGE_SIZE = 20;
const CRM_LEADS_INITIAL_READ_LIMIT = 30;
const CUSTOMER_LEDGER_INITIAL_READ_LIMIT = 25;
const CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT = 25;
const CUSTOMER_LEDGER_SELECTED_CRM_LIMIT = 25;
const CUSTOMER_LEDGER_SELECTED_PROJECT_LIMIT = 20;
const CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT = 50;
const CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT_MESSAGE =
  "Only the first 50 active linked quotations were updated. Please run bulk update or continue updating remaining records.";
const GLOBAL_REMINDER_DUE_READ_LIMIT = 20;
const GLOBAL_REMINDER_LOOKAHEAD_MS = 10 * 60 * 1000;
const GLOBAL_REMINDER_CHECK_TTL_MS = 5 * 60 * 1000;
const ACCOUNT_SECURITY_SESSION_LIMIT = 20;
const ACCOUNT_SECURITY_ACTIVITY_LIMIT = 20;
const ACCOUNT_SECURITY_HEARTBEAT_MS = 5 * 60 * 1000;
const ACCOUNT_SECURITY_SESSION_PREFIX = "fabriquote:sessionId:";
const CRM_TASK_HISTORY_INITIAL_READ_LIMIT = 50;
const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 15 * 1000;
const DASHBOARD_PREVIEW_READ_LIMIT = 8;
const DASHBOARD_TOP_CLIENT_LIMIT = 10;
const DASHBOARD_INSTALLATION_MONTH_LIMIT = 25;
const DASHBOARD_MONTHLY_CHART_MONTHS = 6;
const FABRIC_STOCK_READ_LIMIT = 200;
const STOCK_HISTORY_READ_LIMIT = 100;
const CRM_OVERVIEW_PREVIEW_LIMIT = 5;
const CRM_PIPELINE_REVIEW_READ_LIMIT = 75;
const CRM_PIPELINE_REVIEW_TASK_READ_LIMIT = 75;
const CRM_PIPELINE_REVIEW_QUOTE_READ_LIMIT = 75;
const CRM_WEEKLY_TARGET_READ_LIMIT = 40;
const CRM_PROJECTS_READ_LIMIT = 100;
const CRM_PROJECT_RELATED_READ_LIMIT = 20;
const CRM_PROJECT_TRACKING_ITEMS_READ_LIMIT = 50;
const CRM_PROJECT_PAYMENTS_READ_LIMIT = 50;
const CRM_TRACKING_ATTENTION_READ_LIMIT = 20;
const CRM_PAYMENT_ATTENTION_READ_LIMIT = 20;
const CRM_DATA_REPAIR_PAGE_LIMIT = 25;
const CRM_DATA_REPAIR_TEST_CUSTOMER_ID = "repair_test_customer";
const CRM_DATA_REPAIR_TEST_QUOTE_1_ID = "000_TEST_REPAIR_001";
const CRM_DATA_REPAIR_TEST_QUOTE_2_ID = "000_TEST_REPAIR_002";
const CRM_DATA_REPAIR_TEST_ACTIVITY_ID = "000_TEST_REPAIR_ACTIVITY";
const CRM_DATA_REPAIR_TEST_DEAL_2_ID = "repair_test_deal_002";
const CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID = "repair_test_deal_activity";
const CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID = "repair_quote_index_customer";
const CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID = "000_TEST_QUOTE_INDEX_001";
const CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID = "repair_quote_index_deal_001";
const CRM_DATA_REPAIR_TEST_CUSTOMER = {
  customerId: CRM_DATA_REPAIR_TEST_CUSTOMER_ID,
  customerKey: CRM_DATA_REPAIR_TEST_CUSTOMER_ID,
  customerName: "Test Repair Customer",
  displayName: "Test Repair Customer",
  name: "Test Repair Customer",
  phone: "9999990001",
  customerPhone: "9999990001",
  whatsapp: "9999990001",
  customerWhatsapp: "9999990001",
  email: "test-repair@example.com",
  customerEmail: "test-repair@example.com",
  address: "Repair test data only",
  customerAddress: "Repair test data only",
  tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"],
  repair_test_data: true,
};
const CRM_ACTIVITY_REPAIR_COLLECTIONS = [
  "quote_index",
  "crm_leads",
  "opportunities",
  "projects",
  "customer_search_index",
];
const CRM_TRACKING_UNITS = ["meter", "sqft", "pcs", "roll", "box", "kg", "set", "other"];
const normalizeSalesStageIdentityValue = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || "new_lead";
};
const formatInAppBadgeCount = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count > 99 ? "99+" : String(Math.round(count));
};
const countNotificationItems = (items) => (Array.isArray(items) ? items.length : 0);
const countUniqueNotificationItems = (items = [], getKey = (item) => item?.id) => {
  if (!Array.isArray(items) || items.length === 0) return 0;
  const seen = new Set();
  let fallbackCount = 0;
  items.forEach((item, index) => {
    const key = String(getKey(item, index) || "").trim();
    if (!key) {
      fallbackCount += 1;
      return;
    }
    seen.add(key);
  });
  return seen.size + fallbackCount;
};
const LOCAL_DRAFT_PREFIX = "fabriquote:draft";
const LOCAL_DRAFT_VERSION = 1;
const LOCAL_DRAFT_INTERVAL_MS = 8000;
const SAVE_TRACE_ENV_ENABLED = process.env.REACT_APP_ENABLE_SAVE_TRACE === "true";
const SAVE_TRACE_COLLECTION = "debug_save_traces";
const SAVE_IN_PROGRESS_STORAGE_KEY = "FQ_SAVE_IN_PROGRESS";
const LAST_SAVE_TRACE_STORAGE_KEY = "FQ_LAST_SAVE_TRACE";
const SAVE_ATTEMPT_TRACE_STORAGE_KEY = "FQ_SAVE_ATTEMPT_TRACE";
const LATEST_VERIFIED_SAVED_QUOTE_STORAGE_KEY = "FQ_LATEST_VERIFIED_SAVED_QUOTE";
const SAVE_IN_PROGRESS_STALE_MS = 2 * 60 * 1000;
const isDevRuntime = IS_DEV_UI_ENABLED;
const preventQuoteActionDefault = (event) => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
};
const getSessionStorageJson = (key) => {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};
const setSessionStorageJson = (key, value) => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Save diagnostics must never affect the actual save flow.
  }
};
const removeSessionStorageItem = (key) => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Save diagnostics must never affect the actual save flow.
  }
};
const FABRIQUOTE_BUILD_INFO = Object.freeze({
  label: "staff-invite-backend-smtp-2026-10-06",
  version: process.env.REACT_APP_VERSION || process.env.npm_package_version || "1.0.0",
  buildTime: process.env.REACT_APP_BUILD_TIMESTAMP || "runtime",
  gitCommitOrManualLabel: "staff-invite-backend-smtp-2026-10-06",
  environment: process.env.NODE_ENV || "unknown",
});
const getPerformanceNavigationType = () => {
  if (typeof window === "undefined" || typeof performance === "undefined") return "";
  const navigationEntry = performance.getEntriesByType?.("navigation")?.[0];
  if (navigationEntry?.type) return navigationEntry.type;
  const legacyType = performance.navigation?.type;
  if (legacyType === 1) return "reload";
  if (legacyType === 2) return "back_forward";
  if (legacyType === 0) return "navigate";
  return "";
};
const getQuoteActionEventDebug = (event, eventGuard = {}) => {
  const currentTarget = event?.currentTarget || null;
  const target = event?.target || null;
  const activeElement =
    typeof document !== "undefined" ? document.activeElement || null : null;
  const form =
    currentTarget?.form ||
    target?.form ||
    currentTarget?.closest?.("form") ||
    target?.closest?.("form") ||
    activeElement?.form ||
    activeElement?.closest?.("form") ||
    null;
  return {
    receivedEvent: Boolean(eventGuard.receivedEvent ?? event),
    eventType: event?.type || "",
    defaultPreventedBefore: Boolean(eventGuard.defaultPreventedBefore),
    preventDefaultCalled: Boolean(eventGuard.preventDefaultCalled),
    defaultPreventedAfter: Boolean(eventGuard.defaultPreventedAfter ?? event?.defaultPrevented),
    buttonType:
      currentTarget?.getAttribute?.("type") ||
      currentTarget?.type ||
      target?.getAttribute?.("type") ||
      target?.type ||
      "",
    activeElementTag: activeElement?.tagName || "",
    activeElementType:
      activeElement?.getAttribute?.("type") || activeElement?.type || "",
    formId: form?.id || "",
    formAction: form?.action || "",
    formMethod: form?.method || "",
  };
};
const applyQuoteActionEventGuard = (event) => {
  const receivedEvent = Boolean(event);
  const defaultPreventedBefore = Boolean(event?.defaultPrevented);
  const preventDefaultCalled = Boolean(event?.preventDefault);
  event?.preventDefault?.();
  event?.stopPropagation?.();
  return {
    ...getQuoteActionEventDebug(event, {
      receivedEvent,
      defaultPreventedBefore,
      preventDefaultCalled,
      defaultPreventedAfter: Boolean(event?.defaultPrevented),
    }),
    receivedEvent,
    defaultPreventedBefore,
    preventDefaultCalled,
    defaultPreventedAfter: Boolean(event?.defaultPrevented),
  };
};
const logQuoteSaveEventGuard = ({ handler, traceId, event, eventGuard }) => {
  devLog(
    "QUOTE_SAVE_EVENT_GUARD",
    JSON.stringify(
      {
        handler,
        buildLabel: FABRIQUOTE_BUILD_INFO.label,
        traceId,
        ...getQuoteActionEventDebug(event, eventGuard),
      },
      null,
      2
    )
  );
};
const markQuoteSaveInProgressSession = (marker = {}) => {
  const safeMarker = {
    buildLabel: FABRIQUOTE_BUILD_INFO.label,
    traceId: String(marker.traceId || ""),
    status: "in_progress",
    action: marker.action === "save_as_new" ? "save_as_new" : "save",
    quoteType: marker.quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE,
    quoteNo: String(marker.quoteNo || ""),
    quoteId: String(marker.quoteId || ""),
    quoteIndexId: String(marker.quoteIndexId || marker.quoteId || ""),
    sourcePath: String(marker.sourcePath || ""),
    startedAt: Number(marker.startedAt || Date.now()),
    updatedAt: Date.now(),
  };
  setSessionStorageJson(SAVE_IN_PROGRESS_STORAGE_KEY, safeMarker);
  setSessionStorageJson(SAVE_ATTEMPT_TRACE_STORAGE_KEY, safeMarker);
};
const clearQuoteSaveInProgressSession = (patch = {}) => {
  const previous =
    getSessionStorageJson(SAVE_IN_PROGRESS_STORAGE_KEY) ||
    getSessionStorageJson(SAVE_ATTEMPT_TRACE_STORAGE_KEY) ||
    {};
  const nextTrace = {
    ...previous,
    ...patch,
    clearedAt: Date.now(),
  };
  const verified =
    nextTrace.status === "verified_saved" ||
    nextTrace.backendVerified === true ||
    nextTrace.reason === "verified_saved";
  setSessionStorageJson(SAVE_ATTEMPT_TRACE_STORAGE_KEY, nextTrace);
  if (verified) {
    setSessionStorageJson(LATEST_VERIFIED_SAVED_QUOTE_STORAGE_KEY, nextTrace);
    setSessionStorageJson(LAST_SAVE_TRACE_STORAGE_KEY, nextTrace);
  }
  removeSessionStorageItem(SAVE_IN_PROGRESS_STORAGE_KEY);
};
const getSaveTraceTimestampMs = (trace = {}) => {
  if (!trace || typeof trace !== "object") return 0;
  const candidates = [trace.updatedAt, trace.startedAt, trace.createdAt, trace.at].filter(
    (value) => value !== null && typeof value !== "undefined" && value !== ""
  );
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Date.parse(String(value));
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};
const isStaleSaveInProgressTrace = (trace = {}, nowMs = Date.now()) => {
  const timestampMs = getSaveTraceTimestampMs(trace);
  return Boolean(
    trace &&
      trace.status === "in_progress" &&
      timestampMs > 0 &&
      nowMs - timestampMs > SAVE_IN_PROGRESS_STALE_MS
  );
};
const isVerifiedSavedQuoteTrace = (trace = {}) =>
  Boolean(
    trace &&
      (trace.status === "verified_saved" ||
        trace.backendVerified === true ||
        trace.reason === "verified_saved")
  );
const getLatestVerifiedSavedQuoteTrace = () => {
  const latest = getSessionStorageJson(LATEST_VERIFIED_SAVED_QUOTE_STORAGE_KEY);
  if (isVerifiedSavedQuoteTrace(latest)) return latest;
  const legacy = getSessionStorageJson(LAST_SAVE_TRACE_STORAGE_KEY);
  return isVerifiedSavedQuoteTrace(legacy) ? legacy : {};
};
const logRequiredSaveCompletedBeforeUiPatch = (details = {}) => {
  devLog(
    "REQUIRED_SAVE_COMPLETED_BEFORE_UI_PATCH",
    JSON.stringify(
      {
        quoteNo: String(details.quoteNo || ""),
        quoteId: String(details.quoteId || ""),
        quoteIndexId: String(details.quoteIndexId || details.quoteId || ""),
        sourcePath: String(details.sourcePath || ""),
      },
      null,
      2
    )
  );
};
const PASSWORD_RESET_CONTINUE_URL = "https://quote.fabriquote.in/login";
const PASSWORD_RESET_SAFE_NOTICE =
  "If this email is registered, a password reset link will be sent. Please check Inbox, Spam, Promotions, and Updates.";
const quotationQueryPaths = new WeakMap();
const collection = (...args) => {
  const value = sdkCollection(...args);
  quotationQueryPaths.set(value, value.path);
  return value;
};
const firestoreQuery = (base, ...constraints) => {
  const value = sdkQuery(base, ...constraints);
  quotationQueryPaths.set(value, quotationQueryPaths.get(base) || base.path || '');
  return value;
};
const assertSafeQuotationQuery = value => {
  if (/^users\/[^/]+\/quotations$/.test(quotationQueryPaths.get(value) || value.path || '')) {
    const error = new Error(QUOTE_MAINTENANCE_PAUSED);
    error.code = 'quote/maintenance-paused';
    throw error;
  }
};
const getDoc = createQuotationDocumentReader({
  readRaw: (ref, options = {}) => trackedGetDoc(ref, { label: options.label || "QuotationApp.getDoc", ...options }),
  callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data),
});
const getDocCached = (ref, options = {}) => canonicalQuotationIdentity(ref) ? getDoc(ref, options) : getDocCachedRaw(ref, options);
const getDocs = (ref, options = {}) => {
  assertSafeQuotationQuery(ref);
  return trackedGetDocs(ref, { label: options.label || "QuotationApp.getDocs", ...options });
};
const getDocsCached = (ref, options = {}) => {
  assertSafeQuotationQuery(ref);
  return getDocsCachedRaw(ref, options);
};
const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const logAppStartupTrace = (payload = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  try {
    devLog("APP_STARTUP_TRACE", {
      at: new Date().toISOString(),
      ...payload,
    });
  } catch {
    // Startup tracing must never block rendering.
  }
};
const buildWorkspaceBootstrapDebug = ({
  authUid = "",
  email = "",
  workspaceUid = "",
  loadingStep = "",
  lastCompletedStep = "",
  errorCode = "",
  errorMessage = "",
} = {}) => ({
  authUid,
  email,
  workspaceUid,
  loadingStep,
  lastCompletedStep,
  errorCode,
  errorMessage,
});
const getInviteEmailErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "");
  const lowerMessage = message.toLowerCase();
  if (
    code.includes("network-request-failed") ||
    code.includes("unavailable") ||
    code.includes("deadline-exceeded") ||
    lowerMessage.includes("err_internet_disconnected") ||
    lowerMessage.includes("err_name_not_resolved") ||
    lowerMessage.includes("network") ||
    lowerMessage.includes("failed to fetch")
  ) {
    return "Internet connection lost. Please reconnect and try again.";
  }
  if (code.includes("invalid-email")) return "Please enter a valid email address.";
  if (code.includes("invalid-argument")) {
    return message || "Please check the staff email and try again.";
  }
  if (code.includes("failed-precondition")) {
    return message || "Staff login could not be prepared yet. Please check staff access settings.";
  }
  if (code.includes("unauthorized-continue-uri")) {
    return "Invite link domain is not authorized in Firebase.";
  }
  if (code.includes("operation-not-allowed")) {
    return "Email link sign-in is not enabled in Firebase Auth.";
  }
  if (code.includes("too-many-requests")) {
    return "Too many invite attempts. Please try again later.";
  }
  if (code.includes("permission-denied")) {
    return "You do not have permission to invite staff.";
  }
  if (message) return message;
  return "Staff login setup email could not be sent.";
};
const isFirebaseStorageBillingError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  return code.includes("402") || message.includes("402") || message.includes("payment required");
};
const isLocalStorageKillSwitchEnabled = (key) => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const getSecuritySessionStorageKey = (uid = "") =>
  `${ACCOUNT_SECURITY_SESSION_PREFIX}${String(uid || "").trim()}`;

const createSecuritySessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const getOrCreateSecuritySessionId = (uid = "") => {
  if (typeof window === "undefined" || !uid) return "";
  const key = getSecuritySessionStorageKey(uid);
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const next = createSecuritySessionId();
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    return createSecuritySessionId();
  }
};

const clearSecuritySessionId = (uid = "") => {
  if (typeof window === "undefined" || !uid) return;
  try {
    window.localStorage.removeItem(getSecuritySessionStorageKey(uid));
  } catch {
    // Best effort only.
  }
};

const hashClientText = async (value = "") => {
  const text = String(value || "");
  if (!text) return "";
  try {
    if (typeof crypto === "undefined" || !crypto.subtle || typeof TextEncoder === "undefined") {
      return "";
    }
    const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 64);
  } catch {
    return "";
  }
};

const detectBrowserInfo = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Edg\//.test(ua)) return { browser: "Microsoft Edge", version: ua.match(/Edg\/(\d+)/)?.[1] || "" };
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return { browser: "Chrome", version: ua.match(/Chrome\/(\d+)/)?.[1] || "" };
  if (/Firefox\//.test(ua)) return { browser: "Firefox", version: ua.match(/Firefox\/(\d+)/)?.[1] || "" };
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return { browser: "Safari", version: ua.match(/Version\/(\d+)/)?.[1] || "" };
  return { browser: "Browser", version: "" };
};

const detectOsInfo = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Windows NT/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown OS";
};

const detectDeviceType = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/iPad|Tablet/.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/.test(ua)) return "mobile";
  if (ua) return "desktop";
  return "unknown";
};

const getSafeSessionDeviceInfo = async () => {
  const userAgent =
    typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "";
  const browser = detectBrowserInfo(userAgent);
  const os = detectOsInfo(userAgent);
  return {
    browser: browser.browser,
    browserVersionMajor: browser.version,
    os,
    deviceType: detectDeviceType(userAgent),
    userAgentHash: await hashClientText(userAgent),
    displayLabel: `${browser.browser}${browser.version ? ` ${browser.version}` : ""} on ${os}`,
  };
};

const normalizeSecurityTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatSecurityRelativeTime = (value) => {
  const millis = normalizeSecurityTimestampMillis(value);
  if (!millis) return "Not available";
  const diffMs = Date.now() - millis;
  const absMs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (absMs < minute) return "Active now";
  if (absMs < hour) return `${Math.max(1, Math.round(absMs / minute))} min ago`;
  if (absMs < day) return `${Math.max(1, Math.round(absMs / hour))} hr ago`;
  if (absMs < 2 * day) return "Yesterday";
  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getPasswordStrength = (password = "") => {
  const value = String(password || "");
  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (!value) return { score: 0, label: "Not started", color: "#94a3b8" };
  if (score <= 2) return { score, label: "Weak", color: "#dc2626" };
  if (score <= 4) return { score, label: "Good", color: "#f59e0b" };
  return { score, label: "Strong", color: "#16a34a" };
};

const getSecurityAuthErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  if (code.includes("wrong-password") || code.includes("invalid-credential")) {
    return "Current password is incorrect.";
  }
  if (code.includes("requires-recent-login") || message.includes("sign in again")) {
    return "Please sign in again to continue.";
  }
  if (code.includes("weak-password")) return "Please choose a stronger password.";
  if (code.includes("network-request-failed")) return "Network error. Please try again.";
  if (code.includes("permission-denied")) return "This session was signed out for security.";
  if (code.includes("unauthenticated")) return "Please sign in again.";
  return "Security action failed. Please try again.";
};

const getPasswordResetEmailErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("invalid-email")) return "Enter a valid email address.";
  if (code.includes("too-many-requests")) {
    return "Too many reset requests. Please wait a few minutes and try again.";
  }
  if (code.includes("network-request-failed")) return "Network error. Please try again.";
  if (code.includes("unauthorized-continue-uri") || code.includes("unauthorized-domain")) {
    return "Reset link domain is not authorized. Please contact admin.";
  }
  if (code.includes("invalid-continue-uri")) {
    return "Reset link configuration error. Please contact admin.";
  }
  return "Could not send password reset email. Please try again.";
};

const getSecurityProviderSummary = (authUser) => {
  const providers = Array.isArray(authUser?.providerData) ? authUser.providerData : [];
  const providerIds = providers.map((provider) => provider.providerId).filter(Boolean);
  const hasPassword = providerIds.includes("password");
  const labels = providerIds.map((providerId) => {
    if (providerId === "password") return "Email/Password";
    if (providerId === "google.com") return "Google";
    if (providerId === "phone") return "Phone";
    return providerId;
  });
  return {
    hasPassword,
    labels: labels.length ? labels : ["Unknown"],
    label: labels.length ? labels.join(" + ") : "Unknown",
  };
};

const invalidateWorkspaceCollectionCache = (workspaceUid, collectionName) => {
  if (!workspaceUid || !collectionName) return;
  invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/${collectionName}`);
  if (collectionName === "quote_index") {
    invalidateFirestoreReadCacheByPrefix(`savedQuotes:recent:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`savedQuotes:recycleBin:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`customerLedger:quotes:${workspaceUid}:`);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("fq:quote-index-invalidated", { detail: { workspaceUid } }));
      try {
        window.localStorage.setItem("FQ_QUOTE_INDEX_INVALIDATION", JSON.stringify({ workspaceUid, nonce: `${Date.now()}:${Math.random()}` }));
      } catch { /* Storage may be unavailable; local invalidation still works. */ }
    }
  }
  if (collectionName === "customers") {
    invalidateFirestoreReadCacheByPrefix(`customerLedger:recent:${workspaceUid}:`);
  }
  if (collectionName === "crm_leads") {
    invalidateFirestoreReadCacheByPrefix(`crmDeals:recent:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`customerLedger:crmDeals:${workspaceUid}:`);
  }
  if (collectionName === "crm_tasks") {
    invalidateFirestoreReadCacheByPrefix(`crmTasks:${workspaceUid}:`);
  }
  if (collectionName === "opportunities") {
    invalidateFirestoreReadCacheByPrefix(`crmOpportunities:${workspaceUid}:`);
  }
  if (collectionName === "projects") {
    invalidateFirestoreReadCacheByPrefix(`projects:${workspaceUid}:`);
  }
  if (collectionName === "project_materials") {
    invalidateFirestoreReadCacheByPrefix(`projectMaterials:${workspaceUid}:`);
  }
  if (collectionName === "project_tracking_items") {
    invalidateFirestoreReadCacheByPrefix(`projectTracking:${workspaceUid}:`);
  }
  if (collectionName === "activity_log") {
    invalidateFirestoreReadCacheByPrefix(`crmActivity:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`activityLog:${workspaceUid}:`);
  }
  if (collectionName === "product_configs") {
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
  }
};
const LOCAL_DRAFT_MODULES = {
  GENERAL_QUOTE: "generalQuote",
  CURTAIN_QUOTE: "curtainQuote",
  CRM_LEAD: "crmLead",
  EXECUTION_DETAILS: "executionDetails",
  EXECUTION_TEMPLATE: "executionTemplate",
};
const QUOTE_EDITOR_MODE_NEW = "new";
const QUOTE_EDITOR_MODE_EDITING_EXISTING = "editing_existing";

const TASK_HISTORY_STATUS_COMPLETED = "completed";
const TASK_HISTORY_TYPE_DEAL_ACTION = "deal_action";
const TASK_HISTORY_TYPE_REMINDER = "reminder";
const TASK_HISTORY_TYPE_CHECKLIST = "checklist";
const TASK_HISTORY_TYPE_INSTALLATION = "installation";
const TASK_HISTORY_SOURCE_CRM = "crm";
const TASK_HISTORY_SOURCE_INSTALLATION = "installation";
const TASK_HISTORY_SOURCE_EXECUTION_DETAILS = "execution_details";
const CRM_REMINDER_TYPE_FOLLOW_UP = "follow_up";
const DUE_REMINDER_STATUS_PENDING = "pending";
const DUE_REMINDER_STATUS_COMPLETED = "completed";
const DUE_REMINDER_STATUS_CANCELLED = "cancelled";
const DUE_REMINDER_TYPE_FOLLOW_UP = "followup";
const DUE_REMINDER_TYPE_INSTALLATION = "installation";
const DUE_REMINDER_TYPE_TASK = "task";
const DUE_REMINDER_ID_UNSAFE_CHARS = new RegExp("[/\\\\#?\\[\\]]+", "g");
const DAILY_TASK_SOURCE_FOLLOW_UP = "followup";
const DAILY_TASK_SOURCE_INSTALLATION = "installation";
const DAILY_TASK_SOURCE_DEAL_ACTION = "deal_action";
const DAILY_TASK_SOURCE_CHECKLIST = "checklist";
const DAILY_TASK_SOURCE_CRM_TASK = "crm_task";

const DAILY_TASK_TYPE_LABELS = {
  [DAILY_TASK_SOURCE_FOLLOW_UP]: "Follow-up",
  [DAILY_TASK_SOURCE_INSTALLATION]: "Installation",
  [DAILY_TASK_SOURCE_DEAL_ACTION]: "Deal Action",
  [DAILY_TASK_SOURCE_CHECKLIST]: "Checklist",
  [DAILY_TASK_SOURCE_CRM_TASK]: "CRM Task",
};

const normalizeSearchText = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const normalizeSearchCompact = (value = "") =>
  normalizeSearchText(value).replace(/[^a-z0-9]+/g, "");

const doesSearchBlobMatch = (blob = "", query = "") => {
  const normalizedBlob = normalizeSearchText(blob);
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  if (normalizedBlob.includes(normalizedQuery)) return true;

  const compactBlob = normalizeSearchCompact(normalizedBlob);
  const compactQuery = normalizeSearchCompact(normalizedQuery);
  return Boolean(compactQuery && compactBlob.includes(compactQuery));
};

const SEARCH_TEXT_MAX_CHARS = 20000;
const SEARCH_KEYWORDS_MAX = 300;
const SEARCH_KEYWORD_MAX_CHARS = 60;
const SEARCH_PIECE_MAX_CHARS = 160;
const CUSTOMER_SEARCH_TEXT_MAX_CHARS = 3000;
const CUSTOMER_SEARCH_KEYWORDS_MAX = 100;
const CUSTOMER_SEARCH_PREFIXES_MAX = 200;
const CUSTOMER_SEARCH_RESULT_LIMIT = 15;
const CUSTOMER_SEARCH_TTL_MS = 5 * 60 * 1000;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 500;
const GLOBAL_SEARCH_RESULT_LIMIT = 24;
const REFERRAL_PARTNER_COLLECTION = "referral_partners";
const REFERRAL_PARTNER_LOAD_LIMIT = 100;
const REFERRAL_PARTNER_RESULT_LIMIT = 15;
const REFERRAL_PARTNER_TYPES = [
  { value: "interior_designer", label: "Interior Designer" },
  { value: "architect", label: "Architect" },
  { value: "contractor", label: "Contractor" },
  { value: "builder", label: "Builder" },
  { value: "other", label: "Other" },
];
const REFERRAL_PARTNER_TYPE_VALUES = new Set(
  REFERRAL_PARTNER_TYPES.map((item) => item.value)
);

const firstReferralText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const normalizeReferralPartnerType = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return REFERRAL_PARTNER_TYPE_VALUES.has(normalized)
    ? normalized
    : "interior_designer";
};

const getReferralPartnerTypeLabel = (value = "") =>
  REFERRAL_PARTNER_TYPES.find(
    (item) => item.value === normalizeReferralPartnerType(value)
  )?.label || "Interior Designer";

const createReferralPartnerDraft = (seedName = "") => ({
  displayName: String(seedName || "").trim(),
  type: "interior_designer",
  phone: "",
  email: "",
  companyName: "",
  notes: "",
});

const normalizeReferralPartnerRecord = (record = {}, fallbackId = "") => {
  const source = record && typeof record === "object" ? record : {};
  const partnerId = firstReferralText(
    source.partnerId,
    source.id,
    fallbackId,
    source.referralPartnerId
  );
  const displayName = firstReferralText(
    source.displayName,
    source.name,
    source.referralPartnerName
  );
  const type = normalizeReferralPartnerType(
    source.type || source.referralPartnerType
  );
  const phone = firstReferralText(source.phone, source.mobile, source.contactPhone);
  const email = firstReferralText(source.email, source.emailCanonical);
  const companyName = firstReferralText(
    source.companyName,
    source.company,
    source.firmName
  );
  const notes = firstReferralText(source.notes);

  return {
    ...source,
    id: partnerId,
    partnerId,
    displayName,
    type,
    typeLabel: getReferralPartnerTypeLabel(type),
    phone,
    phoneNormalized:
      source.phoneNormalized || normalizeSearchCompact(phone || source.phone),
    email,
    emailCanonical: normalizeEmailValue(email || source.emailCanonical || ""),
    companyName,
    nameNormalized: source.nameNormalized || normalizeSearchText(displayName),
    companyNameNormalized:
      source.companyNameNormalized || normalizeSearchText(companyName),
    notes,
    active: source.active !== false,
    deleted: source.deleted === true,
  };
};

const normalizeQuoteReferralPartner = (quote = {}) => {
  const source = quote && typeof quote === "object" ? quote : {};
  const embedded =
    source.referralPartner ||
    source.generalQuote?.referralPartner ||
    source.generalQuoteData?.referralPartner ||
    {};
  const partner = normalizeReferralPartnerRecord({
    ...embedded,
    partnerId: firstReferralText(
      embedded.partnerId,
      source.referralPartnerId,
      source.generalQuote?.referralPartnerId,
      source.generalQuoteData?.referralPartnerId
    ),
    displayName: firstReferralText(
      embedded.displayName,
      source.referralPartnerName,
      source.generalQuote?.referralPartnerName,
      source.generalQuoteData?.referralPartnerName
    ),
    type: firstReferralText(
      embedded.type,
      source.referralPartnerType,
      source.generalQuote?.referralPartnerType,
      source.generalQuoteData?.referralPartnerType
    ),
    phone: firstReferralText(
      embedded.phone,
      source.referralPartnerPhone,
      source.generalQuote?.referralPartnerPhone,
      source.generalQuoteData?.referralPartnerPhone
    ),
    companyName: firstReferralText(
      embedded.companyName,
      source.referralPartnerCompanyName,
      source.generalQuote?.referralPartnerCompanyName,
      source.generalQuoteData?.referralPartnerCompanyName
    ),
  });
  return partner.partnerId || partner.displayName ? partner : null;
};

const buildReferralPartnerQuoteFields = (partnerInput = null) => {
  const partner = normalizeReferralPartnerRecord(partnerInput || {});
  if (!partner.partnerId && !partner.displayName) {
    return {
      referralPartner: null,
      referralPartnerId: "",
      referralPartnerName: "",
      referralPartnerType: "",
    };
  }

  return {
    referralPartner: {
      partnerId: partner.partnerId,
      displayName: partner.displayName,
      type: partner.type,
      phone: partner.phone || "",
      companyName: partner.companyName || "",
    },
    referralPartnerId: partner.partnerId,
    referralPartnerName: partner.displayName,
    referralPartnerType: partner.type,
  };
};

const QUOTE_SAVE_CALLABLE_TIMEOUT_MS = 45000;
const QUOTE_SAVE_OPTIONAL_TIMEOUT_MS = 12000;
const QUOTE_SAVE_FAILURE_TRACE_STORAGE_KEY = "FQ_LAST_QUOTE_SAVE_FAILURE_TRACE";
const QUOTE_SAVE_DIAGNOSTICS_FLAG = "FQ_ENABLE_SAVE_DIAGNOSTICS";

const isQuoteSaveDiagnosticsConsoleEnabled = () => {
  if (IS_DEV_UI_ENABLED) return true;
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(QUOTE_SAVE_DIAGNOSTICS_FLAG) === "1";
  } catch {
    return false;
  }
};

const createQuoteSaveTimeoutError = (step = "saveQuotationCanonical") => {
  const error = new Error(
    "Save failed because Firebase connection was unavailable before the save completed."
  );
  error.name = "QuoteSaveTimeoutError";
  error.code = "deadline-exceeded";
  error.__fqSaveTimedOut = true;
  error.__fqRequiredSaveStep = step;
  return error;
};

const withQuoteSaveTimeout = (
  operationPromise,
  { step = "saveQuotationCanonical", timeoutMs = QUOTE_SAVE_CALLABLE_TIMEOUT_MS } = {}
) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(createQuoteSaveTimeoutError(step));
    }, timeoutMs);

    Promise.resolve(operationPromise)
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const buildQuoteSaveFailureTrace = ({
  quoteId = "",
  quoteNo = "",
  quoteType = "",
  workspaceUid = "",
  userUid = "",
  referralPartner = null,
  step = "",
  fullQuoteWriteStarted = false,
  fullQuoteWriteSucceeded = false,
  quoteIndexWriteStarted = false,
  quoteIndexWriteSucceeded = false,
  crmSyncStarted = false,
  crmSyncSucceeded = false,
  error = null,
} = {}) => {
  const partner = normalizeQuoteReferralPartner({
    referralPartner,
    ...(referralPartner || {}),
  });
  const classification = classifyQuoteSaveError(error);
  return {
    quoteId: String(quoteId || ""),
    quoteNo: String(quoteNo || ""),
    quoteType: String(quoteType || ""),
    workspaceUid: String(workspaceUid || ""),
    userUid: String(userUid || ""),
    hasReferralPartner: Boolean(partner?.partnerId || partner?.displayName),
    referralPartnerId: partner?.partnerId || "",
    referralPartnerName: partner?.displayName || "",
    step: String(step || error?.__fqRequiredSaveStep || ""),
    fullQuoteWriteStarted: Boolean(fullQuoteWriteStarted),
    fullQuoteWriteSucceeded: Boolean(fullQuoteWriteSucceeded),
    quoteIndexWriteStarted: Boolean(quoteIndexWriteStarted),
    quoteIndexWriteSucceeded: Boolean(quoteIndexWriteSucceeded),
    crmSyncStarted: Boolean(crmSyncStarted),
    crmSyncSucceeded: Boolean(crmSyncSucceeded),
    errorCode: String(error?.code || error?.customData?.code || error?.details?.code || ""),
    errorMessage: String(error?.message || error || "").slice(0, 500),
    errorName: String(error?.name || ""),
    isNetworkError: classification.isNetworkError,
    isPermissionDenied: classification.isPermissionDenied,
    isUnavailable: classification.isUnavailable,
    isFailedPrecondition: classification.isFailedPrecondition,
    timestamp: new Date().toISOString(),
  };
};

const emitQuoteSaveFailureTrace = (tracePayload) => {
  const safeTrace = sanitizeCrmFirestoreValue(tracePayload) || {};
  if (typeof window !== "undefined" && window.sessionStorage) {
    try {
      window.sessionStorage.setItem(
        QUOTE_SAVE_FAILURE_TRACE_STORAGE_KEY,
        JSON.stringify(safeTrace)
      );
    } catch {
      // Diagnostics must never block the quotation save UI.
    }
  }
  if (isQuoteSaveDiagnosticsConsoleEnabled()) {
    console.warn("QUOTE_SAVE_FAILURE_TRACE", safeTrace);
  } else {
    devError("QUOTE_SAVE_FAILURE_TRACE");
  }
  return safeTrace;
};
const GLOBAL_SEARCH_DEBOUNCE_MS = 450;
const OPTIMIZER_QUOTE_SEARCH_LIMIT = 12;
const OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT = 40;
const OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const OPTIMIZER_QUOTE_SEARCH_DEBOUNCE_MS = 450;

const safeSearchPiece = (value, maxLen = SEARCH_PIECE_MAX_CHARS) => {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, maxLen);
  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString().slice(0, maxLen)
      : "";
  }
  if (typeof value === "object") return "";

  return String(value)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);
};

const pushSafeSearchPiece = (pieces, value, maxLen = SEARCH_PIECE_MAX_CHARS) => {
  if (Array.isArray(value)) {
    value.forEach((entry) => pushSafeSearchPiece(pieces, entry, maxLen));
    return;
  }
  const piece = safeSearchPiece(value, maxLen);
  if (piece) pieces.push(piece);
};

const normalizeCustomerSearchText = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s@.]/g, " ")
    .replace(/\s+/g, " ");

const normalizeCustomerSearchPhone = (value = "") => {
  const digits = String(value || "").replace(/\D+/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const addCustomerSearchPrefixes = (prefixSet, value, maxPrefixLength = 24) => {
  const normalized = normalizeCustomerSearchText(value);
  if (!normalized) return;

  normalized
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .forEach((token) => {
      const capped = token.slice(0, maxPrefixLength);
      for (let index = 1; index <= capped.length; index += 1) {
        prefixSet.add(capped.slice(0, index));
        if (prefixSet.size >= CUSTOMER_SEARCH_PREFIXES_MAX) return;
      }
    });
};

const addCustomerPhonePrefixes = (prefixSet, value) => {
  const phone = normalizeCustomerSearchPhone(value);
  if (!phone) return;
  for (let index = 1; index <= phone.length; index += 1) {
    prefixSet.add(phone.slice(0, index));
    if (prefixSet.size >= CUSTOMER_SEARCH_PREFIXES_MAX) return;
  }
};

const buildSafeSearchResult = (pieces = []) => {
  const normalizedPieces = [];
  const seen = new Set();

  (Array.isArray(pieces) ? pieces : []).forEach((piece) => {
    const safePiece = safeSearchPiece(piece);
    const normalized = normalizeSearchText(safePiece);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    normalizedPieces.push(normalized);
  });

  let searchText = normalizedPieces.join(" ");
  if (searchText.length > SEARCH_TEXT_MAX_CHARS) {
    if (isDevRuntime) {
      devWarn("Search index text truncated", {
        originalLength: searchText.length,
        maxLength: SEARCH_TEXT_MAX_CHARS,
      });
    }
    searchText = searchText.slice(0, SEARCH_TEXT_MAX_CHARS);
  }

  const keywordSet = new Set();
  searchText
    .split(/\s+/)
    .map((value) => value.trim())
    .filter((value) => value.length >= 2)
    .forEach((value) => keywordSet.add(value.slice(0, SEARCH_KEYWORD_MAX_CHARS)));

  return {
    searchText,
    searchKeywords: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
  };
};

const addSafeLinkedQuotePieces = (pieces, linkedQuotes = []) => {
  (Array.isArray(linkedQuotes) ? linkedQuotes : []).forEach((quote) => {
    pushSafeSearchPiece(pieces, quote?.quoteNo || quote?.quoteNumber);
    pushSafeSearchPiece(pieces, quote?.customerName);
    pushSafeSearchPiece(pieces, quote?.type || quote?.quoteType);
    pushSafeSearchPiece(pieces, quote?.value);
  });
};

const addSafeSiteLocationPieces = (pieces, locations = []) => {
  (Array.isArray(locations) ? locations : []).forEach((location) => {
    pushSafeSearchPiece(pieces, location?.label);
    pushSafeSearchPiece(pieces, location?.addressText);
    pushSafeSearchPiece(pieces, location?.siteName);
    pushSafeSearchPiece(pieces, location?.siteAddress);
    pushSafeSearchPiece(pieces, location?.notes, 120);
  });
};

const addSafeQuoteRowPieces = (pieces, rows = []) => {
  (Array.isArray(rows) ? rows : []).slice(0, 120).forEach((row) => {
    pushSafeSearchPiece(pieces, row?.item || row?.name || row?.room);
    pushSafeSearchPiece(pieces, row?.roomName || row?.room);
    pushSafeSearchPiece(pieces, row?.areaName || row?.area);
    pushSafeSearchPiece(pieces, row?.windowLabel || row?.windowName || row?.window);
    pushSafeSearchPiece(pieces, row?.product || row?.productType || row?.type);
    pushSafeSearchPiece(pieces, row?.fabric || row?.fabricName || row?.fabricLabel);
    pushSafeSearchPiece(pieces, row?.material || row?.itemFabric);
    pushSafeSearchPiece(pieces, row?.description, 120);
  });
};

const buildSafeDealSearchIndex = (deal = {}, customer = null) => {
  const linkedQuotes = normalizeCrmLinkedQuotes(deal);
  const primaryLinkedQuote =
    deal.primaryLinkedQuote || getPrimaryCrmLinkedQuote(linkedQuotes) || {};
  const contact = deal.crmContact || deal.contact || deal.crm?.contact || {};
  const crm = deal.crm || {};
  const customerRecord = customer || {};
  const pieces = [];

  [
    deal.searchText,
    deal.searchKeywords,
    deal.customerName,
    deal.customerLabel,
    deal.name,
    deal.crmTitle,
    deal.title,
    deal.dealTitle,
    deal.projectTitle,
    deal.companyOrProject,
    deal.companyName,
    deal.projectName,
    deal.project,
    contact.name,
    contact.phone,
    contact.whatsapp,
    contact.email,
    contact.address,
    deal.phone,
    deal.whatsapp,
    deal.email,
    deal.address,
    deal.location,
    deal.source,
    contact.source,
    crm.source,
    deal.notes,
    deal.crmNotes,
    deal.description,
    deal.status,
    deal.dealStatus,
    deal.crmStageId,
    deal.stageId,
    deal.crmStageLabel,
    deal.stageNameSnapshot,
    deal.pipelineType,
    deal.quoteType,
    deal.quoteTypeResolved,
    deal.assignedTo,
    deal.assignedToName,
    deal.assignedToNameSnapshot,
    deal.assignedUser,
    deal.assignedUserName,
    deal.quoteNo,
    deal.primaryQuoteNo,
    deal.linkedQuoteNo,
    deal.quoteId,
    deal.linkedQuoteId,
    deal.customerId,
    deal.customerPhone,
    deal.customerEmail,
    deal.customerAddress,
    deal.customerCompanyName,
    crm.customerName,
    crm.customerPhone,
    crm.customerWhatsapp,
    crm.customerEmail,
    crm.customerAddress,
    crm.customerCompanyName,
    crm.quoteNo,
    crm.quoteId,
    primaryLinkedQuote.quoteNo,
    primaryLinkedQuote.quoteId,
    primaryLinkedQuote.customerName,
    customerRecord.id,
    customerRecord.customerId,
    customerRecord.customerName,
    customerRecord.phone,
    customerRecord.whatsapp,
    customerRecord.email,
    customerRecord.address,
    customerRecord.companyName,
    customerRecord.source,
    customerRecord.tags,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));

  addSafeLinkedQuotePieces(pieces, linkedQuotes);
  addSafeLinkedQuotePieces(pieces, normalizeCrmLinkedQuotes(crm));
  addSafeSiteLocationPieces(pieces, deal.siteLocations || crm.siteLocations || []);
  addSafeSiteLocationPieces(pieces, customerRecord.siteLocations || []);

  return buildSafeSearchResult(pieces);
};

const buildDealSearchBlob = (deal = {}, customer = null) => {
  const { searchText } = buildSafeDealSearchIndex(deal, customer);
  const compactBlob = normalizeSearchCompact(searchText);
  return compactBlob && compactBlob !== searchText
    ? `${searchText} ${compactBlob}`.slice(0, SEARCH_TEXT_MAX_CHARS)
    : searchText;
};

const buildCrmSearchIndexFields = (deal = {}, customer = null) => {
  const index = buildSafeDealSearchIndex(deal, customer);
  const keywordSet = new Set(index.searchKeywords);
  const compactPhone = normalizeSearchCompact(
    [
      deal?.phone,
      deal?.whatsapp,
      deal?.customerPhone,
      deal?.contact?.phone,
      deal?.contact?.whatsapp,
      deal?.crmContact?.phone,
      deal?.crmContact?.whatsapp,
    ]
      .map((value) => safeSearchPiece(value, 80))
      .filter(Boolean)
      .join(" ")
  );
  if (compactPhone) keywordSet.add(compactPhone.slice(0, SEARCH_KEYWORD_MAX_CHARS));

  return {
    searchText: index.searchText,
    searchKeywords: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
    searchTokens: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
    customerNameLower: normalizeSearchText(
      deal?.customerName ||
        deal?.contact?.name ||
        deal?.crmContact?.name ||
        deal?.title ||
        deal?.companyOrProject ||
        customer?.customerName ||
        ""
    ),
    normalizedPhone: normalizeSearchCompact(
      deal?.customerPhone ||
        deal?.phone ||
        deal?.contact?.phone ||
        deal?.contact?.whatsapp ||
        deal?.crmContact?.phone ||
        deal?.crmContact?.whatsapp ||
        customer?.phone ||
        customer?.whatsapp ||
        ""
    ),
    projectNameLower: normalizeSearchText(
      deal?.projectName ||
        deal?.projectTitle ||
        deal?.companyOrProject ||
        deal?.crmTitle ||
        deal?.title ||
        ""
    ),
    stageLower: normalizeSearchText(
      deal?.crmStageLabel || deal?.stageNameSnapshot || deal?.stageId || deal?.crmStageId || ""
    ),
    sourceLower: normalizeSearchText(deal?.source || deal?.contact?.source || deal?.crmContact?.source || ""),
    quoteNo: String(
      deal?.quoteNo ||
        deal?.primaryQuoteNo ||
        getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal))?.quoteNo ||
        ""
    ).trim(),
    primaryQuoteNo: String(
      deal?.primaryQuoteNo ||
        getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal))?.quoteNo ||
        deal?.quoteNo ||
        ""
    ).trim(),
  };
};

const buildSafeQuoteSearchIndex = (quote = {}) => {
  const pieces = [];
  [
    quote.quoteNo,
    quote.customerName,
    quote.customerPhone,
    quote.customerWhatsapp,
    quote.customerEmail,
    quote.contactPersonName,
    quote.contactPersonPhone,
    quote.contactSnapshot?.contactPersonName,
    quote.contactSnapshot?.contactPersonPhone,
    quote.customerSnapshot?.customerName,
    quote.customerSnapshot?.phone,
    quote.customerSnapshot?.email,
    quote.source,
    quote.quoteType,
    quote.quoteTypeResolved,
    quote.status,
    quote.workStage,
    quote.projectName,
    quote.projectTitle,
    quote.siteName,
    quote.siteAddress,
    quote.siteContactName,
    quote.siteContactPhone,
    quote.siteNotes,
    quote.siteSnapshot?.siteName,
    quote.siteSnapshot?.siteAddress,
    quote.siteSnapshot?.siteContactName,
    quote.siteSnapshot?.siteContactPhone,
    quote.quoteDisplayTitle,
    quote.crm?.stageId,
    quote.crm?.stageName,
    quote.crm?.siteName,
    quote.crm?.siteAddress,
    quote.referralPartnerName,
    quote.referralPartner?.displayName,
    quote.referralPartner?.companyName,
    quote.referralPartner?.phone,
    quote.generalQuote?.referralPartnerName,
    quote.generalQuote?.referralPartner?.displayName,
    quote.generalQuote?.referralPartner?.companyName,
    quote.generalQuote?.quoteFor?.name,
    quote.generalQuote?.quoteFor?.company,
    quote.generalQuote?.quoteFor?.phone,
    quote.generalQuote?.quoteFor?.email,
    quote.generalQuote?.siteName,
    quote.generalQuote?.siteAddress,
    quote.generalQuote?.quoteDisplayTitle,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));
  addSafeQuoteRowPieces(pieces, quote.rows || []);
  addSafeQuoteRowPieces(pieces, quote.generalQuote?.items || []);
  addSafeQuoteRowPieces(pieces, quote.generalQuote?.lineItems || []);
  addSafeSiteLocationPieces(pieces, quote.crm?.siteLocations || []);
  addSafeSiteLocationPieces(pieces, quote.siteLocations || []);
  return buildSafeSearchResult(pieces);
};

const buildQuoteSearchBlob = (quote = {}) => {
  const { searchText } = buildSafeQuoteSearchIndex(quote);
  const compactBlob = normalizeSearchCompact(searchText);
  return compactBlob && compactBlob !== searchText
    ? `${searchText} ${compactBlob}`.slice(0, SEARCH_TEXT_MAX_CHARS)
    : searchText;
};

const buildQuoteLookupFields = (quote = {}) => ({
  quoteNoString: String(quote.quoteNo || quote.id || "").trim(),
  customerNameLower: normalizeSearchText(
    quote.customerSnapshot?.customerName ||
      quote.customerCompanyName ||
      quote.quoteFor?.company ||
      quote.customerName ||
      quote.crm?.customerName ||
      quote.generalQuote?.quoteFor?.company ||
      quote.generalQuote?.quoteFor?.name ||
      ""
  ),
  projectNameLower: normalizeSearchText(
    quote.projectName ||
      quote.projectTitle ||
      quote.siteName ||
      quote.crm?.projectTitle ||
      quote.generalQuote?.siteName ||
      ""
  ),
  siteNameLower: normalizeSearchText(
    quote.siteName ||
      quote.projectSiteName ||
      quote.generalQuote?.siteName ||
      quote.crm?.siteName ||
      ""
  ),
  quoteDisplayTitleLower: normalizeSearchText(
    quote.quoteDisplayTitle ||
      quote.generalQuote?.quoteDisplayTitle ||
      ""
  ),
  normalizedPhone: normalizeSearchCompact(
    quote.customerPhone ||
      quote.customerWhatsapp ||
      quote.siteContactPhone ||
      quote.crm?.customerPhone ||
      quote.crm?.contact?.phone ||
      quote.generalQuote?.quoteFor?.phone ||
      quote.generalQuote?.siteContactPhone ||
    ""
  ),
});

const CANONICAL_CURTAIN_QUOTE_TYPE = "curtain";
const CANONICAL_GENERAL_QUOTE_TYPE = "general";
const CANONICAL_UNKNOWN_QUOTE_TYPE = "unknown";

const normalizeQuoteType = (value = "", doc = {}) => {
  const candidates = [
    value,
    doc?.normalizedQuoteType,
    doc?.quoteTypeResolved,
    doc?.quoteType,
    doc?.quotationType,
    doc?.category,
    doc?.type,
    doc?.linkedQuoteType,
    doc?.crm?.quoteType,
    doc?.crm?.pipelineType,
    doc?.pipelineType,
  ];

  for (const candidate of candidates) {
    const raw = String(candidate || "").trim().toLowerCase();
    if (!raw) continue;
    const compact = raw.replace(/[\s_-]+/g, "");
    if (
      compact === "general" ||
      compact === "generalquote" ||
      compact === "generalquotation" ||
      compact.startsWith("generalquote") ||
      compact.startsWith("generalquotation")
    ) {
      return CANONICAL_GENERAL_QUOTE_TYPE;
    }
    if (
      compact === "curtain" ||
      compact === "curtains" ||
      compact === "curtainquote" ||
      compact === "curtainquotation" ||
      compact === "curtainsandblinds" ||
      compact === "blind" ||
      compact === "blinds" ||
      compact.includes("curtain") ||
      compact.includes("blind")
    ) {
      return CANONICAL_CURTAIN_QUOTE_TYPE;
    }
  }

  const embeddedGeneralQuote =
    doc?.generalQuote && typeof doc.generalQuote === "object"
      ? doc.generalQuote
      : doc?.generalQuoteData && typeof doc.generalQuoteData === "object"
      ? doc.generalQuoteData
      : null;

  if (
    embeddedGeneralQuote &&
    ((Array.isArray(embeddedGeneralQuote.lineItems) &&
      embeddedGeneralQuote.lineItems.length > 0) ||
      (Array.isArray(embeddedGeneralQuote.items) && embeddedGeneralQuote.items.length > 0) ||
      String(embeddedGeneralQuote.title || "").trim() ||
      embeddedGeneralQuote.quoteFrom ||
      embeddedGeneralQuote.quoteFor)
  ) {
    return CANONICAL_GENERAL_QUOTE_TYPE;
  }

  if (
    (Array.isArray(doc?.items) && doc.items.length > 0) ||
    (Array.isArray(doc?.lineItems) && doc.lineItems.length > 0)
  ) {
    return CANONICAL_GENERAL_QUOTE_TYPE;
  }

  if (
    (Array.isArray(doc?.rows) && doc.rows.length > 0) ||
    (Array.isArray(doc?.curtainRows) && doc.curtainRows.length > 0) ||
    doc?.fabricSummary ||
    doc?.repeatSettings ||
    doc?.halfPieceReusePlan ||
    doc?.workshopFabricSummary
  ) {
    return CANONICAL_CURTAIN_QUOTE_TYPE;
  }

  return CANONICAL_UNKNOWN_QUOTE_TYPE;
};

const getAppQuoteTypeFromCanonical = (canonicalType) => {
  if (canonicalType === CANONICAL_GENERAL_QUOTE_TYPE) return GENERAL_QUOTE_TYPE;
  if (canonicalType === CANONICAL_CURTAIN_QUOTE_TYPE) return CURTAIN_QUOTE_TYPE;
  return "";
};

const getCanonicalQuoteTypeLabel = (canonicalType) => {
  if (canonicalType === CANONICAL_GENERAL_QUOTE_TYPE) return "General Quotation";
  if (canonicalType === CANONICAL_CURTAIN_QUOTE_TYPE) return "Curtains";
  return "Quotation";
};

const getAllowedCanonicalQuoteTypes = ({
  canAccessCurtainQuotes = false,
  canAccessGeneralQuotes = false,
} = {}) =>
  [
    canAccessCurtainQuotes ? CANONICAL_CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? CANONICAL_GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

const quoteTypeMatchesAccess = (quote = {}, allowedCanonicalTypes = []) => {
  if (!allowedCanonicalTypes.length) return false;
  const canonicalType = normalizeQuoteType(
    quote.normalizedQuoteType ||
      quote.quoteTypeResolved ||
      quote.quotationType ||
      quote.quoteType ||
      "",
    quote
  );
  if (canonicalType === CANONICAL_UNKNOWN_QUOTE_TYPE) return true;
  return allowedCanonicalTypes.includes(canonicalType);
};

const getQuoteIndexArrayInfo = (...candidates) => {
  let known = false;
  let count = 0;
  candidates.forEach((candidate) => {
    if (!Array.isArray(candidate)) return;
    known = true;
    count = Math.max(count, candidate.length);
  });
  return { known, count };
};

const QUOTE_STORAGE_SCHEMA_VERSION = 2;
const QUOTE_CONTENT_SCHEMA_VERSION = "quote-content-replace-v3";
const GENERAL_QUOTE_CANONICAL_CONTENT_FIELD = "items";
const CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD = "rows";
const QUOTE_ROW_STORAGE_INLINE = "inline_v1";
const QUOTE_ROW_STORAGE_CHUNKED = "chunked_v1";
const QUOTE_ROW_STORAGE_SUBCOLLECTION = "subcollection_v3";
const QUOTE_V3_STORAGE_VERSION = 3;
const QUOTE_V3_CONTENT_STORAGE_FIELD = "rows_subcollection";
const QUOTE_INLINE_PAYLOAD_WARN_BYTES = 850 * 1024;
const QUOTE_INLINE_PAYLOAD_CHUNK_BYTES = 950 * 1024;
const QUOTE_ROW_CHUNK_TARGET_BYTES = 300 * 1024;
const QUOTE_ROW_CHUNK_MAX_BYTES = 350 * 1024;
const CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS = [
  "rows",
  "curtainRows",
  "quoteRows",
  "measurementRows",
  "quotationRows",
  "items",
];
const CURTAIN_QUOTE_STALE_ALIAS_FIELDS = CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS.filter(
  (fieldName) => fieldName !== CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD
);
const isQuoteContentReplacementSchema = (value = "") =>
  String(value || "").startsWith("quote-content-replace");

const getQuoteContentFieldIds = (items = [], getId = (item) => item?.id || item?.itemId || item?.rowId || "") =>
  Array.isArray(items)
    ? items.map((item, index) => String(getId(item) || `row-${index + 1}`).trim()).filter(Boolean)
    : [];

const getQuoteContentAliasDiagnostics = (candidates = []) => ({
  aliasCounts: Object.fromEntries(
    candidates.map((candidate) => [
      candidate.field,
      Array.isArray(candidate.value) ? candidate.value.length : 0,
    ])
  ),
  aliasIds: Object.fromEntries(
    candidates.map((candidate) => [
      candidate.field,
      getQuoteContentFieldIds(candidate.value),
    ])
  ),
});

const selectQuoteContentFromAliases = ({
  candidates = [],
  canonicalField = "",
  canonicalFieldName = "",
  isReplacementSchema = false,
  allowCanonicalEmptyAliasRecovery = false,
} = {}) => {
  const canonicalCandidate = candidates.find((candidate) => candidate.field === canonicalFieldName);
  const nonEmptyCandidate = candidates.find(
    (candidate) => Array.isArray(candidate.value) && candidate.value.length > 0
  );
  const firstArrayCandidate = candidates.find((candidate) => Array.isArray(candidate.value));
  if (isReplacementSchema && canonicalField === canonicalFieldName) {
    const canonicalIsArray = Array.isArray(canonicalCandidate?.value);
    const canonicalIsEmptyArray =
      canonicalIsArray && canonicalCandidate.value.length === 0;
    const shouldRecoverFromAlias =
      allowCanonicalEmptyAliasRecovery &&
      (!canonicalIsArray || canonicalIsEmptyArray) &&
      Boolean(nonEmptyCandidate) &&
      nonEmptyCandidate.field !== canonicalFieldName;
    const selectedCandidate = shouldRecoverFromAlias
      ? nonEmptyCandidate
      : canonicalIsArray
      ? canonicalCandidate
      : nonEmptyCandidate || firstArrayCandidate || { field: canonicalFieldName, value: [] };
    return {
      field: selectedCandidate?.field || canonicalFieldName,
      items: Array.isArray(selectedCandidate?.value) ? selectedCandidate.value : [],
      count: Array.isArray(selectedCandidate?.value) ? selectedCandidate.value.length : 0,
      known: Boolean(canonicalIsArray || candidates.some((candidate) => Array.isArray(candidate.value))),
      recoveredFromCanonicalEmpty: Boolean(shouldRecoverFromAlias),
      canonicalField,
      canonicalFieldName,
      ...getQuoteContentAliasDiagnostics(candidates),
    };
  }

  const selectedCandidate = nonEmptyCandidate || canonicalCandidate || firstArrayCandidate || {
    field: "",
    value: [],
  };
  const canonicalIsEmptyArray =
    canonicalField === canonicalFieldName &&
    isReplacementSchema &&
    Array.isArray(canonicalCandidate?.value) &&
    canonicalCandidate.value.length === 0;
  const recoveredFromCanonicalEmpty =
    Boolean(nonEmptyCandidate) &&
    canonicalIsEmptyArray &&
    nonEmptyCandidate.field !== canonicalFieldName;

  return {
    field: selectedCandidate.field || "",
    items: Array.isArray(selectedCandidate.value) ? selectedCandidate.value : [],
    count: Array.isArray(selectedCandidate.value) ? selectedCandidate.value.length : 0,
    known: candidates.some((candidate) => Array.isArray(candidate.value)),
    recoveredFromCanonicalEmpty,
    canonicalField,
    canonicalFieldName,
    ...getQuoteContentAliasDiagnostics(candidates),
  };
};

const getGeneralQuoteContentCandidates = (rawQuote = {}, embeddedInput = null) => {
  const embedded =
    embeddedInput && typeof embeddedInput === "object"
      ? embeddedInput
      : rawQuote.generalQuote && typeof rawQuote.generalQuote === "object"
      ? rawQuote.generalQuote
      : rawQuote.generalQuoteData && typeof rawQuote.generalQuoteData === "object"
      ? rawQuote.generalQuoteData
      : {};
  const generalQuote =
    rawQuote.generalQuote && typeof rawQuote.generalQuote === "object"
      ? rawQuote.generalQuote
      : {};
  const generalQuoteData =
    rawQuote.generalQuoteData && typeof rawQuote.generalQuoteData === "object"
      ? rawQuote.generalQuoteData
      : {};
  const generalQuoteDataGeneralQuote =
    generalQuoteData.generalQuote && typeof generalQuoteData.generalQuote === "object"
      ? generalQuoteData.generalQuote
      : {};
  const quotation =
    rawQuote.quotation && typeof rawQuote.quotation === "object" ? rawQuote.quotation : {};
  const data = rawQuote.data && typeof rawQuote.data === "object" ? rawQuote.data : {};

  return [
    { field: "items", value: rawQuote.items },
    { field: "lineItems", value: rawQuote.lineItems },
    { field: "products", value: rawQuote.products },
    { field: "quoteItems", value: rawQuote.quoteItems },
    { field: "entries", value: rawQuote.entries },
    { field: "generalRows", value: rawQuote.generalRows },
    { field: "rows", value: rawQuote.rows },
    { field: "quoteRows", value: rawQuote.quoteRows },
    { field: "generalItems", value: rawQuote.generalItems },
    { field: "generalQuoteData.items", value: generalQuoteData.items },
    { field: "generalQuoteData.lineItems", value: generalQuoteData.lineItems },
    { field: "generalQuoteData.products", value: generalQuoteData.products },
    { field: "generalQuoteData.quoteItems", value: generalQuoteData.quoteItems },
    { field: "generalQuoteData.entries", value: generalQuoteData.entries },
    { field: "generalQuoteData.rows", value: generalQuoteData.rows },
    { field: "generalQuoteData.generalRows", value: generalQuoteData.generalRows },
    { field: "generalQuoteData.quoteRows", value: generalQuoteData.quoteRows },
    { field: "generalQuoteData.generalItems", value: generalQuoteData.generalItems },
    { field: "generalQuoteData.generalQuote.items", value: generalQuoteDataGeneralQuote.items },
    { field: "generalQuoteData.generalQuote.lineItems", value: generalQuoteDataGeneralQuote.lineItems },
    { field: "generalQuoteData.generalQuote.products", value: generalQuoteDataGeneralQuote.products },
    { field: "generalQuoteData.generalQuote.quoteItems", value: generalQuoteDataGeneralQuote.quoteItems },
    { field: "generalQuoteData.generalQuote.entries", value: generalQuoteDataGeneralQuote.entries },
    { field: "generalQuoteData.generalQuote.rows", value: generalQuoteDataGeneralQuote.rows },
    { field: "generalQuoteData.generalQuote.generalRows", value: generalQuoteDataGeneralQuote.generalRows },
    { field: "generalQuoteData.generalQuote.quoteRows", value: generalQuoteDataGeneralQuote.quoteRows },
    { field: "generalQuoteData.generalQuote.generalItems", value: generalQuoteDataGeneralQuote.generalItems },
    { field: "generalQuote.items", value: generalQuote.items || embedded.items },
    { field: "generalQuote.lineItems", value: generalQuote.lineItems || embedded.lineItems },
    { field: "generalQuote.products", value: generalQuote.products || embedded.products },
    { field: "generalQuote.quoteItems", value: generalQuote.quoteItems || embedded.quoteItems },
    { field: "generalQuote.entries", value: generalQuote.entries || embedded.entries },
    { field: "generalQuote.rows", value: generalQuote.rows || embedded.rows },
    { field: "generalQuote.generalRows", value: generalQuote.generalRows || embedded.generalRows },
    { field: "generalQuote.quoteRows", value: generalQuote.quoteRows || embedded.quoteRows },
    { field: "generalQuote.generalItems", value: generalQuote.generalItems || embedded.generalItems },
    { field: "quotation.items", value: quotation.items },
    { field: "quotation.products", value: quotation.products },
    { field: "quotation.quoteItems", value: quotation.quoteItems },
    { field: "quotation.entries", value: quotation.entries },
    { field: "data.items", value: data.items },
    { field: "data.products", value: data.products },
    { field: "data.quoteItems", value: data.quoteItems },
    { field: "data.entries", value: data.entries },
  ];
};

const getCurtainQuoteContentCandidates = (rawQuote = {}) => [
  { field: "rows", value: rawQuote.rows },
  { field: "curtainRows", value: rawQuote.curtainRows },
  { field: "quoteRows", value: rawQuote.quoteRows },
  { field: "measurementRows", value: rawQuote.measurementRows },
  { field: "quotationRows", value: rawQuote.quotationRows },
  { field: "items", value: rawQuote.items },
];

const resolveGeneralQuoteContentSelection = (rawQuote = {}, embedded = {}, options = {}) =>
  selectQuoteContentFromAliases({
    candidates: getGeneralQuoteContentCandidates(rawQuote, embedded),
    canonicalField: String(rawQuote?.canonicalContentField || "").trim(),
    canonicalFieldName: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
    isReplacementSchema: isQuoteContentReplacementSchema(rawQuote?.contentSchemaVersion),
    allowCanonicalEmptyAliasRecovery: options.allowCanonicalEmptyAliasRecovery === true,
  });

const resolveCurtainQuoteContentSelection = (rawQuote = {}) =>
  selectQuoteContentFromAliases({
    candidates: getCurtainQuoteContentCandidates(rawQuote),
    canonicalField: String(rawQuote?.canonicalContentField || "").trim(),
    canonicalFieldName: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
    isReplacementSchema: isQuoteContentReplacementSchema(rawQuote?.contentSchemaVersion),
  });

const getQuoteIndexGeneralItemInfo = (quote = {}) => {
  const aliasInfo = resolveGeneralQuoteContentSelection(quote);
  const storedCount = Number(quote?.itemCount ?? quote?.contentItemCount);
  if (!aliasInfo.known && Number.isFinite(storedCount)) {
    return { known: true, count: Math.max(0, storedCount) };
  }
  return { known: aliasInfo.known, count: aliasInfo.count };
};

const getQuoteIndexCurtainRowInfo = (quote = {}) => {
  const aliasInfo = resolveCurtainQuoteContentSelection(quote);
  const storedCount = Number(quote?.rowCount ?? quote?.contentRowCount);
  if (!aliasInfo.known && Number.isFinite(storedCount)) {
    return { known: true, count: Math.max(0, storedCount) };
  }
  return { known: aliasInfo.known, count: aliasInfo.count };
};

const getSavedQuoteRepairBadgeReasons = (quote = {}) => {
  const reasons = [];
  if (quote.indexContentMismatch === true) reasons.push("indexContentMismatch");
  if (quote.dataHealthStatus === "content_missing_index_stale") {
    reasons.push("dataHealthStatus:content_missing_index_stale");
  }
  if (quote.needsRepair === true) reasons.push("storedNeedsRepair");
  if (quote.repairRequired === true) reasons.push("storedRepairRequired");
  const indexHealth = String(quote.indexHealth || "").trim();
  if (indexHealth && indexHealth !== "ok") reasons.push(`indexHealth:${indexHealth}`);
  if (quote.repairReason != null && String(quote.repairReason || "").trim()) {
    reasons.push(`repairReason:${String(quote.repairReason || "").trim()}`);
  }
  return reasons;
};

const getSavedQuoteDisplayTotal = (indexDoc = {}, computedTotals = null) => {
  const candidates = [
    ["grandTotal", indexDoc?.grandTotal],
    ["finalTotal", indexDoc?.finalTotal],
    ["total", indexDoc?.total],
    ["totals.grandTotal", indexDoc?.totals?.grandTotal],
    ["summary.grandTotal", indexDoc?.summary?.grandTotal],
    // Legacy quote_index docs used totalAmount/finalAmount before the current metadata shape.
    ["totalAmount", indexDoc?.totalAmount],
    ["finalAmount", indexDoc?.finalAmount],
    ["computed.grandTotal", computedTotals?.grandTotal],
  ];

  for (const [source, value] of candidates) {
    if (value == null || value === "") continue;
    const amount = Number(value);
    if (Number.isFinite(amount)) {
      return {
        hasAmount: true,
        amount,
        source,
      };
    }
  }

  return {
    hasAmount: false,
    amount: null,
    source: "",
  };
};

const cleanQuoteIdentityText = (value = "") => String(value ?? "").trim();

const buildQuoteDisplayTitleFromParts = ({
  customerName = "",
  siteName = "",
  fallbackTitle = "",
  quoteNo = "",
} = {}) => {
  const derived = [cleanQuoteIdentityText(customerName), cleanQuoteIdentityText(siteName)]
    .filter(Boolean)
    .join(" - ");
  return derived || cleanQuoteIdentityText(fallbackTitle) || cleanQuoteIdentityText(quoteNo);
};

const buildInternalSavedQuoteCardTitle = ({
  customerName = "",
  siteName = "",
  quoteNo = "",
  contactPersonName = "",
} = {}) => {
  const cleanCustomer = cleanQuoteIdentityText(customerName);
  const cleanSite = cleanQuoteIdentityText(siteName);
  const cleanContact = cleanQuoteIdentityText(contactPersonName);
  const cleanQuoteNo = cleanQuoteIdentityText(quoteNo);
  const nameForTitle = cleanCustomer || cleanContact;
  const titleParts = [];
  if (nameForTitle) titleParts.push(nameForTitle);
  if (
    cleanSite &&
    normalizeSearchText(cleanSite) !== normalizeSearchText(nameForTitle)
  ) {
    titleParts.push(cleanSite);
  }
  if (cleanQuoteNo) titleParts.push(`#${cleanQuoteNo}`);
  return titleParts.join(" - ") || cleanQuoteNo || "Quotation";
};

const resolveQuoteIdentityParts = (source = {}, fallback = {}) => {
  const generalQuote = source?.generalQuote || source?.generalQuoteData || {};
  const quoteFor = source?.quoteFor || generalQuote?.quoteFor || {};
  const customerSnapshot = source?.customerSnapshot || generalQuote?.customerSnapshot || {};
  const siteSnapshot = source?.siteSnapshot || generalQuote?.siteSnapshot || {};
  const contactSnapshot = source?.contactSnapshot || generalQuote?.contactSnapshot || {};

  const explicitCompanyName = cleanQuoteIdentityText(
    source.customerCompanyName ||
      source.companyProject ||
      source.companyName ||
      quoteFor.company ||
      generalQuote.customerCompanyName ||
      generalQuote.companyProject ||
      generalQuote.companyName ||
      fallback.customerCompanyName ||
      fallback.companyProject ||
      fallback.companyName ||
      ""
  );
  const directCustomerName = cleanQuoteIdentityText(
    source.customerName ||
      source.customer ||
      source.clientName ||
      generalQuote.customerName ||
      generalQuote.clientName ||
      source.crm?.customerName ||
      fallback.customerName ||
      ""
  );
  const snapshotCustomerName = cleanQuoteIdentityText(customerSnapshot.customerName || "");
  const quoteForName = cleanQuoteIdentityText(
    quoteFor.name || generalQuote.quoteFor?.name || fallback.quoteForName || ""
  );
  const explicitCustomerName = directCustomerName || snapshotCustomerName || quoteForName;
  const customerName = explicitCompanyName || directCustomerName || snapshotCustomerName || quoteForName;
  const namesDiffer =
    customerName &&
    explicitCustomerName &&
    normalizeSearchText(customerName) !== normalizeSearchText(explicitCustomerName);
  const contactPersonName = cleanQuoteIdentityText(
    source.contactPersonName ||
      source.contactName ||
      contactSnapshot.contactPersonName ||
      contactSnapshot.name ||
      source.crm?.contact?.name ||
      (namesDiffer ? explicitCustomerName : "") ||
      fallback.contactPersonName ||
      ""
  );
  const contactPersonPhone = cleanQuoteIdentityText(
    source.contactPersonPhone ||
      source.contactPhone ||
      contactSnapshot.contactPersonPhone ||
      contactSnapshot.phone ||
      source.crm?.contact?.phone ||
      fallback.contactPersonPhone ||
      ""
  );
  const siteName = cleanQuoteIdentityText(
    source.siteName ||
      source.projectSiteName ||
      source.projectLocationName ||
      source.locationName ||
      generalQuote.siteName ||
      generalQuote.projectSiteName ||
      siteSnapshot.siteName ||
      fallback.siteName ||
      ""
  );
  const siteAddress = cleanQuoteIdentityText(
    source.siteAddress ||
      source.siteLocationAddress ||
      source.projectAddress ||
      source.siteLocation ||
      generalQuote.siteAddress ||
      generalQuote.siteLocationAddress ||
      siteSnapshot.siteAddress ||
      fallback.siteAddress ||
      ""
  );
  const siteContactName = cleanQuoteIdentityText(
    source.siteContactName ||
      source.siteContact ||
      source.projectContactName ||
      generalQuote.siteContactName ||
      siteSnapshot.siteContactName ||
      fallback.siteContactName ||
      ""
  );
  const siteContactPhone = cleanQuoteIdentityText(
    source.siteContactPhone ||
      source.sitePhone ||
      source.projectContactPhone ||
      generalQuote.siteContactPhone ||
      siteSnapshot.siteContactPhone ||
      fallback.siteContactPhone ||
      ""
  );
  const siteNotes = cleanQuoteIdentityText(
    source.siteNotes ||
      source.projectNotes ||
      source.siteInstructions ||
      generalQuote.siteNotes ||
      siteSnapshot.siteNotes ||
      siteSnapshot.notes ||
      fallback.siteNotes ||
      ""
  );
  const quoteDisplayTitle = buildQuoteDisplayTitleFromParts({
    customerName,
    siteName,
    fallbackTitle: source.quoteDisplayTitle || generalQuote.quoteDisplayTitle || fallback.quoteDisplayTitle || "",
    quoteNo: source.quoteNo || fallback.quoteNo || "",
  });

  return {
    customerName,
    contactPersonName,
    contactPersonPhone,
    siteName,
    siteAddress,
    siteContactName,
    siteContactPhone,
    siteNotes,
    quoteDisplayTitle,
  };
};

const buildSavedQuoteCardTitleMeta = (indexDoc = {}) => {
  const contactPersonName = cleanQuoteIdentityText(
    indexDoc.contactPersonName ||
      indexDoc.contactSnapshot?.contactPersonName ||
      indexDoc.contactName ||
      ""
  );
  const indexCustomerName = cleanQuoteIdentityText(
    indexDoc.customerName || indexDoc.customerSnapshot?.customerName || ""
  );
  const companyName = cleanQuoteIdentityText(
    indexDoc.customerCompanyName ||
      indexDoc.companyProject ||
      indexDoc.companyName ||
      indexDoc.clientName ||
      ""
  );
  const customerIsActuallyContact =
    indexCustomerName &&
    contactPersonName &&
    normalizeSearchText(indexCustomerName) === normalizeSearchText(contactPersonName);
  const customerName =
    customerIsActuallyContact && companyName && normalizeSearchText(companyName) !== normalizeSearchText(indexCustomerName)
      ? companyName
      : indexCustomerName || companyName;
  const siteName = cleanQuoteIdentityText(
    indexDoc.siteName ||
      indexDoc.siteSnapshot?.siteName ||
      indexDoc.projectName ||
      indexDoc.siteProjectName ||
      ""
  );
  const quoteNo = cleanQuoteIdentityText(
    indexDoc.quoteNo || indexDoc.quotationNumber || indexDoc.quoteNoString || ""
  );
  const hasDuplicateCustomerSite =
    customerName && siteName && normalizeSearchText(customerName) === normalizeSearchText(siteName);
  const renderedTitle = buildInternalSavedQuoteCardTitle({
    customerName,
    siteName: hasDuplicateCustomerSite ? "" : siteName,
    quoteNo,
    contactPersonName,
  });
  const titleSource = customerName
    ? customerIsActuallyContact && companyName
      ? "companyProject+siteName+quoteNo"
      : "customerName+siteName+quoteNo"
    : siteName
    ? "siteName+quoteNo"
    : quoteNo
    ? "quoteNo"
    : "fallback";

  return {
    renderedTitle,
    titleSource,
    customerName,
    contactPersonName,
    siteName,
    projectName: cleanQuoteIdentityText(indexDoc.projectName || ""),
    companyProject: cleanQuoteIdentityText(indexDoc.companyProject || indexDoc.customerCompanyName || ""),
    quoteNo,
    quoteDisplayTitle: cleanQuoteIdentityText(indexDoc.quoteDisplayTitle || ""),
    displayTitle: cleanQuoteIdentityText(indexDoc.displayTitle || indexDoc.title || ""),
    customerDisplayName: cleanQuoteIdentityText(indexDoc.customerDisplayName || ""),
    hasDuplicateCustomerSite,
  };
};

const buildSavedQuoteCardTitle = (indexDoc = {}) =>
  buildSavedQuoteCardTitleMeta(indexDoc).renderedTitle;

const buildQuoteIdentitySnapshots = (source = {}, fallback = {}) => {
  const identity = resolveQuoteIdentityParts(source, fallback);
  const customerId = cleanQuoteIdentityText(source.customerId || source.crm?.customerId || fallback.customerId || "");
  const customerPhone = cleanQuoteIdentityText(
    source.customerPhone ||
      source.customerWhatsapp ||
      source.phone ||
      source.whatsapp ||
      source.quoteFor?.phone ||
      source.generalQuote?.quoteFor?.phone ||
      fallback.customerPhone ||
      fallback.phone ||
      ""
  );
  const customerEmail = cleanQuoteIdentityText(
    source.customerEmail ||
      source.email ||
      source.quoteFor?.email ||
      source.generalQuote?.quoteFor?.email ||
      fallback.customerEmail ||
      fallback.email ||
      ""
  );
  const siteId = cleanQuoteIdentityText(source.siteId || source.customerSiteId || fallback.siteId || "");

  return {
    customerSnapshot: {
      customerId,
      customerName: identity.customerName,
      phone: customerPhone,
      email: customerEmail,
    },
    contactSnapshot: {
      contactPersonName: identity.contactPersonName,
      contactPersonPhone: identity.contactPersonPhone,
    },
    siteSnapshot: {
      siteId,
      siteName: identity.siteName,
      siteAddress: identity.siteAddress,
      siteContactName: identity.siteContactName,
      siteContactPhone: identity.siteContactPhone,
      siteNotes: identity.siteNotes,
      notes: identity.siteNotes,
    },
  };
};

const buildQuoteIdentityFieldsForSave = (source = {}, fallback = {}) => {
  const identity = resolveQuoteIdentityParts(source, fallback);
  return {
    contactPersonName: identity.contactPersonName,
    contactPersonPhone: identity.contactPersonPhone,
    quoteDisplayTitle: buildQuoteDisplayTitleFromParts({
      customerName: identity.customerName,
      siteName: identity.siteName,
      fallbackTitle: fallback.quoteDisplayTitle || "",
      quoteNo: source.quoteNo || fallback.quoteNo || "",
    }),
    ...buildQuoteIdentitySnapshots(source, fallback),
  };
};

function traceQuoteIdentity(action = "", quoteLike = {}, extra = {}) {
  if (!IS_DEV_UI_ENABLED) return;
  const identity = resolveQuoteIdentityParts(quoteLike, extra);
  devLog("QUOTE_IDENTITY_TRACE", {
    action,
    quoteNo: quoteLike.quoteNo || extra.quoteNo || "",
    quoteId: quoteLike.quoteId || quoteLike.id || extra.quoteId || "",
    customerId: quoteLike.customerId || extra.customerId || "",
    customerName: identity.customerName,
    contactPersonName: identity.contactPersonName,
    contactPersonPhone: identity.contactPersonPhone,
    siteName: identity.siteName,
    siteAddress: identity.siteAddress,
    quoteDisplayTitle: identity.quoteDisplayTitle,
    quoteIndexCustomerName:
      extra.quoteIndexCustomerName ||
      quoteLike.quoteIndex?.customerName ||
      quoteLike.indexRecordRaw?.customerName ||
      "",
    quoteIndexSiteName:
      extra.quoteIndexSiteName ||
      quoteLike.quoteIndex?.siteName ||
      quoteLike.indexRecordRaw?.siteName ||
      "",
    customerSearchDisplayName:
      extra.customerSearchDisplayName || quoteLike.displayName || "",
  });
}

const buildQuoteIndexPayload = (quote = {}, quoteIdValue = "", workspaceUidValue = "") => {
  for (const field of ["createdAt", "updatedAt", "lastActivityAt", "lastEditedAt"]) {
    if (isFirestoreSentinel(quote[field])) devWarn("QUOTE_INDEX_SENTINEL_LEAK_DETECTED", {
      quoteId: quoteIdValue, quoteNo: quote.quoteNo, field,
      value: { methodName: quote[field]?._methodName || quote[field]?.methodName || "SDK transform" },
      source: "buildQuoteIndexPayload", actionTaken: "use_valid_historical_fallback",
    });
  }
  quote = stripFirestoreSentinelsForState(quote) || {};
  const resolveIndexNumber = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate == null || candidate === "") continue;
      const numericValue = Number(candidate);
      if (Number.isFinite(numericValue)) return numericValue;
    }
    return 0;
  };
  const explicitSourceQuoteId = String(
    quote.sourceQuoteId ||
      quote.quotationId ||
      quote.sourceId ||
      quote.fullQuoteId ||
      ""
  ).trim();
  const quoteId = String(
    quoteIdValue || quote.quoteId || quote.id || explicitSourceQuoteId || ""
  ).trim();
  const sourceQuoteId = String(
    quote.sourceQuoteId ||
      quote.quotationId ||
      quote.sourceId ||
      quote.fullQuoteId ||
      quote.quoteId ||
      quote.id ||
      quoteId ||
      ""
  ).trim();
  const sourceCollection = String(quote.sourceCollection || "quotations").trim() || "quotations";
  const sourcePath = String(
    quote.sourcePath ||
      (workspaceUidValue && sourceQuoteId
        ? `users/${workspaceUidValue}/${sourceCollection}/${sourceQuoteId}`
        : "")
  ).trim();
  const normalizedQuoteType = normalizeQuoteType(
    quote.normalizedQuoteType ||
      quote.quoteTypeResolved ||
      quote.quoteType ||
      quote.quotationType ||
      quote.category ||
      quote.type ||
      "",
    quote
  );
  const quoteTypeResolved =
    getAppQuoteTypeFromCanonical(normalizedQuoteType) ||
    quote.quoteTypeResolved ||
    resolveQuoteType(quote);
  const indexQuoteType =
    normalizedQuoteType === CANONICAL_UNKNOWN_QUOTE_TYPE
      ? quoteTypeResolved
      : normalizedQuoteType;
  const quoteTypeLabel = getCanonicalQuoteTypeLabel(normalizedQuoteType);
  const quoteNo = String(quote.quoteNo || quote.quoteNoString || quoteId || "").trim();
  const quoteDate = resolveQuoteDateForInput(quote);
  const identityParts = resolveQuoteIdentityParts(quote);
  const customerName = identityParts.customerName;
  const contactPersonName = identityParts.contactPersonName;
  const contactPersonPhone = identityParts.contactPersonPhone;
  const companyProject = cleanQuoteIdentityText(
    quote.companyProject ||
      quote.customerCompanyName ||
      quote.companyName ||
      quote.quoteFor?.company ||
      quote.generalQuote?.quoteFor?.company ||
      quote.generalQuote?.companyProject ||
      customerName
  );
  const customerPhone = String(
    quote.customerPhone ||
      quote.customerWhatsapp ||
      quote.quoteFor?.phone ||
      quote.generalQuote?.quoteFor?.phone ||
      quote.crm?.customerPhone ||
      quote.crm?.contact?.phone ||
      ""
  ).trim();
  const customerEmail = String(
    quote.customerEmail ||
      quote.email ||
      quote.quoteFor?.email ||
      quote.generalQuote?.quoteFor?.email ||
      quote.crm?.customerEmail ||
      quote.crm?.contact?.email ||
      ""
  ).trim();
  const normalizedEmail = customerEmail.toLowerCase();
  const projectName = String(
    quote.projectName ||
      quote.projectTitle ||
      quote.siteName ||
      quote.crm?.projectTitle ||
      ""
  ).trim();
  const siteSnapshot = normalizeQuoteSiteSnapshot(quote, { customerName });
  const siteName = siteSnapshot.siteName;
  const siteAddress = siteSnapshot.siteAddress;
  const siteContactName = siteSnapshot.siteContactName;
  const siteContactPhone = siteSnapshot.siteContactPhone;
  const siteNotes = siteSnapshot.siteNotes;
  const referralPartner = normalizeQuoteReferralPartner(quote);
  const referralPartnerFields = buildReferralPartnerQuoteFields(referralPartner);
  const quoteDisplayTitle = buildQuoteDisplayTitleFromParts({
    customerName,
    siteName,
    fallbackTitle: quote.quoteDisplayTitle || quote.title || "",
    quoteNo,
  });
  const subtotalAmount = resolveIndexNumber(
    quote.subtotal,
    quote.subTotal,
    quote.totals?.subtotal,
    quote.totals?.subTotal,
    quote.summary?.subtotal,
    quote.summary?.subTotal,
    quote.taxableAmount,
    quote.totalAmount,
    quote.grandTotal
  );
  const totalAmount = resolveIndexNumber(
    quote.grandTotal,
    quote.total,
    quote.finalTotal,
    quote.finalAmount,
    quote.totalAmount,
    quote.totals?.grandTotal,
    quote.totals?.finalTotal,
    quote.totals?.total,
    quote.summary?.grandTotal,
    quote.summary?.finalTotal,
    quote.summary?.total,
    quote.amount,
    subtotalAmount
  );
  const taxableAmount = resolveIndexNumber(
    quote.taxableAmount,
    quote.taxableTotal,
    quote.netAmount,
    subtotalAmount
  );
  const totalGst = resolveIndexNumber(
    quote.totalGst,
    quote.totalGstAmount,
    quote.gstTotal,
    quote.gstAmount,
    quote.taxAmount
  );
  const quoteGstMode = resolveCurtainQuoteGstMode(quote);
  const quoteGstRate = resolveCurtainQuoteGstRate(quote);
  const gstModeLabel = quote.gstModeLabel || getCurtainGstModeLabel(quoteGstMode, quoteGstRate);
  const lightweightSearchSource = {
    id: quoteId,
    quoteNo,
    customerName,
    customerPhone,
    contactPersonName,
    contactPersonPhone,
    companyProject,
    customerCompanyName: companyProject,
    companyName: companyProject,
    customerWhatsapp: quote.customerWhatsapp || "",
    customerEmail,
    normalizedEmail,
    projectName,
    projectTitle: projectName,
    siteName,
    siteAddress,
    siteContactName,
    siteContactPhone,
    siteNotes,
    ...referralPartnerFields,
    quoteDisplayTitle,
    quoteType: indexQuoteType,
    quotationType: indexQuoteType,
    quoteTypeResolved,
    normalizedQuoteType,
    quoteTypeLabel,
    status: quote.status || "draft",
    workStage: quote.workStage || "quote_given",
  };
  const quoteSearchIndex = buildSafeQuoteSearchIndex(lightweightSearchSource);
  const lookupFields = buildQuoteLookupFields(lightweightSearchSource);
  const deleted =
    quote.deleted === true ||
    quote.isDeleted === true ||
    quote.archived === true ||
    quote.binStatus === "in_bin" ||
    quote.visibilityStatus === "bin" ||
    quote.movedToBin === true ||
    Boolean(quote.deletedAt || quote.movedToBinAt);
  const binStatus = deleted ? "in_bin" : "active";
  const generalItemInfo = getQuoteIndexGeneralItemInfo(quote);
  const curtainRowInfo = getQuoteIndexCurtainRowInfo(quote);
  const isGeneralIndex = normalizeQuoteType(indexQuoteType, quote) === CANONICAL_GENERAL_QUOTE_TYPE;
  const isCurtainIndex = normalizeQuoteType(indexQuoteType, quote) === CANONICAL_CURTAIN_QUOTE_TYPE;
  const itemCount = isGeneralIndex ? generalItemInfo.count : 0;
  const rowCount = isCurtainIndex ? curtainRowInfo.count : 0;
  const contentCountKnown = isGeneralIndex
    ? generalItemInfo.known
    : isCurtainIndex
    ? curtainRowInfo.known
    : false;
  const contentCount = isGeneralIndex ? itemCount : rowCount;
  const contentMissingIndexStale =
    contentCountKnown && !deleted && totalAmount > 0 && contentCount === 0;
  const existingHealthStatus = String(quote.dataHealthStatus || "").trim();
  const dataHealthStatus = contentMissingIndexStale
    ? "content_missing_index_stale"
    : existingHealthStatus && existingHealthStatus !== "content_missing_index_stale"
    ? existingHealthStatus
    : "ok";
  const indexContentMismatch = contentMissingIndexStale ? true : false;
  const resolvedContentSchemaVersion = contentCountKnown
    ? QUOTE_CONTENT_SCHEMA_VERSION
    : quote.contentSchemaVersion || "";
  const resolvedCanonicalContentField = isGeneralIndex
    ? GENERAL_QUOTE_CANONICAL_CONTENT_FIELD
    : isCurtainIndex
    ? CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD
    : quote.canonicalContentField || "";

  traceQuoteIdentity(
    "quoteIndexBuild",
    {
      ...quote,
      customerName,
      contactPersonName,
      contactPersonPhone,
      siteName,
      siteAddress,
      siteContactName,
      siteContactPhone,
      siteNotes,
      quoteDisplayTitle,
      quoteNo,
    },
    {
      quoteId,
      quoteIndexCustomerName: customerName,
      quoteIndexSiteName: siteName,
    }
  );

  return {
    quoteId,
    id: quoteId,
    quoteIndexId: quote.quoteIndexId || quote.indexId || quoteId,
    indexId: quote.quoteIndexId || quote.indexId || quoteId,
    sourceQuoteId,
    sourceCollection,
    sourcePath,
    workspaceUid: workspaceUidValue || quote.workspaceUid || "",
    quoteNo,
    quoteNoString: lookupFields.quoteNoString,
    customerId: quote.customerId || quote.crm?.customerId || "",
    customerKey:
      quote.customerKey ||
      quote.crm?.customerKey ||
      quote.customerId ||
      quote.crm?.customerId ||
      lookupFields.normalizedPhone ||
      normalizeSearchCompact(customerName) ||
      "",
    customerName,
    companyProject,
    customerCompanyName: companyProject,
    companyName: companyProject,
    customerNameLower: lookupFields.customerNameLower,
    customerPhone,
    customerEmail,
    contactPersonName,
    contactPersonPhone,
    customerWhatsapp: quote.customerWhatsapp || "",
    normalizedEmail,
    normalizedPhone: lookupFields.normalizedPhone,
    projectName: projectName || siteName,
    projectNameLower: lookupFields.projectNameLower,
    siteId: siteSnapshot.siteId,
    customerSiteId: siteSnapshot.siteId,
    siteName,
    siteNameLower: lookupFields.siteNameLower,
    siteAddress,
    siteContactName,
    siteContactPhone,
    siteNotes,
    ...referralPartnerFields,
    quoteDisplayTitle,
    quoteDisplayTitleLower: lookupFields.quoteDisplayTitleLower,
    quoteType: indexQuoteType,
    quoteTypeResolved,
    quotationType: indexQuoteType,
    normalizedQuoteType,
    quoteTypeLabel,
    category: quote.category || indexQuoteType,
    status: quote.status || "draft",
    workStage: quote.workStage || "quote_given",
    leadId: quote.leadId || quote.crm?.leadId || "",
    crmLeadId: quote.crmLeadId || quote.crm?.leadId || "",
    subtotal: Number.isFinite(subtotalAmount) ? subtotalAmount : 0,
    taxableAmount: Number.isFinite(taxableAmount) ? taxableAmount : 0,
    totalGst: Number.isFinite(totalGst) ? totalGst : 0,
    gstAmount: Number.isFinite(totalGst) ? totalGst : 0,
    quoteGstMode,
    gstMode: quoteGstMode,
    quoteGstRate,
    gstRate: quoteGstRate,
    gstModeLabel,
    discountAmount: Number(quote.discountAmount || quote.postGstDiscountAmount || 0),
    totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
    grandTotal: Number.isFinite(totalAmount) ? totalAmount : 0,
    total: Number.isFinite(totalAmount) ? totalAmount : 0,
    finalTotal: Number.isFinite(totalAmount) ? totalAmount : 0,
    finalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
    ...buildQuoteIdentitySnapshots(
      {
        ...quote,
        customerName,
        contactPersonName,
        contactPersonPhone,
        siteName,
        siteAddress,
        siteContactName,
        siteContactPhone,
        siteNotes,
      },
      { customerName }
    ),
    itemCount,
    rowCount,
    contentItemCount: itemCount,
    contentRowCount: rowCount,
    contentCountKnown,
    contentSchemaVersion: resolvedContentSchemaVersion,
    canonicalContentField: resolvedCanonicalContentField,
    dataHealthStatus,
    indexContentMismatch,
    indexHealth: "ok",
    needsRepair: false,
    repairRequired: false,
    repairReason: null,
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    createdBy: quote.createdBy || quote.createdByUid || quote.userUid || "",
    createdByUid: quote.createdByUid || quote.createdBy || "",
    createdByName: quote.createdByName || "",
    updatedBy: quote.updatedBy || quote.updatedByUid || quote.userUid || "",
    createdAt: normalizeIndexDateForWrite(quote.createdAt, [quote.createdOn, quote.createdDate, quoteDate]),
    updatedAt: normalizeIndexDateForWrite(quote.updatedAt, [quote.updatedOn, quote.modifiedAt, quote.createdAt, quoteDate]),
    lastActivityAt: normalizeIndexDateForWrite(quote.lastActivityAt,
      [quote.updatedAt, quote.updatedOn, quote.lastEditedAt, quote.modifiedAt, quote.editedAt, quote.createdAt, quoteDate]),
    lastEditedAt: normalizeIndexDateForWrite(quote.lastEditedAt,
      [quote.updatedAt, quote.updatedOn, quote.modifiedAt, quote.editedAt]),
    lastEditedBy:
      quote.lastEditedBy ||
      quote.updatedBy ||
      quote.updatedByUid ||
      quote.userUid ||
      "",
    lastEditedByName:
      quote.lastEditedByName ||
      quote.updatedByName ||
      quote.savedByName ||
      quote.userName ||
      "",
    deleted,
    archived: deleted,
    binStatus,
    visibilityStatus: deleted ? "bin" : "active",
    deletedAt: quote.deletedAt || null,
    movedToBinAt: quote.movedToBinAt || null,
    restoredAt: quote.restoredAt || null,
    searchText: quoteSearchIndex.searchText,
    searchableText: quoteSearchIndex.searchText,
    searchKeywords: quoteSearchIndex.searchKeywords,
    searchTokens: quoteSearchIndex.searchKeywords,
  };
};

const buildQuoteIndexSavePayload = (quote = {}, quoteIdValue = "", workspaceUidValue = "") => {
  const payload = sanitizeCrmFirestoreValue({
    ...buildQuoteIndexPayload(quote, quoteIdValue, workspaceUidValue),
    updatedAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    lastEditedAt: serverTimestamp(),
  });
  devLog("QUOTE_INDEX_DATE_WRITE_TRACE", {
    quoteId: quoteIdValue, quoteNo: quote.quoteNo,
    updatedAtType: "SDK FieldValue", lastActivityAtType: "SDK FieldValue",
    usedServerTimestampDirectly: true, localPatchDateType: "Timestamp/Date (separate state payload)",
  });
  return payload;
};

const getQuoteIndexSourceQuoteId = (quoteIndex = {}, fallbackId = "") => {
  const sourcePath = String(quoteIndex.sourcePath || "").trim();
  const sourcePathQuoteId =
    sourcePath.includes("/quotations/") && sourcePath.split("/").filter(Boolean).length >= 4
      ? sourcePath.split("/").filter(Boolean).pop()
      : "";
  return String(
    quoteIndex.sourceQuoteId ||
      quoteIndex.quoteId ||
      quoteIndex.quotationId ||
      quoteIndex.fullQuoteId ||
      sourcePathQuoteId ||
      fallbackId ||
      ""
  ).trim();
};

const getRepairComparableText = (value = "") =>
  normalizeSearchText(cleanQuoteIdentityText(value));

const getRepairComparableNumber = (value) => {
  if (value == null || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.round(numberValue * 100) / 100 : null;
};

const getRepairHistoricalDateValue = (...values) => {
  for (const value of values) {
    const millis = safeTimeMillis(value);
    if (millis > 0) return Timestamp.fromDate(new Date(millis));
  }
  return null;
};

const buildExpectedQuoteIndexFromFullQuoteForRepair = (
  fullQuote = {},
  quoteIdValue = "",
  workspaceUidValue = ""
) => {
  const quoteId = String(
    quoteIdValue ||
      fullQuote.quoteId ||
      fullQuote.id ||
      fullQuote.sourceQuoteId ||
      fullQuote.quoteNo ||
      ""
  ).trim();
  const expected = buildQuoteIndexPayload(
    {
      ...fullQuote,
      id: quoteId,
      quoteId,
      sourceQuoteId: quoteId,
      sourceCollection: "quotations",
    },
    quoteId,
    workspaceUidValue
  );
  const historicalUpdatedAt = getRepairHistoricalDateValue(
    fullQuote.updatedAt,
    fullQuote.updatedOn,
    fullQuote.modifiedAt,
    fullQuote.createdAt,
    fullQuote.quoteDate,
    fullQuote.date
  );
  const historicalLastActivityAt = getRepairHistoricalDateValue(
    fullQuote.lastActivityAt,
    fullQuote.updatedAt,
    fullQuote.createdAt,
    fullQuote.quoteDate,
    fullQuote.date
  );
  return {
    ...expected,
    updatedAt: historicalUpdatedAt || expected.updatedAt || null,
    lastActivityAt: historicalLastActivityAt || null,
  };
};

const compareQuoteIndexWithFullQuote = ({
  fullQuote = null,
  quoteIndex = null,
  quoteIndexId = "",
  workspaceUidValue = "",
} = {}) => {
  const quoteId = String(
    fullQuote?.id ||
      fullQuote?.quoteId ||
      getQuoteIndexSourceQuoteId(quoteIndex || {}, quoteIndexId) ||
      quoteIndexId ||
      ""
  ).trim();
  const quoteNo = cleanQuoteIdentityText(
    fullQuote?.quoteNo || fullQuote?.quoteNoString || quoteIndex?.quoteNo || quoteIndex?.quoteNoString || ""
  );
  if (!quoteIndex) {
    return {
      quoteId,
      quoteNo,
      issues: ["quote_index_missing"],
      risk: "Manual Only",
      recommendedAction: "Create or rebuild quote_index from the full quotation.",
      expected: null,
      titleMeta: null,
    };
  }
  if (!fullQuote) {
    return {
      quoteId,
      quoteNo,
      issues: ["full_quote_missing"],
      risk: "Manual Only",
      recommendedAction: "Review manually. The source full quotation was not found.",
      expected: null,
      titleMeta: buildSavedQuoteCardTitleMeta(quoteIndex),
    };
  }

  const expected = buildExpectedQuoteIndexFromFullQuoteForRepair(
    { id: quoteId, ...fullQuote },
    quoteId,
    workspaceUidValue
  );
  const issues = [];
  const expectedTitle = buildInternalSavedQuoteCardTitle({
    customerName: expected.customerName,
    siteName: expected.siteName,
    quoteNo: expected.quoteNo,
    contactPersonName: expected.contactPersonName,
  });
  const titleMeta = buildSavedQuoteCardTitleMeta(quoteIndex);
  const fullCustomerId = cleanQuoteIdentityText(expected.customerId);
  const indexCustomerId = cleanQuoteIdentityText(quoteIndex.customerId || quoteIndex.crm?.customerId || "");
  const fullDealId = cleanQuoteIdentityText(expected.opportunityId || expected.dealId || expected.crmLeadId || "");
  const indexDealId = cleanQuoteIdentityText(
    quoteIndex.opportunityId || quoteIndex.dealId || quoteIndex.crmLeadId || quoteIndex.leadId || ""
  );
  const fullProjectId = cleanQuoteIdentityText(expected.projectId || expected.crm?.projectId || "");
  const indexProjectId = cleanQuoteIdentityText(quoteIndex.projectId || quoteIndex.crm?.projectId || "");
  const fullTotal = getRepairComparableNumber(expected.grandTotal);
  const indexTotal = getRepairComparableNumber(
    quoteIndex.grandTotal ?? quoteIndex.total ?? quoteIndex.finalTotal ?? quoteIndex.totalAmount
  );

  if (cleanQuoteIdentityText(expected.quoteNo) !== cleanQuoteIdentityText(quoteIndex.quoteNo || quoteIndex.quoteNoString)) {
    issues.push("quote_index_quoteNo_mismatch");
  }
  if (fullCustomerId && !indexCustomerId) issues.push("quote_index_customerId_missing");
  if (fullCustomerId && indexCustomerId && fullCustomerId !== indexCustomerId) {
    issues.push("quote_index_customer_mismatch");
  }
  if (getRepairComparableText(expected.customerName) !== getRepairComparableText(quoteIndex.customerName)) {
    issues.push("quote_index_customerName_stale");
  }
  if (getRepairComparableText(expected.siteName) !== getRepairComparableText(quoteIndex.siteName || quoteIndex.projectName)) {
    issues.push("quote_index_siteName_stale");
  }
  if (fullTotal !== null && indexTotal !== null && Math.abs(fullTotal - indexTotal) > 0.01) {
    issues.push("quote_index_total_stale");
  }
  if (getRepairComparableText(expected.status) !== getRepairComparableText(quoteIndex.status)) {
    issues.push("quote_index_status_stale");
  }
  if (getRepairComparableText(expected.workStage) !== getRepairComparableText(quoteIndex.workStage)) {
    issues.push("quote_index_stage_stale");
  }
  if (!safeTimeMillis(quoteIndex.lastActivityAt)) {
    issues.push("quote_index_lastActivityAt_missing");
  }
  if (fullDealId && indexDealId && fullDealId !== indexDealId) {
    issues.push("quote_index_dealId_mismatch");
  }
  if (fullProjectId && indexProjectId && fullProjectId !== indexProjectId) {
    issues.push("quote_index_projectId_mismatch");
  }
  if (getRepairComparableText(titleMeta.renderedTitle) !== getRepairComparableText(expectedTitle)) {
    issues.push("saved_quote_title_stale");
  }
  if (
    titleMeta.contactPersonName &&
    titleMeta.customerName &&
    getRepairComparableText(titleMeta.renderedTitle).startsWith(getRepairComparableText(titleMeta.contactPersonName)) &&
    getRepairComparableText(titleMeta.customerName) !== getRepairComparableText(titleMeta.contactPersonName)
  ) {
    issues.push("saved_quote_title_uses_contact_person");
  }
  if (
    cleanQuoteIdentityText(expected.referralPartnerId || expected.referralPartnerName) &&
    (
      cleanQuoteIdentityText(expected.referralPartnerId) !== cleanQuoteIdentityText(quoteIndex.referralPartnerId) ||
      getRepairComparableText(expected.referralPartnerName) !== getRepairComparableText(quoteIndex.referralPartnerName)
    )
  ) {
    issues.push("quote_index_referralPartner_stale");
  }

  const risk =
    !fullQuote || !quoteIndex
      ? "Manual Only"
      : issues.some((issue) =>
          [
            "quote_index_customer_mismatch",
            "quote_index_dealId_mismatch",
            "quote_index_projectId_mismatch",
          ].includes(issue)
        )
      ? "Needs Review"
      : issues.length
      ? "Safe"
      : "Safe";

  return {
    quoteId,
    quoteNo: cleanQuoteIdentityText(expected.quoteNo || quoteNo),
    issues: uniqueValues(issues),
    risk,
    recommendedAction: issues.length
      ? risk === "Safe"
        ? "Review each repair mode separately; missing links or dates do not authorize display or financial changes."
        : "Review linked customer/deal/project fields before repair."
      : "No quote_index mismatch detected.",
    expected,
    titleMeta,
  };
};

const buildQuoteIndexModePreview = ({ fullQuote, current, expected, mode }) => {
  const title = buildInternalSavedQuoteCardTitle({
    customerName: expected.customerName,
    siteName: expected.siteName,
    quoteNo: expected.quoteNo,
    contactPersonName: expected.contactPersonName,
  });
  return buildQuoteIndexRepairPreview({
    fullQuote, current, mode,
    expected: { ...expected, cardTitle: title, displayTitle: title, searchTitle: title },
  });
};

const compactCustomerSearchIndexPayload = (payload = {}) =>
  Object.entries(payload || {}).reduce((result, [key, value]) => {
    if (typeof value === "undefined" || typeof value === "function") return result;
    if (typeof value === "number" && !Number.isFinite(value)) return result;
    result[key] = value;
    return result;
  }, {});

const buildCustomerSearchIndexWrite = (record = {}, options = {}) => {
  const sourceType = String(options.sourceType || record.sourceType || "customer").trim();
  const indexRecord = buildCustomerSearchIndexRecord(record, {
    sourceType,
    workspaceUid: options.workspaceUid || record.workspaceUid || "",
  });
  const personId = buildCustomerSearchIndexId(indexRecord);
  if (!personId || !indexRecord.displayName) return null;

  const customerId = String(indexRecord.customerId || record.customerId || record.customerKey || "").trim();
  const crmLeadId = String(indexRecord.crmLeadId || record.crmLeadId || record.leadId || "").trim();
  const quoteId = String(indexRecord.quoteId || record.quoteId || record.id || "").trim();
  const payload = compactCustomerSearchIndexPayload({
    ...indexRecord,
    id: personId,
    personId,
    workspaceUid: options.workspaceUid || indexRecord.workspaceUid || "",
    sourceTypes: arrayUnion(sourceType),
    ...(customerId ? { customerIds: arrayUnion(customerId), customerId, customerKey: customerId } : {}),
    ...(crmLeadId ? { crmLeadIds: arrayUnion(crmLeadId), crmLeadId, leadId: crmLeadId } : {}),
    ...(quoteId
      ? {
          quoteIds: arrayUnion(quoteId),
          latestQuoteId: indexRecord.latestQuoteId || quoteId,
          latestQuoteNo: indexRecord.latestQuoteNo || indexRecord.quoteNo || "",
        }
      : {}),
    updatedAt: serverTimestamp(),
  });
  if (sourceType === "quote_customer") {
    delete payload.quoteCount;
    if (indexRecord.siteName || indexRecord.siteAddress) {
      payload.recentSites = arrayUnion({
        siteName: indexRecord.siteName || "",
        siteAddress: indexRecord.siteAddress || "",
        latestQuoteId: quoteId || "",
        latestQuoteNo: indexRecord.latestQuoteNo || indexRecord.quoteNo || "",
      });
    }
  }

  return {
    personId,
    payload,
    localRecord: {
      ...indexRecord,
      id: personId,
      personId,
      sourceTypes: uniqueValues([sourceType, ...(indexRecord.sourceTypes || [])]),
    },
  };
};

const normalizeQuoteIndexRecordForList = (id, raw = {}, normalizeTs = (value) => value) => {
  raw = stripFirestoreSentinelsForState(raw) || {};
  const quoteIndexId = String(raw.quoteIndexId || raw.indexId || id || "").trim();
  const sourceQuoteId = String(
    raw.sourceQuoteId ||
      raw.quotationId ||
      raw.sourceId ||
      raw.fullQuoteId ||
      raw.quoteId ||
      id ||
      ""
  ).trim();
  const quoteId = String(raw.quoteId || sourceQuoteId || id || "").trim();
  const indexPayload = buildQuoteIndexPayload(
    {
      ...raw,
      id: quoteId,
      quoteId,
      quoteIndexId,
      indexId: quoteIndexId,
      sourceQuoteId,
      sourceCollection: raw.sourceCollection || "quotations",
    },
    quoteId
  );
  const isInBin =
    isCrmRecordUnavailable(raw) || Boolean(raw.deletedAt || raw.movedToBinAt) ||
    raw.binStatus === "in_bin" ||
    raw.visibilityStatus === "bin" ||
    raw.deleted === true ||
    raw.archived === true ||
    indexPayload.deleted === true;
  return {
    ...indexPayload,
    id: quoteId,
    quoteId,
    indexId: quoteIndexId,
    quoteIndexId,
    indexRecordRaw: raw,
    indexRecordRawKeys: Object.keys(raw || {}),
    sourceQuoteId: sourceQuoteId || quoteId,
    sourceCollection: raw.sourceCollection || indexPayload.sourceCollection || "quotations",
    sourcePath: raw.sourcePath || indexPayload.sourcePath || "",
    staleSource: raw.staleSource === true,
    sourceMissingAt: normalizeTs(raw.sourceMissingAt),
    quoteType: indexPayload.quoteType,
    quoteTypeResolved: indexPayload.quoteTypeResolved,
    normalizedQuoteType: indexPayload.normalizedQuoteType,
    quoteTypeLabel: indexPayload.quoteTypeLabel,
    rows: [],
    effectiveRows: [],
    generalQuote: null,
    generalQuoteData: null,
    lineItems: undefined,
    items: undefined,
    subtotal: Number(indexPayload.subtotal || 0),
    grandTotal: Number(indexPayload.grandTotal || 0),
    totalAmount: Number(indexPayload.grandTotal || 0),
    total: Number(indexPayload.grandTotal || 0),
    finalTotal: Number(indexPayload.grandTotal || 0),
    finalAmount: Number(indexPayload.grandTotal || 0),
    taxableAmount: Number(indexPayload.taxableAmount || 0),
    totalGst: Number(indexPayload.totalGst || 0),
    gstAmount: Number(indexPayload.gstAmount || 0),
    itemCount: Number(raw.itemCount ?? raw.contentItemCount ?? indexPayload.itemCount ?? 0) || 0,
    rowCount: Number(raw.rowCount ?? raw.contentRowCount ?? indexPayload.rowCount ?? 0) || 0,
    contentItemCount:
      Number(raw.contentItemCount ?? raw.itemCount ?? indexPayload.contentItemCount ?? 0) || 0,
    contentRowCount:
      Number(raw.contentRowCount ?? raw.rowCount ?? indexPayload.contentRowCount ?? 0) || 0,
    contentCountKnown:
      raw.contentCountKnown === true || indexPayload.contentCountKnown === true,
    contentSchemaVersion: raw.contentSchemaVersion || indexPayload.contentSchemaVersion || "",
    canonicalContentField: raw.canonicalContentField || indexPayload.canonicalContentField || "",
    dataHealthStatus: raw.dataHealthStatus || indexPayload.dataHealthStatus || "",
    indexContentMismatch:
      raw.indexContentMismatch === true || indexPayload.indexContentMismatch === true,
    quoteDate: indexPayload.quoteDate,
    date: indexPayload.date,
    quotationDate: indexPayload.quotationDate,
    createdAt: normalizeTs(indexPayload.createdAt),
    updatedAt: normalizeTs(indexPayload.updatedAt),
    lastActivityAt: normalizeTs(indexPayload.lastActivityAt),
    lastEditedAt: normalizeTs(indexPayload.lastEditedAt),
    lastEditedBy: raw.lastEditedBy || raw.updatedBy || indexPayload.lastEditedBy || "",
    lastEditedByName:
      raw.lastEditedByName || raw.updatedByName || indexPayload.lastEditedByName || "",
    crmStatus: raw.crmStatus || indexPayload.crmStatus || "",
    orderStatus: raw.orderStatus || indexPayload.orderStatus || "",
    projectId: raw.projectId || indexPayload.projectId || "",
    opportunityId: raw.opportunityId || indexPayload.opportunityId || "",
    orderValue: Number(raw.orderValue ?? indexPayload.orderValue ?? 0) || 0,
    advanceAmount: Number(raw.advanceAmount ?? indexPayload.advanceAmount ?? 0) || 0,
    balanceAmount: Number(raw.balanceAmount ?? indexPayload.balanceAmount ?? 0) || 0,
    paymentStatus: raw.paymentStatus || indexPayload.paymentStatus || "",
    orderConfirmedAt: normalizeTs(raw.orderConfirmedAt || indexPayload.orderConfirmedAt),
    deleted: isInBin,
    cancelled: raw.cancelled === true || raw.isCancelled === true,
    archived: raw.archived === true || isInBin,
    binStatus: raw.binStatus || (isInBin ? "in_bin" : "active"),
    visibilityStatus: raw.visibilityStatus || (isInBin ? "bin" : "active"),
    deletedAt: normalizeTs(raw.deletedAt),
    movedToBinAt: normalizeTs(raw.movedToBinAt),
    restoredAt: normalizeTs(raw.restoredAt),
  };
};

const normalizeSavedQuoteSourcePath = (workspaceUidValue = "", sourcePath = "") => {
  const workspace = String(workspaceUidValue || "").trim();
  const path = String(sourcePath || "").trim();
  if (!workspace || !path) return "";
  const prefix = `users/${workspace}/quotations/`;
  if (!path.startsWith(prefix)) return "";
  const quoteId = path.slice(prefix.length).trim();
  if (!quoteId || quoteId.includes("/")) return "";
  return `${prefix}${quoteId}`;
};

const getSavedQuoteWorkflowIdentity = (quoteInput = {}, workspaceUidValue = "") => {
  const record =
    quoteInput && typeof quoteInput === "object" ? quoteInput : { id: quoteInput };
  const sourcePath = normalizeSavedQuoteSourcePath(
    workspaceUidValue,
    record.sourcePath || record.path || ""
  );
  const sourcePathQuoteId = sourcePath
    ? sourcePath.split("/").filter(Boolean).pop() || ""
    : "";
  const quoteIndexId = String(
    record.quoteIndexId ||
      record.indexId ||
      record.indexDocId ||
      record.id ||
      record.quoteId ||
      record.sourceQuoteId ||
      ""
  ).trim();
  const sourceQuoteId = String(
    sourcePathQuoteId ||
      record.sourceQuoteId ||
      record.quotationId ||
      record.sourceId ||
      record.fullQuoteId ||
      record.quoteId ||
      record.id ||
      quoteIndexId ||
      ""
  ).trim();
  const quoteId = String(record.quoteId || sourceQuoteId || record.id || quoteIndexId || "").trim();
  const fullQuoteId = sourcePathQuoteId || sourceQuoteId || quoteId || quoteIndexId;
  const fullQuotePath =
    sourcePath ||
    (workspaceUidValue && fullQuoteId
      ? `users/${workspaceUidValue}/quotations/${fullQuoteId}`
      : "");

  return {
    quoteIndexId: quoteIndexId || sourceQuoteId || fullQuoteId,
    quoteId,
    sourceQuoteId,
    fullQuoteId,
    sourcePath: fullQuotePath,
  };
};

const getSavedQuoteWorkflowMatchKeys = (quoteInput = {}, identity = {}) => {
  const record =
    quoteInput && typeof quoteInput === "object" ? quoteInput : { id: quoteInput };
  return new Set(
    [
      identity.quoteIndexId,
      identity.quoteId,
      identity.sourceQuoteId,
      identity.fullQuoteId,
      record.id,
      record.quoteId,
      record.quoteIndexId,
      record.indexId,
      record.sourceQuoteId,
      record.quotationId,
      record.fullQuoteId,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );
};

const savedQuoteMatchesWorkflowIdentity = (quote = {}, identity = {}) => {
  const record = quote && typeof quote === "object" ? quote : { id: quote };
  const targetKeys = new Set(
    [
      identity?.quoteIndexId,
      identity?.indexId,
      identity?.quoteId,
      identity?.sourceQuoteId,
      identity?.fullQuoteId,
      identity?.quoteNo,
      identity?.sourcePath,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );
  if (!targetKeys.size) return false;

  const recordIdentity = getSavedQuoteWorkflowIdentity(record);
  const recordKeys = new Set(
    [
      ...getQuoteIdentityCandidates(record),
      recordIdentity.quoteIndexId,
      recordIdentity.quoteId,
      recordIdentity.sourceQuoteId,
      recordIdentity.fullQuoteId,
      recordIdentity.sourcePath,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );

  return [...targetKeys].some((key) => recordKeys.has(key));
};

const getSavedQuoteWorkflowBusyKey = (quoteInput = {}, action = "", workspaceUidValue = "") => {
  const identity = getSavedQuoteWorkflowIdentity(quoteInput, workspaceUidValue);
  return `${identity.quoteIndexId || identity.fullQuoteId || identity.quoteId || "quote"}:${
    action || "action"
  }`;
};

const getSavedQuoteWorkflowFriendlyError = (error, fallback = "Could not update this quotation. Please try again.") => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "");
  if (code.includes("permission-denied") || message.includes("Missing or insufficient permissions")) {
    return "You do not have permission to update this quotation. Please contact your admin.";
  }
  if (code.includes("not-found") || message.toLowerCase().includes("not found")) {
    return "This quotation record could not be found. Please refresh and try again.";
  }
  if (isFirestoreResourceExhausted(error)) {
    return "Firebase usage limit reached. Please wait and try again.";
  }
  return fallback;
};

const classifyQuoteSaveError = (error) => {
  const code = String(error?.code || error?.customData?.code || error?.details?.code || "").toLowerCase();
  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || error || "").toLowerCase();
  const isNetworkError =
    code.includes("network-request-failed") ||
    code.includes("cancelled") ||
    message.includes("network-request-failed") ||
    message.includes("failed to fetch") ||
    message.includes("network error") ||
    message.includes("err_internet_disconnected") ||
    message.includes("err_name_not_resolved") ||
    message.includes("could not reach cloud firestore backend") ||
    message.includes("client will operate in offline mode");
  const isPermissionDenied =
    code.includes("permission-denied") ||
    message.includes("missing or insufficient permissions") ||
    message.includes("permission denied");
  const isUnavailable =
    code.includes("unavailable") ||
    code.includes("deadline-exceeded") ||
    name.includes("timeout") ||
    message.includes("deadline exceeded") ||
    message.includes("service unavailable") ||
    message.includes("firestore unavailable");
  const isFailedPrecondition =
    code.includes("failed-precondition") ||
    message.includes("failed precondition") ||
    message.includes("requires an index");

  return {
    code,
    isNetworkError,
    isPermissionDenied,
    isUnavailable,
    isFailedPrecondition,
  };
};

const getSaveQuotationFriendlyError = (error) => {
  const notice = quoteFailureNotice(error);
  if (notice) return notice.message;
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  const classification = classifyQuoteSaveError(error);
  const rowValidationDetail = getQuoteRowValidationDetailFromError(error);
  if (rowValidationDetail?.message) {
    return rowValidationDetail.message;
  }
  if (error?.__fqInvalidFirestoreValues?.length) {
    const firstInvalid = error.__fqInvalidFirestoreValues[0];
    return `Could not save quotation because row data is invalid at ${firstInvalid.path || "an unknown field"}.`;
  }
  if (
    error?.details?.code === "payload-too-large" ||
    error?.customData?.code === "payload-too-large" ||
    message.includes("payload is too large") ||
    message.includes("too large to save safely") ||
    message.includes("exceeds the maximum allowed size") ||
    message.includes("maximum allowed size") ||
    message.includes("1048576") ||
    message.includes("1,048,576")
  ) {
    return "This quotation is still too large to save safely. Please try again after refreshing; if it continues, send support the CURTAIN_QUOTE_PAYLOAD_SIZE_TRACE log.";
  }
  if (classification.isPermissionDenied) {
    return "You do not have permission to edit this quotation. Contact your account administrator.";
  }
  if (
    code.includes("trial_exhausted") ||
    message.includes("trial_exhausted") ||
    message.includes("trial exhausted") ||
    message.includes("free trial")
  ) {
    return "Your free trial is complete. Please contact sales to create new quotations.";
  }
  if (code.includes("not-found") || message.includes("not found")) {
    return "We could not find the original quotation. Your unsaved changes are still available. Please contact your account administrator before saving again.";
  }
  if (classification.isNetworkError || classification.isUnavailable) {
    return "Unable to save right now. Your unsaved changes are still available. Please try again.";
  }
  if (classification.isFailedPrecondition) {
    return "Save failed because Firebase could not complete the required save check. Please contact support.";
  }
  if (
    code.includes("invalid-argument") ||
    message.includes("unsupported field value") ||
    message.includes("invalid data")
  ) {
    if (error?.message && !/invalid-argument/i.test(error.message)) {
      return error.message;
    }
    return "Could not save quotation because some row data is invalid. Please check the highlighted row values.";
  }
  if (isFirestoreResourceExhausted(error)) {
    return "Firebase usage limit reached. Please wait and try again.";
  }
  return "Unable to save right now. Your unsaved changes are still available. Please try again.";
};

const resolveExistingQuoteSaveTarget = ({
  workspaceUid: workspaceUidValue = "",
  loadedQuoteMeta = {},
  quoteIndexDoc = {},
  quoteType = "",
} = {}) => {
  const workspace = String(workspaceUidValue || "").trim();
  const sourceRecord =
    loadedQuoteMeta && typeof loadedQuoteMeta === "object" ? loadedQuoteMeta : {};
  const indexRecord =
    quoteIndexDoc && typeof quoteIndexDoc === "object" ? quoteIndexDoc : {};
  const normalizedSourcePath =
    normalizeSavedQuoteSourcePath(workspace, sourceRecord.sourcePath || "") ||
    normalizeSavedQuoteSourcePath(workspace, sourceRecord.indexSourcePath || "") ||
    normalizeSavedQuoteSourcePath(workspace, indexRecord.sourcePath || "") ||
    normalizeSavedQuoteSourcePath(workspace, indexRecord.indexSourcePath || "");
  const sourcePathQuoteId = normalizedSourcePath
    ? normalizedSourcePath.split("/").filter(Boolean).pop() || ""
    : "";
  const fullQuoteId = String(
    sourcePathQuoteId ||
      sourceRecord.sourceQuoteId ||
      indexRecord.sourceQuoteId ||
      sourceRecord.quotationId ||
      indexRecord.quotationId ||
      sourceRecord.fullQuoteId ||
      indexRecord.fullQuoteId ||
      sourceRecord.quoteId ||
      indexRecord.quoteId ||
      sourceRecord.id ||
      ""
  ).trim();
  const quoteIndexId = String(
    sourceRecord.quoteIndexId ||
      sourceRecord.indexId ||
      sourceRecord.indexDocId ||
      indexRecord.quoteIndexId ||
      indexRecord.indexId ||
      indexRecord.indexDocId ||
      fullQuoteId ||
      ""
  ).trim();
  const sourcePath =
    normalizedSourcePath ||
    (workspace && fullQuoteId ? `users/${workspace}/quotations/${fullQuoteId}` : "");

  return {
    saveMode: fullQuoteId ? "update_existing" : "create_new",
    quoteType,
    quoteId: fullQuoteId,
    fullQuoteId,
    sourceQuoteId: fullQuoteId,
    sourcePath,
    quoteIndexId,
    quoteIndexPath:
      workspace && quoteIndexId ? `users/${workspace}/quote_index/${quoteIndexId}` : "",
    resolvedQuotePath: sourcePath,
  };
};

const getSavedQuoteOpenCandidates = (quoteInput, workspaceUidValue = "") => {
  const quoteIndex =
    quoteInput && typeof quoteInput === "object" ? quoteInput : { id: quoteInput };
  const indexId = String(
    quoteIndex.indexId || quoteIndex.quoteIndexId || quoteIndex.indexDocId || ""
  ).trim();
  const sourceQuoteId = String(
    quoteIndex.sourceQuoteId ||
      quoteIndex.quotationId ||
      quoteIndex.sourceId ||
      quoteIndex.fullQuoteId ||
      ""
  ).trim();
  const quotationId = String(
    quoteIndex.quotationId ||
      quoteIndex.quotationID ||
      quoteIndex.quotationDocId ||
      ""
  ).trim();
  const quoteId = String(quoteIndex.quoteId || quoteIndex.id || "").trim();
  const displayId = String(quoteIndex.id || "").trim();
  const candidatePaths = [];
  const pushPath = (path) => {
    const normalizedPath = normalizeSavedQuoteSourcePath(workspaceUidValue, path);
    if (normalizedPath && !candidatePaths.includes(normalizedPath)) {
      candidatePaths.push(normalizedPath);
    }
  };
  const pushId = (id) => {
    const value = String(id || "").trim();
    if (!value || value.includes("/")) return;
    pushPath(`users/${workspaceUidValue}/quotations/${value}`);
  };

  pushPath(quoteIndex.sourcePath);
  pushId(sourceQuoteId);
  pushId(quoteId);
  pushId(quotationId);
  pushId(displayId);
  pushId(indexId);

  return {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quotationId,
    quoteId,
    displayId,
    candidatePaths,
  };
};

const getCurtainQuoteCanonicalRows = (rawQuote = {}) => {
  return resolveCurtainQuoteContentSelection(rawQuote).items;
};

const isCurtainQuoteSubcollectionStorage = (quote = {}) =>
  Number(quote?.storageVersion || 0) >= QUOTE_V3_STORAGE_VERSION ||
  quote?.rowStorageMode === QUOTE_ROW_STORAGE_SUBCOLLECTION ||
  quote?.contentStorageField === QUOTE_V3_CONTENT_STORAGE_FIELD ||
  quote?.rowsStoredInSubcollection === true;

const getCanonicalQuotationContentInfo = (quote = {}, quoteTypeHint = "", options = {}) => {
  const allowStoredCount = options.allowStoredCount === true;
  const canonicalType = normalizeQuoteType(quoteTypeHint, quote);

  if (canonicalType === CANONICAL_GENERAL_QUOTE_TYPE) {
    const embedded =
      quote?.generalQuote && typeof quote.generalQuote === "object"
        ? quote.generalQuote
        : quote?.generalQuoteData && typeof quote.generalQuoteData === "object"
        ? quote.generalQuoteData
        : {};
    const selection = resolveGeneralQuoteContentSelection(quote, embedded, {
      allowCanonicalEmptyAliasRecovery: true,
    });
    const storedItemCount = Number(quote?.itemCount ?? quote?.contentItemCount ?? 0);
    if (
      allowStoredCount &&
      selection.count === 0 &&
      Number.isFinite(storedItemCount) &&
      storedItemCount > 0
    ) {
      return {
        canonicalType,
        canonicalField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
        field: "itemCount",
        count: Math.max(0, storedItemCount),
        items: [],
        known: true,
        recoveredFromCanonicalEmpty: false,
        aliasCounts: selection.aliasCounts || {},
        aliasIds: selection.aliasIds || {},
      };
    }
    return {
      canonicalType,
      canonicalField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      field: selection.field || "empty",
      count: selection.count || 0,
      items: selection.items || [],
      known: selection.known,
      recoveredFromCanonicalEmpty: selection.recoveredFromCanonicalEmpty === true,
      aliasCounts: selection.aliasCounts || {},
      aliasIds: selection.aliasIds || {},
    };
  }

  if (canonicalType === CANONICAL_CURTAIN_QUOTE_TYPE) {
    const selection = resolveCurtainQuoteContentSelection(quote);
    const storedRowCount = Number(quote?.rowCount ?? quote?.contentRowCount ?? 0);
    const isChunked =
      quote?.rowStorageMode === QUOTE_ROW_STORAGE_CHUNKED ||
      quote?.contentStorageField === "row_chunks";
    const isSubcollectionStored = isCurtainQuoteSubcollectionStorage(quote);
    if (
      (isChunked || isSubcollectionStored || allowStoredCount) &&
      Number.isFinite(storedRowCount) &&
      storedRowCount > 0 &&
      selection.count === 0
    ) {
      return {
        canonicalType,
        canonicalField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
        field: isSubcollectionStored ? QUOTE_V3_CONTENT_STORAGE_FIELD : isChunked ? "row_chunks" : "rowCount",
        count: Math.max(0, storedRowCount),
        items: [],
        known: true,
        recoveredFromCanonicalEmpty: false,
        aliasCounts: selection.aliasCounts || {},
        aliasIds: selection.aliasIds || {},
      };
    }
    return {
      canonicalType,
      canonicalField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
      field: selection.field || "empty",
      count: selection.count || 0,
      items: selection.items || [],
      known: selection.known,
      recoveredFromCanonicalEmpty: selection.recoveredFromCanonicalEmpty === true,
      aliasCounts: selection.aliasCounts || {},
      aliasIds: selection.aliasIds || {},
    };
  }

  return {
    canonicalType,
    canonicalField: "",
    field: "unknown",
    count: 0,
    items: [],
    known: false,
    recoveredFromCanonicalEmpty: false,
    aliasCounts: {},
    aliasIds: {},
  };
};

const createQuotePersistenceInvariantError = (message, details = {}) => {
  const error = new Error(message);
  error.code = details.code || "failed-precondition";
  error.__fqRequiredSaveStep = details.step || "canonical_quote_persistence_invariant";
  error.__fqPersistenceInvariant = details;
  return error;
};

const assertCanonicalQuotePersistenceInvariant = ({
  quotePayload = {},
  quoteIndexPayload = {},
  quoteTypeHint = "",
  saveMode = "",
  quoteId = "",
  quoteNo = "",
  quotePath = "",
  quoteIndexPath = "",
  existingContentCount = 0,
  allowExplicitEmptyContentSave = false,
} = {}) => {
  const isCurtainV3Header =
    normalizeQuoteType(quoteTypeHint, quotePayload) === CANONICAL_CURTAIN_QUOTE_TYPE &&
    isCurtainQuoteSubcollectionStorage(quotePayload);
  const fullContent = getCanonicalQuotationContentInfo(quotePayload, quoteTypeHint, {
    allowStoredCount: isCurtainV3Header,
  });
  const indexContent = getCanonicalQuotationContentInfo(quoteIndexPayload, quoteTypeHint, {
    allowStoredCount: true,
  });
  const displayTotal = getSavedQuoteDisplayTotal(quotePayload);
  const fullTotal = Number(displayTotal.amount || quotePayload?.grandTotal || 0);
  const isExistingSave = saveMode === "update_existing" || saveMode === "save_existing";
  const fullQuoteId = String(quotePayload?.quoteId || quotePayload?.sourceQuoteId || quoteId || "").trim();
  const indexSourceQuoteId = String(
    quoteIndexPayload?.sourceQuoteId || quoteIndexPayload?.quoteId || ""
  ).trim();
  const indexSourcePath = String(quoteIndexPayload?.sourcePath || "").trim();
  const expectedSourcePath = String(quotePath || "").trim();
  const failures = [];
  const topLevelGeneralItemsCount = Array.isArray(quotePayload?.items)
    ? quotePayload.items.length
    : null;

  if (!quoteId) failures.push("quote_id_missing");
  if (fullQuoteId && quoteId && fullQuoteId !== quoteId) failures.push("full_quote_id_mismatch");
  if (indexSourceQuoteId && quoteId && indexSourceQuoteId !== quoteId) {
    failures.push("quote_index_source_quote_id_mismatch");
  }
  if (expectedSourcePath && indexSourcePath && indexSourcePath !== expectedSourcePath) {
    failures.push("quote_index_source_path_mismatch");
  }
  if (
    fullContent.canonicalType === CANONICAL_GENERAL_QUOTE_TYPE &&
    fullContent.count > 0 &&
    (topLevelGeneralItemsCount == null || topLevelGeneralItemsCount !== fullContent.count)
  ) {
    failures.push("general_top_level_items_missing");
  }
  if (
    [CANONICAL_GENERAL_QUOTE_TYPE, CANONICAL_CURTAIN_QUOTE_TYPE].includes(fullContent.canonicalType) &&
    fullTotal > 0.05 &&
    fullContent.count === 0
  ) {
    failures.push("full_quote_content_missing_for_nonzero_total");
  }
  if (
    isExistingSave &&
    Number(existingContentCount || 0) > 0 &&
    fullContent.count === 0 &&
    !allowExplicitEmptyContentSave
  ) {
    failures.push("unsafe_empty_content_overwrite");
  }
  if (
    [CANONICAL_GENERAL_QUOTE_TYPE, CANONICAL_CURTAIN_QUOTE_TYPE].includes(fullContent.canonicalType) &&
    indexContent.known &&
    fullContent.count !== indexContent.count
  ) {
    failures.push("quote_index_content_count_mismatch");
  }

  if (failures.length) {
    throw createQuotePersistenceInvariantError(
      "Quotation save blocked because the full quotation document and quote_index would not remain consistent.",
      {
        code: failures.includes("general_top_level_items_missing")
          ? "general-top-level-items-missing"
          : failures.includes("unsafe_empty_content_overwrite")
          ? "unsafe-empty-content-overwrite"
          : "canonical-quote-invariant-failed",
        step: "canonical_quote_persistence_invariant",
        quoteId,
        quoteNo,
        quotePath,
        quoteIndexPath,
        saveMode,
        failures,
        fullContent: {
          field: fullContent.field,
          count: fullContent.count,
          canonicalField: fullContent.canonicalField,
          recoveredFromCanonicalEmpty: fullContent.recoveredFromCanonicalEmpty,
          hasTopLevelItems: Array.isArray(quotePayload?.items),
          topLevelItemsCount: topLevelGeneralItemsCount,
          aliasCounts: fullContent.aliasCounts || {},
          aliasIds: fullContent.aliasIds || {},
          storageVersion: quotePayload?.storageVersion || null,
          rowStorageMode: quotePayload?.rowStorageMode || "",
          contentStorageField: quotePayload?.contentStorageField || "",
        },
        indexContent: {
          field: indexContent.field,
          count: indexContent.count,
          canonicalField: indexContent.canonicalField,
        },
        existingContentCount: Number(existingContentCount || 0),
        fullTotal,
        indexSourceQuoteId,
        indexSourcePath,
      }
    );
  }

  return { fullContent, indexContent };
};

const assertCanonicalSaveResponse = ({
  result = {},
  quoteType = "",
  quoteId = "",
  quoteNo = "",
  sourcePath = "",
  indexPath = "",
  allowExplicitEmptyContentSave = false,
} = {}) => {
  const normalizedType = String(quoteType || result.quoteType || "").trim().toLowerCase();
  const contentCount = Number(
    normalizedType === GENERAL_QUOTE_TYPE
      ? result.itemCount ?? result.contentCount ?? 0
      : result.rowCount ?? result.contentCount ?? 0
  );
  const failures = [];
  if (result?.ok !== true) failures.push("ok_not_true");
  if (result?.verified !== true) failures.push("verified_not_true");
  if (quoteId && result?.quoteId && String(result.quoteId) !== String(quoteId)) {
    failures.push("quote_id_mismatch");
  }
  if (sourcePath && result?.sourcePath && String(result.sourcePath) !== String(sourcePath)) {
    failures.push("source_path_mismatch");
  }
  if (indexPath && result?.indexPath && String(result.indexPath) !== String(indexPath)) {
    failures.push("index_path_mismatch");
  }
  if (
    normalizedType === GENERAL_QUOTE_TYPE &&
    contentCount <= 0 &&
    !allowExplicitEmptyContentSave
  ) {
    failures.push("general_quote_item_count_missing");
  }

  if (failures.length) {
    const error = new Error("Quotation could not be saved safely. Please try again.");
    error.code = "canonical-save-verification-failed";
    error.__fqRequiredSaveStep = "saveQuotationCanonical.verify_response";
    error.__fqCanonicalSaveVerificationFailures = failures;
    error.__fqCanonicalSaveResponse = {
      ok: result?.ok,
      verified: result?.verified,
      quoteId: result?.quoteId,
      quoteNo: result?.quoteNo || quoteNo,
      sourcePath: result?.sourcePath || "",
      indexPath: result?.indexPath || "",
      contentCount,
    };
    throw error;
  }

  return {
    contentCount,
    itemCount: Number(result.itemCount ?? 0),
    rowCount: Number(result.rowCount ?? 0),
  };
};

const getContentAliasIds = (items = [], getId = (item) => item?.id || item?.itemId || "") =>
  Array.isArray(items)
    ? items.map((item) => String(getId(item) || "").trim()).filter(Boolean)
    : [];

const getGeneralQuoteContentAliasArrays = (quote = {}) => {
  const generalQuote =
    quote?.generalQuote && typeof quote.generalQuote === "object" ? quote.generalQuote : {};
  const generalQuoteData =
    quote?.generalQuoteData && typeof quote.generalQuoteData === "object"
      ? quote.generalQuoteData
      : {};
  const quotation =
    quote?.quotation && typeof quote.quotation === "object" ? quote.quotation : {};
  const data = quote?.data && typeof quote.data === "object" ? quote.data : {};
  return {
    items: quote?.items,
    lineItems: quote?.lineItems,
    products: quote?.products,
    rows: quote?.rows,
    generalRows: quote?.generalRows,
    quoteRows: quote?.quoteRows,
    generalItems: quote?.generalItems,
    "generalQuote.items": generalQuote.items,
    "generalQuote.lineItems": generalQuote.lineItems,
    "generalQuote.products": generalQuote.products,
    "generalQuote.rows": generalQuote.rows,
    "generalQuote.generalRows": generalQuote.generalRows,
    "generalQuote.quoteRows": generalQuote.quoteRows,
    "generalQuote.generalItems": generalQuote.generalItems,
    "generalQuoteData.items": generalQuoteData.items,
    "generalQuoteData.lineItems": generalQuoteData.lineItems,
    "generalQuoteData.products": generalQuoteData.products,
    "generalQuoteData.rows": generalQuoteData.rows,
    "generalQuoteData.generalRows": generalQuoteData.generalRows,
    "generalQuoteData.quoteRows": generalQuoteData.quoteRows,
    "generalQuoteData.generalItems": generalQuoteData.generalItems,
    "quotation.items": quotation.items,
    "quotation.products": quotation.products,
    "data.items": data.items,
    "data.products": data.products,
  };
};

const getGeneralQuoteHydrationCandidateCounts = (quote = {}) =>
  Object.fromEntries(
    Object.entries(getGeneralQuoteContentAliasArrays(quote)).map(([field, value]) => [
      field,
      Array.isArray(value) ? value.length : 0,
    ])
  );

const getCurtainQuoteContentAliasArrays = (quote = {}) => ({
  rows: quote?.rows,
  curtainRows: quote?.curtainRows,
  quoteRows: quote?.quoteRows,
  measurementRows: quote?.measurementRows,
  quotationRows: quote?.quotationRows,
  items: quote?.items,
});

const inspectDeletedIdsInContentAliases = (
  aliasArrays = {},
  deletedIds = [],
  getId = (item) => item?.id || item?.itemId || ""
) => {
  const deletedSet = new Set(
    uniqueValues(deletedIds).map((value) => String(value || "").trim()).filter(Boolean)
  );
  const aliasFindings = {};
  const aliasCounts = {};
  Object.entries(aliasArrays || {}).forEach(([field, value]) => {
    const ids = getContentAliasIds(value, getId);
    aliasCounts[field] = ids.length;
    const matches = ids.filter((id) => deletedSet.has(id));
    if (matches.length) {
      aliasFindings[field] = uniqueValues(matches);
    }
  });
  const deletedIdsStillPresent = uniqueValues(Object.values(aliasFindings).flat());
  return {
    aliasCounts,
    aliasFindings,
    deletedIdsStillPresent,
    pass: deletedIdsStillPresent.length === 0,
  };
};

const isGeneralQuoteTypeHint = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return (
    normalized.includes("general") ||
    normalized.includes("custom") ||
    normalized.includes("noncurtain")
  );
};

const normalizeGeneralQuoteForEditor = (rawQuote = {}, indexRecord = {}) => {
  const embedded =
    rawQuote.generalQuote && typeof rawQuote.generalQuote === "object"
      ? rawQuote.generalQuote
      : rawQuote.generalQuoteData && typeof rawQuote.generalQuoteData === "object"
      ? rawQuote.generalQuoteData
      : {};
  const resolvedQuoteNo = String(
    rawQuote.quoteNo ||
      rawQuote.quoteNumber ||
      rawQuote.quotationNumber ||
      embedded.quoteNo ||
      indexRecord.quoteNo ||
      indexRecord.quoteNoString ||
      rawQuote.id ||
      ""
  ).trim();
  const resolvedIdentity = resolveQuoteIdentityParts({
    ...indexRecord,
    ...rawQuote,
    ...embedded,
  });
  const resolvedCustomerName = resolvedIdentity.customerName;
  const resolvedDate =
    resolveQuoteDateForInput({
      ...rawQuote,
      generalQuote: embedded,
      quoteDate:
        rawQuote.quoteDate ||
        rawQuote.date ||
        rawQuote.quotationDate ||
        embedded.quoteDate ||
        indexRecord.quoteDate ||
        indexRecord.date ||
        "",
    }) ||
    resolveQuoteDateForInput(indexRecord) ||
    "";
  const contentSelection = resolveGeneralQuoteContentSelection(rawQuote, embedded, {
    allowCanonicalEmptyAliasRecovery: true,
  });
  const resolvedItems = contentSelection.items;
  const subtotal = Number(
    rawQuote.subtotal ??
      rawQuote.subTotal ??
      rawQuote.totals?.subtotal ??
      rawQuote.totals?.subTotal ??
      rawQuote.summary?.subtotal ??
      rawQuote.summary?.subTotal ??
      embedded.subtotal ??
      embedded.subTotal ??
      embedded.totals?.subtotal ??
      embedded.summary?.subtotal ??
      indexRecord.subtotal ??
      indexRecord.subTotal ??
      indexRecord.totalAmount ??
      0
  );
  const rawTotalSnapshot = getSavedQuoteDisplayTotal(rawQuote);
  const embeddedTotalSnapshot = getSavedQuoteDisplayTotal(embedded);
  const indexTotalSnapshot = getSavedQuoteDisplayTotal(indexRecord);
  const grandTotal = Number(
    rawTotalSnapshot.hasAmount
      ? rawTotalSnapshot.amount
      : embeddedTotalSnapshot.hasAmount
      ? embeddedTotalSnapshot.amount
      : indexTotalSnapshot.hasAmount
      ? indexTotalSnapshot.amount
      : 0
  );
  const warnings = [];
  if (!resolvedItems.length && grandTotal > 0) {
    warnings.push("No item rows found while saved total is greater than zero.");
  }

  if (isDevRuntime && contentSelection.recoveredFromCanonicalEmpty) {
    devWarn("QUOTE_LOAD_CANONICAL_EMPTY_RECOVERED_FROM_ALIAS", {
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: resolvedQuoteNo,
      quoteId: rawQuote.id || indexRecord.sourceQuoteId || indexRecord.quoteId || "",
      canonicalContentField: rawQuote.canonicalContentField || "",
      contentSchemaVersion: rawQuote.contentSchemaVersion || "",
      recoveredFromField: contentSelection.field,
      selectedLoadCount: contentSelection.count,
      aliasCounts: contentSelection.aliasCounts,
      aliasIds: contentSelection.aliasIds,
    });
  }

  if (isDevRuntime) {
    devLog("GENERAL_QUOTE_NORMALIZE_TRACE", {
      quoteNo: resolvedQuoteNo,
      rawKeys: Object.keys(rawQuote || {}),
      resolvedCustomerName,
      resolvedQuoteNo,
      resolvedDate,
      rowCount: resolvedItems.length,
      selectedLoadField: contentSelection.field,
      subtotal: Number.isFinite(subtotal) ? subtotal : 0,
      grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
      warnings,
    });
  }

  return {
    ...embedded,
    id: embedded.id || rawQuote.id || indexRecord.sourceQuoteId || indexRecord.quoteId || "",
    quoteNo: resolvedQuoteNo,
    quoteDate: resolvedDate,
    date: resolvedDate,
    quotationDate: resolvedDate,
    customerId: embedded.customerId || rawQuote.customerId || indexRecord.customerId || "",
    customerName: resolvedCustomerName || embedded.customerName || "",
    contactPersonName:
      embedded.contactPersonName ||
      rawQuote.contactPersonName ||
      indexRecord.contactPersonName ||
      resolvedIdentity.contactPersonName ||
      "",
    contactPersonPhone:
      embedded.contactPersonPhone ||
      rawQuote.contactPersonPhone ||
      indexRecord.contactPersonPhone ||
      resolvedIdentity.contactPersonPhone ||
      "",
    customerPhone:
      embedded.customerPhone ||
      rawQuote.customerPhone ||
      rawQuote.phone ||
      indexRecord.customerPhone ||
      "",
    customerWhatsapp:
      embedded.customerWhatsapp ||
      rawQuote.customerWhatsapp ||
      rawQuote.whatsapp ||
      indexRecord.customerWhatsapp ||
      "",
    customerEmail:
      embedded.customerEmail ||
      rawQuote.customerEmail ||
      rawQuote.email ||
      indexRecord.customerEmail ||
      "",
    customerAddress:
      embedded.customerAddress ||
      rawQuote.customerAddress ||
      rawQuote.address ||
      "",
    ...normalizeQuoteSiteSnapshot(
      {
        ...indexRecord,
        ...rawQuote,
        ...embedded,
      },
      { customerName: resolvedCustomerName }
    ),
    quoteFor: {
      ...(embedded.quoteFor || rawQuote.quoteFor || {}),
      name:
        resolvedCustomerName ||
        embedded.quoteFor?.name ||
        rawQuote.quoteFor?.name ||
        resolvedCustomerName,
      company:
        embedded.quoteFor?.company || rawQuote.quoteFor?.company || resolvedCustomerName || "",
      phone:
        embedded.quoteFor?.phone ||
        rawQuote.quoteFor?.phone ||
        rawQuote.customerPhone ||
        "",
      email:
        embedded.quoteFor?.email ||
        rawQuote.quoteFor?.email ||
        rawQuote.customerEmail ||
        rawQuote.email ||
        "",
      address:
        embedded.quoteFor?.address ||
        rawQuote.quoteFor?.address ||
        rawQuote.customerAddress ||
        rawQuote.address ||
        "",
    },
    lineItems: resolvedItems,
    items: resolvedItems,
    products: resolvedItems,
    rows: resolvedItems,
    generalRows: resolvedItems,
    quoteRows: resolvedItems,
    generalItems: resolvedItems,
    contentSchemaVersion:
      rawQuote.contentSchemaVersion ||
      embedded.contentSchemaVersion ||
      "",
    canonicalContentField:
      rawQuote.canonicalContentField ||
      embedded.canonicalContentField ||
      "",
    contentUpdatedAt:
      rawQuote.contentUpdatedAt ||
      embedded.contentUpdatedAt ||
      "",
    contentLoadField: contentSelection.field,
    contentLoadCount: contentSelection.count,
    contentAliasCounts: contentSelection.aliasCounts,
    contentRecoveredFromCanonicalEmpty: contentSelection.recoveredFromCanonicalEmpty,
    loadedGrandTotalFallback: Number.isFinite(grandTotal) ? grandTotal : 0,
    savedGrandTotalFallback: Number.isFinite(grandTotal) ? grandTotal : 0,
    preserveLoadedGrandTotalFallback:
      Number.isFinite(grandTotal) && grandTotal > 0 && resolvedItems.length > 0,
    dataHealthStatus:
      rawQuote.dataHealthStatus && rawQuote.dataHealthStatus !== "content_missing_index_stale"
        ? rawQuote.dataHealthStatus
        : grandTotal > 0 && resolvedItems.length === 0
        ? "content_missing_index_stale"
        : "ok",
    indexContentMismatch:
      rawQuote.indexContentMismatch === true ||
      (grandTotal > 0 && resolvedItems.length === 0),
    subtotal: Number.isFinite(subtotal) ? subtotal : 0,
    grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
    finalTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
    total: Number.isFinite(grandTotal) ? grandTotal : 0,
    taxableAmount: Number(rawQuote.taxableAmount ?? embedded.taxableAmount ?? 0) || 0,
    gstAmount: Number(rawQuote.gstAmount ?? embedded.gstAmount ?? 0) || 0,
  };
};

const normalizeSavedQuoteForEditor = (rawQuote = {}, quoteIndex = {}) => {
  rawQuote = stripFirestoreSentinelsForState(rawQuote) || {};
  quoteIndex = stripFirestoreSentinelsForState(quoteIndex) || {};
  const quoteIndexId = String(
    quoteIndex.quoteIndexId ||
      quoteIndex.indexId ||
      quoteIndex.indexDocId ||
      ""
  ).trim();
  const sourceQuoteId = String(
    quoteIndex.sourceQuoteId ||
      rawQuote.sourceQuoteId ||
      rawQuote.quotationId ||
      rawQuote.fullQuoteId ||
      rawQuote.quoteId ||
      rawQuote.id ||
      ""
  ).trim();
  const normalizedSourcePath = normalizeSavedQuoteSourcePath(
    quoteIndex.workspaceUid || rawQuote.workspaceUid || "",
    quoteIndex.sourcePath || rawQuote.sourcePath || ""
  );
  const id = String(
    rawQuote.id ||
      rawQuote.quoteId ||
      sourceQuoteId ||
      quoteIndex.quoteId ||
      quoteIndex.id ||
      ""
  ).trim();
  const explicitQuoteType =
    rawQuote.quoteType ||
    rawQuote.quoteTypeResolved ||
    quoteIndex.quoteType ||
    quoteIndex.quoteTypeResolved ||
    "";
  const quoteType =
    explicitQuoteType && isGeneralQuoteTypeHint(explicitQuoteType)
      ? GENERAL_QUOTE_TYPE
      : explicitQuoteType
      ? resolveQuoteType({ quoteType: explicitQuoteType })
      : resolveQuoteType(rawQuote);
  const quoteNo = String(
    rawQuote.quoteNo ||
      rawQuote.quoteNumber ||
      rawQuote.no ||
      quoteIndex.quoteNo ||
      quoteIndex.quoteNoString ||
      id ||
      ""
  ).trim();
  const quoteDate =
    resolveQuoteDateForInput(rawQuote) ||
    resolveQuoteDateForInput(quoteIndex) ||
    "";
  const savedQuoteIdentity = resolveQuoteIdentityParts({
    ...quoteIndex,
    ...rawQuote,
  });
  const customerName = savedQuoteIdentity.customerName;
  const customerPhone = String(
    rawQuote.customerPhone ||
      rawQuote.customerWhatsapp ||
      rawQuote.phone ||
      rawQuote.whatsapp ||
      rawQuote.quoteFor?.phone ||
      rawQuote.generalQuote?.quoteFor?.phone ||
      quoteIndex.customerPhone ||
      quoteIndex.customerWhatsapp ||
      ""
  ).trim();
  const subtotal = Number(
    rawQuote.subtotal ??
      rawQuote.subTotal ??
      rawQuote.totals?.subtotal ??
      rawQuote.totals?.subTotal ??
      rawQuote.summary?.subtotal ??
      rawQuote.summary?.subTotal ??
      quoteIndex.subtotal ??
      quoteIndex.subTotal ??
      0
  );
  const rawTotalSnapshot = getSavedQuoteDisplayTotal(rawQuote);
  const indexTotalSnapshot = getSavedQuoteDisplayTotal(quoteIndex);
  const total = Number(
    rawTotalSnapshot.hasAmount
      ? rawTotalSnapshot.amount
      : indexTotalSnapshot.hasAmount
      ? indexTotalSnapshot.amount
      : 0
  );
  const normalized = {
    ...rawQuote,
    id,
    quoteId: rawQuote.quoteId || id,
    sourceQuoteId: sourceQuoteId || id,
    fullQuoteId: sourceQuoteId || id,
    sourcePath: normalizedSourcePath || rawQuote.sourcePath || quoteIndex.sourcePath || "",
    quoteIndexId: quoteIndexId || quoteIndex.quoteIndexId || "",
    indexId: quoteIndexId || quoteIndex.indexId || "",
    quoteType,
    quoteTypeResolved: quoteType,
    quoteNo,
    quoteNoString: rawQuote.quoteNoString || quoteNo,
    customerName,
    contactPersonName:
      rawQuote.contactPersonName ||
      quoteIndex.contactPersonName ||
      savedQuoteIdentity.contactPersonName ||
      "",
    contactPersonPhone:
      rawQuote.contactPersonPhone ||
      quoteIndex.contactPersonPhone ||
      savedQuoteIdentity.contactPersonPhone ||
      "",
    customerPhone,
    customerWhatsapp: rawQuote.customerWhatsapp || quoteIndex.customerWhatsapp || customerPhone,
    customerEmail: rawQuote.customerEmail || rawQuote.email || quoteIndex.customerEmail || "",
    customerId: rawQuote.customerId || quoteIndex.customerId || "",
    customerKey: rawQuote.customerKey || quoteIndex.customerKey || "",
    ...normalizeQuoteSiteSnapshot(
      {
        ...quoteIndex,
        ...rawQuote,
      },
      { customerName }
    ),
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    subtotal: Number.isFinite(subtotal) ? subtotal : 0,
    grandTotal: Number.isFinite(total) ? total : 0,
    finalTotal: Number.isFinite(total) ? total : 0,
  };

  if (quoteType === GENERAL_QUOTE_TYPE) {
    const embedded = normalizeGeneralQuoteForEditor(normalized, quoteIndex);
    const contentSelection = resolveGeneralQuoteContentSelection(rawQuote, embedded, {
      allowCanonicalEmptyAliasRecovery: true,
    });
    const items = contentSelection.items;
    if (IS_DEV_UI_ENABLED) {
      const aliasArrays = getGeneralQuoteContentAliasArrays(rawQuote);
      devLog("QUOTE_LOAD_CONTENT_SOURCE", {
        quoteType: GENERAL_QUOTE_TYPE,
        quoteNo,
        quoteId: id,
        contentSchemaVersion: rawQuote.contentSchemaVersion || "",
        canonicalContentField: rawQuote.canonicalContentField || "",
        selectedField: contentSelection.field || "empty",
        selectedCount: contentSelection.count,
        recoveredFromCanonicalEmpty: contentSelection.recoveredFromCanonicalEmpty,
        aliasCounts: Object.fromEntries(
          Object.entries(aliasArrays).map(([field, value]) => [
            field,
            Array.isArray(value) ? value.length : 0,
          ])
        ),
        aliasIds: contentSelection.aliasIds,
      });
    }
    return {
      ...normalized,
      itemCount: items.length,
      contentItemCount: items.length,
      dataHealthStatus:
        normalized.dataHealthStatus &&
        normalized.dataHealthStatus !== "content_missing_index_stale"
          ? normalized.dataHealthStatus
          : normalized.grandTotal > 0 && items.length === 0
          ? "content_missing_index_stale"
          : "ok",
      indexContentMismatch:
        normalized.indexContentMismatch === true ||
        (normalized.grandTotal > 0 && items.length === 0),
      contentLoadField: contentSelection.field,
      contentLoadCount: contentSelection.count,
      contentRecoveredFromCanonicalEmpty: contentSelection.recoveredFromCanonicalEmpty,
      lineItems: items,
      items,
      products: items,
      rows: items,
      generalRows: items,
      quoteRows: items,
      generalItems: items,
      generalQuote: {
        ...embedded,
        id: embedded.id || id,
        quoteNo: embedded.quoteNo || quoteNo,
        quoteFor: {
          ...(embedded.quoteFor || {}),
          name: embedded.quoteFor?.name || customerName,
          phone: embedded.quoteFor?.phone || customerPhone,
        },
      items,
      lineItems: items,
      products: items,
      rows: items,
        generalRows: items,
        quoteRows: items,
        generalItems: items,
        loadedGrandTotalFallback: Number.isFinite(total) ? total : 0,
        savedGrandTotalFallback: Number.isFinite(total) ? total : 0,
        preserveLoadedGrandTotalFallback:
          Number.isFinite(total) && total > 0 && items.length > 0,
      },
      generalQuoteData: {
        ...(rawQuote.generalQuoteData && typeof rawQuote.generalQuoteData === "object"
          ? rawQuote.generalQuoteData
          : embedded),
        items,
        lineItems: items,
        products: items,
        rows: items,
        generalRows: items,
        quoteRows: items,
        generalItems: items,
        loadedGrandTotalFallback: Number.isFinite(total) ? total : 0,
        savedGrandTotalFallback: Number.isFinite(total) ? total : 0,
        preserveLoadedGrandTotalFallback:
          Number.isFinite(total) && total > 0 && items.length > 0,
      },
    };
  }

  const contentSelection = resolveCurtainQuoteContentSelection(rawQuote);
  const rows = contentSelection.items;
  if (IS_DEV_UI_ENABLED) {
    const aliasArrays = getCurtainQuoteContentAliasArrays(rawQuote);
    devLog("QUOTE_LOAD_CONTENT_SOURCE", {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      quoteId: id,
      contentSchemaVersion: rawQuote.contentSchemaVersion || "",
      canonicalContentField: rawQuote.canonicalContentField || "",
      selectedField: contentSelection.field || "empty",
      selectedCount: contentSelection.count,
      recoveredFromCanonicalEmpty: contentSelection.recoveredFromCanonicalEmpty,
      aliasCounts: Object.fromEntries(
        Object.entries(aliasArrays).map(([field, value]) => [
          field,
          Array.isArray(value) ? value.length : 0,
        ])
      ),
      aliasIds: contentSelection.aliasIds,
    });
  }
  return {
    ...normalized,
    rows,
    curtainRows: rows,
    quoteRows: rows,
    measurementRows: rows,
    quotationRows: rows,
    items: rows,
    rowCount: rows.length,
    contentRowCount: rows.length,
    dataHealthStatus:
      normalized.dataHealthStatus &&
      normalized.dataHealthStatus !== "content_missing_index_stale"
        ? normalized.dataHealthStatus
        : normalized.grandTotal > 0 && rows.length === 0
        ? "content_missing_index_stale"
        : "ok",
    indexContentMismatch:
      normalized.indexContentMismatch === true ||
      (normalized.grandTotal > 0 && rows.length === 0),
    contentLoadField: contentSelection.field,
    contentLoadCount: contentSelection.count,
    contentRecoveredFromCanonicalEmpty: contentSelection.recoveredFromCanonicalEmpty,
  };
};

const buildQuoteLoadMismatchAuditReport = ({
  quoteIndex = {},
  fullQuote = null,
  quoteIndexId = "",
  quoteId = "",
  sourcePath = "",
  triedPaths = [],
} = {}) => {
  const quoteNo = String(
    quoteIndex.quoteNo || quoteIndex.quoteNoString || fullQuote?.quoteNo || quoteId || ""
  ).trim();
  const normalizedType = normalizeQuoteType(
    fullQuote?.normalizedQuoteType ||
      fullQuote?.quoteType ||
      quoteIndex.normalizedQuoteType ||
      quoteIndex.quoteType ||
      quoteIndex.quotationType ||
      "",
    fullQuote || quoteIndex || {}
  );
  const isGeneral = normalizedType === CANONICAL_GENERAL_QUOTE_TYPE;
  const contentSelection = isGeneral
    ? resolveGeneralQuoteContentSelection(
        fullQuote || {},
        fullQuote?.generalQuote || fullQuote?.generalQuoteData || {},
        { allowCanonicalEmptyAliasRecovery: true }
      )
    : resolveCurtainQuoteContentSelection(fullQuote || {});
  const quoteIndexGrandTotal = Number(
    quoteIndex.grandTotal ??
      quoteIndex.finalTotal ??
      quoteIndex.totalAmount ??
      quoteIndex.total ??
      0
  );
  const fullQuoteGrandTotal = Number(
    fullQuote?.grandTotal ??
      fullQuote?.finalTotal ??
      fullQuote?.totalAmount ??
      fullQuote?.total ??
      0
  );
  const quoteIndexContentCount = isGeneral
    ? Number(quoteIndex.itemCount ?? quoteIndex.contentItemCount ?? 0)
    : Number(quoteIndex.rowCount ?? quoteIndex.contentRowCount ?? 0);
  const mismatchReasons = [];

  if (!fullQuote) {
    mismatchReasons.push("full_quote_missing");
  }
  if (contentSelection.recoveredFromCanonicalEmpty) {
    mismatchReasons.push("canonical_empty_recovered_from_alias");
  }
  if (
    fullQuote &&
    contentSelection.count === 0 &&
    (quoteIndexContentCount > 0 || quoteIndexGrandTotal > 0.05)
  ) {
    mismatchReasons.push("full_quote_content_missing_but_index_has_total_or_count");
  }
  if (
    fullQuote &&
    quoteIndexGrandTotal > 0 &&
    fullQuoteGrandTotal > 0 &&
    Math.abs(quoteIndexGrandTotal - fullQuoteGrandTotal) > 0.05
  ) {
    mismatchReasons.push("quote_index_total_differs_from_full_quote");
  }
  if (sourcePath && quoteIndex.sourcePath && sourcePath !== quoteIndex.sourcePath) {
    mismatchReasons.push("sourcePath_repaired_to_fallback_path");
  }

  return {
    quoteNo,
    quoteIndexId,
    quoteId,
    sourcePath: sourcePath || quoteIndex.sourcePath || "",
    triedPaths,
    quoteIndexGrandTotal,
    quoteIndexItemCount: Number(quoteIndex.itemCount ?? quoteIndex.contentItemCount ?? 0),
    quoteIndexRowCount: Number(quoteIndex.rowCount ?? quoteIndex.contentRowCount ?? 0),
    fullQuoteGrandTotal,
    fullQuoteSubtotal: Number(fullQuote?.subtotal ?? fullQuote?.subTotal ?? 0),
    contentSchemaVersion: fullQuote?.contentSchemaVersion || "",
    canonicalContentField: fullQuote?.canonicalContentField || "",
    selectedLoadField: contentSelection.field || "empty",
    selectedLoadCount: contentSelection.count,
    aliasCounts: contentSelection.aliasCounts,
    aliasIds: contentSelection.aliasIds,
    mismatchReason: mismatchReasons.join(", ") || "none",
  };
};

const classifySavedQuoteOpenFailure = ({
  readError = null,
  exists = false,
  normalizeError = null,
  wrongLoader = false,
  attemptedPaths = [],
} = {}) => {
  const error = normalizeError || readError;
  const code = String(error?.code || "");
  const notice = quoteFailureNotice(error || {});
  if (notice) return { result: notice.kind, message: notice.message, tone: notice.tone };
  if (wrongLoader) {
    return {
      result: "wrong-loader",
      message: "This quote exists, but was routed to the wrong quotation editor.",
      tone: "error",
    };
  }
  if (normalizeError) {
    return {
      result: "schema-error",
      message: "This quotation uses an older format. Please contact support.",
      tone: "error",
    };
  }
  if (code === "resource-exhausted") {
    return {
      result: "resource-exhausted",
      message: "Firebase read limit reached while opening this saved quote.",
      tone: "warning",
    };
  }
  if (code === "permission-denied") {
    return {
      result: "permission-denied",
      message: "You do not have permission to open this quotation.",
      tone: "error",
    };
  }
  if (!exists && attemptedPaths.length > 0 && !readError) {
    return {
      result: "not-found",
      message:
        "This quotation listing exists, but the full quotation record is missing. Please contact support.",
      tone: "warning",
    };
  }
  if (code === "not-found") {
    return {
      result: "not-found",
      message:
        "This quotation listing exists, but the full quotation record is missing. Please contact support.",
      tone: "warning",
    };
  }
  if (code === "unavailable" || code === "deadline-exceeded") {
    return {
      result: "network",
      message: "Network issue while opening this saved quote. Please try again.",
      tone: "warning",
    };
  }
  return {
    result: "unknown",
    message: "This quotation could not be opened. Please contact support.",
    tone: "error",
  };
};

const buildSafeCustomerSearchIndex = (customer = {}) => {
  const pieces = [];
  [
    customer.customerName,
    customer.name,
    customer.phone,
    customer.whatsapp,
    customer.email,
    customer.companyName,
    customer.source,
    customer.address,
    customer.siteName,
    customer.siteAddress,
    customer.quoteDisplayTitle,
    customer.tags,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));
  addSafeSiteLocationPieces(pieces, customer.siteLocations || []);
  const baseResult = buildSafeSearchResult(pieces);
  const searchText = baseResult.searchText.slice(0, CUSTOMER_SEARCH_TEXT_MAX_CHARS);
  const searchKeywords = baseResult.searchKeywords.slice(
    0,
    CUSTOMER_SEARCH_KEYWORDS_MAX
  );
  const searchPrefixSet = new Set();
  const phonePrefixSet = new Set();

  [
    customer.customerName,
    customer.name,
    customer.companyName,
    customer.siteName,
    customer.siteAddress,
    customer.quoteDisplayTitle,
    customer.source,
    customer.email,
    customer.normalizedName,
  ].forEach((value) => addCustomerSearchPrefixes(searchPrefixSet, value));
  [
    customer.phone,
    customer.whatsapp,
    customer.customerPhone,
    customer.customerWhatsapp,
    customer.normalizedPhone,
    customer.normalizedWhatsapp,
  ].forEach((value) => {
    addCustomerPhonePrefixes(phonePrefixSet, value);
    addCustomerSearchPrefixes(searchPrefixSet, normalizeCustomerSearchPhone(value));
  });

  return {
    searchText,
    searchKeywords,
    searchPrefixes: Array.from(searchPrefixSet).slice(
      0,
      CUSTOMER_SEARCH_PREFIXES_MAX
    ),
    phonePrefixes: Array.from(phonePrefixSet).slice(
      0,
      CUSTOMER_SEARCH_PREFIXES_MAX
    ),
  };
};

const MeasuredChartFrame = ({
  active = true,
  children,
  fallback = null,
  height = 220,
  style = {},
}) => {
  const frameRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    const node = frameRef.current;
    if (!node) return undefined;

    let animationFrame = 0;
    const measure = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const nextWidth = Math.floor(node.getBoundingClientRect().width || 0);
        setWidth((prevWidth) =>
          Math.abs(prevWidth - nextWidth) > 1 ? nextWidth : prevWidth
        );
      });
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      window.addEventListener("resize", measure);
      return () => {
        window.cancelAnimationFrame(animationFrame);
        window.removeEventListener("resize", measure);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", measure);
    };
  }, [active]);

  return (
    <div
      ref={frameRef}
      className="fabriquote-chart-shell"
      style={{
        ...style,
        width: "100%",
        minWidth: 0,
        height,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {active && width > 12
        ? children({ width, height })
        : fallback}
    </div>
  );
};

const getCrmReminderFollowUpKeys = (reminder = {}) => {
  const values = [
    reminder?.followUpId,
    reminder?.sourceTaskId,
    reminder?.reminderId,
  ];
  const idSuffix = String(reminder?.id || "").split("__").pop();
  if (idSuffix && idSuffix !== String(reminder?.id || "")) {
    values.push(idSuffix);
  }

  return new Set(values.map((value) => String(value || "").trim()).filter(Boolean));
};

const doesCrmReminderMatchFollowUp = (reminder = {}, followUp = {}) => {
  const keys = getCrmReminderFollowUpKeys(reminder);
  const followUpKeys = [
    followUp?.id,
    followUp?.reminderId,
    followUp?.sourceTaskId,
  ].map((value) => String(value || "").trim());

  return followUpKeys.some((value) => value && keys.has(value));
};

const TASK_HISTORY_TYPE_LABELS = {
  [TASK_HISTORY_TYPE_DEAL_ACTION]: "Deal Action",
  [TASK_HISTORY_TYPE_REMINDER]: "Reminder",
  [TASK_HISTORY_TYPE_CHECKLIST]: "Checklist",
  [TASK_HISTORY_TYPE_INSTALLATION]: "Installation",
};

const TASK_HISTORY_SOURCE_LABELS = {
  [TASK_HISTORY_SOURCE_CRM]: "CRM",
  [TASK_HISTORY_SOURCE_INSTALLATION]: "Installation",
  [TASK_HISTORY_SOURCE_EXECUTION_DETAILS]: "Execution Details",
};

let pdfRuntimePromise = null;
let zipRuntimePromise = null;

const loadPdfRuntime = async () => {
  if (!pdfRuntimePromise) {
    pdfRuntimePromise = Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ])
      .then(([jspdfModule, autoTableModule]) => ({
        jsPDF: jspdfModule.default || jspdfModule.jsPDF || jspdfModule,
        autoTable: autoTableModule.default || autoTableModule,
      }))
      .catch((error) => {
        pdfRuntimePromise = null;
        throw error;
      });
  }

  return pdfRuntimePromise;
};

const loadZipRuntime = async () => {
  if (!zipRuntimePromise) {
    zipRuntimePromise = Promise.all([
      import("jszip"),
      import("file-saver"),
    ])
      .then(([zipModule, fileSaverModule]) => ({
        JSZip: zipModule.default || zipModule,
        saveAs: fileSaverModule.saveAs || fileSaverModule.default,
      }))
      .catch((error) => {
        zipRuntimePromise = null;
        throw error;
      });
  }

  return zipRuntimePromise;
};

const preloadWorkshopZipRuntime = () => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return Promise.resolve(false);
  }

  return Promise.all([loadPdfRuntime(), loadZipRuntime()])
    .then(() => {
      resetChunkLoadReloadGuard();
      return true;
    })
    .catch((error) => {
      if (isChunkLoadError(error)) {
        handleChunkLoadErrorRecovery(error, {
          source: "preloadWorkshopZipRuntime",
          autoReload: false,
        });
      }
      return false;
    });
};

const buildLocalDraftKey = (workspaceUid, module, entityId = "new") =>
  `${LOCAL_DRAFT_PREFIX}:${String(workspaceUid || "anonymous").trim()}:${String(
    module || "unknown"
  ).trim()}:${String(entityId || "new").trim() || "new"}`;

const sanitizeLocalDraftValue = (value, seen = new WeakSet()) => {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString()
      : null;
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeLocalDraftValue(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  const output = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    const sanitized = sanitizeLocalDraftValue(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
  });
  return output;
};

const sanitizeGeneralQuoteImageMetadata = (image = {}) => {
  const normalized = createGeneralQuoteImage(image);
  const storagePath = getGeneralQuoteImageStoragePath(normalized);
  const fileName = String(normalized.filename || normalized.name || "").trim();
  const downloadURL = getGeneralQuoteImageDisplayUrl(normalized);

  return {
    id: String(normalized.id || createClientId("general-image")),
    imageId: String(normalized.imageId || normalized.id || "").trim(),
    fileId: String(normalized.fileId || "").trim(),
    name: fileName,
    filename: fileName,
    originalFilename: String(normalized.originalFilename || fileName).trim(),
    safeFilename: String(normalized.safeFilename || fileName).trim(),
    contentType: String(normalized.contentType || normalized.type || "").trim(),
    sizeBytes: Number.isFinite(Number(normalized.sizeBytes ?? normalized.size))
      ? Number(normalized.sizeBytes ?? normalized.size)
      : null,
    originalSizeBytes: Number.isFinite(Number(normalized.originalSizeBytes))
      ? Number(normalized.originalSizeBytes)
      : null,
    optimizedSizeBytes: Number.isFinite(Number(normalized.optimizedSizeBytes))
      ? Number(normalized.optimizedSizeBytes)
      : null,
    thumbnailSizeBytes: Number.isFinite(Number(normalized.thumbnailSizeBytes))
      ? Number(normalized.thumbnailSizeBytes)
      : null,
    compressionRatio: Number.isFinite(Number(normalized.compressionRatio))
      ? Number(normalized.compressionRatio)
      : null,
    width: Number.isFinite(Number(normalized.width)) ? Number(normalized.width) : null,
    height: Number.isFinite(Number(normalized.height)) ? Number(normalized.height) : null,
    originalWidth: Number.isFinite(Number(normalized.originalWidth))
      ? Number(normalized.originalWidth)
      : null,
    originalHeight: Number.isFinite(Number(normalized.originalHeight))
      ? Number(normalized.originalHeight)
      : null,
    fullPath: storagePath,
    downloadURL,
    thumbnailFullPath: String(normalized.thumbnailFullPath || "").trim(),
    thumbnailDownloadURL: String(normalized.thumbnailDownloadURL || "").trim(),
    bucket: String(normalized.storageBucket || normalized.bucket || "").trim(),
    storageBucket: String(normalized.storageBucket || "").trim(),
    url: downloadURL,
    previewUrl: String(normalized.thumbnailDownloadURL || normalized.previewUrl || downloadURL).trim(),
    path: storagePath,
    storagePath,
    size: Number.isFinite(Number(normalized.sizeBytes ?? normalized.size))
      ? Number(normalized.sizeBytes ?? normalized.size)
      : null,
    type: String(normalized.contentType || normalized.type || "").trim(),
    uploadedAt: String(normalized.uploadedAt || "").trim(),
    uploadedBy: String(normalized.uploadedBy || normalized.uploadedByUid || "").trim(),
    uploadStatus: String(normalized.uploadStatus || "").trim(),
    compressionStatus: String(normalized.compressionStatus || "").trim(),
    thumbnailStatus: String(normalized.thumbnailStatus || "").trim(),
    ownerType: String(normalized.ownerType || "").trim(),
    ownerId: String(normalized.ownerId || "").trim(),
    workspaceUid: String(normalized.workspaceUid || "").trim(),
    quoteId: String(normalized.quoteId || "").trim(),
    itemId: String(normalized.itemId || "").trim(),
    rowId: String(normalized.rowId || "").trim(),
    leadId: String(normalized.leadId || "").trim(),
    removedFromRecord: normalized.removedFromRecord === true,
    archived: normalized.archived === true,
    expiresAt: String(normalized.expiresAt || "").trim(),
    legacyUrl: String(normalized.legacyUrl || "").trim(),
    legacyPath: String(normalized.legacyPath || "").trim(),
    legacyFullPath: String(normalized.legacyFullPath || "").trim(),
    legacyMissingImage: normalized.legacyMissingImage || null,
    replacedMissingImage: normalized.replacedMissingImage === true,
    repairedFromMissing: normalized.repairedFromMissing === true,
    replacedAt: String(normalized.replacedAt || "").trim(),
    repairedFromPath: String(normalized.repairedFromPath || "").trim(),
    repairMethod: String(normalized.repairMethod || "").trim(),
    repairedAt: String(normalized.repairedAt || "").trim(),
    repairedBy: String(normalized.repairedBy || "").trim(),
    removedFromQuote: normalized.removedFromQuote === true,
    removedAt: String(normalized.removedAt || "").trim(),
    removedBy: String(normalized.removedBy || "").trim(),
    removalReason: String(normalized.removalReason || "").trim(),
    cleanupEligibleAt: String(normalized.cleanupEligibleAt || "").trim(),
    cleanupRetentionDays: Number.isFinite(Number(normalized.cleanupRetentionDays))
      ? Number(normalized.cleanupRetentionDays)
      : null,
    cleanupStatus: String(normalized.cleanupStatus || "").trim(),
    removedFromQuoteId: String(normalized.removedFromQuoteId || "").trim(),
    removedFromQuoteNo: String(normalized.removedFromQuoteNo || "").trim(),
    removedFromItemId: String(normalized.removedFromItemId || "").trim(),
    removedFromItemName: String(normalized.removedFromItemName || "").trim(),
  };
};

const sanitizeGeneralQuoteFirestoreValue = (value, seen = new WeakSet()) => {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (typeof Number.isNaN === "function" && typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString()
      : null;
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeGeneralQuoteFirestoreValue(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  return Object.entries(value).reduce((output, [key, entryValue]) => {
    const sanitized = sanitizeGeneralQuoteFirestoreValue(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
    return output;
  }, {});
};

const sanitizeGeneralQuoteItemForFirestore = (item = {}) => {
  const images = normalizeGeneralQuoteItemImages(item).map(
    sanitizeGeneralQuoteImageMetadata
  );
  const removedImages = Array.isArray(item.removedImages)
    ? item.removedImages.map(sanitizeGeneralQuoteImageMetadata)
    : [];
  return sanitizeGeneralQuoteFirestoreValue(
    syncGeneralQuoteItemPrimaryImageFields({
      ...item,
      images,
      removedImages,
    })
  );
};

function extractGeneralQuoteItems(payload = {}) {
  const generalQuote =
    payload?.generalQuote && typeof payload.generalQuote === "object"
      ? payload.generalQuote
      : {};
  const generalQuoteData =
    payload?.generalQuoteData && typeof payload.generalQuoteData === "object"
      ? payload.generalQuoteData
      : {};
  const quotation =
    payload?.quotation && typeof payload.quotation === "object" ? payload.quotation : {};
  const data = payload?.data && typeof payload.data === "object" ? payload.data : {};
  const candidates = [
    payload?.items,
    payload?.lineItems,
    payload?.products,
    payload?.generalRows,
    payload?.rows,
    payload?.quoteRows,
    payload?.quoteItems,
    payload?.entries,
    payload?.generalItems,
    generalQuote.lineItems,
    generalQuote.items,
    generalQuote.products,
    generalQuote.rows,
    generalQuote.quoteItems,
    generalQuote.entries,
    generalQuote.generalRows,
    generalQuote.quoteRows,
    generalQuote.generalItems,
    generalQuoteData.generalQuote?.items,
    generalQuoteData.generalQuote?.lineItems,
    generalQuoteData.generalQuote?.products,
    generalQuoteData.generalQuote?.rows,
    generalQuoteData.generalQuote?.quoteItems,
    generalQuoteData.generalQuote?.entries,
    generalQuoteData.generalQuote?.generalRows,
    generalQuoteData.generalQuote?.quoteRows,
    generalQuoteData.generalQuote?.generalItems,
    generalQuoteData.items,
    generalQuoteData.lineItems,
    generalQuoteData.products,
    generalQuoteData.rows,
    generalQuoteData.quoteItems,
    generalQuoteData.entries,
    generalQuoteData.generalRows,
    generalQuoteData.quoteRows,
    generalQuoteData.generalItems,
    quotation.items,
    quotation.lineItems,
    quotation.products,
    quotation.quoteItems,
    quotation.entries,
    data.items,
    data.lineItems,
    data.products,
    data.quoteItems,
    data.entries,
  ];
  return candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) ||
    candidates.find((candidate) => Array.isArray(candidate)) ||
    [];
}

const GENERAL_QUOTE_ITEM_ALIAS_FIELDS = [
  "items",
  "lineItems",
  "products",
  "rows",
  "generalRows",
  "quoteRows",
  "quoteItems",
  "entries",
  "generalItems",
];

const getNestedValueByPath = (source = {}, path = "") =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => (value && typeof value === "object" ? value[key] : undefined), source);

const getJsonSizeEstimateBytes = (value) => {
  try {
    const json = JSON.stringify(value);
    if (!json) return 0;
    if (typeof Blob !== "undefined") {
      return new Blob([json]).size;
    }
    return json.length;
  } catch {
    return 0;
  }
};

const stripGeneralQuoteItemAliasFields = (source = {}, options = {}) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) return source;
  const { dropEmbeddedGeneralQuoteContainers = false } = options;
  const next = { ...source };
  GENERAL_QUOTE_ITEM_ALIAS_FIELDS.forEach((field) => {
    delete next[field];
  });

  if (dropEmbeddedGeneralQuoteContainers) {
    delete next.generalQuote;
    delete next.generalQuoteData;
    delete next.quotation;
    delete next.data;
    return next;
  }

  ["generalQuote", "generalQuoteData", "quotation", "data"].forEach((field) => {
    if (next[field] && typeof next[field] === "object" && !Array.isArray(next[field])) {
      next[field] = stripGeneralQuoteItemAliasFields(next[field], options);
    }
  });

  return next;
};

const getGeneralQuoteDuplicateArrayFields = (payload = {}) => {
  const aliasPaths = [
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS,
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS.map((field) => `generalQuote.${field}`),
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS.map((field) => `generalQuoteData.${field}`),
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS.map((field) => `generalQuoteData.generalQuote.${field}`),
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS.map((field) => `quotation.${field}`),
    ...GENERAL_QUOTE_ITEM_ALIAS_FIELDS.map((field) => `data.${field}`),
  ];
  return aliasPaths
    .map((field) => {
      const value = getNestedValueByPath(payload, field);
      if (!Array.isArray(value)) return null;
      return {
        field,
        count: value.length,
        estimatedBytes: getJsonSizeEstimateBytes(value),
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.estimatedBytes - left.estimatedBytes);
};

const buildGeneralQuoteLegacyAliasDeletePatch = () => ({
  lineItems: deleteField(),
  products: deleteField(),
  rows: deleteField(),
  generalRows: deleteField(),
  quoteRows: deleteField(),
  quoteItems: deleteField(),
  entries: deleteField(),
  generalItems: deleteField(),
  generalQuote: deleteField(),
  generalQuoteData: deleteField(),
});

const getLargestJsonFields = (payload = {}, limit = 10) =>
  Object.entries(payload || {})
    .map(([field, value]) => ({
      field,
      estimatedBytes: getJsonSizeEstimateBytes(value),
    }))
    .sort((left, right) => right.estimatedBytes - left.estimatedBytes)
    .slice(0, limit);

const buildGeneralQuotePayloadSizeTrace = (sourcePayload = {}, finalPayload = {}, context = {}) => {
  const canonicalItems = extractGeneralQuoteItems(finalPayload);
  return {
    buildLabel: FABRIQUOTE_BUILD_INFO.label,
    quoteId: String(context.quoteId || finalPayload?.quoteId || sourcePayload?.quoteId || ""),
    quoteNo: String(context.quoteNo || finalPayload?.quoteNo || sourcePayload?.quoteNo || ""),
    saveMode: context.saveMode || "",
    itemCount: canonicalItems.length,
    payloadSizeBytes: getJsonSizeEstimateBytes(finalPayload),
    sourcePayloadSizeBytes: getJsonSizeEstimateBytes(sourcePayload),
    duplicateArrayFields: getGeneralQuoteDuplicateArrayFields(sourcePayload).filter(
      (entry) => !(entry.field === "items" && entry.count === canonicalItems.length)
    ),
    largestFields: getLargestJsonFields(finalPayload),
    largestSourceFields: getLargestJsonFields(sourcePayload),
  };
};

function buildCanonicalGeneralQuotePayload(payload = {}) {
  const canonicalItems = extractGeneralQuoteItems(payload).map((item, index) => ({
    ...item,
    id: item?.id || item?.itemId || `general-item-${index + 1}`,
  }));
  const basePayload = stripGeneralQuoteItemAliasFields(payload, {
    dropEmbeddedGeneralQuoteContainers: true,
  });

  return {
    ...basePayload,
    quoteType: GENERAL_QUOTE_TYPE,
    normalizedQuoteType: CANONICAL_GENERAL_QUOTE_TYPE,
    quotationType: GENERAL_QUOTE_TYPE,
    canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
    contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
    items: canonicalItems,
    itemCount: canonicalItems.length,
    contentItemCount: canonicalItems.length,
  };
}

const sanitizeGeneralQuotePayload = (payload = {}) => {
  const canonicalPayload = buildCanonicalGeneralQuotePayload(payload);
  const sanitizedItems = extractGeneralQuoteItems(canonicalPayload).map(
    sanitizeGeneralQuoteItemForFirestore
  );
  const basePayload = stripGeneralQuoteItemAliasFields(canonicalPayload, {
    dropEmbeddedGeneralQuoteContainers: true,
  });
  return sanitizeGeneralQuoteFirestoreValue({
    ...basePayload,
    items: sanitizedItems,
    itemCount: sanitizedItems.length,
    contentItemCount: sanitizedItems.length,
  });
};

const isGeneralQuotePayloadForCanonicalSave = (payload = {}, quoteTypeHint = "") => {
  if (!payload || typeof payload !== "object") return false;
  const normalizedType = normalizeQuoteType(
    quoteTypeHint ||
      payload.normalizedQuoteType ||
      payload.quoteTypeResolved ||
      payload.quoteType ||
      payload.quotationType ||
      "",
    payload
  );
  return (
    normalizedType === CANONICAL_GENERAL_QUOTE_TYPE ||
    payload.quoteType === GENERAL_QUOTE_TYPE ||
    payload.quotationType === GENERAL_QUOTE_TYPE ||
    Boolean(payload.generalQuote || payload.generalQuoteData)
  );
};

const getGeneralQuoteFinalPayloadDiagnostics = (payload = {}, context = {}) => {
  const generalQuote =
    payload?.generalQuote && typeof payload.generalQuote === "object"
      ? payload.generalQuote
      : {};
  const generalQuoteData =
    payload?.generalQuoteData && typeof payload.generalQuoteData === "object"
      ? payload.generalQuoteData
      : {};
  const nestedGeneralQuote =
    generalQuoteData?.generalQuote && typeof generalQuoteData.generalQuote === "object"
      ? generalQuoteData.generalQuote
      : {};
  return {
    buildLabel: FABRIQUOTE_BUILD_INFO.label,
    source: context.source || "",
    saveMode: context.saveMode || "",
    quoteId: String(context.quoteId || payload?.quoteId || payload?.sourceQuoteId || ""),
    quoteNo: String(context.quoteNo || payload?.quoteNo || ""),
    quoteType: String(payload?.quoteType || payload?.quotationType || ""),
    payloadKeys: Object.keys(payload || {}),
    hasTopLevelItems: Array.isArray(payload?.items),
    topLevelItemsLength: Array.isArray(payload?.items) ? payload.items.length : 0,
    lineItemsLength: Array.isArray(payload?.lineItems) ? payload.lineItems.length : 0,
    productsLength: Array.isArray(payload?.products) ? payload.products.length : 0,
    rowsLength: Array.isArray(payload?.rows) ? payload.rows.length : 0,
    generalQuoteItemsLength: Array.isArray(generalQuote.items) ? generalQuote.items.length : 0,
    generalQuoteLineItemsLength: Array.isArray(generalQuote.lineItems)
      ? generalQuote.lineItems.length
      : 0,
    generalQuoteDataItemsLength: Array.isArray(generalQuoteData.items)
      ? generalQuoteData.items.length
      : 0,
    generalQuoteDataLineItemsLength: Array.isArray(generalQuoteData.lineItems)
      ? generalQuoteData.lineItems.length
      : 0,
    nestedGeneralQuoteItemsLength: Array.isArray(nestedGeneralQuote.items)
      ? nestedGeneralQuote.items.length
      : 0,
    nestedGeneralQuoteLineItemsLength: Array.isArray(nestedGeneralQuote.lineItems)
      ? nestedGeneralQuote.lineItems.length
      : 0,
    itemCount: Number(payload?.itemCount || 0),
    contentItemCount: Number(payload?.contentItemCount || 0),
  };
};

const logGeneralCanonicalPayloadFinalCheck = (payload = {}, context = {}) => {
  const diagnostics = getGeneralQuoteFinalPayloadDiagnostics(payload, context);
  devLog("GENERAL_CANONICAL_PAYLOAD_FINAL_CHECK", JSON.stringify(diagnostics, null, 2));
  if (diagnostics.hasTopLevelItems && diagnostics.topLevelItemsLength > 0) {
    devLog("GENERAL_CANONICAL_PAYLOAD_READY", JSON.stringify(diagnostics, null, 2));
  }
  return diagnostics;
};

const finalizeGeneralQuotePayloadForSave = (payload = {}, context = {}) => {
  if (!isGeneralQuotePayloadForCanonicalSave(payload, context.quoteTypeHint)) {
    return payload;
  }
  const sanitizedItems = extractGeneralQuoteItems(payload).map((item, index) => {
    const sanitizedItem = sanitizeGeneralQuoteItemForFirestore(item);
    return {
      ...sanitizedItem,
      id:
        sanitizedItem?.id ||
        sanitizedItem?.itemId ||
        item?.id ||
        item?.itemId ||
        `general-item-${index + 1}`,
    };
  });
  const basePayload = stripGeneralQuoteItemAliasFields(payload, {
    dropEmbeddedGeneralQuoteContainers: true,
  });
  const finalizedPayload = {
    ...basePayload,
    quoteType: GENERAL_QUOTE_TYPE,
    normalizedQuoteType: CANONICAL_GENERAL_QUOTE_TYPE,
    quotationType: GENERAL_QUOTE_TYPE,
    items: sanitizedItems,
    itemCount: sanitizedItems.length,
    contentItemCount: sanitizedItems.length,
    canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
    contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
  };
  logGeneralCanonicalPayloadFinalCheck(finalizedPayload, context);
  if (isDevelopmentMode()) {
    devLog(
      "GENERAL_QUOTE_PAYLOAD_SIZE_TRACE",
      buildGeneralQuotePayloadSizeTrace(payload, finalizedPayload, context)
    );
  }
  return finalizedPayload;
};

const safeDebugPreview = (payload = {}, maxLength = 6000) => {
  try {
    const safePayload = sanitizeGeneralQuotePayload(payload);
    const text = JSON.stringify(safePayload, null, 2);
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  } catch (error) {
    return `Unable to build debug preview: ${error?.message || error}`;
  }
};

const estimateJsonSizeBytes = (payload = {}) => {
  try {
    return new Blob([JSON.stringify(sanitizeGeneralQuotePayload(payload))]).size;
  } catch (error) {
    try {
      return JSON.stringify(sanitizeGeneralQuotePayload(payload)).length;
    } catch {
      return 0;
    }
  }
};

const inspectGeneralQuotePayload = (value, path = "payload", seen = new WeakSet()) => {
  const result = {
    undefinedPaths: [],
    fileBlobPaths: [],
    invalidNumberPaths: [],
    functionPaths: [],
    circularPaths: [],
  };

  const visit = (entry, entryPath) => {
    if (typeof entry === "undefined") {
      result.undefinedPaths.push(entryPath);
      return;
    }
    if (typeof entry === "function") {
      result.functionPaths.push(entryPath);
      return;
    }
    if (typeof File !== "undefined" && entry instanceof File) {
      result.fileBlobPaths.push(entryPath);
      return;
    }
    if (typeof Blob !== "undefined" && entry instanceof Blob) {
      result.fileBlobPaths.push(entryPath);
      return;
    }
    if (typeof entry === "number" && !Number.isFinite(entry)) {
      result.invalidNumberPaths.push(entryPath);
      return;
    }
    if (!entry || typeof entry !== "object") return;
    if (seen.has(entry)) {
      result.circularPaths.push(entryPath);
      return;
    }
    seen.add(entry);

    if (Array.isArray(entry)) {
      entry.forEach((child, index) => visit(child, `${entryPath}[${index}]`));
      return;
    }

    Object.entries(entry).forEach(([key, child]) =>
      visit(child, `${entryPath}.${key}`)
    );
  };

  visit(value, path);

  return {
    ...result,
    hasUndefinedFields: result.undefinedPaths.length > 0,
    hasFileBlobObjects: result.fileBlobPaths.length > 0,
    hasInvalidNumbers: result.invalidNumberPaths.length > 0,
    hasFunctions: result.functionPaths.length > 0,
    hasCircularReferences: result.circularPaths.length > 0,
  };
};

const isFirestoreFieldValueSentinel = isLiveFirestoreSentinel;

const isFirebaseTimestampValue = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      typeof value.toDate === "function" &&
      typeof value.seconds === "number" &&
      typeof value.nanoseconds === "number"
  );

const isFirestoreDocumentReferenceValue = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      value.type === "document" &&
      typeof value.path === "string" &&
      typeof value.id === "string"
  );

const isFirestoreNumericPath = (path = "") =>
  /(?:amount|total|subtotal|tax|gst|discount|rate|price|mrp|meter|metre|sqft|width|height|length|count|qty|quantity|pieces|panels|roll|value|cost|margin|size|bytes|ratio|percent|percentage|inches|feet|ft|in)$/i.test(
    String(path || "").replace(/\[\d+\]/g, "")
  );

const getFirestoreValueType = (value) => {
  if (typeof value === "undefined") return "undefined";
  if (typeof value === "function") return "function";
  if (typeof value === "number" && !Number.isFinite(value)) {
    return Number.isNaN(value) ? "NaN" : value > 0 ? "Infinity" : "-Infinity";
  }
  if (value instanceof Date && Number.isNaN(value.getTime())) return "Invalid Date";
  if (typeof File !== "undefined" && value instanceof File) return "File";
  if (typeof Blob !== "undefined" && value instanceof Blob) return "Blob";
  if (typeof HTMLElement !== "undefined" && value instanceof HTMLElement) return "HTMLElement";
  return Object.prototype.toString.call(value).slice(8, -1) || typeof value;
};

const describeInvalidFirestoreValue = (value) => {
  const type = getFirestoreValueType(value);
  if (type === "File") {
    return `File(${value?.name || "unnamed"}, ${value?.type || "unknown"}, ${value?.size || 0} bytes)`;
  }
  if (type === "Blob") {
    return `Blob(${value?.type || "unknown"}, ${value?.size || 0} bytes)`;
  }
  if (type === "function") return value?.name ? `function ${value.name}` : "function";
  return type;
};

const parseNumericInput = (value, options = {}) => {
  const {
    required = false,
    allowZero = true,
    allowNegative = false,
    fieldLabel = "Value",
  } = options;
  const raw = value;
  const text = String(value ?? "").replace(/,/g, "").trim();

  if (!text) {
    return {
      value: null,
      valid: !required,
      raw,
      reason: required ? `${fieldLabel} is required.` : "",
    };
  }

  const numeric = Number(text);
  if (!Number.isFinite(numeric)) {
    return {
      value: null,
      valid: false,
      raw,
      reason: `${fieldLabel} must be a valid number.`,
    };
  }

  if (!allowNegative && numeric < 0) {
    return {
      value: numeric,
      valid: false,
      raw,
      reason: `${fieldLabel} cannot be negative.`,
    };
  }

  if (!allowZero && numeric <= 0) {
    return {
      value: numeric,
      valid: false,
      raw,
      reason: `${fieldLabel} must be greater than 0.`,
    };
  }

  return {
    value: numeric,
    valid: true,
    raw,
    reason: "",
  };
};

const findInvalidFirestoreValues = (value, path = "payload", seen = new WeakSet()) => {
  const invalidValues = [];

  const visit = (entry, entryPath) => {
    if (typeof entry === "undefined") {
      invalidValues.push({
        path: entryPath,
        type: "undefined",
        valueDescription: "undefined",
      });
      return;
    }
    if (typeof entry === "function") {
      invalidValues.push({
        path: entryPath,
        type: "function",
        valueDescription: describeInvalidFirestoreValue(entry),
      });
      return;
    }
    if (typeof entry === "number" && !Number.isFinite(entry)) {
      invalidValues.push({
        path: entryPath,
        type: getFirestoreValueType(entry),
        valueDescription: describeInvalidFirestoreValue(entry),
      });
      return;
    }
    if (entry instanceof Date) {
      if (Number.isNaN(entry.getTime())) {
        invalidValues.push({
          path: entryPath,
          type: "Invalid Date",
          valueDescription: "Invalid Date",
        });
      }
      return;
    }
    if (typeof File !== "undefined" && entry instanceof File) {
      invalidValues.push({
        path: entryPath,
        type: "File",
        valueDescription: describeInvalidFirestoreValue(entry),
      });
      return;
    }
    if (typeof Blob !== "undefined" && entry instanceof Blob) {
      invalidValues.push({
        path: entryPath,
        type: "Blob",
        valueDescription: describeInvalidFirestoreValue(entry),
      });
      return;
    }
    if (typeof HTMLElement !== "undefined" && entry instanceof HTMLElement) {
      invalidValues.push({
        path: entryPath,
        type: "HTMLElement",
        valueDescription: "DOM element",
      });
      return;
    }
    if (!entry || typeof entry !== "object") return;
    if (
      isFirestoreFieldValueSentinel(entry) ||
      isFirebaseTimestampValue(entry) ||
      isFirestoreDocumentReferenceValue(entry)
    ) {
      return;
    }
    if (seen.has(entry)) {
      invalidValues.push({
        path: entryPath,
        type: "Circular",
        valueDescription: "Circular object reference",
      });
      return;
    }
    seen.add(entry);

    if (Array.isArray(entry)) {
      entry.forEach((child, index) => visit(child, `${entryPath}[${index}]`));
      seen.delete(entry);
      return;
    }

    Object.entries(entry).forEach(([key, child]) => visit(child, `${entryPath}.${key}`));
    seen.delete(entry);
  };

  visit(value, path);
  return invalidValues;
};

const getQuoteRowFieldLabel = (field = "") => {
  const normalized = String(field || "").replace(/\[\d+\]/g, "").split(".").pop() || field;
  const labels = {
    product: "Product",
    productType: "Product",
    item: "Item",
    name: "Item",
    description: "Description",
    fabric: "Fabric / Code",
    fabricCode: "Fabric / Code",
    fabricCodeRaw: "Fabric / Code",
    materialCode: "Fabric / Code",
    fabricWidthIn: "Fabric width",
    width: "Width",
    w: "Width",
    height: "Height",
    h: "Height",
    mrp: "MRP/m",
    mrpPerMeter: "MRP/m",
    rrpPerMeter: "RRP/m",
    rate: "Rate",
    ratePerMeter: "Rate",
    sqftRate: "Sqft rate",
    ratePerSqft: "Sqft rate",
    ratePerPiece: "Rate",
    ratePerFeet: "Rate",
    discount: "Discount %",
    qty: "Qty",
    quantity: "Qty",
    liningRate: "Lining rate",
    billableMeters: "Lining meters",
    total: "Total",
    amount: "Amount",
  };
  return labels[normalized] || normalized || "Field";
};

const getQuoteRowLabel = (row = {}, rowIndex = 0) =>
  String(
    row.item ||
      row.name ||
      row.itemName ||
      row.room ||
      row.windowName ||
      row.productName ||
      row.productType ||
      row.product ||
      `Row ${rowIndex + 1}`
  ).trim() || `Row ${rowIndex + 1}`;

const isLiningEnabledForValidation = (row = {}) =>
  row?.lining?.enabled === true ||
  row?.liningEnabled === true ||
  row?.addLining === true ||
  row?.hasLining === true;

const isInactiveQuoteRowForValidation = (row = {}) =>
  row?.deleted === true ||
  row?.isDeleted === true ||
  row?.hidden === true ||
  row?.isHidden === true ||
  row?.isTemplate === true ||
  row?.isPlaceholder === true ||
  row?.placeholder === true ||
  row?.draftPlaceholder === true;

const isEmptyQuoteRowForValidation = (row = {}) => {
  if (!row || typeof row !== "object") return true;
  const meaningfulFields = [
    row.item,
    row.name,
    row.itemName,
    row.description,
    row.product,
    row.productType,
    row.productName,
    row.fabric,
    row.fabricName,
    row.fabricCode,
    row.fabricCodeRaw,
    row.materialCode,
    row.width,
    row.height,
    row.qty,
    row.quantity,
    row.rate,
    row.mrp,
    row.mrpPerMeter,
    row.rrpPerMeter,
    row.sqftRate,
    row.total,
    row.amount,
  ];
  return meaningfulFields.every((value) => String(value ?? "").trim() === "");
};

const buildQuoteRowSnapshot = (row = {}) => ({
  item: row.item || row.name || row.itemName || "",
  product: row.product || row.productType || row.productName || "",
  productType: row.productType || row.product || "",
  calculationType: row.calculationType || row.productConfigSnapshot?.calculationType || "",
  fabricCodeRaw: row.fabricCodeRaw || "",
  fabricCode: row.fabricCode || row.fabric || row.fabricName || row.materialCode || "",
  brandId: row.brandId || row.fabricBrandId || "",
  brandName: row.brandName || row.fabricBrandName || "",
  fabricSource: row.fabricSource || row.rrpSource || "",
  catalogueId: row.catalogueId || row.fabricCatalogueId || "",
  catalogueName: row.catalogueName || row.fabricCatalogueName || "",
  srNumber: row.srNumber || row.srNo || "",
  rrpPerMeter: row.rrpPerMeter ?? "",
  mrpPerMeter: row.mrpPerMeter ?? row.mrp ?? "",
  ratePerMeter: row.ratePerMeter ?? row.rate ?? "",
  qty: row.qty ?? row.quantity ?? "",
  width: row.width ?? row.w ?? "",
  height: row.height ?? row.h ?? "",
  liningEnabled: isLiningEnabledForValidation(row),
  lining: row.lining || { enabled: false },
});

const createQuoteRowValidationDetail = ({
  quoteType = "",
  saveMode = "",
  quoteNo = "",
  quoteId = "",
  row = {},
  rowIndex = -1,
  failedField = "",
  failedValue = "",
  failedReason = "",
  validatorName = "quoteRowValidation",
  path = "",
} = {}) => {
  const rowLabel = getQuoteRowLabel(row, rowIndex);
  const fieldLabel = getQuoteRowFieldLabel(failedField || path);
  const reason = failedReason || `${fieldLabel} is invalid.`;
  const message = `${rowLabel}: ${reason}`;
  return {
    quoteType,
    saveMode,
    quoteNo,
    quoteId,
    rowIndex,
    rowNumber: Number.isFinite(rowIndex) && rowIndex >= 0 ? rowIndex + 1 : null,
    rowId: row.id || row.rowId || row.clientRowId || row.sourceRowId || "",
    item: row.item || row.name || row.itemName || "",
    product: row.product || row.productType || row.productName || "",
    productType: row.productType || row.product || "",
    calculationType: row.calculationType || row.productConfigSnapshot?.calculationType || "",
    failedField: failedField || path || "",
    failedFieldLabel: fieldLabel,
    failedValue,
    failedReason: reason,
    validatorName,
    path,
    message,
    rowSnapshot: buildQuoteRowSnapshot(row),
  };
};

const getQuoteRowValidationDetailFromError = (error) => {
  const explicit = Array.isArray(error?.__fqRowValidationDetails)
    ? error.__fqRowValidationDetails[0]
    : null;
  if (explicit) return explicit;
  const details = error?.details || {};
  if (details?.code === "invalid-row-data" || details?.failedField || details?.rowIndex != null) {
    return details;
  }
  if (Array.isArray(error?.__fqInvalidFirestoreValues) && error.__fqInvalidFirestoreValues.length) {
    const invalid = error.__fqInvalidFirestoreValues[0];
    return createQuoteRowValidationDetail({
      failedField: invalid.path || "",
      failedValue: invalid.valueDescription || invalid.type || "",
      failedReason: `${getQuoteRowFieldLabel(invalid.path)} contains ${invalid.valueDescription || invalid.type || "an invalid value"}.`,
      path: invalid.path || "",
      validatorName: "firestorePayloadSanitizer",
    });
  }
  return null;
};

const validateGeneralQuoteRowsForSave = ({
  items = [],
  quoteNo = "",
  quoteId = "",
  saveMode = "",
} = {}) => {
  const activeItems = (Array.isArray(items) ? items : [])
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(
      ({ item }) =>
        !isInactiveQuoteRowForValidation(item) && !isEmptyQuoteRowForValidation(item)
    );

  for (let index = 0; index < activeItems.length; index += 1) {
    const { item = {}, originalIndex = index } = activeItems[index] || {};
    const itemName = String(
      item.item || item.name || item.itemName || item.description || item.title || ""
    ).trim();
    if (!itemName) {
      return createQuoteRowValidationDetail({
        quoteType: GENERAL_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row: item,
        rowIndex: originalIndex,
        failedField: "item",
        failedValue: itemName,
        failedReason: "Please enter an item name or description.",
        validatorName: "validateGeneralQuoteRowsForSave",
      });
    }

    const qtyValue = item.qty ?? item.quantity ?? 1;
    const qtyValidation = parseNumericInput(qtyValue, {
      required: true,
      allowZero: false,
      fieldLabel: "Qty",
    });
    if (!qtyValidation.valid) {
      return createQuoteRowValidationDetail({
        quoteType: GENERAL_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row: item,
        rowIndex: originalIndex,
        failedField: "qty",
        failedValue: qtyValidation.raw,
        failedReason: qtyValidation.reason,
        validatorName: "validateGeneralQuoteRowsForSave",
      });
    }

    const rateCandidate =
      item.rate ?? item.unitRate ?? item.price ?? item.amount ?? item.total ?? item.lineTotal;
    const rateValidation = parseNumericInput(rateCandidate, {
      required: true,
      allowZero: true,
      fieldLabel: "Rate",
    });
    if (!rateValidation.valid) {
      return createQuoteRowValidationDetail({
        quoteType: GENERAL_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row: item,
        rowIndex: originalIndex,
        failedField: "rate",
        failedValue: rateValidation.raw,
        failedReason: rateValidation.reason,
        validatorName: "validateGeneralQuoteRowsForSave",
      });
    }

    const lineTotal = getGeneralItemAmount(item);
    if (!Number.isFinite(Number(lineTotal))) {
      return createQuoteRowValidationDetail({
        quoteType: GENERAL_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row: item,
        rowIndex: originalIndex,
        failedField: "total",
        failedValue: lineTotal,
        failedReason: "Total must be a valid number.",
        validatorName: "validateGeneralQuoteRowsForSave",
      });
    }
  }

  return null;
};

const validateCurtainRowsForSave = ({
  rows = [],
  quoteNo = "",
  quoteId = "",
  saveMode = "",
} = {}) => {
  const activeRows = (Array.isArray(rows) ? rows : [])
    .map((row, originalIndex) => ({ row, originalIndex }))
    .filter(
      ({ row }) =>
        !isInactiveQuoteRowForValidation(row) && !isEmptyQuoteRowForValidation(row)
    );

  for (let index = 0; index < activeRows.length; index += 1) {
    const { row = {}, originalIndex = index } = activeRows[index] || {};
    const product = String(row.productType || row.product || row.productName || "").trim();
    const lineType = String(row.lineType || "").toLowerCase();
    const category = String(row.category || row.productConfigSnapshot?.category || "").toLowerCase();
    const calculationType = String(
      row.calculationType || row.productConfigSnapshot?.calculationType || ""
    ).toLowerCase();
    const isAccessory =
      lineType === "accessory" ||
      category === "accessory" ||
      calculationType.includes("accessory");

    if (!product) {
      return createQuoteRowValidationDetail({
        quoteType: CURTAIN_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row,
        rowIndex: originalIndex,
        failedField: "product",
        failedValue: product,
        failedReason: "Please select a product.",
        validatorName: "validateCurtainRowsForSave",
      });
    }

    const validateNumericField = (field, label, options = {}) => {
      const validation = parseNumericInput(row[field], {
        required: options.required === true,
        allowZero: options.allowZero !== false,
        fieldLabel: label,
      });
      if (validation.valid) return null;
      devLog("BLIND_PRICING_VALIDATION_TRACE", { rowId: row.rowId || row.id, productId: row.productId,
        calculationType, ...getProductPricingRequirements(row.productConfigSnapshot || { calculationType }),
        validationField: field, validationReason: validation.reason });
      return createQuoteRowValidationDetail({
        quoteType: CURTAIN_QUOTE_TYPE,
        saveMode,
        quoteNo,
        quoteId,
        row,
        rowIndex: originalIndex,
        failedField: field,
        failedValue: validation.raw,
        failedReason: validation.reason,
        validatorName: "validateCurtainRowsForSave",
      });
    };

    if (isAccessory) {
      const isLengthAccessory = calculationType === "running_length_rate";
      const isManualAccessory = calculationType === "manual_amount";

      if (isLengthAccessory) {
        const lengthError = validateNumericField("length", "Length", {
          required: true,
          allowZero: false,
        });
        if (lengthError) return lengthError;
        const ratePerLengthError = validateNumericField("ratePerLength", "Rate", {
          required: true,
          allowZero: true,
        });
        if (ratePerLengthError) return ratePerLengthError;
      } else if (isManualAccessory) {
        const qtyError = validateNumericField("qty", "Qty", {
          required: false,
          allowZero: false,
        });
        if (qtyError) return qtyError;
        const manualAmountError = validateNumericField("manualAmount", "Amount", {
          required: true,
          allowZero: true,
        });
        if (manualAmountError) return manualAmountError;
      } else {
        const qtyError = validateNumericField("qty", "Qty", {
          required: true,
          allowZero: false,
        });
        if (qtyError) return qtyError;
        const accessoryRateField =
          String(row.unitRate ?? "").trim() !== ""
            ? "unitRate"
            : String(row.ratePerPiece ?? "").trim() !== ""
            ? "ratePerPiece"
            : "rate";
        const rateError = validateNumericField(accessoryRateField, "Rate", {
          required: true,
          allowZero: true,
        });
        if (rateError) return rateError;
      }
      continue;
    }

    const widthError = validateNumericField("width", "Width", {
      required: true,
      allowZero: false,
    });
    if (widthError) return widthError;
    const heightError = validateNumericField("height", "Height", {
      required: true,
      allowZero: false,
    });
    if (heightError) return heightError;

    const pricing = getProductPricingRequirements(row.productConfigSnapshot || { calculationType });
    const hasFabricWidthValue = String(row.fabricWidthIn ?? "").trim() !== "";
    const usesFabricWidth =
      (pricing.usesSeparateFabricCost && row.productConfigSnapshot?.requiredFields?.fabricWidth === true) ||
      (!calculationType.includes("sqft") && hasFabricWidthValue);
    if (usesFabricWidth) {
      const fabricWidthError = validateNumericField("fabricWidthIn", "Fabric width", {
        required: row.productConfigSnapshot?.requiredFields?.fabricWidth === true,
        allowZero: false,
      });
      if (fabricWidthError) return fabricWidthError;
    }

    const hasMrpValue =
      String(row.mrpPerMeter ?? "").trim() !== "" || String(row.mrp ?? "").trim() !== "";
    const mrpRequired = pricing.requiresMrpPerMeter;
    const mrpField = row.mrpPerMeter != null && row.mrpPerMeter !== "" ? "mrpPerMeter" : "mrp";
    if (pricing.usesSeparateFabricCost && (mrpRequired || hasMrpValue)) {
      const mrpError = validateNumericField(mrpField, "MRP/m", {
        required: mrpRequired,
        allowZero: true,
      });
      if (mrpError) return mrpError;
    }

    if (pricing.requiresSqftRate) {
      const rateField = row.sqftRate != null && row.sqftRate !== "" ? "sqftRate" : "ratePerSqft";
      const usesConfiguredRate = row.productConfigSnapshot?.sqftRules?.rateSource === "default_rate"
        && row.rateSources?.sqftRate !== "catalogue"
        && !["manual_override", "loaded_saved_quote"].includes(row.rateSource) && row.rateOverridden !== true;
      if (!usesConfiguredRate) {
        const rateError = validateNumericField(rateField, "Sqft rate", { required: true, allowZero: true });
        if (rateError) return rateError;
      }
    }

    const optionalNumericFields = [
      ["rrpPerMeter", "RRP/m"],
      ["discount", "Discount %"],
      ["rate", "Rate"],
      ["ratePerMeter", "Rate"],
      ["sqftRate", "Sqft rate"],
      ["ratePerSqft", "Sqft rate"],
      ["ratePerPiece", "Rate"],
      ["ratePerFeet", "Rate"],
    ];
    for (const [field, label] of optionalNumericFields) {
      if (!pricing.usesSeparateFabricCost && ["rrpPerMeter", "ratePerMeter"].includes(field)) continue;
      if (String(row[field] ?? "").trim() === "") continue;
      const fieldError = validateNumericField(field, label, {
        required: false,
        allowZero: true,
      });
      if (fieldError) return fieldError;
    }

    if ((calculationType !== "sqft_only" || productSupportsLining(row.productConfigSnapshot, row)) && isLiningEnabledForValidation(row)) {
      const lining = row.lining || {};
      const liningRateValue =
        row.liningRate ??
        lining.ratePerMeter ??
        lining.ratePerSqft ??
        lining.rate ??
        "";
      const liningRateValidation = parseNumericInput(liningRateValue, {
        required: true,
        allowZero: false,
        fieldLabel: "Lining rate",
      });
      if (!liningRateValidation.valid) {
        return createQuoteRowValidationDetail({
          quoteType: CURTAIN_QUOTE_TYPE,
          saveMode,
          quoteNo,
          quoteId,
          row,
          rowIndex: index,
          failedField: "liningRate",
          failedValue: liningRateValidation.raw,
          failedReason: liningRateValidation.reason,
          validatorName: "validateCurtainRowsForSave",
        });
      }
    }
  }

  return null;
};

const sanitizeForFirestore = (value, options = {}, path = "payload", seen = new WeakSet()) => {
  const { arrayUndefinedValue = null } = options;

  if (typeof value === "undefined" || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return null;
  if (typeof Blob !== "undefined" && value instanceof Blob) return null;
  if (typeof HTMLElement !== "undefined" && value instanceof HTMLElement) return null;
  if (typeof value === "number" && !Number.isFinite(value)) {
    return isFirestoreNumericPath(path) ? 0 : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (
    isFirestoreFieldValueSentinel(value) ||
    isFirebaseTimestampValue(value) ||
    isFirestoreDocumentReferenceValue(value)
  ) {
    return value;
  }
  if (typeof value !== "object") return value;
  if (isFirestoreSentinel(value)) return undefined;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    const output = value.map((entry, index) => {
      const sanitized = sanitizeForFirestore(entry, options, `${path}[${index}]`, seen);
      return typeof sanitized === "undefined" ? arrayUndefinedValue : sanitized;
    });
    seen.delete(value);
    return output;
  }

  const output = Object.entries(value).reduce((result, [key, entryValue]) => {
    const sanitized = sanitizeForFirestore(entryValue, options, `${path}.${key}`, seen);
    if (typeof sanitized !== "undefined") {
      result[key] = sanitized;
    }
    return result;
  }, {});
  seen.delete(value);
  return output;
};

const logQuoteSaveInvalidFirestoreValues = ({
  quoteNo = "",
  quoteId = "",
  saveMode = "",
  step = "",
  path = "",
  invalidValues = [],
  phase = "before_sanitize",
} = {}) => {
  if (!isDevRuntime || !invalidValues.length) return;
  devWarn("QUOTE_SAVE_INVALID_PAYLOAD_VALUES", {
    quoteNo,
    quoteId,
    saveMode,
    step,
    path,
    phase,
    invalidValues,
  });
};

const logQuoteRowValidationFailed = ({
  payload = {},
  quoteNo = "",
  quoteId = "",
  saveMode = "",
  step = "",
  invalidValues = [],
  validatorName = "sanitizeQuoteWritePayload",
} = {}) => {
  if (!isDevRuntime || !invalidValues.length) return;
  const readRowForPath = (invalidPath = "") => {
    const match = String(invalidPath || "").match(
      /(?:^|\.)(rows|items|lineItems|quoteItems|entries)\[(\d+)\]/
    );
    if (!match) return null;
    const collectionName = match[1];
    const rowIndex = Number(match[2]);
    const rowList = Array.isArray(payload?.[collectionName]) ? payload[collectionName] : [];
    return Number.isInteger(rowIndex) ? rowList[rowIndex] || null : null;
  };

  invalidValues.forEach((invalidValue) => {
    const item = readRowForPath(invalidValue.path) || {};
    const detail = createQuoteRowValidationDetail({
      quoteType: payload?.quoteType || payload?.quotationType || "",
      quoteNo,
      quoteId,
      saveMode,
      failedField: invalidValue.path || "",
      failedValue: invalidValue.valueDescription || invalidValue.type || "",
      failedReason: invalidValue.valueDescription || invalidValue.type || "",
      validatorName,
      row: item,
      rowIndex: Number(String(invalidValue.path || "").match(/\[(\d+)\]/)?.[1] ?? -1),
      path: invalidValue.path || "",
    });
    devError("QUOTE_ROW_VALIDATION_FAILED_DETAILED", {
      ...detail,
      step,
    });
  });
};

const sanitizeQuoteWritePayload = ({
  payload = {},
  quoteNo = "",
  quoteId = "",
  saveMode = "",
  step = "",
  path = "",
} = {}) => {
  const invalidBefore = findInvalidFirestoreValues(payload);
  logQuoteRowValidationFailed({
    payload,
    quoteNo,
    quoteId,
    saveMode,
    step,
    invalidValues: invalidBefore,
    validatorName: "sanitizeQuoteWritePayload:before_sanitize",
  });
  logQuoteSaveInvalidFirestoreValues({
    quoteNo,
    quoteId,
    saveMode,
    step,
    path,
    invalidValues: invalidBefore,
    phase: "before_sanitize",
  });

  const sanitizedPayload = sanitizeForFirestore(payload);
  const invalidAfter = findInvalidFirestoreValues(sanitizedPayload);
  logQuoteRowValidationFailed({
    payload: sanitizedPayload,
    quoteNo,
    quoteId,
    saveMode,
    step,
    invalidValues: invalidAfter,
    validatorName: "sanitizeQuoteWritePayload:after_sanitize",
  });
  logQuoteSaveInvalidFirestoreValues({
    quoteNo,
    quoteId,
    saveMode,
    step,
    path,
    invalidValues: invalidAfter,
    phase: "after_sanitize",
  });

  if (invalidAfter.length) {
    const error = new Error("Quote payload still contains invalid Firestore values after sanitation.");
    error.code = "invalid-argument";
    error.__fqInvalidFirestoreValues = invalidAfter;
    error.__fqRequiredSaveStep = step || "quote_payload_sanitize";
    throw error;
  }

  if (isDevRuntime && typeof window !== "undefined") {
    const payloadRecord = {
      quoteNo,
      quoteId,
      saveMode,
      step,
      path,
      payload,
      sanitizedPayload,
      invalidBefore,
      invalidAfter,
      checkedAt: new Date().toISOString(),
    };
    const history = Array.isArray(window.__FQ_LAST_QUOTE_WRITE_PAYLOADS__)
      ? window.__FQ_LAST_QUOTE_WRITE_PAYLOADS__
      : [];
    history.push(payloadRecord);
    window.__FQ_LAST_QUOTE_WRITE_PAYLOADS__ = history.slice(-12);
    window.__FQ_LAST_QUOTE_WRITE_PAYLOAD__ = payloadRecord;
  }

  return sanitizedPayload;
};

const estimateFirestorePayloadSize = (payload = {}) => {
  try {
    const json = JSON.stringify(payload || {});
    if (typeof Blob !== "undefined") {
      return new Blob([json]).size;
    }
    return json.length;
  } catch {
    return 0;
  }
};

const createOversizedQuotationPayloadError = ({
  quoteId = "",
  quoteNo = "",
  path = "",
  mainSizeBytes = 0,
  rowChunkOversize = [],
} = {}) => {
  const error = new Error(
    "Prepared quotation payload still exceeds the maximum allowed Firestore document size."
  );
  error.code = "invalid-argument";
  error.__fqRequiredSaveStep = "quote_payload_size_guard";
  error.__fqPayloadSize = {
    quoteId,
    quoteNo,
    path,
    mainSizeBytes,
    rowChunkOversize,
    maxFirestoreDocumentBytes: 1048576,
    safeInlineLimitBytes: QUOTE_INLINE_PAYLOAD_CHUNK_BYTES,
    safeChunkLimitBytes: QUOTE_ROW_CHUNK_MAX_BYTES,
  };
  return error;
};

const getLargestPayloadKeys = (payload = {}, limitCount = 10) =>
  Object.entries(payload || {})
    .map(([key, value]) => ({
      key,
      bytes: estimateFirestorePayloadSize(value),
    }))
    .sort((left, right) => right.bytes - left.bytes)
    .slice(0, limitCount);

const getCurtainContentFieldCounts = (payload = {}) =>
  Object.fromEntries(
    CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS.map((fieldName) => [
      fieldName,
      Array.isArray(payload?.[fieldName]) ? payload[fieldName].length : 0,
    ])
  );

const getLargestCurtainRowsForPayloadTrace = (rows = [], limitCount = 8) =>
  (Array.isArray(rows) ? rows : [])
    .map((row, index) => ({
      index,
      rowId: row?.id || row?.rowId || row?.itemId || `row-${index + 1}`,
      productType: row?.productType || row?.productName || row?.lineType || "",
      fabric:
        row?.fabricName ||
        row?.selectedFabric ||
        row?.materialCode ||
        row?.fabricCode ||
        "",
      bytes: estimateFirestorePayloadSize(row),
      largestFields: getLargestPayloadKeys(row || {}, 8),
    }))
    .sort((left, right) => right.bytes - left.bytes)
    .slice(0, limitCount);

const isLikelyInlineImageString = (value = "") => {
  const text = String(value || "");
  return (
    text.startsWith("data:image/") ||
    text.startsWith("data:application/pdf") ||
    (text.length > 1200 && /^[-A-Za-z0-9+/=]+$/.test(text.slice(0, 1200)))
  );
};

const inspectQuotationPayloadSizeRisks = (value, path = "payload", seen = new WeakSet()) => {
  const risks = {
    hasBase64: false,
    hasDataUrls: false,
    hasPdfHtml: false,
    hasDebugTrace: false,
    hasFullCatalogueObjects: false,
    hasImageData: false,
    paths: [],
  };

  const addRisk = (type, riskPath) => {
    risks[type] = true;
    if (risks.paths.length < 60) {
      risks.paths.push(`${type}:${riskPath}`);
    }
  };

  const visit = (entry, entryPath) => {
    if (typeof entry === "string") {
      const lowerPath = entryPath.toLowerCase();
      if (entry.startsWith("data:")) addRisk("hasDataUrls", entryPath);
      if (isLikelyInlineImageString(entry)) addRisk("hasBase64", entryPath);
      if (
        (lowerPath.includes("pdf") || lowerPath.includes("html")) &&
        /<html|<body|<table|<!doctype/i.test(entry)
      ) {
        addRisk("hasPdfHtml", entryPath);
      }
      return;
    }
    if (!entry || typeof entry !== "object") return;
    if (
      isFirestoreFieldValueSentinel(entry) ||
      isFirebaseTimestampValue(entry) ||
      isFirestoreDocumentReferenceValue(entry)
    ) {
      return;
    }
    if (seen.has(entry)) return;
    seen.add(entry);
    if (typeof File !== "undefined" && entry instanceof File) {
      addRisk("hasImageData", entryPath);
      seen.delete(entry);
      return;
    }
    if (typeof Blob !== "undefined" && entry instanceof Blob) {
      addRisk("hasImageData", entryPath);
      seen.delete(entry);
      return;
    }

    if (Array.isArray(entry)) {
      entry.forEach((child, index) => visit(child, `${entryPath}[${index}]`));
      seen.delete(entry);
      return;
    }

    Object.entries(entry).forEach(([key, child]) => {
      const lowerKey = key.toLowerCase();
      const childPath = `${entryPath}.${key}`;
      if (lowerKey.includes("debug") || lowerKey.includes("trace") || lowerKey.includes("monitor")) {
        addRisk("hasDebugTrace", childPath);
      }
      if (
        lowerKey === "selectedfabricref" ||
        lowerKey === "catalogueoptions" ||
        lowerKey === "catalogues" ||
        lowerKey === "srpricerules" ||
        lowerKey === "pricerules" ||
        lowerKey === "dropdownoptions"
      ) {
        addRisk("hasFullCatalogueObjects", childPath);
      }
      visit(child, childPath);
    });
    seen.delete(entry);
  };

  visit(value, path);
  return risks;
};

const shouldDropHeavyQuotationField = (key = "", value = null) => {
  const lowerKey = String(key || "").toLowerCase();
  if (lowerKey === "includerolloptimizer" && typeof value === "boolean") return false;
  const exactHeavyKeys = new Set([
    "base64",
    "imagedata",
    "imagebinary",
    "file",
    "blob",
    "previewdataurl",
    "customerpdfhtml",
    "workshoppdfhtml",
    "pdfhtml",
    "zipblob",
    "zipdata",
    "qrcodedataurl",
    "latesttracedata",
    "readmonitor",
    "cache",
    "cacheddata",
    "cachedoptions",
    "dropdownoptions",
    "catalogueoptions",
    "selectedfabricref",
    "fabriccataloguerecord",
    "brandrecord",
    "cataloguerecord",
    "optimizerresult",
    "optimizerresults",
    "optimizerstate",
    "optimizerdata",
    "rolloptimizerstate",
    "rolloptimizerresult",
    "workshopzip",
    "pdfblob",
    "pdfdata",
    "pdfpreview",
    "pdfsnapshot",
    "lastpdfpayload",
    "lastworkshoppayload",
    "localsearchcache",
    "lastdebugpayload",
    "debugpayload",
  ]);
  if (exactHeavyKeys.has(lowerKey)) return true;
  if (lowerKey.includes("debug") || lowerKey.includes("trace")) return true;
  if (lowerKey.includes("optimizer") || lowerKey.includes("rollvisual")) return true;
  if (lowerKey.includes("workshopzip") || lowerKey.includes("pdfblob")) return true;
  if (lowerKey.includes("base64") || lowerKey.includes("binary")) return true;
  if (lowerKey.includes("fileobject") || lowerKey.includes("originalfile")) return true;
  if (lowerKey.includes("preview") && typeof value === "string" && isLikelyInlineImageString(value)) {
    return true;
  }
  if ((lowerKey.includes("html") || lowerKey.includes("pdf")) && typeof value === "string") return true;
  return typeof value === "string" && isLikelyInlineImageString(value);
};

const sanitizeQuotationForFirestore = (value, path = "payload", seen = new WeakSet()) => {
  if (typeof value === "undefined" || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (typeof HTMLElement !== "undefined" && value instanceof HTMLElement) return undefined;
  if (typeof value === "string") {
    return isLikelyInlineImageString(value) ? "" : value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : isFirestoreNumericPath(path) ? 0 : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (
    isFirestoreFieldValueSentinel(value) ||
    isFirebaseTimestampValue(value) ||
    isFirestoreDocumentReferenceValue(value)
  ) {
    return value;
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    const output = value
      .map((entry, index) => sanitizeQuotationForFirestore(entry, `${path}[${index}]`, seen))
      .filter((entry) => typeof entry !== "undefined");
    seen.delete(value);
    return output;
  }

  const output = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (shouldDropHeavyQuotationField(key, entryValue)) return;
    const sanitized = sanitizeQuotationForFirestore(entryValue, `${path}.${key}`, seen);
    if (typeof sanitized !== "undefined") {
      output[key] = sanitized;
    }
  });
  seen.delete(value);
  return output;
};

const compactCurtainRowForFirestore = (row = {}, index = 0) => {
  const sanitizedRow = sanitizeQuotationForFirestore(row) || {};
  const nextRow = withCurtainRowId({ ...sanitizedRow }, index);
  const compactPhoto = normalizeFabricPhotoRef(
    row.fabricPhoto || row.fabricImage || row.photo || null
  );
  const compactPhotoHistory = buildFabricPhotoHistory([
    ...(Array.isArray(row.photoHistory) ? row.photoHistory : []),
    ...(Array.isArray(row.fabricPhotoHistory) ? row.fabricPhotoHistory : []),
    compactPhoto,
  ]);

  delete nextRow.selectedFabricRef;
  delete nextRow.catalogueOptions;
  delete nextRow.dropdownOptions;
  delete nextRow.srPriceRules;
  delete nextRow.priceRules;
  delete nextRow.fabricCatalogueRecord;
  delete nextRow.brandRecord;
  delete nextRow.catalogueRecord;

  if (compactPhoto) {
    nextRow.fabricPhoto = compactPhoto;
  }
  if (compactPhotoHistory.length) {
    nextRow.photoHistory = compactPhotoHistory;
    nextRow.fabricPhotoHistory = compactPhotoHistory;
  }

  return nextRow;
};

const compactCurtainRowsForFirestore = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row, index) =>
    compactCurtainRowForFirestore(row, index)
  );

const buildCurtainRowChunks = ({ rows = [], quoteId = "", workspaceUid = "" } = {}) => {
  const rowChunks = [];
  let currentRows = [];

  const flushChunk = () => {
    if (!currentRows.length) return;
    const chunkIndex = rowChunks.length;
    const chunkId = String(chunkIndex + 1).padStart(4, "0");
    const path = `users/${workspaceUid}/quotations/${quoteId}/row_chunks/${chunkId}`;
    rowChunks.push({
      id: chunkId,
      chunkId,
      path,
      chunkIndex,
      quoteId,
      rows: currentRows,
      rowCount: currentRows.length,
    });
    currentRows = [];
  };

  rows.forEach((row) => {
    const candidateRows = [...currentRows, row];
    const candidateSize = estimateFirestorePayloadSize({ rows: candidateRows });
    if (currentRows.length && candidateSize > QUOTE_ROW_CHUNK_TARGET_BYTES) {
      flushChunk();
    }
    currentRows.push(row);
  });
  flushChunk();

  return rowChunks;
};

const buildCurtainChunkRefs = (rowChunks = []) =>
  rowChunks.map((chunk) => ({
    chunkId: chunk.chunkId || chunk.id,
    path: chunk.path,
    chunkIndex: chunk.chunkIndex,
    rowCount: chunk.rowCount,
  }));

const addCurtainAliasDeleteFields = (payload = {}, fields = CURTAIN_QUOTE_STALE_ALIAS_FIELDS) => {
  const next = { ...(payload || {}) };
  fields.forEach((fieldName) => {
    next[fieldName] = deleteField();
  });
  return next;
};

const prepareCurtainQuoteStorageForSave = ({
  payload = {},
  quoteId = "",
  workspaceUid = "",
  saveMode = "",
  action = "save",
  path = "",
  allowDeleteFieldSentinels = true,
  previousRowChunkCount = 0,
} = {}) => {
  const originalPayload = payload || {};
  const originalRows = getCurtainQuoteCanonicalRows(originalPayload);
  const compactRows = compactCurtainRowsForFirestore(originalRows);
  const basePayload = sanitizeQuotationForFirestore(originalPayload) || {};
  CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS.forEach((fieldName) => {
    delete basePayload[fieldName];
  });

  const rowCount = compactRows.length;
  const inlinePayloadBase = {
    ...basePayload,
    rows: compactRows,
    rowStorageMode: QUOTE_ROW_STORAGE_INLINE,
    rowChunkCount: 0,
    rowChunkRefs: [],
    contentStorageField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
    rowCount,
    contentRowCount: rowCount,
  };
  const inlinePayload = allowDeleteFieldSentinels
    ? addCurtainAliasDeleteFields(inlinePayloadBase)
    : inlinePayloadBase;
  const rawOriginalSizeBytes = estimateFirestorePayloadSize(originalPayload);
  const originalSizeBytes = estimateFirestorePayloadSize(sanitizeQuotationForFirestore(originalPayload) || {});
  const inlineSizeBytes = estimateFirestorePayloadSize(inlinePayload);
  const originalRisks = inspectQuotationPayloadSizeRisks(originalPayload);
  const inlineRisks = inspectQuotationPayloadSizeRisks(inlinePayload);

  let storageMode = QUOTE_ROW_STORAGE_INLINE;
  let mainPayload = inlinePayload;
  let rowChunks = [];
  let mainSizeBytes = inlineSizeBytes;

  if (inlineSizeBytes > QUOTE_INLINE_PAYLOAD_CHUNK_BYTES) {
    storageMode = QUOTE_ROW_STORAGE_CHUNKED;
    rowChunks = buildCurtainRowChunks({ rows: compactRows, quoteId, workspaceUid });
    const chunkedPayloadBase = {
      ...basePayload,
      rowStorageMode: QUOTE_ROW_STORAGE_CHUNKED,
      rowChunkCount: rowChunks.length,
      rowChunkRefs: buildCurtainChunkRefs(rowChunks),
      contentStorageField: "row_chunks",
      rowCount,
      contentRowCount: rowCount,
    };
    mainPayload = allowDeleteFieldSentinels
      ? addCurtainAliasDeleteFields(chunkedPayloadBase, CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS)
      : chunkedPayloadBase;
    mainSizeBytes = estimateFirestorePayloadSize(mainPayload);
  }

  const chunkSizeDetails = rowChunks.map((chunk) => {
    const estimatedBytes = estimateFirestorePayloadSize(chunk);
    return {
      chunkId: chunk.chunkId,
      rowCount: chunk.rowCount,
      estimatedBytes,
      estimatedKb: Math.round((estimatedBytes / 1024) * 10) / 10,
    };
  });
  const oversizedChunks = chunkSizeDetails.filter(
    (chunk) => chunk.estimatedBytes > QUOTE_ROW_CHUNK_MAX_BYTES
  );

  if (isDevRuntime) {
    const largestRowKeys = getLargestPayloadKeys(compactRows[0] || {}, 12);
    const trace = {
      action,
      quoteNo: originalPayload.quoteNo || "",
      quoteId,
      path,
      saveMode,
      rowCount,
      accessoryCount: compactRows.filter((row) => row?.lineType === "accessory").length,
      fabricGroupCount: Object.keys(originalPayload.fabricSummaryMeta || {}).length,
      rawEstimatedBytes: rawOriginalSizeBytes,
      rawEstimatedKb: Math.round((rawOriginalSizeBytes / 1024) * 10) / 10,
      originalEstimatedBytes: originalSizeBytes,
      originalEstimatedKb: Math.round((originalSizeBytes / 1024) * 10) / 10,
      inlineEstimatedBytes: inlineSizeBytes,
      inlineEstimatedKb: Math.round((inlineSizeBytes / 1024) * 10) / 10,
      estimatedBytes: mainSizeBytes,
      estimatedKb: Math.round((mainSizeBytes / 1024) * 10) / 10,
      storageMode,
      rowChunkCount: rowChunks.length,
      canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
      contentAliasCountsBefore: getCurtainContentFieldCounts(originalPayload),
      contentAliasCountsAfter: getCurtainContentFieldCounts(mainPayload),
      largestTopLevelKeys: getLargestPayloadKeys(originalPayload, 12),
      largestTopLevelKeysAfter: getLargestPayloadKeys(mainPayload, 12),
      largestRowKeys,
      largestRows: getLargestCurtainRowsForPayloadTrace(compactRows, 8),
      chunkSizes: chunkSizeDetails,
      hasBase64: originalRisks.hasBase64 || inlineRisks.hasBase64,
      hasDataUrls: originalRisks.hasDataUrls || inlineRisks.hasDataUrls,
      hasPdfHtml: originalRisks.hasPdfHtml || inlineRisks.hasPdfHtml,
      hasDebugTrace: originalRisks.hasDebugTrace || inlineRisks.hasDebugTrace,
      hasFullCatalogueObjects:
        originalRisks.hasFullCatalogueObjects || inlineRisks.hasFullCatalogueObjects,
      hasImageData: originalRisks.hasImageData || inlineRisks.hasImageData,
      riskPaths: originalRisks.paths,
      previousRowChunkCount,
    };
    const logger =
      mainSizeBytes > QUOTE_INLINE_PAYLOAD_WARN_BYTES || rowChunks.length
        ? console.warn
        : console.log;
    logger("CURTAIN_QUOTE_PAYLOAD_SIZE_TRACE", trace);
    logger("QUOTE_PAYLOAD_SIZE_TRACE", trace);
    if (oversizedChunks.length) {
      devWarn("QUOTE_ROW_CHUNK_SIZE_WARNING", {
        quoteId,
        quoteNo: originalPayload.quoteNo || "",
        chunkSizes: chunkSizeDetails,
        targetBytes: QUOTE_ROW_CHUNK_TARGET_BYTES,
        maxBytes: QUOTE_ROW_CHUNK_MAX_BYTES,
      });
    }
  }

  if (mainSizeBytes > QUOTE_INLINE_PAYLOAD_CHUNK_BYTES || oversizedChunks.length) {
    const error = createOversizedQuotationPayloadError({
      quoteId,
      quoteNo: originalPayload.quoteNo || "",
      path,
      mainSizeBytes,
      rowChunkOversize: oversizedChunks,
    });
    if (isDevRuntime) {
      devError("QUOTE_PAYLOAD_SIZE_GUARD_FAILED", error.__fqPayloadSize);
    }
    throw error;
  }

  return {
    storageMode,
    mainPayload,
    rowChunks,
    rowCount,
    originalRows,
    compactRows,
    rawOriginalSizeBytes,
    originalSizeBytes,
    inlineSizeBytes,
    mainSizeBytes,
    shouldChunk: storageMode === QUOTE_ROW_STORAGE_CHUNKED,
    previousRowChunkCount: Number(previousRowChunkCount || 0),
  };
};

const isCurtainAccessoryRowForStorage = (row = {}) => {
  const lineType = String(row?.lineType || "").toLowerCase();
  const category = String(row?.category || row?.productConfigSnapshot?.category || "").toLowerCase();
  const calculationType = String(
    row?.calculationType || row?.productConfigSnapshot?.calculationType || ""
  ).toLowerCase();
  return lineType === "accessory" || category === "accessory" || calculationType.includes("accessory");
};

const getCurtainV3RowDocId = (row = {}, index = 0, prefix = "row") => {
  const candidate =
    row.rowId ||
    row.id ||
    row.clientRowId ||
    row.sourceRowId ||
    row.sourceId ||
    row.originalRowId;
  return candidate
    ? sanitizeFirestoreDocIdSegment(`${prefix}_${candidate}`)
    : sanitizeFirestoreDocIdSegment(`${prefix}_${String(index + 1).padStart(4, "0")}`);
};

const stripCurtainParentHeavyFieldsForV3 = (payload = {}) => {
  const next = sanitizeQuotationForFirestore(payload) || {};
  [
    ...CURTAIN_QUOTE_CONTENT_ALIAS_FIELDS,
    "rowChunks",
    "quoteRowChunks",
    "rowChunkRefs",
    "calculationSnapshot",
    "optimizerRuns",
    "optimizerResults",
    "optimizerState",
    "rollOptimizerState",
    "workshopZip",
    "customerPdfHtml",
    "workshopPdfHtml",
    "pdfHtml",
    "pdfBlob",
    "pdfData",
  ].forEach((fieldName) => {
    delete next[fieldName];
  });
  return next;
};

const buildCurtainQuoteV3StorageForSave = ({
  payload = {},
  constructionSourceRows = [],
  quoteId = "",
  workspaceUid = "",
  saveMode = "",
  action = "save",
  path = "",
} = {}) => {
  const originalPayload = payload || {};
  const compactRows = compactCurtainRowsForFirestore(
    getCurtainQuoteCanonicalRows(originalPayload)
  );
  const quoteNo = originalPayload.quoteNo || originalPayload.quoteNoString || quoteId || "";
  const saveRevisionId = `rev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const measurementRows = [];
  const accessoryRows = [];

  compactRows.forEach((row, index) => {
    const isAccessory = isCurtainAccessoryRowForStorage(row);
    const prefix = isAccessory ? "accessory" : "row";
    const docId = getCurtainV3RowDocId(row, index, prefix);
    const rowDoc = {
      ...(row || {}),
      id: docId,
      rowId: docId,
      quoteId,
      quoteNo,
      workspaceUid,
      rowOrder: index,
      order: index,
      lineType: isAccessory ? "accessory" : row?.lineType || "measurement",
      deleted: false,
      saveRevisionId,
      storageVersion: QUOTE_V3_STORAGE_VERSION,
    };
    if (isAccessory) {
      accessoryRows.push(rowDoc);
    } else {
      measurementRows.push(rowDoc);
    }
  });

  const rowCount = measurementRows.length + accessoryRows.length;
  const parentPayload = {
    ...stripCurtainParentHeavyFieldsForV3(originalPayload),
    ...rebaseQuoteConstructionIdentities(originalPayload, constructionSourceRows, compactRows),
    schemaVersion: QUOTE_STORAGE_SCHEMA_VERSION,
    storageVersion: QUOTE_V3_STORAGE_VERSION,
    quoteId,
    sourceQuoteId: quoteId,
    workspaceUid,
    quoteType: CURTAIN_QUOTE_TYPE,
    normalizedQuoteType: CANONICAL_CURTAIN_QUOTE_TYPE,
    quotationType: CURTAIN_QUOTE_TYPE,
    canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
    contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
    rowStorageMode: QUOTE_ROW_STORAGE_SUBCOLLECTION,
    contentStorageField: QUOTE_V3_CONTENT_STORAGE_FIELD,
    rowsStoredInSubcollection: true,
    migratedToSubcollectionRows: true,
    saveRevisionId,
    rowCount,
    contentRowCount: rowCount,
    measurementRowCount: measurementRows.length,
    accessoryCount: accessoryRows.length,
    rowChunkCount: 0,
    rowChunkRefs: [],
    totals: {
      subtotal: Number(originalPayload.subtotal || originalPayload.subTotal || 0),
      gstAmount: Number(originalPayload.gstAmount || originalPayload.totalGst || 0),
      discountAmount: Number(originalPayload.discountAmount || 0),
      totalAfterGst: Number(
        originalPayload.totalAfterGst ||
          originalPayload.finalTotal ||
          originalPayload.grandTotal ||
          0
      ),
      grandTotal: Number(originalPayload.grandTotal || originalPayload.finalTotal || 0),
    },
  };
  const mainSizeBytes = estimateFirestorePayloadSize(parentPayload);
  const rowDocsSizeBytes = estimateFirestorePayloadSize(measurementRows);
  const accessoryDocsSizeBytes = estimateFirestorePayloadSize(accessoryRows);

  if (isDevRuntime) {
    const trace = {
      action,
      quoteNo,
      quoteId,
      path,
      saveMode,
      storageVersion: QUOTE_V3_STORAGE_VERSION,
      rowStorageMode: QUOTE_ROW_STORAGE_SUBCOLLECTION,
      totalRowCount: rowCount,
      measurementRowCount: measurementRows.length,
      accessoryCount: accessoryRows.length,
      parentEstimatedBytes: mainSizeBytes,
      parentEstimatedKb: Math.round((mainSizeBytes / 1024) * 10) / 10,
      rowDocsEstimatedBytes: rowDocsSizeBytes,
      accessoryDocsEstimatedBytes: accessoryDocsSizeBytes,
      largestParentFields: getLargestPayloadKeys(parentPayload, 12),
      largestRows: getLargestCurtainRowsForPayloadTrace(compactRows, 8),
      contentAliasCountsBefore: getCurtainContentFieldCounts(originalPayload),
      contentAliasCountsAfter: getCurtainContentFieldCounts(parentPayload),
    };
    devLog("CURTAIN_QUOTE_V3_PAYLOAD_TRACE", trace);
  }

  return {
    storageMode: QUOTE_ROW_STORAGE_SUBCOLLECTION,
    mainPayload: parentPayload,
    rows: measurementRows,
    accessories: accessoryRows,
    rowChunks: [],
    rowCount,
    measurementRowCount: measurementRows.length,
    accessoryCount: accessoryRows.length,
    compactRows,
    saveRevisionId,
    mainSizeBytes,
    previousRowChunkCount: Number(originalPayload.rowChunkCount || 0),
  };
};

const getCurtainRowChunkReferences = (quote = {}, workspaceUidValue = "") => {
  if (quote?.rowStorageMode !== QUOTE_ROW_STORAGE_CHUNKED) return [];
  const workspace = String(workspaceUidValue || quote.workspaceUid || "").trim();
  const quoteId = String(quote.quoteId || quote.sourceQuoteId || quote.id || "").trim();
  const refs = Array.isArray(quote.rowChunkRefs) ? quote.rowChunkRefs : [];
  const normalizedRefs = refs
    .map((entry, index) => {
      if (typeof entry === "string") {
        return { path: entry, chunkIndex: index, chunkId: entry.split("/").pop() || "" };
      }
      if (!entry || typeof entry !== "object") return null;
      return {
        path: String(entry.path || "").trim(),
        chunkIndex: Number.isFinite(Number(entry.chunkIndex)) ? Number(entry.chunkIndex) : index,
        chunkId: String(entry.chunkId || entry.id || "").trim(),
      };
    })
    .filter((entry) => entry?.path);

  if (normalizedRefs.length) {
    return normalizedRefs.sort((left, right) => left.chunkIndex - right.chunkIndex);
  }

  const chunkCount = Math.max(0, Number(quote.rowChunkCount || 0));
  if (!workspace || !quoteId || !chunkCount) return [];
  return Array.from({ length: chunkCount }, (_, index) => {
    const chunkId = String(index + 1).padStart(4, "0");
    return {
      path: `users/${workspace}/quotations/${quoteId}/row_chunks/${chunkId}`,
      chunkIndex: index,
      chunkId,
    };
  });
};

const hydrateCurtainQuoteRowsFromSubcollections = async ({
  quote = {},
  workspaceUid = "",
  label = "curtainQuoteV3RowsLoad",
} = {}) => {
  if (!quote || !isCurtainQuoteSubcollectionStorage(quote)) return quote;
  assertCompatibleQuote(quote);
  if (quote.canonicalRowsHydrated === true) return quote;
  if (Number(quote.minimumReaderVersion) >= 2) {
    const quoteId = String(quote.quoteId || quote.sourceQuoteId || quote.id || '');
    const snap = await getDoc(doc(db, 'users', workspaceUid || quote.workspaceUid, 'quotations', quoteId));
    if (!snap.exists()) throw new Error('Quotation not found.');
    return { id: snap.id, ...snap.data() };
  }
  const workspace = String(workspaceUid || quote.workspaceUid || "").trim();
  const quoteId = String(quote.quoteId || quote.sourceQuoteId || quote.id || "").trim();
  if (!workspace || !quoteId) return quote;

  const quotePath = `users/${workspace}/quotations/${quoteId}`;
  const saveRevisionId = String(quote.saveRevisionId || "").trim();
  const rowsCollectionRef = collection(db, "users", workspace, "quotations", quoteId, "rows");
  const accessoriesCollectionRef = collection(
    db,
    "users",
    workspace,
    "quotations",
    quoteId,
    "accessories"
  );
  const rowsReadRef = saveRevisionId
    ? firestoreQuery(rowsCollectionRef, firestoreWhere("saveRevisionId", "==", saveRevisionId))
    : rowsCollectionRef;
  const accessoriesReadRef = saveRevisionId
    ? firestoreQuery(
        accessoriesCollectionRef,
        firestoreWhere("saveRevisionId", "==", saveRevisionId)
      )
    : accessoriesCollectionRef;
  let [rowsSnap, accessoriesSnap] = await Promise.all([
    getDocs(rowsReadRef, {
      label: `${label}.rows`,
      reason: "user_open_v3_quote_rows",
    }),
    getDocs(accessoriesReadRef, {
      label: `${label}.accessories`,
      reason: "user_open_v3_quote_accessories",
    }),
  ]);
  const expectedContentRows = Number(quote.rowCount || quote.contentRowCount || 0);
  if (saveRevisionId && expectedContentRows > 0 && rowsSnap.size + accessoriesSnap.size === 0) {
    [rowsSnap, accessoriesSnap] = await Promise.all([
      getDocs(rowsCollectionRef, {
        label: `${label}.rowsRevisionFallback`,
        reason: "user_open_v3_quote_rows_revision_fallback",
      }),
      getDocs(accessoriesCollectionRef, {
        label: `${label}.accessoriesRevisionFallback`,
        reason: "user_open_v3_quote_accessories_revision_fallback",
      }),
    ]);
  }
  const normalizeDocRows = (snap, fallbackLineType = "") =>
    snap.docs
      .map((docSnap) => ({
        id: docSnap.id,
        rowId: docSnap.id,
        ...(docSnap.data() || {}),
        lineType: docSnap.data()?.lineType || fallbackLineType,
      }))
      .filter((row) => row.deleted !== true && row.archived !== true);
  const allRows = [
    ...normalizeDocRows(rowsSnap, "measurement"),
    ...normalizeDocRows(accessoriesSnap, "accessory"),
  ];
  const revisionRows = saveRevisionId
    ? allRows.filter((row) => String(row.saveRevisionId || "") === saveRevisionId)
    : [];
  const rows = (revisionRows.length ? revisionRows : allRows)
    .sort((left, right) => {
      const leftOrder = Number(left.rowOrder ?? left.order ?? 0);
      const rightOrder = Number(right.rowOrder ?? right.order ?? 0);
      return leftOrder - rightOrder;
    })
    .map((row, index) => withCurtainRowId(row, index));

  if (isDevRuntime) {
    devLog("QUOTE_LOAD_V3_ROWS_TRACE", {
      quoteNo: quote.quoteNo || "",
      quoteId,
      quotePath,
      saveRevisionId,
      rowsDocsRead: rowsSnap.size,
      accessoryDocsRead: accessoriesSnap.size,
      totalRowsAfterRevisionFilter: rows.length,
      usedRevisionFilter: Boolean(saveRevisionId && revisionRows.length),
    });
  }

  return {
    ...quote,
    rows,
    rowCount: rows.length,
    contentRowCount: rows.length,
    rowSubcollectionHydrated: true,
    rowSubcollectionLoadedAt: new Date().toISOString(),
    rowSubcollectionPath: quotePath,
  };
};

const hydrateCurtainQuoteRowsFromChunks = async ({
  quote = {},
  workspaceUid = "",
  label = "curtainQuoteRowChunkLoad",
} = {}) => {
  if (quote && isCurtainQuoteSubcollectionStorage(quote)) {
    return hydrateCurtainQuoteRowsFromSubcollections({ quote, workspaceUid, label });
  }
  if (!quote || quote.rowStorageMode !== QUOTE_ROW_STORAGE_CHUNKED) return quote;
  const refs = getCurtainRowChunkReferences(quote, workspaceUid);
  if (!refs.length) {
    const inlineFallbackSelection = resolveCurtainQuoteContentSelection({
      ...quote,
      rowStorageMode: QUOTE_ROW_STORAGE_INLINE,
      contentSchemaVersion: "",
      canonicalContentField: "",
    });
    if (isDevRuntime) {
      devWarn("QUOTE_ROW_CHUNK_LOAD_MISSING_REFS", {
        quoteId: quote.quoteId || quote.id || "",
        quoteNo: quote.quoteNo || "",
        rowChunkCount: quote.rowChunkCount || 0,
        inlineFallbackField: inlineFallbackSelection.field || "empty",
        inlineFallbackRowCount: inlineFallbackSelection.count || 0,
      });
      devLog("QUOTE_LOAD_CHUNKS_TRACE", {
        quoteNo: quote.quoteNo || "",
        rowStorageMode: quote.rowStorageMode,
        rowChunkCount: quote.rowChunkCount || 0,
        rowChunkRefs: [],
        chunksRequested: 0,
        chunksFound: 0,
        totalRowsFromChunks: 0,
        totalRowsAfterFallback: inlineFallbackSelection.count || 0,
        missingChunkRefs: [],
        inlineFallbackField: inlineFallbackSelection.field || "empty",
      });
    }
    return {
      ...quote,
      rows: inlineFallbackSelection.items,
      rowChunkHydrated: false,
      rowChunkLoadSource: inlineFallbackSelection.field || "missing_row_chunk_refs",
      rowChunkLoadWarning: "missing_row_chunk_refs",
      rowChunkMissingRefs: [],
    };
  }

  const loadedChunks = await Promise.all(
    refs.map(async (chunkRef) => {
      const snap = await getDocCached(doc(db, ...chunkRef.path.split("/")), {
        label,
        cacheKey: chunkRef.path,
        ttlMs: 60 * 1000,
        reason: "user_open_chunked_quote",
      });
      if (!snap.exists()) {
        return {
          ...chunkRef,
          rows: [],
          missing: true,
        };
      }
      const data = snap.data() || {};
      return {
        ...chunkRef,
        chunkIndex: Number.isFinite(Number(data.chunkIndex))
          ? Number(data.chunkIndex)
          : chunkRef.chunkIndex,
        rows: Array.isArray(data.rows) ? data.rows : [],
      };
    })
  );
  const rowsFromChunks = loadedChunks
    .sort((left, right) => left.chunkIndex - right.chunkIndex)
    .flatMap((chunk) => chunk.rows || []);
  const inlineFallbackSelection = resolveCurtainQuoteContentSelection({
    ...quote,
    rowStorageMode: QUOTE_ROW_STORAGE_INLINE,
    contentSchemaVersion: "",
    canonicalContentField: "",
  });
  const rows = rowsFromChunks.length ? rowsFromChunks : inlineFallbackSelection.items;
  const missingChunkRefs = loadedChunks
    .filter((chunk) => chunk.missing)
    .map((chunk) => chunk.path || chunk.chunkId || "")
    .filter(Boolean);
  const sourceUsedForRows = rowsFromChunks.length
    ? "row_chunks"
    : inlineFallbackSelection.field || "empty";

  if (isDevRuntime) {
    devLog("QUOTE_LOAD_CHUNKS_TRACE", {
      quoteNo: quote.quoteNo || "",
      rowStorageMode: quote.rowStorageMode,
      rowChunkCount: quote.rowChunkCount || loadedChunks.length,
      rowChunkRefs: refs.map((ref) => ref.path || ref.chunkId || ""),
      chunksRequested: refs.length,
      chunksFound: loadedChunks.filter((chunk) => !chunk.missing).length,
      totalRowsFromChunks: rowsFromChunks.length,
      totalRowsAfterFallback: rows.length,
      missingChunkRefs,
      inlineFallbackField: inlineFallbackSelection.field || "empty",
      sourceUsedForRows,
    });
    devLog("QUOTE_ROW_CHUNK_LOAD_SUCCESS", {
      quoteId: quote.quoteId || quote.id || "",
      quoteNo: quote.quoteNo || "",
      rowChunkCount: loadedChunks.length,
      missingChunkCount: loadedChunks.filter((chunk) => chunk.missing).length,
      rowCount: rows.length,
      chunkRowCount: rowsFromChunks.length,
      sourceUsedForRows,
    });
  }

  return {
    ...quote,
    rows,
    rowChunkHydrated: true,
    rowChunkLoadedAt: new Date().toISOString(),
    rowChunkLoadSource: sourceUsedForRows,
    rowChunkMissingRefs: missingChunkRefs,
  };
};

if (isDevRuntime && typeof window !== "undefined") {
  window.__FQ_FIND_INVALID_QUOTE_PAYLOAD__ = () => {
    const history = Array.isArray(window.__FQ_LAST_QUOTE_WRITE_PAYLOADS__)
      ? window.__FQ_LAST_QUOTE_WRITE_PAYLOADS__
      : [window.__FQ_LAST_QUOTE_WRITE_PAYLOAD__ || {}];
    const checks = history.map((lastPayload) => {
      const payload = lastPayload.payload || lastPayload.sanitizedPayload || {};
      return {
        quoteNo: lastPayload.quoteNo || "",
        quoteId: lastPayload.quoteId || "",
        saveMode: lastPayload.saveMode || "",
        step: lastPayload.step || "",
        path: lastPayload.path || "",
        checkedAt: lastPayload.checkedAt || "",
        invalidValues: findInvalidFirestoreValues(payload),
        invalidBeforeSanitize: lastPayload.invalidBefore || [],
        invalidAfterSanitize: lastPayload.invalidAfter || [],
      };
    });
    devLog("QUOTE_SAVE_INVALID_PAYLOAD_VALUES", {
      phase: "manual_last_payload_check",
      checks,
    });
    return checks;
  };
}

const countGeneralQuoteImages = (quoteLike = {}) => {
  const generalQuote = quoteLike.generalQuote || quoteLike.generalQuoteData || quoteLike;
  const items = Array.isArray(generalQuote?.lineItems)
    ? generalQuote.lineItems
    : Array.isArray(generalQuote?.items)
    ? generalQuote.items
    : Array.isArray(quoteLike?.items)
    ? quoteLike.items
    : [];

  return items.reduce(
    (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
    0
  );
};

const validateGeneralQuotePayloadForFirestore = (payload = {}) => {
  const diagnostics = inspectGeneralQuotePayload(payload);
  const invalidPaths = [
    ...diagnostics.undefinedPaths.map((path) => `undefined:${path}`),
    ...diagnostics.fileBlobPaths.map((path) => `file_or_blob:${path}`),
    ...diagnostics.invalidNumberPaths.map((path) => `invalid_number:${path}`),
    ...diagnostics.functionPaths.map((path) => `function:${path}`),
    ...diagnostics.circularPaths.map((path) => `circular:${path}`),
  ];

  const searchTextLength = String(payload.searchText || "").length;
  if (searchTextLength > SEARCH_TEXT_MAX_CHARS) {
    invalidPaths.push(`oversized_searchText:${searchTextLength}`);
  }

  const searchKeywordsCount = Array.isArray(payload.searchKeywords)
    ? payload.searchKeywords.length
    : 0;
  if (searchKeywordsCount > SEARCH_KEYWORDS_MAX) {
    invalidPaths.push(`oversized_searchKeywords:${searchKeywordsCount}`);
  }

  if (
    Array.isArray(payload.searchKeywords) &&
    payload.searchKeywords.some(
      (keyword) => String(keyword || "").length > SEARCH_KEYWORD_MAX_CHARS
    )
  ) {
    invalidPaths.push("oversized_searchKeyword");
  }

  return {
    valid: invalidPaths.length === 0,
    invalidPaths,
    diagnostics,
    searchTextLength,
    searchKeywordsCount,
  };
};

const readLocalDraftRecord = (key) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    devWarn("Failed to read local draft:", error);
    return null;
  }
};

const writeLocalDraftRecord = (key, payload) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(sanitizeLocalDraftValue(payload)));
  } catch (error) {
    devWarn("Failed to write local draft:", error);
  }
};

const clearLocalDraftRecord = (key) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    devWarn("Failed to clear local draft:", error);
  }
};

const versionedCurtainDraftKey = key => String(key || '').includes(':curtainQuote:') ? `${key}:reader-v2` : key;
const saveLocalDraft = (key, payload) => writeLocalDraftRecord(versionedCurtainDraftKey(key), payload);
const loadLocalDraft = (key) => readLocalDraftRecord(versionedCurtainDraftKey(key)) || readLocalDraftRecord(key);
const clearLocalDraft = (key) => {
  clearLocalDraftRecord(versionedCurtainDraftKey(key));
  clearLocalDraftRecord(key);
};

const getGeneralQuotePayloadFromDraftRecord = (record = {}) => {
  if (!record || typeof record !== "object") return {};
  const candidates = [
    record.data,
    record.payload,
    record.quote,
    record.generalQuote,
    record.generalQuoteData,
    record,
  ];
  return candidates.find((candidate) => candidate && typeof candidate === "object") || {};
};

const buildGeneralQuoteRecoveryItemPreview = (items = []) =>
  (Array.isArray(items) ? items : []).slice(0, 3).map((item, index) => ({
    id: String(item?.id || item?.itemId || item?.rowId || `item-${index + 1}`),
    name: String(
      item?.name ||
        item?.itemName ||
        item?.productName ||
        item?.description ||
        item?.title ||
        `Item ${index + 1}`
    ),
    qty: Number(item?.qty ?? item?.quantity ?? item?.count ?? 0) || 0,
    rate: Number(item?.rate ?? item?.unitRate ?? item?.price ?? item?.mrp ?? 0) || 0,
    amount:
      Number(
        item?.amount ??
          item?.total ??
          item?.lineTotal ??
          item?.itemTotal ??
          item?.finalTotal ??
          0
      ) || 0,
  }));

const findLocalGeneralQuoteDrafts = ({ workspaceUid = "", quoteNo = "", customerName = "" } = {}) => {
  if (typeof window === "undefined") return [];

  const normalizedWorkspaceUid = String(workspaceUid || "").trim();
  const normalizedQuoteNo = String(quoteNo || "").trim().toLowerCase();
  const normalizedCustomerName = normalizeSearchText(customerName || "");
  const stores = [
    { storage: window.localStorage, storageType: "localStorage" },
    { storage: window.sessionStorage, storageType: "sessionStorage" },
  ].filter((entry) => entry.storage);
  const results = [];
  const seenKeys = new Set();

  stores.forEach(({ storage, storageType }) => {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || seenKeys.has(`${storageType}:${key}`)) continue;
      const lowerKey = key.toLowerCase();
      const looksLikeDraft =
        key.startsWith(LOCAL_DRAFT_PREFIX) ||
        (lowerKey.includes("draft") &&
          (lowerKey.includes("general") || lowerKey.includes("quote")));
      if (!looksLikeDraft) continue;

      let record = null;
      try {
        const raw = storage.getItem(key);
        if (!raw) continue;
        record = JSON.parse(raw);
      } catch (error) {
        if (isDevRuntime) {
          devWarn("GENERAL_QUOTE_LOCAL_DRAFT_PARSE_FAILED", {
            key,
            storageType,
            message: error?.message,
          });
        }
        continue;
      }

      if (!record || typeof record !== "object") continue;
      const recordWorkspaceUid = String(record.workspaceUid || "").trim();
      if (
        normalizedWorkspaceUid &&
        recordWorkspaceUid &&
        recordWorkspaceUid !== normalizedWorkspaceUid
      ) {
        continue;
      }

      const moduleName = String(record.module || "").toLowerCase();
      const payload = getGeneralQuotePayloadFromDraftRecord(record);
      const contentSelection = resolveGeneralQuoteContentSelection(
        payload,
        payload.generalQuote || payload.generalQuoteData || {},
        { allowCanonicalEmptyAliasRecovery: true }
      );
      const itemCount = Number(contentSelection.count || 0);
      const payloadQuoteNo = String(
        payload.quoteNo ||
          payload.quoteNumber ||
          payload.quotationNumber ||
          record.metadata?.quoteNo ||
          record.entityId ||
          ""
      ).trim();
      const payloadCustomerName = String(
        payload.customerName ||
          payload.quoteFor?.name ||
          payload.quoteFor?.company ||
          record.metadata?.customerLabel ||
          ""
      ).trim();
      const quoteNoMatches =
        normalizedQuoteNo &&
        payloadQuoteNo &&
        payloadQuoteNo.toLowerCase() === normalizedQuoteNo;
      const keyMatchesQuoteNo = normalizedQuoteNo && lowerKey.includes(normalizedQuoteNo);
      const customerMatches =
        normalizedCustomerName &&
        normalizeSearchText(payloadCustomerName).includes(normalizedCustomerName);
      const isGeneralDraft =
        moduleName === LOCAL_DRAFT_MODULES.GENERAL_QUOTE ||
        lowerKey.includes(LOCAL_DRAFT_MODULES.GENERAL_QUOTE.toLowerCase()) ||
        lowerKey.includes("general");

      if (!isGeneralDraft && !quoteNoMatches && !keyMatchesQuoteNo) continue;
      if (!quoteNoMatches && !keyMatchesQuoteNo && normalizedCustomerName && !customerMatches) {
        continue;
      }
      if (!itemCount) continue;

      seenKeys.add(`${storageType}:${key}`);
      const totalSnapshot = getSavedQuoteDisplayTotal(payload);
      results.push({
        sourceType: "local_draft",
        path: `${storageType}:${key}`,
        storageKey: key,
        storageType,
        createdAt: null,
        updatedAt: record.updatedAt || record.savedAt || payload.updatedAt || null,
        itemCount,
        itemSourceField: contentSelection.field || "",
        customerName: payloadCustomerName,
        quoteNo: payloadQuoteNo,
        grandTotal: Number(totalSnapshot.amount || payload.grandTotal || payload.totalAmount || 0),
        status: "local_draft",
        canOpenAsDraft: true,
        recommendedAction: "Open this device draft and save it as a new quotation.",
        itemPreview: buildGeneralQuoteRecoveryItemPreview(contentSelection.items),
        quote: payload,
      });
    }
  });

  return results.sort(
    (left, right) =>
      (safeTimeMillis(right.updatedAt) || 0) - (safeTimeMillis(left.updatedAt) || 0)
  );
};
const getDraftMeta = (key) => {
  const record = loadLocalDraft(key);
  if (!record) return null;
  return {
    module: record.module || "",
    workspaceUid: record.workspaceUid || "",
    quoteId: record.entityId || record.metadata?.quoteId || "",
    savedAt: record.updatedAt || record.savedAt || "",
    appVersion: record.appVersion || "",
    payloadVersion: record.payloadVersion || record.version || LOCAL_DRAFT_VERSION,
    serverUpdatedAt: Number(record.serverUpdatedAt || 0),
  };
};

const uniqueValues = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));

const CRM_LEGACY_QUOTE_LINK_FIELDS = [
  "linkedQuoteId",
  "quoteId",
  "primaryQuoteId",
  "linkedQuoteIds",
  "linkedQuoteNo",
  "primaryQuoteNo",
  "quoteNo",
  "linkedQuoteType",
  "linkedQuoteValue",
  "linkedQuoteCustomerName",
  "linkedQuoteDate",
];

const CRM_CLOSED_DEAL_KEYWORDS = [
  "cancel",
  "lost",
  "installed",
  "completed",
  "closed",
];

const logQuoteToDealLinkDecision = (details = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  devInfo("[FabriQuote quote-to-deal]", {
    quoteId: details.quoteId || "",
    customerId: details.customerId || "",
    matchedCustomerId: details.matchedCustomerId || "",
    existingOpenDealIds: Array.isArray(details.existingOpenDealIds)
      ? details.existingOpenDealIds
      : [],
    actionSelected: details.actionSelected || "",
    finalLinkedLeadId: details.finalLinkedLeadId || "",
  });
};

const stripLegacyCrmQuoteLinkFields = (record = {}) => {
  const nextRecord = { ...(record || {}) };
  CRM_LEGACY_QUOTE_LINK_FIELDS.forEach((field) => {
    delete nextRecord[field];
  });
  return nextRecord;
};

const createLegacyCrmQuoteLinkDeletePatch = () =>
  CRM_LEGACY_QUOTE_LINK_FIELDS.reduce((patch, field) => {
    patch[field] = deleteField();
    return patch;
  }, {});

const createLegacyQuoteCrmLinkDeletePatch = () =>
  CRM_LEGACY_QUOTE_LINK_FIELDS.reduce((patch, field) => {
    patch[`crm.${field}`] = deleteField();
    return patch;
  }, {});

const normalizeCrmLinkedQuoteEntry = (entry = {}, index = 0) => {
  const quoteId = String(
    entry?.quoteId ||
      entry?.id ||
      entry?.primaryQuoteId ||
      entry?.linkedQuoteId ||
      ""
  ).trim();
  const quoteNo = String(
    entry?.quoteNo ||
      entry?.quoteNumber ||
      entry?.primaryQuoteNo ||
      entry?.linkedQuoteNo ||
      ""
  ).trim();

  if (!quoteId && !quoteNo) return null;

  return {
    quoteId,
    quoteNo,
    type: String(
      entry?.type || entry?.quoteType || entry?.linkedQuoteType || entry?.pipelineType || ""
    ).trim(),
    value: Number(entry?.value ?? entry?.totalValue ?? entry?.linkedQuoteValue ?? 0) || 0,
    isPrimary: entry?.isPrimary === true || index === 0,
  };
};

const normalizeCrmLinkedQuotes = (leadLike = {}, overrides = {}) => {
  const hasLinkedQuotesOverride = Object.prototype.hasOwnProperty.call(
    overrides,
    "linkedQuotes"
  );
  const sourceLinkedQuotes = hasLinkedQuotesOverride
    ? overrides.linkedQuotes
    : leadLike.linkedQuotes;
  const entries = (Array.isArray(sourceLinkedQuotes) ? sourceLinkedQuotes : [])
    .map((entry, index) => normalizeCrmLinkedQuoteEntry(entry, index))
    .filter(Boolean);

  if (!hasLinkedQuotesOverride) {
    const legacyIds = uniqueValues([
      leadLike.primaryQuoteId,
      leadLike.linkedQuoteId,
      leadLike.quoteId,
      ...(Array.isArray(leadLike.linkedQuoteIds) ? leadLike.linkedQuoteIds : []),
    ]);
    const legacyQuoteNo = String(
      leadLike.primaryQuoteNo || leadLike.linkedQuoteNo || leadLike.quoteNo || ""
    ).trim();

    legacyIds.forEach((quoteId, index) => {
      entries.push(
        normalizeCrmLinkedQuoteEntry({
          quoteId,
          quoteNo: index === 0 ? legacyQuoteNo : "",
          type: leadLike.linkedQuoteType || leadLike.pipelineType || leadLike.quoteType || "",
          value: Number(leadLike.linkedQuoteValue || leadLike.finalValue || 0),
          isPrimary: quoteId === leadLike.primaryQuoteId || index === 0,
        })
      );
    });

    if (!legacyIds.length && legacyQuoteNo) {
      entries.push(
        normalizeCrmLinkedQuoteEntry({
          quoteNo: legacyQuoteNo,
          type: leadLike.linkedQuoteType || leadLike.pipelineType || leadLike.quoteType || "",
          value: Number(leadLike.linkedQuoteValue || leadLike.finalValue || 0),
          isPrimary: true,
        })
      );
    }
  }

  const seen = new Set();
  const deduped = entries.filter(Boolean).filter((entry) => {
    const key = entry.quoteId ? `id:${entry.quoteId}` : `no:${entry.quoteNo}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const primaryIndex = deduped.findIndex((entry) => entry.isPrimary === true);
  const resolvedPrimaryIndex = primaryIndex >= 0 ? primaryIndex : 0;

  return deduped.map((entry, index) => ({
    quoteId: entry.quoteId,
    quoteNo: entry.quoteNo,
    type: entry.type,
    value: Number(entry.value || 0),
    isPrimary: index === resolvedPrimaryIndex,
  }));
};

const getPrimaryCrmLinkedQuote = (linkedQuotes = []) =>
  (Array.isArray(linkedQuotes) ? linkedQuotes : []).find((entry) => entry?.isPrimary) ||
  (Array.isArray(linkedQuotes) ? linkedQuotes[0] : null) ||
  null;

const QUOTE_DEAL_LINKS_COLLECTION = "quote_deal_links";
const ARCHIVED_DUPLICATE_STATUS = "archived_duplicate";

const sanitizeFirestoreDocIdSegment = (value = "") => {
  const sanitized = String(value || "")
    .trim()
    .replace(/[/.#[\]]+/g, "_")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return sanitized || "unknown_quote";
};

const getQuoteDealLinkDocId = (quoteId = "") =>
  sanitizeFirestoreDocIdSegment(quoteId);

const buildQuoteCreatedLeadId = (quoteId = "") =>
  `quote_${sanitizeFirestoreDocIdSegment(quoteId)}`;

const getCrmLeadQuoteReferenceSummary = (leadLike = {}) => {
  const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
  const primaryLinkedQuote =
    getPrimaryCrmLinkedQuote(linkedQuotes) || leadLike.primaryLinkedQuote || {};
  const crm = leadLike.crm || {};
  const crmLinkedQuotes = normalizeCrmLinkedQuotes(crm);
  const allLinkedQuotes = [
    ...linkedQuotes,
    ...crmLinkedQuotes,
    leadLike.linkedQuote,
    leadLike.quote,
    primaryLinkedQuote,
    crm.linkedQuote,
    crm.quote,
  ].filter(Boolean);

  const quoteIds = uniqueValues([
    leadLike.quoteId,
    leadLike.linkedQuoteId,
    leadLike.primaryQuoteId,
    leadLike.id && leadLike.crmRecordType === "quote" ? leadLike.id : "",
    crm.quoteId,
    crm.linkedQuoteId,
    crm.primaryQuoteId,
    primaryLinkedQuote.quoteId,
    ...(Array.isArray(leadLike.linkedQuoteIds) ? leadLike.linkedQuoteIds : []),
    ...(Array.isArray(crm.linkedQuoteIds) ? crm.linkedQuoteIds : []),
    ...allLinkedQuotes.map((entry) => entry?.quoteId || entry?.id),
  ])
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const quoteNos = uniqueValues([
    leadLike.quoteNo,
    leadLike.linkedQuoteNo,
    leadLike.primaryQuoteNo,
    leadLike.primaryQuoteNumber,
    crm.quoteNo,
    crm.linkedQuoteNo,
    crm.primaryQuoteNo,
    primaryLinkedQuote.quoteNo,
    ...allLinkedQuotes.map(
      (entry) => entry?.quoteNo || entry?.quoteNumber || entry?.linkedQuoteNo
    ),
  ])
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return {
    linkedQuotes: normalizeCrmLinkedQuotes(
      {},
      { linkedQuotes: allLinkedQuotes.filter((entry) => entry?.quoteId || entry?.quoteNo) }
    ),
    quoteIds,
    quoteNos,
    normalizedQuoteNos: quoteNos.map((value) => normalizeSearchCompact(value)),
    primaryQuoteId: String(primaryLinkedQuote.quoteId || quoteIds[0] || "").trim(),
    primaryQuoteNo: String(primaryLinkedQuote.quoteNo || quoteNos[0] || "").trim(),
  };
};

const doesCrmLeadReferenceQuote = (leadLike = {}, quoteId = "", quoteNo = "") => {
  const referenceSummary = getCrmLeadQuoteReferenceSummary(leadLike);
  const targetQuoteId = String(quoteId || "").trim();
  const targetQuoteNo = String(quoteNo || "").trim();
  const normalizedTargetQuoteNo = normalizeSearchCompact(targetQuoteNo);

  return Boolean(
    (targetQuoteId && referenceSummary.quoteIds.includes(targetQuoteId)) ||
      (targetQuoteNo && referenceSummary.quoteNos.includes(targetQuoteNo)) ||
      (normalizedTargetQuoteNo &&
        referenceSummary.normalizedQuoteNos.includes(normalizedTargetQuoteNo))
  );
};

const getCanonicalDealKey = (dealLike = {}) => {
  const referenceSummary = getCrmLeadQuoteReferenceSummary(dealLike);
  const quoteId =
    referenceSummary.primaryQuoteId ||
    referenceSummary.quoteIds.find(Boolean) ||
    "";
  if (quoteId) return `quote:${quoteId}`;

  const quoteNo =
    referenceSummary.primaryQuoteNo ||
    referenceSummary.quoteNos.find(Boolean) ||
    "";
  const normalizedQuoteNo = normalizeSearchCompact(quoteNo);
  if (normalizedQuoteNo) return `quoteNo:${normalizedQuoteNo}`;

  return `lead:${String(dealLike.id || "unknown_lead").trim()}`;
};

const isArchivedDuplicateCrmLead = (leadLike = {}) =>
  isCrmRecordUnavailable(leadLike) ||
  leadLike?.isArchivedDuplicate === true ||
  String(leadLike?.status || leadLike?.dealStatus || "")
    .toLowerCase()
    .includes(ARCHIVED_DUPLICATE_STATUS);

const getCrmLeadSortMillis = (leadLike = {}, key = "created") => {
  const value =
    key === "updated"
      ? leadLike.updatedAt || leadLike.createdAt
      : leadLike.createdAt || leadLike.updatedAt;
  return Number(safeTimeMillis(value) || 0);
};

const chooseCanonicalQuoteDeal = (leads = [], preferredLeadId = "") => {
  const normalizedPreferredId = String(preferredLeadId || "").trim();
  const candidates = (Array.isArray(leads) ? leads : [])
    .filter((lead) => lead?.id)
    .filter(
      (lead, index, source) =>
        source.findIndex((item) => String(item.id) === String(lead.id)) === index
    );

  if (!candidates.length) return null;

  const preferred = normalizedPreferredId
    ? candidates.find((lead) => String(lead.id) === normalizedPreferredId)
    : null;
  if (preferred && !isArchivedDuplicateCrmLead(preferred)) return preferred;

  const activeCandidates = candidates.filter(
    (lead) => !isArchivedDuplicateCrmLead(lead)
  );
  const sortable = activeCandidates.length ? activeCandidates : candidates;

  return [...sortable].sort((a, b) => {
    const aRecordScore = a.crmRecordType === "quote" ? 1 : 0;
    const bRecordScore = b.crmRecordType === "quote" ? 1 : 0;
    if (aRecordScore !== bRecordScore) return aRecordScore - bRecordScore;

    const aClosed = a.isCrmClosed === true || a.isCrmCancelled === true ? 1 : 0;
    const bClosed = b.isCrmClosed === true || b.isCrmCancelled === true ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;

    const createdDelta = getCrmLeadSortMillis(a, "created") - getCrmLeadSortMillis(b, "created");
    if (createdDelta !== 0) return createdDelta;

    return String(a.id || "").localeCompare(String(b.id || ""));
  })[0];
};

const dedupeCrmDealsByQuote = (deals = []) => {
  const groups = new Map();
  (Array.isArray(deals) ? deals : []).forEach((deal) => {
    const key = getCanonicalDealKey(deal);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(deal);
  });

  return Array.from(groups.values()).map((group) => {
    if (group.length === 1) return group[0];
    return chooseCanonicalQuoteDeal(group) || group[0];
  });
};

const buildQuoteDealDuplicateGroups = (leads = []) => {
  const groups = new Map();
  (Array.isArray(leads) ? leads : [])
    .filter((lead) => lead?.id)
    .forEach((lead) => {
      const referenceSummary = getCrmLeadQuoteReferenceSummary(lead);
      const key = getCanonicalDealKey(lead);
      if (key.startsWith("lead:")) return;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          quoteId: referenceSummary.primaryQuoteId || "",
          quoteNo: referenceSummary.primaryQuoteNo || "",
          leads: [],
        });
      }
      groups.get(key).leads.push(lead);
    });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      canonicalLead: chooseCanonicalQuoteDeal(group.leads),
      duplicateLeads: group.leads.filter(
        (lead) => lead.id !== chooseCanonicalQuoteDeal(group.leads)?.id
      ),
    }))
    .filter((group) => group.leads.length > 1);
};

/**
 * Atomically gets the next quote number and increments it.
 * This prevents duplicates even with autosave / multiple tabs.
 */




// ================= FABRIC KEY NORMALIZATION =================
const normalizeFabricKey = (name) => {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
};

const WORKSHOP_EMPTY_VALUE = "—";

const DEFAULT_HALF_PIECE_REUSE_SETTINGS = {
  heightMatchMode: "can_trim_down",
  heightToleranceInches: 1,
  allowLargerPieceToBeTrimmed: true,
};

const HALF_PIECE_EPSILON = 0.000001;

const hasOwnRepeatKey = (source = {}, keys = []) =>
  keys.some((key) => Object.prototype.hasOwnProperty.call(source, key));

const isRepeatToggleEnabled = (source = {}, keys = []) =>
  keys.some((key) => {
    const value = source?.[key];
    return value === true || value === "true" || value === 1 || value === "1";
  });

const formatRepeatSettings = (repeatSettings = {}) => {
  if (!repeatSettings || typeof repeatSettings !== "object" || Array.isArray(repeatSettings)) {
    return WORKSHOP_EMPTY_VALUE;
  }

  const normalizedRepeat = normalizeCurtainRepeatEntry(repeatSettings);
  const parts = [];

  const verticalToggleKeys = ["verticalEnabled", "verticalRepeatEnabled"];
  const horizontalToggleKeys = ["horizontalEnabled", "horizontalRepeatEnabled"];
  const verticalHasExplicitToggle = hasOwnRepeatKey(repeatSettings, verticalToggleKeys);
  const horizontalHasExplicitToggle = hasOwnRepeatKey(repeatSettings, horizontalToggleKeys);
  const verticalEnabled =
    normalizedRepeat.verticalEnabled ||
    isRepeatToggleEnabled(repeatSettings, verticalToggleKeys) ||
    (!verticalHasExplicitToggle &&
      (repeatSettings.verticalRepeat === true ||
        repeatSettings.verticalRepeat === "true" ||
        Number(normalizedRepeat.verticalRepeatInch || 0) > 0));
  const horizontalEnabled =
    normalizedRepeat.horizontalEnabled ||
    isRepeatToggleEnabled(repeatSettings, horizontalToggleKeys) ||
    (!horizontalHasExplicitToggle &&
      (repeatSettings.horizontalRepeat === true ||
        repeatSettings.horizontalRepeat === "true" ||
        Number(normalizedRepeat.horizontalRepeatInch || 0) > 0));

  const verticalValue = formatRepeatMeasurement(
    normalizedRepeat.verticalRepeatInch,
    normalizedRepeat.verticalRepeatUnit
  );
  const horizontalValue = formatRepeatMeasurement(
    normalizedRepeat.horizontalRepeatInch,
    normalizedRepeat.horizontalRepeatUnit
  );

  if (verticalEnabled && Number(normalizedRepeat.verticalRepeatInch || 0) > 0) {
    parts.push(`V: ${verticalValue} ${normalizedRepeat.verticalRepeatUnit}`);
  }
  if (horizontalEnabled && Number(normalizedRepeat.horizontalRepeatInch || 0) > 0) {
    parts.push(`H: ${horizontalValue} ${normalizedRepeat.horizontalRepeatUnit}`);
  }
  return parts.length ? parts.join(", ") : WORKSHOP_EMPTY_VALUE;
};

const getRepeatScopeSummaryDisplay = (repeatSettings = {}, productTypeOptions = []) => {
  const normalizedMode = normalizeRepeatApplyMode(repeatSettings?.applyMode);
  if (normalizedMode !== REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES) {
    return "All fabric products";
  }

  const scope = normalizeRepeatProductTypeScope(repeatSettings?.productTypeScope || []);
  if (!scope.length) return "Select at least one product type";

  const labelByKey = new Map(
    (Array.isArray(productTypeOptions) ? productTypeOptions : [])
      .map((option) => [option.key, option.label])
      .filter(([key]) => key)
  );
  const labels = scope.map((key) => labelByKey.get(key) || key).filter(Boolean);
  return labels.length ? labels.join(", ") : "Selected product types";
};

const getRepeatSummaryDisplay = (summary = {}) => {
  const candidate =
    summary?.repeatSettings && typeof summary.repeatSettings === "object"
      ? summary.repeatSettings
      : summary?.repeatSummary;
  if (candidate && typeof candidate === "object") {
    const repeatText = formatRepeatSettings(candidate);
    if (repeatText === WORKSHOP_EMPTY_VALUE) return repeatText;
    if (
      normalizeRepeatApplyMode(candidate.applyMode) ===
      REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES
    ) {
      return `${repeatText}\nApplies: ${getRepeatScopeSummaryDisplay(
        candidate,
        summary?.repeatProductTypeOptions || []
      )}`;
    }
    return repeatText;
  }
  const text = String(candidate ?? "").trim();
  if (!text || text === "[object Object]") return WORKSHOP_EMPTY_VALUE;
  return text;
};

const normalizeCurtainRepeatEntry = (repeat = {}) => {
  const source = repeat && typeof repeat === "object" ? repeat : {};
  const hasVerticalDisplayValue = Object.prototype.hasOwnProperty.call(
    source,
    "verticalRepeatValue"
  );
  const hasHorizontalDisplayValue = Object.prototype.hasOwnProperty.call(
    source,
    "horizontalRepeatValue"
  );
  const verticalRepeat = normalizeRepeatMeasurement({
    value: hasVerticalDisplayValue ? source.verticalRepeatValue : undefined,
    unit: source.verticalRepeatUnit || "in",
    fallbackIn:
      source.verticalRepeatInch ??
      source.verticalRepeatIn ??
      source.vr ??
      source.verticalRepeat ??
      "",
  });
  const horizontalRepeat = normalizeRepeatMeasurement({
    value: hasHorizontalDisplayValue ? source.horizontalRepeatValue : undefined,
    unit: source.horizontalRepeatUnit || "in",
    fallbackIn:
      source.horizontalRepeatInch ??
      source.horizontalRepeatIn ??
      source.hr ??
      source.horizontalRepeat ??
      "",
  });
  return {
    verticalEnabled:
      source.verticalEnabled === true ||
      source.verticalEnabled === "true" ||
      source.verticalRepeatEnabled === true ||
      source.verticalRepeatEnabled === "true",
    verticalRepeatValue: verticalRepeat.displayValue,
    verticalRepeatUnit: verticalRepeat.displayUnit,
    verticalRepeatIn: verticalRepeat.valueInches,
    verticalRepeatInch: verticalRepeat.valueInches,
    horizontalEnabled:
      source.horizontalEnabled === true ||
      source.horizontalEnabled === "true" ||
      source.horizontalRepeatEnabled === true ||
      source.horizontalRepeatEnabled === "true",
    horizontalRepeatValue: horizontalRepeat.displayValue,
    horizontalRepeatUnit: horizontalRepeat.displayUnit,
    horizontalRepeatIn: horizontalRepeat.valueInches,
    horizontalRepeatInch: horizontalRepeat.valueInches,
    applyMode: normalizeRepeatApplyMode(
      source.applyMode || source.repeatApplyMode || source.appliesToMode
    ),
    productTypeScope: normalizeRepeatProductTypeScope(
      source.productTypeScope ||
        source.repeatProductTypeScope ||
        source.repeatProductTypes ||
        source.productTypes ||
        source.appliesToProductTypes ||
        []
    ),
  };
};

const normalizeCurtainRepeatSettingsMap = (settings = {}) => {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) return {};
  return Object.fromEntries(
    Object.entries(settings)
      .map(([rawKey, repeat]) => {
        const normalizedKey = normalizeFabricKey(rawKey);
        return normalizedKey
          ? [normalizedKey, normalizeCurtainRepeatEntry(repeat)]
          : null;
      })
      .filter(Boolean)
  );
};

const hasMeaningfulCurtainRepeatEntry = (repeat = {}) => {
  const normalized = normalizeCurtainRepeatEntry(repeat);
  return (
    normalized.verticalEnabled ||
    normalized.horizontalEnabled ||
    Number(normalized.verticalRepeatInch || 0) > 0 ||
    Number(normalized.horizontalRepeatInch || 0) > 0
  );
};

const getCurtainRepeatSignature = (repeat = {}) => {
  const normalized = normalizeCurtainRepeatEntry(repeat);
  const verticalValue = Number(normalized.verticalRepeatInch || 0);
  const horizontalValue = Number(normalized.horizontalRepeatInch || 0);
  return [
    normalized.verticalEnabled && verticalValue > 0 ? `v:${round2(verticalValue)}` : "v:off",
    normalized.horizontalEnabled && horizontalValue > 0 ? `h:${round2(horizontalValue)}` : "h:off",
    `mode:${normalized.applyMode}`,
    `scope:${(normalized.productTypeScope || []).join(",")}`,
  ].join("|");
};

const applyHorizontalRepeatToFabricRules = (
  fabricRules = {},
  repeatSettings = {}
) => {
  return horizontalPatternRules(fabricRules, normalizeCurtainRepeatEntry(repeatSettings));
};

const sanitizeEditableText = (value) => {
  const text = String(value ?? "").trim();
  const lowered = text.toLowerCase();
  if (text === "\u2014" || text === "\u2013") {
    return "";
  }
  if (["—", "-", "n/a", "na"].includes(text) || ["n/a", "na"].includes(lowered)) {
    return "";
  }
  return text;
};

const normalizeWorkshopText = (value) => {
  if (value == null) return "";
  const text = sanitizeEditableText(value);
  if (!text) return "";

  const lowered = text.toLowerCase();
  if (["n/a", "na", "not selected", "fabric pending", "undefined", "null"].includes(lowered)) {
    return "";
  }

  return text;
};

const isWorkshopResolvedLabel = (value) => {
  const text = String(value ?? "").trim();
  const normalized = text.toLowerCase();
  if (!text) return false;
  if (text === WORKSHOP_EMPTY_VALUE || text === "\u2014" || text === "\u2013" || text === "-") {
    return false;
  }
  if (["n/a", "na", "not selected", "undefined", "null"].includes(normalized)) {
    return false;
  }
  return true;
};

const getRawEditableTextValue = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    return getWorkshopFieldLabel(value);
  }
  const text = String(value);
  const trimmed = text.trim();
  const lowered = trimmed.toLowerCase();
  if (["â€”", "\u2014", "\u2013", "-", "n/a", "na"].includes(trimmed)) return "";
  if (["n/a", "na", "undefined", "null"].includes(lowered)) return "";
  return text;
};

const getInputValue = (value) => (value == null ? "" : String(value));

const isSrPricingRuleDisplayText = (value = "") => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  const normalizedSr = normalizeSrNumber(text);
  return (
    /^(?:SR\s*)?\d+\s+(?:to|[\u2013\u2014])\s*\d+$/i.test(text) ||
    /^SR\s+[0-9]+(?:\s*[,;]\s*[0-9]+)+$/i.test(normalizedSr)
  );
};

const getExactFabricSrNumber = (value = "", srLabel = "") => {
  const srNumber = normalizeSrNumber(value || "");
  if (!srNumber) return "";
  if (/^SR\s+\d+\s+to\s+\d+$/i.test(srNumber)) return "";
  return srNumber;
};

const getSpecificRowSrNumber = (row = {}) =>
  getExactFabricSrNumber(row?.srNumber || row?.srNo || row?.sr || "", row?.srLabel || "");

const getSpecificFabricSrNumberFromSources = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const srNumber = getExactFabricSrNumber(
      source.srNumber || source.srNo || source.sr || "",
      source.srLabel || source.srPriceRuleLabel || ""
    );
    if (srNumber) return srNumber;
  }
  return "";
};

const FABRIC_LIBRARY_COLLECTION = "fabric_library";
const FABRIC_PHOTO_STORAGE_ROOT = "fabricPhotos";
const FABRIC_PHOTO_HISTORY_LIMIT = 5;

const sanitizeFabricLibraryKeySegment = (value = "") => {
  const text = sanitizeEditableText(value)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return text.slice(0, 70);
};

const normalizeFabricPhotoRef = (photo = null) => {
  if (!photo || typeof photo !== "object" || Array.isArray(photo)) return null;
  const downloadUrl = sanitizeEditableText(
    photo.downloadUrl || photo.downloadURL || photo.url || ""
  );
  const thumbDownloadUrl = sanitizeEditableText(
    photo.thumbDownloadUrl ||
      photo.thumbnailDownloadURL ||
      photo.previewUrl ||
      photo.thumbnailUrl ||
      ""
  );
  const storagePath = sanitizeEditableText(photo.storagePath || photo.fullPath || "");
  const thumbStoragePath = sanitizeEditableText(
    photo.thumbStoragePath || photo.thumbnailFullPath || photo.thumbnailStoragePath || ""
  );
  if (!downloadUrl && !thumbDownloadUrl && !storagePath && !thumbStoragePath) return null;
  return {
    storagePath,
    downloadUrl,
    thumbStoragePath,
    thumbDownloadUrl,
    uploadedAt: sanitizeEditableText(photo.uploadedAt || ""),
    uploadedBy: sanitizeEditableText(photo.uploadedBy || ""),
    fileName: sanitizeEditableText(photo.fileName || photo.filename || photo.name || ""),
    contentType: sanitizeEditableText(photo.contentType || ""),
    width: Number.isFinite(Number(photo.width)) ? Number(photo.width) : 0,
    height: Number.isFinite(Number(photo.height)) ? Number(photo.height) : 0,
    imageId: sanitizeEditableText(photo.imageId || photo.id || ""),
    fileId: sanitizeEditableText(photo.fileId || ""),
  };
};

const buildFabricPhotoHistory = (photos = []) => {
  const source = Array.isArray(photos) ? photos : [photos];
  const seen = new Set();
  return source
    .map((photo) => normalizeFabricPhotoRef(photo))
    .filter(Boolean)
    .filter((photo) => {
      const key = photo.storagePath || photo.downloadUrl || photo.imageId || photo.fileId;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, FABRIC_PHOTO_HISTORY_LIMIT);
};

const FABRIC_PHOTO_QR_MODAL_INITIAL = {
  open: false,
  loading: false,
  status: "idle",
  error: "",
  sessionId: "",
  shortCode: "",
  uploadUrl: "",
  legacyUploadUrl: "",
  expiresAt: "",
  summaryKey: "",
  fabricLibraryKey: "",
  fabricDisplayName: "",
  storagePathPrefix: "",
  identity: null,
};

const getFabricPhotoDisplayUrl = (photo = null) => {
  const normalized = normalizeFabricPhotoRef(photo);
  return (
    normalized?.thumbDownloadUrl ||
    normalized?.downloadUrl ||
    ""
  );
};

const getFabricPhotoFullUrl = (photo = null) => {
  const normalized = normalizeFabricPhotoRef(photo);
  return normalized?.downloadUrl || normalized?.thumbDownloadUrl || "";
};

const getFabricLibraryIdentity = ({ row = {}, summary = {}, meta = {} } = {}) => {
  const brandId = sanitizeEditableText(
    row.brandId || row.fabricBrandId || summary.brandId || meta.brandId || ""
  );
  const brandName = sanitizeEditableText(
    row.brandName || row.fabricBrandName || summary.brandName || meta.brandName || ""
  );
  const catalogueId = sanitizeEditableText(
    row.catalogueId || row.fabricCatalogueId || summary.catalogueId || meta.catalogueId || ""
  );
  const catalogueName = sanitizeEditableText(
    row.catalogueName ||
      row.fabricCatalogueName ||
      summary.catalogueName ||
      meta.catalogueName ||
      ""
  );
  const catalogueKey = sanitizeEditableText(
    row.catalogueKey || summary.catalogueKey || meta.catalogueKey || ""
  );
  const srNumber = getSpecificFabricSrNumberFromSources(row, summary, meta);
  const fabricCodeRaw = sanitizeEditableText(
    row.fabricCodeRaw ||
      row.fabricCode ||
      row.fabricName ||
      row.fabric ||
      summary.fabricCode ||
      summary.displayName ||
      meta.fabricCode ||
      ""
  );
  const displayFromCatalogue =
    catalogueName && srNumber
      ? buildFabricCatalogueDisplayCode({ catalogueName, srNumber })
      : "";
  const fabricDisplayName = sanitizeEditableText(
    displayFromCatalogue || summary.displayName || summary.fabricCode || fabricCodeRaw
  );
  const brandSegment = sanitizeFabricLibraryKeySegment(brandId || brandName);
  const catalogueSegment = sanitizeFabricLibraryKeySegment(
    catalogueId || catalogueKey || catalogueName
  );
  const srSegment = sanitizeFabricLibraryKeySegment(srNumber);
  const fabricSegment = sanitizeFabricLibraryKeySegment(fabricCodeRaw || fabricDisplayName);
  const fallbackSummaryKey = sanitizeFabricLibraryKeySegment(
    summary.fabricLibraryKey || summary.normalizedKey || summary.key || ""
  );
  const hasCatalogueIdentity = Boolean(catalogueSegment);
  const hasExactCatalogueFabricIdentity = Boolean(brandSegment && catalogueSegment && srSegment);
  const fabricTextLooksLikePricingRule =
    isSrPricingRuleDisplayText(fabricCodeRaw) ||
    isSrPricingRuleDisplayText(fabricDisplayName) ||
    isSrPricingRuleDisplayText(row.srPriceRuleLabel || summary.srPriceRuleLabel || meta.srPriceRuleLabel || "");

  let fabricKey = "";
  if (hasExactCatalogueFabricIdentity) {
    fabricKey = `${brandSegment}__${catalogueSegment}__sr-${srSegment}`;
  } else if (hasCatalogueIdentity) {
    fabricKey = "";
  } else if (brandSegment && fabricSegment && !fabricTextLooksLikePricingRule) {
    fabricKey = `${brandSegment}__${fabricSegment}`;
  } else if (!fabricTextLooksLikePricingRule) {
    fabricKey = fallbackSummaryKey || fabricSegment;
  }

  return {
    fabricKey,
    brandId,
    brandName,
    catalogueId,
    catalogueName,
    catalogueKey,
    srNumber,
    fabricCodeRaw,
    fabricDisplayName,
  };
};

const buildFabricPhotoStoragePath = ({
  workspaceUid = "",
  fabricKey = "",
  imageId = "",
  fileName = "",
} = {}) => {
  const safeWorkspace =
    String(workspaceUid || "")
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .replace(/[\\?#*]+/g, "_")
      .replace(/[[\]]/g, "_") || "workspace";
  const safeFabricKey = sanitizeFabricLibraryKeySegment(fabricKey) || "fabric";
  const safeImageId = sanitizeFabricLibraryKeySegment(imageId) || `photo-${Date.now()}`;
  const safeName = sanitizeFabricLibraryKeySegment(fileName).replace(/-/g, "_") || "fabric_photo";
  return `${FABRIC_PHOTO_STORAGE_ROOT}/${safeWorkspace}/${safeFabricKey}/${safeImageId}-${safeName}.jpg`;
};

const loadPdfImageFromUrl = async (url = "", { maxLongEdge = 520 } = {}) => {
  const sourceUrl = String(url || "").trim();
  if (!sourceUrl || typeof document === "undefined" || typeof window === "undefined") {
    return null;
  }

  try {
    const response = await fetch(sourceUrl, { mode: "cors" });
    if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = new window.Image();
      image.src = objectUrl;
      if (typeof image.decode === "function") {
        await image.decode();
      } else {
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
        });
      }

      const width = Number(image.naturalWidth || image.width || 1);
      const height = Number(image.naturalHeight || image.height || 1);
      const scale = Math.min(1, maxLongEdge / Math.max(width, height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return {
        dataUrl: canvas.toDataURL("image/jpeg", 0.86),
        format: "JPEG",
        sourceUrl,
      };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    if (IS_DEV_UI_ENABLED) {
      devWarn("PDF_FABRIC_PHOTO_LOAD_FAILED", {
        message: error?.message,
        sourceUrl,
      });
    }
    return null;
  }
};

const loadFabricPhotoForPdf = async (photo = null) => {
  const normalized = normalizeFabricPhotoRef(photo);
  const url = normalized?.thumbDownloadUrl || normalized?.downloadUrl || "";
  if (!url) return null;
  return loadPdfImageFromUrl(url);
};

const sanitizeFabricTextAgainstSavedBrandPrefix = (value = "", row = {}) =>
  stripStaleFabricBrandPrefix(getRawEditableTextValue(value), [
    row?.brandName,
    row?.fabricBrandName,
  ]);

const normalizeFabricBrand = (brand = {}, fallbackId = "") => {
  const name = sanitizeEditableText(
    brand.name || brand.brandName || brand.label || ""
  );
  const id = sanitizeEditableText(brand.id || brand.brandId || fallbackId || "");
  return {
    id,
    name,
    nameLower: name.toLowerCase(),
    supplier: sanitizeEditableText(brand.supplier || brand.vendor || ""),
    notes: sanitizeEditableText(brand.notes || ""),
    archived: brand.archived === true,
    updatedAt: brand.updatedAt || null,
    createdAt: brand.createdAt || null,
  };
};

const normalizeFabricBrands = (brands = []) =>
  (Array.isArray(brands) ? brands : [])
    .map((brand, index) => normalizeFabricBrand(brand, brand.id || `brand-${index}`))
    .filter((brand) => brand.id && brand.name && !brand.archived)
    .sort((a, b) => a.name.localeCompare(b.name));

const normalizeFabricSummaryMeta = (meta = {}) => {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return {};
  return Object.fromEntries(
    Object.entries(meta)
      .map(([rawKey, rawValue]) => {
        const normalizedKey = normalizeFabricKey(rawKey);
        if (!normalizedKey) return null;
        const value =
          rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)
            ? rawValue
            : {};
        const normalized = {
          fabricCode: sanitizeEditableText(value.fabricCode || value.code || rawKey),
          brandId: sanitizeEditableText(value.brandId || value.fabricBrandId || ""),
          brandName: sanitizeEditableText(value.brandName || value.fabricBrandName || ""),
          fabricBrandId: sanitizeEditableText(value.fabricBrandId || value.brandId || ""),
          fabricBrandName: sanitizeEditableText(value.fabricBrandName || value.brandName || ""),
          brandSource: sanitizeEditableText(value.brandSource || ""),
          catalogueId: sanitizeEditableText(value.catalogueId || ""),
          catalogueName: sanitizeEditableText(value.catalogueName || ""),
          catalogueKey: sanitizeEditableText(value.catalogueKey || ""),
          srNumber: getExactFabricSrNumber(
            value.srNumber || value.srNo || value.sr || "",
            value.srLabel || value.srPriceRuleLabel || ""
          ),
          fabricLibraryKey: sanitizeEditableText(value.fabricLibraryKey || ""),
          primaryPhoto: normalizeFabricPhotoRef(value.primaryPhoto),
          photoHistory: (Array.isArray(value.photoHistory) ? value.photoHistory : [])
            .map((photo) => normalizeFabricPhotoRef(photo))
            .filter(Boolean)
            .slice(0, FABRIC_PHOTO_HISTORY_LIMIT),
          repeatSettings: normalizeCurtainRepeatEntry(value.repeatSettings || value.repeat || {}),
          notes: sanitizeEditableText(value.notes || ""),
        };
        return [normalizedKey, normalized];
      })
      .filter(Boolean)
  );
};

const normalizeFabricSummaryMetaWithRepeatSettings = (
  meta = {},
  repeatSettingsMap = {}
) => {
  const normalizedMeta = normalizeFabricSummaryMeta(meta);
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettingsMap);
  return Object.fromEntries(
    Array.from(
      new Set([
        ...Object.keys(normalizedMeta),
        ...Object.keys(normalizedRepeatSettings),
      ])
    ).map((fabricKey) => {
      const settingsRepeat = normalizedRepeatSettings[fabricKey];
      const metaRepeat = normalizedMeta[fabricKey]?.repeatSettings;
      const resolvedRepeat = hasMeaningfulCurtainRepeatEntry(settingsRepeat)
        ? settingsRepeat
        : hasMeaningfulCurtainRepeatEntry(metaRepeat)
        ? metaRepeat
        : normalizeCurtainRepeatEntry(settingsRepeat || metaRepeat || {});
      return [
        fabricKey,
        {
          ...(normalizedMeta[fabricKey] || { fabricCode: fabricKey }),
          repeatSettings: resolvedRepeat,
        },
      ];
    })
  );
};

const mergeRepeatSettingsFromFabricSummaryMeta = (
  repeatSettingsMap = {},
  meta = {}
) => {
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettingsMap);
  const normalizedMeta = normalizeFabricSummaryMeta(meta);
  Object.entries(normalizedMeta).forEach(([fabricKey, value]) => {
    const metaRepeat = normalizeCurtainRepeatEntry(value.repeatSettings || {});
    const hasMetaRepeat =
      metaRepeat.verticalEnabled ||
      metaRepeat.horizontalEnabled ||
      Number(metaRepeat.verticalRepeatInch || 0) > 0 ||
      Number(metaRepeat.horizontalRepeatInch || 0) > 0;
    if (
      hasMetaRepeat &&
      !hasMeaningfulCurtainRepeatEntry(normalizedRepeatSettings[fabricKey])
    ) {
      normalizedRepeatSettings[fabricKey] = metaRepeat;
    }
  });
  return normalizedRepeatSettings;
};

const getWorkshopObjectLabel = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";

  const primary =
    normalizeWorkshopText(value.name) ||
    normalizeWorkshopText(value.label) ||
    normalizeWorkshopText(value.title) ||
    normalizeWorkshopText(value.displayName) ||
    normalizeWorkshopText(value.fabricName) ||
    normalizeWorkshopText(value.fabricLabel) ||
    normalizeWorkshopText(value.materialName) ||
    normalizeWorkshopText(value.fabricKey);
  const detail =
    normalizeWorkshopText(value.code) ||
    normalizeWorkshopText(value.sku) ||
    normalizeWorkshopText(value.itemCode) ||
    normalizeWorkshopText(value.shade) ||
    normalizeWorkshopText(value.color);

  if (primary && detail && !primary.toLowerCase().includes(detail.toLowerCase())) {
    return `${primary} (${detail})`;
  }

  return primary || detail;
};

const getWorkshopFieldLabel = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((entry) => getWorkshopFieldLabel(entry)).find(Boolean) || "";
    }
    return getWorkshopObjectLabel(value);
  }
  return normalizeWorkshopText(value);
};

const getWorkshopRowStableId = (row = {}) =>
  normalizeWorkshopText(
    row?.id ||
      row?.rowId ||
      row?.clientRowId ||
      row?.sourceRowId ||
      row?.sourceId ||
      row?.originalRowId
  );

const getWorkshopRowItemLabel = (row = {}) =>
  normalizeWorkshopText(row?.item || row?.name || row?.windowName || row?.label);

const getWorkshopRowProductLabel = (row = {}) =>
  normalizeWorkshopText(row?.productType || row?.product || row?.type);

const normalizeWorkshopProductLabel = (value) =>
  normalizeWorkshopText(value).toLowerCase().replace(/\s+/g, " ");

const getWorkshopRowWidth = (row = {}) =>
  Number(row?.width ?? row?.windowWidth ?? row?.w ?? row?.measurement?.width ?? 0);

const getWorkshopRowHeight = (row = {}) =>
  Number(row?.height ?? row?.drop ?? row?.windowHeight ?? row?.h ?? row?.measurement?.height ?? 0);

const closeWorkshopNumber = (a, b, tolerance = 0.05) => {
  const nA = Number(a || 0);
  const nB = Number(b || 0);
  return Math.abs(nA - nB) <= tolerance;
};

const isSameWorkshopRowIdentity = (row = {}, candidate = {}) => {
  if (!row || !candidate) return false;

  const rowId = getWorkshopRowStableId(row);
  const candidateId = getWorkshopRowStableId(candidate);
  if (rowId && candidateId) return rowId === candidateId;

  const item = getWorkshopRowItemLabel(row).toLowerCase();
  const candidateItem = getWorkshopRowItemLabel(candidate).toLowerCase();
  const product = normalizeWorkshopProductLabel(getWorkshopRowProductLabel(row));
  const candidateProduct = normalizeWorkshopProductLabel(getWorkshopRowProductLabel(candidate));
  const widthMatches = closeWorkshopNumber(getWorkshopRowWidth(row), getWorkshopRowWidth(candidate));
  const heightMatches = closeWorkshopNumber(getWorkshopRowHeight(row), getWorkshopRowHeight(candidate));

  return Boolean(
    item &&
      candidateItem &&
      item === candidateItem &&
      product &&
      candidateProduct &&
      product === candidateProduct &&
      widthMatches &&
      heightMatches
  );
};

const CURTAIN_EDITABLE_FABRIC_FIELDS = [
  "fabric",
  "fabricName",
  "fabricLabel",
  "selectedFabric",
  "fabricDetails",
  "material",
  "materialCode",
  "materialName",
  "colorCode",
  "itemFabric",
  "fabricCode",
  "fabricSku",
  "fabricKey",
];

const sanitizeEditableFabricValue = (value) => {
  if (value == null || typeof value === "string" || typeof value === "number") {
    return sanitizeEditableText(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeEditableFabricValue(entry));
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [
        key,
        sanitizeEditableFabricValue(entryValue),
      ])
    );
  }
  return value;
};

function getFirstNonBlankValue(...values) {
  return (
    values.find(
      (value) => value !== null && value !== undefined && String(value).trim() !== ""
    ) ?? ""
  );
}

function parseMoneyInput(value, fallback = null) {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(numeric) || numeric < 0) return fallback;
  return numeric;
}

const curtainRowSupportsLining = (row = {}, calc = {}) => {
  const productConfig = row?.productConfigSnapshot || null;
  const productName =
    row?.productName ||
    row?.productType ||
    row?.product ||
    calc?.productName ||
    "";
  return productSupportsLining(productConfig, {
    ...row,
    productName,
    productType: productName,
    category:
      row?.category ||
      (calc?.isCurtain ? "Curtain" : calc?.isRoman ? "Roman Blind" : ""),
    calculationType: calc?.calculationType || row?.calculationType,
    usesFabric: Boolean(
      row?.usesFabric === true || calc?.isCurtain === true || calc?.isRoman === true
    ),
    supportsLining: row?.supportsLining,
    isCurtain: calc?.isCurtain === true,
    isRoman: calc?.isRoman === true,
  });
};

const sanitizeCurtainRowEditableTextFields = (row = {}) => {
  const nextRow = { ...(row || {}) };
  CURTAIN_EDITABLE_FABRIC_FIELDS.forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(nextRow, fieldName)) {
      nextRow[fieldName] = sanitizeFabricTextAgainstSavedBrandPrefix(
        sanitizeEditableFabricValue(nextRow[fieldName]),
        nextRow
      );
    }
  });
  ["catalogueName", "fabricCatalogueName", "collectionName"].forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(nextRow, fieldName)) {
      nextRow[fieldName] = sanitizeFabricTextAgainstSavedBrandPrefix(
        nextRow[fieldName],
        nextRow
      );
    }
  });
  if (Object.prototype.hasOwnProperty.call(nextRow, "fabricWidthIn")) {
    nextRow.fabricWidthIn = normalizeFabricWidthIn(nextRow.fabricWidthIn) || "";
  }
  if (Object.prototype.hasOwnProperty.call(nextRow, "catalogueDefaultFabricWidthIn")) {
    nextRow.catalogueDefaultFabricWidthIn =
      normalizeFabricWidthIn(nextRow.catalogueDefaultFabricWidthIn) || null;
  }
  if (Object.prototype.hasOwnProperty.call(nextRow, "srRuleFabricWidthIn")) {
    nextRow.srRuleFabricWidthIn =
      normalizeFabricWidthIn(nextRow.srRuleFabricWidthIn) || null;
  }
  const validFabricWidthSources = new Set([
    "catalogue_rule",
    "catalogue_default",
    "workspace_default",
    "manual_override",
    "",
  ]);
  if (!validFabricWidthSources.has(nextRow.fabricWidthSource || "")) {
    nextRow.fabricWidthSource = "";
  }
  nextRow.fabricWidthManuallyOverridden =
    nextRow.fabricWidthSource === "manual_override" &&
    Boolean(normalizeFabricWidthIn(nextRow.fabricWidthIn));
  const hasFabricText = Boolean(
    sanitizeEditableText(
      nextRow.fabricCodeRaw ||
        nextRow.fabricCode ||
        nextRow.fabric ||
        nextRow.fabricName ||
        ""
    )
  );
  if (hasFabricText && nextRow.fabricSource === "custom_unverified") {
    nextRow.fabricSource = "quote_manual";
  }
  const manualRrpInput = getFirstNonBlankValue(
    nextRow.mrp,
    nextRow.mrpPerMeter,
    nextRow.rrpPerMeter,
    nextRow.ratePerMeter
  );
  const parsedManualRrp = parseMoneyInput(manualRrpInput, null);
  if (
    hasFabricText &&
    (nextRow.fabricSource === "quote_manual" ||
      nextRow.rrpSource === "manual_override" ||
      nextRow.rrpSource === "manual_quote")
  ) {
    nextRow.rrpPerMeter = parsedManualRrp;
    nextRow.rrpSource = parsedManualRrp !== null ? "manual_quote" : nextRow.rrpSource || "quote_manual";
  } else if (Object.prototype.hasOwnProperty.call(nextRow, "rrpPerMeter")) {
    nextRow.rrpPerMeter = parseMoneyInput(nextRow.rrpPerMeter, null);
  }
  if (nextRow.fabricSource === "quote_manual") {
    nextRow.selectedFabricRef = null;
    nextRow.catalogueId = "";
    nextRow.catalogueKey = "";
    nextRow.srPriceRuleId = "";
    nextRow.srPriceRuleType = "none";
    nextRow.srPriceRuleLabel = "";
  }
  nextRow.lining = normalizeLiningForSave(nextRow, nextRow.productConfigSnapshot || null);
  return nextRow;
};

const CURTAIN_RATE_AMOUNT_FIELDS = [
  "sqftRate",
  "ratePerSqft",
  "rate",
  "rateAmount",
  "manualAmount",
  "ratePerFeet",
  "runningFeetRate",
  "length",
  "ratePerLength",
  "unitRate",
  "ratePerPiece",
  "pieceRate",
  "amount",
  "lineAmount",
];

const normalizeRowProductConfigForPricing = (row = {}, productConfig = null) => {
  if (productConfig) return normalizeProductConfig(productConfig);
  if (row?.productConfigSnapshot) return normalizeProductConfig(row.productConfigSnapshot);
  if (row?.calculationType || row?.requiredFields || row?.pricingFields || row?.displayFields) {
    return normalizeProductConfig({
      id: row.productId || row.productName || row.productType || row.product || "row-product",
      name: row.productName || row.productType || row.product || "Row Product",
      calculationType: row.calculationType || "sqft_only",
      requiredFields: row.requiredFields || {},
      pricingFields: row.pricingFields || {},
      displayFields: row.displayFields || {},
      sqftRules: row.sqftRules || {},
      runningFeetRules: row.runningFeetRules || {},
      quantityRules: row.quantityRules || {},
      fabricRules: row.fabricRules || {},
      stitchingRules: row.stitchingRules || {},
      trackRules: row.trackRules || {},
    });
  }
  return null;
};

const shouldProductUseRateAmountField = (row = {}, productConfig = null) => {
  const config = normalizeRowProductConfigForPricing(row, productConfig);
  if (config) {
    const calculationType = config.calculationType;
    if (calculationType === "fabric_meter" && config.pricingFields?.rateAffectsPrice !== true) {
      return false;
    }
    if (
      calculationType === "sqft_only" ||
      calculationType === "sqft_plus_fabric" ||
      calculationType === "running_length_rate" ||
      calculationType === "quantity_rate" ||
      calculationType === "manual_amount"
    ) {
      return true;
    }
    if (
      config.sqftRules?.enabled ||
      config.runningFeetRules?.enabled ||
      config.quantityRules?.enabled ||
      config.requiredFields?.ratePerSqft ||
      config.requiredFields?.ratePerFeet ||
      config.requiredFields?.ratePerLength ||
      config.requiredFields?.unitRate ||
      config.requiredFields?.manualAmount ||
      config.requiredFields?.quantity
    ) {
      return true;
    }
    return config.displayFields?.showRate === true && config.pricingFields?.rateAffectsPrice === true;
  }

  const calculationType = String(row?.calculationType || "").trim();
  if (calculationType === "fabric_meter") return false;
  if (
    ["sqft_only", "sqft_plus_fabric", "running_length_rate", "quantity_rate", "manual_amount"].includes(
      calculationType
    )
  ) {
    return true;
  }

  const productName = normalizeProductName(row?.productType || row?.productName || row?.product || "");
  if (productName.includes("curtain")) return false;
  if (productName.includes("blind") || productName.includes("roman")) return true;
  return true;
};

const sanitizeRowPricingFieldsByProduct = (row = {}, productConfig = null) => {
  const nextRow = { ...(row || {}) };
  if (shouldProductUseRateAmountField(nextRow, productConfig)) return nextRow;

  CURTAIN_RATE_AMOUNT_FIELDS.forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(nextRow, fieldName)) {
      nextRow[fieldName] = "";
    }
  });

  return nextRow;
};

const sanitizeCurtainRowsEditableTextFields = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : []).map((row) =>
    sanitizeRowPricingFieldsByProduct(sanitizeCurtainRowEditableTextFields(row))
  );

const CURTAIN_LINING_TYPES = [
  { type: "satin", label: "Satin" },
  { type: "dimout", label: "Dimout" },
  { type: "blackout", label: "Blackout" },
  { type: "custom", label: "Custom" },
];

const DEFAULT_CURTAIN_LINING = {
  enabled: false,
  type: "satin",
  label: "Satin",
  customLabel: "",
  chargeMethod: "meter",
  ratePerMeter: "",
  ratePerSqft: "",
  fabricCode: "",
  brandId: "",
  brandName: "",
  catalogueId: "",
  catalogueName: "",
  srNumber: "",
  metersMode: "same_as_curtain",
  calculatedMeters: 0,
  meters: 0,
  manualMeters: null,
  overrideMeters: "",
  billableMeters: 0,
  sqft: 0,
  includedInSqftRate: false,
  notes: "",
  amount: 0,
};

const getCurtainLiningTypeMeta = (type = "") =>
  CURTAIN_LINING_TYPES.find((entry) => entry.type === type) ||
  CURTAIN_LINING_TYPES[0];

const parseNonNegativeNumberInput = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, numeric);
};

const getCurtainLiningLabel = (lining = {}) => {
  const type = String(lining?.type || "satin").trim().toLowerCase();
  if (type === "custom") {
    return sanitizeEditableText(lining?.customLabel || lining?.label) || "Custom lining";
  }
  return getCurtainLiningTypeMeta(type).label;
};



const toFiniteLiningNumber = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : fallback;
};

const roundLiningNumber = (value) => {
  const numeric = toFiniteLiningNumber(value, 0);
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

const normalizeLiningChargeMethodForSave = (value = "") => {
  const method = sanitizeEditableText(value).toLowerCase();
  if (method === "meter" || method === "sqft" || method === "manual") {
    return method;
  }
  return "";
};

const getCurtainLiningSource = (row = {}) =>
  row?.lining && typeof row.lining === "object" && !Array.isArray(row.lining)
    ? row.lining
    : {};

function normalizeLiningForSave(row = {}, productConfig = null, calc = null) {
  const source = getCurtainLiningSource(row);
  const hasExternalCalc = Boolean(calc && typeof calc === "object");
  const supportsLining = productConfig
    ? productSupportsLining(productConfig, row)
    : curtainRowSupportsLining(row, calc || {});
  const calculatedLining =
    calc?.lining && typeof calc.lining === "object" && !Array.isArray(calc.lining)
      ? calc.lining
      : calculateCurtainRowLining(
          row,
          toFiniteLiningNumber(
            calc?.customerBillableFabricMeters ??
              calc?.orderedFabricMeters ??
              calc?.fabricMeters ??
              row.customerBillableFabricMeters ??
              row.orderedFabricMeters ??
              row.fabricMeters,
            0
          ),
          supportsLining,
          {
            sqft:
              calc?.adjSqft ??
              calc?.billableSqft ??
              row.billableSqft ??
              row.adjSqft ??
              0,
          }
        );
  const merged = hasExternalCalc
    ? {
        ...source,
        ...calculatedLining,
      }
    : {
        ...calculatedLining,
        ...source,
      };
  const rawChargeMethod =
    normalizeLiningChargeMethodForSave(source.chargeMethod) ||
    normalizeLiningChargeMethodForSave(calculatedLining.chargeMethod) ||
    "meter";
  const chargeMethod = rawChargeMethod || "meter";
  const typeCandidate = sanitizeEditableText(source.type || calculatedLining.type || "satin")
    .toLowerCase();
  const type = CURTAIN_LINING_TYPES.some((entry) => entry.type === typeCandidate)
    ? typeCandidate
    : "satin";
  const hasManualMeterValue =
    source.manualMeters !== null &&
    source.manualMeters !== undefined &&
    String(source.manualMeters).trim() !== "";
  const hasOverrideMeterValue =
    source.overrideMeters !== null &&
    source.overrideMeters !== undefined &&
    String(source.overrideMeters).trim() !== "";
  const hasCustomMeterValue =
    source.customMeters !== null &&
    source.customMeters !== undefined &&
    String(source.customMeters).trim() !== "";
  const hasLegacyManualOverrideMeters =
    source.metersMode !== "same_as_curtain" &&
    (
      (hasManualMeterValue && toFiniteLiningNumber(source.manualMeters, 0) > 0) ||
      (hasOverrideMeterValue && toFiniteLiningNumber(source.overrideMeters, 0) > 0) ||
      (hasCustomMeterValue && toFiniteLiningNumber(source.customMeters, 0) > 0)
    );
  const metersMode =
    source.metersMode === "custom_override" ||
    (
      calculatedLining.metersMode === "custom_override" &&
      source.metersMode !== "same_as_curtain" &&
      hasLegacyManualOverrideMeters
    ) ||
    hasLegacyManualOverrideMeters
      ? "custom_override"
      : "same_as_curtain";
  const enabled = Boolean(supportsLining && (merged.enabled === true || row.liningEnabled === true));
  const billableMeters = enabled
    ? roundLiningNumber(
        merged.billableMeters ??
          merged.meters ??
          row.liningMeters ??
          row.liningMeter ??
          0
      )
    : 0;
  const manualMeters =
    enabled && metersMode === "custom_override"
      ? roundLiningNumber(
          source.manualMeters ??
            source.overrideMeters ??
            calculatedLining.manualMeters ??
            calculatedLining.overrideMeters ??
            billableMeters
        )
      : 0;
  const sqft = enabled
    ? roundLiningNumber(
        merged.sqft ?? row.liningSqft ?? calc?.adjSqft ?? calc?.billableSqft ?? 0
      )
    : 0;
  const amount = enabled
    ? roundLiningNumber(
        calc?.liningAmount ??
          source.amount ??
          row.liningAmount ??
          calculatedLining.amount ??
          0
      )
    : 0;
  const fabricCode = sanitizeEditableText(
    source.fabricCode ||
      source.fabric ||
      source.fabricName ||
      row.liningFabricCode ||
      row.liningFabric ||
      row.liningFabricName ||
      ""
  );
  const customLabel = sanitizeEditableText(source.customLabel || "");
  const normalized = {
    enabled,
    type,
    label: getCurtainLiningLabel({ type, customLabel }),
    customLabel,
    fabricCode,
    brandId: sanitizeEditableText(source.brandId || row.liningBrandId || ""),
    brandName: sanitizeEditableText(source.brandName || row.liningBrandName || ""),
    catalogueId: sanitizeEditableText(
      source.catalogueId || source.fabricCatalogueId || row.liningCatalogueId || ""
    ),
    catalogueName: sanitizeEditableText(
      source.catalogueName || source.fabricCatalogueName || row.liningCatalogueName || ""
    ),
    srNumber: sanitizeEditableText(
      source.srNumber || source.srNo || source.sr || row.liningSrNumber || ""
    ),
    chargeMethod,
    ratePerMeter: toFiniteLiningNumber(
      source.ratePerMeter ?? calculatedLining.ratePerMeter ?? source.rate ?? row.liningRate,
      0
    ),
    meters: billableMeters,
    manualMeters,
    ratePerSqft: toFiniteLiningNumber(
      source.ratePerSqft ??
        calculatedLining.ratePerSqft ??
        source.sqftRate ??
        row.liningRatePerSqft,
      0
    ),
    sqft,
    amount,
    includedInSqftRate: source.includedInSqftRate === true || row.liningIncludedInSqftRate === true,
    notes: sanitizeEditableText(source.notes || row.liningNotes || ""),
    metersMode,
    calculatedMeters: roundLiningNumber(
      calculatedLining.calculatedMeters ?? merged.calculatedMeters ?? billableMeters
    ),
    billableMeters,
    overrideMeters: manualMeters,
  };

  return normalized;
}

const isRomanBlindSaveRow = (row = {}) =>
  normalizeWorkshopProductLabel(getWorkshopRowProductLabel(row)).includes("roman");

const getCurtainSaveLiningDiagnostics = (payload = {}) => {
  const rows = Array.isArray(payload?.rows)
    ? payload.rows
    : Array.isArray(payload?.curtainRows)
    ? payload.curtainRows
    : Array.isArray(payload?.items)
    ? payload.items
    : [];
  const rowsWithLining = rows.filter((row) => row?.lining?.enabled === true);
  const sampleRow = rowsWithLining[0] || rows.find(isRomanBlindSaveRow) || rows[0] || {};
  const sampleLining =
    sampleRow?.lining && typeof sampleRow.lining === "object" ? sampleRow.lining : null;
  const payloadDiagnostics = inspectGeneralQuotePayload(payload, "payload");

  return {
    hasRomanBlindRows: rows.some(isRomanBlindSaveRow),
    rowsWithLiningCount: rowsWithLining.length,
    rowKeys: Object.keys(sampleRow || {}),
    liningKeys: sampleLining ? Object.keys(sampleLining) : [],
    firstLiningPayloadExample: sampleLining,
    invalidPayloadPaths: [
      ...payloadDiagnostics.undefinedPaths.map((path) => `undefined:${path}`),
      ...payloadDiagnostics.fileBlobPaths.map((path) => `file_or_blob:${path}`),
      ...payloadDiagnostics.invalidNumberPaths.map((path) => `invalid_number:${path}`),
      ...payloadDiagnostics.functionPaths.map((path) => `function:${path}`),
      ...payloadDiagnostics.circularPaths.map((path) => `circular:${path}`),
    ],
  };
};

const logCurtainSaveWithLiningTrace = (details = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  const payloadDiagnostics = getCurtainSaveLiningDiagnostics(details.payload || {});
  devLog("CURTAIN_SAVE_WITH_LINING_TRACE", {
    action: details.action || "save_existing",
    quoteNo: details.quoteNo || "",
    quoteId: details.quoteId || "",
    quoteIndexId: details.quoteIndexId || "",
    sourcePath: details.sourcePath || "",
    workspaceUid: details.workspaceUid || "",
    userUid: details.userUid || "",
    editorMode: details.editorMode || "",
    hasRomanBlindRows: payloadDiagnostics.hasRomanBlindRows,
    rowsWithLiningCount: payloadDiagnostics.rowsWithLiningCount,
    failedStepCandidate: details.failedStepCandidate || "",
    writePath: details.writePath || "",
    operation: details.operation || "",
    payloadKeys: Object.keys(details.payload || {}),
    rowKeys: payloadDiagnostics.rowKeys,
    liningKeys: payloadDiagnostics.liningKeys,
    invalidPayloadPaths: payloadDiagnostics.invalidPayloadPaths,
  });
};

const logCurtainSaveWithLiningFailure = (details = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  const payloadDiagnostics = getCurtainSaveLiningDiagnostics(details.payload || {});
  devError("CURTAIN_SAVE_WITH_LINING_FAILED", {
    failedStep: details.failedStep || "",
    writePath: details.writePath || "",
    errorCode: details.error?.code || "",
    errorMessage: details.error?.message || String(details.error || ""),
    quoteNo: details.quoteNo || "",
    quoteId: details.quoteId || "",
    quoteIndexId: details.quoteIndexId || "",
    sourcePath: details.sourcePath || "",
    workspaceUid: details.workspaceUid || "",
    userUid: details.userUid || "",
    payloadKeys: Object.keys(details.payload || {}),
    firstLiningPayloadExample: payloadDiagnostics.firstLiningPayloadExample,
    invalidPayloadPaths: payloadDiagnostics.invalidPayloadPaths,
  });
};

const getEditableFabricValue = (row = {}, originalRow = {}) => {
  const hasValidOriginalRow =
    originalRow &&
    Object.keys(originalRow).length > 0 &&
    isSameWorkshopRowIdentity(row, originalRow);
  const sources = [
    row,
    hasValidOriginalRow ? originalRow : null,
  ].filter(Boolean);

  for (const source of sources) {
    const catalogueName = sanitizeFabricTextAgainstSavedBrandPrefix(
      source?.catalogueName || "",
      source
    );
    const srNumber = getSpecificRowSrNumber(source);
    if (catalogueName && srNumber) {
      return buildFabricCatalogueDisplayCode({ catalogueName, srNumber });
    }
    if (
      catalogueName &&
      (isSrPricingRuleDisplayText(source?.fabricCodeRaw || "") ||
        isSrPricingRuleDisplayText(source?.fabric || "") ||
        isSrPricingRuleDisplayText(source?.fabricCode || ""))
    ) {
      return catalogueName;
    }

    for (const fieldName of CURTAIN_EDITABLE_FABRIC_FIELDS) {
      const value = sanitizeFabricTextAgainstSavedBrandPrefix(
        source?.[fieldName],
        source
      );
      if (value !== "") return value;
    }
  }

  return "";
};

const getDisplayFabricLabel = (row = {}, originalRow = {}) =>
  normalizeWorkshopText(getEditableFabricValue(row, originalRow)) || WORKSHOP_EMPTY_VALUE;

const getCurtainRowFabricLabel = (row = {}, originalRow = {}) =>
  getDisplayFabricLabel(row, originalRow);

const getCurtainRowFabricKey = (row = {}, originalRow = {}) => {
  // Retain historical repeat/group keys; presentation must not rename stored pools.
  const sr = getSpecificRowSrNumber(row);
  if (row.catalogueName && sr) {
    const legacyLabel = /^SR[ .:#/-]/i.test(sr) ? sr : `SR-${sr}`;
    return fabricGroupingKey(`${row.catalogueName} ${legacyLabel}`);
  }
  const label = getEditableFabricValue(row, originalRow);
  return label ? fabricGroupingKey(label) : "";
};

const hslToRgbArray = (hue = 0, saturation = 0, lightness = 0) => {
  const h = (((Number(hue) || 0) % 360) + 360) % 360;
  const s = Math.max(0, Math.min(100, Number(saturation) || 0)) / 100;
  const l = Math.max(0, Math.min(100, Number(lightness) || 0)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return [r, g, b].map((channel) =>
    Math.round((channel + m) * 255)
  );
};

const getWorkshopPdfFabricTintByIndex = (index = 0) =>
  hslToRgbArray((Number(index) || 0) * 137.508, 68, 92);

const getWorkshopFabricColorKey = (
  row = {},
  fallbackLabel = "",
  { includeCatalogueIdentity = false } = {}
) => {
  const srNumber =
    getSpecificRowSrNumber(row) ||
    sanitizeEditableText(row?.srNumber || row?.srNo || row?.sr || "");
  const parts = [
    includeCatalogueIdentity
      ? sanitizeEditableText(
          row?.brandId ||
            row?.fabricBrandId ||
            row?.brandName ||
            row?.fabricBrandName ||
            ""
        )
      : "",
    includeCatalogueIdentity
      ? sanitizeEditableText(
          row?.catalogueId ||
            row?.catalogueKey ||
            row?.catalogueName ||
            row?.collectionName ||
            ""
        )
      : "",
    srNumber ? `sr ${srNumber}` : "",
    sanitizeEditableText(
      row?.fabricCodeRaw ||
        row?.fabricCode ||
        row?.materialCode ||
        row?.displayName ||
        row?.fabricLabel ||
        getEditableFabricValue(row) ||
        fallbackLabel
    ),
  ].filter(Boolean);

  return (
    normalizeFabricKey(parts.join(" ")) ||
    getCurtainRowFabricKey(row) ||
    normalizeFabricKey(fallbackLabel)
  );
};

const findCurtainRowStitchConfig = (row = {}, stitchingStyles = {}) => {
  const styles = stitchingStyles && typeof stitchingStyles === "object" ? stitchingStyles : {};
  const entries = Object.entries(styles);
  if (!entries.length) return null;

  const candidates = [
    row?.stitchingStyleId,
    row?.stitchingId,
    row?.stitchId,
    typeof row?.stitchingStyle === "string" ? row.stitchingStyle : "",
    row?.stitchingStyle?.id,
    row?.selectedStitch?.id,
    row?.selectedStitching?.id,
    row?.stitchingStyle && typeof row.stitchingStyle !== "string"
      ? row.stitchingStyle
      : "",
    row?.stitch,
    row?.stitchType,
    row?.stitching,
    row?.stitchingType,
  ]
    .map((value) => getWorkshopFieldLabel(value))
    .filter(Boolean);

  for (const candidate of candidates) {
    if (styles[candidate]) {
      return { key: candidate, config: styles[candidate] };
    }

    const normalizedCandidate = normalizeWorkshopText(candidate).toLowerCase();
    const matched = entries.find(([key, config]) => {
      const possibleLabels = [
        key,
        config?.id,
        config?.name,
        config?.label,
        config?.title,
        config?.displayName,
      ]
        .map((value) => normalizeWorkshopText(value).toLowerCase())
        .filter(Boolean);
      return possibleLabels.includes(normalizedCandidate);
    });
    if (matched) {
      return { key: matched[0], config: matched[1] };
    }
  }

  return null;
};

const getCurtainRowStitchLabel = (row = {}, stitchingStyles = {}, productConfig = null) => {
  const selectedStyleProbe = {
    stitchingStyleId: row?.stitchingStyleId,
    stitchingId: row?.stitchingId,
    stitchId: row?.stitchId,
    stitchingStyle: row?.stitchingStyle,
  };
  const hasSelectedStyle = [
    selectedStyleProbe.stitchingStyleId,
    selectedStyleProbe.stitchingId,
    selectedStyleProbe.stitchId,
    selectedStyleProbe.stitchingStyle,
  ].some((value) => isWorkshopResolvedLabel(getWorkshopFieldLabel(value)));

  if (hasSelectedStyle) {
    const selectedStyleConfig = findCurtainRowStitchConfig(
      selectedStyleProbe,
      stitchingStyles
    );
    if (selectedStyleConfig) {
      const selectedStyleLabel =
        getWorkshopFieldLabel(selectedStyleConfig.config) ||
        getWorkshopFieldLabel(selectedStyleConfig.key);
      if (isWorkshopResolvedLabel(selectedStyleLabel)) return selectedStyleLabel;
    }

    const selectedStyleText = getWorkshopFieldLabel(row?.stitchingStyle);
    if (isWorkshopResolvedLabel(selectedStyleText)) return selectedStyleText;
  }

  const directLabel = [
    row?.stitchLabel,
    row?.stitchingLabel,
    row?.stitch,
    row?.stitchType,
    row?.stitching,
    row?.stitchingType,
    row?.selectedStitch,
    row?.selectedStitching,
    row?.stitchingStyle && typeof row.stitchingStyle === "object"
      ? row.stitchingStyle
      : "",
  ]
    .map((value) => getWorkshopFieldLabel(value))
    .find((label) => isWorkshopResolvedLabel(label)) || "";

  if (directLabel) return directLabel;

  const styleConfig = findCurtainRowStitchConfig(row, stitchingStyles);
  if (styleConfig) {
    const styleLabel =
      getWorkshopFieldLabel(styleConfig.config) ||
      getWorkshopFieldLabel(styleConfig.key);
    return isWorkshopResolvedLabel(styleLabel) ? styleLabel : WORKSHOP_EMPTY_VALUE;
  }

  const styleText = getWorkshopFieldLabel(row?.stitchingStyle);
  if (isWorkshopResolvedLabel(styleText)) return styleText;

  const defaultStyleId = productConfig?.stitchingRules?.defaultStitchingStyleId || "";
  if (defaultStyleId) {
    const defaultConfig = findCurtainRowStitchConfig(
      { stitchingStyleId: defaultStyleId },
      stitchingStyles
    );
    if (defaultConfig) {
      const defaultLabel =
        getWorkshopFieldLabel(defaultConfig.config) || getWorkshopFieldLabel(defaultConfig.key);
      return isWorkshopResolvedLabel(defaultLabel) ? defaultLabel : WORKSHOP_EMPTY_VALUE;
    }
  }

  return WORKSHOP_EMPTY_VALUE;
};

const getCurtainRowWorkshopStitchLabel = (
  row = {},
  stitchingStyles = {},
  productConfig = null
) => {
  const stitchConfigMatch = findCurtainRowStitchConfig(row, stitchingStyles);
  const resolvedStitch = getCurtainRowStitchLabel(row, stitchingStyles, productConfig);
  const resolvedTrack =
    getCurtainRowDisplay(row?.trackType || row?.trackName || row?.track, "") ||
    getCurtainRowDisplay(stitchConfigMatch?.config?.trackType, "");
  const hasStitch = isWorkshopResolvedLabel(resolvedStitch);
  const hasTrack = Boolean(resolvedTrack);
  const stitchIncludesTrack =
    hasStitch &&
    hasTrack &&
    normalizeWorkshopText(resolvedStitch)
      .toLowerCase()
      .includes(normalizeWorkshopText(resolvedTrack).toLowerCase());

  if (hasStitch && hasTrack && !stitchIncludesTrack) {
    return `${resolvedStitch} - ${resolvedTrack}`;
  }
  if (hasStitch) return resolvedStitch;
  return resolvedTrack || WORKSHOP_EMPTY_VALUE;
};

const buildWorkshopStitchTraceRow = (
  row = {},
  stitchingStyles = {},
  productConfig = null,
  extra = {}
) => ({
  ...extra,
  id: row?.id || row?.rowId || row?.sourceRowId || "",
  item: row?.item || row?.name || "",
  product: row?.productType || row?.productName || row?.product || "",
  stitch: row?.stitch,
  stitchType: row?.stitchType,
  stitching: row?.stitching,
  stitchingType: row?.stitchingType,
  stitchingStyle: row?.stitchingStyle,
  stitchingStyleId: row?.stitchingStyleId,
  selectedStitch: row?.selectedStitch,
  selectedStitching: row?.selectedStitching,
  stitchLabel: row?.stitchLabel,
  stitchingLabel: row?.stitchingLabel,
  track: row?.track,
  trackType: row?.trackType,
  trackName: row?.trackName,
  resolved: getCurtainRowWorkshopStitchLabel(row, stitchingStyles, productConfig),
});

const getCurtainRowDisplay = (value, fallback = WORKSHOP_EMPTY_VALUE) =>
  normalizeWorkshopText(value) || fallback;

const WORKSHOP_PRICING_KEY_TERMS = [
  "mrp",
  "rate",
  "price",
  "pricing",
  "amount",
  "total",
  "subtotal",
  "discount",
  "gst",
  "tax",
  "taxable",
  "value",
  "grandtotal",
  "finaltotal",
  "dealvalue",
  "quotevalue",
  "expectedvalue",
  "finalvalue",
  "cost",
  "billing",
  "payment",
  "advance",
  "balance",
  "margin",
];

const WORKSHOP_PRICING_TEXT_PATTERN =
  /(rs\.|₹|\brate\b|\bmrp\b|\bamount\b|\btotal\b|\bsubtotal\b|\bdiscount\b|\bgst\b|\btaxable\b|\bgrand\s*total\b|\bfinal\s*total\b|\bquotation\s*value\b|\bdeal\s*value\b)/i;

const isWorkshopPricingKey = (key = "") => {
  const normalized = String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  return WORKSHOP_PRICING_KEY_TERMS.some((term) => normalized.includes(term));
};

const sanitizeWorkshopRow = (value, seen = new WeakSet()) => {
  if (value == null || typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeWorkshopRow(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  const output = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (isWorkshopPricingKey(key)) return;
    const sanitized = sanitizeWorkshopRow(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
  });
  return output;
};

const assertNoPricingInWorkshopPdfPayload = (payload, context = {}) => {
  if (!isDevRuntime) return [];

  const leaks = [];
  const visit = (value, path = "", rowContext = {}) => {
    if (value == null) return;
    if (typeof value === "string" || typeof value === "number") {
      if (typeof value === "string" && WORKSHOP_PRICING_TEXT_PATTERN.test(value)) {
        leaks.push({ path, value, ...rowContext });
      }
      return;
    }
    if (typeof value !== "object") return;

    if (Array.isArray(value)) {
      value.forEach((entry, index) =>
        visit(entry, `${path}[${index}]`, rowContext)
      );
      return;
    }

    const nextRowContext = {
      product: value.product || value.productType || rowContext.product || context.product || "",
      rowItem: value.item || value.name || rowContext.rowItem || context.rowItem || "",
    };

    Object.entries(value).forEach(([key, entryValue]) => {
      const keyPath = path ? `${path}.${key}` : key;
      if (isWorkshopPricingKey(key)) {
        leaks.push({ path: keyPath, value: entryValue, ...nextRowContext });
      }
      visit(entryValue, keyPath, nextRowContext);
    });
  };

  visit(payload, context.label || "workshopPayload", context);

  if (leaks.length) {
    devError("WORKSHOP_PDF_PRICING_LEAK_DETECTED", {
      ...context,
      leaks,
    });
  }

  return leaks;
};

const assertNoPricingInWorkshopPdfText = (values, context = {}) => {
  if (!isDevRuntime) return [];
  const flattened = [];
  const visit = (value, path = "") => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((entry, index) => visit(entry, `${path}[${index}]`));
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([key, entryValue]) =>
        visit(entryValue, path ? `${path}.${key}` : key)
      );
      return;
    }
    const text = String(value);
    if (WORKSHOP_PRICING_TEXT_PATTERN.test(text)) {
      flattened.push({ path, value: text });
    }
  };
  visit(values, context.label || "workshopText");
  if (flattened.length) {
    devError("WORKSHOP_PDF_TEXT_PRICING_LEAK", {
      ...context,
      leaks: flattened,
    });
  }
  return flattened;
};

const findOriginalWorkshopRow = (row = {}, rowIndex = -1, sourceRows = []) => {
  const candidates = Array.isArray(sourceRows) ? sourceRows : [];
  const idCandidates = [
    row?.sourceRowId,
    row?.rowId,
    row?.id,
    row?.sourceId,
    row?.originalRowId,
  ]
    .map((value) => normalizeWorkshopText(value))
    .filter(Boolean);

  if (idCandidates.length) {
    const byId = candidates.find((candidate) =>
      [
        candidate?.sourceRowId,
        candidate?.rowId,
        candidate?.id,
        candidate?.sourceId,
        candidate?.originalRowId,
      ]
        .map((value) => normalizeWorkshopText(value))
        .some((value) => value && idCandidates.includes(value))
    );
    if (byId) return byId;
  }

  const sourceIndex = Number(row?.sourceIndex ?? row?.originalIndex ?? row?.rowIndex);
  if (
    Number.isInteger(sourceIndex) &&
    candidates[sourceIndex] &&
    isSameWorkshopRowIdentity(row, candidates[sourceIndex])
  ) {
    if (IS_DEV_UI_ENABLED && !idCandidates.length) {
      devWarn("Workshop export used guarded sourceIndex fallback", {
        sourceIndex,
        row,
        candidate: candidates[sourceIndex],
      });
    }
    return candidates[sourceIndex];
  }

  if (
    Number.isInteger(rowIndex) &&
    candidates[rowIndex] &&
    isSameWorkshopRowIdentity(row, candidates[rowIndex])
  ) {
    if (IS_DEV_UI_ENABLED && !idCandidates.length) {
      devWarn("Workshop export used guarded rowIndex fallback", {
        rowIndex,
        row,
        candidate: candidates[rowIndex],
      });
    }
    return candidates[rowIndex];
  }

  const byExactIdentity = candidates.find((candidate) =>
    isSameWorkshopRowIdentity(row, candidate)
  );
  if (byExactIdentity) return byExactIdentity;

  return {};
};

const createWorkshopSourceRow = (
  row = {},
  rowIndex = 0,
  sourceRows = [],
  options = {}
) => {
  const originalRow = findOriginalWorkshopRow(row, rowIndex, sourceRows);
  const resolvedFabricLabel = getCurtainRowFabricLabel(row, originalRow);
  const stitchingStyles = options.stitchingStyles || {};
  const productConfig = options.productConfig || null;
  const mergedSourceRow = { ...originalRow, ...row };
  const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
    mergedSourceRow,
    stitchingStyles,
    productConfig
  );
  const resolvedStitchFields = isWorkshopResolvedLabel(resolvedStitchLabel)
    ? {
        stitch: resolvedStitchLabel,
        stitchLabel: resolvedStitchLabel,
        stitchingLabel: resolvedStitchLabel,
        resolvedStitchLabel,
      }
    : {};
  const rowId =
    normalizeWorkshopText(row?.id) ||
    normalizeWorkshopText(row?.rowId) ||
    normalizeWorkshopText(row?.clientRowId) ||
    normalizeWorkshopText(row?.sourceRowId) ||
    normalizeWorkshopText(originalRow?.id) ||
    normalizeWorkshopText(originalRow?.rowId) ||
    normalizeWorkshopText(originalRow?.clientRowId) ||
    `workshop-row-${rowIndex + 1}`;

  return {
    ...originalRow,
    ...row,
    sourceIndex: Number.isInteger(Number(row?.sourceIndex ?? row?.originalIndex))
      ? Number(row?.sourceIndex ?? row?.originalIndex)
      : rowIndex,
    id: normalizeWorkshopText(row?.id) || rowId,
    rowId: normalizeWorkshopText(row?.rowId) || rowId,
    clientRowId: normalizeWorkshopText(row?.clientRowId) || rowId,
    sourceRowId: rowId,
    originalRowSnapshot: originalRow && Object.keys(originalRow).length ? originalRow : null,
    fabric: getWorkshopFieldLabel(row?.fabric) || getWorkshopFieldLabel(originalRow?.fabric) || resolvedFabricLabel,
    fabricName:
      getWorkshopFieldLabel(row?.fabricName) ||
      getWorkshopFieldLabel(originalRow?.fabricName) ||
      resolvedFabricLabel,
    fabricLabel:
      getWorkshopFieldLabel(row?.fabricLabel) ||
      getWorkshopFieldLabel(originalRow?.fabricLabel) ||
      resolvedFabricLabel,
    resolvedFabricLabel,
    lining: normalizeLiningForSave(mergedSourceRow, productConfig),
    ...resolvedStitchFields,
  };
};

const createCurtainQuoteRowId = () => createClientId("curtain-row");

const DEFAULT_CURTAIN_PRODUCT_CONFIG =
  DEFAULT_PRODUCT_CONFIGS.find((config) => config.name === "Curtains") ||
  DEFAULT_PRODUCT_CONFIGS[0];

const withCurtainRowId = (row = {}, rowIndex = 0) => {
  const rowId =
    getWorkshopRowStableId(row) ||
    normalizeWorkshopText(row?.sourceRowId) ||
    createCurtainQuoteRowId();

  return {
    ...row,
    rateSource: row.rateSource || "loaded_saved_quote",
    rateOverridden: row.rateOverridden ?? row.rateSource === "manual_override",
    id: normalizeWorkshopText(row?.id) || rowId,
    rowId: normalizeWorkshopText(row?.rowId) || rowId,
    clientRowId: normalizeWorkshopText(row?.clientRowId) || rowId,
    sourceRowId: normalizeWorkshopText(row?.sourceRowId) || rowId,
    sourceIndex: Number.isInteger(Number(row?.sourceIndex ?? row?.originalIndex))
      ? Number(row?.sourceIndex ?? row?.originalIndex)
      : rowIndex,
    rowOrder: Number.isFinite(Number(row?.rowOrder)) ? Number(row.rowOrder) : rowIndex,
  };
};

const getWorkshopRowOrderValue = (row = {}, fallbackIndex = 0) => {
  const value = Number(row?.rowOrder ?? row?.sourceIndex ?? fallbackIndex);
  return Number.isFinite(value) ? value : fallbackIndex;
};

const getWorkshopRowsInQuotationOrder = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => ({
      ...row,
      effectiveRowOrder: getWorkshopRowOrderValue(row, index),
    }))
    .sort((left, right) => {
      const orderDiff =
        getWorkshopRowOrderValue(left, 0) - getWorkshopRowOrderValue(right, 0);
      if (orderDiff !== 0) return orderDiff;
      return Number(left.sourceIndex ?? 0) - Number(right.sourceIndex ?? 0);
    });

const renumberCurtainRowsForOrder = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
    withCurtainRowId({ ...(row || {}), rowOrder: index }, index)
  );

const QUOTE_RAW_FIELD_MUTATION_SOURCES = {
  USER_INPUT: "USER_INPUT",
  PRODUCT_CHANGE: "PRODUCT_CHANGE",
  PRODUCT_DEFAULT_APPLY: "PRODUCT_DEFAULT_APPLY",
  AUTOSAVE_RESTORE: "AUTOSAVE_RESTORE",
  AUTOSAVE_SAVE: "AUTOSAVE_SAVE",
  SERVER_LOAD: "SERVER_LOAD",
  SAVE_READBACK: "SAVE_READBACK",
  CALCULATION_RECOMPUTE: "CALCULATION_RECOMPUTE",
  CUSTOMER_LOOKUP: "CUSTOMER_LOOKUP",
  FABRIC_LOOKUP: "FABRIC_LOOKUP",
  PDF_EXPORT: "PDF_EXPORT",
  WORKSHOP_EXPORT: "WORKSHOP_EXPORT",
  HALF_PIECE_CALC: "HALF_PIECE_CALC",
  BULK_REPLACE: "BULK_REPLACE",
  UNKNOWN: "UNKNOWN",
};

const QUOTE_PROTECTED_RAW_FIELDS = [
  "item",
  "product",
  "productType",
  "productName",
  "fabric",
  "fabricName",
  "material",
  "materialCode",
  "fabricWidthIn",
  "width",
  "height",
  "mrp",
  "mrpPerMeter",
  "discount",
  "sqftRate",
  "rate",
  "ratePerFeet",
  "ratePerLength",
  "unitRate",
  "manualAmount",
  "amount",
  "qty",
  "quantity",
  "unit",
  "length",
  "lengthUnit",
  "description",
  "modelCode",
  "stitchingStyle",
  "stitchingStyleId",
  "customStitchRate",
  "gstRate",
  "lining",
];

const QUOTE_ALLOWED_RAW_MUTATION_SOURCES = new Set([
  QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_RESTORE,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.SERVER_LOAD,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE,
]);

const isQuoteMutationAuditDevMode = () =>
  IS_DEV_UI_ENABLED;

const isPermittedDecimalInput = (value) =>
  value === "" || /^-?\d*(?:\.\d*)?$/.test(String(value));

const roundRawDecimalString = (value, decimals = 4) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const factor = 10 ** decimals;
  return String(Math.round((numericValue + Number.EPSILON) * factor) / factor);
};

const getDimensionInputValueForUnit = (storedValue, selectedUnit = "inch") => {
  if (storedValue === "" || storedValue == null) return "";
  if (selectedUnit !== "cm") return String(storedValue);
  const numericValue = Number(storedValue);
  if (!Number.isFinite(numericValue)) return String(storedValue);
  return roundRawDecimalString(numericValue * 2.54, 2);
};

const getStoredDimensionValueFromInput = (inputValue, selectedUnit = "inch") => {
  if (inputValue === "") return "";
  if (!isPermittedDecimalInput(inputValue)) return null;
  if (selectedUnit !== "cm") return String(inputValue);
  return roundRawDecimalString(Number(inputValue) / 2.54, 4);
};

const getCurtainRowsSignature = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return [
        getWorkshopRowStableId(stableRow),
        stableRow.item ?? "",
        stableRow.productType ?? stableRow.product ?? "",
        stableRow.fabric ?? stableRow.fabricName ?? "",
        stableRow.width ?? "",
        stableRow.height ?? "",
        stableRow.mrp ?? "",
        stableRow.sqftRate ?? stableRow.rate ?? stableRow.unitRate ?? stableRow.manualAmount ?? "",
        stableRow.stitchingStyle ?? "",
      ].join("|");
    })
    .join("||");

const auditQuotationRowMutation = (previousRows = [], nextRows = [], source = "UNKNOWN") => {
  const previousById = new Map(
    (Array.isArray(previousRows) ? previousRows : []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return [getWorkshopRowStableId(stableRow), stableRow];
    })
  );
  const events = [];
  const timestamp = new Date().toISOString();
  const stack = isQuoteMutationAuditDevMode() ? new Error().stack : "";

  (Array.isArray(nextRows) ? nextRows : []).forEach((row, index) => {
    const nextRow = withCurtainRowId(row || {}, index);
    const rowId = getWorkshopRowStableId(nextRow);
    const previousRow = previousById.get(rowId);
    if (!previousRow) return;

    QUOTE_PROTECTED_RAW_FIELDS.forEach((field) => {
      const previousValue = previousRow?.[field];
      const nextValue = nextRow?.[field];
      if (Object.is(previousValue, nextValue)) return;
      const suspicious = !QUOTE_ALLOWED_RAW_MUTATION_SOURCES.has(source);
      const event = {
        source,
        rowId,
        item: nextRow.item || previousRow.item || "",
        field,
        previousValue,
        nextValue,
        suspicious,
        stack,
        timestamp,
      };
      events.push(event);

      if (isQuoteMutationAuditDevMode()) {
        devWarn(suspicious ? "UNAUTHORIZED_RAW_FIELD_CHANGE" : "QUOTE_FIELD_MUTATION", event);
      }
    });
  });

  if (isQuoteMutationAuditDevMode() && typeof window !== "undefined") {
    window.__FQ_LAST_QUOTE_FIELD_MUTATIONS__ = [
      ...(window.__FQ_LAST_QUOTE_FIELD_MUTATIONS__ || []),
      ...events,
    ].slice(-100);
  }

  return events;
};

const createDefaultCurtainQuoteRow = (item = "W1") =>
  withCurtainRowId({
    lineType: "measurement",
    rateSource: "product_default",
    rateOverridden: false,
    item,
    productId: DEFAULT_CURTAIN_PRODUCT_CONFIG?.id || "",
    productName: DEFAULT_CURTAIN_PRODUCT_CONFIG?.name || "Curtains",
    product: DEFAULT_CURTAIN_PRODUCT_CONFIG?.name || "Curtains",
    productType: "Curtains",
    calculationType: DEFAULT_CURTAIN_PRODUCT_CONFIG?.calculationType || "fabric_meter",
    fabric: "",
    fabricCodeRaw: "",
    fabricSource: "none",
    fabricCatalogueInvalidReason: "",
    brandId: "",
    brandName: "",
    fabricBrandId: "",
    fabricBrandName: "",
    brandSource: "none",
    catalogueId: "",
    catalogueName: "",
    catalogueKey: "",
    srNumber: "",
    srLabel: "",
    srPriceRuleId: "",
    srPriceRuleType: "none",
    srPriceRuleLabel: "",
    rrpPerMeter: null,
    rrpSource: "unmatched",
    fabricWidthIn: 54,
    fabricWidthSource: "workspace_default",
    fabricWidthManuallyOverridden: false,
    catalogueDefaultFabricWidthIn: null,
    srRuleFabricWidthIn: null,
    width: "",
    height: "",
    mrp: "",
    discount: "",
    sqftRate: "",
    stitchingStyle: "",
    customStitchRate: "",
    lining: { ...DEFAULT_CURTAIN_LINING },
  });

const DEFAULT_ACCESSORY_PRODUCT_CONFIG =
  DEFAULT_PRODUCT_CONFIGS.find((config) => isAccessoryProductConfig(config)) || null;

const createDefaultCurtainAccessoryRow = (item = "Accessory") =>
  withCurtainRowId({
    lineType: "accessory",
    rateSource: "product_default",
    rateOverridden: false,
    item,
    productId: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.id || "",
    productName: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    product: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    productType: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    category: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.category || "accessory",
    calculationType: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.calculationType || "quantity_rate",
    productConfigSnapshot: DEFAULT_ACCESSORY_PRODUCT_CONFIG
      ? buildProductConfigSnapshot(DEFAULT_ACCESSORY_PRODUCT_CONFIG)
      : null,
    description: "",
    modelCode: "",
    qty: 1,
    quantity: 1,
    unit: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.quantityRules?.defaultUnit || "pcs",
    unitRate: "",
    length: "",
    lengthUnit: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.runningFeetRules?.defaultUnit || "ft",
    ratePerLength: "",
    manualAmount: "",
    discount: "",
    gstRate: "",
    notes: "",
    fabric: "",
    fabricMeters: 0,
    orderedFabricMeters: 0,
    cutFabricMeters: 0,
    lining: { ...DEFAULT_CURTAIN_LINING },
  });


const isDeletedWorkshopExportRow = (row = {}) =>
  row?.deleted === true ||
  row?.isDeleted === true ||
  row?.archived === true ||
  row?.isArchived === true ||
  row?.active === false ||
  row?.isActive === false;

const hasMeaningfulWorkshopExportData = (row = {}) => {
  const product = getWorkshopRowProductLabel(row);
  const fabric = getCurtainRowFabricLabel(row);
  const hasFabric = Boolean(fabric && fabric !== WORKSHOP_EMPTY_VALUE);
  const widthValue = Number(row?.width || 0);
  const heightValue = Number(row?.height || 0);
  const hasMeasurements =
    widthValue > 0 ||
    heightValue > 0;
  const hasQuantity =
    Number(row?.quantity || row?.qty || 0) > 0;
  const hasNotes = Boolean(
    normalizeWorkshopText(row?.remarks || row?.remark || row?.notes || row?.note)
  );

  return Boolean(product && (hasFabric || hasMeasurements || hasQuantity || hasNotes));
};

const normalizeCurtainRowsForExport = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => withCurtainRowId(row, index))
    .filter((row) => !isDeletedWorkshopExportRow(row))
    .filter(hasMeaningfulWorkshopExportData)
    .map((row, index) => createWorkshopSourceRow(row, index, []));

const serializeCurtainRowsForDraft = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
    sanitizeLocalDraftValue(
      sanitizeCurtainRowEditableTextFields(withCurtainRowId(row || {}, index))
    )
  );

const getActiveCurtainRowsForExport = (sourceRows = []) =>
  normalizeCurtainRowsForExport(sourceRows);

const summarizeWorkshopExportRow = (row = {}, index = 0) => ({
  index: index + 1,
  id: getWorkshopRowStableId(row),
  sourceRowId: row?.sourceRowId || "",
  sourceIndex: row?.sourceIndex ?? "",
  item: getWorkshopRowItemLabel(row),
  product: getWorkshopRowProductLabel(row),
  fabric: getDisplayFabricLabel(row),
  fabricWidth: Number(row?.fabricWidthIn || 0),
  width: Number(row?.width || 0),
  height: Number(row?.height || 0),
  stitch: getCurtainRowStitchLabel(row, {}, null),
  fabricMeters: Number(row?.fabricMeters || row?.fabricMeter || row?.meters || 0),
  deleted: Boolean(isDeletedWorkshopExportRow(row)),
});

const countWorkshopRowsByProduct = (rowsToCount = []) =>
  rowsToCount.reduce((acc, row) => {
    const key = getWorkshopRowProductLabel(row) || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const validateWorkshopRowsMatchActiveRows = (activeRows = [], workshopRows = []) => {
  const activeSummary = activeRows.map(summarizeWorkshopExportRow);
  const workshopSummary = workshopRows.map(summarizeWorkshopExportRow);
  const mismatches = [];

  const activeCounts = countWorkshopRowsByProduct(activeRows);
  const workshopCounts = countWorkshopRowsByProduct(workshopRows);
  const productKeys = new Set([
    ...Object.keys(activeCounts),
    ...Object.keys(workshopCounts),
  ]);

  productKeys.forEach((product) => {
    if (Number(activeCounts[product] || 0) !== Number(workshopCounts[product] || 0)) {
      mismatches.push({
        type: "product-count",
        product,
        activeCount: activeCounts[product] || 0,
        workshopCount: workshopCounts[product] || 0,
      });
    }
  });

  activeRows.forEach((activeRow, index) => {
    const workshopRow = workshopRows[index];
    if (!workshopRow) {
      mismatches.push({
        type: "missing-workshop-row",
        active: activeSummary[index],
      });
      return;
    }

    const activeFabric = getCurtainRowFabricLabel(activeRow);
    const workshopFabric = getCurtainRowFabricLabel(workshopRow);
    const rowMismatch =
      !isSameWorkshopRowIdentity(activeRow, workshopRow) ||
      activeFabric !== workshopFabric ||
      !closeWorkshopNumber(activeRow?.fabricWidthIn, workshopRow?.fabricWidthIn) ||
      !closeWorkshopNumber(activeRow?.width, workshopRow?.width) ||
      !closeWorkshopNumber(activeRow?.height, workshopRow?.height);

    if (rowMismatch) {
      mismatches.push({
        type: "row-mismatch",
        active: activeSummary[index],
        workshop: workshopSummary[index],
      });
    }
  });

  workshopRows.slice(activeRows.length).forEach((workshopRow, offset) => {
    mismatches.push({
      type: "extra-workshop-row",
      workshop: workshopSummary[activeRows.length + offset],
      row: workshopRow,
    });
  });

  return {
    ok: mismatches.length === 0,
    activeCounts,
    workshopCounts,
    mismatches,
  };
};

const toMillis = (value) => {
  if (!value || isFirestoreSentinel(value)) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.getTime()
      : 0;
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime()) ? value.getTime() : 0;
  }

  if (typeof value === "string" || typeof value === "number") {
    const dateValue = new Date(value);
    return !Number.isNaN(dateValue.getTime()) ? dateValue.getTime() : 0;
  }

  if (typeof value === "object") {
    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
    if (Number.isFinite(seconds)) {
      return seconds * 1000 + (Number.isFinite(nanoseconds) ? Math.floor(nanoseconds / 1000000) : 0);
    }
  }

  return 0;
};

const toSortableMillis = safeDateMillis;

const getCreatedMillis = (quote) =>
  toMillis(quote?.createdAt) ||
  toMillis(quote?.date) ||
  toMillis(quote?.quoteDate) ||
  toMillis(quote?.generalQuote?.quoteDate) ||
  toMillis(quote?.generalQuoteData?.quoteDate) ||
  toMillis(quote?.crm?.createdAt) ||
  toMillis(quote?.updatedAt) ||
  0;

const getUpdatedMillis = (quote) =>
  toMillis(quote?.lastActivityAt) ||
  toMillis(quote?.updatedAt) ||
  toMillis(quote?.lastEditedAt) ||
  toMillis(quote?.modifiedAt) ||
  toMillis(quote?.editedAt) ||
  toMillis(quote?.crm?.lastActivityAt) ||
  toMillis(quote?.crm?.updatedAt) ||
  getCreatedMillis(quote);

const DEFAULT_RECORD_SORT_FIELDS = [
  "lastActivityMillis",
  "latestActivityMillis",
  "lastActivityAt",
  "updatedAt",
  "lastEditedAt",
  "modifiedAt",
  "editedAt",
  "createdAt",
  "quoteDate",
  "date",
  "quotationDate",
];

const getRecordSortInfo = (record = {}, fields = DEFAULT_RECORD_SORT_FIELDS) => {
  for (const field of fields) {
    const millis = toSortableMillis(record?.[field]);
    if (millis) {
      return { millis, source: field };
    }
  }
  return { millis: 0, source: "none" };
};

const getRecordSortTime = (record = {}, fields = DEFAULT_RECORD_SORT_FIELDS) =>
  getRecordSortInfo(record, fields).millis;

const QUOTE_LIST_SORT_FIELDS = [
  "lastActivityAt",
  "updatedAt",
  "createdAt",
  "quoteDate",
  "date",
  "quotationDate",
];

const CRM_LIST_SORT_FIELDS = [
  "lastActivityAt",
  "updatedAt",
  "lastEditedAt",
  "createdAt",
];

const getQuoteListSortInfo = (quote = {}) =>
  getRecordSortInfo(quote, QUOTE_LIST_SORT_FIELDS);

const getQuoteListSortTime = (quote = {}) =>
  getQuoteListSortInfo(quote).millis;

const getCrmListSortInfo = (record = {}) =>
  getRecordSortInfo(record, CRM_LIST_SORT_FIELDS);

const getCrmListSortTime = (record = {}) =>
  getCrmListSortInfo(record).millis;

const sortRecordsByLatestActivity = (records = [], fields = DEFAULT_RECORD_SORT_FIELDS) =>
  [...(Array.isArray(records) ? records : [])].sort(
    (a, b) => getRecordSortTime(b, fields) - getRecordSortTime(a, fields)
  );

const sortQuotesByLatestActivity = (records = []) =>
  sortRecordsByLatestActivity(records, QUOTE_LIST_SORT_FIELDS);

const sortCrmRecordsByLatestActivity = (records = []) =>
  sortRecordsByLatestActivity(records, CRM_LIST_SORT_FIELDS);

const traceListSort = (label, records = [], getIdentity = (record) => record?.id || "") => {
  if (!isDevRuntime) return;
  const list = Array.isArray(records) ? records : [];
  devLog("LIST_SORT_TRACE", {
    label,
    count: list.length,
    top: list.slice(0, 10).map((record, index) => {
      const sortInfo = getRecordSortInfo(record);
      return {
        rank: index + 1,
        id: getIdentity(record),
        quoteNo: record?.quoteNo || "",
        customerName: record?.customerName || record?.customerLabel || record?.title || "",
        sortSource: sortInfo.source,
        sortTime: sortInfo.millis ? new Date(sortInfo.millis).toISOString() : "",
      };
    }),
  });
};

const safeSavedQuoteArray = (value) => (Array.isArray(value) ? value : []);

const getQuoteIdentityCandidates = (quote = {}) =>
  [
    quote?.quoteIndexId,
    quote?.indexId,
    quote?.id,
    quote?.quoteId,
    quote?.sourceQuoteId,
    quote?.fullQuoteId,
    quote?.sourcePath,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const removeQuoteFromCards = (cards, identity = {}) => {
  const list = safeSavedQuoteArray(cards);
  const targetIds = new Set(
    [
      identity?.quoteIndexId,
      identity?.indexId,
      identity?.quoteId,
      identity?.sourceQuoteId,
      identity?.fullQuoteId,
      identity?.sourcePath,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );

  if (!targetIds.size) return list;

  return list.filter((card) => {
    if (!card || typeof card !== "object") return false;
    return !getQuoteIdentityCandidates(card).some((id) => targetIds.has(id));
  });
};

const removeQuoteFromCardsWithBlankingGuard = (cards, identity = {}) => {
  const list = safeSavedQuoteArray(cards);
  const next = removeQuoteFromCards(list, identity);
  const beforeCount = list.length;
  const afterCount = next.length;
  const removedCount = Math.max(0, beforeCount - afterCount);
  const wouldBlankList = beforeCount > 1 && afterCount === 0;

  if (isDevRuntime) {
    devLog("SAVED_QUOTES_DELETE_STATE_GUARD", {
      quoteIndexId: identity?.quoteIndexId || identity?.indexId || "",
      quoteId: identity?.quoteId || identity?.sourceQuoteId || identity?.fullQuoteId || "",
      quoteNo: identity?.quoteNo || "",
      beforeCount,
      afterCount,
      removedCount,
      hadMoreThanOneBefore: beforeCount > 1,
      wouldBlankList,
    });
  }

  if (wouldBlankList) {
    if (isDevRuntime) {
      devWarn("Blocked unsafe Saved Quotes blanking after delete", {
        quoteIndexId: identity?.quoteIndexId || identity?.indexId || "",
        quoteId: identity?.quoteId || identity?.sourceQuoteId || identity?.fullQuoteId || "",
        beforeCount,
        afterCount,
      });
    }
    return list;
  }

  return next;
};

const runSavedQuoteRemovalGuardSelfTest = () => {
  const cases = [
    {
      name: "removes middle quote by quoteIndexId",
      before: [{ quoteIndexId: "A" }, { quoteIndexId: "B" }, { quoteIndexId: "C" }],
      identity: { quoteIndexId: "B" },
      expectedIds: ["A", "C"],
    },
    {
      name: "handles undefined list",
      before: undefined,
      identity: { quoteIndexId: "B" },
      expectedIds: [],
    },
    {
      name: "handles null list",
      before: null,
      identity: { quoteIndexId: "B" },
      expectedIds: [],
    },
    {
      name: "falls back to quoteId",
      before: [{ quoteId: "A" }, { quoteId: "B" }, { quoteId: "C" }],
      identity: { quoteId: "B" },
      expectedIds: ["A", "C"],
    },
    {
      name: "does not blank a multi-card list on unsafe broad match",
      before: [{ quoteNo: "DUP" }, { quoteNo: "DUP" }],
      identity: { quoteNo: "DUP" },
      expectedIds: ["DUP", "DUP"],
    },
  ];

  const results = cases.map((testCase) => {
    const next = removeQuoteFromCardsWithBlankingGuard(testCase.before, testCase.identity);
    const ids = safeSavedQuoteArray(next).map(
      (quote) => quote.quoteIndexId || quote.quoteId || quote.id || quote.quoteNo || ""
    );
    return {
      name: testCase.name,
      pass: JSON.stringify(ids) === JSON.stringify(testCase.expectedIds),
      ids,
      expectedIds: testCase.expectedIds,
    };
  });

  const pass = results.every((result) => result.pass);
  if (isDevRuntime) {
    devTable(results);
  }
  return { pass, results };
};

if (typeof window !== "undefined" && IS_DEV_UI_ENABLED) {
  window.__FQ_TEST_SAVED_QUOTE_REMOVE_GUARD__ = runSavedQuoteRemovalGuardSelfTest;
}

const safeRemoveQuoteFromList = (prev, predicate) => {
  const list = safeSavedQuoteArray(prev);
  if (typeof predicate !== "function") return list;
  return list.filter((item) => !predicate(item));
};

const safePrependQuoteToList = (prev, quote, predicate) => {
  const withoutDuplicate = safeRemoveQuoteFromList(prev, predicate);
  if (!quote || typeof quote !== "object") return withoutDuplicate;
  return sortQuotesByLatestActivity([quote, ...withoutDuplicate]);
};

const getQuoteValue = (quote) => {
  const candidates = [
    quote?.grandTotal,
    quote?.total,
    quote?.finalTotal,
    quote?.totalAmount,
    quote?.amount,
    quote?.quoteValue,
    quote?.linkedQuoteValue,
    quote?.finalAmount,
    quote?.approvedValue,
    quote?.subtotal,
    quote?.crm?.finalValue,
    quote?.crm?.expectedValue,
  ];
  return (
    candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0) || 0
  );
};

const resolveQuoteType = (quote) => {
  const canonicalType = normalizeQuoteType(
    quote?.normalizedQuoteType ||
      quote?.quoteTypeResolved ||
      quote?.quoteType ||
      quote?.type ||
      quote?.quotationType ||
      "",
    quote
  );
  if (canonicalType === CANONICAL_GENERAL_QUOTE_TYPE) {
    return GENERAL_QUOTE_TYPE;
  }
  if (canonicalType === CANONICAL_CURTAIN_QUOTE_TYPE) {
    return CURTAIN_QUOTE_TYPE;
  }

  return CURTAIN_QUOTE_TYPE;
};

const ROUTE_TO_TAB = {
  "/dashboard": { tab: "dashboard" },
  "/todays-tasks": { tab: "todayTasks" },
  "/crm": { tab: "crm" },
  "/customer-ledger": { tab: "customerLedger" },
  "/execution-details": { tab: "executionSpecs" },
  "/execution-templates": { tab: "executionTemplates" },
  "/general-quotes": { tab: "quote", quoteSubTab: GENERAL_QUOTE_TYPE },
  "/curtain-quotes": { tab: "quote", quoteSubTab: CURTAIN_QUOTE_TYPE },
  "/stitching-rates": { tab: "stitching" },
  "/products-calculations": { tab: "products" },
  "/roll-optimizer": { tab: "fco" },
  "/customer-pdf": { tab: "quote" },
  "/workshop-pdf": { tab: "quote" },
  "/team-access": { tab: "teamAccess" },
  "/account-security": { tab: "accountSecurity" },
  "/maintenance": { tab: "maintenance" },
  "/saved-quotes": { tab: "saved" },
  "/settings": { tab: "settings" },
};

const getCurrentBrowserPathname = () =>
  typeof window !== "undefined" ? window.location.pathname || "" : "";

const QUOTATION_DRAFT_RECOVERY_ROUTES = {
  [GENERAL_QUOTE_TYPE]: "/general-quotes",
  [CURTAIN_QUOTE_TYPE]: "/curtain-quotes",
};

const isQuotationDraftModule = (module) =>
  module === LOCAL_DRAFT_MODULES.GENERAL_QUOTE ||
  module === LOCAL_DRAFT_MODULES.CURTAIN_QUOTE;

const isQuotationDraftRecoveryAllowed = ({
  currentRoute = "",
  activeModule = "",
  activeTab = "",
  activeQuoteSubTab = "",
} = {}) => {
  if (activeModule === LOCAL_DRAFT_MODULES.GENERAL_QUOTE) {
    return (
      currentRoute === QUOTATION_DRAFT_RECOVERY_ROUTES[GENERAL_QUOTE_TYPE] &&
      activeTab === "quote" &&
      activeQuoteSubTab === GENERAL_QUOTE_TYPE
    );
  }

  if (activeModule === LOCAL_DRAFT_MODULES.CURTAIN_QUOTE) {
    return (
      currentRoute === QUOTATION_DRAFT_RECOVERY_ROUTES[CURTAIN_QUOTE_TYPE] &&
      activeTab === "quote" &&
      activeQuoteSubTab === CURTAIN_QUOTE_TYPE
    );
  }

  return false;
};

const getRouteForTab = (tab, quoteSubTab) => {
  if (tab === "dashboard" || tab === "dashboardList") return "/dashboard";
  if (tab === "todayTasks") return "/todays-tasks";
  if (tab === "crm") return "/crm";
  if (tab === "customerLedger") return "/customer-ledger";
  if (tab === "executionSpecs") return "/execution-details";
  if (tab === "executionTemplates") return "/execution-templates";
  if (tab === "quote") {
    return quoteSubTab === GENERAL_QUOTE_TYPE ? "/general-quotes" : "/curtain-quotes";
  }
  if (tab === "stitching") return "/stitching-rates";
  if (tab === "products") return "/products-calculations";
  if (tab === "fco") return "/roll-optimizer";
  if (tab === "teamAccess") return "/team-access";
  if (tab === "accountSecurity") return "/account-security";
  if (tab === "maintenance") return "/maintenance";
  if (tab === "saved") return "/saved-quotes";
  if (tab === "settings") return "/settings";
  return "/dashboard";
};

const getEmbeddedGeneralQuote = (quote) => {
  const withResolvedGeneralItems = (base = {}) => {
    const contentSelection = resolveGeneralQuoteContentSelection(quote || {}, base);
    const items = Array.isArray(contentSelection.items) ? contentSelection.items : [];
    return {
      ...base,
      lineItems: items,
      items,
      rows: items,
      generalRows: items,
      quoteRows: items,
      generalItems: items,
      contentLoadField: contentSelection.field || "",
      contentLoadCount: contentSelection.count || 0,
    };
  };

  if (quote?.generalQuote && typeof quote.generalQuote === "object") {
    return withResolvedGeneralItems({
      ...quote.generalQuote,
      discountType:
        quote.generalQuote.discountType ?? quote.discountType ?? quote.discountMode,
      discountValue:
        quote.generalQuote.discountValue ??
        quote.discountValue ??
        quote.summaryDiscountValue,
      discountAmount: quote.generalQuote.discountAmount ?? quote.discountAmount,
      discountPercent: quote.generalQuote.discountPercent ?? quote.discountPercent,
      summaryDiscountMode:
        quote.generalQuote.summaryDiscountMode ?? quote.summaryDiscountMode,
      summaryDiscountValue:
        quote.generalQuote.summaryDiscountValue ?? quote.summaryDiscountValue,
      grandTotal: quote.generalQuote.grandTotal ?? quote.grandTotal,
      finalTotal: quote.generalQuote.finalTotal ?? quote.finalTotal,
      total: quote.generalQuote.total ?? quote.total,
      totalAmount: quote.generalQuote.totalAmount ?? quote.totalAmount,
      loadedGrandTotalFallback:
        quote.generalQuote.loadedGrandTotalFallback ?? quote.loadedGrandTotalFallback,
      savedGrandTotalFallback:
        quote.generalQuote.savedGrandTotalFallback ?? quote.savedGrandTotalFallback,
      preserveLoadedGrandTotalFallback:
        quote.generalQuote.preserveLoadedGrandTotalFallback === true ||
        quote.preserveLoadedGrandTotalFallback === true,
    });
  }

  if (quote?.generalQuoteData && typeof quote.generalQuoteData === "object") {
    return withResolvedGeneralItems({
      ...quote.generalQuoteData,
      discountType:
        quote.generalQuoteData.discountType ?? quote.discountType ?? quote.discountMode,
      discountValue:
        quote.generalQuoteData.discountValue ??
        quote.discountValue ??
        quote.summaryDiscountValue,
      discountAmount: quote.generalQuoteData.discountAmount ?? quote.discountAmount,
      discountPercent: quote.generalQuoteData.discountPercent ?? quote.discountPercent,
      summaryDiscountMode:
        quote.generalQuoteData.summaryDiscountMode ?? quote.summaryDiscountMode,
      summaryDiscountValue:
        quote.generalQuoteData.summaryDiscountValue ?? quote.summaryDiscountValue,
      grandTotal: quote.generalQuoteData.grandTotal ?? quote.grandTotal,
      finalTotal: quote.generalQuoteData.finalTotal ?? quote.finalTotal,
      total: quote.generalQuoteData.total ?? quote.total,
      totalAmount: quote.generalQuoteData.totalAmount ?? quote.totalAmount,
      loadedGrandTotalFallback:
        quote.generalQuoteData.loadedGrandTotalFallback ?? quote.loadedGrandTotalFallback,
      savedGrandTotalFallback:
        quote.generalQuoteData.savedGrandTotalFallback ?? quote.savedGrandTotalFallback,
      preserveLoadedGrandTotalFallback:
        quote.generalQuoteData.preserveLoadedGrandTotalFallback === true ||
        quote.preserveLoadedGrandTotalFallback === true,
    });
  }

  if (
    Array.isArray(quote?.lineItems) ||
    Array.isArray(quote?.items) ||
    Array.isArray(quote?.products) ||
    Array.isArray(quote?.rows) ||
    Array.isArray(quote?.generalRows) ||
    Array.isArray(quote?.quoteRows) ||
    Array.isArray(quote?.generalItems) ||
    Array.isArray(quote?.quotation?.items) ||
    Array.isArray(quote?.quotation?.products) ||
    Array.isArray(quote?.data?.items) ||
    Array.isArray(quote?.data?.products)
  ) {
    return withResolvedGeneralItems({
      ...quote,
      lineItems: Array.isArray(quote?.lineItems)
        ? quote.lineItems
        : Array.isArray(quote?.items)
        ? quote.items
        : Array.isArray(quote?.products)
        ? quote.products
        : Array.isArray(quote?.rows)
        ? quote.rows
        : Array.isArray(quote?.generalRows)
        ? quote.generalRows
        : Array.isArray(quote?.quoteRows)
        ? quote.quoteRows
        : quote.generalItems,
    });
  }

  return {};
};

const getConfirmedMillis = (quote) => {
  if (!isConfirmedQuoteRecord(quote)) {
    return 0;
  }

  return (
    toMillis(quote?.confirmedAt) ||
    toMillis(quote?.crm?.confirmedAt) ||
    toMillis(quote?.modifiedAt) ||
    toMillis(quote?.updatedAt) ||
    getCreatedMillis(quote)
  );
};

const getInstallationMillis = (quote) =>
  toMillis(quote?.installationDate) ||
  toMillis(quote?.crm?.installationDate) ||
  toMillis(quote?.scheduledInstallationDate) ||
  toMillis(quote?.siteInstallDate) ||
  toMillis(quote?.installDate) ||
  0;

const getQuoteCustomerLabel = (quote) =>
  String(
    quote?.customerName ||
      quote?.customer?.name ||
      quote?.customer ||
      quote?.crm?.customerName ||
      quote?.crm?.contact?.name ||
      quote?.quoteFor?.name ||
      quote?.quoteFor?.company ||
      quote?.generalQuote?.quoteFor?.company ||
      quote?.generalQuote?.quoteFor?.name ||
      quote?.generalQuoteData?.quoteFor?.company ||
      quote?.generalQuoteData?.quoteFor?.name ||
      "Unnamed Customer"
  ).trim() || "Unnamed Customer";

const CANCELLED_QUOTE_STATE_VALUES = new Set([
  "cancelled",
  "canceled",
  "quote_cancelled",
  "quote_canceled",
  "cancelled_by_customer",
  "canceled_by_customer",
  "order_cancelled",
  "order_canceled",
  "lost",
  "closed_lost",
  "lost_to_competitor",
]);

const normalizeQuoteStateValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getQuoteStageValue = (quote) =>
  normalizeQuoteStateValue(
    quote?.workStage ||
      quote?.quoteStage ||
      quote?.workflowStatus ||
      quote?.workStatus ||
      quote?.crm?.stageId ||
      quote?.crm?.stageName ||
      quote?.crm?.quoteStage ||
      quote?.crm?.workflowStatus ||
      quote?.crm?.workStage ||
      quote?.stage ||
      quote?.status ||
      "quote_given"
  );

const isDeletedQuoteRecord = (quote) =>
  quote?.deleted === true ||
  quote?.isDeleted === true ||
  quote?.archived === true ||
  quote?.binStatus === "in_bin" ||
  quote?.visibilityStatus === "bin" ||
  quote?.movedToBin === true ||
  Boolean(quote?.deletedAt || quote?.movedToBinAt);

const isCancelledQuoteRecord = (quote) => {
  if (quote?.cancelled === true || quote?.isCancelled === true) return true;
  return [
    getQuoteStageValue(quote),
    quote?.status,
    quote?.quoteStatus,
    quote?.quoteStage,
    quote?.workflowStatus,
    quote?.crmStatus,
    quote?.crm?.status,
    quote?.crm?.quoteStatus,
    quote?.crm?.quoteStage,
    quote?.crm?.workflowStatus,
    quote?.crm?.workStage,
  ].some((value) => CANCELLED_QUOTE_STATE_VALUES.has(normalizeQuoteStateValue(value)));
};

const isQuoteVisibleInSavedQuotesDefault = (quote) =>
  !isDeletedQuoteRecord(quote) && !isCancelledQuoteRecord(quote);

const REVENUE_STAGE_SET = new Set([
  "materials_ordered",
  "in_production",
  "ready",
  "ready_for_installation",
  "installed",
  "completed",
]);

const isRevenueQuote = (quote) => {
  if (!quote) return false;
  if (isDeletedQuoteRecord(quote) || isCancelledQuoteRecord(quote)) return false;
  if (!isConfirmedQuoteRecord(quote)) return false;

  return REVENUE_STAGE_SET.has(getQuoteStageValue(quote));
};

function isConfirmedQuoteRecord(quote) {
  const statusText = [
    quote?.status,
    quote?.crm?.status,
    quote?.workStage,
    quote?.workStatus,
    quote?.crm?.stageId,
    quote?.crm?.stageName,
    quote?.stage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return (
    quote?.isConfirmed === true ||
    quote?.confirmed === true ||
    Boolean(quote?.confirmedAt || quote?.crm?.confirmedAt) ||
    statusText.includes("confirmed") ||
    statusText.includes("advance") ||
    statusText.includes("materials ordered") ||
    statusText.includes("in production") ||
    statusText.includes("ready for installation") ||
    statusText.includes("installed") ||
    statusText.includes("completed")
  );
}

const isGeneralExecutionEligibleDeal = (deal = {}) => {
  if (!deal || typeof deal !== "object") return false;

  const type = String(
    deal.pipelineType ||
      deal.quoteTypeResolved ||
      deal.quoteType ||
      deal.linkedQuoteType ||
      deal.crm?.quoteType ||
      deal.crm?.pipelineType ||
      ""
  )
    .trim()
    .toLowerCase();
  const compactType = type.replace(/[\s_-]+/g, "");

  return (
    type === String(GENERAL_QUOTE_TYPE).toLowerCase() ||
    compactType === "generalquote" ||
    compactType === "generalquotation" ||
    compactType.startsWith("generalquote") ||
    compactType.startsWith("generalquotation")
  );
};

const sanitizeTaskHistorySegment = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "record";

const buildTaskHistoryId = (taskType, dealId, sourceTaskId) =>
  `${sanitizeTaskHistorySegment(taskType)}_${sanitizeTaskHistorySegment(
    dealId
  )}_${sanitizeTaskHistorySegment(sourceTaskId)}`;

const serializeTaskHistoryValue = (value) => {
  if (value == null) return value ?? null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((entry) => serializeTaskHistoryValue(entry));
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entryValue]) => {
      if (typeof entryValue === "undefined" || typeof entryValue === "function") {
        return result;
      }
      result[key] = serializeTaskHistoryValue(entryValue);
      return result;
    }, {});
  }
  return value;
};

const sanitizeCrmFirestoreValue = (value) => {
  if (typeof value === "undefined" || typeof value === "function") {
    return undefined;
  }
  if (value == null) return null;
  if (isLiveFirestoreSentinel(value)) return value;
  if (isFirestoreSentinel(value)) return undefined;
  if (typeof value?.toDate === "function") {
    return value;
  }
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeCrmFirestoreValue(entry))
      .filter((entry) => typeof entry !== "undefined");
  }
  if (typeof value === "number" && !Number.isFinite(value)) return 0;
  if (typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entryValue]) => {
      const sanitized = sanitizeCrmFirestoreValue(entryValue);
      if (typeof sanitized !== "undefined") {
        result[key] = sanitized;
      }
      return result;
    }, {});
  }
  return value;
};

const buildCrmWriteDebugPreview = (payload = {}) => {
  try {
    return JSON.stringify(sanitizeCrmFirestoreValue(payload), null, 2).slice(0, 6000);
  } catch (error) {
    return `Unable to preview CRM payload: ${error?.message || error}`;
  }
};

const logCrmTaskDoneFailure = (label, error, details = {}) => {
  if (!isDevRuntime) return;

  const { payload, ...rest } = details;
  devError("CRM TASK MARK DONE FAILED", {
    label,
    code: error?.code || "",
    message: error?.message || String(error || ""),
    stack: error?.stack || "",
    attemptedPayload: payload ? buildCrmWriteDebugPreview(payload) : "",
    ...rest,
  });
};

const getCanonicalChecklistCompleted = (item = {}) =>
  item.completed === true ||
  item.done === true ||
  item.checked === true ||
  item.isComplete === true ||
  item.completed === "true" ||
  item.done === "true" ||
  item.checked === "true" ||
  item.isComplete === "true" ||
  item.completed === 1 ||
  item.done === 1 ||
  item.checked === 1 ||
  item.isComplete === 1;

const withCanonicalChecklistAliases = (items = []) => {
  const normalized = preserveChecklistDraftTree(items || [], {
    templateMode: false,
    idPrefix: "crm-check",
  });

  const normalizeNode = (node = {}, index = 0) => {
    const children = Array.isArray(node.children)
      ? node.children.map((child, childIndex) => normalizeNode(child, childIndex))
      : [];
    const completed = getCanonicalChecklistCompleted(node);
    const label = String(node.label || node.title || "").trim();

    return sanitizeCrmFirestoreValue({
      ...node,
      id: String(node.id || node.templateId || `crm-check-${index + 1}`).trim(),
      label,
      title: String(node.title || label || "").trim(),
      completed,
      done: completed,
      checked: completed,
      assignedTo: node.assignedTo || node.assignedToNameSnapshot || "",
      children,
      subItems: children,
      subChecklist: children,
    });
  };

  return normalized.map((node, index) => normalizeNode(node, index));
};

const SITE_LOCATION_COORDINATE_PATTERNS = [
  /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
  /[?&](?:q|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
];

const extractSiteLocationCoordinates = (value = "") => {
  const text = decodeURIComponent(String(value || "").trim());
  for (const pattern of SITE_LOCATION_COORDINATE_PATTERNS) {
    const match = text.match(pattern);
    if (!match) continue;
    const latitude = Number(match[1]);
    const longitude = Number(match[2]);
    if (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      Math.abs(latitude) <= 90 &&
      Math.abs(longitude) <= 180
    ) {
      return { latitude, longitude };
    }
  }
  return { latitude: null, longitude: null };
};

const createSiteLocationDraft = (overrides = {}) => ({
  id: String(overrides.id || "").trim(),
  label: String(overrides.label || overrides.locationLabel || "").trim(),
  addressText: String(
    overrides.addressText || overrides.locationAddress || overrides.address || ""
  ).trim(),
  siteContactName: String(
    overrides.siteContactName || overrides.contactName || overrides.contact?.name || ""
  ).trim(),
  siteContactPhone: String(
    overrides.siteContactPhone || overrides.contactPhone || overrides.contact?.phone || ""
  ).trim(),
  googleMapsUrl: String(
    overrides.googleMapsUrl || overrides.url || overrides.locationUrl || ""
  ).trim(),
  latitude:
    typeof overrides.latitude === "number" || String(overrides.latitude || "").trim()
      ? overrides.latitude
      : "",
  longitude:
    typeof overrides.longitude === "number" ||
    String(overrides.longitude || "").trim()
      ? overrides.longitude
      : "",
  notes: String(overrides.notes || "").trim(),
  isPrimary: overrides.isPrimary === true,
  createdAt: overrides.createdAt || null,
  createdBy: String(overrides.createdBy || "").trim(),
  updatedAt: overrides.updatedAt || null,
  updatedBy: String(overrides.updatedBy || "").trim(),
});

const normalizeSiteLocation = (location = {}, index = 0) => {
  const draft = createSiteLocationDraft(location);
  const extracted = extractSiteLocationCoordinates(draft.googleMapsUrl);
  const latitude =
    Number.isFinite(Number(draft.latitude)) && String(draft.latitude).trim() !== ""
      ? Number(draft.latitude)
      : extracted.latitude;
  const longitude =
    Number.isFinite(Number(draft.longitude)) && String(draft.longitude).trim() !== ""
      ? Number(draft.longitude)
      : extracted.longitude;

  return {
    id: draft.id || createClientId(`site-location-${index}`),
    label: draft.label || (index === 0 ? "Primary Site" : `Site ${index + 1}`),
    addressText: draft.addressText,
    siteContactName: draft.siteContactName,
    siteContactPhone: draft.siteContactPhone,
    googleMapsUrl: draft.googleMapsUrl,
    latitude,
    longitude,
    notes: draft.notes,
    isPrimary: draft.isPrimary === true,
    createdAt: draft.createdAt || null,
    createdBy: draft.createdBy || "",
    updatedAt: draft.updatedAt || null,
    updatedBy: draft.updatedBy || "",
  };
};

const normalizeSiteLocations = (locations = []) => {
  const normalized = (Array.isArray(locations) ? locations : [])
    .filter(Boolean)
    .map((location, index) => normalizeSiteLocation(location, index))
    .filter(
      (location) =>
        location.label ||
        location.addressText ||
        location.siteContactName ||
        location.siteContactPhone ||
        location.googleMapsUrl ||
        location.notes
    );

  let hasPrimary = false;
  return normalized.map((location, index) => {
    const isPrimary =
      location.isPrimary === true && !hasPrimary
        ? true
        : !hasPrimary && index === 0 && !normalized.some((entry) => entry.isPrimary);
    if (isPrimary) hasPrimary = true;
    return { ...location, isPrimary };
  });
};

const getPrimarySiteLocation = (record = {}) => {
  const locations = normalizeSiteLocations(
    record?.siteLocations || record?.crm?.siteLocations || []
  );
  return (
    locations.find((location) => location.isPrimary) ||
    locations[0] ||
    null
  );
};

const buildSiteLocationSearchText = (locations = []) =>
  normalizeSiteLocations(locations)
    .flatMap((location) => [
      location.label,
      location.addressText,
      location.siteContactName,
      location.siteContactPhone,
      location.googleMapsUrl,
      location.notes,
    ])
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const buildSiteLocationOpenUrl = (location = {}) => {
  const normalized = normalizeSiteLocation(location);
  if (/^https?:\/\//i.test(normalized.googleMapsUrl)) {
    return normalized.googleMapsUrl;
  }
  if (Number.isFinite(normalized.latitude) && Number.isFinite(normalized.longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${normalized.latitude},${normalized.longitude}`;
  }
  const query = normalized.addressText || normalized.label;
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
};

const createEmptyQuoteSiteSelection = () => ({
  siteId: "",
  siteName: "",
  siteAddress: "",
  siteContactName: "",
  siteContactPhone: "",
  siteNotes: "",
  quoteDisplayTitle: "",
});

const createEmptyQuoteCustomerLink = () => ({
  customerId: "",
  customerKey: "",
  customerName: "",
  phone: "",
  whatsapp: "",
  email: "",
  address: "",
  companyName: "",
});

const normalizeQuoteSiteSnapshot = (source = {}, fallback = {}, options = {}) => {
  const cleanSiteText = (value = "") => {
    const text = String(value ?? "");
    return options.preserveWhitespace ? text : text.trim();
  };
  const generalQuote = source?.generalQuote || {};
  const siteSnapshot = source?.siteSnapshot || generalQuote?.siteSnapshot || {};
  const customerSnapshot = source?.customerSnapshot || generalQuote?.customerSnapshot || {};
  const quoteFor = source?.quoteFor || generalQuote?.quoteFor || {};
  const primaryLocation =
    getPrimarySiteLocation(source) ||
    getPrimarySiteLocation(generalQuote) ||
    getPrimarySiteLocation(source?.crm || {}) ||
    null;

  const siteName = cleanSiteText(
    siteSnapshot?.siteName ||
      source?.siteName ||
      source?.projectSiteName ||
      source?.projectLocationName ||
      source?.locationName ||
      generalQuote?.siteName ||
      generalQuote?.projectSiteName ||
      primaryLocation?.label ||
      fallback?.siteName ||
      ""
  );
  const siteAddress = cleanSiteText(
    siteSnapshot?.siteAddress ||
      source?.siteAddress ||
      source?.siteLocationAddress ||
      source?.projectAddress ||
      source?.siteLocation ||
      generalQuote?.siteAddress ||
      generalQuote?.siteLocationAddress ||
      quoteFor?.siteAddress ||
      primaryLocation?.addressText ||
      fallback?.siteAddress ||
      ""
  );
  const siteContactName = cleanSiteText(
    siteSnapshot?.siteContactName ||
      source?.siteContactName ||
      source?.siteContact ||
      source?.projectContactName ||
      generalQuote?.siteContactName ||
      fallback?.siteContactName ||
      ""
  );
  const siteContactPhone = cleanSiteText(
    siteSnapshot?.siteContactPhone ||
      source?.siteContactPhone ||
      source?.sitePhone ||
      source?.projectContactPhone ||
      generalQuote?.siteContactPhone ||
      fallback?.siteContactPhone ||
      ""
  );
  const siteNotes = cleanSiteText(
    siteSnapshot?.siteNotes ||
      siteSnapshot?.notes ||
      source?.siteNotes ||
      source?.projectNotes ||
      source?.siteInstructions ||
      generalQuote?.siteNotes ||
      primaryLocation?.notes ||
      fallback?.siteNotes ||
      ""
  );
  const siteId = cleanSiteText(
    siteSnapshot?.siteId ||
      source?.siteId ||
      source?.customerSiteId ||
      source?.projectSiteId ||
      generalQuote?.siteId ||
      primaryLocation?.id ||
      fallback?.siteId ||
      ""
  );
  const displayCustomerName = cleanSiteText(
    customerSnapshot?.customerName ||
      source?.customerCompanyName ||
      quoteFor?.company ||
      source?.customerName ||
      quoteFor?.name ||
      fallback?.customerName ||
      ""
  );
  const derivedQuoteDisplayTitle = [displayCustomerName, siteName]
    .filter(Boolean)
    .join(" - ");
  const quoteDisplayTitle = cleanSiteText(
    derivedQuoteDisplayTitle ||
      fallback?.quoteDisplayTitle ||
      source?.quoteDisplayTitle ||
      generalQuote?.quoteDisplayTitle ||
      ""
  );

  return {
    siteId,
    siteName,
    siteAddress,
    siteContactName,
    siteContactPhone,
    siteNotes,
    quoteDisplayTitle,
  };
};

const hasQuoteSiteSnapshot = (site = {}) =>
  Boolean(
    String(
      site?.siteName ||
        site?.siteAddress ||
        site?.siteContactName ||
        site?.siteContactPhone ||
        site?.siteNotes ||
        ""
    ).trim()
  );

const buildQuoteSiteFieldsForSave = (
  siteLike = {},
  {
    customerName = "",
    customerId = "",
    quoteNo = "",
    ensureSiteId = false,
  } = {}
) => {
  const normalized = normalizeQuoteSiteSnapshot(siteLike, { customerName });
  const shouldPersist = hasQuoteSiteSnapshot(normalized);
  const siteId =
    normalized.siteId ||
    (shouldPersist && ensureSiteId
      ? createClientId("site").replace(/[^a-zA-Z0-9_-]+/g, "_")
      : "");
  const quoteDisplayTitle = buildQuoteDisplayTitleFromParts({
    customerName,
    siteName: normalized.siteName,
    quoteNo,
  });

  return {
    siteId,
    customerSiteId: siteId,
    siteName: normalized.siteName,
    siteAddress: normalized.siteAddress,
    siteContactName: normalized.siteContactName,
    siteContactPhone: normalized.siteContactPhone,
    siteNotes: normalized.siteNotes,
    quoteDisplayTitle,
    projectName: normalized.siteName || siteLike?.projectName || siteLike?.projectTitle || "",
    customerId: customerId || siteLike?.customerId || "",
    siteSnapshot: {
      siteId,
      siteName: normalized.siteName,
      siteAddress: normalized.siteAddress,
      siteContactName: normalized.siteContactName,
      siteContactPhone: normalized.siteContactPhone,
      siteNotes: normalized.siteNotes,
      notes: normalized.siteNotes,
    },
  };
};

const getQuoteSiteDisplayText = (siteLike = {}) => {
  const site = normalizeQuoteSiteSnapshot(siteLike);
  return [site.siteName, site.siteAddress].filter(Boolean).join(" - ");
};

const normalizeCustomerLedgerName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");

const normalizeCustomerLedgerPhone = (value = "") => {
  const digits = String(value || "").replace(/\D+/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const normalizeCustomerLedgerEmail = (value = "") =>
  String(value || "").trim().toLowerCase();

const buildCustomerLedgerSearchText = (customer = {}) => {
  const baseIndex = buildSafeCustomerSearchIndex(customer).searchText;
  const extraLocationText = normalizeSearchText(
    safeSearchPiece(customer.leadLocationSearchText, 800)
  );
  const indexedText = normalizeSearchText(
    [
      customer.searchText,
      customer.latestQuoteNo,
      customer.lastQuoteNo,
      customer.quoteNo,
      customer.stageLabel,
      customer.lastDealStage,
      ...(Array.isArray(customer.sourceLabels) ? customer.sourceLabels : []),
    ]
      .filter(Boolean)
      .join(" ")
  );
  return [baseIndex, indexedText, extraLocationText].filter(Boolean).join(" ");
};

const normalizeCustomerLedgerRecord = (record = {}) => {
  const customerName = String(
    record.customerName ||
      record.displayName ||
      record.name ||
      record.contactName ||
      "Unnamed Customer"
  ).trim();
  const primaryPhone = String(
    record.primaryPhone || record.phone || record.customerPhone || ""
  ).trim();
  const phone = primaryPhone;
  const whatsapp = String(record.whatsapp || record.customerWhatsapp || "").trim();
  const email = String(record.email || record.customerEmail || "").trim();
  const alternatePhones = Array.isArray(record.alternatePhones)
    ? record.alternatePhones.map((entry) => String(entry || "").trim()).filter(Boolean)
    : String(record.alternatePhone || record.alternatePhones || "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);
  const normalizedName =
    record.normalizedName || normalizeCustomerLedgerName(customerName);
  const normalizedPhone =
    record.normalizedPhone || normalizeCustomerLedgerPhone(phone || whatsapp);
  const normalizedWhatsapp =
    record.normalizedWhatsapp || normalizeCustomerLedgerPhone(whatsapp || phone);
  const normalizedEmail =
    record.normalizedEmail || normalizeCustomerLedgerEmail(email);
  const searchIndex = buildSafeCustomerSearchIndex({
    ...record,
    customerName,
    phone,
    whatsapp,
    email,
    normalizedName,
    nameLower: normalizeCustomerSearchText(customerName),
    customerNameLower: normalizeCustomerSearchText(customerName),
    normalizedPhone,
    normalizedWhatsapp,
    normalizedEmail,
  });

  return {
    id: String(record.id || "").trim(),
    customerKey: String(record.customerKey || record.id || record.customerId || "").trim(),
    displayName: String(record.displayName || customerName).trim(),
    customerName,
    customerType: String(record.customerType || "").trim(),
    normalizedName,
    nameLower: normalizeCustomerSearchText(customerName),
    customerNameLower: normalizeCustomerSearchText(customerName),
    nameNormalized: record.nameNormalized || normalizedName,
    normalizedPhone,
    phoneNormalized: record.phoneNormalized || normalizedPhone,
    normalizedWhatsapp,
    normalizedEmail,
    emailCanonical: record.emailCanonical || normalizedEmail,
    primaryPhone,
    phone,
    alternatePhone: alternatePhones.join(", "),
    alternatePhones,
    whatsapp,
    email,
    address: String(record.address || "").trim(),
    location: String(record.location || record.place || "").trim(),
    companyName: String(record.companyName || record.companyOrProject || "").trim(),
    source: String(record.source || "").trim(),
    sourceType: String(record.sourceType || "").trim(),
    sourceTypes: Array.isArray(record.sourceTypes)
      ? record.sourceTypes.filter(Boolean).map(String)
      : [record.sourceType].filter(Boolean).map(String),
    sourceLabels: Array.isArray(record.sourceLabels)
      ? record.sourceLabels.filter(Boolean).map(String)
      : getCustomerSearchSourceLabels(record),
    personId: String(record.personId || record.customerSearchIndexId || "").trim(),
    customerSearchIndexId: String(record.customerSearchIndexId || record.personId || "").trim(),
    customerId: String(record.customerId || "").trim(),
    crmLeadId: String(record.crmLeadId || record.leadId || "").trim(),
    leadId: String(record.leadId || record.crmLeadId || "").trim(),
    quoteId: String(record.quoteId || record.latestQuoteId || "").trim(),
    latestQuoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteNo: String(record.latestQuoteNo || record.quoteNo || "").trim(),
    stageLabel: String(record.stageLabel || record.lastDealStage || "").trim(),
    status: String(record.status || "").trim(),
    tags: Array.isArray(record.tags) ? record.tags.filter(Boolean).map(String) : [],
    ownerUid: String(record.ownerUid || record.assignedToUserId || "").trim(),
    ownerName: String(record.ownerName || record.assignedToNameSnapshot || "").trim(),
    contactPersonName: String(
      record.contactPersonName || record.contactName || record.contact?.name || ""
    ).trim(),
    contactPersonPhone: String(
      record.contactPersonPhone || record.contactPhone || record.contact?.phone || ""
    ).trim(),
    notes: String(record.notes || "").trim(),
    ...normalizeQuoteSiteSnapshot(record, { customerName }),
    siteLocations: normalizeSiteLocations(record.siteLocations || []),
    totals:
      record.totals && typeof record.totals === "object"
        ? {
            ...record.totals,
            totalQuotes: Number(record.totals.totalQuotes ?? record.quoteCount ?? 0),
            totalQuoteValue: Number(
              record.totals.totalQuoteValue ?? record.totalQuotedValue ?? 0
            ),
            confirmedQuotes: Number(
              record.totals.confirmedQuotes ?? record.confirmedCount ?? 0
            ),
            confirmedValue: Number(
              record.totals.confirmedValue ?? record.confirmedValue ?? 0
            ),
          }
        : {
            totalQuotes: Number(record.quoteCount || 0),
            totalQuoteValue: Number(record.totalQuotedValue || 0),
            confirmedQuotes: Number(record.confirmedCount || 0),
            confirmedValue: Number(record.confirmedValue || 0),
            completedJobs: 0,
            pendingAmount: 0,
            receivedAmount: 0,
          },
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || 0),
    totalQuotedValue: Number(record.totalQuotedValue || record.totals?.totalQuoteValue || 0),
    confirmedValue: Number(record.confirmedValue || record.totals?.confirmedValue || 0),
    openDealCount: Number(record.openDealCount || 0),
    activeDealValue: Number(record.activeDealValue || 0),
    pendingFollowupCount: Number(record.pendingFollowupCount || 0),
    upcomingInstallationCount: Number(record.upcomingInstallationCount || 0),
    lastQuoteId: String(record.lastQuoteId || "").trim(),
    lastQuoteNo: String(record.lastQuoteNo || "").trim(),
    lastDealId: String(record.lastDealId || "").trim(),
    lastDealStage: String(record.lastDealStage || "").trim(),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
    createdBy: record.createdBy || "",
    lastActivityAt: record.lastActivityAt || record.updatedAt || record.createdAt || null,
    searchText: String(record.searchText || searchIndex.searchText || "").slice(
      0,
      CUSTOMER_SEARCH_TEXT_MAX_CHARS
    ),
    searchKeywords: Array.isArray(record.searchKeywords)
      ? record.searchKeywords.slice(0, CUSTOMER_SEARCH_KEYWORDS_MAX)
      : searchIndex.searchKeywords,
    searchPrefixes: Array.isArray(record.searchPrefixes)
      ? record.searchPrefixes.slice(0, CUSTOMER_SEARCH_PREFIXES_MAX)
      : searchIndex.searchPrefixes,
    phonePrefixes: Array.isArray(record.phonePrefixes)
      ? record.phonePrefixes.slice(0, CUSTOMER_SEARCH_PREFIXES_MAX)
      : searchIndex.phonePrefixes,
  };
};

const CUSTOMER_LEDGER_TYPE_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "homeowner", label: "Homeowner" },
  { value: "business", label: "Business" },
  { value: "architect_designer", label: "Architect / Designer" },
  { value: "contractor", label: "Contractor" },
  { value: "builder", label: "Builder" },
  { value: "other", label: "Other" },
];

const createCustomerLedgerEditDraft = (customer = {}) => {
  const normalized = normalizeCustomerLedgerRecord(customer || {});
  return {
    customerName: normalized.customerName === "Unnamed Customer" ? "" : normalized.customerName,
    customerType: normalized.customerType || "",
    primaryPhone: normalized.primaryPhone || normalized.phone || "",
    alternatePhone: normalized.alternatePhones?.join(", ") || normalized.alternatePhone || "",
    email: normalized.email || "",
    address: normalized.address || "",
    location: normalized.location || "",
    source: normalized.source || "",
    tags: (normalized.tags || []).join(", "),
    ownerUid: normalized.ownerUid || "",
    ownerName: normalized.ownerName || "",
    contactPersonName: normalized.contactPersonName || "",
    contactPersonPhone: normalized.contactPersonPhone || "",
    notes: normalized.notes || "",
  };
};

const createCustomerLedgerEditState = () => ({
  open: false,
  customerId: "",
  original: null,
  draft: createCustomerLedgerEditDraft(),
  updateActiveQuotes: false,
  includeClosedQuotes: false,
  saving: false,
  error: "",
});

const parseCustomerLedgerTags = (value = "") =>
  String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const parseCustomerLedgerPhones = (value = "") =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

const isValidCustomerLedgerEmail = (value = "") => {
  const email = String(value || "").trim();
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const buildCustomerLedgerEditPatch = (draft = {}, original = {}) => {
  const tags = parseCustomerLedgerTags(draft.tags);
  const alternatePhones = parseCustomerLedgerPhones(draft.alternatePhone);
  const primaryPhone = String(draft.primaryPhone || "").trim();
  const email = String(draft.email || "").trim();
  const normalized = normalizeCustomerLedgerRecord({
    ...original,
    customerName: String(draft.customerName || "").trim(),
    displayName: String(draft.customerName || "").trim(),
    customerType: String(draft.customerType || "").trim(),
    primaryPhone,
    phone: primaryPhone,
    alternatePhones,
    email,
    address: String(draft.address || "").trim(),
    location: String(draft.location || "").trim(),
    source: String(draft.source || "").trim(),
    tags,
    ownerUid: String(draft.ownerUid || "").trim(),
    ownerName: String(draft.ownerName || "").trim(),
    contactPersonName: String(draft.contactPersonName || "").trim(),
    contactPersonPhone: String(draft.contactPersonPhone || "").trim(),
    notes: String(draft.notes || "").trim(),
  });
  const searchIndex = buildSafeCustomerSearchIndex(normalized);

  return {
    ...normalized,
    displayName: normalized.customerName,
    primaryPhone,
    phone: primaryPhone,
    phoneNormalized: normalized.normalizedPhone,
    emailCanonical: normalized.normalizedEmail,
    alternatePhones,
    tags,
    ...searchIndex,
  };
};

const getCustomerLedgerChangedFields = (before = {}, after = {}) => {
  const keys = [
    "customerName",
    "customerType",
    "primaryPhone",
    "phone",
    "alternatePhone",
    "email",
    "address",
    "location",
    "source",
    "tags",
    "ownerUid",
    "ownerName",
    "contactPersonName",
    "contactPersonPhone",
    "notes",
  ];
  return keys.filter((key) => {
    const left = Array.isArray(before[key]) ? before[key].join(",") : String(before[key] ?? "");
    const right = Array.isArray(after[key]) ? after[key].join(",") : String(after[key] ?? "");
    return left.trim() !== right.trim();
  });
};

const CUSTOMER_SEARCH_SOURCE_LABELS = {
  customer: "Customer",
  crm_lead: "Lead",
  quote_customer: "Quote history only",
};

const getCustomerSearchSourceLabels = (record = {}) => {
  const sourceTypes = Array.isArray(record.sourceTypes)
    ? record.sourceTypes
    : [record.sourceType].filter(Boolean);
  return uniqueValues(
    sourceTypes.map((sourceType) => CUSTOMER_SEARCH_SOURCE_LABELS[sourceType] || sourceType)
  ).filter(Boolean);
};

const getPersonIdentityKey = (record = {}) => {
  const phone = normalizeCustomerLedgerPhone(
    record.phone ||
      record.whatsapp ||
      record.normalizedPhone ||
      record.phoneNormalized ||
      record.normalizedWhatsapp ||
      ""
  );
  if (phone) return `phone_${phone}`;

  const email = normalizeCustomerLedgerEmail(
    record.email ||
      record.normalizedEmail ||
      record.emailCanonical ||
      record.customerEmail ||
      ""
  );
  if (email) return `email_${email}`;

  const name = normalizeCustomerLedgerName(
    record.customerName ||
      record.displayName ||
      record.name ||
      record.normalizedName ||
      record.nameNormalized ||
      ""
  );
  if (name) return `name_${name}`;

  return String(record.personId || record.customerSearchIndexId || record.id || "").trim();
};

const getCustomerPersonBadgeLabel = (record = {}) => {
  const hasCustomer =
    record.hasCustomer === true ||
    record.sourceSummary?.hasCustomer === true ||
    (Array.isArray(record.sourceTypes) && record.sourceTypes.includes("customer")) ||
    Boolean(record.customerId);
  const hasLead =
    record.hasLead === true ||
    record.sourceSummary?.hasLead === true ||
    (Array.isArray(record.sourceTypes) && record.sourceTypes.includes("crm_lead")) ||
    Boolean(record.crmLeadId || record.leadId);
  const hasQuotes =
    record.hasQuotes === true ||
    record.sourceSummary?.hasQuotes === true ||
    (Array.isArray(record.sourceTypes) && record.sourceTypes.includes("quote_customer")) ||
    Boolean(record.latestQuoteNo || record.latestQuoteId || record.quoteCount);

  if (hasCustomer && hasQuotes) return "Customer with quotes";
  if (hasCustomer) return "Customer";
  if (hasLead && hasQuotes) return "Lead with previous quote";
  if (hasLead) return "Lead";
  if (hasQuotes) return "Quote history only";
  return "Contact";
};

const mergePersonSearchResultRecords = (entries = []) => {
  const personMap = new Map();

  const pickLatestDate = (left, right) => {
    const leftMillis = safeTimeMillis(left);
    const rightMillis = safeTimeMillis(right);
    return rightMillis > leftMillis ? right : left;
  };

  (Array.isArray(entries) ? entries : []).filter(Boolean).forEach((entry) => {
    const normalized = normalizeCustomerSearchPersonForQuote(entry);
    const identityKey = getPersonIdentityKey(normalized);
    if (!identityKey) return;

    const existing = personMap.get(identityKey);
    if (!existing) {
      personMap.set(identityKey, {
        ...normalized,
        sourceTypes: uniqueValues(normalized.sourceTypes || []),
        sourceLabels: [getCustomerPersonBadgeLabel(normalized)],
      });
      return;
    }

    const latestQuoteDate = pickLatestDate(existing.latestQuoteDate, normalized.latestQuoteDate);
    const latestQuoteMillis = safeTimeMillis(normalized.latestQuoteDate);
    const existingQuoteMillis = safeTimeMillis(existing.latestQuoteDate);
    const useIncomingQuote = latestQuoteMillis >= existingQuoteMillis;
    const mergedSourceTypes = uniqueValues([
      ...(existing.sourceTypes || []),
      ...(normalized.sourceTypes || []),
    ]);
    const hasCustomer =
      existing.hasCustomer === true || normalized.hasCustomer === true ||
      Boolean(existing.customerId || normalized.customerId) ||
      mergedSourceTypes.includes("customer");
    const hasLead =
      existing.hasLead === true || normalized.hasLead === true ||
      Boolean(existing.crmLeadId || existing.leadId || normalized.crmLeadId || normalized.leadId) ||
      mergedSourceTypes.includes("crm_lead");
    const hasQuotes =
      existing.hasQuotes === true || normalized.hasQuotes === true ||
      Boolean(existing.latestQuoteNo || normalized.latestQuoteNo) ||
      mergedSourceTypes.includes("quote_customer");
    const quoteIds = uniqueValues([
      ...(Array.isArray(existing.quoteIds) ? existing.quoteIds : []),
      ...(Array.isArray(normalized.quoteIds) ? normalized.quoteIds : []),
      existing.latestQuoteId,
      normalized.latestQuoteId,
      existing.quoteId,
      normalized.quoteId,
    ]);

    const merged = {
      ...existing,
      ...normalized,
      id: existing.customerId || normalized.customerId || existing.id || normalized.id,
      personId: existing.personId || normalized.personId,
      customerSearchIndexId:
        existing.customerSearchIndexId || normalized.customerSearchIndexId || existing.personId || normalized.personId,
      customerId: existing.customerId || normalized.customerId || "",
      crmLeadId: existing.crmLeadId || normalized.crmLeadId || "",
      leadId: existing.leadId || normalized.leadId || "",
      customerName: existing.customerName || normalized.customerName || "",
      phone: existing.phone || normalized.phone || "",
      whatsapp: existing.whatsapp || normalized.whatsapp || "",
      email: existing.email || normalized.email || "",
      address: existing.address || normalized.address || "",
      companyName: existing.companyName || normalized.companyName || "",
      latestQuoteDate,
      latestQuoteId: useIncomingQuote
        ? normalized.latestQuoteId || normalized.quoteId || existing.latestQuoteId
        : existing.latestQuoteId || normalized.latestQuoteId,
      latestQuoteNo: useIncomingQuote
        ? normalized.latestQuoteNo || normalized.quoteNo || existing.latestQuoteNo
        : existing.latestQuoteNo || normalized.latestQuoteNo,
      quoteCount: Math.max(
        Number(existing.quoteCount || 0),
        Number(normalized.quoteCount || 0),
        quoteIds.length
      ),
      quoteIds,
      sourceTypes: mergedSourceTypes,
      sourceSummary: { hasCustomer, hasLead, hasQuotes },
      hasCustomer,
      hasLead,
      hasQuotes,
      lastActivityAt: pickLatestDate(existing.lastActivityAt, normalized.lastActivityAt),
      lastActivityLabel: normalized.lastActivityLabel || existing.lastActivityLabel || "",
    };
    merged.sourceLabels = [getCustomerPersonBadgeLabel(merged)];
    personMap.set(identityKey, merged);
  });

  return Array.from(personMap.values());
};

const normalizeCustomerSearchPersonForQuote = (record = {}) => {
  const customerId = String(record.customerId || record.customerKey || "").trim();
  const personId = String(record.personId || record.id || "").trim();
  const sourceLabels = getCustomerSearchSourceLabels(record);
  const sourceTypes = Array.isArray(record.sourceTypes) ? record.sourceTypes : [record.sourceType].filter(Boolean);
  const hasCustomer =
    record.hasCustomer === true ||
    record.sourceSummary?.hasCustomer === true ||
    sourceTypes.includes("customer") ||
    Boolean(customerId);
  const hasLead =
    record.hasLead === true ||
    record.sourceSummary?.hasLead === true ||
    sourceTypes.includes("crm_lead") ||
    Boolean(record.crmLeadId || record.leadId);
  const hasQuotes =
    record.hasQuotes === true ||
    record.sourceSummary?.hasQuotes === true ||
    sourceTypes.includes("quote_customer") ||
    Boolean(record.latestQuoteNo || record.quoteNo || record.latestQuoteId || record.quoteId);
  const normalized = normalizeCustomerLedgerRecord({
    ...record,
    id: customerId || personId,
    customerKey: record.customerKey || customerId || personId,
    customerName: record.customerName || record.displayName || "",
    phone: record.phone || record.customerPhone || "",
    whatsapp: record.whatsapp || record.customerWhatsapp || "",
    email: record.email || record.customerEmail || "",
    address: record.address || record.customerAddress || "",
    companyName: record.companyName || record.customerCompanyName || record.companyOrProject || "",
    ...normalizeQuoteSiteSnapshot(record, {
      customerName: record.customerName || record.displayName || "",
    }),
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || (record.latestQuoteId ? 1 : 0)),
    totalQuotedValue: Number(record.totalQuotedValue || record.totalAmount || 0),
    lastQuoteId: record.lastQuoteId || record.latestQuoteId || record.quoteId || "",
    lastQuoteNo: record.lastQuoteNo || record.latestQuoteNo || record.quoteNo || "",
    lastDealId: record.lastDealId || record.crmLeadId || record.leadId || "",
    lastDealStage: record.lastDealStage || record.stageLabel || record.stageId || "",
    lastActivityAt:
      record.lastActivityAt ||
      record.latestQuoteDate ||
      record.quoteDate ||
      record.updatedAt ||
      record.createdAt ||
      null,
  });

  return {
    ...normalized,
    sourceType: record.sourceType || "",
    sourceTypes,
    sourceLabels,
    sourceSummary: { hasCustomer, hasLead, hasQuotes },
    hasCustomer,
    hasLead,
    hasQuotes,
    personId,
    customerSearchIndexId: personId,
    customerId,
    crmLeadId: String(record.crmLeadId || record.leadId || "").trim(),
    leadId: String(record.crmLeadId || record.leadId || "").trim(),
    quoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteNo: String(record.latestQuoteNo || record.quoteNo || "").trim(),
    latestQuoteDate:
      record.latestQuoteDate ||
      record.quoteDate ||
      record.date ||
      record.confirmedAt ||
      record.updatedAt ||
      record.createdAt ||
      null,
    latestQuoteStage: String(record.latestQuoteStage || record.stageLabel || record.stageId || "").trim(),
    latestQuoteType: String(record.latestQuoteType || record.quoteType || record.quotationType || "").trim(),
    sourceQuoteId: String(record.sourceQuoteId || record.fullQuoteId || record.latestQuoteId || record.quoteId || "").trim(),
    fullQuoteId: String(record.sourceQuoteId || record.fullQuoteId || record.latestQuoteId || record.quoteId || "").trim(),
    quoteIndexId: String(record.quoteIndexId || record.indexId || record.indexDocId || record.latestQuoteId || record.quoteId || "").trim(),
    indexId: String(record.quoteIndexId || record.indexId || record.indexDocId || record.latestQuoteId || record.quoteId || "").trim(),
    sourcePath: String(record.sourcePath || record.fullQuotePath || "").trim(),
    quotationType: record.quotationType || record.quoteType || record.quoteTypeResolved || "",
    rowStorageMode: record.rowStorageMode || "",
    rowChunkCount: Number(record.rowChunkCount || 0),
    grandTotal: Number(record.grandTotal || record.totalAmount || record.latestQuoteTotal || 0),
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || (hasQuotes ? 1 : 0)),
    leadStage: String(record.leadStage || record.stageLabel || record.stageId || "").trim(),
    customerStatus: String(record.customerStatus || record.status || "").trim(),
    stageLabel: String(record.stageLabel || record.lastDealStage || "").trim(),
    status: record.status || "",
  };
};

const flattenChecklistNodes = (items = []) => {
  const nodes = [];

  const walk = (entries, parents = []) => {
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      if (!entry) return;

      const title =
        String(entry.title ?? entry.label ?? "").trim() || "Checklist Item";
      const path = [...parents, title];

      nodes.push({
        ...entry,
        title,
        path,
        pathLabel: path.join(" > "),
      });

      if (Array.isArray(entry.children) && entry.children.length > 0) {
        walk(entry.children, path);
      }
    });
  };

  walk(normalizeChecklistTree(items));
  return nodes;
};

const getNewlyCompletedChecklistNodes = (previousItems = [], nextItems = []) => {
  const previousMap = new Map(
    flattenChecklistNodes(previousItems).map((entry) => [entry.id, entry])
  );

  return flattenChecklistNodes(nextItems).filter((entry) => {
    if (entry.done !== true) return false;
    return previousMap.get(entry.id)?.done !== true;
  });
};

/* --------------------------- DEFAULTS --------------------------- */


const STITCHING_SETTING_HELP = {
  rate:
    "Used to calculate stitching/track charge based on curtain width or configured track length.",
  fullness:
    "Used for fabric calculation. Example: 3 means curtain fabric is calculated at 3x fullness.",
  trackLength:
    "Used as fallback track length when a row does not have custom track length.",
};

const BENDED_TRACK_PRICING_METHODS = [
  { value: "none", label: "No extra bend charge" },
  { value: "fixed_per_bend", label: "Fixed charge per bend" },
  { value: "by_angle", label: "Charge by bend angle" },
  { value: "manual", label: "Manual charge in quote" },
];

const BENDED_TRACK_SHAPES = [
  { shape: "straight", label: "Straight Track", defaultBendCount: 0, defaultAngleDeg: null },
  { shape: "bend_90", label: "90 deg L Bend", defaultBendCount: 1, defaultAngleDeg: 90, angleKey: "90" },
  { shape: "bend_120", label: "120 deg Bend", defaultBendCount: 1, defaultAngleDeg: 120, angleKey: "120" },
  { shape: "bend_135", label: "135 deg Bend", defaultBendCount: 1, defaultAngleDeg: 135, angleKey: "135" },
  { shape: "u_shape", label: "U Shape / Two Bends", defaultBendCount: 2, defaultAngleDeg: null, angleKey: "custom" },
  { shape: "custom", label: "Custom Angle", defaultBendCount: 1, defaultAngleDeg: null, angleKey: "custom" },
];

const BENDED_TRACK_SHAPE_MAP = Object.fromEntries(
  BENDED_TRACK_SHAPES.map((entry) => [entry.shape, entry])
);

const DEFAULT_BEND_SETTINGS = {
  enabled: false,
  pricingMethod: "none",
  defaultBendCharge: 0,
  angleCharges: {
    90: 0,
    120: 0,
    135: 0,
    custom: 0,
  },
  allowedAngles: ["bend_90", "bend_120", "bend_135", "u_shape", "custom"],
  notes: "",
};

const DEFAULT_TRACK_CONFIG = {
  shape: "straight",
  isBended: false,
  angleDeg: null,
  bendCount: 0,
  bendChargePerBend: 0,
  bendAmount: 0,
  pricingMethod: "none",
  manualBendCharge: null,
  notes: "",
  segmentUnit: "in",
  segments: [
    { label: "A", length: 0, unit: "in" },
    { label: "B", length: 0, unit: "in" },
    { label: "C", length: 0, unit: "in" },
  ],
  totalSegmentLength: 0,
  totalSegmentLengthUnit: "in",
  totalTrackLengthFt: 0,
  bendLocations: [],
  bendLocationSummary: "",
};

const parseStitchingSettingNumber = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

const normalizeBendedTrackShape = (shape = "straight") => {
  const normalizedShape = String(shape || "straight").trim();
  if (BENDED_TRACK_SHAPE_MAP[normalizedShape]) return normalizedShape;
  if (normalizedShape === "90" || normalizedShape === "bend90") return "bend_90";
  if (normalizedShape === "120" || normalizedShape === "bend120") return "bend_120";
  if (normalizedShape === "135" || normalizedShape === "bend135") return "bend_135";
  if (normalizedShape === "u" || normalizedShape === "u-shape") return "u_shape";
  return "straight";
};

const normalizeAllowedBendShapes = (allowedAngles = DEFAULT_BEND_SETTINGS.allowedAngles) => {
  const source = Array.isArray(allowedAngles) ? allowedAngles : DEFAULT_BEND_SETTINGS.allowedAngles;
  const allowed = source
    .map((entry) => {
      if (entry === 90 || entry === "90") return "bend_90";
      if (entry === 120 || entry === "120") return "bend_120";
      if (entry === 135 || entry === "135") return "bend_135";
      if (entry === "u" || entry === "u_shape" || entry === "u-shape") return "u_shape";
      if (entry === "custom") return "custom";
      return normalizeBendedTrackShape(entry);
    })
    .filter((entry) => entry && entry !== "straight");
  return Array.from(new Set(allowed.length ? allowed : DEFAULT_BEND_SETTINGS.allowedAngles));
};

const normalizeBendSettingsForSave = (values = {}) => {
  const bendSource = values?.bendSettings || {};
  const pricingMethod = BENDED_TRACK_PRICING_METHODS.some(
    (entry) => entry.value === bendSource.pricingMethod
  )
    ? bendSource.pricingMethod
    : "none";

  return {
    ...DEFAULT_BEND_SETTINGS,
    ...bendSource,
    enabled: values?.supportsBendedTrack === true || bendSource.enabled === true,
    pricingMethod,
    defaultBendCharge: parseStitchingSettingNumber(bendSource.defaultBendCharge, 0),
    angleCharges: {
      90: parseStitchingSettingNumber(bendSource.angleCharges?.["90"], 0),
      120: parseStitchingSettingNumber(bendSource.angleCharges?.["120"], 0),
      135: parseStitchingSettingNumber(bendSource.angleCharges?.["135"], 0),
      custom: parseStitchingSettingNumber(bendSource.angleCharges?.custom, 0),
    },
    allowedAngles: normalizeAllowedBendShapes(bendSource.allowedAngles),
    notes: String(bendSource.notes || ""),
  };
};


const buildStitchingSettingsForSave = (settings = {}) => {
  const normalized = {};
  const errors = [];

  Object.entries(settings || {}).forEach(([rawName, values = {}]) => {
    const name = String(rawName || "").trim();
    const displayName = name || "Unnamed style";
    const setting = normalizeStitchingSettingForState(values);
    const { rate, fullness, trackLengthFt, trackRateFt } = setting;
    const bendSettings = setting.bendSettings || DEFAULT_BEND_SETTINGS;

    if (!name) errors.push("Style name is required.");
    if (!Number.isFinite(rate) || rate < 0) {
      errors.push(`${displayName}: Rate / Running ft must be 0 or greater.`);
    }
    if (!Number.isFinite(fullness) || fullness <= 0) {
      errors.push(`${displayName}: Fullness Multiplier must be greater than 0.`);
    }
    if (!Number.isFinite(trackLengthFt) || trackLengthFt < 0) {
      errors.push(`${displayName}: Default Track Length must be 0 or greater.`);
    }
    if (!Number.isFinite(trackRateFt) || trackRateFt < 0) {
      errors.push(`${displayName}: Track rate must be 0 or greater.`);
    }
    if (setting.supportsBendedTrack) {
      if (!BENDED_TRACK_PRICING_METHODS.some((entry) => entry.value === bendSettings.pricingMethod)) {
        errors.push(`${displayName}: Bend pricing method is invalid.`);
      }
      if (!Number.isFinite(bendSettings.defaultBendCharge) || bendSettings.defaultBendCharge < 0) {
        errors.push(`${displayName}: Default bend charge must be 0 or greater.`);
      }
      Object.entries(bendSettings.angleCharges || {}).forEach(([angle, charge]) => {
        if (!Number.isFinite(charge) || charge < 0) {
          errors.push(`${displayName}: ${angle} bend charge must be 0 or greater.`);
        }
      });
    }

    if (name) {
      normalized[name] = {
        rate,
        fullness,
        trackType: setting.trackType || "",
        trackLengthFt,
        trackRateFt,
        includesTrack: setting.includesTrack === true,
        supportsBendedTrack: setting.supportsBendedTrack === true,
        bendSettings,
      };
    }
  });

  return { normalized, errors };
};

const normalizeQuoteFullnessOverrideEntry = (entry = {}, fallbackKey = "") => {
  const source = entry && typeof entry === "object" ? entry : { quoteFullness: entry };
  const stableStitchingId = sanitizeEditableText(
    source.stitchingStyleId ||
      source.stitchingConfigId ||
      source.stitchingId ||
      source.stitchId ||
      source.styleId ||
      source.id ||
      ""
  );
  const rawKey =
    stableStitchingId ||
    source.stitchingKey ||
    source.stitchingStyleKey ||
    source.key ||
    fallbackKey ||
    source.stitchingLabel ||
    source.label ||
    "";
  const stitchingKey = normalizeStitchingKey(rawKey);
  if (!stitchingKey) return null;
  const stitchingLabel =
    sanitizeEditableText(
      source.stitchingLabel ||
        source.label ||
        source.name ||
        source.displayName ||
        fallbackKey ||
        rawKey
    ) || rawKey;
  const defaultFullness = Number(source.defaultFullness);
  const quoteFullness = Number(source.quoteFullness);
  const safeQuoteFullness =
    Number.isFinite(quoteFullness) && quoteFullness > 0 && quoteFullness <= 10
      ? quoteFullness
      : null;

  return {
    stitchingKey,
    stitchingStyleId: stableStitchingId || stitchingKey,
    stitchingLabel,
    defaultFullness:
      Number.isFinite(defaultFullness) && defaultFullness > 0
        ? Number(defaultFullness.toFixed(2))
        : null,
    quoteFullness: safeQuoteFullness == null ? null : Number(safeQuoteFullness.toFixed(2)),
    enabled: safeQuoteFullness != null,
    source: safeQuoteFullness != null ? "quote_override" : "default",
  };
};

const normalizeQuoteFullnessOverrides = (overrides = {}) => {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(overrides)
      .map(([key, value]) => normalizeQuoteFullnessOverrideEntry(value, key))
      .filter(Boolean)
      .map((entry) => [entry.stitchingKey, entry])
  );
};

const buildQuoteFullnessOverrideEntry = ({
  stitchingKey = "",
  stitchingStyleId = "",
  stitchingLabel = "",
  defaultFullness = null,
  quoteFullness = null,
} = {}) =>
  normalizeQuoteFullnessOverrideEntry(
    {
      stitchingKey,
      stitchingStyleId,
      stitchingLabel,
      defaultFullness,
      quoteFullness,
    },
    stitchingKey || stitchingLabel
  );

const DEFAULT_MIN_SQFT = {
  "Roman Blind - Normal": 12,
  "Roman Blind - Clip On": 12,
  "Zebra Blind": 10,
  "Roller Blind": 10,
  "Venetian Blind": 10,
  "Vertical Blind": 10,
};

const normalizeFabricCalcSettingsForState = (settings = {}) => ({
  ...(settings && typeof settings === "object" ? settings : {}),
  curtain_top_hem:
    settings?.curtain_top_hem ?? settings?.curtainTopHem ?? 4,
  curtain_bottom_hem:
    settings?.curtain_bottom_hem ?? settings?.curtainBottomHem ?? 4,
  roman_top_hem: settings?.roman_top_hem ?? settings?.romanTopHem ?? 3,
  roman_bottom_hem:
    settings?.roman_bottom_hem ?? settings?.romanBottomHem ?? 4,
  roman_side_hem: settings?.roman_side_hem ?? settings?.romanSideHem ?? 6,
  usable_width_deduction:
    settings?.usable_width_deduction ?? settings?.usableWidthDeduction ?? 4,
  blindSqftRoundingMode: resolveBlindSqftRoundingMode(settings, "exact"),
});

/* --------------------------- CURRENCIES --------------------------- */

const CURRENCY_LIST = [
  "Rs. Indian Rupee",
  "$ US Dollar",
  "CAD Canadian Dollar",
  "AED UAE Dirham",
  "QAR Qatari Riyal",
  "SAR Saudi Riyal",
  "BHD Bahraini Dinar",
  "OMR Omani Rial",
  "KWD Kuwaiti Dinar",
];

const currencySymbol = (cur) => {
  if (!cur) return "";

  const c = cur.toLowerCase();

  if (c.includes("rupee") || c.includes("indian")) return "Rs.";

  if (c.includes("aed")) return "AED ";
  if (c.includes("usd")) return "$";
  if (c.includes("eur")) return "EUR ";
  if (c.includes("gbp") || c.includes("pound")) return "GBP ";

  return "";
};

const isIndianCurrency = (cur) => {
  const c = String(cur || "").toLowerCase();
  return c.includes("rupee") || c.includes("indian") || c.startsWith("rs");
};

const formatCurrencyINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const formatCurrencyDisplay = (value, cur = "Rs. Indian Rupee") => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  if (isIndianCurrency(cur)) return formatCurrencyINR(amount);

  const symbol = currencySymbol(cur);
  return `${symbol ? `${symbol} ` : ""}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
};



// ASCII-safe symbol for PDFs
const symbolFromCurrency = (c) => {
  switch ((c || "").toUpperCase()) {
    case "INR":
      return "Rs.";
    case "USD":
      return "$";
    case "EUR":
      return "EUR";
    case "GBP":
      return "GBP";
    case "AED":
      return "AED";
    case "SAR":
      return "SAR";
    case "OMR":
      return "OMR";
    case "QAR":
      return "QAR";
    case "BHD":
      return "BHD";
    case "KWD":
      return "KWD";
    case "CAD":
      return "CAD";
    default:
      return c || "";
  }
};

const pdfCurrencySymbol = (c) => {
  const raw = String(c || "").trim();
  if (!raw) return "Rs.";

  const lower = raw.toLowerCase();

  if (lower.includes("rupee") || lower.startsWith("rs.")) return "Rs.";
  if (lower.includes("usd") || raw.startsWith("$")) return "$";
  if (lower.includes("eur")) return "EUR";
  if (lower.includes("gbp") || lower.includes("pound")) return "GBP";
  if (lower.includes("aed")) return "AED";
  if (lower.includes("sar")) return "SAR";
  if (lower.includes("omr")) return "OMR";
  if (lower.includes("qar")) return "QAR";
  if (lower.includes("bhd")) return "BHD";
  if (lower.includes("kwd")) return "KWD";
  if (lower.includes("cad")) return "CAD";

  return symbolFromCurrency(raw.toUpperCase());
};

const formatPdfMoney = (value, currencyValue) =>
  `${pdfCurrencySymbol(currencyValue)} ${Number(value || 0).toFixed(2)}`;

/* ====================== UNIT CONVERSION HELPERS ====================== */
/* =========================
   UNIT HELPERS (GLOBAL)
   ========================= */

const INCH_PER_CM = 0.393700787;

/* DISPLAY */

const displayValue = (inches, unit) => {
  if (inches === "" || inches === null || inches === undefined) return "";

  const n = Number(inches);

  if (unit === "cm") {
    return (n / INCH_PER_CM).toFixed(2);
  }

  return n.toFixed(2);
};

const normalizeLengthUnit = (value = "in") => {
  const normalized = String(value || "in").trim().toLowerCase();
  if (["in", "inch", "inches"].includes(normalized)) return "in";
  if (["cm", "centimeter", "centimeters"].includes(normalized)) return "cm";
  if (["mm", "millimeter", "millimeters"].includes(normalized)) return "mm";
  if (["ft", "foot", "feet", "rft", "running ft", "runningft"].includes(normalized)) return "ft";
  if (["m", "meter", "meters", "metre", "metres"].includes(normalized)) return "m";
  return "in";
};

const unitLabel = (unit) => {
  const normalized = normalizeLengthUnit(unit);
  if (normalized === "cm") return "cm";
  if (normalized === "mm") return "mm";
  if (normalized === "ft") return "ft";
  if (normalized === "m") return "m";
  return "in";
};

const hasLegacyTrackSegmentFeet = (trackConfig = {}) =>
  Array.isArray(trackConfig.segments) &&
  trackConfig.segments.some((segment = {}) => {
    const hasRawLength =
      segment.length !== undefined &&
      segment.length !== null &&
      segment.length !== "";
    return (
      !hasRawLength &&
      segment.lengthFt !== undefined &&
      segment.lengthFt !== null &&
      segment.lengthFt !== ""
    );
  });

const getRowMeasurementUnit = (row = {}, quoteUnit = "in") => {
  const trackConfig = row.trackConfig || {};
  return normalizeLengthUnit(
    row.segmentUnit ||
      trackConfig.segmentUnit ||
      trackConfig.totalSegmentLengthUnit ||
      (hasLegacyTrackSegmentFeet(trackConfig) ? "ft" : "") ||
      row.measurementUnit ||
      row.dimensionUnit ||
      row.unit ||
      quoteUnit ||
      "in"
  );
};

const convertLengthToFeet = (value, sourceUnit = "in") => {
  const amount = Math.max(0, num(value));
  const normalizedUnit = normalizeLengthUnit(sourceUnit);
  if (normalizedUnit === "ft") return round2(amount);
  if (normalizedUnit === "cm") return round2(amount / 30.48);
  if (normalizedUnit === "mm") return round2(amount / 304.8);
  if (normalizedUnit === "m") return round2(amount * 3.28084);
  return round2(amount / 12);
};

const formatLengthWithUnit = (value, sourceUnit = "in", decimals = 2) => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${amount.toFixed(decimals)} ${unitLabel(sourceUnit)}`;
};

const formatMeasurementWithUnit = (valueInches, unit, label = "") => {
  const suffix = label ? ` (${label})` : "";
  return `${displayValue(Number(valueInches || 0), unit)} ${unitLabel(unit)}${suffix}`;
};

const formatMetersLabel = (value) => `${Number(value || 0).toFixed(2)} m`;
const CRM_SCOPE_ALL = "all";
const CRM_PIPELINE_REVIEW_TAB = "pipelineReview";
const CRM_PIPELINE_REVIEW_RANGE_PRESETS = [
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "custom", label: "Custom date range" },
];
const CRM_PIPELINE_REVIEW_DEFAULT_FILTERS = {
  dateRangePreset: "this_week",
  startDate: "",
  endDate: "",
  salesperson: "all",
  stage: "all",
  source: "all",
  productInterest: "all",
};
const CRM_WEEKLY_TARGET_DEFAULTS = {
  targetFreshLeads: 0,
  targetQualifiedLeads: 0,
  targetSiteVisits: 0,
  targetQuotations: 0,
  targetFollowUps: 0,
  targetOrders: 0,
  targetOrderValue: 0,
};

const getLocalDateKeyFromDate = (dateInput = new Date()) => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const parseLocalDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = String(dateKey)
    .split("-")
    .map((part) => Number(part));
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDaysToDateKey = (dateKey, dayCount) => {
  const parsed = parseLocalDateKey(dateKey);
  if (!parsed) return "";
  parsed.setDate(parsed.getDate() + Number(dayCount || 0));
  return getLocalDateKeyFromDate(parsed);
};

const getStartOfWeekDate = (dateInput = new Date()) => {
  const date = dateInput instanceof Date ? new Date(dateInput.getTime()) : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return new Date();
  const day = date.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + mondayOffset);
  date.setHours(12, 0, 0, 0);
  return date;
};

const getCrmPipelineReviewRange = (preset = "this_week") => {
  const now = new Date();
  if (preset === "last_week") {
    const start = getStartOfWeekDate(now);
    start.setDate(start.getDate() - 7);
    const startDate = getLocalDateKeyFromDate(start);
    return {
      preset,
      startDate,
      endDate: addDaysToDateKey(startDate, 6),
      weekStart: startDate,
      weekEnd: addDaysToDateKey(startDate, 6),
    };
  }
  if (preset === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 12, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 12, 0, 0, 0);
    const weekStartDate = getStartOfWeekDate(now);
    return {
      preset,
      startDate: getLocalDateKeyFromDate(start),
      endDate: getLocalDateKeyFromDate(end),
      weekStart: getLocalDateKeyFromDate(weekStartDate),
      weekEnd: addDaysToDateKey(getLocalDateKeyFromDate(weekStartDate), 6),
    };
  }
  const start = getStartOfWeekDate(now);
  const startDate = getLocalDateKeyFromDate(start);
  return {
    preset: "this_week",
    startDate,
    endDate: addDaysToDateKey(startDate, 6),
    weekStart: startDate,
    weekEnd: addDaysToDateKey(startDate, 6),
  };
};

const formatPipelineDateLabel = (dateKey) => {
  const parsed = parseLocalDateKey(dateKey);
  if (!parsed) return "Not set";
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getPipelineReviewTargetId = (weekStart, userId) =>
  `${String(weekStart || "").trim()}_${String(userId || "unassigned").trim() || "unassigned"}`;

const normalizePipelineTargetNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeWeeklyTargetRecord = (target = {}) => ({
  ...CRM_WEEKLY_TARGET_DEFAULTS,
  ...Object.keys(CRM_WEEKLY_TARGET_DEFAULTS).reduce((accumulator, key) => {
    accumulator[key] = normalizePipelineTargetNumber(target[key]);
    return accumulator;
  }, {}),
  targetId: String(target.targetId || target.id || "").trim(),
  workspaceUid: String(target.workspaceUid || "").trim(),
  weekStart: String(target.weekStart || "").trim(),
  weekEnd: String(target.weekEnd || "").trim(),
  userId: String(target.userId || "unassigned").trim() || "unassigned",
  userName: String(target.userName || "Unassigned").trim() || "Unassigned",
  createdAt: target.createdAt || null,
  updatedAt: target.updatedAt || null,
});

const getCrmRecordDateMs = (record = {}, fields = []) => {
  for (const field of fields) {
    const value = record?.[field];
    const millis = safeTimeMillis(value);
    if (millis) return millis;
  }
  return 0;
};

const isDateMsInRange = (millis, startMs, endMs) =>
  Boolean(millis && millis >= startMs && millis <= endMs);

const getCrmPipelineOwnerKey = (record = {}) =>
  String(
    record.ownerUid ||
      record.assignedToUid ||
      record.assignedToUserId ||
      record.createdBy ||
      record.createdByUid ||
      record.uid ||
      "unassigned"
  ).trim() || "unassigned";

const CRM_OWNER_ADMIN_ROLES = new Set(["owner", "admin", "super_admin", "superadmin"]);
const CRM_MANAGER_ROLES = new Set(["manager"]);
const CRM_SALES_ROLES = new Set(["sales", "salesperson", "sales_person"]);
const CRM_ACCOUNTS_ROLES = new Set(["accounts", "account", "accountant", "finance"]);
const CRM_PROJECT_COORDINATOR_ROLES = new Set([
  "project_coordinator",
  "project coordinator",
  "coordinator",
  "operations",
  "installer",
  "installation",
]);

const normalizeCrmAccessRole = (role = "") =>
  String(role || "").trim().toLowerCase().replace(/-/g, "_");

const crmRoleIn = (role = "", roleSet) =>
  roleSet.has(normalizeCrmAccessRole(role)) ||
  roleSet.has(String(role || "").trim().toLowerCase());

const hasCrmAccessPermission = (accessContext = {}, keys = []) => {
  const permissions = {
    ...(accessContext.permissions || {}),
    ...(accessContext.rawPermissions || {}),
  };
  return keys.some((key) => permissions?.[key] === true);
};

const isCrmOwnerAdmin = (accessContext = {}) =>
  Boolean(accessContext.isSuperAdmin || accessContext.isWorkspaceOwner) ||
  crmRoleIn(accessContext.role, CRM_OWNER_ADMIN_ROLES);

const isCrmManager = (accessContext = {}) =>
  isCrmOwnerAdmin(accessContext) || crmRoleIn(accessContext.role, CRM_MANAGER_ROLES);

const isCrmAccountsRole = (accessContext = {}) =>
  crmRoleIn(accessContext.role, CRM_ACCOUNTS_ROLES);

const isCrmProjectCoordinatorRole = (accessContext = {}) =>
  crmRoleIn(accessContext.role, CRM_PROJECT_COORDINATOR_ROLES);

const isCrmSalesRole = (accessContext = {}) =>
  crmRoleIn(accessContext.role, CRM_SALES_ROLES);

const canViewCrmToday = (accessContext = {}) => accessContext.canAccessCrm === true;

const canViewManagerCrmDashboardForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    hasCrmAccessPermission(accessContext, ["dashboard", "crmDashboard", "managerDashboard", "manageCrm", "crmManage"]));

const canViewAttentionRequiredWorkspaceForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    hasCrmAccessPermission(accessContext, ["attentionRequired", "crmAttention", "manageCrm", "crmManage"]));

const canViewPipelineReviewForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    hasCrmAccessPermission(accessContext, ["pipelineReview", "weeklyTargets", "manageCrm", "crmManage"]));

const canManageCrmSettingsForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmOwnerAdmin(accessContext) ||
    accessContext.canAccessSettingsTab === true ||
    hasCrmAccessPermission(accessContext, ["settings", "crmSettings", "manageCrm", "crmManage"]));

const canViewAllCrmRecordsForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    hasCrmAccessPermission(accessContext, ["manageCrm", "crmManage"]));

const canViewOwnCrmRecordsForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true;

const canAssignLeadOwnerForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    hasCrmAccessPermission(accessContext, ["assignLeadOwner", "manageCrm", "crmManage"]));

const canEditCrmTargetsForAccess = (accessContext = {}) =>
  canViewPipelineReviewForAccess(accessContext);

const canViewCrmPaymentsForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmOwnerAdmin(accessContext) ||
    isCrmManager(accessContext) ||
    isCrmAccountsRole(accessContext) ||
    hasCrmAccessPermission(accessContext, ["manageCrm", "crmManage", "payments", "crmPayments"]));

const canAddCrmPaymentForAccess = (accessContext = {}) =>
  canViewCrmPaymentsForAccess(accessContext);

const canScheduleProjectInstallationForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    isCrmProjectCoordinatorRole(accessContext) ||
    hasCrmAccessPermission(accessContext, ["projectExecution", "installation", "manageCrm", "crmManage"]));

const canCloseCrmProjectForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    isCrmProjectCoordinatorRole(accessContext) ||
    hasCrmAccessPermission(accessContext, ["projectExecution", "closeProject", "manageCrm", "crmManage"]));

const canRequestProjectReviewForAccess = (accessContext = {}) =>
  accessContext.canAccessCrm === true &&
  (isCrmManager(accessContext) ||
    isCrmSalesRole(accessContext) ||
    isCrmProjectCoordinatorRole(accessContext) ||
    hasCrmAccessPermission(accessContext, ["projectExecution", "requestReview", "manageCrm", "crmManage"]));

const classifyCrmPipelineReviewError = (error = {}) => {
  const errorCode = String(error?.code || "").trim();
  const errorMessage = String(error?.message || error || "").trim();
  const normalizedMessage = errorMessage.toLowerCase();
  const indexLinkFound =
    normalizedMessage.includes("create_composite") ||
    normalizedMessage.includes("firestore/indexes") ||
    normalizedMessage.includes("console.firebase.google.com");

  if (errorCode === "permission-denied") {
    return {
      classifiedAs: "permission_denied",
      indexLinkFound,
      friendlyMessage: "You do not have permission to view CRM reporting data.",
    };
  }
  if (errorCode === "unavailable" || normalizedMessage.includes("network")) {
    return {
      classifiedAs: "network_unavailable",
      indexLinkFound,
      friendlyMessage: "CRM reporting is temporarily unavailable. Please try again.",
    };
  }
  if (
    errorCode === "failed-precondition" &&
    normalizedMessage.includes("index") &&
    (normalizedMessage.includes("building") ||
      normalizedMessage.includes("currently building") ||
      normalizedMessage.includes("not ready"))
  ) {
    return {
      classifiedAs: "index_building",
      indexLinkFound,
      friendlyMessage:
        "Firebase is preparing the CRM reporting index. This usually clears after a few minutes.",
    };
  }
  if (
    errorCode === "failed-precondition" &&
    (normalizedMessage.includes("index") ||
      normalizedMessage.includes("create") ||
      indexLinkFound)
  ) {
    return {
      classifiedAs: "index_required",
      indexLinkFound,
      friendlyMessage:
        "CRM reporting needs a Firestore index before this view can load. Please try again after indexes finish building.",
    };
  }
  return {
    classifiedAs: "unknown_error",
    indexLinkFound,
    friendlyMessage: "Could not load Pipeline Review. Please try again.",
  };
};



/* --------------------------- HELPERS --------------------------- */



const normalizeDraftText = (value) => String(value ?? "").trim();
const hasMeaningfulDraftText = (...values) =>
  values.some((value) => {
    const text = normalizeDraftText(value);
    return text && !["-", "--", "—", "n/a", "na", "none", "null", "undefined"].includes(text.toLowerCase());
  });
const hasMeaningfulDraftNumber = (...values) =>
  values.some((value) => Math.abs(num(value)) > 0.0001);
const hasMeaningfulDraftArray = (...values) =>
  values.some((value) => Array.isArray(value) && value.length > 0);
const hasMeaningfulDraftObject = (...values) =>
  values.some(
    (value) =>
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0
  );
const isNonNewDraftId = (value) => {
  const text = normalizeDraftText(value);
  return text && text.toLowerCase() !== "new" && !text.toLowerCase().startsWith("draft");
};
const isDefaultCurtainProductText = (value) => {
  const text = normalizeDraftText(value).toLowerCase();
  return !text || text === "curtain" || text === "curtains";
};
const hasMeaningfulCurtainDraftRow = (row = {}, index = 0) => {
  if (!row || typeof row !== "object") return false;

  const lineType = normalizeDraftText(row.lineType || row.type).toLowerCase();
  if (lineType === "accessory") return true;

  if (
    hasMeaningfulDraftText(
      row.fabric,
      row.fabricName,
      row.fabricCode,
      row.fabricCodeRaw,
      row.material,
      row.materialName,
      row.materialCode,
      row.selectedFabric,
      row.srNumber,
      row.srLabel,
      row.brandName,
      row.catalogueName,
      row.notes,
      row.description
    )
  ) {
    return true;
  }

  if (
    hasMeaningfulDraftNumber(
      row.width,
      row.height,
      row.widthIn,
      row.heightIn,
      row.widthCm,
      row.heightCm,
      row.length,
      row.quantity,
      row.qty,
      row.mrp,
      row.rate,
      row.sqftRate,
      row.customStitchRate,
      row.trackRate,
      row.total,
      row.amount
    )
  ) {
    return true;
  }

  if (
    hasMeaningfulDraftArray(
      row.images,
      row.photos,
      row.attachments,
      row.referenceImages,
      row.referencePhotos
    )
  ) {
    return true;
  }

  if (row.lining && typeof row.lining === "object" && row.lining.enabled === true) {
    return true;
  }

  const itemText = normalizeDraftText(row.item || row.name || row.room || row.itemName);
  if (itemText && !(index === 0 && itemText.toLowerCase() === "w1")) {
    return true;
  }

  const productText = normalizeDraftText(row.productName || row.product || row.productType);
  return Boolean(productText && !isDefaultCurtainProductText(productText));
};
const isMeaningfulQuotationDraft = (draftRecord = {}, module = "") => {
  const draft = draftRecord?.data && typeof draftRecord.data === "object"
    ? draftRecord.data
    : draftRecord || {};

  if (!draft || typeof draft !== "object") return false;

  if (
    hasMeaningfulDraftText(
      draft.customerName,
      draft.customerPhone,
      draft.customerWhatsapp,
      draft.customerEmail,
      draft.customerAddress,
      draft.customerCompanyName,
      draft.quoteCustomerLink?.customerName,
      draft.quoteCustomerLink?.phone,
      draft.quoteCustomerLink?.whatsapp,
      draft.quoteCustomerLink?.email,
      draft.quoteCustomerLink?.address,
      draft.quoteCustomerLink?.companyName,
      draft.quoteSiteSelection?.siteName,
      draft.quoteSiteSelection?.siteLabel,
      draft.quoteSiteSelection?.addressText,
      draft.siteName,
      draft.siteAddress,
      draft.projectName,
      draft.notes,
      draft.internalNotes
    )
  ) {
    return true;
  }

  if (
    isNonNewDraftId(draft.quoteId) ||
    isNonNewDraftId(draft.editingQuoteId) ||
    isNonNewDraftId(draft.sourceQuoteId) ||
    hasMeaningfulDraftText(draft.quoteNo)
  ) {
    return true;
  }

  if (
    hasMeaningfulDraftNumber(
      draft.grandTotal,
      draft.finalTotal,
      draft.totalAmount,
      draft.subtotal,
      draft.taxableAmount,
      draft.discountValue,
      draft.summaryDiscountValue,
      draft.gstAmount,
      draft.quoteGstRate,
      draft.gstRate
    )
  ) {
    return true;
  }

  if (
    hasMeaningfulDraftArray(
      draft.images,
      draft.photos,
      draft.attachments,
      draft.referenceImages,
      draft.referencePhotos,
      draft.accessories
    )
  ) {
    return true;
  }

  if (
    hasMeaningfulDraftObject(
      draft.repeatSettings,
      draft.quoteFullnessOverrides,
      draft.fullnessOverrides,
      draft.quoteTrackRateOverrides,
      draft.fabricHemOverrides
    )
  ) {
    return true;
  }

  if (module === LOCAL_DRAFT_MODULES.CURTAIN_QUOTE) {
    const rows = Array.isArray(draft.rows) ? draft.rows : [];
    if (rows.length > 1) return true;
    return rows.some((row, index) => hasMeaningfulCurtainDraftRow(row, index));
  }

  if (module === LOCAL_DRAFT_MODULES.GENERAL_QUOTE) {
    const items = [
      ...(Array.isArray(draft.items) ? draft.items : []),
      ...(Array.isArray(draft.lineItems) ? draft.lineItems : []),
      ...(Array.isArray(draft.rows) ? draft.rows : []),
    ];
    return items.some((item) =>
      hasMeaningfulDraftText(
        item?.itemName,
        item?.name,
        item?.description,
        item?.product,
        item?.notes
      ) ||
      hasMeaningfulDraftNumber(
        item?.quantity,
        item?.qty,
        item?.rate,
        item?.amount,
        item?.total
      ) ||
      hasMeaningfulDraftArray(item?.images, item?.attachments, item?.referenceImages)
    );
  }

  return false;
};
const getDraftRecoveryScopeDecision = ({
  module = "",
  draftRecord = null,
  currentRoute = "",
  activeTab = "",
  activeQuoteSubTab = "",
} = {}) => {
  const hasDraft = Boolean(draftRecord);
  const quoteDraft = isQuotationDraftModule(module);
  const isMeaningful = quoteDraft
    ? isMeaningfulQuotationDraft(draftRecord, module)
    : hasDraft;

  if (!hasDraft) {
    return { hasDraft, isMeaningful, modalAllowed: false, reason: "no_draft" };
  }

  if (quoteDraft && !isMeaningful) {
    return {
      hasDraft,
      isMeaningful,
      modalAllowed: false,
      reason: "empty_default_quotation_draft",
      clearDraft: true,
    };
  }

  if (quoteDraft) {
    const modalAllowed = isQuotationDraftRecoveryAllowed({
      currentRoute,
      activeModule: module,
      activeTab,
      activeQuoteSubTab,
    });
    return {
      hasDraft,
      isMeaningful,
      modalAllowed,
      reason: modalAllowed ? "quotation_route_allowed" : "outside_quotation_route",
    };
  }

  if (module === LOCAL_DRAFT_MODULES.CRM_LEAD) {
    return {
      hasDraft,
      isMeaningful,
      modalAllowed: false,
      reason: "crm_lead_draft_recovery_modal_disabled",
    };
  }

  if (module === LOCAL_DRAFT_MODULES.EXECUTION_DETAILS) {
    const modalAllowed = activeTab === "executionSpecs";
    return {
      hasDraft,
      isMeaningful,
      modalAllowed,
      reason: modalAllowed ? "execution_details_route_allowed" : "outside_execution_details_route",
    };
  }

  if (module === LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE) {
    const modalAllowed = activeTab === "executionTemplates";
    return {
      hasDraft,
      isMeaningful,
      modalAllowed,
      reason: modalAllowed ? "execution_templates_route_allowed" : "outside_execution_templates_route",
    };
  }

  return { hasDraft, isMeaningful, modalAllowed: false, reason: "unsupported_draft_module" };
};

const roundMetersForDisplay = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

const METER_DISPLAY_DRIFT_TOLERANCE = 0.011;

const normalizeFabricSummaryMeterDisplay = ({
  fabricToOrderRaw = 0,
  customerBillableRaw = 0,
  halfPieceSavingRaw = 0,
} = {}) => {
  const safeFabricToOrderRaw = Number.isFinite(Number(fabricToOrderRaw))
    ? Number(fabricToOrderRaw)
    : 0;
  const safeCustomerBillableRaw = Number.isFinite(Number(customerBillableRaw))
    ? Number(customerBillableRaw)
    : 0;
  const safeHalfPieceSavingRaw = Number.isFinite(Number(halfPieceSavingRaw))
    ? Number(halfPieceSavingRaw)
    : 0;
  const fabricToOrderDisplay = roundMetersForDisplay(safeFabricToOrderRaw);
  const customerBillableDisplay = roundMetersForDisplay(safeCustomerBillableRaw);
  const halfPieceSavingDisplay = roundMetersForDisplay(safeHalfPieceSavingRaw);
  const displayDelta = roundMetersForDisplay(
    fabricToOrderDisplay - customerBillableDisplay
  );
  const rawDelta = safeFabricToOrderRaw - safeCustomerBillableRaw;
  const shouldNormalizeDisplayDrift =
    halfPieceSavingDisplay === 0 &&
    Math.abs(displayDelta) > 0 &&
    Math.abs(displayDelta) <= METER_DISPLAY_DRIFT_TOLERANCE &&
    Math.abs(rawDelta) <= METER_DISPLAY_DRIFT_TOLERANCE;

  return {
    fabricToOrderMetersRaw: safeFabricToOrderRaw,
    customerBillableFabricMetersRaw: safeCustomerBillableRaw,
    halfPieceSavingMetersRaw: safeHalfPieceSavingRaw,
    fabricToOrderMeters: shouldNormalizeDisplayDrift
      ? customerBillableDisplay
      : fabricToOrderDisplay,
    customerBillableFabricMeters: customerBillableDisplay,
    halfPieceSavingMeters: halfPieceSavingDisplay,
    roundingDriftNormalized: shouldNormalizeDisplayDrift,
    roundingDriftMeters: displayDelta,
  };
};

const LEGACY_CALCULATION_IMPACT = {
  NO_IMPACT: "no_impact",
  FINANCIAL_DIFFERENCE: "financial_difference",
  PRODUCTION_DIFFERENCE: "production_difference",
  UNKNOWN_REQUIRES_REVIEW: "unknown_requires_review",
};

const LEGACY_CALCULATION_VERSION_STATUS = {
  CURRENT: "current",
  OLDER_VERSION: "older_version",
  MISSING_VERSION: "missing_version",
  MISSING_SNAPSHOT: "missing_snapshot",
};

const rowIsAffectedByLegacyCurtainFormulaStatic = (row = {}, productConfig = null) => {
  const calculationType = String(
    productConfig?.calculationType || row?.calculationType || ""
  )
    .trim()
    .toLowerCase();
  const category = String(productConfig?.category || row?.category || "")
    .trim()
    .toLowerCase();
  const normalizedProduct = normalizeProductName(
    row.productName ||
      row.productType ||
      row.product ||
      row.calculationSnapshot?.productName ||
      productConfig?.name ||
      ""
  );

  if (isAccessoryCalculationType(calculationType)) return false;

  return Boolean(
    productConfig?.usesFabric === true ||
      row?.usesFabric === true ||
      calculationType === "fabric_meter" ||
      calculationType === "sqft_plus_fabric" ||
      category === "curtain" ||
      category === "roman_blind" ||
      category === "roman blind" ||
      normalizedProduct === "curtains" ||
      normalizedProduct.startsWith("roman")
  );
};

const hasCurrencyDifference = (a, b, tolerance = 0.01) =>
  Math.abs(round2(Number(a || 0) - Number(b || 0))) > tolerance;

const deriveLegacyRecalculationImpact = (preview = null) => {
  if (!preview) {
    return {
      recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
      actualChangedRows: 0,
      totalDifference: 0,
      hasFinancialDifference: false,
      hasProductionDifference: false,
      hasUnknownDifference: false,
      shouldShowBanner: false,
      reason: "no_preview",
    };
  }

  const changedRows = Array.isArray(preview.changedRows) ? preview.changedRows : [];
  const rowDiffRows = Array.isArray(preview.rowDiffRows) ? preview.rowDiffRows : [];
  const subtotalDifference = round2(Number(preview.difference?.subtotal || 0));
  const gstDifference = round2(Number(preview.difference?.gst || 0));
  const grandTotalDifference = round2(Number(preview.difference?.grandTotal || 0));
  const hasFinancialDifference =
    hasCurrencyDifference(preview.oldTotals?.subtotalBeforeGst, preview.newTotals?.subtotalBeforeGst) ||
    hasCurrencyDifference(
      preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount,
      preview.newTotals?.totalGstAmount ?? preview.newTotals?.gstAmount
    ) ||
    hasCurrencyDifference(preview.oldTotals?.grandTotal, preview.newTotals?.grandTotal) ||
    changedRows.some((row) => Math.abs(Number(row?.difference || 0)) > 0.01) ||
    rowDiffRows.some(
      (row) =>
        row?.type === "money" &&
        row?.oldExists === true &&
        Math.abs(Number(row?.difference || 0)) > 0.01
    );
  const hasProductionDifference = rowDiffRows.some((row) => {
    const field = String(row?.field || "").toLowerCase();
    if (field.includes("row total") || field.includes("fabric amount")) return false;
    return Math.abs(Number(row?.difference || 0)) > 0.01;
  });
  const hasUnknownDifference = rowDiffRows.some(
    (row) => row?.oldExists === false && Math.abs(Number(row?.newValue || 0)) > 0.01
  );
  const recalculationImpact = hasFinancialDifference
    ? LEGACY_CALCULATION_IMPACT.FINANCIAL_DIFFERENCE
    : hasProductionDifference
    ? LEGACY_CALCULATION_IMPACT.PRODUCTION_DIFFERENCE
    : hasUnknownDifference
    ? LEGACY_CALCULATION_IMPACT.UNKNOWN_REQUIRES_REVIEW
    : LEGACY_CALCULATION_IMPACT.NO_IMPACT;

  return {
    recalculationImpact,
    actualChangedRows: changedRows.length,
    totalDifference: grandTotalDifference,
    subtotalDifference,
    gstDifference,
    grandTotalDifference,
    hasFinancialDifference,
    hasProductionDifference,
    hasUnknownDifference,
    shouldShowBanner: recalculationImpact !== LEGACY_CALCULATION_IMPACT.NO_IMPACT,
    reason:
      recalculationImpact === LEGACY_CALCULATION_IMPACT.NO_IMPACT
        ? "saved_and_recalculated_values_match"
        : recalculationImpact,
  };
};

const BEND_LOCATION_OPTIONS = [
  {
    value: "after_a",
    label: "After Segment A / between A and B",
    locationType: "between_segments",
    afterSegment: "A",
    betweenSegments: ["A", "B"],
    side: null,
  },
  {
    value: "between_b_c",
    label: "Between Segment B and C",
    locationType: "between_segments",
    afterSegment: "B",
    betweenSegments: ["B", "C"],
    side: null,
  },
  {
    value: "left_side",
    label: "Left side bend",
    locationType: "left_side",
    afterSegment: "",
    betweenSegments: [],
    side: "left",
  },
  {
    value: "right_side",
    label: "Right side bend",
    locationType: "right_side",
    afterSegment: "",
    betweenSegments: [],
    side: "right",
  },
  {
    value: "center",
    label: "Center bend",
    locationType: "center",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
  {
    value: "custom",
    label: "Custom note",
    locationType: "custom",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
];

const getBendLocationOption = (location = {}) => {
  if (location.locationType === "left_side") return "left_side";
  if (location.locationType === "right_side") return "right_side";
  if (location.locationType === "center") return "center";
  if (location.locationType === "custom") return "custom";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.join("-")
    : "";
  if (between === "B-C") return "between_b_c";
  return "after_a";
};

const buildBendLocationFromOption = (
  optionValue = "after_a",
  bendNo = 1,
  angleDeg = null,
  distanceUnit = "in",
  previous = {}
) => {
  const option =
    BEND_LOCATION_OPTIONS.find((entry) => entry.value === optionValue) ||
    BEND_LOCATION_OPTIONS[0];
  return {
    bendNo,
    angleDeg: angleDeg == null ? null : Number(angleDeg),
    locationType: option.locationType,
    afterSegment: option.afterSegment,
    betweenSegments: [...option.betweenSegments],
    side: option.side,
    distanceFromStart: previous.distanceFromStart ?? null,
    distanceUnit: normalizeLengthUnit(previous.distanceUnit || distanceUnit),
    note: previous.note || "",
  };
};

const getDefaultBendLocationOption = (shape = "straight", bendNo = 1) => {
  const normalizedShape = normalizeBendedTrackShape(shape);
  if (normalizedShape === "u_shape" && Number(bendNo) === 2) return "between_b_c";
  return "after_a";
};

const normalizeBendLocations = (
  bendLocations = [],
  { shape = "straight", bendCount = 0, angleDeg = null, unit = "in" } = {}
) => {
  const source = Array.isArray(bendLocations) ? bendLocations : [];
  const count = Math.max(0, Math.round(num(bendCount)));
  return Array.from({ length: count }).map((_, index) => {
    const bendNo = index + 1;
    const previous = source[index] || {};
    const option = previous.locationType
      ? getBendLocationOption(previous)
      : getDefaultBendLocationOption(shape, bendNo);
    return {
      ...buildBendLocationFromOption(option, bendNo, previous.angleDeg ?? angleDeg, unit, previous),
      angleDeg:
        previous.angleDeg == null || previous.angleDeg === ""
          ? angleDeg == null
            ? null
            : Number(angleDeg)
          : Number(previous.angleDeg),
    };
  });
};

const summarizeBendLocation = (location = {}) => {
  if (location.locationType === "left_side") return "Left side bend";
  if (location.locationType === "right_side") return "Right side bend";
  if (location.locationType === "center") return "Center bend";
  if (location.locationType === "custom") return location.note || "Custom bend location";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.filter(Boolean)
    : [];
  if (between.length >= 2) {
    return `After Segment ${between[0]} / between ${between[0]} and ${between[1]}`;
  }
  if (location.afterSegment) return `After Segment ${location.afterSegment}`;
  return "";
};

const summarizeBendLocations = (locations = []) =>
  (Array.isArray(locations) ? locations : [])
    .map(summarizeBendLocation)
    .filter(Boolean)
    .join("; ");

const normalizeTrackSegments = (segments = [], defaultUnit = "in") => {
  const source = Array.isArray(segments) ? segments : [];
  const fallbackUnit = normalizeLengthUnit(defaultUnit);
  return ["A", "B", "C"].map((label, index) => {
    const sourceSegment = source[index] || {};
    const hasRawLength =
      sourceSegment.length !== undefined &&
      sourceSegment.length !== null &&
      sourceSegment.length !== "";
    const segmentUnit = hasRawLength
      ? normalizeLengthUnit(sourceSegment.unit || sourceSegment.segmentUnit || fallbackUnit)
      : sourceSegment.lengthFt !== undefined && sourceSegment.lengthFt !== null
      ? "ft"
      : fallbackUnit;
    const length = Math.max(
      0,
      num(hasRawLength ? sourceSegment.length : sourceSegment.lengthFt ?? 0)
    );

    return {
      label: sourceSegment.label || label,
      length,
      unit: segmentUnit,
      lengthFt: convertLengthToFeet(length, segmentUnit),
    };
  });
};

const getTrackSegmentEntries = (trackConfig = {}) =>
  normalizeTrackSegments(
    trackConfig.segments,
    trackConfig.segmentUnit || trackConfig.totalSegmentLengthUnit || "in"
  ).filter((segment) => Number(segment.length || 0) > 0);

const formatTrackSegmentsInline = (trackConfig = {}) =>
  getTrackSegmentEntries(trackConfig)
    .map((segment) =>
      `${segment.label || ""} ${formatLengthWithUnit(
        segment.length || 0,
        segment.unit || trackConfig.segmentUnit || "in"
      )}`.trim()
    )
    .join(" + ");

const buildBendedTrackLayoutPreview = (trackConfig = {}) => {
  const segments = getTrackSegmentEntries(trackConfig);
  if (!segments.length) return "";
  const locations = Array.isArray(trackConfig.bendLocations)
    ? trackConfig.bendLocations
    : [];
  return segments
    .map((segment, index) => {
      const part = `${segment.label}: ${formatLengthWithUnit(
        segment.length,
        segment.unit || trackConfig.segmentUnit
      )}`;
      const bend = locations[index];
      if (!bend) return part;
      const angle = bend.angleDeg || trackConfig.angleDeg;
      return `${part} -> Bend${angle ? ` ${Number(angle).toFixed(0)} deg` : ""} ->`;
    })
    .join(" ")
    .replace(/ ->$/, "");
};

const getBendedTrackShapeMeta = (shape = "straight") =>
  BENDED_TRACK_SHAPE_MAP[normalizeBendedTrackShape(shape)] || BENDED_TRACK_SHAPE_MAP.straight;



const isDevelopmentMode = () =>
  IS_DEV_UI_ENABLED;



const normalizeCurtainGstRateCandidate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") {
    const nestedCandidates = [
      value.quoteGstRate,
      value.gstRate,
      value.gstPercentage,
      value.gstPercent,
      value.selectedGst,
      value.taxRate,
      value.rate,
      value.percent,
      value.percentage,
      value.value,
    ];
    for (const nestedCandidate of nestedCandidates) {
      const normalizedNested = normalizeCurtainGstRateCandidate(nestedCandidate);
      if (normalizedNested !== null) return normalizedNested;
    }
    return null;
  }
  const normalizedString = String(value).trim().replace(/%$/, "");
  if (!normalizedString) return null;
  const numericRate = Number(normalizedString);
  if (!Number.isFinite(numericRate)) return null;
  return CURTAIN_GST_RATE_OPTIONS.includes(numericRate) ? numericRate : null;
};


const resolveCurtainQuoteGstMode = (quoteLike = {}) =>
  normalizeCurtainGstMode(
    quoteLike.quoteGstMode ??
      quoteLike.gstMode ??
      quoteLike.quoteSettings?.quoteGstMode ??
      quoteLike.quoteSettings?.gstMode ??
      quoteLike.gstBreakdown?.quoteGstMode ??
      quoteLike.gstBreakdown?.mode
  );

const resolveCurtainQuoteGstRate = (quoteLike = {}) => {
  const quoteSettings = quoteLike.quoteSettings || {};
  const settings = quoteLike.settings || {};
  const snapshotTotals = quoteLike.calculationSnapshot?.totals || {};
  const snapshotRoot = quoteLike.calculationSnapshot || {};
  const gstBreakdown = quoteLike.gstBreakdown || quoteSettings.gstBreakdown || {};
  const namedRateCandidates = [
    quoteLike.quoteGstRate,
    quoteLike.gstRate,
    quoteSettings.quoteGstRate,
    quoteSettings.gstRate,
    settings.quoteGstRate,
    settings.gstRate,
    snapshotTotals.quoteGstRate,
    snapshotTotals.gstRate,
    snapshotRoot.quoteGstRate,
    snapshotRoot.gstRate,
    gstBreakdown.flatRate,
    gstBreakdown.measurementGstRate,
    gstBreakdown.quoteGstRate,
    quoteLike.gstPercentage,
    quoteLike.gstPercent,
    quoteLike.selectedGst,
    quoteLike.taxRate,
    quoteSettings.gstPercentage,
    quoteSettings.gstPercent,
    quoteSettings.selectedGst,
    quoteSettings.taxRate,
    settings.gstPercentage,
    settings.gstPercent,
    settings.selectedGst,
    settings.taxRate,
    snapshotTotals.gstPercentage,
    snapshotTotals.gstPercent,
    snapshotTotals.selectedGst,
    snapshotTotals.taxRate,
  ];

  for (const candidate of namedRateCandidates) {
    const normalizedRate = normalizeCurtainGstRateCandidate(candidate);
    if (normalizedRate !== null) return normalizedRate;
  }

  const ambiguousLegacyCandidates = [
    quoteLike.gst,
    quoteLike.tax,
    quoteSettings.gst,
    quoteSettings.tax,
    settings.gst,
    settings.tax,
    snapshotTotals.gst,
    snapshotTotals.tax,
  ];

  for (const candidate of ambiguousLegacyCandidates) {
    const normalizedRate = normalizeCurtainGstRateCandidate(candidate);
    if (normalizedRate !== null) return normalizedRate;
  }

  return 0;
};


const getCurtainGstSelectValue = (quoteGstMode = CURTAIN_GST_MODE_FLAT, quoteGstRate = 0) =>
  normalizeCurtainGstMode(quoteGstMode) === CURTAIN_GST_MODE_FABRIC_5_OTHER_18
    ? CURTAIN_GST_MODE_FABRIC_5_OTHER_18
    : String(normalizeCurtainGstRate(quoteGstRate));

const isMixedCurtainGstMode = (quoteGstMode) =>
  normalizeCurtainGstMode(quoteGstMode) === CURTAIN_GST_MODE_FABRIC_5_OTHER_18;




const getSummaryDiscountModeFromType = (discountType) =>
  normalizeGeneralDiscountType(discountType) === "percent" ? "%" : "";

const getDiscountTypeFromSummaryMode = (summaryDiscountMode) =>
  summaryDiscountMode === "%" ? "percent" : "flat";

const normalizeQuoteDiscount = (quoteLike = {}, source = "quote") => {
  const normalized = normalizeGeneralDiscount(quoteLike, source);

  if (isDevelopmentMode()) {
    devLog("QUOTE DISCOUNT SAVE/LOAD", {
      source,
      discountType: normalized.discountType,
      discountValue: normalized.discountValue,
    });
  }

  return normalized;
};







const safeFabricKey = (fab) =>
  (fab || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_") || "__no_fabric__";



/* -----------------------Firestore Timestamp  input and text-----------------------------------------*/

const GENERAL_IMAGE_TTL_MS =
  GENERAL_QUOTE_IMAGE_TTL_DAYS * 24 * 60 * 60 * 1000;

const safeTimeMillis = (value) => {
  if (!value || isFirestoreSentinel(value)) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !isNaN(dateValue.getTime())
      ? dateValue.getTime()
      : 0;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? value.getTime() : 0;
  }

  if (typeof value === "object") {
    const seconds = Number(value.seconds ?? value._seconds);
    if (Number.isFinite(seconds)) {
      return seconds * 1000;
    }
  }

  const parsed = new Date(value);
  return !isNaN(parsed.getTime()) ? parsed.getTime() : 0;
};

const formatSafeDate = (value, fallback = "No recent activity") => {
  const millis = safeTimeMillis(value);
  if (!millis) return fallback;
  try {
    return new Date(millis).toLocaleDateString("en-IN");
  } catch {
    return fallback;
  }
};

const normalizeFileName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "item-image";

const GENERAL_QUOTE_IMAGE_STORAGE_ROOT = "generalQuotationImages";
const GENERAL_QUOTE_ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const GENERAL_QUOTE_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const validateGeneralQuoteImageFile = (file) => {
  const fileType = String(file?.type || "").toLowerCase();
  if (!GENERAL_QUOTE_ALLOWED_IMAGE_TYPES.has(fileType)) {
    return {
      valid: false,
      message: "Please choose a JPG, PNG, or WebP image.",
    };
  }
  if (Number(file?.size || 0) > GENERAL_QUOTE_MAX_IMAGE_BYTES) {
    return {
      valid: false,
      message: "Image is too large. Please choose an image under 10 MB.",
    };
  }
  return { valid: true, message: "" };
};

const buildGeneralQuoteImageStoragePath = ({
  workspaceUid,
  quoteId = "",
  draftId = "",
  itemId,
  imageId,
  fileName,
  timestamp = Date.now(),
} = {}) => {
  const workspaceSegment = sanitizeFirestoreDocIdSegment(workspaceUid);
  const quoteSegment = quoteId
    ? sanitizeFirestoreDocIdSegment(quoteId)
    : `drafts/${sanitizeFirestoreDocIdSegment(draftId || "draft-new")}`;
  return `${GENERAL_QUOTE_IMAGE_STORAGE_ROOT}/${workspaceSegment}/${quoteSegment}/${sanitizeFirestoreDocIdSegment(
    itemId
  )}/${sanitizeFirestoreDocIdSegment(imageId)}-${Number(timestamp) || Date.now()}-${normalizeFileName(
    fileName || "item-image"
  )}`;
};

const buildCrmManagedAttachmentStoragePath = ({
  workspaceUid,
  leadId,
  folder = "",
  attachmentId,
  fileName,
  timestamp = Date.now(),
} = {}) => {
  const workspaceSegment = sanitizeFirestoreDocIdSegment(workspaceUid);
  const leadSegment = sanitizeFirestoreDocIdSegment(leadId || "crm-record");
  const folderSegment = folder ? `/${sanitizeFirestoreDocIdSegment(folder)}` : "";
  return `crmAttachments/${workspaceSegment}/${leadSegment}${folderSegment}/${sanitizeFirestoreDocIdSegment(
    attachmentId || createClientId("crm-attachment")
  )}-${Number(timestamp) || Date.now()}-${normalizeFileName(fileName || "attachment")}`;
};

const isGeneralQuoteDraftImagePath = (path = "", workspaceUid = "") =>
  String(path || "").startsWith(
    `${GENERAL_QUOTE_IMAGE_STORAGE_ROOT}/${sanitizeFirestoreDocIdSegment(workspaceUid)}/drafts/`
  );

const hasGeneralQuoteDraftImagePaths = (quote = {}, workspaceUid = "") =>
  (Array.isArray(quote?.lineItems) ? quote.lineItems : []).some((item) =>
    normalizeGeneralQuoteItemImages(item).some((image) =>
      isGeneralQuoteDraftImagePath(getGeneralQuoteImageStoragePath(image), workspaceUid)
    )
  );

const uploadGeneralQuoteImage = async ({
  workspaceUid,
  quoteId = "",
  draftId = "",
  itemId,
  imageId,
  file,
  metadata = {},
  timestamp = Date.now(),
} = {}) => {
  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    const error = new Error(validation.message);
    error.code = "invalid-general-quote-image";
    throw error;
  }

  const uploadedAt = new Date().toISOString();
  const resolvedImageId = String(imageId || createClientId("general-image"));
  const safeFileName = normalizeFileName(file.name || "item-image");
  const storagePath = buildGeneralQuoteImageStoragePath({
    workspaceUid,
    quoteId,
    draftId,
    itemId,
    imageId: resolvedImageId,
    fileName: safeFileName,
    timestamp,
  });
  const managedImage = await uploadManagedImage({
    workspaceUid,
    file,
    ownerType: "generalQuote",
    ownerId: quoteId || (draftId ? `drafts/${draftId}` : ""),
    quoteId: quoteId || "",
    itemId,
    imageId: resolvedImageId,
    filenamePrefix: `${resolvedImageId}-${timestamp}-`,
    fullPath: storagePath,
    uploadedBy: auth.currentUser?.uid || "",
    metadata: {
      workspaceUid,
      quoteId: quoteId || (draftId ? `drafts/${draftId}` : ""),
      quoteType: GENERAL_QUOTE_TYPE,
      itemId,
      imageId: resolvedImageId,
      uploadedAt,
      ...metadata,
    },
  });

  return createGeneralQuoteImage({
    ...managedImage,
    id: resolvedImageId,
    imageId: resolvedImageId,
    name: managedImage.filename || file.name,
    filename: managedImage.filename || file.name,
    bucket: firebaseApp.options.storageBucket || "",
    storageBucket: firebaseApp.options.storageBucket || "",
    uploadedAt,
  });
};

const getGeneralQuoteImageDisplayUrl = (image = {}) => {
  const url = String(image?.downloadURL || image?.url || image?.previewUrl || "").trim();
  return isProtectedFirebaseStorageRestUrl(url) ? "" : url;
};

const getGeneralQuoteImageStoragePath = (image = {}) =>
  String(image?.fullPath || image?.storagePath || image?.path || "").trim();

const getGeneralQuoteImageCacheKey = (image = {}) => {
  const bucket = String(image?.storageBucket || "").trim();
  const path = getGeneralQuoteImageStoragePath(image);
  return `${bucket || firebaseApp.options.storageBucket || "default"}:${path}`;
};

const getGeneralQuoteImageIdentityKeys = (image = {}) => {
  const normalized = createGeneralQuoteImage(image);
  return [
    normalized.id,
    normalized.imageId,
    normalized.fileId,
    getGeneralQuoteImageStoragePath(normalized),
    normalized.downloadURL,
    normalized.url,
    normalized.previewUrl,
    normalized.legacyUrl,
    normalized.filename,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
};

const generalQuoteImageMatchesIdentity = (image = {}, identity = "") => {
  const target = String(identity || "").trim();
  if (!target) return false;
  return getGeneralQuoteImageIdentityKeys(image).includes(target);
};

const isGeneralQuoteImageTombstoned = (image = {}, tombstones = {}) =>
  getGeneralQuoteImageIdentityKeys(image).some((key) => tombstones?.[key] === true);

const getGeneralQuoteImageStorageRef = (image = {}) => {
  const fullPath = getGeneralQuoteImageStoragePath(image);
  const bucket = String(image?.storageBucket || "").trim();
  if (bucket && fullPath) {
    return ref(storage, `gs://${bucket}/${fullPath}`);
  }
  return ref(storage, fullPath);
};

const isJonyLeadsImageDebugContext = (context = {}) => {
  const haystack = [
    context.quoteNo,
    context.quoteId,
    context.customerName,
    context.itemName,
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return (
    haystack.includes("ac-2026-9451") ||
    haystack.includes("jonyleads") ||
    haystack.includes("rolex chair without arms")
  );
};

const shouldLogGeneralQuoteImageDebug = (context = {}) =>
  isDevelopmentMode() || isJonyLeadsImageDebugContext(context);

const EXACT_JONYLEADS_IMAGE_BUCKET = "accentsquotationapp.firebasestorage.app";
const EXACT_JONYLEADS_IMAGE_FULL_PATH =
  "general-quote-items/scg3CjxGaihVxRL2baAF6R3qacm2/general-item-0b4f9d8f-7e9b-4da7-9f11-5349f0b30260-1776335819996-whatsapp-image-2026-04-16-at-4.00.37-pm.jpeg";

const getRawGeneralQuoteImageDebugEntries = (item = {}) => {
  const entries = [];
  const push = (rawImage, source) => {
    if (rawImage == null || rawImage === "") return;
    entries.push({ rawImage, source });
  };
  [
    ["images", item?.images],
    ["itemImages", item?.itemImages],
    ["productImages", item?.productImages],
    ["referenceImages", item?.referenceImages],
    ["attachments", item?.attachments],
  ].forEach(([source, value]) => {
    if (Array.isArray(value)) value.forEach((image) => push(image, source));
  });
  [
    ["image", item?.image],
    ["imageData", item?.imageData],
    ["referenceImage", item?.referenceImage],
    ["productImage", item?.productImage],
  ].forEach(([source, value]) => push(value, source));
  if (
    item?.imageUrl ||
    item?.imageURL ||
    item?.downloadURL ||
    item?.downloadUrl ||
    item?.imageStoragePath ||
    item?.fullPath ||
    item?.storagePath ||
    item?.path ||
    item?.filePath ||
    item?.gsUrl ||
    item?.gsURL ||
    item?.imageName ||
    item?.imagePreviewName ||
    item?.filename
  ) {
    push(
      {
        imageUrl: item?.imageUrl || item?.imageURL || "",
        downloadURL: item?.downloadURL || item?.downloadUrl || "",
        imageStoragePath: item?.imageStoragePath || "",
        fullPath: item?.fullPath || "",
        storagePath: item?.storagePath || "",
        path: item?.path || "",
        filePath: item?.filePath || "",
        gsUrl: item?.gsUrl || item?.gsURL || "",
        imageName: item?.imageName || "",
        imagePreviewName: item?.imagePreviewName || "",
        filename: item?.filename || "",
      },
      "legacy-item-fields"
    );
  }
  return entries;
};

const getGeneralQuoteImageResolveErrorMessage = (error) => {
  const code = String(error?.code || "").trim();
  if (code === "storage/unauthorized") {
    return "You do not have permission to view this image.";
  }
  if (code === "storage/object-not-found") {
    return "Image file was not found in Storage.";
  }
  if (code === "storage/retry-limit-exceeded" || /network/i.test(String(error?.message || ""))) {
    return "Image could not load due to network issue.";
  }
  return "Image unavailable.";
};

// eslint-disable-next-line no-unused-vars
const ellipsizeChartLabel = (value = "", maxLength = 18) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};



/* ================================================================
   MAIN COMPONENT
   ================================================================ */

export default function QuotationApp() {
  const {
    account,
    accountUser,
    isSuperAdmin,
    features,
    hasFeature,
    hasActiveSubscription,
    isTrialAccount,
    loading: accountLoading,
    currentAccount,
    workspaceUid: accountWorkspaceUid,
    currentUser: accountCurrentUser,
    trialQuotationLimit,
    trialQuotationsRemaining,
    trialQuotationsUsed,
  } = useAccount();
  const [user, setUser] = useState(null);
  const [workspaceContext, setWorkspaceContext] = useState(null);
  const [isResolvingWorkspace, setIsResolvingWorkspace] = useState(true);
  const [workspaceBootstrapError, setWorkspaceBootstrapError] = useState(null);
  const [workspaceBootstrapDebug, setWorkspaceBootstrapDebug] = useState(() =>
    buildWorkspaceBootstrapDebug({ loadingStep: "initializing" })
  );
  const workspaceBootstrapRunRef = useRef(0);
  const { isMobile, isTablet, isCompact } = useViewport();
const appMountIdRef = useRef(
  `mount_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
);
const [isNewQuote, setIsNewQuote] = useState(true);
const [selectedQuote, setSelectedQuote] = useState(null);
const [selectedReferralPartner, setSelectedReferralPartner] = useState(null);
const selectedReferralPartnerRef = useRef(null);
const [referralPartners, setReferralPartners] = useState([]);
const referralPartnersCacheRef = useRef({
  workspaceUid: "",
  loadedAt: 0,
  partners: [],
});
const [referralPartnersLoadState, setReferralPartnersLoadState] = useState({
  loading: false,
  loadedWorkspace: "",
  error: "",
});
const [referralPartnerPickerState, setReferralPartnerPickerState] = useState({
  query: "",
  activeContext: "",
});
const [referralPartnerModalState, setReferralPartnerModalState] = useState({
  open: false,
  context: "",
  draft: createReferralPartnerDraft(),
  saving: false,
  error: "",
  duplicate: null,
});
const [selectedSolutionIndex, setSelectedSolutionIndex] = useState(0);
const [isGeneralQuoteDebugOpen, setIsGeneralQuoteDebugOpen] = useState(false);
const [generalQuoteDebugUploadFile, setGeneralQuoteDebugUploadFile] =
  useState(null);
const [generalQuoteDebugState, setGeneralQuoteDebugState] = useState({
  uploadState: "idle",
  lastUpload: null,
  lastUploadError: null,
  lastSave: null,
  lastSaveError: null,
  directFirestoreTest: null,
  directStorageTest: null,
});

useEffect(() => {
  const previousSaveInProgress = getSessionStorageJson(SAVE_IN_PROGRESS_STORAGE_KEY);
  const lastSaveTraceFromSessionStorage = getSessionStorageJson(LAST_SAVE_TRACE_STORAGE_KEY);
  const latestVerifiedSavedQuote = getSessionStorageJson(LATEST_VERIFIED_SAVED_QUOTE_STORAGE_KEY);
  const navigationType = getPerformanceNavigationType();
  const staleSaveInProgress = isStaleSaveInProgressTrace(previousSaveInProgress);
  const mountPayload = {
    buildLabel: FABRIQUOTE_BUILD_INFO.label,
    mountId: appMountIdRef.current,
    performanceNavigationType: navigationType,
    locationHref: typeof window !== "undefined" ? window.location.href : "",
    previousSaveInProgress,
    staleSaveInProgress,
    lastSaveTraceFromSessionStorage,
    latestVerifiedSavedQuote,
  };
  devInfo("FABRIQUOTE_BUILD_INFO", FABRIQUOTE_BUILD_INFO);
  devLog("APP_MOUNT_TRACE", JSON.stringify(mountPayload, null, 2));
  if (previousSaveInProgress && !staleSaveInProgress) {
    devWarn(
      "APP_RELOADED_DURING_SAVE",
      JSON.stringify(
        {
          savedTrace: previousSaveInProgress,
          navigationType,
          locationHref: mountPayload.locationHref,
        },
        null,
        2
      )
    );
    setSessionStorageJson(SAVE_ATTEMPT_TRACE_STORAGE_KEY, {
      ...previousSaveInProgress,
      status: "interrupted_by_reload",
      interruptedAt: Date.now(),
      navigationType,
      locationHref: mountPayload.locationHref,
    });
    removeSessionStorageItem(SAVE_IN_PROGRESS_STORAGE_KEY);
  } else if (previousSaveInProgress && staleSaveInProgress) {
    devWarn(
      "STALE_SAVE_IN_PROGRESS_TRACE_CLEARED",
      JSON.stringify(
        {
          savedTrace: previousSaveInProgress,
          staleAfterMs: SAVE_IN_PROGRESS_STALE_MS,
          navigationType,
          locationHref: mountPayload.locationHref,
        },
        null,
        2
      )
    );
    setSessionStorageJson(SAVE_ATTEMPT_TRACE_STORAGE_KEY, {
      ...previousSaveInProgress,
      status: "stale_in_progress_cleared",
      staleAfterMs: SAVE_IN_PROGRESS_STALE_MS,
      clearedAt: Date.now(),
      navigationType,
      locationHref: mountPayload.locationHref,
    });
    removeSessionStorageItem(SAVE_IN_PROGRESS_STORAGE_KEY);
  }
}, []);

// ====================== STOCK TAB STATE ======================
const [stockQuoteId, setStockQuoteId] = useState("");
const [stockFabricName, setStockFabricName] = useState("");
const [stockRollMeters, setStockRollMeters] = useState(""); // comma separated
const [stockLooseMeters, setStockLooseMeters] = useState("");
const [stockWastageMeters, setStockWastageMeters] = useState("");






  /* Settings (per user) */
  const [stitchingRates, setStitchingRates] = useState(DEFAULT_STITCHING);
  const [minSqft, setMinSqft] = useState(DEFAULT_MIN_SQFT);
  const [productConfigs, setProductConfigs] = useState(() =>
    mergeProductConfigsWithDefaults([], { minSqft: DEFAULT_MIN_SQFT })
  );
  const [productConfigsLoadState, setProductConfigsLoadState] = useState({
    loading: false,
    loadedWorkspace: "",
    error: "",
  });
  const [productConfigDraft, setProductConfigDraft] = useState(() =>
    createProductConfigDraft({ name: "Mosquito Net", calculationType: "sqft_only" })
  );
  const [editingProductConfigId, setEditingProductConfigId] = useState("");
  const [savingProductConfigId, setSavingProductConfigId] = useState("");
  const productConfigsLoadedWorkspaceRef = useRef("");
  const productConfigsLoadingWorkspaceRef = useRef("");
  const [currency, setCurrency] = useState("Rs. Indian Rupee");
  const [businessName, setBusinessName] = useState("");
  const [shopAddress, setShopAddress] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [crmMemberRoster, setCrmMemberRoster] = useState({ rows: [], loading: false, error: "", scope: "" });
  const [crmMemberRosterRetry, setCrmMemberRosterRetry] = useState(0);
  const [logoURL, setLogoURL] = useState("");
  const [isTeamMemberModalOpen, setIsTeamMemberModalOpen] = useState(false);
  const [teamMemberDraft, setTeamMemberDraft] = useState(() =>
    createTeamMemberDraft({
      permissions: DEFAULT_TEAM_PERMISSIONS,
      status: "draft",
      isActive: false,
    })
  );
  const [teamMemberModalError, setTeamMemberModalError] = useState("");
  const [isSendingTeamInvite, setIsSendingTeamInvite] = useState(false);
  const [resendingTeamInviteId, setResendingTeamInviteId] = useState("");
  const [diagnosingTeamMemberId, setDiagnosingTeamMemberId] = useState("");
  const [repairingTeamMemberId, setRepairingTeamMemberId] = useState("");
  const [disablingTeamMemberId, setDisablingTeamMemberId] = useState("");
  const [isTestingTeamInviteEmail, setIsTestingTeamInviteEmail] = useState(false);

  const [newStitch, setNewStitch] = useState("");
  const [newRate, setNewRate] = useState("");
  const [newFullness, setNewFullness] = useState("3");
  const [newTrackLengthFt, setNewTrackLengthFt] = useState("16");
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isStitchingSettingsDirty, setIsStitchingSettingsDirty] = useState(false);







  /* Theme + nav */
  const [isDark, setIsDark] = useState(false);
  const [tab, setTab] = useState("quote"); // quote | summary | settings | stitching | saved
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [expandedCurtainRows, setExpandedCurtainRows] = useState({});


  /* Quotation meta */
  const [customerName, setCustomerName] = useState("");
  const [quoteCustomerLink, setQuoteCustomerLink] = useState(() =>
    createEmptyQuoteCustomerLink()
  );
  const [quoteSiteSelection, setQuoteSiteSelection] = useState(() =>
    createEmptyQuoteSiteSelection()
  );
  const [customerSitesByCustomerId, setCustomerSitesByCustomerId] = useState({});
  const [customerSitesLoadingId, setCustomerSitesLoadingId] = useState("");
  const [customerSitePickerOpenKey, setCustomerSitePickerOpenKey] = useState("");
  const customerSitesLoadRef = useRef({});
  const [customerPickerOpenKey, setCustomerPickerOpenKey] = useState("");
  const [customerCreateState, setCustomerCreateState] = useState(null);
  const [isCreatingCustomerFromQuote, setIsCreatingCustomerFromQuote] = useState(false);
  const [quoteNo, setQuoteNo] = useState("");
  const [date, setDate] = useState(
    () => getTodayLocalDateString()
  );
  const [quoteSubTab, setQuoteSubTab] = useState(CURTAIN_QUOTE_TYPE);
  const [curtainQuoteEditorMode, setCurtainQuoteEditorMode] = useState(
    QUOTE_EDITOR_MODE_NEW
  );
  const [generalQuoteEditorMode, setGeneralQuoteEditorMode] = useState(
    QUOTE_EDITOR_MODE_NEW
  );
  const quoteEditorMode =
    quoteSubTab === GENERAL_QUOTE_TYPE
      ? generalQuoteEditorMode
      : curtainQuoteEditorMode;
  const [generalEditingQuoteId, setGeneralEditingQuoteId] = useState(null);
  const [generalUploadingItemId, setGeneralUploadingItemId] = useState("");
  const [generalRepairingImageKey, setGeneralRepairingImageKey] = useState("");
  const [generalImageRepairState, setGeneralImageRepairState] = useState({});
  const [generalImageRepairCandidates, setGeneralImageRepairCandidates] = useState({});
  const [generalQuote, setGeneralQuote] = useState(() =>
    createDefaultGeneralQuote({
      currency,
      businessName,
      shopAddress,
      businessPhone,
      businessEmail,
    })
  );
  const generalQuoteRef = useRef(generalQuote);
  const deletedGeneralItemIdsRef = useRef([]);
  const generalUploadPromiseRef = useRef(null);
  const generalQuoteImageUrlCacheRef = useRef(new Map());
  const generalQuoteImageRepairDebugRef = useRef({});
  const generalRemovedImageIdsRef = useRef({});
  const generalActionRuntimeRef = useRef({
    mountedAt: Date.now(),
    lastHydratedAt: null,
    lastPayloadBuiltAt: null,
    locks: {},
  });
  generalQuoteRef.current = generalQuote;
  const setGeneralQuoteLive = useCallback((updater) => {
    setGeneralQuote((previousQuote) => {
      const nextQuote =
        typeof updater === "function" ? updater(previousQuote) : updater;
      generalQuoteRef.current = nextQuote;
      return nextQuote;
    });
  }, []);

// ---------------- TRACK CUTTING OPTIMIZER (TCO) STATE ----------------
const [, setTrackLeftovers] = useState({});   // key -> remaining ft
const [, setTrackCutPlan] = useState([]);       // optimization results


const [isLoadingQuoteForEdit, setIsLoadingQuote] = useState(false);
const incompleteQuoteLoadRef = useRef(null);
const [, setIncompleteQuoteLoadState] = useState(null);

const toggleCurtainRowExpanded = useCallback((rowKey) => {
  setExpandedCurtainRows((prev) => ({
    ...prev,
    [rowKey]: !prev[rowKey],
  }));
}, []);
/* ---------------- DASHBOARD STATES ---------------- */
const [dashView, setDashView] = useState("home"); 
// "home" | "month" | "confirmed" | "draft" | "final"
const [dashboardMode, setDashboardMode] = useState("business");
const [businessDashboardDrilldownKey, setBusinessDashboardDrilldownKey] = useState("");
const [dashboardDrilldownModal, setDashboardDrilldownModal] = useState(null);
const [dashboardDrilldownSearch, setDashboardDrilldownSearch] = useState("");
const [crmView, setCrmView] = useState("overview");
const [customerHistoryRetryBusyId, setCustomerHistoryRetryBusyId] = useState("");
const customerHistoryRetryInFlightRef = useRef(false);
const [crmScope, setCrmScope] = useState(CRM_SCOPE_ALL);
const [crmPipelineType, setCrmPipelineType] = useState(CURTAIN_QUOTE_TYPE);
const [selectedCrmDealId, setSelectedCrmDealId] = useState("");
const [crmDetailDraft, setCrmDetailDraft] = useState(null);
const [crmSearch, setCrmSearch] = useState("");
const deferredCrmSearch = useDeferredValue(crmSearch);
const [crmLeadSearch, setCrmLeadSearch] = useState("");
const deferredCrmLeadSearch = useDeferredValue(crmLeadSearch);
const [crmDealsVisibleCount, setCrmDealsVisibleCount] = useState(CRM_DEALS_PAGE_SIZE);
const [crmLeadsVisibleCount, setCrmLeadsVisibleCount] = useState(CRM_LEADS_PAGE_SIZE);
const [crmLeadPaging, setCrmLeadPaging] = useState({ loading: false, hasMore: false, legacyHasMore: true, error: "" });
const crmLeadPagerRef = useRef(null);
const crmSearchPagesRef = useRef(new Map());
const [, setCrmSearchPageRevision] = useState(0);
const [crmArchivePage, setCrmArchivePage] = useState({ rows: [], cursor: null, hasMore: false, loading: false, error: "", loaded: false });
const crmArchiveBusyRef = useRef(false);
const crmWorkspaceRef = useRef("");
const [crmDuplicateDealAudit, setCrmDuplicateDealAudit] = useState({
  scanned: false,
  isScanning: false,
  groups: [],
});
const [repairingDuplicateDealKey, setRepairingDuplicateDealKey] = useState("");
const [isRepairingCrmSearchIndex, setIsRepairingCrmSearchIndex] = useState(false);
const [crmStageFilter, setCrmStageFilter] = useState("all");
const [crmTypeFilter, setCrmTypeFilter] = useState("all");
const [crmSourceFilter, setCrmSourceFilter] = useState("all");
const [crmSortBy, setCrmSortBy] = useState("newest");
const [crmLeadStageFilter, setCrmLeadStageFilter] = useState("all");
const [crmLeadSourceFilter, setCrmLeadSourceFilter] = useState("all");
const [crmLeadAssignedFilter, setCrmLeadAssignedFilter] = useState("all");
const [crmLeadFromDate, setCrmLeadFromDate] = useState("");
const [crmLeadToDate, setCrmLeadToDate] = useState("");
const [quoteLinkModalState, setQuoteLinkModalState] = useState(null);
const [quoteDealChoiceState, setQuoteDealChoiceState] = useState(null);
const [quoteLinkSearch, setQuoteLinkSearch] = useState("");
const [quoteLinkTypeFilter, setQuoteLinkTypeFilter] = useState("all");
const [quoteLinkMinAmount, setQuoteLinkMinAmount] = useState("");
const [quoteLinkMaxAmount, setQuoteLinkMaxAmount] = useState("");
const [isLinkingQuote, setIsLinkingQuote] = useState(false);
const [quoteSentPromptState, setQuoteSentPromptState] = useState({
  open: false,
  promptRequestId: "",
  quoteType: "",
  quoteContext: null,
  followUpDate: "",
  ownerUid: "",
  ownerName: "",
  note: "",
  createOpportunity: false,
  pdfGenerated: false,
  loading: false,
  error: "",
});
const quoteSentPromptRequestRef = useRef("");
const quoteFollowUpBusyRef = useRef(false);
const [crmDetailActiveTab, setCrmDetailActiveTab] = useState("overview");
const [crmOpportunityDrawerData, setCrmOpportunityDrawerData] = useState({
  opportunityId: "",
  quotes: [],
  tasks: [],
  activity: [],
  loading: {
    quotes: false,
    tasks: false,
    activity: false,
  },
  errors: {
    quotes: "",
    tasks: "",
    activity: "",
  },
});
const [crmDrilldownKey, setCrmDrilldownKey] = useState("");
const [crmLeadRecords, setCrmLeadRecords] = useState([]);
const [debugRawCrmLeadCount, setDebugRawCrmLeadCount] = useState(0);
const [debugRawQuotationCount, setDebugRawQuotationCount] = useState(0);
const [quoteSearchRepairState, setQuoteSearchRepairState] = useState({
  running: false,
  processed: 0,
  updated: 0,
  error: "",
});
const [savedQuoteIndexRecoveryState, setSavedQuoteIndexRecoveryState] = useState({
  running: false,
  checked: 0,
  missing: 0,
  recovered: 0,
  binFlagRepairCount: 0,
  inBinCount: 0,
  error: "",
  report: null,
});
const [quoteContentAuditState, setQuoteContentAuditState] = useState({
  running: false,
  checked: 0,
  conflicts: 0,
  repaired: 0,
  error: "",
  report: null,
});
const [analyticsRebuildState, setAnalyticsRebuildState] = useState({
  running: false,
  scanned: 0,
  indexedQuotes: 0,
  monthlyUpdated: 0,
  clientUpdated: 0,
  installationsUpdated: 0,
  staleCleared: 0,
  skipped: 0,
  failed: 0,
  error: "",
});
const [adminDataAuditState, setAdminDataAuditState] = useState({
  running: false,
  checkedAt: "",
  error: "",
  collections: {},
  progress: {
    collection: "",
    scanned: 0,
  },
  report: null,
});
const [crmDataRepairState, setCrmDataRepairState] = useState({
  quoteLookup: "",
  quoteDiagnosticLoading: false,
  quoteDiagnosticError: "",
  quoteDiagnostic: null,
  unlinkedLoading: false,
  unlinkedError: "",
  unlinkedResults: [],
  unlinkedCursor: null,
  unlinkedHasMore: false,
  unlinkedSelected: {},
  unlinkedMode: "link_only",
  repairingQuoteKey: "",
  activityCollection: "quote_index",
  activityLoading: false,
  activityError: "",
  activityResults: [],
  activityCursor: null,
  activityHasMore: false,
  activitySelected: {},
  repairingActivity: false,
  activityConfirmation: null,
  duplicateDealsLoading: false,
  duplicateDealsError: "",
  duplicateDealResults: [],
  duplicateDealSelected: {},
  duplicateDealsCursor: null,
  duplicateDealsHasMore: false,
  repairingDuplicateDealKey: "",
  projectStageRepairLoading: false,
  projectStageRepairError: "",
  projectStageResults: [],
  projectStageCursor: null,
  projectStageHasMore: false,
  projectStageSelected: {},
  repairingProjectStage: false,
  duplicateProjectsLoading: false,
  duplicateProjectsError: "",
  duplicateProjectResults: [],
  duplicateProjectSelected: {},
  duplicateProjectsCursor: null,
  duplicateProjectsHasMore: false,
  repairingDuplicateProjectKey: "",
  customerIndexSearchQuery: "",
  customerIndexSearchLoading: false,
  customerIndexSearchResults: [],
  customerIndexSearchError: "",
  customerIndexSearchSearched: false,
  customerIndexSelectedCustomer: null,
  customerIndexRepairId: "",
  customerIndexRepairLoading: false,
  customerIndexRepairResult: null,
  customerIndexRepairError: "",
  quoteIndexMismatchLoading: false,
  quoteIndexMismatchError: "",
  quoteIndexMismatchResults: [],
  quoteIndexMismatchCursor: null,
  quoteIndexMismatchHasMore: false,
  repairingQuoteIndexKey: "",
  repairingCrmDealKey: "",
  quoteIndexTestDataBusy: false,
  quoteIndexTestDataMessage: "",
  quoteIndexTestDataError: "",
  quoteIndexTestDataRecords: [],
  quoteIndexTestDataVerification: [],
  testDataBusy: false,
  testDataMessage: "",
  testDataError: "",
  testDataRecords: [],
  testDataVerification: [],
  repairLogChecks: [],
});
const [dashboardAnalyticsState, setDashboardAnalyticsState] = useState({
  monthKeys: [],
  monthlySummaries: {},
  topClients: [],
  installationRecords: [],
  diagnostics: {
    source: "idle",
    reads: {},
    monthKey: "",
    loadedAt: "",
  },
});
const [dashboardQuotePreview, setDashboardQuotePreview] = useState([]);
const [customerRecords, setCustomerRecords] = useState([]);
const [customerLedgerSearch, setCustomerLedgerSearch] = useState("");
const deferredCustomerLedgerSearch = useDeferredValue(customerLedgerSearch);
const [globalSearchStatus, setGlobalSearchStatus] = useState({});
const [customerLedgerSourceFilter, setCustomerLedgerSourceFilter] = useState("all");
const [customerLedgerSortBy, setCustomerLedgerSortBy] = useState("latest");
const [selectedCustomerLedgerId, setSelectedCustomerLedgerId] = useState("");
const [customerLedgerDetailTab, setCustomerLedgerDetailTab] = useState("overview");
const [customerLedgerQuoteSiteFilter, setCustomerLedgerQuoteSiteFilter] =
  useState(null);
const [customerLedgerCrmDealsByCustomer, setCustomerLedgerCrmDealsByCustomer] =
  useState({});
const [customerLedgerCrmDealsLoadingId, setCustomerLedgerCrmDealsLoadingId] =
  useState("");
const customerLedgerCrmDealsLoadRef = useRef({});
const [customerLedgerQuotesByCustomer, setCustomerLedgerQuotesByCustomer] =
  useState({});
const [customerLedgerQuotesLoadingId, setCustomerLedgerQuotesLoadingId] =
  useState("");
const customerLedgerQuotesLoadRef = useRef({});
const [customerLedgerQuoteLinkState, setCustomerLedgerQuoteLinkState] = useState({
  open: false,
  customerId: "",
  search: "",
  loading: false,
  error: "",
  results: [],
  linkingQuoteId: "",
});
const [customerLedgerQuoteLinkConfirm, setCustomerLedgerQuoteLinkConfirm] =
  useState(null);
const [customerLedgerEditState, setCustomerLedgerEditState] = useState(() =>
  createCustomerLedgerEditState()
);
const [customerLedgerProjectsByCustomer, setCustomerLedgerProjectsByCustomer] =
  useState({});
const [customerLedgerProjectsLoadingId, setCustomerLedgerProjectsLoadingId] =
  useState("");
const customerLedgerProjectsLoadRef = useRef({});
const [quotationCustomerSearchQueries, setQuotationCustomerSearchQueries] =
  useState({});
const [quotationCustomerSearchState, setQuotationCustomerSearchState] =
  useState({});
const quotationCustomerSearchCacheRef = useRef(new Map());
const quotationCustomerSearchRequestRef = useRef({});
const [customerSearchRepairState, setCustomerSearchRepairState] = useState({
  running: false,
  processed: 0,
  updated: 0,
  error: "",
});
const [customerLedgerPaymentDraft, setCustomerLedgerPaymentDraft] = useState({
  amount: "",
  date: todayISO(),
  notes: "",
});
const [customerLocationDraft, setCustomerLocationDraft] = useState(() =>
  createSiteLocationDraft()
);
const [crmSiteLocationDraft, setCrmSiteLocationDraft] = useState(() =>
  createSiteLocationDraft()
);
const [isSavingCustomerPayment, setIsSavingCustomerPayment] = useState(false);
const [crmExecutionTemplates, setCrmExecutionTemplates] = useState([]);
const [executionTemplatesLoadState, setExecutionTemplatesLoadState] = useState({
  workspaceUid: "",
  loading: false,
  loaded: false,
  error: "",
});
const [isSavingExecutionTemplates, setIsSavingExecutionTemplates] = useState(false);
const [crmReminderRecords, setCrmReminderRecords] = useState([]);
const [crmTaskHistoryRecords, setCrmTaskHistoryRecords] = useState([]);
const [crmTaskRecords, setCrmTaskRecords] = useState([]);
const [crmProjectRecords, setCrmProjectRecords] = useState([]);
const [crmProjectsState, setCrmProjectsState] = useState({
  loading: false,
  loaded: false,
  error: "",
});
const [crmProjectFilter, setCrmProjectFilter] = useState("active");
const [selectedCrmProjectId, setSelectedCrmProjectId] = useState("");
const [crmProjectDrawerTab, setCrmProjectDrawerTab] = useState("overview");
const [crmProjectDrawerData, setCrmProjectDrawerData] = useState({
  projectId: "",
  tasks: [],
  trackingItems: [],
  payments: [],
  snags: [],
  activity: [],
  loading: { tasks: false, tracking: false, payments: false, snags: false, activity: false },
  errors: { tasks: "", tracking: "", payments: "", snags: "", activity: "" },
});
const [creatingOrderTaskTemplateForProjectId, setCreatingOrderTaskTemplateForProjectId] = useState("");
const [crmMaterialModalState, setCrmMaterialModalState] = useState({
  open: false,
  projectId: "",
  materialId: "",
  draft: null,
  loading: false,
  error: "",
});
const [crmMaterialActionKey, setCrmMaterialActionKey] = useState("");
const [crmPaymentModalState, setCrmPaymentModalState] = useState({
  open: false,
  projectId: "",
  paymentId: "",
  draft: null,
  followUpTask: null,
  completeFollowUpAfterSave: false,
  loading: false,
  error: "",
});
const [crmPaymentFollowUpModalState, setCrmPaymentFollowUpModalState] = useState({
  open: false,
  projectId: "",
  draft: null,
  loading: false,
  error: "",
});
const [crmInstallationModalState, setCrmInstallationModalState] = useState({
  open: false,
  projectId: "",
  mode: "schedule",
  draft: null,
  loading: false,
  error: "",
});
const [crmSnagModalState, setCrmSnagModalState] = useState({
  open: false,
  projectId: "",
  snagId: "",
  draft: null,
  loading: false,
  error: "",
});
const [crmProjectActionKey, setCrmProjectActionKey] = useState("");
const [orderHandoverModalState, setOrderHandoverModalState] = useState({
  open: false,
  source: "",
  opportunity: null,
  quoteIndex: null,
  quote: null,
  draft: null,
  loading: false,
  error: "",
  warnings: [],
});
const [crmAttentionState, setCrmAttentionState] = useState({
  loading: false,
  loaded: false,
  error: "",
  missingOwner: [],
  missingNextAction: [],
  overdueTasks: [],
  missingQuoteFollowUp: [],
  handoverPendingProjects: [],
  advancePendingProjects: [],
  projectsMissingNextAction: [],
  projectsMissingInstallationDate: [],
  overdueTrackingTasks: [],
  trackingItemsNotOrdered: [],
  trackingItemsDelayed: [],
  trackingArrivalsOverdue: [],
  balancePendingProjects: [],
  overduePaymentTasks: [],
  paymentDueTodayTasks: [],
  installationNotScheduledProjects: [],
  installationsDueToday: [],
  overdueInstallations: [],
  productionOverdueProjects: [],
  materialDueOverdueProjects: [],
  openSnagProjects: [],
  overdueSnagProjects: [],
  projectsReadyToClose: [],
  projectsBlockedByFinalPayment: [],
  reviewDueTodayTasks: [],
  overdueReviewTasks: [],
});
const removeCrmAttentionTaskById = useCallback((taskId) => {
  const normalizedTaskId = String(taskId || "").trim();
  if (!normalizedTaskId) return;
  const taskBuckets = [
    "overdueTasks",
    "overdueTrackingTasks",
    "overduePaymentTasks",
    "paymentDueTodayTasks",
    "reviewDueTodayTasks",
    "overdueReviewTasks",
  ];
  setCrmAttentionState((prev) => {
    let changed = false;
    const next = { ...prev };
    taskBuckets.forEach((bucketKey) => {
      const current = Array.isArray(prev[bucketKey]) ? prev[bucketKey] : [];
      const filtered = current.filter(
        (task) =>
          String(task?.taskId || task?.id || task?.sourceId || "").trim() !==
          normalizedTaskId
      );
      if (filtered.length !== current.length) {
        next[bucketKey] = filtered;
        changed = true;
      }
    });
    return changed ? next : prev;
  });
}, []);
const removeCrmAttentionProjectById = useCallback((projectId, bucketKeys = []) => {
  const normalizedProjectId = String(projectId || "").trim();
  if (!normalizedProjectId) return;
  const defaultBuckets = [
    "handoverPendingProjects",
    "advancePendingProjects",
    "projectsMissingNextAction",
    "projectsMissingInstallationDate",
    "balancePendingProjects",
    "installationNotScheduledProjects",
    "installationsDueToday",
    "overdueInstallations",
    "productionOverdueProjects",
    "materialDueOverdueProjects",
    "openSnagProjects",
    "overdueSnagProjects",
    "projectsReadyToClose",
    "projectsBlockedByFinalPayment",
  ];
  const buckets = bucketKeys.length ? bucketKeys : defaultBuckets;
  setCrmAttentionState((prev) => {
    let changed = false;
    const next = { ...prev };
    buckets.forEach((bucketKey) => {
      const current = Array.isArray(prev[bucketKey]) ? prev[bucketKey] : [];
      const filtered = current.filter(
        (project) =>
          String(project?.projectId || project?.id || "").trim() !== normalizedProjectId
      );
      if (filtered.length !== current.length) {
        next[bucketKey] = filtered;
        changed = true;
      }
    });
    return changed ? next : prev;
  });
}, []);
const removeCrmAttentionOpportunityById = useCallback((ids = [], bucketKeys = []) => {
  const normalizedIds = new Set(
    (Array.isArray(ids) ? ids : [ids])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );
  if (!normalizedIds.size) return;
  const buckets = bucketKeys.length ? bucketKeys : ["missingOwner", "missingNextAction"];
  const getItemIds = (item = {}) => [
    item.id,
    item.opportunityId,
    item.legacyLeadId,
    item.leadId,
    item.crmLeadId,
  ];
  setCrmAttentionState((prev) => {
    let changed = false;
    const next = { ...prev };
    buckets.forEach((bucketKey) => {
      const current = Array.isArray(prev[bucketKey]) ? prev[bucketKey] : [];
      const filtered = current.filter((item) =>
        !getItemIds(item).some((id) => normalizedIds.has(String(id || "").trim()))
      );
      if (filtered.length !== current.length) {
        next[bucketKey] = filtered;
        changed = true;
      }
    });
    return changed ? next : prev;
  });
}, []);
const removeCrmAttentionQuoteById = useCallback((ids = []) => {
  const normalizedIds = new Set(
    (Array.isArray(ids) ? ids : [ids])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );
  if (!normalizedIds.size) return;
  const getItemIds = (item = {}) => [
    item.id,
    item.quoteId,
    item.quoteIndexId,
    item.sourceQuoteId,
    item.quoteNo,
  ];
  setCrmAttentionState((prev) => {
    const current = Array.isArray(prev.missingQuoteFollowUp)
      ? prev.missingQuoteFollowUp
      : [];
    const filtered = current.filter((item) =>
      !getItemIds(item).some((id) => normalizedIds.has(String(id || "").trim()))
    );
    return filtered.length === current.length
      ? prev
      : { ...prev, missingQuoteFollowUp: filtered };
  });
}, []);
const [crmAttentionActiveBucket, setCrmAttentionActiveBucket] = useState("missingNextAction");
const [crmSettings, setCrmSettings] = useState(() => normalizeCrmSettings(DEFAULT_CRM_SETTINGS));
const [crmSettingsDraft, setCrmSettingsDraft] = useState(() =>
  normalizeCrmSettings(DEFAULT_CRM_SETTINGS)
);
const [crmSettingsLoadState, setCrmSettingsLoadState] = useState({
  loading: false,
  loadedWorkspace: "",
  error: "",
});
const [isSavingCrmSettings, setIsSavingCrmSettings] = useState(false);
const [crmPipelineFilters, setCrmPipelineFilters] = useState(() => {
  const range = getCrmPipelineReviewRange("this_week");
  return {
    ...CRM_PIPELINE_REVIEW_DEFAULT_FILTERS,
    startDate: range.startDate,
    endDate: range.endDate,
  };
});
const [crmPipelineReviewState, setCrmPipelineReviewState] = useState({
  loading: false,
  loaded: false,
  error: "",
  errorClassification: "",
  errorQueryName: "",
  dateRange: getCrmPipelineReviewRange("this_week"),
  summary: {},
  salespersonRows: [],
  managementWarnings: {
    missingOwnerCount: 0,
    missingNextActionCount: 0,
    overdueTaskCount: 0,
    missingQuoteFollowUpCount: 0,
  },
  targets: [],
  readCounts: {
    opportunitiesRead: 0,
    tasksRead: 0,
    quotesRead: 0,
    projectsRead: 0,
    targetsRead: 0,
  },
  loadedAt: "",
});
const [crmWeeklyTargetDraft, setCrmWeeklyTargetDraft] = useState(() => {
  const range = getCrmPipelineReviewRange("this_week");
  return {
    ...CRM_WEEKLY_TARGET_DEFAULTS,
    weekStart: range.weekStart,
    weekEnd: range.weekEnd,
    userId: "",
    userName: "",
  };
});
const [isSavingCrmWeeklyTarget, setIsSavingCrmWeeklyTarget] = useState(false);
const [crmReminderNow, setCrmReminderNow] = useState(() => Date.now());
const globalReminderCheckedWorkspaceRef = useRef("");
const globalReminderLastCheckRef = useRef(0);
const globalReminderShownIdsRef = useRef(new Set());
const [isCrmLeadModalOpen, setIsCrmLeadModalOpen] = useState(false);
const [crmLeadDraft, setCrmLeadDraft] = useState(null);
const [isSavingCrmLead, setIsSavingCrmLead] = useState(false);
const [pendingCrmLeadId, setPendingCrmLeadId] = useState("");
const [crmPipelines, setCrmPipelines] = useState(() =>
  normalizeCrmPipelines(DEFAULT_CRM_PIPELINES)
);
const [salesPipeline, setSalesPipeline] = useState(() =>
  normalizeSalesPipeline(DEFAULT_SALES_PIPELINE)
);
const [salesPipelineLoadState, setSalesPipelineLoadState] = useState({
  loading: false,
  loadedWorkspace: "",
  error: "",
});
const [isSavingSalesPipeline, setIsSavingSalesPipeline] = useState(false);
const [orderPipeline, setOrderPipeline] = useState(() =>
  normalizeOrderPipeline(DEFAULT_ORDER_PIPELINE)
);
const [orderPipelineLoadState, setOrderPipelineLoadState] = useState({
  loading: false,
  loadedWorkspace: "",
  error: "",
});
const [isSavingOrderPipeline, setIsSavingOrderPipeline] = useState(false);
const [crmAttachmentUploadDealId, setCrmAttachmentUploadDealId] = useState("");
const [crmFollowUpAttachmentUploadKey, setCrmFollowUpAttachmentUploadKey] = useState("");
const [isSavingCrmDetails, setIsSavingCrmDetails] = useState(false);
const [markingDoneKey, setMarkingDoneKey] = useState("");
const [crmDetailsSavedAt, setCrmDetailsSavedAt] = useState(null);
const [crmSaveDebugExpanded, setCrmSaveDebugExpanded] = useState(false);
const [crmSaveDebugState, setCrmSaveDebugState] = useState({
  lastPayload: null,
  lastImmediateReadback: null,
  lastDelayedReadback: null,
  lastWritePath: "",
  lastVerification: null,
});
const [crmDetailsDebugExpanded, setCrmDetailsDebugExpanded] = useState(false);
const [crmDetailsOpenDebug, setCrmDetailsOpenDebug] = useState({
  lastSource: "",
  lastRecordId: "",
  lastResolvedLeadId: "",
  lastResolveReason: "",
  lastCustomerName: "",
  lastQuoteNo: "",
  lastError: "",
});
const [crmFollowUpModalState, setCrmFollowUpModalState] = useState(null);
const [crmExecutionAttachmentUploadKey, setCrmExecutionAttachmentUploadKey] = useState("");
const quotationSettingsLoadedWorkspaceRef = useRef("");
const quotationSettingsLoadingWorkspaceRef = useRef("");
const [quotationSettingsReadyWorkspace, setQuotationSettingsReadyWorkspace] = useState("");
const mainSettingsLoadedWorkspaceRef = useRef("");
const mainSettingsLoadingWorkspaceRef = useRef("");
const trackStockLoadedWorkspaceRef = useRef("");
const trackStockLoadingWorkspaceRef = useRef("");
const [executionWorkspaceSearch, setExecutionWorkspaceSearch] = useState("");
const [executionWorkspaceSourceFilter, setExecutionWorkspaceSourceFilter] = useState("all");
const [executionWorkspaceStageFilter, setExecutionWorkspaceStageFilter] = useState("all");
const [executionWorkspaceItemTypeFilter, setExecutionWorkspaceItemTypeFilter] = useState("all");
const [executionWorkspaceAttachmentFilter, setExecutionWorkspaceAttachmentFilter] = useState("all");
const [executionWorkspaceSortBy, setExecutionWorkspaceSortBy] = useState("updated");
const [selectedExecutionItemId, setSelectedExecutionItemId] = useState("");
const [selectedExecutionTemplateId, setSelectedExecutionTemplateId] = useState("");
const [executionWorkspaceOpeningDealId, setExecutionWorkspaceOpeningDealId] = useState("");
const [executionTemplateSearch, setExecutionTemplateSearch] = useState("");
const [executionTemplateSortBy, setExecutionTemplateSortBy] = useState("name");
const [selectedExecutionTemplateManagerId, setSelectedExecutionTemplateManagerId] = useState("");
const [crmTaskHistorySearch, setCrmTaskHistorySearch] = useState("");
const deferredCrmTaskHistorySearch = useDeferredValue(crmTaskHistorySearch);
const [crmTaskHistoryTypeFilter, setCrmTaskHistoryTypeFilter] = useState("all");
const [crmTaskHistoryCompletedByFilter, setCrmTaskHistoryCompletedByFilter] = useState("all");
const [crmTaskHistoryFromDate, setCrmTaskHistoryFromDate] = useState("");
const [crmTaskHistoryToDate, setCrmTaskHistoryToDate] = useState("");
const [selectedCrmTaskHistoryId, setSelectedCrmTaskHistoryId] = useState("");
const [dailyTaskDate, setDailyTaskDate] = useState(() => todayISO());
const [dailyTaskDateMode, setDailyTaskDateMode] = useState("today");
const [dailyTaskRangeStart, setDailyTaskRangeStart] = useState(() => todayISO());
const [dailyTaskRangeEnd, setDailyTaskRangeEnd] = useState(() => todayISO());
const [dailyTaskSearch, setDailyTaskSearch] = useState("");
const deferredDailyTaskSearch = useDeferredValue(dailyTaskSearch);
const [dailyTaskAssignedFilter, setDailyTaskAssignedFilter] = useState("all");
const [dailyTaskTypeFilter, setDailyTaskTypeFilter] = useState("all");
const [dailyTaskPriorityFilter, setDailyTaskPriorityFilter] = useState("all");
const [dailyTaskStatusFilter, setDailyTaskStatusFilter] = useState("all");
const [dailyTaskSourceFilter, setDailyTaskSourceFilter] = useState("all");
const [dailyTaskQuoteTypeFilter, setDailyTaskQuoteTypeFilter] = useState("all");
const [selectedDailyTaskId, setSelectedDailyTaskId] = useState("");
const [dailyTaskRescheduleState, setDailyTaskRescheduleState] = useState(null);
const [appToasts, setAppToasts] = useState([]);
const [confirmDialog, setConfirmDialog] = useState(null);
const [firebaseHealthWarning, setFirebaseHealthWarning] = useState(null);
const [firebaseReadHealthTick, setFirebaseReadHealthTick] = useState(0);
const [securitySessionId, setSecuritySessionId] = useState("");
const [securitySessions, setSecuritySessions] = useState([]);
const [securityActivity, setSecurityActivity] = useState([]);
const [securityCenterState, setSecurityCenterState] = useState({
  loading: false,
  error: "",
  loadedForUid: "",
});
const [securityActionState, setSecurityActionState] = useState({
  password: false,
  passwordReset: false,
  currentSession: false,
  otherSessions: false,
  allSessions: false,
  targetSessionId: "",
});
const [passwordForm, setPasswordForm] = useState({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  logoutOtherSessions: true,
  showPasswords: false,
  message: "",
  error: "",
});
const securitySessionRegisteredRef = useRef({
  uid: "",
  sessionId: "",
  registering: false,
  lastHeartbeatAt: 0,
});

// ---- INSTALLATION CALENDAR STATE ----
const [calendarMonth, setCalendarMonth] = useState(() => {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() }; // month: 011
});
const [selectedInstallDate, setSelectedInstallDate] = useState("");
const [isCrmDrawerHovered, setIsCrmDrawerHovered] = useState(false);
const [isCrmDrawerPinned, setIsCrmDrawerPinned] = useState(false);

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const changeCalendarMonth = (delta) => {
  setCalendarMonth((prev) => {
    const raw = prev.month + delta;
    const year = prev.year + Math.floor(raw / 12);
    const month = ((raw % 12) + 12) % 12;
    return { year, month };
  });
};


  /* Quote-level summary discount */
  const [summaryDiscountMode, setSummaryDiscountMode] = useState("%"); // "% | ""
  const [summaryDiscountValue, setSummaryDiscountValue] = useState("0");
  const [curtainGstMode, setCurtainGstMode] = useState(CURTAIN_GST_MODE_FLAT);
  const [curtainGstRate, setCurtainGstRate] = useState(0);
  const [quoteFullnessOverrides, setQuoteFullnessOverrides] = useState({});
  const [quoteConstructionSettings, setQuoteConstructionSettings] = useState(() => normalizeQuoteCalculationSettings());
  const quoteConstructionSettingsRef = useRef(quoteConstructionSettings);
  quoteConstructionSettingsRef.current = quoteConstructionSettings;
  const [constructionRevisionReview, setConstructionRevisionReview] = useState(null);
  const [quoteFullnessOverrideInputs, setQuoteFullnessOverrideInputs] =
    useState({});
  const [curtainTotalsSettingsEditVersion, setCurtainTotalsSettingsEditVersion] =
    useState(0);
  const summaryDiscountModeRef = useRef(summaryDiscountMode);
  const summaryDiscountValueRef = useRef(summaryDiscountValue);
  const curtainGstModeRef = useRef(curtainGstMode);
  const curtainGstRateRef = useRef(curtainGstRate);
  const quoteFullnessOverridesRef = useRef(quoteFullnessOverrides);
  const curtainSummaryTotalsRef = useRef(null);

  /* Rows */
  const [rows, setRows] = useState(() => [createDefaultCurtainQuoteRow("W1")]);
  const rowsRef = useRef(rows);
  const deletedCurtainRowIdsRef = useRef([]);
  const currentQuoteEditVersionRef = useRef(0);
  const lastQuoteFieldMutationRef = useRef(null);
  const [quoteDataIntegrityWatch, setQuoteDataIntegrityWatch] = useState(null);
  const [legacyRecalculationModalOpen, setLegacyRecalculationModalOpen] =
    useState(false);
  const [legacyRecalculationPreview, setLegacyRecalculationPreview] =
    useState(null);
  const [pendingRecalculationAudit, setPendingRecalculationAudit] =
    useState(null);
  const [pendingLegacyCalculationSnapshot, setPendingLegacyCalculationSnapshot] =
    useState(null);
  const [legacyRecalculationRevisionRequest, setLegacyRecalculationRevisionRequest] =
    useState(null);
  const legacyRecalculationRevisionSaveRef = useRef("");
  const legacyCurtainRecalculationImpactRef = useRef({
    shouldShowBanner: false,
    recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
  });
  const saveQuotationAsNewRef = useRef(null);


// ================= REPEAT SETTINGS (PER QUOTE / PER FABRIC) =================
const [repeatSettings, setRepeatSettings] = useState({});
const repeatSettingsRef = useRef(repeatSettings);
const [fabricSummaryMeta, setFabricSummaryMeta] = useState({});
const fabricSummaryMetaRef = useRef(fabricSummaryMeta);
const fabricPhotoLibraryCacheRef = useRef({});
const fabricPhotoLibraryLoadingRef = useRef({});
const [fabricPhotoLibraryRevision, setFabricPhotoLibraryRevision] = useState(0);
const [fabricPhotoUploadingKey, setFabricPhotoUploadingKey] = useState("");
const [fabricPhotoRemovingKey, setFabricPhotoRemovingKey] = useState("");
const [fabricPhotoQrCreatingKey, setFabricPhotoQrCreatingKey] = useState("");
const [fabricPhotoQrModal, setFabricPhotoQrModal] = useState(
  FABRIC_PHOTO_QR_MODAL_INITIAL
);
const [fabricPhotoQrCountdownMs, setFabricPhotoQrCountdownMs] = useState(0);
const handledFabricPhotoQrSessionRef = useRef(new Set());
const [fabricBrands, setFabricBrands] = useState([]);
const fabricBrandsRef = useRef([]);
const [fabricBrandsLoading, setFabricBrandsLoading] = useState(false);
const [fabricBrandDraft, setFabricBrandDraft] = useState({
  name: "",
  supplier: "",
  notes: "",
});
const fabricBrandsLoadedWorkspaceRef = useRef("");
const fabricBrandsLoadingWorkspaceRef = useRef("");
const [expandedFabricBrandId, setExpandedFabricBrandId] = useState("");
const [fabricCatalogueLibraryByBrand, setFabricCatalogueLibraryByBrand] =
  useState({});
const fabricCatalogueLibraryRef = useRef({});
const fabricCatalogueLoadedBrandKeysRef = useRef(new Set());
const fabricCatalogueLoadingBrandKeysRef = useRef(new Set());
const fabricCatalogueAllBrandsLoadedWorkspaceRef = useRef("");
const fabricCatalogueAllBrandsLoadingWorkspaceRef = useRef("");
const curtainRowFabricCatalogueSavePatchRef = useRef(null);
const [fabricCatalogueDrafts, setFabricCatalogueDrafts] = useState({});
const [fabricCatalogueEditDrafts, setFabricCatalogueEditDrafts] = useState({});
const [fabricSrRuleDrafts, setFabricSrRuleDrafts] = useState({});
const [fabricCatalogueSavingKey, setFabricCatalogueSavingKey] = useState("");
const [activeFabricSuggestionCell, setActiveFabricSuggestionCell] = useState("");
const [fabricSuggestionKeyboardIndex, setFabricSuggestionKeyboardIndex] =
  useState(-1);
const [fabricSuggestionPosition, setFabricSuggestionPosition] = useState(null);
const fabricSrInputRefs = useRef({});
const [useHalfPieceReuse, setUseHalfPieceReuse] = useState(true);
const [passHalfPieceSavingsToCustomer, setPassHalfPieceSavingsToCustomer] = useState(false);
const useHalfPieceReuseRef = useRef(useHalfPieceReuse);
const passHalfPieceSavingsToCustomerRef = useRef(passHalfPieceSavingsToCustomer);
const [halfPieceAuditOpen, setHalfPieceAuditOpen] = useState(false);
const orderedFabricAuditSignatureRef = useRef("");

summaryDiscountModeRef.current = summaryDiscountMode;
summaryDiscountValueRef.current = summaryDiscountValue;
curtainGstModeRef.current = curtainGstMode;
curtainGstRateRef.current = curtainGstRate;
quoteFullnessOverridesRef.current = quoteFullnessOverrides;
repeatSettingsRef.current = repeatSettings;
fabricSummaryMetaRef.current = fabricSummaryMeta;
useHalfPieceReuseRef.current = useHalfPieceReuse;
passHalfPieceSavingsToCustomerRef.current = passHalfPieceSavingsToCustomer;

useEffect(() => {
  const closeFabricSuggestions = () => {
    setActiveFabricSuggestionCell("");
    setFabricSuggestionKeyboardIndex(-1);
    setFabricSuggestionPosition(null);
  };

  const handlePointerDown = (event) => {
    const target = event.target;
    if (
      target &&
      typeof target.closest === "function" &&
      target.closest('[data-fabric-combobox-root="true"]')
    ) {
      return;
    }
    closeFabricSuggestions();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      closeFabricSuggestions();
    }
  };

  document.addEventListener("mousedown", handlePointerDown);
  document.addEventListener("touchstart", handlePointerDown);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("mousedown", handlePointerDown);
    document.removeEventListener("touchstart", handlePointerDown);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, []);

  // Effective rows after leftover engine
  const [effectiveRows, setEffectiveRows] = useState([]);
  const [, setLeftoverUsageLog] = useState([]);
  const [editingQuoteId, setEditingQuoteId] = useState(null);

  const normalizeRowsForState = useCallback(
    (sourceRows = []) =>
      (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
        withCurtainRowId({ ...(row || {}) }, index)
      ),
    []
  );

  const commitCurtainRows = useCallback(
    (updater, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.UNKNOWN) => {
      setRows((previousRows) => {
        const previousNormalized = normalizeRowsForState(previousRows);
        const proposedRows =
          typeof updater === "function" ? updater(previousNormalized) : updater;
        const nextRows = normalizeRowsForState(proposedRows);
        const mutationEvents = auditQuotationRowMutation(
          previousNormalized,
          nextRows,
          source
        );
        const latestEvent = mutationEvents[mutationEvents.length - 1] || null;

        if (
          mutationEvents.length > 0 &&
          source !== QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_SAVE
        ) {
          currentQuoteEditVersionRef.current += 1;
        }

        const signature = getCurtainRowsSignature(nextRows);
        rowsRef.current = nextRows;

        if (typeof window !== "undefined") {
          window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ = signature;
          window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
        }

        if (latestEvent && isQuoteMutationAuditDevMode()) {
          lastQuoteFieldMutationRef.current = {
            ...latestEvent,
            rowSignature: signature,
            editVersion: currentQuoteEditVersionRef.current,
          };
          window.setTimeout(() => {
            setQuoteDataIntegrityWatch(lastQuoteFieldMutationRef.current);
          }, 0);
        }

        return nextRows;
      });
    },
    [normalizeRowsForState]
  );

  const replaceCurtainRows = useCallback(
    (nextRows, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE, context = {}) => {
      if (isQuoteMutationAuditDevMode()) {
        devWarn("QUOTE_ROWS_BULK_REPLACE", {
          source,
          previousCount: rowsRef.current?.length || 0,
          nextCount: Array.isArray(nextRows) ? nextRows.length : 0,
          quoteId: context.quoteId || selectedQuote?.id || "",
          stack: new Error().stack,
        });
      }
      commitCurtainRows(nextRows, source);
    },
    [commitCurtainRows, selectedQuote?.id]
  );

  const updateQuoteRow = useCallback(
    (rowId, patchOrUpdater, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) => {
      if (!rowId) return;
      commitCurtainRows((previousRows) =>
        previousRows.map((row, index) => {
          const stableRow = withCurtainRowId(row || {}, index);
          if (getWorkshopRowStableId(stableRow) !== rowId) return stableRow;

          const patch =
            typeof patchOrUpdater === "function"
              ? patchOrUpdater(stableRow)
              : patchOrUpdater || {};
          const touchedPatch =
            source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
              ? Object.keys(patch).reduce(
                  (acc, fieldName) => ({
                    ...acc,
                    [fieldName]: true,
                  }),
                  stableRow.touchedFields || {}
                )
              : stableRow.touchedFields || {};

          return withCurtainRowId(
            {
              ...stableRow,
              ...patch,
              touchedFields: touchedPatch,
              rowUpdatedAt: new Date().toISOString(),
            },
            index
          );
        }),
        source
      );
    },
    [commitCurtainRows]
  );

  const removeQuoteRow = useCallback(
    (rowId, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) => {
      if (!rowId) return;
      const currentRows = normalizeRowsForState(rowsRef.current || []);
      const hasTarget = currentRows.some(
        (row, index) =>
          getWorkshopRowStableId(withCurtainRowId(row || {}, index)) === rowId
      );
      if (!hasTarget) {
        if (isDevRuntime) {
          devWarn("REMOVE_CURTAIN_ROW_BLOCKED_TARGET_NOT_FOUND", {
            rowId,
            quoteId: editingQuoteId || selectedQuote?.id || "",
            quoteNo: quoteNo || selectedQuote?.quoteNo || "",
            availableIds: currentRows.map((row, index) =>
              getWorkshopRowStableId(withCurtainRowId(row || {}, index))
            ),
          });
        }
        return;
      }
      deletedCurtainRowIdsRef.current = uniqueValues([
        ...(deletedCurtainRowIdsRef.current || []),
        String(rowId),
      ]);
      commitCurtainRows(
        (previousRows) => {
          const nextRows = previousRows.filter((row, index) => {
            const stableRow = withCurtainRowId(row || {}, index);
            return getWorkshopRowStableId(stableRow) !== rowId;
          });
          if (previousRows.length > 1 && nextRows.length === 0) {
            if (isDevRuntime) {
              devWarn("BLOCKED_UNSAFE_CURTAIN_ROW_DELETE_ALL", {
                rowId,
                beforeCount: previousRows.length,
                afterCount: nextRows.length,
                quoteId: editingQuoteId || selectedQuote?.id || "",
                quoteNo: quoteNo || selectedQuote?.quoteNo || "",
              });
            }
            return previousRows;
          }
          return renumberCurtainRowsForOrder(nextRows);
        },
        source
      );
    },
    [commitCurtainRows, editingQuoteId, normalizeRowsForState, quoteNo, selectedQuote?.id, selectedQuote?.quoteNo]
  );

  const markCurtainTotalsSettingsEdited = useCallback((fieldName = "", rawValue = "") => {
    currentQuoteEditVersionRef.current += 1;
    setCurtainTotalsSettingsEditVersion((version) => version + 1);

    if (typeof window !== "undefined") {
      window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
    }

    if (typeof window !== "undefined" && isQuoteMutationAuditDevMode()) {
      lastQuoteFieldMutationRef.current = {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT,
        fieldName,
        nextValue: rawValue,
        timestamp: new Date().toISOString(),
        editVersion: currentQuoteEditVersionRef.current,
      };
      window.setTimeout(() => {
        setQuoteDataIntegrityWatch(lastQuoteFieldMutationRef.current);
      }, 0);
    }
  }, []);

  useEffect(() => {
    rowsRef.current = normalizeRowsForState(rows);
    const signature = getCurtainRowsSignature(rowsRef.current);
    if (typeof window !== "undefined") {
      window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ = signature;
      window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
    }
    if (isQuoteMutationAuditDevMode()) {
      devLog("QUOTE_ROW_SIGNATURE", {
        signature,
        rowCount: rowsRef.current.length,
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
  }, [normalizeRowsForState, rows]);

  useEffect(() => {
    if (!isQuoteMutationAuditDevMode() || typeof window === "undefined") return undefined;
    window.__FQ_UPDATE_QUOTE_ROW__ = updateQuoteRow;
    return () => {
      if (window.__FQ_UPDATE_QUOTE_ROW__ === updateQuoteRow) {
        delete window.__FQ_UPDATE_QUOTE_ROW__;
      }
    };
  }, [updateQuoteRow]);

  /* Saved quotes */
  const [savedQuotes, setSavedQuotes] = useState([]);
  const savedQuotesRef = useRef([]);
  savedQuotesRef.current = safeSavedQuoteArray(savedQuotes);
  const [deletedQuotes, setDeletedQuotes] = useState([]);
  const deletedQuotesRef = useRef([]);
  deletedQuotesRef.current = safeSavedQuoteArray(deletedQuotes);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedQuotesAccessError, setSavedQuotesAccessError] = useState(null);
  const [savedQuoteOpenError, setSavedQuoteOpenError] = useState(null);
  const [generalQuoteRecoveryState, setGeneralQuoteRecoveryState] = useState({
    running: false,
    error: "",
    quoteNo: "",
    sources: [],
    bestRecoverySource: null,
    checkedAt: "",
  });
  const [openingGeneralQuoteRecoveryKey, setOpeningGeneralQuoteRecoveryKey] = useState("");
  const [repairingLegacyGeneralQuoteKey, setRepairingLegacyGeneralQuoteKey] = useState("");
  const [savedQuoteActionBusyKey, setSavedQuoteActionBusyKey] = useState("");
  const [repairingSavedQuoteLinkId, setRepairingSavedQuoteLinkId] = useState("");
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [loadingRecycleBinQuotes, setLoadingRecycleBinQuotes] = useState(false);
  const [search, setSearch] = useState("");
  const [savedQuotesVisibleCount, setSavedQuotesVisibleCount] = useState(
    SAVED_QUOTES_PAGE_SIZE
  );
  const savedRepositoryRef = useRef(null);
  const savedRepositoryWorkspaceRef = useRef("");
  const savedSearchRepositoryRef = useRef(null);
  const savedSearchGenerationRef = useRef(0);
  const [savedSearchResult, setSavedSearchResult] = useState({ key: "", records: [], loading: false, error: "", hasMore: false });
  const [savedLookupResult, setSavedLookupResult] = useState({ key: "", records: [] });
  const savedLookupGenerationRef = useRef(0);
  const [savedRepositoryRefresh, setSavedRepositoryRefresh] = useState(0);
  const [crmAssignmentPending, setCrmAssignmentPending] = useState({});
  const crmAssignmentPendingRef = useRef(new Set());
    const [hasMoreSavedQuoteIndex, setHasMoreSavedQuoteIndex] = useState(false);
    const [, setHasSavedQuoteIndexCursor] = useState(false);
    const [loadingMoreSavedQuotes, setLoadingMoreSavedQuotes] = useState(false);
    const [savedQuotesPageError, setSavedQuotesPageError] = useState("");
    const savedQuotesPageBusyRef = useRef(false);
    const savedQuoteIndexCursorRef = useRef(null);
    const savedQuoteIndexOrderFieldRef = useRef("lastActivityAt");
    const savedQuoteIndexQueryModeRef = useRef({
      source: "deleted_false_lastActivityAt",
      filterField: "deleted",
      filterValue: false,
      orderField: "lastActivityAt",
      orderDirection: "desc",
    });
    const savedQuotesLocalPatchRevisionRef = useRef(0);
  const savedQuoteRepairBadgeTraceRef = useRef(new Set());
  const savedQuoteCardTitleTraceRef = useRef(new Set());
  const savedQuotesRecentLoadRef = useRef({
    workspaceUid: "",
    loading: false,
    loaded: false,
    promise: null,
  });
  const savedQuotesRecycleBinLoadRef = useRef({
    workspaceUid: "",
    loading: false,
    loaded: false,
    promise: null,
  });

  /* Logo upload */
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [, setUploadProgress] = useState(0);
  const [, setUploading] = useState(false);
  const [storageManagerState, setStorageManagerState] = useState({
    loading: false,
    error: "",
    files: [],
    usage: null,
    loadedAt: "",
  });

// NEW REQUIRED STATES (fixes your crash)
const [optimizerFabric, setOptimizerFabric] = useState("");
const optimizerUsesLiveRef = useRef(false);
const optimizerResultSourceRef = useRef(null);
const [rollOptions, setRollOptions] = useState([]);
const [supplierRollIncrement, setSupplierRollIncrement] = useState("");

const [cutPlan, setCutPlan] = useState([]);
const [optimizerFinalDemands, setOptimizerFinalDemands] = useState([]);

function getEffectiveRepeatForCurtainRow(row = {}, activeRepeatSettings = repeatSettings) {
  const productConfig = resolveProductConfig(row, productConfigs, fabricCalcSettings);
  return resolveProductRepeatSettings(
    row,
    { repeatSettings: activeRepeatSettings || {}, fabricKeyForRepeat: getCurtainRowFabricKey(row) },
    productConfig
  );
}

function fabricHasRepeat(fabricKey, sourceRows = [], activeRepeatSettings = repeatSettings) {
  const key = normalizeFabricKey(fabricKey);
  const rowsForFabric = (Array.isArray(sourceRows) ? sourceRows : []).filter(
    (row) => normalizeFabricKey(getCurtainRowFabricKey(row)) === key
  );

  if (rowsForFabric.length > 0) {
    return rowsForFabric.some((row) => {
      const repeat = getEffectiveRepeatForCurtainRow(row, activeRepeatSettings);
      return repeat.verticalEnabled === true || repeat.horizontalEnabled === true;
    });
  }

  const settings = normalizeCurtainRepeatEntry(activeRepeatSettings?.[key] || {});
  return settings.verticalEnabled === true || settings.horizontalEnabled === true;
}

const openQuoteSentPromptForPdf = async (targetQuoteType) => {
  const quoteContext = buildCurrentQuoteSentContext(targetQuoteType);
  const requestId = createClientId("quote-follow-up-prompt");
  quoteSentPromptRequestRef.current = requestId;
  const fallbackFollowUpDate = getQuoteFollowUpFallbackDate();
  const ownerOptions = getCrmQuoteOwnerOptions();
  const currentOwnerUid = auth.currentUser?.uid || user?.uid || "";
  const fallbackOwner = ownerOptions[0] || {
    value: currentOwnerUid,
    label: user?.displayName || user?.email || auth.currentUser?.email || "Workspace User",
  };
  const ownerUid =
    quoteContext.quote?.crm?.assignedToUserId ||
    quoteContext.quote?.assignedToUserId ||
    fallbackOwner.value ||
    "";
  const ownerName =
    quoteContext.quote?.crm?.assignedToNameSnapshot ||
    quoteContext.quote?.assignedToNameSnapshot ||
    getCrmQuoteOwnerName(ownerUid);

  setQuoteSentPromptState({
    open: true,
    promptRequestId: requestId,
    quoteType: targetQuoteType,
    quoteContext,
    followUpDate: fallbackFollowUpDate,
    ownerUid,
    ownerName,
    note: "",
    createOpportunity: false,
    pdfGenerated: false,
    loading: false,
    error: "",
  });

  getQuoteFollowUpDefaultDate()
    .then((defaultFollowUpDate) => {
      if (!defaultFollowUpDate) return;
      setQuoteSentPromptState((prev) => {
        if (!prev.open || prev.promptRequestId !== requestId) return prev;
        if (prev.followUpDate && prev.followUpDate !== fallbackFollowUpDate) return prev;
        return {
          ...prev,
          followUpDate: defaultFollowUpDate,
        };
      });
    })
    .catch((error) => {
      if (isDevRuntime) {
        devWarn("CRM quote follow-up settings load failed after prompt opened", {
          code: error?.code,
          message: error?.message,
        });
      }
    });

  if (isDevRuntime) {
    devLog("CRM_QUOTE_SENT_PROMPT_TRACE", {
      quoteId: quoteContext.quoteId,
      quoteNo: quoteContext.quoteNo,
      customerName: quoteContext.customerName,
      opportunityId: quoteContext.opportunityId,
      hasExistingFollowUpTask: false,
      selectedFollowUpDate: fallbackFollowUpDate,
    });
  }
};

const exportCustomerPDF = (event) => {
  preventQuoteActionDefault(event);
  if (!operationalQuoteSettingsReady()) return;
  if (hasUnresolvedLiveFabricPricing(rowsRef.current || [], isFrozenLegacyCurtainQuote)) {
    notify("Resolve the fabric price or enter a manual rate before exporting the Customer PDF.", "warning");
    return;
  }
  traceQuoteActionClick({
    action: "curtainCustomerPdfClick",
    quoteType: CURTAIN_QUOTE_TYPE,
    event,
  });
  if (!canUseCustomerPdf) {
    alert("You do not have permission to export customer PDFs.");
    return;
  }

  openQuoteSentPromptForPdf(CURTAIN_QUOTE_TYPE);
};

const exportWorkshopPDF = (event) => {
  preventQuoteActionDefault(event);
  if (!operationalQuoteSettingsReady()) return;
  traceQuoteActionClick({
    action: "curtainWorkshopZipClick",
    quoteType: CURTAIN_QUOTE_TYPE,
    event,
  });
  if (!canUseWorkshopPdf) {
    alert("You do not have permission to export workshop PDFs.");
    return;
  }

  generateWorkshopZIP();
};
const tcoWorkerRef = useRef(null);
const crmContactSectionRef = useRef(null);
const crmAttachmentInputRef = useRef(null);
const dismissedDraftKeysRef = useRef(new Set());
const generalQuotePersistedSignatureRef = useRef("");
const generalQuoteStaleTotalsWarningRef = useRef("");
const curtainQuotePersistedSignatureRef = useRef("");
const crmLeadPersistedSignatureRef = useRef("");
const executionDetailsPersistedSignatureRef = useRef("");
const executionTemplatesDirtyRef = useRef(false);
const executionTemplatesLoadedWorkspaceRef = useRef("");
const executionTemplatesLoadingWorkspaceRef = useRef("");
const executionTemplatesRenderCountRef = useRef(0);
const crmDetailDraftDealIdRef = useRef("");
const crmDetailDraftDirtyRef = useRef(false);
const selectedCrmDealCacheRef = useRef(null);
const selectedCrmDealIdRef = useRef("");
const pendingChecklistFocusIdRef = useRef("");
const checklistInputRefs = useRef(new Map());
const executionTemplatePersistedSignatureRef = useRef("");
const toastTimersRef = useRef(new Map());
const quoteDealChoiceResolverRef = useRef(null);
const previousRouteRef = useRef({
  tab: "quote",
  quoteSubTab: CURTAIN_QUOTE_TYPE,
});
const [, setIsOptimizing] = useState(false);

const [optimizerQuoteId, setOptimizerQuoteId] = useState("");
const [optimizerQuote, setOptimizerQuote] = useState(null);
const [optimizerQuoteSearch, setOptimizerQuoteSearch] = useState("");
const [optimizerQuoteSearchState, setOptimizerQuoteSearchState] = useState({
  loading: false,
  error: "",
  results: [],
  searched: false,
});
const optimizerQuoteSearchCacheRef = useRef(new Map());
const optimizerQuoteSearchRequestRef = useRef(0);
const [optimizerQuoteLoadingId, setOptimizerQuoteLoadingId] = useState("");

const [selectedQuoteWorkStage, setSelectedQuoteWorkStage] = useState("quote_given");
const [selectedQuoteStatus, setSelectedQuoteStatus] = useState("draft");
const [selectedInstallationDate, setSelectedInstallationDate] = useState(null);

// ======== Fabric Stock Viewer State ========
const [fabricStock, setFabricStock] = useState([]);
const [loadingFabricStock, setLoadingFabricStock] = useState(false);

const [stockHistory, setStockHistory] = useState([]);
const [loadingStockHistory, setLoadingStockHistory] = useState(false);

const [stockHistorySearch, setStockHistorySearch] = useState("");
const [stockSearch, setStockSearch] = useState("");

const [isSaving, setIsSaving] = useState(false);
const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
const [quoteRowValidationError, setQuoteRowValidationError] = useState(null);
// idle | saving | saved | error
const [localDraftStatuses, setLocalDraftStatuses] = useState({
  [LOCAL_DRAFT_MODULES.GENERAL_QUOTE]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.CURTAIN_QUOTE]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.CRM_LEAD]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.EXECUTION_DETAILS]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE]: { state: "idle", savedAt: null },
});
const [draftRecoveryPrompt, setDraftRecoveryPrompt] = useState(null);


const [, setQuoteSettings] = useState({
  startNumber: "",
});

const [unit, setUnit] = useState(null);  // no default


// ================= FABRIC CALC SETTINGS =================
const [fabricCalcSettings, setFabricCalcSettings] = useState(() =>
  normalizeFabricCalcSettingsForState()
);


// ================= DASHBOARD PERIOD FILTER =================
const [periodType, setPeriodType] = useState("thisMonth"); 
const [customRange, setCustomRange] = useState({
  start: null,
  end: null,
});
 
// "month" | "lastMonth" | "3months" | "custom"

const [statusFilter, setStatusFilter] = useState("all"); // all | confirmed | draft
const [stageFilter, setStageFilter] = useState("all");   // all | specific stage
const [quoteTypeFilter, setQuoteTypeFilter] = useState("all"); // all | curtains | general
const [sortBy, setSortBy] = useState("repository");

const toDateInputValue = useCallback((value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "";

  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}, []);

const parseDateInputValue = useCallback((value) => {
  if (!value) return null;

  const [year, month, day] = String(value)
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}, []);

const getMonthKeyFromValue = useCallback((value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "";
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}, []);

const getMonthKeyFromDateParts = useCallback(
  (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
  []
);

const getRecentMonthKeys = useCallback((count = DASHBOARD_MONTHLY_CHART_MONTHS) => {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const offset = count - index - 1;
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return getMonthKeyFromDateParts(date.getFullYear(), date.getMonth());
  });
}, [getMonthKeyFromDateParts]);

const getPrimaryDashboardMonthKey = useCallback((type, range) => {
  const now = new Date();
  if (type === "lastMonth") {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return getMonthKeyFromDateParts(lastMonth.getFullYear(), lastMonth.getMonth());
  }
  if (type === "custom" && range?.start) {
    return getMonthKeyFromValue(parseDateInputValue(range.start)) || getMonthKeyFromValue(now);
  }
  return getMonthKeyFromValue(now);
}, [getMonthKeyFromDateParts, getMonthKeyFromValue, parseDateInputValue]);

const getDashboardAnalyticsMonthKeys = useCallback((type, range) => {
  const now = new Date();
  if (type === "thisYear") {
    return Array.from({ length: 12 }, (_, index) =>
      getMonthKeyFromDateParts(now.getFullYear(), index)
    );
  }
  if (type === "custom" && range?.start && range?.end) {
    const start = parseDateInputValue(range.start);
    const end = parseDateInputValue(range.end);
    if (!start || !end) return [getPrimaryDashboardMonthKey(type, range)];
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const endCursor = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= endCursor && keys.length < 12) {
      keys.push(getMonthKeyFromDateParts(cursor.getFullYear(), cursor.getMonth()));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys.length ? keys : [getPrimaryDashboardMonthKey(type, range)];
  }
  return [getPrimaryDashboardMonthKey(type, range)];
}, [
  getMonthKeyFromDateParts,
  getPrimaryDashboardMonthKey,
  parseDateInputValue,
]);

const parseTimeInputValue = (value) => {
  const normalized = String(value || "").trim();
  return /^\d{2}:\d{2}$/.test(normalized)
    ? normalized
    : DEFAULT_CRM_REMINDER_TIME;
};

const normalizeBooleanInput = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return undefined;
};

const buildCrmFollowUpActor = useCallback((actor = {}) => ({
  uid: String(actor?.uid || actor?.completedByUid || actor?.createdByUid || "").trim(),
  name: String(actor?.name || actor?.completedByName || actor?.createdByName || "").trim(),
  email: String(actor?.email || actor?.completedByEmail || actor?.createdByEmail || "").trim(),
}), []);

const createCrmFollowUpDraft = useCallback((overrides = {}) => {
  const id = String(overrides?.id || "").trim() || createClientId("crm-follow-up");
  const priority = String(overrides?.priority || "").trim().toLowerCase();
  const dueDate = toDateInputValue(overrides?.dueDate) || String(overrides?.dueDate || "");
  const status =
    overrides?.status === CRM_FOLLOW_UP_STATUS_DONE
      ? CRM_FOLLOW_UP_STATUS_DONE
      : CRM_FOLLOW_UP_STATUS_PENDING;
  const explicitReminderEnabled = normalizeBooleanInput(
    overrides?.reminderEnabled ?? overrides?.enabled
  );

  return {
    id,
    order: Number.isFinite(Number(overrides?.order)) ? Number(overrides.order) : 0,
    priority: CRM_FOLLOW_UP_PRIORITY_OPTIONS.some((option) => option.key === priority)
      ? priority
      : CRM_FOLLOW_UP_PRIORITY_MEDIUM,
    reminderId: String(overrides?.reminderId || "").trim() || id,
    title: String(overrides?.title || "").trim(),
    description: String(overrides?.description || "").trim(),
    dueDate,
    dueTime: parseTimeInputValue(overrides?.dueTime),
    repeat: String(overrides?.repeat || "none").trim() || "none",
    reminderEnabled:
      explicitReminderEnabled !== undefined
        ? explicitReminderEnabled
        : status !== CRM_FOLLOW_UP_STATUS_DONE && Boolean(dueDate),
    status,
    createdAt: overrides?.createdAt || null,
    updatedAt: overrides?.updatedAt || null,
    createdBy: buildCrmFollowUpActor(overrides?.createdBy),
    completedAt: overrides?.completedAt || null,
    completedBy: buildCrmFollowUpActor(overrides?.completedBy),
    attachments: normalizeCrmAttachments(overrides?.attachments),
  };
}, [buildCrmFollowUpActor]);

const combineReminderDateTime = useCallback((dateValue, timeValue) => {
  if (!dateValue) return null;

  const [year, month, day] = String(dateValue)
    .split("-")
    .map((part) => Number(part));
  const [hours, minutes] = parseTimeInputValue(timeValue)
    .split(":")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const parsed = new Date(
    year,
    month - 1,
    day,
    Number.isFinite(hours) ? hours : 9,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}, []);

const coerceReminderBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
    if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
  }
  return undefined;
};

const buildCanonicalInstallationReminder = useCallback(
  (source = {}, { installationDate = null, reminderConfigs = null } = {}) => {
    const configFromMap =
      reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      source?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      source?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      null;
    const raw =
      source?.installationReminder ||
      source?.crm?.installationReminder ||
      source?.reminderConfig ||
      source?.crm?.reminderConfig ||
      configFromMap ||
      {};
    const fallbackDate = toDateInputValue(installationDate);
    const reminderDate = String(
      raw?.reminderDate || raw?.date || configFromMap?.reminderDate || fallbackDate || ""
    );
    const reminderTime = parseTimeInputValue(
      raw?.reminderTime || raw?.time || configFromMap?.reminderTime || DEFAULT_CRM_REMINDER_TIME
    );
    const reminderEnabledValue = coerceReminderBoolean(
      raw?.reminderEnabled ?? configFromMap?.reminderEnabled
    );
    const enabledValue = coerceReminderBoolean(raw?.enabled ?? configFromMap?.enabled);
    const enabled =
      reminderEnabledValue !== undefined
        ? reminderEnabledValue
        : enabledValue !== undefined
        ? enabledValue
        : Boolean(reminderDate && (raw?.status || configFromMap?.status));
    const repeat = String(
      raw?.repeat || raw?.repeatKey || configFromMap?.repeat || configFromMap?.repeatKey || "daily"
    );
    const scheduledAt = combineReminderDateTime(reminderDate, reminderTime);
    const status =
      raw?.status === CRM_REMINDER_STATUS_DONE ||
      configFromMap?.status === CRM_REMINDER_STATUS_DONE
        ? CRM_REMINDER_STATUS_DONE
        : enabled
        ? CRM_REMINDER_STATUS_ACTIVE
        : CRM_REMINDER_STATUS_DISABLED;

    return {
      type: CRM_REMINDER_TYPE_INSTALLATION,
      enabled,
      reminderEnabled: enabled,
      reminderDate,
      reminderTime,
      repeat,
      repeatKey: repeat,
      status,
      completedAt: raw?.completedAt || configFromMap?.completedAt || null,
      completedBy: raw?.completedBy || configFromMap?.completedBy || null,
      nextNotifyAt:
        raw?.nextNotifyAt ||
        configFromMap?.nextNotifyAt ||
        (enabled && scheduledAt ? scheduledAt.toISOString() : null),
      lastNotifiedAt: raw?.lastNotifiedAt || configFromMap?.lastNotifiedAt || null,
      updatedAt: raw?.updatedAt || configFromMap?.updatedAt || new Date().toISOString(),
    };
  },
  [combineReminderDateTime]
);

const buildReminderDocId = useCallback((dealId, reminderType) =>
  `${String(dealId || "").trim()}__${String(reminderType || "").trim()}`, []);

const buildFollowUpReminderDocId = useCallback((dealId, followUp = {}) => {
  const reminderKey = String(
    followUp?.reminderId || followUp?.id || followUp || ""
  ).trim();
  return `${buildReminderDocId(dealId, CRM_REMINDER_TYPE_FOLLOW_UP)}__${reminderKey}`;
}, [buildReminderDocId]);

const sanitizeDueReminderIdPart = (value = "") =>
  String(value || "")
    .trim()
    .replace(DUE_REMINDER_ID_UNSAFE_CHARS, "_")
    .replace(/\s+/g, "_")
    .slice(0, 140);

const mapCrmReminderTypeToDueReminderType = (reminderType) => {
  if (reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) return DUE_REMINDER_TYPE_FOLLOW_UP;
  if (reminderType === CRM_REMINDER_TYPE_INSTALLATION) return DUE_REMINDER_TYPE_INSTALLATION;
  return DUE_REMINDER_TYPE_TASK;
};

const buildDueReminderDocId = useCallback((leadId, reminderType, sourceId = "") => {
  const safeLeadId = sanitizeDueReminderIdPart(leadId);
  const dueType = mapCrmReminderTypeToDueReminderType(reminderType);
  if (!safeLeadId) return "";

  if (dueType === DUE_REMINDER_TYPE_FOLLOW_UP) {
    const safeSourceId = sanitizeDueReminderIdPart(sourceId) || "followup";
    return `${safeLeadId}_${safeSourceId}`;
  }

  if (dueType === DUE_REMINDER_TYPE_INSTALLATION) {
    return `${safeLeadId}_installation`;
  }

  return `${safeLeadId}_task`;
}, []);

const buildDueReminderStatus = (enabled, sourceStatus, hasNextNotifyAt) => {
  if (sourceStatus === CRM_REMINDER_STATUS_DONE || sourceStatus === CRM_FOLLOW_UP_STATUS_DONE) {
    return DUE_REMINDER_STATUS_COMPLETED;
  }
  if (enabled === true && hasNextNotifyAt) return DUE_REMINDER_STATUS_PENDING;
  return DUE_REMINDER_STATUS_CANCELLED;
};

const formatReminderDateTime = (value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "-";

  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatAttachmentSize = (sizeBytes) => {
  const size = Number(sizeBytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentVisual = (attachment) => {
  const type = String(attachment?.contentType || "").toLowerCase();
  const filename = String(
    attachment?.name || attachment?.filename || ""
  ).toLowerCase();

  if (type.startsWith("image/")) {
    return { icon: FileImage, label: "Image", accent: "#2563eb" };
  }

  if (type === "application/pdf" || filename.endsWith(".pdf")) {
    return { icon: FileText, label: "PDF", accent: "#dc2626" };
  }

  if (
    filename.endsWith(".zip") ||
    filename.endsWith(".rar") ||
    filename.endsWith(".7z")
  ) {
    return { icon: FileArchive, label: "Archive", accent: "#7c3aed" };
  }

  return { icon: Paperclip, label: "File", accent: "#0f172a" };
};

const buildExecutionSpecAttributeRows = useCallback(
  (attributes = {}) =>
    Object.entries(attributes || {}).map(([key, value]) => ({
      id: createClientId("exec-field"),
      key: String(key || ""),
      value: value == null ? "" : String(value),
    })),
  []
);

const buildExecutionSpecDraftItem = useCallback(
  (item = {}) => {
    const normalized = createExecutionSpecItem(item);
    return {
      ...normalized,
      attributeRows: buildExecutionSpecAttributeRows(normalized.attributes),
    };
  },
  [buildExecutionSpecAttributeRows]
);

const hydrateExecutionSpecDraftItems = useCallback(
  (items = []) => normalizeExecutionSpecs(items).map((item) => buildExecutionSpecDraftItem(item)),
  [buildExecutionSpecDraftItem]
);

const serializeExecutionSpecDraftItems = useCallback(
  (items = []) =>
    normalizeExecutionSpecs(
      (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        attributes: (Array.isArray(item?.attributeRows) ? item.attributeRows : []).reduce(
          (accumulator, field) => {
            const nextKey = String(field?.key || "").trim();
            if (!nextKey) return accumulator;
            accumulator[nextKey] = String(field?.value || "");
            return accumulator;
          },
          {}
        ),
        attachments: normalizeExecutionSpecAttachments(item?.attachments),
      }))
    ).map((item) => ({
      ...item,
      createdAt: item.createdAt || new Date(),
      updatedAt: new Date(),
    })),
  []
);

const stampExecutionSpecs = useCallback((items = []) => {
  const stampedAt = new Date();
  return normalizeExecutionSpecs(items).map((item) => ({
    ...item,
    createdAt: item.createdAt || stampedAt,
    updatedAt: stampedAt,
  }));
}, []);

const formatExecutionItemType = useCallback((value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : "Custom Item";
}, []);

const mergeExecutionTemplateFieldsIntoItem = useCallback(
  (item, fields = []) => {
    const currentRows = Array.isArray(item?.attributeRows) ? item.attributeRows : [];
    const currentFieldIndexByKey = new Map();

    currentRows.forEach((row, index) => {
      const normalizedKey = String(row?.key || "").trim().toLowerCase();
      if (!normalizedKey) return;
      currentFieldIndexByKey.set(normalizedKey, index);
    });

    const nextRows = [...currentRows];
    (Array.isArray(fields) ? fields : []).forEach((field) => {
      const nextFieldKey = String(field?.key || field || "").trim();
      const nextFieldDefault =
        field?.defaultValue == null ? "" : String(field.defaultValue);
      if (!nextFieldKey) return;

      const normalizedFieldKey = nextFieldKey.toLowerCase();
      const existingIndex = currentFieldIndexByKey.get(normalizedFieldKey);

      if (existingIndex != null) {
        const existingField = nextRows[existingIndex];
        if (!String(existingField?.value || "").trim() && nextFieldDefault) {
          nextRows[existingIndex] = {
            ...existingField,
            key: existingField?.key || nextFieldKey,
            value: nextFieldDefault,
          };
        }
        return;
      }

      nextRows.push({
        id: createClientId("exec-field"),
        key: nextFieldKey,
        value: nextFieldDefault,
      });
    });

    return {
      ...item,
      itemType: formatExecutionItemType(item?.itemType),
      attributeRows: nextRows,
    };
  },
  [formatExecutionItemType]
);

const pickExecutionSpecAttributes = useCallback(
  (entries = {}) =>
    Object.entries(entries).reduce((accumulator, [key, value]) => {
      const nextValue = value == null ? "" : String(value).trim();
      if (!nextValue) return accumulator;
      accumulator[key] = nextValue;
      return accumulator;
    }, {}),
  []
);

const resolveExecutionSpecsForCrmDeal = useCallback((dealLike = {}) => {
  const linkedLeadId = String(
    dealLike?.leadId || dealLike?.crm?.leadId || ""
  ).trim();

  if (dealLike?.crmRecordType === "lead" || linkedLeadId) {
    return normalizeExecutionSpecs(dealLike?.executionSpecs || []);
  }

  return normalizeExecutionSpecs(
    dealLike?.executionSpecs || dealLike?.crm?.executionSpecs || []
  );
}, []);

const getSavedQuoteTotals = (q) => {
  const storedSubtotal = Number(q?.subtotal ?? q?.taxableAmount ?? q?.grandTotal ?? q?.totalAmount ?? 0);
  const storedGrandTotal = Number(
    q?.grandTotal ??
      q?.total ??
      q?.finalTotal ??
      q?.finalAmount ??
      q?.totalAmount ??
      0
  );
  if (resolveQuoteType(q) === GENERAL_QUOTE_TYPE) {
    const embeddedGeneral = getEmbeddedGeneralQuote(q);
    const hasGeneralItems =
      (Array.isArray(embeddedGeneral?.lineItems) && embeddedGeneral.lineItems.length > 0) ||
      (Array.isArray(embeddedGeneral?.items) && embeddedGeneral.items.length > 0);
    if (!hasGeneralItems && storedGrandTotal > 0) {
      return {
        subtotal: Number(storedSubtotal || storedGrandTotal || 0),
        discountAmount: Number(q?.discountAmount || 0),
        grandTotal: storedGrandTotal,
      };
    }
    const normalized = normalizeGeneralQuote(getEmbeddedGeneralQuote(q), {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });
    return calculateGeneralQuoteTotals(normalized);
  }

  const incoming = q.repeatSettings || {};
  if ((!Array.isArray(q.rows) || q.rows.length === 0) && storedGrandTotal > 0) {
    return {
      subtotal: Number(storedSubtotal || storedGrandTotal || 0),
      discountAmount: Number(q?.discountAmount || 0),
      grandTotal: storedGrandTotal,
    };
  }

  const localRepeatSettings = {};
  Object.keys(incoming).forEach((key) => {
    localRepeatSettings[normalizeFabricKey(key)] = incoming[key];
  });

  let subtotal = 0;
  for (const r of q.rows || []) {
    const c = calcRow(r, localRepeatSettings);
    subtotal += Number(c.rowTotal || 0);
  }

  const normalizedDiscount = normalizeQuoteDiscount(q, "saved_quote_totals");
  const quoteTotals = calculateCurtainQuoteTotals({
    rows: q.rows || [],
    subtotal,
    quoteGstMode: resolveCurtainQuoteGstMode(q),
    quoteGstRate: resolveCurtainQuoteGstRate(q),
    discountType: normalizedDiscount.discountType,
    discountValue: normalizedDiscount.discountValue,
    getRowCalculation: (row) => calcRow(row, localRepeatSettings),
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });

  return {
    subtotal: Number(subtotal.toFixed(2)),
    discountAmount: Number(quoteTotals.discountAmount || 0),
    totalAfterGst: Number(quoteTotals.totalAfterGst || 0),
    grandTotal: Number(quoteTotals.grandTotal || 0),
  };
};

const getNormalizedQuoteTotal = useCallback(
  (quote = {}) => {
    if (!quote) return 0;

    const quoteType = resolveQuoteType(quote);
    const calculatedGeneralTotal =
      quoteType === GENERAL_QUOTE_TYPE
        ? calculateGeneralQuoteTotals(
            normalizeGeneralQuote(getEmbeddedGeneralQuote(quote), {
              shopAddress,
              currency,
              businessName,
              businessPhone,
              businessEmail,
            })
          )?.grandTotal
        : null;
    const candidates = [
      calculatedGeneralTotal,
      getQuoteValue(quote),
      quote.grandTotal,
      quote.totalAmount,
      quote.finalAmount,
      quote.finalTotal,
      quote.approvedValue,
      quote.quoteValue,
      quote.linkedQuoteValue,
      quote.amount,
      quote.total,
      quote.subtotal,
      quote.crm?.finalValue,
      quote.crm?.expectedValue,
    ];

    const firstValid = candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0);

    return Number(firstValid || 0);
  },
  [businessEmail, businessName, businessPhone, currency, shopAddress]
);

const getQuoteTotalForAnalytics = useCallback(
  (quote = {}) => {
    const candidates = [
      ["grandTotal", quote.grandTotal],
      ["finalTotal", quote.finalTotal],
      ["total", quote.total],
      ["netTotal", quote.netTotal],
      ["quoteTotal", quote.quoteTotal],
      ["amount", quote.amount],
      ["totalAmount", quote.totalAmount],
      ["finalAmount", quote.finalAmount],
      ["approvedValue", quote.approvedValue],
      ["quoteValue", quote.quoteValue],
      ["linkedQuoteValue", quote.linkedQuoteValue],
      ["crm.finalValue", quote.crm?.finalValue],
      ["crm.expectedValue", quote.crm?.expectedValue],
      ["computed", getNormalizedQuoteTotal(quote)],
    ];

    for (const [source, value] of candidates) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return {
          amount: Number(numericValue.toFixed(2)),
          source,
          valid: true,
          issue: "",
        };
      }
    }

    return {
      amount: 0,
      source: "",
      valid: false,
      issue: "invalid_amount",
    };
  },
  [getNormalizedQuoteTotal]
);

const buildAnalyticsContributionForQuote = useCallback(
  (quote = {}) => {
    const quoteId = String(quote.id || quote.quoteId || quote.quoteNo || "").trim();
    if (!quoteId) return null;

    const customerNameForIndex = getQuoteCustomerLabel(quote);
    const normalizedPhone = normalizeSearchCompact(
      quote.customerPhone ||
        quote.customerWhatsapp ||
        quote.crm?.customerPhone ||
        quote.crm?.contact?.phone ||
        quote.generalQuote?.quoteFor?.phone ||
        ""
    );
    const customerKey = String(
      quote.customerId ||
        quote.crm?.customerId ||
        normalizedPhone ||
        normalizeSearchCompact(customerNameForIndex) ||
        "unknown"
    ).slice(0, 80);
    const issues = [];
    const totalResult = getQuoteTotalForAnalytics(quote);
    const totalAmount = Number(totalResult.amount || 0);
    if (!totalResult.valid) {
      issues.push("invalid_amount");
    }
    const quoteTypeResolved = resolveQuoteType(quote);
    const status = String(quote.status || "draft").trim().toLowerCase();
    const workStage = String(quote.workStage || quote.crm?.stageId || "quote_given").trim();
    const deleted = quote.deleted === true;
    const cancelled = isCancelledQuoteRecord(quote);
    const confirmed = isConfirmedQuoteRecord(quote) && !cancelled && !deleted;
    const installed =
      !deleted &&
      !cancelled &&
      ["installed", "completed", "closed_won"].includes(workStage.toLowerCase());
    const quoteDateKey = resolveQuoteDateForInput(quote);
    const createdAt = quote.createdAt || quote.createdOn || quote.createdDate || null;
    const confirmedAt =
      quote.confirmedAt || quote.crm?.confirmedAt || null;
    if (confirmed && !confirmedAt) {
      issues.push("confirmed_without_confirmedAt");
    }
    const installationDate =
      quote.installationDate ||
      quote.expectedInstallationDate ||
      quote.scheduledInstallationDate ||
      quote.installedAt ||
      quote.deliveryDate ||
      quote.crm?.installationDate ||
      null;
    const quoteMonthKey = quoteDateKey.slice(0, 7) || getMonthKeyFromValue(createdAt || quote.updatedAt);
    const confirmedMonthKey = confirmed ? getMonthKeyFromValue(confirmedAt) : "";
    const installationDateKey = toDateInputValue(installationDate);
    const installationMonthKey = installationDateKey.slice(0, 7);
    if (!quoteMonthKey) {
      issues.push("invalid_created_date");
    }
    if (confirmed && !confirmedMonthKey) {
      issues.push("invalid_confirmed_date");
    }
    if (installationDate && !installationDateKey) {
      issues.push("invalid_installation_date");
    }

    return {
      quoteId,
      quoteNo: quote.quoteNo || quoteId,
      quoteType: quoteTypeResolved,
      customerKey,
      customerId: quote.customerId || quote.crm?.customerId || "",
      customerName: customerNameForIndex,
      normalizedPhone,
      quoteDate: quoteDateKey,
      quoteMonthKey,
      confirmedMonthKey,
      installationMonthKey,
      installationDateKey,
      status,
      workStage,
      totalAmount,
      totalSource: totalResult.source,
      issues,
      needsRepair: issues.length > 0,
      quotationRevenue: deleted ? 0 : totalAmount,
      confirmedRevenue: confirmed && confirmedMonthKey ? totalAmount : 0,
      cancelledRevenue: cancelled ? totalAmount : 0,
      installedRevenue: installationMonthKey && installed ? totalAmount : 0,
      deleted,
      cancelled,
      confirmed,
      installed,
      hasInstallation: Boolean(installationDateKey),
      lastActivityAt: quote.updatedAt || quote.modifiedAt || confirmedAt || createdAt || new Date(),
      installationDate,
      itemSummary:
        quote.itemSummary ||
        quote.title ||
        quote.projectName ||
        quote.projectTitle ||
        getQuoteTypeLabel(quoteTypeResolved),
      location:
        quote.location ||
        quote.site ||
        quote.siteLocation ||
        quote.crm?.locationAddress ||
        quote.crm?.siteLocation ||
        "",
    };
  },
  [getMonthKeyFromValue, getQuoteTotalForAnalytics, toDateInputValue]
);

const applySummaryDelta = useCallback((current = {}, delta = {}) => ({
  ...current,
  confirmedRevenue: Number(current.confirmedRevenue || 0) + Number(delta.confirmedRevenue || 0),
  confirmedCount: Number(current.confirmedCount || 0) + Number(delta.confirmedCount || 0),
  quotationRevenue: Number(current.quotationRevenue || 0) + Number(delta.quotationRevenue || 0),
  quotationCount: Number(current.quotationCount || 0) + Number(delta.quotationCount || 0),
  cancelledRevenue: Number(current.cancelledRevenue || 0) + Number(delta.cancelledRevenue || 0),
  cancelledCount: Number(current.cancelledCount || 0) + Number(delta.cancelledCount || 0),
  installedRevenue: Number(current.installedRevenue || 0) + Number(delta.installedRevenue || 0),
  installedCount: Number(current.installedCount || 0) + Number(delta.installedCount || 0),
}), []);

const getMonthlyDeltaFromContribution = useCallback((contribution, sign = 1) => {
  const monthDeltas = new Map();
  const add = (monthKey, delta) => {
    if (!monthKey) return;
    monthDeltas.set(monthKey, applySummaryDelta(monthDeltas.get(monthKey), delta));
  };
  add(contribution.quoteMonthKey, {
    quotationRevenue: sign * Number(contribution.quotationRevenue || 0),
    quotationCount: sign * (contribution.deleted ? 0 : 1),
    cancelledRevenue: sign * Number(contribution.cancelledRevenue || 0),
    cancelledCount: sign * (contribution.cancelled ? 1 : 0),
  });
  add(contribution.confirmedMonthKey, {
    confirmedRevenue: sign * Number(contribution.confirmedRevenue || 0),
    confirmedCount: sign * (contribution.confirmed ? 1 : 0),
  });
  add(contribution.installationMonthKey, {
    installedRevenue: sign * Number(contribution.installedRevenue || 0),
    installedCount: sign * (contribution.installed ? 1 : 0),
  });
  return monthDeltas;
}, [applySummaryDelta]);

const dashboardPeriodLabel = useMemo(() => {
  switch (periodType) {
    case "today":
      return "Today";
    case "thisWeek":
      return "This Week";
    case "thisMonth":
      return "This Month";
    case "lastMonth":
      return "Last Month";
    case "thisYear":
      return "This Year";
    case "custom":
      return customRange.start && customRange.end
        ? `${customRange.start} to ${customRange.end}`
        : "Custom Range";
    default:
      return "Selected Period";
  }
}, [customRange.end, customRange.start, periodType]);

const dashboardAllQuotes = useMemo(() => {
  return (dashboardQuotePreview || [])
    .filter(Boolean)
    .map((quote) => {
      const createdMillis = getCreatedMillis(quote);
      const updatedMillis = getUpdatedMillis(quote);
      const confirmedMillis = getConfirmedMillis(quote);
      const installationMillis = getInstallationMillis(quote);
      const resolvedQuoteType = resolveQuoteType(quote);
      const workStageResolved = getQuoteStageValue(quote);
      const totalValue = getNormalizedQuoteTotal(quote);
      const customerLabel = getQuoteCustomerLabel(quote);
      const isDeleted = isDeletedQuoteRecord(quote);
      const isCancelled = isCancelledQuoteRecord(quote);
      const isConfirmed = isConfirmedQuoteRecord(quote);

      return {
        ...quote,
        customerLabel,
        quoteTypeResolved: resolvedQuoteType,
        workStageResolved,
        createdMillis,
        updatedMillis,
        confirmedMillis,
        installationMillis,
        totalValue,
        isDeleted,
        isCancelled,
        isConfirmed,
        isRevenueEligible: isRevenueQuote(quote),
      };
    })
    .filter((quote) => !quote.isDeleted)
    .sort((a, b) => {
      if (b.updatedMillis !== a.updatedMillis) {
        return b.updatedMillis - a.updatedMillis;
      }
      return b.createdMillis - a.createdMillis;
    });
}, [
  getNormalizedQuoteTotal,
  dashboardQuotePreview,
]);

const periodQuotes = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (!quote.createdMillis) return false;
    return isDateInPeriod(new Date(quote.createdMillis), periodType, customRange);
  });
}, [customRange, dashboardAllQuotes, periodType]);

const periodConfirmed = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (!quote.isConfirmed || quote.isCancelled || !quote.confirmedMillis) return false;
    return isDateInPeriod(new Date(quote.confirmedMillis), periodType, customRange);
  });
}, [customRange, dashboardAllQuotes, periodType]);

const draftQuotes = useMemo(
  () => dashboardAllQuotes.filter((quote) => !quote.isConfirmed && !quote.isCancelled),
  [dashboardAllQuotes]
);

const finalQuotes = useMemo(
  () => dashboardAllQuotes.filter((quote) => quote.isConfirmed && !quote.isCancelled),
  [dashboardAllQuotes]
);

const openPipelineQuotes = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (quote.isCancelled) return false;
    return !["installed", "completed"].includes(quote.workStageResolved);
  });
}, [dashboardAllQuotes]);

const todayStartMillis = useMemo(() => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
}, []);

const dashboardInstallationRecords = useMemo(
  () =>
    (dashboardAnalyticsState.installationRecords || [])
      .map((record) => {
        const installationDateKey =
          record.installationDateKey || toDateInputValue(record.installationDate);
        const installationMillis = safeTimeMillis(
          record.installationDate || installationDateKey
        );
        const quoteId = record.quoteId || record.jobId || record.id || "";
        const status = String(record.status || "").toLowerCase();
        const workStage = String(record.workStage || "").toLowerCase();

        return {
          ...record,
          id: quoteId,
          quoteId,
          customerLabel:
            record.customerLabel || record.customerName || "Unnamed Site",
          installationDateKey,
          installationMillis,
          totalValue: Number(record.totalAmount || record.totalValue || 0),
          isCancelled:
            record.deleted === true ||
            ["cancelled", "canceled", "lost"].includes(status) ||
            ["cancelled", "canceled", "lost"].includes(workStage),
        };
      })
      .filter((record) => record.id && record.installationDateKey && record.installationMillis),
  [dashboardAnalyticsState.installationRecords]
);

const upcomingInstallationQuotes = useMemo(() => {
  return dashboardInstallationRecords
    .filter(
      (quote) =>
        !quote.isCancelled &&
        quote.installationMillis &&
        quote.installationMillis >= todayStartMillis
    )
    .sort((a, b) => a.installationMillis - b.installationMillis);
}, [dashboardInstallationRecords, todayStartMillis]);

const upcomingInstallations = useMemo(
  () => upcomingInstallationQuotes.slice(0, 6),
  [upcomingInstallationQuotes]
);

const installationByDate = useMemo(() => {
  const map = {};

  dashboardInstallationRecords.forEach((quote) => {
    if (quote.isCancelled || !quote.installationDateKey) return;
    const key = quote.installationDateKey;

    if (!map[key]) map[key] = [];

    map[key].push(quote);
  });

  return map;
}, [dashboardInstallationRecords]);

const recentQuotes = useMemo(() => dashboardAllQuotes.slice(0, 6), [dashboardAllQuotes]);

const topCustomers = useMemo(() => {
  return (dashboardAnalyticsState.topClients || [])
    .map((customer) => ({
      name: customer.customerName || customer.name || "Unnamed Customer",
      customerId: String(customer.customerId || ""),
      phone: customer.normalizedPhone || customer.phone || "",
      value: Number(customer.confirmedRevenue || customer.quotationRevenue || customer.value || 0),
      count: Number(customer.confirmedCount || customer.quoteCount || customer.count || 0),
      records: [],
      monthKey: customer.monthKey || dashboardAnalyticsState.diagnostics?.monthKey || "",
    }))
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return b.count - a.count;
    })
    .slice(0, 5)
    .map((entry) => ({
      ...entry,
      value: Number(entry.value.toFixed(2)),
    }));
}, [dashboardAnalyticsState.diagnostics?.monthKey, dashboardAnalyticsState.topClients]);

const stageDistribution = useMemo(() => {
  const stageMap = new Map();

  dashboardAllQuotes.forEach((quote) => {
    const quoteType = resolveQuoteType(quote);
    const stageMeta = getCrmStage(
      crmPipelines,
      quoteType,
      quote.workStageResolved || quote.workStage
    );
    const key = `${quoteType}::${stageMeta?.id || quote.workStageResolved || "quote_given"}`;
    const current = stageMap.get(key) || {
      key,
      quoteType,
      stageId: stageMeta?.id || quote.workStageResolved || "quote_given",
      stageLabel: stageMeta?.label || "Quote Given",
      fullLabel: `${getQuoteTypeLabel(quoteType)} | ${stageMeta?.label || "Quote Given"}`,
      label: `${getQuoteTypeLabel(quoteType)} | ${stageMeta?.label || "Quote Given"}`,
      value: 0,
      color: stageMeta?.color || "#64748b",
    };
    current.value += 1;
    stageMap.set(key, current);
  });

  return Array.from(stageMap.values()).sort((a, b) => b.value - a.value);
}, [crmPipelines, dashboardAllQuotes]);

const quoteTypeDistribution = useMemo(() => {
  const curtainsCount = dashboardAllQuotes.filter(
    (quote) => quote.quoteTypeResolved === CURTAIN_QUOTE_TYPE
  ).length;
  const generalCount = dashboardAllQuotes.filter(
    (quote) => quote.quoteTypeResolved === GENERAL_QUOTE_TYPE
  ).length;

  return [
    { name: "Curtains", value: curtainsCount, color: "#4f46e5" },
    {
      name: "General Quotation",
      value: generalCount,
      color: "#0ea5e9",
    },
  ].filter((entry) => entry.value > 0);
}, [dashboardAllQuotes]);

const monthlyTimeSeries = useMemo(() => {
  const monthMap = {};
  const now = new Date();
  const summaries = dashboardAnalyticsState.monthlySummaries || {};

  for (let offset = 5; offset >= 0; offset--) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const summary = summaries[key] || {};
    monthMap[key] = {
      key,
      month: date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      }),
      created: Number(summary.quotationCount || 0),
      confirmed: Number(summary.confirmedCount || 0),
      revenue: Number(summary.confirmedRevenue || 0),
      quotationRevenue: Number(summary.quotationRevenue || 0),
      installedRevenue: Number(summary.installedRevenue || 0),
    };
  }

  return Object.values(monthMap).map((entry) => ({
    ...entry,
    revenue: Number(entry.revenue.toFixed(2)),
  }));
}, [dashboardAnalyticsState.monthlySummaries]);

const selectedDashboardMonthKeys = useMemo(
  () => getDashboardAnalyticsMonthKeys(periodType, customRange),
  [customRange, getDashboardAnalyticsMonthKeys, periodType]
);

const dashboardSummaryAggregate = useMemo(() => {
  const summaries = dashboardAnalyticsState.monthlySummaries || {};
  return selectedDashboardMonthKeys.reduce(
    (accumulator, monthKey) => {
      const summary = summaries[monthKey] || {};
      accumulator.quotationCount += Number(summary.quotationCount || 0);
      accumulator.quotationRevenue += Number(summary.quotationRevenue || 0);
      accumulator.confirmedCount += Number(summary.confirmedCount || 0);
      accumulator.confirmedRevenue += Number(summary.confirmedRevenue || 0);
      accumulator.cancelledCount += Number(summary.cancelledCount || 0);
      accumulator.cancelledRevenue += Number(summary.cancelledRevenue || 0);
      accumulator.installedCount += Number(summary.installedCount || 0);
      accumulator.installedRevenue += Number(summary.installedRevenue || 0);
      return accumulator;
    },
    {
      quotationCount: 0,
      quotationRevenue: 0,
      confirmedCount: 0,
      confirmedRevenue: 0,
      cancelledCount: 0,
      cancelledRevenue: 0,
      installedCount: 0,
      installedRevenue: 0,
    }
  );
}, [dashboardAnalyticsState.monthlySummaries, selectedDashboardMonthKeys]);

const dashboardAnalyticsStatus = dashboardAnalyticsState.diagnostics?.status || "idle";
const dashboardAnalyticsIndexMissing = dashboardAnalyticsStatus === "missing";
const dashboardAnalyticsError = dashboardAnalyticsStatus === "error";
const dashboardAnalyticsNotReady =
  dashboardAnalyticsIndexMissing ||
  dashboardAnalyticsError ||
  dashboardAnalyticsState.diagnostics?.source === "idle";

const conversionRate = dashboardSummaryAggregate.quotationCount
  ? Math.round((dashboardSummaryAggregate.confirmedCount / dashboardSummaryAggregate.quotationCount) * 100)
  : 0;

const avgQuoteValue = dashboardSummaryAggregate.quotationCount
  ? dashboardSummaryAggregate.quotationRevenue / dashboardSummaryAggregate.quotationCount
  : 0;

const avgConfirmedValue = dashboardSummaryAggregate.confirmedCount
  ? dashboardSummaryAggregate.confirmedRevenue / dashboardSummaryAggregate.confirmedCount
  : 0;

const businessOverviewDrilldowns = useMemo(
  () => ({
    quotesThisMonth: {
      key: "quotesThisMonth",
      label: `Quotes ${dashboardPeriodLabel}`,
      records: periodQuotes,
      count: dashboardSummaryAggregate.quotationCount,
      summaryValue: dashboardSummaryAggregate.quotationRevenue,
    },
    month: {
      key: "quotesThisMonth",
      label: `Quotes ${dashboardPeriodLabel}`,
      records: periodQuotes,
      count: dashboardSummaryAggregate.quotationCount,
      summaryValue: dashboardSummaryAggregate.quotationRevenue,
    },
    confirmedThisMonth: {
      key: "confirmedThisMonth",
      label: `Confirmed ${dashboardPeriodLabel}`,
      records: periodConfirmed,
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    confirmed: {
      key: "confirmedThisMonth",
      label: `Confirmed ${dashboardPeriodLabel}`,
      records: periodConfirmed,
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    revenueThisMonth: {
      key: "revenueThisMonth",
      label: `Revenue ${dashboardPeriodLabel}`,
      records: periodConfirmed.filter((quote) => quote.isRevenueEligible),
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    draftQuotes: {
      key: "draftQuotes",
      label: "Draft Quotes",
      records: draftQuotes,
      count: draftQuotes.length,
      summaryValue: null,
    },
    draft: {
      key: "draftQuotes",
      label: "Draft Quotes",
      records: draftQuotes,
      count: draftQuotes.length,
      summaryValue: null,
    },
    openPipeline: {
      key: "openPipeline",
      label: "Open Pipeline",
      records: openPipelineQuotes,
      count: openPipelineQuotes.length,
      summaryValue: null,
    },
    upcomingInstallations: {
      key: "upcomingInstallations",
      label: "Upcoming Installations",
      records: upcomingInstallationQuotes,
      count: upcomingInstallationQuotes.length,
      summaryValue: null,
    },
    activeQuotes: {
      key: "activeQuotes",
      label: "Recent Active Quotes",
      records: dashboardAllQuotes,
      count: dashboardAllQuotes.length,
      summaryValue: null,
    },
    final: {
      key: "finalQuotes",
      label: "Final Quotes",
      records: finalQuotes,
      count: finalQuotes.length,
      summaryValue: null,
    },
  }),
  [
    dashboardAllQuotes,
    dashboardPeriodLabel,
    dashboardSummaryAggregate,
    draftQuotes,
    finalQuotes,
    openPipelineQuotes,
    periodConfirmed,
    periodQuotes,
    upcomingInstallationQuotes,
  ]
);

const activeBusinessDashboardDrilldown = businessDashboardDrilldownKey
  ? businessOverviewDrilldowns[businessDashboardDrilldownKey] || null
  : null;

const activeBusinessDashboardDrilldownIds = useMemo(() => {
  if (!activeBusinessDashboardDrilldown) return null;
  return new Set(
    (activeBusinessDashboardDrilldown.records || [])
      .map((quote) => quote?.id)
      .filter(Boolean)
  );
}, [activeBusinessDashboardDrilldown]);

const dashboardListQuotes = useMemo(() => {
  if (!dashView || dashView === "home") {
    return [];
  }

  return businessOverviewDrilldowns[dashView]?.records || [];
}, [businessOverviewDrilldowns, dashView]);

const activeDashboardListView =
  dashView && dashView !== "home"
    ? businessOverviewDrilldowns[dashView] || null
    : null;

const syncGeneralQuoteDefaults = useCallback(
  (quote) =>
    normalizeGeneralQuote(quote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    }),
  [shopAddress, currency, businessName, businessPhone, businessEmail]
);

const workspaceUid =
  currentAccount?.dataOwnerUid ||
  currentAccount?.ownerUid ||
  accountWorkspaceUid ||
  workspaceContext?.workspaceId ||
  auth.currentUser?.uid ||
  user?.uid ||
  "";
crmWorkspaceRef.current = workspaceUid;
useEffect(() => {
  crmSearchPagesRef.current.clear();
  setCrmLeadRecords([]);
  setCrmArchivePage({ rows: [], cursor: null, hasMore: false, loading: false, error: "", loaded: false });
}, [workspaceUid]);
useEffect(() => {
  setCrmDataRepairState((prev) => ({
    ...prev,
    quoteDiagnostic: null, unlinkedResults: [], unlinkedSelected: {}, unlinkedCursor: null, unlinkedHasMore: false,
    activityResults: [], activitySelected: {}, activityCursor: null, activityHasMore: false,
    activityConfirmation: null,
    quoteIndexMismatchResults: [], quoteIndexMismatchCursor: null, quoteIndexMismatchHasMore: false,
    duplicateDealResults: [], duplicateDealSelected: {}, duplicateDealsCursor: null, duplicateDealsHasMore: false,
    duplicateProjectResults: [], duplicateProjectSelected: {}, duplicateProjectsCursor: null, duplicateProjectsHasMore: false,
    projectStageResults: [], projectStageSelected: {}, projectStageCursor: null, projectStageHasMore: false,
    customerIndexSearchResults: [], customerIndexSelectedCustomer: null, customerIndexRepairResult: null, customerIndexRepairId: "",
    repairLogChecks: [],
  }));
}, [workspaceUid]);
const saveQuotationCanonicalCallable = useMemo(
  () => {
    const save = httpsCallable(functions, "saveQuotationCanonical");
    return payload => save(stripFirestoreSentinelsForState({ ...payload, quoteReaderVersion: QUOTE_READER_VERSION }));
  },
  []
);
const saveQuotationCanonicalV3Callable = useMemo(
  () => {
    const save = httpsCallable(functions, "saveQuotationCanonicalV3");
    return payload => {
      if (process.env.REACT_APP_PATTERN_WRITES_ENABLED === "false" && requiresPatternWriter(payload)) {
        const error = new Error(QUOTE_RECOVERY_PAUSED);
        error.code = 'quote/recovery-paused';
        throw error;
      }
      return save(stripFirestoreSentinelsForState({ ...payload, quoteReaderVersion: QUOTE_READER_VERSION }));
    };
  },
  []
);
const finalizeSavedQuoteCrmLinkCallable = useMemo(
  () => httpsCallable(functions, "finalizeSavedQuoteCrmLink"),
  []
);
  const persistNewQuotationWithTrialGuard = useCallback(
  async ({ quoteId, quotePayload, quoteIndexSource, mode = "create_new" }) => {
    const quoteIndexId = quoteId;
    const quotePath = workspaceUid && quoteId
      ? `users/${workspaceUid}/quotations/${quoteId}`
      : "";
    const quoteIndexPath = workspaceUid && quoteIndexId
      ? `users/${workspaceUid}/quote_index/${quoteIndexId}`
      : "";
    const isCurtainPayload =
      quotePayload?.canonicalContentField === CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD ||
      resolveQuoteType(quotePayload) === CURTAIN_QUOTE_TYPE ||
      resolveQuoteType(quoteIndexSource) === CURTAIN_QUOTE_TYPE;
    const generalPayloadForStorage = isCurtainPayload
      ? null
      : finalizeGeneralQuotePayloadForSave(quotePayload, {
          source: "persistNewQuotationWithTrialGuard.storage_input",
          saveMode: mode,
          quoteId,
          quoteNo: quotePayload?.quoteNo || quoteIndexSource?.quoteNo || quoteId,
          quoteTypeHint: GENERAL_QUOTE_TYPE,
        });
    const storagePlan = isCurtainPayload
      ? buildCurtainQuoteV3StorageForSave({
          payload: quotePayload,
          constructionSourceRows: rowsRef.current,
          quoteId,
          workspaceUid,
          saveMode: "create_new",
          action: "save",
          path: quotePath,
          allowDeleteFieldSentinels: false,
        })
      : {
          mainPayload: sanitizeQuotationForFirestore(generalPayloadForStorage) || generalPayloadForStorage || quotePayload,
          rowChunks: [],
          rowCount: extractGeneralQuoteItems(generalPayloadForStorage || quotePayload).length || 0,
          storageMode: generalPayloadForStorage?.rowStorageMode || quotePayload?.rowStorageMode || QUOTE_ROW_STORAGE_INLINE,
          mainSizeBytes: estimateFirestorePayloadSize(generalPayloadForStorage || quotePayload),
        };
    let quotePayloadForWrite = storagePlan.mainPayload;
    if (!isCurtainPayload) {
      const finalItems = extractGeneralQuoteItems(quotePayloadForWrite);
      const originalItems = extractGeneralQuoteItems(generalPayloadForStorage || quotePayload);
      quotePayloadForWrite = finalizeGeneralQuotePayloadForSave(
        finalItems.length
          ? quotePayloadForWrite
          : {
              ...(quotePayloadForWrite || {}),
              items: originalItems,
              lineItems: originalItems,
            },
        {
          source: "persistNewQuotationWithTrialGuard.final_write",
          saveMode: mode,
          quoteId,
          quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSource?.quoteNo || quoteId,
          quoteTypeHint: GENERAL_QUOTE_TYPE,
        }
      );
    }
    const generalItemCountForWrite = !isCurtainPayload
      ? extractGeneralQuoteItems(quotePayloadForWrite).length
      : 0;
    const quoteIndexSourceForWrite = isCurtainPayload
      ? {
          ...(quoteIndexSource || {}),
          rowCount: storagePlan.rowCount,
          contentRowCount: storagePlan.rowCount,
          measurementRowCount: storagePlan.measurementRowCount,
          accessoryCount: storagePlan.accessoryCount,
          rowStorageMode: storagePlan.storageMode,
          storageVersion: QUOTE_V3_STORAGE_VERSION,
          saveRevisionId: storagePlan.saveRevisionId,
      }
      : {
          ...(quoteIndexSource || {}),
          itemCount: generalItemCountForWrite,
          contentItemCount: generalItemCountForWrite,
          canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
          contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        };
    const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
      {
        ...(quoteIndexSourceForWrite || quotePayloadForWrite || {}),
        id: quoteId,
        quoteId,
        latestQuoteId: quoteId,
        latestQuoteNo:
          quoteIndexSourceForWrite?.quoteNo || quotePayloadForWrite?.quoteNo || quoteId,
        sourceType: "quote_customer",
      },
      {
        sourceType: "quote_customer",
        workspaceUid,
      }
    );
    const quoteIndexPayloadForInvariant = buildQuoteIndexSavePayload(
      {
        ...(quoteIndexSourceForWrite || quotePayloadForWrite || {}),
        quoteIndexId,
        indexId: quoteIndexId,
        sourceQuoteId: quoteId,
      },
      quoteId,
      workspaceUid
    );
    assertCanonicalQuotePersistenceInvariant({
      quotePayload: quotePayloadForWrite,
      quoteIndexPayload: quoteIndexPayloadForInvariant,
      quoteTypeHint:
        quotePayloadForWrite?.normalizedQuoteType ||
        quotePayloadForWrite?.quoteType ||
        quoteIndexSourceForWrite?.quoteType ||
        "",
        saveMode: mode,
      quoteId,
      quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || quoteId,
      quotePath,
      quoteIndexPath,
    });
    devLog("QUOTE_SAVE_BEGIN", {
      buildLabel: FABRIQUOTE_BUILD_INFO.label,
      quoteType: quotePayloadForWrite?.quoteType || quoteIndexSourceForWrite?.quoteType || "",
      quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || "",
      saveMode: mode,
      userUid: auth.currentUser?.uid || user?.uid || "",
      workspaceUid,
      quoteId,
      quoteIndexId,
      customerName: quotePayloadForWrite?.customerName || quoteIndexSourceForWrite?.customerName || "",
      siteName: quotePayloadForWrite?.siteName || quoteIndexSourceForWrite?.siteName || "",
      itemsCount: quotePayloadForWrite?.generalQuote?.items?.length || quotePayloadForWrite?.items?.length || 0,
      rowsCount: storagePlan.rowCount || quotePayloadForWrite?.rows?.length || 0,
      rowStorageMode: storagePlan.storageMode || "",
      rowChunkCount: storagePlan.rowChunks?.length || 0,
      payloadSizeBytes: storagePlan.mainSizeBytes || 0,
      grandTotal: quotePayloadForWrite?.grandTotal || quoteIndexSourceForWrite?.grandTotal || 0,
    });
    devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
      step: "main_quote",
      path: quotePath,
      operation: "transaction.set",
      payloadKeys: Object.keys(quotePayloadForWrite || {}),
    });
    devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
      step: "quote_index",
      path: quoteIndexPath,
      operation: "transaction.set",
      payloadKeys: Object.keys(quoteIndexSourceForWrite || {}),
    });
    try {
      const saveOperation = isCurtainPayload
        ? saveQuotationCanonicalV3Callable({
            workspaceUid,
            mode,
            financialSourceQuoteId: selectedQuote?.quoteId || selectedQuote?.id || editingQuoteId || "",
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteId,
            quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || quoteId,
            quoteHeader: sanitizeCrmFirestoreValue({
              ...quotePayloadForWrite,
              createdAt: undefined,
              updatedAt: undefined,
            }),
            quoteIndexSource: sanitizeCrmFirestoreValue({
              ...quoteIndexSourceForWrite,
              rows: undefined,
              measurementRows: undefined,
              accessories: undefined,
              calculationSnapshot: undefined,
              createdAt: undefined,
              updatedAt: undefined,
            }),
            rows: sanitizeCrmFirestoreValue(storagePlan.rows || []),
            accessories: sanitizeCrmFirestoreValue(storagePlan.accessories || []),
            rowStorageMode: storagePlan.storageMode || "",
            saveRevisionId: storagePlan.saveRevisionId,
            customerSearchIndexWrite: customerSearchIndexWrite
              ? sanitizeCrmFirestoreValue({
                  personId: customerSearchIndexWrite.personId,
                  payload: customerSearchIndexWrite.payload,
                })
              : null,
          })
        : saveQuotationCanonicalCallable({
            workspaceUid,
            mode,
            quoteType: GENERAL_QUOTE_TYPE,
            quoteId,
            quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || quoteId,
            quotePayload: sanitizeCrmFirestoreValue({
              ...quotePayloadForWrite,
              createdAt: undefined,
              updatedAt: undefined,
            }),
            quoteIndexSource: sanitizeCrmFirestoreValue({
              ...quoteIndexSourceForWrite,
              createdAt: undefined,
              updatedAt: undefined,
            }),
            rowChunks: sanitizeCrmFirestoreValue(storagePlan.rowChunks || []),
            rowStorageMode: storagePlan.storageMode || "",
            customerSearchIndexWrite: customerSearchIndexWrite
              ? sanitizeCrmFirestoreValue({
                  personId: customerSearchIndexWrite.personId,
                  payload: customerSearchIndexWrite.payload,
                })
              : null,
          });
      const result = await withQuoteSaveTimeout(saveOperation, {
        step: "saveQuotationCanonical.callable",
      });
      const canonicalResult = result?.data || {};
      assertCanonicalSaveResponse({
        result: canonicalResult,
        quoteType: isCurtainPayload ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE,
        quoteId,
        quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || quoteId,
        sourcePath: quotePath,
        indexPath: quoteIndexPath,
      });
      devLog("QUOTE_SAVE_WRITE_SUCCESS", {
        step: "main_quote",
        path: canonicalResult.sourcePath || quotePath,
      });
      devLog("QUOTE_SAVE_WRITE_SUCCESS", {
        step: "quote_index",
        path: canonicalResult.indexPath || quoteIndexPath,
      });
      return canonicalResult;
    } catch (error) {
      emitQuoteSaveFailureTrace(
        buildQuoteSaveFailureTrace({
          quoteId,
          quoteNo: quotePayloadForWrite?.quoteNo || quoteIndexSourceForWrite?.quoteNo || quoteId,
          quoteType: isCurtainPayload ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE,
          workspaceUid,
          userUid: auth.currentUser?.uid || user?.uid || "",
          referralPartner:
            quotePayloadForWrite?.referralPartner ||
            quoteIndexSourceForWrite?.referralPartner ||
            null,
          step: error?.__fqRequiredSaveStep || "saveQuotationCanonical.callable",
          fullQuoteWriteStarted: true,
          quoteIndexWriteStarted: true,
          fullQuoteWriteSucceeded: false,
          quoteIndexWriteSucceeded: false,
          crmSyncStarted: false,
          crmSyncSucceeded: false,
          error,
        })
      );
      devError("QUOTE_SAVE_WRITE_FAILED", {
        step: "main_quote_or_quote_index_transaction",
        path: `${quotePath} + ${quoteIndexPath}`,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        payloadKeys: Object.keys(quotePayloadForWrite || {}),
      });
      throw error;
    }
  },
  [saveQuotationCanonicalCallable, saveQuotationCanonicalV3Callable, user?.uid, workspaceUid, selectedQuote, editingQuoteId]
);
const markDueReminderCompleted = useCallback(
  async ({
    leadId,
    reminderType,
    sourceId = "",
    crmReminderId = "",
    status = DUE_REMINDER_STATUS_COMPLETED,
  }) => {
    if (!workspaceUid || !leadId) return;

    const dueReminderId = buildDueReminderDocId(leadId, reminderType, sourceId);
    if (!dueReminderId) return;

    await setDoc(
      doc(db, "users", workspaceUid, "due_reminders", dueReminderId),
      {
        id: dueReminderId,
        workspaceUid,
        crmLeadId: String(leadId || ""),
        crmReminderId: String(crmReminderId || ""),
        status,
        reminderEnabled: false,
        notificationSent: false,
        completedAt: status === DUE_REMINDER_STATUS_COMPLETED ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },
  [buildDueReminderDocId, workspaceUid]
);
const updateLocalDraftStatus = useCallback((module, state, savedAt = null) => {
  setLocalDraftStatuses((prev) => ({
    ...prev,
    [module]: {
      state,
      savedAt:
        savedAt ?? (state === "autosaved" ? new Date().toISOString() : prev[module]?.savedAt ?? null),
    },
  }));
}, []);
const buildLocalDraftPayload = useCallback(
  (module, entityId, data, serverUpdatedAt = 0, metadata = {}) => ({
    version: LOCAL_DRAFT_VERSION,
    payloadVersion: LOCAL_DRAFT_VERSION,
    appVersion: process.env.REACT_APP_VERSION || process.env.npm_package_version || "",
    module,
    entityId: String(entityId || "new"),
    workspaceUid,
    updatedAt: new Date().toISOString(),
    savedAt: new Date().toISOString(),
    serverUpdatedAt: Number(serverUpdatedAt || 0),
    metadata,
    data,
  }),
  [workspaceUid]
);
const generalQuoteDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
          generalEditingQuoteId || "new"
        )
      : "",
  [generalEditingQuoteId, workspaceUid]
);
const curtainDraftEntityId = useMemo(
  () =>
    editingQuoteId ||
    (resolveQuoteType(selectedQuote) === CURTAIN_QUOTE_TYPE ? selectedQuote?.id : "") ||
    "new",
  [editingQuoteId, selectedQuote]
);
const curtainQuoteDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          curtainDraftEntityId || "new"
        )
      : "",
  [curtainDraftEntityId, workspaceUid]
);
const crmLeadDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.CRM_LEAD, "new")
      : "",
  [workspaceUid]
);
const executionDetailsDraftKey = useMemo(
  () =>
    workspaceUid && selectedCrmDealId
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
          selectedCrmDealId
        )
      : "",
  [selectedCrmDealId, workspaceUid]
);
const executionTemplatesDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE, "library")
      : "",
  [workspaceUid]
);
const serializeGeneralQuoteDraftData = useCallback(
  (quoteLike = generalQuote) => JSON.stringify(syncGeneralQuoteDefaults(quoteLike || {})),
  [generalQuote, syncGeneralQuoteDefaults]
);

const attachProductSnapshotsToCurtainRows = useCallback(
  (sourceRows = []) =>
    (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      const config = resolveProductConfig(
        stableRow,
        productConfigs,
        fabricCalcSettings
      );
      if (!config || shouldUseLegacyProductCalculation(stableRow, config)) {
        return stableRow;
      }

      return {
        ...stableRow,
        lineType:
          stableRow.lineType ||
          (isAccessoryProductConfig(config) ? "accessory" : "measurement"),
        category: stableRow.category || config.category || "",
        productId: stableRow.productId || config.id,
        productName: stableRow.productName || config.name,
        product: stableRow.product || config.name,
        productType: stableRow.productType || config.name,
        calculationType: stableRow.calculationType || config.calculationType,
        productConfigSnapshot:
          stableRow.productConfigSnapshot || buildProductConfigSnapshot(config),
      };
    }),
  [fabricCalcSettings, productConfigs]
);

const normalizeCurtainQuoteDraftData = useCallback(
  (draft = {}) => {
    const safeRows = Array.isArray(draft.rows)
      ? serializeCurtainRowsForDraft(draft.rows)
      : [createDefaultCurtainQuoteRow("W1")];
    const incomingRepeatSettings = draft.repeatSettings || {};
    const cleanedRepeatSettings = {};
    Object.keys(incomingRepeatSettings).forEach((key) => {
      cleanedRepeatSettings[normalizeFabricKey(key)] = incomingRepeatSettings[key];
    });
    const normalizedDiscount = normalizeQuoteDiscount(
      draft,
      "curtain_draft_normalize"
    );
    const normalizedGstRate = resolveCurtainQuoteGstRate(draft);
    const normalizedGstMode = resolveCurtainQuoteGstMode(draft);
    const normalizedQuoteDate = resolveQuoteDateForInput(
      draft,
      todayISO()
    );
    const draftReferralPartnerFields = buildReferralPartnerQuoteFields(
      normalizeQuoteReferralPartner(draft)
    );

    return {
      ...draft,
      ...draftReferralPartnerFields,
      customerName: String(draft.customerName || ""),
      quoteCustomerLink: {
        customerId: draft.quoteCustomerLink?.customerId || draft.customerId || "",
        customerKey:
          draft.quoteCustomerLink?.customerKey ||
          draft.customerKey ||
          draft.quoteCustomerLink?.customerId ||
          draft.customerId ||
          "",
        customerName:
          draft.quoteCustomerLink?.customerName || draft.customerName || "",
        phone: draft.quoteCustomerLink?.phone || draft.customerPhone || "",
        whatsapp:
          draft.quoteCustomerLink?.whatsapp || draft.customerWhatsapp || "",
        email: draft.quoteCustomerLink?.email || draft.customerEmail || "",
        address:
          draft.quoteCustomerLink?.address || draft.customerAddress || "",
        companyName:
          draft.quoteCustomerLink?.companyName ||
          draft.customerCompanyName ||
          "",
      },
      quoteSiteSelection: normalizeQuoteSiteSnapshot(
        draft.quoteSiteSelection || draft
      ),
      quoteNo: String(draft.quoteNo || ""),
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: safeRows,
      rowCount: safeRows.length,
      rowIds: safeRows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      repeatSettings: cleanedRepeatSettings,
      ...normalizeQuoteCalculationSettings(draft),
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(
        draft.quoteFullnessOverrides || draft.fullnessOverrides || {}
      ),
      fabricSummaryMeta: normalizeFabricSummaryMetaWithRepeatSettings(
        draft.fabricSummaryMeta,
        cleanedRepeatSettings
      ),
      discountType: normalizedDiscount.discountType,
      discountValue: normalizedDiscount.discountValue,
      quoteGstMode: normalizedGstMode,
      gstMode: normalizedGstMode,
      quoteGstRate: normalizedGstRate,
      gstRate: normalizedGstRate,
      gstModeLabel: getCurtainGstModeLabel(normalizedGstMode, normalizedGstRate),
      gstBreakdown: draft.gstBreakdown || draft.quoteSettings?.gstBreakdown || {},
      quoteSettings: {
        ...(draft.quoteSettings || {}),
        quoteGstMode: normalizedGstMode,
        quoteGstRate: normalizedGstRate,
        gstMode: normalizedGstMode,
      },
      gstAmount: round2(num(draft.gstAmount)),
      taxableAmount: round2(num(draft.taxableAmount)),
      finalTotal: round2(num(draft.finalTotal ?? draft.grandTotal)),
      calculationVersion:
        draft.calculationVersion || CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: draft.formulaCheckStatus || "current",
      summaryDiscountMode: getSummaryDiscountModeFromType(
        normalizedDiscount.discountType
      ),
      summaryDiscountValue: String(normalizedDiscount.discountValue),
      selectedQuoteWorkStage: draft.selectedQuoteWorkStage || "quote_given",
      selectedQuoteStatus: draft.selectedQuoteStatus || "draft",
      selectedInstallationDate: draft.selectedInstallationDate || null,
      useHalfPieceReuse: draft.useHalfPieceReuse !== false,
      passHalfPieceSavingsToCustomer: draft.passHalfPieceSavingsToCustomer === true,
      pendingCrmLeadId: draft.pendingCrmLeadId || "",
      quoteCustomerLinkSelection: draft.quoteCustomerLinkSelection || null,
      expandedCurtainRows: draft.expandedCurtainRows || {},
      settingsSnapshot: draft.settingsSnapshot || {},
      optimizer: draft.optimizer || {},
    };
  },
  []
);
const buildCurtainQuoteDraftData = useCallback(
  () => {
    const latestSummaryTotals = curtainSummaryTotalsRef.current || {};
    const curtainIdentity =
      resolveQuoteType(selectedQuote) === CURTAIN_QUOTE_TYPE
        ? getSavedQuoteWorkflowIdentity(selectedQuote || {}, workspaceUid)
        : {};
    return normalizeCurtainQuoteDraftData({
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: curtainDraftEntityId || "new",
      editingQuoteId: editingQuoteId || "",
      selectedQuoteId:
        resolveQuoteType(selectedQuote) === CURTAIN_QUOTE_TYPE
          ? selectedQuote?.id || ""
          : "",
      sourceQuoteId: curtainIdentity.sourceQuoteId || selectedQuote?.sourceQuoteId || "",
      fullQuoteId: curtainIdentity.fullQuoteId || selectedQuote?.fullQuoteId || "",
      quoteIndexId: curtainIdentity.quoteIndexId || selectedQuote?.quoteIndexId || "",
      indexId: curtainIdentity.quoteIndexId || selectedQuote?.indexId || "",
      sourceCollection: selectedQuote?.sourceCollection || "quotations",
      sourcePath: curtainIdentity.sourcePath || selectedQuote?.sourcePath || "",
      customerName,
      quoteCustomerLink,
      customerId: quoteCustomerLink.customerId || selectedQuote?.customerId || "",
      customerPhone: quoteCustomerLink.phone || selectedQuote?.customerPhone || "",
      customerWhatsapp:
        quoteCustomerLink.whatsapp || selectedQuote?.customerWhatsapp || "",
      customerEmail: quoteCustomerLink.email || selectedQuote?.customerEmail || "",
      customerAddress:
        quoteCustomerLink.address || selectedQuote?.customerAddress || "",
      customerCompanyName:
        quoteCustomerLink.companyName ||
        selectedQuote?.customerCompanyName ||
        "",
      ...buildReferralPartnerQuoteFields(
        selectedReferralPartnerRef.current ||
          normalizeQuoteReferralPartner(selectedQuote || {})
      ),
      quoteSiteSelection,
      ...normalizeQuoteSiteSnapshot(quoteSiteSelection),
      quoteNo,
      quoteDate: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      date: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      quotationDate: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      rows: serializeCurtainRowsForDraft(attachProductSnapshotsToCurtainRows(rows)),
      rowCount: rows.length,
      rowIds: rows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      repeatSettings,
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(quoteFullnessOverrides),
      ...quoteConstructionSettings,
      fabricSummaryMeta,
      summaryDiscountMode,
      summaryDiscountValue,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: num(summaryDiscountValue),
      quoteGstMode: curtainGstMode,
      gstMode: curtainGstMode,
      quoteGstRate: curtainGstRate,
      gstRate: curtainGstRate,
      gstModeLabel: getCurtainGstModeLabel(curtainGstMode, curtainGstRate),
      gstBreakdown: latestSummaryTotals.gstBreakdown || {},
      quoteSettings: {
        quoteGstMode: curtainGstMode,
        quoteGstRate: curtainGstRate,
        gstMode: curtainGstMode,
        gstBreakdown: latestSummaryTotals.gstBreakdown || {},
      },
      gstAmount: Number(latestSummaryTotals.gstAmount || 0),
      taxableAmount: Number(latestSummaryTotals.taxableAmount || 0),
      finalTotal: Number(latestSummaryTotals.finalTotal || latestSummaryTotals.grandTotal || 0),
      grandTotal: Number(latestSummaryTotals.grandTotal || latestSummaryTotals.finalTotal || 0),
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
      recalculationAudit:
        pendingRecalculationAudit === false
          ? null
          : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null,
      legacyCalculationSnapshot:
        pendingLegacyCalculationSnapshot === false
          ? null
          : pendingLegacyCalculationSnapshot ||
            selectedQuote?.legacyCalculationSnapshot ||
            null,
      selectedQuoteWorkStage,
      selectedQuoteStatus,
      selectedInstallationDate,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      calculationSettings: {
        ...(selectedQuote?.calculationSettings || {}),
        blindSqftRoundingMode: resolveBlindSqftRoundingMode(
          fabricCalcSettings,
          "exact"
        ),
      },
      pendingCrmLeadId,
      quoteCustomerLinkSelection: quoteCustomerLink,
      expandedCurtainRows,
      settingsSnapshot: {
        unit,
        currency,
        stitchingRates,
        minSqft,
        fabricCalcSettings,
      },
      optimizer: {
        optimizerQuoteId,
        selectedSolutionIndex,
      },
      selectedQuoteCrm: selectedQuote?.crm || null,
      linkedCrmLeadId: pendingCrmLeadId || selectedQuote?.crm?.leadId || "",
    });
  },
  [
    curtainDraftEntityId,
    curtainGstMode,
    curtainGstRate,
    attachProductSnapshotsToCurtainRows,
    currency,
    customerName,
    date,
    editingQuoteId,
    expandedCurtainRows,
    fabricCalcSettings,
    fabricSummaryMeta,
    minSqft,
    normalizeCurtainQuoteDraftData,
    optimizerQuoteId,
    pendingLegacyCalculationSnapshot,
    pendingRecalculationAudit,
    passHalfPieceSavingsToCustomer,
    pendingCrmLeadId,
    quoteCustomerLink,
    quoteSiteSelection,
    quoteNo,
    quoteFullnessOverrides,
    quoteConstructionSettings,
    repeatSettings,
    rows,
    selectedInstallationDate,
    selectedQuote,
    selectedQuoteStatus,
    selectedQuoteWorkStage,
    selectedSolutionIndex,
    stitchingRates,
    summaryDiscountMode,
    summaryDiscountValue,
    unit,
    useHalfPieceReuse,
    workspaceUid,
  ]
);
const serializeCurtainQuoteDraftData = useCallback(
  (draft = buildCurtainQuoteDraftData()) =>
    JSON.stringify(normalizeCurtainQuoteDraftData(draft || {})),
  [buildCurtainQuoteDraftData, normalizeCurtainQuoteDraftData]
);
const serializeCrmLeadDraftData = useCallback(
  (draft = crmLeadDraft) => JSON.stringify(draft || {}),
  [crmLeadDraft]
);
const serializeExecutionDetailsDraftData = useCallback(
  (draft = crmDetailDraft) => JSON.stringify(draft || {}),
  [crmDetailDraft]
);
const serializeExecutionTemplatesDraftData = useCallback(
  (templates = []) =>
    JSON.stringify(normalizeExecutionTemplateDrafts(templates || [])),
  []
);
const generalQuoteIsDirty = useMemo(
  () =>
    Boolean(generalQuotePersistedSignatureRef.current) &&
    serializeGeneralQuoteDraftData(generalQuote) !== generalQuotePersistedSignatureRef.current,
  [generalQuote, serializeGeneralQuoteDraftData]
);
const curtainQuoteIsDirty = useMemo(
  () =>
    tab === "quote" &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    Boolean(curtainQuotePersistedSignatureRef.current) &&
    serializeCurtainQuoteDraftData() !== curtainQuotePersistedSignatureRef.current,
  [quoteSubTab, serializeCurtainQuoteDraftData, tab]
);
const crmLeadDraftIsDirty = useMemo(
  () =>
    isCrmLeadModalOpen &&
    Boolean(crmLeadPersistedSignatureRef.current) &&
    serializeCrmLeadDraftData(crmLeadDraft) !== crmLeadPersistedSignatureRef.current,
  [crmLeadDraft, isCrmLeadModalOpen, serializeCrmLeadDraftData]
);
const executionDetailsDraftIsDirty = useMemo(
  () =>
    Boolean(selectedCrmDealId) &&
    Boolean(executionDetailsPersistedSignatureRef.current) &&
    serializeExecutionDetailsDraftData(crmDetailDraft) !==
      executionDetailsPersistedSignatureRef.current,
  [crmDetailDraft, selectedCrmDealId, serializeExecutionDetailsDraftData]
);
const executionTemplatesDraftIsDirty = useMemo(
  () =>
    Boolean(executionTemplatePersistedSignatureRef.current) &&
    serializeExecutionTemplatesDraftData(crmExecutionTemplates) !==
      executionTemplatePersistedSignatureRef.current,
  [crmExecutionTemplates, serializeExecutionTemplatesDraftData]
);
executionTemplatesDirtyRef.current = executionTemplatesDraftIsDirty;
const logWorkspaceQuery = useCallback((moduleName, collectionPath, count = null) => {
  if (!isDevRuntime) return;
  devLog("workspaceUid:", workspaceUid);
  devLog("module:", moduleName);
  devLog("collectionPath:", collectionPath);
  if (count != null) {
    devLog(`${moduleName} count:`, count);
  }
}, [workspaceUid]);
const markGeneralQuoteBaseline = useCallback(
  (quoteLike, nextState = "idle") => {
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(
      quoteLike
    );
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.GENERAL_QUOTE, nextState, null);
  },
  [serializeGeneralQuoteDraftData, updateLocalDraftStatus]
);
const markCurtainQuoteBaseline = useCallback(
  (draftLike = null, nextState = "idle") => {
    const normalizedDraft = normalizeCurtainQuoteDraftData(
      draftLike || buildCurtainQuoteDraftData()
    );
    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(normalizedDraft);
    setCurtainTotalsSettingsEditVersion(0);
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, nextState, null);
  },
  [
    buildCurtainQuoteDraftData,
    normalizeCurtainQuoteDraftData,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
  ]
);
const markCrmLeadBaseline = useCallback(
  (draftLike, nextState = "idle") => {
    crmLeadPersistedSignatureRef.current = serializeCrmLeadDraftData(draftLike);
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CRM_LEAD, nextState, null);
  },
  [serializeCrmLeadDraftData, updateLocalDraftStatus]
);
const markExecutionDetailsBaseline = useCallback(
  (draftLike, nextState = "idle") => {
    crmDetailDraftDirtyRef.current = false;
    executionDetailsPersistedSignatureRef.current =
      serializeExecutionDetailsDraftData(draftLike);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      nextState,
      null
    );
  },
  [serializeExecutionDetailsDraftData, updateLocalDraftStatus]
);
const markCrmDetailDraftDirty = useCallback(() => {
  crmDetailDraftDirtyRef.current = true;
  updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "dirty", null);
}, [updateLocalDraftStatus]);
const updateCrmDetailDraft = useCallback(
  (updater) => {
    markCrmDetailDraftDirty();
    setCrmDetailDraft((prev) => {
      if (!prev) return prev;
      return typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
    });
  },
  [markCrmDetailDraftDirty]
);
const markExecutionTemplatesBaseline = useCallback(
  (
    templatesLike = crmExecutionTemplates,
    selectedId = selectedExecutionTemplateManagerId || "library",
    nextState = "idle"
  ) => {
    executionTemplatePersistedSignatureRef.current =
      serializeExecutionTemplatesDraftData(templatesLike, selectedId);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      nextState,
      null
    );
  },
  [
    crmExecutionTemplates,
    selectedExecutionTemplateManagerId,
    serializeExecutionTemplatesDraftData,
    updateLocalDraftStatus,
  ]
);
const clearCrmLeadDraft = useCallback(() => {
  if (!crmLeadDraftKey) return;
  clearLocalDraft(crmLeadDraftKey);
}, [crmLeadDraftKey]);
const clearCurtainQuoteDraft = useCallback(
  (key = curtainQuoteDraftKey) => {
    if (!key) return;
    clearLocalDraft(key);
  },
  [curtainQuoteDraftKey]
);
const clearExecutionDetailsDraft = useCallback(() => {
  if (!executionDetailsDraftKey) return;
  clearLocalDraft(executionDetailsDraftKey);
}, [executionDetailsDraftKey]);
const clearExecutionTemplatesDraft = useCallback(() => {
  if (!executionTemplatesDraftKey) return;
  clearLocalDraft(executionTemplatesDraftKey);
}, [executionTemplatesDraftKey]);
const persistGeneralQuoteDraft = useCallback(
  (reason = "autosave") => {
    if (!generalQuoteDraftKey || !workspaceUid || !generalQuoteIsDirty) return false;

    const normalized = syncGeneralQuoteDefaults(generalQuote);
    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
      generalEditingQuoteId || "new",
      normalized,
      getUpdatedMillis(selectedQuote),
      {
        reason,
        quoteNo: normalized.quoteNo || "",
        customerLabel:
          normalized.quoteFor?.company || normalized.quoteFor?.name || normalized.title || "",
      }
    );

    saveLocalDraft(generalQuoteDraftKey, payload);
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(
      normalized
    );
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    generalEditingQuoteId,
    generalQuote,
    generalQuoteDraftKey,
    generalQuoteIsDirty,
    serializeGeneralQuoteDraftData,
    selectedQuote,
    syncGeneralQuoteDefaults,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistCurtainQuoteDraft = useCallback(
  (reason = "autosave") => {
    if (!curtainQuoteDraftKey || !workspaceUid || !curtainQuoteIsDirty) {
      return false;
    }

    const hasFrozenLegacyRows =
      quoteSubTab === CURTAIN_QUOTE_TYPE &&
      legacyCurtainRecalculationImpactRef.current?.shouldShowBanner === true &&
      !pendingRecalculationAudit &&
      !legacyRecalculationRevisionRequest;

    if (hasFrozenLegacyRows) {
      updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "paused", null);
      if (isDevRuntime) {
        devWarn("CURTAIN_LEGACY_AUTOSAVE_PAUSED", {
          reason,
          quoteId: curtainDraftEntityId || "new",
          quoteNo,
          rowCount: rows.length,
        });
      }
      return false;
    }

    const normalized = buildCurtainQuoteDraftData();
    const getCatalogueSavePatch = curtainRowFabricCatalogueSavePatchRef.current;
    const rowsForDraft = (normalized.rows || []).map((row) => ({
      ...row,
      ...(typeof getCatalogueSavePatch === "function"
        ? getCatalogueSavePatch(row)
        : {}),
    }));
    const normalizedForDraft = {
      ...normalized,
      rows: rowsForDraft,
      rowCount: rowsForDraft.length,
      rowIds: rowsForDraft.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      fabricSummaryMeta: sanitizeFabricSummaryMetaForValidCatalogues(
        normalized.fabricSummaryMeta,
        normalized.repeatSettings,
        rowsForDraft
      ),
    };
    if (getCurtainRowsSignature(rowsForDraft) !== getCurtainRowsSignature(normalized.rows || [])) {
      replaceCurtainRows(
        rowsForDraft,
        QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_SAVE,
        {
          quoteId: curtainDraftEntityId || "new",
          reason,
        }
      );
      setEffectiveRows(rowsForDraft);
    }
    if (isQuoteMutationAuditDevMode()) {
      devLog("AUTOSAVE_TRACE", {
        action: "save",
        reason,
        quoteId: curtainDraftEntityId || "new",
        draftUpdatedAt: new Date().toISOString(),
        currentDirty: curtainQuoteIsDirty,
        rowCount: normalizedForDraft.rows?.length || 0,
        rowSignature: getCurtainRowsSignature(normalizedForDraft.rows || []),
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      curtainDraftEntityId || "new",
      normalizedForDraft,
      getUpdatedMillis(selectedQuote),
      {
        reason,
        quoteId: curtainDraftEntityId || "new",
        quoteNo: normalizedForDraft.quoteNo || "",
        customerLabel:
          normalizedForDraft.quoteCustomerLink?.customerName ||
          normalizedForDraft.customerName ||
          "",
        rowCount: normalizedForDraft.rows.length,
        rowIds: normalizedForDraft.rows
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean),
      }
    );

    saveLocalDraft(curtainQuoteDraftKey, payload);
    if (isDevRuntime) {
      devLog("Curtain autosave draft rows", {
        savedRowCount: normalizedForDraft.rows.length,
        restoredRowCount: null,
        rowIds: normalizedForDraft.rows
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean),
      });
    }
    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(normalizedForDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildCurtainQuoteDraftData,
    buildLocalDraftPayload,
    curtainDraftEntityId,
    curtainQuoteDraftKey,
    curtainQuoteIsDirty,
    legacyRecalculationRevisionRequest,
    pendingRecalculationAudit,
    quoteNo,
    quoteSubTab,
    replaceCurtainRows,
    rows,
    selectedQuote,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const restoreCurtainQuoteDraft = useCallback(
  (draftRecord) => {
    const normalizedDraft = normalizeCurtainQuoteDraftData(draftRecord?.data || {});
    const getCatalogueSavePatch = curtainRowFabricCatalogueSavePatchRef.current;
    const restoredRows = (normalizedDraft.rows || []).map((row) => ({
      ...row,
      ...(typeof getCatalogueSavePatch === "function"
        ? getCatalogueSavePatch(row)
        : {}),
    }));
    const restoredDraft = {
      ...normalizedDraft,
      rows: restoredRows,
      rowCount: restoredRows.length,
      rowIds: restoredRows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      fabricSummaryMeta: sanitizeFabricSummaryMetaForValidCatalogues(
        normalizedDraft.fabricSummaryMeta,
        normalizedDraft.repeatSettings,
        restoredRows
      ),
    };
    const restoredRowIds = restoredDraft.rows
      .map((row) => getWorkshopRowStableId(row))
      .filter(Boolean);

    setTab("quote");
    setQuoteSubTab(CURTAIN_QUOTE_TYPE);
    setGeneralEditingQuoteId(null);
    setCustomerName(restoredDraft.customerName || "");
    setQuoteCustomerLink(restoredDraft.quoteCustomerLink);
    setQuoteSiteSelection(
      normalizeQuoteSiteSnapshot(restoredDraft.quoteSiteSelection || restoredDraft)
    );
    setQuoteNo(restoredDraft.quoteNo || "");
    setDate(resolveQuoteDateForInput(restoredDraft, todayISO()));
    if (isQuoteMutationAuditDevMode()) {
      devLog("AUTOSAVE_TRACE", {
        action: "restore",
        quoteId: restoredDraft.editingQuoteId || restoredDraft.quoteId || "new",
        draftUpdatedAt: draftRecord?.updatedAt || null,
        currentDirty: curtainQuoteIsDirty,
        rowCount: restoredDraft.rows?.length || 0,
        rowSignature: getCurtainRowsSignature(restoredDraft.rows || []),
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
    replaceCurtainRows(restoredDraft.rows, QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_RESTORE, {
      quoteId: restoredDraft.editingQuoteId || restoredDraft.quoteId || "",
    });
    setEffectiveRows(restoredDraft.rows);
    const restoredRepeatSettings = mergeRepeatSettingsFromFabricSummaryMeta(
      restoredDraft.repeatSettings || {},
      restoredDraft.fabricSummaryMeta || {}
    );
    const restoredFabricSummaryMeta = sanitizeFabricSummaryMetaForValidCatalogues(
      restoredDraft.fabricSummaryMeta,
      restoredRepeatSettings,
      restoredDraft.rows
    );
    setRepeatSettings(restoredRepeatSettings);
    setFabricSummaryMeta(restoredFabricSummaryMeta);
    setSummaryDiscountMode(restoredDraft.summaryDiscountMode === "%" ? "%" : "");
    setSummaryDiscountValue(restoredDraft.summaryDiscountValue || "0");
    const restoredGstMode = resolveCurtainQuoteGstMode(restoredDraft);
    const restoredGstRate = resolveCurtainQuoteGstRate(restoredDraft);
    curtainGstModeRef.current = restoredGstMode;
    curtainGstRateRef.current = restoredGstRate;
    setCurtainGstMode(restoredGstMode);
    setCurtainGstRate(restoredGstRate);
    const restoredQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      restoredDraft.quoteFullnessOverrides || {}
    );
    quoteFullnessOverridesRef.current = restoredQuoteFullnessOverrides;
    setQuoteConstructionSettings(normalizeQuoteCalculationSettings(restoredDraft));
    setQuoteFullnessOverrides(restoredQuoteFullnessOverrides);
    setQuoteFullnessOverrideInputs({});
    setSelectedQuoteWorkStage(
      restoredDraft.selectedQuoteWorkStage || "quote_given"
    );
    setSelectedQuoteStatus(restoredDraft.selectedQuoteStatus || "draft");
    setSelectedInstallationDate(restoredDraft.selectedInstallationDate || null);
    setUseHalfPieceReuse(restoredDraft.useHalfPieceReuse !== false);
    setPassHalfPieceSavingsToCustomer(
      restoredDraft.passHalfPieceSavingsToCustomer === true
    );
    setPendingCrmLeadId(restoredDraft.pendingCrmLeadId || "");
    setExpandedCurtainRows(restoredDraft.expandedCurtainRows || {});
    const restoredEditingQuoteId = restoredDraft.editingQuoteId || "";
    setEditingQuoteId(restoredEditingQuoteId || null);
    setCurtainQuoteEditorMode(
      restoredEditingQuoteId
        ? QUOTE_EDITOR_MODE_EDITING_EXISTING
        : QUOTE_EDITOR_MODE_NEW
    );
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setIsNewQuote(!restoredEditingQuoteId);

    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(restoredDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      "restored",
      draftRecord?.updatedAt || null
    );
    if (isDevRuntime) {
      devLog("Curtain autosave draft rows", {
        savedRowCount:
          Number(draftRecord?.metadata?.rowCount || draftRecord?.data?.rowCount || 0) ||
          restoredDraft.rows.length,
        restoredRowCount: restoredDraft.rows.length,
        rowIds: restoredRowIds,
      });
    }
  },
  [
    curtainQuoteIsDirty,
    normalizeCurtainQuoteDraftData,
    replaceCurtainRows,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
  ]
);
const persistCrmLeadDraftLocally = useCallback(
  (reason = "autosave") => {
    if (!crmLeadDraftKey || !workspaceUid || !isCrmLeadModalOpen || !crmLeadDraftIsDirty) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.CRM_LEAD,
      "new",
      crmLeadDraft || {},
      0,
      { reason }
    );
    saveLocalDraft(crmLeadDraftKey, payload);
    crmLeadPersistedSignatureRef.current = serializeCrmLeadDraftData(crmLeadDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CRM_LEAD,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmLeadDraft,
    crmLeadDraftIsDirty,
    crmLeadDraftKey,
    isCrmLeadModalOpen,
    serializeCrmLeadDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistExecutionDetailsDraftLocally = useCallback(
  (reason = "autosave") => {
    if (
      !executionDetailsDraftKey ||
      !workspaceUid ||
      !selectedCrmDealId ||
      !crmDetailDraft ||
      !executionDetailsDraftIsDirty
    ) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      selectedCrmDealId,
      crmDetailDraft,
      0,
      {
        reason,
        dealTitle: crmDetailDraft?.title || "",
      }
    );
    saveLocalDraft(executionDetailsDraftKey, payload);
    executionDetailsPersistedSignatureRef.current =
      serializeExecutionDetailsDraftData(crmDetailDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmDetailDraft,
    executionDetailsDraftIsDirty,
    executionDetailsDraftKey,
    selectedCrmDealId,
    serializeExecutionDetailsDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistExecutionTemplatesDraftLocally = useCallback(
  (reason = "autosave") => {
    if (
      !executionTemplatesDraftKey ||
      !workspaceUid ||
      !executionTemplatesDraftIsDirty
    ) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      selectedExecutionTemplateManagerId || "library",
      {
        selectedId: selectedExecutionTemplateManagerId || "",
        templates: normalizeExecutionTemplateDrafts(crmExecutionTemplates),
      },
      0,
      { reason }
    );
    saveLocalDraft(executionTemplatesDraftKey, payload);
    executionTemplatePersistedSignatureRef.current =
      serializeExecutionTemplatesDraftData(
        crmExecutionTemplates,
        selectedExecutionTemplateManagerId || "library"
      );
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmExecutionTemplates,
    executionTemplatesDraftIsDirty,
    executionTemplatesDraftKey,
    selectedExecutionTemplateManagerId,
    serializeExecutionTemplatesDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const promptToRestoreLocalDraft = useCallback(
  ({
    module,
    key,
    title,
    description,
    restoreLabel = "Restore",
    discardLabel = "Discard",
    serverNewerDescription =
      "A newer saved version is available. Restore this copy only if you want to continue from these earlier changes.",
    serverUpdatedAt = 0,
    onRestore,
  }) => {
    if (!key || dismissedDraftKeysRef.current.has(key)) return;

    const draftRecord = loadLocalDraft(key);
    const currentRoute = getCurrentBrowserPathname();
    const scopeDecision = getDraftRecoveryScopeDecision({
      module,
      draftRecord,
      currentRoute,
      activeTab: tab,
      activeQuoteSubTab: quoteSubTab,
    });

    if (isDevRuntime) {
      devLog("DRAFT_MODAL_SCOPE_TRACE", {
        route: currentRoute,
        activeModule: tab,
        draftType: module,
        hasDraft: scopeDecision.hasDraft,
        isMeaningful: scopeDecision.isMeaningful,
        modalAllowed: scopeDecision.modalAllowed,
        reason: scopeDecision.reason,
      });
    }

    if (!draftRecord) {
      consumeEmptyEditorRecoveryCheck(dismissedDraftKeysRef.current, key, draftRecord,
        isQuotationDraftModule(module) && isQuotationDraftRecoveryAllowed({
          currentRoute, activeModule: module, activeTab: tab, activeQuoteSubTab: quoteSubTab,
        }));
      return;
    }
    if (!scopeDecision.modalAllowed) {
      if (scopeDecision.clearDraft) {
        dismissedDraftKeysRef.current.add(key);
        clearLocalDraftRecord(key);
        updateLocalDraftStatus(module, "idle", null);
      }
      return;
    }
    const draftMeta = getDraftMeta(key);

    const serverIsNewer =
      Number(serverUpdatedAt || 0) > 0 &&
      Number(serverUpdatedAt || 0) >
        Number(draftMeta?.serverUpdatedAt || draftRecord.serverUpdatedAt || 0);

    setDraftRecoveryPrompt({
      key,
      module,
      title,
      description,
      restoreLabel,
      discardLabel,
      serverNewerDescription,
      serverIsNewer,
      savedAt: draftMeta?.savedAt || draftRecord.updatedAt || null,
      onRestore: () => {
        const latestDraft = loadLocalDraft(key);
        dismissedDraftKeysRef.current.add(key);
        setDraftRecoveryPrompt(null);
        if (!latestDraft) return;
        onRestore(latestDraft);
      },
      onDiscard: () => {
        dismissedDraftKeysRef.current.add(key);
        clearLocalDraftRecord(key);
        setDraftRecoveryPrompt(null);
        updateLocalDraftStatus(module, "idle", null);
      },
    });
  },
  [quoteSubTab, tab, updateLocalDraftStatus]
);

useEffect(() => {
  if (!draftRecoveryPrompt?.key) return;

  const draftRecord = loadLocalDraft(draftRecoveryPrompt.key);
  const currentRoute = getCurrentBrowserPathname();
  const scopeDecision = getDraftRecoveryScopeDecision({
    module: draftRecoveryPrompt.module,
    draftRecord,
    currentRoute,
    activeTab: tab,
    activeQuoteSubTab: quoteSubTab,
  });

  if (isDevRuntime) {
    devLog("DRAFT_MODAL_SCOPE_TRACE", {
      route: currentRoute,
      activeModule: tab,
      draftType: draftRecoveryPrompt.module,
      hasDraft: scopeDecision.hasDraft,
      isMeaningful: scopeDecision.isMeaningful,
      modalAllowed: scopeDecision.modalAllowed,
      reason: scopeDecision.reason,
    });
  }

  if (scopeDecision.modalAllowed) return;

  if (scopeDecision.clearDraft) {
    dismissedDraftKeysRef.current.add(draftRecoveryPrompt.key);
    clearLocalDraftRecord(draftRecoveryPrompt.key);
    updateLocalDraftStatus(draftRecoveryPrompt.module, "idle", null);
  }
  setDraftRecoveryPrompt(null);
}, [
  draftRecoveryPrompt?.key,
  draftRecoveryPrompt?.module,
  quoteSubTab,
  tab,
  updateLocalDraftStatus,
]);
const getLocalDraftStatusText = useCallback(
  (module) => {
    const status = localDraftStatuses[module] || { state: "idle", savedAt: null };
    if (status.state === "dirty") return "Unsaved changes";
    if (status.state === "autosaved") {
      return `Autosaved locally${
        status.savedAt
          ? ` at ${new Date(status.savedAt).toLocaleTimeString("en-IN", {
              hour: "numeric",
              minute: "2-digit",
            })}`
          : ""
      }`;
    }
    if (status.state === "saved") return "Saved";
    if (status.state === "restored") return "Draft restored";
    if (status.state === "paused") {
      return "Autosave paused for legacy quote until you save as a new revision.";
    }
    return "Ready";
  },
  [localDraftStatuses]
);
const isWorkspaceOwner = workspaceContext?.role !== "staff";
const currentWorkspaceMember = useMemo(
  () =>
    findMatchingTeamMember(
      teamMembers,
      user,
      workspaceContext?.memberId || ""
  ),
  [teamMembers, user, workspaceContext?.memberId]
);
const currentWorkspaceRawPermissions = useMemo(
  () => ({
    ...(workspaceContext?.permissions || {}),
    ...(currentWorkspaceMember?.permissions || {}),
  }),
  [currentWorkspaceMember?.permissions, workspaceContext?.permissions]
);
const currentWorkspacePermissions = useMemo(() => {
  if (isWorkspaceOwner) return OWNER_TEAM_PERMISSIONS;

  return normalizePermissionSet(
    workspaceContext?.permissions || currentWorkspaceMember?.permissions || {}
  );
}, [
  currentWorkspaceMember?.permissions,
  isWorkspaceOwner,
  workspaceContext?.permissions,
]);
const currentWorkspaceStatus = String(
  currentWorkspaceMember?.status || workspaceContext?.status || "active"
);
const isWorkspaceAccessRevoked =
  !isWorkspaceOwner &&
  (currentWorkspaceStatus === "disabled" ||
    currentWorkspaceMember?.isActive === false);
const canAccessTeamPermission = useCallback(
  (permissionKey) =>
    isWorkspaceOwner || hasFeatureAccess(currentWorkspacePermissions, permissionKey),
  [currentWorkspacePermissions, isWorkspaceOwner]
);
const canAccessFeature = useCallback(
  (featureKey) => hasActiveSubscription && hasFeature(featureKey),
  [hasActiveSubscription, hasFeature]
);
const planAllowsDashboard = canAccessFeature("dashboard");
const planAllowsCrm = canAccessFeature("crm");
const planAllowsGeneralQuotes = canAccessFeature("generalQuotes");
const planAllowsCurtainQuotes = canAccessFeature("curtainQuotes");
const planAllowsAnyQuotes = planAllowsGeneralQuotes || planAllowsCurtainQuotes;
const planAllowsCustomerPdf = canAccessFeature("customerPdf");
const planAllowsWorkshopPdf = canAccessFeature("workshopPdf");
const planAllowsSavedQuotes = canAccessFeature("savedQuotes");
const planAllowsOptimizer = canAccessFeature("rollOptimizer");
const planAllowsStitching = canAccessFeature("stitchingRates");
const planAllowsSettings = canAccessFeature("settings");
const canAccessTeamAccess =
  isSuperAdmin || ["owner", "admin"].includes(accountUser?.role || "");
const canViewQuotes =
  planAllowsAnyQuotes &&
  (isWorkspaceOwner ||
    canAccessTeamPermission("quotationsView") ||
    canAccessTeamPermission("quotationsCreate") ||
    canAccessTeamPermission("quotationsEdit"));
const canCreateQuotes =
  planAllowsAnyQuotes && (isWorkspaceOwner || canAccessTeamPermission("quotationsCreate"));
const canEditQuotes =
  planAllowsAnyQuotes && (isWorkspaceOwner || canAccessTeamPermission("quotationsEdit"));
const canUseCustomerPdf =
  planAllowsCustomerPdf && (isWorkspaceOwner || canAccessTeamPermission("customerPdf"));
const canUseWorkshopPdf =
  planAllowsWorkshopPdf && (isWorkspaceOwner || canAccessTeamPermission("workshopPdf"));
const canAccessSavedQuotes =
  planAllowsSavedQuotes && (isWorkspaceOwner || canAccessTeamPermission("savedQuotes"));
const canAccessDashboard =
  planAllowsDashboard && (isWorkspaceOwner || canAccessTeamPermission("dashboard"));
const canAccessCrm =
  planAllowsCrm && (isWorkspaceOwner || canAccessTeamPermission("crm"));
const canAccessOptimizer =
  planAllowsOptimizer && (isWorkspaceOwner || canAccessTeamPermission("optimizer"));
const canAccessStitching =
  planAllowsStitching && (isWorkspaceOwner || canAccessTeamPermission("stitchingRatesTab"));
const canAccessSettingsTab =
  planAllowsSettings && (isWorkspaceOwner || canAccessTeamPermission("settings"));
const currentCrmRole = normalizeCrmAccessRole(
  currentWorkspaceMember?.role ||
    accountUser?.member?.role ||
    workspaceContext?.role ||
    accountUser?.role ||
    ""
);
const isCrmOwnerAdminAccess = Boolean(
  isSuperAdmin ||
    crmRoleIn(currentCrmRole, CRM_OWNER_ADMIN_ROLES) ||
    (String(workspaceContext?.ownerUid || "").trim() &&
    String(workspaceContext?.ownerUid || "").trim() === String(user?.uid || "").trim() &&
      normalizeCrmAccessRole(workspaceContext?.role) !== "staff")
);
const crmAccessContext = useMemo(
  () => ({
    role: currentCrmRole,
    isSuperAdmin,
    isWorkspaceOwner: isCrmOwnerAdminAccess,
    canAccessCrm,
    canAccessSettingsTab,
    permissions: currentWorkspacePermissions,
    rawPermissions: currentWorkspaceRawPermissions,
  }),
  [
    canAccessCrm,
    canAccessSettingsTab,
    currentCrmRole,
    currentWorkspacePermissions,
    currentWorkspaceRawPermissions,
    isCrmOwnerAdminAccess,
    isSuperAdmin,
  ]
);
const canViewCrmTodayScreen = canViewCrmToday(crmAccessContext);
const canViewManagerCrmDashboard = canViewManagerCrmDashboardForAccess(crmAccessContext);
const canViewAttentionRequiredWorkspace =
  canViewAttentionRequiredWorkspaceForAccess(crmAccessContext);
const canViewPipelineReview = canViewPipelineReviewForAccess(crmAccessContext);
const canManageCrmSettings = canManageCrmSettingsForAccess(crmAccessContext);
const canUseCrmDataRepairTools = isSuperAdmin || (canAccessCrm && isCrmOwnerAdminAccess);
const isCrmRepairTestModeEnabled = typeof window !== "undefined" && (() => {
  try { return readRepairTestMode(window.localStorage); }
  catch (error) { return false; }
})();
const canUseCrmRepairTestDataTools = canUseRepairFixtures({
  allowed: canUseCrmDataRepairTools, isSuperAdmin, testModeEnabled: isCrmRepairTestModeEnabled,
});
const canViewAllCrmRecords = canViewAllCrmRecordsForAccess(crmAccessContext);
const canViewOwnCrmRecords = canViewOwnCrmRecordsForAccess(crmAccessContext);
const canAssignLeadOwnerAccess = canAssignLeadOwnerForAccess(crmAccessContext);
const canEditCrmTargetsAccess = canEditCrmTargetsForAccess(crmAccessContext);
const canViewCrmPaymentsAccess = canViewCrmPaymentsForAccess(crmAccessContext);
const canAddCrmPaymentAccess = canAddCrmPaymentForAccess(crmAccessContext);
const canScheduleProjectInstallationAccess =
  canScheduleProjectInstallationForAccess(crmAccessContext);
const canCloseCrmProjectAccess = canCloseCrmProjectForAccess(crmAccessContext);
const canRequestProjectReviewAccess = canRequestProjectReviewForAccess(crmAccessContext);
const crmCurrentUserKeys = useMemo(
  () =>
    [
      user?.uid,
      user?.email,
      user?.displayName,
      accountUser?.uid,
      accountUser?.email,
      accountUser?.name,
      accountUser?.displayName,
      currentWorkspaceMember?.uid,
      currentWorkspaceMember?.id,
      currentWorkspaceMember?.email,
      currentWorkspaceMember?.name,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean),
  [
    accountUser?.displayName,
    accountUser?.email,
    accountUser?.name,
    accountUser?.uid,
    currentWorkspaceMember?.email,
    currentWorkspaceMember?.id,
    currentWorkspaceMember?.name,
    currentWorkspaceMember?.uid,
    user?.displayName,
    user?.email,
    user?.uid,
  ]
);
const isCrmRecordVisibleToCurrentUser = useCallback(
  (record = {}) => {
    if (canViewAllCrmRecords) return true;
    if (!canViewOwnCrmRecords || !crmCurrentUserKeys.length) return false;
    const source = record.crmTask || record.original || record.raw || record.crm || {};
    const recordKeys = [
      record.assignedToUserId,
      record.assignedToUid,
      record.assignedTo,
      record.assignedToNameSnapshot,
      record.ownerUid,
      record.ownerName,
      record.ownerEmail,
      record.projectOwnerUid,
      record.projectOwnerName,
      record.projectOwnerEmail,
      record.salesOwnerUid,
      record.salesOwnerName,
      record.nextActionOwnerUid,
      record.nextActionOwnerName,
      record.createdBy,
      record.createdByUid,
      record.createdByEmail,
      source.assignedToUserId,
      source.assignedToUid,
      source.assignedTo,
      source.assignedToNameSnapshot,
      source.ownerUid,
      source.ownerName,
      source.ownerEmail,
      source.projectOwnerUid,
      source.projectOwnerName,
      source.projectOwnerEmail,
      source.salesOwnerUid,
      source.salesOwnerName,
      source.nextActionOwnerUid,
      source.nextActionOwnerName,
      source.createdBy,
      source.createdByUid,
      source.createdByEmail,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return recordKeys.some((value) => crmCurrentUserKeys.includes(value));
  },
  [canViewAllCrmRecords, canViewOwnCrmRecords, crmCurrentUserKeys]
);
const canUpdateCrmProjectExecution = useCallback(
  (projectLike = {}) =>
    canScheduleProjectInstallationAccess || isCrmRecordVisibleToCurrentUser(projectLike),
  [canScheduleProjectInstallationAccess, isCrmRecordVisibleToCurrentUser]
);
const canManageProductConfigs = canAccessSettingsTab || canAccessStitching;
const canRepairLegacyGeneralQuoteFormat =
  canEditQuotes && (isSuperAdmin || isWorkspaceOwner || canAccessSettingsTab);
const canAccessCurtainQuotes = planAllowsCurtainQuotes && canViewQuotes;
const canAccessGeneralQuotes = planAllowsGeneralQuotes && canViewQuotes;
const canUseSaveTraceRecorder = Boolean(
  IS_DEV_UI_ENABLED &&
    workspaceUid &&
    user?.uid &&
    (
      SAVE_TRACE_ENV_ENABLED ||
      isSuperAdmin ||
      isWorkspaceOwner ||
      ["owner", "admin", "super_admin"].includes(
        String(accountUser?.role || workspaceContext?.role || "").toLowerCase()
      )
    )
);
const activeSaveTraceRef = useRef(null);
const subscribeSavedQuotesRef = useRef(null);

const sanitizeSaveTraceText = useCallback((value, maxLength = 180) => {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  return text.slice(0, maxLength);
}, []);

const getSaveTraceTimestampType = useCallback((value) => {
  if (!value) return "";
  if (typeof value?.toDate === "function") return "Firestore Timestamp";
  if (value instanceof Date) return "Date";
  if (typeof value === "string") return "string";
  if (typeof value === "number") return "number";
  if (typeof value === "object" && value._methodName === "serverTimestamp") {
    return "serverTimestamp";
  }
  return typeof value;
}, []);

const sanitizeSaveTraceError = useCallback(
  (step, error, details = {}) => ({
    step: sanitizeSaveTraceText(step, 120),
    code: sanitizeSaveTraceText(error?.code || details.code || "", 120),
    message: sanitizeSaveTraceText(error?.message || details.message || String(error || ""), 280),
    path: sanitizeSaveTraceText(details.path || "", 260),
    payloadKeys: Array.isArray(details.payloadKeys)
      ? details.payloadKeys.map((key) => sanitizeSaveTraceText(key, 80)).slice(0, 80)
      : [],
  }),
  [sanitizeSaveTraceText]
);

const recordSaveTracePatch = useCallback(
  (traceId, patch = {}, options = {}) => {
    if (!canUseSaveTraceRecorder || !traceId || !workspaceUid) return;
    const traceRef = doc(db, "users", workspaceUid, SAVE_TRACE_COLLECTION, traceId);
    const sanitizedPatch = sanitizeCrmFirestoreValue(patch) || {};
    const payload = {
      ...sanitizedPatch,
      updatedAt: serverTimestamp(),
    };
    if (options.includeCreatedAt) {
      payload.createdAt = serverTimestamp();
    }
    setDoc(traceRef, payload, { merge: true }).catch((error) => {
      devWarn("SAVE_TRACE_WRITE_SKIPPED", {
        traceId,
        path: traceRef.path,
        code: error?.code || "",
        message: error?.message || String(error),
      });
    });
  },
  [canUseSaveTraceRecorder, workspaceUid]
);

const appendSaveTraceError = useCallback(
  (traceId, step, error, details = {}) => {
    if (!canUseSaveTraceRecorder || !traceId || !workspaceUid) return;
    const traceRef = doc(db, "users", workspaceUid, SAVE_TRACE_COLLECTION, traceId);
    const traceError = sanitizeSaveTraceError(step, error, details);
    setDoc(
      traceRef,
      {
        errors: arrayUnion(traceError),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((writeError) => {
      devWarn("SAVE_TRACE_ERROR_WRITE_SKIPPED", {
        traceId,
        step,
        path: traceRef.path,
        code: writeError?.code || "",
        message: writeError?.message || String(writeError),
      });
    });
  },
  [canUseSaveTraceRecorder, sanitizeSaveTraceError, workspaceUid]
);

const startSaveTrace = useCallback(
  ({ action, quoteType, eventGuard }) => {
    if (!canUseSaveTraceRecorder || !workspaceUid || !user?.uid) return "";
    const traceId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    activeSaveTraceRef.current = {
      traceId,
      startedAt: new Date().toISOString(),
      requiredWritesSucceeded: false,
      optionalWriteFailed: false,
    };
    recordSaveTracePatch(
      traceId,
      {
        traceId,
        buildLabel: FABRIQUOTE_BUILD_INFO.label,
        action: action === "save_as_new" ? "save_as_new" : "save",
        quoteType: quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE,
        quoteNo: "",
        quoteId: "",
        quoteIndexId: "",
        sourcePath: "",
        userUid: user.uid,
        workspaceUid,
        customerName: "",
        siteName: "",
        eventGuard: eventGuard || {
          receivedEvent: false,
          defaultPreventedBefore: false,
          preventDefaultCalled: false,
          defaultPreventedAfter: false,
        },
        requiredWrites: {
          mainQuote: "not_attempted",
          quoteIndex: "not_attempted",
        },
        optionalWrites: {
          customerSearchIndex: "skipped",
          crmCustomer: "skipped",
          crmDealLink: "skipped",
          siteDoc: "skipped",
        },
        errors: [],
        savedCard: {
          quoteId: "",
          quoteIndexId: "",
          quoteNo: "",
          customerName: "",
          siteName: "",
          deleted: null,
          hasCreatedAt: false,
          createdAtType: "",
          hasUpdatedAt: false,
          updatedAtType: "",
          quoteType: "",
          quotationType: "",
          hasSearchableText: false,
          sourcePath: "",
        },
        savedQuotesPatch: {
          prependPatchRan: false,
          previousCount: 0,
          nextCount: 0,
          insertedAtTop: false,
          recentCacheReset: false,
          patchRevision: 0,
        },
        recentListMerge: {
          staleResultDetected: false,
          staleResultMerged: false,
          staleResultWipedSavedCard: false,
          firstQuoteNoAfterMerge: "",
        },
        finalResult: "unknown",
        userMessageShown: "",
      },
      { includeCreatedAt: true }
    );
    return traceId;
  },
  [canUseSaveTraceRecorder, recordSaveTracePatch, user?.uid, workspaceUid]
);

const recordSaveTraceQuoteIndexSnapshot = useCallback(
  (traceId, quoteIndexId, fallback = {}) => {
    if (!canUseSaveTraceRecorder || !traceId || !workspaceUid || !quoteIndexId) return;
    const indexRef = doc(db, "users", workspaceUid, "quote_index", quoteIndexId);
    getDoc(indexRef, { label: "saveTraceQuoteIndexSnapshot" })
      .then((snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        recordSaveTracePatch(traceId, {
          savedCard: {
            quoteId: sanitizeSaveTraceText(data.quoteId || data.sourceQuoteId || fallback.quoteId || "", 160),
            quoteIndexId: sanitizeSaveTraceText(quoteIndexId, 160),
            quoteNo: sanitizeSaveTraceText(data.quoteNo || fallback.quoteNo || "", 80),
            customerName: sanitizeSaveTraceText(data.customerName || fallback.customerName || "", 160),
            siteName: sanitizeSaveTraceText(data.siteName || fallback.siteName || "", 160),
            deleted:
              data.deleted === true ? true : data.deleted === false ? false : null,
            hasCreatedAt: Boolean(data.createdAt),
            createdAtType: getSaveTraceTimestampType(data.createdAt),
            hasUpdatedAt: Boolean(data.updatedAt),
            updatedAtType: getSaveTraceTimestampType(data.updatedAt),
            quoteType: sanitizeSaveTraceText(data.quoteType || fallback.quoteType || "", 80),
            quotationType: sanitizeSaveTraceText(data.quotationType || fallback.quotationType || "", 80),
            hasSearchableText: Boolean(data.searchableText),
            sourcePath: sanitizeSaveTraceText(data.sourcePath || fallback.sourcePath || "", 260),
          },
        });
      })
      .catch((error) => {
        appendSaveTraceError(traceId, "quote_index_snapshot", error, {
          path: indexRef.path,
        });
      });
  },
  [
    appendSaveTraceError,
    canUseSaveTraceRecorder,
    getSaveTraceTimestampType,
    recordSaveTracePatch,
    sanitizeSaveTraceText,
    workspaceUid,
  ]
);

const getGeneralSaveAccountGuardSnapshot = useCallback(() => {
  const accountRecord = account || currentAccount || {};
  const accountStatus = String(
    accountRecord.accountStatus ||
      accountRecord.subscriptionStatus ||
      accountRecord.status ||
      ""
  ).trim();
  const createdVia = String(accountRecord.createdVia || "").trim();
  const isManualOrLegacyWorkspace = !createdVia || createdVia !== "website_signup";
  const effectiveStatus =
    accountStatus || (isManualOrLegacyWorkspace ? "active" : "missing");
  const canSaveQuotes =
    canEditQuotes &&
    canAccessGeneralQuotes &&
    (
      effectiveStatus === "active" ||
      effectiveStatus === "trial" ||
      (!accountStatus && isManualOrLegacyWorkspace)
    );
  const reason = !canEditQuotes
    ? "missing_edit_permission_or_plan_access"
    : !canAccessGeneralQuotes
    ? "general_quotes_feature_not_available"
    : effectiveStatus === "active" || effectiveStatus === "trial"
    ? "account_status_allows_save"
    : !accountStatus && isManualOrLegacyWorkspace
    ? "legacy_manual_workspace_missing_status_treated_active"
    : "account_status_blocks_save";

  return {
    workspaceUid,
    accountId: accountRecord.id || accountUser?.accountId || "",
    accountStatus,
    effectiveStatus,
    createdVia,
    plan: accountRecord.plan || accountUser?.plan || "",
    billingPlan: accountRecord.billingPlan || "",
    trialQuotationsUsed,
    trialQuotationLimit,
    canEditQuotes,
    canAccessGeneralQuotes,
    canSaveQuotes,
    reason,
  };
}, [
  account,
  accountUser?.accountId,
  accountUser?.plan,
  canAccessGeneralQuotes,
  canEditQuotes,
  currentAccount,
  trialQuotationLimit,
  trialQuotationsUsed,
  workspaceUid,
]);
const accessibleTabs = useMemo(() => {
  const items = [];

  if (canAccessDashboard) items.push("dashboard");
  if (canViewQuotes) items.push("quote");
  if (canAccessSavedQuotes) items.push("saved");
  if (canAccessCrm) items.push("crm");
  if (canAccessCrm && canViewAllCrmRecords) items.push("customerLedger");
  if (canViewCrmTodayScreen) items.push("todayTasks");
  if (canAccessCrm) items.push("executionSpecs");
  if (canAccessCrm) items.push("executionTemplates");
  if (canAccessOptimizer) items.push("fco");
  if (canManageProductConfigs) items.push("products");
  if (canAccessStitching) items.push("stitching");
  if (canAccessTeamAccess) items.push("teamAccess");
  if (user?.uid) items.push("accountSecurity");
  if (isSuperAdmin) items.push("maintenance");
  if (canAccessSettingsTab) items.push("settings");

  return items.length > 0 ? items : [];
}, [
  canAccessCrm,
  canAccessDashboard,
  canAccessOptimizer,
  canAccessSavedQuotes,
  canAccessSettingsTab,
  canAccessStitching,
  canAccessTeamAccess,
  canViewAllCrmRecords,
  canViewCrmTodayScreen,
  canManageProductConfigs,
  canViewQuotes,
  isSuperAdmin,
  user?.uid,
]);
const quoteEditorReadOnly =
  !isWorkspaceOwner && canViewQuotes && !canEditQuotes;

const fallbackTab = accessibleTabs[0] || "dashboard";

const setRoutedTab = useCallback(
  (nextTab, nextQuoteSubTab = null, options = {}) => {
    const normalizedNextTab = nextTab === "dashboardList" ? "dashboard" : nextTab;
    const allowedTab = accessibleTabs.includes(normalizedNextTab)
      ? nextTab
      : fallbackTab;

    if (allowedTab === "quote") {
      const requestedQuoteType =
        nextQuoteSubTab ||
        (canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE);
      const allowedQuoteType =
        requestedQuoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes
          ? GENERAL_QUOTE_TYPE
          : requestedQuoteType === CURTAIN_QUOTE_TYPE && canAccessCurtainQuotes
          ? CURTAIN_QUOTE_TYPE
          : canAccessCurtainQuotes
          ? CURTAIN_QUOTE_TYPE
          : GENERAL_QUOTE_TYPE;
      setQuoteSubTab(allowedQuoteType);
    }

    setTab(allowedTab);

    if (options.updatePath !== false) {
      const route = getRouteForTab(allowedTab, nextQuoteSubTab || quoteSubTab);
      if (route && window.location.pathname !== route) {
        window.history.replaceState({}, "", route);
      }
    }
  },
  [
    accessibleTabs,
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    fallbackTab,
    quoteSubTab,
  ]
);

useEffect(() => {
  if (accountLoading || !hasActiveSubscription) return;

  const route = ROUTE_TO_TAB[window.location.pathname];
  if (route) {
    if (window.location.pathname === "/customer-pdf" && !canUseCustomerPdf) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (window.location.pathname === "/workshop-pdf" && !canUseWorkshopPdf) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (route.quoteSubTab === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (route.quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    const normalizedRouteTab = route.tab === "dashboardList" ? "dashboard" : route.tab;
    if (!accessibleTabs.includes(normalizedRouteTab)) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    setRoutedTab(route.tab, route.quoteSubTab || null, { updatePath: false });
    return;
  }

  const normalizedActiveTab =
    tab === "dashboardList" ? "dashboard" : tab;

  if (!accessibleTabs.includes(normalizedActiveTab)) {
    setRoutedTab(accessibleTabs[0] || "dashboard");
  }

  if (tab === "quote") {
    if (quoteSubTab === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
      setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
    }
    if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
      setRoutedTab("quote", GENERAL_QUOTE_TYPE);
    }
  }
}, [
  accessibleTabs,
  accountLoading,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  canUseCustomerPdf,
  canUseWorkshopPdf,
  hasActiveSubscription,
  quoteSubTab,
  setRoutedTab,
  tab,
]);

useEffect(() => {
  if (accountLoading || !hasActiveSubscription) return;

  const params = new URLSearchParams(window.location.search);
  const leadId = params.get("leadId");
  const customerId = params.get("customerId");

  if (window.location.pathname === "/crm" && leadId) {
    setSelectedCrmDealId(leadId);
    setCrmView("list");
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    window.history.replaceState({}, "", "/crm");
  }

  if (window.location.pathname === "/customer-ledger" && customerId) {
    setSelectedCustomerLedgerId(customerId);
    setCustomerLedgerDetailTab("overview");
    window.history.replaceState({}, "", "/customer-ledger");
  }
}, [accountLoading, hasActiveSubscription]);

const activeTeamMembers = useMemo(
  () => teamMembers.filter((member) => isActiveTeamMember(member)),
  [teamMembers]
);
const occupiedStaffSlots = useMemo(
  () => countOccupiedStaffSlots(teamMembers),
  [teamMembers]
);
const accountStaffLimit = normalizeStaffLimit(account?.staffLimit, DEFAULT_STAFF_LIMIT);
const remainingStaffSlots = Math.max(0, accountStaffLimit - occupiedStaffSlots);

const teamAccessRows = useMemo(() => {
  const accountMember = accountUser?.member
    ? {
        id: accountUser.member.uid || user?.uid || "current-user",
        uid: accountUser.member.uid || user?.uid || "",
        name: accountUser.displayName || businessName || "Current User",
        email: accountUser.member.email || accountUser.email || user?.email || "",
        role: accountUser.member.role || accountUser.role || "owner",
        plan: accountUser.member.plan || accountUser.plan || account?.plan || "full",
        isActive: accountUser.member.isActive !== false,
        accountName: account?.name || businessName || "FabriQuote Workspace",
        features: accountUser.member.features || features || {},
      }
    : null;

  const legacyRows = (teamMembers || []).map((member) => ({
    id: member.id || member.uid || member.email,
    uid: member.uid || "",
    name: member.name || "Team member",
    email: member.email || "",
    role: member.role || "staff",
    plan: account?.plan || accountUser?.plan || "full",
    isActive: member.isActive !== false && member.status !== "disabled",
    accountName: account?.name || businessName || "FabriQuote Workspace",
    features: features || {},
  }));

  const rows = accountMember ? [accountMember, ...legacyRows] : legacyRows;
  const seen = new Set();
  return rows.filter((row) => {
    const key = row.uid || row.email || row.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [account, accountUser, businessName, features, teamMembers, user]);

const accessFeatureLabels = [
  ["dashboard", "Dashboard"],
  ["crm", "CRM"],
  ["generalQuotes", "General Quotes"],
  ["curtainQuotes", "Curtain Quotes"],
  ["stitchingRates", "Stitching & Rates"],
  ["rollOptimizer", "Roll Optimizer"],
  ["customerPdf", "Customer PDF"],
  ["workshopPdf", "Workshop PDF"],
  ["savedQuotes", "Saved Quotes"],
  ["settings", "Settings"],
];

const teamCellStyle = {
  padding: "14px 12px",
  background: "rgba(255,255,255,0.72)",
  borderTop: "1px solid rgba(226,232,240,0.9)",
  borderBottom: "1px solid rgba(226,232,240,0.9)",
  fontSize: 13,
  fontWeight: 700,
};

const statusPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "5px 9px",
  fontSize: 11,
  fontWeight: 900,
};

const getTeamMemberById = useCallback(
  (memberId) => (crmMemberRoster.scope.startsWith(`${workspaceUid}:`) ? crmMemberRoster.rows : [])
    .find((member) => member.id === memberId || member.staffId === memberId)
    || teamMembers.find((member) => member.id === memberId) || null,
  [teamMembers, crmMemberRoster, workspaceUid]
);

const getTeamMemberLabel = useCallback(
  (memberId, snapshot = "") => {
    const member = getTeamMemberById(memberId);
    if (member?.name) return member.name;
    return String(snapshot || "").trim() || "Unassigned";
  },
  [getTeamMemberById]
);

const getAssignableTeamMembers = useCallback(
  (selectedMemberId = "") => {
    const selectedMember = getTeamMemberById(selectedMemberId);
    const options = crmMemberRoster.error || crmMemberRoster.loading || !crmMemberRoster.scope.startsWith(`${workspaceUid}:`) ? []
      : crmMemberRoster.rows.filter((member) => !member.exclusionReason).map(member =>
        member.staffId && member.staffId === selectedMemberId ? { ...member, id: selectedMemberId } : member);

    if (
      selectedMember &&
      !options.some((member) => member.id === selectedMemberId || member.id === selectedMember.id)
    ) {
      options.push({ ...selectedMember, id: selectedMemberId, isActive: false,
        exclusionReason: selectedMember.exclusionReason || "Needs reassignment" });
    } else if (selectedMemberId && !selectedMember) {
      options.push({ id: selectedMemberId, name: "Previous assignee - needs reassignment", isActive: false,
        exclusionReason: "Membership unavailable" });
    }

    return options;
  },
  [crmMemberRoster, getTeamMemberById, workspaceUid]
);

useEffect(() => {
  if (tab !== "crm" || !canAssignLeadOwnerAccess || !workspaceUid || !user?.uid) return undefined;
  const accountId = account?.id || accountUser?.activeAccountId || accountUser?.accountId;
  let cancelled = false;
  const scope = `${workspaceUid}:${accountId}:${user.uid}`;
  setCrmMemberRoster({ rows: [], loading: true, error: "", scope });
  (async () => {
    try {
      if (!accountId) throw new Error("Missing canonical account");
      const snap = await getDocs(firestoreQuery(collection(db, "accounts", accountId, "members"),
        firestoreOrderBy(firestoreDocumentId()), firestoreLimit(101)), { label: "crmAssignment.memberRoster", ttlMs: 300000 });
      if (snap.size > 100) throw new Error("Roster exceeds bounded page");
      const rows = buildCrmMemberRoster(snap.docs.map(member => ({ ...member.data(), id: member.id })), workspaceUid);
      if (!cancelled) setCrmMemberRoster({ rows, loading: false, error: "", scope });
    } catch {
      if (!cancelled) setCrmMemberRoster({ rows: [], loading: false, error: "Could not load eligible employees. Please retry or contact an administrator.", scope });
    }
  })();
  return () => { cancelled = true; };
}, [tab, canAssignLeadOwnerAccess, workspaceUid, user?.uid, account?.id, accountUser?.activeAccountId, accountUser?.accountId, crmMemberRosterRetry]);

const renderCrmMemberRosterStatus = () => !canAssignLeadOwnerAccess ? null : (
  <div style={{ fontSize: 12, marginTop: 6 }}>
    {crmMemberRoster.loading ? <span role="status">Loading employees...</span> : crmMemberRoster.error ? (
      <span role="alert">{crmMemberRoster.error} <button type="button" onClick={() => setCrmMemberRosterRetry(value => value + 1)}>Retry</button></span>
    ) : !crmMemberRoster.rows.some(member => !member.exclusionReason) ? (
      <span>No eligible employees. Check employee membership and CRM permissions.</span>
    ) : null}
    {crmMemberRoster.rows.filter(member => member.exclusionReason).map(member => (
      <div key={member.id}>{member.name} is not available for CRM assignment: {member.exclusionReason}</div>
    ))}
  </div>
);

const enqueueToast = useCallback((message, tone = "info", duration = 3800) => {
  const text = String(message || "").trim();
  if (!text) return "";

  const toastId = createClientId("toast");

  setAppToasts((prev) => [
    ...prev,
    {
      id: toastId,
      tone,
      message: text,
    },
  ]);

  const timeoutId = window.setTimeout(() => {
    setAppToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    toastTimersRef.current.delete(toastId);
  }, duration);

  toastTimersRef.current.set(toastId, timeoutId);
  return toastId;
}, []);

const dismissToast = useCallback((toastId) => {
  const timeoutId = toastTimersRef.current.get(toastId);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
    toastTimersRef.current.delete(toastId);
  }

  setAppToasts((prev) => prev.filter((toast) => toast.id !== toastId));
}, []);

const notify = useCallback(
  (message, tone = "info", duration = 3800) =>
    enqueueToast(message, tone, duration),
  [enqueueToast]
);

const handleQuoteRowValidationFailure = useCallback(
  (detail, options = {}) => {
    if (!detail) return;
    setQuoteRowValidationError(detail);
    setAutoSaveStatus("error");
    if (isDevRuntime) {
      devError("QUOTE_ROW_VALIDATION_FAILED_DETAILED", {
        ...detail,
        source: options.source || "",
      });
    }
    notify(detail.message || "Please check the highlighted row values.", "error", 8000);
    window.setTimeout(() => {
      if (typeof document === "undefined") return;
      const rowId = detail.rowId ? String(detail.rowId).replace(/"/g, '\\"') : "";
      const selector = rowId
        ? `[data-quote-row-id="${rowId}"]`
        : `[data-quote-row-index="${detail.rowIndex}"]`;
      const element = document.querySelector(selector);
      element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 50);
  },
  [notify]
);

const traceReferralPartner = useCallback(
  (action = "", details = {}) => {
    if (!IS_DEV_UI_ENABLED) return;
    const selectedPartner =
      details.partner ||
      selectedReferralPartnerRef.current ||
      normalizeQuoteReferralPartner(selectedQuote || {});
    devLog("REFERRAL_PARTNER_TRACE", {
      action,
      quoteId:
        details.quoteId ||
        editingQuoteId ||
        generalEditingQuoteId ||
        selectedQuote?.quoteId ||
        selectedQuote?.id ||
        "",
      quoteNo: details.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
      selectedPartnerId:
        details.selectedPartnerId || selectedPartner?.partnerId || "",
      selectedPartnerName:
        details.selectedPartnerName || selectedPartner?.displayName || "",
      createdNewPartner: details.createdNewPartner === true,
      duplicateCheckResult: details.duplicateCheckResult || "",
      savedToQuote: details.savedToQuote === true,
      savedToQuoteIndex: details.savedToQuoteIndex === true,
      hiddenFromCustomerPdf: details.hiddenFromCustomerPdf !== false,
    });
  },
  [editingQuoteId, generalEditingQuoteId, quoteNo, selectedQuote]
);

const sortReferralPartners = useCallback((partners = []) =>
  [...partners].sort((left, right) =>
    String(left?.displayName || "").localeCompare(String(right?.displayName || ""))
  ), []);

const loadReferralPartners = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid) return [];
    const cached = referralPartnersCacheRef.current;
    if (
      !force &&
      cached.workspaceUid === workspaceUid &&
      Array.isArray(cached.partners) &&
      cached.loadedAt > 0
    ) {
      setReferralPartners(cached.partners);
      return cached.partners;
    }

    setReferralPartnersLoadState((prev) => ({
      ...prev,
      loading: true,
      error: "",
    }));
    try {
      const partnersRef = collection(
        db,
        "users",
        workspaceUid,
        REFERRAL_PARTNER_COLLECTION
      );
      const partnersQuery = firestoreQuery(
        partnersRef,
        firestoreOrderBy("displayName", "asc"),
        firestoreLimit(REFERRAL_PARTNER_LOAD_LIMIT)
      );
      const snap = await getDocs(partnersQuery, {
        label: "referralPartners.activeList",
      });
      const partners = sortReferralPartners(
        snap.docs
          .map((docSnap) =>
            normalizeReferralPartnerRecord(docSnap.data(), docSnap.id)
          )
          .filter((partner) => partner.active !== false && partner.deleted !== true)
      );
      referralPartnersCacheRef.current = {
        workspaceUid,
        loadedAt: Date.now(),
        partners,
      };
      setReferralPartners(partners);
      setReferralPartnersLoadState({
        loading: false,
        loadedWorkspace: workspaceUid,
        error: "",
      });
      return partners;
    } catch (error) {
      setReferralPartnersLoadState({
        loading: false,
        loadedWorkspace: workspaceUid,
        error: error?.message || String(error),
      });
      devWarn("REFERRAL_PARTNER_LOAD_FAILED", {
        workspaceUid,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      });
      return [];
    }
  },
  [sortReferralPartners, workspaceUid]
);

const applyReferralPartnerSelection = useCallback(
  (partnerInput = null, context = quoteSubTab, options = {}) => {
    const partner = partnerInput
      ? normalizeReferralPartnerRecord(partnerInput)
      : null;
    const quoteFields = buildReferralPartnerQuoteFields(partner);
    setSelectedReferralPartner(partner);
    selectedReferralPartnerRef.current = partner;
    setReferralPartnerPickerState({
      query: partner?.displayName || "",
      activeContext: "",
    });

    if (context === GENERAL_QUOTE_TYPE) {
      setGeneralQuoteLive((prev) => ({
        ...prev,
        ...quoteFields,
      }));
    }

    traceReferralPartner(options.action || "select", {
      partner,
      createdNewPartner: options.createdNewPartner === true,
      duplicateCheckResult: options.duplicateCheckResult || "",
      hiddenFromCustomerPdf: true,
    });
  },
  [quoteSubTab, setGeneralQuoteLive, traceReferralPartner]
);

const findReferralPartnerDuplicate = useCallback(
  async (draft = {}) => {
    if (!workspaceUid) return null;
    const normalizedDraft = normalizeReferralPartnerRecord(draft);
    const partnersRef = collection(
      db,
      "users",
      workspaceUid,
      REFERRAL_PARTNER_COLLECTION
    );
    const exactChecks = [
      normalizedDraft.phoneNormalized
        ? {
            label: "phone",
            field: "phoneNormalized",
            value: normalizedDraft.phoneNormalized,
            limit: 5,
          }
        : null,
      normalizedDraft.emailCanonical
        ? {
            label: "email",
            field: "emailCanonical",
            value: normalizedDraft.emailCanonical,
            limit: 5,
          }
        : null,
      normalizedDraft.nameNormalized
        ? {
            label: "name_type",
            field: "nameNormalized",
            value: normalizedDraft.nameNormalized,
            limit: 10,
            filter: (partner) => partner.type === normalizedDraft.type,
          }
        : null,
    ].filter(Boolean);

    for (const check of exactChecks) {
      const snap = await getDocs(
        firestoreQuery(
          partnersRef,
          firestoreWhere(check.field, "==", check.value),
          firestoreLimit(check.limit)
        ),
        { label: `referralPartners.duplicate.${check.label}` }
      );
      const match = snap.docs
        .map((docSnap) =>
          normalizeReferralPartnerRecord(docSnap.data(), docSnap.id)
        )
        .find(
          (partner) =>
            partner.active !== false &&
            partner.deleted !== true &&
            (!check.filter || check.filter(partner))
        );
      if (match) {
        return { reason: check.label, partner: match };
      }
    }
    return null;
  },
  [workspaceUid]
);

const saveReferralPartnerFromModal = useCallback(async () => {
  if (!workspaceUid) {
    setReferralPartnerModalState((prev) => ({
      ...prev,
      error: "Workspace is not ready yet.",
    }));
    return;
  }
  const draft = referralPartnerModalState.draft || createReferralPartnerDraft();
  const displayName = String(draft.displayName || "").trim();
  if (!displayName) {
    setReferralPartnerModalState((prev) => ({
      ...prev,
      error: "Name is required.",
    }));
    return;
  }

  setReferralPartnerModalState((prev) => ({
    ...prev,
    saving: true,
    error: "",
    duplicate: null,
  }));

  try {
    const normalizedDraft = normalizeReferralPartnerRecord({
      ...draft,
      displayName,
      type: normalizeReferralPartnerType(draft.type),
    });
    const duplicate = await findReferralPartnerDuplicate(normalizedDraft);
    if (duplicate?.partner) {
      const shouldSelect =
        typeof window !== "undefined"
          ? window.confirm(
              "A similar referral partner already exists. Do you want to select this existing partner instead?"
            )
          : true;
      if (shouldSelect) {
        applyReferralPartnerSelection(
          duplicate.partner,
          referralPartnerModalState.context || quoteSubTab,
          {
            action: "select_duplicate",
            duplicateCheckResult: duplicate.reason,
          }
        );
        setReferralPartnerModalState({
          open: false,
          context: "",
          draft: createReferralPartnerDraft(),
          saving: false,
          error: "",
          duplicate: null,
        });
        notify("Existing referral partner selected.", "success");
      } else {
        setReferralPartnerModalState((prev) => ({
          ...prev,
          saving: false,
          duplicate,
          error: "Similar referral partner found. Select the existing partner to avoid duplicates.",
        }));
      }
      return;
    }

    const partnerRef = doc(
      collection(db, "users", workspaceUid, REFERRAL_PARTNER_COLLECTION)
    );
    const partnerId = partnerRef.id;
    const payload = sanitizeCrmFirestoreValue({
      partnerId,
      displayName,
      nameNormalized: normalizeSearchText(displayName),
      type: normalizedDraft.type,
      phone: normalizedDraft.phone || "",
      phoneNormalized: normalizeSearchCompact(normalizedDraft.phone || ""),
      email: normalizedDraft.email || "",
      emailCanonical: normalizeEmailValue(normalizedDraft.email || ""),
      companyName: normalizedDraft.companyName || "",
      companyNameNormalized: normalizeSearchText(normalizedDraft.companyName || ""),
      notes: normalizedDraft.notes || "",
      active: true,
      deleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || "",
      updatedBy: auth.currentUser?.uid || "",
    });
    await setDoc(partnerRef, payload);
    const localPartner = normalizeReferralPartnerRecord(
      {
        ...payload,
        partnerId,
        id: partnerId,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      partnerId
    );
    const nextPartners = sortReferralPartners([
      ...referralPartners.filter((partner) => partner.partnerId !== partnerId),
      localPartner,
    ]);
    referralPartnersCacheRef.current = {
      workspaceUid,
      loadedAt: Date.now(),
      partners: nextPartners,
    };
    setReferralPartners(nextPartners);
    applyReferralPartnerSelection(
      localPartner,
      referralPartnerModalState.context || quoteSubTab,
      {
        action: "create_and_select",
        createdNewPartner: true,
        duplicateCheckResult: "none",
      }
    );
    setReferralPartnerModalState({
      open: false,
      context: "",
      draft: createReferralPartnerDraft(),
      saving: false,
      error: "",
      duplicate: null,
    });
    notify("Referral partner saved and selected.", "success");
  } catch (error) {
    setReferralPartnerModalState((prev) => ({
      ...prev,
      saving: false,
      error: error?.message || String(error),
    }));
    devError("REFERRAL_PARTNER_SAVE_FAILED", {
      workspaceUid,
      errorCode: error?.code || "",
      errorMessage: error?.message || String(error),
    });
    notify("Could not save referral partner.", "error", 6000);
  }
}, [
  applyReferralPartnerSelection,
  findReferralPartnerDuplicate,
  notify,
  quoteSubTab,
  referralPartnerModalState.context,
  referralPartnerModalState.draft,
  referralPartners,
  sortReferralPartners,
  workspaceUid,
]);

useEffect(() => {
  selectedReferralPartnerRef.current = selectedReferralPartner;
}, [selectedReferralPartner]);

useEffect(() => {
  if (!workspaceUid || tab !== "quote") return;
  loadReferralPartners();
}, [loadReferralPartners, tab, workspaceUid]);

useEffect(() => {
  const loadedPartner = normalizeQuoteReferralPartner(
    selectedQuote || (quoteSubTab === GENERAL_QUOTE_TYPE ? generalQuoteRef.current : {})
  );
  setSelectedReferralPartner(loadedPartner);
  selectedReferralPartnerRef.current = loadedPartner;
  setReferralPartnerPickerState((prev) => ({
    ...prev,
    query: loadedPartner?.displayName || "",
  }));
  if (quoteSubTab === GENERAL_QUOTE_TYPE && loadedPartner) {
    setGeneralQuoteLive((prev) => ({
      ...prev,
      ...buildReferralPartnerQuoteFields(loadedPartner),
    }));
  }
}, [
  generalEditingQuoteId,
  editingQuoteId,
  quoteSubTab,
  selectedQuote?.id,
  selectedQuote?.quoteId,
  selectedQuote?.quoteNo,
  setGeneralQuoteLive,
]);

const raiseFirebaseHealthWarning = useCallback(
  (kind, error, details = {}) => {
    const isFirestoreQuota =
      kind === "firestore" && isFirestoreResourceExhausted(error);
    const isStorageBilling =
      kind === "storage" && isFirebaseStorageBillingError(error);

    if (!isFirestoreQuota && !isStorageBilling) return;

    const message = isStorageBilling
      ? "Firebase Storage billing or quota issue detected. Image uploads may fail."
      : "Firebase usage limit reached. Firestore reads/writes may fail.";

    setFirebaseHealthWarning({
      kind,
      message,
      code: error?.code || "",
      detail: error?.message || String(error || ""),
      details,
      detectedAt: new Date().toISOString(),
    });

    if (isDevRuntime) {
      devError("Firebase quota/billing issue detected", {
        kind,
        code: error?.code,
        message: error?.message,
        details,
      });
    }
  },
  []
);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return undefined;
  devLog("FIREBASE_PROJECT_TRACE", {
    projectId: firebaseApp.options.projectId,
    authDomain: firebaseApp.options.authDomain,
    storageBucket: firebaseApp.options.storageBucket,
  });
  return undefined;
}, []);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return undefined;

  const intervalId = window.setInterval(() => {
    setFirebaseReadHealthTick((value) => value + 1);
  }, 5000);

  return () => window.clearInterval(intervalId);
}, []);

useEffect(() => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return undefined;
  }

  let cancelled = false;
  navigator.serviceWorker
    .getRegistration("/firebase-messaging-sw.js")
    .then((registration) => {
      if (cancelled || !registration) return;
      if (isDevRuntime) {
        devLog("Removing legacy Firebase Messaging service worker", {
          scope: registration.scope,
        });
      }
      registration.unregister().catch((error) => {
        if (isDevRuntime) {
          devWarn("Could not unregister Firebase Messaging service worker", error);
        }
      });
    })
    .catch((error) => {
      if (isDevRuntime) {
        devWarn("Legacy Firebase Messaging service worker cleanup failed", error);
      }
    });

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE) {
    return undefined;
  }

  let cancelled = false;
  const runPreload = () => {
    if (!cancelled) {
      preloadWorkshopZipRuntime();
    }
  };

  const canUseIdleCallback =
    typeof window !== "undefined" &&
    typeof window.requestIdleCallback === "function";
  const preloadHandle = canUseIdleCallback
    ? window.requestIdleCallback(runPreload, { timeout: 4000 })
    : window.setTimeout(runPreload, 1500);

  return () => {
    cancelled = true;
    if (typeof window === "undefined") return;
    if (
      canUseIdleCallback &&
      typeof window.cancelIdleCallback === "function"
    ) {
      window.cancelIdleCallback(preloadHandle);
      return;
    }
    window.clearTimeout(preloadHandle);
  };
}, [quoteSubTab, tab]);

const registerCurrentSecuritySession = useCallback(
  async ({ force = false, reason = "boot" } = {}) => {
    const currentUser = auth.currentUser || user;
    if (!currentUser?.uid) return "";

    const sessionId = getOrCreateSecuritySessionId(currentUser.uid);
    if (!sessionId) return "";

    const now = Date.now();
    const registration = securitySessionRegisteredRef.current;
    const recentlyRegistered =
      registration.uid === currentUser.uid &&
      registration.sessionId === sessionId &&
      now - Number(registration.lastHeartbeatAt || 0) < ACCOUNT_SECURITY_HEARTBEAT_MS;

    if (!force && recentlyRegistered) {
      setSecuritySessionId(sessionId);
      return sessionId;
    }

    if (registration.registering) return sessionId;

    securitySessionRegisteredRef.current = {
      ...registration,
      uid: currentUser.uid,
      sessionId,
      registering: true,
    };

    try {
      const registerSession = httpsCallable(functions, "registerSession");
      await registerSession({
        sessionId,
        reason,
        device: await getSafeSessionDeviceInfo(),
      });
      securitySessionRegisteredRef.current = {
        uid: currentUser.uid,
        sessionId,
        registering: false,
        lastHeartbeatAt: Date.now(),
      };
      setSecuritySessionId(sessionId);
      return sessionId;
    } catch (error) {
      securitySessionRegisteredRef.current = {
        uid: currentUser.uid,
        sessionId,
        registering: false,
        lastHeartbeatAt: 0,
      };
      if (isDevRuntime) {
        devError("ACCOUNT SECURITY SESSION REGISTER FAILED", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          uid: currentUser.uid,
          sessionId,
          reason,
        });
      }
      if (String(error?.code || "").includes("permission-denied")) {
        clearSecuritySessionId(currentUser.uid);
        notify("This session was signed out for security.", "warning", 6000);
        await signOut(auth);
      }
      return "";
    }
  },
  [notify, user]
);

useEffect(() => {
  if (!user?.uid) {
    setSecuritySessionId("");
    securitySessionRegisteredRef.current = {
      uid: "",
      sessionId: "",
      registering: false,
      lastHeartbeatAt: 0,
    };
    return undefined;
  }

  let cancelled = false;
  registerCurrentSecuritySession({ force: true, reason: "auth_boot" }).then((sessionId) => {
    if (!cancelled && sessionId) setSecuritySessionId(sessionId);
  });

  const heartbeat = () => {
    if (document.visibilityState === "hidden") return;
    registerCurrentSecuritySession({ reason: "focus_or_visibility" });
  };

  window.addEventListener("focus", heartbeat);
  document.addEventListener("visibilitychange", heartbeat);

  return () => {
    cancelled = true;
    window.removeEventListener("focus", heartbeat);
    document.removeEventListener("visibilitychange", heartbeat);
  };
}, [registerCurrentSecuritySession, user?.uid]);

useEffect(() => {
  if (!user?.uid || !securitySessionId) return undefined;

  const sessionRef = doc(db, "users", user.uid, "sessions", securitySessionId);
  return trackedOnSnapshot(
    sessionRef,
    {
      label: "accountSecurityCurrentSessionRevocationCheck",
      cacheKey: `users/${user.uid}/sessions/${securitySessionId}`,
    },
    async (snap) => {
      const data = snap.exists() ? snap.data() : null;
      if (data?.revoked === true) {
        if (isDevRuntime) {
          devWarn("ACCOUNT SECURITY CURRENT SESSION REVOKED", {
            uid: user.uid,
            sessionId: securitySessionId,
            revokedReason: data.revokedReason || "",
          });
        }
        clearSecuritySessionId(user.uid);
        notify("This session was signed out for security.", "warning", 7000);
        await signOut(auth);
      }
    },
    (error) => {
      if (isDevRuntime) {
        devError("ACCOUNT SECURITY SESSION LISTENER FAILED", {
          code: error?.code,
          message: error?.message,
          uid: user.uid,
          sessionId: securitySessionId,
        });
      }
    }
  );
}, [notify, securitySessionId, user?.uid]);

const loadAccountSecurityData = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  if (!currentUser?.uid) return;

  setSecurityCenterState({
    loading: true,
    error: "",
    loadedForUid: currentUser.uid,
  });

  try {
    await registerCurrentSecuritySession({ reason: "security_page_open" });
    const sessionsRef = collection(db, "users", currentUser.uid, "sessions");
    const activityRef = collection(db, "users", currentUser.uid, "security_activity");
    const [sessionsSnap, activitySnap] = await Promise.all([
      getDocs(
        firestoreQuery(
          sessionsRef,
          firestoreWhere("revoked", "==", false),
          firestoreOrderBy("lastSeenAt", "desc"),
          firestoreLimit(ACCOUNT_SECURITY_SESSION_LIMIT)
        ),
        {
          label: "accountSecurityActiveSessions",
          cacheKey: `users/${currentUser.uid}/sessions?revoked=false&orderBy=lastSeenAt_desc&limit=${ACCOUNT_SECURITY_SESSION_LIMIT}`,
        }
      ),
      getDocs(
        firestoreQuery(
          activityRef,
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(ACCOUNT_SECURITY_ACTIVITY_LIMIT)
        ),
        {
          label: "accountSecurityActivity",
          cacheKey: `users/${currentUser.uid}/security_activity?orderBy=createdAt_desc&limit=${ACCOUNT_SECURITY_ACTIVITY_LIMIT}`,
        }
      ),
    ]);

    setSecuritySessions(
      sessionsSnap.docs.map((sessionDoc) => ({
        id: sessionDoc.id,
        ...sessionDoc.data(),
      }))
    );
    setSecurityActivity(
      activitySnap.docs.map((activityDoc) => ({
        id: activityDoc.id,
        ...activityDoc.data(),
      }))
    );
    setSecurityCenterState({
      loading: false,
      error: "",
      loadedForUid: currentUser.uid,
    });
  } catch (error) {
    if (isDevRuntime) {
      devError("ACCOUNT SECURITY LOAD FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
      });
    }
    setSecurityCenterState({
      loading: false,
      error: getSecurityAuthErrorMessage(error),
      loadedForUid: currentUser.uid,
    });
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  }
}, [notify, registerCurrentSecuritySession, user]);

useEffect(() => {
  if (tab !== "accountSecurity" || !user?.uid) return;
  loadAccountSecurityData();
}, [loadAccountSecurityData, tab, user?.uid]);

const handleChangePassword = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const providerSummary = getSecurityProviderSummary(currentUser);
  const currentPassword = passwordForm.currentPassword;
  const newPassword = passwordForm.newPassword;
  const confirmPassword = passwordForm.confirmPassword;

  if (!currentUser?.email || !providerSummary.hasPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "Password changes are managed by your sign-in provider.",
      message: "",
    }));
    return;
  }

  if (!currentPassword || !newPassword || !confirmPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "Enter your current password, new password, and confirmation.",
      message: "",
    }));
    return;
  }

  if (newPassword.length < 8) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password must be at least 8 characters.",
      message: "",
    }));
    return;
  }

  if (newPassword === currentPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password must be different from your current password.",
      message: "",
    }));
    return;
  }

  if (newPassword !== confirmPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password and confirmation do not match.",
      message: "",
    }));
    return;
  }

  setSecurityActionState((prev) => ({ ...prev, password: true }));
  setPasswordForm((prev) => ({ ...prev, error: "", message: "" }));

  try {
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
    const sessionId =
      securitySessionId ||
      (await registerCurrentSecuritySession({ force: true, reason: "password_change" }));
    let postUpdateWarning = "";
    if (sessionId) {
      try {
        const recordPasswordChanged = httpsCallable(functions, "recordPasswordChanged");
        await recordPasswordChanged({
          sessionId,
          revokeOtherAppSessions: passwordForm.logoutOtherSessions,
        });
      } catch (activityError) {
        postUpdateWarning =
          "Password updated, but security activity could not be logged.";
        if (isDevRuntime) {
          devError("ACCOUNT SECURITY PASSWORD ACTIVITY LOG FAILED", {
            code: activityError?.code,
            message: activityError?.message,
            stack: activityError?.stack,
            uid: currentUser?.uid,
            sessionId,
          });
        }
      }
    }
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      logoutOtherSessions: true,
      showPasswords: false,
      message:
        postUpdateWarning ||
        (passwordForm.logoutOtherSessions
          ? "Password updated. Other app sessions were signed out."
          : "Password updated."),
      error: "",
    });
    notify(postUpdateWarning || "Password updated successfully.", postUpdateWarning ? "warning" : "success");
    await loadAccountSecurityData();
  } catch (error) {
    const message = getSecurityAuthErrorMessage(error);
    if (isDevRuntime) {
      devError("ACCOUNT SECURITY PASSWORD CHANGE FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser?.uid,
      });
    }
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      logoutOtherSessions: true,
      showPasswords: false,
      message: "",
      error: message,
    });
    notify(message, "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, password: false }));
  }
}, [
  loadAccountSecurityData,
  notify,
  passwordForm.confirmPassword,
  passwordForm.currentPassword,
  passwordForm.logoutOtherSessions,
  passwordForm.newPassword,
  registerCurrentSecuritySession,
  securitySessionId,
  user,
]);

const handleSendAccountPasswordReset = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const emailAddress = normalizeEmailValue(currentUser?.email || "");
  if (!emailAddress) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "No email address is available for this account.",
      message: "",
    }));
    return;
  }

  setSecurityActionState((prev) => ({ ...prev, passwordReset: true }));
  setPasswordForm((prev) => ({ ...prev, error: "", message: "" }));

  try {
    await sendPasswordResetEmail(auth, emailAddress, {
      url: PASSWORD_RESET_CONTINUE_URL,
      handleCodeInApp: false,
    });
    setPasswordForm((prev) => ({
      ...prev,
      message: PASSWORD_RESET_SAFE_NOTICE,
      error: "",
    }));
    notify("Password reset requested.", "success");
  } catch (error) {
    const message = getPasswordResetEmailErrorMessage(error);
    if (isDevRuntime) {
      devError("ACCOUNT_SECURITY_PASSWORD_RESET_FAILED_EXACT", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        email: emailAddress,
      });
    }
    setPasswordForm((prev) => ({
      ...prev,
      message: "",
      error: message,
    }));
    notify(message, "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, passwordReset: false }));
  }
}, [notify, user]);

const handleRevokeCurrentSession = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const sessionId = securitySessionId;
  if (!currentUser?.uid || !sessionId) return;

  setSecurityActionState((prev) => ({ ...prev, currentSession: true }));
  try {
    const revokeCurrentSession = httpsCallable(functions, "revokeCurrentSession");
    await revokeCurrentSession({ sessionId });
    clearSecuritySessionId(currentUser.uid);
    notify("This device has been signed out.", "success");
    await signOut(auth);
  } catch (error) {
    if (isDevRuntime) {
      devError("ACCOUNT SECURITY LOGOUT CURRENT FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
        sessionId,
      });
    }
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, currentSession: false }));
  }
}, [notify, securitySessionId, user]);

const handleRevokeOtherSessions = useCallback(
  async (targetSessionId = "") => {
    const currentUser = auth.currentUser || user;
    const currentSessionId = securitySessionId;
    if (!currentUser?.uid || !currentSessionId) return;

    setSecurityActionState((prev) => ({
      ...prev,
      otherSessions: !targetSessionId,
      targetSessionId,
    }));
    try {
      const revokeOtherSessions = httpsCallable(functions, "revokeOtherSessions");
      const result = await revokeOtherSessions({
        currentSessionId,
        targetSessionId: targetSessionId || undefined,
      });
      const revokedCount = Number(result?.data?.revokedCount || 0);
      notify(
        targetSessionId
          ? "Device signed out."
          : revokedCount > 0
          ? `${revokedCount} other app session${revokedCount === 1 ? "" : "s"} signed out.`
          : "No other app sessions were active.",
        "success"
      );
      await loadAccountSecurityData();
    } catch (error) {
      if (isDevRuntime) {
        devError("ACCOUNT SECURITY REVOKE OTHER FAILED", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          uid: currentUser.uid,
          currentSessionId,
          targetSessionId,
        });
      }
      notify(getSecurityAuthErrorMessage(error), "error", 6000);
    } finally {
      setSecurityActionState((prev) => ({
        ...prev,
        otherSessions: false,
        targetSessionId: "",
      }));
    }
  },
  [loadAccountSecurityData, notify, securitySessionId, user]
);

const handleRevokeAllSessions = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const currentSessionId = securitySessionId;
  if (!currentUser?.uid || !currentSessionId) return;

  setSecurityActionState((prev) => ({ ...prev, allSessions: true }));
  try {
    const revokeAllSessions = httpsCallable(functions, "revokeAllSessions");
    await revokeAllSessions({ currentSessionId });
    clearSecuritySessionId(currentUser.uid);
    notify("All sessions were signed out. Please sign in again.", "success", 6000);
    await signOut(auth);
  } catch (error) {
    if (isDevRuntime) {
      devError("ACCOUNT SECURITY LOGOUT EVERYWHERE FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
        currentSessionId,
      });
    }
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, allSessions: false }));
  }
}, [notify, securitySessionId, user]);

const openSiteLocation = useCallback(
  (location) => {
    const url = buildSiteLocationOpenUrl(location);
    if (!url) {
      notify("Add a map link or address before opening navigation.", "warning");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  },
  [notify]
);

const copySiteLocationLink = useCallback(
  async (location) => {
    const url = buildSiteLocationOpenUrl(location);
    if (!url) {
      notify("Add a map link or address before copying.", "warning");
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      notify("Location link copied", "success");
    } catch (error) {
      devError("Failed to copy location link:", error);
      notify("Could not copy location link. Please try again.", "error");
    }
  },
  [notify]
);

const openConfirmDialog = useCallback(
  ({
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "warning",
    onConfirm,
  }) => {
    setConfirmDialog({
      title: String(title || "Please confirm"),
      description: String(description || ""),
      confirmLabel,
      cancelLabel,
      tone,
      onConfirm,
    });
  },
  []
);

const closeConfirmDialog = useCallback(() => {
  setConfirmDialog(null);
}, []);

const buildBlankGeneralQuoteForEditor = useCallback(() => {
  const quoteDate = todayISO();
  return syncGeneralQuoteDefaults({
    ...createDefaultGeneralQuote({
      currency,
      businessName,
      shopAddress,
      businessPhone,
      businessEmail,
    }),
    id: "",
    quoteId: "",
    quoteIndexId: "",
    sourceQuoteId: "",
    sourcePath: "",
    quoteNo: "",
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    customerId: "",
    customerKey: "",
    customerSearchIndexId: "",
    crmLeadId: "",
    leadId: "",
    customerName: "",
    customerPhone: "",
    customerWhatsapp: "",
    customerEmail: "",
    customerAddress: "",
    customerCompanyName: "",
    customerLedgerLinked: false,
    siteId: "",
    customerSiteId: "",
    siteName: "",
    siteAddress: "",
    siteContactName: "",
    siteContactPhone: "",
    siteNotes: "",
    quoteDisplayTitle: "",
    quoteFor: {
      name: "",
      company: "",
      address: "",
      phone: "",
      email: "",
    },
  });
}, [businessEmail, businessName, businessPhone, currency, shopAddress, syncGeneralQuoteDefaults]);

const buildBlankCurtainQuoteDraftForEditor = useCallback(() => {
  const quoteDate = todayISO();
  const blankRows = [createDefaultCurtainQuoteRow("W1")];
  return normalizeCurtainQuoteDraftData({
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteId: "new",
    editingQuoteId: "",
    selectedQuoteId: "",
    sourceQuoteId: "",
    fullQuoteId: "",
    quoteIndexId: "",
    indexId: "",
    sourceCollection: "quotations",
    sourcePath: "",
    customerName: "",
    quoteCustomerLink: createEmptyQuoteCustomerLink(),
    quoteSiteSelection: createEmptyQuoteSiteSelection(),
    quoteNo: "",
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    rows: blankRows,
    repeatSettings: {},
    quoteFullnessOverrides: {},
    fabricSummaryMeta: {},
    discountType: "percent",
    discountValue: 0,
    quoteGstMode: CURTAIN_GST_MODE_FLAT,
    gstMode: CURTAIN_GST_MODE_FLAT,
    quoteGstRate: 0,
    gstRate: 0,
    gstAmount: 0,
    taxableAmount: 0,
    finalTotal: 0,
    grandTotal: 0,
    summaryDiscountMode: "%",
    summaryDiscountValue: "0",
    selectedQuoteWorkStage: "quote_given",
    selectedQuoteStatus: "draft",
    selectedInstallationDate: null,
    useHalfPieceReuse: true,
    passHalfPieceSavingsToCustomer: false,
    pendingCrmLeadId: "",
    expandedCurtainRows: {},
  });
}, [normalizeCurtainQuoteDraftData]);

const dismissAndClearQuoteDraftKey = useCallback((key) => {
  if (!key) return;
  dismissedDraftKeysRef.current.add(key);
  clearLocalDraftRecord(key);
}, []);

const resetGeneralQuoteEditorToNew = useCallback(() => {
  const previousDraftKey = generalQuoteDraftKey;
  const newDraftKey = workspaceUid
    ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.GENERAL_QUOTE, "new")
    : "";
  const nextGeneralQuote = buildBlankGeneralQuoteForEditor();

  dismissAndClearQuoteDraftKey(previousDraftKey);
  dismissAndClearQuoteDraftKey(newDraftKey);
  deletedGeneralItemIdsRef.current = [];
  generalUploadPromiseRef.current = null;
  generalQuoteImageRepairDebugRef.current = {};
  generalRemovedImageIdsRef.current = {};

  setRoutedTab("quote", GENERAL_QUOTE_TYPE);
  setSelectedQuote(null);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(null);
  setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setIsNewQuote(true);
  setCustomerName("");
  setQuoteCustomerLink(createEmptyQuoteCustomerLink());
  setQuoteSiteSelection(createEmptyQuoteSiteSelection());
  setCustomerPickerOpenKey("");
  setCustomerSitePickerOpenKey("");
  setCustomerCreateState(null);
  setQuoteNo("");
  setDate(resolveQuoteDateForInput(nextGeneralQuote, todayISO()));
  setSelectedQuoteWorkStage("quote_given");
  setSelectedQuoteStatus("draft");
  setSelectedInstallationDate(null);
  setPendingCrmLeadId("");
  setSavedQuoteOpenError(null);
  setGeneralUploadingItemId("");
  setGeneralRepairingImageKey("");
  setGeneralImageRepairState({});
  setGeneralImageRepairCandidates({});
  generalQuoteRef.current = nextGeneralQuote;
  setGeneralQuoteLive(nextGeneralQuote);
  setOptimizerQuoteId("");
  setOptimizerQuote(null);
  setSelectedSolutionIndex(0);
  markGeneralQuoteBaseline(nextGeneralQuote, "idle");
  updateLocalDraftStatus(LOCAL_DRAFT_MODULES.GENERAL_QUOTE, "idle", null);
  setDraftRecoveryPrompt((previousPrompt) =>
    previousPrompt?.module === LOCAL_DRAFT_MODULES.GENERAL_QUOTE ? null : previousPrompt
  );
  setAutoSaveStatus("idle");

  if (isDevRuntime) {
    devLog("GENERAL_QUOTE_STATE_APPLY_TRACE", {
      phase: "reset_to_new",
      quoteNo: "",
      incomingItemCount: 0,
      existingItemCount: Array.isArray(generalQuoteRef.current?.lineItems)
        ? generalQuoteRef.current.lineItems.length
        : 0,
      nextItemCount: Array.isArray(nextGeneralQuote.lineItems)
        ? nextGeneralQuote.lineItems.length
        : 0,
      source: "new_blank_quote",
    });
    devLog("QUOTE_EDITOR_NEW_QUOTE_RESET", {
      quoteType: GENERAL_QUOTE_TYPE,
      previousDraftKey,
      newDraftKey,
      mode: QUOTE_EDITOR_MODE_NEW,
      firestoreReads: 0,
    });
  }
}, [
  buildBlankGeneralQuoteForEditor,
  dismissAndClearQuoteDraftKey,
  generalQuoteDraftKey,
  markGeneralQuoteBaseline,
  setGeneralQuoteLive,
  setRoutedTab,
  updateLocalDraftStatus,
  workspaceUid,
]);

const resetCurtainQuoteEditorToNew = useCallback(() => {
  const previousDraftKey = curtainQuoteDraftKey;
  const newDraftKey = workspaceUid
    ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "new")
    : "";
  const blankDraft = buildBlankCurtainQuoteDraftForEditor();
  const blankRows = blankDraft.rows || [createDefaultCurtainQuoteRow("W1")];

  dismissAndClearQuoteDraftKey(previousDraftKey);
  dismissAndClearQuoteDraftKey(newDraftKey);
  deletedCurtainRowIdsRef.current = [];
  currentQuoteEditVersionRef.current += 1;
  legacyRecalculationRevisionSaveRef.current = "";
  legacyCurtainRecalculationImpactRef.current = {
    shouldShowBanner: false,
    recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
  };

  setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
  setSelectedQuote(null);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(null);
  setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setIsNewQuote(true);
  setCustomerName("");
  setQuoteCustomerLink(createEmptyQuoteCustomerLink());
  setQuoteSiteSelection(createEmptyQuoteSiteSelection());
  setCustomerPickerOpenKey("");
  setCustomerSitePickerOpenKey("");
  setCustomerCreateState(null);
  setQuoteNo("");
  setDate(resolveQuoteDateForInput(blankDraft, todayISO()));
  replaceCurtainRows(
    blankRows,
    QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE,
    { quoteId: "new", reason: "start_new_quote" }
  );
  setEffectiveRows([]);
  setLeftoverUsageLog([]);
  setRepeatSettings({});
  setFabricSummaryMeta({});
  repeatSettingsRef.current = {};
  fabricSummaryMetaRef.current = {};
  setSummaryDiscountMode("%");
  setSummaryDiscountValue("0");
  curtainGstModeRef.current = CURTAIN_GST_MODE_FLAT;
  curtainGstRateRef.current = 0;
  setCurtainGstMode(CURTAIN_GST_MODE_FLAT);
  setCurtainGstRate(0);
  quoteFullnessOverridesRef.current = {};
  setQuoteFullnessOverrides({});
  setQuoteConstructionSettings(normalizeQuoteCalculationSettings());
  setQuoteFullnessOverrideInputs({});
  setSelectedQuoteWorkStage("quote_given");
  setSelectedQuoteStatus("draft");
  setSelectedInstallationDate(null);
  useHalfPieceReuseRef.current = true;
  passHalfPieceSavingsToCustomerRef.current = false;
  setUseHalfPieceReuse(true);
  setPassHalfPieceSavingsToCustomer(false);
  setPendingCrmLeadId("");
  setExpandedCurtainRows({});
  setSelectedSolutionIndex(0);
  setOptimizerQuoteId("");
  setOptimizerQuote(null);
  setPendingRecalculationAudit(null);
  setPendingLegacyCalculationSnapshot(null);
  setLegacyRecalculationPreview(null);
  setLegacyRecalculationModalOpen(false);
  setLegacyRecalculationRevisionRequest(null);
  setSavedQuoteOpenError(null);
  markCurtainQuoteBaseline(blankDraft, "idle");
  updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "idle", null);
  setDraftRecoveryPrompt((previousPrompt) =>
    previousPrompt?.module === LOCAL_DRAFT_MODULES.CURTAIN_QUOTE ? null : previousPrompt
  );
  setAutoSaveStatus("idle");

  if (isDevRuntime) {
    devLog("QUOTE_EDITOR_NEW_QUOTE_RESET", {
      quoteType: CURTAIN_QUOTE_TYPE,
      previousDraftKey,
      newDraftKey,
      mode: QUOTE_EDITOR_MODE_NEW,
      rowCount: blankRows.length,
      firestoreReads: 0,
    });
  }
}, [
  buildBlankCurtainQuoteDraftForEditor,
  curtainQuoteDraftKey,
  dismissAndClearQuoteDraftKey,
  markCurtainQuoteBaseline,
  replaceCurtainRows,
  setRoutedTab,
  updateLocalDraftStatus,
  workspaceUid,
]);

const requestStartNewQuote = useCallback(
  (event, requestedQuoteType = quoteSubTab) => {
    applyQuoteActionEventGuard(event);

    const nextQuoteType =
      requestedQuoteType === GENERAL_QUOTE_TYPE
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;
    const hasAccess =
      nextQuoteType === GENERAL_QUOTE_TYPE
        ? canAccessGeneralQuotes
        : canAccessCurtainQuotes;

    if (!hasAccess) {
      notify("This quotation type is not enabled for this account plan.", "warning");
      return;
    }

    if (!canCreateQuotes) {
      notify("You do not have permission to create quotations in this workspace.", "warning");
      return;
    }

    if (isSaving || generalUploadingItemId || generalUploadPromiseRef.current) {
      notify("Please wait for the current save/upload to finish.", "warning");
      return;
    }

    const reset =
      nextQuoteType === GENERAL_QUOTE_TYPE
        ? resetGeneralQuoteEditorToNew
        : resetCurtainQuoteEditorToNew;
    const activeMode =
      nextQuoteType === GENERAL_QUOTE_TYPE
        ? generalQuoteEditorMode
        : curtainQuoteEditorMode;
    const hasUnsavedChanges =
      nextQuoteType === GENERAL_QUOTE_TYPE
        ? generalQuoteIsDirty
        : curtainQuoteIsDirty;
    const isEditingExisting =
      activeMode === QUOTE_EDITOR_MODE_EDITING_EXISTING ||
      (nextQuoteType === GENERAL_QUOTE_TYPE
        ? Boolean(generalEditingQuoteId)
        : Boolean(editingQuoteId));

    if (isDevRuntime) {
      devLog("QUOTE_EDITOR_NEW_QUOTE_REQUEST", {
        quoteType: nextQuoteType,
        currentMode: activeMode,
        hasUnsavedChanges,
        isEditingExisting,
        editingQuoteId:
          nextQuoteType === GENERAL_QUOTE_TYPE
            ? generalEditingQuoteId || ""
            : editingQuoteId || "",
      });
    }

    if (hasUnsavedChanges || isEditingExisting) {
      openConfirmDialog({
        title: "Start a new quotation?",
        description: hasUnsavedChanges
          ? "This will clear the current editor and discard unsaved local changes. Saved quotations in Firestore will not be changed."
          : "This will clear the loaded quotation from the editor. The saved quotation will remain unchanged.",
        confirmLabel: "Start New Quote",
        cancelLabel: "Cancel",
        tone: "warning",
        onConfirm: reset,
      });
      return;
    }

    reset();
  },
  [
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    canCreateQuotes,
    curtainQuoteEditorMode,
    curtainQuoteIsDirty,
    editingQuoteId,
    generalEditingQuoteId,
    generalQuoteEditorMode,
    generalQuoteIsDirty,
    generalUploadingItemId,
    isSaving,
    notify,
    openConfirmDialog,
    quoteSubTab,
    resetCurtainQuoteEditorToNew,
    resetGeneralQuoteEditorToNew,
  ]
);

const canManageStorageFiles =
  isSuperAdmin || isWorkspaceOwner || ["owner", "admin"].includes(accountUser?.role || "");

const loadStorageManagerFiles = useCallback(async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready yet.", "warning");
    return;
  }

  setStorageManagerState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    const usageRef = doc(db, "workspaces", workspaceUid, "usage", "storage");
    const filesQuery = firestoreQuery(
      collection(db, "workspaces", workspaceUid, "storageFiles"),
      firestoreOrderBy("uploadedAt", "desc"),
      firestoreLimit(50)
    );
    const [usageSnap, filesSnap] = await Promise.all([
      trackedGetDoc(usageRef, {
        label: "storageManagerUsage",
        cacheKey: `workspaces/${workspaceUid}/usage/storage`,
      }),
      trackedGetDocs(filesQuery, {
        label: "storageManagerFiles",
        cacheKey: `workspaces/${workspaceUid}/storageFiles?limit=50`,
      }),
    ]);
    const files = filesSnap.docs.map((fileDoc) => ({
      id: fileDoc.id,
      ...(fileDoc.data() || {}),
    }));
    const computedUsedBytes = files.reduce(
      (total, file) =>
        total +
        Number(file.deleted ? 0 : file.sizeBytes || 0) +
        Number(file.deleted ? 0 : file.thumbnailSizeBytes || 0),
      0
    );
    const usage = usageSnap.exists()
      ? usageSnap.data() || {}
      : {
          usedBytes: computedUsedBytes,
          includedBytes: 1024 * 1024 * 1024,
          imageCount: files.length,
          byCategory: {},
        };
    setStorageManagerState({
      loading: false,
      error: "",
      files,
      usage,
      loadedAt: new Date().toISOString(),
    });
  } catch (error) {
    devError("STORAGE_MANAGER_LOAD_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setStorageManagerState((prev) => ({
      ...prev,
      loading: false,
      error: error?.message || "Could not load storage files.",
    }));
    notify("Could not load storage manager. Please try again.", "error");
  }
}, [notify, workspaceUid]);

const archiveStorageManagerFile = useCallback(
  async (file) => {
    if (!workspaceUid || !file?.fileId) return;
    try {
      await archiveManagedStorageFile({
        workspaceUid,
        fileId: file.fileId,
        removedBy: auth.currentUser?.uid || "",
        removalReason: "manual_storage_manager_archive",
        linkedRecords: Array.isArray(file.linkedRecords) ? file.linkedRecords : [],
      });
      setStorageManagerState((prev) => ({
        ...prev,
        files: (prev.files || []).map((entry) =>
          entry.fileId === file.fileId
            ? {
                ...entry,
                active: false,
                archived: true,
                removedFromRecord: true,
              }
            : entry
        ),
      }));
      notify("File archived. Storage object retained for recovery.", "success");
    } catch (error) {
      devError("STORAGE_MANAGER_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        fileId: file.fileId,
      });
      notify("Could not archive this file.", "error");
    }
  },
  [notify, workspaceUid]
);

const permanentlyDeleteStorageManagerFile = useCallback(
  (file) => {
    if (!workspaceUid || !file?.fileId) return;
    if (!canManageStorageFiles) {
      notify("Only owners/admins can permanently delete stored files.", "warning");
      return;
    }

    const linkedRecords = Array.isArray(file.linkedRecords) ? file.linkedRecords : [];
    openConfirmDialog({
      title: "Permanently delete storage file?",
      description: linkedRecords.length
        ? "This file is linked to a FabriQuote record. Archive/unlink is safer. Permanently deleting removes the Storage object and thumbnail."
        : "This permanently deletes the Storage object and thumbnail. This cannot be undone.",
      confirmLabel: "Delete permanently",
      cancelLabel: "Cancel",
      tone: "danger",
      onConfirm: async () => {
        try {
          const deleteTargets = [file.fullPath, file.thumbnailFullPath]
            .map((path) => String(path || "").trim())
            .filter(Boolean);
          await Promise.all(
            deleteTargets.map((path) =>
              deleteObject(ref(storage, path)).catch((error) => {
                if (error?.code !== "storage/object-not-found") throw error;
              })
            )
          );
          await setDoc(
            doc(db, "workspaces", workspaceUid, "storageFiles", file.fileId),
            {
              active: false,
              archived: true,
              deleted: true,
              deletedAt: serverTimestamp(),
              deletedBy: auth.currentUser?.uid || "",
              deleteMethod: "manual-admin",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          const deletedBytes = Number(file.sizeBytes || 0) + Number(file.thumbnailSizeBytes || 0);
          await setDoc(
            doc(db, "workspaces", workspaceUid, "usage", "storage"),
            {
              usedBytes: increment(-deletedBytes),
              imageCount: increment(-1),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setStorageManagerState((prev) => ({
            ...prev,
            files: (prev.files || []).map((entry) =>
              entry.fileId === file.fileId
                ? {
                    ...entry,
                    active: false,
                    archived: true,
                    deleted: true,
                  }
                : entry
            ),
            usage: prev.usage
              ? {
                  ...prev.usage,
                  usedBytes: Math.max(0, Number(prev.usage.usedBytes || 0) - deletedBytes),
                  imageCount: Math.max(0, Number(prev.usage.imageCount || 0) - 1),
                }
              : prev.usage,
          }));
          notify("File permanently deleted.", "success");
        } catch (error) {
          devError("STORAGE_MANAGER_PERMANENT_DELETE_FAILED", {
            code: error?.code,
            message: error?.message,
            fileId: file.fileId,
          });
          notify("Could not permanently delete this file.", "error");
        }
      },
    });
  },
  [canManageStorageFiles, notify, openConfirmDialog, workspaceUid]
);

const copyStorageManagerFileDebug = useCallback(
  async (file) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(file, null, 2));
      notify("Storage file JSON copied.", "success");
    } catch (error) {
      devLog("STORAGE_FILE_DEBUG_JSON", file);
      notify("Clipboard unavailable. Debug JSON logged to console.", "info");
    }
  },
  [notify]
);

const requestQuoteDealChoice = useCallback((details = {}) => {
  const openDeals = Array.isArray(details.openDeals) ? details.openDeals : [];
  if (!openDeals.length) return Promise.resolve("create");

  return new Promise((resolve) => {
    quoteDealChoiceResolverRef.current = resolve;
    setQuoteDealChoiceState({
      customerName: String(details.customerName || "this customer"),
      quoteNo: String(details.quoteNo || ""),
      quoteType: details.quoteType || "",
      openDeals: openDeals.slice(0, 5),
    });
  });
}, []);

const resolveQuoteDealChoice = useCallback((choice) => {
  const resolver = quoteDealChoiceResolverRef.current;
  quoteDealChoiceResolverRef.current = null;
  setQuoteDealChoiceState(null);
  if (resolver) resolver(choice);
}, []);

useEffect(() => {
  const originalAlert = window.alert;
  const activeToastTimers = toastTimersRef.current;

  window.alert = (message) => {
    const text = String(message || "").trim();
    const lower = text.toLowerCase();
    let tone = "info";

    if (
      lower.includes("saved") ||
      lower.includes("success") ||
      lower.includes("sent") ||
      lower.includes("restored") ||
      lower.includes("added successfully")
    ) {
      tone = "success";
    } else if (
      lower.includes("failed") ||
      lower.includes("error") ||
      lower.includes("could not") ||
      lower.includes("not found") ||
      lower.includes("crashed")
    ) {
      tone = "error";
    } else if (
      lower.includes("permission") ||
      lower.includes("please") ||
      lower.includes("required") ||
      lower.includes("select") ||
      lower.includes("enter") ||
      lower.includes("not enabled")
    ) {
      tone = "warning";
    }

    enqueueToast(text || "Something happened.", tone);
  };

  return () => {
    window.alert = originalAlert;
    activeToastTimers.forEach((timeoutId) => window.clearTimeout(timeoutId));
    activeToastTimers.clear();
  };
}, [enqueueToast]);

const getCurrentTaskHistoryActor = useCallback(() => {
  const name =
    currentWorkspaceMember?.name ||
    accountUser?.displayName ||
    user?.displayName ||
    user?.email ||
    user?.uid ||
    "Workspace User";
  const email =
    currentWorkspaceMember?.email ||
    accountUser?.email ||
    user?.email ||
    "";
  const uid =
    currentWorkspaceMember?.uid ||
    workspaceContext?.memberId ||
    user?.uid ||
    "";

  return {
    uid,
    email,
    name: String(name || email || uid || "Workspace User").trim(),
  };
}, [
  accountUser?.displayName,
  accountUser?.email,
  currentWorkspaceMember?.email,
  currentWorkspaceMember?.name,
  currentWorkspaceMember?.uid,
  user?.displayName,
  user?.email,
  user?.uid,
  workspaceContext?.memberId,
]);

const writeTaskHistoryRecord = useCallback(
  async ({
    taskType,
    sourceTaskId,
    dealLike,
    taskTitle,
    taskDescription = "",
    dueDate = null,
    sourceModule = TASK_HISTORY_SOURCE_CRM,
    originalPayload = {},
  }) => {
    if (!workspaceUid || !sourceTaskId || !dealLike) return;

    const actor = getCurrentTaskHistoryActor();
    const dealId = String(dealLike.id || dealLike.dealId || dealLike.leadId || dealLike.quoteId || "").trim();
    if (!dealId) return;

    const leadId =
      dealLike.crmRecordType === "lead"
        ? dealId
        : String(dealLike.crm?.leadId || dealLike.leadId || "").trim();
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const quoteId =
      dealLike.crmRecordType === "quote"
        ? dealId
        : String(primaryLinkedQuote?.quoteId || "").trim();
    const quoteNo = String(
      dealLike.crmRecordType === "quote"
        ? dealLike.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || ""
    ).trim();
    const customerName = String(
      dealLike.customerLabel ||
        dealLike.crmContact?.name ||
        dealLike.contact?.name ||
        dealLike.clientName ||
        dealLike.dealTitle ||
        "Unnamed Customer"
    ).trim();
    const normalizedPhone = normalizeSearchCompact(
      dealLike.customerPhone ||
        dealLike.crmContact?.phone ||
        dealLike.contact?.phone ||
        dealLike.phone ||
        ""
    );
    const taskSearchIndex = buildSafeSearchResult([
      customerName,
      quoteNo,
      taskTitle,
      taskDescription,
      taskType,
      sourceModule,
      actor.name,
      actor.email,
      normalizedPhone,
    ]);
    const historyId = buildTaskHistoryId(taskType, dealId, sourceTaskId);
    const historyRef = doc(db, "users", workspaceUid, "task_history", historyId);
    const dueMillis = safeTimeMillis(dueDate);
    const dueTimestamp = dueMillis > 0 ? Timestamp.fromDate(new Date(dueMillis)) : null;
    const cleanPayload = serializeTaskHistoryValue(originalPayload);

    const didCreate = await runTransaction(db, async (transaction) => {
      const existing = await transaction.get(historyRef);
      if (existing.exists()) return false;

      transaction.set(historyRef, {
        id: historyId,
        workspaceUid,
        dealId,
        leadId,
        quoteId,
        quoteNo,
        customerName,
        customerNameLower: normalizeSearchText(customerName),
        normalizedPhone,
        taskTitle: String(taskTitle || "Completed Task").trim() || "Completed Task",
        taskTitleLower: normalizeSearchText(taskTitle || "Completed Task"),
        taskDescription: String(taskDescription || "").trim(),
        taskType,
        status: TASK_HISTORY_STATUS_COMPLETED,
        searchText: taskSearchIndex.searchText,
        searchKeywords: taskSearchIndex.searchKeywords,
        dueDate: dueTimestamp,
        completedAt: serverTimestamp(),
        completedByUid: actor.uid || "",
        completedByName: actor.name || actor.email || actor.uid || "Workspace User",
        completedByEmail: actor.email || "",
        sourceModule,
        sourceTaskId: String(sourceTaskId),
        originalPayload: cleanPayload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    });

    invalidateWorkspaceCollectionCache(workspaceUid, "task_history");

    if (didCreate) {
      const completedAt = new Date();
      setCrmTaskHistoryRecords((prev) => {
        if (prev.some((record) => record.id === historyId)) return prev;
        return [
          {
            id: historyId,
            workspaceUid,
            dealId,
            leadId,
            quoteId,
            quoteNo,
            customerName,
            customerNameLower: normalizeSearchText(customerName),
            normalizedPhone,
            taskTitle: String(taskTitle || "Completed Task").trim() || "Completed Task",
            taskTitleLower: normalizeSearchText(taskTitle || "Completed Task"),
            taskDescription: String(taskDescription || "").trim(),
            taskType,
            status: TASK_HISTORY_STATUS_COMPLETED,
            searchText: taskSearchIndex.searchText,
            searchKeywords: taskSearchIndex.searchKeywords,
            dueDate: dueMillis > 0 ? new Date(dueMillis) : null,
            completedAt,
            completedByUid: actor.uid || "",
            completedByName: actor.name || actor.email || actor.uid || "Workspace User",
            completedByEmail: actor.email || "",
            sourceModule,
            sourceTaskId: String(sourceTaskId),
            originalPayload: cleanPayload,
            createdAt: completedAt,
            updatedAt: completedAt,
          },
          ...prev,
        ];
      });
    }
  },
  [getCurrentTaskHistoryActor, workspaceUid]
);

const resolveWorkspaceState = useCallback(async (authUser) => {
  if (!authUser?.uid) return null;

  const activeAccountId = accountUser?.activeAccountId || accountUser?.accountId || "";
  if (activeAccountId) {
    const legacyWorkspaceId =
      currentAccount?.dataOwnerUid ||
      currentAccount?.workspaceOwnerUid ||
      accountWorkspaceUid ||
      account?.dataOwnerUid ||
      account?.workspaceOwnerUid ||
      account?.ownerUid ||
      authUser.uid;
    const accountRole = workspaceBootstrapRole(accountUser?.role);
    const accountProfile = {
      workspaceId: legacyWorkspaceId,
      ownerUid: legacyWorkspaceId,
      memberId:
        accountUser?.memberId ||
        accountUser?.member?.uid ||
        authUser.uid,
      role: accountRole,
      status: accountUser?.member?.isActive === false ? "disabled" : "active",
      permissions:
        accountUser?.member?.permissions ||
        accountUser?.permissions ||
        {},
      email: accountUser?.email || authUser.email || "",
      name: accountUser?.displayName || authUser.displayName || "",
    };

    return normalizeWorkspaceProfile(accountProfile, authUser);
  }

  const profileRef = doc(db, "users", authUser.uid, "profile", "main");
  const profileSnap = await trackedGetDoc(profileRef, {
    label: "legacyWorkspaceProfile",
  });

  if (!profileSnap.exists()) {
    const ownerProfile = buildOwnerWorkspaceProfile(authUser);
    await setDoc(
      profileRef,
      {
        ...ownerProfile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return normalizeWorkspaceProfile(ownerProfile, authUser);
  }

  const resolvedProfile = normalizeWorkspaceProfile(profileSnap.data(), authUser);

  if (resolvedProfile.role !== "staff") {
    const ownerProfile = buildOwnerWorkspaceProfile(authUser);
    await setDoc(
      profileRef,
      {
        ...ownerProfile,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return normalizeWorkspaceProfile(ownerProfile, authUser);
  }

  return resolvedProfile;
}, [account, accountUser, accountWorkspaceUid, currentAccount]);

const syncWorkspaceMemberProfiles = useCallback(
  async (members, workspaceOwnerId, previousMembers = []) => {
    if (!workspaceOwnerId) return;

    if (process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 === 'true') {
      const callable = httpsCallable(functions, 'provisionEmployee');
      const accountId = account?.id || accountUser?.activeAccountId || accountUser?.accountId;
      const previous = normalizeTeamMembers(previousMembers);
      const current = normalizeTeamMembers(members);
      const results = [];
      for (const member of [...current, ...previous.filter(old => !current.some(item => item.uid === old.uid))]) {
        const old = previous.find(item => item.uid === member.uid);
        const removed = !current.some(item => item.uid === member.uid);
        if (!member.uid || member.uid === user?.uid) continue;
        if (!removed && old && JSON.stringify([old.role, old.permissions, old.isActive, old.name]) === JSON.stringify([member.role, member.permissions, member.isActive, member.name])) continue;
        const response = await callable({ accountId, workspaceUid: workspaceOwnerId,
          requestId: window.crypto.randomUUID(), expectedRevision: old?.accessRevision || 0,
          action: removed ? 'remove' : member.isActive === false && member.status !== 'identity_pending' ? 'suspend'
            : old?.isActive === false && old?.status !== 'identity_pending' ? 'restore' : 'update',
          email: member.email, name: member.name, phone: member.phone, role: member.role || 'staff', permissions: member.permissions, sendSetupEmail: false });
        results.push(response.data.staff);
      }
      return results;
    }

    const normalizedMembers = normalizeTeamMembers(members);
    const normalizedPreviousMembers = normalizeTeamMembers(previousMembers);
    const disabledPermissions = TEAM_PERMISSION_DEFINITIONS.reduce(
      (accumulator, permission) => ({
        ...accumulator,
        [permission.key]: false,
      }),
      {}
    );
    const profileWrites = normalizedMembers
      .filter((member) => member.uid)
      .map((member) =>
        setDoc(
          doc(db, "users", member.uid, "profile", "main"),
          {
            workspaceId: workspaceOwnerId,
            ownerUid: workspaceOwnerId,
            memberId: member.id,
            role: "staff",
            status:
              member.isActive === false || member.status === "disabled"
                ? "disabled"
                : member.status || "active",
            permissions: normalizePermissionSet(member.permissions),
            email: member.email,
            name: member.name,
            inviteId: member.inviteId || member.id,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        )
      );

    const revokedProfileWrites = normalizedPreviousMembers
      .filter(
        (member) =>
          member.uid &&
          !normalizedMembers.some((current) => current.uid === member.uid)
      )
      .map((member) =>
        setDoc(
          doc(db, "users", member.uid, "profile", "main"),
          {
            workspaceId: workspaceOwnerId,
            ownerUid: workspaceOwnerId,
            memberId: member.id,
            role: "staff",
            status: "disabled",
            permissions: disabledPermissions,
            email: member.email,
            name: member.name,
            inviteId: member.inviteId || member.id,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );

    await Promise.all([...profileWrites, ...revokedProfileWrites]);
  },
  [account?.id, accountUser?.activeAccountId, accountUser?.accountId, user?.uid]
);

const resetTeamMemberModalState = useCallback(() => {
  setTeamMemberDraft(
    createTeamMemberDraft({
      permissions: DEFAULT_TEAM_PERMISSIONS,
      status: "draft",
      isActive: true,
    })
  );
  setTeamMemberModalError("");
}, []);

const openTeamMemberInviteModal = useCallback(() => {
  resetTeamMemberModalState();
  setIsTeamMemberModalOpen(true);
}, [resetTeamMemberModalState]);

const closeTeamMemberInviteModal = useCallback(() => {
  setIsTeamMemberModalOpen(false);
  resetTeamMemberModalState();
}, [resetTeamMemberModalState]);

const updateTeamMemberDraftField = useCallback((key, value) => {
  setTeamMemberDraft((prev) => ({
    ...prev,
    [key]:
      key === "permissions"
        ? normalizePermissionSet(value)
        : value,
  }));
}, []);

const updateTeamMemberDraftPermission = useCallback((permissionKey, enabled) => {
  setTeamMemberDraft((prev) => ({
    ...prev,
    permissions: normalizePermissionSet({
      ...prev.permissions,
      [permissionKey]: enabled,
    }),
  }));
}, []);

const updateSavedTeamMemberPermission = useCallback((memberId, permissionKey, enabled) => {
  setTeamMembers((prev) =>
    normalizeTeamMembers(
      prev.map((member) =>
        member.id === memberId
          ? {
              ...member,
              permissions: normalizePermissionSet({
                ...member.permissions,
                [permissionKey]: enabled,
              }),
            }
          : member
      )
    )
  );
}, []);

const sendTeamMemberInvite = useCallback(
  async (draftMember = teamMemberDraft, options = {}) => {
    if (!user?.uid || !workspaceUid || !isWorkspaceOwner) {
      setTeamMemberModalError("Only the workspace owner can invite staff.");
      return;
    }

    const normalizedEmail = normalizeEmailValue(draftMember?.email || "");
    const trimmedName = String(draftMember?.name || "").trim();

    if (!trimmedName) {
      setTeamMemberModalError("Please enter the team member's name.");
      return;
    }

    if (!normalizedEmail) {
      setTeamMemberModalError("Please enter a valid email address.");
      return;
    }

    const memberId = String(draftMember?.id || createClientId("team-member"));
    const inviteAccountId = String(
      currentAccount?.id ||
        account?.id ||
        accountUser?.activeAccountId ||
        accountUser?.accountId ||
        workspaceUid ||
        ""
    ).trim();
    const isResend =
      options.resend === true || String(draftMember?.status || "") === "invited";
    const inviteSource = isResend ? "resend" : "add";
    const settingsRef = doc(db, "users", workspaceUid, "settings", "main");
    const inviteRef = doc(db, "users", workspaceUid, "team_invites", memberId);

    if (isDevRuntime) {
      devLog("TEAM INVITE START", {
        source: inviteSource,
        workspaceUid,
        inviteId: memberId,
        email: normalizedEmail,
      });
      if (isResend) {
        devLog("TEAM INVITE RESEND START", {
          workspaceUid,
          inviteId: memberId,
          email: normalizedEmail,
        });
      }
    }

    if (isResend) {
      setResendingTeamInviteId(memberId);
    } else {
      setIsSendingTeamInvite(true);
    }
    setTeamMemberModalError("");

    let inviteStage = "prepare";
    try {
      const normalizedPermissions = normalizePermissionSet(
        draftMember?.permissions || DEFAULT_TEAM_PERMISSIONS
      );
      const normalizedRole = String(draftMember?.role || "").trim();
      const normalizedPhone = String(draftMember?.phone || "").trim();
      const existingMember = normalizeTeamMembers(teamMembers).find(
        (member) => member.id === memberId
      );

      inviteStage = isResend ? "backend_send_staff_setup_link" : "backend_create_or_invite_staff";
      const staffCallable = httpsCallable(
        functions,
        process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 === 'true'
          ? isResend ? 'sendEmployeeSetupLink' : 'provisionEmployee'
          : isResend ? "sendStaffPasswordSetupLink" : "createOrInviteStaffUser",
        { timeout: 60000 }
      );
      const staffResult = await staffCallable({
        workspaceUid,
        accountId: inviteAccountId || workspaceUid,
        staffId: memberId,
        name: trimmedName,
        email: normalizedEmail,
        phone: normalizedPhone,
        role: normalizedRole || "staff",
        permissions: normalizedPermissions,
        isActive: true,
        sendSetupEmail: true,
        requestId: window.crypto.randomUUID(),
        expectedRevision: existingMember?.accessRevision || 0,
        uid: existingMember?.uid || draftMember?.uid || '',
      });
      const staffResponse = staffResult?.data || {};
      if (staffResponse?.ok === false) {
        const backendError = new Error(
          staffResponse.message || "Staff login setup email could not be sent."
        );
        backendError.code =
          staffResponse.emailErrorCode ||
          staffResponse.errorCode ||
          "staff-invite/backend-not-ok";
        throw backendError;
      }

      {
        const inviteTimestamp = new Date().toISOString();
        const patchedMember = createTeamMemberDraft({
          ...existingMember,
          ...draftMember,
          ...(staffResponse.staff || {}),
          id: staffResponse.staffId || memberId,
          staffId: staffResponse.staffId || memberId,
          inviteId: staffResponse.staffId || memberId,
          workspaceId: workspaceUid,
          accountId: inviteAccountId || workspaceUid,
          uid: staffResponse.uid || staffResponse.authUid || draftMember?.uid || "",
          authUid: staffResponse.authUid || staffResponse.uid || draftMember?.authUid || "",
          email: staffResponse.normalizedEmail || normalizedEmail,
          name: trimmedName,
          phone: normalizedPhone,
          role: staffResponse.staff?.role || normalizedRole || "staff",
          status: staffResponse.staff?.status || existingMember?.status || "invited",
          isActive: staffResponse.staff?.isActive ?? existingMember?.isActive ?? false,
          permissions: staffResponse.staff?.permissions ? normalizePermissionSet(staffResponse.staff.permissions) : normalizedPermissions,
          inviteEmailSent: staffResponse.setupEmailSent === true,
          emailStatus: staffResponse.setupEmailSent ? "sent" : "failed",
          emailFailureCode: staffResponse.emailErrorCode || "",
          emailFailureMessage: staffResponse.emailErrorMessage || "",
          lastSetupEmailStatus: staffResponse.setupEmailSent ? "success" : "failed",
          lastSetupEmailSentAt: staffResponse.setupEmailSent ? inviteTimestamp : existingMember?.lastSetupEmailSentAt || null,
          loginStatus: staffResponse.authUserCreated ? "auth_created" : "auth_ready",
          invitedAt: existingMember?.invitedAt || inviteTimestamp,
          lastInviteSentAt: inviteTimestamp,
          updatedAt: inviteTimestamp,
        });

        setTeamMembers((prev) =>
          normalizeTeamMembers([
            ...prev.filter(
              (member) =>
                member.id !== memberId &&
                normalizeEmailValue(member.email || "") !== normalizedEmail
            ),
            patchedMember,
          ])
        );

        if (options.closeAfter !== false && !isResend) {
          closeTeamMemberInviteModal();
        }

        if (isDevRuntime) {
          devLog(isResend ? "TEAM INVITE RESEND SUCCESS" : "TEAM INVITE SUCCESS", {
            source: inviteSource,
            workspaceUid,
            accountId: inviteAccountId || workspaceUid,
            inviteId: memberId,
            authUid: staffResponse.authUid || staffResponse.uid || "",
            setupEmailSent: staffResponse.setupEmailSent === true,
            emailStatus: staffResponse.emailStatus || "",
            emailErrorCode: staffResponse.emailErrorCode || "",
          });
        }

        if (staffResponse.setupEmailSent) {
          notify(
            isResend
              ? `Password setup email sent to ${normalizedEmail}.`
              : staffResponse.message || "Staff login created and setup email sent successfully.",
            "success"
          );
        } else {
          const warning = staffPasswordSetupError(staffResponse);
          setTeamMemberModalError(warning);
          notify(warning, "error", 7000);
        }
        return;
      }

    } catch (error) {
      if (process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 !== 'true' && !isResend && memberId && workspaceUid) {
        const failedTimestamp = new Date().toISOString();
        await setDoc(
          inviteRef,
          {
            inviteEmailSent: false,
            emailStatus: "failed",
            emailFailureCode: error?.code || "",
            emailFailureMessage: error?.message || String(error || ""),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ).catch(() => {});
        await runTransaction(db, async (transaction) => {
          const settingsSnap = await transaction.get(settingsRef);
          const existingMembers = normalizeTeamMembers(
            settingsSnap.exists() ? settingsSnap.data()?.teamMembers : []
          );
          const nextMembers = existingMembers.map((member) =>
            member.id === memberId
              ? {
                  ...member,
                  inviteEmailSent: false,
                  emailStatus: "failed",
                  emailFailureCode: error?.code || "",
                  emailFailureMessage: error?.message || String(error || ""),
                  updatedAt: failedTimestamp,
                }
              : member
          );
          transaction.set(
            settingsRef,
            {
              teamMembers: nextMembers,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }).catch(() => {});
      }
      if (isDevRuntime) {
        const failurePayload = {
          source: inviteSource,
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          workspaceUid,
          inviteId: memberId,
          email: normalizedEmail,
          stage: inviteStage,
        };
        if (isResend) {
          devError("TEAM INVITE RESEND FAILED EXACT", failurePayload);
        } else {
          devError("TEAM INVITE FAILED EXACT", failurePayload);
        }
      }
      if (typeof window !== "undefined") {
        window.__FQ_LAST_TEAM_INVITE_ERROR__ = {
          source: inviteSource,
          stage: inviteStage,
          code: error?.code || "",
          message: error?.message || String(error || ""),
          workspaceUid,
          inviteId: memberId,
        };
      }
      const message = staffPasswordSetupError(error);
      setTeamMemberModalError(message);
      notify(message, "error", 6000);
    } finally {
      if (isResend) {
        setResendingTeamInviteId("");
      } else {
        setIsSendingTeamInvite(false);
      }
    }
  },
  [
    account?.id,
    accountUser?.accountId,
    accountUser?.activeAccountId,
    closeTeamMemberInviteModal,
    currentAccount?.id,
    isWorkspaceOwner,
    notify,
    teamMemberDraft,
    teamMembers,
    user,
    workspaceUid,
  ]
);

const diagnoseTeamMemberLogin = useCallback(
  async (member) => {
    if (!member?.email || !workspaceUid || !isWorkspaceOwner) return;
    const memberId = String(member.id || member.staffId || member.memberId || "");
    const accountId = String(
      currentAccount?.id ||
        account?.id ||
        accountUser?.activeAccountId ||
        accountUser?.accountId ||
        workspaceUid ||
        ""
    ).trim();
    setDiagnosingTeamMemberId(memberId);
    setTeamMemberModalError("");
    try {
      const callable = httpsCallable(functions, "diagnoseStaffLogin");
      const result = await callable({
        workspaceUid,
        accountId,
        staffId: memberId,
        email: normalizeEmailValue(member.email || ""),
      });
      const diagnostic = result?.data || {};
      devLog("STAFF_LOGIN_DIAGNOSTIC_TRACE", { staffId: memberId, emailCanonical: diagnostic.emailCanonical, staffRecordExists: diagnostic.staffRecordExists, authUserExists: diagnostic.authUserExists, membershipExists: diagnostic.membershipExists, issues: diagnostic.issues });
      setTeamMembers((prev) =>
        normalizeTeamMembers(
          prev.map((current) =>
            current.id === memberId
              ? {
                  ...current,
                  authUid: diagnostic.authUid || current.authUid || current.uid || "",
                  authLinked: diagnostic.authUserExists === true,
                  emailFailureCode: diagnostic.lastErrorCode || current.emailFailureCode || "",
                  emailFailureMessage: diagnostic.lastErrorMessage || current.emailFailureMessage || "",
                  uid: diagnostic.authUid || current.uid || "",
                  loginStatus: diagnostic.canLogin
                    ? "ready"
                    : diagnostic.issues?.includes("Firebase Auth user missing")
                    ? "auth_missing"
                    : diagnostic.issues?.includes("Workspace membership missing")
                    ? "membership_missing"
                    : diagnostic.issues?.length
                    ? "needs_repair"
                    : "ready",
                  loginIssues: Array.isArray(diagnostic.issues) ? diagnostic.issues : [],
                  updatedAt: new Date().toISOString(),
                }
              : current
          )
        )
      );
      notify(
        diagnostic.canLogin
          ? "Staff login is ready."
          : `Login check found: ${(diagnostic.issues || []).join(", ") || "Needs repair"}.`,
        diagnostic.canLogin ? "success" : "error",
        7000
      );
    } catch (error) {
      const message = staffPasswordSetupError(error);
      setTeamMemberModalError(message);
      notify(message, "error", 7000);
    } finally {
      setDiagnosingTeamMemberId("");
    }
  },
  [
    account?.id,
    accountUser?.accountId,
    accountUser?.activeAccountId,
    currentAccount?.id,
    isWorkspaceOwner,
    notify,
    workspaceUid,
  ]
);

const repairTeamMemberLogin = useCallback(
  async (member) => {
    if (!member?.email || !workspaceUid || !isWorkspaceOwner) return;
    const memberId = String(member.id || member.staffId || member.memberId || "");
    const accountId = String(
      currentAccount?.id ||
        account?.id ||
        accountUser?.activeAccountId ||
        accountUser?.accountId ||
        workspaceUid ||
        ""
    ).trim();
    setRepairingTeamMemberId(memberId);
    setTeamMemberModalError("");
    try {
      const callable = httpsCallable(functions, "repairStaffLogin");
      const result = await callable({
        workspaceUid,
        accountId,
        staffId: memberId,
        name: member.name || "",
        email: normalizeEmailValue(member.email || ""),
        phone: member.phone || "",
        role: member.role || "staff",
        permissions: normalizePermissionSet(member.permissions || {}),
        isActive: member.isActive !== false,
        sendSetupEmail: false,
      });
      const response = result?.data || {};
      setTeamMembers((prev) =>
        normalizeTeamMembers(
          prev.map((current) =>
            current.id === memberId
              ? {
                  ...current,
                  ...(response.staff || {}),
                  uid: response.uid || response.authUid || current.uid || "",
                  authUid: response.authUid || response.uid || current.authUid || "",
                  staffId: response.staffId || memberId,
                  loginStatus: response.membershipRepaired ? "ready" : "needs_repair",
                  updatedAt: new Date().toISOString(),
                }
              : current
          )
        )
      );
      notify(
        response.message || "Staff login repaired. Use Send Password Setup Link next.",
        response.membershipRepaired ? "success" : "error",
        7000
      );
    } catch (error) {
      const message = staffPasswordSetupError(error);
      setTeamMemberModalError(message);
      notify(message, "error", 7000);
    } finally {
      setRepairingTeamMemberId("");
    }
  },
  [
    account?.id,
    accountUser?.accountId,
    accountUser?.activeAccountId,
    currentAccount?.id,
    isWorkspaceOwner,
    notify,
    workspaceUid,
  ]
);

const disableTeamMemberAccess = useCallback(
  async (member) => {
    if (!member?.email || !workspaceUid || !isWorkspaceOwner) return;
    const memberId = String(member.id || member.staffId || member.memberId || "");
    const accountId = String(
      currentAccount?.id ||
        account?.id ||
        accountUser?.activeAccountId ||
        accountUser?.accountId ||
        workspaceUid ||
        ""
    ).trim();
    setDisablingTeamMemberId(memberId);
    setTeamMemberModalError("");
    try {
      const callable = httpsCallable(functions, process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 === 'true' ? 'provisionEmployee' : "disableStaffAccess");
      const result = await callable({
        workspaceUid,
        accountId,
        staffId: memberId,
        email: normalizeEmailValue(member.email || ""),
        requestId: window.crypto.randomUUID(), action: 'suspend', expectedRevision: member.accessRevision || 0,
        role: member.role || 'staff', permissions: member.permissions, sendSetupEmail: false,
      });
      setTeamMembers((prev) =>
        normalizeTeamMembers(
          prev.map((current) =>
            current.id === memberId
              ? {
                  ...current,
                  ...(result.data?.staff || {}),
                  status: "disabled",
                  isActive: false,
                  loginStatus: "disabled",
                  disabledAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                }
              : current
          )
        )
      );
      notify("Staff access disabled.", "success");
    } catch (error) {
      const message = error?.message || "Could not disable staff access.";
      setTeamMemberModalError(message);
      notify(message, "error", 7000);
    } finally {
      setDisablingTeamMemberId("");
    }
  },
  [
    account?.id,
    accountUser?.accountId,
    accountUser?.activeAccountId,
    currentAccount?.id,
    isWorkspaceOwner,
    notify,
    workspaceUid,
  ]
);

const sendTeamInviteTestEmail = useCallback(async () => {
  if (!isDevRuntime) return;

  setIsTestingTeamInviteEmail(true);
  setTeamMemberModalError("");

  try {
    const message =
      "Client-side invite email testing is disabled. Use Send Setup Link or Resend Setup Link to test the backend SMTP staff invite flow.";
    setTeamMemberModalError(message);
    notify(message, "error", 7000);
  } catch (testError) {
    if (isDevRuntime) {
      devError("TEAM INVITE TEST EMAIL FAILED EXACT", {
        code: testError?.code,
        message: testError?.message,
        stack: testError?.stack,
      });
    }
    const message = testError?.code
      ? getInviteEmailErrorMessage(testError)
      : testError?.message || "Staff login setup email could not be sent.";
    setTeamMemberModalError(message);
    notify(message, "error", 6000);
  } finally {
    setIsTestingTeamInviteEmail(false);
  }
}, [notify]);

const getStageMetaForType = useCallback(
  (quoteType, stageId) =>
    getCrmStage(
      crmPipelines,
      quoteType,
      stageId || getDefaultCrmStageId(crmPipelines, quoteType)
    ),
  [crmPipelines]
);

const createCrmLeadDraftState = useCallback(
  (quoteType = CURTAIN_QUOTE_TYPE) => {
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes
        ? GENERAL_QUOTE_TYPE
        : canAccessCurtainQuotes
        ? CURTAIN_QUOTE_TYPE
        : GENERAL_QUOTE_TYPE;
    const stageId = getDefaultCrmStageId(crmPipelines, normalizedQuoteType);

    return {
      title: "",
      companyOrProject: "",
      contact: {
        name: "",
        phone: "",
        whatsapp: "",
        email: "",
        address: "",
      },
      source: "",
      tags: "",
      notes: "",
      quoteType: normalizedQuoteType,
      pipelineType: normalizedQuoteType,
      stageId,
      expectedValue: "",
      nextAction: "",
      nextActionDate: "",
      assignedToUserId: "",
      assignedToNameSnapshot: "",
      reminderConfigs: normalizeCrmReminderConfigs({}, {}),
      attachments: [],
      executionSpecs: [],
      siteLocations: [],
    };
  },
  [canAccessCurtainQuotes, canAccessGeneralQuotes, crmPipelines]
);

const buildCrmLeadData = useCallback(
  (leadLike = {}, overrides = {}) => {
    const quoteType =
      overrides.quoteType ||
      leadLike.quoteType ||
      leadLike.pipelineType ||
      CURTAIN_QUOTE_TYPE;
    const normalizedQuoteType = isGeneralExecutionEligibleDeal({
      pipelineType: quoteType,
      quoteType,
    })
      ? GENERAL_QUOTE_TYPE
      : CURTAIN_QUOTE_TYPE;
    const stageId =
      overrides.stageId ||
      leadLike.stageId ||
      getDefaultCrmStageId(crmPipelines, normalizedQuoteType);
    const stageMeta = getStageMetaForType(normalizedQuoteType, stageId);
    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike, overrides);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const hasQuote = Object.prototype.hasOwnProperty.call(overrides, "hasQuote")
      ? overrides.hasQuote === true && linkedQuotes.length > 0
      : linkedQuotes.length > 0;
    const sourceValue = String(
      overrides.source ??
        overrides.contact?.source ??
        leadLike.source ??
        leadLike.contact?.source ??
        ""
    );
    const contact = {
      name: String(
        overrides.contact?.name ??
          leadLike.contact?.name ??
          leadLike.contactName ??
          leadLike.customerName ??
          ""
      ),
      phone: String(
        overrides.contact?.phone ??
          leadLike.contact?.phone ??
          leadLike.phone ??
          ""
      ),
      whatsapp: String(
        overrides.contact?.whatsapp ??
          leadLike.contact?.whatsapp ??
          leadLike.whatsapp ??
          ""
      ),
      email: String(
        overrides.contact?.email ??
          leadLike.contact?.email ??
          leadLike.email ??
          ""
      ),
      address: String(
        overrides.contact?.address ??
          leadLike.contact?.address ??
          leadLike.address ??
          ""
      ),
      source: sourceValue,
      notes: String(
        overrides.contact?.notes ??
          leadLike.contact?.notes ??
          ""
      ),
    };
    const hasChecklistOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "checklistItems"
    );
    const nextChecklistItems = hasChecklistOverride
      ? preserveChecklistDraftTree(overrides.checklistItems || [], {
          templateMode: false,
          idPrefix: "crm-check",
        })
      : normalizeDealChecklist(
          leadLike.checklistItems ||
            leadLike.checklist ||
            leadLike.stageChecklist ||
            leadLike.crm?.checklistItems ||
            leadLike.crm?.checklist,
          stageMeta
        );
    const expectedValue = Number(
      overrides.expectedValue ?? leadLike.expectedValue ?? 0
    );
    const finalValue = Number(
      overrides.finalValue ?? leadLike.finalValue ?? expectedValue
    );
    const legacyNextActionDate =
      overrides.nextActionDate ?? leadLike.nextActionDate ?? null;
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "installationDate"
    );
    const installationDate = hasInstallationDateOverride
      ? overrides.installationDate
      : leadLike.installationDate ?? null;
    const sourceReminderConfigs = {
      ...(leadLike.reminderConfigs || {}),
      ...(overrides.reminderConfigs || {}),
    };
    const sourceInstallationReminder =
      overrides.installationReminder ??
      overrides.reminderConfig ??
      leadLike.installationReminder ??
      leadLike.reminderConfig ??
      leadLike.crm?.installationReminder ??
      leadLike.crm?.reminderConfig;
    if (sourceInstallationReminder) {
      sourceReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] =
        sourceInstallationReminder;
    }
    const reminderConfigs = normalizeCrmReminderConfigs(
      sourceReminderConfigs,
      {
        nextActionDate: legacyNextActionDate,
        installationDate,
      }
    );
    const installationReminder = buildCanonicalInstallationReminder(
      {
        ...leadLike,
        ...overrides,
        installationReminder: sourceInstallationReminder,
        reminderConfigs,
      },
      { installationDate, reminderConfigs }
    );
    const followUps = normalizeCrmFollowUps(
      overrides.followUps ?? leadLike.followUps,
      {
        nextAction: overrides.nextAction ?? leadLike.nextAction ?? "",
        nextActionDate: legacyNextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      }
    );
    const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
    const nextActionDate = primaryFollowUp?.dueDate
      ? parseDateInputValue(primaryFollowUp.dueDate)
      : legacyNextActionDate;
    const attachments = normalizeCrmAttachments(
      overrides.attachments ?? leadLike.attachments
    );
    const executionSpecs = normalizeExecutionSpecs(
      overrides.executionSpecs ?? leadLike.executionSpecs
    );
    const siteLocations = normalizeSiteLocations(
      overrides.siteLocations ?? leadLike.siteLocations ?? leadLike.crm?.siteLocations
    );
    const title =
      String(
        overrides.title ??
          leadLike.title ??
          ""
      ).trim() ||
      String(overrides.companyOrProject ?? leadLike.companyOrProject ?? "").trim() ||
      String(contact.name || "").trim() ||
      "New Lead";
    const salesStageId = normalizeSalesStageIdentityValue(
      overrides.salesStageId ??
        overrides.opportunityStage ??
        leadLike.salesStageId ??
        leadLike.opportunityStage ??
        leadLike.stage ??
        stageId
    );
    const salesStatus = normalizeOpportunityStatus(
      overrides.salesStatus ??
        leadLike.salesStatus ??
        overrides.status ??
        leadLike.status ??
        ""
    );

    return stripLegacyCrmQuoteLinkFields({
      ...leadLike,
      title,
      contact,
      companyOrProject: String(
        overrides.companyOrProject ?? leadLike.companyOrProject ?? ""
      ),
      source: sourceValue,
      customerId: String(overrides.customerId ?? leadLike.customerId ?? ""),
      customerKey: String(
        overrides.customerKey ??
          leadLike.customerKey ??
          leadLike.customerId ??
          ""
      ),
      customerName: String(
        overrides.customerName ??
          leadLike.customerName ??
          contact.name ??
          title ??
          ""
      ),
      customerPhone: String(
        overrides.customerPhone ??
          leadLike.customerPhone ??
          contact.phone ??
          contact.whatsapp ??
          ""
      ),
      customerWhatsapp: String(
        overrides.customerWhatsapp ??
          leadLike.customerWhatsapp ??
          contact.whatsapp ??
          ""
      ),
      customerEmail: String(
        overrides.customerEmail ??
          leadLike.customerEmail ??
          contact.email ??
          ""
      ),
      customerAddress: String(
        overrides.customerAddress ??
          leadLike.customerAddress ??
          contact.address ??
          ""
      ),
      customerCompanyName: String(
        overrides.customerCompanyName ??
          leadLike.customerCompanyName ??
          leadLike.companyOrProject ??
          ""
      ),
      quoteType: normalizedQuoteType,
      pipelineType: normalizedQuoteType,
      stageId: stageMeta?.id || stageId,
      salesStageId,
      opportunityStage: salesStageId,
      salesStatus,
      stageNameSnapshot: stageMeta?.label || leadLike.stageNameSnapshot || stageId,
      stageColor: stageMeta?.color || leadLike.stageColor || "#64748b",
      linkedQuotes,
      hasQuote,
      checklistTemplateId:
        overrides.checklistTemplateId ??
        leadLike.checklistTemplateId ??
        stageMeta?.id ??
        "",
      checklistItems: nextChecklistItems,
      checklist: nextChecklistItems,
      stageChecklist: nextChecklistItems,
      expectedValue,
      finalValue,
      nextAction: String(
        primaryFollowUp?.title ??
          overrides.nextAction ??
          leadLike.nextAction ??
          ""
      ),
      nextActionDate,
      assignedToUserId: String(
        overrides.assignedToUserId ?? leadLike.assignedToUserId ?? ""
      ),
      assignedToNameSnapshot: String(
        overrides.assignedToNameSnapshot ??
          leadLike.assignedToNameSnapshot ??
          ""
      ),
      assignedAt: overrides.assignedAt ?? leadLike.assignedAt ?? null,
      assignedBy: String(overrides.assignedBy ?? leadLike.assignedBy ?? ""),
      tags: Array.isArray(overrides.tags)
        ? overrides.tags
        : Array.isArray(leadLike.tags)
        ? leadLike.tags
        : [],
      notes: String(overrides.notes ?? leadLike.notes ?? ""),
      installationDate,
      installationReminder,
      reminderConfig: installationReminder,
      followUps,
      reminderConfigs,
      attachments,
      executionSpecs,
      siteLocations,
      status: String(
        overrides.status ??
          leadLike.status ??
          (stageMeta?.confirmsQuote ? "confirmed" : "draft")
      ),
      createdAt: leadLike.createdAt || new Date(),
      updatedAt: overrides.updatedAt ?? leadLike.updatedAt ?? new Date(),
      lastActivityAt:
        overrides.lastActivityAt ??
        leadLike.lastActivityAt ??
        overrides.updatedAt ??
        leadLike.updatedAt ??
        leadLike.createdAt ??
        new Date(),
      lastEditedAt:
        overrides.lastEditedAt ??
        overrides.updatedAt ??
        leadLike.lastEditedAt ??
        leadLike.updatedAt ??
        null,
      lastEditedBy: String(overrides.lastEditedBy ?? leadLike.lastEditedBy ?? ""),
      lastEditedByName: String(overrides.lastEditedByName ?? leadLike.lastEditedByName ?? ""),
      primaryLinkedQuote,
    });
  },
  [buildCanonicalInstallationReminder, crmPipelines, getStageMetaForType]
);

const getStageMetaForQuote = useCallback(
  (quoteLike) => {
    const quoteType = resolveQuoteType(quoteLike);
    return getStageMetaForType(
      quoteType,
      quoteLike?.crm?.stageId || quoteLike?.workStage
    );
  },
  [getStageMetaForType]
);

const getStageLabelForQuote = useCallback(
  (quoteLike) => getStageMetaForQuote(quoteLike)?.label || "Quote Given",
  [getStageMetaForQuote]
);

const getStageColorForQuote = useCallback(
  (quoteLike) => getStageMetaForQuote(quoteLike)?.color || "#64748b",
  [getStageMetaForQuote]
);

const buildQuoteCrmData = useCallback(
  (quoteLike, overrides = {}) => {
    const quoteType = resolveQuoteType(quoteLike);
    const latestQuoteTotal = getNormalizedQuoteTotal(quoteLike);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE
        ? syncGeneralQuoteDefaults(getEmbeddedGeneralQuote(quoteLike))
        : null;
    const stageId =
      overrides.stageId ||
      quoteLike?.crm?.stageId ||
      quoteLike?.workStage ||
      getDefaultCrmStageId(crmPipelines, quoteType);
    const stageMeta = getStageMetaForType(quoteType, stageId);
    const contact = {
      ...buildCrmContactFromQuote(quoteLike, quoteType, embeddedGeneral),
      ...(quoteLike?.crm?.contact || {}),
      ...(overrides.contact || {}),
    };
    const nextActionDate =
      overrides.nextActionDate ?? quoteLike?.crm?.nextActionDate ?? null;
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "installationDate"
    );
    const installationDate = hasInstallationDateOverride
      ? overrides.installationDate
      : quoteLike?.crm?.installationDate ?? quoteLike?.installationDate ?? null;
    const hasChecklistOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "checklistItems"
    );
    const checklistItems = hasChecklistOverride
      ? preserveChecklistDraftTree(overrides.checklistItems || [], {
          templateMode: false,
          idPrefix: "crm-check",
        })
      : normalizeDealChecklist(
          quoteLike?.crm?.checklistItems ||
            quoteLike?.crm?.checklist ||
            quoteLike?.checklistItems ||
            quoteLike?.checklist,
          stageMeta
        );
    const sourceReminderConfigs = {
      ...(quoteLike?.crm?.reminderConfigs || {}),
      ...(overrides.reminderConfigs || {}),
    };
    const sourceInstallationReminder =
      overrides.installationReminder ??
      overrides.reminderConfig ??
      quoteLike?.crm?.installationReminder ??
      quoteLike?.crm?.reminderConfig;
    if (sourceInstallationReminder) {
      sourceReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] =
        sourceInstallationReminder;
    }
    const reminderConfigs = normalizeCrmReminderConfigs(
      sourceReminderConfigs,
      {
        nextActionDate,
        installationDate,
      }
    );
    const installationReminder = buildCanonicalInstallationReminder(
      {
        ...(quoteLike?.crm || {}),
        ...overrides,
        installationReminder: sourceInstallationReminder,
        reminderConfigs,
      },
      { installationDate, reminderConfigs }
    );
    const followUps = normalizeCrmFollowUps(
      overrides.followUps ?? quoteLike?.crm?.followUps,
      {
        nextAction: overrides.nextAction ?? quoteLike?.crm?.nextAction ?? "",
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      }
    );
    const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
    const summarizedNextActionDate = primaryFollowUp?.dueDate
      ? parseDateInputValue(primaryFollowUp.dueDate)
      : nextActionDate;
    const attachments = normalizeCrmAttachments(
      overrides.attachments ?? quoteLike?.crm?.attachments
    );
    const siteLocations = normalizeSiteLocations(
      overrides.siteLocations ?? quoteLike?.crm?.siteLocations
    );
    const linkedLeadId = overrides.leadId ?? quoteLike?.crm?.leadId ?? "";
    const executionSpecs = linkedLeadId
      ? []
      : normalizeExecutionSpecs(
          overrides.executionSpecs ?? quoteLike?.crm?.executionSpecs
        );
    const customerLabel =
      embeddedGeneral?.quoteFor?.company ||
      embeddedGeneral?.quoteFor?.name ||
      quoteLike?.customerName ||
      "Unnamed Customer";
    const salesStageId = normalizeSalesStageIdentityValue(
      overrides.salesStageId ??
        overrides.opportunityStage ??
        quoteLike?.salesStageId ??
        quoteLike?.opportunityStage ??
        quoteLike?.crm?.salesStageId ??
        quoteLike?.crm?.opportunityStage ??
        quoteLike?.crm?.stage ??
        quoteLike?.workStage ??
        stageId
    );
    const salesStatus = normalizeOpportunityStatus(
      overrides.salesStatus ??
        quoteLike?.salesStatus ??
        quoteLike?.crm?.salesStatus ??
        quoteLike?.crm?.status ??
        quoteLike?.status ??
        ""
    );

    return {
      pipelineType: quoteType,
      stageId: stageMeta?.id || stageId,
      salesStageId,
      opportunityStage: salesStageId,
      salesStatus,
      stageName: stageMeta?.label || stageId,
      stageColor: stageMeta?.color || "#64748b",
      leadId: linkedLeadId,
      source:
        overrides.source ??
        quoteLike?.crm?.source ??
        contact?.source ??
        "",
      customerId: overrides.customerId ?? quoteLike?.customerId ?? quoteLike?.crm?.customerId ?? "",
      customerKey:
        overrides.customerKey ??
        quoteLike?.customerKey ??
        quoteLike?.crm?.customerKey ??
        quoteLike?.customerId ??
        quoteLike?.crm?.customerId ??
        "",
      customerName:
        overrides.customerName ??
        quoteLike?.customerName ??
        customerLabel ??
        "",
      customerPhone:
        overrides.customerPhone ??
        quoteLike?.customerPhone ??
        contact?.phone ??
        contact?.whatsapp ??
        "",
      customerWhatsapp:
        overrides.customerWhatsapp ??
        quoteLike?.customerWhatsapp ??
        contact?.whatsapp ??
        "",
      customerEmail:
        overrides.customerEmail ??
        quoteLike?.customerEmail ??
        contact?.email ??
        "",
      customerAddress:
        overrides.customerAddress ??
        quoteLike?.customerAddress ??
        contact?.address ??
        "",
      customerCompanyName:
        overrides.customerCompanyName ??
        quoteLike?.customerCompanyName ??
        embeddedGeneral?.quoteFor?.company ??
        "",
      hasQuote:
        overrides.hasQuote ??
        quoteLike?.crm?.hasQuote ??
        Boolean(quoteLike?.id || quoteLike?.quoteNo),
      title:
        overrides.title ||
        quoteLike?.crm?.title ||
        `${customerLabel}${quoteLike?.quoteNo ? ` - #${quoteLike.quoteNo}` : ""}`,
      contact,
      checklistTemplateId:
        overrides.checklistTemplateId ||
        quoteLike?.crm?.checklistTemplateId ||
        stageMeta?.id ||
        "",
      checklistItems,
      checklist: checklistItems,
      stageChecklist: checklistItems,
      expectedValue: Number(
        overrides.expectedValue ??
          latestQuoteTotal
      ),
      finalValue: Number(
        overrides.finalValue ??
          latestQuoteTotal
      ),
      nextAction:
        primaryFollowUp?.title ??
        overrides.nextAction ??
        quoteLike?.crm?.nextAction ??
        "",
      nextActionDate: summarizedNextActionDate,
      assignedToUserId:
        overrides.assignedToUserId ?? quoteLike?.crm?.assignedToUserId ?? "",
      assignedToNameSnapshot:
        overrides.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedTo ??
        "",
      assignedTo:
        overrides.assignedTo ??
        overrides.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedTo ??
        quoteLike?.crm?.assignedToNameSnapshot ??
        "",
      assignedAt:
        overrides.assignedAt ?? quoteLike?.crm?.assignedAt ?? null,
      assignedBy:
        overrides.assignedBy ?? quoteLike?.crm?.assignedBy ?? "",
      installationDate,
      installationReminder,
      reminderConfig: installationReminder,
      status: overrides.status ?? quoteLike?.crm?.status ?? quoteLike?.status ?? "draft",
      tags: Array.isArray(overrides.tags)
        ? overrides.tags
        : Array.isArray(quoteLike?.crm?.tags)
        ? quoteLike.crm.tags
        : [],
      notes: overrides.notes ?? quoteLike?.crm?.notes ?? "",
      followUps,
      reminderConfigs,
      attachments,
      siteLocations,
      ...(linkedLeadId ? {} : { executionSpecs }),
      createdAt: quoteLike?.crm?.createdAt || quoteLike?.createdAt || new Date(),
      updatedAt: new Date(),
    };
  },
  [
    buildCanonicalInstallationReminder,
    crmPipelines,
    getNormalizedQuoteTotal,
    getStageMetaForType,
    syncGeneralQuoteDefaults,
  ]
);

const getQuoteGivenStageIdForType = useCallback(
  (quoteType = CURTAIN_QUOTE_TYPE) => {
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;
    const pipeline = getCrmPipeline(crmPipelines, normalizedQuoteType);
    const quoteGivenStage = (pipeline?.stages || []).find((stage) => {
      const stageText = `${stage?.id || ""} ${stage?.label || ""} ${stage?.name || ""}`
        .toLowerCase()
        .replace(/[_-]+/g, " ");
      return (
        stageText.includes("quote given") ||
        stageText.includes("quotation sent") ||
        stageText.includes("quote sent")
      );
    });

    return (
      quoteGivenStage?.id ||
      getDefaultCrmStageId(crmPipelines, normalizedQuoteType)
    );
  },
  [crmPipelines]
);

const isOpenCrmDeal = useCallback(
  (lead = {}) => {
    if (!lead || typeof lead !== "object") return false;
    const quoteType =
      lead.pipelineType === GENERAL_QUOTE_TYPE || lead.quoteType === GENERAL_QUOTE_TYPE
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;
    const stage = getCrmStage(crmPipelines, quoteType, lead.stageId);
    if (stage?.marksComplete === true || stage?.isCancelled === true) {
      return false;
    }

    const statusText = [
      lead.status,
      lead.dealStatus,
      lead.stageId,
      lead.stageNameSnapshot,
      stage?.id,
      stage?.label,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return !CRM_CLOSED_DEAL_KEYWORDS.some((keyword) =>
      statusText.includes(keyword)
    );
  },
  [crmPipelines]
);

const deriveSalesStageIdForQuote = useCallback((quoteLike = {}, crmData = {}) => {
  const statusText = [
    quoteLike.status,
    quoteLike.quoteStatus,
    quoteLike.crmStatus,
    quoteLike.crm?.status,
    quoteLike.crm?.crmStatus,
    crmData.status,
    crmData.crmStatus,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (isCancelledQuoteRecord(quoteLike) || /lost|cancelled|canceled|closed_lost/.test(statusText)) {
    return CRM_OPPORTUNITY_STATUS_LOST;
  }

  const explicitStage =
    quoteLike.salesStageId ||
    quoteLike.opportunityStage ||
    quoteLike.crm?.salesStageId ||
    quoteLike.crm?.opportunityStage ||
    quoteLike.crm?.stage ||
    crmData.salesStageId ||
    crmData.opportunityStage ||
    crmData.stage;
  if (explicitStage) return normalizeSalesStageIdentityValue(explicitStage);

  if (/won|closed_won/.test(statusText)) return CRM_OPPORTUNITY_STATUS_WON;
  if (
    quoteLike.confirmed === true ||
    quoteLike.isConfirmed === true ||
    /confirmed|order_confirmed/.test(statusText)
  ) {
    return "order_confirmed";
  }
  if (
    quoteLike.quoteSentAt ||
    quoteLike.sentAt ||
    crmData.quoteSentAt ||
    quoteLike.crm?.quoteSentAt ||
    /quote_sent|quotation_sent|sent/.test(statusText)
  ) {
    return "quotation_sent";
  }
  if (/draft/.test(statusText)) return "quotation_preparation";
  return normalizeOpportunityStage(quoteLike.workStage || quoteLike.stageId || crmData.stageId || "");
}, []);

const getDealSalesStageId = useCallback(
  (deal = {}) => {
    const statusText = [deal.status, deal.dealStatus, deal.crm?.status]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (/lost|cancelled|canceled|closed_lost/.test(statusText)) return CRM_OPPORTUNITY_STATUS_LOST;
    if (/won|closed_won/.test(statusText)) return CRM_OPPORTUNITY_STATUS_WON;
    if (/nurture|future|on_hold/.test(statusText)) return CRM_OPPORTUNITY_STATUS_NURTURE;

    const explicitStage =
      deal.salesStageId ||
      deal.opportunityStage ||
      deal.stage ||
      deal.crm?.salesStageId ||
      deal.crm?.opportunityStage ||
      deal.crm?.stage;
    if (explicitStage) return normalizeSalesStageIdentityValue(explicitStage);
    return normalizeOpportunityStage(deal.crmStageId || deal.stageId || deal.workStage || "");
  },
  []
);

const findOpenCustomerDeals = useCallback(
  (customerFields = {}, quoteType = "") => {
    const customerId = String(customerFields.customerId || "").trim();
    const customerKey = String(customerFields.customerKey || customerId || "").trim();
    const phoneKey = normalizeCustomerLedgerPhone(
      customerFields.customerPhone ||
        customerFields.phone ||
        customerFields.customerWhatsapp ||
        ""
    );
    const emailKey = normalizeCustomerLedgerEmail(
      customerFields.customerEmail || customerFields.email || ""
    );
    const nameKey = normalizeCustomerLedgerName(
      customerFields.customerName || customerFields.name || ""
    );
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;

    const openDeals = crmLeadRecords
      .filter((lead) => isOpenCrmDeal(lead))
      .filter((lead) => {
        const leadCustomerId = String(lead.customerId || lead.crm?.customerId || "").trim();
        const leadCustomerKey = String(
          lead.customerKey || lead.crm?.customerKey || leadCustomerId || ""
        ).trim();
        const leadContact = lead.contact || lead.crm?.contact || {};
        const leadPhoneKey = normalizeCustomerLedgerPhone(
          lead.customerPhone ||
            leadContact.phone ||
            leadContact.whatsapp ||
            ""
        );
        const leadEmailKey = normalizeCustomerLedgerEmail(
          leadContact.email || lead.email || ""
        );
        const leadNameKey = normalizeCustomerLedgerName(
          lead.customerName ||
            leadContact.name ||
            lead.companyOrProject ||
            lead.title ||
            ""
        );

        return (
          (customerId && leadCustomerId && customerId === leadCustomerId) ||
          (customerKey && leadCustomerKey && customerKey === leadCustomerKey) ||
          (phoneKey && leadPhoneKey && phoneKey === leadPhoneKey) ||
          (emailKey && leadEmailKey && emailKey === leadEmailKey) ||
          (nameKey && leadNameKey && nameKey === leadNameKey)
        );
      });

    const samePipelineDeals = openDeals.filter((lead) => {
      const leadType =
        lead.pipelineType === GENERAL_QUOTE_TYPE || lead.quoteType === GENERAL_QUOTE_TYPE
          ? GENERAL_QUOTE_TYPE
          : CURTAIN_QUOTE_TYPE;
      return leadType === normalizedQuoteType;
    });

    return (samePipelineDeals.length ? samePipelineDeals : openDeals).sort(
      (a, b) =>
        Number(safeTimeMillis(b.updatedAt || b.createdAt) || 0) -
        Number(safeTimeMillis(a.updatedAt || a.createdAt) || 0)
    );
  },
  [crmLeadRecords, isOpenCrmDeal]
);

const buildCrmDetailDraftFromDeal = useCallback(
  (dealLike) => {
    if (!dealLike) return null;
    const draftChecklistItems = cloneChecklistTree(
      dealLike.checklistItems || dealLike.checklist || dealLike.stageChecklist || [],
      {
        preserveIds: true,
        resetState: false,
        idPrefix: "crm-check",
      }
    );
    const draftReminderConfigs = normalizeCrmReminderConfigs(
      {
        ...(dealLike.reminderConfigs || dealLike.crm?.reminderConfigs || {}),
        [CRM_REMINDER_TYPE_INSTALLATION]:
          dealLike.installationReminder ||
          dealLike.crm?.installationReminder ||
          dealLike.reminderConfig ||
          dealLike.crm?.reminderConfig ||
          dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
          dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION],
      },
      {
        nextActionDate: dealLike.nextActionDate,
        installationDate: dealLike.installationDate,
      }
    );
    const draftInstallationReminder = buildCanonicalInstallationReminder(
      {
        ...dealLike,
        reminderConfigs: draftReminderConfigs,
      },
      {
        installationDate: dealLike.installationDate,
        reminderConfigs: draftReminderConfigs,
      }
    );

    return {
      stageId: dealLike.crmStageId || dealLike.stageId || "",
      stage: dealLike.crmStageId || dealLike.stageId || "",
      status: dealLike.status || dealLike.crmStatus || "draft",
      pipelineType:
        dealLike.pipelineType ||
        dealLike.quoteTypeResolved ||
        dealLike.quoteType ||
        dealLike.crm?.pipelineType ||
        "",
      source: dealLike.source || dealLike.crmContact?.source || "",
      title: dealLike.crmTitle || "",
      contact: {
        name: dealLike.crmContact?.name || "",
        phone: dealLike.crmContact?.phone || "",
        whatsapp: dealLike.crmContact?.whatsapp || "",
        email: dealLike.crmContact?.email || "",
        address: dealLike.crmContact?.address || "",
        source: dealLike.crmContact?.source || "",
        notes: dealLike.crmContact?.notes || "",
      },
      nextAction: dealLike.nextAction || "",
      nextActionDate: toDateInputValue(dealLike.nextActionDate),
      installationDate: toDateInputValue(dealLike.installationDate),
      notes: dealLike.crmNotes || "",
      assignedToUserId:
        dealLike.assignedToUserId ||
        dealLike.crm?.assignedToUserId ||
        "",
      assignedToNameSnapshot:
        dealLike.assignedToNameSnapshot ||
        dealLike.crm?.assignedToNameSnapshot ||
        dealLike.crm?.assignedTo ||
        "",
      assignedAt: dealLike.crm?.assignedAt || null,
      assignedBy: dealLike.crm?.assignedBy || "",
      tags: Array.isArray(dealLike.crm?.tags)
        ? dealLike.crm.tags.join(", ")
        : Array.isArray(dealLike.tags)
        ? dealLike.tags.join(", ")
        : "",
      checklistItems: draftChecklistItems,
      checklist: draftChecklistItems,
      stageChecklist: draftChecklistItems,
      reminderConfigs: draftReminderConfigs,
      installationReminder: draftInstallationReminder,
      reminderConfig: draftInstallationReminder,
      followUps: normalizeCrmFollowUps(
        dealLike.followUps || dealLike.crm?.followUps,
        {
          nextAction: dealLike.nextAction || dealLike.crm?.nextAction || "",
          nextActionDate: dealLike.nextActionDate || dealLike.crm?.nextActionDate || null,
          reminderConfig:
            dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            null,
        }
      ).map((followUp) => createCrmFollowUpDraft(followUp)),
      attachments: normalizeCrmAttachments(
        dealLike.attachments || dealLike.crm?.attachments
      ),
      executionSpecs: hydrateExecutionSpecDraftItems(
        resolveExecutionSpecsForCrmDeal(dealLike)
      ),
      siteLocations: normalizeSiteLocations(
        dealLike.siteLocations || dealLike.crm?.siteLocations || []
      ),
    };
  },
  [
    buildCanonicalInstallationReminder,
    createCrmFollowUpDraft,
    hydrateExecutionSpecDraftItems,
    resolveExecutionSpecsForCrmDeal,
  ]
);

const syncQuoteAnalyticsIndexes = useCallback(
  async (quoteId, quoteLike = {}, options = {}) => {
    if (!workspaceUid || !quoteId) return;
    const nextContribution = buildAnalyticsContributionForQuote({
      ...quoteLike,
      id: quoteId,
      quoteId,
    });
    if (!nextContribution) return;

    const contributionRef = doc(db, "users", workspaceUid, "quote_analytics_index", quoteId);
    const installationRef = doc(db, "users", workspaceUid, "installation_index", quoteId);

    const previousSnap = await getDoc(contributionRef, {
      label: "syncQuoteAnalyticsIndexes.previousContribution",
    }).catch((error) => {
      devWarn("QUOTE_ANALYTICS_PREVIOUS_CONTRIBUTION_READ_SKIPPED", {
        quoteId,
        code: error?.code || "",
        message: error?.message || String(error),
      });
      return null;
    });
    const previousContribution =
      previousSnap && typeof previousSnap.exists === "function" && previousSnap.exists()
        ? previousSnap.data() || null
        : null;
    const monthDeltas = new Map();
    const clientDeltas = new Map();

    const mergeDeltas = (targetMap, sourceMap) => {
      sourceMap.forEach((delta, key) => {
        targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
      });
    };

    if (previousContribution) {
      mergeDeltas(monthDeltas, getMonthlyDeltaFromContribution(previousContribution, -1));
      const oldClientMonth = previousContribution.confirmedMonthKey || previousContribution.quoteMonthKey;
      if (oldClientMonth) {
        const clientKey = `${oldClientMonth}_${previousContribution.customerKey || "unknown"}`;
        clientDeltas.set(
          clientKey,
          applySummaryDelta(clientDeltas.get(clientKey), {
            confirmedRevenue: -Number(previousContribution.confirmedRevenue || 0),
            confirmedCount:
              previousContribution.confirmed && previousContribution.confirmedMonthKey ? -1 : 0,
            quotationRevenue: -Number(previousContribution.quotationRevenue || 0),
            quotationCount: previousContribution.deleted ? 0 : -1,
          })
        );
      }
    }

    mergeDeltas(monthDeltas, getMonthlyDeltaFromContribution(nextContribution, 1));
    const nextClientMonth = nextContribution.confirmedMonthKey || nextContribution.quoteMonthKey;
    if (nextClientMonth) {
      const clientKey = `${nextClientMonth}_${nextContribution.customerKey || "unknown"}`;
      clientDeltas.set(
        clientKey,
        applySummaryDelta(clientDeltas.get(clientKey), {
          confirmedRevenue: Number(nextContribution.confirmedRevenue || 0),
          confirmedCount:
            nextContribution.confirmed && nextContribution.confirmedMonthKey ? 1 : 0,
          quotationRevenue: Number(nextContribution.quotationRevenue || 0),
          quotationCount: nextContribution.deleted ? 0 : 1,
        })
      );
    }

    const batch = writeBatch(db);
    monthDeltas.forEach((delta, monthKey) => {
      batch.set(
        doc(db, "users", workspaceUid, "analytics_monthly", monthKey),
        {
          monthKey,
          confirmedRevenue: increment(Number(delta.confirmedRevenue || 0)),
          confirmedCount: increment(Number(delta.confirmedCount || 0)),
          quotationRevenue: increment(Number(delta.quotationRevenue || 0)),
          quotationCount: increment(Number(delta.quotationCount || 0)),
          cancelledRevenue: increment(Number(delta.cancelledRevenue || 0)),
          cancelledCount: increment(Number(delta.cancelledCount || 0)),
          installedRevenue: increment(Number(delta.installedRevenue || 0)),
          installedCount: increment(Number(delta.installedCount || 0)),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    });

    clientDeltas.forEach((delta, clientDocId) => {
      const monthKey = clientDocId.split("_")[0];
      batch.set(
        doc(db, "users", workspaceUid, "client_monthly", clientDocId),
        {
          monthKey,
          customerKey: nextContribution.customerKey,
          customerId: nextContribution.customerId,
          customerName: nextContribution.customerName,
          normalizedPhone: nextContribution.normalizedPhone,
          confirmedRevenue: increment(Number(delta.confirmedRevenue || 0)),
          confirmedCount: increment(Number(delta.confirmedCount || 0)),
          quotationRevenue: increment(Number(delta.quotationRevenue || 0)),
          quoteCount: increment(Number(delta.quotationCount || 0)),
          lastQuoteId: quoteId,
          lastActivityAt: nextContribution.lastActivityAt,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    });

    if (nextContribution.hasInstallation && !nextContribution.deleted) {
      batch.set(
        installationRef,
        {
          quoteId,
          jobId: quoteId,
          quoteNo: nextContribution.quoteNo,
          customerName: nextContribution.customerName,
          normalizedPhone: nextContribution.normalizedPhone,
          installationDate: nextContribution.installationDate,
          installationDateKey: nextContribution.installationDateKey,
          monthKey: nextContribution.installationMonthKey,
          status: nextContribution.status,
          workStage: nextContribution.workStage,
          totalAmount: nextContribution.totalAmount,
          confirmedRevenue: nextContribution.confirmedRevenue,
          itemSummary: nextContribution.itemSummary,
          location: nextContribution.location,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else if (previousContribution?.hasInstallation) {
      batch.delete(installationRef);
    }

    batch.set(
      contributionRef,
      {
        ...nextContribution,
        syncedAt: serverTimestamp(),
        source: options.source || "quoteWrite",
      },
      { merge: true }
    );

    await batch.commit();

    invalidateWorkspaceCollectionCache(workspaceUid, "analytics_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "client_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
  },
  [
    applySummaryDelta,
    buildAnalyticsContributionForQuote,
    getMonthlyDeltaFromContribution,
    workspaceUid,
  ]
);

const applyQuoteLocalPatch = useCallback(
  (quoteId, patch, options = {}) => {
    const localEventDate = new Date().toISOString();
    const liveDateFields = ["updatedAt", "lastActivityAt", "lastEditedAt"]
      .filter(field => isLiveFirestoreSentinel(patch?.[field]));
    patch = stripFirestoreSentinelsForState(patch) || {};
    liveDateFields.forEach(field => { patch[field] = localEventDate; });
    const identity = options.identity || getSavedQuoteWorkflowIdentity(
      {
        ...(options.sourceQuote || {}),
        id: quoteId,
        quoteId,
      },
      workspaceUid
    );
    const matchKeys = getSavedQuoteWorkflowMatchKeys(
      {
        ...(options.sourceQuote || {}),
        id: quoteId,
        quoteId,
      },
      identity
    );
    const canonicalQuoteId = identity.sourceQuoteId || identity.quoteId || quoteId;
    const quoteIndexId = identity.quoteIndexId || canonicalQuoteId;
    const shouldPersistIndex = options.persistIndex !== false;
    invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    customerLedgerQuotesLoadRef.current = {};
    setSavedQuotes((prev) =>
      sortQuotesByLatestActivity(
        safeSavedQuoteArray(prev).map((item) =>
          savedQuoteMatchesWorkflowIdentity(item, identity) ||
          matchKeys.has(String(item.id || "").trim())
            ? {
                ...item,
                ...patch,
                id: item.id || canonicalQuoteId,
                quoteId: item.quoteId || canonicalQuoteId,
                sourceQuoteId: item.sourceQuoteId || canonicalQuoteId,
                quoteIndexId: item.quoteIndexId || quoteIndexId,
                indexId: item.indexId || quoteIndexId,
              }
            : item
        )
      )
    );
    setDeletedQuotes((prev) =>
      safeSavedQuoteArray(prev).map((item) =>
        savedQuoteMatchesWorkflowIdentity(item, identity) ||
        matchKeys.has(String(item.id || "").trim())
          ? {
              ...item,
              ...patch,
              id: item.id || canonicalQuoteId,
              quoteId: item.quoteId || canonicalQuoteId,
              sourceQuoteId: item.sourceQuoteId || canonicalQuoteId,
              quoteIndexId: item.quoteIndexId || quoteIndexId,
              indexId: item.indexId || quoteIndexId,
            }
          : item
      )
    );
    setCustomerLedgerQuotesByCustomer((prev) =>
      Object.fromEntries(
        Object.entries(prev || {}).map(([customerId, quotes]) => [
          customerId,
          (Array.isArray(quotes) ? quotes : []).map((quote) =>
            savedQuoteMatchesWorkflowIdentity(quote, identity) ||
            matchKeys.has(String(quote.id || "").trim())
              ? {
                  ...quote,
                  ...patch,
                }
              : quote
          ),
        ])
      )
    );

    if (
      selectedQuote?.id === quoteId ||
      savedQuoteMatchesWorkflowIdentity(selectedQuote, identity)
    ) {
      setSelectedQuote((prev) => (prev ? { ...prev, ...patch } : prev));
      if (patch.workStage != null) {
        setSelectedQuoteWorkStage(patch.workStage);
      }
      if (patch.status != null) {
        setSelectedQuoteStatus(patch.status);
      }
      if (Object.prototype.hasOwnProperty.call(patch, "installationDate")) {
        setSelectedInstallationDate(patch.installationDate);
      }
    }

    const indexPatch = buildQuoteIndexPayload(
      { ...patch, id: canonicalQuoteId, quoteId: canonicalQuoteId, sourceQuoteId: canonicalQuoteId },
      canonicalQuoteId,
      workspaceUid
    );
    const lightweightPatch = sanitizeCrmFirestoreValue({
      quoteId: canonicalQuoteId,
      id: canonicalQuoteId,
      sourceQuoteId: canonicalQuoteId,
      quoteIndexId,
      indexId: quoteIndexId,
      sourceCollection: "quotations",
      sourcePath:
        identity.sourcePath ||
        (workspaceUid ? `users/${workspaceUid}/quotations/${canonicalQuoteId}` : ""),
      ...(Object.prototype.hasOwnProperty.call(patch, "status")
        ? { status: indexPatch.status }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "workStage")
        ? { workStage: indexPatch.workStage }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "quoteNo")
        ? {
            quoteNo: indexPatch.quoteNo,
            quoteNoString: indexPatch.quoteNoString,
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "customerId") ||
      Object.prototype.hasOwnProperty.call(patch, "customerKey") ||
      Object.prototype.hasOwnProperty.call(patch, "customerName") ||
      Object.prototype.hasOwnProperty.call(patch, "customerPhone") ||
      Object.prototype.hasOwnProperty.call(patch, "customerWhatsapp")
        ? {
            customerId: indexPatch.customerId || "",
            customerKey: indexPatch.customerKey || indexPatch.customerId || "",
            customerName: indexPatch.customerName || "",
            customerNameLower: indexPatch.customerNameLower || "",
            customerPhone: indexPatch.customerPhone || "",
            customerWhatsapp: indexPatch.customerWhatsapp || "",
            normalizedPhone: indexPatch.normalizedPhone || "",
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "crmLeadId") ||
      Object.prototype.hasOwnProperty.call(patch, "leadId") ||
      Object.prototype.hasOwnProperty.call(patch, "crm")
        ? {
            crmLeadId: indexPatch.crmLeadId || "",
            leadId: indexPatch.leadId || indexPatch.crmLeadId || "",
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deleted")
        ? { deleted: patch.deleted === true }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deletedAt")
        ? { deletedAt: patch.deletedAt || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deleteAfter")
        ? { deleteAfter: patch.deleteAfter || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "confirmedAt")
        ? { confirmedAt: patch.confirmedAt || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "subtotal")
        ? { subtotal: Number(patch.subtotal || 0) }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "grandTotal")
        ? {
            grandTotal: Number(patch.grandTotal || 0),
            totalAmount: Number(patch.grandTotal || 0),
            total: Number(patch.grandTotal || 0),
            finalTotal: Number(patch.grandTotal || 0),
            finalAmount: Number(patch.grandTotal || 0),
          }
        : {}),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    });

    if (workspaceUid && quoteIndexId && shouldPersistIndex) {
      const lightweightIndexPatch = sanitizeForFirestore(lightweightPatch);
      setDoc(doc(db, "users", workspaceUid, "quote_index", quoteIndexId), lightweightIndexPatch, {
        merge: true,
      }).then(() => {
        invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
        const currentQuote =
          selectedQuote?.id === canonicalQuoteId
            ? selectedQuote
            : savedQuotesRef.current.find((quote) =>
                savedQuoteMatchesWorkflowIdentity(quote, identity)
              ) || {};
        const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
          {
            ...currentQuote,
            ...patch,
            ...lightweightIndexPatch,
            id: canonicalQuoteId,
            quoteId: canonicalQuoteId,
            latestQuoteId: canonicalQuoteId,
            latestQuoteNo: lightweightIndexPatch.quoteNo || currentQuote.quoteNo || "",
            sourceType: "quote_customer",
          },
          {
            sourceType: "quote_customer",
            workspaceUid,
          }
        );
        if (customerSearchIndexWrite?.personId) {
          const customerSearchPatchPayload = sanitizeForFirestore(
            customerSearchIndexWrite.payload
          );
          setDoc(
            doc(db, "users", workspaceUid, "customer_search_index", customerSearchIndexWrite.personId),
            customerSearchPatchPayload,
            { merge: true }
          )
            .then(() => {
              invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
              quotationCustomerSearchCacheRef.current.clear();
            })
            .catch((error) => {
              if (isDevRuntime) {
                devError("QUOTE CUSTOMER SEARCH INDEX PATCH FAILED", {
                  quoteId,
                  personId: customerSearchIndexWrite.personId,
                  code: error?.code,
                  message: error?.message,
                });
              }
            });
        }
        syncQuoteAnalyticsIndexes(
          canonicalQuoteId,
          {
            ...currentQuote,
            ...patch,
            id: canonicalQuoteId,
          },
          { source: "quoteLocalPatch" }
        ).catch((error) => {
          if (isDevRuntime) {
            devError("QUOTE ANALYTICS PATCH SYNC FAILED", {
              quoteId: canonicalQuoteId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      }).catch((error) => {
        if (isDevRuntime) {
          devError("QUOTE INDEX PATCH FAILED", {
            quoteId: canonicalQuoteId,
            quoteIndexId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    }
  },
  [selectedQuote, syncQuoteAnalyticsIndexes, user?.displayName, user?.email, user?.uid, workspaceUid]
);

const autoHealQuoteIndexIdentityFromLoadedQuote = useCallback(
  ({ workspaceUid: targetWorkspaceUid, quoteIndexId, loadedQuote, resolvedSourcePath = "" } = {}) => {
    const cleanWorkspaceUid = String(targetWorkspaceUid || workspaceUid || "").trim();
    const cleanQuoteIndexId = String(quoteIndexId || "").trim();
    if (!cleanWorkspaceUid || !cleanQuoteIndexId || !loadedQuote) return;

    const sourceQuoteId = String(
      loadedQuote.sourceQuoteId ||
        loadedQuote.fullQuoteId ||
        loadedQuote.quoteId ||
        loadedQuote.id ||
        cleanQuoteIndexId
    ).trim();
    const sourcePath =
      resolvedSourcePath ||
      loadedQuote.sourcePath ||
      (sourceQuoteId ? `users/${cleanWorkspaceUid}/quotations/${sourceQuoteId}` : "");
    const canonicalIndex = buildQuoteIndexPayload(
      {
        ...loadedQuote,
        id: sourceQuoteId || loadedQuote.id || cleanQuoteIndexId,
        quoteId: sourceQuoteId || loadedQuote.quoteId || loadedQuote.id || cleanQuoteIndexId,
        sourceQuoteId: sourceQuoteId || loadedQuote.sourceQuoteId || loadedQuote.id || cleanQuoteIndexId,
        quoteIndexId: cleanQuoteIndexId,
        indexId: cleanQuoteIndexId,
        sourcePath,
      },
      sourceQuoteId || cleanQuoteIndexId,
      cleanWorkspaceUid
    );
    const currentIndex = loadedQuote.indexRecordRaw || loadedQuote.quoteIndex || {};
    if (Object.keys(currentIndex || {}).length === 0) return;
    const fieldsToHeal = [
      "quoteNo",
      "quoteNoString",
      "customerId",
      "customerKey",
      "customerName",
      "customerNameLower",
      "customerPhone",
      "customerWhatsapp",
      "normalizedPhone",
      "contactPersonName",
      "contactPersonPhone",
      "companyProject",
      "customerCompanyName",
      "companyName",
      "projectName",
      "projectNameLower",
      "siteId",
      "customerSiteId",
      "siteName",
      "siteNameLower",
      "siteAddress",
      "siteContactName",
      "siteContactPhone",
      "siteNotes",
      "quoteDisplayTitle",
      "quoteDisplayTitleLower",
      "searchText",
      "searchableText",
      "searchKeywords",
      "searchTokens",
    ];
    const normalizeComparableValue = (value) => {
      if (value == null) return "";
      if (Array.isArray(value)) return JSON.stringify(value.map((item) => String(item ?? "")).sort());
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    };
    const patch = fieldsToHeal.reduce((nextPatch, field) => {
      if (
        normalizeComparableValue(currentIndex?.[field]) !==
        normalizeComparableValue(canonicalIndex?.[field])
      ) {
        nextPatch[field] = canonicalIndex?.[field] ?? "";
      }
      return nextPatch;
    }, {});

    if (Object.keys(patch).length === 0) return;

    const localPatch = {
      ...patch,
      updatedAt: new Date(),
      updatedBy: user?.uid || "",
    };
    const writePatch = sanitizeForFirestore({
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: user?.uid || "",
    });

    if (isDevRuntime) {
      devLog("QUOTE_INDEX_IDENTITY_AUTO_HEAL_START", {
        quoteNo: canonicalIndex.quoteNo || "",
        quoteIndexId: cleanQuoteIndexId,
        sourceQuoteId,
        sourcePath,
        before: {
          customerName: currentIndex.customerName || "",
          contactPersonName: currentIndex.contactPersonName || "",
          siteName: currentIndex.siteName || "",
          projectName: currentIndex.projectName || "",
          companyProject: currentIndex.companyProject || "",
          quoteDisplayTitle: currentIndex.quoteDisplayTitle || "",
        },
        after: {
          customerName: canonicalIndex.customerName || "",
          contactPersonName: canonicalIndex.contactPersonName || "",
          siteName: canonicalIndex.siteName || "",
          projectName: canonicalIndex.projectName || "",
          companyProject: canonicalIndex.companyProject || "",
          quoteDisplayTitle: canonicalIndex.quoteDisplayTitle || "",
        },
        changedFields: Object.keys(patch),
      });
    }

    setDoc(doc(db, "users", cleanWorkspaceUid, "quote_index", cleanQuoteIndexId), writePatch, {
      merge: true,
    })
      .then(() => {
        invalidateWorkspaceCollectionCache(cleanWorkspaceUid, "quote_index");
        applyQuoteLocalPatch(sourceQuoteId || cleanQuoteIndexId, localPatch, {
          persistIndex: false,
          sourceQuote: {
            ...loadedQuote,
            id: sourceQuoteId || loadedQuote.id || cleanQuoteIndexId,
            quoteId: sourceQuoteId || loadedQuote.quoteId || cleanQuoteIndexId,
            sourceQuoteId: sourceQuoteId || cleanQuoteIndexId,
            quoteIndexId: cleanQuoteIndexId,
            indexId: cleanQuoteIndexId,
            sourcePath,
          },
        });
        if (isDevRuntime) {
          devLog("QUOTE_INDEX_IDENTITY_AUTO_HEAL_SUCCESS", {
            quoteNo: canonicalIndex.quoteNo || "",
            quoteIndexId: cleanQuoteIndexId,
            sourceQuoteId,
            changedFields: Object.keys(patch),
          });
        }
      })
      .catch((error) => {
        if (isDevRuntime) {
          devError("QUOTE_INDEX_IDENTITY_AUTO_HEAL_FAILED", {
            quoteNo: canonicalIndex.quoteNo || "",
            quoteIndexId: cleanQuoteIndexId,
            sourceQuoteId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
  },
  [applyQuoteLocalPatch, user?.uid, workspaceUid]
);

const upsertQuoteLocalRecord = useCallback(
  async (
    quoteRecord,
    { persistIndex = true, traceId = "", syncDerived = true } = {}
  ) => {
    const quoteId = String(quoteRecord?.id || quoteRecord?.quoteNo || "").trim();
    if (!quoteId) return;

    invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    savedQuotesRecentLoadRef.current = {
      workspaceUid: "",
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    devLog("SAVED_QUOTES_RECENT_CACHE_RESET", {
      quoteId,
      workspaceUid,
      localPatchRevisionBefore: savedQuotesLocalPatchRevisionRef.current,
    });
    customerLedgerQuotesLoadRef.current = {};
    const localActivityAt = new Date();
    const safeQuoteRecord = stripFirestoreSentinelsForState(quoteRecord) || {};
    const normalizedQuote = {
      ...safeQuoteRecord,
      id: quoteId,
      updatedAt: normalizeIndexDateForWrite(safeQuoteRecord.updatedAt, localActivityAt),
      lastActivityAt: normalizeIndexDateForWrite(safeQuoteRecord.lastActivityAt, [safeQuoteRecord.updatedAt, localActivityAt]),
      lastEditedAt: normalizeIndexDateForWrite(safeQuoteRecord.lastEditedAt, [safeQuoteRecord.updatedAt, localActivityAt]),
      lastEditedBy: quoteRecord.lastEditedBy || user?.uid || "",
      lastEditedByName: quoteRecord.lastEditedByName || user?.displayName || user?.email || "",
    };
    const localIdentity = getSavedQuoteWorkflowIdentity(normalizedQuote, workspaceUid);
    const localMatchKeys = getSavedQuoteWorkflowMatchKeys(normalizedQuote, localIdentity);
    const canonicalQuoteId = localIdentity.sourceQuoteId || localIdentity.quoteId || quoteId;
    const quoteIndexDocId = localIdentity.quoteIndexId || normalizedQuote.quoteIndexId || quoteId;
    const quoteIndexPayload = buildQuoteIndexSavePayload(
      {
        ...normalizedQuote,
        sourceQuoteId: canonicalQuoteId,
        quoteIndexId: quoteIndexDocId,
        indexId: quoteIndexDocId,
      },
      canonicalQuoteId,
      workspaceUid
    );
    const indexGrandTotal = Number(quoteIndexPayload.grandTotal || 0);
    const liveGrandTotal = Number(normalizedQuote.grandTotal || 0);

    if (isDevRuntime && Math.abs(indexGrandTotal - liveGrandTotal) > 0.05) {
      devError("QUOTE_INDEX_TOTAL_MISMATCH", {
        quoteId,
        quoteNo: normalizedQuote.quoteNo || "",
        liveGrandTotal,
        indexGrandTotal,
        type: normalizedQuote.quoteType || normalizedQuote.quoteTypeResolved || "",
      });
    }

    const localUpdatedAt = normalizedQuote.updatedAt || localActivityAt;
    const savedQuoteCard = {
      ...normalizedQuote,
      ...stripFirestoreSentinelsForState(quoteIndexPayload),
      id: quoteIndexDocId || canonicalQuoteId,
      quoteId: canonicalQuoteId,
      sourceQuoteId: canonicalQuoteId,
      fullQuoteId: canonicalQuoteId,
      quoteIndexId: quoteIndexDocId,
      indexId: quoteIndexDocId,
      sourcePath:
        localIdentity.sourcePath ||
        normalizedQuote.sourcePath ||
        (workspaceUid ? `users/${workspaceUid}/quotations/${canonicalQuoteId}` : ""),
      grandTotal: indexGrandTotal,
      totalAmount: indexGrandTotal,
      total: indexGrandTotal,
      finalTotal: indexGrandTotal,
      finalAmount: indexGrandTotal,
      updatedAt: localUpdatedAt,
      lastActivityAt: normalizedQuote.lastActivityAt || localUpdatedAt,
      lastEditedAt: normalizedQuote.lastEditedAt || localUpdatedAt,
      lastEditedBy: normalizedQuote.lastEditedBy || user?.uid || "",
      lastEditedByName: normalizedQuote.lastEditedByName || user?.displayName || user?.email || "",
    };

    recordSaveTracePatch(traceId, {
      quoteNo: sanitizeSaveTraceText(savedQuoteCard.quoteNo || "", 80),
      quoteId: canonicalQuoteId,
      quoteIndexId: quoteIndexDocId,
      sourcePath: savedQuoteCard.sourcePath || "",
      customerName: sanitizeSaveTraceText(savedQuoteCard.customerName || "", 160),
      siteName: sanitizeSaveTraceText(savedQuoteCard.siteName || "", 160),
      savedCard: {
        quoteId: canonicalQuoteId,
        quoteIndexId: quoteIndexDocId,
        quoteNo: sanitizeSaveTraceText(savedQuoteCard.quoteNo || "", 80),
        customerName: sanitizeSaveTraceText(savedQuoteCard.customerName || "", 160),
        siteName: sanitizeSaveTraceText(savedQuoteCard.siteName || "", 160),
        deleted: savedQuoteCard.deleted === true ? true : savedQuoteCard.deleted === false ? false : null,
        hasCreatedAt: Boolean(savedQuoteCard.createdAt),
        createdAtType: getSaveTraceTimestampType(savedQuoteCard.createdAt),
        hasUpdatedAt: Boolean(savedQuoteCard.updatedAt),
        updatedAtType: getSaveTraceTimestampType(savedQuoteCard.updatedAt),
        quoteType: sanitizeSaveTraceText(savedQuoteCard.quoteType || "", 80),
        quotationType: sanitizeSaveTraceText(savedQuoteCard.quotationType || "", 80),
        hasSearchableText: Boolean(savedQuoteCard.searchableText),
        sourcePath: savedQuoteCard.sourcePath || "",
      },
      savedQuotesPatch: {
        recentCacheReset: true,
      },
    });

    savedQuotesLocalPatchRevisionRef.current += 1;
    const localPatchRevision = savedQuotesLocalPatchRevisionRef.current;
    setSavedQuotes((prev) => {
      const previousQuotes = safeSavedQuoteArray(prev);
      const withoutDuplicate = previousQuotes.filter((item) => {
        const itemId = String(item?.id || "").trim();
        const itemQuoteId = String(item?.quoteId || item?.sourceQuoteId || item?.fullQuoteId || "").trim();
        const itemIndexId = String(item?.quoteIndexId || item?.indexId || "").trim();
        return !(
          savedQuoteMatchesWorkflowIdentity(item, localIdentity) ||
          localMatchKeys.has(itemId) ||
          itemQuoteId === canonicalQuoteId ||
          itemIndexId === quoteIndexDocId
        );
      });
      const nextQuotes = sortQuotesByLatestActivity([savedQuoteCard, ...withoutDuplicate]);
      const insertedAtTop = nextQuotes[0]?.quoteIndexId === quoteIndexDocId;
      devLog(
        "SAVED_QUOTES_PREPEND_PATCH",
        JSON.stringify(
          {
            quoteId: canonicalQuoteId,
            quoteIndexId: quoteIndexDocId,
            quoteNo: savedQuoteCard.quoteNo || "",
            customerName: savedQuoteCard.customerName || "",
            siteName: savedQuoteCard.siteName || "",
            updatedAt: savedQuoteCard.updatedAt,
            lastActivityAt: savedQuoteCard.lastActivityAt,
            lastEditedAt: savedQuoteCard.lastEditedAt,
            previousCount: previousQuotes.length,
            nextCount: nextQuotes.length,
            insertedAtTop,
            firstQuoteNoAfterPatch: nextQuotes[0]?.quoteNo || "",
            localPatchRevision,
          },
          null,
          2
        )
      );
      recordSaveTracePatch(traceId, {
        savedQuotesPatch: {
          prependPatchRan: true,
          previousCount: previousQuotes.length,
          nextCount: nextQuotes.length,
          insertedAtTop,
          recentCacheReset: true,
          patchRevision: localPatchRevision,
        },
      });
      return nextQuotes;
    });
    clearQuoteSaveInProgressSession({
      status: "verified_saved",
      reason: "verified_saved",
      backendVerified: true,
      traceId,
      quoteNo: savedQuoteCard.quoteNo || "",
      quoteId: canonicalQuoteId,
      quoteIndexId: quoteIndexDocId,
      sourcePath: savedQuoteCard.sourcePath || "",
      itemCount:
        Number(savedQuoteCard.itemCount || savedQuoteCard.contentItemCount || 0) ||
        extractGeneralQuoteItems(savedQuoteCard).length ||
        0,
    });

    if (workspaceUid && syncDerived) {
      try {
        if (persistIndex) {
          const quoteIndexWritePayload = sanitizeForFirestore({
            ...quoteIndexPayload,
            quoteIndexId: quoteIndexDocId,
            indexId: quoteIndexDocId,
            createdBy: quoteIndexPayload.createdBy || normalizedQuote.createdBy || user?.uid || "",
            updatedBy: user?.uid || quoteIndexPayload.updatedBy || normalizedQuote.updatedBy || "",
          });
          await setDoc(
            doc(db, "users", workspaceUid, "quote_index", quoteIndexDocId),
            quoteIndexWritePayload,
            { merge: true }
          );
          invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
        }
        syncQuoteAnalyticsIndexes(canonicalQuoteId, normalizedQuote, {
          source: "quoteLocalUpsert",
        }).catch((error) => {
          if (isDevRuntime) {
            devError("QUOTE ANALYTICS UPSERT SYNC FAILED", {
              quoteId: canonicalQuoteId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      } catch (error) {
        if (isDevRuntime) {
          devError("QUOTE INDEX UPSERT FAILED", {
            quoteId,
            code: error?.code,
            message: error?.message,
          });
        }
        throw error;
      }

      const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
        {
          ...normalizedQuote,
          ...quoteIndexPayload,
          id: canonicalQuoteId,
          quoteId: canonicalQuoteId,
          latestQuoteId: canonicalQuoteId,
          latestQuoteNo: quoteIndexPayload.quoteNo || "",
          sourceType: "quote_customer",
        },
        {
          sourceType: "quote_customer",
          workspaceUid,
        }
      );
      if (customerSearchIndexWrite?.personId) {
        const customerSearchIndexPayload = sanitizeForFirestore(
          customerSearchIndexWrite.payload
        );
        setDoc(
          doc(db, "users", workspaceUid, "customer_search_index", customerSearchIndexWrite.personId),
          customerSearchIndexPayload,
          { merge: true }
        )
          .then(() => {
            invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
            quotationCustomerSearchCacheRef.current.clear();
            recordSaveTracePatch(traceId, {
              optionalWrites: {
                customerSearchIndex: "success",
              },
            });
          })
          .catch((error) => {
            recordSaveTracePatch(traceId, {
              optionalWrites: {
                customerSearchIndex: "failed",
              },
            });
            appendSaveTraceError(traceId, "customer_search_index", error, {
              path: `users/${workspaceUid}/customer_search_index/${customerSearchIndexWrite.personId}`,
            });
            devWarn("QUOTE CUSTOMER SEARCH INDEX UPSERT SKIPPED", {
              quoteId: canonicalQuoteId,
              personId: customerSearchIndexWrite.personId,
              path: `users/${workspaceUid}/customer_search_index/${customerSearchIndexWrite.personId}`,
              errorCode: error?.code || "",
              errorMessage: error?.message || String(error),
            });
          });
      } else {
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            customerSearchIndex: "skipped",
          },
        });
      }
    }
  },
  [
    appendSaveTraceError,
    getSaveTraceTimestampType,
    recordSaveTracePatch,
    sanitizeSaveTraceText,
    syncQuoteAnalyticsIndexes,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const writeQuotationAndQuoteIndex = useCallback(
  async ({
    quoteRef,
    quoteId,
    quoteIndexId = "",
    quotePayload,
    quoteIndexSource,
    create = false,
    traceContext = {},
  }) => {
    if (!workspaceUid || !quoteId || !quoteRef) {
      throw new Error("Cannot save quotation index without workspace, quote id, and document ref.");
    }

    const quoteIndexDocId = String(
      quoteIndexId ||
        quoteIndexSource?.quoteIndexId ||
        quoteIndexSource?.indexId ||
        quotePayload?.quoteIndexId ||
        quotePayload?.indexId ||
        quoteId
    ).trim();
    const indexRef = doc(db, "users", workspaceUid, "quote_index", quoteIndexDocId);
    const quoteIndexPayload = buildQuoteIndexSavePayload(
      {
        ...(quoteIndexSource || quotePayload || {}),
        quoteIndexId: quoteIndexDocId,
        indexId: quoteIndexDocId,
        sourceQuoteId: quoteId,
      },
      quoteId,
      workspaceUid
    );
    const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
      {
        ...(quoteIndexSource || quotePayload || {}),
        ...quoteIndexPayload,
        id: quoteId,
        quoteId,
        latestQuoteId: quoteId,
        latestQuoteNo: quoteIndexPayload.quoteNo || "",
        sourceType: "quote_customer",
      },
      {
        sourceType: "quote_customer",
        workspaceUid,
      }
    );
    const liveGrandTotal = Number(
      quoteIndexSource?.grandTotal ??
        quotePayload?.grandTotal ??
        quoteIndexPayload.grandTotal ??
        0
    );
    const indexGrandTotal = Number(quoteIndexPayload.grandTotal || 0);

    if (isDevRuntime && Math.abs(indexGrandTotal - liveGrandTotal) > 0.05) {
      devError("QUOTE_INDEX_TOTAL_MISMATCH", {
        quoteId,
        quoteNo: quoteIndexSource?.quoteNo || quotePayload?.quoteNo || "",
        liveGrandTotal,
        indexGrandTotal,
        type:
          quoteIndexSource?.quoteType ||
          quoteIndexSource?.quoteTypeResolved ||
          quotePayload?.quoteType ||
          quotePayload?.quoteTypeResolved ||
        "",
      });
    }

    const indexPayloadWithIds = {
      ...quoteIndexPayload,
      quoteIndexId: quoteIndexDocId,
      indexId: quoteIndexDocId,
      createdBy: quoteIndexPayload.createdBy || quotePayload?.createdBy || user?.uid || "",
      updatedBy: user?.uid || quotePayload?.updatedBy || quoteIndexPayload.updatedBy || "",
    };
    const saveMode = create ? "create_new" : "update_existing";
    const writeQuoteNo = quotePayload?.quoteNo || quoteIndexSource?.quoteNo || traceContext.quoteNo || "";
    const isCurtainPayload =
      quotePayload?.canonicalContentField === CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD ||
      traceContext.quoteType === CURTAIN_QUOTE_TYPE ||
      resolveQuoteType(quotePayload) === CURTAIN_QUOTE_TYPE ||
      resolveQuoteType(quoteIndexSource) === CURTAIN_QUOTE_TYPE;
    const generalPayloadForStorage = isCurtainPayload
      ? null
      : finalizeGeneralQuotePayloadForSave(quotePayload, {
          source: "writeQuotationAndQuoteIndex.storage_input",
          saveMode,
          quoteId,
          quoteNo: writeQuoteNo,
          quoteTypeHint: GENERAL_QUOTE_TYPE,
        });
    const storagePlan = isCurtainPayload
      ? buildCurtainQuoteV3StorageForSave({
          payload: quotePayload,
          constructionSourceRows: rowsRef.current,
          quoteId,
          workspaceUid,
          saveMode,
          action: traceContext.action || (create ? "save_as_new" : "save"),
          path: quoteRef.path,
          allowDeleteFieldSentinels: false,
          previousRowChunkCount:
            traceContext.previousRowChunkCount ||
            quotePayload?.previousRowChunkCount ||
            quotePayload?.rowChunkCount ||
            0,
        })
      : {
          mainPayload: sanitizeQuotationForFirestore(generalPayloadForStorage) || generalPayloadForStorage || quotePayload,
          rowChunks: [],
          rowCount: extractGeneralQuoteItems(generalPayloadForStorage || quotePayload).length || 0,
          storageMode: generalPayloadForStorage?.rowStorageMode || quotePayload?.rowStorageMode || QUOTE_ROW_STORAGE_INLINE,
          mainSizeBytes: estimateFirestorePayloadSize(generalPayloadForStorage || quotePayload),
          previousRowChunkCount: 0,
        };
    let sanitizedQuotePayload = sanitizeQuoteWritePayload({
      payload: storagePlan.mainPayload,
      quoteNo: writeQuoteNo,
      quoteId,
      saveMode,
      step: "main_quote",
      path: quoteRef.path,
    });
    if (!isCurtainPayload) {
      const finalItems = extractGeneralQuoteItems(sanitizedQuotePayload);
      const originalItems = extractGeneralQuoteItems(generalPayloadForStorage || quotePayload);
      sanitizedQuotePayload = finalizeGeneralQuotePayloadForSave(
        finalItems.length
          ? sanitizedQuotePayload
          : {
              ...(sanitizedQuotePayload || {}),
              items: originalItems,
            },
        {
          source: "writeQuotationAndQuoteIndex.final_write",
          saveMode,
          quoteId,
          quoteNo: writeQuoteNo,
          quoteTypeHint: GENERAL_QUOTE_TYPE,
        }
      );
    }
    const generalItemCountForWrite = !isCurtainPayload
      ? extractGeneralQuoteItems(sanitizedQuotePayload).length
      : 0;
    const indexPayloadWithWriteCounts = isCurtainPayload
      ? {
          ...indexPayloadWithIds,
          rowCount: storagePlan.rowCount,
          contentRowCount: storagePlan.rowCount,
          measurementRowCount: storagePlan.measurementRowCount,
          accessoryCount: storagePlan.accessoryCount,
          rowStorageMode: storagePlan.storageMode,
          storageVersion: QUOTE_V3_STORAGE_VERSION,
          saveRevisionId: storagePlan.saveRevisionId,
        }
      : {
          ...indexPayloadWithIds,
          itemCount: generalItemCountForWrite,
          contentItemCount: generalItemCountForWrite,
          canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
          contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        };
    const sanitizedIndexPayloadWithIds = sanitizeQuoteWritePayload({
      payload: indexPayloadWithWriteCounts,
      quoteNo: writeQuoteNo,
      quoteId,
      saveMode,
      step: "quote_index",
      path: indexRef.path,
    });
    const sanitizedRowChunks = (storagePlan.rowChunks || []).map((chunk) =>
      sanitizeQuoteWritePayload({
        payload: {
          ...chunk,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        quoteNo: writeQuoteNo,
        quoteId,
        saveMode,
        step: `row_chunk_${chunk.chunkId || chunk.id || ""}`,
        path: chunk.path,
      })
    );
    const existingContentCount = Number(
      traceContext.existingContentCount ??
        traceContext.loadedContentCount ??
        quotePayload?.existingContentCount ??
        0
    );
    const canonicalWriteInvariant = assertCanonicalQuotePersistenceInvariant({
      quotePayload: sanitizedQuotePayload,
      quoteIndexPayload: sanitizedIndexPayloadWithIds,
      quoteTypeHint:
        traceContext.quoteType ||
        sanitizedQuotePayload?.normalizedQuoteType ||
        sanitizedQuotePayload?.quoteType ||
        quoteIndexSource?.quoteType ||
        "",
      saveMode,
      quoteId,
      quoteNo: writeQuoteNo,
      quotePath: quoteRef.path,
      quoteIndexPath: indexRef.path,
      existingContentCount,
      allowExplicitEmptyContentSave: traceContext.allowExplicitEmptyContentSave === true,
    });
    if (isDevRuntime) {
      devLog("QUOTE_CANONICAL_WRITE_INVARIANT_PASS", {
        quoteId,
        quoteNo: writeQuoteNo,
        quotePath: quoteRef.path,
        quoteIndexPath: indexRef.path,
        saveMode,
        fullContent: {
          field: canonicalWriteInvariant.fullContent.field,
          count: canonicalWriteInvariant.fullContent.count,
          canonicalField: canonicalWriteInvariant.fullContent.canonicalField,
          recoveredFromCanonicalEmpty:
            canonicalWriteInvariant.fullContent.recoveredFromCanonicalEmpty,
        },
        indexContent: {
          field: canonicalWriteInvariant.indexContent.field,
          count: canonicalWriteInvariant.indexContent.count,
          canonicalField: canonicalWriteInvariant.indexContent.canonicalField,
        },
      });
    }
    const existingCurtainTraceEnabled =
      !create &&
      (
        traceContext.quoteType === CURTAIN_QUOTE_TYPE ||
        quotePayload?.quoteType === CURTAIN_QUOTE_TYPE ||
        quoteIndexSource?.quoteType === CURTAIN_QUOTE_TYPE
      );
    const getExistingCurtainTraceBase = () => {
      const accountRecord = account || currentAccount || {};
      return {
        action: "save_existing",
        userUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid,
        role:
          traceContext.role ||
          accountUser?.role ||
          currentWorkspaceMember?.role ||
          workspaceContext?.role ||
          "",
        accountStatus:
          traceContext.accountStatus ||
          accountRecord.accountStatus ||
          accountRecord.subscriptionStatus ||
          accountRecord.status ||
          "",
        quoteNo: writeQuoteNo,
        quoteId,
        sourceQuoteId: traceContext.sourceQuoteId || sanitizedIndexPayloadWithIds.sourceQuoteId || quoteId,
        quoteIndexId: quoteIndexDocId,
        sourcePath: traceContext.sourcePath || sanitizedIndexPayloadWithIds.sourcePath || quoteRef.path,
        resolvedQuotePath: traceContext.resolvedQuotePath || quoteRef.path,
      };
    };
    const logExistingCurtainWriteTrace = (writePath, operation, payload = {}) => {
      if (!isDevRuntime || !existingCurtainTraceEnabled) return;
      const tracePayload = {
        ...getExistingCurtainTraceBase(),
        editorMode: traceContext.editorMode || QUOTE_EDITOR_MODE_EDITING_EXISTING,
        failedStepCandidate: operation,
        writePath,
        operation,
        payloadKeys: Object.keys(payload || {}),
      };
      logCurtainSaveWithLiningTrace({
        ...tracePayload,
        payload,
      });
      devLog("CURTAIN_QUOTE_SAVE_EXISTING_TRACE", tracePayload);
    };
    const logExistingCurtainWriteFailure = (failedStep, writePath, error, payload = {}) => {
      if (!isDevRuntime || !existingCurtainTraceEnabled) return;
      const failurePayload = {
        ...getExistingCurtainTraceBase(),
        failedStep,
        writePath,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        quoteNo: writeQuoteNo,
        quoteId,
        quoteIndexId: quoteIndexDocId,
        sourcePath: traceContext.sourcePath || sanitizedIndexPayloadWithIds.sourcePath || quoteRef.path,
        payloadKeys: Object.keys(payload || {}),
      };
      logCurtainSaveWithLiningFailure({
        ...failurePayload,
        error,
        payload,
      });
      devError("CURTAIN_QUOTE_SAVE_EXISTING_FAILED", failurePayload);
    };

    devLog("QUOTE_SAVE_BEGIN", {
      buildLabel: FABRIQUOTE_BUILD_INFO.label,
      quoteType: sanitizedQuotePayload?.quoteType || quoteIndexSource?.quoteType || "",
      quoteNo: sanitizedQuotePayload?.quoteNo || quoteIndexSource?.quoteNo || "",
      saveMode,
      userUid: auth.currentUser?.uid || user?.uid || "",
      workspaceUid,
      quoteId,
      quoteIndexId: quoteIndexDocId,
      customerName: sanitizedQuotePayload?.customerName || quoteIndexSource?.customerName || "",
      siteName: sanitizedQuotePayload?.siteName || quoteIndexSource?.siteName || "",
      itemsCount: sanitizedQuotePayload?.generalQuote?.items?.length || sanitizedQuotePayload?.items?.length || 0,
      rowsCount: storagePlan.rowCount || sanitizedQuotePayload?.rows?.length || 0,
      rowStorageMode: storagePlan.storageMode || "",
      rowChunkCount: sanitizedRowChunks.length,
      payloadSizeBytes: storagePlan.mainSizeBytes || 0,
      grandTotal: liveGrandTotal,
    });
    devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
      step: "main_quote",
      path: quoteRef.path,
      operation: create ? "batch.set" : "batch.set.merge",
      payloadKeys: Object.keys(sanitizedQuotePayload || {}),
    });
    devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
      step: "quote_index",
      path: indexRef.path,
      operation: create ? "batch.set" : "setDoc.merge",
      payloadKeys: Object.keys(sanitizedIndexPayloadWithIds || {}),
    });
    if (sanitizedRowChunks.length) {
      devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
        step: "row_chunks",
        operation: "batch.set",
        chunkCount: sanitizedRowChunks.length,
        paths: sanitizedRowChunks.map((chunk) => chunk.path),
      });
    }

    const canonicalSaveMode =
      !create
        ? "save_existing"
        : traceContext.action === "save_as_new"
        ? "save_as_new"
        : "create_new";
    let customerSearchIndexForCanonical = null;
    if (customerSearchIndexWrite?.personId) {
      const searchIndexRef = doc(
        db,
        "users",
        workspaceUid,
        "customer_search_index",
        customerSearchIndexWrite.personId
      );
      devLog("QUOTE_SAVE_WRITE_ATTEMPT", {
        step: "customer_search_index",
        path: searchIndexRef.path,
        operation: "callable.transaction.set",
        payloadKeys: Object.keys(customerSearchIndexWrite.payload || {}),
      });
      try {
        const sanitizedCustomerSearchIndexPayload = sanitizeQuoteWritePayload({
          payload: customerSearchIndexWrite.payload,
          quoteNo: writeQuoteNo,
          quoteId,
          saveMode: canonicalSaveMode,
          step: "customer_search_index",
          path: searchIndexRef.path,
        });
        customerSearchIndexForCanonical = {
          personId: customerSearchIndexWrite.personId,
          payload: sanitizedCustomerSearchIndexPayload,
        };
      } catch (error) {
        devWarn("QUOTE_SAVE_OPTIONAL_WRITE_SKIPPED", {
          step: "customer_search_index",
          path: searchIndexRef.path,
          errorCode: error?.code || "",
          errorMessage: error?.message || String(error),
          invalidValues: error?.__fqInvalidFirestoreValues || [],
        });
      }
    }
    try {
      logExistingCurtainWriteTrace(quoteRef.path, "callable.saveQuotationCanonical", sanitizedQuotePayload);
      logExistingCurtainWriteTrace(indexRef.path, "callable.saveQuotationCanonical", sanitizedIndexPayloadWithIds);
      const saveOperation = isCurtainPayload
        ? saveQuotationCanonicalV3Callable({
            workspaceUid,
            mode: canonicalSaveMode,
            financialSourceQuoteId: selectedQuote?.quoteId || selectedQuote?.id || editingQuoteId || "",
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteId,
            quoteIndexId: quoteIndexDocId,
            quoteNo: writeQuoteNo,
            quoteHeader: sanitizeCrmFirestoreValue({
              ...sanitizedQuotePayload,
              createdAt: undefined,
              updatedAt: undefined,
              lastSavedAt: undefined,
              contentUpdatedAt: undefined,
            }),
            quoteIndexSource: sanitizeCrmFirestoreValue({
              ...sanitizedIndexPayloadWithIds,
              rows: undefined,
              measurementRows: undefined,
              accessories: undefined,
              calculationSnapshot: undefined,
              createdAt: undefined,
              updatedAt: undefined,
              indexedAt: undefined,
            }),
            rows: sanitizeCrmFirestoreValue(storagePlan.rows || []),
            accessories: sanitizeCrmFirestoreValue(storagePlan.accessories || []),
            deletedRowIds: sanitizeCrmFirestoreValue(traceContext.deletedRowIds || []),
            deletedAccessoryIds: sanitizeCrmFirestoreValue(traceContext.deletedAccessoryIds || []),
            rowStorageMode: storagePlan.storageMode || "",
            saveRevisionId: storagePlan.saveRevisionId,
            allowExplicitEmptyContentSave:
              traceContext.allowExplicitEmptyContentSave === true,
            customerSearchIndexWrite: customerSearchIndexForCanonical
              ? sanitizeCrmFirestoreValue(customerSearchIndexForCanonical)
              : null,
          })
        : saveQuotationCanonicalCallable({
            workspaceUid,
            mode: canonicalSaveMode,
            quoteType: GENERAL_QUOTE_TYPE,
            quoteId,
            quoteIndexId: quoteIndexDocId,
            quoteNo: writeQuoteNo,
            quotePayload: sanitizeCrmFirestoreValue({
              ...sanitizedQuotePayload,
              createdAt: undefined,
              updatedAt: undefined,
              lastSavedAt: undefined,
              contentUpdatedAt: undefined,
            }),
            quoteIndexSource: sanitizeCrmFirestoreValue({
              ...sanitizedIndexPayloadWithIds,
              createdAt: undefined,
              updatedAt: undefined,
              indexedAt: undefined,
            }),
            rowChunks: sanitizeCrmFirestoreValue(sanitizedRowChunks || []),
            rowStorageMode: storagePlan.storageMode || "",
            allowExplicitEmptyContentSave:
              traceContext.allowExplicitEmptyContentSave === true,
            customerSearchIndexWrite: customerSearchIndexForCanonical
              ? sanitizeCrmFirestoreValue(customerSearchIndexForCanonical)
              : null,
          });
      const result = await withQuoteSaveTimeout(saveOperation, {
        step: "saveQuotationCanonical.callable",
      });
      const canonicalResult = result?.data || {};
      assertCanonicalSaveResponse({
        result: canonicalResult,
        quoteType: isCurtainPayload ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE,
        quoteId,
        quoteNo: writeQuoteNo,
        sourcePath: quoteRef.path,
        indexPath: indexRef.path,
        allowExplicitEmptyContentSave:
          traceContext.allowExplicitEmptyContentSave === true,
      });
      devLog("QUOTE_SAVE_WRITE_SUCCESS", {
        step: "main_quote",
        path: canonicalResult.sourcePath || quoteRef.path,
        operation: "saveQuotationCanonical",
      });
      devLog("QUOTE_SAVE_WRITE_SUCCESS", {
        step: "quote_index",
        path: canonicalResult.indexPath || indexRef.path,
        operation: "saveQuotationCanonical",
      });
      if (sanitizedRowChunks.length) {
        devLog("QUOTE_SAVE_WRITE_SUCCESS", {
          step: "row_chunks",
          chunkCount: canonicalResult.rowChunkCount ?? sanitizedRowChunks.length,
          paths: sanitizedRowChunks.map((chunk) => chunk.path),
        });
      }
      if (customerSearchIndexForCanonical?.personId) {
        devLog("QUOTE_SAVE_WRITE_SUCCESS", {
          step: "customer_search_index",
          operation: "saveQuotationCanonical",
          status: canonicalResult.customerSearchIndexStatus || "queued",
        });
        invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
        quotationCustomerSearchCacheRef.current.clear();
      }
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      if (isCurtainPayload) {
        invalidateFirestoreReadCacheByPrefix(
          `users/${workspaceUid}/quotations/${quoteId}/row_chunks`
        );
      }
      return {
        ...sanitizedIndexPayloadWithIds,
        ...(canonicalResult.quoteIndex || {}),
        quoteId,
        sourceQuoteId: quoteId,
        quoteIndexId: quoteIndexDocId,
        indexId: quoteIndexDocId,
        sourcePath: canonicalResult.sourcePath || sanitizedIndexPayloadWithIds.sourcePath,
      };
    } catch (error) {
      error.__fqRequiredSaveStep = "saveQuotationCanonical";
      error.__fqMainQuoteSaved = false;
      const failureTrace = emitQuoteSaveFailureTrace(
        buildQuoteSaveFailureTrace({
          quoteId,
          quoteNo: writeQuoteNo,
          quoteType: isCurtainPayload ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE,
          workspaceUid,
          userUid: auth.currentUser?.uid || user?.uid || "",
          referralPartner:
            sanitizedQuotePayload?.referralPartner ||
            sanitizedIndexPayloadWithIds?.referralPartner ||
            null,
          step: "saveQuotationCanonical",
          fullQuoteWriteStarted: true,
          quoteIndexWriteStarted: true,
          fullQuoteWriteSucceeded: false,
          quoteIndexWriteSucceeded: false,
          crmSyncStarted: false,
          crmSyncSucceeded: false,
          error,
        })
      );
      recordSaveTracePatch(traceContext.traceId || "", {
        failureTrace,
      });
      logExistingCurtainWriteFailure(
        "saveQuotationCanonical",
        `${quoteRef.path} + ${indexRef.path}`,
        error,
        sanitizedQuotePayload
      );
      devError("QUOTE_SAVE_WRITE_FAILED", {
        step: "saveQuotationCanonical",
        path: `${quoteRef.path} + ${indexRef.path}`,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        payloadKeys: {
          mainQuote: Object.keys(sanitizedQuotePayload || {}),
          quoteIndex: Object.keys(sanitizedIndexPayloadWithIds || {}),
          rowChunks: sanitizedRowChunks.map((chunk) => Object.keys(chunk || {})),
        },
        invalidValues: error?.__fqInvalidFirestoreValues || [],
      });
      throw error;
    }
  },
  [
    account,
    accountUser?.role,
    currentAccount,
    currentWorkspaceMember?.role,
    recordSaveTracePatch,
    saveQuotationCanonicalCallable,
    saveQuotationCanonicalV3Callable,
    selectedQuote,
    editingQuoteId,
    user?.uid,
    workspaceContext?.role,
    workspaceUid,
  ]
);

const applyCrmReminderLocalPatch = useCallback((reminderId, patch) => {
  invalidateWorkspaceCollectionCache(workspaceUid, "crm_reminders");
  setCrmReminderRecords((prev) => {
    const nextPatch = patch
      ? {
          ...patch,
          id: reminderId,
        }
      : null;
    const existingIndex = prev.findIndex((item) => item.id === reminderId);

    if (!nextPatch) {
      return existingIndex >= 0
        ? prev.filter((item) => item.id !== reminderId)
        : prev;
    }

    if (existingIndex < 0) {
      return [nextPatch, ...prev];
    }

    return prev.map((item) =>
      item.id === reminderId
        ? {
            ...item,
            ...nextPatch,
          }
        : item
    );
  });
}, [workspaceUid]);

const syncReminderDocsForDeal = useCallback(
  async (
    dealLike,
    {
      reminderConfigs: reminderOverrides,
      followUps: followUpOverrides,
      nextActionDate,
      installationDate,
    } = {}
  ) => {
    if (!workspaceUid || !dealLike?.id) return;

    const nextReminderConfigs = normalizeCrmReminderConfigs(
      reminderOverrides ?? dealLike.reminderConfigs ?? dealLike.crm?.reminderConfigs,
      {
        nextActionDate:
          nextActionDate ??
          dealLike.nextActionDate ??
          dealLike.crm?.nextActionDate ??
          null,
        installationDate:
          installationDate ??
          dealLike.installationDate ??
          dealLike.crm?.installationDate ??
          null,
      }
    );

    const dealTitle = String(dealLike.crmTitle || dealLike.title || "CRM Deal");
    const clientName = String(
      dealLike.customerLabel ||
        dealLike.crmContact?.name ||
        dealLike.contact?.name ||
        ""
    );
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const quoteNo = String(
      dealLike.crmRecordType === "quote"
        ? dealLike.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || ""
    );
    const pipelineType =
      dealLike.pipelineType ||
      dealLike.quoteTypeResolved ||
      dealLike.quoteType ||
      CURTAIN_QUOTE_TYPE;
    const linkedCustomer = customerRecords.find(
      (customer) =>
        customer.id &&
        String(customer.id) ===
          String(dealLike.customerId || dealLike.crm?.customerId || "")
    );
    const primarySiteLocation =
      getPrimarySiteLocation(dealLike) || getPrimarySiteLocation(linkedCustomer);
    const reminderLocationFields = primarySiteLocation
      ? {
          siteLocation: primarySiteLocation,
          locationLabel: primarySiteLocation.label || "",
          locationAddress: primarySiteLocation.addressText || "",
          locationUrl: buildSiteLocationOpenUrl(primarySiteLocation),
        }
      : {
          siteLocation: null,
          locationLabel: "",
          locationAddress: "",
          locationUrl: "",
        };
    const followUps = normalizeCrmFollowUps(
      followUpOverrides ?? dealLike.followUps ?? dealLike.crm?.followUps,
      {
        nextAction: dealLike.nextAction ?? dealLike.crm?.nextAction ?? "",
        nextActionDate:
          nextActionDate ??
          dealLike.nextActionDate ??
          dealLike.crm?.nextActionDate ??
          null,
        reminderConfig:
          nextReminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] ||
          dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          null,
      }
    );
    const reminderTypes = [
      ...(followUps.length === 0 ? [CRM_REMINDER_TYPE_TASK_DUE] : []),
      CRM_REMINDER_TYPE_INSTALLATION,
    ];
    const crmLeadIdForDueQueue = String(
      dealLike.leadId ||
        dealLike.crmLeadId ||
        dealLike.crm?.leadId ||
        dealLike.id ||
        ""
    ).trim();
    const quoteIdForDueQueue =
      String(
        dealLike.quoteId ||
          primaryLinkedQuote?.quoteId ||
          (dealLike.crmRecordType === "quote" ? dealLike.id : "") ||
          ""
      ).trim() || null;
    const customerIdForDueQueue =
      String(
        dealLike.customerId ||
          dealLike.crm?.customerId ||
          linkedCustomer?.id ||
          ""
      ).trim() || null;
    const assignedToForDueQueue =
      String(
        dealLike.assignedToUserId ||
          dealLike.assignedToUid ||
          dealLike.assignedUserId ||
          dealLike.crm?.assignedToUserId ||
          ""
      ).trim() || null;
    const buildDueReminderMessage = (primaryText = "") =>
      [primaryText, clientName, quoteNo ? `#${quoteNo}` : ""]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" | ");
    const upsertDueReminderDoc = async ({
      reminderType,
      sourceId = "",
      sourceReminderId = "",
      title = "",
      message = "",
      nextNotifyAt = null,
      sourceStatus = CRM_REMINDER_STATUS_DISABLED,
      reminderEnabled = false,
      repeatKey = "none",
    }) => {
      if (!crmLeadIdForDueQueue) return;

      const dueReminderId = buildDueReminderDocId(
        crmLeadIdForDueQueue,
        reminderType,
        sourceId
      );
      if (!dueReminderId) return;

      const nextNotifyMillis = safeTimeMillis(nextNotifyAt);
      const dueStatus = buildDueReminderStatus(
        reminderEnabled === true,
        sourceStatus,
        nextNotifyMillis > 0
      );
      const dueReminderRef = doc(
        db,
        "users",
        workspaceUid,
        "due_reminders",
        dueReminderId
      );

      await setDoc(
        dueReminderRef,
        {
          id: dueReminderId,
          workspaceUid,
          crmLeadId: crmLeadIdForDueQueue,
          quoteId: quoteIdForDueQueue,
          customerId: customerIdForDueQueue,
          crmReminderId: String(sourceReminderId || ""),
          title: String(title || "Reminder").trim() || "Reminder",
          message:
            String(message || "").trim() ||
            buildDueReminderMessage(dealTitle || "CRM reminder"),
          type: mapCrmReminderTypeToDueReminderType(reminderType),
          nextNotifyAt:
            dueStatus === DUE_REMINDER_STATUS_PENDING && nextNotifyMillis > 0
              ? Timestamp.fromMillis(nextNotifyMillis)
              : null,
          status: dueStatus,
          reminderEnabled: reminderEnabled === true && dueStatus === DUE_REMINDER_STATUS_PENDING,
          notificationSent: false,
          assignedTo: assignedToForDueQueue,
          repeatKey: String(repeatKey || "none"),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    };

    await Promise.all(
      reminderTypes.map(async (reminderType) => {
        const reminderId = buildReminderDocId(dealLike.id, reminderType);
        const reminderRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_reminders",
          reminderId
        );
        const config = nextReminderConfigs[reminderType];
        const scheduledAt = combineReminderDateTime(
          config?.reminderDate,
          config?.reminderTime
        );
        const scheduledMillis = safeTimeMillis(scheduledAt);
        const existingReminder =
          crmReminderRecords.find((record) => record.id === reminderId) || null;
        const existingScheduledMillis = safeTimeMillis(
          existingReminder?.scheduledAt
        );
        const existingNextTriggerMillis = safeTimeMillis(
          existingReminder?.nextTriggerAt
        );
        const sameSchedule =
          existingScheduledMillis === scheduledMillis &&
          String(existingReminder?.repeatKey || "") ===
            String(config?.repeatKey || "");

        let status = config?.status || CRM_REMINDER_STATUS_DISABLED;
        if (config?.reminderEnabled !== true || !scheduledMillis) {
          status =
            status === CRM_REMINDER_STATUS_DONE
              ? CRM_REMINDER_STATUS_DONE
              : CRM_REMINDER_STATUS_DISABLED;
        } else if (status !== CRM_REMINDER_STATUS_DONE) {
          status = CRM_REMINDER_STATUS_ACTIVE;
        }

        let nextTriggerAt = null;
        if (status === CRM_REMINDER_STATUS_ACTIVE && scheduledAt) {
          if (
            sameSchedule &&
            existingReminder?.status === CRM_REMINDER_STATUS_ACTIVE &&
            existingNextTriggerMillis > 0
          ) {
            nextTriggerAt =
              existingReminder.nextTriggerAt instanceof Date
                ? existingReminder.nextTriggerAt
                : new Date(existingNextTriggerMillis);
          } else {
            nextTriggerAt = scheduledAt;
          }
        }

        const localPayload = {
          id: reminderId,
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType,
          reminderLabel: getCrmReminderTypeLabel(reminderType),
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled: config?.reminderEnabled === true,
          reminderEnabled: config?.reminderEnabled === true,
          active: status === CRM_REMINDER_STATUS_ACTIVE,
          repeatKey: config?.repeatKey ?? "daily",
          reminderDate: config?.reminderDate ?? "",
          reminderTime: config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME,
          scheduledAt,
          nextTriggerAt,
          status,
          completedAt:
            status === CRM_REMINDER_STATUS_DONE
              ? config?.completedAt || existingReminder?.completedAt || new Date()
              : null,
          createdAt: existingReminder?.createdAt || new Date(),
          updatedAt: new Date(),
          ...reminderLocationFields,
        };

        const firestorePayload = {
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType,
          reminderLabel: getCrmReminderTypeLabel(reminderType),
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled: config?.reminderEnabled === true,
          reminderEnabled: config?.reminderEnabled === true,
          active: status === CRM_REMINDER_STATUS_ACTIVE,
          repeatKey: config?.repeatKey ?? "daily",
          reminderDate: config?.reminderDate ?? "",
          reminderTime: config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME,
          scheduledAt: scheduledAt ? Timestamp.fromDate(scheduledAt) : null,
          nextTriggerAt: nextTriggerAt ? Timestamp.fromDate(nextTriggerAt) : null,
          status,
          completedAt:
            status === CRM_REMINDER_STATUS_DONE
              ? existingReminder?.completedAt || config?.completedAt || serverTimestamp()
              : null,
          updatedAt: serverTimestamp(),
          ...sanitizeCrmFirestoreValue(reminderLocationFields),
        };

        if (!existingReminder?.createdAt) {
          firestorePayload.createdAt = serverTimestamp();
        }

        await setDoc(reminderRef, firestorePayload, { merge: true });
        await upsertDueReminderDoc({
          reminderType,
          sourceReminderId: reminderId,
          title:
            reminderType === CRM_REMINDER_TYPE_INSTALLATION
              ? "Installation reminder"
              : dealLike.nextAction || getCrmReminderTypeLabel(reminderType),
          message: buildDueReminderMessage(
            reminderType === CRM_REMINDER_TYPE_INSTALLATION
              ? "Installation scheduled"
              : dealLike.nextAction || dealTitle
          ),
          nextNotifyAt: nextTriggerAt,
          sourceStatus: status,
          reminderEnabled: config?.reminderEnabled === true,
          repeatKey: config?.repeatKey ?? "daily",
        });
        applyCrmReminderLocalPatch(reminderId, localPayload);
      })
    );

    if (followUps.length > 0) {
      const legacyTaskReminderId = buildReminderDocId(
        dealLike.id,
        CRM_REMINDER_TYPE_TASK_DUE
      );
      await deleteDoc(
        doc(db, "users", workspaceUid, "crm_reminders", legacyTaskReminderId)
      ).catch(() => {});
      await upsertDueReminderDoc({
        reminderType: CRM_REMINDER_TYPE_TASK_DUE,
        sourceReminderId: legacyTaskReminderId,
        title: dealLike.nextAction || "Deal action reminder",
        message: buildDueReminderMessage(dealLike.nextAction || dealTitle),
        sourceStatus: CRM_REMINDER_STATUS_DISABLED,
        reminderEnabled: false,
      });
      applyCrmReminderLocalPatch(legacyTaskReminderId, null);
    }

    const activeFollowUpReminderDocIds = new Set(
      followUps.map((item) => buildFollowUpReminderDocId(dealLike.id, item))
    );
    const existingFollowUpReminders = crmReminderRecords.filter(
      (record) =>
        String(record.dealId || "") === String(dealLike.id) &&
        record.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
    );

    await Promise.all(
      followUps.map(async (followUp) => {
        const reminderId = buildFollowUpReminderDocId(dealLike.id, followUp);
        const reminderRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_reminders",
          reminderId
        );
        const existingReminder =
          existingFollowUpReminders.find((record) => record.id === reminderId) || null;
        const scheduledAt = combineReminderDateTime(
          followUp.dueDate,
          followUp.dueTime
        );
        const scheduledMillis = safeTimeMillis(scheduledAt);
        const existingScheduledMillis = safeTimeMillis(existingReminder?.scheduledAt);
        const existingNextTriggerMillis = safeTimeMillis(existingReminder?.nextTriggerAt);
        const sameSchedule =
          existingScheduledMillis === scheduledMillis &&
          String(existingReminder?.repeatKey || "") === String(followUp.repeat || "");
        const isDone = followUp.status === CRM_FOLLOW_UP_STATUS_DONE;
        const wantsReminder = followUp.reminderEnabled === true;
        const enabled = wantsReminder && !isDone && Boolean(scheduledMillis);
        const repeatKey = followUp.repeat || "none";

        let nextTriggerAt = null;
        if (enabled && scheduledAt) {
          if (
            sameSchedule &&
            existingReminder?.status === CRM_REMINDER_STATUS_ACTIVE &&
            existingNextTriggerMillis > 0
          ) {
            nextTriggerAt =
              existingReminder.nextTriggerAt instanceof Date
                ? existingReminder.nextTriggerAt
                : new Date(existingNextTriggerMillis);
          } else {
            nextTriggerAt = scheduledAt;
          }
        }

        const localPayload = {
          id: reminderId,
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          reminderLabel: "Follow-up",
          taskTitle: followUp.title || "Follow-up",
          taskDescription: followUp.description || "",
          followUpId: followUp.id,
          sourceTaskId: followUp.id,
          reminderId: followUp.reminderId || followUp.id,
          priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
          order: Number.isFinite(Number(followUp.order)) ? Number(followUp.order) : 0,
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled,
          reminderEnabled: wantsReminder && !isDone,
          active: enabled && !isDone,
          repeatKey,
          reminderDate: followUp.dueDate || "",
          reminderTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
          scheduledAt,
          nextTriggerAt,
          status: isDone
            ? CRM_REMINDER_STATUS_DONE
            : enabled
            ? CRM_REMINDER_STATUS_ACTIVE
            : CRM_REMINDER_STATUS_DISABLED,
          completedAt:
            isDone
              ? followUp.completedAt || existingReminder?.completedAt || new Date()
              : null,
          createdAt: existingReminder?.createdAt || followUp.createdAt || new Date(),
          updatedAt: new Date(),
          ...reminderLocationFields,
        };

        const firestorePayload = {
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          reminderLabel: "Follow-up",
          taskTitle: followUp.title || "Follow-up",
          taskDescription: followUp.description || "",
          followUpId: followUp.id,
          sourceTaskId: followUp.id,
          reminderId: followUp.reminderId || followUp.id,
          priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
          order: Number.isFinite(Number(followUp.order)) ? Number(followUp.order) : 0,
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled,
          reminderEnabled: wantsReminder && !isDone,
          active: enabled && !isDone,
          repeatKey,
          reminderDate: followUp.dueDate || "",
          reminderTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
          scheduledAt: scheduledAt ? Timestamp.fromDate(scheduledAt) : null,
          nextTriggerAt: nextTriggerAt ? Timestamp.fromDate(nextTriggerAt) : null,
          status: isDone
            ? CRM_REMINDER_STATUS_DONE
            : enabled
            ? CRM_REMINDER_STATUS_ACTIVE
            : CRM_REMINDER_STATUS_DISABLED,
          completedAt: isDone
            ? existingReminder?.completedAt || followUp.completedAt || serverTimestamp()
            : null,
          updatedAt: serverTimestamp(),
          ...sanitizeCrmFirestoreValue(reminderLocationFields),
        };

        if (!existingReminder?.createdAt) {
          firestorePayload.createdAt = serverTimestamp();
        }

        await setDoc(reminderRef, firestorePayload, { merge: true });
        await upsertDueReminderDoc({
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          sourceId: followUp.id || followUp.reminderId || "",
          sourceReminderId: reminderId,
          title: followUp.title || "Follow-up",
          message: buildDueReminderMessage(followUp.description || followUp.title || "Follow-up"),
          nextNotifyAt: nextTriggerAt,
          sourceStatus: localPayload.status,
          reminderEnabled: enabled,
          repeatKey,
        });
        applyCrmReminderLocalPatch(reminderId, localPayload);
      })
    );

    await Promise.all(
      existingFollowUpReminders
        .filter((record) => !activeFollowUpReminderDocIds.has(record.id))
        .map(async (record) => {
          await deleteDoc(doc(db, "users", workspaceUid, "crm_reminders", record.id)).catch(
            () => {}
          );
          await upsertDueReminderDoc({
            reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
            sourceId:
              record.followUpId ||
              record.sourceTaskId ||
              record.reminderId ||
              record.id ||
              "",
            sourceReminderId: record.id,
            title: record.taskTitle || "Follow-up",
            message: buildDueReminderMessage(record.taskDescription || record.taskTitle || ""),
            sourceStatus: CRM_REMINDER_STATUS_DISABLED,
            reminderEnabled: false,
          });
          applyCrmReminderLocalPatch(record.id, null);
        })
    );
  },
  [
    applyCrmReminderLocalPatch,
    buildDueReminderDocId,
    buildFollowUpReminderDocId,
    buildReminderDocId,
    combineReminderDateTime,
    crmReminderRecords,
    customerRecords,
    workspaceUid,
  ]
);

const getCrmActorSnapshot = useCallback(() => {
  const actor = getCurrentTaskHistoryActor();
  return {
    uid: actor.uid || user?.uid || "",
    name: actor.name || actor.email || user?.email || "Workspace User",
    email: actor.email || user?.email || "",
  };
}, [getCurrentTaskHistoryActor, user?.email, user?.uid]);

const getCrmOpportunityIdFromLead = useCallback((leadLike = {}) => {
  const rawId =
    leadLike?.opportunityId ||
    leadLike?.crmOpportunityId ||
    leadLike?.id ||
    leadLike?.leadId ||
    leadLike?.crmLeadId ||
    "";
  return String(rawId || "").trim();
}, []);

const getCrmOpportunityIdFromQuote = useCallback((quoteLike = {}) => {
  const directId =
    quoteLike?.opportunityId ||
    quoteLike?.crmOpportunityId ||
    quoteLike?.crm?.opportunityId ||
    quoteLike?.crm?.leadId ||
    quoteLike?.leadId ||
    quoteLike?.crmLeadId ||
    "";
  if (directId) return String(directId).trim();
  const quoteIdentity = quoteLike?.id || quoteLike?.quoteId || quoteLike?.quoteNo || "";
  return quoteIdentity ? buildQuoteCreatedLeadId(quoteIdentity) : "";
}, []);

const buildOpportunityPayloadFromLead = useCallback(
  (leadLike = {}, options = {}) => {
    const opportunityId = getCrmOpportunityIdFromLead(leadLike);
    if (!workspaceUid || !opportunityId) return null;

    const actor = getCrmActorSnapshot();
    const stageMeta = getStageMetaForType(
      leadLike.pipelineType || leadLike.quoteType || CURTAIN_QUOTE_TYPE,
      leadLike.stageId || leadLike.stage || ""
    );
    const stage = normalizeOpportunityStage(
      leadLike.salesStageId ||
        leadLike.opportunityStage ||
        leadLike.stage ||
        leadLike.stageId ||
        leadLike.workStage ||
        ""
    );
    const inferredStatus =
      stage === CRM_OPPORTUNITY_STATUS_WON || stageMeta?.marksComplete === true
        ? CRM_OPPORTUNITY_STATUS_WON
        : stage === CRM_OPPORTUNITY_STATUS_LOST || stageMeta?.isCancelled === true
        ? CRM_OPPORTUNITY_STATUS_LOST
        : stage === CRM_OPPORTUNITY_STATUS_NURTURE
        ? CRM_OPPORTUNITY_STATUS_NURTURE
        : normalizeOpportunityStatus(leadLike.status);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(leadLike)
    );
    const primarySiteLocation = getPrimarySiteLocation(leadLike) || {};
    const customerName = String(
      leadLike.customerName ||
        leadLike.contact?.name ||
        leadLike.companyOrProject ||
        leadLike.title ||
        ""
    ).trim();
    const customerPhone = String(
      leadLike.customerPhone ||
        leadLike.contact?.phone ||
        leadLike.contact?.whatsapp ||
        ""
    ).trim();
    const nextActionText = String(
      options.nextActionText ?? leadLike.nextAction ?? ""
    ).trim();
    const nextActionDueAt =
      options.nextActionDueAt ??
      leadLike.nextActionDueAt ??
      leadLike.nextActionDate ??
      null;
    const ownerUid = String(
      leadLike.ownerUid ||
        leadLike.assignedToUserId ||
        leadLike.nextActionOwnerUid ||
        ""
    ).trim();
    const ownerName = String(
      leadLike.ownerName ||
        leadLike.assignedToNameSnapshot ||
        leadLike.assignedTo ||
        ""
    ).trim();
    const estimatedValue = Number(
      leadLike.estimatedValue ||
        leadLike.finalValue ||
        leadLike.expectedValue ||
        leadLike.dealValue ||
        primaryLinkedQuote?.value ||
        0
    );
    const productInterests = Array.isArray(leadLike.productInterests)
      ? leadLike.productInterests
      : Array.isArray(leadLike.tags)
      ? leadLike.tags
      : [];
    const searchIndex = buildSafeSearchResult([
      customerName,
      customerPhone,
      leadLike.customerEmail,
      leadLike.companyOrProject,
      leadLike.source,
      leadLike.title,
      primaryLinkedQuote?.quoteNo,
      nextActionText,
      productInterests.join(" "),
    ]);

    return {
      opportunityId,
      workspaceUid,
      legacyLeadId: String(leadLike.id || leadLike.leadId || opportunityId).trim(),
      customerId: String(leadLike.customerId || "").trim(),
      customerName,
      customerPhone,
      contactPersonName: String(
        leadLike.contactPersonName ||
          leadLike.contact?.contactPersonName ||
          leadLike.contact?.name ||
          ""
      ).trim(),
      contactPersonPhone: String(
        leadLike.contactPersonPhone ||
          leadLike.contact?.phone ||
          leadLike.contact?.whatsapp ||
          ""
      ).trim(),
      siteId: String(primarySiteLocation.id || leadLike.siteId || "").trim(),
      siteName: String(
        primarySiteLocation.label ||
          primarySiteLocation.name ||
          leadLike.siteName ||
          ""
      ).trim(),
      siteAddress: String(
        primarySiteLocation.addressText ||
          leadLike.siteAddress ||
          leadLike.contact?.address ||
          ""
      ).trim(),
      source: String(leadLike.source || leadLike.contact?.source || "").trim(),
      requirementSummary: String(
        leadLike.requirementSummary ||
          leadLike.notes ||
          leadLike.description ||
          ""
      ).trim(),
      productInterests,
      estimatedValue: Number.isFinite(estimatedValue) ? estimatedValue : 0,
      ownerUid,
      ownerName,
      stage,
      salesStageId: stage,
      stageLabel: getOpportunityStageLabel(stage),
      status: inferredStatus,
      nextActionText,
      nextActionType: String(options.nextActionType || leadLike.nextActionType || "follow_up").trim(),
      nextActionDueAt: nextActionDueAt || null,
      nextActionOwnerUid: String(leadLike.nextActionOwnerUid || ownerUid || "").trim(),
      nextActionOwnerName: String(leadLike.nextActionOwnerName || ownerName || "").trim(),
      nextActionMissing: !nextActionText,
      lastContactAt: leadLike.lastContactAt || null,
      lastActivityAt: leadLike.lastActivityAt || leadLike.updatedAt || leadLike.createdAt || null,
      latestQuoteId: String(primaryLinkedQuote?.quoteId || leadLike.quoteId || "").trim(),
      latestQuoteNo: String(primaryLinkedQuote?.quoteNo || leadLike.quoteNo || "").trim(),
      latestQuoteAmount: Number(primaryLinkedQuote?.value || leadLike.finalValue || leadLike.expectedValue || 0),
      lostReason: String(leadLike.lostReason || "").trim(),
      lostReasonNotes: String(leadLike.lostReasonNotes || "").trim(),
      searchText: searchIndex.searchText,
      searchKeywords: searchIndex.searchKeywords,
      createdAt: leadLike.createdAt || serverTimestamp(),
      createdBy: String(leadLike.createdBy || actor.uid || "").trim(),
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    };
  },
  [
    getCrmActorSnapshot,
    getCrmOpportunityIdFromLead,
    getStageMetaForType,
    workspaceUid,
  ]
);

const logCrmActivity = useCallback(
  async (activityInput = {}) => {
    if (!workspaceUid) return null;
    const actor = getCrmActorSnapshot();
    const entry = buildActivityLogEntry({
      ...activityInput,
      workspaceUid,
      createdBy: activityInput.createdBy || actor.uid,
      createdByName: activityInput.createdByName || actor.name,
      createdAt: activityInput.createdAt || serverTimestamp(),
    });
    if (!entry.relatedId && !entry.opportunityId && !entry.quoteId) return null;

    await setDoc(
      doc(db, "users", workspaceUid, "activity_log", entry.activityId),
      sanitizeCrmFirestoreValue(entry),
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "activity_log");
    return entry;
  },
  [getCrmActorSnapshot, workspaceUid]
);

const normalizeCrmMaterialRecord = useCallback((docId, raw = {}) => {
  const orderDate = normalizeCrmDate(raw.orderDate || null);
  const expectedArrivalDate = normalizeCrmDate(raw.expectedArrivalDate || null);
  const receivedDate = normalizeCrmDate(raw.receivedDate || null);
  const lastCheckedAt = normalizeCrmDate(raw.lastCheckedAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const parseQuantity = (value) => {
    if (value == null || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Number(number.toFixed(2))) : null;
  };
  const quantity = parseQuantity(raw.quantity);
  const orderedQuantity = parseQuantity(raw.orderedQuantity);
  const receivedQuantity = parseQuantity(raw.receivedQuantity);
  const itemType = normalizeTrackingItemType(raw.itemType || raw.materialType);
  const status = normalizeTrackingStatus(raw.status);
  const material = {
    id: docId,
    trackingItemId: String(raw.trackingItemId || raw.materialId || docId).trim(),
    materialId: String(raw.trackingItemId || raw.materialId || docId).trim(),
    workspaceUid: raw.workspaceUid || "",
    projectId: raw.projectId || "",
    projectTitle: raw.projectTitle || "",
    quoteId: raw.quoteId || "",
    quoteNo: raw.quoteNo || "",
    customerId: raw.customerId || "",
    customerName: raw.customerName || "",
    siteName: raw.siteName || "",
    itemType,
    materialType: itemType,
    itemTypeLabel: getTrackingItemTypeLabel(itemType),
    materialTypeLabel: getTrackingItemTypeLabel(itemType),
    itemName: raw.itemName || "",
    brandName: raw.brandName || "",
    fabricCode: raw.fabricCode || "",
    catalogueName: raw.catalogueName || "",
    srNumber: raw.srNumber || "",
    supplierName: raw.supplierName || "",
    supplierPhone: raw.supplierPhone || "",
    quantity,
    orderedQuantity,
    receivedQuantity,
    pendingQuantity: calculateTrackingPendingQty(quantity, receivedQuantity),
    unit: raw.unit || "meter",
    orderDate,
    orderDateKey: toDateInputValue(orderDate),
    expectedArrivalDate,
    expectedArrivalDateKey: toDateInputValue(expectedArrivalDate),
    receivedDate,
    receivedDateKey: toDateInputValue(receivedDate),
    courierName: raw.courierName || "",
    transportName: raw.transportName || "",
    trackingNumber: raw.trackingNumber || "",
    trackingUrl: raw.trackingUrl || "",
    lrNumber: raw.lrNumber || "",
    status,
    statusLabel: getTrackingStatusLabel(status),
    priority: raw.priority === "urgent" ? "urgent" : "normal",
    lastCheckedAt,
    lastCheckedBy: raw.lastCheckedBy || "",
    lastUpdateNote: raw.lastUpdateNote || "",
    notes: raw.notes || "",
    createdAt,
    updatedAt,
    createdBy: raw.createdBy || "",
    updatedBy: raw.updatedBy || "",
    raw,
  };
  return {
    ...material,
    displayTitle: buildTrackingItemDisplayTitle(material),
    trackingInfo: formatTrackingInfo(material),
    isOpen: isTrackingItemOpen(material),
    isDelayed: isTrackingItemDelayed(material),
    updatedMillis: safeTimeMillis(updatedAt || createdAt),
  };
}, [toDateInputValue]);

const patchCrmTaskLocalState = useCallback((taskId, patch) => {
  if (!taskId) return;
  invalidateWorkspaceCollectionCache(workspaceUid, "crm_tasks");
  setCrmTaskRecords((prev) => {
    let found = false;
    const nextRecords = prev.map((task) => {
      if (String(task.id || task.taskId || "") !== String(taskId)) return task;
      found = true;
      return {
        ...task,
        ...patch,
        id: taskId,
        taskId,
      };
    });
    if (found) return nextRecords;
    return [{ id: taskId, taskId, ...patch }, ...nextRecords];
  });
}, [workspaceUid]);

const createCrmTask = useCallback(
  async (taskInput = {}) => {
    if (!workspaceUid) return null;
    const actor = getCrmActorSnapshot();
    const relatedType = String(taskInput.relatedType || "opportunity").trim();
    const relatedId = String(taskInput.relatedId || taskInput.opportunityId || "").trim();
    const taskType = String(taskInput.taskType || "follow_up").trim();
    const taskId =
      String(taskInput.taskId || "").trim() ||
      `${sanitizeTaskHistorySegment(relatedType)}_${sanitizeTaskHistorySegment(
        relatedId || "general"
      )}_${sanitizeTaskHistorySegment(taskType || "task")}`;
    if (!taskId) return null;

    const priority = CRM_TASK_PRIORITIES.includes(String(taskInput.priority || "").trim())
      ? String(taskInput.priority).trim()
      : "normal";
    const status = [CRM_TASK_STATUS_COMPLETED, CRM_TASK_STATUS_CANCELLED].includes(
      String(taskInput.status || "").trim()
    )
      ? String(taskInput.status).trim()
      : CRM_TASK_STATUS_OPEN;
    const payload = {
      taskId,
      workspaceUid,
      relatedType,
      relatedId,
      customerId: String(taskInput.customerId || "").trim(),
      customerName: String(taskInput.customerName || "").trim(),
      opportunityId: String(taskInput.opportunityId || relatedId || "").trim(),
      projectId: String(taskInput.projectId || "").trim(),
      quoteId: String(taskInput.quoteId || "").trim(),
      quoteNo: String(taskInput.quoteNo || "").trim(),
      taskType,
      title: buildCrmTaskTitle(taskInput),
      notes: String(taskInput.notes || taskInput.description || "").trim(),
      ownerUid: String(taskInput.ownerUid || taskInput.nextActionOwnerUid || actor.uid || "").trim(),
      ownerName: String(taskInput.ownerName || taskInput.nextActionOwnerName || actor.name || "").trim(),
      dueAt: taskInput.dueAt || taskInput.nextActionDueAt || null,
      status,
      priority,
      completedAt: taskInput.completedAt || null,
      completedBy: String(taskInput.completedBy || "").trim(),
      systemCreated: taskInput.systemCreated === true || taskInput.systemGenerated === true,
      systemGenerated: taskInput.systemGenerated === true || taskInput.systemCreated === true,
      recurringType: String(taskInput.recurringType || "").trim(),
      trackingTaskForProjectId: String(taskInput.trackingTaskForProjectId || "").trim(),
      paymentFollowUpForProjectId: String(taskInput.paymentFollowUpForProjectId || "").trim(),
      dueAmount:
        Number.isFinite(Number(taskInput.dueAmount)) && Number(taskInput.dueAmount) >= 0
          ? Number(Number(taskInput.dueAmount).toFixed(2))
          : null,
      dailyCheckTime: String(taskInput.dailyCheckTime || "").trim(),
      templateKey: String(taskInput.templateKey || "").trim(),
      templateTaskKey: String(taskInput.templateTaskKey || "").trim(),
      sourceKind: String(taskInput.sourceKind || "").trim(),
      createdAt: taskInput.createdAt || serverTimestamp(),
      createdBy: String(taskInput.createdBy || actor.uid || "").trim(),
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    };

    const taskRef = doc(db, "users", workspaceUid, "crm_tasks", taskId);
    const persistedPayload = await runTransaction(db, async (transaction) => {
      const current = await transaction.get(taskRef);
      const patch = { ...payload, createdBy: actor.uid || "" };
      if (current.exists()) {
        ["ownerUid", "ownerName", "createdAt", "createdBy"].forEach((field) => delete patch[field]);
      }
      transaction.set(taskRef, sanitizeCrmFirestoreValue(patch), { merge: true });
      return { ...payload, ...(current.exists() ? {
        ownerUid: current.data().ownerUid || "", ownerName: current.data().ownerName || "",
        createdAt: current.data().createdAt || null, createdBy: current.data().createdBy || "",
      } : { createdAt: new Date(), createdBy: actor.uid || "" }) };
    });
    if (isDevRuntime) {
      devLog("CRM_NEXT_ACTION_TASK_TRACE", {
        opportunityId: payload.opportunityId,
        taskId,
        action: status === CRM_TASK_STATUS_CANCELLED ? "cancel" : "upsert",
        dueAt: payload.dueAt || null,
        ownerUid: payload.ownerUid,
        status: payload.status,
      });
    }
    if (crmWorkspaceRef.current === workspaceUid) patchCrmTaskLocalState(taskId, {
      ...persistedPayload, id: taskId, updatedAt: new Date(),
    });
    return persistedPayload;
  },
  [getCrmActorSnapshot, patchCrmTaskLocalState, workspaceUid]
);

const patchProjectDeliveryTrackingLocal = useCallback((projectId, deliveryTrackingPatch = {}) => {
  if (!projectId) return;
  setCrmProjectRecords((prev) =>
    prev.map((project) =>
      String(project.projectId || project.id || "") === String(projectId)
        ? {
            ...project,
            deliveryTracking: {
              ...(project.deliveryTracking || {}),
              ...deliveryTrackingPatch,
            },
            raw: {
              ...(project.raw || {}),
              deliveryTracking: {
                ...(project.raw?.deliveryTracking || project.deliveryTracking || {}),
                ...deliveryTrackingPatch,
              },
            },
          }
        : project
    )
  );
}, []);

const createDailyTrackingTaskForProject = useCallback(
  async (projectLike = {}, options = {}) => {
    const projectId = String(projectLike.projectId || projectLike.id || options.projectId || "").trim();
    if (!workspaceUid || !projectId || crmSettings.enableDailyTrackingReminder === false) {
      return null;
    }
    const deliveryTracking =
      projectLike.deliveryTracking ||
      projectLike.raw?.deliveryTracking ||
      {};
    const trackingOwnerUid = String(
      options.trackingOwnerUid ||
        deliveryTracking.trackingOwnerUid ||
        projectLike.projectOwnerUid ||
        ""
    ).trim();
    const trackingOwnerName = String(
      options.trackingOwnerName ||
        deliveryTracking.trackingOwnerName ||
        projectLike.projectOwnerName ||
        "Unassigned"
    ).trim();
    if (!trackingOwnerUid) {
      if (isDevRuntime) {
        devLog("CRM_DAILY_TRACKING_TASK_TRACE", {
          projectId,
          taskId: "",
          dueAt: null,
          action: "skipped_no_owner",
        });
      }
      return null;
    }

    const nextCheck = calculateNextTrackingCheckTime({
      dailyCheckTime:
        options.dailyCheckTime ||
        deliveryTracking.dailyCheckTime ||
        crmSettings.defaultTrackingReminderTime ||
        "10:30",
      timezone: options.timezone || deliveryTracking.timezone || "Asia/Kolkata",
      fromDate: options.fromDate || new Date(),
    });
    const taskId = buildDailyTrackingTaskId({ projectId, date: nextCheck.date });
    const buildDeliveryTrackingPatch = (dueAtValue) => ({
      ...(deliveryTracking || {}),
      enabled: true,
      trackingComplete: false,
      trackingOwnerUid,
      trackingOwnerName,
      dailyCheckTime: nextCheck.dailyCheckTime,
      timezone: nextCheck.timezone,
      nextCheckTaskId: taskId,
      nextCheckDueAt: dueAtValue,
    });
    const taskRef = doc(db, "users", workspaceUid, "crm_tasks", taskId);
    const existingSnap = await trackedGetDoc(taskRef, {
      label: "crmDailyTrackingTask.existingTask",
    }).catch(() => null);
    if (existingSnap?.exists?.()) {
      const existingTask = existingSnap.data() || {};
      if (String(existingTask.status || "") === CRM_TASK_STATUS_OPEN) {
        const deliveryTrackingPatch = buildDeliveryTrackingPatch(
          existingTask.dueAt || Timestamp.fromDate(nextCheck.date)
        );
        await updateDoc(
          doc(db, "users", workspaceUid, "projects", projectId),
          sanitizeCrmFirestoreValue({
            deliveryTracking: deliveryTrackingPatch,
            updatedAt: serverTimestamp(),
            updatedBy: getCrmActorSnapshot().uid || "",
          })
        ).catch((error) => {
          if (isDevRuntime) {
            devWarn("CRM_DAILY_TRACKING_PROJECT_PATCH_FAILED_EXACT", {
              projectId,
              taskId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
        patchProjectDeliveryTrackingLocal(projectId, deliveryTrackingPatch);
        if (isDevRuntime) {
          devLog("CRM_DAILY_TRACKING_TASK_TRACE", {
            projectId,
            taskId,
            dueAt: existingTask.dueAt || null,
            action: "skipped_existing",
          });
          devLog("CRM_TRACKING_SUMMARY_TRACE", {
            projectId,
            openItemCount: deliveryTrackingPatch.openItemCount || 0,
            receivedItemCount: deliveryTrackingPatch.receivedItemCount || 0,
            delayedItemCount: deliveryTrackingPatch.delayedItemCount || 0,
            totalItemCount: deliveryTrackingPatch.totalItemCount || 0,
            trackingComplete: deliveryTrackingPatch.trackingComplete === true,
          });
        }
        return { taskId, skippedExisting: true, task: existingTask, deliveryTrackingPatch };
      }
    }

    const dueAt = Timestamp.fromDate(nextCheck.date);
    const createdTask = await createCrmTask({
      taskId,
      relatedType: "project_tracking",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      taskType: "follow_up",
      title: "Update delivery tracking",
      notes: "Check suppliers/couriers and update the Tracking tab.",
      ownerUid: trackingOwnerUid,
      ownerName: trackingOwnerName,
      dueAt,
      status: CRM_TASK_STATUS_OPEN,
      priority: "normal",
      systemCreated: true,
      systemGenerated: true,
      recurringType: "daily_tracking",
      trackingTaskForProjectId: projectId,
      dailyCheckTime: nextCheck.dailyCheckTime,
      sourceKind: "daily_tracking",
    });
    const deliveryTrackingPatch = buildDeliveryTrackingPatch(dueAt);
    await updateDoc(
      doc(db, "users", workspaceUid, "projects", projectId),
      sanitizeCrmFirestoreValue({
        deliveryTracking: deliveryTrackingPatch,
        updatedAt: serverTimestamp(),
        updatedBy: getCrmActorSnapshot().uid || "",
      })
    ).catch((error) => {
      if (isDevRuntime) {
        devWarn("CRM_DAILY_TRACKING_PROJECT_PATCH_FAILED_EXACT", {
          projectId,
          taskId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    patchProjectDeliveryTrackingLocal(projectId, deliveryTrackingPatch);
    if (isDevRuntime) {
      devLog("CRM_DAILY_TRACKING_TASK_TRACE", {
        projectId,
        taskId,
        dueAt,
        action: options.action || "created",
      });
      devLog("CRM_TRACKING_SUMMARY_TRACE", {
        projectId,
        openItemCount: deliveryTrackingPatch.openItemCount || 0,
        receivedItemCount: deliveryTrackingPatch.receivedItemCount || 0,
        delayedItemCount: deliveryTrackingPatch.delayedItemCount || 0,
        totalItemCount: deliveryTrackingPatch.totalItemCount || 0,
        trackingComplete: deliveryTrackingPatch.trackingComplete === true,
      });
    }
    return { taskId, task: createdTask, deliveryTrackingPatch };
  },
  [
    buildDailyTrackingTaskId,
    calculateNextTrackingCheckTime,
    createCrmTask,
    crmSettings.defaultTrackingReminderTime,
    crmSettings.enableDailyTrackingReminder,
    getCrmActorSnapshot,
    patchProjectDeliveryTrackingLocal,
    sanitizeCrmFirestoreValue,
    trackedGetDoc,
    workspaceUid,
  ]
);

const createOrderTemplateTasksForProject = useCallback(
  async (projectLike = {}, options = {}) => {
    const projectId = String(projectLike?.projectId || projectLike?.id || "").trim();
    if (!workspaceUid || !projectId) {
      return { tasksPlanned: 0, tasksCreated: 0, tasksSkipped: 0, createdTasks: [] };
    }

    const actor = getCrmActorSnapshot();
    const templatesEnabled = crmSettings.enableOrderTaskTemplates !== false;
    const manualCreate = options.manual === true;
    const shouldCreate = templatesEnabled && (manualCreate || crmSettings.autoCreateOrderTasks !== false);
    const requestedTemplateKey = String(
      options.templateKey ||
        crmSettings.defaultOrderTaskTemplate ||
        STANDARD_ORDER_TASK_TEMPLATE_KEY
    ).trim();
    const templateKey = Object.prototype.hasOwnProperty.call(
      ORDER_TASK_TEMPLATES,
      requestedTemplateKey
    )
      ? requestedTemplateKey
      : STANDARD_ORDER_TASK_TEMPLATE_KEY;
    const templateTasks = ORDER_TASK_TEMPLATES[templateKey] || [];
    const result = {
      projectId,
      templateKey,
      autoCreateEnabled: crmSettings.autoCreateOrderTasks !== false,
      templatesEnabled,
      tasksPlanned: shouldCreate ? templateTasks.length : 0,
      tasksCreated: 0,
      tasksSkipped: 0,
      skippedExisting: 0,
      skippedCompleted: 0,
      createdTasks: [],
      existingTasks: [],
    };

    if (!shouldCreate || !templateTasks.length) {
      if (isDevRuntime) {
        devLog("CRM_ORDER_TASK_TEMPLATE_TRACE", result);
      }
      return result;
    }

    const projectCreatedAt = normalizeCrmDate(projectLike.createdAt) || new Date();
    const projectCreatedKey = toDateInputValue(projectCreatedAt) || todayISO();
    const expectedInstallationDate = normalizeCrmDate(
      projectLike.expectedInstallationDate ||
        projectLike.installationDate ||
        projectLike.scheduledInstallationDate ||
        null
    );
    const resolveTaskOwner = (templateTask = {}) => {
      const ownerMode = String(templateTask.ownerMode || "project_owner").trim();
      const projectOwner = {
        uid: String(projectLike.projectOwnerUid || "").trim(),
        name: String(projectLike.projectOwnerName || "").trim(),
      };
      const salesOwner = {
        uid: String(projectLike.salesOwnerUid || "").trim(),
        name: String(projectLike.salesOwnerName || "").trim(),
      };
      let owner =
        ownerMode === "sales_owner"
          ? salesOwner.uid
            ? salesOwner
            : projectOwner
          : ownerMode === "current_user"
          ? { uid: actor.uid || "", name: actor.name || "" }
          : projectOwner.uid
          ? projectOwner
          : salesOwner;
      if (!owner?.uid) {
        owner = { uid: actor.uid || "", name: actor.name || "" };
        if (isDevRuntime) {
          devLog("CRM_ORDER_TASK_CREATE_TRACE", {
            projectId,
            templateTaskKey: templateTask.templateTaskKey || "",
            ownerUid: owner.uid,
            dueAt: null,
            action: "owner_fallback_current_user",
          });
        }
      }
      return {
        uid: String(owner.uid || "").trim(),
        name: String(owner.name || actor.name || "Current user").trim(),
      };
    };
    const resolveDueAt = (templateTask = {}) => {
      const offsetDays = Number(templateTask.defaultDueOffsetDays || 0) || 0;
      let dueDate =
        parseDateInputValue(addDaysToDateKey(projectCreatedKey, offsetDays)) ||
        new Date(projectCreatedAt.getTime());
      if (
        templateTask.templateTaskKey === "schedule_installation" &&
        expectedInstallationDate
      ) {
        const oneDayBeforeInstall = new Date(expectedInstallationDate.getTime());
        oneDayBeforeInstall.setDate(oneDayBeforeInstall.getDate() - 1);
        dueDate =
          safeTimeMillis(oneDayBeforeInstall) >= safeTimeMillis(projectCreatedAt)
            ? oneDayBeforeInstall
            : expectedInstallationDate;
        if (safeTimeMillis(dueDate) > safeTimeMillis(expectedInstallationDate)) {
          dueDate = expectedInstallationDate;
        }
      }
      return Number.isNaN(dueDate?.getTime?.()) ? Timestamp.fromDate(new Date()) : Timestamp.fromDate(dueDate);
    };

    for (const templateTask of templateTasks) {
      const templateTaskKey = String(templateTask.templateTaskKey || "").trim();
      if (!templateTaskKey) {
        result.tasksSkipped += 1;
        continue;
      }
      const taskId = `project_${sanitizeTaskHistorySegment(projectId)}__${templateTaskKey}`;
      const taskRef = doc(db, "users", workspaceUid, "crm_tasks", taskId);
      const existingSnap = await getDoc(taskRef, {
        label: "crmOrderTaskTemplate.existingTask",
      }).catch(() => null);
      if (existingSnap?.exists?.()) {
        const existingTask = existingSnap.data() || {};
        const existingStatus = String(existingTask.status || "").trim();
        const skippedCompleted = existingStatus === CRM_TASK_STATUS_COMPLETED;
        result.tasksSkipped += 1;
        result.skippedExisting += skippedCompleted ? 0 : 1;
        result.skippedCompleted += skippedCompleted ? 1 : 0;
        result.existingTasks.push({
          id: taskId,
          taskId,
          ...(existingTask || {}),
          title: buildCrmTaskTitle(existingTask),
          systemGenerated: existingTask.systemGenerated === true || existingTask.systemCreated === true,
          dueMillis: safeTimeMillis(existingTask.dueAt || existingTask.dueDate),
        });
        if (isDevRuntime) {
          devLog("CRM_ORDER_TASK_CREATE_TRACE", {
            projectId,
            taskId,
            templateTaskKey,
            ownerUid: existingTask.ownerUid || "",
            dueAt: existingTask.dueAt || null,
            action: skippedCompleted ? "skipped_completed" : "skipped_existing",
          });
        }
        continue;
      }

      const owner = resolveTaskOwner(templateTask);
      const dueAt = resolveDueAt(templateTask);
      const createdTask = await createCrmTask({
        taskId,
        relatedType: "project",
        relatedId: projectId,
        projectId,
        customerId: projectLike.customerId,
        customerName: projectLike.customerName,
        opportunityId: projectLike.opportunityId,
        quoteId: projectLike.quoteId,
        quoteNo: projectLike.quoteNo,
        taskType: templateTask.taskType || "other",
        title: templateTask.title,
        notes: "",
        ownerUid: owner.uid,
        ownerName: owner.name,
        dueAt,
        status: CRM_TASK_STATUS_OPEN,
        priority: templateTask.priority || "normal",
        systemCreated: true,
        systemGenerated: true,
        sourceKind: "order_task_template",
        templateKey,
        templateTaskKey,
        createdBy: actor.uid || "",
      });
      result.tasksCreated += 1;
      result.createdTasks.push({
        ...(createdTask || {}),
        id: taskId,
        taskId,
        dueMillis: safeTimeMillis(createdTask?.dueAt || null),
      });
      if (isDevRuntime) {
        devLog("CRM_ORDER_TASK_CREATE_TRACE", {
          projectId,
          taskId,
          templateTaskKey,
          ownerUid: owner.uid,
          dueAt,
          action: "created",
        });
      }
    }

    if (result.tasksCreated > 0) {
      await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        opportunityId: projectLike.opportunityId || "",
        customerId: projectLike.customerId || "",
        customerName: projectLike.customerName || "",
        quoteId: projectLike.quoteId || "",
        quoteNo: projectLike.quoteNo || "",
        activityType: "task_created",
        title: "Order tasks created",
        description: "Default order task checklist was created for this project.",
      });
    }

    if (result.createdTasks.length || result.existingTasks.length) {
      setCrmProjectDrawerData((prev) => {
        if (String(prev.projectId || "") !== String(projectId)) return prev;
        const existingIds = new Set(
          (prev.tasks || []).map((task) => String(task.taskId || task.id || ""))
        );
        const nextTasks = [
          ...(prev.tasks || []),
          ...[...result.createdTasks, ...result.existingTasks].filter(
            (task) => !existingIds.has(String(task.taskId || task.id || ""))
          ),
        ].sort(
          (left, right) =>
            safeTimeMillis(left.dueAt || left.dueDate) -
            safeTimeMillis(right.dueAt || right.dueDate)
        );
        return {
          ...prev,
          tasks: nextTasks,
        };
      });
    }

    if (isDevRuntime) {
      devLog("CRM_ORDER_TASK_TEMPLATE_TRACE", result);
    }
    return result;
  },
  [
    createCrmTask,
    crmSettings.autoCreateOrderTasks,
    crmSettings.defaultOrderTaskTemplate,
    crmSettings.enableOrderTaskTemplates,
    getCrmActorSnapshot,
    logCrmActivity,
    parseDateInputValue,
    toDateInputValue,
    workspaceUid,
  ]
);

const persistOpportunityProjection = useCallback(async (payload) => {
  const reference = doc(db, "users", workspaceUid, "opportunities", payload.opportunityId);
  return runTransaction(db, async (transaction) => {
    const current = await transaction.get(reference);
    const patch = { ...payload };
    const preserved = {};
    if (current.exists()) {
      if (current.data().workspaceUid && current.data().workspaceUid !== workspaceUid) throw new Error("Opportunity workspace does not match.");
      ["ownerUid", "ownerName", "nextActionOwnerUid", "nextActionOwnerName", "createdAt", "createdBy"]
        .forEach((field) => {
          delete patch[field];
          if (Object.prototype.hasOwnProperty.call(current.data(), field)) preserved[field] = current.data()[field];
        });
    } else {
      patch.createdBy = auth.currentUser?.uid || user?.uid || "";
    }
    transaction.set(reference, sanitizeCrmFirestoreValue(patch), { merge: true });
    return { ...payload, ...preserved };
  });
}, [workspaceUid, user?.uid]);

const upsertOpportunityFromLegacyLead = useCallback(
  async (leadLike = {}, options = {}) => {
    let payload = buildOpportunityPayloadFromLead(leadLike, options);
    if (!payload?.opportunityId || !workspaceUid) return null;

    payload = await persistOpportunityProjection(payload);
    if (isDevRuntime) {
      devLog("CRM_PHASE1_WRITE_THROUGH_TRACE", {
        leadId: payload.legacyLeadId || leadLike?.id || "",
        opportunityId: payload.opportunityId,
        customerName: payload.customerName,
        ownerUid: payload.ownerUid,
        stage: payload.stage,
        status: payload.status,
        hasNextAction: Boolean(payload.nextActionText),
        nextActionDueAt: payload.nextActionDueAt || null,
      });
    }
    invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");

    if (
      payload.status === CRM_OPPORTUNITY_STATUS_OPEN &&
      payload.nextActionText &&
      payload.nextActionDueAt
    ) {
      await createCrmTask({
        taskId: `${payload.opportunityId}__next_action`,
        relatedType: "opportunity",
        relatedId: payload.opportunityId,
        opportunityId: payload.opportunityId,
        customerId: payload.customerId,
        customerName: payload.customerName,
        quoteId: payload.latestQuoteId,
        quoteNo: payload.latestQuoteNo,
        taskType: payload.nextActionType || "follow_up",
        title: payload.nextActionText,
        notes: payload.requirementSummary,
        ownerUid: payload.nextActionOwnerUid || payload.ownerUid,
        ownerName: payload.nextActionOwnerName || payload.ownerName,
        dueAt: payload.nextActionDueAt,
        priority: "normal",
        systemCreated: true,
        sourceKind: "next_action",
      });
    } else {
      const nextActionTaskId = `${payload.opportunityId}__next_action`;
      await updateDoc(
        doc(db, "users", workspaceUid, "crm_tasks", nextActionTaskId),
        {
          status: CRM_TASK_STATUS_CANCELLED,
          updatedAt: serverTimestamp(),
          updatedBy: payload.updatedBy || "",
        }
      ).catch(() => {});
      if (isDevRuntime) {
        devLog("CRM_NEXT_ACTION_TASK_TRACE", {
          opportunityId: payload.opportunityId,
          taskId: nextActionTaskId,
          action: "cancel",
          dueAt: null,
          ownerUid: payload.ownerUid,
          status: CRM_TASK_STATUS_CANCELLED,
        });
      }
    }

    return payload;
  },
  [buildOpportunityPayloadFromLead, createCrmTask, persistOpportunityProjection, workspaceUid]
);

const ensureOpportunityForLead = useCallback(
  async (leadLike = {}, options = {}) =>
    upsertOpportunityFromLegacyLead(leadLike, options),
  [upsertOpportunityFromLegacyLead]
);

const updateOpportunityFromQuote = useCallback(
  async (quoteInput = {}) => {
    if (!workspaceUid || !quoteInput) return null;
    const opportunityId = getCrmOpportunityIdFromQuote(quoteInput);
    if (!opportunityId) return null;
    const quoteTotal = Number(
      quoteInput.grandTotal ||
        quoteInput.finalTotal ||
        quoteInput.total ||
        quoteInput.netTotal ||
        quoteInput.crm?.finalValue ||
        0
    );
    const actor = getCrmActorSnapshot();
    const customerName = String(
      quoteInput.customerName ||
        quoteInput.crm?.contact?.name ||
        quoteInput.generalQuote?.quoteFor?.name ||
        ""
    ).trim();
    const searchIndex = buildSafeSearchResult([
      customerName,
      quoteInput.customerPhone,
      quoteInput.quoteNo,
      quoteInput.projectName,
      quoteInput.crm?.title,
    ]);
    const salesStageId = deriveSalesStageIdForQuote(quoteInput, quoteInput.crm || {});
    let payload = {
      opportunityId,
      workspaceUid,
      legacyLeadId: String(
        quoteInput.crm?.leadId || quoteInput.leadId || quoteInput.crmLeadId || ""
      ).trim(),
      customerId: String(quoteInput.customerId || quoteInput.crm?.customerId || "").trim(),
      customerName,
      customerPhone: String(
        quoteInput.customerPhone || quoteInput.crm?.contact?.phone || ""
      ).trim(),
      source: quoteInput.crm?.source || "Quotation",
      requirementSummary: String(quoteInput.crm?.notes || quoteInput.notes || "").trim(),
      estimatedValue: Number.isFinite(quoteTotal) ? quoteTotal : 0,
      ownerUid: String(quoteInput.crm?.assignedToUserId || "").trim(),
      ownerName: String(quoteInput.crm?.assignedToNameSnapshot || "").trim(),
      stage: salesStageId,
      salesStageId,
      status: normalizeOpportunityStatus(quoteInput.status || quoteInput.crm?.status || ""),
      nextActionText: String(quoteInput.crm?.nextAction || "").trim(),
      nextActionType: "follow_up",
      nextActionDueAt: quoteInput.crm?.nextActionDate || null,
      nextActionOwnerUid: String(quoteInput.crm?.assignedToUserId || "").trim(),
      nextActionOwnerName: String(quoteInput.crm?.assignedToNameSnapshot || "").trim(),
      nextActionMissing: !String(quoteInput.crm?.nextAction || "").trim(),
      lastActivityAt: quoteInput.updatedAt || quoteInput.createdAt || null,
      latestQuoteId: String(quoteInput.id || quoteInput.quoteId || "").trim(),
      latestQuoteNo: String(quoteInput.quoteNo || "").trim(),
      latestQuoteAmount: Number.isFinite(quoteTotal) ? quoteTotal : 0,
      searchText: searchIndex.searchText,
      searchKeywords: searchIndex.searchKeywords,
      createdAt: quoteInput.createdAt || serverTimestamp(),
      createdBy: String(quoteInput.createdBy || actor.uid || "").trim(),
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    };

    payload = await persistOpportunityProjection(payload);
    if (isDevRuntime) {
      devLog("CRM_PHASE1_WRITE_THROUGH_TRACE", {
        leadId: payload.legacyLeadId || "",
        opportunityId: payload.opportunityId,
        customerName: payload.customerName,
        ownerUid: payload.ownerUid,
        stage: payload.stage,
        status: payload.status,
        hasNextAction: Boolean(payload.nextActionText),
        nextActionDueAt: payload.nextActionDueAt || null,
      });
    }
    invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
    return payload;
  },
  [deriveSalesStageIdForQuote, getCrmActorSnapshot, getCrmOpportunityIdFromQuote, persistOpportunityProjection, workspaceUid]
);

const ensureOpportunityForQuote = useCallback(
  async (quoteInput = {}) => updateOpportunityFromQuote(quoteInput),
  [updateOpportunityFromQuote]
);

// Phase 1 API surface for the next-action editor that will be wired in the CRM detail UI.
// eslint-disable-next-line no-unused-vars
const setOpportunityNextAction = useCallback(
  async (opportunityId = "", nextActionInput = {}) => {
    if (!workspaceUid || !opportunityId) return null;
    const actor = getCrmActorSnapshot();
    const nextActionText = String(nextActionInput.nextActionText || "").trim();
    const nextActionDueAt = nextActionInput.nextActionDueAt || null;
    const patch = {
      nextActionText,
      nextActionType: String(nextActionInput.nextActionType || "follow_up").trim(),
      nextActionDueAt,
      nextActionOwnerUid: String(nextActionInput.nextActionOwnerUid || actor.uid || "").trim(),
      nextActionOwnerName: String(nextActionInput.nextActionOwnerName || actor.name || "").trim(),
      nextActionMissing: !nextActionText,
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    };
    await updateDoc(
      doc(db, "users", workspaceUid, "opportunities", opportunityId),
      sanitizeCrmFirestoreValue(patch)
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
    if (nextActionText && nextActionDueAt) {
      await createCrmTask({
        taskId: `${opportunityId}__next_action`,
        relatedType: "opportunity",
        relatedId: opportunityId,
        opportunityId,
        taskType: patch.nextActionType,
        title: nextActionText,
        ownerUid: patch.nextActionOwnerUid,
        ownerName: patch.nextActionOwnerName,
        dueAt: nextActionDueAt,
        priority: "normal",
        systemCreated: true,
        sourceKind: "next_action",
      });
    } else {
      const nextActionTaskId = `${opportunityId}__next_action`;
      await updateDoc(doc(db, "users", workspaceUid, "crm_tasks", nextActionTaskId), {
        status: CRM_TASK_STATUS_CANCELLED,
        updatedAt: serverTimestamp(),
        updatedBy: actor.uid || "",
      }).catch(() => {});
      if (isDevRuntime) {
        devLog("CRM_NEXT_ACTION_TASK_TRACE", {
          opportunityId,
          taskId: nextActionTaskId,
          action: "cancel",
          dueAt: null,
          ownerUid: patch.nextActionOwnerUid,
          status: CRM_TASK_STATUS_CANCELLED,
        });
      }
    }
    return patch;
  },
  [createCrmTask, getCrmActorSnapshot, workspaceUid]
);

const completeCrmTask = useCallback(
  async (taskId = "", taskLike = {}) => {
    if (!workspaceUid || !taskId) return null;
    const actor = getCrmActorSnapshot();
    const patch = {
      status: CRM_TASK_STATUS_COMPLETED,
      completedAt: serverTimestamp(),
      completedBy: actor.uid || "",
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    };
    await updateDoc(doc(db, "users", workspaceUid, "crm_tasks", taskId), patch);
    patchCrmTaskLocalState(taskId, {
      ...patch,
      completedAt: new Date(),
      updatedAt: new Date(),
    });
    if (isDevRuntime) {
      devLog("CRM_NEXT_ACTION_TASK_TRACE", {
        opportunityId: taskLike.opportunityId || "",
        taskId,
        action: "complete",
        dueAt: taskLike.dueAt || taskLike.dueDate || null,
        ownerUid: taskLike.ownerUid || "",
        status: CRM_TASK_STATUS_COMPLETED,
      });
    }
    const taskRelatedType = taskLike.relatedType || "task";
    const taskRelatedId = taskLike.relatedId || taskLike.opportunityId || taskId;
    const taskProjectId = String(
      taskLike.projectId || (taskRelatedType === "project" ? taskRelatedId : "")
    ).trim();
    if (taskProjectId) {
      setCrmProjectDrawerData((prev) => {
        if (String(prev.projectId || "") !== String(taskProjectId)) return prev;
        return {
          ...prev,
          tasks: (prev.tasks || []).map((entry) =>
            String(entry.taskId || entry.id) === String(taskId)
              ? { ...entry, status: CRM_TASK_STATUS_COMPLETED, completedAt: new Date() }
              : entry
          ),
        };
      });
    }
    if (taskLike.opportunityId) {
      setCrmOpportunityDrawerData((prev) => ({
        ...prev,
        tasks: (prev.tasks || []).map((entry) =>
          String(entry.taskId || entry.id) === String(taskId)
            ? { ...entry, status: CRM_TASK_STATUS_COMPLETED, completedAt: new Date() }
            : entry
        ),
      }));
    }
    const completedActivity = await logCrmActivity({
      relatedType: taskRelatedType,
      relatedId: taskRelatedId,
      projectId: taskProjectId,
      opportunityId: taskLike.opportunityId || "",
      quoteId: taskLike.quoteId || "",
      quoteNo: taskLike.quoteNo || "",
      customerId: taskLike.customerId || "",
      customerName: taskLike.customerName || "",
      activityType: "task_completed",
      title: "Task completed",
      description: taskLike.title || taskLike.notes || "",
    }).catch((error) => {
      if (isDevRuntime) {
        devWarn("CRM_TASK_COMPLETION_ACTIVITY_FAILED_EXACT", {
          taskId,
          relatedType: taskRelatedType,
          relatedId: taskRelatedId,
          projectId: taskProjectId,
          code: error?.code,
          message: error?.message,
        });
      }
      return null;
    });
    if (completedActivity && taskProjectId) {
      setCrmProjectDrawerData((prev) => {
        if (String(prev.projectId || "") !== taskProjectId) return prev;
        const nextEntry = {
          id: completedActivity.activityId,
          activityId: completedActivity.activityId,
          activityType: completedActivity.activityType,
          title: completedActivity.title,
          description: completedActivity.description,
          createdAt: new Date(),
          createdByName: completedActivity.createdByName || actor.name || "",
          quoteNo: completedActivity.quoteNo || "",
          quoteId: completedActivity.quoteId || "",
          projectId: completedActivity.projectId || taskProjectId,
          relatedType: completedActivity.relatedType,
          relatedId: completedActivity.relatedId,
          raw: completedActivity,
        };
        const existing = (prev.activity || []).some(
          (entry) => String(entry.activityId || entry.id) === String(nextEntry.activityId)
        );
        return {
          ...prev,
          activity: existing ? prev.activity : [nextEntry, ...(prev.activity || [])],
        };
      });
    }
    const isDailyTrackingTask =
      String(taskLike.recurringType || "") === "daily_tracking" ||
      String(taskLike.relatedType || "") === "project_tracking" ||
      String(taskLike.trackingTaskForProjectId || "") !== "";
    if (isDailyTrackingTask) {
      const trackingProjectId = String(
        taskLike.trackingTaskForProjectId || taskLike.projectId || taskProjectId || ""
      ).trim();
      if (trackingProjectId) {
        const trackingSnap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "project_tracking_items"),
            firestoreWhere("projectId", "==", trackingProjectId),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_TRACKING_ITEMS_READ_LIMIT)
          ),
          {
            label: "crmDailyTracking.openItemsAfterComplete",
            cacheKey: `projectTracking:${workspaceUid}:project:${trackingProjectId}:dailyCompletion:limit${CRM_PROJECT_TRACKING_ITEMS_READ_LIMIT}`,
            ttlMs: 0,
          }
        );
        const trackingItems = trackingSnap.docs.map((item) =>
          normalizeCrmMaterialRecord(item.id, item.data() || {})
        );
        const openItems = trackingItems.filter((item) => isTrackingItemOpen(item));
        const projectRecord =
          crmProjectRecords.find(
            (project) => String(project.projectId || project.id || "") === trackingProjectId
          ) || {
            projectId: trackingProjectId,
            customerId: taskLike.customerId || "",
            customerName: taskLike.customerName || "",
            quoteId: taskLike.quoteId || "",
            quoteNo: taskLike.quoteNo || "",
            projectOwnerUid: taskLike.ownerUid || "",
            projectOwnerName: taskLike.ownerName || "",
            deliveryTracking: {
              trackingOwnerUid: taskLike.ownerUid || "",
              trackingOwnerName: taskLike.ownerName || "",
              dailyCheckTime: taskLike.dailyCheckTime || "",
            },
          };
        const trackingSummary = buildProjectTrackingSummary(trackingItems);
        const completionPatch = {
          ...(projectRecord.deliveryTracking || {}),
          ...trackingSummary,
          lastCheckedAt: Timestamp.fromDate(new Date()),
          lastCheckedBy: actor.uid || "",
        };
        if (openItems.length) {
          const nextBaseDate = new Date();
          nextBaseDate.setDate(nextBaseDate.getDate() + 1);
          nextBaseDate.setHours(0, 0, 0, 0);
          await createDailyTrackingTaskForProject(
            {
              ...projectRecord,
              deliveryTracking: completionPatch,
            },
            {
              fromDate: nextBaseDate,
              action: "next_created",
            }
          );
        } else {
          const stoppedPatch = {
            ...completionPatch,
            enabled: false,
            trackingComplete: true,
            nextCheckTaskId: "",
            nextCheckDueAt: null,
          };
          await updateDoc(
            doc(db, "users", workspaceUid, "projects", trackingProjectId),
            sanitizeCrmFirestoreValue({
              deliveryTracking: stoppedPatch,
              updatedAt: serverTimestamp(),
              updatedBy: actor.uid || "",
            })
          );
          patchProjectDeliveryTrackingLocal(trackingProjectId, stoppedPatch);
          await logCrmActivity({
            relatedType: "project",
            relatedId: trackingProjectId,
            projectId: trackingProjectId,
            customerId: taskLike.customerId || "",
            customerName: taskLike.customerName || "",
            quoteId: taskLike.quoteId || "",
            quoteNo: taskLike.quoteNo || "",
            activityType: "tracking_all_received",
            title: "All materials received",
            description: "All tracking items for this project are received or closed.",
          }).catch(() => null);
          if (isDevRuntime) {
            devLog("CRM_DAILY_TRACKING_TASK_TRACE", {
              projectId: trackingProjectId,
              taskId,
              dueAt: taskLike.dueAt || null,
              action: "stopped_all_received",
            });
            devLog("CRM_TRACKING_SUMMARY_TRACE", {
              projectId: trackingProjectId,
              openItemCount: stoppedPatch.openItemCount || 0,
              receivedItemCount: stoppedPatch.receivedItemCount || 0,
              delayedItemCount: stoppedPatch.delayedItemCount || 0,
              totalItemCount: stoppedPatch.totalItemCount || 0,
              trackingComplete: stoppedPatch.trackingComplete === true,
            });
          }
        }
        await logCrmActivity({
          relatedType: "project",
          relatedId: trackingProjectId,
          projectId: trackingProjectId,
          customerId: taskLike.customerId || "",
          customerName: taskLike.customerName || "",
          quoteId: taskLike.quoteId || "",
          quoteNo: taskLike.quoteNo || "",
          activityType: "tracking_daily_check_completed",
          title: "Delivery tracking checked",
          description: "Daily tracking update completed.",
        }).catch(() => null);
      }
    }
    return patch;
  },
  [
    buildProjectTrackingSummary,
    createDailyTrackingTaskForProject,
    crmProjectRecords,
    getCrmActorSnapshot,
    isTrackingItemOpen,
    logCrmActivity,
    normalizeCrmMaterialRecord,
    patchCrmTaskLocalState,
    patchProjectDeliveryTrackingLocal,
    sanitizeCrmFirestoreValue,
    workspaceUid,
  ]
);

const getQuoteFollowUpFallbackDate = useCallback(
  (days = 3) => {
    const safeDays = Number.isFinite(Number(days))
      ? Math.min(90, Math.max(0, Number(days)))
      : 3;
    const date = new Date();
    date.setDate(date.getDate() + safeDays);
    return toDateInputValue(date);
  },
  [toDateInputValue]
);

const getQuoteFollowUpDefaultDate = useCallback(async () => {
  let days = 3;
  if (workspaceUid) {
    try {
      const settingsSnap = await loadOperationalSettingsSnapshot(functions, auth.currentUser?.uid, workspaceUid, 'crm');
      const crmSettings = settingsSnap.exists() ? settingsSnap.data() || {} : {};
      const configuredDays = Number(crmSettings.defaultQuoteFollowUpDays);
      if (Number.isFinite(configuredDays) && configuredDays >= 0 && configuredDays <= 90) {
        days = configuredDays;
      }
    } catch (error) {
      if (isDevRuntime) {
        devWarn("CRM quote follow-up settings load failed; using default", {
          code: error?.code,
          message: error?.message,
        });
      }
    }
  }
  return getQuoteFollowUpFallbackDate(days);
}, [getQuoteFollowUpFallbackDate, workspaceUid]);

const getCrmQuoteOwnerOptions = useCallback(() => {
  const options = new Map();
  const currentUserValue = user?.uid || auth.currentUser?.uid || "";
  if (currentUserValue) {
    options.set(currentUserValue, {
      value: currentUserValue,
      label: user?.displayName || user?.email || auth.currentUser?.email || "Me",
    });
  }
  getAssignableTeamMembers("").forEach((member) => {
    const value = member.uid || member.id || member.email || "";
    if (!value) return;
    options.set(value, {
      value,
      label: member.name || member.email || value,
    });
  });
  return Array.from(options.values()).sort((left, right) =>
    left.label.localeCompare(right.label)
  );
}, [getAssignableTeamMembers, user?.displayName, user?.email, user?.uid]);

const getCrmQuoteOwnerName = useCallback(
  (ownerUid = "") => {
    const owner = getCrmQuoteOwnerOptions().find(
      (option) => String(option.value || "") === String(ownerUid || "")
    );
    return owner?.label || user?.displayName || user?.email || auth.currentUser?.email || "Workspace User";
  },
  [getCrmQuoteOwnerOptions, user?.displayName, user?.email]
);


const buildCurrentQuoteSentContext = useCallback(
  (targetQuoteType = quoteSubTab) => {
    if (targetQuoteType === GENERAL_QUOTE_TYPE) {
      const livePayload = buildLiveGeneralQuotePayload({
        action: "quoteSentPrompt",
      });
      const quote = livePayload.quote || generalQuoteRef.current || generalQuote || {};
      const totals = livePayload.summaryTotals || calculateGeneralQuoteTotals(quote);
      const quoteId =
        generalEditingQuoteId ||
        quote.quoteId ||
        quote.id ||
        selectedQuote?.quoteId ||
        selectedQuote?.id ||
        "";
      const identity = resolveQuoteIdentityParts(quote, {
        customerName: quoteCustomerLink?.customerName || customerName || "",
        contactPersonName: quoteCustomerLink?.contactPersonName || "",
        siteName: quoteSiteSelection?.siteName || "",
      });
      const crm = quote.crm || selectedQuote?.crm || {};
      return {
        quoteType: GENERAL_QUOTE_TYPE,
        quoteId: String(quoteId || "").trim(),
        quoteIndexId: String(quote.quoteIndexId || selectedQuote?.quoteIndexId || quoteId || "").trim(),
        quoteNo: String(quote.quoteNo || quoteNo || selectedQuote?.quoteNo || "").trim(),
        customerId: String(quote.customerId || quoteCustomerLink?.customerId || selectedQuote?.customerId || "").trim(),
        customerName: identity.customerName || quote.customerName || customerName || "",
        customerPhone: quote.customerPhone || quote.quoteFor?.phone || quoteCustomerLink?.phone || "",
        siteName: identity.siteName || quote.siteName || quoteSiteSelection?.siteName || "",
        opportunityId: String(
          quote.opportunityId ||
            quote.crmOpportunityId ||
            crm.opportunityId ||
            crm.leadId ||
            quote.leadId ||
            quote.crmLeadId ||
            pendingCrmLeadId ||
            ""
        ).trim(),
        leadId: String(crm.leadId || quote.leadId || quote.crmLeadId || pendingCrmLeadId || "").trim(),
        totalAmount: Number(totals.grandTotal || quote.grandTotal || quote.finalTotal || 0),
        sourcePath:
          selectedQuote?.sourcePath ||
          (workspaceUid && quoteId ? `users/${workspaceUid}/quotations/${quoteId}` : ""),
        quote,
      };
    }

    const livePayload = buildLiveCurtainQuotePayload({
      action: "quoteSentPrompt",
    });
    const summaryTotals = livePayload.summaryTotals || curtainSummaryTotalsRef.current || {};
    const liveQuote = livePayload.selectedQuote || selectedQuote || {};
    const quoteId = editingQuoteId || liveQuote.quoteId || liveQuote.id || "";
    const crm = liveQuote.crm || {};
    return {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: String(quoteId || "").trim(),
      quoteIndexId: String(liveQuote.quoteIndexId || quoteId || "").trim(),
      quoteNo: String(livePayload.quoteNo || quoteNo || liveQuote.quoteNo || "").trim(),
      customerId: String(quoteCustomerLink?.customerId || liveQuote.customerId || "").trim(),
      customerName: String(livePayload.customerName || customerName || liveQuote.customerName || "").trim(),
      customerPhone: quoteCustomerLink?.phone || liveQuote.customerPhone || "",
      siteName: livePayload.quoteSiteSelection?.siteName || quoteSiteSelection?.siteName || liveQuote.siteName || "",
      opportunityId: String(
        liveQuote.opportunityId ||
          liveQuote.crmOpportunityId ||
          crm.opportunityId ||
          crm.leadId ||
          liveQuote.leadId ||
          liveQuote.crmLeadId ||
          livePayload.pendingCrmLeadId ||
          pendingCrmLeadId ||
          ""
      ).trim(),
      leadId: String(crm.leadId || liveQuote.leadId || liveQuote.crmLeadId || livePayload.pendingCrmLeadId || pendingCrmLeadId || "").trim(),
      totalAmount: Number(summaryTotals.grandTotal || liveQuote.grandTotal || liveQuote.finalTotal || 0),
      sourcePath:
        liveQuote.sourcePath ||
        (workspaceUid && quoteId ? `users/${workspaceUid}/quotations/${quoteId}` : ""),
      quote: {
        ...liveQuote,
        quoteNo: livePayload.quoteNo || quoteNo || liveQuote.quoteNo || "",
        quoteType: CURTAIN_QUOTE_TYPE,
        customerName: livePayload.customerName || customerName || liveQuote.customerName || "",
        customerId: quoteCustomerLink?.customerId || liveQuote.customerId || "",
        customerPhone: quoteCustomerLink?.phone || liveQuote.customerPhone || "",
        siteName: livePayload.quoteSiteSelection?.siteName || quoteSiteSelection?.siteName || liveQuote.siteName || "",
        grandTotal: Number(summaryTotals.grandTotal || liveQuote.grandTotal || liveQuote.finalTotal || 0),
        crm,
      },
    };
  },
  [
    buildLiveCurtainQuotePayload,
    buildLiveGeneralQuotePayload,
    calculateGeneralQuoteTotals,
    customerName,
    editingQuoteId,
    generalEditingQuoteId,
    generalQuote,
    pendingCrmLeadId,
    quoteCustomerLink,
    quoteNo,
    quoteSiteSelection,
    quoteSubTab,
    selectedQuote,
    workspaceUid,
  ]
);

const logQuoteActivitySafely = useCallback(
  async (activityInput = {}) => {
    try {
      const entry = await logCrmActivity(activityInput);
      if (isDevRuntime && entry) {
        devLog("CRM_ACTIVITY_LOG_TRACE", {
          activityType: entry.activityType,
          relatedType: entry.relatedType,
          relatedId: entry.relatedId,
          opportunityId: entry.opportunityId,
          quoteId: entry.quoteId,
        });
      }
      return entry;
    } catch (error) {
      if (isDevRuntime) {
        devWarn("CRM activity log skipped", {
          activityType: activityInput.activityType || "",
          quoteId: activityInput.quoteId || "",
          code: error?.code,
          message: error?.message,
        });
      }
      return null;
    }
  },
  [logCrmActivity]
);

const logQuoteSavedActivity = useCallback(
  async ({ activityType, quoteType, quoteId, quoteNo, customerId, customerName, opportunityId, totalAmount }) =>
    logQuoteActivitySafely({
      relatedType: "quote",
      relatedId: quoteId,
      opportunityId: opportunityId || "",
      quoteId,
      quoteNo,
      customerId: customerId || "",
      customerName: customerName || "",
      activityType,
      title: activityType === "quote_created" ? "Quotation created" : "Quotation updated",
      description: `${quoteType === GENERAL_QUOTE_TYPE ? "General" : "Curtain"} quotation ${
        quoteNo || quoteId
      } ${activityType === "quote_created" ? "was created" : "was updated"}${
        Number(totalAmount || 0) > 0 ? ` for ${currencySymbol(currency)} ${Number(totalAmount || 0).toFixed(2)}` : ""
      }.`,
    }),
  [currency, logQuoteActivitySafely]
);


const saveCrmFieldsForQuote = useCallback(
  async (quoteId, crmOverrides = {}, options = {}) => {
    if (!workspaceUid || !quoteId) {
      if (options.requireWrite === true) {
        throw new Error("CRM quote save failed: missing workspace or quote id.");
      }
      return null;
    }

    const sourceQuote = savedQuotes.find((item) => item.id === quoteId);
    if (!sourceQuote) {
      if (options.requireWrite === true) {
        throw new Error(`CRM quote save failed: quote ${quoteId} was not found.`);
      }
      return null;
    }

    const nextCrm = buildQuoteCrmData(sourceQuote, crmOverrides);
    const quoteRef = doc(db, "users", workspaceUid, "quotations", quoteId);
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      crmOverrides,
      "installationDate"
    );
    const updatePayload = {
      crm: nextCrm,
      updatedAt: serverTimestamp(),
    };

    if (hasInstallationDateOverride) {
      updatePayload.installationDate = crmOverrides.installationDate
        ? Timestamp.fromDate(crmOverrides.installationDate)
        : null;
    }

    await updateDoc(quoteRef, updatePayload);

    const localPatch = {
      crm: nextCrm,
      updatedAt: new Date(),
    };

    if (hasInstallationDateOverride) {
      localPatch.installationDate = crmOverrides.installationDate || null;
    }

    applyQuoteLocalPatch(quoteId, localPatch);

    if (crmOverrides.skipReminderSync !== true && options.skipReminderSync !== true) {
      await syncReminderDocsForDeal({
        ...sourceQuote,
        id: quoteId,
        crmRecordType: "quote",
        quoteTypeResolved: resolveQuoteType(sourceQuote),
        pipelineType: resolveQuoteType(sourceQuote),
        quoteNo: sourceQuote.quoteNo || "",
        customerLabel:
          sourceQuote.customerName ||
          nextCrm.contact?.name ||
          "",
        crmTitle: nextCrm.title,
        crmContact: nextCrm.contact,
        nextActionDate: nextCrm.nextActionDate,
        installationDate:
          Object.prototype.hasOwnProperty.call(localPatch, "installationDate")
            ? localPatch.installationDate
            : nextCrm.installationDate,
        reminderConfigs: nextCrm.reminderConfigs,
        followUps: nextCrm.followUps,
        attachments: nextCrm.attachments,
        siteLocations: nextCrm.siteLocations,
      });
    }

    if (options.skipOpportunitySync !== true) {
      await ensureOpportunityForQuote({
        ...sourceQuote,
        id: quoteId,
        crm: nextCrm,
        updatedAt: new Date(),
      }).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM quote saved, but opportunity sync failed", {
            quoteId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    }

    return {
      quoteId,
      firestorePath: `users/${workspaceUid}/quotations/${quoteId}`,
      updatePayload,
    };
  },
  [
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    ensureOpportunityForQuote,
    savedQuotes,
    syncReminderDocsForDeal,
    workspaceUid,
  ]
);

const upsertCustomerSearchIndexRecord = useCallback(
  async (record = {}, options = {}) => {
    if (!workspaceUid) return null;
    const write = buildCustomerSearchIndexWrite(record, {
      sourceType: options.sourceType || record.sourceType || "customer",
      workspaceUid,
    });
    if (!write?.personId) return null;

    await setDoc(
      doc(db, "users", workspaceUid, "customer_search_index", write.personId),
      write.payload,
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
    quotationCustomerSearchCacheRef.current.clear();
    return write.localRecord;
  },
  [workspaceUid]
);

const applyCrmLeadLocalPatch = useCallback((leadId, patch) => {
  if (!leadId) return;
  invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
  customerLedgerCrmDealsLoadRef.current = {};
  setCrmLeadRecords((prev) => {
    let found = false;
    const nextRecords = prev.map((item) => {
      if (item.id !== leadId) return item;
      found = true;
      return stripLegacyCrmQuoteLinkFields({
        ...item,
        ...patch,
        id: leadId,
      });
    });

    if (found) return sortCrmRecordsByLatestActivity(nextRecords);

    return sortCrmRecordsByLatestActivity([
      stripLegacyCrmQuoteLinkFields({
        id: leadId,
        ...patch,
      }),
      ...nextRecords,
    ]);
  });
}, [workspaceUid]);

const syncCrmLeadInstallationIndex = useCallback(
  async (leadId, leadLike = {}, options = {}) => {
    if (!workspaceUid || !leadId) return;
    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
    const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const indexId = String(primaryQuote?.quoteId || leadLike.quoteId || leadId).trim();
    if (!indexId) return;

    const installationDate =
      leadLike.installationDate ||
      leadLike.expectedInstallationDate ||
      leadLike.scheduledInstallationDate ||
      leadLike.installedAt ||
      leadLike.deliveryDate ||
      leadLike.crm?.installationDate ||
      null;
    const installationDateKey = toDateInputValue(installationDate);
    const status = String(leadLike.status || "").toLowerCase();
    const workStage = String(
      leadLike.workStage || leadLike.stageId || leadLike.stage || ""
    ).toLowerCase();
    const isInactive =
      leadLike.deleted === true ||
      ["cancelled", "canceled", "lost"].includes(status) ||
      ["cancelled", "canceled", "lost"].includes(workStage);
    const indexRef = doc(db, "users", workspaceUid, "installation_index", indexId);

    if (!installationDateKey || isInactive) {
      await deleteDoc(indexRef).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM installation index delete skipped", {
            leadId,
            indexId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
      return;
    }

    await setDoc(
      indexRef,
      {
        quoteId: primaryQuote?.quoteId || leadLike.quoteId || "",
        jobId: leadId,
        leadId,
        quoteNo: primaryQuote?.quoteNo || leadLike.quoteNo || "",
        customerName:
          leadLike.customerName ||
          leadLike.contact?.name ||
          leadLike.title ||
          leadLike.crmTitle ||
          "Unnamed Customer",
        normalizedPhone: normalizeSearchCompact(
          leadLike.customerPhone ||
            leadLike.contact?.phone ||
            leadLike.contact?.whatsapp ||
            ""
        ),
        installationDate,
        installationDateKey,
        monthKey: installationDateKey.slice(0, 7),
        status: leadLike.status || "",
        workStage: leadLike.workStage || leadLike.stageId || leadLike.stage || "",
        totalAmount: Number(leadLike.finalValue || leadLike.expectedValue || 0),
        itemSummary:
          leadLike.itemSummary ||
          leadLike.title ||
          leadLike.companyOrProject ||
          leadLike.crmTitle ||
          "CRM job",
        location:
          leadLike.location ||
          leadLike.site ||
          leadLike.siteLocation ||
          leadLike.locationAddress ||
          leadLike.contact?.address ||
          "",
        source: options.source || "crmLeadWrite",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
  },
  [toDateInputValue, workspaceUid]
);

const findLinkedCrmLeadForQuote = useCallback(
  (quote = {}, explicitLeadId = "") => {
    const quoteId = String(quote?.id || "");
    const quoteNoValue = String(quote?.quoteNo || "");
    const leadId =
      explicitLeadId ||
      quote?.crm?.leadId ||
      quote?.leadId ||
      "";

    const matchedLeads = crmLeadRecords.filter(
      (lead) =>
        (leadId && String(lead.id || "") === String(leadId)) ||
        doesCrmLeadReferenceQuote(lead, quoteId, quoteNoValue)
    );

    return chooseCanonicalQuoteDeal(matchedLeads, leadId) || null;
  },
  [crmLeadRecords]
);

const findLinkedQuoteForLead = useCallback(
  (lead = {}) => {
    const linkedQuotes = normalizeCrmLinkedQuotes(lead);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);

    return (
      savedQuotes.find(
        (quote) =>
          primaryLinkedQuote?.quoteId &&
          String(quote.id || "") === String(primaryLinkedQuote.quoteId || "")
      ) ||
      savedQuotes.find(
        (quote) =>
          primaryLinkedQuote?.quoteNo &&
          String(quote.quoteNo || "") === String(primaryLinkedQuote.quoteNo || "")
      ) ||
      savedQuotes.find((quote) =>
        linkedQuotes.some(
          (linkedQuote) =>
            linkedQuote.quoteId &&
            String(quote.id || "") === String(linkedQuote.quoteId || "")
        )
      ) ||
      savedQuotes.find((quote) =>
        linkedQuotes.some(
          (linkedQuote) =>
            linkedQuote.quoteNo &&
            String(quote.quoteNo || "") === String(linkedQuote.quoteNo || "")
        )
      ) ||
      null
    );
  },
  [savedQuotes]
);

const normalizeCrmProjectRecord = useCallback((docId, raw = {}) => {
  const expectedInstallationDate = normalizeCrmDate(
    raw.expectedInstallationDate || raw.installationDate || null
  );
  const nextActionDueAt = normalizeCrmDate(raw.nextActionDueAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  const lastActivityAt = normalizeCrmDate(
    raw.lastActivityAt || raw.updatedAt || raw.createdAt || null
  );
  const installationScheduledAt = normalizeCrmDate(
    raw.installationScheduledAt || raw.expectedInstallationDate || raw.installationDate || null
  );
  const installationCompletedAt = normalizeCrmDate(raw.installationCompletedAt || null);
  const productionDueAt = normalizeCrmDate(raw.productionDueAt || null);
  const materialDueAt = normalizeCrmDate(raw.materialDueAt || null);
  const orderValue = Number(raw.orderValue || 0) || 0;
  const advanceAmount = Number(raw.advanceAmount || 0) || 0;
  const balanceAmount =
    raw.balanceAmount == null
      ? calculateBalanceAmount(orderValue, advanceAmount)
      : Number(raw.balanceAmount || 0) || 0;
  const paymentSummary = calculateProjectPaymentSummary({
    project: {
      ...raw,
      orderValue,
      advanceAmount,
      balanceAmount,
      paymentStatus: raw.paymentStatus,
      paymentSummary: raw.paymentSummary,
      expectedInstallationDate,
    },
    payments: [],
  });
  const normalizedPaymentStatus = normalizePaymentStatus(
    paymentSummary.paymentStatus || raw.paymentStatus
  );
  const checklist = createDefaultProjectExecutionChecklist(raw.checklist || {});
  const checklistSummary = calculateProjectChecklistSummary(
    raw.checklistSummary?.totalItems ? raw.checklist || {} : checklist
  );
  const snagSummary = normalizeProjectSnagSummary(raw.snagSummary || {});
  const reviewSummary = normalizeProjectReviewSummary(raw.reviewSummary || {});
  const installationStatus = normalizeProjectInstallationStatus(
    raw.installationStatus ||
      (installationCompletedAt
        ? PROJECT_INSTALLATION_STATUS_COMPLETED
        : installationScheduledAt
        ? PROJECT_INSTALLATION_STATUS_SCHEDULED
        : PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED)
  );
  const normalizedStage = normalizeProjectStage(raw.stage);
  const resolvedPipelineStage = resolveProjectPipelineStage({
    ...raw,
    stage: raw.stage || normalizedStage,
    status: raw.status,
  });
  return {
    id: docId,
    projectId: String(raw.projectId || docId).trim(),
    workspaceUid: raw.workspaceUid || "",
    pipelineId: raw.pipelineId || "orders",
    projectStage: raw.projectStage || "",
    orderStage: raw.orderStage || "",
    resolvedProjectStage: resolvedPipelineStage,
    opportunityId: raw.opportunityId || "",
    quoteId: raw.quoteId || "",
    quoteNo: raw.quoteNo || "",
    quoteType: raw.quoteType || "",
    customerId: raw.customerId || "",
    customerName: raw.customerName || "Unnamed Customer",
    customerPhone: raw.customerPhone || "",
    contactPersonName: raw.contactPersonName || "",
    contactPersonPhone: raw.contactPersonPhone || "",
    siteId: raw.siteId || "",
    siteName: raw.siteName || "",
    siteAddress: raw.siteAddress || "",
    projectTitle: buildProjectTitle({ ...raw, projectId: docId }),
    orderValue,
    advanceAmount: Number(paymentSummary.advanceAmount || advanceAmount || 0) || 0,
    totalReceived: Number(paymentSummary.totalReceived || advanceAmount || 0) || 0,
    balanceAmount: Number(paymentSummary.balanceAmount ?? balanceAmount ?? 0) || 0,
    paymentStatus: normalizedPaymentStatus,
    paymentStatusLabel: paymentSummary.paymentOverdue
      ? getPaymentStatusLabel(PROJECT_PAYMENT_STATUS_OVERDUE)
      : getPaymentStatusLabel(normalizedPaymentStatus),
    paymentOverdue: paymentSummary.paymentOverdue === true,
    paymentSummary: {
      ...paymentSummary,
      nextPaymentDueDate: normalizeCrmDate(paymentSummary.nextPaymentDueDate || null),
      lastPaymentAt: normalizeCrmDate(paymentSummary.lastPaymentAt || null),
      paymentCompletedAt: normalizeCrmDate(paymentSummary.paymentCompletedAt || null),
    },
    stage: normalizedStage,
    status:
      raw.status ||
      (normalizedStage === PROJECT_STAGE_PROJECT_CLOSED
        ? "closed"
        : normalizedStage === PROJECT_STAGE_CANCELLED
        ? "cancelled"
        : "open"),
    stageLabel: getProjectStageLabel(normalizedStage),
    orderStageLabel: getOrderPipelineStageLabel(orderPipeline, resolvedPipelineStage),
    projectOwnerUid: raw.projectOwnerUid || "",
    projectOwnerName: raw.projectOwnerName || "Unassigned",
    salesOwnerUid: raw.salesOwnerUid || "",
    salesOwnerName: raw.salesOwnerName || "",
    deliveryTracking:
      raw.deliveryTracking && typeof raw.deliveryTracking === "object"
        ? {
            enabled: raw.deliveryTracking.enabled === true,
            trackingComplete: raw.deliveryTracking.trackingComplete === true,
            trackingOwnerUid: raw.deliveryTracking.trackingOwnerUid || "",
            trackingOwnerName: raw.deliveryTracking.trackingOwnerName || "",
            dailyCheckTime: raw.deliveryTracking.dailyCheckTime || "",
            timezone: raw.deliveryTracking.timezone || "Asia/Kolkata",
            openItemCount: Number(raw.deliveryTracking.openItemCount || 0) || 0,
            delayedItemCount: Number(raw.deliveryTracking.delayedItemCount || 0) || 0,
            receivedItemCount: Number(raw.deliveryTracking.receivedItemCount || 0) || 0,
            totalItemCount: Number(raw.deliveryTracking.totalItemCount || 0) || 0,
            lastCheckedAt: normalizeCrmDate(raw.deliveryTracking.lastCheckedAt || null),
            lastCheckedBy: raw.deliveryTracking.lastCheckedBy || "",
            nextCheckTaskId: raw.deliveryTracking.nextCheckTaskId || "",
            nextCheckDueAt: normalizeCrmDate(raw.deliveryTracking.nextCheckDueAt || null),
          }
        : null,
    expectedInstallationDate,
    expectedInstallationDateKey: toDateInputValue(expectedInstallationDate),
    expectedInstallationDateMissing: raw.expectedInstallationDateMissing === true || !expectedInstallationDate,
    installationScheduledAt,
    installationScheduledDateKey: toDateInputValue(installationScheduledAt),
    installationCompletedAt,
    installationCompletedDateKey: toDateInputValue(installationCompletedAt),
    installationStatus,
    installationStatusLabel: getProjectInstallationStatusLabel(installationStatus),
    productionDueAt,
    productionDueDateKey: toDateInputValue(productionDueAt),
    materialDueAt,
    materialDueDateKey: toDateInputValue(materialDueAt),
    handoverChecklist: createDefaultProjectHandoverChecklist(raw.handoverChecklist || {}),
    checklist,
    checklistSummary,
    snagSummary: {
      ...snagSummary,
      nextDueAt: normalizeCrmDate(snagSummary.nextDueAt || null),
      lastUpdatedAt: normalizeCrmDate(snagSummary.lastUpdatedAt || null),
    },
    hasOpenSnags: raw.hasOpenSnags === true || snagSummary.openCount > 0,
    openSnagCount: Number(raw.openSnagCount ?? snagSummary.openCount ?? 0) || 0,
    nextSnagDueAt: normalizeCrmDate(raw.nextSnagDueAt || snagSummary.nextDueAt || null),
    reviewSummary: {
      ...reviewSummary,
      requestedAt: normalizeCrmDate(reviewSummary.requestedAt || null),
      completedAt: normalizeCrmDate(reviewSummary.completedAt || null),
      skippedAt: normalizeCrmDate(reviewSummary.skippedAt || null),
    },
    reviewStatus: reviewSummary.status,
    reviewStatusLabel: getProjectReviewStatusLabel(reviewSummary.status),
    hasPendingBalance:
      raw.hasPendingBalance === true ||
      Number(paymentSummary.balanceAmount ?? balanceAmount ?? 0) > 0,
    lastActivityAt,
    lastActivityType: raw.lastActivityType || "",
    lastActivityLabel: raw.lastActivityLabel || "",
    handoverNotes: raw.handoverNotes || "",
    nextActionText: raw.nextActionText || "",
    nextActionDueAt,
    nextActionDueDateKey: toDateInputValue(nextActionDueAt),
    nextActionOwnerUid: raw.nextActionOwnerUid || "",
    nextActionOwnerName: raw.nextActionOwnerName || "",
    nextActionMissing:
      raw.nextActionMissing === true || !String(raw.nextActionText || "").trim(),
    createdAt,
    updatedAt,
    raw,
  };
}, [orderPipeline, toDateInputValue]);

const resolveOrderProjectOwner = useCallback(
  (opportunity = {}) => {
    const mode = crmSettings.defaultProjectOwnerMode || "current_user";
    const actor = getCrmActorSnapshot();
    if (mode === "lead_owner" && opportunity?.ownerUid) {
      return {
        uid: String(opportunity.ownerUid || "").trim(),
        name: String(opportunity.ownerName || getTeamMemberLabel(opportunity.ownerUid, "") || "").trim(),
      };
    }
    if (mode === "workspace_owner") {
      const ownerMember =
        teamMembers.find((member) => String(member.role || "").toLowerCase() === "owner") ||
        teamMembers[0] ||
        null;
      if (ownerMember?.id) {
        return {
          uid: ownerMember.id,
          name: ownerMember.name || ownerMember.email || "Workspace owner",
        };
      }
    }
    return {
      uid: actor.uid || user?.uid || "",
      name: actor.name || user?.displayName || user?.email || "Current user",
    };
  },
  [crmSettings.defaultProjectOwnerMode, getCrmActorSnapshot, getTeamMemberLabel, teamMembers, user?.displayName, user?.email, user?.uid]
);

const buildOrderHandoverDraft = useCallback(
  ({ source = "", opportunity = null, quoteIndex = null, quote = null } = {}) => {
    const safeOpportunity = opportunity?.raw || opportunity || {};
    const safeQuoteIndex = quoteIndex?.raw || quoteIndex || {};
    const safeQuote = quote || {};
    const linkedQuote =
      quoteIndex ||
      quote ||
      findLinkedQuoteForLead(opportunity || {}) ||
      getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(opportunity || {})) ||
      {};
    const owner = resolveOrderProjectOwner(safeOpportunity);
    const quoteIdentity = getSavedQuoteWorkflowIdentity(
      linkedQuote?.quoteId || linkedQuote?.id || linkedQuote?.quoteNo ? linkedQuote : safeQuoteIndex,
      workspaceUid
    );
    const quoteId = String(
      quoteIdentity.sourceQuoteId ||
        safeQuote.quoteId ||
        safeQuote.id ||
        safeQuoteIndex.quoteId ||
        safeQuoteIndex.sourceQuoteId ||
        linkedQuote.quoteId ||
        linkedQuote.id ||
        ""
    ).trim();
    const quoteNo = String(
      safeQuote.quoteNo ||
        safeQuoteIndex.quoteNo ||
        linkedQuote.quoteNo ||
        quoteId ||
        ""
    ).trim();
    const opportunityId = String(
      safeOpportunity.opportunityId ||
        safeOpportunity.id ||
        safeQuoteIndex.opportunityId ||
        linkedQuote.opportunityId ||
        ""
    ).trim();
    const orderValue = Number(
      safeQuoteIndex.orderValue ||
        safeQuoteIndex.totalAmount ||
        safeQuoteIndex.grandTotal ||
        safeQuoteIndex.finalTotal ||
        safeQuote.grandTotal ||
        safeQuote.finalTotal ||
        safeQuote.totalAmount ||
        safeOpportunity.orderValue ||
        safeOpportunity.latestQuoteAmount ||
        safeOpportunity.estimatedValue ||
        linkedQuote.value ||
        0
    ) || 0;
    const tomorrowKey = addDaysToDateKey(todayISO(), crmSettings.defaultHandoverDueDays || 1);
    const installDate =
      toDateInputValue(
        safeQuoteIndex.expectedInstallationDate ||
          safeQuoteIndex.installationDate ||
          safeQuote.installationDate ||
          safeOpportunity.expectedInstallationDate ||
          safeOpportunity.installationDate ||
          null
      ) || "";
    const customerName = String(
      safeQuoteIndex.customerName ||
        safeQuote.customerName ||
        safeOpportunity.customerName ||
        linkedQuote.customerName ||
        ""
    ).trim();
    const advanceAmount = Number(safeQuoteIndex.advanceAmount || safeQuote.advanceAmount || 0) || 0;
    const balanceAmount = calculateBalanceAmount(orderValue, advanceAmount);
    const paymentStatus =
      advanceAmount <= 0
        ? PROJECT_PAYMENT_STATUS_ADVANCE_PENDING
        : balanceAmount <= 0
        ? PROJECT_PAYMENT_STATUS_FULLY_PAID
        : PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED;
    const handoverChecklist = createDefaultProjectHandoverChecklist({
      quotationConfirmed: Boolean(quoteId || quoteNo),
      customerDetailsConfirmed: Boolean(customerName),
      siteDetailsConfirmed: Boolean(
        safeQuoteIndex.siteName ||
          safeQuoteIndex.siteAddress ||
          safeOpportunity.siteName ||
          safeOpportunity.siteAddress ||
          safeOpportunity.contact?.address ||
          safeOpportunity.address
      ),
      advanceReceived: advanceAmount > 0,
    });
    const projectIdBase = opportunityId || quoteId || quoteNo || createClientId("order");
    const projectId = String(
      safeOpportunity.projectId ||
        safeQuoteIndex.projectId ||
        safeQuote.projectId ||
        `project_${sanitizeTaskHistorySegment(projectIdBase)}`
    ).trim();
    const trackingOwner = owner;
    const quoteTypeForSuggestions = String(
      safeQuoteIndex.quoteType ||
        safeQuoteIndex.quotationType ||
        safeQuote.quoteType ||
        ""
    ).toLowerCase();
    const suggestedTrackingItems = quoteTypeForSuggestions.includes("curtain")
      ? [
          { itemType: "fabric", itemName: "Main fabric", unit: "meter" },
          { itemType: "lining", itemName: "Lining", unit: "meter" },
          { itemType: "track", itemName: "Track / channel", unit: "pcs" },
        ]
      : [];

    const draft = {
      source,
      projectId,
      opportunityId,
      quoteIndexId: quoteIdentity.quoteIndexId || quoteId,
      quoteId,
      quoteNo,
      quoteType: safeQuoteIndex.quoteType || safeQuoteIndex.quotationType || safeQuote.quoteType || "",
      customerId: safeQuoteIndex.customerId || safeQuote.customerId || safeOpportunity.customerId || "",
      customerName,
      customerPhone:
        safeQuoteIndex.customerPhone ||
        safeQuoteIndex.phone ||
        safeQuote.customerPhone ||
        safeOpportunity.customerPhone ||
        "",
      contactPersonName:
        safeQuoteIndex.contactPersonName ||
        safeQuote.contactPersonName ||
        safeOpportunity.contactPersonName ||
        "",
      contactPersonPhone:
        safeQuoteIndex.contactPersonPhone ||
        safeQuote.contactPersonPhone ||
        safeOpportunity.contactPersonPhone ||
        "",
      siteId: safeQuoteIndex.siteId || safeQuote.siteId || safeOpportunity.siteId || "",
      siteName:
        safeQuoteIndex.siteName ||
        safeQuoteIndex.projectName ||
        safeQuote.siteName ||
        safeOpportunity.siteName ||
        "",
      siteAddress:
        safeQuoteIndex.siteAddress ||
        safeQuote.siteAddress ||
        safeOpportunity.siteAddress ||
        safeOpportunity.contact?.address ||
        safeOpportunity.address ||
        "",
      orderValue: Number(orderValue || 0).toFixed(2),
      advanceAmount: Number(advanceAmount || 0).toFixed(2),
      balanceAmount: Number(balanceAmount || 0).toFixed(2),
      paymentStatus,
      projectOwnerUid: owner.uid || "",
      projectOwnerName: owner.name || "",
      salesOwnerUid: safeOpportunity.ownerUid || safeQuoteIndex.ownerUid || "",
      salesOwnerName: safeOpportunity.ownerName || safeQuoteIndex.ownerName || "",
      expectedInstallationDate: installDate,
      handoverChecklist,
      handoverNotes: "",
      nextActionText: "Complete order handover",
      nextActionDueAt: tomorrowKey,
      nextActionOwnerUid: owner.uid || "",
      nextActionOwnerName: owner.name || "",
      enableDeliveryTracking: crmSettings.enableDeliveryTracking !== false,
      trackingOwnerUid: trackingOwner.uid || "",
      trackingOwnerName: trackingOwner.name || "",
      dailyTrackingReminderTime:
        crmSettings.defaultTrackingReminderTime ||
        DEFAULT_CRM_SETTINGS.defaultTrackingReminderTime,
      trackingItems: suggestedTrackingItems.map((item) => ({
        clientId: createClientId("tracking-item"),
        itemType: item.itemType,
        itemName: item.itemName,
        supplierName: "",
        quantity: "",
        unit: item.unit || "meter",
        expectedArrivalDate: "",
        notes: "",
      })),
    };
    if (isDevRuntime) {
      devLog("CRM_ORDER_HANDOVER_MODAL_TRACE", {
        source,
        quoteId: draft.quoteId,
        quoteNo: draft.quoteNo,
        opportunityId: draft.opportunityId,
        customerName: draft.customerName,
        orderValue: Number(draft.orderValue || 0),
        advanceAmount: Number(draft.advanceAmount || 0),
      });
    }
    return draft;
  },
  [
    addDaysToDateKey,
    calculateBalanceAmount,
    crmSettings.defaultHandoverDueDays,
    crmSettings.defaultTrackingReminderTime,
    crmSettings.enableDeliveryTracking,
    createDefaultProjectHandoverChecklist,
    findLinkedQuoteForLead,
    resolveOrderProjectOwner,
    toDateInputValue,
    todayISO,
    workspaceUid,
  ]
);

const openOrderHandoverModal = useCallback(
  (context = {}) => {
    if (crmSettings.enableOrderHandover === false) {
      notify("Order handover is disabled in CRM settings.", "warning");
      return;
    }
    const draft = buildOrderHandoverDraft(context);
    setOrderHandoverModalState({
      open: true,
      source: context.source || "",
      opportunity: context.opportunity || null,
      quoteIndex: context.quoteIndex || null,
      quote: context.quote || null,
      draft,
      loading: false,
      error: "",
      warnings: [],
    });
  },
  [buildOrderHandoverDraft, crmSettings.enableOrderHandover, notify]
);

const updateOrderHandoverDraft = useCallback((patch = {}) => {
  setOrderHandoverModalState((prev) => {
    const currentDraft = prev.draft || {};
    const nextDraft = { ...currentDraft, ...patch };
    const orderValue = Number(nextDraft.orderValue || 0) || 0;
    const advanceAmount = Number(nextDraft.advanceAmount || 0) || 0;
    const balanceAmount = calculateBalanceAmount(orderValue, advanceAmount);
    return {
      ...prev,
      draft: {
        ...nextDraft,
        balanceAmount: Number(balanceAmount || 0).toFixed(2),
        paymentStatus:
          patch.paymentStatus != null
            ? patch.paymentStatus
            : advanceAmount <= 0
            ? PROJECT_PAYMENT_STATUS_ADVANCE_PENDING
            : balanceAmount <= 0
            ? PROJECT_PAYMENT_STATUS_FULLY_PAID
            : PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED,
      },
      error: "",
    };
  });
}, [calculateBalanceAmount]);

const createOrderHandoverTrackingDraftItem = useCallback(
  (overrides = {}) => ({
    clientId: overrides.clientId || createClientId("tracking-item"),
    itemType: normalizeTrackingItemType(overrides.itemType || "fabric"),
    itemName: overrides.itemName || "",
    supplierName: overrides.supplierName || "",
    quantity: overrides.quantity == null ? "" : String(overrides.quantity),
    unit: CRM_TRACKING_UNITS.includes(String(overrides.unit || "").trim())
      ? String(overrides.unit || "").trim()
      : "meter",
    expectedArrivalDate: overrides.expectedArrivalDate || "",
    notes: overrides.notes || "",
  }),
  []
);

const addOrderHandoverTrackingItem = useCallback(() => {
  setOrderHandoverModalState((prev) => {
    const draft = prev.draft || {};
    return {
      ...prev,
      draft: {
        ...draft,
        enableDeliveryTracking: true,
        trackingItems: [
          ...(Array.isArray(draft.trackingItems) ? draft.trackingItems : []),
          createOrderHandoverTrackingDraftItem({ itemName: "New tracking item" }),
        ],
      },
    };
  });
}, [createOrderHandoverTrackingDraftItem]);

const updateOrderHandoverTrackingItem = useCallback((clientId, patch = {}) => {
  setOrderHandoverModalState((prev) => {
    const draft = prev.draft || {};
    const nextItems = (Array.isArray(draft.trackingItems) ? draft.trackingItems : []).map((item) =>
      String(item.clientId || "") === String(clientId)
        ? { ...item, ...patch }
        : item
    );
    return {
      ...prev,
      draft: {
        ...draft,
        trackingItems: nextItems,
      },
    };
  });
}, []);

const removeOrderHandoverTrackingItem = useCallback((clientId) => {
  setOrderHandoverModalState((prev) => {
    const draft = prev.draft || {};
    return {
      ...prev,
      draft: {
        ...draft,
        trackingItems: (Array.isArray(draft.trackingItems) ? draft.trackingItems : []).filter(
          (item) => String(item.clientId || "") !== String(clientId)
        ),
      },
    };
  });
}, []);

const closeOrderHandoverModal = useCallback(() => {
  setOrderHandoverModalState((prev) => ({
    ...prev,
    open: false,
    loading: false,
    error: "",
  }));
}, []);

const patchCrmProjectLocalState = useCallback(
  (projectId, patch = {}) => {
    if (!projectId) return;
    invalidateWorkspaceCollectionCache(workspaceUid, "projects");
    const normalized = normalizeCrmProjectRecord(projectId, {
      ...patch,
      projectId,
      updatedAt: patch.updatedAt || new Date(),
    });
    setCrmProjectRecords((prev) => {
      let found = false;
      const next = prev.map((project) => {
        if (String(project.projectId || project.id) !== String(projectId)) return project;
        found = true;
        return normalizeCrmProjectRecord(projectId, {
          ...(project.raw || project),
          ...patch,
          projectId,
          updatedAt: patch.updatedAt || new Date(),
        });
      });
      return found ? next : [normalized, ...next];
    });
  },
  [normalizeCrmProjectRecord, workspaceUid]
);

const normalizeCrmPaymentRecord = useCallback((docId, raw = {}) => {
  const paymentDate = normalizeCrmDate(raw.paymentDate || raw.createdAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const paymentType = normalizePaymentType(raw.paymentType);
  const paymentMode = normalizePaymentMode(raw.paymentMode);
  const amount = Number(raw.amount || 0);
  return {
    id: docId,
    paymentId: String(raw.paymentId || docId).trim(),
    workspaceUid: raw.workspaceUid || "",
    projectId: raw.projectId || "",
    projectTitle: raw.projectTitle || "",
    quoteId: raw.quoteId || "",
    quoteNo: raw.quoteNo || "",
    opportunityId: raw.opportunityId || "",
    customerId: raw.customerId || "",
    customerName: raw.customerName || "Unnamed Customer",
    customerPhone: raw.customerPhone || "",
    siteName: raw.siteName || "",
    paymentType,
    paymentTypeLabel: getPaymentTypeLabel(paymentType),
    amount: Number.isFinite(amount) ? Math.max(0, Number(amount.toFixed(2))) : 0,
    paymentDate,
    paymentDateKey: toDateInputValue(paymentDate),
    paymentMode,
    paymentModeLabel: getPaymentModeLabel(paymentMode),
    referenceNo: raw.referenceNo || "",
    notes: raw.notes || "",
    collectedByUid: raw.collectedByUid || "",
    collectedByName: raw.collectedByName || "Unassigned",
    createdAt,
    createdBy: raw.createdBy || "",
    updatedAt,
    updatedBy: raw.updatedBy || "",
    raw,
  };
}, [toDateInputValue]);

const normalizeCrmProjectSnagRecord = useCallback((docId, raw = {}) => {
  const dueAt = normalizeCrmDate(raw.dueAt || null);
  const resolvedAt = normalizeCrmDate(raw.resolvedAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const status = String(raw.status || "open").trim() || "open";
  return {
    id: docId,
    snagId: String(raw.snagId || docId).trim(),
    workspaceUid: raw.workspaceUid || "",
    projectId: raw.projectId || "",
    title: String(raw.title || "Snag / correction").trim(),
    description: String(raw.description || "").trim(),
    priority: raw.priority === "urgent" ? "urgent" : "normal",
    status,
    dueAt,
    dueDateKey: toDateInputValue(dueAt),
    ownerUid: raw.ownerUid || "",
    ownerName: raw.ownerName || "Unassigned",
    resolvedAt,
    resolvedBy: raw.resolvedBy || "",
    resolutionNotes: raw.resolutionNotes || "",
    createdAt,
    updatedAt,
    raw,
  };
}, [toDateInputValue]);

const prependProjectActivityLocal = useCallback((projectId, activityEntry = {}) => {
  if (!projectId || !activityEntry?.activityId) return;
  setCrmProjectDrawerData((prev) => {
    if (String(prev.projectId || "") !== String(projectId)) return prev;
    const nextEntry = {
      id: activityEntry.activityId,
      activityId: activityEntry.activityId,
      activityType: activityEntry.activityType || "note",
      title: activityEntry.title || "CRM activity",
      description: activityEntry.description || "",
      createdAt: new Date(),
      createdByName: activityEntry.createdByName || "",
      quoteNo: activityEntry.quoteNo || "",
      quoteId: activityEntry.quoteId || "",
      projectId: activityEntry.projectId || projectId,
      relatedType: activityEntry.relatedType || "project",
      relatedId: activityEntry.relatedId || projectId,
      raw: activityEntry,
    };
    const exists = (prev.activity || []).some(
      (entry) => String(entry.activityId || entry.id) === String(nextEntry.activityId)
    );
    return {
      ...prev,
      activity: exists ? prev.activity : [nextEntry, ...(prev.activity || [])],
    };
  });
}, []);

const resolvePaymentOwnerForProject = useCallback(
  (projectLike = {}, ownerMode = crmSettings.defaultPaymentOwnerMode || "project_owner") => {
    const actor = getCrmActorSnapshot();
    if (ownerMode === "project_owner" && projectLike.projectOwnerUid) {
      return {
        uid: String(projectLike.projectOwnerUid || "").trim(),
        name: String(projectLike.projectOwnerName || getTeamMemberLabel(projectLike.projectOwnerUid, "") || "").trim(),
      };
    }
    if (ownerMode === "workspace_owner") {
      const ownerMember =
        teamMembers.find((member) => String(member.role || "").toLowerCase() === "owner") ||
        teamMembers[0] ||
        null;
      if (ownerMember?.id) {
        return {
          uid: ownerMember.id,
          name: ownerMember.name || ownerMember.email || "Workspace owner",
        };
      }
    }
    return {
      uid: actor.uid || user?.uid || "",
      name: actor.name || user?.displayName || user?.email || "Current user",
    };
  },
  [
    crmSettings.defaultPaymentOwnerMode,
    getCrmActorSnapshot,
    getTeamMemberLabel,
    teamMembers,
    user?.displayName,
    user?.email,
    user?.uid,
  ]
);

const createPaymentFollowUpDraft = useCallback(
  (projectLike = {}, overrides = {}) => {
    const summary = projectLike.paymentSummary || {};
    const owner = resolvePaymentOwnerForProject(projectLike, overrides.ownerMode);
    const dueBase =
      toDateInputValue(overrides.dueDate) ||
      toDateInputValue(summary.nextPaymentDueDate) ||
      toDateInputValue(projectLike.expectedInstallationDate) ||
      addDaysToDateKey(todayISO(), crmSettings.defaultPaymentFollowUpDays || 7);
    return {
      dueAmount:
        overrides.dueAmount != null
          ? String(overrides.dueAmount)
          : String(summary.balanceAmount ?? projectLike.balanceAmount ?? ""),
      dueDate: dueBase || todayISO(),
      ownerUid: overrides.ownerUid || owner.uid || "",
      ownerName: overrides.ownerName || owner.name || "",
      note:
        overrides.note ||
        `Balance pending: ${formatCurrencyDisplay(
          summary.balanceAmount ?? projectLike.balanceAmount ?? 0,
          currency
        )}`,
    };
  },
  [
    addDaysToDateKey,
    crmSettings.defaultPaymentFollowUpDays,
    currency,
    resolvePaymentOwnerForProject,
    toDateInputValue,
  ]
);

const patchCrmPaymentDrawerState = useCallback(
  (projectId, paymentId, paymentPatch = {}) => {
    if (!projectId || !paymentId) return;
    invalidateWorkspaceCollectionCache(workspaceUid, "project_payments");
    const normalized = normalizeCrmPaymentRecord(paymentId, {
      ...paymentPatch,
      paymentId,
      projectId,
      updatedAt: paymentPatch.updatedAt || new Date(),
    });
    setCrmProjectDrawerData((prev) => {
      if (String(prev.projectId || "") !== String(projectId)) return prev;
      let found = false;
      const nextPayments = (prev.payments || []).map((payment) => {
        if (String(payment.paymentId || payment.id) !== String(paymentId)) return payment;
        found = true;
        return normalizeCrmPaymentRecord(paymentId, {
          ...(payment.raw || payment),
          ...paymentPatch,
          paymentId,
          projectId,
          updatedAt: paymentPatch.updatedAt || new Date(),
        });
      });
      const merged = found ? nextPayments : [normalized, ...nextPayments];
      return {
        ...prev,
        payments: merged.sort(
          (left, right) =>
            safeTimeMillis(right.paymentDate || right.createdAt) -
            safeTimeMillis(left.paymentDate || left.createdAt)
        ),
      };
    });
  },
  [normalizeCrmPaymentRecord, workspaceUid]
);

const createPaymentFollowUpTask = useCallback(
  async (projectLike = {}, followUpInput = {}) => {
    const projectId = String(projectLike.projectId || projectLike.id || followUpInput.projectId || "").trim();
    if (!workspaceUid || !projectId || crmSettings.enablePaymentTracking === false) return null;
    const draft = {
      ...createPaymentFollowUpDraft(projectLike, followUpInput),
      ...followUpInput,
    };
    const dueDate =
      parseDateInputValue(draft.dueDate) ||
      parseDateInputValue(addDaysToDateKey(todayISO(), crmSettings.defaultPaymentFollowUpDays || 7)) ||
      new Date();
    const dueAmount = Number(draft.dueAmount || 0) || 0;
    if (dueAmount <= 0) return null;
    const taskId = `project_${sanitizeTaskHistorySegment(projectId)}__payment_follow_up`;
    const taskPayload = await createCrmTask({
      taskId,
      relatedType: "project_payment",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      opportunityId: projectLike.opportunityId || "",
      taskType: "payment",
      title: "Collect balance payment",
      notes: draft.note || "",
      ownerUid: draft.ownerUid || projectLike.projectOwnerUid || "",
      ownerName: draft.ownerName || projectLike.projectOwnerName || "",
      dueAt: Timestamp.fromDate(dueDate),
      status: CRM_TASK_STATUS_OPEN,
      priority: "high",
      systemGenerated: true,
      paymentFollowUpForProjectId: projectId,
      dueAmount,
      sourceKind: "payment_follow_up",
    });
    const paymentSummaryPatch = sanitizeCrmFirestoreValue({
      ...(projectLike.paymentSummary || {}),
      nextPaymentDueAmount: dueAmount,
      nextPaymentDueDate: Timestamp.fromDate(dueDate),
      paymentFollowUpOwnerUid: draft.ownerUid || "",
      paymentFollowUpOwnerName: draft.ownerName || "",
    });
    await updateDoc(
      doc(db, "users", workspaceUid, "projects", projectId),
      sanitizeCrmFirestoreValue({
        paymentSummary: paymentSummaryPatch,
        updatedAt: serverTimestamp(),
        updatedBy: getCrmActorSnapshot().uid || "",
      })
    ).catch(() => null);
    patchCrmProjectLocalState(projectId, { paymentSummary: paymentSummaryPatch });
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      activityType: "payment_follow_up_created",
      title: "Payment follow-up created",
      description: `Collect ${formatCurrencyDisplay(dueAmount, currency)} balance payment.`,
    }).catch(() => null);
    if (activity) prependProjectActivityLocal(projectId, activity);
    if (isDevRuntime) {
      devLog("CRM_PAYMENT_FOLLOWUP_TRACE", {
        projectId,
        taskId,
        dueAmount,
        dueAt: dueDate,
        action: "upsert",
      });
    }
    return taskPayload;
  },
  [
    addDaysToDateKey,
    createCrmTask,
    createPaymentFollowUpDraft,
    crmSettings.defaultPaymentFollowUpDays,
    crmSettings.enablePaymentTracking,
    currency,
    getCrmActorSnapshot,
    logCrmActivity,
    parseDateInputValue,
    patchCrmProjectLocalState,
    prependProjectActivityLocal,
    workspaceUid,
  ]
);

const completePaymentFollowUpTaskForProject = useCallback(
  async (projectId, projectLike = {}) => {
    const cleanProjectId = String(projectId || projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !cleanProjectId) return false;
    const taskId = `project_${sanitizeTaskHistorySegment(cleanProjectId)}__payment_follow_up`;
    const actor = getCrmActorSnapshot();
    const patch = sanitizeCrmFirestoreValue({
      status: CRM_TASK_STATUS_COMPLETED,
      completedAt: serverTimestamp(),
      completedBy: actor.uid || "",
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    });
    try {
      await updateDoc(doc(db, "users", workspaceUid, "crm_tasks", taskId), patch);
      patchCrmTaskLocalState(taskId, {
        ...patch,
        completedAt: new Date(),
        updatedAt: new Date(),
      });
      const activity = await logCrmActivity({
        relatedType: "project",
        relatedId: cleanProjectId,
        projectId: cleanProjectId,
        customerId: projectLike.customerId || "",
        customerName: projectLike.customerName || "",
        quoteId: projectLike.quoteId || "",
        quoteNo: projectLike.quoteNo || "",
        activityType: "payment_follow_up_completed",
        title: "Payment follow-up completed",
        description: "Balance payment follow-up was completed after full payment.",
      }).catch(() => null);
      if (activity) prependProjectActivityLocal(cleanProjectId, activity);
      return true;
    } catch (error) {
      if (String(error?.code || "") !== "not-found" && isDevRuntime) {
        devWarn("CRM_PAYMENT_FOLLOWUP_COMPLETE_FAILED_EXACT", {
          projectId: cleanProjectId,
          taskId,
          code: error?.code || "",
          message: error?.message || String(error),
        });
      }
      return false;
    }
  },
  [
    getCrmActorSnapshot,
    logCrmActivity,
    patchCrmTaskLocalState,
    prependProjectActivityLocal,
    workspaceUid,
  ]
);

const openCrmPaymentModal = useCallback(
  (projectLike = {}, options = {}) => {
    if (!canAddCrmPaymentAccess) {
      notify("You do not have permission to update payments.", "warning");
      return;
    }
    const projectId = String(projectLike?.projectId || projectLike?.id || options.projectId || "").trim();
    if (!projectId) return;
    const actor = getCrmActorSnapshot();
    const summary = projectLike.paymentSummary || {};
    const paymentId = String(options.paymentId || "").trim();
    const suggestedAmount =
      options.amount != null
        ? options.amount
        : summary.balanceAmount ?? projectLike.balanceAmount ?? "";
    setCrmPaymentModalState({
      open: true,
      projectId,
      paymentId,
      followUpTask: options.followUpTask || null,
      completeFollowUpAfterSave: options.completeFollowUpAfterSave === true,
      draft: {
        paymentType: options.paymentType || (Number(summary.totalReceived || 0) > 0 ? "part_payment" : "advance"),
        amount: suggestedAmount === "" ? "" : String(suggestedAmount),
        paymentDate: options.paymentDate || todayISO(),
        paymentMode: options.paymentMode || "cash",
        referenceNo: "",
        collectedByUid: options.collectedByUid || actor.uid || "",
        collectedByName: options.collectedByName || actor.name || "",
        notes: options.notes || "",
      },
      loading: false,
      error: "",
    });
  },
  [canAddCrmPaymentAccess, getCrmActorSnapshot, notify, todayISO]
);

const closeCrmPaymentModal = useCallback(() => {
  setCrmPaymentModalState({
    open: false,
    projectId: "",
    paymentId: "",
    draft: null,
    followUpTask: null,
    completeFollowUpAfterSave: false,
    loading: false,
    error: "",
  });
}, []);

const updateCrmPaymentDraft = useCallback((patch = {}) => {
  setCrmPaymentModalState((prev) => ({
    ...prev,
    draft: {
      ...(prev.draft || {}),
      ...patch,
    },
    error: "",
  }));
}, []);

const openPaymentFollowUpModal = useCallback(
  (projectLike = {}) => {
    if (!canAddCrmPaymentAccess) {
      notify("You do not have permission to update payments.", "warning");
      return;
    }
    const projectId = String(projectLike?.projectId || projectLike?.id || "").trim();
    if (!projectId) return;
    setCrmPaymentFollowUpModalState({
      open: true,
      projectId,
      draft: createPaymentFollowUpDraft(projectLike),
      loading: false,
      error: "",
    });
  },
  [canAddCrmPaymentAccess, createPaymentFollowUpDraft, notify]
);

const closePaymentFollowUpModal = useCallback(() => {
  setCrmPaymentFollowUpModalState({
    open: false,
    projectId: "",
    draft: null,
    loading: false,
    error: "",
  });
}, []);

const updatePaymentFollowUpDraft = useCallback((patch = {}) => {
  setCrmPaymentFollowUpModalState((prev) => ({
    ...prev,
    draft: {
      ...(prev.draft || {}),
      ...patch,
    },
    error: "",
  }));
}, []);

const saveCrmPayment = useCallback(async () => {
  if (!canAddCrmPaymentAccess) {
    setCrmPaymentModalState((prev) => ({
      ...prev,
      loading: false,
      error: "You do not have permission to update payments.",
    }));
    notify("You do not have permission to update payments.", "warning");
    return null;
  }
  const draft = crmPaymentModalState.draft || {};
  const projectId = String(crmPaymentModalState.projectId || draft.projectId || "").trim();
  const selectedProject =
    crmProjectRecords.find(
      (project) =>
        String(project.projectId || "") === projectId ||
        String(project.id || "") === projectId
    ) || null;
  if (!workspaceUid || !selectedProject || !projectId || crmPaymentModalState.loading) {
    return null;
  }
  const amount = Number(draft.amount || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    setCrmPaymentModalState((prev) => ({ ...prev, error: "Enter a valid payment amount." }));
    notify("Enter a valid payment amount.", "warning");
    return null;
  }
  const paymentDate = parseDateInputValue(draft.paymentDate) || new Date();
  const actor = getCrmActorSnapshot();
  const paymentId =
    String(crmPaymentModalState.paymentId || "").trim() ||
    createClientId("project-payment");
  setCrmPaymentModalState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    const payload = sanitizeCrmFirestoreValue({
      paymentId,
      workspaceUid,
      projectId,
      projectTitle: selectedProject.projectTitle || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      opportunityId: selectedProject.opportunityId || "",
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      customerPhone: selectedProject.customerPhone || "",
      siteName: selectedProject.siteName || "",
      paymentType: normalizePaymentType(draft.paymentType),
      amount: Number(amount.toFixed(2)),
      paymentDate: Timestamp.fromDate(paymentDate),
      paymentMode: normalizePaymentMode(draft.paymentMode),
      referenceNo: String(draft.referenceNo || "").trim(),
      notes: String(draft.notes || "").trim(),
      collectedByUid: String(draft.collectedByUid || actor.uid || "").trim(),
      collectedByName: String(draft.collectedByName || actor.name || "").trim(),
      createdAt: serverTimestamp(),
      createdBy: actor.uid || "",
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    });
    await setDoc(
      doc(db, "users", workspaceUid, "project_payments", paymentId),
      payload,
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "project_payments");
    const normalizedPayment = normalizeCrmPaymentRecord(paymentId, {
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date(),
      paymentDate,
    });
    const paymentSummarySnap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "project_payments"),
        firestoreWhere("projectId", "==", projectId),
        firestoreOrderBy("paymentDate", "desc"),
        firestoreLimit(CRM_PROJECT_PAYMENTS_READ_LIMIT)
      ),
      {
        label: "crmPaymentSave.projectPaymentsForSummary",
        cacheKey: `projectPayments:${workspaceUid}:project:${projectId}:paymentDateDesc:save:${paymentId}`,
        ttlMs: 0,
      }
    );
    const currentPayments = paymentSummarySnap.docs.map((item) =>
      normalizeCrmPaymentRecord(item.id, item.data() || {})
    );
    const nextPayments = [
      normalizedPayment,
      ...currentPayments.filter(
        (payment) => String(payment.paymentId || payment.id) !== String(paymentId)
      ),
    ];
    const paymentSummary = calculateProjectPaymentSummary({
      project: selectedProject,
      payments: nextPayments,
    });
    const projectPatch = sanitizeCrmFirestoreValue({
      paymentSummary,
      totalReceived: paymentSummary.totalReceived,
      advanceAmount: paymentSummary.advanceAmount,
      balanceAmount: paymentSummary.balanceAmount,
      paymentStatus: paymentSummary.paymentStatus,
      hasPendingBalance: paymentSummary.balanceAmount > 0,
      checklist:
        paymentSummary.balanceAmount <= 0
          ? createDefaultProjectExecutionChecklist({
              ...(selectedProject.checklist || {}),
              finalPaymentReceived: true,
            })
          : selectedProject.checklist || {},
      checklistSummary:
        paymentSummary.balanceAmount <= 0
          ? calculateProjectChecklistSummary(
              createDefaultProjectExecutionChecklist({
                ...(selectedProject.checklist || {}),
                finalPaymentReceived: true,
              })
            )
          : selectedProject.checklistSummary || {},
      stage:
        paymentSummary.balanceAmount > 0 &&
        selectedProject.installationStatus === PROJECT_INSTALLATION_STATUS_COMPLETED &&
        !selectedProject.hasOpenSnags
          ? PROJECT_STAGE_FINAL_PAYMENT_PENDING
          : paymentSummary.balanceAmount <= 0 &&
            selectedProject.stage === PROJECT_STAGE_FINAL_PAYMENT_PENDING
          ? PROJECT_STAGE_INSTALLATION_COMPLETED
          : selectedProject.stage,
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    });
    await updateDoc(doc(db, "users", workspaceUid, "projects", projectId), projectPatch);
    invalidateWorkspaceCollectionCache(workspaceUid, "projects");
    if (selectedProject.quoteId) {
      await setDoc(
        doc(db, "users", workspaceUid, "quote_index", selectedProject.quoteId),
        sanitizeCrmFirestoreValue({
          projectId,
          paymentStatus: paymentSummary.paymentStatus,
          totalReceived: paymentSummary.totalReceived,
          balanceAmount: paymentSummary.balanceAmount,
          lastPaymentAt: payload.paymentDate,
          updatedAt: serverTimestamp(),
          updatedBy: actor.uid || "",
        }),
        { merge: true }
      ).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM_PAYMENT_QUOTE_INDEX_PATCH_FAILED_EXACT", {
            projectId,
            quoteId: selectedProject.quoteId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    }
    patchCrmPaymentDrawerState(projectId, paymentId, normalizedPayment);
    patchCrmProjectLocalState(projectId, {
      ...projectPatch,
      updatedAt: new Date(),
    });
    invalidateFirestoreReadCacheByPrefix(`customerLedger:projects:${workspaceUid}:`);
    if (selectedProject.customerId) {
      setCustomerLedgerProjectsByCustomer((prev) => {
        const existingProjects = prev[selectedProject.customerId];
        if (!Array.isArray(existingProjects)) return prev;
        return {
          ...prev,
          [selectedProject.customerId]: existingProjects.map((project) =>
            String(project.projectId || project.id || "") === projectId
              ? normalizeCrmProjectRecord(projectId, {
                  ...(project.raw || project),
                  ...projectPatch,
                  projectId,
                  updatedAt: new Date(),
                })
              : project
          ),
        };
      });
    }
    if (String(projectPatch.stage || "") !== String(selectedProject.stage || "")) {
      const stageActivity = await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        paymentId,
        customerId: selectedProject.customerId || "",
        customerName: selectedProject.customerName || "",
        quoteId: selectedProject.quoteId || "",
        quoteNo: selectedProject.quoteNo || "",
        activityType: "project_stage_changed",
        title: "Project stage changed",
        description: `${getProjectStageLabel(selectedProject.stage) || "Previous stage"} -> ${getProjectStageLabel(projectPatch.stage)}`,
      }).catch(() => null);
      if (stageActivity) prependProjectActivityLocal(projectId, stageActivity);
    }
    const paymentTypeLabel = getPaymentTypeLabel(payload.paymentType).toLowerCase();
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      paymentId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      activityType: "payment_recorded",
      title: "Payment recorded",
      description: `${formatCurrencyDisplay(payload.amount, currency)} ${paymentTypeLabel} payment recorded.`,
    }).catch(() => null);
    if (activity) prependProjectActivityLocal(projectId, activity);
    if (paymentSummary.paymentStatus === PROJECT_PAYMENT_STATUS_FULLY_PAID) {
      const fullyPaidActivity = await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        paymentId,
        customerId: selectedProject.customerId || "",
        customerName: selectedProject.customerName || "",
        quoteId: selectedProject.quoteId || "",
        quoteNo: selectedProject.quoteNo || "",
        activityType: "payment_completed",
        title: "Payment completed",
        description: "Full payment has been received for this order.",
      }).catch(() => null);
      if (fullyPaidActivity) prependProjectActivityLocal(projectId, fullyPaidActivity);
      if (
        !(
          crmPaymentModalState.completeFollowUpAfterSave &&
          crmPaymentModalState.followUpTask
        )
      ) {
        await completePaymentFollowUpTaskForProject(projectId, {
          ...selectedProject,
          paymentSummary,
        });
      }
    }
    if (
      crmPaymentModalState.completeFollowUpAfterSave &&
      crmPaymentModalState.followUpTask
    ) {
      const followUpTask = crmPaymentModalState.followUpTask;
      const followUpTaskId = followUpTask.taskId || followUpTask.id || "";
      if (followUpTaskId) {
        await completeCrmTask(followUpTaskId, followUpTask).catch(() => null);
      }
    }
    if (
      paymentSummary.balanceAmount > 0 &&
      crmSettings.enablePaymentTracking !== false &&
      typeof window !== "undefined" &&
      window.confirm("Balance still pending. Create the next payment follow-up?")
    ) {
      await createPaymentFollowUpTask(
        {
          ...selectedProject,
          paymentSummary,
        },
        {
          dueAmount: paymentSummary.balanceAmount,
        }
      );
    }
    if (isDevRuntime) {
      devLog("CRM_PAYMENT_SAVE_TRACE", {
        projectId,
        paymentId,
        amount: payload.amount,
        paymentType: payload.paymentType,
        paymentMode: payload.paymentMode,
      });
      devLog("CRM_PAYMENT_SUMMARY_TRACE", {
        projectId,
        orderValue: paymentSummary.orderValue,
        totalReceived: paymentSummary.totalReceived,
        balanceAmount: paymentSummary.balanceAmount,
        paymentStatus: paymentSummary.paymentStatus,
      });
    }
    closeCrmPaymentModal();
    notify("Payment saved.", "success");
    return payload;
  } catch (error) {
    devError("CRM_PAYMENT_SAVE_FAILED_EXACT", {
      code: error?.code || "",
      message: error?.message || String(error),
      projectId,
      paymentId,
    });
    setCrmPaymentModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Payment could not be saved. Please try again.",
    }));
    notify("Payment could not be saved. Please try again.", "error");
    return null;
  }
}, [
  calculateProjectPaymentSummary,
  canAddCrmPaymentAccess,
  closeCrmPaymentModal,
  completePaymentFollowUpTaskForProject,
  completeCrmTask,
  createPaymentFollowUpTask,
  crmPaymentModalState,
  crmProjectRecords,
  crmSettings.enablePaymentTracking,
  currency,
  getCrmActorSnapshot,
  logCrmActivity,
  normalizeCrmPaymentRecord,
  normalizeCrmProjectRecord,
  notify,
  parseDateInputValue,
  patchCrmPaymentDrawerState,
  patchCrmProjectLocalState,
  prependProjectActivityLocal,
  workspaceUid,
]);

const savePaymentFollowUp = useCallback(async () => {
  if (!canAddCrmPaymentAccess) {
    setCrmPaymentFollowUpModalState((prev) => ({
      ...prev,
      loading: false,
      error: "You do not have permission to update payments.",
    }));
    notify("You do not have permission to update payments.", "warning");
    return null;
  }
  const projectId = String(crmPaymentFollowUpModalState.projectId || "").trim();
  const selectedProject =
    crmProjectRecords.find(
      (project) => String(project.projectId || project.id || "") === projectId
    ) || null;
  if (!workspaceUid || !selectedProject || !projectId || crmPaymentFollowUpModalState.loading) {
    return null;
  }
  setCrmPaymentFollowUpModalState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    await createPaymentFollowUpTask(selectedProject, crmPaymentFollowUpModalState.draft || {});
    closePaymentFollowUpModal();
    notify("Payment follow-up created.", "success");
    return true;
  } catch (error) {
    devError("CRM_PAYMENT_FOLLOWUP_FAILED_EXACT", {
      code: error?.code || "",
      message: error?.message || String(error),
      projectId,
    });
    setCrmPaymentFollowUpModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Payment follow-up could not be created. Please try again.",
    }));
    notify("Payment follow-up could not be created. Please try again.", "error");
    return null;
  }
}, [
  canAddCrmPaymentAccess,
  closePaymentFollowUpModal,
  createPaymentFollowUpTask,
  crmPaymentFollowUpModalState,
  crmProjectRecords,
  notify,
  workspaceUid,
]);

const submitOrderHandover = useCallback(async () => {
  const draft = orderHandoverModalState.draft || {};
  if (!workspaceUid || orderHandoverModalState.loading) return;
  const actor = getCrmActorSnapshot();
  const expectedInstallationDate =
    parseDateInputValue(draft.expectedInstallationDate) || null;
  const nextActionDueAt = parseDateInputValue(draft.nextActionDueAt) || null;
  const projectInput = {
    ...draft,
    workspaceUid,
    orderValue: Number(draft.orderValue || 0) || 0,
    advanceAmount: Number(draft.advanceAmount || 0) || 0,
    expectedInstallationDate: expectedInstallationDate
      ? Timestamp.fromDate(expectedInstallationDate)
      : null,
    nextActionDueAt: nextActionDueAt ? Timestamp.fromDate(nextActionDueAt) : null,
    createdBy: actor.uid,
    updatedBy: actor.uid,
  };
  const validation = validateProjectHandoverInput(projectInput);
  if (!validation.isValid) {
    setOrderHandoverModalState((prev) => ({
      ...prev,
      error: validation.errors.join(" "),
      warnings: validation.warnings,
    }));
    return;
  }

  setOrderHandoverModalState((prev) => ({
    ...prev,
    loading: true,
    error: "",
    warnings: validation.warnings,
  }));

  const projectWriteTimestamp = Timestamp.fromDate(new Date());
  let projectPayload = buildProjectFromOpportunityAndQuote({
    opportunity: orderHandoverModalState.opportunity,
    quoteIndex: orderHandoverModalState.quoteIndex,
    quote: orderHandoverModalState.quote,
    input: {
      ...projectInput,
      createdAt: projectWriteTimestamp,
      updatedAt: projectWriteTimestamp,
    },
  });
  const existingProject = crmProjectRecords.find((project) => {
    const quoteMatches =
      projectPayload.quoteId &&
      String(project.quoteId || "") === String(projectPayload.quoteId || "");
    const quoteNoMatches =
      projectPayload.quoteNo &&
      String(project.quoteNo || "") === String(projectPayload.quoteNo || "");
    const opportunityMatches =
      projectPayload.opportunityId &&
      String(project.opportunityId || "") === String(projectPayload.opportunityId || "");
    return quoteMatches || quoteNoMatches || opportunityMatches;
  });
  if (existingProject?.projectId || existingProject?.id) {
    projectPayload = {
      ...projectPayload,
      projectId: existingProject.projectId || existingProject.id,
      createdAt: existingProject.raw?.createdAt || existingProject.createdAt || projectPayload.createdAt,
    };
  }
  const projectId = projectPayload.projectId;
  if (isDevRuntime) {
    devLog("ORDER_PROJECT_DUPLICATE_GUARD_TRACE", {
      quoteId: projectPayload.quoteId || "",
      quoteNo: projectPayload.quoteNo || "",
      dealId: projectPayload.opportunityId || "",
      existingProjectFound: Boolean(existingProject),
      existingProjectId: existingProject?.projectId || existingProject?.id || "",
      createdNewProject: !existingProject,
    });
  }
  const projectPath = `users/${workspaceUid}/projects/${projectId}`;
  const linkTrace = {
    projectId,
    quoteIndexUpdated: false,
    opportunityUpdated: false,
    taskCreated: false,
    orderTemplateTasksCreated: 0,
    orderTemplateTasksSkipped: 0,
    trackingItemsCreated: 0,
    dailyTrackingTaskCreated: false,
    paymentFollowUpCreated: false,
    activityLogged: false,
  };
  let projectDeliveryTrackingPatch = null;

  try {
    await setDoc(
      doc(db, "users", workspaceUid, "projects", projectId),
      sanitizeCrmFirestoreValue(projectPayload),
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "projects");

    if (projectPayload.opportunityId) {
      const opportunityPatch = sanitizeCrmFirestoreValue({
        workspaceUid,
        status: CRM_OPPORTUNITY_STATUS_WON,
        stage: CRM_OPPORTUNITY_STATUS_WON,
        projectId,
        projectCreatedAt: serverTimestamp(),
        orderValue: projectPayload.orderValue,
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actor.uid || "",
      });
      await setDoc(
        doc(db, "users", workspaceUid, "opportunities", projectPayload.opportunityId),
        opportunityPatch,
        { merge: true }
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
      const localLeadId = String(
        orderHandoverModalState.opportunity?.legacyLeadId ||
          orderHandoverModalState.opportunity?.leadId ||
          orderHandoverModalState.opportunity?.id ||
          ""
      ).trim();
      if (localLeadId) {
        applyCrmLeadLocalPatch(localLeadId, {
          projectId,
          status: CRM_OPPORTUNITY_STATUS_WON,
          crmStageId: CRM_OPPORTUNITY_STATUS_WON,
          workStage: CRM_OPPORTUNITY_STATUS_WON,
          orderValue: projectPayload.orderValue,
          updatedAt: new Date(),
        });
      }
      linkTrace.opportunityUpdated = true;
    }

    const quoteIndexId = String(draft.quoteIndexId || projectPayload.quoteId || "").trim();
    if (quoteIndexId) {
      const quoteIndexPatch = sanitizeCrmFirestoreValue({
        crmStatus: "order_confirmed",
        orderStatus: "project_created",
        projectId,
        orderValue: projectPayload.orderValue,
        advanceAmount: projectPayload.advanceAmount,
        balanceAmount: projectPayload.balanceAmount,
        paymentStatus: projectPayload.paymentStatus,
        orderConfirmedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actor.uid || "",
      });
      await setDoc(
        doc(db, "users", workspaceUid, "quote_index", quoteIndexId),
        quoteIndexPatch,
        { merge: true }
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      applyQuoteLocalPatch(projectPayload.quoteId || quoteIndexId, {
        crmStatus: "order_confirmed",
        orderStatus: "project_created",
        projectId,
        orderValue: projectPayload.orderValue,
        advanceAmount: projectPayload.advanceAmount,
        balanceAmount: projectPayload.balanceAmount,
        paymentStatus: projectPayload.paymentStatus,
        orderConfirmedAt: new Date(),
        updatedAt: new Date(),
      }, { persistIndex: false });
      linkTrace.quoteIndexUpdated = true;
    }

    const taskDueAt =
      projectPayload.nextActionDueAt ||
      projectPayload.expectedInstallationDate ||
      Timestamp.fromDate(parseDateInputValue(addDaysToDateKey(todayISO(), 1)) || new Date());
    await createCrmTask({
      taskId: `${projectId}__handover`,
      relatedType: "project",
      relatedId: projectId,
      projectId,
      opportunityId: projectPayload.opportunityId,
      customerId: projectPayload.customerId,
      customerName: projectPayload.customerName,
      quoteId: projectPayload.quoteId,
      quoteNo: projectPayload.quoteNo,
      taskType: "other",
      title: projectPayload.nextActionText || "Complete order handover",
      notes: projectPayload.handoverNotes,
      ownerUid: projectPayload.nextActionOwnerUid || projectPayload.projectOwnerUid,
      ownerName: projectPayload.nextActionOwnerName || projectPayload.projectOwnerName,
      dueAt: taskDueAt,
      status: CRM_TASK_STATUS_OPEN,
      priority: "normal",
      systemCreated: true,
      sourceKind: "order_handover",
    });
    linkTrace.taskCreated = true;

    const orderTaskTemplateResult = await createOrderTemplateTasksForProject(projectPayload, {
      manual: false,
    });
    linkTrace.orderTemplateTasksCreated = orderTaskTemplateResult.tasksCreated || 0;
    linkTrace.orderTemplateTasksSkipped = orderTaskTemplateResult.tasksSkipped || 0;

    if (
      crmSettings.enablePaymentTracking !== false &&
      crmSettings.autoCreatePaymentFollowUp !== false &&
      Number(projectPayload.balanceAmount || 0) > 0
    ) {
      const paymentTask = await createPaymentFollowUpTask(projectPayload, {
        dueAmount: projectPayload.balanceAmount,
      });
      linkTrace.paymentFollowUpCreated = Boolean(paymentTask);
    }

    const trackingDraftItems = Array.isArray(draft.trackingItems)
      ? draft.trackingItems
          .map((item) => ({
            ...item,
            itemName: String(item.itemName || "").trim(),
            supplierName: String(item.supplierName || "").trim(),
            notes: String(item.notes || "").trim(),
          }))
          .filter(
            (item) =>
              item.itemName ||
              item.supplierName ||
              String(item.quantity || "").trim() ||
              item.expectedArrivalDate ||
              item.notes
          )
      : [];
    if (
      crmSettings.enableDeliveryTracking !== false &&
      draft.enableDeliveryTracking !== false &&
      trackingDraftItems.length
    ) {
      const trackingOwnerUid = String(
        draft.trackingOwnerUid || projectPayload.projectOwnerUid || actor.uid || ""
      ).trim();
      const trackingOwnerName = String(
        draft.trackingOwnerName || projectPayload.projectOwnerName || actor.name || ""
      ).trim();
      const dailyCheckTime = String(
        draft.dailyTrackingReminderTime ||
          crmSettings.defaultTrackingReminderTime ||
          "10:30"
      ).trim();
      const trackingPayloads = trackingDraftItems.map((item, index) => {
        const trackingItemId = `tracking_${sanitizeTaskHistorySegment(projectId)}_${sanitizeTaskHistorySegment(
          item.clientId || index + 1
        )}`;
        const quantityText = String(item.quantity ?? "").trim();
        const quantityNumber = quantityText ? Number(quantityText) : null;
        const expectedArrivalDate = item.expectedArrivalDate
          ? parseDateInputValue(item.expectedArrivalDate)
          : null;
        return sanitizeCrmFirestoreValue({
          trackingItemId,
          workspaceUid,
          projectId,
          projectTitle: projectPayload.projectTitle,
          quoteId: projectPayload.quoteId || "",
          quoteNo: projectPayload.quoteNo || "",
          customerId: projectPayload.customerId || "",
          customerName: projectPayload.customerName || "",
          siteName: projectPayload.siteName || "",
          itemType: normalizeTrackingItemType(item.itemType || "fabric"),
          itemName:
            item.itemName ||
            getTrackingItemTypeLabel(item.itemType || "fabric"),
          supplierName: item.supplierName || "",
          quantity:
            Number.isFinite(quantityNumber) && quantityNumber >= 0
              ? Number(quantityNumber.toFixed(2))
              : null,
          unit: CRM_TRACKING_UNITS.includes(String(item.unit || "").trim())
            ? String(item.unit || "").trim()
            : "meter",
          orderedQuantity: null,
          receivedQuantity: null,
          expectedArrivalDate: expectedArrivalDate
            ? Timestamp.fromDate(expectedArrivalDate)
            : null,
          status: "not_ordered",
          priority: "normal",
          notes: item.notes || "",
          createdAt: serverTimestamp(),
          createdBy: actor.uid || "",
          updatedAt: serverTimestamp(),
          updatedBy: actor.uid || "",
        });
      });
      await Promise.all(
        trackingPayloads.map((payload) =>
          setDoc(
            doc(db, "users", workspaceUid, "project_tracking_items", payload.trackingItemId),
            payload,
            { merge: true }
          )
        )
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "project_tracking_items");
      const normalizedTrackingItems = trackingPayloads.map((payload) =>
        normalizeCrmMaterialRecord(payload.trackingItemId, {
          ...payload,
          createdAt: new Date(),
          updatedAt: new Date(),
        })
      );
      const summary = buildProjectTrackingSummary(normalizedTrackingItems);
      projectDeliveryTrackingPatch = sanitizeCrmFirestoreValue({
        enabled: true,
        trackingComplete: false,
        trackingOwnerUid,
        trackingOwnerName,
        dailyCheckTime,
        timezone: "Asia/Kolkata",
        openItemCount: summary.openItemCount,
        delayedItemCount: summary.delayedItemCount,
        receivedItemCount: summary.receivedItemCount,
        totalItemCount: summary.totalItemCount,
        lastCheckedAt: null,
        lastCheckedBy: "",
        nextCheckTaskId: "",
        nextCheckDueAt: null,
      });
      await updateDoc(
        doc(db, "users", workspaceUid, "projects", projectId),
        sanitizeCrmFirestoreValue({
          deliveryTracking: projectDeliveryTrackingPatch,
          updatedAt: serverTimestamp(),
          updatedBy: actor.uid || "",
        })
      );
      if (crmSettings.enableDailyTrackingReminder !== false) {
        const dailyTaskResult = await createDailyTrackingTaskForProject(
          {
            ...projectPayload,
            deliveryTracking: projectDeliveryTrackingPatch,
          },
          {
            trackingOwnerUid,
            trackingOwnerName,
            dailyCheckTime,
            action: "created",
          }
        );
        linkTrace.dailyTrackingTaskCreated = Boolean(dailyTaskResult);
        if (dailyTaskResult?.deliveryTrackingPatch) {
          projectDeliveryTrackingPatch = sanitizeCrmFirestoreValue({
            ...projectDeliveryTrackingPatch,
            ...dailyTaskResult.deliveryTrackingPatch,
          });
        }
      }
      linkTrace.trackingItemsCreated = trackingPayloads.length;
      if (isDevRuntime) {
        devLog("CRM_TRACKING_SETUP_TRACE", {
          projectId,
          enabled: true,
          trackingOwnerUid,
          dailyCheckTime,
          itemCount: trackingPayloads.length,
        });
      }
    }

    if (projectPayload.opportunityId) {
      await logCrmActivity({
        relatedType: "opportunity",
        relatedId: projectPayload.opportunityId,
        opportunityId: projectPayload.opportunityId,
        customerId: projectPayload.customerId,
        customerName: projectPayload.customerName,
        quoteId: projectPayload.quoteId,
        quoteNo: projectPayload.quoteNo,
        activityType: "won",
        title: "Opportunity won",
        description: "Order handover created.",
      });
    }
    await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      opportunityId: projectPayload.opportunityId,
      customerId: projectPayload.customerId,
      customerName: projectPayload.customerName,
      quoteId: projectPayload.quoteId,
      quoteNo: projectPayload.quoteNo,
      activityType: "created",
      title: "Order handover created",
      description: projectPayload.handoverNotes || "",
    });
    linkTrace.activityLogged = true;

    await waitForPendingWrites(db);
    const projectReadbackRef = doc(db, "users", workspaceUid, "projects", projectId);
    const projectReadbackSnap = await trackedGetDoc(projectReadbackRef, {
      label: "crmProjectCreate.readback.project",
    });
    if (!projectReadbackSnap.exists()) {
      const readbackError = new Error("Project write was not confirmed by Firestore.");
      readbackError.code = "project-readback-missing";
      readbackError.path = projectPath;
      throw readbackError;
    }
    if (quoteIndexId) {
      const quoteIndexReadbackPath = `users/${workspaceUid}/quote_index/${quoteIndexId}`;
      const quoteIndexReadbackSnap = await trackedGetDoc(
        doc(db, "users", workspaceUid, "quote_index", quoteIndexId),
        { label: "crmProjectCreate.readback.quoteIndex" }
      );
      if (!quoteIndexReadbackSnap.exists()) {
        const readbackError = new Error("Quote index order link was not confirmed by Firestore.");
        readbackError.code = "quote-index-readback-missing";
        readbackError.path = quoteIndexReadbackPath;
        throw readbackError;
      }
      const readbackProjectId = String(quoteIndexReadbackSnap.data()?.projectId || "").trim();
      if (readbackProjectId !== projectId) {
        const readbackError = new Error("Quote index points to a different project after order handover.");
        readbackError.code = "quote-index-project-mismatch";
        readbackError.path = quoteIndexReadbackPath;
        readbackError.expectedProjectId = projectId;
        readbackError.actualProjectId = readbackProjectId;
        throw readbackError;
      }
    }

    patchCrmProjectLocalState(projectId, {
      ...projectPayload,
      ...(projectDeliveryTrackingPatch
        ? { deliveryTracking: projectDeliveryTrackingPatch }
        : {}),
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    invalidateFirestoreReadCacheByPrefix(`customerLedger:projects:${workspaceUid}:`);
    if (projectPayload.customerId) {
      const normalizedProject = normalizeCrmProjectRecord(projectId, {
        ...projectPayload,
        ...(projectDeliveryTrackingPatch
          ? { deliveryTracking: projectDeliveryTrackingPatch }
          : {}),
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setCustomerLedgerProjectsByCustomer((prev) => {
        const existingProjects = prev[projectPayload.customerId];
        if (!Array.isArray(existingProjects)) return prev;
        return {
          ...prev,
          [projectPayload.customerId]: [
            normalizedProject,
            ...existingProjects.filter(
              (project) => String(project.projectId || project.id || "") !== projectId
            ),
          ],
        };
      });
    }
    setSelectedCrmProjectId(projectId);
    setCrmProjectDrawerTab("overview");
    setCrmView("projects");
    if (isDevRuntime) {
      devLog("CRM_PROJECT_CREATE_TRACE", {
        projectId,
        quoteId: projectPayload.quoteId,
        quoteNo: projectPayload.quoteNo,
        opportunityId: projectPayload.opportunityId,
        customerName: projectPayload.customerName,
        orderValue: projectPayload.orderValue,
        paymentStatus: projectPayload.paymentStatus,
        path: projectPath,
      });
      devLog("CRM_PROJECT_LINK_TRACE", linkTrace);
    }
    notify("Order handover created", "success");
    closeOrderHandoverModal();
  } catch (error) {
    devError("CRM_PROJECT_CREATE_FAILED_EXACT", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      path: projectPath,
      readbackPath: error?.path || "",
      expectedProjectId: error?.expectedProjectId || "",
      actualProjectId: error?.actualProjectId || "",
      linkTrace,
    });
    setOrderHandoverModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Order was not created. Please try again.",
    }));
    notify("Order was not created. Please try again.", "error");
  }
}, [
  addDaysToDateKey,
  applyCrmLeadLocalPatch,
  applyQuoteLocalPatch,
  buildProjectFromOpportunityAndQuote,
  buildProjectTrackingSummary,
  closeOrderHandoverModal,
  createCrmTask,
  createDailyTrackingTaskForProject,
  createOrderTemplateTasksForProject,
  createPaymentFollowUpTask,
  crmSettings.autoCreatePaymentFollowUp,
  crmSettings.defaultTrackingReminderTime,
  crmSettings.enableDailyTrackingReminder,
  crmSettings.enableDeliveryTracking,
  crmSettings.enablePaymentTracking,
  crmProjectRecords,
  getTrackingItemTypeLabel,
  getCrmActorSnapshot,
  logCrmActivity,
  normalizeCrmMaterialRecord,
  normalizeCrmProjectRecord,
  normalizeTrackingItemType,
  notify,
  orderHandoverModalState,
  parseDateInputValue,
  patchCrmProjectLocalState,
  sanitizeCrmFirestoreValue,
  todayISO,
  validateProjectHandoverInput,
  workspaceUid,
]);

const updateCrmProjectPatch = useCallback(
  async (projectId, patch = {}) => {
    const cleanProjectId = String(projectId || "").trim();
    if (!workspaceUid || !cleanProjectId) return null;
    const actor = getCrmActorSnapshot();
    const projectPatchTimestamp = Timestamp.fromDate(new Date());
    const currentProject = crmProjectRecords.find(
      (project) => String(project.projectId || project.id || "") === cleanProjectId
    );
    const shouldNormalizePipelineStage =
      Object.prototype.hasOwnProperty.call(patch, "projectStage") ||
      Object.prototype.hasOwnProperty.call(patch, "orderStage") ||
      Object.prototype.hasOwnProperty.call(patch, "stage");
    const resolvedPatchPipelineStage = shouldNormalizePipelineStage
      ? resolveProjectPipelineStage({
          projectStage: patch.projectStage,
          orderStage: patch.orderStage,
          stage: patch.stage,
          status: patch.status || currentProject?.status,
        })
      : "";
    const stageCompatibilityPatch = resolvedPatchPipelineStage
      ? {
          pipelineId: "orders",
          projectStage: resolvedPatchPipelineStage,
          orderStage: resolvedPatchPipelineStage,
          stage: mapOrderPipelineStageToLegacyProjectStage(resolvedPatchPipelineStage),
        }
      : {};
    const firestorePatch = sanitizeCrmFirestoreValue({
      ...patch,
      ...stageCompatibilityPatch,
      workspaceUid,
      projectId: cleanProjectId,
      updatedAt: projectPatchTimestamp,
      updatedBy: actor.uid || "",
    });
    await updateDoc(
      doc(db, "users", workspaceUid, "projects", cleanProjectId),
      firestorePatch
    );
    patchCrmProjectLocalState(cleanProjectId, {
      ...patch,
      ...stageCompatibilityPatch,
      projectId: cleanProjectId,
      updatedAt: projectPatchTimestamp,
      updatedBy: actor.uid || "",
    });
    invalidateFirestoreReadCacheByPrefix(`customerLedger:projects:${workspaceUid}:`);
    const relatedCustomerId = String(
      patch.customerId || currentProject?.customerId || ""
    ).trim();
    if (relatedCustomerId) {
      setCustomerLedgerProjectsByCustomer((prev) => {
        const existingProjects = prev[relatedCustomerId];
        if (!Array.isArray(existingProjects)) return prev;
        return {
          ...prev,
          [relatedCustomerId]: existingProjects.map((project) =>
            String(project.projectId || project.id || "") === cleanProjectId
              ? normalizeCrmProjectRecord(cleanProjectId, {
                ...(project.raw || project),
                ...patch,
                  ...stageCompatibilityPatch,
                projectId: cleanProjectId,
                updatedAt: projectPatchTimestamp,
                updatedBy: actor.uid || "",
                })
              : project
          ),
        };
      });
    }
    if (
      Object.prototype.hasOwnProperty.call(firestorePatch, "stage") &&
      String(currentProject?.stage || "") !== String(firestorePatch.stage || "")
    ) {
      const activity = await logCrmActivity({
        relatedType: "project",
        relatedId: cleanProjectId,
        projectId: cleanProjectId,
        customerId: patch.customerId || currentProject?.customerId || "",
        customerName: patch.customerName || currentProject?.customerName || "",
        quoteId: patch.quoteId || currentProject?.quoteId || "",
        quoteNo: patch.quoteNo || currentProject?.quoteNo || "",
        activityType: "project_stage_changed",
        title: "Project stage changed",
        description: `${getProjectStageLabel(currentProject?.stage || "") || "Previous stage"} -> ${getProjectStageLabel(firestorePatch.stage)}`,
        createdBy: actor.uid || "",
        createdByName: actor.name || "",
      }).catch(() => null);
      if (activity) prependProjectActivityLocal(cleanProjectId, activity);
    }
    if (isDevRuntime && Object.prototype.hasOwnProperty.call(firestorePatch, "stage")) {
      devLog("PROJECT_STAGE_TRACE", {
        projectId: cleanProjectId,
        quoteNo: currentProject?.quoteNo || patch.quoteNo || "",
        previousStage: currentProject?.stage || "",
        nextStage: firestorePatch.stage,
        reason: firestorePatch.lastActivityType || firestorePatch.lastActivityLabel || "project_patch",
        userId: actor.uid || "",
      });
    }
    return firestorePatch;
  },
  [
    crmProjectRecords,
    getCrmActorSnapshot,
    logCrmActivity,
    normalizeCrmProjectRecord,
    patchCrmProjectLocalState,
    prependProjectActivityLocal,
    workspaceUid,
  ]
);

const updateProjectPipelineStage = useCallback(
  async (projectLike = {}, requestedStageId = "") => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    const nextStage = getOrderPipelineStage(orderPipeline, requestedStageId);
    if (!workspaceUid || !projectId || !nextStage) return null;
    if (!canUpdateCrmProjectExecution(projectLike)) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }

    const oldStage = resolveProjectPipelineStage(projectLike);
    const newStage = nextStage.stageId;
    if (!newStage || oldStage === newStage) return null;

    if (
      newStage === "installation_scheduled" &&
      !projectLike.expectedInstallationDate &&
      !projectLike.installationScheduledAt
    ) {
      const proceed =
        typeof window === "undefined" ||
        window.confirm("This order has no installation date yet. Move it to Installation Scheduled anyway?");
      if (!proceed) return null;
    }

    if (newStage === "final_payment_pending") {
      const balance = Number(
        projectLike.paymentSummary?.balanceAmount ?? projectLike.balanceAmount ?? 0
      ) || 0;
      if (balance > 0 && typeof window !== "undefined") {
        window.alert(`Balance pending: ${formatCurrencyDisplay(balance, currency)}`);
      }
    }

    let cancellationReason = "";
    if (newStage === "cancelled" && typeof window !== "undefined") {
      cancellationReason = String(
        window.prompt("Reason for cancelling this order?", projectLike.cancellationReason || "") || ""
      ).trim();
    }

    const legacyStage = mapOrderPipelineStageToLegacyProjectStage(newStage);
    const patch = {
      pipelineId: "orders",
      projectStage: newStage,
      orderStage: newStage,
      stage: legacyStage,
      status:
        newStage === "project_closed"
          ? "closed"
          : newStage === "cancelled"
          ? "cancelled"
          : "open",
      ...(newStage === "cancelled"
        ? {
            cancellationReason,
            cancelledAt: Timestamp.fromDate(new Date()),
          }
        : {}),
      lastActivityAt: Timestamp.fromDate(new Date()),
      lastActivityType: "order_pipeline_stage_changed",
      lastActivityLabel: `Moved to ${nextStage.label}`,
    };

    try {
      await updateCrmProjectPatch(projectId, patch);
      if (isDevRuntime) {
        devLog("ORDER_PIPELINE_STAGE_CHANGE_TRACE", {
          projectId,
          quoteNo: projectLike.quoteNo || "",
          customerName: projectLike.customerName || "",
          oldStage,
          newStage,
          projectStageUpdated: true,
          orderStageUpdated: true,
          legacyStageUpdated: true,
          activityLogged: true,
          localStatePatched: true,
          errors: [],
        });
      }
      notify(`Order moved to ${nextStage.label}.`, "success");
      return patch;
    } catch (error) {
      devError("ORDER_PIPELINE_STAGE_CHANGE_TRACE", {
        projectId,
        quoteNo: projectLike.quoteNo || "",
        customerName: projectLike.customerName || "",
        oldStage,
        newStage,
        projectStageUpdated: false,
        orderStageUpdated: false,
        legacyStageUpdated: false,
        activityLogged: false,
        localStatePatched: false,
        errors: [{ code: error?.code || "", message: error?.message || String(error) }],
      });
      notify("Could not move order stage. Please try again.", "error");
      return null;
    }
  },
  [
    canUpdateCrmProjectExecution,
    currency,
    notify,
    orderPipeline,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const getProjectCloseGuard = useCallback(
  (projectLike = {}) => {
    const reasons = [];
    const installStatus = normalizeProjectInstallationStatus(
      projectLike.installationStatus
    );
    const balance = Number(
      projectLike.paymentSummary?.balanceAmount ?? projectLike.balanceAmount ?? 0
    ) || 0;
    const openSnagCount = Number(
      projectLike.openSnagCount ?? projectLike.snagSummary?.openCount ?? 0
    ) || 0;
    if (
      crmSettings.requireInstallationBeforeClose !== false &&
      installStatus !== PROJECT_INSTALLATION_STATUS_COMPLETED
    ) {
      reasons.push("Installation is not marked completed yet.");
    }
    if (crmSettings.requireSnagResolutionBeforeClose !== false && openSnagCount > 0) {
      reasons.push(
        `This project has ${openSnagCount} open snag${openSnagCount === 1 ? "" : "s"}. Resolve ${
          openSnagCount === 1 ? "it" : "them"
        } before closing.`
      );
    }
    if (crmSettings.requireFinalPaymentBeforeClose !== false && balance > 0) {
      reasons.push(
        `This project still has a pending balance of ${formatCurrencyDisplay(
          balance,
          currency
        )}. Add payment or create a payment follow-up before closing.`
      );
    }
    if (isDevRuntime) {
      devLog("PROJECT_CLOSE_GUARD_TRACE", {
        projectId: projectLike.projectId || projectLike.id || "",
        installationCompleted: installStatus === PROJECT_INSTALLATION_STATUS_COMPLETED,
        openSnagCount,
        balanceAmount: balance,
        checklistMissing: reasons.length,
        canClose: reasons.length === 0,
        blockReason: reasons[0] || "",
      });
    }
    return {
      canClose: reasons.length === 0,
      reasons,
    };
  },
  [
    crmSettings.requireFinalPaymentBeforeClose,
    crmSettings.requireInstallationBeforeClose,
    crmSettings.requireSnagResolutionBeforeClose,
    currency,
  ]
);

const createInstallationTaskForProject = useCallback(
  async (projectLike = {}, dueDateInput = null) => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !projectId || crmSettings.autoCreateInstallationTask === false) {
      return null;
    }
    const dueDate =
      parseDateInputValue(dueDateInput) ||
      normalizeCrmDate(projectLike.installationScheduledAt) ||
      normalizeCrmDate(projectLike.expectedInstallationDate) ||
      new Date();
    const taskId = `project_${sanitizeTaskHistorySegment(projectId)}__installation`;
    const task = await createCrmTask({
      taskId,
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      opportunityId: projectLike.opportunityId || "",
      taskType: "installation",
      title: "Complete installation",
      notes: projectLike.siteName
        ? `Installation at ${projectLike.siteName}`
        : "Installation task",
      ownerUid: projectLike.projectOwnerUid || "",
      ownerName: projectLike.projectOwnerName || "",
      dueAt: Timestamp.fromDate(dueDate),
      status: CRM_TASK_STATUS_OPEN,
      priority: "high",
      systemGenerated: true,
      sourceKind: "project_installation",
    });
    if (isDevRuntime) {
      devLog("INSTALLATION_TASK_TRACE", {
        projectId,
        taskId,
        action: "upsert",
        installationScheduledAt: dueDate.toISOString(),
        status: CRM_TASK_STATUS_OPEN,
      });
    }
    return task;
  },
  [
    createCrmTask,
    crmSettings.autoCreateInstallationTask,
    parseDateInputValue,
    workspaceUid,
  ]
);

const openCrmInstallationModal = useCallback(
  (projectLike = {}, mode = "schedule") => {
    if (!canUpdateCrmProjectExecution(projectLike)) {
      notify("You do not have permission to do this.", "warning");
      return;
    }
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!projectId) return;
    const scheduledKey =
      toDateInputValue(projectLike.installationScheduledAt) ||
      toDateInputValue(projectLike.expectedInstallationDate) ||
      todayISO();
    setCrmInstallationModalState({
      open: true,
      projectId,
      mode,
      draft: {
        scheduledDate: scheduledKey,
        completedDate: todayISO(),
        notes: "",
      },
      loading: false,
      error: "",
    });
  },
  [canUpdateCrmProjectExecution, notify, toDateInputValue, todayISO]
);

const closeCrmInstallationModal = useCallback(() => {
  setCrmInstallationModalState({
    open: false,
    projectId: "",
    mode: "schedule",
    draft: null,
    loading: false,
    error: "",
  });
}, []);

const updateCrmInstallationDraft = useCallback((patch = {}) => {
  setCrmInstallationModalState((prev) => ({
    ...prev,
    draft: { ...(prev.draft || {}), ...patch },
    error: "",
  }));
}, []);

const saveCrmInstallation = useCallback(async () => {
  const projectId = String(crmInstallationModalState.projectId || "").trim();
  const selectedProject =
    crmProjectRecords.find(
      (project) => String(project.projectId || project.id || "") === projectId
    ) || null;
  if (!workspaceUid || !selectedProject || crmInstallationModalState.loading) return null;
  if (!canUpdateCrmProjectExecution(selectedProject)) {
    setCrmInstallationModalState((prev) => ({
      ...prev,
      loading: false,
      error: "You do not have permission to do this.",
    }));
    notify("You do not have permission to do this.", "warning");
    return null;
  }
  const draft = crmInstallationModalState.draft || {};
  const mode = crmInstallationModalState.mode || "schedule";
  const actor = getCrmActorSnapshot();
  const scheduledDate = parseDateInputValue(draft.scheduledDate);
  const completedDate = parseDateInputValue(draft.completedDate);
  if (mode === "schedule" && !scheduledDate) {
    setCrmInstallationModalState((prev) => ({ ...prev, error: "Choose an installation date." }));
    return null;
  }
  if (mode === "complete" && !completedDate) {
    setCrmInstallationModalState((prev) => ({ ...prev, error: "Choose the completion date." }));
    return null;
  }
  setCrmInstallationModalState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    const checklist = createDefaultProjectExecutionChecklist({
      ...(selectedProject.checklist || {}),
      installationDateFixed: mode === "schedule" ? true : selectedProject.checklist?.installationDateFixed,
      installationCompleted: mode === "complete" ? true : selectedProject.checklist?.installationCompleted,
    });
    const checklistSummary = calculateProjectChecklistSummary(checklist);
    const balance = Number(
      selectedProject.paymentSummary?.balanceAmount ?? selectedProject.balanceAmount ?? 0
    ) || 0;
    const openSnagCount = Number(
      selectedProject.openSnagCount ?? selectedProject.snagSummary?.openCount ?? 0
    ) || 0;
    const nextStage =
      mode === "complete"
        ? openSnagCount > 0
          ? PROJECT_STAGE_SNAG_CORRECTION
          : balance > 0
          ? PROJECT_STAGE_FINAL_PAYMENT_PENDING
          : PROJECT_STAGE_INSTALLATION_COMPLETED
        : PROJECT_STAGE_INSTALLATION_SCHEDULED;
    const patch = {
      stage: nextStage,
      status: "open",
      installationStatus:
        mode === "complete"
          ? PROJECT_INSTALLATION_STATUS_COMPLETED
          : PROJECT_INSTALLATION_STATUS_SCHEDULED,
      installationScheduledAt:
        mode === "schedule"
          ? Timestamp.fromDate(scheduledDate)
          : selectedProject.installationScheduledAt || null,
      installationCompletedAt:
        mode === "complete" ? Timestamp.fromDate(completedDate) : selectedProject.installationCompletedAt || null,
      checklist,
      checklistSummary,
      lastActivityAt: Timestamp.fromDate(new Date()),
      lastActivityType:
        mode === "complete" ? "installation_completed" : "installation_scheduled",
      lastActivityLabel:
        mode === "complete" ? "Installation completed" : "Installation scheduled",
    };
    await updateCrmProjectPatch(projectId, patch);
    if (mode === "schedule") {
      await createInstallationTaskForProject(
        { ...selectedProject, ...patch },
        draft.scheduledDate
      ).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM_INSTALLATION_TASK_FAILED_EXACT", {
            projectId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    } else {
      await completeCrmTask(
        `project_${sanitizeTaskHistorySegment(projectId)}__installation`,
        { projectId, customerName: selectedProject.customerName }
      ).catch(() => null);
    }
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      activityType: patch.lastActivityType,
      title: patch.lastActivityLabel,
      description:
        mode === "complete"
          ? `Completed on ${formatCrmDate(completedDate)}.`
          : `Scheduled for ${formatCrmDate(scheduledDate)}.`,
      createdBy: actor.uid || "",
      createdByName: actor.name || "",
    }).catch(() => null);
    if (activity) prependProjectActivityLocal(projectId, activity);
    removeCrmAttentionProjectById(projectId, [
      "installationNotScheduledProjects",
      "installationsDueToday",
      "overdueInstallations",
    ]);
    closeCrmInstallationModal();
    notify(patch.lastActivityLabel, "success");
    return patch;
  } catch (error) {
    devError("CRM_INSTALLATION_SAVE_FAILED_EXACT", {
      code: error?.code || "",
      message: error?.message || String(error),
      projectId,
      mode,
    });
    setCrmInstallationModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Installation update could not be saved.",
    }));
    notify("Installation update could not be saved.", "error");
    return null;
  }
}, [
  closeCrmInstallationModal,
  completeCrmTask,
  canUpdateCrmProjectExecution,
  createInstallationTaskForProject,
  crmInstallationModalState,
  crmProjectRecords,
  getCrmActorSnapshot,
  logCrmActivity,
  notify,
  parseDateInputValue,
  prependProjectActivityLocal,
  removeCrmAttentionProjectById,
  updateCrmProjectPatch,
  workspaceUid,
]);

const patchProjectSnagDrawerState = useCallback(
  (projectId, snagId, snagPatch = {}) => {
    if (!projectId || !snagId) return;
    invalidateFirestoreReadCacheByPrefix(`projectSnags:${workspaceUid}:project:${projectId}`);
    const normalized = normalizeCrmProjectSnagRecord(snagId, {
      ...snagPatch,
      snagId,
      projectId,
      updatedAt: snagPatch.updatedAt || new Date(),
    });
    setCrmProjectDrawerData((prev) => {
      if (String(prev.projectId || "") !== String(projectId)) return prev;
      let found = false;
      const nextSnags = (prev.snags || []).map((snag) => {
        if (String(snag.snagId || snag.id) !== String(snagId)) return snag;
        found = true;
        return normalizeCrmProjectSnagRecord(snagId, {
          ...(snag.raw || snag),
          ...snagPatch,
          snagId,
          projectId,
          updatedAt: snagPatch.updatedAt || new Date(),
        });
      });
      const merged = found ? nextSnags : [normalized, ...nextSnags];
      return {
        ...prev,
        snags: merged.sort(
          (left, right) =>
            safeTimeMillis(right.updatedAt || right.createdAt) -
            safeTimeMillis(left.updatedAt || left.createdAt)
        ),
      };
    });
  },
  [normalizeCrmProjectSnagRecord, workspaceUid]
);

const openCrmSnagModal = useCallback(
  (projectLike = {}, snagLike = null) => {
    if (!canUpdateCrmProjectExecution(projectLike)) {
      notify("You do not have permission to do this.", "warning");
      return;
    }
    const projectId = String(projectLike.projectId || projectLike.id || snagLike?.projectId || "").trim();
    if (!projectId) return;
    setCrmSnagModalState({
      open: true,
      projectId,
      snagId: snagLike?.snagId || snagLike?.id || "",
      draft: {
        title: snagLike?.title || "",
        description: snagLike?.description || "",
        dueDate: snagLike?.dueDateKey || toDateInputValue(snagLike?.dueAt) || addDaysToDateKey(todayISO(), 1),
        priority: snagLike?.priority === "urgent" ? "urgent" : "normal",
        ownerUid: snagLike?.ownerUid || projectLike.projectOwnerUid || "",
        ownerName: snagLike?.ownerName || projectLike.projectOwnerName || "",
        resolutionNotes: snagLike?.resolutionNotes || "",
      },
      loading: false,
      error: "",
    });
  },
  [addDaysToDateKey, canUpdateCrmProjectExecution, notify, toDateInputValue, todayISO]
);

const closeCrmSnagModal = useCallback(() => {
  setCrmSnagModalState({
    open: false,
    projectId: "",
    snagId: "",
    draft: null,
    loading: false,
    error: "",
  });
}, []);

const updateCrmSnagDraft = useCallback((patch = {}) => {
  setCrmSnagModalState((prev) => ({
    ...prev,
    draft: { ...(prev.draft || {}), ...patch },
    error: "",
  }));
}, []);

const saveCrmSnag = useCallback(async () => {
  const projectId = String(crmSnagModalState.projectId || "").trim();
  const selectedProject =
    crmProjectRecords.find(
      (project) => String(project.projectId || project.id || "") === projectId
    ) || null;
  if (!workspaceUid || !selectedProject || crmSnagModalState.loading) return null;
  if (!canUpdateCrmProjectExecution(selectedProject)) {
    setCrmSnagModalState((prev) => ({
      ...prev,
      loading: false,
      error: "You do not have permission to do this.",
    }));
    notify("You do not have permission to do this.", "warning");
    return null;
  }
  const draft = crmSnagModalState.draft || {};
  const title = String(draft.title || "").trim();
  if (!title) {
    setCrmSnagModalState((prev) => ({ ...prev, error: "Enter a snag title." }));
    return null;
  }
  const actor = getCrmActorSnapshot();
  const snagId =
    String(crmSnagModalState.snagId || "").trim() || createClientId("project-snag");
  const isEdit = Boolean(crmSnagModalState.snagId);
  const dueDate = parseDateInputValue(draft.dueDate);
  setCrmSnagModalState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    const payload = sanitizeCrmFirestoreValue({
      snagId,
      workspaceUid,
      projectId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      title,
      description: String(draft.description || "").trim(),
      priority: draft.priority === "urgent" ? "urgent" : "normal",
      status: "open",
      dueAt: dueDate ? Timestamp.fromDate(dueDate) : null,
      ownerUid: String(draft.ownerUid || selectedProject.projectOwnerUid || actor.uid || "").trim(),
      ownerName: String(draft.ownerName || selectedProject.projectOwnerName || actor.name || "").trim(),
      resolutionNotes: String(draft.resolutionNotes || "").trim(),
      createdAt: isEdit ? draft.createdAt || undefined : serverTimestamp(),
      createdBy: isEdit ? undefined : actor.uid || "",
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid || "",
    });
    const snagParentIds = Array.from(
      new Set(
        [projectId, selectedProject.id, selectedProject.projectId]
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
    let savedSnagData = null;
    for (const parentProjectId of snagParentIds) {
      const snagRef = doc(
        db,
        "users",
        workspaceUid,
        "projects",
        parentProjectId,
        "snags",
        snagId
      );
      const pathPayload = sanitizeCrmFirestoreValue({
        ...payload,
        projectId: parentProjectId,
      });
      await setDoc(snagRef, pathPayload, { merge: true });
      await waitForPendingWrites(db).catch(() => null);
      const savedSnagSnap = await trackedGetDoc(snagRef, {
        label: "crmSnagSave.readback",
        component: "CRM Project Snags",
      });
      if (!savedSnagSnap.exists()) {
        throw new Error("Snag save readback failed: snag document was not found after write.");
      }
      if (!savedSnagData || parentProjectId === projectId) {
        savedSnagData = {
          ...(savedSnagSnap.data() || pathPayload),
          projectId: parentProjectId,
        };
      }
    }
    const drawerSnagsLoaded =
      String(crmProjectDrawerData.projectId || "") === String(projectId) &&
      Array.isArray(crmProjectDrawerData.snags);
    const loadedSnags = drawerSnagsLoaded ? crmProjectDrawerData.snags || [] : [];
    const loadedOpenCount = loadedSnags.filter(
      (snag) => String(snag.status || "") !== "resolved"
    ).length;
    const previousOpenCount = drawerSnagsLoaded
      ? loadedOpenCount
      : Number(selectedProject.openSnagCount ?? selectedProject.snagSummary?.openCount ?? 0) || 0;
    const nextOpenCount = isEdit ? previousOpenCount : previousOpenCount + 1;
    const nextSummary = normalizeProjectSnagSummary({
      ...(selectedProject.snagSummary || {}),
      openCount: nextOpenCount,
      totalCount: Math.max(
        drawerSnagsLoaded
          ? loadedSnags.length + (isEdit ? 0 : 1)
          : Number(selectedProject.snagSummary?.totalCount || 0) || 0,
        nextOpenCount + Number(selectedProject.snagSummary?.resolvedCount || 0)
      ),
      urgentCount:
        draft.priority === "urgent" && !isEdit
          ? Number(selectedProject.snagSummary?.urgentCount || 0) + 1
          : selectedProject.snagSummary?.urgentCount || 0,
      nextDueAt: dueDate ? Timestamp.fromDate(dueDate) : selectedProject.nextSnagDueAt || null,
      lastUpdatedAt: Timestamp.fromDate(new Date()),
    });
    await updateCrmProjectPatch(projectId, {
      stage: PROJECT_STAGE_SNAG_CORRECTION,
      status: "open",
      snagSummary: nextSummary,
      hasOpenSnags: nextSummary.openCount > 0,
      openSnagCount: nextSummary.openCount,
      nextSnagDueAt: nextSummary.nextDueAt || null,
      lastActivityAt: Timestamp.fromDate(new Date()),
      lastActivityType: isEdit ? "snag_updated" : "snag_created",
      lastActivityLabel: isEdit ? "Snag updated" : "Snag created",
    });
    await createCrmTask({
      taskId: `project_${sanitizeTaskHistorySegment(projectId)}__snag_${sanitizeTaskHistorySegment(snagId)}`,
      relatedType: "project_snag",
      relatedId: snagId,
      projectId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      taskType: "snag",
      title: `Resolve snag: ${title}`,
      notes: payload.description || "",
      ownerUid: payload.ownerUid || "",
      ownerName: payload.ownerName || "",
      dueAt: payload.dueAt || null,
      status: CRM_TASK_STATUS_OPEN,
      priority: payload.priority,
      systemGenerated: true,
      sourceKind: "project_snag",
    }).catch(() => null);
    patchProjectSnagDrawerState(projectId, snagId, {
      ...savedSnagData,
      updatedAt: new Date(),
      dueAt: dueDate || null,
    });
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      activityType: isEdit ? "snag_updated" : "snag_created",
      title: isEdit ? "Snag updated" : "Snag created",
      description: title,
    }).catch(() => null);
    if (activity) prependProjectActivityLocal(projectId, activity);
    closeCrmSnagModal();
    notify(isEdit ? "Snag updated." : "Snag added.", "success");
    return payload;
  } catch (error) {
    devError("CRM_SNAG_SAVE_FAILED_EXACT", {
      code: error?.code || "",
      message: error?.message || String(error),
      projectId,
      snagId,
    });
    setCrmSnagModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Snag could not be saved.",
    }));
    notify("Snag could not be saved.", "error");
    return null;
  }
}, [
  canUpdateCrmProjectExecution,
  closeCrmSnagModal,
  createCrmTask,
  crmProjectDrawerData.projectId,
  crmProjectDrawerData.snags,
  crmProjectRecords,
  crmSnagModalState,
  getCrmActorSnapshot,
  logCrmActivity,
  notify,
  parseDateInputValue,
  patchProjectSnagDrawerState,
  prependProjectActivityLocal,
  updateCrmProjectPatch,
  workspaceUid,
]);

const resolveCrmSnag = useCallback(
  async (snagLike = {}) => {
    const snagId = String(snagLike.snagId || snagLike.id || "").trim();
    const projectId = String(snagLike.projectId || selectedCrmProjectId || "").trim();
    const selectedProject =
      crmProjectRecords.find(
        (project) =>
          String(project.projectId || "") === projectId ||
          String(project.id || "") === projectId
      ) || null;
    if (!workspaceUid || !selectedProject || !projectId || !snagId || crmProjectActionKey) {
      return null;
    }
    if (!canUpdateCrmProjectExecution(selectedProject)) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }
    const actionKey = `snag:${snagId}:resolve`;
    const actor = getCrmActorSnapshot();
    setCrmProjectActionKey(actionKey);
    try {
      const patch = sanitizeCrmFirestoreValue({
        status: "resolved",
        resolvedAt: serverTimestamp(),
        resolvedBy: actor.uid || "",
        updatedAt: serverTimestamp(),
        updatedBy: actor.uid || "",
      });
      const snagParentIds = Array.from(
        new Set(
          [projectId, selectedProject.id, selectedProject.projectId, snagLike.projectId]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );
      await Promise.all(
        snagParentIds.map((parentProjectId) =>
          setDoc(
            doc(db, "users", workspaceUid, "projects", parentProjectId, "snags", snagId),
            sanitizeCrmFirestoreValue({
              ...(snagLike.raw || snagLike),
              ...patch,
              snagId,
              projectId: parentProjectId,
              workspaceUid,
              customerId: selectedProject.customerId || "",
              customerName: selectedProject.customerName || "",
              quoteId: selectedProject.quoteId || "",
              quoteNo: selectedProject.quoteNo || "",
              title: snagLike.title || "Snag / correction",
              updatedAt: serverTimestamp(),
              updatedBy: actor.uid || "",
            }),
            { merge: true }
          )
        )
      );
      snagParentIds.forEach((parentProjectId) => {
        invalidateFirestoreReadCacheByPrefix(`projectSnags:${workspaceUid}:project:${parentProjectId}`);
      });
      const snagsLoaded =
        String(crmProjectDrawerData.projectId || "") === String(projectId) &&
        Array.isArray(crmProjectDrawerData.snags);
      const existingSnags = snagsLoaded ? crmProjectDrawerData.snags || [] : [];
      const unresolved = existingSnags.filter(
        (snag) =>
          String(snag.snagId || snag.id) !== snagId && String(snag.status || "") !== "resolved"
      );
      const previousSummary = selectedProject.snagSummary || {};
      const previousOpenCount = Number(
        selectedProject.openSnagCount ?? previousSummary.openCount ?? 0
      ) || 0;
      const nextOpenCount = snagsLoaded
        ? unresolved.length
        : Math.max(0, previousOpenCount - 1);
      const nextResolvedCount = snagsLoaded
        ? Math.max(0, existingSnags.length - nextOpenCount)
        : Number(previousSummary.resolvedCount || 0) + 1;
      const loadedNextDueAt =
        unresolved
          .map((snag) => snag.dueAt)
          .filter(Boolean)
          .sort((left, right) => safeTimeMillis(left) - safeTimeMillis(right))[0] || null;
      const nextSummary = normalizeProjectSnagSummary({
        ...previousSummary,
        openCount: nextOpenCount,
        resolvedCount: nextResolvedCount,
        totalCount: snagsLoaded
          ? existingSnags.length
          : Number(previousSummary.totalCount || 0) || 0,
        urgentCount:
          snagLike.priority === "urgent"
            ? Math.max(0, Number(previousSummary.urgentCount || 0) - 1)
            : previousSummary.urgentCount || 0,
        nextDueAt:
          nextOpenCount > 0
            ? loadedNextDueAt || previousSummary.nextDueAt || selectedProject.nextSnagDueAt || null
            : null,
        lastUpdatedAt: Timestamp.fromDate(new Date()),
      });
      const balance = Number(
        selectedProject.paymentSummary?.balanceAmount ?? selectedProject.balanceAmount ?? 0
      ) || 0;
      const nextStage =
        nextSummary.openCount > 0
          ? PROJECT_STAGE_SNAG_CORRECTION
          : balance > 0
          ? PROJECT_STAGE_FINAL_PAYMENT_PENDING
          : PROJECT_STAGE_INSTALLATION_COMPLETED;
      await updateCrmProjectPatch(projectId, {
        stage: nextStage,
        snagSummary: nextSummary,
        hasOpenSnags: nextSummary.openCount > 0,
        openSnagCount: nextSummary.openCount,
        nextSnagDueAt: nextSummary.nextDueAt || null,
        lastActivityAt: Timestamp.fromDate(new Date()),
        lastActivityType: "snag_resolved",
        lastActivityLabel: "Snag resolved",
      });
      patchProjectSnagDrawerState(projectId, snagId, {
        ...(snagLike.raw || snagLike),
        ...patch,
        status: "resolved",
        resolvedAt: new Date(),
        updatedAt: new Date(),
      });
      await completeCrmTask(
        `project_${sanitizeTaskHistorySegment(projectId)}__snag_${sanitizeTaskHistorySegment(snagId)}`,
        { projectId, customerName: selectedProject.customerName }
      ).catch(() => null);
      const checklist = createDefaultProjectExecutionChecklist({
        ...(selectedProject.checklist || {}),
        snagsResolved: nextSummary.openCount === 0,
      });
      await updateCrmProjectPatch(projectId, {
        checklist,
        checklistSummary: calculateProjectChecklistSummary(checklist),
      }).catch(() => null);
      const activity = await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        customerId: selectedProject.customerId || "",
        customerName: selectedProject.customerName || "",
        quoteId: selectedProject.quoteId || "",
        quoteNo: selectedProject.quoteNo || "",
        activityType: "snag_resolved",
        title: "Snag resolved",
        description: snagLike.title || "Snag resolved",
      }).catch(() => null);
      if (activity) prependProjectActivityLocal(projectId, activity);
      if (nextSummary.openCount === 0) {
        removeCrmAttentionProjectById(projectId, ["openSnagProjects", "overdueSnagProjects"]);
      }
      notify("Snag resolved.", "success");
      return true;
    } catch (error) {
      devError("CRM_SNAG_RESOLVE_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        projectId,
        snagId,
      });
      notify("Could not resolve snag.", "error");
      return false;
    } finally {
      setCrmProjectActionKey("");
    }
  },
  [
    canUpdateCrmProjectExecution,
    completeCrmTask,
    crmProjectActionKey,
    crmProjectDrawerData.projectId,
    crmProjectDrawerData.snags,
    crmProjectRecords,
    getCrmActorSnapshot,
    logCrmActivity,
    notify,
    patchProjectSnagDrawerState,
    prependProjectActivityLocal,
    removeCrmAttentionProjectById,
    selectedCrmProjectId,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const createReviewRequestTaskForProject = useCallback(
  async (projectLike = {}) => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !projectId) return null;
    const dueDate =
      parseDateInputValue(addDaysToDateKey(todayISO(), crmSettings.defaultReviewRequestDaysAfterClose || 1)) ||
      new Date();
    const taskId = `project_${sanitizeTaskHistorySegment(projectId)}__review_request`;
    const task = await createCrmTask({
      taskId,
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      taskType: "review",
      title: "Ask customer for review/referral",
      notes: "Request a review or referral after successful completion.",
      ownerUid: projectLike.projectOwnerUid || "",
      ownerName: projectLike.projectOwnerName || "",
      dueAt: Timestamp.fromDate(dueDate),
      status: CRM_TASK_STATUS_OPEN,
      priority: "normal",
      systemGenerated: true,
      sourceKind: "project_review_request",
    });
    const reviewSummary = normalizeProjectReviewSummary({
      ...(projectLike.reviewSummary || {}),
      status: PROJECT_REVIEW_STATUS_PENDING,
      requestedAt: Timestamp.fromDate(new Date()),
      requestedBy: getCrmActorSnapshot().uid || "",
      taskId,
    });
    const shouldMoveToReviewStage =
      String(projectLike.status || "") !== "closed" &&
      String(projectLike.stage || "") !== PROJECT_STAGE_PROJECT_CLOSED;
    await updateCrmProjectPatch(projectId, {
      ...(shouldMoveToReviewStage ? { stage: PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED } : {}),
      reviewSummary,
      lastActivityAt: Timestamp.fromDate(new Date()),
      lastActivityType: "review_requested",
      lastActivityLabel: "Review/referral requested",
    }).catch(() => null);
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      activityType: "review_requested",
      title: "Review/referral requested",
      description: "Review/referral request task created.",
    }).catch(() => null);
    if (activity) prependProjectActivityLocal(projectId, activity);
    if (isDevRuntime) {
      devLog("REVIEW_TASK_TRACE", {
        projectId,
        taskId,
        action: "created",
        dueAt: dueDate.toISOString(),
        status: CRM_TASK_STATUS_OPEN,
      });
    }
    return task;
  },
  [
    addDaysToDateKey,
    createCrmTask,
    crmSettings.defaultReviewRequestDaysAfterClose,
    getCrmActorSnapshot,
    logCrmActivity,
    parseDateInputValue,
    prependProjectActivityLocal,
    todayISO,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const updateProjectReviewStatus = useCallback(
  async (projectLike = {}, statusInput = PROJECT_REVIEW_STATUS_COMPLETED) => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !projectId || crmProjectActionKey) return null;
    if (!canRequestProjectReviewAccess) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }
    const status = normalizeProjectReviewStatus(statusInput);
    const actor = getCrmActorSnapshot();
    const actionKey = `review:${projectId}:${status}`;
    setCrmProjectActionKey(actionKey);
    try {
      if (status === PROJECT_REVIEW_STATUS_PENDING) {
        await createReviewRequestTaskForProject(projectLike);
      } else {
        const taskId =
          projectLike.reviewSummary?.taskId ||
          `project_${sanitizeTaskHistorySegment(projectId)}__review_request`;
        const reviewSummary = normalizeProjectReviewSummary({
          ...(projectLike.reviewSummary || {}),
          status,
          completedAt:
            status === PROJECT_REVIEW_STATUS_COMPLETED
              ? Timestamp.fromDate(new Date())
              : projectLike.reviewSummary?.completedAt || null,
          completedBy:
            status === PROJECT_REVIEW_STATUS_COMPLETED
              ? actor.uid || ""
              : projectLike.reviewSummary?.completedBy || "",
          skippedAt:
            status === PROJECT_REVIEW_STATUS_SKIPPED
              ? Timestamp.fromDate(new Date())
              : projectLike.reviewSummary?.skippedAt || null,
          skippedBy:
            status === PROJECT_REVIEW_STATUS_SKIPPED
              ? actor.uid || ""
              : projectLike.reviewSummary?.skippedBy || "",
          taskId,
        });
        await updateCrmProjectPatch(projectId, {
          reviewSummary,
          lastActivityAt: Timestamp.fromDate(new Date()),
          lastActivityType:
            status === PROJECT_REVIEW_STATUS_COMPLETED ? "review_received" : "review_skipped",
          lastActivityLabel:
            status === PROJECT_REVIEW_STATUS_COMPLETED ? "Review received" : "Review skipped",
        });
        if (status === PROJECT_REVIEW_STATUS_COMPLETED || status === PROJECT_REVIEW_STATUS_SKIPPED) {
          await completeCrmTask(taskId, { projectId, customerName: projectLike.customerName }).catch(() => null);
        }
        const activity = await logCrmActivity({
          relatedType: "project",
          relatedId: projectId,
          projectId,
          customerId: projectLike.customerId || "",
          customerName: projectLike.customerName || "",
          quoteId: projectLike.quoteId || "",
          quoteNo: projectLike.quoteNo || "",
          activityType:
            status === PROJECT_REVIEW_STATUS_COMPLETED ? "review_completed" : "review_skipped",
          title:
            status === PROJECT_REVIEW_STATUS_COMPLETED ? "Review received" : "Review skipped",
          description:
            status === PROJECT_REVIEW_STATUS_COMPLETED
              ? "Customer review/referral marked received."
              : "Review/referral request skipped.",
        }).catch(() => null);
        if (activity) prependProjectActivityLocal(projectId, activity);
        if (
          status === PROJECT_REVIEW_STATUS_COMPLETED ||
          status === PROJECT_REVIEW_STATUS_SKIPPED
        ) {
          removeCrmAttentionTaskById(taskId);
        }
        if (isDevRuntime) {
          devLog("REVIEW_TASK_TRACE", {
            projectId,
            taskId,
            action:
              status === PROJECT_REVIEW_STATUS_COMPLETED ? "completed" : "skipped",
            dueAt: "",
            status,
          });
        }
      }
      notify(
        status === PROJECT_REVIEW_STATUS_PENDING
          ? "Review task created."
          : status === PROJECT_REVIEW_STATUS_COMPLETED
          ? "Review marked received."
          : "Review skipped.",
        "success"
      );
      return true;
    } catch (error) {
      devError("CRM_REVIEW_STATUS_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        projectId,
        status,
      });
      notify("Review status could not be updated.", "error");
      return false;
    } finally {
      setCrmProjectActionKey("");
    }
  },
  [
    canRequestProjectReviewAccess,
    completeCrmTask,
    createReviewRequestTaskForProject,
    crmProjectActionKey,
    getCrmActorSnapshot,
    logCrmActivity,
    notify,
    prependProjectActivityLocal,
    removeCrmAttentionTaskById,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const closeCrmProject = useCallback(
  async (projectLike = {}) => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !projectId || crmProjectActionKey) return null;
    if (!canCloseCrmProjectAccess) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }
    const guard = getProjectCloseGuard(projectLike);
    if (!guard.canClose) {
      notify(guard.reasons[0] || "Project is not ready to close.", "warning");
      return null;
    }
    const actor = getCrmActorSnapshot();
    const actionKey = `close:${projectId}`;
    setCrmProjectActionKey(actionKey);
    try {
      const checklist = createDefaultProjectExecutionChecklist({
        ...(projectLike.checklist || {}),
        installationCompleted: true,
        snagsResolved: true,
        finalPaymentReceived: true,
        projectClosed: true,
      });
      await updateCrmProjectPatch(projectId, {
        stage: PROJECT_STAGE_PROJECT_CLOSED,
        status: "closed",
        closedAt: Timestamp.fromDate(new Date()),
        closedBy: actor.uid || "",
        checklist,
        checklistSummary: calculateProjectChecklistSummary(checklist),
        lastActivityAt: Timestamp.fromDate(new Date()),
        lastActivityType: "project_closed",
        lastActivityLabel: "Project closed",
      });
      const activity = await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        customerId: projectLike.customerId || "",
        customerName: projectLike.customerName || "",
        quoteId: projectLike.quoteId || "",
        quoteNo: projectLike.quoteNo || "",
        activityType: "project_closed",
        title: "Project closed",
        description: "Project was completed and closed.",
      }).catch(() => null);
      if (activity) prependProjectActivityLocal(projectId, activity);
      if (crmSettings.autoCreateReviewTaskAfterClose !== false) {
        await createReviewRequestTaskForProject({
          ...projectLike,
          stage: PROJECT_STAGE_PROJECT_CLOSED,
          status: "closed",
        }).catch((error) => {
          if (isDevRuntime) {
            devWarn("CRM_REVIEW_TASK_AFTER_CLOSE_FAILED_EXACT", {
              projectId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      }
      removeCrmAttentionProjectById(projectId);
      notify("Project closed.", "success");
      return true;
    } catch (error) {
      devError("CRM_PROJECT_CLOSE_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        projectId,
      });
      notify("Project could not be closed.", "error");
      return false;
    } finally {
      setCrmProjectActionKey("");
    }
  },
  [
    canCloseCrmProjectAccess,
    createReviewRequestTaskForProject,
    crmProjectActionKey,
    crmSettings.autoCreateReviewTaskAfterClose,
    getCrmActorSnapshot,
    getProjectCloseGuard,
    logCrmActivity,
    notify,
    prependProjectActivityLocal,
    removeCrmAttentionProjectById,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const createMissingDefaultOrderTasks = useCallback(
  async (projectLike = {}) => {
    const projectId = String(projectLike?.projectId || projectLike?.id || "").trim();
    if (!workspaceUid || !projectId || creatingOrderTaskTemplateForProjectId) return null;
    if (crmSettings.enableOrderTaskTemplates === false) {
      notify("Order task templates are disabled in CRM settings.", "warning");
      return null;
    }
    setCreatingOrderTaskTemplateForProjectId(projectId);
    try {
      const result = await createOrderTemplateTasksForProject(projectLike, {
        manual: true,
      });
      if (result.tasksCreated > 0) {
        notify(`Created ${result.tasksCreated} missing order task${result.tasksCreated === 1 ? "" : "s"}.`, "success");
      } else {
        notify("Default order tasks are already up to date.", "success");
      }
      return result;
    } catch (error) {
      devError("CRM_ORDER_TASK_TEMPLATE_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        projectId,
      });
      notify("Could not create order tasks. Please try again.", "error");
      return null;
    } finally {
      setCreatingOrderTaskTemplateForProjectId("");
    }
  },
  [
    creatingOrderTaskTemplateForProjectId,
    createOrderTemplateTasksForProject,
    crmSettings.enableOrderTaskTemplates,
    notify,
    workspaceUid,
  ]
);

const ensureCrmLeadForExecutionSpecs = useCallback(
  async (dealLike = {}, executionSpecsOverride = null) => {
    if (!workspaceUid) return null;

    if (dealLike?.crmRecordType === "lead" && dealLike?.id) {
      return crmLeadRecords.find((lead) => lead.id === dealLike.id) || dealLike;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const directQuoteId =
      dealLike?.crmRecordType === "quote" ? dealLike?.id || "" : "";
    const sourceQuote =
      savedQuotes.find(
        (quote) =>
          directQuoteId && String(quote.id || "") === String(directQuoteId)
      ) ||
      savedQuotes.find((quote) =>
        String(quote.id || "") ===
        String(primaryLinkedQuote?.quoteId || dealLike?.quoteId || "")
      ) ||
      savedQuotes.find(
        (quote) =>
          String(quote.quoteNo || "") &&
          String(quote.quoteNo || "") === String(primaryLinkedQuote?.quoteNo || dealLike?.quoteNo || "")
      ) ||
      null;

    if (!sourceQuote) return null;

    const existingLead = findLinkedCrmLeadForQuote(sourceQuote);
    if (existingLead?.id) {
      setSelectedCrmDealId(existingLead.id);
      return existingLead;
    }

    const quoteCrm = buildQuoteCrmData(sourceQuote);
    const leadId = buildQuoteCreatedLeadId(sourceQuote.id || sourceQuote.quoteNo || "");
    const deterministicLeadSnap = await getDoc(
      doc(db, "users", workspaceUid, "crm_leads", leadId)
    ).catch(() => null);
    if (deterministicLeadSnap?.exists?.()) {
      const deterministicLead = {
        id: leadId,
        ...deterministicLeadSnap.data(),
      };
      setSelectedCrmDealId(leadId);
      return deterministicLead;
    }
    const leadPayload = buildCrmLeadData(
      {
        id: leadId,
        createdAt: new Date(),
      },
      {
        title: quoteCrm.title,
        companyOrProject:
          sourceQuote?.customerName ||
          quoteCrm.contact?.name ||
          "",
        contact: quoteCrm.contact,
        source: quoteCrm.source,
        quoteType: quoteCrm.pipelineType,
        stageId: quoteCrm.stageId,
        linkedQuotes: normalizeCrmLinkedQuotes({}, {
          linkedQuotes: [
            {
              quoteId: sourceQuote.id || "",
              quoteNo: sourceQuote.quoteNo || "",
              type: quoteCrm.pipelineType,
              value: Number(quoteCrm.finalValue || quoteCrm.expectedValue || 0),
              isPrimary: true,
            },
          ],
        }),
        hasQuote: true,
        checklistItems: quoteCrm.checklistItems,
        expectedValue: Number(quoteCrm.expectedValue || 0),
        finalValue: Number(quoteCrm.finalValue || 0),
        nextAction: quoteCrm.nextAction,
        nextActionDate: quoteCrm.nextActionDate,
        installationDate: quoteCrm.installationDate,
        assignedToUserId: quoteCrm.assignedToUserId,
        assignedToNameSnapshot: quoteCrm.assignedToNameSnapshot,
        assignedAt: quoteCrm.assignedAt,
        assignedBy: quoteCrm.assignedBy,
        notes: quoteCrm.notes,
        tags: quoteCrm.tags,
        reminderConfigs: quoteCrm.reminderConfigs,
        followUps: quoteCrm.followUps,
        attachments: quoteCrm.attachments,
        siteLocations: quoteCrm.siteLocations,
        executionSpecs:
          executionSpecsOverride != null
            ? normalizeExecutionSpecs(executionSpecsOverride)
            : normalizeExecutionSpecs(dealLike?.executionSpecs || quoteCrm.executionSpecs || []),
      }
    );

    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const leadCreatePayload = sanitizeCrmFirestoreValue({
      title: leadPayload.title,
      contact: leadPayload.contact,
      companyOrProject: leadPayload.companyOrProject,
      source: leadPayload.source,
      quoteType: leadPayload.quoteType,
      pipelineType: leadPayload.pipelineType,
      stageId: leadPayload.stageId,
      salesStageId: leadPayload.salesStageId || "new_lead",
      opportunityStage: leadPayload.opportunityStage || leadPayload.salesStageId || "new_lead",
      salesStatus: leadPayload.salesStatus || CRM_OPPORTUNITY_STATUS_OPEN,
      stageNameSnapshot: leadPayload.stageNameSnapshot,
      customerId: leadPayload.customerId || sourceQuote.customerId || "",
      customerName: leadPayload.customerName || sourceQuote.customerName || "",
      customerPhone: leadPayload.customerPhone || sourceQuote.customerPhone || "",
      linkedQuotes: leadPayload.linkedQuotes || [],
      hasQuote: true,
      checklistTemplateId: leadPayload.checklistTemplateId || "",
      checklistItems: normalizeChecklistTree(leadPayload.checklistItems || []),
      expectedValue: Number(leadPayload.expectedValue || 0),
      finalValue: Number(leadPayload.finalValue || 0),
      nextAction: leadPayload.nextAction || "",
      nextActionDate: leadPayload.nextActionDate || null,
      assignedToUserId: leadPayload.assignedToUserId || "",
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot || "",
      assignedAt: leadPayload.assignedAt || null,
      assignedBy: leadPayload.assignedBy || "",
      tags: leadPayload.tags || [],
      notes: leadPayload.notes || "",
      installationDate: leadPayload.installationDate || null,
      reminderConfigs: leadPayload.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      followUps: leadPayload.followUps || [],
      attachments: leadPayload.attachments || [],
      executionSpecs: leadPayload.executionSpecs || [],
      siteLocations: leadPayload.siteLocations || [],
      status: leadPayload.status || "draft",
    });
    const localLeadActivityAt = new Date();

    await setDoc(leadRef, {
      ...leadCreatePayload,
      ...(isDevRuntime
        ? {
            _lastWriteSource: "ensureCrmLeadForExecutionSpecs",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    });

    await upsertCustomerSearchIndexRecord(
      {
        ...leadPayload,
        ...leadCreatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        latestQuoteId: sourceQuote.id || "",
        latestQuoteNo: sourceQuote.quoteNo || "",
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        devError("CUSTOMER SEARCH INDEX EXECUTION CRM UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    setCrmLeadRecords((prev) => [
      {
        ...leadPayload,
        id: leadId,
        createdAt: localLeadActivityAt,
        updatedAt: localLeadActivityAt,
        lastActivityAt: localLeadActivityAt,
        lastEditedAt: localLeadActivityAt,
        lastEditedBy: user?.uid || "",
        lastEditedByName: user?.displayName || user?.email || "",
      },
      ...prev,
    ]);

    const nextQuoteCrm = buildQuoteCrmData(sourceQuote, {
      leadId,
      hasQuote: true,
      title: leadPayload.title,
      contact: leadPayload.contact,
      stageId: leadPayload.stageId,
      status: leadPayload.status,
      nextAction: leadPayload.nextAction,
      nextActionDate: leadPayload.nextActionDate,
      installationDate: leadPayload.installationDate,
      checklistItems: leadPayload.checklistItems,
      reminderConfigs: leadPayload.reminderConfigs,
      attachments: leadPayload.attachments,
      assignedToUserId: leadPayload.assignedToUserId,
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot,
      assignedAt: leadPayload.assignedAt,
      assignedBy: leadPayload.assignedBy,
      notes: leadPayload.notes,
      tags: leadPayload.tags,
      source: leadPayload.source,
      siteLocations: leadPayload.siteLocations,
    });

    await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
      crm: nextQuoteCrm,
      updatedAt: serverTimestamp(),
    });
    await setDoc(
      doc(
        db,
        "users",
        workspaceUid,
        QUOTE_DEAL_LINKS_COLLECTION,
        getQuoteDealLinkDocId(sourceQuote.id)
      ),
      {
        quoteId: sourceQuote.id,
        quoteNo: sourceQuote.quoteNo || "",
        leadId,
        customerId: sourceQuote.customerId || "",
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      devWarn("Execution CRM lead created, but quote deal link mapping failed:", error);
    });

    applyQuoteLocalPatch(sourceQuote.id, {
      crm: nextQuoteCrm,
      updatedAt: new Date(),
      lastActivityAt: new Date(),
      lastEditedAt: new Date(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    });

    await syncReminderDocsForDeal({
      ...leadPayload,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        leadPayload.contact?.name ||
        leadPayload.companyOrProject ||
        leadPayload.title ||
        "Unnamed Lead",
      crmTitle: leadPayload.title,
      crmContact: leadPayload.contact,
      primaryLinkedQuote: getPrimaryCrmLinkedQuote(leadPayload.linkedQuotes || []),
      nextActionDate: leadPayload.nextActionDate,
      installationDate: leadPayload.installationDate,
      reminderConfigs: leadPayload.reminderConfigs,
      followUps: leadPayload.followUps,
      attachments: leadPayload.attachments,
      siteLocations: leadPayload.siteLocations,
      pipelineType: leadPayload.pipelineType,
    }).catch((error) => {
      devWarn("CRM lead created, but reminder sync failed:", error);
    });

    await ensureOpportunityForLead({
      ...leadPayload,
      ...leadCreatePayload,
      id: leadId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }).catch((error) => {
      if (isDevRuntime) {
        devWarn("Execution CRM lead created, but opportunity sync failed", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });

    setSelectedCrmDealId(leadId);

    return {
      ...leadPayload,
      id: leadId,
    };
  },
  [
    applyQuoteLocalPatch,
    buildCrmLeadData,
    buildQuoteCrmData,
    crmLeadRecords,
    findLinkedCrmLeadForQuote,
    ensureOpportunityForLead,
    savedQuotes,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const saveCrmFieldsForLead = useCallback(
  async (leadId, crmOverrides = {}, options = {}) => {
    if (!workspaceUid || !leadId) {
      if (options.requireWrite === true) {
        throw new Error("CRM lead save failed: missing workspace or lead id.");
      }
      return null;
    }

    let sourceLead =
      options.sourceLead ||
      crmLeadRecords.find((item) => item.id === leadId) ||
      null;

    if (!sourceLead) {
      try {
        const leadSnap = await getDoc(
          doc(db, "users", workspaceUid, "crm_leads", leadId)
        );
        if (leadSnap.exists()) {
          sourceLead = {
            id: leadId,
            ...leadSnap.data(),
          };
        }
      } catch (error) {
        devError("Failed to load CRM lead before saving:", error);
      }
    }

    if (!sourceLead) {
      if (options.createIfMissing !== true) {
        if (options.requireWrite === true) {
          throw new Error(`CRM lead save failed: lead ${leadId} was not found.`);
        }
        return null;
      }
      sourceLead = {
        id: leadId,
        ...crmOverrides,
        createdAt: null,
        updatedAt: new Date(),
      };
    }

    if (options.assignmentOnly === true) {
      if (!canAssignLeadOwnerAccess) throw new Error("Owner assignment is restricted.");
      const response = await httpsCallable(functions, "assignCrmDealOwner")({
        workspaceUid, leadId, ownerUid: crmOverrides.ownerUid || "",
        memberId: crmOverrides.assignedToUserId || "", confirm: true,
      });
      if (response.data?.ok !== true || !response.data.assignmentPatch) throw new Error("Owner assignment was not confirmed.");
      const patch = { ...response.data.assignmentPatch,
        assignedAt: response.data.assignmentPatch.ownerUid ? new Date() : null };
      if (crmWorkspaceRef.current === workspaceUid) {
        applyCrmLeadLocalPatch(leadId, { ...patch, updatedAt: new Date(), lastActivityAt: new Date() });
      }
      invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
      invalidateWorkspaceCollectionCache(workspaceUid, "activity_log");
      return { leadId, assignmentSaved: true, assignmentPatch: patch };
    }

    const nextLead = buildCrmLeadData(sourceLead, crmOverrides);
    const linkedQuoteForValue = findLinkedQuoteForLead(nextLead);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(nextLead.linkedQuotes || []);
    const latestLinkedQuoteTotal = linkedQuoteForValue
      ? getNormalizedQuoteTotal(linkedQuoteForValue)
      : 0;
    const syncedExpectedValue =
      latestLinkedQuoteTotal > 0
        ? latestLinkedQuoteTotal
        : Number(nextLead.expectedValue || 0);
    const syncedFinalValue =
      latestLinkedQuoteTotal > 0
        ? latestLinkedQuoteTotal
        : Number(nextLead.finalValue || syncedExpectedValue || 0);
    const syncedLinkedQuotes = (nextLead.linkedQuotes || []).map((entry) =>
      entry.isPrimary
        ? { ...entry, value: syncedFinalValue || Number(entry.value || 0) }
        : entry
    );
    const syncedLinkedQuoteIds = uniqueValues(
      syncedLinkedQuotes.map((entry) => String(entry.quoteId || "").trim())
    );
    const syncedLinkedQuoteNos = uniqueValues(
      syncedLinkedQuotes.map((entry) => String(entry.quoteNo || "").trim())
    );
    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const searchIndexFields = buildCrmSearchIndexFields({
      ...nextLead,
      linkedQuotes: syncedLinkedQuotes,
    });
    const persistedChecklistItems = preserveChecklistDraftTree(
      nextLead.checklistItems || [],
      {
        templateMode: false,
        idPrefix: "crm-check",
      }
    );
    const persistedInstallationReminder =
      nextLead.installationReminder ||
      buildCanonicalInstallationReminder(nextLead, {
        installationDate: nextLead.installationDate,
        reminderConfigs: nextLead.reminderConfigs,
      });
    const hasOverrideKey = (key) =>
      Object.prototype.hasOwnProperty.call(crmOverrides || {}, key);
    const isNewLeadWrite = !sourceLead?.createdAt;
    const persistFullCrmDetails = options.persistFullCrmDetails === true;
    const shouldPersistChecklist =
      persistFullCrmDetails ||
      options.persistChecklist === true ||
      isNewLeadWrite ||
      hasOverrideKey("checklistItems") ||
      hasOverrideKey("checklist") ||
      hasOverrideKey("stageChecklist") ||
      hasOverrideKey("checklistTemplateId");
    const shouldPersistInstallationDate =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("installationDate");
    const shouldPersistInstallationReminder =
      persistFullCrmDetails ||
      options.persistInstallationReminder === true ||
      isNewLeadWrite ||
      hasOverrideKey("installationReminder") ||
      hasOverrideKey("reminderConfig") ||
      hasOverrideKey("reminderConfigs");
    const shouldPersistFollowUps =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("followUps");
    const shouldPersistAttachments =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("attachments");
    const shouldPersistExecutionSpecs =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("executionSpecs");
    const shouldPersistSiteLocations =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("siteLocations");
    let updatePayload = {
      title: nextLead.title,
      contact: nextLead.contact,
      companyOrProject: nextLead.companyOrProject,
      source: nextLead.source,
      customerId: nextLead.customerId || "",
      customerKey: nextLead.customerKey || nextLead.customerId || "",
      customerName: nextLead.customerName || nextLead.contact?.name || "",
      customerPhone:
        nextLead.customerPhone ||
        nextLead.contact?.phone ||
        nextLead.contact?.whatsapp ||
        "",
      customerWhatsapp:
        nextLead.customerWhatsapp ||
        nextLead.contact?.whatsapp ||
        "",
      customerEmail:
        nextLead.customerEmail ||
        nextLead.contact?.email ||
        "",
      customerAddress:
        nextLead.customerAddress ||
        nextLead.contact?.address ||
        "",
      customerCompanyName:
        nextLead.customerCompanyName ||
        nextLead.companyOrProject ||
        "",
      quoteType: nextLead.quoteType,
      pipelineType: nextLead.pipelineType,
      stageId: nextLead.stageId,
      stage: nextLead.stageId,
      salesStageId: nextLead.salesStageId || normalizeSalesStageIdentityValue(nextLead.stageId),
      opportunityStage: nextLead.opportunityStage || nextLead.salesStageId || normalizeSalesStageIdentityValue(nextLead.stageId),
      salesStatus: nextLead.salesStatus || normalizeOpportunityStatus(nextLead.status || ""),
      stageNameSnapshot: nextLead.stageNameSnapshot,
      linkedQuotes: syncedLinkedQuotes,
      ...createLegacyCrmQuoteLinkDeletePatch(),
      crmLeadId: leadId,
      quoteId: primaryLinkedQuote?.quoteId || syncedLinkedQuoteIds[0] || "",
      quoteNo: primaryLinkedQuote?.quoteNo || syncedLinkedQuoteNos[0] || "",
      linkedQuoteIds: syncedLinkedQuoteIds,
      linkedQuoteNos: syncedLinkedQuoteNos,
      dealValue: syncedFinalValue,
      hasQuote: nextLead.hasQuote === true,
      expectedValue: syncedExpectedValue,
      finalValue: syncedFinalValue,
      nextAction: nextLead.nextAction || "",
      nextActionDate: nextLead.nextActionDate || null,
      assignedToUserId: nextLead.assignedToUserId || "",
      assignedToNameSnapshot: nextLead.assignedToNameSnapshot || "",
      assignedTo: nextLead.assignedToNameSnapshot || "",
      assignedAt: nextLead.assignedAt || null,
      assignedBy: nextLead.assignedBy || "",
      tags: Array.isArray(nextLead.tags) ? nextLead.tags : [],
      notes: nextLead.notes || "",
      ...searchIndexFields,
      status: nextLead.status || "draft",
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    };

    if (shouldPersistChecklist) {
      updatePayload.checklistTemplateId = nextLead.checklistTemplateId || "";
      updatePayload.checklistItems = persistedChecklistItems;
      updatePayload.checklist = persistedChecklistItems;
      updatePayload.stageChecklist = persistedChecklistItems;
    }

    if (shouldPersistInstallationDate) {
      updatePayload.installationDate = nextLead.installationDate || null;
    }

    if (shouldPersistInstallationReminder) {
      updatePayload.installationReminder = persistedInstallationReminder;
      updatePayload.reminderConfig = persistedInstallationReminder;
      updatePayload.reminderConfigs =
        nextLead.reminderConfigs || normalizeCrmReminderConfigs({}, {});
    }

    if (shouldPersistFollowUps) {
      updatePayload.followUps = sanitizeCrmFirestoreValue(nextLead.followUps || []);
    }

    if (shouldPersistAttachments) {
      updatePayload.attachments = nextLead.attachments || [];
    }

    if (shouldPersistExecutionSpecs) {
      updatePayload.executionSpecs = nextLead.executionSpecs || [];
    }

    if (shouldPersistSiteLocations) {
      updatePayload.siteLocations = sanitizeCrmFirestoreValue(nextLead.siteLocations || []);
    }

    if (isDevRuntime) {
      updatePayload._lastWriteSource = options.writeSource || "saveCrmFieldsForLead";
      updatePayload._lastWriteAt = serverTimestamp();
    }

    if (isDevRuntime && options.writeSource === "saveCrmDetails") {
      devLog("WRITING CRM DETAILS DOC", leadRef.path, updatePayload);
    }

    const proposedPayload = updatePayload;
    updatePayload = await runTransaction(db, async (transaction) => {
      const current = await transaction.get(leadRef);
      const patch = { ...proposedPayload };
      if (current.exists()) {
        // A note/stage/follow-up save must not replay stale assignment snapshots.
        ["assignedToUserId", "assignedToNameSnapshot", "assignedTo", "assignedAt", "assignedBy"]
          .forEach((field) => delete patch[field]);
      } else {
        if (options.createIfMissing !== true) throw new Error("This CRM lead no longer exists. Refresh before saving.");
        patch.createdAt = serverTimestamp();
      }
      transaction.set(leadRef, patch, { merge: true });
      return patch;
    });
    await upsertCustomerSearchIndexRecord(
      {
        ...nextLead,
        ...updatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        latestQuoteId: primaryLinkedQuote?.quoteId || "",
        latestQuoteNo: primaryLinkedQuote?.quoteNo || "",
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        devError("CUSTOMER SEARCH INDEX CRM UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });

    const savedLeadSnap = await getDoc(leadRef);
    if (!savedLeadSnap.exists()) {
      throw new Error(`CRM lead save verification failed: lead ${leadId} was not found after write.`);
    }

    const savedLeadRaw = {
      id: leadId,
      ...savedLeadSnap.data(),
    };
    const savedLeadData = buildCrmLeadData(savedLeadRaw);

    const localLeadPatch = {
      ...savedLeadData,
      id: leadId,
      updatedAt: new Date(),
      lastActivityAt: new Date(),
      lastEditedAt: new Date(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    };

    if (!sourceLead?.createdAt) {
      localLeadPatch.createdAt = new Date();
    }

    applyCrmLeadLocalPatch(leadId, localLeadPatch);
    const attentionIdentityIds = [
      leadId,
      savedLeadData.id,
      savedLeadData.opportunityId,
      savedLeadData.legacyLeadId,
      savedLeadData.leadId,
      getCrmOpportunityIdFromLead({ ...savedLeadData, id: leadId }),
    ];
    const hasSavedNextAction =
      Boolean(String(savedLeadData.nextAction || localLeadPatch.nextAction || "").trim()) ||
      Boolean(getPrimaryPendingCrmFollowUp(normalizeCrmFollowUps(savedLeadData.followUps || [])));
    if (hasSavedNextAction) {
      removeCrmAttentionOpportunityById(attentionIdentityIds, ["missingNextAction"]);
    }
    if (String(savedLeadData.assignedToUserId || localLeadPatch.assignedToUserId || "").trim()) {
      removeCrmAttentionOpportunityById(attentionIdentityIds, ["missingOwner"]);
    }
    const reminderSyncDeal = {
      ...savedLeadData,
      ...localLeadPatch,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        savedLeadData.contact?.name ||
        savedLeadData.companyOrProject ||
        savedLeadData.title ||
        "Unnamed Lead",
      crmTitle: savedLeadData.title,
      crmContact: savedLeadData.contact,
      primaryLinkedQuote,
      nextActionDate: savedLeadData.nextActionDate,
      installationDate: savedLeadData.installationDate,
      reminderConfigs: savedLeadData.reminderConfigs,
      followUps: savedLeadData.followUps,
      attachments: savedLeadData.attachments,
      siteLocations: savedLeadData.siteLocations,
      pipelineType: savedLeadData.pipelineType,
    };

    if (options.skipReminderSync !== true) {
      await syncReminderDocsForDeal(reminderSyncDeal);
    }

    if (options.skipOpportunitySync !== true) {
      await ensureOpportunityForLead({
        ...savedLeadData,
        ...localLeadPatch,
        id: leadId,
        linkedQuotes: syncedLinkedQuotes,
        expectedValue: syncedExpectedValue,
        finalValue: syncedFinalValue,
      }).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM lead saved, but opportunity sync failed", {
            leadId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    }

    if (options.syncQuote === false || !primaryLinkedQuote?.quoteId) {
      return {
        leadId,
        firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
        updatePayload,
        savedLead: savedLeadData,
        savedLeadRaw,
      };
    }

    const sourceQuote =
      savedQuotes.find((item) => item.id === primaryLinkedQuote.quoteId) || null;
    if (!sourceQuote) {
      return {
        leadId,
        firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
        updatePayload,
        savedLead: savedLeadData,
        savedLeadRaw,
      };
    }
    const latestQuoteTotal = getNormalizedQuoteTotal(sourceQuote);

    const nextStageMeta = getStageMetaForType(
      nextLead.pipelineType,
      nextLead.stageId
    );
    const nextStatus =
      nextStageMeta?.confirmsQuote === true ||
      String(sourceQuote?.status || "").toLowerCase() === "confirmed"
        ? "confirmed"
        : "draft";
    const nextCrmPayload = buildQuoteCrmData(
      {
        ...sourceQuote,
        workStage: nextLead.stageId,
        status: nextStatus,
        installationDate: nextLead.installationDate || null,
      },
      {
        stageId: nextLead.stageId,
        status: nextStatus,
        installationDate: nextLead.installationDate || null,
        title: nextLead.title,
        contact: nextLead.contact,
        nextAction: nextLead.nextAction,
        nextActionDate: nextLead.nextActionDate,
        notes: nextLead.notes,
        tags: nextLead.tags,
        checklistItems: nextLead.checklistItems,
        reminderConfigs: nextLead.reminderConfigs,
        followUps: nextLead.followUps,
        attachments: nextLead.attachments,
        assignedToUserId: nextLead.assignedToUserId,
        assignedToNameSnapshot: nextLead.assignedToNameSnapshot,
        assignedTo: nextLead.assignedToNameSnapshot,
        assignedAt: nextLead.assignedAt,
        assignedBy: nextLead.assignedBy,
        leadId: nextLead.id,
        hasQuote: nextLead.hasQuote === true,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
      }
    );

    const quoteRef = doc(db, "users", workspaceUid, "quotations", primaryLinkedQuote.quoteId);
    await updateDoc(quoteRef, {
      workStage: nextLead.stageId,
      status: nextStatus,
      installationDate: nextLead.installationDate || null,
      crm: nextCrmPayload,
      crmLeadId: leadId,
      leadId,
      customerId: nextLead.customerId || sourceQuote.customerId || "",
      customerKey:
        nextLead.customerKey ||
        sourceQuote.customerKey ||
        nextLead.customerId ||
        sourceQuote.customerId ||
        "",
      customerName: nextLead.customerName || sourceQuote.customerName || "",
      customerPhone: nextLead.customerPhone || sourceQuote.customerPhone || "",
      customerWhatsapp: nextLead.customerWhatsapp || sourceQuote.customerWhatsapp || "",
      customerEmail: nextLead.customerEmail || sourceQuote.customerEmail || "",
      customerAddress: nextLead.customerAddress || sourceQuote.customerAddress || "",
      customerCompanyName:
        nextLead.customerCompanyName || sourceQuote.customerCompanyName || "",
      customerLedgerLinked: Boolean(nextLead.customerId || sourceQuote.customerId),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    });

    applyQuoteLocalPatch(primaryLinkedQuote.quoteId, {
      workStage: nextLead.stageId,
      status: nextStatus,
      installationDate: nextLead.installationDate || null,
      crm: nextCrmPayload,
      crmLeadId: leadId,
      leadId,
      customerId: nextLead.customerId || sourceQuote.customerId || "",
      customerKey:
        nextLead.customerKey ||
        sourceQuote.customerKey ||
        nextLead.customerId ||
        sourceQuote.customerId ||
        "",
      customerName: nextLead.customerName || sourceQuote.customerName || "",
      customerPhone: nextLead.customerPhone || sourceQuote.customerPhone || "",
      customerWhatsapp: nextLead.customerWhatsapp || sourceQuote.customerWhatsapp || "",
      customerEmail: nextLead.customerEmail || sourceQuote.customerEmail || "",
      customerAddress: nextLead.customerAddress || sourceQuote.customerAddress || "",
      customerCompanyName:
        nextLead.customerCompanyName || sourceQuote.customerCompanyName || "",
      customerLedgerLinked: Boolean(nextLead.customerId || sourceQuote.customerId),
      updatedAt: new Date(),
      lastActivityAt: new Date(),
      lastEditedAt: new Date(),
      lastEditedBy: user?.uid || "",
      lastEditedByName: user?.displayName || user?.email || "",
    });

    return {
      leadId,
      firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
      updatePayload,
      savedLead: savedLeadData,
      savedLeadRaw,
    };
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    buildCanonicalInstallationReminder,
    buildCrmLeadData,
    buildQuoteCrmData,
    crmLeadRecords,
    canAssignLeadOwnerAccess,
    findLinkedQuoteForLead,
    getNormalizedQuoteTotal,
    getCrmOpportunityIdFromLead,
    getStageMetaForType,
    ensureOpportunityForLead,
    removeCrmAttentionOpportunityById,
    savedQuotes,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const saveCrmFieldsForDeal = useCallback(
  async (dealId, crmOverrides = {}, options = {}) => {
    const sourceLead =
      options.sourceLead ||
      crmLeadRecords.find((deal) => deal.id === dealId) || null;

    if (options.forceLead === true || sourceLead) {
      return await saveCrmFieldsForLead(dealId, crmOverrides, {
        ...options,
        sourceLead,
      });
    }

    return await saveCrmFieldsForQuote(dealId, crmOverrides, options);
  },
  [crmLeadRecords, saveCrmFieldsForLead, saveCrmFieldsForQuote]
);

const performSavedQuoteAction = useCallback(
  async ({ quote, action, updates = {} } = {}) => {
    if (!canEditQuotes) {
      throw new Error("You do not have permission to update quotations in this workspace.");
    }
    if (!workspaceUid) {
      throw new Error("Workspace is not ready. Please refresh and try again.");
    }
    if (!quote || typeof quote !== "object") {
      throw new Error("Quotation card data is missing.");
    }

    const identity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
    if (!identity.quoteIndexId) {
      throw new Error("Saved quote index id is missing.");
    }

    const toDateOrNull = (value) => {
      if (!value) return null;
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      if (typeof value?.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const now = new Date();
    const quoteType = resolveQuoteType(quote);
    const indexRef = doc(db, "users", workspaceUid, "quote_index", identity.quoteIndexId);
    const fullQuoteRef = identity.fullQuoteId
      ? doc(db, "users", workspaceUid, "quotations", identity.fullQuoteId)
      : null;
    const updatedBy = auth.currentUser?.uid || "";
    let indexPatch = {};
    let fullQuotePatch = {};
    let localPatch = {};
    let crmPatch = null;
    let successMessage = "Quotation updated";

    if (action === "updateStage") {
      const nextStage = String(updates.stage || updates.workStage || "").trim();
      if (!nextStage) throw new Error("Stage is missing.");
      const nextStageMeta = getStageMetaForType(quoteType, nextStage);
      const confirmsQuote = nextStageMeta?.confirmsQuote === true;
      const previousConfirmedAt = toDateOrNull(quote.confirmedAt || quote.crm?.confirmedAt);
      const nextConfirmedAt = confirmsQuote ? previousConfirmedAt || now : previousConfirmedAt;
      const nextStatus =
        confirmsQuote || String(quote?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);
      crmPatch = buildQuoteCrmData(
        {
          ...quote,
          workStage: nextStage,
          status: nextStatus,
          confirmedAt: nextConfirmedAt || null,
        },
        {
          stageId: nextStage,
          status: nextStatus,
          confirmedAt: nextConfirmedAt || null,
          checklistItems: nextChecklistItems,
        }
      );

      indexPatch = {
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        status: nextStatus,
        confirmed: nextStatus === "confirmed",
        confirmedAt: nextStatus === "confirmed" ? Timestamp.fromDate(nextConfirmedAt || now) : null,
        crm: crmPatch,
      };
      fullQuotePatch = {
        workStage: nextStage,
        workStageUpdatedAt: serverTimestamp(),
        status: nextStatus,
        confirmedAt: nextStatus === "confirmed" ? Timestamp.fromDate(nextConfirmedAt || now) : null,
        crm: crmPatch,
      };
      localPatch = {
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        status: nextStatus,
        confirmed: nextStatus === "confirmed",
        confirmedAt: nextStatus === "confirmed" ? nextConfirmedAt || now : null,
        crm: crmPatch,
      };
      successMessage = "Stage updated";
    } else if (action === "confirmQuote") {
      const providedTotals = updates.totals || {};
      const fallbackGrandTotal = Number(
        quote?.grandTotal ??
          quote?.total ??
          quote?.finalTotal ??
          quote?.finalAmount ??
          quote?.totalAmount ??
          quote?.subtotal ??
          0
      );
      const totals = {
        subtotal: Number(
          providedTotals.subtotal ??
            quote?.subtotal ??
            quote?.taxableAmount ??
            fallbackGrandTotal ??
            0
        ),
        grandTotal: Number(providedTotals.grandTotal ?? fallbackGrandTotal ?? 0),
      };
      const stageOptions = getStageSelectOptions(
        crmPipelines,
        quoteType,
        quote.workStage || quote.crm?.stageId
      );
      const confirmationStage =
        stageOptions.find((stage) => getStageMetaForType(quoteType, stage.id)?.confirmsQuote) ||
        null;
      const currentStage = quote.workStage || quote.crm?.stageId || "";
      const currentStageMeta = currentStage
        ? getStageMetaForType(quoteType, currentStage)
        : null;
      const nextStage =
        currentStageMeta?.confirmsQuote === true
          ? currentStage
          : confirmationStage?.id || currentStage || "confirmed";
      const nextStageMeta = getStageMetaForType(quoteType, nextStage);
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);
      crmPatch = buildQuoteCrmData(
        {
          ...quote,
          workStage: nextStage,
          status: "confirmed",
          confirmedAt: now,
        },
        {
          stageId: nextStage,
          status: "confirmed",
          confirmedAt: now,
          checklistItems: nextChecklistItems,
          expectedValue: Number(totals.grandTotal || 0),
          finalValue: Number(totals.grandTotal || 0),
        }
      );

      indexPatch = {
        status: "confirmed",
        confirmed: true,
        confirmedAt: Timestamp.fromDate(now),
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        subtotal: Number(totals.subtotal || 0),
        grandTotal: Number(totals.grandTotal || 0),
        totalAmount: Number(totals.grandTotal || 0),
        total: Number(totals.grandTotal || 0),
        finalTotal: Number(totals.grandTotal || 0),
        finalAmount: Number(totals.grandTotal || 0),
        crm: crmPatch,
      };
      fullQuotePatch = {
        status: "confirmed",
        confirmed: true,
        confirmedAt: Timestamp.fromDate(now),
        workStage: nextStage,
        subtotal: Number(totals.subtotal || 0),
        grandTotal: Number(totals.grandTotal || 0),
        crm: crmPatch,
      };
      localPatch = {
        status: "confirmed",
        confirmed: true,
        confirmedAt: now,
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        subtotal: Number(totals.subtotal || 0),
        grandTotal: Number(totals.grandTotal || 0),
        totalAmount: Number(totals.grandTotal || 0),
        total: Number(totals.grandTotal || 0),
        finalTotal: Number(totals.grandTotal || 0),
        finalAmount: Number(totals.grandTotal || 0),
        crm: crmPatch,
      };
      successMessage = "Quote confirmed";
    } else if (action === "unconfirmQuote") {
      const nextStage = quote.workStage || quote.crm?.stageId || getQuoteGivenStageIdForType(quoteType);
      const nextStageMeta = getStageMetaForType(quoteType, nextStage);
      crmPatch = buildQuoteCrmData(
        {
          ...quote,
          status: "draft",
          confirmedAt: null,
          workStage: nextStage,
        },
        {
          stageId: nextStage,
          status: "draft",
          confirmedAt: null,
          checklistItems: createChecklistFromStage(nextStageMeta),
        }
      );
      indexPatch = {
        status: "draft",
        confirmed: false,
        confirmedAt: null,
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        crm: crmPatch,
      };
      fullQuotePatch = {
        status: "draft",
        confirmed: false,
        confirmedAt: null,
        workStage: nextStage,
        crm: crmPatch,
      };
      localPatch = {
        status: "draft",
        confirmed: false,
        confirmedAt: null,
        workStage: nextStage,
        stage: nextStage,
        workflowStage: nextStage,
        stageId: nextStage,
        crm: crmPatch,
      };
      successMessage = "Quote reverted to draft";
    } else if (action === "updateInstallationDate") {
      const nextDate = parseDateInputValue(updates.installationDate);
      crmPatch = buildQuoteCrmData(quote, {
        installationDate: nextDate,
      });
      indexPatch = {
        installationDate: nextDate ? Timestamp.fromDate(nextDate) : null,
        crm: crmPatch,
      };
      fullQuotePatch = {
        installationDate: nextDate ? Timestamp.fromDate(nextDate) : null,
        crm: crmPatch,
      };
      localPatch = {
        installationDate: nextDate,
        crm: crmPatch,
      };
      successMessage = "Installation date saved";
    } else if (action === "updateFollowUpDate") {
      const nextDate = parseDateInputValue(updates.followUpDate);
      indexPatch = {
        followUpDate: nextDate ? Timestamp.fromDate(nextDate) : null,
      };
      fullQuotePatch = {
        followUpDate: nextDate ? Timestamp.fromDate(nextDate) : null,
      };
      localPatch = {
        followUpDate: nextDate,
      };
      successMessage = "Follow-up date saved";
    } else if (action === "moveToBin") {
      indexPatch = {
        deleted: true,
        archived: true,
        binStatus: "in_bin",
        visibilityStatus: "bin",
        deletedAt: serverTimestamp(),
        movedToBinAt: serverTimestamp(),
        restoredAt: null,
      };
      fullQuotePatch = {
        deleted: true,
        archived: true,
        binStatus: "in_bin",
        visibilityStatus: "bin",
        deletedAt: serverTimestamp(),
        movedToBinAt: serverTimestamp(),
        restoredAt: null,
      };
      localPatch = {
        deleted: true,
        archived: true,
        binStatus: "in_bin",
        visibilityStatus: "bin",
        deletedAt: now,
        movedToBinAt: now,
        restoredAt: null,
      };
      successMessage = "Quotation moved to Recycle Bin";
    } else if (action === "restoreFromBin") {
      indexPatch = {
        deleted: false,
        archived: false,
        binStatus: "active",
        visibilityStatus: "active",
        deletedAt: null,
        movedToBinAt: null,
        restoredAt: serverTimestamp(),
      };
      fullQuotePatch = {
        deleted: false,
        archived: false,
        binStatus: "active",
        visibilityStatus: "active",
        deletedAt: null,
        movedToBinAt: null,
        restoredAt: serverTimestamp(),
      };
      localPatch = {
        deleted: false,
        archived: false,
        binStatus: "active",
        visibilityStatus: "active",
        deletedAt: null,
        movedToBinAt: null,
        restoredAt: now,
      };
      successMessage = "Quotation restored";
    } else {
      throw new Error(`Unsupported saved quote action: ${action || "unknown"}`);
    }

    const canonicalQuoteId = identity.fullQuoteId || identity.sourceQuoteId || identity.quoteId;
    const normalizedWorkflowQuoteType = normalizeQuoteType(
      quote.normalizedQuoteType ||
        quote.quoteTypeResolved ||
        quote.quoteType ||
        quote.quotationType ||
        quoteType,
      quote
    );
    const workflowIndexQuoteType =
      normalizedWorkflowQuoteType === CANONICAL_UNKNOWN_QUOTE_TYPE
        ? quoteType
        : normalizedWorkflowQuoteType;
    const indexPayload = sanitizeCrmFirestoreValue({
      quoteId: canonicalQuoteId,
      id: canonicalQuoteId,
      sourceQuoteId: canonicalQuoteId,
      quoteIndexId: identity.quoteIndexId,
      indexId: identity.quoteIndexId,
      sourceCollection: "quotations",
      sourcePath: identity.sourcePath,
      quoteType: workflowIndexQuoteType,
      quoteTypeResolved: quoteType,
      quotationType: workflowIndexQuoteType,
      normalizedQuoteType: normalizedWorkflowQuoteType,
      quoteTypeLabel: getCanonicalQuoteTypeLabel(normalizedWorkflowQuoteType),
      ...indexPatch,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: updatedBy,
      lastEditedByName: user?.displayName || user?.email || "",
      updatedBy,
    });
    const fullPayload = sanitizeCrmFirestoreValue({
      ...fullQuotePatch,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      lastEditedBy: updatedBy,
      lastEditedByName: user?.displayName || user?.email || "",
      updatedBy,
    });
    const isBinWorkflowAction = action === "moveToBin" || action === "restoreFromBin";
    let fullQuoteSynced = false;
    let fullQuoteSyncWarning = "";

    if (isDevRuntime) {
      devLog("SAVED_QUOTE_ACTION_START", {
        action,
        workspaceUid,
        quoteIndexPath: `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`,
        fullQuotePath: identity.sourcePath,
        quoteIndexId: identity.quoteIndexId,
        fullQuoteId: identity.fullQuoteId,
        payloadKeys: Object.keys(indexPayload || {}),
        writeMode: isBinWorkflowAction ? "callable_bin_workflow" : "direct_index_patch",
      });
    }

    if (isBinWorkflowAction) {
      const callableName =
        action === "moveToBin" ? "moveSavedQuoteToBin" : "restoreSavedQuoteFromBin";
      if (isDevRuntime) {
        devLog("SAVED_QUOTE_BIN_CALLABLE_START", {
          callableName,
          workspaceUid,
          quoteIndexId: identity.quoteIndexId,
          quoteIndexPath: indexRef.path,
          fullQuotePath: identity.sourcePath,
        });
      }
      try {
        const binWorkflowCallable = httpsCallable(functions, callableName);
        const callableResult = await binWorkflowCallable({
          workspaceUid,
          quoteIndexId: identity.quoteIndexId,
        });
        const resultData = callableResult?.data || {};
        if (resultData.ok !== true) {
          throw new Error(`${callableName} did not return ok:true.`);
        }
        fullQuoteSynced = resultData.fullQuoteSync === "updated";
        fullQuoteSyncWarning = resultData.fullQuoteSync || "";
        for (const task of resultData.taskUpdates || []) {
          patchCrmTaskLocalState(task.taskId, { status: task.status });
        }
        if ((resultData.taskUpdates || []).length) invalidateWorkspaceCollectionCache(workspaceUid, "crm_tasks");
        if (isDevRuntime) {
          devLog("SAVED_QUOTE_BIN_CALLABLE_SUCCESS", {
            callableName,
            workspaceUid,
            quoteIndexId: identity.quoteIndexId,
            fullQuoteSync: resultData.fullQuoteSync || "",
            fullQuotePath: resultData.fullQuotePath || "",
            fullQuoteResolveSource: resultData.fullQuoteResolveSource || "",
          });
        }
      } catch (binWorkflowError) {
        devError("SAVED_QUOTE_BIN_CALLABLE_FAILED_EXACT", {
          action,
          callableName,
          code: binWorkflowError?.code,
          message: binWorkflowError?.message,
          stack: binWorkflowError?.stack,
          quoteIndexPath: indexRef.path,
          fullQuotePath: identity.sourcePath,
          workspaceUid,
          quoteIndexId: identity.quoteIndexId,
        });
        throw binWorkflowError;
      }
    } else {
      try {
        await setDoc(indexRef, indexPayload, { merge: true });
      } catch (indexWriteError) {
        devError("SAVED_QUOTE_INDEX_WRITE_FAILED_EXACT", {
          action,
          code: indexWriteError?.code,
          message: indexWriteError?.message,
          stack: indexWriteError?.stack,
          quoteIndexPath: indexRef.path,
          fullQuotePath: identity.sourcePath,
          workspaceUid,
          payloadPreview: safeDebugPreview(indexPayload),
        });
        throw indexWriteError;
      }

      if (fullQuoteRef && Object.keys(fullPayload || {}).length > 0) {
        try {
          await updateDoc(fullQuoteRef, fullPayload);
          fullQuoteSynced = true;
        } catch (fullQuoteError) {
          fullQuoteSyncWarning =
            fullQuoteError?.code || fullQuoteError?.message || "full quote sync failed";
          if (isDevRuntime) {
            devWarn("SAVED_QUOTE_ACTION_FULL_QUOTE_SYNC_SKIPPED", {
              action,
              code: fullQuoteError?.code,
              message: fullQuoteError?.message,
              fullQuotePath: identity.sourcePath,
              quoteIndexId: identity.quoteIndexId,
            });
          }
        }
      }
    }

    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    if (identity.fullQuoteId) {
      invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    }
    if (!isBinWorkflowAction) {
      try {
        applyQuoteLocalPatch(
          canonicalQuoteId || identity.quoteIndexId,
          {
            ...localPatch,
            updatedAt: now,
            lastActivityAt: now,
            lastEditedAt: now,
            lastEditedBy: updatedBy,
            lastEditedByName: user?.displayName || user?.email || "",
            updatedBy,
          },
          {
            identity,
            sourceQuote: quote,
            persistIndex: false,
          }
        );
      } catch (localPatchError) {
        if (isDevRuntime) {
          devWarn("SAVED_QUOTE_ACTION_LOCAL_PATCH_SUPPRESSED", {
            action,
            quoteIndexId: identity.quoteIndexId,
            quoteId: canonicalQuoteId || identity.fullQuoteId || identity.quoteId || "",
            errorName: localPatchError?.name || "",
            errorMessage: localPatchError?.message || String(localPatchError),
            stack: localPatchError?.stack || "",
            savedQuotesIsArray: Array.isArray(savedQuotesRef.current),
            savedQuotesType: typeof savedQuotesRef.current,
            savedQuotesLength: Array.isArray(savedQuotesRef.current)
              ? savedQuotesRef.current.length
              : null,
            deletedQuotesIsArray: Array.isArray(deletedQuotesRef.current),
            deletedQuotesType: typeof deletedQuotesRef.current,
            deletedQuotesLength: Array.isArray(deletedQuotesRef.current)
              ? deletedQuotesRef.current.length
              : null,
            activeListPreview: Array.isArray(savedQuotesRef.current)
              ? savedQuotesRef.current.slice(0, 3).map((item) => ({
                  id: item?.id,
                  quoteId: item?.quoteId,
                  quoteIndexId: item?.quoteIndexId,
                  quoteNo: item?.quoteNo,
                }))
              : null,
            backendWriteAlreadySucceeded: true,
            userVisibleFailureSuppressed: true,
          });
        }
        fullQuoteSyncWarning =
          fullQuoteSyncWarning || "local_patch_failed_after_backend_success";
      }
    }

    if (action === "moveToBin" || action === "restoreFromBin") {
      const matchKeys = getSavedQuoteWorkflowMatchKeys(
        {
          ...quote,
          id: canonicalQuoteId || identity.quoteIndexId,
          quoteId: canonicalQuoteId || identity.quoteIndexId,
        },
        identity
      );
      const matchesActionQuote = (item = {}) =>
        savedQuoteMatchesWorkflowIdentity(item, identity) ||
        matchKeys.has(String(item.id || "").trim()) ||
        matchKeys.has(String(item.quoteId || item.sourceQuoteId || item.fullQuoteId || "").trim()) ||
        matchKeys.has(String(item.quoteIndexId || item.indexId || "").trim());
      const patchedCard = {
        ...quote,
        ...localPatch,
        id: identity.quoteIndexId || canonicalQuoteId,
        quoteId: canonicalQuoteId || identity.sourceQuoteId || identity.quoteId || "",
        sourceQuoteId: canonicalQuoteId || identity.sourceQuoteId || identity.quoteId || "",
        fullQuoteId: identity.fullQuoteId || canonicalQuoteId || "",
        quoteIndexId: identity.quoteIndexId || canonicalQuoteId || "",
        indexId: identity.quoteIndexId || canonicalQuoteId || "",
        quoteType: workflowIndexQuoteType,
        quotationType: workflowIndexQuoteType,
        quoteTypeResolved: quoteType,
        normalizedQuoteType: normalizedWorkflowQuoteType,
        quoteTypeLabel: getCanonicalQuoteTypeLabel(normalizedWorkflowQuoteType),
        sourcePath:
          identity.sourcePath ||
          (workspaceUid && canonicalQuoteId
            ? `users/${workspaceUid}/quotations/${canonicalQuoteId}`
            : ""),
        updatedAt: now,
        lastActivityAt: now,
        lastEditedAt: now,
        lastEditedBy: updatedBy,
        lastEditedByName: user?.displayName || user?.email || "",
        updatedBy,
      };

      savedQuotesLocalPatchRevisionRef.current += 1;
      savedQuotesRecycleBinLoadRef.current = {
        workspaceUid: "",
        loading: false,
        loaded: false,
        promise: null,
        result: null,
      };

      if (action === "moveToBin") {
        setSavedQuotes((prev) => {
          try {
            return removeQuoteFromCardsWithBlankingGuard(prev, {
              ...identity,
              quoteId: canonicalQuoteId || identity.quoteId || "",
              sourceQuoteId: canonicalQuoteId || identity.sourceQuoteId || "",
              fullQuoteId: identity.fullQuoteId || canonicalQuoteId || "",
              quoteNo: quote.quoteNo || identity.quoteNo || "",
            });
          } catch (localRemoveError) {
            if (isDevRuntime) {
              devWarn("SAVED_QUOTES_DELETE_LOCAL_REMOVE_PRESERVED_LIST", {
                quoteIndexId: identity.quoteIndexId,
                quoteId: canonicalQuoteId || identity.quoteId || "",
                code: localRemoveError?.code || "",
                message: localRemoveError?.message || String(localRemoveError),
              });
            }
            return safeSavedQuoteArray(prev);
          }
        });
        setDeletedQuotes((prev) =>
          safePrependQuoteToList(prev, patchedCard, matchesActionQuote)
        );
      } else {
        setDeletedQuotes((prev) => safeRemoveQuoteFromList(prev, matchesActionQuote));
        setSavedQuotes((prev) =>
          safePrependQuoteToList(prev, patchedCard, matchesActionQuote)
        );
      }

      if (isDevRuntime) {
        devLog("SAVED_QUOTE_LOCAL_BIN_PATCH", {
          action,
          quoteIndexId: identity.quoteIndexId,
          fullQuoteId: identity.fullQuoteId,
          localPatchRevision: savedQuotesLocalPatchRevisionRef.current,
        });
      }
    }

    if (quote.crm?.leadId && ["updateStage", "confirmQuote", "unconfirmQuote"].includes(action)) {
      saveCrmFieldsForLead(
        quote.crm.leadId,
        {
          stageId: localPatch.workStage,
          status: localPatch.status,
          checklistItems: Array.isArray(crmPatch?.checklistItems)
            ? crmPatch.checklistItems
            : [],
        },
        { syncQuote: false }
      ).catch((leadError) => {
        if (isDevRuntime) {
          devWarn("SAVED_QUOTE_ACTION_LINKED_LEAD_SYNC_FAILED", {
            action,
            leadId: quote.crm?.leadId,
            code: leadError?.code,
            message: leadError?.message,
          });
        }
      });
    }

    syncQuoteAnalyticsIndexes(
      canonicalQuoteId || identity.quoteIndexId,
      {
        ...quote,
        ...localPatch,
        id: canonicalQuoteId || identity.quoteIndexId,
        quoteId: canonicalQuoteId || identity.quoteIndexId,
      },
      { source: `savedQuoteAction:${action}` }
    ).catch((analyticsError) => {
      if (isDevRuntime) {
        devWarn("SAVED_QUOTE_ACTION_ANALYTICS_SYNC_FAILED", {
          action,
          code: analyticsError?.code,
          message: analyticsError?.message,
        });
      }
    });

    if (isDevRuntime) {
      devLog("SAVED_QUOTE_ACTION_SUCCESS", {
        action,
        quoteIndexId: identity.quoteIndexId,
        fullQuoteId: identity.fullQuoteId,
        fullQuoteSynced,
        fullQuoteSyncWarning,
      });
    }

    return {
      identity,
      localPatch,
      indexPayload,
      fullQuoteSynced,
      fullQuoteSyncWarning,
      successMessage,
    };
  },
  [
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    canEditQuotes,
    crmPipelines,
    getQuoteGivenStageIdForType,
    getStageMetaForType,
    parseDateInputValue,
    saveCrmFieldsForLead,
    syncQuoteAnalyticsIndexes,
    patchCrmTaskLocalState,
    user?.displayName,
    user?.email,
    workspaceUid,
  ]
);

const upsertQuoteDealLink = useCallback(
  async ({ quoteId, quoteNo = "", leadId = "", customerId = "", customerKey = "" } = {}) => {
    if (!workspaceUid || !quoteId || !leadId) return;

    const linkRef = doc(
      db,
      "users",
      workspaceUid,
      QUOTE_DEAL_LINKS_COLLECTION,
      getQuoteDealLinkDocId(quoteId)
    );
    const linkSnap = await getDoc(linkRef).catch(() => null);
    await setDoc(
      linkRef,
      {
        quoteId,
        quoteNo,
        leadId,
        customerId,
        customerKey: customerKey || customerId || "",
        syncStatus: "pending",
        updatedAt: serverTimestamp(),
        ...(linkSnap?.exists?.() ? {} : { createdAt: serverTimestamp() }),
      },
      { merge: true }
    );
  },
  [workspaceUid]
);

const findExistingDealForQuote = useCallback(
  async ({ quoteId = "", quoteNo = "", preferredLeadId = "" } = {}) => {
    if (!workspaceUid || (!quoteId && !quoteNo)) {
      return {
        canonicalLead: null,
        duplicateLeads: [],
        matchedLeadIds: [],
        mappingLeadId: "",
      };
    }

    const normalizedQuoteId = String(quoteId || "").trim();
    const normalizedQuoteNo = String(quoteNo || "").trim();
    const normalizedPreferredLeadId = String(preferredLeadId || "").trim();

    const fetchLeadById = async (leadId) => {
      if (!leadId) return null;
      const localLead = crmLeadRecords.find(
        (lead) => String(lead.id || "") === String(leadId)
      );
      if (localLead) return localLead;

      try {
        const leadSnap = await getDoc(
          doc(db, "users", workspaceUid, "crm_leads", leadId)
        );
        return leadSnap.exists()
          ? {
              id: leadSnap.id,
              ...leadSnap.data(),
            }
          : null;
      } catch (error) {
        devWarn("Could not fetch linked CRM lead before quote save:", error);
        return null;
      }
    };

    const candidates = [];
    const foundDealByQuoteId = [];
    const foundDealByQuoteNo = [];
    const addCandidate = (lead) => {
      if (!lead?.id) return;
      if (candidates.some((item) => String(item.id) === String(lead.id))) return;
      candidates.push(lead);
    };
    const addQueriedCandidate = (lead, bucket) => {
      addCandidate(lead);
      const leadId = String(lead?.id || "").trim();
      if (leadId && bucket && !bucket.includes(leadId)) {
        bucket.push(leadId);
      }
    };
    const queryExactLeads = async (fieldName, fieldValue, label, bucket) => {
      const normalizedValue = String(fieldValue || "").trim();
      if (!normalizedValue) return [];
      try {
        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_leads"),
            firestoreWhere(fieldName, "==", normalizedValue),
            firestoreLimit(5)
          ),
          {
            label,
            cacheKey: `users/${workspaceUid}/crm_leads?${fieldName}=${normalizedValue}&limit=5`,
            ttlMs: 60 * 1000,
          }
        );
        const leads = snap.docs.map((leadDoc) => ({
          id: leadDoc.id,
          ...leadDoc.data(),
        }));
        leads.forEach((lead) => addQueriedCandidate(lead, bucket));
        return leads;
      } catch (error) {
        devWarn("Could not query exact CRM quote deal link:", {
          fieldName,
          code: error?.code || "",
          message: error?.message || String(error),
        });
        return [];
      }
    };

    const mappingLeadId = normalizedQuoteId
      ? await getDoc(
          doc(
            db,
            "users",
            workspaceUid,
            QUOTE_DEAL_LINKS_COLLECTION,
            getQuoteDealLinkDocId(normalizedQuoteId)
          )
        )
          .then((snap) => (snap.exists() ? String(snap.data()?.leadId || "") : ""))
          .catch((error) => {
            devWarn("Could not read quote deal link mapping:", error);
            return "";
          })
      : "";

    addCandidate(await fetchLeadById(normalizedPreferredLeadId));
    addCandidate(await fetchLeadById(mappingLeadId));
    addCandidate(await fetchLeadById(buildQuoteCreatedLeadId(normalizedQuoteId)));

    crmLeadRecords
      .filter((lead) =>
        doesCrmLeadReferenceQuote(lead, normalizedQuoteId, normalizedQuoteNo)
      )
      .forEach(addCandidate);

    if (!candidates.length && normalizedQuoteId) {
      await queryExactLeads(
        "quoteId",
        normalizedQuoteId,
        "findQuoteDealByQuoteId",
        foundDealByQuoteId
      );
      await queryExactLeads(
        "latestQuoteId",
        normalizedQuoteId,
        "findQuoteDealByLatestQuoteId",
        foundDealByQuoteId
      );
      await queryExactLeads(
        "primaryQuoteId",
        normalizedQuoteId,
        "findQuoteDealByPrimaryQuoteId",
        foundDealByQuoteId
      );
    }

    if (!candidates.length && normalizedQuoteNo) {
      await queryExactLeads(
        "quoteNo",
        normalizedQuoteNo,
        "findQuoteDealByQuoteNo",
        foundDealByQuoteNo
      );
      await queryExactLeads(
        "latestQuoteNo",
        normalizedQuoteNo,
        "findQuoteDealByLatestQuoteNo",
        foundDealByQuoteNo
      );
      await queryExactLeads(
        "primaryQuoteNo",
        normalizedQuoteNo,
        "findQuoteDealByPrimaryQuoteNo",
        foundDealByQuoteNo
      );
    }

    const canonicalLead =
      chooseCanonicalQuoteDeal(candidates, normalizedPreferredLeadId || mappingLeadId) ||
      null;
    const duplicateLeads = canonicalLead?.id
      ? candidates.filter((lead) => String(lead.id) !== String(canonicalLead.id))
      : [];
    const matchedLeadIds = candidates.map((lead) => String(lead.id || "")).filter(Boolean);

    if (isDevRuntime && matchedLeadIds.length > 1) {
      devLog("Quote deal duplicate audit", {
        quoteId: normalizedQuoteId,
        quoteNo: normalizedQuoteNo,
        matchedLeadIds,
        canonicalLeadId: canonicalLead?.id || "",
      });
    }

    return {
      canonicalLead,
      duplicateLeads,
      matchedLeadIds,
      mappingLeadId,
      foundDealByQuoteId,
      foundDealByQuoteNo,
    };
  },
  [crmLeadRecords, workspaceUid]
);

const ensureSavedQuoteCrmDeal = useCallback(
  async (quoteLike = {}, options = {}) => {
    if (!workspaceUid || !quoteLike?.id || !canAccessCrm) {
      return {
        leadId: quoteLike?.crm?.leadId || "",
        crmPayload: quoteLike?.crm || null,
        stageId: quoteLike?.workStage || "",
        status: quoteLike?.status || "draft",
      };
    }

    const quoteType = resolveQuoteType(quoteLike);
    const quoteId = String(quoteLike.id || "");
    const quoteNoValue = String(options.quoteNo || quoteLike.quoteNo || quoteId);
    const latestQuoteTotal = Number(
      options.totalValue ?? getNormalizedQuoteTotal(quoteLike) ?? 0
    );
    const quoteGivenStageId = getQuoteGivenStageIdForType(quoteType);
    const defaultStatus = String(quoteLike.status || quoteLike.crm?.status || "draft");
    const customerFields = {
      customerId: String(options.customerFields?.customerId || quoteLike.customerId || quoteLike.crm?.customerId || ""),
      customerKey: String(options.customerFields?.customerKey || quoteLike.customerKey || quoteLike.crm?.customerKey || quoteLike.customerId || quoteLike.crm?.customerId || ""),
      customerName: String(options.customerFields?.customerName || quoteLike.customerName || quoteLike.crm?.customerName || ""),
      customerPhone: String(options.customerFields?.customerPhone || quoteLike.customerPhone || quoteLike.crm?.customerPhone || ""),
      customerWhatsapp: String(options.customerFields?.customerWhatsapp || quoteLike.customerWhatsapp || ""),
      customerEmail: String(options.customerFields?.customerEmail || quoteLike.customerEmail || ""),
      customerAddress: String(options.customerFields?.customerAddress || quoteLike.customerAddress || ""),
      customerCompanyName: String(options.customerFields?.customerCompanyName || quoteLike.customerCompanyName || ""),
    };
    const sourceQuote = {
      ...quoteLike,
      quoteType,
      quoteNo: quoteNoValue,
      ...customerFields,
      grandTotal: latestQuoteTotal || quoteLike.grandTotal,
      subtotal: quoteLike.subtotal,
      workStage: quoteLike.workStage || quoteGivenStageId,
      status: defaultStatus,
    };
    const quoteRef = doc(db, "users", workspaceUid, "quotations", quoteId);
    const quoteIndexDocId = String(
      sourceQuote.quoteIndexId || sourceQuote.indexId || quoteId
    ).trim();
    const sourcePath =
      sourceQuote.sourcePath || `users/${workspaceUid}/quotations/${quoteId}`;
    let existingQuoteIndexRecord = {};
    if (quoteIndexDocId) {
      try {
        const quoteIndexSnap = await getDoc(
          doc(db, "users", workspaceUid, "quote_index", quoteIndexDocId),
          {
            label: "quoteDealLinkQuoteIndexLookup",
            cacheKey: `users/${workspaceUid}/quote_index/${quoteIndexDocId}:dealLinkLookup`,
            ttlMs: 30 * 1000,
          }
        );
        existingQuoteIndexRecord = quoteIndexSnap.exists()
          ? quoteIndexSnap.data() || {}
          : {};
      } catch (error) {
        devWarn("Could not read quote_index before CRM deal link:", {
          quoteId,
          quoteIndexDocId,
          code: error?.code || "",
          message: error?.message || String(error),
        });
      }
    }
    const autoCreateQuotationDealEnabled =
      options.autoCreateDeal === true || crmSettings.autoCreateDealFromQuotation !== false;
    const persistQuoteIndexAfterDealLink = async (quotePatch = {}) => {
      if (!quoteIndexDocId) return;
      const quoteIndexPayload = buildQuoteIndexSavePayload(
        {
          ...sourceQuote,
          ...quotePatch,
          id: quoteId,
          quoteId,
          sourceQuoteId: quoteId,
          quoteIndexId: quoteIndexDocId,
          indexId: quoteIndexDocId,
          sourceCollection: "quotations",
          sourcePath,
        },
        quoteId,
        workspaceUid
      );
      await setDoc(
        doc(db, "users", workspaceUid, "quote_index", quoteIndexDocId),
        sanitizeForFirestore(quoteIndexPayload),
        { merge: true }
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      customerLedgerQuotesLoadRef.current = {};
    };
    const inheritedCrmQuoteId = String(
      sourceQuote.crm?.quoteId ||
        sourceQuote.crm?.sourceQuoteId ||
        sourceQuote.crm?.linkedQuoteId ||
        ""
    ).trim();
    const shouldIgnoreInheritedLeadId =
      options.saveAsNew === true ||
      (inheritedCrmQuoteId && inheritedCrmQuoteId !== quoteId);
    const explicitLinkedLeadId = String(
      options.linkedLeadId ||
        sourceQuote.opportunityId ||
        sourceQuote.dealId ||
        sourceQuote.crmLeadId ||
        sourceQuote.leadId ||
        sourceQuote.crm?.opportunityId ||
        sourceQuote.crm?.dealId ||
        sourceQuote.crm?.leadId ||
        existingQuoteIndexRecord.opportunityId ||
        existingQuoteIndexRecord.dealId ||
        existingQuoteIndexRecord.crmLeadId ||
        existingQuoteIndexRecord.leadId ||
        ""
    ).trim();
    const requestedLinkedLeadId = shouldIgnoreInheritedLeadId
      ? ""
      : explicitLinkedLeadId;
    const crmLinkExplicitlyRequested = Boolean(
      requestedLinkedLeadId ||
        options.forceCreate === true ||
        options.forceLinkExisting === true ||
        autoCreateQuotationDealEnabled
    );
    if (!crmLinkExplicitlyRequested) {
      const skippedCrmPayload = buildQuoteCrmData(sourceQuote, {
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        leadId: "",
        hasQuote: true,
        ...customerFields,
      });
      logQuoteToDealLinkDecision({
        quoteId,
        customerId: customerFields.customerId,
        matchedCustomerId: customerFields.customerId,
        existingOpenDealIds: [],
        actionSelected: "skipped_no_explicit_crm_link",
        finalLinkedLeadId: "",
      });
      return {
        leadId: "",
        crmPayload: skippedCrmPayload,
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        action: "skipped",
      };
    }
    const existingDealLookup = await findExistingDealForQuote({
      quoteId,
      quoteNo: quoteNoValue,
      preferredLeadId: requestedLinkedLeadId,
    });
    const existingLinkedLead =
      existingDealLookup.canonicalLead ||
      findLinkedCrmLeadForQuote(sourceQuote, requestedLinkedLeadId);
    const existingQuoteOpportunityId = String(
      sourceQuote.opportunityId ||
        sourceQuote.dealId ||
        sourceQuote.crmLeadId ||
        sourceQuote.leadId ||
        sourceQuote.crm?.opportunityId ||
        sourceQuote.crm?.dealId ||
        sourceQuote.crm?.leadId ||
        ""
    ).trim();
    const existingIndexOpportunityId = String(
      existingQuoteIndexRecord.opportunityId ||
        existingQuoteIndexRecord.dealId ||
        existingQuoteIndexRecord.crmLeadId ||
        existingQuoteIndexRecord.leadId ||
        ""
    ).trim();
    const quoteDealTraceBase = {
      quoteId,
      quoteNo: quoteNoValue,
      quoteCustomerName: customerFields.customerName,
      existingQuoteOpportunityId,
      existingIndexOpportunityId,
      foundDealByQuoteId: existingDealLookup.foundDealByQuoteId || [],
      foundDealByQuoteNo: existingDealLookup.foundDealByQuoteNo || [],
      existingDealFound: Boolean(existingLinkedLead?.id),
      existingDealId: existingLinkedLead?.id || "",
    };
    const logQuoteDealTrace = (overrides = {}) => {
      if (!isDevRuntime) return;
      devLog("QUOTE_DEAL_LINK_TRACE", {
        ...quoteDealTraceBase,
        createdNewDeal: false,
        updatedDealId: "",
        duplicatePrevented: false,
        ...overrides,
      });
    };
    const openDeals = existingLinkedLead?.id
      ? [existingLinkedLead]
      : findOpenCustomerDeals(customerFields, quoteType);
    const existingOpenDealIds = openDeals
      .map((deal) => String(deal?.id || ""))
      .filter(Boolean);
    const notifyQuoteDealOutcome = (actionSelected) => {
      if (options.showSaveOutcomeToast !== true && options.showToast !== true) return;
      if (actionSelected === "save_quote_only") {
        notify("Quote saved only", "success");
      } else if (actionSelected === "create_new") {
        notify("Quote saved and new CRM deal created", "success");
      } else {
        notify("Quote saved and linked to CRM deal", "success");
      }
    };
    const logQuoteDealOutcome = (actionSelected, finalLinkedLeadId = "") => {
      logQuoteToDealLinkDecision({
        quoteId,
        customerId: customerFields.customerId,
        matchedCustomerId: customerFields.customerId,
        existingOpenDealIds,
        actionSelected,
        finalLinkedLeadId,
      });
    };

    if (
      (sourceQuote.crm?.saveQuoteOnly === true || options.previousSaveQuoteOnly === true) &&
      !existingLinkedLead?.id &&
      options.respectSaveQuoteOnly !== false &&
      options.forceCreate !== true &&
      options.forceLinkExisting !== true
    ) {
      const nextCrmPayload = {
        ...buildQuoteCrmData(sourceQuote, {
          stageId: sourceQuote.workStage,
          status: defaultStatus,
          leadId: "",
          hasQuote: true,
          ...customerFields,
        }),
        saveQuoteOnly: true,
      };
      await updateDoc(quoteRef, {
        crm: nextCrmPayload,
        updatedAt: serverTimestamp(),
      });
      await persistQuoteIndexAfterDealLink({
        crm: nextCrmPayload,
        status: defaultStatus,
        workStage: sourceQuote.workStage,
      });
      applyQuoteLocalPatch(quoteId, {
        crm: nextCrmPayload,
        updatedAt: new Date(),
      }, { persistIndex: false });
      logQuoteDealOutcome("save_quote_only", "");
      logQuoteDealTrace({ createdNewDeal: false, updatedDealId: "" });
      notifyQuoteDealOutcome("save_quote_only");
      return {
        leadId: "",
        crmPayload: nextCrmPayload,
        stageId: sourceQuote.workStage,
        status: defaultStatus,
        action: "saveOnly",
      };
    }

    let choice = existingLinkedLead?.id
      ? "linkExisting"
      : options.forceCreate
      ? "create"
      : options.forceLinkExisting
      ? "linkExisting"
      : autoCreateQuotationDealEnabled
      ? openDeals[0]?.id
        ? "linkExisting"
        : "create"
      : "";

    if (!choice) {
      choice = await requestQuoteDealChoice({
        customerName: customerFields.customerName || "this customer",
        quoteNo: quoteNoValue,
        quoteType,
        openDeals,
      });
    }

    const buildFinalQuoteCrm = (overrides = {}) => ({
      ...buildQuoteCrmData(sourceQuote, {
        stageId: overrides.stageId || sourceQuote.workStage || quoteGivenStageId,
        status: overrides.status || defaultStatus,
        leadId: overrides.leadId || "",
        hasQuote: true,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerWhatsapp: customerFields.customerWhatsapp,
        customerEmail: customerFields.customerEmail,
        customerAddress: customerFields.customerAddress,
        customerCompanyName: customerFields.customerCompanyName,
        contact: {
          name: customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
        installationDate:
          overrides.installationDate ??
          sourceQuote.installationDate ??
          sourceQuote.crm?.installationDate ??
          null,
      }),
      saveQuoteOnly: overrides.saveQuoteOnly === true,
    });

    if (choice === "saveOnly") {
      const nextCrmPayload = buildFinalQuoteCrm({
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        saveQuoteOnly: true,
      });
      await updateDoc(quoteRef, {
        crm: nextCrmPayload,
        updatedAt: serverTimestamp(),
      });
      await persistQuoteIndexAfterDealLink({
        crm: nextCrmPayload,
        status: defaultStatus,
        workStage: sourceQuote.workStage,
      });
      applyQuoteLocalPatch(quoteId, {
        crm: nextCrmPayload,
        updatedAt: new Date(),
      }, { persistIndex: false });
      logQuoteDealOutcome("save_quote_only", "");
      logQuoteDealTrace({ createdNewDeal: false, updatedDealId: "" });
      notifyQuoteDealOutcome("save_quote_only");
      return {
        leadId: "",
        crmPayload: nextCrmPayload,
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        action: "saveOnly",
      };
    }

    const targetLead =
      choice === "linkExisting"
        ? existingLinkedLead || openDeals[0] || null
        : null;
    const linkedQuoteEntry = {
      quoteId,
      quoteNo: quoteNoValue,
      type: quoteType,
      value: latestQuoteTotal,
      isPrimary: true,
    };

    if (targetLead?.id) {
      if (targetLead.customerId && targetLead.customerId !== customerFields.customerId) {
        throw new Error("This deal belongs to another customer. The quotation remains saved.");
      }
      const nextLinkedQuotes = normalizeCrmLinkedQuotes(targetLead, {
        linkedQuotes: [
          ...normalizeCrmLinkedQuotes(targetLead)
            .filter(
              (entry) =>
                String(entry.quoteId || "") !== quoteId &&
                String(entry.quoteNo || "") !== quoteNoValue
            )
            .map((entry) => ({ ...entry, isPrimary: false })),
          linkedQuoteEntry,
        ],
      });
      const nextStageId = targetLead.stageId || quoteGivenStageId;
      const nextStatus = targetLead.status || defaultStatus;

      await saveCrmFieldsForLead(
        targetLead.id,
        {
          quoteType,
          pipelineType: quoteType,
          stageId: nextStageId,
          status: nextStatus,
          linkedQuotes: nextLinkedQuotes,
          hasQuote: true,
          customerId: customerFields.customerId,
          customerKey: customerFields.customerKey,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          customerWhatsapp: customerFields.customerWhatsapp,
          customerEmail: customerFields.customerEmail,
          customerAddress: customerFields.customerAddress,
          customerCompanyName: customerFields.customerCompanyName,
          contact: {
            ...(targetLead.contact || {}),
            name: targetLead.contact?.name || customerFields.customerName,
            phone: targetLead.contact?.phone || customerFields.customerPhone,
            whatsapp: targetLead.contact?.whatsapp || customerFields.customerWhatsapp,
            email: targetLead.contact?.email || customerFields.customerEmail,
            address: targetLead.contact?.address || customerFields.customerAddress,
          },
          expectedValue: latestQuoteTotal,
          finalValue: latestQuoteTotal,
          installationDate:
            targetLead.installationDate ||
            sourceQuote.installationDate ||
            sourceQuote.crm?.installationDate ||
            null,
        },
        { sourceLead: targetLead, syncQuote: false }
      );
      await upsertQuoteDealLink({
        quoteId,
        quoteNo: quoteNoValue,
        leadId: targetLead.id,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
      });

      const finalizeResult = await finalizeSavedQuoteCrmLinkCallable({workspaceUid,quoteId,leadId:targetLead.id,
        quoteType:quoteType===GENERAL_QUOTE_TYPE?"general":"curtain"});
      if (!finalizeResult.data?.ok || !finalizeResult.data?.quotePatch) {
        throw new Error("The quotation remains saved. Customer history could not be confirmed.");
      }
      applyQuoteLocalPatch(quoteId, finalizeResult.data.quotePatch, { persistIndex: false });

      logQuoteDealOutcome("link_existing", targetLead.id);
      logQuoteDealTrace({
        createdNewDeal: false,
        updatedDealId: targetLead.id,
        duplicatePrevented:
          Boolean(existingDealLookup.matchedLeadIds?.length) ||
          Boolean(existingDealLookup.foundDealByQuoteId?.length) ||
          Boolean(existingDealLookup.foundDealByQuoteNo?.length),
      });
      notifyQuoteDealOutcome("link_existing");
      return {
        leadId: targetLead.id,
        crmPayload: finalizeResult.data.quotePatch.crm,
        stageId: finalizeResult.data.quotePatch.workStage,
        status: finalizeResult.data.quotePatch.status,
        action: "linked",
      };
    }

    const leadId = buildQuoteCreatedLeadId(quoteId);
    const quoteCrm = buildFinalQuoteCrm({
      leadId,
      stageId: quoteGivenStageId,
      status: defaultStatus,
    });
    const leadPayload = buildCrmLeadData(
      { id: leadId, createdAt: new Date() },
      {
        title: quoteCrm.title || `${customerFields.customerName || "Customer"} - #${quoteNoValue}`,
        companyOrProject:
          customerFields.customerCompanyName ||
          customerFields.customerName ||
          quoteCrm.title ||
          "",
        contact: quoteCrm.contact,
        source: quoteCrm.source,
        quoteType,
        pipelineType: quoteType,
        stageId: quoteGivenStageId,
        status: defaultStatus,
        linkedQuotes: normalizeCrmLinkedQuotes({}, {
          linkedQuotes: [linkedQuoteEntry],
        }),
        hasQuote: true,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerWhatsapp: customerFields.customerWhatsapp,
        customerEmail: customerFields.customerEmail,
        customerAddress: customerFields.customerAddress,
        customerCompanyName: customerFields.customerCompanyName,
        checklistItems: quoteCrm.checklistItems,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
        nextAction: quoteCrm.nextAction,
        nextActionDate: quoteCrm.nextActionDate,
        installationDate: quoteCrm.installationDate,
        reminderConfigs: quoteCrm.reminderConfigs,
        followUps: quoteCrm.followUps,
        attachments: quoteCrm.attachments,
        notes: quoteCrm.notes,
        tags: quoteCrm.tags,
      }
    );

    await saveCrmFieldsForLead(
      leadId,
      leadPayload,
      {
        sourceLead: { id: leadId, createdAt: null },
        createIfMissing: true,
        forceLead: true,
        syncQuote: false,
      }
    );
    await upsertQuoteDealLink({
      quoteId,
      quoteNo: quoteNoValue,
      leadId,
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
    });

    const finalizeResult = await finalizeSavedQuoteCrmLinkCallable({workspaceUid,quoteId,leadId,
      quoteType:quoteType===GENERAL_QUOTE_TYPE?"general":"curtain"});
    if (!finalizeResult.data?.ok || !finalizeResult.data?.quotePatch) {
      throw new Error("The quotation remains saved. Customer history could not be confirmed.");
    }
    applyQuoteLocalPatch(quoteId, finalizeResult.data.quotePatch, { persistIndex: false });

    logQuoteDealOutcome("create_new", leadId);
    logQuoteDealTrace({
      createdNewDeal: true,
      updatedDealId: leadId,
      duplicatePrevented: false,
    });
    notifyQuoteDealOutcome("create_new");
    return {
      leadId,
      crmPayload: finalizeResult.data.quotePatch.crm,
      stageId: finalizeResult.data.quotePatch.workStage,
      status: finalizeResult.data.quotePatch.status,
      action: "created",
    };
  },
  [
    applyQuoteLocalPatch,
    buildCrmLeadData,
    buildQuoteCrmData,
    canAccessCrm,
    findExistingDealForQuote,
    findLinkedCrmLeadForQuote,
    findOpenCustomerDeals,
    getNormalizedQuoteTotal,
    getQuoteGivenStageIdForType,
    crmSettings.autoCreateDealFromQuotation,
    notify,
    requestQuoteDealChoice,
    saveCrmFieldsForLead,
    upsertQuoteDealLink,
    finalizeSavedQuoteCrmLinkCallable,
    workspaceUid,
  ]
);

const retrySavedQuoteCustomerHistory = useCallback(async (quote) => {
  if (!workspaceUid || !canAccessCrm || customerHistoryRetryInFlightRef.current) return;
  const identity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
  if (!identity.fullQuoteId) return;
  customerHistoryRetryInFlightRef.current = true;
  setCustomerHistoryRetryBusyId(identity.fullQuoteId);
  try {
    // Retry only existing history links; never call canonical Save or create another deal.
    const result = await finalizeSavedQuoteCrmLinkCallable({
      workspaceUid,
      quoteId: identity.fullQuoteId,
      quoteType: resolveQuoteType(quote) === GENERAL_QUOTE_TYPE ? "general" : "curtain",
    });
    if (!result.data?.ok || !result.data?.quotePatch) throw new Error("Unconfirmed history update");
    applyQuoteLocalPatch(identity.fullQuoteId, result.data.quotePatch, { persistIndex: false });
    invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
    quotationCustomerSearchCacheRef.current.clear();
    notify("Customer history updated. The saved quotation and totals are unchanged.", "success");
  } catch (error) {
    devWarn("CUSTOMER_HISTORY_RETRY_FAILED", { code: error?.code || "" });
    notify(error?.code === "functions/permission-denied"
      ? "You do not have permission to update customer history. Your quotation is safe."
      : "Customer history could not be updated. Your quotation is safe. Contact your administrator; do not save again to retry this update.", "warning", 9000);
  } finally {
    customerHistoryRetryInFlightRef.current = false;
    setCustomerHistoryRetryBusyId("");
  }
}, [applyQuoteLocalPatch, canAccessCrm, finalizeSavedQuoteCrmLinkCallable, notify, workspaceUid]);

const saveCrmPipelinesSettings = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  const normalized = normalizeCrmPipelines(crmPipelines);
  const settingsRef = doc(db, "users", workspaceUid, "settings", "main");

  await setDoc(
    settingsRef,
    {
      crmPipelines: normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  invalidateFirestoreReadCache(settingsRef);

  setCrmPipelines(normalized);
  notify("CRM pipeline settings saved", "success");
}, [crmPipelines, notify, workspaceUid]);

const saveCrmExecutionTemplates = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  if (isSavingExecutionTemplates) return;

  if (
    executionTemplatesLoadingWorkspaceRef.current === workspaceUid ||
    executionTemplatesLoadedWorkspaceRef.current !== workspaceUid
  ) {
    notify("Execution templates are still loading. Please try again in a moment.", "warning");
    if (isDevRuntime) {
      devWarn("EXECUTION TEMPLATE SAVE BLOCKED BEFORE LOAD", {
        workspaceUid,
        loadingWorkspace: executionTemplatesLoadingWorkspaceRef.current,
        loadedWorkspace: executionTemplatesLoadedWorkspaceRef.current,
        localTemplateCount: crmExecutionTemplates.length,
      });
    }
    return;
  }

  setIsSavingExecutionTemplates(true);
  try {
    const templatesRef = collection(db, "users", workspaceUid, "execution_templates");
    const existingSnapshot = await getDocs(templatesRef, {
      label: "saveExecutionTemplatesExistingScan",
      cacheKey: `users/${workspaceUid}/execution_templates?saveExistingScan`,
    });
    const normalizedTemplates = normalizeExecutionTemplates(crmExecutionTemplates).filter(
      (template) => String(template.name || "").trim()
    );
    const nextIds = new Set(normalizedTemplates.map((template) => template.id));
    if (isDevRuntime) {
      devLog("EXECUTION TEMPLATE SAVE START", {
        workspaceUid,
        existingCount: existingSnapshot.size,
        localCount: crmExecutionTemplates.length,
        normalizedCount: normalizedTemplates.length,
        cacheKey: `users/${workspaceUid}/execution_templates`,
      });
    }

    await Promise.all(
      normalizedTemplates.map((template) =>
        setDoc(
          doc(db, "users", workspaceUid, "execution_templates", template.id),
          {
            name: template.name,
            itemType: normalizeExecutionTemplateItemType(
              template.itemType || template.name
            ),
            fields: (Array.isArray(template.fields) ? template.fields : [])
              .map((field) => ({
                id:
                  String(field?.id || "").trim() ||
                  createClientId("exec-template-field"),
                key: String(field?.key || "").trim(),
                defaultValue:
                  field?.defaultValue == null ? "" : String(field.defaultValue),
              }))
              .filter((field) => field.key),
            updatedAt: serverTimestamp(),
            createdAt: template.createdAt || serverTimestamp(),
          },
          { merge: true }
        )
      )
    );

    await Promise.all(
      existingSnapshot.docs
        .filter((docSnap) => !nextIds.has(docSnap.id))
        .map((docSnap) =>
          deleteDoc(doc(db, "users", workspaceUid, "execution_templates", docSnap.id))
        )
    );

    invalidateWorkspaceCollectionCache(workspaceUid, "execution_templates");
    const savedTemplatesForState = normalizeExecutionTemplateDrafts(normalizedTemplates)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    setCrmExecutionTemplates(savedTemplatesForState);
    executionTemplatesLoadedWorkspaceRef.current = workspaceUid;
    setExecutionTemplatesLoadState({
      workspaceUid,
      loading: false,
      loaded: true,
      error: "",
    });
    markExecutionTemplatesBaseline(
      savedTemplatesForState,
      selectedExecutionTemplateManagerId || "library",
      "saved"
    );
    clearExecutionTemplatesDraft();
    notify("Execution templates saved", "success");
  } catch (error) {
    devError("Execution templates save failed:", {
      error,
      code: error?.code,
      message: error?.message,
      workspaceUid,
      localTemplateCount: crmExecutionTemplates.length,
    });
    notify("Could not save execution templates. Your changes are still here.", "error");
  } finally {
    setIsSavingExecutionTemplates(false);
  }
}, [
  clearExecutionTemplatesDraft,
  crmExecutionTemplates,
  isSavingExecutionTemplates,
  markExecutionTemplatesBaseline,
  notify,
  selectedExecutionTemplateManagerId,
  workspaceUid,
]);

  const addExecutionTemplate = useCallback(() => {
  if (
    workspaceUid &&
    executionTemplatesLoadedWorkspaceRef.current !== workspaceUid
  ) {
    notify("Execution templates are still loading. Please try again in a moment.", "warning");
    return;
  }
  const nextTemplate = createExecutionTemplateDraft(
    "New Template",
    "new template",
    [createExecutionTemplateFieldDraft("Field 1", "")]
  );
  setSelectedExecutionTemplateManagerId(nextTemplate.id);
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts([...prev, nextTemplate])
  );
}, [notify, workspaceUid]);

const updateExecutionTemplate = useCallback((templateId, patch) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              ...patch,
            }
          : template
      )
    )
  );
}, []);

const deleteExecutionTemplate = useCallback((templateId) => {
  setCrmExecutionTemplates((prev) => {
    const nextTemplates = prev.filter((template) => template.id !== templateId);
    if (selectedExecutionTemplateManagerId === templateId) {
      setSelectedExecutionTemplateManagerId(nextTemplates[0]?.id || "");
    }
    return nextTemplates;
  });
}, [selectedExecutionTemplateManagerId]);

const addExecutionTemplateField = useCallback((templateId) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: [
                ...(template.fields || []),
                createExecutionTemplateFieldDraft(
                  `Field ${(template.fields || []).length + 1}`,
                  ""
                ),
              ],
            }
          : template
      )
    )
  );
}, []);

const updateExecutionTemplateField = useCallback((templateId, fieldId, patch) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: (template.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      ...patch,
                    }
                  : field
              ),
            }
          : template
      )
    )
  );
}, []);

const removeExecutionTemplateField = useCallback((templateId, fieldId) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: (template.fields || []).filter((field) => field.id !== fieldId),
            }
          : template
      )
    )
  );
}, []);

const updatePipelineStage = useCallback((quoteType, stageId, patch) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    const stages = next[quoteType].stages.map((stage) => {
      if (stage.id !== stageId) {
        if (patch.confirmsQuote === true) {
          return { ...stage, confirmsQuote: false };
        }
        if (patch.marksComplete === true) {
          return { ...stage, marksComplete: false };
        }
        if (patch.isCancelled === true) {
          return { ...stage, isCancelled: false };
        }
        return stage;
      }

      return {
        ...stage,
        ...patch,
      };
    });

    next[quoteType] = {
      ...next[quoteType],
      stages,
    };

    return next;
  });
}, []);

const movePipelineStage = useCallback((quoteType, stageId, direction) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    const stages = [...next[quoteType].stages];
    const index = stages.findIndex((stage) => stage.id === stageId);
    const targetIndex = index + direction;

    if (index < 0 || targetIndex < 0 || targetIndex >= stages.length) {
      return next;
    }

    const [stage] = stages.splice(index, 1);
    stages.splice(targetIndex, 0, stage);
    next[quoteType] = {
      ...next[quoteType],
      stages,
    };
    return next;
  });
}, []);

const deletePipelineStage = useCallback((quoteType, stageId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    if (next[quoteType].stages.length <= 1) {
      return next;
    }

    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.filter((stage) => stage.id !== stageId),
    };
    return next;
  });
}, []);

const addPipelineStage = useCallback((quoteType) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: [...next[quoteType].stages, createStageDraft("New Stage")],
    };
    return next;
  });
}, []);

const addPipelineChecklistItem = useCallback((quoteType, stageId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: [
                ...(stage.defaultChecklist || []),
                createChecklistTemplateDraft("New Checklist Item"),
              ],
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const addPipelineChecklistChild = useCallback(
  (quoteType, stageId, parentId) => {
    setCrmPipelines((prev) => {
      const next = normalizeCrmPipelines(prev);
      next[quoteType] = {
        ...next[quoteType],
        stages: next[quoteType].stages.map((stage) =>
          stage.id === stageId
            ? {
                ...stage,
                defaultChecklist: insertChecklistChildNode(
                  stage.defaultChecklist || [],
                  parentId,
                  createChecklistChildDraft("New Checklist Item", {
                    templateMode: true,
                  })
                ),
              }
            : stage
        ),
      };
      return next;
    });
  },
  []
);

const updatePipelineChecklistItem = useCallback(
  (quoteType, stageId, itemId, patch) => {
    setCrmPipelines((prev) => {
      const next = normalizeCrmPipelines(prev);
      next[quoteType] = {
        ...next[quoteType],
        stages: next[quoteType].stages.map((stage) =>
          stage.id === stageId
            ? {
                ...stage,
                defaultChecklist: updateChecklistNodeById(
                  stage.defaultChecklist || [],
                  itemId,
                  (item) => ({ ...item, ...patch })
                ),
              }
            : stage
        ),
      };
      return next;
    });
  },
  []
);

const movePipelineChecklistItem = useCallback((quoteType, stageId, itemId, direction) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: moveChecklistNodeById(
                stage.defaultChecklist || [],
                itemId,
                direction
              ),
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const deletePipelineChecklistItem = useCallback((quoteType, stageId, itemId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: deleteChecklistNodeById(
                stage.defaultChecklist || [],
                itemId
              ),
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const savedStageOptions = useMemo(
  () => getStageFilterOptions(crmPipelines, quoteTypeFilter),
  [crmPipelines, quoteTypeFilter]
);

const savedQuotesSearchText = useMemo(
  () => normalizeSearchText(search),
  [search]
);

const savedQuotesViewModel = useMemo(() => {
  const searchRecords = savedSearchResult.key === `${workspaceUid}:${savedQuotesSearchText}:${user?.uid}:${canAccessCurtainQuotes}:${canAccessGeneralQuotes}:${recycleBinOpen}` ? savedSearchResult.records : [];
  let activeQuotes = (savedQuotesSearchText ? searchRecords : savedQuotes).filter(
    (quote) =>
      isQuoteVisibleInSavedQuotesDefault(quote)
  );

  if (statusFilter !== "all") {
    activeQuotes = activeQuotes.filter((quote) => quote.status === statusFilter);
  }

  if (stageFilter !== "all") {
    const [stagePipelineType, stageId] = String(stageFilter).split("::");
    activeQuotes = activeQuotes.filter(
      (quote) =>
        resolveQuoteType(quote) === stagePipelineType &&
        (quote.workStage || quote.crm?.stageId || "quote_given") === stageId
    );
  }

  if (quoteTypeFilter !== "all") {
    activeQuotes = activeQuotes.filter(
      (quote) => resolveQuoteType(quote) === quoteTypeFilter
    );
  }

  const sortedActiveQuotes = [...activeQuotes].sort((a, b) => {
    if (sortBy === "repository") return 0;
    if (sortBy === "highest") {
      return getQuoteValue(b) - getQuoteValue(a);
    }

    if (sortBy === "lowest") {
      return getQuoteValue(a) - getQuoteValue(b);
    }

    if (sortBy === "oldest") {
      return getCreatedMillis(a) - getCreatedMillis(b);
    }

    if (sortBy === "created") {
      return getCreatedMillis(b) - getCreatedMillis(a) || String(a.id).localeCompare(String(b.id));
    }

    if (sortBy === "recently_edited") {
      return (resolveQuoteListingDate(b).millis || 0) - (resolveQuoteListingDate(a).millis || 0) || String(a.id).localeCompare(String(b.id));
    }

    return (resolveQuoteListingDate(b).millis || 0) - (resolveQuoteListingDate(a).millis || 0) || String(a.id).localeCompare(String(b.id));
  });

  const filteredRecycledQuotes = (savedQuotesSearchText && recycleBinOpen ? searchRecords : deletedQuotes)
    .filter((quote) => isDeletedQuoteRecord(quote))
    .filter(
      (quote) =>
        !savedQuotesSearchText ||
        doesSearchBlobMatch(buildQuoteSearchBlob(quote), savedQuotesSearchText)
    )
    .filter((quote) =>
      quoteTypeFilter === "all"
        ? true
        : resolveQuoteType(quote) === quoteTypeFilter
    );

  return {
    activeQuotes: sortedActiveQuotes,
    filteredRecycledQuotes,
  };
}, [
  deletedQuotes,
  quoteTypeFilter,
  savedQuotes,
  savedQuotesSearchText,
  sortBy,
  stageFilter,
  statusFilter,
  savedSearchResult,
  workspaceUid,
  user?.uid,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  recycleBinOpen,
]);

useEffect(() => {
  setSavedQuotesVisibleCount(SAVED_QUOTES_PAGE_SIZE);
}, [
  businessDashboardDrilldownKey,
  savedQuotesSearchText,
  stageFilter,
  statusFilter,
  quoteTypeFilter,
  sortBy,
]);

const filteredSavedQuotesForDisplay = useMemo(() => {
  if (!activeBusinessDashboardDrilldownIds) {
    return savedQuotesViewModel.activeQuotes;
  }

  return savedQuotesViewModel.activeQuotes.filter((quote) =>
    activeBusinessDashboardDrilldownIds.has(quote.id)
  );
}, [activeBusinessDashboardDrilldownIds, savedQuotesViewModel.activeQuotes]);

const visibleSavedQuotesDisplay = useMemo(
  () => filteredSavedQuotesForDisplay.slice(0, savedQuotesVisibleCount),
  [filteredSavedQuotesForDisplay, savedQuotesVisibleCount]
);

useEffect(() => {
  traceListSort("Saved Quotes", visibleSavedQuotesDisplay, (quote) =>
    quote.quoteNo || quote.quoteIndexId || quote.id || ""
  );
  if (!isDevRuntime) return;
  visibleSavedQuotesDisplay.slice(0, SAVED_QUOTES_PAGE_SIZE).forEach((quote, index) => {
    const sortInfo = getQuoteListSortInfo(quote);
    devLog("SAVED_QUOTE_CARD_SORT_TRACE", {
      renderedIndex: index,
      quoteNo: quote?.quoteNo || quote?.quoteNoString || "",
      quoteId: quote?.quoteId || quote?.sourceQuoteId || quote?.id || "",
      quoteIndexId: quote?.quoteIndexId || quote?.indexId || "",
      sortSource: sortInfo.source,
      sortMillis: sortInfo.millis,
      lastActivityAt: quote?.lastActivityAt || null,
      updatedAt: quote?.updatedAt || null,
      createdAt: quote?.createdAt || null,
      quoteDate: quote?.quoteDate || null,
      date: quote?.date || null,
      visibleInDefaultList: isQuoteVisibleInSavedQuotesDefault(quote),
      cancelled: isCancelledQuoteRecord(quote),
      deletedOrBin: isDeletedQuoteRecord(quote),
      loadSource: quote?.savedQuotesLoadSource || "",
    });
  });
}, [visibleSavedQuotesDisplay]);

const hasMoreSavedQuotesDisplay =
  visibleSavedQuotesDisplay.length < filteredSavedQuotesForDisplay.length;
const canLoadMoreSavedQuoteIndex =
  !activeBusinessDashboardDrilldown &&
  (savedQuotesSearchText ? savedSearchResult.hasMore : hasMoreSavedQuoteIndex);

const recycleBinQuotesForDisplay = activeBusinessDashboardDrilldown
  ? []
  : recycleBinOpen
  ? savedQuotesViewModel.filteredRecycledQuotes
  : [];

const crmLeadDeals = useMemo(() => {
  return crmLeadRecords.filter((lead) => !isArchivedDuplicateCrmLead(lead)).map((lead) => {
    const crmData = buildCrmLeadData(lead);
    const linkedQuote = findLinkedQuoteForLead(crmData);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(crmData.linkedQuotes || []);
    const liveQuoteTotal = linkedQuote ? getNormalizedQuoteTotal(linkedQuote) : 0;
    const displayedDealValue =
      liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.expectedValue || 0);
    const stageMeta = getStageMetaForType(crmData.pipelineType, crmData.stageId);
    const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
    const checklistSummary = getChecklistSummary({
      ...crmData,
      checklistItems: checklist,
    });
    const followUpSummary = getFollowUpSummary(crmData);
    const nextFollowUp = followUpSummary.nextFollowUp || null;
    const installationSummary = getInstallationSummary(crmData);
    const createdMillis = getCreatedMillis(crmData);
    const updatedMillis = getUpdatedMillis(crmData);
    const installationMillis = getInstallationMillis({
      ...crmData,
      installationDate: installationSummary.installationDate,
    });
    const nextAction = nextFollowUp?.title || crmData.nextAction || "";
    const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
    const nextActionMillis = toMillis(nextActionDate);
    const customerLabel =
      crmData.contact?.name ||
      crmData.companyOrProject ||
      crmData.title ||
      "Unnamed Lead";
    const salesStageId = getDealSalesStageId({ ...lead, ...crmData });
    const salesStage = getSalesPipelineStage(salesPipeline, salesStageId);

    return {
      ...crmData,
      id: lead.id,
      crmRecordType: "lead",
      crm: {
        leadId: lead.id,
        title: crmData.title,
        contact: crmData.contact,
        stageId: crmData.stageId,
        linkedQuotes: crmData.linkedQuotes || [],
        hasQuote: crmData.hasQuote,
        nextAction,
        nextActionDate,
        assignedToUserId: crmData.assignedToUserId,
        assignedToNameSnapshot: crmData.assignedToNameSnapshot,
        assignedTo: crmData.assignedToNameSnapshot,
        assignedAt: crmData.assignedAt,
        assignedBy: crmData.assignedBy,
        notes: crmData.notes,
        tags: crmData.tags,
        checklistItems: checklist,
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
      },
      primaryLinkedQuote,
      quoteNo: primaryLinkedQuote?.quoteNo || "",
      customerLabel,
      quoteTypeResolved: crmData.pipelineType,
      workStage: crmData.stageId,
      workStageResolved: crmData.stageId,
      createdMillis,
      updatedMillis,
      confirmedMillis:
        String(crmData.status || "").toLowerCase() === "confirmed"
          ? updatedMillis || createdMillis
          : 0,
      installationMillis,
      installationDate: installationSummary.installationDate || crmData.installationDate || "",
      installationSummary,
      totalValue: displayedDealValue,
      crmStageId: stageMeta?.id || crmData.stageId,
      crmStageLabel: stageMeta?.label || crmData.stageNameSnapshot || crmData.stageId,
      crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
      salesStageId: salesStage?.stageId || salesStageId,
      salesStageLabel: salesStage?.label || getOpportunityStageLabel(salesStageId),
      salesStageColor: salesStage?.color || "#64748b",
      stageMeta,
      checklistItems: checklist,
      checklistCompleted: checklistSummary.completed,
      checklistTotal: checklistSummary.total,
      checklistSummary,
      nextAction,
      nextActionDate,
      nextActionMillis,
      crmNotes: crmData.notes || "",
      crmTitle: crmData.title || customerLabel,
      crmContact: crmData.contact || {},
      source: crmData.source || crmData.contact?.source || "",
      followUps: crmData.followUps || [],
      followUpSummary,
      reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      attachments: crmData.attachments || [],
      executionSpecs: crmData.executionSpecs || [],
      siteLocations: crmData.siteLocations || [],
      assignedToUserId: crmData.assignedToUserId || "",
      assignedToNameSnapshot: crmData.assignedToNameSnapshot || "",
      pipelineType: crmData.pipelineType,
      isCrmCancelled: stageMeta?.isCancelled === true,
      isCrmClosed: stageMeta?.marksComplete === true,
      isCrmConfirmed: stageMeta?.confirmsQuote === true,
      expectedValue: displayedDealValue,
      finalValue: liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.finalValue || 0),
      hasQuote: crmData.hasQuote === true,
      linkedQuotes: crmData.linkedQuotes || [],
    };
  });
}, [
  buildCrmLeadData,
  crmLeadRecords,
  findLinkedQuoteForLead,
  getDealSalesStageId,
    getNormalizedQuoteTotal,
  getStageMetaForType,
  salesPipeline,
]);

const crmDeals = useMemo(() => {
  // An archived authoritative deal still owns its quote links; do not synthesize it back into Today.
  const leadIdSet = new Set(crmLeadRecords.map((deal) => deal.id));
  const ownedQuoteIds = new Set(crmLeadRecords.flatMap((lead) =>
    normalizeCrmLinkedQuotes(lead).map((link) => String(link.quoteId || "")).filter(Boolean)
  ));
  const quoteDeals = dashboardAllQuotes
    .filter((quote) => {
      const leadId = quote?.crmLeadId || quote?.leadId || quote?.crm?.leadId || "";
      return (!leadId || !leadIdSet.has(leadId)) &&
        !ownedQuoteIds.has(String(quote.sourceQuoteId || quote.quoteId || quote.id || ""));
    })
    .map((quote) => {
      const quoteType = resolveQuoteType(quote);
      const crmData = buildQuoteCrmData(quote);
      const stageMeta = getStageMetaForType(quoteType, crmData.stageId);
      const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
      const checklistSummary = getChecklistSummary({
        ...crmData,
        checklistItems: checklist,
      });
      const followUpSummary = getFollowUpSummary(crmData);
      const nextFollowUp = followUpSummary.nextFollowUp || null;
      const installationSummary = getInstallationSummary(crmData);
      const nextAction = nextFollowUp?.title || crmData.nextAction || "";
      const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
      const nextActionMillis = toMillis(nextActionDate);
      const salesStageId = deriveSalesStageIdForQuote(quote, crmData);
      const salesStage = getSalesPipelineStage(salesPipeline, salesStageId);

      return {
        ...quote,
        crmRecordType: "quote",
        crm: crmData,
        quoteTypeResolved: quoteType,
        crmStageId: stageMeta?.id || crmData.stageId,
        crmStageLabel: stageMeta?.label || crmData.stageName || crmData.stageId,
        crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
        salesStageId: salesStage?.stageId || salesStageId,
        salesStageLabel: salesStage?.label || getOpportunityStageLabel(salesStageId),
        salesStageColor: salesStage?.color || "#64748b",
        stageMeta,
        checklistItems: checklist,
        checklistCompleted: checklistSummary.completed,
        checklistTotal: checklistSummary.total,
        checklistSummary,
        nextAction,
        nextActionDate,
        nextActionMillis,
        crmNotes: crmData.notes || "",
        crmTitle: crmData.title || quote.customerLabel,
        crmContact: crmData.contact || {},
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        followUpSummary,
        installationDate: installationSummary.installationDate || crmData.installationDate || "",
        installationSummary,
        reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
        attachments: crmData.attachments || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
        assignedToUserId: crmData.assignedToUserId || "",
        assignedToNameSnapshot:
          crmData.assignedToNameSnapshot || crmData.assignedTo || "",
        pipelineType: quoteType,
        isCrmCancelled: stageMeta?.isCancelled === true || quote.isCancelled,
        isCrmClosed: stageMeta?.marksComplete === true,
        isCrmConfirmed:
          quote.isConfirmed || stageMeta?.confirmsQuote === true,
        expectedValue: Number(crmData.expectedValue || quote.totalValue || 0),
        finalValue: Number(crmData.finalValue || quote.totalValue || 0),
        hasQuote: true,
        primaryLinkedQuote: {
          quoteId: quote.id,
          quoteNo: quote.quoteNo || "",
          type: quoteType,
          value: Number(crmData.finalValue || quote.totalValue || 0),
          isPrimary: true,
        },
      };
    });

  return dedupeCrmDealsByQuote([...crmLeadDeals, ...quoteDeals]).sort(
    (a, b) => Number(b.updatedMillis || 0) - Number(a.updatedMillis || 0)
  );
}, [
  buildQuoteCrmData,
  crmLeadDeals,
  crmLeadRecords,
  dashboardAllQuotes,
  deriveSalesStageIdForQuote,
  getStageMetaForType,
  salesPipeline,
]);

const salesPipelineActiveStages = useMemo(
  () => getActiveSalesPipelineStages(salesPipeline),
  [salesPipeline]
);

const getSalesStageStatus = useCallback((stage = {}) => {
  if (stage?.isWon === true || stage?.stageId === CRM_OPPORTUNITY_STATUS_WON) {
    return CRM_OPPORTUNITY_STATUS_WON;
  }
  if (stage?.isLost === true || stage?.stageId === CRM_OPPORTUNITY_STATUS_LOST) {
    return CRM_OPPORTUNITY_STATUS_LOST;
  }
  if (stage?.isNurture === true || stage?.stageId === CRM_OPPORTUNITY_STATUS_NURTURE) {
    return CRM_OPPORTUNITY_STATUS_NURTURE;
  }
  return CRM_OPPORTUNITY_STATUS_OPEN;
}, []);

const salesPipelineStageHasActiveDeals = useCallback(
  (stageId = "") => {
    const normalizedStageId = String(stageId || "").trim();
    if (!normalizedStageId) return false;
    return crmDeals.some((deal) => {
      const dealStageId = String(deal.salesStageId || getDealSalesStageId(deal) || "").trim();
      return dealStageId === normalizedStageId && isOpenCrmDeal(deal);
    });
  },
  [crmDeals, getDealSalesStageId, isOpenCrmDeal]
);

const updateSalesPipelineStageDraft = useCallback(
  (stageId, patch = {}) => {
    const normalizedStageId = String(stageId || "").trim();
    if (!normalizedStageId) return;
    if (patch.active === false && salesPipelineStageHasActiveDeals(normalizedStageId)) {
      notify("This stage has active deals. Move them to another stage before deactivating.", "warning");
      return;
    }
    setSalesPipeline((prev) => {
      const normalized = normalizeSalesPipeline(prev);
      return normalizeSalesPipeline({
        ...normalized,
        stages: normalized.stages.map((stage) =>
          stage.stageId === normalizedStageId
            ? {
                ...stage,
                ...patch,
                stageId: stage.stageId,
                id: stage.stageId,
              }
            : stage
        ),
      });
    });
  },
  [notify, salesPipelineStageHasActiveDeals]
);

const moveSalesPipelineStage = useCallback((stageId, direction) => {
  const normalizedStageId = String(stageId || "").trim();
  setSalesPipeline((prev) => {
    const normalized = normalizeSalesPipeline(prev);
    const stages = [...normalized.stages].sort(
      (left, right) => Number(left.order || 0) - Number(right.order || 0)
    );
    const index = stages.findIndex((stage) => stage.stageId === normalizedStageId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= stages.length) return normalized;
    const [stage] = stages.splice(index, 1);
    stages.splice(targetIndex, 0, stage);
    const reorderedStages = stages.map((item, itemIndex) => ({
      ...item,
      order: (itemIndex + 1) * 10,
    }));
    return normalizeSalesPipeline({ ...normalized, stages: reorderedStages });
  });
}, []);

const addSalesPipelineStage = useCallback(() => {
  setSalesPipeline((prev) => {
    const normalized = normalizeSalesPipeline(prev);
    return normalizeSalesPipeline({
      ...normalized,
      stages: [
        ...normalized.stages,
        {
          ...createSalesPipelineStageDraft("New Stage"),
          order: (normalized.stages.length + 1) * 10,
        },
      ],
    });
  });
}, []);

const restoreDefaultSalesPipeline = useCallback(() => {
  const confirmed =
    typeof window === "undefined" ||
    window.confirm("Restore the default Sales Pipeline stages for this workspace?");
  if (!confirmed) return;
  setSalesPipeline(normalizeSalesPipeline(DEFAULT_SALES_PIPELINE));
}, []);

const saveSalesPipelineSettings = useCallback(async () => {
  if (!workspaceUid || !canManageCrmSettings) {
    notify("You do not have permission to edit CRM settings.", "error");
    return;
  }
  if (isSavingSalesPipeline) return;
  const normalized = normalizeSalesPipeline(salesPipeline);
  setIsSavingSalesPipeline(true);
  try {
    const pipelineRef = doc(db, "users", workspaceUid, "pipelines", "sales");
    await setDoc(
      pipelineRef,
      sanitizeCrmFirestoreValue({
        ...normalized,
        workspaceUid,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || "",
      }),
      { merge: true }
    );
    invalidateFirestoreReadCache(pipelineRef);
    setSalesPipeline(normalized);
    setSalesPipelineLoadState({ loading: false, loadedWorkspace: workspaceUid, error: "" });
    if (isDevRuntime) {
      devLog("PIPELINE_SETTINGS_TRACE", {
        workspaceUid,
        pipelineExists: true,
        defaultCreated: false,
        stageCount: normalized.stages.length,
        activeStageCount: normalized.stages.filter((stage) => stage.active !== false).length,
      });
    }
    notify("Sales pipeline settings saved", "success");
  } catch (error) {
    devError("Sales pipeline settings save failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path: `users/${workspaceUid}/pipelines/sales`,
    });
    notify("Could not save sales pipeline settings.", "error");
  } finally {
    setIsSavingSalesPipeline(false);
  }
}, [
  canManageCrmSettings,
  isSavingSalesPipeline,
  notify,
  salesPipeline,
  user?.uid,
  workspaceUid,
]);

const orderPipelineActiveStages = useMemo(
  () => getActiveOrderPipelineStages(orderPipeline),
  [orderPipeline]
);

const orderPipelineStageHasActiveProjects = useCallback(
  (stageId = "") => {
    const normalizedStageId = String(stageId || "").trim();
    if (!normalizedStageId) return false;
    return crmProjectRecords.some((project) => {
      const projectStageId = resolveProjectPipelineStage(project);
      const isClosedOrCancelled =
        projectStageId === "project_closed" ||
        projectStageId === "cancelled" ||
        ["closed", "cancelled", "canceled"].includes(String(project.status || "").toLowerCase());
      return projectStageId === normalizedStageId && !isClosedOrCancelled;
    });
  },
  [crmProjectRecords]
);

const updateOrderPipelineStageDraft = useCallback(
  (stageId, patch = {}) => {
    const normalizedStageId = String(stageId || "").trim();
    if (!normalizedStageId) return;
    if (patch.active === false && orderPipelineStageHasActiveProjects(normalizedStageId)) {
      notify("This stage has active orders. Move them to another stage before deactivating.", "warning");
      return;
    }
    setOrderPipeline((prev) => {
      const normalized = normalizeOrderPipeline(prev);
      return normalizeOrderPipeline({
        ...normalized,
        stages: normalized.stages.map((stage) =>
          stage.stageId === normalizedStageId
            ? {
                ...stage,
                ...patch,
                stageId: stage.stageId,
                id: stage.stageId,
              }
            : stage
        ),
      });
    });
  },
  [notify, orderPipelineStageHasActiveProjects]
);

const moveOrderPipelineStage = useCallback((stageId, direction) => {
  const normalizedStageId = String(stageId || "").trim();
  setOrderPipeline((prev) => {
    const normalized = normalizeOrderPipeline(prev);
    const stages = [...normalized.stages].sort(
      (left, right) => Number(left.order || 0) - Number(right.order || 0)
    );
    const index = stages.findIndex((stage) => stage.stageId === normalizedStageId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= stages.length) return normalized;
    const [stage] = stages.splice(index, 1);
    stages.splice(targetIndex, 0, stage);
    const reorderedStages = stages.map((item, itemIndex) => ({
      ...item,
      order: (itemIndex + 1) * 10,
    }));
    return normalizeOrderPipeline({ ...normalized, stages: reorderedStages });
  });
}, []);

const addOrderPipelineStage = useCallback(() => {
  setOrderPipeline((prev) => {
    const normalized = normalizeOrderPipeline(prev);
    return normalizeOrderPipeline({
      ...normalized,
      stages: [
        ...normalized.stages,
        {
          ...createOrderPipelineStageDraft("New Stage"),
          order: (normalized.stages.length + 1) * 10,
        },
      ],
    });
  });
}, []);

const restoreDefaultOrderPipeline = useCallback(() => {
  const confirmed =
    typeof window === "undefined" ||
    window.confirm("Restore the default Orders Pipeline stages for this workspace?");
  if (!confirmed) return;
  setOrderPipeline(normalizeOrderPipeline(DEFAULT_ORDER_PIPELINE));
}, []);

const saveOrderPipelineSettings = useCallback(async () => {
  if (!workspaceUid || !canManageCrmSettings) {
    notify("You do not have permission to edit CRM settings.", "error");
    return;
  }
  if (isSavingOrderPipeline) return;
  const normalized = normalizeOrderPipeline(orderPipeline);
  setIsSavingOrderPipeline(true);
  try {
    const pipelineRef = doc(db, "users", workspaceUid, "pipelines", "orders");
    await setDoc(
      pipelineRef,
      sanitizeCrmFirestoreValue({
        ...normalized,
        workspaceUid,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || "",
      }),
      { merge: true }
    );
    invalidateFirestoreReadCache(pipelineRef);
    setOrderPipeline(normalized);
    setOrderPipelineLoadState({ loading: false, loadedWorkspace: workspaceUid, error: "" });
    if (isDevRuntime) {
      devLog("ORDER_PIPELINE_SETTINGS_TRACE", {
        workspaceUid,
        pipelineExists: true,
        defaultCreated: false,
        stageCount: normalized.stages.length,
        activeStageCount: normalized.stages.filter((stage) => stage.active !== false).length,
      });
    }
    notify("Orders pipeline settings saved", "success");
  } catch (error) {
    devError("Orders pipeline settings save failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path: `users/${workspaceUid}/pipelines/orders`,
    });
    notify("Could not save orders pipeline settings.", "error");
  } finally {
    setIsSavingOrderPipeline(false);
  }
}, [
  canManageCrmSettings,
  isSavingOrderPipeline,
  notify,
  orderPipeline,
  user?.uid,
  workspaceUid,
]);

const updateSalesPipelineStageForDeal = useCallback(
  async (deal = {}, requestedStageId = "") => {
    if (!workspaceUid || !canAccessCrm) {
      notify("Please sign in before updating CRM stages.", "warning");
      return;
    }
    if (!canEditQuotes) {
      notify("You do not have permission to update CRM stages.", "error");
      return;
    }
    const currentStageId = String(deal.salesStageId || getDealSalesStageId(deal) || "").trim();
    const nextStage = getSalesPipelineStage(salesPipeline, requestedStageId);
    const nextStageId = String(nextStage?.stageId || requestedStageId || "").trim();
    if (!nextStageId || nextStageId === currentStageId) return;

    const nextStatus = getSalesStageStatus(nextStage);
    const actor = getCrmActorSnapshot();
    const now = new Date();
    const leadId =
      deal.crmRecordType === "lead"
        ? String(deal.id || deal.leadId || "").trim()
        : String(deal.crm?.leadId || deal.leadId || deal.crmLeadId || "").trim();
    const identity = getSavedQuoteWorkflowIdentity(deal, workspaceUid);
    const quoteId = String(
      identity.sourceQuoteId ||
        identity.quoteId ||
        deal.quoteId ||
        (deal.crmRecordType === "quote" ? deal.id : "") ||
        deal.primaryLinkedQuote?.quoteId ||
        ""
    ).trim();
    const quoteIndexId = String(identity.quoteIndexId || quoteId || deal.quoteIndexId || "").trim();
    const opportunityId = leadId
      ? getCrmOpportunityIdFromLead({ ...deal, id: leadId })
      : getCrmOpportunityIdFromQuote({ ...deal, id: quoteId, quoteId });
    const trace = {
      dealId: String(deal.id || leadId || quoteId || "").trim(),
      quoteId,
      quoteNo: String(deal.quoteNo || deal.primaryLinkedQuote?.quoteNo || "").trim(),
      oldStage: currentStageId,
      newStage: nextStageId,
      changedBy: actor.uid || "",
      dealUpdated: false,
      quoteIndexUpdatedIfNeeded: false,
      activityLogged: false,
      errors: [],
    };

    try {
      let lostReason = "";
      if (nextStatus === CRM_OPPORTUNITY_STATUS_LOST) {
        lostReason =
          typeof window === "undefined"
            ? "Marked lost"
            : String(window.prompt("Why was this deal lost?") || "").trim();
        if (!lostReason) {
          notify("Please add a lost reason before marking the deal lost.", "warning");
          return;
        }
      }

      let followUpDate = "";
      if (nextStageId === "quotation_sent") {
        const hasLinkedQuote = Boolean(quoteId || deal.hasQuote || deal.primaryLinkedQuote?.quoteId);
        if (
          !hasLinkedQuote &&
          typeof window !== "undefined" &&
          !window.confirm("This deal has no linked quote yet. Move it to Quotation Sent anyway?")
        ) {
          return;
        }
        followUpDate =
          typeof window === "undefined"
            ? ""
            : String(window.prompt("Optional follow-up date after sending quote (YYYY-MM-DD). Leave blank to skip.") || "").trim();
        if (followUpDate && !parseDateInputValue(followUpDate)) {
          notify("Please enter a valid follow-up date in YYYY-MM-DD format.", "warning");
          return;
        }
      }

      if (
        nextStatus === CRM_OPPORTUNITY_STATUS_WON &&
        typeof window !== "undefined" &&
        !window.confirm("Mark this opportunity as won? You can create the order/project later.")
      ) {
        return;
      }

      const shouldPatchMainStatus =
        [CRM_OPPORTUNITY_STATUS_WON, CRM_OPPORTUNITY_STATUS_LOST, CRM_OPPORTUNITY_STATUS_NURTURE].includes(nextStatus) ||
        [CRM_OPPORTUNITY_STATUS_WON, CRM_OPPORTUNITY_STATUS_LOST, CRM_OPPORTUNITY_STATUS_NURTURE].includes(
          normalizeOpportunityStatus(deal.status || deal.salesStatus || "")
        );
      const statusPatch = shouldPatchMainStatus ? { status: nextStatus } : {};
      const followUpPatch = followUpDate
        ? {
            nextAction: deal.nextAction || deal.crm?.nextAction || "Follow up on quotation",
            nextActionDate: parseDateInputValue(followUpDate),
          }
        : {};

      if (leadId) {
        const leadPatch = sanitizeCrmFirestoreValue({
          salesStageId: nextStageId,
          opportunityStage: nextStageId,
          salesStatus: nextStatus,
          ...statusPatch,
          ...(lostReason ? { lostReason } : {}),
          ...followUpPatch,
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          lastEditedAt: serverTimestamp(),
          lastEditedBy: actor.uid || "",
          lastEditedByName: actor.name || "",
        });
        await setDoc(doc(db, "users", workspaceUid, "crm_leads", leadId), leadPatch, {
          merge: true,
        });
        applyCrmLeadLocalPatch(leadId, {
          salesStageId: nextStageId,
          opportunityStage: nextStageId,
          salesStatus: nextStatus,
          ...statusPatch,
          ...(lostReason ? { lostReason } : {}),
          ...followUpPatch,
          updatedAt: now,
          lastActivityAt: now,
          lastEditedAt: now,
          lastEditedBy: actor.uid || "",
          lastEditedByName: actor.name || "",
        });
        trace.dealUpdated = true;
        invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
      }

      if (quoteId) {
        const crmPatch = {
          ...(deal.crm || {}),
          salesStageId: nextStageId,
          opportunityStage: nextStageId,
          salesStatus: nextStatus,
          ...(followUpPatch.nextAction ? { nextAction: followUpPatch.nextAction } : {}),
          ...(followUpPatch.nextActionDate ? { nextActionDate: followUpPatch.nextActionDate } : {}),
        };
        const quotePatch = sanitizeCrmFirestoreValue({
          salesStageId: nextStageId,
          opportunityStage: nextStageId,
          salesStatus: nextStatus,
          ...(lostReason ? { lostReason } : {}),
          crm: crmPatch,
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
        });
        await setDoc(doc(db, "users", workspaceUid, "quotations", quoteId), quotePatch, {
          merge: true,
        });
        if (quoteIndexId) {
          await setDoc(
            doc(db, "users", workspaceUid, "quote_index", quoteIndexId),
            sanitizeCrmFirestoreValue({
              salesStageId: nextStageId,
              opportunityStage: nextStageId,
              salesStatus: nextStatus,
              ...(lostReason ? { lostReason } : {}),
              updatedAt: serverTimestamp(),
              lastActivityAt: serverTimestamp(),
            }),
            { merge: true }
          );
          trace.quoteIndexUpdatedIfNeeded = true;
        }
        applyQuoteLocalPatch(
          quoteId,
          {
            salesStageId: nextStageId,
            opportunityStage: nextStageId,
            salesStatus: nextStatus,
            ...(lostReason ? { lostReason } : {}),
            crm: crmPatch,
            updatedAt: now,
            lastActivityAt: now,
          },
          { persistIndex: false, identity }
        );
        trace.dealUpdated = true;
        invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
        invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      }

      if (opportunityId) {
        await setDoc(
          doc(db, "users", workspaceUid, "opportunities", opportunityId),
          sanitizeCrmFirestoreValue({
            opportunityId,
            workspaceUid,
            stage: nextStageId,
            salesStageId: nextStageId,
            stageLabel: nextStage?.label || getOpportunityStageLabel(nextStageId),
            status: nextStatus,
            ...(lostReason ? { lostReason } : {}),
            ...(followUpPatch.nextAction
              ? {
                  nextActionText: followUpPatch.nextAction,
                  nextActionDueAt: followUpPatch.nextActionDate,
                  nextActionMissing: false,
                }
              : {}),
            updatedAt: serverTimestamp(),
            updatedBy: actor.uid || "",
            lastActivityAt: serverTimestamp(),
          }),
          { merge: true }
        );
        invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
      }

      if (opportunityId && followUpPatch.nextAction && followUpPatch.nextActionDate) {
        await setOpportunityNextAction(opportunityId, {
          nextActionText: followUpPatch.nextAction,
          nextActionDueAt: followUpPatch.nextActionDate,
          ownerUid: deal.assignedToUserId || actor.uid || "",
          ownerName: deal.assignedToNameSnapshot || actor.name || "",
          taskType: "follow_up",
        }).catch((error) => {
          trace.errors.push(`follow_up_task:${error?.code || error?.message || String(error)}`);
        });
      }

      await logCrmActivity({
        relatedType: leadId ? "lead" : quoteId ? "quote" : "opportunity",
        relatedId: leadId || quoteId || opportunityId,
        opportunityId,
        quoteId,
        quoteNo: trace.quoteNo,
        customerId: String(deal.customerId || deal.crm?.customerId || "").trim(),
        customerName: deal.customerName || deal.customerLabel || deal.crmContact?.name || "",
        activityType:
          nextStatus === CRM_OPPORTUNITY_STATUS_LOST
            ? "lost"
            : nextStatus === CRM_OPPORTUNITY_STATUS_WON
            ? "won"
            : "stage_changed",
        title: "Pipeline stage changed",
        description: `${getOpportunityStageLabel(currentStageId)} -> ${nextStage?.label || getOpportunityStageLabel(nextStageId)}`,
      }).then((entry) => {
        trace.activityLogged = Boolean(entry);
      }).catch((error) => {
        trace.errors.push(`activity:${error?.code || error?.message || String(error)}`);
      });

      if (isDevRuntime) {
        devLog("PIPELINE_STAGE_CHANGE_TRACE", trace);
      }
      notify(`Moved to ${nextStage?.label || getOpportunityStageLabel(nextStageId)}`, "success");
    } catch (error) {
      trace.errors.push(error?.code || error?.message || String(error));
      devError("PIPELINE_STAGE_CHANGE_FAILED", {
        code: error?.code || "",
        message: error?.message || String(error),
        trace,
      });
      notify("Could not update pipeline stage. Please try again.", "error");
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    canAccessCrm,
    canEditQuotes,
    getCrmActorSnapshot,
    getCrmOpportunityIdFromLead,
    getCrmOpportunityIdFromQuote,
    getDealSalesStageId,
    getSalesStageStatus,
    logCrmActivity,
    notify,
    parseDateInputValue,
    salesPipeline,
    setOpportunityNextAction,
    workspaceUid,
  ]
);

const scanDuplicateQuoteDeals = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  setCrmDuplicateDealAudit((prev) => ({
    ...prev,
    isScanning: true,
    scanned: true,
  }));

  try {
    let leadRecords = crmLeadRecords;
    if (!leadRecords.length) {
      const leadSnap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_LEADS_INITIAL_READ_LIMIT)
        ),
        {
          label: "duplicateDealScanRecentLeads",
          cacheKey: `users/${workspaceUid}/crm_leads?duplicateScanRecent=${CRM_LEADS_INITIAL_READ_LIMIT}`,
        }
      );
      leadRecords = leadSnap.docs.map((leadDoc) => ({
        id: leadDoc.id,
        ...leadDoc.data(),
      }));
    }
    const groups = buildQuoteDealDuplicateGroups(leadRecords);

    if (isDevRuntime) {
      groups.forEach((group) => {
        devLog("Quote deal duplicate audit", {
          quoteId: group.quoteId,
          quoteNo: group.quoteNo,
          matchedLeadIds: group.leads.map((lead) => lead.id),
          canonicalLeadId: group.canonicalLead?.id || "",
        });
      });
    }

    setCrmDuplicateDealAudit({
      scanned: true,
      isScanning: false,
      groups,
    });
    notify(
      groups.length
        ? `${groups.length} duplicate quote deal group${groups.length === 1 ? "" : "s"} found`
        : "No duplicate quote deals found",
      groups.length ? "warning" : "success"
    );
  } catch (error) {
    devError("Duplicate quote deal scan failed:", error);
    setCrmDuplicateDealAudit((prev) => ({
      ...prev,
      isScanning: false,
    }));
    notify("Could not scan duplicate deals. Please try again.", "error");
  }
}, [crmLeadRecords, notify, workspaceUid]);

const repairDuplicateQuoteDealGroup = useCallback(
  async (group = {}) => {
    if (!workspaceUid || !group?.canonicalLead?.id) return;

    const canonicalLead = group.canonicalLead;
    const duplicateLeads = (group.duplicateLeads || []).filter(
      (lead) => lead?.id && lead.id !== canonicalLead.id
    );
    if (!duplicateLeads.length) return;

    setRepairingDuplicateDealKey(group.key || canonicalLead.id);

    try {
      const mergedByKey = (items = [], getKey) => {
        const seen = new Set();
        return items.filter((item, index) => {
          if (!item) return false;
          const key =
            getKey?.(item) ||
            item.id ||
            item.reminderId ||
            item.downloadURL ||
            item.url ||
            item.title ||
            `item-${index}`;
          const normalizedKey = normalizeSearchText(key);
          if (!normalizedKey || seen.has(normalizedKey)) return false;
          seen.add(normalizedKey);
          return true;
        });
      };

      const allLeads = [canonicalLead, ...duplicateLeads];
      const mergedFollowUps = mergedByKey(
        allLeads.flatMap((lead) => normalizeCrmFollowUps(lead.followUps || [])),
        (item) =>
          item.id ||
          item.reminderId ||
          `${item.title || ""}:${item.dueDate || ""}:${item.dueTime || ""}`
      );
      const mergedAttachments = mergedByKey(
        allLeads.flatMap((lead) => lead.attachments || []),
        (item) => item.id || item.downloadURL || item.url || item.name
      );
      const mergedSiteLocations = mergedByKey(
        allLeads.flatMap((lead) => normalizeSiteLocations(lead.siteLocations || [])),
        (item) => item.id || item.googleMapsUrl || `${item.label}:${item.addressText}`
      );
      const mergedExecutionSpecs = mergedByKey(
        allLeads.flatMap((lead) => normalizeExecutionSpecs(lead.executionSpecs || [])),
        (item) => item.id || item.name || item.itemType
      );
      const mergedTags = uniqueValues(
        allLeads.flatMap((lead) => (Array.isArray(lead.tags) ? lead.tags : []))
      );
      const duplicateNotes = duplicateLeads
        .map((lead) => String(lead.notes || "").trim())
        .filter(Boolean)
        .filter((note) => !String(canonicalLead.notes || "").includes(note));
      const mergedNotes = [canonicalLead.notes || "", ...duplicateNotes]
        .filter(Boolean)
        .join("\n\n");
      const canonicalLinkedQuotes = normalizeCrmLinkedQuotes(canonicalLead);
      const mergedLinkedQuotes = normalizeCrmLinkedQuotes(
        {},
        {
          linkedQuotes: allLeads.flatMap((lead) => normalizeCrmLinkedQuotes(lead)),
        }
      );
      const canonicalPatch = {
        linkedQuotes: mergedLinkedQuotes.length ? mergedLinkedQuotes : canonicalLinkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: true,
        followUps: sanitizeCrmFirestoreValue(mergedFollowUps),
        attachments: sanitizeCrmFirestoreValue(mergedAttachments),
        siteLocations: sanitizeCrmFirestoreValue(mergedSiteLocations),
        executionSpecs: sanitizeCrmFirestoreValue(mergedExecutionSpecs),
        tags: mergedTags,
        notes: mergedNotes,
        duplicateDealRepairAt: serverTimestamp(),
        ...(isDevRuntime
          ? {
              _lastWriteSource: "duplicateDealRepair.canonical",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      await setDoc(
        doc(db, "users", workspaceUid, "crm_leads", canonicalLead.id),
        canonicalPatch,
        { merge: true }
      );

      const archivePayload = {
        status: ARCHIVED_DUPLICATE_STATUS,
        dealStatus: ARCHIVED_DUPLICATE_STATUS,
        isArchivedDuplicate: true,
        duplicateOfLeadId: canonicalLead.id,
        archivedAt: serverTimestamp(),
        archivedBy: auth.currentUser?.uid || user?.uid || "",
        ...(isDevRuntime
          ? {
              _lastWriteSource: "duplicateDealRepair.archive",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      await Promise.all(
        duplicateLeads.map((lead) =>
          setDoc(
            doc(db, "users", workspaceUid, "crm_leads", lead.id),
            archivePayload,
            { merge: true }
          )
        )
      );

      const quoteId =
        group.quoteId ||
        getCrmLeadQuoteReferenceSummary(canonicalLead).primaryQuoteId ||
        "";
      const quoteNo =
        group.quoteNo ||
        getCrmLeadQuoteReferenceSummary(canonicalLead).primaryQuoteNo ||
        "";
      const sourceQuote =
        dashboardAllQuotes.find(
          (quote) =>
            (quoteId && String(quote.id || "") === String(quoteId)) ||
            (quoteNo && String(quote.quoteNo || "") === String(quoteNo))
        ) || null;

      if (sourceQuote?.id) {
        const nextCrmPayload = buildQuoteCrmData(sourceQuote, {
          leadId: canonicalLead.id,
          hasQuote: true,
          stageId: canonicalLead.stageId || sourceQuote.workStage,
          status: canonicalLead.status || sourceQuote.status || "draft",
        });
        await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
          crm: nextCrmPayload,
          updatedAt: serverTimestamp(),
        });
        applyQuoteLocalPatch(sourceQuote.id, {
          crm: nextCrmPayload,
          updatedAt: new Date(),
        });
        await upsertQuoteDealLink({
          quoteId: sourceQuote.id,
          quoteNo: sourceQuote.quoteNo || quoteNo,
          leadId: canonicalLead.id,
          customerId: canonicalLead.customerId || sourceQuote.customerId || "",
          customerKey:
            canonicalLead.customerKey ||
            sourceQuote.customerKey ||
            canonicalLead.customerId ||
            sourceQuote.customerId ||
            "",
        });
      }

      applyCrmLeadLocalPatch(canonicalLead.id, {
        ...canonicalPatch,
        updatedAt: new Date(),
      });
      duplicateLeads.forEach((lead) =>
        applyCrmLeadLocalPatch(lead.id, {
          ...archivePayload,
          archivedAt: new Date(),
          updatedAt: new Date(),
        })
      );

      setCrmDuplicateDealAudit((prev) => ({
        ...prev,
        groups: prev.groups.filter((item) => item.key !== group.key),
      }));
      notify("Duplicate quote deals repaired", "success");
    } catch (error) {
      devError("Duplicate quote deal repair failed:", error, group);
      notify("Could not repair duplicate deals. Please try again.", "error");
    } finally {
      setRepairingDuplicateDealKey("");
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    dashboardAllQuotes,
    notify,
    upsertQuoteDealLink,
    user?.uid,
    workspaceUid,
  ]
);

const repairCrmSearchIndex = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  setIsRepairingCrmSearchIndex(true);
  try {
    let cursor = null;
    let repairedCount = 0;
    let checkedCount = 0;
    let hasMore = true;

    while (hasMore) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));

      const snap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          ...constraints
        ),
        {
          label: "repairCrmSearchIndexBatch",
          cacheKey: `users/${workspaceUid}/crm_leads?repairSearchAfter=${
            cursor?.id || "start"
          }`,
        }
      );

      if (snap.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      let pageWrites = 0;

      snap.docs.forEach((leadDoc) => {
        const lead = {
          id: leadDoc.id,
          ...(leadDoc.data() || {}),
        };
        const searchIndexFields = buildCrmSearchIndexFields(lead);
        batch.set(
          doc(db, "users", workspaceUid, "crm_leads", leadDoc.id),
          sanitizeCrmFirestoreValue({
            ...searchIndexFields,
            searchIndexRepairedAt: serverTimestamp(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "searchIndexRepair",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
          }),
          { merge: true }
        );
        pageWrites += 1;
      });

      if (pageWrites > 0) {
        await batch.commit();
      }

      checkedCount += snap.size;
      repairedCount += pageWrites;
      cursor = snap.docs[snap.docs.length - 1];
      hasMore = snap.size === 100;
    }

    setCrmLeadRecords((prev) =>
      prev.map((lead) => ({
        ...lead,
        ...buildCrmSearchIndexFields(lead),
      }))
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    notify(
      `Search index repaired for ${repairedCount} CRM deal${
        repairedCount === 1 ? "" : "s"
      } (${checkedCount} checked).`,
      "success"
    );
  } catch (error) {
    devError("CRM search index repair failed:", error);
    notify("Could not repair search index. Please try again.", "error");
  } finally {
    setIsRepairingCrmSearchIndex(false);
  }
}, [notify, workspaceUid]);

// Linked values are synchronized by explicit quote/CRM saves, never by browsing.

const activeCrmPipelineType =
  crmScope === CRM_SCOPE_ALL ? crmPipelineType : crmScope;

const crmScopeDeals = useMemo(
  () =>
    crmDeals
      .filter((deal) => crmScope === CRM_SCOPE_ALL || deal.pipelineType === crmScope)
      .filter((deal) => isCrmRecordVisibleToCurrentUser(deal)),
  [crmDeals, crmScope, isCrmRecordVisibleToCurrentUser]
);

const crmSourceOptions = useMemo(() => {
  const values = new Set();
  crmScopeDeals.forEach((deal) => {
    const sourceValue = String(
      deal.source || deal.crmContact?.source || ""
    ).trim();
    if (sourceValue) {
      values.add(sourceValue);
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmScopeDeals]);

const executionTemplateCatalog = useMemo(
  () =>
    normalizeExecutionTemplateDrafts(crmExecutionTemplates).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    ),
  [crmExecutionTemplates]
);

const normalizeExecutionTemplateMatchValue = useCallback(
  (value = "") =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, ""),
  []
);

const getExecutionTemplateForType = useCallback(
  (itemType = "") => {
    const normalizedType = normalizeExecutionTemplateMatchValue(itemType);
    if (!normalizedType) return null;

    const exactMatch =
      executionTemplateCatalog.find(
        (template) =>
          normalizeExecutionTemplateMatchValue(template.itemType) === normalizedType
      ) ||
      executionTemplateCatalog.find(
        (template) =>
          normalizeExecutionTemplateMatchValue(template.name) === normalizedType
      ) ||
      null;

    if (exactMatch) return exactMatch;

    return (
      executionTemplateCatalog.find((template) => {
        const templateType = normalizeExecutionTemplateMatchValue(template.itemType);
        return (
          Boolean(templateType) &&
          (normalizedType.includes(templateType) || templateType.includes(normalizedType))
        );
      }) ||
      executionTemplateCatalog.find((template) => {
        const templateName = normalizeExecutionTemplateMatchValue(template.name);
        return (
          Boolean(templateName) &&
          (normalizedType.includes(templateName) || templateName.includes(normalizedType))
        );
      }) ||
      null
    );
  },
  [executionTemplateCatalog, normalizeExecutionTemplateMatchValue]
);

const getExecutionTemplateById = useCallback(
  (templateId = "") =>
    executionTemplateCatalog.find((template) => template.id === templateId) || null,
  [executionTemplateCatalog]
);

const executionTemplateItemTypeOptions = useMemo(() => {
  const values = new Set(
    executionTemplateCatalog
      .map((template) => String(template.itemType || "").trim())
      .filter(Boolean)
  );

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    normalizeExecutionSpecs(deal.executionSpecs || []).forEach((item) => {
      const itemType = String(item.itemType || "").trim();
      if (itemType) {
        values.add(itemType);
      }
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmDeals, executionTemplateCatalog]);

const crmBoardFilterOptions = useMemo(
  () =>
    salesPipelineActiveStages.map((stage) => ({
      key: stage.stageId,
      label: stage.label,
    })),
  [salesPipelineActiveStages]
);

const crmBoardDeals = useMemo(() => {
  const queryText = normalizeSearchText(crmSearch);

  return crmScopeDeals
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmSourceFilter;
    })
    .filter((deal) => {
      if (crmStageFilter === "all") return true;
      return String(deal.salesStageId || getDealSalesStageId(deal) || "").trim() === crmStageFilter;
    })
    .sort((a, b) => b.updatedMillis - a.updatedMillis);
}, [
  crmScopeDeals,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  customerRecords,
  getDealSalesStageId,
]);

const selectedCrmDealFromRecords = useMemo(
  () =>
    crmLeadDeals.find((deal) => String(deal.id || "") === String(selectedCrmDealId || "")) ||
    crmScopeDeals.find((deal) => String(deal.id || "") === String(selectedCrmDealId || "")) ||
    null,
  [crmLeadDeals, crmScopeDeals, selectedCrmDealId]
);

const cachedSelectedCrmDeal = selectedCrmDealCacheRef.current;
const selectedCrmDeal =
  selectedCrmDealFromRecords ||
  (selectedCrmDealId &&
  cachedSelectedCrmDeal &&
  String(cachedSelectedCrmDeal.id || "") === String(selectedCrmDealId)
    ? cachedSelectedCrmDeal
    : null);
const selectedCrmOpportunityId = useMemo(
  () =>
    selectedCrmDeal
      ? getCrmOpportunityIdFromLead(selectedCrmDeal)
      : "",
  [getCrmOpportunityIdFromLead, selectedCrmDeal]
);

useEffect(() => {
  if (selectedCrmDealFromRecords) {
    selectedCrmDealCacheRef.current = selectedCrmDealFromRecords;
    selectedCrmDealIdRef.current = String(selectedCrmDealFromRecords.id || "");
    return;
  }

  if (!selectedCrmDealId) {
    selectedCrmDealCacheRef.current = null;
    selectedCrmDealIdRef.current = "";
  }
}, [selectedCrmDealFromRecords, selectedCrmDealId]);

useEffect(() => {
  if (!isDevRuntime) return;
  if (!(isCrmDrawerPinned || isCrmDrawerHovered || isCompact)) return;

  devLog("CRM drawer render audit", {
    drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
    selectedCrmDealId,
    hasSelectedFromRecords: Boolean(selectedCrmDealFromRecords),
    hasCache: Boolean(selectedCrmDealCacheRef.current),
    selectedName:
      selectedCrmDeal?.customerName ||
      selectedCrmDeal?.customerLabel ||
      selectedCrmDeal?.crmTitle ||
      selectedCrmDeal?.title ||
      "",
  });
}, [
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  selectedCrmDeal,
  selectedCrmDealFromRecords,
  selectedCrmDealId,
]);

const buildCrmLeadDetailsRecord = useCallback(
  (leadLike = {}) => {
    const leadId = String(leadLike?.id || leadLike?.leadId || "").trim();
    if (!leadId) return null;

    const existingDeal =
      crmLeadDeals.find((deal) => String(deal.id || "") === leadId) ||
      crmDeals.find(
        (deal) => deal.crmRecordType === "lead" && String(deal.id || "") === leadId
      );
    if (existingDeal) return existingDeal;

    const crmData = buildCrmLeadData({ ...leadLike, id: leadId });
    const linkedQuote = findLinkedQuoteForLead(crmData);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(crmData.linkedQuotes || []);
    const liveQuoteTotal = linkedQuote ? getNormalizedQuoteTotal(linkedQuote) : 0;
    const displayedDealValue =
      liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.expectedValue || 0);
    const stageMeta = getStageMetaForType(crmData.pipelineType, crmData.stageId);
    const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
    const checklistSummary = getChecklistSummary({
      ...crmData,
      checklistItems: checklist,
    });
    const followUpSummary = getFollowUpSummary(crmData);
    const nextFollowUp = followUpSummary.nextFollowUp || null;
    const installationSummary = getInstallationSummary(crmData);
    const createdMillis = getCreatedMillis(crmData);
    const updatedMillis = getUpdatedMillis(crmData);
    const installationMillis = getInstallationMillis({
      ...crmData,
      installationDate: installationSummary.installationDate,
    });
    const nextAction = nextFollowUp?.title || crmData.nextAction || "";
    const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
    const nextActionMillis = toMillis(nextActionDate);
    const customerLabel =
      crmData.contact?.name ||
      crmData.companyOrProject ||
      crmData.title ||
      "Unnamed Lead";

    return {
      ...crmData,
      id: leadId,
      crmRecordType: "lead",
      crm: {
        leadId,
        title: crmData.title,
        contact: crmData.contact,
        stageId: crmData.stageId,
        linkedQuotes: crmData.linkedQuotes || [],
        hasQuote: crmData.hasQuote,
        nextAction,
        nextActionDate,
        assignedToUserId: crmData.assignedToUserId,
        assignedToNameSnapshot: crmData.assignedToNameSnapshot,
        assignedTo: crmData.assignedToNameSnapshot,
        assignedAt: crmData.assignedAt,
        assignedBy: crmData.assignedBy,
        notes: crmData.notes,
        tags: crmData.tags,
        checklistItems: checklist,
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
        reminderConfigs: crmData.reminderConfigs || {},
        installationReminder: crmData.installationReminder || null,
      },
      primaryLinkedQuote,
      quoteNo: primaryLinkedQuote?.quoteNo || "",
      customerLabel,
      quoteTypeResolved: crmData.pipelineType,
      workStage: crmData.stageId,
      workStageResolved: crmData.stageId,
      createdMillis,
      updatedMillis,
      confirmedMillis:
        String(crmData.status || "").toLowerCase() === "confirmed"
          ? updatedMillis || createdMillis
          : 0,
      installationMillis,
      installationDate: installationSummary.installationDate || crmData.installationDate || "",
      installationSummary,
      totalValue: displayedDealValue,
      crmStageId: stageMeta?.id || crmData.stageId,
      crmStageLabel: stageMeta?.label || crmData.stageNameSnapshot || crmData.stageId,
      crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
      stageMeta,
      checklistItems: checklist,
      checklistCompleted: checklistSummary.completed,
      checklistTotal: checklistSummary.total,
      checklistSummary,
      nextAction,
      nextActionDate,
      nextActionMillis,
      crmNotes: crmData.notes || "",
      crmTitle: crmData.title || customerLabel,
      crmContact: crmData.contact || {},
      source: crmData.source || crmData.contact?.source || "",
      followUps: crmData.followUps || [],
      followUpSummary,
      reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      installationReminder: crmData.installationReminder || null,
      attachments: crmData.attachments || [],
      executionSpecs: crmData.executionSpecs || [],
      siteLocations: crmData.siteLocations || [],
      assignedToUserId: crmData.assignedToUserId || "",
      assignedToNameSnapshot: crmData.assignedToNameSnapshot || "",
      pipelineType: crmData.pipelineType,
      isCrmCancelled: stageMeta?.isCancelled === true,
      isCrmClosed: stageMeta?.marksComplete === true,
      isCrmConfirmed: stageMeta?.confirmsQuote === true,
      expectedValue: displayedDealValue,
      finalValue: liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.finalValue || 0),
      hasQuote: crmData.hasQuote === true,
      linkedQuotes: crmData.linkedQuotes || [],
    };
  },
  [
    buildCrmLeadData,
    crmDeals,
    crmLeadDeals,
    findLinkedQuoteForLead,
    getNormalizedQuoteTotal,
    getStageMetaForType,
  ]
);

const fetchCrmLeadForDetails = useCallback(
  async (leadId = "", sourceReason = "direct") => {
    const normalizedLeadId = String(leadId || "").trim();
    if (!workspaceUid || !normalizedLeadId) return null;

    const localLead =
      crmLeadRecords.find((lead) => String(lead.id || "") === normalizedLeadId) ||
      null;
    if (localLead) {
      return {
        leadId: normalizedLeadId,
        lead: buildCrmLeadDetailsRecord(localLead),
        sourceReason: `${sourceReason}:local_crm_leads`,
      };
    }

    try {
      const leadSnap = await getDoc(
        doc(db, "users", workspaceUid, "crm_leads", normalizedLeadId)
      );
      if (!leadSnap.exists()) return null;

      const rawLead = { id: leadSnap.id, ...leadSnap.data() };
      return {
        leadId: leadSnap.id,
        lead: buildCrmLeadDetailsRecord(rawLead),
        sourceReason: `${sourceReason}:firestore_crm_leads`,
      };
    } catch (error) {
      if (isDevRuntime) {
        devError("CRM details lead fetch failed", error, {
          leadId: normalizedLeadId,
          sourceReason,
          path: `users/${workspaceUid}/crm_leads/${normalizedLeadId}`,
        });
      }
      return null;
    }
  },
  [buildCrmLeadDetailsRecord, crmLeadRecords, workspaceUid]
);

const resolveCrmDealForDetails = useCallback(
  async (record = {}) => {
    if (!record) {
      return { leadId: "", lead: null, sourceReason: "missing_record" };
    }

    const tryLeadId = async (leadId, sourceReason) => {
      const resolved = await fetchCrmLeadForDetails(leadId, sourceReason);
      return resolved?.leadId && resolved?.lead ? resolved : null;
    };

    const directCandidates = [
      { value: record.id, reason: "record.id" },
      { value: record.leadId, reason: "record.leadId" },
      { value: record.crmLeadId, reason: "record.crmLeadId" },
      { value: record.canonicalLeadId, reason: "record.canonicalLeadId" },
      { value: record.crm?.leadId, reason: "record.crm.leadId" },
      { value: record.primaryLeadId, reason: "record.primaryLeadId" },
    ];

    for (const candidate of directCandidates) {
      const resolved = await tryLeadId(candidate.value, candidate.reason);
      if (resolved) return resolved;
    }

    const linkedQuoteSummary = getCrmLeadQuoteReferenceSummary(record);
    const quoteIds = [
      linkedQuoteSummary.primaryQuoteId,
      record.quoteId,
      record.primaryQuoteId,
      record.linkedQuoteId,
      record.primaryLinkedQuote?.quoteId,
      record.crm?.quoteId,
      record.crm?.primaryQuoteId,
      record.crmRecordType === "quote" ? record.id : "",
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);
    const quoteNos = [
      linkedQuoteSummary.primaryQuoteNo,
      record.quoteNo,
      record.primaryQuoteNo,
      record.linkedQuoteNo,
      record.primaryLinkedQuote?.quoteNo,
      record.crm?.quoteNo,
      record.crm?.primaryQuoteNo,
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);

    for (const quoteId of quoteIds) {
      const lookup = await findExistingDealForQuote({
        quoteId,
        quoteNo: quoteNos[0] || "",
        preferredLeadId: record.crm?.leadId || record.leadId || record.crmLeadId || "",
      });
      if (lookup?.canonicalLead?.id) {
        const resolved = await tryLeadId(
          lookup.canonicalLead.id,
          `quote_lookup:${quoteId}`
        );
        if (resolved) {
          return {
            ...resolved,
            sourceReason: `${resolved.sourceReason}:canonical_quote_lookup`,
          };
        }
      }

      const mappingLeadId = lookup?.mappingLeadId || "";
      const mappingResolved = await tryLeadId(
        mappingLeadId,
        `quote_deal_links:${quoteId}`
      );
      if (mappingResolved) return mappingResolved;

      const deterministicResolved = await tryLeadId(
        buildQuoteCreatedLeadId(quoteId),
        `deterministic_quote_lead:${quoteId}`
      );
      if (deterministicResolved) return deterministicResolved;
    }

    for (const quoteNo of quoteNos) {
      const localLead =
        crmLeadRecords.find((lead) => doesCrmLeadReferenceQuote(lead, "", quoteNo)) ||
        null;
      if (localLead?.id) {
        const resolved = await tryLeadId(localLead.id, `local_quote_no:${quoteNo}`);
        if (resolved) return resolved;
      }

      if (!workspaceUid) continue;
      try {
        const normalizedQuoteNo = normalizeSearchText(quoteNo);
        const crmLeadSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_leads"),
            firestoreWhere("searchKeywords", "array-contains", normalizedQuoteNo),
            firestoreLimit(10)
          ),
          {
            label: "resolveCrmDealByQuoteNo",
            cacheKey: `users/${workspaceUid}/crm_leads?quoteNo=${normalizedQuoteNo}&limit=10`,
          }
        );
        const matchingLeads = crmLeadSnap.docs
          .map((leadDoc) => ({ id: leadDoc.id, ...leadDoc.data() }))
          .filter((lead) => doesCrmLeadReferenceQuote(lead, "", quoteNo));
        const canonicalLead = chooseCanonicalQuoteDeal(matchingLeads);
        if (canonicalLead?.id) {
          const resolved = await tryLeadId(
            canonicalLead.id,
            `firestore_quote_no_scan:${quoteNo}`
          );
          if (resolved) return resolved;
        }
      } catch (error) {
        if (isDevRuntime) {
          devError("CRM details quote number scan failed", error, {
            quoteNo,
            path: `users/${workspaceUid}/crm_leads`,
          });
        }
      }
    }

    return {
      leadId: "",
      lead: null,
      sourceReason: quoteIds.length || quoteNos.length ? "quote_match_not_found" : "no_identifier",
    };
  },
  [crmLeadRecords, fetchCrmLeadForDetails, findExistingDealForQuote, workspaceUid]
);

const openCrmDealDetails = useCallback(
  async (record = {}, sourceOrOptions = "unknown", maybeOptions = {}) => {
    const source =
      typeof sourceOrOptions === "string"
        ? sourceOrOptions
        : sourceOrOptions?.source || sourceOrOptions?.crmView || "unknown";
    const options =
      typeof sourceOrOptions === "string" ? maybeOptions || {} : sourceOrOptions || {};
    const clickedRecordId = String(record?.id || record?.leadId || record?.crmLeadId || "");
    const customerName =
      record?.customerName ||
      record?.customerLabel ||
      record?.crmContact?.name ||
      record?.contact?.name ||
      "";
    const quoteNo =
      record?.quoteNo ||
      record?.primaryQuoteNo ||
      record?.primaryLinkedQuote?.quoteNo ||
      "";

    if (isDevRuntime) {
      devLog("OPEN CRM DETAILS START", {
        source,
        clickedRecordId,
        customerName,
        quoteNo,
        record,
      });
    }

    try {
      const resolved = await resolveCrmDealForDetails(record);

      if (isDevRuntime) {
        devLog("OPEN CRM DETAILS RESOLVED", {
          source,
          clickedRecordId,
          resolvedLeadId: resolved?.leadId || "",
          sourceReason: resolved?.sourceReason || "",
          customerName:
            resolved?.lead?.customerName ||
            resolved?.lead?.customerLabel ||
            resolved?.lead?.crmContact?.name ||
            customerName,
          quoteNo:
            resolved?.lead?.quoteNo ||
            resolved?.lead?.primaryLinkedQuote?.quoteNo ||
            quoteNo,
        });
      }

      if (!resolved?.leadId || !resolved?.lead) {
        setCrmDetailsOpenDebug({
          lastSource: source,
          lastRecordId: clickedRecordId,
          lastResolvedLeadId: "",
          lastResolveReason: resolved?.sourceReason || "no_lead",
          lastCustomerName: customerName,
          lastQuoteNo: quoteNo,
          lastError: "No CRM lead id could be resolved for this record.",
        });
        devError("OPEN CRM DETAILS FAILED: no leadId", {
          source,
          record,
          resolved,
        });
        notify("Could not open deal details. Please refresh and try again.", "error");
        return false;
      }

      const dealForDetails = {
        ...resolved.lead,
        id: resolved.leadId,
        crmRecordType: "lead",
      };
      const nextDraft = buildCrmDetailDraftFromDeal(dealForDetails);
      if (!nextDraft) {
        throw new Error("Could not build CRM details draft for selected lead.");
      }

      selectedCrmDealCacheRef.current = dealForDetails;
      selectedCrmDealIdRef.current = resolved.leadId;
      crmDetailDraftDealIdRef.current = resolved.leadId;
      crmDetailDraftDirtyRef.current = false;
      setSelectedCrmDealId(resolved.leadId);
      setCrmDetailDraft(nextDraft);
      markExecutionDetailsBaseline(nextDraft, "saved");
      setCrmDetailsSavedAt(null);
      setCrmDetailsOpenDebug({
        lastSource: source,
        lastRecordId: clickedRecordId,
        lastResolvedLeadId: resolved.leadId,
        lastResolveReason: resolved.sourceReason || "",
        lastCustomerName:
          dealForDetails.customerName ||
          dealForDetails.customerLabel ||
          dealForDetails.crmContact?.name ||
          customerName,
        lastQuoteNo:
          dealForDetails.quoteNo ||
          dealForDetails.primaryLinkedQuote?.quoteNo ||
          quoteNo,
        lastError: "",
      });
      setCrmView((prev) => options.crmView || prev);
      setIsCrmDrawerPinned(true);
      setIsCrmDrawerHovered(false);

      if (isDevRuntime) {
        devLog("OPEN CRM DETAILS APPLIED", {
          leadId: resolved.leadId,
          drawerOpen: true,
          customerName:
            dealForDetails.customerName ||
            dealForDetails.customerLabel ||
            dealForDetails.crmContact?.name ||
            "",
          sourceReason: resolved.sourceReason,
        });
      }

      return true;
    } catch (error) {
      setCrmDetailsOpenDebug({
        lastSource: source,
        lastRecordId: clickedRecordId,
        lastResolvedLeadId: "",
        lastResolveReason: "open_failed",
        lastCustomerName: customerName,
        lastQuoteNo: quoteNo,
        lastError: error?.message || String(error),
      });
      devError("OPEN CRM DETAILS FAILED", error, { source, record });
      notify("Could not open deal details. Please refresh and try again.", "error");
      return false;
    }
  },
  [
    buildCrmDetailDraftFromDeal,
    markExecutionDetailsBaseline,
    notify,
    resolveCrmDealForDetails,
  ]
);

useEffect(() => {
  if (!workspaceUid || !selectedCrmDealId || selectedCrmDealFromRecords) return undefined;

  const cachedDeal = selectedCrmDealCacheRef.current;
  if (cachedDeal && String(cachedDeal.id || "") === String(selectedCrmDealId)) {
    return undefined;
  }

  let cancelled = false;
  fetchCrmLeadForDetails(selectedCrmDealId, "selected_id_rehydrate").then((resolved) => {
    if (cancelled || !resolved?.leadId || !resolved?.lead) return;

    const dealForDetails = {
      ...resolved.lead,
      id: resolved.leadId,
      crmRecordType: "lead",
    };
    const nextDraft = buildCrmDetailDraftFromDeal(dealForDetails);
    selectedCrmDealCacheRef.current = dealForDetails;
    selectedCrmDealIdRef.current = resolved.leadId;
    if (!crmDetailDraftDirtyRef.current && nextDraft) {
      crmDetailDraftDealIdRef.current = resolved.leadId;
      setCrmDetailDraft(nextDraft);
      markExecutionDetailsBaseline(nextDraft, "saved");
    }
    setCrmDetailsOpenDebug((prev) => ({
      ...prev,
      lastResolvedLeadId: resolved.leadId,
      lastResolveReason: resolved.sourceReason || "selected_id_rehydrate",
      lastError: "",
    }));
  });

  return () => {
    cancelled = true;
  };
}, [
  buildCrmDetailDraftFromDeal,
  fetchCrmLeadForDetails,
  markExecutionDetailsBaseline,
  selectedCrmDealFromRecords,
  selectedCrmDealId,
  workspaceUid,
]);

const executionWorkspaceTemplateOptions = useMemo(
  () =>
    executionTemplateCatalog.filter(
      (template) => String(template.name || "").trim() && String(template.itemType || "").trim()
    ),
  [executionTemplateCatalog]
);

const filteredExecutionTemplates = useMemo(() => {
  const queryText = executionTemplateSearch.trim().toLowerCase();

  return executionTemplateCatalog
    .filter((template) => {
      if (!queryText) return true;

      return [
        template.name,
        template.itemType,
        ...(Array.isArray(template.fields)
          ? template.fields.flatMap((field) => [field.key, field.defaultValue])
          : []),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .sort((a, b) => {
      if (executionTemplateSortBy === "updated") {
        return Number(toMillis(b.updatedAt) || 0) - Number(toMillis(a.updatedAt) || 0);
      }

      if (executionTemplateSortBy === "oldest") {
        return Number(toMillis(a.createdAt) || 0) - Number(toMillis(b.createdAt) || 0);
      }

      return String(a.name || "").localeCompare(String(b.name || ""));
    });
}, [executionTemplateCatalog, executionTemplateSearch, executionTemplateSortBy]);

const executionWorkspaceSourceOptions = useMemo(() => {
  const values = new Set();

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    const sourceValue = String(deal.source || deal.crmContact?.source || "").trim();
    if (sourceValue) {
      values.add(sourceValue);
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmDeals]);

const executionWorkspaceStageOptions = useMemo(() => {
  const values = new Map();

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    const stageKey = `${deal.pipelineType}::${deal.crmStageId}`;
    if (!deal.crmStageId || values.has(stageKey)) return;

    values.set(stageKey, {
      value: stageKey,
      label: `${getQuoteTypeLabel(deal.pipelineType)} | ${deal.crmStageLabel}`,
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmDeals]);

const executionWorkspaceDeals = useMemo(
  () =>
    crmDeals
      .filter((deal) => isGeneralExecutionEligibleDeal(deal))
      .sort(
        (a, b) =>
          Number(b.updatedMillis || b.createdMillis || 0) -
          Number(a.updatedMillis || a.createdMillis || 0)
      ),
  [crmDeals]
);

const executionWorkspaceFilteredDeals = useMemo(() => {
  const queryText = executionWorkspaceSearch.trim().toLowerCase();

  return executionWorkspaceDeals
    .filter((deal) => {
      if (!queryText) return true;

      const itemTypes = normalizeExecutionSpecs(deal.executionSpecs || [])
        .map((item) => String(item.itemType || "").trim())
        .filter(Boolean)
        .join(" ");

      return [
        deal.crmTitle,
        deal.customerLabel,
        deal.quoteNo,
        deal.primaryLinkedQuote?.quoteNo,
        deal.crmContact?.name,
        deal.crmContact?.phone,
        deal.crmContact?.email,
        deal.source,
        deal.crmContact?.source,
        deal.companyOrProject,
        deal.crmStageLabel,
        itemTypes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .filter((deal) => {
      if (executionWorkspaceSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === executionWorkspaceSourceFilter;
    })
    .filter((deal) => {
      if (executionWorkspaceStageFilter === "all") return true;
      return `${deal.pipelineType}::${deal.crmStageId}` === executionWorkspaceStageFilter;
    })
    .filter((deal) => {
      if (executionWorkspaceItemTypeFilter === "all") return true;
      return normalizeExecutionSpecs(deal.executionSpecs || []).some(
        (item) => String(item.itemType || "").trim() === executionWorkspaceItemTypeFilter
      );
    })
    .filter((deal) => {
      if (executionWorkspaceAttachmentFilter === "all") return true;

      const hasAttachments = normalizeExecutionSpecs(deal.executionSpecs || []).some(
        (item) => normalizeExecutionSpecAttachments(item.attachments || []).length > 0
      );

      return executionWorkspaceAttachmentFilter === "with"
        ? hasAttachments
        : !hasAttachments;
    })
    .sort((a, b) => {
      if (executionWorkspaceSortBy === "client") {
        return String(a.customerLabel || a.crmContact?.name || "").localeCompare(
          String(b.customerLabel || b.crmContact?.name || "")
        );
      }

      if (executionWorkspaceSortBy === "oldest") {
        return Number(a.updatedMillis || a.createdMillis || 0) - Number(b.updatedMillis || b.createdMillis || 0);
      }

      return Number(b.updatedMillis || b.createdMillis || 0) - Number(a.updatedMillis || a.createdMillis || 0);
    });
}, [
  executionWorkspaceAttachmentFilter,
  executionWorkspaceDeals,
  executionWorkspaceItemTypeFilter,
  executionWorkspaceSearch,
  executionWorkspaceSortBy,
  executionWorkspaceSourceFilter,
  executionWorkspaceStageFilter,
]);

const openExecutionWorkspaceForDeal = useCallback(
  async (dealLike = null) => {
    const sourceDeal = dealLike || selectedCrmDeal || null;

    if (!sourceDeal) {
      setRoutedTab("executionSpecs");
      return;
    }

    if (!isGeneralExecutionEligibleDeal(sourceDeal)) {
      notify(
        "Execution Details is available for General Quotations only.",
        "warning"
      );
      return;
    }

    setExecutionWorkspaceOpeningDealId(sourceDeal.id);

    try {
      const sourceExecutionSpecs =
        selectedCrmDeal?.id === sourceDeal.id
          ? serializeExecutionSpecDraftItems(
              crmDetailDraft?.executionSpecs || sourceDeal.executionSpecs || []
            )
          : normalizeExecutionSpecs(sourceDeal.executionSpecs || []);

      const canonicalDeal =
        sourceDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(
              sourceDeal,
              sourceExecutionSpecs
            )
          : sourceDeal;

      if (!canonicalDeal?.id) {
        notify("Could not open Execution Details for this deal. Please try again.", "error");
        return;
      }

      setSelectedCrmDealId(canonicalDeal?.id || sourceDeal.id || "");
      setSelectedExecutionItemId("");
      setRoutedTab("executionSpecs");
    } catch (error) {
      devError("Failed to open execution workspace:", error);
      notify("Could not open Execution Details. Please try again.", "error");
    } finally {
      setExecutionWorkspaceOpeningDealId("");
    }
  },
  [
    crmDetailDraft,
    ensureCrmLeadForExecutionSpecs,
    notify,
    selectedCrmDeal,
    serializeExecutionSpecDraftItems,
    setRoutedTab,
  ]
);

const returnToCrmFromExecutionWorkspace = useCallback(
  (dealId = selectedCrmDealId) => {
    if (dealId) {
      setSelectedCrmDealId(dealId);
    }

    setCrmView("list");
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    setRoutedTab("crm");
  },
  [selectedCrmDealId, setRoutedTab]
);

const resolveCrmReminderFollowUp = useCallback(
  (reminder = {}) => {
    if (reminder?.reminderType !== CRM_REMINDER_TYPE_FOLLOW_UP) {
      return { deal: null, followUp: null, followUps: [] };
    }

    const candidateMap = new Map();
    const addCandidate = (deal) => {
      if (!deal?.id || candidateMap.has(deal.id)) return;
      candidateMap.set(deal.id, deal);
    };

    crmDeals.forEach((deal) => {
      const linkedQuotes = normalizeCrmLinkedQuotes(deal);
      const matchesDirectId =
        String(deal.id || "") === String(reminder.dealId || "") ||
        String(deal.id || "") === String(reminder.leadId || "") ||
        String(deal.id || "") === String(reminder.quoteId || "");
      const matchesQuoteLink = linkedQuotes.some(
        (linkedQuote) =>
          (reminder.dealId &&
            String(linkedQuote.quoteId || "") === String(reminder.dealId || "")) ||
          (reminder.quoteId &&
            String(linkedQuote.quoteId || "") === String(reminder.quoteId || "")) ||
          (reminder.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(reminder.quoteNo || ""))
      );

      if (matchesDirectId || matchesQuoteLink) {
        addCandidate(deal);
      }
    });

    crmLeadRecords.forEach((lead) => {
      const matchingDeal = crmDeals.find((deal) => deal.id === lead.id);
      if (matchingDeal) {
        return;
      }

      const linkedQuotes = normalizeCrmLinkedQuotes(lead);
      const matchesDirectId =
        String(lead.id || "") === String(reminder.dealId || "") ||
        String(lead.id || "") === String(reminder.leadId || "");
      const matchesQuoteLink = linkedQuotes.some(
        (linkedQuote) =>
          (reminder.dealId &&
            String(linkedQuote.quoteId || "") === String(reminder.dealId || "")) ||
          (reminder.quoteId &&
            String(linkedQuote.quoteId || "") === String(reminder.quoteId || "")) ||
          (reminder.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(reminder.quoteNo || ""))
      );

      if (matchesDirectId || matchesQuoteLink) {
        addCandidate({
          ...lead,
          crmRecordType: "lead",
          crmTitle: lead.title || lead.companyOrProject || lead.contact?.name || "CRM Lead",
          crmContact: lead.contact || {},
          customerLabel:
            lead.contact?.name || lead.companyOrProject || lead.title || "Unnamed Lead",
        });
      }
    });

    crmDeals.forEach(addCandidate);

    for (const deal of candidateMap.values()) {
      const followUps = normalizeCrmFollowUps(deal.followUps || deal.crm?.followUps);
      const followUp = followUps.find((item) =>
        doesCrmReminderMatchFollowUp(reminder, item)
      );

      if (followUp) {
        return { deal, followUp, followUps };
      }
    }

    return { deal: null, followUp: null, followUps: [] };
  },
  [crmDeals, crmLeadRecords]
);

const dueCrmReminders = useMemo(() => {
  if (!canAccessCrm) return [];

  return crmReminderRecords
    .filter(
      (reminder) =>
        reminder.active === true ||
        reminder.enabled === true ||
        reminder.reminderEnabled === true
    )
    .filter((reminder) => reminder.status === CRM_REMINDER_STATUS_ACTIVE)
    .filter((reminder) => {
      const nextTriggerMillis = safeTimeMillis(reminder.nextTriggerAt);
      return nextTriggerMillis > 0 && nextTriggerMillis <= crmReminderNow;
    })
    .sort((a, b) => {
      const aMillis = safeTimeMillis(a.nextTriggerAt);
      const bMillis = safeTimeMillis(b.nextTriggerAt);
      return aMillis - bMillis;
    });
}, [canAccessCrm, crmReminderNow, crmReminderRecords]);

useEffect(() => {
  dueCrmReminders.forEach((reminder) => {
    if (reminder?.id) {
      globalReminderShownIdsRef.current.add(reminder.id);
    }
  });
}, [dueCrmReminders]);

const dailyTaskDateKey = dailyTaskDate || todayISO();

const dailyTaskSourceItems = useMemo(() => {
  const todayKey = todayISO();
  const toDateKeyFromOffset = (offset) => {
    const date = parseDateInputValue(todayKey) || new Date();
    date.setDate(date.getDate() + offset);
    return toDateInputValue(date);
  };
  const tomorrowKey = toDateKeyFromOffset(1);
  const weekEndKey = toDateKeyFromOffset(6);
  const customStartKey = dailyTaskRangeStart || dailyTaskDateKey;
  const customEndKey = dailyTaskRangeEnd || customStartKey;
  const nowMillis = Date.now();
  const tasks = [];

  const getDealBasics = (deal) => {
    const linkedQuotes = normalizeCrmLinkedQuotes(deal);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const leadId =
      deal.crmRecordType === "lead"
        ? deal.id
        : String(deal.crm?.leadId || deal.leadId || "").trim();
    const quoteId =
      deal.crmRecordType === "quote"
        ? deal.id
        : String(primaryLinkedQuote?.quoteId || deal.quoteId || "").trim();
    const quoteNo =
      deal.crmRecordType === "quote"
        ? deal.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || deal.quoteNo || "";
    const linkedCustomer = customerRecords.find(
      (customer) =>
        customer.id &&
        String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
    );
    const primarySiteLocation =
      getPrimarySiteLocation(deal) || getPrimarySiteLocation(linkedCustomer);
    const locationUrl = primarySiteLocation
      ? buildSiteLocationOpenUrl(primarySiteLocation)
      : "";

    return {
      deal,
      dealId: deal.id,
      leadId,
      quoteId,
      quoteNo,
      quoteType: deal.pipelineType || deal.quoteTypeResolved || deal.quoteType || "",
      customerName:
        deal.customerLabel ||
        deal.crmContact?.name ||
        deal.contact?.name ||
        deal.companyOrProject ||
        deal.crmTitle ||
        "Unnamed Customer",
      assignedToUserId: deal.assignedToUserId || "",
      assignedTo:
        deal.assignedToNameSnapshot ||
        deal.assignedTo ||
        deal.crm?.assignedToNameSnapshot ||
        "Unassigned",
      source: deal.source || deal.crmContact?.source || "",
      address:
        primarySiteLocation?.addressText ||
        deal.crmContact?.address ||
        deal.contact?.address ||
        deal.address ||
        "",
      siteLocation: primarySiteLocation,
      locationLabel: primarySiteLocation?.label || "",
      locationAddress: primarySiteLocation?.addressText || "",
      locationUrl,
      stageLabel: deal.crmStageLabel || deal.stageNameSnapshot || "",
    };
  };

  const pushTask = (task) => {
    const dueMillis = safeTimeMillis(task.dueDateTime || task.dueDate);
    const completedDateKey = toDateInputValue(task.completedAt);
    const dueDateKey = toDateInputValue(task.dueDate);
    const status =
      task.status === CRM_FOLLOW_UP_STATUS_DONE ||
      task.status === CRM_TASK_STATUS_COMPLETED
        ? "completed"
        : "pending";
    const isOverdue =
      status !== "completed" && dueMillis > 0 && dueMillis < nowMillis;
    let shouldShow = false;

    if (dailyTaskDateMode === "overdue") {
      shouldShow = isOverdue;
    } else if (dailyTaskDateMode === "tomorrow") {
      shouldShow = dueDateKey === tomorrowKey || completedDateKey === tomorrowKey;
    } else if (dailyTaskDateMode === "week") {
      shouldShow =
        (dueDateKey >= todayKey && dueDateKey <= weekEndKey) ||
        (completedDateKey >= todayKey && completedDateKey <= weekEndKey) ||
        isOverdue;
    } else if (dailyTaskDateMode === "custom") {
      const start = customStartKey <= customEndKey ? customStartKey : customEndKey;
      const end = customStartKey <= customEndKey ? customEndKey : customStartKey;
      shouldShow =
        (dueDateKey >= start && dueDateKey <= end) ||
        (completedDateKey >= start && completedDateKey <= end);
    } else {
      shouldShow =
        dueDateKey === todayKey || completedDateKey === todayKey || isOverdue;
    }

    if (!shouldShow) return;

    tasks.push({
      ...task,
      status,
      dueMillis,
      isOverdue,
      typeLabel: task.typeLabel || DAILY_TASK_TYPE_LABELS[task.sourceType] || "Task",
    });
  };

  crmTaskRecords.forEach((task) => {
    if (task.status === CRM_TASK_STATUS_CANCELLED || isCrmRecordUnavailable(task)) return;
    const dueDate = task.dueAt || task.dueDate || null;
    if (!dueDate) return;
    const isProjectTrackingTask =
      String(task.recurringType || "") === "daily_tracking" ||
      String(task.relatedType || "") === "project_tracking" ||
      String(task.trackingTaskForProjectId || "").trim() !== "";
    const trackingProjectId = isProjectTrackingTask
      ? String(
          task.trackingTaskForProjectId ||
            task.projectId ||
            (task.relatedType === "project_tracking" ? task.relatedId : "") ||
            ""
        ).trim()
      : "";
    pushTask({
      id: `crm-task:${task.taskId || task.id}`,
      sourceId: task.taskId || task.id,
      sourceType: DAILY_TASK_SOURCE_CRM_TASK,
      title: task.title || "CRM Task",
      description: task.notes || "",
      dueDate,
      dueDateKey: task.dueDateKey || toDateInputValue(dueDate),
      dueTime: task.dueTime || DEFAULT_CRM_REMINDER_TIME,
      dueDateTime: dueDate,
      completedAt: task.completedAt || null,
      priority: task.priority || "normal",
      original: task,
      deal: null,
      projectId: task.projectId || "",
      isProjectTrackingTask,
      trackingTaskForProjectId: trackingProjectId,
      paymentFollowUpForProjectId: task.paymentFollowUpForProjectId || "",
      dueAmount: task.dueAmount,
      dealId: task.relatedId || task.opportunityId || task.quoteId || "",
      leadId: task.relatedType === "lead" ? task.relatedId : "",
      quoteId: task.quoteId || "",
      quoteNo: task.quoteNo || "",
      quoteType: "",
      customerName: task.customerName || "Unnamed Customer",
      assignedToUserId: task.ownerUid || "",
      assignedTo: task.ownerName || "Unassigned",
      address: "",
      siteLocation: null,
      locationLabel: "",
      locationAddress: "",
      locationUrl: "",
      stageLabel: trackingProjectId ? "Tracking" : "",
      status: task.status || CRM_TASK_STATUS_OPEN,
      source: trackingProjectId ? "Delivery Tracking" : "CRM Task",
      typeLabel: trackingProjectId ? "Delivery Tracking" : "",
      crmTask: task,
    });
  });

  crmDeals.forEach((deal) => {
    const basics = getDealBasics(deal);
    const reminderConfigs = normalizeCrmReminderConfigs(deal.reminderConfigs, {
      nextActionDate: deal.nextActionDate,
      installationDate: deal.installationDate,
    });
    const followUps = normalizeCrmFollowUps(deal.followUps || deal.crm?.followUps);

    followUps.forEach((followUp) => {
      const dueDateTime =
        combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
        parseDateInputValue(followUp.dueDate);

      pushTask({
        id: `followup:${deal.id}:${followUp.id}`,
        sourceId: followUp.id,
        reminderId: followUp.reminderId || followUp.id,
        sourceType: DAILY_TASK_SOURCE_FOLLOW_UP,
        title: followUp.title || "Follow-up",
        description: followUp.description || "",
        dueDate: parseDateInputValue(followUp.dueDate),
        dueDateKey: followUp.dueDate || "",
        dueTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
        dueDateTime,
        completedAt: followUp.completedAt || null,
        priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: followUp,
        ...basics,
        status:
          followUp.status === CRM_FOLLOW_UP_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    });

    if (followUps.length === 0 && (deal.nextAction || deal.nextActionDate)) {
      const taskConfig = reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] || {};
      const dueDate = deal.nextActionDate || parseDateInputValue(taskConfig.reminderDate);
      const dueTime = taskConfig.reminderTime || DEFAULT_CRM_REMINDER_TIME;
      const dueDateTime =
        combineReminderDateTime(toDateInputValue(dueDate), dueTime) || dueDate;

      pushTask({
        id: `deal-action:${deal.id}`,
        sourceId: `${deal.id}__${CRM_REMINDER_TYPE_TASK_DUE}`,
        sourceType: DAILY_TASK_SOURCE_DEAL_ACTION,
        title: deal.nextAction || "Deal action",
        description: deal.crmNotes || deal.notes || "",
        dueDate,
        dueDateKey: toDateInputValue(dueDate),
        dueTime,
        dueDateTime,
        completedAt: taskConfig.completedAt || null,
        priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: taskConfig,
        ...basics,
        status:
          taskConfig.status === CRM_REMINDER_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    }

    const installationDate =
      deal.installationDate ||
      deal.scheduledInstallationDate ||
      deal.crm?.installationDate ||
      null;
    if (installationDate) {
      const installConfig = reminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] || {};
      const installDateKey = toDateInputValue(installationDate);
      const installTime =
        installConfig.reminderTime ||
        (installationDate instanceof Date
          ? `${String(installationDate.getHours()).padStart(2, "0")}:${String(
              installationDate.getMinutes()
            ).padStart(2, "0")}`
          : DEFAULT_CRM_REMINDER_TIME);
      const dueDateTime =
        combineReminderDateTime(installDateKey, installTime) || installationDate;

      pushTask({
        id: `installation:${deal.id}`,
        sourceId: `${deal.id}__${CRM_REMINDER_TYPE_INSTALLATION}`,
        sourceType: DAILY_TASK_SOURCE_INSTALLATION,
        title: "Installation",
        description: basics.locationAddress || basics.address || deal.crmNotes || "",
        dueDate: installationDate,
        dueDateKey: installDateKey,
        dueTime: installTime,
        dueDateTime,
        completedAt: installConfig.completedAt || null,
        priority: CRM_FOLLOW_UP_PRIORITY_HIGH,
        original: installConfig,
        ...basics,
        status:
          installConfig.status === CRM_REMINDER_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    }

    flattenChecklistNodes(deal.checklistItems || []).forEach((checklistItem) => {
      const checklistDueDate =
        checklistItem.dueDate ||
        checklistItem.dueAt ||
        checklistItem.nextActionDate ||
        checklistItem.reminderDate ||
        "";
      if (!checklistDueDate) return;

      const checklistDueKey = toDateInputValue(checklistDueDate);
      const checklistDueTime =
        parseTimeInputValue(checklistItem.dueTime || checklistItem.reminderTime || "") ||
        DEFAULT_CRM_REMINDER_TIME;
      const checklistDueDateTime =
        combineReminderDateTime(checklistDueKey, checklistDueTime) ||
        parseDateInputValue(checklistDueKey);

      pushTask({
        id: `checklist:${deal.id}:${checklistItem.id}`,
        sourceId: checklistItem.id,
        sourceType: DAILY_TASK_SOURCE_CHECKLIST,
        title: checklistItem.title || checklistItem.label || "Checklist Item",
        description: checklistItem.pathLabel || checklistItem.notes || "",
        dueDate: parseDateInputValue(checklistDueKey),
        dueDateKey: checklistDueKey,
        dueTime: checklistDueTime,
        dueDateTime: checklistDueDateTime,
        completedAt: checklistItem.completedAt || null,
        priority: checklistItem.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: checklistItem,
        ...basics,
        status:
          checklistItem.done === true
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    });
  });

  return tasks.sort((a, b) => {
    if (a.status !== b.status) return a.status === "pending" ? -1 : 1;
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    return Number(a.dueMillis || 0) - Number(b.dueMillis || 0);
  });
}, [
  combineReminderDateTime,
  crmDeals,
  crmTaskRecords,
  customerRecords,
  dailyTaskDateKey,
  dailyTaskDateMode,
  dailyTaskRangeEnd,
  dailyTaskRangeStart,
]);

const dailyTaskAssignedOptions = useMemo(() => {
  const options = new Map();

  dailyTaskSourceItems.forEach((task) => {
    if (task.assignedToUserId) {
      options.set(task.assignedToUserId, {
        value: task.assignedToUserId,
        label: task.assignedTo || task.assignedToUserId,
      });
    } else if (task.assignedTo && task.assignedTo !== "Unassigned") {
      options.set(task.assignedTo, {
        value: task.assignedTo,
        label: task.assignedTo,
      });
    }
  });

  teamMembers.forEach((member) => {
    const value = member.uid || member.id || member.email || "";
    if (!value) return;
    options.set(value, {
      value,
      label: member.name || member.email || value,
    });
  });

  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [dailyTaskSourceItems, teamMembers]);

const dailyTaskSourceOptions = useMemo(() => {
  const values = new Set();
  dailyTaskSourceItems.forEach((task) => {
    const sourceValue = String(task.source || "").trim();
    if (sourceValue) values.add(sourceValue);
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [dailyTaskSourceItems]);

const filteredDailyTasks = useMemo(() => {
  const queryText = deferredDailyTaskSearch.trim().toLowerCase();

  return dailyTaskSourceItems
    .filter((task) => {
      if (dailyTaskAssignedFilter === "all") return true;
      return (
        task.assignedToUserId === dailyTaskAssignedFilter ||
        task.assignedTo === dailyTaskAssignedFilter
      );
    })
    .filter((task) =>
      dailyTaskTypeFilter === "all" ? true : task.sourceType === dailyTaskTypeFilter
    )
    .filter((task) =>
      dailyTaskPriorityFilter === "all" ? true : task.priority === dailyTaskPriorityFilter
    )
    .filter((task) =>
      dailyTaskSourceFilter === "all" ? true : task.source === dailyTaskSourceFilter
    )
    .filter((task) =>
      dailyTaskQuoteTypeFilter === "all"
        ? true
        : task.quoteType === dailyTaskQuoteTypeFilter
    )
    .filter((task) => {
      if (dailyTaskStatusFilter === "all") return true;
      if (dailyTaskStatusFilter === "overdue") return task.isOverdue;
      if (dailyTaskStatusFilter === "completed") return task.status === "completed";
      return task.status === "pending" && !task.isOverdue;
    })
    .filter((task) => {
      if (!queryText) return true;
      return [
        task.title,
        task.description,
        task.customerName,
        task.quoteNo,
        task.source,
        task.assignedTo,
        task.stageLabel,
        task.locationLabel,
        task.locationAddress,
        task.siteLocation?.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    });
}, [
  dailyTaskAssignedFilter,
  dailyTaskPriorityFilter,
  dailyTaskQuoteTypeFilter,
  dailyTaskSourceFilter,
  dailyTaskSourceItems,
  dailyTaskStatusFilter,
  dailyTaskTypeFilter,
  deferredDailyTaskSearch,
]);

const dailyTaskGroups = useMemo(
  () => ({
    overdue: filteredDailyTasks.filter(
      (task) => task.status !== "completed" && task.isOverdue
    ),
    today: filteredDailyTasks.filter(
      (task) =>
        task.status !== "completed" &&
        !task.isOverdue &&
        task.sourceType !== DAILY_TASK_SOURCE_INSTALLATION
    ),
    installations: filteredDailyTasks.filter(
      (task) =>
        task.status !== "completed" &&
        !task.isOverdue &&
        task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
    ),
    completed: filteredDailyTasks.filter((task) => task.status === "completed"),
  }),
  [filteredDailyTasks]
);

const dailyTaskSummary = useMemo(
  () => ({
    total: filteredDailyTasks.length,
    pending: filteredDailyTasks.filter((task) => task.status !== "completed").length,
    overdue: filteredDailyTasks.filter((task) => task.isOverdue).length,
    installations: filteredDailyTasks.filter(
      (task) => task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
    ).length,
    completed: filteredDailyTasks.filter((task) => task.status === "completed").length,
  }),
  [filteredDailyTasks]
);

const crmNotificationCounts = useMemo(() => {
  const currentUserKeys = [
    user?.uid,
    user?.email,
    user?.displayName,
    accountUser?.uid,
    accountUser?.email,
    accountUser?.name,
    currentWorkspaceMember?.uid,
    currentWorkspaceMember?.id,
    currentWorkspaceMember?.email,
    currentWorkspaceMember?.name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const taskBelongsToCurrentUser = (task = {}) => {
    if (canViewAttentionRequiredWorkspace) return true;
    if (!currentUserKeys.length) return false;
    const source = task.crmTask || task.original || {};
    const taskKeys = [
      task.assignedToUserId,
      task.assignedTo,
      task.ownerUid,
      task.ownerName,
      task.ownerEmail,
      source.ownerUid,
      source.ownerName,
      source.ownerEmail,
      source.assignedToUserId,
      source.assignedTo,
      source.assignedToNameSnapshot,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return taskKeys.some((value) => currentUserKeys.includes(value));
  };
  const pendingDailyTasks = (Array.isArray(dailyTaskSourceItems) ? dailyTaskSourceItems : [])
    .filter(taskBelongsToCurrentUser)
    .filter((task) => task?.status !== "completed");
  const dailyOverdue = pendingDailyTasks.filter((task) => task.isOverdue).length;
  const dailyDueToday = pendingDailyTasks.filter(
    (task) =>
      !task.isOverdue &&
      task.sourceType !== DAILY_TASK_SOURCE_INSTALLATION
  ).length;
  const paymentCount =
    crmSettings.enablePaymentTracking === false
      ? 0
      : countUniqueNotificationItems(
          [
            ...(crmAttentionState.overduePaymentTasks || []),
            ...(crmAttentionState.paymentDueTodayTasks || []),
          ],
          (task) => task.taskId || task.id || task.sourceId
        );
  const installationCount = countUniqueNotificationItems(
    [
      ...(crmAttentionState.overdueInstallations || []),
      ...(crmAttentionState.installationsDueToday || []),
    ],
    (project) => project.projectId || project.id
  );
  const snagCount = countUniqueNotificationItems(
    [
      ...(crmAttentionState.overdueSnagProjects || []),
      ...(crmAttentionState.openSnagProjects || []),
    ],
    (project) => project.projectId || project.id
  );
  const reviewCount = countUniqueNotificationItems(
    [
      ...(crmAttentionState.overdueReviewTasks || []),
      ...(crmAttentionState.reviewDueTodayTasks || []),
    ],
    (task) => task.taskId || task.id || task.sourceId
  );
  const trackingCount =
    crmSettings.enableDeliveryTracking === false
      ? 0
      : countNotificationItems(crmAttentionState.overdueTrackingTasks) +
        countNotificationItems(crmAttentionState.trackingItemsNotOrdered) +
        countNotificationItems(crmAttentionState.trackingItemsDelayed) +
        countNotificationItems(crmAttentionState.trackingArrivalsOverdue);
  const managerAttention =
    countNotificationItems(crmAttentionState.missingOwner) +
    countNotificationItems(crmAttentionState.missingNextAction) +
    countNotificationItems(crmAttentionState.overdueTasks) +
    countNotificationItems(crmAttentionState.missingQuoteFollowUp) +
    countNotificationItems(crmAttentionState.handoverPendingProjects) +
    countNotificationItems(crmAttentionState.advancePendingProjects) +
    countNotificationItems(crmAttentionState.projectsMissingNextAction) +
    countNotificationItems(crmAttentionState.projectsMissingInstallationDate) +
    countNotificationItems(crmAttentionState.balancePendingProjects) +
    countNotificationItems(crmAttentionState.installationNotScheduledProjects) +
    countNotificationItems(crmAttentionState.productionOverdueProjects) +
    countNotificationItems(crmAttentionState.materialDueOverdueProjects) +
    countNotificationItems(crmAttentionState.projectsReadyToClose) +
    countNotificationItems(crmAttentionState.projectsBlockedByFinalPayment) +
    trackingCount +
    paymentCount +
    installationCount +
    snagCount +
    reviewCount;

  return {
    today: dailyOverdue + dailyDueToday,
    overdue: dailyOverdue,
    dueToday: dailyDueToday,
    attention: canViewAttentionRequiredWorkspace ? managerAttention : dailyOverdue + dailyDueToday,
    payment: paymentCount,
    installation: installationCount,
    snags: snagCount,
    review: reviewCount,
    leadsWithoutNextAction: countNotificationItems(crmAttentionState.missingNextAction),
    quotesWithoutFollowUp: countNotificationItems(crmAttentionState.missingQuoteFollowUp),
  };
}, [
  accountUser?.email,
  accountUser?.name,
  accountUser?.uid,
  canViewAttentionRequiredWorkspace,
  currentWorkspaceMember?.email,
  currentWorkspaceMember?.id,
  currentWorkspaceMember?.name,
  currentWorkspaceMember?.uid,
  crmAttentionState,
  crmSettings.enableDeliveryTracking,
  crmSettings.enablePaymentTracking,
  dailyTaskSourceItems,
  user?.displayName,
  user?.email,
  user?.uid,
]);

const sidebarItemBadges = useMemo(() => {
  if (!canAccessCrm) return {};
  return {
    crm: canViewAttentionRequiredWorkspace
      ? crmNotificationCounts.attention
      : crmNotificationCounts.today,
    todayTasks: crmNotificationCounts.today,
  };
}, [canAccessCrm, canViewAttentionRequiredWorkspace, crmNotificationCounts]);

const crmNotificationChips = useMemo(() => {
  if (!canAccessCrm) return [];
  const openPaymentBucket =
    countNotificationItems(crmAttentionState.overduePaymentTasks) > 0
      ? "overduePaymentTasks"
      : "paymentDueTodayTasks";
  const openInstallationBucket =
    countNotificationItems(crmAttentionState.overdueInstallations) > 0
      ? "overdueInstallations"
      : "installationsDueToday";
  const openSnagBucket =
    countNotificationItems(crmAttentionState.overdueSnagProjects) > 0
      ? "overdueSnagProjects"
      : "openSnagProjects";
  const openReviewBucket =
    countNotificationItems(crmAttentionState.overdueReviewTasks) > 0
      ? "overdueReviewTasks"
      : "reviewDueTodayTasks";
  const chips = [
    {
      key: "today",
      label: "Due",
      count: crmNotificationCounts.today,
      accent: "#2563eb",
      targetView: "overview",
    },
    canViewAttentionRequiredWorkspace
      ? {
          key: "attention",
          label: "Attention",
          count: crmNotificationCounts.attention,
          accent: "#ef4444",
          targetView: "attention",
        }
      : null,
    canViewAttentionRequiredWorkspace && crmSettings.enablePaymentTracking !== false
      ? {
          key: "payment",
          label: "Payment",
          count: crmNotificationCounts.payment,
          accent: "#d97706",
          targetView: "attention",
          targetBucket: openPaymentBucket,
        }
      : null,
    canViewAttentionRequiredWorkspace
      ? {
          key: "installation",
          label: "Installation",
          count: crmNotificationCounts.installation,
          accent: "#0ea5e9",
          targetView: "attention",
          targetBucket: openInstallationBucket,
        }
      : null,
    canViewAttentionRequiredWorkspace
      ? {
          key: "snag",
          label: "Snag",
          count: crmNotificationCounts.snags,
          accent: "#f97316",
          targetView: "attention",
          targetBucket: openSnagBucket,
        }
      : null,
    canViewAttentionRequiredWorkspace
      ? {
          key: "review",
          label: "Review",
          count: crmNotificationCounts.review,
          accent: "#7c3aed",
          targetView: "attention",
          targetBucket: openReviewBucket,
        }
      : null,
    canViewAttentionRequiredWorkspace
      ? {
          key: "leads",
          label: "Leads",
          count: crmNotificationCounts.leadsWithoutNextAction,
          accent: "#f97316",
          targetView: "attention",
          targetBucket: "missingNextAction",
        }
      : null,
    canViewAttentionRequiredWorkspace
      ? {
          key: "quotes",
          label: "Quotes",
          count: crmNotificationCounts.quotesWithoutFollowUp,
          accent: "#2563eb",
          targetView: "attention",
          targetBucket: "missingQuoteFollowUp",
        }
      : null,
  ];

  return chips.filter((chip) => chip && Number(chip.count || 0) > 0);
}, [
  canAccessCrm,
  canViewAttentionRequiredWorkspace,
  crmAttentionState,
  crmNotificationCounts,
  crmSettings.enablePaymentTracking,
]);

const selectedDailyTask = useMemo(
  () =>
    filteredDailyTasks.find((task) => task.id === selectedDailyTaskId) ||
    filteredDailyTasks[0] ||
    null,
  [filteredDailyTasks, selectedDailyTaskId]
);

const crmSummary = useMemo(() => {
  const openDeals = crmDeals.filter(
    (deal) => !deal.isCrmClosed && !deal.isCrmCancelled
  );
  const newDeals = crmDeals.filter((deal) => {
    if (!deal.createdMillis) return false;
    return isDateInPeriod(new Date(deal.createdMillis), periodType, customRange);
  });
  const overdueFollowUps = openDeals.filter(
    (deal) => deal.nextActionMillis && deal.nextActionMillis < Date.now()
  );
  const stalledDeals = openDeals.filter(
    (deal) =>
      deal.updatedMillis &&
      Date.now() - deal.updatedMillis > 1000 * 60 * 60 * 24 * 10
  );
  const completedDeals = crmDeals.filter((deal) => deal.isCrmClosed);
  const lostDeals = crmDeals.filter((deal) => deal.isCrmCancelled);
  const checklistTotals = openDeals.reduce(
    (sum, deal) => {
      sum.completed += deal.checklistCompleted;
      sum.total += deal.checklistTotal;
      return sum;
    },
    { completed: 0, total: 0 }
  );

  return {
    activeDeals: openDeals.length,
    newDeals: newDeals.length,
    overdueFollowUps: overdueFollowUps.length,
    stalledDeals: stalledDeals.length,
    pipelineValue: openDeals.reduce(
      (sum, deal) => sum + Number(deal.expectedValue || 0),
      0
    ),
    wonDeals: completedDeals.length,
    lostDeals: lostDeals.length,
    checklistCompleted: checklistTotals.completed,
    checklistTotal: checklistTotals.total,
    checklistCompletion: checklistTotals.total
      ? Math.round((checklistTotals.completed / checklistTotals.total) * 100)
      : 0,
  };
}, [crmDeals, customRange, periodType]);

const crmStageOverview = useMemo(() => {
  if (crmScope === CRM_SCOPE_ALL) {
    const stageMap = new Map();

    crmScopeDeals.forEach((deal) => {
      const stageMeta = getCrmStage(
        crmPipelines,
        deal.pipelineType,
        deal.crmStageId
      );
      const key = `${deal.pipelineType}::${stageMeta?.id || deal.crmStageId}`;
      const current = stageMap.get(key) || {
        id: key,
        quoteType: deal.pipelineType,
        stageId: stageMeta?.id || deal.crmStageId,
        stageLabel: stageMeta?.label || deal.crmStageLabel,
        fullLabel: `${getQuoteTypeLabel(deal.pipelineType)} | ${stageMeta?.label || deal.crmStageLabel}`,
        label: `${getQuoteTypeLabel(deal.pipelineType)} | ${stageMeta?.label || deal.crmStageLabel}`,
        color: stageMeta?.color || deal.crmStageColor || "#64748b",
        count: 0,
        value: 0,
      };

      current.count += 1;
      current.value += Number(deal.expectedValue || 0);
      stageMap.set(key, current);
    });

    return Array.from(stageMap.values()).sort((a, b) => b.count - a.count);
  }

  const pipeline = getCrmPipeline(crmPipelines, activeCrmPipelineType);

  return pipeline.stages.map((stage) => {
    const deals = crmScopeDeals.filter((deal) => deal.crmStageId === stage.id);

    return {
      ...stage,
      quoteType: activeCrmPipelineType,
      stageId: stage.id,
      stageLabel: stage.label,
      fullLabel: `${getQuoteTypeLabel(activeCrmPipelineType)} | ${stage.label}`,
      count: deals.length,
      value: deals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
    };
  });
}, [activeCrmPipelineType, crmPipelines, crmScope, crmScopeDeals]);

const crmBoardStages = useMemo(() => {
  const knownIds = new Set(salesPipelineActiveStages.map((stage) => stage.stageId));
  const legacyStages = crmBoardDeals
    .filter((deal) => !knownIds.has(String(deal.salesStageId || getDealSalesStageId(deal) || "").trim()))
    .map((deal) => ({
      id: String(deal.salesStageId || getDealSalesStageId(deal) || "").trim(),
      stageId: String(deal.salesStageId || getDealSalesStageId(deal) || "").trim(),
      label: deal.salesStageLabel || getOpportunityStageLabel(deal.salesStageId),
      color: deal.salesStageColor || "#64748b",
      confirmsQuote: false,
      ordersMaterial: false,
      marksComplete: false,
      isCancelled: false,
      defaultChecklist: [],
    }));

  const dedupedLegacy = legacyStages.filter(
    (stage, index) =>
      legacyStages.findIndex((item) => item.id === stage.id) === index
  );

  return [...salesPipelineActiveStages, ...dedupedLegacy].filter((stage) => stage.id || stage.stageId);
}, [crmBoardDeals, getDealSalesStageId, salesPipelineActiveStages]);

useEffect(() => {
  if (!isDevRuntime || tab !== "crm" || crmView !== "board") return;
  devLog("PIPELINE_BOARD_QUERY_TRACE", {
    workspaceUid,
    filters: {
      scope: crmScope,
      stage: crmStageFilter,
      source: crmSourceFilter,
      search: normalizeSearchText(crmSearch),
    },
    stageIds: crmBoardStages.map((stage) => stage.stageId || stage.id),
    resultCount: crmBoardDeals.length,
    hasMore: false,
    readMode: "bounded_loaded_crm_records_grouped_client_side",
  });
}, [
  crmBoardDeals.length,
  crmBoardStages,
  crmScope,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  crmView,
  tab,
  workspaceUid,
]);

const crmTopClients = useMemo(() => {
  const clientMap = {};

  crmScopeDeals.forEach((deal) => {
    const key =
      deal.crmContact?.name ||
      deal.customerLabel ||
      "Unnamed Customer";
    if (!clientMap[key]) {
      clientMap[key] = {
        name: key,
        value: 0,
        count: 0,
        deals: [],
      };
    }

    clientMap[key].value += Number(deal.expectedValue || 0);
    clientMap[key].count += 1;
    clientMap[key].deals.push(deal);
  });

  return Object.values(clientMap)
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return b.count - a.count;
    })
}, [crmScopeDeals]);

const crmChecklistTrend = useMemo(
  () => [
    {
      name: "Complete",
      value: crmDeals.reduce((sum, deal) => sum + deal.checklistCompleted, 0),
      color: "#10b981",
    },
    {
      name: "Remaining",
      value: crmDeals.reduce(
        (sum, deal) =>
          sum + Math.max(0, deal.checklistTotal - deal.checklistCompleted),
        0
      ),
      color: "#cbd5e1",
    },
  ].filter((entry) => entry.value > 0),
  [crmDeals]
);

const crmOpenDeals = useMemo(
  () =>
    crmScopeDeals.filter(
      (deal) => !deal.isCrmClosed && !deal.isCrmCancelled
    ),
  [crmScopeDeals]
);

const crmOverdueDeals = useMemo(
  () =>
    crmOpenDeals
      .filter(
        (deal) => deal.nextActionMillis && deal.nextActionMillis < Date.now()
      )
      .sort((a, b) => a.nextActionMillis - b.nextActionMillis),
  [crmOpenDeals]
);

const crmStalledDeals = useMemo(
  () =>
    crmOpenDeals
      .filter(
        (deal) =>
          deal.updatedMillis &&
          Date.now() - deal.updatedMillis > 1000 * 60 * 60 * 24 * 10
      )
      .sort((a, b) => a.updatedMillis - b.updatedMillis),
  [crmOpenDeals]
);

const crmNewDealsInPeriod = useMemo(
  () =>
    crmScopeDeals
      .filter((deal) => {
        if (!deal.createdMillis) return false;
        return isDateInPeriod(new Date(deal.createdMillis), periodType, customRange);
      })
      .sort((a, b) => b.createdMillis - a.createdMillis),
  [crmScopeDeals, customRange, periodType]
);

const crmUpcomingInstallationDeals = useMemo(
  () =>
    crmScopeDeals
      .filter(
        (deal) =>
          !deal.isCrmCancelled &&
          deal.installationMillis &&
          deal.installationMillis >= todayStartMillis
      )
      .sort((a, b) => a.installationMillis - b.installationMillis),
  [crmScopeDeals, todayStartMillis]
);

const crmUpcomingDeals = useMemo(
  () => crmUpcomingInstallationDeals.slice(0, CRM_OVERVIEW_PREVIEW_LIMIT),
  [crmUpcomingInstallationDeals]
);

const crmStageFilterOptions = useMemo(
  () => crmBoardFilterOptions,
  [crmBoardFilterOptions]
);

const crmPipelineChecklistTrend = useMemo(
  () => [
    {
      name: "Complete",
      value: crmScopeDeals.reduce(
        (sum, deal) => sum + Number(deal.checklistCompleted || 0),
        0
      ),
      color: "#10b981",
    },
    {
      name: "Remaining",
      value: crmScopeDeals.reduce(
        (sum, deal) =>
          sum + Math.max(0, Number(deal.checklistTotal || 0) - Number(deal.checklistCompleted || 0)),
        0
      ),
      color: "#cbd5e1",
    },
  ].filter((entry) => entry.value > 0),
  [crmScopeDeals]
);

const crmPipelineSummary = useMemo(() => {
  const checklistTotals = crmOpenDeals.reduce(
    (sum, deal) => {
      sum.completed += deal.checklistCompleted;
      sum.total += deal.checklistTotal;
      return sum;
    },
    { completed: 0, total: 0 }
  );

  return {
    activeDeals: crmOpenDeals.length,
    newDeals: crmNewDealsInPeriod.length,
    overdue: crmOverdueDeals.length,
    stalled: crmStalledDeals.length,
    pipelineValue: crmOpenDeals.reduce(
      (sum, deal) => sum + Number(deal.expectedValue || 0),
      0
    ),
    completed: crmScopeDeals.filter((deal) => deal.isCrmClosed).length,
    cancelled: crmScopeDeals.filter((deal) => deal.isCrmCancelled).length,
    checklistCompleted: checklistTotals.completed,
    checklistTotal: checklistTotals.total,
    checklistCompletion: checklistTotals.total
      ? Math.round((checklistTotals.completed / checklistTotals.total) * 100)
      : 0,
  };
}, [crmNewDealsInPeriod.length, crmOpenDeals, crmOverdueDeals.length, crmScopeDeals, crmStalledDeals.length]);

const crmScopeLabel =
  crmScope === CRM_SCOPE_ALL ? "All Pipelines" : getQuoteTypeLabel(crmScope);

const crmScopeSubtitle =
  crmScope === CRM_SCOPE_ALL
    ? "Curtains and general quotation deals combined in one CRM view."
    : getCrmPipeline(crmPipelines, crmScope).label;

const crmRecentActivityDeals = useMemo(
  () => [...crmScopeDeals].sort((a, b) => b.updatedMillis - a.updatedMillis).slice(0, 5),
  [crmScopeDeals]
);

const crmQuoteTypeMix = useMemo(
  () =>
    [
      {
        key: "quoteType:curtains",
        quoteType: CURTAIN_QUOTE_TYPE,
        name: "Curtains",
        deals: crmScopeDeals.filter((deal) => deal.pipelineType === CURTAIN_QUOTE_TYPE),
        color: "#2563eb",
      },
      {
        key: "quoteType:general",
        quoteType: GENERAL_QUOTE_TYPE,
        name: "General Quotation",
        deals: crmScopeDeals.filter((deal) => deal.pipelineType === GENERAL_QUOTE_TYPE),
        color: "#8b5cf6",
      },
    ]
      .map((entry) => ({
        ...entry,
        count: entry.deals.length,
        value: entry.deals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
      }))
      .filter((entry) => entry.count > 0 || entry.value > 0),
  [crmScopeDeals]
);

const crmOverviewDrilldowns = useMemo(() => {
  const quoteTypeDrilldowns = crmQuoteTypeMix.reduce(
    (next, entry) => ({
      ...next,
      [entry.key]: {
        key: entry.key,
        label: `${entry.name} Deals`,
        description: `${entry.count} ${entry.name.toLowerCase()} deal${
          entry.count === 1 ? "" : "s"
        } contributing ${formatCurrencyDisplay(entry.value || 0, currency)}.`,
        deals: entry.deals,
        totalValue: entry.value,
      },
    }),
    {}
  );

  const clientDrilldowns = crmTopClients.reduce(
    (next, client) => ({
      ...next,
      [`client:${client.name}`]: {
        key: `client:${client.name}`,
        label: client.name,
        description: `${client.count} linked deal${client.count === 1 ? "" : "s"} for this client.`,
        deals: client.deals,
        totalValue: client.value,
      },
    }),
    {}
  );

  return {
    active: {
      key: "active",
      label: "Active Deals",
      deals: crmOpenDeals,
    },
    new: {
      key: "new",
      label: `New Deals ${dashboardPeriodLabel}`,
      deals: crmNewDealsInPeriod,
    },
    value: {
      key: "value",
      label: "Pipeline Value",
      deals: crmOpenDeals,
      totalValue: crmOpenDeals.reduce(
        (sum, deal) => sum + Number(deal.expectedValue || 0),
        0
      ),
    },
    overdue: {
      key: "overdue",
      label: "Overdue Follow-ups",
      deals: crmOverdueDeals,
    },
    stalled: {
      key: "stalled",
      label: "Stalled Deals",
      deals: crmStalledDeals,
    },
    installs: {
      key: "installs",
      label: "Upcoming Installations",
      deals: crmUpcomingInstallationDeals,
    },
    quoteTypeMix: {
      key: "quoteTypeMix",
      label: "Quote Type Mix",
      description: "All CRM deals grouped by quotation type.",
      deals: crmScopeDeals,
      totalValue: crmScopeDeals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
    },
    topClients: {
      key: "topClients",
      label: "Top Clients",
      description: "All ranked client records contributing to the top-client widget.",
      deals: crmTopClients.flatMap((client) => client.deals),
      totalValue: crmTopClients.reduce((sum, client) => sum + Number(client.value || 0), 0),
    },
    ...quoteTypeDrilldowns,
    ...clientDrilldowns,
  };
}, [
  crmNewDealsInPeriod,
  crmOpenDeals,
  crmOverdueDeals,
  crmQuoteTypeMix,
  crmScopeDeals,
  crmStalledDeals,
  crmTopClients,
  crmUpcomingInstallationDeals,
  currency,
  dashboardPeriodLabel,
]);

const crmActiveDrilldown = crmDrilldownKey
  ? crmOverviewDrilldowns[crmDrilldownKey] || null
  : null;

const crmDealListBaseDeals = useMemo(
  () => (crmActiveDrilldown ? crmActiveDrilldown.deals : crmScopeDeals),
  [crmActiveDrilldown, crmScopeDeals]
);

const crmFilteredDeals = useMemo(() => {
  const queryText = normalizeSearchText(deferredCrmSearch);

  return crmDealListBaseDeals
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmSourceFilter;
    })
    .filter((deal) => {
      if (crmTypeFilter === "all") return true;
      return deal.pipelineType === crmTypeFilter;
    })
    .filter((deal) => {
      if (crmStageFilter === "all") return true;
      if (String(crmStageFilter).includes("::")) {
        const [pipelineType, stageId] = crmStageFilter.split("::");
        return deal.pipelineType === pipelineType && deal.crmStageId === stageId;
      }
      return String(deal.salesStageId || getDealSalesStageId(deal) || "").trim() === crmStageFilter;
    })
    .sort((a, b) => {
      if (crmSortBy === "highest") {
        return Number(b.expectedValue || 0) - Number(a.expectedValue || 0);
      }
      if (crmSortBy === "client") {
        return String(a.customerLabel || a.crmContact?.name || "").localeCompare(
          String(b.customerLabel || b.crmContact?.name || "")
        );
      }
      if (crmSortBy === "oldest") {
        return Number(a.updatedMillis || a.createdMillis || 0) - Number(b.updatedMillis || b.createdMillis || 0);
      }
      return Number(b.updatedMillis || b.createdMillis || 0) - Number(a.updatedMillis || a.createdMillis || 0);
    });
}, [
  crmDealListBaseDeals,
  crmSortBy,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  customerRecords,
  deferredCrmSearch,
  getDealSalesStageId,
]);

const visibleCrmDeals = useMemo(
  () => crmFilteredDeals.slice(0, crmDealsVisibleCount),
  [crmDealsVisibleCount, crmFilteredDeals]
);

const hasMoreCrmDeals = visibleCrmDeals.length < crmFilteredDeals.length;

useEffect(() => {
  traceListSort("CRM Deals", visibleCrmDeals, (deal) =>
    deal.quoteNo || deal.leadId || deal.crmLeadId || deal.id || ""
  );
}, [visibleCrmDeals]);

useEffect(() => {
  if (!isDevRuntime) return;
  const auditRecords = visibleCrmDeals.slice(0, 5).map((deal) => ({
    id: deal.id,
    leadId: deal.leadId,
    crmLeadId: deal.crmLeadId,
    canonicalLeadId: deal.canonicalLeadId,
    crmLeadIdFromCrm: deal.crm?.leadId,
    quoteId: deal.quoteId || deal.primaryLinkedQuote?.quoteId || "",
    quoteNo: deal.quoteNo || deal.primaryLinkedQuote?.quoteNo || "",
    customerName:
      deal.customerName ||
      deal.customerLabel ||
      deal.crmContact?.name ||
      deal.contact?.name ||
      "",
    sourceType: deal.sourceType || deal.crmRecordType || "",
    hasRawLead: Boolean(deal.rawLead),
  }));
  devLog("DEAL LIST RECORD AUDIT", auditRecords);
}, [visibleCrmDeals]);

useEffect(() => {
  if (!isDevRuntime) return;
  const auditDeal = visibleCrmDeals.find(
    (deal) =>
      (selectedCrmDealId && String(deal.id || "") === String(selectedCrmDealId)) ||
      (crmDetailsOpenDebug.lastResolvedLeadId &&
        String(deal.id || "") === String(crmDetailsOpenDebug.lastResolvedLeadId))
  );
  if (!auditDeal) return;

  devLog("DEAL CARD SUMMARY AUDIT", {
    id: auditDeal.id,
    customerName:
      auditDeal.customerName ||
      auditDeal.customerLabel ||
      auditDeal.crmContact?.name ||
      "",
    rawChecklistItems: auditDeal.checklistItems,
    checklistSummary: getChecklistSummary(auditDeal),
    rawFollowUps: auditDeal.followUps,
    followUpSummary: getFollowUpSummary(auditDeal, { nowMillis: Date.now() }),
    installationDate: auditDeal.installationDate,
    installationSummary: getInstallationSummary(auditDeal),
  });
}, [crmDetailsOpenDebug.lastResolvedLeadId, selectedCrmDealId, visibleCrmDeals]);

useEffect(() => {
  setCrmDealsVisibleCount(CRM_DEALS_PAGE_SIZE);
}, [
  crmActiveDrilldown,
  crmSortBy,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  deferredCrmSearch,
]);

const crmQuoteLeadDeals = useMemo(() => {
  const linkedLeadQuoteIds = new Set();
  const linkedLeadQuoteNos = new Set();

  crmLeadDeals.forEach((deal) => {
    normalizeCrmLinkedQuotes(deal).forEach((linkedQuote) => {
      if (linkedQuote.quoteId) {
        linkedLeadQuoteIds.add(String(linkedQuote.quoteId));
      }
      if (linkedQuote.quoteNo) {
        linkedLeadQuoteNos.add(String(linkedQuote.quoteNo));
      }
    });
  });

  return crmDeals.filter((deal) => {
    if (deal.crmRecordType !== "quote") return false;

    const linkedLeadId = String(deal.crm?.leadId || "").trim();
    if (linkedLeadId) return false;

    const quoteId = String(deal.id || "");
    const quoteNo = String(deal.quoteNo || "");

    if (quoteId && linkedLeadQuoteIds.has(quoteId)) return false;
    if (quoteNo && linkedLeadQuoteNos.has(quoteNo)) return false;

    return true;
  });
}, [crmDeals, crmLeadDeals]);

const crmUnifiedLeadDeals = useMemo(() => {
  return sortCrmRecordsByLatestActivity([...crmLeadDeals, ...crmQuoteLeadDeals]);
}, [crmLeadDeals, crmQuoteLeadDeals]);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return;
  devLog("crm_leads:", crmLeadDeals.length);
  devLog("unlinked quotes:", crmQuoteLeadDeals.length);
  devLog("final leads:", crmUnifiedLeadDeals.length);
}, [crmLeadDeals.length, crmQuoteLeadDeals.length, crmUnifiedLeadDeals.length]);

const crmLeadSourceOptions = useMemo(() => {
  const values = new Set();

  crmUnifiedLeadDeals.forEach((deal) => {
    const sourceValue = String(deal.source || deal.crmContact?.source || "").trim();
    if (sourceValue) values.add(sourceValue);
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmUnifiedLeadDeals]);

const crmLeadAssignedOptions = useMemo(() => {
  const values = new Map();

  crmUnifiedLeadDeals.forEach((deal) => {
    const memberId = String(deal.assignedToUserId || "").trim();
    if (!memberId || values.has(memberId)) return;

    values.set(memberId, {
      id: memberId,
      label: getTeamMemberLabel(memberId, deal.assignedToNameSnapshot),
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmUnifiedLeadDeals, getTeamMemberLabel]);

const crmLeadStageOptions = useMemo(() => {
  const values = new Map();

  crmUnifiedLeadDeals.forEach((deal) => {
    const stageKey = `${deal.pipelineType}::${deal.crmStageId}`;
    if (!deal.crmStageId || values.has(stageKey)) return;

    values.set(stageKey, {
      key: stageKey,
      label: `${getQuoteTypeLabel(deal.pipelineType)} | ${deal.crmStageLabel}`,
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmUnifiedLeadDeals]);

const crmFilteredLeadDeals = useMemo(() => {
  const queryText = normalizeSearchText(deferredCrmLeadSearch);
  const fromMillis = crmLeadFromDate
    ? safeTimeMillis(parseDateInputValue(crmLeadFromDate))
    : 0;
  const toMillisValue = crmLeadToDate
    ? safeTimeMillis(parseDateInputValue(crmLeadToDate)) + 24 * 60 * 60 * 1000 - 1
    : 0;

  return crmUnifiedLeadDeals
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmLeadSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmLeadSourceFilter;
    })
    .filter((deal) => {
      if (crmLeadStageFilter === "all") return true;
      return `${deal.pipelineType}::${deal.crmStageId}` === crmLeadStageFilter;
    })
    .filter((deal) => {
      if (crmLeadAssignedFilter === "all") return true;
      return String(deal.assignedToUserId || "") === crmLeadAssignedFilter;
    })
    .filter((deal) => {
      const referenceMillis = Number(getCrmListSortTime(deal) || deal.createdMillis || 0);
      if (fromMillis && referenceMillis < fromMillis) return false;
      if (toMillisValue && referenceMillis > toMillisValue) return false;
      return true;
    })
    .sort(
      (a, b) =>
        Number(getCrmListSortTime(b) || b.createdMillis || 0) -
        Number(getCrmListSortTime(a) || a.createdMillis || 0)
    );
}, [
  crmLeadAssignedFilter,
  crmLeadFromDate,
  crmLeadSourceFilter,
  crmLeadStageFilter,
  crmLeadToDate,
  crmUnifiedLeadDeals,
  customerRecords,
  deferredCrmLeadSearch,
]);

const getQuoteLinkDisplayData = useCallback(
  (quote = {}) => {
    const quoteType = resolveQuoteType(quote);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE ? getEmbeddedGeneralQuote(quote) : null;
    const contact = buildCrmContactFromQuote(quote, quoteType, embeddedGeneral);
    const quoteDate = resolveQuoteDateForInput(
      {
        ...quote,
        generalQuote: embeddedGeneral || quote.generalQuote,
      },
      null
    ) || quote.createdAt || null;

    return {
      id: quote.id || "",
      quoteNo: quote.quoteNo || "",
      customerName:
        contact?.name ||
        quote.customerName ||
        embeddedGeneral?.quoteFor?.company ||
        embeddedGeneral?.quoteFor?.name ||
        getQuoteCustomerLabel(quote),
      phone: contact?.phone || quote.phone || embeddedGeneral?.quoteFor?.phone || "",
      email: contact?.email || quote.email || embeddedGeneral?.quoteFor?.email || "",
      quoteType,
      quoteTypeLabel: quoteType === GENERAL_QUOTE_TYPE ? "General" : "Curtain",
      quoteDate,
      quoteDateLabel: quoteDate ? toDateInputValue(quoteDate) || "-" : "-",
      totalValue: getNormalizedQuoteTotal(quote),
      status: quote.status || quote.crm?.status || "draft",
      crmLeadId: quote.crmLeadId || quote.leadId || quote.crm?.leadId || "",
    };
  },
  [getNormalizedQuoteTotal]
);

const quoteLinkTargetLead = useMemo(() => {
  if (!quoteLinkModalState?.leadId) return null;
  return crmDeals.find((deal) => deal.id === quoteLinkModalState.leadId) || null;
}, [crmDeals, quoteLinkModalState]);

const quoteLinkResults = useMemo(() => {
  const queryText = quoteLinkSearch.trim().toLowerCase();
  const minAmount = quoteLinkMinAmount === "" ? null : Number(quoteLinkMinAmount);
  const maxAmount = quoteLinkMaxAmount === "" ? null : Number(quoteLinkMaxAmount);

  return mergeQuoteIndexPages(savedQuotes, savedLookupResult.key === `${workspaceUid}:${normalizeSearchText(quoteLinkSearch)}:${user?.uid}:${canAccessCurtainQuotes}:${canAccessGeneralQuotes}:${recycleBinOpen}` ? savedLookupResult.records : [])
    .filter((quote) => !isDeletedQuoteRecord(quote))
    .map((quote) => ({
      quote,
      meta: getQuoteLinkDisplayData(quote),
    }))
    .filter(({ meta }) => {
      if (quoteLinkTypeFilter !== "all" && meta.quoteType !== quoteLinkTypeFilter) {
        return false;
      }

      if (Number.isFinite(minAmount) && meta.totalValue < minAmount) {
        return false;
      }

      if (Number.isFinite(maxAmount) && meta.totalValue > maxAmount) {
        return false;
      }

      if (!queryText) return true;

      return [
        meta.quoteNo,
        meta.customerName,
        meta.phone,
        meta.email,
        meta.quoteTypeLabel,
        meta.status,
        meta.totalValue,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .sort((a, b) => {
      const linkedDelta =
        Number(Boolean(b.meta.crmLeadId)) - Number(Boolean(a.meta.crmLeadId));
      if (linkedDelta !== 0) return linkedDelta;
      return Number(getUpdatedMillis(b.quote) || getCreatedMillis(b.quote) || 0) -
        Number(getUpdatedMillis(a.quote) || getCreatedMillis(a.quote) || 0);
    })
    .slice(0, 80);
}, [
  getQuoteLinkDisplayData,
  quoteLinkMaxAmount,
  quoteLinkMinAmount,
  quoteLinkSearch,
  savedLookupResult,
  recycleBinOpen,
  workspaceUid,
  user?.uid,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  quoteLinkTypeFilter,
  savedQuotes,
]);

const getCustomerIdentityFromQuote = useCallback(
  (quoteLike = {}) => {
    const quoteType = resolveQuoteType(quoteLike);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE ? getEmbeddedGeneralQuote(quoteLike) : null;
    const contact = buildCrmContactFromQuote(quoteLike, quoteType, embeddedGeneral);
    const identity = resolveQuoteIdentityParts({
      ...quoteLike,
      generalQuote: embeddedGeneral || quoteLike.generalQuote || {},
    });
    const customerName =
      identity.customerName ||
      quoteLike.customerName ||
      embeddedGeneral?.quoteFor?.company ||
      getQuoteCustomerLabel(quoteLike);

    return {
      customerName,
      phone: contact?.phone || quoteLike.customerPhone || quoteLike.phone || embeddedGeneral?.quoteFor?.phone || "",
      whatsapp: contact?.whatsapp || quoteLike.whatsapp || "",
      email: contact?.email || quoteLike.email || embeddedGeneral?.quoteFor?.email || "",
      address: contact?.address || quoteLike.address || embeddedGeneral?.quoteFor?.address || "",
      companyName:
        quoteLike.customerCompanyName ||
        embeddedGeneral?.quoteFor?.company ||
        quoteLike.companyName ||
        customerName ||
        "",
      contactPersonName: identity.contactPersonName || contact?.name || "",
      contactPersonPhone: identity.contactPersonPhone || contact?.phone || "",
      source: quoteLike.source || quoteLike.crm?.source || "",
      tags: Array.isArray(quoteLike.tags) ? quoteLike.tags : [],
    };
  },
  []
);

const getCustomerIdentityFromLeadDraft = useCallback((draft = {}) => {
  const contact = draft.contact || {};
  return {
    customerName:
      contact.name ||
      draft.companyOrProject ||
      draft.title ||
      "Unnamed Customer",
    phone: contact.phone || "",
    whatsapp: contact.whatsapp || "",
    email: contact.email || "",
    address: contact.address || "",
    companyName: draft.companyOrProject || "",
    source: draft.source || contact.source || "",
    tags: String(draft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
  };
}, []);

const findCustomerLedgerMatch = useCallback(
  (identity = {}) => {
    const normalizedPhone = normalizeCustomerLedgerPhone(
      identity.phone || identity.whatsapp
    );
    const normalizedWhatsapp = normalizeCustomerLedgerPhone(
      identity.whatsapp || identity.phone
    );
    const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
    const normalizedName = normalizeCustomerLedgerName(identity.customerName);

    const exactContactMatch = customerRecords.find((customer) => {
      const customerPhone = normalizeCustomerLedgerPhone(
        customer.phone || customer.normalizedPhone
      );
      const customerWhatsapp = normalizeCustomerLedgerPhone(
        customer.whatsapp || customer.normalizedWhatsapp
      );
      const customerEmail = normalizeCustomerLedgerEmail(
        customer.email || customer.normalizedEmail
      );

      return (
        (normalizedPhone &&
          (customerPhone === normalizedPhone || customerWhatsapp === normalizedPhone)) ||
        (normalizedWhatsapp &&
          (customerPhone === normalizedWhatsapp || customerWhatsapp === normalizedWhatsapp)) ||
        (normalizedEmail && customerEmail === normalizedEmail)
      );
    });

    if (exactContactMatch) {
      return { customer: exactContactMatch, confidence: "exact" };
    }

    const exactNameMatches = customerRecords.filter(
      (customer) =>
        normalizedName &&
        normalizeCustomerLedgerName(customer.normalizedName || customer.customerName) ===
          normalizedName
    );

    if (!normalizedPhone && !normalizedEmail && exactNameMatches.length === 1) {
      return { customer: exactNameMatches[0], confidence: "name" };
    }

    return {
      customer: null,
      confidence: "none",
      possibleMatches: exactNameMatches,
    };
  },
  [customerRecords]
);

const isExactCustomerIdentityMatch = useCallback((customer = {}, identity = {}) => {
  const normalizedPhone = normalizeCustomerLedgerPhone(
    identity.phone || identity.whatsapp
  );
  const normalizedWhatsapp = normalizeCustomerLedgerPhone(
    identity.whatsapp || identity.phone
  );
  const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
  const normalizedName = normalizeCustomerLedgerName(identity.customerName);
  const customerPhone = normalizeCustomerLedgerPhone(
    customer.phone || customer.normalizedPhone
  );
  const customerWhatsapp = normalizeCustomerLedgerPhone(
    customer.whatsapp || customer.normalizedWhatsapp
  );
  const customerEmail = normalizeCustomerLedgerEmail(
    customer.email || customer.normalizedEmail
  );
  const customerName = normalizeCustomerLedgerName(
    customer.normalizedName || customer.customerName
  );

  return Boolean(
    (normalizedPhone &&
      (customerPhone === normalizedPhone || customerWhatsapp === normalizedPhone)) ||
      (normalizedWhatsapp &&
        (customerPhone === normalizedWhatsapp ||
          customerWhatsapp === normalizedWhatsapp)) ||
      (normalizedEmail && customerEmail === normalizedEmail) ||
      (normalizedName && customerName === normalizedName)
  );
}, []);

const mergeCustomersById = useCallback((entries = []) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .filter(Boolean)
    .map((entry) => normalizeCustomerLedgerRecord(entry))
    .filter((entry) => {
      if (!entry.id || seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
}, []);

const searchCustomersForQuotation = useCallback(
  async (queryText = "", options = {}) => {
    const normalizedQuery = normalizeCustomerSearchText(queryText);
    const normalizedPhoneQuery = normalizeCustomerSearchPhone(queryText);
    const normalizedEmailQuery = normalizeEmailValue(queryText);

    if (!workspaceUid || normalizedQuery.length < 2) {
      return [];
    }

    const cacheKey = `${workspaceUid}:${normalizedQuery}:person-index`;
    const cached = quotationCustomerSearchCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < CUSTOMER_SEARCH_TTL_MS) {
      if (isDevRuntime) {
        devLog("CUSTOMER SEARCH QUERY", {
          query: queryText,
          normalizedQuery,
          cacheHit: true,
          resultCount: cached.results.length,
          source: "memory-cache",
        });
      }
      return cached.results;
    }

    const resultMap = new Map();
    const personResults = await globalSearchCustomerPeople(workspaceUid, queryText, {
      limit: CUSTOMER_SEARCH_RESULT_LIMIT * 2,
      ttlMs: CUSTOMER_SEARCH_TTL_MS,
      labelPrefix: "quotationCustomerPersonSearch",
    });
    mergePersonSearchResultRecords(personResults).forEach((customer) => {
      const identityKey = getPersonIdentityKey(customer) || customer.id;
      if (identityKey) resultMap.set(identityKey, customer);
    });

    const results = Array.from(resultMap.values())
      .filter((customer) => {
        const searchText = buildCustomerLedgerSearchText(customer);
        const phoneMatches =
          normalizedPhoneQuery &&
          [
            customer.normalizedPhone,
            customer.normalizedWhatsapp,
            customer.phone,
            customer.whatsapp,
          ]
            .map(normalizeCustomerSearchPhone)
            .some((value) => value.startsWith(normalizedPhoneQuery));
        const emailMatches =
          normalizedEmailQuery &&
          [
            customer.email,
            customer.customerEmail,
            customer.normalizedEmail,
            customer.emailCanonical,
            customer.emailLower,
          ]
            .map(normalizeEmailValue)
            .some((value) => value.startsWith(normalizedEmailQuery));
        return searchText.includes(normalizedQuery) || phoneMatches || emailMatches;
      })
      .sort((a, b) => {
        const aActivity = getRecordSortTime(a, [
          "lastActivityAt",
          "latestActivityAt",
          "updatedAt",
          "createdAt",
        ]);
        const bActivity = getRecordSortTime(b, [
          "lastActivityAt",
          "latestActivityAt",
          "updatedAt",
          "createdAt",
        ]);
        if (aActivity !== bActivity) return bActivity - aActivity;
        return String(a.customerName || "").localeCompare(
          String(b.customerName || "")
        );
      })
      .slice(0, CUSTOMER_SEARCH_RESULT_LIMIT);

    quotationCustomerSearchCacheRef.current.set(cacheKey, {
      fetchedAt: Date.now(),
      results,
    });

    if (isDevRuntime) {
      devLog("CUSTOMER SEARCH QUERY", {
        query: queryText,
        normalizedQuery,
        cacheHit: false,
        resultCount: results.length,
        source: "person-index-only",
      });
    }

    return results;
  },
  [workspaceUid]
);

const runQuotationCustomerSearch = useCallback(
  async (context, queryText, options = {}) => {
    const requestId =
      Number(quotationCustomerSearchRequestRef.current[context] || 0) + 1;
    quotationCustomerSearchRequestRef.current[context] = requestId;
    const normalizedQuery = normalizeCustomerSearchText(queryText);

    if (!workspaceUid || normalizedQuery.length < 2) {
      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          query: queryText,
          loading: false,
          results: [],
          error: "",
          searched: false,
        },
      }));
      return;
    }

    setQuotationCustomerSearchState((prev) => ({
      ...prev,
      [context]: {
        ...(prev[context] || {}),
        query: queryText,
        loading: true,
        error: "",
        searched: true,
      },
    }));

    try {
      const results = await searchCustomersForQuotation(queryText, options);
      if (requestId !== quotationCustomerSearchRequestRef.current[context]) return;

      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          query: queryText,
          loading: false,
          results,
          error: "",
          searched: true,
        },
      }));
      setCustomerRecords((prev) =>
        mergeCustomersById([...results, ...prev])
      );
    } catch (error) {
      if (requestId !== quotationCustomerSearchRequestRef.current[context]) return;
      devError("Quotation customer search failed", error);
      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          ...(prev[context] || {}),
          query: queryText,
          loading: false,
          error: "Could not search customers.",
          searched: true,
        },
      }));
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [mergeCustomersById, notify, searchCustomersForQuotation, workspaceUid]
);

useEffect(() => {
  const timers = Object.entries(quotationCustomerSearchQueries).map(
    ([context, queryText]) =>
      window.setTimeout(() => {
        runQuotationCustomerSearch(context, queryText);
      }, CUSTOMER_SEARCH_DEBOUNCE_MS)
  );

  return () => {
    timers.forEach((timerId) => window.clearTimeout(timerId));
  };
}, [quotationCustomerSearchQueries, runQuotationCustomerSearch]);

const buildCustomerLedgerTotals = useCallback(
  (customerId, extraQuote = null, extraLead = null) => {
    const existingCustomer = customerRecords.find((customer) => customer.id === customerId);
    const customerPhoneKey = normalizeCustomerLedgerPhone(
      existingCustomer?.phone || existingCustomer?.whatsapp || ""
    );
    const customerEmailKey = normalizeCustomerLedgerEmail(existingCustomer?.email || "");
    const customerNameKey = normalizeCustomerLedgerName(existingCustomer?.customerName || "");
    const quoteMatchesCustomer = (quote) => {
      const quoteCustomerId = String(quote.customerId || quote.crm?.customerId || "");
      if (quoteCustomerId && quoteCustomerId === String(customerId)) return true;
      const identity = getCustomerIdentityFromQuote(quote);
      const quotePhoneKey = normalizeCustomerLedgerPhone(identity.phone || identity.whatsapp);
      const quoteEmailKey = normalizeCustomerLedgerEmail(identity.email);
      const quoteNameKey = normalizeCustomerLedgerName(identity.customerName);
      return (
        (customerPhoneKey && quotePhoneKey && customerPhoneKey === quotePhoneKey) ||
        (customerEmailKey && quoteEmailKey && customerEmailKey === quoteEmailKey) ||
        (customerNameKey && quoteNameKey && customerNameKey === quoteNameKey)
      );
    };
    const leadMatchesCustomer = (lead) => {
      const leadCustomerId = String(lead.customerId || lead.crm?.customerId || "");
      if (leadCustomerId && leadCustomerId === String(customerId)) return true;
      const leadContact = lead.contact || lead.crm?.contact || {};
      const leadPhoneKey = normalizeCustomerLedgerPhone(
        lead.customerPhone || leadContact.phone || leadContact.whatsapp || ""
      );
      const leadEmailKey = normalizeCustomerLedgerEmail(leadContact.email || lead.email || "");
      const leadNameKey = normalizeCustomerLedgerName(
        lead.customerName || leadContact.name || lead.companyOrProject || lead.title || ""
      );
      return (
        (customerPhoneKey && leadPhoneKey && customerPhoneKey === leadPhoneKey) ||
        (customerEmailKey && leadEmailKey && customerEmailKey === leadEmailKey) ||
        (customerNameKey && leadNameKey && customerNameKey === leadNameKey)
      );
    };
    const allQuotes = [
      ...savedQuotes,
      ...(extraQuote ? [extraQuote] : []),
    ].filter(
      (quote, index, list) =>
        quote &&
        quoteMatchesCustomer(quote) &&
        !isDeletedQuoteRecord(quote) &&
        list.findIndex((entry) => String(entry.id || "") === String(quote.id || "")) === index
    );
    const allLeads = [
      ...crmLeadRecords,
      ...(extraLead ? [extraLead] : []),
    ].filter(
      (lead, index, list) =>
        lead &&
        leadMatchesCustomer(lead) &&
        list.findIndex((entry) => String(entry.id || "") === String(lead.id || "")) === index
    );
    const totalQuoteValue = allQuotes.reduce(
      (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
      0
    );
    const confirmedQuoteRecords = allQuotes.filter(
      (quote) => String(quote.status || quote.crm?.status || "").toLowerCase() === "confirmed"
    );
    const confirmedQuotes = confirmedQuoteRecords.length;
    const confirmedValue = confirmedQuoteRecords.reduce(
      (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
      0
    );
    const openDealRecords = allLeads.filter((lead) => isOpenCrmDeal(lead));
    const openDealCount = openDealRecords.length;
    const activeDealValue = openDealRecords.reduce(
      (sum, lead) =>
        sum +
        Number(
          lead.expectedValue ||
            lead.finalValue ||
            lead.totalValue ||
            lead.crm?.expectedValue ||
            lead.crm?.finalValue ||
            0
        ),
      0
    );
    const pendingFollowupCount = allLeads.reduce(
      (sum, lead) =>
        sum +
        normalizeCrmFollowUps(lead.followUps || lead.crm?.followUps || []).filter(
          (followUp) => followUp.status !== CRM_FOLLOW_UP_STATUS_DONE
        ).length,
      0
    );
    const upcomingInstallationCount = allLeads.filter(
      (lead) =>
        lead.installationDate ||
        lead.crm?.installationDate ||
        lead.expectedInstallationDate ||
        lead.scheduledInstallationDate
    ).length;
    const completedJobs = allLeads.filter((lead) => {
      const stage = getCrmStage(crmPipelines, lead.pipelineType || lead.quoteType, lead.stageId);
      return stage?.marksComplete === true || String(lead.status || "").toLowerCase() === "completed";
    }).length;
    const receivedAmount = Number(existingCustomer?.totals?.receivedAmount || 0);

    return {
      totalQuotes: allQuotes.length,
      totalQuoteValue,
      confirmedQuotes,
      confirmedValue,
      openDealCount,
      activeDealValue,
      pendingFollowupCount,
      upcomingInstallationCount,
      completedJobs,
      pendingAmount: Math.max(0, totalQuoteValue - receivedAmount),
      receivedAmount,
    };
  },
  [
    crmLeadRecords,
    crmPipelines,
    customerRecords,
    getCustomerIdentityFromQuote,
    getNormalizedQuoteTotal,
    isOpenCrmDeal,
    savedQuotes,
  ]
);

const ensureCustomerLedgerRecord = useCallback(
  async (identity = {}, options = {}) => {
    if (!workspaceUid) return null;

    const sanitizedIdentity = normalizeCustomerLedgerRecord({
      customerName: identity.customerName,
      phone: identity.phone,
      whatsapp: identity.whatsapp,
      email: identity.email,
      address: identity.address,
      companyName: identity.companyName,
      source: identity.source,
      tags: identity.tags,
      notes: identity.notes,
    });
    const match = findCustomerLedgerMatch(sanitizedIdentity);
    let existingCustomer = options.customerId
      ? customerRecords.find((customer) => customer.id === options.customerId) || null
      : match.customer;

    if (!existingCustomer && !options.customerId) {
      const lookupText =
        sanitizedIdentity.phone ||
        sanitizedIdentity.whatsapp ||
        sanitizedIdentity.email ||
        sanitizedIdentity.customerName;
      try {
        const candidates = await searchCustomersForQuotation(lookupText);
        existingCustomer =
          candidates.find((customer) =>
            isExactCustomerIdentityMatch(customer, sanitizedIdentity)
          ) || null;
      } catch (error) {
        if (options.generalSaveDiagnostics) {
          devError("GENERAL_SAVE_CUSTOMER_LOOKUP_FAILED", {
            step: "customer_lookup",
            lookupText,
            errorCode: error?.code || "",
            errorMessage: error?.message || String(error),
          });
        }
      }
    }

    const customerId =
      existingCustomer?.id || options.customerId || createClientId("customer");
    const customerKey =
      existingCustomer?.customerKey ||
      options.customerKey ||
      customerId;
    const nextCustomer = normalizeCustomerLedgerRecord({
      ...existingCustomer,
      ...sanitizedIdentity,
      id: customerId,
      customerKey,
      customerName:
        sanitizedIdentity.customerName ||
        existingCustomer?.customerName ||
        "Unnamed Customer",
      phone: sanitizedIdentity.phone || existingCustomer?.phone || "",
      whatsapp: sanitizedIdentity.whatsapp || existingCustomer?.whatsapp || "",
      email: sanitizedIdentity.email || existingCustomer?.email || "",
      address: sanitizedIdentity.address || existingCustomer?.address || "",
      companyName: sanitizedIdentity.companyName || existingCustomer?.companyName || "",
      source: sanitizedIdentity.source || existingCustomer?.source || "",
      tags:
        sanitizedIdentity.tags.length > 0
          ? sanitizedIdentity.tags
          : existingCustomer?.tags || [],
    });
    const totals = buildCustomerLedgerTotals(
      customerId,
      options.extraQuote
        ? { ...options.extraQuote, customerId }
        : null,
      options.extraLead
        ? { ...options.extraLead, customerId }
        : null
    );
    const existingTotals = existingCustomer?.totals || {};
    const mergedTotals = {
      ...totals,
      totalQuotes: Math.max(
        Number(existingTotals.totalQuotes || 0),
        Number(existingCustomer?.quoteCount || 0),
        Number(totals.totalQuotes || 0)
      ),
      totalQuoteValue: Math.max(
        Number(existingTotals.totalQuoteValue || 0),
        Number(existingCustomer?.totalQuotedValue || 0),
        Number(totals.totalQuoteValue || 0)
      ),
      confirmedQuotes: Math.max(
        Number(existingTotals.confirmedQuotes || 0),
        Number(existingCustomer?.confirmedCount || 0),
        Number(totals.confirmedQuotes || 0)
      ),
      confirmedValue: Math.max(
        Number(existingTotals.confirmedValue || 0),
        Number(existingCustomer?.confirmedValue || 0),
        Number(totals.confirmedValue || 0)
      ),
      completedJobs: Math.max(
        Number(existingTotals.completedJobs || 0),
        Number(existingCustomer?.completedJobsCount || 0),
        Number(totals.completedJobs || 0)
      ),
      openDealCount: Math.max(
        Number(existingTotals.openDealCount || 0),
        Number(existingCustomer?.openDealCount || 0),
        Number(totals.openDealCount || 0)
      ),
      activeDealValue: Math.max(
        Number(existingTotals.activeDealValue || 0),
        Number(existingCustomer?.activeDealValue || 0),
        Number(totals.activeDealValue || 0)
      ),
      pendingFollowupCount: Math.max(
        Number(existingTotals.pendingFollowupCount || 0),
        Number(existingCustomer?.pendingFollowupCount || 0),
        Number(totals.pendingFollowupCount || 0)
      ),
      upcomingInstallationCount: Math.max(
        Number(existingTotals.upcomingInstallationCount || 0),
        Number(existingCustomer?.upcomingInstallationCount || 0),
        Number(totals.upcomingInstallationCount || 0)
      ),
      receivedAmount: Math.max(
        Number(existingTotals.receivedAmount || 0),
        Number(totals.receivedAmount || 0)
      ),
    };
    mergedTotals.pendingAmount = Math.max(
      0,
      Number(mergedTotals.totalQuoteValue || 0) - Number(mergedTotals.receivedAmount || 0)
    );
    const customerSummaryFields = {
      quoteCount: Number(mergedTotals.totalQuotes || 0),
      totalQuotedValue: Number(mergedTotals.totalQuoteValue || 0),
      confirmedCount: Number(mergedTotals.confirmedQuotes || 0),
      confirmedValue: Number(mergedTotals.confirmedValue || 0),
      openDealCount: Number(mergedTotals.openDealCount || 0),
      activeDealValue: Number(mergedTotals.activeDealValue || 0),
      pendingFollowupCount: Number(mergedTotals.pendingFollowupCount || 0),
      upcomingInstallationCount: Number(mergedTotals.upcomingInstallationCount || 0),
      lastQuoteId: options.extraQuote?.id || existingCustomer?.lastQuoteId || "",
      lastQuoteNo: options.extraQuote?.quoteNo || existingCustomer?.lastQuoteNo || "",
      lastDealId: options.extraLead?.id || existingCustomer?.lastDealId || "",
      lastDealStage:
        options.extraLead?.stageNameSnapshot ||
        options.extraLead?.stageId ||
        existingCustomer?.lastDealStage ||
        "",
    };
    const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
    const searchIndexFields = buildSafeCustomerSearchIndex(nextCustomer);
    const customerPayload = sanitizeCrmFirestoreValue({
      id: customerId,
      customerKey,
      customerName: nextCustomer.customerName,
      normalizedName: nextCustomer.normalizedName,
      nameLower: nextCustomer.nameLower,
      customerNameLower: nextCustomer.customerNameLower,
      phone: nextCustomer.phone,
      whatsapp: nextCustomer.whatsapp,
      email: nextCustomer.email,
      normalizedPhone: nextCustomer.normalizedPhone,
      normalizedWhatsapp: nextCustomer.normalizedWhatsapp,
      normalizedEmail: nextCustomer.normalizedEmail,
      address: nextCustomer.address,
      companyName: nextCustomer.companyName,
      source: nextCustomer.source,
      tags: nextCustomer.tags,
      notes: nextCustomer.notes,
      ...searchIndexFields,
      searchPrefixes: searchIndexFields.searchPrefixes,
      phonePrefixes: searchIndexFields.phonePrefixes,
      totals: mergedTotals,
      ...customerSummaryFields,
      createdBy:
        existingCustomer?.createdBy ||
        user?.uid ||
        user?.email ||
        "",
    });

    const customerFirestorePayload = {
      ...customerPayload,
      ...(existingCustomer?.id ? {} : { createdAt: serverTimestamp() }),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
    };

    if (options.generalSaveDiagnostics?.runWrite) {
      await options.generalSaveDiagnostics.runWrite({
        step: "crm_customer",
        operation: "setDoc",
        path: customerRef.path,
        payload: customerFirestorePayload,
        merge: true,
        action: () => setDoc(customerRef, customerFirestorePayload, { merge: true }),
      });
    } else {
      await setDoc(customerRef, customerFirestorePayload, { merge: true });
    }

    const localCustomer = {
      ...nextCustomer,
      ...searchIndexFields,
      totals: mergedTotals,
      ...customerSummaryFields,
      createdAt: existingCustomer?.createdAt || new Date(),
      updatedAt: new Date(),
      lastActivityAt: new Date(),
    };
    await upsertCustomerSearchIndexRecord(
      {
        ...localCustomer,
        customerId,
        sourceType: "customer",
      },
      { sourceType: "customer" }
    ).catch((error) => {
      if (isDevRuntime) {
        devError("CUSTOMER SEARCH INDEX CUSTOMER UPSERT FAILED", {
          customerId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    quotationCustomerSearchCacheRef.current.clear();
    setCustomerRecords((prev) => {
      const without = prev.filter((customer) => customer.id !== customerId);
      return [localCustomer, ...without];
    });

    return localCustomer;
  },
  [
    buildCustomerLedgerTotals,
    customerRecords,
    findCustomerLedgerMatch,
    isExactCustomerIdentityMatch,
    searchCustomersForQuotation,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]
);

const findCustomerDuplicateCandidates = useCallback(
  (identity = {}) => {
    const normalizedPhone = normalizeCustomerLedgerPhone(
      identity.phone || identity.whatsapp
    );
    const normalizedWhatsapp = normalizeCustomerLedgerPhone(
      identity.whatsapp || identity.phone
    );
    const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
    const normalizedName = normalizeCustomerLedgerName(identity.customerName);
    const normalizedAddress = normalizeCustomerLedgerName(identity.address);

    return customerRecords
      .map((customer) => {
        const customerPhone = normalizeCustomerLedgerPhone(
          customer.phone || customer.normalizedPhone
        );
        const customerWhatsapp = normalizeCustomerLedgerPhone(
          customer.whatsapp || customer.normalizedWhatsapp
        );
        const customerEmail = normalizeCustomerLedgerEmail(
          customer.email || customer.normalizedEmail
        );
        const customerName = normalizeCustomerLedgerName(
          customer.normalizedName || customer.customerName
        );
        const customerAddress = normalizeCustomerLedgerName(customer.address);

        const exactContact =
          (normalizedPhone &&
            (customerPhone === normalizedPhone ||
              customerWhatsapp === normalizedPhone)) ||
          (normalizedWhatsapp &&
            (customerPhone === normalizedWhatsapp ||
              customerWhatsapp === normalizedWhatsapp)) ||
          (normalizedEmail && customerEmail === normalizedEmail);
        const exactName = normalizedName && customerName === normalizedName;
        const addressHint =
          exactName &&
          normalizedAddress &&
          customerAddress &&
          (customerAddress.includes(normalizedAddress) ||
            normalizedAddress.includes(customerAddress));

        if (!exactContact && !exactName && !addressHint) return null;

        return {
          customer,
          reason: exactContact
            ? "Same contact"
            : addressHint
            ? "Similar name and address"
            : "Same name",
        };
      })
      .filter(Boolean)
      .slice(0, 5);
  },
  [customerRecords]
);

const applyCustomerToQuoteContext = useCallback(
  (context = CURTAIN_QUOTE_TYPE, customer = {}) => {
    if (!customer?.id) return;

    const searchIndexIdPattern = /^(phone|email|lead|quote|name)_/i;
    const customerSearchIndexId = String(
      customer.customerSearchIndexId || customer.personId || ""
    ).trim();
    const resolvedCustomerId =
      customer.customerId ||
      (customerSearchIndexId || searchIndexIdPattern.test(String(customer.id || ""))
        ? ""
        : customer.id);
    const resolvedCustomerKey =
      customer.customerKey ||
      resolvedCustomerId ||
      customerSearchIndexId ||
      customer.id ||
      "";
    const resolvedCrmLeadId = customer.crmLeadId || customer.leadId || "";

    const rawSelectedName = String(customer.customerName || customer.displayName || "").trim();
    const rawSelectedCompany = String(customer.companyName || customer.customerCompanyName || "").trim();
    const selectedCustomerName = rawSelectedCompany || rawSelectedName;
    const selectedContactPersonName =
      rawSelectedCompany &&
      rawSelectedName &&
      normalizeSearchText(rawSelectedCompany) !== normalizeSearchText(rawSelectedName)
        ? rawSelectedName
        : String(customer.contactPersonName || customer.contactName || "").trim();
    const nextLink = {
      customerId: resolvedCustomerId,
      customerKey: resolvedCustomerKey,
      customerSearchIndexId: customerSearchIndexId || customer.id || "",
      crmLeadId: resolvedCrmLeadId,
      leadId: resolvedCrmLeadId,
      customerName: selectedCustomerName,
      contactPersonName: selectedContactPersonName,
      contactPersonPhone: customer.contactPersonPhone || customer.phone || "",
      phone: customer.phone || "",
      whatsapp: customer.whatsapp || "",
      email: customer.email || "",
      address: customer.address || "",
      companyName: rawSelectedCompany,
    };
    const customerSiteSnapshot = normalizeQuoteSiteSnapshot(customer, {
      customerName: selectedCustomerName,
    });
    const currentSiteSnapshot =
      context === GENERAL_QUOTE_TYPE
        ? normalizeQuoteSiteSnapshot(
            {
              ...quoteSiteSelection,
              ...generalQuoteRef.current,
            },
            { customerName: selectedCustomerName }
          )
        : normalizeQuoteSiteSnapshot(quoteSiteSelection, {
            customerName: selectedCustomerName,
          });
    const shouldApplySelectedCustomerSite =
      !hasQuoteSiteSnapshot(currentSiteSnapshot) &&
      hasQuoteSiteSnapshot(customerSiteSnapshot);
    const nextSiteSnapshot = shouldApplySelectedCustomerSite
      ? customerSiteSnapshot
      : normalizeQuoteSiteSnapshot(currentSiteSnapshot, {
          customerName: selectedCustomerName,
        });
    const normalizedCustomer = normalizeCustomerLedgerRecord(customer);

    setCustomerRecords((prev) =>
      mergeCustomersById([normalizedCustomer, ...prev])
    );
    if (resolvedCrmLeadId) {
      setPendingCrmLeadId(resolvedCrmLeadId);
    }

    if (context === GENERAL_QUOTE_TYPE) {
      setGeneralQuote((prev) => ({
        ...prev,
        customerId: resolvedCustomerId,
        customerKey: resolvedCustomerKey,
        customerSearchIndexId: customerSearchIndexId || customer.id || "",
        crmLeadId: resolvedCrmLeadId,
        leadId: resolvedCrmLeadId,
        customerName: selectedCustomerName,
        contactPersonName: selectedContactPersonName,
        contactPersonPhone: customer.contactPersonPhone || customer.phone || "",
        customerPhone: customer.phone || "",
        customerWhatsapp: customer.whatsapp || "",
        customerEmail: customer.email || "",
        customerAddress: customer.address || "",
        customerCompanyName: rawSelectedCompany,
        siteId: nextSiteSnapshot.siteId,
        customerSiteId: nextSiteSnapshot.siteId,
        siteName: nextSiteSnapshot.siteName,
        siteAddress: nextSiteSnapshot.siteAddress,
        siteContactName: nextSiteSnapshot.siteContactName,
        siteContactPhone: nextSiteSnapshot.siteContactPhone,
        siteNotes: nextSiteSnapshot.siteNotes,
        quoteDisplayTitle: buildQuoteDisplayTitleFromParts({
          customerName: selectedCustomerName,
          siteName: nextSiteSnapshot.siteName,
        }),
        projectName: nextSiteSnapshot.siteName || prev.projectName || "",
        customerLedgerLinked: true,
        quoteFor: {
          ...(prev.quoteFor || {}),
          name: selectedCustomerName || prev.quoteFor?.name || "",
          company: rawSelectedCompany || selectedCustomerName || prev.quoteFor?.company || "",
          phone: customer.phone || customer.whatsapp || prev.quoteFor?.phone || "",
          email: customer.email || prev.quoteFor?.email || "",
          address: customer.address || prev.quoteFor?.address || "",
        },
      }));
      setCustomerName(selectedCustomerName);
    } else {
      setCustomerName(selectedCustomerName);
      setQuoteCustomerLink(nextLink);
    }
    setQuoteSiteSelection(nextSiteSnapshot);
    traceQuoteIdentity(
      "selectCustomer",
      {
        ...nextLink,
        ...nextSiteSnapshot,
      },
      {
        customerSearchDisplayName: customer.displayName || customer.customerName || "",
      }
    );

    setCustomerPickerOpenKey("");
  },
  [mergeCustomersById, quoteSiteSelection, setGeneralQuote]
);

const openCreateCustomerFromQuote = useCallback(
  (context = CURTAIN_QUOTE_TYPE, seed = {}) => {
    setCustomerCreateState({
      context,
      draft: {
        customerName: seed.customerName || seed.name || "",
        phone: seed.phone || "",
        whatsapp: seed.whatsapp || "",
        email: seed.email || "",
        address: seed.address || "",
        companyName: seed.companyName || "",
        source: seed.source || "",
        tags: Array.isArray(seed.tags) ? seed.tags.join(", ") : seed.tags || "",
      },
      duplicateMatches: [],
      duplicateWarning: false,
    });
    setCustomerPickerOpenKey("");
  },
  []
);

const openCreateLeadFromQuoteCustomer = useCallback(
  (context = CURTAIN_QUOTE_TYPE, seed = {}) => {
    const quoteType = context === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;
    const nextDraft = createCrmLeadDraftState(quoteType);
    const customerLabel = String(seed.customerName || seed.name || "").trim();
    const seededDraft = {
      ...nextDraft,
      title: customerLabel || nextDraft.title,
      companyOrProject: seed.companyName || nextDraft.companyOrProject || "",
      contact: {
        ...(nextDraft.contact || {}),
        name: customerLabel || nextDraft.contact?.name || "",
        phone: seed.phone || nextDraft.contact?.phone || "",
        whatsapp: seed.whatsapp || nextDraft.contact?.whatsapp || "",
        email: seed.email || nextDraft.contact?.email || "",
        address: seed.address || nextDraft.contact?.address || "",
      },
    };
    setCrmLeadDraft(seededDraft);
    markCrmLeadBaseline(seededDraft, "idle");
    setIsCrmLeadModalOpen(true);
    setCustomerPickerOpenKey("");
  },
  [createCrmLeadDraftState, markCrmLeadBaseline]
);

const createCustomerFromQuote = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid || !customerCreateState?.draft) {
      notify("Could not create customer. Please try again.", "error");
      return;
    }

    const draft = customerCreateState.draft;
    const tags = String(draft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const identity = {
      ...draft,
      tags,
      customerName: String(draft.customerName || "").trim(),
    };

    if (!identity.customerName) {
      notify("Please enter a customer name.", "warning");
      return;
    }

    const duplicateMatches = findCustomerDuplicateCandidates(identity);
    if (!force && duplicateMatches.length > 0) {
      setCustomerCreateState((prev) => ({
        ...prev,
        duplicateMatches,
        duplicateWarning: true,
      }));
      return;
    }

    setIsCreatingCustomerFromQuote(true);
    try {
      const customerId = createClientId("customer");
      const customerKey = customerId;
      const nextCustomer = normalizeCustomerLedgerRecord({
        ...identity,
        id: customerId,
        customerKey,
      });
      const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
      const searchIndexFields = buildSafeCustomerSearchIndex(nextCustomer);
      const totals = {
        totalQuotes: 0,
        totalQuoteValue: 0,
        confirmedQuotes: 0,
        completedJobs: 0,
        pendingAmount: 0,
        receivedAmount: 0,
      };
      const payload = sanitizeCrmFirestoreValue({
        id: customerId,
        customerKey,
        customerName: nextCustomer.customerName,
        normalizedName: nextCustomer.normalizedName,
        nameLower: nextCustomer.nameLower,
        customerNameLower: nextCustomer.customerNameLower,
        phone: nextCustomer.phone,
        whatsapp: nextCustomer.whatsapp,
        email: nextCustomer.email,
        normalizedPhone: nextCustomer.normalizedPhone,
        normalizedWhatsapp: nextCustomer.normalizedWhatsapp,
        normalizedEmail: nextCustomer.normalizedEmail,
        address: nextCustomer.address,
        companyName: nextCustomer.companyName,
        source: nextCustomer.source,
        tags: nextCustomer.tags,
        notes: nextCustomer.notes,
        searchText: searchIndexFields.searchText,
        searchKeywords: searchIndexFields.searchKeywords,
        searchPrefixes: searchIndexFields.searchPrefixes,
        phonePrefixes: searchIndexFields.phonePrefixes,
        totals,
        createdBy: user?.uid || user?.email || "",
      });

      await setDoc(customerRef, {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      const localCustomer = {
        ...nextCustomer,
        ...searchIndexFields,
        totals,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastActivityAt: new Date(),
      };

      await upsertCustomerSearchIndexRecord(
        {
          ...localCustomer,
          customerId,
          sourceType: "customer",
        },
        { sourceType: "customer" }
      ).catch((error) => {
        if (isDevRuntime) {
          devError("CUSTOMER SEARCH INDEX CREATE UPSERT FAILED", {
            customerId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      quotationCustomerSearchCacheRef.current.clear();
      invalidateWorkspaceCollectionCache(workspaceUid, "customers");
      setCustomerRecords((prev) => mergeCustomersById([localCustomer, ...prev]));
      applyCustomerToQuoteContext(customerCreateState.context, localCustomer);
      setCustomerCreateState(null);
      notify("Customer created", "success");
    } catch (error) {
      devError("Customer create failed", error);
      notify("Could not create customer. Please try again.", "error");
    } finally {
      setIsCreatingCustomerFromQuote(false);
    }
  },
  [
    applyCustomerToQuoteContext,
    customerCreateState,
    findCustomerDuplicateCandidates,
    mergeCustomersById,
    notify,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]
);

const repairCustomerSearchIndex = useCallback(async () => {
  if (!workspaceUid || customerSearchRepairState.running) return;

  setCustomerSearchRepairState({
    running: true,
    processed: 0,
    updated: 0,
    error: "",
  });

  try {
    const personMap = new Map();
    let processed = 0;
    let updated = 0;

    const mergeIndexRecord = (record = {}) => {
      const prepared = buildCustomerSearchIndexRecord(record, {
        sourceType: record.sourceType || "customer",
        workspaceUid,
      });
      const personKey = getPersonIdentityKey(prepared);
      if (!personKey || !prepared.displayName) return;

      const existing = personMap.get(personKey);
      if (!existing) {
        personMap.set(personKey, {
          ...prepared,
          id: personKey,
          personId: personKey,
          customerIds: uniqueValues([prepared.customerId]).filter(Boolean),
          crmLeadIds: uniqueValues([prepared.crmLeadId || prepared.leadId]).filter(Boolean),
          quoteIds: uniqueValues([prepared.latestQuoteId || prepared.quoteId]).filter(Boolean),
          sourceTypes: uniqueValues(prepared.sourceTypes || [prepared.sourceType]).filter(Boolean),
        });
        return;
      }

      const sourceTypes = uniqueValues([
        ...(existing.sourceTypes || []),
        ...(prepared.sourceTypes || []),
        prepared.sourceType,
      ]).filter(Boolean);
      const quoteIds = uniqueValues([
        ...(existing.quoteIds || []),
        ...(prepared.quoteIds || []),
        existing.latestQuoteId,
        prepared.latestQuoteId,
        existing.quoteId,
        prepared.quoteId,
      ]).filter(Boolean);
      const preparedQuoteMillis = safeTimeMillis(prepared.latestQuoteDate || prepared.lastActivityAt);
      const existingQuoteMillis = safeTimeMillis(existing.latestQuoteDate || existing.lastActivityAt);
      const usePreparedLatestQuote = preparedQuoteMillis >= existingQuoteMillis;
      const hasCustomer =
        existing.hasCustomer === true ||
        prepared.hasCustomer === true ||
        sourceTypes.includes("customer") ||
        Boolean(existing.customerId || prepared.customerId);
      const hasLead =
        existing.hasLead === true ||
        prepared.hasLead === true ||
        sourceTypes.includes("crm_lead") ||
        Boolean(existing.crmLeadId || prepared.crmLeadId || existing.leadId || prepared.leadId);
      const hasQuotes =
        existing.hasQuotes === true ||
        prepared.hasQuotes === true ||
        sourceTypes.includes("quote_customer") ||
        quoteIds.length > 0;

      personMap.set(personKey, {
        ...existing,
        ...prepared,
        id: personKey,
        personId: personKey,
        displayName: existing.displayName || prepared.displayName,
        customerName: existing.customerName || prepared.customerName || prepared.displayName,
        phone: existing.phone || prepared.phone || "",
        whatsapp: existing.whatsapp || prepared.whatsapp || "",
        email: existing.email || prepared.email || "",
        address: existing.address || prepared.address || "",
        customerId: existing.customerId || prepared.customerId || "",
        customerKey: existing.customerKey || prepared.customerKey || existing.customerId || prepared.customerId || "",
        crmLeadId: existing.crmLeadId || prepared.crmLeadId || "",
        leadId: existing.leadId || prepared.leadId || "",
        customerIds: uniqueValues([...(existing.customerIds || []), prepared.customerId]).filter(Boolean),
        crmLeadIds: uniqueValues([...(existing.crmLeadIds || []), prepared.crmLeadId || prepared.leadId]).filter(Boolean),
        quoteIds,
        quoteCount: Math.max(
          Number(existing.quoteCount || 0),
          Number(prepared.quoteCount || 0),
          quoteIds.length
        ),
        latestQuoteId: usePreparedLatestQuote
          ? prepared.latestQuoteId || prepared.quoteId || existing.latestQuoteId
          : existing.latestQuoteId || prepared.latestQuoteId,
        latestQuoteNo: usePreparedLatestQuote
          ? prepared.latestQuoteNo || prepared.quoteNo || existing.latestQuoteNo
          : existing.latestQuoteNo || prepared.latestQuoteNo,
        latestQuoteDate: usePreparedLatestQuote
          ? prepared.latestQuoteDate || prepared.lastActivityAt || existing.latestQuoteDate
          : existing.latestQuoteDate || prepared.latestQuoteDate,
        latestQuoteStage: usePreparedLatestQuote
          ? prepared.latestQuoteStage || prepared.stageLabel || existing.latestQuoteStage
          : existing.latestQuoteStage || prepared.latestQuoteStage,
        latestQuoteType: usePreparedLatestQuote
          ? prepared.latestQuoteType || prepared.quoteType || existing.latestQuoteType
          : existing.latestQuoteType || prepared.latestQuoteType,
        sourceTypes,
        sourceSummary: { hasCustomer, hasLead, hasQuotes },
        hasCustomer,
        hasLead,
        hasQuotes,
        lastActivityAt:
          safeTimeMillis(prepared.lastActivityAt) > safeTimeMillis(existing.lastActivityAt)
            ? prepared.lastActivityAt
            : existing.lastActivityAt,
      });
    };

    const readSourceCollection = async ({ collectionName, sourceType, mapRecord }) => {
      let cursor = null;
      let hasMore = true;

      while (hasMore) {
        const constraints = [firestoreOrderBy(firestoreDocumentId()), firestoreLimit(100)];
        if (cursor) constraints.push(firestoreStartAfter(cursor));

        const pageQuery = firestoreQuery(
          collection(db, "users", workspaceUid, collectionName),
          ...constraints
        );
        const snap = await getDocsCached(pageQuery, {
          label: `rebuildCustomerSearchIndex.${collectionName}`,
          cacheKey: `users/${workspaceUid}/${collectionName}?rebuildCustomerSearchIndexAfter=${
            cursor?.id || "start"
          }`,
          ttlMs: 0,
        });

        if (snap.empty) {
          hasMore = false;
          break;
        }

        snap.docs.forEach((docSnap) => {
          const raw = { id: docSnap.id, ...(docSnap.data() || {}) };
          const mapped = mapRecord ? mapRecord(raw) : raw;
          if (!mapped) return;
          mergeIndexRecord({
            ...mapped,
            sourceType,
          });
        });

        processed += snap.size;
        cursor = snap.docs[snap.docs.length - 1];
        hasMore = snap.size === 100;
        setCustomerSearchRepairState({
          running: true,
          processed,
          updated,
          error: "",
        });
      }
    };

    await readSourceCollection({
      collectionName: "customers",
      sourceType: "customer",
      mapRecord: (record) => ({
        ...record,
        customerId: record.id,
        customerKey: record.id,
        displayName: record.customerName || record.name || "",
      }),
    });

    await readSourceCollection({
      collectionName: "crm_leads",
      sourceType: "crm_lead",
      mapRecord: (record) => {
        const primaryQuote = getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(record));
        return {
          ...record,
          crmLeadId: record.id,
          leadId: record.id,
          displayName:
            record.customerName ||
            record.contact?.name ||
            record.companyOrProject ||
            record.title ||
            "",
          phone: record.customerPhone || record.contact?.phone || "",
          whatsapp: record.customerWhatsapp || record.contact?.whatsapp || "",
          email: record.customerEmail || record.contact?.email || "",
          address: record.customerAddress || record.contact?.address || "",
          companyName: record.customerCompanyName || record.companyOrProject || "",
          latestQuoteId: primaryQuote?.quoteId || record.quoteId || "",
          latestQuoteNo: primaryQuote?.quoteNo || record.quoteNo || "",
          latestQuoteDate: primaryQuote?.quoteDate || record.lastActivityAt || record.updatedAt || record.createdAt,
          leadStage: record.stageNameSnapshot || record.stageId || "",
        };
      },
    });

    await readSourceCollection({
      collectionName: "quote_index",
      sourceType: "quote_customer",
      mapRecord: (record) =>
        record.deleted === true || record.archived === true || record.visibilityStatus === "bin"
          ? null
          : {
              ...record,
              displayName: record.customerName || record.displayName || "",
              phone: record.customerPhone || record.phone || "",
              whatsapp: record.customerWhatsapp || record.whatsapp || "",
              email: record.customerEmail || record.email || "",
              quoteId: record.quoteId || record.sourceQuoteId || record.id,
              latestQuoteId: record.quoteId || record.sourceQuoteId || record.id,
              latestQuoteNo: record.quoteNo || record.latestQuoteNo || "",
              latestQuoteDate: record.quoteDate || record.date || record.updatedAt || record.createdAt,
              latestQuoteType: record.quoteType || record.quotationType || "",
              latestQuoteStage: record.workStage || record.status || "",
            },
    });

    const finalRecords = Array.from(personMap.values());
    for (let index = 0; index < finalRecords.length; index += 400) {
      const batch = writeBatch(db);
      finalRecords.slice(index, index + 400).forEach((record) => {
        const finalRecord = buildCustomerSearchIndexRecord(
          {
            ...record,
            id: record.personId,
            personId: record.personId,
            sourceType: record.hasCustomer ? "customer" : record.hasLead ? "crm_lead" : "quote_customer",
          },
          {
            workspaceUid,
            sourceType: record.hasCustomer ? "customer" : record.hasLead ? "crm_lead" : "quote_customer",
          }
        );
        batch.set(
          doc(db, "users", workspaceUid, "customer_search_index", record.personId),
          sanitizeCrmFirestoreValue({
            ...record,
            ...finalRecord,
            id: record.personId,
            personId: record.personId,
            sourceTypes: record.sourceTypes || finalRecord.sourceTypes || [],
            sourceSummary: record.sourceSummary || finalRecord.sourceSummary || {},
            hasCustomer: record.hasCustomer === true,
            hasLead: record.hasLead === true,
            hasQuotes: record.hasQuotes === true,
            customerIds: record.customerIds || [],
            crmLeadIds: record.crmLeadIds || [],
            quoteIds: (record.quoteIds || []).slice(0, 100),
            quoteCount: Number(record.quoteCount || 0),
            rebuiltAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
          { merge: true }
        );
      });
      await batch.commit();
      updated += Math.min(400, finalRecords.length - index);
      setCustomerSearchRepairState({
        running: true,
        processed,
        updated,
        error: "",
      });
    }

    quotationCustomerSearchCacheRef.current.clear();
    invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
    setCustomerSearchRepairState({
      running: false,
      processed,
      updated,
      error: "",
    });
    notify(`Customer search index rebuilt (${updated} people indexed).`, "success");
  } catch (error) {
    devError("Customer search index repair failed", error);
    setCustomerSearchRepairState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Repair failed",
    }));
    notify("Could not repair customer search index.", "error");
  }
}, [
  customerSearchRepairState.running,
  notify,
  workspaceUid,
]);

const customerLedgerSourceOptions = useMemo(() => {
  const values = new Set();
  customerRecords.forEach((customer) => {
    if (customer.source) values.add(customer.source);
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [customerRecords]);

const customerLedgerSummaries = useMemo(() => {
  const customerMap = new Map();
  customerRecords.forEach((customer) => {
    customerMap.set(customer.id, {
      ...customer,
      quotes: [],
      leads: [],
      openDealsCount: Number(customer.openDealCount || 0),
      completedJobsCount: Number(customer.completedJobsCount || customer.totals?.completedJobs || 0),
      pendingFollowUpsCount: Number(customer.pendingFollowupCount || 0),
      installationCount: Number(customer.upcomingInstallationCount || 0),
      activeDealValue: Number(customer.activeDealValue || 0),
      duplicateWarning: "",
      leadLocationSearchText: "",
    });
  });

  const getCustomerMatches = (record = {}, type = "quote") => {
    const recordCustomerId = String(record.customerId || record.crm?.customerId || "");
    if (recordCustomerId && customerMap.has(recordCustomerId)) {
      return [customerMap.get(recordCustomerId)];
    }

    const identity =
      type === "quote"
        ? getCustomerIdentityFromQuote(record)
        : {
            customerName:
              record.customerName ||
              record.contact?.name ||
              record.crm?.contact?.name ||
              record.companyOrProject ||
              record.title ||
              "",
            phone:
              record.customerPhone ||
              record.contact?.phone ||
              record.contact?.whatsapp ||
              record.crm?.contact?.phone ||
              "",
            whatsapp: record.contact?.whatsapp || record.crm?.contact?.whatsapp || "",
            email: record.contact?.email || record.crm?.contact?.email || record.email || "",
          };
    const phoneKey = normalizeCustomerLedgerPhone(identity.phone || identity.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(identity.email);
    const nameKey = normalizeCustomerLedgerName(identity.customerName);

    return Array.from(customerMap.values()).filter((customer) => {
      const customerPhoneKey = normalizeCustomerLedgerPhone(customer.phone || customer.whatsapp);
      const customerEmailKey = normalizeCustomerLedgerEmail(customer.email);
      const customerNameKey = normalizeCustomerLedgerName(customer.customerName);
      return (
        (phoneKey && customerPhoneKey && phoneKey === customerPhoneKey) ||
        (emailKey && customerEmailKey && emailKey === customerEmailKey) ||
        (nameKey && customerNameKey && nameKey === customerNameKey)
      );
    });
  };

  savedQuotes.forEach((quote) => {
    if (isDeletedQuoteRecord(quote)) return;
    getCustomerMatches(quote, "quote").forEach((summary) => {
      if (
        !summary.quotes.some(
          (existingQuote) => String(existingQuote.id || "") === String(quote.id || "")
        )
      ) {
        summary.quotes.push(quote);
      }
    });
  });

  Object.entries(customerLedgerQuotesByCustomer || {}).forEach(([customerId, quotes]) => {
    const summary = customerMap.get(customerId);
    if (!summary) return;
    (Array.isArray(quotes) ? quotes : []).forEach((quote) => {
      if (isDeletedQuoteRecord(quote)) return;
      if (
        summary.quotes.some(
          (existingQuote) => String(existingQuote.id || "") === String(quote.id || "")
        )
      ) {
        return;
      }
      summary.quotes.push(quote);
    });
  });

  Object.entries(customerLedgerCrmDealsByCustomer || {}).forEach(([customerId, leads]) => {
    const summary = customerMap.get(customerId);
    if (!summary) return;
    (Array.isArray(leads) ? leads : []).forEach((lead) => {
      if (
        summary.leads.some(
          (existingLead) => String(existingLead.id || "") === String(lead.id || "")
        )
      ) {
        return;
      }
      summary.leads.push(lead);
      const leadLocations = normalizeSiteLocations(lead.siteLocations || []);
      if (leadLocations.length) {
        summary.leadLocationSearchText = [
          summary.leadLocationSearchText,
          buildSiteLocationSearchText(leadLocations),
        ]
          .filter(Boolean)
          .join(" ");
      }
      const stage = getCrmStage(
        crmPipelines,
        lead.pipelineType || lead.quoteType,
        lead.stageId
      );
      if (stage?.marksComplete || String(lead.status || "").toLowerCase() === "completed") {
        summary.completedJobsCount += 1;
      } else if (isOpenCrmDeal(lead)) {
        summary.openDealsCount += 1;
      }
      summary.pendingFollowUpsCount += normalizeCrmFollowUps(lead.followUps).filter(
        (followUp) => followUp.status !== CRM_FOLLOW_UP_STATUS_DONE
      ).length;
      if (lead.installationDate) summary.installationCount += 1;
    });
  });

  const phoneCounts = new Map();
  const emailCounts = new Map();
  const nameCounts = new Map();
  customerRecords.forEach((customer) => {
    const phoneKey = normalizeCustomerLedgerPhone(customer.phone || customer.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(customer.email);
    const nameKey = normalizeCustomerLedgerName(customer.customerName);
    if (phoneKey) phoneCounts.set(phoneKey, (phoneCounts.get(phoneKey) || 0) + 1);
    if (emailKey) emailCounts.set(emailKey, (emailCounts.get(emailKey) || 0) + 1);
    if (nameKey) nameCounts.set(nameKey, (nameCounts.get(nameKey) || 0) + 1);
  });

  return Array.from(customerMap.values()).map((summary) => {
    const totalQuoteValue = Math.max(
      Number(summary.totalQuotedValue || summary.totals?.totalQuoteValue || 0),
      summary.quotes.reduce(
        (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
        0
      )
    );
    const confirmedQuotes = summary.quotes.filter(
      (quote) => String(quote.status || quote.crm?.status || "").toLowerCase() === "confirmed"
    ).length || Number(summary.totals?.confirmedQuotes || 0);
    const phoneKey = normalizeCustomerLedgerPhone(summary.phone || summary.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(summary.email);
    const nameKey = normalizeCustomerLedgerName(summary.customerName);
    const duplicateReasons = [
      phoneKey && phoneCounts.get(phoneKey) > 1 ? "same phone" : "",
      emailKey && emailCounts.get(emailKey) > 1 ? "same email" : "",
      nameKey && nameCounts.get(nameKey) > 1 ? "same name" : "",
    ].filter(Boolean);

    return {
      ...summary,
      totals: {
        ...summary.totals,
        totalQuotes: Math.max(
          summary.quotes.length,
          Number(summary.quoteCount || summary.totals?.totalQuotes || 0)
        ),
        totalQuoteValue,
        confirmedQuotes,
        completedJobs: summary.completedJobsCount,
        activeDealValue: summary.activeDealValue,
        confirmedValue: Number(summary.confirmedValue || summary.totals?.confirmedValue || 0),
        pendingAmount: Math.max(
          0,
          totalQuoteValue - Number(summary.totals?.receivedAmount || 0)
        ),
      },
      lastActivityMillis: Math.max(
        getRecordSortTime(summary, ["lastActivityAt", "latestActivityAt", "updatedAt", "createdAt"]),
        ...summary.quotes.map((quote) => getQuoteListSortTime(quote) || getCreatedMillis(quote) || 0),
        ...summary.leads.map((lead) => getCrmListSortTime(lead))
      ),
      duplicateWarning: duplicateReasons.join(", "),
    };
  });
}, [
  crmPipelines,
  customerLedgerCrmDealsByCustomer,
  customerLedgerQuotesByCustomer,
  customerRecords,
  getCustomerIdentityFromQuote,
  getNormalizedQuoteTotal,
  isOpenCrmDeal,
  savedQuotes,
]);

const filteredCustomerLedgerSummaries = useMemo(() => {
  const queryText = deferredCustomerLedgerSearch.trim().toLowerCase();
  const filtered = customerLedgerSummaries.filter((customer) => {
    if (customerLedgerSourceFilter !== "all" && customer.source !== customerLedgerSourceFilter) {
      return false;
    }
    if (!queryText) return true;
    return buildCustomerLedgerSearchText(customer).includes(queryText);
  });

  return filtered.sort((a, b) => {
    if (customerLedgerSortBy === "value") {
      return Number(b.totals?.totalQuoteValue || 0) - Number(a.totals?.totalQuoteValue || 0);
    }
    if (customerLedgerSortBy === "quotes") {
      return Number(b.totals?.totalQuotes || 0) - Number(a.totals?.totalQuotes || 0);
    }
    if (customerLedgerSortBy === "name") {
      return String(a.customerName || "").localeCompare(String(b.customerName || ""));
    }
    return Number(b.lastActivityMillis || 0) - Number(a.lastActivityMillis || 0);
  });
}, [
  customerLedgerSourceFilter,
  customerLedgerSortBy,
  customerLedgerSummaries,
  deferredCustomerLedgerSearch,
]);

useEffect(() => {
  traceListSort("Customer Ledger", filteredCustomerLedgerSummaries, (customer) =>
    customer.customerName || customer.id || ""
  );
}, [filteredCustomerLedgerSummaries]);

const selectedCustomerLedger =
  customerLedgerSummaries.find((customer) => customer.id === selectedCustomerLedgerId) ||
  filteredCustomerLedgerSummaries[0] ||
  null;

useEffect(() => {
  if (selectedCustomerLedgerId && customerLedgerSummaries.some((customer) => customer.id === selectedCustomerLedgerId)) {
    return;
  }
  setSelectedCustomerLedgerId(filteredCustomerLedgerSummaries[0]?.id || "");
}, [customerLedgerSummaries, filteredCustomerLedgerSummaries, selectedCustomerLedgerId]);

const openCustomerLedger = useCallback(
  (customerId) => {
    if (!customerId) {
      notify("This record is not linked to a customer ledger yet.", "info");
      return;
    }
    setSelectedCustomerLedgerId(customerId);
    setRoutedTab("customerLedger");
  },
  [notify, setRoutedTab]
);

const openCustomerLedgerEditModal = useCallback(
  (customer = selectedCustomerLedger) => {
    if (!customer?.id) {
      notify("Select a customer before editing.", "warning");
      return;
    }
    setCustomerLedgerEditState({
      open: true,
      customerId: customer.id,
      original: normalizeCustomerLedgerRecord(customer),
      draft: createCustomerLedgerEditDraft(customer),
      updateActiveQuotes: false,
      includeClosedQuotes: false,
      saving: false,
      error: "",
    });
  },
  [notify, selectedCustomerLedger]
);

const closeCustomerLedgerEditModal = useCallback(() => {
  setCustomerLedgerEditState(createCustomerLedgerEditState());
}, []);

const updateCustomerLedgerEditDraft = useCallback((field, value) => {
  setCustomerLedgerEditState((prev) => {
    const nextDraft = {
      ...(prev.draft || createCustomerLedgerEditDraft()),
      [field]: value,
    };
    if (field === "ownerUid") {
      const owner = getCrmQuoteOwnerOptions().find(
        (option) => String(option.value || "") === String(value || "")
      );
      nextDraft.ownerName = owner?.label || "";
    }
    return {
      ...prev,
      draft: nextDraft,
      error: "",
    };
  });
}, [getCrmQuoteOwnerOptions]);

const updateActiveCustomerLinkedRecords = useCallback(
  async ({ customerId, customerPatch, includeClosedQuotes = false }) => {
    if (!workspaceUid || !customerId) {
      return { activeQuotesUpdated: 0, crmDealsUpdated: 0, opportunitiesUpdated: 0 };
    }

    const quoteSnap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "quote_index"),
        firestoreWhere("customerId", "==", customerId),
        firestoreLimit(CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT + 1)
      ),
      {
        label: "customerLedgerEdit.linkedQuoteIndexByCustomer",
        cacheKey: `users/${workspaceUid}/quote_index?customerId=${customerId}&customerLedgerEdit=1`,
        ttlMs: 0,
      }
    );
    const dealSnap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "crm_leads"),
        firestoreWhere("customerId", "==", customerId),
        firestoreLimit(CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT + 1)
      ),
      {
        label: "customerLedgerEdit.crmDealsByCustomer",
        cacheKey: `users/${workspaceUid}/crm_leads?customerId=${customerId}&customerLedgerEdit=1`,
        ttlMs: 0,
      }
    );

    const opportunitySnap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "opportunities"),
        firestoreWhere("customerId", "==", customerId),
        firestoreLimit(CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT + 1)
      ),
      {
        label: "customerLedgerEdit.opportunitiesByCustomer",
        cacheKey: `users/${workspaceUid}/opportunities?customerId=${customerId}&customerLedgerEdit=1`,
        ttlMs: 0,
      }
    );

    const batch = writeBatch(db);
    let writeCount = 0;
    const now = serverTimestamp();
    const customerNameLower = normalizeCustomerSearchText(customerPatch.customerName);
    const phoneNormalized = normalizeCustomerLedgerPhone(customerPatch.primaryPhone || customerPatch.phone);
    const emailCanonical = normalizeCustomerLedgerEmail(customerPatch.email);
    const linkedQuoteIds = [];
    const quoteIndexPatchBase = sanitizeCrmFirestoreValue({
      customerId,
      customerKey: customerId,
      customerName: customerPatch.customerName,
      displayName: customerPatch.customerName,
      customerNameLower,
      normalizedName: customerNameLower,
      customerPhone: customerPatch.primaryPhone,
      phone: customerPatch.primaryPhone,
      primaryPhone: customerPatch.primaryPhone,
      normalizedPhone: phoneNormalized,
      phoneNormalized,
      customerEmail: customerPatch.email,
      email: customerPatch.email,
      emailCanonical,
      customerAddress: customerPatch.address,
      address: customerPatch.address,
      location: customerPatch.location,
      customerType: customerPatch.customerType,
      contactPersonName: customerPatch.contactPersonName,
      contactPersonPhone: customerPatch.contactPersonPhone,
      customerSnapshot: {
        id: customerId,
        customerId,
        customerName: customerPatch.customerName,
        phone: customerPatch.primaryPhone,
        email: customerPatch.email,
        address: customerPatch.address,
        customerType: customerPatch.customerType,
        contactPersonName: customerPatch.contactPersonName,
        contactPersonPhone: customerPatch.contactPersonPhone,
      },
      customerMasterUpdatedAt: now,
    });

    const activeQuoteDocs = quoteSnap.docs.filter((docSnap) => {
      const quoteIndex = { id: docSnap.id, ...(docSnap.data() || {}) };
      return includeClosedQuotes || isQuoteVisibleInSavedQuotesDefault(quoteIndex);
    });
    const quoteUpdateLimitReached =
      quoteSnap.size > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT ||
      activeQuoteDocs.length > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT;
    const quoteDocsToUpdate = activeQuoteDocs.slice(0, CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT);

    quoteDocsToUpdate.forEach((docSnap) => {
      const quoteIndex = { id: docSnap.id, ...(docSnap.data() || {}) };
      const quoteId = String(
        quoteIndex.sourceQuoteId || quoteIndex.quoteId || quoteIndex.fullQuoteId || docSnap.id
      ).trim();
      if (!quoteId) return;
      linkedQuoteIds.push(quoteId);
      batch.set(
        doc(db, "users", workspaceUid, "quote_index", docSnap.id),
        quoteIndexPatchBase,
        { merge: true }
      );
      batch.set(
        doc(db, "users", workspaceUid, "quotations", quoteId),
        sanitizeCrmFirestoreValue({
          customerId,
          customerKey: customerId,
          customerName: customerPatch.customerName,
          customerPhone: customerPatch.primaryPhone,
          phone: customerPatch.primaryPhone,
          customerEmail: customerPatch.email,
          email: customerPatch.email,
          customerAddress: customerPatch.address,
          address: customerPatch.address,
          customerType: customerPatch.customerType,
          contactPersonName: customerPatch.contactPersonName,
          contactPersonPhone: customerPatch.contactPersonPhone,
          customerSnapshot: quoteIndexPatchBase.customerSnapshot,
          quoteFor: {
            name: customerPatch.customerName,
            phone: customerPatch.primaryPhone,
            email: customerPatch.email,
            address: customerPatch.address,
          },
          crm: {
            customerId,
            customerKey: customerId,
            customerName: customerPatch.customerName,
            customerPhone: customerPatch.primaryPhone,
            customerEmail: customerPatch.email,
          },
          customerMasterUpdatedAt: now,
        }),
        { merge: true }
      );
      writeCount += 2;
    });

    const dealPatch = sanitizeCrmFirestoreValue({
      customerId,
      customerKey: customerId,
      customerName: customerPatch.customerName,
      customerPhone: customerPatch.primaryPhone,
      customerEmail: customerPatch.email,
      customerAddress: customerPatch.address,
      customerType: customerPatch.customerType,
      contactPersonName: customerPatch.contactPersonName,
      contactPersonPhone: customerPatch.contactPersonPhone,
      contact: {
        name: customerPatch.contactPersonName || customerPatch.customerName,
        phone: customerPatch.contactPersonPhone || customerPatch.primaryPhone,
        email: customerPatch.email,
        address: customerPatch.address,
      },
      updatedAt: now,
    });
    const activeDealDocs = dealSnap.docs.filter((docSnap) => {
      const deal = docSnap.data() || {};
      if (!includeClosedQuotes) {
        const status = String(deal.status || deal.stageId || "").toLowerCase();
        if (["cancelled", "canceled", "lost", "closed_lost"].includes(status)) return false;
      }
      return true;
    });
    const dealUpdateLimitReached =
      dealSnap.size > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT ||
      activeDealDocs.length > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT;
    const dealDocsToUpdate = activeDealDocs.slice(0, CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT);
    const updatedDealIds = [];
    dealDocsToUpdate.forEach((docSnap) => {
      batch.set(doc(db, "users", workspaceUid, "crm_leads", docSnap.id), dealPatch, {
        merge: true,
      });
      updatedDealIds.push(docSnap.id);
      writeCount += 1;
    });

    const opportunityPatch = sanitizeCrmFirestoreValue({
      customerId,
      customerName: customerPatch.customerName,
      contactPersonName: customerPatch.contactPersonName,
      phone: customerPatch.primaryPhone,
      email: customerPatch.email,
      updatedAt: now,
    });
    const activeOpportunityDocs = opportunitySnap.docs.filter((docSnap) => {
      const opportunity = docSnap.data() || {};
      if (!includeClosedQuotes) {
        const status = String(opportunity.status || opportunity.stage || "").toLowerCase();
        if (["cancelled", "canceled", "lost", "closed_lost"].includes(status)) return false;
      }
      return true;
    });
    const opportunityUpdateLimitReached =
      opportunitySnap.size > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT ||
      activeOpportunityDocs.length > CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT;
    const opportunityDocsToUpdate = activeOpportunityDocs.slice(0, CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT);
    const updatedOpportunityIds = [];
    opportunityDocsToUpdate.forEach((docSnap) => {
      batch.set(doc(db, "users", workspaceUid, "opportunities", docSnap.id), opportunityPatch, {
        merge: true,
      });
      updatedOpportunityIds.push(docSnap.id);
      writeCount += 1;
    });

    if (writeCount > 0) await batch.commit();
    if (linkedQuoteIds.length > 0) {
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      setSavedQuotes((prev) =>
        prev.map((quote) =>
          String(quote.customerId || quote.crm?.customerId || "") === customerId ||
          linkedQuoteIds.includes(String(quote.id || quote.quoteId || ""))
            ? { ...quote, ...quoteIndexPatchBase }
            : quote
        )
      );
      setCustomerLedgerQuotesByCustomer((prev) => ({
        ...prev,
        [customerId]: (prev[customerId] || []).map((quote) =>
          linkedQuoteIds.includes(String(quote.id || quote.quoteId || ""))
            ? { ...quote, ...quoteIndexPatchBase }
            : quote
        ),
      }));
    }
    if (updatedDealIds.length > 0) {
      invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
      const updatedDealIdSet = new Set(updatedDealIds);
      setCrmLeadRecords((prev) =>
        prev.map((deal) =>
          updatedDealIdSet.has(String(deal.id || deal.leadId || ""))
            ? { ...deal, ...dealPatch }
            : deal
        )
      );
      setCustomerLedgerCrmDealsByCustomer((prev) => ({
        ...prev,
        [customerId]: (prev[customerId] || []).map((deal) =>
          updatedDealIdSet.has(String(deal.id || deal.leadId || ""))
            ? { ...deal, ...dealPatch }
            : deal
        ),
      }));
    }
    if (updatedOpportunityIds.length > 0) {
      invalidateWorkspaceCollectionCache(workspaceUid, "opportunities");
    }

    return {
      activeQuotesUpdated: linkedQuoteIds.length,
      crmDealsUpdated: updatedDealIds.length,
      opportunitiesUpdated: updatedOpportunityIds.length,
      quoteUpdateLimitReached,
      dealUpdateLimitReached,
      opportunityUpdateLimitReached,
    };
  },
  [workspaceUid]
);

const saveCustomerLedgerEdit = useCallback(async () => {
  if (!workspaceUid || !customerLedgerEditState.open || !customerLedgerEditState.customerId) {
    return;
  }
  const customerId = customerLedgerEditState.customerId;
  const original =
    customerRecords.find((customer) => String(customer.id) === String(customerId)) ||
    customerLedgerEditState.original ||
    {};
  const draft = customerLedgerEditState.draft || createCustomerLedgerEditDraft(original);
  const customerName = String(draft.customerName || "").trim();
  const primaryPhone = String(draft.primaryPhone || "").trim();

  if (!customerName && !primaryPhone) {
    setCustomerLedgerEditState((prev) => ({
      ...prev,
      error: "Enter at least a customer name or phone number.",
    }));
    return;
  }
  if (!isValidCustomerLedgerEmail(draft.email)) {
    setCustomerLedgerEditState((prev) => ({
      ...prev,
      error: "Enter a valid email address or leave it blank.",
    }));
    return;
  }
  if (
    original.customerName &&
    !customerName &&
    !window.confirm("This will clear the existing customer name. Continue?")
  ) {
    return;
  }

  const before = normalizeCustomerLedgerRecord(original);
  const after = buildCustomerLedgerEditPatch(draft, {
    ...before,
    id: customerId,
    customerId,
    customerKey: before.customerKey || customerId,
  });
  const changedFields = getCustomerLedgerChangedFields(before, after);
  const oldName = String(before.customerName || "").trim();
  const newName = String(after.customerName || "").trim();
  const nameChanged =
    oldName &&
    newName &&
    normalizeCustomerLedgerName(oldName) !== normalizeCustomerLedgerName(newName);

  if (changedFields.length === 0 && !customerLedgerEditState.updateActiveQuotes) {
    notify("No customer changes to save.", "info");
    return;
  }

  if (
    nameChanged &&
    customerLedgerEditState.updateActiveQuotes &&
    !window.confirm(
      `You are changing this customer name from ${oldName} to ${newName}. This will update the customer ledger, search results, and active linked quotation/CRM snapshots. Continue?`
    )
  ) {
    return;
  }

  setCustomerLedgerEditState((prev) => ({ ...prev, saving: true, error: "" }));
  const trace = {
    customerId,
    before,
    after,
    changedFields,
    customerDocUpdated: false,
    searchIndexUpdated: false,
    updateActiveQuotesSelected: customerLedgerEditState.updateActiveQuotes,
    activeQuotesUpdated: 0,
    crmDealsUpdated: 0,
    opportunitiesUpdated: 0,
    quoteUpdateLimitReached: false,
    dealUpdateLimitReached: false,
    opportunityUpdateLimitReached: false,
    errors: [],
  };

  try {
    const previousSearchWrite = buildCustomerSearchIndexWrite(before, {
      sourceType: "customer",
      workspaceUid,
    });
    const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
    const customerPayload = sanitizeCrmFirestoreValue({
      id: customerId,
      customerId,
      customerKey: after.customerKey || customerId,
      displayName: after.customerName,
      customerName: after.customerName,
      customerType: after.customerType,
      primaryPhone: after.primaryPhone,
      phone: after.primaryPhone,
      phoneNormalized: after.normalizedPhone,
      normalizedPhone: after.normalizedPhone,
      whatsapp: after.whatsapp,
      normalizedWhatsapp: after.normalizedWhatsapp,
      alternatePhones: after.alternatePhones,
      email: after.email,
      emailCanonical: after.normalizedEmail,
      normalizedEmail: after.normalizedEmail,
      address: after.address,
      location: after.location,
      companyName: after.companyName,
      source: after.source,
      tags: after.tags,
      ownerUid: after.ownerUid,
      ownerName: after.ownerName,
      contactPersonName: after.contactPersonName,
      contactPersonPhone: after.contactPersonPhone,
      notes: after.notes,
      searchText: after.searchText,
      searchKeywords: after.searchKeywords,
      searchTokens: after.searchKeywords,
      searchPrefixes: after.searchPrefixes,
      phonePrefixes: after.phonePrefixes,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      updatedBy: user?.uid || user?.email || "",
    });

    await setDoc(customerRef, customerPayload, { merge: true });
    trace.customerDocUpdated = true;

    const localCustomer = normalizeCustomerLedgerRecord({
      ...before,
      ...after,
      id: customerId,
      customerId,
      customerKey: after.customerKey || customerId,
      updatedAt: new Date(),
      lastActivityAt: new Date(),
    });
    const nextSearchRecord = await upsertCustomerSearchIndexRecord(
      {
        ...localCustomer,
        customerId,
        customerKey: after.customerKey || customerId,
        sourceType: "customer",
      },
      { sourceType: "customer" }
    );
    trace.searchIndexUpdated = Boolean(nextSearchRecord);

    if (
      previousSearchWrite?.personId &&
      nextSearchRecord?.personId &&
      previousSearchWrite.personId !== nextSearchRecord.personId
    ) {
      await setDoc(
        doc(db, "users", workspaceUid, "customer_search_index", previousSearchWrite.personId),
        sanitizeCrmFirestoreValue({
          active: false,
          deleted: true,
          replacedBy: nextSearchRecord.personId,
          replacedByCustomerId: customerId,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
    }

    if (customerLedgerEditState.updateActiveQuotes) {
      const linkedUpdateResult = await updateActiveCustomerLinkedRecords({
        customerId,
        customerPatch: localCustomer,
        includeClosedQuotes: customerLedgerEditState.includeClosedQuotes,
      });
      trace.activeQuotesUpdated = linkedUpdateResult.activeQuotesUpdated || 0;
      trace.crmDealsUpdated = linkedUpdateResult.crmDealsUpdated || 0;
      trace.opportunitiesUpdated = linkedUpdateResult.opportunitiesUpdated || 0;
      trace.quoteUpdateLimitReached = Boolean(linkedUpdateResult.quoteUpdateLimitReached);
      trace.dealUpdateLimitReached = Boolean(linkedUpdateResult.dealUpdateLimitReached);
      trace.opportunityUpdateLimitReached = Boolean(linkedUpdateResult.opportunityUpdateLimitReached);
    }

    await logCrmActivity({
      relatedType: "customer",
      relatedId: customerId,
      customerId,
      customerName: localCustomer.customerName,
      activityType: "note",
      title: "Customer details updated",
      description: "Customer details updated.",
    }).catch((error) => {
      trace.errors.push({
        step: "activity_log",
        code: error?.code || "",
        message: error?.message || String(error),
      });
    });

    setCustomerRecords((prev) => mergeCustomersById([localCustomer, ...prev]));
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
    quotationCustomerSearchCacheRef.current.clear();
    devLog("CUSTOMER_LEDGER_EDIT_TRACE", trace);
    if (trace.quoteUpdateLimitReached) {
      notify(CUSTOMER_LEDGER_ACTIVE_UPDATE_LIMIT_MESSAGE, "warning", 8000);
    } else {
      notify("Customer details updated.", "success");
    }
    closeCustomerLedgerEditModal();
  } catch (error) {
    trace.errors.push({
      step: "save_customer",
      code: error?.code || "",
      message: error?.message || String(error),
    });
    devError("CUSTOMER_LEDGER_EDIT_TRACE", trace);
    setCustomerLedgerEditState((prev) => ({
      ...prev,
      saving: false,
      error: error?.message || "Could not save customer details.",
    }));
    notify(error?.message || "Could not save customer details.", "error");
  }
}, [
  closeCustomerLedgerEditModal,
  customerLedgerEditState,
  customerRecords,
  logCrmActivity,
  mergeCustomersById,
  notify,
  updateActiveCustomerLinkedRecords,
  upsertCustomerSearchIndexRecord,
  user?.email,
  user?.uid,
  workspaceUid,
]);

const mergeQuoteCustomerSiteCache = useCallback((customerId, sites = []) => {
  const normalizedCustomerId = String(customerId || "").trim();
  if (!normalizedCustomerId) return;
  setCustomerSitesByCustomerId((prev) => ({
    ...prev,
    [normalizedCustomerId]: Array.from(
      [
        ...(Array.isArray(sites) ? sites : []),
        ...(prev[normalizedCustomerId] || []),
      ]
        .filter(Boolean)
        .reduce((map, site) => {
          const key = String(site.siteId || site.id || "").trim();
          if (!key) return map;
          map.set(key, { ...(map.get(key) || {}), ...site });
          return map;
        }, new Map())
        .values()
    ).sort((a, b) =>
      String(a.siteName || "").localeCompare(String(b.siteName || ""))
    ),
  }));
}, []);

const normalizeCustomerSiteDocForQuote = useCallback((siteId = "", data = {}) => {
  const snapshot = normalizeQuoteSiteSnapshot({
    ...data,
    siteId: data.siteId || data.id || siteId,
    siteName: data.siteName || data.label || data.name || "",
    siteAddress: data.siteAddress || data.addressText || data.address || "",
    siteContactName: data.siteContactName || data.contactName || "",
    siteContactPhone: data.siteContactPhone || data.contactPhone || "",
    siteNotes: data.siteNotes || data.notes || "",
  });
  return {
    ...snapshot,
    id: snapshot.siteId || siteId,
    siteId: snapshot.siteId || siteId,
    active: data.active !== false && data.deleted !== true,
    updatedAt: data.updatedAt || data.lastActivityAt || null,
    createdAt: data.createdAt || null,
  };
}, []);

const loadCustomerSitesForQuote = useCallback(
  async (customerId, { force = false } = {}) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId) return [];
    if (!force && Array.isArray(customerSitesByCustomerId[normalizedCustomerId])) {
      return customerSitesByCustomerId[normalizedCustomerId];
    }
    if (customerSitesLoadRef.current[normalizedCustomerId]) {
      return customerSitesLoadRef.current[normalizedCustomerId];
    }

    const sitesPath = `users/${workspaceUid}/customers/${normalizedCustomerId}/sites`;
    const sitesQuery = firestoreQuery(
      collection(db, "users", workspaceUid, "customers", normalizedCustomerId, "sites"),
      firestoreLimit(50)
    );
    const loadPromise = getDocsCached(sitesQuery, {
      label: "quoteCustomerSites",
      cacheKey: `${sitesPath}:first50`,
      ttlMs: 5 * 60 * 1000,
    })
      .then((snap) => {
        const sites = snap.docs
          .map((siteDoc) =>
            normalizeCustomerSiteDocForQuote(siteDoc.id, siteDoc.data() || {})
          )
          .filter((site) => site.active !== false && hasQuoteSiteSnapshot(site))
          .sort((a, b) =>
            String(a.siteName || "").localeCompare(String(b.siteName || ""))
          );
        setCustomerSitesByCustomerId((prev) => ({
          ...prev,
          [normalizedCustomerId]: sites,
        }));
        return sites;
      })
      .catch((error) => {
        devError("QUOTE_CUSTOMER_SITES_LOAD_FAILED", {
          code: error?.code || "",
          message: error?.message || String(error),
          path: sitesPath,
        });
        return [];
      })
      .finally(() => {
        delete customerSitesLoadRef.current[normalizedCustomerId];
        setCustomerSitesLoadingId((current) =>
          current === normalizedCustomerId ? "" : current
        );
      });

    customerSitesLoadRef.current[normalizedCustomerId] = loadPromise;
    setCustomerSitesLoadingId(normalizedCustomerId);
    return loadPromise;
  },
  [customerSitesByCustomerId, normalizeCustomerSiteDocForQuote, workspaceUid]
);

const updateQuoteSiteSelectionForContext = useCallback(
  (
    context = CURTAIN_QUOTE_TYPE,
    sitePatch = {},
    { replace = false, preserveWhitespace = false } = {}
  ) => {
    const merged = replace ? sitePatch : { ...quoteSiteSelection, ...sitePatch };
    const next = normalizeQuoteSiteSnapshot(
      merged,
      {
        customerName:
          context === GENERAL_QUOTE_TYPE
            ? generalQuoteRef.current?.quoteFor?.name ||
              generalQuoteRef.current?.customerName ||
              ""
            : customerName,
      },
      { preserveWhitespace }
    );
    setQuoteSiteSelection(next);
    if (context === GENERAL_QUOTE_TYPE) {
      setGeneralQuoteLive((previous) => ({
        ...previous,
        siteId: next.siteId,
        customerSiteId: next.siteId,
        siteName: next.siteName,
        siteAddress: next.siteAddress,
        siteContactName: next.siteContactName,
        siteContactPhone: next.siteContactPhone,
        siteNotes: next.siteNotes,
        quoteDisplayTitle: next.quoteDisplayTitle,
        projectName: next.siteName || previous.projectName || "",
      }));
    }
  },
  [customerName, quoteSiteSelection, setGeneralQuoteLive]
);

const persistQuoteSiteForCustomer = useCallback(
  async (customerId, customerFields = {}, siteLike = {}) => {
    const normalizedCustomerId = String(customerId || customerFields.customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId) return null;
    const siteFields = buildQuoteSiteFieldsForSave(siteLike, {
      customerId: normalizedCustomerId,
      customerName: customerFields.customerName || customerFields.name || "",
      ensureSiteId: true,
    });
    if (!hasQuoteSiteSnapshot(siteFields) || !siteFields.siteId) return siteFields;

    const siteSearchIndex = buildSafeSearchResult([
      siteFields.siteName,
      siteFields.siteAddress,
      siteFields.siteContactName,
      siteFields.siteContactPhone,
      siteFields.siteNotes,
      customerFields.customerName,
      customerFields.customerPhone,
      customerFields.customerWhatsapp,
      customerFields.customerEmail,
    ]);
    const sitePayload = sanitizeCrmFirestoreValue({
      id: siteFields.siteId,
      siteId: siteFields.siteId,
      customerId: normalizedCustomerId,
      customerName: customerFields.customerName || "",
      customerPhone: customerFields.customerPhone || customerFields.phone || "",
      customerWhatsapp: customerFields.customerWhatsapp || customerFields.whatsapp || "",
      customerEmail: customerFields.customerEmail || customerFields.email || "",
      siteName: siteFields.siteName,
      siteAddress: siteFields.siteAddress,
      siteContactName: siteFields.siteContactName,
      siteContactPhone: siteFields.siteContactPhone,
      siteNotes: siteFields.siteNotes,
      label: siteFields.siteName,
      addressText: siteFields.siteAddress,
      contactName: siteFields.siteContactName,
      contactPhone: siteFields.siteContactPhone,
      notes: siteFields.siteNotes,
      active: true,
      searchText: siteSearchIndex.searchText,
      searchKeywords: siteSearchIndex.searchKeywords,
      searchTokens: siteSearchIndex.searchKeywords,
      updatedAt: serverTimestamp(),
      updatedBy: user?.uid || user?.email || "",
    });
    const siteRef = doc(
      db,
      "users",
      workspaceUid,
      "customers",
      normalizedCustomerId,
      "sites",
      siteFields.siteId
    );

    await setDoc(siteRef, sitePayload, { merge: true });
    mergeQuoteCustomerSiteCache(normalizedCustomerId, [
      normalizeCustomerSiteDocForQuote(siteFields.siteId, sitePayload),
    ]);
    invalidateFirestoreReadCacheByPrefix(
      `users/${workspaceUid}/customers/${normalizedCustomerId}/sites`
    );
    return siteFields;
  },
  [
    mergeQuoteCustomerSiteCache,
    normalizeCustomerSiteDocForQuote,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

useEffect(() => {
  if (tab !== "customerLedger" || !selectedCustomerLedger?.id) return;
  loadCustomerSitesForQuote(selectedCustomerLedger.id);
}, [loadCustomerSitesForQuote, selectedCustomerLedger?.id, tab]);

const saveCustomerSiteLocations = useCallback(
  async (customerId, nextLocations = []) => {
    if (!workspaceUid || !customerId) {
      notify("Select a customer before saving a location.", "warning");
      return false;
    }

    const normalizedLocations = normalizeSiteLocations(nextLocations).map(
      (location) => ({
        ...location,
        updatedAt: location.updatedAt || new Date().toISOString(),
        updatedBy: location.updatedBy || user?.uid || user?.email || "",
      })
    );

    try {
      await setDoc(
        doc(db, "users", workspaceUid, "customers", customerId),
        {
          siteLocations: sanitizeCrmFirestoreValue(normalizedLocations),
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          updatedBy: user?.uid || user?.email || "",
        },
        { merge: true }
      );

      if (normalizedLocations.length > 0) {
        const sitesBatch = writeBatch(db);
        let hasSiteWrites = false;
        normalizedLocations.forEach((location) => {
          const siteSnapshot = buildQuoteSiteFieldsForSave(
            {
              siteId: location.id,
              siteName: location.label,
              siteAddress: location.addressText,
              siteNotes: location.notes,
            },
            {
              customerId,
              customerName:
                selectedCustomerLedger?.customerName ||
                customerRecords.find((customer) => customer.id === customerId)
                  ?.customerName ||
                "",
              ensureSiteId: true,
            }
          );
          if (!hasQuoteSiteSnapshot(siteSnapshot) || !siteSnapshot.siteId) return;
          const siteSearchIndex = buildSafeSearchResult([
            siteSnapshot.siteName,
            siteSnapshot.siteAddress,
            location.siteContactName,
            location.siteContactPhone,
            siteSnapshot.siteNotes,
          ]);
          sitesBatch.set(
            doc(db, "users", workspaceUid, "customers", customerId, "sites", siteSnapshot.siteId),
            sanitizeCrmFirestoreValue({
              id: siteSnapshot.siteId,
              siteId: siteSnapshot.siteId,
              customerId,
              siteName: siteSnapshot.siteName,
              siteAddress: siteSnapshot.siteAddress,
              siteContactName: location.siteContactName || "",
              siteContactPhone: location.siteContactPhone || "",
              siteNotes: siteSnapshot.siteNotes,
              label: siteSnapshot.siteName,
              addressText: siteSnapshot.siteAddress,
              contactName: location.siteContactName || "",
              contactPhone: location.siteContactPhone || "",
              notes: siteSnapshot.siteNotes,
              active: true,
              searchText: siteSearchIndex.searchText,
              searchKeywords: siteSearchIndex.searchKeywords,
              searchTokens: siteSearchIndex.searchKeywords,
              updatedAt: serverTimestamp(),
              updatedBy: user?.uid || user?.email || "",
            }),
            { merge: true }
          );
          hasSiteWrites = true;
        });
        if (hasSiteWrites) {
          await sitesBatch.commit();
          invalidateFirestoreReadCacheByPrefix(
            `users/${workspaceUid}/customers/${customerId}/sites`
          );
        }
      }

      setCustomerRecords((prev) =>
        prev.map((customer) =>
          customer.id === customerId
            ? {
                ...customer,
                siteLocations: normalizedLocations,
                updatedAt: new Date(),
                lastActivityAt: new Date(),
              }
            : customer
        )
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "customers");
      return true;
    } catch (error) {
      devError("Failed to save customer site location:", error, {
        customerId,
      });
      notify("Could not save site location. Please try again.", "error");
      return false;
    }
  },
  [customerRecords, notify, selectedCustomerLedger, user, workspaceUid]
);

const backfillCustomerLedgers = useCallback(async () => {
  if (!workspaceUid) return;

  const quotesToLink = savedQuotes.filter((quote) => !quote.customerId && !isDeletedQuoteRecord(quote));
  const leadsToLink = crmLeadRecords.filter((lead) => !lead.customerId);
  const totalRecords = quotesToLink.length + leadsToLink.length;

  if (totalRecords === 0) {
    notify("All visible records are already linked to customer ledgers.", "success");
    return;
  }

  openConfirmDialog({
    title: "Repair quote customer links?",
    description: `${totalRecords} visible quote or lead record${totalRecords === 1 ? "" : "s"} will be linked to non-destructive customer ledger records. Existing quotes and leads will not be deleted.`,
    confirmLabel: "Repair Links",
    cancelLabel: "Cancel",
    tone: "info",
    onConfirm: async () => {
      try {
        for (const quote of quotesToLink) {
          const customer = await ensureCustomerLedgerRecord(getCustomerIdentityFromQuote(quote), {
            extraQuote: quote,
          });
          if (!customer?.id) continue;
          await updateDoc(doc(db, "users", workspaceUid, "quotations", quote.id), {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            customerLedgerLinked: true,
            "crm.customerId": customer.id,
            "crm.customerKey": customer.customerKey || customer.id,
            "crm.customerName": customer.customerName,
            "crm.customerPhone": customer.phone || customer.whatsapp || "",
            updatedAt: serverTimestamp(),
          });
          applyQuoteLocalPatch(quote.id, {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            customerLedgerLinked: true,
            crm: {
              ...(quote.crm || {}),
              customerId: customer.id,
              customerKey: customer.customerKey || customer.id,
              customerName: customer.customerName,
              customerPhone: customer.phone || customer.whatsapp || "",
            },
            updatedAt: new Date(),
          });
        }

        for (const lead of leadsToLink) {
          const customer = await ensureCustomerLedgerRecord(
            getCustomerIdentityFromLeadDraft({
              title: lead.title,
              companyOrProject: lead.companyOrProject,
              contact: lead.contact || lead.crmContact || {},
              source: lead.source,
              tags: Array.isArray(lead.tags) ? lead.tags.join(", ") : "",
            }),
            { extraLead: lead }
          );
          if (!customer?.id) continue;
          await updateDoc(doc(db, "users", workspaceUid, "crm_leads", lead.id), {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "customerLedgerBackfill",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          });
          applyCrmLeadLocalPatch(lead.id, {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            updatedAt: new Date(),
          });
        }

        notify("Quote customer links repaired", "success");
      } catch (error) {
        devError("Quote customer link repair failed:", error);
        notify("Could not repair quote customer links. Please try again.", "error");
      }
    },
  });
}, [
  applyCrmLeadLocalPatch,
  applyQuoteLocalPatch,
  crmLeadRecords,
  ensureCustomerLedgerRecord,
  getCustomerIdentityFromLeadDraft,
  getCustomerIdentityFromQuote,
  notify,
  openConfirmDialog,
  savedQuotes,
  workspaceUid,
]);

const addCustomerLedgerPayment = useCallback(async () => {
  if (!workspaceUid || !selectedCustomerLedger?.id) return;
  const amount = Number(customerLedgerPaymentDraft.amount || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    notify("Please enter a valid payment amount.", "warning");
    return;
  }

  setIsSavingCustomerPayment(true);
  try {
    const entryId = createClientId("ledger-entry");
    const entryRef = doc(
      db,
      "users",
      workspaceUid,
      "customers",
      selectedCustomerLedger.id,
      "ledger_entries",
      entryId
    );
    const entryPayload = sanitizeCrmFirestoreValue({
      id: entryId,
      type: "payment",
      title: "Payment received",
      amount,
      date: customerLedgerPaymentDraft.date || todayISO(),
      notes: customerLedgerPaymentDraft.notes || "",
      createdBy: user?.uid || user?.email || "",
    });
    const receivedAmount =
      Number(selectedCustomerLedger.totals?.receivedAmount || 0) + amount;
    const pendingAmount = Math.max(
      0,
      Number(selectedCustomerLedger.totals?.totalQuoteValue || 0) - receivedAmount
    );

    await setDoc(entryRef, {
      ...entryPayload,
      createdAt: serverTimestamp(),
    });
    await setDoc(
      doc(db, "users", workspaceUid, "customers", selectedCustomerLedger.id),
      {
        totals: {
          ...(selectedCustomerLedger.totals || {}),
          receivedAmount,
          pendingAmount,
        },
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setCustomerRecords((prev) =>
      prev.map((customer) =>
        customer.id === selectedCustomerLedger.id
          ? {
              ...customer,
              totals: {
                ...(customer.totals || {}),
                receivedAmount,
                pendingAmount,
              },
              lastActivityAt: new Date(),
              updatedAt: new Date(),
            }
          : customer
      )
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    setCustomerLedgerPaymentDraft({ amount: "", date: todayISO(), notes: "" });
    notify("Payment added", "success");
  } catch (error) {
    devError("Customer payment save failed:", error);
    notify("Could not add payment. Please try again.", "error");
  } finally {
    setIsSavingCustomerPayment(false);
  }
}, [
  customerLedgerPaymentDraft,
  notify,
  selectedCustomerLedger,
  user,
  workspaceUid,
]);

const visibleCrmLeadDeals = useMemo(
  () => crmFilteredLeadDeals.slice(0, crmLeadsVisibleCount),
  [crmFilteredLeadDeals, crmLeadsVisibleCount]
);

const hasMoreCrmLeadDeals =
  visibleCrmLeadDeals.length < crmFilteredLeadDeals.length;

useEffect(() => {
  setCrmLeadsVisibleCount(CRM_LEADS_PAGE_SIZE);
}, [
  crmLeadAssignedFilter,
  crmLeadFromDate,
  crmLeadSourceFilter,
  crmLeadStageFilter,
  crmLeadToDate,
  deferredCrmLeadSearch,
]);

const crmTaskHistoryCompletedByOptions = useMemo(() => {
  const options = new Map();

  crmTaskHistoryRecords.forEach((record) => {
    const key = String(
      record.completedByUid || record.completedByEmail || record.completedByName || ""
    ).trim();
    if (!key || options.has(key)) return;

    options.set(key, {
      key,
      label:
        record.completedByName ||
        record.completedByEmail ||
        record.completedByUid ||
        "Workspace User",
    });
  });

  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
}, [crmTaskHistoryRecords]);

const crmFilteredTaskHistoryRecords = useMemo(() => {
  const queryText = deferredCrmTaskHistorySearch.trim().toLowerCase();
  const fromMillis = crmTaskHistoryFromDate
    ? safeTimeMillis(parseDateInputValue(crmTaskHistoryFromDate))
    : 0;
  const toMillisValue = crmTaskHistoryToDate
    ? safeTimeMillis(parseDateInputValue(crmTaskHistoryToDate)) + 24 * 60 * 60 * 1000 - 1
    : 0;

  return crmTaskHistoryRecords
    .filter((record) => {
      if (!queryText) return true;

      return [
        record.taskTitle,
        record.taskDescription,
        record.customerName,
        record.quoteNo,
        record.completedByName,
        record.completedByEmail,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .filter((record) => {
      if (crmTaskHistoryTypeFilter === "all") return true;
      return String(record.taskType || "") === crmTaskHistoryTypeFilter;
    })
    .filter((record) => {
      if (crmTaskHistoryCompletedByFilter === "all") return true;
      const actorKey = String(
        record.completedByUid || record.completedByEmail || record.completedByName || ""
      ).trim();
      return actorKey === crmTaskHistoryCompletedByFilter;
    })
    .filter((record) => {
      const completedMillis = safeTimeMillis(record.completedAt);
      if (fromMillis && completedMillis < fromMillis) return false;
      if (toMillisValue && completedMillis > toMillisValue) return false;
      return true;
    });
}, [
  crmTaskHistoryCompletedByFilter,
  crmTaskHistoryFromDate,
  crmTaskHistoryRecords,
  crmTaskHistoryToDate,
  crmTaskHistoryTypeFilter,
  deferredCrmTaskHistorySearch,
]);

const selectedCrmTaskHistoryRecord = useMemo(
  () =>
    crmFilteredTaskHistoryRecords.find((record) => record.id === selectedCrmTaskHistoryId) ||
    null,
  [crmFilteredTaskHistoryRecords, selectedCrmTaskHistoryId]
);

useEffect(() => {
  if (crmFilteredTaskHistoryRecords.length === 0) {
    if (selectedCrmTaskHistoryId) {
      setSelectedCrmTaskHistoryId("");
    }
    return;
  }

  if (
    !selectedCrmTaskHistoryId ||
    !crmFilteredTaskHistoryRecords.some((record) => record.id === selectedCrmTaskHistoryId)
  ) {
    setSelectedCrmTaskHistoryId(crmFilteredTaskHistoryRecords[0].id);
  }
}, [crmFilteredTaskHistoryRecords, selectedCrmTaskHistoryId]);

const crmVisibleDeals = useMemo(() => {
  if (crmView === "board") {
    return crmBoardDeals;
  }

  if (crmView === "leads") {
    return crmFilteredLeadDeals;
  }

  if (crmView === "list") {
    return crmFilteredDeals;
  }

  return crmScopeDeals;
}, [crmBoardDeals, crmFilteredDeals, crmFilteredLeadDeals, crmScopeDeals, crmView]);

const openNewCrmLeadModal = useCallback(
  (quoteType = crmScope === CRM_SCOPE_ALL ? crmPipelineType : crmScope) => {
    const nextDraft = createCrmLeadDraftState(quoteType);
    setCrmLeadDraft(nextDraft);
    markCrmLeadBaseline(nextDraft, "idle");
    setIsCrmLeadModalOpen(true);
  },
  [createCrmLeadDraftState, crmPipelineType, crmScope, markCrmLeadBaseline]
);

const closeNewCrmLeadModal = useCallback(() => {
  persistCrmLeadDraftLocally("route-change");
  setIsCrmLeadModalOpen(false);
  setCrmLeadDraft(null);
}, [persistCrmLeadDraftLocally]);

const saveNewCrmLead = useCallback(async () => {
  if (!crmLeadDraft) {
    return;
  }

  if (!workspaceUid) {
    notify("Please sign in before creating a lead.", "warning");
    return;
  }

  const contactName = String(crmLeadDraft.contact?.name || "").trim();
  const leadTitle = String(crmLeadDraft.title || "").trim();

  if (!contactName && !leadTitle) {
    notify("Please fill required fields.", "warning");
    return;
  }

  const normalizedQuoteType = isGeneralExecutionEligibleDeal({
    pipelineType: crmLeadDraft.pipelineType || crmLeadDraft.quoteType,
    quoteType: crmLeadDraft.quoteType,
  })
    ? GENERAL_QUOTE_TYPE
    : CURTAIN_QUOTE_TYPE;
  const pipeline = getCrmPipeline(crmPipelines, normalizedQuoteType);
  const requestedStageId = String(crmLeadDraft.stageId || "").trim();
  const stageMeta =
    pipeline.stages.find((stage) => stage.id === requestedStageId) ||
    pipeline.stages[0] ||
    null;

  if (!normalizedQuoteType || !stageMeta?.id) {
    notify("Please fill required fields.", "warning");
    return;
  }

  const parsedNextActionDate = crmLeadDraft.nextActionDate
    ? parseDateInputValue(crmLeadDraft.nextActionDate)
    : null;
  if (crmLeadDraft.nextActionDate && !parsedNextActionDate) {
    notify("Please select a valid next action date.", "warning");
    return;
  }

  setIsSavingCrmLead(true);

  let leadPayloadForLog = null;
  let leadCreatePayloadForLog = null;

  try {
    const assignedMember = getTeamMemberById(crmLeadDraft.assignedToUserId);
    const tags = String(crmLeadDraft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const leadId = createClientId("crm-lead");
    const expectedValue = Number(crmLeadDraft.expectedValue || 0);
    const customerLedger = await ensureCustomerLedgerRecord(
      getCustomerIdentityFromLeadDraft({
        ...crmLeadDraft,
        tags: tags.join(", "),
      })
    );
    const leadPayload = buildCrmLeadData(
      {
        id: leadId,
        createdAt: new Date(),
      },
      {
        title: crmLeadDraft.title,
        companyOrProject: crmLeadDraft.companyOrProject,
        contact: crmLeadDraft.contact,
        source: crmLeadDraft.source,
        quoteType: normalizedQuoteType,
        pipelineType: normalizedQuoteType,
        stageId: stageMeta.id,
        customerId: customerLedger?.id || "",
        customerName:
          customerLedger?.customerName ||
          contactName ||
          leadTitle ||
          "",
        customerPhone:
          customerLedger?.phone ||
          customerLedger?.whatsapp ||
          crmLeadDraft.contact?.phone ||
          crmLeadDraft.contact?.whatsapp ||
          "",
        expectedValue: Number.isFinite(expectedValue) ? expectedValue : 0,
        nextAction: crmLeadDraft.nextAction,
        nextActionDate: parsedNextActionDate,
        assignedToUserId: crmLeadDraft.assignedToUserId || "",
        assignedToNameSnapshot: assignedMember?.name || "",
        assignedAt: crmLeadDraft.assignedToUserId ? new Date() : null,
        assignedBy: crmLeadDraft.assignedToUserId
          ? user?.email || user?.uid || ""
          : "",
        notes: crmLeadDraft.notes,
        tags,
        linkedQuotes: [],
        hasQuote: false,
        reminderConfigs: crmLeadDraft.reminderConfigs,
        followUps: [],
        attachments: crmLeadDraft.attachments,
        executionSpecs: crmLeadDraft.executionSpecs,
        siteLocations: crmLeadDraft.siteLocations,
      }
    );
    leadPayloadForLog = leadPayload;
    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const searchIndexFields = buildCrmSearchIndexFields(leadPayload);
    const leadCreatePayload = sanitizeCrmFirestoreValue({
      title: leadPayload.title,
      contact: leadPayload.contact,
      companyOrProject: leadPayload.companyOrProject,
      source: leadPayload.source,
      quoteType: leadPayload.quoteType,
      pipelineType: leadPayload.pipelineType,
      stageId: leadPayload.stageId,
      stageNameSnapshot: leadPayload.stageNameSnapshot,
      customerId: leadPayload.customerId || "",
      customerName: leadPayload.customerName || leadPayload.contact?.name || "",
      customerPhone:
        leadPayload.customerPhone ||
        leadPayload.contact?.phone ||
        leadPayload.contact?.whatsapp ||
        "",
      linkedQuotes: [],
      hasQuote: false,
      checklistTemplateId: leadPayload.checklistTemplateId || "",
      checklistItems: normalizeChecklistTree(leadPayload.checklistItems || []),
      expectedValue: Number(leadPayload.expectedValue || 0),
      finalValue: Number(leadPayload.finalValue || 0),
      nextAction: leadPayload.nextAction || "",
      nextActionDate: leadPayload.nextActionDate || null,
      assignedToUserId: leadPayload.assignedToUserId || "",
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot || "",
      assignedAt: leadPayload.assignedAt || null,
      assignedBy: leadPayload.assignedBy || "",
      tags: leadPayload.tags || [],
      notes: leadPayload.notes || "",
      installationDate: null,
      reminderConfigs: leadPayload.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      followUps: leadPayload.followUps || [],
      attachments: leadPayload.attachments || [],
      executionSpecs: leadPayload.executionSpecs || [],
      siteLocations: leadPayload.siteLocations || [],
      ...searchIndexFields,
      status: leadPayload.status || "draft",
    });
    leadCreatePayloadForLog = leadCreatePayload;

    await setDoc(leadRef, {
      ...leadCreatePayload,
      ...(isDevRuntime
        ? {
            _lastWriteSource: "createCrmLead",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await upsertCustomerSearchIndexRecord(
      {
        ...leadPayload,
        ...leadCreatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        devError("CUSTOMER SEARCH INDEX NEW LEAD UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    setCrmLeadRecords((prev) => [
      {
        ...leadPayload,
        ...searchIndexFields,
        id: leadId,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      ...prev,
    ]);

    let reminderSyncFailed = false;
    await syncReminderDocsForDeal({
      ...leadPayload,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        leadPayload.contact?.name ||
        leadPayload.companyOrProject ||
        leadPayload.title ||
        "Unnamed Lead",
      crmTitle: leadPayload.title,
      crmContact: leadPayload.contact,
      nextActionDate: leadPayload.nextActionDate,
      installationDate: null,
      reminderConfigs: leadPayload.reminderConfigs,
      attachments: leadPayload.attachments,
      siteLocations: leadPayload.siteLocations,
      pipelineType: leadPayload.pipelineType,
    }).catch((error) => {
      reminderSyncFailed = true;
      devWarn("CRM lead created, but reminder sync failed:", error);
    });

    await ensureOpportunityForLead({
      ...leadPayload,
      ...leadCreatePayload,
      id: leadId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }).catch((error) => {
      if (isDevRuntime) {
        devWarn("CRM lead created, but opportunity sync failed", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });

    setSelectedCrmDealId(leadId);
    if (crmView === "overview") {
      setCrmView("list");
    }
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    closeNewCrmLeadModal();
    clearCrmLeadDraft();
    notify(
      reminderSyncFailed
        ? "Lead created, but reminders could not be scheduled."
        : "Lead created",
      reminderSyncFailed ? "warning" : "success"
    );
  } catch (error) {
    devError("Create lead failed", error, {
      workspaceUid,
      leadPayload: leadCreatePayloadForLog || leadPayloadForLog,
    });
    notify("Could not create lead. Please try again.", "error");
  } finally {
    setIsSavingCrmLead(false);
  }
}, [
  buildCrmLeadData,
  clearCrmLeadDraft,
  closeNewCrmLeadModal,
  crmLeadDraft,
  crmPipelines,
  crmView,
  ensureCustomerLedgerRecord,
  ensureOpportunityForLead,
  getCustomerIdentityFromLeadDraft,
  getTeamMemberById,
  notify,
  syncReminderDocsForDeal,
  upsertCustomerSearchIndexRecord,
  user,
  workspaceUid,
]);






useEffect(() => {
  // Quote numbers are now generated ONLY when saving
  // This prevents duplicates and race conditions

  if (editingQuoteId) return;

  if (!customerName) return;

  // Do nothing  number will be generated in saveQuotation()
}, [editingQuoteId, customerName]);


useEffect(() => {
  devLog("editingQuoteId changed:", editingQuoteId);
}, [editingQuoteId]);

useEffect(() => {
  devLog("isNewQuote changed:", isNewQuote);
}, [isNewQuote]);

useEffect(() => {
  setGeneralQuote((prev) => {
    const normalized = syncGeneralQuoteDefaults(prev);
    return JSON.stringify(prev) === JSON.stringify(normalized) ? prev : normalized;
  });
}, [syncGeneralQuoteDefaults]);

useEffect(() => {
  if (!isMobile) {
    setIsNavOpen(false);
  }
}, [isMobile]);

useEffect(() => {
  if (crmView !== "board" && crmView !== "list" && crmView !== "leads") {
    setIsCrmDrawerHovered(false);
    setIsCrmDrawerPinned(false);
  }
}, [crmView]);

useEffect(() => {
  if (tab === "executionSpecs") {
    return;
  }

  if (
    isDevRuntime &&
    selectedCrmDealId &&
    !crmVisibleDeals.some((deal) => String(deal.id || "") === String(selectedCrmDealId))
  ) {
    devWarn("CRM drawer selected deal is outside the current visible list", {
      selectedCrmDealId,
      drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
      lastKnownDealName:
        selectedCrmDealCacheRef.current?.crmTitle ||
        selectedCrmDealCacheRef.current?.customerLabel ||
        selectedCrmDealCacheRef.current?.title ||
        "",
      activeFilters: {
        tab,
        crmView,
        crmScope,
        crmPipelineType,
        crmStageFilter,
        crmTypeFilter,
        crmSourceFilter,
        crmSearch,
        crmLeadSearch,
      },
      visibleCount: crmVisibleDeals.length,
    });
  }
}, [
  crmLeadSearch,
  crmPipelineType,
  crmScope,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  crmView,
  crmVisibleDeals,
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  selectedCrmDealId,
  tab,
]);

useEffect(() => {
  if (crmScope === CRM_SCOPE_ALL) return;
  if (crmPipelineType === crmScope) return;
  setCrmPipelineType(crmScope);
}, [crmPipelineType, crmScope]);

useEffect(() => {
  if (crmScope !== CRM_SCOPE_ALL) return;
  if (crmView !== "board" && crmView !== "settings") return;

  if (crmStageFilter !== "all") {
    const filterValue = String(crmStageFilter);
    if (filterValue.includes("::")) {
      setCrmStageFilter("all");
      return;
    }
    if (
      crmView === "board" &&
      !crmBoardFilterOptions.some((option) => option.key === filterValue)
    ) {
      setCrmStageFilter("all");
    }
  }
}, [crmBoardFilterOptions, crmScope, crmStageFilter, crmView]);

useEffect(() => {
  if (!selectedCrmDeal) {
    if (selectedCrmDealId) {
      if (isDevRuntime) {
        devWarn("CRM drawer lost selected deal", {
          selectedCrmDealId,
          drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
          lastKnownDealName:
            selectedCrmDealCacheRef.current?.crmTitle ||
            selectedCrmDealCacheRef.current?.customerLabel ||
            selectedCrmDealCacheRef.current?.title ||
            "",
          activeFilters: {
            tab,
            crmView,
            crmScope,
            crmPipelineType,
            crmStageFilter,
            crmTypeFilter,
            crmSourceFilter,
            crmSearch,
            crmLeadSearch,
          },
          crmLeadRecordsCount: crmLeadRecords.length,
        });
      }
      return;
    }

    if (isDevRuntime && crmDetailDraft) {
      devWarn("CRM DETAILS SELECTION CLEARED", {
        reason: "no_selected_crm_deal_id",
        previousSelectedCrmDealId: selectedCrmDealIdRef.current,
        drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
      });
    }
    setCrmDetailDraft(null);
    crmDetailDraftDealIdRef.current = "";
    crmDetailDraftDirtyRef.current = false;
    executionDetailsPersistedSignatureRef.current = "";
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "idle", null);
    return;
  }

  const nextDealId = String(selectedCrmDeal.id || "");
  const isSameSelectedDeal = crmDetailDraftDealIdRef.current === nextDealId;
  if (
    isSameSelectedDeal &&
    (isSavingCrmDetails || crmDetailDraftDirtyRef.current || executionDetailsDraftIsDirty)
  ) {
    return;
  }

  const nextDraft = buildCrmDetailDraftFromDeal(selectedCrmDeal);
  crmDetailDraftDealIdRef.current = nextDealId;
  setCrmDetailDraft(nextDraft);
  markExecutionDetailsBaseline(nextDraft, "saved");
}, [
  buildCrmDetailDraftFromDeal,
  crmDetailDraft,
  crmLeadRecords.length,
  crmLeadSearch,
  crmPipelineType,
  crmScope,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  crmView,
  executionDetailsDraftIsDirty,
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  isSavingCrmDetails,
  markExecutionDetailsBaseline,
  selectedCrmDeal,
  selectedCrmDealId,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionSpecs") return;

  const executionItems = Array.isArray(crmDetailDraft?.executionSpecs)
    ? crmDetailDraft.executionSpecs
    : [];

  if (executionItems.length === 0) {
    if (selectedExecutionItemId) {
      setSelectedExecutionItemId("");
    }
    return;
  }

  if (!selectedExecutionItemId || !executionItems.some((item) => item.id === selectedExecutionItemId)) {
    setSelectedExecutionItemId(executionItems[0].id);
  }
}, [crmDetailDraft, selectedExecutionItemId, tab]);

useEffect(() => {
  const checklistItemId = pendingChecklistFocusIdRef.current;
  if (!checklistItemId) return undefined;

  const frameId = window.requestAnimationFrame(() => {
    const element = checklistInputRefs.current.get(checklistItemId);
    if (element) {
      element.focus();
      element.select?.();
      pendingChecklistFocusIdRef.current = "";
    }
  });

  return () => window.cancelAnimationFrame(frameId);
}, [crmDetailDraft?.checklistItems]);

useEffect(() => {
  const selectedExecutionType = selectedExecutionItemId
    ? crmDetailDraft?.executionSpecs?.find((item) => item.id === selectedExecutionItemId)
        ?.itemType || ""
    : "";
  if (!normalizeExecutionTemplateItemType(selectedExecutionType)) {
    setSelectedExecutionTemplateId("");
    return;
  }

  setSelectedExecutionTemplateId(
    getExecutionTemplateForType(selectedExecutionType)?.id || ""
  );
}, [crmDetailDraft, getExecutionTemplateForType, selectedExecutionItemId]);

useEffect(() => {
  if (executionTemplateCatalog.length === 0) {
    if (selectedExecutionTemplateManagerId) {
      setSelectedExecutionTemplateManagerId("");
    }
    return;
  }

  if (
    !selectedExecutionTemplateManagerId ||
    !executionTemplateCatalog.some((template) => template.id === selectedExecutionTemplateManagerId)
  ) {
    setSelectedExecutionTemplateManagerId(executionTemplateCatalog[0].id);
  }
}, [executionTemplateCatalog, selectedExecutionTemplateManagerId]);

useEffect(() => {
  if (!generalQuotePersistedSignatureRef.current) {
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(generalQuote);
  }
}, [generalQuote, serializeGeneralQuoteDraftData]);

useEffect(() => {
  if (!curtainQuotePersistedSignatureRef.current) {
    curtainQuotePersistedSignatureRef.current = serializeCurtainQuoteDraftData();
  }
}, [serializeCurtainQuoteDraftData]);

useEffect(() => {
  if (
    tab === "quote" &&
    quoteSubTab === GENERAL_QUOTE_TYPE &&
    generalQuoteIsDirty
  ) {
    if (autoSaveStatus === "saved") {
      setAutoSaveStatus("idle");
    }
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.GENERAL_QUOTE, "dirty", null);
  }
}, [autoSaveStatus, generalQuoteIsDirty, quoteSubTab, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (
    tab === "quote" &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    curtainQuoteIsDirty
  ) {
    if (autoSaveStatus === "saved") {
      setAutoSaveStatus("idle");
    }
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "dirty", null);
  }
}, [autoSaveStatus, curtainQuoteIsDirty, quoteSubTab, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (isCrmLeadModalOpen && crmLeadDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CRM_LEAD, "dirty", null);
  }
}, [crmLeadDraftIsDirty, isCrmLeadModalOpen, updateLocalDraftStatus]);

useEffect(() => {
  if ((tab === "executionSpecs" || tab === "crm") && executionDetailsDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "dirty", null);
  }
}, [executionDetailsDraftIsDirty, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (tab === "executionTemplates" && executionTemplatesDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE, "dirty", null);
  }
}, [executionTemplatesDraftIsDirty, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== GENERAL_QUOTE_TYPE || !generalQuoteDraftKey) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
    key: generalQuoteDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    serverUpdatedAt: getUpdatedMillis(selectedQuote),
    onRestore: (draftRecord) => {
      const restoredQuote = syncGeneralQuoteDefaults({
        ...(draftRecord?.data || {}),
        quoteDate: resolveQuoteDateForInput(draftRecord?.data || {}, todayISO()),
      });
      const restoredIdentity = resolveGeneralDraftEditingIdentity(draftRecord, workspaceUid);
      if (restoredIdentity.error) {
        alert("This draft's quotation identity needs review. Your draft has not been changed. Contact support before saving.");
        return;
      }
      const restoredEditingQuoteId = restoredIdentity.quoteId;
      const nextEditingQuoteId =
        restoredEditingQuoteId && restoredEditingQuoteId !== "new"
          ? restoredEditingQuoteId
          : "";
      setEditingQuoteId(null);
      setGeneralEditingQuoteId(nextEditingQuoteId || null);
      setGeneralQuoteEditorMode(
        nextEditingQuoteId
          ? QUOTE_EDITOR_MODE_EDITING_EXISTING
          : QUOTE_EDITOR_MODE_NEW
      );
      setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
      setIsNewQuote(!nextEditingQuoteId);
      if (!nextEditingQuoteId) {
        setSelectedQuote(null);
      }
      generalQuoteRef.current = restoredQuote;
      setGeneralQuote(restoredQuote);
      setCustomerName(
        restoredQuote.quoteFor?.company ||
          restoredQuote.quoteFor?.name ||
          ""
      );
      setQuoteNo(restoredQuote.quoteNo || "");
      setDate(resolveQuoteDateForInput(restoredQuote, todayISO()));
      generalQuotePersistedSignatureRef.current =
        serializeGeneralQuoteDraftData(restoredQuote);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  generalQuoteDraftKey,
  promptToRestoreLocalDraft,
  quoteSubTab,
  selectedQuote,
  serializeGeneralQuoteDraftData,
  syncGeneralQuoteDefaults,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE || !curtainQuoteDraftKey) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
    key: curtainQuoteDraftKey,
    title: "Unsaved curtain quotation found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    restoreLabel: "Restore Draft",
    discardLabel: "Discard",
    serverNewerDescription:
      "This quote was saved more recently than the recovered draft. Restore anyway?",
    serverUpdatedAt: getUpdatedMillis(selectedQuote),
    onRestore: restoreCurtainQuoteDraft,
  });
}, [
  curtainQuoteDraftKey,
  promptToRestoreLocalDraft,
  quoteSubTab,
  restoreCurtainQuoteDraft,
  selectedQuote,
  tab,
]);

useEffect(() => {
  if (!isCrmLeadModalOpen || !crmLeadDraftKey) return;

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.CRM_LEAD,
    key: crmLeadDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    onRestore: (draftRecord) => {
      const restoredDraft = draftRecord?.data || createCrmLeadDraftState();
      setCrmLeadDraft(restoredDraft);
      crmLeadPersistedSignatureRef.current =
        serializeCrmLeadDraftData(restoredDraft);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.CRM_LEAD,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  crmLeadDraftKey,
  createCrmLeadDraftState,
  isCrmLeadModalOpen,
  promptToRestoreLocalDraft,
  serializeCrmLeadDraftData,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionSpecs" || !executionDetailsDraftKey || !selectedCrmDealId) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
    key: executionDetailsDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    serverUpdatedAt: getUpdatedMillis(selectedCrmDeal),
    onRestore: (draftRecord) => {
      const restoredDraft = draftRecord?.data || null;
      if (!restoredDraft) return;
      setCrmDetailDraft(restoredDraft);
      executionDetailsPersistedSignatureRef.current =
        serializeExecutionDetailsDraftData(restoredDraft);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  executionDetailsDraftKey,
  promptToRestoreLocalDraft,
  selectedCrmDeal,
  selectedCrmDealId,
  serializeExecutionDetailsDraftData,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionTemplates" || !executionTemplatesDraftKey) return;
  if (
    !executionTemplatesLoadState.loaded ||
    executionTemplatesLoadState.workspaceUid !== workspaceUid
  ) {
    return;
  }

  const existingDraft = loadLocalDraft(executionTemplatesDraftKey);
  const draftTemplates = normalizeExecutionTemplateDrafts(
    existingDraft?.data?.templates || []
  );
  if (existingDraft && draftTemplates.length === 0 && crmExecutionTemplates.length > 0) {
    if (isDevRuntime) {
      devWarn("IGNORING EMPTY EXECUTION TEMPLATE LOCAL DRAFT", {
        workspaceUid,
        serverTemplateCount: crmExecutionTemplates.length,
        draftKey: executionTemplatesDraftKey,
      });
    }
    clearLocalDraftRecord(executionTemplatesDraftKey);
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
    key: executionTemplatesDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    onRestore: (draftRecord) => {
      const restoredTemplates = normalizeExecutionTemplateDrafts(
        draftRecord?.data?.templates || []
      );
      const restoredSelectedId =
        draftRecord?.data?.selectedId || restoredTemplates[0]?.id || "";
      setCrmExecutionTemplates(restoredTemplates);
      setSelectedExecutionTemplateManagerId(restoredSelectedId);
      executionTemplatePersistedSignatureRef.current =
        serializeExecutionTemplatesDraftData(restoredTemplates, restoredSelectedId);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  crmExecutionTemplates.length,
  executionTemplatesLoadState.loaded,
  executionTemplatesLoadState.workspaceUid,
  executionTemplatesDraftKey,
  promptToRestoreLocalDraft,
  serializeExecutionTemplatesDraftData,
  tab,
  updateLocalDraftStatus,
  workspaceUid,
]);

useEffect(() => {
  if (!(tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistGeneralQuoteDraft("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [generalQuoteIsDirty, persistGeneralQuoteDraft, quoteSubTab, tab]);

useEffect(() => {
  if (!(tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistCurtainQuoteDraft("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [curtainQuoteIsDirty, persistCurtainQuoteDraft, quoteSubTab, tab]);

useEffect(() => {
  if (!(isCrmLeadModalOpen && crmLeadDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistCrmLeadDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [crmLeadDraftIsDirty, isCrmLeadModalOpen, persistCrmLeadDraftLocally]);

useEffect(() => {
  if (!(tab === "executionSpecs" && executionDetailsDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistExecutionDetailsDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [executionDetailsDraftIsDirty, persistExecutionDetailsDraftLocally, tab]);

useEffect(() => {
  if (!(tab === "executionTemplates" && executionTemplatesDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistExecutionTemplatesDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [executionTemplatesDraftIsDirty, persistExecutionTemplatesDraftLocally, tab]);

useEffect(() => {
  const previousRoute = previousRouteRef.current;

  if (
    previousRoute.tab === "quote" &&
    previousRoute.quoteSubTab === GENERAL_QUOTE_TYPE &&
    (tab !== "quote" || quoteSubTab !== GENERAL_QUOTE_TYPE)
  ) {
    persistGeneralQuoteDraft("route-change");
  }

  if (
    previousRoute.tab === "quote" &&
    previousRoute.quoteSubTab === CURTAIN_QUOTE_TYPE &&
    (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE)
  ) {
    persistCurtainQuoteDraft("route-change");
  }

  if (previousRoute.tab === "executionSpecs" && tab !== "executionSpecs") {
    persistExecutionDetailsDraftLocally("route-change");
  }

  if (previousRoute.tab === "executionTemplates" && tab !== "executionTemplates") {
    persistExecutionTemplatesDraftLocally("route-change");
  }

  previousRouteRef.current = { tab, quoteSubTab };
}, [
  persistExecutionDetailsDraftLocally,
  persistExecutionTemplatesDraftLocally,
  persistCurtainQuoteDraft,
  persistGeneralQuoteDraft,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  const handleBeforeUnload = () => {
    persistGeneralQuoteDraft("route-change");
    persistCurtainQuoteDraft("route-change");
    persistCrmLeadDraftLocally("route-change");
    persistExecutionDetailsDraftLocally("route-change");
    persistExecutionTemplatesDraftLocally("route-change");
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => window.removeEventListener("beforeunload", handleBeforeUnload);
}, [
  persistCrmLeadDraftLocally,
  persistCurtainQuoteDraft,
  persistExecutionDetailsDraftLocally,
  persistExecutionTemplatesDraftLocally,
  persistGeneralQuoteDraft,
]);

useEffect(() => {
  if (!isStitchingSettingsDirty) return undefined;

  const handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = "";
    return "";
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => window.removeEventListener("beforeunload", handleBeforeUnload);
}, [isStitchingSettingsDirty]);



useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "quote" && tab !== "settings" && tab !== "products") return;
  if (quotationSettingsLoadedWorkspaceRef.current === workspaceUid) {
    if (quotationSettingsReadyWorkspace !== workspaceUid) {
      setQuotationSettingsReadyWorkspace(workspaceUid);
    }
    if (isDevRuntime) {
      devLog("Quotation settings load skipped; workspace already loaded", {
        workspaceUid,
      });
    }
    return;
  }
  if (quotationSettingsLoadingWorkspaceRef.current === workspaceUid) {
    if (isDevRuntime) {
      devLog("Quotation settings load skipped; workspace load already in flight", {
        workspaceUid,
      });
    }
    return;
  }
  quotationSettingsLoadingWorkspaceRef.current = workspaceUid;

  const loadSettings = async () => {
    try {
      logWorkspaceQuery("Quotation Settings", `users/${workspaceUid}/settings/quotation`);
      const snap = await loadOperationalSettingsSnapshot(functions, auth.currentUser?.uid, workspaceUid, 'quotation');
      if (isDevRuntime) {
        devLog("Quotation Settings exists:", snap.exists());
      }

      if (snap.exists()) {
        const data = snap.data();

        // Quote numbering
        setQuoteSettings({
          startNumber: data.startNumber || "",
          lastQuoteNumber: data.lastQuoteNumber || "",
        });

        // Fabric calculation settings
        if (data.fabricCalcSettings) {
          setFabricCalcSettings(
            normalizeFabricCalcSettingsForState(data.fabricCalcSettings)
          );
        }
      }
      quotationSettingsLoadedWorkspaceRef.current = workspaceUid;
      setQuotationSettingsReadyWorkspace(workspaceUid);
    } finally {
      if (quotationSettingsLoadingWorkspaceRef.current === workspaceUid) {
        quotationSettingsLoadingWorkspaceRef.current = "";
      }
    }
  };

  loadSettings().catch((error) => {
    devError("Failed to load quotation settings:", error);
    notify('Quotation settings could not be loaded. Please contact your account administrator.', 'error');
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${workspaceUid}/settings/quotation`,
        source: "loadQuotationSettings",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  });
}, [
  logWorkspaceQuery,
  notify,
  quotationSettingsReadyWorkspace,
  raiseFirebaseHealthWarning,
  tab,
  workspaceUid,
]);

const loadFabricBrands = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid) return [];
    if (!force && fabricBrandsLoadedWorkspaceRef.current === workspaceUid) {
      return fabricBrandsRef.current;
    }
    if (!force && fabricBrandsLoadingWorkspaceRef.current === workspaceUid) {
      return fabricBrandsRef.current;
    }

    fabricBrandsLoadingWorkspaceRef.current = workspaceUid;
    setFabricBrandsLoading(true);
    try {
      const brandQuery = firestoreQuery(
        collection(db, "users", workspaceUid, "fabricBrands"),
        firestoreLimit(200)
      );
      logWorkspaceQuery("Fabric Brands", `users/${workspaceUid}/fabricBrands`);
      const snap = await getDocsCached(brandQuery, {
        label: "loadFabricBrands",
        cacheKey: `users/${workspaceUid}/fabricBrands:list:v1`,
        ttlMs: 5 * 60 * 1000,
      });
      const nextBrands = normalizeFabricBrands(
        snap.docs.map((brandDoc) => ({
          id: brandDoc.id,
          ...brandDoc.data(),
        }))
      );
      fabricBrandsRef.current = nextBrands;
      setFabricBrands(nextBrands);
      fabricBrandsLoadedWorkspaceRef.current = workspaceUid;
      return nextBrands;
    } catch (error) {
      devError("FABRIC_BRANDS_LOAD_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Fabric brands will load later.", "warning");
      }
      return [];
    } finally {
      if (fabricBrandsLoadingWorkspaceRef.current === workspaceUid) {
        fabricBrandsLoadingWorkspaceRef.current = "";
      }
      setFabricBrandsLoading(false);
    }
  },
  [logWorkspaceQuery, notify, workspaceUid]
);

useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "quote" && tab !== "settings") return;
  loadFabricBrands();
}, [loadFabricBrands, tab, workspaceUid]);

const saveFabricBrandDraft = useCallback(async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready. Please refresh and try again.", "warning");
    return;
  }
  const name = sanitizeEditableText(fabricBrandDraft.name || "");
  if (!name) {
    notify("Fabric brand name is required.", "warning");
    return;
  }

  try {
    const brandRef = doc(collection(db, "users", workspaceUid, "fabricBrands"));
    await setDoc(brandRef, {
      id: brandRef.id,
      name,
      nameLower: name.toLowerCase(),
      supplier: sanitizeEditableText(fabricBrandDraft.supplier || ""),
      notes: sanitizeEditableText(fabricBrandDraft.notes || ""),
      archived: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user?.uid || "",
    });
    invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands`);
    invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands:list`);
    fabricBrandsLoadedWorkspaceRef.current = "";
    fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
    setFabricBrandDraft({ name: "", supplier: "", notes: "" });
    await loadFabricBrands({ force: true });
    notify("Fabric brand added.", "success");
  } catch (error) {
    devError("FABRIC_BRAND_SAVE_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
      name,
    });
    notify("Could not save fabric brand.", "error");
  }
}, [fabricBrandDraft, loadFabricBrands, notify, user?.uid, workspaceUid]);

const archiveFabricBrand = useCallback(
  async (brandId) => {
    if (!workspaceUid || !brandId) return;
    try {
      const brandRef = doc(db, "users", workspaceUid, "fabricBrands", brandId);
      await updateDoc(brandRef, {
        archived: true,
        updatedAt: serverTimestamp(),
      });
      invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands`);
      invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands:list`);
      fabricBrandsLoadedWorkspaceRef.current = "";
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
      setFabricBrands((prev) => {
        const nextBrands = prev.filter((brand) => brand.id !== brandId);
        fabricBrandsRef.current = nextBrands;
        return nextBrands;
      });
      notify("Fabric brand archived.", "success");
    } catch (error) {
      devError("FABRIC_BRAND_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId,
      });
      notify("Could not archive fabric brand.", "error");
    }
  },
  [notify, workspaceUid]
);

const getEmptyFabricCatalogueLibrary = useCallback(
  (brand = {}) => ({
    brandId: brand.id || "",
    brandName: brand.name || "",
    catalogues: [],
    rulesByCatalogueId: {},
    loadedAt: "",
  }),
  []
);

const getFabricBrandById = useCallback((brandId = "") => {
  const normalizedId = sanitizeEditableText(brandId);
  return (
    fabricBrandsRef.current.find((brand) => brand.id === normalizedId) ||
    fabricBrands.find((brand) => brand.id === normalizedId) ||
    null
  );
}, [fabricBrands]);

const loadFabricCatalogueLibrary = useCallback(
  async (brandId, { force = false } = {}) => {
    if (!workspaceUid || !brandId) return getEmptyFabricCatalogueLibrary();
    const brand = getFabricBrandById(brandId) || { id: brandId, name: "" };
    const loadedKey = `${workspaceUid}:${brandId}`;
    const cachedLibrary =
      fabricCatalogueLibraryRef.current[brandId] ||
      getEmptyFabricCatalogueLibrary(brand);

    if (
      !force &&
      fabricCatalogueLoadedBrandKeysRef.current.has(loadedKey)
    ) {
      return cachedLibrary;
    }

    if (
      !force &&
      fabricCatalogueLoadingBrandKeysRef.current.has(loadedKey)
    ) {
      return cachedLibrary;
    }

    if (force) {
      invalidateFirestoreReadCacheByPrefix(
        `users/${workspaceUid}/fabricBrands/${brandId}/catalogues`
      );
      fabricCatalogueLoadedBrandKeysRef.current.delete(loadedKey);
    }

    fabricCatalogueLoadingBrandKeysRef.current.add(loadedKey);
    try {
      const cataloguesPath = `users/${workspaceUid}/fabricBrands/${brandId}/catalogues`;
      const cataloguesQuery = firestoreQuery(
        collection(db, "users", workspaceUid, "fabricBrands", brandId, "catalogues"),
        firestoreLimit(200)
      );
      logWorkspaceQuery("Fabric Catalogues", cataloguesPath);
      const catalogueSnap = await getDocsCached(cataloguesQuery, {
        label: "loadFabricCataloguesForBrand",
        cacheKey: `${cataloguesPath}:list:v1`,
        ttlMs: 5 * 60 * 1000,
      });
      const catalogues = catalogueSnap.docs
        .map((catalogueDoc) =>
          normalizeFabricCatalogue(
            {
              id: catalogueDoc.id,
              brandId,
              brandName: brand.name || "",
              ...catalogueDoc.data(),
            },
            catalogueDoc.id
          )
        )
        .filter((catalogue) => catalogue.id && catalogue.catalogueName && catalogue.active)
        .sort((a, b) => a.catalogueName.localeCompare(b.catalogueName));

      const rulesEntries = await Promise.all(
        catalogues.map(async (catalogue) => {
          const rulesPath = `${cataloguesPath}/${catalogue.id}/srPriceRules`;
          const rulesQuery = firestoreQuery(
            collection(
              db,
              "users",
              workspaceUid,
              "fabricBrands",
              brandId,
              "catalogues",
              catalogue.id,
              "srPriceRules"
            ),
            firestoreLimit(500)
          );
          const rulesSnap = await getDocsCached(rulesQuery, {
            label: "loadFabricSrPriceRulesForCatalogue",
            cacheKey: `${rulesPath}:list:v1`,
            ttlMs: 5 * 60 * 1000,
          });
          const rules = rulesSnap.docs
            .map((ruleDoc) =>
              normalizeSrPriceRule(
                {
                  id: ruleDoc.id,
                  ...ruleDoc.data(),
                  brandId,
                  brandName: brand.name || "",
                  catalogueId: catalogue.id,
                  catalogueName: catalogue.catalogueName,
                  catalogueKey: catalogue.catalogueKey,
                },
                ruleDoc.id
              )
            )
            .filter((rule) => rule.id && rule.active)
            .sort((a, b) => getSrPriceRuleLabel(a).localeCompare(getSrPriceRuleLabel(b)));
          return [catalogue.id, rules];
        })
      );

      const library = {
        brandId,
        brandName: brand.name || "",
        catalogues,
        rulesByCatalogueId: Object.fromEntries(rulesEntries),
        loadedAt: new Date().toISOString(),
      };
      fabricCatalogueLibraryRef.current = {
        ...fabricCatalogueLibraryRef.current,
        [brandId]: library,
      };
      setFabricCatalogueLibraryByBrand((prev) => ({
        ...prev,
        [brandId]: library,
      }));
      fabricCatalogueLoadedBrandKeysRef.current.add(loadedKey);
      return library;
    } catch (error) {
      devError("FABRIC_CATALOGUE_LIBRARY_LOAD_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId,
      });
      notify("Could not load fabric catalogues.", "error");
      return cachedLibrary;
    } finally {
      fabricCatalogueLoadingBrandKeysRef.current.delete(loadedKey);
    }
  },
  [
    getEmptyFabricCatalogueLibrary,
    getFabricBrandById,
    logWorkspaceQuery,
    notify,
    workspaceUid,
  ]
);

const loadAllFabricCatalogueLibraries = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid) return [];
    if (
      !force &&
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current === workspaceUid
    ) {
      return Object.values(fabricCatalogueLibraryRef.current || {});
    }
    if (
      !force &&
      fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid
    ) {
      return Object.values(fabricCatalogueLibraryRef.current || {});
    }

    fabricCatalogueAllBrandsLoadingWorkspaceRef.current = workspaceUid;
    try {
      const brands = await loadFabricBrands({ force: false });
      const activeBrands = Array.isArray(brands) && brands.length ? brands : fabricBrandsRef.current;
      const libraries = await Promise.all(
        (activeBrands || [])
          .filter((brand) => brand?.id)
          .map((brand) => loadFabricCatalogueLibrary(brand.id, { force }))
      );
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current = workspaceUid;
      return libraries.filter(Boolean);
    } finally {
      if (fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid) {
        fabricCatalogueAllBrandsLoadingWorkspaceRef.current = "";
      }
    }
  },
  [loadFabricBrands, loadFabricCatalogueLibrary, workspaceUid]
);

const saveFabricCatalogueDraft = useCallback(
  async (brand) => {
    if (!workspaceUid || !brand?.id) return;
    const draft = fabricCatalogueDrafts[brand.id] || {};
    const catalogueName = sanitizeEditableText(draft.catalogueName || "");
    const catalogueKey = normalizeCatalogueKey(catalogueName);
    if (!catalogueName || !catalogueKey) {
      notify("Catalogue name is required.", "warning");
      return;
    }
    const rawCatalogueWidth =
      draft.catalogueDefaultFabricWidthIn ?? draft.defaultFabricWidthIn ?? "";
    const catalogueDefaultFabricWidthIn =
      String(rawCatalogueWidth || "").trim()
        ? normalizeFabricWidthIn(rawCatalogueWidth)
        : null;
    if (String(rawCatalogueWidth || "").trim() && !catalogueDefaultFabricWidthIn) {
      notify("Enter a valid catalogue fabric width.", "warning");
      return;
    }

    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      (await loadFabricCatalogueLibrary(brand.id));
    const duplicate = (library.catalogues || []).find(
      (catalogue) =>
        catalogue.catalogueKey === catalogueKey ||
        normalizeCatalogueKey(catalogue.catalogueName || "") === catalogueKey ||
        catalogue.id === catalogueKey
    );
    if (duplicate) {
      notify(`${catalogueName} already exists under ${brand.name}.`, "warning");
      return;
    }

    const savingKey = `${brand.id}:catalogue:${catalogueKey}`;
    setFabricCatalogueSavingKey(savingKey);
    try {
      const catalogueRef = doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogueKey
      );
      await setDoc(
        catalogueRef,
        {
          id: catalogueKey,
          brandId: brand.id,
          brandName: brand.name || "",
          catalogueName,
          catalogueKey,
          priceUnit: draft.priceUnit === "sqft" ? "sqft" : "m",
          defaultRrpPerMeter:
            Number(draft.defaultRrpPerMeter) > 0
              ? Number(draft.defaultRrpPerMeter)
              : null,
          defaultFabricWidthIn: catalogueDefaultFabricWidthIn,
          catalogueDefaultFabricWidthIn,
          currency: "INR",
          active: true,
          archived: false,
          notes: sanitizeEditableText(draft.notes || ""),
          createdAt: serverTimestamp(),
          createdBy: user?.uid || "",
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );
      setFabricCatalogueDrafts((prev) => ({
        ...prev,
        [brand.id]: {
          catalogueName: "",
          priceUnit: "m",
          defaultRrpPerMeter: "",
          catalogueDefaultFabricWidthIn: "",
          notes: "",
        },
      }));
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify(`${catalogueName} added under ${brand.name}.`, "success");
    } catch (error) {
      devError("FABRIC_CATALOGUE_SAVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueName,
      });
      notify("Could not save catalogue.", "error");
    } finally {
      setFabricCatalogueSavingKey("");
    }
  },
  [
    fabricCatalogueDrafts,
    loadFabricCatalogueLibrary,
    notify,
    user?.uid,
    workspaceUid,
  ]
);

const getFabricCatalogueEditDraftKey = useCallback((brand = {}, catalogue = {}) => {
  return `${brand?.id || ""}:${catalogue?.id || ""}`;
}, []);

const startEditFabricCatalogue = useCallback(
  (brand, catalogue) => {
    if (!brand?.id || !catalogue?.id) return;
    const draftKey = getFabricCatalogueEditDraftKey(brand, catalogue);
    setFabricCatalogueEditDrafts((prev) => ({
      ...prev,
      [draftKey]: {
        catalogueName: catalogue.catalogueName || "",
        priceUnit: catalogue.priceUnit === "sqft" ? "sqft" : "m",
      },
    }));
  },
  [getFabricCatalogueEditDraftKey]
);

const updateFabricCatalogueEditDraft = useCallback((draftKey = "", field = "", value = "") => {
  if (!draftKey || !field) return;
  setFabricCatalogueEditDrafts((prev) => ({
    ...prev,
    [draftKey]: {
      ...(prev[draftKey] || {}),
      [field]: value,
    },
  }));
}, []);

const cancelEditFabricCatalogue = useCallback((brand, catalogue) => {
  const draftKey = getFabricCatalogueEditDraftKey(brand, catalogue);
  setFabricCatalogueEditDrafts((prev) => {
    if (!prev[draftKey]) return prev;
    const next = { ...prev };
    delete next[draftKey];
    return next;
  });
}, [getFabricCatalogueEditDraftKey]);

const loadFabricCatalogueSrRuleDocsForMaintenance = useCallback(
  async (brandId = "", catalogueId = "", label = "fabricCatalogueSrRulesMaintenance") => {
    if (!workspaceUid || !brandId || !catalogueId) return [];
    const rulesPath = `users/${workspaceUid}/fabricBrands/${brandId}/catalogues/${catalogueId}/srPriceRules`;
    const rulesCollection = collection(
      db,
      "users",
      workspaceUid,
      "fabricBrands",
      brandId,
      "catalogues",
      catalogueId,
      "srPriceRules"
    );
    const docs = [];
    let lastDoc = null;

    for (let page = 0; page < 50; page += 1) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(450),
      ];
      if (lastDoc) {
        constraints.splice(1, 0, firestoreStartAfter(lastDoc));
      }
      const snap = await getDocsCached(firestoreQuery(rulesCollection, ...constraints), {
        label,
        cacheKey: `${rulesPath}:${label}:${Date.now()}:${page}`,
        ttlMs: 0,
      });
      docs.push(...snap.docs);
      if (snap.docs.length < 450) break;
      lastDoc = snap.docs[snap.docs.length - 1];
    }

    return docs;
  },
  [workspaceUid]
);

const saveFabricCatalogueEditDraft = useCallback(
  async (brand, catalogue) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    const draftKey = getFabricCatalogueEditDraftKey(brand, catalogue);
    const draft = fabricCatalogueEditDrafts[draftKey] || {};
    const priceUnit = (draft.priceUnit ?? catalogue.priceUnit) === "sqft" ? "sqft" : "m";
    const catalogueName = sanitizeEditableText(
      draft.catalogueName ?? catalogue.catalogueName ?? ""
    );
    const catalogueKey = normalizeCatalogueKey(catalogueName);
    if (!catalogueName || !catalogueKey) {
      notify("Catalogue name is required.", "warning");
      return;
    }

    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      fabricCatalogueLibraryByBrand[brand.id] ||
      (await loadFabricCatalogueLibrary(brand.id));
    const duplicate = (library.catalogues || []).find((entry) => {
      if (!entry?.id || entry.id === catalogue.id) return false;
      return (
        entry.catalogueKey === catalogueKey ||
        normalizeCatalogueKey(entry.catalogueName || "") === catalogueKey ||
        entry.id === catalogueKey
      );
    });
    if (duplicate) {
      notify(`${catalogueName} already exists under ${brand.name}.`, "warning");
      return;
    }

    const savingKey = `${brand.id}:catalogue-edit:${catalogue.id}`;
    setFabricCatalogueSavingKey(savingKey);
    try {
      const catalogueRef = doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogue.id
      );
      const ruleDocs = await loadFabricCatalogueSrRuleDocsForMaintenance(
        brand.id,
        catalogue.id,
        "renameFabricCatalogue.loadSrRules"
      );
      if (!canChangeCataloguePriceUnit(catalogue, priceUnit, ruleDocs.map(ruleDoc => ruleDoc.data()))) {
        notify("Existing prices cannot be converted by changing the unit. Create a separate catalogue with the required unit and enter its prices.", "warning");
        return;
      }

      let batch = writeBatch(db);
      let operationCount = 0;
      const commitBatch = async () => {
        if (!operationCount) return;
        await batch.commit();
        batch = writeBatch(db);
        operationCount = 0;
      };

      batch.set(
        catalogueRef,
        {
          catalogueName,
          displayName: catalogueName,
          name: catalogueName,
          catalogueKey,
          priceUnit,
          brandId: brand.id,
          brandName: brand.name || "",
          active: true,
          archived: false,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );
      operationCount += 1;

      for (const ruleDoc of ruleDocs) {
        if (operationCount >= 450) {
          await commitBatch();
        }
        batch.update(ruleDoc.ref, {
          catalogueId: catalogue.id,
          catalogueName,
          catalogueKey,
          brandId: brand.id,
          brandName: brand.name || "",
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        });
        operationCount += 1;
      }

      await commitBatch();
      setFabricCatalogueEditDrafts((prev) => {
        const next = { ...prev };
        delete next[draftKey];
        return next;
      });
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify("Catalogue updated.", "success");
    } catch (error) {
      devError("FABRIC_CATALOGUE_RENAME_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
        attemptedName: catalogueName,
      });
      notify(
        String(error?.code || "").includes("permission-denied")
          ? "You do not have permission to manage catalogues. Please contact your admin."
          : "Could not update catalogue. Please try again.",
        "error"
      );
    } finally {
      setFabricCatalogueSavingKey("");
    }
  },
  [
    fabricCatalogueEditDrafts,
    fabricCatalogueLibraryByBrand,
    getFabricCatalogueEditDraftKey,
    loadFabricCatalogueLibrary,
    loadFabricCatalogueSrRuleDocsForMaintenance,
    notify,
    user?.uid,
    workspaceUid,
  ]
);

const createFabricSrRuleDraftRow = useCallback(
  (overrides = {}) => ({
    clientId: createClientId("fabric-sr-rule"),
    existingRuleId: "",
    ruleType: "range",
    srNumber: "",
    srFrom: "",
    srTo: "",
    srNumbers: "",
    rrpPerMeter: "",
    fabricWidthIn: "",
    notes: "",
    ...overrides,
  }),
  []
);

const getFabricSrRuleDraftRows = useCallback(
  (draftKey = "") => {
    const value = fabricSrRuleDrafts[draftKey];
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      return [
        createFabricSrRuleDraftRow({
          ...value,
          clientId: value.clientId || createClientId("fabric-sr-rule"),
        }),
      ];
    }
    return [];
  },
  [createFabricSrRuleDraftRow, fabricSrRuleDrafts]
);

const setFabricSrRuleDraftRows = useCallback((draftKey = "", updater) => {
  setFabricSrRuleDrafts((prev) => {
    const current = Array.isArray(prev[draftKey])
      ? prev[draftKey]
      : prev[draftKey] && typeof prev[draftKey] === "object"
      ? [prev[draftKey]]
      : [];
    const nextRows =
      typeof updater === "function" ? updater(current) : Array.isArray(updater) ? updater : current;
    return {
      ...prev,
      [draftKey]: nextRows,
    };
  });
}, []);

const addFabricSrRuleDraftRow = useCallback(
  (brand, catalogue, overrides = {}) => {
    if (!brand?.id || !catalogue?.id) return;
    const draftKey = `${brand.id}:${catalogue.id}`;
    setFabricSrRuleDraftRows(draftKey, (current) => [
      ...current,
      createFabricSrRuleDraftRow(overrides),
    ]);
  },
  [createFabricSrRuleDraftRow, setFabricSrRuleDraftRows]
);

const editFabricSrPriceRule = useCallback(
  (brand, catalogue, rule) => {
    if (!brand?.id || !catalogue?.id || !rule?.id) return;
    addFabricSrRuleDraftRow(brand, catalogue, {
      clientId: `edit-${rule.id}-${Date.now()}`,
      existingRuleId: rule.id,
      ruleType: rule.ruleType || "single",
      srNumber: rule.srNumber || "",
      srFrom: rule.srFrom || "",
      srTo: rule.srTo || "",
      srNumbers: (rule.normalizedSrNumbers || rule.srNumbers || []).join(", "),
      rrpPerMeter: rule.rrpPerMeter != null ? String(rule.rrpPerMeter) : "",
      fabricWidthIn: rule.fabricWidthIn != null ? String(rule.fabricWidthIn) : "",
      notes: rule.notes || "",
    });
  },
  [addFabricSrRuleDraftRow]
);

const updateFabricSrRuleDraftRow = useCallback(
  (draftKey = "", clientId = "", field = "", value = "") => {
    setFabricSrRuleDraftRows(draftKey, (current) =>
      current.map((draft) =>
        draft.clientId === clientId ? { ...draft, [field]: value } : draft
      )
    );
  },
  [setFabricSrRuleDraftRows]
);

const removeFabricSrRuleDraftRow = useCallback(
  (draftKey = "", clientId = "") => {
    setFabricSrRuleDraftRows(draftKey, (current) =>
      current.filter((draft) => draft.clientId !== clientId)
    );
  },
  [setFabricSrRuleDraftRows]
);

const getSrNumericValue = useCallback((value = "") => {
  return numericSrValue(value);
}, []);

const validateFabricSrRuleDraft = useCallback(
  ({ draft = {}, brand = {}, catalogue = {}, existingRules = [] } = {}) => {
    const payload = buildSrPriceRulePayload({ draft, brand, catalogue });
    const ruleId = buildSrPriceRuleDocId(draft);
    const errors = [];
    const warnings = [];
    const activeRules = (existingRules || []).filter(
      (rule) => rule.active !== false && rule.id !== draft.existingRuleId
    );
    const rrp = Number(payload.rrpPerMeter || 0);
    const hasFabricWidthInput = String(draft.fabricWidthIn || "").trim() !== "";

    if (!(rrp > 0)) {
      errors.push(`RRP/${catalogue.priceUnit === "sqft" ? "sqft" : "m"} must be greater than 0.`);
    }
    if (hasFabricWidthInput && !normalizeFabricWidthIn(draft.fabricWidthIn)) {
      errors.push("Enter a valid fabric width.");
    }

    if (payload.ruleType === "single" && !payload.srNumber) {
      errors.push("SR number is required.");
    }
    if (payload.ruleType === "range") {
      if (!payload.srFrom || !payload.srTo) {
        errors.push("SR range from/to is required.");
      } else {
        const fromNumeric = getSrNumericValue(payload.srFrom);
        const toNumeric = getSrNumericValue(payload.srTo);
        if (fromNumeric == null || toNumeric == null) {
          errors.push("SR range values must include numbers.");
        } else if (fromNumeric > toNumeric) {
          errors.push("SR range From must be less than or equal to To.");
        }
      }
    }
    if (payload.ruleType === "list" && !payload.normalizedSrNumbers.length) {
      errors.push("Enter at least one SR number in the list.");
    }

    const duplicateExact = activeRules.find(
      (rule) =>
        rule.ruleType === "single" &&
        payload.ruleType === "single" &&
        getSrPricingMatchKey(rule.srNumber) === getSrPricingMatchKey(payload.srNumber) &&
        rule.id !== ruleId
    );
    if (duplicateExact) {
      errors.push(`${getSrPriceRuleLabel(duplicateExact)} already exists. Use Edit to update it.`);
    }

    if (payload.ruleType === "range" && !errors.length) {
      const candidateFrom = getSrNumericValue(payload.srFrom);
      const candidateTo = getSrNumericValue(payload.srTo);
      activeRules
        .filter((rule) => rule.ruleType === "range")
        .forEach((rule) => {
          const fromNumeric = getSrNumericValue(rule.srFrom);
          const toNumeric = getSrNumericValue(rule.srTo);
          if (
            candidateFrom == null ||
            candidateTo == null ||
            fromNumeric == null ||
            toNumeric == null
          ) {
            return;
          }
          const overlaps =
            Math.max(candidateFrom, Math.min(fromNumeric, toNumeric)) <=
            Math.min(candidateTo, Math.max(fromNumeric, toNumeric));
          if (overlaps) {
            warnings.push(
              `${getSrPriceRuleLabel(payload)} overlaps with ${getSrPriceRuleLabel(rule)}. Prefer non-overlapping ranges like 1-10 and 11-20.`
            );
          }
        });
    }

    const candidateSrNumbers =
      payload.ruleType === "single"
        ? [payload.srNumber]
        : payload.ruleType === "list"
        ? payload.normalizedSrNumbers
        : [];
    if (candidateSrNumbers.length) {
      const duplicateSrNumbers = [];
      activeRules.forEach((rule) => {
        const existingSrNumbers =
          rule.ruleType === "single"
            ? [rule.srNumber]
            : rule.ruleType === "list"
            ? rule.normalizedSrNumbers || rule.srNumbers || []
            : [];
        existingSrNumbers.forEach((sr) => {
          if (
            candidateSrNumbers
              .map((entry) => getSrPricingMatchKey(entry))
              .includes(getSrPricingMatchKey(sr))
          ) {
            duplicateSrNumbers.push(normalizeSrNumber(sr));
          }
        });
      });
      if (duplicateSrNumbers.length) {
        warnings.push(
          `SR ${[...new Set(duplicateSrNumbers)].join(", ")} also appears in another exact/list rule. Exact/list priority will apply.`
        );
      }
    }

    return { payload, ruleId, errors, warnings };
  },
  [getSrNumericValue]
);

const saveFabricSrRuleDraftRow = useCallback(
  async (
    brand,
    catalogue,
    draft,
    { skipWarnings = false, removeAfterSave = true, silent = false } = {}
  ) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !draft) return false;
    const draftKey = `${brand.id}:${catalogue.id}`;
    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      fabricCatalogueLibraryByBrand[brand.id] ||
      getEmptyFabricCatalogueLibrary(brand);
    const existingRules = library.rulesByCatalogueId?.[catalogue.id] || [];
    const validation = validateFabricSrRuleDraft({
      draft,
      brand,
      catalogue,
      existingRules,
    });

    if (validation.errors.length) {
      notify(validation.errors[0], "warning");
      return false;
    }

    if (!skipWarnings && validation.warnings.length) {
      openConfirmDialog({
        title: "SR pricing overlap warning",
        description: validation.warnings.join(" "),
        confirmLabel: "Save Anyway",
        cancelLabel: "Review",
        tone: "warning",
        onConfirm: () =>
          saveFabricSrRuleDraftRow(brand, catalogue, draft, {
            skipWarnings: true,
            removeAfterSave,
            silent,
          }),
      });
      return false;
    }

    const { payload, ruleId } = validation;
    const savingKey = `${draftKey}:rule:${ruleId}`;
    setFabricCatalogueSavingKey(savingKey);
    try {
      const ruleRef = doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogue.id,
        "srPriceRules",
        ruleId
      );
      await setDoc(
        ruleRef,
        {
          id: ruleId,
          ...payload,
          ...(draft.existingRuleId ? {} : { createdAt: serverTimestamp(), createdBy: user?.uid || "" }),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );

      if (draft.existingRuleId && draft.existingRuleId !== ruleId) {
        await updateDoc(
          doc(
            db,
            "users",
            workspaceUid,
            "fabricBrands",
            brand.id,
            "catalogues",
            catalogue.id,
            "srPriceRules",
            draft.existingRuleId
          ),
          {
            active: false,
            archived: true,
            replacedByRuleId: ruleId,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || "",
          }
        );
      }

      if (removeAfterSave) {
        removeFabricSrRuleDraftRow(draftKey, draft.clientId);
      }
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      if (!silent) {
        notify("SR pricing rule saved.", "success");
      }
      return true;
    } catch (error) {
      devError("FABRIC_SR_PRICE_RULE_SAVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
        ruleId,
      });
      notify("Could not save SR price rule.", "error");
      return false;
    } finally {
      setFabricCatalogueSavingKey("");
    }
  },
  [
    fabricCatalogueLibraryByBrand,
    getEmptyFabricCatalogueLibrary,
    loadFabricCatalogueLibrary,
    notify,
    openConfirmDialog,
    removeFabricSrRuleDraftRow,
    user?.uid,
    validateFabricSrRuleDraft,
    workspaceUid,
  ]
);

const saveAllFabricSrRuleDrafts = useCallback(
  async (brand, catalogue, { skipWarnings = false } = {}) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    const draftKey = `${brand.id}:${catalogue.id}`;
    const draftRows = getFabricSrRuleDraftRows(draftKey);
    if (!draftRows.length) {
      notify("Add at least one SR pricing rule first.", "info");
      return;
    }

    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      fabricCatalogueLibraryByBrand[brand.id] ||
      getEmptyFabricCatalogueLibrary(brand);
    const existingRules = library.rulesByCatalogueId?.[catalogue.id] || [];
    const validations = draftRows.map((draft) =>
      validateFabricSrRuleDraft({ draft, brand, catalogue, existingRules })
    );
    const firstError = validations.flatMap((entry) => entry.errors)[0];
    if (firstError) {
      notify(firstError, "warning");
      return;
    }
    const warnings = validations.flatMap((entry) => entry.warnings);
    if (warnings.length && !skipWarnings) {
      openConfirmDialog({
        title: "SR pricing overlap warning",
        description: [...new Set(warnings)].join(" "),
        confirmLabel: "Save All Anyway",
        cancelLabel: "Review",
        tone: "warning",
        onConfirm: () =>
          saveAllFabricSrRuleDrafts(brand, catalogue, { skipWarnings: true }),
      });
      return;
    }

    let savedCount = 0;
    for (const draft of draftRows) {
      const saved = await saveFabricSrRuleDraftRow(brand, catalogue, draft, {
        skipWarnings: true,
        removeAfterSave: true,
        silent: true,
      });
      if (saved) savedCount += 1;
    }

    if (savedCount > 0) {
      notify(
        `${savedCount} SR pricing rule${savedCount === 1 ? "" : "s"} saved.`,
        "success"
      );
    } else {
      notify("No SR pricing rules were saved.", "warning");
    }
  },
  [
    fabricCatalogueLibraryByBrand,
    getEmptyFabricCatalogueLibrary,
    getFabricSrRuleDraftRows,
    notify,
    openConfirmDialog,
    saveFabricSrRuleDraftRow,
    validateFabricSrRuleDraft,
    workspaceUid,
  ]
);

/* RESTORED_FROM_LAST_GOOD_BUILD: catalogue/settings helpers */
const archiveFabricCatalogue = useCallback(
  async (brand, catalogue) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    try {
      await updateDoc(
        doc(
          db,
          "users",
          workspaceUid,
          "fabricBrands",
          brand.id,
          "catalogues",
          catalogue.id
        ),
        {
          active: false,
          archived: true,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        }
      );
      cancelEditFabricCatalogue(brand, catalogue);
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify("Catalogue archived.", "success");
    } catch (error) {
      devError("FABRIC_CATALOGUE_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
      });
      notify("Could not archive catalogue.", "error");
    }
  },
  [cancelEditFabricCatalogue, loadFabricCatalogueLibrary, notify, user?.uid, workspaceUid]
);

const deleteFabricCatalogue = useCallback(
  async (brand, catalogue) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    const catalogueName = sanitizeEditableText(
      catalogue.catalogueName || catalogue.name || catalogue.id
    );
    openConfirmDialog({
      title: "Delete Catalogue?",
      description: `Are you sure you want to delete catalogue "${catalogueName}"? This action cannot be undone. Existing quotations will keep their saved fabric details.`,
      confirmLabel: "Yes, Delete",
      cancelLabel: "No, Cancel",
      tone: "danger",
      onConfirm: async () => {
        const savingKey = `${brand.id}:catalogue-delete:${catalogue.id}`;
        setFabricCatalogueSavingKey(savingKey);
        try {
          const catalogueRef = doc(
            db,
            "users",
            workspaceUid,
            "fabricBrands",
            brand.id,
            "catalogues",
            catalogue.id
          );
          const ruleDocs = await loadFabricCatalogueSrRuleDocsForMaintenance(
            brand.id,
            catalogue.id,
            "deleteFabricCatalogue.loadSrRules"
          );

          let batch = writeBatch(db);
          let operationCount = 0;
          const commitBatch = async () => {
            if (!operationCount) return;
            await batch.commit();
            batch = writeBatch(db);
            operationCount = 0;
          };

          for (const ruleDoc of ruleDocs) {
            if (operationCount >= 450) {
              await commitBatch();
            }
            batch.delete(ruleDoc.ref);
            operationCount += 1;
          }

          if (operationCount >= 450) {
            await commitBatch();
          }
          batch.delete(catalogueRef);
          operationCount += 1;
          await commitBatch();

          cancelEditFabricCatalogue(brand, catalogue);
          setFabricSrRuleDrafts((prev) => {
            const next = { ...prev };
            delete next[`${brand.id}:${catalogue.id}`];
            return next;
          });
          fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
          await loadFabricCatalogueLibrary(brand.id, { force: true });
          notify("Catalogue deleted.", "success");
        } catch (error) {
          devError("FABRIC_CATALOGUE_DELETE_FAILED", {
            code: error?.code,
            message: error?.message,
            workspaceUid,
            brandId: brand.id,
            catalogueId: catalogue.id,
          });
          notify(
            String(error?.code || "").includes("permission-denied")
              ? "You do not have permission to manage catalogues. Please contact your admin."
              : "Could not delete catalogue. Please try again.",
            "error"
          );
        } finally {
          setFabricCatalogueSavingKey("");
        }
      },
    });
  },
  [
    cancelEditFabricCatalogue,
    loadFabricCatalogueLibrary,
    loadFabricCatalogueSrRuleDocsForMaintenance,
    notify,
    openConfirmDialog,
    workspaceUid,
  ]
);

const archiveFabricSrPriceRule = useCallback(
  async (brand, catalogue, rule) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !rule?.id) return;
    try {
      await updateDoc(
        doc(
          db,
          "users",
          workspaceUid,
          "fabricBrands",
          brand.id,
          "catalogues",
          catalogue.id,
          "srPriceRules",
          rule.id
        ),
        {
          active: false,
          archived: true,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        }
      );
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify("SR price rule archived.", "success");
    } catch (error) {
      devError("FABRIC_SR_PRICE_RULE_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
        ruleId: rule.id,
      });
      notify("Could not archive SR price rule.", "error");
    }
  },
  [loadFabricCatalogueLibrary, notify, user?.uid, workspaceUid]
);

const deleteFabricSrPriceRule = useCallback(
  async (brand, catalogue, rule) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !rule?.id) return;
    openConfirmDialog({
      title: "Delete SR pricing rule?",
      description: `${getSrPriceRuleLabel(rule)} will be permanently removed from ${catalogue.catalogueName}. Archive is safer if you only want to stop using it.`,
      confirmLabel: "Delete Rule",
      cancelLabel: "Cancel",
      tone: "danger",
      onConfirm: async () => {
        try {
          await deleteDoc(
            doc(
              db,
              "users",
              workspaceUid,
              "fabricBrands",
              brand.id,
              "catalogues",
              catalogue.id,
              "srPriceRules",
              rule.id
            )
          );
          await loadFabricCatalogueLibrary(brand.id, { force: true });
          notify("SR pricing rule deleted.", "success");
        } catch (error) {
          devError("FABRIC_SR_PRICE_RULE_DELETE_FAILED", {
            code: error?.code,
            message: error?.message,
            workspaceUid,
            brandId: brand.id,
            catalogueId: catalogue.id,
            ruleId: rule.id,
          });
          notify("Could not delete SR price rule.", "error");
        }
      },
    });
  },
  [loadFabricCatalogueLibrary, notify, openConfirmDialog, workspaceUid]
);

const renderFabricBrandCatalogueManager = (brand) => {
  const library = getFabricCatalogueLibraryForBrand(brand.id);
  const catalogueDraft = fabricCatalogueDrafts[brand.id] || {
    catalogueName: "",
    priceUnit: "m",
    defaultRrpPerMeter: "",
    catalogueDefaultFabricWidthIn: "",
    notes: "",
  };
  const draftPriceUnit = catalogueDraft.priceUnit === "sqft" ? "sqft" : "m";
  const updateCatalogueDraft = (field, value) =>
    setFabricCatalogueDrafts((prev) => ({
      ...prev,
      [brand.id]: {
        ...(prev[brand.id] || {}),
        [field]: value,
      },
    }));
  const updateRuleDraft = (catalogueId, clientId, field, value) => {
    const draftKey = `${brand.id}:${catalogueId}`;
    updateFabricSrRuleDraftRow(draftKey, clientId, field, value);
  };
  const ruleCardStyle = {
    borderRadius: 14,
    padding: 12,
    border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid #e2e8f0",
    background: isDark ? "rgba(15,23,42,0.7)" : "#ffffff",
  };
  const miniLabelStyle = {
    fontSize: 11,
    fontWeight: 900,
    color: dashboardMuted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(2,6,23,0.38)" : "#f8fafc",
        border: isDark
          ? "1px solid rgba(96,165,250,0.16)"
          : "1px solid rgba(191,219,254,0.85)",
        display: "grid",
        gap: 14,
      }}
    >
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 900 }}>
          Catalogues / books for {brand.name}
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.55 }}>
          Add catalogue names once, then add exact, range, list, or default SR RRP
          rules. Quote rows resolve these locally after this brand library is loaded.
          {" "}Rules inherit the catalogue price unit. Prices retain the legacy storage
          names defaultRrpPerMeter / rrpPerMeter; changing units does not convert values.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(180px, 1.2fr) minmax(140px, 0.6fr) minmax(120px, 0.45fr) minmax(130px, 0.45fr) minmax(180px, 1fr) auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Catalogue / Book</span>
          <input
            style={input}
            value={catalogueDraft.catalogueName || ""}
            placeholder="Soft Drape, Wilbert, Vista"
            onChange={(event) =>
              updateCatalogueDraft("catalogueName", event.target.value)
            }
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Price Unit</span>
          <select
            style={selectInput}
            value={draftPriceUnit}
            onChange={(event) => updateCatalogueDraft("priceUnit", event.target.value)}
          >
            <option value="m">Per meter (/m)</option>
            <option value="sqft">Per square foot (/sqft)</option>
          </select>
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Default RRP/{draftPriceUnit}</span>
          <input
            type="text"
            inputMode="decimal"
            style={input}
            value={catalogueDraft.defaultRrpPerMeter || ""}
            placeholder="Optional"
            onChange={(event) =>
              updateCatalogueDraft("defaultRrpPerMeter", event.target.value)
            }
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Default Width (in)</span>
          <input
            type="text"
            inputMode="decimal"
            style={input}
            value={catalogueDraft.catalogueDefaultFabricWidthIn || ""}
            placeholder="54"
            onChange={(event) =>
              updateCatalogueDraft("catalogueDefaultFabricWidthIn", event.target.value)
            }
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Notes</span>
          <input
            style={input}
            value={catalogueDraft.notes || ""}
            placeholder="Optional"
            onChange={(event) => updateCatalogueDraft("notes", event.target.value)}
          />
        </label>
        <Button
          icon={Plus}
          variant="success"
          disabled={Boolean(fabricCatalogueSavingKey)}
          onClick={() => saveFabricCatalogueDraft(brand)}
        >
          Add Catalogue
        </Button>
      </div>

      {(library.catalogues || []).length ? (
        <div style={{ display: "grid", gap: 12 }}>
          {library.catalogues.map((catalogue) => {
            const draftKey = `${brand.id}:${catalogue.id}`;
            const draftRows = getFabricSrRuleDraftRows(draftKey);
            const rules = library.rulesByCatalogueId?.[catalogue.id] || [];
            const editDraft = fabricCatalogueEditDrafts[draftKey] || null;
            const priceUnit = catalogue.priceUnit === "sqft" ? "sqft" : "m";
            const isEditingCatalogue = Boolean(editDraft);
            const isCatalogueActionBusy = Boolean(fabricCatalogueSavingKey);
            const tableHeaderStyle = {
              padding: "8px 10px",
              fontSize: 10.5,
              fontWeight: 900,
              color: dashboardMuted,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid #e2e8f0",
            };
            const tableCellStyle = {
              padding: "9px 10px",
              fontSize: 12,
              borderBottom: isDark
                ? "1px solid rgba(148,163,184,0.1)"
                : "1px solid #edf2f7",
              verticalAlign: "middle",
            };
            return (
              <div key={catalogue.id} style={ruleCardStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    {isEditingCatalogue ? (
                      <div style={{ display: "grid", gap: 8, minWidth: isMobile ? "100%" : 320 }}>
                        <label style={{ display: "grid", gap: 5 }}>
                          <span style={miniLabelStyle}>Catalogue Name</span>
                          <input
                            style={input}
                            value={editDraft.catalogueName || ""}
                            placeholder="Catalogue name"
                            onChange={(event) =>
                              updateFabricCatalogueEditDraft(
                                draftKey,
                                "catalogueName",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label style={{ display: "grid", gap: 5 }}>
                          <span style={miniLabelStyle}>Price Unit</span>
                          <select
                            style={selectInput}
                            value={editDraft.priceUnit === "sqft" ? "sqft" : "m"}
                            disabled={isCatalogueActionBusy}
                            onChange={(event) =>
                              updateFabricCatalogueEditDraft(
                                draftKey,
                                "priceUnit",
                                event.target.value
                              )
                            }
                          >
                            <option value="m">Per meter (/m)</option>
                            <option value="sqft">Per square foot (/sqft)</option>
                          </select>
                        </label>
                        <div style={{ fontSize: 11.5, color: dashboardMuted }}>
                          Applies to the catalogue default and all SR rules after saving.
                          Changing units does not convert existing prices.
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => saveFabricCatalogueEditDraft(brand, catalogue)}
                            disabled={isCatalogueActionBusy}
                            style={{
                              border: 0,
                              background: isCatalogueActionBusy ? "#94a3b8" : "#2563eb",
                              color: "#ffffff",
                              fontSize: 12,
                              fontWeight: 900,
                              cursor: isCatalogueActionBusy ? "not-allowed" : "pointer",
                              padding: "7px 10px",
                              borderRadius: 10,
                            }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelEditFabricCatalogue(brand, catalogue)}
                            disabled={isCatalogueActionBusy}
                            style={{
                              border: isDark
                                ? "1px solid rgba(148,163,184,0.22)"
                                : "1px solid #cbd5e1",
                              background: isDark ? "#0f172a" : "#ffffff",
                              color: isDark ? "#e2e8f0" : "#334155",
                              fontSize: 12,
                              fontWeight: 900,
                              cursor: isCatalogueActionBusy ? "not-allowed" : "pointer",
                              padding: "6px 10px",
                              borderRadius: 10,
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, fontWeight: 900 }}>
                        {catalogue.catalogueName}
                      </div>
                    )}
                    <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
                      Price unit: /{priceUnit}{" | Default RRP: "}
                      {catalogue.defaultRrpPerMeter != null
                        ? `${currencySymbol(currency)} ${Number(
                            catalogue.defaultRrpPerMeter
                          ).toFixed(2)}/${priceUnit}`
                        : "Not set"}
                      {" | Default width: "}
                      {catalogue.catalogueDefaultFabricWidthIn ||
                      catalogue.defaultFabricWidthIn
                        ? `${Number(
                            catalogue.catalogueDefaultFabricWidthIn ||
                              catalogue.defaultFabricWidthIn
                          )} in`
                        : "Workspace default"}
                      {catalogue.notes ? ` | ${catalogue.notes}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {!isEditingCatalogue ? (
                      <button
                        type="button"
                        onClick={() => startEditFabricCatalogue(brand, catalogue)}
                        disabled={isCatalogueActionBusy}
                        style={{
                          border: 0,
                          background: "transparent",
                          color: "#2563eb",
                          fontSize: 12,
                          fontWeight: 900,
                          cursor: isCatalogueActionBusy ? "not-allowed" : "pointer",
                          padding: 0,
                        }}
                      >
                        Edit
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => archiveFabricCatalogue(brand, catalogue)}
                      disabled={isCatalogueActionBusy}
                      style={{
                        border: 0,
                        background: "transparent",
                        color: "#b45309",
                        fontSize: 12,
                        fontWeight: 900,
                        cursor: isCatalogueActionBusy ? "not-allowed" : "pointer",
                        padding: 0,
                      }}
                    >
                      Archive
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteFabricCatalogue(brand, catalogue)}
                      disabled={isCatalogueActionBusy}
                      style={{
                        border: 0,
                        background: "transparent",
                        color: "#dc2626",
                        fontSize: 12,
                        fontWeight: 900,
                        cursor: isCatalogueActionBusy ? "not-allowed" : "pointer",
                        padding: 0,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900 }}>
                        SR Pricing Rules
                      </div>
                      <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
                        One catalogue can contain many single, range, and list rules.
                        {" "}All rule prices are /{priceUnit}, inherited from this catalogue.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        icon={Plus}
                        variant="secondary"
                        disabled={Boolean(fabricCatalogueSavingKey)}
                        onClick={() => addFabricSrRuleDraftRow(brand, catalogue)}
                      >
                        Add SR Rule
                      </Button>
                      <Button
                        icon={Save}
                        variant="primary"
                        disabled={Boolean(fabricCatalogueSavingKey) || !draftRows.length}
                        onClick={() => saveAllFabricSrRuleDrafts(brand, catalogue)}
                      >
                        Save All Rules
                      </Button>
                    </div>
                  </div>

                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: 12,
                      border: isDark
                        ? "1px solid rgba(148,163,184,0.14)"
                        : "1px solid #e2e8f0",
                      background: isDark ? "rgba(2,6,23,0.28)" : "#ffffff",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        minWidth: 860,
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>Rule Type</th>
                          <th style={tableHeaderStyle}>From</th>
                          <th style={tableHeaderStyle}>To</th>
                          <th style={tableHeaderStyle}>SR List</th>
                          <th style={tableHeaderStyle}>RRP/{priceUnit}</th>
                          <th style={tableHeaderStyle}>Width</th>
                          <th style={tableHeaderStyle}>Notes</th>
                          <th style={tableHeaderStyle}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.length ? (
                          rules.map((rule) => (
                            <tr key={rule.id}>
                              <td style={tableCellStyle}>{getSrPriceRuleLabel(rule)}</td>
                              <td style={tableCellStyle}>{rule.ruleType === "range" ? rule.srFrom : "-"}</td>
                              <td style={tableCellStyle}>{rule.ruleType === "range" ? rule.srTo : "-"}</td>
                              <td style={tableCellStyle}>
                                {rule.ruleType === "list"
                                  ? (rule.normalizedSrNumbers || rule.srNumbers || []).join(", ")
                                  : rule.ruleType === "single"
                                  ? rule.srNumber
                                  : "-"}
                              </td>
                              <td style={tableCellStyle}>
                                {currencySymbol(currency)}{" "}
                                {Number(rule.rrpPerMeter || 0).toFixed(2)}/{priceUnit}
                              </td>
                              <td style={tableCellStyle}>
                                {rule.fabricWidthIn
                                  ? `${Number(rule.fabricWidthIn)} in`
                                  : catalogue.catalogueDefaultFabricWidthIn ||
                                    catalogue.defaultFabricWidthIn
                                  ? `${Number(
                                      catalogue.catalogueDefaultFabricWidthIn ||
                                        catalogue.defaultFabricWidthIn
                                    )} in default`
                                  : "Workspace default"}
                              </td>
                              <td style={tableCellStyle}>{rule.notes || "-"}</td>
                              <td style={tableCellStyle}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    onClick={() => editFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#2563eb",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => archiveFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#b45309",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Archive
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#dc2626",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={8}
                              style={{
                                ...tableCellStyle,
                                color: dashboardMuted,
                                textAlign: "center",
                              }}
                            >
                              No SR rules yet. Catalogue default will apply if it is set.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {draftRows.length ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      {draftRows.map((ruleDraft) => {
                        const draftValidation = validateFabricSrRuleDraft({
                          draft: ruleDraft,
                          brand,
                          catalogue,
                          existingRules: rules,
                        });
                        return (
                          <div
                            key={ruleDraft.clientId}
                            style={{
                              borderRadius: 12,
                              padding: 12,
                              border: isDark
                                ? "1px solid rgba(96,165,250,0.22)"
                                : "1px solid rgba(191,219,254,0.95)",
                              background: isDark ? "rgba(30,41,59,0.55)" : "#eff6ff",
                              display: "grid",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: isMobile
                                  ? "1fr"
                                  : "120px repeat(2, minmax(100px, 1fr)) minmax(150px, 1.2fr) 110px 120px minmax(140px, 1fr) auto",
                                gap: 8,
                                alignItems: "end",
                              }}
                            >
                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>Rule</span>
                                <select
                                  style={selectInput}
                                  value={ruleDraft.ruleType || "single"}
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "ruleType",
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="single">Single SR</option>
                                  <option value="range">SR Range</option>
                                  <option value="list">SR List</option>
                                </select>
                              </label>

                              {ruleDraft.ruleType === "range" ? (
                                <>
                                  <label style={{ display: "grid", gap: 4 }}>
                                    <span style={miniLabelStyle}>From</span>
                                    <input
                                      style={input}
                                      value={ruleDraft.srFrom || ""}
                                      placeholder="1"
                                      onChange={(event) =>
                                        updateRuleDraft(
                                          catalogue.id,
                                          ruleDraft.clientId,
                                          "srFrom",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label style={{ display: "grid", gap: 4 }}>
                                    <span style={miniLabelStyle}>To</span>
                                    <input
                                      style={input}
                                      value={ruleDraft.srTo || ""}
                                      placeholder="10"
                                      onChange={(event) =>
                                        updateRuleDraft(
                                          catalogue.id,
                                          ruleDraft.clientId,
                                          "srTo",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <div />
                                </>
                              ) : ruleDraft.ruleType === "list" ? (
                                <label style={{ display: "grid", gap: 4, gridColumn: isMobile ? "auto" : "span 3" }}>
                                  <span style={miniLabelStyle}>SR List</span>
                                  <textarea
                                    style={{ ...input, minHeight: 38, resize: "vertical" }}
                                    value={ruleDraft.srNumbers || ""}
                                    placeholder="12, 14, 18, 22"
                                    onChange={(event) =>
                                      updateRuleDraft(
                                        catalogue.id,
                                        ruleDraft.clientId,
                                        "srNumbers",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              ) : (
                                <label style={{ display: "grid", gap: 4, gridColumn: isMobile ? "auto" : "span 3" }}>
                                  <span style={miniLabelStyle}>SR Number</span>
                                  <input
                                    style={input}
                                    value={ruleDraft.srNumber || ""}
                                    placeholder="12"
                                    onChange={(event) =>
                                      updateRuleDraft(
                                        catalogue.id,
                                        ruleDraft.clientId,
                                        "srNumber",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              )}

                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>RRP/{priceUnit}</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  style={input}
                                  value={ruleDraft.rrpPerMeter || ""}
                                  placeholder="0"
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "rrpPerMeter",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>Width (in)</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  style={input}
                                  value={ruleDraft.fabricWidthIn || ""}
                                  placeholder={
                                    catalogue.catalogueDefaultFabricWidthIn ||
                                    catalogue.defaultFabricWidthIn
                                      ? String(
                                          catalogue.catalogueDefaultFabricWidthIn ||
                                            catalogue.defaultFabricWidthIn
                                        )
                                      : "54"
                                  }
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "fabricWidthIn",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>Notes</span>
                                <input
                                  style={input}
                                  value={ruleDraft.notes || ""}
                                  placeholder="Optional"
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "notes",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <Button
                                  icon={Save}
                                  variant="primary"
                                  disabled={Boolean(fabricCatalogueSavingKey)}
                                  onClick={() =>
                                    saveFabricSrRuleDraftRow(brand, catalogue, ruleDraft)
                                  }
                                >
                                  Save
                                </Button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeFabricSrRuleDraftRow(draftKey, ruleDraft.clientId)
                                  }
                                  style={{
                                    border: 0,
                                    background: "transparent",
                                    color: "#dc2626",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    padding: 0,
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            {draftValidation.warnings.length ? (
                              <div
                                style={{
                                  padding: "8px 10px",
                                  borderRadius: 10,
                                  background: isDark
                                    ? "rgba(245,158,11,0.18)"
                                    : "#fffbeb",
                                  color: isDark ? "#fde68a" : "#92400e",
                                  fontSize: 11.5,
                                  fontWeight: 800,
                                }}
                              >
                                {draftValidation.warnings[0]}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: dashboardMuted }}>
                      Click Add SR Rule to add one or more pricing rows under this catalogue.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          No catalogues added for this brand yet.
        </div>
      )}
    </div>
  );
};

useEffect(() => {
  const shouldLoadProducts =
    Boolean(workspaceUid) && (tab === "quote" || tab === "products");
  if (!shouldLoadProducts) return;
  if (
    quotationSettingsLoadedWorkspaceRef.current !== workspaceUid &&
    quotationSettingsLoadingWorkspaceRef.current === workspaceUid
  ) {
    return;
  }
  if (productConfigsLoadedWorkspaceRef.current === workspaceUid) return;
  if (productConfigsLoadingWorkspaceRef.current === workspaceUid) return;

  productConfigsLoadingWorkspaceRef.current = workspaceUid;
  setProductConfigsLoadState({
    loading: true,
    loadedWorkspace: productConfigsLoadedWorkspaceRef.current,
    error: "",
  });

  const loadProductConfigs = async () => {
    try {
      const productRef = collection(
        db,
        "users",
        workspaceUid,
        PRODUCT_CONFIG_COLLECTION
      );
      const snap = await getDocsCached(productRef, {
        label: "loadProductConfigs",
        cacheKey: `productConfigs:${workspaceUid}`,
        ttlMs: 5 * 60 * 1000,
      });
      const fetched = snap.docs.map((docSnap) =>
        normalizeProductConfig({ id: docSnap.id, ...docSnap.data() })
      );
      const fetchedIds = new Set(fetched.map((config) => String(config.id || "")));
      const productLegacySettings = { ...fabricCalcSettings, minSqft };
      const defaultProductConfigs = buildDefaultProductConfigs(productLegacySettings);
      const missingDefaults = defaultProductConfigs.filter(
        (config) => !fetchedIds.has(config.id)
      );

      if (missingDefaults.length > 0 && (isWorkspaceOwner || canAccessTeamPermission("settings"))) {
        await Promise.all(
          missingDefaults.map((config) =>
            setDoc(
              doc(db, "users", workspaceUid, PRODUCT_CONFIG_COLLECTION, config.id),
              {
                ...normalizeProductConfig(config),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            )
          )
        );
      }

      setProductConfigs(mergeProductConfigsWithDefaults(fetched, productLegacySettings));
      productConfigsLoadedWorkspaceRef.current = workspaceUid;
      setProductConfigsLoadState({
        loading: false,
        loadedWorkspace: workspaceUid,
        error: "",
      });
    } catch (error) {
      devError("Failed to load product configs:", error);
      setProductConfigs(mergeProductConfigsWithDefaults([], { ...fabricCalcSettings, minSqft }));
      setProductConfigsLoadState({
        loading: false,
        loadedWorkspace: productConfigsLoadedWorkspaceRef.current,
        error: error?.message || "Could not load product configs.",
      });
    } finally {
      if (productConfigsLoadingWorkspaceRef.current === workspaceUid) {
        productConfigsLoadingWorkspaceRef.current = "";
      }
    }
  };

  loadProductConfigs();
}, [fabricCalcSettings, minSqft, quotationSettingsReadyWorkspace, tab, workspaceUid]);


useEffect(() => {
  if (editingQuoteId) {
    devLog("EDIT MODE LOCKED:", editingQuoteId);
  }
}, [editingQuoteId]);

function getVerticalRepeatMeters(
  fabricKey,
  sourceRows = [],
  activeRepeatSettings = repeatSettings
) {
  const key = normalizeFabricKey(fabricKey);
  const rowsForFabric = (Array.isArray(sourceRows) ? sourceRows : []).filter(
    (row) => normalizeFabricKey(getCurtainRowFabricKey(row)) === key
  );

  if (rowsForFabric.length > 0) {
    const maxRepeatInch = rowsForFabric.reduce((maxValue, row) => {
      const repeat = getEffectiveRepeatForCurtainRow(row, activeRepeatSettings);
      return repeat.verticalEnabled
        ? Math.max(maxValue, Number(repeat.verticalRepeatInch || 0))
        : maxValue;
    }, 0);
    return maxRepeatInch > 0 ? maxRepeatInch / 39.37 : 0;
  }

  const settings = normalizeCurtainRepeatEntry(activeRepeatSettings?.[key] || {});
  if (!settings || !settings.verticalEnabled) return 0;

  return (Number(settings.verticalRepeatInch) || 0) / 39.37;
}


const filteredStockHistory = useMemo(() => {
  const queryText = stockHistorySearch.toLowerCase().trim();
  if (!queryText) return stockHistory;

  return stockHistory.filter((h) => {
    const customer = (h.customerName || "").toLowerCase();
    const quoteId = (h.quoteId || "").toLowerCase();
    const quoteLabel = (h.quoteLabel || "").toLowerCase();

    const fabricsText = Array.isArray(h.fabrics)
      ? h.fabrics
          .map((f) => f.fabricKey || "")
          .join(" ")
          .toLowerCase()
      : (h.fabricKey || "").toLowerCase();

    return (
      customer.includes(queryText) ||
      quoteId.includes(queryText) ||
      quoteLabel.includes(queryText) ||
      fabricsText.includes(queryText)
    );
  });
}, [stockHistory, stockHistorySearch]);






useEffect(() => {
  tcoWorkerRef.current = new Worker("/tcoWorker.js");

  tcoWorkerRef.current.onmessage = async (e) => {
    const { ok, result, error } = e.data;
    setIsOptimizing(false);

    if (!ok) {
      alert(error || "Track optimizer failed");
      return;
    }

    const rawLog = Array.isArray(result.log) ? result.log : [];
    const finalStock = result.newStock || {};

    const mappedLog = rawLog.map((entry, idx) => {
      const usedIn = Number(
        entry.usedIn ?? entry.usedFrom ?? entry.usedSum ?? 0
      );
      const leftoverIn = Number(entry.leftoverGenerated ?? 0);

      return {
        ...entry,
        usedFt: Math.round((usedIn / 12) * 100) / 100,
        leftoverFt: Math.round((leftoverIn / 12) * 100) / 100,
        trackId: entry.trackId || `T${idx + 1}`,
      };
    });

    setTrackCutPlan(mappedLog);
    setTrackLeftovers(finalStock);
  };

  tcoWorkerRef.current.onerror = (err) => {
    devError("TCO Worker crashed:", err);
    setIsOptimizing(false);
    alert("Track optimizer worker crashed.");
  };

  return () => tcoWorkerRef.current?.terminate();
}, []);


/* ============================================================
   SAVE SETTINGS  FINAL W1 VERSION
   ============================================================ */
const saveSettings = async (options = {}) => {
  const saveOptions =
    options && typeof options === "object" && !options.nativeEvent ? options : {};
  if (isSavingSettings) return;

  const stitchingSettingsResult = buildStitchingSettingsForSave(stitchingRates);
  if (stitchingSettingsResult.errors.length) {
    notify(stitchingSettingsResult.errors[0], "error", 5200);
    return;
  }

  setIsSavingSettings(true);
  try {
    if (!user || !workspaceUid) {
      notify("Please sign in first.", "warning");
      return;
    }
    const normalizedTeamMembers = normalizeTeamMembers(teamMembers);

    let finalLogoURL = logoURL; // Keep existing logo if no new upload

    /* --------------------- LOGO UPLOAD --------------------- */
    if (selectedLogoFile) {
      setUploading(true);

      const logoImageId = createClientId("logo");
      const logoPath = `logos/${sanitizeFirestoreDocIdSegment(workspaceUid)}/${logoImageId}-${Date.now()}-${normalizeFileName(
        selectedLogoFile.name || "branding-logo"
      )}`;
      const managedLogo = await uploadManagedImage({
        workspaceUid,
        file: selectedLogoFile,
        ownerType: "logo",
        ownerId: "branding",
        imageId: logoImageId,
        fullPath: logoPath,
        uploadedBy: user?.uid || "",
        preserveTransparency: true,
        metadata: {
          workspaceUid,
          purpose: "branding_logo",
        },
      });

      finalLogoURL = managedLogo.downloadURL;
      setLogoURL(finalLogoURL);

      setUploading(false);
      setUploadProgress(100);
    }

    /* --------------------- FIRESTORE SAVE --------------------- */
    const settingsRef = doc(db, "users", workspaceUid, "settings", "main");
    const previousSettingsSnap = await getDoc(settingsRef);
    const previousTeamMembers = normalizeTeamMembers(
      previousSettingsSnap.exists() ? previousSettingsSnap.data()?.teamMembers : []
    );
    const fabricCalcSettingsForSave =
      normalizeFabricCalcSettingsForState(fabricCalcSettings);

await setDoc(
  settingsRef,
  {
    fabricCalcSettings: fabricCalcSettingsForSave,
    stitching: stitchingSettingsResult.normalized,
    businessName,
    businessPhone,
    businessEmail,
    currency,
    crmPipelines,
    ...(process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 === 'true' ? {} : { teamMembers: normalizedTeamMembers }),
    shopAddress,
    minSqft,
    unit,
    logoURL: finalLogoURL,
    updatedAt: serverTimestamp(),
  },
  { merge: true }
);

    invalidateFirestoreReadCache(settingsRef);
    clearOperationalSettingsCache();
    mainSettingsLoadedWorkspaceRef.current = "";
    setFabricCalcSettings(fabricCalcSettingsForSave);
    commitCurtainRows(
      (prevRows) => prevRows.map((row) => ({ ...row })),
      QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY
    );
    setEffectiveRows((prevRows) => prevRows.map((row) => ({ ...row })));


    const employeeResults = await syncWorkspaceMemberProfiles(
      normalizedTeamMembers,
      workspaceUid,
      previousTeamMembers
    );
    setSelectedLogoFile(null);
    setTeamMembers(normalizedTeamMembers.map(member => ({ ...member,
      ...employeeResults?.find(result => result.uid === member.uid) })));
    setStitchingRates({ ...stitchingSettingsResult.normalized });
    setIsStitchingSettingsDirty(false);
    notify(saveOptions.successMessage || "Settings saved successfully.", "success");
  } catch (error) {
    devError("Error saving settings:", error);
    notify(
      error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : "Failed to save settings. Check console for details.",
      "error",
      5200
    );
  } finally {
    setIsSavingSettings(false);
    setUploading(false);
  }
};



/* ============================================================
   LOAD USER SETTINGS  FINAL W1 VERSION
   ============================================================ */
const loadUserSettings = useCallback(async (uid) => {
  try {
    if (!uid) return;
    if (mainSettingsLoadedWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        devLog("Settings load skipped; workspace already loaded", { uid });
      }
      return;
    }
    if (mainSettingsLoadingWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        devLog("Settings load skipped; workspace load already in flight", { uid });
      }
      return;
    }
    mainSettingsLoadingWorkspaceRef.current = uid;

    // Correct W1 path
    devLog("workspaceUid:", uid);
    devLog("module:", "Settings");
    devLog("collectionPath:", `users/${uid}/settings/main`);
    const snap = await loadOperationalSettingsSnapshot(functions, auth.currentUser?.uid, uid, 'main');
    if (isDevRuntime) {
      devLog("Settings exists:", snap.exists());
    }
    const data = snap.exists() ? snap.data() : {};

setFabricCalcSettings(
  normalizeFabricCalcSettingsForState(data.fabricCalcSettings)
);

    /* ---------------------------
       NORMALISE STITCHING
       --------------------------- */

   const raw =
  data.stitching && typeof data.stitching === "object"
    ? data.stitching
    : {};
const normalised = {};

// 1) Load EXACT USER-SAVED FIRST  never lose new entries
for (const [name, vals] of Object.entries(raw)) {
  normalised[name] = normalizeStitchingSettingForState(vals);
}

// 2) Add defaults ONLY IF missing AND ensure no overwrite
for (const [name, def] of Object.entries(DEFAULT_STITCHING)) {
  if (!Object.hasOwn(normalised, name)) {
    normalised[name] = normalizeStitchingSettingForState(def);
  }
}

// ALWAYS preserve user-added order
setStitchingRates({ ...normalised });
setIsStitchingSettingsDirty(false);


    
    /* ---------------------------
       BASIC SETTINGS
       --------------------------- */

    setCurrency(data.currency || "Rs. Indian Rupee");
    setBusinessName(data.businessName || "");
    setBusinessPhone(data.businessPhone || "");
    setBusinessEmail(data.businessEmail || "");
    setShopAddress(data.shopAddress || "");
    setTeamMembers(normalizeTeamMembers(data.teamMembers));
    setCrmPipelines(normalizeCrmPipelines(data.crmPipelines));
    setMinSqft(data.minSqft || DEFAULT_MIN_SQFT);
    setUnit(data.unit || "in");   // <-- ADD THIS

    /* ---------------------------
       LOAD LOGO
       --------------------------- */
    try {
      const logoRef = ref(storage, `logos/${uid}/branding-logo.png`);
      const url = await getDownloadURL(logoRef);
      setLogoURL(url);
    } catch (e) {
      // The authorized settings snapshot may already contain a valid token URL.
      // A failed SDK metadata lookup must not erase that same-workspace logo.
      const storedLogo = String(data.logoURL || "");
      const logoImage = createGeneralQuoteImage({ url: storedLogo });
      setLogoURL(getGeneralQuoteImageStoragePath(logoImage) === `logos/${uid}/branding-logo.png`
        && logoImage.storageBucket === firebaseApp.options.storageBucket
        && hasFirebaseStorageDownloadToken(storedLogo) ? storedLogo : "");
    }
    mainSettingsLoadedWorkspaceRef.current = uid;

    /* ---------------------------
       LOAD QUOTES (W1 path)
       --------------------------- */
  

  } catch (error) {
    devError("Error loading user settings:", error);
    notify('Quotation settings could not be loaded. Please contact your account administrator.', 'error');
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${uid}/settings/main`,
        source: "loadUserSettings",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    if (mainSettingsLoadingWorkspaceRef.current === uid) {
      mainSettingsLoadingWorkspaceRef.current = "";
    }
  }
}, [notify, raiseFirebaseHealthWarning]);


/* --------------------------- LOAD TRACK STOCK (W1) --------------------------- */
const loadTrackStock = useCallback(async (uid) => {
  try {
    if (!uid) return;
    if (trackStockLoadedWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        devLog("Track stock load skipped; workspace already loaded", { uid });
      }
      return;
    }
    if (trackStockLoadingWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        devLog("Track stock load skipped; workspace load already in flight", { uid });
      }
      return;
    }
    trackStockLoadingWorkspaceRef.current = uid;

    //  Correct new W1 path
    devLog("workspaceUid:", uid);
    devLog("module:", "Track Stock");
    devLog("collectionPath:", `users/${uid}/trackStock/main`);
    const refDoc = doc(db, "users", uid, "trackStock", "main");
    const snap = await getDocCached(refDoc, {
      label: "loadTrackStock",
      cacheKey: `users/${uid}/trackStock/main`,
      ttlMs: 5 * 60 * 1000,
    });
    if (isDevRuntime) {
      devLog("Track Stock exists:", snap.exists());
    }

    if (snap.exists()) {
      setTrackLeftovers(snap.data().stock || {});
    } else {
      setTrackLeftovers({});
    }
    trackStockLoadedWorkspaceRef.current = uid;
  } catch (err) {
    devError("Error loading track stock:", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${uid}/trackStock/main`,
        source: "loadTrackStock",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    if (trackStockLoadingWorkspaceRef.current === uid) {
      trackStockLoadingWorkspaceRef.current = "";
    }
  }
}, [notify, raiseFirebaseHealthWarning]);

/* ========================= FETCH FABRIC STOCK (NORMALIZED + SAFE) ========================= */

const fetchFabricStock = useCallback(async () => {
  try {
    if (!workspaceUid) return;

    setLoadingFabricStock(true);

    const collectionPath = `users/${workspaceUid}/fabricStock`;
    logWorkspaceQuery("Fabric Stock", collectionPath);
    const snap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "fabricStock"),
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(FABRIC_STOCK_READ_LIMIT)
      ),
      {
        label: "fetchFabricStock",
        cacheKey: `${collectionPath}?limit=${FABRIC_STOCK_READ_LIMIT}`,
        ttlMs: 60 * 1000,
      }
    );
    logWorkspaceQuery("Fabric Stock", collectionPath, snap.size);

    const list = snap.docs.map((doc) => {
      const d = doc.data();

      let buckets = [];

      /* ---------- CASE 1: NEW FORMAT ---------- */
      if (Array.isArray(d.buckets)) {
        buckets = d.buckets.map((b) => {
          let length = Number(b.length) || 0;
          let type = b.type;

          //  Normalize legacy / invalid bucket types
          if (type === "NEGATIVE") {
            type = "LEFTOVER";
          }

          //  Roll should NEVER be negative
          if (type === "ROLL" && length < 0) {
            type = "LEFTOVER";
          }

          return {
            ...b,
            type,
            length,
          };
        });
      }

      /* ---------- CASE 2: OLD FORMAT (rolls / loose) ---------- */
      else {
        if (Array.isArray(d.rolls)) {
          d.rolls.forEach((r) => {
            const len = Number(r);
            if (len !== 0) {
              buckets.push({
                type: "ROLL",
                length: len,
              });
            }
          });
        }

        if (Number(d.loose)) {
          buckets.push({
            type: "LEFTOVER",
            length: Number(d.loose),
          });
        }
      }

      /* ---------- FINAL CLEANUP ---------- */

      // Remove zero-length junk
      buckets = buckets.filter(
        (b) => Number(b.length) !== 0
      );

      return {
        fabricKey: doc.id,
        buckets,
      };
    });

    setFabricStock(list);
  } catch (err) {
    devError("Failed to load fabric stock", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${workspaceUid}/fabricStock`,
        source: "fetchFabricStock",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    } else {
      alert("Failed to load fabric stock");
    }
  } finally {
    setLoadingFabricStock(false);
  }
}, [logWorkspaceQuery, notify, raiseFirebaseHealthWarning, workspaceUid]);

/* ========================= FETCH STOCK HISTORY (W1) ========================= */
const fetchStockHistory = useCallback(async () => {
  try {
    if (!workspaceUid) return;

    setLoadingStockHistory(true);

    const collectionPath = `users/${workspaceUid}/stockHistory`;
    logWorkspaceQuery("Stock History", collectionPath);
    let snap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "stockHistory"),
        firestoreOrderBy("triggeredAt", "desc"),
        firestoreLimit(STOCK_HISTORY_READ_LIMIT)
      ),
      {
        label: "fetchStockHistory",
        cacheKey: `${collectionPath}?orderBy=triggeredAt_desc&limit=${STOCK_HISTORY_READ_LIMIT}`,
        ttlMs: 60 * 1000,
      }
    );
    if (snap.size === 0) {
      snap = await getDocsCached(
        firestoreQuery(
          collection(db, "users", workspaceUid, "stockHistory"),
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(STOCK_HISTORY_READ_LIMIT)
        ),
        {
          label: "fetchStockHistoryLegacyPage",
          cacheKey: `${collectionPath}?legacyDocIdLimit=${STOCK_HISTORY_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      );
    }
    logWorkspaceQuery("Stock History", collectionPath, snap.size);

    const history = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      .sort((a, b) => {
        const ta = a.triggeredAt?.toMillis?.() || 0; //  FIXED
        const tb = b.triggeredAt?.toMillis?.() || 0;
        return tb - ta;
      });

    setStockHistory(history);
  } catch (err) {
    devError("Failed to load stock history", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${workspaceUid}/stockHistory`,
        source: "fetchStockHistory",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    setLoadingStockHistory(false);
  }
}, [logWorkspaceQuery, notify, raiseFirebaseHealthWarning, workspaceUid]);

const shouldSubscribeSavedQuotes = useMemo(
  () => tab === "saved",
  [tab]
);

const shouldSubscribeCrmLeads = useMemo(
  () =>
    tab === "todayTasks" ||
    tab === "executionSpecs" ||
    (tab === "crm" &&
      ["overview", "list", "board", "leads"].includes(crmView) &&
      canViewAllCrmRecords),
  [canViewAllCrmRecords, crmView, tab]
);

const shouldSubscribeCustomerLedger = useMemo(
  () => tab === "customerLedger" && canViewAllCrmRecords,
  [canViewAllCrmRecords, tab]
);

const shouldSubscribeCrmReminders = useMemo(
  () =>
    tab === "todayTasks" ||
    (tab === "crm" &&
      ["overview", "list", "board", "leads"].includes(crmView) &&
      canViewAllCrmRecords) ||
    (tab === "crm" && crmView === "attention" && canViewAttentionRequiredWorkspace),
  [canViewAllCrmRecords, canViewAttentionRequiredWorkspace, crmView, tab]
);

const shouldSubscribeCrmTasks = useMemo(
  () =>
    tab === "todayTasks" ||
    (tab === "crm" &&
      ["overview", "attention"].includes(crmView) &&
      crmSettings.enableAttentionRequired !== false &&
      (crmView === "overview" || canViewAttentionRequiredWorkspace)),
  [canViewAttentionRequiredWorkspace, crmSettings.enableAttentionRequired, crmView, tab]
);

const shouldLoadCrmAttention = useMemo(
  () =>
    crmSettings.enableAttentionRequired !== false &&
    canViewAttentionRequiredWorkspace &&
    (tab === "todayTasks" ||
      (tab === "crm" && ["overview", "attention", CRM_PIPELINE_REVIEW_TAB].includes(crmView))),
  [canViewAttentionRequiredWorkspace, crmSettings.enableAttentionRequired, crmView, tab]
);

const shouldSubscribeCrmExecutionTemplates = useMemo(
  () =>
    (tab === "crm" && ["overview", "list", "board", "leads"].includes(crmView)) ||
    tab === "executionSpecs" ||
    tab === "executionTemplates" ||
    (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE),
  [crmView, quoteSubTab, tab]
);

useEffect(() => {
  if (!isDevRuntime || !canAccessCrm) return;
  const screen = tab === "crm" ? crmView : tab;
  const managerScreens = new Set(["attention", CRM_PIPELINE_REVIEW_TAB, "settings"]);
  const allowed =
    screen === "attention"
      ? canViewAttentionRequiredWorkspace
      : screen === CRM_PIPELINE_REVIEW_TAB
      ? canViewPipelineReview
      : screen === "settings"
      ? canManageCrmSettings
      : managerScreens.has(screen)
      ? false
      : true;
  devLog("CRM_PERMISSION_TRACE", {
    userId: user?.uid || "",
    role: currentCrmRole || "",
    canAccessCrm,
    isCrmOwnerAdminAccess,
    canViewManagerCrmDashboard,
    canViewAttentionRequiredWorkspace,
    canViewPipelineReview,
    canManageCrmSettings,
    canViewCrmPayments: canViewCrmPaymentsAccess,
    canEditCrmTargets: canEditCrmTargetsAccess,
    screen,
    allowed,
  });
}, [
  canAccessCrm,
  canEditCrmTargetsAccess,
  canManageCrmSettings,
  canViewAttentionRequiredWorkspace,
  canViewCrmPaymentsAccess,
  canViewManagerCrmDashboard,
  canViewPipelineReview,
  crmView,
  currentCrmRole,
  isCrmOwnerAdminAccess,
  tab,
  user?.uid,
]);

/* END RESTORED_FROM_LAST_GOOD_BUILD: catalogue/settings helpers */

/* --------------------------- DELETE QUOTATION --------------------------- */
const deleteQuotation = async (quoteInput) => {
  if (!canEditQuotes) {
    alert("You do not have permission to delete quotations in this workspace.");
    return;
  }

  const quote =
    quoteInput && typeof quoteInput === "object"
      ? quoteInput
      : savedQuotesRef.current.find((item) =>
          savedQuoteMatchesWorkflowIdentity(
            item,
            getSavedQuoteWorkflowIdentity({ id: quoteInput, quoteId: quoteInput }, workspaceUid)
          )
        ) || { id: quoteInput, quoteId: quoteInput };
  const busyKey = getSavedQuoteWorkflowBusyKey(quote, "moveToBin", workspaceUid);

  if (!workspaceUid || !quote) return;

  openConfirmDialog({
    title: "Move quotation to Recycle Bin?",
    description:
      "This quotation will move out of Saved Quotes and can be restored from Recycle Bin.",
    confirmLabel: "Move to Bin",
    cancelLabel: "Cancel",
    tone: "warning",
    onConfirm: async () => {
      setSavedQuoteActionBusyKey(busyKey);
      try {
        const result = await performSavedQuoteAction({
          quote,
          action: "moveToBin",
        });
        notify(result.successMessage || "Quotation moved to Recycle Bin.", "success");
      } catch (error) {
        if (isDevRuntime) {
          const identity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
          devError("SAVED_QUOTE_MOVE_TO_BIN_FAILED_EXACT", {
            code: error?.code,
            message: error?.message,
            stack: error?.stack,
            quoteIndexPath: identity.quoteIndexId
              ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
              : "",
            fullQuotePath: identity.sourcePath,
          });
        }
        notify(
          getSavedQuoteWorkflowFriendlyError(
            error,
            "Could not move quotation to bin. Please try again."
          ),
          "error"
        );
      } finally {
        setSavedQuoteActionBusyKey("");
      }
    },
  });
};


/* --------------------------- RESTORE QUOTATION --------------------------- */
const restoreQuotation = async (quoteInput) => {
  if (!canEditQuotes) {
    alert("You do not have permission to restore quotations in this workspace.");
    return;
  }

  const quote =
    quoteInput && typeof quoteInput === "object"
      ? quoteInput
      : deletedQuotesRef.current.find((item) =>
          savedQuoteMatchesWorkflowIdentity(
            item,
            getSavedQuoteWorkflowIdentity({ id: quoteInput, quoteId: quoteInput }, workspaceUid)
          )
        ) ||
        savedQuotesRef.current.find((item) =>
          savedQuoteMatchesWorkflowIdentity(
            item,
            getSavedQuoteWorkflowIdentity({ id: quoteInput, quoteId: quoteInput }, workspaceUid)
          )
        ) ||
        { id: quoteInput, quoteId: quoteInput };
  const busyKey = getSavedQuoteWorkflowBusyKey(quote, "restoreFromBin", workspaceUid);

  if (!workspaceUid || !quote) return;

  setSavedQuoteActionBusyKey(busyKey);
  try {
    const result = await performSavedQuoteAction({
      quote,
      action: "restoreFromBin",
    });
    notify(result.successMessage || "Quotation restored successfully.", "success");
  } catch (error) {
    if (isDevRuntime) {
      const identity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
      devError("SAVED_QUOTE_RESTORE_FAILED_EXACT", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        quoteIndexPath: identity.quoteIndexId
          ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
          : "",
        fullQuotePath: identity.sourcePath,
      });
    }
    notify(
      getSavedQuoteWorkflowFriendlyError(
        error,
        "Could not restore quotation. Please try again."
      ),
      "error"
    );
  } finally {
    setSavedQuoteActionBusyKey("");
  }
};


/* --------------------------- LOAD QUOTATION --------------------------- */


/* RESTORED_FROM_LAST_GOOD_BUILD: workspace/load/save helpers */
/* --------------------------- AUTH BOOTSTRAP --------------------------- */
useEffect(() => {
  const bootstrapUser = accountCurrentUser || auth.currentUser || null;
  const runId = workspaceBootstrapRunRef.current + 1;
  workspaceBootstrapRunRef.current = runId;
  let cancelled = false;
  let timeoutId = null;
  let lastCompletedStep = "";
  let loadingStep = accountLoading ? "account-loading" : "auth-state-start";

  const setDebug = (patch = {}) => {
    if (cancelled || workspaceBootstrapRunRef.current !== runId) return;
    setWorkspaceBootstrapDebug(
      buildWorkspaceBootstrapDebug({
        authUid: bootstrapUser?.uid || "",
        email: bootstrapUser?.email || "",
        workspaceUid: accountWorkspaceUid || "",
        loadingStep,
        lastCompletedStep,
        ...patch,
      })
    );
  };

  logAppStartupTrace({ step: "auth-state-start" });
  setUser(bootstrapUser);

  if (accountLoading) {
    setIsResolvingWorkspace(true);
    setWorkspaceBootstrapError(null);
    setDebug({ loadingStep: "account-loading" });
    logAppStartupTrace({ step: "auth-state-waiting-for-account" });
    return () => {
      cancelled = true;
    };
  }

  logAppStartupTrace({
    step: "auth-state-resolved",
    uid: bootstrapUser?.uid || "",
    email: bootstrapUser?.email || "",
  });

  if (!bootstrapUser?.uid) {
    setWorkspaceContext(null);
    setTeamMembers([]);
    setSavedQuotes([]);
    setDashboardQuotePreview([]);
    setCrmLeadRecords([]);
    setCustomerRecords([]);
    setWorkspaceBootstrapError(null);
    loadingStep = "signed-out";
    lastCompletedStep = "signed-out-cleanup";
    setDebug({
      authUid: "",
      email: "",
      workspaceUid: "",
      loadingStep,
      lastCompletedStep,
    });
    setIsResolvingWorkspace(false);
    return () => {
      cancelled = true;
    };
  }

  const finishWithError = (error, fallbackCode = "workspace_bootstrap_failed") => {
    if (cancelled || workspaceBootstrapRunRef.current !== runId) return;
    const errorCode = error?.code || error?.debug?.code || fallbackCode;
    const errorMessage =
      error?.message ||
      "We could not load your workspace. Please refresh or contact support.";
    const debug = buildWorkspaceBootstrapDebug({
      authUid: bootstrapUser.uid,
      email: bootstrapUser.email || "",
      workspaceUid: accountWorkspaceUid || "",
      loadingStep,
      lastCompletedStep,
      errorCode,
      errorMessage,
    });
    devError("FABRIQUOTE_WORKSPACE_LOAD_FAILED", {
      step: "workspace-load-failed",
      ...debug,
      stack: error?.stack || "",
    });
    setWorkspaceBootstrapDebug(debug);
    setWorkspaceBootstrapError({
      code: errorCode,
      message:
        String(errorCode).toLowerCase().includes("permission") ||
        String(error?.message || "").toLowerCase().includes("permission")
          ? "We could not load your account. Missing or insufficient permissions."
          : errorMessage,
    });
    setWorkspaceContext(null);
    setIsResolvingWorkspace(false);
  };

  const bootstrapWorkspace = async () => {
    setIsResolvingWorkspace(true);
    setWorkspaceBootstrapError(null);
    loadingStep = "workspace-load-start";
    setDebug({ loadingStep });
    logAppStartupTrace({ step: "workspace-load-start" });

    timeoutId = window.setTimeout(() => {
      const timeoutError = new Error(
        "We could not load your workspace. Please refresh or contact support."
      );
      timeoutError.code = "workspace_timeout";
      finishWithError(timeoutError, "workspace_timeout");
    }, WORKSPACE_BOOTSTRAP_TIMEOUT_MS);

    try {
      const resolvedWorkspace = await resolveWorkspaceState(bootstrapUser);
      if (cancelled || workspaceBootstrapRunRef.current !== runId) return;

      setWorkspaceContext(resolvedWorkspace);
      const resolvedWorkspaceUid = resolvedWorkspace?.workspaceId || bootstrapUser.uid;
      lastCompletedStep = "workspace-load-success";
      loadingStep = "settings-load-start";
      setDebug({
        workspaceUid: resolvedWorkspaceUid,
        loadingStep,
        lastCompletedStep,
      });
      logAppStartupTrace({
        step: "workspace-load-success",
        workspaceUid: resolvedWorkspaceUid,
      });

      await loadUserSettings(resolvedWorkspaceUid);
      if (cancelled || workspaceBootstrapRunRef.current !== runId) return;

      lastCompletedStep = "settings-load-success";
      loadingStep = "app-render-ready";
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = null;
      setWorkspaceBootstrapError(null);
      setDebug({
        workspaceUid: resolvedWorkspaceUid,
        loadingStep,
        lastCompletedStep,
      });
      logAppStartupTrace({
        step: "app-render-ready",
        workspaceUid: resolvedWorkspaceUid,
      });
      setIsResolvingWorkspace(false);
    } catch (error) {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = null;
      if (isFirestoreResourceExhausted(error)) {
        raiseFirebaseHealthWarning("firestore", error, {
          path: `users/${bootstrapUser.uid}/settings/main`,
          source: "workspaceBootstrap",
        });
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      finishWithError(error);
    }
  };

  bootstrapWorkspace();

  return () => {
    cancelled = true;
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}, [
  accountCurrentUser,
  accountLoading,
  accountWorkspaceUid,
  loadUserSettings,
  notify,
  raiseFirebaseHealthWarning,
  resolveWorkspaceState,
]);


/* ================= LOAD STOCK + HISTORY WHEN STOCK TAB OPENS ================= */
useEffect(() => {
  if (tab === "stock" && workspaceUid) {
    fetchFabricStock();
    fetchStockHistory();
  }
}, [fetchFabricStock, fetchStockHistory, tab, workspaceUid]);

useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "stock" && tab !== "fco") return;

  loadTrackStock(workspaceUid);
}, [loadTrackStock, tab, workspaceUid]);


//  Auto-load quotation when KPI list selects a quote
/* --------------------- RESET Selected Quote When Leaving Quote Tab --------------------- */
useEffect(() => {
  if (tab !== "quote" && tab !== "fco") {
    setSelectedQuote(null); 
  }
}, [tab]);

// INSERT THIS RIGHT ABOVE "return ("
// SAFE VERSION  fresh object every time
const insertRowAfter = (index) => {
  commitCurtainRows((prevRows) => {
    const newRows = [...prevRows];

    newRows.splice(index + 1, 0, createDefaultCurtainQuoteRow(`W${index + 2}`));

    return renumberCurtainRowsForOrder(newRows);
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const addCurtainMeasurementRow = () => {
  commitCurtainRows((prevRows) => {
    const measurementCount = prevRows.filter((row) => !isCurtainAccessoryRow(row)).length;
    return renumberCurtainRowsForOrder([
      ...prevRows,
      createDefaultCurtainQuoteRow(`W${measurementCount + 1}`),
    ]);
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const addCurtainAccessoryRow = () => {
  commitCurtainRows((prevRows) => {
    const accessoryCount = prevRows.filter((row) => isCurtainAccessoryRow(row)).length;
    return renumberCurtainRowsForOrder([
      ...prevRows,
      createDefaultCurtainAccessoryRow(`Accessory ${accessoryCount + 1}`),
    ]);
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const filteredFabricStock = useMemo(() => {
  const queryText = stockSearch.trim().toLowerCase();
  if (!queryText) return fabricStock;

  return fabricStock.filter((fab) => {
    const name = (fab.displayName || fab.fabricKey || "").toLowerCase();
    return name.includes(queryText);
  });
}, [fabricStock, stockSearch]);

/* ---------------- Quote number ---------------- */


/* ---------------- LOAD SELECTED QUOTE INTO EDITOR ---------------- */


/* ---------------- LOAD SELECTED QUOTE INTO EDITOR ---------------- */
useEffect(() => {
  //  IMPORTANT:
  // Quotation loading is handled explicitly via loadQuotation()
  // from Dashboard / Saved tab / other click handlers.
  // This effect previously caused duplicate fetches and false
  // "Failed to load quotation" errors.
  
  // Intentionally left blank.
}, [selectedQuote, user]);



/* ---------------- LOAD OPTIMIZER QUOTE WHEN ID CHANGES ---------------- */
const getOptimizerQuoteSearchText = (quote = {}) =>
  normalizeSearchText(
    [
      quote.quoteNo,
      quote.quoteNoString,
      quote.customerName,
      quote.customerNameLower,
      quote.customerPhone,
      quote.customerWhatsapp,
      quote.normalizedPhone,
      quote.projectName,
      quote.projectNameLower,
      quote.title,
      quote.searchText,
      Array.isArray(quote.searchKeywords) ? quote.searchKeywords.join(" ") : "",
    ]
      .filter(Boolean)
      .join(" ")
  );

const normalizeOptimizerQuoteResult = useCallback((id, raw = {}) => ({
  id,
  quoteNo: raw.quoteNo || id,
  customerName:
    raw.customerName ||
    raw.quoteFor?.name ||
    raw.quoteFor?.company ||
    raw.crm?.customerName ||
    "Untitled customer",
  customerPhone:
    raw.customerPhone ||
    raw.customerWhatsapp ||
    raw.crm?.customerPhone ||
    raw.crm?.contact?.phone ||
    "",
  projectName:
    raw.projectName ||
    raw.projectTitle ||
    raw.title ||
    raw.crm?.projectTitle ||
    "",
  quoteDate: resolveQuoteDateForInput(raw),
  date: resolveQuoteDateForInput(raw),
  quoteType: raw.quoteType || resolveQuoteType(raw),
  status: raw.status || "draft",
  grandTotal: Number(
    raw.grandTotal ??
      raw.finalTotal ??
      raw.totalAmount ??
      raw.finalAmount ??
      raw.total ??
      0
  ),
  updatedAt: raw.updatedAt || raw.createdAt || null,
  searchText: getOptimizerQuoteSearchText(raw),
}), []);

const cloneOptimizerQuoteForState = (quote = {}) => ({
  ...quote,
  rows: Array.isArray(quote.rows)
    ? quote.rows.map((row, index) =>
        sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...row }, index))
      )
    : [],
  repeatSettings: quote.repeatSettings ? { ...quote.repeatSettings } : {},
});

const searchOptimizerQuotes = useCallback(
  async (queryText) => {
    const normalizedQuery = normalizeSearchText(queryText);
    const compactQuery = normalizeSearchCompact(queryText);
    if (!workspaceUid || normalizedQuery.length < 2) {
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results: [],
        searched: false,
      });
      return;
    }

    const cacheKey = `${workspaceUid}:${normalizedQuery}`;
    const cached = optimizerQuoteSearchCacheRef.current.get(cacheKey);
    if (
      cached &&
      Date.now() - Number(cached.cachedAt || 0) < OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS
    ) {
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results: cached.results,
        searched: true,
      });
      if (isDevRuntime) {
        devLog("OPTIMIZER QUOTE SEARCH CACHE HIT", {
          query: queryText,
          normalizedQuery,
          resultCount: cached.results.length,
        });
      }
      return;
    }

    const requestId = optimizerQuoteSearchRequestRef.current + 1;
    optimizerQuoteSearchRequestRef.current = requestId;
    setOptimizerQuoteSearchState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      searched: true,
    }));

    try {
      const quoteRef = collection(db, "users", workspaceUid, "quote_index");
      const resultMap = new Map();
      const addSnapResults = (snap, source) => {
        snap.docs.forEach((docSnap) => {
          if (resultMap.size >= OPTIMIZER_QUOTE_SEARCH_LIMIT) return;
          const raw = docSnap.data() || {};
          if (isDeletedQuoteRecord(raw)) return;
          if (resolveQuoteType(raw) === GENERAL_QUOTE_TYPE) return;
          const result = normalizeOptimizerQuoteResult(docSnap.id, raw);
          const haystack = result.searchText;
          const compactHaystack = normalizeSearchCompact(haystack);
          if (
            haystack.includes(normalizedQuery) ||
            (compactQuery && compactHaystack.includes(compactQuery))
          ) {
            resultMap.set(docSnap.id, { ...result, source });
          }
        });
      };

      const globalResults = await globalSearchQuotations(workspaceUid, queryText, {
        limit: OPTIMIZER_QUOTE_SEARCH_LIMIT,
        ttlMs: OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS,
        labelPrefix: "optimizerQuoteIndexSearch",
      });
      globalResults.forEach((raw) => {
        if (resultMap.size >= OPTIMIZER_QUOTE_SEARCH_LIMIT) return;
        if (isDeletedQuoteRecord(raw)) return;
        if (resolveQuoteType(raw) === GENERAL_QUOTE_TYPE) return;
        const result = normalizeOptimizerQuoteResult(raw.id, raw);
        const haystack = result.searchText;
        const compactHaystack = normalizeSearchCompact(haystack);
        if (
          haystack.includes(normalizedQuery) ||
          (compactQuery && compactHaystack.includes(compactQuery))
        ) {
          resultMap.set(raw.id, { ...result, source: raw._searchSource || "globalSearch" });
        }
      });

      if (resultMap.size < OPTIMIZER_QUOTE_SEARCH_LIMIT) {
        const recentSnap = await getDocsCached(
          firestoreQuery(
            quoteRef,
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT)
          ),
          {
            label: "optimizerQuoteIndexRecentFallback",
            cacheKey: `users/${workspaceUid}/quote_index?optimizerRecentFallback=${OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        addSnapResults(recentSnap, "recentFallback");
      }

      const results = Array.from(resultMap.values()).slice(
        0,
        OPTIMIZER_QUOTE_SEARCH_LIMIT
      );
      optimizerQuoteSearchCacheRef.current.set(cacheKey, {
        cachedAt: Date.now(),
        results,
      });

      if (optimizerQuoteSearchRequestRef.current !== requestId) return;
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results,
        searched: true,
      });
      if (isDevRuntime) {
        devLog("OPTIMIZER QUOTE SEARCH QUERY", {
          query: queryText,
          normalizedQuery,
          resultCount: results.length,
          sources: Array.from(new Set(results.map((result) => result.source))),
        });
      }
    } catch (error) {
      if (optimizerQuoteSearchRequestRef.current !== requestId) return;
      devError("Optimizer quote search failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        queryText,
      });
      setOptimizerQuoteSearchState({
        loading: false,
        error: "Could not search saved quotations.",
        results: [],
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [normalizeOptimizerQuoteResult, notify, workspaceUid]
);

const loadOptimizerQuoteById = useCallback(
  async (quoteId) => {
    if (!quoteId || !workspaceUid) return null;
    setOptimizerQuoteLoadingId(quoteId);
    try {
      const ref = doc(db, "users", workspaceUid, "quotations", quoteId);
      const snap = await readCompatibleQuoteDocument({ ref, workspaceUid, getDoc: r => getDocCached(r, {
        label: "fullQuoteLoadById.optimizer", cacheKey: r.path, ttlMs: 60 * 1000,
      }), callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data) });

      if (!snap.exists()) {
        notify("Quote not found.", "warning");
        return null;
      }

      const hydratedQuote = snap.canonicalRowsHydrated ? { id: snap.id, ...snap.data() } : await hydrateCurtainQuoteRowsFromChunks({
        quote: {
          id: snap.id,
          ...snap.data(),
        },
        workspaceUid,
        label: "fullQuoteRowChunks.optimizer",
      });
      const fullQuote = cloneOptimizerQuoteForState({
        id: snap.id,
        ...hydratedQuote,
      });
      if (resolveQuoteType(fullQuote) === GENERAL_QUOTE_TYPE) {
        notify("Roll Optimizer currently supports curtain quotations.", "warning");
        return null;
      }

      setOptimizerQuote(fullQuote);
      optimizerUsesLiveRef.current = false;
      setOptimizerQuoteId(snap.id);
      setOptimizerFabric("");
      setRollOptions([]);
      setCutPlan([]);
      setOptimizerFinalDemands([]);
      setSelectedSolutionIndex(0);
      return fullQuote;
    } catch (error) {
      devError("Failed to load optimizer quote:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        quoteId,
      });
      notify(
        isFirestoreResourceExhausted(error)
          ? "Firebase read limit reached. Please wait and try again."
          : String(error.code || '').includes('update-required') || error.details?.code === 'QUOTE_CLIENT_UPDATE_REQUIRED'
          ? QUOTE_UPDATE_REQUIRED : quoteFailureNotice(error)?.message || "Could not load quotation for optimizer.",
        "warning"
      );
      return null;
    } finally {
      setOptimizerQuoteLoadingId("");
    }
  },
  [notify, workspaceUid]
);

useEffect(() => {
  if (tab !== "fco") return undefined;
  const queryText = optimizerQuoteSearch.trim();
  if (queryText.length < 2) {
    setOptimizerQuoteSearchState({
      loading: false,
      error: "",
      results: [],
      searched: false,
    });
    return undefined;
  }

  const timerId = window.setTimeout(() => {
    searchOptimizerQuotes(queryText);
  }, OPTIMIZER_QUOTE_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [optimizerQuoteSearch, searchOptimizerQuotes, tab]);

useEffect(() => {
  if (!optimizerQuoteId || !workspaceUid) return;
  if (optimizerQuote?.id === optimizerQuoteId && optimizerQuote?.rows) return;
  loadOptimizerQuoteById(optimizerQuoteId);
}, [loadOptimizerQuoteById, optimizerQuote, optimizerQuoteId, workspaceUid]);

const currentEditorOptimizerQuote = useMemo(() => {
  if (resolveQuoteType(selectedQuote) === GENERAL_QUOTE_TYPE) {
    return null;
  }

  const currentRows = rows;

  return cloneOptimizerQuoteForState({
    ...selectedQuote,
    id: selectedQuote?.id || "unsaved-current-quote",
    rows: currentRows,
    ...quoteConstructionSettings,
    repeatSettings,
    quoteFullnessOverrides,
    useHalfPieceReuse,
  });
}, [repeatSettings, rows, selectedQuote, quoteFullnessOverrides, quoteConstructionSettings, useHalfPieceReuse]);

const quoteGeometryOverridesSignature = getQuoteCuttingInputSignature({ quoteFullnessOverrides,
  fabricHemOverrides: quoteConstructionSettings.fabricHemOverrides, repeatSettings, useHalfPieceReuse, rows });
useEffect(() => {
  setCutPlan([]);
  setOptimizerFinalDemands([]);
}, [quoteGeometryOverridesSignature]);

const effectiveOptimizerQuote = selectOptimizerSource(
  optimizerQuote, currentEditorOptimizerQuote, optimizerUsesLiveRef.current
);
const optimizerFabricOptions = getOptimizerFabricOptions(
  effectiveOptimizerQuote?.rows || [], getCurtainRowFabricKey, getCurtainRowFabricLabel
);

function getCurrentOptimizerDemand() {
  return buildOptimizerDemandInput({
    quote: effectiveOptimizerQuote, selectedKey: optimizerFabric,
    getFabricKey: getCurtainRowFabricKey, buildPlan: buildReuseAwareDemands,
    getProductRules: getRowProductRules,
    getRepeatForRow: getEffectiveRepeatForCurtainRow,
  });
}

/* --------------------------- QUOTE LOADER --------------------------- */
/* --------------------------- QUOTE LIST LOADER (FAST) --------------------------- */
/* --------------------------- FETCH SAVED QUOTES (normalized) --------------------------- */
/* --------------------------- FETCH SAVED QUOTES --------------------------- */
const subscribeSavedQuotes = useCallback((uid) => {
  if (!uid) return () => {};
  savedRepositoryRef.current?.dispose();
  const allowed = getAllowedCanonicalQuoteTypes({ canAccessCurtainQuotes, canAccessGeneralQuotes });
  const key = quoteRepositoryKey({
    workspaceUid: uid, authUid: auth.currentUser?.uid || user?.uid,
    access: allowed, sort: sortBy, filters: [statusFilter, stageFilter, quoteTypeFilter],
  });
  let cancelled = false;
  const revision = savedQuotesLocalPatchRevisionRef.current;
  const pager = createQuoteRepositoryPager({
    key,
    fetchPage: async (cursor) => {
      const snap = await getDocsCached(buildLegacyQuoteIndexPageQuery(
        collection(db, "users", uid, "quote_index"), cursor
      ), {
        label: "savedQuotes.compatibilityPage",
        cacheKey: `users/${uid}/quote_index?repository=${encodeURIComponent(key)}&after=${cursor?.id || "start"}`,
        ttlMs: 60000,
      });
      return {
        records: snap.docs.map(d => ({
          ...normalizeQuoteIndexRecordForList(d.id, d.data() || {}, normalizeCrmDate),
          savedQuotesLoadSource: "complete_document_id_compatibility",
        })),
        rawCount: snap.size, cursor: snap.docs[snap.docs.length - 1] || cursor,
        hasMore: snap.size === SAVED_QUOTES_PAGE_SIZE,
      };
    },
    eligible: quote => quoteTypeMatchesAccess(quote, allowed) && isQuoteVisibleInSavedQuotesDefault(quote),
  });
  savedRepositoryRef.current = pager;
  savedQuotesPageBusyRef.current = false;
  setLoadingMoreSavedQuotes(false);
  setLoadingSaved(true);
  setSavedQuotesAccessError(null);
  setSavedQuotesPageError("");
  setHasMoreSavedQuoteIndex(false);
  setSavedQuotesVisibleCount(SAVED_QUOTES_PAGE_SIZE);
  // A new query never inherits pages from a previous workspace/filter/sort.
  const sameWorkspace = savedRepositoryWorkspaceRef.current === uid;
  savedRepositoryWorkspaceRef.current = uid;
  const latest = getLatestVerifiedSavedQuoteTrace();
  const pinned = sameWorkspace && sortBy !== "repository" ? savedQuotesRef.current.filter(quote =>
    [latest.quoteId, latest.quoteIndexId].filter(Boolean).includes(quote.id || quote.quoteId)) : [];
  setSavedQuotes(pinned);
  const current = () => !cancelled && savedRepositoryRef.current === pager;
  if (!allowed.length) {
    setLoadingSaved(false);
    return () => { cancelled = true; pager.dispose(); };
  }
  pager.next().then(result => {
    if (!current() || !result) return;
    setSavedQuotes(prev => revision < savedQuotesLocalPatchRevisionRef.current
      ? mergeQuoteIndexPages(prev, result.records) : mergeQuoteIndexPages(result.records, pinned));
    savedQuoteIndexCursorRef.current = result.cursor;
    setHasMoreSavedQuoteIndex(!result.exhausted);
    setHasSavedQuoteIndexCursor(!result.exhausted);
    setDebugRawQuotationCount(result.fetched);
    devLog("SAVED_QUOTES_QUERY_TRACE", { queryIdentity: key, mode: "document_id_compatibility",
      fetched: result.fetched, eligible: result.records.length, exhausted: result.exhausted });
  }).catch(error => {
    if (!current()) return;
    setSavedQuotesPageError("Unable to load quotations. Retry.");
    setHasMoreSavedQuoteIndex(true);
    devError("SAVED_QUOTES_QUERY_FAILED", { code: error?.code, queryIdentity: key });
  }).finally(() => { if (current()) setLoadingSaved(false); });
  return () => {
    cancelled = true;
    pager.dispose();
    if (savedRepositoryRef.current === pager) savedRepositoryRef.current = null;
  };
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, user?.uid, sortBy, statusFilter, stageFilter, quoteTypeFilter]);

useEffect(() => {
  subscribeSavedQuotesRef.current = subscribeSavedQuotes;
}, [subscribeSavedQuotes]);

const openRecycleBin = useCallback(async () => {
  if (!workspaceUid) return;
  setRecycleBinOpen(true);

  const allowedQuoteTypes = getAllowedCanonicalQuoteTypes({
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
  });
  if (allowedQuoteTypes.length === 0) {
    setDeletedQuotes([]);
    return;
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const applyRecycleResult = (result) => {
    setDeletedQuotes(result?.deletedQuotes || []);
  };

  const loadState = savedQuotesRecycleBinLoadRef.current || {};
  if (loadState.workspaceUid !== workspaceUid) {
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
  }

  const currentLoadState = savedQuotesRecycleBinLoadRef.current || {};
  if (currentLoadState.loaded && currentLoadState.result) {
    applyRecycleResult(currentLoadState.result);
    return;
  }

  if (currentLoadState.loading && currentLoadState.promise) {
    setLoadingRecycleBinQuotes(true);
    try {
      applyRecycleResult(await currentLoadState.promise);
    } finally {
      setLoadingRecycleBinQuotes(false);
    }
    return;
  }

  setLoadingRecycleBinQuotes(true);
  const collectionPath = `users/${workspaceUid}/quote_index`;
  const recycleCacheKey = `savedQuotes:recycleBin:${workspaceUid}:firstPage:limit${SAVED_QUOTES_DELETED_READ_LIMIT}`;
  const recyclePromise = (async () => {
    const quoteIndexRef = collection(db, "users", workspaceUid, "quote_index");
    const recycleMap = new Map();
    const addRecycleSnap = (snap, source) => {
      if (!snap) return;
      snap.docs.forEach((docSnap) => {
        const quote = normalizeQuoteIndexRecordForList(
          docSnap.id,
          docSnap.data() || {},
          normalizeTs
        );
        const identity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
        const key =
          identity.quoteIndexId ||
          identity.sourceQuoteId ||
          identity.quoteId ||
          docSnap.id;
        if (!key || recycleMap.has(key)) return;
        recycleMap.set(key, {
          ...quote,
          recycleBinSource: source,
        });
      });
    };

    let primarySnap = null;
    let primaryError = null;
    try {
      primarySnap = await getDocsCached(
        firestoreQuery(
          quoteIndexRef,
          firestoreWhere("binStatus", "==", "in_bin"),
          firestoreOrderBy("movedToBinAt", "desc"),
          firestoreLimit(SAVED_QUOTES_DELETED_READ_LIMIT)
        ),
        {
          label: "loadSavedQuotesRecycleBinPage.binStatus",
          cacheKey: `${recycleCacheKey}:binStatusMovedAt`,
          ttlMs: 5 * 60 * 1000,
        }
      );
      addRecycleSnap(primarySnap, "binStatus");
    } catch (error) {
      primaryError = error;
      if (isDevRuntime) {
        devWarn("SAVED_QUOTES_RECYCLE_BIN_PRIMARY_QUERY_FAILED", {
          code: error?.code,
          message: error?.message,
          path: collectionPath,
        });
      }
      try {
        const unorderedBinSnap = await getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("binStatus", "==", "in_bin"),
            firestoreLimit(SAVED_QUOTES_DELETED_READ_LIMIT)
          ),
          {
            label: "loadSavedQuotesRecycleBinPage.binStatusNoOrderFallback",
            cacheKey: `${recycleCacheKey}:binStatusNoOrder`,
            ttlMs: 5 * 60 * 1000,
          }
        );
        primarySnap = unorderedBinSnap;
        addRecycleSnap(unorderedBinSnap, "binStatusNoOrderFallback");
      } catch (fallbackError) {
        if (isDevRuntime) {
          devWarn("SAVED_QUOTES_RECYCLE_BIN_BINSTATUS_FALLBACK_FAILED", {
            code: fallbackError?.code,
            message: fallbackError?.message,
            path: collectionPath,
          });
        }
      }
    }

    let legacySnap = null;
    let legacyError = null;
    let archivedFallbackSnap = null;
    try {
      legacySnap = await getDocsCached(
        firestoreQuery(
          quoteIndexRef,
          firestoreWhere("deleted", "==", true),
          firestoreOrderBy("deletedAt", "desc"),
          firestoreLimit(SAVED_QUOTES_DELETED_READ_LIMIT)
        ),
        {
          label: "loadSavedQuotesRecycleBinPage.legacyDeleted",
          cacheKey: `${recycleCacheKey}:legacyDeletedAt`,
          ttlMs: 5 * 60 * 1000,
        }
      );
      addRecycleSnap(legacySnap, "legacyDeleted");
    } catch (error) {
      legacyError = error;
      if (isDevRuntime) {
        devWarn("SAVED_QUOTES_RECYCLE_BIN_LEGACY_QUERY_FAILED", {
          code: error?.code,
          message: error?.message,
          path: collectionPath,
        });
      }
    }

    try {
      archivedFallbackSnap = await getDocsCached(
        firestoreQuery(
          quoteIndexRef,
          firestoreWhere("archived", "==", true),
          firestoreLimit(SAVED_QUOTES_DELETED_READ_LIMIT)
        ),
        {
          label: "loadSavedQuotesRecycleBinPage.archivedNoOrderFallback",
          cacheKey: `${recycleCacheKey}:archivedNoOrder`,
          ttlMs: 5 * 60 * 1000,
        }
      );
      addRecycleSnap(archivedFallbackSnap, "archivedNoOrderFallback");
    } catch (error) {
      if (isDevRuntime) {
        devWarn("SAVED_QUOTES_RECYCLE_BIN_ARCHIVED_FALLBACK_FAILED", {
          code: error?.code,
          message: error?.message,
          path: collectionPath,
        });
      }
    }

    if (!primarySnap && !legacySnap && !archivedFallbackSnap) {
      throw primaryError || legacyError || new Error("Recycle Bin query failed.");
    }

    const deletedQuotes = Array.from(recycleMap.values())
      .filter((quote) =>
        quoteTypeMatchesAccess(quote, allowedQuoteTypes)
      )
      .filter((quote) => isDeletedQuoteRecord(quote))
      .sort((a, b) => {
        const bTime =
          getUpdatedMillis({ updatedAt: b.movedToBinAt || b.deletedAt || b.updatedAt }) || 0;
        const aTime =
          getUpdatedMillis({ updatedAt: a.movedToBinAt || a.deletedAt || a.updatedAt }) || 0;
        return bTime - aTime;
      })
      .slice(0, SAVED_QUOTES_DELETED_READ_LIMIT);
    if (isDevRuntime) {
      devLog("SAVED_QUOTES_RECYCLE_BIN_QUERY_TRACE", {
        path: collectionPath,
        primaryCount: primarySnap?.size || 0,
        legacyCount: legacySnap?.size || 0,
        archivedFallbackCount: archivedFallbackSnap?.size || 0,
        mergedCount: deletedQuotes.length,
        firstQuoteNo: deletedQuotes[0]?.quoteNo || "",
      });
    }
    const result = { deletedQuotes };
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: true,
      promise: null,
      result,
    };
    return result;
  })().catch((error) => {
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    throw error;
  });

  savedQuotesRecycleBinLoadRef.current = {
    workspaceUid,
    loading: true,
    loaded: false,
    promise: recyclePromise,
    result: null,
  };

  try {
    applyRecycleResult(await recyclePromise);
  } catch (error) {
    devError("Saved quotes recycle bin load failed:", {
      code: error?.code,
      message: error?.message,
      path: collectionPath,
    });
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    } else {
      notify("Could not load Recycle Bin.", "warning");
    }
  } finally {
    setLoadingRecycleBinQuotes(false);
  }
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, notify, workspaceUid]);

const loadMoreSavedQuoteIndex = useCallback(async () => {
  const pager = savedRepositoryRef.current;
  if (!pager || savedQuotesPageBusyRef.current || loadingSaved) return;
  savedQuotesPageBusyRef.current = true;
  setLoadingMoreSavedQuotes(true);
  setSavedQuotesPageError("");
  try {
    const result = await pager.next();
    if (!result || savedRepositoryRef.current !== pager) return;
    setSavedQuotes(prev => mergeQuoteIndexPages(result.records, safeSavedQuoteArray(prev)));
    savedQuoteIndexCursorRef.current = result.cursor;
    setHasMoreSavedQuoteIndex(!result.exhausted);
    setHasSavedQuoteIndexCursor(!result.exhausted);
    setSavedQuotesVisibleCount(prev => prev + SAVED_QUOTES_PAGE_SIZE * 3);
  } catch (error) {
    if (savedRepositoryRef.current !== pager) return;
    setSavedQuotesPageError("Unable to load quotations. Retry.");
    devError("SAVED_QUOTES_LOAD_MORE_FAILED", { code: error?.code });
  } finally {
    if (savedRepositoryRef.current === pager) {
      savedQuotesPageBusyRef.current = false;
      setLoadingMoreSavedQuotes(false);
    }
  }
}, [loadingSaved]);

const searchSavedQuotesRemote = useCallback(async (uid, queryText, more = false) => {
  const normalized = normalizeSearchText(queryText);
  if (!uid || normalized.length < 2) return;
  const key = `${uid}:${normalized}:${user?.uid}:${canAccessCurtainQuotes}:${canAccessGeneralQuotes}:${recycleBinOpen}`;
  const allowed = getAllowedCanonicalQuoteTypes({ canAccessCurtainQuotes, canAccessGeneralQuotes });
  if (tab !== "saved" || normalized !== normalizeSearchText(search)) {
    const generation = ++savedLookupGenerationRef.current;
    const entries = await globalSearchQuotations(uid, queryText, { limit: SAVED_QUOTES_PAGE_SIZE,
      cacheScope: `${user?.uid}:${allowed.join(",")}` });
    if (generation !== savedLookupGenerationRef.current || crmWorkspaceRef.current !== uid) return;
    setSavedLookupResult({ key, records: entries.map(entry => normalizeQuoteIndexRecordForList(entry.id, entry, normalizeCrmDate))
      .filter(quote => quoteTypeMatchesAccess(quote, allowed) && isQuoteVisibleInSavedQuotesDefault(quote)) });
    return;
  }
  let pager = savedSearchRepositoryRef.current;
  if (!more || pager?.snapshot().key !== key) {
    pager?.dispose();
    const generation = ++savedSearchGenerationRef.current;
    pager = createQuoteRepositoryPager({
      key,
      fetchPage: async cursor => {
        const result = await globalSearchQuotations(uid, queryText, {
          limit: SAVED_QUOTES_PAGE_SIZE, pagination: cursor || {},
          cacheScope: `${auth.currentUser?.uid || user?.uid}:${allowed.join(",")}`,
          labelPrefix: "savedQuotes.paginatedSearch",
        });
        return { records: result.records.map(entry => normalizeQuoteIndexRecordForList(entry.id, entry, normalizeCrmDate)),
          rawCount: result.rawCount ?? result.records.length, cursor: result.pageState, hasMore: result.hasMore };
      },
      eligible: quote => quoteTypeMatchesAccess(quote, allowed) && (recycleBinOpen || isQuoteVisibleInSavedQuotesDefault(quote)),
    });
    pager.requestGeneration = generation;
    savedSearchRepositoryRef.current = pager;
    setSavedSearchResult({ key, records: [], loading: true, error: "", hasMore: true });
  } else {
    setSavedSearchResult(prev => ({ ...prev, loading: true, error: "" }));
  }
  try {
    const result = await pager.next();
    if (!result || savedSearchRepositoryRef.current !== pager ||
        pager.requestGeneration !== savedSearchGenerationRef.current) return;
    setSavedSearchResult({ key, records: result.records, loading: false, error: "", hasMore: !result.exhausted });
    setSavedQuotesVisibleCount(prev => more ? prev + 75 : SAVED_QUOTES_PAGE_SIZE);
  } catch (error) {
    if (savedSearchRepositoryRef.current !== pager || pager.requestGeneration !== savedSearchGenerationRef.current) return;
    setSavedSearchResult(prev => ({ ...prev, loading: false, error: "Search failed. Try again.", hasMore: true }));
    devError("SAVED_QUOTES_SEARCH_FAILED", { code: error?.code });
  }
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, user?.uid, tab, search, recycleBinOpen]);

const repairQuotationSearchIndex = useCallback(async () => {
  if (!workspaceUid || quoteSearchRepairState.running) return;

  setQuoteSearchRepairState({
    running: true,
    processed: 0,
    updated: 0,
    error: "",
  });

  try {
    const repairQuoteSearchIndexCallable = httpsCallable(functions, "repairQuoteSearchIndex");
    const result = await repairQuoteSearchIndexCallable({ workspaceUid });
    const processed = Number(result?.data?.scanned || 0);
    const updated = Number(result?.data?.repaired || 0);
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    savedQuotesRecentLoadRef.current = {
      workspaceUid: "",
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    setSavedQuotesAccessError(null);
    setQuoteSearchRepairState({
      running: false,
      processed,
      updated,
      error: result?.data?.errors ? `${result.data.errors} record(s) skipped` : "",
    });
    notify(`Quotation search index repaired (${updated} updated).`, "success");
    if (tab === "saved") {
      subscribeSavedQuotes(workspaceUid);
    }
  } catch (error) {
    devError("Quotation search index repair failed", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setQuoteSearchRepairState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Repair failed",
    }));
    notify("Could not repair quotation search index.", "error");
  }
}, [notify, quoteSearchRepairState.running, subscribeSavedQuotes, tab, workspaceUid]);

const recoverMissingSavedQuoteIndexes = useCallback(async () => {
  if (!workspaceUid || savedQuoteIndexRecoveryState.running) return;

  setSavedQuoteIndexRecoveryState({
    running: true,
    checked: 0,
    missing: 0,
    recovered: 0,
    binFlagRepairCount: 0,
    inBinCount: 0,
    error: "",
    report: null,
  });

  try {
    const recoverCallable = httpsCallable(functions, "auditAndRecoverQuoteIndex");
    const result = await recoverCallable({
      workspaceUid,
      mode: "fullRepairSafe",
      limit: 500,
    });
    const report = result?.data || {};
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    savedQuotesRecentLoadRef.current = {
      workspaceUid: "",
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid: "",
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    setSavedQuoteIndexRecoveryState({
      running: false,
      checked: Number(report.quotationCount || 0),
      missing: Number(report.missingIndexCount || 0),
      recovered: Number(report.recoveredCount || report.recoveredIndexCount || 0),
      binFlagRepairCount: Number(report.binFlagRepairCount || 0),
      inBinCount: Number(report.inBinCount || 0),
      error: report.failedCount ? `${report.failedCount} record(s) failed` : "",
      report,
    });
    notify(
      `Saved quote listings repaired (${Number(report.normalizedCount || 0)} normalized, ${Number(
        report.recoveredCount || report.recoveredIndexCount || 0
      )} recreated).`,
      "success"
    );
    if (tab === "saved") {
      subscribeSavedQuotes(workspaceUid);
    }
  } catch (error) {
    devError("Saved quote index recovery failed", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setSavedQuoteIndexRecoveryState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Recovery failed",
    }));
    notify("Could not recover saved quote listings.", "error");
  }
}, [
  notify,
  savedQuoteIndexRecoveryState.running,
  subscribeSavedQuotes,
  tab,
  workspaceUid,
]);

const repairQuoteContentConsistency = useCallback(async () => {
  if (!workspaceUid || quoteContentAuditState.running) return;

  setQuoteContentAuditState({
    running: true,
    checked: 0,
    conflicts: 0,
    repaired: 0,
    error: "",
    report: null,
  });

  try {
    const repairCallable = httpsCallable(functions, "auditQuoteContentConsistency");
    const result = await repairCallable({
      workspaceUid,
      repair: true,
      limit: 100,
    });
    const report = result?.data || {};
    invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    setQuoteContentAuditState({
      running: false,
      checked: Number(report.checked || 0),
      conflicts: Number(report.conflictCount || 0),
      repaired: Number(report.repairedCount || 0),
      error: report.failedCount ? `${report.failedCount} record(s) failed` : "",
      report,
    });
    notify(
      `Quote content aliases checked (${Number(report.repairedCount || 0)} repaired).`,
      "success"
    );
  } catch (error) {
    devError("Quote content consistency repair failed", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setQuoteContentAuditState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Content consistency repair failed",
    }));
    notify("Could not repair quote content aliases.", "error");
  }
}, [notify, quoteContentAuditState.running, workspaceUid]);

const rebuildAnalyticsAndCalendarIndexes = useCallback(async () => {
  if (!workspaceUid || analyticsRebuildState.running) return;
  // This legacy bulk writer requires unfiltered quotation-body reads. Keep it
  // fail-closed; ordinary index-based dashboards and reports remain available.
  if (QUOTE_READER_VERSION >= 2) {
    notify(QUOTE_MAINTENANCE_PAUSED, "warning");
    return;
  }

  setAnalyticsRebuildState({
    running: true,
    scanned: 0,
    indexedQuotes: 0,
    monthlyUpdated: 0,
    clientUpdated: 0,
    installationsUpdated: 0,
    staleCleared: 0,
    skipped: 0,
    failed: 0,
    error: "",
  });

  try {
    let cursor = null;
    let scanned = 0;
    let indexedQuotes = 0;
    let monthlyUpdated = 0;
    let clientUpdated = 0;
    let installationsUpdated = 0;
    let staleCleared = 0;
    let skipped = 0;
    let failed = 0;
    let hasMore = true;
    const monthlyTotals = new Map();
    const clientTotals = new Map();
    const clientMetadata = new Map();
    const expectedQuoteIds = new Set();
    const expectedInstallationIds = new Set();
    const mergeSummaryDelta = (targetMap, sourceMap) => {
      sourceMap.forEach((delta, key) => {
        targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
      });
    };

    while (hasMore) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));

      const snap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "quotations"),
          ...constraints
        ),
        {
          label: "rebuildAnalyticsIndexes",
          cacheKey: `users/${workspaceUid}/quotations?rebuildAnalyticsAfter=${
            cursor?.id || "start"
          }`,
        }
      );

      if (snap.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      const pageSources = [];

      snap.docs.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        const source = {
          id: docSnap.id,
          ...raw,
        };
        pageSources.push(source);
        batch.set(
          doc(db, "users", workspaceUid, "quote_index", docSnap.id),
          {
            ...sanitizeCrmFirestoreValue(
              buildQuoteIndexPayload(source, docSnap.id, workspaceUid)
            ),
            updatedAt: serverTimestamp(),
            searchIndexRepairedAt: serverTimestamp(),
            analyticsIndexRebuiltAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      for (const source of pageSources) {
        try {
          scanned += 1;
          const contribution = buildAnalyticsContributionForQuote(source);
          if (!contribution) {
            skipped += 1;
            continue;
          }
          expectedQuoteIds.add(source.id);

          mergeSummaryDelta(monthlyTotals, getMonthlyDeltaFromContribution(contribution, 1));
          const clientMonth = contribution.confirmedMonthKey || contribution.quoteMonthKey;
          if (clientMonth) {
            const clientDocId = `${clientMonth}_${contribution.customerKey || "unknown"}`;
            clientTotals.set(
              clientDocId,
            applySummaryDelta(clientTotals.get(clientDocId), {
              confirmedRevenue: Number(contribution.confirmedRevenue || 0),
              confirmedCount:
                contribution.confirmed && contribution.confirmedMonthKey ? 1 : 0,
              quotationRevenue: Number(contribution.quotationRevenue || 0),
              quotationCount: contribution.deleted ? 0 : 1,
            })
            );
            clientMetadata.set(clientDocId, {
              monthKey: clientMonth,
              customerKey: contribution.customerKey || "unknown",
              customerId: contribution.customerId || "",
              customerName: contribution.customerName || "Unnamed Customer",
              normalizedPhone: contribution.normalizedPhone || "",
              lastQuoteId: source.id,
              lastActivityAt: contribution.lastActivityAt || new Date(),
            });
          }

          batch.set(
            doc(db, "users", workspaceUid, "quote_analytics_index", source.id),
            {
              ...sanitizeCrmFirestoreValue(contribution),
              syncedAt: serverTimestamp(),
              source: "rebuildAnalyticsIndexes",
            },
            { merge: true }
          );

          if (contribution.hasInstallation && !contribution.deleted) {
            expectedInstallationIds.add(source.id);
            batch.set(
              doc(db, "users", workspaceUid, "installation_index", source.id),
              {
                quoteId: source.id,
                jobId: source.id,
                quoteNo: contribution.quoteNo,
                customerName: contribution.customerName,
                normalizedPhone: contribution.normalizedPhone,
                installationDate: contribution.installationDate,
                installationDateKey: contribution.installationDateKey,
                monthKey: contribution.installationMonthKey,
                status: contribution.status,
                workStage: contribution.workStage,
                totalAmount: contribution.totalAmount,
                confirmedRevenue: contribution.confirmedRevenue,
                itemSummary: contribution.itemSummary,
                location: contribution.location,
                source: "rebuildAnalyticsIndexes",
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }

          const uniqueMonths = new Set(
            [
              contribution.quoteMonthKey,
              contribution.confirmedMonthKey,
              contribution.installationMonthKey,
            ].filter(Boolean)
          );
          indexedQuotes += 1;
          monthlyUpdated = monthlyTotals.size || uniqueMonths.size;
          clientUpdated = clientTotals.size;
          installationsUpdated += contribution.hasInstallation && !contribution.deleted ? 1 : 0;
        } catch (error) {
          failed += 1;
          if (isDevRuntime) {
            devError("Analytics rebuild failed for quote", {
              quoteId: source.id,
              code: error?.code,
              message: error?.message,
            });
          }
        }
      }

      await batch.commit();

      cursor = snap.docs[snap.docs.length - 1];
      hasMore = snap.size === 100;
      setAnalyticsRebuildState({
        running: true,
        scanned,
        indexedQuotes,
        monthlyUpdated,
        clientUpdated,
        installationsUpdated,
        staleCleared,
        skipped,
        failed,
        error: "",
      });
    }

    const commitSummaryEntries = async (entries, writer) => {
      for (let index = 0; index < entries.length; index += 450) {
        const page = entries.slice(index, index + 450);
        const batch = writeBatch(db);
        page.forEach(([key, value]) => writer(batch, key, value));
        await batch.commit();
      }
    };

    await commitSummaryEntries(Array.from(monthlyTotals.entries()), (batch, monthKey, totals) => {
      batch.set(
        doc(db, "users", workspaceUid, "analytics_monthly", monthKey),
        {
          monthKey,
          confirmedRevenue: Number(Number(totals.confirmedRevenue || 0).toFixed(2)),
          confirmedCount: Number(totals.confirmedCount || 0),
          quotationRevenue: Number(Number(totals.quotationRevenue || 0).toFixed(2)),
          quotationCount: Number(totals.quotationCount || 0),
          cancelledRevenue: Number(Number(totals.cancelledRevenue || 0).toFixed(2)),
          cancelledCount: Number(totals.cancelledCount || 0),
          installedRevenue: Number(Number(totals.installedRevenue || 0).toFixed(2)),
          installedCount: Number(totals.installedCount || 0),
          rebuiltAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: false }
      );
    });

    await commitSummaryEntries(Array.from(clientTotals.entries()), (batch, clientDocId, totals) => {
      const meta = clientMetadata.get(clientDocId) || {};
      batch.set(
        doc(db, "users", workspaceUid, "client_monthly", clientDocId),
        {
          monthKey: meta.monthKey || clientDocId.split("_")[0],
          customerKey: meta.customerKey || "unknown",
          customerId: meta.customerId || "",
          customerName: meta.customerName || "Unnamed Customer",
          normalizedPhone: meta.normalizedPhone || "",
          confirmedRevenue: Number(Number(totals.confirmedRevenue || 0).toFixed(2)),
          confirmedCount: Number(totals.confirmedCount || 0),
          quotationRevenue: Number(Number(totals.quotationRevenue || 0).toFixed(2)),
          quoteCount: Number(totals.quotationCount || 0),
          lastQuoteId: meta.lastQuoteId || "",
          lastActivityAt: meta.lastActivityAt || new Date(),
          rebuiltAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: false }
      );
    });

    monthlyUpdated = monthlyTotals.size;
    clientUpdated = clientTotals.size;

    let leadCursor = null;
    let hasMoreLeads = true;
    while (hasMoreLeads) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (leadCursor) constraints.push(firestoreStartAfter(leadCursor));

      const leadSnap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          ...constraints
        ),
        {
          label: "rebuildAnalyticsIndexes.crmLeads",
          cacheKey: `users/${workspaceUid}/crm_leads?rebuildInstallationAfter=${
            leadCursor?.id || "start"
          }`,
        }
      );

      if (leadSnap.empty) {
        hasMoreLeads = false;
        break;
      }

      for (const leadDoc of leadSnap.docs) {
        scanned += 1;
        const lead = { id: leadDoc.id, ...(leadDoc.data() || {}) };
        const hasInstallationDate = Boolean(
          lead.installationDate ||
            lead.expectedInstallationDate ||
            lead.scheduledInstallationDate ||
            lead.installedAt ||
            lead.deliveryDate ||
            lead.crm?.installationDate
        );
        if (!hasInstallationDate) {
          skipped += 1;
          continue;
        }
        try {
          const linkedQuotes = normalizeCrmLinkedQuotes(lead);
          const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
          const expectedInstallationId = String(
            primaryQuote?.quoteId || lead.quoteId || leadDoc.id
          ).trim();
          const leadStatus = String(lead.status || "").toLowerCase();
          const leadWorkStage = String(
            lead.workStage || lead.stageId || lead.stage || ""
          ).toLowerCase();
          const leadInactive =
            lead.deleted === true ||
            ["cancelled", "canceled", "lost"].includes(leadStatus) ||
            ["cancelled", "canceled", "lost"].includes(leadWorkStage);
          if (expectedInstallationId && !leadInactive) {
            expectedInstallationIds.add(expectedInstallationId);
          }
          await syncCrmLeadInstallationIndex(leadDoc.id, lead, {
            source: "rebuildAnalyticsIndexes.crmLeads",
          });
          installationsUpdated += 1;
        } catch (error) {
          failed += 1;
          if (isDevRuntime) {
            devError("CRM installation index rebuild failed for lead", {
              leadId: leadDoc.id,
              code: error?.code,
              message: error?.message,
            });
          }
        }
      }

      leadCursor = leadSnap.docs[leadSnap.docs.length - 1];
      hasMoreLeads = leadSnap.size === 100;
      setAnalyticsRebuildState({
        running: true,
        scanned,
        indexedQuotes,
        monthlyUpdated,
        clientUpdated,
        installationsUpdated,
        staleCleared,
        skipped,
        failed,
        error: "",
      });
    }

    const markStaleDerivedDocs = async (collectionName, expectedIds, buildPatch) => {
      let staleCursor = null;
      let hasMoreStaleDocs = true;
      while (hasMoreStaleDocs) {
        const constraints = [
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(100),
        ];
        if (staleCursor) constraints.push(firestoreStartAfter(staleCursor));

        const staleSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, collectionName),
            ...constraints
          ),
          {
            label: `rebuildAnalyticsIndexes.clearStale.${collectionName}`,
            cacheKey: `users/${workspaceUid}/${collectionName}?clearStaleAfter=${
              staleCursor?.id || "start"
            }`,
          }
        );

        if (staleSnap.empty) {
          hasMoreStaleDocs = false;
          break;
        }

        const staleBatch = writeBatch(db);
        let pageWrites = 0;
        staleSnap.docs.forEach((docSnap) => {
          if (expectedIds.has(docSnap.id)) return;
          const patch = sanitizeCrmFirestoreValue(buildPatch(docSnap.data() || {}, docSnap.id));
          if (!patch || Object.keys(patch).length === 0) return;
          staleBatch.set(
            doc(db, "users", workspaceUid, collectionName, docSnap.id),
            patch,
            { merge: true }
          );
          pageWrites += 1;
        });

        if (pageWrites > 0) {
          await staleBatch.commit();
          staleCleared += pageWrites;
          setAnalyticsRebuildState({
            running: true,
            scanned,
            indexedQuotes,
            monthlyUpdated,
            clientUpdated,
            installationsUpdated,
            staleCleared,
            skipped,
            failed,
            error: "",
          });
        }

        staleCursor = staleSnap.docs[staleSnap.docs.length - 1];
        hasMoreStaleDocs = staleSnap.size === 100;
      }
    };

    await markStaleDerivedDocs("analytics_monthly", new Set(monthlyTotals.keys()), () => ({
      confirmedRevenue: 0,
      confirmedCount: 0,
      quotationRevenue: 0,
      quotationCount: 0,
      cancelledRevenue: 0,
      cancelledCount: 0,
      installedRevenue: 0,
      installedCount: 0,
      stale: true,
      staleReason: "not_present_in_latest_source_rebuild",
      rebuiltAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("client_monthly", new Set(clientTotals.keys()), () => ({
      confirmedRevenue: 0,
      confirmedCount: 0,
      quotationRevenue: 0,
      quoteCount: 0,
      stale: true,
      staleReason: "not_present_in_latest_source_rebuild",
      rebuiltAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("installation_index", expectedInstallationIds, () => ({
      active: false,
      stale: true,
      staleReason: "no_current_source_installation_date",
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("quote_analytics_index", expectedQuoteIds, () => ({
      quotationRevenue: 0,
      confirmedRevenue: 0,
      cancelledRevenue: 0,
      installedRevenue: 0,
      totalAmount: 0,
      sourceMissing: true,
      stale: true,
      staleReason: "source_quotation_missing",
      syncedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("quote_index", expectedQuoteIds, () => ({
      deleted: true,
      sourceMissing: true,
      stale: true,
      staleReason: "source_quotation_missing",
      updatedAt: serverTimestamp(),
    }));

    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_analytics_index");
    invalidateWorkspaceCollectionCache(workspaceUid, "analytics_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "client_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
    setAnalyticsRebuildState({
      running: false,
      scanned,
      indexedQuotes,
      monthlyUpdated,
      clientUpdated,
      installationsUpdated,
      staleCleared,
      skipped,
      failed,
      error: "",
    });
    notify(`Analytics and calendar indexes rebuilt (${indexedQuotes} quotations indexed).`, "success");
  } catch (error) {
    devError("Analytics and calendar index rebuild failed", error);
    setAnalyticsRebuildState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Rebuild failed",
    }));
    notify("Could not rebuild analytics and calendar indexes.", "error");
  }
}, [
  analyticsRebuildState.running,
  applySummaryDelta,
  buildAnalyticsContributionForQuote,
  getMonthlyDeltaFromContribution,
  notify,
  syncCrmLeadInstallationIndex,
  workspaceUid,
]);

const runTemporaryFullDataAudit = useCallback(async () => {
  if (!workspaceUid || adminDataAuditState.running) return;

  setAdminDataAuditState({
    running: true,
    checkedAt: "",
    error: "",
    collections: {},
    progress: {
      collection: "",
      scanned: 0,
    },
    report: null,
  });

  if (typeof httpsCallable === "function") {
    try {
      const auditWorkspaceDataCallable = httpsCallable(functions, "auditWorkspaceData");
      const result = await auditWorkspaceDataCallable({ workspaceUid });
      const data = result?.data || {};
      const collections = data.collections || {};
      const report = data.report || {
        generatedAt: data.checkedAt || new Date().toISOString(),
        workspaceUid,
        sourceDocumentCounts: {},
        derivedDocumentCounts: {},
        issueCounts: {},
        mismatchSummary: {
          totalMismatchCount: 0,
          rootCauseHints: [],
        },
      };
      const totalScanned = Object.values(collections).reduce(
        (total, collectionResult) =>
          total + Number(collectionResult?.totalCount || 0),
        0
      );

      setAdminDataAuditState({
        running: false,
        checkedAt: data.checkedAt || report.generatedAt || new Date().toISOString(),
        error: "",
        collections,
        progress: {
          collection: "",
          scanned: totalScanned,
        },
        report,
      });
      notify("Firebase data audit completed.", "success");
      return;
    } catch (error) {
      devError("Audit Firebase Data callable failed", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
      });
      setAdminDataAuditState((prev) => ({
        ...prev,
        running: false,
        error: error?.message || "Audit failed",
      }));
      notify("Could not complete Firebase data audit.", "error");
      return;
    }
  }

  const collectionNames = [
    "quotations",
    "crm_leads",
    "customers",
    "quote_index",
    "analytics_monthly",
    "client_monthly",
    "installation_index",
    "quote_analytics_index",
    "crm_reminders",
  ];

  const nextCollections = {};
  const collectionDocs = {};
  const startedAt = new Date().toISOString();

  const roundAuditNumber = (value) => Number(Number(value || 0).toFixed(2));
  const numberCloseEnough = (actual, expected) =>
    Math.abs(Number(actual || 0) - Number(expected || 0)) < 0.01;
  const nonZeroSummaryValue = (docData = {}) =>
    [
      "confirmedRevenue",
      "confirmedCount",
      "quotationRevenue",
      "quotationCount",
      "cancelledRevenue",
      "cancelledCount",
      "installedRevenue",
      "installedCount",
      "quoteCount",
    ].some((field) => Math.abs(Number(docData?.[field] || 0)) > 0.01);
  const pushSample = (target, value, max = 20) => {
    if (!Array.isArray(target) || target.length >= max) return;
    target.push(sanitizeCrmFirestoreValue(value));
  };
  const mergeAuditSummaryDelta = (targetMap, sourceMap) => {
    sourceMap.forEach((delta, key) => {
      targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
    });
  };
  const summarizeAuditDoc = (id, data = {}) => ({
    id,
    keys: Object.keys(data).slice(0, 16),
    quoteNo: data.quoteNo || "",
    customerName: data.customerName || data.contact?.name || "",
    monthKey: data.monthKey || "",
    installationDateKey: data.installationDateKey || "",
    quoteType: data.quoteType || data.quoteTypeResolved || "",
  });

  const scanCollectionForAudit = async (collectionName) => {
    const collectionPath = `users/${workspaceUid}/${collectionName}`;
    const docsMap = new Map();
    const samples = [];
    let cursor = null;
    let hasMore = true;
    let batches = 0;

    try {
      while (hasMore) {
        const constraints = [
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(100),
        ];
        if (cursor) constraints.push(firestoreStartAfter(cursor));

        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, collectionName),
            ...constraints
          ),
          {
            label: `auditFirebaseData.scan.${collectionName}`,
            cacheKey: `${collectionPath}?auditAfter=${cursor?.id || "start"}`,
          }
        );

        batches += 1;
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          docsMap.set(docSnap.id, {
            id: docSnap.id,
            ...data,
          });
          if (samples.length < 5) {
            samples.push(summarizeAuditDoc(docSnap.id, data));
          }
        });

        nextCollections[collectionName] = {
          ok: true,
          path: collectionPath,
          totalCount: docsMap.size,
          sampleCount: samples.length,
          batches,
          samples,
        };

        setAdminDataAuditState((prev) => ({
          ...prev,
          running: true,
          checkedAt: "",
          error: "",
          collections: { ...nextCollections },
          progress: {
            collection: collectionName,
            scanned: docsMap.size,
          },
          report: null,
        }));

        cursor = snap.docs[snap.docs.length - 1] || null;
        hasMore = snap.size === 100;
      }
    } catch (error) {
      nextCollections[collectionName] = {
        ok: false,
        path: collectionPath,
        totalCount: docsMap.size,
        sampleCount: samples.length,
        batches,
        samples,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      };
      if (isDevRuntime) {
        devError("Audit Firebase Data collection failed", {
          collectionName,
          path: collectionPath,
          code: error?.code,
          message: error?.message,
        });
      }
    }

    return docsMap;
  };

  try {
    for (const collectionName of collectionNames) {
      collectionDocs[collectionName] = await scanCollectionForAudit(collectionName);
    }

    const quoteDocs = collectionDocs.quotations || new Map();
    const crmLeadDocs = collectionDocs.crm_leads || new Map();
    const quoteIndexDocs = collectionDocs.quote_index || new Map();
    const analyticsMonthlyDocs = collectionDocs.analytics_monthly || new Map();
    const clientMonthlyDocs = collectionDocs.client_monthly || new Map();
    const installationIndexDocs = collectionDocs.installation_index || new Map();
    const quoteAnalyticsDocs = collectionDocs.quote_analytics_index || new Map();

    const expectedMonthly = new Map();
    const expectedClientMonthly = new Map();
    const expectedClientMetadata = new Map();
    const expectedInstallation = new Map();
    const expectedQuoteAnalytics = new Map();
    const sourceQuoteIds = new Set();
    const auditSamples = {
      invalidAmounts: [],
      invalidDates: [],
      confirmedWithoutConfirmedAt: [],
      missingQuoteIndex: [],
      missingQuoteAnalyticsIndex: [],
      missingAnalyticsMonthly: [],
      analyticsMismatches: [],
      staleAnalyticsMonthly: [],
      missingClientMonthly: [],
      clientMonthlyMismatches: [],
      duplicateClientMonthly: [],
      staleClientMonthly: [],
      missingInstallationIndex: [],
      installationMismatches: [],
      staleInstallationIndex: [],
      quoteAnalyticsMismatches: [],
      quoteAnalyticsSourceMissing: [],
      quoteIndexSourceMissing: [],
      deletedIncludedInAnalytics: [],
      cancelledIncludedInConfirmedRevenue: [],
      skippedQuotes: [],
    };
    const issueCounts = {
      invalidAmountCount: 0,
      invalidDateCount: 0,
      confirmedWithoutConfirmedAtCount: 0,
      skippedQuoteCount: 0,
      missingQuoteIndexCount: 0,
      missingQuoteAnalyticsIndexCount: 0,
      missingAnalyticsMonthlyCount: 0,
      analyticsMismatchCount: 0,
      staleAnalyticsMonthlyCount: 0,
      missingClientMonthlyCount: 0,
      clientMonthlyMismatchCount: 0,
      duplicatedClientMonthlyCount: 0,
      staleClientMonthlyCount: 0,
      missingInstallationIndexCount: 0,
      installationMismatchCount: 0,
      staleInstallationIndexCount: 0,
      quoteAnalyticsMismatchCount: 0,
      quoteAnalyticsSourceMissingCount: 0,
      quoteIndexSourceMissingCount: 0,
      deletedIncludedInAnalyticsCount: 0,
      cancelledIncludedInConfirmedRevenueCount: 0,
    };

    const compareMonthlyFields = (actual = {}, expected = {}, fields, onMismatch) => {
      fields.forEach((field) => {
        if (!numberCloseEnough(actual[field], expected[field])) {
          onMismatch({
            field,
            actual: roundAuditNumber(actual[field]),
            expected: roundAuditNumber(expected[field]),
          });
        }
      });
    };

    quoteDocs.forEach((quote, quoteId) => {
      sourceQuoteIds.add(quoteId);
      const contribution = buildAnalyticsContributionForQuote({
        ...quote,
        id: quoteId,
        quoteId,
      });

      if (!contribution) {
        issueCounts.skippedQuoteCount += 1;
        pushSample(auditSamples.skippedQuotes, { quoteId, reason: "No analytics contribution" });
        return;
      }

      expectedQuoteAnalytics.set(quoteId, contribution);
      mergeAuditSummaryDelta(expectedMonthly, getMonthlyDeltaFromContribution(contribution, 1));

      const clientMonth = contribution.confirmedMonthKey || contribution.quoteMonthKey;
      if (clientMonth) {
        const clientDocId = `${clientMonth}_${contribution.customerKey || "unknown"}`;
        expectedClientMonthly.set(
          clientDocId,
          applySummaryDelta(expectedClientMonthly.get(clientDocId), {
            confirmedRevenue: Number(contribution.confirmedRevenue || 0),
            confirmedCount:
              contribution.confirmed && contribution.confirmedMonthKey ? 1 : 0,
            quotationRevenue: Number(contribution.quotationRevenue || 0),
            quotationCount: contribution.deleted ? 0 : 1,
          })
        );
        expectedClientMetadata.set(clientDocId, {
          monthKey: clientMonth,
          customerKey: contribution.customerKey || "unknown",
          customerName: contribution.customerName || "Unnamed Customer",
        });
      }

      if (contribution.hasInstallation && !contribution.deleted) {
        expectedInstallation.set(quoteId, {
          source: "quotations",
          quoteId,
          installationDateKey: contribution.installationDateKey,
          monthKey: contribution.installationMonthKey,
          customerName: contribution.customerName,
          quoteNo: contribution.quoteNo,
        });
      }

      if (contribution.issues?.includes("invalid_amount")) {
        issueCounts.invalidAmountCount += 1;
        pushSample(auditSamples.invalidAmounts, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
          totalSource: contribution.totalSource,
        });
      }
      const dateIssues = (contribution.issues || []).filter((issue) =>
        issue.startsWith("invalid_") && issue.includes("date")
      );
      if (dateIssues.length) {
        issueCounts.invalidDateCount += 1;
        pushSample(auditSamples.invalidDates, {
          quoteId,
          quoteNo: contribution.quoteNo,
          issues: dateIssues,
        });
      }
      if (contribution.issues?.includes("confirmed_without_confirmedAt")) {
        issueCounts.confirmedWithoutConfirmedAtCount += 1;
        pushSample(auditSamples.confirmedWithoutConfirmedAt, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
          status: contribution.status,
          workStage: contribution.workStage,
        });
      }

      if (!quoteIndexDocs.has(quoteId)) {
        issueCounts.missingQuoteIndexCount += 1;
        pushSample(auditSamples.missingQuoteIndex, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
        });
      }

      const quoteAnalyticsDoc = quoteAnalyticsDocs.get(quoteId);
      if (!quoteAnalyticsDoc) {
        issueCounts.missingQuoteAnalyticsIndexCount += 1;
        pushSample(auditSamples.missingQuoteAnalyticsIndex, {
          quoteId,
          quoteNo: contribution.quoteNo,
        });
      } else {
        const mismatches = [];
        compareMonthlyFields(
          quoteAnalyticsDoc,
          contribution,
          [
            "quotationRevenue",
            "confirmedRevenue",
            "cancelledRevenue",
            "installedRevenue",
            "totalAmount",
          ],
          (mismatch) => mismatches.push(mismatch)
        );
        ["quoteMonthKey", "confirmedMonthKey", "installationDateKey", "installationMonthKey"].forEach(
          (field) => {
            if (String(quoteAnalyticsDoc[field] || "") !== String(contribution[field] || "")) {
              mismatches.push({
                field,
                actual: quoteAnalyticsDoc[field] || "",
                expected: contribution[field] || "",
              });
            }
          }
        );
        if (mismatches.length) {
          issueCounts.quoteAnalyticsMismatchCount += 1;
          pushSample(auditSamples.quoteAnalyticsMismatches, {
            quoteId,
            quoteNo: contribution.quoteNo,
            mismatches,
          });
        }
        if (contribution.deleted && nonZeroSummaryValue(quoteAnalyticsDoc)) {
          issueCounts.deletedIncludedInAnalyticsCount += 1;
          pushSample(auditSamples.deletedIncludedInAnalytics, {
            quoteId,
            quoteNo: contribution.quoteNo,
            actual: {
              quotationRevenue: quoteAnalyticsDoc.quotationRevenue || 0,
              confirmedRevenue: quoteAnalyticsDoc.confirmedRevenue || 0,
            },
          });
        }
        if (contribution.cancelled && Number(quoteAnalyticsDoc.confirmedRevenue || 0) > 0) {
          issueCounts.cancelledIncludedInConfirmedRevenueCount += 1;
          pushSample(auditSamples.cancelledIncludedInConfirmedRevenue, {
            quoteId,
            quoteNo: contribution.quoteNo,
            confirmedRevenue: quoteAnalyticsDoc.confirmedRevenue || 0,
          });
        }
      }
    });

    crmLeadDocs.forEach((lead, leadId) => {
      const linkedQuotes = normalizeCrmLinkedQuotes(lead);
      const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
      const indexId = String(primaryQuote?.quoteId || lead.quoteId || leadId).trim();
      if (!indexId) return;

      const installationDate =
        lead.installationDate ||
        lead.expectedInstallationDate ||
        lead.scheduledInstallationDate ||
        lead.installedAt ||
        lead.deliveryDate ||
        lead.crm?.installationDate ||
        null;
      const installationDateKey = toDateInputValue(installationDate);
      const status = String(lead.status || "").toLowerCase();
      const workStage = String(lead.workStage || lead.stageId || lead.stage || "").toLowerCase();
      const inactive =
        lead.deleted === true ||
        ["cancelled", "canceled", "lost"].includes(status) ||
        ["cancelled", "canceled", "lost"].includes(workStage);

      if (installationDateKey && !inactive && !expectedInstallation.has(indexId)) {
        expectedInstallation.set(indexId, {
          source: "crm_leads",
          quoteId: primaryQuote?.quoteId || lead.quoteId || "",
          leadId,
          installationDateKey,
          monthKey: installationDateKey.slice(0, 7),
          customerName:
            lead.customerName || lead.contact?.name || lead.title || "Unnamed Customer",
          quoteNo: primaryQuote?.quoteNo || lead.quoteNo || "",
        });
      }
    });

    expectedMonthly.forEach((totals, monthKey) => {
      const actual = analyticsMonthlyDocs.get(monthKey);
      if (!actual) {
        issueCounts.missingAnalyticsMonthlyCount += 1;
        pushSample(auditSamples.missingAnalyticsMonthly, { monthKey, expected: totals });
        return;
      }
      const mismatches = [];
      compareMonthlyFields(
        actual,
        totals,
        [
          "confirmedRevenue",
          "confirmedCount",
          "quotationRevenue",
          "quotationCount",
          "cancelledRevenue",
          "cancelledCount",
          "installedRevenue",
          "installedCount",
        ],
        (mismatch) => mismatches.push(mismatch)
      );
      if (mismatches.length) {
        issueCounts.analyticsMismatchCount += 1;
        pushSample(auditSamples.analyticsMismatches, {
          monthKey,
          mismatches,
        });
      }
    });

    analyticsMonthlyDocs.forEach((actual, monthKey) => {
      if (!expectedMonthly.has(monthKey) && nonZeroSummaryValue(actual)) {
        issueCounts.staleAnalyticsMonthlyCount += 1;
        pushSample(auditSamples.staleAnalyticsMonthly, {
          monthKey,
          actual: {
            confirmedRevenue: actual.confirmedRevenue || 0,
            quotationRevenue: actual.quotationRevenue || 0,
            installedRevenue: actual.installedRevenue || 0,
          },
        });
      }
    });

    expectedClientMonthly.forEach((totals, clientDocId) => {
      const actual = clientMonthlyDocs.get(clientDocId);
      if (!actual) {
        issueCounts.missingClientMonthlyCount += 1;
        pushSample(auditSamples.missingClientMonthly, {
          clientDocId,
          metadata: expectedClientMetadata.get(clientDocId),
          expected: totals,
        });
        return;
      }
      const mismatches = [];
      compareMonthlyFields(
        actual,
        {
          ...totals,
          quoteCount: totals.quotationCount,
        },
        ["confirmedRevenue", "confirmedCount", "quotationRevenue", "quoteCount"],
        (mismatch) => mismatches.push(mismatch)
      );
      if (mismatches.length) {
        issueCounts.clientMonthlyMismatchCount += 1;
        pushSample(auditSamples.clientMonthlyMismatches, {
          clientDocId,
          metadata: expectedClientMetadata.get(clientDocId),
          mismatches,
        });
      }
    });

    const clientSignatureGroups = new Map();
    clientMonthlyDocs.forEach((actual, clientDocId) => {
      const signature = `${actual.monthKey || clientDocId.split("_")[0] || ""}_${
        actual.customerKey || "unknown"
      }`;
      clientSignatureGroups.set(signature, [
        ...(clientSignatureGroups.get(signature) || []),
        clientDocId,
      ]);
      if (!expectedClientMonthly.has(clientDocId) && nonZeroSummaryValue(actual)) {
        issueCounts.staleClientMonthlyCount += 1;
        pushSample(auditSamples.staleClientMonthly, {
          clientDocId,
          monthKey: actual.monthKey || "",
          customerName: actual.customerName || "",
          confirmedRevenue: actual.confirmedRevenue || 0,
          quotationRevenue: actual.quotationRevenue || 0,
        });
      }
    });
    clientSignatureGroups.forEach((ids, signature) => {
      if (ids.length > 1) {
        issueCounts.duplicatedClientMonthlyCount += ids.length - 1;
        pushSample(auditSamples.duplicateClientMonthly, { signature, ids });
      }
    });

    expectedInstallation.forEach((expected, installationId) => {
      const actual = installationIndexDocs.get(installationId);
      if (!actual) {
        issueCounts.missingInstallationIndexCount += 1;
        pushSample(auditSamples.missingInstallationIndex, {
          installationId,
          ...expected,
        });
        return;
      }
      const mismatches = [];
      ["installationDateKey", "monthKey"].forEach((field) => {
        if (String(actual[field] || "") !== String(expected[field] || "")) {
          mismatches.push({
            field,
            actual: actual[field] || "",
            expected: expected[field] || "",
          });
        }
      });
      if (mismatches.length) {
        issueCounts.installationMismatchCount += 1;
        pushSample(auditSamples.installationMismatches, {
          installationId,
          source: expected.source,
          mismatches,
        });
      }
    });

    installationIndexDocs.forEach((actual, installationId) => {
      if (!expectedInstallation.has(installationId) && actual.active !== false && actual.stale !== true) {
        issueCounts.staleInstallationIndexCount += 1;
        pushSample(auditSamples.staleInstallationIndex, {
          installationId,
          quoteId: actual.quoteId || "",
          leadId: actual.leadId || "",
          customerName: actual.customerName || "",
          installationDateKey: actual.installationDateKey || "",
        });
      }
    });

    quoteAnalyticsDocs.forEach((actual, quoteId) => {
      if (!sourceQuoteIds.has(quoteId) && actual.sourceMissing !== true && actual.stale !== true) {
        issueCounts.quoteAnalyticsSourceMissingCount += 1;
        pushSample(auditSamples.quoteAnalyticsSourceMissing, {
          quoteId,
          quoteNo: actual.quoteNo || "",
          customerName: actual.customerName || "",
        });
      }
    });

    quoteIndexDocs.forEach((actual, quoteId) => {
      if (!sourceQuoteIds.has(quoteId) && actual.sourceMissing !== true && actual.stale !== true) {
        issueCounts.quoteIndexSourceMissingCount += 1;
        pushSample(auditSamples.quoteIndexSourceMissing, {
          quoteId,
          quoteNo: actual.quoteNo || "",
          customerName: actual.customerName || "",
        });
      }
    });

    const totalMismatchCount = Object.entries(issueCounts)
      .filter(([key]) => !["invalidAmountCount", "invalidDateCount"].includes(key))
      .reduce((total, [, value]) => total + Number(value || 0), 0);
    const rootCauseHints = [];
    if (
      issueCounts.missingAnalyticsMonthlyCount ||
      issueCounts.missingClientMonthlyCount ||
      issueCounts.missingQuoteIndexCount ||
      issueCounts.missingQuoteAnalyticsIndexCount ||
      issueCounts.missingInstallationIndexCount
    ) {
      rootCauseHints.push("Derived index/summary documents are missing or rebuild did not complete.");
    }
    if (
      issueCounts.analyticsMismatchCount ||
      issueCounts.clientMonthlyMismatchCount ||
      issueCounts.quoteAnalyticsMismatchCount ||
      issueCounts.installationMismatchCount
    ) {
      rootCauseHints.push("Derived summaries are out of sync with source quotations/CRM leads.");
    }
    if (
      issueCounts.staleAnalyticsMonthlyCount ||
      issueCounts.staleClientMonthlyCount ||
      issueCounts.staleInstallationIndexCount ||
      issueCounts.quoteAnalyticsSourceMissingCount ||
      issueCounts.quoteIndexSourceMissingCount
    ) {
      rootCauseHints.push("Stale derived documents from earlier rebuild/diff logic are still visible.");
    }
    if (issueCounts.confirmedWithoutConfirmedAtCount) {
      rootCauseHints.push("Some confirmed quotations are missing confirmedAt, so monthly confirmed revenue needs repair.");
    }
    if (issueCounts.invalidAmountCount) {
      rootCauseHints.push("Some quotations do not expose a recognizable total amount field.");
    }
    if (!rootCauseHints.length) {
      rootCauseHints.push("No source-vs-index mismatches found by this audit.");
    }

    const report = {
      generatedAt: new Date().toISOString(),
      startedAt,
      workspaceUid,
      sourceDocumentCounts: {
        quotations: quoteDocs.size,
        crm_leads: crmLeadDocs.size,
        customers: (collectionDocs.customers || new Map()).size,
        crm_reminders: (collectionDocs.crm_reminders || new Map()).size,
      },
      derivedDocumentCounts: {
        quote_index: quoteIndexDocs.size,
        analytics_monthly: analyticsMonthlyDocs.size,
        client_monthly: clientMonthlyDocs.size,
        installation_index: installationIndexDocs.size,
        quote_analytics_index: quoteAnalyticsDocs.size,
      },
      expectedDerivedCounts: {
        quote_index: sourceQuoteIds.size,
        analytics_monthly: expectedMonthly.size,
        client_monthly: expectedClientMonthly.size,
        installation_index: expectedInstallation.size,
        quote_analytics_index: expectedQuoteAnalytics.size,
      },
      issueCounts,
      mismatchSummary: {
        totalMismatchCount,
        rootCauseHints,
      },
      expectedMonthly: Object.fromEntries(
        Array.from(expectedMonthly.entries()).map(([monthKey, totals]) => [
          monthKey,
          {
            confirmedRevenue: roundAuditNumber(totals.confirmedRevenue),
            confirmedCount: Number(totals.confirmedCount || 0),
            quotationRevenue: roundAuditNumber(totals.quotationRevenue),
            quotationCount: Number(totals.quotationCount || 0),
            cancelledRevenue: roundAuditNumber(totals.cancelledRevenue),
            cancelledCount: Number(totals.cancelledCount || 0),
            installedRevenue: roundAuditNumber(totals.installedRevenue),
            installedCount: Number(totals.installedCount || 0),
          },
        ])
      ),
      samples: auditSamples,
      collectionResults: nextCollections,
      readLabels: collectionNames.map((name) => `auditFirebaseData.scan.${name}`),
    };

    setAdminDataAuditState({
      running: false,
      checkedAt: report.generatedAt,
      error: "",
      collections: nextCollections,
      progress: {
        collection: "",
        scanned:
          quoteDocs.size +
          crmLeadDocs.size +
          quoteIndexDocs.size +
          analyticsMonthlyDocs.size +
          clientMonthlyDocs.size +
          installationIndexDocs.size +
          quoteAnalyticsDocs.size,
      },
      report,
    });
  } catch (error) {
    devError("Audit Firebase Data failed", error);
    setAdminDataAuditState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Audit failed",
    }));
    notify("Could not complete Firebase data audit.", "error");
  }
}, [
  adminDataAuditState.running,
  applySummaryDelta,
  buildAnalyticsContributionForQuote,
  getMonthlyDeltaFromContribution,
  notify,
  toDateInputValue,
  workspaceUid,
]);

const downloadAdminDataAuditReport = useCallback(() => {
  if (!adminDataAuditState.report || typeof document === "undefined") return;
  const fileNameWorkspace = String(workspaceUid || "workspace").replace(/[^a-z0-9_-]/gi, "");
  const blob = new Blob(
    [JSON.stringify(adminDataAuditState.report, null, 2)],
    { type: "application/json" }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `fabriquote-firebase-audit-${fileNameWorkspace}-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}, [adminDataAuditState.report, workspaceUid]);

const setSearchStatus = useCallback((key, patch) => {
  setGlobalSearchStatus((prev) => ({
    ...prev,
    [key]: {
      ...(prev[key] || {}),
      ...patch,
    },
  }));
}, []);

const normalizeCrmLeadSearchRecord = useCallback(
  (entry = {}) => {
    const lead = buildCrmLeadData({
      id: entry.id,
      ...entry,
    });
    return {
      ...lead,
      ...buildCrmSearchIndexFields(lead),
    };
  },
  [buildCrmLeadData]
);

const mergeCrmLeadSearchRecords = useCallback(
  (entries = []) => {
    const normalizedEntries = (Array.isArray(entries) ? entries : [])
      .map(normalizeCrmLeadSearchRecord)
      .filter((lead) => lead?.id);
    if (normalizedEntries.length === 0) return;

    setCrmLeadRecords((prev) => {
      const merged = new Map((prev || []).map((lead) => [String(lead.id || ""), lead]));
      normalizedEntries.forEach((lead) => {
        const key = String(lead.id || "");
        merged.set(key, {
          ...(merged.get(key) || {}),
          ...lead,
        });
      });
      return sortCrmRecordsByLatestActivity(Array.from(merged.values()));
    });
  },
  [normalizeCrmLeadSearchRecord]
);

const mergeCustomerSearchRecords = useCallback(
  (entries = []) => {
    const normalizedEntries = mergeCustomersById(entries);
    if (normalizedEntries.length === 0) return;

    setCustomerRecords((prev) => {
      const merged = new Map((prev || []).map((customer) => [String(customer.id || ""), customer]));
      normalizedEntries.forEach((customer) => {
        const key = String(customer.id || "");
        merged.set(key, {
          ...(merged.get(key) || {}),
          ...customer,
        });
      });
      return Array.from(merged.values()).sort(
        (a, b) =>
          safeTimeMillis(b.lastActivityAt || b.updatedAt || b.createdAt) -
          safeTimeMillis(a.lastActivityAt || a.updatedAt || a.createdAt)
      );
    });
  },
  [mergeCustomersById]
);

const runCrmGlobalSearch = useCallback(
  async (queryText, statusKey = "crm", append = false) => {
    const normalizedQuery = normalizeSearchText(queryText);
    if (!workspaceUid || !canAccessCrm || normalizedQuery.length < 2) return;

    const existingPage = crmSearchPagesRef.current.get(statusKey);
    if (append && (existingPage?.busy || !existingPage?.hasMore || existingPage?.query !== `${workspaceUid}:${normalizedQuery}`)) return;
    const requestPage = append ? existingPage : { query: `${workspaceUid}:${normalizedQuery}`, state: {}, hasMore: true };
    requestPage.busy = true;
    crmSearchPagesRef.current.set(statusKey, requestPage);
    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const page = await globalSearchCrmLeads(workspaceUid, queryText, {
        limit: 25,
        pagination: requestPage.state,
        labelPrefix: `${statusKey}.globalCrmLeadSearch`,
      });
      if (crmSearchPagesRef.current.get(statusKey) !== requestPage) return;
      const results = page.records;
      requestPage.state = page.pageState;
      requestPage.hasMore = page.hasMore;
      mergeCrmLeadSearchRecords(results);
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
      if (isDevRuntime) {
        devLog("GLOBAL CRM SEARCH HYDRATED", {
          statusKey,
          query: queryText,
          resultCount: results.length,
        });
      }
    } catch (error) {
      if (crmSearchPagesRef.current.get(statusKey) !== requestPage) return;
      devError("Global CRM search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older CRM records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    } finally {
      requestPage.busy = false;
      setCrmSearchPageRevision((prev) => prev + 1);
    }
  },
  [
    canAccessCrm,
    mergeCrmLeadSearchRecords,
    notify,
    setSearchStatus,
    workspaceUid,
  ]
);

const runCustomerGlobalSearch = useCallback(
  async (queryText, statusKey = "customers") => {
    const normalizedQuery = normalizeCustomerSearchText(queryText);
    if (!workspaceUid || normalizedQuery.length < 2) return;

    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const results = await globalSearchCustomers(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: `${statusKey}.globalCustomerSearch`,
      });
      mergeCustomerSearchRecords(results);
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
      if (isDevRuntime) {
        devLog("GLOBAL CUSTOMER SEARCH HYDRATED", {
          statusKey,
          query: queryText,
          resultCount: results.length,
        });
      }
    } catch (error) {
      devError("Global customer search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older customer records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [
    mergeCustomerSearchRecords,
    notify,
    setSearchStatus,
    workspaceUid,
  ]
);

const runTaskHistoryGlobalSearch = useCallback(
  async (queryText, statusKey = "taskHistorySearch") => {
    const normalizedQuery = normalizeSearchText(queryText);
    if (!workspaceUid || !canAccessCrm || normalizedQuery.length < 2) return;

    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const results = await globalSearchTaskHistory(workspaceUid, queryText, {
        limit: GLOBAL_SEARCH_RESULT_LIMIT,
        labelPrefix: `${statusKey}.globalTaskHistorySearch`,
      });
      setCrmTaskHistoryRecords((prev) => {
        const merged = new Map((prev || []).map((record) => [String(record.id || ""), record]));
        results.forEach((record) => {
          const key = String(record.id || "");
          if (!key) return;
          merged.set(key, {
            ...(merged.get(key) || {}),
            ...record,
          });
        });
        return Array.from(merged.values()).sort(
          (a, b) =>
            safeTimeMillis(b.completedAt || b.updatedAt || b.createdAt) -
            safeTimeMillis(a.completedAt || a.updatedAt || a.createdAt)
        );
      });
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
    } catch (error) {
      devError("Global task history search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older task records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [canAccessCrm, notify, setSearchStatus, workspaceUid]
);

const subscribeCrmLeads = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const allowedLeadTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

  if (allowedLeadTypes.length === 0) {
    setCrmLeadRecords([]);
    setDebugRawCrmLeadCount(0);
    return () => {};
  }

  const collectionPath = `users/${uid}/crm_leads`;
  if (IS_DEV_UI_ENABLED) {
    devLog("workspaceUid:", uid);
    devLog("module:", "CRM");
    devLog("collectionPath:", collectionPath);
  }

  const leadsRef = collection(db, "users", uid, "crm_leads");
  let cancelled = false;
  const cursors = { recent: null, legacy: null };
  const exhausted = { recent: false, legacy: false };
  let busy = false;
  const loadPage = async (mode = "recent", append = true) => {
    if (cancelled || busy || (append && exhausted[mode])) return;
    busy = true;
    setCrmLeadPaging((prev) => ({ ...prev, loading: true, error: "", retryMode: mode }));
    const { pageQuery } = buildCrmLeadListQueries(leadsRef, mode, append ? cursors[mode] : null);
    return getDocsCached(pageQuery, {
      label: `loadCrmLeadsPage.${mode}`,
      cacheKey: `crmDeals:recent:${uid}:${mode}:after=${append ? cursors[mode]?.id || "start" : "start"}:limit25`,
      ttlMs: 5 * 60 * 1000,
    })
    .then(async (snap) => {
      if (cancelled) return;
      const sourceSnap = snap;
      if (IS_DEV_UI_ENABLED) {
        devLog("CRM count:", sourceSnap.size);
      }
      setDebugRawCrmLeadCount(sourceSnap.size);
      const nextLeads = [];

      sourceSnap.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        const pipelineType = isGeneralExecutionEligibleDeal(raw)
          ? GENERAL_QUOTE_TYPE
          : CURTAIN_QUOTE_TYPE;

        if (!allowedLeadTypes.includes(pipelineType)) {
          return;
        }

        const needsQuoteLinkMigration = CRM_LEGACY_QUOTE_LINK_FIELDS.some((field) =>
          Object.prototype.hasOwnProperty.call(raw, field)
        );
        const linkedQuotes = normalizeCrmLinkedQuotes({
          ...raw,
          linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
        });
        const rawChecklistItems = Array.isArray(raw.checklistItems)
          ? raw.checklistItems
          : Array.isArray(raw.checklist)
          ? raw.checklist
          : Array.isArray(raw.stageChecklist)
          ? raw.stageChecklist
          : [];

        nextLeads.push(stripLegacyCrmQuoteLinkFields({
          id: docSnap.id,
          ...raw,
          quoteType: raw.quoteType || pipelineType,
          pipelineType,
          contact: raw.contact || {},
          customerId: raw.customerId || "",
          customerName:
            raw.customerName ||
            raw.contact?.name ||
            raw.title ||
            "",
          customerPhone:
            raw.customerPhone ||
            raw.contact?.phone ||
            raw.contact?.whatsapp ||
            "",
          linkedQuotes,
          tags: Array.isArray(raw.tags) ? raw.tags : [],
          checklistItems: rawChecklistItems,
          hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
          reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
            nextActionDate: normalizeTs(raw.nextActionDate),
            installationDate: normalizeTs(raw.installationDate),
          }),
          followUps: normalizeCrmFollowUps(raw.followUps, {
            nextAction: raw.nextAction,
            nextActionDate: normalizeTs(raw.nextActionDate),
            reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
          }),
          attachments: normalizeCrmAttachments(raw.attachments),
          executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
          assignedAt: normalizeTs(raw.assignedAt),
          createdAt: normalizeTs(raw.createdAt),
          updatedAt: normalizeTs(raw.updatedAt),
          lastActivityAt: normalizeTs(raw.lastActivityAt),
          needsQuoteLinkMigration,
        }));
      });

      cursors[mode] = sourceSnap.docs[sourceSnap.docs.length - 1] || cursors[mode];
      exhausted[mode] = sourceSnap.size < 25;
      setCrmLeadPaging((prev) => ({ ...prev, loading: false, error: "",
        hasMore: !exhausted.recent, legacyHasMore: !exhausted.legacy }));
      setCrmLeadRecords((previous) => sortCrmRecordsByLatestActivity(
        Array.from(new Map([...(append ? previous : []), ...nextLeads].map((item) => [item.id, item])).values())
      ));
      if (append) {
        setCrmDealsVisibleCount((prev) => prev + CRM_DEALS_PAGE_SIZE);
        setCrmLeadsVisibleCount((prev) => prev + CRM_LEADS_PAGE_SIZE);
      }
    })
    .catch((error) => {
      if (cancelled) return;
      devError("CRM leads load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      setCrmLeadPaging((prev) => ({ ...prev, loading: false,
        error: "Could not load the next CRM page. Existing records are unchanged." }));
    }).finally(() => { busy = false; });
  };
  crmLeadPagerRef.current = { uid, loadPage };
  setCrmArchivePage({ rows: [], cursor: null, hasMore: false, loading: false, error: "", loaded: false });
  setCrmLeadPaging({ loading: false, hasMore: false, legacyHasMore: true, error: "" });
  loadPage("recent", false);

  return () => {
    cancelled = true;
    if (crmLeadPagerRef.current?.uid === uid) crmLeadPagerRef.current = null;
    if (isDevRuntime) {
      devLog("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmLeadsOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, notify]);

const normalizeCrmTaskRecord = useCallback((docId, raw = {}) => {
  const dueAt = normalizeCrmDate(raw.dueAt || raw.dueDate || null);
  const completedAt = normalizeCrmDate(raw.completedAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  return {
    id: docId,
    taskId: String(raw.taskId || docId).trim(),
    workspaceUid: raw.workspaceUid || "",
    relatedType: raw.relatedType || "",
    relatedId: raw.relatedId || "",
    customerId: raw.customerId || "",
    customerName: raw.customerName || "Unnamed Customer",
    opportunityId: raw.opportunityId || "",
    projectId: raw.projectId || "",
    quoteId: raw.quoteId || "",
    quoteNo: raw.quoteNo || "",
    taskType: raw.taskType || "other",
    title: buildCrmTaskTitle(raw),
    notes: raw.notes || "",
    ownerUid: raw.ownerUid || "",
    ownerName: raw.ownerName || "Unassigned",
    dueAt,
    dueDate: dueAt,
    dueDateKey: toDateInputValue(dueAt),
    dueTime: dueAt
      ? `${String(dueAt.getHours()).padStart(2, "0")}:${String(
          dueAt.getMinutes()
        ).padStart(2, "0")}`
      : "",
    dueMillis: safeTimeMillis(dueAt),
    status: raw.status || CRM_TASK_STATUS_OPEN,
    priority: CRM_TASK_PRIORITIES.includes(String(raw.priority || "").trim())
      ? String(raw.priority).trim()
      : "normal",
    completedAt,
    completedBy: raw.completedBy || "",
    systemCreated: raw.systemCreated === true,
    systemGenerated: raw.systemGenerated === true || raw.systemCreated === true,
    recurringType: raw.recurringType || "",
    trackingTaskForProjectId: raw.trackingTaskForProjectId || "",
    paymentFollowUpForProjectId: raw.paymentFollowUpForProjectId || "",
    dueAmount: Number.isFinite(Number(raw.dueAmount)) ? Number(raw.dueAmount) : null,
    dailyCheckTime: raw.dailyCheckTime || "",
    templateKey: raw.templateKey || "",
    templateTaskKey: raw.templateTaskKey || "",
    sourceKind: raw.sourceKind || "",
    createdAt,
    updatedAt,
    original: raw,
  };
}, [toDateInputValue]);

const normalizeCrmOpportunityRecord = useCallback((docId, raw = {}) => {
  const nextActionDueAt = normalizeCrmDate(raw.nextActionDueAt || null);
  const updatedAt = normalizeCrmDate(raw.updatedAt || null);
  const createdAt = normalizeCrmDate(raw.createdAt || null);
  return {
    id: docId,
    opportunityId: raw.opportunityId || docId,
    legacyLeadId: raw.legacyLeadId || "",
    customerName: raw.customerName || buildOpportunityDisplayTitle(raw),
    customerPhone: raw.customerPhone || "",
    ownerUid: raw.ownerUid || "",
    ownerName: raw.ownerName || "",
    stage: normalizeOpportunityStage(raw.stage),
    stageLabel: raw.stageLabel || getOpportunityStageLabel(raw.stage),
    status: normalizeOpportunityStatus(raw.status),
    nextActionText: raw.nextActionText || "",
    nextActionDueAt,
    nextActionStatus: getNextActionStatus(raw),
    latestQuoteId: raw.latestQuoteId || "",
    latestQuoteNo: raw.latestQuoteNo || "",
    latestQuoteAmount: Number(raw.latestQuoteAmount || raw.estimatedValue || 0),
    updatedAt,
    createdAt,
    raw,
  };
}, []);

const normalizeCrmDrawerQuoteRecord = useCallback((docId, raw = {}) => ({
  id: docId,
  quoteIndexId: docId,
  quoteId: raw.quoteId || raw.sourceQuoteId || docId,
  quoteNo: raw.quoteNo || docId,
  quoteType: raw.quoteType || raw.quotationType || "",
  customerName: raw.customerName || "",
  crmStatus: raw.crmStatus || raw.status || "",
  followUpDueAt: normalizeCrmDate(raw.followUpDueAt || null),
  updatedAt: normalizeCrmDate(raw.updatedAt || null),
  totalAmount: Number(raw.grandTotal || raw.finalTotal || raw.totalAmount || 0),
  raw,
}), []);

const normalizeCrmActivityRecord = useCallback((docId, raw = {}) => ({
  id: docId,
  activityId: raw.activityId || docId,
  activityType: raw.activityType || "",
  title: raw.title || "Activity",
  description: raw.description || "",
  createdAt: normalizeCrmDate(raw.createdAt || null),
  createdByName: raw.createdByName || raw.createdBy || "",
  quoteNo: raw.quoteNo || "",
  quoteId: raw.quoteId || "",
  trackingItemId: raw.trackingItemId || "",
  materialId: raw.materialId || "",
  projectId: raw.projectId || "",
  relatedType: raw.relatedType || "",
  relatedId: raw.relatedId || "",
  raw,
}), []);

const loadCrmProjects = useCallback(async (uid) => {
  if (!uid || !canAccessCrm) {
    setCrmProjectRecords([]);
    setCrmProjectsState({ loading: false, loaded: false, error: "" });
    return;
  }
  setCrmProjectsState((prev) => ({ ...prev, loading: true, error: "" }));
  const projectsRef = collection(db, "users", uid, "projects");
  const mergeProjectQueryResults = (querySnapshots = []) => {
    const byProjectId = new Map();
    querySnapshots.forEach((querySnap) => {
      querySnap.docs.forEach((docSnap) => {
        const normalizedProject = normalizeCrmProjectRecord(
          docSnap.id,
          docSnap.data() || {}
        );
        const projectKey = String(
          normalizedProject.projectId || normalizedProject.id || docSnap.id
        ).trim();
        if (!projectKey) return;
        const existingProject = byProjectId.get(projectKey);
        const nextSortTime = safeTimeMillis(
          normalizedProject.lastActivityAt ||
            normalizedProject.updatedAt ||
            normalizedProject.createdAt
        );
        const existingSortTime = safeTimeMillis(
          existingProject?.lastActivityAt ||
            existingProject?.updatedAt ||
            existingProject?.createdAt
        );
        if (!existingProject || nextSortTime >= existingSortTime) {
          byProjectId.set(projectKey, normalizedProject);
        }
      });
    });
    return Array.from(byProjectId.values())
      .sort(
        (left, right) =>
          safeTimeMillis(right.lastActivityAt || right.updatedAt || right.createdAt) -
          safeTimeMillis(left.lastActivityAt || left.updatedAt || left.createdAt)
      )
      .slice(0, CRM_PROJECTS_READ_LIMIT);
  };
  try {
    const [snap, legacySnap] = await Promise.all([
      getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreOrderBy("lastActivityAt", "desc"),
          firestoreLimit(CRM_PROJECTS_READ_LIMIT)
        ),
        {
          label: "crmProjects.list",
          cacheKey: `projects:${uid}:ordersPipeline:lastActivityAtDesc:limit${CRM_PROJECTS_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      ),
      getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_PROJECTS_READ_LIMIT)
        ),
        {
          label: "crmProjects.listLegacyWindow",
          cacheKey: `projects:${uid}:ordersPipeline:updatedAtDescLegacyWindow:limit${CRM_PROJECTS_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      ),
    ]);
    const projects = mergeProjectQueryResults([snap, legacySnap]);

    setCrmProjectRecords(projects);
    setCrmProjectsState({ loading: false, loaded: true, error: "" });
    if (isDevRuntime) {
      devLog("CRM_PROJECT_QUERY_TRACE", {
        filters: { mode: "orders_pipeline", limit: CRM_PROJECTS_READ_LIMIT },
        primaryReadCount: snap.size,
        legacyWindowReadCount: legacySnap.size,
        projectsRead: snap.size + legacySnap.size,
        mergedProjectCount: projects.length,
        querySource: "lastActivityAt_desc_plus_updatedAt_desc_legacy_merge",
      });
    }
  } catch (error) {
    try {
      const fallbackSnap = await getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_PROJECTS_READ_LIMIT)
        ),
        {
          label: "crmProjects.listFallbackAfterError",
          cacheKey: `projects:${uid}:ordersPipeline:updatedAtDesc:limit${CRM_PROJECTS_READ_LIMIT}`,
          ttlMs: 30 * 1000,
        }
      );
      const fallbackProjects = mergeProjectQueryResults([fallbackSnap]);
      setCrmProjectRecords(fallbackProjects);
      setCrmProjectsState({ loading: false, loaded: true, error: "" });
      if (isDevRuntime) {
        devWarn("CRM_PROJECT_QUERY_TRACE", {
          filters: { mode: "orders_pipeline", limit: CRM_PROJECTS_READ_LIMIT },
          primaryErrorCode: error?.code,
          primaryErrorMessage: error?.message,
          fallbackReadCount: fallbackSnap.size,
          projectsRead: fallbackProjects.length,
          querySource: "updatedAt_desc_fallback_after_error",
        });
      }
      return;
    } catch (fallbackError) {
      devError("CRM projects fallback load failed:", {
        code: fallbackError?.code,
        message: fallbackError?.message,
        path: `users/${uid}/projects`,
        mode: "orders_pipeline",
      });
    }
    devError("CRM projects load failed:", {
      code: error?.code,
      message: error?.message,
      path: `users/${uid}/projects`,
      mode: "orders_pipeline",
    });
    setCrmProjectsState({
      loading: false,
      loaded: true,
      error:
        error?.code === "failed-precondition"
          ? "Project indexes are still building. Please try again shortly."
          : "Could not load orders.",
    });
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [canAccessCrm, normalizeCrmProjectRecord, notify]);

const loadCrmProjectById = useCallback(
  async (projectId, options = {}) => {
    const cleanProjectId = String(projectId || "").trim();
    if (!workspaceUid || !canAccessCrm || !cleanProjectId) return null;
    const { navigate = false, notifyMissing = false } = options || {};
    try {
      const projectRef = doc(db, "users", workspaceUid, "projects", cleanProjectId);
      const snap = await trackedGetDoc(projectRef, {
        label: "crmProject.loadById",
      });
      if (!snap.exists()) {
        if (notifyMissing) notify("Order handover was not found.", "error");
        return null;
      }
      const project = normalizeCrmProjectRecord(snap.id, snap.data() || {});
      setCrmProjectRecords((prev) => {
        const existing = prev.some(
          (item) => String(item.projectId || item.id) === String(project.projectId || project.id)
        );
        return existing
          ? prev.map((item) =>
              String(item.projectId || item.id) === String(project.projectId || project.id)
                ? project
                : item
            )
          : [project, ...prev];
      });
      setSelectedCrmProjectId(project.projectId || project.id);
      setCrmProjectDrawerTab("overview");
      if (navigate) {
        setCrmView("projects");
        setRoutedTab("crm");
      }
      if (isDevRuntime) {
        devLog("CRM_PROJECT_QUERY_TRACE", {
          filters: { projectId: cleanProjectId },
          projectsRead: 1,
          querySource: "exact_project_read",
        });
      }
      return project;
    } catch (error) {
      devError("CRM project exact load failed:", {
        code: error?.code,
        message: error?.message,
        path: `users/${workspaceUid}/projects/${cleanProjectId}`,
      });
      if (notifyMissing) notify("Could not open order handover. Please try again.", "error");
      return null;
    }
  },
  [canAccessCrm, normalizeCrmProjectRecord, notify, setRoutedTab, workspaceUid]
);

const loadCrmProjectDrawerSection = useCallback(
  async (section, projectIdOverride = selectedCrmProjectId) => {
    const projectId = String(projectIdOverride || "").trim();
    if (
      !workspaceUid ||
      !projectId ||
      !["tasks", "tracking", "payments", "snags", "activity"].includes(section)
    ) return;
    if (section === "tracking" && crmSettings.enableDeliveryTracking === false) return;
    if (section === "payments" && crmSettings.enablePaymentTracking === false) return;
    setCrmProjectDrawerData((prev) => ({
      ...prev,
      projectId,
      loading: { ...(prev.loading || {}), [section]: true },
      errors: { ...(prev.errors || {}), [section]: "" },
    }));
    try {
      if (section === "tasks") {
        const snap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_tasks"),
            firestoreWhere("relatedType", "==", "project"),
            firestoreWhere("relatedId", "==", projectId),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "crmProjectDrawer.tasks",
            cacheKey: `crmTasks:${workspaceUid}:project:${projectId}:dueAtAsc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        setCrmProjectDrawerData((prev) => ({
          ...prev,
          projectId,
          tasks: snap.docs.map((item) => normalizeCrmTaskRecord(item.id, item.data() || {})),
          loading: { ...(prev.loading || {}), tasks: false },
        }));
        return;
      }
      if (section === "tracking") {
        const snap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "project_tracking_items"),
            firestoreWhere("projectId", "==", projectId),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_TRACKING_ITEMS_READ_LIMIT)
          ),
          {
            label: "crmProjectDrawer.tracking",
            cacheKey: `projectTracking:${workspaceUid}:project:${projectId}:updatedAtDesc:limit${CRM_PROJECT_TRACKING_ITEMS_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        const materialRecords = snap.docs.map((item) =>
          normalizeCrmMaterialRecord(item.id, item.data() || {})
        );
        if (isDevRuntime) {
          devLog("CRM_TRACKING_QUERY_TRACE", {
            projectId,
            trackingItemsRead: snap.size,
            source: "project_drawer_tracking_tab",
          });
        }
        setCrmProjectDrawerData((prev) => ({
          ...prev,
          projectId,
          trackingItems: materialRecords,
          loading: { ...(prev.loading || {}), tracking: false },
        }));
        return;
      }
      if (section === "payments") {
        const snap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "project_payments"),
            firestoreWhere("projectId", "==", projectId),
            firestoreOrderBy("paymentDate", "desc"),
            firestoreLimit(CRM_PROJECT_PAYMENTS_READ_LIMIT)
          ),
          {
            label: "crmProjectDrawer.payments",
            cacheKey: `projectPayments:${workspaceUid}:project:${projectId}:paymentDateDesc:limit${CRM_PROJECT_PAYMENTS_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        const paymentRecords = snap.docs.map((item) =>
          normalizeCrmPaymentRecord(item.id, item.data() || {})
        );
        if (isDevRuntime) {
          devLog("CRM_PAYMENT_QUERY_TRACE", {
            projectId,
            paymentsRead: snap.size,
            source: "project_drawer_payments_tab",
          });
        }
        setCrmProjectDrawerData((prev) => ({
          ...prev,
          projectId,
          payments: paymentRecords,
          loading: { ...(prev.loading || {}), payments: false },
        }));
        return;
      }
      if (section === "snags") {
        const selectedProjectForSnags =
          crmProjectRecords.find(
            (project) =>
              String(project.projectId || "") === String(projectId) ||
              String(project.id || "") === String(projectId)
          ) || null;
        const snagParentIds = Array.from(
          new Set(
            [
              projectId,
              selectedProjectForSnags?.id,
              selectedProjectForSnags?.projectId,
            ]
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          )
        );
        const snagSnaps = await Promise.all(
          snagParentIds.map((parentProjectId) =>
            trackedGetDocs(
              firestoreQuery(
                collection(db, "users", workspaceUid, "projects", parentProjectId, "snags"),
                firestoreOrderBy("updatedAt", "desc"),
                firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
              ),
              {
                label: "crmProjectDrawer.snags",
                cacheKey: `projectSnags:${workspaceUid}:project:${parentProjectId}:updatedAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
              }
            ).then((snap) => ({ parentProjectId, snap }))
          )
        );
        const fetchedSnagRecords = [];
        snagSnaps.forEach(({ parentProjectId, snap }) => {
          snap.docs.forEach((item) => {
            fetchedSnagRecords.push(
              normalizeCrmProjectSnagRecord(item.id, {
                ...(item.data() || {}),
                projectId: parentProjectId,
              })
            );
          });
        });
        if (!fetchedSnagRecords.length) {
          const taskFallbackSnaps = await Promise.all(
            snagParentIds.map((parentProjectId) =>
              trackedGetDocs(
                firestoreQuery(
                  collection(db, "users", workspaceUid, "crm_tasks"),
                  firestoreWhere("projectId", "==", parentProjectId),
                  firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
                ),
                {
                  label: "crmProjectDrawer.snagsFromTasks",
                  cacheKey: `crmTasks:${workspaceUid}:projectSnagFallback:${parentProjectId}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
                }
              ).then((snap) => ({ parentProjectId, snap }))
            )
          );
          taskFallbackSnaps.forEach(({ parentProjectId, snap }) => {
            snap.docs.forEach((item) => {
              const task = normalizeCrmTaskRecord(item.id, item.data() || {});
              const isSnagTask =
                task.sourceKind === "project_snag" ||
                task.relatedType === "project_snag" ||
                task.taskType === "snag";
              if (!isSnagTask) return;
              const snagId =
                String(task.relatedId || task.sourceId || "").trim() ||
                String(task.taskId || task.id || "").replace(/^.*__snag_/, "");
              if (!snagId) return;
              fetchedSnagRecords.push(
                normalizeCrmProjectSnagRecord(snagId, {
                  snagId,
                  projectId: parentProjectId,
                  workspaceUid,
                  customerId: task.customerId || selectedProjectForSnags?.customerId || "",
                  customerName: task.customerName || selectedProjectForSnags?.customerName || "",
                  quoteId: task.quoteId || selectedProjectForSnags?.quoteId || "",
                  quoteNo: task.quoteNo || selectedProjectForSnags?.quoteNo || "",
                  title: String(task.title || "Snag / correction").replace(/^Resolve snag:\s*/i, ""),
                  description: task.notes || "",
                  priority: task.priority === "urgent" ? "urgent" : "normal",
                  status:
                    task.status === CRM_TASK_STATUS_COMPLETED ||
                    task.status === CRM_TASK_STATUS_CANCELLED
                      ? "resolved"
                      : "open",
                  dueAt: task.dueAt || null,
                  ownerUid: task.ownerUid || "",
                  ownerName: task.ownerName || "",
                  createdAt: task.createdAt || null,
                  updatedAt: task.updatedAt || task.createdAt || null,
                  sourceTaskId: task.taskId || task.id || item.id,
                })
              );
            });
          });
        }
        if (isDevRuntime) {
          devLog("CRM_PROJECT_SNAG_QUERY_TRACE", {
            projectId,
            snagParentIds,
            snagsRead: fetchedSnagRecords.length,
            source: "project_drawer_snags_tab",
          });
        }
        setCrmProjectDrawerData((prev) => ({
          ...prev,
          projectId,
          snags: (() => {
            const existingSnags =
              String(prev.projectId || "") === String(projectId)
                ? prev.snags || []
                : [];
            const byId = new Map();
            [...existingSnags, ...fetchedSnagRecords].forEach((snag) => {
              const id = String(snag.snagId || snag.id || "").trim();
              if (!id) return;
              byId.set(id, snag);
            });
            return Array.from(byId.values()).sort(
              (left, right) =>
                safeTimeMillis(right.updatedAt || right.createdAt) -
                safeTimeMillis(left.updatedAt || left.createdAt)
            );
          })(),
          loading: { ...(prev.loading || {}), snags: false },
        }));
        return;
      }
      const snap = await getDocsCached(
        firestoreQuery(
          collection(db, "users", workspaceUid, "activity_log"),
          firestoreWhere("relatedType", "==", "project"),
          firestoreWhere("relatedId", "==", projectId),
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
        ),
        {
          label: "crmProjectDrawer.activity",
          cacheKey: `activityLog:${workspaceUid}:project:${projectId}:createdAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      );
      let activityRecords = snap.docs.map((item) =>
        normalizeCrmActivityRecord(item.id, item.data() || {})
      );
      let activityFallbackReadCount = 0;
      if (!activityRecords.length) {
        const fallbackSnap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "activity_log"),
            firestoreWhere("projectId", "==", projectId),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "crmProjectDrawer.activityByProjectId",
            cacheKey: `activityLog:${workspaceUid}:projectId:${projectId}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        activityFallbackReadCount = fallbackSnap.size;
        activityRecords = fallbackSnap.docs
          .map((item) => normalizeCrmActivityRecord(item.id, item.data() || {}))
          .sort((left, right) => safeTimeMillis(right.createdAt) - safeTimeMillis(left.createdAt));
      }
      if (isDevRuntime) {
        devLog("CRM_PROJECT_QUERY_TRACE", {
          filters: { projectId, section: "activity", limit: CRM_PROJECT_RELATED_READ_LIMIT },
          projectsRead: 0,
          activityRead: snap.size,
          activityFallbackReadCount,
        });
      }
      setCrmProjectDrawerData((prev) => ({
        ...prev,
        projectId,
        activity: activityRecords,
        loading: { ...(prev.loading || {}), activity: false },
      }));
    } catch (error) {
      devError("CRM project drawer load failed:", {
        code: error?.code,
        message: error?.message,
        projectId,
        section,
      });
      setCrmProjectDrawerData((prev) => ({
        ...prev,
        projectId,
        loading: { ...(prev.loading || {}), [section]: false },
        errors: {
          ...(prev.errors || {}),
          [section]:
            error?.code === "failed-precondition"
              ? "Project detail index is still building."
              : "Could not load this section.",
        },
      }));
    }
  },
  [
    normalizeCrmActivityRecord,
    normalizeCrmMaterialRecord,
    normalizeCrmPaymentRecord,
    normalizeCrmProjectSnagRecord,
    normalizeCrmTaskRecord,
    crmProjectRecords,
    crmSettings.enableDeliveryTracking,
    crmSettings.enablePaymentTracking,
    selectedCrmProjectId,
    workspaceUid,
  ]
);

const createCrmMaterialDraft = useCallback((projectLike = {}, materialLike = null) => {
  const material = materialLike || {};
  return {
    materialId: material.trackingItemId || material.materialId || material.id || "",
    trackingItemId: material.trackingItemId || material.materialId || material.id || "",
    projectId: projectLike.projectId || projectLike.id || material.projectId || "",
    materialType: normalizeTrackingItemType(material.itemType || material.materialType || "fabric"),
    itemType: normalizeTrackingItemType(material.itemType || material.materialType || "fabric"),
    itemName: material.itemName || "",
    brandName: material.brandName || "",
    fabricCode: material.fabricCode || "",
    catalogueName: material.catalogueName || "",
    srNumber: material.srNumber || "",
    supplierName: material.supplierName || "",
    supplierPhone: material.supplierPhone || "",
    quantity: material.quantity == null ? "" : String(material.quantity),
    unit: material.unit || "meter",
    orderedQuantity: material.orderedQuantity == null ? "" : String(material.orderedQuantity),
    receivedQuantity: material.receivedQuantity == null ? "" : String(material.receivedQuantity),
    orderDate: material.orderDateKey || toDateInputValue(material.orderDate) || "",
    expectedArrivalDate:
      material.expectedArrivalDateKey || toDateInputValue(material.expectedArrivalDate) || "",
    receivedDate: material.receivedDateKey || toDateInputValue(material.receivedDate) || "",
    courierName: material.courierName || "",
    transportName: material.transportName || "",
    trackingNumber: material.trackingNumber || "",
    trackingUrl: material.trackingUrl || "",
    lrNumber: material.lrNumber || "",
    status: normalizeTrackingStatus(material.status || "not_ordered"),
    priority: material.priority === "urgent" ? "urgent" : "normal",
    lastUpdateNote: material.lastUpdateNote || "",
    notes: material.notes || "",
    createdAt: material.createdAt || material.raw?.createdAt || null,
  };
}, [toDateInputValue]);

const patchCrmMaterialDrawerState = useCallback((projectId, materialId, materialPatch = {}) => {
  if (!projectId || !materialId) return;
  invalidateWorkspaceCollectionCache(workspaceUid, "project_tracking_items");
  const normalized = normalizeCrmMaterialRecord(materialId, {
    ...materialPatch,
    materialId,
    projectId,
    updatedAt: materialPatch.updatedAt || new Date(),
  });
  setCrmProjectDrawerData((prev) => {
    if (String(prev.projectId || "") !== String(projectId)) return prev;
    let found = false;
    const nextMaterials = (prev.trackingItems || []).map((material) => {
      if (String(material.materialId || material.id) !== String(materialId)) return material;
      found = true;
      return normalizeCrmMaterialRecord(materialId, {
        ...(material.raw || material),
        ...materialPatch,
        materialId,
        projectId,
        updatedAt: materialPatch.updatedAt || new Date(),
      });
    });
    const merged = found ? nextMaterials : [normalized, ...nextMaterials];
    return {
      ...prev,
      trackingItems: merged.sort(
        (left, right) =>
          safeTimeMillis(right.updatedAt || right.createdAt) -
          safeTimeMillis(left.updatedAt || left.createdAt)
      ),
    };
  });
}, [normalizeCrmMaterialRecord, workspaceUid]);

const patchProjectDeliveryTrackingSummary = useCallback(
  async (projectLike = {}, trackingItems = [], overrides = {}) => {
    const projectId = String(projectLike.projectId || projectLike.id || "").trim();
    if (!workspaceUid || !projectId) return null;
    const currentSummary =
      projectLike.deliveryTracking ||
      projectLike.raw?.deliveryTracking ||
      {};
    const computed = buildProjectTrackingSummary(trackingItems);
    const nextSummary = sanitizeCrmFirestoreValue({
      ...currentSummary,
      enabled:
        overrides.enabled != null
          ? overrides.enabled === true
          : computed.openItemCount > 0,
      trackingComplete:
        overrides.trackingComplete != null
          ? overrides.trackingComplete === true
          : computed.trackingComplete,
      trackingOwnerUid:
        overrides.trackingOwnerUid ||
        currentSummary.trackingOwnerUid ||
        projectLike.projectOwnerUid ||
        "",
      trackingOwnerName:
        overrides.trackingOwnerName ||
        currentSummary.trackingOwnerName ||
        projectLike.projectOwnerName ||
        "",
      dailyCheckTime:
        overrides.dailyCheckTime ||
        currentSummary.dailyCheckTime ||
        crmSettings.defaultTrackingReminderTime ||
        "10:30",
      timezone: overrides.timezone || currentSummary.timezone || "Asia/Kolkata",
      openItemCount: computed.openItemCount,
      delayedItemCount: computed.delayedItemCount,
      receivedItemCount: computed.receivedItemCount,
      totalItemCount: computed.totalItemCount,
      lastCheckedAt: overrides.lastCheckedAt || currentSummary.lastCheckedAt || null,
      lastCheckedBy: overrides.lastCheckedBy || currentSummary.lastCheckedBy || "",
      nextCheckTaskId: overrides.nextCheckTaskId || currentSummary.nextCheckTaskId || "",
      nextCheckDueAt: overrides.nextCheckDueAt || currentSummary.nextCheckDueAt || null,
    });
    await updateCrmProjectPatch(projectId, { deliveryTracking: nextSummary });
    if (isDevRuntime) {
      devLog("CRM_TRACKING_SUMMARY_TRACE", {
        projectId,
        openItemCount: nextSummary.openItemCount || 0,
        receivedItemCount: nextSummary.receivedItemCount || 0,
        delayedItemCount: nextSummary.delayedItemCount || 0,
        totalItemCount: nextSummary.totalItemCount || 0,
        trackingComplete: nextSummary.trackingComplete === true,
      });
    }
    return nextSummary;
  },
  [
    buildProjectTrackingSummary,
    crmSettings.defaultTrackingReminderTime,
    sanitizeCrmFirestoreValue,
    updateCrmProjectPatch,
    workspaceUid,
  ]
);

const openCrmMaterialModal = useCallback((projectLike = {}, materialLike = null) => {
  if (!canUpdateCrmProjectExecution(projectLike)) {
    notify("You do not have permission to do this.", "warning");
    return;
  }
  const projectId = String(projectLike?.projectId || projectLike?.id || materialLike?.projectId || "").trim();
  if (!projectId) return;
  setCrmMaterialModalState({
    open: true,
    projectId,
    materialId: materialLike?.materialId || materialLike?.id || "",
    draft: createCrmMaterialDraft(projectLike, materialLike),
    loading: false,
    error: "",
  });
}, [canUpdateCrmProjectExecution, createCrmMaterialDraft, notify]);

const closeCrmMaterialModal = useCallback(() => {
  setCrmMaterialModalState({
    open: false,
    projectId: "",
    materialId: "",
    draft: null,
    loading: false,
    error: "",
  });
}, []);

const updateCrmMaterialDraft = useCallback((patch = {}) => {
  setCrmMaterialModalState((prev) => ({
    ...prev,
    draft: {
      ...(prev.draft || {}),
      ...patch,
    },
  }));
}, []);

const buildCrmMaterialPayload = useCallback(
  (projectLike = {}, draft = {}, options = {}) => {
    const actor = getCrmActorSnapshot();
    const materialId =
      String(draft.trackingItemId || draft.materialId || options.materialId || "").trim() ||
      createClientId("tracking-item");
    const parseOptionalNumber = (value) => {
      const text = String(value ?? "").trim();
      if (!text) return null;
      const number = Number(text);
      return Number.isFinite(number) ? Math.max(0, Number(number.toFixed(2))) : null;
    };
    const parseOptionalDateForFirestore = (value) => {
      const text = String(value || "").trim();
      if (!text) return null;
      const date = parseDateInputValue(text);
      return date ? Timestamp.fromDate(date) : null;
    };
    const itemType = normalizeTrackingItemType(draft.itemType || draft.materialType);
    const itemName =
      String(draft.itemName || "").trim() || getTrackingItemTypeLabel(itemType);
    const quantity = parseOptionalNumber(draft.quantity);
    const receivedQuantity = parseOptionalNumber(draft.receivedQuantity);
    const orderedQuantity =
      parseOptionalNumber(draft.orderedQuantity) ?? quantity ?? null;
    const nowTimestamp = Timestamp.fromDate(new Date());
    const isEdit = options.isEdit === true;
    return sanitizeCrmFirestoreValue({
      trackingItemId: materialId,
      materialId,
      workspaceUid,
      projectId: String(projectLike.projectId || projectLike.id || draft.projectId || "").trim(),
      projectTitle: projectLike.projectTitle || buildProjectTitle(projectLike),
      quoteId: projectLike.quoteId || "",
      quoteNo: projectLike.quoteNo || "",
      customerId: projectLike.customerId || "",
      customerName: projectLike.customerName || "",
      siteName: projectLike.siteName || "",
      itemType,
      materialType: itemType,
      itemName,
      brandName: String(draft.brandName || "").trim(),
      fabricCode: String(draft.fabricCode || "").trim(),
      catalogueName: String(draft.catalogueName || "").trim(),
      srNumber: String(draft.srNumber || "").trim(),
      supplierName: String(draft.supplierName || "").trim(),
      supplierPhone: String(draft.supplierPhone || "").trim(),
      quantity,
      unit: CRM_TRACKING_UNITS.includes(String(draft.unit || "").trim())
        ? String(draft.unit || "").trim()
        : "meter",
      orderedQuantity,
      receivedQuantity,
      orderDate: parseOptionalDateForFirestore(draft.orderDate),
      expectedArrivalDate: parseOptionalDateForFirestore(draft.expectedArrivalDate),
      receivedDate: parseOptionalDateForFirestore(draft.receivedDate),
      courierName: String(draft.courierName || "").trim(),
      transportName: String(draft.transportName || "").trim(),
      trackingNumber: String(draft.trackingNumber || "").trim(),
      trackingUrl: String(draft.trackingUrl || "").trim(),
      lrNumber: String(draft.lrNumber || "").trim(),
      status: normalizeTrackingStatus(draft.status),
      priority: draft.priority === "urgent" ? "urgent" : "normal",
      lastCheckedAt: draft.lastCheckedAt || null,
      lastCheckedBy: String(draft.lastCheckedBy || "").trim(),
      lastUpdateNote: String(draft.lastUpdateNote || "").trim(),
      notes: String(draft.notes || "").trim(),
      createdAt: isEdit ? draft.createdAt || nowTimestamp : nowTimestamp,
      createdBy: isEdit ? undefined : actor.uid || "",
      updatedAt: nowTimestamp,
      updatedBy: actor.uid || "",
    });
  },
  [getCrmActorSnapshot, parseDateInputValue, workspaceUid]
);

const saveCrmMaterial = useCallback(async () => {
  const draft = crmMaterialModalState.draft || {};
  const projectId = String(crmMaterialModalState.projectId || draft.projectId || "").trim();
  const selectedProject =
    crmProjectRecords.find(
      (project) => String(project.projectId || project.id || "") === projectId
    ) || null;
  if (!workspaceUid || !selectedProject || !projectId || crmMaterialModalState.loading) {
    return null;
  }
  if (!canUpdateCrmProjectExecution(selectedProject)) {
    setCrmMaterialModalState((prev) => ({
      ...prev,
      loading: false,
      error: "You do not have permission to do this.",
    }));
    notify("You do not have permission to do this.", "warning");
    return null;
  }

  setCrmMaterialModalState((prev) => ({ ...prev, loading: true, error: "" }));
  const isEdit = Boolean(crmMaterialModalState.materialId);
  const materialId =
    String(crmMaterialModalState.materialId || draft.trackingItemId || draft.materialId || "").trim() ||
    createClientId("tracking-item");
  try {
    const payload = buildCrmMaterialPayload(selectedProject, draft, {
      materialId,
      isEdit,
    });
    await setDoc(
      doc(db, "users", workspaceUid, "project_tracking_items", materialId),
      payload,
      { merge: true }
    );
    const patchedItem = {
      ...payload,
      trackingItemId: materialId,
      materialId,
      id: materialId,
      updatedAt: new Date(),
    };
    patchCrmMaterialDrawerState(projectId, materialId, patchedItem);
    const currentItems =
      crmProjectDrawerData.projectId === projectId
        ? crmProjectDrawerData.trackingItems || []
        : [];
    const summaryItems = [
      normalizeCrmMaterialRecord(materialId, patchedItem),
      ...currentItems.filter(
        (item) =>
          String(item.trackingItemId || item.materialId || item.id) !== String(materialId)
      ),
    ];
    await patchProjectDeliveryTrackingSummary(selectedProject, summaryItems);
    const activity = await logCrmActivity({
      relatedType: "project",
      relatedId: projectId,
      projectId,
      trackingItemId: materialId,
      materialId,
      customerId: selectedProject.customerId || "",
      customerName: selectedProject.customerName || "",
      opportunityId: selectedProject.opportunityId || "",
      quoteId: selectedProject.quoteId || "",
      quoteNo: selectedProject.quoteNo || "",
      activityType: isEdit ? "tracking_item_updated" : "tracking_item_added",
      title: isEdit ? "Tracking item updated" : "Tracking item added",
      description: `${buildTrackingItemDisplayTitle(payload)} ${
        isEdit ? "updated" : "added for tracking"
      }.`,
    }).catch((error) => {
      if (isDevRuntime) {
        devWarn("CRM_TRACKING_ACTIVITY_FAILED_EXACT", {
          trackingItemId: materialId,
          projectId,
          code: error?.code,
          message: error?.message,
        });
      }
      return null;
    });
    if (activity) prependProjectActivityLocal(projectId, activity);
    if (isDevRuntime) {
      devLog("CRM_TRACKING_ITEM_SAVE_TRACE", {
        trackingItemId: materialId,
        projectId,
        status: payload.status,
        action: isEdit ? "update" : "create",
      });
    }
    closeCrmMaterialModal();
    notify(isEdit ? "Tracking item updated." : "Tracking item added.", "success");
    return payload;
  } catch (error) {
    devError("CRM_TRACKING_ITEM_SAVE_FAILED_EXACT", {
      code: error?.code || "",
      message: error?.message || String(error),
      projectId,
      trackingItemId: materialId,
    });
    setCrmMaterialModalState((prev) => ({
      ...prev,
      loading: false,
      error: "Tracking item could not be saved. Please try again.",
    }));
    notify("Tracking item could not be saved. Please try again.", "error");
    return null;
  }
}, [
  buildCrmMaterialPayload,
  canUpdateCrmProjectExecution,
  closeCrmMaterialModal,
  crmProjectDrawerData.projectId,
  crmProjectDrawerData.trackingItems,
  crmMaterialModalState,
  crmProjectRecords,
  logCrmActivity,
  normalizeCrmMaterialRecord,
  notify,
  patchCrmMaterialDrawerState,
  patchProjectDeliveryTrackingSummary,
  prependProjectActivityLocal,
  workspaceUid,
]);

const updateCrmMaterialStatus = useCallback(
  async (materialLike = {}, nextStatusInput = "", options = {}) => {
    const materialId = String(materialLike.trackingItemId || materialLike.materialId || materialLike.id || "").trim();
    const projectId = String(materialLike.projectId || "").trim();
    if (!workspaceUid || !materialId || !projectId || crmMaterialActionKey) return null;
    const selectedProject =
      crmProjectRecords.find(
        (project) => String(project.projectId || project.id || "") === projectId
      ) || {};
    if (!canUpdateCrmProjectExecution(selectedProject)) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }
    const nextStatus = normalizeTrackingStatus(nextStatusInput);
    const previousStatus = normalizeTrackingStatus(materialLike.status);
    const actor = getCrmActorSnapshot();
    const todayKey = todayISO();
    const nowTimestamp = Timestamp.fromDate(new Date());
    const patch = {
      status: nextStatus,
      lastCheckedAt: nowTimestamp,
      lastCheckedBy: actor.uid || "",
      updatedAt: nowTimestamp,
      updatedBy: actor.uid || "",
    };
    if (nextStatus === "ordered" && !materialLike.orderDate) {
      patch.orderDate = Timestamp.fromDate(parseDateInputValue(todayKey) || new Date());
    }
    if (nextStatus === "received") {
      patch.receivedDate = Timestamp.fromDate(parseDateInputValue(todayKey) || new Date());
      patch.receivedQuantity =
        materialLike.quantity == null ? materialLike.receivedQuantity ?? null : materialLike.quantity;
    }
    if (nextStatus === "partially_received") {
      const currentValue =
        options.receivedQuantity != null
          ? options.receivedQuantity
          : typeof window !== "undefined"
          ? window.prompt("Received quantity", materialLike.receivedQuantity ?? "")
          : "";
      if (currentValue === null) return null;
      const receivedNumber = Number(currentValue);
      if (!Number.isFinite(receivedNumber) || receivedNumber < 0) {
        notify("Enter a valid received quantity.", "warning");
        return null;
      }
      patch.receivedQuantity = Number(receivedNumber.toFixed(2));
    }

    const actionKey = `${materialId}:${nextStatus}`;
    setCrmMaterialActionKey(actionKey);
    try {
      await updateDoc(
        doc(db, "users", workspaceUid, "project_tracking_items", materialId),
        sanitizeCrmFirestoreValue(patch)
      );
      const patchedMaterial = {
        ...(materialLike.raw || materialLike),
        ...patch,
        trackingItemId: materialId,
        materialId,
        projectId,
      };
      patchCrmMaterialDrawerState(projectId, materialId, patchedMaterial);
      const currentItems =
        crmProjectDrawerData.projectId === projectId
          ? crmProjectDrawerData.trackingItems || []
          : [];
      const summaryItems = [
        normalizeCrmMaterialRecord(materialId, patchedMaterial),
        ...currentItems.filter(
          (item) =>
            String(item.trackingItemId || item.materialId || item.id) !== String(materialId)
        ),
      ];
      await patchProjectDeliveryTrackingSummary(selectedProject, summaryItems, {
        lastCheckedAt: nowTimestamp,
        lastCheckedBy: actor.uid || "",
      });
      const statusLabel = getTrackingStatusLabel(nextStatus);
      const activityType =
        nextStatus === "ordered"
          ? "tracking_item_ordered"
          : nextStatus === "in_transit"
          ? "tracking_item_in_transit"
          : nextStatus === "reached_local_hub"
          ? "tracking_item_local_hub"
          : nextStatus === "partially_received"
          ? "tracking_item_partially_received"
          : nextStatus === "received"
          ? "tracking_item_received"
          : nextStatus === "delayed"
          ? "tracking_item_delayed"
          : nextStatus === "cancelled"
          ? "tracking_item_cancelled"
          : "tracking_item_updated";
      const activity = await logCrmActivity({
        relatedType: "project",
        relatedId: projectId,
        projectId,
        trackingItemId: materialId,
        materialId,
        customerId: materialLike.customerId || "",
        customerName: materialLike.customerName || "",
        quoteId: materialLike.quoteId || "",
        quoteNo: materialLike.quoteNo || "",
        activityType,
        title: "Tracking updated",
        description: `${buildTrackingItemDisplayTitle(materialLike)} marked ${statusLabel.toLowerCase()}.`,
      }).catch((error) => {
        if (isDevRuntime) {
          devWarn("CRM_TRACKING_ACTIVITY_FAILED_EXACT", {
            trackingItemId: materialId,
            projectId,
            code: error?.code,
            message: error?.message,
          });
        }
        return null;
      });
      if (activity) prependProjectActivityLocal(projectId, activity);
      if (isDevRuntime) {
        devLog("CRM_TRACKING_STATUS_TRACE", {
          trackingItemId: materialId,
          previousStatus,
          nextStatus,
          projectId,
        });
      }
      notify(`Tracking item marked ${statusLabel.toLowerCase()}.`, "success");
      return patchedMaterial;
    } catch (error) {
      devError("CRM_TRACKING_STATUS_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        trackingItemId: materialId,
        projectId,
        nextStatus,
      });
      notify("Tracking status could not be updated. Please try again.", "error");
      return null;
    } finally {
      setCrmMaterialActionKey("");
    }
  },
  [
    canUpdateCrmProjectExecution,
    crmMaterialActionKey,
    crmProjectDrawerData.projectId,
    crmProjectDrawerData.trackingItems,
    crmProjectRecords,
    getCrmActorSnapshot,
    logCrmActivity,
    normalizeCrmMaterialRecord,
    notify,
    parseDateInputValue,
    patchCrmMaterialDrawerState,
    patchProjectDeliveryTrackingSummary,
    prependProjectActivityLocal,
    workspaceUid,
  ]
);

const createCrmMaterialFollowUpTask = useCallback(
  async (materialLike = {}) => {
    const materialId = String(materialLike.trackingItemId || materialLike.materialId || materialLike.id || "").trim();
    const projectId = String(materialLike.projectId || "").trim();
    if (!workspaceUid || !materialId || !projectId) return null;
    const selectedProject =
      crmProjectRecords.find(
        (project) => String(project.projectId || project.id || "") === projectId
      ) || materialLike;
    if (!canUpdateCrmProjectExecution(selectedProject)) {
      notify("You do not have permission to do this.", "warning");
      return null;
    }
    const tomorrow = parseDateInputValue(addDaysToDateKey(todayISO(), 1)) || new Date();
    try {
      const task = await createCrmTask({
        taskId: `project_tracking_${sanitizeTaskHistorySegment(materialId)}__follow_up`,
        relatedType: "project_tracking_item",
        relatedId: materialId,
        projectId,
        customerId: materialLike.customerId || "",
        customerName: materialLike.customerName || "",
        quoteId: materialLike.quoteId || "",
        quoteNo: materialLike.quoteNo || "",
        taskType: "follow_up",
        title: `Follow up material: ${buildTrackingItemDisplayTitle(materialLike)}`,
        dueAt: Timestamp.fromDate(tomorrow),
        priority: materialLike.priority === "urgent" ? "urgent" : "normal",
        sourceKind: "project_tracking_manual_follow_up",
      });
      notify("Tracking follow-up task created.", "success");
      return task;
    } catch (error) {
      devError("CRM_TRACKING_TASK_FAILED_EXACT", {
        code: error?.code || "",
        message: error?.message || String(error),
        trackingItemId: materialId,
        projectId,
      });
      notify("Could not create tracking follow-up task.", "error");
      return null;
    }
  },
  [
    addDaysToDateKey,
    canUpdateCrmProjectExecution,
    createCrmTask,
    crmProjectRecords,
    notify,
    parseDateInputValue,
    todayISO,
    workspaceUid,
  ]
);

const loadCrmSettings = useCallback(async (uid) => {
  if (!uid || !canAccessCrm) return;
  const path = `users/${uid}/settings/crm`;
  setCrmSettingsLoadState({ loading: true, loadedWorkspace: "", error: "" });
  try {
    const snap = await loadOperationalSettingsSnapshot(functions, auth.currentUser?.uid, uid, 'crm');
    const normalized = normalizeCrmSettings(snap.exists() ? snap.data() || {} : {});
    setCrmSettings(normalized);
    setCrmSettingsDraft(normalized);
    setCrmSettingsLoadState({ loading: false, loadedWorkspace: uid, error: "" });
    if (isDevRuntime) {
      devLog("CRM_SETTINGS_TRACE", {
        loaded: true,
        saved: false,
        defaulted: !snap.exists(),
        workspaceUid: uid,
      });
      devLog("CRM_ORDER_TASK_SETTINGS_TRACE", {
        loaded: true,
        enableOrderTaskTemplates: normalized.enableOrderTaskTemplates,
        autoCreateOrderTasks: normalized.autoCreateOrderTasks,
        defaultOrderTaskTemplate: normalized.defaultOrderTaskTemplate,
        enableDeliveryTracking: normalized.enableDeliveryTracking,
        enableDailyTrackingReminder: normalized.enableDailyTrackingReminder,
        defaultTrackingReminderTime: normalized.defaultTrackingReminderTime,
      });
    }
  } catch (error) {
    devError("CRM settings load failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path,
    });
    setCrmSettingsLoadState({
      loading: false,
      loadedWorkspace: "",
      error: "Could not load CRM settings.",
    });
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [canAccessCrm, notify]);

const loadSalesPipelineSettings = useCallback(async (uid) => {
  if (!uid || !canAccessCrm) return;
  const path = `users/${uid}/pipelines/sales`;
  setSalesPipelineLoadState({ loading: true, loadedWorkspace: "", error: "" });
  try {
    const pipelineRef = doc(db, "users", uid, "pipelines", "sales");
    const snap = await getDocCached(pipelineRef, {
      label: "loadSalesPipelineSettings",
      cacheKey: path,
      ttlMs: 5 * 60 * 1000,
    });
    const pipelineExists = snap.exists();
    const normalized = normalizeSalesPipeline(pipelineExists ? snap.data() || {} : DEFAULT_SALES_PIPELINE);
    setSalesPipeline(normalized);
    setSalesPipelineLoadState({ loading: false, loadedWorkspace: uid, error: "" });

    // Missing defaults are rendered in memory and persisted only by Settings Save.
    const defaultCreated = false;

    if (isDevRuntime) {
      devLog("PIPELINE_SETTINGS_TRACE", {
        workspaceUid: uid,
        pipelineExists,
        defaultCreated,
        stageCount: normalized.stages.length,
        activeStageCount: normalized.stages.filter((stage) => stage.active !== false).length,
      });
    }
  } catch (error) {
    devError("Sales pipeline settings load failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path,
    });
    setSalesPipeline(normalizeSalesPipeline(DEFAULT_SALES_PIPELINE));
    setSalesPipelineLoadState({
      loading: false,
      loadedWorkspace: "",
      error: "Could not load sales pipeline settings.",
    });
  }
}, [canAccessCrm]);

const loadOrderPipelineSettings = useCallback(async (uid) => {
  if (!uid || !canAccessCrm) return;
  const path = `users/${uid}/pipelines/orders`;
  setOrderPipelineLoadState({ loading: true, loadedWorkspace: "", error: "" });
  try {
    const pipelineRef = doc(db, "users", uid, "pipelines", "orders");
    const snap = await getDocCached(pipelineRef, {
      label: "loadOrderPipelineSettings",
      cacheKey: path,
      ttlMs: 5 * 60 * 1000,
    });
    const pipelineExists = snap.exists();
    const normalized = normalizeOrderPipeline(
      pipelineExists ? snap.data() || {} : DEFAULT_ORDER_PIPELINE
    );
    setOrderPipeline(normalized);
    setOrderPipelineLoadState({ loading: false, loadedWorkspace: uid, error: "" });

    // Missing defaults are rendered in memory and persisted only by Settings Save.
    const defaultCreated = false;

    if (isDevRuntime) {
      devLog("ORDER_PIPELINE_SETTINGS_TRACE", {
        workspaceUid: uid,
        pipelineExists,
        defaultCreated,
        stageCount: normalized.stages.length,
        activeStageCount: normalized.stages.filter((stage) => stage.active !== false).length,
      });
    }
  } catch (error) {
    devError("Orders pipeline settings load failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path,
    });
    setOrderPipeline(normalizeOrderPipeline(DEFAULT_ORDER_PIPELINE));
    setOrderPipelineLoadState({
      loading: false,
      loadedWorkspace: "",
      error: "Could not load orders pipeline settings.",
    });
  }
}, [canAccessCrm]);

const saveCrmSettings = useCallback(async () => {
  if (!workspaceUid || !canManageCrmSettings) {
    notify("You do not have permission to edit CRM settings.", "error");
    return;
  }
  const normalized = normalizeCrmSettings(crmSettingsDraft);
  setIsSavingCrmSettings(true);
  try {
    await setDoc(
      doc(db, "users", workspaceUid, "settings", "crm"),
      sanitizeCrmFirestoreValue({
        ...normalized,
        workspaceUid,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || auth.currentUser?.uid || "",
      }),
      { merge: true }
    );
    invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/settings/crm`);
    clearOperationalSettingsCache();
    setCrmSettings(normalized);
    setCrmSettingsDraft(normalized);
    notify("CRM settings saved.", "success");
    if (isDevRuntime) {
      devLog("CRM_SETTINGS_TRACE", {
        loaded: false,
        saved: true,
        defaulted: false,
        workspaceUid,
      });
      devLog("CRM_ORDER_TASK_SETTINGS_TRACE", {
        loaded: true,
        enableOrderTaskTemplates: normalized.enableOrderTaskTemplates,
        autoCreateOrderTasks: normalized.autoCreateOrderTasks,
        defaultOrderTaskTemplate: normalized.defaultOrderTaskTemplate,
        enableDeliveryTracking: normalized.enableDeliveryTracking,
        enableDailyTrackingReminder: normalized.enableDailyTrackingReminder,
        defaultTrackingReminderTime: normalized.defaultTrackingReminderTime,
      });
    }
  } catch (error) {
    devError("CRM settings save failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path: `users/${workspaceUid}/settings/crm`,
    });
    notify(
      error?.code === "permission-denied"
        ? "You do not have permission to edit this setting."
        : "Could not save CRM settings.",
      "error"
    );
  } finally {
    setIsSavingCrmSettings(false);
  }
}, [canManageCrmSettings, crmSettingsDraft, notify, user?.uid, workspaceUid]);

const getCrmPipelineReviewOwnerOptions = useCallback(() => {
  const options = new Map();
  const addOption = (uidValue, nameValue) => {
    const uidKey = String(uidValue || "unassigned").trim() || "unassigned";
    const name = String(nameValue || "").trim() || (uidKey === "unassigned" ? "Unassigned" : "Team member");
    if (!options.has(uidKey)) {
      options.set(uidKey, { uid: uidKey, name });
    }
  };

  addOption("unassigned", "Unassigned");
  addOption(user?.uid || auth.currentUser?.uid || "", currentWorkspaceMember?.name || accountUser?.displayName || user?.displayName || user?.email || "Current user");
  teamMembers.forEach((member) => {
    if (member?.isActive === false || member?.status === "disabled") return;
    addOption(member.uid || member.id || member.email, member.name || member.email);
  });
  crmLeadRecords.forEach((record) => {
    addOption(
      record.ownerUid || record.assignedToUserId || record.assignedToUid,
      record.ownerName || record.assignedToName || record.assignedTo || record.salespersonName
    );
  });
  crmTaskRecords.forEach((record) => addOption(record.ownerUid, record.ownerName));
  crmPipelineReviewState.salespersonRows.forEach((row) => addOption(row.userId, row.userName));

  return Array.from(options.values()).sort((left, right) => {
    if (left.uid === "unassigned") return 1;
    if (right.uid === "unassigned") return -1;
    return String(left.name || "").localeCompare(String(right.name || ""));
  });
}, [
  accountUser?.displayName,
  crmLeadRecords,
  crmPipelineReviewState.salespersonRows,
  crmTaskRecords,
  currentWorkspaceMember?.name,
  teamMembers,
  user?.displayName,
  user?.email,
  user?.uid,
]);

const loadCrmPipelineReview = useCallback(async (uid, filtersOverride = crmPipelineFilters) => {
  if (!uid || !canAccessCrm || !canViewPipelineReview) return;

  const filters = {
    ...CRM_PIPELINE_REVIEW_DEFAULT_FILTERS,
    ...(filtersOverride || {}),
  };
  const presetRange =
    filters.dateRangePreset === "custom"
      ? null
      : getCrmPipelineReviewRange(filters.dateRangePreset);
  const startDateKey =
    filters.dateRangePreset === "custom"
      ? filters.startDate || getCrmPipelineReviewRange("this_week").startDate
      : presetRange.startDate;
  const endDateKey =
    filters.dateRangePreset === "custom"
      ? filters.endDate || startDateKey
      : presetRange.endDate;
  const startDate = parseLocalDateKey(startDateKey) || parseLocalDateKey(getCrmPipelineReviewRange("this_week").startDate);
  const endDate = parseLocalDateKey(endDateKey) || startDate;
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  const weekStart = addDaysToDateKey(getLocalDateKeyFromDate(getStartOfWeekDate(startDate)), 0);
  const dateRange = {
    preset: filters.dateRangePreset,
    startDate: getLocalDateKeyFromDate(startDate),
    endDate: getLocalDateKeyFromDate(endDate),
    weekStart,
    weekEnd: addDaysToDateKey(weekStart, 6),
  };

  setCrmPipelineReviewState((prev) => ({
    ...prev,
    loading: true,
    error: "",
    errorClassification: "",
    errorQueryName: "",
    dateRange,
  }));

  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);
  const opportunitiesRef = collection(db, "users", uid, "opportunities");
  const tasksRef = collection(db, "users", uid, "crm_tasks");
  const quotesRef = collection(db, "users", uid, "quote_index");
  const targetsRef = collection(db, "users", uid, "weekly_targets");
  const projectsRef = collection(db, "users", uid, "projects");
  const cacheSuffix = `${dateRange.startDate}_${dateRange.endDate}`;
  const runPipelineReviewQuery = (queryName, promise) =>
    promise.catch((error) => {
      error.crmPipelineReviewQueryName = queryName;
      throw error;
    });

  try {
    const [
      opportunitiesCreatedSnap,
      opportunitiesUpdatedSnap,
      opportunitiesOpenSnap,
      tasksDueSnap,
      tasksCompletedSnap,
      quotesSentSnap,
      projectsCreatedSnap,
      projectsActiveSnap,
      targetsSnap,
    ] = await Promise.all([
      runPipelineReviewQuery("opportunitiesCreated", getDocsCached(
        firestoreQuery(
          opportunitiesRef,
          firestoreWhere("createdAt", ">=", startTimestamp),
          firestoreWhere("createdAt", "<=", endTimestamp),
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.opportunitiesCreated",
          cacheKey: `opportunities:${uid}:created:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("opportunitiesUpdated", getDocsCached(
        firestoreQuery(
          opportunitiesRef,
          firestoreWhere("updatedAt", ">=", startTimestamp),
          firestoreWhere("updatedAt", "<=", endTimestamp),
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.opportunitiesUpdated",
          cacheKey: `opportunities:${uid}:updated:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("opportunitiesOpen", getDocsCached(
        firestoreQuery(
          opportunitiesRef,
          firestoreWhere("status", "==", CRM_OPPORTUNITY_STATUS_OPEN),
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.opportunitiesOpen",
          cacheKey: `opportunities:${uid}:statusOpen:updatedAtDesc:limit${CRM_PIPELINE_REVIEW_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("tasksDue", getDocsCached(
        firestoreQuery(
          tasksRef,
          firestoreWhere("dueAt", ">=", startTimestamp),
          firestoreWhere("dueAt", "<=", endTimestamp),
          firestoreOrderBy("dueAt", "asc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_TASK_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.tasksDue",
          cacheKey: `crmTasks:${uid}:due:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_TASK_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("tasksCompleted", getDocsCached(
        firestoreQuery(
          tasksRef,
          firestoreWhere("completedAt", ">=", startTimestamp),
          firestoreWhere("completedAt", "<=", endTimestamp),
          firestoreOrderBy("completedAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_TASK_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.tasksCompleted",
          cacheKey: `crmTasks:${uid}:completed:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_TASK_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("quoteIndexSent", getDocsCached(
        firestoreQuery(
          quotesRef,
          firestoreWhere("crmStatus", "==", "quote_sent"),
          firestoreWhere("quoteSentAt", ">=", startTimestamp),
          firestoreWhere("quoteSentAt", "<=", endTimestamp),
          firestoreOrderBy("quoteSentAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_QUOTE_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.quoteIndexSent",
          cacheKey: `quoteIndex:${uid}:quoteSent:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_QUOTE_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("projectsCreated", getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreWhere("createdAt", ">=", startTimestamp),
          firestoreWhere("createdAt", "<=", endTimestamp),
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.projectsCreated",
          cacheKey: `projects:${uid}:created:${cacheSuffix}:limit${CRM_PIPELINE_REVIEW_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("projectsActive", getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreWhere("status", "==", "open"),
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_PIPELINE_REVIEW_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.projectsActive",
          cacheKey: `projects:${uid}:statusOpen:updatedAtDesc:limit${CRM_PIPELINE_REVIEW_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
      runPipelineReviewQuery("weeklyTargets", getDocsCached(
        firestoreQuery(
          targetsRef,
          firestoreWhere("weekStart", "==", dateRange.weekStart),
          firestoreLimit(CRM_WEEKLY_TARGET_READ_LIMIT)
        ),
        {
          label: "crmPipelineReview.weeklyTargets",
          cacheKey: `weeklyTargets:${uid}:weekStart:${dateRange.weekStart}:limit${CRM_WEEKLY_TARGET_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      )),
    ]);

    const opportunitiesById = new Map();
    [...opportunitiesCreatedSnap.docs, ...opportunitiesUpdatedSnap.docs, ...opportunitiesOpenSnap.docs].forEach((docSnap) => {
      const raw = docSnap.data() || {};
      opportunitiesById.set(docSnap.id, normalizeCrmOpportunityRecord(docSnap.id, raw));
    });

    const tasksById = new Map();
    [...tasksDueSnap.docs, ...tasksCompletedSnap.docs].forEach((docSnap) => {
      tasksById.set(docSnap.id, normalizeCrmTaskRecord(docSnap.id, docSnap.data() || {}));
    });

    const quoteRecords = quotesSentSnap.docs
      .map((docSnap) => {
        const raw = docSnap.data() || {};
        const quoteSentMs = getCrmRecordDateMs(raw, ["quoteSentAt", "sentAt", "createdAt"]);
        return {
          id: docSnap.id,
          quoteId: raw.quoteId || raw.sourceQuoteId || docSnap.id,
          quoteNo: raw.quoteNo || docSnap.id,
          customerName: raw.customerName || "Unnamed Customer",
          ownerUid: getCrmPipelineOwnerKey(raw),
          ownerName: raw.ownerName || raw.createdByName || raw.salespersonName || "Unassigned",
          crmStatus: raw.crmStatus || "",
          source: raw.source || raw.leadSource || raw.sourceName || "",
          productInterest: raw.productInterest || raw.quoteType || raw.quotationType || raw.category || "",
          quoteSentAt: raw.quoteSentAt || raw.sentAt || raw.createdAt || null,
          quoteSentMs,
          totalAmount: Number(raw.totalAmount || raw.grandTotal || raw.finalTotal || raw.totalValue || 0) || 0,
          raw,
        };
      })
      .filter((quote) => isDateMsInRange(quote.quoteSentMs, startMs, endMs));
    const projectRecords = projectsCreatedSnap.docs
      .map((docSnap) => normalizeCrmProjectRecord(docSnap.id, docSnap.data() || {}))
      .filter((project) =>
        isDateMsInRange(getCrmRecordDateMs(project, ["createdAt"]), startMs, endMs)
      );
    const activeProjectRecords = projectsActiveSnap.docs
      .map((docSnap) => normalizeCrmProjectRecord(docSnap.id, docSnap.data() || {}))
      .filter((project) => project.stage !== PROJECT_STAGE_CANCELLED);

    const targets = targetsSnap.docs.map((docSnap) =>
      normalizeWeeklyTargetRecord({ id: docSnap.id, ...(docSnap.data() || {}) })
    );
    const targetsByUser = new Map(targets.map((target) => [String(target.userId || "unassigned"), target]));

    const salespersonFilter = String(filters.salesperson || "all");
    const stageFilter = String(filters.stage || "all");
    const sourceFilter = String(filters.source || "all").toLowerCase();
    const productFilter = String(filters.productInterest || "all").toLowerCase();

    const sourceMatches = (value) =>
      sourceFilter === "all" || String(value || "").toLowerCase().includes(sourceFilter);
    const productMatches = (value) =>
      productFilter === "all" || String(value || "").toLowerCase().includes(productFilter);
    const ownerMatches = (value) => salespersonFilter === "all" || String(value || "unassigned") === salespersonFilter;

    const opportunities = Array.from(opportunitiesById.values()).filter((record) => {
      if (isCrmRecordUnavailable(record)) return false;
      const raw = record.raw || {};
      const ownerKey = getCrmPipelineOwnerKey(record);
      const source = raw.source || raw.leadSource || raw.sourceName || "";
      const productInterest =
        raw.productInterest || raw.interestedProduct || raw.quoteType || raw.pipelineType || "";
      return (
        ownerMatches(ownerKey) &&
        (stageFilter === "all" || record.stage === stageFilter) &&
        sourceMatches(source) &&
        productMatches(productInterest)
      );
    });

    const tasks = Array.from(tasksById.values()).filter((task) =>
      !isCrmRecordUnavailable(task) && ownerMatches(task.ownerUid || "unassigned")
    );
    const quotes = quoteRecords.filter((quote) =>
      ownerMatches(quote.ownerUid) &&
      sourceMatches(quote.source) &&
      productMatches(quote.productInterest)
    );
    const projects = projectRecords.filter((project) =>
      ownerMatches(project.projectOwnerUid || project.salesOwnerUid || "unassigned")
    );
    const activeProjects = activeProjectRecords.filter((project) =>
      ownerMatches(project.projectOwnerUid || project.salesOwnerUid || "unassigned")
    );
    const confirmedOrderPaymentStatuses = new Set([
      PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED,
      PROJECT_PAYMENT_STATUS_PART_PAID,
      PROJECT_PAYMENT_STATUS_FULLY_PAID,
    ]);
    const confirmedOrderProjects = projects.filter(
      (project) =>
        project.stage !== PROJECT_STAGE_CANCELLED &&
        confirmedOrderPaymentStatuses.has(project.paymentStatus)
    );
    const advancePendingProjects = projects.filter(
      (project) =>
        project.stage !== PROJECT_STAGE_CANCELLED &&
        project.paymentStatus === PROJECT_PAYMENT_STATUS_ADVANCE_PENDING
    );
    const projectOpportunityIds = new Set(
      projects.map((project) => String(project.opportunityId || "").trim()).filter(Boolean)
    );

    const rows = new Map();
    const getRow = (userId, userName = "") => {
      const uidKey = String(userId || "unassigned").trim() || "unassigned";
      if (!rows.has(uidKey)) {
        const target = targetsByUser.get(uidKey) || normalizeWeeklyTargetRecord({
          targetId: getPipelineReviewTargetId(dateRange.weekStart, uidKey),
          weekStart: dateRange.weekStart,
          weekEnd: dateRange.weekEnd,
          userId: uidKey,
          userName: userName || (uidKey === "unassigned" ? "Unassigned" : "Team member"),
        });
        rows.set(uidKey, {
          userId: uidKey,
          userName: userName || target.userName || (uidKey === "unassigned" ? "Unassigned" : "Team member"),
          leads: 0,
          followUpsDone: 0,
          siteVisits: 0,
          quotesSent: 0,
          ordersWon: 0,
          orderValue: 0,
          advancePendingOrders: 0,
          advancePendingValue: 0,
          overdueTasks: 0,
          leadsWithoutNextAction: 0,
          target,
        });
      }
      return rows.get(uidKey);
    };

    const isStage = (record, keys = []) => keys.includes(record.stage);
    const isOpenOpportunity = (record) =>
      ![CRM_OPPORTUNITY_STATUS_WON, CRM_OPPORTUNITY_STATUS_LOST].includes(record.status);
    const hasNextAction = (record) =>
      Boolean(String(record.nextActionText || record.raw?.nextAction || "").trim() || record.nextActionDueAt);
    const opportunityValue = (record) =>
      Number(
        record.latestQuoteAmount ||
          record.raw?.orderValue ||
          record.raw?.confirmedValue ||
          record.raw?.estimatedValue ||
          record.raw?.totalValue ||
          0
      ) || 0;

    const summary = {
      openLeads: 0,
      openOpportunityValue: 0,
      newLeads: 0,
      contactedLeads: 0,
      qualifiedLeads: 0,
      siteVisitsScheduled: 0,
      measurementsCompleted: 0,
      quotationsSent: quotes.length,
      quoteValue: quotes.reduce((total, quote) => total + Number(quote.totalAmount || 0), 0),
      quotationSentValue: quotes.reduce((total, quote) => total + Number(quote.totalAmount || 0), 0),
      negotiationValue: 0,
      ordersWon: 0,
      orderValue: 0,
      wonValue: 0,
      advancePendingOrders: 0,
      advancePendingValue: 0,
      activeProjects: activeProjects.length,
      activeProjectValue: activeProjects.reduce(
        (total, project) => total + (Number(project.orderValue || project.paymentSummary?.orderValue || 0) || 0),
        0
      ),
      paymentPendingBalance: activeProjects.reduce(
        (total, project) => total + Math.max(0, Number(project.balanceAmount || project.paymentSummary?.balanceAmount || 0) || 0),
        0
      ),
      projectsReadyToClose: activeProjects.filter(
        (project) =>
          project.stage === PROJECT_STAGE_INSTALLATION_COMPLETED &&
          !project.hasOpenSnags &&
          !project.hasPendingBalance
      ).length,
      lostOpportunities: 0,
      overdueTasks: 0,
      leadsWithoutNextAction: 0,
    };

    opportunities.forEach((record) => {
      const raw = record.raw || {};
      const ownerKey = getCrmPipelineOwnerKey(record);
      const row = getRow(ownerKey, record.ownerName || raw.ownerName || raw.assignedToName || "Unassigned");
      const createdMs = getCrmRecordDateMs(record, ["createdAt"]) || getCrmRecordDateMs(raw, ["createdAt"]);
      const amount = opportunityValue(record);
      if (isOpenOpportunity(record)) {
        summary.openLeads += 1;
        summary.openOpportunityValue += amount;
      }
      if (isDateMsInRange(createdMs, startMs, endMs)) {
        summary.newLeads += 1;
        row.leads += 1;
      }
      if (isStage(record, ["contacted"])) summary.contactedLeads += 1;
      if (isStage(record, ["qualified", "site_visit_scheduled", "measurement_completed"])) {
        summary.qualifiedLeads += 1;
        row.targetQualifiedLeadsActual = (row.targetQualifiedLeadsActual || 0) + 1;
      }
      if (isStage(record, ["site_visit_scheduled"])) {
        summary.siteVisitsScheduled += 1;
        row.siteVisits += 1;
      }
      if (isStage(record, ["measurement_completed"])) summary.measurementsCompleted += 1;
      if (isStage(record, ["negotiation", "negotiation_follow_up"])) {
        summary.negotiationValue += amount;
      }
      if (
        !projectOpportunityIds.has(String(record.opportunityId || record.id || "").trim()) &&
        (record.status === CRM_OPPORTUNITY_STATUS_WON || isStage(record, ["won", "order_confirmed"]))
      ) {
        summary.ordersWon += 1;
        summary.orderValue += amount;
        summary.wonValue += amount;
        row.ordersWon += 1;
        row.orderValue += amount;
      }
      if (record.status === CRM_OPPORTUNITY_STATUS_LOST || isStage(record, ["lost"])) {
        summary.lostOpportunities += 1;
      }
      if (isOpenOpportunity(record) && !hasNextAction(record)) {
        summary.leadsWithoutNextAction += 1;
        row.leadsWithoutNextAction += 1;
      }
    });

    quotes.forEach((quote) => {
      const row = getRow(quote.ownerUid, quote.ownerName);
      row.quotesSent += 1;
    });

    const getProjectBalanceValue = (project = {}) => {
      const explicitBalance = Number(project.balanceAmount);
      if (Number.isFinite(explicitBalance)) {
        return Math.max(0, explicitBalance);
      }
      return calculateBalanceAmount(
        Number(project.orderValue || 0) || 0,
        Number(project.advanceAmount || 0) || 0
      );
    };

    // Payment status is the sales source of truth; project stage is operational.
    confirmedOrderProjects.forEach((project) => {
      const ownerKey = project.projectOwnerUid || project.salesOwnerUid || "unassigned";
      const row = getRow(ownerKey, project.projectOwnerName || project.salesOwnerName || "Unassigned");
      const amount = Number(project.orderValue || 0) || 0;
      summary.ordersWon += 1;
      summary.orderValue += amount;
      summary.wonValue += amount;
      row.ordersWon += 1;
      row.orderValue += amount;
    });

    advancePendingProjects.forEach((project) => {
      const ownerKey = project.projectOwnerUid || project.salesOwnerUid || "unassigned";
      const row = getRow(ownerKey, project.projectOwnerName || project.salesOwnerName || "Unassigned");
      const amount = getProjectBalanceValue(project);
      summary.advancePendingOrders += 1;
      summary.advancePendingValue += amount;
      row.advancePendingOrders += 1;
      row.advancePendingValue += amount;
    });

    const nowMs = Date.now();
    tasks.forEach((task) => {
      const row = getRow(task.ownerUid || "unassigned", task.ownerName || "Unassigned");
      if (task.status === CRM_TASK_STATUS_COMPLETED) {
        row.followUpsDone += 1;
      }
      if (task.status !== CRM_TASK_STATUS_COMPLETED && task.status !== CRM_TASK_STATUS_CANCELLED && task.dueMillis && task.dueMillis < nowMs) {
        summary.overdueTasks += 1;
        row.overdueTasks += 1;
      }
    });

    targets.forEach((target) => getRow(target.userId, target.userName));

    const salespersonRows = Array.from(rows.values()).sort((left, right) =>
      String(left.userName || "").localeCompare(String(right.userName || ""))
    );
    const managementWarnings = {
      missingOwnerCount: opportunities.filter((record) => getCrmPipelineOwnerKey(record) === "unassigned").length,
      missingNextActionCount: summary.leadsWithoutNextAction,
      overdueTaskCount: summary.overdueTasks,
      missingQuoteFollowUpCount: quotes.filter((quote) => !quote.raw?.followUpDueAt).length,
    };

    setCrmPipelineReviewState({
      loading: false,
      loaded: true,
      error: "",
      errorClassification: "",
      errorQueryName: "",
      dateRange,
      summary,
      salespersonRows,
      managementWarnings,
      targets,
      readCounts: {
        opportunitiesRead: opportunitiesCreatedSnap.size + opportunitiesUpdatedSnap.size + opportunitiesOpenSnap.size,
        tasksRead: tasksDueSnap.size + tasksCompletedSnap.size,
        quotesRead: quotesSentSnap.size,
        projectsRead: projectsCreatedSnap.size + projectsActiveSnap.size,
        targetsRead: targetsSnap.size,
      },
      loadedAt: new Date().toISOString(),
    });

    if (isDevRuntime) {
      devLog("CRM_PIPELINE_REVIEW_QUERY_TRACE", {
        workspaceUid: uid,
        dateRange,
        filters,
        opportunitiesRead: opportunitiesCreatedSnap.size + opportunitiesUpdatedSnap.size + opportunitiesOpenSnap.size,
        tasksRead: tasksDueSnap.size + tasksCompletedSnap.size,
        quotesRead: quotesSentSnap.size,
        projectsRead: projectsCreatedSnap.size + projectsActiveSnap.size,
        weeklyTargetsRead: targetsSnap.size,
        openOpportunityCount: opportunities.filter((record) => isOpenOpportunity(record)).length,
        activeProjectCount: activeProjects.length,
        confirmedOrderProjectCount: confirmedOrderProjects.length,
        advancePendingProjectCount: advancePendingProjects.length,
      });
      devLog("CRM_ATTENTION_REQUIRED_TRACE", managementWarnings);
    }
  } catch (error) {
    const classified = classifyCrmPipelineReviewError(error);
    devError("CRM pipeline review load failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      queryName: error?.crmPipelineReviewQueryName || "unknown",
      classifiedAs: classified.classifiedAs,
      workspaceUid: uid,
    });
    if (isDevRuntime) {
      devLog("CRM_PIPELINE_REVIEW_ERROR_TRACE", {
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        queryName: error?.crmPipelineReviewQueryName || "unknown",
        dateRange,
        filters,
        classifiedAs: classified.classifiedAs,
        indexLinkFound: classified.indexLinkFound,
      });
    }
    setCrmPipelineReviewState((prev) => ({
      ...prev,
      loading: false,
      loaded: false,
      error: classified.friendlyMessage,
      errorClassification: classified.classifiedAs,
      errorQueryName: error?.crmPipelineReviewQueryName || "unknown",
    }));
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [
  canAccessCrm,
  canViewPipelineReview,
  crmPipelineFilters,
  normalizeCrmProjectRecord,
  normalizeCrmOpportunityRecord,
  normalizeCrmTaskRecord,
  notify,
]);

const saveCrmWeeklyTarget = useCallback(async () => {
  if (!workspaceUid || !canEditCrmTargetsAccess) {
    notify("You do not have permission to edit weekly targets.", "error");
    return;
  }
  const weekStart = crmWeeklyTargetDraft.weekStart || crmPipelineReviewState.dateRange.weekStart;
  const weekEnd = crmWeeklyTargetDraft.weekEnd || addDaysToDateKey(weekStart, 6);
  const userId = String(crmWeeklyTargetDraft.userId || "").trim();
  if (!weekStart || !userId) {
    notify("Choose a week and salesperson before saving a target.", "warning");
    return;
  }
  const ownerOption =
    getCrmPipelineReviewOwnerOptions().find((option) => option.uid === userId) || {};
  const targetId = getPipelineReviewTargetId(weekStart, userId);
  const payload = sanitizeCrmFirestoreValue({
    targetId,
    workspaceUid,
    weekStart,
    weekEnd,
    userId,
    userName:
      crmWeeklyTargetDraft.userName || ownerOption.name || (userId === "unassigned" ? "Unassigned" : "Team member"),
    targetFreshLeads: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetFreshLeads),
    targetQualifiedLeads: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetQualifiedLeads),
    targetSiteVisits: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetSiteVisits),
    targetQuotations: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetQuotations),
    targetFollowUps: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetFollowUps),
    targetOrders: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetOrders),
    targetOrderValue: normalizePipelineTargetNumber(crmWeeklyTargetDraft.targetOrderValue),
    updatedAt: serverTimestamp(),
    updatedBy: user?.uid || auth.currentUser?.uid || "",
    createdAt: crmWeeklyTargetDraft.targetId ? undefined : serverTimestamp(),
    createdBy: crmWeeklyTargetDraft.targetId ? undefined : user?.uid || auth.currentUser?.uid || "",
  });

  setIsSavingCrmWeeklyTarget(true);
  try {
    await setDoc(doc(db, "users", workspaceUid, "weekly_targets", targetId), payload, {
      merge: true,
    });
    invalidateFirestoreReadCacheByPrefix(`weeklyTargets:${workspaceUid}:weekStart:${weekStart}`);
    const savedTarget = normalizeWeeklyTargetRecord({
      ...payload,
      targetId,
      workspaceUid,
      weekStart,
      weekEnd,
      userId,
      userName: payload.userName,
    });
    setCrmPipelineReviewState((prev) => {
      const nextTargets = [
        ...(prev.targets || []).filter((target) => target.targetId !== targetId),
        savedTarget,
      ];
      return {
        ...prev,
        targets: nextTargets,
        salespersonRows: (prev.salespersonRows || []).map((row) =>
          row.userId === userId ? { ...row, target: savedTarget } : row
        ),
      };
    });
    notify("Weekly target saved.", "success");
    if (isDevRuntime) {
      devLog("CRM_WEEKLY_TARGET_TRACE", {
        weekStart,
        userId,
        targetId,
        action: "saved",
      });
    }
  } catch (error) {
    devError("CRM weekly target save failed:", {
      code: error?.code || "",
      message: error?.message || String(error),
      path: `users/${workspaceUid}/weekly_targets/${targetId}`,
    });
    notify(
      error?.code === "permission-denied"
        ? "You do not have permission to edit this setting."
        : "Could not save weekly target.",
      "error"
    );
  } finally {
    setIsSavingCrmWeeklyTarget(false);
  }
}, [
  canEditCrmTargetsAccess,
  crmPipelineReviewState.dateRange.weekStart,
  crmWeeklyTargetDraft,
  getCrmPipelineReviewOwnerOptions,
  notify,
  user?.uid,
  workspaceUid,
]);

const loadCrmOpportunityDrawerSection = useCallback(
  async (section, opportunityIdOverride = "") => {
    const opportunityId = String(opportunityIdOverride || selectedCrmOpportunityId || "").trim();
    if (!workspaceUid || !canAccessCrm || !opportunityId) return;
    const validSections = new Set(["quotes", "tasks", "activity"]);
    if (!validSections.has(section)) return;

    setCrmOpportunityDrawerData((prev) => ({
      ...prev,
      opportunityId,
      loading: {
        ...(prev.loading || {}),
        [section]: true,
      },
      errors: {
        ...(prev.errors || {}),
        [section]: "",
      },
    }));

    try {
      if (section === "quotes") {
        const quoteSnap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "quote_index"),
            firestoreWhere("opportunityId", "==", opportunityId),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(10)
          ),
          {
            label: "loadOpportunityDrawerQuotes",
            cacheKey: `quoteIndex:${workspaceUid}:opportunity:${opportunityId}:updatedAtDesc:limit10`,
            ttlMs: 60 * 1000,
          }
        );
        const quotes = quoteSnap.docs.map((item) =>
          normalizeCrmDrawerQuoteRecord(item.id, item.data() || {})
        );
        setCrmOpportunityDrawerData((prev) => ({
          ...prev,
          opportunityId,
          quotes,
          loading: { ...(prev.loading || {}), quotes: false },
        }));
        return;
      }

      if (section === "tasks") {
        const taskSnap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_tasks"),
            firestoreWhere("opportunityId", "==", opportunityId),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(20)
          ),
          {
            label: "loadOpportunityDrawerTasks",
            cacheKey: `crmTasks:${workspaceUid}:opportunity:${opportunityId}:dueAtAsc:limit20`,
            ttlMs: 60 * 1000,
          }
        );
        const tasks = taskSnap.docs.map((item) =>
          normalizeCrmTaskRecord(item.id, item.data() || {})
        );
        setCrmOpportunityDrawerData((prev) => ({
          ...prev,
          opportunityId,
          tasks,
          loading: { ...(prev.loading || {}), tasks: false },
        }));
        return;
      }

      const activitySnap = await getDocsCached(
        firestoreQuery(
          collection(db, "users", workspaceUid, "activity_log"),
          firestoreWhere("opportunityId", "==", opportunityId),
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(20)
        ),
        {
          label: "loadOpportunityDrawerActivity",
          cacheKey: `activityLog:${workspaceUid}:opportunity:${opportunityId}:createdAtDesc:limit20`,
          ttlMs: 60 * 1000,
        }
      );
      let activity = activitySnap.docs.map((item) =>
        normalizeCrmActivityRecord(item.id, item.data() || {})
      );
      if (!activity.length) {
        const relatedActivitySnap = await getDocsCached(
          firestoreQuery(
            collection(db, "users", workspaceUid, "activity_log"),
            firestoreWhere("relatedId", "==", opportunityId),
            firestoreLimit(20)
          ),
          {
            label: "loadOpportunityDrawerActivityByRelatedId",
            cacheKey: `activityLog:${workspaceUid}:related:${opportunityId}:limit20`,
            ttlMs: 60 * 1000,
          }
        );
        activity = relatedActivitySnap.docs
          .map((item) => normalizeCrmActivityRecord(item.id, item.data() || {}))
          .sort((left, right) => safeTimeMillis(right.createdAt) - safeTimeMillis(left.createdAt));
      }
      setCrmOpportunityDrawerData((prev) => ({
        ...prev,
        opportunityId,
        activity,
        loading: { ...(prev.loading || {}), activity: false },
      }));
    } catch (error) {
      devError("CRM opportunity drawer section load failed:", {
        section,
        opportunityId,
        code: error?.code || "",
        message: error?.message || String(error),
      });
      setCrmOpportunityDrawerData((prev) => ({
        ...prev,
        opportunityId,
        loading: {
          ...(prev.loading || {}),
          [section]: false,
        },
        errors: {
          ...(prev.errors || {}),
          [section]:
            error?.code === "failed-precondition"
              ? "This CRM index is still building. Please try again shortly."
              : "Could not load this section.",
        },
      }));
    }
  },
  [
    canAccessCrm,
    normalizeCrmActivityRecord,
    normalizeCrmDrawerQuoteRecord,
    normalizeCrmTaskRecord,
    selectedCrmOpportunityId,
    workspaceUid,
  ]
);

useEffect(() => {
  setCrmDetailActiveTab("overview");
  setCrmOpportunityDrawerData({
    opportunityId: selectedCrmOpportunityId || "",
    quotes: [],
    tasks: [],
    activity: [],
    loading: { quotes: false, tasks: false, activity: false },
    errors: { quotes: "", tasks: "", activity: "" },
  });
}, [selectedCrmOpportunityId]);

useEffect(() => {
  if (
    tab !== "crm" ||
    !selectedCrmOpportunityId ||
    !["quotes", "tasks", "activity"].includes(crmDetailActiveTab)
  ) {
    return;
  }

  loadCrmOpportunityDrawerSection(crmDetailActiveTab, selectedCrmOpportunityId);
}, [
  crmDetailActiveTab,
  loadCrmOpportunityDrawerSection,
  selectedCrmOpportunityId,
  tab,
]);

const subscribeCrmTasks = useCallback((uid) => {
  if (!uid || !canAccessCrm || !user?.uid) {
    setCrmTaskRecords([]);
    return () => {};
  }

  const tasksRef = collection(db, "users", uid, "crm_tasks");
  const tasksQuery = firestoreQuery(
    tasksRef,
    firestoreWhere("ownerUid", "==", user.uid),
    firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
    firestoreOrderBy("dueAt", "asc"),
    firestoreLimit(50)
  );
  let cancelled = false;

  getDocsCached(tasksQuery, {
    label: "loadCrmTasksForToday",
    cacheKey: `crmTasks:${uid}:owner:${user.uid}:openDueAtAsc:limit50`,
    ttlMs: 60 * 1000,
  })
    .then((snap) => {
      if (cancelled) return;
      setCrmTaskRecords(
        snap.docs.map((taskDoc) =>
          normalizeCrmTaskRecord(taskDoc.id, taskDoc.data() || {})
        ).filter((task) => !isCrmRecordUnavailable(task))
      );
    })
    .catch((error) => {
      if (cancelled) return;
      devError("CRM tasks load failed:", {
        code: error?.code,
        message: error?.message,
        path: `users/${uid}/crm_tasks`,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      setCrmTaskRecords([]);
    });

  return () => {
    cancelled = true;
  };
}, [canAccessCrm, normalizeCrmTaskRecord, notify, user?.uid]);

const loadCrmAttentionRequired = useCallback(async (uid) => {
  if (!uid || !canAccessCrm) {
    setCrmAttentionState({
      loading: false,
      loaded: false,
      error: "",
      missingOwner: [],
      missingNextAction: [],
      overdueTasks: [],
      missingQuoteFollowUp: [],
      handoverPendingProjects: [],
      advancePendingProjects: [],
      projectsMissingNextAction: [],
      projectsMissingInstallationDate: [],
      overdueTrackingTasks: [],
      trackingItemsNotOrdered: [],
      trackingItemsDelayed: [],
      trackingArrivalsOverdue: [],
      balancePendingProjects: [],
      overduePaymentTasks: [],
      paymentDueTodayTasks: [],
      installationNotScheduledProjects: [],
      installationsDueToday: [],
      overdueInstallations: [],
      productionOverdueProjects: [],
      materialDueOverdueProjects: [],
      openSnagProjects: [],
      overdueSnagProjects: [],
      projectsReadyToClose: [],
      projectsBlockedByFinalPayment: [],
      reviewDueTodayTasks: [],
      overdueReviewTasks: [],
    });
    return;
  }

  setCrmAttentionState((prev) => ({
    ...prev,
    loading: true,
    error: "",
  }));

  try {
    const opportunitiesRef = collection(db, "users", uid, "opportunities");
    const tasksRef = collection(db, "users", uid, "crm_tasks");
    const quoteIndexRef = collection(db, "users", uid, "quote_index");
    const projectsRef = collection(db, "users", uid, "projects");
    const nowTimestamp = Timestamp.fromDate(new Date());
    const [
      missingOwnerSnap,
      missingNextActionSnap,
      overdueTaskSnap,
      quoteFollowUpMissingSnap,
      handoverPendingProjectsSnap,
      advancePendingProjectsSnap,
      projectsMissingNextActionSnap,
      projectsMissingInstallationDateSnap,
    ] =
      await Promise.all([
        getDocsCached(
          firestoreQuery(
            opportunitiesRef,
            firestoreWhere("status", "==", CRM_OPPORTUNITY_STATUS_OPEN),
            firestoreWhere("ownerUid", "==", ""),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionMissingOwner",
            cacheKey: `crmOpportunities:${uid}:missingOwner:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            opportunitiesRef,
            firestoreWhere("status", "==", CRM_OPPORTUNITY_STATUS_OPEN),
            firestoreWhere("nextActionMissing", "==", true),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionMissingNextAction",
            cacheKey: `crmOpportunities:${uid}:missingNextAction:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", "<=", nowTimestamp),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(50)
          ),
          {
            label: "loadCrmAttentionOverdueTasks",
            cacheKey: `crmTasks:${uid}:overdue:limit50:${toDateInputValue(new Date())}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("crmStatus", "==", "quote_sent"),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionQuoteFollowUpMissing",
            cacheKey: `quoteIndex:${uid}:crmStatusQuoteSent:updatedAtDesc:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("stage", "==", PROJECT_STAGE_HANDOVER_PENDING),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionHandoverPendingProjects",
            cacheKey: `projects:${uid}:stage:${PROJECT_STAGE_HANDOVER_PENDING}:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("paymentStatus", "==", PROJECT_PAYMENT_STATUS_ADVANCE_PENDING),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionAdvancePendingProjects",
            cacheKey: `projects:${uid}:payment:${PROJECT_PAYMENT_STATUS_ADVANCE_PENDING}:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("nextActionMissing", "==", true),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionProjectsMissingNextAction",
            cacheKey: `projects:${uid}:nextActionMissing:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("expectedInstallationDateMissing", "==", true),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(20)
          ),
          {
            label: "loadCrmAttentionProjectsMissingInstallDate",
            cacheKey: `projects:${uid}:installDateMissing:limit20`,
            ttlMs: 60 * 1000,
          }
        ),
      ]);

    const missingOwner = missingOwnerSnap.docs.map((item) =>
      normalizeCrmOpportunityRecord(item.id, item.data() || {})
    ).filter((record) => !isCrmRecordUnavailable(record));
    const missingNextAction = missingNextActionSnap.docs.map((item) =>
      normalizeCrmOpportunityRecord(item.id, item.data() || {})
    ).filter((record) => !isCrmRecordUnavailable(record));
    const overdueTasks = overdueTaskSnap.docs.map((item) =>
      normalizeCrmTaskRecord(item.id, item.data() || {})
    ).filter((record) => !isCrmRecordUnavailable(record));
    const missingQuoteFollowUp = quoteFollowUpMissingSnap.docs
      .map((item) => {
        const raw = item.data() || {};
        return {
          id: item.id,
          quoteId: raw.quoteId || raw.sourceQuoteId || item.id,
          quoteIndexId: item.id,
          quoteNo: raw.quoteNo || item.id,
          quoteType: raw.quoteType || raw.quotationType || "",
          customerId: raw.customerId || "",
          customerName: raw.customerName || raw.customerDisplayName || "",
          siteName: raw.siteName || "",
          opportunityId: raw.opportunityId || "",
          followUpDueAt: normalizeCrmDate(raw.followUpDueAt || null),
          updatedAt: normalizeCrmDate(raw.updatedAt || null),
          totalAmount: Number(raw.grandTotal || raw.finalTotal || raw.totalAmount || 0),
          raw,
        };
      })
      .filter((item) => !item.followUpDueAt && !isCrmRecordUnavailable(item))
      .slice(0, 20);
    let handoverPendingProjects = handoverPendingProjectsSnap.docs.map((item) =>
      normalizeCrmProjectRecord(item.id, item.data() || {})
    );
    let advancePendingProjects = advancePendingProjectsSnap.docs.map((item) =>
      normalizeCrmProjectRecord(item.id, item.data() || {})
    );
    let projectsMissingNextAction = projectsMissingNextActionSnap.docs.map((item) =>
      normalizeCrmProjectRecord(item.id, item.data() || {})
    );
    let projectsMissingInstallationDate = projectsMissingInstallationDateSnap.docs.map((item) =>
      normalizeCrmProjectRecord(item.id, item.data() || {})
    );
    let projectFallbackReadCount = 0;
    if (
      !handoverPendingProjects.length ||
      !advancePendingProjects.length ||
      !projectsMissingNextAction.length ||
      !projectsMissingInstallationDate.length
    ) {
      const projectFallbackSnap = await getDocsCached(
        firestoreQuery(
          projectsRef,
          firestoreLimit(20)
        ),
        {
          label: "loadCrmAttentionProjectsFallback",
          cacheKey: `projects:${uid}:attentionBoundedAnyStage:limit20`,
          ttlMs: 30 * 1000,
        }
      );
      projectFallbackReadCount = projectFallbackSnap.size;
      const recentProjects = projectFallbackSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      if (!handoverPendingProjects.length) {
        handoverPendingProjects = recentProjects
          .filter((project) => project.stage === PROJECT_STAGE_HANDOVER_PENDING)
          .slice(0, 20);
      }
      if (!advancePendingProjects.length) {
        advancePendingProjects = recentProjects
          .filter((project) => project.paymentStatus === PROJECT_PAYMENT_STATUS_ADVANCE_PENDING)
          .slice(0, 20);
      }
      if (!projectsMissingNextAction.length) {
        projectsMissingNextAction = recentProjects
          .filter((project) => project.nextActionMissing === true)
          .slice(0, 20);
      }
      if (!projectsMissingInstallationDate.length) {
        projectsMissingInstallationDate = recentProjects
          .filter((project) => project.expectedInstallationDateMissing === true)
          .slice(0, 20);
      }
    }

    let overdueTrackingTasks = [];
    let trackingItemsNotOrdered = [];
    let trackingItemsDelayed = [];
    let trackingArrivalsOverdue = [];
    if (crmSettings.enableDeliveryTracking !== false) {
      const trackingRef = collection(db, "users", uid, "project_tracking_items");
      const todayStartDate = parseDateInputValue(toDateInputValue(new Date())) || new Date();
      todayStartDate.setHours(0, 0, 0, 0);
      const todayEndDate = new Date(todayStartDate.getTime());
      todayEndDate.setHours(23, 59, 59, 999);
      const [overdueDailySnap, notOrderedSnap, delayedSnap, dueSnap] = await Promise.all([
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("recurringType", "==", "daily_tracking"),
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", "<=", nowTimestamp),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_TRACKING_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionTrackingDailyTasksOverdue",
            cacheKey: `crmTasks:${uid}:recurringType:daily_tracking:status:open:due:${toDateInputValue(new Date())}:limit${CRM_TRACKING_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            trackingRef,
            firestoreWhere("status", "==", "not_ordered"),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_TRACKING_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionTrackingNotOrdered",
            cacheKey: `projectTracking:${uid}:status:not_ordered:updatedAtDesc:limit${CRM_TRACKING_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            trackingRef,
            firestoreWhere("status", "==", "delayed"),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_TRACKING_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionTrackingDelayed",
            cacheKey: `projectTracking:${uid}:status:delayed:updatedAtDesc:limit${CRM_TRACKING_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            trackingRef,
            firestoreWhere("status", "in", [
              "not_ordered",
              "ordered",
              "in_transit",
              "reached_local_hub",
              "partially_received",
              "delayed",
            ]),
            firestoreWhere("expectedArrivalDate", "<=", Timestamp.fromDate(todayEndDate)),
            firestoreOrderBy("expectedArrivalDate", "asc"),
            firestoreLimit(CRM_TRACKING_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionTrackingDue",
            cacheKey: `projectTracking:${uid}:dueThrough:${toDateInputValue(todayEndDate)}:limit${CRM_TRACKING_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
      ]);
      overdueTrackingTasks = overdueDailySnap.docs.map((item) =>
        normalizeCrmTaskRecord(item.id, item.data() || {})
      );
      trackingItemsNotOrdered = notOrderedSnap.docs.map((item) =>
        normalizeCrmMaterialRecord(item.id, item.data() || {})
      );
      trackingItemsDelayed = delayedSnap.docs.map((item) =>
        normalizeCrmMaterialRecord(item.id, item.data() || {})
      );
      const dueItems = dueSnap.docs
        .map((item) => normalizeCrmMaterialRecord(item.id, item.data() || {}))
        .filter((material) => !["received", "cancelled"].includes(material.status));
      trackingArrivalsOverdue = dueItems
        .filter((material) => {
          const millis = safeTimeMillis(material.expectedArrivalDate);
          return millis && millis < todayStartDate.getTime();
        })
        .slice(0, CRM_TRACKING_ATTENTION_READ_LIMIT);
      if (isDevRuntime) {
        devLog("CRM_TRACKING_ATTENTION_TRACE", {
          overdueTrackingTasks: overdueTrackingTasks.length,
          delayedItems: trackingItemsDelayed.length,
          overdueArrivals: trackingArrivalsOverdue.length,
          notOrderedItems: trackingItemsNotOrdered.length,
        });
      }
    }

    let balancePendingProjects = [];
    let overduePaymentTasks = [];
    let paymentDueTodayTasks = [];
    if (crmSettings.enablePaymentTracking !== false) {
      const todayStartDate = parseDateInputValue(toDateInputValue(new Date())) || new Date();
      todayStartDate.setHours(0, 0, 0, 0);
      const todayEndDate = new Date(todayStartDate.getTime());
      todayEndDate.setHours(23, 59, 59, 999);
      const [balancePendingSnap, overduePaymentSnap, dueTodayPaymentSnap] = await Promise.all([
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("paymentStatus", "in", [
              PROJECT_PAYMENT_STATUS_ADVANCE_PENDING,
              PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED,
              PROJECT_PAYMENT_STATUS_PART_PAID,
            ]),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PAYMENT_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionPaymentBalancePending",
            cacheKey: `projects:${uid}:paymentStatus:pendingAny:updatedAtDesc:limit${CRM_PAYMENT_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("sourceKind", "==", "payment_follow_up"),
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_PAYMENT_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionPaymentTasksOverdue",
            cacheKey: `crmTasks:${uid}:sourceKind:payment_follow_up:status:open:overdue:${toDateInputValue(todayStartDate)}:limit${CRM_PAYMENT_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("sourceKind", "==", "payment_follow_up"),
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", ">=", Timestamp.fromDate(todayStartDate)),
            firestoreWhere("dueAt", "<=", Timestamp.fromDate(todayEndDate)),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_PAYMENT_ATTENTION_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionPaymentTasksDueToday",
            cacheKey: `crmTasks:${uid}:sourceKind:payment_follow_up:status:open:dueToday:${toDateInputValue(todayStartDate)}:limit${CRM_PAYMENT_ATTENTION_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
      ]);
      balancePendingProjects = balancePendingSnap.docs
        .map((item) => normalizeCrmProjectRecord(item.id, item.data() || {}))
        .filter(
          (project) =>
            project.stage !== PROJECT_STAGE_CANCELLED &&
            Number(project.balanceAmount || project.paymentSummary?.balanceAmount || 0) > 0
        )
        .slice(0, CRM_PAYMENT_ATTENTION_READ_LIMIT);
      overduePaymentTasks = overduePaymentSnap.docs.map((item) =>
        normalizeCrmTaskRecord(item.id, item.data() || {})
      );
      paymentDueTodayTasks = dueTodayPaymentSnap.docs.map((item) =>
        normalizeCrmTaskRecord(item.id, item.data() || {})
      );
      if (isDevRuntime) {
        devLog("CRM_PAYMENT_ATTENTION_TRACE", {
          balancePendingCount: balancePendingProjects.length,
          overduePaymentTasks: overduePaymentTasks.length,
          paymentDueToday: paymentDueTodayTasks.length,
        });
      }
    }

    const todayStartDate = parseDateInputValue(toDateInputValue(new Date())) || new Date();
    todayStartDate.setHours(0, 0, 0, 0);
    const todayEndDate = new Date(todayStartDate.getTime());
    todayEndDate.setHours(23, 59, 59, 999);
    let installationNotScheduledProjects = [];
    let installationsDueToday = [];
    let overdueInstallations = [];
    let productionOverdueProjects = [];
    let materialDueOverdueProjects = [];
    let openSnagProjects = [];
    let overdueSnagProjects = [];
    let projectsReadyToClose = [];
    let projectsBlockedByFinalPayment = [];
    let reviewDueTodayTasks = [];
    let overdueReviewTasks = [];
    try {
      const [
        installationNotScheduledSnap,
        installationsTodaySnap,
        overdueInstallationsSnap,
        productionOverdueSnap,
        materialDueOverdueSnap,
        openSnagsSnap,
        overdueSnagsSnap,
        readyToCloseSnap,
        blockedByPaymentSnap,
        reviewDueTodaySnap,
        overdueReviewSnap,
      ] = await Promise.all([
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("installationStatus", "==", PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionInstallationNotScheduled",
            cacheKey: `projects:${uid}:installationNotScheduled:updatedAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("installationStatus", "==", PROJECT_INSTALLATION_STATUS_SCHEDULED),
            firestoreWhere("installationScheduledAt", ">=", Timestamp.fromDate(todayStartDate)),
            firestoreWhere("installationScheduledAt", "<=", Timestamp.fromDate(todayEndDate)),
            firestoreOrderBy("installationScheduledAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionInstallationsDueToday",
            cacheKey: `projects:${uid}:installationsDueToday:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("productionDueAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("productionDueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionProductionOverdue",
            cacheKey: `projects:${uid}:productionOverdue:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("materialDueAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("materialDueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionMaterialDueOverdue",
            cacheKey: `projects:${uid}:materialDueOverdue:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("installationStatus", "==", PROJECT_INSTALLATION_STATUS_SCHEDULED),
            firestoreWhere("installationScheduledAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("installationScheduledAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionInstallationsOverdue",
            cacheKey: `projects:${uid}:installationsOverdue:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("hasOpenSnags", "==", true),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionOpenSnags",
            cacheKey: `projects:${uid}:hasOpenSnags:true:updatedAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("hasOpenSnags", "==", true),
            firestoreWhere("nextSnagDueAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("nextSnagDueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionSnagsOverdue",
            cacheKey: `projects:${uid}:snagsOverdue:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("stage", "==", PROJECT_STAGE_INSTALLATION_COMPLETED),
            firestoreWhere("hasPendingBalance", "==", false),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionProjectsReadyToClose",
            cacheKey: `projects:${uid}:readyToClose:updatedAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("status", "==", "open"),
            firestoreWhere("stage", "==", PROJECT_STAGE_FINAL_PAYMENT_PENDING),
            firestoreWhere("hasPendingBalance", "==", true),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionProjectsBlockedByPayment",
            cacheKey: `projects:${uid}:blockedByFinalPayment:updatedAtDesc:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("sourceKind", "==", "project_review_request"),
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", ">=", Timestamp.fromDate(todayStartDate)),
            firestoreWhere("dueAt", "<=", Timestamp.fromDate(todayEndDate)),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionReviewDueToday",
            cacheKey: `crmTasks:${uid}:reviewDueToday:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
        getDocsCached(
          firestoreQuery(
            tasksRef,
            firestoreWhere("sourceKind", "==", "project_review_request"),
            firestoreWhere("status", "==", CRM_TASK_STATUS_OPEN),
            firestoreWhere("dueAt", "<", Timestamp.fromDate(todayStartDate)),
            firestoreOrderBy("dueAt", "asc"),
            firestoreLimit(CRM_PROJECT_RELATED_READ_LIMIT)
          ),
          {
            label: "loadCrmAttentionReviewOverdue",
            cacheKey: `crmTasks:${uid}:reviewOverdue:${toDateInputValue(todayStartDate)}:limit${CRM_PROJECT_RELATED_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        ),
      ]);
      installationNotScheduledProjects = installationNotScheduledSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      installationsDueToday = installationsTodaySnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      overdueInstallations = overdueInstallationsSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      productionOverdueProjects = productionOverdueSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      materialDueOverdueProjects = materialDueOverdueSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      openSnagProjects = openSnagsSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      overdueSnagProjects = overdueSnagsSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      projectsReadyToClose = readyToCloseSnap.docs
        .map((item) => normalizeCrmProjectRecord(item.id, item.data() || {}))
        .filter((project) => !project.hasOpenSnags && !project.hasPendingBalance);
      projectsBlockedByFinalPayment = blockedByPaymentSnap.docs.map((item) =>
        normalizeCrmProjectRecord(item.id, item.data() || {})
      );
      reviewDueTodayTasks = reviewDueTodaySnap.docs.map((item) =>
        normalizeCrmTaskRecord(item.id, item.data() || {})
      );
      overdueReviewTasks = overdueReviewSnap.docs.map((item) =>
        normalizeCrmTaskRecord(item.id, item.data() || {})
      );
      if (isDevRuntime) {
        devLog("CRM_EXECUTION_ATTENTION_TRACE", {
          installationNotScheduledProjects: installationNotScheduledProjects.length,
          installationsDueToday: installationsDueToday.length,
          overdueInstallations: overdueInstallations.length,
          productionOverdueProjects: productionOverdueProjects.length,
          materialDueOverdueProjects: materialDueOverdueProjects.length,
          openSnagProjects: openSnagProjects.length,
          overdueSnagProjects: overdueSnagProjects.length,
          projectsReadyToClose: projectsReadyToClose.length,
          projectsBlockedByFinalPayment: projectsBlockedByFinalPayment.length,
          reviewDueTodayTasks: reviewDueTodayTasks.length,
          overdueReviewTasks: overdueReviewTasks.length,
        });
        [
          ["installation_not_scheduled", installationNotScheduledProjects.length],
          ["installations_due_today", installationsDueToday.length],
          ["overdue_installations", overdueInstallations.length],
          ["production_overdue", productionOverdueProjects.length],
          ["material_due_overdue", materialDueOverdueProjects.length],
          ["open_snags", openSnagProjects.length],
          ["overdue_snags", overdueSnagProjects.length],
          ["projects_ready_to_close", projectsReadyToClose.length],
          ["projects_blocked_by_final_payment", projectsBlockedByFinalPayment.length],
          ["review_due_today", reviewDueTodayTasks.length],
          ["overdue_review_requests", overdueReviewTasks.length],
        ].forEach(([card, resultCount]) => {
          devLog("ATTENTION_REQUIRED_PROJECT_TRACE", {
            card,
            query: "bounded_project_execution_attention",
            resultCount,
            boundedLimit: CRM_PROJECT_RELATED_READ_LIMIT,
          });
        });
      }
    } catch (executionAttentionError) {
      if (isDevRuntime) {
        devWarn("CRM_EXECUTION_ATTENTION_FAILED_EXACT", {
          code: executionAttentionError?.code || "",
          message: executionAttentionError?.message || String(executionAttentionError),
        });
      }
    }

    if (isDevRuntime) {
      devLog("CRM_ATTENTION_REQUIRED_TRACE", {
        missingOwnerCount: missingOwner.length,
        missingNextActionCount: missingNextAction.length,
        overdueTaskCount: overdueTasks.length,
        missingQuoteFollowUpCount: missingQuoteFollowUp.length,
        handoverPendingProjectCount: handoverPendingProjects.length,
        advancePendingProjectCount: advancePendingProjects.length,
        projectsMissingNextActionCount: projectsMissingNextAction.length,
        projectsMissingInstallationDateCount: projectsMissingInstallationDate.length,
        overdueTrackingTasksCount: overdueTrackingTasks.length,
        trackingItemsNotOrderedCount: trackingItemsNotOrdered.length,
        trackingItemsDelayedCount: trackingItemsDelayed.length,
        trackingArrivalsOverdueCount: trackingArrivalsOverdue.length,
        balancePendingProjectCount: balancePendingProjects.length,
        overduePaymentTaskCount: overduePaymentTasks.length,
        paymentDueTodayTaskCount: paymentDueTodayTasks.length,
        installationNotScheduledCount: installationNotScheduledProjects.length,
        installationsDueTodayCount: installationsDueToday.length,
        overdueInstallationsCount: overdueInstallations.length,
        productionOverdueProjectCount: productionOverdueProjects.length,
        materialDueOverdueProjectCount: materialDueOverdueProjects.length,
        openSnagProjectCount: openSnagProjects.length,
        overdueSnagProjectCount: overdueSnagProjects.length,
        projectsReadyToCloseCount: projectsReadyToClose.length,
        projectsBlockedByFinalPaymentCount: projectsBlockedByFinalPayment.length,
        reviewDueTodayTaskCount: reviewDueTodayTasks.length,
        overdueReviewTaskCount: overdueReviewTasks.length,
        projectFallbackReadCount,
      });
    }

    setCrmAttentionState({
      loading: false,
      loaded: true,
      error: "",
      missingOwner,
      missingNextAction,
      overdueTasks,
      missingQuoteFollowUp,
      handoverPendingProjects,
      advancePendingProjects,
      projectsMissingNextAction,
      projectsMissingInstallationDate,
      overdueTrackingTasks,
      trackingItemsNotOrdered,
      trackingItemsDelayed,
      trackingArrivalsOverdue,
      balancePendingProjects,
      overduePaymentTasks,
      paymentDueTodayTasks,
      installationNotScheduledProjects,
      installationsDueToday,
      overdueInstallations,
      productionOverdueProjects,
      materialDueOverdueProjects,
      openSnagProjects,
      overdueSnagProjects,
      projectsReadyToClose,
      projectsBlockedByFinalPayment,
      reviewDueTodayTasks,
      overdueReviewTasks,
    });
  } catch (error) {
    devError("CRM attention load failed:", {
      code: error?.code,
      message: error?.message,
      path: `users/${uid}/opportunities + crm_tasks + projects`,
    });
    setCrmAttentionState((prev) => ({
      ...prev,
      loading: false,
      loaded: true,
      error:
        error?.code === "failed-precondition"
          ? "CRM attention indexes are still building. Please try again shortly."
          : "Could not load attention required items.",
    }));
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [
  canAccessCrm,
  crmSettings.enableDeliveryTracking,
  crmSettings.enablePaymentTracking,
  normalizeCrmMaterialRecord,
  normalizeCrmOpportunityRecord,
  normalizeCrmProjectRecord,
  normalizeCrmTaskRecord,
  notify,
  parseDateInputValue,
  toDateInputValue,
]);

const loadDashboardPreviewData = useCallback(async (uid) => {
  if (!uid) return;
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_DASHBOARD_PRELOADS")) {
    if (isDevRuntime) {
      devWarn("Dashboard preview reads disabled by FQ_DISABLE_DASHBOARD_PRELOADS");
    }
    return;
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const allowedQuoteTypes = getAllowedCanonicalQuoteTypes({
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
  });
  const allowedLeadTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

  try {
    const selectedAnalyticsMonthKeys = Array.from(
      new Set([
        ...getRecentMonthKeys(DASHBOARD_MONTHLY_CHART_MONTHS),
        ...getDashboardAnalyticsMonthKeys(periodType, customRange),
      ])
    );
    const primaryMonthKey = getPrimaryDashboardMonthKey(periodType, customRange);
    const calendarMonthKey = getMonthKeyFromDateParts(calendarMonth.year, calendarMonth.month);

    const monthlySnaps = await Promise.all(
      selectedAnalyticsMonthKeys.map((monthKey) =>
        getDocCached(doc(db, "users", uid, "analytics_monthly", monthKey), {
          label: "dashboardAnalyticsMonthlyById",
          cacheKey: `users/${uid}/analytics_monthly/${monthKey}`,
          ttlMs: 5 * 60 * 1000,
        }).then((snap) => ({ monthKey, snap }))
      )
    );

    const topClientsQuery = firestoreQuery(
      collection(db, "users", uid, "client_monthly"),
      firestoreWhere("monthKey", "==", primaryMonthKey),
      firestoreOrderBy("confirmedRevenue", "desc"),
      firestoreLimit(DASHBOARD_TOP_CLIENT_LIMIT)
    );
    const topClientSnap = await getDocsCached(topClientsQuery, {
      label: "dashboardClientMonthlyTop",
      cacheKey: `users/${uid}/client_monthly?monthKey=${primaryMonthKey}&orderBy=confirmedRevenue_desc&limit=${DASHBOARD_TOP_CLIENT_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const installationQuery = firestoreQuery(
      collection(db, "users", uid, "installation_index"),
      firestoreWhere("monthKey", "==", calendarMonthKey),
      firestoreOrderBy("installationDateKey", "asc"),
      firestoreLimit(DASHBOARD_INSTALLATION_MONTH_LIMIT)
    );
    const installationSnap = await getDocsCached(installationQuery, {
      label: "dashboardInstallationIndexMonth",
      cacheKey: `users/${uid}/installation_index?monthKey=${calendarMonthKey}&orderBy=installationDateKey_asc&limit=${DASHBOARD_INSTALLATION_MONTH_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const existingMonthlyCount = monthlySnaps.filter(({ snap }) => snap.exists()).length;
    const monthlySummaries = monthlySnaps.reduce((accumulator, { monthKey, snap }) => {
      accumulator[monthKey] = snap.exists()
        ? {
            monthKey,
            ...(snap.data() || {}),
            updatedAt: normalizeTs(snap.data()?.updatedAt),
          }
        : {
            monthKey,
            confirmedRevenue: 0,
            confirmedCount: 0,
            quotationRevenue: 0,
            quotationCount: 0,
            cancelledRevenue: 0,
            cancelledCount: 0,
            installedRevenue: 0,
            installedCount: 0,
            updatedAt: null,
          };
      return accumulator;
    }, {});
    const analyticsIndexMissing =
      existingMonthlyCount === 0 && topClientSnap.size === 0 && installationSnap.size === 0;

    const topClients = topClientSnap.docs.map((docSnap) => {
      const raw = docSnap.data() || {};
      return {
        id: docSnap.id,
        ...raw,
        name: raw.customerName || "Unnamed Customer",
        value: Number(raw.confirmedRevenue || raw.quotationRevenue || 0),
        count: Number(raw.confirmedCount || raw.quoteCount || 0),
        lastActivityAt: normalizeTs(raw.lastActivityAt),
        updatedAt: normalizeTs(raw.updatedAt),
      };
    });

    const installationRecords = installationSnap.docs.map((docSnap) => {
      const raw = docSnap.data() || {};
      const installDateKey =
        raw.installationDateKey || toDateInputValue(raw.installationDate);
      return {
        id: docSnap.id,
        ...raw,
        quoteId: raw.quoteId || docSnap.id,
        jobId: raw.jobId || raw.quoteId || docSnap.id,
        customerLabel: raw.customerName || "Unnamed Customer",
        customerName: raw.customerName || "",
        quoteNo: raw.quoteNo || "",
        installationDateKey: installDateKey,
        installationMillis: safeTimeMillis(raw.installationDate || installDateKey),
        totalValue: Number(raw.totalAmount || raw.confirmedRevenue || 0),
        updatedAt: normalizeTs(raw.updatedAt),
      };
    });

    setDashboardAnalyticsState({
      monthKeys: selectedAnalyticsMonthKeys,
      monthlySummaries,
      topClients,
      installationRecords,
      diagnostics: {
        source: "analytics_indexes",
        monthKey: primaryMonthKey,
        calendarMonthKey,
        reads: {
          analyticsMonthly: selectedAnalyticsMonthKeys.length,
          clientMonthly: topClientSnap.size,
          installationIndex: installationSnap.size,
        },
        summaryDocsFound: existingMonthlyCount,
        topClientDocsFound: topClientSnap.size,
        installationDocsFound: installationSnap.size,
        indexMissing: analyticsIndexMissing,
        status: analyticsIndexMissing ? "missing" : "ready",
        lastUpdated:
          monthlySummaries[primaryMonthKey]?.updatedAt ||
          topClients[0]?.updatedAt ||
          installationRecords[0]?.updatedAt ||
          null,
        loadedAt: new Date().toISOString(),
      },
    });

    const quoteQuery = firestoreQuery(
      collection(db, "users", uid, "quote_index"),
      firestoreOrderBy("lastActivityAt", "desc"),
      firestoreLimit(DASHBOARD_PREVIEW_READ_LIMIT)
    );
    const quoteSnap = await getDocsCached(quoteQuery, {
      label: "dashboardQuoteIndexLimited",
      cacheKey: `users/${uid}/quote_index?dashboardOrderBy=lastActivityAt_desc&limit=${DASHBOARD_PREVIEW_READ_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const dashboardQuotes = quoteSnap.docs
      .map((docSnap) => {
        const raw = docSnap.data() || {};
        return normalizeQuoteIndexRecordForList(docSnap.id, raw, normalizeTs);
      })
      .filter(
        (quote) =>
          !quote.deleted &&
          quoteTypeMatchesAccess(quote, allowedQuoteTypes)
      );

    setDashboardQuotePreview(dashboardQuotes);
    setDebugRawQuotationCount(quoteSnap.size);

    if (!canAccessCrm || allowedLeadTypes.length === 0) {
      setCrmLeadRecords([]);
      return;
    }

    const leadQuery = firestoreQuery(
      collection(db, "users", uid, "crm_leads"),
      firestoreOrderBy("lastActivityAt", "desc"),
      firestoreLimit(DASHBOARD_PREVIEW_READ_LIMIT)
    );
    const leadSnap = await getDocsCached(leadQuery, {
      label: "dashboardCrmLeadsLimited",
      cacheKey: `users/${uid}/crm_leads?dashboardOrderBy=lastActivityAt_desc&limit=${DASHBOARD_PREVIEW_READ_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const dashboardLeads = [];
    leadSnap.forEach((docSnap) => {
      const raw = docSnap.data() || {};
      const pipelineType = isGeneralExecutionEligibleDeal(raw)
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;

      if (!allowedLeadTypes.includes(pipelineType)) {
        return;
      }

      const linkedQuotes = normalizeCrmLinkedQuotes({
        ...raw,
        linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
      });
      const rawChecklistItems = Array.isArray(raw.checklistItems)
        ? raw.checklistItems
        : Array.isArray(raw.checklist)
        ? raw.checklist
        : Array.isArray(raw.stageChecklist)
        ? raw.stageChecklist
        : [];

      dashboardLeads.push(stripLegacyCrmQuoteLinkFields({
        id: docSnap.id,
        ...raw,
        quoteType: raw.quoteType || pipelineType,
        pipelineType,
        contact: raw.contact || {},
        customerId: raw.customerId || "",
        customerName:
          raw.customerName ||
          raw.contact?.name ||
          raw.title ||
          "",
        customerPhone:
          raw.customerPhone ||
          raw.contact?.phone ||
          raw.contact?.whatsapp ||
          "",
        linkedQuotes,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        checklistItems: rawChecklistItems,
        hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
        nextActionDate: normalizeTs(raw.nextActionDate),
        installationDate: normalizeTs(raw.installationDate),
        reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
        }),
        followUps: normalizeCrmFollowUps(raw.followUps, {
          nextAction: raw.nextAction,
          nextActionDate: normalizeTs(raw.nextActionDate),
          reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }),
        attachments: normalizeCrmAttachments(raw.attachments),
        executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
        assignedAt: normalizeTs(raw.assignedAt),
        createdAt: normalizeTs(raw.createdAt),
        updatedAt: normalizeTs(raw.updatedAt),
        lastActivityAt: normalizeTs(raw.lastActivityAt),
        needsQuoteLinkMigration: false,
      }));
    });

    setDebugRawCrmLeadCount(leadSnap.size);
    setCrmLeadRecords(sortCrmRecordsByLatestActivity(dashboardLeads));
  } catch (error) {
    devError("Dashboard preview load failed:", error);
    setDashboardAnalyticsState((prev) => ({
      ...prev,
      diagnostics: {
        ...(prev.diagnostics || {}),
        source: "analytics_indexes",
        status: "error",
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        loadedAt: new Date().toISOString(),
      },
    }));
    const message = String(error?.message || "");
    if (error?.code === "failed-precondition" && message.includes("index")) {
      devError("Missing Firestore index for dashboard summary query", {
        expectedIndexes: [
          "client_monthly: monthKey ASC, confirmedRevenue DESC",
          "installation_index: monthKey ASC, installationDateKey ASC",
        ],
      });
      if (isDevRuntime) {
        notify("Dashboard summary index is missing. Deploy firestore.indexes.json.", "warning");
      }
    }
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${uid}/dashboard-limited-data`,
        source: "loadDashboardPreviewData",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [
  canAccessCrm,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  calendarMonth,
  customRange,
  getDashboardAnalyticsMonthKeys,
  getMonthKeyFromDateParts,
  getPrimaryDashboardMonthKey,
  getRecentMonthKeys,
  notify,
  periodType,
  raiseFirebaseHealthWarning,
]);

const subscribeCustomerLedger = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const customersRef = collection(db, "users", uid, "customers");
  const customersQuery = firestoreQuery(
    customersRef,
    firestoreOrderBy("lastActivityAt", "desc"),
    firestoreLimit(CUSTOMER_LEDGER_INITIAL_READ_LIMIT)
  );

  const collectionPath = `users/${uid}/customers`;
  let cancelled = false;
  getDocsCached(customersQuery, {
    label: "loadCustomerLedgerRecentPage",
    cacheKey: `customerLedger:recent:${uid}:limit${CUSTOMER_LEDGER_INITIAL_READ_LIMIT}`,
    ttlMs: 5 * 60 * 1000,
  })
    .then(async (snap) => {
      if (cancelled) return;
      let sourceSnap = snap;
      if (sourceSnap.size === 0) {
        sourceSnap = await getDocsCached(
          firestoreQuery(
            customersRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(CUSTOMER_LEDGER_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadCustomerLedgerLegacyPage",
            cacheKey: `customerLedger:recent:${uid}:legacyDocId:limit${CUSTOMER_LEDGER_INITIAL_READ_LIMIT}`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      const nextCustomers = sourceSnap.docs
        .map((docSnap) =>
          normalizeCustomerLedgerRecord({
            id: docSnap.id,
            ...docSnap.data(),
          })
        )
        .map((customer) => ({
          ...customer,
          createdAt: normalizeTs(customer.createdAt),
          updatedAt: normalizeTs(customer.updatedAt),
          lastActivityAt: normalizeTs(customer.lastActivityAt),
        }))
        .sort(
          (a, b) =>
            getRecordSortTime(b, ["lastActivityAt", "latestActivityAt", "updatedAt", "createdAt"]) -
            getRecordSortTime(a, ["lastActivityAt", "latestActivityAt", "updatedAt", "createdAt"])
        );

      setCustomerRecords(nextCustomers);
    })
    .catch((error) => {
      if (cancelled) return;
      devError("Customer ledger load failed:", error);
      setCustomerRecords([]);
      notify(
        isFirestoreResourceExhausted(error)
          ? "Firebase read limit reached. Please wait and try again."
          : "Could not load customer list. You can still enter customer details manually.",
        "warning"
      );
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      devLog("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCustomerLedgerOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

const loadCustomerLedgerQuotes = useCallback(
  async (customerId) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId) return [];

    const selectedCustomer =
      customerRecords.find((customer) => String(customer.id || "") === normalizedCustomerId) ||
      null;
    const cacheKey = `customerLedger:quotes:${workspaceUid}:${normalizedCustomerId}:lastActivityAt:limit${CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT}`;
    const loadState = customerLedgerQuotesLoadRef.current?.[cacheKey];

    if (loadState?.loaded && Array.isArray(loadState.records)) {
      setCustomerLedgerQuotesByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: loadState.records,
      }));
      return loadState.records;
    }

    if (loadState?.loading && loadState.promise) {
      setCustomerLedgerQuotesLoadingId(normalizedCustomerId);
      try {
        const records = await loadState.promise;
        setCustomerLedgerQuotesByCustomer((prev) => ({
          ...prev,
          [normalizedCustomerId]: records,
        }));
        return records;
      } finally {
        setCustomerLedgerQuotesLoadingId("");
      }
    }

    const normalizeTs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === "string" || typeof v === "number") {
        const parsed = new Date(v);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const allowedQuoteTypes = getAllowedCanonicalQuoteTypes({
      canAccessCurtainQuotes,
      canAccessGeneralQuotes,
    });
    const traceCustomerLedgerQuoteMatch = (quote = {}, details = {}) => {
      if (!isDevRuntime) return;
      devLog("CUSTOMER_LEDGER_QUOTE_MATCH_TRACE", {
        selectedCustomerId: normalizedCustomerId,
        selectedCustomerName: selectedCustomer?.customerName || "",
        quoteId: quote.id || quote.quoteId || "",
        quoteNo: quote.quoteNo || "",
        quoteCustomerId: quote.customerId || quote.crm?.customerId || "",
        quoteCustomerName: quote.customerName || quote.crm?.customerName || "",
        matchType: details.matchType || "",
        included: details.included === true,
        excludedReason: details.excludedReason || "",
      });
    };

    const runCustomerQuoteQuery = async () => {
      const quoteIndexRef = collection(db, "users", workspaceUid, "quote_index");
      const preferredQuery = firestoreQuery(
        quoteIndexRef,
        firestoreWhere("customerId", "==", normalizedCustomerId),
        firestoreOrderBy("lastActivityAt", "desc"),
        firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
      );

      let snap;
      try {
        snap = await getDocsCached(preferredQuery, {
          label: "customerLedgerSelectedCustomerQuotes",
          cacheKey,
          ttlMs: 5 * 60 * 1000,
        });
      } catch (error) {
        if (String(error?.code || "") !== "failed-precondition") {
          throw error;
        }
        snap = await getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerQuotesNoOrderFallback",
            cacheKey: `${cacheKey}:noOrderFallback`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      let records = snap.docs
        .map((docSnap) =>
          normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
        )
        .filter((quote) =>
          quoteTypeMatchesAccess(quote, allowedQuoteTypes)
        )
        .map((quote) => ({
          ...quote,
          customerLedgerMatchType: "customerId",
          customerLedgerLinked: true,
          customerLedgerUnlinkedMatch: false,
        }));
      records.forEach((quote) =>
        traceCustomerLedgerQuoteMatch(quote, {
          matchType: "customerId",
          included: true,
        })
      );

      if (records.length === 0 && selectedCustomer?.customerKey) {
        const keySnap = await getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("customerKey", "==", selectedCustomer.customerKey),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerQuotesByCustomerKey",
            cacheKey: `${cacheKey}:customerKey`,
            ttlMs: 5 * 60 * 1000,
          }
        );
        records = keySnap.docs
          .map((docSnap) =>
            normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
          )
          .filter((quote) =>
            quoteTypeMatchesAccess(quote, allowedQuoteTypes)
          )
          .map((quote) => {
            const linkedCustomerId = String(
              quote.customerId || quote.crm?.customerId || ""
            ).trim();
            const isLegacyCustomerKeyMatch = !linkedCustomerId;
            traceCustomerLedgerQuoteMatch(quote, {
              matchType: isLegacyCustomerKeyMatch ? "customerKey" : "customerId",
              included:
                isLegacyCustomerKeyMatch || linkedCustomerId === normalizedCustomerId,
              excludedReason:
                linkedCustomerId && linkedCustomerId !== normalizedCustomerId
                  ? "linked_to_other_customer"
                  : "",
            });
            return {
              ...quote,
              customerLedgerMatchType: isLegacyCustomerKeyMatch
                ? "customerKey"
                : "customerId",
              customerLedgerLinked: !isLegacyCustomerKeyMatch,
              customerLedgerUnlinkedMatch: isLegacyCustomerKeyMatch,
            };
          })
          .filter((quote) => {
            const linkedCustomerId = String(
              quote.customerId || quote.crm?.customerId || ""
            ).trim();
            return !linkedCustomerId || linkedCustomerId === normalizedCustomerId;
          });
      }

      if (selectedCustomer) {
        const mergeExactMatches = (nextRecords = [], matchType = "legacy_exact") => {
          const byId = new Map(records.map((quote) => [String(quote.id || quote.quoteId || ""), quote]));
          nextRecords.forEach((quote) => {
            if (!quoteTypeMatchesAccess(quote, allowedQuoteTypes)) {
              traceCustomerLedgerQuoteMatch(quote, {
                matchType,
                included: false,
                excludedReason: "quote_type_not_allowed",
              });
              return;
            }
            const linkedCustomerId = String(quote.customerId || quote.crm?.customerId || "").trim();
            if (linkedCustomerId && linkedCustomerId !== normalizedCustomerId) {
              traceCustomerLedgerQuoteMatch(quote, {
                matchType,
                included: false,
                excludedReason: "linked_to_other_customer",
              });
              return;
            }
            if (linkedCustomerId) {
              traceCustomerLedgerQuoteMatch(quote, {
                matchType,
                included: false,
                excludedReason: "legacy_match_skipped_because_customerId_exists",
              });
              return;
            }
            const quoteId = String(quote.id || quote.quoteId || "").trim();
            if (!quoteId) {
              traceCustomerLedgerQuoteMatch(quote, {
                matchType,
                included: false,
                excludedReason: "missing_quote_id",
              });
              return;
            }
            if (byId.has(quoteId)) {
              traceCustomerLedgerQuoteMatch(quote, {
                matchType,
                included: false,
                excludedReason: "already_loaded",
              });
              return;
            }
            const legacyQuote = {
              ...quote,
              customerLedgerMatchType: matchType,
              customerLedgerLinked: false,
              customerLedgerUnlinkedMatch: true,
            };
            traceCustomerLedgerQuoteMatch(legacyQuote, {
              matchType,
              included: true,
            });
            byId.set(quoteId, legacyQuote);
          });
          records = Array.from(byId.values());
        };
        const normalizedPhone = normalizeCustomerLedgerPhone(
          selectedCustomer.normalizedPhone ||
            selectedCustomer.phone ||
            selectedCustomer.whatsapp ||
            ""
        );
        const normalizedEmail = normalizeCustomerLedgerEmail(
          selectedCustomer.normalizedEmail || selectedCustomer.email || ""
        );
        const normalizedName = normalizeSearchText(selectedCustomer.customerName || "");
        const fallbackQueries = [
          normalizedPhone
            ? {
                key: "phone",
                queryRef: firestoreQuery(
                  quoteIndexRef,
                  firestoreWhere("normalizedPhone", "==", normalizedPhone),
                  firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
                ),
              }
            : null,
          normalizedEmail
            ? {
                key: "email",
                queryRef: firestoreQuery(
                  quoteIndexRef,
                  firestoreWhere("normalizedEmail", "==", normalizedEmail),
                  firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
                ),
              }
            : null,
          normalizedName
            ? {
                key: "name",
                queryRef: firestoreQuery(
                  quoteIndexRef,
                  firestoreWhere("customerNameLower", "==", normalizedName),
                  firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
                ),
              }
            : null,
        ].filter(Boolean);

        for (const fallback of fallbackQueries) {
          const fallbackSnap = await getDocsCached(fallback.queryRef, {
            label: `customerLedgerSelectedCustomerQuotesExact${fallback.key}`,
            cacheKey: `${cacheKey}:exact:${fallback.key}`,
            ttlMs: 5 * 60 * 1000,
          });
          mergeExactMatches(
            fallbackSnap.docs.map((docSnap) =>
              normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
            ),
            `legacy_exact_${fallback.key}`
          );
        }
      }

      records.sort(
        (a, b) =>
          getQuoteListSortTime(b) - getQuoteListSortTime(a)
      );
      return records;
    };

    const promise = runCustomerQuoteQuery()
      .then((records) => {
        customerLedgerQuotesLoadRef.current[cacheKey] = {
          loading: false,
          loaded: true,
          promise: null,
          records,
        };
        return records;
      })
      .catch((error) => {
        delete customerLedgerQuotesLoadRef.current[cacheKey];
        throw error;
      });

    customerLedgerQuotesLoadRef.current[cacheKey] = {
      loading: true,
      loaded: false,
      promise,
      records: [],
    };

    setCustomerLedgerQuotesLoadingId(normalizedCustomerId);
    try {
      const records = await promise;
      setCustomerLedgerQuotesByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: records,
      }));
      return records;
    } catch (error) {
      devError("Customer ledger selected quotes load failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        customerId: normalizedCustomerId,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else {
        notify("Could not load quotes for this customer.", "warning");
      }
      return [];
    } finally {
      setCustomerLedgerQuotesLoadingId("");
    }
  },
  [
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    customerRecords,
    notify,
    workspaceUid,
  ]
);

const loadCustomerLedgerCrmDeals = useCallback(
  async (customerId) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId || !canAccessCrm) return [];

    const selectedCustomer =
      customerRecords.find((customer) => String(customer.id || "") === normalizedCustomerId) ||
      null;
    const cacheKey = `customerLedger:crmDeals:${workspaceUid}:${normalizedCustomerId}:lastActivityAt:limit${CUSTOMER_LEDGER_SELECTED_CRM_LIMIT}`;
    const loadState = customerLedgerCrmDealsLoadRef.current?.[cacheKey];

    if (loadState?.loaded && Array.isArray(loadState.records)) {
      setCustomerLedgerCrmDealsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: loadState.records,
      }));
      return loadState.records;
    }

    if (loadState?.loading && loadState.promise) {
      setCustomerLedgerCrmDealsLoadingId(normalizedCustomerId);
      try {
        const records = await loadState.promise;
        setCustomerLedgerCrmDealsByCustomer((prev) => ({
          ...prev,
          [normalizedCustomerId]: records,
        }));
        return records;
      } finally {
        setCustomerLedgerCrmDealsLoadingId("");
      }
    }

    const normalizeTs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === "string" || typeof v === "number") {
        const parsed = new Date(v);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const allowedLeadTypes = [
      canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
      canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
    ].filter(Boolean);

    const normalizeLeadDoc = (docSnap) => {
      const raw = docSnap.data() || {};
      const pipelineType = isGeneralExecutionEligibleDeal(raw)
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;
      const linkedQuotes = normalizeCrmLinkedQuotes({
        ...raw,
        linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
      });
      return normalizeCrmLeadSearchRecord({
        id: docSnap.id,
        ...raw,
        quoteType: raw.quoteType || pipelineType,
        pipelineType,
        contact: raw.contact || {},
        customerId: raw.customerId || "",
        customerName: raw.customerName || raw.contact?.name || raw.title || "",
        customerPhone:
          raw.customerPhone ||
          raw.contact?.phone ||
          raw.contact?.whatsapp ||
          "",
        linkedQuotes,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
        nextActionDate: normalizeTs(raw.nextActionDate),
        installationDate: normalizeTs(raw.installationDate),
        reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
        }),
        followUps: normalizeCrmFollowUps(raw.followUps, {
          nextAction: raw.nextAction,
          nextActionDate: normalizeTs(raw.nextActionDate),
          reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }),
        attachments: normalizeCrmAttachments(raw.attachments),
        executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
      });
    };

    const runCustomerLeadQuery = async () => {
      const leadsRef = collection(db, "users", workspaceUid, "crm_leads");
      const preferredQuery = firestoreQuery(
        leadsRef,
        firestoreWhere("customerId", "==", normalizedCustomerId),
        firestoreOrderBy("lastActivityAt", "desc"),
        firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
      );

      let snap;
      try {
        snap = await getDocsCached(preferredQuery, {
          label: "customerLedgerSelectedCustomerCrmDeals",
          cacheKey,
          ttlMs: 5 * 60 * 1000,
        });
      } catch (error) {
        if (String(error?.code || "") !== "failed-precondition") {
          throw error;
        }
        snap = await getDocsCached(
          firestoreQuery(
            leadsRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerCrmDealsNoOrderFallback",
            cacheKey: `${cacheKey}:noOrderFallback`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      let records = snap.docs.map(normalizeLeadDoc).filter((lead) =>
        allowedLeadTypes.includes(lead.pipelineType || lead.quoteType)
      );

      if (records.length === 0 && selectedCustomer?.customerKey) {
        const keySnap = await getDocsCached(
          firestoreQuery(
            leadsRef,
            firestoreWhere("customerKey", "==", selectedCustomer.customerKey),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerCrmDealsByCustomerKey",
            cacheKey: `${cacheKey}:customerKey`,
            ttlMs: 5 * 60 * 1000,
          }
        );
        records = keySnap.docs.map(normalizeLeadDoc).filter((lead) =>
          allowedLeadTypes.includes(lead.pipelineType || lead.quoteType)
        );
      }

      records.sort(
        (a, b) =>
          getCrmListSortTime(b) - getCrmListSortTime(a)
      );
      return records;
    };

    const promise = runCustomerLeadQuery()
      .then((records) => {
        customerLedgerCrmDealsLoadRef.current[cacheKey] = {
          loading: false,
          loaded: true,
          promise: null,
          records,
        };
        return records;
      })
      .catch((error) => {
        delete customerLedgerCrmDealsLoadRef.current[cacheKey];
        throw error;
      });

    customerLedgerCrmDealsLoadRef.current[cacheKey] = {
      loading: true,
      loaded: false,
      promise,
      records: [],
    };

    setCustomerLedgerCrmDealsLoadingId(normalizedCustomerId);
    try {
      const records = await promise;
      setCustomerLedgerCrmDealsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: records,
      }));
      mergeCrmLeadSearchRecords(records);
      return records;
    } catch (error) {
      devError("Customer ledger selected CRM deals load failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        customerId: normalizedCustomerId,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else {
        notify("Could not load CRM deals for this customer.", "warning");
      }
      return [];
    } finally {
      setCustomerLedgerCrmDealsLoadingId("");
    }
  },
  [
    canAccessCrm,
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    customerRecords,
    mergeCrmLeadSearchRecords,
    notify,
    normalizeCrmLeadSearchRecord,
    workspaceUid,
  ]
);

const loadCustomerLedgerProjects = useCallback(
  async (customerId) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId || !canAccessCrm) return [];

    const cacheKey = `customerLedger:projects:${workspaceUid}:${normalizedCustomerId}:lastActivityAt:limit${CUSTOMER_LEDGER_SELECTED_PROJECT_LIMIT}`;
    const loadState = customerLedgerProjectsLoadRef.current?.[cacheKey];

    if (loadState?.loaded && Array.isArray(loadState.records)) {
      setCustomerLedgerProjectsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: loadState.records,
      }));
      return loadState.records;
    }

    if (loadState?.loading && loadState.promise) {
      setCustomerLedgerProjectsLoadingId(normalizedCustomerId);
      try {
        const records = await loadState.promise;
        setCustomerLedgerProjectsByCustomer((prev) => ({
          ...prev,
          [normalizedCustomerId]: records,
        }));
        return records;
      } finally {
        setCustomerLedgerProjectsLoadingId("");
      }
    }

    const runCustomerProjectQuery = async () => {
      const projectsRef = collection(db, "users", workspaceUid, "projects");
      let snap;
      try {
        snap = await getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreOrderBy("lastActivityAt", "desc"),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_PROJECT_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerProjects",
            cacheKey,
            ttlMs: 5 * 60 * 1000,
          }
        );
      } catch (error) {
        if (String(error?.code || "") !== "failed-precondition") {
          throw error;
        }
        snap = await getDocsCached(
          firestoreQuery(
            projectsRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_PROJECT_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerProjectsUpdatedAtFallback",
            cacheKey: `${cacheKey}:updatedAtFallback`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }
      const records = snap.docs.map((docSnap) =>
        normalizeCrmProjectRecord(docSnap.id, docSnap.data() || {})
      );
      records.sort(
        (a, b) =>
          getRecordSortTime(b, ["lastActivityAt", "updatedAt", "createdAt"]) -
          getRecordSortTime(a, ["lastActivityAt", "updatedAt", "createdAt"])
      );
      return records;
    };

    const promise = runCustomerProjectQuery()
      .then((records) => {
        customerLedgerProjectsLoadRef.current[cacheKey] = {
          loading: false,
          loaded: true,
          promise: null,
          records,
        };
        return records;
      })
      .catch((error) => {
        delete customerLedgerProjectsLoadRef.current[cacheKey];
        throw error;
      });

    customerLedgerProjectsLoadRef.current[cacheKey] = {
      loading: true,
      loaded: false,
      promise,
      records: [],
    };

    setCustomerLedgerProjectsLoadingId(normalizedCustomerId);
    try {
      const records = await promise;
      setCustomerLedgerProjectsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: records,
      }));
      return records;
    } catch (error) {
      devError("Customer ledger selected projects load failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        customerId: normalizedCustomerId,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else {
        notify("Could not load projects for this customer.", "warning");
      }
      return [];
    } finally {
      setCustomerLedgerProjectsLoadingId("");
    }
  },
  [canAccessCrm, normalizeCrmProjectRecord, notify, workspaceUid]
);

const openCustomerLedgerQuoteLinkModal = useCallback(
  (customer = null) => {
    const targetCustomer = customer || selectedCustomerLedger;
    if (!targetCustomer?.id) {
      notify("Select a customer before linking a quotation.", "info");
      return;
    }

    const initialSearch =
      targetCustomer.phone ||
      targetCustomer.whatsapp ||
      targetCustomer.email ||
      targetCustomer.customerName ||
      "";
    setCustomerLedgerQuoteLinkState({
      open: true,
      customerId: targetCustomer.id,
      search: initialSearch,
      loading: false,
      error: "",
      results: [],
      linkingQuoteId: "",
    });
  },
  [notify, selectedCustomerLedger]
);

const closeCustomerLedgerQuoteLinkModal = useCallback(() => {
  setCustomerLedgerQuoteLinkState({
    open: false,
    customerId: "",
    search: "",
    loading: false,
    error: "",
    results: [],
    linkingQuoteId: "",
  });
  setCustomerLedgerQuoteLinkConfirm(null);
}, []);

const linkExistingQuoteToCustomerLedger = useCallback(
  async (quote = {}, options = {}) => {
    if (!workspaceUid || !quote?.id) return;
    const requestedCustomerId = String(
      customerLedgerQuoteLinkState.customerId || selectedCustomerLedger?.id || ""
    ).trim();
    const targetCustomer =
      customerRecords.find(
        (customer) =>
          String(customer.id || "") ===
          requestedCustomerId
      ) ||
      (String(selectedCustomerLedger?.id || "") === requestedCustomerId
        ? selectedCustomerLedger
        : null);

    if (!targetCustomer?.id) {
      notify("Could not find the selected customer ledger.", "warning");
      return;
    }

    const quoteId = String(
      quote.sourceQuoteId || quote.quoteId || quote.fullQuoteId || quote.id || ""
    ).trim();
    const quoteIndexId = String(
      quote.quoteIndexId || quote.indexId || quote.id || quoteId
    ).trim();
    if (!quoteId || !quoteIndexId) {
      notify("This quotation cannot be linked because its source ID is missing.", "warning");
      return;
    }

    const quoteNo = String(quote.quoteNo || quote.quoteNoString || quoteId || "").trim();
    const previousCustomer = {
      customerId: quote.customerId || quote.crm?.customerId || "",
      customerName: quote.customerName || quote.crm?.customerName || "",
    };
    const updateQuoteCustomer = options.updateQuoteCustomer !== false;
    const customerFields = {
      customerId: targetCustomer.id,
      customerKey: targetCustomer.customerKey || targetCustomer.id || "",
      customerName: updateQuoteCustomer
        ? targetCustomer.customerName || ""
        : quote.customerName || quote.crm?.customerName || targetCustomer.customerName || "",
      customerPhone: updateQuoteCustomer
        ? targetCustomer.phone || targetCustomer.whatsapp || ""
        : quote.customerPhone || quote.crm?.customerPhone || targetCustomer.phone || "",
      customerWhatsapp: updateQuoteCustomer
        ? targetCustomer.whatsapp || ""
        : quote.customerWhatsapp || quote.crm?.customerWhatsapp || targetCustomer.whatsapp || "",
      customerEmail: updateQuoteCustomer
        ? targetCustomer.email || ""
        : quote.customerEmail || quote.crm?.customerEmail || targetCustomer.email || "",
      customerAddress: updateQuoteCustomer
        ? targetCustomer.address || ""
        : quote.customerAddress || quote.crm?.customerAddress || targetCustomer.address || "",
      customerCompanyName: updateQuoteCustomer
        ? targetCustomer.companyName || ""
        : quote.customerCompanyName || quote.crm?.customerCompanyName || targetCustomer.companyName || "",
      customerLedgerLinked: true,
    };
    const customerSnapshot = {
      id: customerFields.customerId,
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      phone: customerFields.customerPhone,
      whatsapp: customerFields.customerWhatsapp,
      email: customerFields.customerEmail,
      address: customerFields.customerAddress,
      companyName: customerFields.customerCompanyName,
    };

    if (options.skipConfirm !== true) {
      setCustomerLedgerQuoteLinkConfirm({
        quote,
        targetCustomer,
        quoteId,
        quoteIndexId,
        quoteNo,
        previousCustomer,
        nextCustomerName: targetCustomer.customerName || "",
      });
      return;
    }

    setCustomerLedgerQuoteLinkState((prev) => ({
      ...prev,
      linkingQuoteId: quote.id,
      error: "",
    }));

    const trace = {
      quoteId,
      quoteIndexId,
      quoteNo,
      oldCustomerId: previousCustomer.customerId,
      oldCustomerName: previousCustomer.customerName,
      newCustomerId: customerFields.customerId,
      newCustomerName: customerFields.customerName,
      updateQuoteCustomer,
      fullQuoteUpdated: false,
      quoteIndexUpdated: false,
      dealUpdated: false,
      newCustomerSummaryUpdated: false,
      localStatePatched: false,
      errors: [],
    };

    try {
      const sourcePath =
        quote.sourcePath || `users/${workspaceUid}/quotations/${quoteId}`;
      const fullQuotePatch = sanitizeCrmFirestoreValue({
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerLedgerLinked: true,
        "generalQuote.customerId": customerFields.customerId,
        "generalQuote.customerKey": customerFields.customerKey,
        "generalQuote.customerLedgerLinked": true,
        "crm.customerId": customerFields.customerId,
        "crm.customerKey": customerFields.customerKey,
        ...(updateQuoteCustomer
          ? {
              customerSnapshot,
              customerName: customerFields.customerName,
              customerPhone: customerFields.customerPhone,
              customerWhatsapp: customerFields.customerWhatsapp,
              customerEmail: customerFields.customerEmail,
              customerAddress: customerFields.customerAddress,
              customerCompanyName: customerFields.customerCompanyName,
              "quoteFor.name": customerFields.customerName,
              "quoteFor.company":
                customerFields.customerCompanyName || customerFields.customerName,
              "quoteFor.phone": customerFields.customerPhone,
              "quoteFor.email": customerFields.customerEmail,
              "quoteFor.address": customerFields.customerAddress,
              "generalQuote.customerName": customerFields.customerName,
              "generalQuote.customerPhone": customerFields.customerPhone,
              "generalQuote.customerEmail": customerFields.customerEmail,
              "generalQuote.customerAddress": customerFields.customerAddress,
              "generalQuote.customerCompanyName": customerFields.customerCompanyName,
              "generalQuote.quoteFor.name": customerFields.customerName,
              "generalQuote.quoteFor.company":
                customerFields.customerCompanyName || customerFields.customerName,
              "generalQuote.quoteFor.phone": customerFields.customerPhone,
              "generalQuote.quoteFor.email": customerFields.customerEmail,
              "generalQuote.quoteFor.address": customerFields.customerAddress,
              "crm.customerName": customerFields.customerName,
              "crm.customerPhone": customerFields.customerPhone,
              "crm.customerWhatsapp": customerFields.customerWhatsapp,
              "crm.customerEmail": customerFields.customerEmail,
              "crm.customerAddress": customerFields.customerAddress,
              "crm.customerCompanyName": customerFields.customerCompanyName,
            }
          : {}),
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        updatedBy: user?.uid || user?.email || "",
      });
      await updateDoc(
        doc(db, "users", workspaceUid, "quotations", quoteId),
        fullQuotePatch
      );
      trace.fullQuoteUpdated = true;

      const quoteWithCustomer = {
        ...quote,
        ...customerFields,
        id: quoteId,
        quoteId,
        sourceQuoteId: quoteId,
        quoteIndexId,
        indexId: quoteIndexId,
        sourceCollection: "quotations",
        sourcePath,
        customerSnapshot,
      };
      const quoteIndexPayload = buildQuoteIndexSavePayload(
        quoteWithCustomer,
        quoteId,
        workspaceUid
      );
      await setDoc(
        doc(db, "users", workspaceUid, "quote_index", quoteIndexId),
        sanitizeCrmFirestoreValue(quoteIndexPayload),
        { merge: true }
      );
      trace.quoteIndexUpdated = true;

      await upsertCustomerSearchIndexRecord(
        {
          ...targetCustomer,
          id: targetCustomer.id,
          customerId: targetCustomer.id,
          customerKey: targetCustomer.customerKey || targetCustomer.id || "",
          customerName: targetCustomer.customerName || customerFields.customerName || "",
          phone: targetCustomer.phone || customerFields.customerPhone || "",
          whatsapp: targetCustomer.whatsapp || customerFields.customerWhatsapp || "",
          email: targetCustomer.email || customerFields.customerEmail || "",
          address: targetCustomer.address || customerFields.customerAddress || "",
          companyName: targetCustomer.companyName || customerFields.customerCompanyName || "",
          quoteId,
          latestQuoteId: quoteId,
          latestQuoteNo: quoteNo,
          latestQuoteTotal: Number(getNormalizedQuoteTotal(quote) || 0),
          sourceType: "quote_customer",
        },
        { sourceType: "quote_customer" }
      );
      trace.newCustomerSummaryUpdated = true;

      const dealResult = await ensureSavedQuoteCrmDeal(quoteWithCustomer, {
        quoteNo: quoteNo || quoteId,
        quoteType: resolveQuoteType(quoteWithCustomer),
        totalValue: Number(getNormalizedQuoteTotal(quoteWithCustomer) || 0),
        customerFields,
        autoCreateDeal: true,
        respectSaveQuoteOnly: false,
        showToast: false,
      });
      trace.dealUpdated = Boolean(dealResult?.leadId || dealResult?.crmPayload);

      const localLinkedAt = new Date();
      const localPatch = {
        ...customerFields,
        customerSnapshot,
        customerLedgerLinked: true,
        updatedAt: localLinkedAt,
        lastActivityAt: localLinkedAt,
        ...(dealResult?.leadId
          ? {
              crmLeadId: dealResult.leadId,
              leadId: dealResult.leadId,
              crm: {
                ...(quote.crm || {}),
                ...(dealResult.crmPayload || {}),
                leadId: dealResult.leadId,
              },
            }
          : {}),
      };
      applyQuoteLocalPatch(quoteId, localPatch, {
        persistIndex: false,
        sourceQuote: quoteWithCustomer,
      });
      setCustomerLedgerQuotesByCustomer((prev) => {
        const existing = prev[targetCustomer.id] || [];
        const nextQuote = normalizeQuoteIndexRecordForList(
          quoteIndexId,
          {
            ...quoteWithCustomer,
            ...localPatch,
            updatedAt: localLinkedAt,
            lastActivityAt: localLinkedAt,
          },
          (value) => {
            if (!value) return null;
            if (value?.toDate) return value.toDate();
            if (value instanceof Date) return value;
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }
        );
        const merged = new Map(
          existing.map((item) => [
            String(item.quoteIndexId || item.id || item.quoteId || ""),
            item,
          ])
        );
        merged.set(String(nextQuote.quoteIndexId || nextQuote.id || quoteIndexId), nextQuote);
        return {
          ...prev,
          [targetCustomer.id]: sortQuotesByLatestActivity(Array.from(merged.values())),
        };
      });
      trace.localStatePatched = true;

      invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      invalidateWorkspaceCollectionCache(workspaceUid, "customers");
      customerLedgerQuotesLoadRef.current = {};
      customerLedgerCrmDealsLoadRef.current = {};
      await Promise.all([
        loadCustomerLedgerQuotes(targetCustomer.id),
        loadCustomerLedgerCrmDeals(targetCustomer.id),
      ]);

      if (isDevRuntime) {
        devLog("QUOTE_CUSTOMER_REASSIGN_TRACE", trace);
      }
      closeCustomerLedgerQuoteLinkModal();
      setCustomerLedgerQuoteLinkConfirm(null);
      setCustomerLedgerDetailTab("quotations");
      notify("Quotation linked to customer ledger.", "success");
    } catch (error) {
      trace.errors.push({
        code: error?.code || "",
        message: error?.message || String(error),
      });
      if (isDevRuntime) {
        devError("QUOTE_CUSTOMER_REASSIGN_TRACE", trace);
      }
      setCustomerLedgerQuoteLinkState((prev) => ({
        ...prev,
        error: error?.message || "Could not link quotation.",
      }));
      notify(
        "Could not link quotation to this customer. Please try again.",
        "error"
      );
    } finally {
      setCustomerLedgerQuoteLinkState((prev) => ({
        ...prev,
        linkingQuoteId: "",
      }));
    }
  },
  [
    applyQuoteLocalPatch,
    closeCustomerLedgerQuoteLinkModal,
    customerLedgerQuoteLinkState.customerId,
    customerRecords,
    ensureSavedQuoteCrmDeal,
    getNormalizedQuoteTotal,
    loadCustomerLedgerCrmDeals,
    loadCustomerLedgerQuotes,
    notify,
    selectedCustomerLedger,
    upsertCustomerSearchIndexRecord,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const diagnoseQuoteLinks = useCallback(
  async (input = {}) => {
    if (!workspaceUid) {
      throw new Error("Workspace is not ready.");
    }
    const request =
      typeof input === "string"
        ? { quoteId: input, quoteNo: input }
        : input && typeof input === "object"
        ? input
        : {};
    const requestedQuoteId = String(request.quoteId || request.id || "").trim();
    const requestedQuoteNo = String(request.quoteNo || request.quoteNumber || "").trim();
    if (!requestedQuoteId && !requestedQuoteNo) {
      throw new Error("Pass { quoteId } or { quoteNo }.");
    }

    const normalizeDoc = (snap) =>
      snap?.exists?.()
        ? {
            id: snap.id,
            ...(snap.data() || {}),
          }
        : null;
    const firstNonEmpty = (...values) =>
      values.map((value) => String(value || "").trim()).find(Boolean) || "";
    const readDocSafe = async (pathSegments, label) => {
      try {
        return normalizeDoc(
          await getDoc(doc(db, ...pathSegments), {
            label,
            cacheKey: pathSegments.join("/"),
            ttlMs: 30 * 1000,
          })
        );
      } catch (error) {
        if (pathSegments[2] === 'quotations' && quoteFailureNotice(error)) throw error;
        return {
          id: pathSegments[pathSegments.length - 1],
          readError: {
            code: error?.code || "",
            message: error?.message || String(error),
          },
        };
      }
    };
    const querySafe = async (collectionName, fieldName, fieldValue, label, max = 5) => {
      const normalizedValue = String(fieldValue || "").trim();
      if (!normalizedValue) return [];
      try {
        if (collectionName === "quotations") {
          const matches = await getDocs(firestoreQuery(collection(db, "users", workspaceUid, "quote_index"),
            firestoreWhere(fieldName, "==", normalizedValue), firestoreLimit(max)), { label: `${label}.indexLookup` });
          const results = [];
          const seen = new Set();
          for (const indexDoc of matches.docs) {
            const index = indexDoc.data();
            const id = String(index.sourceQuoteId || index.quoteId || indexDoc.id);
            if (!id || id.includes('/') || seen.has(id)) continue;
            seen.add(id);
            const snap = await getDoc(doc(db, 'users', workspaceUid, 'quotations', id), { label: `${label}.canonical` });
            if (snap.exists()) results.push({ id: snap.id, ...snap.data() });
          }
          return results;
        }
        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, collectionName),
            firestoreWhere(fieldName, "==", normalizedValue),
            firestoreLimit(max)
          ),
          {
            label,
            cacheKey: `users/${workspaceUid}/${collectionName}?${fieldName}=${normalizedValue}&limit=${max}`,
            ttlMs: 30 * 1000,
          }
        );
        return snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() || {}),
        }));
      } catch (error) {
        if (collectionName === 'quotations' && quoteFailureNotice(error)) throw error;
        return [
          {
            id: `${collectionName}:${fieldName}`,
            queryError: {
              fieldName,
              value: normalizedValue,
              code: error?.code || "",
              message: error?.message || String(error),
            },
          },
        ];
      }
    };
    const dedupeById = (records = []) => {
      const map = new Map();
      records.forEach((record) => {
        const id = String(record?.id || "").trim();
        if (!id || map.has(id)) return;
        map.set(id, record);
      });
      return Array.from(map.values());
    };

    const fullQuoteCandidates = [];
    if (requestedQuoteId) {
      fullQuoteCandidates.push(
        await readDocSafe(
          ["users", workspaceUid, "quotations", requestedQuoteId],
          "diagnoseQuoteLinksFullQuoteById"
        )
      );
    }
    if (requestedQuoteNo) {
      fullQuoteCandidates.push(
        ...(await querySafe(
          "quotations",
          "quoteNo",
          requestedQuoteNo,
          "diagnoseQuoteLinksFullQuoteByQuoteNo",
          3
        ))
      );
    }
    const fullQuotes = dedupeById(
      fullQuoteCandidates.filter(Boolean).filter((item) => !item.readError && !item.queryError)
    );
    const fullQuote = fullQuotes[0] || null;

    const quoteIndexCandidates = [];
    const quoteIndexId = firstNonEmpty(requestedQuoteId, fullQuote?.quoteIndexId, fullQuote?.indexId);
    if (quoteIndexId) {
      quoteIndexCandidates.push(
        await readDocSafe(
          ["users", workspaceUid, "quote_index", quoteIndexId],
          "diagnoseQuoteLinksQuoteIndexById"
        )
      );
    }
    const resolvedQuoteId = firstNonEmpty(
      requestedQuoteId,
      fullQuote?.id,
      fullQuote?.quoteId,
      fullQuote?.sourceQuoteId
    );
    if (resolvedQuoteId) {
      quoteIndexCandidates.push(
        ...(await querySafe(
          "quote_index",
          "quoteId",
          resolvedQuoteId,
          "diagnoseQuoteLinksQuoteIndexByQuoteId",
          3
        )),
        ...(await querySafe(
          "quote_index",
          "sourceQuoteId",
          resolvedQuoteId,
          "diagnoseQuoteLinksQuoteIndexBySourceQuoteId",
          3
        ))
      );
    }
    const resolvedQuoteNo = firstNonEmpty(
      requestedQuoteNo,
      fullQuote?.quoteNo,
      fullQuote?.quoteNoString
    );
    if (resolvedQuoteNo) {
      quoteIndexCandidates.push(
        ...(await querySafe(
          "quote_index",
          "quoteNo",
          resolvedQuoteNo,
          "diagnoseQuoteLinksQuoteIndexByQuoteNo",
          3
        ))
      );
    }
    const quoteIndexes = dedupeById(
      quoteIndexCandidates.filter(Boolean).filter((item) => !item.readError && !item.queryError)
    );
    const quoteIndex = quoteIndexes[0] || null;
    const finalQuoteId = firstNonEmpty(
      resolvedQuoteId,
      quoteIndex?.sourceQuoteId,
      quoteIndex?.quoteId,
      quoteIndex?.id
    );
    const finalQuoteNo = firstNonEmpty(resolvedQuoteNo, quoteIndex?.quoteNo, quoteIndex?.quoteNoString);
    const explicitDealIds = dedupeById(
      [
        fullQuote?.opportunityId,
        fullQuote?.dealId,
        fullQuote?.crmLeadId,
        fullQuote?.leadId,
        fullQuote?.crm?.opportunityId,
        fullQuote?.crm?.dealId,
        fullQuote?.crm?.leadId,
        quoteIndex?.opportunityId,
        quoteIndex?.dealId,
        quoteIndex?.crmLeadId,
        quoteIndex?.leadId,
        finalQuoteId ? buildQuoteCreatedLeadId(finalQuoteId) : "",
      ]
        .map((id) => ({ id: String(id || "").trim() }))
        .filter((item) => item.id)
    ).map((item) => item.id);
    const dealCandidates = [];
    for (const dealId of explicitDealIds) {
      dealCandidates.push(
        await readDocSafe(
          ["users", workspaceUid, "crm_leads", dealId],
          "diagnoseQuoteLinksCrmDealByExplicitId"
        )
      );
    }
    if (finalQuoteId) {
      dealCandidates.push(
        ...(await querySafe("crm_leads", "quoteId", finalQuoteId, "diagnoseQuoteLinksDealByQuoteId")),
        ...(await querySafe(
          "crm_leads",
          "latestQuoteId",
          finalQuoteId,
          "diagnoseQuoteLinksDealByLatestQuoteId"
        )),
        ...(await querySafe(
          "crm_leads",
          "primaryQuoteId",
          finalQuoteId,
          "diagnoseQuoteLinksDealByPrimaryQuoteId"
        ))
      );
    }
    if (finalQuoteNo) {
      dealCandidates.push(
        ...(await querySafe("crm_leads", "quoteNo", finalQuoteNo, "diagnoseQuoteLinksDealByQuoteNo")),
        ...(await querySafe(
          "crm_leads",
          "latestQuoteNo",
          finalQuoteNo,
          "diagnoseQuoteLinksDealByLatestQuoteNo"
        )),
        ...(await querySafe(
          "crm_leads",
          "primaryQuoteNo",
          finalQuoteNo,
          "diagnoseQuoteLinksDealByPrimaryQuoteNo"
        ))
      );
    }
    const deals = dedupeById(
      dealCandidates.filter(Boolean).filter((item) => !item.readError && !item.queryError)
    );
    const linkedDealIds = deals.map((deal) => String(deal.id || "").trim()).filter(Boolean);
    const projectCandidates = [];
    if (finalQuoteId) {
      projectCandidates.push(
        ...(await querySafe("projects", "quoteId", finalQuoteId, "diagnoseQuoteLinksProjectByQuoteId")),
        ...(await querySafe(
          "projects",
          "sourceQuoteId",
          finalQuoteId,
          "diagnoseQuoteLinksProjectBySourceQuoteId"
        ))
      );
    }
    if (finalQuoteNo) {
      projectCandidates.push(
        ...(await querySafe("projects", "quoteNo", finalQuoteNo, "diagnoseQuoteLinksProjectByQuoteNo"))
      );
    }
    for (const dealId of linkedDealIds.slice(0, 3)) {
      projectCandidates.push(
        ...(await querySafe("projects", "opportunityId", dealId, "diagnoseQuoteLinksProjectByOpportunityId")),
        ...(await querySafe("projects", "dealId", dealId, "diagnoseQuoteLinksProjectByDealId"))
      );
    }
    const projects = dedupeById(
      projectCandidates.filter(Boolean).filter((item) => !item.readError && !item.queryError)
    );
    const customerId = firstNonEmpty(
      fullQuote?.customerId,
      fullQuote?.crm?.customerId,
      quoteIndex?.customerId,
      quoteIndex?.crm?.customerId
    );
    const linkedCustomer = customerId
      ? await readDocSafe(
          ["users", workspaceUid, "customers", customerId],
          "diagnoseQuoteLinksCustomerById"
        )
      : null;
    const customerSearchMatches = customerId
      ? await querySafe(
          "customer_search_index",
          "customerId",
          customerId,
          "diagnoseQuoteLinksCustomerSearchByCustomerId",
          3
        )
      : [];
    const issues = [];
    if (!fullQuote) issues.push("full_quote_missing");
    if (!quoteIndex) issues.push("quote_index_missing");
    if (fullQuote && !firstNonEmpty(fullQuote.customerId, fullQuote.crm?.customerId)) {
      issues.push("full_quote_missing_customerId");
    }
    if (quoteIndex && !firstNonEmpty(quoteIndex.customerId, quoteIndex.crm?.customerId)) {
      issues.push("quote_index_missing_customerId");
    }
    if (
      fullQuote &&
      quoteIndex &&
      firstNonEmpty(fullQuote.customerId, fullQuote.crm?.customerId) &&
      firstNonEmpty(quoteIndex.customerId, quoteIndex.crm?.customerId) &&
      firstNonEmpty(fullQuote.customerId, fullQuote.crm?.customerId) !==
        firstNonEmpty(quoteIndex.customerId, quoteIndex.crm?.customerId)
    ) {
      issues.push("quote_index_customer_mismatch");
    }
    if (customerId && (!linkedCustomer || linkedCustomer.readError)) {
      issues.push("quote_not_in_customer_ledger");
    }
    if (!deals.length) issues.push("no_deal_for_quote");
    if (deals.length > 1) issues.push("multiple_deals_for_quote");
    deals.forEach((deal) => {
      const dealCustomerId = firstNonEmpty(deal.customerId, deal.crm?.customerId);
      if (customerId && dealCustomerId && dealCustomerId !== customerId) {
        issues.push("deal_customer_mismatch");
      }
      if (!doesCrmLeadReferenceQuote(deal, finalQuoteId, finalQuoteNo)) {
        issues.push("deal_missing_quote_reference");
      }
      if (
        fullQuote &&
        firstNonEmpty(fullQuote.customerName, fullQuote.crm?.customerName) &&
        firstNonEmpty(deal.customerName, deal.contact?.name, deal.title) &&
        normalizeSearchText(firstNonEmpty(fullQuote.customerName, fullQuote.crm?.customerName)) !==
          normalizeSearchText(firstNonEmpty(deal.customerName, deal.contact?.name, deal.title))
      ) {
        issues.push("crm_deal_customer_mismatch");
      }
      if (
        fullQuote &&
        firstNonEmpty(fullQuote.quoteNo, fullQuote.quoteNoString) &&
        firstNonEmpty(deal.quoteNo, deal.latestQuoteNo, deal.primaryQuoteNo) &&
        firstNonEmpty(fullQuote.quoteNo, fullQuote.quoteNoString) !==
          firstNonEmpty(deal.quoteNo, deal.latestQuoteNo, deal.primaryQuoteNo)
      ) {
        issues.push("crm_deal_stale_quote_fields");
      }
    });
    if (projects.length > 1) issues.push("multiple_projects_for_quote");
    const wonishText = [
      fullQuote?.status,
      fullQuote?.workStage,
      fullQuote?.salesStatus,
      fullQuote?.crm?.status,
      quoteIndex?.status,
      quoteIndex?.workStage,
      quoteIndex?.salesStatus,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (
      !projects.length &&
      (wonishText.includes("won") ||
        wonishText.includes("confirm") ||
        wonishText.includes("order"))
    ) {
      issues.push("no_project_for_won_quote");
    }
    if (
      fullQuote &&
      quoteIndex &&
      firstNonEmpty(fullQuote.customerName, fullQuote.crm?.customerName) &&
      firstNonEmpty(quoteIndex.customerName, quoteIndex.crm?.customerName) &&
      normalizeSearchText(firstNonEmpty(fullQuote.customerName, fullQuote.crm?.customerName)) !==
        normalizeSearchText(firstNonEmpty(quoteIndex.customerName, quoteIndex.crm?.customerName))
    ) {
      issues.push("stale_customer_name");
    }
    if (quoteIndex && !quoteIndex.lastActivityAt) {
      issues.push("missing_lastActivityAt");
    }
    const quoteIndexConsistency = compareQuoteIndexWithFullQuote({
      fullQuote,
      quoteIndex,
      quoteIndexId: quoteIndex?.id || quoteIndexId || finalQuoteId,
      workspaceUidValue: workspaceUid,
    });
    issues.push(...(quoteIndexConsistency.issues || []));

    const crmDeals = deals.map((deal) => ({
      dealId: deal.id,
      customerId: firstNonEmpty(deal.customerId, deal.crm?.customerId),
      customerName: firstNonEmpty(deal.customerName, deal.contact?.name, deal.title),
      quoteId: firstNonEmpty(deal.quoteId, deal.latestQuoteId, getCrmLeadQuoteReferenceSummary(deal).primaryQuoteId),
      quoteNo: firstNonEmpty(deal.quoteNo, deal.latestQuoteNo, getCrmLeadQuoteReferenceSummary(deal).primaryQuoteNo),
      stage: firstNonEmpty(deal.stageId, deal.stage, deal.workStage),
    }));

    const result = {
      quoteId: finalQuoteId,
      quoteNo: finalQuoteNo,
      fullQuote: {
        exists: Boolean(fullQuote),
        path: finalQuoteId ? `users/${workspaceUid}/quotations/${finalQuoteId}` : "",
        customerId: firstNonEmpty(fullQuote?.customerId, fullQuote?.crm?.customerId),
        customerName: firstNonEmpty(fullQuote?.customerName, fullQuote?.crm?.customerName),
        customerPhone: firstNonEmpty(fullQuote?.customerPhone, fullQuote?.phone, fullQuote?.crm?.customerPhone),
        siteName: firstNonEmpty(fullQuote?.siteName, fullQuote?.projectName, fullQuote?.quoteSiteSelection?.siteName),
        opportunityId: firstNonEmpty(fullQuote?.opportunityId, fullQuote?.crm?.opportunityId),
        dealId: firstNonEmpty(fullQuote?.dealId, fullQuote?.crmLeadId, fullQuote?.leadId),
        projectId: firstNonEmpty(fullQuote?.projectId, fullQuote?.crm?.projectId),
      },
      quoteIndex: {
        exists: Boolean(quoteIndex),
        path: quoteIndex?.id ? `users/${workspaceUid}/quote_index/${quoteIndex.id}` : "",
        customerId: firstNonEmpty(quoteIndex?.customerId, quoteIndex?.crm?.customerId),
        customerName: firstNonEmpty(quoteIndex?.customerName, quoteIndex?.crm?.customerName),
        customerPhone: firstNonEmpty(quoteIndex?.customerPhone, quoteIndex?.phone, quoteIndex?.crm?.customerPhone),
        siteName: firstNonEmpty(quoteIndex?.siteName, quoteIndex?.projectName),
        quoteNo: firstNonEmpty(quoteIndex?.quoteNo, quoteIndex?.quoteNoString),
        quoteType: firstNonEmpty(quoteIndex?.quoteType, quoteIndex?.quotationType, quoteIndex?.normalizedQuoteType),
        grandTotal: quoteIndex?.grandTotal ?? quoteIndex?.total ?? quoteIndex?.totalAmount ?? null,
        status: firstNonEmpty(quoteIndex?.status, quoteIndex?.quoteStage, quoteIndex?.workflowStatus),
        workStage: firstNonEmpty(quoteIndex?.workStage, quoteIndex?.stageId),
        opportunityId: firstNonEmpty(quoteIndex?.opportunityId, quoteIndex?.crm?.opportunityId),
        dealId: firstNonEmpty(quoteIndex?.dealId, quoteIndex?.crmLeadId, quoteIndex?.leadId),
        projectId: firstNonEmpty(quoteIndex?.projectId, quoteIndex?.crm?.projectId),
        referralPartnerId: firstNonEmpty(quoteIndex?.referralPartnerId),
        referralPartnerName: firstNonEmpty(quoteIndex?.referralPartnerName, quoteIndex?.referralPartner?.displayName),
        lastActivityAt: quoteIndex?.lastActivityAt || null,
        updatedAt: quoteIndex?.updatedAt || null,
        createdAt: quoteIndex?.createdAt || null,
      },
      quoteIndexConsistency: {
        issues: quoteIndexConsistency.issues || [],
        risk: quoteIndexConsistency.risk || "",
        recommendedAction: quoteIndexConsistency.recommendedAction || "",
        expectedCardTitle: quoteIndexConsistency.expected
          ? buildInternalSavedQuoteCardTitle({
              customerName: quoteIndexConsistency.expected.customerName,
              siteName: quoteIndexConsistency.expected.siteName,
              quoteNo: quoteIndexConsistency.expected.quoteNo,
              contactPersonName: quoteIndexConsistency.expected.contactPersonName,
            })
          : "",
        currentCardTitle: quoteIndexConsistency.titleMeta?.renderedTitle || "",
      },
      customer: {
        matchedCustomerId: customerId,
        matchType: customerId ? "linked_customerId" : "",
        confidence: customerId && linkedCustomer && !linkedCustomer.readError ? 1 : 0,
        displayName:
          firstNonEmpty(
            linkedCustomer?.displayName,
            linkedCustomer?.customerName,
            linkedCustomer?.name,
            linkedCustomer?.companyName
          ) ||
          customerSearchMatches[0]?.displayName ||
          customerSearchMatches[0]?.customerName ||
          "",
      },
      crmDeals,
      deals: crmDeals,
      projects: projects.map((project) => ({
        projectId: project.id,
        quoteId: firstNonEmpty(project.quoteId, project.sourceQuoteId),
        quoteNo: firstNonEmpty(project.quoteNo, project.quoteNoString),
        projectStage: project.projectStage || "",
        orderStage: project.orderStage || "",
        stage: project.stage || "",
      })),
      customerSearchIndex: {
        matchedCustomerId: customerId,
        displayName: customerSearchMatches[0]?.displayName || customerSearchMatches[0]?.customerName || "",
      },
      issues: uniqueValues(issues),
    };
    if (isDevRuntime) {
      devLog("QUOTE_LINK_DIAGNOSTIC_RESULT", result);
    }
    return result;
  },
  [workspaceUid]
);

useEffect(() => {
  if (typeof window === "undefined" || (!isDevRuntime && !isSuperAdmin)) return undefined;
  window.diagnoseQuoteLinks = diagnoseQuoteLinks;
  return () => {
    if (window.diagnoseQuoteLinks === diagnoseQuoteLinks) {
      delete window.diagnoseQuoteLinks;
    }
  };
}, [diagnoseQuoteLinks, isSuperAdmin]);

const logCrmDataRepairAction = useCallback(
  async ({
    toolName = "",
    recordType = "",
    recordId = "",
    quoteId = "",
    quoteNo = "",
    before = {},
    after = {},
    dryRun = false,
    success = true,
    errorCode = "",
    errorMessage = "",
  } = {}) => {
    if (!workspaceUid || dryRun) return null;
    const actor = getCrmActorSnapshot();
    const repairId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const payload = sanitizeCrmFirestoreValue({
      repairId,
      workspaceUid,
      toolName,
      recordType,
      recordId,
      quoteId,
      quoteNo,
      before,
      after,
      dryRun: false,
      performedBy: {
        uid: actor.uid || user?.uid || "",
        name: actor.name || user?.displayName || user?.email || "",
        email: actor.email || user?.email || "",
      },
      performedByUid: actor.uid || user?.uid || "",
      performedByName: actor.name || user?.displayName || user?.email || "",
      performedAt: serverTimestamp(),
      success,
      errorCode,
      errorMessage,
    });
    await setDoc(doc(db, "users", workspaceUid, "repair_logs", repairId), payload);
    return repairId;
  },
  [getCrmActorSnapshot, user?.displayName, user?.email, user?.uid, workspaceUid]
);

const getCustomerRepairFields = useCallback((customer = {}) => {
  const customerId = String(customer.customerId || customer.id || customer.customerKey || "").trim();
  const customerName = String(
    customer.customerName ||
      customer.displayName ||
      customer.name ||
      customer.companyName ||
      customer.contactPersonName ||
      ""
  ).trim();
  const customerPhone = String(
    customer.phone ||
      customer.customerPhone ||
      customer.contactPersonPhone ||
      customer.whatsapp ||
      ""
  ).trim();
  const customerEmail = String(customer.email || customer.customerEmail || "").trim();
  return {
    customerId,
    customerKey: String(customer.customerKey || customerId).trim(),
    customerName,
    customerPhone,
    customerWhatsapp: String(customer.whatsapp || customerPhone || "").trim(),
    customerEmail,
    customerAddress: String(customer.address || customer.customerAddress || "").trim(),
    customerCompanyName: String(customer.companyName || "").trim(),
  };
}, []);

const findStrictCustomerMatchForRepair = useCallback(
  async (source = {}) => {
    if (!workspaceUid) return null;
    const quoteCustomerName = String(
      source.customerName ||
        source.customer?.name ||
        source.crm?.customerName ||
        source.quoteFor?.name ||
        ""
    ).trim();
    const quotePhone = String(
      source.customerPhone ||
        source.phone ||
        source.customerWhatsapp ||
        source.whatsapp ||
        source.crm?.customerPhone ||
        ""
    ).trim();
    const quoteEmail = String(
      source.customerEmail || source.email || source.crm?.customerEmail || ""
    ).trim();
    const normalizedPhone = normalizeCustomerSearchPhone(
      source.normalizedPhone || source.phoneNormalized || quotePhone
    );
    const emailCanonical = normalizeEmailValue(
      source.emailCanonical || source.normalizedEmail || quoteEmail
    );
    const normalizedName = normalizeCustomerSearchText(
      source.customerNameLower || source.nameLower || quoteCustomerName
    );

    const dedupeCandidates = (records = []) => {
      const byId = new Map();
      records.forEach((record) => {
        const customerId = String(
          record.customerId || record.customerKey || (record.__source === "customers" ? record.id : "")
        ).trim();
        if (!customerId || byId.has(customerId)) return;
        byId.set(customerId, { ...record, customerId });
      });
      return Array.from(byId.values());
    };

    const queryCustomers = async (fieldName, value, label) => {
      const cleanValue = String(value || "").trim();
      if (!cleanValue) return [];
      try {
        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "customers"),
            firestoreWhere(fieldName, "==", cleanValue),
            firestoreLimit(5)
          ),
          {
            label,
            cacheKey: `users/${workspaceUid}/customers?repair_${fieldName}=${cleanValue}&limit=5`,
            ttlMs: 30 * 1000,
          }
        );
        return snap.docs.map((docSnap) => ({
          id: docSnap.id,
          __source: "customers",
          ...(docSnap.data() || {}),
        }));
      } catch (error) {
        devWarn("CRM data repair customer match query failed", {
          fieldName,
          code: error?.code || "",
          message: error?.message || String(error),
        });
        return [];
      }
    };

    const queryCustomerSearchIndex = async (fieldName, value, label) => {
      const cleanValue = String(value || "").trim();
      if (!cleanValue) return [];
      try {
        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "customer_search_index"),
            firestoreWhere(fieldName, "==", cleanValue),
            firestoreLimit(5)
          ),
          {
            label,
            cacheKey: `users/${workspaceUid}/customer_search_index?repair_${fieldName}=${cleanValue}&limit=5`,
            ttlMs: 30 * 1000,
          }
        );
        return snap.docs.map((docSnap) => ({
          id: docSnap.id,
          __source: "customer_search_index",
          ...(docSnap.data() || {}),
        }));
      } catch (error) {
        devWarn("CRM data repair customer index match query failed", {
          fieldName,
          code: error?.code || "",
          message: error?.message || String(error),
        });
        return [];
      }
    };

    const matchAttempts = [
      {
        matchType: "exact_phone",
        value: normalizedPhone,
        safeToAutoLink: true,
        reason: "Phone number matches exactly.",
        queries: [
          ["normalizedPhone", normalizedPhone, "repairCustomerMatch.customers.normalizedPhone"],
          ["phoneNormalized", normalizedPhone, "repairCustomerMatch.customers.phoneNormalized"],
        ],
        indexQueries: [
          ["normalizedPhone", normalizedPhone, "repairCustomerMatch.index.normalizedPhone"],
          ["phoneNormalized", normalizedPhone, "repairCustomerMatch.index.phoneNormalized"],
        ],
      },
      {
        matchType: "exact_email",
        value: emailCanonical,
        safeToAutoLink: true,
        reason: "Email matches exactly.",
        queries: [
          ["emailCanonical", emailCanonical, "repairCustomerMatch.customers.emailCanonical"],
          ["normalizedEmail", emailCanonical, "repairCustomerMatch.customers.normalizedEmail"],
          ["emailLower", emailCanonical, "repairCustomerMatch.customers.emailLower"],
        ],
        indexQueries: [
          ["emailCanonical", emailCanonical, "repairCustomerMatch.index.emailCanonical"],
          ["normalizedEmail", emailCanonical, "repairCustomerMatch.index.normalizedEmail"],
          ["emailLower", emailCanonical, "repairCustomerMatch.index.emailLower"],
        ],
      },
      {
        matchType: "exact_name",
        value: normalizedName,
        safeToAutoLink: false,
        reason: "Customer name matches exactly. Review before linking.",
        queries: [
          ["customerNameLower", normalizedName, "repairCustomerMatch.customers.customerNameLower"],
          ["nameLower", normalizedName, "repairCustomerMatch.customers.nameLower"],
          ["displayNameLower", normalizedName, "repairCustomerMatch.customers.displayNameLower"],
        ],
        indexQueries: [
          ["normalizedName", normalizedName, "repairCustomerMatch.index.normalizedName"],
          ["nameLower", normalizedName, "repairCustomerMatch.index.nameLower"],
          ["displayNameLower", normalizedName, "repairCustomerMatch.index.displayNameLower"],
        ],
      },
    ];

    for (const attempt of matchAttempts) {
      if (!attempt.value) continue;
      const customerResults = (
        await Promise.all(
          attempt.queries.map(([fieldName, value, label]) =>
            queryCustomers(fieldName, value, label)
          )
        )
      ).flat();
      const indexResults = (
        await Promise.all(
          attempt.indexQueries.map(([fieldName, value, label]) =>
            queryCustomerSearchIndex(fieldName, value, label)
          )
        )
      ).flat();
      const candidates = dedupeCandidates([...customerResults, ...indexResults]);
      if (candidates.length === 1) {
        const candidate = candidates[0];
        let customerDoc = candidate;
        if (candidate.__source !== "customers") {
          const customerSnap = await getDoc(
            doc(db, "users", workspaceUid, "customers", candidate.customerId),
            {
              label: "repairCustomerMatch.fetchSuggestedCustomer",
              cacheKey: `users/${workspaceUid}/customers/${candidate.customerId}:repairSuggestion`,
              ttlMs: 30 * 1000,
            }
          ).catch(() => null);
          if (customerSnap?.exists?.()) {
            customerDoc = { id: customerSnap.id, __source: "customers", ...(customerSnap.data() || {}) };
          }
        }
        return {
          suggestedCustomerId: candidate.customerId,
          suggestedCustomerName:
            customerDoc.customerName ||
            customerDoc.displayName ||
            customerDoc.name ||
            candidate.displayName ||
            "",
          matchType: attempt.matchType,
          safeToAutoLink: attempt.safeToAutoLink,
          risk: attempt.safeToAutoLink ? "Safe" : "Needs Review",
          reason: attempt.reason,
          candidateCount: candidates.length,
        };
      }
      if (candidates.length > 1) {
        return {
          suggestedCustomerId: "",
          suggestedCustomerName: "",
          matchType: attempt.matchType,
          safeToAutoLink: false,
          risk: "Manual Only",
          reason: `Multiple exact ${attempt.matchType.replace("exact_", "")} matches found.`,
          candidateCount: candidates.length,
        };
      }
    }

    return {
      suggestedCustomerId: "",
      suggestedCustomerName: "",
      matchType: "",
      safeToAutoLink: false,
      risk: "Manual Only",
      reason: "No exact phone, email, or full-name customer match found.",
      candidateCount: 0,
    };
  },
  [workspaceUid]
);

const readCrmDataRepairLogCheck = useCallback(
  async (repairLogRef, expectedFields = []) => {
    if (!repairLogRef) return null;
    const snap = await getDoc(repairLogRef, {
      label: "crmDataRepair.readRepairLogCheck",
      cacheKey: `${repairLogRef.path}:repairLogCheck:${Date.now()}`,
      ttlMs: 0,
    }).catch((error) => ({
      exists: () => false,
      id: repairLogRef.id,
      data: () => ({
        readError: error?.message || String(error),
      }),
    }));
    const data = snap?.exists?.() ? snap.data() || {} : {};
    if (!snap?.exists?.() || expectedFields.some((field) => data[field] === undefined)) {
      notify(REPAIR_VERIFY_WARNING, "warning");
      throw new Error(REPAIR_VERIFY_WARNING);
    }
    if (data.success === true) {
      try {
        await verifyRepairTargets(data, async (collectionName, recordId) => {
          const target = await getDoc(doc(db, "users", workspaceUid, collectionName, recordId), {
            label: "crmDataRepair.verifyRepairedDocument",
            ttlMs: 0,
          });
          return target.exists() ? target.data() : null;
        });
      } catch (error) {
        notify(REPAIR_VERIFY_WARNING, "warning");
        throw new Error(REPAIR_VERIFY_WARNING);
      }
    }
    return {
      repairId: repairLogRef.id,
      exists: Boolean(snap?.exists?.()),
      toolName: data.toolName || "",
      recordId: data.recordId || "",
      quoteId: data.quoteId || "",
      quoteNo: data.quoteNo || "",
      success: data.success === true,
      performedAt: data.performedAt || null,
      verified: data.success === true,
      hasBefore: Boolean(data.before && Object.keys(data.before || {}).length),
      hasAfter: Boolean(data.after && Object.keys(data.after || {}).length),
      hasPerformedBy: Boolean(data.performedBy || data.performedByUid || data.performedByName),
      hasPerformedAt: Boolean(data.performedAt),
      missingFields: expectedFields.filter((fieldName) => {
        if (fieldName === "before") return !data.before;
        if (fieldName === "after") return !data.after;
        if (fieldName === "performedBy") return !data.performedBy && !data.performedByUid && !data.performedByName;
        return typeof data[fieldName] === "undefined" || data[fieldName] === "";
      }),
      readError: data.readError || "",
    };
  },
  [notify, workspaceUid]
);

const createCrmDataRepairTestData = useCallback(async () => {
  if (!workspaceUid || !canUseCrmRepairTestDataTools) {
    notify("Repair test fixtures require superadmin access or repair test mode.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({
    ...prev,
    testDataBusy: true,
    testDataMessage: "",
    testDataError: "",
    testDataVerification: [],
  }));
  try {
    const actor = getCrmActorSnapshot();
    const createdAt = Timestamp.fromDate(new Date("2026-01-10T04:30:00.000Z"));
    const updatedAt = Timestamp.fromDate(new Date("2026-01-15T04:30:00.000Z"));
    const customerNameLower = normalizeCustomerSearchText(CRM_DATA_REPAIR_TEST_CUSTOMER.customerName);
    const normalizedPhone = normalizeCustomerSearchPhone(CRM_DATA_REPAIR_TEST_CUSTOMER.phone);
    const emailCanonical = normalizeEmailValue(CRM_DATA_REPAIR_TEST_CUSTOMER.email);
    const searchTokens = uniqueValues(
      [
        ...customerNameLower.split(/\s+/),
        normalizedPhone,
        emailCanonical,
        "repair",
        "test",
        "data",
        "test-repair",
      ].filter(Boolean)
    );
    const customerPayload = sanitizeCrmFirestoreValue({
      ...CRM_DATA_REPAIR_TEST_CUSTOMER,
      workspaceUid,
      active: true,
      deleted: false,
      archived: false,
      nameLower: customerNameLower,
      customerNameLower,
      displayNameLower: customerNameLower,
      normalizedName: customerNameLower,
      normalizedPhone,
      phoneNormalized: normalizedPhone,
      emailCanonical,
      normalizedEmail: emailCanonical,
      emailLower: emailCanonical,
      searchTokens,
      searchKeywords: searchTokens,
      sourceTypes: ["customer", "quote_customer"],
      createdAt,
      updatedAt,
      lastActivityAt: updatedAt,
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });
    const customerSearchWrite = buildCustomerSearchIndexWrite(customerPayload, {
      workspaceUid,
      sourceType: "customer",
    });
    if (!customerSearchWrite?.personId) {
      throw new Error("Could not build repair test customer search index.");
    }

    const buildTestQuoteIndex = ({
      quoteId,
      quoteNo,
      crmLeadId = "",
      includeCustomerId = false,
    }) =>
      sanitizeCrmFirestoreValue({
        quoteId,
        sourceQuoteId: quoteId,
        quoteIndexId: quoteId,
        quoteNo,
        quoteNoString: quoteNo,
        quoteDisplayTitle: `${quoteNo} - TEST DATA - SAFE TO DELETE`,
        customerName: CRM_DATA_REPAIR_TEST_CUSTOMER.customerName,
        customerPhone: CRM_DATA_REPAIR_TEST_CUSTOMER.phone,
        customerWhatsapp: CRM_DATA_REPAIR_TEST_CUSTOMER.phone,
        customerEmail: CRM_DATA_REPAIR_TEST_CUSTOMER.email,
        ...(includeCustomerId
          ? {
              customerId: CRM_DATA_REPAIR_TEST_CUSTOMER_ID,
              customerKey: CRM_DATA_REPAIR_TEST_CUSTOMER_ID,
            }
          : {}),
        ...(crmLeadId ? { crmLeadId, leadId: crmLeadId, opportunityId: crmLeadId } : {}),
        workspaceUid,
        quoteType: "curtain",
        quotationType: "curtain",
        status: "repair_test_data",
        workStage: "repair_test_data",
        quoteCategory: "repair_test_data",
        category: "repair_test_data",
        projectName: "Repair Test Site - TEST DATA - SAFE TO DELETE",
        siteName: "Repair Test Site - TEST DATA - SAFE TO DELETE",
        sourcePath: `users/${workspaceUid}/quotations/${quoteId}`,
        totalAmount: 0,
        grandTotal: 0,
        finalTotal: 0,
        taxableAmount: 0,
        gstAmount: 0,
        deleted: false,
        archived: false,
        binStatus: "active",
        visibilityStatus: "active",
        repair_test_data: true,
        testDataLabel: "TEST DATA - SAFE TO DELETE",
        tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"],
        customerNameLower,
        normalizedPhone,
        emailCanonical,
        normalizedEmail: emailCanonical,
        searchableText: normalizeSearchText(
          `${quoteNo} ${CRM_DATA_REPAIR_TEST_CUSTOMER.customerName} ${CRM_DATA_REPAIR_TEST_CUSTOMER.phone} ${CRM_DATA_REPAIR_TEST_CUSTOMER.email} repair test data`
        ),
        searchText: normalizeSearchText(
          `${quoteNo} ${CRM_DATA_REPAIR_TEST_CUSTOMER.customerName} ${CRM_DATA_REPAIR_TEST_CUSTOMER.phone} ${CRM_DATA_REPAIR_TEST_CUSTOMER.email} repair test data`
        ),
        searchTokens,
        searchKeywords: searchTokens,
        createdAt,
        updatedAt,
        lastEditedAt: updatedAt,
        createdBy: actor.uid || user?.uid || "",
        updatedBy: actor.uid || user?.uid || "",
      });

    const testFullQuotePayload = sanitizeCrmFirestoreValue({
      quoteId: CRM_DATA_REPAIR_TEST_QUOTE_2_ID,
      id: CRM_DATA_REPAIR_TEST_QUOTE_2_ID,
      quoteNo: "TEST-REPAIR-002",
      quoteNoString: "TEST-REPAIR-002",
      customerName: "Unlinked Test Snapshot - TEST DATA - SAFE TO DELETE",
      customerPhone: CRM_DATA_REPAIR_TEST_CUSTOMER.phone,
      customerEmail: CRM_DATA_REPAIR_TEST_CUSTOMER.email,
      workspaceUid,
      quoteType: "curtain",
      quotationType: "curtain",
      status: "repair_test_data",
      workStage: "repair_test_data",
      contentStorageMode: "inline",
      rows: [],
      measurements: [],
      items: [],
      lineItems: [],
      quoteItems: [],
      totalAmount: 0,
      grandTotal: 0,
      finalTotal: 0,
      taxableAmount: 0,
      gstAmount: 0,
      repair_test_data: true,
      testDataLabel: "TEST DATA - SAFE TO DELETE",
      tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"],
      createdAt,
      updatedAt,
      lastEditedAt: updatedAt,
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });

    const buildTestDeal = ({ dealId, quoteId, quoteNo }) =>
      sanitizeCrmFirestoreValue({
        id: dealId,
        leadId: dealId,
        opportunityId: dealId,
        workspaceUid,
        title: `${quoteNo} repair test deal - TEST DATA - SAFE TO DELETE`,
        customerName: "Unlinked Test Deal - TEST DATA - SAFE TO DELETE",
        phone: CRM_DATA_REPAIR_TEST_CUSTOMER.phone,
        customerPhone: CRM_DATA_REPAIR_TEST_CUSTOMER.phone,
        email: CRM_DATA_REPAIR_TEST_CUSTOMER.email,
        customerEmail: CRM_DATA_REPAIR_TEST_CUSTOMER.email,
        quoteId,
        latestQuoteId: quoteId,
        primaryQuoteId: quoteId,
        quoteNo,
        latestQuoteNo: quoteNo,
        primaryQuoteNo: quoteNo,
        quoteType: "curtain",
        quotationType: "curtain",
        stageId: "new_lead",
        stage: "new_lead",
        status: CRM_OPPORTUNITY_STATUS_OPEN,
        salesStatus: CRM_OPPORTUNITY_STATUS_OPEN,
        repair_test_data: true,
        testDataLabel: "TEST DATA - SAFE TO DELETE",
        tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"],
        createdAt,
        updatedAt,
        lastActivityAt: updatedAt,
        createdBy: actor.uid || user?.uid || "",
        updatedBy: actor.uid || user?.uid || "",
      });

    const batch = createRepairFixtureBatch({ db, runTransaction, onRead: recordFirestoreRead });
    batch.set(
      doc(db, "users", workspaceUid, "customers", CRM_DATA_REPAIR_TEST_CUSTOMER_ID),
      customerPayload,
      { merge: true }
    );
    batch.set(
      doc(db, "users", workspaceUid, "customer_search_index", customerSearchWrite.personId),
      sanitizeCrmFirestoreValue({
        ...customerSearchWrite.payload,
        repair_test_data: true,
        testDataLabel: "TEST DATA - SAFE TO DELETE",
        createdAt,
      }),
      { merge: true }
    );
    batch.set(
      doc(db, "users", workspaceUid, "quote_index", CRM_DATA_REPAIR_TEST_QUOTE_1_ID),
      buildTestQuoteIndex({
        quoteId: CRM_DATA_REPAIR_TEST_QUOTE_1_ID,
        quoteNo: "TEST-REPAIR-001",
      })
    );
    batch.set(
      doc(db, "users", workspaceUid, "quote_index", CRM_DATA_REPAIR_TEST_QUOTE_2_ID),
      buildTestQuoteIndex({
        quoteId: CRM_DATA_REPAIR_TEST_QUOTE_2_ID,
        quoteNo: "TEST-REPAIR-002",
        crmLeadId: CRM_DATA_REPAIR_TEST_DEAL_2_ID,
      })
    );
    batch.set(
      doc(db, "users", workspaceUid, "quotations", CRM_DATA_REPAIR_TEST_QUOTE_2_ID),
      testFullQuotePayload
    );
    batch.set(
      doc(db, "users", workspaceUid, "crm_leads", CRM_DATA_REPAIR_TEST_DEAL_2_ID),
      buildTestDeal({
        dealId: CRM_DATA_REPAIR_TEST_DEAL_2_ID,
        quoteId: CRM_DATA_REPAIR_TEST_QUOTE_2_ID,
        quoteNo: "TEST-REPAIR-002",
      })
    );
    batch.set(
      doc(db, "users", workspaceUid, "quote_index", CRM_DATA_REPAIR_TEST_ACTIVITY_ID),
      buildTestQuoteIndex({
        quoteId: CRM_DATA_REPAIR_TEST_ACTIVITY_ID,
        quoteNo: "TEST-REPAIR-ACTIVITY",
        crmLeadId: CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID,
        includeCustomerId: true,
      })
    );
    batch.set(
      doc(db, "users", workspaceUid, "crm_leads", CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID),
      buildTestDeal({
        dealId: CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID,
        quoteId: CRM_DATA_REPAIR_TEST_ACTIVITY_ID,
        quoteNo: "TEST-REPAIR-ACTIVITY",
      })
    );
    await batch.commit();
    ["customers", "customer_search_index", "quote_index", "quotations", "crm_leads"].forEach(
      (collectionName) => invalidateWorkspaceCollectionCache(workspaceUid, collectionName)
    );
    customerLedgerQuotesLoadRef.current = {};
    customerLedgerCrmDealsLoadRef.current = {};
    setCrmDataRepairState((prev) => ({
      ...prev,
      testDataBusy: false,
      testDataMessage:
        "Repair test data created. Use TEST-REPAIR-001 for link_only and TEST-REPAIR-002 for link_and_update_quote_customer.",
      testDataError: "",
      testDataRecords: [
        `customers/${CRM_DATA_REPAIR_TEST_CUSTOMER_ID}`,
        `customer_search_index/${customerSearchWrite.personId}`,
        `quote_index/${CRM_DATA_REPAIR_TEST_QUOTE_1_ID}`,
        `quote_index/${CRM_DATA_REPAIR_TEST_QUOTE_2_ID}`,
        `quotations/${CRM_DATA_REPAIR_TEST_QUOTE_2_ID}`,
        `crm_leads/${CRM_DATA_REPAIR_TEST_DEAL_2_ID}`,
        `quote_index/${CRM_DATA_REPAIR_TEST_ACTIVITY_ID}`,
        `crm_leads/${CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID}`,
      ],
      testDataVerification: [],
      quoteLookup: "TEST-REPAIR-001",
      quoteDiagnostic: null,
      unlinkedResults: [],
      unlinkedCursor: null,
      unlinkedHasMore: false,
      activityResults: [],
      activityCursor: null,
      activityHasMore: false,
      activitySelected: {},
    }));
    notify("Repair test data created.", "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({
      ...prev,
      testDataBusy: false,
      testDataError: error?.message || "Could not create repair test data.",
    }));
    devError("CRM_DATA_REPAIR_CREATE_TEST_DATA_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    notify(error?.message || "Could not create repair test data.", "error");
  }
}, [
  canUseCrmRepairTestDataTools,
  getCrmActorSnapshot,
  notify,
  user?.uid,
  workspaceUid,
]);

const deleteCrmDataRepairTestData = useCallback(async () => {
  if (!workspaceUid || !canUseCrmRepairTestDataTools) {
    notify("Repair test fixture cleanup requires superadmin access or repair test mode.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({
    ...prev,
    testDataBusy: true,
    testDataMessage: "",
    testDataError: "",
    testDataVerification: [],
  }));
  try {
    const candidateRefs = [
      ["customers", CRM_DATA_REPAIR_TEST_CUSTOMER_ID],
      ["quote_index", CRM_DATA_REPAIR_TEST_QUOTE_1_ID],
      ["quote_index", CRM_DATA_REPAIR_TEST_QUOTE_2_ID],
      ["quotations", CRM_DATA_REPAIR_TEST_QUOTE_2_ID],
      ["crm_leads", CRM_DATA_REPAIR_TEST_DEAL_2_ID],
      ["quote_index", CRM_DATA_REPAIR_TEST_ACTIVITY_ID],
      ["crm_leads", CRM_DATA_REPAIR_TEST_ACTIVITY_DEAL_ID],
    ];
    const batch = writeBatch(db);
    const deletedRecords = [];
    const verification = [];
    for (const [collectionName, recordId] of candidateRefs) {
      if (!recordId) continue;
      const recordRef = doc(db, "users", workspaceUid, collectionName, recordId);
      const snap = await getDoc(recordRef, {
        label: "crmDataRepair.cleanupTestData.checkRecord",
        cacheKey: `users/${workspaceUid}/${collectionName}/${recordId}:repairTestCleanup`,
        ttlMs: 0,
      });
      if (snap?.exists?.() && snap.data()?.repair_test_data === true) {
        batch.delete(recordRef);
        deletedRecords.push(`${collectionName}/${recordId}`);
        verification.push({
          path: `${collectionName}/${recordId}`,
          existed: true,
          tagged: true,
          deleteQueued: true,
        });
      } else {
        verification.push({
          path: `${collectionName}/${recordId}`,
          existed: Boolean(snap?.exists?.()),
          tagged: snap?.exists?.() ? snap.data()?.repair_test_data === true : false,
          deleteQueued: false,
        });
      }
    }
    await batch.commit();
    const postDeleteVerification = [];
    for (const [collectionName, recordId] of candidateRefs) {
      if (!recordId) continue;
      const recordRef = doc(db, "users", workspaceUid, collectionName, recordId);
      const snap = await getDoc(recordRef, {
        label: "crmDataRepair.cleanupTestData.verifyDeleted",
        cacheKey: `users/${workspaceUid}/${collectionName}/${recordId}:repairTestCleanupVerify:${Date.now()}`,
        ttlMs: 0,
      });
      const before = verification.find((item) => item.path === `${collectionName}/${recordId}`) || {};
      postDeleteVerification.push({
        ...before,
        path: `${collectionName}/${recordId}`,
        gone: !snap?.exists?.(),
      });
    }
    const repairLogSnap = await getDocs(
      firestoreQuery(collection(db, "users", workspaceUid, "repair_logs"), firestoreLimit(1)),
      {
        label: "crmDataRepair.cleanupTestData.verifyRepairLogsRemain",
        cacheKey: `users/${workspaceUid}/repair_logs?cleanupVerify=1&at=${Date.now()}`,
        ttlMs: 0,
      }
    ).catch(() => null);
    ["customers", "customer_search_index", "quote_index", "quotations", "crm_leads"].forEach(
      (collectionName) => invalidateWorkspaceCollectionCache(workspaceUid, collectionName)
    );
    customerLedgerQuotesLoadRef.current = {};
    customerLedgerCrmDealsLoadRef.current = {};
    setCrmDataRepairState((prev) => ({
      ...prev,
      testDataBusy: false,
      testDataMessage: deletedRecords.length
        ? `Deleted ${deletedRecords.length} approved repair test record${deletedRecords.length === 1 ? "" : "s"}. Verified tagged before deletion. Repair logs remain immutable${repairLogSnap?.size ? " and present" : ""}.`
        : `No tagged approved repair test records found to delete. Repair logs remain immutable${repairLogSnap?.size ? " and present" : ""}.`,
      testDataError: "",
      testDataRecords: deletedRecords,
      testDataVerification: postDeleteVerification,
      quoteDiagnostic: null,
      unlinkedResults: [],
      unlinkedCursor: null,
      unlinkedHasMore: false,
      activityResults: [],
      activityCursor: null,
      activityHasMore: false,
      activitySelected: {},
    }));
    notify("Repair test data cleanup finished.", "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({
      ...prev,
      testDataBusy: false,
      testDataError: error?.message || "Could not delete repair test data.",
    }));
    devError("CRM_DATA_REPAIR_DELETE_TEST_DATA_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    notify(error?.message || "Could not delete repair test data.", "error");
  }
}, [canUseCrmRepairTestDataTools, notify, workspaceUid]);

const createQuoteIndexRepairTestData = useCallback(async () => {
  if (!workspaceUid || !canUseCrmRepairTestDataTools) {
    notify("Quote index repair test fixtures require superadmin access or repair test mode.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({
    ...prev,
    quoteIndexTestDataBusy: true,
    quoteIndexTestDataMessage: "",
    quoteIndexTestDataError: "",
    quoteIndexTestDataVerification: [],
  }));
  try {
    const actor = getCrmActorSnapshot();
    const createdAt = Timestamp.fromDate(new Date("2026-02-01T04:30:00.000Z"));
    const updatedAt = Timestamp.fromDate(new Date("2026-02-05T04:30:00.000Z"));
    const customerPayload = sanitizeCrmFirestoreValue({
      customerId: CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID,
      customerKey: CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID,
      customerName: "Quote Index Repair Customer",
      displayName: "Quote Index Repair Customer",
      name: "Quote Index Repair Customer",
      phone: "9999990003",
      customerPhone: "9999990003",
      email: "quote-index-repair@example.com",
      customerEmail: "quote-index-repair@example.com",
      nameLower: normalizeCustomerSearchText("Quote Index Repair Customer"),
      customerNameLower: normalizeCustomerSearchText("Quote Index Repair Customer"),
      normalizedPhone: normalizeCustomerSearchPhone("9999990003"),
      phoneNormalized: normalizeCustomerSearchPhone("9999990003"),
      emailCanonical: normalizeEmailValue("quote-index-repair@example.com"),
      active: true,
      deleted: false,
      archived: false,
      repair_test_data: true,
      tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"],
      workspaceUid,
      createdAt,
      updatedAt,
      lastActivityAt: updatedAt,
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });
    const fullQuotePayload = sanitizeCrmFirestoreValue({
      quoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      sourceQuoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      quoteNo: "TEST-QI-001",
      quoteNoString: "TEST-QI-001",
      quoteType: CURTAIN_QUOTE_TYPE,
      quotationType: CURTAIN_QUOTE_TYPE,
      customerId: CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID,
      customerName: "Quote Index Repair Customer",
      customerPhone: "9999990003",
      customerEmail: "quote-index-repair@example.com",
      siteName: "Correct Site",
      projectName: "Correct Site",
      contactPersonName: "Repair Contact",
      subtotal: 12345,
      taxableAmount: 12345,
      grandTotal: 12345,
      total: 12345,
      finalTotal: 12345,
      totalAmount: 12345,
      status: "draft",
      workStage: "quote_given",
      opportunityId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      crmLeadId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      referralPartnerId: "repair_referral_partner",
      referralPartnerName: "Repair Test Designer",
      repair_test_data: true,
      testDataLabel: "TEST DATA - SAFE TO DELETE",
      workspaceUid,
      createdAt,
      updatedAt,
      lastActivityAt: updatedAt,
      rows: [],
      effectiveRows: [],
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });
    const badQuoteIndexPayload = sanitizeCrmFirestoreValue({
      quoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      sourceQuoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      quoteIndexId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      indexId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      sourceCollection: "quotations",
      sourcePath: `users/${workspaceUid}/quotations/${CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID}`,
      quoteNo: "TEST-QI-001",
      quoteNoString: "TEST-QI-001",
      quoteType: CURTAIN_QUOTE_TYPE,
      quotationType: CURTAIN_QUOTE_TYPE,
      customerId: "stale_customer_id",
      customerName: "Wrong Customer Name",
      customerPhone: "9999990003",
      siteName: "Wrong Site",
      projectName: "Wrong Site",
      contactPersonName: "Wrong Contact",
      grandTotal: 999,
      total: 999,
      finalTotal: 999,
      totalAmount: 999,
      status: "draft",
      workStage: "wrong_stage",
      cardTitle: "Wrong Contact - Wrong Site",
      displayTitle: "Wrong Contact - Wrong Site",
      opportunityId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      crmLeadId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      repair_test_data: true,
      testDataLabel: "TEST DATA - SAFE TO DELETE",
      deleted: false,
      archived: false,
      workspaceUid,
      createdAt,
      updatedAt: createdAt,
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });
    const staleDealPayload = sanitizeCrmFirestoreValue({
      leadId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      opportunityId: CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID,
      quoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      latestQuoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      primaryQuoteId: CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID,
      quoteNo: "TEST-QI-001",
      latestQuoteNo: "TEST-QI-001",
      primaryQuoteNo: "TEST-QI-001",
      quoteType: CURTAIN_QUOTE_TYPE,
      quotationType: CURTAIN_QUOTE_TYPE,
      customerId: "stale_customer_id",
      customerName: "Wrong Customer Name",
      siteName: "Wrong Site",
      quoteTotal: 999,
      grandTotal: 999,
      status: CRM_OPPORTUNITY_STATUS_OPEN,
      salesStatus: CRM_OPPORTUNITY_STATUS_OPEN,
      repair_test_data: true,
      testDataLabel: "TEST DATA - SAFE TO DELETE",
      workspaceUid,
      createdAt,
      updatedAt,
      lastActivityAt: updatedAt,
      createdBy: actor.uid || user?.uid || "",
      updatedBy: actor.uid || user?.uid || "",
    });
    const batch = createRepairFixtureBatch({ db, runTransaction, onRead: recordFirestoreRead });
    batch.set(doc(db, "users", workspaceUid, "customers", CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID), customerPayload);
    batch.set(doc(db, "users", workspaceUid, "quotations", CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID), fullQuotePayload, { merge: true });
    batch.set(doc(db, "users", workspaceUid, "quote_index", CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID), badQuoteIndexPayload, { merge: true });
    batch.set(doc(db, "users", workspaceUid, "crm_leads", CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID), staleDealPayload, { merge: true });
    await batch.commit();
    ["customers", "quote_index", "quotations", "crm_leads"].forEach((collectionName) =>
      invalidateWorkspaceCollectionCache(workspaceUid, collectionName)
    );
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteIndexTestDataBusy: false,
      quoteIndexTestDataMessage: "Quote index repair test data created. Use TEST-QI-001.",
      quoteIndexTestDataError: "",
      quoteIndexTestDataRecords: [
        `customers/${CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID}`,
        `quotations/${CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID}`,
        `quote_index/${CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID}`,
        `crm_leads/${CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID}`,
      ],
      quoteIndexTestDataVerification: [],
      quoteLookup: "TEST-QI-001",
      quoteDiagnostic: null,
      quoteIndexMismatchResults: [],
      quoteIndexMismatchCursor: null,
      quoteIndexMismatchHasMore: false,
    }));
    notify("Quote index repair test data created.", "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteIndexTestDataBusy: false,
      quoteIndexTestDataError: error?.message || "Could not create quote index repair test data.",
    }));
    devError("CRM_QUOTE_INDEX_REPAIR_CREATE_TEST_DATA_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    notify(error?.message || "Could not create quote index repair test data.", "error");
  }
}, [
  canUseCrmRepairTestDataTools,
  getCrmActorSnapshot,
  notify,
  user?.uid,
  workspaceUid,
]);

const deleteQuoteIndexRepairTestData = useCallback(async () => {
  if (!workspaceUid || !canUseCrmRepairTestDataTools) {
    notify("Quote index repair fixture cleanup requires superadmin access or repair test mode.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({
    ...prev,
    quoteIndexTestDataBusy: true,
    quoteIndexTestDataMessage: "",
    quoteIndexTestDataError: "",
    quoteIndexTestDataVerification: [],
  }));
  try {
    const candidateRefs = [
      ["customers", CRM_QUOTE_INDEX_REPAIR_TEST_CUSTOMER_ID],
      ["quotations", CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID],
      ["quote_index", CRM_QUOTE_INDEX_REPAIR_TEST_QUOTE_ID],
      ["crm_leads", CRM_QUOTE_INDEX_REPAIR_TEST_DEAL_ID],
    ];
    const batch = writeBatch(db);
    const deletedRecords = [];
    const verification = [];
    for (const [collectionName, recordId] of candidateRefs) {
      const recordRef = doc(db, "users", workspaceUid, collectionName, recordId);
      const snap = await getDoc(recordRef, {
        label: "crmDataRepair.quoteIndexCleanup.checkRecord",
        cacheKey: `users/${workspaceUid}/${collectionName}/${recordId}:quoteIndexRepairCleanup`,
        ttlMs: 0,
      });
      const tagged = snap?.exists?.() ? snap.data()?.repair_test_data === true : false;
      if (snap?.exists?.() && tagged) {
        batch.delete(recordRef);
        deletedRecords.push(`${collectionName}/${recordId}`);
      }
      verification.push({
        path: `${collectionName}/${recordId}`,
        existed: Boolean(snap?.exists?.()),
        tagged,
        deleteQueued: Boolean(snap?.exists?.() && tagged),
      });
    }
    await batch.commit();
    const postDeleteVerification = [];
    for (const [collectionName, recordId] of candidateRefs) {
      const snap = await getDoc(doc(db, "users", workspaceUid, collectionName, recordId), {
        label: "crmDataRepair.quoteIndexCleanup.verifyDeleted",
        cacheKey: `users/${workspaceUid}/${collectionName}/${recordId}:quoteIndexRepairCleanupVerify:${Date.now()}`,
        ttlMs: 0,
      });
      const before = verification.find((item) => item.path === `${collectionName}/${recordId}`) || {};
      postDeleteVerification.push({
        ...before,
        gone: !snap?.exists?.(),
      });
    }
    ["customers", "quote_index", "quotations", "crm_leads"].forEach((collectionName) =>
      invalidateWorkspaceCollectionCache(workspaceUid, collectionName)
    );
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteIndexTestDataBusy: false,
      quoteIndexTestDataMessage: deletedRecords.length
        ? `Deleted ${deletedRecords.length} quote index repair fixture${deletedRecords.length === 1 ? "" : "s"}. Repair logs were not deleted.`
        : "No tagged quote index repair fixtures found. Repair logs were not deleted.",
      quoteIndexTestDataError: "",
      quoteIndexTestDataRecords: deletedRecords,
      quoteIndexTestDataVerification: postDeleteVerification,
      quoteDiagnostic: null,
      quoteIndexMismatchResults: [],
      quoteIndexMismatchCursor: null,
      quoteIndexMismatchHasMore: false,
    }));
    notify("Quote index repair test cleanup finished.", "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteIndexTestDataBusy: false,
      quoteIndexTestDataError: error?.message || "Could not delete quote index repair test data.",
    }));
    devError("CRM_QUOTE_INDEX_REPAIR_DELETE_TEST_DATA_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
    });
    notify(error?.message || "Could not delete quote index repair test data.", "error");
  }
}, [canUseCrmRepairTestDataTools, notify, workspaceUid]);

const runQuoteLinkDiagnosis = useCallback(async () => {
  const queryText = String(crmDataRepairState.quoteLookup || "").trim();
  if (!queryText) {
    notify("Enter a quote number or quote ID first.", "warning");
    return;
  }
  if (!canUseCrmDataRepairTools) {
    notify("Only workspace owners/admins can use data repair tools.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({
    ...prev,
    quoteDiagnosticLoading: true,
    quoteDiagnosticError: "",
  }));
  try {
    const diagnostic = await diagnoseQuoteLinks({ quoteId: queryText, quoteNo: queryText });
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteDiagnosticLoading: false,
      quoteDiagnostic: diagnostic,
    }));
  } catch (error) {
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteDiagnosticLoading: false,
      quoteDiagnosticError: error?.message || "Could not diagnose quote links.",
    }));
  }
}, [canUseCrmDataRepairTools, crmDataRepairState.quoteLookup, diagnoseQuoteLinks, notify]);

const findUnlinkedQuotesForRepair = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    setCrmDataRepairState((prev) => ({
      ...prev,
      unlinkedLoading: true,
      unlinkedError: "",
      ...(append ? {} : { unlinkedResults: [], unlinkedCursor: null, unlinkedSelected: {} }),
    }));
    const cursor = append ? crmDataRepairState.unlinkedCursor : null;
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, "quote_index"), ...constraints),
        {
          label: "crmDataRepair.findUnlinkedQuotes",
          cacheKey: `users/${workspaceUid}/quote_index?dataRepair=unlinked&after=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const rows = [];
      for (const quoteDoc of snap.docs) {
        const quoteIndex = { id: quoteDoc.id, ...(quoteDoc.data() || {}) };
        const quoteId = String(
          quoteIndex.sourceQuoteId || quoteIndex.quoteId || quoteIndex.id || quoteDoc.id
        ).trim();
        const quoteNo = String(quoteIndex.quoteNo || quoteIndex.quoteNoString || "").trim();
        const quoteIndexCustomerId = String(
          quoteIndex.customerId || quoteIndex.crm?.customerId || ""
        ).trim();
        const dealIds = Array.from(
          new Set(
            [
              quoteIndex.opportunityId,
              quoteIndex.dealId,
              quoteIndex.crmLeadId,
              quoteIndex.leadId,
              quoteIndex.crm?.opportunityId,
              quoteIndex.crm?.dealId,
              quoteIndex.crm?.leadId,
            ]
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          )
        );
        const issues = [];
        if (!quoteIndexCustomerId && String(quoteIndex.customerName || "").trim()) {
          issues.push("quote_index_missing_customerId");
        }
        if (!dealIds.length) {
          issues.push("no_deal_for_quote");
        }
        if (!issues.length) continue;
        const matchSource = {
          ...quoteIndex,
          customerName: quoteIndex.customerName || "",
          customerPhone:
            quoteIndex.customerPhone ||
            quoteIndex.phone ||
            "",
          customerEmail: quoteIndex.customerEmail || quoteIndex.email || "",
        };
        const suggestion = await findStrictCustomerMatchForRepair(matchSource);
        rows.push({
          key: quoteDoc.id,
          quoteIndexId: quoteDoc.id,
          repair_test_data: quoteIndex.repair_test_data === true,
          quoteId,
          quoteNo,
          quoteCustomerName: matchSource.customerName || "",
          quotePhone: matchSource.customerPhone || "",
          quoteEmail: matchSource.customerEmail || "",
          currentQuoteIndexCustomerId: quoteIndexCustomerId,
          currentFullQuoteCustomerId: "",
          issues,
          dealIds,
          ...suggestion,
        });
      }
      setCrmDataRepairState((prev) => ({
        ...prev,
        unlinkedLoading: false,
        unlinkedResults: append ? [...prev.unlinkedResults, ...rows] : rows,
        unlinkedCursor: snap.docs[snap.docs.length - 1] || null,
        unlinkedHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        unlinkedLoading: false,
        unlinkedError: error?.message || "Could not find unlinked quotes.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.unlinkedCursor,
    findStrictCustomerMatchForRepair,
    notify,
    workspaceUid,
  ]
);

const repairQuoteCustomerLink = useCallback(
  async ({ repairRow = null, targetCustomerId = "", mode = "link_only" } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools || !repairRow) {
      notify("Only workspace owners/admins can repair quote links.", "warning");
      return;
    }
    const quoteId = String(repairRow.quoteId || "").trim();
    const quoteIndexId = String(repairRow.quoteIndexId || repairRow.key || quoteId || "").trim();
    const quoteNo = String(repairRow.quoteNo || "").trim();
    const cleanCustomerId = String(targetCustomerId || repairRow.suggestedCustomerId || "").trim();
    if (!quoteIndexId || !cleanCustomerId) {
      notify("Choose a customer before repairing this quote.", "warning");
      return;
    }
    if (!crmDataRepairState.unlinkedResults.some((row) => row.key === repairRow.key)) {
      notify("Run a dry run and select a result before repairing.", "warning");
      return;
    }
    const confirmed = confirmGuidedRepair(repairRow,
      "You are about to update customer links for this quotation. This will not change quote items or totals.");
    if (!confirmed) return;
    const repairKey = `${quoteIndexId}:${cleanCustomerId}`;
    setCrmDataRepairState((prev) => ({ ...prev, repairingQuoteKey: repairKey }));
    try {
      const customerSnap = await getDoc(
        doc(db, "users", workspaceUid, "customers", cleanCustomerId),
        {
          label: "crmDataRepair.repairQuoteCustomerLink.customer",
          cacheKey: `users/${workspaceUid}/customers/${cleanCustomerId}:repairQuoteLink`,
          ttlMs: 10 * 1000,
        }
      );
      if (!customerSnap.exists()) {
        throw new Error("Customer record was not found.");
      }
      const customerFields = getCustomerRepairFields({
        id: customerSnap.id,
        ...(customerSnap.data() || {}),
      });
      const quoteIndexRef = doc(db, "users", workspaceUid, "quote_index", quoteIndexId);
      const quoteIndexSnap = await getDoc(quoteIndexRef, {
        label: "crmDataRepair.repairQuoteCustomerLink.quoteIndex",
        cacheKey: `users/${workspaceUid}/quote_index/${quoteIndexId}:repairQuoteLink`,
        ttlMs: 10 * 1000,
      });
      const quoteIndexBefore = quoteIndexSnap.exists() ? quoteIndexSnap.data() || {} : {};
      const resolvedQuoteId = String(
        quoteId || quoteIndexBefore.sourceQuoteId || quoteIndexBefore.quoteId || quoteIndexId
      ).trim();
      const fullQuoteRef = resolvedQuoteId
        ? doc(db, "users", workspaceUid, "quotations", resolvedQuoteId)
        : null;
      const shouldUpdateFullQuoteCustomer = mode === "link_and_update_quote_customer";
      const fullQuoteSnap = shouldUpdateFullQuoteCustomer && fullQuoteRef
        ? await getDoc(fullQuoteRef, {
            label: "crmDataRepair.repairQuoteCustomerLink.fullQuote",
            cacheKey: `users/${workspaceUid}/quotations/${resolvedQuoteId}:repairQuoteLink`,
            ttlMs: 10 * 1000,
          })
        : null;
      const fullQuoteBefore = fullQuoteSnap?.exists?.() ? fullQuoteSnap.data() || {} : {};
      if (shouldUpdateFullQuoteCustomer && !fullQuoteSnap?.exists?.()) {
        throw new Error("Full quotation was not found. Run diagnosis before updating its customer snapshot.");
      }
      const batch = writeBatch(db);
      const quoteIndexPatch = sanitizeCrmFirestoreValue({
        ...customerFields,
        customerLedgerLinked: true,
        customerLinkRepairedAt: serverTimestamp(),
        customerLinkRepairMode: mode,
        repairedBy: user?.uid || auth.currentUser?.uid || "",
      });
      batch.set(quoteIndexRef, quoteIndexPatch, { merge: true });

      if (shouldUpdateFullQuoteCustomer && fullQuoteRef) {
        batch.set(
          fullQuoteRef,
          sanitizeCrmFirestoreValue({
            ...customerFields,
            customerLedgerLinked: true,
            crm: {
              customerId: customerFields.customerId,
              customerKey: customerFields.customerKey,
              customerName: customerFields.customerName,
              customerPhone: customerFields.customerPhone,
              customerEmail: customerFields.customerEmail,
            },
            customerLinkRepairedAt: serverTimestamp(),
            customerLinkRepairMode: mode,
            repairedBy: user?.uid || auth.currentUser?.uid || "",
          }),
          { merge: true }
        );
      }

      const dealSnaps = [];
      if (resolvedQuoteId) {
        dealSnaps.push(
          await getDocs(
            firestoreQuery(
              collection(db, "users", workspaceUid, "crm_leads"),
              firestoreWhere("quoteId", "==", resolvedQuoteId),
              firestoreLimit(5)
            ),
            {
              label: "crmDataRepair.repairQuoteCustomerLink.dealByQuoteId",
              cacheKey: `users/${workspaceUid}/crm_leads?repairLinkQuoteId=${resolvedQuoteId}&limit=5`,
              ttlMs: 10 * 1000,
            }
          ).catch(() => null)
        );
      }
      if (quoteNo) {
        dealSnaps.push(
          await getDocs(
            firestoreQuery(
              collection(db, "users", workspaceUid, "crm_leads"),
              firestoreWhere("quoteNo", "==", quoteNo),
              firestoreLimit(5)
            ),
            {
              label: "crmDataRepair.repairQuoteCustomerLink.dealByQuoteNo",
              cacheKey: `users/${workspaceUid}/crm_leads?repairLinkQuoteNo=${quoteNo}&limit=5`,
              ttlMs: 10 * 1000,
            }
          ).catch(() => null)
        );
      }
      const dealIds = Array.from(
        new Set(
          dealSnaps
            .filter(Boolean)
            .flatMap((snap) => snap.docs.map((dealDoc) => dealDoc.id))
        )
      );
      dealIds.forEach((dealId) => {
        batch.set(
          doc(db, "users", workspaceUid, "crm_leads", dealId),
          sanitizeCrmFirestoreValue({
            ...customerFields,
            crm: {
              customerId: customerFields.customerId,
              customerKey: customerFields.customerKey,
              customerName: customerFields.customerName,
              customerPhone: customerFields.customerPhone,
              customerEmail: customerFields.customerEmail,
            },
            customerLedgerLinked: true,
            customerLinkRepairedAt: serverTimestamp(),
            customerLinkRepairMode: mode,
          }),
          { merge: true }
        );
      });

      const customerSearchWrite = buildCustomerSearchIndexWrite(
        {
          ...customerFields,
          workspaceUid,
          quoteId: resolvedQuoteId,
          quoteNo: quoteNo || quoteIndexBefore.quoteNo || "",
          latestQuoteId: resolvedQuoteId,
          latestQuoteNo: quoteNo || quoteIndexBefore.quoteNo || "",
          latestQuoteTotal: quoteIndexBefore.totalAmount || quoteIndexBefore.grandTotal || 0,
          updatedAt: quoteIndexBefore.lastActivityAt || quoteIndexBefore.updatedAt || quoteIndexBefore.createdAt || null,
        },
        { workspaceUid, sourceType: "quote_customer" }
      );
      if (customerSearchWrite?.personId) {
        batch.set(
          doc(db, "users", workspaceUid, "customer_search_index", customerSearchWrite.personId),
          sanitizeCrmFirestoreValue(customerSearchWrite.payload),
          { merge: true }
        );
      }

      const repairId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const actor = getCrmActorSnapshot();
      const repairLogRef = doc(db, "users", workspaceUid, "repair_logs", repairId);
      batch.set(
        repairLogRef,
        sanitizeCrmFirestoreValue({
          repairId,
          workspaceUid,
          toolName: "repairQuoteCustomerLink",
          recordType: "quote",
          recordId: resolvedQuoteId || quoteIndexId,
          quoteId: resolvedQuoteId,
          quoteNo: quoteNo || quoteIndexBefore.quoteNo || "",
          before: {
            quoteIndex: {
              customerId: quoteIndexBefore.customerId || "",
              customerName: quoteIndexBefore.customerName || "",
            },
            fullQuote: {
              customerId: fullQuoteBefore.customerId || fullQuoteBefore.crm?.customerId || "",
              customerName: fullQuoteBefore.customerName || fullQuoteBefore.crm?.customerName || "",
            },
            dealIds,
          },
          after: {
            mode,
            quoteIndexId,
            customerSearchIndexId: customerSearchWrite?.personId || "",
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            dealIds,
          },
          dryRun: false,
          performedBy: {
            uid: actor.uid || user?.uid || "",
            name: actor.name || user?.displayName || user?.email || "",
            email: actor.email || user?.email || "",
          },
          performedByUid: actor.uid || user?.uid || "",
          performedByName: actor.name || user?.displayName || user?.email || "",
          performedAt: serverTimestamp(),
          success: true,
          errorCode: "",
          errorMessage: "",
        })
      );

      await batch.commit();
      const repairLogCheck = await readCrmDataRepairLogCheck(repairLogRef, [
        "toolName",
        "recordId",
        "before",
        "after",
        "performedBy",
        "performedAt",
        "success",
      ]);
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
      invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
      customerLedgerQuotesLoadRef.current = {};
      customerLedgerCrmDealsLoadRef.current = {};
      applyQuoteLocalPatch(resolvedQuoteId || quoteIndexId, {
        ...customerFields,
        customerLedgerLinked: true,
      }, { persistIndex: false, sourceQuote: { ...quoteIndexBefore, quoteIndexId, sourceQuoteId: resolvedQuoteId } });
      setCrmDataRepairState((prev) => ({
        ...prev,
        repairLogChecks: repairLogCheck
          ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
          : prev.repairLogChecks,
        unlinkedResults: prev.unlinkedResults.map((row) =>
          row.key === repairRow.key
            ? {
                ...row,
                repaired: true,
                currentQuoteIndexCustomerId: customerFields.customerId,
                ...(mode === "link_and_update_quote_customer"
                  ? { currentFullQuoteCustomerId: customerFields.customerId }
                  : {}),
              }
            : row
        ),
        unlinkedSelected: {
          ...prev.unlinkedSelected,
          [repairRow.key]: false,
        },
      }));
      notify("Quote customer link repaired.", "success");
    } catch (error) {
      await logCrmDataRepairAction({
        toolName: "repairQuoteCustomerLink",
        recordType: "quote",
        recordId: repairRow.quoteId || repairRow.quoteIndexId || repairRow.key || "",
        quoteId: repairRow.quoteId || "",
        quoteNo: repairRow.quoteNo || "",
        before: repairRow,
        after: { targetCustomerId: cleanCustomerId, mode },
        success: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      }).catch(() => null);
      devError("CRM_DATA_REPAIR_QUOTE_LINK_FAILED", {
        code: error?.code || "",
        message: error?.message || String(error),
        quoteId,
        quoteIndexId,
        targetCustomerId: cleanCustomerId,
        mode,
      });
      notify(error?.message || "Could not repair quote customer link.", "error");
    } finally {
      setCrmDataRepairState((prev) => ({ ...prev, repairingQuoteKey: "" }));
    }
  },
  [
    applyQuoteLocalPatch,
    canUseCrmDataRepairTools,
    crmDataRepairState.unlinkedResults,
    getCrmActorSnapshot,
    getCustomerRepairFields,
    logCrmDataRepairAction,
    notify,
    readCrmDataRepairLogCheck,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const findQuoteIndexMismatches = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    const cursor = append ? crmDataRepairState.quoteIndexMismatchCursor : null;
    setCrmDataRepairState((prev) => ({
      ...prev,
      quoteIndexMismatchLoading: true,
      quoteIndexMismatchError: "",
      ...(append
        ? {}
        : {
            quoteIndexMismatchResults: [],
            quoteIndexMismatchCursor: null,
            quoteIndexMismatchHasMore: false,
          }),
    }));
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, "quote_index"), ...constraints),
        {
          label: "crmDataRepair.findQuoteIndexMismatches",
          cacheKey: `users/${workspaceUid}/quote_index?dataRepair=mismatches&after=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const rows = [];
      for (const quoteDoc of snap.docs) {
        const quoteIndex = { id: quoteDoc.id, ...(quoteDoc.data() || {}) };
        const quoteId = getQuoteIndexSourceQuoteId(quoteIndex, quoteDoc.id);
        const quoteNo = cleanQuoteIdentityText(quoteIndex.quoteNo || quoteIndex.quoteNoString || "");
        const fullQuoteSnap = quoteId
          ? await getDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
              label: "crmDataRepair.findQuoteIndexMismatches.fullQuote",
              cacheKey: `users/${workspaceUid}/quotations/${quoteId}:quoteIndexMismatch`,
              ttlMs: 10 * 1000,
            })
          : null;
        const fullQuote = fullQuoteSnap?.exists?.()
          ? { id: fullQuoteSnap.id, ...(fullQuoteSnap.data() || {}) }
          : null;
        const comparison = compareQuoteIndexWithFullQuote({
          fullQuote,
          quoteIndex,
          quoteIndexId: quoteDoc.id,
          workspaceUidValue: workspaceUid,
        });
        if (!comparison.issues?.length) continue;
        const expected = comparison.expected || {};
        rows.push({
          key: quoteDoc.id,
          modePreviews: Object.fromEntries(QUOTE_INDEX_REPAIR_MODES.map(([mode]) => [mode,
            fullQuote ? buildQuoteIndexModePreview({ fullQuote, current: quoteIndex, expected, mode }) : { fields: [], patch: {}, warning: "Full quotation was not found.", risk: "Manual Only" }
          ])),
          quoteIndexId: quoteDoc.id,
          repair_test_data: quoteIndex.repair_test_data === true && fullQuote?.repair_test_data === true,
          quoteId: quoteId || comparison.quoteId || quoteDoc.id,
          quoteNo: comparison.quoteNo || quoteNo,
          quoteType: expected.quoteType || quoteIndex.quoteType || quoteIndex.quotationType || "",
          fullQuotePath: quoteId ? `users/${workspaceUid}/quotations/${quoteId}` : "",
          quoteIndexPath: `users/${workspaceUid}/quote_index/${quoteDoc.id}`,
          fullQuoteCustomerName: expected.customerName || "",
          indexCustomerName: quoteIndex.customerName || "",
          fullQuoteCustomerId: expected.customerId || "",
          indexCustomerId: quoteIndex.customerId || "",
          fullQuoteSiteName: expected.siteName || "",
          indexSiteName: quoteIndex.siteName || quoteIndex.projectName || "",
          fullQuoteGrandTotal: expected.grandTotal ?? null,
          indexGrandTotal:
            quoteIndex.grandTotal ?? quoteIndex.total ?? quoteIndex.finalTotal ?? quoteIndex.totalAmount ?? null,
          fullQuoteStatus: expected.status || "",
          indexStatus: quoteIndex.status || "",
          fullQuoteLastActivityAt: expected.lastActivityAt || null,
          indexLastActivityAt: quoteIndex.lastActivityAt || null,
          dealId:
            quoteIndex.opportunityId ||
            quoteIndex.dealId ||
            quoteIndex.crmLeadId ||
            quoteIndex.leadId ||
            expected.opportunityId ||
            expected.dealId ||
            "",
          issues: comparison.issues,
          risk: comparison.risk,
          recommendedAction: comparison.recommendedAction,
          repaired: false,
        });
      }
      setCrmDataRepairState((prev) => ({
        ...prev,
        quoteIndexMismatchLoading: false,
        quoteIndexMismatchResults: append
          ? [...prev.quoteIndexMismatchResults, ...rows]
          : rows,
        quoteIndexMismatchCursor: snap.docs[snap.docs.length - 1] || null,
        quoteIndexMismatchHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        quoteIndexMismatchLoading: false,
        quoteIndexMismatchError: error?.message || "Could not find quote index mismatches.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.quoteIndexMismatchCursor,
    notify,
    workspaceUid,
  ]
);

const repairQuoteIndexFromFullQuote = useCallback(
  async ({ row = null, mode = "display_only" } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools || !row) {
      notify("Only workspace owners/admins can repair quote indexes.", "warning");
      return;
    }
    const quoteIndexId = String(row.quoteIndexId || row.key || "").trim();
    const quoteId = String(row.quoteId || "").trim();
    if (!quoteIndexId || !quoteId) {
      notify("Missing quote index or full quote identity.", "warning");
      return;
    }
    if (!crmDataRepairState.quoteIndexMismatchResults.some((item) => item.key === row.key)) {
      notify("Run a dry run and select a result before repairing.", "warning");
      return;
    }
    const repairKey = `${quoteIndexId}:${mode}`;
    setCrmDataRepairState((prev) => ({ ...prev, repairingQuoteIndexKey: repairKey }));
    try {
      const [fullQuoteSnap, quoteIndexSnap] = await Promise.all([
        getDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
          label: "crmDataRepair.repairQuoteIndex.fullQuote",
          cacheKey: `users/${workspaceUid}/quotations/${quoteId}:repairQuoteIndex`,
          ttlMs: 0,
        }),
        getDoc(doc(db, "users", workspaceUid, "quote_index", quoteIndexId), {
          label: "crmDataRepair.repairQuoteIndex.quoteIndex",
          cacheKey: `users/${workspaceUid}/quote_index/${quoteIndexId}:repairQuoteIndex`,
          ttlMs: 0,
        }),
      ]);
      if (!fullQuoteSnap.exists()) throw new Error("Full quotation was not found.");
      if (!quoteIndexSnap.exists()) throw new Error("quote_index document was not found.");
      const fullQuote = { id: fullQuoteSnap.id, ...(fullQuoteSnap.data() || {}) };
      assertLegacyRepairSupported(fullQuote);
      const quoteIndexBefore = { id: quoteIndexSnap.id, ...(quoteIndexSnap.data() || {}) };
      const comparison = compareQuoteIndexWithFullQuote({
        fullQuote,
        quoteIndex: quoteIndexBefore,
        quoteIndexId,
        workspaceUidValue: workspaceUid,
      });
      const preview = buildQuoteIndexModePreview({ fullQuote, current: quoteIndexBefore, expected: comparison.expected, mode });
      if (preview.warning) throw new Error(preview.warning);
      const patch = sanitizeCrmFirestoreValue(preview.patch);
      const selectedFields = Object.keys(patch);
      if (!selectedFields.length) { notify("No changed fields in this repair mode.", "info"); return; }
      const dryPreview = row.modePreviews?.[mode];
      if (!dryPreview || JSON.stringify(dryPreview.fields.map((field) => [field.field, repairValueText(field.before), repairValueText(field.after)])) !== JSON.stringify(preview.fields.map((field) => [field.field, repairValueText(field.before), repairValueText(field.after)]))) {
        throw new Error("The record changed after the dry run. Run a new preview before repairing.");
      }
      const fieldReview = preview.fields.map((field) => `${field.field}: ${repairValueText(field.before)} -> ${repairValueText(field.after)} | Will update: ${field.changed ? "Yes" : "No"}`).join("\n");
      if (!confirmGuidedRepair(row, `${quoteIndexRepairConfirmation(mode)}\n\n${fieldReview}`)) return;
      const actor = getCrmActorSnapshot();
      const repairId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const auditPayload = sanitizeCrmFirestoreValue({
        repairId, workspaceUid, toolName: "repairQuoteIndexFromFullQuote", recordType: "quote_index",
        recordId: quoteIndexId, quoteId, quoteNo: comparison.quoteNo || row.quoteNo || "",
        mode, selectedFields,
        before: Object.fromEntries(selectedFields.map((field) => [field, quoteIndexBefore[field] ?? null])),
        after: { mode, selectedFields, patch },
        performedBy: { uid: actor.uid || user?.uid || "", name: actor.name || "", email: actor.email || "" },
        performedAt: serverTimestamp(), dryRun: false, success: true,
      });
      await runTransaction(db, async (transaction) => {
        const indexRef = doc(db, "users", workspaceUid, "quote_index", quoteIndexId);
        const sourceRef = doc(db, "users", workspaceUid, "quotations", quoteId);
        const indexSnap = await transaction.get(indexRef);
        recordFirestoreRead({ path: indexRef.path, label: "crmDataRepair.quoteIndexPreflight", kind: "transaction.get", stack: new Error().stack });
        const sourceSnap = await transaction.get(sourceRef);
        recordFirestoreRead({ path: sourceRef.path, label: "crmDataRepair.quoteIndexSourcePreflight", kind: "transaction.get", stack: new Error().stack });
        if (!indexSnap.exists() || !sourceSnap.exists()) throw new Error("Repair source no longer exists.");
        const source = { id: quoteId, ...sourceSnap.data() };
        const current = { id: quoteIndexId, ...indexSnap.data() };
        const freshComparison = compareQuoteIndexWithFullQuote({ fullQuote: source, quoteIndex: current, quoteIndexId, workspaceUidValue: workspaceUid });
        const freshPreview = buildQuoteIndexModePreview({ fullQuote: source, current, expected: freshComparison.expected, mode });
        if (freshPreview.warning || JSON.stringify(freshPreview.fields.map((field) => [field.field, repairValueText(field.before), repairValueText(field.after)])) !== JSON.stringify(preview.fields.map((field) => [field.field, repairValueText(field.before), repairValueText(field.after)]))) {
          throw new Error("The repair preview is stale. Run a new dry run.");
        }
        transaction.update(indexRef, patch);
        transaction.set(doc(db, "users", workspaceUid, "repair_logs", repairId), auditPayload);
      });
      const readbackSnap = await getDoc(doc(db, "users", workspaceUid, "quote_index", quoteIndexId), {
        label: "crmDataRepair.repairQuoteIndex.readback",
        cacheKey: `users/${workspaceUid}/quote_index/${quoteIndexId}:repairQuoteIndexReadback:${Date.now()}`,
        ttlMs: 0,
      });
      if (!readbackSnap.exists() || !selectedFields.every((field) => repairValueText(readbackSnap.data()[field]) === repairValueText(patch[field]))) throw new Error(REPAIR_VERIFY_WARNING);
      const repairLogCheck = repairId
        ? await readCrmDataRepairLogCheck(doc(db, "users", workspaceUid, "repair_logs", repairId), [
            "toolName",
            "recordId",
            "before",
            "after",
            "performedBy",
            "performedAt",
            "success",
          ])
        : null;
      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      customerLedgerQuotesLoadRef.current = {};
      // The selected repair was already committed and verified. Do not let the
      // normal quote-edit patcher persist a second write or bump sorting dates.
      applyQuoteLocalPatch(quoteId, patch, { persistIndex: false, sourceQuote: quoteIndexBefore });
      setCrmDataRepairState((prev) => ({
        ...prev,
        repairLogChecks: repairLogCheck
          ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
          : prev.repairLogChecks,
        quoteIndexMismatchResults: prev.quoteIndexMismatchResults.map((item) =>
          item.key === row.key ? { ...item, repaired: true, repairedMode: mode } : item
        ),
      }));
      notify("quote_index repaired from full quotation.", "success");
    } catch (error) {
      await logCrmDataRepairAction({
        toolName: "repairQuoteIndexFromFullQuote",
        recordType: "quote_index",
        recordId: row.quoteIndexId || row.key || "",
        quoteId: row.quoteId || "",
        quoteNo: row.quoteNo || "",
        before: Object.fromEntries((row.modePreviews?.[mode]?.fields || []).map((field) => [field.field, field.before])),
        after: { mode },
        success: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      }).catch(() => null);
      devError("CRM_DATA_REPAIR_QUOTE_INDEX_FAILED", {
        code: error?.code || "",
        message: error?.message || String(error),
        quoteId: row.quoteId || "",
        quoteIndexId: row.quoteIndexId || "",
        mode,
      });
      notify(error?.message || "Could not repair quote_index.", "error");
    } finally {
      setCrmDataRepairState((prev) => ({ ...prev, repairingQuoteIndexKey: "" }));
    }
  },
  [
    applyQuoteLocalPatch,
    canUseCrmDataRepairTools,
    crmDataRepairState.quoteIndexMismatchResults,
    getCrmActorSnapshot,
    logCrmDataRepairAction,
    notify,
    readCrmDataRepairLogCheck,
    user?.uid,
    workspaceUid,
  ]
);

const repairCrmDealQuoteFieldsFromQuote = useCallback(
  async ({ row = null } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools || !row) {
      notify("Only workspace owners/admins can repair CRM deal fields.", "warning");
      return;
    }
    const quoteId = String(row.quoteId || "").trim();
    const quoteIndexId = String(row.quoteIndexId || row.key || quoteId || "").trim();
    const quoteNo = String(row.quoteNo || "").trim();
    if (!quoteId && !quoteNo) {
      notify("Missing quote identity for CRM deal repair.", "warning");
      return;
    }
    if (!crmDataRepairState.quoteIndexMismatchResults.some((item) => item.key === row.key)) {
      notify("Run a dry run and select a result before repairing.", "warning");
      return;
    }
    const repairKey = `${quoteId || quoteNo}:crmDeal`;
    setCrmDataRepairState((prev) => ({ ...prev, repairingCrmDealKey: repairKey }));
    try {
      const fullQuoteSnap = quoteId
        ? await getDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
            label: "crmDataRepair.repairCrmDeal.fullQuote",
            cacheKey: `users/${workspaceUid}/quotations/${quoteId}:repairCrmDeal`,
            ttlMs: 0,
          })
        : null;
      const fullQuote = fullQuoteSnap?.exists?.()
        ? { id: fullQuoteSnap.id, ...(fullQuoteSnap.data() || {}) }
        : null;
      if (!fullQuote) throw new Error("Full quotation was not found.");
      const quoteIndexSnap = quoteIndexId
        ? await getDoc(doc(db, "users", workspaceUid, "quote_index", quoteIndexId), {
            label: "crmDataRepair.repairCrmDeal.quoteIndex",
            cacheKey: `users/${workspaceUid}/quote_index/${quoteIndexId}:repairCrmDeal`,
            ttlMs: 0,
          }).catch(() => null)
        : null;
      const quoteIndex = quoteIndexSnap?.exists?.()
        ? { id: quoteIndexSnap.id, ...(quoteIndexSnap.data() || {}) }
        : null;
      const expected = buildExpectedQuoteIndexFromFullQuoteForRepair(fullQuote, quoteId, workspaceUid);
      const explicitDealId = cleanQuoteIdentityText(
        row.dealId ||
          quoteIndex?.opportunityId ||
          quoteIndex?.dealId ||
          quoteIndex?.crmLeadId ||
          quoteIndex?.leadId ||
          fullQuote.opportunityId ||
          fullQuote.dealId ||
          fullQuote.crmLeadId ||
          fullQuote.leadId
      );
      let dealRecord = null;
      if (explicitDealId) {
        const dealSnap = await getDoc(doc(db, "users", workspaceUid, "crm_leads", explicitDealId), {
          label: "crmDataRepair.repairCrmDeal.explicitDeal",
          cacheKey: `users/${workspaceUid}/crm_leads/${explicitDealId}:repairCrmDeal`,
          ttlMs: 0,
        }).catch(() => null);
        if (dealSnap?.exists?.()) dealRecord = { id: dealSnap.id, ...(dealSnap.data() || {}) };
      }
      if (!dealRecord && quoteId) {
        const dealSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_leads"),
            firestoreWhere("quoteId", "==", quoteId),
            firestoreLimit(2)
          ),
          {
            label: "crmDataRepair.repairCrmDeal.byQuoteId",
            cacheKey: `users/${workspaceUid}/crm_leads?repairDealQuoteId=${quoteId}&limit=2`,
            ttlMs: 0,
          }
        );
        if (dealSnap.size === 1) {
          dealRecord = { id: dealSnap.docs[0].id, ...(dealSnap.docs[0].data() || {}) };
        }
      }
      if (!dealRecord && quoteNo) {
        const dealSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_leads"),
            firestoreWhere("quoteNo", "==", quoteNo),
            firestoreLimit(2)
          ),
          {
            label: "crmDataRepair.repairCrmDeal.byQuoteNo",
            cacheKey: `users/${workspaceUid}/crm_leads?repairDealQuoteNo=${quoteNo}&limit=2`,
            ttlMs: 0,
          }
        );
        if (dealSnap.size === 1) {
          dealRecord = { id: dealSnap.docs[0].id, ...(dealSnap.docs[0].data() || {}) };
        }
      }
      if (!dealRecord) throw new Error("No single linked CRM deal was found for this quote.");
      const actor = getCrmActorSnapshot();
      const patch = sanitizeCrmFirestoreValue({
        quoteId: expected.quoteId || quoteId,
        latestQuoteId: expected.quoteId || quoteId,
        primaryQuoteId: expected.quoteId || quoteId,
        quoteNo: expected.quoteNo || quoteNo,
        latestQuoteNo: expected.quoteNo || quoteNo,
        primaryQuoteNo: expected.quoteNo || quoteNo,
        quoteType: expected.quoteType || "",
        quotationType: expected.quotationType || expected.quoteType || "",
        customerId: expected.customerId || "",
        customerName: expected.customerName || "",
        customerPhone: expected.customerPhone || "",
        customerEmail: expected.customerEmail || "",
        siteName: expected.siteName || "",
        projectName: expected.projectName || expected.siteName || "",
        quoteTotal: expected.grandTotal ?? expected.totalAmount ?? 0,
        grandTotal: expected.grandTotal ?? expected.totalAmount ?? 0,
        latestQuoteTotal: expected.grandTotal ?? expected.totalAmount ?? 0,
        quoteFieldsRepairedAt: serverTimestamp(),
        repairedBy: actor.uid || user?.uid || "",
        updatedAt: serverTimestamp(),
      });
      const fieldReview = Object.entries(patch).map(([field, value]) => `${field} | ${repairValueText(dealRecord[field])} | ${value?._methodName ? "Repair timestamp" : repairValueText(value)} | ${repairValueText(dealRecord[field]) !== repairValueText(value) ? "Yes" : "No"}`).join("\n");
      if (!confirmGuidedRepair(row, `Update linked CRM deal fields from the saved quotation?\n\nField | Current CRM deal | Full quote source | Will update?\n${fieldReview}`)) return;
      await setDoc(doc(db, "users", workspaceUid, "crm_leads", dealRecord.id), patch, { merge: true });
      const repairId = await logCrmDataRepairAction({
        toolName: "repairCrmDealQuoteFieldsFromQuote",
        recordType: "crm_leads",
        recordId: dealRecord.id,
        quoteId: expected.quoteId || quoteId,
        quoteNo: expected.quoteNo || quoteNo,
        before: {
          customerId: dealRecord.customerId || "",
          customerName: dealRecord.customerName || dealRecord.title || "",
          siteName: dealRecord.siteName || "",
          quoteNo: dealRecord.quoteNo || "",
          quoteTotal: dealRecord.quoteTotal ?? dealRecord.grandTotal ?? null,
        },
        after: patch,
        success: true,
      });
      const repairLogCheck = repairId
        ? await readCrmDataRepairLogCheck(doc(db, "users", workspaceUid, "repair_logs", repairId), [
            "toolName",
            "recordId",
            "before",
            "after",
            "performedBy",
            "performedAt",
            "success",
          ])
        : null;
      invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
      applyCrmLeadLocalPatch(dealRecord.id, { ...patch, updatedAt: new Date() });
      setCrmDataRepairState((prev) => ({
        ...prev,
        repairLogChecks: repairLogCheck
          ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
          : prev.repairLogChecks,
        quoteIndexMismatchResults: prev.quoteIndexMismatchResults.map((item) =>
          item.key === row.key ? { ...item, crmDealRepaired: true } : item
        ),
      }));
      notify("Linked CRM deal fields repaired.", "success");
    } catch (error) {
      await logCrmDataRepairAction({
        toolName: "repairCrmDealQuoteFieldsFromQuote",
        recordType: "crm_leads",
        recordId: row.dealId || "",
        quoteId,
        quoteNo,
        before: row,
        after: {},
        success: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      }).catch(() => null);
      devError("CRM_DATA_REPAIR_DEAL_QUOTE_FIELDS_FAILED", {
        code: error?.code || "",
        message: error?.message || String(error),
        quoteId,
        quoteNo,
      });
      notify(error?.message || "Could not repair CRM deal fields.", "error");
    } finally {
      setCrmDataRepairState((prev) => ({ ...prev, repairingCrmDealKey: "" }));
    }
  },
  [
    applyCrmLeadLocalPatch,
    canUseCrmDataRepairTools,
    crmDataRepairState.quoteIndexMismatchResults,
    getCrmActorSnapshot,
    logCrmDataRepairAction,
    notify,
    readCrmDataRepairLogCheck,
    user?.uid,
    workspaceUid,
  ]
);

const getActivityDateRepairProposal = useCallback((record = {}) => {
  const proposal = buildActivityDateProposal(record);
  if (!proposal || proposal.proposedLastActivityAtMs == null) return proposal;
  return { ...proposal, proposedLastActivityAt: Timestamp.fromMillis(proposal.proposedLastActivityAtMs) };
}, []);

const findMissingActivityDatesForRepair = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    const collectionName = CRM_ACTIVITY_REPAIR_COLLECTIONS.includes(
      crmDataRepairState.activityCollection
    )
      ? crmDataRepairState.activityCollection
      : "quote_index";
    const cursor = append ? crmDataRepairState.activityCursor : null;
    setCrmDataRepairState((prev) => ({
      ...prev,
      activityLoading: true,
      activityError: "",
      ...(append ? {} : { activityResults: [], activityCursor: null, activitySelected: {} }),
    }));
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, collectionName), ...constraints),
        {
          label: "crmDataRepair.findMissingActivityDates",
          cacheKey: `users/${workspaceUid}/${collectionName}?activityRepairAfter=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const rows = snap.docs
        .map((docSnap) => {
          const raw = docSnap.data() || {};
          const proposal = getActivityDateRepairProposal(raw);
          if (!proposal) return null;
          return {
            key: `${collectionName}:${docSnap.id}`,
            collectionName,
            recordId: docSnap.id,
            repair_test_data: raw.repair_test_data === true,
            customerName: raw.customerName || raw.name || "",
            quoteId: raw.quoteId || raw.sourceQuoteId || "",
            quoteNo: raw.quoteNo || raw.quoteNoString || "",
            createdAt: raw.createdAt || null,
            updatedAt: raw.updatedAt || null,
            quoteDate: raw.quoteDate || null,
            date: raw.date || null,
            ...proposal,
          };
        })
        .filter(Boolean);
      setCrmDataRepairState((prev) => ({
        ...prev,
        activityLoading: false,
        activityResults: append ? [...prev.activityResults, ...rows] : rows,
        activityCursor: snap.docs[snap.docs.length - 1] || null,
        activityHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        activityLoading: false,
        activityError: error?.message || "Could not find missing activity dates.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.activityCollection,
    crmDataRepairState.activityCursor,
    getActivityDateRepairProposal,
    notify,
    workspaceUid,
  ]
);

const repairSelectedActivityDates = useCallback(async ({ confirmation = null } = {}) => {
  if (!workspaceUid || !canUseCrmDataRepairTools || crmDataRepairState.repairingActivity) {
    notify("Only workspace owners/admins can repair activity dates.", "warning");
    return;
  }
  const selectedRows = crmDataRepairState.activityResults.filter(
    (row) => crmDataRepairState.activitySelected[row.key]
  );
  if (!selectedRows.length) {
    notify("Select at least one activity date repair row.", "warning");
    return;
  }
  if (selectedRows.length > CRM_DATA_REPAIR_PAGE_LIMIT || selectedRows.some((row) => !canRepairActivityDateRow(row) || !CRM_ACTIVITY_REPAIR_COLLECTIONS.includes(row.collectionName))) {
    notify("Only selected records with a missing activity date and a valid historical proposal can be repaired.", "warning");
    return;
  }
  const selectionSignature = JSON.stringify(selectedRows.map((row) => [row.key, row.source, row.proposedLastActivityAtMs]));
  if (!confirmation) {
    setCrmDataRepairState((prev) => ({ ...prev, activityConfirmation: { selectionSignature, workspaceUid, reviewed: false } }));
    return;
  }
  if (confirmation !== crmDataRepairState.activityConfirmation || !confirmation.reviewed || confirmation.workspaceUid !== workspaceUid || confirmation.selectionSignature !== selectionSignature) {
    notify("Selection changed. Review the activity date preview again.", "warning");
    return;
  }
  setCrmDataRepairState((prev) => ({ ...prev, repairingActivity: true }));
  try {
    const actor = getCrmActorSnapshot();
    const repairLogRefs = selectedRows.map((row) => doc(db, "users", workspaceUid, "repair_logs", `${Date.now()}_${row.collectionName}_${row.recordId}_${Math.random().toString(36).slice(2, 6)}`));
    await runTransaction(db, async (transaction) => {
      // Read all selected documents before writing; retries protect against concurrent edits.
      const snapshots = await Promise.all(selectedRows.map(async (row) => {
        const ref = doc(db, "users", workspaceUid, row.collectionName, row.recordId);
        const snap = await transaction.get(ref);
        recordFirestoreRead({ path: ref.path, label: "crmDataRepair.activityDatePreflight", kind: "transaction.get", stack: new Error().stack });
        if (!snap.exists()) throw new Error("A selected record no longer exists. Run a new dry run.");
        assertFreshActivityDateProposal(snap.data(), row);
        return snap;
      }));
    selectedRows.forEach((row, index) => {
      const before = snapshots[index].data();
      transaction.update(
        doc(db, "users", workspaceUid, row.collectionName, row.recordId),
        sanitizeCrmFirestoreValue({
          lastActivityAt: row.proposedLastActivityAt,
          activityDateRepairedAt: serverTimestamp(),
          activityDateRepairSource: row.source,
          repairedBy: actor.uid || user?.uid || "",
        })
      );
      const repairLogRef = repairLogRefs[index];
      const repairId = repairLogRef.id;
      transaction.set(
        repairLogRef,
        sanitizeCrmFirestoreValue({
          repairId,
          workspaceUid,
          toolName: "repairMissingActivityDates",
          recordType: row.collectionName,
          recordId: row.recordId,
          quoteId: row.quoteId || "",
          quoteNo: row.quoteNo || "",
          before: {
            lastActivityAt: before.lastActivityAt ?? null,
            lastActivityAtWasPresent: Object.prototype.hasOwnProperty.call(before, "lastActivityAt"),
            createdAt: before.createdAt ?? null,
            updatedAt: before.updatedAt ?? null,
            quoteDate: before.quoteDate ?? null,
            date: before.date ?? null,
          },
          after: {
            lastActivityAt: row.proposedLastActivityAt,
            source: row.source,
          },
          dryRun: false,
          performedBy: {
            uid: actor.uid || user?.uid || "",
            name: actor.name || user?.displayName || user?.email || "",
            email: actor.email || user?.email || "",
          },
          performedByUid: actor.uid || user?.uid || "",
          performedByName: actor.name || user?.displayName || user?.email || "",
          performedAt: serverTimestamp(),
          success: true,
          errorCode: "",
          errorMessage: "",
        })
      );
    });
    });
    const repairLogChecks = (
      await Promise.all(
        repairLogRefs.map((repairLogRef) =>
          readCrmDataRepairLogCheck(repairLogRef, [
            "toolName",
            "recordId",
            "before",
            "after",
            "performedBy",
            "performedAt",
            "success",
          ])
        )
      )
    ).filter(Boolean);
    selectedRows.forEach((row) => invalidateWorkspaceCollectionCache(workspaceUid, row.collectionName));
    setCrmDataRepairState((prev) => ({
      ...prev,
      repairLogChecks: repairLogChecks.length
        ? [...repairLogChecks, ...(prev.repairLogChecks || [])].slice(0, 5)
        : prev.repairLogChecks,
      activityResults: prev.activityResults.map((row) =>
        prev.activitySelected[row.key] ? { ...row, repaired: true } : row
      ),
      activitySelected: {},
      activityConfirmation: null,
      repairingActivity: false,
    }));
    notify(`${selectedRows.length} activity date${selectedRows.length === 1 ? "" : "s"} repaired.`, "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({ ...prev, repairingActivity: false, activityConfirmation: null }));
    devError("CRM_DATA_REPAIR_ACTIVITY_DATES_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
      selectedCount: selectedRows.length,
    });
    notify(error?.message || "Could not repair selected activity dates.", "error");
  }
}, [
  canUseCrmDataRepairTools,
  crmDataRepairState.activityResults,
  crmDataRepairState.activitySelected,
  crmDataRepairState.activityConfirmation,
  crmDataRepairState.repairingActivity,
  getCrmActorSnapshot,
  notify,
  readCrmDataRepairLogCheck,
  user?.displayName,
  user?.email,
  user?.uid,
  workspaceUid,
]);

const getRepairRecordActivityMillis = useCallback((record = {}) => {
  return (
    safeTimeMillis(record.lastActivityAt) ||
    safeTimeMillis(record.updatedAt) ||
    safeTimeMillis(record.createdAt) ||
    0
  );
}, []);

const isRepairArchivedOrDeleted = useCallback((record = {}) => {
  const status = String(record.status || record.dealStatus || record.projectStatus || "").toLowerCase();
  return (
    record.archived === true ||
    record.deleted === true ||
    record.isArchivedDuplicate === true ||
    status.includes("archived") ||
    status.includes("deleted") ||
    status.includes("cancelled") ||
    status.includes("canceled")
  );
}, []);

const getRepairQuoteReference = useCallback((record = {}) => {
  const summary =
    typeof getCrmLeadQuoteReferenceSummary === "function"
      ? getCrmLeadQuoteReferenceSummary(record)
      : {};
  return {
    quoteId: String(
      record.quoteId ||
        record.latestQuoteId ||
        record.primaryQuoteId ||
        record.sourceQuoteId ||
        record.crm?.quoteId ||
        summary.primaryQuoteId ||
        ""
    ).trim(),
    quoteNo: String(
      record.quoteNo ||
        record.latestQuoteNo ||
        record.primaryQuoteNo ||
        record.quoteNoString ||
        record.crm?.quoteNo ||
        summary.primaryQuoteNo ||
        ""
    ).trim(),
  };
}, []);

const choosePrimaryRepairRecord = useCallback(
  (records = [], preferredId = "") => {
    const cleanPreferredId = String(preferredId || "").trim();
    const uniqueRecords = Array.from(
      new Map(
        (records || [])
          .filter((record) => record?.id)
          .map((record) => [String(record.id), record])
      ).values()
    );
    if (!uniqueRecords.length) return null;
    const preferred = cleanPreferredId
      ? uniqueRecords.find((record) => String(record.id) === cleanPreferredId)
      : null;
    if (preferred && !isRepairArchivedOrDeleted(preferred)) return preferred;

    return [...uniqueRecords].sort((a, b) => {
      const activeDelta =
        Number(isRepairArchivedOrDeleted(a)) - Number(isRepairArchivedOrDeleted(b));
      if (activeDelta !== 0) return activeDelta;
      const aCompleteness =
        Number(Boolean(a.customerId || a.customerKey)) +
        Number(Boolean(a.quoteId || a.latestQuoteId || a.primaryQuoteId)) +
        Number(Boolean(a.quoteNo || a.latestQuoteNo || a.primaryQuoteNo));
      const bCompleteness =
        Number(Boolean(b.customerId || b.customerKey)) +
        Number(Boolean(b.quoteId || b.latestQuoteId || b.primaryQuoteId)) +
        Number(Boolean(b.quoteNo || b.latestQuoteNo || b.primaryQuoteNo));
      if (aCompleteness !== bCompleteness) return bCompleteness - aCompleteness;
      const activityDelta = getRepairRecordActivityMillis(b) - getRepairRecordActivityMillis(a);
      if (activityDelta !== 0) return activityDelta;
      const createdDelta = safeTimeMillis(a.createdAt) - safeTimeMillis(b.createdAt);
      if (createdDelta !== 0) return createdDelta;
      return String(a.id || "").localeCompare(String(b.id || ""));
    })[0];
  },
  [getRepairRecordActivityMillis, isRepairArchivedOrDeleted]
);

const findDuplicateDealsForRepair = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    const cursor = append ? crmDataRepairState.duplicateDealsCursor : null;
    setCrmDataRepairState((prev) => ({
      ...prev,
      duplicateDealsLoading: true,
      duplicateDealsError: "",
      ...(append ? {} : { duplicateDealResults: [], duplicateDealsCursor: null }),
    }));
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, "crm_leads"), ...constraints),
        {
          label: "crmDataRepair.findDuplicateDealsForQuotes",
          cacheKey: `users/${workspaceUid}/crm_leads?duplicateDealsAfter=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const deals = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      const groupMap = new Map();
      deals.forEach((deal) => {
        if (!deal?.id || isRepairArchivedOrDeleted(deal)) return;
        const { quoteId, quoteNo } = getRepairQuoteReference(deal);
        const keys = [
          quoteId ? `quoteId:${quoteId}` : "",
          quoteNo ? `quoteNo:${normalizeSearchCompact(quoteNo)}` : "",
        ].filter(Boolean);
        keys.forEach((key) => {
          if (!groupMap.has(key)) {
            groupMap.set(key, { key, quoteId, quoteNo, deals: [] });
          }
          groupMap.get(key).deals.push(deal);
        });
      });

      const rows = [];
      for (const group of groupMap.values()) {
        const uniqueDeals = Array.from(
          new Map(group.deals.map((deal) => [String(deal.id), deal])).values()
        );
        if (uniqueDeals.length < 2) continue;
        let linkedDealId = "";
        const quoteIndexConstraints = [];
        if (group.quoteId) {
          quoteIndexConstraints.push(["quoteId", group.quoteId]);
          quoteIndexConstraints.push(["sourceQuoteId", group.quoteId]);
        }
        if (group.quoteNo) quoteIndexConstraints.push(["quoteNo", group.quoteNo]);
        for (const [fieldName, value] of quoteIndexConstraints.slice(0, 2)) {
          const indexSnap = await getDocs(
            firestoreQuery(
              collection(db, "users", workspaceUid, "quote_index"),
              firestoreWhere(fieldName, "==", value),
              firestoreLimit(3)
            ),
            {
              label: "crmDataRepair.findDuplicateDeals.quoteIndexLink",
              cacheKey: `users/${workspaceUid}/quote_index?dupDeal_${fieldName}=${value}&limit=3`,
              ttlMs: 10 * 1000,
            }
          ).catch(() => null);
          const linked = indexSnap?.docs
            ?.map((item) => item.data() || {})
            ?.map((item) => item.crmLeadId || item.leadId || item.opportunityId || item.dealId)
            ?.map((id) => String(id || "").trim())
            ?.find((id) => uniqueDeals.some((deal) => deal.id === id));
          if (linked) {
            linkedDealId = linked;
            break;
          }
        }
        const primary = choosePrimaryRepairRecord(uniqueDeals, linkedDealId);
        rows.push({
          key: group.key,
          quoteId: group.quoteId,
          quoteNo: group.quoteNo,
          repair_test_data: uniqueDeals.every((deal) => deal.repair_test_data === true),
          customerId:
            primary?.customerId ||
            uniqueDeals.find((deal) => deal.customerId)?.customerId ||
            "",
          customerName:
            primary?.customerName ||
            primary?.title ||
            uniqueDeals.find((deal) => deal.customerName)?.customerName ||
            "",
          dealIds: uniqueDeals.map((deal) => deal.id),
          recommendedPrimaryDealId: primary?.id || "",
          duplicateDealIds: uniqueDeals
            .map((deal) => deal.id)
            .filter((dealId) => dealId !== primary?.id),
          reason: linkedDealId
            ? "Primary deal matches quote_index link."
            : "Multiple active CRM deals share the same exact quote reference in this bounded page.",
          risk: uniqueDeals.some((deal) => deal.followUps?.length || deal.attachments?.length || deal.tasks?.length) ? "Manual Only" : linkedDealId ? "Safe" : "Needs Review",
          deals: uniqueDeals.map((deal) => ({
            dealId: deal.id,
            customerName: deal.customerName || deal.title || "",
            status: deal.status || deal.dealStatus || "",
            lastActivityAt: deal.lastActivityAt || deal.updatedAt || deal.createdAt || null,
            hasUniqueWork: Boolean(
              String(deal.notes || "").trim() ||
                (Array.isArray(deal.followUps) && deal.followUps.length) ||
                (Array.isArray(deal.attachments) && deal.attachments.length) ||
                (Array.isArray(deal.tasks) && deal.tasks.length)
            ),
          })),
        });
      }
      setCrmDataRepairState((prev) => ({
        ...prev,
        duplicateDealsLoading: false,
        duplicateDealResults: append ? [...prev.duplicateDealResults, ...rows] : rows,
        duplicateDealsCursor: snap.docs[snap.docs.length - 1] || null,
        duplicateDealsHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        duplicateDealsLoading: false,
        duplicateDealsError: error?.message || "Could not scan duplicate CRM deals.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    choosePrimaryRepairRecord,
    crmDataRepairState.duplicateDealsCursor,
    getRepairQuoteReference,
    isRepairArchivedOrDeleted,
    notify,
    workspaceUid,
  ]
);

const archiveDuplicateDealGroup = useCallback(
  async (group = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools || !group?.recommendedPrimaryDealId) {
      notify("Only workspace owners/admins can archive duplicate deals.", "warning");
      return;
    }
    const duplicateDealIds = (group.duplicateDealIds || []).filter(
      (dealId) => dealId && dealId !== group.recommendedPrimaryDealId
    );
    if (!duplicateDealIds.length) return;
    if (!crmDataRepairState.duplicateDealSelected[group.key] || !crmDataRepairState.duplicateDealResults.some((row) => row.key === group.key)) {
      notify("Run a dry run and select a duplicate group first.", "warning");
      return;
    }
    const confirmed = confirmGuidedRepair(group, "This will archive duplicate records. It will not delete them.");
    if (!confirmed) return;
    const repairKey = group.key || group.recommendedPrimaryDealId;
    setCrmDataRepairState((prev) => ({ ...prev, repairingDuplicateDealKey: repairKey }));
    try {
      const primaryRef = doc(
        db,
        "users",
        workspaceUid,
        "crm_leads",
        group.recommendedPrimaryDealId
      );
      const primarySnap = await getDoc(primaryRef, {
        label: "crmDataRepair.archiveDuplicateDeals.primary",
        cacheKey: `users/${workspaceUid}/crm_leads/${group.recommendedPrimaryDealId}:dupDealPrimary`,
        ttlMs: 0,
      });
      if (!primarySnap.exists()) throw new Error("Primary CRM deal was not found.");
      const duplicateSnaps = await Promise.all(
        duplicateDealIds.map((dealId) =>
          getDoc(doc(db, "users", workspaceUid, "crm_leads", dealId), {
            label: "crmDataRepair.archiveDuplicateDeals.duplicate",
            cacheKey: `users/${workspaceUid}/crm_leads/${dealId}:dupDealArchive`,
            ttlMs: 0,
          })
        )
      );
      const missing = duplicateSnaps.find((snap) => !snap.exists());
      if (missing) throw new Error("One duplicate CRM deal was not found.");
      const duplicateRecords = duplicateSnaps.map((snap) => ({ id: snap.id, ...(snap.data() || {}) }));
      if (isRepairArchivedOrDeleted(primarySnap.data()) || duplicateRecords.some(isRepairArchivedOrDeleted)) {
        throw new Error("This duplicate group changed since the dry run. Run diagnosis again.");
      }
      const hasUniqueWork = duplicateRecords.some(
        (deal) =>
          String(deal.notes || "").trim() ||
          (Array.isArray(deal.followUps) && deal.followUps.length) ||
          (Array.isArray(deal.attachments) && deal.attachments.length) ||
          (Array.isArray(deal.tasks) && deal.tasks.length)
      );
      if (hasUniqueWork) {
        const confirmUnique =
          typeof window === "undefined" ||
          window.confirm(
            "One or more duplicate CRM deals has notes, follow-ups, tasks, or attachments. Archive them without moving that data?"
          );
        if (!confirmUnique) return;
      }

      const batch = writeBatch(db);
      const actor = getCrmActorSnapshot();
      const archivePatch = sanitizeCrmFirestoreValue({
        archived: true,
        isArchivedDuplicate: true,
        status: "duplicate_archived",
        dealStatus: "duplicate_archived",
        archivedReason: "duplicate_deal_for_quote",
        archivedAt: serverTimestamp(),
        archivedBy: actor.uid || user?.uid || "",
        duplicateOfDealId: group.recommendedPrimaryDealId,
        updatedAt: serverTimestamp(),
      });
      duplicateDealIds.forEach((dealId) => {
        batch.set(doc(db, "users", workspaceUid, "crm_leads", dealId), archivePatch, {
          merge: true,
        });
      });
      batch.set(
        primaryRef,
        sanitizeCrmFirestoreValue({
          duplicateDealsArchived: true,
          duplicateDealIdsArchived: arrayUnion(...duplicateDealIds),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      const repairId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const repairLogRef = doc(db, "users", workspaceUid, "repair_logs", repairId);
      batch.set(
        repairLogRef,
        sanitizeCrmFirestoreValue({
          repairId,
          workspaceUid,
          toolName: "archiveDuplicateQuoteDeals",
          recordType: "crm_leads",
          recordId: group.recommendedPrimaryDealId,
          quoteId: group.quoteId || "",
          quoteNo: group.quoteNo || "",
          before: {
            primaryDeal: { id: primarySnap.id, ...(primarySnap.data() || {}) },
            duplicateDeals: duplicateRecords,
          },
          after: {
            primaryDealId: group.recommendedPrimaryDealId,
            duplicateDealIds,
            archivePatch: { ...archivePatch, archivedAt: "serverTimestamp", updatedAt: "serverTimestamp" },
          },
          dryRun: false,
          performedBy: {
            uid: actor.uid || user?.uid || "",
            name: actor.name || user?.displayName || user?.email || "",
            email: actor.email || user?.email || "",
          },
          performedByUid: actor.uid || user?.uid || "",
          performedByName: actor.name || user?.displayName || user?.email || "",
          performedAt: serverTimestamp(),
          success: true,
          errorCode: "",
          errorMessage: "",
        })
      );
      await batch.commit();
      const repairLogCheck = await readCrmDataRepairLogCheck(repairLogRef, [
        "toolName",
        "recordId",
        "before",
        "after",
        "performedBy",
        "performedAt",
        "success",
      ]);
      invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
      duplicateDealIds.forEach((dealId) =>
        applyCrmLeadLocalPatch(dealId, {
          ...archivePatch,
          archivedAt: new Date(),
          updatedAt: new Date(),
        })
      );
      setCrmDataRepairState((prev) => ({
        ...prev,
        repairLogChecks: repairLogCheck
          ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
          : prev.repairLogChecks,
        duplicateDealResults: prev.duplicateDealResults.filter(
          (item) => item.key !== group.key
        ),
      }));
      notify("Duplicate CRM deals archived.", "success");
    } catch (error) {
      await logCrmDataRepairAction({
        toolName: "archiveDuplicateQuoteDeals",
        recordType: "crm_leads",
        recordId: group.recommendedPrimaryDealId || "",
        quoteId: group.quoteId || "",
        quoteNo: group.quoteNo || "",
        before: group,
        after: { duplicateDealIds },
        success: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      }).catch(() => null);
      notify(error?.message || "Could not archive duplicate deals.", "error");
    } finally {
      setCrmDataRepairState((prev) => ({ ...prev, repairingDuplicateDealKey: "" }));
    }
  },
  [
    applyCrmLeadLocalPatch,
    canUseCrmDataRepairTools,
    crmDataRepairState.duplicateDealResults,
    crmDataRepairState.duplicateDealSelected,
    getCrmActorSnapshot,
    isRepairArchivedOrDeleted,
    logCrmDataRepairAction,
    notify,
    readCrmDataRepairLogCheck,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const findProjectsMissingPipelineStageForRepair = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    const cursor = append ? crmDataRepairState.projectStageCursor : null;
    setCrmDataRepairState((prev) => ({
      ...prev,
      projectStageRepairLoading: true,
      projectStageRepairError: "",
      ...(append ? {} : { projectStageResults: [], projectStageCursor: null, projectStageSelected: {} }),
    }));
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, "projects"), ...constraints),
        {
          label: "crmDataRepair.findProjectsMissingPipelineStage",
          cacheKey: `users/${workspaceUid}/projects?missingStageAfter=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const orderStages = normalizeOrderPipeline(orderPipeline).stages || [];
      const activeStageIds = new Set(
        orderStages
          .filter((stage) => stage.active !== false)
          .map((stage) => String(stage.stageId || stage.id || "").trim())
          .filter(Boolean)
      );
      const rows = snap.docs
        .map((docSnap) => {
          const raw = { id: docSnap.id, ...(docSnap.data() || {}) };
          const suggestedStage = resolveProjectPipelineStage(raw);
          const stageExists = activeStageIds.has(suggestedStage);
          const missing =
            !raw.projectStage ||
            !raw.orderStage ||
            !raw.pipelineId ||
            raw.pipelineId !== "orders" ||
            !stageExists;
          if (!missing || isRepairArchivedOrDeleted(raw)) return null;
          return {
            key: docSnap.id,
            projectId: docSnap.id,
            repair_test_data: raw.repair_test_data === true,
            quoteId: raw.quoteId || raw.sourceQuoteId || "",
            quoteNo: raw.quoteNo || raw.quoteNoString || "",
            customerName: raw.customerName || raw.title || "",
            currentStage: raw.stage || "",
            projectStage: raw.projectStage || "",
            orderStage: raw.orderStage || "",
            pipelineId: raw.pipelineId || "",
            suggestedStage,
            suggestedStageLabel: getOrderPipelineStageLabel(orderPipeline, suggestedStage),
            reason: !stageExists
              ? "Current project/order stage is missing from active Orders pipeline stages."
              : "Project is missing one or more Orders pipeline fields.",
            risk: stageExists ? "Safe" : "Needs Review",
            lastActivityAt: raw.lastActivityAt || raw.updatedAt || raw.createdAt || null,
            updatedAt: raw.updatedAt || null,
            createdAt: raw.createdAt || null,
          };
        })
        .filter(Boolean);
      setCrmDataRepairState((prev) => ({
        ...prev,
        projectStageRepairLoading: false,
        projectStageResults: append ? [...prev.projectStageResults, ...rows] : rows,
        projectStageCursor: snap.docs[snap.docs.length - 1] || null,
        projectStageHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        projectStageRepairLoading: false,
        projectStageRepairError: error?.message || "Could not scan project pipeline stages.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.projectStageCursor,
    isRepairArchivedOrDeleted,
    notify,
    orderPipeline,
    workspaceUid,
  ]
);

const repairSelectedProjectPipelineStages = useCallback(async () => {
  if (!workspaceUid || !canUseCrmDataRepairTools || crmDataRepairState.repairingProjectStage) {
    notify("Only workspace owners/admins can repair project stages.", "warning");
    return;
  }
  const selectedRows = crmDataRepairState.projectStageResults.filter(
    (row) => crmDataRepairState.projectStageSelected[row.key]
  );
  if (!selectedRows.length) {
    notify("Select at least one project stage repair row.", "warning");
    return;
  }
  const confirmed = confirmGuidedRepair(selectedRows, "This will update selected project pipeline stage fields only.");
  if (!confirmed) return;
  setCrmDataRepairState((prev) => ({ ...prev, repairingProjectStage: true }));
  try {
    const batch = writeBatch(db);
    const actor = getCrmActorSnapshot();
    const repairLogRefs = [];
    for (const row of selectedRows) {
      const projectRef = doc(db, "users", workspaceUid, "projects", row.projectId);
      const projectSnap = await getDoc(projectRef, {
        label: "crmDataRepair.repairProjectPipelineStage.project",
        cacheKey: `users/${workspaceUid}/projects/${row.projectId}:pipelineRepair`,
        ttlMs: 0,
      });
      if (!projectSnap.exists()) throw new Error("A selected project no longer exists. Run a new dry run.");
      const before = projectSnap.data() || {};
      const fallbackLastActivityAt = before.lastActivityAt || before.updatedAt || before.createdAt || null;
      const patch = sanitizeCrmFirestoreValue({
        projectStage: row.suggestedStage,
        orderStage: row.suggestedStage,
        pipelineId: "orders",
        stage: row.suggestedStage,
        lastActivityAt: fallbackLastActivityAt,
        updatedAt: serverTimestamp(),
        pipelineStageRepairedAt: serverTimestamp(),
        repairedBy: actor.uid || user?.uid || "",
      });
      batch.set(projectRef, patch, { merge: true });
      const repairId = `${Date.now()}_${row.projectId}_${Math.random().toString(36).slice(2, 6)}`;
      const repairLogRef = doc(db, "users", workspaceUid, "repair_logs", repairId);
      repairLogRefs.push(repairLogRef);
      batch.set(
        repairLogRef,
        sanitizeCrmFirestoreValue({
          repairId,
          workspaceUid,
          toolName: "repairProjectPipelineStage",
          recordType: "projects",
          recordId: row.projectId,
          quoteId: row.quoteId || before.quoteId || "",
          quoteNo: row.quoteNo || before.quoteNo || "",
          before: {
            projectStage: before.projectStage || "",
            orderStage: before.orderStage || "",
            pipelineId: before.pipelineId || "",
            stage: before.stage || "",
            lastActivityAt: before.lastActivityAt || null,
          },
          after: {
            projectStage: row.suggestedStage,
            orderStage: row.suggestedStage,
            pipelineId: "orders",
            stage: row.suggestedStage,
            lastActivityAt: fallbackLastActivityAt,
          },
          dryRun: false,
          performedBy: {
            uid: actor.uid || user?.uid || "",
            name: actor.name || user?.displayName || user?.email || "",
            email: actor.email || user?.email || "",
          },
          performedByUid: actor.uid || user?.uid || "",
          performedByName: actor.name || user?.displayName || user?.email || "",
          performedAt: serverTimestamp(),
          success: true,
          errorCode: "",
          errorMessage: "",
        })
      );
    }
    await batch.commit();
    const repairLogChecks = (
      await Promise.all(
        repairLogRefs.map((repairLogRef) =>
          readCrmDataRepairLogCheck(repairLogRef, [
            "toolName",
            "recordId",
            "before",
            "after",
            "performedBy",
            "performedAt",
            "success",
          ])
        )
      )
    ).filter(Boolean);
    invalidateWorkspaceCollectionCache(workspaceUid, "projects");
    setCrmDataRepairState((prev) => ({
      ...prev,
      repairLogChecks: repairLogChecks.length
        ? [...repairLogChecks, ...(prev.repairLogChecks || [])].slice(0, 5)
        : prev.repairLogChecks,
      projectStageResults: prev.projectStageResults.map((row) =>
        prev.projectStageSelected[row.key] ? { ...row, repaired: true } : row
      ),
      projectStageSelected: {},
      repairingProjectStage: false,
    }));
    notify(`${selectedRows.length} project pipeline stage${selectedRows.length === 1 ? "" : "s"} repaired.`, "success");
  } catch (error) {
    setCrmDataRepairState((prev) => ({ ...prev, repairingProjectStage: false }));
    notify(error?.message || "Could not repair selected project stages.", "error");
  }
}, [
  canUseCrmDataRepairTools,
  crmDataRepairState.projectStageResults,
  crmDataRepairState.projectStageSelected,
  crmDataRepairState.repairingProjectStage,
  getCrmActorSnapshot,
  notify,
  readCrmDataRepairLogCheck,
  user?.displayName,
  user?.email,
  user?.uid,
  workspaceUid,
]);

const findDuplicateProjectsForRepair = useCallback(
  async ({ append = false } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can use data repair tools.", "warning");
      return;
    }
    const cursor = append ? crmDataRepairState.duplicateProjectsCursor : null;
    setCrmDataRepairState((prev) => ({
      ...prev,
      duplicateProjectsLoading: true,
      duplicateProjectsError: "",
      ...(append ? {} : { duplicateProjectResults: [], duplicateProjectsCursor: null }),
    }));
    try {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CRM_DATA_REPAIR_PAGE_LIMIT),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));
      const snap = await getDocs(
        firestoreQuery(collection(db, "users", workspaceUid, "projects"), ...constraints),
        {
          label: "crmDataRepair.findDuplicateProjects",
          cacheKey: `users/${workspaceUid}/projects?duplicateProjectsAfter=${cursor?.id || "start"}&limit=${CRM_DATA_REPAIR_PAGE_LIMIT}`,
          ttlMs: 10 * 1000,
        }
      );
      const projects = snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
      const groupMap = new Map();
      projects.forEach((project) => {
        if (!project?.id || isRepairArchivedOrDeleted(project)) return;
        const keys = [
          project.quoteId ? `quoteId:${String(project.quoteId).trim()}` : "",
          project.quoteNo ? `quoteNo:${normalizeSearchCompact(project.quoteNo)}` : "",
          project.dealId ? `dealId:${String(project.dealId).trim()}` : "",
          project.opportunityId ? `opportunityId:${String(project.opportunityId).trim()}` : "",
        ].filter(Boolean);
        keys.forEach((key) => {
          if (!groupMap.has(key)) {
            groupMap.set(key, {
              key,
              quoteId: project.quoteId || "",
              quoteNo: project.quoteNo || "",
              dealId: project.dealId || "",
              opportunityId: project.opportunityId || "",
              projects: [],
            });
          }
          groupMap.get(key).projects.push(project);
        });
      });
      const rows = [];
      for (const group of groupMap.values()) {
        const uniqueProjects = Array.from(
          new Map(group.projects.map((project) => [String(project.id), project])).values()
        );
        if (uniqueProjects.length < 2) continue;
        let linkedProjectId = "";
        if (group.quoteId || group.quoteNo) {
          const fieldName = group.quoteId ? "quoteId" : "quoteNo";
          const value = group.quoteId || group.quoteNo;
          const indexSnap = await getDocs(
            firestoreQuery(
              collection(db, "users", workspaceUid, "quote_index"),
              firestoreWhere(fieldName, "==", value),
              firestoreLimit(3)
            ),
            {
              label: "crmDataRepair.findDuplicateProjects.quoteIndexLink",
              cacheKey: `users/${workspaceUid}/quote_index?dupProject_${fieldName}=${value}&limit=3`,
              ttlMs: 10 * 1000,
            }
          ).catch(() => null);
          linkedProjectId = indexSnap?.docs
            ?.map((item) => item.data() || {})
            ?.map((item) => item.projectId || item.crm?.projectId)
            ?.map((id) => String(id || "").trim())
            ?.find((id) => uniqueProjects.some((project) => project.id === id)) || "";
        }
        const primary = choosePrimaryRepairRecord(uniqueProjects, linkedProjectId);
        rows.push({
          key: group.key,
          quoteId: group.quoteId,
          quoteNo: group.quoteNo,
          dealId: group.dealId,
          opportunityId: group.opportunityId,
          projectIds: uniqueProjects.map((project) => project.id),
          repair_test_data: uniqueProjects.every((project) => project.repair_test_data === true),
          recommendedPrimaryProjectId: primary?.id || "",
          duplicateProjectIds: uniqueProjects
            .map((project) => project.id)
            .filter((projectId) => projectId !== primary?.id),
          customerName: primary?.customerName || "",
          reason: linkedProjectId
            ? "Primary project matches quote_index link."
            : "Multiple active projects share the same exact quote/deal reference in this bounded page.",
          risk: uniqueProjects.some((project) => Number(project.paymentSummary?.totalReceived || project.totalReceived || 0) > 0 || Number(project.snagSummary?.openCount || project.openSnagCount || 0) > 0) ? "Manual Only" : linkedProjectId ? "Safe" : "Needs Review",
          projects: uniqueProjects.map((project) => ({
            projectId: project.id,
            status: project.status || "",
            stage: project.stage || project.projectStage || project.orderStage || "",
            hasUniqueWork: Boolean(
              Number(project.paymentSummary?.totalReceived || project.totalReceived || 0) ||
                Number(project.snagSummary?.openCount || project.openSnagCount || 0) ||
                Number(project.deliveryTracking?.openItemCount || 0) ||
                Number(project.deliveryTracking?.totalItemCount || 0)
            ),
          })),
        });
      }
      setCrmDataRepairState((prev) => ({
        ...prev,
        duplicateProjectsLoading: false,
        duplicateProjectResults: append ? [...prev.duplicateProjectResults, ...rows] : rows,
        duplicateProjectsCursor: snap.docs[snap.docs.length - 1] || null,
        duplicateProjectsHasMore: snap.size === CRM_DATA_REPAIR_PAGE_LIMIT,
      }));
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        duplicateProjectsLoading: false,
        duplicateProjectsError: error?.message || "Could not scan duplicate projects.",
      }));
    }
  },
  [
    canUseCrmDataRepairTools,
    choosePrimaryRepairRecord,
    crmDataRepairState.duplicateProjectsCursor,
    isRepairArchivedOrDeleted,
    notify,
    workspaceUid,
  ]
);

const archiveDuplicateProjectGroup = useCallback(
  async (group = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools || !group?.recommendedPrimaryProjectId) {
      notify("Only workspace owners/admins can archive duplicate projects.", "warning");
      return;
    }
    const duplicateProjectIds = (group.duplicateProjectIds || []).filter(
      (projectId) => projectId && projectId !== group.recommendedPrimaryProjectId
    );
    if (!duplicateProjectIds.length) return;
    if (!crmDataRepairState.duplicateProjectSelected[group.key] || !crmDataRepairState.duplicateProjectResults.some((row) => row.key === group.key)) {
      notify("Run a dry run and select a duplicate group first.", "warning");
      return;
    }
    const confirmed = confirmGuidedRepair(group, "This will archive duplicate records. It will not delete them. Payments, snags and tracking records remain untouched.");
    if (!confirmed) return;
    const repairKey = group.key || group.recommendedPrimaryProjectId;
    setCrmDataRepairState((prev) => ({ ...prev, repairingDuplicateProjectKey: repairKey }));
    try {
      const primaryRef = doc(
        db,
        "users",
        workspaceUid,
        "projects",
        group.recommendedPrimaryProjectId
      );
      const primarySnap = await getDoc(primaryRef, {
        label: "crmDataRepair.archiveDuplicateProjects.primary",
        cacheKey: `users/${workspaceUid}/projects/${group.recommendedPrimaryProjectId}:dupProjectPrimary`,
        ttlMs: 0,
      });
      if (!primarySnap.exists()) throw new Error("Primary project was not found.");
      const duplicateSnaps = await Promise.all(
        duplicateProjectIds.map((projectId) =>
          getDoc(doc(db, "users", workspaceUid, "projects", projectId), {
            label: "crmDataRepair.archiveDuplicateProjects.duplicate",
            cacheKey: `users/${workspaceUid}/projects/${projectId}:dupProjectArchive`,
            ttlMs: 0,
          })
        )
      );
      const missing = duplicateSnaps.find((snap) => !snap.exists());
      if (missing) throw new Error("One duplicate project was not found.");
      const duplicateRecords = duplicateSnaps.map((snap) => ({ id: snap.id, ...(snap.data() || {}) }));
      if (isRepairArchivedOrDeleted(primarySnap.data()) || duplicateRecords.some(isRepairArchivedOrDeleted)) {
        throw new Error("This duplicate group changed since the dry run. Run diagnosis again.");
      }
      const hasUniqueWork = duplicateRecords.some(
        (project) =>
          Number(project.paymentSummary?.totalReceived || project.totalReceived || 0) ||
          Number(project.snagSummary?.openCount || project.openSnagCount || 0) ||
          Number(project.deliveryTracking?.openItemCount || 0) ||
          Number(project.deliveryTracking?.totalItemCount || 0)
      );
      if (hasUniqueWork) {
        const confirmUnique =
          typeof window === "undefined" ||
          window.confirm(
            "One or more duplicate projects has payments, snags, or tracking records. Archive the project document without moving/deleting that data?"
          );
        if (!confirmUnique) return;
      }
      const batch = writeBatch(db);
      const actor = getCrmActorSnapshot();
      const archivePatch = sanitizeCrmFirestoreValue({
        archived: true,
        status: "duplicate_archived",
        archivedReason: "duplicate_project_for_quote_or_deal",
        archivedAt: serverTimestamp(),
        archivedBy: actor.uid || user?.uid || "",
        duplicateOfProjectId: group.recommendedPrimaryProjectId,
        updatedAt: serverTimestamp(),
      });
      duplicateProjectIds.forEach((projectId) => {
        batch.set(doc(db, "users", workspaceUid, "projects", projectId), archivePatch, {
          merge: true,
        });
      });
      batch.set(
        primaryRef,
        sanitizeCrmFirestoreValue({
          duplicateProjectsArchived: true,
          duplicateProjectIdsArchived: arrayUnion(...duplicateProjectIds),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      const repairId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const repairLogRef = doc(db, "users", workspaceUid, "repair_logs", repairId);
      batch.set(
        repairLogRef,
        sanitizeCrmFirestoreValue({
          repairId,
          workspaceUid,
          toolName: "archiveDuplicateProjects",
          recordType: "projects",
          recordId: group.recommendedPrimaryProjectId,
          quoteId: group.quoteId || "",
          quoteNo: group.quoteNo || "",
          before: {
            primaryProject: { id: primarySnap.id, ...(primarySnap.data() || {}) },
            duplicateProjects: duplicateRecords,
          },
          after: {
            primaryProjectId: group.recommendedPrimaryProjectId,
            duplicateProjectIds,
            archivePatch: { ...archivePatch, archivedAt: "serverTimestamp", updatedAt: "serverTimestamp" },
          },
          dryRun: false,
          performedBy: {
            uid: actor.uid || user?.uid || "",
            name: actor.name || user?.displayName || user?.email || "",
            email: actor.email || user?.email || "",
          },
          performedByUid: actor.uid || user?.uid || "",
          performedByName: actor.name || user?.displayName || user?.email || "",
          performedAt: serverTimestamp(),
          success: true,
          errorCode: "",
          errorMessage: "",
        })
      );
      await batch.commit();
      const repairLogCheck = await readCrmDataRepairLogCheck(repairLogRef, [
        "toolName",
        "recordId",
        "before",
        "after",
        "performedBy",
        "performedAt",
        "success",
      ]);
      invalidateWorkspaceCollectionCache(workspaceUid, "projects");
      setCrmDataRepairState((prev) => ({
        ...prev,
        repairLogChecks: repairLogCheck
          ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
          : prev.repairLogChecks,
        duplicateProjectResults: prev.duplicateProjectResults.filter(
          (item) => item.key !== group.key
        ),
      }));
      notify("Duplicate projects archived.", "success");
    } catch (error) {
      await logCrmDataRepairAction({
        toolName: "archiveDuplicateProjects",
        recordType: "projects",
        recordId: group.recommendedPrimaryProjectId || "",
        quoteId: group.quoteId || "",
        quoteNo: group.quoteNo || "",
        before: group,
        after: { duplicateProjectIds },
        success: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      }).catch(() => null);
      notify(error?.message || "Could not archive duplicate projects.", "error");
    } finally {
      setCrmDataRepairState((prev) => ({ ...prev, repairingDuplicateProjectKey: "" }));
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.duplicateProjectResults,
    crmDataRepairState.duplicateProjectSelected,
    getCrmActorSnapshot,
    isRepairArchivedOrDeleted,
    logCrmDataRepairAction,
    notify,
    readCrmDataRepairLogCheck,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const summarizeCustomerSearchIndexDocForRepair = useCallback((data = null) => {
  if (!data) {
    return {
      exists: false,
      displayName: "",
      phoneNormalized: "",
      emailCanonical: "",
      active: false,
      deleted: false,
      archived: false,
      searchTokenCount: 0,
      searchPrefixCount: 0,
      updatedAt: null,
    };
  }
  return {
    exists: true,
    displayName: data.displayName || data.customerName || "",
    phoneNormalized: data.phoneNormalized || data.normalizedPhone || "",
    emailCanonical: data.emailCanonical || data.normalizedEmail || "",
    active: data.active !== false,
    deleted: data.deleted === true,
    archived: data.archived === true,
    searchTokenCount: Array.isArray(data.searchTokens)
      ? data.searchTokens.length
      : Array.isArray(data.searchKeywords)
      ? data.searchKeywords.length
      : 0,
    searchPrefixCount: Array.isArray(data.searchPrefixes) ? data.searchPrefixes.length : 0,
    updatedAt: data.updatedAt || null,
  };
}, []);

const searchCustomersForIndexRepair = useCallback(
  async (queryText = "") => {
    const normalizedQuery = normalizeCustomerSearchText(queryText);
    const normalizedPhoneQuery = normalizeCustomerSearchPhone(queryText);
    const normalizedEmailQuery = normalizeEmailValue(queryText);
    if (!workspaceUid || !canUseCrmDataRepairTools || normalizedQuery.length < 2) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        customerIndexSearchLoading: false,
        customerIndexSearchResults: [],
        customerIndexSearchError: "",
        customerIndexSearchSearched: false,
      }));
      return;
    }

    setCrmDataRepairState((prev) => ({
      ...prev,
      customerIndexSearchLoading: true,
      customerIndexSearchError: "",
      customerIndexSearchSearched: true,
    }));

    try {
      const rawResults = await globalSearchCustomerPeople(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: "crmDataRepair.customerIndexPicker",
      });
      const resultMap = new Map();
      (Array.isArray(rawResults) ? rawResults : []).forEach((record) => {
        const normalized = normalizeCustomerSearchPersonForQuote(record);
        const customerId = String(
          record.customerId ||
            record.customerKey ||
            normalized.customerId ||
            (Array.isArray(record.customerIds) ? record.customerIds[0] : "") ||
            ""
        ).trim();
        if (!customerId || record.deleted === true || record.archived === true || record.active === false) {
          return;
        }
        const searchText = buildCustomerLedgerSearchText({
          ...normalized,
          customerName: normalized.customerName || record.displayName || record.customerName || "",
          phone: normalized.phone || record.phone || "",
          email: normalized.email || record.email || "",
        });
        const phoneMatches =
          normalizedPhoneQuery &&
          [
            normalized.normalizedPhone,
            normalized.phoneNormalized,
            normalized.phone,
            record.normalizedPhone,
            record.phoneNormalized,
            record.phone,
            record.whatsapp,
          ]
            .map(normalizeCustomerSearchPhone)
            .some((value) => value.startsWith(normalizedPhoneQuery));
        const emailMatches =
          normalizedEmailQuery &&
          [
            normalized.email,
            normalized.normalizedEmail,
            normalized.emailCanonical,
            normalized.emailLower,
            record.email,
            record.customerEmail,
            record.normalizedEmail,
            record.emailCanonical,
            record.emailLower,
          ]
            .map(normalizeEmailValue)
            .some((value) => value.startsWith(normalizedEmailQuery));
        if (!searchText.includes(normalizedQuery) && !phoneMatches && !emailMatches) return;
        if (!resultMap.has(customerId)) {
          resultMap.set(customerId, {
            customerId,
            customerSearchIndexId: record.personId || record.id || normalized.customerSearchIndexId || "",
            displayName: normalized.customerName || record.displayName || record.customerName || "",
            phone: normalized.phone || record.phone || record.whatsapp || "",
            email: normalized.email || record.email || "",
            customerType: getCustomerPersonBadgeLabel({
              ...record,
              ...normalized,
              customerId,
              hasCustomer: true,
            }),
            sourceTypes: Array.isArray(record.sourceTypes) ? record.sourceTypes : normalized.sourceTypes || [],
            active: record.active !== false,
            deleted: record.deleted === true,
            archived: record.archived === true,
            lastActivityAt: normalized.lastActivityAt || record.lastActivityAt || record.updatedAt || record.createdAt || null,
          });
        }
      });

      const results = Array.from(resultMap.values())
        .sort((a, b) => getRecordSortTime(b, ["lastActivityAt"]) - getRecordSortTime(a, ["lastActivityAt"]))
        .slice(0, CUSTOMER_SEARCH_RESULT_LIMIT);

      setCrmDataRepairState((prev) => ({
        ...prev,
        customerIndexSearchLoading: false,
        customerIndexSearchResults: results,
        customerIndexSearchError: "",
        customerIndexSearchSearched: true,
      }));
    } catch (error) {
      devError("Customer index repair picker search failed", error);
      setCrmDataRepairState((prev) => ({
        ...prev,
        customerIndexSearchLoading: false,
        customerIndexSearchResults: [],
        customerIndexSearchError: error?.message || "Could not search customers.",
        customerIndexSearchSearched: true,
      }));
    }
  },
  [canUseCrmDataRepairTools, workspaceUid]
);

useEffect(() => {
  if (!canUseCrmDataRepairTools) return undefined;
  const queryText = crmDataRepairState.customerIndexSearchQuery || "";
  const timerId = window.setTimeout(() => {
    searchCustomersForIndexRepair(queryText);
  }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  return () => window.clearTimeout(timerId);
}, [canUseCrmDataRepairTools, crmDataRepairState.customerIndexSearchQuery, searchCustomersForIndexRepair]);

const rebuildCustomerSearchIndexForRepair = useCallback(
  async ({ dryRun = true } = {}) => {
    if (!workspaceUid || !canUseCrmDataRepairTools) {
      notify("Only workspace owners/admins can repair customer search index.", "warning");
      return;
    }
    const selectedCustomer = crmDataRepairState.customerIndexSelectedCustomer || null;
    const customerId = String(selectedCustomer?.customerId || crmDataRepairState.customerIndexRepairId || "").trim();
    if (!selectedCustomer?.customerId || !customerId) {
      notify("Select a customer from the search results first.", "warning");
      return;
    }
    if (!dryRun) {
      if (!crmDataRepairState.customerIndexRepairResult?.dryRun || crmDataRepairState.customerIndexRepairResult?.customerId !== customerId) {
        notify("Run a dry run for the selected customer first.", "warning");
        return;
      }
      const confirmed = confirmGuidedRepair(selectedCustomer, "This will rebuild the search index for this one customer only.");
      if (!confirmed) return;
    }
    setCrmDataRepairState((prev) => ({
      ...prev,
      customerIndexRepairLoading: true,
      customerIndexRepairError: "",
      customerIndexRepairResult: null,
    }));
    try {
      const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
      const customerSnap = await getDoc(customerRef, {
        label: "crmDataRepair.rebuildCustomerSearchIndex.customer",
        cacheKey: `users/${workspaceUid}/customers/${customerId}:customerIndexRepair`,
        ttlMs: 0,
      });
      if (!customerSnap.exists()) throw new Error("Customer record was not found.");
      const customer = { id: customerSnap.id, ...(customerSnap.data() || {}) };
      const beforeSnap = await getDoc(
        doc(db, "users", workspaceUid, "customer_search_index", customerId),
        {
          label: "crmDataRepair.rebuildCustomerSearchIndex.before",
          cacheKey: `users/${workspaceUid}/customer_search_index/${customerId}:beforeRepair`,
          ttlMs: 0,
        }
      ).catch(() => null);
      const prepared = buildCustomerSearchIndexRecord(
        {
          ...customer,
          customerId,
          customerKey: customer.customerKey || customerId,
          workspaceUid,
        },
        { sourceType: "customer", workspaceUid }
      );
      const payload = sanitizeCrmFirestoreValue({
        ...prepared,
        id: customerId,
        personId: customerId,
        customerId,
        customerKey: customerId,
        workspaceUid,
        sourceTypes: arrayUnion("customer"),
        customerIds: arrayUnion(customerId),
        updatedAt: serverTimestamp(),
      });
      const result = {
        customerId,
        repair_test_data: customer.repair_test_data === true,
        displayName: payload.displayName || payload.customerName || "",
        phone: payload.phone || payload.primaryPhone || "",
        phoneNormalized: payload.phoneNormalized || payload.normalizedPhone || "",
        email: payload.email || "",
        emailCanonical: payload.emailCanonical || payload.normalizedEmail || "",
        active: payload.active !== false,
        deleted: payload.deleted === true,
        archived: payload.archived === true,
        searchTokenCount: Array.isArray(payload.searchTokens)
          ? payload.searchTokens.length
          : Array.isArray(payload.searchKeywords)
          ? payload.searchKeywords.length
          : 0,
        searchPrefixCount: Array.isArray(payload.searchPrefixes) ? payload.searchPrefixes.length : 0,
        quoteSummary: "Quote summary not rebuilt in this tool.",
        before: summarizeCustomerSearchIndexDocForRepair(
          beforeSnap?.exists?.() ? beforeSnap.data() || {} : null
        ),
        after: {
          exists: true,
          displayName: payload.displayName || payload.customerName || "",
          phoneNormalized: payload.phoneNormalized || payload.normalizedPhone || "",
          emailCanonical: payload.emailCanonical || payload.normalizedEmail || "",
          active: payload.active !== false,
          deleted: payload.deleted === true,
          archived: payload.archived === true,
          searchTokenCount: Array.isArray(payload.searchTokens)
            ? payload.searchTokens.length
            : Array.isArray(payload.searchKeywords)
            ? payload.searchKeywords.length
            : 0,
          searchPrefixCount: Array.isArray(payload.searchPrefixes) ? payload.searchPrefixes.length : 0,
        },
        readBack: null,
        dryRun,
      };
      if (!dryRun) {
        const batch = writeBatch(db);
        batch.set(doc(db, "users", workspaceUid, "customer_search_index", customerId), payload, {
          merge: true,
        });
        const actor = getCrmActorSnapshot();
        const repairId = `${Date.now()}_${customerId}_${Math.random().toString(36).slice(2, 6)}`;
        const repairLogRef = doc(db, "users", workspaceUid, "repair_logs", repairId);
        batch.set(
          repairLogRef,
          sanitizeCrmFirestoreValue({
            repairId,
            workspaceUid,
            toolName: "rebuildCustomerSearchIndexForCustomer",
            recordType: "customer_search_index",
            recordId: customerId,
            quoteId: "",
            quoteNo: "",
            before: beforeSnap?.exists?.() ? beforeSnap.data() || {} : {},
            after: {
              customerId,
              displayName: payload.displayName || payload.customerName || "",
              nameNormalized: payload.nameNormalized || payload.normalizedName || "",
              phoneNormalized: payload.phoneNormalized || payload.normalizedPhone || "",
              emailCanonical: payload.emailCanonical || payload.normalizedEmail || "",
              searchTokenCount: result.searchTokenCount,
              searchPrefixCount: result.searchPrefixCount,
              active: payload.active !== false,
              deleted: payload.deleted === true,
              archived: payload.archived === true,
            },
            dryRun: false,
            performedBy: {
              uid: actor.uid || user?.uid || "",
              name: actor.name || user?.displayName || user?.email || "",
              email: actor.email || user?.email || "",
            },
            performedByUid: actor.uid || user?.uid || "",
            performedByName: actor.name || user?.displayName || user?.email || "",
            performedAt: serverTimestamp(),
            success: true,
            errorCode: "",
            errorMessage: "",
          })
        );
        await batch.commit();
        const readBackSnap = await getDoc(
          doc(db, "users", workspaceUid, "customer_search_index", customerId),
          {
            label: "crmDataRepair.rebuildCustomerSearchIndex.readBack",
            cacheKey: `users/${workspaceUid}/customer_search_index/${customerId}:readBack:${Date.now()}`,
            ttlMs: 0,
          }
        ).catch(() => null);
        result.readBack = summarizeCustomerSearchIndexDocForRepair(
          readBackSnap?.exists?.() ? readBackSnap.data() || {} : null
        );
        const repairLogCheck = await readCrmDataRepairLogCheck(repairLogRef, [
          "toolName",
          "recordId",
          "before",
          "after",
          "performedBy",
          "performedAt",
          "success",
        ]);
        invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
        quotationCustomerSearchCacheRef.current.clear();
        setCrmDataRepairState((prev) => ({
          ...prev,
          repairLogChecks: repairLogCheck
            ? [repairLogCheck, ...(prev.repairLogChecks || [])].slice(0, 5)
            : prev.repairLogChecks,
        }));
      }
      setCrmDataRepairState((prev) => ({
        ...prev,
        customerIndexRepairLoading: false,
        customerIndexRepairResult: result,
      }));
      notify(
        dryRun ? "Customer search index preview ready." : "Customer search index rebuilt.",
        "success"
      );
    } catch (error) {
      setCrmDataRepairState((prev) => ({
        ...prev,
        customerIndexRepairLoading: false,
        customerIndexRepairError: error?.message || "Could not rebuild customer search index.",
      }));
      notify(error?.message || "Could not rebuild customer search index.", "error");
    }
  },
  [
    canUseCrmDataRepairTools,
    crmDataRepairState.customerIndexRepairId,
    crmDataRepairState.customerIndexRepairResult,
    crmDataRepairState.customerIndexSelectedCustomer,
    getCrmActorSnapshot,
    notify,
    readCrmDataRepairLogCheck,
    summarizeCustomerSearchIndexDocForRepair,
    user?.displayName,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const subscribeCrmReminders = useCallback((uid, reason = "global") => {
  if (!uid) return () => {};
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_REMINDER_CHECKS")) {
    if (isDevRuntime) {
      devWarn("Global reminder reads disabled by FQ_DISABLE_REMINDER_CHECKS", {
        workspaceUid: uid,
        reason,
      });
    }
    return () => {};
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const collectionPath = `users/${uid}/crm_reminders`;
  const now = Date.now();
  if (globalReminderCheckedWorkspaceRef.current !== uid) {
    globalReminderCheckedWorkspaceRef.current = uid;
    globalReminderLastCheckRef.current = 0;
    globalReminderShownIdsRef.current.clear();
  }
  if (
    reason !== "force" &&
    globalReminderLastCheckRef.current &&
    now - globalReminderLastCheckRef.current < GLOBAL_REMINDER_CHECK_TTL_MS
  ) {
    if (isDevRuntime) {
      devLog("GLOBAL REMINDER DUE CHECK SKIPPED", {
        reason,
        ageMs: now - globalReminderLastCheckRef.current,
      });
    }
    return () => {};
  }
  globalReminderLastCheckRef.current = now;

  const remindersRef = collection(db, "users", uid, "crm_reminders");
  const lookaheadAt = new Date(now + GLOBAL_REMINDER_LOOKAHEAD_MS);
  const activeRemindersQuery = firestoreQuery(
    remindersRef,
    firestoreWhere("active", "==", true),
    firestoreWhere("nextTriggerAt", "<=", Timestamp.fromDate(lookaheadAt)),
    firestoreOrderBy("nextTriggerAt", "asc"),
    firestoreLimit(GLOBAL_REMINDER_DUE_READ_LIMIT)
  );

  let cancelled = false;
  getDocsCached(activeRemindersQuery, {
    label: "globalReminderDueCheck.active",
    cacheKey: `${collectionPath}?active=true&nextTriggerAt_lte=${lookaheadAt.toISOString()}&limit=${GLOBAL_REMINDER_DUE_READ_LIMIT}`,
    ttlMs: 60 * 1000,
  })
    .then(async (activeSnap) => {
      if (cancelled) return;
      const reminderDocs = new Map();
      activeSnap.docs.forEach((docSnap) => reminderDocs.set(docSnap.id, docSnap));
      const nextReminders = [];

      reminderDocs.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        nextReminders.push({
          id: docSnap.id,
          ...raw,
          active:
            raw.active === true ||
            (raw.status === CRM_REMINDER_STATUS_ACTIVE &&
              (raw.enabled === true || raw.reminderEnabled === true)),
          scheduledAt: normalizeTs(raw.scheduledAt),
          nextTriggerAt: normalizeTs(raw.nextTriggerAt),
          completedAt: normalizeTs(raw.completedAt),
          createdAt: normalizeTs(raw.createdAt),
          updatedAt: normalizeTs(raw.updatedAt),
        });
      });

      setCrmReminderRecords((prev) => {
        const dueIds = new Set(nextReminders.map((reminder) => reminder.id));
        const preserved = (prev || []).filter((reminder) => !dueIds.has(reminder.id));
        return [...nextReminders, ...preserved].filter((reminder) => {
          if (reminder.status === CRM_REMINDER_STATUS_DONE) return false;
          const nextMillis = safeTimeMillis(reminder.nextTriggerAt);
          return !nextMillis || nextMillis <= safeTimeMillis(lookaheadAt);
        });
      });
      setCrmReminderNow(Date.now());
    })
    .catch((error) => {
      if (cancelled) return;
      devError("Global CRM reminders due check failed:", error);
      const message = String(error?.message || "");
      if (error?.code === "failed-precondition" && message.includes("index")) {
        devError("Missing Firestore index for global reminders due check", {
          collection: "crm_reminders",
          fields: ["active ASC", "nextTriggerAt ASC"],
          path: collectionPath,
        });
        if (isDevRuntime) {
          notify("CRM reminder index is missing. Deploy firestore.indexes.json.", "warning");
        }
      }
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      devLog("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmRemindersOnce",
        path: collectionPath,
        reason,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

const subscribeCrmExecutionTemplates = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const templatesRef = collection(db, "users", uid, "execution_templates");

  const collectionPath = `users/${uid}/execution_templates`;
  let cancelled = false;
  executionTemplatesLoadingWorkspaceRef.current = uid;
  setExecutionTemplatesLoadState({
    workspaceUid: uid,
    loading: true,
    loaded: executionTemplatesLoadedWorkspaceRef.current === uid,
    error: "",
  });
  if (isDevRuntime) {
    devLog("EXECUTION TEMPLATE LOAD START", {
      workspaceUid: uid,
      cacheKey: collectionPath,
    });
  }
  getDocsCached(templatesRef, {
    label: "loadCrmExecutionTemplatesOnce",
    cacheKey: collectionPath,
    ttlMs: 5 * 60 * 1000,
  })
    .then((snap) => {
      if (cancelled) return;
      const nextTemplates = normalizeExecutionTemplateDrafts(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: normalizeTs(docSnap.data()?.createdAt),
          updatedAt: normalizeTs(docSnap.data()?.updatedAt),
        }))
      ).sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      executionTemplatesLoadedWorkspaceRef.current = uid;
      executionTemplatesLoadingWorkspaceRef.current = "";
      setExecutionTemplatesLoadState({
        workspaceUid: uid,
        loading: false,
        loaded: true,
        error: "",
      });

      if (isDevRuntime) {
        devLog("EXECUTION TEMPLATE LOAD RESULT", {
          workspaceUid: uid,
          cacheKey: collectionPath,
          returnedCount: snap.size,
          normalizedCount: nextTemplates.length,
          localDirty: executionTemplatesDirtyRef.current,
        });
      }

      if (executionTemplatesDirtyRef.current) {
        if (isDevRuntime) {
          devWarn("EXECUTION TEMPLATE LOAD SKIPPED LOCAL OVERWRITE", {
            workspaceUid: uid,
            reason: "local template draft is dirty",
            returnedCount: nextTemplates.length,
          });
        }
        return;
      }

      setCrmExecutionTemplates(nextTemplates);
      executionTemplatePersistedSignatureRef.current =
        serializeExecutionTemplatesDraftData(nextTemplates);
    })
    .catch((error) => {
      if (cancelled) return;
      executionTemplatesLoadingWorkspaceRef.current = "";
      setExecutionTemplatesLoadState({
        workspaceUid: uid,
        loading: false,
        loaded: executionTemplatesLoadedWorkspaceRef.current === uid,
        error: error?.message || String(error),
      });
      devError("CRM execution templates load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    });

  return () => {
    cancelled = true;
    if (executionTemplatesLoadingWorkspaceRef.current === uid) {
      executionTemplatesLoadingWorkspaceRef.current = "";
    }
    if (isDevRuntime) {
      devLog("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmExecutionTemplatesOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify, serializeExecutionTemplatesDraftData]);

const subscribeCrmTaskHistory = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const historyRef = collection(db, "users", uid, "task_history");
  const historyQuery = firestoreQuery(
    historyRef,
    firestoreOrderBy("completedAt", "desc"),
    firestoreLimit(CRM_TASK_HISTORY_INITIAL_READ_LIMIT)
  );

  const collectionPath = `users/${uid}/task_history`;
  let cancelled = false;
  getDocsCached(historyQuery, {
    label: "loadCrmTaskHistoryRecentPage",
    cacheKey: `${collectionPath}?orderBy=completedAt_desc&limit=${CRM_TASK_HISTORY_INITIAL_READ_LIMIT}`,
    ttlMs: 60 * 1000,
  })
    .then(async (snap) => {
      if (cancelled) return;
      let sourceSnap = snap;
      if (sourceSnap.size === 0) {
        sourceSnap = await getDocsCached(
          firestoreQuery(
            historyRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(CRM_TASK_HISTORY_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadCrmTaskHistoryLegacyPage",
            cacheKey: `${collectionPath}?legacyDocIdLimit=${CRM_TASK_HISTORY_INITIAL_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
      }
      const nextRecords = sourceSnap.docs
        .map((docSnap) => {
          const raw = docSnap.data() || {};
          return {
            id: docSnap.id,
            ...raw,
            dueDate: normalizeTs(raw.dueDate),
            completedAt: normalizeTs(raw.completedAt),
            createdAt: normalizeTs(raw.createdAt),
            updatedAt: normalizeTs(raw.updatedAt),
          };
        })
        .sort(
          (a, b) =>
            safeTimeMillis(b.completedAt || b.updatedAt || b.createdAt) -
            safeTimeMillis(a.completedAt || a.updatedAt || a.createdAt)
        );

      setCrmTaskHistoryRecords(nextRecords);
    })
    .catch((error) => {
      if (cancelled) return;
      devError("CRM task history load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      setCrmTaskHistoryRecords([]);
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      devLog("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmTaskHistoryOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

useEffect(() => {
  if (!workspaceUid || tab !== "dashboard") return;
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_DASHBOARD_PRELOADS")) {
    if (isDevRuntime) {
      devWarn("Dashboard preview effect skipped by FQ_DISABLE_DASHBOARD_PRELOADS");
    }
    return;
  }

  loadDashboardPreviewData(workspaceUid);
}, [loadDashboardPreviewData, tab, workspaceUid]);

useEffect(() => {
  if (!workspaceUid || !canAccessCrm) {
    setCrmSettings(normalizeCrmSettings(DEFAULT_CRM_SETTINGS));
    setCrmSettingsDraft(normalizeCrmSettings(DEFAULT_CRM_SETTINGS));
    setCrmSettingsLoadState({ loading: false, loadedWorkspace: "", error: "" });
    setSalesPipeline(normalizeSalesPipeline(DEFAULT_SALES_PIPELINE));
    setSalesPipelineLoadState({ loading: false, loadedWorkspace: "", error: "" });
    setOrderPipeline(normalizeOrderPipeline(DEFAULT_ORDER_PIPELINE));
    setOrderPipelineLoadState({ loading: false, loadedWorkspace: "", error: "" });
    return;
  }
  loadCrmSettings(workspaceUid);
  loadSalesPipelineSettings(workspaceUid);
  loadOrderPipelineSettings(workspaceUid);
}, [canAccessCrm, loadCrmSettings, loadOrderPipelineSettings, loadSalesPipelineSettings, workspaceUid]);

useEffect(() => {
  if (
    !workspaceUid ||
    !canAccessCrm ||
    !canViewPipelineReview ||
    tab !== "crm" ||
    !["overview", CRM_PIPELINE_REVIEW_TAB].includes(crmView) ||
    crmSettings.enablePipelineReview === false
  ) {
    return;
  }
  loadCrmPipelineReview(workspaceUid, crmPipelineFilters);
}, [
  canAccessCrm,
  canViewPipelineReview,
  crmPipelineFilters,
  crmSettings.enablePipelineReview,
  crmView,
  loadCrmPipelineReview,
  tab,
  workspaceUid,
]);

useEffect(() => {
  if (!workspaceUid || !shouldSubscribeSavedQuotes) return undefined;

  const unsubscribe = subscribeSavedQuotes(workspaceUid);

  return () => unsubscribe();
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  shouldSubscribeSavedQuotes,
  subscribeSavedQuotes,
  workspaceUid,
  savedRepositoryRefresh,
  tab,
]);

useEffect(() => {
  savedSearchGenerationRef.current += 1;
  savedSearchRepositoryRef.current?.dispose();
  savedSearchRepositoryRef.current = null;
  setSavedSearchResult({ key: "", records: [], loading: false, error: "", hasMore: false });
  if (!workspaceUid || tab !== "saved" || savedQuotesSearchText.length < 2) return undefined;
  const timer = window.setTimeout(() => searchSavedQuotesRemote(workspaceUid, search), 450);
  return () => {
    window.clearTimeout(timer);
    savedSearchGenerationRef.current += 1;
    savedSearchRepositoryRef.current?.dispose();
  };
}, [savedQuotesSearchText, searchSavedQuotesRemote, tab, workspaceUid, savedRepositoryRefresh]);

useEffect(() => {
  const refresh = event => {
    const uid = event.detail?.workspaceUid;
    if (uid !== workspaceUid) return;
    savedRepositoryRef.current?.dispose();
    savedSearchGenerationRef.current += 1;
    savedSearchRepositoryRef.current?.dispose();
    savedLookupGenerationRef.current += 1;
    setSavedLookupResult({ key: "", records: [] });
    savedQuotesRecycleBinLoadRef.current = { workspaceUid: "", loading: false, loaded: false, promise: null, result: null };
    if (tab === "saved") setSavedRepositoryRefresh(value => value + 1);
  };
  const crossTab = event => {
    if (event.key !== "FQ_QUOTE_INDEX_INVALIDATION" || !event.newValue) return;
    try {
      const { workspaceUid: uid } = JSON.parse(event.newValue);
      if (uid !== workspaceUid) return;
      invalidateFirestoreReadCacheByPrefix(`users/${uid}/quote_index`);
      invalidateFirestoreReadCacheByPrefix(`savedQuotes:recent:${uid}:`);
      invalidateFirestoreReadCacheByPrefix(`savedQuotes:recycleBin:${uid}:`);
      invalidateFirestoreReadCacheByPrefix(`customerLedger:quotes:${uid}:`);
      refresh({ detail: { workspaceUid: uid } });
    } catch { /* Ignore malformed cross-tab signals, never business data. */ }
  };
  window.addEventListener("fq:quote-index-invalidated", refresh);
  window.addEventListener("storage", crossTab);
  return () => {
    window.removeEventListener("fq:quote-index-invalidated", refresh);
    window.removeEventListener("storage", crossTab);
  };
}, [workspaceUid, tab]);

useEffect(() => {
  if (!workspaceUid || !quoteLinkModalState) return undefined;
  const queryText = quoteLinkSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      devError("Quote link global search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [quoteLinkModalState, quoteLinkSearch, searchSavedQuotesRemote, workspaceUid]);

useEffect(() => {
  if (!workspaceUid || !customerLedgerQuoteLinkState.open) return undefined;
  const queryText = customerLedgerQuoteLinkState.search.trim();
  if (queryText.length < 2) {
    setCustomerLedgerQuoteLinkState((prev) => ({
      ...prev,
      loading: false,
      error: "",
      results: [],
    }));
    return undefined;
  }

  const allowedQuoteTypes = getAllowedCanonicalQuoteTypes({
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
  });

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const timerId = window.setTimeout(() => {
    setCustomerLedgerQuoteLinkState((prev) => ({
      ...prev,
      loading: true,
      error: "",
    }));

    globalSearchQuotations(workspaceUid, queryText, {
      limit: 25,
      labelPrefix: "customerLedgerQuoteLinkSearch",
    })
      .then((results) => {
        const normalizedResults = (Array.isArray(results) ? results : [])
          .map((entry) =>
            normalizeQuoteIndexRecordForList(entry.id, entry, normalizeTs)
          )
          .filter((quote) => !isDeletedQuoteRecord(quote))
          .filter((quote) => quoteTypeMatchesAccess(quote, allowedQuoteTypes))
          .sort((a, b) => getQuoteListSortTime(b) - getQuoteListSortTime(a))
          .slice(0, 25);

        setCustomerLedgerQuoteLinkState((prev) => {
          if (!prev.open || prev.search.trim() !== queryText) return prev;
          return {
            ...prev,
            loading: false,
            error: "",
            results: normalizedResults,
          };
        });

        if (isDevRuntime) {
          devLog("CUSTOMER_LEDGER_QUOTE_LINK_SEARCH_TRACE", {
            queryText,
            resultCount: normalizedResults.length,
            workspaceUid,
          });
        }
      })
      .catch((error) => {
        devError("Customer ledger quote link search failed:", {
          code: error?.code || "",
          message: error?.message || String(error),
          workspaceUid,
        });
        setCustomerLedgerQuoteLinkState((prev) => {
          if (!prev.open || prev.search.trim() !== queryText) return prev;
          return {
            ...prev,
            loading: false,
            error:
              error?.code === "failed-precondition"
                ? "Search index is still building. Please try again shortly."
                : "Could not search quotations.",
            results: [],
          };
        });
      });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  customerLedgerQuoteLinkState.open,
  customerLedgerQuoteLinkState.search,
  workspaceUid,
]);

useEffect(() => {
  if (tab !== "crm") return undefined;
  if (!["overview", "list", "board"].includes(crmView)) return undefined;
  const queryText = deferredCrmSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "crmDealSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      devError("CRM quote-derived deal search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "crm" || crmView !== "leads") return undefined;
  const queryText = deferredCrmLeadSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "crmLeadSearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmLeadSearch, runCrmGlobalSearch, tab]);

useEffect(() => {
  if (tab !== "executionSpecs") return undefined;
  const queryText = executionWorkspaceSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "executionWorkspaceSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      devError("Execution workspace quote search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [executionWorkspaceSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "todayTasks") return undefined;
  const queryText = deferredDailyTaskSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "todayTaskSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      devError("Today task quote search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [deferredDailyTaskSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "customerLedger") return undefined;
  const queryText = deferredCustomerLedgerSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCustomerGlobalSearch(queryText, "customerLedgerSearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [deferredCustomerLedgerSearch, runCustomerGlobalSearch, tab]);

useEffect(() => {
  if (tab !== "crm" || crmView !== "history") return undefined;
  const queryText = deferredCrmTaskHistorySearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runTaskHistoryGlobalSearch(queryText, "crmTaskHistorySearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmTaskHistorySearch, runTaskHistoryGlobalSearch, tab]);

useEffect(() => {
  if (!workspaceUid) {
    setCrmLeadRecords([]);
    return undefined;
  }

  if (!shouldSubscribeCrmLeads) {
    return undefined;
  }

  const unsubscribe = subscribeCrmLeads(workspaceUid);

  return () => unsubscribe();
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  shouldSubscribeCrmLeads,
  subscribeCrmLeads,
  workspaceUid,
]);

useEffect(() => {
  if (!workspaceUid || !canAccessCrm || !shouldSubscribeCustomerLedger) {
    setCustomerRecords([]);
    return undefined;
  }

  const unsubscribe = subscribeCustomerLedger(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCustomerLedger,
  subscribeCustomerLedger,
  workspaceUid,
]);

// Legacy links remain readable through normalization; explicit saves persist the canonical form.

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || !shouldSubscribeCrmTasks) {
    setCrmTaskRecords([]);
    return undefined;
  }

  const unsubscribe = subscribeCrmTasks(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCrmTasks,
  subscribeCrmTasks,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || !shouldLoadCrmAttention) {
    setCrmAttentionState({
      loading: false,
      loaded: false,
      error: "",
      missingOwner: [],
      missingNextAction: [],
      overdueTasks: [],
      missingQuoteFollowUp: [],
      handoverPendingProjects: [],
      advancePendingProjects: [],
      projectsMissingNextAction: [],
      projectsMissingInstallationDate: [],
      overdueTrackingTasks: [],
      trackingItemsNotOrdered: [],
      trackingItemsDelayed: [],
      trackingArrivalsOverdue: [],
      balancePendingProjects: [],
      overduePaymentTasks: [],
      paymentDueTodayTasks: [],
      installationNotScheduledProjects: [],
      installationsDueToday: [],
      overdueInstallations: [],
      productionOverdueProjects: [],
      materialDueOverdueProjects: [],
      openSnagProjects: [],
      overdueSnagProjects: [],
      projectsReadyToClose: [],
      projectsBlockedByFinalPayment: [],
      reviewDueTodayTasks: [],
      overdueReviewTasks: [],
    });
    return;
  }

  loadCrmAttentionRequired(workspaceUid);
}, [
  canAccessCrm,
  loadCrmAttentionRequired,
  shouldLoadCrmAttention,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || tab !== "crm" || crmView !== "projects") {
    return;
  }
  if (!canViewAllCrmRecords) {
    return;
  }
  loadCrmProjects(workspaceUid);
}, [
  canAccessCrm,
  canViewAllCrmRecords,
  crmView,
  loadCrmProjects,
  tab,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || tab !== "crm" || crmView !== "projects") {
    return;
  }
  const cleanProjectId = String(selectedCrmProjectId || "").trim();
  if (!cleanProjectId) return;
  const hasSelectedProject = crmProjectRecords.some(
    (project) => String(project.projectId || project.id) === cleanProjectId
  );
  if (hasSelectedProject) return;
  loadCrmProjectById(cleanProjectId);
}, [
  canAccessCrm,
  crmProjectRecords,
  crmView,
  loadCrmProjectById,
  selectedCrmProjectId,
  tab,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || tab !== "crm" || crmView !== "projects") {
    return;
  }
  if (crmProjectDrawerTab === "payments" && !canViewCrmPaymentsAccess) {
    return;
  }
  if (
    !selectedCrmProjectId ||
    crmProjectDrawerTab === "overview" ||
    crmProjectDrawerTab === "checklist" ||
    crmProjectDrawerTab === "installation"
  ) {
    return;
  }
  if (crmProjectDrawerTab === "tracking" && crmSettings.enableDeliveryTracking === false) {
    return;
  }
  if (crmProjectDrawerTab === "payments" && crmSettings.enablePaymentTracking === false) {
    return;
  }
  loadCrmProjectDrawerSection(crmProjectDrawerTab, selectedCrmProjectId);
}, [
  canAccessCrm,
  canViewCrmPaymentsAccess,
  crmSettings.enableDeliveryTracking,
  crmSettings.enablePaymentTracking,
  crmProjectDrawerTab,
  crmView,
  loadCrmProjectDrawerSection,
  selectedCrmProjectId,
  tab,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || !shouldSubscribeCrmReminders) {
    setCrmReminderRecords([]);
    return undefined;
  }

  const unsubscribe = subscribeCrmReminders(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCrmReminders,
  subscribeCrmReminders,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) return undefined;

  const runReminderRefresh = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    subscribeCrmReminders(workspaceUid, "focus");
  };

  window.addEventListener("focus", runReminderRefresh);
  document.addEventListener("visibilitychange", runReminderRefresh);

  return () => {
    window.removeEventListener("focus", runReminderRefresh);
    document.removeEventListener("visibilitychange", runReminderRefresh);
  };
}, [canAccessCrm, subscribeCrmReminders, workspaceUid]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) {
    executionTemplatesLoadedWorkspaceRef.current = "";
    executionTemplatesLoadingWorkspaceRef.current = "";
    setExecutionTemplatesLoadState({
      workspaceUid: workspaceUid || "",
      loading: false,
      loaded: false,
      error: "",
    });
    setCrmExecutionTemplates([]);
    return undefined;
  }

  if (!shouldSubscribeCrmExecutionTemplates) {
    return undefined;
  }

  const unsubscribe = subscribeCrmExecutionTemplates(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCrmExecutionTemplates,
  subscribeCrmExecutionTemplates,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) {
    setCrmTaskHistoryRecords([]);
    return undefined;
  }

  if (tab !== "crm" || crmView !== "history") {
    return undefined;
  }

  const unsubscribe = subscribeCrmTaskHistory(workspaceUid);
  return () => unsubscribe();
}, [canAccessCrm, crmView, subscribeCrmTaskHistory, tab, workspaceUid]);

useEffect(() => {
  const intervalId = window.setInterval(() => {
    setCrmReminderNow(Date.now());
  }, 30000);

  return () => window.clearInterval(intervalId);
}, []);

/* --------------------------- LOAD FULL QUOTE --------------------------- */
const loadFullQuote = async (quoteInput, options = {}) => {
  const {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quoteId,
    displayId,
    candidatePaths,
  } = getSavedQuoteOpenCandidates(quoteInput, workspaceUid);
  const quoteNo = quoteIndex.quoteNo || quoteIndex.quoteNoString || "";
  const quoteType = quoteIndex.quoteType || quoteIndex.quoteTypeResolved || "";
  const openedFrom =
    options.openedFrom ||
    quoteIndex.openedFrom ||
    quoteIndex.sourceType ||
    quoteIndex.sourceCollection ||
    "saved_quotes";
  const indexDisplayTotal = getSavedQuoteDisplayTotal(quoteIndex);
  const indexGrandTotal = Number(indexDisplayTotal.amount || 0);
  const indexSubtotal = Number(quoteIndex.subtotal ?? quoteIndex.subTotal ?? 0);
  const indexRowCount = Number(
    quoteIndex.rowCount ??
      quoteIndex.contentRowCount ??
      quoteIndex.itemCount ??
      quoteIndex.contentItemCount ??
      0
  );
  const attemptedPaths = [];
  const foundPaths = [];
  let fullQuoteReads = 0;
  let fallbackAttempts = 0;
  let lastError = null;
  let schemaOpenError = null;
  let foundAnySourceDoc = false;
  let pendingUnsafeSaveState = null;

  const logOpenTrace = (result, extra = {}) => {
    if (!isDevRuntime) return;
    devLog("SAVED_QUOTE_OPEN_TRACE", {
      authUid: auth.currentUser?.uid || user?.uid || "",
      workspaceUid,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result,
      ...extra,
    });
    devLog("SAVED_QUOTES_READ_BUDGET_TRACE", {
      action: "openSavedQuote",
      quoteIndexReads: 0,
      fullQuoteReads,
      fallbackFunctionCalled: false,
      attemptedPaths,
    });
    devLog("SAVED_QUOTE_LOW_READ_TRACE", {
      action: "openSavedQuote",
      quoteIndexReads: 0,
      fullQuoteReads,
      fallbackAttempts,
      callableUsed: false,
      broadScanUsed: false,
      attemptedPaths,
    });
  };
  const logRealOpenError = (error, failedPath = "", extra = {}) => {
    if (!isDevRuntime) return;
    devLog("SAVED_QUOTE_OPEN_REAL_ERROR", {
      quoteNo,
      quoteType,
      indexId,
      workspaceUid,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      attemptedPaths,
      errorCode: error?.code,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
      failedPath,
      ...extra,
    });
  };
  const logIndexRecordTrace = () => {
    if (!isDevRuntime) return;
    const indexRecord = quoteIndex.indexRecordRaw || quoteIndex || {};
    devLog("QUOTE_LOAD_SOURCE_TRACE", {
      quoteNo,
      quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
      quoteId,
      sourceQuoteId,
      sourcePath: quoteIndex.sourcePath || "",
      quotationType: quoteIndex.quotationType || "",
      quoteType,
      openedFrom,
      indexGrandTotal,
      indexSubtotal,
      indexRowCount,
      indexRowStorageMode: quoteIndex.rowStorageMode || "",
      resolvedMainQuotePath: candidatePaths[0] || "",
    });
    devLog("SAVED_QUOTE_INDEX_RECORD_TRACE", {
      indexId,
      quoteNo: indexRecord.quoteNo || quoteNo,
      quoteType: indexRecord.quoteType || quoteType,
      customerName: indexRecord.customerName || quoteIndex.customerName || "",
      sourcePath: indexRecord.sourcePath || quoteIndex.sourcePath || "",
      sourceQuoteId: indexRecord.sourceQuoteId || sourceQuoteId,
      quoteId: indexRecord.quoteId || quoteId,
      quotationId: indexRecord.quotationId || quoteIndex.quotationId || "",
      id: indexRecord.id || quoteIndex.id || "",
      collection: indexRecord.sourceCollection || quoteIndex.sourceCollection || "quotations",
      createdAt: indexRecord.createdAt || quoteIndex.createdAt || null,
      updatedAt: indexRecord.updatedAt || quoteIndex.updatedAt || null,
      rawKeys: Object.keys(indexRecord || {}),
    });
  };
  const logForensicAuditTrace = (extra = {}) => {
    if (!isDevRuntime) return;
    devLog("QUOTE_FORENSIC_AUDIT_TRACE", {
      quoteNo,
      quoteIndexPath:
        workspaceUid && (indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "")
          ? `users/${workspaceUid}/quote_index/${indexId || quoteIndex.quoteIndexId || quoteIndex.indexId}`
          : "",
      quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
      quoteType,
      triedPaths: [...attemptedPaths],
      foundPaths: [...foundPaths],
      selectedFullQuotePath: extra.selectedFullQuotePath || "",
      itemSourceField: extra.itemSourceField || "",
      itemCount: Number(extra.itemCount || 0),
      hasOnlyIndexData: Boolean(extra.hasOnlyIndexData),
      deleted: quoteIndex.deleted === true,
      archived: quoteIndex.archived === true,
      createdAt: quoteIndex.createdAt || null,
      updatedAt: quoteIndex.updatedAt || null,
      saveTargetStatus: extra.saveTargetStatus || "",
      reason: extra.reason || "",
    });
  };

  if (!workspaceUid || candidatePaths.length === 0) {
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: "not-found",
      message: "This quotation could not be opened. Please contact support.",
    };
    setSavedQuoteOpenError(errorState);
    if (isDevRuntime) {
      devError("SAVED_QUOTE_LOAD_FAILED", {
        quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
        quoteNo,
        quoteType,
        failedReason: "no-candidate-paths",
        triedPaths: [],
        errorCode: "",
        errorMessage: "No exact sourcePath/sourceQuoteId/quoteId candidate was available.",
      });
    }
    logOpenTrace("not-found", { reason: "no-candidate-paths" });
    notify(errorState.message, "warning", 7000);
    return null;
  }

  setIsLoadingQuote(true);
  setSavedQuoteOpenError(null);
  setGeneralQuoteRecoveryState({
    running: false,
    error: "",
    quoteNo: "",
    sources: [],
    bestRecoverySource: null,
    checkedAt: "",
  });
  logIndexRecordTrace();

  try {
    for (const [candidateIndex, candidatePath] of candidatePaths.entries()) {
      attemptedPaths.push(candidatePath);
      fallbackAttempts = Math.max(0, candidateIndex);
      const ref = doc(db, ...candidatePath.split("/"));
      let foundExistingDoc = false;
      try {
        if (isDevRuntime) {
          devLog("SAVED_QUOTE_OPEN_ATTEMPT", {
            quoteNo,
            quoteType,
            attemptedPath: candidatePath,
            attemptNumber: candidateIndex + 1,
          });
        }
        fullQuoteReads += 1;
        const snap = await readCompatibleQuoteDocument({ ref, index: quoteIndex, workspaceUid,
          getDoc: r => getDoc(r, { label: "fullQuoteLoadBySourcePointer", cacheKey: candidatePath, reason: "user_open_saved_quote" }),
          callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data) });
        if (isDevRuntime) {
          devLog("SAVED_QUOTE_OPEN_ATTEMPT_RESULT", {
            quoteNo,
            attemptedPath: candidatePath,
            exists: snap.exists(),
            docId: snap.id,
            dataKeys: snap.exists() ? Object.keys(snap.data() || {}) : [],
          });
          const mainDocData = snap.exists() ? snap.data() || {} : {};
          devLog("QUOTE_LOAD_MAIN_DOC_TRACE", {
            quoteNo,
            resolvedMainQuotePath: candidatePath,
            exists: snap.exists(),
            docKeys: snap.exists() ? Object.keys(mainDocData) : [],
            rowStorageMode: mainDocData.rowStorageMode || "",
            rowChunkCount: mainDocData.rowChunkCount || 0,
            rowChunkRefs: Array.isArray(mainDocData.rowChunkRefs)
              ? mainDocData.rowChunkRefs
              : [],
            hasRows: Array.isArray(mainDocData.rows),
            rowsLength: Array.isArray(mainDocData.rows) ? mainDocData.rows.length : 0,
            hasCurtainRows: Array.isArray(mainDocData.curtainRows),
            curtainRowsLength: Array.isArray(mainDocData.curtainRows)
              ? mainDocData.curtainRows.length
              : 0,
            hasQuoteRows: Array.isArray(mainDocData.quoteRows),
            quoteRowsLength: Array.isArray(mainDocData.quoteRows)
              ? mainDocData.quoteRows.length
              : 0,
            hasMeasurementRows: Array.isArray(mainDocData.measurementRows),
            measurementRowsLength: Array.isArray(mainDocData.measurementRows)
              ? mainDocData.measurementRows.length
              : 0,
            hasItems: Array.isArray(mainDocData.items),
            itemsLength: Array.isArray(mainDocData.items) ? mainDocData.items.length : 0,
            subtotal:
              mainDocData.subtotal ??
              mainDocData.subTotal ??
              mainDocData.totals?.subtotal ??
              null,
            grandTotal:
              mainDocData.grandTotal ??
              mainDocData.totals?.grandTotal ??
              mainDocData.summary?.grandTotal ??
              null,
            finalTotal: mainDocData.finalTotal ?? null,
          });
        }

        if (!snap.exists()) {
          logRealOpenError(
            {
              code: "not-found",
              name: "DocumentMissing",
              message: "Document does not exist.",
            },
            candidatePath
          );
          continue;
        }
        foundExistingDoc = true;
        foundAnySourceDoc = true;
        if (!foundPaths.includes(candidatePath)) foundPaths.push(candidatePath);
        const rawFullQuoteData = {
          id: snap.id,
          ...(snap.data() || {}),
        };

        const hydratedRawQuote = snap.canonicalRowsHydrated ? rawFullQuoteData : await hydrateCurtainQuoteRowsFromChunks({
          quote: rawFullQuoteData,
          workspaceUid,
          label: "fullQuoteRowChunks.savedQuoteOpen",
        });
        let fullQuote = normalizeSavedQuoteForEditor(hydratedRawQuote, quoteIndex);
        fullQuote = {
          ...fullQuote,
          id: snap.id,
          quoteId: snap.id,
          sourceQuoteId: snap.id,
          sourceCollection: "quotations",
          sourcePath: candidatePath,
        };

        const resolvedEditor = resolveQuoteType(fullQuote) === GENERAL_QUOTE_TYPE ? "general" : "curtain";
        const loadedContentSelection =
          resolvedEditor === "general"
            ? resolveGeneralQuoteContentSelection(
                fullQuote,
                fullQuote.generalQuote || fullQuote.generalQuoteData || {},
                { allowCanonicalEmptyAliasRecovery: true }
              )
            : resolveCurtainQuoteContentSelection(fullQuote);
        const quoteIndexExpectedCount =
          resolvedEditor === "general"
            ? Number(quoteIndex.itemCount ?? quoteIndex.contentItemCount ?? 0)
            : Number(quoteIndex.rowCount ?? quoteIndex.contentRowCount ?? 0);
        const quoteIndexExpectedTotal = Number(
          quoteIndex.grandTotal ??
            quoteIndex.finalTotal ??
            quoteIndex.totalAmount ??
            quoteIndex.total ??
            0
        );
        const candidateHasStaleEmptyContent =
          loadedContentSelection.count === 0 &&
          (quoteIndexExpectedCount > 0 || quoteIndexExpectedTotal > 0.05);
        const candidateRecoveredFromCanonicalEmpty =
          loadedContentSelection.recoveredFromCanonicalEmpty === true;
        const candidateRequiresSaveProtection = candidateHasStaleEmptyContent;
        const saveTargetStatus = candidateRequiresSaveProtection
          ? "content_unverified"
          : candidateIndex > 0 ||
            (quoteIndex.sourcePath &&
              normalizeSavedQuoteSourcePath(workspaceUid, quoteIndex.sourcePath) !== candidatePath)
          ? "legacy_fallback"
          : "safe";
        const saveTargetReason = candidateHasStaleEmptyContent
          ? "full_quote_read_but_items_not_verified"
          : candidateRecoveredFromCanonicalEmpty
          ? "legacy_alias_recovered_into_canonical_content"
          : saveTargetStatus === "legacy_fallback"
          ? "loaded_from_exact_fallback_path"
          : "";
        if (isDevRuntime) {
          if (resolvedEditor === "general") {
            const rawGeneralSelection = resolveGeneralQuoteContentSelection(
              rawFullQuoteData,
              rawFullQuoteData.generalQuote || rawFullQuoteData.generalQuoteData || {},
              { allowCanonicalEmptyAliasRecovery: true }
            );
            const rawTopLevelTotal = getSavedQuoteDisplayTotal(rawFullQuoteData);
            const rawGeneralTotal = getSavedQuoteDisplayTotal(
              rawFullQuoteData.generalQuote || rawFullQuoteData.generalQuoteData || {}
            );
            const hydratedTotal = getSavedQuoteDisplayTotal(fullQuote);
            devLog("GENERAL_QUOTE_HYDRATION_TRACE", {
              quoteNo,
              quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
              resolvedPath: candidatePath,
              topLevelKeys: Object.keys(rawFullQuoteData || {}),
              generalQuoteKeys:
                rawFullQuoteData.generalQuote && typeof rawFullQuoteData.generalQuote === "object"
                  ? Object.keys(rawFullQuoteData.generalQuote)
                  : [],
              candidateItemCounts: getGeneralQuoteHydrationCandidateCounts(rawFullQuoteData),
              selectedItemSourceField: rawGeneralSelection.field || "empty",
              selectedItemCount: rawGeneralSelection.count,
              hydratedItemCount: loadedContentSelection.count,
              indexGrandTotal: quoteIndexExpectedTotal,
              loadedGrandTotal:
                rawTopLevelTotal.amount ??
                rawGeneralTotal.amount ??
                Number(rawFullQuoteData.grandTotal || rawFullQuoteData.totalAmount || 0),
              hydratedGrandTotal:
                hydratedTotal.amount ??
                Number(fullQuote.grandTotal || fullQuote.totalAmount || 0),
              recoveredFromCanonicalEmpty: rawGeneralSelection.recoveredFromCanonicalEmpty,
            });
          }
          devLog("SAVED_QUOTE_LOAD_SOURCE_RESOLUTION", {
            quoteIndexId: indexId || displayId || quoteId || "",
            quoteNo,
            triedPaths: [...attemptedPaths],
            selectedPath: candidatePath,
            exists: true,
            contentCount: loadedContentSelection.count,
            selectedLoadField: loadedContentSelection.field || "empty",
            recoveredFromCanonicalEmpty: loadedContentSelection.recoveredFromCanonicalEmpty,
            grandTotal: Number(fullQuote.grandTotal ?? fullQuote.totalAmount ?? 0),
            quoteIndexGrandTotal: quoteIndexExpectedTotal,
            quoteIndexExpectedCount,
            mismatchReason: candidateHasStaleEmptyContent
              ? "existing_source_doc_has_empty_content_but_index_expects_content"
              : candidateRecoveredFromCanonicalEmpty
              ? "canonical_empty_recovered_from_legacy_alias_for_viewing"
              : "",
          });
          devLog("SAVED_QUOTE_LOAD_RESOLVE_TRACE", {
            quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
            quoteNo,
            quoteType,
            sourcePath: quoteIndex.sourcePath || "",
            sourceQuoteId,
            quoteId,
            triedPaths: [...attemptedPaths],
            resolvedPath: candidatePath,
            resolvedDocExists: true,
            loadStatus: "loaded",
            saveTargetStatus,
            saveTargetReason,
            itemSourceField: loadedContentSelection.field || "empty",
            itemCount: loadedContentSelection.count,
          });
          devLog("QUOTE_HYDRATION_RESULT_TRACE", {
            quoteNo,
            hydratedRowCount:
              resolvedEditor === "general" ? 0 : loadedContentSelection.count,
            hydratedItemCount:
              resolvedEditor === "general" ? loadedContentSelection.count : 0,
            hydratedAccessoryCount:
              resolvedEditor === "general"
                ? 0
                : (Array.isArray(fullQuote.accessories)
                    ? fullQuote.accessories.length
                    : Array.isArray(fullQuote.accessoryRows)
                    ? fullQuote.accessoryRows.length
                    : Array.isArray(fullQuote.addOns)
                    ? fullQuote.addOns.length
                    : Array.isArray(fullQuote.extraItems)
                    ? fullQuote.extraItems.length
                    : 0),
            hydratedTotal: Number(
              fullQuote.grandTotal ??
                fullQuote.finalTotal ??
                fullQuote.total ??
                fullQuote.totalAmount ??
                0
            ),
            sourceUsedForRows:
              resolvedEditor === "general"
                ? loadedContentSelection.field || "empty"
                : hydratedRawQuote.rowChunkLoadSource ||
                  loadedContentSelection.field ||
                  "empty",
            sourceUsedForTotals:
              getSavedQuoteDisplayTotal(fullQuote).source ||
              getSavedQuoteDisplayTotal(quoteIndex).source ||
              "none",
            openedAsEmpty: candidateHasStaleEmptyContent,
            warning: candidateHasStaleEmptyContent
              ? "index_expected_content_but_hydration_returned_empty"
              : candidateRecoveredFromCanonicalEmpty
              ? "canonical_empty_recovered_from_legacy_alias_save_protected"
              : "",
          });
        }
        if (candidateHasStaleEmptyContent && candidateIndex < candidatePaths.length - 1) {
          if (isDevRuntime) {
            devWarn("SAVED_QUOTE_SOURCE_EMPTY_TRYING_NEXT_CANDIDATE", {
              quoteIndexId: indexId || displayId || quoteId || "",
              quoteNo,
              skippedPath: candidatePath,
              selectedLoadField: loadedContentSelection.field || "empty",
              selectedLoadCount: loadedContentSelection.count,
              quoteIndexExpectedCount,
              quoteIndexGrandTotal: quoteIndexExpectedTotal,
              remainingPaths: candidatePaths.slice(candidateIndex + 1),
            });
          }
          continue;
        }
        if (candidateHasStaleEmptyContent) {
          logForensicAuditTrace({
            selectedFullQuotePath: candidatePath,
            itemSourceField: loadedContentSelection.field || "empty",
            itemCount: loadedContentSelection.count,
            hasOnlyIndexData: true,
            saveTargetStatus,
            reason: "full_doc_exists_but_no_recoverable_content",
          });
          const protectedErrorState = {
            quoteIndex,
            indexId,
            quoteNo,
            quoteType,
            sourcePath: quoteIndex.sourcePath || "",
            sourceQuoteId,
            quoteId: snap.id || quoteId,
            attemptedPaths,
            result: "item-details-not-found",
            code: "item-details-not-found",
            message:
              "This quotation record exists, but item details were not found in the saved quotation document.",
            hydratedRowCount:
              resolvedEditor === "general" ? 0 : loadedContentSelection.count,
            hydratedItemCount:
              resolvedEditor === "general" ? loadedContentSelection.count : 0,
            indexGrandTotal: quoteIndexExpectedTotal,
            indexContentCount: quoteIndexExpectedCount,
            selectedLoadField: loadedContentSelection.field || "empty",
            resolvedMainQuotePath: candidatePath,
            loadStatus: "loaded",
            saveTargetStatus,
            saveTargetReason,
            recoveredFromCanonicalEmpty: candidateRecoveredFromCanonicalEmpty,
          };
          incompleteQuoteLoadRef.current = protectedErrorState;
          setIncompleteQuoteLoadState(protectedErrorState);
          setSavedQuoteOpenError(protectedErrorState);
          if (isDevRuntime) {
            devError("SAVED_QUOTE_LOAD_FAILED", {
              quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
              quoteNo,
              quoteType,
              failedReason: "item-details-not-found",
              triedPaths: [...attemptedPaths],
              errorCode: protectedErrorState.code,
              errorMessage: protectedErrorState.message,
            });
          }
          logOpenTrace("item-details-not-found", {
            loadedPath: candidatePath,
            selectedLoadField: loadedContentSelection.field || "empty",
            quoteIndexExpectedCount,
            quoteIndexGrandTotal: quoteIndexExpectedTotal,
          });
          notify(protectedErrorState.message, "error", 9000);
          return null;
        }
        if (candidateRecoveredFromCanonicalEmpty && isDevRuntime) {
          devWarn("SAVED_QUOTE_LEGACY_CONTENT_ALIAS_RECOVERED", {
            quoteNo,
            quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
            loadedPath: candidatePath,
            selectedLoadField: loadedContentSelection.field || "empty",
            contentCount: loadedContentSelection.count,
            message:
              "Legacy content was found in an alias field and will be saved back through the canonical content field on the next save.",
          });
        }
        if (candidateRecoveredFromCanonicalEmpty && resolvedEditor === "general") {
          notify(
            "This older quotation was recovered from an older format. Please Save As New.",
            "warning",
            7000
          );
        }
        fullQuote = {
          ...fullQuote,
          loadStatus: fullQuote.loadStatus || "loaded",
          saveTargetStatus: fullQuote.saveTargetStatus || saveTargetStatus,
          saveTargetReason: fullQuote.saveTargetReason || saveTargetReason,
          quoteIndex,
          indexRecordRaw: quoteIndex,
        };
        if (isDevRuntime) {
          devLog("SAVED_QUOTE_ROUTE_TRACE", {
            quoteNo,
            quoteType,
            resolvedEditor,
            reason:
              resolvedEditor === "general"
                ? "quoteType/general schema resolved"
                : "curtain schema resolved",
          });
        }

        if (resolvedEditor === "general") {
          try {
            const embeddedGeneralQuote = getEmbeddedGeneralQuote(fullQuote);
            const { cleanedQuote } = await cleanupExpiredGeneralQuoteImages(
              embeddedGeneralQuote
            );

            fullQuote = {
              ...fullQuote,
              generalQuote: cleanedQuote,
            };

            // Cleanup on open is in-memory only; explicit Save owns persistence.
          } catch (cleanupError) {
            if (isDevRuntime) {
              devWarn("GENERAL_QUOTE_OPEN_CLEANUP_SKIPPED", {
                quoteNo,
                path: candidatePath,
                code: cleanupError?.code,
                message: cleanupError?.message,
                stack: cleanupError?.stack,
              });
            }
          }
        }

        const quoteIndexDocId = indexId || displayId || quoteId || snap.id;
        const nextQuoteIndexPayload = buildQuoteIndexPayload(
          {
            ...fullQuote,
            id: snap.id,
            quoteId: snap.id,
            sourceQuoteId: snap.id,
            sourceCollection: "quotations",
            sourcePath: candidatePath,
          },
          snap.id,
          workspaceUid
        );
        const quoteIndexTotalMismatch =
          Math.abs(
            Number(quoteIndex.grandTotal ?? quoteIndex.totalAmount ?? 0) -
              Number(nextQuoteIndexPayload.grandTotal || 0)
          ) > 0.05;
        const quoteIndexContentCountMismatch =
          resolvedEditor === "general"
            ? Number(quoteIndex.itemCount ?? quoteIndex.contentItemCount ?? 0) !==
              Number(nextQuoteIndexPayload.itemCount || 0)
            : Number(quoteIndex.rowCount ?? quoteIndex.contentRowCount ?? 0) !==
              Number(nextQuoteIndexPayload.rowCount || 0);
        const quoteIndexHealthMismatch =
          String(quoteIndex.dataHealthStatus || "") !==
            String(nextQuoteIndexPayload.dataHealthStatus || "") ||
          Boolean(quoteIndex.indexContentMismatch) !==
            Boolean(nextQuoteIndexPayload.indexContentMismatch);
        const quoteIndexRepairBadgeReasons = getSavedQuoteRepairBadgeReasons(quoteIndex);
        const quoteIndexStoredRepairFlagMismatch = quoteIndexRepairBadgeReasons.length > 0;
        const shouldSelfHealQuoteIndex =
          !pendingUnsafeSaveState &&
          (quoteIndex.sourcePath !== candidatePath ||
            quoteIndex.sourceQuoteId !== snap.id ||
            quoteIndexTotalMismatch ||
            quoteIndexContentCountMismatch ||
            quoteIndexHealthMismatch ||
            quoteIndexStoredRepairFlagMismatch);
        if (
          workspaceUid &&
          quoteIndexDocId &&
          shouldSelfHealQuoteIndex
        ) {
          // Opening is read-only. Explicit saves/authorized repair own index reconciliation.
          devWarn("SAVED_QUOTE_INDEX_RECONCILIATION_REQUIRED", {
            quoteIndexId: quoteIndexDocId, quoteIndexTotalMismatch,
            quoteIndexContentCountMismatch, quoteIndexHealthMismatch,
          });
        }

        try {
          if (pendingUnsafeSaveState) {
            incompleteQuoteLoadRef.current = pendingUnsafeSaveState;
            setIncompleteQuoteLoadState(pendingUnsafeSaveState);
          } else {
            incompleteQuoteLoadRef.current = null;
            setIncompleteQuoteLoadState(null);
          }
          loadQuoteForEdit(fullQuote);
        } catch (editorError) {
          schemaOpenError = editorError;
          lastError = editorError;
          logRealOpenError(editorError, candidatePath, {
            classificationHint: "schema-error",
          });
          break;
        }
        logOpenTrace("loaded", {
          loadedPath: candidatePath,
          loadedQuoteId: snap.id,
        });
        logForensicAuditTrace({
          selectedFullQuotePath: candidatePath,
          itemSourceField: loadedContentSelection.field || "empty",
          itemCount: loadedContentSelection.count,
          hasOnlyIndexData: false,
          saveTargetStatus,
          reason: candidateRecoveredFromCanonicalEmpty
            ? "legacy_content_alias_recovered"
            : "loaded",
        });
        return fullQuote;
      } catch (error) {
        lastError = error;
        if (Number(quoteIndex.minimumReaderVersion) >= 2 || error?.code === 'quote/update-required' || error?.details?.code === 'QUOTE_CLIENT_UPDATE_REQUIRED') {
          schemaOpenError = error;
          break;
        }
        if (foundExistingDoc && !error?.code) {
          schemaOpenError = error;
        }
        logRealOpenError(error, candidatePath, {
          classificationHint: foundExistingDoc ? "schema-error" : "read-error",
        });
        if (isDevRuntime) {
          devError("SAVED_QUOTE_OPEN_ATTEMPT_ERROR", {
            quoteNo,
            attemptedPath: candidatePath,
            errorCode: error?.code,
            errorName: error?.name,
            errorMessage: error?.message,
            errorStack: error?.stack,
          });
          devError("SAVED_QUOTE_OPEN_CANDIDATE_FAILED", {
            code: error?.code,
            message: error?.message,
            path: candidatePath,
            workspaceUid,
            indexId,
            quoteNo,
          });
        }
      }
    }

    const classification = classifySavedQuoteOpenFailure({
      readError: lastError,
      exists: foundAnySourceDoc,
      normalizeError: schemaOpenError,
      attemptedPaths,
    });
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: classification.result,
      code: lastError?.code || "",
      message: classification.message,
    };
    if (isDevRuntime) {
      devError("SAVED_QUOTE_LOAD_FAILED", {
        quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
        quoteNo,
        quoteType,
        failedReason: classification.result,
        triedPaths: [...attemptedPaths],
        errorCode: lastError?.code || "",
        errorMessage: lastError?.message || "",
      });
    }
    setSavedQuoteOpenError(errorState);
    logForensicAuditTrace({
      selectedFullQuotePath: "",
      itemSourceField: "",
      itemCount: 0,
      hasOnlyIndexData: false,
      reason: classification.result,
    });
    logOpenTrace(errorState.result, {
      code: lastError?.code || "",
      message: lastError?.message || "",
    });
    notify(
      classification.result === "resource-exhausted"
        ? "Firebase read limit reached. Please wait and try again."
        : errorState.message,
      classification.tone,
      8000
    );
    return null;
  } catch (err) {
    lastError = err;
    const classification = classifySavedQuoteOpenFailure({
      readError: err,
      exists: foundAnySourceDoc,
      normalizeError: !err?.code ? err : null,
      attemptedPaths,
    });
    logRealOpenError(err, attemptedPaths[attemptedPaths.length - 1] || "", {
      classificationHint: classification.result,
    });
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: classification.result,
      code: err?.code || "",
      message: classification.message || err?.message || "Could not open this saved quotation.",
    };
    if (isDevRuntime) {
      devError("SAVED_QUOTE_LOAD_FAILED", {
        quoteIndexId: indexId || quoteIndex.quoteIndexId || quoteIndex.indexId || "",
        quoteNo,
        quoteType,
        failedReason: classification.result,
        triedPaths: [...attemptedPaths],
        errorCode: err?.code || "",
        errorMessage: err?.message || "",
      });
    }
    setSavedQuoteOpenError(errorState);
    logOpenTrace(classification.result, {
      code: err?.code || "",
      message: err?.message || "",
    });
    devError("Failed to load full quote", {
      code: err?.code,
      message: err?.message,
      stack: err?.stack,
      workspaceUid,
      indexId,
      quoteNo,
      attemptedPaths,
    });
    notify(errorState.message, classification.tone || "error");
    return null;
  } finally {
    setIsLoadingQuote(false);
  }
};

const repairSavedQuoteLink = async (quoteInput = savedQuoteOpenError?.quoteIndex) => {
  if (!workspaceUid || !quoteInput || repairingSavedQuoteLinkId) return;

  const {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quoteId,
    displayId,
    candidatePaths,
  } = getSavedQuoteOpenCandidates(quoteInput, workspaceUid);
  const quoteNo = quoteIndex.quoteNo || quoteIndex.quoteNoString || "";
  const quoteType = quoteIndex.quoteType || quoteIndex.quoteTypeResolved || "";
  const repairKey = indexId || displayId || quoteId || sourceQuoteId || quoteNo;
  if (!repairKey) return;

  setRepairingSavedQuoteLinkId(repairKey);

  try {
    if (isDevRuntime) {
      devLog("SAVED_QUOTE_OPEN_TRACE", {
        authUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid,
        indexId,
        quoteNo,
        quoteType,
        sourcePath: quoteIndex.sourcePath || "",
        sourceQuoteId,
        quoteId,
        attemptedPaths: candidatePaths,
        result: "repair-start",
      });
      devLog("SAVED_QUOTES_READ_BUDGET_TRACE", {
        action: "repairSavedQuoteLink",
        quoteIndexReads: 0,
        fullQuoteReads: 0,
        fallbackFunctionCalled: true,
        attemptedPaths: candidatePaths,
      });
      devLog("SAVED_QUOTE_LOW_READ_TRACE", {
        action: "repairSavedQuoteLink",
        quoteIndexReads: 0,
        fullQuoteReads: 0,
        fallbackAttempts: candidatePaths.length,
        callableUsed: true,
        broadScanUsed: false,
        attemptedPaths: candidatePaths,
      });
    }

    const resolveSavedQuoteCallable = httpsCallable(functions, "resolveSavedQuoteLink");
    const result = await resolveSavedQuoteCallable({
      workspaceUid,
      indexId: indexId || displayId || quoteId,
      quoteNo,
      sourceQuoteId,
      sourcePath: quoteIndex.sourcePath || "",
      quoteType,
    });
    const data = result?.data || {};

    if (isDevRuntime) {
      devLog("SAVED_QUOTE_OPEN_TRACE", {
        authUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid,
        indexId,
        quoteNo,
        quoteType,
        sourcePath: quoteIndex.sourcePath || "",
        sourceQuoteId,
        quoteId,
        attemptedPaths: candidatePaths,
        result: data.status || (data.ok ? "loaded" : "not-found"),
        resolvedPath: data.sourcePath || "",
        candidateCount: Array.isArray(data.candidates) ? data.candidates.length : 0,
      });
    }

    if (data.ok && data.quote) {
      const hydratedRepairedQuote = await hydrateCurtainQuoteRowsFromChunks({
        quote: data.quote,
        workspaceUid,
        label: "fullQuoteRowChunks.repairedSavedQuoteOpen",
      });
      const fullQuote = normalizeSavedQuoteForEditor(hydratedRepairedQuote, {
        ...quoteIndex,
        sourceQuoteId: data.sourceQuoteId || hydratedRepairedQuote.id,
        sourcePath: data.sourcePath || "",
      });
      const quoteIndexDocId = data.indexId || indexId || fullQuote.id;
      const localIndexRecord = {
        ...buildQuoteIndexPayload(fullQuote, fullQuote.id, workspaceUid),
        quoteIndexId: quoteIndexDocId,
        indexId: quoteIndexDocId,
        sourceResolvedAt: new Date(),
        staleSource: false,
        sourceMissingAt: null,
      };

      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      setSavedQuotes((prev) => {
        const replaceKeys = new Set(
          [quoteIndexDocId, indexId, displayId, quoteId, sourceQuoteId, fullQuote.id].filter(Boolean)
        );
        let replaced = false;
        const next = safeSavedQuoteArray(prev).map((item) => {
          const itemKeys = [
            item.id,
            item.quoteId,
            item.sourceQuoteId,
            item.indexId,
            item.quoteIndexId,
          ].filter(Boolean);
          if (!itemKeys.some((key) => replaceKeys.has(String(key)))) return item;
          replaced = true;
          return {
            ...item,
            ...localIndexRecord,
          };
        });
        return replaced ? next : [localIndexRecord, ...next];
      });
      setDeletedQuotes((prev) =>
        safeSavedQuoteArray(prev).map((item) => {
          const itemKeys = [
            item.id,
            item.quoteId,
            item.sourceQuoteId,
            item.indexId,
            item.quoteIndexId,
          ].filter(Boolean);
          return itemKeys.some((key) =>
            [quoteIndexDocId, indexId, displayId, quoteId, sourceQuoteId, fullQuote.id]
              .filter(Boolean)
              .includes(String(key))
          )
            ? {
                ...item,
                ...localIndexRecord,
              }
            : item;
        })
      );
      setSavedQuoteOpenError(null);
      notify("Quote link repaired and loaded.", "success");
      loadQuoteForEdit(fullQuote);
      return;
    }

    const message =
      data.status === "multiple-matches"
        ? "Multiple quotations match this old index. Open the exact quote from a repaired index result."
        : "This saved quote search result is stale. The source quotation document was not found.";
    setSavedQuoteOpenError({
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths: candidatePaths,
      result: data.status || "not-found",
      message,
      candidates: Array.isArray(data.candidates) ? data.candidates : [],
    });
    notify(message, "warning", 8000);
  } catch (error) {
    devError("Saved quote link repair failed", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      indexId,
      quoteNo,
    });
    setSavedQuoteOpenError((prev) => ({
      ...(prev || {}),
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths: candidatePaths,
      result: String(error?.code || "").includes("permission-denied")
        ? "permission-denied"
        : "resolve-error",
      code: error?.code || "",
      message: error?.message || "Could not repair this saved quote link.",
    }));
    notify("Could not repair this saved quote link.", "error", 7000);
  } finally {
    setRepairingSavedQuoteLinkId("");
  }
};

const findGeneralQuoteRecoveryOptions = useCallback(
  async (errorState = savedQuoteOpenError) => {
    const quoteIndex = errorState?.quoteIndex || {};
    const quoteNo = String(
      errorState?.quoteNo ||
        quoteIndex.quoteNo ||
        quoteIndex.quoteNoString ||
        quoteIndex.id ||
        ""
    ).trim();
    const quoteId = String(
      errorState?.quoteId ||
        errorState?.sourceQuoteId ||
        quoteIndex.quoteId ||
        quoteIndex.sourceQuoteId ||
        quoteIndex.id ||
        ""
    ).trim();
    const quoteIndexId = String(
      errorState?.indexId ||
        quoteIndex.quoteIndexId ||
        quoteIndex.indexId ||
        quoteIndex.id ||
        quoteNo ||
        ""
    ).trim();
    const customerNameForLocalSearch = String(
      quoteIndex.customerName || quoteIndex.customer || errorState?.customerName || ""
    ).trim();

    if (!workspaceUid || (!quoteNo && !quoteId && !quoteIndexId)) {
      notify("Quote recovery needs a workspace and quote number or id.", "warning", 6000);
      return;
    }

    setGeneralQuoteRecoveryState({
      running: true,
      error: "",
      quoteNo,
      sources: [],
      bestRecoverySource: null,
      checkedAt: "",
    });

    try {
      const localDrafts = findLocalGeneralQuoteDrafts({
        workspaceUid,
        quoteNo,
        customerName: customerNameForLocalSearch,
      });
      const findRecoveryCallable = httpsCallable(functions, "findGeneralQuoteRecoverySources");
      const result = await findRecoveryCallable({
        workspaceUid,
        quoteNo,
        quoteId,
        quoteIndexId,
      });
      const data = result?.data || {};
      const serverSources = Array.isArray(data.recoverySources)
        ? data.recoverySources
        : [];
      const decorateRecoverySource = (source = {}) => {
        const rawQuote = source.quote || source.rawPayload || source.payload || {};
        const contentSelection = resolveGeneralQuoteContentSelection(
          rawQuote,
          rawQuote.generalQuote || rawQuote.generalQuoteData || {},
          { allowCanonicalEmptyAliasRecovery: true }
        );
        const previewItems =
          Array.isArray(source.itemPreview) && source.itemPreview.length
            ? source.itemPreview
            : buildGeneralQuoteRecoveryItemPreview(contentSelection.items);
        return {
          ...source,
          itemPreview: previewItems,
          itemSourceField: source.itemSourceField || contentSelection.field || "",
          itemCount: Number(source.itemCount || contentSelection.count || 0),
        };
      };
      const sources = [...serverSources, ...localDrafts].map(decorateRecoverySource).sort((left, right) => {
        const leftRecoverable = left.canOpenAsDraft && Number(left.itemCount || 0) > 0 ? 1 : 0;
        const rightRecoverable = right.canOpenAsDraft && Number(right.itemCount || 0) > 0 ? 1 : 0;
        if (leftRecoverable !== rightRecoverable) return rightRecoverable - leftRecoverable;
        return (safeTimeMillis(right.updatedAt) || 0) - (safeTimeMillis(left.updatedAt) || 0);
      });
      const bestRecoverySource =
        sources.find((source) => source.canOpenAsDraft && Number(source.itemCount || 0) > 0) ||
        null;

      setGeneralQuoteRecoveryState({
        running: false,
        error: "",
        quoteNo,
        sources,
        bestRecoverySource,
        checkedAt: new Date().toISOString(),
      });

      if (isDevRuntime) {
        devLog("GENERAL_QUOTE_RECOVERY_OPTIONS_RESULT", {
          workspaceUid,
          quoteNo,
          quoteId,
          quoteIndexId,
          serverSourceCount: serverSources.length,
          localDraftCount: localDrafts.length,
          recoverableCount: sources.filter(
            (source) => source.canOpenAsDraft && Number(source.itemCount || 0) > 0
          ).length,
          sources: sources.map((source) => ({
            sourceType: source.sourceType,
            path: source.path,
            itemCount: source.itemCount,
            itemSourceField: source.itemSourceField,
            canOpenAsDraft: source.canOpenAsDraft,
          })),
        });
      }

      if (bestRecoverySource) {
        notify("Recovery found. You can open it as a draft and Save As New.", "success", 7000);
      } else {
        notify(
          "No item details were found in the quotation record, save attempts, or drafts available to this browser.",
          "warning",
          9000
        );
      }
    } catch (error) {
      devError("GENERAL_QUOTE_RECOVERY_OPTIONS_FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        workspaceUid,
        quoteNo,
        quoteId,
        quoteIndexId,
      });
      setGeneralQuoteRecoveryState({
        running: false,
        error: "Could not check recovery sources. Please try again.",
        quoteNo,
        sources: [],
        bestRecoverySource: null,
        checkedAt: new Date().toISOString(),
      });
      notify("Could not check recovery sources. Please try again.", "error", 7000);
    }
  },
  [notify, savedQuoteOpenError, workspaceUid]
);

const openGeneralQuoteRecoverySourceAsDraft = useCallback(
  async (source = {}) => {
    const sourceKey = `${source.sourceType || "source"}:${source.path || source.id || ""}`;
    setOpeningGeneralQuoteRecoveryKey(sourceKey);
    try {
    let sourceForOpen = source;
    let rawQuote = source.quote || source.rawPayload || source.payload || {};
    if (!rawQuote || !Object.keys(rawQuote).length) {
      if (!workspaceUid || !source.path) {
        notify("This recovery source needs a workspace and source path.", "warning", 6000);
        return;
      }
      const openRecoveryCallable = httpsCallable(functions, "openGeneralQuoteRecoverySource");
      const result = await openRecoveryCallable({
        workspaceUid,
        quoteNo: source.quoteNo || savedQuoteOpenError?.quoteNo || "",
        sourceType: source.sourceType || "",
        sourcePath: source.path || "",
        recoverySourceId: source.id || "",
      });
      const data = result?.data || {};
      if (!data.ok || !data.recoveredQuotePayload) {
        notify("This recovery source could not be opened.", "warning", 7000);
        return;
      }
      sourceForOpen = {
        ...source,
        ...(data.recoverySource || {}),
        quoteNo: data.quoteNo || source.quoteNo || "",
        customerName: data.customerName || source.customerName || "",
        grandTotal: Number(data.grandTotal ?? source.grandTotal ?? 0),
        itemCount: Number(data.itemCount ?? source.itemCount ?? 0),
        itemSourceField: data.itemSourceField || source.itemSourceField || "",
        itemPreview: Array.isArray(data.recoverySource?.itemPreview)
          ? data.recoverySource.itemPreview
          : buildGeneralQuoteRecoveryItemPreview(data.recoveredItems || []),
        quote: data.recoveredQuotePayload,
      };
      rawQuote = data.recoveredQuotePayload;
    }
    const contentSelection = resolveGeneralQuoteContentSelection(
      rawQuote,
      rawQuote.generalQuote || rawQuote.generalQuoteData || {},
      { allowCanonicalEmptyAliasRecovery: true }
    );
    const itemCount = Number(contentSelection.count || sourceForOpen.itemCount || 0);
    if (!itemCount) {
      notify("This recovery source has no item rows and cannot be opened.", "warning", 6000);
      return;
    }

    const sourceQuoteNo = String(
      sourceForOpen.quoteNo ||
        rawQuote.quoteNo ||
        rawQuote.quoteNoString ||
        savedQuoteOpenError?.quoteNo ||
        ""
    ).trim();
    const normalized = normalizeGeneralQuoteForEditor(
      {
        ...rawQuote,
        quoteType: GENERAL_QUOTE_TYPE,
        quotationType: GENERAL_QUOTE_TYPE,
        recoverySourceType: sourceForOpen.sourceType || "",
        recoverySourcePath: sourceForOpen.path || "",
        recoveredFromQuoteNo: sourceQuoteNo,
      },
      {
        quoteType: GENERAL_QUOTE_TYPE,
        quotationType: GENERAL_QUOTE_TYPE,
        customerName: sourceForOpen.customerName || "",
      }
    );
    const recoveredDraft = syncGeneralQuoteDefaults({
      ...normalized,
      id: "",
      quoteId: "",
      sourceQuoteId: "",
      quoteIndexId: "",
      indexId: "",
      sourcePath: "",
      quoteNo: "",
      originalQuoteNo: sourceQuoteNo,
      recoveredFromQuoteNo: sourceQuoteNo,
      recoverySourceType: sourceForOpen.sourceType || "",
      recoverySourcePath: sourceForOpen.path || "",
      recoveryItemSourceField: sourceForOpen.itemSourceField || contentSelection.field || "",
      loadStatus: "recovered_draft",
      saveTargetStatus: "new_recovery_draft",
      saveTargetReason: "opened_from_recovery_source_save_as_new_required",
    });
    const recoveredCustomerName =
      recoveredDraft.quoteFor?.company ||
      recoveredDraft.quoteFor?.name ||
      recoveredDraft.customerName ||
      sourceForOpen.customerName ||
      "";
    const recoveredSiteSelection = normalizeQuoteSiteSnapshot(recoveredDraft, {
      customerName: recoveredCustomerName,
    });

    setRoutedTab("quote", GENERAL_QUOTE_TYPE);
    setTab("quote");
    setSelectedQuote(null);
    setEditingQuoteId(null);
    setGeneralEditingQuoteId(null);
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setIsNewQuote(true);
    setQuoteNo("");
    setDate(resolveQuoteDateForInput(recoveredDraft, todayISO()));
    setCustomerName(recoveredCustomerName);
    setQuoteCustomerLink({
      customerId: recoveredDraft.customerId || "",
      customerKey: recoveredDraft.customerKey || recoveredDraft.customerId || "",
      customerName: recoveredCustomerName,
      phone: recoveredDraft.customerPhone || recoveredDraft.quoteFor?.phone || "",
      whatsapp: recoveredDraft.customerWhatsapp || "",
      email: recoveredDraft.customerEmail || recoveredDraft.quoteFor?.email || "",
      address: recoveredDraft.customerAddress || recoveredDraft.quoteFor?.address || "",
      companyName: recoveredDraft.customerCompanyName || recoveredDraft.quoteFor?.company || "",
    });
    setQuoteSiteSelection(recoveredSiteSelection);
    setSelectedQuoteWorkStage("quote_given");
    setSelectedQuoteStatus("draft");
    setSelectedInstallationDate(null);
    setPendingCrmLeadId("");
    setSavedQuoteOpenError(null);
    incompleteQuoteLoadRef.current = null;
    setIncompleteQuoteLoadState(null);
    deletedGeneralItemIdsRef.current = [];
    generalQuoteRef.current = recoveredDraft;
    setGeneralQuoteLive(recoveredDraft);
    markGeneralQuoteBaseline(recoveredDraft, "idle");
    setAutoSaveStatus("idle");

    if (isDevRuntime) {
      devLog("GENERAL_QUOTE_RECOVERY_DRAFT_OPENED", {
        recoveredFromQuoteNo: sourceQuoteNo,
        sourceType: sourceForOpen.sourceType || "",
        sourcePath: sourceForOpen.path || "",
        itemCount,
        itemSourceField: sourceForOpen.itemSourceField || contentSelection.field || "",
        saveModeRequired: "save_as_new",
      });
    }
    notify(
      `Recovery draft opened${sourceQuoteNo ? ` from ${sourceQuoteNo}` : ""}. Use Save As New to preserve it.`,
      "success",
      8000
    );
    } catch (error) {
      devError("GENERAL_QUOTE_OPEN_RECOVERY_SOURCE_FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        workspaceUid,
        sourceType: source.sourceType || "",
        sourcePath: source.path || "",
      });
      notify("Could not open this recovery source. Please try another source.", "error", 7000);
    } finally {
      setOpeningGeneralQuoteRecoveryKey("");
    }
  },
  [
    markGeneralQuoteBaseline,
    notify,
    savedQuoteOpenError?.quoteNo,
    setGeneralQuoteLive,
    setRoutedTab,
    syncGeneralQuoteDefaults,
    workspaceUid,
  ]
);

const repairLegacyGeneralQuoteFormat = async (source = {}) => {
  if (!workspaceUid || !canRepairLegacyGeneralQuoteFormat) {
    notify("Only workspace admins can repair old General quotation formats.", "warning", 6000);
    return;
  }
  const sourceKey = `${source.sourceType || "source"}:${source.path || source.id || ""}`;
  setRepairingLegacyGeneralQuoteKey(sourceKey);
  try {
    const repairCallable = httpsCallable(functions, "repairLegacyGeneralQuoteFormat");
    const result = await repairCallable({
      workspaceUid,
      quoteId: source.id || source.quoteId || savedQuoteOpenError?.quoteId || "",
      quoteIndexId:
        savedQuoteOpenError?.indexId ||
        savedQuoteOpenError?.quoteIndex?.quoteIndexId ||
        savedQuoteOpenError?.quoteIndex?.indexId ||
        "",
      sourcePath: source.path || savedQuoteOpenError?.resolvedMainQuotePath || "",
    });
    const data = result?.data || {};
    if (!data.ok || !data.quote) {
      notify("Could not repair this quotation format.", "warning", 7000);
      return;
    }
    const fullQuote = normalizeSavedQuoteForEditor(data.quote, {
      ...(savedQuoteOpenError?.quoteIndex || {}),
      sourceQuoteId: data.quoteId || data.quote?.quoteId || "",
      sourcePath: data.sourcePath || "",
      quoteIndexId: data.quoteIndexId || "",
      indexId: data.quoteIndexId || "",
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    setSavedQuoteOpenError(null);
    setGeneralQuoteRecoveryState({
      running: false,
      error: "",
      quoteNo: "",
      sources: [],
      bestRecoverySource: null,
      checkedAt: "",
    });
    loadQuoteForEdit(fullQuote);
    notify(
      `Legacy format repaired. Backup created at ${data.backupPath || "quotation_migration_backups"}.`,
      "success",
      8000
    );
  } catch (error) {
    devError("REPAIR_LEGACY_GENERAL_QUOTE_FORMAT_FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      sourceType: source.sourceType || "",
      sourcePath: source.path || "",
    });
    notify(
      error?.code === "permission-denied"
        ? "Only workspace admins can repair old quotation formats."
        : "Could not repair this quotation format.",
      "error",
      8000
    );
  } finally {
    setRepairingLegacyGeneralQuoteKey("");
  }
};

const moveBrokenSavedQuoteListingToBin = useCallback(async () => {
  const quote = savedQuoteOpenError?.quoteIndex;
  if (!quote) {
    notify("Broken quote listing data is missing.", "warning", 5000);
    return;
  }
  const busyKey = getSavedQuoteWorkflowBusyKey(quote, "moveToBin", workspaceUid);
  setSavedQuoteActionBusyKey(busyKey);
  try {
    await performSavedQuoteAction({ quote, action: "moveToBin" });
    setSavedQuoteOpenError(null);
    setGeneralQuoteRecoveryState({
      running: false,
      error: "",
      quoteNo: "",
      sources: [],
      bestRecoverySource: null,
      checkedAt: "",
    });
    notify("Broken quotation listing moved to Recycle Bin.", "success", 5000);
  } catch (error) {
    devError("GENERAL_QUOTE_RECOVERY_MOVE_TO_BIN_FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      quoteNo: savedQuoteOpenError?.quoteNo || quote.quoteNo || "",
      quoteIndexId: quote.quoteIndexId || quote.indexId || quote.id || "",
    });
    notify("Could not move this quotation to Recycle Bin.", "error", 7000);
  } finally {
    setSavedQuoteActionBusyKey("");
  }
}, [notify, performSavedQuoteAction, savedQuoteOpenError, workspaceUid]);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED || typeof window === "undefined") return undefined;

  window.__FQ_AUDIT_QUOTE_LOAD_MISMATCH__ = async (quoteIdOrNo = "") => {
    const lookup = String(quoteIdOrNo || "").trim();
    if (!workspaceUid || !lookup) {
      const report = {
        ok: false,
        reason: !workspaceUid ? "workspace_missing" : "quote_id_or_number_missing",
      };
      devWarn("QUOTE_LOAD_MISMATCH_AUDIT", report);
      return report;
    }

    let quoteIndexSnap = await getDoc(
      doc(db, "users", workspaceUid, "quote_index", lookup),
      { label: "quoteLoadMismatchAudit.indexById" }
    ).catch((error) => ({ error }));
    if (quoteIndexSnap?.error) {
      devWarn("QUOTE_LOAD_MISMATCH_AUDIT_INDEX_READ_FAILED", {
        lookup,
        code: quoteIndexSnap.error?.code || "",
        message: quoteIndexSnap.error?.message || String(quoteIndexSnap.error),
      });
      quoteIndexSnap = null;
    }

    if (!quoteIndexSnap?.exists?.()) {
      const querySnap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "quote_index"),
          firestoreWhere("quoteNo", "==", lookup),
          firestoreLimit(1)
        ),
        { label: "quoteLoadMismatchAudit.indexByQuoteNo" }
      );
      quoteIndexSnap = querySnap.docs[0] || null;
    }

    const quoteIndex = quoteIndexSnap?.exists?.()
      ? {
          id: quoteIndexSnap.id,
          quoteIndexId: quoteIndexSnap.id,
          indexId: quoteIndexSnap.id,
          ...quoteIndexSnap.data(),
        }
      : {
          id: lookup,
          quoteIndexId: lookup,
          indexId: lookup,
          quoteId: lookup,
          sourceQuoteId: lookup,
          sourcePath: `users/${workspaceUid}/quotations/${lookup}`,
        };
    const { candidatePaths } = getSavedQuoteOpenCandidates(quoteIndex, workspaceUid);
    const triedReports = [];
    let selectedReport = null;

    for (const candidatePath of candidatePaths) {
      const snap = await getDoc(doc(db, ...candidatePath.split("/")), {
        label: "quoteLoadMismatchAudit.fullQuoteCandidate",
      }).catch((error) => ({ error }));
      if (snap?.error) {
        triedReports.push({
          path: candidatePath,
          exists: false,
          errorCode: snap.error?.code || "",
          errorMessage: snap.error?.message || String(snap.error),
        });
        continue;
      }
      if (!snap?.exists?.()) {
        triedReports.push({ path: candidatePath, exists: false });
        continue;
      }
      const fullQuote = { id: snap.id, ...snap.data() };
      const report = buildQuoteLoadMismatchAuditReport({
        quoteIndex,
        fullQuote,
        quoteIndexId: quoteIndex.quoteIndexId || quoteIndex.indexId || quoteIndex.id || lookup,
        quoteId: snap.id,
        sourcePath: candidatePath,
        triedPaths: candidatePaths,
      });
      triedReports.push({ path: candidatePath, exists: true, report });
      if (!selectedReport || report.selectedLoadCount > selectedReport.selectedLoadCount) {
        selectedReport = report;
      }
    }

    const report =
      selectedReport ||
      buildQuoteLoadMismatchAuditReport({
        quoteIndex,
        fullQuote: null,
        quoteIndexId: quoteIndex.quoteIndexId || quoteIndex.indexId || quoteIndex.id || lookup,
        quoteId: quoteIndex.sourceQuoteId || quoteIndex.quoteId || lookup,
        sourcePath: quoteIndex.sourcePath || "",
        triedPaths: candidatePaths,
      });
    const payload = {
      ...report,
      ok: true,
      workspaceUid,
      lookup,
      quoteIndexExists: Boolean(quoteIndexSnap?.exists?.()),
      candidateReports: triedReports,
    };
    devLog("QUOTE_LOAD_MISMATCH_AUDIT", payload);
    return payload;
  };

  return () => {
    if (window.__FQ_AUDIT_QUOTE_LOAD_MISMATCH__) {
      delete window.__FQ_AUDIT_QUOTE_LOAD_MISMATCH__;
    }
  };
}, [workspaceUid]);

  /* --------------------------- STYLES --------------------------- */

  const pageBg = isDark ? "#020617" : "#e5e7eb";
  const textColor = isDark ? "#e5e7eb" : "#0f172a";

  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    justifySelf: "start",
    flex: "0 0 auto",
    width: "auto",
    maxWidth: "100%",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    marginRight: 8,
    color: fg,
    background: bg,
    fontSize: 14,
    whiteSpace: "nowrap",
  });

  const input = {
    padding: 6,
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    background: isDark ? "#020617" : "#ffffff",
    color: textColor,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    fontSize: 13,
  };

  const selectInput = {
    ...input,
    appearance: "auto",
    WebkitAppearance: "menulist",
    MozAppearance: "menulist",
    WebkitTextFillColor: textColor,
    color: textColor,
    colorScheme: isDark ? "dark" : "light",
  };

  const renderReferralPartnerSelector = ({
    context = quoteSubTab,
    label = "Referred By",
  } = {}) => {
    const selectedPartner = selectedReferralPartner;
    const isOpen = referralPartnerPickerState.activeContext === context;
    const queryValue = isOpen
      ? referralPartnerPickerState.query
      : selectedPartner?.displayName || referralPartnerPickerState.query || "";
    const normalizedQuery = normalizeSearchText(queryValue);
    const compactQuery = normalizeSearchCompact(queryValue);
    const matches = referralPartners
      .filter((partner) => {
        if (partner.active === false || partner.deleted === true) return false;
        if (!normalizedQuery && !compactQuery) return true;
        const haystack = normalizeSearchText(
          [
            partner.displayName,
            partner.companyName,
            partner.phone,
            partner.email,
            getReferralPartnerTypeLabel(partner.type),
          ]
            .filter(Boolean)
            .join(" ")
        );
        const compactHaystack = normalizeSearchCompact(haystack);
        return (
          haystack.includes(normalizedQuery) ||
          (compactQuery && compactHaystack.includes(compactQuery))
        );
      })
      .slice(0, REFERRAL_PARTNER_RESULT_LIMIT);
    const openAddModal = () => {
      setReferralPartnerModalState({
        open: true,
        context,
        draft: createReferralPartnerDraft(queryValue),
        saving: false,
        error: "",
        duplicate: null,
      });
      setReferralPartnerPickerState((prev) => ({
        ...prev,
        activeContext: "",
      }));
    };

    return (
      <div style={{ position: "relative", minWidth: 0 }}>
        <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 700 }}>
          {label}
        </label>
        <div style={{ position: "relative", marginTop: 6 }}>
          <input
            style={{
              ...input,
              paddingRight: selectedPartner ? 76 : 14,
              borderRadius: 14,
              paddingTop: 11,
              paddingBottom: 11,
              border: selectedPartner
                ? "1px solid rgba(20,184,166,0.45)"
                : input.border,
              boxShadow: selectedPartner
                ? "0 0 0 3px rgba(20,184,166,0.08)"
                : "none",
            }}
            value={queryValue}
            onFocus={() => {
              setReferralPartnerPickerState({
                query: selectedPartner?.displayName || queryValue || "",
                activeContext: context,
              });
              loadReferralPartners();
            }}
            onChange={(event) => {
              const nextValue = event.target.value;
              setReferralPartnerPickerState({
                query: nextValue,
                activeContext: context,
              });
              if (!nextValue.trim()) {
                applyReferralPartnerSelection(null, context, {
                  action: "clear",
                });
              }
            }}
            onBlur={() => {
              window.setTimeout(() => {
                setReferralPartnerPickerState((prev) =>
                  prev.activeContext === context
                    ? { ...prev, activeContext: "" }
                    : prev
                );
              }, 140);
            }}
            placeholder="Select designer / architect"
            autoComplete="off"
          />
          {selectedPartner ? (
            <button
              type="button"
              onClick={() =>
                applyReferralPartnerSelection(null, context, {
                  action: "clear",
                })
              }
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                color: isDark ? "#93c5fd" : "#2563eb",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
        {isOpen ? (
          <div
            style={{
              position: "absolute",
              zIndex: 40,
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              maxHeight: 300,
              overflowY: "auto",
              border: `1px solid ${isDark ? "#334155" : "#dbe4ef"}`,
              borderRadius: 16,
              background: isDark ? "#0f172a" : "#ffffff",
              boxShadow: "0 20px 45px rgba(15,23,42,0.18)",
              padding: 8,
            }}
          >
            {referralPartnersLoadState.loading ? (
              <div style={{ padding: 10, fontSize: 12, color: dashboardMuted }}>
                Loading referral partners...
              </div>
            ) : null}
            {matches.map((partner) => (
              <button
                key={partner.partnerId || partner.id || partner.displayName}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applyReferralPartnerSelection(partner, context)}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 6,
                  textAlign: "left",
                  cursor: "pointer",
                  background:
                    selectedPartner?.partnerId &&
                    selectedPartner.partnerId === partner.partnerId
                      ? isDark
                        ? "rgba(20,184,166,0.22)"
                        : "rgba(20,184,166,0.12)"
                      : "transparent",
                  color: textColor,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 900 }}>
                    {partner.displayName || "Unnamed partner"}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      borderRadius: 999,
                      padding: "3px 7px",
                      background: isDark
                        ? "rgba(59,130,246,0.22)"
                        : "rgba(37,99,235,0.1)",
                      color: isDark ? "#bfdbfe" : "#1d4ed8",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getReferralPartnerTypeLabel(partner.type)}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: dashboardMuted,
                    lineHeight: 1.45,
                  }}
                >
                  {partner.companyName || partner.phone || partner.email || "No phone/company saved"}
                </div>
              </button>
            ))}
            {!referralPartnersLoadState.loading && matches.length === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: dashboardMuted }}>
                No referral partner found.
              </div>
            ) : null}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={openAddModal}
              style={{
                width: "100%",
                border: `1px dashed ${isDark ? "#475569" : "#94a3b8"}`,
                borderRadius: 12,
                padding: "10px 12px",
                background: isDark ? "rgba(15,23,42,0.8)" : "#f8fafc",
                color: isDark ? "#bfdbfe" : "#1d4ed8",
                fontWeight: 900,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              + Add New Referral Partner
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderReferralPartnerModal = () => {
    if (!referralPartnerModalState.open) return null;
    const draft = referralPartnerModalState.draft || createReferralPartnerDraft();
    const updateDraft = (patch) =>
      setReferralPartnerModalState((prev) => ({
        ...prev,
        draft: {
          ...(prev.draft || createReferralPartnerDraft()),
          ...patch,
        },
        error: "",
        duplicate: null,
      }));

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          background: "rgba(15,23,42,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 22,
            background: isDark ? "#0f172a" : "#ffffff",
            color: textColor,
            boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
            padding: 22,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>
                Add Referral Partner
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 4 }}>
                Internal only. This will not appear on customer PDFs.
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                setReferralPartnerModalState({
                  open: false,
                  context: "",
                  draft: createReferralPartnerDraft(),
                  saving: false,
                  error: "",
                  duplicate: null,
                })
              }
              style={{
                border: "none",
                background: "transparent",
                color: dashboardMuted,
                cursor: "pointer",
                fontSize: 20,
                fontWeight: 900,
              }}
              aria-label="Close referral partner modal"
            >
              x
            </button>
          </div>

          <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
            <div>
              <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                Name *
              </label>
              <input
                style={{ ...input, marginTop: 6, padding: 10, borderRadius: 12 }}
                value={draft.displayName || ""}
                onChange={(event) => updateDraft({ displayName: event.target.value })}
                placeholder="ABC Interiors"
              />
            </div>
            <div>
              <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                Type
              </label>
              <select
                style={{ ...selectInput, marginTop: 6, padding: 10, borderRadius: 12 }}
                value={normalizeReferralPartnerType(draft.type)}
                onChange={(event) => updateDraft({ type: event.target.value })}
              >
                {REFERRAL_PARTNER_TYPES.map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                  Phone
                </label>
                <input
                  style={{ ...input, marginTop: 6, padding: 10, borderRadius: 12 }}
                  value={draft.phone || ""}
                  onChange={(event) => updateDraft({ phone: event.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                  Email
                </label>
                <input
                  style={{ ...input, marginTop: 6, padding: 10, borderRadius: 12 }}
                  value={draft.email || ""}
                  onChange={(event) => updateDraft({ email: event.target.value })}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                Company / Firm Name
              </label>
              <input
                style={{ ...input, marginTop: 6, padding: 10, borderRadius: 12 }}
                value={draft.companyName || ""}
                onChange={(event) => updateDraft({ companyName: event.target.value })}
                placeholder="Firm name"
              />
            </div>
            <div>
              <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 800 }}>
                Notes
              </label>
              <textarea
                style={{
                  ...input,
                  marginTop: 6,
                  padding: 10,
                  borderRadius: 12,
                  minHeight: 80,
                  resize: "vertical",
                }}
                value={draft.notes || ""}
                onChange={(event) => updateDraft({ notes: event.target.value })}
                placeholder="Internal notes"
              />
            </div>
          </div>

          {referralPartnerModalState.error ? (
            <div
              style={{
                marginTop: 14,
                borderRadius: 12,
                padding: 10,
                background: isDark ? "rgba(220,38,38,0.16)" : "#fee2e2",
                color: isDark ? "#fecaca" : "#991b1b",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {referralPartnerModalState.error}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <button
              type="button"
              onClick={() =>
                setReferralPartnerModalState({
                  open: false,
                  context: "",
                  draft: createReferralPartnerDraft(),
                  saving: false,
                  error: "",
                  duplicate: null,
                })
              }
              disabled={referralPartnerModalState.saving}
              style={btn(isDark ? "#334155" : "#e2e8f0", textColor)}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveReferralPartnerFromModal}
              disabled={referralPartnerModalState.saving}
              style={{
                ...btn("#0f766e", "white"),
                opacity: referralPartnerModalState.saving ? 0.65 : 1,
              }}
            >
              {referralPartnerModalState.saving ? "Saving..." : "Save & Select"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerLedgerCombobox = ({
    context = CURTAIN_QUOTE_TYPE,
    label = "Customer Name",
    value = "",
    selectedCustomerId = "",
    onTextChange,
    seed = {},
  } = {}) => {
    const query = normalizeCustomerSearchText(value);
    const searchState = quotationCustomerSearchState[context] || {};
    const searchResults = Array.isArray(searchState.results)
      ? searchState.results
      : [];
    const shouldSearch = query.length >= 2;
    const selectedCustomerFromState =
      customerRecords.find((customer) => customer.id === selectedCustomerId) ||
      searchResults.find((customer) => customer.id === selectedCustomerId) ||
      null;
    const availableCustomers = shouldSearch
      ? mergePersonSearchResultRecords([
          ...(selectedCustomerFromState ? [selectedCustomerFromState] : []),
          ...searchResults,
        ])
      : selectedCustomerFromState
      ? [selectedCustomerFromState]
      : [];
    const selectedCustomer =
      availableCustomers.find((customer) => customer.id === selectedCustomerId) ||
      selectedCustomerFromState ||
      null;
    const matches = availableCustomers
      .filter((customer) => {
        if (!query) return selectedCustomerId && customer.id === selectedCustomerId;
        return buildCustomerLedgerSearchText(customer).includes(query);
      })
      .sort((a, b) => {
        if (selectedCustomerId && a.id === selectedCustomerId) return -1;
        if (selectedCustomerId && b.id === selectedCustomerId) return 1;
        return String(a.customerName || "").localeCompare(
          String(b.customerName || "")
        );
      })
      .slice(0, CUSTOMER_SEARCH_RESULT_LIMIT);
    const exactNameExists = availableCustomers.some(
      (customer) =>
        normalizeCustomerLedgerName(customer.customerName) ===
        normalizeCustomerLedgerName(value)
    );
    const isOpen = customerPickerOpenKey === context;
    const createSeed = {
      ...seed,
      customerName: value || seed.customerName || "",
    };

    return (
      <div style={{ position: "relative", minWidth: 0 }}>
        <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 700 }}>
          {label}
        </label>
        <div
          style={{
            position: "relative",
            marginTop: 6,
          }}
        >
          <input
            style={{
              ...input,
              paddingRight: selectedCustomer ? 112 : 14,
              borderRadius: 14,
              paddingTop: 11,
              paddingBottom: 11,
              border: selectedCustomer
                ? "1px solid rgba(37,99,235,0.45)"
                : input.border,
              boxShadow: selectedCustomer
                ? "0 0 0 3px rgba(37,99,235,0.08)"
                : "none",
            }}
            value={value ?? ""}
            onFocus={() => {
              setCustomerPickerOpenKey(context);
              if (normalizeCustomerSearchText(value).length >= 2) {
                setQuotationCustomerSearchQueries((prev) => ({
                  ...prev,
                  [context]: value,
                }));
              }
            }}
            onChange={(event) => {
              const nextValue = event.target.value;
              onTextChange?.(nextValue);
              setQuotationCustomerSearchQueries((prev) => ({
                ...prev,
                [context]: nextValue,
              }));
              setCustomerPickerOpenKey(context);
            }}
            onBlur={() => {
              window.setTimeout(() => {
                setCustomerPickerOpenKey((current) =>
                  current === context ? "" : current
                );
              }, 140);
            }}
            placeholder="Search or type customer name..."
            autoComplete="off"
          />
          {selectedCustomer ? (
            <button
              type="button"
              onClick={() => openCustomerLedger(selectedCustomer.id)}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                borderRadius: 999,
                padding: "5px 9px",
                background: isDark ? "rgba(37,99,235,0.22)" : "#dbeafe",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Linked
            </button>
          ) : null}
        </div>
        {isOpen ? (
          <div
            style={{
              position: "absolute",
              zIndex: 40,
              left: 0,
              right: 0,
              top: "calc(100% + 8px)",
              padding: 8,
              borderRadius: 18,
              background: isDark ? "#0f172a" : "#ffffff",
              border: isDark
                ? "1px solid rgba(148,163,184,0.22)"
                : "1px solid rgba(226,232,240,0.95)",
              boxShadow: isDark
                ? "0 24px 54px rgba(2,6,23,0.48)"
                : "0 24px 54px rgba(15,23,42,0.16)",
              display: "grid",
              gap: 8,
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {searchState.loading ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  color: dashboardMuted,
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                Searching customer records...
              </div>
            ) : null}
            {searchState.error ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(239,68,68,0.14)" : "#fef2f2",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {searchState.error}
              </div>
            ) : null}
            {!searchState.loading && matches.length > 0 ? (
              matches.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCustomerToQuoteContext(context, customer)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background:
                      customer.id === selectedCustomerId
                        ? isDark
                          ? "rgba(37,99,235,0.22)"
                          : "#eff6ff"
                        : isDark
                        ? "rgba(255,255,255,0.04)"
                        : "#f8fafc",
                    color: textColor,
                    cursor: "pointer",
                    display: "grid",
                    gap: 5,
                  }}
                >
                  {(() => {
                    const sourceText = getCustomerPersonBadgeLabel(customer);
                    const latestQuoteText =
                      customer.latestQuoteNo || customer.lastQuoteNo
                        ? `Latest quote: ${customer.latestQuoteNo || customer.lastQuoteNo}`
                        : "";
                    const quoteCountText = Number(customer.quoteCount || 0) > 0
                      ? `${Number(customer.quoteCount || 0)} quotation${
                          Number(customer.quoteCount || 0) === 1 ? "" : "s"
                        }`
                      : "";
                    const stageText = customer.stageLabel || customer.lastDealStage || "";
                    const lastActivityLabel = formatSafeDate(customer.lastActivityAt);
                    return (
                      <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "flex-start",
                      minWidth: 0,
                    }}
                  >
                    <strong style={{ fontSize: 14, overflowWrap: "anywhere" }}>
                      {customer.customerName || "Unnamed Customer"}
                    </strong>
                    <span
                      style={{
                        flex: "0 0 auto",
                        borderRadius: 999,
                        padding: "3px 8px",
                        background: isDark ? "rgba(148,163,184,0.12)" : "#e2e8f0",
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {sourceText}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
                    {[
                      customer.phone || customer.whatsapp,
                      customer.companyName,
                      customer.siteName,
                      customer.address || customer.siteAddress,
                    ]
                      .filter(Boolean)
                      .join(" | ") || "No contact details yet"}
                  </div>
                  {latestQuoteText || stageText ? (
                    <div style={{ fontSize: 11, color: dashboardMuted }}>
                      {[latestQuoteText, quoteCountText, stageText].filter(Boolean).join(" | ")}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 11, color: dashboardMuted }}>
                    Last activity: {lastActivityLabel}
                  </div>
                      </>
                    );
                  })()}
                </button>
              ))
            ) : !searchState.loading ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  color: dashboardMuted,
                  fontSize: 13,
                }}
              >
                {shouldSearch
                  ? "No existing customer found"
                  : "Type at least 2 characters to search all customer records"}
              </div>
            ) : null}
            {String(value || "").trim() && !exactNameExists ? (
              <div style={{ display: "grid", gap: 8 }}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => openCreateCustomerFromQuote(context, createSeed)}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                    color: "#2563eb",
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  + Create new customer "{String(value || "").trim()}"
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => openCreateLeadFromQuoteCustomer(context, createSeed)}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(20,184,166,0.16)" : "#ccfbf1",
                    color: isDark ? "#5eead4" : "#0f766e",
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  + Create lead from "{String(value || "").trim()}"
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onTextChange?.(String(value || "").trim());
                    setCustomerPickerOpenKey("");
                  }}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(148,163,184,0.1)" : "#f8fafc",
                    color: dashboardMuted,
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  Use typed name without linking
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {selectedCustomer ? (
          <div style={{ marginTop: 7, fontSize: 12, color: dashboardMuted }}>
            Linked to Customer Ledger. This quote will save against the selected customer.
          </div>
        ) : (
          <div style={{ marginTop: 7, fontSize: 12, color: dashboardMuted }}>
            Type freely or pick one unified customer record. Search checks the customer index only.
          </div>
        )}
      </div>
    );
  };

  const renderQuoteSiteProjectPanel = ({
    context = CURTAIN_QUOTE_TYPE,
    selectedCustomerId = "",
  } = {}) => {
    const normalizedCustomerId = String(selectedCustomerId || "").trim();
    const site =
      context === GENERAL_QUOTE_TYPE
        ? normalizeQuoteSiteSnapshot(
            {
              ...quoteSiteSelection,
              ...generalQuote,
            },
            {},
            { preserveWhitespace: true }
          )
        : normalizeQuoteSiteSnapshot(
            quoteSiteSelection,
            {},
            { preserveWhitespace: true }
          );
    const availableSites = normalizedCustomerId
      ? customerSitesByCustomerId[normalizedCustomerId] || []
      : [];
    const isOpen = customerSitePickerOpenKey === context;
    const isLoading =
      normalizedCustomerId && customerSitesLoadingId === normalizedCustomerId;
    const updateSite = (patch, options = {}) =>
      updateQuoteSiteSelectionForContext(context, patch, options);
    const openPicker = () => {
      setCustomerSitePickerOpenKey(context);
      if (normalizedCustomerId) {
        loadCustomerSitesForQuote(normalizedCustomerId);
      }
    };
    const selectedSiteId = String(site.siteId || "").trim();

    return (
      <div
        style={{
          gridColumn: "1 / -1",
          borderRadius: 18,
          border: isDark
            ? "1px solid rgba(148,163,184,0.18)"
            : "1px solid rgba(226,232,240,0.95)",
          background: isDark ? "rgba(15,23,42,0.62)" : "#f8fafc",
          padding: 14,
          display: "grid",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: textColor }}>
              Site / Project Location
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3 }}>
              Pick an existing site for this customer or type a new project location.
            </div>
          </div>
          {selectedSiteId ? (
            <span
              style={{
                borderRadius: 999,
                padding: "4px 9px",
                background: isDark ? "rgba(37,99,235,0.2)" : "#dbeafe",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 900,
              }}
            >
              Saved site selected
            </span>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "1.1fr 1.4fr",
            gap: 12,
          }}
        >
          <div style={{ position: "relative" }}>
            <label style={{ fontSize: 12, opacity: 0.72, fontWeight: 800 }}>
              Site / Project Name
            </label>
            <input
              style={{ ...input, marginTop: 6 }}
              value={site.siteName}
              onFocus={openPicker}
              onChange={(event) => {
                const nextName = event.target.value;
                updateSite(
                  {
                  siteId: "",
                  customerSiteId: "",
                  siteName: nextName,
                  quoteDisplayTitle: buildQuoteDisplayTitleFromParts({
                    customerName:
                      context === GENERAL_QUOTE_TYPE
                        ? generalQuote.quoteFor?.company ||
                          generalQuote.quoteFor?.name ||
                          generalQuote.customerName
                        : customerName,
                    siteName: nextName,
                  }),
                  },
                  { preserveWhitespace: true }
                );
                setCustomerSitePickerOpenKey(context);
              }}
              onBlur={() => {
                const trimmedName = String(site.siteName || "").trim();
                if (trimmedName !== site.siteName) {
                  updateSite({
                    siteName: trimmedName,
                    quoteDisplayTitle: buildQuoteDisplayTitleFromParts({
                      customerName:
                        context === GENERAL_QUOTE_TYPE
                          ? generalQuote.quoteFor?.company ||
                            generalQuote.quoteFor?.name ||
                            generalQuote.customerName
                          : customerName,
                      siteName: trimmedName,
                    }),
                  });
                }
                window.setTimeout(() => {
                  setCustomerSitePickerOpenKey((current) =>
                    current === context ? "" : current
                  );
                }, 140);
              }}
              placeholder={
                normalizedCustomerId
                  ? "Search/select saved site or type new site"
                  : "Select a customer to show saved sites"
              }
              autoComplete="off"
            />
            {isOpen ? (
              <div
                style={{
                  position: "absolute",
                  zIndex: 36,
                  left: 0,
                  right: 0,
                  top: "calc(100% + 8px)",
                  padding: 8,
                  borderRadius: 16,
                  background: isDark ? "#0f172a" : "#ffffff",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.22)"
                    : "1px solid rgba(226,232,240,0.95)",
                  boxShadow: isDark
                    ? "0 20px 46px rgba(2,6,23,0.42)"
                    : "0 20px 46px rgba(15,23,42,0.14)",
                  display: "grid",
                  gap: 8,
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {isLoading ? (
                  <div style={{ padding: 10, color: dashboardMuted, fontSize: 12, fontWeight: 800 }}>
                    Loading saved sites...
                  </div>
                ) : null}
                {!normalizedCustomerId ? (
                  <div style={{ padding: 10, color: dashboardMuted, fontSize: 12 }}>
                    Link a customer first to reuse saved project locations.
                  </div>
                ) : !isLoading && availableSites.length === 0 ? (
                  <div style={{ padding: 10, color: dashboardMuted, fontSize: 12 }}>
                    No saved sites for this customer yet.
                  </div>
                ) : null}
                {availableSites.map((savedSite) => (
                  <button
                    key={savedSite.siteId || savedSite.id}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      updateSite(savedSite, { replace: true });
                      setCustomerSitePickerOpenKey("");
                    }}
                    style={{
                      border: "none",
                      borderRadius: 13,
                      padding: 10,
                      textAlign: "left",
                      background:
                        savedSite.siteId === selectedSiteId
                          ? isDark
                            ? "rgba(37,99,235,0.22)"
                            : "#eff6ff"
                          : isDark
                          ? "rgba(255,255,255,0.04)"
                          : "#f8fafc",
                      color: textColor,
                      cursor: "pointer",
                      display: "grid",
                      gap: 4,
                    }}
                  >
                    <strong style={{ fontSize: 13 }}>{savedSite.siteName}</strong>
                    <span style={{ fontSize: 12, color: dashboardMuted }}>
                      {[savedSite.siteAddress, savedSite.siteContactPhone]
                        .filter(Boolean)
                        .join(" | ") || "No address/contact saved"}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.72, fontWeight: 800 }}>
              Site Address
            </label>
            <input
              style={{ ...input, marginTop: 6 }}
              value={site.siteAddress}
              onChange={(event) => updateSite({ siteAddress: event.target.value })}
              placeholder="Apartment, villa, branch, floor, city..."
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "repeat(3, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <div>
            <label style={{ fontSize: 12, opacity: 0.72, fontWeight: 800 }}>
              Site Contact
            </label>
            <input
              style={{ ...input, marginTop: 6 }}
              value={site.siteContactName}
              onChange={(event) =>
                updateSite({ siteContactName: event.target.value })
              }
              placeholder="Optional contact person"
            />
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.72, fontWeight: 800 }}>
              Site Phone
            </label>
            <input
              style={{ ...input, marginTop: 6 }}
              value={site.siteContactPhone}
              onChange={(event) =>
                updateSite({ siteContactPhone: event.target.value })
              }
              placeholder="Optional phone"
            />
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.72, fontWeight: 800 }}>
              Site Notes
            </label>
            <input
              style={{ ...input, marginTop: 6 }}
              value={site.siteNotes}
              onChange={(event) => updateSite({ siteNotes: event.target.value })}
              placeholder="Access, timing, installation notes"
            />
          </div>
        </div>
      </div>
    );
  };

  const getCurtainQuoteCustomerIdentity = (stateOverride = null) => {
    const sourceCustomerName = stateOverride?.customerName ?? customerName;
    const sourceQuoteCustomerLink =
      stateOverride?.quoteCustomerLink || quoteCustomerLink || {};
    const sourceSelectedQuote = stateOverride?.selectedQuote || selectedQuote || {};
    return {
    customerName: String(sourceCustomerName || "").trim(),
    phone:
      sourceQuoteCustomerLink.phone ||
      sourceSelectedQuote?.customerPhone ||
      sourceSelectedQuote?.crm?.customerPhone ||
      "",
    whatsapp:
      sourceQuoteCustomerLink.whatsapp ||
      sourceSelectedQuote?.customerWhatsapp ||
      sourceSelectedQuote?.crm?.customerWhatsapp ||
      "",
    email:
      sourceQuoteCustomerLink.email ||
      sourceSelectedQuote?.customerEmail ||
      sourceSelectedQuote?.email ||
      sourceSelectedQuote?.crm?.contact?.email ||
      "",
    address:
      sourceQuoteCustomerLink.address ||
      sourceSelectedQuote?.customerAddress ||
      sourceSelectedQuote?.address ||
      sourceSelectedQuote?.crm?.contact?.address ||
      "",
    companyName:
      sourceQuoteCustomerLink.companyName ||
      sourceSelectedQuote?.customerCompanyName ||
      sourceSelectedQuote?.companyName ||
      "",
    };
  };

  const getCurtainQuoteSelectedCustomerId = (stateOverride = null) => {
    const sourceQuoteCustomerLink =
      stateOverride?.quoteCustomerLink || quoteCustomerLink || {};
    const sourceSelectedQuote = stateOverride?.selectedQuote || selectedQuote || {};
    return (
      sourceQuoteCustomerLink.customerId ||
      sourceSelectedQuote?.customerId ||
      sourceSelectedQuote?.crm?.customerId ||
      ""
    );
  };

  const buildQuoteCustomerLinkFields = (customerLedger, fallback = {}) => {
    const customerId = customerLedger?.id || fallback.customerId || "";
    const customerKey =
      customerLedger?.customerKey || fallback.customerKey || customerId || "";
    const fallbackCustomerName =
      typeof fallback.customerName === "string"
        ? fallback.customerName
        : customerName?.trim?.() || "";
    const ledgerCompanyName = String(customerLedger?.companyName || "").trim();
    const ledgerCustomerName = String(customerLedger?.customerName || "").trim();
    const resolvedCustomerName =
      ledgerCompanyName ||
      fallback.customerCompanyName ||
      fallback.companyName ||
      fallback.customerName ||
      ledgerCustomerName ||
      customerName?.trim?.() ||
      "";
    const contactPersonName =
      fallback.contactPersonName ||
      customerLedger?.contactPersonName ||
      (ledgerCompanyName &&
      ledgerCustomerName &&
      normalizeSearchText(ledgerCompanyName) !== normalizeSearchText(ledgerCustomerName)
        ? ledgerCustomerName
        : "") ||
      "";
    const contactPersonPhone =
      fallback.contactPersonPhone ||
      customerLedger?.contactPersonPhone ||
      customerLedger?.phone ||
      fallback.customerPhone ||
      fallback.phone ||
      "";
    const customerPhone =
      customerLedger?.phone ||
      fallback.customerPhone ||
      fallback.phone ||
      customerLedger?.whatsapp ||
      "";

    return {
      customerId,
      customerKey,
      customerName: resolvedCustomerName || fallbackCustomerName,
      contactPersonName,
      contactPersonPhone,
      customerPhone,
      customerWhatsapp:
        customerLedger?.whatsapp || fallback.customerWhatsapp || fallback.whatsapp || "",
      customerEmail: customerLedger?.email || fallback.customerEmail || fallback.email || "",
      customerAddress:
        customerLedger?.address || fallback.customerAddress || fallback.address || "",
      customerCompanyName:
        customerLedger?.companyName ||
        fallback.customerCompanyName ||
        fallback.companyName ||
        "",
      customerLedgerLinked: Boolean(customerId),
    };
  };

  const hasCorrectCurtainCalculationSnapshot = (snapshot = null) =>
    Boolean(
      snapshot &&
        snapshot.calculationVersion === CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION &&
        snapshot.roundingMode === "ceil" &&
        Number(snapshot.usableFabricWidthIn || 0) > 0 &&
        Number.isFinite(Number(snapshot.pieces))
    );

  const hasSavedCurtainCalculationFields = (row = {}) =>
    [
      row.calculationSnapshot?.pieces,
      row.finalPieces,
      row.totalPieces,
      row.pcsFull,
      row.pcs,
      row.orderedFabricMeters,
      row.customerBillableFabricMeters,
      row.fabricMeters,
      row.lineTotal,
      row.rowTotal,
      row.total,
    ].some((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  const hasOwnStoredField = (source = {}, fieldName = "") =>
    Boolean(
      source &&
        Object.prototype.hasOwnProperty.call(source, fieldName) &&
        source[fieldName] !== undefined
    );

  const getFirstStoredNumberField = (
    row = {},
    rowFieldNames = [],
    snapshotFieldNames = []
  ) => {
    const snapshot = row.calculationSnapshot || {};
    for (const fieldName of snapshotFieldNames) {
      if (hasOwnStoredField(snapshot, fieldName)) {
        const value = Number(snapshot[fieldName]);
        return {
          exists: true,
          value: Number.isFinite(value) ? value : null,
          source: `calculationSnapshot.${fieldName}`,
        };
      }
    }
    for (const fieldName of rowFieldNames) {
      if (hasOwnStoredField(row, fieldName)) {
        const value = Number(row[fieldName]);
        return {
          exists: true,
          value: Number.isFinite(value) ? value : null,
          source: fieldName,
        };
      }
    }
    return { exists: false, value: null, source: "" };
  };

  const createLegacyCurtainCalculationSnapshot = (row = {}) => {
    const existingSnapshot = row.calculationSnapshot || {};
    const storedPieces = getFirstStoredNumberField(
      row,
      ["finalPieces", "totalPieces", "pcsFull", "pcs"],
      ["pieces"]
    );
    const storedOrderedMeters = getFirstStoredNumberField(
      row,
      [
        "orderedFabricMeters",
        "customerBillableFabricMeters",
        "fabricMeters",
        "fabricMetersFull",
      ],
      ["orderedFabricMeters", "customerBillableFabricMeters", "fabricMeters"]
    );
    const storedFabricAmount = getFirstStoredNumberField(
      row,
      ["fabricAmount", "fabricCost"],
      ["fabricAmount", "fabricCost"]
    );
    const storedStitchTrackAmount = getFirstStoredNumberField(
      row,
      ["stitchingTrackAmount", "stitchTrackAmount", "stitchingAndTrackAmount"],
      ["stitchingTrackAmount", "stitchTrackAmount"]
    );
    const storedLineTotal = getFirstStoredNumberField(
      row,
      ["lineTotal", "rowTotal", "total", "amount", "finalAmount"],
      ["lineTotal", "rowTotal", "total"]
    );
    const savedPieces = Number(storedPieces.value ?? 0);
    const savedOrderedMeters = Number(storedOrderedMeters.value ?? 0);
    const savedLineTotal = Number(storedLineTotal.value ?? 0);

    if (savedPieces <= 0 && savedOrderedMeters <= 0 && savedLineTotal <= 0) {
      return null;
    }

    return {
      calculationVersion:
        existingSnapshot.calculationVersion || "legacy-saved-row-values",
      productId: row.productId || row.productConfigSnapshot?.id || "",
      productName:
        row.productName || row.productType || row.productConfigSnapshot?.name || "",
      fabricWidthIn: Number(
        existingSnapshot.fabricWidthIn ?? row.fabricWidthIn ?? 54
      ),
      usableWidthDeductionIn: Number(
        existingSnapshot.usableWidthDeductionIn ?? 0
      ),
      usableFabricWidthIn: Number(
        existingSnapshot.usableFabricWidthIn ??
          row.usableFabricWidthIn ??
          row.fabricWidthIn ??
          54
      ),
      fullness: Number(existingSnapshot.fullness ?? row.fullness ?? 0),
      widthIn: Number(existingSnapshot.widthIn ?? row.width ?? 0),
      heightIn: Number(existingSnapshot.heightIn ?? row.height ?? 0),
      rawPieces: Number(existingSnapshot.rawPieces ?? savedPieces),
      pieces: savedPieces,
      roundingMode: existingSnapshot.roundingMode || "legacy",
      cutHeightIn: Number(
        existingSnapshot.cutHeightIn ??
          row.cutHeightIn ??
          row.cutCurtain ??
          row.height ??
          0
      ),
      orderedFabricMeters: savedOrderedMeters,
      customerBillableFabricMeters: Number(
        existingSnapshot.customerBillableFabricMeters ??
          row.customerBillableFabricMeters ??
          savedOrderedMeters
      ),
      fabricAmount: Number(storedFabricAmount.value ?? 0),
      fabricCost: Number(storedFabricAmount.value ?? 0),
      stitchingTrackAmount: Number(storedStitchTrackAmount.value ?? 0),
      lineTotal: savedLineTotal,
      rowTotal: savedLineTotal,
      calculatedAt: existingSnapshot.calculatedAt || row.updatedAt || "",
      legacyFormula: true,
      requiresRecalculation: true,
      syntheticLegacySnapshot: true,
      storedFieldMap: {
        pieces: storedPieces.exists,
        orderedFabric: storedOrderedMeters.exists,
        fabricAmount: storedFabricAmount.exists,
        stitchingTrackAmount: storedStitchTrackAmount.exists,
        rowTotal: storedLineTotal.exists,
      },
      storedFieldSources: {
        pieces: storedPieces.source,
        orderedFabric: storedOrderedMeters.source,
        fabricAmount: storedFabricAmount.source,
        stitchingTrackAmount: storedStitchTrackAmount.source,
        rowTotal: storedLineTotal.source,
      },
    };
  };

  const hydrateCurtainRowCalculationSnapshot = (row = {}) => {
    if (hasCorrectCurtainCalculationSnapshot(row.calculationSnapshot)) {
      return row;
    }
    if (!hasSavedCurtainCalculationFields(row)) return row;
    const productConfig = resolveProductConfig(row, productConfigs, fabricCalcSettings);
    if (!rowIsAffectedByLegacyCurtainFormulaStatic(row, productConfig)) {
      return {
        ...row,
        calculationSnapshot: row.calculationSnapshot
          ? {
              ...row.calculationSnapshot,
              requiresRecalculation: false,
              legacyFormula: false,
              legacyReviewNotApplicable: true,
            }
          : null,
        calculationSnapshotStatus: row.calculationSnapshotStatus || "not_applicable",
      };
    }
    const legacySnapshot = createLegacyCurtainCalculationSnapshot(row);
    if (!legacySnapshot) return row;
    return {
      ...row,
      calculationSnapshot: legacySnapshot,
      calculationSnapshotStatus: "legacy",
    };
  };

  const shouldUseCurtainCalculationSnapshot = (row = {}) =>
    Boolean(
      row.calculationSnapshot &&
        row.__forceCurrentCalculation !== true &&
        row.__recalculated !== true
    );

  const buildCurrentCurtainCalculationSnapshot = (row = {}, calc = {}) => {
    const productConfig = resolveProductConfig(row, productConfigs, fabricCalcSettings);
    const fabricRules =
      calc.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const fullnessResult =
      calc.calculationBreakdown?.fullness ||
      resolveCurtainFullness(row, productConfig, stitchingRates, {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides,
      });
    const pieceCalc =
      calc.calculationBreakdown?.curtainPieceCalc ||
      calculateCurtainPieces({
        widthIn: row.width,
        fabricWidthIn: fabricRules.fabricWidthIn,
        usableWidthDeductionIn: fabricRules.usableWidthDeductionIn,
        fullness: fullnessResult.fullness,
      });

    return {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      productId: row.productId || productConfig?.id || "",
      productName:
        row.productName || row.productType || productConfig?.name || "",
      fabricWidthIn: pieceCalc.fabricWidthIn,
      usableWidthDeductionIn: pieceCalc.usableWidthDeductionIn,
      usableFabricWidthIn: pieceCalc.usableFabricWidthIn,
      fullness: pieceCalc.fullness,
      effectiveFullness: Number(fullnessResult.fullness || pieceCalc.fullness || 0),
      fullnessSource: fullnessResult.source || "",
      stitchingKey: fullnessResult.stitchingStyleKey || "",
      stitchingLabel: fullnessResult.stitchingLabel || "",
      defaultFullness: Number(
        fullnessResult.stitchingFullness ||
          fullnessResult.productDefaultFullness ||
          fullnessResult.productFullness ||
          0
      ),
      quoteFullness: Number(fullnessResult.quoteFullness || 0) || null,
      widthIn: Number(row.width || 0),
      heightIn: Number(row.height || 0),
      rawSqft: Number(
        calc.sqftCalculation?.rawSqft ?? calc.rawSqft ?? 0
      ),
      billableSqft: Number(
        calc.sqftCalculation?.billableSqft ??
          calc.billableSqft ??
          calc.adjSqft ??
          0
      ),
      sqftCalculation: calc.sqftCalculation || row.sqftCalculation || null,
      rawPieces: pieceCalc.rawPieces,
      pieces: pieceCalc.pieces,
      roundingMode: "ceil",
      cutHeightIn: Number(calc.cutCurtain || calc.cutRoman || 0),
      ...(calc.repeatCuttingVersion ? { repeatCuttingVersion: calc.repeatCuttingVersion, baseCutHeightIn: Number(calc.baseCutHeightIn || 0) } : {}),
      orderedFabricMeters: Number(
        calc.orderedFabricMeters ?? calc.fabricMeters ?? 0
      ),
      fullOrderedFabricMeters: Number(
        calc.fullOrderedFabricMeters ??
          calc.noReuseOrderedFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      customerBillableFabricMeters: Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      workshopFabricRequiredMeters: Number(
        calc.workshopFabricRequiredMeters ??
          calc.workshopNewFabricMeters ??
          calc.newFabricNeededMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      workshopNewFabricMeters: Number(
        calc.workshopNewFabricMeters ??
          calc.newFabricNeededMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      cutFabricMeters: Number(calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0),
      fabricAmount: Number(calc.fabricCost || 0),
      fabricCost: Number(calc.fabricCost || 0),
      fabricGrossAmount: Number(calc.fabricGrossAmount || 0),
      fabricDiscountPercent: Number(calc.fabricDiscountPercent ?? row.discount ?? 0),
      fabricDiscountAmount: Number(calc.fabricDiscountAmount || 0),
      fabricNetAmount: Number(calc.fabricNetAmount ?? calc.fabricCost ?? 0),
      discountScope: calc.discountScope || getRowDiscountScope(row, productConfig, {
        hasFabricMeterComponent: Boolean(calc.isCurtain || calc.isRoman),
        fabricGrossAmount: calc.fabricGrossAmount || 0,
      }),
      discountBase: Number(calc.discountBase || 0),
      discountAmount: Number(calc.discountAmount || calc.fabricDiscountAmount || 0),
      baseLineAmountBeforeDiscount: Number(
        calc.baseLineAmountBeforeDiscount ?? calc.rowTotal ?? calc.lineTotal ?? 0
      ),
      sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
      stitchingTrackAmount: round2(
        Number(calc.stitchingCost || 0) +
          Number(calc.trackCost || 0) +
          Number(calc.bendAmount || 0)
      ),
      rowSubtotal: Number(calc.rowTotal ?? calc.lineTotal ?? 0),
      rowDiscount: 0,
      rowGst: Number(calc.gstAmount || 0),
      rowTotal: Number(calc.rowTotal ?? calc.lineTotal ?? 0),
      lineTotal: Number(calc.lineTotal ?? calc.rowTotal ?? 0),
      lining: normalizeLiningForSave(
        {
          ...row,
          lining: calc.lining || row.lining,
        },
        productConfig,
        calc
      ),
      liningAmount: Number(calc.liningAmount ?? calc.lining?.amount ?? 0),
      bendAmount: Number(calc.bendAmount ?? calc.trackConfig?.bendAmount ?? 0),
      trackConfig: calc.trackConfig || row.trackConfig || null,
      repeatSettings: normalizeCurtainRepeatEntry(
        calc.repeatSettings ||
          repeatSettings?.[
            normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
          ] ||
          row.repeatSettings ||
          {}
      ),
      repeatSignature: getCurtainRepeatSignature(
        calc.repeatSettings ||
          repeatSettings?.[
            normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
          ] ||
          row.repeatSettings ||
          {}
      ),
      calculationInputs: {
        fabricWidth: pieceCalc.fabricWidthIn,
        usableWidthDeduction: pieceCalc.usableWidthDeductionIn,
        usableFabricWidth: pieceCalc.usableFabricWidthIn,
        fullness: pieceCalc.fullness,
        effectiveFullness: Number(fullnessResult.fullness || pieceCalc.fullness || 0),
        fullnessSource: fullnessResult.source || "",
        stitchingKey: fullnessResult.stitchingStyleKey || "",
        stitchingLabel: fullnessResult.stitchingLabel || "",
        defaultFullness: Number(
          fullnessResult.stitchingFullness ||
            fullnessResult.productDefaultFullness ||
            fullnessResult.productFullness ||
            0
        ),
        quoteFullness: Number(fullnessResult.quoteFullness || 0) || null,
        repeatSettings: normalizeCurtainRepeatEntry(
          calc.repeatSettings ||
            repeatSettings?.[
              normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
            ] ||
            row.repeatSettings ||
            {}
        ),
        rate: num(row.mrp || row.mrpPerMeter || row.rate || 0),
        stitchingRate: Number(row.stitchingRate || row.stitchingCost || 0),
        trackLength: Number(row.trackLengthFt || row.trackLength || 0),
        gstRate: resolveCurtainSavedRowGstRate(row),
      },
      calculatedAt: new Date().toISOString(),
    };
  };

  const resolveCurtainRowSnapshotForSave = (row, calc) => {
    if (!calc.usesCalculationSnapshot || !row.calculationSnapshot) return buildCurrentCurtainCalculationSnapshot(row, calc);
    const saved = row.calculationSnapshot;
    return curtainTotalsSettingsEditVersion > 0 && !saved.legacyFormula && saved.calculationVersion === CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION
      ? { ...saved, rowGst: Number(calc.gstAmount || 0) } : saved;
  };
  const buildCurtainQuoteCalculationSnapshot = (options = {}) => {
    const snapshotRows = Array.isArray(options.rows) ? options.rows : rows;
    const snapshotTotals = options.summaryTotals || summaryTotals;
    const snapshotGetRowCalc =
      typeof options.getRowCalc === "function"
        ? options.getRowCalc
        : getCurtainProductionAdjustedRowCalc;
    const calculatedAt = new Date().toISOString();
    const currentUser = auth.currentUser;
    const totalsSnapshot = {
      subtotal: Number(snapshotTotals?.subTotal || 0),
      discount: Number(snapshotTotals?.discountAmount || 0),
      discountApplication: snapshotTotals?.discountApplication || "after_gst",
      taxableAmount: Number(snapshotTotals?.taxableAmount || 0),
      gstAmount: Number(snapshotTotals?.totalGstAmount ?? snapshotTotals?.gstAmount ?? 0),
      quoteGstMode: snapshotTotals?.quoteGstMode || CURTAIN_GST_MODE_FLAT,
      gstMode: snapshotTotals?.gstMode || snapshotTotals?.quoteGstMode || CURTAIN_GST_MODE_FLAT,
      gstModeLabel:
        snapshotTotals?.gstModeLabel ||
        getCurtainGstModeLabel(
          snapshotTotals?.quoteGstMode || CURTAIN_GST_MODE_FLAT,
          resolveCurtainQuoteGstRate({
            ...snapshotTotals,
            quoteSettings: {
              quoteGstRate: snapshotTotals?.quoteGstRate,
              gstRate: snapshotTotals?.gstRate,
            },
          })
        ),
      quoteGstRate: resolveCurtainQuoteGstRate({
        ...snapshotTotals,
        quoteSettings: {
          quoteGstRate: snapshotTotals?.quoteGstRate,
          gstRate: snapshotTotals?.gstRate,
        },
      }),
      fabricTaxableAmount: Number(snapshotTotals?.fabricTaxableAmount || 0),
      otherTaxableAmount: Number(snapshotTotals?.otherTaxableAmount || 0),
      fabricGstAmount: Number(snapshotTotals?.fabricGstAmount || 0),
      otherGstAmount: Number(snapshotTotals?.otherGstAmount || 0),
      gstBreakdown: snapshotTotals?.gstBreakdown || {},
      totalAfterGst: Number(snapshotTotals?.totalAfterGst || 0),
      grandTotal: Number(snapshotTotals?.grandTotal || 0),
      totalInWords: amountToWords(snapshotTotals?.grandTotal || 0, currency),
    };
    const rowSnapshots = snapshotRows.map((row, index) => {
      const calc = snapshotGetRowCalc(row, index);
      const rowSnapshot =
        resolveCurtainRowSnapshotForSave(row, calc);

      return {
        rowId: getWorkshopRowStableId(row) || `row-${index}`,
        itemName: row.item || row.name || row.room || `Row ${index + 1}`,
        product: row.productName || row.productType || row.product || "",
        fabricCode: getCurtainRowFabricLabel(row),
        width: row.width ?? "",
        height: row.height ?? "",
        unit: getRowMeasurementUnit(row, unit),
        pieces: Number(rowSnapshot?.pieces ?? calc.totalPieces ?? calc.pcsFull ?? 0),
        orderedFabric: Number(
          rowSnapshot?.orderedFabricMeters ??
            calc.orderedFabricMeters ??
            calc.fabricMeters ??
            0
        ),
        fabricAmount: Number(rowSnapshot?.fabricAmount ?? calc.fabricCost ?? 0),
        stitchingTrackAmount: Number(
          rowSnapshot?.stitchingTrackAmount ??
            round2(
              Number(calc.stitchingCost || 0) +
                Number(calc.trackCost || 0) +
                Number(calc.bendAmount || 0)
            )
        ),
        liningAmount: Number(rowSnapshot?.liningAmount ?? calc.liningAmount ?? 0),
        accessoryAmount: isCurtainAccessoryRowStatic(row)
          ? Number(calc.rowTotal || 0)
          : 0,
        rowSubtotal: Number(rowSnapshot?.rowSubtotal ?? calc.rowTotal ?? 0),
        rowDiscount: Number(rowSnapshot?.rowDiscount || 0),
        rowGst: Number(rowSnapshot?.rowGst ?? calc.gstAmount ?? 0),
        rowTotal: Number(rowSnapshot?.rowTotal ?? calc.rowTotal ?? calc.lineTotal ?? 0),
        calculationInputs: rowSnapshot?.calculationInputs || {
          fabricWidth: Number(rowSnapshot?.fabricWidthIn ?? row.fabricWidthIn ?? 0),
          usableWidthDeduction: Number(rowSnapshot?.usableWidthDeductionIn ?? 0),
          fullness: Number(rowSnapshot?.fullness ?? row.fullness ?? 0),
          rate: num(row.mrp || row.mrpPerMeter || row.rate || 0),
          stitchingRate: Number(row.stitchingRate || row.stitchingCost || 0),
          trackLength: Number(row.trackLengthFt || row.trackLength || 0),
          gstRate: resolveCurtainSavedRowGstRate(row),
        },
      };
    });

    return {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      createdAt: calculatedAt,
      createdBy: currentUser?.uid || "",
      createdByEmail: currentUser?.email || "",
      quoteGstMode: normalizeCurtainGstMode(
        snapshotTotals?.quoteGstMode ?? curtainGstMode
      ),
      quoteGstRate: normalizeCurtainGstRate(
        snapshotTotals?.quoteGstRate ?? snapshotTotals?.gstRate ?? curtainGstRate
      ),
      discountMode: summaryDiscountMode,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: Number(summaryDiscountValue || 0),
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(quoteFullnessOverrides),
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      totals: totalsSnapshot,
      rows: rowSnapshots,
      defaultCurtainTopHem:
        fabricCalcSettings?.curtain_top_hem ??
        fabricCalcSettings?.curtainTopHem ??
        null,
      defaultCurtainBottomHem:
        fabricCalcSettings?.curtain_bottom_hem ??
        fabricCalcSettings?.curtainBottomHem ??
        null,
      defaultUsableWidthDeduction:
        fabricCalcSettings?.usable_width_deduction ??
        fabricCalcSettings?.usableWidthDeduction ??
        null,
      defaultPanelRoundingMode: "ceil",
      createdWithAppVersion: "fabriquote-web",
      calculatedAt,
      lastCalculatedAt: calculatedAt,
      lastCalculatedBy: currentUser?.uid || "",
    };
  };

  const getCurtainSnapshotCalc = (row = {}, currentRepeatSettings = {}) => {
    if (!shouldUseCurtainCalculationSnapshot(row)) return null;
    const snapshot = row.calculationSnapshot || {};
    const snapshotRepeatSettings =
      snapshot.repeatSettings ||
      snapshot.calculationInputs?.repeatSettings ||
      row.repeatSettings ||
      {};
    if (
      getCurtainRepeatSignature(snapshotRepeatSettings) !==
      getCurtainRepeatSignature(currentRepeatSettings)
    ) {
      return null;
    }
    const normalizedProduct = normalizeProductName(
      row.productName || row.productType || row.product || snapshot.productName || ""
    );
    const isCurtainRow = normalizedProduct === "curtains";
    const isRomanRow = normalizedProduct.startsWith("roman");
    if (!isCurtainRow && !isRomanRow) return null;

    const pieces = Math.max(0, Number(snapshot.pieces || 0));
    const orderedFabricMeters = round2(
      Number(
        snapshot.orderedFabricMeters ??
          snapshot.customerBillableFabricMeters ??
          row.orderedFabricMeters ??
          row.fabricMeters ??
          0
      )
    );
    const customerBillableFabricMeters = round2(
      Number(snapshot.customerBillableFabricMeters ?? orderedFabricMeters)
    );
    const cutHeightIn = Number(
      snapshot.cutHeightIn || row.cutHeightIn || row.cutCurtain || row.height || 0
    );
    const lineTotal = round2(
      Number(snapshot.lineTotal ?? row.lineTotal ?? row.rowTotal ?? row.total ?? 0)
    );

    const selectedStitchingTrack = resolveQuoteStitchingConfig(row, findCurtainRowStitchConfig(row, stitchingRates), quoteConstructionSettings).config;
    const trackConfig = calculateCurtainTrackConfig(row, selectedStitchingTrack, {
      baseTrackLengthFt: Number(row.trackLengthFt || row.trackLength || selectedStitchingTrack.trackLengthFt || 0),
      measurementUnit: getRowMeasurementUnit(row, unit),
    });

    return {
      pcs: pieces,
      pcsFull: pieces,
      pcsHalf: pieces,
      fabricMeters: customerBillableFabricMeters || orderedFabricMeters,
      fabricMetersFull: orderedFabricMeters,
      fabricMetersHalf: customerBillableFabricMeters || orderedFabricMeters,
      totalPieces: pieces,
      cutCurtain: isCurtainRow ? cutHeightIn : 0,
      cutRoman: isRomanRow ? cutHeightIn : 0,
      rawSqft: Number(
        snapshot.sqftCalculation?.rawSqft ??
          snapshot.rawSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      adjSqft: round2(
        snapshot.sqftCalculation?.billableSqft ??
          snapshot.billableSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      billableSqft: round2(
        snapshot.sqftCalculation?.billableSqft ??
          snapshot.billableSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      sqftCalculation: snapshot.sqftCalculation || row.sqftCalculation || null,
      rowTotal: lineTotal,
      lineTotal,
      taxableAmount: lineTotal,
      gstRate: row.gstRate === "" || row.gstRate == null ? curtainGstRate : Number(row.gstRate),
      gstAmount: Number(snapshot.rowGst || 0),
      trackCost: Number(row.trackCost || 0),
      trackConfig,
      bendAmount: Number(row.bendAmount || trackConfig.bendAmount || 0),
      stitchingCost: Number(row.stitchingCost || 0),
      fabricCost: Number(row.fabricCost || 0),
      disableStitching: !isCurtainRow,
      isCurtain: isCurtainRow,
      isRoman: isRomanRow,
      isBlind: false,
      repeatSettings: normalizeCurtainRepeatEntry(snapshotRepeatSettings),
      calculationSnapshot: snapshot,
      repeatCuttingVersion: snapshot.repeatCuttingVersion || '',
      baseCutHeightIn: Number(snapshot.baseCutHeightIn || 0),
      usesCalculationSnapshot: true,
      requiresRecalculation: snapshot.requiresRecalculation === true,
      legacyFormula: snapshot.legacyFormula === true,
    };
  };

/* --------------------------- CALC ROW (FINAL FIXED VERSION) --------------------------- */

/* --------------------------- CALC ROW (FINAL HR + VR SAFE) --------------------------- */
const calcRow = useCallback((r, repeatSettings, options = {}) => {
  const construction = options.quoteConstructionSettings || quoteConstructionSettings;
  const effectiveStitch = resolveQuoteStitchingConfig(r, resolveSelectedStitchingStyle(r, stitchingRates), construction);
  const activeStitchingRates = { ...stitchingRates, [effectiveStitch.key]: effectiveStitch.config };
  const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
    options || {},
    "quoteFullnessOverrides"
  )
    ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
    : quoteFullnessOverrides;
  const resolvedProductConfig = resolveProductConfig(
    r,
    productConfigs,
    { ...fabricCalcSettings, minSqft }
  );
  const repeat = resolveProductRepeatSettings(
    r,
    { repeatSettings, fabricKeyForRepeat: getCurtainRowFabricKey(r) },
    resolvedProductConfig
  );
  const hrEnabled =
    repeat.horizontalEnabled === true ||
    repeat.horizontalEnabled === "true";
  const hrValue = Number(repeat.horizontalRepeatInch) || 0;
  const vrEnabled =
    repeat.verticalEnabled === true ||
    repeat.verticalEnabled === "true";
  const vrValue = Number(repeat.verticalRepeatInch) || 0;

  const snapshotCalc = getCurtainSnapshotCalc(r, repeat);
  if (snapshotCalc) return snapshotCalc;

  if (
    resolvedProductConfig &&
    !shouldUseLegacyProductCalculation(r, resolvedProductConfig)
  ) {
    const productCalc = calculateProductRow(r, resolvedProductConfig, {
      stitchingRates: activeStitchingRates,
      ...construction,
      fabricCalcSettings,
      minSqft,
      calculationSettings: selectedQuote?.calculationSettings || null,
      preserveLegacyBlindSqftRounding: Boolean(editingQuoteId || selectedQuote?.id),
      repeatSettings,
      fabricKeyForRepeat: getCurtainRowFabricKey(r),
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      gstRate: curtainGstRate,
    });
    const selectedStitchingStyle = effectiveStitch;
    if (IS_DEV_UI_ENABLED && productCalc?.isRoman) {
      devLog("ROMAN_BLIND_PANEL_CALC_TRACE", {
        rowId: r.id || r.rowId || "",
        itemName: r.item || r.name || "",
        productName: r.productType || r.productName || r.product || resolvedProductConfig?.name || "",
        widthIn: productCalc.widthIn,
        adjustedWidthIn:
          productCalc.romanPanelCalc?.adjustedWidthIn || productCalc.widthIn,
        usableFabricWidthIn:
          productCalc.romanPanelCalc?.usableFabricWidthIn ||
          productCalc.calculationBreakdown?.curtainPieceCalc?.usableFabricWidthIn ||
          0,
        quantity: productCalc.romanPanelCalc?.quantity || productCalc.quantity || r.qty || r.quantity || 1,
        panelCountPerBlind: productCalc.panelCountPerBlind,
        panelWidthsIn: productCalc.panelWidthsIn || [],
        totalPanels: productCalc.totalPieces,
        cutHeightIn: productCalc.cutRoman || productCalc.cutHeightIn || 0,
        fabricMeters: productCalc.fabricMeters,
        repeatApplied: Boolean(
          productCalc.repeatSettings?.horizontalEnabled ||
            productCalc.repeatSettings?.verticalEnabled
        ),
        halfPieceReuseAllowed: productCalc.halfPieceReuseAllowed === true,
      });
    }
    return applyBendedTrackToCalcResult(r, productCalc, selectedStitchingStyle?.config || {}, unit);
  }

  if (isCurtainAccessoryRowStatic(r)) {
    return calculateProductRow(
      r,
      normalizeProductConfig({
        id: r.productId || r.productName || r.productType || r.product || "accessory-row",
        name: r.productName || r.productType || r.product || "Accessory",
        category: r.category || "accessory",
        calculationType: r.calculationType || "quantity_rate",
        quantityRules: r.quantityRules || {},
        runningFeetRules: r.runningFeetRules || {},
        workshopRules: {
          includeInWorkshopPdf: r.includeInWorkshopPdf !== false,
          includeFabricSummary: false,
          includeRollOptimizer: false,
          ...(r.workshopRules || {}),
        },
        displayFields: r.displayFields || { showFabric: false, showRate: true },
        pricingFields: r.pricingFields || { rateAffectsPrice: true },
      }),
      {
        stitchingRates,
        fabricCalcSettings,
        repeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        gstRate: curtainGstRate,
      }
    );
  }

  const w = r.width ? Number(r.width) : 0;
  const h = r.height ? Number(r.height) : 0;
  const fabricW = r.fabricWidthIn ? Number(r.fabricWidthIn) : 54;

  const mrp = num(r.mrp);
  const disc = num(r.discount);
  const rateSqft = num(r.sqftRate);

  /* --------------------------- PRODUCT TYPES --------------------------- */

  const isCurtain = r.productType === "Curtains";
  const isRoman = r.productType?.startsWith("Roman");
  const isBlind =
    !isCurtain &&
    !isRoman &&
    r.productType?.toLowerCase().includes("blind");
  const measurementQuantity = isRoman
    ? Math.max(1, num(r.qty ?? r.quantity) || 1)
    : 1;

  const disableStitching = isBlind;
  const resolvedFabricRules = resolveFabricRulesForRow(
    r,
    null,
    { ...fabricCalcSettings, quoteConstructionSettings: construction }
  );
  const fabricRulesForCalculation = applyHorizontalRepeatToFabricRules(
    resolvedFabricRules,
    repeat
  );

  /* --------------------------- SQFT CALC --------------------------- */

  const rawSqft = (w * h) / 144;
  const min = resolveProductMinSqft(r, resolvedProductConfig, { ...fabricCalcSettings, minSqft });
  const storedBlindSqftMode =
    r.sqftCalculation?.roundingMode ||
    r.calculationSnapshot?.sqftCalculation?.roundingMode ||
    selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
    selectedQuote?.settingsSnapshot?.fabricCalcSettings?.blindSqftRoundingMode ||
    selectedQuote?.fabricCalcSettings?.blindSqftRoundingMode;
  const hasStoredBlindSqftValues =
    Number(r.billableSqft ?? r.adjSqft ?? 0) > 0 ||
    Number(r.sqftAmount ?? 0) > 0 ||
    Number(r.rowTotal ?? r.total ?? 0) > 0;
  const useLegacyBlindSqftRounding =
    (isRoman || isBlind) &&
    !storedBlindSqftMode &&
    Boolean(editingQuoteId || selectedQuote?.id) &&
    hasStoredBlindSqftValues;
  const blindSqftRoundingMode = normalizeBlindSqftRoundingMode(
    storedBlindSqftMode ||
      (useLegacyBlindSqftRounding
        ? "ceil"
        : fabricCalcSettings?.blindSqftRoundingMode),
    "exact"
  );
  const blindSqftCalculation =
    isRoman || isBlind
      ? calculateBlindSqft({
          widthIn: w,
          heightIn: h,
          roundingMode: blindSqftRoundingMode,
        })
      : null;
  const adjSqft =
    isRoman || isBlind
      ? Math.max(Number(blindSqftCalculation?.billableSqft || 0), min)
      : round2(rawSqft);
  const sqftCalculation =
    isRoman || isBlind
      ? {
          rawSqft: Number(blindSqftCalculation?.rawSqft || 0),
          billableSqft: round2(adjSqft),
          roundingMode: blindSqftRoundingMode,
          minimumSqft: min,
          minimumApplied:
            min > 0 && Number(blindSqftCalculation?.billableSqft || 0) < min,
          legacyRoundingFallback: useLegacyBlindSqftRounding,
        }
      : null;

  /* --------------------------- PCS LOGIC --------------------------- */

  let pcsFull = 0;
  let pcsHalf = 0;
  let curtainPieceCalc = null;
  let romanPanelCalc = null;
  let romanPanelCountPerBlind = 0;
  let romanPanelWidthsIn = [];
  const selectedStitchingStyle = effectiveStitch;
  const stitchCfg = selectedStitchingStyle?.config || {};
  const fullnessResult = resolveCurtainFullness(r, null, activeStitchingRates, {
    fabricCalcSettings,
    fabricRules: fabricRulesForCalculation,
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });

  if (isCurtain) {

    const fullness = Number(fullnessResult.fullness || 1);
    const pieceCalc = calculateCurtainPieces({
      widthIn: w,
      fabricWidthIn: fabricW,
      usableWidthDeductionIn: Number(fabricRulesForCalculation.usableWidthDeductionIn || 0),
      fullness,
    });
    curtainPieceCalc = pieceCalc;

  /* ---------------- PATTERN FABRICS ---------------- */

if (hrEnabled || vrEnabled) {

if (IS_DEV_UI_ENABLED) {
  devLog("repeat-aware curtain panels use canonical usable-width ceil pieces", {
    item: r.item || r.name || "",
    hrEnabled,
    hrValue,
    vrEnabled,
    vrValue,
  });
}

  pcsHalf = pieceCalc.pieces;
  pcsFull = pieceCalc.pieces;
}

    /* ---------------- PLAIN FABRICS ---------------- */

    else {

      pcsHalf = pieceCalc.pieces;
      pcsFull = pieceCalc.pieces;

    }

    if (IS_DEV_UI_ENABLED) {
      devLog("CURTAIN_PIECE_CALC_TRACE", {
        item: r.item || r.name || "",
        product: r.productType || r.product || "",
        widthIn: w,
        fullness,
        fabricWidthIn: pieceCalc.fabricWidthIn,
        usableWidthDeductionIn: pieceCalc.usableWidthDeductionIn,
        usableFabricWidthIn: pieceCalc.usableFabricWidthIn,
        rawPieces: pieceCalc.rawPieces,
        pieces: pieceCalc.pieces,
        roundingMode: "ceil",
        calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      });
    }

  }

  /* ---------------- ROMAN BLINDS ---------------- */

  else if (isRoman) {

    romanPanelCalc = {
      ...calculateRomanBlindPanels({
        finishedWidthIn: w,
        usableFabricWidthIn: fabricRulesForCalculation.usableFabricWidthIn,
        fabricWidthIn: fabricW,
        usableWidthDeductionIn: Number(fabricRulesForCalculation.usableWidthDeductionIn || 0),
        sideAllowanceIn: 0,
        joinMode: "balanced_equal_panels",
      }),
      quantity: measurementQuantity,
    };
    romanPanelCountPerBlind = Number(romanPanelCalc.panelCount || 0);
    romanPanelWidthsIn = romanPanelCalc.panelWidthsIn || [];
    const totalRomanPanels = romanPanelCountPerBlind * measurementQuantity;
    romanPanelCalc.totalPanels = totalRomanPanels;
    curtainPieceCalc = {
      ...romanPanelCalc,
      pieces: totalRomanPanels,
      panelCountPerBlind: romanPanelCountPerBlind,
      totalPanels: totalRomanPanels,
      quantity: measurementQuantity,
    };

    pcsFull = totalRomanPanels;
    pcsHalf = totalRomanPanels;

  }

  /* ---------------- OTHER BLINDS ---------------- */

  else if (isBlind) {

    pcsFull = 1;
    pcsHalf = 1;

  }

  /* --------------------------- CUT HEIGHT --------------------------- */

  let cutCurtain =
    h +
    Number(fabricRulesForCalculation.dropAllowanceIn || 0);

  let cutRoman =
    h +
    Number(fabricRulesForCalculation.dropAllowanceIn || 0);

  /* ---------------- VERTICAL REPEAT ALIGNMENT ---------------- */

  if (vrEnabled && vrValue > 0) {

    if (isCurtain) {

      const hemTotal = Number(fabricRulesForCalculation.dropAllowanceIn || 0);

      const alignedDrop =
        repeatCutHeight(h + hemTotal, repeat);

      cutCurtain = alignedDrop;

    }

    if (isRoman) {

      const hemTotal = Number(fabricRulesForCalculation.dropAllowanceIn || 0);

      const alignedDrop =
        repeatCutHeight(h + hemTotal, repeat);

      cutRoman = alignedDrop;

    }

  }



  /* ---------------- FABRIC METERS ---------------- */

  let fabricMetersFull = 0;
  let fabricMetersHalf = 0;

  if (isCurtain) {

    fabricMetersFull =
      round2((pcsFull * cutCurtain) / 39.37);

    fabricMetersHalf =
      round2((pcsHalf * cutCurtain) / 39.37);

  }

  else if (isRoman) {

    fabricMetersFull =
      round2((pcsFull * cutRoman) / 39.37);

    fabricMetersHalf = fabricMetersFull;

  }

  /* ---------------- COST CALCULATIONS ---------------- */

  const fabricGrossAmount =
    (isCurtain || isRoman) && mrp > 0
      ? round2(fabricMetersFull * mrp)
      : 0;
  const fabricDiscountPercent = Math.min(100, Math.max(0, disc));
  let fabricDiscountAmount = 0;
  let fabricCost = fabricGrossAmount;

  const baseStitchRate =
    num(stitchCfg.rate) || 0;

  const customStitchRate =
    num(r.customStitchRate) || 0;

  const stitchRate =
    isCurtain && r.stitchingStyle === "Eyelet (Custom)"
      ? customStitchRate
      : baseStitchRate;

  const defaultTrackLengthFt =
    num(stitchCfg.trackLengthFt) || 0;

  const rowTrackLengthFt =
    num(r.trackLengthFt ?? r.trackLength) || 0;

  const resolvedTrackLengthFt =
    rowTrackLengthFt > 0 ? rowTrackLengthFt : defaultTrackLengthFt;
  const trackConfig = calculateCurtainTrackConfig(r, stitchCfg, {
    baseTrackLengthFt: resolvedTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(r, unit),
  });
  const effectiveTrackLengthFt =
    Number(trackConfig.totalTrackLengthFt || 0) > 0
      ? Number(trackConfig.totalTrackLengthFt || 0)
      : resolvedTrackLengthFt;
  const bendAmount = round2(Number(trackConfig.bendAmount || 0));

  const stitchingCost =
    isCurtain && !disableStitching
      ? (w / 12) * stitchRate
      : 0;

  /* ---------------- TRACK COST ---------------- */

  let trackCost = 0;

  if (isCurtain && (stitchCfg.includesTrack !== false || stitchCfg.supportsBendedTrack === true) && r.includeTrack !== false) {

    const trackRateFt =
      num(stitchCfg.trackRateFt) || 0;

    trackCost =
      trackRateFt * effectiveTrackLengthFt;

  }

  if (IS_DEV_UI_ENABLED && isCurtain) {
    devLog("STITCHING_RATE_TRACE", {
      styleName:
        selectedStitchingStyle?.key ||
        r.stitchingStyle ||
        r.stitchingStyleId ||
        "",
      ratePerRunningFt: stitchRate,
      fullness: Number(fullnessResult.fullness || 0),
      defaultTrackLengthFt,
      rowWidth: w,
      resolvedTrackLengthFt,
      stitchingCharge: round2(stitchingCost),
    });
  }

  if (IS_DEV_UI_ENABLED && isCurtain) {
    devLog("FULLNESS_TRACE", {
      item: r.item || r.name || "",
      product: r.productType || r.product || "",
      selectedStitch:
        r.stitchingStyle ||
        r.stitchingStyleId ||
        r.stitchLabel ||
        r.stitch ||
        "",
      productFullness: fullnessResult.productFullness,
      stitchingFullness: fullnessResult.stitchingFullness,
      resolvedFullness: fullnessResult.fullness,
      source: fullnessResult.source,
      stitchingStyleKey: fullnessResult.stitchingStyleKey,
      panels: pcsFull,
      totalPieces: pcsHalf,
      fabricMeters: fabricMetersFull,
    });
  }

  if (IS_DEV_UI_ENABLED && isRoman) {
    devLog("ROMAN_BLIND_PANEL_CALC_TRACE", {
      rowId: r.id || r.rowId || "",
      itemName: r.item || r.name || "",
      productName: r.productType || r.product || "",
      widthIn: w,
      adjustedWidthIn: Number(romanPanelCalc?.adjustedWidthIn || w),
      usableFabricWidthIn: Number(romanPanelCalc?.usableFabricWidthIn || 0),
      quantity: Number(romanPanelCalc?.quantity || r.qty || r.quantity || 1),
      panelCountPerBlind: romanPanelCountPerBlind,
      panelWidthsIn: romanPanelWidthsIn,
      totalPanels: pcsFull,
      cutHeightIn: cutRoman,
      fabricMeters: fabricMetersFull,
      repeatApplied: Boolean(hrEnabled || vrEnabled),
      halfPieceReuseAllowed: false,
    });
  }

  /* ---------------- BLIND COST ---------------- */

  let blindCost = 0;

  if (isBlind && rateSqft > 0) {

    blindCost =
      adjSqft * rateSqft;

  }

  if (isRoman && rateSqft > 0) {

    blindCost =
      adjSqft * measurementQuantity * rateSqft;

  }

  const baseLineAmountBeforeDiscount = round2(
    fabricGrossAmount +
      stitchingCost +
      trackCost +
      bendAmount +
      blindCost
  );
  const discountScope = getRowDiscountScope(
    r,
    resolvedProductConfig || {
      category: isCurtain ? "curtain" : isRoman ? "roman_blind" : "blind",
      calculationType: isCurtain || isRoman ? "fabric_meter" : "sqft_only",
      usesFabric: isCurtain || isRoman,
    },
    {
      hasFabricMeterComponent: isCurtain || isRoman,
      fabricGrossAmount,
    }
  );
  const discountBase =
    discountScope === "fabric_only"
      ? fabricGrossAmount
      : discountScope === "line_total"
      ? baseLineAmountBeforeDiscount
      : 0;
  const discountAmount = round2(discountBase * (fabricDiscountPercent / 100));
  fabricDiscountAmount = discountScope === "fabric_only" ? discountAmount : 0;
  fabricCost =
    discountScope === "fabric_only"
      ? round2(Math.max(0, fabricGrossAmount - fabricDiscountAmount))
      : fabricGrossAmount;

  const rowTotal =
    discountScope === "line_total"
      ? round2(Math.max(0, baseLineAmountBeforeDiscount - discountAmount))
      : round2(
          fabricCost +
            stitchingCost +
            trackCost +
            bendAmount +
            blindCost
        );

if (isCurtain && !cutCurtain) {
  devError("cutCurtain missing", r);
}

if (isRoman && !cutRoman) {
  devError("cutRoman missing", r);
}

  return {

    pcs: pcsFull,
    pcsFull,
    pcsHalf,

    fabricMeters: fabricMetersFull,
    fabricMetersFull,
    fabricMetersHalf,
    panelMode: romanPanelCalc?.panelMode || "",
    romanPanelCalc,
    panelCountPerBlind: romanPanelCountPerBlind,
    panelWidthsIn: romanPanelWidthsIn,
    halfPieceReuseAllowed: isRoman ? false : true,
    totalPieces: pcsHalf,
// totalPieces = final panels (including half)
    cutCurtain,
    cutRoman,

    rawSqft: round2(rawSqft),
    adjSqft: round2(adjSqft),
    sqftCalculation,

    rowTotal,
    trackCost,
    trackConfig,
    bendAmount,
    stitchingCost,
    fabricCost,
    fabricGrossAmount,
    fabricDiscountPercent,
    fabricDiscountAmount,
    fabricNetAmount: fabricCost,
    baseLineAmountBeforeDiscount,
    discountScope,
    discountBase,
    discountAmount,
    sqft: round2(adjSqft),
    billableSqft: round2(adjSqft),
    totalBillableSqft: round2(adjSqft * measurementQuantity),
    sqftQuantityMultiplier: measurementQuantity,
    ratePerSqft: rateSqft,
    sqftAmount: round2(blindCost),
    stitchingTrackAmount: round2(stitchingCost + trackCost + bendAmount),
    rowSubtotalBeforeGst: rowTotal,
    curtainPieceCalc,
    repeatSettings: repeat,
    calculationBreakdown: {
      curtainPieceCalc,
      romanPanelCalc,
      fabricRules: fabricRulesForCalculation,
      baseFabricRules: resolvedFabricRules,
      fullness: fullnessResult,
      repeatSettings: repeat,
    },

    disableStitching,
    isCurtain,
    isRoman,
    isBlind

  };

}, [
  curtainGstRate,
  editingQuoteId,
  fabricCalcSettings,
  minSqft,
  productConfigs,
  quoteFullnessOverrides,
  quoteConstructionSettings,
  selectedQuote,
  stitchingRates,
]);

async function applyStockTransaction({
  uid,
  fabricKey,
  meters,
  type,
  quoteId = null,
  quoteNo = null,
  customerName = null,
}) {
  if (!uid) throw new Error("No user");

  const inventoryRef = doc(db, "fabric_inventory", fabricKey);
  const ledgerRef = doc(collection(db, "fabric_ledger"));

  await runTransaction(db, async (transaction) => {
    const invSnap = await transaction.get(inventoryRef);

    const current = invSnap.exists()
      ? Number(invSnap.data().totalMeters || 0)
      : 0;

    const next = current + meters;

    if (next < 0) {
      throw new Error("Insufficient stock");
    }

    transaction.set(
      inventoryRef,
      {
        fabricKey,
        totalMeters: next,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    transaction.set(ledgerRef, {
      fabricKey,
      meters,
      type,
      quoteId,
      quoteNo,
      customerName,
      createdAt: serverTimestamp(),
    });
  });
}
const applyGeneralQuoteState = (quote) => {
  const normalized = syncGeneralQuoteDefaults(getEmbeddedGeneralQuote(quote));
  const loadedQuoteDate = resolveQuoteDateForInput(
    {
      ...quote,
      generalQuote: normalized,
    },
    todayISO()
  );
  const generalCustomerId =
    normalized.customerId || quote.customerId || quote.crm?.customerId || "";
  const generalCustomerName =
    normalized.customerName ||
    quote.customerName ||
    normalized.quoteFor?.name ||
    normalized.quoteFor?.company ||
    "";
  const generalCustomerPhone =
    normalized.customerPhone ||
    quote.customerPhone ||
    quote.crm?.customerPhone ||
    normalized.quoteFor?.phone ||
    "";
  const generalCustomerEmail =
    normalized.customerEmail ||
    quote.customerEmail ||
    normalized.quoteFor?.email ||
    "";
  const generalCustomerAddress =
    normalized.customerAddress ||
    quote.customerAddress ||
    normalized.quoteFor?.address ||
    "";
  const generalCustomerCompanyName =
    normalized.customerCompanyName ||
    quote.customerCompanyName ||
    normalized.quoteFor?.company ||
    "";
  const generalSiteSnapshot = normalizeQuoteSiteSnapshot(
    {
      ...quote,
      ...normalized,
      generalQuote: normalized,
    },
    { customerName: generalCustomerName }
  );
  const nextGeneralQuote = {
    ...normalized,
    ...generalSiteSnapshot,
    quoteDate: loadedQuoteDate,
    date: loadedQuoteDate,
    quotationDate: loadedQuoteDate,
    customerId: generalCustomerId,
    customerName: generalCustomerName,
    customerPhone: generalCustomerPhone,
    customerWhatsapp: normalized.customerWhatsapp || quote.customerWhatsapp || "",
    customerEmail: generalCustomerEmail,
    customerAddress: generalCustomerAddress,
    customerCompanyName: generalCustomerCompanyName,
    customerLedgerLinked:
      normalized.customerLedgerLinked === true ||
      quote.customerLedgerLinked === true ||
      Boolean(generalCustomerId),
    quoteNo: quote.quoteNo || normalized.quoteNo || "",
    currency: normalized.currency || currency,
    quoteFor: {
      ...(normalized.quoteFor || {}),
      name: normalized.quoteFor?.name || generalCustomerName,
      company: normalized.quoteFor?.company || generalCustomerCompanyName,
      phone: normalized.quoteFor?.phone || generalCustomerPhone,
      email: normalized.quoteFor?.email || generalCustomerEmail,
      address: normalized.quoteFor?.address || generalCustomerAddress,
    },
  };
  if (isDevRuntime) {
    const incomingItemCount = Array.isArray(normalized.lineItems)
      ? normalized.lineItems.length
      : 0;
    const existingItemCount = Array.isArray(generalQuoteRef.current?.lineItems)
      ? generalQuoteRef.current.lineItems.length
      : Array.isArray(generalQuote?.lineItems)
      ? generalQuote.lineItems.length
      : 0;
    const nextItemCount = Array.isArray(nextGeneralQuote.lineItems)
      ? nextGeneralQuote.lineItems.length
      : 0;
    devLog("GENERAL_QUOTE_STATE_APPLY_TRACE", {
      phase: "apply_loaded_quote",
      quoteNo: nextGeneralQuote.quoteNo || quote.quoteNo || "",
      incomingItemCount,
      existingItemCount,
      nextItemCount,
      source: "loaded_quote",
      selectedItemSourceField:
        normalized.contentLoadField ||
        quote.contentLoadField ||
        quote.selectedLoadField ||
        "",
      indexGrandTotal: Number(quote.indexGrandTotal || 0),
      incomingGrandTotal: Number(normalized.grandTotal || 0),
      nextGrandTotal: Number(nextGeneralQuote.grandTotal || 0),
      preserveLoadedGrandTotalFallback:
        nextGeneralQuote.preserveLoadedGrandTotalFallback === true,
    });
  }
  const generalCustomer =
    nextGeneralQuote.quoteFor?.company ||
    nextGeneralQuote.quoteFor?.name ||
    nextGeneralQuote.customerName ||
    "";
  const loadedGeneralWorkflowIdentity = getSavedQuoteWorkflowIdentity(
    { ...quote, quoteType: GENERAL_QUOTE_TYPE },
    workspaceUid
  );

  setRoutedTab("quote", GENERAL_QUOTE_TYPE);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(quote.id || null);
  setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
  setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  generalQuoteRef.current = nextGeneralQuote;
  setGeneralQuote(nextGeneralQuote);
  markGeneralQuoteBaseline(nextGeneralQuote, "saved");
  setSelectedQuote({
    ...quote,
    id: quote.id,
    quoteId: loadedGeneralWorkflowIdentity.quoteId || quote.quoteId || quote.id,
    sourceQuoteId:
      loadedGeneralWorkflowIdentity.sourceQuoteId || quote.sourceQuoteId || quote.id,
    fullQuoteId:
      loadedGeneralWorkflowIdentity.fullQuoteId || quote.fullQuoteId || quote.id,
    quoteIndexId:
      loadedGeneralWorkflowIdentity.quoteIndexId ||
      quote.quoteIndexId ||
      quote.indexId ||
      quote.id,
    indexId:
      loadedGeneralWorkflowIdentity.quoteIndexId ||
      quote.indexId ||
      quote.quoteIndexId ||
      quote.id,
    sourceCollection: quote.sourceCollection || "quotations",
    sourcePath: loadedGeneralWorkflowIdentity.sourcePath || quote.sourcePath || "",
    quoteType: GENERAL_QUOTE_TYPE,
  });
  setSelectedQuoteWorkStage(quote.workStage || "quote_given");
  setSelectedQuoteStatus(quote.status || "draft");
  setSelectedInstallationDate(quote.installationDate || null);
  generalRemovedImageIdsRef.current = {};
  generalActionRuntimeRef.current.lastHydratedAt = Date.now();
  setTab("quote");
  setIsNewQuote(false);
  setAutoSaveStatus("saved");
  setCustomerName(generalCustomer);
  setQuoteSiteSelection(generalSiteSnapshot);
};

const getGeneralQuoteImageCleanupEligibleAt = (removedAtIso) => {
  const removedMillis = safeTimeMillis(removedAtIso) || Date.now();
  return new Date(
    removedMillis + GENERAL_QUOTE_IMAGE_TTL_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();
};

const archiveGeneralQuoteImageMetadata = (
  image = {},
  {
    itemId = "",
    itemName = "",
    quoteId = "",
    quoteNo = "",
    reason = "removed_from_quote",
    removedBy = "",
  } = {}
) => {
  const removedAt = new Date().toISOString();
  const normalized = createGeneralQuoteImage(image);
  return createGeneralQuoteImage({
    ...normalized,
    removedFromQuote: true,
    removedAt,
    removedBy,
    removalReason: reason,
    cleanupEligibleAt: getGeneralQuoteImageCleanupEligibleAt(removedAt),
    cleanupRetentionDays: GENERAL_QUOTE_IMAGE_TTL_DAYS,
    cleanupStatus: "pending_retention_cleanup",
    removedFromQuoteId: quoteId,
    removedFromQuoteNo: quoteNo,
    removedFromItemId: itemId,
    removedFromItemName: itemName,
  });
};

const resolveGeneralQuoteImageDisplayUrl = async (image = {}, context = {}) => {
  const normalized = createGeneralQuoteImage(image);
  const fullPath = getGeneralQuoteImageStoragePath(normalized);
  const displayUrl = getGeneralQuoteImageDisplayUrl(normalized);
  const originalUrl = String(
    image?.downloadURL ||
      image?.downloadUrl ||
      image?.imageUrl ||
      image?.imageURL ||
      image?.url ||
      image?.previewUrl ||
      ""
  ).trim();
  const extractedFullPath = extractFirebaseStorageFullPathFromUrl(originalUrl);
  const originalIsFirebaseStorageRestUrl =
    isFirebaseStorageRestUrl(originalUrl) || isFirebaseStorageRestUrl(displayUrl);
  const storageRef = fullPath
    ? getGeneralQuoteImageStorageRef(normalized)
    : null;
  const shouldLogImageDebug = shouldLogGeneralQuoteImageDebug(context);
  const shouldResolveFromStorage =
    fullPath &&
    (!displayUrl ||
      originalIsFirebaseStorageRestUrl ||
      isProtectedFirebaseStorageRestUrl(displayUrl));
  const isExactJonyLeadsPath =
    fullPath === EXACT_JONYLEADS_IMAGE_FULL_PATH &&
    (normalized.storageBucket || firebaseApp.options.storageBucket || "") ===
      EXACT_JONYLEADS_IMAGE_BUCKET;

  if (shouldLogImageDebug) {
    devLog("JONYLEADS_IMAGE_RESOLVE_STEP", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      workspaceUid,
      authUid: auth.currentUser?.uid || "",
      hasOriginalUrl: Boolean(originalUrl),
      hasLegacyUrl: Boolean(normalized.legacyUrl),
      hasDownloadURLBefore: Boolean(normalized.downloadURL),
      extractedFullPath,
      decodedFullPath: fullPath,
      storagePathUsedForRef: storageRef?.fullPath || fullPath,
      storageBucketFromImage: normalized.storageBucket || "",
      configuredStorageBucket: firebaseApp.options.storageBucket || "",
      isFirebaseRestUrl: originalIsFirebaseStorageRestUrl,
      hasTokenInOriginalUrl: hasFirebaseStorageDownloadToken(originalUrl),
      willCallGetMetadata: Boolean(fullPath),
      willCallGetDownloadURL: Boolean(shouldResolveFromStorage),
    });
  }

  if (!shouldResolveFromStorage && displayUrl) {
    return {
      ...normalized,
      downloadURL: displayUrl,
      url: displayUrl,
      previewUrl: displayUrl,
      resolveStatus: "ready",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  }

  if (!fullPath) {
    const unavailable = {
      ...normalized,
      url: "",
      previewUrl: "",
      downloadURL: "",
      resolveStatus: "unavailable",
      resolveErrorCode: "missing-storage-path",
      resolveErrorMessage: normalized.filename
        ? "Image file path is missing."
        : "Image unavailable.",
    };
    if (isDevelopmentMode()) {
      devLog("GENERAL_QUOTE_IMAGE_RESOLVE_TRACE", {
        quoteNo: context.quoteNo || "",
        itemName: context.itemName || "",
        imageName: unavailable.name || unavailable.filename || "",
        imageKeys: Object.keys(image || {}),
        fullPath: "",
        hasDownloadURL: false,
        hasLegacyUrl: Boolean(normalized.legacyUrl),
        resolved: false,
        reason: "missing-storage-path",
      });
    }
    return unavailable;
  }

  const cacheKey = getGeneralQuoteImageCacheKey(normalized);
  const cachedUrl = generalQuoteImageUrlCacheRef.current.get(cacheKey);
  if (cachedUrl) {
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: cachedUrl,
      url: cachedUrl,
      previewUrl: cachedUrl,
      resolveStatus: "resolved",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  }

  if (shouldLogImageDebug) {
    devLog("GENERAL_QUOTE_IMAGE_METADATA_TRACE", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      workspaceUid,
      authUid: auth.currentUser?.uid || "",
      imageKeys: Object.keys(image || {}),
      image: {
        id: normalized.id,
        fullPath: normalized.fullPath,
        hasDownloadURL: Boolean(normalized.downloadURL),
      },
    });
  }

  try {
    let metadataSuccess = false;
    let metadataErrorCode = "";
    let metadataErrorMessage = "";
    try {
      const metadata = await getMetadata(storageRef);
      metadataSuccess = true;
      if (shouldLogImageDebug) {
        devLog("JONYLEADS_IMAGE_METADATA_RESULT", {
          quoteNo: context.quoteNo || "",
          itemName: context.itemName || "",
          fullPath,
          storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
          exists: true,
          contentType: metadata?.contentType || "",
          size: metadata?.size || 0,
          customMetadataKeys: Object.keys(metadata?.customMetadata || {}),
        });
      }
    } catch (metadataError) {
      metadataErrorCode = metadataError?.code || "";
      metadataErrorMessage = metadataError?.message || "";
      if (shouldLogImageDebug) {
        devError("JONYLEADS_IMAGE_METADATA_FAILED", {
          quoteNo: context.quoteNo || "",
          itemName: context.itemName || "",
          fullPath,
          storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
          errorCode: metadataError?.code,
          errorMessage: metadataError?.message,
        });
      }
    }

    const resolvedUrl = await getDownloadURL(storageRef);
    if (isExactJonyLeadsPath || shouldLogImageDebug) {
      devLog("EXACT_JONYLEADS_IMAGE_STORAGE_TEST", {
        bucket: EXACT_JONYLEADS_IMAGE_BUCKET,
        fullPath: EXACT_JONYLEADS_IMAGE_FULL_PATH,
        metadataSuccess,
        downloadUrlSuccess: true,
        errorCode: metadataErrorCode,
        errorMessage: metadataErrorMessage,
      });
    }
    generalQuoteImageUrlCacheRef.current.set(cacheKey, resolvedUrl);
    if (shouldLogImageDebug) {
      devLog("GENERAL_QUOTE_IMAGE_RESOLVE_TRACE", {
        quoteNo: context.quoteNo || "",
        quoteId: context.quoteId || "",
        itemName: context.itemName || "",
        imageName: normalized.name || normalized.filename || "",
        fullPath,
        storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
        hasDownloadURL: Boolean(normalized.downloadURL),
        hasLegacyUrl: Boolean(normalized.legacyUrl),
        resolved: true,
      });
      devLog("JONYLEADS_IMAGE_RESOLVE_RESULT", {
        quoteNo: context.quoteNo || "",
        itemName: context.itemName || "",
        fullPath,
        resolvedDownloadURLPresent: Boolean(resolvedUrl),
        resolvedHasToken: resolvedUrl?.includes("token="),
      });
    }
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: resolvedUrl,
      url: resolvedUrl,
      previewUrl: resolvedUrl,
      resolveStatus: "resolved",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  } catch (error) {
    const message = getGeneralQuoteImageResolveErrorMessage(error);
    const retainedUrl = hasFirebaseStorageDownloadToken(displayUrl)
      && extractFirebaseStorageFullPathFromUrl(displayUrl) === fullPath
      && createGeneralQuoteImage({ url: displayUrl }).storageBucket === (normalized.storageBucket || firebaseApp.options.storageBucket)
      ? displayUrl : "";
    devError("GENERAL_QUOTE_IMAGE_RESOLVE_FAILED", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      fullPath,
      storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
      errorCode: error?.code,
      errorMessage: error?.message,
    });
    devError("JONYLEADS_IMAGE_GET_DOWNLOAD_URL_FAILED", {
      quoteNo: context.quoteNo || "",
      itemName: context.itemName || "",
      fullPath,
      errorCode: error?.code,
      errorName: error?.name,
      errorMessage: error?.message,
      storageBucket:
        normalized.storageBucket ||
        storage.app.options.storageBucket ||
        firebaseApp.options.storageBucket ||
        "",
    });
    if (isExactJonyLeadsPath || shouldLogImageDebug) {
      devLog("EXACT_JONYLEADS_IMAGE_STORAGE_TEST", {
        bucket: EXACT_JONYLEADS_IMAGE_BUCKET,
        fullPath: EXACT_JONYLEADS_IMAGE_FULL_PATH,
        metadataSuccess: false,
        downloadUrlSuccess: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || "",
      });
    }
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: retainedUrl,
      url: retainedUrl,
      previewUrl: retainedUrl,
      resolveStatus: retainedUrl ? "cached" : "failed",
      resolveErrorCode: error?.code || "",
      resolveErrorMessage: message,
    };
  }
};

const cleanupExpiredGeneralQuoteImages = async (quoteLike) => {
  const normalized = syncGeneralQuoteDefaults(quoteLike || {});
  let changed = false;
  let metadataChanged = false;

  const lineItems = await Promise.all(
    (normalized.lineItems || []).map(async (item) => {
      const imageContext = {
        quoteNo: normalized.quoteNo,
        quoteId: normalized.id || quoteLike?.id || selectedQuote?.id || "",
        customerName:
          normalized.customerName ||
          normalized.quoteFor?.name ||
          normalized.quoteFor?.company ||
          "",
        itemName: item.itemName || item.name || "",
      };
      if (shouldLogGeneralQuoteImageDebug(imageContext)) {
        getRawGeneralQuoteImageDebugEntries(item).forEach((entry, imageIndex) => {
          const rawImage = entry.rawImage;
          devLog("JONYLEADS_IMAGE_DEBUG_RAW", {
            quoteNo: imageContext.quoteNo,
            quoteId: imageContext.quoteId,
            itemName: imageContext.itemName,
            workspaceUid,
            authUid: auth.currentUser?.uid || "",
            imageIndex,
            source: entry.source,
            rawImagePresent: Boolean(rawImage),
            rawImageKeys:
              rawImage && typeof rawImage === "object" ? Object.keys(rawImage || {}) : [],
            rawStringPresent: typeof rawImage === "string",
          });
        });
      }
      const itemImages = normalizeGeneralQuoteItemImages(item);
      if (!itemImages.length) return syncGeneralQuoteItemPrimaryImageFields(item);

      const activeImages = [];

      for (const image of itemImages) {
        const imageStoragePath = getGeneralQuoteImageStoragePath(image);
        if (!imageStoragePath) {
          activeImages.push(
            await resolveGeneralQuoteImageDisplayUrl(image, {
              ...imageContext,
            })
          );
          continue;
        }

        let expiresAtMillis = safeTimeMillis(image.expiresAt);

        try {
          const metadata = await getMetadata(getGeneralQuoteImageStorageRef(image));
          expiresAtMillis =
            safeTimeMillis(metadata?.customMetadata?.expiresAt) ||
            expiresAtMillis;
        } catch (error) {
          if (error?.code !== "storage/object-not-found") {
            devWarn("Image metadata lookup failed:", error);
          }
        }

        const expired =
          expiresAtMillis > 0 && expiresAtMillis <= Date.now();

        if (!expired) {
          const resolvedImage = await resolveGeneralQuoteImageDisplayUrl(
            {
              ...image,
              fullPath: imageStoragePath,
              storagePath: imageStoragePath,
              path: imageStoragePath,
            },
            {
              ...imageContext,
            }
          );
          if (
            getGeneralQuoteImageDisplayUrl(resolvedImage) !==
              getGeneralQuoteImageDisplayUrl(image) ||
            getGeneralQuoteImageStoragePath(resolvedImage) !== imageStoragePath
          ) {
            metadataChanged = true;
          }
          activeImages.push(resolvedImage);
          continue;
        }

        const retainedExpiredImage = await resolveGeneralQuoteImageDisplayUrl(
          {
            ...image,
            fullPath: imageStoragePath,
            storagePath: imageStoragePath,
            path: imageStoragePath,
            cleanupEligibleAt: image.cleanupEligibleAt || image.expiresAt || "",
            cleanupStatus: image.cleanupStatus || "retention_expired_review",
          },
          {
            ...imageContext,
          }
        );
        activeImages.push(retainedExpiredImage);
        metadataChanged = true;
        if (isDevelopmentMode()) {
          devInfo("GENERAL_QUOTE_IMAGE_EXPIRY_DELETE_SKIPPED", {
            quoteNo: imageContext.quoteNo || "",
            itemName: imageContext.itemName || "",
            storagePath: imageStoragePath,
            reason:
              "Expired General Quote images are retained in Storage and reviewed later.",
          });
        }
      }

      return syncGeneralQuoteItemPrimaryImageFields({
        ...item,
        images: activeImages,
      });
    })
  );

  return {
    cleanedQuote: {
      ...normalized,
      lineItems,
    },
    changed,
    metadataChanged,
  };
};

const uploadGeneralQuoteItemImage = async (itemId, filesInput) => {
  if (!canAccessGeneralQuotes || (generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
    ? !canEditQuotes : !canCreateQuotes)) {
    notify("You do not have permission to change quotation images.", "warning");
    return [];
  }
  const files = Array.isArray(filesInput)
    ? filesInput.filter(Boolean)
    : filesInput
    ? [filesInput]
    : [];

  if (!workspaceUid || !itemId || files.length === 0) {
    devError("REAL GENERAL IMAGE UPLOAD BLOCKED: workspace/auth/item missing", {
      workspaceUid,
      uid: auth.currentUser?.uid || "",
      itemId,
      fileCount: files.length,
    });
    notify("Could not save because workspace is not ready. Please refresh and try again.", "error");
    return;
  }

  if (!auth.currentUser?.uid) {
    devError("REAL GENERAL IMAGE UPLOAD BLOCKED: auth missing", {
      workspaceUid,
      itemId,
    });
    notify("Could not save because workspace is not ready. Please refresh and try again.", "error");
    return;
  }

  traceGeneralActionHealth("imageUpload");

  const runUpload = async () => {
    const uploadedImages = [];
    setGeneralUploadingItemId(itemId);
    const savedGeneralQuoteIdForUpload =
      generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
        ? generalEditingQuoteId || selectedQuote?.id || ""
        : "";
    let uploadDebugContext = {
      workspaceUid,
      quoteId: savedGeneralQuoteIdForUpload || generalQuoteDraftKey || "draft-new",
      itemId,
      storagePath: "",
    };

    try {
      const draftId = sanitizeFirestoreDocIdSegment(
        generalQuoteDraftKey || createClientId("general-draft")
      );
      const rawQuoteIdForUpload = savedGeneralQuoteIdForUpload;
    const quoteIdOrDraftId = rawQuoteIdForUpload
      ? sanitizeFirestoreDocIdSegment(rawQuoteIdForUpload)
      : `drafts/${draftId}`;
    uploadDebugContext = {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      storagePath: "",
    };

    for (const file of files) {
      const validation = validateGeneralQuoteImageFile(file);
      if (!validation.valid) {
        notify(validation.message, "warning");
        continue;
      }

      const uploadedAt = new Date().toISOString();
      const expiresAt = new Date(Date.now() + GENERAL_IMAGE_TTL_MS).toISOString();
      const imageId = createClientId("general-image");
      const safeFileName = normalizeFileName(file.name || "item-image");
      const uploadTimestamp = Date.now();
      const storagePath = buildGeneralQuoteImageStoragePath({
        workspaceUid,
        quoteId: rawQuoteIdForUpload,
        draftId,
        itemId,
        imageId,
        fileName: safeFileName,
        timestamp: uploadTimestamp,
      });
      uploadDebugContext = {
        workspaceUid,
        quoteId: quoteIdOrDraftId,
        itemId,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        storagePath,
      };

      if (isDevelopmentMode()) {
        devLog("GENERAL QUOTE IMAGE UPLOAD START", uploadDebugContext);
      }
      const currentUploadDebugContext = { ...uploadDebugContext };
      devLog("REAL GENERAL IMAGE UPLOAD START", {
        ...currentUploadDebugContext,
        uid: auth.currentUser?.uid,
        draftId,
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        uploadState: "uploading",
        lastUploadError: null,
        lastUpload: {
          ...currentUploadDebugContext,
          state: "uploading",
          at: new Date().toISOString(),
        },
      }));

      devLog("REAL GENERAL IMAGE UPLOAD BEFORE uploadBytes", uploadDebugContext);
      const uploadedImage = await uploadGeneralQuoteImage({
        workspaceUid,
        quoteId: rawQuoteIdForUpload,
        draftId,
        itemId,
        imageId,
        file,
        timestamp: uploadTimestamp,
        metadata: {
          accountId: account?.id || accountUser?.activeAccountId || accountUser?.accountId || "",
          workspaceUid,
          quoteId: quoteIdOrDraftId,
          expiresAt,
        },
      });

      if (isDevelopmentMode()) {
        devLog("GENERAL QUOTE IMAGE UPLOAD BYTES OK", uploadDebugContext);
      }
      devLog("REAL GENERAL IMAGE UPLOAD uploadBytes SUCCESS", uploadDebugContext);

      if (isDevelopmentMode()) {
        devLog("GENERAL QUOTE IMAGE DOWNLOAD URL OK", {
          ...uploadDebugContext,
          hasDownloadUrl: Boolean(uploadedImage.downloadURL),
        });
      }
      devLog("REAL GENERAL IMAGE UPLOAD getDownloadURL SUCCESS", {
        ...uploadDebugContext,
        hasDownloadUrl: Boolean(uploadedImage.downloadURL),
      });

      uploadedImages.push(
        createGeneralQuoteImage({
          ...uploadedImage,
          expiresAt,
          uploadedAt,
        })
      );
      if (uploadedImage.uploadStatus === "uploaded") {
        notify("Image uploaded, but preview or storage tracking could not finish. Keep this image and retry its preview; do not upload it again.", "warning", 6500);
      }
      devLog(
        "GENERAL_QUOTE_NEW_UPLOAD_METADATA_SAVED",
        { imageId: uploadedImage.imageId, fullPath: uploadedImage.fullPath,
          hasDownloadURL: Boolean(uploadedImage.downloadURL) }
      );
    }

    if (!uploadedImages.length) {
      return;
    }

    devLog("REAL GENERAL IMAGE UPLOAD BEFORE LOCAL STATE UPDATE", {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      uploadedCount: uploadedImages.length,
    });

    const currentQuote = generalQuoteRef.current || generalQuote;
    const activeUploadedImages = uploadedImages.filter(
      (image) => !isGeneralQuoteImageTombstoned(image, generalRemovedImageIdsRef.current)
    );

    const nextQuote = {
      ...currentQuote,
      lineItems: (currentQuote.lineItems || []).map((item) =>
        item.id === itemId
          ? syncGeneralQuoteItemPrimaryImageFields({
              ...item,
              images: [
                ...normalizeGeneralQuoteItemImages(item),
                ...activeUploadedImages,
              ],
            })
          : item
      ),
    };
    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);

    devLog("GENERAL_IMAGE_OPERATION_TRACE", {
      operation: "upload", stage: "state_attached", workspaceUid,
      authUid: auth.currentUser?.uid || "", userRole: accountUser?.role || "unknown",
      membershipValid: null, quoteId: quoteIdOrDraftId, itemId,
      uploadCompleted: true, downloadUrlResolved: activeUploadedImages.every(image => Boolean(image.downloadURL)),
      metadataAttached: true, errorCode: "", errorMessage: "",
    });
    devLog("REAL GENERAL IMAGE UPLOAD LOCAL STATE UPDATE SUCCESS", {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      uploadedCount: activeUploadedImages.length,
    });
    traceGeneralActionHealth(
      "imageUploadCommitted",
      buildLiveGeneralQuotePayload({ action: "imageUploadCommitted" })
    );
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      uploadState: "uploaded",
      lastUploadError: null,
      lastUpload: {
        ...uploadDebugContext,
        state: "uploaded",
        uploadedCount: uploadedImages.length,
        at: new Date().toISOString(),
      },
    }));

    if (isDevelopmentMode()) {
      devLog("GENERAL QUOTE IMAGE LOCAL STATE UPDATED", {
        workspaceUid,
        quoteId: quoteIdOrDraftId,
        itemId,
        uploadedCount: uploadedImages.length,
      });
    }
      return uploadedImages;
    } catch (error) {
    // Preserve earlier successful uploads if a later file in the selection fails.
    if (uploadedImages.length) {
      const current = generalQuoteRef.current || generalQuote;
      const lineItems = (current.lineItems || []).map(item => item.id === itemId
        ? replaceGeneralQuoteItemImages(item, [...normalizeGeneralQuoteItemImages(item),
            ...uploadedImages.filter(image => !isGeneralQuoteImageTombstoned(image, generalRemovedImageIdsRef.current))]) : item);
      const next = { ...current, lineItems, items: lineItems, rows: lineItems,
        generalRows: lineItems, quoteRows: lineItems, generalItems: lineItems };
      generalQuoteRef.current = next;
      setGeneralQuote(next);
    }
    devError("REAL GENERAL IMAGE UPLOAD FAILED EXACT", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      storagePath: uploadDebugContext.storagePath,
      workspaceUid,
      quoteId: uploadDebugContext.quoteId,
      itemId,
      fileName: uploadDebugContext.fileName || files[0]?.name,
      fileType: uploadDebugContext.fileType || files[0]?.type,
      fileSize: uploadDebugContext.fileSize || files[0]?.size,
    });
    devError("GENERAL QUOTE IMAGE UPLOAD FAILED", {
      error,
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      quoteId: uploadDebugContext.quoteId,
      itemId,
      fileName: uploadDebugContext.fileName || files[0]?.name,
      fileType: uploadDebugContext.fileType || files[0]?.type,
      fileSize: uploadDebugContext.fileSize || files[0]?.size,
      storagePath: uploadDebugContext.storagePath,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      uploadState: "failed",
      lastUploadError: {
        code: error?.code || "",
        message: error?.message || String(error),
        stack: error?.stack || "",
        storagePath: uploadDebugContext.storagePath,
        at: new Date().toISOString(),
      },
      lastUpload: {
        ...(prev.lastUpload || {}),
        ...uploadDebugContext,
        state: "failed",
      },
    }));
    raiseFirebaseHealthWarning("storage", error, {
      storagePath: uploadDebugContext.storagePath,
      source: "generalQuoteImageUpload",
    });
    notify(
      isFirebaseStorageBillingError(error)
        ? "Firebase Storage billing or bucket access issue detected. Check Firebase Storage billing/bucket configuration."
        : error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : uploadedImages.length ? "Some images uploaded and were kept. A later image could not upload." : "Image upload failed. Your quotation is unchanged.",
      "error",
      isFirebaseStorageBillingError(error) ? 7000 : 3800
    );
      return uploadedImages;
    } finally {
      setGeneralUploadingItemId("");
    }
  };

  const uploadPromise = runUpload();
  generalUploadPromiseRef.current = uploadPromise;
  try {
    return await uploadPromise;
  } finally {
    if (generalUploadPromiseRef.current === uploadPromise) {
      generalUploadPromiseRef.current = null;
    }
  }
};

const removeGeneralQuoteItemImage = async (itemId, imageId) => {
  if (!canAccessGeneralQuotes || (generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
    ? !canEditQuotes : !canCreateQuotes)) {
    notify("You do not have permission to change quotation images.", "warning");
    return;
  }
  const currentQuote = generalQuoteRef.current || generalQuote;
  const targetItem = (currentQuote.lineItems || []).find((item) => item.id === itemId);
  if (!targetItem) return;

  const targetImages = normalizeGeneralQuoteItemImages(targetItem);
  const targetImage = targetImages.find((image) =>
    generalQuoteImageMatchesIdentity(image, imageId)
  );
  if (!targetImage) return;
  // Filenames are not unique: two uploads of photo.png must remain independent.
  const removedImageKeys = getGeneralQuoteImageIdentityKeys(targetImage).filter(
    (key) => key !== targetImage.filename
  );
  const targetPath = getGeneralQuoteImageStoragePath(targetImage);
  removedImageKeys.forEach((key) => {
    generalRemovedImageIdsRef.current[key] = true;
  });

  const archivedImage = archiveGeneralQuoteImageMetadata(targetImage, {
    itemId,
    itemName: targetItem.itemName || targetItem.name || "",
    quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
    quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
    reason: "user_removed_from_quote",
    removedBy: auth.currentUser?.uid || "",
  });
  const beforeImagesCount = targetImages.length;
  const nextLineItems = (currentQuote.lineItems || []).map((item) =>
    item.id === itemId
      ? replaceGeneralQuoteItemImages({
          ...item,
          removedImages: [
            ...(Array.isArray(item.removedImages) ? item.removedImages : []),
            archivedImage,
          ],
        }, normalizeGeneralQuoteItemImages(item).filter(
          (image) => !(targetPath
            ? getGeneralQuoteImageStoragePath(image) === targetPath
            : image.id === targetImage.id || image.imageId === targetImage.imageId)
        ))
      : item
  );
  const nextQuote = {
    ...currentQuote,
    lineItems: nextLineItems,
    items: nextLineItems,
    rows: nextLineItems,
    generalRows: nextLineItems,
    quoteRows: nextLineItems,
    generalItems: nextLineItems,
  };
  generalQuoteRef.current = nextQuote;
  setGeneralQuote(nextQuote);
  traceGeneralActionHealth(
    "removeImage",
    buildLiveGeneralQuotePayload({ action: "removeImage" })
  );
  // Detach only. A saved revision/another quote may still reference this object.
  // Persist removedImages on Save; do not mark the shared manifest unreferenced here.
  notify("Image removed from this draft. Save the quotation to keep this change.", "info");
  if (isDevelopmentMode()) {
    const afterImagesCount =
      normalizeGeneralQuoteItemImages(
        (nextQuote.lineItems || []).find((item) => item.id === itemId) || {}
      ).length;
    devLog("GENERAL_IMAGE_REMOVE_TRACE", {
      itemId,
      imageId,
      matchedImageId: targetImage.imageId || targetImage.id || "",
      beforeImagesCount,
      afterImagesCount,
      clearedLegacyImage: afterImagesCount === 0,
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
    });
    devInfo("GENERAL_QUOTE_IMAGE_DETACHED_FROM_QUOTE", {
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      itemId,
      imageId,
      storagePath: getGeneralQuoteImageStoragePath(targetImage),
      cleanupEligibleAt: archivedImage.cleanupEligibleAt || "",
    });
  }
};

const retryGeneralQuoteItemImagePreview = async (itemId, imageId) => {
  const current = generalQuoteRef.current || generalQuote;
  const item = (current.lineItems || []).find((entry) => entry.id === itemId);
  const image = normalizeGeneralQuoteItemImages(item || {}).find((entry) => entry.id === imageId);
  if (!image) return;
  generalQuoteImageUrlCacheRef.current.delete(getGeneralQuoteImageCacheKey(image));
  const resolved = await resolveGeneralQuoteImageDisplayUrl(image, { quoteId: current.id || "" });
  const latest = generalQuoteRef.current || generalQuote;
  const lineItems = (latest.lineItems || []).map((entry) => entry.id === itemId
    ? replaceGeneralQuoteItemImages(entry,
        normalizeGeneralQuoteItemImages(entry).map((candidate) => candidate.id === imageId ? resolved : candidate)) : entry);
  const next = { ...latest, lineItems, items: lineItems, rows: lineItems,
    generalRows: lineItems, quoteRows: lineItems, generalItems: lineItems };
  generalQuoteRef.current = next;
  setGeneralQuote(next);
  notify(resolved.resolveStatus === "resolved" ? "Image preview restored." : "Preview is still unavailable. Your uploaded image reference is retained.",
    resolved.resolveStatus === "resolved" ? "success" : "warning");
};

const repairGeneralQuoteItemImage = async (
  itemId,
  imageId,
  imageIndex = -1,
  selectedCandidate = null
) => {
  if (!canAccessGeneralQuotes || !canEditQuotes) {
    notify("You do not have permission to change quotation images.", "warning");
    return;
  }
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const quoteId = generalEditingQuoteId || selectedQuote?.id || quoteState.id || "";
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId);
  const targetImages = normalizeGeneralQuoteItemImages(targetItem || {});
  const numericImageIndex = Number(imageIndex);
  const targetImage =
    Number.isFinite(numericImageIndex) && numericImageIndex >= 0
      ? targetImages[numericImageIndex]
      : targetImages.find((image) => image.id === imageId);
  const repairKey = `${itemId}:${imageId || imageIndex}`;

  if (!workspaceUid || !quoteId || !targetItem || !targetImage) {
    devError("GENERAL_QUOTE_IMAGE_REPAIR_BLOCKED", {
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
      hasTargetItem: Boolean(targetItem),
      hasTargetImage: Boolean(targetImage),
    });
    notify("Could not repair this image because the quotation context is missing.", "error");
    return;
  }

  const originalImageUrl =
    targetImage.downloadURL ||
    targetImage.url ||
    targetImage.previewUrl ||
    targetImage.legacyUrl ||
    "";
  const initialDebug = {
    quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
    quoteId,
    itemName: targetItem.itemName || targetItem.name || "",
    workspaceUid,
    itemId,
    imageId,
    imageIndex,
    originalUrl: originalImageUrl,
    extractedBucket: targetImage.storageBucket || "",
    extractedPath: getGeneralQuoteImageStoragePath(targetImage),
    extractedFullPath: getGeneralQuoteImageStoragePath(targetImage),
    exactPathExists: "checking",
    exactCheckResult: {
      status: "pending",
      fullPath: getGeneralQuoteImageStoragePath(targetImage),
    },
    prefixesSearched: ["pending: exact path check started"],
    candidateSearchResult: null,
    candidatesFound: [],
    selectedCandidate: selectedCandidate || null,
    repairResult: "checking",
    canonicalPath: "",
    finalStatus: "checking",
    debugUpdatedAt: new Date().toISOString(),
  };
  generalQuoteImageRepairDebugRef.current[repairKey] = initialDebug;
  setGeneralImageRepairState((prev) => ({
    ...prev,
    [repairKey]: {
      status: "checking",
      debug: initialDebug,
      error: null,
      updatedAt: initialDebug.debugUpdatedAt,
    },
  }));
  setGeneralRepairingImageKey(repairKey);
  try {
    const repairCallable = httpsCallable(functions, "repairGeneralQuoteImageLinks");
    const result = await repairCallable({
      workspaceUid,
      quoteId,
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      itemId,
      imageId,
      imageIndex: Number.isFinite(numericImageIndex) ? numericImageIndex : -1,
      storageBucket: firebaseApp.options.storageBucket || "",
      selectedCandidate,
    });
    const responseData = result?.data || {};
    const candidatesFound =
      responseData.candidatesFound ||
      responseData.candidateSearch?.candidates ||
      [];
    const nextDebug = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      workspaceUid,
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        responseData.originalUrl ||
        originalImageUrl,
      extractedBucket: targetImage.storageBucket || "",
      extractedPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      extractedFullPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      exactPathExists:
        typeof responseData.exactPathExists === "boolean"
          ? responseData.exactPathExists
          : (responseData.exactChecks || []).some((check) => check.fileExists),
      exactCheckResult: responseData.exactChecks || null,
      prefixesSearched:
        responseData.prefixesSearched ||
        responseData.candidateSearch?.prefixesSearched ||
        [],
      candidateSearchResult: responseData.candidateSearch || null,
      candidatesFound,
      selectedCandidate: responseData.selectedCandidate || selectedCandidate || null,
      repairResult: responseData.repairResult || responseData.status || null,
      repairResponse: responseData || null,
      canonicalPath: responseData.canonicalPath || responseData.canonicalFullPath || "",
      finalStatus: responseData.finalStatus || responseData.status || "success",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    generalQuoteImageRepairDebugRef.current[repairKey] = nextDebug;
    if (responseData.status === "multiple-candidates") {
      setGeneralImageRepairCandidates((prev) => ({
        ...prev,
        [repairKey]: {
          candidates: candidatesFound,
          quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
          quoteId,
          itemName: targetItem.itemName || targetItem.name || "",
          workspaceUid,
          originalUrl:
            targetImage.downloadURL ||
            targetImage.url ||
            targetImage.previewUrl ||
            targetImage.legacyUrl ||
            "",
          extractedPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
          prefixesSearched:
            responseData.prefixesSearched ||
            responseData.candidateSearch?.prefixesSearched ||
            [],
          at: new Date().toISOString(),
        },
      }));
      setGeneralImageRepairState((prev) => ({
        ...prev,
        [repairKey]: {
          status: "searching",
          debug: nextDebug,
          error: null,
          updatedAt: nextDebug.debugUpdatedAt,
        },
      }));
      notify(
        "Multiple possible image files found. Choose the correct file below the image.",
        "warning",
        8000
      );
      return;
    }
    if (responseData.ok === false) {
      const isMissing =
        responseData.finalStatus === "needs-user-upload" ||
        responseData.repairResult === "object-not-found" ||
        responseData.status === "not-found";
      setGeneralImageRepairCandidates((prev) => {
        const next = { ...prev };
        delete next[repairKey];
        return next;
      });
      setGeneralImageRepairState((prev) => ({
        ...prev,
        [repairKey]: {
          status: isMissing ? "missing" : "failed",
          debug: {
            ...nextDebug,
            finalStatus: responseData.finalStatus || (isMissing ? "needs-user-upload" : "failed"),
          },
          error:
            responseData.message ||
            (isMissing
              ? "Image file was not found in Firebase Storage."
              : "Could not repair this image."),
          updatedAt: nextDebug.debugUpdatedAt,
        },
      }));
      notify(
        isMissing
          ? "Image file was not found in Firebase Storage. Upload a replacement image if you have it."
          : responseData.message || "Could not repair this image.",
        isMissing ? "warning" : "error",
        8000
      );
      return;
    }
    const repairedImage = createGeneralQuoteImage(responseData.image || {});
    if (!getGeneralQuoteImageDisplayUrl(repairedImage)) {
      throw new Error("Repair completed without a usable download URL.");
    }
    const repairedContentUpdatedAt = new Date().toISOString();
    const nextQuoteBase = {
      ...quoteState,
      lineItems: (quoteState.lineItems || []).map((item) => {
        if (item.id !== itemId) return item;
        const images = normalizeGeneralQuoteItemImages(item);
        const nextImages = [...images];
        const targetIndex =
          Number.isFinite(numericImageIndex) && numericImageIndex >= 0
            ? numericImageIndex
            : images.findIndex((image) => image.id === imageId);
        nextImages[targetIndex >= 0 ? targetIndex : nextImages.length] = repairedImage;
        return replaceGeneralQuoteItemImages(item, nextImages);
      }),
    };
    const repairedItems = getGeneralActionItems(nextQuoteBase);
    const nextQuote = {
      ...nextQuoteBase,
      lineItems: repairedItems,
      items: repairedItems,
      rows: repairedItems,
      generalRows: repairedItems,
      quoteRows: repairedItems,
      generalItems: repairedItems,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      contentUpdatedAt: repairedContentUpdatedAt,
    };
    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);
    generalQuoteImageUrlCacheRef.current.set(
      getGeneralQuoteImageCacheKey(repairedImage),
      getGeneralQuoteImageDisplayUrl(repairedImage)
    );
    setGeneralImageRepairCandidates((prev) => {
      const next = { ...prev };
      delete next[repairKey];
      return next;
    });
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "repaired",
        debug: {
          ...nextDebug,
          repairResult: responseData.repairResult || "repaired",
          finalStatus: responseData.finalStatus || "repaired",
          canonicalPath: responseData.canonicalPath || responseData.canonicalFullPath || repairedImage.fullPath,
        },
        error: null,
        updatedAt: nextDebug.debugUpdatedAt,
      },
    }));
    devLog("GENERAL_QUOTE_IMAGE_REPAIR_SUCCESS", {
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
      selectedCandidate: responseData.selectedCandidate || selectedCandidate || null,
      canonicalFullPath: responseData.canonicalFullPath || repairedImage.fullPath,
      canonicalBucketName: responseData.canonicalBucketName || repairedImage.storageBucket,
      hasToken: getGeneralQuoteImageDisplayUrl(repairedImage).includes("token="),
    });
    notify("Image link repaired.", "success");
  } catch (error) {
    const errorDebug = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      workspaceUid,
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        targetImage.downloadURL ||
        targetImage.url ||
        targetImage.previewUrl ||
        targetImage.legacyUrl ||
        "",
      extractedBucket: targetImage.storageBucket || "",
      extractedPath: error?.details?.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      extractedFullPath: error?.details?.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      exactPathExists:
        typeof error?.details?.exactPathExists === "boolean"
          ? error.details.exactPathExists
          : (error?.details?.exactChecks || []).some((check) => check.fileExists),
      exactCheckResult: error?.details?.exactChecks || null,
      prefixesSearched:
        error?.details?.prefixesSearched ||
        error?.details?.candidateSearch?.prefixesSearched ||
        [],
      candidateSearchResult: error?.details?.candidateSearch || null,
      candidatesFound:
        error?.details?.candidatesFound ||
        error?.details?.candidateSearch?.candidates ||
        [],
      selectedCandidate: error?.details?.selectedCandidate || selectedCandidate || null,
      repairResult: error?.details?.repairResult || error?.details?.reason || error?.code || "failed",
      repairResponse: error?.details || null,
      canonicalPath: error?.details?.canonicalPath || error?.details?.canonicalFullPath || "",
      finalStatus: error?.details?.finalStatus || error?.details?.reason || error?.code || "failed",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    generalQuoteImageRepairDebugRef.current[repairKey] = errorDebug;
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "failed",
        debug: errorDebug,
        error: error?.message || "Could not repair this image.",
        updatedAt: errorDebug.debugUpdatedAt,
      },
    }));
    devError("GENERAL_QUOTE_IMAGE_REPAIR_FAILED", {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
    });
    notify(
      error?.details?.reason === "object-not-found"
        ? "Image file not found in Firebase Storage. Upload a replacement image if you have it."
        : error?.message || "Could not repair this image.",
      "error",
      8000
    );
  } finally {
    setGeneralRepairingImageKey("");
  }
};

const replaceGeneralQuoteItemImage = async (
  itemId,
  imageId,
  imageIndex = -1,
  file,
  previousImage = {}
) => {
  if (!canAccessGeneralQuotes || !canEditQuotes) {
    notify("You do not have permission to change quotation images.", "warning");
    return;
  }
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const quoteId = generalEditingQuoteId || selectedQuote?.id || quoteState.id || "";
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId);
  const numericImageIndex = Number(imageIndex);
  const repairKey = `${itemId}:${imageId || imageIndex}`;

  if (!workspaceUid || !auth.currentUser?.uid || !quoteId || !targetItem || !file) {
    devError("GENERAL_QUOTE_IMAGE_REPLACEMENT_BLOCKED", {
      workspaceUid,
      uid: auth.currentUser?.uid || "",
      quoteId,
      itemId,
      imageId,
      imageIndex,
      hasTargetItem: Boolean(targetItem),
      hasFile: Boolean(file),
    });
    notify("Could not upload replacement because quotation context is missing.", "error");
    return;
  }

  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    notify(validation.message, "warning");
    return;
  }

  setGeneralRepairingImageKey(repairKey);
  try {
    const imageIdForUpload = String(imageId || "").trim() || createClientId("general-image");
    const safeFileName = normalizeFileName(file.name || "replacement-image");
    const uploadTimestamp = Date.now();
    const storagePath = buildGeneralQuoteImageStoragePath({
      workspaceUid,
      quoteId,
      itemId,
      imageId: imageIdForUpload,
      fileName: safeFileName,
      timestamp: uploadTimestamp,
    });
    const uploadedAt = new Date().toISOString();
    const uploadedImage = await uploadGeneralQuoteImage({
      workspaceUid,
      quoteId,
      itemId,
      imageId: imageIdForUpload,
      file,
      timestamp: uploadTimestamp,
      metadata: {
        workspaceUid,
        quoteId,
        replacementFor: imageId || "",
        uploadedAt,
      },
    });
    const replacementImage = createGeneralQuoteImage({
      ...uploadedImage,
      id: imageIdForUpload,
      uploadedAt,
      replacedMissingImage: true,
      repairedFromMissing: true,
      legacyUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      legacyFullPath: getGeneralQuoteImageStoragePath(previousImage),
      legacyPath: getGeneralQuoteImageStoragePath(previousImage),
      legacyMissingImage: {
        oldUrl:
          previousImage?.downloadURL ||
          previousImage?.url ||
          previousImage?.previewUrl ||
          previousImage?.legacyUrl ||
          "",
        oldFullPath: getGeneralQuoteImageStoragePath(previousImage),
        oldBucket: previousImage?.storageBucket || "",
        repairAttemptedAt: new Date().toISOString(),
        reason: "object-not-found",
      },
      repairMethod: "replacement-upload",
      replacedAt: uploadedAt,
      repairedAt: uploadedAt,
      repairedBy: auth.currentUser.uid,
    });

    const repairedContentUpdatedAt = new Date().toISOString();
    const nextQuoteBase = {
      ...quoteState,
      lineItems: (quoteState.lineItems || []).map((item) => {
        if (item.id !== itemId) return item;
        const images = normalizeGeneralQuoteItemImages(item);
        const nextImages = [...images];
        const targetIndex =
          Number.isFinite(numericImageIndex) && numericImageIndex >= 0
            ? numericImageIndex
            : images.findIndex((image) => image.id === imageId);
        nextImages[targetIndex >= 0 ? targetIndex : nextImages.length] = replacementImage;
        return replaceGeneralQuoteItemImages(item, nextImages);
      }),
    };
    const repairedItems = getGeneralActionItems(nextQuoteBase);
    const repairedItemsForFirestore = sanitizeCrmFirestoreValue(repairedItems);
    const nextQuote = {
      ...nextQuoteBase,
      lineItems: repairedItems,
      items: repairedItems,
      rows: repairedItems,
      generalRows: repairedItems,
      quoteRows: repairedItems,
      generalItems: repairedItems,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      contentUpdatedAt: repairedContentUpdatedAt,
    };

    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);
    setSelectedQuote((prev) =>
      prev?.id === quoteId
        ? {
            ...prev,
            generalQuote: nextQuote,
            updatedAt: new Date(),
          }
        : prev
    );
    generalQuoteImageUrlCacheRef.current.set(
      getGeneralQuoteImageCacheKey(replacementImage),
      getGeneralQuoteImageDisplayUrl(replacementImage)
    );
    await updateDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
      ...buildGeneralQuoteLegacyAliasDeletePatch(),
      items: repairedItemsForFirestore,
      itemCount: repairedItemsForFirestore.length,
      contentItemCount: repairedItemsForFirestore.length,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      contentUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    generalQuoteImageRepairDebugRef.current[repairKey] = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      oldUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      extractedBucket: previousImage?.storageBucket || "",
      oldFullPath: getGeneralQuoteImageStoragePath(previousImage),
      extractedFullPath: getGeneralQuoteImageStoragePath(previousImage),
      exactCheckResult:
        generalQuoteImageRepairDebugRef.current[repairKey]?.exactCheckResult || null,
      candidateSearchResult:
        generalQuoteImageRepairDebugRef.current[repairKey]?.candidateSearchResult || null,
      repairResult: {
        replacementFullPath: storagePath,
        replacementBucket: firebaseApp.options.storageBucket || "",
        replacementDownloadURL: getGeneralQuoteImageDisplayUrl(replacementImage),
      },
      selectedCandidate: null,
      replacementUploaded: true,
      replacementFullPath: storagePath,
      replacementDownloadURL: getGeneralQuoteImageDisplayUrl(replacementImage),
      canonicalPath: storagePath,
      finalStatus: "replacement-uploaded",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    setGeneralImageRepairCandidates((prev) => {
      const next = { ...prev };
      delete next[repairKey];
      return next;
    });
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "repaired",
        debug: generalQuoteImageRepairDebugRef.current[repairKey],
        error: null,
        updatedAt: generalQuoteImageRepairDebugRef.current[repairKey]?.debugUpdatedAt,
      },
    }));
    notify("Replacement image uploaded.", "success");
  } catch (error) {
    devError("GENERAL_QUOTE_IMAGE_REPLACEMENT_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
    });
    notify(
      error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : "Could not upload replacement image.",
      "error",
      7000
    );
  } finally {
    setGeneralRepairingImageKey("");
  }
};

const selectGeneralQuoteImageRepairCandidate = (
  itemId,
  imageId,
  imageIndex = -1,
  candidate
) => repairGeneralQuoteItemImage(itemId, imageId, imageIndex, candidate);

const copyGeneralQuoteImageDebugJson = async (
  itemId,
  imageId,
  imageIndex = -1,
  image = {}
) => {
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId) || {};
  const repairKey = `${itemId}:${imageId || imageIndex}`;
  const stateEntry = generalImageRepairState[repairKey] || {};
  const debugEntry = stateEntry.debug || generalQuoteImageRepairDebugRef.current[repairKey] || {};
  const candidateEntry = generalImageRepairCandidates[repairKey] || {};
  const payload = {
    quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
    quoteId: generalEditingQuoteId || selectedQuote?.id || quoteState.id || "",
    itemName: targetItem.itemName || targetItem.name || "",
    workspaceUid,
    itemId,
    imageId,
    imageIndex,
    originalUrl:
      image?.downloadURL || image?.url || image?.previewUrl || image?.legacyUrl || "",
    oldUrl:
      debugEntry.oldUrl ||
      image?.legacyUrl ||
      image?.legacyMissingImage?.oldUrl ||
      image?.downloadURL ||
      image?.url ||
      image?.previewUrl ||
      "",
    extractedBucket: image?.storageBucket || "",
    oldFullPath:
      debugEntry.oldFullPath ||
      image?.legacyFullPath ||
      image?.legacyPath ||
      image?.legacyMissingImage?.oldFullPath ||
      getGeneralQuoteImageStoragePath(image),
    extractedPath: debugEntry.extractedPath || getGeneralQuoteImageStoragePath(image),
    extractedFullPath: debugEntry.extractedFullPath || getGeneralQuoteImageStoragePath(image),
    exactPathExists: debugEntry.exactPathExists ?? null,
    exactCheckResult: debugEntry.exactCheckResult || null,
    prefixesSearched: debugEntry.prefixesSearched || candidateEntry.prefixesSearched || [],
    candidateSearchResult: debugEntry.candidateSearchResult || null,
    candidatesFound: debugEntry.candidatesFound || candidateEntry.candidates || [],
    selectedCandidate: debugEntry.selectedCandidate || null,
    repairResult: debugEntry.repairResult || null,
    repairResponse: debugEntry.repairResponse || null,
    replacementUploaded:
      debugEntry.replacementUploaded === true || image?.replacedMissingImage === true,
    replacementFullPath:
      debugEntry.replacementFullPath ||
      (image?.replacedMissingImage ? getGeneralQuoteImageStoragePath(image) : ""),
    replacementDownloadURL:
      debugEntry.replacementDownloadURL ||
      (image?.replacedMissingImage ? getGeneralQuoteImageDisplayUrl(image) : ""),
    canonicalPath:
      debugEntry.canonicalPath ||
      (image?.replacedMissingImage ? getGeneralQuoteImageStoragePath(image) : ""),
    uiStatus: stateEntry.status || "",
    error: stateEntry.error || "",
    finalStatus: debugEntry.finalStatus || image?.resolveStatus || "",
    debugUpdatedAt: debugEntry.debugUpdatedAt || stateEntry.updatedAt || "",
  };

  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    notify("Image debug JSON copied.", "success");
  } catch (error) {
    devLog("IMAGE_DEBUG_JSON", payload);
    notify("Clipboard unavailable. Debug JSON was logged to console.", "info", 6000);
  }
};

const removeGeneralQuoteItem = async (itemId) => {
  const normalizedItemId = String(itemId || "").trim();
  if (!normalizedItemId) {
    if (isDevRuntime) {
      devWarn("REMOVE_GENERAL_ITEM_BLOCKED_MISSING_ID", {
        quoteId: generalEditingQuoteId || selectedQuote?.id || generalQuoteRef.current?.id || "",
        quoteNo: generalQuoteRef.current?.quoteNo || selectedQuote?.quoteNo || "",
      });
    }
    return;
  }

  const currentQuote = generalQuoteRef.current || generalQuote;
  const getItemId = (item = {}) =>
    String(item?.id || item?.itemId || item?.rowId || "").trim();
  const sourceItems =
    [
      currentQuote.lineItems,
      currentQuote.items,
      currentQuote.rows,
      currentQuote.generalRows,
      currentQuote.quoteRows,
      currentQuote.generalItems,
    ].find(
      (list) =>
        Array.isArray(list) &&
        list.some((item) => getItemId(item) === normalizedItemId)
    ) ||
    getGeneralActionItems(currentQuote) ||
    [];
  const target = sourceItems.find((item) => getItemId(item) === normalizedItemId);
  if (!target) {
    if (isDevRuntime) {
      devWarn("REMOVE_GENERAL_ITEM_BLOCKED_TARGET_NOT_FOUND", {
        itemId: normalizedItemId,
        quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
        quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
        availableIds: sourceItems.map(getItemId).filter(Boolean),
      });
    }
    return;
  }

  if (sourceItems.length <= 1) {
    return;
  }

  const nextItems = sourceItems.filter((item) => getItemId(item) !== normalizedItemId);
  if (sourceItems.length > 1 && nextItems.length === 0) {
    if (isDevRuntime) {
      devWarn("BLOCKED_UNSAFE_GENERAL_ITEM_DELETE_ALL", {
        itemId: normalizedItemId,
        beforeCount: sourceItems.length,
        afterCount: nextItems.length,
        quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
        quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      });
    }
    return;
  }

  deletedGeneralItemIdsRef.current = uniqueValues([
    ...(deletedGeneralItemIdsRef.current || []),
    normalizedItemId,
  ]);

  const targetImages = normalizeGeneralQuoteItemImages(target);
  const archivedImages = targetImages.map((image) =>
    archiveGeneralQuoteImageMetadata(image, {
      itemId: normalizedItemId,
      itemName: target.itemName || target.name || "",
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      reason: "item_removed_from_quote",
      removedBy: auth.currentUser?.uid || "",
    })
  );
  const existingRemovedImages = Array.isArray(target.removedImages)
    ? target.removedImages.map((image) =>
        createGeneralQuoteImage({
          ...image,
          removedFromQuote: true,
          removedFromItemId: image.removedFromItemId || normalizedItemId,
          removedFromItemName:
            image.removedFromItemName || target.itemName || target.name || "",
        })
      )
    : [];
  const patchGeneralQuoteAliases = (quoteLike) =>
    quoteLike && typeof quoteLike === "object"
      ? {
          ...quoteLike,
          lineItems: nextItems,
          items: nextItems,
          rows: nextItems,
          generalRows: nextItems,
          quoteRows: nextItems,
          generalItems: nextItems,
        }
      : quoteLike;

  const nextQuote = {
    ...currentQuote,
    removedImages: [
      ...(Array.isArray(currentQuote.removedImages) ? currentQuote.removedImages : []),
      ...existingRemovedImages,
      ...archivedImages,
    ],
    lineItems: nextItems,
    items: nextItems,
    rows: nextItems,
    generalRows: nextItems,
    quoteRows: nextItems,
    generalItems: nextItems,
    generalQuote: patchGeneralQuoteAliases(currentQuote.generalQuote),
    generalQuoteData: patchGeneralQuoteAliases(currentQuote.generalQuoteData),
  };
  generalQuoteRef.current = nextQuote;
  setGeneralQuote(nextQuote);
  archivedImages
    .filter((image) => image.fileId)
    .forEach((image) => {
      archiveManagedStorageFile({
        workspaceUid,
        fileId: image.fileId,
        removedBy: auth.currentUser?.uid || "",
        removalReason: "item_removed_from_quote",
        linkedRecords: [],
      }).catch((error) => {
        if (isDevelopmentMode()) {
          devWarn("GENERAL_QUOTE_STORAGE_MANIFEST_ARCHIVE_FAILED", {
            fileId: image.fileId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    });
  if (isDevelopmentMode() && archivedImages.length) {
    devInfo("GENERAL_QUOTE_ITEM_IMAGES_DETACHED_FROM_QUOTE", {
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      itemId: normalizedItemId,
      imageCount: archivedImages.length,
      retentionDays: GENERAL_QUOTE_IMAGE_TTL_DAYS,
    });
  }
};

const testGeneralQuoteFirestoreWrite = async () => {
  if (!workspaceUid || !auth.currentUser) {
    const errorMessage = "Missing workspace or signed-in user";
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "failed",
        message: errorMessage,
        at: new Date().toISOString(),
      },
    }));
    notify("Debug write failed: please sign in again.", "error");
    return;
  }

  const debugRef = doc(
    db,
    "users",
    workspaceUid,
    "quotations",
    "debug_general_quote_test"
  );
  const payload = {
    quoteType: GENERAL_QUOTE_TYPE,
    customerName: "Debug General Customer",
    generalQuote: {
      items: [
        {
          id: "debug-item-1",
          name: "Debug Sofa",
          qty: 1,
          rate: 1000,
          total: 1000,
          images: [],
        },
      ],
    },
    discountType: "flat",
    discountValue: 100,
    subtotal: 1000,
    grandTotal: 900,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  setGeneralQuoteDebugState((prev) => ({
    ...prev,
    directFirestoreTest: {
      state: "writing",
      path: debugRef.path,
      at: new Date().toISOString(),
    },
  }));

  try {
    devLog("DIRECT GENERAL FIRESTORE TEST START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      path: debugRef.path,
    });
    await setDoc(debugRef, payload, { merge: true });
    const readback = await getDoc(debugRef);
    const readBackData = readback.exists() ? readback.data() : null;
    devLog("DIRECT GENERAL FIRESTORE TEST SUCCESS", readBackData);
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "passed",
        path: debugRef.path,
        exists: readback.exists(),
        keys: readback.exists() ? Object.keys(readback.data() || {}) : [],
        at: new Date().toISOString(),
      },
    }));
    notify("Debug Firestore write passed", "success");
  } catch (error) {
    devError("DIRECT GENERAL FIRESTORE TEST FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      uid: auth.currentUser?.uid,
      path: debugRef.path,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "failed",
        path: debugRef.path,
        code: error?.code || "",
        message: error?.message || String(error),
        at: new Date().toISOString(),
      },
    }));
    notify("Debug Firestore write failed", "error");
  }
};

const testGeneralQuoteImageUpload = async () => {
  const file = generalQuoteDebugUploadFile;
  if (!workspaceUid || !auth.currentUser || !file) {
    notify("Choose a test image and make sure you are signed in.", "warning");
    return;
  }

  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    notify(validation.message, "warning");
    return;
  }

  const debugQuoteId = "debug_general_quote_test";
  const debugItemId = "debug-item-1";
  const debugImageId = createClientId("general-image-debug");
  const uploadTimestamp = Date.now();
  const storagePath = buildGeneralQuoteImageStoragePath({
    workspaceUid,
    quoteId: debugQuoteId,
    itemId: debugItemId,
    imageId: debugImageId,
    fileName: file.name || "debug-image",
    timestamp: uploadTimestamp,
  });

  setGeneralQuoteDebugState((prev) => ({
    ...prev,
    directStorageTest: {
      state: "uploading",
      storagePath,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      at: new Date().toISOString(),
    },
  }));

  try {
    devLog("DIRECT GENERAL IMAGE UPLOAD START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      storagePath,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
    const uploadedImage = await uploadGeneralQuoteImage({
      workspaceUid,
      quoteId: debugQuoteId,
      itemId: debugItemId,
      imageId: debugImageId,
      file,
      timestamp: uploadTimestamp,
      metadata: {
        workspaceUid,
        purpose: "general_quote_debug_upload",
      },
    });
    const downloadUrl = getGeneralQuoteImageDisplayUrl(uploadedImage);
    devLog("DIRECT GENERAL IMAGE UPLOAD SUCCESS", { downloadURL: downloadUrl });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directStorageTest: {
        state: "passed",
        storagePath,
        hasDownloadUrl: Boolean(downloadUrl),
        downloadUrl,
        at: new Date().toISOString(),
      },
    }));
    notify("Debug image upload passed", "success");
  } catch (error) {
    devError("DIRECT GENERAL IMAGE UPLOAD FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      storagePath,
      workspaceUid,
      uid: auth.currentUser?.uid,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directStorageTest: {
        state: "failed",
        storagePath,
        code: error?.code || "",
        message: error?.message || String(error),
        at: new Date().toISOString(),
      },
    }));
    raiseFirebaseHealthWarning("storage", error, {
      storagePath,
      source: "directGeneralQuoteImageUpload",
    });
    notify(
      isFirebaseStorageBillingError(error)
        ? "Firebase Storage billing or bucket access issue detected. Check Firebase Storage billing/bucket configuration."
        : "Debug image upload failed",
      "error",
      isFirebaseStorageBillingError(error) ? 7000 : 3800
    );
  }
};
/* ---------------- LOAD QUOTE SAFELY (EDIT-LOCKED) ---------------- */
/* ---------------- LOAD QUOTE (EDIT-LOCKED) ---------------- */
const loadQuoteForEdit = (quote) => {
  if (!quote?.id) return;
  assertCompatibleQuote(quote);
  assertQuoteDocumentIdentity(quote, workspaceUid, quote.id);
  const originalQuoteInput = quote;
  quote = normalizeSavedQuoteForEditor(quote, quote.indexRecordRaw || quote.quoteIndex || {});
  quote = {
    ...quote,
    id: quote.id || originalQuoteInput.id,
    sourcePath: quote.sourcePath || originalQuoteInput.sourcePath || "",
  };

  if (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty) {
    persistGeneralQuoteDraft("route-change");
  }
  if (tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty) {
    persistCurtainQuoteDraft("route-change");
  }

  if (tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty) {
    persistCurtainQuoteDraft("route-change");
  }

  devLog("LOAD FOR EDIT CALLED");
  traceQuoteIdentity("loadQuoteForEdit", quote, {
    quoteId: quote.id || quote.quoteId || "",
    quoteNo: quote.quoteNo || "",
    quoteIndexCustomerName:
      quote.indexRecordRaw?.customerName || quote.quoteIndex?.customerName || "",
    quoteIndexSiteName: quote.indexRecordRaw?.siteName || quote.quoteIndex?.siteName || "",
  });
  const loadedQuoteWorkflowIdentity = getSavedQuoteWorkflowIdentity(quote, workspaceUid);
  autoHealQuoteIndexIdentityFromLoadedQuote({
    workspaceUid,
    quoteIndexId:
      loadedQuoteWorkflowIdentity.quoteIndexId ||
      quote.quoteIndexId ||
      quote.indexId ||
      quote.id,
    loadedQuote: quote,
    resolvedSourcePath: loadedQuoteWorkflowIdentity.sourcePath || quote.sourcePath || "",
  });
  setPendingCrmLeadId(quote?.crm?.leadId || "");
  setPendingRecalculationAudit(null);
  setPendingLegacyCalculationSnapshot(null);
  setLegacyRecalculationPreview(null);
  setLegacyRecalculationModalOpen(false);
  setLegacyRecalculationRevisionRequest(null);
  legacyRecalculationRevisionSaveRef.current = "";

  if (resolveQuoteType(quote) === GENERAL_QUOTE_TYPE) {
    if (!canAccessGeneralQuotes) {
      alert("General quotations are not enabled for this account plan.");
      return;
    }
    applyGeneralQuoteState(quote);
    return;
  }

  if (!canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
  setEditingQuoteId(quote.id);
  setGeneralEditingQuoteId(null);
  setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
  setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setSelectedQuote({
    ...quote,
    id: quote.id,
    quoteId: loadedQuoteWorkflowIdentity.quoteId || quote.quoteId || quote.id,
    sourceQuoteId: loadedQuoteWorkflowIdentity.sourceQuoteId || quote.sourceQuoteId || quote.id,
    fullQuoteId: loadedQuoteWorkflowIdentity.fullQuoteId || quote.fullQuoteId || quote.id,
    quoteIndexId: loadedQuoteWorkflowIdentity.quoteIndexId || quote.quoteIndexId || quote.indexId || quote.id,
    indexId: loadedQuoteWorkflowIdentity.quoteIndexId || quote.indexId || quote.quoteIndexId || quote.id,
    sourceCollection: quote.sourceCollection || "quotations",
    sourcePath: loadedQuoteWorkflowIdentity.sourcePath || quote.sourcePath || "",
    quoteType: CURTAIN_QUOTE_TYPE,
  });

  /* ================= BASIC FIELDS ================= */
  setCustomerName(quote.customerName || "");
  const loadedQuoteSiteSelection = normalizeQuoteSiteSnapshot(quote, {
    customerName: quote.customerName || "",
  });
  setQuoteSiteSelection(loadedQuoteSiteSelection);
  setQuoteCustomerLink({
    customerId: quote.customerId || quote.crm?.customerId || "",
    customerKey:
      quote.customerKey ||
      quote.crm?.customerKey ||
      quote.customerId ||
      quote.crm?.customerId ||
      "",
    customerName: quote.customerName || "",
    phone: quote.customerPhone || quote.crm?.customerPhone || "",
    whatsapp: quote.customerWhatsapp || quote.crm?.customerWhatsapp || "",
    email: quote.customerEmail || quote.email || quote.crm?.contact?.email || "",
    address:
      quote.customerAddress || quote.address || quote.crm?.contact?.address || "",
    companyName: quote.customerCompanyName || quote.companyName || "",
  });
  setQuoteNo(quote.quoteNo || "");
  const loadedQuoteDate = resolveQuoteDateForInput(quote, todayISO());
  setDate(loadedQuoteDate);

  /* =================  RESTORE REPEAT FIRST ================= */
  const cleaned = mergeRepeatSettingsFromFabricSummaryMeta(
    quote.repeatSettings || {},
    quote.fabricSummaryMeta || {}
  );

  setRepeatSettings(cleaned);

  /* ================= ROWS ================= */
const loadedCurtainContentSelection = resolveCurtainQuoteContentSelection(quote);
const loadedCurtainRows = Array.isArray(loadedCurtainContentSelection.items)
  ? loadedCurtainContentSelection.items
  : [];
const legacyAccessoryRows = [
  quote.accessories,
  quote.accessoryRows,
  quote.addOns,
  quote.extraItems,
]
  .filter(Array.isArray)
  .flat();
const existingLoadedRowKeys = new Set(
  loadedCurtainRows
    .map((row) => getWorkshopRowStableId(row) || row?.id || row?.itemId || "")
    .map((value) => String(value || "").trim())
    .filter(Boolean)
);
const mergedLoadedRows = [...loadedCurtainRows];
legacyAccessoryRows.forEach((row, accessoryIndex) => {
  const rowKey = String(
    getWorkshopRowStableId(row) ||
      row?.id ||
      row?.itemId ||
      row?.accessoryId ||
      `legacy-accessory-${accessoryIndex + 1}`
  ).trim();
  if (rowKey && existingLoadedRowKeys.has(rowKey)) return;
  if (rowKey) existingLoadedRowKeys.add(rowKey);
  mergedLoadedRows.push({
    ...row,
    id: row?.id || rowKey,
    itemId: row?.itemId || rowKey,
    lineType: row?.lineType || "accessory",
    item: row?.item || row?.name || row?.label || "Accessory",
  });
});
const safeRows = Array.isArray(mergedLoadedRows)
  ? mergedLoadedRows.map((r, index) =>
      hydrateCurtainRowCalculationSnapshot(
        sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...r }, index))
      )
    )
  : [];
const loadedQuoteDisplayTotal = getSavedQuoteDisplayTotal(quote);
const expectedLoadedRowCount = Number(
  quote.rowCount ?? quote.contentRowCount ?? loadedCurtainContentSelection.count ?? 0
);
const openedAsUnsafeEmpty =
  safeRows.length === 0 &&
  (expectedLoadedRowCount > 0 || Number(loadedQuoteDisplayTotal.amount || 0) > 0.05);
if (openedAsUnsafeEmpty) {
  const protectedErrorState = {
    quoteIndex: quote.indexRecordRaw || quote.quoteIndex || quote,
    indexId: quote.quoteIndexId || quote.indexId || "",
    quoteNo: quote.quoteNo || "",
    quoteType: quote.quoteType || CURTAIN_QUOTE_TYPE,
    sourcePath: quote.sourcePath || "",
    sourceQuoteId: quote.sourceQuoteId || quote.id || "",
    quoteId: quote.id || quote.quoteId || "",
    attemptedPaths: [quote.sourcePath].filter(Boolean),
    result: "loaded-with-unsafe-save-target",
    code: "content-unverified-save-protected",
    message:
      "This quotation was opened from an older record and cannot be safely overwritten. Please use Save As New.",
    hydratedRowCount: 0,
    indexGrandTotal: Number(loadedQuoteDisplayTotal.amount || 0),
    indexContentCount: expectedLoadedRowCount,
    selectedLoadField: loadedCurtainContentSelection.field || "empty",
    resolvedMainQuotePath: quote.sourcePath || "",
    loadStatus: "loaded",
    saveTargetStatus: quote.saveTargetStatus || "content_unverified",
    saveTargetReason:
      quote.saveTargetReason || "editor_hydration_found_no_rows_for_non_empty_quote",
  };
  incompleteQuoteLoadRef.current = protectedErrorState;
  setIncompleteQuoteLoadState(protectedErrorState);
  if (isDevRuntime) {
    devWarn("QUOTE_HYDRATION_RESULT_TRACE", {
      quoteNo: quote.quoteNo || "",
      hydratedRowCount: 0,
      hydratedAccessoryCount: 0,
      hydratedTotal: Number(loadedQuoteDisplayTotal.amount || 0),
      sourceUsedForRows: loadedCurtainContentSelection.field || "empty",
      sourceUsedForTotals: loadedQuoteDisplayTotal.source || "none",
      openedAsEmpty: true,
      warning: "editor_hydration_empty_rows_save_will_be_blocked",
      aliasCounts: loadedCurtainContentSelection.aliasCounts,
    });
  }
}

if (IS_DEV_UI_ENABLED) {
  const hasLegacyCalculationRows = safeRows.some(
    (row) => row.calculationSnapshot?.requiresRecalculation === true
  );
  devLog("QUOTE_HYDRATION_RESULT_TRACE", {
    quoteNo: quote.quoteNo || "",
    hydratedRowCount: safeRows.length,
    hydratedAccessoryCount: safeRows.filter((row) => isCurtainAccessoryRowStatic(row)).length,
    hydratedTotal: Number(loadedQuoteDisplayTotal.amount || 0),
    sourceUsedForRows: quote.rowChunkLoadSource || loadedCurtainContentSelection.field || "empty",
    sourceUsedForTotals: loadedQuoteDisplayTotal.source || "none",
    openedAsEmpty: safeRows.length === 0,
    warning: safeRows.length === 0 ? "empty_quote_loaded" : "",
    aliasCounts: loadedCurtainContentSelection.aliasCounts,
  });
  devLog(
    "STITCH_TRACE_SAVED_QUOTE_ROWS",
    safeRows.map((row, index) =>
      buildWorkshopStitchTraceRow(row, stitchingRates, getRowProductConfig(row), {
        stage: "saved_quote_rows",
        index,
      })
    )
  );
  devLog("QUOTE_REOPEN_CALC_TRACE", {
    quoteNo: quote.quoteNo || "",
    mode: hasLegacyCalculationRows ? "legacy-snapshot-awaiting-recalculate" : "hydrate-snapshot",
    rows: safeRows.map((row) => ({
      item: row.item,
      width: row.width,
      height: row.height,
      fabricWidth: row.fabricWidthIn,
      usableFabricWidth: row.calculationSnapshot?.usableFabricWidthIn,
      fullness: row.calculationSnapshot?.fullness,
      rawPanelCount: row.calculationSnapshot?.rawPieces,
      finalPieces: row.calculationSnapshot?.pieces,
      orderedFabricMeters: row.calculationSnapshot?.orderedFabricMeters,
      lineTotal: row.calculationSnapshot?.lineTotal,
      recalculated: row.__recalculated === true,
      calculationVersion: row.calculationSnapshot?.calculationVersion || "",
      requiresRecalculation:
        row.calculationSnapshot?.requiresRecalculation === true,
    })),
  });
}

const loadedFabricSummaryMeta = sanitizeFabricSummaryMetaForValidCatalogues(
  quote.fabricSummaryMeta,
  cleaned,
  safeRows
);
setFabricSummaryMeta(loadedFabricSummaryMeta);
fabricSummaryMetaRef.current = loadedFabricSummaryMeta;

replaceCurtainRows(safeRows, QUOTE_RAW_FIELD_MUTATION_SOURCES.SERVER_LOAD, {
  quoteId: quote.id || "",
});
  // Workshop export must follow the current visible rows, not a stale saved
  // effectiveRows snapshot from an earlier edit/export cycle.
  setEffectiveRows(safeRows);
  setLeftoverUsageLog(quote.leftoverUsageLog || []);

  /* ================= DISCOUNT ================= */
  const loadedDiscount = normalizeQuoteDiscount(
    quote,
    "curtain_quote_open"
  );
  const loadedGstMode = resolveCurtainQuoteGstMode(quote);
  const loadedGstRate = resolveCurtainQuoteGstRate(quote);
  const loadedQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quote.quoteFullnessOverrides || quote.fullnessOverrides || {}
  );
  setSummaryDiscountMode(getSummaryDiscountModeFromType(loadedDiscount.discountType));
  setSummaryDiscountValue(String(loadedDiscount.discountValue));
  curtainGstModeRef.current = loadedGstMode;
  curtainGstRateRef.current = loadedGstRate;
  setCurtainGstMode(loadedGstMode);
  setCurtainGstRate(loadedGstRate);
  quoteFullnessOverridesRef.current = loadedQuoteFullnessOverrides;
  setQuoteConstructionSettings(normalizeQuoteCalculationSettings(quote));
  setConstructionRevisionReview(null);
  setQuoteFullnessOverrides(loadedQuoteFullnessOverrides);
  setQuoteFullnessOverrideInputs({});

  /* ================= STATUS ================= */
  setSelectedQuoteWorkStage(quote.workStage || "quote_given");
  setSelectedQuoteStatus(quote.status || "draft");
  setSelectedInstallationDate(quote.installationDate || null);
  setUseHalfPieceReuse(quote.useHalfPieceReuse !== false);
  setPassHalfPieceSavingsToCustomer(quote.passHalfPieceSavingsToCustomer === true);
  markCurtainQuoteBaseline(
    {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: quote.id || "new",
      sourceQuoteId: loadedQuoteWorkflowIdentity.sourceQuoteId || quote.sourceQuoteId || quote.id || "",
      fullQuoteId: loadedQuoteWorkflowIdentity.fullQuoteId || quote.fullQuoteId || quote.id || "",
      quoteIndexId: loadedQuoteWorkflowIdentity.quoteIndexId || quote.quoteIndexId || quote.indexId || "",
      indexId: loadedQuoteWorkflowIdentity.quoteIndexId || quote.indexId || quote.quoteIndexId || "",
      sourceCollection: quote.sourceCollection || "quotations",
      sourcePath: loadedQuoteWorkflowIdentity.sourcePath || quote.sourcePath || "",
      editingQuoteId: quote.id || "",
      selectedQuoteId: quote.id || "",
      customerName: quote.customerName || "",
      quoteSiteSelection: loadedQuoteSiteSelection,
      ...loadedQuoteSiteSelection,
      quoteCustomerLink: {
        customerId: quote.customerId || quote.crm?.customerId || "",
        customerName: quote.customerName || "",
        phone: quote.customerPhone || quote.crm?.customerPhone || "",
        whatsapp: quote.customerWhatsapp || quote.crm?.customerWhatsapp || "",
        email: quote.customerEmail || quote.email || quote.crm?.contact?.email || "",
        address:
          quote.customerAddress || quote.address || quote.crm?.contact?.address || "",
        companyName: quote.customerCompanyName || quote.companyName || "",
      },
      quoteNo: quote.quoteNo || "",
      quoteDate: loadedQuoteDate,
      date: loadedQuoteDate,
      quotationDate: loadedQuoteDate,
      rows: safeRows,
      repeatSettings: cleaned,
      quoteFullnessOverrides: loadedQuoteFullnessOverrides,
      fabricSummaryMeta: loadedFabricSummaryMeta,
      discountType: loadedDiscount.discountType,
      discountValue: loadedDiscount.discountValue,
      quoteGstMode: loadedGstMode,
      gstMode: loadedGstMode,
      quoteGstRate: loadedGstRate,
      gstRate: loadedGstRate,
      gstModeLabel: getCurtainGstModeLabel(loadedGstMode, loadedGstRate),
      gstBreakdown: quote.gstBreakdown || quote.quoteSettings?.gstBreakdown || {},
      quoteSettings: {
        ...(quote.quoteSettings || {}),
        quoteGstMode: loadedGstMode,
        quoteGstRate: loadedGstRate,
        gstMode: loadedGstMode,
        gstBreakdown: quote.gstBreakdown || quote.quoteSettings?.gstBreakdown || {},
      },
      gstAmount: Number(quote.gstAmount || 0),
      taxableAmount: Number(quote.taxableAmount || 0),
      finalTotal: Number(quote.finalTotal || quote.grandTotal || 0),
      grandTotal: Number(quote.grandTotal || quote.finalTotal || 0),
      summaryDiscountMode: getSummaryDiscountModeFromType(loadedDiscount.discountType),
      summaryDiscountValue: String(loadedDiscount.discountValue),
      selectedQuoteWorkStage: quote.workStage || "quote_given",
      selectedQuoteStatus: quote.status || "draft",
      selectedInstallationDate: quote.installationDate || null,
      useHalfPieceReuse: quote.useHalfPieceReuse !== false,
      passHalfPieceSavingsToCustomer: quote.passHalfPieceSavingsToCustomer === true,
      pendingCrmLeadId: quote?.crm?.leadId || "",
    },
    "saved"
  );

  /* ================= NAVIGATION ================= */
  setTab("quote");
};

const openCrmQuoteForDeal = (deal) => {
  if (!canViewQuotes) {
    alert("You do not have permission to view quotations in this workspace.");
    return;
  }

  const primaryLinkedQuote = getPrimaryCrmLinkedQuote(deal?.linkedQuotes || []);
  const quoteId =
    deal?.crmRecordType === "lead"
      ? primaryLinkedQuote?.quoteId || ""
      : deal?.id || "";

  const resolvedQuoteId =
    quoteId ||
    savedQuotes.find(
      (quote) =>
        primaryLinkedQuote?.quoteNo &&
        String(quote.quoteNo || "") === String(primaryLinkedQuote.quoteNo || "")
    )?.id ||
    "";

  if (!resolvedQuoteId) return;
  loadFullQuote(resolvedQuoteId);
};

const startQuoteFromCrmLead = (deal) => {
  if (!canCreateQuotes) {
    alert("You do not have permission to create quotations in this workspace.");
    return;
  }

  if (!deal?.id) return;
  const requestedQuoteType = deal.pipelineType || deal.quoteTypeResolved || CURTAIN_QUOTE_TYPE;

  if (requestedQuoteType === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
    alert("General quotations are not enabled for this account plan.");
    return;
  }

  if (requestedQuoteType !== GENERAL_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  const leadContact = deal.crmContact || deal.contact || {};
  const leadCustomerName =
    leadContact.name ||
    deal.customerLabel ||
    deal.companyOrProject ||
    deal.crmTitle ||
    "";
  const leadCustomerLink = {
    customerId: deal.customerId || deal.crm?.customerId || "",
    customerName: leadCustomerName,
    phone: leadContact.phone || deal.customerPhone || deal.crm?.customerPhone || "",
    whatsapp: leadContact.whatsapp || deal.customerWhatsapp || "",
    email: leadContact.email || deal.customerEmail || "",
    address: leadContact.address || deal.customerAddress || "",
    companyName: deal.companyOrProject || deal.customerCompanyName || "",
  };

  setPendingCrmLeadId(deal.id);
  if (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty) {
    persistGeneralQuoteDraft("route-change");
  }
  setTab("quote");
  setSelectedQuote(null);
  setSelectedQuoteStatus(deal.status || "draft");
  setSelectedQuoteWorkStage(
    deal.crmStageId ||
      getDefaultCrmStageId(crmPipelines, deal.pipelineType || deal.quoteTypeResolved)
  );
  setSelectedInstallationDate(deal.installationDate || null);
  setIsNewQuote(true);

  if (requestedQuoteType === GENERAL_QUOTE_TYPE) {
    const nextGeneralQuote = normalizeGeneralQuote(
      {
        ...createDefaultGeneralQuote({
          currency,
          businessName,
          shopAddress,
          businessPhone,
          businessEmail,
        }),
        title: deal.crmTitle || "Quotation",
        customerId: leadCustomerLink.customerId,
        customerName: leadCustomerLink.customerName,
        customerPhone: leadCustomerLink.phone,
        customerWhatsapp: leadCustomerLink.whatsapp,
        customerEmail: leadCustomerLink.email,
        customerAddress: leadCustomerLink.address,
        customerCompanyName: leadCustomerLink.companyName,
        customerLedgerLinked: Boolean(leadCustomerLink.customerId),
        quoteFor: {
          name: leadContact.name || "",
          company: deal.companyOrProject || "",
          address: leadContact.address || "",
          phone: leadContact.phone || leadContact.whatsapp || "",
          email: leadContact.email || "",
        },
        notes: deal.crmNotes || "",
      },
      {
        currency,
        businessName,
        shopAddress,
        businessPhone,
        businessEmail,
      }
    );

    setRoutedTab("quote", GENERAL_QUOTE_TYPE);
    setEditingQuoteId(null);
    setGeneralEditingQuoteId(null);
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setGeneralQuote(nextGeneralQuote);
    markGeneralQuoteBaseline(nextGeneralQuote, "idle");
    setCustomerName(leadCustomerName);
    setQuoteCustomerLink(leadCustomerLink);
    setQuoteNo("");
    setDate(resolveQuoteDateForInput(nextGeneralQuote, todayISO()));
    setAutoSaveStatus("idle");
    return;
  }

  setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(null);
  setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
  setCustomerName(leadCustomerName);
  setQuoteCustomerLink(leadCustomerLink);
  setQuoteNo("");
  setDate(todayISO());
  const defaultCurtainRows = [createDefaultCurtainQuoteRow("W1")];
  replaceCurtainRows(
    defaultCurtainRows,
    QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE,
    { quoteId: "new" }
  );
  setRepeatSettings({});
  setFabricSummaryMeta({});
  setEffectiveRows([]);
  setLeftoverUsageLog([]);
  setSummaryDiscountMode("%");
  setSummaryDiscountValue("0");
  curtainGstModeRef.current = CURTAIN_GST_MODE_FLAT;
  curtainGstRateRef.current = 0;
  setCurtainGstMode(CURTAIN_GST_MODE_FLAT);
  setCurtainGstRate(0);
  quoteFullnessOverridesRef.current = {};
  setQuoteConstructionSettings(normalizeQuoteCalculationSettings());
  setConstructionRevisionReview(null);
  setQuoteFullnessOverrides({});
  setQuoteFullnessOverrideInputs({});
  setUseHalfPieceReuse(true);
  setPassHalfPieceSavingsToCustomer(false);
  const newCurtainQuoteDate = todayISO();
  markCurtainQuoteBaseline(
    {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: "new",
      editingQuoteId: "",
      selectedQuoteId: "",
      customerName: leadCustomerName,
      quoteCustomerLink: leadCustomerLink,
      quoteNo: "",
      quoteDate: newCurtainQuoteDate,
      date: newCurtainQuoteDate,
      quotationDate: newCurtainQuoteDate,
      rows: defaultCurtainRows,
      repeatSettings: {},
      quoteFullnessOverrides: {},
      fabricSummaryMeta: {},
      discountType: "percent",
      discountValue: 0,
      quoteGstMode: CURTAIN_GST_MODE_FLAT,
      gstMode: CURTAIN_GST_MODE_FLAT,
      gstRate: 0,
      gstAmount: 0,
      taxableAmount: 0,
      finalTotal: 0,
      grandTotal: 0,
      summaryDiscountMode: "%",
      summaryDiscountValue: "0",
      selectedQuoteWorkStage:
        deal.crmStageId ||
        getDefaultCrmStageId(crmPipelines, deal.pipelineType || deal.quoteTypeResolved),
      selectedQuoteStatus: deal.status || "draft",
      selectedInstallationDate: deal.installationDate || null,
      useHalfPieceReuse: true,
      passHalfPieceSavingsToCustomer: false,
      pendingCrmLeadId: deal.id,
    },
    "idle"
  );
};

const openQuoteLinkModal = useCallback(
  (deal) => {
    if (!deal?.id) return;

    if (deal.crmRecordType !== "lead") {
      notify("Open the CRM lead record before linking a quote.", "info");
      return;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(deal)
    );
    setQuoteLinkModalState({ leadId: deal.id });
    setQuoteLinkSearch(
      primaryLinkedQuote?.quoteNo ||
        deal.quoteNo ||
        deal.crmContact?.name ||
        deal.customerLabel ||
        ""
    );
    setQuoteLinkTypeFilter("all");
    setQuoteLinkMinAmount("");
    setQuoteLinkMaxAmount("");
  },
  [notify]
);

const closeQuoteLinkModal = useCallback(() => {
  setQuoteLinkModalState(null);
  setQuoteLinkSearch("");
  setQuoteLinkTypeFilter("all");
  setQuoteLinkMinAmount("");
  setQuoteLinkMaxAmount("");
  setIsLinkingQuote(false);
}, []);

const openQuoteFromLinkModal = (quote) => {
  if (!quote?.id) return;
  closeQuoteLinkModal();
  loadFullQuote(quote);
};

const performLinkExistingQuote = useCallback(
  async (leadLike, quote) => {
    if (!workspaceUid || !leadLike?.id || !quote?.id) return;

    setIsLinkingQuote(true);
    try {
      const quoteMeta = getQuoteLinkDisplayData(quote);
      const linkedQuoteValue = Number(quoteMeta.totalValue || 0);
      const existingLinkedQuotes = normalizeCrmLinkedQuotes(leadLike);
      const isAlreadyLinkedToLead = existingLinkedQuotes.some(
        (linkedQuote) =>
          String(linkedQuote.quoteId || "") === String(quote.id || "") ||
          (quoteMeta.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(quoteMeta.quoteNo || ""))
      );
      const linkedQuotes = normalizeCrmLinkedQuotes(leadLike, {
        linkedQuotes: [
          ...existingLinkedQuotes
            .filter(
              (linkedQuote) =>
                String(linkedQuote.quoteId || "") !== String(quote.id || "")
            )
            .map((linkedQuote) => ({ ...linkedQuote, isPrimary: false })),
          {
            quoteId: quote.id,
            quoteNo: quoteMeta.quoteNo || "",
            type: quoteMeta.quoteType,
            value: linkedQuoteValue,
            isPrimary: true,
          },
        ],
      });
      const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadLike.id);
      const leadPatch = {
        linkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: true,
        expectedValue: linkedQuoteValue || Number(leadLike.expectedValue || 0),
        finalValue: linkedQuoteValue || Number(leadLike.finalValue || 0),
        ...(isDevRuntime
          ? {
              _lastWriteSource: "linkQuoteToDeal.targetLead",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      const previousLead = findLinkedCrmLeadForQuote(quote);
      if (previousLead?.id && previousLead.id !== leadLike.id) {
        const previousLinkedQuotes = normalizeCrmLinkedQuotes(previousLead).filter(
          (linkedQuote) =>
            String(linkedQuote.quoteId || "") !== String(quote.id || "") &&
            String(linkedQuote.quoteNo || "") !== String(quoteMeta.quoteNo || "")
        );
        const previousLeadRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_leads",
          previousLead.id
        );
        await setDoc(
          previousLeadRef,
          {
            linkedQuotes: normalizeCrmLinkedQuotes(previousLead, {
              linkedQuotes: previousLinkedQuotes,
            }),
            hasQuote: previousLinkedQuotes.length > 0,
            ...createLegacyCrmQuoteLinkDeletePatch(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "linkQuoteToDeal.previousLead",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        applyCrmLeadLocalPatch(previousLead.id, {
          linkedQuotes: normalizeCrmLinkedQuotes(previousLead, {
            linkedQuotes: previousLinkedQuotes,
          }),
          hasQuote: previousLinkedQuotes.length > 0,
          updatedAt: new Date(),
        });
      }

      await setDoc(leadRef, leadPatch, { merge: true });

      const linkedBy = user?.uid || "";
      const linkedByEmail = user?.email || "";
      const quoteUpdate = {
        "crm.leadId": leadLike.id,
        "crm.linkedAt": serverTimestamp(),
        "crm.linkedBy": linkedBy,
        "crm.linkedByEmail": linkedByEmail,
        ...createLegacyQuoteCrmLinkDeletePatch(),
        updatedAt: serverTimestamp(),
      };

      if (previousLead?.id && previousLead.id !== leadLike.id) {
        quoteUpdate["crm.previousLinkedLeadId"] = previousLead.id;
        quoteUpdate["crm.previousLinkedLeadTitle"] =
          previousLead.title || previousLead.contact?.name || "";
      }

      await updateDoc(doc(db, "users", workspaceUid, "quotations", quote.id), quoteUpdate);

      const localLinkedAt = new Date();
      applyCrmLeadLocalPatch(leadLike.id, {
        ...leadPatch,
        updatedAt: localLinkedAt,
      });
      applyQuoteLocalPatch(quote.id, {
        crm: stripLegacyCrmQuoteLinkFields({
          ...(quote.crm || {}),
          leadId: leadLike.id,
          linkedAt: localLinkedAt,
          linkedBy,
          linkedByEmail,
          ...(previousLead?.id && previousLead.id !== leadLike.id
            ? {
                previousLinkedLeadId: previousLead.id,
                previousLinkedLeadTitle:
                  previousLead.title || previousLead.contact?.name || "",
              }
            : {}),
        }),
        updatedAt: localLinkedAt,
      });

      setSelectedCrmDealId(leadLike.id);
      setCrmDetailsSavedAt(localLinkedAt);
      closeQuoteLinkModal();
      notify(isAlreadyLinkedToLead ? "Quote already linked" : "Quote linked successfully", "success");
    } catch (error) {
      devError("Failed to link existing quote:", error);
      notify("Could not link quote. Please try again.", "error");
    } finally {
      setIsLinkingQuote(false);
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    closeQuoteLinkModal,
    findLinkedCrmLeadForQuote,
    getQuoteLinkDisplayData,
    notify,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const requestLinkExistingQuote = useCallback(
  (leadLike, quote) => {
    if (!leadLike?.id || !quote?.id) return;

    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
    const alreadyLinkedToThisLead = linkedQuotes.some(
      (linkedQuote) =>
        String(linkedQuote.quoteId || "") === String(quote.id || "") ||
        (quote.quoteNo &&
          String(linkedQuote.quoteNo || "") === String(quote.quoteNo || ""))
    );
    if (alreadyLinkedToThisLead) {
      notify("This quote is already linked to this lead.", "info");
      return;
    }
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const replacingCurrent =
      Boolean(primaryLinkedQuote?.quoteId || primaryLinkedQuote?.quoteNo) &&
      primaryLinkedQuote?.quoteId !== String(quote.id || "") &&
      primaryLinkedQuote?.quoteNo !== String(quote.quoteNo || "");
    const linkedLead = findLinkedCrmLeadForQuote(quote);
    const linkedElsewhere = linkedLead?.id && linkedLead.id !== leadLike.id;

    if (replacingCurrent || linkedElsewhere) {
      const description = linkedElsewhere
        ? "This quote is already linked to another lead."
        : "This lead already has a linked quote. Replace it?";

      openConfirmDialog({
        title: linkedElsewhere ? "Quote already linked" : "Replace linked quote?",
        description,
        confirmLabel: linkedElsewhere ? "Link Anyway" : "Replace Quote",
        cancelLabel: "Cancel",
        tone: linkedElsewhere ? "warning" : "danger",
        onConfirm: () => performLinkExistingQuote(leadLike, quote),
      });
      return;
    }

    performLinkExistingQuote(leadLike, quote);
  },
  [findLinkedCrmLeadForQuote, notify, openConfirmDialog, performLinkExistingQuote]
);

const performUnlinkQuoteFromLead = useCallback(
  async (leadLike) => {
    if (!workspaceUid || !leadLike?.id) return;

    const linkedQuote = findLinkedQuoteForLead(leadLike);
    if (!linkedQuote?.id) {
      notify("This lead does not have a linked quote.", "info");
      return;
    }

    setIsLinkingQuote(true);
    try {
      const currentLinkedQuotes = normalizeCrmLinkedQuotes(leadLike);
      const primaryLinkedQuote = getPrimaryCrmLinkedQuote(currentLinkedQuotes);
      const remainingLinkedQuotes = normalizeCrmLinkedQuotes(leadLike, {
        linkedQuotes: currentLinkedQuotes.filter(
          (linkedQuote) =>
            String(linkedQuote.quoteId || "") !== String(primaryLinkedQuote?.quoteId || "") &&
            String(linkedQuote.quoteNo || "") !== String(primaryLinkedQuote?.quoteNo || "")
        ),
      });
      const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadLike.id);
      await updateDoc(leadRef, {
        linkedQuotes: remainingLinkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: remainingLinkedQuotes.length > 0,
        ...(isDevRuntime
          ? {
              _lastWriteSource: "unlinkQuoteFromDeal",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      });

      const quoteLeadId = String(linkedQuote.crm?.leadId || "");
      if (quoteLeadId === leadLike.id) {
        await updateDoc(doc(db, "users", workspaceUid, "quotations", linkedQuote.id), {
          "crm.leadId": deleteField(),
          "crm.linkedAt": deleteField(),
          "crm.linkedBy": deleteField(),
          "crm.linkedByEmail": deleteField(),
          ...createLegacyQuoteCrmLinkDeletePatch(),
          updatedAt: serverTimestamp(),
        });

        const nextCrm = stripLegacyCrmQuoteLinkFields({ ...(linkedQuote.crm || {}) });
        delete nextCrm.leadId;
        delete nextCrm.linkedAt;
        delete nextCrm.linkedBy;
        delete nextCrm.linkedByEmail;
        applyQuoteLocalPatch(linkedQuote.id, {
          crm: nextCrm,
          updatedAt: new Date(),
        });
      }

      const localUpdatedAt = new Date();
      applyCrmLeadLocalPatch(leadLike.id, {
        linkedQuotes: remainingLinkedQuotes,
        hasQuote: remainingLinkedQuotes.length > 0,
        updatedAt: localUpdatedAt,
      });

      setCrmDetailsSavedAt(localUpdatedAt);
      notify("Quote unlinked", "success");
    } catch (error) {
      devError("Failed to unlink quote:", error);
      notify("Could not unlink quote. Please try again.", "error");
    } finally {
      setIsLinkingQuote(false);
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    findLinkedQuoteForLead,
    notify,
    workspaceUid,
  ]
);

const requestUnlinkQuoteFromLead = useCallback(
  (leadLike) => {
    if (!leadLike?.id) return;

    openConfirmDialog({
      title: "Unlink quote?",
      description: "This removes the quote link from the lead only. The quotation itself stays unchanged.",
      confirmLabel: "Unlink Quote",
      cancelLabel: "Cancel",
      tone: "warning",
      onConfirm: () => performUnlinkQuoteFromLead(leadLike),
    });
  },
  [openConfirmDialog, performUnlinkQuoteFromLead]
);

const generateQuoteNumber = async (uid, quoteType) => {
  const reserve = httpsCallable(functions, 'reserveQuotationNumber');
  const result = await reserve({ workspaceUid: uid, quoteType });
  if (!result.data?.quoteNo) throw new Error('Quote number could not be reserved.');
  return result.data.quoteNo;
};
const saveGeneralQuotation = async ({ asNew = false, event = null } = {}) => {
  const eventGuard = applyQuoteActionEventGuard(event);
  if (!operationalQuoteSettingsReady()) return false;
  const generalSaveActionName = asNew ? "saveAsNew" : "save";
  const traceId = startSaveTrace({
    action: asNew ? "save_as_new" : "save",
    quoteType: GENERAL_QUOTE_TYPE,
    eventGuard,
  });
  let requiredWritesSucceeded = false;
  let optionalWriteFailed = false;
  logQuoteSaveEventGuard({
    handler: "saveGeneralQuotation",
    traceId,
    event,
    eventGuard,
  });
  const effectiveGeneralEditingQuoteId =
    generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
      ? generalEditingQuoteId || ""
      : "";
  const isEditingExisting =
    !asNew &&
    generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING &&
    Boolean(effectiveGeneralEditingQuoteId);
  const previousDraftKey = generalQuoteDraftKey;
  let debugQuoteId = effectiveGeneralEditingQuoteId;
  let debugPayload = null;

  if (!canAccessGeneralQuotes) {
    alert("General quotations are not enabled for this account plan.");
    return;
  }

  if (isEditingExisting ? !canEditQuotes : !canCreateQuotes) {
    alert(
      isEditingExisting
        ? "You do not have permission to edit quotations in this workspace."
        : "You do not have permission to create quotations in this workspace."
    );
    return;
  }

  if (isLoadingQuoteForEdit) {
    notify("Quotation is still loading. Please wait before saving.", "warning", 5000);
    return;
  }

  const blockedGeneralLoad = incompleteQuoteLoadRef.current;
  if (
    !asNew &&
    isEditingExisting &&
    blockedGeneralLoad &&
    [
      blockedGeneralLoad.quoteId,
      blockedGeneralLoad.sourceQuoteId,
      blockedGeneralLoad.indexId,
      blockedGeneralLoad.quoteIndex?.quoteId,
      blockedGeneralLoad.quoteIndex?.sourceQuoteId,
      blockedGeneralLoad.quoteIndex?.id,
    ]
      .filter(Boolean)
      .map(String)
      .includes(String(effectiveGeneralEditingQuoteId))
  ) {
    devError("GENERAL_SAVE_BLOCKED_INCOMPLETE_QUOTE_LOAD", {
      quoteNo: blockedGeneralLoad.quoteNo || "",
      quoteId: effectiveGeneralEditingQuoteId,
      sourcePath: blockedGeneralLoad.resolvedMainQuotePath || blockedGeneralLoad.sourcePath || "",
      selectedLoadField: blockedGeneralLoad.selectedLoadField || "",
      indexGrandTotal: blockedGeneralLoad.indexGrandTotal || 0,
      indexContentCount: blockedGeneralLoad.indexContentCount || 0,
    });
    setAutoSaveStatus("error");
    notify(
      "This quotation was opened from an older record and cannot be safely overwritten. Please use Save As New.",
      "error",
      8000
    );
    return;
  }

  if (isSaving) {
    traceGeneralActionHealth(generalSaveActionName);
    return;
  }

  const isProtectedLegacyUpdate =
    isEditingExisting &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    legacyCurtainRecalculationImpactRef.current?.shouldShowBanner === true &&
    !pendingRecalculationAudit &&
    !legacyRecalculationRevisionRequest;

  if (isProtectedLegacyUpdate) {
    notify(
      "This older quote is protected and still shows saved values. Create a new revision to save the latest formula.",
      "warning"
    );
    setAutoSaveStatus("idle");
    return false;
  }

  markQuoteSaveInProgressSession({
    traceId,
    action: asNew ? "save_as_new" : "save",
    quoteType: GENERAL_QUOTE_TYPE,
    quoteId: !asNew ? effectiveGeneralEditingQuoteId : "",
    quoteIndexId: !asNew ? effectiveGeneralEditingQuoteId : "",
    quoteNo: generalQuoteRef.current?.quoteNo || generalQuote?.quoteNo || selectedQuote?.quoteNo || "",
  });
  beginGeneralActionLock(generalSaveActionName, "saveInProgress");
  setIsSaving(true);
  setAutoSaveStatus("saving");
  setQuoteRowValidationError(null);

  try {
    const currentUser = auth.currentUser;
    if (!currentUser || !workspaceUid) {
      devError("REAL GENERAL QUOTE SAVE BLOCKED: workspace/auth missing", {
        workspaceUid,
        uid: currentUser?.uid || "",
        email: currentUser?.email || "",
      });
      alert("Could not save because workspace is not ready. Please refresh and try again.");
      setAutoSaveStatus("error");
      return;
    }
    if (generalUploadPromiseRef.current) {
      if (isDevelopmentMode()) {
        devLog("GENERAL QUOTE SAVE WAITING FOR IMAGE UPLOAD", {
          workspaceUid,
          uid: currentUser.uid,
          uploadingItemId: generalUploadingItemId,
        });
      }
      await generalUploadPromiseRef.current;
    }

    const liveGeneralActionPayload = buildLiveGeneralQuotePayload({
      action: generalSaveActionName,
    });
    const generalHealthTrace = traceGeneralActionHealth(
      generalSaveActionName,
      liveGeneralActionPayload
    );
    if (
      generalHealthTrace &&
      (generalHealthTrace.customerNameVisible || generalHealthTrace.customerNameInPayload) &&
      String(generalHealthTrace.customerNameVisible || "").trim() !==
        String(generalHealthTrace.customerNameInPayload || "").trim()
    ) {
      devWarn("GENERAL_SAVE_PAYLOAD_MISMATCH", {
        type: "customerName",
        visible: generalHealthTrace.customerNameVisible,
        payload: generalHealthTrace.customerNameInPayload,
        quoteNo: generalHealthTrace.quoteNo,
      });
    }
    if (
      generalHealthTrace &&
      (generalHealthTrace.itemCountVisible !== generalHealthTrace.itemCountInPayload ||
        generalHealthTrace.imageCountVisible !== generalHealthTrace.imageCountInPayload)
    ) {
      devWarn("GENERAL_SAVE_PAYLOAD_MISMATCH", {
        type: "counts",
        quoteNo: generalHealthTrace.quoteNo,
        itemCountVisible: generalHealthTrace.itemCountVisible,
        itemCountInPayload: generalHealthTrace.itemCountInPayload,
        imageCountVisible: generalHealthTrace.imageCountVisible,
        imageCountInPayload: generalHealthTrace.imageCountInPayload,
      });
    }
    traceLiveActionPayload(liveGeneralActionPayload);
    guardStaleActionPayload(
      liveGeneralActionPayload,
      liveGeneralActionPayload.summaryTotals
    );

    const quoteStateSnapshot = liveGeneralActionPayload.quote;
    const { cleanedQuote, changed, metadataChanged } = await cleanupExpiredGeneralQuoteImages(
      quoteStateSnapshot
    );
    const normalized = normalizeGeneralQuote(cleanedQuote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });

    const normalizedGeneralQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        generalQuote: normalized,
      },
      todayISO()
    );
    const normalizedWithDate = {
      ...normalized,
      quoteDate: normalizedGeneralQuoteDate,
      date: normalizedGeneralQuoteDate,
      quotationDate: normalizedGeneralQuoteDate,
    };
    const totals = calculateGeneralQuoteTotals(normalizedWithDate);
    const existingId = isEditingExisting ? effectiveGeneralEditingQuoteId : "";
    const loadedGeneralItemCount = Math.max(
      getGeneralQuoteAnyAliasItemCount(selectedQuote?.generalQuote || {}),
      getGeneralQuoteAnyAliasItemCount(selectedQuote?.generalQuoteData || {}),
      getGeneralQuoteAnyAliasItemCount(selectedQuote || {})
    );
    const liveGeneralItemCount = getGeneralActionItems(normalizedWithDate).length;
    if (!asNew && existingId && loadedGeneralItemCount > 0 && liveGeneralItemCount === 0) {
      devError("GENERAL_SAVE_BLOCKED_UNSAFE_EMPTY_ITEMS", {
        quoteNo: normalizedWithDate.quoteNo || selectedQuote?.quoteNo || "",
        quoteId: existingId,
        loadedCount: loadedGeneralItemCount,
        liveCount: liveGeneralItemCount,
        deletedIds: uniqueValues(deletedGeneralItemIdsRef.current || []),
        source: liveGeneralActionPayload.saveSource || "",
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSaveError: {
          code: "unsafe-empty-items",
          message: "Save blocked because all items would be removed.",
          path: `users/${workspaceUid}/quotations/${existingId}`,
          at: new Date().toISOString(),
        },
      }));
      setAutoSaveStatus("error");
      notify(
        "Save blocked because all items would be removed. Please refresh and try again.",
        "error",
        7000
      );
      return;
    }
    const nextQuoteNo =
      !asNew && existingId
        ? normalizedWithDate.quoteNo || selectedQuote?.quoteNo || ""
        : await generateQuoteNumber(workspaceUid, "general");

    const nextQuote = {
      ...normalizedWithDate,
      quoteNo: nextQuoteNo,
      currency: normalizedWithDate.currency || currency,
    };

    if (changed || metadataChanged || nextQuote.quoteNo !== quoteStateSnapshot.quoteNo) {
      generalQuoteRef.current = nextQuote;
      setGeneralQuote(nextQuote);
    }

    const quoteDocId = !asNew && existingId ? existingId : nextQuoteNo;
    const generalSourceIdentity = resolveQuoteIdentityParts(
      {
        ...selectedQuote,
        ...nextQuote,
      },
      {
        customerName,
        quoteNo: nextQuoteNo,
      }
    );
    const customerLabel =
      generalSourceIdentity.customerName ||
      nextQuote.title ||
      "General Quotation";
    const selectedGeneralCustomerId =
      nextQuote.customerId ||
      (isEditingExisting
        ? selectedQuote?.customerId || selectedQuote?.crm?.customerId || ""
        : "");
    const customerLedger = await ensureCustomerLedgerRecord(
      getCustomerIdentityFromQuote({
        ...selectedQuote,
        id: quoteDocId,
        quoteType: GENERAL_QUOTE_TYPE,
        customerId: selectedGeneralCustomerId,
        customerName: customerLabel,
        customerPhone: nextQuote.customerPhone || nextQuote.quoteFor?.phone || "",
        customerWhatsapp: nextQuote.customerWhatsapp || "",
        customerEmail: nextQuote.customerEmail || nextQuote.quoteFor?.email || "",
        customerAddress: nextQuote.customerAddress || nextQuote.quoteFor?.address || "",
        contactPersonName:
          generalSourceIdentity.contactPersonName || nextQuote.contactPersonName || "",
        contactPersonPhone:
          generalSourceIdentity.contactPersonPhone || nextQuote.contactPersonPhone || "",
        customerCompanyName:
          nextQuote.customerCompanyName ||
          nextQuote.quoteFor?.company ||
          customerLabel ||
          "",
        generalQuote: nextQuote,
      }),
      {
        customerId: selectedGeneralCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: nextQuoteNo,
          quoteType: GENERAL_QUOTE_TYPE,
          quoteDate: normalizedGeneralQuoteDate,
          customerId: selectedGeneralCustomerId,
          customerName: customerLabel,
          grandTotal: Number(totals.grandTotal || 0),
          status:
            !asNew && existingId
              ? selectedQuoteStatus || "draft"
              : "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      customerId: selectedGeneralCustomerId,
      customerName: customerLabel,
      customerPhone: nextQuote.customerPhone || nextQuote.quoteFor?.phone || "",
      customerWhatsapp: nextQuote.customerWhatsapp || "",
      customerEmail: nextQuote.customerEmail || nextQuote.quoteFor?.email || "",
      customerAddress: nextQuote.customerAddress || nextQuote.quoteFor?.address || "",
      contactPersonName:
        generalSourceIdentity.contactPersonName || nextQuote.contactPersonName || "",
      contactPersonPhone:
        generalSourceIdentity.contactPersonPhone || nextQuote.contactPersonPhone || "",
      customerCompanyName:
        nextQuote.customerCompanyName ||
        nextQuote.quoteFor?.company ||
        customerLabel ||
        "",
    });
    const quoteSiteFields = buildQuoteSiteFieldsForSave(
      {
        ...quoteSiteSelection,
        ...selectedQuote,
        ...nextQuote,
      },
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        quoteNo: nextQuoteNo,
        ensureSiteId: true,
      }
    );
    const generalIdentityFields = buildQuoteIdentityFieldsForSave(
      {
        ...selectedQuote,
        ...nextQuote,
        ...customerFields,
        ...quoteSiteFields,
        quoteNo: nextQuoteNo,
        quoteFor: {
          ...(nextQuote.quoteFor || {}),
          name: customerFields.customerName,
          company:
            customerFields.customerCompanyName || customerFields.customerName || "",
        },
      },
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerEmail: customerFields.customerEmail,
        quoteNo: nextQuoteNo,
      }
    );
    const generalReferralPartnerFields = buildReferralPartnerQuoteFields(
      nextQuote.referralPartner ||
        selectedReferralPartnerRef.current ||
        normalizeQuoteReferralPartner(selectedQuote || {})
    );
    let linkedGeneralQuote = {
      ...nextQuote,
      ...customerFields,
      ...quoteSiteFields,
      ...generalIdentityFields,
      ...generalReferralPartnerFields,
      subtotal: Number(totals.subtotal || 0),
      subtotalExcludingGst: Number(totals.subtotalExcludingGst || 0),
      subtotalBeforeGst: Number(totals.subtotalBeforeGst || totals.subtotalExcludingGst || 0),
      taxableAmount: Number(totals.taxableAmount || 0),
      totalGst: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalGstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      gstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalAfterGst: Number(totals.totalAfterGst || 0),
      discountApplication: "after_gst",
      discountType: totals.discountType || nextQuote.discountType || "flat",
      discountValue: String(totals.discountValue ?? nextQuote.discountValue ?? 0),
      discountAmount: Number(totals.discountAmount || 0),
      grandTotal: Number(totals.grandTotal || 0),
      finalTotal: Number(totals.grandTotal || 0),
      totalInWords: totals.totalInWords || "",
      quoteFor: {
        ...(nextQuote.quoteFor || {}),
        name: customerFields.customerName || nextQuote.quoteFor?.name || "",
        company:
          customerFields.customerCompanyName || customerFields.customerName || "",
        phone: nextQuote.quoteFor?.phone || customerFields.customerPhone || "",
        email: nextQuote.quoteFor?.email || customerFields.customerEmail || "",
        address:
          nextQuote.quoteFor?.address || customerFields.customerAddress || "",
      },
    };
    const liveGeneralItemsForSave = getGeneralActionItems(linkedGeneralQuote).map((item, index) => ({
      ...item,
      id: item.id || item.itemId || `general-item-${index + 1}`,
    }));
    const generalRowValidationDetail = validateGeneralQuoteRowsForSave({
      items: liveGeneralItemsForSave,
      quoteNo: nextQuoteNo,
      quoteId: quoteDocId,
      saveMode: asNew ? "save_as_new" : isEditingExisting ? "save_existing" : "create_new",
    });
    if (generalRowValidationDetail) {
      handleQuoteRowValidationFailure(generalRowValidationDetail, {
        source: "saveGeneralQuotation",
      });
      return;
    }
    const contentUpdatedAtIso = new Date().toISOString();
    linkedGeneralQuote = {
      ...linkedGeneralQuote,
      lineItems: liveGeneralItemsForSave,
      items: liveGeneralItemsForSave,
      rows: liveGeneralItemsForSave,
      generalRows: liveGeneralItemsForSave,
      quoteRows: liveGeneralItemsForSave,
      generalItems: liveGeneralItemsForSave,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      contentUpdatedAt: contentUpdatedAtIso,
    };
    let savedGeneralQuoteForState = linkedGeneralQuote;
    const linkedLeadId =
      pendingCrmLeadId || (!asNew ? selectedQuote?.crm?.leadId || "" : "");
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const initialGeneralStage =
      !asNew && existingId
        ? selectedQuoteWorkStage || "quote_given"
        : linkedLead?.stageId || getQuoteGivenStageIdForType(GENERAL_QUOTE_TYPE);
    const initialGeneralStatus =
      !asNew && existingId
        ? selectedQuoteStatus || "draft"
        : linkedLead?.status || "draft";

    const rawPayload = {
      schemaVersion: QUOTE_STORAGE_SCHEMA_VERSION,
      quoteType: GENERAL_QUOTE_TYPE,
      normalizedQuoteType: CANONICAL_GENERAL_QUOTE_TYPE,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      contentUpdatedAt: contentUpdatedAtIso,
      ...customerFields,
      quoteNo: nextQuoteNo,
      quoteDate: normalizedGeneralQuoteDate,
      date: normalizedGeneralQuoteDate,
      quotationDate: normalizedGeneralQuoteDate,
      ...quoteSiteFields,
      ...generalIdentityFields,
      ...generalReferralPartnerFields,
      quoteFor: linkedGeneralQuote.quoteFor,
      items: liveGeneralItemsForSave,
      subtotal: Number(totals.subtotal || 0),
      subtotalExcludingGst: Number(totals.subtotalExcludingGst || 0),
      subtotalBeforeGst: Number(totals.subtotalBeforeGst || totals.subtotalExcludingGst || 0),
      taxableAmount: Number(totals.taxableAmount || 0),
      totalGst: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalGstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      gstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalAfterGst: Number(totals.totalAfterGst || 0),
      grandTotal: Number(totals.grandTotal || 0),
      finalTotal: Number(totals.grandTotal || 0),
      totalInWords: totals.totalInWords || "",
      deleted: false,
      discountType: totals.discountType || nextQuote.discountType || "flat",
      discountValue: Number(totals.discountValue ?? nextQuote.discountValue ?? 0),
      discountAmount: Number(totals.discountAmount || 0),
      discountApplication: "after_gst",
      summaryDiscountMode: getSummaryDiscountModeFromType(
        totals.discountType || nextQuote.discountType
      ),
      summaryDiscountValue: Number(totals.discountValue ?? nextQuote.discountValue ?? 0),
      status: initialGeneralStatus,
      workStage: initialGeneralStage,
      installationDate:
        !asNew && existingId
          ? selectedInstallationDate || null
          : linkedLead?.installationDate || null,
    };
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...selectedQuote,
        ...rawPayload,
      },
      {
        stageId: rawPayload.workStage,
        status: rawPayload.status,
        installationDate: rawPayload.installationDate,
        expectedValue: Number(totals.grandTotal || 0),
        finalValue: Number(totals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contactPersonName: generalIdentityFields.contactPersonName,
        contactPersonPhone: generalIdentityFields.contactPersonPhone,
        contact: {
          name:
            generalIdentityFields.contactPersonName ||
            customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchIndex = buildSafeQuoteSearchIndex({
      id: quoteDocId,
      ...rawPayload,
      crm: crmPayload,
    });
    const payload = sanitizeGeneralQuotePayload({
      ...rawPayload,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields({
        id: quoteDocId,
        ...rawPayload,
        crm: crmPayload,
      }),
    });
    payload.updatedAt = serverTimestamp();
    payload.contentUpdatedAt = serverTimestamp();
    traceQuoteIdentity("generalSavePayloadBuild", payload, {
      quoteId: quoteDocId,
      quoteNo: nextQuoteNo,
      quoteIndexCustomerName: customerFields.customerName,
      quoteIndexSiteName: quoteSiteFields.siteName,
    });
    debugQuoteId = quoteDocId;
    debugPayload = payload;
    const payloadValidation = validateGeneralQuotePayloadForFirestore(payload);
    if (!payloadValidation.valid) {
      devError("GENERAL QUOTE PAYLOAD INVALID", {
        invalidPaths: payloadValidation.invalidPaths,
        diagnostics: payloadValidation.diagnostics,
        workspaceUid,
        uid: auth.currentUser?.uid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        payloadPreview: safeDebugPreview(payload),
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSaveError: {
          code: "invalid-payload",
          message: "General quote payload contains invalid Firestore values.",
          invalidPaths: payloadValidation.invalidPaths,
          path: `users/${workspaceUid}/quotations/${quoteDocId}`,
          at: new Date().toISOString(),
        },
        lastSave: {
          ...(prev.lastSave || {}),
          state: "invalid-payload",
        },
      }));
      throw new Error("General quote payload contains invalid Firestore values.");
    }

    if (isDevelopmentMode()) {
      devLog("QUOTE DISCOUNT SAVE/LOAD", {
        source: "general_quote_save",
        discountType: payload.discountType,
        discountValue: payload.discountValue,
      });
      devLog("GENERAL QUOTE SAVE PAYLOAD READY", {
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        payloadKeys: Object.keys(payload || {}),
        searchTextLength: String(payload.searchText || "").length,
        searchKeywordsCount: Array.isArray(payload.searchKeywords)
          ? payload.searchKeywords.length
          : 0,
        payloadPreview: safeDebugPreview(payload),
      });
    }
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      lastSaveError: null,
      lastSave: {
        state: "ready",
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        docPath: `users/${workspaceUid}/quotations/${quoteDocId}`,
        payloadKeys: Object.keys(payload || {}),
        payloadSizeBytes: estimateJsonSizeBytes(payload),
        diagnostics: inspectGeneralQuotePayload(payload),
        searchTextLength: String(payload.searchText || "").length,
        searchKeywordsCount: Array.isArray(payload.searchKeywords)
          ? payload.searchKeywords.length
          : 0,
        discountType: payload.discountType,
        discountValue: payload.discountValue,
        itemCount: extractGeneralQuoteItems(payload).length,
        imageMetadataCount: extractGeneralQuoteItems(payload).reduce(
          (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
          0
        ),
      },
    }));

    const quoteRef = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );

    const payloadDiagnostics = inspectGeneralQuotePayload(payload);
    devLog("REAL GENERAL QUOTE SAVE START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      quoteId: quoteDocId,
      quoteNo: nextQuoteNo,
      isExistingQuote: !asNew && Boolean(existingId),
      docPath: quoteRef.path,
      payloadKeys: Object.keys(payload || {}),
      payloadSizeEstimate: JSON.stringify(safeDebugPreview(payload || {})).length,
      itemCount: extractGeneralQuoteItems(payload).length,
      imageCount: countGeneralQuoteImages(payload),
      payloadSizeBytes: estimateJsonSizeBytes(payload),
      hasInvalidUndefinedFields: payloadDiagnostics.hasUndefinedFields,
      hasFileBlobObjects: payloadDiagnostics.hasFileBlobObjects,
      hasInvalidNumbers: payloadDiagnostics.hasInvalidNumbers,
      searchTextLength: String(payload.searchText || "").length,
      searchKeywordsCount: Array.isArray(payload.searchKeywords)
        ? payload.searchKeywords.length
        : 0,
      discountType: payload.discountType || payload?.generalQuote?.discountType,
      discountValue:
        payload.discountValue || payload?.generalQuote?.discountValue,
      duplicateArrayFields: getGeneralQuoteDuplicateArrayFields(payload),
    });
    if (isDevelopmentMode()) {
      const deletedLocalIds = uniqueValues(deletedGeneralItemIdsRef.current || []);
      devLog("QUOTE_SAVE_CONTENT_TRACE", {
        quoteType: GENERAL_QUOTE_TYPE,
        quoteNo: nextQuoteNo,
        quoteId: quoteDocId,
        action: asNew ? "save_as_new" : "save",
        liveRowCount: 0,
        liveItemCount: liveGeneralItemsForSave.length,
        payloadItemsCount: Array.isArray(payload.items) ? payload.items.length : 0,
        payloadRowsCount: Array.isArray(payload.rows) ? payload.rows.length : 0,
        loadedSnapshotItemsCount:
          selectedQuote?.generalQuote?.lineItems?.length ||
          selectedQuote?.generalQuote?.items?.length ||
          selectedQuote?.items?.length ||
          0,
        loadedSnapshotRowsCount: Array.isArray(selectedQuote?.rows)
          ? selectedQuote.rows.length
          : 0,
        saveSource: liveGeneralActionPayload.saveSource || "",
        liveVisibleIds: getGeneralActionItems(generalQuote || {}).map(
          (item) => item?.id || item?.itemId || ""
        ),
        liveRefIds: getGeneralActionItems(generalQuoteRef.current || {}).map(
          (item) => item?.id || item?.itemId || ""
        ),
        staleLoadedSnapshotIds: getGeneralActionItems(
          selectedQuote?.generalQuote || selectedQuote?.generalQuoteData || selectedQuote || {}
        ).map((item) => item?.id || item?.itemId || ""),
        deletedLocalIds,
        payloadItemIds: liveGeneralItemsForSave.map((item) => item.id || item.itemId || ""),
        payloadRowIds: Array.isArray(payload.rows)
          ? payload.rows.map((item) => item.id || item.itemId || "")
          : [],
        writePath: quoteRef.path,
        mergeUsed: !asNew && Boolean(existingId),
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
      });
    }

    markQuoteSaveInProgressSession({
      traceId,
      action: asNew ? "save_as_new" : "save",
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: nextQuoteNo,
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: quoteRef.path,
    });
    recordSaveTracePatch(traceId, {
      quoteNo: sanitizeSaveTraceText(nextQuoteNo || "", 80),
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: quoteRef.path,
      customerName: sanitizeSaveTraceText(customerFields.customerName || customerLabel || "", 160),
      siteName: sanitizeSaveTraceText(quoteSiteFields.siteName || "", 160),
      requiredWrites: {
        mainQuote: "not_attempted",
        quoteIndex: "not_attempted",
      },
    });

    try {
      if (!asNew && existingId) {
        await writeQuotationAndQuoteIndex({
          quoteRef,
          quoteId: quoteDocId,
          quoteIndexId: quoteDocId,
          quotePayload: payload,
          quoteIndexSource: {
            id: quoteDocId,
            ...payload,
            sourceQuoteId: quoteDocId,
          },
          create: false,
          traceContext: {
            traceId,
            quoteType: GENERAL_QUOTE_TYPE,
            quoteNo: nextQuoteNo,
            quoteId: quoteDocId,
            sourceQuoteId: quoteDocId,
            quoteIndexId: quoteDocId,
            sourcePath: quoteRef.path,
            resolvedQuotePath: quoteRef.path,
            existingContentCount: loadedGeneralItemCount,
          },
        });
      } else {
        await persistNewQuotationWithTrialGuard({
          quoteId: quoteDocId,
          quotePayload: payload,
          mode: asNew ? "save_as_new" : "create_new",
          quoteIndexSource: {
            id: quoteDocId,
            ...payload,
            sourceQuoteId: quoteDocId,
          },
          traceId,
        });
      }

      requiredWritesSucceeded = true;
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "success",
          quoteIndex: "success",
        },
      });
      traceReferralPartner("save_general", {
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        partner: generalReferralPartnerFields.referralPartner,
        savedToQuote: true,
        savedToQuoteIndex: true,
        hiddenFromCustomerPdf: true,
      });
      recordSaveTraceQuoteIndexSnapshot(traceId, quoteDocId, {
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        customerName: customerFields.customerName || customerLabel || "",
        siteName: quoteSiteFields.siteName || "",
        quoteType: GENERAL_QUOTE_TYPE,
        quotationType: GENERAL_QUOTE_TYPE,
        sourcePath: quoteRef.path,
      });
      logRequiredSaveCompletedBeforeUiPatch({
        quoteNo: nextQuoteNo,
        quoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        sourcePath: quoteRef.path,
      });
      await logQuoteSavedActivity({
        activityType: !asNew && existingId ? "quote_updated" : "quote_created",
        quoteType: GENERAL_QUOTE_TYPE,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        customerId: customerFields.customerId || payload.customerId || "",
        customerName: customerFields.customerName || customerLabel || payload.customerName || "",
        opportunityId:
          payload.opportunityId ||
          payload.crmOpportunityId ||
          payload.crm?.opportunityId ||
          payload.crm?.leadId ||
          pendingCrmLeadId ||
          "",
        totalAmount: Number(payload.grandTotal || payload.finalTotal || 0),
      }).catch(() => {});

      await upsertQuoteLocalRecord({
        id: quoteDocId,
        ...payload,
        createdAt:
          !asNew && existingId
            ? selectedQuote?.createdAt || new Date()
            : new Date(),
        updatedAt: new Date(),
      }, { persistIndex: false, traceId, syncDerived: false });
      setEditingQuoteId(null);
      setGeneralEditingQuoteId(quoteDocId);
      setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
      setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
      setIsNewQuote(false);
      setAutoSaveStatus("saved");

      devLog("GENERAL QUOTE FIRESTORE WRITE SUCCESS", {
        path: quoteRef.path,
      });

      try {
        await withQuoteSaveTimeout(
          persistQuoteSiteForCustomer(
            customerFields.customerId,
            customerFields,
            quoteSiteFields
          ),
          { step: "optional.general.siteDoc", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            siteDoc: "success",
          },
        });
      } catch (siteError) {
        optionalWriteFailed = true;
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            siteDoc: "failed",
          },
        });
        appendSaveTraceError(traceId, "siteDoc", siteError, {
          path:
            workspaceUid && customerFields.customerId
              ? `users/${workspaceUid}/customers/${customerFields.customerId}/sites`
              : "",
        });
        devWarn("QUOTE_SITE_OPTIONAL_WRITE_SKIPPED", {
          quoteType: GENERAL_QUOTE_TYPE,
          quoteId: quoteDocId,
          customerId: customerFields.customerId,
          errorCode: siteError?.code || "",
          errorMessage: siteError?.message || String(siteError),
        });
        notify(
          "Quotation saved, but the customer site library could not be updated.",
          "warning",
          5200
        );
      }

      if (hasGeneralQuoteDraftImagePaths(savedGeneralQuoteForState, workspaceUid)) {
        try {
          devLog("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_START", {
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
          });
          const canonicalizeCallable = httpsCallable(
            functions,
            "canonicalizeGeneralQuoteImagePaths"
          );
          const canonicalizeResult = await canonicalizeCallable({
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
            storageBucket: firebaseApp.options.storageBucket || "",
          });
          const canonicalizeData = canonicalizeResult?.data || {};
          devLog("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_RESULT", {
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
            changed: canonicalizeData.changed,
            copiedImages: canonicalizeData.copiedImages || [],
            failedImages: canonicalizeData.failedImages || [],
          });
          if (canonicalizeData.changed && canonicalizeData.generalQuote) {
            savedGeneralQuoteForState = normalizeGeneralQuote(
              canonicalizeData.generalQuote,
              {
                shopAddress,
                currency,
                businessName,
                businessPhone,
                businessEmail,
              }
            );
            payload.items = extractGeneralQuoteItems(savedGeneralQuoteForState).map(
              sanitizeGeneralQuoteItemForFirestore
            );
            payload.itemCount = payload.items.length;
            payload.contentItemCount = payload.items.length;
          }
          if (Array.isArray(canonicalizeData.failedImages) && canonicalizeData.failedImages.length) {
            notify(
              "Quotation saved, but one draft image could not be moved to the permanent folder.",
              "warning",
              7000
            );
          }
        } catch (canonicalizeError) {
          devError("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_FAILED", {
            code: canonicalizeError?.code,
            message: canonicalizeError?.message,
            stack: canonicalizeError?.stack,
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
          });
          notify(
            "Quotation saved, but image path cleanup could not finish. You can still repair images from the quote.",
            "warning",
            7000
          );
        }
      }

      {
        const deletedIdsForVerification = uniqueValues(deletedGeneralItemIdsRef.current || []);
        const shouldVerifyContentSave =
          deletedIdsForVerification.length > 0 || isDevelopmentMode();
        if (shouldVerifyContentSave) {
        const savedSnap = await getDoc(quoteRef, { label: "generalQuoteSaveReadback" });
        const savedData = savedSnap.exists() ? savedSnap.data() : null;
        const savedEmbedded =
          savedData?.generalQuote && typeof savedData.generalQuote === "object"
            ? savedData.generalQuote
            : savedData?.generalQuoteData && typeof savedData.generalQuoteData === "object"
            ? savedData.generalQuoteData
            : {};
        const savedContentSelection = resolveGeneralQuoteContentSelection(
          savedData || {},
          savedEmbedded
        );
        const savedItems = savedContentSelection.items;
        const indexSnap = await getDoc(
          doc(db, "users", workspaceUid, "quote_index", quoteDocId),
          { label: "generalQuoteIndexSaveReadback" }
        );
        const indexData = indexSnap.exists() ? indexSnap.data() : null;
        const readBackGrandTotal = Number(
          savedData?.grandTotal ??
            savedData?.finalTotal ??
            savedData?.total ??
            savedData?.totalAmount ??
            0
        );
        const quoteIndexGrandTotal = Number(
          indexData?.grandTotal ?? indexData?.totalAmount ?? indexData?.total ?? 0
        );
        const liveGrandTotal = Number(totals.grandTotal || 0);
        const failures = [];
        if (!savedSnap.exists()) failures.push("full_quote_missing");
        if (!indexSnap.exists()) failures.push("quote_index_missing");
        if (savedItems.length !== liveGeneralItemsForSave.length) {
          failures.push("full_quote_item_count_mismatch");
        }
        if (Number(indexData?.itemCount ?? indexData?.contentItemCount ?? 0) !== liveGeneralItemsForSave.length) {
          failures.push("quote_index_item_count_mismatch");
        }
        if (Math.abs(readBackGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("full_quote_total_mismatch");
        }
        if (Math.abs(quoteIndexGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("quote_index_total_mismatch");
        }
        const contentInspection = inspectDeletedIdsInContentAliases(
          getGeneralQuoteContentAliasArrays(savedData || {}),
          deletedIdsForVerification,
          (item) => item?.id || item?.itemId || ""
        );
        const deletedIdsStillPresent = contentInspection.deletedIdsStillPresent;

        devLog("GENERAL QUOTE READBACK", {
          exists: savedSnap.exists(),
          dataKeys: savedData ? Object.keys(savedData) : [],
          itemCount: savedItems.length,
          imageMetadataCount: savedItems.reduce(
            (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
            0
          ),
        });
        devLog("QUOTE_SAVE_VERIFY_READBACK", {
          quoteType: GENERAL_QUOTE_TYPE,
          quoteId: quoteDocId,
          quoteNo: nextQuoteNo,
          liveCount: liveGeneralItemsForSave.length,
          readBackSelectedField: savedContentSelection.field || "empty",
          readBackSelectedCount: savedItems.length,
          quoteIndexCount: Number(indexData?.itemCount ?? indexData?.contentItemCount ?? 0),
          liveGrandTotal,
          readBackGrandTotal,
          quoteIndexGrandTotal,
          failures,
          deletedIds: deletedIdsForVerification,
          canonicalField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
          canonicalIds: getContentAliasIds(savedItems, (item) => item?.id || item?.itemId || ""),
          aliasFindings: contentInspection.aliasFindings,
          aliasCounts: contentInspection.aliasCounts,
          savedPayloadItemCount: liveGeneralItemsForSave.length,
          savedPayloadRowCount: Array.isArray(payload.rows) ? payload.rows.length : 0,
          readBackItemCount: savedItems.length,
          readBackRowCount: Array.isArray(savedData?.rows) ? savedData.rows.length : 0,
          deletedIdsStillPresent,
          pass: contentInspection.pass && failures.length === 0,
          contentSchemaVersion: savedData?.contentSchemaVersion || "",
          canonicalContentField: savedData?.canonicalContentField || "",
        });
        if (!contentInspection.pass) {
          throw new Error("General quote save verification failed: deleted item still present.");
        }
        if (failures.length > 0) {
          throw new Error(`General quote save verification failed: ${failures.join(", ")}`);
        }

        setGeneralQuoteDebugState((prev) => ({
          ...prev,
          lastSaveError: null,
          lastSave: {
            ...(prev.lastSave || {}),
            state: "saved",
            readbackExists: savedSnap.exists(),
            readbackKeys: savedData ? Object.keys(savedData) : [],
            readbackItemCount: savedItems.length,
            savedAt: new Date().toISOString(),
          },
        }));
        } else {
        setGeneralQuoteDebugState((prev) => ({
          ...prev,
          lastSaveError: null,
          lastSave: {
            ...(prev.lastSave || {}),
            state: "saved",
            savedAt: new Date().toISOString(),
          },
        }));
      }
      }
      notify("Quotation saved.", "success");
      deletedGeneralItemIdsRef.current = [];
    } catch (error) {
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "failed",
          quoteIndex: "failed",
        },
        finalResult: "required_write_failed",
        userMessageShown: "Failed to save quotation",
      });
      appendSaveTraceError(traceId, "required_quote_or_index_write", error, {
        path: `${quoteRef.path} + users/${workspaceUid}/quote_index/${quoteDocId}`,
        payloadKeys: Object.keys(payload || {}),
      });
      clearQuoteSaveInProgressSession({
        status: "failed",
        reason: "required_write_failed",
        backendVerified: false,
        traceId,
        quoteNo: nextQuoteNo,
        quoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        sourcePath: quoteRef.path,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      });
      devError("REAL GENERAL QUOTE SAVE FAILED EXACT", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        workspaceUid,
        uid: auth.currentUser?.uid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        docPath: quoteRef.path,
        payloadPreview: safeDebugPreview(payload),
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSaveError: {
          code: error?.code || "",
          message: error?.message || String(error),
          stack: error?.stack || "",
          path: quoteRef.path,
          at: new Date().toISOString(),
        },
        lastSave: {
          ...(prev.lastSave || {}),
          state: "failed",
        },
      }));
      throw error;
    }

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      ...payload,
      createdAt:
        !asNew && existingId
          ? selectedQuote?.createdAt || new Date()
          : new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false, traceId });

    let crmDealLinkResult = null;
    let crmSyncFailed = false;
    if (linkedLeadId) {
      try {
        await withQuoteSaveTimeout(
          saveCrmFieldsForLead(
            linkedLeadId,
            {
            quoteType: GENERAL_QUOTE_TYPE,
            stageId: payload.workStage,
            status: payload.status,
            linkedQuotes: [
              {
                quoteId: quoteDocId,
                quoteNo: nextQuoteNo,
                type: GENERAL_QUOTE_TYPE,
                value: Number(totals.grandTotal || 0),
                linkedQuoteDate: normalizedGeneralQuoteDate,
                isPrimary: true,
              },
            ],
            hasQuote: true,
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            expectedValue: Number(totals.grandTotal || 0),
            finalValue: Number(totals.grandTotal || 0),
            installationDate: payload.installationDate || null,
            },
            { syncQuote: false }
          ),
          { step: "optional.general.crmCustomer", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        setPendingCrmLeadId("");
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "success",
          },
        });
      } catch (crmCustomerError) {
        crmSyncFailed = true;
        optionalWriteFailed = true;
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "failed",
          },
        });
        appendSaveTraceError(traceId, "general_crm_customer", crmCustomerError, {
          path: `users/${workspaceUid}/crm_leads/${linkedLeadId}`,
        });
        devError("GENERAL QUOTE CRM CUSTOMER SYNC FAILED AFTER QUOTE SAVE", {
          code: crmCustomerError?.code,
          message: crmCustomerError?.message,
          stack: crmCustomerError?.stack,
          workspaceUid,
          quoteId: quoteDocId,
          quoteNo: nextQuoteNo,
          linkedLeadId,
        });
      }
    } else {
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmCustomer: "skipped",
        },
      });
    }

    try {
      crmDealLinkResult = await withQuoteSaveTimeout(
        ensureSavedQuoteCrmDeal(
          {
          id: quoteDocId,
          ...payload,
          crm: crmPayload,
          ...customerFields,
          },
          {
          quoteNo: nextQuoteNo,
          quoteType: GENERAL_QUOTE_TYPE,
          totalValue: Number(totals.grandTotal || 0),
          customerFields,
          linkedLeadId,
          saveAsNew: asNew,
          previousSaveQuoteOnly: selectedQuote?.crm?.saveQuoteOnly === true,
          showSaveOutcomeToast: false,
          }
        ),
        { step: "optional.general.crmDealLink", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
      );
      const crmDealLinkStatus =
        crmDealLinkResult?.action === "skipped" ||
        crmDealLinkResult?.action === "saveOnly"
          ? "skipped"
          : "success";
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmDealLink: crmDealLinkStatus,
        },
      });
    } catch (crmError) {
      crmSyncFailed = true;
      optionalWriteFailed = true;
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmDealLink:
            linkedLeadId || crmSettings.autoCreateDealFromQuotation !== false
              ? "failed"
              : "skipped",
        },
      });
      appendSaveTraceError(traceId, "general_crm_deal_link", crmError, {
        path: `users/${workspaceUid}/crm_leads`,
      });
      devError("GENERAL QUOTE CRM SYNC FAILED AFTER QUOTE SAVE", {
        code: crmError?.code,
        message: crmError?.message,
        stack: crmError?.stack,
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
      });
      if (linkedLeadId) {
        notify(
          "Quotation saved. CRM link could not be updated.",
          "warning",
          5200
        );
      }
    }
    if (crmDealLinkResult?.crmPayload) {
      payload.crm = crmDealLinkResult.crmPayload;
      payload.workStage = crmDealLinkResult.stageId || payload.workStage;
      payload.status = crmDealLinkResult.status || payload.status;
      payload.installationDate =
        crmDealLinkResult.crmPayload.installationDate || payload.installationDate || null;
    }

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      ...payload,
      createdAt:
        !asNew && existingId
          ? selectedQuote?.createdAt || new Date()
          : new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false, traceId });
    setEditingQuoteId(null);
    setGeneralEditingQuoteId(quoteDocId);
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
    setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
    setSelectedQuote({
      ...payload,
      id: quoteDocId,
      quoteId: quoteDocId,
      sourceQuoteId: quoteDocId,
      fullQuoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      indexId: quoteDocId,
      sourceCollection: "quotations",
      sourcePath: quoteRef.path,
      quoteType: GENERAL_QUOTE_TYPE,
      quotationType: GENERAL_QUOTE_TYPE,
    });
    setQuoteSiteSelection(normalizeQuoteSiteSnapshot(quoteSiteFields));
    setSelectedQuoteStatus(payload.status);
    setSelectedQuoteWorkStage(payload.workStage);
    setSelectedInstallationDate(payload.installationDate);
    setQuoteSubTab(GENERAL_QUOTE_TYPE);
    setIsNewQuote(false);
    setTab("quote");
    generalQuoteRef.current = savedGeneralQuoteForState;
    setGeneralQuote(savedGeneralQuoteForState);
    markGeneralQuoteBaseline(savedGeneralQuoteForState, "saved");
    if (previousDraftKey) {
      clearLocalDraftRecord(previousDraftKey);
    }
    clearLocalDraftRecord(
      buildLocalDraftKey(
        workspaceUid,
        LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
        quoteDocId || "new"
      )
    );
    setAutoSaveStatus("saved");
    recordSaveTracePatch(traceId, {
      finalResult: optionalWriteFailed
        ? "optional_write_failed_but_saved"
        : "success",
      userMessageShown: optionalWriteFailed
        ? "Quotation saved, but one optional sync could not be updated."
        : "",
    });
    if (optionalWriteFailed) {
      notify(
        "Quotation saved successfully. Customer history could not be updated. Your quotation is safe. Use Update Customer History in Saved Quotes.",
        "warning",
        7000
      );
    }
    if (crmSyncFailed) {
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSave: {
          ...(prev.lastSave || {}),
          crmSync: "failed-after-quote-save",
        },
      }));
    }
  } catch (error) {
    devError("GENERAL QUOTE SAVE FAILED", {
      error,
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      quoteId: debugQuoteId,
      quoteNo: debugPayload?.quoteNo,
      payloadKeys: Object.keys(debugPayload || {}),
      payloadPreview: safeDebugPreview(debugPayload || {}),
    });
    if (requiredWritesSucceeded) {
      devWarn("GENERAL_QUOTE_POST_REQUIRED_SAVE_OPTIONAL_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        quoteId: debugQuoteId,
      });
      setAutoSaveStatus("saved");
      recordSaveTracePatch(traceId, {
        finalResult: "optional_write_failed_but_saved",
        userMessageShown: "",
      });
      appendSaveTraceError(traceId, "general_save_post_required_optional", error, {
        path: debugQuoteId ? `users/${workspaceUid}/quotations/${debugQuoteId}` : "",
        payloadKeys: Object.keys(debugPayload || {}),
      });
      return;
    }
    const rowValidationDetail = getQuoteRowValidationDetailFromError(error);
    if (rowValidationDetail) {
      handleQuoteRowValidationFailure(rowValidationDetail, {
        source: "saveGeneralQuotation.outerCatch",
      });
      recordSaveTracePatch(traceId, {
        finalResult: "required_write_failed",
        failedStep: error?.__fqRequiredSaveStep || "",
        userMessageShown: rowValidationDetail.message || "Quotation row data is invalid.",
      });
      appendSaveTraceError(traceId, "general_save_row_validation", error, {
        path: debugQuoteId
          ? `users/${workspaceUid}/quotations/${debugQuoteId}`
          : "",
        payloadKeys: Object.keys(debugPayload || {}),
      });
      return;
    }
    setAutoSaveStatus("error");
    const friendlyMessage = getSaveQuotationFriendlyError(error);
    recordSaveTracePatch(traceId, {
      finalResult: requiredWritesSucceeded
        ? "optional_write_failed_but_saved"
        : "required_write_failed",
      userMessageShown: isFirestoreResourceExhausted(error)
        ? "Firebase read limit reached. Please wait and try again."
        : friendlyMessage,
    });
    appendSaveTraceError(traceId, "general_save_outer_catch", error, {
      path: debugQuoteId
        ? `users/${workspaceUid}/quotations/${debugQuoteId}`
        : "",
      payloadKeys: Object.keys(debugPayload || {}),
    });
    alert(
      isFirestoreResourceExhausted(error)
        ? "Firebase read limit reached. Please wait and try again."
        : friendlyMessage
    );
  } finally {
    endGeneralActionLock(generalSaveActionName);
    clearQuoteSaveInProgressSession({
      traceId,
      action: asNew ? "save_as_new" : "save",
      quoteType: GENERAL_QUOTE_TYPE,
      reason: requiredWritesSucceeded ? "general_save_finished" : "general_save_finished_without_required_writes",
    });
    setIsSaving(false);
  }
};

/* --------------------------- SAVE QUOTATION (HARD UNIQUE  FIXED) --------------------------- */

/* --------------------------- SAVE QUOTATION (FINAL, HARD SAFE) --------------------------- */
/* --------------------------- SAVE QUOTATION (SINGLE SOURCE OF TRUTH) --------------------------- */
const saveQuotation = async (event) => {
  if (quoteSubTab === GENERAL_QUOTE_TYPE) {
    await saveGeneralQuotation({ asNew: false, event });
    return;
  }
  const eventGuard = applyQuoteActionEventGuard(event);
  if (!operationalQuoteSettingsReady()) return false;
  const traceId = startSaveTrace({
    action: "save",
    quoteType: CURTAIN_QUOTE_TYPE,
    eventGuard,
  });
  let requiredWritesSucceeded = false;
  let optionalWriteFailed = false;
  let debugCurtainSavePayload = null;
  let debugCurtainSaveContext = {};
  logQuoteSaveEventGuard({
    handler: "saveQuotation",
    traceId,
    event,
    eventGuard,
  });
  markQuoteSaveInProgressSession({
    traceId,
    action: "save",
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteId:
      curtainQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
        ? editingQuoteId || ""
        : "",
    quoteIndexId:
      curtainQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
        ? editingQuoteId || ""
        : "",
    quoteNo: quoteNo || selectedQuote?.quoteNo || "",
  });
  const effectiveCurtainEditingQuoteId =
    curtainQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
      ? editingQuoteId || ""
      : "";
  const isEditingExisting =
    curtainQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING &&
    Boolean(effectiveCurtainEditingQuoteId);
  const previousCurtainDraftKey = curtainQuoteDraftKey;

  if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  if (isEditingExisting ? !canEditQuotes : !canCreateQuotes) {
    alert(
      isEditingExisting
        ? "You do not have permission to edit quotations in this workspace."
        : "You do not have permission to create quotations in this workspace."
    );
    return;
  }

  if (isLoadingQuoteForEdit) {
    notify("Quotation is still loading. Please wait before saving.", "warning", 5000);
    return;
  }

  const blockedCurtainLoad = incompleteQuoteLoadRef.current;
  if (
    isEditingExisting &&
    blockedCurtainLoad &&
    [
      blockedCurtainLoad.quoteId,
      blockedCurtainLoad.sourceQuoteId,
      blockedCurtainLoad.indexId,
      blockedCurtainLoad.quoteIndex?.quoteId,
      blockedCurtainLoad.quoteIndex?.sourceQuoteId,
      blockedCurtainLoad.quoteIndex?.id,
    ]
      .filter(Boolean)
      .map(String)
      .includes(String(effectiveCurtainEditingQuoteId))
  ) {
    devError("CURTAIN_SAVE_BLOCKED_INCOMPLETE_QUOTE_LOAD", {
      quoteNo: blockedCurtainLoad.quoteNo || quoteNo || "",
      quoteId: effectiveCurtainEditingQuoteId,
      sourcePath: blockedCurtainLoad.resolvedMainQuotePath || blockedCurtainLoad.sourcePath || "",
      selectedLoadField: blockedCurtainLoad.selectedLoadField || "",
      indexGrandTotal: blockedCurtainLoad.indexGrandTotal || 0,
      indexContentCount: blockedCurtainLoad.indexContentCount || 0,
    });
    setAutoSaveStatus("error");
    notify(
      "This quotation was opened from an older record and cannot be safely overwritten. Please use Save As New.",
      "error",
      8000
    );
    return;
  }

  if (isSaving) return;

  setIsSaving(true);
  setAutoSaveStatus("saving");
  setQuoteRowValidationError(null);
  const saveStartedEditVersion = currentQuoteEditVersionRef.current;

  try {
    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: isEditingExisting ? "save" : "save",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryMeta,
      summaryTotals,
      currentCurtainDiscountFields,
      currentCurtainGstFields,
      customerName,
      quoteSiteSelection: liveQuoteSiteSelection,
      quoteNo,
      quoteDate: date,
      selectedQuote,
      selectedQuoteStatus,
      selectedQuoteWorkStage,
      selectedInstallationDate,
      pendingCrmLeadId,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      quoteFullnessOverrides: liveQuoteFullnessOverrides,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    } = liveActionPayload;
    const currentQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      liveQuoteFullnessOverrides || {}
    );
    let currentQuoteFullnessFields = {
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
      ...buildQuoteConstructionFields(rows),
    };
    setQuoteConstructionSettings(normalizeQuoteCalculationSettings(currentQuoteFullnessFields));
    const curtainReferralPartnerFields = buildReferralPartnerQuoteFields(
      liveActionPayload.referralPartner ||
        selectedReferralPartnerRef.current ||
        normalizeQuoteReferralPartner(selectedQuote || {})
    );

    if (!customerName?.trim()) {
      setAutoSaveStatus("idle");
      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser || !workspaceUid) {
      alert("Please sign in first.");
      setAutoSaveStatus("error");
      return;
    }

    await syncFabricCatalogueEntriesForQuoteRows(rows);

    const productionRowsForSave = rows.map((row, index) => {
      const productionCalc = getCurtainProductionAdjustedRowCalc(row, index);
      const rowForSaveBase = {
        ...row,
        ...getCurtainRowFabricCatalogueSavePatch(row),
      };
      delete rowForSaveBase.__forceCurrentCalculation;
      delete rowForSaveBase.__recalculated;
      const rowCalculationSnapshot =
        productionCalc.isCurtain || productionCalc.isRoman
          ? resolveCurtainRowSnapshotForSave(row, productionCalc)
          : null;
      const rowLiningForSave = normalizeLiningForSave(
        {
          ...row,
          lining: productionCalc.lining || row.lining,
        },
        row.productConfigSnapshot || null,
        productionCalc
      );
      return {
        ...rowForSaveBase,
        ...buildCurtainRowFinancialAliasesForSave(productionCalc),
        repeatSettings: normalizeCurtainRepeatEntry(
          repeatSettings?.[normalizeFabricKey(getCurtainRowFabricKey(row))] || {}
        ),
        lineType: productionCalc.lineType || row.lineType || (productionCalc.isAccessory ? "accessory" : "measurement"),
        calculationType: productionCalc.calculationType || row.calculationType || "",
        category: row.category || row.productConfigSnapshot?.category || "",
        calculationSnapshot: rowCalculationSnapshot,
        total: Number(productionCalc.rowTotal ?? productionCalc.total ?? 0),
        rowTotal: Number(productionCalc.rowTotal || 0),
        lineTotal: Number(productionCalc.lineTotal ?? productionCalc.rowTotal ?? 0),
        taxableAmount: Number(productionCalc.taxableAmount ?? productionCalc.rowTotal ?? 0),
        ...(!productionCalc.usesCalculationSnapshot || rowCalculationSnapshot?.calculationVersion === CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION
          ? { gstAmount: Number(productionCalc.gstAmount || 0), rowGst: Number(productionCalc.gstAmount || 0) } : {}),
        adjSqft: Number(productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0),
        billableSqft: Number(productionCalc.billableSqft ?? productionCalc.adjSqft ?? 0),
        ...(productionCalc.sqftCalculation
          ? {
              rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
              sqftCalculation: {
                ...productionCalc.sqftCalculation,
                rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
                billableSqft: Number(
                  productionCalc.sqftCalculation.billableSqft || 0
                ),
              },
            }
          : {}),
        lining: rowLiningForSave,
        liningEnabled: rowLiningForSave.enabled,
        liningType: rowLiningForSave.type,
        liningRate:
          rowLiningForSave.chargeMethod === "sqft"
            ? rowLiningForSave.ratePerSqft
            : rowLiningForSave.ratePerMeter,
        liningAmount: rowLiningForSave.amount,
        liningSqft: rowLiningForSave.sqft,
        liningMeters: rowLiningForSave.billableMeters,
        trackConfig: productionCalc.trackConfig || row.trackConfig || { ...DEFAULT_TRACK_CONFIG },
        bendAmount: Number(productionCalc.bendAmount || productionCalc.trackConfig?.bendAmount || 0),
        trackCost: Number(productionCalc.trackCost || 0),
        stitchingCost: Number(productionCalc.stitchingCost || 0),
        fabricGrossAmount: Number(productionCalc.fabricGrossAmount || 0),
        fabricDiscountPercent: Number(productionCalc.fabricDiscountPercent ?? row.discount ?? 0),
        fabricDiscountAmount: Number(productionCalc.fabricDiscountAmount || 0),
        fabricNetAmount: Number(productionCalc.fabricNetAmount ?? productionCalc.fabricCost ?? 0),
        discountScope: productionCalc.discountScope || getRowDiscountScope(row, row.productConfigSnapshot, {
          hasFabricMeterComponent: Boolean(productionCalc.isCurtain || productionCalc.isRoman),
          fabricGrossAmount: productionCalc.fabricGrossAmount || 0,
        }),
        discountBase: Number(productionCalc.discountBase || 0),
        discountAmount: Number(
          productionCalc.discountAmount || productionCalc.fabricDiscountAmount || 0
        ),
        baseLineAmountBeforeDiscount: Number(
          productionCalc.baseLineAmountBeforeDiscount ??
            productionCalc.rowTotal ??
            productionCalc.lineTotal ??
            0
        ),
        fabricCost: Number(productionCalc.fabricCost || 0),
        sqftAmount: Number(productionCalc.sqftAmount || productionCalc.productCharge || 0),
        rowSubtotalBeforeGst: Number(
          productionCalc.rowSubtotalBeforeGst ?? productionCalc.rowTotal ?? 0
        ),
        gstRate: resolveCurtainSavedRowGstRate(row),
        fabricMeters: productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        customerBillableFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        fullOrderedFabricMeters:
          productionCalc.fullOrderedFabricMeters ??
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        noReuseOrderedFabricMeters:
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.customerBillableFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        cutFabricMeters: productionCalc.cutFabricMeters ?? productionCalc.fabricMeters,
        workshopFabricRequiredMeters:
          productionCalc.workshopFabricRequiredMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        workshopNewFabricMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalNewFabricNeededMeters:
          productionCalc.internalNewFabricNeededMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        orderedFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        newFabricNeededMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalHalfPieceSavingMeters: productionCalc.internalHalfPieceSavingMeters || 0,
        internalHalfPieceSavingValue: productionCalc.internalHalfPieceSavingValue || 0,
        halfReusedMeters: productionCalc.halfReusedMeters || 0,
        internalHalfReusedMeters:
          productionCalc.internalHalfReusedMeters ?? productionCalc.halfReusedMeters ?? 0,
        halfPiecesSaved: productionCalc.halfPiecesSaved || 0,
        halfPiecesReused: productionCalc.halfPiecesReused || 0,
      };
    });
    const rowsForSave = sanitizeCurtainRowsEditableTextFields(
      attachProductSnapshotsToCurtainRows(productionRowsForSave)
    );
    currentQuoteFullnessFields = { ...currentQuoteFullnessFields,
      ...rebaseQuoteConstructionIdentities(currentQuoteFullnessFields, rows, rowsForSave) };
    const curtainRowValidationDetail = validateCurtainRowsForSave({
      rows: rowsForSave,
      quoteNo,
      quoteId: effectiveCurtainEditingQuoteId || "",
      saveMode: isEditingExisting ? "save_existing" : "create_new",
    });
    if (curtainRowValidationDetail) {
      handleQuoteRowValidationFailure(curtainRowValidationDetail, {
        source: "saveQuotation",
      });
      return;
    }
    debugCurtainSavePayload = {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      quoteId: effectiveCurtainEditingQuoteId || "",
      rows: rowsForSave,
    };
    debugCurtainSaveContext = {
      action: isEditingExisting ? "save_existing" : "save_create",
      quoteNo,
      quoteId: effectiveCurtainEditingQuoteId || "",
      quoteIndexId: effectiveCurtainEditingQuoteId || "",
      sourcePath: selectedQuote?.sourcePath || "",
      workspaceUid,
      userUid: currentUser.uid,
      editorMode: curtainQuoteEditorMode,
      writePath: workspaceUid
        ? `users/${workspaceUid}/quotations/${effectiveCurtainEditingQuoteId || "(new)"}`
        : "",
    };
    const loadedCurtainRowCount = Math.max(
      getCurtainQuoteAnyAliasRowCount(selectedQuote || {}),
      normalizeRowsForState(rowsRef.current || []).length
    );
    if (isEditingExisting && loadedCurtainRowCount > 0 && rowsForSave.length === 0) {
      devError("CURTAIN_SAVE_BLOCKED_UNSAFE_EMPTY_ROWS", {
        quoteNo,
        quoteId: effectiveCurtainEditingQuoteId,
        loadedCount: loadedCurtainRowCount,
        liveCount: rowsForSave.length,
        deletedIds: uniqueValues(deletedCurtainRowIdsRef.current || []),
        source: liveActionPayload.saveSource || "latestRef",
      });
      setAutoSaveStatus("error");
      notify(
        "Save blocked because all rows would be removed. Please refresh and try again.",
        "error",
        7000
      );
      return;
    }
    const normalizedFabricSummaryMeta = sanitizeFabricSummaryMetaForValidCatalogues(
      fabricSummaryMeta,
      repeatSettings,
      rowsForSave
    );
    const quoteCalculationSnapshot = buildCurtainQuoteCalculationSnapshot({
      rows,
      summaryTotals,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    });
    const quoteBlindSqftRoundingMode = normalizeBlindSqftRoundingMode(
      rowsForSave.find((row) => row?.sqftCalculation?.roundingMode)
        ?.sqftCalculation?.roundingMode ||
        selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
        fabricCalcSettings?.blindSqftRoundingMode,
      "exact"
    );
    const currentCurtainCalculationSettings = {
      ...(selectedQuote?.calculationSettings || {}),
      blindSqftRoundingMode: quoteBlindSqftRoundingMode,
      fullnessMode: "quote_override_supported",
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };
    const currentCalculationMetadata = {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
    };
    const recalculationFieldsForSave = resolveCurtainRecalculationFieldsForSave();
    const normalizedQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    if (normalizedQuoteDate && normalizedQuoteDate !== date) {
      setDate(normalizedQuoteDate);
    }

    if (isDevelopmentMode()) {
      devLog("QUOTE DISCOUNT SAVE/LOAD", {
        source: isEditingExisting ? "curtain_quote_update" : "curtain_quote_create",
        discountType: currentCurtainDiscountFields.discountType,
        discountValue: currentCurtainDiscountFields.discountValue,
      });
      devLog("SAVING repeatSettings:", repeatSettings);
    }

    /* =================================================
        EDIT MODE  UPDATE ONLY
       ================================================= */

    if (isEditingExisting) {

      const existingSaveTarget = resolveExistingQuoteSaveTarget({
        workspaceUid,
        loadedQuoteMeta: {
          ...(selectedQuote || {}),
          id: selectedQuote?.id || effectiveCurtainEditingQuoteId,
          quoteId: selectedQuote?.quoteId || effectiveCurtainEditingQuoteId,
          quoteNo,
        },
        quoteIndexDoc: selectedQuote?.indexRecordRaw || selectedQuote?.quoteIndex || {},
        quoteType: CURTAIN_QUOTE_TYPE,
      });
      if (
        existingSaveTarget.saveMode !== "update_existing" ||
        !existingSaveTarget.resolvedQuotePath
      ) {
        const friendlyMessage =
          "This quotation could not be saved because its original record could not be found. Please use Save As New.";
        if (isDevRuntime) {
          devError("CURTAIN_QUOTE_SAVE_EXISTING_FAILED", {
            failedStep: "resolve_source_path",
            writePath: "",
            errorCode: "missing-source-path",
            errorMessage: friendlyMessage,
            quoteNo,
            quoteId: effectiveCurtainEditingQuoteId,
            quoteIndexId: selectedQuote?.quoteIndexId || selectedQuote?.indexId || "",
            sourcePath: selectedQuote?.sourcePath || "",
            payloadKeys: [],
          });
        }
        recordSaveTracePatch(traceId, {
          finalResult: "missing_source_path",
          userMessageShown: friendlyMessage,
        });
        setAutoSaveStatus("error");
        notify(friendlyMessage, "error", 7000);
        return false;
      }
      const existingQuoteId =
        existingSaveTarget.fullQuoteId ||
        existingSaveTarget.quoteId ||
        effectiveCurtainEditingQuoteId;
      const existingQuoteIndexId =
        existingSaveTarget.quoteIndexId ||
        selectedQuote?.quoteIndexId ||
        selectedQuote?.indexId ||
        existingQuoteId;
      const existingQuoteSourcePath = existingSaveTarget.resolvedQuotePath;
      const ref = doc(db, ...existingQuoteSourcePath.split("/"));
      debugCurtainSavePayload = {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteNo,
        quoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        rows: rowsForSave,
      };
      debugCurtainSaveContext = {
        action: "save_existing",
        quoteNo,
        quoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        sourcePath: ref.path,
        workspaceUid,
        userUid: currentUser.uid,
        editorMode: curtainQuoteEditorMode,
        writePath: ref.path,
      };
      const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
      const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
        liveActionPayload
      );
      const preflightTracePayload = {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteNo,
        quoteId: existingQuoteId,
        rows: rowsForSave,
      };
      logCurtainSaveWithLiningTrace({
        action: "save_existing",
        quoteNo,
        quoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        sourcePath: ref.path,
        workspaceUid,
        userUid: currentUser.uid,
        editorMode: curtainQuoteEditorMode,
        failedStepCandidate: "customer_ledger_preflight",
        writePath: workspaceUid ? `users/${workspaceUid}/customers` : "",
        operation: "ensureCustomerLedgerRecord",
        payload: preflightTracePayload,
      });
      let customerLedger = null;
      try {
        customerLedger = await ensureCustomerLedgerRecord(
          curtainIdentity,
          {
            customerId: selectedCurtainCustomerId,
            extraQuote: {
              id: existingQuoteId,
              quoteNo,
              quoteType: CURTAIN_QUOTE_TYPE,
              quoteDate: normalizedQuoteDate,
              customerId: selectedCurtainCustomerId,
              customerName: curtainIdentity.customerName,
              grandTotal: Number(summaryTotals.grandTotal || 0),
              status: selectedQuoteStatus || "draft",
            },
          }
        );
      } catch (preflightError) {
        logCurtainSaveWithLiningFailure({
          failedStep: "customer_ledger_preflight",
          writePath: workspaceUid ? `users/${workspaceUid}/customers` : "",
          error: preflightError,
          quoteNo,
          quoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          sourcePath: ref.path,
          workspaceUid,
          userUid: currentUser.uid,
          payload: preflightTracePayload,
        });
        throw preflightError;
      }
      const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
        ...curtainIdentity,
        customerId: selectedCurtainCustomerId,
      });
      const quoteSiteFields = buildQuoteSiteFieldsForSave(
        liveQuoteSiteSelection || quoteSiteSelection,
        {
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          quoteNo,
          ensureSiteId: true,
        }
      );
      const curtainIdentityFields = buildQuoteIdentityFieldsForSave(
        {
          ...selectedQuote,
          ...customerFields,
          ...quoteSiteFields,
          quoteNo,
        },
        {
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          customerEmail: customerFields.customerEmail,
          quoteNo,
        }
      );
      const linkedLeadId = pendingCrmLeadId || selectedQuote?.crm?.leadId || "";
      const crmPayload = buildQuoteCrmData(
        {
          id: existingQuoteId,
          ...selectedQuote,
          ...customerFields,
          ...quoteSiteFields,
          ...curtainIdentityFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: selectedQuoteStatus || "draft",
          workStage: selectedQuoteWorkStage || "quote_given",
          installationDate: selectedInstallationDate || null,
        },
        {
          stageId: selectedQuoteWorkStage || "quote_given",
          status: selectedQuoteStatus || "draft",
          installationDate: selectedInstallationDate || null,
          expectedValue: Number(summaryTotals.grandTotal || 0),
          finalValue: Number(summaryTotals.grandTotal || 0),
          leadId: linkedLeadId,
          hasQuote: true,
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
        contact: {
            name:
              curtainIdentityFields.contactPersonName ||
              customerFields.customerName,
            phone: customerFields.customerPhone,
            whatsapp: customerFields.customerWhatsapp,
            email: customerFields.customerEmail,
            address: customerFields.customerAddress,
          },
        }
      );
      const quoteSearchSource = {
        id: existingQuoteId,
        quoteId: existingQuoteId,
        sourceQuoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        indexId: existingQuoteIndexId,
        sourceCollection: "quotations",
        sourcePath: ref.path,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: selectedQuoteStatus || "draft",
        workStage: selectedQuoteWorkStage || "quote_given",
        installationDate: selectedInstallationDate || null,
        crm: crmPayload,
      };
      const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

      const curtainUpdatePayload = sanitizeCrmFirestoreValue({
        schemaVersion: QUOTE_STORAGE_SCHEMA_VERSION,
        quoteType: CURTAIN_QUOTE_TYPE,
        normalizedQuoteType: CANONICAL_CURTAIN_QUOTE_TYPE,
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
        quoteId: existingQuoteId,
        sourceQuoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        indexId: existingQuoteIndexId,
        sourceCollection: "quotations",
        sourcePath: ref.path,

        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,

        rows: rowsForSave,
        curtainRows: rowsForSave,
        quoteRows: rowsForSave,
        measurementRows: rowsForSave,
        quotationRows: rowsForSave,
        items: rowsForSave,

        repeatSettings: Object.fromEntries(
          Object.entries(repeatSettings).map(([k, v]) => [
            normalizeFabricKey(k),
            normalizeCurtainRepeatEntry(v)
          ])
        ),
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,

        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),

        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        crm: crmPayload,
        searchText: quoteSearchIndex.searchText,
        searchKeywords: quoteSearchIndex.searchKeywords,
        searchTokens: quoteSearchIndex.searchKeywords,
        ...buildQuoteLookupFields(quoteSearchSource),

        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        lastSavedAt: serverTimestamp()

      });
      curtainUpdatePayload.contentUpdatedAt = serverTimestamp();
      traceQuoteIdentity("curtainSavePayloadBuild", curtainUpdatePayload, {
        quoteId: existingQuoteId,
        quoteNo,
        quoteIndexCustomerName: customerFields.customerName,
        quoteIndexSiteName: quoteSiteFields.siteName,
      });

      if (isDevelopmentMode()) {
        const deletedLocalIds = uniqueValues(deletedCurtainRowIdsRef.current || []);
        devLog("QUOTE_SAVE_CONTENT_TRACE", {
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteNo,
          quoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          action: "save",
          liveRowCount: rows.length,
          liveItemCount: 0,
          payloadItemsCount: Array.isArray(curtainUpdatePayload.items)
            ? curtainUpdatePayload.items.length
            : 0,
          payloadRowsCount: Array.isArray(curtainUpdatePayload.rows)
            ? curtainUpdatePayload.rows.length
            : 0,
          loadedSnapshotItemsCount: Array.isArray(selectedQuote?.items)
            ? selectedQuote.items.length
            : 0,
          loadedSnapshotRowsCount: getCurtainQuoteCanonicalRows(selectedQuote || {}).length,
          saveSource: "latestRef",
          liveVisibleIds: rows.map((row) => getWorkshopRowStableId(row)),
          liveRefIds: normalizeRowsForState(rowsRef.current || []).map((row) =>
            getWorkshopRowStableId(row)
          ),
          staleLoadedSnapshotIds: getCurtainQuoteCanonicalRows(selectedQuote || {}).map((row) =>
            getWorkshopRowStableId(row)
          ),
          deletedLocalIds,
          payloadItemIds: Array.isArray(curtainUpdatePayload.items)
            ? curtainUpdatePayload.items.map((row) => getWorkshopRowStableId(row))
            : [],
          payloadRowIds: rowsForSave.map((row) => getWorkshopRowStableId(row)),
          writePath: ref.path,
          mergeUsed: true,
          contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
          canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
        });
      }

      markQuoteSaveInProgressSession({
        traceId,
        action: "save",
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteNo,
        quoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        sourcePath: ref.path,
      });
      recordSaveTracePatch(traceId, {
        quoteNo: sanitizeSaveTraceText(quoteNo || "", 80),
        quoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        sourcePath: ref.path,
        customerName: sanitizeSaveTraceText(customerFields.customerName || customerName || "", 160),
        siteName: sanitizeSaveTraceText(quoteSiteFields.siteName || "", 160),
        requiredWrites: {
          mainQuote: "not_attempted",
          quoteIndex: "not_attempted",
        },
      });

      try {
        await writeQuotationAndQuoteIndex({
          quoteRef: ref,
          quoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          quotePayload: curtainUpdatePayload,
          quoteIndexSource: {
            ...quoteSearchSource,
            sourceQuoteId: existingQuoteId,
            quoteIndexId: existingQuoteIndexId,
            indexId: existingQuoteIndexId,
            sourcePath: ref.path,
            deleted: false,
            archived: false,
            binStatus: "active",
            visibilityStatus: "active",
          },
          create: false,
          traceContext: {
            traceId,
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteNo,
            quoteId: existingQuoteId,
            sourceQuoteId: existingQuoteId,
            quoteIndexId: existingQuoteIndexId,
            sourcePath: ref.path,
            resolvedQuotePath: ref.path,
            editorMode: curtainQuoteEditorMode,
            existingContentCount: loadedCurtainRowCount,
            deletedRowIds: uniqueValues(deletedCurtainRowIdsRef.current || []),
            deletedAccessoryIds: uniqueValues(deletedCurtainRowIdsRef.current || []),
          },
        });
        requiredWritesSucceeded = true;
        recordSaveTracePatch(traceId, {
          requiredWrites: {
            mainQuote: "success",
            quoteIndex: "success",
          },
        });
        traceReferralPartner("save_curtain", {
          quoteId: existingQuoteId,
          quoteNo,
          partner: curtainReferralPartnerFields.referralPartner,
          savedToQuote: true,
          savedToQuoteIndex: true,
          hiddenFromCustomerPdf: true,
        });
        recordSaveTraceQuoteIndexSnapshot(traceId, existingQuoteIndexId, {
          quoteId: existingQuoteId,
          quoteNo,
          customerName: customerFields.customerName || customerName || "",
          siteName: quoteSiteFields.siteName || "",
          quoteType: CURTAIN_QUOTE_TYPE,
          quotationType: CURTAIN_QUOTE_TYPE,
          sourcePath: ref.path,
        });
        logRequiredSaveCompletedBeforeUiPatch({
          quoteNo,
          quoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          sourcePath: ref.path,
        });
        await logQuoteSavedActivity({
          activityType: "quote_updated",
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteId: existingQuoteId,
          quoteNo,
          customerId: customerFields.customerId || "",
          customerName: customerFields.customerName || customerName || "",
          opportunityId:
            crmPayload.opportunityId ||
            crmPayload.leadId ||
            pendingCrmLeadId ||
            selectedQuote?.crm?.opportunityId ||
            selectedQuote?.crm?.leadId ||
            "",
          totalAmount: Number(summaryTotals.grandTotal || 0),
        }).catch(() => {});
        await upsertQuoteLocalRecord({
          ...selectedQuote,
          id: existingQuoteId,
          quoteId: existingQuoteId,
          sourceQuoteId: existingQuoteId,
          fullQuoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          indexId: existingQuoteIndexId,
          sourceCollection: "quotations",
          sourcePath: ref.path,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...customerFields,
          ...quoteSiteFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: selectedQuoteStatus || "draft",
          workStage: selectedQuoteWorkStage || "quote_given",
          installationDate: selectedInstallationDate || null,
          crm: crmPayload,
          updatedAt: new Date(),
        }, { persistIndex: false, traceId, syncDerived: false });
        setEditingQuoteId(existingQuoteId);
        setGeneralEditingQuoteId(null);
        setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
        setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
        setIsNewQuote(false);
        setAutoSaveStatus("saved");
        {
          const deletedIdsForVerification = uniqueValues(deletedCurtainRowIdsRef.current || []);
          const shouldVerifyContentSave =
            deletedIdsForVerification.length > 0 || isDevelopmentMode();
          if (shouldVerifyContentSave) {
          const savedSnap = await readCompatibleQuoteDocument({ ref, workspaceUid,
            getDoc: r => getDoc(r, { label: "curtainQuoteSaveReadback" }),
            callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data) });
          const savedData = savedSnap.exists()
            ? await hydrateCurtainQuoteRowsFromChunks({
                quote: { id: savedSnap.id, ...savedSnap.data() },
                workspaceUid,
                label: "curtainQuoteSaveReadback.rowChunks",
              })
            : null;
          const readBackContentSelection = resolveCurtainQuoteContentSelection(savedData || {});
          const readBackRows = readBackContentSelection.items;
          const indexSnap = await getDoc(
            doc(db, "users", workspaceUid, "quote_index", existingQuoteIndexId),
            { label: "curtainQuoteIndexSaveReadback" }
          );
          const indexData = indexSnap.exists() ? indexSnap.data() : null;
          const readBackGrandTotal = Number(
            savedData?.grandTotal ??
              savedData?.finalTotal ??
              savedData?.total ??
              savedData?.totalAmount ??
              0
          );
          const quoteIndexGrandTotal = Number(
            indexData?.grandTotal ?? indexData?.totalAmount ?? indexData?.total ?? 0
          );
          const liveGrandTotal = Number(summaryTotals.grandTotal || 0);
          const failures = [];
          const expectedLiningRowIds = rowsForSave
            .filter((row) => row?.lining?.enabled === true)
            .map((row) => getWorkshopRowStableId(row))
            .filter(Boolean);
          const readBackLiningRowIds = readBackRows
            .filter((row) => row?.lining?.enabled === true)
            .map((row) => getWorkshopRowStableId(row))
            .filter(Boolean);
          if (!savedSnap.exists()) failures.push("full_quote_missing");
          if (!indexSnap.exists()) failures.push("quote_index_missing");
          if (readBackRows.length !== rowsForSave.length) {
            failures.push("full_quote_row_count_mismatch");
          }
          if (
            expectedLiningRowIds.length !== readBackLiningRowIds.length ||
            expectedLiningRowIds.some((rowId) => !readBackLiningRowIds.includes(rowId))
          ) {
            failures.push("lining_row_persistence_mismatch");
          }
          if (Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0) !== rowsForSave.length) {
            failures.push("quote_index_row_count_mismatch");
          }
          if (Math.abs(readBackGrandTotal - liveGrandTotal) > 0.05) {
            failures.push("full_quote_total_mismatch");
          }
          if (Math.abs(quoteIndexGrandTotal - liveGrandTotal) > 0.05) {
            failures.push("quote_index_total_mismatch");
          }
          const contentInspection = inspectDeletedIdsInContentAliases(
            getCurtainQuoteContentAliasArrays(savedData || {}),
            deletedIdsForVerification,
            getWorkshopRowStableId
          );
          const deletedIdsStillPresent = contentInspection.deletedIdsStillPresent;
          devLog("QUOTE_SAVE_VERIFY_READBACK", {
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteId: existingQuoteId,
            quoteIndexId: existingQuoteIndexId,
            quoteNo,
            liveCount: rowsForSave.length,
            readBackSelectedField: readBackContentSelection.field || "empty",
            readBackSelectedCount: readBackRows.length,
            expectedLiningRowIds,
            readBackLiningRowIds,
            quoteIndexCount: Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0),
            liveGrandTotal,
            readBackGrandTotal,
            quoteIndexGrandTotal,
            failures,
            deletedIds: deletedIdsForVerification,
            canonicalField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
            canonicalIds: getContentAliasIds(readBackRows, getWorkshopRowStableId),
            aliasFindings: contentInspection.aliasFindings,
            aliasCounts: contentInspection.aliasCounts,
            savedPayloadItemCount: Array.isArray(curtainUpdatePayload.items)
              ? curtainUpdatePayload.items.length
              : 0,
            savedPayloadRowCount: rowsForSave.length,
            readBackItemCount: Array.isArray(savedData?.items) ? savedData.items.length : 0,
            readBackRowCount: readBackRows.length,
            deletedIdsStillPresent,
            pass: contentInspection.pass && failures.length === 0,
            contentSchemaVersion: savedData?.contentSchemaVersion || "",
            canonicalContentField: savedData?.canonicalContentField || "",
          });
          if (!contentInspection.pass) {
            throw new Error("Curtain quote save verification failed: deleted row still present.");
          }
          if (failures.length > 0) {
            throw new Error(`Curtain quote save verification failed: ${failures.join(", ")}`);
          }
        }
        }
        notify("Quotation saved.", "success");
        deletedCurtainRowIdsRef.current = [];
      } catch (writeError) {
        const failedStep = writeError?.__fqRequiredSaveStep || "main_quote_or_quote_index";
        const mainQuoteSavedBeforeFailure = writeError?.__fqMainQuoteSaved === true;
        const userMessage =
          failedStep === "quote_index" && mainQuoteSavedBeforeFailure
            ? "Quotation saved, but listing update failed. Please contact support."
            : getSaveQuotationFriendlyError(writeError);
        recordSaveTracePatch(traceId, {
          requiredWrites: {
            mainQuote: mainQuoteSavedBeforeFailure ? "success" : "failed",
            quoteIndex: failedStep === "quote_index" ? "failed" : "not_confirmed",
          },
          finalResult: "required_write_failed",
          failedStep,
          userMessageShown: userMessage,
        });
        appendSaveTraceError(traceId, "curtain_required_quote_or_index_write", writeError, {
          path: `${ref.path} + users/${workspaceUid}/quote_index/${existingQuoteIndexId}`,
          payloadKeys: Object.keys(curtainUpdatePayload || {}),
        });
        throw writeError;
      }

      try {
        await withQuoteSaveTimeout(
          persistQuoteSiteForCustomer(
            customerFields.customerId,
            customerFields,
            quoteSiteFields
          ),
          { step: "optional.curtain.siteDoc", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            siteDoc: "success",
          },
        });
      } catch (siteError) {
        optionalWriteFailed = true;
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            siteDoc: "failed",
          },
        });
        appendSaveTraceError(traceId, "curtain_siteDoc", siteError, {
          path:
            workspaceUid && customerFields.customerId
              ? `users/${workspaceUid}/customers/${customerFields.customerId}/sites`
              : "",
        });
        devWarn("QUOTE_SITE_OPTIONAL_WRITE_SKIPPED", {
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteId: existingQuoteId,
          customerId: customerFields.customerId,
          errorCode: siteError?.code || "",
          errorMessage: siteError?.message || String(siteError),
        });
        notify(
          "Quotation saved, but the customer site library could not be updated.",
          "warning",
          5200
        );
      }

      let crmDealLinkResult = null;
      let crmCustomerWriteStatus = linkedLeadId ? "not_attempted" : "skipped";
      try {
        if (linkedLeadId) {
          await withQuoteSaveTimeout(
            saveCrmFieldsForLead(
              linkedLeadId,
              {
              quoteType: CURTAIN_QUOTE_TYPE,
              stageId: selectedQuoteWorkStage || "quote_given",
              status: selectedQuoteStatus || "draft",
              linkedQuotes: [
                {
                  quoteId: existingQuoteId,
                  quoteNo,
                  type: CURTAIN_QUOTE_TYPE,
                  value: Number(summaryTotals.grandTotal || 0),
                  linkedQuoteDate: normalizedQuoteDate,
                  isPrimary: true,
                },
              ],
              hasQuote: true,
              customerId: customerFields.customerId,
              customerName: customerFields.customerName,
              customerPhone: customerFields.customerPhone,
              expectedValue: Number(summaryTotals.grandTotal || 0),
              finalValue: Number(summaryTotals.grandTotal || 0),
              installationDate: selectedInstallationDate || null,
              },
              { syncQuote: false }
            ),
            { step: "optional.curtain.crmCustomer", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
          );
          setPendingCrmLeadId("");
          crmCustomerWriteStatus = "success";
          recordSaveTracePatch(traceId, {
            optionalWrites: {
              crmCustomer: "success",
            },
          });
        } else {
          recordSaveTracePatch(traceId, {
            optionalWrites: {
              crmCustomer: "skipped",
            },
          });
        }

        crmDealLinkResult = await withQuoteSaveTimeout(
          ensureSavedQuoteCrmDeal(
            {
            ...customerFields,
            ...quoteSiteFields,
            id: existingQuoteId,
            quoteId: existingQuoteId,
            sourceQuoteId: existingQuoteId,
            quoteIndexId: existingQuoteIndexId,
            indexId: existingQuoteIndexId,
            sourcePath: ref.path,
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteNo,
            quoteDate: normalizedQuoteDate,
            date: normalizedQuoteDate,
            quotationDate: normalizedQuoteDate,
            rows: rowsForSave,
            repeatSettings,
            fabricSummaryMeta: normalizedFabricSummaryMeta,
            useHalfPieceReuse,
            passHalfPieceSavingsToCustomer,
            ...currentQuoteFullnessFields,
            calculationSnapshot: quoteCalculationSnapshot,
            calculationSettings: currentCurtainCalculationSettings,
            ...currentCalculationMetadata,
            ...recalculationFieldsForSave,
            subtotal: Number(summaryTotals.subTotal || 0),
            grandTotal: Number(summaryTotals.grandTotal || 0),
            ...currentCurtainDiscountFields,
            ...currentCurtainGstFields,
            status: selectedQuoteStatus || "draft",
            workStage: selectedQuoteWorkStage || "quote_given",
            installationDate: selectedInstallationDate || null,
            crm: crmPayload,
            },
            {
            quoteNo,
            quoteType: CURTAIN_QUOTE_TYPE,
            totalValue: Number(summaryTotals.grandTotal || 0),
            customerFields,
            linkedLeadId,
            previousSaveQuoteOnly: selectedQuote?.crm?.saveQuoteOnly === true,
            showSaveOutcomeToast: false,
            }
          ),
          { step: "optional.curtain.crmDealLink", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        const crmDealLinkStatus =
          crmDealLinkResult?.action === "skipped" ||
          crmDealLinkResult?.action === "saveOnly"
            ? "skipped"
            : "success";
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmDealLink: crmDealLinkStatus,
          },
        });
      } catch (crmError) {
        optionalWriteFailed =
          Boolean(linkedLeadId) || crmSettings.autoCreateDealFromQuotation !== false;
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: crmCustomerWriteStatus === "success" ? "success" : linkedLeadId ? "failed" : "skipped",
            crmDealLink:
              linkedLeadId || crmSettings.autoCreateDealFromQuotation !== false
                ? "failed"
                : "skipped",
          },
        });
        appendSaveTraceError(traceId, "curtain_crm_link_after_required_save", crmError, {
          path: linkedLeadId
            ? `users/${workspaceUid}/crm_leads/${linkedLeadId}`
            : `users/${workspaceUid}/crm_leads`,
        });
        devWarn("QUOTE_SAVE_OPTIONAL_WRITE_SKIPPED", {
          step: "crm_link_after_required_quote_save",
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteId: existingQuoteId,
          quoteNo,
          path: linkedLeadId
            ? `users/${workspaceUid}/crm_leads/${linkedLeadId}`
            : `users/${workspaceUid}/crm_leads`,
          errorCode: crmError?.code || "",
          errorMessage: crmError?.message || String(crmError),
        });
        if (linkedLeadId) {
          notify(
            "Quotation saved. CRM link could not be updated.",
            "warning",
            5200
          );
        }
      }
      const finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
      const finalWorkStage = crmDealLinkResult?.stageId || selectedQuoteWorkStage || "quote_given";
      const finalQuoteStatus = crmDealLinkResult?.status || selectedQuoteStatus || "draft";
      const finalInstallationDate =
        finalCrmPayload.installationDate || selectedInstallationDate || null;

      await upsertQuoteLocalRecord({
        ...selectedQuote,
        id: existingQuoteId,
        quoteId: existingQuoteId,
        sourceQuoteId: existingQuoteId,
        quoteIndexId: existingQuoteIndexId,
        indexId: existingQuoteIndexId,
        sourceCollection: "quotations",
        sourcePath: ref.path,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
        updatedAt: new Date(),
      }, { persistIndex: false, traceId });
      setQuoteCustomerLink({
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        phone: customerFields.customerPhone,
        whatsapp: customerFields.customerWhatsapp,
        email: customerFields.customerEmail,
        address: customerFields.customerAddress,
        companyName: customerFields.customerCompanyName,
      });
      setQuoteSiteSelection(normalizeQuoteSiteSnapshot(quoteSiteFields));
      if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
        setSelectedQuote({
          ...selectedQuote,
          id: existingQuoteId,
          quoteId: existingQuoteId,
          sourceQuoteId: existingQuoteId,
          quoteIndexId: existingQuoteIndexId,
          indexId: existingQuoteIndexId,
          sourceCollection: "quotations",
          sourcePath: ref.path,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...customerFields,
          ...quoteSiteFields,
          ...curtainIdentityFields,
          ...curtainReferralPartnerFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: finalQuoteStatus,
          workStage: finalWorkStage,
          installationDate: finalInstallationDate,
          crm: finalCrmPayload,
        });
        markCurtainQuoteBaseline(
          {
            ...buildCurtainQuoteDraftData(),
            quoteId: existingQuoteId,
            sourceQuoteId: existingQuoteId,
            quoteIndexId: existingQuoteIndexId,
            indexId: existingQuoteIndexId,
            sourcePath: ref.path,
            editingQuoteId: existingQuoteId,
            quoteNo,
            quoteSiteSelection: normalizeQuoteSiteSnapshot(quoteSiteFields),
            ...normalizeQuoteSiteSnapshot(quoteSiteFields),
            quoteDate: normalizedQuoteDate,
            date: normalizedQuoteDate,
            quotationDate: normalizedQuoteDate,
            selectedQuoteStatus: finalQuoteStatus,
            selectedQuoteWorkStage: finalWorkStage,
            selectedInstallationDate: finalInstallationDate,
          },
          "saved"
        );
        clearCurtainQuoteDraft(previousCurtainDraftKey);
      } else if (isQuoteMutationAuditDevMode()) {
        devWarn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
          source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
          quoteId: existingQuoteId,
          saveStartedEditVersion,
          currentEditVersion: currentQuoteEditVersionRef.current,
        });
      }
      setAutoSaveStatus("saved");
      recordSaveTracePatch(traceId, {
        finalResult: optionalWriteFailed
          ? "optional_write_failed_but_saved"
          : "success",
        userMessageShown: optionalWriteFailed
          ? "Quotation saved, but one optional sync could not be updated."
          : "",
      });
      if (optionalWriteFailed) {
        notify(
          "Quotation saved successfully. Customer history could not be updated. Your quotation is safe. Use Update Customer History in Saved Quotes.",
          "warning",
          7000
        );
      }
      return;
    }

    /* =================================================
        CREATE MODE
       ================================================= */

    const generatedQuoteNo = await generateQuoteNumber(
      workspaceUid, "curtain"
    );

    setQuoteNo(generatedQuoteNo);

    const quoteDocId = generatedQuoteNo;

    const ref = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );
    const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
    const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
      liveActionPayload
    );
    const customerLedger = await ensureCustomerLedgerRecord(
      curtainIdentity,
      {
        customerId: selectedCurtainCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: generatedQuoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteDate: normalizedQuoteDate,
          customerId: selectedCurtainCustomerId,
          customerName: curtainIdentity.customerName,
          grandTotal: Number(summaryTotals.grandTotal || 0),
          status: "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      ...curtainIdentity,
      customerId: selectedCurtainCustomerId,
    });
    const quoteSiteFields = buildQuoteSiteFieldsForSave(
      liveQuoteSiteSelection || quoteSiteSelection,
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        quoteNo: generatedQuoteNo,
        ensureSiteId: true,
      }
    );
    const curtainIdentityFields = buildQuoteIdentityFieldsForSave(
      {
        ...customerFields,
        ...quoteSiteFields,
        quoteNo: generatedQuoteNo,
      },
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerEmail: customerFields.customerEmail,
        quoteNo: generatedQuoteNo,
      }
    );
    const linkedLeadId = pendingCrmLeadId || "";
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const createStageId =
      linkedLead?.stageId || getQuoteGivenStageIdForType(CURTAIN_QUOTE_TYPE);
    const createStatus = linkedLead?.status || "draft";
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: createStatus,
        workStage: createStageId,
        installationDate: linkedLead?.installationDate || null,
        quoteType: CURTAIN_QUOTE_TYPE,
      },
      {
        stageId: createStageId,
        status: createStatus,
        installationDate: linkedLead?.installationDate || null,
        expectedValue: Number(summaryTotals.grandTotal || 0),
        finalValue: Number(summaryTotals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contact: {
          name:
            curtainIdentityFields.contactPersonName ||
            customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchSource = {
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,
      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: createStatus,
      workStage: createStageId,
      installationDate: linkedLead?.installationDate || null,
      crm: crmPayload,
    };
    const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

    const curtainCreatePayload = sanitizeCrmFirestoreValue({
      schemaVersion: QUOTE_STORAGE_SCHEMA_VERSION,
      quoteType: CURTAIN_QUOTE_TYPE,
      normalizedQuoteType: CANONICAL_CURTAIN_QUOTE_TYPE,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,

      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,

      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,

      rows: rowsForSave,
      curtainRows: rowsForSave,
      quoteRows: rowsForSave,
      measurementRows: rowsForSave,
      quotationRows: rowsForSave,
      items: rowsForSave,

      repeatSettings: Object.fromEntries(
        Object.entries(repeatSettings).map(([k, v]) => [
          normalizeFabricKey(k),
          normalizeCurtainRepeatEntry(v)
        ])
      ),
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,

        subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),

      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,

      status: createStatus,
      workStage: createStageId,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields(quoteSearchSource),

      installationDate: linkedLead?.installationDate || null,

      createdAt: serverTimestamp(),
      contentUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()

    });
    traceQuoteIdentity("curtainCreatePayloadBuild", curtainCreatePayload, {
      quoteId: quoteDocId,
      quoteNo: generatedQuoteNo,
      quoteIndexCustomerName: customerFields.customerName,
      quoteIndexSiteName: quoteSiteFields.siteName,
    });

    if (isDevelopmentMode()) {
      const deletedLocalIds = uniqueValues(deletedCurtainRowIdsRef.current || []);
      devLog("QUOTE_SAVE_CONTENT_TRACE", {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteNo: generatedQuoteNo,
        quoteId: quoteDocId,
        action: "save",
        liveRowCount: rows.length,
        liveItemCount: 0,
        payloadItemsCount: Array.isArray(curtainCreatePayload.items)
          ? curtainCreatePayload.items.length
          : 0,
        payloadRowsCount: Array.isArray(curtainCreatePayload.rows)
          ? curtainCreatePayload.rows.length
          : 0,
        loadedSnapshotItemsCount: Array.isArray(selectedQuote?.items)
          ? selectedQuote.items.length
          : 0,
        loadedSnapshotRowsCount: getCurtainQuoteCanonicalRows(selectedQuote || {}).length,
        saveSource: "latestRef",
        liveVisibleIds: rows.map((row) => getWorkshopRowStableId(row)),
        liveRefIds: normalizeRowsForState(rowsRef.current || []).map((row) =>
          getWorkshopRowStableId(row)
        ),
        staleLoadedSnapshotIds: getCurtainQuoteCanonicalRows(selectedQuote || {}).map((row) =>
          getWorkshopRowStableId(row)
        ),
        deletedLocalIds,
        payloadItemIds: Array.isArray(curtainCreatePayload.items)
          ? curtainCreatePayload.items.map((row) => getWorkshopRowStableId(row))
          : [],
        payloadRowIds: rowsForSave.map((row) => getWorkshopRowStableId(row)),
        writePath: ref.path,
        mergeUsed: false,
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
      });
    }

    markQuoteSaveInProgressSession({
      traceId,
      action: "save",
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo: generatedQuoteNo,
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: ref.path,
    });
    recordSaveTracePatch(traceId, {
      quoteNo: sanitizeSaveTraceText(generatedQuoteNo || "", 80),
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: ref.path,
      customerName: sanitizeSaveTraceText(customerFields.customerName || customerName || "", 160),
      siteName: sanitizeSaveTraceText(quoteSiteFields.siteName || "", 160),
      requiredWrites: {
        mainQuote: "not_attempted",
        quoteIndex: "not_attempted",
      },
    });

    try {
      await persistNewQuotationWithTrialGuard({
        quoteId: quoteDocId,
        quotePayload: curtainCreatePayload,
        quoteIndexSource: {
          ...quoteSearchSource,
          sourceQuoteId: quoteDocId,
        },
        traceId,
      });
      requiredWritesSucceeded = true;
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "success",
          quoteIndex: "success",
        },
      });
      traceReferralPartner("save_curtain", {
        quoteId: quoteDocId,
        quoteNo: generatedQuoteNo,
        partner: curtainReferralPartnerFields.referralPartner,
        savedToQuote: true,
        savedToQuoteIndex: true,
        hiddenFromCustomerPdf: true,
      });
      recordSaveTraceQuoteIndexSnapshot(traceId, quoteDocId, {
        quoteId: quoteDocId,
        quoteNo: generatedQuoteNo,
        customerName: customerFields.customerName || customerName || "",
        siteName: quoteSiteFields.siteName || "",
        quoteType: CURTAIN_QUOTE_TYPE,
        quotationType: CURTAIN_QUOTE_TYPE,
        sourcePath: ref.path,
      });
      logRequiredSaveCompletedBeforeUiPatch({
        quoteNo: generatedQuoteNo,
        quoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        sourcePath: ref.path,
      });
      await logQuoteSavedActivity({
        activityType: "quote_created",
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteId: quoteDocId,
        quoteNo: generatedQuoteNo,
        customerId: customerFields.customerId || "",
        customerName: customerFields.customerName || customerName || "",
        opportunityId: crmPayload.opportunityId || crmPayload.leadId || pendingCrmLeadId || "",
        totalAmount: Number(summaryTotals.grandTotal || 0),
      }).catch(() => {});
      await upsertQuoteLocalRecord({
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: createStatus,
        workStage: createStageId,
        installationDate: linkedLead?.installationDate || null,
        crm: crmPayload,
        createdAt: new Date(),
        updatedAt: new Date(),
      }, { persistIndex: false, traceId, syncDerived: false });
      setEditingQuoteId(quoteDocId);
      setGeneralEditingQuoteId(null);
      setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
      setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
      setIsNewQuote(false);
      setAutoSaveStatus("saved");
      {
        const deletedIdsForVerification = uniqueValues(deletedCurtainRowIdsRef.current || []);
        const shouldVerifyContentSave =
          deletedIdsForVerification.length > 0 || isDevelopmentMode();
        if (shouldVerifyContentSave) {
        const savedSnap = await readCompatibleQuoteDocument({ ref, workspaceUid,
          getDoc: r => getDoc(r, { label: "curtainQuoteCreateReadback" }),
          callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data) });
        const savedData = savedSnap.exists()
          ? await hydrateCurtainQuoteRowsFromChunks({
              quote: { id: savedSnap.id, ...savedSnap.data() },
              workspaceUid,
              label: "curtainQuoteCreateReadback.rowChunks",
            })
          : null;
        const readBackContentSelection = resolveCurtainQuoteContentSelection(savedData || {});
        const readBackRows = readBackContentSelection.items;
        const indexSnap = await getDoc(
          doc(db, "users", workspaceUid, "quote_index", quoteDocId),
          { label: "curtainQuoteIndexCreateReadback" }
        );
        const indexData = indexSnap.exists() ? indexSnap.data() : null;
        const readBackGrandTotal = Number(
          savedData?.grandTotal ??
            savedData?.finalTotal ??
            savedData?.total ??
            savedData?.totalAmount ??
            0
        );
        const quoteIndexGrandTotal = Number(
          indexData?.grandTotal ?? indexData?.totalAmount ?? indexData?.total ?? 0
        );
        const liveGrandTotal = Number(summaryTotals.grandTotal || 0);
        const failures = [];
        const expectedLiningRowIds = rowsForSave
          .filter((row) => row?.lining?.enabled === true)
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean);
        const readBackLiningRowIds = readBackRows
          .filter((row) => row?.lining?.enabled === true)
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean);
        if (!savedSnap.exists()) failures.push("full_quote_missing");
        if (!indexSnap.exists()) failures.push("quote_index_missing");
        if (readBackRows.length !== rowsForSave.length) {
          failures.push("full_quote_row_count_mismatch");
        }
        if (
          expectedLiningRowIds.length !== readBackLiningRowIds.length ||
          expectedLiningRowIds.some((rowId) => !readBackLiningRowIds.includes(rowId))
        ) {
          failures.push("lining_row_persistence_mismatch");
        }
        if (Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0) !== rowsForSave.length) {
          failures.push("quote_index_row_count_mismatch");
        }
        if (Math.abs(readBackGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("full_quote_total_mismatch");
        }
        if (Math.abs(quoteIndexGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("quote_index_total_mismatch");
        }
        const contentInspection = inspectDeletedIdsInContentAliases(
          getCurtainQuoteContentAliasArrays(savedData || {}),
          deletedIdsForVerification,
          getWorkshopRowStableId
        );
        const deletedIdsStillPresent = contentInspection.deletedIdsStillPresent;
        devLog("QUOTE_SAVE_VERIFY_READBACK", {
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteId: quoteDocId,
          quoteNo: generatedQuoteNo,
          liveCount: rowsForSave.length,
          readBackSelectedField: readBackContentSelection.field || "empty",
          readBackSelectedCount: readBackRows.length,
          expectedLiningRowIds,
          readBackLiningRowIds,
          quoteIndexCount: Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0),
          liveGrandTotal,
          readBackGrandTotal,
          quoteIndexGrandTotal,
          failures,
          deletedIds: deletedIdsForVerification,
          canonicalField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
          canonicalIds: getContentAliasIds(readBackRows, getWorkshopRowStableId),
          aliasFindings: contentInspection.aliasFindings,
          aliasCounts: contentInspection.aliasCounts,
          savedPayloadItemCount: Array.isArray(curtainCreatePayload.items)
            ? curtainCreatePayload.items.length
            : 0,
          savedPayloadRowCount: rowsForSave.length,
          readBackItemCount: Array.isArray(savedData?.items) ? savedData.items.length : 0,
          readBackRowCount: readBackRows.length,
          deletedIdsStillPresent,
          pass: contentInspection.pass && failures.length === 0,
          contentSchemaVersion: savedData?.contentSchemaVersion || "",
          canonicalContentField: savedData?.canonicalContentField || "",
        });
        if (!contentInspection.pass) {
          throw new Error("Curtain quote create verification failed: deleted row still present.");
        }
        if (failures.length > 0) {
          throw new Error(`Curtain quote create verification failed: ${failures.join(", ")}`);
        }
      }
      }
      notify("Quotation saved.", "success");
      deletedCurtainRowIdsRef.current = [];
    } catch (writeError) {
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "failed",
          quoteIndex: "failed",
        },
        finalResult: "required_write_failed",
        userMessageShown: "Failed to save quotation",
      });
      appendSaveTraceError(traceId, "curtain_create_required_quote_or_index_write", writeError, {
        path: `${ref.path} + users/${workspaceUid}/quote_index/${quoteDocId}`,
        payloadKeys: Object.keys(curtainCreatePayload || {}),
      });
      throw writeError;
    }

    try {
      await withQuoteSaveTimeout(
        persistQuoteSiteForCustomer(
          customerFields.customerId,
          customerFields,
          quoteSiteFields
        ),
        { step: "optional.curtainCreate.siteDoc", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
      );
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          siteDoc: "success",
        },
      });
    } catch (siteError) {
      optionalWriteFailed = true;
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          siteDoc: "failed",
        },
      });
      appendSaveTraceError(traceId, "curtain_create_siteDoc", siteError, {
        path:
          workspaceUid && customerFields.customerId
            ? `users/${workspaceUid}/customers/${customerFields.customerId}/sites`
            : "",
      });
      devWarn("QUOTE_SITE_OPTIONAL_WRITE_SKIPPED", {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteId: quoteDocId,
        customerId: customerFields.customerId,
        errorCode: siteError?.code || "",
        errorMessage: siteError?.message || String(siteError),
      });
      notify(
        "Quotation saved, but the customer site library could not be updated.",
        "warning",
        5200
      );
    }

    setEditingQuoteId(quoteDocId);
    setGeneralEditingQuoteId(null);
    setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);

    let crmDealLinkResult = null;
    let crmCustomerWriteStatus = linkedLeadId ? "not_attempted" : "skipped";
    try {
      if (linkedLeadId) {
        await withQuoteSaveTimeout(
          saveCrmFieldsForLead(
            linkedLeadId,
            {
            quoteType: CURTAIN_QUOTE_TYPE,
            stageId: createStageId,
            status: createStatus,
            linkedQuotes: [
              {
                quoteId: quoteDocId,
                quoteNo: generatedQuoteNo,
                type: CURTAIN_QUOTE_TYPE,
                value: Number(summaryTotals.grandTotal || 0),
                linkedQuoteDate: normalizedQuoteDate,
                isPrimary: true,
              },
            ],
            hasQuote: true,
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            expectedValue: Number(summaryTotals.grandTotal || 0),
            finalValue: Number(summaryTotals.grandTotal || 0),
            installationDate: linkedLead?.installationDate || null,
            },
            { syncQuote: false }
          ),
          { step: "optional.curtainCreate.crmCustomer", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        setPendingCrmLeadId("");
        crmCustomerWriteStatus = "success";
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "success",
          },
        });
      } else {
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "skipped",
          },
        });
      }

      crmDealLinkResult = await withQuoteSaveTimeout(
        ensureSavedQuoteCrmDeal(
          {
          id: quoteDocId,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...customerFields,
          ...quoteSiteFields,
          ...curtainIdentityFields,
          ...curtainReferralPartnerFields,
          quoteNo: generatedQuoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: createStatus,
          workStage: createStageId,
          installationDate: linkedLead?.installationDate || null,
          crm: crmPayload,
          },
          {
            quoteNo: generatedQuoteNo,
            quoteType: CURTAIN_QUOTE_TYPE,
            totalValue: Number(summaryTotals.grandTotal || 0),
            customerFields,
            linkedLeadId,
            saveAsNew: true,
            showSaveOutcomeToast: false,
          }
        ),
        { step: "optional.curtainCreate.crmDealLink", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
      );
    const crmDealLinkStatus =
      crmDealLinkResult?.action === "skipped" ||
      crmDealLinkResult?.action === "saveOnly"
        ? "skipped"
        : "success";
    recordSaveTracePatch(traceId, {
      optionalWrites: {
        crmDealLink: crmDealLinkStatus,
      },
    });
  } catch (crmError) {
    optionalWriteFailed =
      Boolean(linkedLeadId) || crmSettings.autoCreateDealFromQuotation !== false;
    recordSaveTracePatch(traceId, {
      optionalWrites: {
        crmCustomer: crmCustomerWriteStatus === "success" ? "success" : linkedLeadId ? "failed" : "skipped",
        crmDealLink:
          linkedLeadId || crmSettings.autoCreateDealFromQuotation !== false
            ? "failed"
            : "skipped",
      },
    });
      appendSaveTraceError(traceId, "curtain_create_crm_link_after_required_save", crmError, {
        path: linkedLeadId
          ? `users/${workspaceUid}/crm_leads/${linkedLeadId}`
          : `users/${workspaceUid}/crm_leads`,
      });
      devWarn("QUOTE_SAVE_OPTIONAL_WRITE_SKIPPED", {
        step: "crm_link_after_required_quote_save",
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteId: quoteDocId,
        quoteNo: generatedQuoteNo,
        path: linkedLeadId
          ? `users/${workspaceUid}/crm_leads/${linkedLeadId}`
          : `users/${workspaceUid}/crm_leads`,
        errorCode: crmError?.code || "",
        errorMessage: crmError?.message || String(crmError),
      });
    if (linkedLeadId) {
      notify(
        "Quotation saved. CRM link could not be updated.",
        "warning",
        5200
      );
    }
  }
    const finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
    const finalWorkStage = crmDealLinkResult?.stageId || createStageId;
    const finalQuoteStatus = crmDealLinkResult?.status || createStatus;
    const finalInstallationDate =
      finalCrmPayload.installationDate || linkedLead?.installationDate || null;

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,
      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...recalculationFieldsForSave,
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
      status: finalQuoteStatus,
      workStage: finalWorkStage,
      installationDate: finalInstallationDate,
      crm: finalCrmPayload,
      createdAt: new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false, traceId });
    setQuoteCustomerLink({
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      phone: customerFields.customerPhone,
      whatsapp: customerFields.customerWhatsapp,
      email: customerFields.customerEmail,
      address: customerFields.customerAddress,
      companyName: customerFields.customerCompanyName,
    });
    setQuoteSiteSelection(normalizeQuoteSiteSnapshot(quoteSiteFields));
    if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
      setSelectedQuote({
        id: quoteDocId,
        quoteId: quoteDocId,
        sourceQuoteId: quoteDocId,
        fullQuoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        indexId: quoteDocId,
        sourceCollection: "quotations",
        sourcePath: ref.path,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
      });
      markCurtainQuoteBaseline(
        {
          ...buildCurtainQuoteDraftData(),
          quoteId: quoteDocId,
          editingQuoteId: quoteDocId,
          quoteNo: generatedQuoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          selectedQuoteStatus: finalQuoteStatus,
          selectedQuoteWorkStage: finalWorkStage,
          selectedInstallationDate: finalInstallationDate,
        },
        "saved"
      );
      clearCurtainQuoteDraft(previousCurtainDraftKey);
      clearLocalDraft(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          quoteDocId || "new"
        )
      );
    } else if (isQuoteMutationAuditDevMode()) {
      devWarn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
        quoteId: quoteDocId,
        saveStartedEditVersion,
        currentEditVersion: currentQuoteEditVersionRef.current,
      });
    }
    setAutoSaveStatus("saved");
    recordSaveTracePatch(traceId, {
      finalResult: optionalWriteFailed
        ? "optional_write_failed_but_saved"
        : "success",
      userMessageShown: optionalWriteFailed
        ? "Quotation saved, but one optional sync could not be updated."
        : "",
    });
    if (optionalWriteFailed) {
      notify(
        "Quotation saved successfully. Customer history could not be updated. Your quotation is safe. Use Update Customer History in Saved Quotes.",
        "warning",
        7000
      );
    }

  } catch (err) {

    devError("Save failed:", err);
    logCurtainSaveWithLiningFailure({
      ...debugCurtainSaveContext,
      failedStep: err?.__fqRequiredSaveStep || "save_pipeline",
      writePath:
        err?.__fqRequiredSaveStep === "quote_index"
          ? debugCurtainSaveContext.quoteIndexId && workspaceUid
            ? `users/${workspaceUid}/quote_index/${debugCurtainSaveContext.quoteIndexId}`
            : debugCurtainSaveContext.writePath || ""
          : debugCurtainSaveContext.writePath || "",
      error: err,
      payload: debugCurtainSavePayload || {},
    });

    if (requiredWritesSucceeded) {
      devWarn("CURTAIN_QUOTE_POST_REQUIRED_SAVE_OPTIONAL_FAILED", {
        errorCode: err?.code || "",
        errorMessage: err?.message || String(err),
        workspaceUid,
      });
      setAutoSaveStatus("saved");
      recordSaveTracePatch(traceId, {
        finalResult: "optional_write_failed_but_saved",
        userMessageShown: "",
      });
      appendSaveTraceError(traceId, "curtain_save_post_required_optional", err, {
        path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
      });
      return true;
    }

    const rowValidationDetail = getQuoteRowValidationDetailFromError(err);
    if (rowValidationDetail) {
      handleQuoteRowValidationFailure(rowValidationDetail, {
        source: "saveQuotation.outerCatch",
      });
      recordSaveTracePatch(traceId, {
        finalResult: "required_write_failed",
        failedStep: err?.__fqRequiredSaveStep || "",
        userMessageShown: rowValidationDetail.message || "Quotation row data is invalid.",
      });
      appendSaveTraceError(traceId, "curtain_save_row_validation", err, {
        path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
      });
      return false;
    }

    setAutoSaveStatus("error");
    const userMessage =
      err?.__fqRequiredSaveStep === "quote_index" && err?.__fqMainQuoteSaved === true
        ? "Quotation saved, but listing update failed. Please contact support."
        : getSaveQuotationFriendlyError(err);
    recordSaveTracePatch(traceId, {
      finalResult: requiredWritesSucceeded
        ? "optional_write_failed_but_saved"
        : "required_write_failed",
      failedStep: err?.__fqRequiredSaveStep || "",
      userMessageShown: userMessage,
    });
    appendSaveTraceError(traceId, "curtain_save_outer_catch", err, {
      path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
    });

    alert(userMessage);

  } finally {

    setIsSaving(false);

  }

};
/* END RESTORED_FROM_LAST_GOOD_BUILD: workspace/load/save helpers */

/* --------------------------- SAVE AS NEW --------------------------- */
const saveQuotationAsNew = async (event) => {
  if (!operationalQuoteSettingsReady()) { applyQuoteActionEventGuard(event); return false; }
  if (quoteSubTab === GENERAL_QUOTE_TYPE) {
    await saveGeneralQuotation({ asNew: true, event });
    return;
  }
  const eventGuard = applyQuoteActionEventGuard(event);
  const traceId = startSaveTrace({
    action: "save_as_new",
    quoteType: CURTAIN_QUOTE_TYPE,
    eventGuard,
  });
  let requiredWritesSucceeded = false;
  let optionalWriteFailed = false;
  logQuoteSaveEventGuard({
    handler: "saveQuotationAsNew",
    traceId,
    event,
    eventGuard,
  });
  markQuoteSaveInProgressSession({
    traceId,
    action: "save_as_new",
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteId: "",
    quoteIndexId: "",
    quoteNo: quoteNo || selectedQuote?.quoteNo || "",
  });
  traceQuoteActionClick({
    action:
      quoteSubTab === GENERAL_QUOTE_TYPE
        ? "generalSaveAsNewClick"
        : "curtainSaveAsNewClick",
    quoteType: quoteSubTab,
    event,
  });
  const previousCurtainDraftKey = curtainQuoteDraftKey;

  if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  if (!canCreateQuotes) {
    alert("You do not have permission to create quotations in this workspace.");
    return;
  }

  if (isSaving) return;

  setIsSaving(true);
  setAutoSaveStatus("saving");
  setQuoteRowValidationError(null);
  const saveStartedEditVersion = currentQuoteEditVersionRef.current;

  try {
    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: "saveAsNew",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryMeta,
      summaryTotals,
      currentCurtainDiscountFields,
      currentCurtainGstFields,
      customerName,
      quoteSiteSelection: liveQuoteSiteSelection,
      quoteNo,
      quoteDate: date,
      selectedQuote,
      pendingCrmLeadId,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      quoteFullnessOverrides: liveQuoteFullnessOverrides,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    } = liveActionPayload;
    const currentQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      liveQuoteFullnessOverrides || {}
    );
    let currentQuoteFullnessFields = {
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
      ...buildQuoteConstructionFields(rows),
    };
    setQuoteConstructionSettings(normalizeQuoteCalculationSettings(currentQuoteFullnessFields));
    const curtainReferralPartnerFields = buildReferralPartnerQuoteFields(
      liveActionPayload.referralPartner ||
        selectedReferralPartnerRef.current ||
        normalizeQuoteReferralPartner(selectedQuote || {})
    );

    if (!customerName?.trim()) {
      alert("Customer name required");
      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser || !workspaceUid) {
      alert("Please sign in first.");
      return;
    }

    await ensureFabricCatalogueLibrariesForRows(rows, { force: true });
    await syncFabricCatalogueEntriesForQuoteRows(rows);

    const productionRowsForSave = rows.map((row, index) => {
      const productionCalc = getCurtainProductionAdjustedRowCalc(row, index);
      const rowForSaveBase = {
        ...row,
        ...getCurtainRowFabricCatalogueSavePatch(row),
      };
      delete rowForSaveBase.__forceCurrentCalculation;
      delete rowForSaveBase.__recalculated;
      const rowCalculationSnapshot =
        productionCalc.isCurtain || productionCalc.isRoman
          ? resolveCurtainRowSnapshotForSave(row, productionCalc)
          : null;
      const rowLiningForSave = normalizeLiningForSave(
        {
          ...row,
          lining: productionCalc.lining || row.lining,
        },
        row.productConfigSnapshot || null,
        productionCalc
      );
      return {
        ...rowForSaveBase,
        ...buildCurtainRowFinancialAliasesForSave(productionCalc),
        repeatSettings: normalizeCurtainRepeatEntry(
          repeatSettings?.[normalizeFabricKey(getCurtainRowFabricKey(row))] || {}
        ),
        lineType: productionCalc.lineType || row.lineType || (productionCalc.isAccessory ? "accessory" : "measurement"),
        calculationType: productionCalc.calculationType || row.calculationType || "",
        category: row.category || row.productConfigSnapshot?.category || "",
        calculationSnapshot: rowCalculationSnapshot,
        total: Number(productionCalc.rowTotal ?? productionCalc.total ?? 0),
        rowTotal: Number(productionCalc.rowTotal || 0),
        lineTotal: Number(productionCalc.lineTotal ?? productionCalc.rowTotal ?? 0),
        taxableAmount: Number(productionCalc.taxableAmount ?? productionCalc.rowTotal ?? 0),
        ...(!productionCalc.usesCalculationSnapshot || rowCalculationSnapshot?.calculationVersion === CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION
          ? { gstAmount: Number(productionCalc.gstAmount || 0), rowGst: Number(productionCalc.gstAmount || 0) } : {}),
        adjSqft: Number(productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0),
        billableSqft: Number(productionCalc.billableSqft ?? productionCalc.adjSqft ?? 0),
        ...(productionCalc.sqftCalculation
          ? {
              rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
              sqftCalculation: {
                ...productionCalc.sqftCalculation,
                rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
                billableSqft: Number(
                  productionCalc.sqftCalculation.billableSqft || 0
                ),
              },
            }
          : {}),
        lining: rowLiningForSave,
        liningEnabled: rowLiningForSave.enabled,
        liningType: rowLiningForSave.type,
        liningRate:
          rowLiningForSave.chargeMethod === "sqft"
            ? rowLiningForSave.ratePerSqft
            : rowLiningForSave.ratePerMeter,
        liningAmount: rowLiningForSave.amount,
        liningSqft: rowLiningForSave.sqft,
        liningMeters: rowLiningForSave.billableMeters,
        trackConfig: productionCalc.trackConfig || row.trackConfig || { ...DEFAULT_TRACK_CONFIG },
        bendAmount: Number(productionCalc.bendAmount || productionCalc.trackConfig?.bendAmount || 0),
        trackCost: Number(productionCalc.trackCost || 0),
        stitchingCost: Number(productionCalc.stitchingCost || 0),
        fabricGrossAmount: Number(productionCalc.fabricGrossAmount || 0),
        fabricDiscountPercent: Number(productionCalc.fabricDiscountPercent ?? row.discount ?? 0),
        fabricDiscountAmount: Number(productionCalc.fabricDiscountAmount || 0),
        fabricNetAmount: Number(productionCalc.fabricNetAmount ?? productionCalc.fabricCost ?? 0),
        discountScope: productionCalc.discountScope || getRowDiscountScope(row, row.productConfigSnapshot, {
          hasFabricMeterComponent: Boolean(productionCalc.isCurtain || productionCalc.isRoman),
          fabricGrossAmount: productionCalc.fabricGrossAmount || 0,
        }),
        discountBase: Number(productionCalc.discountBase || 0),
        discountAmount: Number(
          productionCalc.discountAmount || productionCalc.fabricDiscountAmount || 0
        ),
        baseLineAmountBeforeDiscount: Number(
          productionCalc.baseLineAmountBeforeDiscount ??
            productionCalc.rowTotal ??
            productionCalc.lineTotal ??
            0
        ),
        fabricCost: Number(productionCalc.fabricCost || 0),
        sqftAmount: Number(productionCalc.sqftAmount || productionCalc.productCharge || 0),
        rowSubtotalBeforeGst: Number(
          productionCalc.rowSubtotalBeforeGst ?? productionCalc.rowTotal ?? 0
        ),
        gstRate: resolveCurtainSavedRowGstRate(row),
        fabricMeters: productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        customerBillableFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        fullOrderedFabricMeters:
          productionCalc.fullOrderedFabricMeters ??
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        noReuseOrderedFabricMeters:
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.customerBillableFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        cutFabricMeters: productionCalc.cutFabricMeters ?? productionCalc.fabricMeters,
        workshopFabricRequiredMeters:
          productionCalc.workshopFabricRequiredMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        workshopNewFabricMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalNewFabricNeededMeters:
          productionCalc.internalNewFabricNeededMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        orderedFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        newFabricNeededMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalHalfPieceSavingMeters: productionCalc.internalHalfPieceSavingMeters || 0,
        internalHalfPieceSavingValue: productionCalc.internalHalfPieceSavingValue || 0,
        halfReusedMeters: productionCalc.halfReusedMeters || 0,
        internalHalfReusedMeters:
          productionCalc.internalHalfReusedMeters ?? productionCalc.halfReusedMeters ?? 0,
        halfPiecesSaved: productionCalc.halfPiecesSaved || 0,
        halfPiecesReused: productionCalc.halfPiecesReused || 0,
      };
    });
    const rowsForSave = sanitizeCurtainRowsEditableTextFields(
      attachProductSnapshotsToCurtainRows(productionRowsForSave)
    );
    const curtainRowValidationDetail = validateCurtainRowsForSave({
      rows: rowsForSave,
      quoteNo,
      quoteId: "",
      saveMode: "save_as_new",
    });
    currentQuoteFullnessFields = { ...currentQuoteFullnessFields,
      ...rebaseQuoteConstructionIdentities(currentQuoteFullnessFields, rows, rowsForSave) };
    if (curtainRowValidationDetail) {
      handleQuoteRowValidationFailure(curtainRowValidationDetail, {
        source: "saveQuotationAsNew",
      });
      return false;
    }
    const normalizedFabricSummaryMeta = sanitizeFabricSummaryMetaForValidCatalogues(
      fabricSummaryMeta,
      repeatSettings,
      rowsForSave
    );
    const quoteCalculationSnapshot = buildCurtainQuoteCalculationSnapshot({
      rows,
      summaryTotals,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    });
    const quoteBlindSqftRoundingMode = normalizeBlindSqftRoundingMode(
      rowsForSave.find((row) => row?.sqftCalculation?.roundingMode)
        ?.sqftCalculation?.roundingMode ||
        selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
        fabricCalcSettings?.blindSqftRoundingMode,
      "exact"
    );
    const currentCurtainCalculationSettings = {
      ...(selectedQuote?.calculationSettings || {}),
      blindSqftRoundingMode: quoteBlindSqftRoundingMode,
      fullnessMode: "quote_override_supported",
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };
    const currentCalculationMetadata = {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
    };
    const recalculationFieldsForSave = resolveCurtainRecalculationFieldsForSave();
    const legacyRevisionAuditForSave =
      pendingRecalculationAudit?.action === "save_new_revision"
        ? pendingRecalculationAudit
        : null;
    const normalizedQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    if (normalizedQuoteDate && normalizedQuoteDate !== date) {
      setDate(normalizedQuoteDate);
    }

    /* =================================================
       GENERATE NEW QUOTE NUMBER (TRANSACTION SAFE)
       ================================================= */

    const parentQuoteNoForRevision = String(
      legacyRevisionAuditForSave?.revisionOfQuoteNo ||
        selectedQuote?.quoteNo ||
        quoteNo ||
        ""
    ).trim();
    const revisionBaseQuoteNo =
      parentQuoteNoForRevision.replace(/-R\d+$/i, "") ||
      parentQuoteNoForRevision ||
      quoteNo ||
      "";
    const existingRevisionMatch = parentQuoteNoForRevision.match(/-R(\d+)$/i);
    let nextRevisionNo = legacyRevisionAuditForSave
      ? Number(existingRevisionMatch?.[1] || selectedQuote?.revisionNo || 0) + 1
      : 0;
    let newQuoteNo = legacyRevisionAuditForSave
      ? `${revisionBaseQuoteNo}-R${nextRevisionNo}`
      : await generateQuoteNumber(workspaceUid, "curtain");
    if (legacyRevisionAuditForSave) {
      let attempts = 0;
      while (attempts < 10) {
        const candidateRef = doc(db, "users", workspaceUid, "quotations", newQuoteNo);
        const candidateSnap = await getDoc(candidateRef);
        if (!candidateSnap.exists()) break;
        attempts += 1;
        nextRevisionNo += 1;
        newQuoteNo = `${revisionBaseQuoteNo}-R${nextRevisionNo}`;
      }
      if (attempts >= 10) {
        newQuoteNo = `${revisionBaseQuoteNo}-R${nextRevisionNo}-${Date.now()}`;
      }
    }
    const legacyRevisionFieldsForSave = legacyRevisionAuditForSave
      ? {
          parentQuoteNo: revisionBaseQuoteNo,
          parentQuoteId:
            legacyRevisionAuditForSave.revisionOfQuoteId ||
            selectedQuote?.id ||
            editingQuoteId ||
            "",
          revisionNo: nextRevisionNo,
          revisionReason: "Recalculated with current formula",
          createdFromLegacyCalculation: true,
        }
      : {};

    setQuoteNo(newQuoteNo);

    /* =================================================
       CREATE NEW QUOTATION DOCUMENT
       ================================================= */

    const quoteDocId = newQuoteNo;

    const ref = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );
    const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
    const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
      liveActionPayload
    );
    const customerLedger = await ensureCustomerLedgerRecord(
      curtainIdentity,
      {
        customerId: selectedCurtainCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: newQuoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteDate: normalizedQuoteDate,
          customerId: selectedCurtainCustomerId,
          customerName: curtainIdentity.customerName,
          grandTotal: Number(summaryTotals.grandTotal || 0),
          status: "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      ...curtainIdentity,
      customerId: selectedCurtainCustomerId,
    });
    const quoteSiteFields = buildQuoteSiteFieldsForSave(
      liveQuoteSiteSelection || quoteSiteSelection,
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        quoteNo: newQuoteNo,
        ensureSiteId: true,
      }
    );
    const curtainIdentityFields = buildQuoteIdentityFieldsForSave(
      {
        ...selectedQuote,
        ...customerFields,
        ...quoteSiteFields,
        quoteNo: newQuoteNo,
      },
      {
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerEmail: customerFields.customerEmail,
        quoteNo: newQuoteNo,
      }
    );
    const linkedLeadId = pendingCrmLeadId || selectedQuote?.crm?.leadId || "";
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const newStageId =
      linkedLead?.stageId || getQuoteGivenStageIdForType(CURTAIN_QUOTE_TYPE);
    const newStatus = linkedLead?.status || "draft";
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...legacyRevisionFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: newStatus,
        workStage: newStageId,
        installationDate: linkedLead?.installationDate || null,
        quoteType: CURTAIN_QUOTE_TYPE,
      },
      {
        stageId: newStageId,
        status: newStatus,
        installationDate: linkedLead?.installationDate || null,
        expectedValue: Number(summaryTotals.grandTotal || 0),
        finalValue: Number(summaryTotals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contact: {
          name:
            curtainIdentityFields.contactPersonName ||
            customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchSource = {
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,
      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: newStatus,
      workStage: newStageId,
      installationDate: linkedLead?.installationDate || null,
      crm: crmPayload,
    };
    const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

    const curtainRevisionPayload = sanitizeCrmFirestoreValue({
      schemaVersion: QUOTE_STORAGE_SCHEMA_VERSION,
      quoteType: CURTAIN_QUOTE_TYPE,
      normalizedQuoteType: CANONICAL_CURTAIN_QUOTE_TYPE,
      contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
      canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,

      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,

      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,

      rows: rowsForSave,
      curtainRows: rowsForSave,
      quoteRows: rowsForSave,
      measurementRows: rowsForSave,
      quotationRows: rowsForSave,
      items: rowsForSave,

      repeatSettings: Object.fromEntries(
        Object.entries(repeatSettings).map(([k,v]) => [
          normalizeFabricKey(k),
          normalizeCurtainRepeatEntry(v)
        ])
      ),
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,

      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),

      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,

      status: newStatus,
      workStage: newStageId,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields(quoteSearchSource),

      installationDate: linkedLead?.installationDate || null,

      createdAt: serverTimestamp(),
      contentUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),

    });
    traceQuoteIdentity("curtainSaveAsNewPayloadBuild", curtainRevisionPayload, {
      quoteId: quoteDocId,
      quoteNo: newQuoteNo,
      quoteIndexCustomerName: customerFields.customerName,
      quoteIndexSiteName: quoteSiteFields.siteName,
    });

    if (isDevelopmentMode()) {
      const deletedLocalIds = uniqueValues(deletedCurtainRowIdsRef.current || []);
      devLog("QUOTE_SAVE_CONTENT_TRACE", {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteNo: newQuoteNo,
        quoteId: quoteDocId,
        action: "save_as_new",
        liveRowCount: rows.length,
        liveItemCount: 0,
        payloadItemsCount: Array.isArray(curtainRevisionPayload.items)
          ? curtainRevisionPayload.items.length
          : 0,
        payloadRowsCount: Array.isArray(curtainRevisionPayload.rows)
          ? curtainRevisionPayload.rows.length
          : 0,
        loadedSnapshotItemsCount: Array.isArray(selectedQuote?.items)
          ? selectedQuote.items.length
          : 0,
        loadedSnapshotRowsCount: getCurtainQuoteCanonicalRows(selectedQuote || {}).length,
        saveSource: "latestRef",
        liveVisibleIds: rows.map((row) => getWorkshopRowStableId(row)),
        liveRefIds: normalizeRowsForState(rowsRef.current || []).map((row) =>
          getWorkshopRowStableId(row)
        ),
        staleLoadedSnapshotIds: getCurtainQuoteCanonicalRows(selectedQuote || {}).map((row) =>
          getWorkshopRowStableId(row)
        ),
        deletedLocalIds,
        payloadItemIds: Array.isArray(curtainRevisionPayload.items)
          ? curtainRevisionPayload.items.map((row) => getWorkshopRowStableId(row))
          : [],
        payloadRowIds: rowsForSave.map((row) => getWorkshopRowStableId(row)),
        writePath: ref.path,
        mergeUsed: false,
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
      });
    }

    markQuoteSaveInProgressSession({
      traceId,
      action: "save_as_new",
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo: newQuoteNo,
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: ref.path,
    });
    recordSaveTracePatch(traceId, {
      quoteNo: sanitizeSaveTraceText(newQuoteNo || "", 80),
      quoteId: quoteDocId,
      quoteIndexId: quoteDocId,
      sourcePath: ref.path,
      customerName: sanitizeSaveTraceText(customerFields.customerName || customerName || "", 160),
      siteName: sanitizeSaveTraceText(quoteSiteFields.siteName || "", 160),
      requiredWrites: {
        mainQuote: "not_attempted",
        quoteIndex: "not_attempted",
      },
    });

    try {
      await persistNewQuotationWithTrialGuard({
        quoteId: quoteDocId,
        quotePayload: curtainRevisionPayload,
        mode: "save_as_new",
        quoteIndexSource: {
          ...quoteSearchSource,
          sourceQuoteId: quoteDocId,
        },
        traceId,
      });
      requiredWritesSucceeded = true;
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "success",
          quoteIndex: "success",
        },
      });
      traceReferralPartner("save_curtain_as_new", {
        quoteId: quoteDocId,
        quoteNo: newQuoteNo,
        partner: curtainReferralPartnerFields.referralPartner,
        savedToQuote: true,
        savedToQuoteIndex: true,
        hiddenFromCustomerPdf: true,
      });
      recordSaveTraceQuoteIndexSnapshot(traceId, quoteDocId, {
        quoteId: quoteDocId,
        quoteNo: newQuoteNo,
        customerName: customerFields.customerName || customerName || "",
        siteName: quoteSiteFields.siteName || "",
        quoteType: CURTAIN_QUOTE_TYPE,
        quotationType: CURTAIN_QUOTE_TYPE,
        sourcePath: ref.path,
      });
      logRequiredSaveCompletedBeforeUiPatch({
        quoteNo: newQuoteNo,
        quoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        sourcePath: ref.path,
      });
      await logQuoteSavedActivity({
        activityType: "quote_created",
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteId: quoteDocId,
        quoteNo: newQuoteNo,
        customerId: customerFields.customerId || "",
        customerName: customerFields.customerName || customerName || "",
        opportunityId: crmPayload.opportunityId || crmPayload.leadId || pendingCrmLeadId || "",
        totalAmount: Number(summaryTotals.grandTotal || 0),
      }).catch(() => {});
      await upsertQuoteLocalRecord({
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...legacyRevisionFieldsForSave,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: newStatus,
        workStage: newStageId,
        installationDate: linkedLead?.installationDate || null,
        crm: crmPayload,
        createdAt: new Date(),
        updatedAt: new Date(),
      }, { persistIndex: false, traceId, syncDerived: false });
      setEditingQuoteId(quoteDocId);
      setGeneralEditingQuoteId(null);
      setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
      setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);
      setIsNewQuote(false);
      setAutoSaveStatus("saved");
      {
        const deletedIdsForVerification = uniqueValues(deletedCurtainRowIdsRef.current || []);
        const shouldVerifyContentSave =
          deletedIdsForVerification.length > 0 || isDevelopmentMode();
        if (shouldVerifyContentSave) {
        const savedSnap = await readCompatibleQuoteDocument({ ref, workspaceUid,
          getDoc: r => getDoc(r, { label: "curtainQuoteSaveAsNewReadback" }),
          callLoader: data => httpsCallable(functions, 'loadQuotationCanonical')(data) });
        const savedData = savedSnap.exists()
          ? await hydrateCurtainQuoteRowsFromChunks({
              quote: { id: savedSnap.id, ...savedSnap.data() },
              workspaceUid,
              label: "curtainQuoteSaveAsNewReadback.rowChunks",
            })
          : null;
        const readBackContentSelection = resolveCurtainQuoteContentSelection(savedData || {});
        const readBackRows = readBackContentSelection.items;
        const indexSnap = await getDoc(
          doc(db, "users", workspaceUid, "quote_index", quoteDocId),
          { label: "curtainQuoteIndexSaveAsNewReadback" }
        );
        const indexData = indexSnap.exists() ? indexSnap.data() : null;
        const readBackGrandTotal = Number(
          savedData?.grandTotal ??
            savedData?.finalTotal ??
            savedData?.total ??
            savedData?.totalAmount ??
            0
        );
        const quoteIndexGrandTotal = Number(
          indexData?.grandTotal ?? indexData?.totalAmount ?? indexData?.total ?? 0
        );
        const liveGrandTotal = Number(summaryTotals.grandTotal || 0);
        const failures = [];
        const expectedLiningRowIds = rowsForSave
          .filter((row) => row?.lining?.enabled === true)
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean);
        const readBackLiningRowIds = readBackRows
          .filter((row) => row?.lining?.enabled === true)
          .map((row) => getWorkshopRowStableId(row))
          .filter(Boolean);
        if (!savedSnap.exists()) failures.push("full_quote_missing");
        if (!indexSnap.exists()) failures.push("quote_index_missing");
        if (readBackRows.length !== rowsForSave.length) {
          failures.push("full_quote_row_count_mismatch");
        }
        if (
          expectedLiningRowIds.length !== readBackLiningRowIds.length ||
          expectedLiningRowIds.some((rowId) => !readBackLiningRowIds.includes(rowId))
        ) {
          failures.push("lining_row_persistence_mismatch");
        }
        if (Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0) !== rowsForSave.length) {
          failures.push("quote_index_row_count_mismatch");
        }
        if (Math.abs(readBackGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("full_quote_total_mismatch");
        }
        if (Math.abs(quoteIndexGrandTotal - liveGrandTotal) > 0.05) {
          failures.push("quote_index_total_mismatch");
        }
        const contentInspection = inspectDeletedIdsInContentAliases(
          getCurtainQuoteContentAliasArrays(savedData || {}),
          deletedIdsForVerification,
          getWorkshopRowStableId
        );
        const deletedIdsStillPresent = contentInspection.deletedIdsStillPresent;
        devLog("QUOTE_SAVE_VERIFY_READBACK", {
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteId: quoteDocId,
          quoteNo: newQuoteNo,
          liveCount: rowsForSave.length,
          readBackSelectedField: readBackContentSelection.field || "empty",
          readBackSelectedCount: readBackRows.length,
          expectedLiningRowIds,
          readBackLiningRowIds,
          quoteIndexCount: Number(indexData?.rowCount ?? indexData?.contentRowCount ?? 0),
          liveGrandTotal,
          readBackGrandTotal,
          quoteIndexGrandTotal,
          failures,
          deletedIds: deletedIdsForVerification,
          canonicalField: CURTAIN_QUOTE_CANONICAL_CONTENT_FIELD,
          canonicalIds: getContentAliasIds(readBackRows, getWorkshopRowStableId),
          aliasFindings: contentInspection.aliasFindings,
          aliasCounts: contentInspection.aliasCounts,
          savedPayloadItemCount: Array.isArray(curtainRevisionPayload.items)
            ? curtainRevisionPayload.items.length
            : 0,
          savedPayloadRowCount: rowsForSave.length,
          readBackItemCount: Array.isArray(savedData?.items) ? savedData.items.length : 0,
          readBackRowCount: readBackRows.length,
          deletedIdsStillPresent,
          pass: contentInspection.pass && failures.length === 0,
          contentSchemaVersion: savedData?.contentSchemaVersion || "",
          canonicalContentField: savedData?.canonicalContentField || "",
        });
        if (!contentInspection.pass) {
          throw new Error("Curtain quote save-as-new verification failed: deleted row still present.");
        }
        if (failures.length > 0) {
          throw new Error(`Curtain quote save-as-new verification failed: ${failures.join(", ")}`);
        }
      }
      }
      notify("Quotation saved.", "success");
      deletedCurtainRowIdsRef.current = [];
    } catch (writeError) {
      recordSaveTracePatch(traceId, {
        requiredWrites: {
          mainQuote: "failed",
          quoteIndex: "failed",
        },
        finalResult: "required_write_failed",
        userMessageShown: "Could not save quotation. Please try again.",
      });
      appendSaveTraceError(traceId, "curtain_save_as_new_required_quote_or_index_write", writeError, {
        path: `${ref.path} + users/${workspaceUid}/quote_index/${quoteDocId}`,
        payloadKeys: Object.keys(curtainRevisionPayload || {}),
      });
      throw writeError;
    }

    try {
      await withQuoteSaveTimeout(
        persistQuoteSiteForCustomer(
          customerFields.customerId,
          customerFields,
          quoteSiteFields
        ),
        { step: "optional.curtainSaveAsNew.siteDoc", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
      );
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          siteDoc: "success",
        },
      });
    } catch (siteError) {
      optionalWriteFailed = true;
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          siteDoc: "failed",
        },
      });
      appendSaveTraceError(traceId, "curtain_save_as_new_siteDoc", siteError, {
        path:
          workspaceUid && customerFields.customerId
            ? `users/${workspaceUid}/customers/${customerFields.customerId}/sites`
            : "",
      });
      devWarn("QUOTE_SITE_OPTIONAL_WRITE_SKIPPED", {
        quoteType: CURTAIN_QUOTE_TYPE,
        quoteId: quoteDocId,
        customerId: customerFields.customerId,
        errorCode: siteError?.code || "",
        errorMessage: siteError?.message || String(siteError),
      });
      notify(
        "Quotation saved, but the customer site library could not be updated.",
        "warning",
        5200
      );
    }

    setEditingQuoteId(quoteDocId);
    setGeneralEditingQuoteId(null);
    setCurtainQuoteEditorMode(QUOTE_EDITOR_MODE_EDITING_EXISTING);
    setGeneralQuoteEditorMode(QUOTE_EDITOR_MODE_NEW);

    let crmCustomerWriteStatus = linkedLeadId ? "not_attempted" : "skipped";
    if (linkedLeadId) {
      try {
        await withQuoteSaveTimeout(
          saveCrmFieldsForLead(
            linkedLeadId,
            {
            quoteType: CURTAIN_QUOTE_TYPE,
            stageId: newStageId,
            status: newStatus,
            linkedQuotes: [
              {
                quoteId: quoteDocId,
                quoteNo: newQuoteNo,
                type: CURTAIN_QUOTE_TYPE,
                value: Number(summaryTotals.grandTotal || 0),
                linkedQuoteDate: normalizedQuoteDate,
                isPrimary: true,
              },
            ],
            hasQuote: true,
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            expectedValue: Number(summaryTotals.grandTotal || 0),
            finalValue: Number(summaryTotals.grandTotal || 0),
            installationDate: linkedLead?.installationDate || null,
            },
            { syncQuote: false }
          ),
          { step: "optional.curtainSaveAsNew.crmCustomer", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
        );
        setPendingCrmLeadId("");
        crmCustomerWriteStatus = "success";
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "success",
          },
        });
      } catch (error) {
        optionalWriteFailed = true;
        crmCustomerWriteStatus = "failed";
        recordSaveTracePatch(traceId, {
          optionalWrites: {
            crmCustomer: "failed",
          },
        });
        appendSaveTraceError(traceId, "curtain_save_as_new_crm_customer", error, {
          path: workspaceUid && linkedLeadId ? `users/${workspaceUid}/crm_leads/${linkedLeadId}` : "",
        });
        devWarn("QUOTE_SAVE_OPTIONAL_WRITE_SKIPPED", {
          step: "crm_customer",
          path: workspaceUid && linkedLeadId ? `users/${workspaceUid}/crm_leads/${linkedLeadId}` : "",
          errorCode: error?.code || "",
          errorMessage: error?.message || String(error),
        });
      }
    } else {
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmCustomer: "skipped",
        },
      });
    }

    let finalCrmPayload = crmPayload;
    let finalWorkStage = newStageId;
    let finalQuoteStatus = newStatus;
    try {
      const crmDealLinkResult = await withQuoteSaveTimeout(
        ensureSavedQuoteCrmDeal(
          {
          id: quoteDocId,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...customerFields,
          ...quoteSiteFields,
          ...curtainIdentityFields,
          ...curtainReferralPartnerFields,
          quoteNo: newQuoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...legacyRevisionFieldsForSave,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: newStatus,
          workStage: newStageId,
          installationDate: linkedLead?.installationDate || null,
          crm: crmPayload,
          },
          {
          quoteNo: newQuoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          totalValue: Number(summaryTotals.grandTotal || 0),
          customerFields,
          linkedLeadId,
          showSaveOutcomeToast: false,
          }
        ),
        { step: "optional.curtainSaveAsNew.crmDealLink", timeoutMs: QUOTE_SAVE_OPTIONAL_TIMEOUT_MS }
      );
      finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
      finalWorkStage = crmDealLinkResult?.stageId || newStageId;
      finalQuoteStatus = crmDealLinkResult?.status || newStatus;
      const crmDealLinkStatus =
        crmDealLinkResult?.action === "skipped" ||
        crmDealLinkResult?.action === "saveOnly"
          ? "skipped"
          : "success";
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmDealLink: crmDealLinkStatus,
        },
      });
    } catch (error) {
      optionalWriteFailed =
        Boolean(linkedLeadId) || crmSettings.autoCreateDealFromQuotation !== false;
      recordSaveTracePatch(traceId, {
        optionalWrites: {
          crmCustomer: crmCustomerWriteStatus,
          crmDealLink:
            linkedLeadId || crmSettings.autoCreateDealFromQuotation !== false
              ? "failed"
              : "skipped",
        },
      });
      appendSaveTraceError(traceId, "curtain_save_as_new_crm_deal_link", error, {
        path: workspaceUid ? `users/${workspaceUid}/crm_leads` : "",
      });
      devWarn("QUOTE_SAVE_OPTIONAL_WRITE_SKIPPED", {
        step: "crm_link_after_required_quote_save",
        path: workspaceUid ? `users/${workspaceUid}/crm_leads` : "",
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      });
      if (linkedLeadId) {
        notify(
          "Quotation saved. CRM link could not be updated.",
          "warning",
          5200
        );
      }
    }
    const finalInstallationDate =
      finalCrmPayload.installationDate || linkedLead?.installationDate || null;

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      ...quoteSiteFields,
      ...curtainIdentityFields,
      ...curtainReferralPartnerFields,
      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: finalQuoteStatus,
      workStage: finalWorkStage,
      installationDate: finalInstallationDate,
      crm: finalCrmPayload,
      createdAt: new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false, traceId });
    setQuoteCustomerLink({
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      phone: customerFields.customerPhone,
      whatsapp: customerFields.customerWhatsapp,
      email: customerFields.customerEmail,
      address: customerFields.customerAddress,
      companyName: customerFields.customerCompanyName,
    });
    setQuoteSiteSelection(normalizeQuoteSiteSnapshot(quoteSiteFields));
    if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
      setSelectedQuote({
        id: quoteDocId,
        quoteId: quoteDocId,
        sourceQuoteId: quoteDocId,
        fullQuoteId: quoteDocId,
        quoteIndexId: quoteDocId,
        indexId: quoteDocId,
        sourceCollection: "quotations",
        sourcePath: ref.path,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        ...quoteSiteFields,
        ...curtainIdentityFields,
        ...curtainReferralPartnerFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...legacyRevisionFieldsForSave,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
      });
      markCurtainQuoteBaseline(
        {
          ...buildCurtainQuoteDraftData(),
          quoteId: quoteDocId,
          editingQuoteId: quoteDocId,
          quoteNo: newQuoteNo,
          quoteSiteSelection: normalizeQuoteSiteSnapshot(quoteSiteFields),
          ...normalizeQuoteSiteSnapshot(quoteSiteFields),
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          selectedQuoteStatus: finalQuoteStatus,
          selectedQuoteWorkStage: finalWorkStage,
          selectedInstallationDate: finalInstallationDate,
        },
        "saved"
      );
      clearCurtainQuoteDraft(previousCurtainDraftKey);
      clearLocalDraft(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          quoteDocId || "new"
        )
      );
    } else if (isQuoteMutationAuditDevMode()) {
      devWarn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
        quoteId: quoteDocId,
        saveStartedEditVersion,
        currentEditVersion: currentQuoteEditVersionRef.current,
      });
    }
    setAutoSaveStatus("saved");
    recordSaveTracePatch(traceId, {
      finalResult: optionalWriteFailed
        ? "optional_write_failed_but_saved"
        : "success",
      userMessageShown: optionalWriteFailed
        ? "Quotation saved, but one optional sync could not be updated."
        : "",
    });
    if (optionalWriteFailed) {
      notify(
        "Quotation saved successfully. Customer history could not be updated. Your quotation is safe. Use Update Customer History in Saved Quotes.",
        "warning",
        7000
      );
    }
    return true;

  } catch (err) {

    devError("QUOTE_SAVE_REQUIRED_FAILED_EXACT", {
      step: "main_quote_or_quote_index",
      errorCode: err?.code || "",
      errorMessage: err?.message || String(err),
      stack: err?.stack || "",
    });

    if (requiredWritesSucceeded) {
      devWarn("CURTAIN_SAVE_AS_NEW_POST_REQUIRED_SAVE_OPTIONAL_FAILED", {
        errorCode: err?.code || "",
        errorMessage: err?.message || String(err),
        workspaceUid,
      });
      setAutoSaveStatus("saved");
      recordSaveTracePatch(traceId, {
        finalResult: "optional_write_failed_but_saved",
        userMessageShown: "",
      });
      appendSaveTraceError(traceId, "curtain_save_as_new_post_required_optional", err, {
        path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
      });
      return true;
    }

    const rowValidationDetail = getQuoteRowValidationDetailFromError(err);
    if (rowValidationDetail) {
      handleQuoteRowValidationFailure(rowValidationDetail, {
        source: "saveQuotationAsNew.outerCatch",
      });
      recordSaveTracePatch(traceId, {
        finalResult: "required_write_failed",
        failedStep: err?.__fqRequiredSaveStep || "",
        userMessageShown: rowValidationDetail.message || "Quotation row data is invalid.",
      });
      appendSaveTraceError(traceId, "curtain_save_as_new_row_validation", err, {
        path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
      });
      return false;
    }

    setAutoSaveStatus("error");
    const userMessage = getSaveQuotationFriendlyError(err);
    recordSaveTracePatch(traceId, {
      finalResult: requiredWritesSucceeded
        ? "optional_write_failed_but_saved"
        : "required_write_failed",
      userMessageShown: userMessage,
    });
    appendSaveTraceError(traceId, "curtain_save_as_new_outer_catch", err, {
      path: workspaceUid ? `users/${workspaceUid}/quotations` : "",
    });

    alert(userMessage);
    return false;

  } finally {

    setIsSaving(false);

  }

};
saveQuotationAsNewRef.current = saveQuotationAsNew;
// ================= SAVE FABRIC STOCK (W1 VERSION  FIXED) =================
// --------------------------- GRAND TOTALS & SUMMARY --------------------------- //

const buildCurtainFabricSummary = useCallback(
  (sourceRows = [], options = {}) => {
    const {
      activeRepeatSettings = repeatSettings,
      halfPieceReuseEnabled = useHalfPieceReuse,
      passSavingsToCustomer = passHalfPieceSavingsToCustomer,
      targetFabricKey = null,
      debugTag = "quote-screen",
      useProductWorkshopRules = false,
      productConfigsForRules = productConfigs,
      fabricCalcSettingsForRules = fabricCalcSettings,
      quoteFullnessOverridesForCalculation = quoteFullnessOverrides,
    } = options;

    const sourceSummaryRows = (Array.isArray(sourceRows) ? sourceRows : [])
      .map((row, index) => withCurtainRowId(row, index))
      .filter((row) => !isDeletedWorkshopExportRow(row))
      .filter(hasMeaningfulWorkshopExportData);

    const plan = buildReuseAwareDemands(sourceSummaryRows, activeRepeatSettings, {
      targetFabricKey,
      enableReuse: halfPieceReuseEnabled,
      debugTag,
      useProductWorkshopRules,
      productConfigsForRules,
      fabricCalcSettingsForRules,
      quoteFullnessOverridesForCalculation,
    });

    const noReusePlan = halfPieceReuseEnabled
      ? buildReuseAwareDemands(sourceSummaryRows, activeRepeatSettings, {
          targetFabricKey,
          enableReuse: false,
          debugTag: `${debugTag}-full-order`,
          useProductWorkshopRules,
          productConfigsForRules,
          fabricCalcSettingsForRules,
          quoteFullnessOverridesForCalculation,
        })
      : plan;

    const sumRowPlanMetersByFabricKey = (rowPlans = [], fieldNames = []) =>
      (Array.isArray(rowPlans) ? rowPlans : []).reduce((acc, rowPlan) => {
        if (!rowPlan) return acc;
        const fabricKey = normalizeFabricKey(rowPlan.fabricKey || "");
        if (!fabricKey) return acc;
        const meters = fieldNames.reduce((resolved, fieldName) => {
          if (Number.isFinite(Number(resolved))) return resolved;
          return rowPlan[fieldName];
        }, undefined);
        acc[fabricKey] = Number(acc[fabricKey] || 0) + Number(meters || 0);
        return acc;
      }, {});

    const fabricToOrderByKey = sumRowPlanMetersByFabricKey(plan.rowPlans, [
      "newFabricNeededMeters",
      "newMeters",
      "orderedFabricMeters",
    ]);
    const fullOrderByKey = sumRowPlanMetersByFabricKey(noReusePlan.rowPlans, [
      "newFabricNeededMeters",
      "newMeters",
      "orderedFabricMeters",
    ]);

    const allFabricKeys = Array.from(
      new Set([
        ...Object.keys(plan.fabricSummaryByKey || {}),
        ...Object.keys(noReusePlan.fabricSummaryByKey || {}),
      ])
    );
    const fabricSummaries = Object.fromEntries(
      allFabricKeys
        .map((fabricKey) => {
          const summary = plan.fabricSummaryByKey?.[fabricKey] || {};
          const fullSummary = noReusePlan.fabricSummaryByKey?.[fabricKey] || summary;
          const normalizedFabricKey = normalizeFabricKey(fabricKey);
          const rowAllocatedFabricToOrderMeters = fabricToOrderByKey[normalizedFabricKey];
          const rowAllocatedFullOrderMeters = fullOrderByKey[normalizedFabricKey];
          const newFabricNeededMetersRaw =
            Number(
              halfPieceReuseEnabled && Number.isFinite(Number(rowAllocatedFabricToOrderMeters))
                ? rowAllocatedFabricToOrderMeters
                : !halfPieceReuseEnabled && Number.isFinite(Number(rowAllocatedFullOrderMeters))
                ? rowAllocatedFullOrderMeters
                : halfPieceReuseEnabled
                ? summary.newFabricNeededMeters ?? summary.orderedFabricMeters ?? 0
                : fullSummary.newFabricNeededMeters ?? fullSummary.orderedFabricMeters ?? 0
            );
          const fullOrderedFabricMetersRaw =
            Number(
              (Number.isFinite(Number(rowAllocatedFullOrderMeters))
                ? rowAllocatedFullOrderMeters
                : undefined) ??
                fullSummary.newFabricNeededMeters ??
                fullSummary.orderedFabricMeters ??
                summary.newFabricNeededMeters ??
                summary.orderedFabricMeters ??
                0
            );
          const workshopFabricRequiredMetersRaw = halfPieceReuseEnabled
            ? newFabricNeededMetersRaw
            : fullOrderedFabricMetersRaw;
          const customerBillableFabricMetersRaw =
            halfPieceReuseEnabled && passSavingsToCustomer
              ? workshopFabricRequiredMetersRaw
              : fullOrderedFabricMetersRaw;
          const cutFabricMetersRaw = Number(
            summary.cutFabricMeters ?? fullSummary.cutFabricMeters ?? 0
          );
          const halfReusedMetersRaw = halfPieceReuseEnabled
            ? Number(summary.halfReusedMeters || 0)
            : 0;
          const displayMeters = normalizeFabricSummaryMeterDisplay({
            fabricToOrderRaw: workshopFabricRequiredMetersRaw,
            customerBillableRaw: customerBillableFabricMetersRaw,
            halfPieceSavingRaw: halfReusedMetersRaw,
          });
          const cutFabricMeters = roundMetersForDisplay(cutFabricMetersRaw);
          const newFabricNeededMeters = roundMetersForDisplay(newFabricNeededMetersRaw);
          const fullOrderedFabricMeters = roundMetersForDisplay(fullOrderedFabricMetersRaw);
          const workshopFabricRequiredMeters = displayMeters.fabricToOrderMeters;
          const customerBillableFabricMeters = displayMeters.customerBillableFabricMeters;
          const halfReusedMeters = displayMeters.halfPieceSavingMeters;
          if (
            cutFabricMeters <= 0 &&
            fullOrderedFabricMeters <= 0 &&
            workshopFabricRequiredMeters <= 0 &&
            customerBillableFabricMeters <= 0
          ) {
            return null;
          }
          return [
            fabricKey,
            {
              ...summary,
              fabricKey,
              fabricLabel:
                summary.fabricLabel ||
                fullSummary.fabricLabel ||
                summary.displayName ||
                fullSummary.displayName ||
                fabricKey,
              displayName:
                summary.displayName ||
                fullSummary.displayName ||
                summary.fabricLabel ||
                fullSummary.fabricLabel ||
                fabricKey,
              fabricWidthIn:
                normalizeFabricWidthIn(summary.fabricWidthIn) ||
                normalizeFabricWidthIn(fullSummary.fabricWidthIn) ||
                null,
              fabricWidthSource:
                summary.fabricWidthSource || fullSummary.fabricWidthSource || "",
              fabricWidthManuallyOverridden:
                summary.fabricWidthManuallyOverridden === true ||
                fullSummary.fabricWidthManuallyOverridden === true,
              catalogueDefaultFabricWidthIn:
                normalizeFabricWidthIn(summary.catalogueDefaultFabricWidthIn) ||
                normalizeFabricWidthIn(fullSummary.catalogueDefaultFabricWidthIn) ||
                null,
              srRuleFabricWidthIn:
                normalizeFabricWidthIn(summary.srRuleFabricWidthIn) ||
                normalizeFabricWidthIn(fullSummary.srRuleFabricWidthIn) ||
                null,
              fabricWidthValues: Array.from(
                new Set(
                  [
                    ...(summary.fabricWidthValues || []),
                    ...(fullSummary.fabricWidthValues || []),
                  ]
                    .map((value) => normalizeFabricWidthIn(value))
                    .filter(Boolean)
                )
              ),
              hasMixedFabricWidths:
                summary.hasMixedFabricWidths === true ||
                fullSummary.hasMixedFabricWidths === true,
              cutFabricMeters,
              cutFabricMetersRaw,
              halfReusedMeters,
              halfPieceSavingMetersRaw: displayMeters.halfPieceSavingMetersRaw,
              fabricToOrderMeters: workshopFabricRequiredMeters,
              fabricToOrderMetersRaw: displayMeters.fabricToOrderMetersRaw,
              newFabricNeededMeters,
              newFabricNeededMetersRaw,
              fullOrderedFabricMeters,
              fullOrderedFabricMetersRaw,
              customerBillableFabricMeters,
              customerBillableFabricMetersRaw:
                displayMeters.customerBillableFabricMetersRaw,
              workshopFabricRequiredMeters,
              workshopFabricRequiredMetersRaw:
                displayMeters.fabricToOrderMetersRaw,
              roundingDriftNormalized: displayMeters.roundingDriftNormalized,
              roundingDriftMeters: displayMeters.roundingDriftMeters,
              meters: workshopFabricRequiredMeters,
              orderedFabricMeters: fullOrderedFabricMeters,
            },
          ];
        })
        .filter(Boolean)
    );

    const productionRows = (plan.rowPlans || []).filter(Boolean).map((rowPlan) => ({
      ...rowPlan,
      item: sourceSummaryRows[rowPlan.rowIndex]?.item || sourceSummaryRows[rowPlan.rowIndex]?.name || "",
      fabric: rowPlan.fabricLabel,
      panels: rowPlan.totalPieces,
      halfPiecesSaved: rowPlan.createdLeftoverId ? 1 : 0,
      halfPiecesReused: Number(rowPlan.reusedHalfCount || 0),
      fabricSource: rowPlan.createdLeftoverId
        ? "new-cut-saved-half"
        : rowPlan.reusedLeftoverId
        ? "reused-half-piece"
        : "new-cut",
    }));

    if (IS_DEV_UI_ENABLED) {
      devLog("HALF_PIECE_SUMMARY_TRACE", {
        enabled: halfPieceReuseEnabled,
        passSavingsToCustomer,
        audit: plan.audit || null,
        fabricSummaries,
        rows: productionRows.map((row) => ({
          item: row.item,
          fabric: row.fabricLabel,
          panels: row.totalPieces,
          cutHeightIn: row.cutHeightIn,
          newFullCuts: row.newFullCuts,
          halfPiecesSaved: row.halfPiecesSaved,
          halfPiecesReused: row.halfPiecesReused,
          cutFabricMeters: row.cutFabricMeters,
          newFabricNeededMeters: row.newFabricNeededMeters,
          fabricSource: row.fabricSource,
        })),
      });
    }

    return {
      fabricSummaries,
      productionRows,
      plan,
      noReusePlan,
    };
  },
  // buildReuseAwareDemands is a function declaration in this component; adding
  // it here would make this callback change every render without changing inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    calcRow,
    fabricCalcSettings,
    passHalfPieceSavingsToCustomer,
    productConfigs,
    quoteFullnessOverrides,
    repeatSettings,
    useHalfPieceReuse,
  ]
);

const curtainFabricSummaryResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: useHalfPieceReuse,
      passSavingsToCustomer: passHalfPieceSavingsToCustomer,
      debugTag: "quote-screen",
    }),
  [
    rows,
    repeatSettings,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    buildCurtainFabricSummary,
  ]
);

const curtainNoReuseBillingResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: false,
      debugTag: "customer-billing-no-reuse",
    }),
  [rows, repeatSettings, buildCurtainFabricSummary]
);

const curtainFabricOrderSummaryResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: useHalfPieceReuse,
      passSavingsToCustomer: passHalfPieceSavingsToCustomer,
      debugTag: "quote-screen-fabric-to-order",
      useProductWorkshopRules: true,
    }),
  [
    rows,
    repeatSettings,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    buildCurtainFabricSummary,
  ]
);

const fabricSummaryUI = useMemo(
  () => curtainFabricSummaryResult.fabricSummaries || {},
  [curtainFabricSummaryResult]
);

const fabricOrderSummaryUI = useMemo(
  () => curtainFabricOrderSummaryResult.fabricSummaries || {},
  [curtainFabricOrderSummaryResult]
);

const curtainHalfPieceAudit = useMemo(
  () => curtainFabricSummaryResult?.plan?.audit || null,
  [curtainFabricSummaryResult]
);

const halfPieceAuditResultText = useMemo(() => {
  if (!curtainHalfPieceAudit) return "";
  if (!useHalfPieceReuse) return "Half-piece reuse is off.";
  if (Number(curtainHalfPieceAudit.totalHalfPiecesReused || 0) > 0) {
    return `Half-piece reuse found ${curtainHalfPieceAudit.totalHalfPiecesReused} reused half piece(s), saving ${Number(curtainHalfPieceAudit.totalMetersSaved || 0).toFixed(2)} m for workshop production.`;
  }
  return (
    curtainHalfPieceAudit.reasons?.[0] ||
    "No compatible half pieces were found for the current rows."
  );
}, [curtainHalfPieceAudit, useHalfPieceReuse]);

const canViewHalfPieceAudit =
  IS_DEV_UI_ENABLED;

const copyHalfPieceAuditJson = useCallback(async () => {
  const payload = {
    quoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    audit: curtainHalfPieceAudit,
  };
  const serialized = JSON.stringify(payload, null, 2);
  try {
    await navigator.clipboard.writeText(serialized);
    notify("Half-piece audit JSON copied.", "success");
  } catch (error) {
    devLog("HALF_PIECE_AUDIT_JSON", payload);
    notify("Clipboard unavailable. Half-piece audit JSON logged to console.", "info");
  }
}, [
  curtainHalfPieceAudit,
  notify,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  useHalfPieceReuse,
]);

const getFabricBrandNameById = useCallback(
  (brandId = "") =>
    fabricBrands.find((brand) => brand.id === brandId)?.name || "",
  [fabricBrands]
);

const getCurtainProductionAdjustedRowCalcFromSummary = useCallback(
  (
    row,
    rowIndex = -1,
    activeFabricSummaryResult = null,
    activeNoReuseBillingResult = null
  ) => {
    const calc = calcRow(row, repeatSettings);
    const supportsLining = curtainRowSupportsLining(row, calc);
    if (!calc.isCurtain && !calc.isRoman && !supportsLining) {
      const lining = calculateCurtainRowLining(row, Number(calc.fabricMeters || 0), false);
      return {
        ...calc,
        lining,
        liningAmount: 0,
        customerBillableFabricMeters: Number(calc.fabricMeters || 0),
        workshopNewFabricMeters: Number(calc.fabricMeters || 0),
        cutFabricMeters: Number(calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0),
      };
    }

    if (calc.usesCalculationSnapshot) {
      const snapshot = calc.calculationSnapshot || row.calculationSnapshot || {};
      if (snapshot.legacyFormula || snapshot.calculationVersion !== CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION) {
        return preserveFrozenConstructionRow(calc, row);
      }
      const rowPlan =
        Number.isInteger(rowIndex) && rowIndex >= 0
          ? activeFabricSummaryResult?.plan?.rowPlans?.[rowIndex]
          : null;
      const noReuseRowPlan =
        Number.isInteger(rowIndex) && rowIndex >= 0
          ? activeNoReuseBillingResult?.plan?.rowPlans?.[rowIndex]
          : null;
      const orderedFabricMeters = round2(
        Number(
          snapshot.orderedFabricMeters ??
            calc.orderedFabricMeters ??
            snapshot.customerBillableFabricMeters ??
            calc.customerBillableFabricMeters ??
            calc.fabricMeters ??
            0
        )
      );
      const fullOrderedFabricMeters = round2(
        Number(
          noReuseRowPlan?.newFabricNeededMeters ??
            noReuseRowPlan?.newMeters ??
            snapshot.fullOrderedFabricMeters ??
            snapshot.noReuseOrderedFabricMeters ??
            row.fullOrderedFabricMeters ??
            row.noReuseOrderedFabricMeters ??
            orderedFabricMeters
        )
      );
      const hasActualHalfPieceReuse =
        Number(rowPlan?.reusedHalfCount || 0) > 0 ||
        Number(rowPlan?.halfReusedMeters || 0) > 0;
      const workshopFabricRequiredMeters = round2(
        Number(
          rowPlan?.newFabricNeededMeters ??
            rowPlan?.newMeters ??
            snapshot.workshopFabricRequiredMeters ??
            snapshot.workshopNewFabricMeters ??
            row.workshopFabricRequiredMeters ??
            row.workshopNewFabricMeters ??
            row.newFabricNeededMeters ??
            calc.workshopFabricRequiredMeters ??
            calc.workshopNewFabricMeters ??
            calc.newFabricNeededMeters ??
            fullOrderedFabricMeters
        )
      );
      const passSavingsBillableMeters = hasActualHalfPieceReuse
        ? workshopFabricRequiredMeters
        : fullOrderedFabricMeters;
      const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
        ? passSavingsBillableMeters
        : fullOrderedFabricMeters;
      const cutFabricMeters = round2(
        Number(
          rowPlan?.cutFabricMeters ??
            snapshot.cutFabricMeters ??
            calc.cutFabricMeters ??
            calc.fabricMetersHalf ??
            orderedFabricMeters
        )
      );
      const lineTotal = round2(
        Number(snapshot.lineTotal ?? calc.lineTotal ?? calc.rowTotal ?? 0)
      );
      const discountPercent = Math.min(100, num(row.discount));
      const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
      const previousBillableMeters = round2(
        Number(snapshot.customerBillableFabricMeters ?? orderedFabricMeters)
      );
      const computedPreviousFabricGrossAmount = round2(
        previousBillableMeters > 0 && mrpPerMeter > 0
          ? previousBillableMeters * mrpPerMeter
          : 0
      );
      const storedPreviousFabricGrossAmount = Number(snapshot.fabricGrossAmount ?? 0);
      const previousFabricGrossAmount = round2(
        storedPreviousFabricGrossAmount > 0
          ? storedPreviousFabricGrossAmount
          : computedPreviousFabricGrossAmount
      );
      const previousFabricDiscountAmount = round2(
        Number(
          snapshot.fabricDiscountAmount ??
            (previousFabricGrossAmount * discountPercent) / 100
        )
      );
      const computedPreviousFabricCost = round2(
        Math.max(0, previousFabricGrossAmount - previousFabricDiscountAmount)
      );
      const storedPreviousFabricCost = Number(
        snapshot.fabricCost ?? snapshot.fabricAmount ?? calc.fabricCost ?? 0
      );
      const previousFabricCost = round2(
        storedPreviousFabricCost > 0 ? storedPreviousFabricCost : computedPreviousFabricCost
      );
      const billableUnchanged = Math.abs(customerBillableFabricMeters - previousBillableMeters) < 0.005;
      const fabricGrossAmount = billableUnchanged ? previousFabricGrossAmount : round2(customerBillableFabricMeters * mrpPerMeter);
      const fabricDiscountAmount = billableUnchanged ? previousFabricDiscountAmount : round2(fabricGrossAmount * (discountPercent / 100));
      // Preserve saved cents on reopen. Intentional quantity changes use the
      // canonical net multiplication order, not subtraction of rounded money.
      const customerBillableFabricCost = billableUnchanged
        ? Number(snapshot.fabricCost ?? snapshot.fabricAmount ?? previousFabricCost)
        : round2(customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100));
      const customerBillableRowTotal =
        billableUnchanged ? lineTotal : previousFabricCost > 0
          ? round2(Math.max(0, lineTotal - previousFabricCost + customerBillableFabricCost))
          : lineTotal;
      const lining = calculateCurtainRowLining(
        row,
        customerBillableFabricMeters,
        supportsLining,
        { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
      );

      return {
        ...calc,
        lining,
        liningAmount: Number(
          snapshot.liningAmount ?? row.liningAmount ?? calc.liningAmount ?? lining.amount ?? 0
        ),
        fabricMeters: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        fabricMetersFull: fullOrderedFabricMeters || orderedFabricMeters,
        fabricMetersHalf: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        orderedFabricMeters: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        noReuseOrderedFabricMeters: fullOrderedFabricMeters,
        fullOrderedFabricMeters,
        customerBillableFabricMeters,
        workshopNewFabricMeters: workshopFabricRequiredMeters,
        workshopFabricRequiredMeters,
        internalNewFabricNeededMeters: Number(
          row.internalNewFabricNeededMeters ??
            calc.internalNewFabricNeededMeters ??
            workshopFabricRequiredMeters
        ),
        newFabricNeededMeters: Number(
          snapshot.newFabricNeededMeters ??
            row.newFabricNeededMeters ??
            calc.newFabricNeededMeters ??
            workshopFabricRequiredMeters
        ),
        cutFabricMeters,
        fabricCost: customerBillableFabricCost,
        fabricGrossAmount,
        fabricDiscountPercent: discountPercent,
        fabricDiscountAmount,
        fabricNetAmount: customerBillableFabricCost,
        discountScope: "fabric_only",
        discountBase: fabricGrossAmount,
        discountAmount: fabricDiscountAmount,
        totalPieces: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcsFull ?? 0),
        pcs: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcs ?? 0),
        pcsFull: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcsFull ?? 0),
        pcsHalf: Number(rowPlan?.totalPieces ?? rowPlan?.halfStepPanels ?? snapshot.pieces ?? calc.pcsHalf ?? 0),
        rawPanels: Number(rowPlan?.rawPanels ?? noReuseRowPlan?.rawPanels ?? snapshot.rawPieces ?? 0),
        halfStepProductionPanels: Number(rowPlan?.totalPieces ?? rowPlan?.halfStepPanels ?? calc.pcsHalf ?? 0),
        wholeBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? 0),
        workshopPanels: Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5,
        customerBillingPanels: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? 0),
        calculationVersion: snapshot.calculationVersion || calc.calculationVersion || "",
        rowSubtotalBeforeGst: customerBillableRowTotal,
        gstAmount: round2(customerBillableRowTotal * num(calc.gstRate) / 100),
        rowTotal: customerBillableRowTotal,
        lineTotal: customerBillableRowTotal,
        taxableAmount: customerBillableRowTotal,
      };
    }

    const rowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? activeFabricSummaryResult?.plan?.rowPlans?.[rowIndex]
        : null;
    const noReuseRowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? activeNoReuseBillingResult?.plan?.rowPlans?.[rowIndex]
        : null;
    if (!rowPlan) {
      const fallbackNewFabricMeters = round2(
        Number(
          noReuseRowPlan?.newFabricNeededMeters ??
            noReuseRowPlan?.newMeters ??
            calc.customerBillableFabricMeters ??
            calc.fabricMetersFull ??
            calc.fabricMeters ??
            0
        )
      );
      const fallbackCutFabricMeters = round2(
        Number(noReuseRowPlan?.cutFabricMeters ?? calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0)
      );
      const customerBillableFabricMeters = fallbackNewFabricMeters;
      const discountPercent = Math.min(100, num(row.discount));
      const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
      const customerBillableFabricCost = round2(
        customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100)
      );
      const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
      const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
      const lining = calculateCurtainRowLining(
        row,
        customerBillableFabricMeters,
        supportsLining,
        { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
      );
      const customerBillableRowTotal = round2(
        Number(calc.rowTotal || 0) -
          Number(calc.fabricCost || 0) +
          customerBillableFabricCost +
          Number(lining.amount || 0)
      );
      return {
        ...calc,
        lining,
        liningAmount: Number(lining.amount || 0),
        fabricMeters: customerBillableFabricMeters,
        orderedFabricMeters: customerBillableFabricMeters,
        customerBillableFabricMeters,
        noReuseOrderedFabricMeters: fallbackNewFabricMeters,
        workshopNewFabricMeters: fallbackNewFabricMeters,
        internalNewFabricNeededMeters: fallbackNewFabricMeters,
        newFabricNeededMeters: fallbackNewFabricMeters,
        fullOrderedFabricMeters: fallbackNewFabricMeters,
        workshopFabricRequiredMeters: fallbackNewFabricMeters,
        cutFabricMeters: fallbackCutFabricMeters,
        internalHalfReusedMeters: 0,
        rawPanels: Number(noReuseRowPlan?.rawPanels ?? calc.rawPanels ?? 0),
        halfStepProductionPanels: Number(noReuseRowPlan?.totalPieces ?? noReuseRowPlan?.halfStepPanels ?? calc.pcsHalf ?? 0),
        wholeBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        workshopPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        customerBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        fabricCost: customerBillableFabricCost,
        fabricGrossAmount,
        fabricDiscountPercent: discountPercent,
        fabricDiscountAmount,
        fabricNetAmount: customerBillableFabricCost,
        discountScope: "fabric_only",
        discountBase: fabricGrossAmount,
        discountAmount: fabricDiscountAmount,
        baseLineAmountBeforeDiscount: round2(
          fabricGrossAmount +
            Number(calc.sqftAmount || calc.productCharge || 0) +
            Number(calc.stitchingCost || 0) +
            Number(calc.trackCost || 0) +
            Number(calc.bendAmount || 0) +
            Number(lining.amount || 0)
        ),
        sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
        rowSubtotalBeforeGst: customerBillableRowTotal,
        gstAmount: round2(customerBillableRowTotal * num(calc.gstRate) / 100),
        rowTotal: customerBillableRowTotal,
        lineTotal: customerBillableRowTotal,
        taxableAmount: customerBillableRowTotal,
      };
    }

    const workshopNewFabricMeters = round2(
      Number(rowPlan.newFabricNeededMeters ?? rowPlan.newMeters ?? calc.fabricMeters ?? 0)
    );
    const noReuseOrderedFabricMeters = round2(
      Number(
        noReuseRowPlan?.newFabricNeededMeters ??
          noReuseRowPlan?.newMeters ??
          calc.customerBillableFabricMeters ??
          calc.fabricMetersFull ??
          calc.fabricMeters ??
          0
      )
    );
    const cutFabricMeters = round2(
      Number(rowPlan.cutFabricMeters ?? noReuseRowPlan?.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0)
    );
    const hasActualHalfPieceReuse =
      Number(rowPlan.reusedHalfCount || 0) > 0 ||
      Number(rowPlan.halfReusedMeters || 0) > 0;
    const passSavingsBillableMeters = hasActualHalfPieceReuse
      ? workshopNewFabricMeters
      : noReuseOrderedFabricMeters;
    const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
      ? passSavingsBillableMeters
      : noReuseOrderedFabricMeters;
    const halfStepProductionPanels = Number(
      rowPlan.totalPieces ?? rowPlan.halfStepPanels ?? calc.pcsHalf ?? calc.totalPieces ?? 0
    );
    const wholeBillingPanels = Number(
      noReuseRowPlan?.newFullCuts ??
        noReuseRowPlan?.wholePanels ??
        calc.pcsFull ??
        calc.totalPieces ??
        0
    );
    const workshopPanels = Number(rowPlan.newFullCuts || 0) + Number(rowPlan.reusedHalfCount || 0) * 0.5;
    const customerBillingPanels =
      passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
        ? workshopPanels
        : wholeBillingPanels;
    const discountPercent = Math.min(100, num(row.discount));
    const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
    const customerBillableFabricCost = round2(
      customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100)
    );
    const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
    const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
    const lining = calculateCurtainRowLining(
      row,
      customerBillableFabricMeters,
      supportsLining,
      { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
    );
    const customerBillableRowTotal = round2(
      Number(calc.rowTotal || 0) -
        Number(calc.fabricCost || 0) +
        customerBillableFabricCost +
        Number(lining.amount || 0)
    );
    const internalHalfPieceSavingMeters = round2(
      Math.max(0, noReuseOrderedFabricMeters - workshopNewFabricMeters)
    );
    const internalHalfPieceSavingValue = round2(
      internalHalfPieceSavingMeters * mrpPerMeter * (1 - discountPercent / 100)
    );

    return {
      ...calc,
      lining,
      liningAmount: Number(lining.amount || 0),
      fabricMeters: customerBillableFabricMeters,
      orderedFabricMeters: customerBillableFabricMeters,
      noReuseOrderedFabricMeters,
      fullOrderedFabricMeters: noReuseOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopNewFabricMeters,
      workshopFabricRequiredMeters: workshopNewFabricMeters,
      internalNewFabricNeededMeters: workshopNewFabricMeters,
      newFabricNeededMeters: workshopNewFabricMeters,
      cutFabricMeters,
      rawPanels: Number(rowPlan.rawPanels ?? noReuseRowPlan?.rawPanels ?? 0),
      halfStepProductionPanels,
      wholeBillingPanels,
      workshopPanels,
      customerBillingPanels,
      halfReusedMeters: Number(rowPlan.halfReusedMeters || 0),
      internalHalfReusedMeters: Number(rowPlan.halfReusedMeters || 0),
      halfPiecesSaved: rowPlan.createdLeftoverId ? 1 : 0,
      halfPiecesReused: Number(rowPlan.reusedHalfCount || 0),
      internalHalfPieceSavingMeters,
      internalHalfPieceSavingValue,
      passHalfPieceSavingsToCustomer: passHalfPieceSavingsToCustomer === true,
      fabricCost: customerBillableFabricCost,
      fabricGrossAmount,
      fabricDiscountPercent: discountPercent,
      fabricDiscountAmount,
      fabricNetAmount: customerBillableFabricCost,
      discountScope: "fabric_only",
      discountBase: fabricGrossAmount,
      discountAmount: fabricDiscountAmount,
      baseLineAmountBeforeDiscount: round2(
        fabricGrossAmount +
          Number(calc.sqftAmount || calc.productCharge || 0) +
          Number(calc.stitchingCost || 0) +
          Number(calc.trackCost || 0) +
          Number(calc.bendAmount || 0) +
          Number(lining.amount || 0)
      ),
      sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
      rowSubtotalBeforeGst: customerBillableRowTotal,
      gstAmount: round2(customerBillableRowTotal * num(calc.gstRate) / 100),
      rowTotal: customerBillableRowTotal,
      lineTotal: customerBillableRowTotal,
      taxableAmount: customerBillableRowTotal,
      totalPieces: customerBillingPanels,
      pcsFull: wholeBillingPanels,
      pcsHalf: halfStepProductionPanels,
      newFullCuts: Number(rowPlan.newFullCuts ?? calc.pcsFull ?? 0),
    };
  },
  [
    calcRow,
    passHalfPieceSavingsToCustomer,
    repeatSettings,
  ]
);

const getCurtainProductionAdjustedRowCalc = useCallback(
  (row, rowIndex = -1) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      rowIndex,
      curtainFabricSummaryResult,
      curtainNoReuseBillingResult
    ),
  [
    curtainFabricSummaryResult,
    curtainNoReuseBillingResult,
    getCurtainProductionAdjustedRowCalcFromSummary,
  ]
);

/* =============================================
    TOTALS  always recompute using calcRow()
   ============================================= */
const totals = useMemo(() => {
  return round2(
    rows.reduce(
      (sum, r, index) => sum + getCurtainProductionAdjustedRowCalc(r, index).rowTotal,
      0
    )
  );
}, [
  rows,
  getCurtainProductionAdjustedRowCalc,
]);


/* =============================================
    SUMMARY TOTALS  reacts instantly
   ============================================= */
const summaryTotals = useMemo(() => {
  const subTotal = totals;
  const gstTotals = calculateCurtainQuoteTotals({
    rows,
    subtotal: subTotal,
    discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
    discountValue: summaryDiscountValue,
    quoteGstMode: curtainGstMode,
    quoteGstRate: curtainGstRate,
    getRowCalculation: getCurtainProductionAdjustedRowCalc,
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });

  return {
    subTotal: round2(subTotal),
    discountType: gstTotals.discountType,
    discountValue: gstTotals.discountValue,
    discountAmount: gstTotals.discountAmount,
    discountApplication: gstTotals.discountApplication || "after_gst",
    postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
    preGstGrandTotal: gstTotals.subtotalBeforeGst,
    subtotalBeforeGst: gstTotals.subtotalBeforeGst,
    totalAfterGst: gstTotals.totalAfterGst,
    measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
    measurementDiscountAmount: gstTotals.measurementDiscountAmount,
    accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
    taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
    fabricTaxableAmount: gstTotals.fabricTaxableAmount,
    otherTaxableAmount: gstTotals.otherTaxableAmount,
    taxableAmount: gstTotals.taxableAmount,
    quoteGstMode: gstTotals.quoteGstMode,
    gstMode: gstTotals.gstMode,
    gstModeLabel: gstTotals.gstModeLabel,
    quoteGstRate: gstTotals.quoteGstRate,
    gstRate: gstTotals.gstRate,
    gstRates: gstTotals.gstRates || [gstTotals.gstRate],
    accessoryGstRates: gstTotals.accessoryGstRates || [],
    hasMixedGstRates: gstTotals.hasMixedGstRates === true,
    isMixedGstMode: gstTotals.isMixedGstMode === true,
    fabricGstAmount: gstTotals.fabricGstAmount,
    otherGstAmount: gstTotals.otherGstAmount,
    measurementGstAmount: gstTotals.measurementGstAmount,
    accessoryGstAmount: gstTotals.accessoryGstAmount,
    gstAmount: gstTotals.gstAmount,
    totalGstAmount: gstTotals.totalGstAmount,
    gstBreakdown: gstTotals.gstBreakdown || {},
    finalTotal: gstTotals.finalTotal,
    grandTotal: gstTotals.grandTotal,
  };
}, [
  curtainGstMode,
  curtainGstRate,
  getCurtainProductionAdjustedRowCalc,
  rows,
  totals,
  summaryDiscountMode,
  summaryDiscountValue,
]);

curtainSummaryTotalsRef.current = summaryTotals;

useEffect(() => {
  if (!isDevRuntime) return;
  const totalAfterGst = Number(summaryTotals.totalAfterGst || 0);
  const discountAmount = Number(summaryTotals.discountAmount || 0);
  const expectedGrandTotal = round2(Math.max(0, totalAfterGst - discountAmount));
  const actualGrandTotal = round2(Number(summaryTotals.grandTotal || 0));
  if (discountAmount > 0.005 && Math.abs(expectedGrandTotal - actualGrandTotal) > 0.05) {
    devError("DISCOUNT_NOT_APPLIED_TO_GRAND_TOTAL", {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      totalAfterGst,
      discountType: summaryTotals.discountType,
      discountValue: summaryTotals.discountValue,
      discountAmount,
      grandTotal: actualGrandTotal,
      expectedGrandTotal,
    });
  }
}, [
  quoteNo,
  summaryTotals.discountAmount,
  summaryTotals.discountType,
  summaryTotals.discountValue,
  summaryTotals.grandTotal,
  summaryTotals.totalAfterGst,
]);

const legacyCurtainCalculationCandidateRows = useMemo(
  () =>
    rows.filter(
      (row) => row.calculationSnapshot?.requiresRecalculation === true
    ),
  [rows]
);
const hasLegacyCurtainCalculationRows = legacyCurtainCalculationCandidateRows.length > 0;
const curtainLegacyVersionStatus = useMemo(() => {
  if (!hasLegacyCurtainCalculationRows) {
    return LEGACY_CALCULATION_VERSION_STATUS.CURRENT;
  }
  if (legacyCurtainCalculationCandidateRows.some((row) => !row.calculationSnapshot)) {
    return LEGACY_CALCULATION_VERSION_STATUS.MISSING_SNAPSHOT;
  }
  if (
    legacyCurtainCalculationCandidateRows.some(
      (row) => !row.calculationSnapshot?.calculationVersion
    )
  ) {
    return LEGACY_CALCULATION_VERSION_STATUS.MISSING_VERSION;
  }
  if (
    legacyCurtainCalculationCandidateRows.some(
      (row) =>
        row.calculationSnapshot?.calculationVersion !==
        CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION
    )
  ) {
    return LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION;
  }
  return LEGACY_CALCULATION_VERSION_STATUS.CURRENT;
}, [hasLegacyCurtainCalculationRows, legacyCurtainCalculationCandidateRows]);
const curtainQuoteTotalMode =
  hasLegacyCurtainCalculationRows &&
  !pendingRecalculationAudit &&
  !legacyRecalculationRevisionRequest &&
  !curtainQuoteIsDirty &&
  curtainTotalsSettingsEditVersion <= 0
    ? "savedSnapshot"
    : "liveEditing";
const isFrozenLegacyCurtainQuote = curtainQuoteTotalMode === "savedSnapshot";

const getCurtainSavedTotalsFromQuote = useCallback(
  (quote = selectedQuote) => {
    if (!quote || resolveQuoteType(quote) !== CURTAIN_QUOTE_TYPE) return null;
    const snapshotTotals = quote.calculationSnapshot?.totals || {};
    const storedGrandTotal = Number(
      snapshotTotals.grandTotal ??
        quote.grandTotal ??
        quote.finalTotal ??
        quote.finalAmount ??
        quote.totalAmount ??
        0
    );
    const storedSubtotal = Number(
      snapshotTotals.subtotal ??
        quote.subtotal ??
        quote.subTotal ??
        quote.totalAmount ??
        storedGrandTotal ??
        0
    );
    const storedDiscount = Number(
      snapshotTotals.discount ??
        quote.discountAmount ??
        quote.discount ??
        0
    );
    const storedGst = Number(
      snapshotTotals.gstAmount ??
        quote.totalGstAmount ??
        quote.gstAmount ??
        0
    );
    const storedGstMode = resolveCurtainQuoteGstMode({
      ...quote,
      quoteGstMode: quote.quoteGstMode ?? snapshotTotals.quoteGstMode,
      gstMode: quote.gstMode ?? snapshotTotals.gstMode,
      gstBreakdown: quote.gstBreakdown || snapshotTotals.gstBreakdown,
    });
    const storedGstRate = resolveCurtainQuoteGstRate({
      ...quote,
      quoteSettings: quote.quoteSettings || {},
      calculationSnapshot: {
        ...(quote.calculationSnapshot || {}),
        totals: snapshotTotals,
      },
    });
    const storedTaxable = Number(
      snapshotTotals.taxableAmount ??
        quote.taxableAmount ??
        quote.subtotalBeforeGst ??
        quote.preGstGrandTotal ??
        Math.max(0, storedSubtotal - storedDiscount)
    );

    if (
      !Number.isFinite(storedGrandTotal) &&
      !Number.isFinite(storedSubtotal)
    ) {
      return null;
    }

    return {
      subTotal: Number.isFinite(storedSubtotal) ? round2(storedSubtotal) : 0,
      subtotalBeforeGst: Number.isFinite(storedTaxable) ? round2(storedTaxable) : 0,
      taxableAmount: Number.isFinite(storedTaxable) ? round2(storedTaxable) : 0,
      discountAmount: Number.isFinite(storedDiscount) ? round2(storedDiscount) : 0,
      discountApplication: quote.discountApplication || snapshotTotals.discountApplication || "legacy",
      gstAmount: Number.isFinite(storedGst) ? round2(storedGst) : 0,
      totalGstAmount: Number.isFinite(storedGst) ? round2(storedGst) : 0,
      quoteGstMode: storedGstMode,
      gstMode: storedGstMode,
      gstModeLabel: getCurtainGstModeLabel(storedGstMode, storedGstRate),
      fabricTaxableAmount: Number(
        snapshotTotals.fabricTaxableAmount ??
          quote.fabricTaxableAmount ??
          quote.gstBreakdown?.fabricTaxableAmount ??
          0
      ),
      otherTaxableAmount: Number(
        snapshotTotals.otherTaxableAmount ??
          quote.otherTaxableAmount ??
          quote.gstBreakdown?.otherTaxableAmount ??
          0
      ),
      fabricGstAmount: Number(
        snapshotTotals.fabricGstAmount ??
          quote.fabricGstAmount ??
          quote.gstBreakdown?.fabricGstAmount ??
          0
      ),
      otherGstAmount: Number(
        snapshotTotals.otherGstAmount ??
          quote.otherGstAmount ??
          quote.gstBreakdown?.otherGstAmount ??
          0
      ),
      gstBreakdown:
        snapshotTotals.gstBreakdown ||
        quote.gstBreakdown ||
        quote.quoteSettings?.gstBreakdown ||
        {},
      totalAfterGst: Number(
        snapshotTotals.totalAfterGst ??
          quote.totalAfterGst ??
          (Number.isFinite(storedTaxable) || Number.isFinite(storedGst)
            ? round2(Number(storedTaxable || 0) + Number(storedGst || 0))
            : 0)
      ),
      grandTotal: Number.isFinite(storedGrandTotal) ? round2(storedGrandTotal) : 0,
      quoteGstRate: storedGstRate,
      source: snapshotTotals.grandTotal != null ? "calculationSnapshot" : "storedQuoteFields",
    };
  },
  [selectedQuote]
);
const curtainDisplaySummaryTotals = useMemo(() => {
  if (!isFrozenLegacyCurtainQuote) return summaryTotals;
  const savedTotals = getCurtainSavedTotalsFromQuote(selectedQuote);
  if (!savedTotals) return summaryTotals;
  return {
    ...summaryTotals,
    subTotal: savedTotals.subTotal,
    subtotalBeforeGst: savedTotals.subtotalBeforeGst,
    preGstGrandTotal: savedTotals.subtotalBeforeGst,
    taxableAmount: savedTotals.taxableAmount,
    discountAmount: savedTotals.discountAmount,
    discountApplication: savedTotals.discountApplication,
    gstAmount: savedTotals.gstAmount,
    totalGstAmount: savedTotals.totalGstAmount,
    totalAfterGst: savedTotals.totalAfterGst,
    quoteGstMode: savedTotals.quoteGstMode,
    gstMode: savedTotals.gstMode,
    gstModeLabel: savedTotals.gstModeLabel,
    fabricTaxableAmount: savedTotals.fabricTaxableAmount,
    otherTaxableAmount: savedTotals.otherTaxableAmount,
    fabricGstAmount: savedTotals.fabricGstAmount,
    otherGstAmount: savedTotals.otherGstAmount,
    gstBreakdown: savedTotals.gstBreakdown,
    isMixedGstMode: isMixedCurtainGstMode(savedTotals.quoteGstMode),
    measurementGstAmount:
      isMixedCurtainGstMode(savedTotals.quoteGstMode)
        ? savedTotals.fabricGstAmount
        : Number(savedTotals.totalGstAmount || 0) > 0
        ? savedTotals.totalGstAmount
        : summaryTotals.measurementGstAmount,
    accessoryGstAmount: isMixedCurtainGstMode(savedTotals.quoteGstMode)
      ? savedTotals.otherGstAmount
      : summaryTotals.accessoryGstAmount,
    grandTotal: savedTotals.grandTotal,
    finalTotal: savedTotals.grandTotal,
    quoteGstRate: savedTotals.quoteGstRate,
    gstRate: savedTotals.quoteGstRate,
    source: savedTotals.source,
  };
}, [
  getCurtainSavedTotalsFromQuote,
  isFrozenLegacyCurtainQuote,
  selectedQuote,
  summaryTotals,
]);

useEffect(() => {
  if (!isDevRuntime) return;
  devLog("DISCOUNT_INPUT_STATE_TRACE", {
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteNo,
    inputRawValue: summaryDiscountValue,
    parsedDiscountValue: num(summaryDiscountValue),
    discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
    quoteSettingsDiscountValue: summaryDiscountValue,
    helperDiscountValue: summaryTotals.discountValue,
    helperDiscountType: summaryTotals.discountType,
    liveDiscountAmount: summaryTotals.discountAmount,
    liveTotalAfterGst: summaryTotals.totalAfterGst,
    liveGrandTotal: summaryTotals.grandTotal,
    displayedGrandTotal: curtainDisplaySummaryTotals.grandTotal,
    quoteDisplayMode: curtainQuoteTotalMode,
    totalsSettingsEditVersion: curtainTotalsSettingsEditVersion,
  });
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainQuoteTotalMode,
  curtainTotalsSettingsEditVersion,
  quoteNo,
  summaryDiscountMode,
  summaryDiscountValue,
  summaryTotals.discountAmount,
  summaryTotals.discountType,
  summaryTotals.discountValue,
  summaryTotals.grandTotal,
  summaryTotals.totalAfterGst,
]);

useEffect(() => {
  if (!isDevRuntime || !isFrozenLegacyCurtainQuote) return;
  const savedTotals = getCurtainSavedTotalsFromQuote(selectedQuote);
  if (!savedTotals) return;
  const displayedGrandTotal = Number(curtainDisplaySummaryTotals.grandTotal || 0);
  const liveCalculatedGrandTotal = Number(summaryTotals.grandTotal || 0);
  const savedSnapshotGrandTotal = Number(savedTotals.grandTotal || 0);
  if (Math.abs(displayedGrandTotal - savedSnapshotGrandTotal) >= 0.01) {
    devError("QUOTE_TOTAL_SOURCE_MISMATCH", {
      quoteNo,
      mode: curtainQuoteTotalMode,
      displayedGrandTotal,
      savedSnapshotGrandTotal,
      liveCalculatedGrandTotal,
    });
  }
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainQuoteTotalMode,
  getCurtainSavedTotalsFromQuote,
  isFrozenLegacyCurtainQuote,
  quoteNo,
  selectedQuote,
  summaryTotals.grandTotal,
]);

useEffect(() => {
  if (!isDevRuntime || isFrozenLegacyCurtainQuote) return;
  const liveSubtotal = round2(
    rows.reduce(
      (sum, row, index) =>
        sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const displayedSubtotal = round2(Number(curtainDisplaySummaryTotals.subTotal || 0));
  if (Math.abs(liveSubtotal - displayedSubtotal) >= 0.01) {
    devError("QUOTE_TOTAL_STALE_STATE_DETECTED", {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      mode: curtainQuoteTotalMode,
      changedField: "rows",
      liveSubtotal,
      displayedSubtotal,
      lineTotals: rows.map((row, index) => ({
        rowId: getWorkshopRowStableId(row) || index,
        rowTotal: Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      })),
      subtotal: displayedSubtotal,
      totalGst: Number(curtainDisplaySummaryTotals.totalGstAmount || 0),
      totalAfterGst: Number(curtainDisplaySummaryTotals.totalAfterGst || 0),
      discountAmount: Number(curtainDisplaySummaryTotals.discountAmount || 0),
      grandTotal: Number(curtainDisplaySummaryTotals.grandTotal || 0),
      displayedGrandTotal: curtainDisplaySummaryTotals.grandTotal,
      liveGrandTotal: summaryTotals.grandTotal,
      rowCount: rows.length,
    });
  }
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainDisplaySummaryTotals.discountAmount,
  curtainDisplaySummaryTotals.subTotal,
  curtainDisplaySummaryTotals.totalAfterGst,
  curtainDisplaySummaryTotals.totalGstAmount,
  curtainQuoteTotalMode,
  getCurtainProductionAdjustedRowCalc,
  isFrozenLegacyCurtainQuote,
  quoteNo,
  rows,
  summaryTotals.grandTotal,
]);

const legacyPreviewFieldDefinitions = useMemo(() => ({
  pieces: {
    rowFields: ["finalPieces", "totalPieces", "pcsFull", "pcs"],
    snapshotFields: ["pieces"],
  },
  orderedFabric: {
    rowFields: [
      "orderedFabricMeters",
      "customerBillableFabricMeters",
      "fabricMeters",
      "fabricMetersFull",
    ],
    snapshotFields: ["orderedFabricMeters", "customerBillableFabricMeters", "fabricMeters"],
  },
  fabricAmount: {
    rowFields: ["fabricAmount", "fabricCost"],
    snapshotFields: ["fabricAmount", "fabricCost"],
  },
  stitchingTrackAmount: {
    rowFields: [
      "stitchingTrackAmount",
      "stitchTrackAmount",
      "stitchingAndTrackAmount",
    ],
    snapshotFields: ["stitchingTrackAmount", "stitchTrackAmount"],
  },
  liningAmount: {
    rowFields: ["liningAmount"],
    snapshotFields: ["liningAmount"],
  },
  rowTotal: {
    rowFields: ["lineTotal", "rowTotal", "total", "amount", "finalAmount"],
    snapshotFields: ["lineTotal", "rowTotal", "total"],
  },
}), []);

const getLegacyFieldDisplay = useCallback((row = {}, fieldName = "") => {
  const definition = legacyPreviewFieldDefinitions[fieldName] || {
    rowFields: [fieldName],
    snapshotFields: [fieldName],
  };
  const snapshot = row.calculationSnapshot || {};
  const storedMap = snapshot.storedFieldMap || null;
  const isSyntheticLegacySnapshotWithoutMap =
    snapshot.legacyFormula === true &&
    snapshot.requiresRecalculation === true &&
    !storedMap;

  for (const field of definition.rowFields || []) {
    if (hasOwnStoredField(row, field)) {
      const value = Number(row[field]);
      return {
        exists: true,
        value: Number.isFinite(value) ? value : row[field],
        source: field,
      };
    }
  }

  if (storedMap && storedMap[fieldName] === false) {
    return {
      exists: false,
      value: null,
      display: "Not stored in legacy quote",
      source: "",
    };
  }

  if (!isSyntheticLegacySnapshotWithoutMap || fieldName === "rowTotal") {
    for (const field of definition.snapshotFields || []) {
      if (hasOwnStoredField(snapshot, field)) {
        const value = Number(snapshot[field]);
        if (
          isSyntheticLegacySnapshotWithoutMap &&
          fieldName === "rowTotal" &&
          (!Number.isFinite(value) || value <= 0)
        ) {
          continue;
        }
        return {
          exists: true,
          value: Number.isFinite(value) ? value : snapshot[field],
          source: `calculationSnapshot.${field}`,
        };
      }
    }
  }

  return {
    exists: false,
    value: null,
    display: "Not stored in legacy quote",
    source: "",
  };
}, [legacyPreviewFieldDefinitions]);

const rowIsAffectedByLegacyCurtainFormula = useCallback((row = {}, productConfig = null) => {
  const config =
    productConfig || resolveProductConfig(row, productConfigs, fabricCalcSettings);
  return rowIsAffectedByLegacyCurtainFormulaStatic(row, config);
}, [fabricCalcSettings, productConfigs]);

const isLegacyCurtainRecalculationTarget = useCallback((row = {}) => {
  if (row.calculationSnapshot?.requiresRecalculation !== true) return false;
  if (row.__forceCurrentCalculation === true || row.__recalculated === true) {
    return false;
  }
  return rowIsAffectedByLegacyCurtainFormula(row);
}, [rowIsAffectedByLegacyCurtainFormula]);

const buildCurtainSummaryTotalsForRows = useCallback(
  (sourceRows = [], getRowCalc, sourceLabel = "curtain_summary_preview") => {
    const rowCalc =
      typeof getRowCalc === "function"
        ? getRowCalc
        : (row, index) => getCurtainProductionAdjustedRowCalc(row, index);
    const subTotal = round2(
      (Array.isArray(sourceRows) ? sourceRows : []).reduce(
        (sum, row, index) => sum + Number(rowCalc(row, index).rowTotal || 0),
        0
      )
    );
    const gstTotals = calculateCurtainQuoteTotals({
      rows: sourceRows,
      subtotal: subTotal,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: summaryDiscountValue,
      quoteGstMode: curtainGstMode,
      quoteGstRate: curtainGstRate,
      getRowCalculation: rowCalc,
      isAccessoryRow: isCurtainAccessoryRowStatic,
    });

    return {
      subTotal: round2(subTotal),
      discountType: gstTotals.discountType,
      discountValue: gstTotals.discountValue,
      discountAmount: gstTotals.discountAmount,
      discountApplication: gstTotals.discountApplication || "after_gst",
      postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
      preGstGrandTotal: gstTotals.subtotalBeforeGst,
      subtotalBeforeGst: gstTotals.subtotalBeforeGst,
      totalAfterGst: gstTotals.totalAfterGst,
      measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
      accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
      accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
      measurementDiscountAmount: gstTotals.measurementDiscountAmount,
      accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
      taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
      taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
      fabricTaxableAmount: gstTotals.fabricTaxableAmount,
      otherTaxableAmount: gstTotals.otherTaxableAmount,
      taxableAmount: gstTotals.taxableAmount,
      quoteGstMode: gstTotals.quoteGstMode,
      gstMode: gstTotals.gstMode,
      gstModeLabel: gstTotals.gstModeLabel,
      quoteGstRate: gstTotals.quoteGstRate,
      gstRate: gstTotals.gstRate,
      gstRates: gstTotals.gstRates || [gstTotals.gstRate],
      accessoryGstRates: gstTotals.accessoryGstRates || [],
      hasMixedGstRates: gstTotals.hasMixedGstRates === true,
      isMixedGstMode: gstTotals.isMixedGstMode === true,
      fabricGstAmount: gstTotals.fabricGstAmount,
      otherGstAmount: gstTotals.otherGstAmount,
      measurementGstAmount: gstTotals.measurementGstAmount,
      accessoryGstAmount: gstTotals.accessoryGstAmount,
      gstAmount: gstTotals.gstAmount,
      totalGstAmount: gstTotals.totalGstAmount,
      gstBreakdown: gstTotals.gstBreakdown || {},
      finalTotal: gstTotals.finalTotal,
      grandTotal: gstTotals.grandTotal,
    };
  },
  [
    curtainGstMode,
    curtainGstRate,
    getCurtainProductionAdjustedRowCalc,
    summaryDiscountMode,
    summaryDiscountValue,
  ]
);

const getLegacyRecalculationReasonCodes = useCallback(
  (row = {}, legacyFields = {}, newCalc = {}) => {
    const snapshot = row.calculationSnapshot || {};
    const reasons = ["Legacy formula"];

    if (
      legacyFields.pieces?.exists === false ||
      legacyFields.orderedFabric?.exists === false ||
      legacyFields.fabricAmount?.exists === false ||
      legacyFields.stitchingTrackAmount?.exists === false ||
      legacyFields.liningAmount?.exists === false
    ) {
      reasons.push("Missing old calculation breakdown");
    }

    if (
      Number(snapshot.usableWidthDeductionIn || 0) !==
      Number(newCalc.calculationBreakdown?.curtainPieceCalc?.usableWidthDeductionIn || 0)
    ) {
      reasons.push("Usable width deduction applied");
    }

    const newPieces = Number(newCalc.totalPieces ?? newCalc.pcsFull ?? 0);
    if (
      legacyFields.pieces?.exists === true &&
      Number(legacyFields.pieces.value || 0) !== newPieces
    ) {
      reasons.push("Pieces recalculated");
    } else if (legacyFields.pieces?.exists === false && newPieces > 0) {
      reasons.push("Pieces recalculated");
    }

    if (
      Number(snapshot.fullness || 0) !==
      Number(newCalc.calculationBreakdown?.curtainPieceCalc?.fullness || snapshot.fullness || 0)
    ) {
      reasons.push("Fullness/stitching setting changed");
    }

    const newStitchTrackAmount = round2(
      Number(newCalc.stitchingCost || 0) +
        Number(newCalc.trackCost || 0) +
        Number(newCalc.bendAmount || 0)
    );
    if (
      legacyFields.stitchingTrackAmount?.exists === true &&
      round2(Number(legacyFields.stitchingTrackAmount.value || 0)) !==
        newStitchTrackAmount
    ) {
      reasons.push("Track/stitching recalculated");
    } else if (
      legacyFields.stitchingTrackAmount?.exists === false &&
      newStitchTrackAmount > 0
    ) {
      reasons.push("Track/stitching recalculated");
    }

    const newLiningAmount = round2(Number(newCalc.liningAmount ?? newCalc.lining?.amount ?? 0));
    if (
      legacyFields.liningAmount?.exists === true &&
      round2(Number(legacyFields.liningAmount.value || 0)) !== newLiningAmount
    ) {
      reasons.push("Lining recalculated");
    } else if (legacyFields.liningAmount?.exists === false && newLiningAmount > 0) {
      reasons.push("Lining recalculated");
    }

    return Array.from(new Set(reasons));
  },
  []
);

const buildLegacyCurtainRecalculationPreview = useCallback(() => {
  if (!hasLegacyCurtainCalculationRows) return null;

  const generatedAt = new Date().toISOString();
  const oldRows = rows.map((row, index) =>
    withCurtainRowId({ ...(row || {}) }, index)
  );
  const newRows = oldRows.map((row, index) => {
    if (!isLegacyCurtainRecalculationTarget(row)) {
      return withCurtainRowId({ ...row }, index);
    }

    const nextRow = sanitizeCurtainRowEditableTextFields({
      ...row,
      calculationSnapshot: null,
      calculationSnapshotStatus: "current",
      __forceCurrentCalculation: true,
      __recalculated: true,
      rowUpdatedAt: generatedAt,
    });

    return withCurtainRowId(nextRow, index);
  });
  const previewFabricSummaryResult = buildCurtainFabricSummary(newRows, {
    activeRepeatSettings: repeatSettings,
    halfPieceReuseEnabled: useHalfPieceReuse,
    debugTag: "legacy-recalculation-preview",
  });
  const previewNoReuseBillingResult = buildCurtainFabricSummary(newRows, {
    activeRepeatSettings: repeatSettings,
    halfPieceReuseEnabled: false,
    debugTag: "legacy-recalculation-preview-no-reuse",
  });
  const getPreviewRowCalc = (row, index) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      index,
      previewFabricSummaryResult,
      previewNoReuseBillingResult
    );
  const newSummaryTotals = buildCurtainSummaryTotalsForRows(
    newRows,
    getPreviewRowCalc,
    "legacy_recalculation_preview"
  );
  const savedQuoteTotalsForPreview = getCurtainSavedTotalsFromQuote(selectedQuote);
  const oldSummaryTotals = {
    subTotal: Number(
      savedQuoteTotalsForPreview?.subTotal ?? summaryTotals.subTotal ?? 0
    ),
    subtotalBeforeGst: Number(
      savedQuoteTotalsForPreview?.subtotalBeforeGst ??
        summaryTotals.subtotalBeforeGst ??
        summaryTotals.preGstGrandTotal ??
        0
    ),
    gstAmount: Number(
      savedQuoteTotalsForPreview?.totalGstAmount ??
        summaryTotals.totalGstAmount ??
        summaryTotals.gstAmount ??
        0
    ),
    totalGstAmount: Number(
      savedQuoteTotalsForPreview?.totalGstAmount ??
        summaryTotals.totalGstAmount ??
        summaryTotals.gstAmount ??
        0
    ),
    grandTotal: Number(
      savedQuoteTotalsForPreview?.grandTotal ??
        summaryTotals.grandTotal ??
        summaryTotals.finalTotal ??
        0
    ),
    quoteGstRate: Number(
      savedQuoteTotalsForPreview?.quoteGstRate ??
        summaryTotals.quoteGstRate ??
        summaryTotals.gstRate ??
        0
    ),
    discountAmount: Number(
      savedQuoteTotalsForPreview?.discountAmount ??
        summaryTotals.discountAmount ??
        0
    ),
    source: savedQuoteTotalsForPreview?.source || "liveSnapshotTotals",
  };
  const rowCalcsById = {};
  const rowDiffRows = [];
  const changedRows = [];

  oldRows.forEach((oldRow, index) => {
    const rowId = getWorkshopRowStableId(oldRow) || `row-${index}`;
    const newRow = newRows[index] || oldRow;
    const newCalc = getPreviewRowCalc(newRow, index);
    rowCalcsById[getWorkshopRowStableId(newRow) || rowId] = newCalc;

    if (!isLegacyCurtainRecalculationTarget(oldRow)) return;

    const legacyFields = {
      pieces: getLegacyFieldDisplay(oldRow, "pieces"),
      orderedFabric: getLegacyFieldDisplay(oldRow, "orderedFabric"),
      fabricAmount: getLegacyFieldDisplay(oldRow, "fabricAmount"),
      stitchingTrackAmount: getLegacyFieldDisplay(oldRow, "stitchingTrackAmount"),
      liningAmount: getLegacyFieldDisplay(oldRow, "liningAmount"),
      rowTotal: getLegacyFieldDisplay(oldRow, "rowTotal"),
    };
    const newPieces = Number(newCalc.totalPieces ?? newCalc.pcsFull ?? 0);
    const newOrderedFabric = Number(
      newCalc.orderedFabricMeters ??
        newCalc.customerBillableFabricMeters ??
        newCalc.fabricMeters ??
        0
    );
    const newFabricAmount = Number(newCalc.fabricCost || 0);
    const newStitchTrackAmount = round2(
      Number(newCalc.stitchingCost || 0) +
        Number(newCalc.trackCost || 0) +
        Number(newCalc.bendAmount || 0)
    );
    const newLiningAmount = Number(newCalc.liningAmount ?? newCalc.lining?.amount ?? 0);
    const newTotal = Number(newCalc.lineTotal ?? newCalc.rowTotal ?? 0);
    const reasonCodes = getLegacyRecalculationReasonCodes(
      oldRow,
      legacyFields,
      newCalc
    );
    const buildPreviewField = ({ field, type, oldField, newValue, unit = "" }) => {
      const normalizedNewValue = round2(Number(newValue || 0));
      const oldExists = oldField?.exists === true;
      const normalizedOldValue = oldExists
        ? round2(Number(oldField.value || 0))
        : null;
      const difference = oldExists
        ? round2(normalizedNewValue - Number(normalizedOldValue || 0))
        : null;
      const shouldShow = oldExists
        ? Math.abs(Number(difference || 0)) >= 0.01
        : Math.abs(normalizedNewValue) >= 0.01;

      return shouldShow
        ? {
            field,
            type,
            oldValue: normalizedOldValue,
            oldExists,
            oldSource: oldField?.source || "",
            newValue: normalizedNewValue,
            difference,
            unit,
          }
        : null;
    };
    const rowFields = [
      {
        field: "Pieces",
        type: "number",
        oldField: legacyFields.pieces,
        newValue: round2(newPieces),
        unit: "",
      },
      {
        field: "Ordered fabric",
        type: "meters",
        oldField: legacyFields.orderedFabric,
        newValue: round2(newOrderedFabric),
        unit: "m",
      },
      {
        field: "Fabric amount",
        type: "money",
        oldField: legacyFields.fabricAmount,
        newValue: round2(newFabricAmount),
        unit: "",
      },
      {
        field: "Stitching / track amount",
        type: "money",
        oldField: legacyFields.stitchingTrackAmount,
        newValue: round2(newStitchTrackAmount),
        unit: "",
      },
      {
        field: "Lining amount",
        type: "money",
        oldField: legacyFields.liningAmount,
        newValue: round2(newLiningAmount),
        unit: "",
      },
      {
        field: "Row total",
        type: "money",
        oldField: legacyFields.rowTotal,
        newValue: round2(newTotal),
        unit: "",
      },
    ]
      .map(buildPreviewField)
      .filter(Boolean);

    if (!rowFields.length) return;

    const oldPieces = legacyFields.pieces.exists
      ? round2(Number(legacyFields.pieces.value || 0))
      : null;
    const oldOrderedFabric = legacyFields.orderedFabric.exists
      ? round2(Number(legacyFields.orderedFabric.value || 0))
      : null;
    const oldTotal = legacyFields.rowTotal.exists
      ? round2(Number(legacyFields.rowTotal.value || 0))
      : null;

    const rowSummary = {
      rowId,
      itemName: oldRow.item || oldRow.name || oldRow.room || `Row ${index + 1}`,
      product:
        oldRow.productName ||
        oldRow.productType ||
        oldRow.product ||
        oldRow.calculationSnapshot?.productName ||
        "",
      fabric: getCurtainRowFabricLabel(oldRow),
      oldCalculationVersion:
        oldRow.calculationSnapshot?.calculationVersion || "legacy-saved-row-values",
      newCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      oldPieces,
      newPieces: round2(newPieces),
      oldOrderedFabric,
      newOrderedFabric: round2(newOrderedFabric),
      oldTotal,
      oldTotalStored: legacyFields.rowTotal.exists === true,
      newTotal: round2(newTotal),
      difference:
        legacyFields.rowTotal.exists === true
          ? round2(newTotal - Number(legacyFields.rowTotal.value || 0))
          : null,
      reasonCodes,
    };

    changedRows.push(rowSummary);
    rowFields.forEach((field) => {
      rowDiffRows.push({
        ...rowSummary,
        field: field.field,
        type: field.type,
        oldValue: field.oldValue,
        oldExists: field.oldExists,
        oldSource: field.oldSource,
        newValue: field.newValue,
        difference: field.difference,
        unit: field.unit,
      });
    });
  });

  const preview = {
    id: `legacy-recalc-${Date.now()}`,
    quoteId: editingQuoteId || selectedQuote?.id || "",
    quoteNo: quoteNo || selectedQuote?.quoteNo || "",
    customerName,
    generatedAt,
    versionStatus: curtainLegacyVersionStatus,
    legacyCandidateRows: legacyCurtainCalculationCandidateRows.length,
    eligibleLegacyRows: oldRows.filter(isLegacyCurtainRecalculationTarget).length,
    fromCalculationVersion:
      changedRows[0]?.oldCalculationVersion || "legacy-saved-row-values",
    toCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
    oldRows: sanitizeLocalDraftValue(oldRows),
    newRows,
    rowCalcsById,
    oldTotals: oldSummaryTotals,
    newTotals: newSummaryTotals,
    difference: {
      subtotal: round2(
        Number(newSummaryTotals.subtotalBeforeGst || 0) -
          Number(oldSummaryTotals.subtotalBeforeGst || 0)
      ),
      gst: round2(
        Number(newSummaryTotals.totalGstAmount || newSummaryTotals.gstAmount || 0) -
          Number(oldSummaryTotals.totalGstAmount || oldSummaryTotals.gstAmount || 0)
      ),
      grandTotal: round2(
        Number(newSummaryTotals.grandTotal || 0) -
          Number(oldSummaryTotals.grandTotal || 0)
      ),
    },
    changedRows,
    rowDiffRows,
    affectedRowCount: changedRows.length,
  };

  const impact = deriveLegacyRecalculationImpact(preview);
  const result = {
    ...preview,
    ...impact,
    affectedRowCount: impact.actualChangedRows,
  };

  if (isDevelopmentMode()) {
    devLog("LEGACY_CALCULATION_CHECK_RESULT", {
      quoteNo: result.quoteNo,
      versionStatus: result.versionStatus,
      legacyCandidateRows: result.legacyCandidateRows,
      actualChangedRows: result.actualChangedRows,
      savedSubtotal: result.oldTotals?.subtotalBeforeGst,
      recalculatedSubtotal: result.newTotals?.subtotalBeforeGst,
      savedGst: result.oldTotals?.totalGstAmount ?? result.oldTotals?.gstAmount,
      recalculatedGst: result.newTotals?.totalGstAmount ?? result.newTotals?.gstAmount,
      savedGrandTotal: result.oldTotals?.grandTotal,
      recalculatedGrandTotal: result.newTotals?.grandTotal,
      totalDifference: result.totalDifference,
      recalculationImpact: result.recalculationImpact,
      shouldShowBanner: result.shouldShowBanner,
      reason: result.reason,
    });
  }

  return result;
}, [
  buildCurtainFabricSummary,
  buildCurtainSummaryTotalsForRows,
  curtainLegacyVersionStatus,
  customerName,
  editingQuoteId,
  getCurtainProductionAdjustedRowCalcFromSummary,
  getCurtainSavedTotalsFromQuote,
  getLegacyFieldDisplay,
  getLegacyRecalculationReasonCodes,
  hasLegacyCurtainCalculationRows,
  isLegacyCurtainRecalculationTarget,
  legacyCurtainCalculationCandidateRows.length,
  quoteNo,
  repeatSettings,
  rows,
  selectedQuote,
  summaryTotals,
  useHalfPieceReuse,
]);

const legacyRecalculationImpactPreview = useMemo(
  () => buildLegacyCurtainRecalculationPreview(),
  [buildLegacyCurtainRecalculationPreview]
);

const shouldShowLegacyCalculationBanner = Boolean(
  legacyRecalculationImpactPreview?.shouldShowBanner === true &&
    !pendingRecalculationAudit &&
    !legacyRecalculationRevisionRequest
);

legacyCurtainRecalculationImpactRef.current = legacyRecalculationImpactPreview || {
  shouldShowBanner: false,
  recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
};

const buildLegacyRecalculationAuditPackage = useCallback(
  (preview, action = "save_new_revision") => {
    if (!preview) return { audit: null, legacySnapshot: null };
    const recalculatedAt = new Date().toISOString();
    const currentUser = auth.currentUser;
    const audit = {
      recalculatedAt,
      recalculatedBy: currentUser?.uid || "",
      recalculatedByEmail: currentUser?.email || "",
      action,
      versionStatus: preview.versionStatus || LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION,
      recalculationImpact:
        preview.recalculationImpact ||
        deriveLegacyRecalculationImpact(preview).recalculationImpact,
      quoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      quoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
      fromCalculationVersion: preview.fromCalculationVersion,
      toCalculationVersion: preview.toCalculationVersion,
      oldSubtotal: Number(preview.oldTotals?.subtotalBeforeGst || 0),
      newSubtotal: Number(preview.newTotals?.subtotalBeforeGst || 0),
      oldGst: Number(preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount ?? 0),
      newGst: Number(preview.newTotals?.totalGstAmount ?? preview.newTotals?.gstAmount ?? 0),
      oldGrandTotal: Number(preview.oldTotals?.grandTotal || 0),
      newGrandTotal: Number(preview.newTotals?.grandTotal || 0),
      difference: Number(preview.difference?.grandTotal || 0),
      actualChangedRows: Number(preview.actualChangedRows ?? preview.changedRows?.length ?? 0),
      legacyCandidateRows: Number(preview.legacyCandidateRows || 0),
      changedRows: sanitizeLocalDraftValue(preview.changedRows || []),
    };
    const legacySnapshot = {
      capturedAt: recalculatedAt,
      quoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      quoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
      subtotal: Number(preview.oldTotals?.subtotalBeforeGst || 0),
      gst: Number(preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount ?? 0),
      grandTotal: Number(preview.oldTotals?.grandTotal || 0),
      rows: sanitizeLocalDraftValue(preview.oldRows || []),
    };

    return {
      audit: sanitizeLocalDraftValue(audit),
      legacySnapshot: sanitizeLocalDraftValue(legacySnapshot),
    };
  },
  [editingQuoteId, quoteNo, selectedQuote?.id, selectedQuote?.quoteNo]
);

const resolveCurtainRecalculationFieldsForSave = useCallback(
  (overrides = {}) => {
    const hasAuditOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "recalculationAudit"
    );
    const hasSnapshotOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "legacyCalculationSnapshot"
    );
    const auditSource = hasAuditOverride
      ? overrides.recalculationAudit
      : pendingRecalculationAudit === false
      ? null
      : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null;
    const snapshotSource = hasSnapshotOverride
      ? overrides.legacyCalculationSnapshot
      : pendingLegacyCalculationSnapshot === false
      ? null
      : pendingLegacyCalculationSnapshot ||
        selectedQuote?.legacyCalculationSnapshot ||
        null;
    const shouldWrite =
      hasAuditOverride ||
      hasSnapshotOverride ||
      pendingRecalculationAudit !== null ||
      pendingLegacyCalculationSnapshot !== null ||
      Boolean(selectedQuote?.recalculationAudit) ||
      Boolean(selectedQuote?.legacyCalculationSnapshot);

    return shouldWrite
      ? {
          recalculationAudit: auditSource ? sanitizeLocalDraftValue(auditSource) : null,
          legacyCalculationSnapshot: snapshotSource
            ? sanitizeLocalDraftValue(snapshotSource)
            : null,
        }
      : {};
  },
  [
    pendingLegacyCalculationSnapshot,
    pendingRecalculationAudit,
    selectedQuote?.legacyCalculationSnapshot,
    selectedQuote?.recalculationAudit,
  ]
);

const openLegacyRecalculationPreview = useCallback(() => {
  const preview = legacyRecalculationImpactPreview || buildLegacyCurtainRecalculationPreview();
  if (!preview) return;
  setLegacyRecalculationPreview(preview);
  setLegacyRecalculationModalOpen(true);
}, [buildLegacyCurtainRecalculationPreview, legacyRecalculationImpactPreview]);

const saveLegacyRecalculationAsNewRevision = useCallback(
  (preview = legacyRecalculationPreview) => {
    if (!preview) return;
    if (
      preview.recalculationImpact === LEGACY_CALCULATION_IMPACT.NO_IMPACT ||
      deriveLegacyRecalculationImpact(preview).recalculationImpact ===
        LEGACY_CALCULATION_IMPACT.NO_IMPACT
    ) {
      notify("No changes found. This quote already matches the latest calculation output.", "success");
      setLegacyRecalculationModalOpen(false);
      setLegacyRecalculationPreview(null);
      return;
    }
    if (!canCreateQuotes) {
      notify("You do not have permission to create a quotation revision.", "warning");
      return;
    }
    const { audit, legacySnapshot } = buildLegacyRecalculationAuditPackage(
      preview,
      "save_new_revision"
    );
    setPendingRecalculationAudit({
      ...audit,
      revisionOfQuoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      revisionOfQuoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
    });
    setPendingLegacyCalculationSnapshot(legacySnapshot);
    commitCurtainRows(
      preview.newRows,
      QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
    );
    setLegacyRecalculationModalOpen(false);
    setLegacyRecalculationPreview(null);
    setLegacyRecalculationRevisionRequest({
      id: preview.id || `legacy-revision-${Date.now()}`,
      requestedAt: new Date().toISOString(),
    });
    notify("Preparing a recalculated revision from the reviewed changes.", "info");
  },
  [
    buildLegacyRecalculationAuditPackage,
    canCreateQuotes,
    commitCurtainRows,
    editingQuoteId,
    legacyRecalculationPreview,
    notify,
    quoteNo,
    selectedQuote?.id,
    selectedQuote?.quoteNo,
  ]
);

const markLegacyCalculationNoImpactReviewed = useCallback(
  async (preview = legacyRecalculationPreview) => {
    if (!preview) return;
    const impact = deriveLegacyRecalculationImpact(preview);
    if (impact.recalculationImpact !== LEGACY_CALCULATION_IMPACT.NO_IMPACT) {
      notify("This quote has calculation differences and needs a revision review.", "warning");
      return;
    }
    const quoteId = preview.quoteId || editingQuoteId || selectedQuote?.id || "";
    if (!workspaceUid || !quoteId) {
      notify("Could not mark this quote as reviewed because the quote is not saved yet.", "warning");
      return;
    }
    if (!canEditQuotes) {
      notify("You do not have permission to update this quote review status.", "warning");
      return;
    }

    const currentUser = auth.currentUser;
    const reviewedAt = new Date().toISOString();
    const reviewLocal = {
      reviewedAt,
      reviewedBy: currentUser?.uid || "",
      reviewedByEmail: currentUser?.email || "",
      checkedAgainstCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      versionStatus: preview.versionStatus || LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION,
      impact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
      savedGrandTotal: Number(preview.oldTotals?.grandTotal || 0),
      recalculatedGrandTotal: Number(preview.newTotals?.grandTotal || 0),
      difference: 0,
      legacyCandidateRows: Number(preview.legacyCandidateRows || 0),
      actualChangedRows: 0,
    };

    try {
      await updateDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
        legacyCalculationReview: {
          ...reviewLocal,
          reviewedAt: serverTimestamp(),
        },
        calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
        formulaCheckStatus: "current",
        updatedAt: serverTimestamp(),
      });
      setSelectedQuote((previous) =>
        previous
          ? {
              ...previous,
              legacyCalculationReview: reviewLocal,
              calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
              formulaCheckStatus: "current",
            }
          : previous
      );
      setLegacyRecalculationModalOpen(false);
      setLegacyRecalculationPreview(null);
      notify("Calculation check marked as reviewed.", "success");
    } catch (error) {
      devError("LEGACY_CALCULATION_MARK_REVIEWED_FAILED", {
        code: error?.code,
        message: error?.message,
        quoteId,
        workspaceUid,
      });
      notify("Could not mark calculation check as reviewed.", "error");
    }
  },
  [
    canEditQuotes,
    editingQuoteId,
    legacyRecalculationPreview,
    notify,
    selectedQuote?.id,
    workspaceUid,
  ]
);

const revertLegacyCurtainRecalculation = () => {
  const legacySnapshot =
    pendingLegacyCalculationSnapshot === false
      ? null
      : pendingLegacyCalculationSnapshot || selectedQuote?.legacyCalculationSnapshot;
  if (!legacySnapshot?.rows?.length) {
    notify("No previous calculation snapshot is available to restore.", "warning");
    return;
  }
  const restoredRows = legacySnapshot.rows.map((row, index) =>
    hydrateCurtainRowCalculationSnapshot(
      sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...(row || {}) }, index))
    )
  );
  commitCurtainRows(restoredRows, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
  setPendingRecalculationAudit(false);
  setPendingLegacyCalculationSnapshot(false);
  setSelectedQuote((previous) =>
    previous
      ? {
          ...previous,
          rows: restoredRows,
          recalculationAudit: null,
          legacyCalculationSnapshot: null,
        }
      : previous
  );
  notify("Previous calculation restored in the editor. Save to persist the revert.", "success");
};

const downloadLegacyRecalculationReport = useCallback(() => {
  const preview = legacyRecalculationPreview;
  if (!preview || typeof document === "undefined") return;
  const blob = new Blob([JSON.stringify(sanitizeLocalDraftValue(preview), null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${preview.quoteNo || "curtain-quote"}-recalculation-diff.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}, [legacyRecalculationPreview]);

const formatLegacyRecalculationValue = useCallback(
  (entry, value) => {
    if (entry?.oldExists === false && value == null) {
      return "Not stored in legacy quote";
    }
    if (value == null || value === "") return "-";
    const amount = Number(value);
    if (!Number.isFinite(amount)) return String(value);
    if (entry?.type === "money") return formatCurrencyDisplay(amount, currency);
    if (entry?.type === "meters") return `${amount.toFixed(2)} m`;
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  },
  [currency]
);

const activeCurtainRecalculationAudit =
  pendingRecalculationAudit === false
    ? null
    : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null;
const activeCurtainLegacyCalculationSnapshot =
  pendingLegacyCalculationSnapshot === false
    ? null
    : pendingLegacyCalculationSnapshot ||
      selectedQuote?.legacyCalculationSnapshot ||
      null;
const canRevertLegacyCurtainRecalculation =
  canEditQuotes && (isSuperAdmin || isWorkspaceOwner || canAccessSettingsTab);

useEffect(() => {
  if (!legacyRecalculationRevisionRequest) return undefined;
  const requestId = legacyRecalculationRevisionRequest.id;
  const timer = window.setTimeout(async () => {
    if (legacyRecalculationRevisionSaveRef.current === requestId) return;
    legacyRecalculationRevisionSaveRef.current = requestId;
    const saved = await saveQuotationAsNewRef.current?.();
    setLegacyRecalculationRevisionRequest(null);
    if (saved) {
      setPendingRecalculationAudit(null);
      setPendingLegacyCalculationSnapshot(null);
    } else {
      notify(
        "The recalculated revision was prepared but could not be saved. You can try Save As New again.",
        "warning"
      );
    }
    legacyRecalculationRevisionSaveRef.current = "";
  }, 0);

  return () => window.clearTimeout(timer);
}, [legacyRecalculationRevisionRequest, notify]);

useEffect(() => {
  if (!isDevelopmentMode()) return;
  const currentQuoteNo = quoteNo || selectedQuote?.quoteNo || "";
  if (!rows.length) return;

  const traceRows = rows.map((row, index) => {
    const customerCalc = getCurtainProductionAdjustedRowCalc(row, index);
    const baseCalc = calcRow(row, repeatSettings);
    const rowPlan = curtainFabricSummaryResult?.plan?.rowPlans?.[index] || null;
    const workshopNewFabricMeters = round2(
      Number(
        customerCalc.workshopNewFabricMeters ??
          rowPlan?.newFabricNeededMeters ??
          rowPlan?.newMeters ??
          baseCalc.fabricMeters ??
          0
      )
    );
    const discountPercent = Math.min(100, num(row.discount));
    const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
    const workshopFabricCost =
      (baseCalc.isCurtain || baseCalc.isRoman) && rowPlan
        ? round2(workshopNewFabricMeters * mrpPerMeter * (1 - discountPercent / 100))
        : Number(baseCalc.fabricCost || 0);
    const workshopRowTotal =
      (baseCalc.isCurtain || baseCalc.isRoman) && rowPlan
        ? round2(Number(baseCalc.rowTotal || 0) - Number(baseCalc.fabricCost || 0) + workshopFabricCost)
        : Number(customerCalc.rowTotal || baseCalc.rowTotal || 0);

    return {
      item: row.item,
      product: row.product || row.productType || row.productName,
      fabric: row.fabric,
      fabricLabel: getCurtainRowFabricLabel(row),
      stitch: row.stitch || row.stitching || row.stitchLabel,
      mrp: row.mrp || row.mrpPerMeter || "",
      oldOrExpectedOrderedFabric: row.expectedOrderedFabricMeters,
      cutFabricMeters: customerCalc.cutFabricMeters,
      internalNewFabricNeededMeters:
        customerCalc.internalNewFabricNeededMeters ?? workshopNewFabricMeters,
      workshopNewFabricMeters,
      internalHalfReusedMeters:
        customerCalc.internalHalfReusedMeters ?? customerCalc.halfReusedMeters ?? 0,
      noReuseOrderedFabricMeters: customerCalc.noReuseOrderedFabricMeters,
      customerBillableFabricMeters: customerCalc.customerBillableFabricMeters,
      lineTotal: customerCalc.lineTotal ?? customerCalc.rowTotal,
      rowTotal: customerCalc.rowTotal,
      workshopOptimizedRowTotal: workshopRowTotal,
      calculationType: customerCalc.calculationType || row.calculationType,
      lineType: customerCalc.lineType || row.lineType,
      internalHalfPieceSavingMeters: customerCalc.internalHalfPieceSavingMeters || 0,
      internalHalfPieceSavingValue: customerCalc.internalHalfPieceSavingValue || 0,
    };
  });
  const customerBillableSubtotal = round2(
    traceRows.reduce((sum, row) => sum + Number(row.rowTotal || 0), 0)
  );
  const workshopOptimizedSubtotal = round2(
    traceRows.reduce((sum, row) => sum + Number(row.workshopOptimizedRowTotal || 0), 0)
  );
  const affectedRows = traceRows.filter((row) =>
    /living w3 sheer|dining w1 sheer|up bedroom w1 sheer|up bedroom 3-w1 sheer|3rd floor sheer/i.test(
      String(row.item || "")
    )
  );
  const fabricBillingSummaries = Object.values(
    traceRows.reduce((acc, row) => {
      const fabric = row.fabricLabel || row.fabric || "Unknown Fabric";
      if (!acc[fabric]) {
        acc[fabric] = {
          fabricLabel: fabric,
          cutFabricMeters: 0,
          halfReusedMeters: 0,
          newFabricNeededMeters: 0,
          customerBillableFabricMeters: 0,
        };
      }
      acc[fabric].cutFabricMeters = round2(
        acc[fabric].cutFabricMeters + Number(row.cutFabricMeters || 0)
      );
      acc[fabric].halfReusedMeters = round2(
        acc[fabric].halfReusedMeters + Number(row.internalHalfReusedMeters || 0)
      );
      acc[fabric].newFabricNeededMeters = round2(
        acc[fabric].newFabricNeededMeters + Number(row.internalNewFabricNeededMeters || 0)
      );
      acc[fabric].customerBillableFabricMeters = round2(
        acc[fabric].customerBillableFabricMeters + Number(row.customerBillableFabricMeters || 0)
      );
      return acc;
    }, {})
  );
  const totalCutFabricMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.cutFabricMeters || 0), 0)
  );
  const totalNewFabricNeededMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.newFabricNeededMeters || 0), 0)
  );
  const totalHalfReusedMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.halfReusedMeters || 0), 0)
  );
  const totalCustomerBillableFabricMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.customerBillableFabricMeters || 0), 0)
  );

  devLog("QUOTE_TOTAL_REGRESSION_TRACE", {
    quoteNo: currentQuoteNo,
    passHalfPieceSavingsToCustomer,
    customerBillableSubtotal,
    workshopOptimizedSubtotal,
    difference: round2(customerBillableSubtotal - workshopOptimizedSubtotal),
    affectedSheerRows: affectedRows,
    rows: traceRows,
  });
  devLog("HALF_PIECE_BILLING_TRACE", {
    quoteNo: currentQuoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    rows: traceRows.map((row) => ({
      item: row.item,
      fabric: row.fabricLabel || row.fabric,
      mrp: row.mrp,
      cutFabricMeters: row.cutFabricMeters,
      internalNewFabricNeededMeters: row.internalNewFabricNeededMeters,
      internalHalfReusedMeters: row.internalHalfReusedMeters,
      noReuseOrderedFabricMeters: row.noReuseOrderedFabricMeters,
      customerBillableFabricMeters: row.customerBillableFabricMeters,
      lineTotal: row.lineTotal,
    })),
    subtotal: customerBillableSubtotal,
  });
  devLog("CUSTOMER_BILLABLE_FABRIC_TRACE", {
    quoteNo: currentQuoteNo,
    cutFabricMeters: totalCutFabricMeters,
    newFabricNeededMeters: totalNewFabricNeededMeters,
    halfReusedMeters: totalHalfReusedMeters,
    customerBillableFabricMeters: totalCustomerBillableFabricMeters,
    subtotal: customerBillableSubtotal,
    grandTotal: summaryTotals.grandTotal,
    expectedTotalForCurrentQuote: 286495.84,
  });
  devTable(
    fabricBillingSummaries.map((fabric) => ({
      fabric: fabric.fabricLabel,
      cut: fabric.cutFabricMeters,
      halfReused: fabric.halfReusedMeters,
      newNeeded: fabric.newFabricNeededMeters,
      billable: fabric.customerBillableFabricMeters,
    }))
  );
  devLog("HALF_PIECE_PASS_SAVINGS_TRACE", {
    quoteNo: currentQuoteNo,
    passHalfPieceSavingsToCustomer,
    fabrics: fabricBillingSummaries.map((fabric) => ({
      fabric: fabric.fabricLabel,
      cutFabricMeters: fabric.cutFabricMeters,
      newFabricNeededMeters: fabric.newFabricNeededMeters,
      customerBillableFabricMeters: fabric.customerBillableFabricMeters,
    })),
  });
  devLog("MIXED_GST_TOTAL_TRACE", {
    quoteGstRate: summaryTotals.quoteGstRate,
    measurementSubtotalBeforeDiscount: summaryTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: summaryTotals.accessorySubtotalBeforeGst,
    discountAmount: summaryTotals.discountAmount,
    taxableMeasurementAmount: summaryTotals.taxableMeasurementAmount,
    measurementGstAmount: summaryTotals.measurementGstAmount,
    accessoryGstAmount: summaryTotals.accessoryGstAmount,
    totalGstAmount: summaryTotals.totalGstAmount,
    grandTotal: summaryTotals.grandTotal,
    accessoryRows: rows
      .map((row, index) => ({
        row,
        index,
        calc: getCurtainProductionAdjustedRowCalc(row, index),
      }))
      .filter(({ row }) => isCurtainAccessoryRowStatic(row))
      .map(({ row, calc }) => ({
        product: row.productName || row.productType || row.product || "",
        calculationType: calc.calculationType || row.calculationType || "",
        qty: row.qty ?? row.quantity ?? calc.quantity ?? "",
        amount: row.amount ?? row.manualAmount ?? row.lineAmount ?? "",
        unitRate: row.unitRate ?? calc.unitRate ?? "",
        gstRate: resolveCurtainAccessoryRowGstRate(row),
        taxable: calc.taxableAmount ?? calc.rowTotal ?? 0,
        gstAmount: round2(
          (Number(calc.taxableAmount ?? calc.rowTotal ?? 0) *
            resolveCurtainAccessoryRowGstRate(row)) /
            100
        ),
        total: round2(
          Number(calc.taxableAmount ?? calc.rowTotal ?? 0) +
            (Number(calc.taxableAmount ?? calc.rowTotal ?? 0) *
              resolveCurtainAccessoryRowGstRate(row)) /
              100
        ),
      })),
  });
}, [
  calcRow,
  curtainFabricSummaryResult,
  getCurtainProductionAdjustedRowCalc,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  repeatSettings,
  rows,
  selectedQuote?.quoteNo,
  summaryTotals.accessoryGstAmount,
  summaryTotals.accessorySubtotalBeforeGst,
  summaryTotals.accessorySubtotalBeforeDiscount,
  summaryTotals.discountAmount,
  summaryTotals.grandTotal,
  summaryTotals.measurementGstAmount,
  summaryTotals.measurementSubtotalBeforeDiscount,
  summaryTotals.quoteGstRate,
  summaryTotals.taxableMeasurementAmount,
  summaryTotals.totalGstAmount,
  useHalfPieceReuse,
]);

const generalQuoteDebugSnapshot = useMemo(() => {
  const normalized = syncGeneralQuoteDefaults(generalQuote || {});
  const normalizedTotals = calculateGeneralQuoteTotals(normalized);
  const quoteId =
    generalEditingQuoteId ||
    selectedQuote?.id ||
    normalized.quoteNo ||
    "new";
  const quoteSearchIndex = buildSafeQuoteSearchIndex({
    id: quoteId,
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalized.quoteNo,
    generalQuote: normalized,
  });
  const normalizedQuoteDate = resolveQuoteDateForInput(
    {
      ...selectedQuote,
      generalQuote: normalized,
    },
    todayISO()
  );
  const rawPayload = {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalized.quoteNo || "",
    quoteDate: normalizedQuoteDate,
    date: normalizedQuoteDate,
    quotationDate: normalizedQuoteDate,
    customerName:
      normalized.quoteFor?.company ||
      normalized.quoteFor?.name ||
      normalized.customerName ||
      "",
    generalQuote: normalized,
    subtotal: Number(normalizedTotals.subtotal || 0),
    subtotalBeforeGst: Number(normalizedTotals.subtotalBeforeGst || 0),
    taxableAmount: Number(normalizedTotals.taxableAmount || 0),
    totalGst: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    totalGstAmount: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    gstAmount: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    totalAfterGst: Number(normalizedTotals.totalAfterGst || 0),
    grandTotal: Number(normalizedTotals.grandTotal || 0),
    finalTotal: Number(normalizedTotals.grandTotal || 0),
    totalInWords: normalizedTotals.totalInWords || "",
    discountType: normalizedTotals.discountType || normalized.discountType,
    discountValue: Number(
      normalizedTotals.discountValue ?? normalized.discountValue ?? 0
    ),
    discountAmount: Number(normalizedTotals.discountAmount || 0),
    discountApplication: "after_gst",
    searchText: quoteSearchIndex.searchText,
    searchKeywords: quoteSearchIndex.searchKeywords,
    searchTokens: quoteSearchIndex.searchKeywords,
  };
  const sanitizedPayload = sanitizeGeneralQuotePayload(rawPayload);
  const diagnostics = inspectGeneralQuotePayload(rawPayload);
  const imageMetadataCount = (normalized.lineItems || []).reduce(
    (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
    0
  );

  return {
    workspaceUid,
    uid: auth.currentUser?.uid || user?.uid || "",
    email: auth.currentUser?.email || user?.email || "",
    quoteId,
    quoteNo: normalized.quoteNo || "",
    isEditingExistingQuote: Boolean(generalEditingQuoteId),
    savePath: workspaceUid
      ? `users/${workspaceUid}/quotations/${quoteId}`
      : "",
    payloadSizeBytes: estimateJsonSizeBytes(sanitizedPayload),
    payloadKeys: Object.keys(sanitizedPayload || {}),
    hasInvalidUndefinedFields: diagnostics.hasUndefinedFields,
    hasFileBlobObjects: diagnostics.hasFileBlobObjects,
    hasInvalidNumbers: diagnostics.hasInvalidNumbers,
    searchTextLength: String(sanitizedPayload.searchText || "").length,
    searchKeywordsCount: Array.isArray(sanitizedPayload.searchKeywords)
      ? sanitizedPayload.searchKeywords.length
      : 0,
    discountType: sanitizedPayload.discountType,
    discountValue: sanitizedPayload.discountValue,
    itemCount: normalized.lineItems?.length || 0,
    imageMetadataCount,
  };
}, [
  generalEditingQuoteId,
  generalQuote,
  selectedQuote,
  syncGeneralQuoteDefaults,
  user,
  workspaceUid,
]);

const renderGeneralQuoteDebugPanel = () => {
  if (!IS_DEV_UI_ENABLED) return null;

  const info = generalQuoteDebugSnapshot;
  const latestUpload =
    generalQuoteDebugState.lastUpload ||
    generalQuoteDebugState.directStorageTest ||
    {};
  const debugRow = (label, value) => (
    <div
      key={label}
      style={{
        display: "grid",
        gridTemplateColumns: "190px minmax(0, 1fr)",
        gap: 10,
        fontSize: 12,
        alignItems: "start",
      }}
    >
      <strong style={{ color: isDark ? "#bfdbfe" : "#1d4ed8" }}>{label}</strong>
      <span
        style={{
          color: isDark ? "#e2e8f0" : "#0f172a",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {typeof value === "string" || typeof value === "number"
          ? String(value)
          : JSON.stringify(value, null, 2)}
      </span>
    </div>
  );

  return (
    <div
      style={{
        margin: "0 0 16px",
        borderRadius: 18,
        border: isDark
          ? "1px solid rgba(96,165,250,0.35)"
          : "1px solid rgba(37,99,235,0.22)",
        background: isDark ? "rgba(15,23,42,0.92)" : "#eff6ff",
        padding: 14,
      }}
    >
      <button
        type="button"
        onClick={() => setIsGeneralQuoteDebugOpen((prev) => !prev)}
        style={{
          width: "100%",
          border: 0,
          background: "transparent",
          color: isDark ? "#dbeafe" : "#1e3a8a",
          fontWeight: 900,
          fontSize: 14,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        General Quote Debug {isGeneralQuoteDebugOpen ? "Hide" : "Show"}
      </button>

      {isGeneralQuoteDebugOpen ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {debugRow("workspaceUid", info.workspaceUid || "(empty)")}
          {debugRow("current user", `${info.uid || "(no uid)"} / ${info.email || "(no email)"}`)}
          {debugRow("quoteId", info.quoteId)}
          {debugRow("quoteNo", info.quoteNo || "(empty)")}
          {debugRow("is editing existing", String(info.isEditingExistingQuote))}
          {debugRow("save path", info.savePath || "(missing workspace)")}
          {debugRow("payload size bytes", info.payloadSizeBytes)}
          {debugRow("payload keys", info.payloadKeys.join(", "))}
          {debugRow("has undefined fields", String(info.hasInvalidUndefinedFields))}
          {debugRow("has File/Blob objects", String(info.hasFileBlobObjects))}
          {debugRow("has NaN/Infinity", String(info.hasInvalidNumbers))}
          {debugRow("searchText length", info.searchTextLength)}
          {debugRow("searchKeywords count", info.searchKeywordsCount)}
          {debugRow("discount", `${info.discountType} / ${info.discountValue}`)}
          {debugRow("item count", info.itemCount)}
          {debugRow("image metadata count", info.imageMetadataCount)}
          {debugRow("selected item id", latestUpload.itemId || "(none)")}
          {debugRow("file", latestUpload.fileName ? `${latestUpload.fileName} / ${latestUpload.fileType || ""} / ${latestUpload.fileSize || 0}` : "(none)")}
          {debugRow("intended storage path", latestUpload.storagePath || "(none)")}
          {debugRow("upload state", generalQuoteDebugState.uploadState || "idle")}
          {debugRow("last upload error", generalQuoteDebugState.lastUploadError || "(none)")}
          {debugRow("last save error", generalQuoteDebugState.lastSaveError || "(none)")}
          {debugRow("direct Firestore test", generalQuoteDebugState.directFirestoreTest || "(not run)")}
          {debugRow("direct Storage test", generalQuoteDebugState.directStorageTest || "(not run)")}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
            <Button onClick={testGeneralQuoteFirestoreWrite}>
              Test Firestore General Quote Save
            </Button>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 12px",
                borderRadius: 12,
                background: isDark ? "#020617" : "#ffffff",
                border: "1px solid rgba(148,163,184,0.35)",
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              Choose Test Image
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                onChange={(event) =>
                  setGeneralQuoteDebugUploadFile(event.target.files?.[0] || null)
                }
              />
            </label>
            <Button onClick={testGeneralQuoteImageUpload}>
              Test General Image Upload
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const generalQuoteTotals = useMemo(() => {
  return calculateGeneralQuoteTotals(
    syncGeneralQuoteDefaults(generalQuote)
  );
}, [generalQuote, syncGeneralQuoteDefaults]);

function getGeneralActionItems(quoteLike = {}) {
  if (Array.isArray(quoteLike?.lineItems)) return quoteLike.lineItems;
  if (Array.isArray(quoteLike?.items)) return quoteLike.items;
  return [];
}

function getGeneralQuoteAnyAliasItemCount(quoteLike = {}) {
  return Math.max(
    0,
    ...Object.values(getGeneralQuoteContentAliasArrays(quoteLike || {})).map((value) =>
      Array.isArray(value) ? value.length : 0
    )
  );
}

function getCurtainQuoteAnyAliasRowCount(quoteLike = {}) {
  return Math.max(
    0,
    ...Object.values(getCurtainQuoteContentAliasArrays(quoteLike || {})).map((value) =>
      Array.isArray(value) ? value.length : 0
    )
  );
}

function getGeneralActionContentScore(quoteLike = {}) {
  return getGeneralActionItems(quoteLike).reduce((score, item) => {
    const hasText = [
      item?.name,
      item?.itemName,
      item?.productName,
      item?.description,
      item?.room,
      item?.material,
      item?.fabric,
      item?.notes,
    ].some((value) => String(value || "").trim());
    const hasSpecs =
      Array.isArray(item?.specifications) && item.specifications.length > 0;
    const hasImages =
      normalizeGeneralQuoteItemImages(item).length > 0;
    const hasAmount = [
      item?.qty,
      item?.quantity,
      item?.rate,
      item?.price,
      item?.amount,
      item?.total,
      item?.grandTotal,
    ].some((value) => Number.isFinite(Number(value)) && Number(value) > 0);

    return score + (hasText || hasSpecs || hasImages || hasAmount ? 1 : 0);
  }, 0);
}

function countGeneralActionImages(items = []) {
  return (Array.isArray(items) ? items : []).reduce(
    (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
    0
  );
}

function getGeneralVisibleActionSnapshot() {
  const currentQuote = syncGeneralQuoteDefaults(generalQuoteRef.current || generalQuote || {});
  const visibleItems = getGeneralActionItems(currentQuote);
  return {
    quote: currentQuote,
    quoteNo: currentQuote.quoteNo || quoteNo || "",
    quoteId: generalEditingQuoteId || selectedQuote?.id || "",
    customerName:
      currentQuote.quoteFor?.name ||
      currentQuote.customerName ||
      quoteCustomerLink?.customerName ||
      customerName ||
      "",
    itemCount: visibleItems.length,
    imageCount: countGeneralActionImages(visibleItems),
  };
}

function beginGeneralActionLock(action = "unknown", lockName = "generalAction") {
  const startedAt = Date.now();
  const existingLock = generalActionRuntimeRef.current.locks?.[action];
  if (existingLock?.timeoutId && typeof window !== "undefined") {
    window.clearTimeout(existingLock.timeoutId);
  }
  const lock = {
    action,
    lockName,
    startedAt,
    timeoutId: null,
  };
  if (typeof window !== "undefined") {
    lock.timeoutId = window.setTimeout(() => {
      const currentLock = generalActionRuntimeRef.current.locks?.[action];
      if (!currentLock || currentLock.startedAt !== startedAt) return;
      const lockAgeMs = Date.now() - startedAt;
      if (lockAgeMs < SAVE_IN_PROGRESS_STALE_MS) return;
      delete generalActionRuntimeRef.current.locks[action];
      if (lockName === "saveInProgress") {
        setIsSaving(false);
      }
      devWarn("GENERAL_ACTION_LOCK_STALE_RESET", {
        action,
        lockName,
        lockAgeMs,
        resetReason: "stale_general_action_timeout",
      });
      if (lockName === "saveInProgress") {
        notify("Please try again. The previous action was reset.", "warning", 4500);
      }
    }, SAVE_IN_PROGRESS_STALE_MS + 1000);
  }
  generalActionRuntimeRef.current.locks = {
    ...(generalActionRuntimeRef.current.locks || {}),
    [action]: lock,
  };
  return lock;
}

function endGeneralActionLock(action = "unknown") {
  const lock = generalActionRuntimeRef.current.locks?.[action];
  if (lock?.timeoutId && typeof window !== "undefined") {
    window.clearTimeout(lock.timeoutId);
  }
  if (lock) {
    delete generalActionRuntimeRef.current.locks[action];
  }
}

function traceGeneralActionHealth(action = "unknown", payload = null) {
  if (!IS_DEV_UI_ENABLED) return null;
  const visible = getGeneralVisibleActionSnapshot();
  const payloadQuote = payload?.quote || {};
  const payloadItems = Array.isArray(payload?.items)
    ? payload.items
    : getGeneralActionItems(payloadQuote);
  const customerNameInPayload =
    payloadQuote.quoteFor?.name ||
    payloadQuote.customerName ||
    payloadQuote.clientName ||
    "";
  const lastPayloadBuiltAt =
    payload?.payloadBuiltAt || generalActionRuntimeRef.current.lastPayloadBuiltAt || null;
  const activeLocks = generalActionRuntimeRef.current.locks || {};
  const trace = {
    action,
    quoteNo: payload?.quoteNo || visible.quoteNo,
    quoteId: visible.quoteId,
    isNewQuote: generalQuoteEditorMode !== QUOTE_EDITOR_MODE_EDITING_EXISTING,
    saveInProgress: Boolean(isSaving || activeLocks.save || activeLocks.saveAsNew),
    exportInProgress: Boolean(activeLocks.customerPdf || activeLocks.executionPdf),
    imageCanonicalizationInProgress: Boolean(
      generalUploadingItemId || generalUploadPromiseRef.current
    ),
    customerNameVisible: visible.customerName,
    customerNameInPayload,
    itemCountVisible: visible.itemCount,
    itemCountInPayload: payloadItems.length,
    imageCountVisible: visible.imageCount,
    imageCountInPayload: countGeneralActionImages(payloadItems),
    buildLabel: FABRIQUOTE_BUILD_INFO.label,
    mountedAt: generalActionRuntimeRef.current.mountedAt,
    lastHydratedAt: generalActionRuntimeRef.current.lastHydratedAt,
    lastPayloadBuiltAt,
  };
  devLog("GENERAL_ACTION_HEALTH_TRACE", trace);
  return trace;
}

function buildLiveGeneralQuotePayload({ action = "unknown" } = {}) {
  const selectedGeneralQuote =
    generalEditingQuoteId &&
    selectedQuote &&
    resolveQuoteType(selectedQuote) === GENERAL_QUOTE_TYPE
      ? selectedQuote.generalQuote ||
        selectedQuote.generalQuoteData ||
        selectedQuote
      : null;
  const candidates = [
    { source: "generalQuoteRef", quote: generalQuoteRef.current },
    { source: "generalQuoteState", quote: generalQuote },
    { source: "selectedQuote", quote: selectedGeneralQuote },
  ]
    .filter((candidate) => candidate.quote && typeof candidate.quote === "object")
    .map((candidate) => {
      const normalized = syncGeneralQuoteDefaults(candidate.quote);
      return {
        ...candidate,
        quote: normalized,
        itemCount: getGeneralActionItems(normalized).length,
        contentScore: getGeneralActionContentScore(normalized),
      };
    });

  const chosenCandidate =
    candidates.find((candidate) => candidate.source === "generalQuoteRef") ||
    candidates.find((candidate) => candidate.source === "generalQuoteState") ||
    candidates.find((candidate) => candidate.source === "selectedQuote") ||
    { source: "empty", quote: syncGeneralQuoteDefaults({}) };

  const liveGeneralSiteSelection = normalizeQuoteSiteSnapshot(
    {
      ...quoteSiteSelection,
      ...chosenCandidate.quote,
    },
    {
      customerName:
        chosenCandidate.quote?.quoteFor?.name ||
        chosenCandidate.quote?.customerName ||
        quoteCustomerLink?.customerName ||
        customerName ||
        "",
    }
  );
  const liveCustomerName =
    chosenCandidate.quote?.quoteFor?.name ||
    chosenCandidate.quote?.customerName ||
    liveGeneralSiteSelection.customerName ||
    quoteCustomerLink?.customerName ||
    customerName ||
    "";
  const liveCustomerPhone =
    chosenCandidate.quote?.quoteFor?.phone ||
    chosenCandidate.quote?.customerPhone ||
    liveGeneralSiteSelection.customerPhone ||
    quoteCustomerLink?.phone ||
    "";
  const liveCustomerEmail =
    chosenCandidate.quote?.quoteFor?.email ||
    chosenCandidate.quote?.customerEmail ||
    liveGeneralSiteSelection.customerEmail ||
    quoteCustomerLink?.email ||
    "";
  const liveCustomerAddress =
    chosenCandidate.quote?.quoteFor?.address ||
    chosenCandidate.quote?.customerAddress ||
    liveGeneralSiteSelection.customerAddress ||
    quoteCustomerLink?.address ||
    "";
  const liveCustomerCompanyName =
    chosenCandidate.quote?.quoteFor?.company ||
    chosenCandidate.quote?.customerCompanyName ||
    liveGeneralSiteSelection.customerCompanyName ||
    quoteCustomerLink?.companyName ||
    "";
  const liveReferralPartner =
    selectedReferralPartnerRef.current ||
    normalizeQuoteReferralPartner(chosenCandidate.quote) ||
    normalizeQuoteReferralPartner(selectedQuote || {});
  const liveReferralPartnerFields =
    buildReferralPartnerQuoteFields(liveReferralPartner);
  const normalizedQuote = syncGeneralQuoteDefaults({
    ...chosenCandidate.quote,
    ...liveReferralPartnerFields,
    quoteNo: chosenCandidate.quote?.quoteNo || quoteNo,
    ...liveGeneralSiteSelection,
    customerName: liveCustomerName,
    customerPhone: liveCustomerPhone,
    customerEmail: liveCustomerEmail,
    customerAddress: liveCustomerAddress,
    customerCompanyName: liveCustomerCompanyName,
    quoteFor: {
      ...(chosenCandidate.quote?.quoteFor || {}),
      name: liveCustomerName,
      company: liveCustomerCompanyName,
      phone: liveCustomerPhone,
      email: liveCustomerEmail,
      address: liveCustomerAddress,
    },
  });
  const totals = calculateGeneralQuoteTotals(normalizedQuote);
  const items = getGeneralActionItems(normalizedQuote);
  const payloadBuiltAt = Date.now();
  generalActionRuntimeRef.current.lastPayloadBuiltAt = payloadBuiltAt;

  if (IS_DEV_UI_ENABLED) {
    devLog("QUOTE_ACTION_CLICK_TRACE", {
      action,
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: normalizedQuote.quoteNo || quoteNo || "",
      source: chosenCandidate.source,
      saveSource:
        chosenCandidate.source === "selectedQuote" ? "loadedSnapshot" : "latestRef",
      candidateSummary: candidates.map((candidate) => ({
        source: candidate.source,
        itemCount: candidate.itemCount,
        contentScore: candidate.contentScore,
      })),
      rowsCount: items.length,
      itemsCount: items.length,
      grandTotal: totals.grandTotal,
      subtotal: totals.subtotal,
      discountType: totals.discountType,
      discountValue: totals.discountValue,
      gstRate: totals.gstRate,
    });
  }

  return {
    action,
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalizedQuote.quoteNo || quoteNo,
    quote: normalizedQuote,
    items,
    payloadBuiltAt,
    saveSource:
      chosenCandidate.source === "selectedQuote" ? "loadedSnapshot" : "latestRef",
    summaryTotals: totals,
    totals,
  };
}

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }

  const normalizedDiscount = normalizeGeneralDiscount(
    generalQuote || {},
    "general_discount_input_trace"
  );

  devLog("DISCOUNT_INPUT_STATE_TRACE", {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: generalQuote?.quoteNo || quoteNo,
    inputRawValue: generalQuote?.discountValue ?? "",
    parsedDiscountValue: Number(normalizedDiscount.discountValue || 0),
    discountType: normalizedDiscount.discountType,
    quoteSettingsDiscountValue: generalQuote?.discountValue ?? "",
    helperDiscountValue: generalQuoteTotals?.discountValue,
    helperDiscountType: generalQuoteTotals?.discountType,
    liveDiscountAmount: generalQuoteTotals?.discountAmount,
    liveTotalAfterGst: generalQuoteTotals?.totalAfterGst,
    liveGrandTotal: generalQuoteTotals?.grandTotal,
  });
}, [
  generalQuote,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.discountType,
  generalQuoteTotals?.discountValue,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.totalAfterGst,
  quoteNo,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }
  const totalAfterGst = Number(generalQuoteTotals?.totalAfterGst || 0);
  const discountAmount = Number(generalQuoteTotals?.discountAmount || 0);
  const expectedGrandTotal = round2(Math.max(0, totalAfterGst - discountAmount));
  const actualGrandTotal = round2(Number(generalQuoteTotals?.grandTotal || 0));
  if (discountAmount > 0.005 && Math.abs(expectedGrandTotal - actualGrandTotal) > 0.05) {
    devError("DISCOUNT_NOT_APPLIED_TO_GRAND_TOTAL", {
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: generalQuote?.quoteNo || quoteNo,
      totalAfterGst,
      discountType: generalQuoteTotals?.discountType,
      discountValue: generalQuoteTotals?.discountValue,
      discountAmount,
      grandTotal: actualGrandTotal,
      expectedGrandTotal,
    });
  }
}, [
  generalQuote?.quoteNo,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.discountType,
  generalQuoteTotals?.discountValue,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.totalAfterGst,
  quoteNo,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }

  const liveRows = Array.isArray(generalQuote?.lineItems)
    ? generalQuote.lineItems
    : [];
  const legacyAliasRows = Array.isArray(generalQuote?.items)
    ? generalQuote.items
    : [];

  if (!liveRows.length || !legacyAliasRows.length || liveRows === legacyAliasRows) {
    return;
  }

  const buildTotalSignature = (rows) =>
    rows
      .map((item) =>
        [
          item?.id,
          item?.name || item?.itemName,
          item?.quantity,
          item?.rate,
          item?.amount,
          item?.gstPercent,
          item?.gstMode,
        ]
          .map((value) => String(value ?? ""))
          .join("|")
      )
      .join("||");

  const liveSignature = buildTotalSignature(liveRows);
  const aliasSignature = buildTotalSignature(legacyAliasRows);

  if (liveSignature === aliasSignature) {
    return;
  }

  const staleAliasGrandTotal = calcGeneralGrandTotal({
    ...generalQuote,
    lineItems: legacyAliasRows,
    items: legacyAliasRows,
  });
  const liveGrandTotal = Number(generalQuoteTotals?.grandTotal || 0);
  const grandTotalDelta = Math.abs(staleAliasGrandTotal - liveGrandTotal);

  if (grandTotalDelta <= 0.05) {
    return;
  }

  const warningSignature = [
    generalQuote?.quoteNo || "",
    liveSignature,
    aliasSignature,
    liveGrandTotal,
    staleAliasGrandTotal,
  ].join("::");

  if (generalQuoteStaleTotalsWarningRef.current === warningSignature) {
    return;
  }

  generalQuoteStaleTotalsWarningRef.current = warningSignature;
  devError("QUOTE_TOTAL_STALE_STATE_DETECTED", {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: generalQuote?.quoteNo || quoteNo,
    changedField: "lineItems",
    lineTotals: liveRows.map((item) => ({
      id: item?.id || "",
      total: getGeneralItemAmount(item),
      taxableAmount: getGeneralItemBaseAmount(item),
      gstAmount: getGeneralItemTaxAmount(item),
    })),
    lineItemsCount: liveRows.length,
    itemsAliasCount: legacyAliasRows.length,
    subtotal: Number(generalQuoteTotals?.subtotalBeforeGst ?? generalQuoteTotals?.subtotalExcludingGst ?? 0),
    totalGst: Number(generalQuoteTotals?.totalGstAmount ?? generalQuoteTotals?.gstTotal ?? 0),
    totalAfterGst: Number(generalQuoteTotals?.totalAfterGst || 0),
    discountAmount: Number(generalQuoteTotals?.discountAmount || 0),
    grandTotal: liveGrandTotal,
    displayedGrandTotal: liveGrandTotal,
    staleAliasSubtotal: calcGeneralSubtotal(legacyAliasRows),
    staleAliasGrandTotal,
    grandTotalDelta,
  });
}, [
  generalQuote,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.gstTotal,
  generalQuoteTotals?.subtotal,
  generalQuoteTotals?.subtotalBeforeGst,
  generalQuoteTotals?.subtotalExcludingGst,
  generalQuoteTotals?.totalAfterGst,
  generalQuoteTotals?.totalGstAmount,
  quoteNo,
  quoteSubTab,
  tab,
]);

/* =============================================
    FABRIC SUMMARY  reacts to repeat + hem
   ============================================= */
// ================= PERIOD FILTER (FIXED & SAFE) =================
function isDateInPeriod(date, type, customRange) {
  if (!date) return false;

  const now = new Date();
  const d = new Date(date);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (type) {
    case "today":
      return (
        d >= startOfToday &&
        d < new Date(startOfToday.getTime() + 86400000)
      );

    case "thisWeek": {
      const firstDay = new Date(now);
      firstDay.setDate(now.getDate() - now.getDay());
      firstDay.setHours(0, 0, 0, 0);
      const nextWeek = new Date(firstDay);
      nextWeek.setDate(firstDay.getDate() + 7);

      return d >= firstDay && d < nextWeek;
    }

    case "thisMonth":
      return (
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
      );

    case "lastMonth": {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return (
        d.getMonth() === lastMonth.getMonth() &&
        d.getFullYear() === lastMonth.getFullYear()
      );
    }

    case "thisYear":
      return d.getFullYear() === now.getFullYear();

    case "custom":
      if (!customRange.start || !customRange.end) return false;

      const start = new Date(`${customRange.start}T00:00:00`);
      const end = new Date(`${customRange.end}T23:59:59.999`);
      end.setHours(23, 59, 59, 999);

      return d >= start && d <= end;

    default:
      return true;
  }
}


async function updateWorkStage(quoteInput, newStage) {
  try {
    if (!canEditQuotes) {
      throw new Error("You do not have permission to update quotation stages.");
    }

    if (!workspaceUid) throw new Error("User not authenticated");

    const uid = workspaceUid;
    const quoteObject =
      quoteInput && typeof quoteInput === "object" ? quoteInput : null;
    const quoteIdentity = getSavedQuoteWorkflowIdentity(
      quoteObject || { id: quoteInput, quoteId: quoteInput },
      workspaceUid
    );
    const quoteId = quoteObject
      ? quoteIdentity.fullQuoteId || quoteIdentity.quoteId || quoteObject.id
      : quoteInput;
    const sourceLead =
      (!quoteObject && crmLeadRecords.find((item) => item.id === quoteId)) || null;

    if (sourceLead) {
      const nextStageMeta = getStageMetaForType(
        sourceLead.pipelineType,
        newStage
      );
      const nextStatus =
        nextStageMeta?.confirmsQuote === true ||
        String(sourceLead?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);

      if (selectedCrmDealId === quoteId) {
        updateCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                stageId: newStage,
                status: nextStatus,
                checklistItems: cloneChecklistTree(nextChecklistItems, {
                  preserveIds: true,
                  resetState: false,
                  idPrefix: "crm-check",
                }),
              }
            : prev
        );
      }

      await saveCrmFieldsForLead(quoteId, {
        stageId: newStage,
        status: nextStatus,
        checklistItems: nextChecklistItems,
      });
      notify("Stage updated", "success");
      return;
    }

    const sourceQuote =
      quoteObject ||
      savedQuotesRef.current.find((item) =>
        savedQuoteMatchesWorkflowIdentity(item, quoteIdentity)
      ) ||
      null;

    if (sourceQuote) {
      const busyKey = getSavedQuoteWorkflowBusyKey(
        sourceQuote,
        "updateStage",
        workspaceUid
      );
      setSavedQuoteActionBusyKey(busyKey);
      try {
        const result = await performSavedQuoteAction({
          quote: sourceQuote,
          action: "updateStage",
          updates: { stage: newStage },
        });
        notify(result.successMessage || "Stage updated", "success");
      } finally {
        setSavedQuoteActionBusyKey("");
      }
      return;
    }

    const quoteRef = doc(db, "users", uid, "quotations", quoteId);

    await runTransaction(db, async (tx) => {
      const quoteSnap = await tx.get(quoteRef);
      if (!quoteSnap.exists()) throw new Error("Quote not found");

      const quote = quoteSnap.data();
      const quoteType = resolveQuoteType(quote);
      const nextStageMeta = getStageMetaForType(quoteType, newStage);
      const nextStatus =
        nextStageMeta?.confirmsQuote === true ||
        String(quote?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);
      const crmPayload = buildQuoteCrmData(
        {
          id: quoteId,
          ...quote,
          workStage: newStage,
          status: nextStatus,
          installationDate: quote.installationDate || null,
        },
        {
          stageId: newStage,
          status: nextStatus,
          installationDate: quote.installationDate || null,
          checklistItems: nextChecklistItems,
          expectedValue: Number(quote.grandTotal ?? quote.subtotal ?? 0),
          finalValue: Number(quote.grandTotal ?? quote.subtotal ?? 0),
        }
      );

      tx.update(quoteRef, {
        workStage: newStage,
        workStageUpdatedAt: serverTimestamp(),
        status: nextStatus,
        confirmedAt:
          nextStageMeta?.confirmsQuote === true
            ? quote?.confirmedAt || serverTimestamp()
            : quote?.confirmedAt || deleteField(),
        crm: crmPayload,
      });
    });

    notify("Stage updated", "success");

  } catch (err) {
    if (isDevRuntime) {
      const identity = getSavedQuoteWorkflowIdentity(
        quoteInput && typeof quoteInput === "object"
          ? quoteInput
          : { id: quoteInput, quoteId: quoteInput },
        workspaceUid
      );
      devError("SAVED_QUOTE_STAGE_UPDATE_FAILED_EXACT", {
        code: err?.code,
        message: err?.message,
        stack: err?.stack,
        attemptedStage: newStage,
        quoteIndexPath: identity.quoteIndexId
          ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
          : "",
        fullQuotePath: identity.sourcePath,
      });
    } else {
      devError(err);
    }
    const errorMessage = String(err?.message || "");
    if (errorMessage.includes("searchText") || errorMessage.includes("1048487")) {
      notify("Could not save changes. Please try again.", "warning");
    } else {
      notify(
        getSavedQuoteWorkflowFriendlyError(
          err,
          "Could not update the stage. Please try again."
        ),
        "error"
      );
    }
  }
}

  const updateInstallationDate = useCallback(
    async (quoteInput, value) => {
    if (!canEditQuotes) {
      notify("You do not have permission to edit installation dates.", "warning");
      return;
    }

    if (!workspaceUid || !quoteInput) return;

    try {
      const quoteObject =
        quoteInput && typeof quoteInput === "object" ? quoteInput : null;
      const identity = getSavedQuoteWorkflowIdentity(
        quoteObject || { id: quoteInput, quoteId: quoteInput },
        workspaceUid
      );
      const quoteId = identity.fullQuoteId || identity.quoteId || quoteInput;
      const sourceQuote =
        quoteObject ||
        savedQuotesRef.current.find((item) =>
          savedQuoteMatchesWorkflowIdentity(item, identity)
        ) ||
        null;

      if (sourceQuote) {
        const busyKey = getSavedQuoteWorkflowBusyKey(
          sourceQuote,
          "updateInstallationDate",
          workspaceUid
        );
        setSavedQuoteActionBusyKey(busyKey);
        try {
          const result = await performSavedQuoteAction({
            quote: sourceQuote,
            action: "updateInstallationDate",
            updates: { installationDate: value },
          });
          const nextDate = result.localPatch?.installationDate || null;
          const nextCrm = result.localPatch?.crm || buildQuoteCrmData(sourceQuote, {
            installationDate: nextDate,
          });

          if (
            selectedCrmDealId === quoteId ||
            selectedCrmDealId === sourceQuote?.crm?.leadId
          ) {
            setCrmDetailDraft((prev) =>
              prev
                ? {
                    ...prev,
                    installationDate: value || "",
                    reminderConfigs: normalizeCrmReminderConfigs(prev.reminderConfigs, {
                      nextActionDate: parseDateInputValue(prev.nextActionDate),
                      installationDate: nextDate,
                    }),
                  }
                : prev
            );
          }

          if (sourceQuote?.crm?.leadId) {
            await saveCrmFieldsForLead(
              sourceQuote.crm.leadId,
              {
                installationDate: nextDate,
              },
              { syncQuote: false }
            );
          } else {
            await syncReminderDocsForDeal({
              ...sourceQuote,
              id: quoteId,
              crmRecordType: "quote",
              quoteTypeResolved: resolveQuoteType(sourceQuote),
              pipelineType: resolveQuoteType(sourceQuote),
              quoteNo: sourceQuote.quoteNo || "",
              customerLabel:
                sourceQuote.customerName ||
                nextCrm.contact?.name ||
                "",
              crmTitle: nextCrm.title,
              crmContact: nextCrm.contact,
              nextActionDate: nextCrm.nextActionDate,
              installationDate: nextDate,
              reminderConfigs: nextCrm.reminderConfigs,
              followUps: nextCrm.followUps,
              attachments: nextCrm.attachments,
            });
          }

          notify(result.successMessage || "Installation date saved", "success");
        } finally {
          setSavedQuoteActionBusyKey("");
        }
        return;
      }

      const nextDate = parseDateInputValue(value);
      const quoteRef = doc(
        db,
        "users",
        workspaceUid,
        "quotations",
        quoteId
      );

      await updateDoc(quoteRef, {
        installationDate: nextDate ? Timestamp.fromDate(nextDate) : null,
        crm: sourceQuote
          ? buildQuoteCrmData(sourceQuote, {
              installationDate: nextDate,
            })
          : deleteField(),
        updatedAt: serverTimestamp(),
      });

      applyQuoteLocalPatch(quoteId, {
        installationDate: nextDate,
        crm: sourceQuote
          ? buildQuoteCrmData(sourceQuote, {
              installationDate: nextDate,
            })
          : undefined,
        updatedAt: new Date(),
      });

      if (selectedCrmDealId === quoteId) {
        setCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                installationDate: value || "",
                reminderConfigs: normalizeCrmReminderConfigs(prev.reminderConfigs, {
                  nextActionDate: parseDateInputValue(prev.nextActionDate),
                  installationDate: nextDate,
                }),
              }
            : prev
        );
      }

      if (sourceQuote?.crm?.leadId) {
        await saveCrmFieldsForLead(
          sourceQuote.crm.leadId,
          {
            installationDate: nextDate,
          },
          { syncQuote: false }
        );
        notify("Installation date saved", "success");
        return;
      }

      if (sourceQuote) {
        const nextCrm = buildQuoteCrmData(sourceQuote, {
          installationDate: nextDate,
        });

        await syncReminderDocsForDeal({
          ...sourceQuote,
          id: quoteId,
          crmRecordType: "quote",
          quoteTypeResolved: resolveQuoteType(sourceQuote),
          pipelineType: resolveQuoteType(sourceQuote),
          quoteNo: sourceQuote.quoteNo || "",
          customerLabel:
            sourceQuote.customerName ||
            nextCrm.contact?.name ||
            "",
          crmTitle: nextCrm.title,
          crmContact: nextCrm.contact,
          nextActionDate: nextCrm.nextActionDate,
          installationDate: nextDate,
          reminderConfigs: nextCrm.reminderConfigs,
          followUps: nextCrm.followUps,
          attachments: nextCrm.attachments,
        });
      }

      notify("Installation date saved", "success");
    } catch (error) {
      if (isDevRuntime) {
        const identity = getSavedQuoteWorkflowIdentity(
          quoteInput && typeof quoteInput === "object"
            ? quoteInput
            : { id: quoteInput, quoteId: quoteInput },
          workspaceUid
        );
        devError("SAVED_QUOTE_INSTALLATION_DATE_FAILED_EXACT", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          attemptedDate: value,
          quoteIndexPath: identity.quoteIndexId
            ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
            : "",
          fullQuotePath: identity.sourcePath,
        });
      } else {
        devError("Failed to save installation date:", error);
      }
      notify(
        getSavedQuoteWorkflowFriendlyError(
          error,
          "Could not save the installation date. Please try again."
        ),
        "error"
      );
    }
  },
  [
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    canEditQuotes,
    notify,
    performSavedQuoteAction,
    parseDateInputValue,
    saveCrmFieldsForLead,
    selectedCrmDealId,
    syncReminderDocsForDeal,
    workspaceUid,
  ]
);

/* ========================= UPDATE INSTALLATION DATE (SAFE) ========================= */

  /* --------------------------- CUTTING OPTIMIZER CORE HELPERS --------------------------- */

const compareOptimizerSolutions = (a, b) => {
  const aRequiresExtra =
    Boolean(a?.requiresExtraCut) ||
    Number(a?.finalRemainingMeters || 0) > 0 ||
    a?.isCompleteFromAvailable === false;
  const bRequiresExtra =
    Boolean(b?.requiresExtraCut) ||
    Number(b?.finalRemainingMeters || 0) > 0 ||
    b?.isCompleteFromAvailable === false;

  if (aRequiresExtra !== bRequiresExtra) {
    return Number(aRequiresExtra) - Number(bRequiresExtra);
  }

  const wasteDiff =
    Number(a?.totalWasteMeters ?? a?.totalWaste ?? 0) -
    Number(b?.totalWasteMeters ?? b?.totalWaste ?? 0);
  if (wasteDiff !== 0) return wasteDiff;

  const rollCountDiff =
    Number(a?.rollCount ?? a?.totalRollsUsed ?? 0) -
    Number(b?.rollCount ?? b?.totalRollsUsed ?? 0);
  if (rollCountDiff !== 0) return rollCountDiff;

  return (
    Number(a?.totalRollLengthMeters ?? a?.totalUsedMeters ?? 0) -
    Number(b?.totalRollLengthMeters ?? b?.totalUsedMeters ?? 0)
  );
};



const optimizeCutsGGMultiLengthOnly = ({
  rolls,
  demands,
  fabricKey,
  sourceRows = [],
  activeRepeatSettings = repeatSettings,
}) => {
  const cleanRolls = (rolls || [])
    .map(value => Number(String(value).replace(/,/g, '')))
    .filter((r) => Number.isFinite(r) && r > 0)
    .sort((a, b) => b - a);

  const repeatFabric = fabricHasRepeat(fabricKey, sourceRows, activeRepeatSettings);

  // Each demand is already VR-aligned. There is no declared roll starting
  // phase in this model, so adding another whole repeat would double count it.
  const vrMeters = 0;

  if (!cleanRolls.length) {
    alert("Enter at least one roll length.");
    return [];
  }

  const demandMap = {};

  (demands || []).forEach((d) => {
    const len = Number(d.length);
    const qty = Math.max(0, Math.floor(Number(d.qty)));

    if (!Number.isFinite(len) || !Number.isFinite(qty) || len <= 0 || qty <= 0) return;

    const key = String(len);

    if (!demandMap[key])
      demandMap[key] = { length: len, qty: 0 };

    demandMap[key].qty += qty;
  });

  const demandList = Object.values(demandMap)
    .sort((a, b) => b.length - a.length);

  if (!demandList.length) {
    alert("No valid demand pieces.");
    return [];
  }

  const toMm = (m) => Math.floor(m * 1000 + 1e-8);
  
  const bestPatternForRollDP = (rollLenMeters, demandArray) => {

  const rollMm = toMm(rollLenMeters);
  const items = [];

  demandArray.forEach((d) => {
    const lenMm = Math.max(1, toMm(d.length));
    let qty = Math.max(0, Math.floor(d.qty));

    let power = 1;

    while (qty > 0) {
      const take = Math.min(power, qty);

      items.push({
        lenMm,
        count: take,
        valueMm: lenMm * take,
        origLen: d.length,
      });

      qty -= take;
      power <<= 1;
    }
  });

  const dp = new Int32Array(rollMm + 1);
  const parent = Array(rollMm + 1).fill(null);

  for (let i = 0; i < items.length; i++) {
    const weight = items[i].valueMm;

    for (let cap = rollMm; cap >= weight; cap--) {
      const candidate = dp[cap - weight] + weight;

      if (candidate > dp[cap]) {
        dp[cap] = candidate;
        parent[cap] = { previous: parent[cap - weight], item: i };
      }
    }
  }

  let bestCap = 0;

  for (let c = 1; c <= rollMm; c++) {
    if (dp[c] > dp[bestCap]) {
      bestCap = c;
    }
  }

  const usedCounts = {};
  let link = parent[bestCap];

  while (link) {
    const { previous, item } = link;
    const it = items[item];

    const key = String(it.origLen);
    usedCounts[key] = (usedCounts[key] || 0) + (it.count || 0);

    link = previous;
  }

  const pattern = {};
  Object.entries(usedCounts).forEach(([lenStr, cnt]) => {
    pattern[Number(lenStr)] = cnt;
  });

  let piecesMeters = Object.entries(pattern).reduce(
  (acc, [len, qty]) => acc + (Number(len) * qty),
  0
);

// Millimetre DP proposes candidates; exact metre arithmetic is authoritative.
// Trim any fractional overfill, then fill exact-fit residual capacity without
// exceeding the requested quantity or accepting a physically short roll.
const exactLengths = demandArray.map(d => Number(d.length)).sort((a, b) => a - b);
for (const length of exactLengths) {
  while (piecesMeters > rollLenMeters + 1e-9 && Number(pattern[length] || 0) > 0) {
    pattern[length]--; piecesMeters -= length;
  }
}
for (const demand of [...demandArray].sort((a, b) => b.length - a.length)) {
  const used = Number(pattern[demand.length] || 0);
  const extra = Math.min(Math.max(0, demand.qty - used), Math.max(0, Math.floor((rollLenMeters - piecesMeters + 1e-9) / demand.length)));
  if (extra) { pattern[demand.length] = used + extra; piecesMeters += extra * demand.length; }
}
Object.keys(pattern).forEach(key => { if (!pattern[key]) delete pattern[key]; });

const repeatAdded =
  repeatFabric && Object.keys(pattern).length > 0
    ? vrMeters
    : 0;

const finalUsed = piecesMeters + repeatAdded;

  return {
  pattern,

  piecesMeters,
  repeatAdded,

  used: finalUsed,
  waste: Math.max(0, rollLenMeters - finalUsed),
};
};


const generateCombos = (rollsArr) => {
  const combos = [];
  const n = rollsArr.length;

  const dfs = (idx, cur) => {
    if (idx === n) {
      if (cur.length > 0) combos.push(cur.slice());
      return;
    }

    // skip
    dfs(idx + 1, cur);

    // take
    cur.push(rollsArr[idx]);
    dfs(idx + 1, cur);
    cur.pop();
  };

  dfs(0, []);
  return combos;
};

  const evaluateCombo = (combo) => {

    const demandClone = demandList.map((d) => ({
      length: d.length,
      qty: d.qty,
    }));

    const rollResults = [];

    for (let ri = 0; ri < combo.length; ri++) {

      const rollLen = combo[ri];

      const patt = bestPatternForRollDP(rollLen, demandClone, fabricKey);

      Object.entries(patt.pattern || {}).forEach(
        ([lenStr, usedCount]) => {

          const lenNum = Number(lenStr);

          const target = demandClone.find(
            (x) =>
              Number(x.length) === lenNum
          );

          if (target) {
            target.qty = Math.max(
              0,
              target.qty - usedCount
            );
          }
        }
      );

     rollResults.push({
  rollLen,
  patternInfo: patt,
});
    }

let remaining = demandClone
  .filter(d => d.qty > 0)
  .map(d => ({
    length: d.length,
    qty: d.qty
  }));

let remainingMetersRaw = (
  remaining.reduce(
    (s, p) => s + p.length * p.qty,
    0
  )
);
    const isCompleteFromAvailable = remaining.length === 0;
    const maxRoll = cleanRolls.length ? Math.max(...cleanRolls) : 0;
    const remainingRolls =
      remainingMetersRaw > 0 && maxRoll > 0
        ? Math.ceil(remainingMetersRaw / maxRoll)
        : 0;

    const finalRemainingMeters = (
      remainingMetersRaw + (repeatFabric ? remainingRolls * vrMeters : 0)
    );

    const totalUsedMeters = (
      rollResults.reduce((s, r) => s + Number(r.patternInfo?.used || 0), 0) +
      finalRemainingMeters
    );

    const totalWaste = (
      rollResults.reduce(
        (s, r) =>
          s + (r.patternInfo?.waste || 0),
        0
      )
    );
    const availableRollLengthMeters = (
      combo.reduce((sum, rollLen) => sum + Number(rollLen || 0), 0)
    );
    const totalRollLengthMeters = (
      availableRollLengthMeters + finalRemainingMeters
    );
    const rollCount = combo.length + remainingRolls;

    return {
  comboRolls: combo.slice(),
  rollResults,
  remaining,
  isCompleteFromAvailable,
  requiresExtraCut: finalRemainingMeters > 0,
  finalRemainingMeters,
  totalWaste,
  totalWasteMeters: totalWaste,
  rollCount,
  totalRollLengthMeters,
  availableRollLengthMeters,
  totalRollsUsed: combo.length + remainingRolls,
  totalUsedMeters,
};
  };

  let allEvaluated = [];
  const combos = generateCombos(cleanRolls);

  combos.forEach((combo) => {
    const res = evaluateCombo(combo);
    allEvaluated.push(res);
  });

 allEvaluated.sort(compareOptimizerSolutions);

  const TOP_K = 10;

  const finalList = allEvaluated
    .slice(0, TOP_K)
    .map((s, i) => {

      s.id = `opt-${i + 1}`;

      s.rollResults = s.rollResults.map((r) => ({
        rollLen: r.rollLen,
        patternInfo:
          r.patternInfo || {
            pattern: {},
            used: 0,
            waste: round2(r.rollLen || 0),
          },
      }));

      return s;
    });

  return finalList;
};

const optimizeCutsGGMulti = (input) => optimizeWithRollAlignment({
  ...input, pack: optimizeCutsGGMultiLengthOnly,
});

 
// === Product type helpers ===
const isCurtainProductConfigOption = (config = {}) => {
  const quoteTypes = Array.isArray(config.usedInQuoteTypes)
    ? config.usedInQuoteTypes
    : [];
  if (quoteTypes.length === 0) return true;

  return quoteTypes.some((type) => {
    const normalizedType = String(type || "").trim().toLowerCase();
    return (
      normalizedType === CURTAIN_QUOTE_TYPE ||
      normalizedType === "curtain" ||
      normalizedType === "curtains"
    );
  });
};

const curtainProductOptions = useMemo(
  () =>
    mergeProductConfigsWithDefaults(productConfigs, { ...fabricCalcSettings, minSqft })
      .filter(
        (config) =>
          config.active !== false &&
          isCurtainProductConfigOption(config)
      )
      .sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
  [fabricCalcSettings, minSqft, productConfigs]
);

const productConfigById = useMemo(
  () => new Map(curtainProductOptions.map((config) => [config.id, config])),
  [curtainProductOptions]
);

const getCurtainRowActiveProductConfig = useCallback(
  (row = {}) => {
    const candidateIds = [
      row.productId,
      row.productConfigSnapshot?.id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    for (const candidateId of candidateIds) {
      const match = productConfigById.get(candidateId);
      if (match) return match;
    }

    const candidateNames = [
      row.productName,
      row.productType,
      row.product,
      row.productConfigSnapshot?.name,
      row.productConfigSnapshot?.normalizedName,
    ]
      .map((value) => normalizeProductName(value || ""))
      .filter(Boolean);

    return (
      curtainProductOptions.find((config) =>
        candidateNames.includes(normalizeProductName(config.normalizedName || config.name))
      ) || null
    );
  },
  [curtainProductOptions, productConfigById]
);

const getCurtainRowSavedProductLabel = useCallback((row = {}) => {
  const label =
    row.productName ||
    row.productType ||
    row.product ||
    row.productConfigSnapshot?.name ||
    row.productConfigSnapshot?.normalizedName ||
    "";
  return String(label || "").trim();
}, []);

const getCurtainRowProductSelectValue = useCallback(
  (row = {}) => {
    const activeConfig = getCurtainRowActiveProductConfig(row);
    if (activeConfig?.id) return activeConfig.id;

    const savedLabel = getCurtainRowSavedProductLabel(row);
    return savedLabel ? `saved:${normalizeProductName(savedLabel) || savedLabel}` : "";
  },
  [getCurtainRowActiveProductConfig, getCurtainRowSavedProductLabel]
);

const getCurtainRowSavedProductOption = useCallback(
  (row = {}) => {
    if (getCurtainRowActiveProductConfig(row)) return null;
    const savedLabel = getCurtainRowSavedProductLabel(row);
    if (!savedLabel) return null;
    return {
      value: getCurtainRowProductSelectValue(row),
      label: `${savedLabel} (saved)`,
    };
  },
  [
    getCurtainRowActiveProductConfig,
    getCurtainRowProductSelectValue,
    getCurtainRowSavedProductLabel,
  ]
);

const getRowProductConfig = useCallback(
  (row = {}) => resolveProductConfig(row, curtainProductOptions, fabricCalcSettings),
  [curtainProductOptions, fabricCalcSettings]
);

const getRowProductRules = useCallback(
  (row = {}) => {
    const config = getRowProductConfig(row);
    return {
      config,
      isLegacy: shouldUseLegacyProductCalculation(row, config),
      requiredFields: config?.requiredFields || {},
      calculationType: config?.calculationType || "legacy",
      workshopRules: config?.workshopRules || {},
      pdfRules: config?.pdfRules || {},
    };
  },
  [getRowProductConfig]
);

const isCurtainAccessoryRow = useCallback(
  (row = {}) => {
    const config = getRowProductConfig(row);
    return (
      row?.lineType === "accessory" ||
      isCurtainAccessoryRowStatic(row) ||
      (config ? isAccessoryProductConfig(config) : false)
    );
  },
  [getRowProductConfig]
);

const curtainMeasurementEntries = useMemo(
  () =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !isCurtainAccessoryRow(row)),
  [isCurtainAccessoryRow, rows]
);

const curtainAccessoryEntries = useMemo(
  () =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => isCurtainAccessoryRow(row)),
  [isCurtainAccessoryRow, rows]
);

const quoteFullnessOverrideRows = useMemo(() => {
  const usedStitches = new Map();

  curtainMeasurementEntries.forEach(({ row }) => {
    const productConfig = getRowProductConfig(row);
    const productName = normalizeProductName(
      row?.productName || row?.productType || row?.product || productConfig?.name || ""
    );
    const fabricRules = resolveFabricRulesForRow(
      row,
      productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
        ? productConfig
        : null,
      fabricCalcSettings
    );
    const usesStitchFullness =
      productName === "curtains" ||
      (productConfig?.calculationType === "fabric_meter" &&
        fabricRules?.fullnessSource === "stitching");

    if (!usesStitchFullness) return;

    const stitchMatch = resolveQuoteStitchingConfig(row, findCurtainRowStitchConfig(row, stitchingRates), quoteConstructionSettings);
    const stitchingLabel =
      getCurtainRowWorkshopStitchLabel(row, stitchingRates, productConfig) ||
      stitchMatch?.key ||
      "";
    const stableStitchingId = sanitizeEditableText(
      row?.stitchingStyleId ||
        row?.stitchingConfigId ||
        row?.stitchingId ||
        row?.stitchId ||
        row?.selectedStitch?.id ||
        row?.selectedStitching?.id ||
        stitchMatch?.config?.id ||
        stitchMatch?.candidate ||
        ""
    );
    const stitchingKey = normalizeStitchingKey(
      stableStitchingId ||
        stitchMatch?.key ||
        row?.stitchingStyle ||
        row?.stitch ||
        stitchingLabel
    );

    if (!stitchingKey) return;

    const defaultFullnessResult = resolveCurtainFullness(
      row,
      productConfig,
      stitchingRates,
      {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides: {},
      }
    );
    const defaultFullness = Number(
      stitchMatch?.config?.fullness ||
        defaultFullnessResult?.stitchingFullness ||
        defaultFullnessResult?.fullness ||
        0
    );
    const storedOverrideKeys = [
      stitchingKey,
      stableStitchingId,
      stitchMatch?.candidate,
      stitchMatch?.key,
      stitchMatch?.config?.id,
      stitchingLabel,
    ]
      .map((value) => normalizeStitchingKey(value))
      .filter(Boolean);
    const storedOverride =
      storedOverrideKeys
        .map((key) => quoteFullnessOverrides?.[key])
        .find((override) => override && typeof override === "object") || {};
    const existing = usedStitches.get(stitchingKey);

    usedStitches.set(stitchingKey, {
      stitchingKey,
      stitchingStyleId: stableStitchingId || stitchingKey,
      stitchingLabel:
        stitchingLabel && stitchingLabel !== WORKSHOP_EMPTY_VALUE
          ? stitchingLabel
          : stitchMatch?.key || "Selected Stitch",
      defaultFullness:
        existing?.defaultFullness ||
        (Number.isFinite(defaultFullness) && defaultFullness > 0
          ? Number(defaultFullness.toFixed(2))
          : null),
      quoteFullness:
        Number.isFinite(Number(storedOverride?.quoteFullness)) &&
        Number(storedOverride.quoteFullness) > 0
          ? Number(storedOverride.quoteFullness)
          : null,
      rowCount: Number(existing?.rowCount || 0) + 1,
    });
  });

  return Array.from(usedStitches.values()).sort((a, b) =>
    String(a.stitchingLabel || "").localeCompare(String(b.stitchingLabel || ""))
  );
}, [
  curtainMeasurementEntries,
  fabricCalcSettings,
  getRowProductConfig,
  quoteFullnessOverrides,
  stitchingRates,
  quoteConstructionSettings,
]);

const updateQuoteFullnessOverride = useCallback(
  (stitchRow = {}, rawValue = "") => {
    const inputValue = String(rawValue ?? "").trim();
    if (!isPermittedDecimalInput(inputValue)) return;

    const stitchingKey = normalizeStitchingKey(
      stitchRow.stitchingKey || stitchRow.stitchingLabel || ""
    );
    if (!stitchingKey) return;
    if (!prepareQuoteConstructionEdit(row => {
      const match = findCurtainRowStitchConfig(row, stitchingRates);
      return normalizeStitchingKey(row.stitchingStyleId || row.stitchingConfigId || match?.config?.id || match?.key || row.stitchingStyle || "") === stitchingKey;
    })) return;

    setQuoteFullnessOverrideInputs((prev) => ({
      ...prev,
      [stitchingKey]: inputValue,
    }));

    const numericValue = Number(inputValue);
    const validQuoteFullness =
      inputValue !== "" &&
      Number.isFinite(numericValue) &&
      numericValue > 0 &&
      numericValue <= 10
        ? numericValue
        : null;
    const nextEntry = buildQuoteFullnessOverrideEntry({
      stitchingKey,
      stitchingStyleId: stitchRow.stitchingStyleId || stitchingKey,
      stitchingLabel: stitchRow.stitchingLabel,
      defaultFullness: stitchRow.defaultFullness,
      quoteFullness: validQuoteFullness,
    });
    if (!nextEntry) return;

    setQuoteFullnessOverrides((prev) => {
      const normalizedPrev = normalizeQuoteFullnessOverrides(prev);
      const next = {
        ...normalizedPrev,
        [stitchingKey]: nextEntry,
      };
      quoteFullnessOverridesRef.current = next;
      return next;
    });
    markCurtainTotalsSettingsEdited(
      "quoteFullnessOverrides",
      `${stitchingKey}:${inputValue}`
    );
  },
  [markCurtainTotalsSettingsEdited, stitchingRates, selectedQuote]
);

function prepareQuoteConstructionEdit(matches) {
  const affected = rowsRef.current.filter(matches);
  if (affected.some(row => row.calculationSnapshot && !row.__forceCurrentCalculation)) {
    if (!window.confirm("This changes the saved calculation. Review the original and proposed totals below before explicitly saving a revision. Continue?")) return false;
    setConstructionRevisionReview({ originalTotal: Number(selectedQuote?.grandTotal ?? selectedQuote?.totalAmount ?? 0) });
  }
  setRows(previous => previous.map(row => matches(row) ? { ...row, __forceCurrentCalculation: true, __recalculated: true } : row));
  return true;
}

function buildQuoteConstructionFields(sourceRows = rowsRef.current) {
  const knownRows = selectedQuote?.id
    ? sourceRows.filter(row => row.__forceCurrentCalculation || row.__recalculated || !row.calculationSnapshot || quoteConstructionSettingsRef.current.quoteCalculationSettingsSnapshot?.fabricRules?.[getQuoteConstructionKey(row)])
    : sourceRows;
  if (!knownRows.length) return normalizeQuoteCalculationSettings(quoteConstructionSettingsRef.current);
  return captureQuoteCalculationSettings({ rows: knownRows, quote: quoteConstructionSettingsRef.current,
    getProductConfig: row => getRowProductConfig(row),
    getFabricRules: (row, config) => resolveFabricRulesForRow(row, config, fabricCalcSettings),
    getStitchingStyle: row => resolveSelectedStitchingStyle(row, stitchingRates),
  });
}

function operationalQuoteSettingsReady() {
  if (mainSettingsLoadedWorkspaceRef.current === workspaceUid
    && quotationSettingsLoadedWorkspaceRef.current === workspaceUid
    && productConfigsLoadState.loadedWorkspace === workspaceUid
    && !productConfigsLoadState.error && !productConfigsLoadState.loading) return true;
  notify('Quotation settings could not be loaded. Please contact your account administrator.', 'error');
  return false;
}

const quoteTrackControlRows = (() => {
  const used = new Map();
  for (const row of rows) {
    const selected = resolveQuoteStitchingConfig(row, resolveSelectedStitchingStyle(row, stitchingRates), quoteConstructionSettings);
    const config = selected.config;
    if (config.includesTrack === false && !config.supportsBendedTrack) continue;
    const key = selected.trackKey;
    if (!Object.prototype.hasOwnProperty.call(config, "trackRateFt")) continue;
    used.set(key, { key, label: config.trackType || config.label || config.name || selected.key, defaultRate: Number(config.quoteTrackDefaultRateFt ?? config.trackRateFt ?? 0),
      unit: config.trackRateUnit || "rft", rate: quoteConstructionSettings.quoteTrackRateOverrides?.[key]?.rate });
  }
  return [...used.values()];
})();

function getQuoteHemControlRows(sourceRows = rows) {
  const used = new Map();
  for (const row of sourceRows) {
    const key = getQuoteFabricIdentity(row);
    const rules = resolveFabricRulesForRow(row, getRowProductConfig(row), { ...fabricCalcSettings, quoteConstructionSettings });
    if (!rules.enabled || !["curtain_panels", "roman_panels"].includes(rules.meterFormulaType)) continue;
    if (!used.has(key)) used.set(key, { key, label: getCurtainRowFabricLabel(row), effective: rules,
      source: rules.hemSource, override: quoteConstructionSettings.fabricHemOverrides?.[key] || {} });
  }
  return [...used.values()];
}

function updateQuoteTrackOverride(track, rate) {
  if (!prepareQuoteConstructionEdit(row => resolveQuoteStitchingConfig(row, resolveSelectedStitchingStyle(row, stitchingRates), quoteConstructionSettings).trackKey === track.key)) return;
  setQuoteConstructionSettings(previous => {
    const next = { ...previous.quoteTrackRateOverrides };
    if (rate == null) delete next[track.key]; else next[track.key] = { rate, unit: track.unit };
    return { ...previous, quoteTrackRateOverrides: next };
  });
  markCurtainTotalsSettingsEdited("quoteTrackRateOverrides", track.key);
}

function updateQuoteHemOverride(fabric, field, value) {
  if (!prepareQuoteConstructionEdit(row => getQuoteFabricIdentity(row) === fabric.key)) return;
  setQuoteConstructionSettings(previous => {
    const next = { ...previous.fabricHemOverrides };
    if (!field) delete next[fabric.key];
    else { const entry = { ...next[fabric.key] }; if (value == null) delete entry[field]; else entry[field] = value; next[fabric.key] = entry; }
    return { ...previous, fabricHemOverrides: next };
  });
  markCurtainTotalsSettingsEdited("fabricHemOverrides", fabric.key);
}

const curtainLineFamilyTotals = useMemo(() => {
  const measurementSubtotal = curtainMeasurementEntries.reduce(
    (sum, { row, index }) =>
      sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
    0
  );
  const accessorySubtotal = curtainAccessoryEntries.reduce(
    (sum, { row, index }) =>
      sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
    0
  );
  return {
    measurementSubtotal: round2(measurementSubtotal),
    accessorySubtotal: round2(accessorySubtotal),
  };
}, [
  curtainAccessoryEntries,
  curtainMeasurementEntries,
  getCurtainProductionAdjustedRowCalc,
]);

const getCurtainOrderedFabricDisplayTrace = useCallback(
  (row = {}, rowIndex = -1, providedCalc = null) => {
    const rowId = getWorkshopRowStableId(row) || `row-${rowIndex}`;
    const productConfig = getRowProductConfig(row);
    const baseCalc = calcRow(row, repeatSettings);
    const adjustedCalc =
      providedCalc || getCurtainProductionAdjustedRowCalc(row, rowIndex);
    const rowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? curtainFabricSummaryResult?.plan?.rowPlans?.[rowIndex] || null
        : null;
    const noReuseRowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? curtainNoReuseBillingResult?.plan?.rowPlans?.[rowIndex] || null
        : null;
    const fabricRules =
      adjustedCalc?.calculationBreakdown?.fabricRules ||
      baseCalc?.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const stitchConfigMatch = findCurtainRowStitchConfig(row, stitchingRates);
    const stitchConfig = stitchConfigMatch?.config || {};
    const fullnessResult =
      adjustedCalc?.calculationBreakdown?.fullness ||
      baseCalc?.calculationBreakdown?.fullness ||
      resolveCurtainFullness(row, productConfig, stitchingRates, {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides,
      });
    const panelMath = getHalfPieceReusePiecePlan(row, baseCalc, {
      productConfig,
    });
    const fullnessSource = panelMath.fullnessSource || fullnessResult?.source || "";
    const finalFullnessUsed = Number(panelMath.fullness || fullnessResult?.fullness || 0);
    const fullnessApplicable =
      fullnessResult?.applicable !== false &&
      !["none", "not_applicable"].includes(String(fullnessSource || "").trim());
    const fullnessRowOverride =
      fullnessApplicable && Number(fullnessResult?.rowOverride || 0) > 0
        ? Number(fullnessResult.rowOverride)
        : null;
    const fullnessStitchingStyle =
      fullnessApplicable && Number(stitchConfig?.fullness || fullnessResult?.stitchingFullness || 0) > 0
        ? Number(stitchConfig?.fullness || fullnessResult?.stitchingFullness || 0)
        : null;
    const fullnessQuoteOverride =
      fullnessApplicable && Number(fullnessResult?.quoteFullness || 0) > 0
        ? Number(fullnessResult.quoteFullness)
        : null;
    const fullnessProductDefault =
      fullnessApplicable &&
      Number(fullnessResult?.productDefaultFullness ?? fullnessResult?.productFullness ?? 0) > 0
        ? Number(fullnessResult?.productDefaultFullness ?? fullnessResult?.productFullness)
        : null;
    const fullnessLegacyFallback =
      fullnessApplicable &&
      fullnessSource === "legacy_global_fallback" &&
      Number(fullnessResult?.legacyGlobalFallback || 0) > 0
        ? Number(fullnessResult.legacyGlobalFallback)
        : null;
    const fullnessLegacyFallbackIgnored =
      fullnessApplicable &&
      fullnessSource !== "legacy_global_fallback" &&
      Number(fullnessResult?.legacyGlobalFallbackIgnored || 0) > 0
        ? Number(fullnessResult.legacyGlobalFallbackIgnored)
        : null;
    const fullnessAudit = {
      rowOverride: fullnessRowOverride,
      quoteOverride: fullnessQuoteOverride,
      stitchingStyle: fullnessStitchingStyle,
      productDefault: fullnessProductDefault,
      legacyGlobalFallback: fullnessLegacyFallback,
      legacyGlobalFallbackIgnored: Boolean(fullnessLegacyFallbackIgnored),
      finalUsed: fullnessApplicable ? finalFullnessUsed : 1,
      source: fullnessApplicable ? fullnessSource || "unknown" : "not_applicable",
      applicable: Boolean(fullnessApplicable),
    };
    const snapshot = row?.calculationSnapshot || adjustedCalc?.calculationSnapshot || {};
    const savedSnapshotMode = adjustedCalc?.usesCalculationSnapshot === true;
    const legacyMode =
      savedSnapshotMode ||
      row?.calculationSnapshotStatus === "legacy" ||
      snapshot?.legacyFormula === true ||
      snapshot?.requiresRecalculation === true;
    const finalCutHeightIn = round2(
      Number(
        adjustedCalc?.cutCurtain ||
          adjustedCalc?.cutRoman ||
          baseCalc?.cutCurtain ||
          baseCalc?.cutRoman ||
          rowPlan?.cutHeightIn ||
          snapshot?.cutHeightIn ||
          0
      )
    );
    const rawOrderedMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.rawPanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const wholePanelMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.wholePanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const halfStepMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.halfStepPanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const savedOrderedFabricMeters = round2(
      Number(
        snapshot?.orderedFabricMeters ??
          snapshot?.customerBillableFabricMeters ??
          row?.orderedFabricMeters ??
          row?.customerBillableFabricMeters ??
          row?.fabricMeters ??
          0
      )
    );
    const liveCalculatedOrderedFabricMeters = round2(
      Number(
        noReuseRowPlan?.newFabricNeededMeters ??
          noReuseRowPlan?.newMeters ??
          baseCalc?.fabricMetersFull ??
          baseCalc?.fabricMeters ??
          wholePanelMeters ??
          0
      )
    );
    const displayedOrderedFabricMeters = round2(
      Number(
        adjustedCalc?.customerBillableFabricMeters ??
          adjustedCalc?.orderedFabricMeters ??
          adjustedCalc?.fabricMeters ??
          0
      )
    );
    const customerBillableFabricMeters = round2(
      Number(
        adjustedCalc?.customerBillableFabricMeters ??
          displayedOrderedFabricMeters
      )
    );
    const workshopFabricRequiredMeters = round2(
      Number(
        adjustedCalc?.workshopFabricRequiredMeters ??
          adjustedCalc?.workshopNewFabricMeters ??
          rowPlan?.newFabricNeededMeters ??
          rowPlan?.newMeters ??
          0
      )
    );
    const actualReuseOnThisRow =
      Number(rowPlan?.reusedHalfCount || 0) > 0 ||
      Number(rowPlan?.halfReusedMeters || 0) > 0;
    const wholeBillingPanels = Number(
      adjustedCalc?.wholeBillingPanels ??
        noReuseRowPlan?.newFullCuts ??
        noReuseRowPlan?.wholePanels ??
        panelMath.wholePanels ??
        0
    );
    const halfStepProductionPanels = Number(
      adjustedCalc?.halfStepProductionPanels ??
        rowPlan?.totalPieces ??
        rowPlan?.halfStepPanels ??
        panelMath.halfStepPanels ??
        0
    );
    const panelsUsedForWorkshop = Number(
      adjustedCalc?.workshopPanels ??
        (rowPlan
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : wholeBillingPanels)
    );
    const panelsUsedForCustomerBilling = Number(
      adjustedCalc?.customerBillingPanels ??
        (passHalfPieceSavingsToCustomer && actualReuseOnThisRow
          ? panelsUsedForWorkshop
          : wholeBillingPanels)
    );
    const panelsSource = passHalfPieceSavingsToCustomer && actualReuseOnThisRow
      ? "halfPieceReuse.rowPlan"
      : "noReuseWholePanelBilling";
    const displayedMetersSource = passHalfPieceSavingsToCustomer && actualReuseOnThisRow
      ? "customerBillableFromHalfPieceReuse"
      : "customerBillableFromNoReuseFullOrdered";
    const topAllowanceIn = round2(
      Number(fabricRules?.topHemIn ?? fabricRules?.topHem ?? fabricCalcSettings?.curtain_top_hem ?? 0)
    );
    const bottomAllowanceIn = round2(
      Number(fabricRules?.bottomHemIn ?? fabricRules?.bottomHem ?? fabricCalcSettings?.curtain_bottom_hem ?? 0)
    );
    const totalCutAllowanceIn = round2(
      Number(fabricRules?.dropAllowanceIn ?? topAllowanceIn + bottomAllowanceIn)
    );

    return {
      quoteNo,
      rowId,
      rowIndex,
      itemName: row?.item || row?.name || "",
      product: row?.productType || row?.productName || row?.product || productConfig?.name || "",
      fabricCode: getCurtainRowFabricLabel(row),
      fabricKey: getCurtainRowFabricKey(row),
      selectedStitchLabel: getCurtainRowWorkshopStitchLabel(row, stitchingRates, productConfig),
      selectedStitchId:
        stitchConfigMatch?.key ||
        row?.stitchingStyleId ||
        row?.stitchingStyle ||
        row?.stitch ||
        "",
      selectedStitchRawValue: row?.stitchingStyle || row?.stitchingStyleId || row?.stitch || "",
      resolvedStitchConfig: {
        source: stitchConfigMatch?.source || "",
        key: stitchConfigMatch?.key || "",
        rate: Number(stitchConfig?.rate || 0),
        fullness: Number(stitchConfig?.fullness || 0),
        trackLengthFt: Number(stitchConfig?.trackLengthFt || 0),
        trackRateFt: Number(stitchConfig?.trackRateFt || 0),
        rawConfig: stitchConfig,
      },
      widthInput: row?.width ?? "",
      heightInput: row?.height ?? "",
      measurementUnit: getRowMeasurementUnit(row, unit),
      widthIn: Number(row?.width || 0),
      heightIn: Number(row?.height || 0),
      fabricWidthIn: Number(panelMath.fabricWidthIn || fabricRules?.fabricWidthIn || 0),
      usableWidthDeductionIn: Number(
        panelMath.usableWidthDeductionIn || fabricRules?.usableWidthDeductionIn || 0
      ),
      usableFabricWidthIn: Number(
        panelMath.usableFabricWidthIn || fabricRules?.usableFabricWidthIn || 0
      ),
      fullnessFromRow: fullnessRowOverride,
      fullnessFromQuote: fullnessQuoteOverride,
      fullnessFromStitch: fullnessStitchingStyle,
      fullnessFromProduct: fullnessProductDefault,
      fullnessFromSettings: fullnessLegacyFallback,
      legacyGlobalFullnessFallbackIgnored: Boolean(fullnessLegacyFallbackIgnored),
      finalFullnessUsed: fullnessAudit.finalUsed,
      fullnessSource: fullnessAudit.source,
      fullness: fullnessAudit,
      topAllowanceIn,
      bottomAllowanceIn,
      hemAllowanceIn: totalCutAllowanceIn,
      stitchAllowanceIn: Number(stitchConfig?.cutAllowanceIn || stitchConfig?.stitchAllowanceIn || 0),
      trackAllowanceIn: Number(stitchConfig?.trackAllowanceIn || 0),
      totalCutAllowanceIn,
      finalCutHeightIn,
      cutHeightSource: savedSnapshotMode
        ? "savedSnapshot.cutHeightIn"
        : "liveHeightPlusDropAllowance",
      rawPanels: Number(panelMath.rawPanels || 0),
      wholePanels: Number(panelMath.wholePanels || 0),
      halfStepPanels: Number(panelMath.halfStepPanels || 0),
      halfStepProductionPanels,
      wholeBillingPanels,
      panelsUsedForCustomerBilling,
      panelsUsedForWorkshop,
      workshopPanels: panelsUsedForWorkshop,
      customerBillingPanels: panelsUsedForCustomerBilling,
      panelsSource,
      rawOrderedMeters,
      orderedFabricMeters: liveCalculatedOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopFabricMeters: workshopFabricRequiredMeters,
      workshopFabricRequiredMeters,
      fullOrderedFabricMeters: round2(
        Number(adjustedCalc?.fullOrderedFabricMeters ?? liveCalculatedOrderedFabricMeters)
      ),
      cutFabricMeters: round2(Number(adjustedCalc?.cutFabricMeters || halfStepMeters || 0)),
      wholePanelOrderedMeters: wholePanelMeters,
      halfStepOrderedMeters: halfStepMeters,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      halfPieceImpactOnThisRow: {
        createsHalfPiece: Boolean(rowPlan?.createdLeftoverId),
        reusedHalfCount: Number(rowPlan?.reusedHalfCount || 0),
        halfReusedMeters: Number(rowPlan?.halfReusedMeters || 0),
        reusedLeftoverId: rowPlan?.reusedLeftoverId || "",
        createdLeftoverId: rowPlan?.createdLeftoverId || "",
        affectsDisplayedMeters: Boolean(passHalfPieceSavingsToCustomer && actualReuseOnThisRow),
        reasonIfNoReuse: rowPlan?.reasonIfNoReuse || "",
      },
      legacyMode,
      savedSnapshotMode,
      savedOrderedFabricMeters,
      liveCalculatedOrderedFabricMeters,
      displayedOrderedFabricMeters,
      orderedFabricDisplayed: displayedOrderedFabricMeters,
      rowTotalFabricMetersUsed: customerBillableFabricMeters,
      displayedMetersSource,
      displaySource: displayedMetersSource,
      billingSource: displayedMetersSource,
      workshopSource: rowPlan ? "halfPiecePlanner.rowPlan" : "fullOrderedFabric",
      quoteDisplayMode: curtainQuoteTotalMode,
      calculationVersion:
        snapshot?.calculationVersion ||
        adjustedCalc?.calculationVersion ||
        CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
    };
  },
  // getHalfPieceReusePiecePlan is a function declaration in this component; it
  // reads the current quote settings through closure and is intentionally kept
  // out of this dependency list to avoid recreating the audit helper every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    calcRow,
    curtainFabricSummaryResult,
    curtainNoReuseBillingResult,
    curtainQuoteTotalMode,
    fabricCalcSettings,
    getCurtainProductionAdjustedRowCalc,
    getRowProductConfig,
    passHalfPieceSavingsToCustomer,
    quoteNo,
    quoteFullnessOverrides,
    repeatSettings,
    stitchingRates,
    unit,
    useHalfPieceReuse,
  ]
);

const compareCurtainRowsForFabricCalculation = useCallback((rowA = {}, rowB = {}) => {
  const trackedFields = [
    ["product", true],
    ["fabricCode", true],
    ["selectedStitchId", false],
    ["selectedStitchLabel", false],
    ["widthIn", true],
    ["heightIn", true],
    ["fabricWidthIn", true],
    ["usableWidthDeductionIn", true],
    ["usableFabricWidthIn", true],
    ["finalFullnessUsed", true],
    ["fullnessSource", true],
    ["totalCutAllowanceIn", true],
    ["finalCutHeightIn", true],
    ["rawPanels", true],
    ["wholePanels", true],
    ["halfStepPanels", true],
    ["panelsUsedForCustomerBilling", true],
    ["panelsUsedForWorkshop", true],
    ["panelsSource", true],
    ["savedSnapshotMode", true],
    ["displayedMetersSource", true],
    ["passHalfPieceSavingsToCustomer", true],
    ["displayedOrderedFabricMeters", false],
  ];
  const valueChanged = (a, b) => {
    const numberA = Number(a);
    const numberB = Number(b);
    if (Number.isFinite(numberA) && Number.isFinite(numberB)) {
      return Math.abs(numberA - numberB) > 0.01;
    }
    return String(a ?? "") !== String(b ?? "");
  };
  const differences = trackedFields
    .filter(([field]) => valueChanged(rowA[field], rowB[field]))
    .map(([field, affectsOrderedFabric]) => ({
      field,
      rowAValue: rowA[field],
      rowBValue: rowB[field],
      affectsOrderedFabric,
    }));
  const outputDiff = Math.abs(
    Number(rowA.displayedOrderedFabricMeters || 0) -
      Number(rowB.displayedOrderedFabricMeters || 0)
  );
  const inputDiffs = differences.filter((diff) => diff.affectsOrderedFabric);
  let conclusion = "Rows use the same ordered-fabric inputs and output.";

  if (inputDiffs.some((diff) => diff.field === "finalFullnessUsed" || diff.field === "fullnessSource")) {
    conclusion = "Different fullness used";
  } else if (
    inputDiffs.some((diff) =>
      ["finalCutHeightIn", "totalCutAllowanceIn"].includes(diff.field)
    )
  ) {
    conclusion = "Different cut height / allowance used";
  } else if (
    rowA.halfPieceImpactOnThisRow?.affectsDisplayedMeters ||
    rowB.halfPieceImpactOnThisRow?.affectsDisplayedMeters
  ) {
    conclusion = "One row affected by half-piece/customer billing setting";
  } else if (
    inputDiffs.some((diff) =>
      ["panelsUsedForCustomerBilling", "panelsUsedForWorkshop", "panelsSource"].includes(diff.field)
    )
  ) {
    conclusion = "Different panel rounding/source used";
  } else if (
    inputDiffs.some((diff) => diff.field === "savedSnapshotMode" || diff.field === "displayedMetersSource")
  ) {
    conclusion = "One row uses saved/stale ordered fabric";
  } else if (outputDiff > 0.01) {
    conclusion = "Bug: same inputs but different output";
  }

  return {
    rowA: {
      rowId: rowA.rowId,
      rowIndex: rowA.rowIndex,
      itemName: rowA.itemName,
      displayedOrderedFabricMeters: rowA.displayedOrderedFabricMeters,
    },
    rowB: {
      rowId: rowB.rowId,
      rowIndex: rowB.rowIndex,
      itemName: rowB.itemName,
      displayedOrderedFabricMeters: rowB.displayedOrderedFabricMeters,
    },
    sameVisibleInputs: inputDiffs.length === 0,
    outputDifferenceMeters: round2(outputDiff),
    differences,
    conclusion,
  };
}, []);

const getFullnessSourceLabel = (source = "") =>
  ({
    row_override: "Row Override",
    quote_stitch_override: "Quote Fullness",
    stitching_style: "Stitching Style",
    row_selected_stitch: "Selected Stitching",
    product_default: "Product Default",
    legacy_global_fallback: "Legacy Global Fallback",
    default_one: "Default 1x",
    not_applicable: "Not Applicable",
    none: "Not Applicable",
  }[String(source || "").trim()] || source || "Unknown");

const getOrderedFabricTooltipText = useCallback((audit = {}) => {
  const fullnessInfo = audit.fullness || {};
  const lines = fullnessInfo.applicable === false
    ? ["Fullness: Not applicable"]
    : [
        `Final fullness used: ${Number(audit.finalFullnessUsed || fullnessInfo.finalUsed || 0).toFixed(2)}`,
        `Source: ${getFullnessSourceLabel(audit.fullnessSource || fullnessInfo.source)}`,
      ];
  if (fullnessInfo.stitchingStyle) {
    lines.push(`Stitching style fullness: ${Number(fullnessInfo.stitchingStyle).toFixed(2)}`);
  }
  if (fullnessInfo.quoteOverride) {
    lines.push(`Quote fullness: ${Number(fullnessInfo.quoteOverride).toFixed(2)}`);
  }
  if (fullnessInfo.rowOverride) {
    lines.push(`Row override fullness: ${Number(fullnessInfo.rowOverride).toFixed(2)}`);
  }
  if (fullnessInfo.productDefault && !fullnessInfo.stitchingStyle) {
    lines.push(`Product default fullness: ${Number(fullnessInfo.productDefault).toFixed(2)}`);
  }
  if (fullnessInfo.legacyGlobalFallback) {
    lines.push(`Legacy global fallback used: ${Number(fullnessInfo.legacyGlobalFallback).toFixed(2)}`);
  }
  if (fullnessInfo.legacyGlobalFallbackIgnored) {
    lines.push("Legacy global fallback: ignored");
  }
  lines.push(
    `Usable fabric width: ${Number(audit.usableFabricWidthIn || 0).toFixed(2)} in`,
    `Raw panels: ${Number(audit.rawPanels || 0).toFixed(2)}`,
    `Whole panels: ${Number(audit.wholePanels || 0).toFixed(2)}`,
    `Half-step panels: ${Number(audit.halfStepPanels || 0).toFixed(2)}`,
    `Customer panels: ${Number(audit.panelsUsedForCustomerBilling || 0).toFixed(2)}`,
    `Workshop panels: ${Number(audit.panelsUsedForWorkshop || 0).toFixed(2)}`,
    `Cut height: ${Number(audit.finalCutHeightIn || 0).toFixed(2)} in`,
    `Displayed ordered fabric: ${Number(audit.displayedOrderedFabricMeters || 0).toFixed(2)} m`,
    `Source: ${audit.displayedMetersSource || "unknown"}`,
  );
  if (audit.savedSnapshotMode) {
    lines.push(`Saved value: ${Number(audit.savedOrderedFabricMeters || 0).toFixed(2)} m`);
  }
  if (audit.halfPieceImpactOnThisRow?.affectsDisplayedMeters) {
    lines.push("Half-piece reuse is affecting this displayed meter value.");
  }
  return lines.join("\n");
}, []);

const curtainOrderedFabricRowAudits = useMemo(
  () =>
    curtainMeasurementEntries.map(({ row, index }) =>
      getCurtainOrderedFabricDisplayTrace(row, index)
    ),
  [curtainMeasurementEntries, getCurtainOrderedFabricDisplayTrace]
);

const curtainOrderedFabricComparisons = useMemo(() => {
  const comparisons = [];
  for (let i = 0; i < curtainOrderedFabricRowAudits.length; i += 1) {
    for (let j = i + 1; j < curtainOrderedFabricRowAudits.length; j += 1) {
      const left = curtainOrderedFabricRowAudits[i];
      const right = curtainOrderedFabricRowAudits[j];
      const sameVisibleMeasurements =
        normalizeProductName(left.product) === normalizeProductName(right.product) &&
        normalizeFabricKey(left.fabricCode) === normalizeFabricKey(right.fabricCode) &&
        Math.abs(Number(left.widthIn || 0) - Number(right.widthIn || 0)) <= 0.01 &&
        Math.abs(Number(left.heightIn || 0) - Number(right.heightIn || 0)) <= 0.01 &&
        Math.abs(Number(left.fabricWidthIn || 0) - Number(right.fabricWidthIn || 0)) <= 0.01;
      const displayedDiff =
        Math.abs(
          Number(left.displayedOrderedFabricMeters || 0) -
            Number(right.displayedOrderedFabricMeters || 0)
        ) > 0.01;

      if (sameVisibleMeasurements || displayedDiff) {
        comparisons.push(compareCurtainRowsForFabricCalculation(left, right));
      }
    }
  }
  return comparisons;
}, [compareCurtainRowsForFabricCalculation, curtainOrderedFabricRowAudits]);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return;
  if (!curtainOrderedFabricRowAudits.length) return;
  const signature = JSON.stringify(
    curtainOrderedFabricRowAudits.map((audit) => ({
      rowId: audit.rowId,
      rowIndex: audit.rowIndex,
      itemName: audit.itemName,
      displayedOrderedFabricMeters: audit.displayedOrderedFabricMeters,
      displayedMetersSource: audit.displayedMetersSource,
      finalFullnessUsed: audit.finalFullnessUsed,
      finalCutHeightIn: audit.finalCutHeightIn,
      wholePanels: audit.wholePanels,
      halfStepPanels: audit.halfStepPanels,
    }))
  );
  if (orderedFabricAuditSignatureRef.current === signature) return;
  orderedFabricAuditSignatureRef.current = signature;

  curtainOrderedFabricRowAudits.forEach((audit) => {
    devLog("CUSTOMER_VS_WORKSHOP_FABRIC_TRACE", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      rawPanels: audit.rawPanels,
      halfStepProductionPanels: audit.halfStepProductionPanels,
      wholeBillingPanels: audit.wholeBillingPanels,
      fullOrderedFabricMeters: audit.fullOrderedFabricMeters,
      workshopFabricMeters: audit.workshopFabricMeters,
      customerBillableFabricMeters: audit.customerBillableFabricMeters,
      useHalfPieceReuse: audit.useHalfPieceReuse,
      passHalfPieceSavingsToCustomer: audit.passHalfPieceSavingsToCustomer,
      orderedFabricDisplayed: audit.orderedFabricDisplayed,
      rowTotalFabricMetersUsed: audit.rowTotalFabricMetersUsed,
      displaySource: audit.displaySource,
      billingSource: audit.billingSource,
      workshopSource: audit.workshopSource,
    });
    devLog("ORDERED_FABRIC_ROW_AUDIT", audit);
    const fullnessInfo = audit.fullness || {};
    const stitchingStyleFullness = Number(fullnessInfo.stitchingStyle || 0);
    if (
      fullnessInfo.applicable !== false &&
      stitchingStyleFullness > 0 &&
      Math.abs(Number(fullnessInfo.finalUsed || audit.finalFullnessUsed || 0) - stitchingStyleFullness) > 0.01
    ) {
      devWarn("FULLNESS_SOURCE_MISMATCH", {
        rowId: audit.rowId,
        itemName: audit.itemName,
        product: audit.product,
        selectedStitchLabel: audit.selectedStitchLabel,
        rowOverride: fullnessInfo.rowOverride,
        stitchingStyleFullness,
        productDefaultFullness: fullnessInfo.productDefault,
        legacyGlobalFallback: fullnessInfo.legacyGlobalFallback || null,
        legacyGlobalFallbackIgnored: Boolean(fullnessInfo.legacyGlobalFallbackIgnored),
        finalFullnessUsed: fullnessInfo.finalUsed,
        fullnessSource: fullnessInfo.source,
      });
    }
    if (
      fullnessInfo.applicable === false &&
      (audit.fullnessFromProduct !== null || audit.fullnessFromSettings !== null)
    ) {
      devWarn("FULLNESS_SHOULD_NOT_APPLY_TO_PRODUCT", {
        rowId: audit.rowId,
        itemName: audit.itemName,
        product: audit.product,
        fullnessFromProduct: audit.fullnessFromProduct,
        fullnessFromSettings: audit.fullnessFromSettings,
        finalFullnessUsed: audit.finalFullnessUsed,
        fullnessSource: audit.fullnessSource,
      });
    }
    devLog("STITCH_CONFIG_AUDIT", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      stitchId: audit.selectedStitchId,
      name: audit.selectedStitchLabel,
      rate: audit.resolvedStitchConfig?.rate,
      fullness: audit.resolvedStitchConfig?.fullness,
      trackLength: audit.resolvedStitchConfig?.trackLengthFt,
      topAllowance: audit.topAllowanceIn,
      bottomAllowance: audit.bottomAllowanceIn,
      hemAllowance: audit.hemAllowanceIn,
      cutAllowance: audit.totalCutAllowanceIn,
      calculationFields: {
        fullnessSource: audit.fullnessSource,
        panelsSource: audit.panelsSource,
        displayedMetersSource: audit.displayedMetersSource,
      },
      rawConfig: audit.resolvedStitchConfig?.rawConfig || {},
    });
    devLog("PANEL_ROUNDING_AUDIT", {
      rowId: audit.rowId,
      itemName: audit.itemName,
      stitch: audit.selectedStitchLabel,
      rawPanels: audit.rawPanels,
      wholePanels: audit.wholePanels,
      halfStepPanels: audit.halfStepPanels,
      panelsUsed: audit.panelsUsedForCustomerBilling,
      panelsUsedSource: audit.panelsSource,
      calculationMode: audit.savedSnapshotMode ? "savedSnapshot" : "live",
    });
    devLog("ROW_DISPLAY_SOURCE_AUDIT", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      legacyMode: audit.legacyMode,
      savedSnapshotExists: audit.savedSnapshotMode,
      savedSnapshotOrderedFabric: audit.savedOrderedFabricMeters,
      liveOrderedFabric: audit.liveCalculatedOrderedFabricMeters,
      displayedOrderedFabric: audit.displayedOrderedFabricMeters,
      displayedSource: audit.displayedMetersSource,
    });
  });

  curtainOrderedFabricComparisons.forEach((comparison) => {
    if (
      comparison.conclusion === "Bug: same inputs but different output" ||
      (comparison.sameVisibleInputs && comparison.outputDifferenceMeters > 0.01)
    ) {
      devError("IDENTICAL_CURTAIN_INPUTS_DIFFERENT_FABRIC_METERS", comparison);
    }
  });
}, [
  curtainOrderedFabricComparisons,
  curtainOrderedFabricRowAudits,
]);

const copyCurtainRowCalculationAudit = useCallback(async () => {
  const payload = {
    quoteNo,
    quoteDisplayMode: curtainQuoteTotalMode,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    rows: curtainOrderedFabricRowAudits,
    comparisons: curtainOrderedFabricComparisons,
  };
  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    notify("Row calculation audit copied.", "success");
  } catch (error) {
    devLog("CURTAIN_ROW_CALCULATION_AUDIT", payload);
    notify("Clipboard unavailable. Row calculation audit logged to console.", "info");
  }
}, [
  curtainOrderedFabricComparisons,
  curtainOrderedFabricRowAudits,
  curtainQuoteTotalMode,
  notify,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  useHalfPieceReuse,
]);

const curtainLiningSummaryRows = useMemo(() => {
  const grouped = new Map();

  curtainMeasurementEntries.forEach(({ row, index }) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    const lining = normalizeLiningForSave(
      {
        ...row,
        lining:
          calc.lining ||
          calculateCurtainRowLining(row, 0, false, {
            sqft: calc.adjSqft ?? calc.billableSqft ?? 0,
          }),
      },
      getRowProductConfig(row),
      calc
    );
    if (!curtainRowSupportsLining(row, calc) || !lining.enabled) return;

    const meters = round2(Number(lining.billableMeters || 0));
    const sqft = round2(Number(lining.sqft || 0));
    const amount = round2(Number(lining.amount || 0));
    if (meters <= 0 && amount <= 0) return;

    const label = getCurtainLiningLabel(lining);
    const chargeMethod = lining.chargeMethod === "sqft" ? "sqft" : "meter";
    const fabricCode = sanitizeEditableText(lining.fabricCode || "");
    const brandName =
      sanitizeEditableText(lining.brandName || "") ||
      getFabricBrandNameById(lining.brandId);
    const key = `${chargeMethod}::${normalizeFabricKey(label)}::${normalizeFabricKey(brandName)}::${normalizeFabricKey(fabricCode)}`;
    const previous = grouped.get(key) || {
      key,
      label,
      fabricCode,
      brandName,
      chargeMethod,
      meters: 0,
      sqft: 0,
      amount: 0,
      rowCount: 0,
    };

    grouped.set(key, {
      ...previous,
      meters: round2(previous.meters + meters),
      sqft: round2(previous.sqft + sqft),
      amount: round2(previous.amount + amount),
      rowCount: previous.rowCount + 1,
    });
  });

  return Array.from(grouped.values()).sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""))
  );
}, [
  curtainMeasurementEntries,
  getCurtainProductionAdjustedRowCalc,
  getFabricBrandNameById,
  getRowProductConfig,
]);

const curtainLiningTotalAmount = useMemo(
  () =>
    round2(
      curtainLiningSummaryRows.reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0
      )
    ),
  [curtainLiningSummaryRows]
);

const shouldShowFabricInput = useCallback(
  (row = {}, productConfig = null) => {
    const config = productConfig || getRowProductConfig(row);
    const hasExistingFabricReference = Boolean(getEditableFabricValue(row));
    if (hasExistingFabricReference) return true;
    if (!config) return true;
    const displayFields = config.displayFields || {};
    if (typeof displayFields.showFabric === "boolean") {
      return displayFields.showFabric;
    }

    return Boolean(
      config.requiredFields?.fabric ||
        config.fabricRules?.enabled ||
        config.pdfRules?.showFabric ||
        config.workshopRules?.includeFabricSummary
    );
  },
  [getRowProductConfig]
);

const shouldEnableFabricInput = useCallback(
  (row = {}, productConfig = null) => {
    const config = productConfig || getRowProductConfig(row);
    if (config?.displayFields?.fabricDisabled === true) return false;
    return shouldShowFabricInput(row, config);
  },
  [getRowProductConfig, shouldShowFabricInput]
);

const getFabricInputLabel = useCallback((productConfig = null) => {
  const label = productConfig?.displayFields?.fabricLabel || "Fabric";
  return String(label || "Fabric").trim() || "Fabric";
}, []);

const shouldShowRateAmountInput = useCallback(
  (row = {}, productConfig = null) =>
    shouldProductUseRateAmountField(row, productConfig || getRowProductConfig(row)),
  [getRowProductConfig]
);

const getRowRateFieldMeta = useCallback(
  (row = {}) => {
    const { config, calculationType, requiredFields } = getRowProductRules(row);
    if (!shouldShowRateAmountInput(row, config)) {
      return { field: "", label: "", disabled: true, hidden: true };
    }
    if (calculationType === "manual_amount") {
      return { field: "manualAmount", label: "Amount", disabled: false };
    }
    if (calculationType === "quantity_rate") {
      return { field: "unitRate", label: "Unit Rate", disabled: false };
    }
    if (calculationType === "running_length_rate") {
      return { field: "ratePerLength", label: "Rate/length", disabled: false };
    }
    if (requiredFields.ratePerSqft || calculationType === "sqft_only") {
      return { field: "sqftRate", label: "Rate/sqft", disabled: false };
    }
    return { field: "sqftRate", label: "Rate", disabled: true };
  },
  [getRowProductRules, shouldShowRateAmountInput]
);

const getRowSecondaryFieldMeta = useCallback(
  (row = {}) => {
    const { config, calculationType, requiredFields } = getRowProductRules(row);
    if (calculationType === "quantity_rate" || requiredFields.quantity) {
      return { field: "qty", label: "Qty", disabled: false };
    }
    if (
      calculationType === "running_length_rate" ||
      (config?.runningFeetRules?.formula === "manual_feet" && requiredFields.length)
    ) {
      return { field: "length", label: "Length", disabled: false };
    }
    return { field: "customStitchRate", label: "Custom Stitch", disabled: true };
  },
  [getRowProductRules]
);

const applyProductConfigToRow = useCallback(
  (row = {}, productConfig = null, options = {}) => {
    const config = productConfig ? normalizeProductConfig(productConfig) : null;
    if (!config) return row;

    const nextRow = {
      ...applyProductToRow(row, productConfig, { ...options, legacySettings: { ...fabricCalcSettings, minSqft } }),
      lineType: isAccessoryProductConfig(config) ? "accessory" : "measurement",
      category: config.category || row.category || "",
      productId: config.id,
      productName: config.name,
      product: config.name,
      productType: config.name,
      calculationType: config.calculationType,
      productConfigSnapshot: buildProductConfigSnapshot(config),
    };
    const canApplyDefault = (fieldName) =>
      (nextRow[fieldName] === "" || nextRow[fieldName] == null) &&
      nextRow.touchedFields?.[fieldName] !== true;

    if (config.requiredFields.fabricWidth && canApplyDefault("fabricWidthIn")) {
      nextRow.fabricWidthIn = config.fabricRules.fabricWidthIn || 54;
    }
    if (config.requiredFields.ratePerSqft && canApplyDefault("sqftRate")) {
      nextRow.sqftRate = config.sqftRules.defaultRate || "";
    }
    if (config.requiredFields.ratePerFeet && canApplyDefault("ratePerFeet")) {
      nextRow.ratePerFeet = config.runningFeetRules.defaultRate || "";
    }
    if (config.requiredFields.ratePerLength && canApplyDefault("ratePerLength")) {
      nextRow.ratePerLength = config.runningFeetRules.defaultRate || "";
    }
    if (config.requiredFields.quantity && canApplyDefault("quantity")) {
      nextRow.quantity = 1;
    }
    if (config.requiredFields.quantity && canApplyDefault("qty")) {
      nextRow.qty = nextRow.quantity || 1;
    }
    if (config.requiredFields.unitRate && canApplyDefault("unitRate")) {
      nextRow.unitRate = config.quantityRules.defaultRate || "";
    }
    if (isAccessoryProductConfig(config)) {
      nextRow.fabric = "";
      nextRow.fabricWidthIn = "";
      nextRow.width = "";
      nextRow.height = "";
      nextRow.mrp = "";
      nextRow.sqftRate = "";
      nextRow.customStitchRate = "";
      nextRow.stitchingStyle = "";
      nextRow.lining = { ...DEFAULT_CURTAIN_LINING };
      nextRow.unit = nextRow.unit || config.quantityRules.defaultUnit || "pcs";
      nextRow.lengthUnit = nextRow.lengthUnit || config.runningFeetRules.defaultUnit || "ft";
      if (config.calculationType === "quantity_rate") {
        nextRow.qty = nextRow.qty || nextRow.quantity || 1;
        nextRow.quantity = nextRow.quantity || nextRow.qty || 1;
        nextRow.unitRate = nextRow.unitRate ?? config.quantityRules.defaultRate ?? "";
      }
      if (config.calculationType === "running_length_rate") {
        nextRow.length = nextRow.length || nextRow.runningFeet || "";
        nextRow.ratePerLength =
          nextRow.ratePerLength ?? nextRow.ratePerFeet ?? config.runningFeetRules.defaultRate ?? "";
      }
      if (config.calculationType === "manual_amount") {
        nextRow.manualAmount = nextRow.manualAmount ?? "";
      }
    }
    if (config.requiredFields.manualAmount && nextRow.manualAmount == null) {
      nextRow.manualAmount = "";
    }
    if (
      config.stitchingRules?.defaultStitchingStyleId &&
      !nextRow.stitchingStyle
    ) {
      nextRow.stitchingStyle = config.stitchingRules.defaultStitchingStyleId;
    }

    return sanitizeRowPricingFieldsByProduct(nextRow, config);
  },
  [fabricCalcSettings, minSqft]
);

const isCurtain = (type) =>
  type === "Curtains" ||
  type === "Roman Blind - Normal" ||
  type === "Roman Blind - Clip On";

const isEyeletCustom = (row) =>
  row.stitchingStyle === "Eyelet (Custom)";

const updateField = (rowIdOrIndex, field, value, explicitSource = null) => {
  const source =
    explicitSource ||
    (["productType", "__resetProductRate"].includes(field)
      ? QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
      : QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);

  commitCurtainRows((prev) => {
    const targetRowId =
      typeof rowIdOrIndex === "number"
        ? getWorkshopRowStableId(prev[rowIdOrIndex] || {})
        : rowIdOrIndex;

    return prev.map((existingRow, index) => {
      const stableExistingRow = withCurtainRowId(existingRow || {}, index);
      if (getWorkshopRowStableId(stableExistingRow) !== targetRowId) {
        return stableExistingRow;
      }

    const nextValue = value;

    let row = {
      ...stableExistingRow,
      [field]: nextValue
    };
    if (["gstRate", "gst"].includes(field) && source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) {
      row.gstSource = "manual_override";
    }
    if (EDITABLE_PRODUCT_RATE_FIELDS.includes(field) && source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) {
      if (field === "sqftRate") row.ratePerSqft = nextValue;
      if (field === "ratePerLength") row.ratePerFeet = nextValue;
      row.rateChangeNotice = "";
      row.rateSources = { ...(row.rateSources || {}), [field]: "manual_override" };
      row.catalogueRates = { ...(row.catalogueRates || {}) };
      delete row.catalogueRates[field];
      if (row.cataloguePricing?.field === field) row.cataloguePricing = null;
      row.cataloguePricingError = "";
      const primaryField = resolveDefaultRateForProduct(getRowProductConfig(row) || {}).field;
      if (field === primaryField) {
        row.rateSource = "manual_override";
        row.rateOverridden = true;
        row.rateField = field;
      }
    }

    if (field === "stitchingStyle") {
      const stitchConfig = stitchingRates?.[nextValue] || {};
      const stitchSourceRow = {
        ...row,
        stitchingStyle: nextValue,
        stitchingStyleId: nextValue,
      };
      const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
        stitchSourceRow,
        stitchingRates,
        getRowProductConfig(stitchSourceRow)
      );
      const safeStitchLabel = isWorkshopResolvedLabel(resolvedStitchLabel)
        ? resolvedStitchLabel
        : "";

      row = {
        ...row,
        stitchingStyle: nextValue,
        stitchingStyleId: nextValue,
        stitch: safeStitchLabel,
        stitchLabel: safeStitchLabel,
        stitchingLabel: safeStitchLabel,
        trackType: getWorkshopFieldLabel(stitchConfig.trackType) || row.trackType || "",
        trackName: getWorkshopFieldLabel(stitchConfig.trackName) || row.trackName || "",
      };

      if (IS_DEV_UI_ENABLED) {
        devLog("STITCH_DROPDOWN_CHANGE", {
          rowId: row.id || row.rowId || row.sourceRowId || "",
          rowIndex: index,
          selectedValue: nextValue,
          selectedOption: stitchConfig,
          resolvedStitchLabel: safeStitchLabel,
          nextRow: row,
        });
      }
    }

    if (field === "productType" || field === "__resetProductRate") {
      const productValue = field === "__resetProductRate" ? stableExistingRow.productId || stableExistingRow.productType : value;
      const selectedConfig =
        productConfigById.get(String(productValue || "")) ||
        curtainProductOptions.find(
          (config) => config.name === productValue || config.normalizedName === normalizeProductName(productValue)
        );
      row = selectedConfig ? applyProductConfigToRow(stableExistingRow, selectedConfig, { resetRate: field === "__resetProductRate" }) : stableExistingRow;
      if (selectedConfig && IS_DEV_UI_ENABLED) {
        const defaultRate = resolveDefaultRateForProduct(selectedConfig);
        const line = calculateProductRow(row, selectedConfig, { stitchingRates, fabricCalcSettings, minSqft, repeatSettings, quoteFullnessOverrides, gstRate: curtainGstRate });
        devLog("PRODUCT_CHANGE_RATE_TRACE", {
          quoteType: "curtain", rowId: targetRowId,
          oldProductId: stableExistingRow.productId, oldProductName: stableExistingRow.productName || stableExistingRow.productType,
          oldProductType: stableExistingRow.productType,
          oldRate: stableExistingRow[stableExistingRow.rateField || defaultRate.field], oldRateSource: stableExistingRow.rateSource || "loaded_saved_quote",
          newProductId: row.productId, newProductName: row.productName, newProductType: row.productType,
          newDefaultRate: defaultRate.rate, finalRate: row[defaultRate.field], finalRateSource: row.rateSource,
          recalculatedLineTotal: line.total, totalsUpdated: true,
        });
        if (row.rateSource === "product_default" && defaultRate.rate != null && Number(row[defaultRate.field]) !== Number(defaultRate.rate)) devWarn("STALE_PRODUCT_RATE_DETECTED", { rowId: targetRowId, productName: row.productName, rate: row[defaultRate.field], expectedDefaultRate: defaultRate.rate });
      }
    }

    if (
      [
        "stitchingStyle",
        "stitchingStyleId",
        "customStitchRate",
        "trackConfig",
        "trackLengthFt",
        "trackLength",
      ].includes(field)
    ) {
      row = {
        ...row,
        calculationSnapshot: null,
        calculationSnapshotStatus: "current",
      };
    }

    if (
      source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT ||
      source === QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
    ) {
      row = {
        ...row,
        calculationSnapshot: null,
        calculationSnapshotStatus: "current",
        __forceCurrentCalculation: true,
        __recalculated: true,
      };
    }

    const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
    row.repeatSettings = normalizeCurtainRepeatEntry(
      repeatSettings?.[fabricKey] || {}
    );

      const touchedFields =
        source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT ||
        source === QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
          ? {
              ...(row.touchedFields || {}),
              [field]: true,
            }
          : stableExistingRow.touchedFields || {};

      return withCurtainRowId(
        {
          ...row,
          touchedFields,
          rowUpdatedAt: new Date().toISOString(),
        },
        index
      );
    });
  }, source);
};

const renderProductRateStatus = (row, rowId) => {
  if (row.rateSource !== "manual_override") return row.rateChangeNotice ? <div style={{ marginTop: 4, fontSize: 11, color: "#15803d" }}>{row.rateChangeNotice}</div> : null;
  const defaultRate = resolveDefaultRateForProduct(getRowProductConfig(row) || {});
  return <div style={{ marginTop: 4, fontSize: 11 }}>
    <span style={{ color: "#b45309", fontWeight: 800 }}>Manual Rate</span>{" "}
    {defaultRate.rate != null ? <button type="button" title="Reset to Product Rate" onClick={() => updateField(rowId, "__resetProductRate", null)} style={{ border: "1px solid #94a3b8", borderRadius: 6, padding: "3px 5px", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 10 }}>Reset to Product Rate</button> : <span>Fabric price is entered separately.</span>}
  </div>;
};

const patchCurtainRowFields = (
  rowIdOrIndex,
  patch = {},
  explicitSource = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
) => {
  const source = explicitSource || QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT;
  commitCurtainRows((prev) => {
    const targetRowId =
      typeof rowIdOrIndex === "number"
        ? getWorkshopRowStableId(prev[rowIdOrIndex] || {})
        : rowIdOrIndex;

    return prev.map((existingRow, index) => {
      const stableExistingRow = withCurtainRowId(existingRow || {}, index);
      if (getWorkshopRowStableId(stableExistingRow) !== targetRowId) {
        return stableExistingRow;
      }

      const resolvedPatch = typeof patch === "function" ? patch(stableExistingRow) : patch;
      if (!resolvedPatch) return stableExistingRow;
      const patchKeys = Object.keys(resolvedPatch);
      let row = {
        ...stableExistingRow,
        ...resolvedPatch,
      };

      if (source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) {
        row = {
          ...row,
          calculationSnapshot: null,
          calculationSnapshotStatus: "current",
          __forceCurrentCalculation: true,
          __recalculated: true,
        };
      }

      const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
      row.repeatSettings = normalizeCurtainRepeatEntry(
        repeatSettings?.[fabricKey] || {}
      );

      return withCurtainRowId(
        {
          ...row,
          touchedFields: {
            ...(stableExistingRow.touchedFields || {}),
            ...Object.fromEntries(patchKeys.map((key) => [key, true])),
          },
          rowUpdatedAt: new Date().toISOString(),
        },
        index
      );
    });
  }, source);
};

const getFabricCatalogueLibraryForBrand = useCallback(
  (brandId = "") =>
    fabricCatalogueLibraryRef.current[brandId] ||
    fabricCatalogueLibraryByBrand[brandId] ||
    getEmptyFabricCatalogueLibrary(getFabricBrandById(brandId) || { id: brandId }),
  [fabricCatalogueLibraryByBrand, getEmptyFabricCatalogueLibrary, getFabricBrandById]
);

const findFabricBrandForRow = useCallback(
  (row = {}, preferredBrandId = "") => {
    const brandId = sanitizeEditableText(
      preferredBrandId || row.brandId || row.fabricBrandId || ""
    );
    const brandName = sanitizeEditableText(row.brandName || row.fabricBrandName || "");
    return (
      (brandId ? getFabricBrandById(brandId) : null) ||
      (brandName
        ? (fabricBrandsRef.current.length ? fabricBrandsRef.current : fabricBrands).find(
            (brand) =>
              normalizeFabricKey(brand.name || "") === normalizeFabricKey(brandName) &&
              brand.archived !== true &&
              brand.active !== false
          ) || null
        : null)
    );
  },
  [fabricBrands, getFabricBrandById]
);

const getWorkspaceDefaultFabricWidthIn = useCallback(
  () =>
    normalizeFabricWidthIn(
      fabricCalcSettings?.fabricWidthIn ??
        fabricCalcSettings?.defaultFabricWidthIn ??
        fabricCalcSettings?.fabricRules?.fabricWidthIn,
      54
    ) || 54,
  [fabricCalcSettings]
);

const buildWorkspaceFabricWidthPatch = useCallback(
  () => ({
    fabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
    fabricWidthSource: "workspace_default",
    fabricWidthManuallyOverridden: false,
    catalogueDefaultFabricWidthIn: null,
    srRuleFabricWidthIn: null,
  }),
  [getWorkspaceDefaultFabricWidthIn]
);

const validateCurtainRowFabricAgainstSettings = useCallback(
  (
    row = {},
    {
      brandId = row.brandId || row.fabricBrandId || "",
      fabricText = getEditableFabricValue(row),
    } = {}
  ) => {
    const brand = findFabricBrandForRow(row, brandId);
    const library = brand?.id
      ? getFabricCatalogueLibraryForBrand(brand.id)
      : getEmptyFabricCatalogueLibrary({});
    return validateQuoteFabricAgainstSettings(row, {
      brand,
      library,
      fabricText,
      existingMrp: row.mrp || row.mrpPerMeter || "",
      workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
    });
  },
  [
    findFabricBrandForRow,
    getEmptyFabricCatalogueLibrary,
    getFabricCatalogueLibraryForBrand,
    getWorkspaceDefaultFabricWidthIn,
  ]
);

const sanitizeCurtainFabricForDisplayAgainstSettings = useCallback(
  (
    row = {},
    {
      brandId = row.brandId || row.fabricBrandId || "",
      fabricText = getEditableFabricValue(row),
    } = {}
  ) => {
    const brand = findFabricBrandForRow(row, brandId);
    const library = brand?.id
      ? getFabricCatalogueLibraryForBrand(brand.id)
      : getEmptyFabricCatalogueLibrary({});
    const displayInfo = sanitizeFabricForDisplayAgainstSettings(row, {
      brand,
      library,
      fabricText,
      existingMrp: row.mrp || row.mrpPerMeter || "",
      workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
    });
    return {
      ...displayInfo,
      libraryLoaded: brand?.id
        ? fabricCatalogueLoadedBrandKeysRef.current.has(`${workspaceUid}:${brand.id}`)
        : true,
      brandId: brand?.id || "",
    };
  },
  [
    findFabricBrandForRow,
    getEmptyFabricCatalogueLibrary,
    getFabricCatalogueLibraryForBrand,
    getWorkspaceDefaultFabricWidthIn,
    workspaceUid,
  ]
);

const ensureFabricCatalogueLibrariesForRows = useCallback(
  async (sourceRows = [], { force = false } = {}) => {
    if (!workspaceUid) return;
    const brandIds = Array.from(
      new Set(
        (Array.isArray(sourceRows) ? sourceRows : [])
          .map((row) => findFabricBrandForRow(row)?.id || "")
          .filter(Boolean)
      )
    );
    await Promise.all(
      brandIds.map((brandId) => loadFabricCatalogueLibrary(brandId, { force }))
    );
  },
  [findFabricBrandForRow, loadFabricCatalogueLibrary, workspaceUid]
);

const buildCustomUnverifiedFabricPatch = (
  row = {},
  fabricText = getEditableFabricValue(row),
  invalidReason = "custom_not_in_settings"
) => {
  const displayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row, {
    fabricText,
  });
  const cleanFabricText = sanitizeEditableText(
    displayInfo.displayFabricCode || fabricText || ""
  );
  const manualRrp = parseMoneyInput(
    getFirstNonBlankValue(row.mrp, row.mrpPerMeter, row.rrpPerMeter, row.ratePerMeter),
    null
  );
  const preservedBrandId = sanitizeEditableText(row.brandId || row.fabricBrandId || "");
  const preservedBrandName = sanitizeEditableText(
    row.brandName || row.fabricBrandName || getFabricBrandNameById(preservedBrandId) || ""
  );
  const preservedBrandSource = sanitizeEditableText(row.brandSource || "");
  const wasCatalogueWidth =
    row.fabricWidthSource === "catalogue_rule" ||
    row.fabricWidthSource === "catalogue_default" ||
    row.fabricSource === "settings_catalogue" ||
    Boolean(row.catalogueId || row.catalogueKey || row.catalogueName);
  const widthPatch =
    wasCatalogueWidth && row.fabricWidthManuallyOverridden !== true
      ? buildWorkspaceFabricWidthPatch()
      : {};
  return {
    fabric: cleanFabricText,
    fabricName: cleanFabricText,
    fabricCode: cleanFabricText,
    fabricCodeRaw: cleanFabricText,
    fabricSource: cleanFabricText ? "quote_manual" : "none",
    fabricCatalogueInvalidReason: invalidReason,
    brandId: preservedBrandId,
    brandName: preservedBrandName,
    fabricBrandId: preservedBrandId,
    fabricBrandName: preservedBrandName,
    brandSource: preservedBrandId || preservedBrandName
      ? preservedBrandSource === "manual"
        ? "manual"
        : preservedBrandSource || "quote_manual"
      : preservedBrandSource === "manual_cleared"
      ? "manual_cleared"
      : "none",
    catalogueId: "",
    catalogueName: row.catalogueId ? "" : String(row.catalogueName ?? ""),
    catalogueKey: "",
    srNumber: sanitizeEditableText(row.srNumber || row.srNo || row.sr || ""),
    srLabel: srDisplayLabel(row.srNumber || row.srNo || row.sr || ""),
    srPriceRuleId: "",
    srPriceRuleType: "none",
    srPriceRuleLabel: "",
    rrpPerMeter: manualRrp,
    rrpSource: manualRrp !== null ? "manual_quote" : "quote_manual",
    selectedFabricRef: null,
    ...widthPatch,
  };
};

const syncFabricSummaryBrandFromRows = useCallback(
  (rowId = "", rowPatch = {}) => {
    if (!rowId) return;
    const nextRows = normalizeRowsForState(rowsRef.current || []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return getWorkshopRowStableId(stableRow) === rowId
        ? withCurtainRowId({ ...stableRow, ...rowPatch }, index)
        : stableRow;
    });
    const targetRow = nextRows.find((row) => getWorkshopRowStableId(row) === rowId);
    const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(targetRow || {}));
    if (!fabricKey) return;

    const groupRows = nextRows.filter(
      (row) => normalizeFabricKey(getCurtainRowFabricKey(row)) === fabricKey
    );
    if (!groupRows.length) return;

    const inferredIdentity = inferFabricSummaryIdentityFromRows(groupRows, {
      key: fabricKey,
      normalizedKey: fabricKey,
      fabricCode: getCurtainRowFabricLabel(targetRow || {}),
    });
    if (
      inferredIdentity.hasMixedBrands ||
      (!inferredIdentity.brandId && !inferredIdentity.brandName)
    ) {
      return;
    }

    const brandId = inferredIdentity.brandId || "";
    const brandName = inferredIdentity.brandName || getFabricBrandNameById(brandId);
    const cataloguePatch =
      inferredIdentity.catalogueId ||
      inferredIdentity.catalogueName ||
      inferredIdentity.catalogueKey ||
      inferredIdentity.srNumber
        ? {
            catalogueId: inferredIdentity.catalogueId || "",
            catalogueName: inferredIdentity.catalogueName || "",
            catalogueKey: inferredIdentity.catalogueKey || "",
            srNumber: inferredIdentity.srNumber || "",
            fabricLibraryKey: inferredIdentity.fabricKey || "",
          }
        : {};
    setFabricSummaryMeta((prev) => {
      const normalized = normalizeFabricSummaryMeta(prev);
      const current = normalized[fabricKey] || {};
      const seededMeta = normalizeFabricSummaryMeta({
        ...normalized,
        [fabricKey]: {
          ...current,
          brandId,
          brandName,
          fabricBrandId: brandId,
          fabricBrandName: brandName,
          brandSource: current.brandSource || "row_inferred",
          ...cataloguePatch,
        },
      });
      const nextMeta = buildFabricSummaryMetaWithRowInferences(
        seededMeta,
        repeatSettingsRef.current || repeatSettings || {},
        nextRows,
        { traceLabel: "row_change" }
      );
      fabricSummaryMetaRef.current = nextMeta;
      return nextMeta;
    });
  },
  [getFabricBrandNameById, normalizeRowsForState, repeatSettings]
);

const buildFabricCatalogueResolutionPatch = (
  row = {},
  {
    brandId = row.brandId || row.fabricBrandId || "",
    fabricText = getEditableFabricValue(row),
    includeResolvedMrp = true,
    forceResolvedFabricWidth = false,
  } = {}
) => {
  const preserveManualFabricWidth =
    forceResolvedFabricWidth !== true &&
    (row.fabricWidthManuallyOverridden === true ||
      row.fabricWidthSource === "manual_override");
  const brand = getFabricBrandById(brandId);
  if (!brand) {
    return {
      fabricCodeRaw: fabricText,
      brandId: "",
      brandName: "",
      fabricBrandId: "",
      fabricBrandName: "",
      catalogueId: "",
      catalogueName: "",
      catalogueKey: "",
      srNumber: "",
      srLabel: "",
      srPriceRuleId: "",
      srPriceRuleType: "none",
      rrpSource: "unmatched",
    };
  }

  const library = getFabricCatalogueLibraryForBrand(brand.id);
  const resolution = resolveFabricCatalogueRrp({
    brand,
    library,
    fabricText,
    existingMrp: includeResolvedMrp ? "" : row.mrp,
    workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
  });
  const validation = validateQuoteFabricAgainstSettings(row, {
    brand,
    library,
    fabricText,
    existingMrp: row.mrp || row.mrpPerMeter || "",
    workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
  });
  if (!validation.isValidCatalogue) {
    return buildCustomUnverifiedFabricPatch(
      row,
      fabricText,
      validation.invalidReason || "custom_not_in_settings"
    );
  }
  const parsed = resolution.parsed || parseFabricCatalogueInput(fabricText);
  const patch = {
    fabricCodeRaw: fabricText,
    brandId: brand.id,
    brandName: brand.name,
    fabricBrandId: brand.id,
    fabricBrandName: brand.name,
    brandSource: "catalogue_selection",
    fabricSource: "settings_catalogue",
    fabricCatalogueInvalidReason: "",
    ...buildFabricCatalogueRowPatch(resolution),
    catalogueName:
      validation.validCatalogueName || resolution.catalogue?.catalogueName || "",
    catalogueKey:
      validation.validCatalogueKey || resolution.catalogue?.catalogueKey || "",
    srNumber: parsed.srNumber || "",
    srLabel: parsed.srLabel || "",
  };

  if (preserveManualFabricWidth) {
    const manualFabricWidth = normalizeFabricWidthIn(row.fabricWidthIn);
    patch.fabricWidthIn = manualFabricWidth || row.fabricWidthIn || "";
    patch.fabricWidthSource = "manual_override";
    patch.fabricWidthManuallyOverridden = true;
  }

  if (includeResolvedMrp) {
    // Identity and pricing are separate; never treat a meter price as sqft.
    Object.assign(patch, buildCatalogueRatePatch({ ...row, productConfigSnapshot: getRowProductConfig(row) }, resolution));
    if ((getRowProductConfig(row)?.calculationType || row.calculationType) === "sqft_only") {
      delete patch.rrpPerMeter;
      delete patch.rrpSource;
    }
  } else {
    // Save normalization may refresh identity, never reprice persisted inputs.
    delete patch.rrpPerMeter;
    delete patch.rrpSource;
  }
  if (!includeResolvedMrp && hasManualFabricRate(row)) {
    for (const field of ["mrp", "mrpPerMeter", "ratePerMeter", "rrpPerMeter", "rrpSource", "rateSource", "rateSources"]) {
      if (Object.prototype.hasOwnProperty.call(row, field)) patch[field] = row[field];
    }
  }

  if (IS_DEV_UI_ENABLED) {
    devLog("FABRIC_CATALOGUE_RRP_RESOLVE_TRACE", {
      rowId: row.id || row.rowId || row.sourceRowId || "",
      brandId: brand.id,
      brandName: brand.name,
      fabricText,
      parsed,
      catalogueId: resolution.catalogue?.id || "",
      ruleId: resolution.rule?.id || "",
      ruleType: resolution.rule?.ruleType || "",
      rrpPerMeter: resolution.rrpPerMeter,
      rrpSource: resolution.rrpSource,
      fabricWidthIn: patch.fabricWidthIn,
      fabricWidthSource: patch.fabricWidthSource,
      catalogueDefaultFabricWidthIn: patch.catalogueDefaultFabricWidthIn,
      srRuleFabricWidthIn: patch.srRuleFabricWidthIn,
    });
  }

  return patch;
};

const handleCurtainRowBrandChange = async (rowId, row = {}, brandId = "") => {
  const brand = getFabricBrandById(brandId);
  const fabricText = getEditableFabricValue(row);
  const brandPatch = {
    brandId: brand?.id || "",
    brandName: brand?.name || "",
    fabricBrandId: brand?.id || "",
    fabricBrandName: brand?.name || "",
    brandSource: brand?.id ? "manual" : "none",
  };
  patchCurtainRowFields(rowId, brandPatch);
  syncFabricSummaryBrandFromRows(rowId, brandPatch);

  if (!brand?.id) return;
  await loadFabricCatalogueLibrary(brand.id);
  const fresh = (rowsRef.current || []).find(candidate => getWorkshopRowStableId(candidate) === rowId);
  if (!fresh || (fresh.brandId || fresh.fabricBrandId) !== brand.id || getEditableFabricValue(fresh) !== fabricText) return;
  const nextRow = {
    ...fresh,
    brandId: brand.id,
    brandName: brand.name,
    fabricBrandId: brand.id,
    fabricBrandName: brand.name,
    brandSource: "manual",
  };
  if (fabricText) {
    patchCurtainRowFields(rowId, {
      ...buildFabricCatalogueResolutionPatch(nextRow, {
        brandId: brand.id,
        fabricText,
        includeResolvedMrp: true,
        forceResolvedFabricWidth: false,
      }),
      brandSource: "manual",
    });
  }
};

const handleCurtainFabricInputChange = (rowId, row = {}, value = "") => {
  const patch = buildManualFabricDraftPatch(row, value);
  patchCurtainRowFields(rowId, patch);
  syncFabricSummaryBrandFromRows(rowId, patch);
};

const handleCurtainFabricSrNumberChange = async (rowId, row = {}, value = "") => {
  const srNumber = normalizeSrNumber(value);
  const latest = () => (rowsRef.current || []).find(candidate => getWorkshopRowStableId(candidate) === rowId);
  const current = latest() || row;
  const brandId = current.brandId || current.fabricBrandId || "";
  const catalogueId = current.catalogueId;
  if (!hasManualCatalogueRate(current) && isAutomaticFabricRate(current) && brandId && catalogueId &&
      !fabricCatalogueLoadedBrandKeysRef.current.has(`${workspaceUid}:${brandId}`)) {
    await loadFabricCatalogueLibrary(brandId);
  }
  // Apply against current state: a late library response must not undo newer edits.
  patchCurtainRowFields(rowId, fresh => {
    if (String(fresh.srNumber ?? "").trim() !== srNumber ||
        (fresh.brandId || fresh.fabricBrandId || "") !== brandId || fresh.catalogueId !== catalogueId) return null;
    const patch = buildSrPricingCommitPatch({ ...fresh, productConfigSnapshot: getRowProductConfig(fresh) }, srNumber, {
      brand: getFabricBrandById(brandId), library: getFabricCatalogueLibraryForBrand(brandId),
      workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
    });
    devLog("FABRIC_SR_PARENT_PRICE_TRACE", {
      rowId, catalogueUnchanged: !Object.hasOwn(patch, "catalogueId") && !Object.hasOwn(patch, "catalogueName"),
      manualRatePreserved: hasManualFabricRate(fresh), pricingSource: patch.rrpSource || fresh.rrpSource,
    });
    return patch;
  });
};

const handleCurtainFabricInputBlur = async (rowId, row = {}) => {
  // Moving focus from a selected parent to its SR is not a catalogue change.
  if (row.catalogueId && row.catalogueName) return;
  const fabricText = sanitizeEditableText(
    row.fabricCodeRaw || getEditableFabricValue(row)
  );
  if (!fabricText) return;
  const validation = validateCurtainRowFabricAgainstSettings(row, { fabricText });
  const patch = validation.isValidCatalogue && row.catalogueId && row.fabricSource !== "quote_manual"
    ? getCurtainRowFabricCatalogueSavePatch(row)
    : buildCustomUnverifiedFabricPatch(
        row,
        fabricText,
        validation.invalidReason || "custom_not_in_settings"
      );
  patchCurtainRowFields(rowId, patch);
};

const syncFabricCatalogueEntriesForQuoteRows = async (sourceRows = []) => {
  if (!Array.isArray(sourceRows) || !sourceRows.length) return;
  if (IS_DEV_UI_ENABLED) {
    const invalidRows = sourceRows
      .map((row, index) => {
        const validation = validateCurtainRowFabricAgainstSettings(row);
        return validation.isValidCatalogue || !getEditableFabricValue(row)
          ? null
          : {
              index,
              rowId: getWorkshopRowStableId(row) || "",
              fabricText: getEditableFabricValue(row),
              invalidReason: validation.invalidReason,
            };
      })
      .filter(Boolean);
    if (invalidRows.length) {
      devLog("FABRIC_CATALOGUE_SAVE_VALIDATION_ONLY", invalidRows);
    }
  }
};

const getCurtainRowFabricCatalogueSavePatch = (row = {}) => {
  const fabricText = row.fabricCodeRaw || getEditableFabricValue(row);
  const displayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row, {
    fabricText,
  });
  if (!displayInfo.isValidCatalogue && displayInfo.libraryLoaded === false) {
    return {};
  }
  if (!displayInfo.isValidCatalogue) {
    return buildCustomUnverifiedFabricPatch(
      row,
      displayInfo.displayFabricCode || fabricText,
      displayInfo.invalidReason || "custom_not_in_settings"
    );
  }
  const savePatch = buildFabricCatalogueResolutionPatch(row, {
    brandId: displayInfo.validation?.validBrandId || "",
    fabricText,
    includeResolvedMrp: false,
  });
  return { ...savePatch, srNumber: String(row.srNumber ?? savePatch.srNumber ?? ""),
    srLabel: row.srLabel || savePatch.srLabel || "" };
};
curtainRowFabricCatalogueSavePatchRef.current = getCurtainRowFabricCatalogueSavePatch;

function getFabricSummaryRowIdentityCandidate(row = {}) {
  const displayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
  const activeBrand = findFabricBrandForRow(row);
  const hasCatalogueReference = Boolean(
    row.catalogueId ||
      row.fabricCatalogueId ||
      row.catalogueKey ||
      row.catalogueName ||
      row.fabricCatalogueName ||
      row.fabricSource === "settings_catalogue" ||
      row.brandSource === "catalogue_selection"
  );
  const catalogueRejected =
    displayInfo.libraryLoaded !== false &&
    displayInfo.invalidReason === "catalogue_not_found_or_archived";
  const canInferBrand = Boolean(activeBrand?.id || activeBrand?.name) && !catalogueRejected;
  const brandId = sanitizeEditableText(
    displayInfo.isValidCatalogue
      ? displayInfo.validation?.validBrandId || activeBrand?.id || ""
      : canInferBrand
      ? activeBrand?.id || row.brandId || row.fabricBrandId || ""
      : ""
  );
  const brandName = sanitizeEditableText(
    displayInfo.isValidCatalogue
      ? displayInfo.displayBrandName ||
          displayInfo.validation?.validBrandName ||
          getFabricBrandNameById(brandId)
      : canInferBrand
      ? activeBrand?.name ||
          getFabricBrandNameById(brandId) ||
          row.brandName ||
          row.fabricBrandName ||
          ""
      : ""
  );
  const canUseCatalogueMetadata =
    displayInfo.isValidCatalogue ||
    (displayInfo.libraryLoaded === false && hasCatalogueReference);
  const catalogueId = sanitizeEditableText(
    canUseCatalogueMetadata
      ? displayInfo.validation?.validCatalogueId ||
          row.catalogueId ||
          row.fabricCatalogueId ||
          ""
      : ""
  );
  const catalogueName = sanitizeEditableText(
    canUseCatalogueMetadata
      ? displayInfo.displayCatalogueName ||
          displayInfo.validation?.validCatalogueName ||
          row.catalogueName ||
          row.fabricCatalogueName ||
          ""
      : ""
  );
  const catalogueKey = sanitizeEditableText(
    canUseCatalogueMetadata
      ? displayInfo.validation?.validCatalogueKey ||
          row.catalogueKey ||
          normalizeCatalogueKey(catalogueName)
      : ""
  );
  const srNumber = canUseCatalogueMetadata ? getSpecificRowSrNumber(row) : "";
  const fabricCode = sanitizeEditableText(
    displayInfo.displayFabricCode ||
      getEditableFabricValue(row) ||
      row.fabricCodeRaw ||
      row.fabricCode ||
      row.fabricName ||
      row.fabric ||
      ""
  );

  if (!brandId && !brandName && !catalogueId && !catalogueName && !fabricCode) {
    return null;
  }

  return {
    rowId: getWorkshopRowStableId(row) || "",
    fabricKey: normalizeFabricKey(getCurtainRowFabricKey(row)),
    brandId,
    brandName,
    fabricBrandId: brandId,
    fabricBrandName: brandName,
    catalogueId,
    catalogueName,
    catalogueKey,
    srNumber,
    fabricCode,
    fabricSource: displayInfo.source || row.fabricSource || "",
    isValidCatalogue: displayInfo.isValidCatalogue === true,
    invalidReason: displayInfo.invalidReason || "",
  };
}

function inferFabricSummaryIdentityFromRows(sourceRows = [], summary = {}, currentMeta = {}) {
  const candidates = (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row) => getFabricSummaryRowIdentityCandidate(row))
    .filter(Boolean);
  const uniqueBrands = Array.from(
    new Map(
      candidates
        .filter((entry) => entry.brandId || entry.brandName)
        .map((entry) => [
          entry.brandId || normalizeFabricKey(entry.brandName),
          entry,
        ])
    ).values()
  );
  const uniqueCatalogues = Array.from(
    new Map(
      candidates
        .filter((entry) => entry.catalogueId || entry.catalogueKey || entry.catalogueName)
        .map((entry) => [
          entry.catalogueId || entry.catalogueKey || normalizeFabricKey(entry.catalogueName),
          entry,
        ])
    ).values()
  );
  const uniqueSrNumbers = Array.from(
    new Set(candidates.map((entry) => entry.srNumber).filter(Boolean))
  );
  const uniqueFabricCodes = Array.from(
    new Set(candidates.map((entry) => entry.fabricCode).filter(Boolean))
  );
  const inferredBrand = uniqueBrands.length === 1 ? uniqueBrands[0] : {};
  const inferredCatalogue = uniqueCatalogues.length === 1 ? uniqueCatalogues[0] : {};
  const inferredSrNumber = uniqueSrNumbers.length === 1 ? uniqueSrNumbers[0] : "";
  const fabricCode =
    sanitizeEditableText(summary.fabricCode || summary.displayName || "") ||
    uniqueFabricCodes[0] ||
    sanitizeEditableText(currentMeta.fabricCode || summary.key || summary.normalizedKey || "");
  const identity = getFabricLibraryIdentity({
    row: {},
    summary: {
      ...summary,
      fabricCode,
      displayName: fabricCode || summary.displayName || summary.key || "",
      brandId: inferredBrand.brandId || "",
      brandName: inferredBrand.brandName || "",
      catalogueId: inferredCatalogue.catalogueId || "",
      catalogueName: inferredCatalogue.catalogueName || "",
      catalogueKey: inferredCatalogue.catalogueKey || "",
      srNumber: inferredSrNumber,
    },
    meta: currentMeta,
  });

  return {
    ...identity,
    brandId: inferredBrand.brandId || "",
    brandName: inferredBrand.brandName || "",
    fabricBrandId: inferredBrand.brandId || "",
    fabricBrandName: inferredBrand.brandName || "",
    catalogueId: inferredCatalogue.catalogueId || identity.catalogueId || "",
    catalogueName: inferredCatalogue.catalogueName || identity.catalogueName || "",
    catalogueKey: inferredCatalogue.catalogueKey || identity.catalogueKey || "",
    srNumber: inferredSrNumber || identity.srNumber || "",
    fabricCode,
    fabricDisplayName: identity.fabricDisplayName || fabricCode,
    brandSource: inferredBrand.brandId || inferredBrand.brandName ? "row_inferred" : "",
    hasMixedBrands: uniqueBrands.length > 1,
    mixedBrandNames: uniqueBrands
      .map((entry) => entry.brandName || getFabricBrandNameById(entry.brandId))
      .filter(Boolean),
    traceRows: candidates,
  };
}

function resolveFabricSummaryIdentityForGroup(sourceRows = [], summary = {}, currentMeta = {}) {
  const inferred = inferFabricSummaryIdentityFromRows(sourceRows, summary, currentMeta);
  const metaBrandSource = sanitizeEditableText(currentMeta.brandSource || "");
  const metaBrandId = sanitizeEditableText(currentMeta.brandId || currentMeta.fabricBrandId || "");
  const metaBrandName = sanitizeEditableText(
    currentMeta.brandName || currentMeta.fabricBrandName || ""
  );
  const metaHasBrand = Boolean(metaBrandId || metaBrandName);
  const manualCleared = metaBrandSource === "manual_cleared";
  const preserveMetaBrand =
    !manualCleared &&
    (metaBrandSource === "manual" ||
      (metaHasBrand && metaBrandSource !== "row_inferred"));
  const brandId = manualCleared
    ? ""
    : preserveMetaBrand
    ? metaBrandId
    : inferred.hasMixedBrands
    ? ""
    : inferred.brandId || "";
  const brandName = manualCleared
    ? ""
    : preserveMetaBrand
    ? metaBrandName || getFabricBrandNameById(metaBrandId)
    : inferred.hasMixedBrands
    ? ""
    : inferred.brandName || getFabricBrandNameById(inferred.brandId);
  const brandSource = manualCleared
    ? "manual_cleared"
    : preserveMetaBrand
    ? metaBrandSource || "saved_meta"
    : brandId || brandName
    ? "row_inferred"
    : metaBrandSource || "";
  const catalogueId = sanitizeEditableText(currentMeta.catalogueId || inferred.catalogueId || "");
  const catalogueName = sanitizeEditableText(
    currentMeta.catalogueName || inferred.catalogueName || ""
  );
  const catalogueKey = sanitizeEditableText(currentMeta.catalogueKey || inferred.catalogueKey || "");
  const srNumber = getSpecificFabricSrNumberFromSources(currentMeta, inferred);
  const fabricCode = sanitizeEditableText(
    currentMeta.fabricCode || inferred.fabricCode || summary.fabricCode || summary.displayName || ""
  );
  const identity = getFabricLibraryIdentity({
    row: {},
    summary: {
      ...summary,
      brandId,
      brandName,
      catalogueId,
      catalogueName,
      catalogueKey,
      srNumber,
      fabricCode,
      displayName: fabricCode || summary.displayName || summary.key || "",
    },
    meta: {
      ...currentMeta,
      brandId,
      brandName,
      fabricBrandId: brandId,
      fabricBrandName: brandName,
      catalogueId,
      catalogueName,
      catalogueKey,
      srNumber,
      fabricCode,
    },
  });

  return {
    ...identity,
    brandId,
    brandName,
    fabricBrandId: brandId,
    fabricBrandName: brandName,
    brandSource,
    catalogueId,
    catalogueName,
    catalogueKey,
    srNumber,
    fabricCode,
    fabricDisplayName: identity.fabricDisplayName || fabricCode,
    hasMixedBrands: inferred.hasMixedBrands && !brandId && !brandName && !manualCleared,
    mixedBrandNames: inferred.mixedBrandNames || [],
    traceRows: inferred.traceRows || [],
  };
}

function buildFabricSummaryMetaWithRowInferences(
  meta = {},
  repeatSettingsMap = {},
  sourceRows = [],
  { traceLabel = "" } = {}
) {
  const normalizedMeta = normalizeFabricSummaryMetaWithRepeatSettings(
    meta,
    repeatSettingsMap
  );
  const groupedRows = new Map();
  (Array.isArray(sourceRows) ? sourceRows : []).forEach((row) => {
    const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
    if (!fabricKey) return;
    if (!groupedRows.has(fabricKey)) groupedRows.set(fabricKey, []);
    groupedRows.get(fabricKey).push(row);
  });

  let changed = false;
  const nextMeta = { ...normalizedMeta };
  groupedRows.forEach((groupRows, fabricKey) => {
    const current = normalizedMeta[fabricKey] || {};
    const resolved = resolveFabricSummaryIdentityForGroup(
      groupRows,
      {
        key: fabricKey,
        normalizedKey: fabricKey,
        fabricCode: current.fabricCode || getCurtainRowFabricLabel(groupRows[0] || {}),
        displayName: current.fabricCode || getCurtainRowFabricLabel(groupRows[0] || {}),
      },
      current
    );
    const nextEntry = {
      ...current,
      fabricCode:
        current.fabricCode || resolved.fabricDisplayName || resolved.fabricCode || fabricKey,
      catalogueId: current.catalogueId || resolved.catalogueId || "",
      catalogueName: current.catalogueName || resolved.catalogueName || "",
      catalogueKey: current.catalogueKey || resolved.catalogueKey || "",
      srNumber: current.srNumber || resolved.srNumber || "",
      fabricLibraryKey:
        resolved.brandId &&
        (resolved.catalogueId || resolved.catalogueKey || resolved.catalogueName) &&
        resolved.srNumber &&
        resolved.fabricKey
          ? resolved.fabricKey
          : current.fabricLibraryKey || resolved.fabricKey || "",
      repeatSettings: normalizeCurtainRepeatEntry(
        current.repeatSettings || repeatSettingsMap?.[fabricKey] || {}
      ),
      notes: sanitizeEditableText(current.notes || ""),
    };
    const brandSource = sanitizeEditableText(current.brandSource || "");
    const currentHasBrand = Boolean(current.brandId || current.brandName);
    const shouldApplyInferredBrand =
      brandSource !== "manual_cleared" &&
      brandSource !== "manual" &&
      (!currentHasBrand || brandSource === "row_inferred");

    if (shouldApplyInferredBrand && !resolved.hasMixedBrands && (resolved.brandId || resolved.brandName)) {
      nextEntry.brandId = resolved.brandId || "";
      nextEntry.brandName = resolved.brandName || "";
      nextEntry.fabricBrandId = resolved.brandId || "";
      nextEntry.fabricBrandName = resolved.brandName || "";
      nextEntry.brandSource = "row_inferred";
    } else if (brandSource === "manual_cleared") {
      nextEntry.brandId = "";
      nextEntry.brandName = "";
      nextEntry.fabricBrandId = "";
      nextEntry.fabricBrandName = "";
      nextEntry.brandSource = "manual_cleared";
    } else {
      nextEntry.brandId = current.brandId || "";
      nextEntry.brandName = current.brandName || "";
      nextEntry.fabricBrandId = current.fabricBrandId || current.brandId || "";
      nextEntry.fabricBrandName = current.fabricBrandName || current.brandName || "";
      nextEntry.brandSource = brandSource || "";
    }

    const normalizedEntry = normalizeFabricSummaryMeta({ [fabricKey]: nextEntry })[fabricKey] || {};
    if (JSON.stringify(normalizedEntry) !== JSON.stringify(current)) {
      changed = true;
      if (IS_DEV_UI_ENABLED && (normalizedEntry.brandId || normalizedEntry.brandName)) {
        devLog("FABRIC_SUMMARY_BRAND_TRACE", {
          traceLabel,
          fabricKey,
          rowId: resolved.traceRows?.[0]?.rowId || "",
          rowBrandId: resolved.traceRows?.[0]?.brandId || "",
          rowBrandName: resolved.traceRows?.[0]?.brandName || "",
          rowCatalogueId: resolved.traceRows?.[0]?.catalogueId || "",
          rowCatalogueName: resolved.traceRows?.[0]?.catalogueName || "",
          rowSrNumber: resolved.traceRows?.[0]?.srNumber || "",
          summaryBrandIdBefore: current.brandId || "",
          summaryBrandNameBefore: current.brandName || "",
          inferredBrandId: resolved.brandId || "",
          inferredBrandName: resolved.brandName || "",
          summaryBrandIdAfter: normalizedEntry.brandId || "",
          summaryBrandNameAfter: normalizedEntry.brandName || "",
          brandSource: normalizedEntry.brandSource || "",
        });
      }
    }
    nextMeta[fabricKey] = normalizedEntry;
  });

  return changed ? normalizeFabricSummaryMeta(nextMeta) : normalizedMeta;
}

function sanitizeFabricSummaryMetaForValidCatalogues(
  meta = {},
  repeatSettingsMap = {},
  sourceRows = []
) {
  const normalizedMeta = normalizeFabricSummaryMetaWithRepeatSettings(
    meta,
    repeatSettingsMap
  );
  const sanitizedMeta = {};
  const safeRows = Array.isArray(sourceRows) ? sourceRows : [];

  safeRows.forEach((row) => {
    const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
    if (!fabricKey) return;
    const displayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
    const current = sanitizedMeta[fabricKey] || normalizedMeta[fabricKey] || {};
    const currentBrandSource = sanitizeEditableText(current.brandSource || "");
    const keepManualBrand = currentBrandSource === "manual";
    const keepManualClearedBrand = currentBrandSource === "manual_cleared";
    const preserveUnloadedCatalogue =
      displayInfo.isValidCatalogue !== true && displayInfo.libraryLoaded === false;
    sanitizedMeta[fabricKey] = {
      ...current,
      fabricCode:
        displayInfo.displayFabricCode ||
        sanitizeFabricTextAgainstSavedBrandPrefix(current.fabricCode || fabricKey, current),
      brandId: keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandId || ""
        : displayInfo.isValidCatalogue
        ? displayInfo.validation?.validBrandId || ""
        : preserveUnloadedCatalogue
        ? current.brandId || ""
        : "",
      brandName: keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandName || ""
        : displayInfo.isValidCatalogue
        ? displayInfo.displayBrandName || ""
        : preserveUnloadedCatalogue
        ? current.brandName || ""
        : "",
      fabricBrandId: keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.fabricBrandId || current.brandId || ""
        : displayInfo.isValidCatalogue
        ? displayInfo.validation?.validBrandId || ""
        : preserveUnloadedCatalogue
        ? current.fabricBrandId || current.brandId || ""
        : "",
      fabricBrandName: keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.fabricBrandName || current.brandName || ""
        : displayInfo.isValidCatalogue
        ? displayInfo.displayBrandName || ""
        : preserveUnloadedCatalogue
        ? current.fabricBrandName || current.brandName || ""
        : "",
      brandSource: keepManualClearedBrand
        ? "manual_cleared"
        : keepManualBrand
        ? "manual"
        : displayInfo.isValidCatalogue || preserveUnloadedCatalogue
        ? current.brandSource || "row_inferred"
        : current.brandSource || "",
      catalogueId: displayInfo.isValidCatalogue
        ? displayInfo.validation?.validCatalogueId || ""
        : preserveUnloadedCatalogue
        ? current.catalogueId || ""
        : "",
      catalogueName: displayInfo.isValidCatalogue
        ? displayInfo.displayCatalogueName || ""
        : preserveUnloadedCatalogue
        ? current.catalogueName || ""
        : "",
      catalogueKey: displayInfo.isValidCatalogue
        ? displayInfo.validation?.validCatalogueKey || ""
        : preserveUnloadedCatalogue
        ? current.catalogueKey || ""
        : "",
      repeatSettings: normalizeCurtainRepeatEntry(
        current.repeatSettings || repeatSettingsMap?.[fabricKey] || {}
      ),
      notes: sanitizeEditableText(current.notes || ""),
    };
  });

  Object.entries(normalizedMeta).forEach(([fabricKey, value]) => {
    if (sanitizedMeta[fabricKey]) return;
    const repeatSettingsEntry = normalizeCurtainRepeatEntry(value.repeatSettings || {});
    const notes = sanitizeEditableText(value.notes || "");
    if (!hasMeaningfulCurtainRepeatEntry(repeatSettingsEntry) && !notes) return;
    sanitizedMeta[fabricKey] = {
      ...value,
      fabricCode: sanitizeFabricTextAgainstSavedBrandPrefix(
        value.fabricCode || fabricKey,
        value
      ),
      brandId: "",
      brandName: "",
      fabricBrandId: "",
      fabricBrandName: "",
      catalogueId: "",
      catalogueName: "",
      catalogueKey: "",
      repeatSettings: repeatSettingsEntry,
      notes,
    };
  });

  return buildFabricSummaryMetaWithRowInferences(
    sanitizedMeta,
    repeatSettingsMap,
    sourceRows,
    { traceLabel: "sanitize" }
  );
}

const handleCurtainRowMrpBlur = async (rowId, row = {}) => {
  const newRrp = parseMoneyInput(
    getFirstNonBlankValue(row.mrp, row.mrpPerMeter, row.rrpPerMeter),
    null
  );
  if (!(newRrp >= 0)) return;
  const wasSettingsPriced =
    row.rrpSource === "sr_price_rule" ||
    row.rrpSource === "catalogue_default" ||
    row.fabricSource === "settings_catalogue";
  patchCurtainRowFields(rowId, {
    mrp: String(newRrp),
    mrpPerMeter: String(newRrp),
    rrpPerMeter: newRrp,
    rrpSource: "manual_quote",
    fabricSource: row.fabricSource === "settings_catalogue" ? row.fabricSource : "quote_manual",
  });
  if (wasSettingsPriced) {
    notify(
      "RRP changed for this quotation only. Update catalogue pricing from Settings.",
      "info"
    );
  }
};

const updateCurtainRowFabricWidthManual = (rowId, value = "") => {
  const widthInputValue = String(value ?? "");
  if (widthInputValue === "") {
    patchCurtainRowFields(rowId, {
      fabricWidthIn: "",
      fabricWidthSource: "",
      fabricWidthManuallyOverridden: false,
    });
    return;
  }
  if (widthInputValue === "-" || widthInputValue === "." || widthInputValue === "-.") return;
  if (!isPermittedDecimalInput(widthInputValue)) return;
  if (!normalizeFabricWidthIn(widthInputValue)) return;
  patchCurtainRowFields(rowId, {
    fabricWidthIn: widthInputValue,
    fabricWidthSource: "manual_override",
    fabricWidthManuallyOverridden: true,
  });
};

const getFabricWidthDisplayLabel = (value) => {
  const fabricWidth = normalizeFabricWidthIn(value);
  return fabricWidth ? `${fabricWidth} in` : "";
};

const getFabricCatalogueWidthInfo = (
  catalogue = {},
  rule = null,
  resolution = {}
) => {
  const resolvedWidth = normalizeFabricWidthIn(resolution.fabricWidthIn);
  if (resolvedWidth) {
    return {
      fabricWidthIn: resolvedWidth,
      fabricWidthSource: resolution.fabricWidthSource || "catalogue_rule",
      fabricWidthManuallyOverridden: false,
      catalogueDefaultFabricWidthIn:
        resolution.catalogueDefaultFabricWidthIn ||
        normalizeFabricWidthIn(
          catalogue.catalogueDefaultFabricWidthIn ||
            catalogue.defaultFabricWidthIn
        ) ||
        null,
      srRuleFabricWidthIn:
        resolution.srRuleFabricWidthIn ||
        normalizeFabricWidthIn(rule?.fabricWidthIn) ||
        null,
    };
  }

  const srRuleFabricWidthIn = normalizeFabricWidthIn(rule?.fabricWidthIn);
  if (srRuleFabricWidthIn) {
    return {
      fabricWidthIn: srRuleFabricWidthIn,
      fabricWidthSource: "catalogue_rule",
      fabricWidthManuallyOverridden: false,
      catalogueDefaultFabricWidthIn:
        normalizeFabricWidthIn(
          catalogue.catalogueDefaultFabricWidthIn ||
            catalogue.defaultFabricWidthIn
        ) || null,
      srRuleFabricWidthIn,
    };
  }

  const catalogueDefaultFabricWidthIn =
    normalizeFabricWidthIn(
      catalogue.catalogueDefaultFabricWidthIn || catalogue.defaultFabricWidthIn
    ) || null;
  if (catalogueDefaultFabricWidthIn) {
    return {
      fabricWidthIn: catalogueDefaultFabricWidthIn,
      fabricWidthSource: "catalogue_default",
      fabricWidthManuallyOverridden: false,
      catalogueDefaultFabricWidthIn,
      srRuleFabricWidthIn: null,
    };
  }

  return {
    fabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
    fabricWidthSource: "workspace_default",
    fabricWidthManuallyOverridden: false,
    catalogueDefaultFabricWidthIn: null,
    srRuleFabricWidthIn: null,
  };
};

const normalizeFabricSuggestionText = (value = "") =>
  normalizeCatalogueKey(value).replace(/-/g, " ");

const getFabricCatalogueLibrariesForSuggestions = (preferredBrandId = "") => {
  const preferredId = sanitizeEditableText(preferredBrandId);
  const brands = (fabricBrandsRef.current.length ? fabricBrandsRef.current : fabricBrands)
    .filter((brand) => brand?.id);
  const orderedBrands = [
    ...brands.filter((brand) => brand.id === preferredId),
    ...brands.filter((brand) => brand.id !== preferredId),
  ];

  return orderedBrands
    .map((brand) => ({
      brand,
      library: getFabricCatalogueLibraryForBrand(brand.id),
      preferred: preferredId && brand.id === preferredId,
    }))
    .filter(({ library }) => Array.isArray(library.catalogues) && library.catalogues.length);
};

const getFabricSuggestionRuleLabel = (resolution = {}, fallback = "Catalogue") => {
  const ruleType = resolution.rule?.ruleType || resolution.srPriceRuleType || "";
  if (ruleType === "single") return "Exact SR";
  if (ruleType === "list") return "SR group";
  if (ruleType === "range") return "Range rule";
  if (resolution.rrpSource === "catalogue_default") return "Default RRP";
  if (resolution.rrpSource === "manual_override" || resolution.rrpSource === "manual_quote") {
    return "Quote-only RRP";
  }
  return fallback;
};

const getFabricCatalogueSuggestionOptionsForRow = (row = {}) => {
  const preferredBrandId = row.brandId || row.fabricBrandId || "";
  const libraries = getFabricCatalogueLibrariesForSuggestions(preferredBrandId);
  const rawQuery = sanitizeEditableText(row.fabricCodeRaw || getEditableFabricValue(row));
  const currentQuery = normalizeFabricSuggestionText(rawQuery);
  const suggestions = [];
  const seen = new Set();
  const addSuggestion = (option = {}) => {
    const value = sanitizeEditableText(option.value || option.commitValue || "");
    if (!value) return;
    const searchKey = normalizeFabricSuggestionText(
      [
        value,
        option.commitValue,
        option.title,
        option.subtitle,
        option.brandName,
        option.catalogueName,
        option.srLabel,
        option.previewRuleLabel,
      ]
        .filter(Boolean)
        .join(" ")
    );
    const dedupeKey = [
      option.brandId || "",
      option.catalogueId || option.catalogueKey || "",
      option.ruleId || option.previewRuleId || option.srLabel || "catalogue",
      normalizeCatalogueKey(option.commitValue || value),
    ].join(":");
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    suggestions.push({
      ...option,
      id: option.id || dedupeKey,
      value,
      commitValue: option.commitValue || value,
      searchKey,
    });
  };

  libraries.forEach(({ brand, library, preferred }, brandIndex) => {
    const parsed = parseFabricCatalogueInput(rawQuery, library.catalogues || []);
    (library.catalogues || []).forEach((catalogue) => {
      if (!catalogue.active) return;
      const catalogueNameSearch = normalizeFabricSuggestionText(catalogue.catalogueName);
      const baseSearchKey = normalizeFabricSuggestionText(
        [
          catalogue.catalogueName,
          catalogue.catalogueKey,
          brand.name,
          library.brandName,
        ]
          .filter(Boolean)
          .join(" ")
      );
      const parsedMatchesCatalogue =
        parsed.catalogueKey && parsed.catalogueKey === catalogue.catalogueKey;
      const queryMatchesCatalogue =
        !currentQuery ||
        parsedMatchesCatalogue ||
        baseSearchKey.includes(currentQuery) ||
        (catalogueNameSearch && currentQuery.includes(catalogueNameSearch));
      if (!queryMatchesCatalogue) return;

      const commitValue =
        parsedMatchesCatalogue && parsed.srLabel
          ? buildFabricCatalogueDisplayCode({
              catalogueName: catalogue.catalogueName,
              srNumber: parsed.srNumber,
            })
          : catalogue.catalogueName;
      const resolution = resolveFabricCatalogueRrp({
        brand,
        library,
        fabricText: commitValue,
        existingMrp: row.mrp,
        workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
      });
      const widthInfo = getFabricCatalogueWidthInfo(
        catalogue,
        resolution.rule,
        resolution
      );
      const ruleLabel = resolution.rule
        ? getSrPriceRuleLabel(resolution.rule)
        : parsedMatchesCatalogue
        ? parsed.srLabel
        : "";
      addSuggestion({
        id: `catalogue-${brand.id}-${catalogue.id}-${parsed.srNumber || "default"}`,
        value: catalogue.catalogueName,
        commitValue,
        title: commitValue,
        subtitle: ruleLabel
          ? `${brand.name} - pricing: ${ruleLabel} - ${getFabricSuggestionRuleLabel(resolution, "Catalogue")}`
          : `${brand.name} - ${getFabricSuggestionRuleLabel(resolution, "Catalogue")}`,
        brandId: brand.id,
        brandName: brand.name,
        catalogueId: catalogue.id,
        catalogueName: catalogue.catalogueName,
        catalogueKey: catalogue.catalogueKey,
        srNumber: parsedMatchesCatalogue ? parsed.srNumber : "",
        srLabel: parsedMatchesCatalogue ? parsed.srLabel : "",
        ruleId: resolution.rule?.id || "",
        ruleType:
          resolution.rule?.ruleType ||
          (resolution.rrpSource === "catalogue_default" ? "default" : "none"),
        price: resolution.matched ? resolution.rrpPerMeter : catalogue.defaultRrpPerMeter,
        priceUnit: catalogue.priceUnit || "m",
        ...widthInfo,
        source: resolution.rrpSource || "catalogue",
        priority: (preferred ? 0 : 10) + (parsedMatchesCatalogue ? 0 : 2) + brandIndex,
      });

      const rules = library.rulesByCatalogueId?.[catalogue.id] || [];
      rules.slice(0, 40).forEach((rule) => {
        if (!rule.active || rule.ruleType === "default") return;
        const priceRuleLabel = getSrPriceRuleLabel(rule);
        const isExactSrRule = rule.ruleType === "single";
        const ruleSrNumber = isExactSrRule ? normalizeSrNumber(rule.srNumber) : "";
        const commitValue = isExactSrRule
          ? buildFabricCatalogueDisplayCode({
              catalogueName: catalogue.catalogueName,
              srNumber: ruleSrNumber,
            })
          : catalogue.catalogueName;
        const value = isExactSrRule
          ? commitValue
          : `${catalogue.catalogueName} pricing ${priceRuleLabel}`;
        const ruleSearchKey = normalizeFabricSuggestionText(
          [
            value,
            catalogue.catalogueName,
            catalogue.catalogueKey,
            priceRuleLabel,
            brand.name,
          ]
            .filter(Boolean)
            .join(" ")
        );
        if (
          currentQuery &&
          !parsedMatchesCatalogue &&
          !ruleSearchKey.includes(currentQuery) &&
          !(catalogueNameSearch && currentQuery.includes(catalogueNameSearch))
        ) {
          return;
        }
        const parsedRuleInput = parseFabricCatalogueInput(value, library.catalogues || []);
        const ruleResolution = resolveFabricCatalogueRrp({
          brand,
          library,
          fabricText: commitValue,
          existingMrp: row.mrp,
          workspaceDefaultFabricWidthIn: getWorkspaceDefaultFabricWidthIn(),
        });
        const ruleWidthInfo = getFabricCatalogueWidthInfo(
          catalogue,
          rule,
          ruleResolution
        );
        addSuggestion({
          id: `rule-${brand.id}-${catalogue.id}-${rule.id}`,
          value,
          commitValue,
          title: isExactSrRule ? commitValue : catalogue.catalogueName,
          subtitle: `${brand.name} - pricing: ${priceRuleLabel} - ${getFabricSuggestionRuleLabel(
            { rule, rrpSource: "sr_price_rule" },
            "SR price"
          )}`,
          brandId: brand.id,
          brandName: brand.name,
          catalogueId: catalogue.id,
          catalogueName: catalogue.catalogueName,
          catalogueKey: catalogue.catalogueKey,
          srNumber: isExactSrRule ? ruleSrNumber || parsedRuleInput.srNumber || "" : "",
          srLabel: isExactSrRule
            ? srDisplayLabel(ruleSrNumber || parsedRuleInput.srNumber || "")
            : "",
          ruleId: isExactSrRule ? rule.id : "",
          ruleType: isExactSrRule ? rule.ruleType : "none",
          previewRuleId: rule.id,
          previewRuleType: rule.ruleType,
          previewRuleLabel: priceRuleLabel,
          requiresSrNumber: !isExactSrRule,
          price: rule.rrpPerMeter,
          priceUnit: catalogue.priceUnit || "m",
          ...ruleWidthInfo,
          source: ruleResolution.rrpSource || "sr_price_rule",
          priority: (preferred ? 1 : 12) + brandIndex,
        });
      });
    });
  });

  return suggestions
    .sort((left, right) => {
      const priorityDiff = Number(left.priority || 0) - Number(right.priority || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return String(left.title || left.value).localeCompare(String(right.title || right.value));
    })
    .slice(0, 12);
};

const updateFabricSuggestionPosition = (element) => {
  if (!element || typeof element.getBoundingClientRect !== "function") return;
  const rect = element.getBoundingClientRect();
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight || 720 : 720;
  setFabricSuggestionPosition({
    left: Math.max(12, rect.left),
    top: rect.bottom + 6,
    width: Math.max(250, rect.width),
    maxHeight: Math.max(150, Math.min(280, viewportHeight - rect.bottom - 18)),
  });
};

const openFabricCatalogueSuggestions = (cellKey, brandId = "", element = null) => {
  setActiveFabricSuggestionCell(cellKey);
  setFabricSuggestionKeyboardIndex(-1);
  updateFabricSuggestionPosition(element);
  if (brandId) loadFabricCatalogueLibrary(brandId);
  loadAllFabricCatalogueLibraries();
};

const closeFabricCatalogueSuggestions = () => {
  setActiveFabricSuggestionCell("");
  setFabricSuggestionKeyboardIndex(-1);
  setFabricSuggestionPosition(null);
};

const renderFabricCatalogueCombobox = ({
  row = {},
  rowId = "",
  rowIndex = 0,
  label = "Fabric / Code",
  disabled = false,
  compact = false,
} = {}) => {
  const brandId = row.brandId || row.fabricBrandId || "";
  const cellKey = `${compact ? "compact" : "table"}:${rowId}`;
  const isOpen = activeFabricSuggestionCell === cellKey && !disabled;
  const suggestions = getFabricCatalogueSuggestionOptionsForRow(row);
  const highlightedIndex = Math.min(
    Math.max(fabricSuggestionKeyboardIndex, -1),
    Math.max(suggestions.length - 1, 0)
  );
  const listboxId = `fabric-catalogue-combobox-${cellKey.replace(
    /[^a-zA-Z0-9_-]/g,
    "-"
  )}`;
  const fieldHeight = compact ? 34 : 36;
  const compactFieldStyle = {
    ...input,
    minHeight: fieldHeight,
    height: fieldHeight,
    padding: "6px 8px",
    fontSize: 13,
    borderRadius: 9,
  };
  const brandSelectStyle = {
    ...selectInput,
    minHeight: 30,
    height: 30,
    padding: "5px 8px",
    fontSize: 12,
    borderRadius: 9,
  };
  const listPosition = fabricSuggestionPosition || {};
  const specificSrNumber = getSpecificRowSrNumber(row);
  const catalogueInputValue =
    row.catalogueName || specificSrNumber
      ? String(row.catalogueName ?? "")
      : String(row.fabricCodeRaw ?? getEditableFabricValue(row));
  const getCatalogueInputCommitValue = (value = "") => {
    const catalogueName = sanitizeEditableText(value);
    if (!catalogueName) return "";
    if (/\bS\.?\s*R\.?\b/i.test(catalogueName)) return catalogueName;
    return specificSrNumber
      ? buildFabricCatalogueDisplayCode({
          catalogueName,
          srNumber: specificSrNumber,
        })
      : catalogueName;
  };

  const applySuggestion = (suggestion) => {
    const selectedBrand = getFabricBrandById(suggestion?.brandId || "");
    const commitValue = sanitizeEditableText(
      suggestion?.commitValue || suggestion?.value || ""
    );
    if (!commitValue) return;

    if (selectedBrand?.id) {
      const nextRow = {
        ...row,
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        fabricBrandId: selectedBrand.id,
        fabricBrandName: selectedBrand.name,
      };
      const resolutionPatch = suggestion?.requiresSrNumber
        ? {
            fabric: commitValue,
            fabricName: commitValue,
            fabricCode: commitValue,
            fabricCodeRaw: commitValue,
            catalogueId: suggestion.catalogueId || "",
            catalogueName: suggestion.catalogueName || commitValue,
            catalogueKey:
              suggestion.catalogueKey ||
              normalizeCatalogueKey(suggestion.catalogueName || commitValue),
            srNumber: "",
            srLabel: "",
            srPriceRuleId: "",
            srPriceRuleType: "none",
            srPriceRuleLabel: "",
            ...buildCatalogueRatePatch({ ...row, productConfigSnapshot: getRowProductConfig(row) }, {
              priceUnit: suggestion.priceUnit || "m", matched: false,
              rrpSource: "needs_sr_number", catalogue: { id: suggestion.catalogueId },
            }),
            fabricWidthIn:
              suggestion.fabricWidthIn || getWorkspaceDefaultFabricWidthIn(),
            fabricWidthSource:
              suggestion.fabricWidthSource || "workspace_default",
            fabricWidthManuallyOverridden: false,
            catalogueDefaultFabricWidthIn:
              suggestion.catalogueDefaultFabricWidthIn || null,
            srRuleFabricWidthIn: suggestion.srRuleFabricWidthIn || null,
          }
        : buildFabricCatalogueResolutionPatch(nextRow, {
            brandId: selectedBrand.id,
            fabricText: commitValue,
            includeResolvedMrp: true,
            forceResolvedFabricWidth: true,
          });
      const patch = {
        fabric: commitValue,
        fabricName: commitValue,
        fabricCode: commitValue,
        fabricCodeRaw: commitValue,
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        fabricBrandId: selectedBrand.id,
        fabricBrandName: selectedBrand.name,
        brandSource: "catalogue_selection",
        ...resolutionPatch,
      };
      patchCurtainRowFields(rowId, patch);
      syncFabricSummaryBrandFromRows(rowId, patch);
      if (suggestion?.requiresSrNumber && typeof window !== "undefined") {
        window.setTimeout(() => {
          fabricSrInputRefs.current[rowId]?.focus?.();
        }, 0);
      }
    } else {
      handleCurtainFabricInputChange(rowId, row, commitValue);
    }

    closeFabricCatalogueSuggestions();
  };

  const handleFabricKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget);
        return;
      }
      setFabricSuggestionKeyboardIndex((current) =>
        suggestions.length ? Math.min(current + 1, suggestions.length - 1) : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFabricSuggestionKeyboardIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" && isOpen && suggestions[highlightedIndex]) {
      event.preventDefault();
      applySuggestion(suggestions[highlightedIndex]);
    }
  };

  return (
    <div
      data-fabric-combobox-root="true"
      style={{ display: "grid", gap: compact ? 5 : 6, minWidth: 0 }}
    >
      <select
        style={brandSelectStyle}
        value={brandId}
        onChange={(event) => {
          closeFabricCatalogueSuggestions();
          handleCurtainRowBrandChange(rowId, row, event.target.value);
        }}
        disabled={disabled}
        aria-label={`Select fabric brand for ${row.item || `row ${rowIndex + 1}`}`}
        title="Fabric brand"
      >
        <option value="">No brand</option>
        {fabricBrands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </select>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "minmax(0, 1fr) 64px" : "minmax(0, 1fr) 72px",
          gap: 6,
          alignItems: "start",
          minWidth: 0,
        }}
      >
      <div style={{ position: "relative", minWidth: 0 }}>
        <FabricTextInput
          style={{
            ...compactFieldStyle,
            paddingRight:
              catalogueInputValue || brandId || row.catalogueId || row.catalogueName
                ? 34
                : compactFieldStyle.paddingRight || compactFieldStyle.padding,
          }}
          title={label}
          placeholder={row.catalogueName ? "Catalogue / Fabric" : label || "Fabric / Code"}
          value={catalogueInputValue}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          onFocus={(event) =>
            openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget)
          }
          onChange={(event) => {
            openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget);
            const value = event.target.value;
            patchCurtainRowFields(rowId, fresh => buildCatalogueInputDraftPatch(fresh, value));
          }}
          onKeyDown={handleFabricKeyDown}
          onCommitError={(error) => { devWarn("FABRIC_TEXT_COMMIT_FAILED", { code: error?.code, message: error?.message }); notify("Could not resolve fabric settings. Your typed text is preserved.", "warning"); }}
          onCommit={(value) => {
            const latest = (rowsRef.current || []).find(candidate => getWorkshopRowStableId(candidate) === rowId) || row;
            const text = getCatalogueInputCommitValue(value);
            return handleCurtainFabricInputBlur(rowId, { ...latest, fabric: text, fabricName: text, fabricCode: text, fabricCodeRaw: text });
          }}
          disabled={disabled}
        />

        {!disabled && (catalogueInputValue || brandId || row.catalogueId || row.catalogueName) ? (
          <button
            type="button"
            title="Clear fabric catalogue"
            aria-label="Clear fabric catalogue"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              closeFabricCatalogueSuggestions();
              const clearPatch = clearCatalogueSelection(row);
              patchCurtainRowFields(rowId, clearPatch);
              syncFabricSummaryBrandFromRows(rowId, clearPatch);
            }}
            style={{
              position: "absolute",
              right: 5,
              top: "50%",
              transform: "translateY(-50%)",
              width: 24,
              height: 24,
              borderRadius: 999,
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              background: isDark ? "rgba(148,163,184,0.22)" : "#e2e8f0",
              color: isDark ? "#f8fafc" : "#0f172a",
              fontSize: 15,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            x
          </button>
        ) : null}

        {isOpen ? (
          <div
            id={listboxId}
            role="listbox"
            style={{
              position: "fixed",
              left: listPosition.left || 0,
              top: listPosition.top || 0,
              width: listPosition.width || 260,
              maxHeight: listPosition.maxHeight || 260,
              overflowY: "auto",
              zIndex: 9000,
              padding: 6,
              borderRadius: 14,
              border: isDark
                ? "1px solid rgba(148,163,184,0.24)"
                : "1px solid rgba(203,213,225,0.95)",
              background: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark
                ? "0 22px 50px rgba(2,6,23,0.45)"
                : "0 22px 45px rgba(15,23,42,0.16)",
            }}
          >
            {suggestions.length ? (
              suggestions.map((suggestion, suggestionIndex) => {
                const isHighlighted = suggestionIndex === highlightedIndex;
                const suggestionWidthLabel = getFabricWidthDisplayLabel(
                  suggestion.fabricWidthIn
                );
                return (
                  <button
                    key={suggestion.id || suggestion.value}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    onMouseEnter={() =>
                      setFabricSuggestionKeyboardIndex(suggestionIndex)
                    }
                    onMouseDown={(event) => {
                      event.preventDefault();
                      applySuggestion(suggestion);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 9px",
                      display: "grid",
                      gap: 4,
                      textAlign: "left",
                      cursor: "pointer",
                      background: isHighlighted
                        ? isDark
                          ? "rgba(37,99,235,0.24)"
                          : "#eff6ff"
                        : "transparent",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 850,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {suggestion.title || suggestion.value}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: 11,
                        color: isDark ? "#cbd5e1" : "#64748b",
                      }}
                    >
                      <span>{suggestion.subtitle || "Catalogue"}</span>
                      <span
                        style={{
                          display: "inline-flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                        }}
                      >
                        {Number(suggestion.price || 0) > 0 ? (
                          <span style={{ fontWeight: 850 }}>
                            {currencySymbol(currency)}
                            {Number(suggestion.price || 0).toFixed(2)}/{suggestion.priceUnit || "m"}
                          </span>
                        ) : null}
                        {suggestionWidthLabel ? (
                          <span style={{ fontWeight: 850 }}>
                            Width {suggestionWidthLabel}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  padding: "10px 9px",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: dashboardMuted,
                }}
              >
                {brandId
                  ? "No saved catalogue match. You can type freely."
                  : fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid
                  ? "Loading saved catalogues..."
                  : "No saved catalogue match. You can type freely."}
              </div>
            )}
          </div>
        ) : null}
      </div>

        <FabricTextInput
          ref={(node) => {
            if (node) {
              fabricSrInputRefs.current[rowId] = node;
            } else {
              delete fabricSrInputRefs.current[rowId];
            }
          }}
          type="text"
          style={{
            ...compactFieldStyle,
            padding: "6px 6px",
            textAlign: "center",
          }}
          title="Specific SR number"
          onCommitError={(error) => { devWarn("FABRIC_SR_COMMIT_FAILED", { code: error?.code, message: error?.message }); notify("Could not resolve SR pricing. Your typed code is preserved.", "warning"); }}
          aria-label={`Specific SR number for ${row.item || `row ${rowIndex + 1}`}`}
          placeholder="SR No"
          value={row.srNumber ?? row.srNo ?? row.sr ?? ""}
          autoComplete="off"
          onChange={(event) => {
            const value = event.target.value;
            patchCurtainRowFields(rowId, fresh => buildSrDraftPatch(fresh, value));
          }}
          onCommit={(value) => {
            const latestRow =
              (rowsRef.current || []).find(
                (candidate) => getWorkshopRowStableId(candidate) === rowId
              ) || row;
            return handleCurtainFabricSrNumberChange(rowId, latestRow, value);
          }}
          disabled={disabled}
        />
      </div>

      {hasUnresolvedLiveFabricPricing([row], isFrozenLegacyCurtainQuote) ? (
        <div role="status" style={{ fontSize: 10.5, color: "#b45309" }}>
          {row.cataloguePricingError || "Catalogue price unresolved. Enter a manual rate or choose a matching SR."}
        </div>
      ) : row.fabricSource === "quote_manual" ||
      row.fabricSource === "custom_unverified" ||
      row.rrpSource === "manual_quote" ||
      row.rrpSource === "quote_manual" ||
      row.rrpSource === "custom_unverified" ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          Quote-only fabric. Catalogue setup is optional.
        </div>
      ) : row.rrpSource && row.rrpSource !== "unmatched" ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          {row.catalogueName || "Catalogue"}
          {row.srNumber ? ` / ${srDisplayLabel(row.srNumber)}` : ""} -{" "}
          {row.rrpSource === "needs_sr_number"
            ? "enter selected SR number"
            : row.rrpSource === "manual_override" || row.rrpSource === "manual_quote"
            ? "quote-only RRP"
            : "saved RRP"}
        </div>
      ) : null}
      {row.brandSource === "catalogue_selection" &&
      row.fabricSource === "settings_catalogue" ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          Brand auto-selected from catalogue.
        </div>
      ) : null}
      {!brandId &&
      getEditableFabricValue(row) &&
      parseMoneyInput(getFirstNonBlankValue(row.mrp, row.mrpPerMeter, row.rrpPerMeter), null) !== null ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          Custom fabric/code. Add official catalogues and RRP from Settings.
        </div>
      ) : null}
    </div>
  );
};

const getCurtainRowLiningForUi = (row = {}, calc = {}) =>
  calculateCurtainRowLining(
    row,
    Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
    ),
    curtainRowSupportsLining(row, calc),
    { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
  );

const updateCurtainRowLining = (rowId, row = {}, calc = {}, patch = {}) => {
  if (!rowId) return;
  const currentLining = getCurtainRowLiningForUi(row, calc);
  const nextLining = normalizeCurtainRowLining(
    {
      ...currentLining,
      ...patch,
    },
    {
      calculatedMeters: Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      sqft: calc.adjSqft ?? calc.billableSqft ?? 0,
      applicable: curtainRowSupportsLining(row, calc),
    }
  );

  updateField(rowId, "lining", nextLining);
};

const getFabricSummaryMetaForKey = useCallback(
  (fabricKey = "") => {
    const normalizedKey = normalizeFabricKey(fabricKey);
    return normalizedKey ? fabricSummaryMeta?.[normalizedKey] || {} : {};
  },
  [fabricSummaryMeta]
);

const getFabricPhotoCacheKey = useCallback(
  (fabricLibraryKey = "") =>
    workspaceUid && fabricLibraryKey ? `${workspaceUid}:${fabricLibraryKey}` : "",
  [workspaceUid]
);

const buildFabricLibraryIdentityForSummary = useCallback(
  (summary = {}) => {
    const normalizedKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || "");
    const summaryMeta = getFabricSummaryMetaForKey(normalizedKey);
    const sourceRowsForFabric = (rowsRef.current || rows || []).filter(
      (sourceRow) =>
        normalizeFabricKey(getCurtainRowFabricKey(sourceRow)) === normalizedKey
    );
    return resolveFabricSummaryIdentityForGroup(
      sourceRowsForFabric,
      {
        ...summary,
        normalizedKey,
      },
      summaryMeta
    );
  },
  [getFabricSummaryMetaForKey, rows]
);

const getCachedFabricLibraryRecord = useCallback(
  (fabricLibraryKey = "") => {
    const cacheKey = getFabricPhotoCacheKey(fabricLibraryKey);
    if (!cacheKey) return null;
    if (!Object.prototype.hasOwnProperty.call(fabricPhotoLibraryCacheRef.current, cacheKey)) {
      return null;
    }
    return fabricPhotoLibraryCacheRef.current[cacheKey];
  },
  [getFabricPhotoCacheKey]
);

const mergeFabricPhotoIntoSummaryMeta = useCallback(
  ({
    summaryKey = "",
    identity = {},
    primaryPhoto = null,
    photoHistory = [],
  } = {}) => {
    const normalizedSummaryKey = normalizeFabricKey(summaryKey);
    const fabricLibraryKey = sanitizeEditableText(identity.fabricKey || "");
    const normalizedPrimaryPhoto = normalizeFabricPhotoRef(primaryPhoto);
    if (!normalizedSummaryKey || !fabricLibraryKey || !normalizedPrimaryPhoto) return;

    setFabricSummaryMeta((prev) => {
      const normalized = normalizeFabricSummaryMeta(prev);
      const current = normalized[normalizedSummaryKey] || {};
      const keepManualBrand = current.brandSource === "manual";
      const keepManualClearedBrand = current.brandSource === "manual_cleared";
      const resolvedBrandId = keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandId || ""
        : identity.brandId || current.brandId || "";
      const resolvedBrandName = keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandName || ""
        : identity.brandName || current.brandName || "";
      const nextMeta = normalizeFabricSummaryMeta({
        ...normalized,
        [normalizedSummaryKey]: {
          ...current,
          fabricCode: current.fabricCode || identity.fabricDisplayName || identity.fabricCodeRaw || "",
          brandId: resolvedBrandId,
          brandName: resolvedBrandName,
          fabricBrandId: resolvedBrandId,
          fabricBrandName: resolvedBrandName,
          brandSource:
            current.brandSource ||
            (resolvedBrandId || resolvedBrandName ? "row_inferred" : ""),
          catalogueId: identity.catalogueId || current.catalogueId || "",
          catalogueName: identity.catalogueName || current.catalogueName || "",
          catalogueKey: identity.catalogueKey || current.catalogueKey || "",
          srNumber: identity.srNumber || current.srNumber || "",
          fabricLibraryKey,
          primaryPhoto: normalizedPrimaryPhoto,
          photoHistory: buildFabricPhotoHistory(photoHistory),
          repeatSettings: current.repeatSettings || {},
          notes: current.notes || "",
        },
      });
      fabricSummaryMetaRef.current = nextMeta;
      return nextMeta;
    });
  },
  []
);

const clearFabricPhotoFromSummaryMeta = useCallback(
  ({
    summaryKey = "",
    identity = {},
    photoHistory = [],
  } = {}) => {
    const normalizedSummaryKey = normalizeFabricKey(summaryKey);
    if (!normalizedSummaryKey) return;
    const fabricLibraryKey = sanitizeEditableText(identity.fabricKey || "");

    setFabricSummaryMeta((prev) => {
      const normalized = normalizeFabricSummaryMeta(prev);
      const current = normalized[normalizedSummaryKey] || {};
      const keepManualBrand = current.brandSource === "manual";
      const keepManualClearedBrand = current.brandSource === "manual_cleared";
      const resolvedBrandId = keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandId || ""
        : identity.brandId || current.brandId || "";
      const resolvedBrandName = keepManualClearedBrand
        ? ""
        : keepManualBrand
        ? current.brandName || ""
        : identity.brandName || current.brandName || "";
      const nextMeta = normalizeFabricSummaryMeta({
        ...normalized,
        [normalizedSummaryKey]: {
          ...current,
          fabricCode:
            current.fabricCode ||
            identity.fabricDisplayName ||
            identity.fabricCodeRaw ||
            "",
          brandId: resolvedBrandId,
          brandName: resolvedBrandName,
          fabricBrandId: resolvedBrandId,
          fabricBrandName: resolvedBrandName,
          brandSource:
            current.brandSource ||
            (resolvedBrandId || resolvedBrandName ? "row_inferred" : ""),
          catalogueId: identity.catalogueId || current.catalogueId || "",
          catalogueName: identity.catalogueName || current.catalogueName || "",
          catalogueKey: identity.catalogueKey || current.catalogueKey || "",
          srNumber: identity.srNumber || current.srNumber || "",
          fabricLibraryKey: fabricLibraryKey || current.fabricLibraryKey || "",
          primaryPhoto: null,
          photoHistory: buildFabricPhotoHistory(photoHistory),
          repeatSettings: current.repeatSettings || {},
          notes: current.notes || "",
        },
      });
      fabricSummaryMetaRef.current = nextMeta;
      return nextMeta;
    });
  },
  []
);

const loadFabricLibraryPhotoForSummary = useCallback(
  async (summary = {}) => {
    if (!workspaceUid) return null;
    const identity =
      summary.fabricLibraryIdentity || buildFabricLibraryIdentityForSummary(summary);
    const fabricLibraryKey = sanitizeEditableText(
      summary.fabricLibraryKey || identity.fabricKey || ""
    );
    if (!fabricLibraryKey) return null;

    const cacheKey = getFabricPhotoCacheKey(fabricLibraryKey);
    if (!cacheKey) return null;
    if (Object.prototype.hasOwnProperty.call(fabricPhotoLibraryCacheRef.current, cacheKey)) {
      return fabricPhotoLibraryCacheRef.current[cacheKey];
    }
    if (fabricPhotoLibraryLoadingRef.current[cacheKey]) {
      return fabricPhotoLibraryLoadingRef.current[cacheKey];
    }

    const summaryKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || "");
    const loadPromise = (async () => {
      try {
        const fabricRef = doc(
          db,
          "users",
          workspaceUid,
          FABRIC_LIBRARY_COLLECTION,
          fabricLibraryKey
        );
        const snap = await getDocCached(fabricRef, {
          ttlMs: 5 * 60 * 1000,
          cacheKey: `fabricLibrary:${workspaceUid}:${fabricLibraryKey}`,
          label: "fabricLibrary.photoByKey",
          callerLabel: "Fabric Summary photo autoload",
          component: "QuotationApp",
        });
        const record = snap.exists()
          ? {
              id: snap.id,
              ...snap.data(),
              primaryPhoto: normalizeFabricPhotoRef(snap.data()?.primaryPhoto),
              photoHistory: (Array.isArray(snap.data()?.photoHistory)
                ? snap.data().photoHistory
                : []
              )
                .map((photo) => normalizeFabricPhotoRef(photo))
                .filter(Boolean)
                .slice(0, FABRIC_PHOTO_HISTORY_LIMIT),
            }
          : null;
        fabricPhotoLibraryCacheRef.current[cacheKey] = record;
        if (record?.primaryPhoto && summaryKey) {
          mergeFabricPhotoIntoSummaryMeta({
            summaryKey,
            identity: { ...identity, fabricKey: fabricLibraryKey },
            primaryPhoto: record.primaryPhoto,
            photoHistory: record.photoHistory || [],
          });
          setFabricPhotoLibraryRevision((revision) => revision + 1);
        }
        return record;
      } catch (error) {
        if (IS_DEV_UI_ENABLED) {
          devError("FABRIC_LIBRARY_PHOTO_LOAD_FAILED", {
            code: error?.code,
            message: error?.message,
            workspaceUid,
            fabricLibraryKey,
          });
        }
        return null;
      } finally {
        delete fabricPhotoLibraryLoadingRef.current[cacheKey];
      }
    })();

    fabricPhotoLibraryLoadingRef.current[cacheKey] = loadPromise;
    return loadPromise;
  },
  [
    buildFabricLibraryIdentityForSummary,
    getFabricPhotoCacheKey,
    mergeFabricPhotoIntoSummaryMeta,
    workspaceUid,
  ]
);

const resolveFabricSummaryPhotoForPdf = useCallback(
  async (summary = {}) => {
    const existingPhoto = normalizeFabricPhotoRef(summary.primaryPhoto);
    if (existingPhoto) return existingPhoto;
    const libraryRecord = await loadFabricLibraryPhotoForSummary(summary);
    return normalizeFabricPhotoRef(libraryRecord?.primaryPhoto);
  },
  [loadFabricLibraryPhotoForSummary]
);

const handleFabricPhotoFileSelected = useCallback(
  async (summary = {}, file = null) => {
    if (!file) return;
    if (!workspaceUid || !auth.currentUser?.uid) {
      notify("Could not upload fabric photo because workspace is not ready.", "error");
      if (IS_DEV_UI_ENABLED) {
        devError("FABRIC_PHOTO_UPLOAD_BLOCKED", {
          workspaceUid,
          uid: auth.currentUser?.uid || "",
          reason: "missing_workspace_or_auth",
        });
      }
      return;
    }

    const identity = buildFabricLibraryIdentityForSummary(summary);
    const fabricLibraryKey = sanitizeEditableText(identity.fabricKey || "");
    const summaryKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || "");
    if (!fabricLibraryKey || !summaryKey) {
      notify("Select a fabric or SR before uploading a fabric photo.", "warning");
      return;
    }

    const imageId = createClientId("fabric-photo");
    const uploadFile =
      typeof File === "function"
        ? new File([file], `${imageId}-${file.name || "fabric-photo.jpg"}`, {
            type: file.type,
            lastModified: file.lastModified || Date.now(),
          })
        : file;
    const storagePath = buildFabricPhotoStoragePath({
      workspaceUid,
      fabricKey: fabricLibraryKey,
      imageId,
      fileName: uploadFile.name || file.name || "fabric-photo",
    });
    const cacheKey = getFabricPhotoCacheKey(fabricLibraryKey);
    setFabricPhotoUploadingKey(fabricLibraryKey);

    try {
      if (IS_DEV_UI_ENABLED) {
        devLog("FABRIC_PHOTO_UPLOAD_START", {
          workspaceUid,
          uid: auth.currentUser?.uid,
          fabricLibraryKey,
          summaryKey,
          storagePath,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });
      }

      const existingRecord =
        getCachedFabricLibraryRecord(fabricLibraryKey) ||
        (await loadFabricLibraryPhotoForSummary({
          ...summary,
          fabricLibraryKey,
          fabricLibraryIdentity: identity,
        }));
      const previousPrimaryPhoto = normalizeFabricPhotoRef(existingRecord?.primaryPhoto);
      const managedImage = await uploadManagedImage({
        workspaceUid,
        file: uploadFile,
        ownerType: "fabricLibrary",
        ownerId: fabricLibraryKey,
        imageId,
        fullPath: storagePath,
        uploadedBy: auth.currentUser.uid,
        metadata: {
          fabricKey: fabricLibraryKey,
          brandId: identity.brandId || "",
          catalogueId: identity.catalogueId || "",
          srNumber: identity.srNumber || "",
        },
        preserveTransparency: false,
        fullMaxLongEdge: 1400,
        thumbnailMaxLongEdge: 420,
      });
      const primaryPhoto = normalizeFabricPhotoRef({
        storagePath: managedImage.fullPath,
        downloadUrl: managedImage.downloadURL || managedImage.url,
        thumbStoragePath: managedImage.thumbnailFullPath,
        thumbDownloadUrl:
          managedImage.thumbnailDownloadURL || managedImage.previewUrl || "",
        uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
        uploadedBy: auth.currentUser.uid,
        fileName: managedImage.originalFilename || file.name || managedImage.filename,
        contentType: managedImage.contentType || uploadFile.type,
        width: managedImage.width,
        height: managedImage.height,
        imageId,
        fileId: managedImage.fileId,
      });
      const previousHistory = Array.isArray(existingRecord?.photoHistory)
        ? existingRecord.photoHistory
        : [];
      const photoHistory = buildFabricPhotoHistory([
        previousPrimaryPhoto,
        ...previousHistory,
      ]);

      const fabricRef = doc(
        db,
        "users",
        workspaceUid,
        FABRIC_LIBRARY_COLLECTION,
        fabricLibraryKey
      );
      const savedRecord = {
        id: fabricLibraryKey,
        fabricKey: fabricLibraryKey,
        ...identity,
        primaryPhoto,
        photoHistory,
        photoCount: Number(existingRecord?.photoCount || 0) + 1,
        createdAt: existingRecord?.createdAt || serverTimestamp(),
        createdBy: existingRecord?.createdBy || auth.currentUser.uid,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser.uid,
      };
      await setDoc(fabricRef, savedRecord, { merge: true });
      invalidateFirestoreReadCache(`fabricLibrary:${workspaceUid}:${fabricLibraryKey}`);
      fabricPhotoLibraryCacheRef.current[cacheKey] = {
        ...savedRecord,
        updatedAt: new Date().toISOString(),
      };
      mergeFabricPhotoIntoSummaryMeta({
        summaryKey,
        identity: { ...identity, fabricKey: fabricLibraryKey },
        primaryPhoto,
        photoHistory,
      });
      setFabricPhotoLibraryRevision((revision) => revision + 1);

      if (IS_DEV_UI_ENABLED) {
        devLog("FABRIC_PHOTO_UPLOAD_SUCCESS", {
          workspaceUid,
          fabricLibraryKey,
          storagePath: primaryPhoto.storagePath,
          thumbStoragePath: primaryPhoto.thumbStoragePath,
        });
      }
      notify("Fabric photo saved to library.", "success");
    } catch (error) {
      if (IS_DEV_UI_ENABLED) {
        devError("FABRIC_PHOTO_UPLOAD_FAILED_EXACT", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          workspaceUid,
          fabricLibraryKey,
          summaryKey,
          storagePath,
          fileName: file?.name,
          fileType: file?.type,
          fileSize: file?.size,
        });
      }
      notify("Could not upload fabric photo. Please try again.", "error");
    } finally {
      setFabricPhotoUploadingKey("");
    }
  },
  [
    buildFabricLibraryIdentityForSummary,
    getCachedFabricLibraryRecord,
    getFabricPhotoCacheKey,
    loadFabricLibraryPhotoForSummary,
    mergeFabricPhotoIntoSummaryMeta,
    notify,
    workspaceUid,
  ]
);

const applyFabricPhotoQrUploadToLocalState = useCallback(
  ({
    summaryKey = "",
    fabricLibraryKey = "",
    identity = {},
    uploadedPhoto = null,
    photoHistory = [],
    fabricRecord = {},
  } = {}) => {
    const normalizedSummaryKey = normalizeFabricKey(summaryKey);
    const resolvedFabricLibraryKey = sanitizeEditableText(
      fabricLibraryKey || identity.fabricKey || fabricRecord.fabricKey || fabricRecord.id || ""
    );
    const primaryPhoto = normalizeFabricPhotoRef(
      uploadedPhoto || fabricRecord.primaryPhoto || null
    );
    if (!normalizedSummaryKey || !resolvedFabricLibraryKey || !primaryPhoto) return false;

    const localCacheKey = getFabricPhotoCacheKey(resolvedFabricLibraryKey);
    const incomingPhotoHistory = Array.isArray(photoHistory) ? photoHistory : [];
    const normalizedHistory = buildFabricPhotoHistory(
      incomingPhotoHistory.length ? incomingPhotoHistory : fabricRecord.photoHistory || []
    );
    const record = {
      ...(fabricPhotoLibraryCacheRef.current[localCacheKey] || {}),
      ...fabricRecord,
      id: resolvedFabricLibraryKey,
      fabricKey: resolvedFabricLibraryKey,
      ...identity,
      primaryPhoto,
      photoHistory: normalizedHistory,
      updatedAt: new Date().toISOString(),
    };
    fabricPhotoLibraryCacheRef.current[localCacheKey] = record;
    invalidateFirestoreReadCache(`fabricLibrary:${workspaceUid}:${resolvedFabricLibraryKey}`);
    mergeFabricPhotoIntoSummaryMeta({
      summaryKey: normalizedSummaryKey,
      identity: { ...identity, fabricKey: resolvedFabricLibraryKey },
      primaryPhoto,
      photoHistory: normalizedHistory,
    });
    setFabricPhotoLibraryRevision((revision) => revision + 1);
    return true;
  },
  [getFabricPhotoCacheKey, mergeFabricPhotoIntoSummaryMeta, workspaceUid]
);

const openFabricPhotoQrUpload = useCallback(
  async (summary = {}) => {
    if (!workspaceUid || !auth.currentUser?.uid) {
      notify("Could not create phone upload link because workspace is not ready.", "error");
      if (IS_DEV_UI_ENABLED) {
        devError("FABRIC_PHOTO_QR_SESSION_FAILED", {
          reason: "missing_workspace_or_auth",
          workspaceUid,
          uid: auth.currentUser?.uid || "",
        });
      }
      return;
    }

    const identity = buildFabricLibraryIdentityForSummary(summary);
    const fabricLibraryKey = sanitizeEditableText(identity.fabricKey || "");
    const summaryKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || "");
    if (!fabricLibraryKey || !summaryKey) {
      notify("Select a fabric brand, catalogue, and SR before uploading from phone.", "warning");
      return;
    }

    handledFabricPhotoQrSessionRef.current.delete(fabricLibraryKey);
    setFabricPhotoQrCreatingKey(fabricLibraryKey);
    setFabricPhotoQrModal({
      ...FABRIC_PHOTO_QR_MODAL_INITIAL,
      open: true,
      loading: true,
      status: "creating",
      summaryKey,
      fabricLibraryKey,
      fabricDisplayName: identity.fabricDisplayName || summary.displayName || "Selected fabric",
      identity: { ...identity, fabricKey: fabricLibraryKey },
    });

    try {
      const createSession = httpsCallable(functions, "createFabricPhotoUploadSession");
      const result = await createSession({
        workspaceUid,
        quoteId: editingQuoteId || selectedQuote?.id || quoteNo || "",
        quoteNo: quoteNo || selectedQuote?.quoteNo || "",
        fabricKey: fabricLibraryKey,
        fabricIdentity: identity,
        appOrigin:
          typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : "",
      });
      const data = result?.data || {};
      setFabricPhotoQrModal({
        ...FABRIC_PHOTO_QR_MODAL_INITIAL,
        open: true,
        loading: false,
        status: "pending",
        error: "",
        sessionId: data.sessionId || "",
        shortCode: data.shortCode || "",
        uploadUrl: data.uploadUrl || data.shortUploadUrl || "",
        legacyUploadUrl: data.legacyUploadUrl || "",
        expiresAt: data.expiresAt || "",
        storagePathPrefix: data.storagePathPrefix || "",
        summaryKey,
        fabricLibraryKey,
        fabricDisplayName: identity.fabricDisplayName || summary.displayName || "Selected fabric",
        identity: { ...identity, fabricKey: fabricLibraryKey },
      });
      if (IS_DEV_UI_ENABLED) {
        devLog("FABRIC_QR_SESSION_CREATED", {
          workspaceUid,
          uid: auth.currentUser?.uid || "",
          quoteId: editingQuoteId || selectedQuote?.id || quoteNo || "",
          quoteNo: quoteNo || selectedQuote?.quoteNo || "",
          sessionId: data.sessionId || "",
          shortCode: data.shortCode || "",
          uploadUrl: data.uploadUrl || data.shortUploadUrl || "",
          shortUploadUrl: data.shortUploadUrl || data.uploadUrl || "",
          legacyUploadUrl: data.legacyUploadUrl || "",
          storagePathPrefix: data.storagePathPrefix || "",
          fabricLibraryKey,
          summaryKey,
          expiresAt: data.expiresAt || "",
        });
        const qrValue = data.uploadUrl || data.shortUploadUrl || "";
        devLog("FABRIC_QR_URL_TRACE", {
          uploadUrl: qrValue,
          uploadUrlLength: qrValue.length,
          origin:
            typeof window !== "undefined" && window.location?.origin
              ? window.location.origin
              : "",
          sessionId: data.sessionId || "",
          shortCode: data.shortCode || "",
          quoteId: editingQuoteId || selectedQuote?.id || quoteNo || "",
          fabricKey: fabricLibraryKey,
          expiresAt: data.expiresAt || "",
        });
        devLog("FABRIC_QR_RENDER_TRACE", {
          uploadUrl: qrValue,
          uploadUrlLength: qrValue.length,
          qrValue,
          qrValueLength: qrValue.length,
          qrSize: 320,
          qrCompactSize: 280,
          qrMargin: 4,
          errorCorrectionLevel: "H",
          sessionId: data.sessionId || "",
          shortCode: data.shortCode || "",
          hasToken: /[?&](?:t|token)=/.test(qrValue),
          expiresAt: data.expiresAt || "",
        });
      }
    } catch (error) {
      if (IS_DEV_UI_ENABLED) {
        devError("FABRIC_PHOTO_QR_SESSION_FAILED", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          workspaceUid,
          fabricLibraryKey,
          summaryKey,
        });
      }
      setFabricPhotoQrModal((prev) => ({
        ...prev,
        loading: false,
        status: "failed",
        error: error?.message || "Could not create a phone upload link.",
      }));
      notify("Could not create phone upload link. Please try again.", "error");
    } finally {
      setFabricPhotoQrCreatingKey("");
    }
  },
  [
    buildFabricLibraryIdentityForSummary,
    editingQuoteId,
    notify,
    quoteNo,
    selectedQuote?.id,
    selectedQuote?.quoteNo,
    workspaceUid,
  ]
);

const closeFabricPhotoQrModal = useCallback(() => {
  setFabricPhotoQrModal({ ...FABRIC_PHOTO_QR_MODAL_INITIAL });
  setFabricPhotoQrCountdownMs(0);
}, []);

useEffect(() => {
  if (!fabricPhotoQrModal.open || !fabricPhotoQrModal.expiresAt) {
    setFabricPhotoQrCountdownMs(0);
    return undefined;
  }

  const updateCountdown = () => {
    const expiresAt = new Date(fabricPhotoQrModal.expiresAt).getTime();
    const remainingMs = Number.isFinite(expiresAt)
      ? Math.max(0, expiresAt - Date.now())
      : 0;
    setFabricPhotoQrCountdownMs(remainingMs);
    if (remainingMs <= 0) {
      setFabricPhotoQrModal((prev) =>
        prev.open && prev.status === "pending"
          ? {
              ...prev,
              status: "expired",
              error: "This phone upload link has expired.",
            }
          : prev
      );
    }
  };
  updateCountdown();
  const timerId = window.setInterval(updateCountdown, 1000);
  return () => window.clearInterval(timerId);
}, [fabricPhotoQrModal.expiresAt, fabricPhotoQrModal.open]);

useEffect(() => {
  if (
    !fabricPhotoQrModal.open ||
    fabricPhotoQrModal.status !== "pending" ||
    !workspaceUid ||
    !fabricPhotoQrModal.sessionId
  ) {
    return undefined;
  }

  const sessionRef = doc(
    db,
    "users",
    workspaceUid,
    "fabric_photo_upload_sessions",
    fabricPhotoQrModal.sessionId
  );
  const listenerSummaryKey = fabricPhotoQrModal.summaryKey;
  const listenerFabricKey = fabricPhotoQrModal.fabricLibraryKey;
  const listenerIdentity = fabricPhotoQrModal.identity || {};
  const listenerSessionId = fabricPhotoQrModal.sessionId;

  let cancelled = false;
  let inFlight = false;
  const sessionPath = `users/${workspaceUid}/fabric_photo_upload_sessions/${listenerSessionId}`;

  const handleSessionSnap = (snap) => {
      const data = snap.exists() ? snap.data() || {} : {};
      const status = String(data.status || "").toLowerCase();
      if (status === "uploaded" && data.uploadedPhoto) {
        const handledKey = `${listenerSessionId}:uploaded`;
        if (handledFabricPhotoQrSessionRef.current.has(handledKey)) return;
        handledFabricPhotoQrSessionRef.current.add(handledKey);
        const applied = applyFabricPhotoQrUploadToLocalState({
          summaryKey: listenerSummaryKey,
          fabricLibraryKey: listenerFabricKey,
          identity: listenerIdentity,
          uploadedPhoto: data.uploadedPhoto,
          photoHistory: Array.isArray(data.photoHistory) ? data.photoHistory : [],
          fabricRecord: data.fabricRecord || {},
        });
        setFabricPhotoQrModal((prev) => ({
          ...prev,
          loading: false,
          status: applied ? "uploaded" : "failed",
          error: applied ? "" : "Photo uploaded, but the quote screen could not refresh it.",
        }));
        notify(
          applied
            ? "Fabric photo uploaded from phone."
            : "Fabric photo uploaded. Refresh if it does not appear.",
          applied ? "success" : "warning"
        );
      } else if (status === "expired" || status === "cancelled") {
        setFabricPhotoQrModal((prev) => ({
          ...prev,
          loading: false,
          status,
          error:
            status === "expired"
              ? "This phone upload link has expired."
            : "This phone upload link was cancelled.",
        }));
      }
  };

  const pollSession = async (reason = "interval") => {
    if (cancelled || inFlight) return;
    inFlight = true;
    if (IS_DEV_UI_ENABLED) {
      devLog("FABRIC_PHOTO_QR_SESSION_POLL", {
        workspaceUid,
        sessionId: listenerSessionId,
        path: sessionPath,
        reason,
      });
    }
    try {
      const snap = await trackedGetDoc(sessionRef, {
        label: "fabricPhotoUploadSessionPoll",
        cacheKey: sessionPath,
        component: "QuotationApp",
      });
      if (!cancelled) handleSessionSnap(snap);
    } catch (error) {
      if (cancelled) return;
      if (IS_DEV_UI_ENABLED) {
        devError("FABRIC_PHOTO_QR_SESSION_POLL_FAILED", {
          code: error?.code,
          message: error?.message,
          workspaceUid,
          sessionId: listenerSessionId,
          path: sessionPath,
        });
      }
      setFabricPhotoQrModal((prev) => ({
        ...prev,
        loading: false,
        status: "failed",
        error: "Could not check this phone upload session.",
      }));
    } finally {
      inFlight = false;
    }
  };

  pollSession("initial");
  const pollTimerId = window.setInterval(() => pollSession("timer_5s"), 5000);
  return () => {
    cancelled = true;
    window.clearInterval(pollTimerId);
    if (IS_DEV_UI_ENABLED) {
      devLog("FABRIC_PHOTO_QR_SESSION_POLL_STOPPED", {
        workspaceUid,
        sessionId: listenerSessionId,
        path: sessionPath,
      });
    }
  };
}, [
  applyFabricPhotoQrUploadToLocalState,
  fabricPhotoQrModal.fabricLibraryKey,
  fabricPhotoQrModal.identity,
  fabricPhotoQrModal.open,
  fabricPhotoQrModal.sessionId,
  fabricPhotoQrModal.status,
  fabricPhotoQrModal.summaryKey,
  notify,
  workspaceUid,
]);

const handleRemoveFabricPhoto = useCallback(
  (summary = {}) => {
    if (!workspaceUid || !auth.currentUser?.uid) {
      notify("Could not remove fabric photo because workspace is not ready.", "error");
      return;
    }

    const identity = buildFabricLibraryIdentityForSummary(summary);
    const fabricLibraryKey = sanitizeEditableText(
      summary.fabricLibraryKey || identity.fabricKey || ""
    );
    const summaryKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || "");
    if (!fabricLibraryKey || !summaryKey) {
      notify("Select a fabric before removing a fabric photo.", "warning");
      return;
    }

    openConfirmDialog({
      title: "Remove fabric photo?",
      description:
        "This will remove the current photo from this fabric in FabriQuote. Existing old PDFs may still contain old saved links, but new quotes will not auto-use this photo.",
      confirmLabel: "Remove Photo",
      cancelLabel: "Cancel",
      tone: "danger",
      onConfirm: async () => {
        const cacheKey = getFabricPhotoCacheKey(fabricLibraryKey);
        const docCacheKey = `fabricLibrary:${workspaceUid}:${fabricLibraryKey}`;
        setFabricPhotoRemovingKey(fabricLibraryKey);
        try {
          const existingRecord =
            getCachedFabricLibraryRecord(fabricLibraryKey) ||
            (await loadFabricLibraryPhotoForSummary({
              ...summary,
              fabricLibraryKey,
              fabricLibraryIdentity: identity,
            }));
          const previousPrimaryPhoto =
            normalizeFabricPhotoRef(summary.primaryPhoto) ||
            normalizeFabricPhotoRef(existingRecord?.primaryPhoto);
          const photoHistory = buildFabricPhotoHistory([
            previousPrimaryPhoto,
            ...(Array.isArray(existingRecord?.photoHistory)
              ? existingRecord.photoHistory
              : Array.isArray(summary.photoHistory)
              ? summary.photoHistory
              : []),
          ]);
          const removedBy = auth.currentUser?.uid || user?.uid || "";
          const fabricRef = doc(
            db,
            "users",
            workspaceUid,
            FABRIC_LIBRARY_COLLECTION,
            fabricLibraryKey
          );
          const savedRecord = {
            id: fabricLibraryKey,
            fabricKey: fabricLibraryKey,
            ...identity,
            primaryPhoto: null,
            photoHistory,
            photoRemovedAt: serverTimestamp(),
            photoRemovedBy: removedBy,
            updatedAt: serverTimestamp(),
            updatedBy: removedBy,
          };
          await setDoc(fabricRef, savedRecord, { merge: true });

          if (previousPrimaryPhoto?.fileId) {
            archiveManagedStorageFile({
              workspaceUid,
              fileId: previousPrimaryPhoto.fileId,
              removedBy,
              removalReason: "fabric_photo_removed_from_library",
              linkedRecords: [
                {
                  type: "fabricLibrary",
                  id: fabricLibraryKey,
                },
              ],
            }).catch((error) => {
              if (IS_DEV_UI_ENABLED) {
                devWarn("FABRIC_PHOTO_REMOVE_MANIFEST_ARCHIVE_FAILED", {
                  code: error?.code,
                  message: error?.message,
                  workspaceUid,
                  fabricLibraryKey,
                  fileId: previousPrimaryPhoto.fileId,
                });
              }
            });
          }

          invalidateFirestoreReadCache(docCacheKey);
          fabricPhotoLibraryCacheRef.current[cacheKey] = {
            ...(existingRecord || {}),
            ...savedRecord,
            updatedAt: new Date().toISOString(),
          };
          clearFabricPhotoFromSummaryMeta({
            summaryKey,
            identity: { ...identity, fabricKey: fabricLibraryKey },
            photoHistory,
          });
          setFabricPhotoLibraryRevision((revision) => revision + 1);
          if (IS_DEV_UI_ENABLED) {
            devLog("FABRIC_PHOTO_REMOVED", {
              workspaceUid,
              fabricLibraryKey,
              summaryKey,
              previousStoragePath: previousPrimaryPhoto?.storagePath || "",
            });
          }
          notify("Fabric photo removed from this fabric.", "success");
        } catch (error) {
          if (IS_DEV_UI_ENABLED) {
            devError("FABRIC_PHOTO_REMOVE_FAILED", {
              code: error?.code,
              message: error?.message,
              stack: error?.stack,
              workspaceUid,
              fabricLibraryKey,
              summaryKey,
            });
          }
          notify("Could not remove fabric photo. Please try again.", "error");
        } finally {
          setFabricPhotoRemovingKey("");
        }
      },
    });
  },
  [
    buildFabricLibraryIdentityForSummary,
    clearFabricPhotoFromSummaryMeta,
    getCachedFabricLibraryRecord,
    getFabricPhotoCacheKey,
    loadFabricLibraryPhotoForSummary,
    notify,
    openConfirmDialog,
    user?.uid,
    workspaceUid,
  ]
);

const updateFabricRepeatSettings = useCallback((fabricKey = "", patch = {}) => {
  const normalizedKey = normalizeFabricKey(fabricKey);
  if (!normalizedKey) return;
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettingsRef.current || repeatSettings);
  const nextRepeat = normalizeCurtainRepeatEntry({
    ...(normalizedRepeatSettings[normalizedKey] || {}),
    ...patch,
  });
  const nextRepeatSettings = {
    ...normalizedRepeatSettings,
    [normalizedKey]: nextRepeat,
  };
  const previousRepeat = normalizeCurtainRepeatEntry(normalizedRepeatSettings[normalizedKey] || {});
  const geometry = entry => [entry.verticalEnabled, entry.verticalRepeatInch, entry.horizontalEnabled,
    entry.horizontalRepeatInch, entry.applyMode, entry.productTypeScope];
  if (JSON.stringify(geometry(previousRepeat)) !== JSON.stringify(geometry(nextRepeat))) {
    const appliesToRow = row => {
      const config = resolveProductConfig(row, productConfigs, fabricCalcSettings);
      const active = { ...row, __forceCurrentCalculation: true };
      return resolveProductRepeatSettings(active, { repeatSettings: normalizedRepeatSettings, fabricKeyForRepeat: normalizedKey }, config).repeatAppliesToRow
        || resolveProductRepeatSettings(active, { repeatSettings: nextRepeatSettings, fabricKeyForRepeat: normalizedKey }, config).repeatAppliesToRow;
    };
    const matches = row => normalizeFabricKey(getCurtainRowFabricKey(row)) === normalizedKey && appliesToRow(row);
    if ((rowsRef.current || []).some(row => matches(row) && row.calculationSnapshot && !row.__forceCurrentCalculation)) {
      setConstructionRevisionReview({ originalTotal: Number(selectedQuote?.grandTotal ?? selectedQuote?.totalAmount ?? 0) });
    }
    commitCurtainRows(previous => applyRepeatEditToRows(previous, normalizedKey, nextRepeat, {
      getFabricKey: row => normalizeFabricKey(getCurtainRowFabricKey(row)), appliesToRow,
    }), QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
    markCurtainTotalsSettingsEdited("repeatSettings", normalizedKey);
  }
  repeatSettingsRef.current = nextRepeatSettings;
  setRepeatSettings(nextRepeatSettings);
  setFabricSummaryMeta((prev) => {
    const normalized = normalizeFabricSummaryMeta(prev);
    const current = normalized[normalizedKey] || {};
    const nextMeta = normalizeFabricSummaryMeta({
      ...normalized,
      [normalizedKey]: {
        ...current,
        repeatSettings: nextRepeat,
      },
    });
    fabricSummaryMetaRef.current = nextMeta;
    return nextMeta;
  });
}, [repeatSettings, commitCurtainRows, productConfigs, fabricCalcSettings, selectedQuote, markCurtainTotalsSettingsEdited]);

const updateFabricRepeatMeasurementValue = useCallback(
  (fabricKey = "", axis = "vertical", value = "", currentRepeat = {}) => {
    const safeAxis = axis === "horizontal" ? "horizontal" : "vertical";
    const unitKey = `${safeAxis}RepeatUnit`;
    const valueKey = `${safeAxis}RepeatValue`;
    const inchesKey = `${safeAxis}RepeatInch`;
    const canonicalInKey = `${safeAxis}RepeatIn`;
    const enabledKey = `${safeAxis}Enabled`;
    const unit = normalizeRepeatUnit(currentRepeat?.[unitKey] || "in");
    const valueInches = convertRepeatToInches(value, unit);
    updateFabricRepeatSettings(fabricKey, {
      [enabledKey]: true,
      [valueKey]: value,
      [unitKey]: unit,
      [canonicalInKey]: valueInches,
      [inchesKey]: valueInches,
    });
  },
  [updateFabricRepeatSettings]
);

const updateFabricRepeatMeasurementUnit = useCallback(
  (fabricKey = "", axis = "vertical", nextUnit = "in", currentRepeat = {}) => {
    const safeAxis = axis === "horizontal" ? "horizontal" : "vertical";
    const unit = normalizeRepeatUnit(nextUnit);
    const valueKey = `${safeAxis}RepeatValue`;
    const unitKey = `${safeAxis}RepeatUnit`;
    const inchesKey = `${safeAxis}RepeatInch`;
    const canonicalInKey = `${safeAxis}RepeatIn`;
    const enabledKey = `${safeAxis}Enabled`;
    const currentInches = Number(currentRepeat?.[inchesKey] || 0);
    const nextDisplayValue =
      currentInches > 0 ? formatRepeatMeasurement(currentInches, unit) : "";
    updateFabricRepeatSettings(fabricKey, {
      [enabledKey]: true,
      [valueKey]: nextDisplayValue,
      [unitKey]: unit,
      [canonicalInKey]: currentInches > 0 ? Number(currentInches.toFixed(6)) : "",
      [inchesKey]: currentInches > 0 ? Number(currentInches.toFixed(6)) : "",
    });
  },
  [updateFabricRepeatSettings]
);

const getCurtainFabricDisplayWithBrand = useCallback(
  (row = {}, originalRow = {}) => {
    const displayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row, {
      fabricText: getEditableFabricValue(row, originalRow),
    });
    const brandName = displayInfo.isValidCatalogue
      ? displayInfo.displayBrandName
      : "";
    const fabricLabel =
      displayInfo.displayFabricCode ||
      getCurtainRowFabricLabel(row, originalRow);
    return [brandName, fabricLabel].filter(Boolean).join(" / ");
  },
  [sanitizeCurtainFabricForDisplayAgainstSettings]
);

const logPdfFabricSourceTrace = useCallback(
  (pdfType = "", row = {}, displayInfo = {}, finalPdfDisplay = "") => {
    if (!IS_DEV_UI_ENABLED) return;
    const normalizedFabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
    const summaryMeta = getFabricSummaryMetaForKey(normalizedFabricKey);
    devLog("PDF_FABRIC_SOURCE_TRACE", {
      pdfType,
      quoteNo,
      rowId: getWorkshopRowStableId(row) || "",
      productName: row.productName || row.productType || row.product || "",
      rawFabricCode: getEditableFabricValue(row),
      rawBrandId: row.brandId || row.fabricBrandId || "",
      rawBrandName: row.brandName || row.fabricBrandName || "",
      rawCatalogueId: row.catalogueId || row.fabricCatalogueId || "",
      rawCatalogueName: row.catalogueName || row.fabricCatalogueName || "",
      fabricSummaryMetaBrand: summaryMeta?.brandName || "",
      fabricSummaryMetaCatalogue: summaryMeta?.catalogueName || "",
      savedSnapshotBrand: row.calculationSnapshot?.brandName || "",
      savedSnapshotCatalogue: row.calculationSnapshot?.catalogueName || "",
      validatedBrandName: displayInfo.displayBrandName || "",
      validatedCatalogueName: displayInfo.displayCatalogueName || "",
      isValidCatalogue: displayInfo.isValidCatalogue === true,
      invalidReason: displayInfo.invalidReason || "",
      finalPdfDisplay,
    });
  },
  [getFabricSummaryMetaForKey, quoteNo]
);

function supportsHalfPieceReuse(row = {}, productConfig = null) {
    const productName = normalizeProductName(
      row?.productName || row?.productType || row?.product || productConfig?.name || ""
    );
    const category = normalizeProductName(productConfig?.category || row?.category || "");
    const meterFormulaType = normalizeProductName(productConfig?.fabricRules?.meterFormulaType || "");
    const isRomanRow =
      productName.startsWith("roman") ||
      category === "roman_blind" ||
      meterFormulaType === "roman_panels";
    if (isRomanRow) {
      return (
        productConfig?.fabricRules?.allowAsymmetricalRomanJoin === true ||
        productConfig?.workshopRules?.allowRomanHalfPieceReuse === true
      );
    }
    const isCurtainRow =
      productName === "curtains" ||
      category === "curtain" ||
      meterFormulaType === "curtain_panels";
    if (!isCurtainRow) return false;
    return productConfig?.workshopRules?.allowHalfPieceReuse !== false;
}

function getHalfPieceReusePiecePlan(row = {}, calc = {}, options = {}) {
    // Reopened pattern rows must use their effective saved join width, not the
    // product's pre-repeat width. Otherwise production demand reprices the row.
    const savedPattern = calc.usesCalculationSnapshot &&
      ['pattern-repeat-v2', 'legacy-repeat-v1'].includes(calc.calculationSnapshot?.repeatCuttingVersion)
      ? calc.calculationSnapshot : null;
    const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
      options || {},
      "quoteFullnessOverrides"
    )
      ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
      : quoteFullnessOverrides;
    const productConfig =
      options.productConfig ||
      row?.productConfigSnapshot ||
      null;
    const productName = normalizeProductName(
      row?.productName || row?.productType || row?.product || productConfig?.name || ""
    );
    const isRomanRow =
      calc?.isRoman === true ||
      productName.startsWith("roman");
    const fabricRules = savedPattern ? {
      fabricWidthIn: Number(savedPattern.fabricWidthIn),
      usableWidthDeductionIn: Number(savedPattern.usableWidthDeductionIn),
      usableFabricWidthIn: Number(savedPattern.usableFabricWidthIn),
    } : calc?.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const quantity = Math.max(1, num(row?.qty ?? row?.quantity) || 1);
    if (isRomanRow) {
      const romanPanelCalc =
        calc?.romanPanelCalc ||
        calc?.calculationBreakdown?.romanPanelCalc ||
        calculateRomanBlindPanels({
          finishedWidthIn: row?.width,
          usableFabricWidthIn:
            calc?.calculationBreakdown?.curtainPieceCalc?.usableFabricWidthIn ||
            calc?.curtainPieceCalc?.usableFabricWidthIn ||
            fabricRules.usableFabricWidthIn,
          fabricWidthIn:
            calc?.calculationBreakdown?.curtainPieceCalc?.fabricWidthIn ||
            calc?.curtainPieceCalc?.fabricWidthIn ||
            fabricRules.fabricWidthIn,
          usableWidthDeductionIn:
            calc?.calculationBreakdown?.curtainPieceCalc?.usableWidthDeductionIn ||
            calc?.curtainPieceCalc?.usableWidthDeductionIn ||
            fabricRules.usableWidthDeductionIn,
          sideAllowanceIn: 0,
          joinMode: "balanced_equal_panels",
        });
      const panelCountPerBlind = Number(romanPanelCalc.panelCount || 0);
      const totalPanels = panelCountPerBlind * quantity;
      const rawPanels = Number(romanPanelCalc.rawPanels || 0) * quantity;

      return {
        width: Number(row?.width || 0),
        adjustedWidthIn: Number(romanPanelCalc.adjustedWidthIn || row?.width || 0),
        fullness: 1,
        fullnessSource: "roman_balanced_equal_panels",
        fabricWidth: Number(romanPanelCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
        fabricWidthIn: Number(romanPanelCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
        usableWidthDeduction: Number(
          romanPanelCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
        ),
        usableWidthDeductionIn: Number(
          romanPanelCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
        ),
        usableFabricWidth: Number(
          romanPanelCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
        ),
        usableFabricWidthIn: Number(
          romanPanelCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
        ),
        rawPieces: round2(rawPanels),
        rawPanels: round2(rawPanels),
        rawPanelsPerBlind: Number(romanPanelCalc.rawPanels || 0),
        snapshotRawPieces: 0,
        billingPieces: totalPanels,
        wholePanels: totalPanels,
        halfStepPanels: totalPanels,
        reuseTotalPieces: totalPanels,
        floorPieces: totalPanels,
        fullCutsWithoutReuse: totalPanels,
        hasHalfPiece: false,
        hasHalfPanelRequirement: false,
        fractionalPieces: 0,
        panelMode: romanPanelCalc.panelMode || "balanced_equal_panels",
        joinMode: romanPanelCalc.joinMode || "balanced_equal_panels",
        panelCountPerBlind,
        panelsPerBlind: panelCountPerBlind,
        panelWidthsIn: romanPanelCalc.panelWidthsIn || romanPanelCalc.panelWidths || [],
        totalPanels,
        quantity,
        jointDescription: romanPanelCalc.jointDescription || "",
        halfPieceReuseAllowed: supportsHalfPieceReuse(row, productConfig),
        source: "romanBalancedEqualPanels",
      };
    }
    const fullnessResult = savedPattern ? {
      fullness: Number(savedPattern.effectiveFullness ?? savedPattern.fullness), source: savedPattern.fullnessSource,
    } : calc?.calculationBreakdown?.fullness ||
        resolveCurtainFullness(row, productConfig, stitchingRates, {
            fabricCalcSettings,
            fabricRules,
            quoteFullnessOverrides: activeQuoteFullnessOverrides,
          });
    const pieceCalc = savedPattern ? {
      ...fabricRules, rawPieces: Number(savedPattern.rawPieces), pieces: Number(savedPattern.pieces),
    } : calc?.calculationBreakdown?.curtainPieceCalc ||
      calc?.curtainPieceCalc ||
      calculateCurtainPieces({
        widthIn: row?.width,
        fabricWidthIn: fabricRules.fabricWidthIn,
        usableWidthDeductionIn: fabricRules.usableWidthDeductionIn,
        fullness: fullnessResult.fullness,
      });
    const recomputedRawPieces = Number(pieceCalc.rawPieces || 0);
    const snapshotRawPieces = Number(
      calc?.calculationBreakdown?.curtainPieceCalc?.rawPieces ??
        calc?.curtainPieceCalc?.rawPieces ??
        calc?.rawPieces ??
        0
    );
    const billingPieces = Number(
      calc?.totalPieces ??
        calc?.pcsHalf ??
        calc?.pcsFull ??
        calc?.pcs ??
        pieceCalc.pieces ??
        0
    );
    const safeRawPieces =
      Number.isFinite(recomputedRawPieces) && recomputedRawPieces > 0
        ? recomputedRawPieces
        : Number.isFinite(snapshotRawPieces) && snapshotRawPieces > 0
        ? snapshotRawPieces
        : 0;
    const safeBillingPieces =
      Number.isFinite(billingPieces) && billingPieces > 0
        ? billingPieces
        : Number(pieceCalc.pieces || 0);
    const reuseTotalPieces = safeRawPieces
      ? Math.ceil(Math.max(0, safeRawPieces - HALF_PIECE_EPSILON) * 2) / 2
      : safeBillingPieces;
    const floorPieces = Math.floor(reuseTotalPieces + HALF_PIECE_EPSILON);
    const fractionalPieces = round2(Math.max(0, reuseTotalPieces - floorPieces));
    const hasHalfPiece = fractionalPieces >= 0.5 - HALF_PIECE_EPSILON;
    const wholePanels = safeRawPieces
      ? Math.ceil(Math.max(0, safeRawPieces - HALF_PIECE_EPSILON))
      : safeBillingPieces;

    return {
      width: Number(row?.width || 0),
      fullness: Number(fullnessResult.fullness || 0),
      fullnessSource: fullnessResult.source || "",
      fabricWidth: Number(pieceCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
      fabricWidthIn: Number(pieceCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
      usableWidthDeduction: Number(
        pieceCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
      ),
      usableWidthDeductionIn: Number(
        pieceCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
      ),
      usableFabricWidth: Number(
        pieceCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
      ),
      usableFabricWidthIn: Number(
        pieceCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
      ),
      rawPieces: Number(pieceCalc.rawPieces || safeRawPieces || 0),
      rawPanels: Number(pieceCalc.rawPieces || safeRawPieces || 0),
      snapshotRawPieces: Number.isFinite(snapshotRawPieces) ? snapshotRawPieces : 0,
      billingPieces: safeBillingPieces,
      wholePanels,
      halfStepPanels: round2(reuseTotalPieces),
      reuseTotalPieces: round2(reuseTotalPieces),
      floorPieces,
      fullCutsWithoutReuse: wholePanels,
      hasHalfPiece,
      hasHalfPanelRequirement: hasHalfPiece,
      fractionalPieces,
      source:
        Number.isFinite(recomputedRawPieces) && recomputedRawPieces > 0
          ? "recomputedRowInputsRoundedToHalf"
          : safeRawPieces
          ? "snapshotRawPiecesRoundedToHalf"
          : "billingPiecesFallback",
    };
}

const getCurtainCustomerBillingFabricTrace = useCallback(
  (row = {}, rowIndex = -1, providedCalc = null) => {
    const calc = providedCalc || getCurtainProductionAdjustedRowCalc(row, rowIndex);
    const productConfig = getRowProductConfig(row);
    const panelPlan = getHalfPieceReusePiecePlan(row, calc, {
      productConfig,
    });
    const cutHeightIn = Number(
      calc.cutCurtain ||
        calc.cutRoman ||
        calc.cutHeightIn ||
        row.calculationSnapshot?.cutHeightIn ||
        row.cutHeightIn ||
        row.height ||
        0
    );
    const cutHeightMeters = cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0;
    const rawPanels = Number(panelPlan.rawPanels || panelPlan.rawPieces || 0);
    const floorPanels = Math.floor(Math.max(0, rawPanels));
    const halfStepProductionPanels = Number(
      panelPlan.halfStepPanels ?? calc.halfStepProductionPanels ?? calc.pcsHalf ?? 0
    );
    const wholeBillingPanels = Number(
      panelPlan.wholePanels ?? calc.wholeBillingPanels ?? calc.pcsFull ?? calc.totalPieces ?? 0
    );
    const computedFullOrderedFabricMeters = round2(wholeBillingPanels * cutHeightMeters);
    const storedFullOrderedFabricMeters = round2(
      Number(
        calc.fullOrderedFabricMeters ??
          calc.noReuseOrderedFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      )
    );
    const fullOrderedFabricMeters =
      computedFullOrderedFabricMeters > 0
        ? computedFullOrderedFabricMeters
        : storedFullOrderedFabricMeters;
    const reducedCustomerMeters = round2(
      Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          fullOrderedFabricMeters
      )
    );
    const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
      ? reducedCustomerMeters
      : fullOrderedFabricMeters;
    const newFullCuts = Number(calc.newFullCuts ?? panelPlan.floorPieces ?? floorPanels ?? 0);
    const rowNewFabricMeters = round2(newFullCuts * cutHeightMeters);

    return {
      rowId: getWorkshopRowStableId(row) || `row-${rowIndex}`,
      itemName: row.item || row.name || row.room || `Row ${rowIndex + 1}`,
      product: row.productName || row.productType || row.product || productConfig?.name || "",
      fabricCode: getCurtainFabricDisplayWithBrand(row),
      fabricKey: getCurtainRowFabricKey(row),
      widthIn: Number(row.width || 0),
      heightIn: Number(row.height || 0),
      fabricWidthIn: Number(panelPlan.fabricWidthIn || row.fabricWidthIn || 0),
      usableWidthDeductionIn: Number(panelPlan.usableWidthDeductionIn || 0),
      fullness: Number(panelPlan.fullness || 0),
      rawPanels,
      floorPanels,
      halfStepProductionPanels,
      wholeBillingPanels,
      cutHeightIn,
      fullOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopFabricRequiredMeters: round2(
        Number(calc.workshopFabricRequiredMeters ?? calc.workshopNewFabricMeters ?? 0)
      ),
      rowNewFabricMeters,
      newFullCuts,
      cutFabricMeters: round2(Number(calc.cutFabricMeters ?? 0)),
      liveScreenOrderedFabricMeters: customerBillableFabricMeters,
      pdfOrderedFabricPrinted: customerBillableFabricMeters,
      pdfSourceField: passHalfPieceSavingsToCustomer
        ? "customerBillableFabricMeters"
        : "computedWholeBillingPanels * cutHeightMeters",
    };
  },
  // getHalfPieceReusePiecePlan is a hoisted function declaration so render-time
  // memos can call it before this block. It reads current settings through
  // closure and is intentionally omitted from dependencies.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    getCurtainFabricDisplayWithBrand,
    getCurtainProductionAdjustedRowCalc,
    getRowProductConfig,
    passHalfPieceSavingsToCustomer,
    quoteFullnessOverrides,
  ]
);

const curtainCanonicalFabricSummaryRows = useMemo(() => {
  const billableMetersByKey = {};
  rows.forEach((row, index) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    if (!calc?.isCurtain && !calc?.isRoman) return;
    const fabricKey = getCurtainRowFabricKey(row);
    if (!fabricKey) return;
    const normalizedFabricKey = normalizeFabricKey(fabricKey);
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    billableMetersByKey[normalizedFabricKey] =
      Number(billableMetersByKey[normalizedFabricKey] || 0) +
      Number(billingTrace.customerBillableFabricMeters || 0);
  });

  return Object.entries(fabricSummaryUI || {})
    .map(([key, value], index) => {
      const normalizedKey = normalizeFabricKey(key);
      const orderSummary =
        fabricOrderSummaryUI?.[normalizedKey] ||
        fabricOrderSummaryUI?.[key] ||
        value ||
        {};
      const summaryMeta = getFabricSummaryMetaForKey(normalizedKey);
      const sourceRowsForFabric = rows.filter(
        (sourceRow) =>
          normalizeFabricKey(getCurtainRowFabricKey(sourceRow)) === normalizedKey
      );
      const repeatProductTypeOptions = Array.from(
        new Map(
          sourceRowsForFabric
            .map((sourceRow) => {
              const productConfig = getRowProductConfig(sourceRow);
              if (!isRepeatApplicableProduct(sourceRow, productConfig)) return null;
              const productType = getRepeatProductType(sourceRow, productConfig);
              return productType.key
                ? [
                    productType.key,
                    {
                      key: productType.key,
                      label: productType.label,
                      productId: productType.id || "",
                    },
                  ]
                : null;
            })
            .filter(Boolean)
        ).values()
      ).sort((left, right) => left.label.localeCompare(right.label));
      const sanitizedDisplayCodes = sourceRowsForFabric
        .map((sourceRow) =>
          sanitizeCurtainFabricForDisplayAgainstSettings(sourceRow)
            .displayFabricCode
        )
        .filter(Boolean);
      const fabricCode =
        sanitizedDisplayCodes[0] ||
        sanitizeFabricTextAgainstSavedBrandPrefix(summaryMeta.fabricCode || "", summaryMeta) ||
        sanitizeFabricTextAgainstSavedBrandPrefix(
          value?.fabricLabel || value?.displayName || key,
          summaryMeta
        );
      const sourceFabricWidthValues = sourceRowsForFabric
        .map((sourceRow) => normalizeFabricWidthIn(sourceRow.fabricWidthIn))
        .filter(Boolean);
      const fabricWidthValues = Array.from(
        new Set(
          [
            ...(Array.isArray(value?.fabricWidthValues) ? value.fabricWidthValues : []),
            ...sourceFabricWidthValues,
            value?.fabricWidthIn,
          ]
            .map((widthValue) => normalizeFabricWidthIn(widthValue))
            .filter(Boolean)
        )
      );
      const fabricWidthIn =
        normalizeFabricWidthIn(value?.fabricWidthIn) ||
        normalizeFabricWidthIn(sourceFabricWidthValues[0]) ||
        null;
      const hasMixedFabricWidths =
        value?.hasMixedFabricWidths === true || fabricWidthValues.length > 1;
      const fullOrderedMetersRaw = Number(
        value?.fullOrderedFabricMetersRaw ??
          value?.fullOrderedFabricMeters ??
          value?.orderedFabricMetersRaw ??
          value?.orderedFabricMeters ??
          0
      );
      const newNeededMetersRaw = Number(
        orderSummary?.fabricToOrderMetersRaw ??
          orderSummary?.newFabricNeededMetersRaw ??
          orderSummary?.workshopFabricRequiredMetersRaw ??
          orderSummary?.fabricToOrderMeters ??
          orderSummary?.newFabricNeededMeters ??
          orderSummary?.workshopFabricRequiredMeters ??
          value?.fabricToOrderMetersRaw ??
          value?.fabricToOrderMeters ??
          value?.newFabricNeededMetersRaw ??
          value?.newFabricNeededMeters ??
          value?.workshopFabricRequiredMetersRaw ??
          value?.workshopFabricRequiredMeters ??
          value?.meters ??
          0
      );
      const workshopMetersRaw = Number(
        orderSummary?.fabricToOrderMetersRaw ??
          orderSummary?.workshopFabricRequiredMetersRaw ??
          orderSummary?.newFabricNeededMetersRaw ??
          orderSummary?.fabricToOrderMeters ??
          orderSummary?.workshopFabricRequiredMeters ??
          orderSummary?.newFabricNeededMeters ??
          newNeededMetersRaw ??
          fullOrderedMetersRaw
      );
      const hasLiveBillableMeters = Object.prototype.hasOwnProperty.call(
        billableMetersByKey,
        normalizedKey
      );
      const fabricGroupCustomerBillableMetersRaw = Number(
        value?.customerBillableFabricMetersRaw ??
          value?.customerBillableFabricMeters ??
          0
      );
      const billableMetersRaw = Number(
        hasLiveBillableMeters
          ? billableMetersByKey[normalizedKey]
          : fabricGroupCustomerBillableMetersRaw
      );
      const halfReusedMetersRaw = Number(
        orderSummary?.halfPieceSavingMetersRaw ??
          orderSummary?.halfReusedMeters ??
          value?.halfPieceSavingMetersRaw ??
          value?.halfReusedMeters ??
          0
      );
      const displayMeters = normalizeFabricSummaryMeterDisplay({
        fabricToOrderRaw: workshopMetersRaw,
        customerBillableRaw: billableMetersRaw,
        halfPieceSavingRaw: halfReusedMetersRaw,
      });
      const fullOrderedMeters = roundMetersForDisplay(fullOrderedMetersRaw);
      const newNeededMeters = displayMeters.fabricToOrderMeters;
      const workshopMeters = displayMeters.fabricToOrderMeters;
      const fabricGroupCustomerBillableMeters = roundMetersForDisplay(
        fabricGroupCustomerBillableMetersRaw
      );
      const billableMeters = displayMeters.customerBillableFabricMeters;
      const cutMeters = roundMetersForDisplay(
        Number(value?.cutFabricMetersRaw ?? value?.cutFabricMeters ?? 0)
      );
      const halfReusedMeters = displayMeters.halfPieceSavingMeters;
      const usedInItems = sourceRowsForFabric
        .map((sourceRow, sourceIndex) => {
          return (
            sanitizeEditableText(sourceRow.item || sourceRow.room || sourceRow.name || "") ||
            `Row ${sourceIndex + 1}`
          );
        })
        .filter(Boolean);
      const resolvedSummaryIdentity = resolveFabricSummaryIdentityForGroup(
        sourceRowsForFabric,
        {
          key,
          normalizedKey,
          ...value,
          fabricCode,
          displayName: fabricCode,
        },
        summaryMeta
      );
      const hasMixedBrands = resolvedSummaryIdentity.hasMixedBrands === true;
      const brandName = hasMixedBrands
        ? "Mixed brands"
        : resolvedSummaryIdentity.brandName || "";
      const summaryRepeatSettings = normalizeCurtainRepeatEntry(
        repeatSettings?.[normalizedKey] || summaryMeta.repeatSettings || {}
      );
      const repeatSummary = formatRepeatSettings(summaryRepeatSettings);
      const fabricLibraryIdentity = resolvedSummaryIdentity;
      const hasExactSummaryFabricIdentity = Boolean(
        fabricLibraryIdentity.brandId &&
          (fabricLibraryIdentity.catalogueId ||
            fabricLibraryIdentity.catalogueKey ||
            fabricLibraryIdentity.catalogueName) &&
          fabricLibraryIdentity.srNumber &&
          fabricLibraryIdentity.fabricKey
      );
      const fabricLibraryKey = sanitizeEditableText(
        hasExactSummaryFabricIdentity
          ? fabricLibraryIdentity.fabricKey
          : summaryMeta.fabricLibraryKey || fabricLibraryIdentity.fabricKey || ""
      );
      const cachedFabricLibraryRecord = getCachedFabricLibraryRecord(fabricLibraryKey);
      const primaryPhoto =
        normalizeFabricPhotoRef(summaryMeta.primaryPhoto) ||
        normalizeFabricPhotoRef(cachedFabricLibraryRecord?.primaryPhoto);

      if (
        cutMeters <= 0 &&
        fullOrderedMeters <= 0 &&
        newNeededMeters <= 0 &&
        workshopMeters <= 0 &&
        billableMeters <= 0
      ) {
        return null;
      }

      return {
        key,
        normalizedKey,
        displayName: fabricCode,
        fabricCode,
        brandId: hasMixedBrands ? "" : resolvedSummaryIdentity.brandId || "",
        brandName,
        fabricBrandId: hasMixedBrands ? "" : resolvedSummaryIdentity.fabricBrandId || "",
        fabricBrandName: hasMixedBrands ? "" : resolvedSummaryIdentity.fabricBrandName || "",
        brandSource: resolvedSummaryIdentity.brandSource || "",
        hasMixedBrands,
        catalogueId: fabricLibraryIdentity.catalogueId || summaryMeta.catalogueId || "",
        catalogueName: fabricLibraryIdentity.catalogueName || summaryMeta.catalogueName || "",
        catalogueKey: fabricLibraryIdentity.catalogueKey || summaryMeta.catalogueKey || "",
        srNumber: fabricLibraryIdentity.srNumber || summaryMeta.srNumber || "",
        fabricWidthIn,
        fabricWidthSource:
          value?.fabricWidthSource ||
          sourceRowsForFabric.find((sourceRow) => sourceRow.fabricWidthSource)
            ?.fabricWidthSource ||
          "",
        fabricWidthManuallyOverridden:
          value?.fabricWidthManuallyOverridden === true ||
          sourceRowsForFabric.some(
            (sourceRow) => sourceRow.fabricWidthManuallyOverridden === true
          ),
        catalogueDefaultFabricWidthIn:
          normalizeFabricWidthIn(value?.catalogueDefaultFabricWidthIn) ||
          normalizeFabricWidthIn(sourceRowsForFabric[0]?.catalogueDefaultFabricWidthIn) ||
          null,
        srRuleFabricWidthIn:
          normalizeFabricWidthIn(value?.srRuleFabricWidthIn) ||
          normalizeFabricWidthIn(sourceRowsForFabric[0]?.srRuleFabricWidthIn) ||
          null,
        fabricWidthValues,
        hasMixedFabricWidths,
        fabricLibraryKey,
        fabricLibraryIdentity: {
          ...fabricLibraryIdentity,
          fabricKey: fabricLibraryKey || fabricLibraryIdentity.fabricKey,
        },
        primaryPhoto,
        photoHistory: (summaryMeta.photoHistory || cachedFabricLibraryRecord?.photoHistory || [])
          .map((photo) => normalizeFabricPhotoRef(photo))
          .filter(Boolean)
          .slice(0, FABRIC_PHOTO_HISTORY_LIMIT),
        mixedBrandNames: resolvedSummaryIdentity.mixedBrandNames || [],
        usedInItems,
        repeatProductTypeOptions,
        repeatSettings: summaryRepeatSettings,
        repeatSummary,
        notes: sanitizeEditableText(summaryMeta.notes || ""),
        cutFabricMeters: cutMeters,
        cutFabricMetersRaw: Number(value?.cutFabricMetersRaw ?? value?.cutFabricMeters ?? 0),
        fullOrderedFabricMeters: fullOrderedMeters,
        fullOrderedFabricMetersRaw: fullOrderedMetersRaw,
        fabricToOrderMeters: workshopMeters,
        fabricToOrderMetersRaw: displayMeters.fabricToOrderMetersRaw,
        newFabricNeededMeters: newNeededMeters,
        newFabricNeededMetersRaw: newNeededMetersRaw,
        workshopFabricRequiredMeters: workshopMeters,
        workshopFabricRequiredMetersRaw: displayMeters.fabricToOrderMetersRaw,
        customerBillableFabricMeters: billableMeters,
        customerBillableFabricMetersRaw:
          displayMeters.customerBillableFabricMetersRaw,
        fabricGroupCustomerBillableFabricMeters: fabricGroupCustomerBillableMeters,
        fabricGroupCustomerBillableFabricMetersRaw:
          fabricGroupCustomerBillableMetersRaw,
        uiSummaryCustomerBillableFabricMeters: billableMeters,
        uiSummaryCustomerBillableFabricMetersRaw:
          displayMeters.customerBillableFabricMetersRaw,
        uiSummaryBillableSource: hasLiveBillableMeters
          ? "sum(customer PDF billing resolver rows)"
          : "fabricSummaryUI.customerBillableFabricMeters",
        halfReusedMeters,
        halfPieceSavingMetersRaw: displayMeters.halfPieceSavingMetersRaw,
        roundingDriftNormalized: displayMeters.roundingDriftNormalized,
        roundingDriftMeters: displayMeters.roundingDriftMeters,
        color: `hsl(${(index * 37) % 360} 70% 60%)`,
      };
    })
    .filter(Boolean);
}, [
  fabricOrderSummaryUI,
  fabricSummaryUI,
  fabricPhotoLibraryRevision,
  getCurtainCustomerBillingFabricTrace,
  getCurtainProductionAdjustedRowCalc,
  getCachedFabricLibraryRecord,
  getFabricBrandNameById,
  getFabricSummaryMetaForKey,
  getRowProductConfig,
  repeatSettings,
  rows,
  sanitizeCurtainFabricForDisplayAgainstSettings,
]);

useEffect(() => {
  if (!Array.isArray(curtainCanonicalFabricSummaryRows) || !curtainCanonicalFabricSummaryRows.length) {
    return;
  }

  setFabricSummaryMeta((prev) => {
    const currentMeta = normalizeFabricSummaryMetaWithRepeatSettings(
      prev,
      repeatSettingsRef.current || repeatSettings || {}
    );
    const nextMeta = buildFabricSummaryMetaWithRowInferences(
      currentMeta,
      repeatSettingsRef.current || repeatSettings || {},
      rowsRef.current || rows || [],
      { traceLabel: "ui_auto_hydrate" }
    );
    if (JSON.stringify(nextMeta) === JSON.stringify(currentMeta)) {
      fabricSummaryMetaRef.current = currentMeta;
      return prev;
    }
    fabricSummaryMetaRef.current = nextMeta;
    return nextMeta;
  });
}, [curtainCanonicalFabricSummaryRows, repeatSettings, rows]);

useEffect(() => {
  if (!workspaceUid || !Array.isArray(curtainCanonicalFabricSummaryRows)) return;
  curtainCanonicalFabricSummaryRows.forEach((summary) => {
    const fabricLibraryKey = sanitizeEditableText(summary.fabricLibraryKey || "");
    if (!fabricLibraryKey || normalizeFabricPhotoRef(summary.primaryPhoto)) return;
    loadFabricLibraryPhotoForSummary(summary);
  });
}, [curtainCanonicalFabricSummaryRows, loadFabricLibraryPhotoForSummary, workspaceUid]);

function buildCurtainCanonicalFabricSummaryRowsForAction({
  sourceRows = [],
  fabricSummarySource = {},
  fabricOrderSummarySource = {},
  liveFabricSummaryMeta = {},
  liveGetRowCalc,
} = {}) {
  const billableMetersByKey = {};
  const safeRows = Array.isArray(sourceRows) ? sourceRows : [];
  safeRows.forEach((row, index) => {
    const calc =
      typeof liveGetRowCalc === "function"
        ? liveGetRowCalc(row, index)
        : getCurtainProductionAdjustedRowCalc(row, index);
    if (!calc?.isCurtain && !calc?.isRoman) return;
    const fabricKey = getCurtainRowFabricKey(row);
    if (!fabricKey) return;
    const normalizedFabricKey = normalizeFabricKey(fabricKey);
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    billableMetersByKey[normalizedFabricKey] =
      Number(billableMetersByKey[normalizedFabricKey] || 0) +
      Number(billingTrace.customerBillableFabricMeters || 0);
  });

  return Object.entries(fabricSummarySource || {})
    .map(([key, value], index) => {
      const normalizedKey = normalizeFabricKey(key);
      const orderSummary =
        fabricOrderSummarySource?.[normalizedKey] ||
        fabricOrderSummarySource?.[key] ||
        value ||
        {};
      const summaryMeta = normalizedKey
        ? liveFabricSummaryMeta?.[normalizedKey] || {}
        : {};
      const sourceRowsForFabric = safeRows.filter(
        (sourceRow) =>
          normalizeFabricKey(getCurtainRowFabricKey(sourceRow)) === normalizedKey
      );
      const repeatProductTypeOptions = Array.from(
        new Map(
          sourceRowsForFabric
            .map((sourceRow) => {
              const productConfig = getRowProductConfig(sourceRow);
              if (!isRepeatApplicableProduct(sourceRow, productConfig)) return null;
              const productType = getRepeatProductType(sourceRow, productConfig);
              return productType.key
                ? [
                    productType.key,
                    {
                      key: productType.key,
                      label: productType.label,
                      productId: productType.id || "",
                    },
                  ]
                : null;
            })
            .filter(Boolean)
        ).values()
      ).sort((left, right) => left.label.localeCompare(right.label));
      const sanitizedDisplayCodes = sourceRowsForFabric
        .map((sourceRow) =>
          sanitizeCurtainFabricForDisplayAgainstSettings(sourceRow)
            .displayFabricCode
        )
        .filter(Boolean);
      const fabricCode =
        sanitizedDisplayCodes[0] ||
        sanitizeFabricTextAgainstSavedBrandPrefix(summaryMeta.fabricCode || "", summaryMeta) ||
        sanitizeFabricTextAgainstSavedBrandPrefix(
          value?.fabricLabel || value?.displayName || key,
          summaryMeta
        );
      const sourceFabricWidthValues = sourceRowsForFabric
        .map((sourceRow) => normalizeFabricWidthIn(sourceRow.fabricWidthIn))
        .filter(Boolean);
      const fabricWidthValues = Array.from(
        new Set(
          [
            ...(Array.isArray(value?.fabricWidthValues) ? value.fabricWidthValues : []),
            ...sourceFabricWidthValues,
            value?.fabricWidthIn,
          ]
            .map((widthValue) => normalizeFabricWidthIn(widthValue))
            .filter(Boolean)
        )
      );
      const fabricWidthIn =
        normalizeFabricWidthIn(value?.fabricWidthIn) ||
        normalizeFabricWidthIn(sourceFabricWidthValues[0]) ||
        null;
      const hasMixedFabricWidths =
        value?.hasMixedFabricWidths === true || fabricWidthValues.length > 1;
      const fullOrderedMetersRaw = Number(
        value?.fullOrderedFabricMetersRaw ??
          value?.fullOrderedFabricMeters ??
          value?.orderedFabricMetersRaw ??
          value?.orderedFabricMeters ??
          0
      );
      const workshopMetersRaw = Number(
        orderSummary?.fabricToOrderMetersRaw ??
          orderSummary?.workshopFabricRequiredMetersRaw ??
          orderSummary?.newFabricNeededMetersRaw ??
          orderSummary?.fabricToOrderMeters ??
          orderSummary?.workshopFabricRequiredMeters ??
          orderSummary?.newFabricNeededMeters ??
          value?.fabricToOrderMetersRaw ??
          value?.fabricToOrderMeters ??
          value?.workshopFabricRequiredMetersRaw ??
          value?.workshopFabricRequiredMeters ??
          value?.newFabricNeededMetersRaw ??
          value?.newFabricNeededMeters ??
          0
      );
      const liveBillableMeters = billableMetersByKey[normalizedKey];
      const hasLiveBillableMeters = Number.isFinite(Number(liveBillableMeters));
      const fabricGroupCustomerBillableMetersRaw = Number(
        value?.customerBillableFabricMetersRaw ??
          value?.customerBillableFabricMeters ??
          value?.fullOrderedFabricMetersRaw ??
          value?.fullOrderedFabricMeters ??
          value?.orderedFabricMetersRaw ??
          value?.orderedFabricMeters ??
          0
      );
      const billableMetersRaw = hasLiveBillableMeters
        ? Number(liveBillableMeters || 0)
        : fabricGroupCustomerBillableMetersRaw;
      const halfReusedMetersRaw = Number(
        orderSummary?.halfPieceSavingMetersRaw ??
          orderSummary?.halfReusedMeters ??
          value?.halfPieceSavingMetersRaw ??
          value?.halfReusedMeters ??
          0
      );
      const displayMeters = normalizeFabricSummaryMeterDisplay({
        fabricToOrderRaw: workshopMetersRaw,
        customerBillableRaw: billableMetersRaw,
        halfPieceSavingRaw: halfReusedMetersRaw,
      });
      const fullOrderedMeters = roundMetersForDisplay(fullOrderedMetersRaw);
      const workshopMeters = displayMeters.fabricToOrderMeters;
      const newNeededMeters = displayMeters.fabricToOrderMeters;
      const fabricGroupCustomerBillableMeters = roundMetersForDisplay(
        fabricGroupCustomerBillableMetersRaw
      );
      const billableMeters = displayMeters.customerBillableFabricMeters;
      const cutMeters = roundMetersForDisplay(
        Number(value?.cutFabricMetersRaw ?? value?.cutFabricMeters ?? 0)
      );
      const halfReusedMeters = displayMeters.halfPieceSavingMeters;
      const usedInItems = sourceRowsForFabric
        .map((row) => row.item || row.name || row.room || "")
        .filter(Boolean);
      const summaryRepeatSettings = normalizeCurtainRepeatEntry(
        summaryMeta.repeatSettings || {}
      );
      const repeatSummary = formatRepeatSettings(summaryRepeatSettings);
      const fabricLibraryIdentity = resolveFabricSummaryIdentityForGroup(
        sourceRowsForFabric,
        {
          key,
          normalizedKey,
          ...value,
          fabricCode,
          displayName: fabricCode,
        },
        summaryMeta
      );
      const hasMixedBrands = fabricLibraryIdentity.hasMixedBrands === true;
      const brandName = hasMixedBrands
        ? "Mixed brands"
        : fabricLibraryIdentity.brandName || "";
      const hasExactSummaryFabricIdentity = Boolean(
        fabricLibraryIdentity.brandId &&
          (fabricLibraryIdentity.catalogueId ||
            fabricLibraryIdentity.catalogueKey ||
            fabricLibraryIdentity.catalogueName) &&
          fabricLibraryIdentity.srNumber &&
          fabricLibraryIdentity.fabricKey
      );
      const fabricLibraryKey = sanitizeEditableText(
        hasExactSummaryFabricIdentity
          ? fabricLibraryIdentity.fabricKey
          : summaryMeta.fabricLibraryKey || fabricLibraryIdentity.fabricKey || ""
      );
      const cachedFabricLibraryRecord = getCachedFabricLibraryRecord(fabricLibraryKey);
      const primaryPhoto =
        normalizeFabricPhotoRef(summaryMeta.primaryPhoto) ||
        normalizeFabricPhotoRef(cachedFabricLibraryRecord?.primaryPhoto);

      if (
        cutMeters <= 0 &&
        fullOrderedMeters <= 0 &&
        workshopMeters <= 0 &&
        billableMeters <= 0
      ) {
        return null;
      }

      return {
        key,
        normalizedKey,
        displayName: fabricCode,
        fabricCode,
        brandId: hasMixedBrands ? "" : fabricLibraryIdentity.brandId || "",
        brandName,
        fabricBrandId: hasMixedBrands ? "" : fabricLibraryIdentity.fabricBrandId || "",
        fabricBrandName: hasMixedBrands ? "" : fabricLibraryIdentity.fabricBrandName || "",
        brandSource: fabricLibraryIdentity.brandSource || "",
        hasMixedBrands,
        catalogueId: fabricLibraryIdentity.catalogueId || summaryMeta.catalogueId || "",
        catalogueName: fabricLibraryIdentity.catalogueName || summaryMeta.catalogueName || "",
        catalogueKey: fabricLibraryIdentity.catalogueKey || summaryMeta.catalogueKey || "",
        srNumber: fabricLibraryIdentity.srNumber || summaryMeta.srNumber || "",
        fabricWidthIn,
        fabricWidthSource:
          value?.fabricWidthSource ||
          sourceRowsForFabric.find((sourceRow) => sourceRow.fabricWidthSource)
            ?.fabricWidthSource ||
          "",
        fabricWidthManuallyOverridden:
          value?.fabricWidthManuallyOverridden === true ||
          sourceRowsForFabric.some(
            (sourceRow) => sourceRow.fabricWidthManuallyOverridden === true
          ),
        catalogueDefaultFabricWidthIn:
          normalizeFabricWidthIn(value?.catalogueDefaultFabricWidthIn) ||
          normalizeFabricWidthIn(sourceRowsForFabric[0]?.catalogueDefaultFabricWidthIn) ||
          null,
        srRuleFabricWidthIn:
          normalizeFabricWidthIn(value?.srRuleFabricWidthIn) ||
          normalizeFabricWidthIn(sourceRowsForFabric[0]?.srRuleFabricWidthIn) ||
          null,
        fabricWidthValues,
        hasMixedFabricWidths,
        fabricLibraryKey,
        fabricLibraryIdentity: {
          ...fabricLibraryIdentity,
          fabricKey: fabricLibraryKey || fabricLibraryIdentity.fabricKey,
        },
        primaryPhoto,
        photoHistory: (summaryMeta.photoHistory || cachedFabricLibraryRecord?.photoHistory || [])
          .map((photo) => normalizeFabricPhotoRef(photo))
          .filter(Boolean)
          .slice(0, FABRIC_PHOTO_HISTORY_LIMIT),
        mixedBrandNames: fabricLibraryIdentity.mixedBrandNames || [],
        usedInItems,
        repeatProductTypeOptions,
        repeatSettings: summaryRepeatSettings,
        repeatSummary,
        notes: sanitizeEditableText(summaryMeta.notes || ""),
        cutFabricMeters: cutMeters,
        cutFabricMetersRaw: Number(value?.cutFabricMetersRaw ?? value?.cutFabricMeters ?? 0),
        fullOrderedFabricMeters: fullOrderedMeters,
        fullOrderedFabricMetersRaw: fullOrderedMetersRaw,
        fabricToOrderMeters: workshopMeters,
        fabricToOrderMetersRaw: displayMeters.fabricToOrderMetersRaw,
        newFabricNeededMeters: newNeededMeters,
        newFabricNeededMetersRaw: workshopMetersRaw,
        workshopFabricRequiredMeters: workshopMeters,
        workshopFabricRequiredMetersRaw: displayMeters.fabricToOrderMetersRaw,
        customerBillableFabricMeters: billableMeters,
        customerBillableFabricMetersRaw:
          displayMeters.customerBillableFabricMetersRaw,
        fabricGroupCustomerBillableFabricMeters: fabricGroupCustomerBillableMeters,
        fabricGroupCustomerBillableFabricMetersRaw:
          fabricGroupCustomerBillableMetersRaw,
        uiSummaryCustomerBillableFabricMeters: billableMeters,
        uiSummaryCustomerBillableFabricMetersRaw:
          displayMeters.customerBillableFabricMetersRaw,
        uiSummaryBillableSource: hasLiveBillableMeters
          ? "sum(customer PDF billing resolver rows)"
          : "fabricSummaryUI.customerBillableFabricMeters",
        halfReusedMeters,
        halfPieceSavingMetersRaw: displayMeters.halfPieceSavingMetersRaw,
        roundingDriftNormalized: displayMeters.roundingDriftNormalized,
        roundingDriftMeters: displayMeters.roundingDriftMeters,
        color: `hsl(${(index * 37) % 360} 70% 60%)`,
      };
    })
    .filter(Boolean);
}

function buildLiveCurtainQuotePayload({ action = "unknown" } = {}) {
  assertCompatibleQuote(selectedQuote || {});
  const liveRows = normalizeRowsForState(rowsRef.current || rows);
  const liveRepeatSettings = normalizeCurtainRepeatSettingsMap(
    repeatSettingsRef.current || repeatSettings || {}
  );
  const liveFabricSummaryMeta = sanitizeFabricSummaryMetaForValidCatalogues(
    fabricSummaryMetaRef.current || fabricSummaryMeta || {},
    liveRepeatSettings,
    liveRows
  );
  fabricSummaryMetaRef.current = liveFabricSummaryMeta;
  const liveUseHalfPieceReuse = useHalfPieceReuseRef.current !== false;
  const livePassHalfPieceSavingsToCustomer =
    passHalfPieceSavingsToCustomerRef.current === true;
  const liveDiscountMode = summaryDiscountModeRef.current ?? summaryDiscountMode;
  const liveDiscountValue = summaryDiscountValueRef.current ?? summaryDiscountValue;
  const liveGstMode = normalizeCurtainGstMode(
    curtainGstModeRef.current ?? curtainGstMode
  );
  const liveGstRate = normalizeCurtainGstRate(
    curtainGstRateRef.current ?? curtainGstRate
  );
  const liveQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quoteFullnessOverridesRef.current || quoteFullnessOverrides || {}
  );
  const liveQuoteSiteSelection = normalizeQuoteSiteSnapshot(quoteSiteSelection, {
    customerName,
  });

  const liveFabricSummaryResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: liveUseHalfPieceReuse,
    passSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
    debugTag: `${action}-live-action`,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveNoReuseBillingResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: false,
    debugTag: `${action}-live-action-no-reuse`,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveFabricOrderSummaryResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: liveUseHalfPieceReuse,
    passSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
    debugTag: `${action}-live-action-fabric-to-order`,
    useProductWorkshopRules: true,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveGetRowCalc = (row, index) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      index,
      liveFabricSummaryResult,
      liveNoReuseBillingResult
    );
  const liveSubtotal = round2(
    liveRows.reduce(
      (sum, row, index) => sum + Number(liveGetRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const gstTotals = calculateCurtainQuoteTotals({
    rows: liveRows,
    subtotal: liveSubtotal,
    discountType: getDiscountTypeFromSummaryMode(liveDiscountMode),
    discountValue: liveDiscountValue,
    quoteGstMode: liveGstMode,
    quoteGstRate: liveGstRate,
    getRowCalculation: liveGetRowCalc,
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });
  const liveSummaryTotals = {
    subTotal: round2(liveSubtotal),
    discountType: gstTotals.discountType,
    discountValue: gstTotals.discountValue,
    discountAmount: gstTotals.discountAmount,
    discountApplication: gstTotals.discountApplication || "after_gst",
    postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
    preGstGrandTotal: gstTotals.subtotalBeforeGst,
    subtotalBeforeGst: gstTotals.subtotalBeforeGst,
    totalAfterGst: gstTotals.totalAfterGst,
    measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
    measurementDiscountAmount: gstTotals.measurementDiscountAmount,
    accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
    taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
    fabricTaxableAmount: gstTotals.fabricTaxableAmount,
    otherTaxableAmount: gstTotals.otherTaxableAmount,
    taxableAmount: gstTotals.taxableAmount,
    quoteGstMode: gstTotals.quoteGstMode,
    gstMode: gstTotals.gstMode,
    gstModeLabel: gstTotals.gstModeLabel,
    quoteGstRate: gstTotals.quoteGstRate,
    gstRate: gstTotals.gstRate,
    gstRates: gstTotals.gstRates || [gstTotals.gstRate],
    accessoryGstRates: gstTotals.accessoryGstRates || [],
    hasMixedGstRates: gstTotals.hasMixedGstRates === true,
    isMixedGstMode: gstTotals.isMixedGstMode === true,
    fabricGstAmount: gstTotals.fabricGstAmount,
    otherGstAmount: gstTotals.otherGstAmount,
    measurementGstAmount: gstTotals.measurementGstAmount,
    accessoryGstAmount: gstTotals.accessoryGstAmount,
    gstAmount: gstTotals.gstAmount,
    totalGstAmount: gstTotals.totalGstAmount,
    gstBreakdown: gstTotals.gstBreakdown || {},
    finalTotal: gstTotals.finalTotal,
    grandTotal: gstTotals.grandTotal,
  };
  const liveCurrentCurtainDiscountFields = {
    discountType: liveSummaryTotals.discountType,
    discountValue: liveSummaryTotals.discountValue,
    discountAmount: liveSummaryTotals.discountAmount,
    discountApplication: liveSummaryTotals.discountApplication || "after_gst",
    postGstDiscountAmount:
      liveSummaryTotals.postGstDiscountAmount ?? liveSummaryTotals.discountAmount,
    summaryDiscountMode: getSummaryDiscountModeFromType(liveSummaryTotals.discountType),
    summaryDiscountValue: liveSummaryTotals.discountValue,
  };
  const liveCurrentCurtainGstFields = {
    quoteGstMode: liveSummaryTotals.quoteGstMode,
    gstMode: liveSummaryTotals.gstMode,
    gstModeLabel: liveSummaryTotals.gstModeLabel,
    quoteGstRate: liveSummaryTotals.quoteGstRate,
    gstRate: liveSummaryTotals.gstRate,
    gstAmount: liveSummaryTotals.gstAmount,
    totalGstAmount: liveSummaryTotals.totalGstAmount,
    fabricGstAmount: liveSummaryTotals.fabricGstAmount,
    otherGstAmount: liveSummaryTotals.otherGstAmount,
    measurementGstAmount: liveSummaryTotals.measurementGstAmount,
    accessoryGstAmount: liveSummaryTotals.accessoryGstAmount,
    fabricTaxableAmount: liveSummaryTotals.fabricTaxableAmount,
    otherTaxableAmount: liveSummaryTotals.otherTaxableAmount,
    taxableMeasurementAmount: liveSummaryTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: liveSummaryTotals.taxableAccessoryAmount,
    taxableAmount: liveSummaryTotals.taxableAmount,
    subtotalBeforeGst: liveSummaryTotals.subtotalBeforeGst,
    totalAfterGst: liveSummaryTotals.totalAfterGst,
    finalTotal: liveSummaryTotals.finalTotal,
    preGstGrandTotal: liveSummaryTotals.preGstGrandTotal,
    gstBreakdown: liveSummaryTotals.gstBreakdown || {},
    totalInWords: amountToWords(liveSummaryTotals.grandTotal, currency),
  };
  const liveFabricSummaryUI = liveFabricSummaryResult.fabricSummaries || {};
  const liveFabricOrderSummaryUI = liveFabricOrderSummaryResult.fabricSummaries || {};
  const liveCurtainCanonicalFabricSummaryRows =
    buildCurtainCanonicalFabricSummaryRowsForAction({
      sourceRows: liveRows,
      fabricSummarySource: liveFabricSummaryUI,
      fabricOrderSummarySource: liveFabricOrderSummaryUI,
      liveFabricSummaryMeta,
      liveGetRowCalc,
    });
  const liveDisplaySummaryTotals =
    isFrozenLegacyCurtainQuote &&
    !curtainQuoteIsDirty &&
    curtainTotalsSettingsEditVersion <= 0
      ? curtainDisplaySummaryTotals
      : liveSummaryTotals;
  const liveReferralPartner =
    selectedReferralPartnerRef.current ||
    normalizeQuoteReferralPartner(selectedQuote || {});
  const liveReferralPartnerFields =
    buildReferralPartnerQuoteFields(liveReferralPartner);

  return {
    action,
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteNo,
    quoteDate: date,
    customerName,
    quoteCustomerLink,
    referralPartner: liveReferralPartnerFields.referralPartner,
    referralPartnerId: liveReferralPartnerFields.referralPartnerId,
    referralPartnerName: liveReferralPartnerFields.referralPartnerName,
    referralPartnerType: liveReferralPartnerFields.referralPartnerType,
    quoteSiteSelection: liveQuoteSiteSelection,
    ...liveQuoteSiteSelection,
    selectedQuote,
    selectedQuoteStatus,
    selectedQuoteWorkStage,
    selectedInstallationDate,
    pendingCrmLeadId,
    rows: liveRows,
    repeatSettings: liveRepeatSettings,
    ...buildQuoteConstructionFields(liveRows),
    fabricSummaryMeta: liveFabricSummaryMeta,
    quoteFullnessOverrides: liveQuoteFullnessOverrides,
    fabricSummaryResult: liveFabricSummaryResult,
    fabricOrderSummaryResult: liveFabricOrderSummaryResult,
    fabricSummaryUI: liveFabricSummaryUI,
    fabricOrderSummaryUI: liveFabricOrderSummaryUI,
    curtainCanonicalFabricSummaryRows: liveCurtainCanonicalFabricSummaryRows,
    getRowCalc: liveGetRowCalc,
    summaryTotals: liveSummaryTotals,
    displaySummaryTotals: liveDisplaySummaryTotals,
    currentCurtainDiscountFields: liveCurrentCurtainDiscountFields,
    currentCurtainGstFields: liveCurrentCurtainGstFields,
    summaryDiscountMode: liveDiscountMode,
    summaryDiscountValue: liveDiscountValue,
    curtainGstMode: liveGstMode,
    curtainGstRate: liveGstRate,
    useHalfPieceReuse: liveUseHalfPieceReuse,
    passHalfPieceSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
  };
}

function getCurtainActionMeterTotals(payload = {}) {
  const summaryRows = payload.curtainCanonicalFabricSummaryRows || [];
  const totalsRaw = summaryRows.reduce(
    (acc, summary) => {
      acc.fabricToOrderTotal += Number(
        summary.fabricToOrderMeters ??
          summary.workshopFabricRequiredMeters ??
          summary.newFabricNeededMeters ??
          0
      );
      acc.customerBillableTotal += Number(summary.customerBillableFabricMeters || 0);
      return acc;
    },
    { fabricToOrderTotal: 0, customerBillableTotal: 0 }
  );
  return {
    fabricToOrderTotal: roundMetersForDisplay(totalsRaw.fabricToOrderTotal),
    customerBillableTotal: roundMetersForDisplay(totalsRaw.customerBillableTotal),
  };
}

function traceLiveActionPayload(payload = {}) {
  if (!IS_DEV_UI_ENABLED) return;
  const totals = payload.summaryTotals || {};
  const meterTotals = getCurtainActionMeterTotals(payload);
  devLog("LIVE_ACTION_PAYLOAD_TRACE", {
    action: payload.action || "unknown",
    quoteType: payload.quoteType || CURTAIN_QUOTE_TYPE,
    quoteNo: payload.quoteNo || "",
    saveSource: payload.saveSource || "",
    rowsCount: payload.rows?.length || payload.items?.length || 0,
    discountType: totals.discountType,
    discountValue: totals.discountValue,
    gstRate: totals.quoteGstRate ?? totals.gstRate,
    subtotal:
      totals.subTotal ??
      totals.subtotalBeforeGst ??
      totals.subtotalExcludingGst ??
      totals.subtotal,
    totalAfterGst: totals.totalAfterGst,
    discountAmount: totals.discountAmount,
    grandTotal: totals.grandTotal,
    fabricToOrderTotal: meterTotals.fabricToOrderTotal,
    customerBillableTotal: meterTotals.customerBillableTotal,
    generatedAt: new Date().toISOString(),
  });
}

function traceQuoteActionClick({
  action = "unknown",
  quoteType = quoteSubTab,
  event = null,
  payload = null,
} = {}) {
  if (!IS_DEV_UI_ENABLED) return;
  const isGeneralAction = quoteType === GENERAL_QUOTE_TYPE;
  const currentGeneralQuote = generalQuoteRef.current || generalQuote || {};
  const generalItems = getGeneralActionItems(currentGeneralQuote);
  const generalTotals = isGeneralAction
    ? calculateGeneralQuoteTotals(syncGeneralQuoteDefaults(currentGeneralQuote))
    : null;
  const curtainRows = !isGeneralAction
    ? normalizeRowsForState(rowsRef.current || rows)
    : [];
  const sourceTotals =
    payload?.summaryTotals ||
    payload?.totals ||
    (isGeneralAction ? generalTotals : summaryTotals) ||
    {};
  const recordCount = isGeneralAction ? generalItems.length : curtainRows.length;

  devLog("QUOTE_ACTION_CLICK_TRACE", {
    action,
    quoteType,
    quoteNo:
      payload?.quoteNo ||
      (isGeneralAction ? currentGeneralQuote.quoteNo : quoteNo) ||
      "",
    rowsCount: recordCount,
    itemsCount: recordCount,
    grandTotal: sourceTotals.grandTotal,
    subtotal:
      sourceTotals.subTotal ??
      sourceTotals.subtotal ??
      sourceTotals.subtotalBeforeGst,
    eventType: event?.type || "",
    defaultPrevented: Boolean(event?.defaultPrevented),
    generatedAt: new Date().toISOString(),
  });
}

function guardStaleActionPayload(payload = {}, payloadTotals = null) {
  if (!IS_DEV_UI_ENABLED) return;
  const visibleTotals = payload.summaryTotals || {};
  const actualPayloadTotals = payloadTotals || payload.summaryTotals || {};
  const visibleGrandTotal = round2(Number(visibleTotals.grandTotal || 0));
  const payloadGrandTotal = round2(Number(actualPayloadTotals.grandTotal || 0));
  const visibleDiscountValue = Number(visibleTotals.discountValue || 0);
  const payloadDiscountValue = Number(actualPayloadTotals.discountValue || 0);
  const visibleGstRate = Number(visibleTotals.quoteGstRate ?? visibleTotals.gstRate ?? 0);
  const payloadGstRate = Number(
    actualPayloadTotals.quoteGstRate ?? actualPayloadTotals.gstRate ?? 0
  );
  if (
    Math.abs(visibleGrandTotal - payloadGrandTotal) > 0.05 ||
    Math.abs(visibleDiscountValue - payloadDiscountValue) > 0.05 ||
    Math.abs(visibleGstRate - payloadGstRate) > 0.05
  ) {
    devError("STALE_ACTION_PAYLOAD_DETECTED", {
      action: payload.action || "unknown",
      visibleGrandTotal,
      payloadGrandTotal,
      visibleDiscountValue,
      payloadDiscountValue,
      visibleGstRate,
      payloadGstRate,
    });
  }
}

const updateFabricSummaryMetaForKey = useCallback((fabricKey, patch = {}) => {
  const normalizedKey = normalizeFabricKey(fabricKey);
  if (!normalizedKey) return;
  setFabricSummaryMeta((prev) => {
    const current = normalizeFabricSummaryMeta(prev)[normalizedKey] || {};
    const nextMeta = normalizeFabricSummaryMeta({
      ...prev,
      [normalizedKey]: {
        ...current,
        ...patch,
      },
    });
    fabricSummaryMetaRef.current = nextMeta;
    return nextMeta;
  });
}, []);

const syncFabricSummaryBrandToRows = useCallback(
  (fabricKey, selectedBrand = null) => {
    const normalizedKey = normalizeFabricKey(fabricKey);
    if (!normalizedKey) return;

    const currentRows = normalizeRowsForState(rowsRef.current || []);
    const targetRowIds = [];
    let skippedManualRows = 0;
    currentRows.forEach((row) => {
      const rowFabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
      if (rowFabricKey !== normalizedKey) return;
      const currentBrandId = sanitizeEditableText(row.brandId || row.fabricBrandId || "");
      const brandSource = sanitizeEditableText(
        row.brandSource || (currentBrandId ? "legacy" : "none")
      );
      const canUpdate =
        !currentBrandId ||
        brandSource === "fabric_summary" ||
        brandSource === "none";
      if (canUpdate) {
        targetRowIds.push(getWorkshopRowStableId(row));
      } else if (
        selectedBrand?.id &&
        currentBrandId &&
        currentBrandId !== selectedBrand.id
      ) {
        skippedManualRows += 1;
      }
    });

    if (!targetRowIds.length) {
      if (skippedManualRows > 0) {
        notify("Some rows have manually selected brands and were not changed.", "warning");
      }
      return;
    }

    const targetIdSet = new Set(targetRowIds);
    const timestamp = new Date().toISOString();
    commitCurtainRows(
      (previousRows) =>
        previousRows.map((row, index) => {
          const stableRow = withCurtainRowId(row || {}, index);
          if (!targetIdSet.has(getWorkshopRowStableId(stableRow))) return stableRow;
          const nextRow = {
            ...stableRow,
            brandId: selectedBrand?.id || "",
            brandName: selectedBrand?.name || "",
            fabricBrandId: selectedBrand?.id || "",
            fabricBrandName: selectedBrand?.name || "",
            brandSource: selectedBrand?.id ? "fabric_summary" : "none",
            updatedFromFabricSummaryAt: timestamp,
          };
          const rowFabricKey = normalizeFabricKey(getCurtainRowFabricKey(nextRow));
          nextRow.repeatSettings = repeatSettings?.[rowFabricKey] || {};
          return withCurtainRowId(nextRow, index);
        }),
      QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
    );

    if (skippedManualRows > 0) {
      notify("Some rows have manually selected brands and were not changed.", "warning");
    }
  },
  [commitCurtainRows, normalizeRowsForState, notify, repeatSettings]
);

const updateFabricSummaryBrand = useCallback(
  (fabricKey, brandId) => {
    const selectedBrand = fabricBrands.find((brand) => brand.id === brandId);
    updateFabricSummaryMetaForKey(fabricKey, {
      brandId: selectedBrand?.id || "",
      brandName: selectedBrand?.name || "",
      fabricBrandId: selectedBrand?.id || "",
      fabricBrandName: selectedBrand?.name || "",
      brandSource: selectedBrand?.id ? "manual" : "manual_cleared",
    });
    syncFabricSummaryBrandToRows(fabricKey, selectedBrand || null);
  },
  [fabricBrands, syncFabricSummaryBrandToRows, updateFabricSummaryMetaForKey]
);

const renderCurtainLiningToggle = (row, calc, rowId) => {
  if (!curtainRowSupportsLining(row, calc)) {
    return (
      <span style={{ color: dashboardMuted, fontSize: 12, opacity: 0.65 }}>
        -
      </span>
    );
  }

  const lining = getCurtainRowLiningForUi(row, calc);

  return (
    <label
      style={{
        display: "grid",
        gap: 5,
        fontSize: 12,
        fontWeight: 800,
        color: isDark ? "#e2e8f0" : "#334155",
        cursor: "pointer",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <input
          type="checkbox"
          checked={lining.enabled}
          onChange={(event) =>
            updateCurtainRowLining(rowId, row, calc, {
              enabled: event.target.checked,
            })
          }
        />
        Add Lining
      </span>
      {lining.enabled ? (
        <span
          style={{
            color: "#2563eb",
            fontSize: 11,
            fontWeight: 900,
            lineHeight: 1.35,
          }}
        >
          {getCurtainLiningLabel(lining)}
          <br />
          {currencySymbol(currency)} {Number(lining.amount || 0).toFixed(2)}
        </span>
      ) : null}
    </label>
  );
};

const renderCurtainLiningPanel = (row, calc, rowId) => {
  if (!curtainRowSupportsLining(row, calc)) return null;

  const lining = getCurtainRowLiningForUi(row, calc);
  const updateLining = (patch) => updateCurtainRowLining(rowId, row, calc, patch);
  const panelInput = {
    ...input,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelSelect = {
    ...selectInput,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelLabel = {
    fontSize: 11,
    fontWeight: 800,
    color: dashboardMuted,
    marginBottom: 5,
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: isDark
          ? "1px solid rgba(96,165,250,0.22)"
          : "1px solid rgba(191,219,254,0.9)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.94),rgba(30,41,59,0.86))"
          : "linear-gradient(135deg,#ffffff,#eff6ff)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 900,
            color: isDark ? "#f8fafc" : "#0f172a",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={lining.enabled}
            onChange={(event) =>
              updateLining({
                enabled: event.target.checked,
              })
            }
          />
          Lining required
        </label>
        {lining.enabled ? (
          <div
            style={{
              borderRadius: 999,
              padding: "5px 10px",
              background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
              color: "#2563eb",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            Includes lining: {currencySymbol(currency)}{" "}
            {Number(lining.amount || 0).toFixed(2)}
          </div>
        ) : null}
      </div>

      {lining.enabled ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div>
              <div style={panelLabel}>Type</div>
              <select
                style={panelSelect}
                value={lining.type}
                onChange={(event) =>
                  updateLining({
                    type: event.target.value,
                  })
                }
              >
                {CURTAIN_LINING_TYPES.map((option) => (
                  <option key={option.type} value={option.type}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={panelLabel}>Lining Fabric / Code</div>
              <input
                style={panelInput}
                value={lining.fabricCode ?? ""}
                placeholder="Optional fabric/code"
                onChange={(event) =>
                  updateLining({
                    fabricCode: event.target.value,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Brand</div>
              <select
                style={panelSelect}
                value={lining.brandId || ""}
                onChange={(event) => {
                  const selectedBrand = fabricBrands.find(
                    (brand) => brand.id === event.target.value
                  );
                  updateLining({
                    brandId: selectedBrand?.id || "",
                    brandName: selectedBrand?.name || "",
                  });
                }}
              >
                <option value="">No brand selected</option>
                {fabricBrands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={panelLabel}>Charge Method</div>
              <select
                style={panelSelect}
                value={lining.chargeMethod || "meter"}
                onChange={(event) =>
                  updateLining({
                    chargeMethod: event.target.value,
                  })
                }
              >
                <option value="meter">Per meter</option>
                <option value="sqft">Per sqft</option>
              </select>
            </div>
            <div>
              <div style={panelLabel}>
                {lining.chargeMethod === "sqft" ? "Lining Rate / sqft" : "Lining Rate / m"}
              </div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={
                  lining.chargeMethod === "sqft"
                    ? lining.ratePerSqft ?? ""
                    : lining.ratePerMeter ?? ""
                }
                placeholder="0"
                onChange={(event) =>
                  updateLining({
                    [lining.chargeMethod === "sqft" ? "ratePerSqft" : "ratePerMeter"]:
                      event.target.value,
                  })
                }
              />
            </div>
            {lining.chargeMethod === "meter" ? (
              <div>
                <div style={panelLabel}>Meters</div>
                <select
                  style={panelSelect}
                  value={lining.metersMode}
                  onChange={(event) =>
                    updateLining({
                      metersMode: event.target.value,
                    })
                  }
                >
                  <option value="same_as_curtain">Same as fabric meters</option>
                  <option value="custom_override">Override meters</option>
                </select>
              </div>
            ) : (
              <div>
                <div style={panelLabel}>Calculated Sqft</div>
                <div
                  style={{
                    ...panelInput,
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 900,
                    background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                  }}
                >
                  {Number(lining.sqft || 0).toFixed(2)} sqft
                </div>
              </div>
            )}
            {lining.type === "custom" ? (
              <div style={{ gridColumn: isMobile ? "auto" : "span 2" }}>
                <div style={panelLabel}>Custom Lining Label</div>
                <input
                  style={panelInput}
                  value={lining.customLabel ?? ""}
                  placeholder="Imported blackout lining"
                  onChange={(event) =>
                    updateLining({
                      customLabel: event.target.value,
                    })
                  }
                />
              </div>
            ) : null}
            {lining.chargeMethod === "meter" && lining.metersMode === "custom_override" ? (
              <div>
                <div style={panelLabel}>Override Meters</div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={panelInput}
                  value={lining.overrideMeters ?? ""}
                  placeholder={Number(lining.calculatedMeters || 0).toFixed(2)}
                  onChange={(event) =>
                    updateLining({
                      overrideMeters: event.target.value,
                    })
                  }
                />
              </div>
            ) : null}
            <div>
              <div style={panelLabel}>Lining Amount</div>
              <div
                style={{
                  ...panelInput,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 900,
                  color: "#2563eb",
                  background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                }}
              >
                {currencySymbol(currency)} {Number(lining.amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <span>
              {lining.chargeMethod === "sqft" ? (
                <>
                  Lining sqft:{" "}
                  <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {Number(lining.sqft || 0).toFixed(2)} sqft
                  </strong>{" "}
                  charged at rate/sqft.
                </>
              ) : (
                <>
                  Lining meters:{" "}
                  <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {Number(lining.billableMeters || 0).toFixed(2)} m
                  </strong>{" "}
                  from row fabric requirement{" "}
                  {Number(lining.calculatedMeters || 0).toFixed(2)} m.
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() =>
                updateLining({
                  enabled: false,
                })
              }
              style={{
                border: "none",
                borderRadius: 999,
                padding: "7px 11px",
                background: isDark ? "rgba(239,68,68,0.14)" : "#fee2e2",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Remove lining
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

const getCurtainRowSelectedTrackPackage = (row = {}) =>
  findCurtainRowStitchConfig(row, stitchingRates)?.config || null;

const shouldShowCurtainTrackConfigPanel = (row = {}, calc = {}) => {
  if (calc?.isAccessory) return false;
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row);
  const normalizedPackage = selectedTrackPackage
    ? normalizeStitchingSettingForState(selectedTrackPackage)
    : null;
  return Boolean(
    normalizedPackage &&
      (normalizedPackage.includesTrack === true ||
        normalizedPackage.supportsBendedTrack === true ||
        row?.trackConfig?.isBended === true)
  );
};

const getCurtainTrackConfigForUi = (row = {}, calc = {}) => {
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const baseTrackLengthFt =
    num(row.trackLengthFt ?? row.trackLength) ||
    num(selectedTrackPackage.trackLengthFt ?? selectedTrackPackage.trackLength);
  return calc?.trackConfig || calculateCurtainTrackConfig(row, selectedTrackPackage, {
    baseTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(row, unit),
  });
};

const updateCurtainTrackConfig = (rowId, row = {}, calc = {}, patch = {}) => {
  if (!rowId) return;
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const currentTrackConfig = getCurtainTrackConfigForUi(row, calc);
  const nextDraft = {
    ...currentTrackConfig,
    ...patch,
  };
  const normalized = calculateCurtainTrackConfig(
    {
      ...row,
      trackConfig: nextDraft,
    },
    selectedTrackPackage,
    {
      baseTrackLengthFt:
        num(row.trackLengthFt ?? row.trackLength) ||
        num(selectedTrackPackage.trackLengthFt ?? selectedTrackPackage.trackLength),
      measurementUnit: getRowMeasurementUnit(row, unit),
    }
  );
  updateField(rowId, "trackConfig", normalized);
};

const renderCurtainTrackConfigPanel = (row, calc, rowId) => {
  if (!shouldShowCurtainTrackConfigPanel(row, calc)) return null;

  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const normalizedPackage = normalizeStitchingSettingForState(selectedTrackPackage);
  const trackConfig = getCurtainTrackConfigForUi(row, calc);
  const updateTrack = (patch) => updateCurtainTrackConfig(rowId, row, calc, patch);
  const bendSettings = normalizedPackage.bendSettings || DEFAULT_BEND_SETTINGS;
  const allowedShapes = normalizeAllowedBendShapes(bendSettings.allowedAngles);
  const shapeOptions = normalizedPackage.supportsBendedTrack
    ? BENDED_TRACK_SHAPES.filter(
        (entry) => entry.shape === "straight" || allowedShapes.includes(entry.shape)
      )
    : [BENDED_TRACK_SHAPE_MAP.straight];
  const isBendedShape = trackConfig.shape !== "straight";
  const chargeEditable = trackConfig.pricingMethod === "manual";
  const segmentUnit = trackConfig.segmentUnit || getRowMeasurementUnit(row, unit);
  const segmentUnitLabel = unitLabel(segmentUnit);
  const hasSegmentOverride = Number(trackConfig.totalSegmentLength || 0) > 0;
  const layoutPreview = buildBendedTrackLayoutPreview(trackConfig);
  const panelInput = {
    ...input,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelSelect = {
    ...selectInput,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelLabel = {
    fontSize: 11,
    fontWeight: 800,
    color: dashboardMuted,
    marginBottom: 5,
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: isDark
          ? "1px solid rgba(20,184,166,0.24)"
          : "1px solid rgba(153,246,228,0.95)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.94),rgba(15,118,110,0.2))"
          : "linear-gradient(135deg,#ffffff,#ecfeff)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
            Track Shape
          </div>
          <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
            {normalizedPackage.supportsBendedTrack
              ? "This stitching/track package allows bended track charges."
              : "This package includes track but does not support bended track charges."}
          </div>
        </div>
        {trackConfig.bendAmount > 0 ? (
          <div
            style={{
              borderRadius: 999,
              padding: "5px 10px",
              background: isDark ? "rgba(20,184,166,0.18)" : "#ccfbf1",
              color: "#0f766e",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            Bend Amount: {currencySymbol(currency)} {Number(trackConfig.bendAmount || 0).toFixed(2)}
          </div>
        ) : null}
      </div>

      {trackConfig.disabledReason ? (
        <div
          style={{
            borderRadius: 12,
            padding: "8px 10px",
            background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
            color: isDark ? "#fde68a" : "#92400e",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {trackConfig.disabledReason}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(150px, 1fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) minmax(130px, 0.8fr)",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div>
          <div style={panelLabel}>Track Shape</div>
          <select
            style={panelSelect}
            value={trackConfig.shape}
            onChange={(event) => {
              const shape = normalizeBendedTrackShape(event.target.value);
              const shapeMeta = getBendedTrackShapeMeta(shape);
              updateTrack({
                shape,
                isBended: shape !== "straight",
                bendCount: shapeMeta.defaultBendCount,
                angleDeg: shapeMeta.defaultAngleDeg,
                bendLocations: normalizeBendLocations([], {
                  shape,
                  bendCount: shapeMeta.defaultBendCount,
                  angleDeg: shapeMeta.defaultAngleDeg,
                  unit: segmentUnit,
                }),
              });
            }}
          >
            {shapeOptions.map((option) => (
              <option key={option.shape} value={option.shape}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isBendedShape ? (
          <>
            <div>
              <div style={panelLabel}>No. of bends</div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={trackConfig.bendCount ?? ""}
                onChange={(event) =>
                  updateTrack({
                    bendCount: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Angle degrees</div>
              <input
                type="text"
                inputMode="decimal"
                disabled={trackConfig.shape !== "custom"}
                style={{
                  ...panelInput,
                  opacity: trackConfig.shape === "custom" ? 1 : 0.55,
                }}
                value={trackConfig.angleDeg ?? ""}
                onChange={(event) =>
                  updateTrack({
                    angleDeg: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Bend charge / bend</div>
              <input
                type="text"
                inputMode="decimal"
                disabled={!chargeEditable}
                style={{
                  ...panelInput,
                  opacity: chargeEditable ? 1 : 0.62,
                }}
                value={
                  chargeEditable
                    ? trackConfig.manualBendCharge ?? ""
                    : Number(trackConfig.bendChargePerBend || 0).toFixed(2)
                }
                onChange={(event) =>
                  updateTrack({
                    manualBendCharge: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
          </>
        ) : null}
      </div>

      {isBendedShape ? (
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
          Bend Amount = {Number(trackConfig.bendCount || 0).toFixed(2)} x{" "}
          {currencySymbol(currency)} {Number(trackConfig.bendChargePerBend || 0).toFixed(2)} ={" "}
          <strong style={{ color: isDark ? "#99f6e4" : "#0f766e" }}>
            {currencySymbol(currency)} {Number(trackConfig.bendAmount || 0).toFixed(2)}
          </strong>
        </div>
      ) : null}

      {isBendedShape ? (
        <div
          style={{
            borderRadius: 14,
            padding: 12,
            background: isDark ? "rgba(15,23,42,0.48)" : "rgba(255,255,255,0.78)",
            border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #dbeafe",
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: isDark ? "#99f6e4" : "#0f766e" }}>
            Bend Location
          </div>
          {(trackConfig.bendLocations || []).map((location, bendIndex) => (
            <div
              key={`bend-location-${bendIndex}`}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "minmax(180px, 1.2fr) minmax(100px, 0.6fr) minmax(180px, 1fr)",
                gap: 10,
                alignItems: "end",
              }}
            >
              <div>
                <div style={panelLabel}>
                  Bend {bendIndex + 1} Location
                </div>
                <select
                  style={panelSelect}
                  value={getBendLocationOption(location)}
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = buildBendLocationFromOption(
                      event.target.value,
                      bendIndex + 1,
                      nextLocations[bendIndex]?.angleDeg ?? trackConfig.angleDeg,
                      segmentUnit,
                      nextLocations[bendIndex]
                    );
                    updateTrack({ bendLocations: nextLocations });
                  }}
                >
                  {BEND_LOCATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={panelLabel}>Angle</div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={panelInput}
                  value={location.angleDeg ?? trackConfig.angleDeg ?? ""}
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = {
                      ...nextLocations[bendIndex],
                      angleDeg: event.target.value,
                    };
                    updateTrack({ bendLocations: nextLocations });
                  }}
                />
              </div>
              <div>
                <div style={panelLabel}>Custom note</div>
                <input
                  style={panelInput}
                  value={location.note || ""}
                  placeholder="Optional bend note"
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = {
                      ...nextLocations[bendIndex],
                      note: event.target.value,
                    };
                    updateTrack({ bendLocations: nextLocations });
                  }}
                />
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: dashboardMuted, fontWeight: 700 }}>
            {trackConfig.bendLocationSummary || "Choose where each bend happens."}
          </div>
        </div>
      ) : null}

      <details>
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 900,
            color: isDark ? "#99f6e4" : "#0f766e",
          }}
        >
          Advanced Track Segments
        </summary>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 10,
            marginTop: 10,
          }}
        >
          {(trackConfig.segments || DEFAULT_TRACK_CONFIG.segments).map((segment, index) => (
            <div key={segment.label || index}>
              <div style={panelLabel}>
                Segment {segment.label || String.fromCharCode(65 + index)} length ({segmentUnitLabel})
              </div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={segment.length || ""}
                onChange={(event) => {
                  const nextSegments = normalizeTrackSegments(trackConfig.segments, segmentUnit);
                  nextSegments[index] = {
                    ...nextSegments[index],
                    length: event.target.value,
                    unit: segmentUnit,
                  };
                  updateTrack({ segmentUnit, segments: nextSegments });
                }}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 11.5, color: dashboardMuted }}>
          {hasSegmentOverride ? (
            <>
              Total segment length:{" "}
              <strong>
                {formatLengthWithUnit(trackConfig.totalSegmentLength || 0, segmentUnit)}
              </strong>
              {" "} - Pricing length:{" "}
              <strong>{Number(trackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft</strong>
            </>
          ) : (
            "If no segments are entered, the existing default track length logic is used."
          )}
        </div>
        {layoutPreview ? (
          <div style={{ marginTop: 8, fontSize: 11.5, color: isDark ? "#99f6e4" : "#0f766e", fontWeight: 800 }}>
            Track layout: {layoutPreview}
          </div>
        ) : null}
        {formatTrackSegmentsInline(trackConfig) ? (
          <div style={{ marginTop: 4, fontSize: 11.5, color: dashboardMuted }}>
            Segments: {formatTrackSegmentsInline(trackConfig)}
          </div>
        ) : null}
      </details>

      <div>
        <div style={panelLabel}>Track notes</div>
        <input
          style={panelInput}
          value={trackConfig.notes || ""}
          placeholder="Bay window bend, ceiling bend, site note..."
          onChange={(event) => updateTrack({ notes: event.target.value })}
        />
      </div>
    </div>
  );
};

const startNewProductConfig = () => {
  setEditingProductConfigId("");
  setProductConfigDraft(
    createProductConfigDraft({
      name: "New Product",
      calculationType: "sqft_only",
      isDefault: false,
    })
  );
};

const editProductConfig = (config) => {
  const normalized = normalizeProductConfig(config);
  setEditingProductConfigId(normalized.id);
  setProductConfigDraft(normalized);
};

const duplicateProductConfig = (config) => {
  const normalized = normalizeProductConfig(config);
  const duplicateName = `${normalized.name} Copy`;
  setEditingProductConfigId("");
  setProductConfigDraft(
    createProductConfigDraft({
      ...normalized,
      id: `custom-${normalizeProductName(duplicateName).replace(/\s+/g, "-")}-${Date.now()}`,
      name: duplicateName,
      normalizedName: normalizeProductName(duplicateName),
      isDefault: false,
      createdAt: null,
      updatedAt: null,
    })
  );
};

const updateProductConfigDraftField = (field, value) => {
  setProductConfigDraft((prev) =>
    ({
      ...prev,
      [field]: value,
      ...(field === "name" ? { normalizedName: normalizeProductName(value) } : {}),
    })
  );
};

const updateProductConfigRule = (section, patch) => {
  setProductConfigDraft((prev) =>
    ({
      ...prev,
      [section]: {
        ...(prev?.[section] || {}),
        ...patch,
      },
    })
  );
};

const saveProductConfig = async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready. Please try again.", "warning");
    return;
  }
  if (!canManageProductConfigs) {
    notify("You do not have permission to manage product configs.", "warning");
    return;
  }

  const validation = validateProductConfig(productConfigDraft, productConfigs);
  if (!validation.valid) {
    notify(validation.errors[0] || "Please check product settings.", "error");
    return;
  }

  const normalized = {
    ...validation.normalized,
    updatedAt: serverTimestamp(),
    updatedBy: user?.uid || user?.email || "",
    ...(editingProductConfigId
      ? {}
      : {
          createdAt: serverTimestamp(),
          createdBy: user?.uid || user?.email || "",
        }),
  };

  setSavingProductConfigId(validation.normalized.id);
  try {
    await setDoc(
      doc(
        db,
        "users",
        workspaceUid,
        PRODUCT_CONFIG_COLLECTION,
        validation.normalized.id
      ),
      normalized,
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, PRODUCT_CONFIG_COLLECTION);
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
    productConfigsLoadedWorkspaceRef.current = "";
    const localNormalized = normalizeProductConfig({
      ...validation.normalized,
      updatedAt: new Date(),
    });
    setProductConfigs((prev) =>
      mergeProductConfigsWithDefaults(
        [...prev.filter((item) => item.id !== localNormalized.id), localNormalized],
        { ...fabricCalcSettings, minSqft }
      )
    );
    setEditingProductConfigId(localNormalized.id);
    setProductConfigDraft(localNormalized);
    notify("Product configuration saved.", "success");
  } catch (error) {
    devError("PRODUCT CONFIG SAVE FAILED", {
      code: error?.code,
      message: error?.message,
      productId: validation.normalized.id,
    });
    notify("Could not save product configuration.", "error");
  } finally {
    setSavingProductConfigId("");
  }
};

const setProductConfigActive = async (config, active) => {
  if (!workspaceUid || !config?.id) return;
  if (!canManageProductConfigs) {
    notify("You do not have permission to manage product configs.", "warning");
    return;
  }

  setSavingProductConfigId(config.id);
  try {
    await updateDoc(
      doc(db, "users", workspaceUid, PRODUCT_CONFIG_COLLECTION, config.id),
      {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || user?.email || "",
      }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, PRODUCT_CONFIG_COLLECTION);
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
    productConfigsLoadedWorkspaceRef.current = "";
    setProductConfigs((prev) =>
      mergeProductConfigsWithDefaults(
        prev.map((item) =>
          item.id === config.id ? { ...item, active, updatedAt: new Date() } : item
        ),
        { ...fabricCalcSettings, minSqft }
      )
    );
    notify(active ? "Product activated." : "Product deactivated.", "success");
  } catch (error) {
    devError("PRODUCT CONFIG ACTIVE TOGGLE FAILED", {
      code: error?.code,
      message: error?.message,
      productId: config.id,
    });
    notify("Could not update product status.", "error");
  } finally {
    setSavingProductConfigId("");
  }
};

const renderProductCalculationsPage = () => {
  if (!canManageProductConfigs) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Products & Calculations</h2>
        <p style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
          You do not have permission to manage product settings.
        </p>
      </div>
    );
  }

  const draft = normalizeProductConfig(productConfigDraft);
  const getDraftFieldValue = (field, fallback = "") => {
    if (productConfigDraft && Object.prototype.hasOwnProperty.call(productConfigDraft, field)) {
      return getInputValue(productConfigDraft[field]);
    }
    return getInputValue(draft[field] ?? fallback);
  };
  const getDraftRuleInputValue = (section, field, fallback = "") => {
    const rawSection = productConfigDraft?.[section] || {};
    if (Object.prototype.hasOwnProperty.call(rawSection, field)) {
      return getInputValue(rawSection[field]);
    }
    return getInputValue(draft?.[section]?.[field] ?? fallback);
  };
  const previewRow = {
    productId: draft.id,
    productType: draft.name,
    productName: draft.name,
    productConfigSnapshot: buildProductConfigSnapshot(draft),
    width: 100,
    height: 100,
    fabricWidthIn: draft.fabricRules.fabricWidthIn || 54,
    mrp: draft.calculationType.includes("fabric") ? 250 : "",
    sqftRate: draft.sqftRules.defaultRate || 100,
    ratePerFeet: draft.runningFeetRules.defaultRate || 100,
    ratePerLength: draft.runningFeetRules.defaultRate || 100,
    length: 10,
    lengthUnit: draft.runningFeetRules.defaultUnit || "ft",
    ratePerPiece: draft.quantityRules.defaultRate || 1000,
    unitRate: draft.quantityRules.defaultRate || 1000,
    qty: 1,
    unit: draft.quantityRules.defaultUnit || "pcs",
    quantity: 1,
    manualAmount: 1000,
    discount: 0,
  };
  const preview = calculateProductRow(previewRow, draft, {
    stitchingRates,
    fabricCalcSettings,
  });
  const previewFabricRules = resolveFabricRulesForRow(
    previewRow,
    draft,
    fabricCalcSettings
  );
  const draftIsAccessory = isAccessoryProductConfig(draft);

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
    gap: 12,
  };
  const cardStyle = {
    borderRadius: 18,
    padding: 18,
    border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
    background: isDark ? "#0f172a" : "#ffffff",
    boxShadow: isDark
      ? "0 18px 40px rgba(2,6,23,0.22)"
      : "0 18px 34px rgba(15,23,42,0.06)",
  };
  const muted = isDark ? "#94a3b8" : "#64748b";

  const renderCheckbox = (label, checked, onChange) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#cbd5e1" : "#334155",
      }}
    >
      <input type="checkbox" checked={Boolean(checked)} onChange={onChange} />
      {label}
    </label>
  );

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div
        style={{
          ...cardStyle,
          background: isDark
            ? "linear-gradient(135deg,#0f172a,#111827)"
            : "linear-gradient(135deg,#ffffff,#f8fafc)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Products & Calculations</h2>
            <p style={{ margin: "8px 0 0", color: muted, maxWidth: 760 }}>
              Add custom products and choose safe calculation templates. No raw
              JavaScript formulas are allowed, so quotation totals, PDFs,
              workshop exports, and saved quotes stay stable.
            </p>
          </div>
          <Button icon={Plus} variant="primary" onClick={startNewProductConfig}>
            New Product
          </Button>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: muted }}>
          {productConfigsLoadState.loading
            ? "Loading product configs..."
            : productConfigsLoadState.error ||
              `${productConfigs.length} product configs loaded`}
        </div>
      </div>

      <div style={{ ...cardStyle, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
          <thead>
            <tr style={{ color: muted, textAlign: "left", fontSize: 12 }}>
              <th style={{ padding: 10 }}>Product Name</th>
              <th style={{ padding: 10 }}>Category</th>
              <th style={{ padding: 10 }}>Calculation</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }}>Used In</th>
              <th style={{ padding: 10 }}>Last Updated</th>
              <th style={{ padding: 10 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {productConfigs.map((config) => (
              <tr
                key={config.id}
                style={{
                  borderTop: isDark
                    ? "1px solid rgba(148,163,184,0.12)"
                    : "1px solid #e2e8f0",
                }}
              >
                <td style={{ padding: 10, fontWeight: 800 }}>{config.name}</td>
                <td style={{ padding: 10 }}>
                  {PRODUCT_CATEGORY_LABELS[config.category] || config.category}
                </td>
                <td style={{ padding: 10 }}>
                  {PRODUCT_CALCULATION_LABELS[config.calculationType] ||
                    config.calculationType}
                </td>
                <td style={{ padding: 10 }}>
                  <span
                    style={{
                      padding: "4px 8px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 800,
                      background: config.active ? "#dcfce7" : "#fee2e2",
                      color: config.active ? "#166534" : "#991b1b",
                    }}
                  >
                    {config.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: 10 }}>
                  {(config.usedInQuoteTypes || ["curtain"]).join(", ")}
                </td>
                <td style={{ padding: 10, color: muted }}>
                  {config.updatedAt?.toDate
                    ? config.updatedAt.toDate().toLocaleDateString()
                    : config.updatedAt instanceof Date
                    ? config.updatedAt.toLocaleDateString()
                    : "-"}
                </td>
                <td style={{ padding: 10 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button variant="secondary" onClick={() => editProductConfig(config)}>
                      Edit
                    </Button>
                    <Button variant="secondary" onClick={() => duplicateProductConfig(config)}>
                      Duplicate
                    </Button>
                    <Button
                      variant={config.active ? "danger" : "success"}
                      disabled={savingProductConfigId === config.id}
                      onClick={() => setProductConfigActive(config, !config.active)}
                    >
                      {config.active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 18 }}>
        <div>
          <h3 style={{ margin: 0 }}>
            {editingProductConfigId ? "Edit Product" : "Add Product"}
          </h3>
          <p style={{ margin: "6px 0 0", color: muted }}>
            Configure product behavior using controlled templates. Old quotes
            keep their saved product snapshots.
          </p>
        </div>

        <div style={fieldGrid}>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Product Name</label>
            <input
              style={input}
              value={getDraftFieldValue("name")}
              onChange={(event) =>
                updateProductConfigDraftField("name", event.target.value)
              }
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Category</label>
            <select
              style={selectInput}
              value={draft.category}
              onChange={(event) =>
                updateProductConfigDraftField("category", event.target.value)
              }
            >
              {PRODUCT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {PRODUCT_CATEGORY_LABELS[category] || category}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Calculation Type</label>
            <select
              style={selectInput}
              value={draft.calculationType}
              onChange={(event) =>
                setProductConfigDraft((prev) =>
                  createProductConfigDraft({
                    ...prev,
                    calculationType: event.target.value,
                    usesFabric:
                      !isAccessoryCalculationType(event.target.value) &&
                      ["fabric_meter", "sqft_plus_fabric"].includes(event.target.value),
                    supportsLining:
                      !isAccessoryCalculationType(event.target.value) &&
                      ["fabric_meter", "sqft_plus_fabric"].includes(event.target.value),
                    category:
                      isAccessoryCalculationType(event.target.value) &&
                      (!prev?.category || ["curtain", "blind", "soft_furnishing"].includes(prev.category))
                        ? event.target.value === "running_length_rate"
                          ? "track_channel"
                          : "accessory"
                        : prev?.category,
                    requiredFields: {},
                    fabricRules: isAccessoryCalculationType(event.target.value)
                      ? { enabled: false, fullnessSource: "none" }
                      : { ...(prev?.fabricRules || {}), enabled: ["fabric_meter", "sqft_plus_fabric"].includes(event.target.value) },
                    workshopRules: isAccessoryCalculationType(event.target.value)
                      ? {
                          ...(prev?.workshopRules || {}),
                          includeFabricSummary: false,
                          includeRollOptimizer: false,
                        }
                      : prev?.workshopRules || {},
                    isDefault: prev?.isDefault === true,
                  })
                )
              }
            >
              {PRODUCT_CALCULATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {PRODUCT_CALCULATION_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          {renderCheckbox("Active", draft.active, (event) =>
            updateProductConfigDraftField("active", event.target.checked)
          )}
          {renderCheckbox("Use in Curtain Quotations", true, () => {})}
          {!draftIsAccessory
            ? renderCheckbox("Show Fabric / Material Field", draft.displayFields.showFabric, (event) =>
                updateProductConfigRule("displayFields", {
                  showFabric: event.target.checked,
                })
              )
            : null}
          {renderCheckbox("Show in Customer PDF", draft.pdfRules.showMeasurements, (event) =>
            updateProductConfigRule("pdfRules", {
              showMeasurements: event.target.checked,
              showFabric: event.target.checked,
              showSqft: event.target.checked,
              showRate: event.target.checked,
            })
          )}
        </div>

        {(draft.calculationType === "sqft_only" ||
          draft.calculationType === "sqft_plus_fabric") && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Sqft Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Rate/sqft</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("sqftRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      defaultRate: event.target.value,
                      rateSource: "row_rate",
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Minimum Billing Sqft</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("sqftRules", "minSqft")}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      minSqft: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Rounding</label>
                <select
                  style={selectInput}
                  value={draft.sqftRules.rounding}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      rounding: event.target.value,
                    })
                  }
                >
                  <option value="none">None</option>
                  <option value="ceil">Round Up</option>
                  <option value="round_2">2 decimals</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {(draft.calculationType === "fabric_meter" ||
          draft.calculationType === "sqft_plus_fabric") && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Fabric Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Fabric Field Label</label>
                <input
                  style={input}
                  value={getDraftRuleInputValue("displayFields", "fabricLabel", "Fabric")}
                  onChange={(event) =>
                    updateProductConfigRule("displayFields", {
                      fabricLabel: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Fabric Width</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "fabricWidthIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fabricWidthIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>
                  {draft.fabricRules.fullnessSource === "stitching"
                    ? "Fallback Fullness"
                    : "Fullness"}
                </label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "fullness")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fullness: event.target.value,
                      enabled: true,
                    })
                  }
                />
                <div style={{ marginTop: 6, fontSize: 11, color: muted, lineHeight: 1.45 }}>
                  {draft.fabricRules.fullnessSource === "stitching"
                    ? "Curtain fullness normally comes from the selected stitching style. This fallback is used only if the stitching style has no fullness."
                    : draft.fabricRules.meterFormulaType === "roman_panels"
                    ? "Roman blinds usually use 1x fullness. Change only if your production method requires extra fullness."
                    : "Product-level fullness is used directly for this fabric calculation."}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Fullness Source</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.fullnessSource || "product"}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fullnessSource: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="stitching">Stitching Style</option>
                  <option value="product">Product Config</option>
                  <option value="none">None / 1x</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Top Hem (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "topHemIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      topHemIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Bottom Hem (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "bottomHemIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      bottomHemIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Side Allowance per Side (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "sideAllowancePerSideIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      sideAllowancePerSideIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Panel Rounding</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.panelRounding}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      panelRounding: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="ceil_full">Ceil full panel</option>
                  <option value="ceil_half">Ceil half panel</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Meter Formula</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.meterFormulaType}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      meterFormulaType: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="curtain_panels">Curtain panels</option>
                  <option value="roman_panels">Roman panels</option>
                  <option value="simple_length">Simple length</option>
                </select>
              </div>
              <div
                style={{
                  gridColumn: isMobile ? "auto" : "1 / -1",
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(15,23,42,0.7)" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.18)"
                    : "1px solid #e2e8f0",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Total Width Deduction</div>
                  <strong>{previewFabricRules.usableWidthDeductionIn} in</strong>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Usable Fabric Width</div>
                  <strong>{previewFabricRules.usableFabricWidthIn} in</strong>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Total Drop Allowance</div>
                  <strong>{previewFabricRules.dropAllowanceIn} in</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {draft.calculationType === "running_length_rate" && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Running Length Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Rate per Length</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("runningFeetRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("runningFeetRules", {
                      defaultRate: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Length Unit</label>
                <select
                  style={selectInput}
                  value={draft.runningFeetRules.defaultUnit || "ft"}
                  onChange={(event) =>
                    updateProductConfigRule("runningFeetRules", {
                      defaultUnit: event.target.value,
                      formula: "manual_feet",
                      enabled: true,
                    })
                  }
                >
                  <option value="ft">ft</option>
                  <option value="m">m</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {draft.calculationType === "quantity_rate" && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Quantity Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Unit Rate</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("quantityRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("quantityRules", {
                      defaultRate: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Unit</label>
                <select
                  style={selectInput}
                  value={draft.quantityRules.defaultUnit || "pcs"}
                  onChange={(event) =>
                    updateProductConfigRule("quantityRules", {
                      defaultUnit: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="pcs">pcs</option>
                  <option value="set">set</option>
                  <option value="nos">nos</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...cardStyle, boxShadow: "none" }}>
          <h4 style={{ marginTop: 0 }}>Workshop / Optimizer</h4>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {renderCheckbox(
              "Include in Workshop PDF",
              draft.workshopRules.includeInWorkshopPdf,
              (event) =>
                updateProductConfigRule("workshopRules", {
                  includeInWorkshopPdf: event.target.checked,
                })
            )}
            {!draftIsAccessory
              ? renderCheckbox(
                  "Include Fabric Summary",
                  draft.workshopRules.includeFabricSummary,
                  (event) =>
                    updateProductConfigRule("workshopRules", {
                      includeFabricSummary: event.target.checked,
                    })
                )
              : null}
            {!draftIsAccessory
              ? renderCheckbox(
                  "Include in Roll Optimizer",
                  draft.workshopRules.includeRollOptimizer,
                  (event) =>
                    updateProductConfigRule("workshopRules", {
                      includeRollOptimizer: event.target.checked,
                    })
                )
              : null}
          </div>
          {draftIsAccessory ? (
            <div style={{ marginTop: 10, fontSize: 12, color: muted, lineHeight: 1.5 }}>
              Accessory products do not enter fabric summary, half-piece reuse,
              or the roll optimizer.
            </div>
          ) : null}
        </div>

        <div
          style={{
            ...cardStyle,
            boxShadow: "none",
            background: isDark ? "#020617" : "#f8fafc",
          }}
        >
          <h4 style={{ marginTop: 0 }}>Live Preview</h4>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", color: muted }}>
            {!draftIsAccessory ? (
              <>
                <span>Width: 100 in</span>
                <span>Height: 100 in</span>
                {draft.displayFields.showSqft ? (
                  <span>Sqft: {preview.billableSqft.toFixed(2)}</span>
                ) : null}
                {draft.displayFields.showFabricMeters ? (
                  <span>Fabric: {preview.fabricMeters.toFixed(2)} m</span>
                ) : null}
              </>
            ) : draft.calculationType === "running_length_rate" ? (
              <>
                <span>Length: {preview.runningLength || 10} {draft.runningFeetRules.defaultUnit || "ft"}</span>
                <span>Rate: {currencySymbol(currency)} {Number(preview.ratePerLength || 0).toFixed(2)}</span>
              </>
            ) : draft.calculationType === "quantity_rate" ? (
              <>
                <span>Qty: {preview.quantity || 1} {draft.quantityRules.defaultUnit || "pcs"}</span>
                <span>Unit Rate: {currencySymbol(currency)} {Number(preview.unitRate || 0).toFixed(2)}</span>
              </>
            ) : (
              <span>Manual amount product</span>
            )}
            <strong style={{ color: isDark ? "#bfdbfe" : "#1d4ed8" }}>
              Line Total: {currencySymbol(currency)} {preview.rowTotal.toFixed(2)}
            </strong>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            icon={Save}
            variant="primary"
            onClick={saveProductConfig}
            disabled={Boolean(savingProductConfigId)}
          >
            {savingProductConfigId ? "Saving..." : "Save Product"}
          </Button>
          <Button variant="secondary" onClick={startNewProductConfig}>
            Clear Form
          </Button>
        </div>
      </div>
    </div>
  );
};

const compactCardStyle = {
  borderRadius: 18,
  border: isDark
    ? "1px solid rgba(148,163,184,0.18)"
    : "1px solid rgba(226,232,240,0.95)",
  background: isDark ? "#0f172a" : "#ffffff",
  boxShadow: isDark
    ? "0 18px 40px rgba(2,6,23,0.25)"
    : "0 18px 36px rgba(15,23,42,0.06)",
  padding: isMobile ? 14 : 18,
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: isDark ? "#94a3b8" : "#64748b",
  marginBottom: 8,
};

const rowActionCellStyle = {
  minWidth: isMobile ? 84 : 76,
  width: isMobile ? 84 : 76,
  display: "flex",
  gap: 6,
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
};

const renderRowActionIconButton = ({
  label,
  title = label,
  color,
  icon: Icon,
  onClick,
  disabled = false,
}) => {
  const size = isMobile ? 36 : 32;

  return (
    <button
      type="button"
      className="row-action-btn"
      title={title}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: 9,
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: color,
        color: "#ffffff",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        lineHeight: 1,
        padding: 0,
        boxShadow: isDark
          ? "0 8px 18px rgba(2,6,23,0.24)"
          : "0 8px 18px rgba(15,23,42,0.12)",
      }}
    >
      <Icon size={isMobile ? 18 : 16} strokeWidth={2.8} aria-hidden="true" />
    </button>
  );
};

const rowSummaryKey = (row, index) =>
  getWorkshopRowStableId(row) || `curtain-row-${index}`;

const renderCurtainCompactRow = (r, i) => {
  const c = getCurtainProductionAdjustedRowCalc(r, i);
  const rowRules = getRowProductRules(r);
  const orderedFabricAudit = getCurtainOrderedFabricDisplayTrace(r, i, c);
  const orderedFabricTooltip = getOrderedFabricTooltipText(orderedFabricAudit);
  const requiredFields = { ...rowRules.requiredFields,
    mrpPerMeter: getProductPricingRequirements(rowRules.config).requiresMrpPerMeter };
  const rateMeta = getRowRateFieldMeta(r);
  const secondaryMeta = getRowSecondaryFieldMeta(r);
  const fabricInputVisible = shouldShowFabricInput(r, rowRules.config);
  const fabricInputEnabled = shouldEnableFabricInput(r, rowRules.config);
  const fabricInputLabel = getFabricInputLabel(rowRules.config);
  const showRepeatControls = Boolean(rowRules.config?.fabricRules?.enabled);
  const rowKey = rowSummaryKey(r, i);
  const rowId = rowKey;
  const rowValidationMatches =
    quoteRowValidationError?.quoteType === CURTAIN_QUOTE_TYPE &&
    (String(quoteRowValidationError?.rowId || "") === String(rowId) ||
      Number(quoteRowValidationError?.rowIndex) === i);
  const compactRowStyle = rowValidationMatches
    ? {
        ...compactCardStyle,
        border: "2px solid #dc2626",
        boxShadow: isDark
          ? "0 16px 34px rgba(127,29,29,0.22)"
          : "0 16px 34px rgba(220,38,38,0.12)",
      }
    : compactCardStyle;
  const isExpanded = expandedCurtainRows[rowKey] ?? i === 0;
  const fabricKey =
    normalizeFabricKey(getCurtainRowFabricKey(r)) ||
    safeFabricKey(r.fabric);
  const currentRepeat = normalizeCurtainRepeatEntry(repeatSettings?.[fabricKey] || {});
  const summaryTitle = r.item || `Item ${i + 1}`;
  const stitchLabel = getCurtainRowStitchLabel(r, stitchingRates, rowRules.config);
  const rowTrackConfig = getCurtainTrackConfigForUi(r, c);
  const showTrackConfigPanel = shouldShowCurtainTrackConfigPanel(r, c);
  const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
    hasFabricMeterComponent: Boolean(c.isCurtain || c.isRoman),
    fabricGrossAmount: c.fabricGrossAmount || 0,
  });
  const discountInputEnabled = rowDiscountScope !== "none";
  const discountLabel = rowDiscountScope === "fabric_only" ? "Fabric Disc %" : "Disc %";
  const discountTooltip =
    rowDiscountScope === "fabric_only"
      ? "Applies only to fabric amount."
      : rowDiscountScope === "line_total"
      ? "Applies to this line amount."
      : "Discount is disabled for this product.";

  return (
    <div
      key={rowKey}
      data-quote-row-id={rowId}
      data-quote-row-index={i}
      style={compactRowStyle}
    >
      <button
        type="button"
        onClick={() => toggleCurtainRowExpanded(rowKey)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "grid", gap: 6, flex: 1 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              {summaryTitle}
            </span>
            <span
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
                color: "#2563eb",
              }}
            >
              {r.productType || "Curtains"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 13,
              color: isDark ? "#cbd5e1" : "#475569",
            }}
          >
            <span>
              {displayValue(r.width, unit)} x {displayValue(r.height, unit)} {unitLabel(unit)}
            </span>
            <span>{getDisplayFabricLabel(r)}</span>
            <span>{stitchLabel}</span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 12,
              color: isDark ? "#94a3b8" : "#64748b",
            }}
          >
            <span title={orderedFabricTooltip}>
              Ordered Fabric {c.fabricMeters.toFixed(2)} m{" "}
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
                  color: isDark ? "#bfdbfe" : "#1d4ed8",
                  fontSize: 10,
                  fontWeight: 900,
                }}
              >
                i
              </span>
            </span>
            {c.lining?.enabled ? (
              <span>
                Lining {getCurtainLiningLabel(c.lining)}{" "}
                {Number(c.lining.billableMeters || 0).toFixed(2)} m
              </span>
            ) : null}
            {rowTrackConfig?.isBended ? (
              <span>
                Track {getBendedTrackShapeMeta(rowTrackConfig.shape).label},{" "}
                {Number(rowTrackConfig.bendCount || 0).toFixed(0)} bend
                {Number(rowTrackConfig.bendCount || 0) === 1 ? "" : "s"}
                {rowTrackConfig.bendLocationSummary
                  ? ` - ${rowTrackConfig.bendLocationSummary}`
                  : ""}
                {formatTrackSegmentsInline(rowTrackConfig)
                  ? ` - ${formatTrackSegmentsInline(rowTrackConfig)}`
                  : ""}
              </span>
            ) : null}
            <span>Total {currencySymbol(currency)} {c.rowTotal.toFixed(2)}</span>
          </div>
        </div>

        <div
          style={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isDark ? "#111827" : "#f1f5f9",
            color: isDark ? "#e2e8f0" : "#334155",
          }}
        >
          {isExpanded ? (
            <ChevronUp size={18} strokeWidth={2.25} />
          ) : (
            <ChevronDown size={18} strokeWidth={2.25} />
          )}
        </div>
      </button>

      {rowValidationMatches ? (
        <div
          role="alert"
          style={{
            marginTop: 12,
            borderRadius: 14,
            border: isDark ? "1px solid rgba(248,113,113,0.4)" : "1px solid #fecaca",
            background: isDark ? "rgba(127,29,29,0.34)" : "#fef2f2",
            color: isDark ? "#fecaca" : "#991b1b",
            padding: "10px 12px",
            fontSize: 12.5,
            fontWeight: 800,
            lineHeight: 1.45,
          }}
        >
          <div>{quoteRowValidationError.message}</div>
          <div style={{ marginTop: 4, opacity: 0.78 }}>
            Field: {quoteRowValidationError.failedFieldLabel || quoteRowValidationError.failedField || "Field"}
            {" "}· Current value: {String(quoteRowValidationError.failedValue ?? "") || "empty"}
          </div>
        </div>
      ) : null}

      {isExpanded ? (
        <div style={{ display: "grid", gap: 18, marginTop: 18 }}>
          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Product & Item</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "minmax(0, 1fr) minmax(180px, 220px)",
                gap: 12,
              }}
            >
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Item</label>
                <input
                  style={input}
                  value={r.item}
                  onChange={(e) => updateField(rowId, "item", e.target.value)}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Product</label>
                {(() => {
                  const savedProductOption = getCurtainRowSavedProductOption(r);
                  return (
                <select
                  style={selectInput}
                  value={getCurtainRowProductSelectValue(r)}
                  onChange={(e) => updateField(rowId, "productType", e.target.value)}
                >
                  {savedProductOption ? (
                    <option value={savedProductOption.value}>{savedProductOption.label}</option>
                  ) : null}
                  {curtainProductOptions.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                  );
                })()}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Dimensions</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  Width ({unitLabel(unit)})
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  style={input}
    aria-label={`Width for ${r.item || 'window'}`}
                  value={getDimensionInputValueForUnit(r.width, unit)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const storedValue = getStoredDimensionValueFromInput(v, unit);
                    if (storedValue == null) return;
                    updateField(rowId, "width", storedValue);
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  Height ({unitLabel(unit)})
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  style={input}
    aria-label={`Height for ${r.item || 'window'}`}
                  value={getDimensionInputValueForUnit(r.height, unit)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const storedValue = getStoredDimensionValueFromInput(v, unit);
                    if (storedValue == null) return;
                    updateField(rowId, "height", storedValue);
                  }}
                />
              </div>
              <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Fabric Width (in)</label>
                <input
                  type="text"
                  style={input}
                  value={
                    r.fabricWidthIn === "" || r.fabricWidthIn == null
                      ? ""
                      : r.fabricWidthIn
                  }
                  onChange={(e) =>
                    updateCurtainRowFabricWidthManual(rowId, e.target.value)
                  }
                  disabled={!requiredFields.fabricWidth}
                />
                {r.fabricWidthManuallyOverridden ? (
                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: isDark ? "#bfdbfe" : "#1d4ed8",
                    }}
                  >
                    Manual width
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {fabricInputVisible || showRepeatControls ? (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>
              {showRepeatControls ? "Fabric & Repeat" : fabricInputLabel}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : showRepeatControls
                  ? "minmax(0, 1fr) minmax(0, 1fr)"
                  : "minmax(0, 1fr)",
                gap: 12,
              }}
            >
              {fabricInputVisible ? (
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, opacity: 0.75 }}>{fabricInputLabel}</label>
                {renderFabricCatalogueCombobox({
                  row: r,
                  rowId,
                  rowIndex: i,
                  label: fabricInputLabel || "Fabric / Code",
                  disabled: !fabricInputEnabled,
                  compact: true,
                })}
              </div>
              ) : null}
              {showRepeatControls ? (
              <div
                style={{
                  borderRadius: 14,
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.16)"
                    : "1px solid #dbe4f0",
                  padding: 12,
                  background: isDark ? "#020617" : "#f8fafc",
                  display: "grid",
                  gap: 10,
                }}
              >
                <label style={{ fontSize: 12, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={currentRepeat.verticalEnabled || false}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        verticalEnabled: e.target.checked,
                        verticalRepeatValue: currentRepeat.verticalRepeatValue || "",
                        verticalRepeatUnit: currentRepeat.verticalRepeatUnit || "in",
                        verticalRepeatIn: currentRepeat.verticalRepeatInch || "",
                        verticalRepeatInch: currentRepeat.verticalRepeatInch || "",
                      })
                    }
                  />
                  {" "}Vertical Repeat
                </label>
                {currentRepeat.verticalEnabled ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) 74px",
                      gap: 8,
                    }}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      style={input}
                      placeholder={`Repeat (${currentRepeat.verticalRepeatUnit || "in"})`}
                      value={currentRepeat.verticalRepeatValue ?? ""}
                      onChange={(e) =>
                        updateFabricRepeatMeasurementValue(
                          fabricKey,
                          "vertical",
                          e.target.value,
                          currentRepeat
                        )
                      }
                    />
                    <select
                      style={selectInput}
                      value={currentRepeat.verticalRepeatUnit || "in"}
                      onChange={(e) =>
                        updateFabricRepeatMeasurementUnit(
                          fabricKey,
                          "vertical",
                          e.target.value,
                          currentRepeat
                        )
                      }
                    >
                      <option value="in">in</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>
                ) : null}

                <label style={{ fontSize: 12, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={currentRepeat.horizontalEnabled || false}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        horizontalEnabled: e.target.checked,
                        horizontalRepeatValue: currentRepeat.horizontalRepeatValue || "",
                        horizontalRepeatUnit: currentRepeat.horizontalRepeatUnit || "in",
                        horizontalRepeatIn: currentRepeat.horizontalRepeatInch || "",
                        horizontalRepeatInch: currentRepeat.horizontalRepeatInch || "",
                      })
                    }
                  />
                  {" "}Horizontal Repeat
                </label>
                {currentRepeat.horizontalEnabled ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) 74px",
                      gap: 8,
                    }}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      style={input}
                      placeholder={`Repeat (${currentRepeat.horizontalRepeatUnit || "in"})`}
                      value={currentRepeat.horizontalRepeatValue ?? ""}
                      onChange={(e) =>
                        updateFabricRepeatMeasurementValue(
                          fabricKey,
                          "horizontal",
                          e.target.value,
                          currentRepeat
                        )
                      }
                    />
                    <select
                      style={selectInput}
                      value={currentRepeat.horizontalRepeatUnit || "in"}
                      onChange={(e) =>
                        updateFabricRepeatMeasurementUnit(
                          fabricKey,
                          "horizontal",
                          e.target.value,
                          currentRepeat
                        )
                      }
                    >
                      <option value="in">in</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>
                ) : null}
              </div>
              ) : null}
            </div>
          </div>
          ) : null}

          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Stitching & Pricing</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(4, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              {requiredFields.mrpPerMeter ? (
                <div>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>MRP/m</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r.mrp ?? ""}
                    onChange={(e) => updateField(rowId, "mrp", e.target.value)}
                    onBlur={(e) =>
                      handleCurtainRowMrpBlur(rowId, {
                        ...r,
                        mrp: e.target.value,
                        mrpPerMeter: e.target.value,
                      })
                    }
                  />
                </div>
              ) : null}
              {!rateMeta.hidden ? (
                <div>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>
                    {rateMeta.label}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={rateMeta.disabled}
                    style={{
                      ...input,
                      opacity: rateMeta.disabled ? 0.35 : 1,
                    }}
                    value={rateMeta.field ? r[rateMeta.field] ?? "" : ""}
                    onChange={(e) => {
                      if (!rateMeta.field) return;
                      updateField(rowId, rateMeta.field, e.target.value);
                    }}
                  />
                  {renderProductRateStatus(r, rowId)}
                </div>
              ) : null}
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>{discountLabel}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={!discountInputEnabled}
                  title={discountTooltip}
                  style={{
                    ...input,
                    opacity: discountInputEnabled ? 1 : 0.45,
                  }}
                  value={r.discount ?? ""}
                  onChange={(e) => updateField(rowId, "discount", e.target.value)}
                />
                <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
                  {discountTooltip}
                </div>
                {!discountInputEnabled ? (
                  <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
                    Discount disabled
                  </div>
                ) : null}
              </div>
              {requiredFields.stitching ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>Stitch</label>
                  <select
                    style={selectInput}
                    value={r.stitchingStyle || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateField(rowId, "stitchingStyle", val);
                      if (val !== "Eyelet (Custom)") {
                        updateField(rowId, "customStitchRate", "");
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {Object.keys(stitchingRates).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {!secondaryMeta.disabled && secondaryMeta.field !== "customStitchRate" ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>
                    {secondaryMeta.label}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r[secondaryMeta.field] ?? ""}
                    onChange={(e) =>
                      updateField(rowId, secondaryMeta.field, e.target.value)
                    }
                  />
                </div>
              ) : isCurtain(r.productType) && isEyeletCustom(r) ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>Custom Stitch Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r.customStitchRate}
                    onChange={(e) =>
                      updateField(rowId, "customStitchRate", e.target.value)
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          {showTrackConfigPanel ? (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={sectionLabelStyle}>Track Shape</div>
              {renderCurtainTrackConfigPanel(r, c, rowId)}
            </div>
          ) : null}

          {curtainRowSupportsLining(r, c) ? (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={sectionLabelStyle}>Lining</div>
              {renderCurtainLiningPanel(r, c, rowId)}
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
              padding: 14,
              borderRadius: 16,
              background: isDark ? "#020617" : "#f8fafc",
              border: isDark
                ? "1px solid rgba(148,163,184,0.16)"
                : "1px solid #e2e8f0",
            }}
          >
            <div>
              <div style={sectionLabelStyle}>Ordered Fabric</div>
              <div
                title={orderedFabricTooltip}
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{c.fabricMeters.toFixed(2)} m</span>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  i
                </span>
              </div>
            </div>
            <div>
              <div style={sectionLabelStyle}>Pieces</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>
                {Number(c.totalPieces || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={sectionLabelStyle}>Total</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>
                {currencySymbol(currency)} {c.rowTotal.toFixed(2)}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ ...rowActionCellStyle, justifyContent: "flex-start" }}>
              {renderRowActionIconButton({
                label: "Add row below",
                title: "Add row below",
                color: "#16a34a",
                icon: Plus,
                onClick: () => insertRowAfter(i),
              })}
              {renderRowActionIconButton({
                label: "Delete row",
                title: "Delete row",
                color: "#dc2626",
                icon: Trash2,
                onClick: () => {
                  removeQuoteRow(getWorkshopRowStableId(r));
                },
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const renderCurtainAccessoryRow = (r, i, displayIndex = 0) => {
  const c = getCurtainProductionAdjustedRowCalc(r, i);
  const rowRules = getRowProductRules(r);
  const calculationType = rowRules.calculationType;
  const isLengthRow = calculationType === "running_length_rate";
  const isManualRow = calculationType === "manual_amount";
  const accessoryUnit =
    r.unit ||
    rowRules.config?.quantityRules?.defaultUnit ||
    (isManualRow ? "nos" : "pcs");
  const lengthUnit =
    r.lengthUnit || rowRules.config?.runningFeetRules?.defaultUnit || "ft";
  const rowId = getWorkshopRowStableId(r) || `accessory-${i}`;
  const rowValidationMatches =
    quoteRowValidationError?.quoteType === CURTAIN_QUOTE_TYPE &&
    (String(quoteRowValidationError?.rowId || "") === String(rowId) ||
      Number(quoteRowValidationError?.rowIndex) === i);
  const validationFieldName = String(quoteRowValidationError?.failedField || "").toLowerCase();
  const fieldMatchesValidation = (fieldNames = []) =>
    rowValidationMatches &&
    fieldNames.some((fieldName) =>
      validationFieldName.includes(String(fieldName || "").toLowerCase())
    );
  const invalidInputAccent = {
    border: "2px solid #dc2626",
    boxShadow: "0 0 0 3px rgba(220,38,38,0.16)",
  };
  const inputStyleForValidation = (baseStyle, fieldNames = []) =>
    fieldMatchesValidation(fieldNames) ? { ...baseStyle, ...invalidInputAccent } : baseStyle;
  const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
    hasFabricMeterComponent: false,
    fabricGrossAmount: c.fabricGrossAmount || 0,
  });
  const discountInputEnabled = rowDiscountScope !== "none";
  const discountTooltip =
    rowDiscountScope === "fabric_only"
      ? "Applies only to fabric amount."
      : rowDiscountScope === "line_total"
      ? "Applies to this line amount."
      : "Discount is disabled for this product.";

  return (
    <div
      key={getWorkshopRowStableId(r) || `accessory-${i}`}
      data-quote-row-id={rowId}
      data-quote-row-index={i}
      style={{
        borderRadius: 18,
        border: rowValidationMatches
          ? "2px solid #dc2626"
          : isDark
          ? "1px solid rgba(96,165,250,0.22)"
          : "1px solid rgba(191,219,254,0.95)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.96),rgba(30,41,59,0.86))"
          : "linear-gradient(135deg,#ffffff,#eff6ff)",
        padding: isCompact ? 14 : 16,
        display: "grid",
        gap: 12,
        boxShadow: isDark
          ? "0 16px 34px rgba(2,6,23,0.28)"
          : "0 16px 34px rgba(37,99,235,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Add-on #{displayIndex + 1}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, marginTop: 2 }}>
            {r.item || r.productName || "Accessory"}
          </div>
        </div>
        <div
          style={{
            borderRadius: 999,
            padding: "7px 12px",
            background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
            color: "#2563eb",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          {PRODUCT_CALCULATION_LABELS[calculationType] || calculationType}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : "minmax(130px,1fr) minmax(160px,1.2fr) minmax(160px,1.4fr)",
          gap: 10,
        }}
      >
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Item / Location</label>
          <input
            style={inputStyleForValidation(input, ["item", "name"])}
            value={r.item ?? ""}
            onChange={(event) => updateField(rowId, "item", event.target.value)}
            placeholder="Living Room"
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Product</label>
          {(() => {
            const savedProductOption = getCurtainRowSavedProductOption(r);
            return (
              <select
                style={inputStyleForValidation(selectInput, ["product"])}
                value={getCurtainRowProductSelectValue(r)}
                onChange={(event) => updateField(rowId, "productType", event.target.value)}
              >
                {savedProductOption ? (
                  <option value={savedProductOption.value}>{savedProductOption.label}</option>
                ) : null}
                {curtainProductOptions.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            );
          })()}
        </div>
        <div>
          {renderProductRateStatus(r, rowId)}
          <label style={{ fontSize: 12, color: dashboardMuted }}>Description / Model / Code</label>
          <input
            style={inputStyleForValidation(input, ["description", "modelCode"])}
            value={r.description ?? r.modelCode ?? ""}
            onChange={(event) => {
              updateField(rowId, "description", event.target.value);
              updateField(rowId, "modelCode", event.target.value);
            }}
            placeholder="Model, code, note"
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "repeat(2,minmax(0,1fr))"
            : "repeat(7,minmax(0,1fr))",
          gap: 10,
          alignItems: "end",
        }}
      >
        {isLengthRow ? (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Length</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["length"])}
                value={r.length ?? ""}
                onChange={(event) => updateField(rowId, "length", event.target.value)}
                placeholder="30"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <select
                style={selectInput}
                value={lengthUnit}
                onChange={(event) => updateField(rowId, "lengthUnit", event.target.value)}
              >
                <option value="ft">ft</option>
                <option value="m">m</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Rate</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["ratePerLength", "rate"])}
                value={r.ratePerLength ?? ""}
                onChange={(event) => updateField(rowId, "ratePerLength", event.target.value)}
                placeholder="Rate"
              />
            </div>
          </>
        ) : isManualRow ? (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Qty</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["qty", "quantity"])}
                value={r.qty ?? r.quantity ?? 1}
                onChange={(event) => {
                  updateField(rowId, "qty", event.target.value);
                  updateField(rowId, "quantity", event.target.value);
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <input
                style={input}
                value={accessoryUnit}
                onChange={(event) => updateField(rowId, "unit", event.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Amount</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["manualAmount", "amount"])}
                value={r.manualAmount ?? ""}
                onChange={(event) => updateField(rowId, "manualAmount", event.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Qty</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["qty", "quantity"])}
                value={r.qty ?? r.quantity ?? ""}
                onChange={(event) => {
                  updateField(rowId, "qty", event.target.value);
                  updateField(rowId, "quantity", event.target.value);
                }}
                placeholder="1"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <input
                style={input}
                value={accessoryUnit}
                onChange={(event) => updateField(rowId, "unit", event.target.value)}
                placeholder="pcs"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit Rate</label>
              <input
                type="text"
                inputMode="decimal"
                style={inputStyleForValidation(input, ["unitRate", "rate"])}
                value={r.unitRate ?? ""}
                onChange={(event) => updateField(rowId, "unitRate", event.target.value)}
                placeholder="Rate"
              />
            </div>
          </>
        )}

        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>
            {rowDiscountScope === "fabric_only" ? "Fabric Disc %" : "Disc %"}
          </label>
          <input
            type="text"
            inputMode="decimal"
            disabled={!discountInputEnabled}
            title={discountTooltip}
            style={{
              ...input,
              ...(fieldMatchesValidation(["discount"]) ? invalidInputAccent : {}),
              opacity: discountInputEnabled ? 1 : 0.45,
            }}
            value={r.discount ?? ""}
            onChange={(event) => updateField(rowId, "discount", event.target.value)}
            placeholder="0"
          />
          <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
            {discountTooltip}
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>GST</label>
          <select
            style={selectInput}
            value={String(resolveCurtainAccessoryRowGstRate(r))}
            onChange={(event) => updateField(rowId, "gstRate", event.target.value)}
          >
            {[0, 5, 12, 18].map((rate) => (
              <option key={rate} value={rate}>
                {rate === 0 ? "No GST" : `${rate}%`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Total</label>
          <div
            style={{
              ...input,
              minHeight: 36,
              display: "flex",
              alignItems: "center",
              fontWeight: 900,
              color: "#2563eb",
              background: isDark ? "rgba(15,23,42,0.5)" : "#ffffff",
            }}
          >
            {currencySymbol(currency)} {Number(c.rowTotal || 0).toFixed(2)}
          </div>
        </div>
        <div style={{ ...rowActionCellStyle, justifyContent: "flex-start" }}>
          {renderRowActionIconButton({
            label: "Add accessory row",
            title: "Add accessory row",
            color: "#16a34a",
            icon: Plus,
            onClick: addCurtainAccessoryRow,
          })}
          {renderRowActionIconButton({
            label: "Delete row",
            title: "Delete row",
            color: "#dc2626",
            icon: Trash2,
            onClick: () => {
              removeQuoteRow(getWorkshopRowStableId(r));
            },
          })}
        </div>
      </div>
      {rowValidationMatches ? (
        <div
          role="alert"
          style={{
            borderRadius: 14,
            border: isDark ? "1px solid rgba(248,113,113,0.4)" : "1px solid #fecaca",
            background: isDark ? "rgba(127,29,29,0.34)" : "#fef2f2",
            color: isDark ? "#fecaca" : "#991b1b",
            padding: "10px 12px",
            fontSize: 12.5,
            fontWeight: 800,
            lineHeight: 1.45,
          }}
        >
          <div>{quoteRowValidationError.message}</div>
          <div style={{ marginTop: 4, opacity: 0.78 }}>
            Field: {quoteRowValidationError.failedFieldLabel || quoteRowValidationError.failedField || "Field"}
            {" "}· Current value: {String(quoteRowValidationError.failedValue ?? "") || "empty"}
          </div>
        </div>
      ) : null}
    </div>
  );
};


// === Column Width Presets (global inside component) ===
const colItem     = { width: 120 };
const colProduct  = { width: 130 };
const colFabric   = { width: 210 };
const colXS       = { width: 56 };
const colS        = { width: 80 };
const colWH       = { width: 70 };
const colM        = { width: 70 };
const colStitch   = { width: 130 };
const colLining   = { width: 80 };
const colFabricM  = { width: 80 };
const colTotal    = { width: 95 };
const colActions  = { width: 76 };
const curtainTableMinWidth = 1320;
const stickyTotalHeaderStyle = {
  position: "sticky",
  right: colActions.width,
  zIndex: 6,
  background: isDark ? "#111827" : "#f8fafc",
  boxShadow: isDark
    ? "-10px 0 18px rgba(2,6,23,0.3)"
    : "-10px 0 18px rgba(15,23,42,0.08)",
};
const stickyActionsHeaderStyle = {
  position: "sticky",
  right: 0,
  zIndex: 7,
  background: isDark ? "#111827" : "#f8fafc",
};
const getStickyTotalCellStyle = (background) => ({
  position: "sticky",
  right: colActions.width,
  zIndex: 4,
  background,
  boxShadow: isDark
    ? "-10px 0 18px rgba(2,6,23,0.3)"
    : "-10px 0 18px rgba(15,23,42,0.08)",
});
const getStickyActionsCellStyle = (background) => ({
  position: "sticky",
  right: 0,
  zIndex: 5,
  background,
});


  /* --------------------------- PDF HELPERS / UI COMING NEXT --------------------------- */
  /* --------------------------- PDF HELPERS --------------------------- */

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const loadPdfImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
  });

const buildPdfImageAsset = async (
  source,
  {
    maxWidth = 360,
    maxHeight = 360,
    format = "JPEG",
    quality = 0.72,
    backgroundColor = "#ffffff",
  } = {}
) => {
  if (!source) return null;

  try {
    const dataUrl = String(source).startsWith("data:")
      ? source
      : await (async () => {
          const res = await fetch(source);
          if (!res.ok) {
            throw new Error(`Image request failed with status ${res.status}`);
          }
          const blob = await res.blob();
          return blobToDataURL(blob);
        })();

    const img = await loadPdfImageElement(dataUrl);
    const naturalWidth = Number(img.naturalWidth || img.width || 1);
    const naturalHeight = Number(img.naturalHeight || img.height || 1);
    const scale = Math.min(
      1,
      maxWidth / naturalWidth,
      maxHeight / naturalHeight
    );
    const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
    const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    if (String(format).toUpperCase() === "JPEG") {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    const mimeType =
      String(format).toUpperCase() === "PNG" ? "image/png" : "image/jpeg";
    const encoded = canvas.toDataURL(
      mimeType,
      mimeType === "image/png" ? undefined : quality
    );

    return {
      dataUrl: encoded,
      format: mimeType === "image/png" ? "PNG" : "JPEG",
      width: targetWidth,
      height: targetHeight,
    };
  } catch (error) {
    devWarn("PDF image preparation failed:", error);
    return null;
  }
};

const fitPdfImageInsideBox = (imageAsset, maxWidth, maxHeight) => {
  const sourceWidth = Number(imageAsset?.width || maxWidth || 1);
  const sourceHeight = Number(imageAsset?.height || maxHeight || 1);
  const ratio = Math.min(
    maxWidth / sourceWidth,
    maxHeight / sourceHeight
  );

  return {
    width: Math.max(1, Number((sourceWidth * ratio).toFixed(2))),
    height: Math.max(1, Number((sourceHeight * ratio).toFixed(2))),
  };
};

// --- Convert Firebase logo URL to optimized dataURL for jsPDF ---
const getLogoDataURL = async (options = {}) => {
  if (!logoURL) return null;

  try {
    const asset = await buildPdfImageAsset(logoURL, {
      maxWidth: 560,
      maxHeight: 260,
      format: "PNG",
      ...options,
    });
    return asset?.dataUrl || null;
  } catch (err) {
    devWarn("Logo conversion failed:", err);
    return null;
  }
};

// --- IMAGE HELPER: load remote product image as optimized dataURL for jsPDF ---
const fetchImageAsDataURL = (url, options = {}) =>
  buildPdfImageAsset(url, {
    maxWidth: 320,
    maxHeight: 320,
    format: "JPEG",
    quality: 0.72,
    backgroundColor: "#ffffff",
    ...options,
  });

const prepareGeneralQuoteImageForPdf = async (image = {}, context = {}, options = {}) => {
  const normalized = createGeneralQuoteImage(image);
  const fullPath = getGeneralQuoteImageStoragePath(normalized);
  const downloadUrl = getGeneralQuoteImageDisplayUrl(normalized);
  const traceBase = {
    itemId: context.itemId || normalized.itemId || "",
    imageId: normalized.imageId || normalized.id || "",
    hasFullPath: Boolean(fullPath),
    fullPath,
    hasDownloadUrl: Boolean(downloadUrl),
  };

  const buildResult = (asset, methodUsed) => {
    if (IS_DEV_UI_ENABLED) {
      devLog("GENERAL_PDF_IMAGE_PREP_TRACE", {
        ...traceBase,
        methodUsed,
        ok: Boolean(asset?.dataUrl),
      });
    }
    return asset?.dataUrl
      ? {
          ok: true,
          methodUsed,
          dataUrl: asset.dataUrl,
          format: asset.format,
          width: asset.width,
          height: asset.height,
        }
      : { ok: false, methodUsed, reason: "empty-image-asset" };
  };

  if (fullPath) {
    try {
      const blob = await getBlob(getGeneralQuoteImageStorageRef(normalized));
      const dataUrl = await blobToDataURL(blob);
      const asset = await buildPdfImageAsset(dataUrl, options);
      return buildResult(asset, "storage-sdk-blob");
    } catch (error) {
      if (IS_DEV_UI_ENABLED) {
        devWarn("GENERAL_PDF_IMAGE_PREP_TRACE", {
          ...traceBase,
          methodUsed: "storage-sdk-blob",
          ok: false,
          errorCode: error?.code || "",
          errorMessage: error?.message || String(error),
        });
      }
    }
  }

  if (downloadUrl) {
    try {
      const asset = await fetchImageAsDataURL(downloadUrl, options);
      return buildResult(asset, "download-url");
    } catch (error) {
      if (IS_DEV_UI_ENABLED) {
        devWarn("GENERAL_PDF_IMAGE_PREP_TRACE", {
          ...traceBase,
          methodUsed: "download-url",
          ok: false,
          errorCode: error?.code || "",
          errorMessage: error?.message || String(error),
        });
      }
    }
  }

  return {
    ok: false,
    methodUsed: fullPath ? "storage-sdk-blob" : "none",
    reason: fullPath || downloadUrl ? "image-unavailable" : "missing-image-source",
  };
};

const downloadGeneralQuotationPDF = async (event) => {
  preventQuoteActionDefault(event);
  if (!operationalQuoteSettingsReady()) return;
  traceQuoteActionClick({
    action: "generalCustomerPdfClick",
    quoteType: GENERAL_QUOTE_TYPE,
    event,
  });
  traceGeneralActionHealth("customerPdfClick");
  if (!canUseCustomerPdf) {
    alert("You do not have permission to export customer PDFs.");
    return;
  }

  beginGeneralActionLock("customerPdf", "exportInProgress");
  try {
    if (generalUploadPromiseRef.current) {
      if (IS_DEV_UI_ENABLED) {
        devLog("GENERAL_IMAGE_CANONICALIZATION_WAIT", {
          action: "customerPdf",
          quoteNo: generalQuoteRef.current?.quoteNo || quoteNo || "",
          reason: "waiting_for_in_flight_general_image_upload",
          buildLabel: FABRIQUOTE_BUILD_INFO.label,
        });
      }
      await generalUploadPromiseRef.current;
    }

    const { jsPDF, autoTable } = await loadPdfRuntime();
    const businessNameLabel = buildBusinessDisplayName(businessName, shopAddress);
    const actionClickedAt = Date.now();
    const liveGeneralActionPayload = buildLiveGeneralQuotePayload({
      action: "customerPdf",
    });
    const generalPdfHealthTrace = traceGeneralActionHealth(
      "customerPdf",
      liveGeneralActionPayload
    );
    if (
      IS_DEV_UI_ENABLED &&
      generalPdfHealthTrace &&
      (generalPdfHealthTrace.itemCountVisible !==
        generalPdfHealthTrace.itemCountInPayload ||
        generalPdfHealthTrace.imageCountVisible !==
          generalPdfHealthTrace.imageCountInPayload ||
        normalizeSearchText(generalPdfHealthTrace.customerNameVisible || "") !==
          normalizeSearchText(generalPdfHealthTrace.customerNameInPayload || ""))
    ) {
      devWarn("GENERAL_PDF_PAYLOAD_MISMATCH", {
        quoteNo: liveGeneralActionPayload.quoteNo || "",
        customerNameVisible: generalPdfHealthTrace.customerNameVisible,
        customerNameInPayload: generalPdfHealthTrace.customerNameInPayload,
        itemCountVisible: generalPdfHealthTrace.itemCountVisible,
        itemCountInPayload: generalPdfHealthTrace.itemCountInPayload,
        imageCountVisible: generalPdfHealthTrace.imageCountVisible,
        imageCountInPayload: generalPdfHealthTrace.imageCountInPayload,
        payloadBuiltAt: liveGeneralActionPayload.payloadBuiltAt,
        actionClickedAt,
        buildLabel: FABRIQUOTE_BUILD_INFO.label,
      });
    }
    traceLiveActionPayload(liveGeneralActionPayload);
    guardStaleActionPayload(
      liveGeneralActionPayload,
      liveGeneralActionPayload.summaryTotals
    );
    const { cleanedQuote, changed, metadataChanged } =
      await cleanupExpiredGeneralQuoteImages(liveGeneralActionPayload.quote);
    const quoteData = normalizeGeneralQuote(cleanedQuote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });
    const totals = calculateGeneralQuoteTotals(quoteData);
    const lineItemCount = Array.isArray(quoteData.lineItems)
      ? quoteData.lineItems.length
      : 0;
    const pdfImageCount = (quoteData.lineItems || []).reduce(
      (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
      0
    );
    const pdfIdentity = resolveQuoteIdentityParts(
      quoteData,
      {
        customerName: quoteCustomerLink?.customerName || customerName || "",
        contactPersonName: quoteCustomerLink?.contactPersonName || "",
        contactPersonPhone: quoteCustomerLink?.contactPersonPhone || "",
        siteName: quoteSiteSelection?.siteName || "",
        siteAddress: quoteSiteSelection?.siteAddress || "",
      }
    );
    const finalPdfCustomerName =
      pdfIdentity.customerName ||
      quoteData.customerName ||
      quoteData.clientName ||
      quoteCustomerLink?.customerName ||
      customerName ||
      "";
    const finalPdfContactPersonName =
      pdfIdentity.contactPersonName || quoteData.contactPersonName || "";
    const finalPdfContactPersonPhone =
      pdfIdentity.contactPersonPhone || quoteData.contactPersonPhone || "";
    quoteData.customerName = finalPdfCustomerName || "—";
    quoteData.contactPersonName = finalPdfContactPersonName;
    quoteData.contactPersonPhone = finalPdfContactPersonPhone;
    quoteData.quoteFor = {
      ...(quoteData.quoteFor || {}),
      name: finalPdfCustomerName || "—",
      company: quoteData.quoteFor?.company || finalPdfCustomerName || "",
      phone: quoteData.quoteFor?.phone || quoteData.customerPhone || quoteCustomerLink?.phone || "",
      email: quoteData.quoteFor?.email || quoteData.customerEmail || quoteCustomerLink?.email || "",
      address:
        quoteData.quoteFor?.address ||
        quoteData.customerAddress ||
        quoteCustomerLink?.address ||
        "",
    };
    if (IS_DEV_UI_ENABLED) {
      devLog("GENERAL_PDF_CUSTOMER_TRACE", {
        customerNameFromPayload:
          liveGeneralActionPayload.quote?.customerName ||
          liveGeneralActionPayload.quote?.quoteFor?.name ||
          "",
        customerNameFromState:
          generalQuoteRef.current?.customerName ||
          generalQuoteRef.current?.quoteFor?.name ||
          generalQuote?.customerName ||
          generalQuote?.quoteFor?.name ||
          "",
        selectedCustomerName: quoteCustomerLink?.customerName || customerName || "",
        finalPdfCustomerName,
        finalPdfContactPersonName,
        quoteNo: quoteData.quoteNo || quoteNo || "",
        itemCount: lineItemCount,
      });
      devLog("GENERAL_PDF_EXPORT_PAYLOAD_TRACE", {
        quoteNo: quoteData.quoteNo || quoteNo || "",
        customerName: finalPdfCustomerName,
        itemCount: lineItemCount,
        imageCount: pdfImageCount,
        timestamp: new Date().toISOString(),
      });
    }
    const printableItemCount = getGeneralActionContentScore(quoteData);

    if (lineItemCount === 0 || printableItemCount === 0) {
      if (IS_DEV_UI_ENABLED) {
        devError("GENERAL_QUOTE_PDF_EMPTY_PAYLOAD_BLOCKED", {
          quoteNo: quoteData.quoteNo || quoteNo || "",
          sourceItemCount: liveGeneralActionPayload.items?.length || 0,
          printableItemCount,
          grandTotal: totals.grandTotal,
          payloadPreview: safeDebugPreview(liveGeneralActionPayload.quote),
        });
      }
      notify("Add at least one item before exporting the quotation PDF.", "warning");
      return;
    }

    if (changed || metadataChanged) {
      generalQuoteRef.current = quoteData;
      setGeneralQuote(quoteData);
    }

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageW = Number(doc.internal.pageSize.getWidth());
    const pageH = Number(doc.internal.pageSize.getHeight());
    const marginX = 15;
    const curSym = pdfCurrencySymbol(quoteData.currency || currency);
    const safeFileName = (value) =>
      String(value || "quotation").replace(/[^\w-]+/g, "_");

    const logoDataUrl = await getLogoDataURL({
      maxWidth: 560,
      maxHeight: 220,
    });
    const imageMap = {};

    const getGeneralPdfItemKey = (item = {}, index = 0) =>
      String(item.id || item.itemId || item.rowId || `general-item-${index}`).trim();

    await Promise.all(
      (quoteData.lineItems || []).map(async (item, itemIndex) => {
        const itemImages = normalizeGeneralQuoteItemImages(item);
        if (!itemImages.length) return;

        const preparedImages = await Promise.all(
          itemImages.slice(0, 4).map((image) =>
            prepareGeneralQuoteImageForPdf(
              image,
              {
                itemId: getGeneralPdfItemKey(item, itemIndex),
                quoteNo: quoteData.quoteNo || "",
              },
              {
                maxWidth: 320,
                maxHeight: 320,
                quality: 0.7,
              }
            )
          )
        );

        imageMap[getGeneralPdfItemKey(item, itemIndex)] = {
          totalCount: itemImages.length,
          assets: preparedImages.filter((result) => result?.ok && result.dataUrl),
          failedCount: preparedImages.filter((result) => !result?.ok).length,
        };
      })
    );

    const headerTop = 10;
    const headerWidth = pageW - marginX * 2;
    const headerGap = 6;
    const headerPadX = 5;
    const headerPadY = 5;
    const leftHeaderWidth = 60;
    const rightHeaderWidth = 42;
    const centerHeaderWidth =
      headerWidth - leftHeaderWidth - rightHeaderWidth - headerGap * 2;
    const splitTextBlock = (value, width) => {
      const text = String(value || "").trim();
      if (!text) return [];
      return doc.splitTextToSize(text, Math.max(12, width));
    };
    const wrapTextFields = (values, width) =>
      (Array.isArray(values) ? values : [])
        .flatMap((value) => splitTextBlock(value, width))
        .filter(Boolean);

    let logoW = 0;
    let logoH = 0;

    if (logoDataUrl) {
      try {
        const logoProps = doc.getImageProperties(logoDataUrl);
        const maxW = 24;
        const maxH = 16;
        const ratio = Math.min(
          maxW / (logoProps.width || maxW),
          maxH / (logoProps.height || maxH)
        );

        logoW = (logoProps.width || maxW) * ratio;
        logoH = (logoProps.height || maxH) * ratio;
      } catch (imageError) {
        devWarn("Failed to size business logo for general PDF:", imageError);
      }
    }

    const leftTextWidth = Math.max(22, leftHeaderWidth - headerPadX * 2);
    const rightTextWidth = Math.max(18, rightHeaderWidth - headerPadX * 2);
    const centerTextWidth = Math.max(20, centerHeaderWidth - headerPadX * 2);

    const businessNameLines = splitTextBlock(businessNameLabel, leftTextWidth);
    const businessSupportLines = wrapTextFields(
      [quoteData.quoteFrom?.address || shopAddress || ""],
      leftTextWidth
    );
    const titleLines = splitTextBlock(
      quoteData.title || "Quotation",
      centerTextWidth
    );
    const subtitleLines = splitTextBlock(quoteData.subtitle, centerTextWidth);
    const metaGroups = [
      splitTextBlock(`Quote No: ${quoteData.quoteNo || "Draft"}`, rightTextWidth),
      splitTextBlock(`Date: ${quoteData.quoteDate || "-"}`, rightTextWidth),
      splitTextBlock(`Valid Till: ${quoteData.validTill || "-"}`, rightTextWidth),
    ].filter((lines) => lines.length > 0);

    const leftTextHeight =
      businessNameLines.length * 5 +
      (businessSupportLines.length ? 2 + businessSupportLines.length * 4.2 : 0);
    const leftLogoGap = logoW && logoH ? 4 : 0;
    const leftBlockHeight =
      (logoH ? logoH + leftLogoGap : 0) + leftTextHeight;
    const centerBlockHeight =
      titleLines.length * 6 +
      (subtitleLines.length ? 2 + subtitleLines.length * 4.2 : 0);
    const metaBlockHeight = metaGroups.reduce(
      (sum, lines, index) =>
        sum + lines.length * 4.4 + (index < metaGroups.length - 1 ? 2 : 0),
      0
    );
    const headerInnerHeight = Math.max(
      leftBlockHeight,
      centerBlockHeight,
      metaBlockHeight,
      20
    );
    const headerHeight = headerInnerHeight + headerPadY * 2;

    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(marginX, headerTop, headerWidth, headerHeight, 4, 4, "F");

    const leftBlockX = marginX + headerPadX;
    const rightBlockX =
      marginX + leftHeaderWidth + headerGap + centerHeaderWidth + headerGap;
    const innerTop = headerTop + headerPadY;

    if (logoDataUrl && logoW && logoH) {
      try {
        const leftContentTop =
          innerTop + Math.max(0, (headerInnerHeight - leftBlockHeight) / 2);
        const logoX =
          leftBlockX + Math.max(0, (leftHeaderWidth - headerPadX * 2 - logoW) / 2);
        doc.addImage(logoDataUrl, "PNG", logoX, leftContentTop, logoW, logoH);
      } catch (imageError) {
        devWarn("Failed to render business logo for general PDF:", imageError);
      }
    }

    const leftContentTop =
      innerTop + Math.max(0, (headerInnerHeight - leftBlockHeight) / 2);
    const businessTextX = leftBlockX + (leftHeaderWidth - headerPadX * 2) / 2;
    const businessTextTop = leftContentTop + (logoH ? logoH + leftLogoGap : 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    if (businessNameLines.length) {
      doc.text(businessNameLines, businessTextX, businessTextTop + 4.5, {
        align: "center",
      });
    }

    if (businessSupportLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.2);
      doc.setTextColor(71, 85, 105);
      const supportY = businessTextTop + businessNameLines.length * 5 + 6;
      doc.text(businessSupportLines, businessTextX, supportY, {
        align: "center",
      });
    }

    const centerContentTop =
      innerTop + Math.max(0, (headerInnerHeight - centerBlockHeight) / 2);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(titleLines, pageW / 2, centerContentTop + 6, { align: "center" });

    if (subtitleLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(
        subtitleLines,
        pageW / 2,
        centerContentTop + titleLines.length * 6 + 8,
        { align: "center" }
      );
    }

    const metaTop =
      innerTop + Math.max(0, (headerInnerHeight - metaBlockHeight) / 2) + 4;
    let metaY = metaTop;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(71, 85, 105);
    metaGroups.forEach((lines, index) => {
      doc.text(lines, rightBlockX + rightHeaderWidth - headerPadX, metaY, {
        align: "right",
      });
      metaY += lines.length * 4.4 + (index < metaGroups.length - 1 ? 2 : 0);
    });

    const partyTop = headerTop + headerHeight + 8;
    const partyWidth = (headerWidth - 8) / 2;

    const drawPartyBlock = (x, title, values) => {
      const split = wrapTextFields(values, partyWidth - 8);
      const blockHeight = Math.max(34, 16 + split.length * 4.5 + 6);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, partyTop, partyWidth, blockHeight, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(title, x + 4, partyTop + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(71, 85, 105);
      if (split.length) {
        doc.text(split, x + 4, partyTop + 13);
      }
      return blockHeight;
    };

    const fromBlockHeight = drawPartyBlock(marginX, "Quotation From", [
      quoteData.quoteFrom?.name,
      quoteData.quoteFrom?.address,
      quoteData.quoteFrom?.phone,
      quoteData.quoteFrom?.email,
    ]);

    const toBlockHeight = drawPartyBlock(marginX + partyWidth + 8, "Quotation For", [
      quoteData.customerName || quoteData.quoteFor?.company || quoteData.quoteFor?.name,
      quoteData.contactPersonName ? `Contact: ${quoteData.contactPersonName}` : "",
      quoteData.contactPersonPhone ? `Contact Phone: ${quoteData.contactPersonPhone}` : "",
      quoteData.quoteFor?.address,
      quoteData.siteName ? `Site: ${quoteData.siteName}` : "",
      quoteData.siteAddress ? `Site Address: ${quoteData.siteAddress}` : "",
      quoteData.siteContactName ? `Site Contact: ${quoteData.siteContactName}` : "",
      quoteData.siteContactPhone ? `Site Phone: ${quoteData.siteContactPhone}` : "",
      quoteData.siteNotes ? `Site Notes: ${quoteData.siteNotes}` : "",
      quoteData.quoteFor?.phone,
      quoteData.quoteFor?.email,
    ]);

    const tableStartY = partyTop + Math.max(fromBlockHeight, toBlockHeight) + 8;

    const body = (quoteData.lineItems || []).map((item, index) => [
      String(index + 1),
      "",
      [
        item.itemName || item.name || "-",
        [item.roomName, item.areaName, item.windowLabel].filter(Boolean).length
          ? `Location: ${[item.roomName, item.areaName, item.windowLabel]
              .filter(Boolean)
              .join(" / ")}`
          : "",
        item.description ? `Description: ${item.description}` : "",
        item.imageLinkUrl || item.productUrl
          ? `Link: ${item.imageLinkUrl || item.productUrl}`
          : "",
      ]
        .filter(Boolean)
        .join("\n"),
      Number(item.quantity || 0).toFixed(2),
      item.unit || "-",
      `${curSym} ${Number(item.rate || 0).toFixed(2)}`,
      `${Number(item.gstPercent || 0).toFixed(0)}% ${
        item.gstMode === "exclusive" ? "Exc" : "Inc"
      }`,
      `${curSym} ${getGeneralItemAmount(item).toFixed(2)}`,
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [["#", "Image", "Item", "Qty", "Unit", "Rate", "GST", "Amount"]],
      body,
      margin: { left: marginX, right: marginX },
      theme: "grid",
      rowPageBreak: "avoid",
      styles: {
        fontSize: 9,
        cellPadding: 2.8,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "top",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: {
          cellWidth: 12,
          halign: "center",
          valign: "middle",
          overflow: "hidden",
          cellPadding: { top: 2.8, right: 1.5, bottom: 2.8, left: 1.5 },
        },
        1: { cellWidth: 24, halign: "center" },
        2: { cellWidth: 53 },
        3: { cellWidth: 14, halign: "right" },
        4: { cellWidth: 14, halign: "center" },
        5: { cellWidth: 21, halign: "right" },
        6: { cellWidth: 18, halign: "center" },
        7: { cellWidth: 24, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.column.index === 0) {
          data.cell.styles.overflow = "hidden";
          data.cell.styles.halign = "center";
          data.cell.styles.valign = "middle";
          data.cell.text = [String(data.cell.raw ?? "").trim()];
        }

        if (data.section !== "body") return;

        const item = quoteData.lineItems?.[data.row.index];
        const imageEntry = imageMap[getGeneralPdfItemKey(item, data.row.index)];
        const hasThumbnail = Boolean(imageEntry?.assets?.length);
        if (data.column.index === 1) {
          data.cell.text = [""];
          data.cell.styles.minCellHeight =
            hasThumbnail || Number(imageEntry?.failedCount || 0) > 0 ? 24 : 10;
        }

        if (
          (hasThumbnail || Number(imageEntry?.failedCount || 0) > 0) &&
          data.column.index === 2
        ) {
          data.cell.styles.minCellHeight = Math.max(
            Number(data.cell.styles.minCellHeight || 0),
            24
          );
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 1) return;

        const item = quoteData.lineItems?.[data.row.index];
        const imageEntry = imageMap[getGeneralPdfItemKey(item, data.row.index)];
        const imageAssets = imageEntry?.assets || [];
        if (
          !item ||
          (imageAssets.length === 0 && Number(imageEntry?.failedCount || 0) === 0)
        ) {
          return;
        }

        const thumbBox = Math.min(18, Math.max(12, data.cell.width - 4));
        const availableHeight = Math.max(10, Math.min(thumbBox, data.cell.height - 4));
        const boxX =
          data.cell.x + (data.cell.width - Math.min(thumbBox, data.cell.width - 4)) / 2;
        const boxY = data.cell.y + 2;
        const boxWidth = Math.min(thumbBox, data.cell.width - 4);
        const boxHeight = availableHeight;

        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(
          boxX,
          boxY,
          boxWidth,
          boxHeight,
          1.5,
          1.5
        );

        if (imageAssets.length === 0) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6.5);
          doc.setTextColor(100, 116, 139);
          doc.text("Image", boxX + boxWidth / 2, boxY + boxHeight / 2 - 1.5, {
            align: "center",
          });
          doc.text("unavailable", boxX + boxWidth / 2, boxY + boxHeight / 2 + 2.5, {
            align: "center",
          });
          doc.setTextColor(15, 23, 42);
        } else if (imageAssets.length === 1) {
          const fitted = fitPdfImageInsideBox(imageAssets[0], boxWidth, boxHeight);
          const imgX = boxX + (boxWidth - fitted.width) / 2;
          const imgY = boxY + (boxHeight - fitted.height) / 2;

          doc.addImage(
            imageAssets[0].dataUrl,
            imageAssets[0].format,
            imgX,
            imgY,
            fitted.width,
            fitted.height
          );
        } else {
          const columns = 2;
          const rows = imageAssets.length > 2 ? 2 : 1;
          const gap = 0.8;
          const tileWidth = (boxWidth - gap * (columns - 1)) / columns;
          const tileHeight = (boxHeight - gap * (rows - 1)) / rows;

          imageAssets.slice(0, 4).forEach((asset, imageIndex) => {
            const col = imageIndex % columns;
            const row = Math.floor(imageIndex / columns);
            const tileX = boxX + col * (tileWidth + gap);
            const tileY = boxY + row * (tileHeight + gap);
            const fitted = fitPdfImageInsideBox(asset, tileWidth, tileHeight);
            const imgX = tileX + (tileWidth - fitted.width) / 2;
            const imgY = tileY + (tileHeight - fitted.height) / 2;

            doc.addImage(
              asset.dataUrl,
              asset.format,
              imgX,
              imgY,
              fitted.width,
              fitted.height
            );

            if (
              imageIndex === 3 &&
              Number(imageEntry?.totalCount || 0) > imageAssets.length
            ) {
              doc.setFillColor(15, 23, 42);
              doc.setDrawColor(15, 23, 42);
              doc.roundedRect(
                tileX + tileWidth - 7,
                tileY + tileHeight - 5.5,
                6.5,
                4.8,
                1,
                1,
                "F"
              );
              doc.setTextColor(255, 255, 255);
              doc.setFont("helvetica", "bold");
              doc.setFontSize(6.5);
              doc.text(
                `+${Number(imageEntry.totalCount || 0) - imageAssets.length}`,
                tileX + tileWidth - 3.5,
                tileY + tileHeight - 2.1,
                { align: "center" }
              );
              doc.setTextColor(15, 23, 42);
            }
          });
        }

        const primaryImage = normalizeGeneralQuoteItemImages(item)[0];
        const rawItemLink = item.imageLinkUrl || item.productUrl || "";
        const safeItemLink = isFirebaseStorageRestUrl(rawItemLink) ? "" : rawItemLink;
        const href =
          safeItemLink ||
          getGeneralQuoteImageDisplayUrl(primaryImage) ||
          "";
        if (href) {
          doc.link(
            boxX,
            boxY,
            boxWidth,
            boxHeight,
            { url: href }
          );
        }
      },
    });

    let cursorY = Number(doc.lastAutoTable?.finalY || 98) + 12;
    const contentWidth = pageW - marginX * 2;
    const topMargin = 18;
    const bottomMargin = 14;
    const rowHeight = 6;
    const textLineHeight = 4.5;
    const blockPaddingX = 5;
    const blockPaddingY = 6;
    const subtotalExcludingGst = Number(
      totals.subtotalExcludingGst ??
        (quoteData.lineItems || []).reduce(
          (sum, item) => sum + getGeneralItemBaseAmount(item),
          0
        )
    );
    const itemGstTotal = Number(
      (
        Number(totals.itemsGstTotal || 0) ||
        Number(totals.gstTotal || 0) - Number(totals.additionalChargesGst || 0)
      ).toFixed(2)
    );

    const ensurePageSpace = (neededHeight) => {
      if (cursorY + neededHeight > pageH - bottomMargin) {
        doc.addPage();
        cursorY = topMargin;
      }
    };

    const splitPdfBlockText = (value, blockWidth = contentWidth) => {
      const textValue = String(value || "").trim();
      if (!textValue) return [];
      return doc.splitTextToSize(textValue, Math.max(24, blockWidth));
    };

    const specificationShowcaseItems = (quoteData.lineItems || [])
      .map((item, index) => ({
        item,
        index,
        specifications: normalizeGeneralQuoteItemSpecifications(item).filter(
          (specification) =>
            String(specification?.key ?? "").trim() ||
            String(specification?.value ?? "").trim()
        ),
      }))
      .filter((entry) => entry.specifications.length > 0);

    const writeSpecificationShowcase = () => {
      if (!specificationShowcaseItems.length) return;

      ensurePageSpace(18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text("Specifications", marginX, cursorY);
      cursorY += 8;

      const chipGap = 4;
      const chipWidth = (contentWidth - chipGap) / 2;

      specificationShowcaseItems.forEach(({ item, index, specifications }) => {
        const itemNameLines = splitPdfBlockText(
          item.itemName || item.name || `Item ${index + 1}`,
          contentWidth - blockPaddingX * 2
        );
        const metaLabel = [item.specTemplateName, item.specItemType]
          .filter(Boolean)
          .join(" | ");
        const metaLines = splitPdfBlockText(
          metaLabel,
          contentWidth - blockPaddingX * 2
        );
        const descriptionLines = splitPdfBlockText(
          item.description,
          contentWidth - blockPaddingX * 2
        );
        const headerHeight =
          10 +
          itemNameLines.length * 5 +
          (metaLines.length ? metaLines.length * 4 + 2 : 0) +
          (descriptionLines.length ? descriptionLines.length * 4.2 + 4 : 0);

        ensurePageSpace(headerHeight + 10);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, cursorY, contentWidth, headerHeight, 3, 3, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(itemNameLines, marginX + blockPaddingX, cursorY + 7);

        let headerCursorY = cursorY + 7 + itemNameLines.length * 5;

        if (metaLines.length) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(37, 99, 235);
          doc.text(metaLines, marginX + blockPaddingX, headerCursorY + 1.5);
          headerCursorY += metaLines.length * 4 + 2;
        }

        if (descriptionLines.length) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(71, 85, 105);
          doc.text(descriptionLines, marginX + blockPaddingX, headerCursorY + 2.5);
        }

        cursorY += headerHeight + 4;

        const needsVerticalLayout =
          specifications.length > 6 ||
          specifications.some(
            (specification) =>
              String(specification?.key ?? "").trim().length > 22 ||
              String(specification?.value ?? "").trim().length > 28
          );

        if (needsVerticalLayout) {
          specifications.forEach((specification) => {
            const label = String(specification?.key || "Specification").trim() || "Specification";
            const value = String(specification?.value || "-").trim() || "-";
            const detailLines = splitPdfBlockText(
              `${label}: ${value}`,
              contentWidth - blockPaddingX * 2
            );
            const rowHeight = Math.max(10, 6 + detailLines.length * 4.2);

            ensurePageSpace(rowHeight + 2);
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(marginX, cursorY, contentWidth, rowHeight, 3, 3, "FD");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.2);
            doc.setTextColor(30, 41, 59);
            doc.text(detailLines, marginX + blockPaddingX, cursorY + 5.5);
            cursorY += rowHeight + 3;
          });

          cursorY += 5;
          return;
        }

        for (let specIndex = 0; specIndex < specifications.length; specIndex += 2) {
          const rowSpecifications = specifications.slice(specIndex, specIndex + 2);
          const renderedChips = rowSpecifications.map((specification) => {
            const chipText = `${specification.key || "Specification"}: ${
              specification.value || "-"
            }`;
            const chipLines = splitPdfBlockText(
              chipText,
              chipWidth - blockPaddingX * 2
            );

            return {
              chipLines,
              height: Math.max(10, 6 + chipLines.length * 4.2),
            };
          });
          const rowHeight = Math.max(...renderedChips.map((chip) => chip.height));

          ensurePageSpace(rowHeight + 2);
          const currentRowY = cursorY;

          for (let chipIndex = 0; chipIndex < renderedChips.length; chipIndex += 1) {
            const chip = renderedChips[chipIndex];
            const chipX = marginX + chipIndex * (chipWidth + chipGap);
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(chipX, currentRowY, chipWidth, rowHeight, 3, 3, "FD");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.2);
            doc.setTextColor(30, 41, 59);
            doc.text(chip.chipLines, chipX + blockPaddingX, currentRowY + 5.5);
          }

          cursorY += rowHeight + 3;
        }

        cursorY += 5;
      });
    };

    const writeSummaryBlock = () => {
      const additionalLabel =
        quoteData.additionalChargesLabel || "Additional Charges";
      const discountLabel =
        quoteData.discountType === "percent"
          ? `Post-GST Discount (${Number(quoteData.discountValue || 0)}%)`
          : "Post-GST Discount";
      const summaryRows = [
        ["Subtotal (Excl. GST)", `${curSym} ${subtotalExcludingGst.toFixed(2)}`],
        ["Item GST", `${curSym} ${itemGstTotal.toFixed(2)}`],
        [additionalLabel, `${curSym} ${Number(totals.additionalCharges || 0).toFixed(2)}`],
        [
          `${additionalLabel} GST`,
          `${curSym} ${Number(totals.additionalChargesGst || 0).toFixed(2)}`,
        ],
        [
          "Total After GST",
          `${curSym} ${Number(totals.totalAfterGst ?? totals.subtotal ?? 0).toFixed(2)}`,
        ],
        [discountLabel, `- ${curSym} ${totals.discountAmount.toFixed(2)}`],
      ];
      const blockHeight = 18 + summaryRows.length * rowHeight + 12;

      ensurePageSpace(blockHeight);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, cursorY, contentWidth, blockHeight, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("Totals", marginX + blockPaddingX, cursorY + 7);

      let rowY = cursorY + 15;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      summaryRows.forEach(([label, value]) => {
        doc.text(label, marginX + blockPaddingX, rowY);
        doc.text(value, pageW - marginX - blockPaddingX, rowY, {
          align: "right",
        });
        rowY += rowHeight;
      });

      doc.setDrawColor(226, 232, 240);
      doc.line(
        marginX + blockPaddingX,
        rowY - 1,
        pageW - marginX - blockPaddingX,
        rowY - 1
      );

      doc.setFont("helvetica", "bold");
      doc.text("Grand Total", marginX + blockPaddingX, rowY + 5);
      doc.text(
        `${curSym} ${totals.grandTotal.toFixed(2)}`,
        pageW - marginX - blockPaddingX,
        rowY + 5,
        { align: "right" }
      );

      cursorY += blockHeight + 8;
    };

    const writeTextBlock = (title, value) => {
      if (!value) return;

      const textValue = String(value || "").trim();
      if (!textValue) return;

      const lines = doc.splitTextToSize(textValue, contentWidth - blockPaddingX * 2);
      let lineIndex = 0;

      while (lineIndex < lines.length) {
        ensurePageSpace(18);

        const availableHeight = pageH - bottomMargin - cursorY;
        const maxLines = Math.max(
          1,
          Math.floor((availableHeight - 14) / textLineHeight)
        );
        const chunk = lines.slice(lineIndex, lineIndex + maxLines);
        const blockHeight = 12 + chunk.length * textLineHeight + blockPaddingY;

        ensurePageSpace(blockHeight);

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, cursorY, contentWidth, blockHeight, 3, 3, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(title, marginX + blockPaddingX, cursorY + 7);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(71, 85, 105);
        doc.text(chunk, marginX + blockPaddingX, cursorY + 13);

        cursorY += blockHeight + 8;
        lineIndex += chunk.length;
      }
    };

    writeSpecificationShowcase();
    writeSummaryBlock();
    writeTextBlock("Total In Words", totals.totalInWords);
    writeTextBlock("Notes", quoteData.notes);
    writeTextBlock("Terms & Conditions", quoteData.terms);

    ensurePageSpace(8);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Generated by ${businessNameLabel} by using FabriQuote`,
      marginX,
      cursorY
    );

    doc.save(
      `General-Quote-${safeFileName(
        quoteData.quoteFor?.company || quoteData.quoteFor?.name || quoteData.quoteNo
      )}-${safeFileName(quoteData.quoteNo || "draft")}.pdf`
    );
  } catch (error) {
    devError("Failed to export general quotation PDF:", error);
    alert("Failed to export general quotation PDF");
  } finally {
    endGeneralActionLock("customerPdf");
  }
};

const exportGeneralQuotationPDF = async (event) => {
  preventQuoteActionDefault(event);
  traceQuoteActionClick({
    action: "generalCustomerPdfClick",
    quoteType: GENERAL_QUOTE_TYPE,
    event,
  });
  traceGeneralActionHealth("customerPdfClick");
  if (!canUseCustomerPdf) {
    alert("You do not have permission to export customer PDFs.");
    return;
  }

  await openQuoteSentPromptForPdf(GENERAL_QUOTE_TYPE);
};

const downloadExecutionWorkshopPdf = async (dealOverride = null, pdfOptions = {}) => {
  if (!canUseWorkshopPdf) {
    notify("You do not have permission to export workshop PDFs.", "warning");
    return;
  }

  const sourceDeal =
    pdfOptions.sourceDeal ||
    dealOverride ||
    selectedCrmDeal ||
    selectedCrmDealCacheRef.current ||
    null;
  const sourceDraft =
    pdfOptions.sourceDraft !== undefined ? pdfOptions.sourceDraft : crmDetailDraft || null;

  if (!sourceDeal && !sourceDraft) {
    notify("Open an execution deal before downloading the workshop PDF.", "warning");
    return;
  }

  const rawExecutionItems = Array.isArray(pdfOptions.executionItems)
    ? pdfOptions.executionItems
    : Array.isArray(sourceDraft?.executionSpecs)
    ? sourceDraft.executionSpecs
    : Array.isArray(sourceDeal?.executionSpecs)
    ? sourceDeal.executionSpecs
    : [];

  const executionItems = normalizeExecutionSpecs(
    (Array.isArray(rawExecutionItems) ? rawExecutionItems : []).map((item) => ({
      ...item,
      attributes: Array.isArray(item?.attributeRows)
        ? item.attributeRows.reduce((accumulator, field) => {
            const key = String(field?.key || "").trim();
            if (!key) return accumulator;
            accumulator[key] = String(field?.value ?? "");
            return accumulator;
          }, {})
        : item?.attributes,
      attachments: normalizeExecutionSpecAttachments(item?.attachments || []),
    }))
  );

  if (executionItems.length === 0) {
    notify("Add execution items before downloading the workshop PDF.", "warning");
    return;
  }

  try {
    const { jsPDF, autoTable } = await loadPdfRuntime();
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageW = Number(doc.internal.pageSize.getWidth());
    const pageH = Number(doc.internal.pageSize.getHeight());
    const marginX = 14;
    const contentWidth = pageW - marginX * 2;
    const businessNameLabel = buildBusinessDisplayName(businessName, shopAddress);
    const generatedAt = new Date();
    const primaryLocation = getPrimarySiteLocation({
      siteLocations: sourceDraft?.siteLocations || sourceDeal?.siteLocations || [],
    });
    const linkedQuote = getPrimaryCrmLinkedQuote(sourceDeal?.linkedQuotes || []) || {};
    const customerNameValue =
      sourceDraft?.contact?.name ||
      sourceDeal?.customerLabel ||
      sourceDeal?.customerName ||
      sourceDeal?.crmContact?.name ||
      sourceDeal?.contact?.name ||
      "Unnamed Customer";
    const customerPhoneValue =
      sourceDraft?.contact?.phone ||
      sourceDraft?.contact?.whatsapp ||
      sourceDeal?.crmContact?.phone ||
      sourceDeal?.crmContact?.whatsapp ||
      sourceDeal?.contact?.phone ||
      sourceDeal?.contact?.whatsapp ||
      sourceDeal?.customerPhone ||
      "";
    const quoteReference =
      linkedQuote.quoteNo ||
      sourceDeal?.quoteNo ||
      sourceDeal?.primaryQuoteNo ||
      sourceDraft?.quoteNo ||
      "-";
    const jobReference =
      sourceDeal?.crmTitle ||
      sourceDeal?.title ||
      sourceDeal?.dealTitle ||
      sourceDeal?.id ||
      "Execution Job";
    const installationDateValue =
      sourceDraft?.installationDate ||
      sourceDeal?.installationDate ||
      sourceDeal?.crm?.installationDate ||
      "";
    const stageLabel =
      sourceDraft?.stageNameSnapshot ||
      sourceDeal?.crmStageLabel ||
      sourceDeal?.stageNameSnapshot ||
      sourceDeal?.status ||
      "-";
    const commercialFieldPattern =
      /(rs\.|₹|\bprice\b|\brate\b|\bmrp\b|\bdiscount\b|\bgst\b|\btax\b|\bmargin\b|\bpayment\b|\badvance\b|\bbalance\b|\bcommercial\b|\bamount\b|\bsubtotal\b|\btotal\b|\bgrand\s*total\b|\bquote\s*value\b|\bfinal\s*value\b|\bexpected\s*value\b|\bcost\b|\bbilling\b)/i;
    const safeFileName = (value) =>
      normalizeFileName(String(value || "execution-workshop").slice(0, 80));
    const formatPdfDate = (value) => {
      const millis = toMillis(value);
      if (!millis) return "";
      return new Date(millis).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };
    const displayValue = (value, fallback = "-") => {
      const next = String(value ?? "").trim();
      return next || fallback;
    };
    const splitValue = (value, width = contentWidth) =>
      doc.splitTextToSize(displayValue(value, ""), Math.max(20, width));
    const isCommercialField = (label) => commercialFieldPattern.test(String(label || ""));
    const getOperationalAttributeRows = (item) =>
      Object.entries(item?.attributes || {})
        .map(([key, value]) => [String(key || "").trim(), displayValue(value)])
        .filter(([key, value]) => key && !isCommercialField(key) && !isCommercialField(value));
    const sanitizeOperationalNotes = (value) =>
      String(value || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line && !commercialFieldPattern.test(line))
        .join("\n");
    const isImageAttachment = (attachment) => {
      const type = String(attachment?.contentType || "").toLowerCase();
      const name = String(attachment?.name || "").toLowerCase();
      return (
        type.startsWith("image/") ||
        /\.(png|jpe?g|webp|gif)$/i.test(name)
      );
    };

    const imageAssetsByItemId = {};
    await Promise.all(
      executionItems.map(async (item) => {
        const imageAttachments = normalizeExecutionSpecAttachments(item.attachments || [])
          .filter((attachment) => attachment.url && isImageAttachment(attachment))
          .slice(0, 2);

        if (!imageAttachments.length) return;

        const assets = await Promise.all(
          imageAttachments.map((attachment) =>
            fetchImageAsDataURL(attachment.url, {
              maxWidth: 360,
              maxHeight: 260,
              quality: 0.72,
            })
          )
        );
        imageAssetsByItemId[item.id] = imageAttachments.map((attachment, index) => ({
          attachment,
          asset: assets[index] || null,
        }));
      })
    );

    const logoDataUrl = await getLogoDataURL({
      maxWidth: 420,
      maxHeight: 160,
    });

    let cursorY = 14;

    const ensurePageSpace = (height = 24) => {
      if (cursorY + height <= pageH - 18) return;
      doc.addPage();
      cursorY = 16;
    };

    const writeHeader = () => {
      const headerHeight = 34;
      const logoBadgeX = marginX + 5;
      const logoBadgeY = cursorY + 5;
      const logoBadgeW = 28;
      const logoBadgeH = 18;
      doc.setFillColor(15, 23, 42);
      doc.roundedRect(marginX, cursorY, contentWidth, headerHeight, 4, 4, "F");

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(logoBadgeX, logoBadgeY, logoBadgeW, logoBadgeH, 3, 3, "FD");

      if (logoDataUrl) {
        try {
          const logoProps = doc.getImageProperties(logoDataUrl);
          const fit = fitPdfImageInsideBox(
            {
              width: logoProps.width,
              height: logoProps.height,
            },
            logoBadgeW - 8,
            logoBadgeH - 6
          );
          doc.addImage(
            logoDataUrl,
            "PNG",
            logoBadgeX + (logoBadgeW - fit.width) / 2,
            logoBadgeY + (logoBadgeH - fit.height) / 2,
            fit.width,
            fit.height
          );
        } catch (error) {
          devWarn("Execution workshop PDF logo render failed:", error);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(15, 23, 42);
          doc.text(
            doc.splitTextToSize(businessNameLabel, logoBadgeW - 5).slice(0, 2),
            logoBadgeX + logoBadgeW / 2,
            logoBadgeY + 7,
            { align: "center" }
          );
        }
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(15, 23, 42);
        doc.text(
          doc.splitTextToSize(businessNameLabel, logoBadgeW - 5).slice(0, 2),
          logoBadgeX + logoBadgeW / 2,
          logoBadgeY + 7,
          { align: "center" }
        );
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("WORKSHOP / EXECUTION SHEET", marginX + 34, cursorY + 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(
        "Production / installation copy",
        marginX + 34,
        cursorY + 20
      );
      doc.text(businessNameLabel, pageW - marginX - 5, cursorY + 12, {
        align: "right",
      });
      doc.text(
        `Generated: ${generatedAt.toLocaleString("en-IN")}`,
        pageW - marginX - 5,
        cursorY + 20,
        { align: "right" }
      );
      cursorY += headerHeight + 8;
      doc.setTextColor(15, 23, 42);
    };

    const writeSectionTitle = (title, subtitle = "") => {
      ensurePageSpace(14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(title, marginX, cursorY);
      if (subtitle) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(subtitle, marginX, cursorY + 5);
        cursorY += 10;
      } else {
        cursorY += 6;
      }
    };

    writeHeader();

    const executionSiteSnapshot = normalizeQuoteSiteSnapshot(
      sourceDraft || sourceDeal || selectedQuote || quoteSiteSelection,
      { customerName: customerNameValue }
    );
    const siteValue = [
      executionSiteSnapshot.siteName || primaryLocation?.label,
      executionSiteSnapshot.siteAddress || primaryLocation?.addressText,
      executionSiteSnapshot.siteNotes || primaryLocation?.notes,
    ]
      .filter(Boolean)
      .join(" - ");

    autoTable(doc, {
      startY: cursorY,
      head: [["Job", "Customer", "Quote Ref", "Status"]],
      body: [
        [
          displayValue(jobReference),
          displayValue(customerNameValue),
          displayValue(quoteReference),
          displayValue(stageLabel),
        ],
        [
          `Phone: ${displayValue(customerPhoneValue)}`,
          `Site: ${displayValue(siteValue)}`,
          `Installation: ${displayValue(formatPdfDate(installationDateValue))}`,
          `Items: ${executionItems.length}`,
        ],
      ],
      margin: { left: marginX, right: marginX },
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2.8,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });
    cursorY = Number(doc.lastAutoTable?.finalY || cursorY) + 10;

    writeSectionTitle(
      "Execution Items",
      "Manufacturing details, reference photos, and site notes for the execution team."
    );

    executionItems.forEach((item, index) => {
      const attributeRows = getOperationalAttributeRows(item);
      const attachments = normalizeExecutionSpecAttachments(item.attachments || []);
      const attachmentRows = attachments
        .filter((attachment) => attachment.name || attachment.url)
        .map((attachment) => [
          "Attachment",
          displayValue(attachment.name || attachment.url),
        ])
        .filter(([, value]) => !isCommercialField(value));
      const tableRows = [
        ["Item Type", displayValue(item.itemType || "Custom")],
        ["Item / Product", displayValue(item.itemName || `Item ${index + 1}`)],
        ...attributeRows,
        ...attachmentRows,
      ];
      assertNoPricingInWorkshopPdfText(tableRows, {
        label: `executionWorkshopItem:${item.id || index}`,
        product: item.itemType || item.itemName || "",
        rowItem: item.itemName || "",
      });

      ensurePageSpace(26);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(
        `${index + 1}. ${displayValue(item.itemName || item.itemType || "Execution Item")}`,
        marginX,
        cursorY
      );
      cursorY += 4;

      autoTable(doc, {
        startY: cursorY,
        head: [["Field", "Instruction / Detail"]],
        body: tableRows.length ? tableRows : [["Details", "-"]],
        margin: { left: marginX, right: marginX },
        theme: "grid",
        rowPageBreak: "avoid",
        styles: {
          fontSize: 9.2,
          cellPadding: 2.8,
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
          overflow: "linebreak",
          valign: "top",
        },
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 48, fontStyle: "bold" },
          1: { cellWidth: contentWidth - 48 },
        },
      });
      cursorY = Number(doc.lastAutoTable?.finalY || cursorY) + 5;

      const imageEntries = imageAssetsByItemId[item.id] || [];
      if (imageEntries.length) {
        ensurePageSpace(42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text("Reference Images", marginX, cursorY);
        cursorY += 4;

        imageEntries.forEach(({ attachment, asset }, imageIndex) => {
          const boxWidth = 42;
          const boxHeight = 30;
          const x = marginX + imageIndex * (boxWidth + 6);
          const imageUrl = String(attachment?.url || "").trim();
          doc.setDrawColor(226, 232, 240);
          doc.roundedRect(x, cursorY, boxWidth, boxHeight, 2, 2, "S");
          if (asset) {
            const fit = fitPdfImageInsideBox(asset, boxWidth, boxHeight);
            try {
              doc.addImage(
                asset.dataUrl,
                asset.format || "JPEG",
                x + (boxWidth - fit.width) / 2,
                cursorY + (boxHeight - fit.height) / 2,
                fit.width,
                fit.height
              );
            } catch (error) {
              devWarn("Execution workshop PDF image render failed:", error);
            }
          } else {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text("Image", x + boxWidth / 2, cursorY + boxHeight / 2, {
              align: "center",
            });
          }

          if (imageUrl) {
            try {
              doc.link(x, cursorY, boxWidth, boxHeight, { url: imageUrl });
            } catch (error) {
              devWarn("Execution workshop PDF image link failed:", error);
            }
          }
        });

        const linkY = cursorY + 35;
        imageEntries.forEach(({ attachment }, imageIndex) => {
          const imageUrl = String(attachment?.url || "").trim();
          if (!imageUrl) return;

          const boxWidth = 42;
          const x = marginX + imageIndex * (boxWidth + 6);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.2);
          doc.setTextColor(37, 99, 235);
          doc.text("Open full image", x, linkY);
          try {
            doc.link(x, linkY - 3, 25, 5, { url: imageUrl });
          } catch (error) {
            devWarn("Execution workshop PDF text link failed:", error);
          }
        });
        doc.setTextColor(15, 23, 42);
        cursorY += 45;
      }
    });

    const operationalNotes = sanitizeOperationalNotes(
      sourceDraft?.executionNotes ||
        sourceDraft?.internalNotes ||
        sourceDraft?.notes ||
        sourceDeal?.executionNotes ||
        sourceDeal?.internalNotes ||
        sourceDeal?.notes ||
        ""
    );

    if (operationalNotes) {
      writeSectionTitle("Production Notes");
      const noteLines = splitValue(operationalNotes, contentWidth - 8);
      const noteHeight = Math.max(18, 8 + noteLines.length * 4.4);
      ensurePageSpace(noteHeight + 4);
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, cursorY, contentWidth, noteHeight, 3, 3, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(30, 41, 59);
      doc.text(noteLines, marginX + 4, cursorY + 7);
      cursorY += noteHeight + 8;
    }

    const pageCount = doc.getNumberOfPages();
    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      doc.setPage(pageIndex);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(
        "Generated by FabriQuote",
        marginX,
        pageH - 8
      );
      doc.text(`Page ${pageIndex} of ${pageCount}`, pageW - marginX, pageH - 8, {
        align: "right",
      });
    }

    if (isDevRuntime) {
      devLog("Execution workshop PDF generated", {
        dealId: sourceDeal?.id,
        itemCount: executionItems.length,
        quoteReference,
        excludedCommercialFields: true,
      });
    }

    doc.save(
      `Workshop-Execution-${safeFileName(customerNameValue)}-${safeFileName(
        quoteReference || sourceDeal?.id || "job"
      )}.pdf`
    );
  } catch (error) {
    devError("Failed to export execution workshop PDF:", error);
    notify("Could not download workshop PDF. Please try again.", "error");
  }
};

// ---------- WORKSHOP LOGO PREP ----------
const prepareLogoForWorkshop = async (getLogoDataURLFn, logoURL) => {
  if (!logoURL) return null;
  try {
    const d = await getLogoDataURLFn();
    if (!d) return null;

    const img = new Image();
    img.src = d;
    await img.decode();

    const maxPixels = 400;
    const maxSide = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxPixels / maxSide);

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/png");

    const maxWmm = 80;
    const maxHmm = 40;
    const r = canvas.width / canvas.height || 1;

    let logoWmm = maxWmm;
    let logoHmm = maxWmm / r;
    if (logoHmm > maxHmm) {
      logoHmm = maxHmm;
      logoWmm = maxHmm * r;
    }

    return { dataUrl, logoWmm, logoHmm };
  } catch (e) {
    devWarn("Workshop logo prep failed:", e);
    return null;
  }
};

// ---------- COMMON WORKSHOP HEADER ----------
const addWorkshopHeader = (doc, logoMeta, shopAddress) => {
  const pageW = Number(doc.internal.pageSize.getWidth());
  const marginX = 14;
  const topY = 18;
  let headerBottomY = topY;

  if (logoMeta && logoMeta.dataUrl) {
    try {
      doc.addImage(
        logoMeta.dataUrl,
        "PNG",
        marginX,
        topY,
        logoMeta.logoWmm,
        logoMeta.logoHmm
      );
      headerBottomY = topY + logoMeta.logoHmm;
    } catch (e) {
      devWarn("Workshop header logo failed:", e);
    }
  }

  if (shopAddress?.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(shopAddress.trim(), 80);
    const addrY = topY + 4;
    doc.text(lines, pageW - marginX, addrY, { align: "right" });

    const approxLineHeight = 4;
    const addrBottom = addrY + lines.length * approxLineHeight;
    headerBottomY = Math.max(headerBottomY, addrBottom);
  }

  return { marginX, pageW, headerBottomY };
};


/* --------------------------- WORKSHOP FOOTER --------------------------- */
const addWorkshopFooter = (doc) => {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(150);

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      "Powered by FabriQuote",
      pageW / 2,
      pageH - 10,
      { align: "center" }
    );
  }

  doc.setTextColor(0);
};
/* --------------------------- CUSTOMERPDF (FULL PCS) --------------------------- *//* --------------------------- CUSTOMERPDF (FULL PCS) --------------------------- */

const generateCustomerPDF = async () => {
  const { jsPDF, autoTable } = await loadPdfRuntime();
  await ensureFabricCatalogueLibrariesForRows(rowsRef.current || [], { force: true });
  const liveActionPayload = buildLiveCurtainQuotePayload({
    action: "customerPdf",
  });
  traceLiveActionPayload(liveActionPayload);
  guardStaleActionPayload(liveActionPayload, liveActionPayload.displaySummaryTotals);
  const {
    rows,
    quoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    getRowCalc: getCurtainProductionAdjustedRowCalc,
    displaySummaryTotals: pdfSummaryTotals,
    curtainCanonicalFabricSummaryRows,
  } = liveActionPayload;
  const itemEntries = rows.map((row, index) => ({ row, index }));
  const measurementEntries = itemEntries.filter(({ row }) => !isCurtainAccessoryRow(row));
  const accessoryEntries = itemEntries.filter(({ row }) => isCurtainAccessoryRow(row));
  const generatedAt = new Date();
  const generatedAtIso = generatedAt.toISOString();
  const generatedStamp = `${generatedAt.getFullYear()}${String(generatedAt.getMonth() + 1).padStart(2, "0")}${String(
    generatedAt.getDate()
  ).padStart(2, "0")}-${String(generatedAt.getHours()).padStart(2, "0")}${String(
    generatedAt.getMinutes()
  ).padStart(2, "0")}`;
  const pdfMode = isFrozenLegacyCurtainQuote ? "saved" : "live";
  const pdfCalculationVersion = CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION;
  const customerPdfMeasurementRows = measurementEntries.map(({ row, index }) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    const fabricDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
    const pdfFabricDisplay = [
      fabricDisplayInfo.isValidCatalogue ? fabricDisplayInfo.displayBrandName : "",
      fabricDisplayInfo.displayFabricCode || getCurtainRowFabricLabel(row),
    ]
      .filter(Boolean)
      .join(" / ");
    logPdfFabricSourceTrace("customer", row, fabricDisplayInfo, pdfFabricDisplay);
    return {
      row,
      index,
      calc,
      fabricDisplayInfo,
      pdfFabricDisplay,
      ...billingTrace,
      newFabricNeededMeters: round2(Number(calc.newFabricNeededMeters ?? 0)),
      pdfMeterSource: billingTrace.pdfSourceField,
    };
  });
  const customerPdfBillableMetersByFabricKey = customerPdfMeasurementRows.reduce((acc, entry) => {
    const fabricKey = normalizeFabricKey(entry.fabricKey);
    if (!fabricKey) return acc;
    acc[fabricKey] =
      Number(acc[fabricKey] || 0) +
      Number(entry.customerBillableFabricMeters || 0);
    return acc;
  }, {});
  const pdfFabricSummaries = addDirectFabricPdfSummaries(curtainCanonicalFabricSummaryRows,
    customerPdfMeasurementRows.map(({ row, calc }) => ({ row, calc, config: getRowProductConfig(row),
      fabricKey: getCurtainRowFabricKey(row), fabricLabel: getCurtainRowFabricLabel(row) })), "customer");
  const customerPdfFabricSummaryRows = pdfFabricSummaries.map((summary) => {
    const fabricKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || "");
    const rowSumCustomerBillable = roundMetersForDisplay(
      Number(customerPdfBillableMetersByFabricKey[fabricKey] || 0)
    );
    const fabricGroupCustomerBillableMeters = roundMetersForDisplay(
      Number(
        summary.fabricGroupCustomerBillableFabricMeters ??
          summary.customerBillableFabricMeters ??
          0
      )
    );
    const uiSummaryCustomerBillableMeters = roundMetersForDisplay(
      Number(
        summary.uiSummaryCustomerBillableFabricMeters ??
          summary.customerBillableFabricMeters ??
          0
      )
    );
    const pdfBillableFabricPrinted = summary.pdfDirectFabricIncluded ? summary.customerBillableFabricMeters : uiSummaryCustomerBillableMeters;
    return {
      ...summary,
      fabricKey,
      customerBillableFabricMeters: pdfBillableFabricPrinted,
      pdfBillableFabricPrinted,
      pdfBillableSource: "UI Fabric Summary customerBillableFabricMeters",
      rowSumCustomerBillable,
      fabricGroupCustomerBillableMeters,
      uiSummaryCustomerBillableMeters,
    };
  });

  if (IS_DEV_UI_ENABLED) {
    customerPdfMeasurementRows.forEach((entry) => {
      devLog("CUSTOMER_PDF_FABRIC_ROW_SOURCE_AUDIT", {
        quoteNo,
        rowId: entry.rowId,
        itemName: entry.itemName,
        product: entry.product,
        fabricCode: entry.fabricCode,
        widthIn: entry.widthIn,
        heightIn: entry.heightIn,
        fabricWidthIn: entry.fabricWidthIn,
        usableWidthDeductionIn: entry.usableWidthDeductionIn,
        fullness: entry.fullness,
        rawPanels: entry.rawPanels,
        floorPanels: entry.floorPanels,
        halfStepProductionPanels: entry.halfStepProductionPanels,
        wholeBillingPanels: entry.wholeBillingPanels,
        cutHeightIn: entry.cutHeightIn,
        fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
        customerBillableFabricMeters: entry.customerBillableFabricMeters,
        workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
        rowNewFabricMeters: entry.rowNewFabricMeters,
        newFullCuts: entry.newFullCuts,
        cutFabricMeters: entry.cutFabricMeters,
        liveScreenOrderedFabricMeters: entry.liveScreenOrderedFabricMeters,
        pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
        pdfSourceField: entry.pdfSourceField,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
      });
      devLog("CUSTOMER_PDF_ROW_METER_SOURCE_TRACE", {
        quoteNo,
        rowId: entry.rowId,
        itemName: entry.itemName,
        fabricCode: entry.fabricCode,
        product: entry.product,
        liveScreenOrderedFabricMeters: entry.liveScreenOrderedFabricMeters,
        customerBillableFabricMeters: entry.customerBillableFabricMeters,
        fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
        workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
        newFabricNeededMeters: entry.newFabricNeededMeters,
        cutFabricMeters: entry.cutFabricMeters,
        rowNewFabricMeters: entry.rowNewFabricMeters,
        pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
        pdfMeterSource: entry.pdfMeterSource,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
      });
      if (
        passHalfPieceSavingsToCustomer === false &&
        Math.abs(entry.pdfOrderedFabricPrinted - entry.customerBillableFabricMeters) > 0.05
      ) {
        devError("CUSTOMER_PDF_WRONG_FABRIC_SOURCE", {
          quoteNo,
          rowId: entry.rowId,
          itemName: entry.itemName,
          pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
          customerBillableFabricMeters: entry.customerBillableFabricMeters,
          pdfMeterSource: entry.pdfMeterSource,
        });
      }
    });

    customerPdfFabricSummaryRows.forEach((summary) => {
      const summaryRows = customerPdfMeasurementRows.filter(
        (entry) => normalizeFabricKey(entry.fabricKey) === summary.fabricKey
      );
      devLog("CUSTOMER_PDF_FABRIC_SUMMARY_TRACE", {
        quoteNo,
        fabricKey: summary.fabricKey,
        fabricCode: summary.fabricCode || summary.displayName || "",
        brandName: summary.brandName || "",
        cutFabricMeters: Number(summary.cutFabricMeters || 0),
        fullOrderedFabricMeters: Number(summary.fullOrderedFabricMeters || 0),
        halfReusedMeters: Number(summary.halfReusedMeters || 0),
        newFabricNeededMeters: Number(summary.newFabricNeededMeters || 0),
        workshopRequiredMeters: Number(summary.workshopFabricRequiredMeters || 0),
        customerBillableFabricMeters: Number(summary.customerBillableFabricMeters || 0),
        pdfBillableFabricPrinted: Number(summary.pdfBillableFabricPrinted || 0),
        sourceUsed: summary.pdfBillableSource,
        rowSumCustomerBillable: summary.rowSumCustomerBillable,
        fabricGroupCustomerBillableMeters: summary.fabricGroupCustomerBillableMeters,
        uiSummaryCustomerBillableMeters: summary.uiSummaryCustomerBillableMeters,
      });
      const printedVsRowDelta = round2(
        Number(summary.pdfBillableFabricPrinted || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const groupVsRowDelta = round2(
        Number(summary.fabricGroupCustomerBillableMeters || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const uiVsRowDelta = round2(
        Number(summary.uiSummaryCustomerBillableMeters || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const groupVsUiDelta = round2(
        Number(summary.fabricGroupCustomerBillableMeters || 0) -
          Number(summary.uiSummaryCustomerBillableMeters || 0)
      );
      if (
        Math.abs(printedVsRowDelta) > 0.05 ||
        Math.abs(groupVsRowDelta) > 0.05 ||
        Math.abs(uiVsRowDelta) > 0.05 ||
        Math.abs(groupVsUiDelta) > 0.05
      ) {
        devError("CUSTOMER_PDF_FABRIC_SUMMARY_MISMATCH", {
          quoteNo,
          fabricKey: summary.fabricKey,
          fabricCode: summary.fabricCode || summary.displayName || "",
          rowSumCustomerBillable: summary.rowSumCustomerBillable,
          fabricGroupCustomerBillable: summary.fabricGroupCustomerBillableMeters,
          uiSummaryCustomerBillable: summary.uiSummaryCustomerBillableMeters,
          pdfBillableFabricPrinted: summary.pdfBillableFabricPrinted,
          preferredPrintedSource: summary.pdfBillableSource,
          deltas: {
            printedVsRow: printedVsRowDelta,
            fabricGroupVsRow: groupVsRowDelta,
            uiVsRow: uiVsRowDelta,
            fabricGroupVsUi: groupVsUiDelta,
          },
          didPreferUiSummaryValue:
            Math.abs(groupVsUiDelta) > 0.05 || Math.abs(printedVsRowDelta) > 0.05,
          passHalfPieceSavingsToCustomer,
          rowLevelDebug: summaryRows.map((entry) => ({
            rowId: entry.rowId,
            itemName: entry.itemName,
            product: entry.product,
            widthIn: entry.widthIn,
            heightIn: entry.heightIn,
            fabricWidthIn: entry.fabricWidthIn,
            usableWidthDeductionIn: entry.usableWidthDeductionIn,
            fullness: entry.fullness,
            rawPanels: entry.rawPanels,
            floorPanels: entry.floorPanels,
            halfStepProductionPanels: entry.halfStepProductionPanels,
            wholeBillingPanels: entry.wholeBillingPanels,
            cutHeightIn: entry.cutHeightIn,
            pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
            customerBillableFabricMeters: entry.customerBillableFabricMeters,
            fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
            workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
            rowNewFabricMeters: entry.rowNewFabricMeters,
            newFullCuts: entry.newFullCuts,
            cutFabricMeters: entry.cutFabricMeters,
            pdfSourceField: entry.pdfSourceField,
          })),
        });
      }
    });
  }

  //  Detect if ANY row has fabric discount
  const hasAnyFabricDiscount = customerPdfMeasurementRows.some(({ row: r, calc: c }) => {
    return Number(r.discount) > 0 && (c.discountScope || "line_total") !== "none";
  });

  /* ------------ PDF INIT ------------ */
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 14;
  const topY = 18;

  /* ------------ LOGO ------------ */
  let logoWmm = 0;
  let logoHmm = 0;

  try {
    const d = await getLogoDataURL();
    if (d) {
      const img = new Image();
      img.src = d;
      await img.decode();

      const maxPixels = 400;
      const scale = Math.min(
        1,
        maxPixels / Math.max(img.width, img.height)
      );

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas
        .getContext("2d")
        .drawImage(img, 0, 0, canvas.width, canvas.height);

      const logoData = canvas.toDataURL("image/png");

      const maxWmm = 80;
      const maxHmm = 40;
      const r = canvas.width / canvas.height;

      logoWmm = maxWmm;
      logoHmm = maxWmm / r;
      if (logoHmm > maxHmm) {
        logoHmm = maxHmm;
        logoWmm = maxHmm * r;
      }

      doc.addImage(
        logoData,
        "PNG",
        marginX,
        topY,
        logoWmm,
        logoHmm
      );
    }
  } catch {}

  /* ------------ ADDRESS ------------ */
  let headerBottomY = topY + logoHmm;

  if (shopAddress?.trim()) {
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(shopAddress.trim(), 80);
    const addrY = topY + 12;
    doc.text(lines, pageW - marginX, addrY, {
      align: "right",
    });
    headerBottomY = Math.max(
      headerBottomY,
      addrY + lines.length * 4
    );
  }

  /* ------------ DIVIDER ------------ */
  const lineY = headerBottomY + 10;
  doc.line(marginX, lineY, pageW - marginX, lineY);

  let y = lineY + 8;

  /* ------------ HEADER PANEL ------------ */
  const humanDate = (() => {
    const displayDate = resolveQuoteDateForDisplay(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    return displayDate || date || todayISO();
  })();
  const customerPdfSiteSnapshot = normalizeQuoteSiteSnapshot(
    liveActionPayload?.quoteSiteSelection || selectedQuote || quoteSiteSelection,
    { customerName }
  );
  const customerPdfSiteLines = [
    customerPdfSiteSnapshot.siteName
      ? `Site / Project: ${customerPdfSiteSnapshot.siteName}`
      : "",
    customerPdfSiteSnapshot.siteAddress
      ? `Site Address: ${customerPdfSiteSnapshot.siteAddress}`
      : "",
  ].filter(Boolean);
  const customerHeaderPanelHeight = 38 + customerPdfSiteLines.length * 6;

  doc.setFillColor(245, 245, 248);
  doc.roundedRect(
    marginX + 6,
    y,
    pageW - marginX * 2 - 6,
    customerHeaderPanelHeight,
    3,
    3,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(22, 61, 174);
  doc.text("Customer Quotation", marginX + 10, y + 8);

  doc.setFontSize(10);
  doc.setTextColor(0);

  doc.text("Customer:", marginX + 10, y + 18);
  doc.text(customerName || "", marginX + 40, y + 18);

  doc.text("Quote No:", marginX + 10, y + 25);
  doc.text(quoteNo || "", marginX + 40, y + 25);

  doc.text("Date:", marginX + 10, y + 32);
  doc.text(humanDate, marginX + 40, y + 32);

  customerPdfSiteLines.forEach((line, index) => {
    doc.text(line, marginX + 10, y + 39 + index * 6);
  });

  y += customerHeaderPanelHeight + 8;

  /* ------------ TABLE HEAD ------------ */
  const pdfComponents = customerPdfMeasurementRows.map(({ row, calc }) => resolveQuotePdfComponents(row, calc, getRowProductConfig(row)));
  const hasPdfFabric = pdfComponents.some(component => component.usesSeparateFabricCost);
  const hasPdfSqft = pdfComponents.some(component => component.requiresSqftRate);
  const pdfQuantity = value => value === null ? "Not recorded" : Number(value).toFixed(2);
  const tableHead = [
    "Item",
    "Product",
    "Fabric / Code",
    "W",
    "H",
  ];
  if (hasPdfFabric) tableHead.push("MRP/m");

  if (hasAnyFabricDiscount) {
    tableHead.push("Disc. %");
  }

  if (hasPdfSqft) tableHead.push("Billable Sqft", "Rate/sqft");
  if (hasPdfFabric) tableHead.push("Stitch", "Ordered Fabric (m)");
  tableHead.push("Total");

  /* ------------ TABLE BODY ------------ */
  const tableBody = customerPdfMeasurementRows.map(({ row: r, calc: c, pdfOrderedFabricPrinted, pdfFabricDisplay }) => {
    const component = resolveQuotePdfComponents(r, c, getRowProductConfig(r));
    const row = [
      [
        r.item || "",
        [r.roomName || r.room, r.areaName || r.area, r.windowLabel || r.windowName || r.window]
          .filter(Boolean)
          .join(" / "),
      ]
        .filter(Boolean)
        .join("\n"),
      r.productType || "",
      pdfFabricDisplay || "",
     // WIDTH shown in selected unit
displayValue(r.width, unit) + " " + unitLabel(unit),

// HEIGHT shown in selected unit
displayValue(r.height, unit) + " " + unitLabel(unit),

    ];
    if (hasPdfFabric) row.push(component.requiresMrpPerMeter
      ? component.mrpPerMeter === null ? "Not recorded" : formatPdfMoney(component.mrpPerMeter, currency) : "");

    if (hasAnyFabricDiscount) {
      row.push(
        Number(r.discount) > 0 && (c.discountScope || "line_total") !== "none"
          ? `${r.discount}%`
          : ""
      );
    }

    if (hasPdfSqft) row.push(component.requiresSqftRate ? pdfQuantity(component.billableSqft) : "",
      component.requiresSqftRate ? component.sqftRate === null ? "Not recorded" : formatPdfMoney(component.sqftRate, currency) : "");

const customerPdfTrackConfig = c.trackConfig || {};
const customerTrackUnit =
  customerPdfTrackConfig.totalSegmentLengthUnit ||
  customerPdfTrackConfig.segmentUnit ||
  getRowMeasurementUnit(r, unit);
const customerTrackSegments = formatTrackSegmentsInline(customerPdfTrackConfig);
const customerTrackLengthLabel =
  Number(customerPdfTrackConfig.totalSegmentLength || 0) > 0
    ? `${formatLengthWithUnit(
        customerPdfTrackConfig.totalSegmentLength || 0,
        customerTrackUnit
      )} / ${Number(customerPdfTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
    : Number(customerPdfTrackConfig.totalTrackLengthFt || 0) > 0
    ? `${Number(customerPdfTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
    : "";
const customerPdfTrackParts =
  customerPdfTrackConfig?.isBended
    ? [
        `Shape: ${getBendedTrackShapeMeta(customerPdfTrackConfig.shape).label}`,
        customerPdfTrackConfig.bendLocationSummary
          ? `Bend Location: ${customerPdfTrackConfig.bendLocationSummary}`
          : "",
        customerTrackSegments ? `Segments: ${customerTrackSegments}` : "",
        customerTrackLengthLabel ? `Track Length: ${customerTrackLengthLabel}` : "",
        `${Number(customerPdfTrackConfig.bendCount || 0)} bend${
          Number(customerPdfTrackConfig.bendCount || 0) === 1 ? "" : "s"
        }${
          customerPdfTrackConfig.angleDeg
            ? ` at ${Number(customerPdfTrackConfig.angleDeg).toFixed(0)} deg`
            : ""
        }`,
        Number(customerPdfTrackConfig.bendAmount || 0) > 0
          ? `Bend Charge: ${Number(customerPdfTrackConfig.bendCount || 0)} x ${formatPdfMoney(
              customerPdfTrackConfig.bendChargePerBend || 0,
              currency
            )} = ${formatPdfMoney(customerPdfTrackConfig.bendAmount || 0, currency)}`
          : "",
        customerPdfTrackConfig.notes ? `Track Notes: ${customerPdfTrackConfig.notes}` : "",
      ].filter(Boolean)
    : [];

if (hasPdfFabric) row.push(
  c.isCurtain || c.trackConfig?.isBended
    ? [r.stitchingStyle || "", ...customerPdfTrackParts].filter(Boolean).join("\n")
    : ""
);

if (hasPdfFabric) row.push(
  component.usesSeparateFabricCost ? pdfQuantity(component.isDirectFabric || component.usesSavedSnapshot ? component.customerFabricMeters : pdfOrderedFabricPrinted ?? component.customerFabricMeters) : ""
);

row.push(
  formatPdfMoney(c.rowTotal, currency)
);

    return row;
  });

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [tableHead],
    body: tableBody,
    rowPageBreak: "avoid",
    styles: {
      fontSize: 8.8,
      cellPadding: 2.4,
      textColor: [15, 23, 42],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      valign: "middle",
    },
    headStyles: {
      fillColor: [25, 76, 180],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 255],
    },
    columnStyles: {
      3: { halign: "right" },
      4: { halign: "right" },
      ...Object.fromEntries(tableHead.map((label, index) => [index, label]).filter(([, label]) => ["MRP/m", "Disc. %", "Billable Sqft", "Rate/sqft", "Ordered Fabric (m)", "Total"].includes(label)).map(([index]) => [index, { halign: "right" }])),
    },
  });

  const liningDetailRows = measurementEntries
    .map(({ row: r, index }) => {
      const c = getCurtainProductionAdjustedRowCalc(r, index);
      const lining = normalizeLiningForSave(
        {
          ...r,
          lining:
            c.lining ||
            calculateCurtainRowLining(r, 0, false, {
              sqft: c.adjSqft ?? c.billableSqft ?? 0,
            }),
        },
        getRowProductConfig(r),
        c
      );
      if (!curtainRowSupportsLining(r, c) || !lining.enabled) return null;

      const liningBrandName =
        sanitizeEditableText(lining.brandName || "") ||
        getFabricBrandNameById(lining.brandId);
      const liningFabricLabel = [liningBrandName, sanitizeEditableText(lining.fabricCode || "")]
        .filter(Boolean)
        .join(" / ");
      const isSqftLining = lining.chargeMethod === "sqft";
      const qtyLabel = isSqftLining
        ? `${Number(lining.sqft || 0).toFixed(2)} sqft`
        : `${Number(lining.billableMeters || 0).toFixed(2)} m`;
      const rateValue = isSqftLining
        ? parseNonNegativeNumberInput(lining.ratePerSqft, 0)
        : parseNonNegativeNumberInput(lining.ratePerMeter, 0);

      return [
        r.item || "",
        getCurtainFabricDisplayWithBrand(r) || "",
        getCurtainLiningLabel(lining),
        liningFabricLabel,
        isSqftLining ? "Per sqft" : "Per meter",
        qtyLabel,
        formatPdfMoney(rateValue, currency),
        formatPdfMoney(lining.amount || 0, currency),
      ];
    })
    .filter(Boolean);

  if (liningDetailRows.length > 0) {
    y = doc.lastAutoTable.finalY + 10;
    if (y > pageH - 38) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Lining Details", marginX, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [["Item", "Main Fabric", "Lining Type", "Lining Fabric / Code", "Method", "Qty", "Rate", "Amount"]],
      body: liningDetailRows,
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
    });
  }

  if (accessoryEntries.length > 0) {
    y = doc.lastAutoTable.finalY + 10;
    if (y > pageH - 36) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Motors / Tracks / Accessories", marginX, y);
    y += 5;

    const accessoryHead = [
      "#",
      "Item",
      "Product",
      "Description",
      "Qty / Length",
      "Unit",
      "Rate",
      "Discount",
      "GST",
      "Total",
    ];
    const accessoryBody = accessoryEntries.map(({ row: r, index }, displayIndex) => {
      const c = getCurtainProductionAdjustedRowCalc(r, index);
      const calcType = c.calculationType || r.calculationType || "";
      const isLengthRow = calcType === "running_length_rate";
      const isManualRow = calcType === "manual_amount";
      const qtyValue = isLengthRow
        ? Number(r.length || c.runningLength || 0)
        : isManualRow
        ? Number(r.qty || r.quantity || 1)
        : Number(r.qty || r.quantity || c.quantity || 0);
      const unitValue = isLengthRow
        ? r.lengthUnit || c.lengthUnit || "ft"
        : r.unit || c.unit || "pcs";
      const rateValue = isLengthRow
        ? r.ratePerLength || c.ratePerLength || 0
        : isManualRow
        ? r.manualAmount || 0
        : r.unitRate || c.unitRate || 0;

      return [
        String(displayIndex + 1),
        r.item || "",
        r.productName || r.productType || r.product || "",
        r.description || r.modelCode || r.notes || "",
        Number(qtyValue || 0).toFixed(qtyValue % 1 ? 2 : 0),
        unitValue,
        formatPdfMoney(rateValue || 0, currency),
        Number(r.discount || 0) > 0 && (c.discountScope || "line_total") !== "none"
          ? `${r.discount}%`
          : "",
        `${resolveCurtainAccessoryRowGstRate(r)}%`,
        formatPdfMoney(c.rowTotal || 0, currency),
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [accessoryHead],
      body: accessoryBody,
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "middle",
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        4: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
      },
    });
  }

  if (customerPdfFabricSummaryRows.length > 0) {
    y = Number(doc.lastAutoTable?.finalY || y) + 10;
    if (y > pageH - 42) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Fabric Summary", marginX, y);
    y += 5;

    const customerPdfFabricPhotoImages = await Promise.all(
      customerPdfFabricSummaryRows.map(async (summary) =>
        loadFabricPhotoForPdf(await resolveFabricSummaryPhotoForPdf(summary))
      )
    );

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [["Fabric Photo", "Brand", "Fabric / SR", "Fabric Width", "Used in", "Billable Fabric", "Repeat", "Notes"]],
      body: customerPdfFabricSummaryRows.map((summary) => [
        "",
        summary.brandName || "",
        summary.fabricCode || summary.displayName || "",
        summary.hasMixedFabricWidths
          ? (summary.fabricWidthValues || [])
              .map((value) => getFabricWidthDisplayLabel(value))
              .filter(Boolean)
              .join(", ")
          : getFabricWidthDisplayLabel(summary.fabricWidthIn) || "54 in",
        (summary.usedInItems || []).slice(0, 6).join(", "),
        summary.pdfFabricMetersUnavailable ? "Not recorded" : `${Number(summary.pdfBillableFabricPrinted ?? summary.customerBillableFabricMeters ?? 0).toFixed(2)} m`,
        getRepeatSummaryDisplay(summary),
        [
          summary.notes || "",
          useHalfPieceReuse &&
          passHalfPieceSavingsToCustomer &&
          Number(curtainHalfPieceAudit?.totalHalfPiecesReused || 0) > 0
            ? "Half-piece savings applied"
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
      ]),
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "top",
      },
      headStyles: {
        fillColor: [22, 61, 174],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        0: { cellWidth: 18, halign: "center" },
        5: { halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.minCellHeight = 18;
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 0) return;
        const image = customerPdfFabricPhotoImages[data.row.index];
        if (!image?.dataUrl) return;
        const size = Math.min(14, data.cell.width - 3, data.cell.height - 3);
        if (size <= 0) return;
        const x = data.cell.x + (data.cell.width - size) / 2;
        const yPos = data.cell.y + (data.cell.height - size) / 2;
        doc.addImage(image.dataUrl, image.format || "JPEG", x, yPos, size, size);
        if (image.sourceUrl && typeof doc.link === "function") {
          doc.link(x, yPos, size, size, { url: image.sourceUrl });
        }
      },
    });
  }

  /* ------------ SUMMARY CARD ------------ */
  let cardY = doc.lastAutoTable.finalY + 12;
  const pdfGstMode = normalizeCurtainGstMode(
    pdfSummaryTotals.quoteGstMode ??
      pdfSummaryTotals.gstMode ??
      pdfSummaryTotals.gstBreakdown?.quoteGstMode ??
      pdfSummaryTotals.gstBreakdown?.mode
  );
  const hasPdfMixedGst = isMixedCurtainGstMode(pdfGstMode);
  const pdfGstRate = resolveCurtainQuoteGstRate(pdfSummaryTotals);
  const measurementSubtotal = round2(
    customerPdfMeasurementRows.reduce(
      (sum, { calc }) =>
        sum + Number(calc.rowTotal || 0),
      0
    )
  );
  const accessorySubtotal = round2(
    accessoryEntries.reduce(
      (sum, { row, index }) =>
        sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const sub = Number(pdfSummaryTotals.subTotal || 0).toFixed(2);
  const discVal = Number(pdfSummaryTotals.discountValue || 0);
  const discAmount = Number(pdfSummaryTotals.discountAmount || 0);
  const taxable = Number(pdfSummaryTotals.taxableAmount || 0).toFixed(2);
  const gstAmount = Number(pdfSummaryTotals.gstAmount || 0).toFixed(2);
  const totalAfterGst = Number(
    pdfSummaryTotals.totalAfterGst ??
      Number(pdfSummaryTotals.taxableAmount || 0) + Number(pdfSummaryTotals.gstAmount || 0)
  ).toFixed(2);
  const grand = Number(pdfSummaryTotals.grandTotal || 0).toFixed(2);
  const pdfSubtotal = round2(measurementSubtotal + accessorySubtotal);
  const screenSubtotal = round2(Number(pdfSummaryTotals.subTotal || 0));
  const screenGrandTotal = round2(Number(pdfSummaryTotals.grandTotal || 0));
  const pdfGrandTotal = screenGrandTotal;

  if (IS_DEV_UI_ENABLED) {
    const fabricSummaryTotalsRaw = customerPdfFabricSummaryRows.reduce(
      (acc, summary) => {
        acc.cutFabric += Number(summary.cutFabricMeters || 0);
        acc.fullOrdered += Number(summary.fullOrderedFabricMeters || 0);
        acc.newFabricNeeded += Number(summary.newFabricNeededMeters || 0);
        acc.workshopRequired += Number(summary.workshopFabricRequiredMeters || 0);
        acc.customerBillable += Number(
          summary.pdfBillableFabricPrinted ?? summary.customerBillableFabricMeters ?? 0
        );
        return acc;
      },
      {
        cutFabric: 0,
        fullOrdered: 0,
        newFabricNeeded: 0,
        workshopRequired: 0,
        customerBillable: 0,
      }
    );
    const fabricSummaryTotals = {
      cutFabric: roundMetersForDisplay(fabricSummaryTotalsRaw.cutFabric),
      fullOrdered: roundMetersForDisplay(fabricSummaryTotalsRaw.fullOrdered),
      newFabricNeeded: roundMetersForDisplay(fabricSummaryTotalsRaw.newFabricNeeded),
      workshopRequired: roundMetersForDisplay(fabricSummaryTotalsRaw.workshopRequired),
      customerBillable: roundMetersForDisplay(fabricSummaryTotalsRaw.customerBillable),
    };
    devLog("CUSTOMER_PDF_GENERATION_TRACE", {
      quoteNo,
      generatedAt: generatedAtIso,
      pdfMode,
      calculationVersion: pdfCalculationVersion,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      grandTotal: screenGrandTotal,
      fabricSummaryTotals,
      filenameStamp: generatedStamp,
    });
    devLog("CUSTOMER_PDF_GENERATION_VERSION_TRACE", {
      quoteNo,
      generatedAt: generatedAtIso,
      pdfMode,
      calculationVersion: pdfCalculationVersion,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      grandTotal: screenGrandTotal,
      fabricSummaryTotals,
    });
    devLog("CUSTOMER_PDF_TOTAL_SOURCE_AUDIT", {
      quoteNo,
      screenSubtotal,
      pdfSubtotal,
      screenGrandTotal,
      pdfGrandTotal,
      customerBillableFabricTotal: fabricSummaryTotals.customerBillable,
      pdfFabricSummaryTotal: fabricSummaryTotals.customerBillable,
      passHalfPieceSavingsToCustomer,
      source: "curtainDisplaySummaryTotals || summaryTotals",
    });
    if (
      Math.abs(pdfSubtotal - screenSubtotal) > 0.05 ||
      Math.abs(pdfGrandTotal - screenGrandTotal) > 0.05
    ) {
      devError("CUSTOMER_PDF_TOTAL_MISMATCH", {
        quoteNo,
        screenSubtotal,
        pdfSubtotal,
        screenGrandTotal,
        pdfGrandTotal,
        difference: {
          subtotal: round2(pdfSubtotal - screenSubtotal),
          grandTotal: round2(pdfGrandTotal - screenGrandTotal),
        },
      });
    }
  }
  const hasPdfDiscount = discAmount > 0 || discVal > 0;
  const hasPdfFabricGst =
    hasPdfMixedGst && Number(pdfSummaryTotals.taxableAmount || 0) > 0;
  const hasPdfOtherGst =
    hasPdfMixedGst && Number(pdfSummaryTotals.taxableAmount || 0) > 0;
  const hasPdfMeasurementGst = !hasPdfMixedGst &&
    Number(pdfSummaryTotals.measurementSubtotalBeforeDiscount || 0) > 0;
  const hasPdfAccessoryGst = !hasPdfMixedGst &&
    Number(pdfSummaryTotals.accessorySubtotalBeforeDiscount || 0) > 0;
  const hasPdfTotalGst =
    hasPdfMixedGst ||
    pdfSummaryTotals.hasMixedGstRates ||
    (Number(pdfSummaryTotals.measurementGstAmount || 0) > 0 &&
      Number(pdfSummaryTotals.accessoryGstAmount || 0) > 0);
  const cardH =
    75 +
    (measurementSubtotal > 0 ? 7 : 0) +
    (accessorySubtotal > 0 ? 7 : 0) +
    (hasPdfDiscount ? 7 : 0) +
    (hasPdfFabricGst ? 7 : 0) +
    (hasPdfOtherGst ? 7 : 0) +
    (hasPdfMeasurementGst ? 7 : 0) +
    (hasPdfAccessoryGst ? 7 : 0) +
    (hasPdfTotalGst ? 7 : 0);

  if (cardY + cardH > pageH - 20) {
    doc.addPage();
    cardY = 20;
  }

  const cardX = marginX;
  const cardW = pageW - marginX * 2;

  doc.setDrawColor(180);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(cardX, cardY, cardW, cardH, 4, 4, "DF");

  let sy = cardY + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Summary", cardX + 4, sy);

  sy += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  if (measurementSubtotal > 0) {
    doc.text("Curtains & Blinds:", cardX + 4, sy);
    doc.text(formatPdfMoney(measurementSubtotal, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    sy += 7;
  }

  if (accessorySubtotal > 0) {
    doc.text("Accessories:", cardX + 4, sy);
    doc.text(formatPdfMoney(accessorySubtotal, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    sy += 7;
  }

  doc.text("Subtotal:", cardX + 4, sy);
  doc.text(formatPdfMoney(sub, currency), cardX + cardW - 6, sy, {
    align: "right",
  });

  sy += 7;
  doc.text("Taxable Amount:", cardX + 4, sy);
  doc.text(formatPdfMoney(taxable, currency), cardX + cardW - 6, sy, {
    align: "right",
  });

  if (hasPdfMixedGst) {
    if (hasPdfFabricGst) {
      sy += 7;
      doc.text("Fabric GST @ 5%:", cardX + 4, sy);
      doc.text(
        formatPdfMoney(pdfSummaryTotals.fabricGstAmount || 0, currency),
        cardX + cardW - 6,
        sy,
        { align: "right" }
      );
    }
    if (hasPdfOtherGst) {
      sy += 7;
      doc.text("Other GST @ 18%:", cardX + 4, sy);
      doc.text(
        formatPdfMoney(pdfSummaryTotals.otherGstAmount || 0, currency),
        cardX + cardW - 6,
        sy,
        { align: "right" }
      );
    }
  } else if (hasPdfMeasurementGst) {
    sy += 7;
    const measurementGstLabel =
      accessorySubtotal > 0
        ? `GST (Curtains/Blinds @ ${pdfGstRate}%):`
        : `GST @ ${pdfGstRate}%:`;
    doc.text(measurementGstLabel, cardX + 4, sy);
    doc.text(
      formatPdfMoney(pdfSummaryTotals.measurementGstAmount || 0, currency),
      cardX + cardW - 6,
      sy,
      { align: "right" }
    );
  }

  if (hasPdfAccessoryGst) {
    sy += 7;
    const accessoryRates = pdfSummaryTotals.accessoryGstRates || [];
    const accessoryGstLabel =
      accessoryRates.length > 1
        ? "GST (Accessories mixed):"
        : accessoryRates.length === 1
        ? `GST (Accessories @ ${accessoryRates[0]}%):`
        : "GST (Accessories):";
    doc.text(accessoryGstLabel, cardX + 4, sy);
    doc.text(formatPdfMoney(pdfSummaryTotals.accessoryGstAmount || 0, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
  }

  if (hasPdfTotalGst) {
    sy += 7;
    doc.setFont("helvetica", "bold");
    doc.text("Total GST:", cardX + 4, sy);
    doc.text(formatPdfMoney(gstAmount, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    doc.setFont("helvetica", "normal");
  }

  sy += 7;
  doc.setFont("helvetica", "bold");
  doc.text("Total After GST:", cardX + 4, sy);
  doc.text(formatPdfMoney(totalAfterGst, currency), cardX + cardW - 6, sy, {
    align: "right",
  });
  doc.setFont("helvetica", "normal");

  if (hasPdfDiscount) {
    sy += 7;
    doc.text("Post-GST Discount:", cardX + 4, sy);
    doc.text(
      pdfSummaryTotals.discountType === "percent"
        ? `- ${discVal}% / ${formatPdfMoney(discAmount, currency)}`
        : `- ${formatPdfMoney(discAmount, currency)}`,
      cardX + cardW - 6,
      sy,
      { align: "right" }
    );
  }

  const barY = cardY + cardH - 18;
  doc.setFillColor(37, 99, 235);
  doc.roundedRect(
    cardX + 6,
    barY,
    cardW - 12,
    11,
    3,
    3,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setTextColor(255);
  doc.text(
    Number(pdfSummaryTotals.gstAmount || 0) > 0 ? "Grand Total (Incl. GST):" : "Grand Total:",
    cardX + 10,
    barY + 7
  );
  doc.text(
    formatPdfMoney(grand, currency),
    cardX + cardW - 10,
    barY + 7,
    { align: "right" }
  );

  doc.setTextColor(0);

  /* ------------ FOOTER ------------ */
  doc.setFontSize(9);
  doc.setTextColor(150);
  doc.text(
    "Powered by FabriQuote",
    pageW / 2,
    pageH - 10,
    { align: "center" }
  );

  /* ------------ SAVE ------------ */
  const safeCustomerPdfName = (value) =>
    String(value || "quote")
      .trim()
      .replace(/[^\w-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "quote";
  doc.save(
    `Customer-${safeCustomerPdfName(customerName || "Customer")}-${safeCustomerPdfName(
      quoteNo || "Draft"
    )}-${generatedStamp}.pdf`
  );
};

const runQuoteSentPromptPdfDownload = async (targetQuoteType) => {
  if (targetQuoteType === GENERAL_QUOTE_TYPE) {
    await downloadGeneralQuotationPDF();
    return;
  }
  await generateCustomerPDF();
};

const closeQuoteSentPrompt = () => {
  setQuoteSentPromptState((prev) =>
    prev.loading
      ? prev
      : {
          open: false,
          quoteType: "",
          quoteContext: null,
          followUpDate: "",
          ownerUid: "",
          ownerName: "",
          note: "",
          createOpportunity: false,
          loading: false,
          error: "",
        }
  );
};

const updateQuoteSentPromptField = (field, value) => {
  setQuoteSentPromptState((prev) => ({
    ...prev,
    [field]: value,
    error: "",
  }));
};

const handleQuoteSentDownloadOnly = async () => {
  const prompt = quoteSentPromptState;
  if (!prompt.open || prompt.loading) return;

  setQuoteSentPromptState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    await runQuoteSentPromptPdfDownload(prompt.quoteType);
    setQuoteSentPromptState({
      open: false,
      quoteType: "",
      quoteContext: null,
      followUpDate: "",
      ownerUid: "",
      ownerName: "",
      note: "",
      createOpportunity: false,
      loading: false,
      error: "",
    });
  } catch (error) {
    devError("QUOTE_SENT_DOWNLOAD_ONLY_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
      quoteId: prompt.quoteContext?.quoteId || "",
      quoteNo: prompt.quoteContext?.quoteNo || "",
    });
    setQuoteSentPromptState((prev) => ({
      ...prev,
      loading: false,
      error: "Could not download the PDF. Please try again.",
    }));
  }
};

const handleQuoteSentCreateFollowUp = async () => {
  const prompt = quoteSentPromptState;
  if (!prompt.open || prompt.loading || quoteFollowUpBusyRef.current) return;

  const dueDate = parseDateInputValue(prompt.followUpDate);
  if (!dueDate || !Number.isFinite(dueDate.getTime())) {
    setQuoteSentPromptState(prev => ({ ...prev, error: "Choose a valid follow-up date." }));
    return;
  }
  if (!prompt.quoteContext?.quoteId) {
    setQuoteSentPromptState(prev => ({ ...prev, error: "Save this quotation before scheduling a follow-up. You can download the PDF only." }));
    return;
  }

  quoteFollowUpBusyRef.current = true;
  setQuoteSentPromptState((prev) => ({ ...prev, loading: true, error: "" }));
  let pdfGenerated = prompt.pdfGenerated === true;
  let followUpCommitted = false;
  const trace = {
    quoteId: prompt.quoteContext.quoteId, quoteNo: prompt.quoteContext.quoteNo,
    quoteType: prompt.quoteType, workspaceUid, actorUid: auth.currentUser?.uid,
    assignedStaffUid: prompt.ownerUid, dueAt: dueDate.toISOString(),
    backendOperation: "scheduleQuotePdfFollowUp",
  };
  try {
    if (!pdfGenerated) {
      await runQuoteSentPromptPdfDownload(prompt.quoteType);
      pdfGenerated = true;
      setQuoteSentPromptState(prev => ({ ...prev, pdfGenerated: true }));
    }
    devLog("CUSTOMER_PDF_FOLLOWUP_TRACE", { ...trace, action: "schedule" });
    const response = await httpsCallable(functions, "scheduleQuotePdfFollowUp")({
      workspaceUid, quoteId: prompt.quoteContext.quoteId,
      requestId: prompt.promptRequestId,
      expectedCustomerId: prompt.quoteContext.customerId || "",
      quoteIndexId: prompt.quoteContext.quoteIndexId || prompt.quoteContext.quoteId,
      quoteType: prompt.quoteType === GENERAL_QUOTE_TYPE ? "general" : "curtain",
      dueAt: dueDate.toISOString(),
      ownerUid: prompt.ownerUid,
      note: prompt.note,
    });
    const result = response.data;
    if (result?.ok !== true || !result.taskId) throw new Error("Follow-up was not confirmed by the server.");
    followUpCommitted = true;
    devLog("CUSTOMER_PDF_FOLLOWUP_TRACE", { ...trace, action: "committed",
      taskId: result.taskId, customerId: result.customerId, dealId: result.leadId,
      opportunityId: result.opportunityId, taskStatus: result.status,
      commitAction: result.action });
    patchCrmTaskLocalState(result.taskId, {
      ...result, workspaceUid, relatedType: "quote", relatedId: result.quoteId,
      title: "Follow up quotation", taskType: "follow_up", sourceKind: "quote_follow_up",
      dueAt: new Date(result.dueAt), status: result.status || "open", updatedAt: new Date(),
    });
    notify("Follow-up scheduled", "success");
    setQuoteSentPromptState({
      open: false,
      quoteType: "",
      quoteContext: null,
      followUpDate: "",
      ownerUid: "",
      ownerName: "",
      note: "",
      createOpportunity: false,
      loading: false,
      error: "",
    });
    loadCrmAttentionRequired(workspaceUid).catch(error =>
      devWarn("CUSTOMER_PDF_FOLLOWUP_TRACE", { action: "refresh_failed", errorCode: error?.code }));
  } catch (error) {
    if (followUpCommitted) {
      devWarn("CUSTOMER_PDF_FOLLOWUP_TRACE", { ...trace, action: "local_refresh_failed", errorCode: error?.code });
      notify("Follow-up saved. Reopen CRM to refresh your work list.", "warning");
      setQuoteSentPromptState(prev => ({ ...prev, open: false, loading: false }));
      return;
    }
    devWarn("CUSTOMER_PDF_FOLLOWUP_TRACE", { ...trace, action: "failed", pdfGenerated,
      errorCode: error?.code, errorMessage: error?.message,
      failureStage: error?.details?.failureStage || (pdfGenerated ? "callable" : "pdf"),
      writeTarget: error?.details?.writeTarget || "" });
    devError("QUOTE_SENT_FOLLOWUP_FAILED", {
      code: error?.code || "",
      message: error?.message || String(error),
      stack: error?.stack || "",
      pdfGenerated,
      quoteId: prompt.quoteContext?.quoteId || "",
      quoteNo: prompt.quoteContext?.quoteNo || "",
      opportunityId: prompt.quoteContext?.opportunityId || "",
    });
    const message = pdfGenerated
      ? "Your PDF is ready, but the follow-up wasn't saved. Retry the follow-up without downloading again."
      : "Could not download the PDF. Please try again.";
    notify(message, pdfGenerated ? "warning" : "error", 6500);
    setQuoteSentPromptState((prev) => ({
      ...prev,
      loading: false,
      pdfGenerated,
      error: message,
    }));
  } finally {
    quoteFollowUpBusyRef.current = false;
  }
};


function resolveHalfPieceReuseSettings(options = {}) {
  return {
    ...DEFAULT_HALF_PIECE_REUSE_SETTINGS,
    ...(options?.halfPieceReuseSettings || {}),
  };
}

function getHalfPieceReuseFabricWidth(calc = {}, row = {}) {
  return round2(
    Number(
      calc?.calculationBreakdown?.curtainPieceCalc?.fabricWidthIn ??
        calc?.curtainPieceCalc?.fabricWidthIn ??
        row?.fabricWidthIn ??
        row?.fabricWidth ??
        0
    )
  );
}

function getHalfPieceReuseMatchKey(fabricKey = "", fabricWidthIn = 0) {
  const safeWidth = Number(fabricWidthIn || 0) > 0 ? round2(Number(fabricWidthIn)) : 0;
  return `${fabricKey || "missing-fabric"}::width:${safeWidth || "unknown"}`;
}

function isHalfPieceLeftoverCompatible(leftover = {}, requiredLengthM = 0, settings = {}) {
  const toleranceM = Math.max(0, Number(settings.heightToleranceInches || 0)) / 39.37;
  const leftoverLengthM = Number(leftover.length || 0);
  const requiredM = Number(requiredLengthM || 0);

  if (settings.heightMatchMode === "exact" || settings.heightMatchMode === "tolerance") {
    return Math.abs(leftoverLengthM - requiredM) <= toleranceM + HALF_PIECE_EPSILON;
  }

  if (settings.allowLargerPieceToBeTrimmed === false) {
    return Math.abs(leftoverLengthM - requiredM) <= toleranceM + HALF_PIECE_EPSILON;
  }

  return leftoverLengthM + toleranceM + HALF_PIECE_EPSILON >= requiredM;
}

function createEmptyHalfPieceAudit(options = {}) {
  return {
    enabled: options.enabled === true,
    settings: resolveHalfPieceReuseSettings(options),
    totalRowsConsidered: 0,
    totalRowsEligible: 0,
    totalHalfPiecesCreated: 0,
    totalHalfPiecesAvailable: 0,
    totalHalfPiecesReusable: 0,
    totalHalfPiecesReused: 0,
    totalHalfPanelNeeds: 0,
    totalPairsCreated: 0,
    totalMetersSaved: 0,
    fabricGroups: {},
    skippedRows: [],
    reasons: [],
  };
}

function getOrCreateHalfPieceAuditGroup(audit, fabricKey, seed = {}) {
  const key = fabricKey || "__missing_fabric__";
  if (!audit.fabricGroups[key]) {
    audit.fabricGroups[key] = {
      fabricKey: key,
      fabricCode: seed.fabricLabel || seed.fabricCode || "",
      brandName: seed.brandName || "",
      fabricWidths: [],
      rowsConsidered: 0,
      rowsEligible: 0,
      rowsIneligible: 0,
      halfPiecesCreated: 0,
      halfPiecesAvailable: 0,
      halfPiecesReusable: 0,
      halfPiecesReused: 0,
      halfPanelNeeds: 0,
      pairsCreated: 0,
      metersSaved: 0,
      reasons: [],
      rows: [],
    };
  }

  const group = audit.fabricGroups[key];
  if (seed.fabricLabel && !group.fabricCode) group.fabricCode = seed.fabricLabel;
  const width = Number(seed.fabricWidth || 0);
  if (width > 0 && !group.fabricWidths.includes(width)) {
    group.fabricWidths.push(width);
  }
  return group;
}

function finalizeHalfPieceAudit(audit = {}) {
  const groups = Object.values(audit.fabricGroups || {}).map((group) => {
    const reasons = Array.from(
      new Set(
        [
          ...(group.reasons || []),
          group.halfPiecesCreated <= 0
            ? "No half-piece rows were created from current row widths."
            : "",
          group.halfPiecesCreated > 0 && group.halfPiecesReused <= 0
            ? "Half pieces were available, but no later compatible row matched the same fabric, width, and cut-height rule."
            : "",
        ].filter(Boolean)
      )
    );
    return {
      ...group,
      fabricWidth:
        group.fabricWidths.length === 1
          ? group.fabricWidths[0]
          : group.fabricWidths.join(", "),
      reasons,
    };
  });

  const globalReasons = Array.from(
    new Set(
      [
        ...(audit.reasons || []),
        audit.enabled ? "" : "Half-piece reuse toggle is off.",
        audit.enabled && audit.totalHalfPiecesCreated <= 0
          ? "No half pieces were created from the current rows."
          : "",
        audit.enabled &&
        audit.totalHalfPiecesCreated > 0 &&
        audit.totalHalfPiecesReused <= 0
          ? "Half pieces were created, but no compatible later rows could reuse them."
          : "",
      ].filter(Boolean)
    )
  );

  return {
    ...audit,
    fabricGroups: groups,
    reasons: globalReasons,
  };
}

function buildReuseAwareDemands(sourceRows, activeRepeatSettings, options = {}) {
  const {
    targetFabricKey = null,
    enableReuse = true,
    debugTag = "",
    useProductWorkshopRules = true,
    getProductRules = null,
    productConfigsForRules = productConfigs,
    fabricCalcSettingsForRules = fabricCalcSettings,
    minSqftForRules = minSqft,
    quoteFullnessOverridesForCalculation = quoteFullnessOverrides,
    quoteConstructionSettingsForCalculation = quoteConstructionSettings,
    getRowCalcForPlan = calcRow,
  } = options;
  const halfPieceReuseSettings = resolveHalfPieceReuseSettings(options);
  const fallbackProductRules = { config: null, workshopRules: {} };
  const resolveProductRulesForPlan =
    typeof getProductRules === "function"
      ? getProductRules
      : (row = {}) => {
          if (!useProductWorkshopRules) return fallbackProductRules;
          const config = resolveProductConfig(
            row,
            mergeProductConfigsWithDefaults(
              productConfigsForRules,
              { ...fabricCalcSettingsForRules, minSqft: minSqftForRules }
            ),
            { ...fabricCalcSettingsForRules, minSqft: minSqftForRules }
          );
          return {
            config,
            isLegacy: shouldUseLegacyProductCalculation(row, config),
            requiredFields: config?.requiredFields || {},
            calculationType: config?.calculationType || "legacy",
            workshopRules: config?.workshopRules || {},
            pdfRules: config?.pdfRules || {},
          };
        };

  const rowPlans = [];
  const demandMap = {};
  const fabricMetersByKey = {};
  const fabricSummaryByKey = {};
  const leftoversByKey = {};
  const pool = {};
  const reuseLog = [];
  const skippedReasons = [];
  const audit = createEmptyHalfPieceAudit({
    enabled: enableReuse,
    halfPieceReuseSettings,
  });
  let leftoverSequence = 0;

  const demandKey = (lengthM) =>
    Math.round(lengthM * 1000);
  const poolKey = (fabricMatchKey, lengthM) => `${fabricMatchKey}::${demandKey(lengthM)}`;
  const createLeftoverId = (fabricKey, lengthKey, rowIndex) =>
    `${fabricKey || "fabric"}-${lengthKey}-${rowIndex}-${leftoverSequence++}`;

  const sortedRows = [...sourceRows]
    .map((row, index) => {
      const calc = getRowCalcForPlan(row, activeRepeatSettings, {
        quoteFullnessOverrides: quoteFullnessOverridesForCalculation,
        quoteConstructionSettings: quoteConstructionSettingsForCalculation,
      });
      return { row, index, calc };
    })
    .sort((a, b) => {
      const lenA = Number(a.calc.cutCurtain || a.calc.cutRoman || 0);
      const lenB = Number(b.calc.cutCurtain || b.calc.cutRoman || 0);

      return lenB - lenA;
    });

  sortedRows.forEach(({ row, index, calc }) => {
    const rowIndex = index;
    const rowProductRules = useProductWorkshopRules
      ? resolveProductRulesForPlan(row)
      : fallbackProductRules;
    const rowWorkshopRules = rowProductRules.workshopRules || {};
    const includeFabricDemand =
      debugTag === "optimizer"
        ? rowWorkshopRules.includeRollOptimizer === true
        : rowWorkshopRules.includeInWorkshopPdf !== false &&
          rowWorkshopRules.includeFabricSummary !== false;
    const fabricLabel = getCurtainRowFabricLabel(row);
    const fabricKey = getCurtainRowFabricKey(row);
    const panelMath = getHalfPieceReusePiecePlan(row, calc, {
      productConfig: rowProductRules.config || null,
      quoteFullnessOverrides: quoteFullnessOverridesForCalculation,
    });
    const halfPieceReuseAllowed = supportsHalfPieceReuse(
      row,
      rowProductRules.config || null
    );
    const fabricWidthIn =
      Number(panelMath.fabricWidthIn || panelMath.fabricWidth || 0) ||
      getHalfPieceReuseFabricWidth(calc, row);
    const auditGroup = getOrCreateHalfPieceAuditGroup(audit, fabricKey, {
      fabricLabel,
      fabricWidth: fabricWidthIn,
      brandName: row?.fabricBrandName || row?.brandName || "",
    });
    audit.totalRowsConsidered += 1;
    auditGroup.rowsConsidered += 1;

    const recordSkippedRow = (reason, extra = {}) => {
      const skipped = {
        rowIndex,
        rowId: row?.id || row?.rowId || "",
        item: row?.item || row?.name || "",
        product: row?.productType || row?.product || "",
        fabricLabel,
        fabricKey,
        fabricWidthIn,
        reason,
        ...extra,
      };
      rowPlans[rowIndex] = null;
      skippedReasons.push(skipped);
      audit.skippedRows.push(skipped);
      auditGroup.rowsIneligible += 1;
      if (reason && !auditGroup.reasons.includes(reason)) {
        auditGroup.reasons.push(reason);
      }
      auditGroup.rows.push({
        ...skipped,
        pieces: 0,
        rawPanels: panelMath.rawPanels,
        wholePanels: panelMath.wholePanels,
        halfStepPanels: panelMath.halfStepPanels,
        hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
        cutHeight: 0,
        eligibleForReuse: false,
        createsHalfPiece: false,
        halfPieceCreated: false,
        halfPieceReused: false,
        reasonIfNoReuse: reason,
      });
    };

    const logHalfPiecePanelMathTrace = (skipReason = "", eligibleForHalfPieceReuse = false) => {
      if (!IS_DEV_UI_ENABLED) return;
      devLog("HALF_PIECE_FABRIC_KEY_TRACE", {
        quoteNo,
        tag: debugTag || "default",
        rowId: row?.id || row?.rowId || "",
        itemName: row?.item || row?.name || "",
        rawFabricCode: getEditableFabricValue(row),
        brandId: row?.fabricBrandId || row?.brandId || "",
        brandName: row?.fabricBrandName || row?.brandName || "",
        normalizedFabricKey: fabricKey,
        fabricWidth: fabricWidthIn,
        repeatSettings: activeRepeatSettings?.[fabricKey] || {},
      });
      devLog("HALF_PIECE_PANEL_MATH_TRACE", {
        quoteNo,
        tag: debugTag || "default",
        rowId: row?.id || row?.rowId || "",
        itemName: row?.item || row?.name || "",
        product: row?.productType || row?.product || "",
        fabricCode: fabricLabel,
        fabricWidth: fabricWidthIn,
        usableWidthDeduction: panelMath.usableWidthDeductionIn,
        usableFabricWidth: panelMath.usableFabricWidthIn,
        width: panelMath.width,
        fullness: panelMath.fullness,
        fullnessSource: panelMath.fullnessSource,
        rawPanels: panelMath.rawPanels,
        snapshotRawPieces: panelMath.snapshotRawPieces,
        wholePanels: panelMath.wholePanels,
        halfStepPanels: panelMath.halfStepPanels,
        hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
        cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
        eligibleForHalfPieceReuse: Boolean(
          eligibleForHalfPieceReuse && halfPieceReuseAllowed
        ),
        halfPieceReuseAllowed,
        skipReason,
      });
    };

    if (rowProductRules.config && !includeFabricDemand) {
      if (IS_DEV_UI_ENABLED) {
        devLog("HALF_PIECE_PANEL_MATH_TRACE", {
          quoteNo,
          tag: debugTag || "default",
          rowId: row?.id || row?.rowId || "",
          itemName: row?.item || row?.name || "",
          product: row?.productType || row?.product || "",
          fabricCode: fabricLabel,
          fabricWidth: fabricWidthIn,
          usableWidthDeduction: panelMath.usableWidthDeductionIn,
          usableFabricWidth: panelMath.usableFabricWidthIn,
          width: panelMath.width,
          fullness: panelMath.fullness,
          fullnessSource: panelMath.fullnessSource,
          rawPanels: panelMath.rawPanels,
          wholePanels: panelMath.wholePanels,
          halfStepPanels: panelMath.halfStepPanels,
          hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
          cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
          eligibleForHalfPieceReuse: false,
          skipReason:
            debugTag === "optimizer"
              ? "product not enabled for roll optimizer"
              : "product not enabled for fabric workshop demand",
        });
      }
      recordSkippedRow(
        debugTag === "optimizer"
          ? "product not enabled for roll optimizer"
          : "product not enabled for fabric workshop demand"
      );
      return;
    }

    if (!fabricKey) {
      if (IS_DEV_UI_ENABLED) {
        devLog("HALF_PIECE_PANEL_MATH_TRACE", {
          quoteNo,
          tag: debugTag || "default",
          rowId: row?.id || row?.rowId || "",
          itemName: row?.item || row?.name || "",
          product: row?.productType || row?.product || "",
          fabricCode: fabricLabel,
          fabricWidth: fabricWidthIn,
          usableWidthDeduction: panelMath.usableWidthDeductionIn,
          usableFabricWidth: panelMath.usableFabricWidthIn,
          width: panelMath.width,
          fullness: panelMath.fullness,
          rawPanels: panelMath.rawPanels,
          wholePanels: panelMath.wholePanels,
          halfStepPanels: panelMath.halfStepPanels,
          hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
          cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
          eligibleForHalfPieceReuse: false,
          skipReason: "missing fabric",
        });
      }
      recordSkippedRow("missing fabric");
      return;
    }
    if (targetFabricKey && fabricKey !== targetFabricKey) {
      logHalfPiecePanelMathTrace("fabric mismatch with selected optimizer fabric", false);
      recordSkippedRow("fabric mismatch with selected optimizer fabric");
      return;
    }
    if (!calc.isCurtain && !calc.isRoman) {
      logHalfPiecePanelMathTrace("not a curtain or roman row", false);
      recordSkippedRow("not a curtain or roman row");
      return;
    }

    if (calc.calculationBreakdown?.fabricRules?.horizontalPatternMatchingStatus === "manual_review") {
      recordSkippedRow("horizontal pattern matching requires manual review");
      return;
    }
    const cutHeightIn = Number(calc.cutCurtain || calc.cutRoman || 0);
    if (!cutHeightIn || cutHeightIn <= 0) {
      logHalfPiecePanelMathTrace("invalid cut height", false);
      recordSkippedRow("invalid cut height");
      return;
    }
    logHalfPiecePanelMathTrace(
      halfPieceReuseAllowed
        ? ""
        : calc.isRoman
        ? "Roman blind uses balanced equal panels; half-piece reuse disabled"
        : "half-piece reuse disabled for this product",
      halfPieceReuseAllowed
    );

    const cutLengthM = cuttingDropMetres(cutHeightIn, calc.repeatSettings, calc.usesCalculationSnapshot === true, calc.repeatCuttingVersion);
    const cutLengthKey = demandKey(cutLengthM);
    const piecePlan = panelMath;
    const totalPieces = Number(piecePlan.reuseTotalPieces || 0);
    let fullCuts = Number(piecePlan.floorPieces || 0);
    const hasHalf = piecePlan.hasHalfPiece ? 1 : 0;
    const fabricMatchKey = getHalfPieceReuseMatchKey(fabricKey, fabricWidthIn);
    const rowCanUseHalfPieceReuse = enableReuse && halfPieceReuseAllowed;
    let reasonIfNoReuse = "";
    let reusedFromRowId = "";
    let reusedFromItem = "";

    if (halfPieceReuseAllowed) {
      audit.totalRowsEligible += 1;
      auditGroup.rowsEligible += 1;
    } else {
      auditGroup.rowsIneligible += 1;
    }

    let reusedHalfCount = 0;
    let createdHalf = false;
    let createdLeftoverId = "";
    let reusedLeftoverId = "";

    if (hasHalf === 1 && rowCanUseHalfPieceReuse) {
      const poolEntriesForGroup = Object.entries(pool)
        .filter(([k]) => k.startsWith(fabricMatchKey + "::"))
        .map(([k, v]) => {
          const sep = k.lastIndexOf("::");
          const lenStr = sep >= 0 ? k.slice(sep + 2) : "0";
          return {
            key: k,
            lengthKey: Number(lenStr),
            leftovers: Array.isArray(v) ? v : [],
          };
        })
        .filter((p) => p.leftovers.length > 0);

      const poolEntries = poolEntriesForGroup
        .filter(
          (p) =>
            p.leftovers.some((leftover) =>
              isHalfPieceLeftoverCompatible(
                leftover,
                cutLengthM,
                halfPieceReuseSettings
              )
            )
        )
        .map((entry) => ({
          ...entry,
          leftovers: entry.leftovers.filter((leftover) =>
            isHalfPieceLeftoverCompatible(
              leftover,
              cutLengthM,
              halfPieceReuseSettings
            )
          ),
        }))
        .sort((a, b) => {
          if (a.lengthKey !== b.lengthKey) {
            return a.lengthKey - b.lengthKey;
          }

          return Number(a.leftovers[0]?.sourceRowIndex || 0) -
            Number(b.leftovers[0]?.sourceRowIndex || 0);
        });

      if (poolEntries.length > 0) {
        const best = poolEntries[0];
        const reusedLeftover = best.leftovers.shift() || null;
        const originalPool = pool[best.key] || [];
        pool[best.key] = originalPool.filter(
          (leftover) => leftover?.id !== reusedLeftover?.id
        );
        if (pool[best.key].length <= 0) {
          delete pool[best.key];
        }
        reusedHalfCount = 1;
        reusedLeftoverId = reusedLeftover?.id || "";
        reusedFromRowId = reusedLeftover?.sourceRowId || "";
        reusedFromItem = reusedLeftover?.sourceItem || "";
        Object.values(audit.fabricGroups || {}).forEach((group) => {
          const sourceAuditRow = (group.rows || []).find(
            (auditRow) => auditRow?.createdLeftoverId === reusedLeftoverId
          );
          if (sourceAuditRow) {
            sourceAuditRow.reusedInto = row?.item || row?.name || "";
            sourceAuditRow.reuseTargetRowId = row?.id || row?.rowId || "";
          }
        });
        reuseLog.push({
          rowIndex,
          item: row?.item || row?.name || "",
          fabricLabel,
          action: "reused-half-piece",
          reusedLeftoverId,
          requiredLengthM: cutLengthM,
          leftoverLengthM: Number(reusedLeftover?.length || 0),
        });
      } else {
        fullCuts += 1;
        reasonIfNoReuse =
          poolEntriesForGroup.length > 0
            ? halfPieceReuseSettings.heightMatchMode === "exact"
              ? "existing half pieces did not match the exact cut height"
              : "existing half pieces were shorter than this cut height"
            : "no earlier half-piece available for same fabric and width";
        const newKey = poolKey(fabricMatchKey, cutLengthM);
        const leftover = {
          id: createLeftoverId(fabricKey, cutLengthKey, rowIndex),
          fabricKey,
          fabricMatchKey,
          fabricWidthIn,
          length: cutLengthM,
          lengthKey: cutLengthKey,
          sourceRowIndex: rowIndex,
          sourceRowId: row?.id || row?.rowId || "",
          sourceItem: row?.item || row?.name || "",
          type: "HALF",
        };
        if (!Array.isArray(pool[newKey])) {
          pool[newKey] = [];
        }
        pool[newKey].push(leftover);
        createdHalf = true;
        createdLeftoverId = leftover.id;
        reuseLog.push({
          rowIndex,
          item: row?.item || row?.name || "",
          fabricLabel,
          action: "created-half-leftover",
          createdLeftoverId,
          requiredLengthM: cutLengthM,
          reason: reasonIfNoReuse,
        });
      }
    } else if (hasHalf === 1) {
      fullCuts += 1;
      reasonIfNoReuse = !halfPieceReuseAllowed
        ? calc.isRoman
          ? "Roman blind uses balanced equal panels; half-piece reuse disabled"
          : "half-piece reuse disabled for this product"
        : enableReuse
        ? "half-piece reuse not applicable"
        : "reuse disabled";
      reuseLog.push({
        rowIndex,
        item: row?.item || row?.name || "",
        fabricLabel,
        action: "rounded-half-to-full-cut",
        requiredLengthM: cutLengthM,
        reason: reasonIfNoReuse,
      });
    } else {
      reasonIfNoReuse = !halfPieceReuseAllowed
        ? calc.isRoman
          ? "Roman blind uses balanced equal panels; no half piece needed"
          : "half-piece reuse disabled for this product"
        : "raw pieces rounded to a whole panel; no half piece needed";
      reuseLog.push({
        rowIndex,
        item: row?.item || row?.name || "",
        fabricLabel,
        action: "full-cuts-only",
        requiredLengthM: cutLengthM,
        reason: reasonIfNoReuse,
      });
    }

    const newMeters = round2(fullCuts * cutLengthM);
    const cutFabricMeters = round2(totalPieces * cutLengthM);
    const halfReusedMeters = round2(reusedHalfCount * cutLengthM);
    const dKey = String(cutLengthM);
    demandMap[dKey] = { length: cutLengthM, qty: (demandMap[dKey]?.qty || 0) + fullCuts };
    fabricMetersByKey[fabricKey] = round2(
      (fabricMetersByKey[fabricKey] || 0) + newMeters
    );
    if (!fabricSummaryByKey[fabricKey]) {
      fabricSummaryByKey[fabricKey] = {
        fabricKey,
        fabricLabel,
        displayName: fabricLabel,
        fabricWidthIn,
        fabricWidthSource: row?.fabricWidthSource || "",
        fabricWidthManuallyOverridden: row?.fabricWidthManuallyOverridden === true,
        catalogueDefaultFabricWidthIn:
          normalizeFabricWidthIn(row?.catalogueDefaultFabricWidthIn) || null,
        srRuleFabricWidthIn: normalizeFabricWidthIn(row?.srRuleFabricWidthIn) || null,
        fabricWidthValues: fabricWidthIn ? [fabricWidthIn] : [],
        hasMixedFabricWidths: false,
        cutFabricMeters: 0,
        halfReusedMeters: 0,
        newFabricNeededMeters: 0,
        orderedFabricMeters: 0,
        halfPiecesSaved: 0,
        halfPiecesAvailable: 0,
        halfPiecesReusable: 0,
        halfPiecesReused: 0,
        totalMetersSaved: 0,
      };
    }
    const fabricSummaryEntry = fabricSummaryByKey[fabricKey];
    if (
      fabricWidthIn &&
      !(fabricSummaryEntry.fabricWidthValues || []).some(
        (entry) => Math.abs(Number(entry || 0) - Number(fabricWidthIn || 0)) <= 0.01
      )
    ) {
      fabricSummaryEntry.fabricWidthValues = [
        ...(fabricSummaryEntry.fabricWidthValues || []),
        fabricWidthIn,
      ];
      fabricSummaryEntry.hasMixedFabricWidths =
        fabricSummaryEntry.fabricWidthValues.length > 1;
    }
    if (!fabricSummaryEntry.fabricWidthIn && fabricWidthIn) {
      fabricSummaryEntry.fabricWidthIn = fabricWidthIn;
    }
    fabricSummaryByKey[fabricKey].cutFabricMeters = round2(
      fabricSummaryByKey[fabricKey].cutFabricMeters + cutFabricMeters
    );
    fabricSummaryByKey[fabricKey].halfReusedMeters = round2(
      fabricSummaryByKey[fabricKey].halfReusedMeters + halfReusedMeters
    );
    fabricSummaryByKey[fabricKey].newFabricNeededMeters = round2(
      fabricSummaryByKey[fabricKey].newFabricNeededMeters + newMeters
    );
    fabricSummaryByKey[fabricKey].orderedFabricMeters =
      fabricSummaryByKey[fabricKey].newFabricNeededMeters;
    fabricSummaryByKey[fabricKey].halfPiecesSaved += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesAvailable += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesReusable += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesReused += Number(reusedHalfCount || 0);
    fabricSummaryByKey[fabricKey].totalMetersSaved = round2(
      fabricSummaryByKey[fabricKey].totalMetersSaved + halfReusedMeters
    );

    const rowAudit = {
      rowIndex,
      rowId: row?.id || row?.rowId || "",
      itemName: row?.item || row?.name || "",
      item: row?.item || row?.name || "",
      product: row?.productType || row?.product || "",
      fabricCode: fabricLabel,
      brandName: row?.fabricBrandName || row?.brandName || "",
      fabricKey,
      fabricMatchKey,
      fabricWidth: fabricWidthIn,
      pieces: totalPieces,
      billingPieces: Number(piecePlan.billingPieces || 0),
      rawPieces: Number(piecePlan.rawPieces || 0),
      rawPanels: Number(piecePlan.rawPanels || 0),
      wholePanels: Number(piecePlan.wholePanels || 0),
      halfStepPanels: Number(piecePlan.halfStepPanels || 0),
      hasHalfPanelRequirement: piecePlan.hasHalfPanelRequirement === true,
      halfPieceReuseAllowed,
      panelMode: piecePlan.panelMode || "",
      panelCountPerBlind: Number(piecePlan.panelCountPerBlind || 0),
      totalPanels: Number(piecePlan.totalPanels || totalPieces || 0),
      panelWidthsIn: piecePlan.panelWidthsIn || [],
      quantity: Number(piecePlan.quantity || row?.qty || row?.quantity || 1),
      jointDescription: piecePlan.jointDescription || "",
      fullness: Number(piecePlan.fullness || 0),
      fullnessSource: piecePlan.fullnessSource || "",
      usableWidthDeduction: Number(piecePlan.usableWidthDeductionIn || 0),
      usableFabricWidth: Number(piecePlan.usableFabricWidthIn || 0),
      cutHeight: cutHeightIn,
      orderedFabricMeters: newMeters,
      cutFabricMeters,
      eligibleForReuse: halfPieceReuseAllowed,
      createsHalfPiece: hasHalf === 1 && halfPieceReuseAllowed,
      halfPieceCreated: createdHalf,
      halfPieceSize: hasHalf === 1 && halfPieceReuseAllowed ? "0.5 panel" : "",
      halfPieceReused: reusedHalfCount > 0,
      reusedFrom: reusedFromItem,
      reusedFromRowId,
      reusedInto: reusedHalfCount > 0 ? row?.item || row?.name || "" : "",
      reusedLeftoverId,
      createdLeftoverId,
      reasonIfNoReuse: reusedHalfCount > 0 ? "" : reasonIfNoReuse,
    };
    auditGroup.rows.push(rowAudit);
    auditGroup.halfPiecesCreated += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesAvailable += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesReusable += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesReused += Number(reusedHalfCount || 0);
    auditGroup.halfPanelNeeds += hasHalf === 1 ? 1 : 0;
    auditGroup.pairsCreated += Number(reusedHalfCount || 0);
    auditGroup.metersSaved = round2(auditGroup.metersSaved + halfReusedMeters);
    if (reasonIfNoReuse && !auditGroup.reasons.includes(reasonIfNoReuse)) {
      auditGroup.reasons.push(reasonIfNoReuse);
    }
    audit.totalHalfPiecesCreated += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesAvailable += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesReusable += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesReused += Number(reusedHalfCount || 0);
    audit.totalHalfPanelNeeds += hasHalf === 1 ? 1 : 0;
    audit.totalPairsCreated += Number(reusedHalfCount || 0);
    audit.totalMetersSaved = round2(audit.totalMetersSaved + halfReusedMeters);

    rowPlans[rowIndex] = {
      rowIndex,
      fabricKey,
      fabricLabel,
      fabricMatchKey,
      fabricWidthIn,
      cutHeightIn,
      cutLengthM,
      rawPieces: Number(piecePlan.rawPieces || 0),
      rawPanels: Number(piecePlan.rawPanels || 0),
      billingPieces: Number(piecePlan.billingPieces || 0),
      wholePanels: Number(piecePlan.wholePanels || 0),
      halfStepPanels: Number(piecePlan.halfStepPanels || 0),
      hasHalfPanelRequirement: piecePlan.hasHalfPanelRequirement === true,
      fullness: Number(piecePlan.fullness || 0),
      fullnessSource: piecePlan.fullnessSource || "",
      usableWidthDeductionIn: Number(piecePlan.usableWidthDeductionIn || 0),
      usableFabricWidthIn: Number(piecePlan.usableFabricWidthIn || 0),
      productType: row?.productType || row?.productName || row?.product || "",
      panelMode: piecePlan.panelMode || "",
      panelCountPerBlind: Number(piecePlan.panelCountPerBlind || 0),
      panelsPerBlind: Number(piecePlan.panelsPerBlind || piecePlan.panelCountPerBlind || 0),
      panelWidthsIn: piecePlan.panelWidthsIn || [],
      jointDescription: piecePlan.jointDescription || "",
      quantity: Number(piecePlan.quantity || row?.qty || row?.quantity || 1),
      halfPieceReuseAllowed,
      reuseTotalPieces: totalPieces,
      totalPieces,
      cutFabricMeters,
      newFullCuts: fullCuts,
      fullCutsWithoutReuse: Number(piecePlan.fullCutsWithoutReuse || 0),
      newMeters,
      newFabricNeededMeters: newMeters,
      halfReusedMeters,
      reusedHalfCount,
      createdHalf,
      createdLeftoverId,
      reusedLeftoverId,
      reusedFromRowId,
      reusedFromItem,
      reasonIfNoReuse,
    };

    if (IS_DEV_UI_ENABLED) {
      devLog("HALF_PIECE_ROW_AUDIT", rowAudit);
    }
  });

  Object.entries(pool).forEach(([k, leftoverList]) => {
    if (!Array.isArray(leftoverList) || leftoverList.length <= 0) return;
    leftoverList.forEach((leftover) => {
      const leftoverFabricKey = leftover?.fabricKey || k;
      if (!leftoversByKey[leftoverFabricKey]) leftoversByKey[leftoverFabricKey] = [];
      leftoversByKey[leftoverFabricKey].push({
        ...leftover,
        fabricKey: leftoverFabricKey,
        length: Number(leftover?.length || 0),
        type: "HALF",
      });
    });
  });

  const demands = Object.values(demandMap)
    .map(({ length, qty }) => ({
      length: Number(length),
      qty: Math.max(0, Math.floor(Number(qty))),
    }))
    .filter((d) => d.qty > 0)
    .sort((a, b) => b.length - a.length);
  const finalizedAudit = finalizeHalfPieceAudit(audit);

  if (IS_DEV_UI_ENABLED) {
    devLog("REUSE PLAN:", {
      tag: debugTag || "default",
      inputRows: sourceRows.length,
      demands,
      halfPiecesCreated: finalizedAudit.totalHalfPiecesCreated,
      halfPiecesAvailable: finalizedAudit.totalHalfPiecesAvailable,
      reusedHalfCount: rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.reusedHalfCount || 0), 0),
      metersSaved: finalizedAudit.totalMetersSaved,
      auditReasons: finalizedAudit.reasons,
      reuseLog,
      skippedReasons,
      pool,
    });
  }

  return {
    demands,
    rowPlans,
    fabricMetersByKey,
    fabricSummaryByKey,
    leftoversByKey,
    pool,
    reuseLog,
    skippedReasons,
    audit: finalizedAudit,
  };
}

const isSqftOnlyBlindWorkshopProduct = (row = {}, productConfig = null) => {
  const config = productConfig ? normalizeProductConfig(productConfig) : getRowProductConfig(row);
  const productLabel = normalizeProductName(
    config?.name ||
      config?.normalizedName ||
      row.productName ||
      row.productType ||
      row.product ||
      ""
  );
  const category = normalizeProductName(config?.category || row.category || "");
  const calculationType = String(
    config?.calculationType || row.calculationType || ""
  ).trim();
  const isRomanLike =
    productLabel.startsWith("roman") ||
    productLabel.includes("roman blind") ||
    config?.fabricRules?.meterFormulaType === "roman_panels" ||
    calculationType === "sqft_plus_fabric";
  if (isRomanLike) return false;

  const usesFabricMeters =
    config?.usesFabric === true ||
    config?.fabricRules?.enabled === true ||
    ["fabric_meter", "sqft_plus_fabric"].includes(calculationType);
  if (usesFabricMeters) return false;

  return Boolean(
    isBlindSqftCalculationProduct(config || {}) ||
      calculationType === "sqft_only" ||
      category.includes("blind") ||
      productLabel.includes("blind") ||
      productLabel.includes("zebra") ||
      productLabel.includes("roller") ||
      productLabel.includes("venetian") ||
      productLabel.includes("vertical") ||
      productLabel.includes("pvc")
  );
};

const getWorkshopTemplateType = (row = {}, productConfig = null, production = null) => {
  const config = productConfig ? normalizeProductConfig(productConfig) : getRowProductConfig(row);
  if (isCurtainAccessoryRow(row)) return "accessory";

  const productLabel = normalizeProductName(
    config?.name ||
      config?.normalizedName ||
      row.productName ||
      row.productType ||
      row.product ||
      ""
  );
  const category = normalizeProductName(config?.category || row.category || "");
  const calculationType = String(
    config?.calculationType || row.calculationType || ""
  ).trim();
  const calc = production?.calc || {};

  // Explicit calculation types take precedence over legacy names/categories.
  if (calculationType === "sqft_only") return "sqft_blind";
  if (calculationType === "sqft_plus_fabric") {
    if (config.fabricRules?.meterFormulaType === "roman_panels") return "roman_blind";
    if (config.fabricRules?.meterFormulaType === "curtain_panels") return "curtain_fabric";
    return "general";
  }

  if (
    calc.isRoman === true ||
    productLabel.startsWith("roman") ||
    productLabel.includes("roman blind") ||
    config?.fabricRules?.meterFormulaType === "roman_panels"
  ) {
    return "roman_blind";
  }

  if (isSqftOnlyBlindWorkshopProduct(row, config)) {
    return "sqft_blind";
  }

  if (
    calc.isCurtain === true ||
    calculationType === "fabric_meter" ||
    category.includes("curtain") ||
    productLabel === "curtains" ||
    productLabel.includes("curtain")
  ) {
    return "curtain_fabric";
  }

  return "general";
};

const calculateCurtainRowProduction = (row = {}, options = {}) => {
  const activeRepeatSettings = options.repeatSettings || repeatSettings;
  const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
    options || {},
    "quoteFullnessOverrides"
  )
    ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
    : quoteFullnessOverrides;
  const productConfig =
    options.productConfig ||
    getRowProductConfig(row);
  const calc = calcRow(row, activeRepeatSettings, {
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });
  const fullnessResult = resolveCurtainFullness(row, productConfig, stitchingRates, {
    fabricCalcSettings,
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });
  const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
    row,
    stitchingRates,
    productConfig
  );
  const fabricLabel = getCurtainRowFabricLabel(row);
  const fabricKey = getCurtainRowFabricKey(row);
  const cutFabricMeters = round2(
    Number(calc.fabricMetersHalf ?? calc.cutFabricMeters ?? calc.fabricMeters ?? 0)
  );
  const orderedFabricMeters = round2(Number(calc.fabricMeters || 0));

  const production = {
    widthIn: num(row.width),
    heightIn: num(row.height),
    resolvedStitchLabel,
    resolvedFullness: Number(fullnessResult.fullness || 0),
    fullnessSource: fullnessResult.source || "",
    panels: Number(calc.totalPieces || calc.pcsHalf || calc.pcs || 0),
    panelMode: calc.panelMode || calc.romanPanelCalc?.panelMode || "",
    panelCountPerBlind: Number(calc.panelCountPerBlind || calc.romanPanelCalc?.panelCount || 0),
    totalPanels: Number(calc.romanPanelCalc?.totalPanels || calc.totalPieces || calc.pcsHalf || calc.pcs || 0),
    panelWidthsIn: calc.panelWidthsIn || calc.romanPanelCalc?.panelWidthsIn || [],
    halfPieceReuseAllowed: calc.halfPieceReuseAllowed === true,
    cutWidthIn: Number(row.fabricWidthIn || 0),
    cutHeightIn: Number(calc.cutCurtain || calc.cutRoman || 0),
    visibleDropIn: num(row.height),
    fabricMeters: cutFabricMeters,
    cutFabricMeters,
    orderedFabricMeters,
    newFabricMeters: orderedFabricMeters,
    reusedHalfMeters: 0,
    leftoverSaved: 0,
    trackLabel: getCurtainRowDisplay(row?.trackType || row?.trackName || row?.track, ""),
    trackLengthFt: Number(row?.trackLengthFt || 0),
    fabricLabel,
    fabricKey,
    calc,
  };

  if (IS_DEV_UI_ENABLED && calc.isCurtain) {
    devLog("FULLNESS_AND_STITCH_TRACE", {
      item: row?.item || row?.name || "",
      product: row?.productType || row?.product || "",
      visibleStitch: row?.stitchingStyle || row?.stitchingStyleId || row?.stitch || row?.stitching || row?.stitchLabel || "",
      resolvedStitchLabel,
      resolvedFullness: production.resolvedFullness,
      panels: production.panels,
      fabricMeters: production.fabricMeters,
    });
  }

  return production;
};

const getCurtainRowSignature = (
  row = {},
  production = null,
  stitchingStyles = stitchingRates,
  productConfig = null
) => {
  const resolvedProduction =
    production ||
    calculateCurtainRowProduction(row, {
      productConfig,
      repeatSettings,
    });

  return {
    id: getWorkshopRowStableId(row),
    item: getWorkshopRowItemLabel(row),
    product: getWorkshopRowProductLabel(row),
    fabric: getCurtainRowFabricLabel(row),
    stitch: getCurtainRowWorkshopStitchLabel(
      row,
      stitchingStyles,
      productConfig || getRowProductConfig(row)
    ),
    width: Number(row?.width || 0),
    height: Number(row?.height || 0),
    mrp: row?.mrp ?? "",
    rate: row?.rate ?? row?.sqftRate ?? row?.ratePerFeet ?? row?.ratePerPiece ?? "",
    fabricMeters: Number(resolvedProduction?.fabricMeters || 0).toFixed(3),
  };
};

const buildCurrentCurtainQuoteExportPayload = ({
  quoteState = {},
  rows: sourceRows = [],
  productConfigs: activeProductConfigs = productConfigs,
  stitchingStyles = stitchingRates,
  quoteSettings = {},
  halfPieceReuseEnabled = useHalfPieceReuse,
} = {}) => {
  const activeRepeatSettings =
    quoteState.repeatSettings || quoteSettings.repeatSettings || repeatSettings;
  const activeQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quoteState.quoteFullnessOverrides ||
      quoteSettings.quoteFullnessOverrides ||
      quoteFullnessOverrides ||
      {}
  );
  const sourceVisibleRows = (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => withCurtainRowId(row, index))
    .filter((row) => !isDeletedWorkshopExportRow(row))
    .filter(hasMeaningfulWorkshopExportData);
  const workshopRowsLive = sourceVisibleRows.map((row, index) =>
    createWorkshopSourceRow(row, index, sourceVisibleRows, {
      stitchingStyles,
      productConfig: getRowProductConfig(row),
    })
  );
  const workshopRows = workshopRowsLive
    .map((row) => sanitizeWorkshopRow(row))
    .filter(Boolean);

  const includeRowInWorkshop = (row) =>
    getRowProductRules(row).workshopRules?.includeInWorkshopPdf !== false;
  const getWorkshopRowGroup = (row) => {
    if (isCurtainAccessoryRow(row)) return "accessory";
    const productConfig = getRowProductConfig(row);
    const production = calculateCurtainRowProduction(row, {
      repeatSettings: activeRepeatSettings,
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      productConfig,
    });
    const templateType = getWorkshopTemplateType(row, productConfig, production);
    if (templateType === "curtain_fabric") return "curtain";
    if (templateType === "roman_blind") return "roman";
    if (templateType === "sqft_blind") return "blind";
    return "custom";
  };

  const includedWorkshopRows = getWorkshopRowsInQuotationOrder(
    workshopRows.filter(includeRowInWorkshop)
  );
  const curtainRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "curtain"
  );
  const romanRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "roman"
  );
  const blindRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "blind"
  );
  const accessoryRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "accessory"
  );
  const customRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "custom"
  );
  const curtainRomanRows = getWorkshopRowsInQuotationOrder([...curtainRows, ...romanRows]);
  const orderedProductionRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) !== "accessory"
  );

  const fabricLabelByKey = {};
  const rowProduction = workshopRows.map((row, index) => {
    const production = calculateCurtainRowProduction(row, {
      repeatSettings: activeRepeatSettings,
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      productConfig: getRowProductConfig(row),
    });
    const key = production.fabricKey;
    if (key) {
      if (!fabricLabelByKey[key] && production.fabricLabel !== WORKSHOP_EMPTY_VALUE) {
        fabricLabelByKey[key] = production.fabricLabel;
      }
    }
    return {
      row,
      index,
      ...production,
    };
  });

  const workshopPlan = buildReuseAwareDemands(curtainRomanRows, activeRepeatSettings, {
    enableReuse: halfPieceReuseEnabled,
    debugTag: "workshop",
    getProductRules: getRowProductRules,
    quoteFullnessOverridesForCalculation: activeQuoteFullnessOverrides,
  });

  const cutFabricSummary = Object.fromEntries(
    Object.entries(workshopPlan.fabricSummaryByKey || {})
      .filter(([, summary]) => Number(summary?.newFabricNeededMeters || 0) > 0)
      .map(([fabricKey, summary]) => {
        const displayName =
          summary.displayName ||
          summary.fabricLabel ||
          fabricLabelByKey[fabricKey] ||
          fabricKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        if (!fabricLabelByKey[fabricKey]) {
          fabricLabelByKey[fabricKey] = displayName;
        }
        return [
          fabricKey,
          {
            ...summary,
            displayName,
            fabricLabel: summary.fabricLabel || displayName,
            newFabricMeters: Number(summary.newFabricNeededMeters || 0),
            fabricToOrderMeters: Number(summary.fabricToOrderMeters ?? summary.newFabricNeededMeters ?? 0),
            newFabricNeededMeters: Number(summary.newFabricNeededMeters || 0),
            orderedFabricMeters: Number(summary.newFabricNeededMeters || 0),
          },
        ];
      })
  );

  const uiRowSignatures = sourceVisibleRows.map((row) =>
    getCurtainRowSignature(
      row,
      calculateCurtainRowProduction(row, {
        repeatSettings: activeRepeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        productConfig: getRowProductConfig(row),
      }),
      stitchingStyles,
      getRowProductConfig(row)
    )
  );
  const workshopRowSignatures = workshopRowsLive.map((row) =>
    getCurtainRowSignature(
      row,
      calculateCurtainRowProduction(row, {
        repeatSettings: activeRepeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        productConfig: getRowProductConfig(row),
      }),
      stitchingStyles,
      getRowProductConfig(row)
    )
  );
  const signatureMismatches = uiRowSignatures
    .map((uiSignature, index) => {
      const workshopSignature = workshopRowSignatures[index];
      if (!workshopSignature) {
        return { type: "missing-workshop-row", index, ui: uiSignature };
      }
      const mismatchFields = ["id", "item", "product", "fabric", "stitch", "width", "height", "fabricMeters"].filter(
        (field) => String(uiSignature[field] ?? "") !== String(workshopSignature[field] ?? "")
      );
      return mismatchFields.length
        ? {
            type: "signature-mismatch",
            index,
            fields: mismatchFields,
            ui: uiSignature,
            workshop: workshopSignature,
          }
        : null;
    })
    .filter(Boolean);

  if (workshopRowSignatures.length > uiRowSignatures.length) {
    workshopRowSignatures.slice(uiRowSignatures.length).forEach((signature, offset) => {
      signatureMismatches.push({
        type: "extra-workshop-row",
        index: uiRowSignatures.length + offset,
        workshop: signature,
      });
    });
  }

  return {
    quoteState,
    activeProductConfigs,
    quoteSettings,
    sourceVisibleRows,
    workshopRowsLive,
    workshopRows,
    includedWorkshopRows,
    curtainRows,
    romanRows,
    blindRows,
    accessoryRows,
    customRows,
    curtainRomanRows,
    orderedProductionRows,
    workshopPlan,
    cutFabricSummary,
    fabricLabelByKey,
    rowProduction,
    uiRowSignatures,
    workshopRowSignatures,
    signatureMismatches,
  };
};

 /* --------------------------- WORKSHOP ZIP EXPORT (WITH CUTTING) --------------------------- */
/* --------------------------- WORKSHOP ZIP EXPORT (QUOTATION ORDER) --------------------------- */

/* --------------------------- WORKSHOP ZIP EXPORT (LOW-READ PHOTO-AWARE) --------------------------- */
/* --------------------------- WORKSHOP ZIP EXPORT (W1 READY) --------------------------- */

const generateWorkshopZIP = async () => {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      notify(
        "You appear to be offline. Please reconnect and try Workshop ZIP again.",
        "warning"
      );
      return;
    }

    let jsPDF;
    let autoTable;
    let JSZip;
    let saveAs;

    try {
      const [pdfRuntime, zipRuntime] = await Promise.all([
        loadPdfRuntime(),
        loadZipRuntime(),
      ]);
      ({ jsPDF, autoTable } = pdfRuntime);
      ({ JSZip, saveAs } = zipRuntime);
      resetChunkLoadReloadGuard();
    } catch (runtimeError) {
      devError("WORKSHOP_ZIP_CHUNK_LOAD_FAILED", {
        name: runtimeError?.name || "",
        message: runtimeError?.message || String(runtimeError || ""),
        stack: runtimeError?.stack || "",
        ...getChunkLoadDebugContext(),
      });

      if (
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        notify(
          "You appear to be offline. Please reconnect and try Workshop ZIP again.",
          "warning"
        );
        return;
      }

      if (isChunkLoadError(runtimeError)) {
        handleChunkLoadErrorRecovery(runtimeError, {
          source: "generateWorkshopZIP.runtime",
          autoReload: false,
        });
        notify(
          "Workshop exporter could not load. Please refresh the page and try again.",
          "warning",
          7000
        );
        return;
      }

      throw runtimeError;
    }

    await ensureFabricCatalogueLibrariesForRows(rowsRef.current || [], { force: true });

    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: "workshopZip",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryUI,
      selectedQuote,
      customerName,
      quoteNo,
      quoteDate: date,
      quoteFullnessOverrides,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
    } = liveActionPayload;

    const buildStitchTrace = (sourceRows = [], stage = "") =>
      (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
        buildWorkshopStitchTraceRow(
          row,
          stitchingRates,
          getRowProductConfig(row),
          { stage, index }
        )
      );

    if (IS_DEV_UI_ENABLED) {
      devLog("STITCH_TRACE_1_UI_ROWS", buildStitchTrace(rows, "ui_rows"));
      devLog("STITCH_TRACE_2_EFFECTIVE_ROWS", buildStitchTrace(effectiveRows, "state_effective_rows"));
      devLog(
        "WORKSHOP_EXPORT_TRACE_UI_ROWS",
        rows.map((r) => {
          const production = calculateCurtainRowProduction(r, {
            repeatSettings,
            productConfig: getRowProductConfig(r),
          });
          return {
            id: r.id,
            item: r.item,
            product: r.product || r.productType || r.productName,
            fabric: r.fabric || r.fabricName || r.materialCode,
            stitch: r.stitch,
            stitchType: r.stitchType,
            stitching: r.stitching,
            stitchingType: r.stitchingType,
            stitchLabel: r.stitchLabel,
            stitchingLabel: r.stitchingLabel,
            selectedStitch: r.selectedStitch,
            stitchingStyleId: r.stitchingStyleId,
            stitchingStyle: r.stitchingStyle,
            width: r.width || r.w,
            height: r.height || r.h,
            mrp: r.mrp,
            rate: r.rate || r.sqftRate || r.ratePerFeet || r.ratePerPiece,
            fabricMeters: production.fabricMeters,
            rowTotal: production.calc?.rowTotal,
          };
        })
      );
    }

    const exportPayload = buildCurrentCurtainQuoteExportPayload({
      quoteState: {
        selectedQuote,
        customerName,
        quoteNo,
        quoteDate: resolveQuoteDateForInput(
          {
            ...selectedQuote,
            quoteDate: date || selectedQuote?.quoteDate,
            date: date || selectedQuote?.date,
          },
          todayISO()
        ),
        date: resolveQuoteDateForInput(
          {
            ...selectedQuote,
            quoteDate: date || selectedQuote?.quoteDate,
            date: date || selectedQuote?.date,
          },
          todayISO()
        ),
        repeatSettings,
        quoteFullnessOverrides,
      },
      rows,
      productConfigs,
      stitchingStyles: stitchingRates,
      quoteSettings: {
        ...fabricCalcSettings,
        repeatSettings,
        quoteFullnessOverrides,
      },
      halfPieceReuseEnabled: useHalfPieceReuse,
    });

    const {
      sourceVisibleRows: activeExportRows,
      workshopRowsLive,
      workshopRows: base,
      includedWorkshopRows,
      curtainRows,
      romanRows,
      blindRows,
      accessoryRows,
      customRows,
      orderedProductionRows,
      workshopPlan,
      cutFabricSummary,
      signatureMismatches,
    } = exportPayload;

    if (IS_DEV_UI_ENABLED) {
      devLog("WORKSHOP_HALF_PIECE_TOGGLE_TRACE", {
        quoteNo,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        rowsCount: rows.length,
        fabricGroupsCount: Object.keys(fabricSummaryUI || {}).length,
        workshopAudit: workshopPlan?.audit || null,
      });
      devLog("STITCH_TRACE_2_ACTIVE_EXPORT_ROWS", buildStitchTrace(activeExportRows, "active_export_rows"));
      devLog("STITCH_TRACE_3_WORKSHOP_SOURCE_ROWS", buildStitchTrace(base, "workshop_source_rows"));
      devLog(
        "WORKSHOP_EXPORT_TRACE_FINAL_ROWS",
        orderedProductionRows.map((row) => {
          const production = calculateCurtainRowProduction(row, {
            repeatSettings,
            productConfig: getRowProductConfig(row),
          });
          const rowPlan =
            workshopPlan.rowPlans?.find(
              (planRow) =>
                getWorkshopRowStableId(planRow?.row || planRow || {}) ===
                getWorkshopRowStableId(row)
            ) || {};
          return {
            id: row.id,
            item: row.item,
            product: row.product || row.productType || row.productName,
            fabric: production.fabricLabel,
            stitch: production.resolvedStitchLabel,
            width: production.widthIn,
            height: production.heightIn,
            panels: production.panels,
            fabricMeters: production.fabricMeters,
            newFabricMeters: Number(rowPlan.newMeters || production.fabricMeters || 0),
            reusedHalfMeters: Number(rowPlan.halfReusedMeters || 0),
            source: "live-rows",
          };
        })
      );
      devLog("WORKSHOP_EXPORT_FABRIC_TOTAL_COMPARE", {
        uiFabricSummary: Object.fromEntries(
          Object.entries(fabricSummaryUI || {}).map(([key, value]) => [
            key,
            {
              displayName: value.displayName,
              cutFabricMeters: Number(value.cutFabricMeters || 0).toFixed(2),
              halfReusedMeters: Number(value.halfReusedMeters || 0).toFixed(2),
              orderedFabricMeters: Number(value.orderedFabricMeters || value.newFabricNeededMeters || value.meters || 0).toFixed(2),
            },
          ])
        ),
        workshopFabricSummary: Object.fromEntries(
          Object.entries(cutFabricSummary || {}).map(([key, value]) => [
            key,
            {
              displayName: value.displayName,
              cutFabricMeters: Number(value.cutFabricMeters || 0).toFixed(2),
              newFabricMeters: Number(value.newFabricMeters || 0).toFixed(2),
              reusedHalfMeters: Number(value.reusedHalfMeters || 0).toFixed(2),
            },
          ])
        ),
        quoteNo,
        rowCountUi: rows.length,
        rowCountWorkshop: base.length,
      });
    }

    if (!base.length) {
      notify("No current quotation rows are available for workshop export.", "warning");
      return;
    }

    if (IS_DEV_UI_ENABLED) {
      const customerPdfRows = getActiveCurtainRowsForExport(rows).map(
        summarizeWorkshopExportRow
      );
      const workshopSourceRows = base.map(summarizeWorkshopExportRow);
      const staleEffectiveRows = getActiveCurtainRowsForExport(effectiveRows).map(
        summarizeWorkshopExportRow
      );

      devLog("Workshop export source comparison", {
        customerPdfRows,
        workshopSourceRows,
        effectiveRows: staleEffectiveRows,
      });
      devTable([
        ...customerPdfRows.map((row) => ({ source: "customerPdfRows", ...row })),
        ...workshopSourceRows.map((row) => ({ source: "workshopSourceRows", ...row })),
        ...staleEffectiveRows.map((row) => ({ source: "effectiveRows", ...row })),
      ]);
      devLog(
        "Workshop PDF row audit",
        base.map((row) => ({
          item: row.item || row.name,
          product: row.productType || row.product,
          fabric: row.fabric,
          fabricName: row.fabricName,
          fabricLabel: row.fabricLabel,
          resolvedFabric: getCurtainRowFabricLabel(row),
          fabricWidth: row.fabricWidthIn,
          width: row.width,
          height: row.height,
          stitch: row.stitch,
          stitchLabel: row.stitchLabel,
          stitchingStyle: row.stitchingStyle,
          resolvedStitch: getCurtainRowWorkshopStitchLabel(
            row,
            stitchingRates,
            getRowProductConfig(row)
          ),
          sourceRowId: row.sourceRowId,
          sourceIndex: row.sourceIndex,
        }))
      );
      assertNoPricingInWorkshopPdfPayload(base, {
        label: "curtainWorkshopBaseRows",
      });
    }

    if (signatureMismatches.length) {
      devError("WORKSHOP_EXPORT_STALE_OR_MISMATCHED_ROWS", {
        quoteNo,
        mismatches: signatureMismatches,
        uiSignatures: exportPayload.uiRowSignatures,
        workshopSignatures: exportPayload.workshopRowSignatures,
      });
      if (IS_DEV_UI_ENABLED) {
        notify(
          "Workshop export rows do not match the current visible quotation rows. Check console trace.",
          "error"
        );
        return;
      }
    }

    const exportValidation = validateWorkshopRowsMatchActiveRows(
      activeExportRows,
      workshopRowsLive
    );
    if (!exportValidation.ok) {
      if (IS_DEV_UI_ENABLED) {
        devError("Workshop export validation failed", exportValidation);
        devTable(exportValidation.mismatches || []);
      }
      notify(
        "Workshop export data does not match the current quotation. Please save and try again.",
        "error"
      );
      return;
    }

    console.time("workshop-zip");

    const zip = new JSZip();
    const logoMeta = await prepareLogoForWorkshop(getLogoDataURL, logoURL);
    const workshopQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
        createdAt: selectedQuote?.createdAt || selectedQuote?.updatedAt,
      },
      todayISO()
    );
    const getWorkshopRowItemWithLocation = (row = {}, fallback = "") => {
      const locationText = [
        row.roomName || row.room,
        row.areaName || row.area,
        row.windowLabel || row.windowName || row.window,
      ]
        .filter(Boolean)
        .join(" / ");
      return [getCurtainRowDisplay(row.item || row.name, fallback), locationText]
        .filter(Boolean)
        .join("\n");
    };

    const excludedRows = base
      .filter(
        (row) =>
          !includedWorkshopRows.some(
            (includedRow) =>
              getWorkshopRowStableId(includedRow) === getWorkshopRowStableId(row)
          )
      )
      .map((row) => {
        const rowRules = getRowProductRules(row);
        return {
          item: row.item || row.name || "",
          product: row.productType || row.productName || row.product || "",
          reason: "workshop PDF disabled by product config",
          resolvedProductConfigId: rowRules.config?.id || "",
          workshopRules: rowRules.workshopRules || {},
        };
      });

    if (IS_DEV_UI_ENABLED) {
      devLog("WORKSHOP PDF DEBUG", {
        totalInputRows: rows.length,
        activeExportRows: base.length,
        includedRows: includedWorkshopRows.length,
        excludedRows,
        curtainRows: curtainRows.length,
        romanRows: romanRows.length,
        blindRows: blindRows.length,
        customRows: customRows.length,
        productNames: rows.map((r) => r.product || r.productName || r.productType || ""),
      });
      if (excludedRows.length) {
        devTable(excludedRows);
      }
    }

    if (!includedWorkshopRows.length) {
      notify("No quotation rows are enabled for workshop export.", "warning");
      return;
    }

    const workshopDemandTotal = round2(
      (workshopPlan.demands || []).reduce(
        (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
        0
      )
    );
    if (IS_DEV_UI_ENABLED) {
      devLog("WORKSHOP DEMAND TOTAL:", workshopDemandTotal);
      devLog("Half-piece reuse audit", {
        enabled: useHalfPieceReuse,
        availableLeftovers: workshopPlan.leftoversByKey || {},
        demandRows: (workshopPlan.rowPlans || []).filter(Boolean),
        reuseLog: workshopPlan.reuseLog || [],
        skippedReasons: workshopPlan.skippedReasons || [],
      });
    }


    const getSafeWorkshopZipNamePart = (value = "", fallback = "workshop") => {
      const cleaned = sanitizeEditableText(value || fallback)
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      return cleaned || fallback;
    };

    const getWorkshopZipGroupMeta = (row = {}, fallbackIndex = 0) => {
      const productConfig = getRowProductConfig(row);
      const production = calculateCurtainRowProduction(row, {
        repeatSettings,
        quoteFullnessOverrides,
        productConfig,
      });
      const rawProductLabel =
        productConfig?.name ||
        row.productName ||
        row.productType ||
        row.product ||
        "Other Products";
      const normalizedProduct = normalizeProductName(
        productConfig?.normalizedName || rawProductLabel
      );
      const normalizedCategory = normalizeProductName(
        productConfig?.category || row.category || ""
      );
      const stableProductSegment =
        sanitizeFabricLibraryKeySegment(productConfig?.id || normalizedProduct) ||
        `product-${fallbackIndex + 1}`;
      const templateType = getWorkshopTemplateType(row, productConfig, production);

      if (
        production.calc?.isRoman === true ||
        normalizedProduct.startsWith("roman") ||
        normalizedProduct.includes("roman blind") ||
        templateType === "roman_blind" ||
        templateType === "curtain_fabric" ||
        production.calc?.isCurtain === true ||
        normalizedProduct.includes("curtain") ||
        normalizedCategory.includes("curtain")
      ) {
        return {
          key: "curtains_romans",
          label: "Curtains & Roman Blinds",
          title: "Workshop Sheet - Curtains & Roman Blinds",
          fileStem: "Workshop-Curtains-Roman-Blinds",
          templateType: "curtain_fabric",
          rank: 0,
        };
      }

      if (templateType === "sqft_blind" && normalizedProduct.includes("zebra")) {
        return {
          key: "zebra_blinds",
          label: "Zebra Blinds",
          title: "Workshop Sheet - Zebra Blinds",
          fileStem: "Workshop-Zebra-Blinds",
          templateType: "sqft_blind",
          rank: 10,
        };
      }

      if (templateType === "sqft_blind" && normalizedProduct.includes("roller")) {
        return {
          key: "roller_blinds",
          label: "Roller Blinds",
          title: "Workshop Sheet - Roller Blinds",
          fileStem: "Workshop-Roller-Blinds",
          templateType: "sqft_blind",
          rank: 20,
        };
      }

      if (templateType === "sqft_blind" && normalizedProduct.includes("venetian")) {
        return {
          key: "venetian_blinds",
          label: "Venetian Blinds",
          title: "Workshop Sheet - Venetian Blinds",
          fileStem: "Workshop-Venetian-Blinds",
          templateType: "sqft_blind",
          rank: 30,
        };
      }

      if (templateType === "sqft_blind" && normalizedProduct.includes("vertical")) {
        return {
          key: "vertical_blinds",
          label: "Vertical Blinds",
          title: "Workshop Sheet - Vertical Blinds",
          fileStem: "Workshop-Vertical-Blinds",
          templateType: "sqft_blind",
          rank: 40,
        };
      }

      if (templateType === "sqft_blind") {
        const blindLabel = getCurtainRowDisplay(rawProductLabel, "Blinds");
        const blindFileStem = `Workshop-${getSafeWorkshopZipNamePart(
          blindLabel,
          `Blind-${stableProductSegment}`
        )}`;
        return {
          key: `blind_${stableProductSegment}`,
          label: blindLabel,
          title: `Workshop Sheet - ${blindLabel}`,
          fileStem: blindFileStem,
          templateType: "sqft_blind",
          rank: 50,
        };
      }

      const productLabel = getCurtainRowDisplay(rawProductLabel, "Other Products");
      return {
        key: `product_${stableProductSegment}`,
        label: productLabel,
        title: `Workshop Sheet - ${productLabel}`,
        fileStem: `Workshop-${getSafeWorkshopZipNamePart(
          productLabel,
          `Product-${stableProductSegment}`
        )}`,
        templateType: "general",
        rank: 100,
      };
    };

    const buildWorkshopGroupFabricLabelByKey = (groupRows = []) => {
      const labelByKey = {};
      groupRows.forEach((row) => {
        const production = calculateCurtainRowProduction(row, {
          repeatSettings,
          quoteFullnessOverrides,
          productConfig: getRowProductConfig(row),
        });
        const key = production.fabricKey;
        if (key && !labelByKey[key] && production.fabricLabel !== WORKSHOP_EMPTY_VALUE) {
          labelByKey[key] = production.fabricLabel;
        }
      });
      return labelByKey;
    };

    const buildWorkshopCutFabricSummaryForPlan = (
      groupWorkshopPlan = {},
      groupFabricLabelByKey = {}
    ) =>
      Object.fromEntries(
        Object.entries(groupWorkshopPlan.fabricSummaryByKey || {})
          .filter(([, summary]) => Number(summary?.newFabricNeededMeters || 0) > 0)
          .map(([fabricKey, summary]) => {
            const displayName =
              summary.displayName ||
              summary.fabricLabel ||
              groupFabricLabelByKey[fabricKey] ||
              fabricKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
            if (!groupFabricLabelByKey[fabricKey]) {
              groupFabricLabelByKey[fabricKey] = displayName;
            }
            return [
              fabricKey,
              {
                ...summary,
                displayName,
                fabricLabel: summary.fabricLabel || displayName,
                newFabricMeters: Number(summary.newFabricNeededMeters || 0),
                fabricToOrderMeters: Number(summary.fabricToOrderMeters ?? summary.newFabricNeededMeters ?? 0),
                newFabricNeededMeters: Number(summary.newFabricNeededMeters || 0),
                orderedFabricMeters: Number(summary.newFabricNeededMeters || 0),
              },
            ];
          })
      );

    const appendWorkshopPhotoOnlyFabricSummaries = (
      summaryRows = [],
      groupRows = []
    ) => {
      const nextRows = (Array.isArray(summaryRows) ? summaryRows : []).map((summary) => ({
        ...(summary || {}),
      }));
      const existingKeys = new Set();
      nextRows.forEach((summary) => {
        [
          summary.fabricLibraryKey,
          summary.fabricKey,
          summary.normalizedKey,
          normalizeFabricKey(summary.fabricCode || summary.displayName || summary.key || ""),
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean)
          .forEach((key) => existingKeys.add(key));
      });

      const photoOnlyByKey = new Map();
      (Array.isArray(groupRows) ? groupRows : []).forEach((row, rowIndex) => {
        const normalizedRowFabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
        if (!normalizedRowFabricKey) return;

        const summaryMeta =
          liveActionPayload.fabricSummaryMeta?.[normalizedRowFabricKey] || {};
        const fabricDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
        const rowFabricCode =
          fabricDisplayInfo.displayFabricCode ||
          getEditableFabricValue(row) ||
          getCurtainRowFabricLabel(row);
        const fabricLibraryIdentity = getFabricLibraryIdentity({
          row,
          summary: {
            normalizedKey: normalizedRowFabricKey,
            fabricCode: rowFabricCode,
            displayName: rowFabricCode,
          },
          meta: summaryMeta,
        });
        const fabricLibraryKey = sanitizeEditableText(
          summaryMeta.fabricLibraryKey || fabricLibraryIdentity.fabricKey || ""
        );
        const identityKeys = [fabricLibraryKey, normalizedRowFabricKey]
          .map((value) => String(value || "").trim())
          .filter(Boolean);

        if (!fabricLibraryKey) {
          return;
        }

        const itemLabel =
          sanitizeEditableText(row.item || row.room || row.name || "") ||
          `Row ${rowIndex + 1}`;
        const existingPhotoRow = photoOnlyByKey.get(fabricLibraryKey);
        if (existingPhotoRow) {
          existingPhotoRow.usedInItems = Array.from(
            new Set([...(existingPhotoRow.usedInItems || []), itemLabel].filter(Boolean))
          );
          return;
        }

        if (identityKeys.some((key) => existingKeys.has(key))) {
          return;
        }

        const cachedFabricLibraryRecord = getCachedFabricLibraryRecord(fabricLibraryKey);
        const primaryPhoto =
          normalizeFabricPhotoRef(summaryMeta.primaryPhoto) ||
          normalizeFabricPhotoRef(cachedFabricLibraryRecord?.primaryPhoto);
        const photoHistory = (
          summaryMeta.photoHistory ||
          cachedFabricLibraryRecord?.photoHistory ||
          []
        )
          .map((photo) => normalizeFabricPhotoRef(photo))
          .filter(Boolean)
          .slice(0, FABRIC_PHOTO_HISTORY_LIMIT);
        const repeatSettingsForFabric = normalizeCurtainRepeatEntry(
          repeatSettings?.[normalizedRowFabricKey] || summaryMeta.repeatSettings || {}
        );
        photoOnlyByKey.set(fabricLibraryKey, {
          key: normalizedRowFabricKey,
          normalizedKey: normalizedRowFabricKey,
          fabricKey: normalizedRowFabricKey,
          fabricLibraryKey,
          fabricLibraryIdentity: {
            ...fabricLibraryIdentity,
            fabricKey: fabricLibraryKey,
          },
          brandId:
            fabricLibraryIdentity.brandId ||
            summaryMeta.brandId ||
            fabricDisplayInfo.validation?.validBrandId ||
            "",
          brandName:
            fabricLibraryIdentity.brandName ||
            summaryMeta.brandName ||
            (fabricDisplayInfo.isValidCatalogue ? fabricDisplayInfo.displayBrandName : ""),
          catalogueId: fabricLibraryIdentity.catalogueId || summaryMeta.catalogueId || "",
          catalogueName:
            fabricLibraryIdentity.catalogueName || summaryMeta.catalogueName || "",
          catalogueKey:
            fabricLibraryIdentity.catalogueKey || summaryMeta.catalogueKey || "",
          srNumber: fabricLibraryIdentity.srNumber || summaryMeta.srNumber || "",
          fabricCode: sanitizeFabricTextAgainstSavedBrandPrefix(
            rowFabricCode || fabricLibraryIdentity.fabricDisplayName || normalizedRowFabricKey,
            row
          ),
          displayName: sanitizeFabricTextAgainstSavedBrandPrefix(
            rowFabricCode || fabricLibraryIdentity.fabricDisplayName || normalizedRowFabricKey,
            row
          ),
          fabricWidthIn: normalizeFabricWidthIn(row.fabricWidthIn) || null,
          fabricWidthValues: [normalizeFabricWidthIn(row.fabricWidthIn)].filter(Boolean),
          hasMixedFabricWidths: false,
          usedInItems: [itemLabel].filter(Boolean),
          repeatSettings: repeatSettingsForFabric,
          repeatSummary: formatRepeatSettings(repeatSettingsForFabric),
          notes: sanitizeEditableText(summaryMeta.notes || ""),
          primaryPhoto,
          photoHistory,
          cutFabricMeters: 0,
          fullOrderedFabricMeters: 0,
          fabricToOrderMeters: 0,
          newFabricNeededMeters: 0,
          workshopFabricRequiredMeters: 0,
          customerBillableFabricMeters: 0,
          halfReusedMeters: 0,
          isPhotoOnlySummary: true,
        });
        identityKeys.forEach((key) => existingKeys.add(key));
      });

      return [...nextRows, ...Array.from(photoOnlyByKey.values())];
    };

    const buildWorkshopZipGroups = () => {
      const groupedRows = new Map();
      getWorkshopRowsInQuotationOrder(orderedProductionRows).forEach((row, rowIndex) => {
        const meta = getWorkshopZipGroupMeta(row, rowIndex);
        if (!groupedRows.has(meta.key)) {
          groupedRows.set(meta.key, {
            ...meta,
            firstIndex: rowIndex,
            rows: [],
          });
        }
        groupedRows.get(meta.key).rows.push(row);
      });

      return Array.from(groupedRows.values())
        .sort((left, right) => {
          const rankDiff = Number(left.rank || 0) - Number(right.rank || 0);
          if (rankDiff !== 0) return rankDiff;
          return Number(left.firstIndex || 0) - Number(right.firstIndex || 0);
        })
        .map((group) => {
          const groupRows = getWorkshopRowsInQuotationOrder(group.rows);
          const isSqftBlindGroup =
            groupRows.length > 0 &&
            groupRows.every((row) =>
              getWorkshopTemplateType(row, getRowProductConfig(row)) === "sqft_blind"
            );
          const groupCurtainRomanRows = groupRows.filter((row) => {
            const production = calculateCurtainRowProduction(row, {
              repeatSettings,
              quoteFullnessOverrides,
              productConfig: getRowProductConfig(row),
            });
            return resolveQuotePdfComponents(row, production.calc, getRowProductConfig(row)).isPanelFabric;
          });
          const emptyWorkshopPlan = {
            demands: [],
            rowPlans: [],
            fabricMetersByKey: {},
            fabricSummaryByKey: {},
            leftoversByKey: {},
            pool: {},
            reuseLog: [],
            skippedReasons: [],
            audit: {},
          };
          const groupFabricSummaryResult = isSqftBlindGroup
            ? { fabricSummaries: {} }
            : buildCurtainFabricSummary(groupRows, {
                activeRepeatSettings: repeatSettings,
                halfPieceReuseEnabled: useHalfPieceReuse,
                passSavingsToCustomer: passHalfPieceSavingsToCustomer,
                debugTag: `workshop-zip-${group.key}`,
                quoteFullnessOverridesForCalculation: quoteFullnessOverrides,
              });
          const groupNoReuseBillingResult = isSqftBlindGroup
            ? { fabricSummaries: {} }
            : buildCurtainFabricSummary(groupRows, {
                activeRepeatSettings: repeatSettings,
                halfPieceReuseEnabled: false,
                passSavingsToCustomer: passHalfPieceSavingsToCustomer,
                debugTag: `workshop-zip-${group.key}-full-order`,
                quoteFullnessOverridesForCalculation: quoteFullnessOverrides,
              });
          const groupFabricOrderSummaryResult = isSqftBlindGroup
            ? { fabricSummaries: {} }
            : buildCurtainFabricSummary(groupRows, {
                activeRepeatSettings: repeatSettings,
                halfPieceReuseEnabled: useHalfPieceReuse,
                passSavingsToCustomer: passHalfPieceSavingsToCustomer,
                debugTag: `workshop-zip-${group.key}-fabric-to-order`,
                useProductWorkshopRules: true,
                quoteFullnessOverridesForCalculation: quoteFullnessOverrides,
              });
          const groupWorkshopPlan = isSqftBlindGroup
            ? emptyWorkshopPlan
            : buildReuseAwareDemands(
                groupCurtainRomanRows,
                repeatSettings,
                {
                  enableReuse: useHalfPieceReuse,
                  debugTag: `workshop-zip-${group.key}-plan`,
                  getProductRules: getRowProductRules,
                  quoteFullnessOverridesForCalculation: quoteFullnessOverrides,
                  getRowCalcForPlan: (row, activeRepeat, options) => {
                    const calc = calcRow(row, activeRepeat, options);
                    const config = getRowProductConfig(row);
                    if (config?.calculationType !== "sqft_plus_fabric") return calc;
                    return { ...calc, isRoman: config.fabricRules?.meterFormulaType === "roman_panels",
                      isCurtain: config.fabricRules?.meterFormulaType === "curtain_panels" };
                  },
                }
              );
          const groupFabricSummaryRows = isSqftBlindGroup
            ? []
            : appendWorkshopPhotoOnlyFabricSummaries(
                buildCurtainCanonicalFabricSummaryRowsForAction({
                  sourceRows: groupRows,
                  fabricSummarySource: groupFabricSummaryResult.fabricSummaries || {},
                  fabricOrderSummarySource:
                    groupFabricOrderSummaryResult.fabricSummaries || {},
                  liveFabricSummaryMeta: liveActionPayload.fabricSummaryMeta || {},
                  liveGetRowCalc: (row, rowIndex) =>
                    getCurtainProductionAdjustedRowCalcFromSummary(
                      row,
                      rowIndex,
                      groupFabricSummaryResult,
                      groupNoReuseBillingResult
                    ),
                }),
                groupRows
              );
          const pdfFabricSummaryRows = addDirectFabricPdfSummaries(groupFabricSummaryRows,
            groupRows.map((row) => ({ row, calc: getCurtainProductionAdjustedRowCalc(row, Number(row.sourceIndex ?? 0)),
              config: getRowProductConfig(row), fabricKey: getCurtainRowFabricKey(row), fabricLabel: getCurtainRowFabricLabel(row) })));
          const groupFabricLabelByKey = buildWorkshopGroupFabricLabelByKey(groupRows);
          const groupCutFabricSummary = buildWorkshopCutFabricSummaryForPlan(
            groupWorkshopPlan,
            groupFabricLabelByKey
          );
          const safeCustomer = getSafeWorkshopZipNamePart(customerName, "Customer");
          const safeQuote = getSafeWorkshopZipNamePart(quoteNo, "Quote");
          return {
            ...group,
            rows: groupRows,
            templateType: isSqftBlindGroup ? "sqft_blind" : group.templateType,
            curtainRomanRows: groupCurtainRomanRows,
            workshopPlan: groupWorkshopPlan,
            cutFabricSummary: groupCutFabricSummary,
            fabricLabelByKey: groupFabricLabelByKey,
            fabricSummaryRows: pdfFabricSummaryRows,
            fileName: `${group.fileStem}-${safeCustomer}-${safeQuote}.pdf`,
          };
        })
        .filter((group) => group.rows.length > 0);
    };

    const workshopZipGroups = buildWorkshopZipGroups();
    if (IS_DEV_UI_ENABLED) {
      devLog("WORKSHOP_ZIP_GROUPING_TRACE", {
        quoteNo,
        totalRows: orderedProductionRows.length,
        includedRows: includedWorkshopRows.length,
        accessoryRows: accessoryRows.length,
        groupCount: workshopZipGroups.length,
        groups: workshopZipGroups.map((group) => ({
          groupKey: group.key,
          templateType: group.templateType,
          groupLabel: group.label,
          label: group.label,
          fileName: group.fileName,
          rowCount: group.rows.length,
          rowIds: group.rows.map((row) => getWorkshopRowStableId(row)),
          productNames: Array.from(
            new Set(
              group.rows.map((row) =>
                getCurtainRowDisplay(row.productName || row.productType || row.product, "")
              )
            )
          ).filter(Boolean),
          fabricKeys: group.rows
            .map((row) => getCurtainRowFabricKey(row))
            .filter(Boolean),
          fabricSummaryCount: group.fabricSummaryRows.length,
          fabricSummaryKeys: group.fabricSummaryRows.map(
            (summary) => summary.fabricLibraryKey || summary.normalizedKey || summary.fabricKey
          ),
        })),
      });
    }

    for (const workshopZipGroup of workshopZipGroups) {
      const orderedProductionRows = workshopZipGroup.rows;
      const curtainRomanRows = workshopZipGroup.curtainRomanRows;
      const workshopPlan = workshopZipGroup.workshopPlan;
      const getWorkshopFabricSummaryRowsForPdf = () =>
        Array.isArray(workshopZipGroup.fabricSummaryRows) &&
        workshopZipGroup.fabricSummaryRows.length > 0
          ? workshopZipGroup.fabricSummaryRows
          : [];

      if (IS_DEV_UI_ENABLED) {
        devLog("WORKSHOP_PDF_RENDER_TRACE", {
          quoteNo,
          pdfName: workshopZipGroup.fileName,
          groupKey: workshopZipGroup.key,
          templateType: workshopZipGroup.templateType,
          groupLabel: workshopZipGroup.label,
          label: workshopZipGroup.label,
          fileName: workshopZipGroup.fileName,
          rowCount: orderedProductionRows.length,
          curtainRomanRowCount: curtainRomanRows.length,
          fabricSummaryGroupCount: getWorkshopFabricSummaryRowsForPdf().length,
          fabricSummaryCount: getWorkshopFabricSummaryRowsForPdf().length,
          rowIds: orderedProductionRows.map((row) => getWorkshopRowStableId(row)),
          productNames: Array.from(
            new Set(
              orderedProductionRows.map((row) =>
                getCurtainRowDisplay(row.productName || row.productType || row.product, "")
              )
            )
          ).filter(Boolean),
          fabricKeysIncluded: orderedProductionRows
            .map((row) => getCurtainRowFabricKey(row))
            .filter(Boolean),
          fabricKeys: orderedProductionRows
            .map((row) => getCurtainRowFabricKey(row))
            .filter(Boolean),
          summaryKeys: getWorkshopFabricSummaryRowsForPdf().map(
            (summary) => summary.fabricLibraryKey || summary.normalizedKey || summary.fabricKey
          ),
        });
      }
    const workshopAllocationByRowId = {};
    const workshopAllocationsByFabricKey = {};
    curtainRomanRows.forEach((row, rowIdx) => {
      const rowId = getWorkshopRowStableId(row) || `workshop-row-${rowIdx}`;
      const rowPlan = workshopPlan.rowPlans?.[rowIdx] || null;
      const fallbackCalc = calcRow(row, repeatSettings);
      const fabricKey = getCurtainRowFabricKey(row);
      const fabricDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
      const fabricCode = [
        fabricDisplayInfo.isValidCatalogue ? fabricDisplayInfo.displayBrandName : "",
        fabricDisplayInfo.displayFabricCode || getCurtainRowFabricLabel(row),
      ]
        .filter(Boolean)
        .join(" / ");
      logPdfFabricSourceTrace("workshop", row, fabricDisplayInfo, fabricCode);
      const cutHeightIn = Number(
        rowPlan?.cutHeightIn || fallbackCalc.cutCurtain || fallbackCalc.cutRoman || row.height || 0
      );
      const cutHeightMeters = cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0;
      const newFullCuts = Number(rowPlan?.newFullCuts ?? fallbackCalc.pcsFull ?? fallbackCalc.pcs ?? 0);
      const expectedNewFabricFromCuts = round2(newFullCuts * cutHeightMeters);
      const rowNewFabricMeters = round2(
        Number.isFinite(Number(rowPlan?.newMeters))
          ? Number(rowPlan?.newMeters || 0)
          : expectedNewFabricFromCuts
      );
      const rowWorkshopRequiredMeters = round2(
        Number.isFinite(Number(rowPlan?.newFabricNeededMeters))
          ? Number(rowPlan?.newFabricNeededMeters || 0)
          : rowNewFabricMeters
      );
      const fabricColorKey = fabricDisplayInfo.isValidCatalogue
        ? getWorkshopFabricColorKey(row, fabricCode, { includeCatalogueIdentity: true })
        : normalizeFabricKey(fabricCode || fabricKey);
      const sourceRowIndex = Number.isInteger(Number(row.sourceIndex))
        ? Number(row.sourceIndex)
        : rowIdx;
      const customerCalc = getCurtainProductionAdjustedRowCalc(row, sourceRowIndex);
      const rowFullOrderedMeters = round2(
        Number(
          customerCalc.fullOrderedFabricMeters ??
            customerCalc.noReuseOrderedFabricMeters ??
            customerCalc.orderedFabricMeters ??
            customerCalc.fabricMeters ??
            0
        )
      );
      const rowCustomerBillableMeters = round2(
        Number(customerCalc.customerBillableFabricMeters ?? rowFullOrderedMeters)
      );
      const reusedHalfCount = Number(rowPlan?.reusedHalfCount || 0);
      const savedHalfCount = rowPlan?.createdLeftoverId ? 1 : 0;
      const fabricSource = rowPlan?.reusedLeftoverId
        ? "Reused Half"
        : rowPlan?.createdLeftoverId
        ? "New Cut + Half Leftover Saved"
        : rowPlan
        ? "New Cut"
        : "Calculated Fallback";
      const allocation = {
        rowId,
        rowIndex: rowIdx,
        rowPlan,
        fabricKey,
        fabricCode,
        brandName: fabricDisplayInfo.isValidCatalogue
          ? fabricDisplayInfo.displayBrandName
          : "",
        panels: Number(rowPlan?.totalPieces ?? rowPlan?.reuseTotalPieces ?? fallbackCalc.totalPieces ?? 0),
        rawPanels: Number(rowPlan?.rawPanels ?? fallbackCalc.rawPanels ?? 0),
        halfStepProductionPanels: Number(rowPlan?.halfStepPanels ?? fallbackCalc.pcsHalf ?? 0),
        wholeBillingPanels: Number(rowPlan?.wholePanels ?? customerCalc.wholeBillingPanels ?? fallbackCalc.pcsFull ?? 0),
        cutHeightIn,
        cutHeightMeters,
        newFullCuts,
        reusedHalfCount,
        savedHalfCount,
        rowNewFabricMeters,
        rowWorkshopRequiredMeters,
        rowCustomerBillableMeters,
        rowFullOrderedMeters,
        rowCutFabricMeters: round2(Number(rowPlan?.cutFabricMeters ?? fallbackCalc.cutFabricMeters ?? 0)),
        rowHalfReusedMeters: round2(Number(rowPlan?.halfReusedMeters || 0)),
        panelMode: rowPlan?.panelMode || fallbackCalc.panelMode || "",
        panelCountPerBlind: Number(rowPlan?.panelCountPerBlind || fallbackCalc.panelCountPerBlind || 0),
        totalPanels: Number(rowPlan?.totalPanels || rowPlan?.totalPieces || fallbackCalc.totalPieces || 0),
        panelWidthsIn: rowPlan?.panelWidthsIn || fallbackCalc.panelWidthsIn || [],
        quantity: Number(rowPlan?.quantity || fallbackCalc.romanPanelCalc?.quantity || row.qty || row.quantity || 1),
        jointDescription: rowPlan?.jointDescription || fallbackCalc.romanPanelCalc?.jointDescription || "",
        halfPieceReuseAllowed: rowPlan?.halfPieceReuseAllowed === true,
        fabricSource,
        fabricColorKey,
        reusedFromRowId: rowPlan?.reusedFromRowId || "",
        reusedIntoRowId: rowPlan?.reusedLeftoverId ? rowId : "",
        expectedNewFabricFromCuts,
        sourceUsed: rowPlan ? "workshopPlan.rowPlans[rowIndex].newMeters" : "newFullCuts * cutHeightMeters fallback",
      };
      workshopAllocationByRowId[rowId] = allocation;
      if (!workshopAllocationsByFabricKey[fabricKey]) workshopAllocationsByFabricKey[fabricKey] = [];
      workshopAllocationsByFabricKey[fabricKey].push(allocation);
    });

    if (IS_DEV_UI_ENABLED) {
      getWorkshopFabricSummaryRowsForPdf().forEach((summary) => {
        const fabricKey = summary.normalizedKey || summary.fabricKey || normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        const rowsForGroup = workshopAllocationsByFabricKey[fabricKey] || [];
        const sumRowNewFabricMeters = round2(
          rowsForGroup.reduce((sum, row) => sum + Number(row.rowNewFabricMeters || 0), 0)
        );
        const groupNewNeeded = round2(
          Number(summary.newFabricNeededMeters || summary.workshopFabricRequiredMeters || 0)
        );
        const difference = round2(sumRowNewFabricMeters - groupNewNeeded);
        if (Math.abs(difference) > 0.05) {
          devError("WORKSHOP_PDF_GROUP_ROW_SUM_MISMATCH", {
            quoteNo,
            fabricKey,
            fabricCode: summary.fabricCode || summary.displayName || "",
            sumRowNewFabricMeters,
            groupNewNeeded,
            difference,
            rows: rowsForGroup,
          });
        }
      });
    }

    if ((orderedProductionRows || []).length) {
      const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

      const { marginX, pageW, headerBottomY } = addWorkshopHeader(
        doc,
        logoMeta,
        shopAddress
      );

      const titleY = headerBottomY + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(workshopZipGroup.title || "Workshop Sheet", pageW / 2, titleY, {
        align: "center",
      });

      doc.setLineWidth(0.3);
      doc.line(marginX, titleY + 5, pageW - marginX, titleY + 5);

      let y = titleY + 13;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const customerPhone =
        quoteCustomerLink?.phone ||
        quoteCustomerLink?.whatsapp ||
        selectedQuote?.customerPhone ||
        selectedQuote?.customerWhatsapp ||
        selectedQuote?.crm?.customerPhone ||
        "";
      const installDate =
        toDateInputValue(selectedQuote?.installationDate || selectedQuote?.crm?.installationDate) ||
        "";
      const workshopSiteSnapshot = normalizeQuoteSiteSnapshot(
        liveActionPayload?.quoteSiteSelection || selectedQuote || quoteSiteSelection,
        { customerName }
      );
      const workshopSiteText = getQuoteSiteDisplayText(workshopSiteSnapshot);
      const headerParts = [
        `Customer: ${customerName || WORKSHOP_EMPTY_VALUE}`,
        customerPhone ? `Phone: ${customerPhone}` : "",
        workshopSiteText ? `Site: ${workshopSiteText}` : "",
        `Quote: ${quoteNo || WORKSHOP_EMPTY_VALUE}`,
        `Date: ${workshopQuoteDate || WORKSHOP_EMPTY_VALUE}`,
        installDate ? `Install: ${installDate}` : "",
      ].filter(Boolean);
      const headerLines = doc.splitTextToSize(headerParts.join("   |   "), pageW - marginX * 2);
      doc.text(headerLines, marginX, y);
      y += headerLines.length * 4.5 + 4;

      const isSqftBlindWorkshopGroup =
        workshopZipGroup.templateType === "sqft_blind" ||
        (
          orderedProductionRows.length > 0 &&
          orderedProductionRows.every((row) =>
            getWorkshopTemplateType(row, getRowProductConfig(row)) === "sqft_blind"
          )
        );

      if (isSqftBlindWorkshopGroup) {
        const getBlindWorkshopSqft = (row = {}) => {
          const productConfig = getRowProductConfig(row);
          const liveCalc = calcRow(row, repeatSettings);
          const liveBillableSqft = Number(
            liveCalc?.billableSqft ??
              liveCalc?.adjSqft ??
              liveCalc?.sqftCalculation?.billableSqft ??
              0
          );
          if (liveBillableSqft > 0) {
            return {
              rawSqft: Number(liveCalc?.rawSqft ?? liveCalc?.sqftCalculation?.rawSqft ?? 0),
              billableSqft: round2(liveBillableSqft),
              minimumSqft: Number(liveCalc?.sqftCalculation?.minimumSqft || 0),
              source: "liveCalc",
            };
          }

          const snapshot =
            row.calculationSnapshot ||
            row.productionSnapshot ||
            row.calculation ||
            {};
          const snapshotBillableSqft = Number(
            snapshot?.sqftCalculation?.billableSqft ??
              snapshot?.billableSqft ??
              row?.sqftCalculation?.billableSqft ??
              row?.billableSqft ??
              row?.adjSqft ??
              0
          );
          if (snapshotBillableSqft > 0) {
            return {
              rawSqft: Number(snapshot?.sqftCalculation?.rawSqft ?? snapshot?.rawSqft ?? 0),
              billableSqft: round2(snapshotBillableSqft),
              minimumSqft: Number(snapshot?.sqftCalculation?.minimumSqft || 0),
              source: "snapshot",
            };
          }

          const roundingMode = normalizeBlindSqftRoundingMode(
            row?.sqftCalculation?.roundingMode ||
              snapshot?.sqftCalculation?.roundingMode ||
              fabricCalcSettings?.blindSqftRoundingMode,
            "exact"
          );
          const canonical = calculateBlindSqft({
            widthIn: num(row.width),
            heightIn: num(row.height),
            roundingMode,
          });
          const minimumSqft = resolveProductMinSqft(row, productConfig, {
            ...fabricCalcSettings,
            minSqft,
          });
          return {
            rawSqft: Number(canonical.rawSqft || 0),
            billableSqft: round2(Math.max(Number(canonical.billableSqft || 0), minimumSqft)),
            minimumSqft,
            source: "canonical",
          };
        };

        const formatBlindDimension = (value) => {
          const numeric = Number(value);
          return Number.isFinite(numeric) && numeric > 0
            ? numeric.toFixed(2)
            : WORKSHOP_EMPTY_VALUE;
        };

        const blindWorkshopRows = orderedProductionRows.map((row, index) => {
          const calc = calcRow(row, repeatSettings);
          const productConfig = getRowProductConfig(row);
          const sqft = getBlindWorkshopSqft(row);
          const qty = Math.max(1, Number(row.qty ?? row.quantity ?? calc.quantity ?? 1) || 1);
          const fabricDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
          const materialCode =
            fabricDisplayInfo.displayFabricCode ||
            getEditableFabricValue(row) ||
            getCurtainRowFabricLabel(row) ||
            WORKSHOP_EMPTY_VALUE;
          const notes = [
            sanitizeEditableText(row.notes || row.remarks || row.remark || row.note || ""),
            sanitizeEditableText(row.controlSide || row.chainSide || row.operationSide || "")
              ? `Control: ${sanitizeEditableText(row.controlSide || row.chainSide || row.operationSide || "")}`
              : "",
            sanitizeEditableText(row.mechanism || row.bracket || row.bracketType || row.chainType || "")
              ? `Mechanism: ${sanitizeEditableText(row.mechanism || row.bracket || row.bracketType || row.chainType || "")}`
              : "",
            sanitizeEditableText(row.mountType || row.mount || row.fixingType || "")
              ? `Mount: ${sanitizeEditableText(row.mountType || row.mount || row.fixingType || "")}`
              : "",
          ]
            .filter(Boolean)
            .join("\n");

          if (IS_DEV_UI_ENABLED) {
            devLog("BLIND_WORKSHOP_SQFT_TRACE", {
              quoteNo,
              rowId: getWorkshopRowStableId(row),
              item: row.item || row.name || "",
              product:
                productConfig?.name || row.productName || row.productType || row.product || "",
              widthIn: Number(row.width || 0),
              heightIn: Number(row.height || 0),
              rawSqft: sqft.rawSqft,
              minimumSqft: sqft.minimumSqft,
              billableSqft: sqft.billableSqft,
              source: sqft.source,
            });
          }

          return {
            body: [
              getWorkshopRowItemWithLocation(row, `B${index + 1}`),
              getCurtainRowDisplay(
                productConfig?.name || row.productName || row.productType || row.product,
                "Blind"
              ),
              materialCode,
              formatBlindDimension(row.width),
              formatBlindDimension(row.height),
              sqft.billableSqft > 0 ? sqft.billableSqft.toFixed(2) : WORKSHOP_EMPTY_VALUE,
              String(qty),
              notes || WORKSHOP_EMPTY_VALUE,
            ],
            billableSqft: sqft.billableSqft,
            qty,
          };
        });

        assertNoPricingInWorkshopPdfText(
          {
            head: [
              "Item",
              "Product",
              "Material / Code",
              "Width (In)",
              "Height (In)",
              "Billable Sqft",
              "Qty",
              "Notes",
            ],
            body: blindWorkshopRows.map((row) => row.body),
          },
          { label: "sqftBlindWorkshopPdfText" }
        );

        autoTable(doc, {
          startY: y,
          margin: { left: marginX, right: marginX },
          head: [[
            "Item",
            "Product",
            "Material / Code",
            "Width (In)",
            "Height (In)",
            "Billable Sqft",
            "Qty",
            "Notes",
          ]],
          body: blindWorkshopRows.map((row) => row.body),
          rowPageBreak: "avoid",
          styles: {
            fontSize: 8.8,
            cellPadding: 2.5,
            valign: "top",
            overflow: "linebreak",
            textColor: [15, 23, 42],
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
          },
          headStyles: { fillColor: [15, 23, 42], textColor: 255 },
          alternateRowStyles: { fillColor: [248, 250, 255] },
          columnStyles: {
            0: { cellWidth: 26 },
            1: { cellWidth: 28 },
            2: { cellWidth: 34 },
            3: { cellWidth: 18, halign: "right" },
            4: { cellWidth: 18, halign: "right" },
            5: { cellWidth: 22, halign: "right" },
            6: { cellWidth: 12, halign: "right" },
            7: { cellWidth: 24 },
          },
        });

        const totalBillableSqft = round2(
          blindWorkshopRows.reduce(
            (sum, row) => sum + Number(row.billableSqft || 0) * Number(row.qty || 1),
            0
          )
        );
        const summaryY = Number(doc.lastAutoTable?.finalY || y + 10) + 8;
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, summaryY, 72, 16, 3, 3, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Sqft Covered", marginX + 4, summaryY + 6);
        doc.setFont("helvetica", "normal");
        doc.text(`${totalBillableSqft.toFixed(2)} sqft`, marginX + 4, summaryY + 12);

        addWorkshopFooter(doc);
        zip.file(workshopZipGroup.fileName, doc.output("arraybuffer"));
        continue;
      }

      const tableRows = [];
      const workshopFabricSummaryRows = getWorkshopFabricSummaryRowsForPdf();
      const fabricColorMap = {};
      const workshopFabricColorKeys = [];
      const workshopFabricColorKeyByNormalizedKey = {};
      const registerWorkshopFabricColorKey = (rawKey = "") => {
        const key = normalizeFabricKey(rawKey);
        if (!key || workshopFabricColorKeys.includes(key)) return key;
        workshopFabricColorKeys.push(key);
        fabricColorMap[key] = getWorkshopPdfFabricTintByIndex(
          workshopFabricColorKeys.length - 1
        );
        return key;
      };
      orderedProductionRows.forEach((row) => {
        const normalizedFabricKey = getCurtainRowFabricKey(row);
        const fabricDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings(row);
        const fabricLabel = getCurtainFabricDisplayWithBrand(row);
        const colorKey = registerWorkshopFabricColorKey(
          fabricDisplayInfo.isValidCatalogue
            ? getWorkshopFabricColorKey(row, fabricLabel, {
                includeCatalogueIdentity: true,
              })
            : normalizeFabricKey(fabricLabel || normalizedFabricKey)
        );
        if (normalizedFabricKey && colorKey && !workshopFabricColorKeyByNormalizedKey[normalizedFabricKey]) {
          workshopFabricColorKeyByNormalizedKey[normalizedFabricKey] = colorKey;
        }
      });
      const getWorkshopSummaryFabricColorKey = (summary = {}) => {
        const summaryNormalizedKey =
          summary.normalizedKey ||
          summary.fabricKey ||
          normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        if (summaryNormalizedKey && workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey]) {
          return workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey];
        }
        return getWorkshopFabricColorKey(
          summary,
          summary.fabricCode || summary.displayName || summaryNormalizedKey || ""
        );
      };
      workshopFabricSummaryRows.forEach((summary) => {
        const summaryNormalizedKey =
          summary.normalizedKey ||
          summary.fabricKey ||
          normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        const colorKey = registerWorkshopFabricColorKey(
          getWorkshopSummaryFabricColorKey(summary)
        );
        if (summaryNormalizedKey && colorKey && !workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey]) {
          workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey] = colorKey;
        }
      });
      const getWorkshopFabricTint = (
        fabricKey = "",
        fallbackLabel = "",
        explicitColorKey = ""
      ) => {
        const normalizedFabricKey = normalizeFabricKey(fabricKey);
        const colorKey =
          normalizeFabricKey(explicitColorKey) ||
          workshopFabricColorKeyByNormalizedKey[normalizedFabricKey] ||
          normalizedFabricKey ||
          normalizeFabricKey(fallbackLabel) ||
          "__workshop_fabric__";
        const registeredKey = registerWorkshopFabricColorKey(colorKey);
        return fabricColorMap[registeredKey] || getWorkshopPdfFabricTintByIndex(0);
      };
      const rowVisuals = [];
      const workshopHalfPieceAudit = workshopPlan.audit || {};
      const availableHalfPieces = Number(
        workshopHalfPieceAudit.totalHalfPiecesAvailable ??
          workshopPlan.rowPlans
            .filter(Boolean)
            .reduce((sum, plan) => sum + (plan?.createdLeftoverId ? 1 : 0), 0)
      );
      const reusedHalfPieces = Number(
        workshopHalfPieceAudit.totalHalfPiecesReused ??
          workshopPlan.rowPlans
            .filter(Boolean)
            .reduce((sum, plan) => sum + Number(plan?.reusedHalfCount || 0), 0)
      );
      const halfPieceMetersSaved = round2(
        Number(workshopHalfPieceAudit.totalMetersSaved || 0)
      );
      const halfPieceReuseResult =
        reusedHalfPieces > 0
          ? "Reuse applied"
          : workshopHalfPieceAudit.reasons?.[0] || "No compatible half pieces found";

      const getWorkshopRowVisual = (rowPlan) => {
        const linkedLeftoverId =
          rowPlan?.createdLeftoverId || rowPlan?.reusedLeftoverId || "";

        if (rowPlan?.reusedLeftoverId) {
          return {
            label: "Reused Half",
            linkedLeftoverId,
          };
        }

        if (rowPlan?.createdLeftoverId) {
          return {
            label: "New Cut + Half Leftover Saved",
            linkedLeftoverId,
          };
        }

        return {
          label: "New Cut",
          linkedLeftoverId: "",
        };
      };

      const summaryBoxW = 88;
      const summaryBoxX = pageW - marginX - summaryBoxW;
      const summaryBoxY = y - 4;
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(summaryBoxX, summaryBoxY, summaryBoxW, 36, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Half-Piece Tracking", summaryBoxX + 4, summaryBoxY + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.6);
      doc.text(
        `Half-piece reuse: ${useHalfPieceReuse ? "Enabled" : "Disabled"}`,
        summaryBoxX + 4,
        summaryBoxY + 10
      );
      doc.text(
        `Half Pieces Available: ${availableHalfPieces}`,
        summaryBoxX + 4,
        summaryBoxY + 15
      );
      doc.text(
        `Half Pieces Reused: ${reusedHalfPieces}`,
        summaryBoxX + 4,
        summaryBoxY + 20
      );
      doc.text(
        `Fabric Saved: ${halfPieceMetersSaved.toFixed(2)} m`,
        summaryBoxX + 4,
        summaryBoxY + 25
      );
      const resultLines = doc.splitTextToSize(`Reuse Result: ${halfPieceReuseResult}`, summaryBoxW - 8);
      doc.text(resultLines.slice(0, 2), summaryBoxX + 4, summaryBoxY + 30);
      y = Math.max(y + 2, summaryBoxY + 40);

      if (IS_DEV_UI_ENABLED) {
        devLog(
          "WORKSHOP STITCH DEBUG",
          buildStitchTrace(curtainRomanRows, "curtain_roman_rows")
        );
      }

      const workshopLiningSummaryRows = [];

      orderedProductionRows.forEach((r, rowIdx) => {
        const rowId = getWorkshopRowStableId(r) || `workshop-row-${rowIdx}`;
        const c = calcRow(r, repeatSettings);
        const allocationForRow = workshopAllocationByRowId[rowId] || null;
        const rowPlan = allocationForRow?.rowPlan || null;
        const pdfComponent = resolveQuotePdfComponents(r, getCurtainProductionAdjustedRowCalc(r, Number(r.sourceIndex ?? rowIdx)), getRowProductConfig(r));
        const isFabricWorkshopRow = pdfComponent.usesSeparateFabricCost;
        const effectiveRowPlan = rowPlan || {
          totalPieces: c.totalPieces || c.pcsHalf || c.pcs || 1,
          cutHeightIn: c.cutCurtain || c.cutRoman || Number(r.height || 0),
          newMeters: c.fabricMeters || c.fabricMetersFull || 0,
          newFullCuts: c.pcsFull || c.pcs || 0,
          reusedHalfCount: 0,
          createdLeftoverId: "",
          reusedLeftoverId: "",
        };

        const fabricLabel = getCurtainFabricDisplayWithBrand(r);
        const fabricKey = getCurtainRowFabricKey(r);
        const repeat = c.repeatSettings || {};

        const repeatText = formatRepeatSettings(repeat);
        const hasRowRepeat = repeatText !== WORKSHOP_EMPTY_VALUE;
        const repeatLabel = hasRowRepeat
          ? [`Repeat Setting: ${repeatText}`, patternCuttingInstructions(c),
              repeat.verticalEnabled && Number(repeat.verticalRepeatInch) > 0
                ? "Multi-roll reserve: use the optimizer cutting PDF. Workshop motif alignment is required; reserve is not guaranteed waste." : ""].filter(Boolean).join("\n")
          : "";

        const item = getWorkshopRowItemWithLocation(r, `W${rowIdx + 1}`);
        const product = getCurtainRowDisplay(r.productType || r.product, "Curtains");
        const fabric = fabricLabel;
        const widthIn = num(r.width);
        const heightIn = num(r.height);
        const fabricW = num(r.fabricWidthIn) || 54;
        const totalPieces = Number(effectiveRowPlan.totalPieces || 0);
        const cutHeightIn = Number(effectiveRowPlan.cutHeightIn || 0);
        const allocation =
          allocationForRow || {
            rowId,
            rowPlan,
            fabricKey,
            fabricCode: fabric,
            panels: totalPieces,
            rawPanels: 0,
            halfStepProductionPanels: totalPieces,
            wholeBillingPanels: Number(effectiveRowPlan.newFullCuts || 0),
            cutHeightIn,
            cutHeightMeters: cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0,
            newFullCuts: Number(effectiveRowPlan.newFullCuts || 0),
            reusedHalfCount: Number(effectiveRowPlan.reusedHalfCount || 0),
            savedHalfCount: effectiveRowPlan.createdLeftoverId ? 1 : 0,
            rowNewFabricMeters: round2(
              Number(effectiveRowPlan.newMeters || 0) ||
                Number(effectiveRowPlan.newFullCuts || 0) * (cutHeightIn / 39.3701)
            ),
            rowWorkshopRequiredMeters: round2(Number(effectiveRowPlan.newMeters || 0)),
            rowCustomerBillableMeters: 0,
            rowFullOrderedMeters: 0,
            rowCutFabricMeters: 0,
            rowHalfReusedMeters: 0,
            fabricSource: "Calculated Fallback",
            fabricColorKey: getWorkshopFabricColorKey(r, fabric),
            reusedFromRowId: "",
            reusedIntoRowId: "",
            expectedNewFabricFromCuts: round2(
              Number(effectiveRowPlan.newFullCuts || 0) * (cutHeightIn / 39.3701)
            ),
            sourceUsed: "effectiveRowPlan fallback",
          };
        const newMeters = pdfComponent.isDirectFabric || pdfComponent.usesSavedSnapshot
          ? pdfComponent.workshopFabricMeters : round2(Number(allocation.rowNewFabricMeters || 0));
        const rowVisual = rowPlan
          ? getWorkshopRowVisual(rowPlan)
          : {
              fillColor: [255, 255, 255],
              label: "Measurement Row",
              linkedLeftoverId: "",
            };
        const displayProductConfig = getRowProductConfig(r);
        const stitchConfigMatch = findCurtainRowStitchConfig(r, stitchingRates);
        const stitchCfg = stitchConfigMatch?.config || {};
        const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
          r,
          stitchingRates,
          displayProductConfig
        );
        const trackTypeLabel = getCurtainRowDisplay(stitchCfg.trackType, "");
        const workshopStitchLabel = resolvedStitchLabel;
        const trackParts = [
          trackTypeLabel,
          Number(stitchCfg.trackLengthFt || 0) > 0
            ? `${Number(stitchCfg.trackLengthFt)} ft`
            : "",
        ].filter(Boolean);
        const notesLabel = getCurtainRowDisplay(
          r.remarks || r.remark || r.notes || r.note,
          ""
        );
        const sourceRowIndex = Number.isInteger(Number(r.sourceIndex))
          ? Number(r.sourceIndex)
          : rowIdx;
        const adjustedCustomerCalc = getCurtainProductionAdjustedRowCalc(
          r,
          sourceRowIndex
        );
        if (IS_DEV_UI_ENABLED) {
          const fabricSummaryForRow =
            getWorkshopFabricSummaryRowsForPdf().find((summary) => {
              const summaryKey =
                summary.normalizedKey ||
                summary.fabricKey ||
                normalizeFabricKey(summary.fabricCode || summary.displayName || "");
              return summaryKey === fabricKey;
            }) || {};
          const rowTrace = {
            quoteNo,
            rowId,
            itemName: item,
            product,
            fabricCode: fabric,
            fabricKey,
            panels: allocation.panels,
            newFullCuts: allocation.newFullCuts,
            reusedHalfCount: allocation.reusedHalfCount,
            cutHeightIn: allocation.cutHeightIn,
            expectedNewFabricFromCuts: allocation.expectedNewFabricFromCuts,
            rowWorkshopNewFabricMeters: allocation.rowNewFabricMeters,
            rowWorkshopRequiredMeters: allocation.rowWorkshopRequiredMeters,
            rowCustomerBillableMeters: allocation.rowCustomerBillableMeters,
            groupNewFabricNeededMeters: Number(fabricSummaryForRow.newFabricNeededMeters || 0),
            groupWorkshopRequiredMeters: Number(fabricSummaryForRow.workshopFabricRequiredMeters || 0),
            printedNewFabricMeters: newMeters,
            sourceUsed: allocation.sourceUsed,
          };
          devLog("WORKSHOP_PDF_ROW_METER_SOURCE_TRACE", rowTrace);
          if (Math.abs(newMeters - Number(allocation.expectedNewFabricFromCuts || 0)) > 0.05) {
            devError("WORKSHOP_PDF_ROW_NEW_FABRIC_MISMATCH", rowTrace);
          }
        }
        const lining = normalizeLiningForSave(
          {
            ...r,
            lining:
              adjustedCustomerCalc.lining ||
              calculateCurtainRowLining(
                r,
                Number(
                  adjustedCustomerCalc.customerBillableFabricMeters ??
                    adjustedCustomerCalc.orderedFabricMeters ??
                    adjustedCustomerCalc.fabricMeters ??
                    0
                ),
                curtainRowSupportsLining(r, adjustedCustomerCalc),
                {
                  sqft:
                    adjustedCustomerCalc.adjSqft ??
                    adjustedCustomerCalc.billableSqft ??
                    0,
                }
              ),
          },
          getRowProductConfig(r),
          adjustedCustomerCalc
        );
        const liningDisplay =
          lining?.enabled
            ? (() => {
                const liningBrandName =
                  sanitizeEditableText(lining.brandName || "") ||
                  getFabricBrandNameById(lining.brandId);
                const liningFabric = [
                  liningBrandName,
                  sanitizeEditableText(lining.fabricCode || ""),
                ]
                  .filter(Boolean)
                  .join(" / ");
                const isSqftLining = lining.chargeMethod === "sqft";
                const liningQuantity = isSqftLining
                  ? `${Number(lining.sqft || 0).toFixed(2)} sqft`
                  : `${Number(lining.billableMeters || 0).toFixed(2)} m`;
                const liningBasis = isSqftLining
                  ? "Sqft"
                  : lining.metersMode === "custom_override"
                  ? "Custom meters"
                  : "Meter";
                const liningLabel = getCurtainLiningLabel(lining);
                return {
                  cellText: [
                    "LINING",
                    liningLabel,
                    liningFabric ? `Fabric: ${liningFabric}` : "",
                    liningQuantity,
                    `Basis: ${liningBasis}`,
                  ]
                    .filter(Boolean)
                    .join("\n"),
                  summaryRow: [
                    item,
                    fabric,
                    liningLabel,
                    liningFabric || WORKSHOP_EMPTY_VALUE,
                    liningBasis,
                    liningQuantity,
                    sanitizeEditableText(lining.notes || "") || WORKSHOP_EMPTY_VALUE,
                  ],
                };
              })()
            : {
                cellText: WORKSHOP_EMPTY_VALUE,
                summaryRow: null,
              };
        if (liningDisplay.summaryRow) {
          workshopLiningSummaryRows.push(liningDisplay.summaryRow);
        }
        const workshopTrackConfig = adjustedCustomerCalc.trackConfig || calculateCurtainTrackConfig(
          r,
          stitchCfg,
          {
            baseTrackLengthFt:
              Number(r.trackLengthFt || r.trackLength || stitchCfg.trackLengthFt || 0),
            measurementUnit: getRowMeasurementUnit(r, unit),
          }
        );
        const segmentParts = getTrackSegmentEntries(workshopTrackConfig).map((segment) =>
          `${segment.label || ""} ${formatLengthWithUnit(
            segment.length || 0,
            segment.unit || workshopTrackConfig.segmentUnit || "in"
          )}`.trim()
        );
        const workshopTrackUnit =
          workshopTrackConfig.totalSegmentLengthUnit ||
          workshopTrackConfig.segmentUnit ||
          getRowMeasurementUnit(r, unit);
        const bendedTrackParts =
          workshopTrackConfig?.isBended
            ? [
                "Track: Bended Track",
                `Shape: ${getBendedTrackShapeMeta(workshopTrackConfig.shape).label}`,
                workshopTrackConfig.bendLocationSummary
                  ? `Bend Location: ${workshopTrackConfig.bendLocationSummary}`
                  : "",
                workshopTrackConfig.angleDeg ? `Angle: ${Number(workshopTrackConfig.angleDeg).toFixed(0)} deg` : "",
                `No. of bends: ${Number(workshopTrackConfig.bendCount || 0)}`,
                segmentParts.length ? `Segments: ${segmentParts.join(" | ")}` : "",
                Number(workshopTrackConfig.totalSegmentLength || 0) > 0
                  ? `Segment length: ${formatLengthWithUnit(
                      workshopTrackConfig.totalSegmentLength || 0,
                      workshopTrackUnit
                    )}`
                  : "",
                Number(workshopTrackConfig.totalTrackLengthFt || 0) > 0
                  ? `Running length: ${Number(workshopTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
                  : "",
                workshopTrackConfig.notes ? `Track Notes: ${workshopTrackConfig.notes}` : "",
              ].filter(Boolean)
            : [];
        const fabricTint = getWorkshopFabricTint(
          fabricKey,
          fabric,
          allocation.fabricColorKey || getWorkshopFabricColorKey(r, fabric)
        );
        rowVisuals.push({
          ...rowVisual,
          fillColor: fabricTint,
          fabricTint,
          halfPieceLabel: allocation.fabricSource || rowVisual.label,
          hasLining: Boolean(lining?.enabled),
        });

        const displayFabricRules = resolveFabricRulesForRow(
          r,
          shouldUseLegacyProductCalculation(r, displayProductConfig)
            ? null
            : displayProductConfig,
          { ...fabricCalcSettings, quoteConstructionSettings }
        );
        let visibleDropIn = heightIn;
        if (r.productType === "Curtains") {
          visibleDropIn = heightIn + Number(displayFabricRules.dropAllowanceIn || 0);
        } else if (r.productType?.startsWith("Roman")) {
          visibleDropIn = heightIn + Number(displayFabricRules.dropAllowanceIn || 0);
        }

        const quantityLabel = r.quantity ?? r.qty ?? c.quantity ?? "";
        const blindSqft = Number(c.billableSqft ?? c.adjSqft ?? 0);
        const isRomanWorkshopRow =
          c.isRoman === true || normalizeProductName(product).startsWith("roman");
        const romanPanelCountPerBlind = Number(
          rowPlan?.panelCountPerBlind ||
            c.panelCountPerBlind ||
            c.romanPanelCalc?.panelCount ||
            0
        );
        const romanQuantity = Math.max(
          1,
          Number(rowPlan?.quantity || c.romanPanelCalc?.quantity || r.qty || r.quantity || 1)
        );
        const romanTotalPanels = Number(
          rowPlan?.totalPanels ||
            rowPlan?.totalPieces ||
            c.romanPanelCalc?.totalPanels ||
            c.totalPieces ||
            0
        );
        const romanPanelWidths = (
          rowPlan?.panelWidthsIn ||
          c.panelWidthsIn ||
          c.romanPanelCalc?.panelWidthsIn ||
          []
        )
          .map((panelWidth) => Number(panelWidth || 0))
          .filter((panelWidth) => panelWidth > 0);
        const romanPanelWidthsText = romanPanelWidths.length
          ? romanPanelWidths
              .map((panelWidth) => `${Number(panelWidth).toFixed(1)} in`)
              .join(" + ")
          : "";
        const cuttingExpr = (
          isRomanWorkshopRow
            ? [
                romanPanelCountPerBlind > 0
                  ? romanQuantity > 1
                    ? `Panels: ${romanPanelCountPerBlind} per blind x ${romanQuantity} = ${romanTotalPanels}`
                    : `Panels: ${romanPanelCountPerBlind} equal panel${romanPanelCountPerBlind === 1 ? "" : "s"}`
                  : "",
                romanPanelWidthsText
                  ? `Panel Widths per blind: ${romanPanelWidthsText}`
                  : "",
                romanPanelCountPerBlind > 1
                  ? romanPanelCountPerBlind === 2
                    ? "Joint: Center joint"
                    : "Joint: Balanced equal joints"
                  : "Joint: None",
                `Each cut height: ${formatMeasurementWithUnit(
                  cutHeightIn,
                  unit,
                  "Cut H"
                )}`,
                `Base Cut: ${formatMeasurementWithUnit(visibleDropIn, unit)}`,
                hasRowRepeat ? "Repeat Cut" : "",
                repeatLabel,
                notesLabel ? `Notes: ${notesLabel}` : "",
              ]
            : pdfComponent.isPanelFabric
            ? [
                `Panels: ${totalPieces.toFixed(1)} pcs`,
                `New Full Cuts: ${Number(effectiveRowPlan.newFullCuts || 0)}`,
                Number(effectiveRowPlan.reusedHalfCount || 0) > 0
                  ? `Reused Half: ${effectiveRowPlan.reusedHalfCount}`
                  : "",
                `Each: ${formatMeasurementWithUnit(fabricW, unit, "W")} x ${formatMeasurementWithUnit(
                  cutHeightIn,
                  unit,
                  "Cut H"
                )}`,
                `Base Cut: ${formatMeasurementWithUnit(visibleDropIn, unit)}`,
                hasRowRepeat ? "Repeat Cut" : "",
                repeatLabel,
                trackParts.length ? `Track: ${trackParts.join(" | ")}` : "",
                ...bendedTrackParts,
                `Half-piece: [${allocation.fabricSource || rowVisual.label}]`,
                displayFabricRules.noBottomHem ? "NO BOTTOM HEM" : "",
                `Hems (in): top ${displayFabricRules.topHemIn}, bottom ${displayFabricRules.bottomHemIn}, each side ${displayFabricRules.sideAllowancePerSideIn}`,
                notesLabel ? `Notes: ${notesLabel}` : "",
              ]
            : [
                blindSqft > 0 ? `Billable Sqft: ${blindSqft.toFixed(2)}` : "",
                pdfComponent.isDirectFabric ? `Fabric required: ${newMeters === null ? "Not recorded" : newMeters.toFixed(2) + " m"}` : "",
                pdfComponent.isDirectFabric ? repeatLabel : "",
                quantityLabel ? `Qty: ${quantityLabel}` : "",
                trackParts.length ? `Track: ${trackParts.join(" | ")}` : "",
                ...bendedTrackParts,
                notesLabel ? `Notes: ${notesLabel}` : "",
              ]
        )
          .filter(Boolean)
          .join("\n");

        tableRows.push([
          item,
          product,
          fabric,
          workshopStitchLabel,
          formatMeasurementWithUnit(widthIn, unit, "W"),
          formatMeasurementWithUnit(heightIn, unit, "Finished H"),
          cuttingExpr,
          liningDisplay.cellText,
          isFabricWorkshopRow ? newMeters === null ? "Not recorded" : newMeters.toFixed(2) : "",
        ]);
      });

      if (IS_DEV_UI_ENABLED) {
        devLog("STITCH_TRACE_4_PDF_TABLE_ROWS", tableRows);
        devLog("CURTAIN_WORKSHOP_TABLE_BODY", tableRows);
      }

      assertNoPricingInWorkshopPdfText(
        {
          head: [
            "Item",
            "Product",
            "Fabric",
            "Stitch",
            "W",
            "H",
            "Cutting",
            "Lining",
            "Fabric to Order (m)",
          ],
          body: tableRows,
          summaryHead: [
            "Fabric Photo",
            "Brand",
            "Fabric / SR",
            "Fabric Width",
            "Used in",
            "Fabric to Order (m)",
            "Half-piece Saving (m)",
            "Repeat",
            "Notes",
          ],
          summaryBody: workshopFabricSummaryRows.map((summary) => [
            "",
            summary.brandName || "",
            summary.fabricCode || summary.displayName || "",
            summary.hasMixedFabricWidths
              ? (summary.fabricWidthValues || [])
                  .map((value) => getFabricWidthDisplayLabel(value))
                  .filter(Boolean)
                  .join(", ")
              : getFabricWidthDisplayLabel(summary.fabricWidthIn) || "54 in",
            (summary.usedInItems || []).slice(0, 6).join(", "),
            summary.pdfFabricMetersUnavailable ? "Not recorded" : Number(
              summary?.fabricToOrderMeters ??
                summary?.workshopFabricRequiredMeters ??
                summary?.newFabricNeededMeters ??
                0
            ).toFixed(2),
            Number(summary?.halfReusedMeters || 0).toFixed(2),
            getRepeatSummaryDisplay(summary),
            summary.notes || "",
          ]),
          liningHead: [
            "Item",
            "Main Fabric",
            "Lining",
            "Lining Fabric / Code",
            "Basis",
            "Quantity",
            "Notes",
          ],
          liningBody: workshopLiningSummaryRows,
        },
        { label: "curtainWorkshopPdfText" }
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[
          "Item",
          "Product",
          "Fabric",
          "Stitch",
          "W",
          "H",
          "Cutting",
          "Lining",
          "Fabric to Order (m)",
        ]],
        body: tableRows,
        rowPageBreak: "avoid",
        styles: {
          fontSize: 8.7,
          cellPadding: 2.6,
          valign: "top",
          overflow: "linebreak",
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
        },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 14 },
          1: { cellWidth: 16 },
          2: { cellWidth: 24 },
          3: { cellWidth: 15 },
          4: { cellWidth: 13, halign: "right" },
          5: { cellWidth: 15, halign: "right" },
          6: { cellWidth: 34 },
          7: { cellWidth: 28 },
          8: { halign: "right", cellWidth: 20 },
        },
        didParseCell: (data) => {
          if (data.section !== "body") return;

          const visual = rowVisuals[data.row.index];
          if (visual?.fillColor) {
            data.cell.styles.fillColor = visual.fillColor;
          }

          if (data.column.index === 6) {
            data.cell.styles.minCellHeight = Math.max(
              Number(data.cell.styles.minCellHeight || 0),
              17
            );
          }
          if (data.column.index === 7 && visual?.hasLining) {
            data.cell.styles.fillColor = [236, 253, 245];
            data.cell.styles.textColor = [6, 95, 70];
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      const afterTableY = Number(doc.lastAutoTable?.finalY || y + 10) + 8;
      if (workshopFabricSummaryRows.length > 0) {
        const workshopFabricPhotoImages = await Promise.all(
          workshopFabricSummaryRows.map(async (summary) =>
            loadFabricPhotoForPdf(await resolveFabricSummaryPhotoForPdf(summary))
          )
        );

        autoTable(doc, {
          startY: afterTableY,
          margin: { left: marginX, right: marginX },
          head: [[
            "Fabric Photo",
            "Brand",
            "Fabric / SR",
            "Fabric Width",
            "Used in",
            "Fabric to Order (m)",
            "Half-piece Saving (m)",
            "Repeat",
            "Notes",
          ]],
          body: workshopFabricSummaryRows.map((summary) => [
            "",
            summary.brandName || "",
            summary.fabricCode || summary.displayName || "",
            summary.hasMixedFabricWidths
              ? (summary.fabricWidthValues || [])
                  .map((value) => getFabricWidthDisplayLabel(value))
                  .filter(Boolean)
                  .join(", ")
              : getFabricWidthDisplayLabel(summary.fabricWidthIn) || "54 in",
            (summary.usedInItems || []).slice(0, 6).join(", "),
            summary.pdfFabricMetersUnavailable ? "Not recorded" : Number(
              summary?.fabricToOrderMeters ??
                summary?.workshopFabricRequiredMeters ??
                summary?.newFabricNeededMeters ??
                0
            ).toFixed(2),
            Number(summary?.halfReusedMeters || 0).toFixed(2),
            getRepeatSummaryDisplay(summary),
            summary.notes || "",
          ]),
          styles: { fontSize: 7.4, cellPadding: 1.6, overflow: "linebreak" },
          headStyles: { fillColor: [37, 99, 235], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 22, halign: "center" },
            5: { halign: "right" },
            6: { halign: "right" },
          },
          didParseCell: (data) => {
            if (data.section !== "body") return;
            const summary = workshopFabricSummaryRows[data.row.index] || {};
            data.cell.styles.fillColor = getWorkshopFabricTint(
              summary.normalizedKey || summary.fabricKey || "",
              summary.fabricCode || summary.displayName || ""
            );
            if (data.column.index === 0) {
              data.cell.styles.minCellHeight = 22;
            }
          },
          didDrawCell: (data) => {
            if (data.section !== "body" || data.column.index !== 0) return;
            const image = workshopFabricPhotoImages[data.row.index];
            if (!image?.dataUrl) return;
            const size = Math.min(18, data.cell.width - 3, data.cell.height - 3);
            if (size <= 0) return;
            const x = data.cell.x + (data.cell.width - size) / 2;
            const yPos = data.cell.y + (data.cell.height - size) / 2;
            doc.addImage(image.dataUrl, image.format || "JPEG", x, yPos, size, size);
            if (image.sourceUrl && typeof doc.link === "function") {
              doc.link(x, yPos, size, size, { url: image.sourceUrl });
            }
          },
        });
      }

      if (workshopLiningSummaryRows.length > 0) {
        const afterFabricSummaryY = Number(doc.lastAutoTable?.finalY || afterTableY) + 8;
        autoTable(doc, {
          startY: afterFabricSummaryY,
          margin: { left: marginX, right: marginX },
          head: [[
            "Item",
            "Main Fabric",
            "Lining",
            "Lining Fabric / Code",
            "Basis",
            "Quantity",
            "Notes",
          ]],
          body: workshopLiningSummaryRows,
          styles: { fontSize: 7.5, cellPadding: 1.7, overflow: "linebreak" },
          headStyles: { fillColor: [5, 150, 105], textColor: 255 },
          columnStyles: {
            1: { cellWidth: 28 },
            3: { cellWidth: 32 },
            5: { halign: "right" },
          },
        });
      }

      addWorkshopFooter(doc);

      zip.file(workshopZipGroup.fileName, doc.output("arraybuffer"));
    }
    }

    if (accessoryRows.length) {
      const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
      const { marginX, pageW, headerBottomY } = addWorkshopHeader(
        doc,
        logoMeta,
        shopAddress
      );
      const titleY = headerBottomY + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Accessories / Hardware Required", pageW / 2, titleY, {
        align: "center",
      });

      let y = titleY + 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Customer: ${customerName || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      const blindWorkshopSiteText = getQuoteSiteDisplayText(
        normalizeQuoteSiteSnapshot(
          liveActionPayload?.quoteSiteSelection || selectedQuote || quoteSiteSelection,
          { customerName }
        )
      );
      if (blindWorkshopSiteText) {
        doc.text(`Site: ${blindWorkshopSiteText}`, marginX, y);
        y += 6;
      }
      doc.text(`Quote: ${quoteNo || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      doc.text(`Date: ${workshopQuoteDate || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 10;

      const accessoryBody = accessoryRows.map((row, index) => {
        const calc = calcRow(row, repeatSettings);
        const calcType = calc.calculationType || row.calculationType || "";
        const isLengthRow = calcType === "running_length_rate";
        const quantityValue = isLengthRow
          ? row.length || calc.runningLength || ""
          : row.qty || row.quantity || calc.quantity || "";
        const unitValue = isLengthRow
          ? row.lengthUnit || calc.lengthUnit || "ft"
          : row.unit || calc.unit || "pcs";
        return [
          getWorkshopRowItemWithLocation(row, `A${index + 1}`),
          getCurtainRowDisplay(row.productName || row.productType || row.product, "Accessory"),
          getCurtainRowDisplay(row.description || row.modelCode || row.materialCode || "", ""),
          String(quantityValue || ""),
          unitValue,
          getCurtainRowDisplay(row.notes || row.remarks || row.note, ""),
        ];
      });

      assertNoPricingInWorkshopPdfText(
        {
          head: ["Item", "Product", "Description / Code", "Qty / Length", "Unit", "Notes"],
          body: accessoryBody,
        },
        { label: "curtainWorkshopAccessoryText" }
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [["Item", "Product", "Description / Code", "Qty / Length", "Unit", "Notes"]],
        body: accessoryBody,
        rowPageBreak: "avoid",
        styles: {
          fontSize: 9,
          cellPadding: 2.6,
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
          valign: "top",
        },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        columnStyles: {
          3: { halign: "right" },
        },
      });

      addWorkshopFooter(doc);
      zip.file(
        `Workshop-Accessories-Hardware-${customerName || "Customer"}-${quoteNo}.pdf`,
        doc.output("arraybuffer")
      );
    }

    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });

    console.timeEnd("workshop-zip");

    saveAs(
      zipBlob,
      `Workshop-${customerName || "Customer"}-${quoteNo || ""}.zip`
    );

    // Export is read-only. A cutting document is not an inventory reservation
    // or permission to mutate the saved quotation and its sorting timestamps.
  } catch (err) {
    devError("WORKSHOP_ZIP_FAILED_EXACT", {
      name: err?.name || "",
      message: err?.message || String(err || ""),
      stack: err?.stack || "",
      ...getChunkLoadDebugContext(),
    });

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      notify(
        "You appear to be offline. Please reconnect and try Workshop ZIP again.",
        "warning"
      );
      return;
    }

    if (isChunkLoadError(err)) {
      handleChunkLoadErrorRecovery(err, {
        source: "generateWorkshopZIP.catch",
        autoReload: false,
      });
      notify(
        "Workshop exporter could not load. Please refresh the page and try again.",
        "warning",
        7000
      );
      return;
    }

    notify("Workshop ZIP failed. Please try again.", "error");
  }
};
const exportCuttingPDF = async (
  solution,
  quote,
  selectedFabric,
  finalDemands = []
) => {
  if (!solution) return alert("No optimization selected.");
  assertCompatibleQuote(quote);

  const { jsPDF } = await loadPdfRuntime();
  const doc = new jsPDF("p", "mm", "a4");

  const pageW = Number(doc.internal.pageSize.getWidth());
  const pageH = Number(doc.internal.pageSize.getHeight());
  const marginX = 15;

  const safeCustomer = quote?.customerName?.trim() || "-";
  const safeQuote = quote?.quoteNo?.trim() || "-";
  const quoteRowsForFabric = Array.isArray(quote?.rows)
    ? quote.rows
    : [];
  const selectedFabricRow = quoteRowsForFabric.find(row => getCurtainRowFabricKey(row) === selectedFabric);
  const safeFabric =
    (selectedFabricRow ? getCurtainRowFabricLabel(selectedFabricRow) : "") ||
    normalizeWorkshopText(selectedFabric) ||
    quoteRowsForFabric.map((row) => getCurtainRowFabricLabel(row)).find((label) => label && label !== WORKSHOP_EMPTY_VALUE) ||
    WORKSHOP_EMPTY_VALUE;
  const safeDate = new Date().toLocaleDateString();

  const totalRequired = (
    (finalDemands || []).reduce(
      (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
      0
    )
  );

  let logoMeta = null;
  try {
    const raw = await prepareLogoForWorkshop(getLogoDataURL, logoURL);

    if (raw?.dataUrl) {
      const BASE_W = 64;
      const originalW = raw.logoWmm || BASE_W;
      const originalH = raw.logoHmm || 12;

      const scale = BASE_W / originalW;
      let w = BASE_W;
      let h = originalH * scale;

      const MAX_H = 32;
      const MIN_H = 16;

      if (h > MAX_H) {
        const s = MAX_H / h;
        h = MAX_H;
        w = w * s;
      }
      if (h < MIN_H) {
        const s = MIN_H / h;
        h = MIN_H;
        w = w * s;
      }

      logoMeta = {
        dataUrl: raw.dataUrl,
        format: "PNG",
        w,
        h,
      };
    }
  } catch (e) {
    devWarn("Logo load failed:", e);
  }

  const headerTop = 12;
  const headerHeight = 24;

  if (logoMeta) {
    const logoY = headerTop + (headerHeight - logoMeta.h) / 2;

    doc.addImage(
      logoMeta.dataUrl,
      logoMeta.format,
      marginX,
      logoY,
      logoMeta.w,
      logoMeta.h
    );
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ROLL OPTIMIZATION", pageW / 2, 25, {
    align: "center",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(shopAddress || "", pageW - marginX, 20, {
    align: "right",
  });

  const ruleY = headerTop + headerHeight + 4;
  doc.setLineWidth(0.3);
  doc.line(marginX, ruleY, pageW - marginX, ruleY);

  let y = ruleY + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Customer:", marginX, y);
  doc.setFont("helvetica", "normal");
  const customerHeaderLines = doc.splitTextToSize(safeCustomer, pageW / 2 + 5 - marginX - 28);
  doc.text(customerHeaderLines, marginX + 28, y);

  doc.setFont("helvetica", "bold");
  doc.text("Quote No:", pageW / 2 + 10, y);
  doc.setFont("helvetica", "normal");
  const quoteHeaderLines = doc.splitTextToSize(safeQuote, pageW / 2 - marginX - 32);
  doc.text(quoteHeaderLines, pageW / 2 + 32, y);

  y += Math.max(customerHeaderLines.length, quoteHeaderLines.length) * 4.5 + 3;

  doc.setFont("helvetica", "bold");
  doc.text("Fabric:", marginX, y);
  doc.setFont("helvetica", "normal");
  const fabricHeaderLines = doc.splitTextToSize(safeFabric, pageW / 2 + 5 - marginX - 28);
  doc.text(fabricHeaderLines, marginX + 28, y);

  doc.setFont("helvetica", "bold");
  doc.text("Date:", pageW / 2 + 10, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeDate, pageW / 2 + 32, y);

  y += fabricHeaderLines.length * 4.5 + 3;

  doc.setFont("helvetica", "bold");
  doc.text("Cutting metres:", marginX, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${Number(totalRequired).toFixed(4)} m`, marginX + 36, y);

  y += 12;

  const contentRightX = pageW - marginX;
  const maxContentY = pageH - 24;
  let currentSectionTitle = "";
  let sectionRowCount = 0;

  const drawSectionTitle = (title) => {
    if (y + 10 > maxContentY) {
      doc.addPage();
      y = 20;
    }

    currentSectionTitle = title;
    sectionRowCount = 0;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, marginX, y);
    y += 7;
  };

  const addSectionPage = (title = currentSectionTitle) => {
    doc.addPage();
    y = 20;
    currentSectionTitle = title || "";
    sectionRowCount = 0;

    if (title) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(title, marginX, y);
      y += 7;
    }
  };

  const ensureSectionSpace = (neededHeight, title = currentSectionTitle) => {
    if (
      y + neededHeight > maxContentY ||
      (sectionRowCount > 0 && sectionRowCount % 25 === 0)
    ) {
      addSectionPage(title);
    }
  };

  const ensurePlainSpace = (neededHeight) => {
    if (y + neededHeight > maxContentY) {
      doc.addPage();
      y = 20;
      currentSectionTitle = "";
      sectionRowCount = 0;
    }
  };

  const getCutDisplayMeta = (cutHeight) => {
    const matchedRow = (quote?.rows || []).find((row) => {
      const calc = calcRow(row, repeatSettings);
      const rowCutHeight = Number(calc.cutCurtain || calc.cutRoman || 0);
      return Math.abs(rowCutHeight - Number(cutHeight || 0)) < 0.5;
    });

    let visibleDrop = Number(cutHeight || 0);
    let effectiveFabricWidthIn = Number(solution.fabricWidthsIn?.[0] || selectedFabricRow?.fabricWidthIn || 54);
    let fabricKey = normalizeFabricKey(safeFabric || selectedFabric || "");

    if (matchedRow) {
      fabricKey = getCurtainRowFabricKey(matchedRow) || fabricKey;

      const isCurtain = matchedRow.productType === "Curtains";
      const matchedProductConfig = getRowProductConfig(matchedRow);
      const matchedFabricRules = resolveFabricRulesForRow(
        matchedRow,
        shouldUseLegacyProductCalculation(matchedRow, matchedProductConfig)
          ? null
          : matchedProductConfig,
        fabricCalcSettings
      );
      effectiveFabricWidthIn = Number(matchedFabricRules.fabricWidthIn || effectiveFabricWidthIn);
      if (isCurtain) {
        visibleDrop =
          Number(matchedRow.height || 0) + Number(matchedFabricRules.dropAllowanceIn || 0);
      } else {
        visibleDrop =
          Number(matchedRow.height || 0) + Number(matchedFabricRules.dropAllowanceIn || 0);
      }
    }

    const repeat = matchedRow
      ? calcRow(matchedRow, repeatSettings).repeatSettings || {}
      : normalizeCurtainRepeatEntry(repeatSettings?.[fabricKey] || {});
    const repeatDisplay = formatRepeatSettings(repeat);
    const hasRepeat = repeatDisplay !== WORKSHOP_EMPTY_VALUE;

    const displayCutHeightIn = hasRepeat
      ? Number(cutHeight || 0)
      : Number(visibleDrop || 0);

    return {
      repeatDisplay,
      hasRepeat,
      visibleDropIn: Number(visibleDrop || 0),
      displayCutHeightIn,
      fabricWidthIn: effectiveFabricWidthIn,
    };
  };

  const drawCutRow = ({ qty, cutHeight, sectionTitle }) => {
    const { repeatDisplay, hasRepeat, visibleDropIn, displayCutHeightIn, fabricWidthIn } =
      getCutDisplayMeta(cutHeight);

    const rowLines = [
      `${qty} pcs`,
      `Each: ${formatMeasurementWithUnit(fabricWidthIn, unit, "W")} x ${formatMeasurementWithUnit(
        displayCutHeightIn,
        unit,
        "Cut H"
      )}`,
      hasRepeat ? `Base Cut: ${formatMeasurementWithUnit(visibleDropIn, unit)}` : "",
      hasRepeat ? "Repeat Cut" : "Standard Cut",
      hasRepeat ? `Repeat Setting: ${repeatDisplay}` : "",
    ].filter(Boolean);

    const rowHeight = 8 + rowLines.length * 4.5 + 3;

    ensureSectionSpace(rowHeight, sectionTitle);

    doc.setFillColor(
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 254 : 248,
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 249 : 250,
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 195 : 252
    );
    doc.roundedRect(marginX, y - 4, pageW - marginX * 2, rowHeight, 2.5, 2.5, "F");

    rowLines.forEach((line, index) => {
      doc.setFont("helvetica", index === 0 ? "bold" : "normal");
      doc.setFontSize(index === 0 ? 10.5 : 9.5);
      doc.text(line, marginX + 4, y);
      y += 4.5;
    });

    y += 3;
    sectionRowCount += 1;
  };

  for (const line of rollAlignmentSummaryLines(solution).filter(text => !text.startsWith("Customer billable"))) {
    const lines = doc.splitTextToSize(line, pageW - marginX * 2);
    for (const text of lines) {
      ensurePlainSpace(6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(text, marginX, y);
      y += 5;
    }
  }
  y += 4;
  drawSectionTitle("ROLL CUTTING");

  (solution.rollResults || []).forEach((rr, i) => {
    const used = Number(rr?.patternInfo?.used || 0);
    const waste = Number(rr?.patternInfo?.waste || 0);
    ensureSectionSpace(26, "ROLL CUTTING");

    doc.setFillColor(241, 245, 249);
    doc.roundedRect(marginX, y - 4, pageW - marginX * 2, 24, 3, 3, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(rr.rollId || `Roll ${i + 1}`, marginX + 4, y + 1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(
      `Total Length: ${formatMetersLabel(rr?.rollLen || 0)}`,
      marginX + 4,
      y + 7
    );
    doc.text(
      `Reserved consumption: ${formatMetersLabel(used)} | Residual: ${formatMetersLabel(waste)}`,
      contentRightX - 4,
      y + 7,
      { align: "right" }
    );
    doc.text(`Alignment reserve: ${Number(rr.alignmentReserveMeters || 0).toFixed(4)} m (trim only as required)`, marginX + 4, y + 14);
    doc.text(`Reference: ${[...new Set((rr.allocations || []).map(a => a.referenceRollId))].join(', ') || 'workshop verification required'}`, marginX + 4, y + 18);
    y += 26;

    Object.entries(rr?.patternInfo?.pattern || {}).forEach(([len, qty]) => {
      drawCutRow({
        qty,
        cutHeight: Number(len || 0) / 0.0254,
        sectionTitle: "ROLL CUTTING",
      });
    });

    y += 4;
  });

  const totalRollWaste = (solution.rollResults || []).reduce(
    (sum, result) => sum + Number(result?.patternInfo?.waste || 0),
    0
  );

  ensurePlainSpace(8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(
    `Residual on Allocated Rolls: ${formatMetersLabel(totalRollWaste)}`,
    marginX,
    y
  );

  y += 12;

  drawSectionTitle("EXTRA CUT FABRIC REQUIRED");

  if (solution.remaining?.length) {
    solution.remaining.forEach((item) => {
      drawCutRow({
        qty: item.qty,
        cutHeight: Number(item.length || 0) / 0.0254,
        sectionTitle: "EXTRA CUT FABRIC REQUIRED",
      });
    });

    y += 4;
    ensurePlainSpace(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(
      `Procurement (continuous usable rolls as listed above): ${formatMetersLabel(
        solution.additionalProcurementMeters ?? solution.finalRemainingMeters ?? 0
      )}`,
      marginX,
      y
    );
  } else {
    ensurePlainSpace(6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("No extra cut fabric required.", marginX + 4, y);
  }

  y += 18;
  ensurePlainSpace(14);
  doc.setLineWidth(0.2);
  doc.line(marginX, y, marginX + 60, y);
  doc.line(pageW - marginX - 60, y, pageW - marginX, y);

  doc.setFontSize(10);
  doc.text("Prepared By", marginX, y + 6);
  doc.text("Workshop Approval", pageW - marginX - 60, y + 6);

  const pageCount = doc.getNumberOfPages();
  for (let pageNo = 1; pageNo <= pageCount; pageNo++) {
    doc.setPage(pageNo);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      marginX,
      pageH - 12
    );

    doc.text(`Powered by FabriQuote`, pageW / 2, pageH - 12, {
      align: "center",
    });

    doc.text(`Page ${pageNo} of ${pageCount}`, pageW - marginX, pageH - 12, {
      align: "right",
    });
  }

  doc.save(`Cutting-${safeCustomer}-${safeQuote}-Selected.pdf`);
};

// ================= TRACK OPTIMIZATION PDF =================
const buildRollVisualization = (rollResults) => {
  return (rollResults || []).map((rr, i) => {
    const segments = [];
    let current = 0;

    const reserve = Number(rr.alignmentReserveMeters || 0);
    if (reserve > 0) { segments.push({ type: "reserve", length: reserve }); current += reserve; }

    const pattern = rr?.patternInfo?.pattern || {};

    Object.entries(pattern)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .forEach(([length, count]) => {
      const len = Number(length);
      const qty = Number(count) || 0;

      for (let j = 0; j < qty; j++) {
        segments.push({ type: "cut", length: len });
        current += len;
      }
      });

    const rollLength = Number(rr?.rollLength || rr?.rollLen || 0);
    const wasteLength = Math.max(0, rollLength - current);

    if (wasteLength > 0) {
      segments.push({
        type: "waste",
        length: wasteLength,
      });
    }

    return {
      rollIndex: i + 1,
      rollLength,
      segments,
      usedMeters: current,
      wasteMeters: wasteLength,
    };
  });
};

const RollVisualizer = ({ rolls }) => {
  return (
    <div style={{ marginTop: 20 }}>
      {(rolls || []).map((roll, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12 }}>
            Roll {roll.rollIndex} ({roll.rollLength}m)
          </div>
          <div style={{ fontSize: 11, marginBottom: 4 }}>
            Reserved consumption: {Number(roll.usedMeters || 0).toFixed(2)}m | Residual: {Number(roll.wasteMeters || 0).toFixed(2)}m
          </div>

          <div
            style={{
              display: "flex",
              height: 20,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            {(() => {
              const containerWidth = 400;

              const safeContainerWidth =
                Number.isFinite(containerWidth) && containerWidth > 0
                  ? Math.floor(containerWidth)
                  : 1;

              const segs = Array.isArray(roll?.segments) ? roll.segments : [];
              const n = segs.length;

              const raw = segs.map((seg) => {
                const v = Number(seg?.length);
                return Number.isFinite(v) && v > 0 ? v : 0;
              });

              const total = raw.reduce((a, b) => a + b, 0);

              const norm =
                total > 0
                  ? raw.map((w) => (w / total) * safeContainerWidth)
                  : raw.map(() => safeContainerWidth / Math.max(1, n));

              let widths = norm.map((v) => Math.floor(v));

              let remaining =
                safeContainerWidth - widths.reduce((a, b) => a + b, 0);

              const order = norm
                .map((v, i) => ({ i, frac: v - Math.floor(v) }))
                .sort((a, b) => b.frac - a.frac);

              for (let k = 0; k < order.length && remaining > 0; k++) {
                widths[order[k].i] += 1;
                remaining--;
              }

              if (n > 0 && n <= safeContainerWidth) {
                let deficit = 0;

                for (let i = 0; i < n; i++) {
                  if (widths[i] < 1) {
                    deficit += 1 - widths[i];
                    widths[i] = 1;
                  }
                }

                if (deficit > 0) {
                  let pass = 0;
                  const maxPass = n;

                  while (deficit > 0 && pass < maxPass) {
                    let changed = false;

                    const sorted = [...widths]
                      .map((w, i) => ({ i, w }))
                      .sort((a, b) => b.w - a.w);

                    for (
                      let k = 0;
                      k < sorted.length && deficit > 0;
                      k++
                    ) {
                      const idx = sorted[k].i;

                      if (widths[idx] > 1) {
                        widths[idx] -= 1;
                        deficit--;
                        changed = true;
                      }
                    }

                    if (!changed) break;
                    pass++;
                  }
                }
              }

              return segs.map((seg, j) => {
                const w =
                  Number.isFinite(widths[j]) && widths[j] > 0
                    ? widths[j]
                    : 1;

                const len = Number.isFinite(Number(seg?.length))
                  ? Number(seg.length)
                  : 0;

                return (
                  <div
                    key={`${j}-${seg?.type || "seg"}-${len}`}
                    title={`${seg?.type || "cut"} ${len.toFixed(2)}m`}
                    style={{
                      width: `${w}px`,
                      background:
                        seg?.type === "cut" ? "#22c55e" : seg?.type === "reserve" ? "#fbbf24" : "#fecaca",
                      borderRight:
                        j !== segs.length - 1
                          ? "1px solid #fff"
                          : "none",
                    }}
                  />
                );
              });
            })()}
          </div>
        </div>
      ))}
    </div>
  );
};


  /* --------------------------- UI BEGINS (PART 3 CONTINUES) --------------------------- */
   /* ---------------- MAIN PANEL ---------------- */

  const dashboardBorder = isDark
    ? "1px solid rgba(148,163,184,0.14)"
    : "1px solid rgba(226,232,240,0.9)";
  const dashboardSurface = isDark ? "#0f172a" : "#ffffff";
  const dashboardCardBg = isDark ? "rgba(15,23,42,0.86)" : "#ffffff";
  const dashboardMuted = isDark ? "#94a3b8" : "#64748b";
  const dashboardSoftBg = isDark ? "rgba(15,23,42,0.7)" : "#f8fafc";

  const dashboardPanelStyle = {
    background: dashboardSurface,
    padding: isCompact ? 18 : 26,
    borderRadius: 24,
    boxShadow: isDark
      ? "0 18px 50px rgba(2,6,23,0.42)"
      : "0 18px 50px rgba(15,23,42,0.08)",
    border: dashboardBorder,
    marginBottom: 26,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const dashboardCardStyle = {
    background: dashboardCardBg,
    borderRadius: 22,
    padding: isCompact ? 16 : 20,
    border: dashboardBorder,
    boxShadow: isDark
      ? "0 10px 30px rgba(2,6,23,0.35)"
      : "0 10px 30px rgba(15,23,42,0.05)",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowWrap: "normal",
    wordBreak: "normal",
  };

  const openDashboardCustomer = useCallback(
    (customerLike = {}) => {
      const directCustomerId = String(customerLike.customerId || customerLike.id || "").trim();
      const normalizedName = normalizeCustomerLedgerName(
        customerLike.name || customerLike.customerName || ""
      );
      const phone = normalizeCustomerLedgerPhone(customerLike.phone || customerLike.customerPhone || "");
      const matchedCustomer =
        (directCustomerId &&
          customerRecords.find((customer) => customer.id === directCustomerId)) ||
        customerRecords.find((customer) => {
          const customerPhone = normalizeCustomerLedgerPhone(
            customer.phone || customer.whatsapp || ""
          );
          if (phone && customerPhone && phone === customerPhone) return true;
          return (
            normalizedName &&
            normalizeCustomerLedgerName(customer.customerName) === normalizedName
          );
        });

      if (matchedCustomer?.id) {
        openCustomerLedger(matchedCustomer.id);
        setCustomerLedgerDetailTab("overview");
        return;
      }

      const fallbackName = String(customerLike.name || customerLike.customerName || "").trim();
      setCustomerLedgerSearch(fallbackName);
      setSelectedCustomerLedgerId("");
      setCustomerLedgerDetailTab("overview");
      setRoutedTab("customerLedger");
    },
    [customerRecords, openCustomerLedger, setRoutedTab]
  );

  const getDashboardMetricRecords = useCallback(
    (metricKey, context = {}) => {
      const revenueQuotes = periodConfirmed.filter((quote) => quote.isRevenueEligible);
      const monthKey = String(context.monthKey || "").trim();
      const quoteType = context.quoteType || "";
      const stageKey = context.stageKey || "";
      const dateKey = String(context.dateKey || "").trim();
      const monthMatches = (millis) => {
        if (!monthKey || !millis) return false;
        const date = new Date(millis);
        if (Number.isNaN(date.getTime())) return false;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        return key === monthKey;
      };
      const stageMatches = (quote) => {
        if (!stageKey) return false;
        const resolvedQuoteType = resolveQuoteType(quote);
        const stageMeta = getCrmStage(
          crmPipelines,
          resolvedQuoteType,
          quote.workStageResolved || quote.workStage
        );
        const quoteStageKey = `${resolvedQuoteType}::${stageMeta?.id || quote.workStageResolved || "quote_given"}`;
        return quoteStageKey === stageKey;
      };
      const make = ({
        title,
        subtitle = "",
        records = [],
        recordType = "quotes",
        summaryValue = null,
        metricValue = null,
        filters = [],
      }) => ({
        key: metricKey,
        title,
        subtitle,
        records,
        recordType,
        summaryValue,
        metricValue: metricValue ?? records.length,
        filters,
      });

      switch (metricKey) {
        case "activeQuotes":
          return make({
            title: "Recent Active Quotes",
            subtitle: "Recent active quotations. Use Saved Quotes search for all historical quotations.",
            records: dashboardAllQuotes,
            filters: ["Recent active quotations"],
          });
        case "quotesThisMonth":
          return make({
            title: `Quotes ${dashboardPeriodLabel}`,
            subtitle: "Quotation count and value from monthly analytics summaries.",
            records: periodQuotes,
            summaryValue: dashboardSummaryAggregate.quotationRevenue,
            metricValue: dashboardSummaryAggregate.quotationCount,
            filters: [dashboardPeriodLabel, "Created date"],
          });
        case "confirmedThisMonth":
          return make({
            title: `Confirmed ${dashboardPeriodLabel}`,
            subtitle: "Confirmed quotations from monthly analytics summaries.",
            records: periodConfirmed,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: dashboardSummaryAggregate.confirmedCount,
            filters: [dashboardPeriodLabel, "Confirmed date"],
          });
        case "revenueThisMonth":
          return make({
            title: `Revenue ${dashboardPeriodLabel}`,
            subtitle: "Confirmed revenue from monthly analytics summaries.",
            records: revenueQuotes,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: dashboardSummaryAggregate.confirmedCount,
            filters: [dashboardPeriodLabel, "Revenue eligible"],
          });
        case "draftQuotes":
          return make({
            title: "Draft Quotes",
            subtitle: "Active quotations that are still in draft.",
            records: draftQuotes,
            filters: ["Draft", "Not cancelled"],
          });
        case "openPipeline":
          return make({
            title: "Open Pipeline",
            subtitle: "CRM deals that are not cancelled, lost, installed, completed, or closed.",
            records: crmOpenDeals,
            recordType: "deals",
            summaryValue: crmOpenDeals.reduce(
              (sum, deal) => sum + Number(deal.expectedValue || 0),
              0
            ),
            filters: ["Open CRM deals"],
          });
        case "upcomingInstallations":
          return make({
            title: "Upcoming Installations",
            subtitle: "Scheduled installations from today onwards.",
            records: upcomingInstallationQuotes,
            recordType: "installations",
            filters: ["Installation date from today"],
          });
        case "createdQuoteConversion":
          return make({
            title: "Created Quote Conversion",
            subtitle: `${dashboardSummaryAggregate.confirmedCount} of ${dashboardSummaryAggregate.quotationCount} quotes in this period are confirmed.`,
            records: periodQuotes,
            metricValue: conversionRate,
            filters: [dashboardPeriodLabel, "Created quotes with confirmed subset"],
          });
        case "averageQuoteValue":
          return make({
            title: "Average Quote Value",
            subtitle: "Average quote value from monthly analytics summaries.",
            records: periodQuotes,
            summaryValue: dashboardSummaryAggregate.quotationRevenue,
            metricValue: avgQuoteValue,
            filters: [dashboardPeriodLabel, "Created quotes"],
          });
        case "averageConfirmedValue":
          return make({
            title: "Average Confirmed Value",
            subtitle: "Average confirmed value from monthly analytics summaries.",
            records: periodConfirmed,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: avgConfirmedValue,
            filters: [dashboardPeriodLabel, "Confirmed quotes"],
          });
        case "quoteType":
          return make({
            title: `${getQuoteTypeLabel(quoteType)} Quotes`,
            subtitle: "Quotations included in this quote type split.",
            records: dashboardAllQuotes.filter(
              (quote) => quote.quoteTypeResolved === quoteType
            ),
            filters: [getQuoteTypeLabel(quoteType)],
          });
        case "stage":
          return make({
            title: context.title || "Stage Distribution",
            subtitle: "Quotations included in this stage distribution bar.",
            records: dashboardAllQuotes.filter(stageMatches),
            filters: [context.stageLabel || "Selected stage"],
          });
        case "monthCreated":
          return make({
            title: `Created Quotes in ${context.monthLabel || "Selected Month"}`,
            subtitle: "Quotations created in the selected trend month.",
            records: dashboardAllQuotes.filter((quote) => monthMatches(quote.createdMillis)),
            filters: [context.monthLabel || monthKey, "Created date"],
          });
        case "monthRevenue":
          const summaryForMonth = dashboardAnalyticsState.monthlySummaries?.[monthKey] || {};
          return make({
            title: `Revenue in ${context.monthLabel || "Selected Month"}`,
            subtitle: "Confirmed revenue from monthly analytics summaries.",
            records: dashboardAllQuotes.filter(
              (quote) =>
                quote.isConfirmed &&
                quote.isRevenueEligible &&
                monthMatches(quote.confirmedMillis)
            ),
            summaryValue: Number(summaryForMonth.confirmedRevenue || 0),
            metricValue: Number(summaryForMonth.confirmedCount || 0),
            filters: [context.monthLabel || monthKey, "Confirmed revenue"],
          });
        case "installationDate":
          return make({
            title: `Installations on ${dateKey || "Selected Date"}`,
            subtitle: "Installation records scheduled for the selected calendar date.",
            records: installationByDate[dateKey] || [],
            recordType: "installations",
            filters: [dateKey],
          });
        case "topCustomer":
          return make({
            title: context.name || "Top Customer",
            subtitle: "Quotations contributing to this customer total.",
            records: context.records || [],
            recordType: "quotes",
            summaryValue: context.value || 0,
            filters: ["Top customer"],
          });
        default:
          return make({
            title: "Dashboard Records",
            records: [],
          });
      }
    },
    [
      avgConfirmedValue,
      avgQuoteValue,
      conversionRate,
      crmOpenDeals,
      crmPipelines,
      dashboardAnalyticsState.monthlySummaries,
      dashboardAllQuotes,
      dashboardPeriodLabel,
      dashboardSummaryAggregate,
      draftQuotes,
      installationByDate,
      periodConfirmed,
      periodQuotes,
      upcomingInstallationQuotes,
    ]
  );

  const dashboardMetricData = useMemo(
    () => ({
      activeQuotes: getDashboardMetricRecords("activeQuotes"),
      quotesThisMonth: getDashboardMetricRecords("quotesThisMonth"),
      confirmedThisMonth: getDashboardMetricRecords("confirmedThisMonth"),
      revenueThisMonth: getDashboardMetricRecords("revenueThisMonth"),
      draftQuotes: getDashboardMetricRecords("draftQuotes"),
      openPipeline: getDashboardMetricRecords("openPipeline"),
      upcomingInstallations: getDashboardMetricRecords("upcomingInstallations"),
      createdQuoteConversion: getDashboardMetricRecords("createdQuoteConversion"),
      averageQuoteValue: getDashboardMetricRecords("averageQuoteValue"),
      averageConfirmedValue: getDashboardMetricRecords("averageConfirmedValue"),
    }),
    [getDashboardMetricRecords]
  );

  const openBusinessDashboardDrilldown = useCallback(
    (key, context = {}) => {
      const nextDrilldown = getDashboardMetricRecords(key, context);
      if (!nextDrilldown) return;
      setBusinessDashboardDrilldownKey("");
      setDashboardDrilldownModal({ key, context });
      setDashboardDrilldownSearch("");
    },
    [getDashboardMetricRecords]
  );

  const clearBusinessDashboardDrilldown = useCallback(() => {
    setBusinessDashboardDrilldownKey("");
  }, []);

  const dashboardKpis = [
    {
      key: "created",
      label: `Quotes ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.quotesThisMonth.metricValue,
      accent: "#2563eb",
      note: "Created in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("quotesThisMonth"),
    },
    {
      key: "confirmed",
      label: `Confirmed ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.confirmedThisMonth.metricValue,
      accent: "#10b981",
      note: "Finalized in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("confirmedThisMonth"),
    },
    {
      key: "revenue",
      label: `Revenue ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.revenueThisMonth.summaryValue || 0, currency),
      accent: "#0ea5e9",
      note: "Confirmed revenue in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("revenueThisMonth"),
    },
    {
      key: "drafts",
      label: "Draft Quotes",
      value: dashboardMetricData.draftQuotes.metricValue,
      accent: "#f59e0b",
      note: "Active quotes still in draft",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("draftQuotes"),
    },
    {
      key: "pipeline",
      label: "Open Pipeline",
      value: dashboardMetricData.openPipeline.metricValue,
      accent: "#6366f1",
      note: "Live jobs not yet installed",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("openPipeline"),
    },
    {
      key: "upcoming",
      label: "Upcoming Installations",
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.upcomingInstallations.metricValue,
      accent: "#ec4899",
      note: "Scheduled from today onwards",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("upcomingInstallations"),
    },
  ];

  const dashboardInsights = [
    {
      key: "conversion",
      label: "Created Quote Conversion",
      value: dashboardAnalyticsNotReady
        ? "0%"
        : `${dashboardMetricData.createdQuoteConversion.metricValue}%`,
      description: "Quotes created in this period that are now confirmed",
      onClick: () => openBusinessDashboardDrilldown("createdQuoteConversion"),
    },
    {
      key: "averageQuote",
      label: "Average Quote Value",
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.averageQuoteValue.metricValue || 0, currency),
      description: "Across created quotes in selected period",
      onClick: () => openBusinessDashboardDrilldown("averageQuoteValue"),
    },
    {
      key: "averageConfirmed",
      label: "Average Confirmed Value",
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.averageConfirmedValue.metricValue || 0, currency),
      description: "Across confirmed quotes in selected period",
      onClick: () => openBusinessDashboardDrilldown("averageConfirmedValue"),
    },
  ];

  const dashboardTooltipStyle = {
    backgroundColor: isDark ? "#0f172a" : "#ffffff",
    border: dashboardBorder,
    borderRadius: 12,
    color: isDark ? "#e2e8f0" : "#0f172a",
    maxWidth: isMobile ? 280 : 360,
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
  };
  const chartShellStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    height: 220,
    overflow: "hidden",
    position: "relative",
    boxSizing: "border-box",
  };
  const getChartShellStyle = (height) => ({
    ...chartShellStyle,
    height,
  });
  const renderChartEmptyState = (
    title = "No data yet",
    subtitle = "Data will appear as quotations and CRM activity are added."
  ) => (
    <div
      style={{
        height: "100%",
        minHeight: 140,
        display: "grid",
        placeItems: "center",
        borderRadius: 18,
        background: dashboardSoftBg,
        border: dashboardBorder,
        color: dashboardMuted,
        fontSize: 13,
        fontWeight: 700,
        textAlign: "center",
        padding: 18,
      }}
    >
      <div>
        <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
          {title}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            lineHeight: 1.5,
            color: dashboardMuted,
            fontWeight: 650,
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
  const chartAxisTickStyle = {
    fontSize: isMobile ? 9.5 : 11,
    fontWeight: 700,
  };
  const chartMargin = isMobile
    ? { top: 10, right: 8, left: -18, bottom: 10 }
    : { top: 10, right: 12, left: -10, bottom: 8 };
  const quoteTrendChartHeight = isMobile ? 190 : isCompact ? 205 : 220;
  const revenueChartHeight = isMobile ? 188 : isCompact ? 202 : 214;
  const buildChartData = (rows = [], numericKeys = []) =>
    (Array.isArray(rows) ? rows : []).map((entry) => {
      const next = { ...entry };
      numericKeys.forEach((key) => {
        next[key] = Number.isFinite(Number(entry?.[key]))
          ? Number(entry[key])
          : 0;
      });
      return next;
    });
  const monthlyChartData = useMemo(
    () => buildChartData(monthlyTimeSeries, ["created", "confirmed", "revenue"]),
    [monthlyTimeSeries]
  );
  const stageDistributionChartData = useMemo(
    () => buildChartData(stageDistribution, ["value"]),
    [stageDistribution]
  );
  const crmStageOverviewChartData = useMemo(
    () => buildChartData(crmStageOverview, ["count", "value"]),
    [crmStageOverview]
  );
  const quoteTypeDistributionChartData = useMemo(
    () => buildChartData(quoteTypeDistribution, ["value"]),
    [quoteTypeDistribution]
  );
  const crmChecklistChartData = useMemo(
    () => buildChartData(crmChecklistTrend, ["value"]),
    [crmChecklistTrend]
  );
  const crmPipelineChecklistChartData = useMemo(
    () => buildChartData(crmPipelineChecklistTrend, ["value"]),
    [crmPipelineChecklistTrend]
  );
  const hasQuoteCreationTrendData = monthlyTimeSeries.some(
    (entry) => Number(entry.created || 0) > 0 || Number(entry.confirmed || 0) > 0
  );
  const hasRevenueTrendData = monthlyTimeSeries.some(
    (entry) => Number(entry.revenue || 0) > 0
  );
  const hasQuoteTypeDistributionData = quoteTypeDistribution.some(
    (entry) => Number(entry.value || 0) > 0
  );
  const hasStageDistributionData = stageDistribution.some(
    (entry) => Number(entry.value || 0) > 0
  );
  const hasCrmStageOverviewData = crmStageOverview.some(
    (entry) => Number(entry.count || 0) > 0
  );

  useEffect(() => {
    if (!IS_DEV_UI_ENABLED) return;

    const findLinkedCustomerForDeal = (deal = {}) =>
      customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      ) || null;

    const firstSearchBlobs = crmDealListBaseDeals.slice(0, 3).map((deal) => ({
      id: deal.id || "",
      customer:
        deal.customerLabel ||
        deal.customerName ||
        deal.crmContact?.name ||
        deal.crmTitle ||
        "",
      blob: buildDealSearchBlob(deal, findLinkedCustomerForDeal(deal)),
    }));

    console.groupCollapsed("[FabriQuote diagnostics] Deal List search");
    devLog("total crm_leads loaded:", debugRawCrmLeadCount);
    devLog("total quotations loaded:", debugRawQuotationCount);
    devLog("total normalized deal records:", crmDeals.length);
    devLog("deal list base records:", crmDealListBaseDeals.length);
    devLog("search term:", deferredCrmSearch);
    devLog("rendered count:", visibleCrmDeals.length);
    devLog("final filtered result count:", crmFilteredDeals.length);
    devLog("first 3 normalized search blobs:", firstSearchBlobs);
    console.groupEnd();

    console.groupCollapsed("[FabriQuote diagnostics] Dashboard charts");
    devLog("raw quotations count:", debugRawQuotationCount);
    devLog("normalized quotations count:", dashboardAllQuotes.length);
    devLog("quote creation trend data array:", monthlyChartData);
    devLog("quote type split data array:", quoteTypeDistributionChartData);
    devLog("stage distribution data array:", stageDistributionChartData);
    devLog(
      "revenue trend data array:",
      monthlyChartData.map((entry) => ({
        month: entry.month,
        revenue: Number(entry.revenue || 0),
      }))
    );
    console.groupEnd();

    console.groupCollapsed("[FabriQuote diagnostics] CRM charts");
    devLog("raw crm_leads count:", debugRawCrmLeadCount);
    devLog("normalized leads count:", crmLeadDeals.length);
    devLog("pipeline stage overview data array:", crmStageOverviewChartData);
    devLog("checklist completion data array:", crmChecklistChartData);
    console.groupEnd();
  }, [
    crmChecklistChartData,
    crmDealListBaseDeals,
    crmDeals.length,
    crmFilteredDeals.length,
    crmLeadDeals.length,
    crmStageOverviewChartData,
    customerRecords,
    dashboardAllQuotes.length,
    debugRawCrmLeadCount,
    debugRawQuotationCount,
    deferredCrmSearch,
    monthlyChartData,
    quoteTypeDistributionChartData,
    stageDistributionChartData,
    visibleCrmDeals.length,
  ]);

  const renderInlineLegend = (items = []) => (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: 10,
        fontSize: 12,
        color: dashboardMuted,
        fontWeight: 750,
      }}
    >
      {items.map((item) => (
        <span
          key={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: item.color,
              flex: "0 0 auto",
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
  const renderHorizontalMetricBars = (
    items = [],
    {
      valueKey = "value",
      labelKey = "stageLabel",
      valueLabel = "records",
      emptyTitle = "No data yet",
      maxItems = 10,
      onItemClick = null,
    } = {}
  ) => {
    const validItems = (Array.isArray(items) ? items : [])
      .filter((entry) => Number(entry?.[valueKey] || 0) > 0)
      .slice(0, maxItems);
    const maxValue = Math.max(
      1,
      ...validItems.map((entry) => Number(entry?.[valueKey] || 0))
    );

    if (!validItems.length) {
      return renderChartEmptyState(emptyTitle);
    }

    return (
      <div style={{ display: "grid", gap: 12 }}>
        {validItems.map((entry) => {
          const value = Number(entry?.[valueKey] || 0);
          const widthPercent = Math.max(7, Math.round((value / maxValue) * 100));
          const label =
            entry?.[labelKey] ||
            entry?.label ||
            entry?.fullLabel ||
            entry?.name ||
            "Unlabelled";
          const meta = entry?.quoteType ? getQuoteTypeLabel(entry.quoteType) : "";

          const RowTag = onItemClick ? "button" : "div";

          return (
            <RowTag
              key={entry.id || entry.key || label}
              type={onItemClick ? "button" : undefined}
              onClick={onItemClick ? () => onItemClick(entry) : undefined}
              style={{
                display: "grid",
                gap: 6,
                minWidth: 0,
                border: "none",
                padding: 0,
                background: "transparent",
                color: textColor,
                textAlign: "left",
                cursor: onItemClick ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: textColor,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={entry.fullLabel || label}
                  >
                    {label}
                  </div>
                  {meta ? (
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 10.5,
                        color: dashboardMuted,
                        fontWeight: 750,
                      }}
                    >
                      {meta}
                    </div>
                  ) : null}
                </div>
                <div
                  style={{
                    flex: "0 0 auto",
                    fontSize: 12,
                    fontWeight: 900,
                    color: textColor,
                    whiteSpace: "nowrap",
                  }}
                >
                  {value} {valueLabel}
                </div>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${widthPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: entry.color || "#2563eb",
                  }}
                />
              </div>
            </RowTag>
          );
        })}
      </div>
    );
  };

  const renderQuoteTypeSplit = (items = [], onItemClick = null) => {
    const validItems = (Array.isArray(items) ? items : []).filter(
      (entry) => Number(entry?.value || 0) > 0
    );
    const total = validItems.reduce(
      (sum, entry) => sum + Number(entry.value || 0),
      0
    );

    if (!total) {
      return renderChartEmptyState("No quote type data yet");
    }

    return (
      <div style={{ display: "grid", gap: 14 }}>
        <div
          style={{
            height: 18,
            borderRadius: 999,
            background: dashboardSoftBg,
            border: dashboardBorder,
            overflow: "hidden",
            display: "flex",
          }}
        >
          {validItems.map((entry) => {
            const percent = (Number(entry.value || 0) / total) * 100;
            return (
              <div
                key={entry.name}
                title={`${entry.name}: ${entry.value}`}
                style={{
                  width: `${Math.max(4, percent)}%`,
                  background: entry.color || "#2563eb",
                }}
              />
            );
          })}
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {validItems.map((entry) => {
            const percent = Math.round((Number(entry.value || 0) / total) * 100);
            const RowTag = onItemClick ? "button" : "div";
            return (
              <RowTag
                key={entry.name}
                type={onItemClick ? "button" : undefined}
                onClick={onItemClick ? () => onItemClick(entry) : undefined}
                style={{
                  padding: 12,
                  borderRadius: 15,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  minWidth: 0,
                  color: textColor,
                  textAlign: "left",
                  cursor: onItemClick ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      background: entry.color || "#2563eb",
                      flex: "0 0 auto",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: textColor,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: dashboardMuted,
                        marginTop: 2,
                        fontWeight: 750,
                      }}
                    >
                      {percent}% of quotes
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    flex: "0 0 auto",
                    fontSize: 12,
                    fontWeight: 900,
                    color: textColor,
                    textAlign: "right",
                  }}
                >
                  {entry.value} quote{entry.value === 1 ? "" : "s"}
                </div>
              </RowTag>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChecklistProgress = (
    items = [],
    summary = {},
    emptyTitle = "No checklist data yet"
  ) => {
    const completedFromItems = Number(
      (Array.isArray(items) ? items : []).find((entry) => entry.name === "Complete")
        ?.value || 0
    );
    const totalFromItems = (Array.isArray(items) ? items : []).reduce(
      (sum, entry) => sum + Number(entry?.value || 0),
      0
    );
    const completed = Number(summary?.checklistCompleted ?? completedFromItems);
    const total = Number(summary?.checklistTotal ?? totalFromItems);
    const percent = total ? Math.round((completed / total) * 100) : 0;

    if (!total) {
      return renderChartEmptyState(emptyTitle);
    }

    return (
      <div
        style={{
          display: "grid",
          gap: 14,
          padding: 14,
          borderRadius: 18,
          background: dashboardSoftBg,
          border: dashboardBorder,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            gap: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 34,
                lineHeight: 1,
                letterSpacing: "-0.05em",
                fontWeight: 950,
                color: textColor,
              }}
            >
              {percent}%
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 750,
              }}
            >
              Checklist completion
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontSize: 12,
              color: dashboardMuted,
              fontWeight: 850,
            }}
          >
            <div style={{ color: textColor }}>{completed} complete</div>
            <div>{Math.max(0, total - completed)} remaining</div>
          </div>
        </div>
        <div
          style={{
            height: 12,
            borderRadius: 999,
            background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(3, percent)}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #10b981, #22c55e)",
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 750 }}>
          {completed} / {total} checklist tasks complete.
        </div>
      </div>
    );
  };

  const crmSummaryCards = [
    {
      key: "active",
      label: "Active Deals",
      value: crmSummary.activeDeals,
      note: "Open opportunities across CRM pipelines",
      accent: "#2563eb",
    },
    {
      key: "new",
      label: `New Deals ${dashboardPeriodLabel}`,
      value: crmSummary.newDeals,
      note: "Created in the selected period",
      accent: "#0ea5e9",
    },
    {
      key: "pipelineValue",
      label: "Pipeline Value",
      value: formatCurrencyDisplay(crmSummary.pipelineValue, currency),
      note: "Expected value of all active deals",
      accent: "#6366f1",
    },
    {
      key: "overdue",
      label: "Overdue Follow-ups",
      value: crmSummary.overdueFollowUps,
      note: "Deals with a missed next action",
      accent: "#f97316",
    },
    {
      key: "stalled",
      label: "Stalled Deals",
      value: crmSummary.stalledDeals,
      note: "No meaningful update in the last 10 days",
      accent: "#ef4444",
    },
    {
      key: "installations",
      label: "Upcoming Installations",
      value: upcomingInstallationQuotes.length,
      note: "Scheduled jobs coming up next",
      accent: "#10b981",
    },
  ];

  const crmNavItems = [
    { key: "overview", label: "Today", icon: LayoutGrid },
    canViewAllCrmRecords ? { key: "board", label: "Leads / Pipeline", icon: Columns3 } : null,
    canViewAllCrmRecords ? { key: "leads", label: "Leads", icon: Users } : null,
    canViewAllCrmRecords ? { key: "list", label: "Deal List", icon: Users } : null,
    crmSettings.enableOrderHandover === false || !canViewAllCrmRecords
      ? null
      : { key: "projects", label: "Orders", icon: FileArchive },
    crmSettings.enableAttentionRequired === false || !canViewAttentionRequiredWorkspace
      ? null
      : { key: "attention", label: "Attention Required", icon: BellRing },
    crmSettings.enablePipelineReview === false || !canViewPipelineReview
      ? null
      : { key: CRM_PIPELINE_REVIEW_TAB, label: "Pipeline Review", icon: CalendarDays },
    { key: "history", label: "Task History", icon: CheckCircle2 },
    canManageCrmSettings ? { key: "settings", label: "Settings", icon: Settings2 } : null,
  ].filter(Boolean);

  const openCrmDrilldown = useCallback((key) => {
    if (!crmOverviewDrilldowns[key]) return;
    setCrmDrilldownKey(key);
    setCrmSearch("");
    setCrmStageFilter("all");
    setCrmTypeFilter("all");
    setCrmSourceFilter("all");
    setCrmSortBy("newest");
    setCrmView("list");
  }, [crmOverviewDrilldowns]);

  const clearCrmDrilldown = useCallback(() => {
    setCrmDrilldownKey("");
  }, []);

  const buildExecutionSpecsFromQuote = useCallback((quoteLike) => {
    if (!quoteLike) return [];

    const quoteType = resolveQuoteType(quoteLike);

    if (quoteType === GENERAL_QUOTE_TYPE) {
      const generalQuote = normalizeGeneralQuote(getEmbeddedGeneralQuote(quoteLike));

      return (generalQuote.lineItems || []).map((item, index) =>
        {
          const itemSpecifications = normalizeGeneralQuoteItemSpecifications(item);
          const specificationAttributes = itemSpecifications.reduce(
            (accumulator, specification) => {
              const nextKey = String(specification?.key ?? "").trim();
              if (!nextKey) return accumulator;
              accumulator[nextKey] = specification?.value ?? "";
              return accumulator;
            },
            {}
          );

          return createExecutionSpecItem({
            itemType:
              item.specItemType ||
              item.category ||
              item.specTemplateName ||
              "General Item",
            itemName:
              item.itemName ||
              item.name ||
              `Item ${index + 1}`,
            attributes: pickExecutionSpecAttributes({
              ...(item.specTemplateName
                ? { Template: item.specTemplateName }
                : {}),
              ...(Object.keys(specificationAttributes).length > 0
                ? specificationAttributes
                : {
                    Category: item.category,
                    Description: item.description,
                    Quantity: item.quantity,
                    Unit: item.unit,
                  }),
              ...(item.description ? { Description: item.description } : {}),
            }),
            attachments: normalizeExecutionSpecAttachments(
              normalizeGeneralQuoteItemImages(item).map((image) => ({
                name: image.filename || item.itemName || item.name || "Reference Image",
                url: getGeneralQuoteImageDisplayUrl(image),
                path: getGeneralQuoteImageStoragePath(image),
                uploadedAt: image.uploadedAt || null,
                uploadedBy: "",
              }))
            ),
          });
        }
      );
    }

    return (Array.isArray(quoteLike.rows) ? quoteLike.rows : []).map((row, index) =>
      createExecutionSpecItem({
        itemType: row.productType || "Curtains",
        itemName: row.item || `Item ${index + 1}`,
        attributes: pickExecutionSpecAttributes({
          "Product Type": row.productType,
          Fabric: row.fabric,
          "Fabric Width (in)": row.fabricWidthIn,
          Width: row.width,
          Height: row.height,
          "MRP / m": row.mrp,
          "Discount %": row.discount,
          "Rate / sqft": row.sqftRate,
          "Stitching Style": row.stitchingStyle,
          "Custom Stitch Rate": row.customStitchRate,
        }),
      })
    );
  }, [pickExecutionSpecAttributes]);

  const getGeneralQuotePdfStateSignature = useCallback(() => {
    const currentQuote = generalQuoteRef.current || generalQuote || {};
    return JSON.stringify({
      quoteId: generalEditingQuoteId || "",
      quoteNo: currentQuote.quoteNo || quoteNo || "",
      customerName:
        currentQuote.quoteFor?.name ||
        currentQuote.customerName ||
        "",
      itemCount: Array.isArray(currentQuote.lineItems)
        ? currentQuote.lineItems.length
        : 0,
      grandTotal: Number(generalQuoteTotals?.grandTotal || 0),
    });
  }, [generalEditingQuoteId, generalQuote, generalQuoteTotals?.grandTotal, quoteNo]);

  const exportGeneralExecutionPDF = async (event) => {
    preventQuoteActionDefault(event);
    traceQuoteActionClick({
      action: "generalExecutionPdfClick",
      quoteType: GENERAL_QUOTE_TYPE,
      event,
    });
    if (!canUseWorkshopPdf) {
      notify("You do not have permission to export workshop PDFs.", "warning");
      return;
    }

    const beforeSignature = getGeneralQuotePdfStateSignature();
    const liveGeneralActionPayload = buildLiveGeneralQuotePayload({
      action: "executionPdf",
    });
    traceLiveActionPayload(liveGeneralActionPayload);
    guardStaleActionPayload(
      liveGeneralActionPayload,
      liveGeneralActionPayload.summaryTotals
    );
    const currentQuote = liveGeneralActionPayload.quote || generalQuoteRef.current || generalQuote || {};
    const clonedQuote = JSON.parse(JSON.stringify(currentQuote || {}));
    const normalizedQuote = normalizeGeneralQuote(
      {
        ...clonedQuote,
        quoteNo: clonedQuote.quoteNo || quoteNo,
        quoteDate: clonedQuote.quoteDate || date,
      },
      {
        businessName,
        businessPhone,
        businessEmail,
        shopAddress,
        currency: clonedQuote.currency || currency,
      }
    );
    const quoteId = generalEditingQuoteId || selectedQuote?.id || "";
    const customerNameValue =
      normalizedQuote.quoteFor?.name ||
      normalizedQuote.customerName ||
      "Unnamed Customer";
    const quoteLike = {
      id: quoteId,
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
      customerName: customerNameValue,
      customerPhone:
        normalizedQuote.quoteFor?.phone ||
        normalizedQuote.customerPhone ||
        "",
      customerWhatsapp:
        normalizedQuote.quoteFor?.whatsapp ||
        normalizedQuote.customerWhatsapp ||
        "",
      generalQuote: normalizedQuote,
      executionSpecs: [],
    };
    const executionItems = buildExecutionSpecsFromQuote(quoteLike);

    if (executionItems.length === 0) {
      notify("Add at least one item before downloading the execution PDF.", "warning");
      return;
    }

    await downloadExecutionWorkshopPdf(null, {
      sourceDeal: {
        id: quoteId || normalizedQuote.quoteNo || "general-quote-draft",
        quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
        customerName: customerNameValue,
        customerLabel: customerNameValue,
        customerPhone: quoteLike.customerPhone || quoteLike.customerWhatsapp || "",
        crmTitle: normalizedQuote.title || normalizedQuote.projectName || "General Quote",
        status: "quotation",
        executionSpecs: executionItems,
        linkedQuotes: [
          {
            quoteId,
            quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
            type: GENERAL_QUOTE_TYPE,
          },
        ],
      },
      sourceDraft: {
        quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
        contact: {
          name: customerNameValue,
          phone: quoteLike.customerPhone || quoteLike.customerWhatsapp || "",
          whatsapp: quoteLike.customerWhatsapp || "",
          email: normalizedQuote.quoteFor?.email || normalizedQuote.customerEmail || "",
          address: normalizedQuote.quoteFor?.address || normalizedQuote.customerAddress || "",
        },
        executionSpecs: executionItems,
        notes: normalizedQuote.notes || "",
      },
      executionItems,
      source: "general_quote_screen",
    });

    const afterSignature = getGeneralQuotePdfStateSignature();
    if (beforeSignature !== afterSignature) {
      devError("PDF_GENERATION_MUTATED_QUOTE_STATE", {
        beforeSignature,
        afterSignature,
        quoteId,
        quoteNo: normalizedQuote.quoteNo || quoteNo || "",
      });
    }
  };

  const addCrmExecutionSpec = useCallback(() => {
    const nextItem = buildExecutionSpecDraftItem(createExecutionSpecItem());
    setSelectedExecutionItemId(nextItem.id);
    updateCrmDetailDraft((prev) =>
      prev
        ? {
            ...prev,
            executionSpecs: [...(prev.executionSpecs || []), nextItem],
          }
        : prev
    );
  }, [buildExecutionSpecDraftItem, updateCrmDetailDraft]);

  const updateCrmExecutionSpec = useCallback((itemId, updater) => {
    updateCrmDetailDraft((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        executionSpecs: (prev.executionSpecs || []).map((item) => {
          if (item.id !== itemId) return item;
          return typeof updater === "function" ? updater(item) : { ...item, ...updater };
        }),
      };
    });
  }, [updateCrmDetailDraft]);

  const deleteCrmExecutionSpec = useCallback((itemId) => {
    updateCrmDetailDraft((prev) => {
      if (!prev) return prev;

      const nextItems = (prev.executionSpecs || []).filter((item) => item.id !== itemId);
      if (selectedExecutionItemId === itemId) {
        setSelectedExecutionItemId(nextItems[0]?.id || "");
      }

      return {
        ...prev,
        executionSpecs: nextItems,
      };
    });
  }, [selectedExecutionItemId, updateCrmDetailDraft]);

  const syncCrmExecutionSpecType = useCallback(
    (itemId, nextType) => {
      const normalizedType = formatExecutionItemType(nextType);
      const matchingTemplate = getExecutionTemplateForType(normalizedType);

      updateCrmExecutionSpec(itemId, (item) => {
        const nextItem = {
          ...item,
          itemType: normalizedType,
        };

        return matchingTemplate
          ? mergeExecutionTemplateFieldsIntoItem(nextItem, matchingTemplate.fields)
          : nextItem;
      });
    },
      [
        formatExecutionItemType,
        getExecutionTemplateForType,
        mergeExecutionTemplateFieldsIntoItem,
        updateCrmExecutionSpec,
      ]
    );

  const addCrmExecutionSpecField = useCallback((itemId) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: [
        ...(Array.isArray(item.attributeRows) ? item.attributeRows : []),
        {
          id: createClientId("exec-field"),
          key: "New Field",
          value: "",
          type: "text",
        },
      ],
    }));
  }, [updateCrmExecutionSpec]);

  const updateCrmExecutionSpecField = useCallback((itemId, fieldId, patch) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: (Array.isArray(item.attributeRows) ? item.attributeRows : []).map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...patch,
            }
          : field
      ),
    }));
  }, [updateCrmExecutionSpec]);

  const removeCrmExecutionSpecField = useCallback((itemId, fieldId) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: (Array.isArray(item.attributeRows) ? item.attributeRows : []).filter(
        (field) => field.id !== fieldId
      ),
    }));
  }, [updateCrmExecutionSpec]);

  const applyExecutionTemplateToSpec = useCallback(
    (itemId, templateId) => {
      const template = getExecutionTemplateById(templateId);
      if (!template) return;

      updateCrmExecutionSpec(itemId, (item) =>
        mergeExecutionTemplateFieldsIntoItem(
          {
            ...item,
            itemType: template.itemType || template.name,
          },
          template.fields
        )
      );
      setSelectedExecutionTemplateId(template.id);
    },
    [getExecutionTemplateById, mergeExecutionTemplateFieldsIntoItem, updateCrmExecutionSpec]
  );

  const convertDealQuoteToExecutionSpecs = useCallback(async (deal) => {
    if (!deal?.hasQuote) {
      notify("This lead does not have a linked quotation yet.", "warning");
      return;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(deal.linkedQuotes || []);
    const sourceQuote =
      savedQuotes.find((quote) => quote.id === (primaryLinkedQuote?.quoteId || deal.id)) ||
      savedQuotes.find((quote) => String(quote.quoteNo || "") === String(primaryLinkedQuote?.quoteNo || "")) ||
      null;

    if (!sourceQuote) {
      notify("The linked quotation could not be found.", "error");
      return;
    }

    const generatedSpecs = buildExecutionSpecsFromQuote(sourceQuote);
    if (generatedSpecs.length === 0) {
      notify("No quotation items were available to convert.", "warning");
      return;
    }

    const canonicalDeal =
      deal?.crmRecordType === "lead"
        ? deal
        : await ensureCrmLeadForExecutionSpecs(deal);
    const targetDealId = canonicalDeal?.id || deal.id;
    const existingSpecs = serializeExecutionSpecDraftItems(
      crmDetailDraft?.executionSpecs ||
        canonicalDeal?.executionSpecs ||
        deal.executionSpecs ||
        []
    );
    const existingKeys = new Set(
      existingSpecs.map((item) =>
        `${String(item.itemType || "").trim().toLowerCase()}::${String(item.itemName || "").trim().toLowerCase()}`
      )
    );
    const mergedSpecs = stampExecutionSpecs([
      ...existingSpecs,
      ...generatedSpecs.filter((item) => {
        const key = `${String(item.itemType || "").trim().toLowerCase()}::${String(
          item.itemName || ""
        ).trim().toLowerCase()}`;
        return !existingKeys.has(key);
      }),
    ]);

    updateCrmDetailDraft((prev) =>
      prev
        ? {
            ...prev,
            executionSpecs: hydrateExecutionSpecDraftItems(mergedSpecs),
          }
        : prev
    );

    await saveCrmFieldsForDeal(
      targetDealId,
      {
        executionSpecs: mergedSpecs,
      },
      {
        forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id,
        sourceLead:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id
            ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
            : null,
        createIfMissing: canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id,
      }
    );

    if (
      workspaceUid &&
      sourceQuote?.id &&
      resolveQuoteType(sourceQuote) === GENERAL_QUOTE_TYPE
    ) {
      const lockedGeneralQuote = normalizeGeneralQuote(
        getEmbeddedGeneralQuote(sourceQuote),
        {
          currency,
          businessName,
          shopAddress,
          businessEmail,
        }
      );

      const lockedLineItems = (lockedGeneralQuote.lineItems || []).map((item) => ({
        ...item,
        isExecutionLocked:
          item.isExecutionLocked === true ||
          normalizeGeneralQuoteItemSpecifications(item).length > 0 ||
          Boolean(item.specTemplateId || item.specTemplateName),
      }));

      const lockedContentUpdatedAt = new Date().toISOString();
      const nextLockedQuote = {
        ...lockedGeneralQuote,
        lineItems: lockedLineItems,
        items: lockedLineItems,
        rows: lockedLineItems,
        generalRows: lockedLineItems,
        quoteRows: lockedLineItems,
        generalItems: lockedLineItems,
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
        contentUpdatedAt: lockedContentUpdatedAt,
      };
      const lockedItemsForFirestore = sanitizeCrmFirestoreValue(lockedLineItems);

      await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
        ...buildGeneralQuoteLegacyAliasDeletePatch(),
        items: lockedItemsForFirestore,
        itemCount: lockedItemsForFirestore.length,
        contentItemCount: lockedItemsForFirestore.length,
        contentSchemaVersion: QUOTE_CONTENT_SCHEMA_VERSION,
        canonicalContentField: GENERAL_QUOTE_CANONICAL_CONTENT_FIELD,
        contentUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      applyQuoteLocalPatch(sourceQuote.id, {
        generalQuote: nextLockedQuote,
        updatedAt: new Date(),
      });

      clearLocalDraftRecord(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
          sourceQuote.id
        )
      );

      if (generalEditingQuoteId === sourceQuote.id) {
        setGeneralQuote(nextLockedQuote);
        markGeneralQuoteBaseline(nextLockedQuote, "saved");
      }
    }
  }, [
    applyQuoteLocalPatch,
    businessEmail,
    businessName,
    buildExecutionSpecsFromQuote,
    currency,
    crmDetailDraft,
    ensureCrmLeadForExecutionSpecs,
    generalEditingQuoteId,
    hydrateExecutionSpecDraftItems,
    markGeneralQuoteBaseline,
    notify,
    saveCrmFieldsForDeal,
    savedQuotes,
    serializeExecutionSpecDraftItems,
    shopAddress,
    stampExecutionSpecs,
    updateCrmDetailDraft,
    workspaceUid,
  ]);

  const sanitizeChecklistItems = useCallback(
    (items = []) =>
      preserveChecklistDraftTree(items, {
        templateMode: false,
        idPrefix: "crm-check",
      }),
    []
  );

  const updateCrmDetailChecklist = useCallback(
    (updater) => {
      markCrmDetailDraftDirty();
      setCrmDetailDraft((prev) => {
        if (!prev) return prev;
        const nextChecklistItems = sanitizeChecklistItems(
          updater(prev.checklistItems || [])
        );
        return {
          ...prev,
          checklistItems: nextChecklistItems,
          checklist: nextChecklistItems,
          stageChecklist: nextChecklistItems,
        };
      });
    },
    [markCrmDetailDraftDirty, sanitizeChecklistItems]
  );

  const addCrmChecklistItem = useCallback(
    (parentId = "") => {
      const actor = getCurrentTaskHistoryActor();
      const createdAt = new Date();
      const nextItem = parentId
        ? createChecklistChildDraft("", {
            createdAt,
            createdBy: actor,
            done: false,
          })
        : createDealChecklistItem("", {
            createdAt,
            createdBy: actor,
            done: false,
          });
      pendingChecklistFocusIdRef.current = nextItem.id;

      updateCrmDetailChecklist((items) => {
        if (!parentId) {
          return [...(items || []), nextItem];
        }

        return insertChecklistChildNode(
          items || [],
          parentId,
          nextItem
        );
      });
    },
    [getCurrentTaskHistoryActor, updateCrmDetailChecklist]
  );

  const updateCrmChecklistItemField = useCallback(
    (itemId, patch) => {
      updateCrmDetailChecklist((items) =>
        updateChecklistNodeById(items || [], itemId, (item) => {
          const nextPatch = {
            ...patch,
            updatedAt: new Date(),
          };

          if (Object.prototype.hasOwnProperty.call(patch, "label")) {
            nextPatch.title = patch.label;
          }

          return {
            ...item,
            ...nextPatch,
          };
        })
      );
    },
    [updateCrmDetailChecklist]
  );

  const toggleCrmChecklistItem = useCallback(
    (itemId, checked) => {
      const actor = getCurrentTaskHistoryActor();
      updateCrmDetailChecklist((items) => {
        const beforeProgress = getChecklistProgress(items || []);
        const nextItems = toggleChecklistNodeById(items || [], itemId, checked, {
          completedAt: new Date(),
          completedBy: actor,
          updatedAt: new Date(),
        });

        if (isDevRuntime) {
          const toggledItem = flattenChecklistNodes(nextItems).find(
            (entry) => String(entry.id || "") === String(itemId || "")
          );
          const progress = getChecklistProgress(nextItems);
          devLog("CHECKLIST CLICK TRACE", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checkedFromEvent: checked,
            beforeDraftCompletedCount: beforeProgress.completed,
            afterDraftCompletedCount: progress.completed,
            afterDraftChecklistItems: nextItems,
          });
          devLog("CHECKLIST UI TOGGLE", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checked,
            draftChecklist: nextItems,
            draftChecklistItems: nextItems,
            draftStageChecklist: nextItems,
          });
          devLog("CRM checklist toggle", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checked,
            draftCompletedCount: progress.completed,
            draftTotalCount: progress.total,
          });
        }

        return nextItems;
      });
    },
    [getCurrentTaskHistoryActor, selectedCrmDealId, updateCrmDetailChecklist]
  );

  const moveCrmChecklistItem = useCallback(
    (itemId, direction) => {
      updateCrmDetailChecklist((items) =>
        moveChecklistNodeById(items || [], itemId, direction)
      );
    },
    [updateCrmDetailChecklist]
  );

  const deleteCrmChecklistItem = useCallback(
    (itemId) => {
      updateCrmDetailChecklist((items) =>
        deleteChecklistNodeById(items || [], itemId)
      );
    },
    [updateCrmDetailChecklist]
  );

  const updateCrmDealAssignment = useCallback(
    async (quoteId, memberId) => {
      if (!canAssignLeadOwnerAccess) {
        notify("Only managers/admins can assign owners.", "warning");
        return;
      }
      const member = getTeamMemberById(memberId);
      if (memberId && (crmMemberRoster.loading || crmMemberRoster.error ||
          !crmMemberRoster.rows.some(item => (item.id === memberId || item.staffId === memberId) && !item.exclusionReason))) {
        notify("Choose an active member of this workspace.", "warning");
        return;
      }
      if (crmAssignmentPendingRef.current.has(quoteId)) return;
      crmAssignmentPendingRef.current.add(quoteId);
      setCrmAssignmentPending((prev) => ({ ...prev, [quoteId]: true }));
      try {
        const assignmentPatch = buildDealOwnerAssignment(member?.id || "", crmMemberRoster.rows, user?.uid || "");
        const deal = crmDeals.find((item) => item.id === quoteId);
        const lead = deal?.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(deal)
          : crmLeadRecords.find((item) => item.id === quoteId);
        if (!lead?.id) throw new Error("The linked CRM deal could not be found.");
        const savedAssignment = await saveCrmFieldsForDeal(lead.id, assignmentPatch, {
          assignmentOnly: true, forceLead: true, sourceLead: lead, requireWrite: true,
        });
        if (crmWorkspaceRef.current !== workspaceUid) return;
        setCrmDetailDraft((prev) =>
          prev && selectedCrmDeal?.id === quoteId
            ? {
                ...prev,
                ...(savedAssignment?.assignmentPatch || assignmentPatch),
              }
            : prev
        );
        setCrmDetailsSavedAt(new Date());
        notify(memberId ? "Owner saved. Existing task owners are unchanged." : "Assignment cleared. Existing task owners are unchanged.", "success");
      } catch (error) {
        devError("Failed to save CRM assignment:", error);
        notify("Could not save the assignment. Please try again.", "error");
      } finally {
        crmAssignmentPendingRef.current.delete(quoteId);
        setCrmAssignmentPending((prev) => ({ ...prev, [quoteId]: false }));
      }
    },
    [canAssignLeadOwnerAccess, getTeamMemberById, notify, saveCrmFieldsForDeal, user,
      crmDeals, crmLeadRecords, ensureCrmLeadForExecutionSpecs, selectedCrmDeal, crmMemberRoster, workspaceUid]
  );

  const setDealArchiveFromUi = async (deal, archived) => {
    if (!(isCrmOwnerAdminAccess || isSuperAdmin) || !workspaceUid || !deal?.id) {
      notify("Only owners and admins can archive or restore deals.", "warning");
      return;
    }
    if (crmArchiveBusyRef.current) return;
    const reason = archived ? window.prompt("Why are you archiving this deal? Customer history, quotations, orders and payments will be preserved.") : "";
    if (archived && !reason?.trim()) return;
    if (!window.confirm(archived
      ? "Archive this sales deal and pause its open sales tasks? Order, installation and payment work will not be changed."
      : "Restore this deal and eligible sales tasks paused by its archive action?")) return;
    crmArchiveBusyRef.current = true;
    setCrmArchivePage((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const result = await httpsCallable(functions, "setCrmDealArchived")({ workspaceUid, leadId: deal.id, archived, reason: reason || "", confirm: true });
      const data = result.data;
      if (crmWorkspaceRef.current !== workspaceUid) return;
      if (!data?.ok || data.leadId !== deal.id || data.workspaceUid !== workspaceUid) throw new Error("Archive verification failed");
      setCrmLeadRecords((prev) => {
        const updated = { ...deal, archived };
        return prev.some((item) => item.id === deal.id)
          ? prev.map((item) => item.id === deal.id ? { ...item, archived } : item)
          : [updated, ...prev];
      });
      setCrmArchivePage((prev) => ({ ...prev, rows: archived
        ? [{ ...deal, archived: true }, ...prev.rows.filter((item) => item.id !== deal.id)]
        : prev.rows.filter((item) => item.id !== deal.id) }));
      for (const task of data.taskUpdates || []) patchCrmTaskLocalState(task.taskId, { status: task.status });
      removeCrmAttentionOpportunityById(data.opportunityId);
      if (archived) for (const taskId of data.taskIds || []) removeCrmAttentionTaskById(taskId);
      ["crm_leads", "opportunities", "crm_tasks", "activity_log"].forEach((name) => invalidateWorkspaceCollectionCache(workspaceUid, name));
      notify(archived ? "Deal archived. Business history has been kept." : "Deal restored. Refresh Today to review restored work.", "success");
    } catch (error) {
      devError("CRM_DEAL_ARCHIVE_FAILED", { code: error?.code });
      notify("Could not change deal archive state. Check your access and retry; large or inconsistent task histories need admin review.", "error");
    } finally {
      crmArchiveBusyRef.current = false;
      setCrmArchivePage((prev) => ({ ...prev, loading: false }));
    }
  };

  const loadArchivedCrmDeals = async (append = false) => {
    if (!workspaceUid || !(isCrmOwnerAdminAccess || isSuperAdmin) || crmArchiveBusyRef.current) return;
    crmArchiveBusyRef.current = true;
    setCrmArchivePage((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const constraints = [firestoreWhere("archived", "==", true), firestoreOrderBy(firestoreDocumentId()), firestoreLimit(25)];
      if (append && crmArchivePage.cursor) constraints.push(firestoreStartAfter(crmArchivePage.cursor));
      const snap = await getDocs(firestoreQuery(collection(db, "users", workspaceUid, "crm_leads"), ...constraints), { label: "crmArchive.historyPage", ttlMs: 0 });
      const rows = snap.docs.map((item) => ({ ...item.data(), id: item.id }));
      if (crmWorkspaceRef.current !== workspaceUid) return;
      setCrmArchivePage((prev) => ({ rows: append ? Array.from(new Map([...prev.rows, ...rows].map((item) => [item.id, item])).values()) : rows,
        cursor: snap.docs[snap.docs.length - 1] || null, hasMore: snap.size === 25, loaded: true, loading: false, error: "" }));
    } catch {
      setCrmArchivePage((prev) => ({ ...prev, loading: false, error: "Could not load archived deals. Retry." }));
    } finally { crmArchiveBusyRef.current = false; }
  };

  const reassignOpenTasksToDealOwner = async (deal) => {
    if (!workspaceUid || !canAssignLeadOwnerAccess) {
      notify("Only managers/admins can assign owners.", "warning");
      return;
    }
    const member = getTeamMemberById(deal.assignedToUserId);
    if (!member || !isActiveTeamMember(member)) {
      notify("Assign an active owner and save before moving tasks.", "warning");
      return;
    }
    const ownerUid = member.uid || member.id;
    if (crmAssignmentPendingRef.current.has(deal.id)) return;
    crmAssignmentPendingRef.current.add(deal.id);
    setCrmAssignmentPending((prev) => ({ ...prev, [deal.id]: true }));
    try {
      const opportunityId = getCrmOpportunityIdFromLead(deal);
      const snap = await getDocs(firestoreQuery(
        collection(db, "users", workspaceUid, "crm_tasks"),
        firestoreWhere("opportunityId", "==", opportunityId), firestoreLimit(25)
      ), { label: "crmAssignment.previewOpenTasks", ttlMs: 0 });
      if (snap.size === 25) {
        notify("This deal has at least 25 linked tasks. No tasks were changed; review them individually to avoid a partial reassignment.", "warning");
        return;
      }
      const openTasks = snap.docs.filter((item) =>
        ["open", "pending"].includes(item.data().status) && item.data().ownerUid !== ownerUid);
      if (!openTasks.length) {
        notify("No open linked tasks need reassignment.", "info");
        return;
      }
      if (!window.confirm(`Reassign ${openTasks.length} open linked task(s) to ${member.name || "this owner"}? Completed and cancelled tasks will not change.`)) return;
      const result = await httpsCallable(functions, "assignCrmDealOwner")({
        workspaceUid, leadId: deal.id, mode: "transfer_tasks", expectedOwnerUid: ownerUid,
        taskIds: openTasks.map((item) => item.id), confirm: true,
      });
      if (result.data?.ok !== true || !Array.isArray(result.data.taskUpdates)) throw new Error("Task transfer was not confirmed.");
      if (crmWorkspaceRef.current !== workspaceUid) return;
      const taskUpdates = result.data.taskUpdates;
      if (crmWorkspaceRef.current === workspaceUid) taskUpdates.forEach(({ taskId, ...patch }) => patchCrmTaskLocalState(taskId, patch));
      invalidateWorkspaceCollectionCache(workspaceUid, "crm_tasks");
      invalidateWorkspaceCollectionCache(workspaceUid, "activity_log");
      notify(`${taskUpdates.length} open task(s) reassigned.`, "success");
    } catch (error) {
      devError("CRM_TASK_OWNER_REASSIGN_FAILED", { code: error?.code });
      notify("Could not reassign tasks. Refresh the deal owner and try again.", "error");
    } finally {
      crmAssignmentPendingRef.current.delete(deal.id);
      setCrmAssignmentPending((prev) => ({ ...prev, [deal.id]: false }));
    }
  };

  const updateCrmChecklistAssignee = useCallback(
    async (quoteId, itemId, memberId) => {
      const member = getTeamMemberById(memberId);
      let nextChecklistItems = [];
      markCrmDetailDraftDirty();

      setCrmDetailDraft((prev) => {
        if (!prev) return prev;

        nextChecklistItems = sanitizeChecklistItems(
          updateChecklistNodeById(prev.checklistItems || [], itemId, (current) => ({
            ...current,
            assignedToUserId: memberId,
            assignedToNameSnapshot: memberId ? member?.name || "" : "",
            assignedAt: memberId ? new Date() : null,
            updatedAt: new Date(),
          }))
        );

        return {
          ...prev,
          checklistItems: nextChecklistItems,
          checklist: nextChecklistItems,
          stageChecklist: nextChecklistItems,
        };
      });

      if (nextChecklistItems.length > 0) {
        const targetDeal =
          selectedCrmDeal?.id === quoteId && selectedCrmDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal)
            : selectedCrmDeal;
        const targetDealId = targetDeal?.id || quoteId;

        if (targetDealId && targetDealId !== quoteId) {
          setSelectedCrmDealId(targetDealId);
        }

        const shouldSaveAsLead =
          targetDeal?.crmRecordType === "lead" || targetDealId !== quoteId;
        await saveCrmFieldsForDeal(
          targetDealId,
          {
            checklistItems: nextChecklistItems,
          },
          {
            forceLead: shouldSaveAsLead,
            sourceLead: shouldSaveAsLead
              ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
              : null,
            createIfMissing: shouldSaveAsLead,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Checklist updated", "success");
      }
    },
    [
      ensureCrmLeadForExecutionSpecs,
      getTeamMemberById,
      markCrmDetailDraftDirty,
      notify,
      sanitizeChecklistItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
    ]
  );

  const buildCanonicalCrmDetailsPayloadFromDraft = useCallback(
    (draft = {}, currentUser = {}) => {
      const installationDateInput = String(draft.installationDate || "").trim();
      const parsedInstallationDate = parseDateInputValue(installationDateInput);
      const installationDateKey =
        toDateInputValue(parsedInstallationDate) || installationDateInput;

      if (
        installationDateInput &&
        (!parsedInstallationDate ||
          toDateInputValue(parsedInstallationDate) !== installationDateInput)
      ) {
        throw new Error("Please select a valid installation date.");
      }

      const checklistItems = withCanonicalChecklistAliases(
        draft.checklistItems || draft.checklist || draft.stageChecklist || []
      );
      const draftReminderConfigs = normalizeCrmReminderConfigs(draft.reminderConfigs, {
        nextActionDate: parseDateInputValue(draft.nextActionDate),
        installationDate: parsedInstallationDate || installationDateKey || null,
      });
      const rawInstallationReminder =
        draft.installationReminder ||
        draft.reminderConfig ||
        draftReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] ||
        {};
      const enabled =
        rawInstallationReminder.enabled === true ||
        rawInstallationReminder.reminderEnabled === true;
      const reminderDate = String(
        rawInstallationReminder.reminderDate ||
          rawInstallationReminder.date ||
          installationDateKey ||
          ""
      ).trim();
      const reminderTime = parseTimeInputValue(
        rawInstallationReminder.reminderTime ||
          rawInstallationReminder.time ||
          DEFAULT_CRM_REMINDER_TIME
      );
      const repeat = String(
        rawInstallationReminder.repeat ||
          rawInstallationReminder.repeatKey ||
          "daily"
      ).trim() || "daily";
      const nextNotifyAtDate =
        enabled && reminderDate
          ? combineReminderDateTime(reminderDate, reminderTime)
          : null;
      const actorUid = String(currentUser?.uid || auth.currentUser?.uid || "").trim();
      const actorEmail = String(
        currentUser?.email || auth.currentUser?.email || ""
      ).trim();
      const updatedAtIso = new Date().toISOString();
      const installationReminder = {
        enabled,
        reminderEnabled: enabled,
        reminderDate,
        reminderTime,
        repeat,
        repeatKey: repeat,
        status: enabled ? "pending" : CRM_REMINDER_STATUS_DISABLED,
        nextNotifyAt: nextNotifyAtDate ? nextNotifyAtDate.toISOString() : null,
        updatedAt: updatedAtIso,
        updatedBy: actorUid,
        updatedByEmail: actorEmail,
      };
      const reminderConfigs = {
        ...draftReminderConfigs,
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          ...(draftReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] || {}),
          ...installationReminder,
        },
      };
      const parsedTags = Array.isArray(draft.tags)
        ? draft.tags
        : String(draft.tags || "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);
      const followUps = normalizeCrmFollowUps(draft.followUps || [], {
        nextAction: draft.nextAction || "",
        nextActionDate: parseDateInputValue(draft.nextActionDate),
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      });

      return sanitizeCrmFirestoreValue({
        title: draft.title || "",
        contact: draft.contact || {},
        source: draft.source || draft.contact?.source || "",
        stage: draft.stage || draft.stageId || "",
        stageId: draft.stageId || draft.stage || "",
        status: draft.status || "draft",
        pipelineType: draft.pipelineType || CURTAIN_QUOTE_TYPE,
        quoteType: draft.pipelineType || CURTAIN_QUOTE_TYPE,
        installationDate: installationDateKey || "",
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs,
        checklistItems,
        checklist: checklistItems,
        stageChecklist: checklistItems,
        followUps,
        notes: draft.notes || "",
        internalNotes: draft.internalNotes || draft.notes || "",
        tags: parsedTags,
        assignedToUserId: draft.assignedToUserId || "",
        assignedToNameSnapshot: draft.assignedToNameSnapshot || "",
        assignedTo: draft.assignedToNameSnapshot || draft.assignedTo || "",
        assignedToName: draft.assignedToNameSnapshot || draft.assignedTo || "",
        assignedAt: draft.assignedAt || null,
        assignedBy: draft.assignedBy || "",
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
        updatedByEmail: actorEmail,
        ...(isDevRuntime
          ? {
              _lastWriteSource: "saveCrmDetailsCanonical",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
      });
    },
    [combineReminderDateTime]
  );

  const verifyCanonicalCrmDetailsSnapshot = useCallback(
    (rawData = {}, expectedPayload = {}) => {
      const expectedChecklistProgress = getChecklistProgress(
        expectedPayload.checklistItems || []
      );
      const savedChecklistItems = withCanonicalChecklistAliases(
        rawData.checklistItems || rawData.checklist || rawData.stageChecklist || []
      );
      const savedChecklistProgress = getChecklistProgress(savedChecklistItems);
      const expectedInstallationDate = String(
        expectedPayload.installationDate || ""
      ).trim();
      const savedInstallationDate =
        toDateInputValue(rawData.installationDate) ||
        String(rawData.installationDate || "").trim();
      const expectedReminder = expectedPayload.installationReminder || {};
      const savedReminder =
        rawData.installationReminder ||
        rawData.reminderConfig ||
        rawData.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
        {};
      const expectedReminderEnabled = expectedReminder.enabled === true;
      const savedReminderEnabled =
        savedReminder.enabled === true || savedReminder.reminderEnabled === true;
      const mismatches = [];

      if (expectedInstallationDate !== savedInstallationDate) {
        mismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== savedChecklistProgress.total ||
        expectedChecklistProgress.completed !== savedChecklistProgress.completed
      ) {
        mismatches.push("checklistItems");
      }
      if (expectedReminderEnabled !== savedReminderEnabled) {
        mismatches.push("installationReminder.enabled");
      }
      if (
        expectedReminderEnabled &&
        String(expectedReminder.reminderDate || "") !==
          String(savedReminder.reminderDate || "")
      ) {
        mismatches.push("installationReminder.reminderDate");
      }
      if (
        expectedReminderEnabled &&
        String(expectedReminder.reminderTime || "") !==
          String(savedReminder.reminderTime || "")
      ) {
        mismatches.push("installationReminder.reminderTime");
      }

      return {
        passed: mismatches.length === 0,
        mismatches,
        expectedChecklistProgress,
        savedChecklistProgress,
        expectedInstallationDate,
        savedInstallationDate,
        expectedReminderEnabled,
        savedReminderEnabled,
        lastWriteSource: rawData._lastWriteSource || "",
      };
    },
    []
  );

  const saveCrmDetailsCanonical = useCallback(
    async ({ workspaceUid: targetWorkspaceUid, selectedCrmDealId: targetLeadId, draft, currentUser }) => {
      if (!targetWorkspaceUid || !targetLeadId) {
        throw new Error("Could not save because no deal is selected.");
      }
      if (!draft) {
        throw new Error("CRM details save failed: no draft was available.");
      }

      const leadRef = doc(db, "users", targetWorkspaceUid, "crm_leads", targetLeadId);
      const payload = buildCanonicalCrmDetailsPayloadFromDraft(draft, currentUser);
      const payloadChecklistProgress = getChecklistProgress(payload.checklistItems || []);

      if (isDevRuntime) {
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastPayload: payload,
          lastImmediateReadback: null,
          lastDelayedReadback: null,
          lastWritePath: `users/${targetWorkspaceUid}/crm_leads/${targetLeadId}`,
          lastVerification: {
            status: "canonical-writing",
            payloadChecklistCompleted: payloadChecklistProgress.completed,
            payloadChecklistTotal: payloadChecklistProgress.total,
            installationDate: payload.installationDate,
            installationReminderEnabled: payload.installationReminder?.enabled === true,
          },
        }));
        devLog("CRM SAVE PAYLOAD FINAL", {
          selectedCrmDealId: targetLeadId,
          path: `users/${targetWorkspaceUid}/crm_leads/${targetLeadId}`,
          checklistItems: payload.checklistItems,
          checklist: payload.checklist,
          stageChecklist: payload.stageChecklist,
          installationDate: payload.installationDate,
          installationReminder: payload.installationReminder,
          reminderConfig: payload.reminderConfig,
          reminderConfigs: payload.reminderConfigs,
          payloadKeys: Object.keys(payload),
        });
        devLog("WRITING CRM DETAILS DOC", leadRef.path, payload);
      }

      await setDoc(leadRef, payload, { merge: true });

      const snap1 = await getDoc(leadRef);
      if (!snap1.exists()) {
        throw new Error("CRM details save verification failed: document missing after write.");
      }
      const data1 = { id: snap1.id, ...snap1.data() };
      const immediateVerification = verifyCanonicalCrmDetailsSnapshot(data1, payload);

      if (isDevRuntime) {
        devLog("CRM SAVE READBACK IMMEDIATE", data1);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastImmediateReadback: data1,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: immediateVerification.passed
              ? "canonical-immediate-ok"
              : "canonical-immediate-mismatch",
            immediateMismatches: immediateVerification.mismatches,
            immediateChecklistCompleted:
              immediateVerification.savedChecklistProgress.completed,
            immediateChecklistTotal: immediateVerification.savedChecklistProgress.total,
            immediateInstallationReminderEnabled:
              immediateVerification.savedReminderEnabled,
            immediateLastWriteSource: immediateVerification.lastWriteSource,
          },
        }));
      }

      await waitFor(2000);

      const snap2 = await getDoc(leadRef);
      if (!snap2.exists()) {
        throw new Error("CRM details save verification failed: document missing after delayed read.");
      }
      const data2 = { id: snap2.id, ...snap2.data() };
      const delayedVerification = verifyCanonicalCrmDetailsSnapshot(data2, payload);

      if (isDevRuntime) {
        devLog("CRM SAVE READBACK DELAYED", data2);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastDelayedReadback: data2,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status:
              immediateVerification.passed && delayedVerification.passed
                ? "canonical-verified"
                : "canonical-mismatch",
            immediateMismatches: immediateVerification.mismatches,
            delayedMismatches: delayedVerification.mismatches,
            delayedChecklistCompleted:
              delayedVerification.savedChecklistProgress.completed,
            delayedChecklistTotal: delayedVerification.savedChecklistProgress.total,
            delayedInstallationReminderEnabled:
              delayedVerification.savedReminderEnabled,
            delayedLastWriteSource: delayedVerification.lastWriteSource,
          },
        }));
      }

      if (!immediateVerification.passed || !delayedVerification.passed) {
        if (isDevRuntime) {
          devError("CRM canonical save verification failed", {
            path: leadRef.path,
            payload,
            immediateVerification,
            delayedVerification,
            immediateReadback: data1,
            delayedReadback: data2,
          });
        }
        throw new Error("CRM details save verification failed.");
      }

      return {
        payload,
        immediateReadback: data1,
        delayedReadback: data2,
        verification: delayedVerification,
      };
    },
    [
      buildCanonicalCrmDetailsPayloadFromDraft,
      verifyCanonicalCrmDetailsSnapshot,
    ]
  );

  const handleSaveCrmDetailsCanonical = useCallback(async () => {
    if (!selectedCrmDealId) {
      notify("Could not save because no deal is selected.", "error");
      return;
    }
    if (!crmDetailDraft) {
      notify("Could not save CRM details. Your changes are still here.", "error");
      return;
    }

    setIsSavingCrmDetails(true);
    try {
      const result = await saveCrmDetailsCanonical({
        workspaceUid,
        selectedCrmDealId,
        draft: crmDetailDraft,
        currentUser: auth.currentUser || user || {},
      });
      const savedAt = new Date();
      const savedRaw = result.delayedReadback || result.immediateReadback || {};
      const savedLead = buildCrmLeadData(savedRaw);
      const savedDraft = {
        ...crmDetailDraft,
        installationDate: String(result.payload.installationDate || ""),
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        followUps: normalizeCrmFollowUps(result.payload.followUps || []).map((followUp) =>
          createCrmFollowUpDraft(followUp)
        ),
      };
      let reminderQueueSyncFailed = false;

      applyCrmLeadLocalPatch(selectedCrmDealId, {
        ...savedLead,
        id: selectedCrmDealId,
        ...savedRaw,
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        installationDate: result.payload.installationDate,
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
        updatedAt: savedAt,
      });
      await upsertCustomerSearchIndexRecord(
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          crmLeadId: selectedCrmDealId,
          leadId: selectedCrmDealId,
          sourceType: "crm_lead",
        },
        { sourceType: "crm_lead" }
      ).catch((error) => {
        if (isDevRuntime) {
          devError("CUSTOMER SEARCH INDEX CRM DETAILS UPSERT FAILED", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      await syncCrmLeadInstallationIndex(
        selectedCrmDealId,
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          installationDate: result.payload.installationDate,
          installationReminder: result.payload.installationReminder,
        },
        { source: "saveCrmDetailsCanonical" }
      ).catch((error) => {
        if (isDevRuntime) {
          devError("CRM installation index sync failed after details save", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      await syncReminderDocsForDeal(
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          crmRecordType: "lead",
          customerLabel:
            savedLead.customerLabel ||
            savedLead.customerName ||
            savedLead.contact?.name ||
            savedLead.title ||
            "Unnamed Lead",
          crmTitle: savedLead.crmTitle || savedLead.title || "",
          crmContact: savedLead.contact || {},
          installationDate: result.payload.installationDate,
          installationReminder: result.payload.installationReminder,
          reminderConfigs: result.payload.reminderConfigs,
          followUps: result.payload.followUps,
        },
        {
          installationDate: result.payload.installationDate,
          reminderConfigs: result.payload.reminderConfigs,
          followUps: result.payload.followUps,
        }
      ).catch((error) => {
        reminderQueueSyncFailed = true;
        if (isDevRuntime) {
          devError("CRM due reminder queue sync failed after details save", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      selectedCrmDealCacheRef.current = {
        ...(selectedCrmDealCacheRef.current || {}),
        ...savedLead,
        ...savedRaw,
        id: selectedCrmDealId,
        crmRecordType: "lead",
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        installationDate: result.payload.installationDate,
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
      };
      selectedCrmDealIdRef.current = String(selectedCrmDealId);
      crmDetailDraftDealIdRef.current = String(selectedCrmDealId);
      crmDetailDraftDirtyRef.current = false;
      setCrmDetailDraft(savedDraft);
      setCrmDetailsSavedAt(savedAt);
      markExecutionDetailsBaseline(savedDraft, "saved");
      clearExecutionDetailsDraft();
      notify(
        reminderQueueSyncFailed
          ? "CRM details saved, but reminder notifications could not be queued."
          : "CRM details saved",
        reminderQueueSyncFailed ? "warning" : "success"
      );
    } catch (error) {
      if (isDevRuntime) {
        devError("Failed to save CRM details canonical pipeline:", error);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: "canonical-failed",
            error: error?.message || String(error),
          },
        }));
      }
      notify(
        String(error?.message || "").includes("verification")
          ? "Save could not be verified. Your changes are still here."
          : "Could not save CRM details. Your changes are still here.",
        "error"
      );
    } finally {
      setIsSavingCrmDetails(false);
    }
  }, [
    applyCrmLeadLocalPatch,
    buildCrmLeadData,
    clearExecutionDetailsDraft,
    createCrmFollowUpDraft,
    crmDetailDraft,
    markExecutionDetailsBaseline,
    notify,
    saveCrmDetailsCanonical,
    selectedCrmDealId,
    syncCrmLeadInstallationIndex,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]);

  const testDirectCrmDetailsSave = useCallback(async () => {
    if (!workspaceUid || !selectedCrmDealId) {
      notify("Could not run test because no deal is selected.", "warning");
      return;
    }

    const leadRef = doc(db, "users", workspaceUid, "crm_leads", selectedCrmDealId);
    const debugPayload = sanitizeCrmFirestoreValue({
      installationDate: "2099-01-01",
      installationReminder: {
        enabled: true,
        reminderEnabled: true,
        reminderDate: "2099-01-01",
        reminderTime: "09:00",
        repeat: "daily",
        repeatKey: "daily",
        status: "pending",
      },
      reminderConfig: {
        enabled: true,
        reminderEnabled: true,
        reminderDate: "2099-01-01",
        reminderTime: "09:00",
        repeat: "daily",
        repeatKey: "daily",
        status: "pending",
      },
      reminderConfigs: {
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          enabled: true,
          reminderEnabled: true,
          reminderDate: "2099-01-01",
          reminderTime: "09:00",
          repeat: "daily",
          repeatKey: "daily",
          status: "pending",
        },
      },
      checklistItems: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      checklist: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      stageChecklist: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || user?.uid || "",
      updatedByEmail: auth.currentUser?.email || user?.email || "",
      ...(isDevRuntime
        ? {
            _lastWriteSource: "debugDirectCrmDetailsSave",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
    });

    try {
      devLog("WRITING CRM DETAILS DOC", leadRef.path, debugPayload);
      await setDoc(leadRef, debugPayload, { merge: true });
      const snap1 = await getDoc(leadRef);
      const data1 = snap1.exists() ? { id: snap1.id, ...snap1.data() } : null;
      await waitFor(2000);
      const snap2 = await getDoc(leadRef);
      const data2 = snap2.exists() ? { id: snap2.id, ...snap2.data() } : null;
      devLog("CRM DIRECT TEST READBACK IMMEDIATE", data1);
      devLog("CRM DIRECT TEST READBACK DELAYED", data2);
      setCrmSaveDebugState((prev) => ({
        ...prev,
        lastPayload: debugPayload,
        lastImmediateReadback: data1,
        lastDelayedReadback: data2,
        lastWritePath: `users/${workspaceUid}/crm_leads/${selectedCrmDealId}`,
        lastVerification: {
          status: "direct-test-complete",
          immediateHasReminder: data1?.installationReminder?.enabled === true,
          delayedHasReminder: data2?.installationReminder?.enabled === true,
          delayedLastWriteSource: data2?._lastWriteSource || "",
        },
      }));
      notify("Direct CRM save test completed. Check CRM Save Debug.", "success");
    } catch (error) {
      devError("Direct CRM details save test failed", error);
      notify("Direct CRM save test failed. Check console.", "error");
    }
  }, [notify, selectedCrmDealId, user, workspaceUid]);

  const persistCrmDetailDraft = async () => {
    const activeCrmDeal = selectedCrmDeal || selectedCrmDealCacheRef.current;
    if (!selectedCrmDealId && !activeCrmDeal?.id) {
      notify("Could not save because no deal is selected.", "error");
      if (isDevRuntime) {
        devError("Save blocked: selectedCrmDealId missing", {
          selectedCrmDeal,
          cachedDeal: selectedCrmDealCacheRef.current,
        });
      }
      return;
    }

    if (!activeCrmDeal || !crmDetailDraft) {
      notify("Could not save CRM details. Your changes are still here.", "error");
      return;
    }

    const beforeSelectedCrmDealId = selectedCrmDealId;
    const beforeSelectedDealName =
      activeCrmDeal.crmTitle ||
      activeCrmDeal.customerLabel ||
      activeCrmDeal.title ||
      "";
    setIsSavingCrmDetails(true);

    try {
      const parsedTags = String(crmDetailDraft.tags || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      const nextActionDate = parseDateInputValue(crmDetailDraft.nextActionDate);
      const installationDateInput = String(crmDetailDraft.installationDate || "").trim();
      const installationDate = parseDateInputValue(crmDetailDraft.installationDate);
      if (
        installationDateInput &&
        (!installationDate || toDateInputValue(installationDate) !== installationDateInput)
      ) {
        notify("Please select a valid installation date", "warning");
        return;
      }
      const reminderConfigs = normalizeCrmReminderConfigs(
        crmDetailDraft.reminderConfigs,
        {
          nextActionDate,
          installationDate,
        }
      );
      const installationReminder = buildCanonicalInstallationReminder(
        {
          ...crmDetailDraft,
          reminderConfigs,
        },
        {
          installationDate,
          reminderConfigs,
        }
      );
      const reminderConfigsForSave = {
        ...reminderConfigs,
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          ...reminderConfigs[CRM_REMINDER_TYPE_INSTALLATION],
          ...installationReminder,
        },
      };
      const followUps = normalizeCrmFollowUps(crmDetailDraft.followUps || [], {
        nextAction: crmDetailDraft.nextAction || "",
        nextActionDate,
        reminderConfig: reminderConfigsForSave[CRM_REMINDER_TYPE_TASK_DUE],
      });
      const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
      const executionSpecs = stampExecutionSpecs(
        serializeExecutionSpecDraftItems(crmDetailDraft.executionSpecs || [])
      );
      const canonicalDeal =
        activeCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(activeCrmDeal, executionSpecs)
          : activeCrmDeal;
      const targetDealId = canonicalDeal?.id || activeCrmDeal.id;
      if (!targetDealId) {
        throw new Error("CRM details save failed: no CRM lead id was available.");
      }
      const targetDealCrm = canonicalDeal?.crm || activeCrmDeal.crm || {};
      const previousChecklistItems = normalizeChecklistTree(
        canonicalDeal?.checklistItems ||
          targetDealCrm?.checklistItems ||
          activeCrmDeal.checklistItems ||
          []
      );
      const nextChecklistItems = sanitizeChecklistItems(
        crmDetailDraft.checklistItems || []
      );
      const sourceLeadForSave = {
        ...canonicalDeal,
        id: targetDealId,
        crmRecordType: "lead",
      };
      const crmDetailsSavePayload = {
        title: crmDetailDraft.title || canonicalDeal?.crmTitle || activeCrmDeal.crmTitle,
        contact: {
          ...(crmDetailDraft.contact || {}),
        },
        source: crmDetailDraft.contact?.source || canonicalDeal?.source || activeCrmDeal.source || "",
        stageId: crmDetailDraft.stageId || canonicalDeal?.crmStageId || canonicalDeal?.stageId || activeCrmDeal.crmStageId || "",
        status: crmDetailDraft.status || canonicalDeal?.status || activeCrmDeal.status || "draft",
        quoteType: canonicalDeal?.pipelineType || activeCrmDeal.pipelineType || activeCrmDeal.quoteTypeResolved || CURTAIN_QUOTE_TYPE,
        nextAction: primaryFollowUp?.title || crmDetailDraft.nextAction || "",
        nextActionDate: primaryFollowUp?.dueDate
          ? parseDateInputValue(primaryFollowUp.dueDate)
          : nextActionDate,
        installationDate,
        notes: crmDetailDraft.notes || "",
        assignedToUserId: crmDetailDraft.assignedToUserId || "",
        assignedToNameSnapshot: crmDetailDraft.assignedToNameSnapshot || "",
        assignedTo: crmDetailDraft.assignedToNameSnapshot || "",
        assignedAt: crmDetailDraft.assignedToUserId
          ? crmDetailDraft.assignedAt || targetDealCrm.assignedAt || new Date()
          : null,
        assignedBy: crmDetailDraft.assignedToUserId
          ? targetDealCrm.assignedBy || user?.email || user?.uid || ""
          : "",
        tags: parsedTags,
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps,
        attachments: normalizeCrmAttachments(crmDetailDraft.attachments || []),
        executionSpecs,
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
        expectedValue: Number(canonicalDeal?.expectedValue || activeCrmDeal.expectedValue || 0),
        finalValue: Number(canonicalDeal?.finalValue || activeCrmDeal.finalValue || 0),
      };
      const draftChecklistProgress = getChecklistProgress(
        crmDetailDraft.checklistItems || []
      );
      const payloadChecklistProgress = getChecklistProgress(
        crmDetailsSavePayload.checklistItems || []
      );

      if (isDevRuntime) {
        if (
          draftChecklistProgress.completed > 0 &&
          payloadChecklistProgress.completed < draftChecklistProgress.completed
        ) {
          throw new Error("CRM save payload lost checklistItems before Firestore write");
        }
        if (
          installationReminder?.enabled === true &&
          crmDetailsSavePayload.installationReminder?.enabled !== true
        ) {
          throw new Error("CRM save payload lost installationReminder before Firestore write");
        }
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastPayload: crmDetailsSavePayload,
          lastImmediateReadback: null,
          lastDelayedReadback: null,
          lastWritePath: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          lastVerification: {
            status: "writing",
            at: new Date().toISOString(),
            draftChecklistCompleted: draftChecklistProgress.completed,
            draftChecklistTotal: draftChecklistProgress.total,
            payloadChecklistCompleted: payloadChecklistProgress.completed,
            payloadChecklistTotal: payloadChecklistProgress.total,
            installationReminderEnabled:
              crmDetailsSavePayload.installationReminder?.enabled === true,
          },
        }));
        devLog("CRM SAVE PAYLOAD FINAL", {
          selectedCrmDealId: targetDealId,
          path: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          checklist: crmDetailsSavePayload.checklist,
          checklistItems: crmDetailsSavePayload.checklistItems,
          stageChecklist: crmDetailsSavePayload.stageChecklist,
          installationDate: crmDetailsSavePayload.installationDate,
          installationReminder: crmDetailsSavePayload.installationReminder,
          reminderConfig: crmDetailsSavePayload.reminderConfig,
          payloadKeys: Object.keys(crmDetailsSavePayload),
        });
        devLog("CRM details save payload audit", {
          workspaceUid,
          selectedCrmDealId,
          selectedCrmDealName: beforeSelectedDealName,
          firestorePath: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          payloadKeys: Object.keys(crmDetailsSavePayload),
          draftChecklistCompleted: draftChecklistProgress.completed,
          draftChecklistTotal: draftChecklistProgress.total,
          payloadChecklistCompleted: payloadChecklistProgress.completed,
          payloadChecklistTotal: payloadChecklistProgress.total,
          installationDate: crmDetailsSavePayload.installationDate,
          followUpsCount: crmDetailsSavePayload.followUps?.length || 0,
          reminderEnabled:
            crmDetailsSavePayload.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION]
              ?.reminderEnabled,
        });
      }

      const saveResult = await saveCrmFieldsForLead(targetDealId, crmDetailsSavePayload, {
        forceLead: true,
        sourceLead: sourceLeadForSave,
        createIfMissing: true,
        requireWrite: true,
        persistFullCrmDetails: true,
        writeSource: "saveCrmDetails",
      });

      if (targetDealId && targetDealId !== activeCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      } else if (targetDealId) {
        setSelectedCrmDealId(targetDealId);
      }
      selectedCrmDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");

      const savedLeadForVerification = saveResult?.savedLead || null;
      if (!savedLeadForVerification) {
        throw new Error("CRM details save verification failed: saved lead was not returned.");
      }

      const expectedChecklistProgress = payloadChecklistProgress;
      const savedChecklistItems = normalizeChecklistTree(
        savedLeadForVerification.checklistItems ||
          savedLeadForVerification.checklist ||
          savedLeadForVerification.stageChecklist ||
          []
      );
      const savedChecklistProgress = getChecklistProgress(savedChecklistItems);
      const expectedInstallationDateKey = toDateInputValue(installationDate);
      const savedInstallationDateKey = toDateInputValue(
        savedLeadForVerification.installationDate
      );
      const expectedInstallationReminder = installationReminder;
      const savedInstallationReminder = buildCanonicalInstallationReminder(
        savedLeadForVerification,
        {
          installationDate: savedLeadForVerification.installationDate,
          reminderConfigs: savedLeadForVerification.reminderConfigs,
        }
      );
      const expectedReminderDateKey = String(expectedInstallationReminder?.reminderDate || "");
      const savedReminderDateKey = String(savedInstallationReminder?.reminderDate || "");
      const expectedReminderRepeat = String(
        expectedInstallationReminder?.repeat || expectedInstallationReminder?.repeatKey || ""
      );
      const savedReminderRepeat = String(
        savedInstallationReminder?.repeat || savedInstallationReminder?.repeatKey || ""
      );
      const savedReminderConfig = normalizeCrmReminderConfigs(
        savedLeadForVerification.reminderConfigs,
        {
          nextActionDate: savedLeadForVerification.nextActionDate,
          installationDate: savedLeadForVerification.installationDate,
        }
      )[CRM_REMINDER_TYPE_INSTALLATION];
      const legacyExpectedReminderConfig = normalizeCrmReminderConfigs(reminderConfigsForSave, {
        nextActionDate,
        installationDate,
      })[CRM_REMINDER_TYPE_INSTALLATION];
      const savedRawInstallationReminder =
        saveResult?.savedLeadRaw?.installationReminder || null;
      const savedFollowUps = normalizeCrmFollowUps(savedLeadForVerification.followUps || []);
      const verificationMismatches = [];

      if (expectedInstallationDateKey !== savedInstallationDateKey) {
        verificationMismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== savedChecklistProgress.total ||
        expectedChecklistProgress.completed !== savedChecklistProgress.completed
      ) {
        verificationMismatches.push("checklistItems");
      }
      if (followUps.length !== savedFollowUps.length) {
        verificationMismatches.push("followUps");
      }
      if (
        expectedInstallationReminder?.enabled !== savedInstallationReminder?.enabled ||
        expectedReminderDateKey !== savedReminderDateKey ||
        expectedInstallationReminder?.reminderTime !== savedInstallationReminder?.reminderTime ||
        expectedReminderRepeat !== savedReminderRepeat ||
        legacyExpectedReminderConfig?.reminderEnabled !== savedReminderConfig?.reminderEnabled ||
        legacyExpectedReminderConfig?.reminderTime !== savedReminderConfig?.reminderTime ||
        legacyExpectedReminderConfig?.repeatKey !== savedReminderConfig?.repeatKey ||
        (expectedInstallationReminder?.enabled === true &&
          savedRawInstallationReminder?.enabled !== true)
      ) {
        verificationMismatches.push("installationReminder");
      }
      if (String(crmDetailsSavePayload.notes || "") !== String(savedLeadForVerification.notes || "")) {
        verificationMismatches.push("notes");
      }

      if (isDevRuntime) {
        const savedLeadRawForDebug = saveResult?.savedLeadRaw || {};
        devLog("CRM SAVE READBACK IMMEDIATE", savedLeadRawForDebug);
        devLog("CRM SAVE READBACK", {
          selectedCrmDealId: targetDealId,
          checklist: savedLeadRawForDebug.checklist,
          checklistItems: savedLeadRawForDebug.checklistItems,
          stageChecklist: savedLeadRawForDebug.stageChecklist,
          installationDate: savedLeadRawForDebug.installationDate,
          installationReminder: savedLeadRawForDebug.installationReminder,
          reminderConfig: savedLeadRawForDebug.reminderConfig,
        });
        devLog("CRM details save readback audit", {
          selectedCrmDealId: targetDealId,
          savedExists: true,
          savedInstallationDate: savedInstallationDateKey,
          savedChecklistCount: savedChecklistProgress.total,
          savedChecklistCompleted: savedChecklistProgress.completed,
          savedFollowUpsCount: savedFollowUps.length,
          verificationPassed: verificationMismatches.length === 0,
          selectedCrmDealIdStillSame:
            String(targetDealId || "") === String(selectedCrmDealIdRef.current || targetDealId || ""),
          verificationMismatches,
        });
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastImmediateReadback: saveResult?.savedLeadRaw || {},
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: verificationMismatches.length === 0 ? "immediate-ok" : "immediate-mismatch",
            immediateMismatches: verificationMismatches,
            immediateChecklistCompleted: savedChecklistProgress.completed,
            immediateChecklistTotal: savedChecklistProgress.total,
            immediateInstallationReminderEnabled:
              savedInstallationReminder?.enabled === true,
            immediateLastWriteSource:
              saveResult?.savedLeadRaw?._lastWriteSource || "",
            immediateUpdatedAt: saveResult?.savedLeadRaw?.updatedAt || null,
          },
        }));
      }

      await waitFor(2000);
      const delayedLeadRef = doc(db, "users", workspaceUid, "crm_leads", targetDealId);
      const delayedLeadSnap = await getDoc(delayedLeadRef);
      if (!delayedLeadSnap.exists()) {
        throw new Error("CRM details save verification failed: delayed read-back document is missing.");
      }
      const delayedLeadRaw = {
        id: targetDealId,
        ...delayedLeadSnap.data(),
      };
      const delayedLeadForVerification = buildCrmLeadData(delayedLeadRaw);
      const delayedChecklistItems = normalizeChecklistTree(
        delayedLeadForVerification.checklistItems ||
          delayedLeadForVerification.checklist ||
          delayedLeadForVerification.stageChecklist ||
          []
      );
      const delayedChecklistProgress = getChecklistProgress(delayedChecklistItems);
      const delayedInstallationDateKey = toDateInputValue(
        delayedLeadForVerification.installationDate
      );
      const delayedInstallationReminder = buildCanonicalInstallationReminder(
        delayedLeadForVerification,
        {
          installationDate: delayedLeadForVerification.installationDate,
          reminderConfigs: delayedLeadForVerification.reminderConfigs,
        }
      );
      const delayedReminderDateKey = String(delayedInstallationReminder?.reminderDate || "");
      const delayedReminderRepeat = String(
        delayedInstallationReminder?.repeat || delayedInstallationReminder?.repeatKey || ""
      );
      const delayedReminderConfig = normalizeCrmReminderConfigs(
        delayedLeadForVerification.reminderConfigs,
        {
          nextActionDate: delayedLeadForVerification.nextActionDate,
          installationDate: delayedLeadForVerification.installationDate,
        }
      )[CRM_REMINDER_TYPE_INSTALLATION];
      const delayedRawInstallationReminder = delayedLeadRaw.installationReminder || null;
      const delayedFollowUps = normalizeCrmFollowUps(
        delayedLeadForVerification.followUps || []
      );
      const delayedVerificationMismatches = [];

      if (expectedInstallationDateKey !== delayedInstallationDateKey) {
        delayedVerificationMismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== delayedChecklistProgress.total ||
        expectedChecklistProgress.completed !== delayedChecklistProgress.completed
      ) {
        delayedVerificationMismatches.push("checklistItems");
      }
      if (followUps.length !== delayedFollowUps.length) {
        delayedVerificationMismatches.push("followUps");
      }
      if (
        expectedInstallationReminder?.enabled !== delayedInstallationReminder?.enabled ||
        expectedReminderDateKey !== delayedReminderDateKey ||
        expectedInstallationReminder?.reminderTime !== delayedInstallationReminder?.reminderTime ||
        expectedReminderRepeat !== delayedReminderRepeat ||
        legacyExpectedReminderConfig?.reminderEnabled !== delayedReminderConfig?.reminderEnabled ||
        legacyExpectedReminderConfig?.reminderTime !== delayedReminderConfig?.reminderTime ||
        legacyExpectedReminderConfig?.repeatKey !== delayedReminderConfig?.repeatKey ||
        (expectedInstallationReminder?.enabled === true &&
          delayedRawInstallationReminder?.enabled !== true)
      ) {
        delayedVerificationMismatches.push("installationReminder");
      }
      if (
        String(crmDetailsSavePayload.notes || "") !==
        String(delayedLeadForVerification.notes || "")
      ) {
        delayedVerificationMismatches.push("notes");
      }

      if (isDevRuntime) {
        devLog("CRM SAVE READBACK DELAYED", delayedLeadRaw);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastDelayedReadback: delayedLeadRaw,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status:
              verificationMismatches.length === 0 &&
              delayedVerificationMismatches.length === 0
                ? "verified"
                : "mismatch",
            immediateMismatches: verificationMismatches,
            delayedMismatches: delayedVerificationMismatches,
            delayedChecklistCompleted: delayedChecklistProgress.completed,
            delayedChecklistTotal: delayedChecklistProgress.total,
            delayedInstallationReminderEnabled:
              delayedInstallationReminder?.enabled === true,
            delayedLastWriteSource: delayedLeadRaw?._lastWriteSource || "",
            delayedUpdatedAt: delayedLeadRaw?.updatedAt || null,
          },
        }));
      }

      if (
        verificationMismatches.length > 0 ||
        delayedVerificationMismatches.length > 0
      ) {
        if (isDevRuntime) {
          devError("CRM save verification failed", {
            selectedCrmDealId: targetDealId,
            expectedChecklistProgress,
            savedChecklistProgress,
            expectedInstallationDateKey,
            savedInstallationDateKey,
            expectedFollowUpsCount: followUps.length,
            savedFollowUpsCount: savedFollowUps.length,
            expectedInstallationReminder,
            savedInstallationReminder,
            savedRawInstallationReminder,
            delayedChecklistProgress,
            delayedInstallationDateKey,
            delayedFollowUpsCount: delayedFollowUps.length,
            delayedInstallationReminder,
            delayedRawInstallationReminder,
            verificationMismatches,
            delayedVerificationMismatches,
            lastWriteSource: delayedLeadRaw?._lastWriteSource || "",
          });
        }
        throw new Error(
          `CRM details save verification failed: ${[
            ...verificationMismatches,
            ...delayedVerificationMismatches,
          ].join(", ")} did not match read-back data.`
        );
      }

      const newlyCompletedChecklistItems = getNewlyCompletedChecklistNodes(
        previousChecklistItems,
        nextChecklistItems
      );

      if (newlyCompletedChecklistItems.length > 0) {
        await Promise.all(
          newlyCompletedChecklistItems.map((item) =>
            writeTaskHistoryRecord({
              taskType: TASK_HISTORY_TYPE_CHECKLIST,
              sourceTaskId: item.id,
              dealLike: {
                ...canonicalDeal,
                id: targetDealId,
              },
              taskTitle: item.title || "Checklist Item",
              taskDescription: item.notes || item.pathLabel || "",
              dueDate: null,
              sourceModule:
                tab === "executionSpecs"
                  ? TASK_HISTORY_SOURCE_EXECUTION_DETAILS
                  : TASK_HISTORY_SOURCE_CRM,
              originalPayload: {
                checklistItem: item,
                path: item.path,
                pathLabel: item.pathLabel,
                assignedToUserId: item.assignedToUserId || "",
                assignedToNameSnapshot: item.assignedToNameSnapshot || "",
              },
            })
          )
        );
      }

      const savedAt = new Date();
      const savedDraft = {
        ...crmDetailDraft,
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps: followUps.map((followUp) => createCrmFollowUpDraft(followUp)),
        executionSpecs: hydrateExecutionSpecDraftItems(executionSpecs),
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
      };
      const cachedSavedDeal = {
        ...activeCrmDeal,
        ...canonicalDeal,
        id: targetDealId || activeCrmDeal.id,
        crmRecordType: "lead",
        crmTitle: crmDetailDraft.title || canonicalDeal?.crmTitle || activeCrmDeal.crmTitle,
        crmContact: crmDetailDraft.contact || {},
        contact: crmDetailDraft.contact || {},
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps,
        attachments: normalizeCrmAttachments(crmDetailDraft.attachments || []),
        executionSpecs,
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
        notes: crmDetailDraft.notes || "",
        crmNotes: crmDetailDraft.notes || "",
        assignedToUserId: crmDetailDraft.assignedToUserId || "",
        assignedToNameSnapshot: crmDetailDraft.assignedToNameSnapshot || "",
        updatedAt: savedAt,
      };
      selectedCrmDealCacheRef.current = cachedSavedDeal;
      selectedCrmDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");
      crmDetailDraftDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");
      crmDetailDraftDirtyRef.current = false;
      setCrmDetailDraft(savedDraft);
      setCrmDetailsSavedAt(savedAt);
      notify("CRM details saved", "success");
      markExecutionDetailsBaseline(savedDraft, "saved");
      clearExecutionDetailsDraft();
      if (isDevRuntime) {
        const afterSelectedCrmDealId = String(targetDealId || activeCrmDeal.id || "");
        devLog("CRM save selection audit", {
          beforeSelectedCrmDealId,
          beforeSelectedDealName,
          savePayloadLeadId: String(activeCrmDeal.id || ""),
          updatedLeadId: afterSelectedCrmDealId,
          canonicalLeadId:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== activeCrmDeal.id
              ? afterSelectedCrmDealId
              : "",
          afterSelectedCrmDealId,
          drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
          foundSelectedAfterRefresh: crmDeals.some(
            (deal) => String(deal.id || "") === afterSelectedCrmDealId
          ),
          crmLeadRecordsCount: crmLeadRecords.length,
        });
      }
    } catch (error) {
      if (isDevRuntime) {
        devError("Failed to save CRM details:", error);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: "failed",
            error: error?.message || String(error),
            at: new Date().toISOString(),
          },
        }));
      }
      const errorMessage = String(error?.message || "");
      notify(
        errorMessage.includes("verification failed")
          ? "Save could not be verified. Please try again."
          : "Could not save CRM details. Your changes are still here.",
        "error"
      );
    } finally {
      setIsSavingCrmDetails(false);
    }
  };

  const updateCrmReminderConfig = useCallback((reminderType, patch) => {
    markCrmDetailDraftDirty();
    setCrmDetailDraft((prev) => {
      if (!prev) return prev;

      const nextDate = parseDateInputValue(prev.nextActionDate);
      const installDate = parseDateInputValue(prev.installationDate);
      const currentConfigs = normalizeCrmReminderConfigs(prev.reminderConfigs, {
        nextActionDate: nextDate,
        installationDate: installDate,
      });
      const currentConfig = currentConfigs[reminderType] || {};
      const hasEnabledPatch = Object.prototype.hasOwnProperty.call(patch, "enabled");
      const hasReminderEnabledPatch = Object.prototype.hasOwnProperty.call(
        patch,
        "reminderEnabled"
      );
      const nextEnabled = hasReminderEnabledPatch
        ? patch.reminderEnabled === true
        : hasEnabledPatch
        ? patch.enabled === true
        : currentConfig.enabled === true;
      const nextConfig = {
        ...currentConfig,
        ...patch,
        enabled: nextEnabled,
        reminderEnabled: nextEnabled,
      };
      const nextReminderConfigs = {
        ...currentConfigs,
        [reminderType]: nextConfig,
      };
      const nextDraft = {
        ...prev,
        reminderConfigs: nextReminderConfigs,
      };

      if (reminderType === CRM_REMINDER_TYPE_INSTALLATION) {
        const nextInstallationReminder = buildCanonicalInstallationReminder(
          {
            ...nextDraft,
            installationReminder: nextConfig,
            reminderConfig: nextConfig,
          },
          {
            installationDate: installDate,
            reminderConfigs: nextReminderConfigs,
          }
        );
        nextDraft.installationReminder = nextInstallationReminder;
        nextDraft.reminderConfig = nextInstallationReminder;

        if (isDevRuntime) {
          devLog("INSTALLATION REMINDER TRACE", {
            selectedCrmDealId,
            enabledFromEvent: nextEnabled,
            beforeDraftInstallationReminder: prev.installationReminder,
            afterDraftInstallationReminder: nextDraft.installationReminder,
          });
          devLog("INSTALLATION REMINDER UI CHANGE", {
            selectedCrmDealId,
            installationDate: nextDraft.installationDate,
            installationReminder: nextDraft.installationReminder,
            reminderConfig: nextDraft.reminderConfig,
          });
        }
      }

      return nextDraft;
    });
  }, [
    buildCanonicalInstallationReminder,
    markCrmDetailDraftDirty,
    selectedCrmDealId,
  ]);

  const openCrmFollowUpModal = useCallback(
    (followUp = null) => {
      const actor = getCurrentTaskHistoryActor();
      const currentFollowUps = normalizeCrmFollowUps(
        crmDetailDraft?.followUps || selectedCrmDeal?.followUps || []
      );
      setCrmFollowUpModalState({
        mode: followUp ? "edit" : "create",
        followUp: createCrmFollowUpDraft(
          followUp || {
            title: "",
            description: "",
            order: currentFollowUps.length + 1,
            priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
            repeat: "none",
            createdBy: actor,
          }
        ),
      });
    },
    [createCrmFollowUpDraft, crmDetailDraft, getCurrentTaskHistoryActor, selectedCrmDeal]
  );

  const closeCrmFollowUpModal = useCallback(() => {
    setCrmFollowUpModalState(null);
    setCrmFollowUpAttachmentUploadKey("");
  }, []);

  const updateCrmFollowUpModalDraft = useCallback((patch) => {
    setCrmFollowUpModalState((prev) =>
      prev
        ? {
            ...prev,
            followUp: {
              ...prev.followUp,
              ...patch,
            },
          }
        : prev
    );
  }, []);

  const resolveCrmTaskWriteTarget = useCallback(
    (dealLike = {}, fallbackId = "") => {
      const candidateLeadId = String(
        dealLike?.crmRecordType === "lead"
          ? dealLike?.id || fallbackId || ""
          : dealLike?.leadId ||
              dealLike?.crmLeadId ||
              dealLike?.canonicalLeadId ||
              dealLike?.primaryLeadId ||
              dealLike?.crm?.leadId ||
              ""
      ).trim();

      if (candidateLeadId) {
        const sourceLead =
          crmLeadRecords.find(
            (lead) => String(lead.id || "") === String(candidateLeadId)
          ) ||
          (dealLike?.crmRecordType === "lead" && String(dealLike.id || "") === candidateLeadId
            ? dealLike
            : null);

        return {
          targetId: candidateLeadId,
          sourceLead,
          forceLead: true,
          firestorePath: workspaceUid
            ? `users/${workspaceUid}/crm_leads/${candidateLeadId}`
            : "",
        };
      }

      const fallbackDealId = String(
        fallbackId ||
          dealLike?.id ||
          dealLike?.dealId ||
          dealLike?.quoteId ||
          dealLike?.primaryQuoteId ||
          ""
      ).trim();

      return {
        targetId: fallbackDealId,
        sourceLead: null,
        forceLead: false,
        firestorePath:
          workspaceUid && fallbackDealId
            ? `users/${workspaceUid}/quotations/${fallbackDealId}`
            : "",
      };
    },
    [crmLeadRecords, workspaceUid]
  );

  const resolveDailyTaskWriteTarget = useCallback(
    (task = {}) => {
      const explicitLeadId = String(task?.leadId || task?.crmLeadId || "").trim();
      if (explicitLeadId) {
        return resolveCrmTaskWriteTarget(
          { ...(task?.deal || {}), leadId: explicitLeadId },
          explicitLeadId
        );
      }

      return resolveCrmTaskWriteTarget(
        task?.deal || {},
        task?.dealId || task?.quoteId || task?.sourceDealId || ""
      );
    },
    [resolveCrmTaskWriteTarget]
  );

  const persistCrmFollowUpsForDeal = useCallback(
    async (dealLike, nextFollowUps, successMessage = "Follow-up saved") => {
      if (!workspaceUid || !dealLike?.id) {
        throw new Error("Missing workspace or CRM deal for follow-up save.");
      }

      const normalizedFollowUps = normalizeCrmFollowUps(nextFollowUps).map((followUp) =>
        sanitizeCrmFirestoreValue(followUp)
      );
      const canonicalDeal =
        dealLike.crmRecordType === "quote" && normalizedFollowUps.length > 0
          ? await ensureCrmLeadForExecutionSpecs(dealLike)
          : dealLike;
      const targetDealId = canonicalDeal?.id || dealLike.id;
      if (!targetDealId) {
        throw new Error("Missing CRM lead id for follow-up save.");
      }
      const primaryFollowUp = getPrimaryPendingCrmFollowUp(normalizedFollowUps);
      const primaryFollowUpDueDate = primaryFollowUp?.dueDate
        ? parseDateInputValue(primaryFollowUp.dueDate)
        : null;

      setCrmDetailDraft((prev) =>
        prev
          ? {
              ...prev,
              followUps: normalizedFollowUps.map((followUp) =>
                createCrmFollowUpDraft(followUp)
              ),
              nextAction: primaryFollowUp?.title || "",
              nextActionDate: primaryFollowUp?.dueDate || "",
            }
          : prev
      );

      if (
        canonicalDeal?.id &&
        canonicalDeal.id !== dealLike.id &&
        selectedCrmDealId === dealLike.id
      ) {
        setSelectedCrmDealId(canonicalDeal.id);
      }

      await saveCrmFieldsForDeal(targetDealId, {
        followUps: normalizedFollowUps,
        nextAction: primaryFollowUp?.title || "",
        nextActionDate: primaryFollowUpDueDate,
      }, {
        forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id,
        sourceLead:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id
            ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
            : null,
        createIfMissing:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id,
        skipReminderSync: true,
        syncQuote: false,
      });

      let reminderSyncFailed = false;
      try {
        await syncReminderDocsForDeal(
          {
            ...dealLike,
            ...canonicalDeal,
            id: targetDealId,
            crmRecordType: "lead",
            followUps: normalizedFollowUps,
            nextAction: primaryFollowUp?.title || "",
            nextActionDate: primaryFollowUpDueDate,
          },
          {
            followUps: normalizedFollowUps,
            nextActionDate: primaryFollowUpDueDate,
          }
        );
      } catch (error) {
        reminderSyncFailed = true;
        devError("Follow-up saved but reminder sync failed:", error, {
          workspaceUid,
          leadId: targetDealId,
          followUps: normalizedFollowUps,
        });
      }

      setCrmDetailsSavedAt(new Date());
      notify(
        reminderSyncFailed
          ? "Follow-up saved, but reminder could not be scheduled."
          : successMessage,
        reminderSyncFailed ? "warning" : "success"
      );
      return canonicalDeal || dealLike;
    },
    [
      createCrmFollowUpDraft,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDealId,
      syncReminderDocsForDeal,
      workspaceUid,
    ]
  );

  const saveCrmFollowUpModal = useCallback(async () => {
    if (!selectedCrmDeal || !crmFollowUpModalState?.followUp) return;

    let resolvedLeadId =
      selectedCrmDeal.crmRecordType === "lead"
        ? String(selectedCrmDeal.id || "")
        : String(selectedCrmDeal.crm?.leadId || selectedCrmDeal.leadId || "");
    let followUpDraftForLog = crmFollowUpModalState.followUp;
    const actor = getCurrentTaskHistoryActor();
    const draft = crmFollowUpModalState.followUp;
    const title = String(draft.title || "").trim();
    const dueDate = String(draft.dueDate || "").trim();
    const dueTimeInput = String(draft.dueTime || "").trim();
    const dueTime = parseTimeInputValue(dueTimeInput);
    const explicitReminderEnabled = normalizeBooleanInput(
      draft.reminderEnabled ?? draft.enabled
    );
    const reminderEnabled =
      explicitReminderEnabled !== undefined ? explicitReminderEnabled : Boolean(dueDate);

    if (!workspaceUid) {
      notify("Please sign in again to save this follow-up.", "warning");
      return;
    }

    if (!title) {
      notify("Please add a follow-up title.", "warning");
      return;
    }

    const parsedDueDate = parseDateInputValue(dueDate);
    if (dueDate && (!parsedDueDate || toDateInputValue(parsedDueDate) !== dueDate)) {
      notify("Please choose a valid due date.", "warning");
      return;
    }

    if (dueTimeInput && !/^\d{2}:\d{2}$/.test(dueTimeInput)) {
      notify("Please choose a valid due time.", "warning");
      return;
    }

    if (reminderEnabled && !dueDate) {
      notify("Choose a due date or turn the reminder off.", "warning");
      return;
    }

    try {
      const currentFollowUps = normalizeCrmFollowUps(
        crmDetailDraft?.followUps || selectedCrmDeal.followUps || [],
        {
          nextAction: crmDetailDraft?.nextAction || selectedCrmDeal.nextAction || "",
          nextActionDate:
            parseDateInputValue(crmDetailDraft?.nextActionDate) ||
            selectedCrmDeal.nextActionDate ||
            null,
          reminderConfig:
            crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            selectedCrmDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            null,
        }
      );

      const nextFollowUp = sanitizeCrmFirestoreValue({
        ...draft,
        id: String(draft.id || "").trim() || createClientId("crm-follow-up"),
        title,
        description: String(draft.description || ""),
        dueDate,
        dueTime,
        repeat: String(draft.repeat || "none"),
        reminderEnabled:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE ? false : reminderEnabled,
        order: Number.isFinite(Number(draft.order)) && Number(draft.order) > 0
          ? Number(draft.order)
          : currentFollowUps.length + 1,
        priority: CRM_FOLLOW_UP_PRIORITY_OPTIONS.some(
          (option) => option.key === String(draft.priority || "").trim().toLowerCase()
        )
          ? String(draft.priority || "").trim().toLowerCase()
          : CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        reminderId: String(draft.reminderId || "").trim() || String(draft.id || "").trim(),
        status:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
        createdAt: draft.createdAt || new Date(),
        updatedAt: new Date(),
        createdBy:
          draft.createdBy?.uid || draft.createdBy?.name || draft.createdBy?.email
            ? draft.createdBy
            : actor,
        completedAt:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? draft.completedAt || new Date()
            : null,
        completedBy:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? actor
            : buildCrmFollowUpActor({}),
        attachments: normalizeCrmAttachments(draft.attachments || []),
      });
      followUpDraftForLog = nextFollowUp;

      const exists = currentFollowUps.some((followUp) => followUp.id === nextFollowUp.id);
      const nextFollowUps = exists
        ? currentFollowUps.map((followUp) =>
            followUp.id === nextFollowUp.id ? nextFollowUp : followUp
          )
        : [nextFollowUp, ...currentFollowUps];

      const savedDeal = await persistCrmFollowUpsForDeal(
        selectedCrmDeal,
        nextFollowUps,
        exists ? "Follow-up saved" : "Follow-up added"
      );
      resolvedLeadId = savedDeal?.id || resolvedLeadId;
      closeCrmFollowUpModal();
    } catch (error) {
      devError("Failed to save follow-up", error, {
        workspaceUid,
        leadId: resolvedLeadId,
        dealId: selectedCrmDeal?.id,
        dealRecordType: selectedCrmDeal?.crmRecordType,
        followUpDraft: followUpDraftForLog,
      });
      notify("Could not save follow-up. Please try again.", "error");
    }
  }, [
    closeCrmFollowUpModal,
    crmDetailDraft,
    crmFollowUpModalState,
    buildCrmFollowUpActor,
    getCurrentTaskHistoryActor,
    notify,
    persistCrmFollowUpsForDeal,
    selectedCrmDeal,
    workspaceUid,
  ]);

  const markCrmFollowUpDone = useCallback(
    async (followUpIdentifier, dealOverride = null, options = {}) => {
      const sourceDeal = dealOverride || selectedCrmDeal;
      if (!sourceDeal || !followUpIdentifier) {
        devError("Failed to mark done: missing follow-up or deal", {
          followUpIdentifier,
          dealId: sourceDeal?.id,
        });
        notify("Could not mark task as done. Please try again.", "error");
        return;
      }
      if (!workspaceUid) {
        devError("Failed to mark done: missing workspace");
        notify("Please sign in again to mark this task as done.", "error");
        return;
      }

      const actor = getCurrentTaskHistoryActor();
      const reminder = options?.reminder || null;
      const identifier = String(followUpIdentifier || "").trim();
      const useDraft = sourceDeal.id === selectedCrmDeal?.id;
      const currentFollowUps = normalizeCrmFollowUps(
        useDraft
          ? crmDetailDraft?.followUps || sourceDeal.followUps || []
          : sourceDeal.followUps || [],
        {
          nextAction: useDraft
            ? crmDetailDraft?.nextAction || sourceDeal.nextAction || ""
            : sourceDeal.nextAction || "",
          nextActionDate: useDraft
            ? parseDateInputValue(crmDetailDraft?.nextActionDate) ||
              sourceDeal.nextActionDate ||
              null
            : sourceDeal.nextActionDate || null,
          reminderConfig: useDraft
            ? crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
              sourceDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
              null
            : sourceDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }
      );
      const targetFollowUp = currentFollowUps.find(
        (followUp) =>
          String(followUp.id || "") === identifier ||
          String(followUp.reminderId || "") === identifier ||
          (reminder && doesCrmReminderMatchFollowUp(reminder, followUp))
      );

      if (!targetFollowUp) {
        devError("Failed to mark done: follow-up not found", {
          followUpIdentifier,
          reminderId: reminder?.reminderId,
          dealId: sourceDeal.id,
        });
        notify("We could not find this task. Please open the deal and check it manually.", "error");
        return;
      }

      const nextFollowUps = currentFollowUps.map((followUp) =>
        followUp.id === targetFollowUp.id
          ? {
              ...followUp,
              reminderId: followUp.reminderId || followUp.id,
              reminderEnabled: false,
              status: CRM_FOLLOW_UP_STATUS_DONE,
              completedAt: new Date(),
              completedBy: actor,
              updatedAt: new Date(),
            }
          : followUp
      );

      const doneKey = `followup:${sourceDeal.id || sourceDeal.leadId || "unknown"}:${targetFollowUp.id}`;
      let writeAudit = {
        path: workspaceUid
          ? `users/${workspaceUid}/crm_leads/${sourceDeal.id || sourceDeal.leadId || ""}`
          : "",
        payload: { followUps: nextFollowUps },
        dealId: sourceDeal.id || "",
        followUpId: targetFollowUp.id || "",
      };
      setMarkingDoneKey(doneKey);

      try {
        const canonicalDeal = await persistCrmFollowUpsForDeal(
          sourceDeal,
          nextFollowUps,
          options?.successMessage || "Task marked as done"
        );
        const targetDealId = canonicalDeal?.id || sourceDeal.id;
        const completedFollowUp =
          normalizeCrmFollowUps(nextFollowUps).find(
            (followUp) => followUp.id === targetFollowUp.id
          ) || {
            ...targetFollowUp,
            reminderId: targetFollowUp.reminderId || targetFollowUp.id,
            status: CRM_FOLLOW_UP_STATUS_DONE,
          };
        const canonicalReminderId = buildFollowUpReminderDocId(
          targetDealId,
          completedFollowUp
        );
        const reminderIdsToComplete = Array.from(
          new Set([canonicalReminderId, reminder?.id].filter(Boolean))
        );

        await Promise.all(
          reminderIdsToComplete.map(async (reminderDocId) => {
            const reminderPayload = {
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              completedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            writeAudit = {
              path: `users/${workspaceUid}/crm_reminders/${reminderDocId}`,
              payload: reminderPayload,
              dealId: targetDealId,
              followUpId: targetFollowUp.id || "",
            };
            await setDoc(
              doc(db, "users", workspaceUid, "crm_reminders", reminderDocId),
              reminderPayload,
              { merge: true }
            );
            await markDueReminderCompleted({
              leadId: targetDealId,
              reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
              sourceId: completedFollowUp.id || completedFollowUp.reminderId || targetFollowUp.id || "",
              crmReminderId: reminderDocId,
            });
            applyCrmReminderLocalPatch(reminderDocId, {
              ...(reminderDocId === reminder?.id ? reminder : {}),
              id: reminderDocId,
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              completedAt: new Date(),
              updatedAt: new Date(),
            });
          })
        );

        await writeTaskHistoryRecord({
          taskType: TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: targetFollowUp.id,
          dealLike: canonicalDeal || sourceDeal,
          taskTitle: targetFollowUp.title || "Follow-up",
          taskDescription: targetFollowUp.description || "",
          dueDate:
            combineReminderDateTime(targetFollowUp.dueDate, targetFollowUp.dueTime) ||
            parseDateInputValue(targetFollowUp.dueDate) ||
            null,
          sourceModule: TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            followUp: targetFollowUp,
            completedBy: actor,
          },
        });
      } catch (error) {
        logCrmTaskDoneFailure("markCrmFollowUpDone", error, {
          ...writeAudit,
          workspaceUid,
          selectedCrmDealId,
        });
        notify("Could not mark task as done. Please try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      applyCrmReminderLocalPatch,
      buildFollowUpReminderDocId,
      combineReminderDateTime,
      crmDetailDraft,
      getCurrentTaskHistoryActor,
      markDueReminderCompleted,
      notify,
      persistCrmFollowUpsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      workspaceUid,
      writeTaskHistoryRecord,
    ]
  );

  const deleteCrmFollowUp = useCallback(
    (followUpId) => {
      if (!selectedCrmDeal || !followUpId) return;

      openConfirmDialog({
        title: "Delete follow-up?",
        description: "This follow-up and its reminder will be removed from this deal.",
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        tone: "danger",
        onConfirm: async () => {
          try {
            const currentFollowUps = normalizeCrmFollowUps(
              crmDetailDraft?.followUps || selectedCrmDeal.followUps || []
            );
            const targetFollowUp = currentFollowUps.find(
              (followUp) => followUp.id === followUpId
            );

            await Promise.all(
              normalizeCrmAttachments(targetFollowUp?.attachments || []).map((attachment) =>
                attachment.fileId
                  ? archiveManagedStorageFile({
                      workspaceUid,
                      fileId: attachment.fileId,
                      removedBy: auth.currentUser?.uid || "",
                      removalReason: "crm_follow_up_deleted",
                      linkedRecords: [],
                    }).catch(() => {})
                  : Promise.resolve()
              )
            );

            const nextFollowUps = currentFollowUps.filter(
              (followUp) => followUp.id !== followUpId
            );
            await persistCrmFollowUpsForDeal(
              selectedCrmDeal,
              nextFollowUps,
              "Follow-up deleted"
            );
          } catch (error) {
            devError("Failed to delete follow-up:", error);
            notify("Could not delete follow-up. Please try again.", "error");
          }
        },
      });
    },
    [
      crmDetailDraft,
      notify,
      openConfirmDialog,
      persistCrmFollowUpsForDeal,
      selectedCrmDeal,
      workspaceUid,
    ]
  );

  const uploadCrmFollowUpAttachments = useCallback(
    async (filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (
        !workspaceUid ||
        !selectedCrmDeal ||
        !crmFollowUpModalState?.followUp ||
        files.length === 0
      ) {
        return;
      }

      const followUp = crmFollowUpModalState.followUp;
      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      const uploadKey = `${selectedCrmDeal.id}:${followUp.id}`;
      setCrmFollowUpAttachmentUploadKey(uploadKey);

      try {
        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("crm-follow-up-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              followUpId: followUp.id,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeCrmAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name || "Attachment",
                filename: managedImage.filename || file.name || "attachment",
                url: managedImage.downloadURL,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedByUid: user?.uid || "",
                uploadedByName:
                  currentWorkspaceMember?.name ||
                  user?.displayName ||
                  user?.email ||
                  user?.uid ||
                  "",
              },
            ])[0]
          );
        }

        updateCrmFollowUpModalDraft({
          attachments: normalizeCrmAttachments([
            ...(followUp.attachments || []),
            ...uploadedAttachments,
          ]),
        });
        notify("Attachment added to follow-up", "success");
      } catch (error) {
        devError("Failed to upload follow-up attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload follow-up attachment. Please try again.", "error");
      } finally {
        setCrmFollowUpAttachmentUploadKey("");
      }
    },
    [
      crmFollowUpModalState,
      currentWorkspaceMember?.name,
      ensureCrmLeadForExecutionSpecs,
      notify,
      selectedCrmDeal,
      updateCrmFollowUpModalDraft,
      user,
      workspaceUid,
    ]
  );

  const removeCrmFollowUpAttachment = useCallback(
    async (attachmentId) => {
      const followUp = crmFollowUpModalState?.followUp;
      if (!followUp || !attachmentId) return;

      const currentAttachments = normalizeCrmAttachments(followUp.attachments || []);
      const targetAttachment = currentAttachments.find(
        (attachment) => attachment.id === attachmentId
      );

      try {
        if (targetAttachment?.fileId) {
          await archiveManagedStorageFile({
            workspaceUid,
            fileId: targetAttachment.fileId,
            removedBy: auth.currentUser?.uid || "",
            removalReason: "crm_follow_up_attachment_removed",
            linkedRecords: [],
          });
        }

        updateCrmFollowUpModalDraft({
          attachments: currentAttachments.filter(
            (attachment) => attachment.id !== attachmentId
          ),
        });
        notify("Attachment removed", "success");
      } catch (error) {
        devError("Failed to remove follow-up attachment:", error);
        notify("Could not remove follow-up attachment. Please try again.", "error");
      }
    },
    [crmFollowUpModalState, notify, updateCrmFollowUpModalDraft, workspaceUid]
  );

  const uploadCrmExecutionSpecAttachments = useCallback(
    async (itemId, filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (!workspaceUid || !selectedCrmDeal || !crmDetailDraft || !itemId || files.length === 0) {
        return;
      }

      const currentSpecs = serializeExecutionSpecDraftItems(
        crmDetailDraft.executionSpecs || []
      );
      const targetSpec = currentSpecs.find((item) => item.id === itemId);
      if (!targetSpec) return;

      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal, currentSpecs)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      if (targetDealId && targetDealId !== selectedCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      }
      const uploadKey = `${targetDealId}:${itemId}`;
      setCrmExecutionAttachmentUploadKey(uploadKey);

      try {
        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("exec-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              itemId,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeExecutionSpecAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name || "Attachment",
                filename: managedImage.filename || file.name || "attachment",
                url: managedImage.downloadURL,
                path: managedImage.fullPath,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedBy: user?.uid || "",
                uploadedByName: user?.email || "",
              },
            ])[0]
          );
        }

        const nextSpecs = stampExecutionSpecs(currentSpecs.map((item) =>
          item.id === itemId
            ? {
                ...item,
                attachments: normalizeExecutionSpecAttachments([
                  ...(item.attachments || []),
                  ...uploadedAttachments,
                ]),
              }
            : item
        ));

        setCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                executionSpecs: hydrateExecutionSpecDraftItems(nextSpecs),
              }
            : prev
        );

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            executionSpecs: nextSpecs,
          },
          {
            forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
            sourceLead:
              canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id
                ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing:
              canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
          }
        );
      } catch (error) {
        devError("Failed to upload execution spec attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload execution attachment. Please try again.", "error");
      } finally {
        setCrmExecutionAttachmentUploadKey("");
      }
    },
    [
      crmDetailDraft,
      ensureCrmLeadForExecutionSpecs,
      hydrateExecutionSpecDraftItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      serializeExecutionSpecDraftItems,
      stampExecutionSpecs,
      notify,
      user,
      workspaceUid,
    ]
  );

  const removeCrmExecutionSpecAttachment = useCallback(
    async (itemId, attachmentId) => {
      if (!selectedCrmDeal || !crmDetailDraft) return;

      const currentSpecs = serializeExecutionSpecDraftItems(
        crmDetailDraft.executionSpecs || []
      );
      const targetSpec = currentSpecs.find((item) => item.id === itemId);
      if (!targetSpec) return;

      const targetAttachment = normalizeExecutionSpecAttachments(
        targetSpec.attachments || []
      ).find((attachment) => attachment.id === attachmentId);

      if (!targetAttachment) return;

      if (targetAttachment.fileId) {
        archiveManagedStorageFile({
          workspaceUid,
          fileId: targetAttachment.fileId,
          removedBy: auth.currentUser?.uid || "",
          removalReason: "crm_execution_spec_attachment_removed",
          linkedRecords: [],
        }).catch((error) => {
          if (isDevelopmentMode()) {
            devWarn("CRM_EXECUTION_SPEC_ATTACHMENT_ARCHIVE_FAILED", {
              fileId: targetAttachment.fileId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      }

      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal, currentSpecs)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      if (targetDealId && targetDealId !== selectedCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      }
      const nextSpecs = stampExecutionSpecs(currentSpecs.map((item) =>
        item.id === itemId
          ? {
              ...item,
              attachments: normalizeExecutionSpecAttachments(item.attachments || []).filter(
                (attachment) => attachment.id !== attachmentId
              ),
            }
          : item
      ));

      updateCrmDetailDraft((prev) =>
        prev
          ? {
              ...prev,
              executionSpecs: hydrateExecutionSpecDraftItems(nextSpecs),
            }
          : prev
      );

      await saveCrmFieldsForDeal(
        targetDealId,
        {
          executionSpecs: nextSpecs,
        },
        {
          forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
          sourceLead:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id
              ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
              : null,
          createIfMissing:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
        }
      );
    },
    [
      crmDetailDraft,
      ensureCrmLeadForExecutionSpecs,
      hydrateExecutionSpecDraftItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      serializeExecutionSpecDraftItems,
      stampExecutionSpecs,
      updateCrmDetailDraft,
      workspaceUid,
    ]
  );

  const uploadCrmDealAttachments = useCallback(
    async (dealId, filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (!workspaceUid || !dealId || files.length === 0) {
        return;
      }

      const sourceDeal = crmDeals.find((deal) => deal.id === dealId) || selectedCrmDeal;
      if (!sourceDeal) return;

      setCrmAttachmentUploadDealId(dealId);

      try {
        const canonicalDeal =
          sourceDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(sourceDeal)
            : sourceDeal;
        const targetDeal = canonicalDeal || sourceDeal;
        const targetDealId = targetDeal.id || dealId;
        const targetRecordType = targetDeal.crmRecordType || "lead";

        if (targetDealId && targetDealId !== dealId) {
          setSelectedCrmDealId(targetDealId);
        }

        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("crm-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              recordType: targetRecordType,
              attachmentId,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeCrmAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name,
                filename: managedImage.filename || file.name,
                url: managedImage.downloadURL,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedByUid: user?.uid || "",
                uploadedByName:
                  currentWorkspaceMember?.name ||
                  user?.displayName ||
                  user?.email ||
                  user?.uid ||
                  "",
              },
            ])[0]
          );
        }

        const nextAttachments = normalizeCrmAttachments([
          ...(targetDeal.attachments || []),
          ...uploadedAttachments,
        ]);

        setCrmDetailDraft((prev) =>
          (selectedCrmDealId === dealId || selectedCrmDealId === targetDealId) && prev
            ? {
                ...prev,
                attachments: nextAttachments,
              }
            : prev
        );

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            attachments: nextAttachments,
          },
          {
            forceLead: targetRecordType === "lead" || targetDealId !== dealId,
            sourceLead:
              targetRecordType === "lead" || targetDealId !== dealId
                ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing: targetRecordType === "lead" || targetDealId !== dealId,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Attachment uploaded", "success");
      } catch (error) {
        devError("Failed to upload CRM attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload the attachment. Please try again.", "error");
      } finally {
        setCrmAttachmentUploadDealId("");
      }
    },
    [
      crmDeals,
      currentWorkspaceMember?.name,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      user,
      workspaceUid,
    ]
  );

  const removeCrmDealAttachment = useCallback(
    async (dealId, attachmentId) => {
      const sourceDeal = crmDeals.find((deal) => deal.id === dealId) || selectedCrmDeal;
      if (!workspaceUid || !sourceDeal) return;

      try {
        const canonicalDeal =
          sourceDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(sourceDeal)
            : sourceDeal;
        const targetDeal = canonicalDeal || sourceDeal;
        const targetDealId = targetDeal.id || dealId;
        const targetRecordType = targetDeal.crmRecordType || "lead";
        const currentAttachments = normalizeCrmAttachments(targetDeal.attachments || []);
        const targetAttachment = currentAttachments.find(
          (attachment) => attachment.id === attachmentId
        );
        const nextAttachments = currentAttachments.filter(
          (attachment) => attachment.id !== attachmentId
        );

        if (targetAttachment?.fileId) {
          await archiveManagedStorageFile({
            workspaceUid,
            fileId: targetAttachment.fileId,
            removedBy: auth.currentUser?.uid || "",
            removalReason: "crm_deal_attachment_removed",
            linkedRecords: [],
          });
        }

        setCrmDetailDraft((prev) =>
          (selectedCrmDealId === dealId || selectedCrmDealId === targetDealId) && prev
            ? {
                ...prev,
                attachments: nextAttachments,
              }
            : prev
        );

        if (targetDealId && targetDealId !== dealId) {
          setSelectedCrmDealId(targetDealId);
        }

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            attachments: nextAttachments,
          },
          {
            forceLead: targetRecordType === "lead" || targetDealId !== dealId,
            sourceLead:
              targetRecordType === "lead" || targetDealId !== dealId
                ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing: targetRecordType === "lead" || targetDealId !== dealId,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Attachment removed", "success");
      } catch (error) {
        devError("Failed to remove CRM attachment:", error);
        notify("Could not remove the attachment. Please try again.", "error");
      }
    },
    [
      crmDeals,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      workspaceUid,
    ]
  );

  const dismissCrmReminder = useCallback(
    async (reminder) => {
      if (!workspaceUid || !reminder?.id) return;

      if (reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) {
        const { followUp } = resolveCrmReminderFollowUp(reminder);
        if (!followUp || followUp.status === CRM_FOLLOW_UP_STATUS_DONE) {
          await setDoc(
            doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
            {
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          ).catch((error) => {
            devError("Failed to dismiss stale reminder:", error);
          });
          applyCrmReminderLocalPatch(reminder.id, {
            ...reminder,
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            updatedAt: new Date(),
          });
          return;
        }
      }

      const repeatMinutes =
        reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? getCrmFollowUpRepeatMinutes(reminder.repeatKey)
          : getCrmReminderRepeatMinutes(reminder.repeatKey);
      const snoozeMinutes = repeatMinutes > 0 ? repeatMinutes : 30;
      const nextTriggerAt = new Date(Date.now() + snoozeMinutes * 60 * 1000);
      const reminderRef = doc(
        db,
        "users",
        workspaceUid,
        "crm_reminders",
        reminder.id
      );

      await setDoc(
        reminderRef,
        {
          active: true,
          nextTriggerAt: Timestamp.fromDate(nextTriggerAt),
          status: CRM_REMINDER_STATUS_ACTIVE,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      const dueReminderLeadId = String(
        reminder.leadId || reminder.crmLeadId || reminder.dealId || ""
      ).trim();
      const dueReminderId = buildDueReminderDocId(
        dueReminderLeadId,
        reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
        reminder.followUpId || reminder.sourceTaskId || reminder.reminderId || ""
      );
      if (dueReminderLeadId && dueReminderId) {
        await setDoc(
          doc(db, "users", workspaceUid, "due_reminders", dueReminderId),
          {
            id: dueReminderId,
            workspaceUid,
            crmLeadId: dueReminderLeadId,
            crmReminderId: reminder.id || "",
            status: DUE_REMINDER_STATUS_PENDING,
            reminderEnabled: true,
            notificationSent: false,
            nextNotifyAt: Timestamp.fromDate(nextTriggerAt),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      applyCrmReminderLocalPatch(reminder.id, {
        ...reminder,
        active: true,
        nextTriggerAt,
        status: CRM_REMINDER_STATUS_ACTIVE,
        updatedAt: new Date(),
      });
      notify("Reminder dismissed for now", "info");
    },
    [
      applyCrmReminderLocalPatch,
      buildDueReminderDocId,
      notify,
      resolveCrmReminderFollowUp,
      workspaceUid,
    ]
  );

  const buildCrmLeadDealFromReminderSnapshot = useCallback((leadId, raw = {}) => {
    const nextActionDate =
      typeof raw.nextActionDate?.toDate === "function"
        ? raw.nextActionDate.toDate()
        : raw.nextActionDate || null;
    const installationDate =
      typeof raw.installationDate?.toDate === "function"
        ? raw.installationDate.toDate()
        : raw.installationDate || null;
    const linkedQuotes = normalizeCrmLinkedQuotes({
      ...raw,
      linkedQuoteDate:
        typeof raw.linkedQuoteDate?.toDate === "function"
          ? raw.linkedQuoteDate.toDate()
          : raw.linkedQuoteDate || null,
    });
    const contact = raw.contact || {};
    const customerLabel =
      raw.customerName ||
      contact.name ||
      raw.companyOrProject ||
      raw.title ||
      "Unnamed Lead";
    const reminderConfigs = normalizeCrmReminderConfigs(raw.reminderConfigs, {
      nextActionDate,
      installationDate,
    });

    return stripLegacyCrmQuoteLinkFields({
      id: leadId,
      ...raw,
      crmRecordType: "lead",
      contact,
      crmContact: contact,
      crmTitle: raw.title || raw.companyOrProject || customerLabel,
      customerLabel,
      customerName: raw.customerName || contact.name || "",
      customerPhone:
        raw.customerPhone ||
        contact.phone ||
        contact.whatsapp ||
        "",
      linkedQuotes,
      hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
      quoteType: raw.quoteType || raw.pipelineType || "",
      pipelineType: raw.pipelineType || raw.quoteType || "",
      nextActionDate,
      installationDate,
      reminderConfigs,
      followUps: normalizeCrmFollowUps(raw.followUps, {
        nextAction: raw.nextAction,
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] || null,
      }),
      attachments: normalizeCrmAttachments(raw.attachments),
      checklistItems: Array.isArray(raw.checklistItems)
        ? raw.checklistItems
        : Array.isArray(raw.checklist)
        ? raw.checklist
        : Array.isArray(raw.stageChecklist)
        ? raw.stageChecklist
        : [],
      executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
    });
  }, []);

  const loadCrmLeadDealForReminder = useCallback(
    async (reminder = {}) => {
      if (!workspaceUid || !reminder) return null;

      const candidateLeadIds = [];
      const pushLeadId = (value) => {
        const id = String(value || "").trim();
        if (id && !candidateLeadIds.includes(id)) {
          candidateLeadIds.push(id);
        }
      };

      pushLeadId(reminder.leadId);
      pushLeadId(reminder.crmLeadId);

      const reminderDealId = String(reminder.dealId || "").trim();
      pushLeadId(reminderDealId);
      if (reminder.dealRecordType === "lead") {
        pushLeadId(reminderDealId);
      }

      const directLocalLead =
        crmLeadRecords.find(
          (lead) =>
            String(lead.id || "") === reminderDealId ||
            String(lead.id || "") === String(reminder.leadId || "") ||
            String(lead.id || "") === String(reminder.crmLeadId || "")
        ) || null;
      if (directLocalLead?.id) {
        pushLeadId(directLocalLead.id);
      }

      const reminderQuoteId = String(
        reminder.quoteId ||
          (reminder.dealRecordType === "quote" ? reminder.dealId : "") ||
          ""
      ).trim();
      const reminderQuoteNo = String(reminder.quoteNo || "").trim();
      const linkedLocalLead =
        crmLeadRecords.find((lead) =>
          normalizeCrmLinkedQuotes(lead).some(
            (linkedQuote) =>
              (reminderQuoteId &&
                String(linkedQuote.quoteId || "") === reminderQuoteId) ||
              (reminderQuoteNo &&
                String(linkedQuote.quoteNo || "") === reminderQuoteNo)
          )
        ) || null;
      if (linkedLocalLead?.id) {
        pushLeadId(linkedLocalLead.id);
      }

      const linkedQuote =
        savedQuotes.find(
          (quote) =>
            (reminderQuoteId && String(quote.id || "") === reminderQuoteId) ||
            (reminderQuoteNo && String(quote.quoteNo || "") === reminderQuoteNo)
        ) || null;
      pushLeadId(linkedQuote?.crm?.leadId || linkedQuote?.leadId);

      const localDealLead =
        crmDeals.find(
          (deal) =>
            deal.crmRecordType === "lead" &&
            candidateLeadIds.includes(String(deal.id || ""))
        ) || null;

      for (const leadId of candidateLeadIds) {
        try {
          const leadSnap = await getDoc(
            doc(db, "users", workspaceUid, "crm_leads", leadId),
            { label: "crmFullLeadLoadById.reminder" }
          );
          if (leadSnap.exists()) {
            return buildCrmLeadDealFromReminderSnapshot(leadId, leadSnap.data() || {});
          }
        } catch (error) {
          devError("Failed to fetch CRM lead for reminder:", error, {
            reminder,
            workspaceUid,
            leadId,
          });
        }
      }

      const fallbackLead = directLocalLead || linkedLocalLead || localDealLead;
      return fallbackLead?.id
        ? buildCrmLeadDealFromReminderSnapshot(fallbackLead.id, fallbackLead)
        : null;
    },
    [
      buildCrmLeadDealFromReminderSnapshot,
      crmDeals,
      crmLeadRecords,
      savedQuotes,
      workspaceUid,
    ]
  );

  const resolveReminderFollowUpFromDeal = useCallback(
    (reminder = {}, deal = null) => {
      if (!deal) return { deal: null, followUp: null, followUps: [] };

      const followUps = normalizeCrmFollowUps(deal.followUps, {
        nextAction: deal.nextAction,
        nextActionDate: deal.nextActionDate,
        reminderConfig:
          deal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          deal.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          null,
      });

      const reminderKeys = getCrmReminderFollowUpKeys(reminder);
      const scheduledSource =
        reminder.scheduledAt ||
        reminder.nextTriggerAt ||
        combineReminderDateTime(reminder.reminderDate, reminder.reminderTime);
      const scheduledMillis = safeTimeMillis(scheduledSource);
      const scheduledDateKey =
        toDateInputValue(scheduledSource) ||
        toDateInputValue(reminder.reminderDate) ||
        String(reminder.reminderDate || "");
      const scheduledTimeKey = scheduledMillis
        ? `${String(new Date(scheduledMillis).getHours()).padStart(2, "0")}:${String(
            new Date(scheduledMillis).getMinutes()
          ).padStart(2, "0")}`
        : parseTimeInputValue(reminder.reminderTime || "");
      const reminderTitle = String(
        reminder.taskTitle || reminder.title || reminder.reminderLabel || deal.nextAction || ""
      )
        .trim()
        .toLowerCase();

      let followUp =
        followUps.find((item) => doesCrmReminderMatchFollowUp(reminder, item)) ||
        followUps.find((item) =>
          [item.id, item.reminderId, item.sourceTaskId]
            .map((value) => String(value || "").trim())
            .some((value) => value && reminderKeys.has(value))
        );

      if (!followUp) {
        followUp = followUps.find((item) => {
          if (item.status === CRM_FOLLOW_UP_STATUS_DONE) return false;
          const itemTitle = String(item.title || "").trim().toLowerCase();
          const titleMatches =
            reminderTitle &&
            (itemTitle === reminderTitle ||
              itemTitle.includes(reminderTitle) ||
              reminderTitle.includes(itemTitle));
          const dateMatches = !scheduledDateKey || item.dueDate === scheduledDateKey;
          const timeMatches =
            !scheduledTimeKey ||
            !item.dueTime ||
            item.dueTime === scheduledTimeKey;
          return titleMatches && dateMatches && timeMatches;
        });
      }

      if (!followUp && scheduledDateKey) {
        const dateMatches = followUps.filter(
          (item) =>
            item.status !== CRM_FOLLOW_UP_STATUS_DONE &&
            item.dueDate === scheduledDateKey &&
            (!scheduledTimeKey || !item.dueTime || item.dueTime === scheduledTimeKey)
        );
        if (dateMatches.length === 1) {
          followUp = dateMatches[0];
        }
      }

      if (!followUp) {
        const pendingFollowUps = followUps.filter(
          (item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE
        );
        if (
          reminder.reminderType === CRM_REMINDER_TYPE_TASK_DUE &&
          pendingFollowUps.length === 1
        ) {
          followUp = pendingFollowUps[0];
        }
      }

      return { deal, followUp: followUp || null, followUps };
    },
    [combineReminderDateTime]
  );

  const resolveReminderFollowUpFromLatestSource = useCallback(
    async (reminder = {}) => {
      const localResolved =
        reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? resolveCrmReminderFollowUp(reminder)
          : { deal: null, followUp: null, followUps: [] };

      if (localResolved.followUp) {
        return localResolved;
      }

      const latestDeal = await loadCrmLeadDealForReminder(reminder);
      return resolveReminderFollowUpFromDeal(reminder, latestDeal);
    },
    [
      loadCrmLeadDealForReminder,
      resolveCrmReminderFollowUp,
      resolveReminderFollowUpFromDeal,
    ]
  );

  const markCrmReminderDone = useCallback(
    async (reminder) => {
      const reminderFollowUpKeys = getCrmReminderFollowUpKeys(reminder);
      if (
        !reminder?.dealId &&
        !reminder?.leadId &&
        !reminder?.quoteId &&
        !reminder?.quoteNo &&
        reminderFollowUpKeys.size === 0
      ) {
        devError("Failed to mark done: reminder has no source identifiers", reminder);
        notify("Could not mark task as done. Please open the deal and try again.", "error");
        return;
      }
      if (!workspaceUid) {
        devError("Failed to mark reminder done: missing workspace", reminder);
        notify("Please sign in again to mark this task as done.", "error");
        return;
      }

      const doneKey = `reminder:${reminder.id || reminder.reminderId || reminder.dealId || "unknown"}`;
      let writeAudit = {
        path: workspaceUid && reminder?.id
          ? `users/${workspaceUid}/crm_reminders/${reminder.id}`
          : "",
        payload: {},
        reminderId: reminder?.id || "",
        dealId: reminder?.dealId || reminder?.leadId || "",
      };
      setMarkingDoneKey(doneKey);

      try {
        const markReminderDocumentDoneOnly = async (reason = "reminderOnly") => {
          if (!reminder?.id) {
            throw new Error("Cannot complete reminder without a reminder document id.");
          }
          const reminderPayload = {
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            completedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          writeAudit = {
            path: `users/${workspaceUid}/crm_reminders/${reminder.id}`,
            payload: reminderPayload,
            reminderId: reminder.id || "",
            dealId: reminder.dealId || reminder.leadId || "",
            reason,
          };
          await setDoc(
            doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
            reminderPayload,
            { merge: true }
          );
          await markDueReminderCompleted({
            leadId: reminder.leadId || reminder.crmLeadId || reminder.dealId || "",
            reminderType: reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
            sourceId:
              reminder.followUpId ||
              reminder.sourceTaskId ||
              reminder.reminderId ||
              "",
            crmReminderId: reminder.id,
          });
          applyCrmReminderLocalPatch(reminder.id, {
            ...reminder,
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            completedAt: new Date(),
            updatedAt: new Date(),
          });
          notify("Reminder marked done", "success");
        };

        if (
          reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP ||
          reminder.reminderType === CRM_REMINDER_TYPE_TASK_DUE
        ) {
          const { deal: sourceDeal, followUp } =
            await resolveReminderFollowUpFromLatestSource(reminder);

          if (sourceDeal && followUp) {
            await markCrmFollowUpDone(followUp.id, sourceDeal, {
              reminder,
              successMessage: "Task marked as done",
            });
            return;
          }

          if (reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) {
            devError("Failed to mark done: follow-up reminder target missing", {
              reminder,
              reminderId: reminder.reminderId,
              followUpId: reminder.followUpId,
              dealId: reminder.dealId,
            });
            await markReminderDocumentDoneOnly("followUpSourceMissing");
            return;
          }
        }

        const sourceDeal =
          crmDeals.find((deal) => deal.id === reminder.dealId) ||
          (await loadCrmLeadDealForReminder(reminder));
        if (!sourceDeal) {
          devError("Failed to mark done: reminder deal not found", {
            reminder,
            reminderId: reminder.id,
            dealId: reminder.dealId,
          });
          await markReminderDocumentDoneOnly("dealSourceMissing");
          return;
        }

        const nextReminderConfigs = normalizeCrmReminderConfigs(
          sourceDeal.reminderConfigs,
          {
            nextActionDate: sourceDeal.nextActionDate,
            installationDate: sourceDeal.installationDate,
          }
        );

        nextReminderConfigs[reminder.reminderType] = {
          ...nextReminderConfigs[reminder.reminderType],
          enabled: false,
          reminderEnabled: false,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date().toISOString(),
        };
        const isInstallationReminder =
          reminder.reminderType === CRM_REMINDER_TYPE_INSTALLATION;

        if (selectedCrmDealId === reminder.dealId) {
          setCrmDetailDraft((prev) =>
            prev
              ? {
                  ...prev,
                  reminderConfigs: nextReminderConfigs,
                }
              : prev
          );
        }

        const reminderWriteTarget = resolveCrmTaskWriteTarget(
          sourceDeal,
          reminder.leadId || reminder.crmLeadId || reminder.dealId || sourceDeal.id
        );
        const reminderConfigPayload = {
          reminderConfigs: nextReminderConfigs,
        };
        writeAudit = {
          path: reminderWriteTarget.firestorePath,
          payload: reminderConfigPayload,
          reminderId: reminder.id || "",
          dealId: sourceDeal.id || reminder.dealId || "",
        };

        await saveCrmFieldsForDeal(
          reminderWriteTarget.targetId,
          reminderConfigPayload,
          {
            forceLead: reminderWriteTarget.forceLead,
            sourceLead: reminderWriteTarget.sourceLead,
            createIfMissing: false,
            requireWrite: true,
            skipReminderSync: true,
            syncQuote: false,
            writeSource: "markCrmReminderDone.reminderConfigs",
          }
        );

        const reminderPayload = {
          enabled: false,
          reminderEnabled: false,
          active: false,
          nextTriggerAt: null,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        writeAudit = {
          path: `users/${workspaceUid}/crm_reminders/${reminder.id}`,
          payload: reminderPayload,
          reminderId: reminder.id || "",
          dealId: sourceDeal.id || reminder.dealId || "",
        };
        await setDoc(
          doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
          reminderPayload,
          { merge: true }
        );
        await markDueReminderCompleted({
          leadId: reminder.leadId || reminder.crmLeadId || sourceDeal.id || reminder.dealId || "",
          reminderType: reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
          sourceId:
            reminder.followUpId ||
            reminder.sourceTaskId ||
            reminder.reminderId ||
            "",
          crmReminderId: reminder.id,
        });
        applyCrmReminderLocalPatch(reminder.id, {
          ...reminder,
          enabled: false,
          reminderEnabled: false,
          active: false,
          nextTriggerAt: null,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date(),
          updatedAt: new Date(),
        });

        if (isInstallationReminder && sourceDeal.crmRecordType === "lead") {
          const installationPayload = {
            installationReminderDone: true,
            installationReminderCompletedAt: serverTimestamp(),
            installationReminderCompletedBy: getCurrentTaskHistoryActor(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "markReminderDone.installation",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          };
          writeAudit = {
            path: `users/${workspaceUid}/crm_leads/${sourceDeal.id}`,
            payload: installationPayload,
            reminderId: reminder.id || "",
            dealId: sourceDeal.id || "",
          };
          await setDoc(
            doc(db, "users", workspaceUid, "crm_leads", sourceDeal.id),
            installationPayload,
            { merge: true }
          );
          applyCrmLeadLocalPatch(sourceDeal.id, {
            installationReminderDone: true,
            installationReminderCompletedAt: new Date(),
            installationReminderCompletedBy: getCurrentTaskHistoryActor(),
            updatedAt: new Date(),
          });
        }

        const reminderDueDate =
          reminder.scheduledAt ||
          reminder.nextTriggerAt ||
          combineReminderDateTime(reminder.reminderDate, reminder.reminderTime) ||
          (isInstallationReminder
            ? sourceDeal.installationDate
            : sourceDeal.nextActionDate);

        await writeTaskHistoryRecord({
          taskType: isInstallationReminder
            ? TASK_HISTORY_TYPE_INSTALLATION
            : TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: reminder.id,
          dealLike: sourceDeal,
          taskTitle: isInstallationReminder
            ? "Installation Reminder"
            : sourceDeal.nextAction || reminder.reminderLabel || "Deal Action",
          taskDescription: isInstallationReminder
            ? `Installation reminder completed for ${sourceDeal.customerLabel || sourceDeal.crmTitle || "this deal"}.`
            : sourceDeal.nextAction || reminder.reminderLabel || "",
          dueDate: reminderDueDate,
          sourceModule: isInstallationReminder
            ? TASK_HISTORY_SOURCE_INSTALLATION
            : TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            reminder,
            reminderConfig: nextReminderConfigs[reminder.reminderType],
            dealSnapshot: {
              id: sourceDeal.id,
              crmRecordType: sourceDeal.crmRecordType,
              crmTitle: sourceDeal.crmTitle,
              customerLabel: sourceDeal.customerLabel,
              quoteNo:
                sourceDeal.quoteNo ||
                getPrimaryCrmLinkedQuote(sourceDeal.linkedQuotes || [])?.quoteNo ||
                "",
            },
          },
        });
        notify("Task marked as done", "success");
      } catch (error) {
        logCrmTaskDoneFailure("markCrmReminderDone", error, {
          ...writeAudit,
          reminder,
          workspaceUid,
          resolvedLeadId: reminder?.leadId || reminder?.crmLeadId || reminder?.dealId || "",
          resolvedFollowUpId: reminder?.followUpId || reminder?.reminderId || "",
        });
        notify("Could not mark task as done. Please open the deal and try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      applyCrmLeadLocalPatch,
      applyCrmReminderLocalPatch,
      combineReminderDateTime,
      crmDeals,
      getCurrentTaskHistoryActor,
      loadCrmLeadDealForReminder,
      markDueReminderCompleted,
      markCrmFollowUpDone,
      notify,
      resolveCrmTaskWriteTarget,
      resolveReminderFollowUpFromLatestSource,
      saveCrmFieldsForDeal,
      selectedCrmDealId,
      writeTaskHistoryRecord,
      workspaceUid,
    ]
  );

  const openReminderDeal = useCallback(
    (reminder) => {
      const resolvedFollowUp =
        reminder?.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? resolveCrmReminderFollowUp(reminder)
          : null;
      const targetDealId = resolvedFollowUp?.deal?.id || reminder?.dealId || "";
      const targetDeal =
        resolvedFollowUp?.deal ||
        crmDeals.find((deal) => String(deal.id || "") === String(targetDealId || "")) ||
        { id: targetDealId, leadId: reminder?.leadId || reminder?.crmLeadId || targetDealId };
      setCrmDrilldownKey("");
      setCrmScope(CRM_SCOPE_ALL);
      setCrmSearch("");
      setCrmStageFilter("all");
      setCrmTypeFilter("all");
      setCrmSortBy("newest");
      setCrmView("list");
      setTab("crm");
      openCrmDealDetails(targetDeal, { crmView: "list" });
    },
    [crmDeals, openCrmDealDetails, resolveCrmReminderFollowUp]
  );

  // Declared after the existing openers so render-time callback dependencies are initialized.
  const navigateToTaskTarget = async (task) => {
    const target = resolveTaskTarget(task);
    if (!workspaceUid || !canAccessCrm) return;
    if (!target.id) {
      notify("This task has no linked record. You can still manage it from Today's Tasks.", "info");
      return;
    }
    try {
      if (target.type === "project") {
        const project = await loadCrmProjectById(target.id, { navigate: false });
        if (!project || isUnavailableTaskTarget(project)) {
          notify("This order is unavailable or archived. The task remains in Today's Tasks.", "warning");
          return;
        }
        setCrmDrilldownKey("");
        setCrmView("projects");
        setRoutedTab("crm");
        return;
      }
      if (target.type === "quotation") {
        await loadFullQuote({ quoteId: target.id, sourceQuoteId: target.id }, { openedFrom: "crm_task" });
        return;
      }
      const collectionName = target.type === "customer" ? "customers" :
        target.type === "opportunity" ? "opportunities" : "crm_leads";
      const snap = await trackedGetDoc(doc(db, "users", workspaceUid, collectionName, target.id), {
        label: "crmTask.openExactTarget",
      });
      if (!snap.exists() || isUnavailableTaskTarget(snap.data())) {
        notify("The linked record is unavailable or archived. The task remains in Today's Tasks.", "warning");
        return;
      }
      if (target.type === "customer") {
        openCustomerLedger(target.id);
        return;
      }
      if (target.type === "opportunity") {
        const leadId = String(snap.data().legacyLeadId || snap.data().crmLeadId || "").trim();
        if (!leadId) {
          notify("This task's sales record needs its customer/deal link checked. No record was changed.", "warning");
          return;
        }
        await navigateToTaskTarget({ leadId });
        return;
      }
      const opened = await openCrmDealDetails(
        { ...snap.data(), id: snap.id, leadId: snap.id, crmRecordType: "lead" },
        { source: "crm_task", crmView: "list" }
      );
      if (opened) setRoutedTab("crm");
    } catch (error) {
      devError("CRM_TASK_TARGET_OPEN_FAILED", { type: target.type, code: error?.code });
      notify("Could not open the linked record. Please retry from Today's Tasks.", "error");
    }
  };
  const openCrmTaskHistoryDeal = navigateToTaskTarget;
  const openDailyTaskDeal = navigateToTaskTarget;

  const markDailyTaskDone = useCallback(
    async (task) => {
      if (task?.sourceType === DAILY_TASK_SOURCE_CRM_TASK) {
        const crmTask = task.crmTask || task.original || task;
        const taskId = task.sourceId || crmTask?.taskId || crmTask?.id || "";
        if (!taskId) {
          notify("Could not find that task. Please try again.", "error");
          return;
        }
        const isPaymentFollowUpTask =
          String(crmTask.taskType || "") === "payment" ||
          String(crmTask.paymentFollowUpForProjectId || "").trim() !== "" ||
          String(crmTask.sourceKind || "") === "payment_follow_up";
        if (isPaymentFollowUpTask) {
          const paymentProjectId = String(
            crmTask.paymentFollowUpForProjectId ||
              crmTask.projectId ||
              (crmTask.relatedType === "project_payment" ? crmTask.relatedId : "") ||
              ""
          ).trim();
          let paymentProject =
            crmProjectRecords.find(
              (project) =>
                String(project.projectId || project.id || "") === String(paymentProjectId)
            ) || null;
          if (!paymentProject && paymentProjectId) {
            paymentProject = await loadCrmProjectById(paymentProjectId, {
              navigate: false,
              notifyMissing: true,
            });
          }
          const shouldRecordPayment =
            paymentProject &&
            typeof window !== "undefined" &&
            window.confirm(
              "Was payment received? Choose OK to record the payment, or Cancel to complete this follow-up only."
            );
          if (shouldRecordPayment) {
            openCrmPaymentModal(paymentProject, {
              followUpTask: crmTask,
              completeFollowUpAfterSave: true,
              amount:
                crmTask.dueAmount ??
                paymentProject.paymentSummary?.balanceAmount ??
                paymentProject.balanceAmount ??
                "",
            });
            return;
          }
        }

        const doneKey = `daily:${task.id || taskId}`;
        setMarkingDoneKey(doneKey);
        try {
          await completeCrmTask(taskId, crmTask);
          removeCrmAttentionTaskById(taskId);
          notify("Task marked as done", "success");
        } catch (error) {
          logCrmTaskDoneFailure("markDailyTaskDone.crmTask", error, {
            workspaceUid,
            path: `users/${workspaceUid}/crm_tasks/${taskId}`,
            taskId,
          });
          notify("Could not mark task as done. Please try again.", "error");
        } finally {
          setMarkingDoneKey((current) => (current === doneKey ? "" : current));
        }
        return;
      }

      if (!task?.deal) {
        notify("Could not find that task. Please open the deal and check it manually.", "error");
        return;
      }

      const doneKey = `daily:${task.id || task.sourceType || "task"}:${task.sourceId || task.dealId || ""}`;
      const writeTarget = resolveDailyTaskWriteTarget(task);
      let writeAudit = {
        path: writeTarget.firestorePath,
        payload: {},
        taskId: task.id || "",
        sourceType: task.sourceType || "",
        sourceId: task.sourceId || "",
        dealId: task.dealId || "",
        leadId: task.leadId || "",
      };

      if (!writeTarget.targetId) {
        logCrmTaskDoneFailure("markDailyTaskDone.missingTarget", new Error("Missing CRM task write target"), writeAudit);
        notify("Could not find that task. Please open the deal and check it manually.", "error");
        return;
      }

      setMarkingDoneKey(doneKey);

      try {
        if (task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP) {
          await markCrmFollowUpDone(task.sourceId, task.deal, {
            successMessage: "Task marked as done",
          });
          return;
        }

        if (task.sourceType === DAILY_TASK_SOURCE_CHECKLIST) {
          const actor = getCurrentTaskHistoryActor();
          const nextChecklistItems = toggleChecklistNodeById(
            task.deal.checklistItems || [],
            task.sourceId,
            true,
            {
              completedAt: new Date(),
              completedBy: actor,
              updatedAt: new Date(),
            }
          );

          const payload = {
            checklistItems: nextChecklistItems,
          };
          writeAudit = {
            ...writeAudit,
            path: writeTarget.firestorePath,
            payload,
          };

          await saveCrmFieldsForDeal(writeTarget.targetId, payload, {
            forceLead: writeTarget.forceLead,
            sourceLead: writeTarget.sourceLead,
            requireWrite: true,
            skipReminderSync: true,
            syncQuote: false,
            writeSource: "markDailyTaskDone.checklist",
          });

          await writeTaskHistoryRecord({
            taskType: TASK_HISTORY_TYPE_CHECKLIST,
            sourceTaskId: task.sourceId || task.id,
            dealLike: task.deal,
            taskTitle: task.title,
            taskDescription: task.description || "",
            dueDate: task.dueDateTime || task.dueDate || null,
            sourceModule: TASK_HISTORY_SOURCE_CRM,
            originalPayload: {
              dailyTask: {
                id: task.id,
                sourceType: task.sourceType,
                sourceId: task.sourceId,
                dueDate: task.dueDateKey,
                dueTime: task.dueTime,
              },
            },
          });

          notify("Checklist item marked done", "success");
          return;
        }

        const reminderType =
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? CRM_REMINDER_TYPE_INSTALLATION
            : CRM_REMINDER_TYPE_TASK_DUE;
        const nextReminderConfigs = normalizeCrmReminderConfigs(
          task.deal.reminderConfigs,
          {
            nextActionDate: task.deal.nextActionDate,
            installationDate: task.deal.installationDate,
          }
        );

        nextReminderConfigs[reminderType] = {
          ...nextReminderConfigs[reminderType],
          enabled: false,
          reminderEnabled: false,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date().toISOString(),
        };

        const payload = {
          reminderConfigs: nextReminderConfigs,
        };
        writeAudit = {
          ...writeAudit,
          path: writeTarget.firestorePath,
          payload,
        };

        await saveCrmFieldsForDeal(writeTarget.targetId, payload, {
          forceLead: writeTarget.forceLead,
          sourceLead: writeTarget.sourceLead,
          requireWrite: true,
          skipReminderSync: true,
          syncQuote: false,
          writeSource: "markDailyTaskDone.reminderConfigs",
        });
        await markDueReminderCompleted({
          leadId: writeTarget.targetId || task.leadId || task.dealId || "",
          reminderType,
          sourceId: task.sourceId || "",
          crmReminderId: task.reminderId || task.id || "",
        });

        await writeTaskHistoryRecord({
          taskType:
            task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
              ? TASK_HISTORY_TYPE_INSTALLATION
              : TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: task.sourceId || task.id,
          dealLike: task.deal,
          taskTitle: task.title,
          taskDescription: task.description || "",
          dueDate: task.dueDateTime || task.dueDate || null,
          sourceModule:
            task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
              ? TASK_HISTORY_SOURCE_INSTALLATION
              : TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            dailyTask: {
              id: task.id,
              sourceType: task.sourceType,
              sourceId: task.sourceId,
              dueDate: task.dueDateKey,
              dueTime: task.dueTime,
            },
          },
        });

        notify(
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? "Installation marked done"
            : "Task marked as done",
          "success"
        );
      } catch (error) {
        logCrmTaskDoneFailure("markDailyTaskDone", error, {
          ...writeAudit,
          workspaceUid,
        });
        notify("Could not mark task as done. Please try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      getCurrentTaskHistoryActor,
      completeCrmTask,
      crmProjectRecords,
      loadCrmProjectById,
      markCrmFollowUpDone,
      markDueReminderCompleted,
      notify,
      openCrmPaymentModal,
      removeCrmAttentionTaskById,
      resolveDailyTaskWriteTarget,
      saveCrmFieldsForDeal,
      writeTaskHistoryRecord,
      workspaceUid,
    ]
  );

  const openDailyTaskReschedule = useCallback((task) => {
    if (!task) return;

    setDailyTaskRescheduleState({
      task,
      dueDate: task.dueDateKey || toDateInputValue(task.dueDate) || dailyTaskDateKey,
      dueTime: task.dueTime || DEFAULT_CRM_REMINDER_TIME,
    });
  }, [dailyTaskDateKey]);

  const saveDailyTaskReschedule = useCallback(async () => {
    const task = dailyTaskRescheduleState?.task;
    if (task?.sourceType === DAILY_TASK_SOURCE_CRM_TASK) {
      if (!dailyTaskRescheduleState?.dueDate) {
        notify("Please choose a new task date.", "warning");
        return;
      }
      const taskId = task.sourceId || task.crmTask?.taskId || task.crmTask?.id || "";
      const nextDueDate = dailyTaskRescheduleState.dueDate;
      const nextDueTime = parseTimeInputValue(dailyTaskRescheduleState.dueTime);
      const nextDueAt = combineReminderDateTime(nextDueDate, nextDueTime);
      if (!taskId || !nextDueAt) {
        notify("Could not reschedule this task. Please try again.", "error");
        return;
      }

      try {
        await updateDoc(doc(db, "users", workspaceUid, "crm_tasks", taskId), {
          dueAt: Timestamp.fromDate(nextDueAt),
          status: CRM_TASK_STATUS_OPEN,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        });
        patchCrmTaskLocalState(taskId, {
          dueAt: nextDueAt,
          dueDate: nextDueAt,
          dueDateKey: toDateInputValue(nextDueAt),
          dueTime: nextDueTime,
          status: CRM_TASK_STATUS_OPEN,
          updatedAt: new Date(),
        });
        removeCrmAttentionTaskById(taskId);
        setDailyTaskRescheduleState(null);
        notify("Task rescheduled", "success");
      } catch (error) {
        devError("Failed to reschedule CRM task:", {
          code: error?.code,
          message: error?.message,
          path: `users/${workspaceUid}/crm_tasks/${taskId}`,
        });
        notify("Could not reschedule task. Please try again.", "error");
      }
      return;
    }

    if (!task?.deal) {
      notify("Could not find that task. Please open the deal and check it manually.", "error");
      return;
    }
    if (!dailyTaskRescheduleState?.dueDate) {
      notify("Please choose a new task date.", "warning");
      return;
    }

    const nextDueDate = dailyTaskRescheduleState.dueDate;
    const nextDueTime = parseTimeInputValue(dailyTaskRescheduleState.dueTime);
    if (!parseDateInputValue(nextDueDate)) {
      notify("Please choose a valid task date.", "warning");
      return;
    }

    try {
      if (task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP) {
        const currentFollowUps = normalizeCrmFollowUps(task.deal.followUps || task.deal.crm?.followUps);
        const hasTargetFollowUp = currentFollowUps.some(
          (followUp) =>
            followUp.id === task.sourceId || followUp.reminderId === task.reminderId
        );

        if (!hasTargetFollowUp) {
          notify("Could not find that task. Please open the deal and check it manually.", "error");
          return;
        }

        const nextFollowUps = currentFollowUps.map((followUp) =>
          followUp.id === task.sourceId || followUp.reminderId === task.reminderId
            ? {
                ...followUp,
                dueDate: nextDueDate,
                dueTime: nextDueTime,
                reminderEnabled: true,
                status: CRM_FOLLOW_UP_STATUS_PENDING,
                updatedAt: new Date(),
              }
            : followUp
        );

        await persistCrmFollowUpsForDeal(
          task.deal,
          nextFollowUps,
          "Task rescheduled"
        );
      } else if (task.sourceType === DAILY_TASK_SOURCE_CHECKLIST) {
        const nextChecklistItems = updateChecklistNodeById(
          task.deal.checklistItems || [],
          task.sourceId,
          (node) => ({
            ...node,
            dueDate: nextDueDate,
            dueTime: nextDueTime,
            updatedAt: new Date(),
          })
        );

        await saveCrmFieldsForDeal(task.dealId, {
          checklistItems: nextChecklistItems,
        });
        notify("Task rescheduled", "success");
      } else {
        const reminderType =
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? CRM_REMINDER_TYPE_INSTALLATION
            : CRM_REMINDER_TYPE_TASK_DUE;
        const nextReminderConfigs = normalizeCrmReminderConfigs(
          task.deal.reminderConfigs,
          {
            nextActionDate: task.deal.nextActionDate,
            installationDate: task.deal.installationDate,
          }
        );

        nextReminderConfigs[reminderType] = {
          ...nextReminderConfigs[reminderType],
          enabled: true,
          reminderEnabled: true,
          reminderDate: nextDueDate,
          reminderTime: nextDueTime,
          status: CRM_REMINDER_STATUS_ACTIVE,
          completedAt: null,
        };

        const overrides = {
          reminderConfigs: nextReminderConfigs,
        };

        if (task.sourceType === DAILY_TASK_SOURCE_INSTALLATION) {
          overrides.installationDate = combineReminderDateTime(nextDueDate, nextDueTime);
        } else {
          overrides.nextActionDate = parseDateInputValue(nextDueDate);
        }

        await saveCrmFieldsForDeal(task.dealId, overrides);
        notify("Task rescheduled", "success");
      }

      setDailyTaskRescheduleState(null);
    } catch (error) {
      devError("Failed to reschedule task:", error);
      notify("Could not reschedule task. Please try again.", "error");
    }
  }, [
    combineReminderDateTime,
    dailyTaskRescheduleState,
    notify,
    patchCrmTaskLocalState,
    persistCrmFollowUpsForDeal,
    removeCrmAttentionTaskById,
    saveCrmFieldsForDeal,
    toDateInputValue,
    user?.uid,
    workspaceUid,
  ]);

  const getChecklistChildButtonLabel = useCallback((depth) => {
    if (depth === 1) return "Add Sub-checklist";
    if (depth === 2) return "Add Sub-sub-checklist";
    return "";
  }, []);

  const renderPipelineChecklistNode = (stage, item, depth = 1) => {
    const canAddChild = depth < CRM_CHECKLIST_MAX_DEPTH;
    const children = Array.isArray(item.children) ? item.children : [];

    return (
      <div
        key={item.id}
        style={{
          display: "grid",
          gap: 10,
          padding: 12,
          borderRadius: 16,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          border: dashboardBorder,
          marginLeft: depth > 1 ? (depth - 1) * 18 : 0,
          boxShadow:
            depth > 1
              ? `inset 3px 0 0 ${isDark ? "rgba(96,165,250,0.28)" : "rgba(37,99,235,0.16)"}`
              : "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
            gap: 10,
            alignItems: "center",
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: dashboardMuted,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {depth === 1
                ? "Checklist"
                : depth === 2
                ? "Sub-checklist"
                : "Sub-sub-checklist"}
            </div>
            <input
              value={item.label || ""}
              onChange={(e) =>
                updatePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  { label: e.target.value }
                )
              }
              style={input}
              placeholder="Checklist item"
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: isCompact ? "flex-start" : "flex-end",
            }}
          >
            {canAddChild ? (
              <button
                type="button"
                onClick={() =>
                  addPipelineChecklistChild(
                    activeCrmPipelineType,
                    stage.id,
                    item.id
                  )
                }
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
              >
                {getChecklistChildButtonLabel(depth)}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() =>
                movePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  -1
                )
              }
              style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
            >
              Up
            </button>
            <button
              type="button"
              onClick={() =>
                movePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  1
                )
              }
              style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
            >
              Down
            </button>
            <button
              type="button"
              onClick={() =>
                deletePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id
                )
              }
              style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
            >
              Delete
            </button>
          </div>
        </div>

        {children.length > 0 ? (
          <div style={{ display: "grid", gap: 10 }}>
            {children.map((child) =>
              renderPipelineChecklistNode(stage, child, depth + 1)
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmChecklistNode = (item, depth = 1) => {
    if (!selectedCrmDeal) return null;

    const progress = getChecklistNodeProgress(item);
    const childButtonLabel = getChecklistChildButtonLabel(depth);
    const canAddChild = depth < CRM_CHECKLIST_MAX_DEPTH;
    const children = Array.isArray(item.children) ? item.children : [];

    return (
      <div
        key={item.id}
        style={{
          display: "grid",
          gap: 10,
          padding: 12,
          borderRadius: 16,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          border: dashboardBorder,
          marginLeft: depth > 1 ? (depth - 1) * 18 : 0,
          boxShadow:
            depth > 1
              ? `inset 3px 0 0 ${isDark ? "rgba(96,165,250,0.28)" : "rgba(37,99,235,0.16)"}`
              : "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            gap: 12,
            alignItems: "start",
          }}
        >
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 700,
              paddingTop: 8,
            }}
          >
            <input
              type="checkbox"
              checked={progress.done}
              ref={(element) => {
                if (element) {
                  element.indeterminate = progress.partial;
                }
              }}
              onChange={(e) =>
                toggleCrmChecklistItem(item.id, e.target.checked)
              }
            />
            {progress.done ? "Done" : progress.partial ? "Partial" : "Open"}
          </label>

          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: dashboardMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {depth === 1
                  ? "Checklist"
                  : depth === 2
                  ? "Sub-checklist"
                  : "Sub-sub-checklist"}
              </div>
              <textarea
                ref={(element) => {
                  if (element) {
                    checklistInputRefs.current.set(item.id, element);
                  } else {
                    checklistInputRefs.current.delete(item.id);
                  }
                }}
                value={item.label ?? item.title ?? ""}
                onChange={(e) =>
                  updateCrmChecklistItemField(item.id, {
                    label: e.target.value,
                  })
                }
                style={{
                  ...input,
                  minHeight: 62,
                  resize: "vertical",
                  background: isDark ? "#0f172a" : "#ffffff",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.28)"
                    : "1px solid #cbd5e1",
                }}
                placeholder={
                  depth === 1
                    ? "Type checklist item..."
                    : depth === 2
                    ? "Type sub-checklist item..."
                    : "Type sub-sub-checklist item..."
                }
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 10,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Assigned Team Member
                </div>
                <select
                  value={item.assignedToUserId || ""}
                  onChange={(e) =>
                    updateCrmChecklistAssignee(
                      selectedCrmDeal.id,
                      item.id,
                      e.target.value
                    )
                  }
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(item.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id} disabled={Boolean(member.exclusionReason)}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                      {member.exclusionReason ? " | Needs reassignment" : ""}
                    </option>
                  ))}
                </select>
                {renderCrmMemberRosterStatus()}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {canAddChild ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      addCrmChecklistItem(item.id);
                    }}
                    style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                  >
                    {childButtonLabel}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    moveCrmChecklistItem(item.id, -1);
                  }}
                  style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    moveCrmChecklistItem(item.id, 1);
                  }}
                  style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                >
                  Down
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteCrmChecklistItem(item.id);
                  }}
                  style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                >
                  Delete
                </button>
                <div
                  style={{
                    fontSize: 11,
                    color: dashboardMuted,
                    fontWeight: 700,
                    marginLeft: "auto",
                  }}
                >
                  {progress.completed}/{progress.total} complete
                </div>
              </div>
            </div>
          </div>
        </div>

        {children.length > 0 ? (
          <div style={{ display: "grid", gap: 10 }}>
            {children.map((child) => renderCrmChecklistNode(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmStageSelect = (deal, extraStyle = {}) => {
    const activeStageId = String(deal.salesStageId || getDealSalesStageId(deal) || "").trim();
    const activeStage = getSalesPipelineStage(salesPipeline, activeStageId);
    const stageOptions = [
      ...salesPipelineActiveStages,
      ...(activeStage &&
      !salesPipelineActiveStages.some((stage) => stage.stageId === activeStage.stageId)
        ? [activeStage]
        : []),
    ];

    return (
      <select
        value={activeStage?.stageId || activeStageId}
        onChange={(e) => updateSalesPipelineStageForDeal(deal, e.target.value)}
        style={{
          ...selectInput,
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 12,
          background: activeStage?.color || deal.salesStageColor || "#64748b",
          color: "#ffffff",
          WebkitTextFillColor: "#ffffff",
          border: "none",
          ...extraStyle,
        }}
      >
        {stageOptions.map((stage) => (
          <option key={stage.stageId || stage.id} value={stage.stageId || stage.id}>
            {stage.label}
          </option>
        ))}
      </select>
    );
  };

  const getCanonicalCrmLeadForCard = (cardRecord = {}) => {
    const directLeadIds = [
      cardRecord.canonicalLeadId,
      cardRecord.crmLeadId,
      cardRecord.leadId,
      cardRecord.crm?.leadId,
      cardRecord.primaryLeadId,
      cardRecord.crmRecordType === "lead" ? cardRecord.id : "",
      cardRecord.id,
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);

    for (const leadId of directLeadIds) {
      const lead = crmLeadRecords.find((item) => String(item.id || "") === leadId);
      if (lead) return lead;
    }

    const quoteReference = getCrmLeadQuoteReferenceSummary(cardRecord);
    const quoteId =
      quoteReference.primaryQuoteId ||
      cardRecord.quoteId ||
      cardRecord.primaryQuoteId ||
      cardRecord.primaryLinkedQuote?.quoteId ||
      (cardRecord.crmRecordType === "quote" ? cardRecord.id : "");
    const quoteNo =
      quoteReference.primaryQuoteNo ||
      cardRecord.quoteNo ||
      cardRecord.primaryQuoteNo ||
      cardRecord.primaryLinkedQuote?.quoteNo ||
      "";

    return (
      crmLeadRecords.find((lead) =>
        doesCrmLeadReferenceQuote(lead, quoteId, quoteNo)
      ) || null
    );
  };

  const getLatestCrmLeadForCard = (cardRecord = {}) => {
    const canonicalLead = getCanonicalCrmLeadForCard(cardRecord);
    if (!canonicalLead?.id) {
      return {
        ...cardRecord,
        canonicalLeadId: cardRecord.canonicalLeadId || cardRecord.crmLeadId || cardRecord.leadId || "",
      };
    }

    const latestDeal = buildCrmLeadDetailsRecord(canonicalLead) || canonicalLead;
    return {
      ...cardRecord,
      ...latestDeal,
      id: latestDeal.id || canonicalLead.id,
      canonicalLeadId: latestDeal.id || canonicalLead.id,
      crmLeadId: latestDeal.id || canonicalLead.id,
      leadId: latestDeal.id || canonicalLead.id,
      crmRecordType: "lead",
    };
  };

  const renderCrmDealCard = (
    cardRecord,
    { dense = false, overview = false, renderSource = "DealList" } = {}
  ) => {
    const deal = getLatestCrmLeadForCard(cardRecord);
    const checklistSummary = getChecklistSummary(deal);
    const followUpSummary = getFollowUpSummary(deal, { nowMillis: Date.now() });
    const installationSummary = getInstallationSummary(deal);
    const hasOverdueTasks = Number(followUpSummary.overdue || 0) > 0;
    const installationLabel = installationSummary.installationLabel || "";
    const nextFollowUpLabel = followUpSummary.nextFollowUpLabel || "No follow-up set";
    const shouldTraceDealListCard =
      isDevRuntime &&
      renderSource === "DealList" &&
      (String(deal.id || "") === String(selectedCrmDealId || "") ||
        String(cardRecord?.id || "") === String(selectedCrmDealId || "") ||
        String(deal.id || "") === String(crmDetailsOpenDebug.lastResolvedLeadId || "") ||
        String(cardRecord?.id || "") === String(crmDetailsOpenDebug.lastRecordId || ""));

    if (shouldTraceDealListCard) {
      const canonical = getCanonicalCrmLeadForCard(cardRecord);
      devLog("DEAL LIST CARD DATA TRACE", {
        renderSource: "DealList",
        cardId: cardRecord?.id,
        canonicalLeadId: deal.canonicalLeadId,
        quoteId: cardRecord?.quoteId || cardRecord?.primaryLinkedQuote?.quoteId || "",
        quoteNo: cardRecord?.quoteNo || cardRecord?.primaryLinkedQuote?.quoteNo || "",
        customerName:
          cardRecord?.customerName ||
          cardRecord?.customerLabel ||
          cardRecord?.crmContact?.name ||
          "",
        rawChecklistItems: cardRecord?.checklistItems,
        rawChecklist: cardRecord?.checklist,
        rawStageChecklist: cardRecord?.stageChecklist,
        rawFollowUps: cardRecord?.followUps,
        rawInstallationDate: cardRecord?.installationDate,
        rawInstallationReminder: cardRecord?.installationReminder,
        checklistCompleted: cardRecord?.checklistCompleted,
        checklistTotal: cardRecord?.checklistTotal,
        followUpSummary: cardRecord?.followUpSummary,
        installationDisplay: cardRecord?.installationDisplay,
        checklistSummary,
        followUpSummaryFromHelper: followUpSummary,
        installationSummary,
      });
      devLog("DEAL LIST CANONICAL LEAD TRACE", {
        cardId: cardRecord?.id,
        canonicalFound: Boolean(canonical),
        canonicalId: canonical?.id,
        canonicalChecklistItems: canonical?.checklistItems,
        canonicalFollowUps: canonical?.followUps,
        canonicalInstallationDate: canonical?.installationDate,
        canonicalInstallationReminder: canonical?.installationReminder,
        canonicalChecklistSummary: canonical ? getChecklistSummary(canonical) : null,
      });
    }

    if (overview) {
      return (
        <div
          key={deal.id}
          style={{
            padding: 12,
            borderRadius: 16,
            border:
              selectedCrmDealId === deal.id
                ? "1px solid rgba(37,99,235,0.4)"
                : dashboardBorder,
            background:
              selectedCrmDealId === deal.id
                ? isDark
                  ? "rgba(37,99,235,0.12)"
                  : "#eff6ff"
                : dashboardSoftBg,
            display: "grid",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <button
              type="button"
              onClick={() =>
                openCrmDealDetails(
                  deal,
                  overview ? "crm_overview_card_title" : "crm_deal_list_card_title"
                )
              }
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                color: textColor,
                minWidth: 0,
                flex: "1 1 auto",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 900,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {deal.crmTitle}
              </div>
              <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                {deal.hasQuote
                  ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                  : "Lead"}{" "}
                | {deal.customerLabel || "Unnamed Customer"}
              </div>
            </button>
            <span
              style={{
                flex: "0 0 auto",
                maxWidth: 118,
                padding: "5px 9px",
                borderRadius: 999,
                background: deal.crmStageColor || "#64748b",
                color: "#ffffff",
                fontSize: 10.5,
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {deal.crmStageLabel || "Stage"}
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
              fontSize: 11,
            }}
          >
            {[
              ["Value", formatCurrencyDisplay(deal.expectedValue || 0, currency)],
              ["Tasks", followUpSummary.label],
              ["Checklist", checklistSummary.label],
              ["Install", installationLabel || "-"],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: "8px 9px",
                  borderRadius: 12,
                  background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                  border: dashboardBorder,
                  minWidth: 0,
                }}
              >
                <div style={{ color: dashboardMuted, fontWeight: 800 }}>
                  {label}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    color:
                      label === "Tasks" && hasOverdueTasks ? "#be123c" : textColor,
                    fontWeight: 900,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={() =>
                openCrmDealDetails(
                  deal,
                  overview ? "crm_overview_card_details" : "crm_deal_list_card_details"
                )
              }
              style={{
                ...btn("#0f172a"),
                marginRight: 0,
                padding: "8px 12px",
                background: isDark ? "#1e293b" : "#0f172a",
              }}
            >
              Details
            </button>
            {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
              <button
                type="button"
                onClick={() =>
                  deal.hasQuote
                    ? openCrmQuoteForDeal(deal)
                    : startQuoteFromCrmLead(deal)
                }
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  padding: "8px 12px",
                }}
              >
                {deal.hasQuote ? "Open Quote" : "Create Quote"}
              </button>
            ) : null}
            {deal.customerId ? (
              <button
                type="button"
                onClick={() => openCustomerLedger(deal.customerId)}
                style={{ ...btn("#475569"), marginRight: 0, padding: "8px 12px" }}
              >
                Open Customer
              </button>
            ) : null}
            {canEditQuotes ? (
              <>
                <button
                  type="button"
                  onClick={() => updateSalesPipelineStageForDeal(deal, CRM_OPPORTUNITY_STATUS_LOST)}
                  style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 12px" }}
                >
                  Mark Lost
                </button>
                <button
                  type="button"
                  onClick={() => updateSalesPipelineStageForDeal(deal, CRM_OPPORTUNITY_STATUS_WON)}
                  style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 12px" }}
                >
                  Mark Won
                </button>
              </>
            ) : null}
          </div>
        </div>
      );
    }

    return (
    <div
      key={deal.id}
      style={{
        padding: dense ? 14 : 16,
        borderRadius: 18,
        border:
          selectedCrmDealId === deal.id
            ? "1px solid rgba(37,99,235,0.4)"
            : dashboardBorder,
        background:
          selectedCrmDealId === deal.id
            ? isDark
              ? "rgba(37,99,235,0.12)"
              : "#eff6ff"
            : dashboardSoftBg,
        display: "grid",
        gap: 12,
        boxShadow:
          selectedCrmDealId === deal.id
            ? "0 14px 28px rgba(37,99,235,0.12)"
            : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <button
          type="button"
          onClick={() => openCrmDealDetails(deal, "crm_deal_list_card_title")}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            color: textColor,
            flex: 1,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800 }}>{deal.crmTitle}</div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
            {deal.hasQuote
              ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"} | ${deal.customerLabel}`
              : `Lead | ${deal.customerLabel}`}
          </div>
        </button>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background:
              deal.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
            color:
              deal.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
            fontSize: 11,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {getQuoteTypeLabel(deal.quoteTypeResolved)}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: dense || isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: dashboardMuted, marginBottom: 4 }}>
            {deal.hasQuote ? "Deal Value" : "Expected Value"}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            {formatCurrencyDisplay(deal.expectedValue || 0, currency)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: dashboardMuted, marginBottom: 4 }}>
            Checklist
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {checklistSummary.label}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted }}>
        <div>
          Assigned:{" "}
          <strong style={{ color: textColor }}>
            {getTeamMemberLabel(deal.assignedToUserId, deal.assignedToNameSnapshot)}
          </strong>
          {canAssignLeadOwnerAccess ? (
            <select
              aria-label={`Assign staff for ${deal.customerLabel || "deal"}`}
              value={deal.assignedToUserId || ""}
              disabled={Boolean(crmAssignmentPending[deal.id])}
              onChange={(event) => updateCrmDealAssignment(deal.id, event.target.value)}
              style={{ marginLeft: 8, maxWidth: "100%", padding: "6px 8px", borderRadius: 6 }}
            >
              <option value="">Unassigned</option>
              {getAssignableTeamMembers(deal.assignedToUserId).map((member) => (
                <option key={member.id} value={member.id} disabled={Boolean(member.exclusionReason)}>
                  {member.name || member.email || "Team member"}
                  {member.exclusionReason ? " | Needs reassignment" : ""}
                </option>
              ))}
            </select>
          ) : null}
          {renderCrmMemberRosterStatus()}
          {crmAssignmentPending[deal.id] ? <span role="status"> Saving...</span> : null}
          {canAssignLeadOwnerAccess && deal.crmRecordType === "lead" && deal.assignedToUserId ? (
            <button type="button" disabled={Boolean(crmAssignmentPending[deal.id])}
              onClick={() => reassignOpenTasksToDealOwner(deal)}
              style={{ marginLeft: 8, fontSize: 11 }}>
              Review task reassignment
            </button>
          ) : null}
          {(isCrmOwnerAdminAccess || isSuperAdmin) && deal.crmRecordType === "lead" ? (
            <button type="button" disabled={crmArchivePage.loading} onClick={() => setDealArchiveFromUi(deal, true)} style={{ marginLeft: 8, fontSize: 11 }}>Archive Deal</button>
          ) : null}
        </div>
        <div>
          Next Action:{" "}
          <strong style={{ color: textColor }}>
            {nextFollowUpLabel}
          </strong>
        </div>
        <div>
          Tasks:{" "}
          <strong style={{ color: hasOverdueTasks ? "#be123c" : textColor }}>
            {followUpSummary.label}
          </strong>
        </div>
        <div>
          Source:{" "}
          <strong style={{ color: textColor }}>
            {deal.source || deal.crmContact?.source || "-"}
          </strong>
        </div>
        <div>
          {deal.hasQuote ? "Installation:" : "Quote:"}{" "}
          <strong style={{ color: textColor }}>
            {deal.hasQuote
              ? installationLabel || "-"
              : "No Quote Yet"}
          </strong>
          {deal.hasQuote && installationSummary.reminderEnabled ? (
            <span style={{ color: "#16a34a", fontWeight: 800 }}> | Reminder on</span>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
      >
        {renderCrmStageSelect(deal, {
          minWidth: dense ? "100%" : 180,
          flex: dense ? "1 1 100%" : "0 1 200px",
        })}

        <button
          type="button"
          onClick={() => openCrmDealDetails(deal, "crm_deal_list_card_details")}
          style={{
            ...btn("#0f172a"),
            marginRight: 0,
            padding: "9px 14px",
            background: isDark ? "#1e293b" : "#0f172a",
          }}
        >
          Details
        </button>

        {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
          <button
            type="button"
            onClick={() =>
              deal.hasQuote ? openCrmQuoteForDeal(deal) : startQuoteFromCrmLead(deal)
            }
            style={{
              ...btn("#2563eb"),
              marginRight: 0,
              padding: "9px 14px",
            }}
          >
            {deal.hasQuote ? "Open Quote" : "Create Quote"}
          </button>
        ) : null}
        {deal.customerId ? (
          <button
            type="button"
            onClick={() => openCustomerLedger(deal.customerId)}
            style={{
              ...btn("#475569"),
              marginRight: 0,
              padding: "9px 14px",
            }}
          >
            Open Customer
          </button>
        ) : null}
        {canEditQuotes ? (
          <>
            <button
              type="button"
              onClick={() => updateSalesPipelineStageForDeal(deal, CRM_OPPORTUNITY_STATUS_LOST)}
              style={{
                ...btn("#ef4444"),
                marginRight: 0,
                padding: "9px 14px",
              }}
            >
              Mark Lost
            </button>
            <button
              type="button"
              onClick={() => updateSalesPipelineStageForDeal(deal, CRM_OPPORTUNITY_STATUS_WON)}
              style={{
                ...btn("#16a34a"),
                marginRight: 0,
                padding: "9px 14px",
              }}
            >
              Mark Won
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
  };

  const renderCrmDetailPanel = () => {
    const renderDetailsDebugPanel = () => {
      if (!IS_DEV_UI_ENABLED) return null;

      const cachedDeal = selectedCrmDealCacheRef.current;
      const selectedExistsInRecords = crmLeadRecords.some(
        (lead) => String(lead.id || "") === String(selectedCrmDealId || "")
      );
      const debugRows = [
        ["drawerOpen", String(isCrmDrawerPinned || isCrmDrawerHovered || isCompact)],
        ["selectedCrmDealId", selectedCrmDealId || "-"],
        ["selectedCrmDealCache.id", cachedDeal?.id || "-"],
        ["selectedCrmDeal.id", selectedCrmDeal?.id || "-"],
        [
          "selectedCrmDeal.customerName",
          selectedCrmDeal?.customerName ||
            selectedCrmDeal?.customerLabel ||
            selectedCrmDeal?.crmContact?.name ||
            "-",
        ],
        ["crmLeadRecords count", String(crmLeadRecords.length)],
        ["exists in crmLeadRecords", String(selectedExistsInRecords)],
        ["lastDetailsOpenSource", crmDetailsOpenDebug.lastSource || "-"],
        ["lastDetailsOpenRecordId", crmDetailsOpenDebug.lastRecordId || "-"],
        ["lastDetailsResolvedLeadId", crmDetailsOpenDebug.lastResolvedLeadId || "-"],
        ["lastDetailsResolveReason", crmDetailsOpenDebug.lastResolveReason || "-"],
        ["lastDetailsError", crmDetailsOpenDebug.lastError || "-"],
      ];

      return (
        <div
          style={{
            padding: 12,
            borderRadius: 16,
            border: "1px solid rgba(59,130,246,0.35)",
            background: isDark ? "rgba(30,64,175,0.16)" : "#eff6ff",
            display: "grid",
            gap: 8,
            fontSize: 12,
            color: dashboardMuted,
          }}
        >
          <button
            type="button"
            onClick={() => setCrmDetailsDebugExpanded((prev) => !prev)}
            style={{
              ...btn(isDark ? "#1e3a8a" : "#2563eb", "#ffffff"),
              marginRight: 0,
              justifyContent: "space-between",
            }}
          >
            Details Debug {crmDetailsDebugExpanded ? "Hide" : "Show"}
          </button>
          {crmDetailsDebugExpanded ? (
            <div style={{ display: "grid", gap: 4 }}>
              {debugRows.map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(120px, 0.45fr) minmax(0, 1fr)",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <strong style={{ color: textColor }}>{label}</strong>
                  <span style={{ wordBreak: "break-word" }}>{value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      );
    };

    if (!selectedCrmDeal || !crmDetailDraft) {
      if (selectedCrmDealId || selectedCrmDealCacheRef.current) {
        return (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.7 }}>
              Loading the selected deal details...
            </div>
            {renderDetailsDebugPanel()}
          </div>
        );
      }

      return (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.7 }}>
            Select a CRM deal to view contact details, checklist progress, notes, and next actions.
          </div>
          {renderDetailsDebugPanel()}
        </div>
      );
    }

    const checklistProgress = getChecklistProgress(crmDetailDraft.checklistItems || []);
    const nextActionDate = parseDateInputValue(crmDetailDraft.nextActionDate);
    const installationDate = parseDateInputValue(crmDetailDraft.installationDate);
    const reminderConfigs = normalizeCrmReminderConfigs(
      crmDetailDraft.reminderConfigs,
      {
        nextActionDate,
        installationDate,
      }
    );
      const attachments = normalizeCrmAttachments(crmDetailDraft.attachments || []);
      const followUps = normalizeCrmFollowUps(crmDetailDraft.followUps || [], {
        nextAction: crmDetailDraft.nextAction || "",
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      });
      const linkedQuote = findLinkedQuoteForLead(selectedCrmDeal);
      const selectedPrimaryLinkedQuote = getPrimaryCrmLinkedQuote(
        selectedCrmDeal.linkedQuotes || []
      );
      const linkedQuoteMeta = linkedQuote
        ? getQuoteLinkDisplayData(linkedQuote)
        : {
            quoteNo: selectedPrimaryLinkedQuote?.quoteNo || selectedCrmDeal.quoteNo || "",
            quoteType: selectedPrimaryLinkedQuote?.type || selectedCrmDeal.quoteTypeResolved || selectedCrmDeal.pipelineType || "",
            quoteTypeLabel:
              selectedPrimaryLinkedQuote?.type === GENERAL_QUOTE_TYPE
                ? "General"
                : selectedPrimaryLinkedQuote?.type === CURTAIN_QUOTE_TYPE
                ? "Curtain"
                : selectedCrmDeal.pipelineType === GENERAL_QUOTE_TYPE
                ? "General"
                : "Curtain",
            quoteDateLabel: "-",
            totalValue: Number(
              selectedPrimaryLinkedQuote?.value ||
                selectedCrmDeal.finalValue ||
                selectedCrmDeal.expectedValue ||
                0
            ),
          };
      const executionSpecs = Array.isArray(crmDetailDraft.executionSpecs)
        ? crmDetailDraft.executionSpecs
        : [];
    const legacyStageId = crmDetailDraft.stageId || selectedCrmDeal.crmStageId;
    const legacyStageTemplate = getCrmStage(
      crmPipelines,
      selectedCrmDeal.pipelineType,
      legacyStageId
    );
    const activeSalesStageId = String(
      selectedCrmDeal.salesStageId || getDealSalesStageId(selectedCrmDeal) || ""
    ).trim();
    const salesStageTemplate = getSalesPipelineStage(salesPipeline, activeSalesStageId);
    const salesStageOptions = [
      ...salesPipelineActiveStages,
      ...(salesStageTemplate &&
      !salesPipelineActiveStages.some((stage) => stage.stageId === salesStageTemplate.stageId)
        ? [salesStageTemplate]
        : []),
    ];
    const sectionCardStyle = {
      padding: 16,
      borderRadius: 18,
      background: dashboardSoftBg,
      border: dashboardBorder,
      display: "grid",
      gap: 12,
    };
    const siteLocations = normalizeSiteLocations(crmDetailDraft.siteLocations || []);
    const selectedCustomerId = String(
      selectedCrmDeal.customerId || selectedCrmDeal.crm?.customerId || ""
    ).trim();
    const resetCrmSiteLocationDraft = () =>
      setCrmSiteLocationDraft(createSiteLocationDraft());
    const persistCrmSiteLocations = async (nextLocations) => {
      const normalizedLocations = normalizeSiteLocations(nextLocations);
      updateCrmDetailDraft((prev) => ({
        ...prev,
        siteLocations: normalizedLocations,
      }));

      const leadId =
        selectedCrmDeal.crmRecordType === "lead"
          ? selectedCrmDeal.id
          : String(selectedCrmDeal.crm?.leadId || selectedCrmDeal.leadId || "").trim();

      try {
        if (leadId) {
          await saveCrmFieldsForLead(
            leadId,
            { siteLocations: normalizedLocations },
            {
              sourceLead:
                selectedCrmDeal.crmRecordType === "lead" ? selectedCrmDeal : null,
            }
          );
        } else {
          await saveCrmFieldsForDeal(selectedCrmDeal.id, {
            siteLocations: normalizedLocations,
          });
        }
        notify("Site location saved", "success");
      } catch (error) {
        devError("Failed to save CRM site location:", error, {
          dealId: selectedCrmDeal.id,
          leadId,
        });
        notify("Could not save site location. Please try again.", "error");
      }
    };
    const saveCrmSiteLocationDraft = async () => {
      const hasLocationContent = [
        crmSiteLocationDraft.label,
        crmSiteLocationDraft.addressText,
        crmSiteLocationDraft.googleMapsUrl,
        crmSiteLocationDraft.notes,
      ].some((value) => String(value || "").trim());

      if (!hasLocationContent) {
        notify("Add a label, address, or map link before saving.", "warning");
        return;
      }

      const nowIso = new Date().toISOString();
      const userId = user?.uid || user?.email || "";
      const nextLocation = normalizeSiteLocation(
        {
          ...crmSiteLocationDraft,
          id: crmSiteLocationDraft.id || createClientId("site-location"),
          createdAt: crmSiteLocationDraft.createdAt || nowIso,
          createdBy: crmSiteLocationDraft.createdBy || userId,
          updatedAt: nowIso,
          updatedBy: userId,
        },
        siteLocations.length
      );
      const nextLocations = siteLocations.some(
        (location) => location.id === nextLocation.id
      )
        ? siteLocations.map((location) =>
            location.id === nextLocation.id
              ? nextLocation
              : nextLocation.isPrimary
              ? { ...location, isPrimary: false }
              : location
          )
        : [
            ...siteLocations.map((location) =>
              nextLocation.isPrimary ? { ...location, isPrimary: false } : location
            ),
            nextLocation,
          ];

      await persistCrmSiteLocations(nextLocations);
      resetCrmSiteLocationDraft();
    };
    const editCrmSiteLocation = (location) => {
      setCrmSiteLocationDraft(createSiteLocationDraft(location));
    };
    const deleteCrmSiteLocation = async (locationId) => {
      await persistCrmSiteLocations(
        siteLocations.filter((location) => location.id !== locationId)
      );
    };
    const copyCrmLocationToCustomerLedger = async (location) => {
      if (!selectedCustomerId) {
        notify("Link this deal to a customer before syncing the location.", "info");
        return;
      }
      const customer = customerRecords.find(
        (entry) => entry.id === selectedCustomerId
      );
      const currentLocations = normalizeSiteLocations(customer?.siteLocations || []);
      const nextLocation = normalizeSiteLocation({
        ...location,
        id: createClientId("customer-site-location"),
        isPrimary: currentLocations.length === 0 || location.isPrimary === true,
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || user?.email || "",
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || user?.email || "",
      });
      const saved = await saveCustomerSiteLocations(selectedCustomerId, [
        ...currentLocations,
        nextLocation,
      ]);
      if (saved) notify("Location saved to customer ledger", "success");
    };

    const renderReminderManager = (
      reminderType,
      {
        dueFieldLabel,
        dueValue,
        helperText,
      }
    ) => {
      const rawConfig = reminderConfigs[reminderType];
      const config =
        reminderType === CRM_REMINDER_TYPE_INSTALLATION
          ? buildCanonicalInstallationReminder(
              {
                ...crmDetailDraft,
                installationReminder:
                  crmDetailDraft.installationReminder ||
                  crmDetailDraft.reminderConfig ||
                  rawConfig,
                reminderConfig: crmDetailDraft.reminderConfig || rawConfig,
              },
              {
                installationDate: parseDateInputValue(crmDetailDraft.installationDate),
                reminderConfigs,
              }
            )
          : rawConfig;

      return (
        <div
          style={{
            padding: 14,
            borderRadius: 16,
            background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
            border: dashboardBorder,
            display: "grid",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                {getCrmReminderTypeLabel(reminderType)}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                {helperText}
              </div>
            </div>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <input
                type="checkbox"
                checked={
                  reminderType === CRM_REMINDER_TYPE_INSTALLATION
                    ? Boolean(crmDetailDraft.installationReminder?.enabled)
                    : config?.reminderEnabled === true
                }
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    enabled: event.target.checked,
                    reminderEnabled: event.target.checked,
                    status: event.target.checked
                      ? CRM_REMINDER_STATUS_ACTIVE
                      : CRM_REMINDER_STATUS_DISABLED,
                    completedAt: event.target.checked ? null : config?.completedAt || null,
                  })
                }
              />
              Reminder Enabled
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10,
            }}
          >
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Reminder Date
              </div>
              <input
                type="date"
                value={config?.reminderDate ?? ""}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    reminderDate: event.target.value,
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...input, width: "100%", minWidth: 0 }}
              />
            </div>
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Reminder Time
              </div>
              <input
                type="time"
                value={config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    reminderTime: parseTimeInputValue(event.target.value),
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...input, width: "100%", minWidth: 0 }}
              />
            </div>
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Repeat
              </div>
              <select
                value={config?.repeatKey ?? "daily"}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    repeatKey: event.target.value,
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...selectInput, width: "100%", minWidth: 0 }}
              >
                {CRM_REMINDER_REPEAT_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <span>
              {dueFieldLabel}: <strong style={{ color: textColor }}>{dueValue || "-"}</strong>
            </span>
            <span>
              Reminder fires at{" "}
              <strong style={{ color: textColor }}>
                {config?.reminderDate
                  ? formatReminderDateTime(
                      combineReminderDateTime(
                        config?.reminderDate,
                        config?.reminderTime
                      )
                    )
                  : "-"}
              </strong>
            </span>
            {config?.status === CRM_REMINDER_STATUS_DONE ? (
              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: isDark ? "rgba(22,163,74,0.18)" : "#dcfce7",
                  color: isDark ? "#86efac" : "#166534",
                  fontWeight: 700,
                }}
              >
                Marked Done
              </span>
            ) : null}
          </div>
        </div>
      );
    };

    const debugStringify = (value) => {
      try {
        return JSON.stringify(
          value ?? null,
          (_key, item) => {
            if (item instanceof Date) return item.toISOString();
            if (item && typeof item.toDate === "function") {
              try {
                return item.toDate().toISOString();
              } catch (_error) {
                return item;
              }
            }
            return item;
          },
          2
        );
      } catch (error) {
        return `Could not render debug JSON: ${error?.message || error}`;
      }
    };
    const getDebugChecklistItems = (source) =>
      source?.checklistItems || source?.checklist || source?.stageChecklist || [];
    const renderDebugJson = (value) => (
      <pre
        style={{
          margin: 0,
          padding: 10,
          borderRadius: 12,
          background: isDark ? "rgba(15,23,42,0.78)" : "#f8fafc",
          border: dashboardBorder,
          color: isDark ? "#e2e8f0" : "#0f172a",
          fontSize: 11,
          lineHeight: 1.45,
          maxHeight: 180,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {debugStringify(value)}
      </pre>
    );
    const renderCrmSaveDebugPanel = () => {
      if (!IS_DEV_UI_ENABLED) return null;

      const draftChecklistProgress = getChecklistProgress(
        crmDetailDraft?.checklistItems || []
      );
      const payloadChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastPayload)
      );
      const immediateChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastImmediateReadback)
      );
      const delayedChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastDelayedReadback)
      );
      const selectedCrmDealCache = selectedCrmDealCacheRef.current;

      return (
        <div
          style={{
            padding: 14,
            borderRadius: 18,
            border: "1px solid rgba(245,158,11,0.35)",
            background: isDark ? "rgba(120,53,15,0.18)" : "#fffbeb",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setCrmSaveDebugExpanded((prev) => !prev)}
              style={{
                ...btn(isDark ? "#78350f" : "#f59e0b", isDark ? "#fde68a" : "#111827"),
                marginRight: 0,
                justifyContent: "space-between",
              }}
            >
              CRM Save Debug {crmSaveDebugExpanded ? "Hide" : "Show"}
            </button>
            <button
              type="button"
              onClick={testDirectCrmDetailsSave}
              style={{
                ...btn(isDark ? "#0f172a" : "#111827", "#ffffff"),
                marginRight: 0,
              }}
            >
              Test Direct Save
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 10,
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <div>
              <strong style={{ color: textColor }}>Selected identity</strong>
              <div>workspaceUid: {workspaceUid || "-"}</div>
              <div>selectedCrmDealId: {selectedCrmDealId || "-"}</div>
              <div>selectedCrmDealCache.id: {selectedCrmDealCache?.id || "-"}</div>
              <div>selectedCrmDeal.id: {selectedCrmDeal?.id || "-"}</div>
              <div>
                path:{" "}
                {selectedCrmDealId
                  ? `users/${workspaceUid}/crm_leads/${selectedCrmDealId}`
                  : "-"}
              </div>
            </div>
            <div>
              <strong style={{ color: textColor }}>Verification</strong>
              <div>status: {crmSaveDebugState.lastVerification?.status || "-"}</div>
              <div>last write path: {crmSaveDebugState.lastWritePath || "-"}</div>
              <div>
                immediate source:{" "}
                {crmSaveDebugState.lastVerification?.immediateLastWriteSource || "-"}
              </div>
              <div>
                delayed source:{" "}
                {crmSaveDebugState.lastVerification?.delayedLastWriteSource || "-"}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            {[
              ["Draft", draftChecklistProgress],
              ["Payload", payloadChecklistProgress],
              ["Read-back", immediateChecklistProgress],
              ["Delayed", delayedChecklistProgress],
            ].map(([label, progress]) => (
              <div
                key={label}
                style={{
                  padding: 10,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
                  border: dashboardBorder,
                  fontSize: 12,
                }}
              >
                <div style={{ color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                <div style={{ marginTop: 4, color: textColor, fontWeight: 900 }}>
                  {progress.completed}/{progress.total} checklist
                </div>
              </div>
            ))}
          </div>
          {crmSaveDebugExpanded ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Draft values
                </div>
                {renderDebugJson({
                  checklist: crmDetailDraft?.checklist,
                  checklistItems: crmDetailDraft?.checklistItems,
                  stageChecklist: crmDetailDraft?.stageChecklist,
                  installationDate: crmDetailDraft?.installationDate,
                  installationReminder: crmDetailDraft?.installationReminder,
                  reminderConfig: crmDetailDraft?.reminderConfig,
                  reminderConfigsInstallation:
                    crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION],
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Last save payload
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastPayload?.checklist,
                  checklistItems: crmSaveDebugState.lastPayload?.checklistItems,
                  stageChecklist: crmSaveDebugState.lastPayload?.stageChecklist,
                  installationReminder:
                    crmSaveDebugState.lastPayload?.installationReminder,
                  reminderConfig: crmSaveDebugState.lastPayload?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastPayload?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Immediate Firestore read-back
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastImmediateReadback?.checklist,
                  checklistItems:
                    crmSaveDebugState.lastImmediateReadback?.checklistItems,
                  stageChecklist:
                    crmSaveDebugState.lastImmediateReadback?.stageChecklist,
                  installationDate:
                    crmSaveDebugState.lastImmediateReadback?.installationDate,
                  installationReminder:
                    crmSaveDebugState.lastImmediateReadback?.installationReminder,
                  reminderConfig:
                    crmSaveDebugState.lastImmediateReadback?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastImmediateReadback?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                  updatedAt: crmSaveDebugState.lastImmediateReadback?.updatedAt,
                  _lastWriteSource:
                    crmSaveDebugState.lastImmediateReadback?._lastWriteSource,
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Delayed Firestore read-back
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastDelayedReadback?.checklist,
                  checklistItems:
                    crmSaveDebugState.lastDelayedReadback?.checklistItems,
                  stageChecklist:
                    crmSaveDebugState.lastDelayedReadback?.stageChecklist,
                  installationDate:
                    crmSaveDebugState.lastDelayedReadback?.installationDate,
                  installationReminder:
                    crmSaveDebugState.lastDelayedReadback?.installationReminder,
                  reminderConfig:
                    crmSaveDebugState.lastDelayedReadback?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastDelayedReadback?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                  updatedAt: crmSaveDebugState.lastDelayedReadback?.updatedAt,
                  _lastWriteSource:
                    crmSaveDebugState.lastDelayedReadback?._lastWriteSource,
                })}
              </div>
            </div>
          ) : null}
      </div>
    );
  };

    const opportunityHeader = {
      customerName:
        selectedCrmDeal.customerLabel ||
        selectedCrmDeal.customerName ||
        crmDetailDraft.contact?.name ||
        "Unnamed Customer",
      phone:
        crmDetailDraft.contact?.phone ||
        crmDetailDraft.contact?.whatsapp ||
        selectedCrmDeal.customerPhone ||
        "-",
      stageLabel:
        salesStageTemplate?.label ||
        selectedCrmDeal.salesStageLabel ||
        getOpportunityStageLabel(activeSalesStageId),
      ownerLabel: getTeamMemberLabel(
        crmDetailDraft.assignedToUserId,
        crmDetailDraft.assignedToNameSnapshot
      ),
      nextAction: crmDetailDraft.nextAction || "No next action",
      nextActionDue: nextActionDate ? formatCrmDate(nextActionDate) : "No date set",
      estimatedValue: Number(
        selectedCrmDeal.expectedValue ||
          selectedCrmDeal.finalValue ||
          selectedPrimaryLinkedQuote?.value ||
          0
      ),
    };

    const drawerSectionData =
      crmOpportunityDrawerData.opportunityId === selectedCrmOpportunityId
        ? crmOpportunityDrawerData
        : {
            quotes: [],
            tasks: [],
            activity: [],
            loading: { quotes: false, tasks: false, activity: false },
            errors: { quotes: "", tasks: "", activity: "" },
          };
    const selectedOpportunityProjectId = String(
      selectedCrmDeal.projectId ||
        selectedCrmDeal.raw?.projectId ||
        crmDetailDraft.projectId ||
        ""
    ).trim();
    const selectedOpportunityProject =
      crmProjectRecords.find(
        (project) =>
          String(project.projectId || project.id) === String(selectedOpportunityProjectId)
      ) || null;

    const handleDrawerAddNote = async () => {
      if (!selectedCrmOpportunityId) {
        notify("Save or open an opportunity before adding notes.", "warning");
        return;
      }
      const note = window.prompt("Add a note for this opportunity");
      if (!String(note || "").trim()) return;
      const entry = await logQuoteActivitySafely({
        relatedType: "opportunity",
        relatedId: selectedCrmOpportunityId,
        opportunityId: selectedCrmOpportunityId,
        customerId: selectedCustomerId,
        customerName: opportunityHeader.customerName,
        activityType: "note",
        title: "Note added",
        description: note,
      });
      if (entry) {
        setCrmOpportunityDrawerData((prev) => ({
          ...prev,
          opportunityId: selectedCrmOpportunityId,
          activity: [
            normalizeCrmActivityRecord(entry.activityId || createClientId("activity"), {
              ...entry,
              createdAt: new Date(),
            }),
            ...(prev.activity || []),
          ].slice(0, 20),
        }));
        setCrmDetailActiveTab("activity");
        notify("Note added", "success");
      }
    };

    const handleDrawerAddFollowUp = async () => {
      if (!selectedCrmOpportunityId) {
        notify("Save or open an opportunity before adding follow-ups.", "warning");
        return;
      }
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dueDateInput = window.prompt(
        "Follow-up date (YYYY-MM-DD)",
        toDateInputValue(tomorrow)
      );
      if (!String(dueDateInput || "").trim()) return;
      const dueDate = parseDateInputValue(dueDateInput);
      if (!dueDate) {
        notify("Enter a valid follow-up date.", "warning");
        return;
      }
      const title =
        window.prompt("Follow-up task", "Follow up customer") || "Follow up customer";
      const actor = getCrmActorSnapshot();
      const taskId = `${selectedCrmOpportunityId}__manual_${Date.now()}`;
      const task = await createCrmTask({
        taskId,
        relatedType: "opportunity",
        relatedId: selectedCrmOpportunityId,
        opportunityId: selectedCrmOpportunityId,
        customerId: selectedCustomerId,
        customerName: opportunityHeader.customerName,
        taskType: "follow_up",
        title,
        ownerUid: crmDetailDraft.assignedToUserId || actor.uid || "",
        ownerName:
          getTeamMemberLabel(
            crmDetailDraft.assignedToUserId,
            crmDetailDraft.assignedToNameSnapshot
          ) || actor.name,
        dueAt: Timestamp.fromDate(dueDate),
        status: CRM_TASK_STATUS_OPEN,
        priority: "normal",
        sourceKind: "manual_follow_up",
      });
      if (task) {
        setCrmOpportunityDrawerData((prev) => ({
          ...prev,
          opportunityId: selectedCrmOpportunityId,
          tasks: [
            normalizeCrmTaskRecord(taskId, {
              ...task,
              dueAt: dueDate,
            }),
            ...(prev.tasks || []),
          ].slice(0, 20),
        }));
        setCrmDetailActiveTab("tasks");
        notify("Follow-up added", "success");
      }
    };

    const handleDrawerMarkLost = async () => {
      if (!selectedCrmOpportunityId || !selectedCrmDeal) {
        notify("Save or open an opportunity before marking it lost.", "warning");
        return;
      }
      await updateSalesPipelineStageForDeal(selectedCrmDeal, CRM_OPPORTUNITY_STATUS_LOST);
    };

    const handleDrawerMarkWon = async () => {
      if (!selectedCrmOpportunityId) {
        notify("Save or open an opportunity before marking it won.", "warning");
        return;
      }
      openOrderHandoverModal({
        source: "opportunity",
        opportunity: {
          ...selectedCrmDeal,
          ...(selectedCrmDeal?.raw || {}),
          opportunityId: selectedCrmOpportunityId,
          ownerUid: selectedCrmDeal?.assignedToUserId || selectedCrmDeal?.ownerUid || "",
          ownerName:
            selectedCrmDeal?.assignedToNameSnapshot ||
            selectedCrmDeal?.ownerName ||
            "",
        },
        quoteIndex: selectedPrimaryLinkedQuote || findLinkedQuoteForLead(selectedCrmDeal) || null,
      });
    };

    const renderOpportunityDrawerTabContent = () => {
              if (crmDetailActiveTab === "overview") {
        return (
          <div
            style={{ display: "grid", gap: 12 }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {[
                ["Phone", opportunityHeader.phone],
                ["Stage", opportunityHeader.stageLabel],
                ["Owner", opportunityHeader.ownerLabel],
                ["Next action", opportunityHeader.nextAction],
                ["Due date", opportunityHeader.nextActionDue],
                ["Estimated value", formatCurrencyDisplay(opportunityHeader.estimatedValue, currency)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                    border: dashboardBorder,
                  }}
                >
                  <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 900, marginTop: 5, color: textColor }}>
                    {value || "-"}
                  </div>
                </div>
              ))}
            </div>
            {selectedOpportunityProjectId ? (
              <div style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg, display: "grid", gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: textColor }}>
                  Order / Project
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                  {selectedOpportunityProject?.projectTitle || "Order handover created"} |{" "}
                  {selectedOpportunityProject?.stageLabel || "Project linked"}
                  {selectedOpportunityProject?.paymentStatusLabel
                    ? ` | ${selectedOpportunityProject.paymentStatusLabel}`
                    : ""}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCrmProjectId(selectedOpportunityProjectId);
                    setCrmProjectDrawerTab("overview");
                    setCrmView("projects");
                  }}
                  style={{ ...btn("#0f766e"), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}
                >
                  Open project
                </button>
              </div>
            ) : null}
          </div>
        );
      }

      if (crmDetailActiveTab === "quotes") {
        if (drawerSectionData.loading?.quotes) {
          return <div style={{ color: dashboardMuted, fontSize: 13 }}>Loading quotes...</div>;
        }
        if (drawerSectionData.errors?.quotes) {
          return <div style={{ color: "#dc2626", fontSize: 13 }}>{drawerSectionData.errors.quotes}</div>;
        }
        if (!drawerSectionData.quotes.length) {
          return <div style={{ color: dashboardMuted, fontSize: 13 }}>No quotes connected yet.</div>;
        }
        return (
          <div style={{ display: "grid", gap: 10 }}>
            {drawerSectionData.quotes.map((quote) => (
              <div
                key={quote.quoteIndexId}
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontWeight: 900 }}>#{quote.quoteNo || quote.quoteId}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3 }}>
                    {getQuoteTypeLabel(quote.quoteType)} | {formatCurrencyDisplay(quote.totalAmount || 0, currency)}
                    {quote.followUpDueAt ? ` | Follow-up ${formatCrmDate(quote.followUpDueAt)}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => loadFullQuote(quote.quoteId || quote.quoteIndexId)}
                  style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                >
                  Open quote
                </button>
              </div>
            ))}
          </div>
        );
      }

      if (crmDetailActiveTab === "tasks") {
        if (drawerSectionData.loading?.tasks) {
          return <div style={{ color: dashboardMuted, fontSize: 13 }}>Loading tasks...</div>;
        }
        if (drawerSectionData.errors?.tasks) {
          return <div style={{ color: "#dc2626", fontSize: 13 }}>{drawerSectionData.errors.tasks}</div>;
        }
        if (!drawerSectionData.tasks.length) {
          return (
            <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
              No tasks connected yet. Add a follow-up or next action so this customer is not missed.
            </div>
          );
        }
        return (
          <div style={{ display: "grid", gap: 10 }}>
            {drawerSectionData.tasks.map((task) => (
              <div
                key={task.taskId || task.id}
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontWeight: 900 }}>{task.title || "CRM task"}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3 }}>
                    Due {formatCrmDate(task.dueAt || task.dueDate)} | {task.ownerName || "Unassigned"} | {task.status}
                  </div>
                </div>
                {task.status !== CRM_TASK_STATUS_COMPLETED ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await completeCrmTask(task.taskId || task.id, task);
                      setCrmOpportunityDrawerData((prev) => ({
                        ...prev,
                        tasks: (prev.tasks || []).map((entry) =>
                          String(entry.taskId || entry.id) === String(task.taskId || task.id)
                            ? {
                                ...entry,
                                status: CRM_TASK_STATUS_COMPLETED,
                                completedAt: new Date(),
                              }
                            : entry
                        ),
                      }));
                    }}
                    style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 12px" }}
                  >
                    Mark as Done
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }

      if (drawerSectionData.loading?.activity) {
        return <div style={{ color: dashboardMuted, fontSize: 13 }}>Loading activity...</div>;
      }
      if (drawerSectionData.errors?.activity) {
        return <div style={{ color: "#dc2626", fontSize: 13 }}>{drawerSectionData.errors.activity}</div>;
      }
      if (!drawerSectionData.activity.length) {
        return (
          <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
            No activity yet. Follow-ups, stage changes, and task updates will appear here.
          </div>
        );
      }
      return (
        <div style={{ display: "grid", gap: 10 }}>
          {drawerSectionData.activity.map((entry) => (
            <div
              key={entry.activityId || entry.id}
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontWeight: 900 }}>{entry.title || "Activity"}</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                {formatCrmDate(entry.createdAt)} {entry.createdByName ? `| ${entry.createdByName}` : ""}
              </div>
              {entry.description ? (
                <div style={{ fontSize: 13, color: textColor, lineHeight: 1.55 }}>
                  {entry.description}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      );
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexDirection: isCompact ? "column" : "row",
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              {selectedCrmDeal.crmTitle}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {selectedCrmDeal.hasQuote
                ? `#${selectedPrimaryLinkedQuote?.quoteNo || selectedCrmDeal.quoteNo || "-"} | ${selectedCrmDeal.customerLabel}`
                : `Lead | ${selectedCrmDeal.customerLabel}`}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                if (selectedCrmDeal.customerId) {
                  openCustomerLedger(selectedCrmDeal.customerId);
                  return;
                }
                crmContactSectionRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              View Customer
            </button>
            {(selectedCrmDeal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
              <button
                type="button"
                onClick={() =>
                  selectedCrmDeal.hasQuote
                    ? openCrmQuoteForDeal(selectedCrmDeal)
                    : startQuoteFromCrmLead(selectedCrmDeal)
                }
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                {selectedCrmDeal.hasQuote ? "Open Quote" : "Create Quote"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleSaveCrmDetailsCanonical}
              disabled={isSavingCrmDetails}
              style={{ ...btn("#16a34a"), marginRight: 0 }}
            >
              {isSavingCrmDetails ? "Saving..." : "Save CRM Details"}
            </button>
            {crmDetailsSavedAt ? (
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                Saved {crmDetailsSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            ) : null}
          </div>
        </div>

        {renderDetailsDebugPanel()}

        {renderCrmSaveDebugPanel()}

        <div style={{ ...sectionCardStyle, gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              alignItems: isCompact ? "stretch" : "flex-start",
              flexDirection: isCompact ? "column" : "row",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 950, color: textColor }}>
                {opportunityHeader.customerName}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                {opportunityHeader.phone} | {opportunityHeader.stageLabel} | Owner: {opportunityHeader.ownerLabel}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Next action: <strong style={{ color: textColor }}>{opportunityHeader.nextAction}</strong> |{" "}
                Due: <strong style={{ color: textColor }}>{opportunityHeader.nextActionDue}</strong> |{" "}
                Value: <strong style={{ color: textColor }}>{formatCurrencyDisplay(opportunityHeader.estimatedValue, currency)}</strong>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={handleDrawerAddNote}
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
              >
                Add Note
              </button>
              <button
                type="button"
                onClick={handleDrawerAddFollowUp}
                style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 12px" }}
              >
                Add Follow-up
              </button>
              {canCreateQuotes ? (
                <button
                  type="button"
                  onClick={() => startQuoteFromCrmLead(selectedCrmDeal)}
                  style={{ ...btn("#7c3aed"), marginRight: 0, padding: "8px 12px" }}
                >
                  Create Quote
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleDrawerMarkLost}
                style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 12px" }}
              >
                Mark Lost
              </button>
              <button
                type="button"
                onClick={() => {
                  if (selectedOpportunityProjectId) {
                    setSelectedCrmProjectId(selectedOpportunityProjectId);
                    setCrmProjectDrawerTab("overview");
                    setCrmView("projects");
                    return;
                  }
                  handleDrawerMarkWon();
                }}
                style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 12px" }}
              >
                {selectedOpportunityProjectId ? "Open Project" : "Create Order"}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              ["overview", "Overview"],
              ["tasks", "Tasks"],
              ["quotes", "Quotes"],
              ["activity", "Activity"],
            ].map(([key, label]) => {
              const active = crmDetailActiveTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCrmDetailActiveTab(key)}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 13px",
                    background: active
                      ? "#2563eb"
                      : isDark
                      ? "rgba(255,255,255,0.08)"
                      : "#e2e8f0",
                    color: active ? "#ffffff" : textColor,
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {renderOpportunityDrawerTabContent()}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 14,
          }}
        >
          <div style={{ ...sectionCardStyle, gridTemplateColumns: "1fr", gap: 14 }}>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Current Stage
                </div>
                <select
                  value={salesStageTemplate?.stageId || activeSalesStageId}
                  onChange={(e) => updateSalesPipelineStageForDeal(selectedCrmDeal, e.target.value)}
                  style={selectInput}
                >
                  {salesStageOptions.map((stage) => (
                    <option key={stage.stageId || stage.id} value={stage.stageId || stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: dashboardMuted }}>
                  Sales pipeline stage changes update this deal, linked quote metadata, and activity.
                </div>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Assigned Team Member
                </div>
                <select
                  value={crmDetailDraft.assignedToUserId || ""}
                  onChange={(e) => updateCrmDealAssignment(selectedCrmDeal.id, e.target.value)}
                  disabled={!canAssignLeadOwnerAccess}
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(crmDetailDraft.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id} disabled={Boolean(member.exclusionReason)}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                      {member.exclusionReason ? " | Needs reassignment" : ""}
                    </option>
                  ))}
                </select>
                {renderCrmMemberRosterStatus()}
                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 2 }}>
                  {!canAssignLeadOwnerAccess
                    ? "Only managers/admins can assign owners."
                    : `Current assignee: ${getTeamMemberLabel(
                        crmDetailDraft.assignedToUserId,
                        crmDetailDraft.assignedToNameSnapshot
                      )}`}
                </div>
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Linked Quote</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                  Connect this lead to an existing curtain or general quotation.
                </div>
              </div>
              {selectedCrmDeal.crmRecordType === "lead" ? (
                <button
                  type="button"
                  onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  <Link2 size={14} style={{ marginRight: 6 }} />
                  {selectedCrmDeal.hasQuote ? "Change Quote" : "Link Quote"}
                </button>
              ) : null}
            </div>

            {selectedCrmDeal.hasQuote ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>
                      #{linkedQuoteMeta.quoteNo || "-"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {linkedQuoteMeta.quoteTypeLabel || "Quotation"} |{" "}
                      {currencySymbol(currency)}{" "}
                      {Number(linkedQuoteMeta.totalValue || 0).toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "7px 11px",
                      borderRadius: 999,
                      background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                      color: isDark ? "#bfdbfe" : "#1d4ed8",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    Linked
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  {[
                    ["Quote Date", linkedQuoteMeta.quoteDateLabel || "-"],
                    ["Quote Type", linkedQuoteMeta.quoteTypeLabel || "-"],
                    [
                      "Quote Value",
                      `${currencySymbol(currency)} ${Number(linkedQuoteMeta.totalValue || 0).toFixed(2)}`,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canViewQuotes ? (
                    <button
                      type="button"
                      onClick={() => openCrmQuoteForDeal(selectedCrmDeal)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      <ExternalLink size={14} style={{ marginRight: 6 }} />
                      Open Quote
                    </button>
                  ) : null}
                  {selectedCrmDeal.crmRecordType === "lead" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Change Quote
                      </button>
                      <button
                        type="button"
                        onClick={() => requestUnlinkQuoteFromLead(selectedCrmDeal)}
                        disabled={isLinkingQuote}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        <Unlink size={14} style={{ marginRight: 6 }} />
                        Unlink Quote
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>No quote linked yet</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    Link an existing quotation or create a new one from this lead.
                  </div>
                </div>
                {selectedCrmDeal.crmRecordType === "lead" ? (
                  <button
                    type="button"
                    onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    <Link2 size={14} style={{ marginRight: 6 }} />
                    Link Quote
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 16,
          }}
        >
          <div
            ref={crmContactSectionRef}
            style={sectionCardStyle}
          >
            <div style={{ fontSize: 16, fontWeight: 800 }}>Contact</div>
            <input
              value={crmDetailDraft.contact?.name || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, name: e.target.value },
                }))
              }
              style={input}
              placeholder="Client / company name"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <div style={{ position: "relative", minWidth: 0 }}>
                <Phone
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    color: dashboardMuted,
                  }}
                />
                <input
                  value={crmDetailDraft.contact?.phone || ""}
                  onChange={(e) =>
                    updateCrmDetailDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, phone: e.target.value },
                    }))
                  }
                  style={{ ...input, paddingLeft: 32 }}
                  placeholder="Phone"
                />
              </div>
              <div style={{ position: "relative", minWidth: 0 }}>
                <Phone
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    color: dashboardMuted,
                  }}
                />
                <input
                  value={crmDetailDraft.contact?.whatsapp || ""}
                  onChange={(e) =>
                    updateCrmDetailDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, whatsapp: e.target.value },
                    }))
                  }
                  style={{ ...input, paddingLeft: 32 }}
                  placeholder="WhatsApp"
                />
              </div>
            </div>
            <div style={{ position: "relative", minWidth: 0 }}>
              <Mail
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  color: dashboardMuted,
                }}
              />
              <input
                value={crmDetailDraft.contact?.email || ""}
                onChange={(e) =>
                  updateCrmDetailDraft((prev) => ({
                    ...prev,
                    contact: { ...prev.contact, email: e.target.value },
                  }))
                }
                style={{ ...input, paddingLeft: 32 }}
                placeholder="Email"
              />
            </div>
            <textarea
              value={crmDetailDraft.contact?.address || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, address: e.target.value },
                }))
              }
              style={{ ...input, minHeight: 82, resize: "vertical" }}
              placeholder="Address"
            />
            <input
              value={crmDetailDraft.contact?.source || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, source: e.target.value },
                }))
              }
              style={input}
              placeholder="Lead source"
            />
          </div>

          <div style={sectionCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Site Location</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                  Save Google Maps or WhatsApp location links so your team can find the site later.
                </div>
              </div>
              {siteLocations.length ? (
                <button
                  type="button"
                  onClick={resetCrmSiteLocationDraft}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Add Another Location
                </button>
              ) : null}
            </div>

            {siteLocations.length === 0 ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ color: textColor }}>No site location saved yet.</strong>
                <br />
                Paste a Google Maps or WhatsApp location link, or just save the address/landmark.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {siteLocations.map((location) => (
                  <div
                    key={location.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 14, fontWeight: 900 }}>
                            {location.label || "Site Location"}
                          </span>
                          {location.isPrimary ? (
                            <span
                              style={{
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: isDark ? "rgba(16,185,129,0.18)" : "#dcfce7",
                                color: isDark ? "#86efac" : "#166534",
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              Primary
                            </span>
                          ) : null}
                        </div>
                        {location.addressText ? (
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.5 }}>
                            {location.addressText}
                          </div>
                        ) : null}
                        {location.notes ? (
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                            Notes: {location.notes}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => openSiteLocation(location)}
                        style={{ ...btn("#0f172a"), marginRight: 0 }}
                      >
                        Open Map
                      </button>
                      <button
                        type="button"
                        onClick={() => copySiteLocationLink(location)}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Copy Link
                      </button>
                      <button
                        type="button"
                        onClick={() => editCrmSiteLocation(location)}
                        style={{ ...btn("#64748b"), marginRight: 0 }}
                      >
                        Edit
                      </button>
                      {selectedCustomerId ? (
                        <button
                          type="button"
                          onClick={() => copyCrmLocationToCustomerLedger(location)}
                          style={{ ...btn("#10b981"), marginRight: 0 }}
                        >
                          Save to Customer Ledger
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => deleteCrmSiteLocation(location.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                padding: 14,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 850 }}>
                {crmSiteLocationDraft.id ? "Edit Location" : "Add Location"}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <input
                  value={crmSiteLocationDraft.label}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Location label, e.g. Villa Site"
                />
                <input
                  value={crmSiteLocationDraft.googleMapsUrl}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      googleMapsUrl: event.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Google Maps / WhatsApp location link"
                />
              </div>
              <textarea
                value={crmSiteLocationDraft.addressText}
                onChange={(event) =>
                  setCrmSiteLocationDraft((prev) => ({
                    ...prev,
                    addressText: event.target.value,
                  }))
                }
                style={{ ...input, minHeight: 72, resize: "vertical" }}
                placeholder="Address / landmark"
              />
              <textarea
                value={crmSiteLocationDraft.notes}
                onChange={(event) =>
                  setCrmSiteLocationDraft((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                style={{ ...input, minHeight: 62, resize: "vertical" }}
                placeholder="Notes for installers or measurement team"
              />
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                <input
                  type="checkbox"
                  checked={crmSiteLocationDraft.isPrimary === true}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      isPrimary: event.target.checked,
                    }))
                  }
                />
                Primary Location
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={saveCrmSiteLocationDraft}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Save Location
                </button>
                <button
                  type="button"
                  onClick={resetCrmSiteLocationDraft}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div
            style={sectionCardStyle}
          >
            <div style={{ fontSize: 16, fontWeight: 800 }}>Customer Actions</div>
            <input
              value={crmDetailDraft.title || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              style={input}
              placeholder="Deal title"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Installation Date
                </div>
                <div style={{ position: "relative", minWidth: 0 }}>
                  <CalendarDays
                    size={14}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      color: dashboardMuted,
                    }}
                  />
                  <input
                    type="date"
                    value={crmDetailDraft.installationDate || ""}
                    onChange={(e) => {
                      const nextInstallationDate = e.target.value;
                      updateCrmDetailDraft((prev) => {
                        const nextReminderConfigs = normalizeCrmReminderConfigs(
                          prev?.reminderConfigs,
                          {
                            nextActionDate: parseDateInputValue(prev?.nextActionDate),
                            installationDate: parseDateInputValue(nextInstallationDate),
                          }
                        );
                        const nextInstallationReminder =
                          buildCanonicalInstallationReminder(
                            {
                              ...prev,
                              installationDate: nextInstallationDate,
                              reminderConfigs: nextReminderConfigs,
                            },
                            {
                              installationDate: parseDateInputValue(nextInstallationDate),
                              reminderConfigs: nextReminderConfigs,
                            }
                          );

                        if (isDevRuntime) {
                          devLog("INSTALLATION REMINDER TRACE", {
                            selectedCrmDealId,
                            enabledFromEvent: nextInstallationReminder?.enabled === true,
                            beforeDraftInstallationReminder:
                              prev.installationReminder,
                            afterDraftInstallationReminder:
                              nextInstallationReminder,
                          });
                          devLog("INSTALLATION REMINDER UI CHANGE", {
                            selectedCrmDealId,
                            installationDate: nextInstallationDate,
                            installationReminder: nextInstallationReminder,
                            reminderConfig: nextInstallationReminder,
                          });
                        }

                        return {
                          ...prev,
                          installationDate: nextInstallationDate,
                          reminderConfigs: nextReminderConfigs,
                          installationReminder: nextInstallationReminder,
                          reminderConfig: nextInstallationReminder,
                        };
                      });
                    }}
                    style={{ ...input, paddingLeft: 32 }}
                  />
                </div>
                <div style={{ fontSize: 11, color: dashboardMuted }}>
                  Use this for installation scheduling and installation reminders.
                </div>
              </div>
            </div>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 11, color: dashboardMuted }}>Follow-up Summary</div>
              <div style={{ fontWeight: 800 }}>
                {followUps.length > 0
                  ? `${followUps.filter((item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE).length} pending`
                  : "No follow-ups yet"}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Add, schedule, and complete tasks from the Follow-ups section below.
              </div>
            </div>
            <input
              value={crmDetailDraft.tags || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  tags: e.target.value,
                }))
              }
              style={input}
              placeholder="Tags, comma separated"
            />
            <textarea
              value={crmDetailDraft.notes || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              style={{ ...input, minHeight: 120, resize: "vertical" }}
              placeholder="Internal notes"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 10,
              }}
            >
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Expected</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {currencySymbol(currency)} {Number(selectedCrmDeal.expectedValue || 0).toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Final</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {currencySymbol(currency)} {Number(selectedCrmDeal.finalValue || 0).toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Progress</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {checklistProgress.completed}/{checklistProgress.total || 0}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 11, color: dashboardMuted }}>Assigned Team Member</div>
              <div style={{ fontWeight: 800 }}>
                {getTeamMemberLabel(
                  crmDetailDraft.assignedToUserId,
                  crmDetailDraft.assignedToNameSnapshot
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Follow-ups / Tasks</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Track multiple callbacks, visits, approvals, and delivery tasks for this lead without squeezing everything into one reminder.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openCrmFollowUpModal()}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + Add Follow-up
            </button>
          </div>

          {followUps.length === 0 ? (
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800 }}>No follow-ups added yet</div>
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Add your first follow-up to schedule callbacks, approvals, site work, or delivery tasks for this deal.
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {followUps.map((followUp, index) => {
                const isDone = followUp.status === CRM_FOLLOW_UP_STATUS_DONE;
                const dueDateTime = formatReminderDateTime(
                  combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
                    parseDateInputValue(followUp.dueDate)
                );
                const dueMillis = safeTimeMillis(
                  combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
                    parseDateInputValue(followUp.dueDate)
                );
                const isOverdue = !isDone && dueMillis > 0 && dueMillis < Date.now();
                const attachmentCount = normalizeCrmAttachments(
                  followUp.attachments || []
                ).length;
                const priorityOption =
                  CRM_FOLLOW_UP_PRIORITY_OPTIONS.find(
                    (option) => option.key === followUp.priority
                  ) || CRM_FOLLOW_UP_PRIORITY_OPTIONS.find(
                    (option) => option.key === CRM_FOLLOW_UP_PRIORITY_MEDIUM
                  );
                const priorityAccent =
                  followUp.priority === "high"
                    ? "#dc2626"
                    : followUp.priority === "low"
                    ? "#2563eb"
                    : "#d97706";

                return (
                  <div
                    key={followUp.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isOverdue
                        ? isDark
                          ? "rgba(127,29,29,0.24)"
                          : "#fff1f2"
                        : isDark
                        ? "rgba(255,255,255,0.03)"
                        : "#ffffff",
                      border: isOverdue
                        ? `1px solid ${isDark ? "rgba(248,113,113,0.45)" : "#fecdd3"}`
                        : dashboardBorder,
                      display: "grid",
                      gap: 12,
                      opacity: isDone ? 0.72 : 1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexDirection: isCompact ? "column" : "row",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0 }}>
                        <input
                          type="checkbox"
                          checked={isDone}
                          disabled={isDone || Boolean(markingDoneKey)}
                          onChange={(event) => {
                            if (event.target.checked) {
                              markCrmFollowUpDone(followUp.id);
                            }
                          }}
                          style={{ marginTop: 4 }}
                          aria-label={`Mark ${followUp.title || "follow-up"} as done`}
                        />
                        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            Follow-up {index + 1}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 900, wordBreak: "break-word" }}>
                            {followUp.title || "Untitled follow-up"}
                          </div>
                          {followUp.description ? (
                            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                              {followUp.description}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          justifyContent: isCompact ? "flex-start" : "flex-end",
                        }}
                      >
                        {isOverdue ? (
                          <div
                            style={{
                              padding: "6px 10px",
                              borderRadius: 999,
                              background: isDark
                                ? "rgba(248,113,113,0.2)"
                                : "#ffe4e6",
                              color: isDark ? "#fecdd3" : "#be123c",
                              fontSize: 11,
                              fontWeight: 900,
                            }}
                          >
                            Overdue
                          </div>
                        ) : null}
                        <div
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: `${priorityAccent}18`,
                            color: priorityAccent,
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          {priorityOption?.label || "Medium"} Priority
                        </div>
                        <div
                          style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: isDone
                            ? isDark
                              ? "rgba(22,163,74,0.18)"
                              : "#dcfce7"
                            : isDark
                            ? "rgba(59,130,246,0.18)"
                            : "#dbeafe",
                          color: isDone
                            ? isDark
                              ? "#86efac"
                              : "#166534"
                            : isDark
                            ? "#bfdbfe"
                            : "#1d4ed8",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {isDone ? "Done" : "Pending"}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                          ? "1fr"
                          : "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Order", Number(followUp.order || 0) > 0 ? followUp.order : index + 1],
                        ["Due", dueDateTime],
                        [
                          "Repeat",
                          CRM_FOLLOW_UP_REPEAT_OPTIONS.find(
                            (option) => option.key === followUp.repeat
                          )?.label || "No repeat",
                        ],
                        ["Reminder", followUp.reminderEnabled ? "On" : "Off"],
                        ["Priority", priorityOption?.label || "Medium"],
                        ["Attachments", `${attachmentCount} file${attachmentCount === 1 ? "" : "s"}`],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            padding: 12,
                            borderRadius: 16,
                            background: dashboardSoftBg,
                            border: dashboardBorder,
                          }}
                        >
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {isDone ? (
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        Completed by{" "}
                        <strong style={{ color: textColor }}>
                          {followUp.completedBy?.name ||
                            followUp.completedBy?.email ||
                            "Workspace User"}
                        </strong>
                        {followUp.completedAt
                          ? ` on ${new Date(followUp.completedAt).toLocaleString("en-IN")}`
                          : ""}
                      </div>
                    ) : null}

                    {attachmentCount > 0 ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        {normalizeCrmAttachments(followUp.attachments || []).map((attachment) => {
                          const isImage = String(attachment.contentType || "").startsWith("image/");
                          return (
                            <div
                              key={attachment.id}
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                                padding: 10,
                                borderRadius: 14,
                                background: dashboardSoftBg,
                                border: dashboardBorder,
                              }}
                            >
                              {isImage && attachment.url ? (
                                <img
                                  src={attachment.url}
                                  alt={attachment.name}
                                  style={{
                                    width: 56,
                                    height: 56,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    border: dashboardBorder,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 12,
                                    display: "grid",
                                    placeItems: "center",
                                    background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                    border: dashboardBorder,
                                  }}
                                >
                                  <Paperclip size={16} />
                                </div>
                              )}
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 800, wordBreak: "break-word" }}>
                                  {attachment.name}
                                </div>
                                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4 }}>
                                  {attachment.uploadedAt
                                    ? `Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                                    : "Uploaded"}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    ...btn("#0f172a"),
                                    marginRight: 0,
                                    textDecoration: "none",
                                  }}
                                >
                                  View
                                </a>
                                <a
                                  href={attachment.url}
                                  download={attachment.filename || attachment.name}
                                  style={{
                                    ...btn("#2563eb"),
                                    marginRight: 0,
                                    textDecoration: "none",
                                  }}
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => openCrmFollowUpModal(followUp)}
                        style={{ ...btn("#0f172a"), marginRight: 0 }}
                      >
                        Edit
                      </button>
                      {!isDone ? (
                        <button
                          type="button"
                          disabled={Boolean(markingDoneKey)}
                          onClick={() => markCrmFollowUpDone(followUp.id)}
                          style={{
                            ...btn("#16a34a"),
                            marginRight: 0,
                            opacity: markingDoneKey ? 0.7 : 1,
                          }}
                        >
                          {markingDoneKey ? "Saving..." : "Mark as Done"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => deleteCrmFollowUp(followUp.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Execution Details</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Capture what will actually be delivered or manufactured using flexible fields per execution item.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {selectedCrmDeal.hasQuote ? (
                <button
                  type="button"
                  onClick={() => convertDealQuoteToExecutionSpecs(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Convert Quote to Execution Specs
                </button>
              ) : null}
              {canUseWorkshopPdf && executionSpecs.length > 0 ? (
                <button
                  type="button"
                  onClick={() => downloadExecutionWorkshopPdf(selectedCrmDeal)}
                  style={{
                    ...btn("#7c3aed"),
                    marginRight: 0,
                    display: "grid",
                    gap: 3,
                    justifyItems: "start",
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Download size={14} />
                    Download Workshop PDF
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.86 }}>
                    Production / installation copy
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={addCrmExecutionSpec}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                + Add Item
              </button>
            </div>
          </div>

          {executionSpecs.length === 0 ? (
            <div
              style={{
                padding: 14,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                fontSize: 13,
                color: dashboardMuted,
                lineHeight: 1.7,
              }}
            >
              No execution items added yet. Open the full execution workspace to add structured job specs, dynamic fields, and attachments without working inside this drawer.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {executionSpecs.slice(0, 3).map((item, index) => {
                const normalizedAttachments = normalizeExecutionSpecAttachments(
                  item.attachments || []
                );
                const fieldCount = Array.isArray(item.attributeRows)
                  ? item.attributeRows.length
                  : 0;

                return (
                  <div
                    key={item.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        Execution Item {index + 1}
                      </div>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: dashboardSoftBg,
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {item.itemType || "Custom"}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>
                      {item.itemName || "Untitled execution item"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      {fieldCount} field{fieldCount === 1 ? "" : "s"} | {normalizedAttachments.length} attachment
                      {normalizedAttachments.length === 1 ? "" : "s"}
                    </div>
                  </div>
                );
              })}
              {executionSpecs.length > 3 ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  +{executionSpecs.length - 3} more execution item{executionSpecs.length - 3 === 1 ? "" : "s"} in the full workspace
                </div>
              ) : null}
            </div>
          )}

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: dashboardSoftBg,
              border: dashboardBorder,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Execution Specs now have a dedicated full-page workspace for General Quotations. Use it for larger item sets, deep attribute editing, and attachment review.
            </div>
            {isGeneralExecutionEligibleDeal(selectedCrmDeal) ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => openExecutionWorkspaceForDeal(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  {executionWorkspaceOpeningDealId === selectedCrmDeal.id
                    ? "Opening..."
                    : "Manage Execution Details"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    addCrmExecutionSpec();
                    openExecutionWorkspaceForDeal(selectedCrmDeal);
                  }}
                  style={{ ...btn("#0f172a"), marginRight: 0 }}
                >
                  Add Item in Workspace
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Execution Details is available for General Quotations only.
              </div>
            )}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Reminders</div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Installation reminders stay inside FabriQuote until they are dismissed or marked done. Follow-up task reminders are managed from the Follow-ups section above.
            </div>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {renderReminderManager(CRM_REMINDER_TYPE_INSTALLATION, {
              dueFieldLabel: "Installation date",
              dueValue: crmDetailDraft.installationDate || "-",
              helperText:
                "Defaults to the Installation Date so the team keeps seeing pending installation alerts until the work is done.",
            })}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Attachments</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Upload site photos, PDFs, measurements, or reference documents directly against this deal.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                ref={crmAttachmentInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  uploadCrmDealAttachments(selectedCrmDeal.id, files);
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => crmAttachmentInputRef.current?.click()}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                <UploadCloud size={14} style={{ marginRight: 6 }} />
                Add Attachment
              </button>
            </div>
          </div>

          {crmAttachmentUploadDealId === selectedCrmDeal.id ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Uploading attachment...
            </div>
          ) : null}

          {attachments.length === 0 ? (
            <div style={{ fontSize: 13, color: dashboardMuted }}>
              No attachments added yet for this deal.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {attachments.map((attachment) => {
                const visual = getAttachmentVisual(attachment);
                const Icon = visual.icon;

                return (
                  <div
                    key={attachment.id}
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexDirection: isCompact ? "column" : "row",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: 14,
                            display: "grid",
                            placeItems: "center",
                            background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            color: visual.accent,
                          }}
                        >
                          <Icon size={18} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, wordBreak: "break-word" }}>
                            {attachment.name}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            {visual.label}
                            {formatAttachmentSize(attachment.sizeBytes)
                              ? ` | ${formatAttachmentSize(attachment.sizeBytes)}`
                              : ""}
                            {attachment.uploadedAt
                              ? ` | Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                              : ""}
                            {attachment.uploadedByName
                              ? ` | By ${attachment.uploadedByName}`
                              : ""}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (attachment.url) {
                              window.open(attachment.url, "_blank", "noopener,noreferrer");
                            }
                          }}
                          style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                        >
                          <ExternalLink size={14} style={{ marginRight: 6 }} />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!attachment.url) return;
                            const anchor = document.createElement("a");
                            anchor.href = attachment.url;
                            anchor.download = attachment.filename || attachment.name || "attachment";
                            anchor.target = "_blank";
                            anchor.rel = "noopener noreferrer";
                            anchor.click();
                          }}
                          style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCrmDealAttachment(selectedCrmDeal.id, attachment.id)}
                          style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={sectionCardStyle}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Checklist</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Stage template: {legacyStageTemplate?.label || "Custom"}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  addCrmChecklistItem();
                }}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Add Item
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  updateCrmDetailChecklist(() => createChecklistFromStage(legacyStageTemplate));
                }}
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                Reset from Stage Template
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {(crmDetailDraft.checklistItems || []).length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No checklist items yet for this deal. Add a top-level item or reset from the stage template.
              </div>
            ) : (
              (crmDetailDraft.checklistItems || []).map((item) =>
                renderCrmChecklistNode(item, 1)
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmDetailDrawer = () => {
    const drawerExpanded = !isCompact && (isCrmDrawerHovered || isCrmDrawerPinned);
    const collapsedWidth = 12;
    const expandedWidth = isTablet ? 360 : 420;
    const drawerTop = isTablet ? 108 : 118;

    return (
      <div
        onMouseEnter={() => {
          if (!isCompact) {
            setIsCrmDrawerHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (!isCompact && !isCrmDrawerPinned) {
            setIsCrmDrawerHovered(false);
          }
        }}
        style={{
          width: isCompact ? "100%" : drawerExpanded ? expandedWidth : collapsedWidth,
          minWidth: isCompact ? "100%" : drawerExpanded ? expandedWidth : collapsedWidth,
          position: isCompact ? "static" : "fixed",
          top: isCompact ? "auto" : drawerTop,
          right: isCompact ? "auto" : 10,
          bottom: isCompact ? "auto" : 16,
          zIndex: isCompact ? "auto" : 90,
          overflow: "visible",
          transition: "width 0.24s ease, min-width 0.24s ease",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            minHeight: isCompact ? "auto" : `calc(100vh - ${drawerTop + 16}px)`,
            height: isCompact ? "auto" : `calc(100vh - ${drawerTop + 16}px)`,
            padding: 0,
            display: "grid",
            overflow: "hidden",
            borderRadius: 24,
            boxShadow: drawerExpanded
              ? isDark
                ? "0 24px 60px rgba(2,6,23,0.7)"
                : "0 24px 60px rgba(15,23,42,0.18)"
              : "0 8px 18px rgba(15,23,42,0.08)",
          }}
        >
          <div
            style={{
              width: isCompact ? "100%" : expandedWidth,
              height: "100%",
              transform: "translateX(0)",
              padding: drawerExpanded || isCompact ? 18 : 6,
              display: "grid",
              gap: 14,
              minHeight: "100%",
            }}
          >
            {drawerExpanded || isCompact ? (
              <div style={{ display: "grid", gap: 14, minHeight: "100%" }}>
                {!isCompact ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      paddingBottom: 10,
                      borderBottom: dashboardBorder,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        CRM Details
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {selectedCrmDeal
                          ? selectedCrmDeal.crmStageLabel
                          : selectedCrmDealId || selectedCrmDealCacheRef.current
                          ? "Loading selected deal details"
                          : "Select a deal to view details"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCrmDrawerPinned(false);
                        setIsCrmDrawerHovered(false);
                      }}
                      style={{
                        ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                        marginRight: 0,
                        padding: "8px 12px",
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : null}
                <div style={{ overflowY: "auto", paddingRight: isCompact ? 0 : 4 }}>
                  {renderCrmDetailPanel()}
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  alignContent: "start",
                  justifyItems: "center",
                  minHeight: "100%",
                  background: isDark
                    ? "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(15,23,42,0.88))"
                    : "linear-gradient(180deg, #ffffff, #f8fafc)",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 120,
                    borderRadius: 16,
                    background: selectedCrmDeal?.crmStageColor || "#2563eb",
                    marginTop: 8,
                    boxShadow: "0 12px 24px rgba(37,99,235,0.18)",
                  }}
                />

                <div
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: dashboardMuted,
                  }}
                >
                  CRM Drawer
                </div>

                <div style={{ fontSize: 11, color: dashboardMuted, textAlign: "center" }}>
                  {selectedCrmDeal
                    ? "Hover to expand"
                    : selectedCrmDealId || selectedCrmDealCacheRef.current
                    ? "Loading selected record"
                    : "Select a customer or lead to view details"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDailyWorkQueue = ({ embedded = false } = {}) => {
    const limitItems = embedded ? 5 : 8;
    const currentUserKeys = [user?.uid, user?.email, accountUser?.uid, accountUser?.email]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase())
      .filter(Boolean);
    const projectPool = [
      ...(crmProjectRecords || []),
      ...(crmAttentionState.installationsDueToday || []),
      ...(crmAttentionState.overdueInstallations || []),
      ...(crmAttentionState.openSnagProjects || []),
      ...(crmAttentionState.overdueSnagProjects || []),
      ...(crmAttentionState.projectsReadyToClose || []),
      ...(crmAttentionState.balancePendingProjects || []),
      ...(crmAttentionState.projectsBlockedByFinalPayment || []),
    ];
    const projectById = new Map();
    projectPool.forEach((project) => {
      const projectId = String(project?.projectId || project?.id || "").trim();
      if (projectId && !projectById.has(projectId)) projectById.set(projectId, project);
    });
    const dedupeById = (items, getId) => {
      const seen = new Set();
      return items.filter((item, index) => {
        const id = String(getId(item, index) || "").trim();
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };
    const taskAsDaily = (taskLike = {}) => {
      if (taskLike.sourceType) return taskLike;
      const taskId = taskLike.taskId || taskLike.id || "";
      const dueAt = taskLike.dueAt || taskLike.dueDate || null;
      const dueMillis = safeTimeMillis(dueAt);
      const status =
        taskLike.status === CRM_TASK_STATUS_COMPLETED ? "completed" : "pending";
      return {
        ...taskLike,
        id: `crm-task:${taskId}`,
        sourceId: taskId,
        sourceType: DAILY_TASK_SOURCE_CRM_TASK,
        title: taskLike.title || "Task",
        description: taskLike.notes || taskLike.description || "",
        dueDate: dueAt,
        dueDateTime: dueAt,
        dueDateKey: taskLike.dueDateKey || toDateInputValue(dueAt),
        dueTime: taskLike.dueTime || DEFAULT_CRM_REMINDER_TIME,
        status,
        isOverdue: status !== "completed" && dueMillis > 0 && dueMillis < Date.now(),
        priority: taskLike.priority || "normal",
        customerName: taskLike.customerName || "Unnamed Customer",
        quoteNo: taskLike.quoteNo || "",
        assignedTo: taskLike.ownerName || "Unassigned",
        assignedToUserId: taskLike.ownerUid || "",
        crmTask: taskLike,
      };
    };
    const getTaskProjectId = (taskLike = {}) => {
      const task = taskLike.crmTask || taskLike.original || taskLike;
      return String(
        taskLike.projectId ||
          task.projectId ||
          task.paymentFollowUpForProjectId ||
          task.trackingTaskForProjectId ||
          (String(task.relatedType || "").startsWith("project") ? task.relatedId : "") ||
          ""
      ).trim();
    };
    const rememberProject = (project = {}) => {
      const projectId = String(project.projectId || project.id || "").trim();
      if (!projectId) return;
      setCrmProjectRecords((prev) =>
        prev.some((item) => String(item.projectId || item.id || "") === projectId)
          ? prev
          : [project, ...prev]
      );
    };
    const resolveProjectForAction = async (projectOrId) => {
      const projectId =
        typeof projectOrId === "object"
          ? String(projectOrId?.projectId || projectOrId?.id || "").trim()
          : String(projectOrId || "").trim();
      if (!projectId) return null;
      const existing =
        typeof projectOrId === "object" && projectOrId
          ? projectOrId
          : projectById.get(projectId) ||
            crmProjectRecords.find(
              (project) => String(project.projectId || project.id || "") === projectId
            ) ||
            null;
      if (existing) {
        rememberProject(existing);
        return existing;
      }
      return loadCrmProjectById(projectId, { navigate: false, notifyMissing: true });
    };
    const openProjectFromToday = async (projectOrId, drawerTab = "overview") => {
      const project = await resolveProjectForAction(projectOrId);
      const projectId = String(project?.projectId || project?.id || projectOrId || "").trim();
      if (!projectId) return;
      setSelectedCrmProjectId(projectId);
      setCrmProjectDrawerTab(drawerTab);
      setCrmView("projects");
      setRoutedTab("crm");
    };
    const openAttentionBucket = (bucketKey) => {
      setCrmAttentionActiveBucket(bucketKey);
      setCrmView("attention");
      setRoutedTab("crm");
    };
    const openTaskList = (mode, status) => {
      setDailyTaskDateMode(mode);
      setDailyTaskStatusFilter(status);
      setRoutedTab("todayTasks");
    };
    const openLeadFromToday = (leadLike = {}) => {
      const leadId = leadLike.legacyLeadId || leadLike.leadId || leadLike.id || leadLike.opportunityId || "";
      const deal =
        crmDeals.find((item) => String(item.id || "") === String(leadId)) ||
        crmLeadRecords.find((item) => String(item.id || "") === String(leadId));
      setCrmView("list");
      setRoutedTab("crm");
      if (deal) openCrmDealDetails(deal, { crmView: "list" });
    };
    const isProjectMine = (project = {}) => {
      if (!currentUserKeys.length) return true;
      const ownerKeys = [
        project.projectOwnerUid,
        project.ownerUid,
        project.assignedToUserId,
        project.projectOwnerEmail,
        project.ownerEmail,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
      return ownerKeys.some((value) => currentUserKeys.includes(value));
    };
    const sortWork = (items) =>
      [...items].sort((a, b) => {
        if (Boolean(a.isOverdue) !== Boolean(b.isOverdue)) return a.isOverdue ? -1 : 1;
        const dueA = safeTimeMillis(a.dueAt) || Number.MAX_SAFE_INTEGER;
        const dueB = safeTimeMillis(b.dueAt) || Number.MAX_SAFE_INTEGER;
        if (dueA !== dueB) return dueA - dueB;
        const priorityRank = { urgent: 0, high: 1, medium: 2, normal: 3, low: 4 };
        const priorityA = priorityRank[a.priority] ?? 3;
        const priorityB = priorityRank[b.priority] ?? 3;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return safeTimeMillis(b.updatedAt || b.createdAt) - safeTimeMillis(a.updatedAt || a.createdAt);
      });
    const action = (label, onClick, color = "#0f172a", disabled = false) => ({
      label,
      onClick,
      color,
      disabled,
    });
    const taskItem = (taskLike, overrides = {}) => {
      const task = taskAsDaily(taskLike);
      return {
        key: overrides.key || task.id,
        title: overrides.title || task.title || "Task",
        customerName: task.customerName || "Unnamed Customer",
        actionText: overrides.actionText || task.description || task.title,
        dueAt: task.dueDateTime || task.dueDate,
        owner: task.assignedTo || "Unassigned",
        related: overrides.related || (task.quoteNo ? `Quote #${task.quoteNo}` : ""),
        badge: overrides.badge || (task.isOverdue ? "Overdue" : "Due today"),
        accent: overrides.accent || (task.isOverdue ? "#dc2626" : "#2563eb"),
        priority: task.priority || "normal",
        isOverdue: task.isOverdue,
        updatedAt: task.updatedAt,
        onOpen: overrides.onOpen || (() => openDailyTaskDeal(task)),
        actions:
          overrides.actions ||
          [
            action("Mark Done", () => markDailyTaskDone(task), "#16a34a", Boolean(markingDoneKey)),
            action("Add Next Action", () => openDailyTaskReschedule(task), "#2563eb"),
            action(resolveTaskTarget(task).label, () => openDailyTaskDeal(task), "#0f172a"),
          ],
      };
    };
    const paymentItem = (taskLike, isOverdue) => {
      const task = taskAsDaily(taskLike);
      const projectId = getTaskProjectId(task);
      const project = projectById.get(projectId);
      return taskItem(task, {
        key: `payment:${task.sourceId || task.id}`,
        title: task.title || "Payment follow-up",
        actionText:
          task.dueAmount != null
            ? `Collect ${formatCurrencyDisplay(task.dueAmount || 0, currency)}`
            : "Collect pending payment",
        badge: isOverdue ? "Payment overdue" : "Payment pending",
        accent: isOverdue ? "#dc2626" : "#d97706",
        related: task.quoteNo ? `Quote #${task.quoteNo}` : project?.projectTitle || "Project",
        onOpen: () => openProjectFromToday(project || projectId, "payments"),
        actions: [
          action("Add Payment", async () => {
            const resolvedProject = await resolveProjectForAction(project || projectId);
            if (resolvedProject) {
              openCrmPaymentModal(resolvedProject, {
                followUpTask: task.crmTask || task,
                completeFollowUpAfterSave: true,
                amount:
                  task.dueAmount ??
                  resolvedProject.paymentSummary?.balanceAmount ??
                  resolvedProject.balanceAmount ??
                  "",
              });
            }
          }, "#16a34a"),
          action("Reschedule Follow-up", () => openDailyTaskReschedule(task), "#2563eb"),
          action("Open Project", () => openProjectFromToday(project || projectId, "payments"), "#0f172a"),
        ],
      });
    };
    const reviewItem = (taskLike, isOverdue) => {
      const task = taskAsDaily(taskLike);
      const projectId = getTaskProjectId(task);
      const project = projectById.get(projectId);
      return taskItem(task, {
        key: `review:${task.sourceId || task.id}`,
        title: task.title || "Review request",
        actionText: "Ask for review or referral",
        badge: isOverdue ? "Review overdue" : "Review due",
        accent: isOverdue ? "#dc2626" : "#7c3aed",
        related: task.quoteNo ? `Quote #${task.quoteNo}` : project?.projectTitle || "Project",
        onOpen: () => openProjectFromToday(project || projectId, "review"),
        actions: [
          action("Mark Review Received", async () => {
            const resolvedProject = await resolveProjectForAction(project || projectId);
            if (resolvedProject) updateProjectReviewStatus(resolvedProject, PROJECT_REVIEW_STATUS_COMPLETED);
          }, "#16a34a", Boolean(crmProjectActionKey)),
          action("Skip", async () => {
            const resolvedProject = await resolveProjectForAction(project || projectId);
            if (resolvedProject) updateProjectReviewStatus(resolvedProject, PROJECT_REVIEW_STATUS_SKIPPED);
          }, "#64748b", Boolean(crmProjectActionKey)),
          action("Open Project", () => openProjectFromToday(project || projectId, "review"), "#0f172a"),
        ],
      });
    };
    const projectItem = (project, options = {}) => {
      const balance = Number(project.paymentSummary?.balanceAmount ?? project.balanceAmount ?? 0) || 0;
      return {
        key: `${options.keyPrefix || "project"}:${project.projectId || project.id}`,
        title: project.projectTitle || project.customerName || "Untitled project",
        customerName: project.customerName || "Unnamed customer",
        actionText:
          options.actionText ||
          project.lastActivityLabel ||
          project.stageLabel ||
          getProjectStageLabel(project.stage) ||
          "Project needs action",
        dueAt:
          options.dueAt ||
          project.installationScheduledAt ||
          project.nextSnagDueAt ||
          project.expectedInstallationDate ||
          project.updatedAt,
        owner: project.projectOwnerName || project.ownerName || "Unassigned",
        related: [
          project.quoteNo ? `Quote #${project.quoteNo}` : "",
          balance > 0 ? `Balance ${formatCurrencyDisplay(balance, currency)}` : "",
        ].filter(Boolean).join(" | "),
        badge: options.badge || "Project",
        accent: options.accent || "#2563eb",
        priority: options.priority || "normal",
        isOverdue: options.isOverdue === true,
        updatedAt: project.updatedAt,
        onOpen: () => openProjectFromToday(project, options.projectTab || "overview"),
        actions: options.actions || [
          action("Open Project", () => openProjectFromToday(project, options.projectTab || "overview"), "#0f172a"),
        ],
      };
    };
    const plainTasks = (task) => {
      const sourceKind = String(task.crmTask?.sourceKind || task.original?.sourceKind || "").trim();
      return sourceKind !== "payment_follow_up" && sourceKind !== "project_review_request";
    };
    const overdueItems = sortWork(
      dailyTaskGroups.overdue.filter(plainTasks).map((task) =>
        taskItem(task, { badge: "Overdue", accent: "#dc2626" })
      )
    );
    const todayItems = sortWork(
      dailyTaskGroups.today.filter(plainTasks).map((task) =>
        taskItem(task, { badge: "Due today", accent: "#2563eb" })
      )
    );
    const paymentItems = sortWork(
      dedupeById(
        [
          ...(crmAttentionState.overduePaymentTasks || []).map((task) => ({ ...task, __overdue: true })),
          ...(crmAttentionState.paymentDueTodayTasks || []).map((task) => ({ ...task, __overdue: false })),
        ],
        (task) => task.taskId || task.id
      ).map((task) => paymentItem(task, task.__overdue))
    );
    const reviewItems = sortWork(
      dedupeById(
        [
          ...(crmAttentionState.overdueReviewTasks || []).map((task) => ({ ...task, __overdue: true })),
          ...(crmAttentionState.reviewDueTodayTasks || []).map((task) => ({ ...task, __overdue: false })),
        ],
        (task) => task.taskId || task.id
      ).map((task) => reviewItem(task, task.__overdue))
    );
    const snagProjects = dedupeById(
      [
        ...(crmAttentionState.overdueSnagProjects || []).map((project) => ({ ...project, __overdue: true })),
        ...(crmAttentionState.openSnagProjects || []).map((project) => ({ ...project, __overdue: false })),
      ],
      (project) => project.projectId || project.id
    ).filter(isProjectMine);
    const sections = [
      {
        key: "overdue",
        title: "My Overdue Tasks",
        helper: "Follow-ups and tasks that should have been completed already.",
        empty: "No overdue tasks assigned to you.",
        accent: "#dc2626",
        count: overdueItems.length,
        viewAll: () => openTaskList("overdue", "overdue"),
        items: overdueItems.slice(0, limitItems),
      },
      {
        key: "today",
        title: "My Tasks Today",
        helper: "Customer calls, next actions, and team tasks due today.",
        empty: "No tasks are due today.",
        accent: "#2563eb",
        count: todayItems.length,
        viewAll: () => openTaskList("today", "pending"),
        items: todayItems.slice(0, limitItems),
      },
      {
        key: "leadsWithoutNextAction",
        title: "Leads Without Next Action",
        helper: "Leads that need one clear next step before they go cold.",
        empty: "Every lead has a next action.",
        accent: "#f97316",
        count: (crmAttentionState.missingNextAction || []).length,
        viewAll: () => openAttentionBucket("missingNextAction"),
        items: (crmAttentionState.missingNextAction || []).slice(0, limitItems).map((lead) => ({
          key: `lead:${lead.opportunityId || lead.id}`,
          customerName: lead.customerName || lead.customerLabel || buildOpportunityDisplayTitle(lead),
          actionText: lead.nextActionText || lead.nextAction || "Add a next action",
          dueAt: lead.nextActionDueAt || lead.nextActionDate,
          owner: lead.ownerName || lead.assignedTo || "Unassigned",
          related: lead.latestQuoteNo || lead.quoteNo ? `Quote #${lead.latestQuoteNo || lead.quoteNo}` : "",
          badge: "No next action",
          accent: "#f97316",
          priority: "high",
          onOpen: () => openLeadFromToday(lead),
          actions: [
            action("Add Next Action", () => openLeadFromToday(lead), "#2563eb"),
            action("Open Customer", () => openLeadFromToday(lead), "#0f172a"),
          ],
        })),
      },
      {
        key: "quotesToFollow",
        title: "Quotations To Follow Up",
        helper: "Sent quotations that still need a follow-up date or action.",
        empty: "No quotations need follow-up planning.",
        accent: "#0ea5e9",
        count: (crmAttentionState.missingQuoteFollowUp || []).length,
        viewAll: () => openAttentionBucket("missingQuoteFollowUp"),
        items: (crmAttentionState.missingQuoteFollowUp || []).slice(0, limitItems).map((quote) => ({
          key: `quote:${quote.quoteIndexId || quote.quoteId || quote.quoteNo}`,
          customerName: quote.customerName || "Unnamed customer",
          actionText: "Quotation needs a follow-up plan",
          dueAt: quote.updatedAt,
          owner: "Unassigned",
          related: quote.quoteNo ? `Quote #${quote.quoteNo}` : "Quotation",
          badge: "Needs follow-up",
          accent: "#0ea5e9",
          priority: "medium",
          onOpen: () => loadFullQuote(quote.quoteId || quote.quoteIndexId),
          actions: [
            action("Open Quote", () => loadFullQuote(quote.quoteId || quote.quoteIndexId), "#0f172a"),
            action("View All", () => openAttentionBucket("missingQuoteFollowUp"), "#2563eb"),
          ],
        })),
      },
      {
        key: "payments",
        title: "Payment Follow-ups Due",
        helper: "Payment calls and collections due now or overdue.",
        empty: "No payment follow-ups are due.",
        accent: "#d97706",
        count: paymentItems.length,
        viewAll: () => openAttentionBucket("paymentDueTodayTasks"),
        items: paymentItems.slice(0, limitItems),
      },
      {
        key: "installToday",
        title: "Installations Today",
        helper: "Projects scheduled for installation today.",
        empty: "No installations are scheduled today.",
        accent: "#0ea5e9",
        count: (crmAttentionState.installationsDueToday || []).length,
        viewAll: () => openAttentionBucket("installationsDueToday"),
        items: sortWork((crmAttentionState.installationsDueToday || []).map((project) =>
          projectItem(project, {
            keyPrefix: "install-today",
            badge: "Installation today",
            accent: "#0ea5e9",
            dueAt: project.installationScheduledAt,
            projectTab: "installation",
            actions: [
              action("Mark Completed", () => openCrmInstallationModal(project, "complete"), "#16a34a"),
              action("Reschedule", () => openCrmInstallationModal(project, "schedule"), "#2563eb"),
              action("Open Project", () => openProjectFromToday(project, "installation"), "#0f172a"),
            ],
          })
        )).slice(0, limitItems),
      },
      {
        key: "installOverdue",
        title: "Overdue Installations",
        helper: "Scheduled installations that are past their date.",
        empty: "No installations are overdue.",
        accent: "#dc2626",
        count: (crmAttentionState.overdueInstallations || []).length,
        viewAll: () => openAttentionBucket("overdueInstallations"),
        items: sortWork((crmAttentionState.overdueInstallations || []).map((project) =>
          projectItem(project, {
            keyPrefix: "install-overdue",
            badge: "Overdue",
            accent: "#dc2626",
            dueAt: project.installationScheduledAt,
            projectTab: "installation",
            isOverdue: true,
            actions: [
              action("Mark Completed", () => openCrmInstallationModal(project, "complete"), "#16a34a"),
              action("Reschedule", () => openCrmInstallationModal(project, "schedule"), "#2563eb"),
              action("Open Project", () => openProjectFromToday(project, "installation"), "#0f172a"),
            ],
          })
        )).slice(0, limitItems),
      },
      {
        key: "snags",
        title: "Open Snags Assigned To Me",
        helper: "Corrections assigned to you or your projects.",
        empty: "No open snags are assigned to you.",
        accent: "#f97316",
        count: snagProjects.length,
        viewAll: () => openAttentionBucket("openSnagProjects"),
        items: sortWork(snagProjects.map((project) =>
          projectItem(project, {
            keyPrefix: "snag",
            badge: project.__overdue ? "Snag overdue" : "Snag pending",
            accent: project.__overdue ? "#dc2626" : "#f97316",
            dueAt: project.nextSnagDueAt || project.updatedAt,
            projectTab: "snags",
            isOverdue: project.__overdue,
            actions: [
              action("Open Snags", () => openProjectFromToday(project, "snags"), "#16a34a"),
              action("Open Project", () => openProjectFromToday(project, "snags"), "#0f172a"),
            ],
          })
        )).slice(0, limitItems),
      },
      {
        key: "readyClose",
        title: "Projects Ready To Close",
        helper: "Installed projects with no open snag or payment blocker.",
        empty: "No projects are ready to close yet.",
        accent: "#16a34a",
        count: (crmAttentionState.projectsReadyToClose || []).length,
        viewAll: () => openAttentionBucket("projectsReadyToClose"),
        items: sortWork((crmAttentionState.projectsReadyToClose || []).map((project) =>
          projectItem(project, {
            keyPrefix: "ready-close",
            badge: "Ready to close",
            accent: "#16a34a",
            actions: [
              action("Close Project", () => closeCrmProject(project), "#16a34a", Boolean(crmProjectActionKey)),
              action("Open Project", () => openProjectFromToday(project, "overview"), "#0f172a"),
            ],
          })
        )).slice(0, limitItems),
      },
      {
        key: "reviews",
        title: "Review Requests Due",
        helper: "Review or referral requests due today or overdue.",
        empty: "No review requests are due.",
        accent: "#7c3aed",
        count: reviewItems.length,
        viewAll: () => openAttentionBucket("reviewDueTodayTasks"),
        items: reviewItems.slice(0, limitItems),
      },
    ];
    const totalWorkItems = sections.reduce((sum, section) => sum + section.count, 0);
    const renderItem = (item) => (
      <div
        key={item.key}
        role={item.onOpen ? "button" : undefined}
        tabIndex={item.onOpen ? 0 : undefined}
        onClick={item.onOpen}
        onKeyDown={(event) => {
          if (!item.onOpen) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            item.onOpen();
          }
        }}
        style={{
          padding: 14,
          borderRadius: 16,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 9,
          cursor: item.onOpen ? "pointer" : "default",
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor, overflowWrap: "anywhere" }}>
              {item.customerName || item.title || "Unnamed customer"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.45 }}>
              {item.actionText || item.title}
            </div>
          </div>
          <span style={{ padding: "4px 8px", borderRadius: 999, background: `${item.accent}16`, color: item.accent, fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
            {item.badge}
          </span>
        </div>
        <div style={{ display: "grid", gap: 4, fontSize: 12, color: dashboardMuted }}>
          <div>Due: {item.dueAt ? formatReminderDateTime(item.dueAt) : "No date set"}</div>
          <div>Owner: {item.owner || "Unassigned"}{item.related ? ` | ${item.related}` : ""}</div>
        </div>
        {item.actions?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {item.actions.map((actionItem) => (
              <button
                key={actionItem.label}
                type="button"
                disabled={actionItem.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  actionItem.onClick?.();
                }}
                style={{
                  ...btn(actionItem.color || "#0f172a"),
                  padding: "7px 10px",
                  marginRight: 0,
                  opacity: actionItem.disabled ? 0.65 : 1,
                }}
              >
                {actionItem.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
    const renderSection = (section) => (
      <section key={section.key} style={{ ...dashboardCardStyle, display: "grid", gap: 12, border: section.count ? `1px solid ${section.accent}33` : dashboardBorder }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: section.accent }}>{section.title}</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.55 }}>{section.helper}</div>
          </div>
          <div style={{ padding: "6px 10px", borderRadius: 999, background: `${section.accent}16`, color: section.accent, fontSize: 12, fontWeight: 900 }}>
            {section.count}
          </div>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {section.items.length ? section.items.map(renderItem) : (
            <div style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: isDark ? "rgba(15,23,42,0.42)" : "#f8fafc", fontSize: 13, color: dashboardMuted, lineHeight: 1.55 }}>
              {section.empty}
            </div>
          )}
        </div>
        {section.count > 0 ? (
          <button type="button" onClick={section.viewAll} style={{ ...btn(section.accent), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}>
            View All
          </button>
        ) : null}
      </section>
    );

    return (
      <section style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "flex", justifyContent: "space-between", gap: 14, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center", background: isDark ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.94))" : "linear-gradient(135deg, #eff6ff, #ffffff)" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb", marginBottom: 6 }}>
              Today Work Queue
            </div>
            <div style={{ fontSize: isCompact ? 24 : 30, fontWeight: 950, letterSpacing: "-0.04em", color: textColor }}>
              Start with what needs action now
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8, lineHeight: 1.65 }}>
              Overdue work, follow-ups, payments, installations, snags, closing, and reviews are grouped here from bounded CRM task and project queries.
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, minWidth: isCompact ? "100%" : 220 }}>
            <div style={{ padding: 16, borderRadius: 18, background: isDark ? "rgba(37,99,235,0.16)" : "#dbeafe", border: "1px solid rgba(37,99,235,0.22)" }}>
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>Open action items</div>
              <div style={{ fontSize: 32, fontWeight: 950, color: "#2563eb" }}>{totalWorkItems}</div>
            </div>
            <button type="button" onClick={() => loadCrmAttentionRequired(workspaceUid)} disabled={crmAttentionState.loading || !workspaceUid} style={{ ...btn("#2563eb"), marginRight: 0, opacity: crmAttentionState.loading ? 0.7 : 1 }}>
              {crmAttentionState.loading ? "Refreshing..." : "Refresh Today"}
            </button>
          </div>
        </div>
        {crmAttentionState.error ? (
          <div style={{ ...dashboardCardStyle, border: "1px solid rgba(239,68,68,0.22)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
            {crmAttentionState.error}
          </div>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14, alignItems: "start" }}>
          {sections.map(renderSection)}
        </div>
      </section>
    );
  };

  const renderManagerDashboard = () => {
    const summary = crmPipelineReviewState.summary || {};
    const dateRange = crmPipelineReviewState.dateRange || getCrmPipelineReviewRange("this_week");
    const dedupeManagerItems = (items = [], getKey) => {
      const map = new Map();
      (items || []).forEach((item, index) => {
        const key = String(getKey?.(item) || item?.id || item?.projectId || index).trim();
        if (!map.has(key)) map.set(key, item);
      });
      return Array.from(map.values());
    };
    const sumProjectValue = (projects = [], resolver) =>
      dedupeManagerItems(projects || [], (project) => project.projectId || project.id)
        .reduce((total, project) => total + (Number(resolver(project) || 0) || 0), 0);
    const balancePendingProjects = crmAttentionState.balancePendingProjects || [];
    const paymentPendingValue = Math.max(
      Number(summary.paymentPendingBalance || 0) || 0,
      sumProjectValue(
        balancePendingProjects,
        (project) => project.paymentSummary?.balanceAmount ?? project.balanceAmount
      )
    );
    const activeProjectValue = Math.max(
      Number(summary.activeProjectValue || 0) || 0,
      sumProjectValue(
        [
          ...(crmAttentionState.handoverPendingProjects || []),
          ...(crmAttentionState.advancePendingProjects || []),
          ...(crmAttentionState.balancePendingProjects || []),
          ...(crmAttentionState.projectsReadyToClose || []),
          ...(crmAttentionState.projectsBlockedByFinalPayment || []),
        ],
        (project) => project.paymentSummary?.orderValue ?? project.orderValue
      )
    );
    const openSnagCount = dedupeManagerItems(
      [
        ...(crmAttentionState.openSnagProjects || []),
        ...(crmAttentionState.overdueSnagProjects || []),
      ],
      (project) => project.projectId || project.id
    ).length;
    const reviewDueCount = (crmAttentionState.reviewDueTodayTasks || []).length;
    const overdueReviewCount = (crmAttentionState.overdueReviewTasks || []).length;
    const navigateAttention = (bucketKey) => {
      setCrmAttentionActiveBucket(bucketKey);
      setCrmView("attention");
    };
    const navigatePipelineReview = () => setCrmView(CRM_PIPELINE_REVIEW_TAB);
    const navigateProjects = () => setCrmView("projects");
    const renderManagerCard = (card) => (
      <button
        key={card.key}
        type="button"
        onClick={card.onClick}
        style={{
          textAlign: "left",
          padding: 14,
          borderRadius: 18,
          border: dashboardBorder,
          background: isDark ? "rgba(15,23,42,0.74)" : "#ffffff",
          color: textColor,
          cursor: "pointer",
          display: "grid",
          gap: 7,
          minWidth: 0,
          boxShadow: isDark
            ? "0 10px 24px rgba(2,6,23,0.22)"
            : "0 10px 24px rgba(15,23,42,0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 900, lineHeight: 1.35 }}>
            {card.label}
          </span>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: card.accent, marginTop: 3, flex: "0 0 auto" }} />
        </div>
        <div style={{ fontSize: card.compact ? 20 : 24, fontWeight: 950, color: card.accent, lineHeight: 1.15 }}>
          {card.value}
        </div>
        <div style={{ fontSize: 11.5, color: dashboardMuted, lineHeight: 1.45 }}>
          {card.note}
        </div>
      </button>
    );
    const renderManagerSection = (section) => (
      <section key={section.key} style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 950 }}>{section.title}</div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
            {section.helper}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {section.cards.map(renderManagerCard)}
        </div>
      </section>
    );
    const managerSections = [
      {
        key: "sales",
        title: "Sales Overview",
        helper: "This period's lead and quotation movement.",
        cards: [
          { key: "newLeads", label: "New leads this week", value: summary.newLeads || 0, note: "Created in the selected review range", accent: "#2563eb", onClick: navigatePipelineReview },
          { key: "contactedLeads", label: "Contacted leads", value: summary.contactedLeads || 0, note: "Leads moved to contacted", accent: "#0ea5e9", onClick: navigatePipelineReview },
          { key: "quotesSent", label: "Quotes sent", value: summary.quotationsSent || 0, note: "Quote follow-up records in range", accent: "#f59e0b", onClick: navigatePipelineReview },
          { key: "ordersWon", label: "Orders won", value: summary.ordersWon || 0, note: "Advance received, part-paid, or fully paid", accent: "#16a34a", onClick: navigatePipelineReview },
          { key: "lostLeads", label: "Lost leads", value: summary.lostOpportunities || 0, note: "Marked lost in the review range", accent: "#ef4444", onClick: navigatePipelineReview },
        ],
      },
      {
        key: "pipelineValue",
        title: "Pipeline Value",
        helper: "Revenue movement for the selected review period.",
        cards: [
          { key: "openValue", label: "Open opportunity value", value: formatCurrencyDisplay(summary.openOpportunityValue || 0, currency), note: `${summary.openLeads || 0} open leads in this review`, accent: "#6366f1", compact: true, onClick: navigatePipelineReview },
          { key: "quoteValue", label: "Quotation sent value", value: formatCurrencyDisplay(summary.quotationSentValue || summary.quoteValue || 0, currency), note: "From sent quote records", accent: "#d97706", compact: true, onClick: navigatePipelineReview },
          { key: "negotiationValue", label: "Negotiation value", value: formatCurrencyDisplay(summary.negotiationValue || 0, currency), note: "Opportunities currently in negotiation", accent: "#7c3aed", compact: true, onClick: navigatePipelineReview },
          { key: "wonValue", label: "Won value", value: formatCurrencyDisplay(summary.wonValue || summary.orderValue || 0, currency), note: "Sales counted from payment status", accent: "#15803d", compact: true, onClick: navigatePipelineReview },
        ],
      },
      {
        key: "followUp",
        title: "Follow-up Health",
        helper: "Where sales momentum can leak.",
        cards: [
          { key: "overdueFollowUps", label: "Overdue follow-ups", value: summary.overdueTasks || (crmAttentionState.overdueTasks || []).length, note: "Open tasks past due", accent: "#dc2626", onClick: () => navigateAttention("overdueTasks") },
          { key: "noNextAction", label: "Leads without next action", value: summary.leadsWithoutNextAction || (crmAttentionState.missingNextAction || []).length, note: "Needs one clear next step", accent: "#f97316", onClick: () => navigateAttention("missingNextAction") },
          { key: "quotesNoFollowUp", label: "Quotes without follow-up", value: (crmAttentionState.missingQuoteFollowUp || []).length, note: "Sent quotes missing a follow-up date", accent: "#0ea5e9", onClick: () => navigateAttention("missingQuoteFollowUp") },
          { key: "newNotContacted", label: "New leads not contacted", value: Math.max(0, Number(summary.newLeads || 0) - Number(summary.contactedLeads || 0)), note: "New minus contacted in the review range", accent: "#64748b", onClick: navigatePipelineReview },
        ],
      },
      {
        key: "execution",
        title: "Project Execution",
        helper: "Operational health for active orders.",
        cards: [
          { key: "activeProjects", label: "Active projects", value: summary.activeProjects || 0, note: `${formatCurrencyDisplay(activeProjectValue, currency)} active value`, accent: "#2563eb", compact: true, onClick: navigateProjects },
          { key: "installToday", label: "Installation due today", value: (crmAttentionState.installationsDueToday || []).length, note: "Scheduled for today", accent: "#0ea5e9", onClick: () => navigateAttention("installationsDueToday") },
          { key: "installOverdue", label: "Overdue installations", value: (crmAttentionState.overdueInstallations || []).length, note: "Past scheduled date", accent: "#dc2626", onClick: () => navigateAttention("overdueInstallations") },
          { key: "snags", label: "Open snags", value: openSnagCount, note: "Open or overdue correction work", accent: "#f97316", onClick: () => navigateAttention("openSnagProjects") },
          { key: "readyClose", label: "Projects ready to close", value: (crmAttentionState.projectsReadyToClose || []).length || summary.projectsReadyToClose || 0, note: "Installed, paid, and not blocked", accent: "#16a34a", onClick: () => navigateAttention("projectsReadyToClose") },
        ],
      },
      {
        key: "payment",
        title: "Payment Health",
        helper: "Balances and collection work.",
        cards: [
          { key: "pendingBalance", label: "Total pending balance", value: formatCurrencyDisplay(paymentPendingValue, currency), note: `${balancePendingProjects.length} projects with pending balance`, accent: "#d97706", compact: true, onClick: () => navigateAttention("balancePendingProjects") },
          { key: "paymentToday", label: "Payment follow-ups due today", value: (crmAttentionState.paymentDueTodayTasks || []).length, note: "Collection calls due today", accent: "#2563eb", onClick: () => navigateAttention("paymentDueTodayTasks") },
          { key: "paymentOverdue", label: "Overdue payment follow-ups", value: (crmAttentionState.overduePaymentTasks || []).length, note: "Payment tasks past due", accent: "#dc2626", onClick: () => navigateAttention("overduePaymentTasks") },
          { key: "blockedPayment", label: "Blocked by final payment", value: (crmAttentionState.projectsBlockedByFinalPayment || []).length, note: "Ready operationally, waiting for balance", accent: "#ef4444", onClick: () => navigateAttention("projectsBlockedByFinalPayment") },
        ],
      },
      {
        key: "reviews",
        title: "Review / Referral",
        helper: "Post-installation customer asks.",
        cards: [
          { key: "reviewDue", label: "Review requests due", value: reviewDueCount, note: "Requests due today", accent: "#7c3aed", onClick: () => navigateAttention("reviewDueTodayTasks") },
          { key: "reviewOverdue", label: "Overdue review requests", value: overdueReviewCount, note: "Requests past due", accent: "#dc2626", onClick: () => navigateAttention("overdueReviewTasks") },
          { key: "reviewsTracked", label: "Reviews received", value: "Tracked", note: "Open a project to update review status", accent: "#16a34a", onClick: navigateProjects },
        ],
      },
    ];
    const attentionRows = (crmPipelineReviewState.salespersonRows || [])
      .map((row) => ({
        ...row,
        attentionScore: Number(row.overdueTasks || 0) + Number(row.leadsWithoutNextAction || 0),
      }))
      .sort((left, right) =>
        right.attentionScore - left.attentionScore ||
        Number(right.orderValue || 0) - Number(left.orderValue || 0)
      )
      .slice(0, 5);

    return (
      <section style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "flex", justifyContent: "space-between", gap: 14, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center", background: isDark ? "linear-gradient(135deg, rgba(8,47,73,0.82), rgba(15,23,42,0.94))" : "linear-gradient(135deg, #ecfeff, #ffffff)" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", color: "#0891b2", marginBottom: 6 }}>
              Manager Dashboard
            </div>
            <div style={{ fontSize: isCompact ? 23 : 30, fontWeight: 950, letterSpacing: "-0.04em", color: textColor }}>
              Sales and operations health at a glance
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8, lineHeight: 1.65 }}>
              Shows sales, follow-up, payment, and project health for {formatPipelineDateLabel(dateRange.startDate)} to {formatPipelineDateLabel(dateRange.endDate)}.
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, minWidth: isCompact ? "100%" : 240 }}>
            <button type="button" onClick={navigatePipelineReview} style={{ ...btn("#0891b2"), marginRight: 0 }}>
              Open Pipeline Review
            </button>
            <button type="button" onClick={() => loadCrmPipelineReview(workspaceUid, crmPipelineFilters)} disabled={crmPipelineReviewState.loading || !workspaceUid} style={{ ...btn("#0f172a"), marginRight: 0, opacity: crmPipelineReviewState.loading ? 0.7 : 1 }}>
              {crmPipelineReviewState.loading ? "Refreshing..." : "Refresh Manager Data"}
            </button>
          </div>
        </div>

        {crmPipelineReviewState.error ? (
          <div style={{ ...dashboardCardStyle, border: "1px solid rgba(239,68,68,0.22)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
            {crmPipelineReviewState.error}
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14, alignItems: "start" }}>
          {managerSections.map(renderManagerSection)}
        </div>

        <section style={{ ...dashboardCardStyle, display: "grid", gap: 14, overflowX: "auto" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 950 }}>Team Performance</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              Salespeople needing attention are sorted by overdue tasks and missing next actions.
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead>
              <tr>
                {["Salesperson", "Leads", "Quotes sent", "Orders won", "Value won", "Overdue tasks", "Needs next action"].map((heading) => (
                  <th key={heading} style={{ textAlign: "left", padding: "10px 8px", fontSize: 11, color: dashboardMuted, borderBottom: dashboardBorder, textTransform: "uppercase", letterSpacing: "0.04em" }}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {attentionRows.length ? (
                attentionRows.map((row) => (
                  <tr key={`manager-${row.userId}`}>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, fontWeight: 900 }}>{row.userName}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.leads}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.quotesSent}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.ordersWon}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{formatCurrencyDisplay(row.orderValue || 0, currency)}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, color: row.overdueTasks ? "#dc2626" : textColor }}>{row.overdueTasks}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, color: row.leadsWithoutNextAction ? "#f97316" : textColor }}>{row.leadsWithoutNextAction}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ padding: 18, color: dashboardMuted, textAlign: "center" }}>
                    No salesperson activity found for this review period yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </section>
    );
  };

  const renderCrmOverview = () => {
    const overviewCards = [
      {
        key: "active",
        label: "Active Leads",
        value: crmOverviewDrilldowns.active.deals.length,
        note:
          crmScope === CRM_SCOPE_ALL
            ? "Live opportunities across curtains and general quotations"
            : "Live opportunities in the selected pipeline",
        accent: "#2563eb",
      },
      {
        key: "new",
        label: `New Leads ${dashboardPeriodLabel}`,
        value: crmOverviewDrilldowns.new.deals.length,
        note: "Created in the selected period",
        accent: "#0ea5e9",
      },
      {
        key: "value",
        label: "Pipeline Value",
        value: formatCurrencyDisplay(crmOverviewDrilldowns.value.totalValue || 0, currency),
        note: "Expected value across active customer and lead records",
        accent: "#6366f1",
      },
      {
        key: "overdue",
        label: "Overdue Follow-ups",
        value: crmOverviewDrilldowns.overdue.deals.length,
        note: "Missed next actions that need attention",
        accent: "#f97316",
      },
      {
        key: "stalled",
        label: "Stalled Leads",
        value: crmOverviewDrilldowns.stalled.deals.length,
        note: "No updates in the last 10 days",
        accent: "#ef4444",
      },
      {
        key: "installs",
        label: "Upcoming Installations",
        value: crmOverviewDrilldowns.installs.deals.length,
        note:
          crmScope === CRM_SCOPE_ALL
            ? "Upcoming scheduled jobs across the CRM workspace"
            : "Upcoming scheduled jobs for this pipeline",
        accent: "#10b981",
      },
    ];

    if (!canViewAllCrmRecords) {
      return (
        <div style={{ display: "grid", gap: 16 }}>
          {renderDailyWorkQueue({ embedded: true })}
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 6 }}>
            <div style={{ fontSize: 16, fontWeight: 950 }}>
              Your dashboard shows your assigned work.
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Manager dashboard, workspace lead lists, and full pipeline tools are available to admins and managers.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "grid", gap: 16 }}>
        {renderDailyWorkQueue({ embedded: true })}
        {canViewManagerCrmDashboard ? (
          renderManagerDashboard()
        ) : (
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 6 }}>
            <div style={{ fontSize: 16, fontWeight: 950 }}>
              Your dashboard shows your assigned work.
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Manager dashboard is available to admins and managers.
            </div>
          </div>
        )}

        <div
          style={{
            ...dashboardCardStyle,
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            flexDirection: isCompact ? "column" : "row",
            alignItems: isCompact ? "stretch" : "center",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Lead Management</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              Search customers, leads, saved quote contacts, and quote-linked jobs without loading the whole CRM workspace.
            </div>
          </div>
          <button
            type="button"
            onClick={() => openNewCrmLeadModal()}
            style={{ ...btn("#2563eb"), marginRight: 0 }}
          >
            + New Lead
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : isTablet
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          {overviewCards.map((card) => (
            <div
              key={card.key}
              onClick={() => openCrmDrilldown(card.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 24px 48px rgba(2,6,23,0.44)"
                  : "0 24px 48px rgba(15,23,42,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 18px 40px rgba(2,6,23,0.35)"
                  : "0 18px 40px rgba(15,23,42,0.08)";
              }}
              style={{
                ...dashboardCardStyle,
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                boxShadow: isDark
                  ? "0 18px 40px rgba(2,6,23,0.35)"
                  : "0 18px 40px rgba(15,23,42,0.08)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${card.accent}16, transparent 58%)`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 22,
                  boxShadow: `inset 0 0 0 1px ${card.accent}22`,
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "relative", display: "grid", gap: 8 }}>
                <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: isCompact ? 26 : 30, fontWeight: 900 }}>
                  {typeof card.value === "number" ? (
                    <CountUp end={card.value} duration={1.1} />
                  ) : (
                    card.value
                  )}
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>{card.note}</div>
                <div style={{ fontSize: 11, color: card.accent, fontWeight: 700 }}>
                  Click to view records
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.25fr) minmax(320px, 0.95fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Pipeline Stage Overview</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                {crmScope === CRM_SCOPE_ALL
                  ? "Stage occupancy across both CRM pipelines"
                  : `Occupancy across the ${getQuoteTypeLabel(activeCrmPipelineType).toLowerCase()} pipeline`}
              </div>
            </div>
            <div
              style={{
                width: "100%",
                minWidth: 0,
              }}
            >
              {hasCrmStageOverviewData ? (
                renderHorizontalMetricBars(crmStageOverviewChartData, {
                  valueKey: "count",
                  valueLabel: "deals",
                  emptyTitle: "No pipeline stage data yet",
                })
              ) : (
                renderChartEmptyState("No pipeline stage data yet")
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Checklist Completion
              </div>
              {renderChecklistProgress(
                crmPipelineChecklistChartData,
                crmPipelineSummary,
                "No checklist data yet"
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {crmPipelineSummary.checklistCompletion}% of checklist tasks are complete for open deals in the selected CRM scope.
              </div>
            </div>

            {crmScope === CRM_SCOPE_ALL ? (
              <div
                onClick={() => openCrmDrilldown("quoteTypeMix")}
                style={{ ...dashboardCardStyle, cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    Quote Type Mix
                  </div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCrmDrilldown("quoteTypeMix");
                    }}
                    style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                  >
                    View All
                  </button>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {crmQuoteTypeMix.map((entry) => (
                    <div
                      key={entry.name}
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        openCrmDrilldown(entry.key);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          openCrmDrilldown(entry.key);
                        }
                      }}
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 6,
                        cursor: "pointer",
                        transition: "transform 0.16s ease, border-color 0.16s ease",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          flexWrap: isMobile ? "wrap" : "nowrap",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 800,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              background: entry.color,
                              display: "inline-block",
                            }}
                          />
                          {entry.name}
                        </div>
                        <div style={{ fontWeight: 800, minWidth: 0 }}>{entry.count} deals</div>
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted }}>
                        {formatCurrencyDisplay(entry.value || 0, currency)} in pipeline value
                      </div>
                      <div style={{ fontSize: 11, color: entry.color, fontWeight: 800 }}>
                        Open {entry.name} drill-down
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              onClick={() => openCrmDrilldown("topClients")}
              style={{ ...dashboardCardStyle, cursor: "pointer" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800 }}>
                  Top Clients
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openCrmDrilldown("topClients");
                  }}
                  style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                >
                  View All
                </button>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {crmTopClients.slice(0, 5).map((client, index) => (
                  <div
                    key={client.name}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      openCrmDrilldown(`client:${client.name}`);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        openCrmDrilldown(`client:${client.name}`);
                      }
                    }}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      cursor: "pointer",
                      transition: "transform 0.16s ease, border-color 0.16s ease",
                      minWidth: 0,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {index + 1}. {client.name}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {client.count} linked quote{client.count === 1 ? "" : "s"}
                      </div>
                      <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 4 }}>
                        Open client drill-down
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, flex: "0 0 auto", whiteSpace: "nowrap", textAlign: "right", fontSize: isMobile ? 13 : 14 }}>
                      {formatCurrencyDisplay(client.value || 0, currency)}
                    </div>
                  </div>
                ))}
                {crmTopClients.length === 0 && (
                  <div style={{ fontSize: 13, color: dashboardMuted }}>
                    No customer concentration data is available yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Overdue Follow-ups
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmOverdueDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No overdue follow-ups in this pipeline.
                </div>
              ) : (
                crmOverdueDeals.slice(0, 5).map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Upcoming Installations
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmUpcomingDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No upcoming installations scheduled in this pipeline.
                </div>
              ) : (
                crmUpcomingDeals.map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Recent Activity
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmRecentActivityDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No recent CRM activity yet.
                </div>
              ) : (
                crmRecentActivityDeals.map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmLegacyPageControl = (includeRecent = false) => (
    <div style={{ display: "grid", gap: 8, fontSize: 12, color: dashboardMuted }}>
      {crmLeadPaging.error ? <div role="alert">{crmLeadPaging.error}</div> : null}
      {includeRecent && (crmLeadPaging.hasMore || crmLeadPaging.error) ? (
        <button type="button" disabled={crmLeadPaging.loading}
          onClick={() => crmLeadPagerRef.current?.loadPage(crmLeadPaging.error ? crmLeadPaging.retryMode : "recent")}
          style={{ ...btn("#2563eb"), justifySelf: "start" }}>
          {crmLeadPaging.loading ? "Loading..." : crmLeadPaging.error ? "Retry CRM Page" : "Load More Deals"}
        </button>
      ) : null}
      {crmLeadPaging.legacyHasMore ? (
        <button type="button" disabled={crmLeadPaging.loading}
          onClick={() => crmLeadPagerRef.current?.loadPage("legacy")}
          style={{ ...btn("#64748b"), justifySelf: "start" }}>
          Load older / legacy records
        </button>
      ) : null}
      <span>Filters and sorting apply to loaded records. Older records without activity dates are available through bounded legacy pages; complete historical ordering requires reviewed date indexing.</span>
      {crmView === "list" && (isCrmOwnerAdminAccess || isSuperAdmin) ? <details>
        <summary>Archived Deals</summary>
        <p>Archiving preserves history. Orders and payment records are not deleted.</p>
        <button type="button" disabled={crmArchivePage.loading} onClick={() => loadArchivedCrmDeals(false)} style={btn("#64748b")}>
          {crmArchivePage.loading ? "Loading..." : crmArchivePage.error ? "Retry Archived Deals" : "Load Archived Deals"}
        </button>
        {crmArchivePage.error ? <p role="alert">{crmArchivePage.error}</p> : null}
        {crmArchivePage.rows.map((record) => <div key={record.id} style={{ display: "flex", gap: 8, padding: 8, alignItems: "center" }}>
          <span>{record.customerName || record.contact?.name || record.title || "Archived deal"}</span>
          <button type="button" disabled={crmArchivePage.loading} onClick={() => setDealArchiveFromUi(record, false)}>Restore Deal</button>
        </div>)}
        {crmArchivePage.hasMore ? <button type="button" disabled={crmArchivePage.loading} onClick={() => loadArchivedCrmDeals(true)}>Load More Archived Deals</button> : null}
      </details> : null}
      {(() => {
        const text = crmView === "leads" ? deferredCrmLeadSearch : deferredCrmSearch;
        const key = crmView === "leads" ? "crmLeadSearch" : "crmDealSearch";
        const page = crmSearchPagesRef.current.get(key);
        if (normalizeSearchText(text).length < 2 || page?.query !== `${workspaceUid}:${normalizeSearchText(text)}`) return null;
        return page.hasMore ? <button type="button" disabled={page.busy}
          onClick={() => runCrmGlobalSearch(text, key, true)} style={{ ...btn("#0f172a"), justifySelf: "start" }}>
          {page.busy ? "Searching..." : "Load More Search Results"}
        </button> : <span>End of indexed search pages. Legacy records without searchable fields may require reviewed indexing.</span>;
      })()}
    </div>
  );

  const renderCrmBoard = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div
          style={{
            ...dashboardCardStyle,
            display: "grid",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Sales Pipeline Board</div>
              {renderCrmLegacyPageControl(true)}
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Move live leads and quote-linked deals through your workspace sales stages.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1fr) minmax(220px, 260px) minmax(220px, 260px)",
              gap: 12,
            }}
          >
            <input
              value={crmSearch}
              onChange={(e) => setCrmSearch(e.target.value)}
              style={input}
              placeholder="Search customers, leads, phone, quote number, or source"
            />
            <select
              value={crmStageFilter}
              onChange={(e) => setCrmStageFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {crmBoardFilterOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmSourceFilter}
              onChange={(e) => setCrmSourceFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {crmSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>
          {deferredCrmSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search finds searchable customers and leads across pages. Older records may also need legacy loading.
              {globalSearchStatus.crmDealSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmDealSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmDealSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : `repeat(${crmBoardStages.length}, minmax(280px, 1fr))`,
            gap: 14,
            overflowX: isCompact ? "visible" : "auto",
            alignItems: "start",
          }}
        >
          {crmBoardStages.map((stage) => {
            const stageId = String(stage.stageId || stage.id || "").trim();
            const stageDeals = crmBoardDeals.filter(
              (deal) => String(deal.salesStageId || getDealSalesStageId(deal) || "").trim() === stageId
            );

            return (
              <div
                key={stageId}
                style={{
                  ...dashboardCardStyle,
                  minWidth: isCompact ? "auto" : 280,
                  display: "grid",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 16,
                        fontWeight: 800,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: stage.color,
                          display: "inline-block",
                        }}
                      />
                      {stage.label}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {stageDeals.length} lead{stageDeals.length === 1 ? "" : "s"} |{" "}
                      {formatCurrencyDisplay(
                        stageDeals.reduce(
                          (sum, deal) => sum + Number(deal.expectedValue || 0),
                          0
                        ),
                        currency
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  {stageDeals.length === 0 ? (
                    <div
                      style={{
                        padding: 16,
                        borderRadius: 16,
                        border: dashboardBorder,
                        background: dashboardSoftBg,
                        color: dashboardMuted,
                        fontSize: 13,
                      }}
                    >
                      No leads in this stage.
                    </div>
                  ) : (
                    stageDeals.map((deal) =>
                      renderCrmDealCard(deal, {
                        dense: true,
                        renderSource: "PipelineBoard",
                      })
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isCompact ? renderCrmDetailPanel() : null}
    </div>
  );

  const renderCrmList = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {crmActiveDrilldown ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
  <div
    style={{
      fontSize: 12,
      fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 6,
                }}
              >
                Showing
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>
                {crmActiveDrilldown.label}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
                {crmDealListBaseDeals.length} matching customer/lead
                {crmDealListBaseDeals.length === 1 ? "" : "s"}
                {" | "}
                {formatCurrencyDisplay(
                  crmActiveDrilldown.totalValue ??
                    crmDealListBaseDeals.reduce(
                      (sum, deal) => sum + Number(deal.expectedValue || 0),
                      0
                    ),
                  currency
                )}{" "}
                total value
                {crmActiveDrilldown.key === "value"
                  ? ` | ${formatCurrencyDisplay(
                      crmActiveDrilldown.totalValue || 0,
                      currency
                    )} total pipeline value`
                  : ""}
              </div>
              {crmActiveDrilldown.description ? (
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                  {crmActiveDrilldown.description}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={clearCrmDrilldown}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Clear Filter
              </button>
            </div>
          </div>
        ) : null}

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Customer / Lead List</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Review customers, leads, and quote-linked jobs in one simple workspace.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1fr) repeat(5, minmax(150px, 210px))",
              gap: 12,
            }}
          >
            <input
              value={crmSearch}
              onChange={(e) => setCrmSearch(e.target.value)}
              style={input}
              placeholder="Search customers, leads, phone, quote number, or source"
            />
            <select
              value={crmStageFilter}
              onChange={(e) => setCrmStageFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {crmStageFilterOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmTypeFilter}
              onChange={(e) => setCrmTypeFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Types</option>
              {canAccessCurtainQuotes ? (
                <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
              ) : null}
              {canAccessGeneralQuotes ? (
                <option value={GENERAL_QUOTE_TYPE}>General Quotation</option>
              ) : null}
            </select>
            <select
              value={crmSourceFilter}
              onChange={(e) => setCrmSourceFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {crmSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={crmSortBy}
              onChange={(e) => setCrmSortBy(e.target.value)}
              style={selectInput}
            >
              <option value="newest">Newest Updated</option>
              <option value="oldest">Oldest Updated</option>
              <option value="highest">Highest Value</option>
              <option value="client">Client Name</option>
            </select>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 14,
                border: dashboardBorder,
                background: dashboardSoftBg,
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Showing {visibleCrmDeals.length} of {crmFilteredDeals.length} loaded matching record{crmFilteredDeals.length === 1 ? "" : "s"}
              {crmActiveDrilldown &&
              crmFilteredDeals.length !== crmDealListBaseDeals.length
                ? ` | ${crmDealListBaseDeals.length} in drill-down`
                : ""}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {crmFilteredDeals.length === 0 ? (
            <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
              No customers or leads match the current filters.
            </div>
          ) : (
            visibleCrmDeals.map((deal) => renderCrmDealCard(deal))
          )}
          {hasMoreCrmDeals || crmLeadPaging.hasMore || crmLeadPaging.error ? (
            <button
              type="button"
              disabled={crmLeadPaging.loading}
              onClick={() => hasMoreCrmDeals
                ? setCrmDealsVisibleCount((prev) => prev + CRM_DEALS_PAGE_SIZE)
                : crmLeadPagerRef.current?.loadPage(crmLeadPaging.error ? crmLeadPaging.retryMode : "recent")}
              style={{
                ...btn(deferredCrmSearch ? "#0f172a" : "#2563eb"),
                marginRight: 0,
                justifySelf: "center",
                minWidth: 220,
              }}
            >
              {crmLeadPaging.loading ? "Loading..." : crmLeadPaging.error ? "Retry CRM Page" : "Load More Records"}
            </button>
          ) : crmFilteredDeals.length > CRM_DEALS_PAGE_SIZE ? (
            <div style={{ textAlign: "center", color: dashboardMuted, fontSize: 12 }}>
              All currently loaded matching records are shown.
            </div>
          ) : null}
          {renderCrmLegacyPageControl()}
        </div>
      </div>

      {isCompact ? renderCrmDetailPanel() : null}
    </div>
  );

  const renderCrmProjects = () => {
    const normalizedOrderPipeline = normalizeOrderPipeline(orderPipeline);
    const configuredActiveOrderStages = orderPipelineActiveStages.length
      ? orderPipelineActiveStages
      : getActiveOrderPipelineStages(normalizedOrderPipeline);
    const showClosedCancelledOrders = crmProjectFilter === "all";
    const baseVisibleProjectRecords = canViewAllCrmRecords
      ? crmProjectRecords
      : crmProjectRecords.filter((project) => isCrmRecordVisibleToCurrentUser(project));
    const visibleCrmProjectRecords = baseVisibleProjectRecords.filter((project) => {
      const resolvedStage = resolveProjectPipelineStage(project);
      if (showClosedCancelledOrders) return true;
      return resolvedStage !== "project_closed" && resolvedStage !== "cancelled";
    });
    const configuredStageIds = new Set(
      configuredActiveOrderStages.map((stage) => stage.stageId)
    );
    const fallbackVisibleStages = Array.from(
      new Set(visibleCrmProjectRecords.map((project) => resolveProjectPipelineStage(project)))
    )
      .filter((stageId) => !configuredStageIds.has(stageId))
      .map((stageId) => {
        const stageMeta = getOrderPipelineStage(normalizedOrderPipeline, stageId);
        return stageMeta?.stageId === stageId
          ? stageMeta
          : {
              stageId,
              id: stageId,
              label: getOrderPipelineStageLabel(normalizedOrderPipeline, stageId),
              color: "#64748b",
              active: true,
            };
      });
    const activeOrderStages = [...configuredActiveOrderStages, ...fallbackVisibleStages];
    const projectsByOrderStage = activeOrderStages.reduce((acc, stage) => {
      acc[stage.stageId] = [];
      return acc;
    }, {});
    visibleCrmProjectRecords.forEach((project) => {
      const resolvedStage = resolveProjectPipelineStage(project);
      if (!projectsByOrderStage[resolvedStage]) {
        projectsByOrderStage[resolvedStage] = [];
      }
      projectsByOrderStage[resolvedStage].push(project);
      if (isDevRuntime) {
        devLog("ORDER_PIPELINE_FILTER_TRACE", {
          projectId: project.projectId || project.id || "",
          quoteNo: project.quoteNo || "",
          customerName: project.customerName || "",
          rawStage: project.raw?.stage || project.stage || "",
          projectStage: project.projectStage || project.raw?.projectStage || "",
          orderStage: project.orderStage || project.raw?.orderStage || "",
          status: project.status || "",
          resolvedStage,
          includedStage: resolvedStage,
          includeReason: "resolved_single_pipeline_stage",
        });
      }
    });
    const selectedProject =
      visibleCrmProjectRecords.find(
        (project) =>
          String(project.projectId || project.id) === String(selectedCrmProjectId || "")
      ) || null;
    const attentionSummary = visibleCrmProjectRecords.reduce(
      (summary, project) => {
        const balance = Number(project.paymentSummary?.balanceAmount ?? project.balanceAmount ?? 0) || 0;
        if (balance > 0) summary.paymentPending += 1;
        if (project.hasOpenSnags || Number(project.openSnagCount || 0) > 0) summary.openSnags += 1;
        if (project.installationStatus === PROJECT_INSTALLATION_STATUS_SCHEDULED) summary.installationScheduled += 1;
        if (project.reviewSummary?.status === PROJECT_REVIEW_STATUS_PENDING) summary.reviewPending += 1;
        return summary;
      },
      { paymentPending: 0, openSnags: 0, installationScheduled: 0, reviewPending: 0 }
    );

    const updateSelectedProjectChecklist = async (key, checked) => {
      if (!selectedProject?.projectId) return;
      const nextChecklist = {
        ...(selectedProject.checklist || {}),
        [key]: checked,
      };
      const checklistSummary = calculateProjectChecklistSummary(nextChecklist);
      const stagePatch =
        key === "materialReceived" && checked
          ? PROJECT_STAGE_MATERIAL_RECEIVED
          : key === "productionStarted" && checked
          ? PROJECT_STAGE_PRODUCTION_STITCHING
          : key === "productionCompleted" && checked
          ? PROJECT_STAGE_QC
          : key === "qcCompleted" && checked
          ? PROJECT_STAGE_INSTALLATION_SCHEDULED
          : null;
      try {
        await updateCrmProjectPatch(selectedProject.projectId, {
          checklist: nextChecklist,
          checklistSummary,
          ...(stagePatch ? { stage: stagePatch } : {}),
          lastActivityAt: Timestamp.fromDate(new Date()),
          lastActivityType: "project_checklist_updated",
          lastActivityLabel: PROJECT_EXECUTION_CHECKLIST_LABELS[key] || "Checklist updated",
        });
        const activity = await logCrmActivity({
          relatedType: "project",
          relatedId: selectedProject.projectId,
          projectId: selectedProject.projectId,
          customerId: selectedProject.customerId || "",
          customerName: selectedProject.customerName || "",
          quoteId: selectedProject.quoteId || "",
          quoteNo: selectedProject.quoteNo || "",
          activityType: "checklist_updated",
          title: checked ? "Checklist item completed" : "Checklist item reopened",
          description: PROJECT_EXECUTION_CHECKLIST_LABELS[key] || key,
        }).catch(() => null);
        if (activity) prependProjectActivityLocal(selectedProject.projectId, activity);
        notify("Project checklist updated", "success");
      } catch (error) {
        devError("CRM_PROJECT_CHECKLIST_UPDATE_FAILED_EXACT", {
          code: error?.code,
          message: error?.message,
          projectId: selectedProject.projectId,
          key,
        });
        notify("Could not update checklist. Please try again.", "error");
      }
    };

    const renderProjectDrawer = () => {
      if (!selectedProject) {
        return (
          <section style={{ ...dashboardCardStyle, color: dashboardMuted, fontSize: 13 }}>
            Select a project/order to see payments, installation, snags, and next actions.
          </section>
        );
      }
      const drawerData =
        crmProjectDrawerData.projectId === selectedProject.projectId
      ? crmProjectDrawerData
      : {
              tasks: [],
              trackingItems: [],
              payments: [],
              snags: [],
              activity: [],
              loading: { tasks: false, tracking: false, payments: false, snags: false, activity: false },
              errors: { tasks: "", tracking: "", payments: "", snags: "", activity: "" },
            };
      const tabs = [
        { key: "overview", label: "Overview" },
        { key: "checklist", label: "Checklist" },
        { key: "installation", label: "Installation" },
        { key: "tasks", label: "Tasks" },
        ...(crmSettings.enableDeliveryTracking === false
          ? []
          : [{ key: "tracking", label: "Tracking" }]),
        ...(crmSettings.enablePaymentTracking === false
          ? []
          : canViewCrmPaymentsAccess
          ? [{ key: "payments", label: "Payments" }]
          : []),
        { key: "snags", label: "Snags" },
        { key: "activity", label: "Activity" },
      ];
      const orderTaskTemplateKey = Object.prototype.hasOwnProperty.call(
        ORDER_TASK_TEMPLATES,
        crmSettings.defaultOrderTaskTemplate
      )
        ? crmSettings.defaultOrderTaskTemplate
        : STANDARD_ORDER_TASK_TEMPLATE_KEY;
      const orderTaskTemplate = ORDER_TASK_TEMPLATES[orderTaskTemplateKey] || [];
      const templateTaskKeys = new Set(
        orderTaskTemplate.map((task) => task.templateTaskKey).filter(Boolean)
      );
      const projectTemplateTasks = (drawerData.tasks || []).filter(
        (task) =>
          task.templateKey === orderTaskTemplateKey &&
          templateTaskKeys.has(task.templateTaskKey)
      );
      const existingTemplateTaskKeys = new Set(
        projectTemplateTasks.map((task) => task.templateTaskKey).filter(Boolean)
      );
      const missingTemplateTasks =
        crmSettings.enableOrderTaskTemplates === false
          ? []
          : orderTaskTemplate.filter(
              (task) => !existingTemplateTaskKeys.has(task.templateTaskKey)
            );
      const completedTemplateTaskCount = projectTemplateTasks.filter(
        (task) => task.status === CRM_TASK_STATUS_COMPLETED
      ).length;
      const overdueTemplateTaskCount = projectTemplateTasks.filter(
        (task) =>
          task.status !== CRM_TASK_STATUS_COMPLETED &&
          task.status !== CRM_TASK_STATUS_CANCELLED &&
          task.dueMillis &&
          task.dueMillis < Date.now()
      ).length;
      const projectMaterials = Array.isArray(drawerData.trackingItems)
        ? drawerData.trackingItems
        : [];
      const materialSummary = projectMaterials.reduce(
        (summary, material) => {
          summary.total += 1;
          if (material.status === "received") summary.received += 1;
          if (["ordered", "in_transit", "reached_local_hub", "partially_received"].includes(material.status)) {
            summary.inTransit += 1;
          }
          if (material.status === "delayed" || material.isDelayed) summary.delayed += 1;
          return summary;
        },
        { total: 0, received: 0, inTransit: 0, delayed: 0 }
      );
      const allProjectMaterialsReceived =
        materialSummary.total > 0 && materialSummary.received === materialSummary.total;
      const deliveryTrackingSummary = selectedProject.deliveryTracking || {};
      const selectedPaymentSummary = selectedProject.paymentSummary || {};
      const selectedPaymentReceived = Number(
        selectedPaymentSummary.totalReceived ?? selectedProject.totalReceived ?? 0
      ) || 0;
      const selectedPaymentBalance = Number(
        selectedPaymentSummary.balanceAmount ?? selectedProject.balanceAmount ?? 0
      ) || 0;
      const selectedPaymentOrderValue = Number(
        selectedPaymentSummary.orderValue ?? selectedProject.orderValue ?? 0
      ) || 0;
      const selectedPaymentStatusLabel =
        selectedProject.paymentStatusLabel ||
        getPaymentStatusLabel(selectedPaymentSummary.paymentStatus || selectedProject.paymentStatus);
      const selectedPaymentOverdue =
        selectedProject.paymentOverdue ||
        selectedPaymentSummary.paymentStatus === PROJECT_PAYMENT_STATUS_OVERDUE;
      const projectPayments = Array.isArray(drawerData.payments) ? drawerData.payments : [];
      const projectSnags = Array.isArray(drawerData.snags) ? drawerData.snags : [];
      const drawerSnagsLoadedForSelectedProject =
        Array.isArray(drawerData.snags) &&
        [selectedProject.projectId, selectedProject.id]
          .map((value) => String(value || "").trim())
          .filter(Boolean)
          .some((id) => String(drawerData.projectId || "") === id);
      const visibleOpenSnags = projectSnags.filter(
        (snag) => String(snag.status || "") !== "resolved"
      ).length;
      const visibleResolvedSnags = projectSnags.filter(
        (snag) => String(snag.status || "") === "resolved"
      ).length;
      const visibleNextOpenSnagDueAt =
        projectSnags
          .filter((snag) => String(snag.status || "") !== "resolved" && snag.dueAt)
          .map((snag) => snag.dueAt)
          .sort((left, right) => safeTimeMillis(left) - safeTimeMillis(right))[0] || null;
      const selectedSnagSummary = drawerSnagsLoadedForSelectedProject
        ? normalizeProjectSnagSummary({
            ...(selectedProject.snagSummary || {}),
            openCount: visibleOpenSnags,
            resolvedCount: visibleResolvedSnags,
            totalCount: projectSnags.length,
            nextDueAt: visibleNextOpenSnagDueAt,
          })
        : selectedProject.snagSummary || {};
      const selectedProjectForClose = drawerSnagsLoadedForSelectedProject
        ? {
            ...selectedProject,
            snagSummary: selectedSnagSummary,
            hasOpenSnags: visibleOpenSnags > 0,
            openSnagCount: visibleOpenSnags,
            nextSnagDueAt: selectedSnagSummary.nextDueAt || null,
          }
        : selectedProject;
      const selectedReviewSummary = selectedProject.reviewSummary || {};
      const projectCloseGuard = getProjectCloseGuard(selectedProjectForClose);
      const canUpdateSelectedProjectExecution =
        canUpdateCrmProjectExecution(selectedProject);
      const isCreatingDefaultOrderTasks =
        creatingOrderTaskTemplateForProjectId === selectedProject.projectId;
      const renderCreateMissingOrderTasksButton = () =>
        crmSettings.enableOrderTaskTemplates !== false && missingTemplateTasks.length ? (
          <button
            type="button"
            onClick={() => createMissingDefaultOrderTasks(selectedProject)}
            disabled={isCreatingDefaultOrderTasks}
            style={{
              ...btn(isCreatingDefaultOrderTasks ? "#94a3b8" : "#2563eb"),
              marginRight: 0,
              padding: "8px 12px",
              opacity: isCreatingDefaultOrderTasks ? 0.72 : 1,
            }}
          >
            {isCreatingDefaultOrderTasks
              ? "Creating tasks..."
              : `Create ${missingTemplateTasks.length} missing default task${
                  missingTemplateTasks.length === 1 ? "" : "s"
                }`}
          </button>
        ) : null;

      const renderProjectTab = () => {
        if (crmProjectDrawerTab === "checklist") {
          return (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                  Progress
                </div>
                <div style={{ fontSize: 20, fontWeight: 950, color: textColor, marginTop: 4 }}>
                  {selectedProject.checklistSummary?.completedItems || 0} / {selectedProject.checklistSummary?.totalItems || 0} done
                  {selectedProject.checklistSummary?.completionPercent != null
                    ? ` | ${selectedProject.checklistSummary.completionPercent}%`
                    : ""}
                </div>
              </div>
              {PROJECT_EXECUTION_CHECKLIST_GROUPS.map((group) => (
                <section
                  key={group.key}
                  style={{
                    display: "grid",
                    gap: 8,
                    padding: 12,
                    borderRadius: 16,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                  }}
                >
                  <div style={{ fontWeight: 950, color: textColor }}>
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <label
                      key={item.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: 10,
                        borderRadius: 12,
                        background: isDark ? "rgba(15,23,42,0.38)" : "#ffffff",
                        border: dashboardBorder,
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProject.checklist?.[item.key] === true}
                        onChange={(event) =>
                          updateSelectedProjectChecklist(item.key, event.target.checked)
                        }
                      />
                      {item.label}
                    </label>
                  ))}
                </section>
              ))}
            </div>
          );
        }

        if (crmProjectDrawerTab === "installation") {
          const installStatus = normalizeProjectInstallationStatus(
            selectedProject.installationStatus
          );
          return (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["Status", getProjectInstallationStatusLabel(installStatus)],
                  ["Scheduled", formatCrmDate(selectedProject.installationScheduledAt)],
                  ["Completed", formatCrmDate(selectedProject.installationCompletedAt)],
                  ["Expected", formatCrmDate(selectedProject.expectedInstallationDate)],
                  ["Owner", selectedProject.projectOwnerName || "Unassigned"],
                  ["Site", [selectedProject.siteName, selectedProject.siteAddress].filter(Boolean).join(" - ") || "Not set"],
                ].map(([label, value]) => (
                  <div key={label} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                    <div style={{ fontSize: 13, color: textColor, fontWeight: 900, marginTop: 5 }}>{value || "Not set"}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => openCrmInstallationModal(selectedProject, "schedule")}
                  disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
                  title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
                  style={{ ...btn(canUpdateSelectedProjectExecution ? "#2563eb" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
                >
                  Schedule Installation
                </button>
                <button
                  type="button"
                  onClick={() => openCrmInstallationModal(selectedProject, "complete")}
                  disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
                  title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
                  style={{ ...btn(canUpdateSelectedProjectExecution ? "#16a34a" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
                >
                  Mark Installation Completed
                </button>
                <button
                  type="button"
                  onClick={() => openCrmSnagModal(selectedProject)}
                  disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
                  title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
                  style={{ ...btn(canUpdateSelectedProjectExecution ? "#f97316" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
                >
                  Add Snag
                </button>
              </div>
            </div>
          );
        }

        if (crmProjectDrawerTab === "tasks") {
          if (drawerData.loading?.tasks) {
            return <div style={{ color: dashboardMuted }}>Loading tasks...</div>;
          }
          if (drawerData.errors?.tasks) {
            return <div style={{ color: "#dc2626" }}>{drawerData.errors.tasks}</div>;
          }
          if (!drawerData.tasks.length) {
            return (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: dashboardMuted, lineHeight: 1.6 }}>
                  No project tasks yet. Create default order tasks so the team knows what to do next.
                </div>
                {renderCreateMissingOrderTasksButton()}
              </div>
            );
          }
          return (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                  Order Tasks: {completedTemplateTaskCount} / {orderTaskTemplate.length} completed
                  {overdueTemplateTaskCount ? ` | ${overdueTemplateTaskCount} overdue` : ""}
                </div>
                {renderCreateMissingOrderTasksButton()}
              </div>
              {drawerData.tasks.map((task) => (
                <div
                  key={task.taskId || task.id}
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {task.title || "Project task"}
                      {task.systemGenerated ? (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "2px 7px",
                            borderRadius: 999,
                            background: "rgba(37,99,235,0.10)",
                            color: "#2563eb",
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          Auto-created
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      Due {formatCrmDate(task.dueAt || task.dueDate)} | {task.ownerName || "Unassigned"} | {task.priority || "normal"} | {task.status || "open"}
                    </div>
                  </div>
                  {task.status !== CRM_TASK_STATUS_COMPLETED ? (
                    <button
                      type="button"
                      onClick={() =>
                        markDailyTaskDone({
                          ...task,
                          id: `crm-task:${task.taskId || task.id}`,
                          sourceId: task.taskId || task.id,
                          sourceType: DAILY_TASK_SOURCE_CRM_TASK,
                          crmTask: task,
                          original: task,
                        })
                      }
                      style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 12px" }}
                    >
                      Mark as Done
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          );
        }

        if (crmProjectDrawerTab === "tracking") {
          if (crmSettings.enableDeliveryTracking === false) {
            return <div style={{ color: dashboardMuted }}>Delivery tracking is disabled in CRM Settings.</div>;
          }
          if (drawerData.loading?.tracking) {
            return <div style={{ color: dashboardMuted }}>Loading tracking items...</div>;
          }
          if (drawerData.errors?.tracking) {
            return <div style={{ color: "#dc2626" }}>{drawerData.errors.tracking}</div>;
          }
          const renderStatusButton = (material, nextStatus, label, color = "#0f172a") => {
            const actionKey = `${material.materialId || material.id}:${nextStatus}`;
            return (
              <button
                key={`${material.materialId || material.id}:${nextStatus}`}
                type="button"
                onClick={() => updateCrmMaterialStatus(material, nextStatus)}
                disabled={Boolean(crmMaterialActionKey)}
                style={{
                  ...btn(color),
                  marginRight: 0,
                  padding: "7px 10px",
                  fontSize: 12,
                  opacity: crmMaterialActionKey === actionKey ? 0.72 : 1,
                }}
              >
                {crmMaterialActionKey === actionKey ? "Saving..." : label}
              </button>
            );
          };
          const statusBadgeStyle = (status) => {
            const color =
              status === "received"
                ? "#16a34a"
                : status === "delayed" || status === "cancelled"
                ? "#dc2626"
                : status === "in_transit" ||
                  status === "reached_local_hub" ||
                  status === "partially_received"
                ? "#2563eb"
                : status === "ordered"
                ? "#7c3aed"
                : "#d97706";
            return {
              padding: "4px 8px",
              borderRadius: 999,
              background: `${color}18`,
              color,
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            };
          };
          return (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                    {materialSummary.total} total | {materialSummary.received} received | {materialSummary.inTransit} active/in transit | {materialSummary.delayed} delayed
                  </span>
                  {allProjectMaterialsReceived ? (
                    <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(22,163,74,0.12)", color: "#16a34a", fontSize: 11, fontWeight: 900 }}>
                      All materials received
                    </span>
                  ) : materialSummary.delayed ? (
                    <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(245,158,11,0.14)", color: "#b45309", fontSize: 11, fontWeight: 900 }}>
                      Material delay
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => openCrmMaterialModal(selectedProject)}
                  style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 12px" }}
                >
                  Add Tracking Item
                </button>
              </div>

              {!projectMaterials.length ? (
                <div style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg, color: dashboardMuted, fontSize: 13 }}>
                  No material tracking items added yet. Add fabrics, tracks, motors, courier/LR details, or other order materials here.
                </div>
              ) : null}

              {projectMaterials.map((material) => (
                <div
                  key={material.materialId || material.id}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 950, color: textColor }}>
                        {material.displayTitle || "Material"}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.55 }}>
                        {material.itemTypeLabel || material.materialTypeLabel} | Supplier: {material.supplierName || "Not set"} | Expected: {formatCrmDate(material.expectedArrivalDate)}
                      </div>
                    </div>
                    <span style={statusBadgeStyle(material.status)}>
                      {material.statusLabel || getTrackingStatusLabel(material.status)}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                    {[
                      ["Quantity", material.quantity == null ? "Not set" : `${material.quantity} ${material.unit || ""}`.trim()],
                      ["Ordered", material.orderedQuantity == null ? "Not set" : `${material.orderedQuantity} ${material.unit || ""}`.trim()],
                      ["Received", material.receivedQuantity == null ? "Not set" : `${material.receivedQuantity} ${material.unit || ""}`.trim()],
                      ["Pending", material.pendingQuantity == null ? "Not set" : `${material.pendingQuantity} ${material.unit || ""}`.trim()],
                      ["Order date", formatCrmDate(material.orderDate)],
                      ["Received date", formatCrmDate(material.receivedDate)],
                      ["Courier / LR", material.trackingInfo || "Not set"],
                      ["Last checked", formatCrmDate(material.lastCheckedAt)],
                      ["Priority", material.priority === "urgent" ? "Urgent" : "Normal"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: 10, borderRadius: 12, background: isDark ? "rgba(15,23,42,0.44)" : "#ffffff", border: dashboardBorder }}>
                        <div style={{ fontSize: 10.5, color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                        <div style={{ fontSize: 12.5, color: textColor, fontWeight: 900, marginTop: 4 }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {material.trackingUrl ? (
                    <a
                      href={material.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, justifySelf: "start" }}
                    >
                      Open tracking link
                    </a>
                  ) : null}
                  {material.lastUpdateNote || material.notes ? (
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      {material.lastUpdateNote ? `Last update: ${material.lastUpdateNote}` : `Notes: ${material.notes}`}
                    </div>
                  ) : null}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => openCrmMaterialModal(selectedProject, material)}
                      disabled={Boolean(crmMaterialActionKey)}
                      style={{ ...btn("#0f172a"), marginRight: 0, padding: "7px 10px", fontSize: 12 }}
                    >
                      Edit
                    </button>
                    {renderStatusButton(material, "ordered", "Mark Ordered", "#7c3aed")}
                    {renderStatusButton(material, "in_transit", "Mark In Transit", "#2563eb")}
                    {renderStatusButton(material, "reached_local_hub", "Reached Local Hub", "#0891b2")}
                    {renderStatusButton(material, "partially_received", "Partially Received", "#0ea5e9")}
                    {renderStatusButton(material, "received", "Mark Received", "#16a34a")}
                    {renderStatusButton(material, "delayed", "Mark Delayed", "#d97706")}
                    {renderStatusButton(material, "cancelled", "Cancel", "#dc2626")}
                    <button
                      type="button"
                      onClick={() => createCrmMaterialFollowUpTask(material)}
                      disabled={Boolean(crmMaterialActionKey)}
                      style={{ ...btn("#475569"), marginRight: 0, padding: "7px 10px", fontSize: 12 }}
                    >
                      Create Follow-up Task
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        }

        if (crmProjectDrawerTab === "payments") {
          if (crmSettings.enablePaymentTracking === false) {
            return <div style={{ color: dashboardMuted }}>Payment tracking is disabled in CRM Settings.</div>;
          }
          if (!canViewCrmPaymentsAccess) {
            return <div style={{ color: dashboardMuted }}>This payment section is restricted.</div>;
          }
          if (drawerData.loading?.payments) {
            return <div style={{ color: dashboardMuted }}>Loading payments...</div>;
          }
          if (drawerData.errors?.payments) {
            return <div style={{ color: "#dc2626" }}>{drawerData.errors.payments}</div>;
          }
          return (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["Order Value", formatCurrencyDisplay(selectedPaymentOrderValue, currency)],
                  ["Received", formatCurrencyDisplay(selectedPaymentReceived, currency)],
                  ["Balance", formatCurrencyDisplay(selectedPaymentBalance, currency)],
                  [
                    "Next Follow-up",
                    selectedPaymentSummary.nextPaymentDueDate
                      ? `${formatCrmDate(selectedPaymentSummary.nextPaymentDueDate)}${
                          selectedPaymentSummary.paymentFollowUpOwnerName
                            ? ` | ${selectedPaymentSummary.paymentFollowUpOwnerName}`
                            : ""
                        }`
                      : "Not set",
                  ],
                ].map(([label, value]) => (
                  <div key={label} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                    <div style={{ fontSize: 13, color: textColor, fontWeight: 900, marginTop: 5 }}>{value}</div>
                  </div>
                ))}
              </div>

              {selectedPaymentOverdue ? (
                <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.24)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
                  Payment follow-up is overdue.
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => openCrmPaymentModal(selectedProject)}
                  style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 12px" }}
                >
                  Add Payment
                </button>
                {selectedPaymentBalance > 0 ? (
                  <button
                    type="button"
                    onClick={() => openPaymentFollowUpModal(selectedProject)}
                    style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 12px" }}
                  >
                    Create Payment Follow-up
                  </button>
                ) : null}
              </div>

              {!projectPayments.length ? (
                <div style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg, color: dashboardMuted, fontSize: 13 }}>
                  No payments added yet. Add advance or payment received from the customer.
                </div>
              ) : null}

              {projectPayments.map((payment) => (
                <div
                  key={payment.paymentId || payment.id}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 950, color: textColor }}>
                        {formatCurrencyDisplay(payment.amount || 0, currency)}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {getPaymentTypeLabel(payment.paymentType)} | {getPaymentModeLabel(payment.paymentMode)} | {formatCrmDate(payment.paymentDate)}
                      </div>
                    </div>
                    <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(22,163,74,0.12)", color: "#16a34a", fontSize: 11, fontWeight: 900 }}>
                      Payment recorded
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                    Collected by {payment.collectedByName || "Not set"}
                    {payment.referenceNo ? ` | Ref: ${payment.referenceNo}` : ""}
                  </div>
                  {payment.notes ? (
                    <div style={{ fontSize: 13, color: textColor, lineHeight: 1.6 }}>
                      {payment.notes}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        }

        if (crmProjectDrawerTab === "snags") {
          if (drawerData.loading?.snags) {
            return <div style={{ color: dashboardMuted }}>Loading snags...</div>;
          }
          if (drawerData.errors?.snags) {
            return <div style={{ color: "#dc2626" }}>{drawerData.errors.snags}</div>;
          }
          return (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                  Open: {visibleOpenSnags} | Resolved: {visibleResolvedSnags}
                </div>
                <button
                  type="button"
                  onClick={() => openCrmSnagModal(selectedProject)}
                  disabled={Boolean(crmProjectActionKey)}
                  style={{ ...btn("#f97316"), marginRight: 0, padding: "8px 12px" }}
                >
                  Add Snag
                </button>
              </div>
              {!projectSnags.length ? (
                <div style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg, color: dashboardMuted, fontSize: 13 }}>
                  No snags recorded for this project. Use Add Snag if installation or quality corrections are needed.
                </div>
              ) : null}
              {projectSnags.map((snag) => {
                const isResolved = String(snag.status || "") === "resolved";
                return (
                  <div
                    key={snag.snagId || snag.id}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 950, color: textColor }}>
                          {snag.title || "Snag / correction"}
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                          Due {formatCrmDate(snag.dueAt)} | {snag.ownerName || "Unassigned"} | {snag.priority || "normal"}
                        </div>
                      </div>
                      <span style={{ padding: "4px 8px", borderRadius: 999, background: isResolved ? "rgba(22,163,74,0.12)" : "rgba(249,115,22,0.14)", color: isResolved ? "#16a34a" : "#f97316", fontSize: 11, fontWeight: 900 }}>
                        {isResolved ? "Resolved" : "Open"}
                      </span>
                    </div>
                    {snag.description ? (
                      <div style={{ fontSize: 13, color: textColor, lineHeight: 1.6 }}>
                        {snag.description}
                      </div>
                    ) : null}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => openCrmSnagModal(selectedProject, snag)}
                        disabled={Boolean(crmProjectActionKey)}
                        style={{ ...btn("#0f172a"), marginRight: 0, padding: "7px 10px", fontSize: 12 }}
                      >
                        Edit
                      </button>
                      {!isResolved ? (
                        <button
                          type="button"
                          onClick={() => resolveCrmSnag(snag)}
                          disabled={Boolean(crmProjectActionKey)}
                          style={{ ...btn("#16a34a"), marginRight: 0, padding: "7px 10px", fontSize: 12 }}
                        >
                          {crmProjectActionKey === `snag:${snag.snagId || snag.id}:resolve`
                            ? "Saving..."
                            : "Resolve Snag"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        if (crmProjectDrawerTab === "activity") {
          if (drawerData.loading?.activity) {
            return <div style={{ color: dashboardMuted }}>Loading activity...</div>;
          }
          if (drawerData.errors?.activity) {
            return <div style={{ color: "#dc2626" }}>{drawerData.errors.activity}</div>;
          }
          if (!drawerData.activity.length) {
            return (
              <div style={{ color: dashboardMuted, lineHeight: 1.6 }}>
                No activity yet. Payment, installation, snag, and close updates will appear here.
              </div>
            );
          }
          return (
            <div style={{ display: "grid", gap: 10 }}>
              {drawerData.activity.map((entry) => (
                <div
                  key={entry.activityId || entry.id}
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                  }}
                >
                  <div style={{ fontWeight: 900 }}>{entry.title || "Activity"}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {formatCrmDate(entry.createdAt)} {entry.createdByName ? `| ${entry.createdByName}` : ""}
                  </div>
                  {entry.description ? (
                    <div style={{ fontSize: 13, color: textColor, marginTop: 6 }}>
                      {entry.description}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        }

        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 10,
            }}
          >
            {[
              ["Customer", selectedProject.customerName],
              ["Site", [selectedProject.siteName, selectedProject.siteAddress].filter(Boolean).join(" - ") || "Not set"],
              ["Quote", selectedProject.quoteNo || selectedProject.quoteId || "Not set"],
              [
                "Order value",
                canViewCrmPaymentsAccess
                  ? formatCurrencyDisplay(selectedPaymentOrderValue, currency)
                  : "Restricted",
              ],
              [
                "Received",
                canViewCrmPaymentsAccess
                  ? formatCurrencyDisplay(selectedPaymentReceived, currency)
                  : "Restricted",
              ],
              [
                "Balance",
                canViewCrmPaymentsAccess
                  ? formatCurrencyDisplay(selectedPaymentBalance, currency)
                  : "Restricted",
              ],
              ["Payment", canViewCrmPaymentsAccess ? selectedPaymentStatusLabel : "Restricted"],
              [
                "Payment Follow-up",
                !canViewCrmPaymentsAccess
                  ? "Restricted"
                  : selectedPaymentSummary.nextPaymentDueDate
                  ? `${formatCrmDate(selectedPaymentSummary.nextPaymentDueDate)}${
                      selectedPaymentSummary.paymentFollowUpOwnerName
                        ? ` | ${selectedPaymentSummary.paymentFollowUpOwnerName}`
                        : ""
                    }`
                  : selectedPaymentBalance > 0
                  ? "Not scheduled"
                  : "No balance pending",
              ],
              ["Owner", selectedProject.projectOwnerName || "Unassigned"],
              ["Expected date", formatCrmDate(selectedProject.expectedInstallationDate)],
              ["Installation", `${selectedProject.installationStatusLabel || getProjectInstallationStatusLabel(selectedProject.installationStatus)} | Scheduled ${formatCrmDate(selectedProject.installationScheduledAt)}`],
              ["Installed on", formatCrmDate(selectedProject.installationCompletedAt)],
              [
                "Snags",
                `${selectedSnagSummary.openCount || selectedProject.openSnagCount || 0} open${
                  selectedSnagSummary.nextDueAt ? ` | next due ${formatCrmDate(selectedSnagSummary.nextDueAt)}` : ""
                }`,
              ],
              ["Review", selectedProject.reviewStatusLabel || getProjectReviewStatusLabel(selectedReviewSummary.status)],
              [
                "Closure",
                projectCloseGuard.canClose
                  ? "Ready to close"
                  : projectCloseGuard.reasons.join(" "),
              ],
              ["Next action", selectedProject.nextActionText || "Not set"],
              [
                "Order Tasks",
                crmSettings.enableOrderTaskTemplates === false
                  ? "Templates disabled"
                  : drawerData.projectId === selectedProject.projectId &&
                    Array.isArray(drawerData.tasks)
                  ? `${completedTemplateTaskCount} / ${orderTaskTemplate.length} completed${
                      overdueTemplateTaskCount ? ` | ${overdueTemplateTaskCount} overdue` : ""
                    }`
                  : "Open Tasks tab to load",
              ],
              [
                "Delivery Tracking",
                crmSettings.enableDeliveryTracking === false
                  ? "Delivery tracking disabled"
                  : deliveryTrackingSummary?.totalItemCount
                  ? `${deliveryTrackingSummary.totalItemCount} items | ${
                      deliveryTrackingSummary.receivedItemCount || 0
                    } received${
                      deliveryTrackingSummary.delayedItemCount
                        ? ` | ${deliveryTrackingSummary.delayedItemCount} delayed`
                        : ""
                    }${
                      deliveryTrackingSummary.nextCheckDueAt
                        ? ` | Next check ${formatCrmDate(deliveryTrackingSummary.nextCheckDueAt)}`
                        : ""
                    }`
                  : drawerData.projectId === selectedProject.projectId &&
                    Array.isArray(drawerData.trackingItems) &&
                    drawerData.trackingItems.length
                  ? `${materialSummary.total} total | ${materialSummary.received} received${
                      materialSummary.delayed ? ` | ${materialSummary.delayed} delayed` : ""
                    }`
                  : "Open Tracking tab to load",
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                <div style={{ fontSize: 13, color: textColor, fontWeight: 900, marginTop: 5 }}>{value || "Not set"}</div>
              </div>
            ))}
          </div>
        );
      };

      return (
        <section style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{selectedProject.projectTitle}</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {selectedProject.orderStageLabel || selectedProject.stageLabel} | {selectedProject.paymentStatusLabel}
              </div>
            </div>
            {selectedProject.quoteId ? (
              <button
                type="button"
                onClick={() => loadFullQuote(selectedProject.quoteId)}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Open quote
              </button>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => openCrmInstallationModal(selectedProject, "schedule")}
              disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
              title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
              style={{ ...btn(canUpdateSelectedProjectExecution ? "#2563eb" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
            >
              Schedule Installation
            </button>
            <button
              type="button"
              onClick={() => openCrmInstallationModal(selectedProject, "complete")}
              disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
              title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
              style={{ ...btn(canUpdateSelectedProjectExecution ? "#16a34a" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
            >
              Mark Installation Completed
            </button>
            <button
              type="button"
              onClick={() => openCrmSnagModal(selectedProject)}
              disabled={Boolean(crmProjectActionKey) || !canUpdateSelectedProjectExecution}
              title={!canUpdateSelectedProjectExecution ? "You do not have permission to do this." : undefined}
              style={{ ...btn(canUpdateSelectedProjectExecution ? "#f97316" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
            >
              Add Snag
            </button>
            <button
              type="button"
              onClick={() => closeCrmProject(selectedProjectForClose)}
              disabled={Boolean(crmProjectActionKey) || !canCloseCrmProjectAccess}
              title={
                !canCloseCrmProjectAccess
                  ? "You do not have permission to do this."
                  : projectCloseGuard.canClose
                  ? "Close project"
                  : projectCloseGuard.reasons.join(" ")
              }
              style={{
                ...btn(projectCloseGuard.canClose && canCloseCrmProjectAccess ? "#0f172a" : "#94a3b8"),
                marginRight: 0,
                padding: "8px 12px",
                opacity: projectCloseGuard.canClose && canCloseCrmProjectAccess ? 1 : 0.72,
              }}
            >
              Close Project
            </button>
            <button
              type="button"
              onClick={() => updateProjectReviewStatus(selectedProject, PROJECT_REVIEW_STATUS_PENDING)}
              disabled={Boolean(crmProjectActionKey) || !canRequestProjectReviewAccess}
              title={!canRequestProjectReviewAccess ? "You do not have permission to do this." : undefined}
              style={{ ...btn(canRequestProjectReviewAccess ? "#7c3aed" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
            >
              Mark Review Requested
            </button>
            {selectedReviewSummary.status === PROJECT_REVIEW_STATUS_PENDING ? (
              <>
                <button
                  type="button"
                  onClick={() => updateProjectReviewStatus(selectedProject, PROJECT_REVIEW_STATUS_COMPLETED)}
                  disabled={Boolean(crmProjectActionKey) || !canRequestProjectReviewAccess}
                  title={!canRequestProjectReviewAccess ? "You do not have permission to do this." : undefined}
                  style={{ ...btn(canRequestProjectReviewAccess ? "#16a34a" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
                >
                  Mark Review Received
                </button>
                <button
                  type="button"
                  onClick={() => updateProjectReviewStatus(selectedProject, PROJECT_REVIEW_STATUS_SKIPPED)}
                  disabled={Boolean(crmProjectActionKey) || !canRequestProjectReviewAccess}
                  title={!canRequestProjectReviewAccess ? "You do not have permission to do this." : undefined}
                  style={{ ...btn(canRequestProjectReviewAccess ? "#64748b" : "#94a3b8"), marginRight: 0, padding: "8px 12px" }}
                >
                  Skip Review
                </button>
              </>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {tabs.map((tabItem) => (
              <button
                key={tabItem.key}
                type="button"
                onClick={() => setCrmProjectDrawerTab(tabItem.key)}
                style={{
                  ...btn(crmProjectDrawerTab === tabItem.key ? "#2563eb" : "#0f172a"),
                  marginRight: 0,
                  padding: "8px 12px",
                }}
              >
                {tabItem.label}
              </button>
            ))}
          </div>
          {renderProjectTab()}
        </section>
      );
    };

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <section style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Orders</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Confirmed orders and handovers created from saved quotes or opportunities.
              </div>
            </div>
            <button
              type="button"
              onClick={() => loadCrmProjects(workspaceUid)}
              disabled={crmProjectsState.loading}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              {crmProjectsState.loading ? "Refreshing..." : "Refresh Orders"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setCrmProjectFilter(showClosedCancelledOrders ? "active" : "all")}
              style={{
                ...btn(showClosedCancelledOrders ? "#2563eb" : "#0f172a"),
                marginRight: 0,
                padding: "8px 12px",
              }}
            >
              {showClosedCancelledOrders ? "Hide closed/cancelled" : "Show closed/cancelled"}
            </button>
            {[
              ["Balance pending", attentionSummary.paymentPending],
              ["Installation scheduled", attentionSummary.installationScheduled],
              ["Open snags", attentionSummary.openSnags],
              ["Review pending", attentionSummary.reviewPending],
            ].map(([label, count]) => (
              <span
                key={label}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 12,
                  fontWeight: 900,
                  color: dashboardMuted,
                }}
              >
                {label}: {count}
              </span>
            ))}
          </div>
          {crmProjectsState.error ? (
            <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmProjectsState.error}
            </div>
          ) : null}
        </section>

        <div style={{ display: "grid", gap: 16, alignItems: "start" }}>
          <section style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Orders Pipeline</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                  {visibleCrmProjectRecords.length} order{visibleCrmProjectRecords.length === 1 ? "" : "s"} shown. Each order appears in one current stage.
                </div>
              </div>
              {orderPipelineLoadState.error ? (
                <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
                  {orderPipelineLoadState.error}
                </div>
              ) : null}
            </div>
            {crmProjectsState.loading && !visibleCrmProjectRecords.length ? (
              <div style={{ color: dashboardMuted, fontSize: 13 }}>Loading orders...</div>
            ) : null}
            {!crmProjectsState.loading && !visibleCrmProjectRecords.length ? (
              <div style={{ color: dashboardMuted, fontSize: 13 }}>No orders found for this view.</div>
            ) : null}
            <div style={{ overflowX: "auto", paddingBottom: 4 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.max(activeOrderStages.length, 1)}, minmax(280px, 1fr))`,
                  gap: 12,
                  minWidth: isCompact ? Math.max(activeOrderStages.length, 1) * 280 : "100%",
                }}
              >
                {activeOrderStages.map((stage) => {
                  const stageProjects = [...(projectsByOrderStage[stage.stageId] || [])].sort(
                    (left, right) =>
                      safeTimeMillis(right.lastActivityAt || right.updatedAt) -
                      safeTimeMillis(left.lastActivityAt || left.updatedAt)
                  );
                  return (
                    <div
                      key={stage.stageId}
                      style={{
                        borderRadius: 18,
                        border: dashboardBorder,
                        background: isDark ? "rgba(15,23,42,0.36)" : "#f8fafc",
                        minHeight: 220,
                        display: "grid",
                        gridTemplateRows: "auto 1fr",
                      }}
                    >
                      <div style={{ padding: 12, borderBottom: dashboardBorder, display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 999, background: stage.color || "#64748b", flex: "0 0 auto" }} />
                            <div style={{ fontWeight: 950, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {stage.label}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3, fontWeight: 800 }}>
                            {stageProjects.length} order{stageProjects.length === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: 10, display: "grid", gap: 10, alignContent: "start" }}>
                        {!stageProjects.length ? (
                          <div style={{ padding: 12, borderRadius: 14, border: dashboardBorder, color: dashboardMuted, fontSize: 12, lineHeight: 1.5 }}>
                            No orders in this stage.
                          </div>
                        ) : null}
                        {stageProjects.map((project) => {
                          const active = String(selectedCrmProjectId || "") === String(project.projectId || project.id);
                          const paymentSummary = project.paymentSummary || {};
                          const orderValue = Number(paymentSummary.orderValue ?? project.orderValue ?? 0) || 0;
                          const received = Number(paymentSummary.totalReceived ?? project.totalReceived ?? 0) || 0;
                          const balance = Number(paymentSummary.balanceAmount ?? project.balanceAmount ?? 0) || 0;
                          const openSnagCount = Number(project.openSnagCount ?? project.snagSummary?.openCount ?? 0) || 0;
                          const cardStage = resolveProjectPipelineStage(project);
                          const cardStageMeta = getOrderPipelineStage(orderPipeline, cardStage) || stage;
                          return (
                            <div
                              key={project.projectId || project.id}
                              style={{
                                padding: 12,
                                borderRadius: 16,
                                border: active ? "1px solid rgba(37,99,235,0.55)" : dashboardBorder,
                                borderTop: `4px solid ${cardStageMeta.color || stage.color || "#64748b"}`,
                                background: active
                                  ? isDark
                                    ? "rgba(37,99,235,0.18)"
                                    : "#eff6ff"
                                  : isDark
                                  ? "rgba(15,23,42,0.58)"
                                  : "#ffffff",
                                color: textColor,
                                display: "grid",
                                gap: 9,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedCrmProjectId(project.projectId || project.id);
                                  setCrmProjectDrawerTab("overview");
                                }}
                                style={{
                                  padding: 0,
                                  border: 0,
                                  background: "transparent",
                                  color: "inherit",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  display: "grid",
                                  gap: 5,
                                }}
                              >
                                <div style={{ fontWeight: 950, lineHeight: 1.3 }}>{project.projectTitle}</div>
                                <div style={{ fontSize: 12, color: dashboardMuted }}>
                                  {project.customerName} {project.quoteNo ? `| #${project.quoteNo}` : ""}
                                </div>
                                <div style={{ fontSize: 12, color: dashboardMuted }}>
                                  Owner: {project.projectOwnerName || "Unassigned"} | Last: {formatCrmDate(project.lastActivityAt || project.updatedAt)}
                                </div>
                              </button>

                              <select
                                value={cardStage}
                                onChange={(event) => updateProjectPipelineStage(project, event.target.value)}
                                disabled={Boolean(crmProjectActionKey) || !canUpdateCrmProjectExecution(project)}
                                style={{ ...input, fontSize: 12, padding: "7px 9px" }}
                                title={!canUpdateCrmProjectExecution(project) ? "You do not have permission to do this." : "Change order stage"}
                              >
                                {activeOrderStages.map((stageOption) => (
                                  <option key={stageOption.stageId} value={stageOption.stageId}>
                                    {stageOption.label}
                                  </option>
                                ))}
                              </select>

                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {canViewCrmPaymentsAccess ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(37,99,235,0.10)", color: "#2563eb", fontSize: 11, fontWeight: 900 }}>
                                    {formatCurrencyDisplay(orderValue, currency)}
                                  </span>
                                ) : null}
                                {balance > 0 ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(217,119,6,0.12)", color: "#d97706", fontSize: 11, fontWeight: 900 }}>
                                    Balance {formatCurrencyDisplay(balance, currency)}
                                  </span>
                                ) : canViewCrmPaymentsAccess && received > 0 ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(22,163,74,0.12)", color: "#16a34a", fontSize: 11, fontWeight: 900 }}>
                                    Paid
                                  </span>
                                ) : null}
                                {project.expectedInstallationDate || project.installationScheduledAt ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(20,184,166,0.12)", color: "#0f766e", fontSize: 11, fontWeight: 900 }}>
                                    Install {formatCrmDate(project.installationScheduledAt || project.expectedInstallationDate)}
                                  </span>
                                ) : null}
                                {openSnagCount > 0 ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(239,68,68,0.12)", color: "#dc2626", fontSize: 11, fontWeight: 900 }}>
                                    {openSnagCount} open snag{openSnagCount === 1 ? "" : "s"}
                                  </span>
                                ) : null}
                                {project.reviewSummary?.status === PROJECT_REVIEW_STATUS_PENDING ? (
                                  <span style={{ padding: "4px 7px", borderRadius: 999, background: "rgba(124,58,237,0.12)", color: "#7c3aed", fontSize: 11, fontWeight: 900 }}>
                                    Review pending
                                  </span>
                                ) : null}
                              </div>

                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {project.quoteId ? (
                                  <button type="button" onClick={() => loadFullQuote(project.quoteId)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "6px 8px", fontSize: 11 }}>
                                    Open quote
                                  </button>
                                ) : null}
                                <button type="button" onClick={() => openCustomerLedger(project.customerId)} style={{ ...btn("#334155"), marginRight: 0, padding: "6px 8px", fontSize: 11 }}>
                                  Ledger
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCrmProjectId(project.projectId || project.id);
                                    setCrmProjectDrawerTab("tasks");
                                  }}
                                  style={{ ...btn("#475569"), marginRight: 0, padding: "6px 8px", fontSize: 11 }}
                                >
                                  Tasks
                                </button>
                                {canViewCrmPaymentsAccess ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedCrmProjectId(project.projectId || project.id);
                                      setCrmProjectDrawerTab("payments");
                                    }}
                                    style={{ ...btn("#16a34a"), marginRight: 0, padding: "6px 8px", fontSize: 11 }}
                                  >
                                    Payments
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCrmProjectId(project.projectId || project.id);
                                    setCrmProjectDrawerTab("snags");
                                  }}
                                  style={{ ...btn("#f97316"), marginRight: 0, padding: "6px 8px", fontSize: 11 }}
                                >
                                  Snags
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          {renderProjectDrawer()}
        </div>
      </div>
    );
  };

  const renderCrmAttentionRequired = () => {
    const localNoOwner = crmScopeDeals
      .filter((deal) => !deal.isCrmClosed && !deal.isCrmCancelled)
      .filter((deal) => !String(deal.assignedToUserId || "").trim())
      .slice(0, 10);
    const localNoNextAction = crmScopeDeals
      .filter((deal) => !deal.isCrmClosed && !deal.isCrmCancelled)
      .filter((deal) => !String(deal.nextAction || "").trim())
      .slice(0, 10);
    const missingOwner = crmAttentionState.missingOwner.length
      ? crmAttentionState.missingOwner
      : localNoOwner;
    const missingNextAction = crmAttentionState.missingNextAction.length
      ? crmAttentionState.missingNextAction
      : localNoNextAction;
    const overdueTasks = crmAttentionState.overdueTasks;
    const missingQuoteFollowUp = crmAttentionState.missingQuoteFollowUp || [];
    const handoverPendingProjects = crmAttentionState.handoverPendingProjects || [];
    const advancePendingProjects = crmAttentionState.advancePendingProjects || [];
    const projectsMissingNextAction = crmAttentionState.projectsMissingNextAction || [];
    const projectsMissingInstallationDate =
      crmAttentionState.projectsMissingInstallationDate || [];
    const overdueTrackingTasks = crmAttentionState.overdueTrackingTasks || [];
    const trackingItemsNotOrdered = crmAttentionState.trackingItemsNotOrdered || [];
    const trackingItemsDelayed = crmAttentionState.trackingItemsDelayed || [];
    const trackingArrivalsOverdue = crmAttentionState.trackingArrivalsOverdue || [];
    const balancePendingProjects = crmAttentionState.balancePendingProjects || [];
    const overduePaymentTasks = crmAttentionState.overduePaymentTasks || [];
    const paymentDueTodayTasks = crmAttentionState.paymentDueTodayTasks || [];
    const installationNotScheduledProjects =
      crmAttentionState.installationNotScheduledProjects || [];
    const installationsDueToday = crmAttentionState.installationsDueToday || [];
    const overdueInstallations = crmAttentionState.overdueInstallations || [];
    const productionOverdueProjects = crmAttentionState.productionOverdueProjects || [];
    const materialDueOverdueProjects = crmAttentionState.materialDueOverdueProjects || [];
    const openSnagProjects = crmAttentionState.openSnagProjects || [];
    const overdueSnagProjects = crmAttentionState.overdueSnagProjects || [];
    const projectsReadyToClose = crmAttentionState.projectsReadyToClose || [];
    const projectsBlockedByFinalPayment =
      crmAttentionState.projectsBlockedByFinalPayment || [];
    const reviewDueTodayTasks = crmAttentionState.reviewDueTodayTasks || [];
    const overdueReviewTasks = crmAttentionState.overdueReviewTasks || [];

    const openOpportunity = (opportunity = {}) => {
      const leadId = opportunity.legacyLeadId || opportunity.id || opportunity.opportunityId || "";
      const matchingDeal =
        crmDeals.find((deal) => String(deal.id || "") === String(leadId)) ||
        crmLeadRecords.find((lead) => String(lead.id || "") === String(leadId)) ||
        null;
      if (matchingDeal) {
        setCrmView("list");
        openCrmDealDetails(matchingDeal, { crmView: "list" });
      }
    };

    const renderOpportunityItem = (item, reason) => {
      const title =
        item.customerLabel ||
        item.customerName ||
        item.crmContact?.name ||
        item.contact?.name ||
        buildOpportunityDisplayTitle(item);
      const stageLabel =
        item.crmStageLabel ||
        item.stageLabel ||
        getOpportunityStageLabel(item.stage || item.crmStageId || "");
      const dueLabel =
        item.nextActionDueAt || item.nextActionDate
          ? formatCrmDate(item.nextActionDueAt || item.nextActionDate)
          : "No date set";

      return (
        <div
          key={`${reason}:${item.id || item.opportunityId || item.legacyLeadId || title}`}
          style={{
            padding: 14,
            borderRadius: 16,
            border: dashboardBorder,
            background: dashboardSoftBg,
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
                {title || "Untitled opportunity"}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {stageLabel} {item.latestQuoteNo || item.quoteNo ? `| #${item.latestQuoteNo || item.quoteNo}` : ""}
              </div>
            </div>
            <span
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                background: reason === "owner" ? "rgba(245,158,11,0.14)" : "rgba(239,68,68,0.12)",
                color: reason === "owner" ? "#b45309" : "#dc2626",
                fontSize: 11,
                fontWeight: 900,
                whiteSpace: "nowrap",
              }}
            >
              {reason === "owner" ? "No owner assigned" : "No next action"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Next action: {item.nextActionText || item.nextAction || "No next action"} | {dueLabel}
          </div>
          <button
            type="button"
            onClick={() => openOpportunity(item)}
            style={{ ...btn("#0f172a"), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}
          >
            Open record
          </button>
        </div>
      );
    };

    const renderTaskItem = (task, reasonLabel = "Overdue", accent = "#dc2626") => (
      <div
        key={task.id || task.taskId}
        style={{
          padding: 14,
          borderRadius: 16,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 900 }}>{task.title || "CRM task"}</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {task.customerName || "Unnamed Customer"} {task.quoteNo ? `| #${task.quoteNo}` : ""}
            </div>
          </div>
          <span style={{ padding: "4px 8px", borderRadius: 999, background: `${accent}18`, color: accent, fontSize: 11, fontWeight: 900 }}>
            {reasonLabel}
          </span>
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          Due: {formatCrmDate(task.dueAt || task.dueDate)}
          {task.dueAmount != null ? ` | Amount: ${formatCurrencyDisplay(task.dueAmount || 0, currency)}` : ""}
        </div>
        <button
          type="button"
          onClick={() => markDailyTaskDone({
            ...task,
            id: `crm-task:${task.taskId || task.id}`,
            sourceId: task.taskId || task.id,
            sourceType: DAILY_TASK_SOURCE_CRM_TASK,
            crmTask: task,
          })}
          disabled={Boolean(markingDoneKey)}
          style={{ ...btn("#16a34a"), justifySelf: "start", padding: "8px 12px", marginRight: 0, opacity: markingDoneKey ? 0.7 : 1 }}
        >
          {markingDoneKey ? "Saving..." : "Mark as Done"}
        </button>
      </div>
    );

    const renderQuoteFollowUpMissingItem = (quote) => (
      <div
        key={`quote-follow-up:${quote.quoteIndexId || quote.quoteId || quote.quoteNo}`}
        style={{
          padding: 14,
          borderRadius: 16,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
              {quote.customerName || "Unnamed Customer"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {quote.quoteNo ? `#${quote.quoteNo}` : "Quotation"}{" "}
              {quote.siteName ? `| ${quote.siteName}` : ""}
            </div>
          </div>
          <span
            style={{
              padding: "4px 8px",
              borderRadius: 999,
              background: "rgba(37,99,235,0.12)",
              color: "#2563eb",
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            Quote needs follow-up
          </span>
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          Marked as sent, but no follow-up date is set yet.
        </div>
        <button
          type="button"
          onClick={() => loadFullQuote(quote.quoteId || quote.quoteIndexId)}
          style={{ ...btn("#0f172a"), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}
        >
          Open quote
        </button>
      </div>
    );

    const openProjectFromAttention = (project) => {
      setSelectedCrmProjectId(project.projectId || project.id || "");
      setCrmProjectDrawerTab("overview");
      setCrmView("projects");
    };

    const openMaterialProjectFromAttention = (material) => {
      setSelectedCrmProjectId(material.projectId || "");
      setCrmProjectDrawerTab("tracking");
      setCrmView("projects");
    };

    const renderProjectItem = (project, reasonLabel) => (
      <div
        key={`${reasonLabel}:${project.projectId || project.id}`}
        style={{
          padding: 14,
          borderRadius: 16,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
              {project.projectTitle || project.customerName || "Untitled order"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {project.quoteNo ? `#${project.quoteNo}` : "Order"}{" "}
              {project.siteName ? `| ${project.siteName}` : ""}
            </div>
          </div>
          <span
            style={{
              padding: "4px 8px",
              borderRadius: 999,
              background: "rgba(37,99,235,0.12)",
              color: "#2563eb",
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {reasonLabel}
          </span>
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          {project.customerName || "Unnamed Customer"} | {project.stageLabel || getProjectStageLabel(project.stage)} | {project.paymentStatusLabel || getPaymentStatusLabel(project.paymentStatus)}
          {Number(project.paymentSummary?.balanceAmount ?? project.balanceAmount ?? 0) > 0
            ? ` | Balance ${formatCurrencyDisplay(project.paymentSummary?.balanceAmount ?? project.balanceAmount ?? 0, currency)}`
            : ""}
        </div>
        <button
          type="button"
          onClick={() => openProjectFromAttention(project)}
          style={{ ...btn("#0f172a"), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}
        >
          View Project
        </button>
      </div>
    );

    const renderMaterialItem = (material, reasonLabel) => (
      <div
        key={`${reasonLabel}:${material.materialId || material.id}`}
        style={{
          padding: 14,
          borderRadius: 16,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
              {material.displayTitle || "Tracking item"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {material.customerName || "Order material"} {material.quoteNo ? `| #${material.quoteNo}` : ""}
            </div>
          </div>
          <span
            style={{
              padding: "4px 8px",
              borderRadius: 999,
              background: reasonLabel === "Material delayed" || reasonLabel === "Expected arrival overdue" ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.14)",
              color: reasonLabel === "Material delayed" || reasonLabel === "Expected arrival overdue" ? "#dc2626" : "#b45309",
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {reasonLabel}
          </span>
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          Expected: {formatCrmDate(material.expectedArrivalDate)} | Supplier: {material.supplierName || "Not set"} | Status: {material.statusLabel}
        </div>
        <button
          type="button"
          onClick={() => openMaterialProjectFromAttention(material)}
          style={{ ...btn("#0f172a"), justifySelf: "start", padding: "8px 12px", marginRight: 0 }}
        >
          Open Project
        </button>
      </div>
    );

    const buckets = [
      {
        key: "missingOwner",
        title: "No owner assigned",
        helper: "Assign these records so someone clearly owns the follow-up.",
        empty: "Every open record has someone assigned.",
        count: missingOwner.length,
        accent: "#f59e0b",
        content: missingOwner.map((item) => renderOpportunityItem(item, "owner")),
      },
      {
        key: "missingNextAction",
        title: "No next action",
        helper: "Add a clear next step so promising work does not go quiet.",
        empty: "Every open record has a next action.",
        count: missingNextAction.length,
        accent: "#ef4444",
        content: missingNextAction.map((item) => renderOpportunityItem(item, "nextAction")),
      },
      {
        key: "overdueTasks",
        title: "Overdue tasks",
        helper: "Finish or reschedule tasks that are already past their due date.",
        empty: "No overdue tasks right now.",
        count: overdueTasks.length,
        accent: "#dc2626",
        content: overdueTasks.map((task) => renderTaskItem(task, "Overdue", "#dc2626")),
      },
      {
        key: "missingQuoteFollowUp",
        title: "Quotation follow-up missing",
        helper: "Review sent quotations that still need a planned follow-up.",
        empty: "All sent quotations have follow-up dates.",
        count: missingQuoteFollowUp.length,
        accent: "#2563eb",
        content: missingQuoteFollowUp.map(renderQuoteFollowUpMissingItem),
      },
      {
        key: "handoverPendingProjects",
        title: "Handover pending",
        helper: "Confirmed orders that still need a clean handover.",
        empty: "No orders are waiting for handover.",
        count: handoverPendingProjects.length,
        accent: "#7c3aed",
        content: handoverPendingProjects.map((item) =>
          renderProjectItem(item, "Handover pending")
        ),
      },
      {
        key: "advancePendingProjects",
        title: "Advance pending",
        helper: "Orders that are created but still need an advance update.",
        empty: "No orders are waiting for advance.",
        count: advancePendingProjects.length,
        accent: "#d97706",
        content: advancePendingProjects.map((item) =>
          renderProjectItem(item, "Advance pending")
        ),
      },
      {
        key: "projectsMissingNextAction",
        title: "Projects without next action",
        helper: "Orders that need a clear next step for the team.",
        empty: "Every order has a next action.",
        count: projectsMissingNextAction.length,
        accent: "#f97316",
        content: projectsMissingNextAction.map((item) =>
          renderProjectItem(item, "No next action")
        ),
      },
      {
        key: "projectsMissingInstallationDate",
        title: "Installation date missing",
        helper: "Orders that need an expected installation or delivery date.",
        empty: "Every order has an expected date.",
        count: projectsMissingInstallationDate.length,
        accent: "#0ea5e9",
        content: projectsMissingInstallationDate.map((item) =>
          renderProjectItem(item, "Date missing")
        ),
      },
      {
        key: "installationNotScheduledProjects",
        title: "Installation not scheduled",
        helper: "Open projects that still need someone to schedule installation.",
        empty: "Every active project has an installation plan.",
        count: installationNotScheduledProjects.length,
        accent: "#0ea5e9",
        content: installationNotScheduledProjects.map((item) =>
          renderProjectItem(item, "Installation not scheduled")
        ),
      },
      {
        key: "installationsDueToday",
        title: "Installation due today",
        helper: "Projects scheduled for installation today.",
        empty: "No installations are due today.",
        count: installationsDueToday.length,
        accent: "#0ea5e9",
        content: installationsDueToday.map((item) =>
          renderProjectItem(item, "Installation today")
        ),
      },
      {
        key: "overdueInstallations",
        title: "Installation overdue",
        helper: "Scheduled installations that are past their date.",
        empty: "No installations are overdue.",
        count: overdueInstallations.length,
        accent: "#dc2626",
        content: overdueInstallations.map((item) =>
          renderProjectItem(item, "Installation overdue")
        ),
      },
      {
        key: "productionOverdueProjects",
        title: "Production overdue",
        helper: "Projects whose production due date has passed.",
        empty: "No production deadlines are overdue.",
        count: productionOverdueProjects.length,
        accent: "#dc2626",
        content: productionOverdueProjects.map((item) =>
          renderProjectItem(item, "Production overdue")
        ),
      },
      {
        key: "materialDueOverdueProjects",
        title: "Material deadline overdue",
        helper: "Projects where material needed for production is past the due date.",
        empty: "No project material deadlines are overdue.",
        count: materialDueOverdueProjects.length,
        accent: "#d97706",
        content: materialDueOverdueProjects.map((item) =>
          renderProjectItem(item, "Material deadline overdue")
        ),
      },
      {
        key: "openSnagProjects",
        title: "Open snags",
        helper: "Projects with corrections still pending.",
        empty: "No projects have open snags.",
        count: openSnagProjects.length,
        accent: "#f97316",
        content: openSnagProjects.map((item) =>
          renderProjectItem(item, "Open snags")
        ),
      },
      {
        key: "overdueSnagProjects",
        title: "Snags overdue",
        helper: "Corrections whose due date has passed.",
        empty: "No overdue snags right now.",
        count: overdueSnagProjects.length,
        accent: "#dc2626",
        content: overdueSnagProjects.map((item) =>
          renderProjectItem(item, "Snag overdue")
        ),
      },
      {
        key: "projectsReadyToClose",
        title: "Ready to close",
        helper: "Installed projects with no payment or snag blockers.",
        empty: "No projects are ready to close.",
        count: projectsReadyToClose.length,
        accent: "#16a34a",
        content: projectsReadyToClose.map((item) =>
          renderProjectItem(item, "Ready to close")
        ),
      },
      ...(crmSettings.enableDeliveryTracking === false
        ? []
        : [
            {
              key: "overdueTrackingTasks",
              title: "Tracking update overdue",
              helper: "Daily delivery tracking reminders that need completion.",
              empty: "No overdue tracking reminders right now.",
              count: overdueTrackingTasks.length,
              accent: "#dc2626",
              content: overdueTrackingTasks.map(renderTaskItem),
            },
            {
              key: "trackingItemsNotOrdered",
              title: "Not ordered",
              helper: "Materials or order items that were added but not yet ordered.",
              empty: "No not-yet-ordered tracking items need attention.",
              count: trackingItemsNotOrdered.length,
              accent: "#d97706",
              content: trackingItemsNotOrdered.map((item) =>
                renderMaterialItem(item, "Not ordered")
              ),
            },
            {
              key: "trackingItemsDelayed",
              title: "Materials delayed",
              helper: "Review supplier or courier delays before production is blocked.",
              empty: "No material delays right now.",
              count: trackingItemsDelayed.length,
              accent: "#dc2626",
              content: trackingItemsDelayed.map((item) =>
                renderMaterialItem(item, "Material delayed")
              ),
            },
            {
              key: "trackingArrivalsOverdue",
              title: "Expected arrival overdue",
              helper: "Follow up materials that have not arrived by the expected date.",
              empty: "No overdue materials right now.",
              count: trackingArrivalsOverdue.length,
              accent: "#ef4444",
              content: trackingArrivalsOverdue.map((item) =>
                renderMaterialItem(item, "Expected arrival overdue")
              ),
            },
          ]),
      {
        key: "projectsBlockedByFinalPayment",
        title: "Closure blocked by payment",
        helper: "Projects waiting for final payment before closure.",
        empty: "No closures are blocked by final payment.",
        count: projectsBlockedByFinalPayment.length,
        accent: "#d97706",
        content: projectsBlockedByFinalPayment.map((item) =>
          renderProjectItem(item, "Final payment pending")
        ),
      },
      {
        key: "reviewDueTodayTasks",
        title: "Review due today",
        helper: "Review/referral requests scheduled for today.",
        empty: "No review requests are due today.",
        count: reviewDueTodayTasks.length,
        accent: "#2563eb",
        content: reviewDueTodayTasks.map((task) =>
          renderTaskItem(task, "Review due today", "#2563eb")
        ),
      },
      {
        key: "overdueReviewTasks",
        title: "Review overdue",
        helper: "Review/referral requests that need follow-up.",
        empty: "No review requests are overdue.",
        count: overdueReviewTasks.length,
        accent: "#dc2626",
        content: overdueReviewTasks.map((task) =>
          renderTaskItem(task, "Review overdue", "#dc2626")
        ),
      },
      ...(crmSettings.enablePaymentTracking === false
        ? []
        : [
            {
              key: "balancePendingProjects",
              title: "Balance pending",
              helper: "Orders with payment still to collect.",
              empty: "No order balances need attention.",
              count: balancePendingProjects.length,
              accent: "#d97706",
              content: balancePendingProjects.map((item) =>
                renderProjectItem(item, "Balance pending")
              ),
            },
            {
              key: "overduePaymentTasks",
              title: "Payment follow-up overdue",
              helper: "Payment follow-ups that are already past their due date.",
              empty: "No payment follow-ups are overdue.",
              count: overduePaymentTasks.length,
              accent: "#dc2626",
              content: overduePaymentTasks.map((task) =>
                renderTaskItem(task, "Payment overdue", "#dc2626")
              ),
            },
            {
              key: "paymentDueTodayTasks",
              title: "Payment due today",
              helper: "Payment follow-ups scheduled for today.",
              empty: "No payment follow-ups are due today.",
              count: paymentDueTodayTasks.length,
              accent: "#2563eb",
              content: paymentDueTodayTasks.map((task) =>
                renderTaskItem(task, "Due today", "#2563eb")
              ),
            },
          ]),
    ];
    const activeBucket =
      buckets.find((bucket) => bucket.key === crmAttentionActiveBucket) || buckets[0];

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "flex", justifyContent: "space-between", gap: 14, alignItems: isCompact ? "stretch" : "center", flexDirection: isCompact ? "column" : "row" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Attention Required</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
              A focused view for records that need assignment, a next action, or overdue task follow-up.
              Open a card, read the reason, then use the action button to update the customer/order.
            </div>
          </div>
          <button
            type="button"
            onClick={() => loadCrmAttentionRequired(workspaceUid)}
            disabled={crmAttentionState.loading}
            style={{ ...btn("#2563eb"), marginRight: 0 }}
          >
            {crmAttentionState.loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {crmAttentionState.error ? (
          <div style={{ ...dashboardCardStyle, border: "1px solid rgba(239,68,68,0.22)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
            {crmAttentionState.error}
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 14, alignItems: "stretch" }}>
          {buckets.map((bucket) => (
            <section
              key={bucket.key}
              style={{
                ...dashboardCardStyle,
                display: "grid",
                gap: 14,
                border:
                  activeBucket.key === bucket.key
                    ? `1px solid ${bucket.accent}55`
                    : dashboardBorder,
                boxShadow:
                  activeBucket.key === bucket.key
                    ? "0 18px 40px rgba(15,23,42,0.10)"
                    : dashboardCardStyle.boxShadow,
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 17, fontWeight: 900, color: bucket.accent }}>{bucket.title}</div>
                  <div style={{ padding: "6px 10px", borderRadius: 999, background: `${bucket.accent}16`, color: bucket.accent, fontSize: 12, fontWeight: 900 }}>{bucket.count}</div>
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.55 }}>{bucket.helper}</div>
              </div>
              <button
                type="button"
                onClick={() => setCrmAttentionActiveBucket(bucket.key)}
                style={{
                  ...btn(activeBucket.key === bucket.key ? bucket.accent : "#0f172a"),
                  justifySelf: "start",
                  padding: "8px 14px",
                  marginRight: 0,
                }}
              >
                View
              </button>
            </section>
          ))}
        </div>

        <section style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: isCompact ? "flex-start" : "center", flexDirection: isCompact ? "column" : "row" }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900, color: activeBucket.accent }}>
                {activeBucket.title}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {activeBucket.count
                  ? `${activeBucket.count} ${activeBucket.count === 1 ? "item" : "items"} need attention.`
                  : activeBucket.empty}
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {activeBucket.content.length ? activeBucket.content : (
              <div style={{ padding: 16, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg, fontSize: 13, color: dashboardMuted }}>
                {activeBucket.empty}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  };

  const renderCrmPipelineReview = () => {
    const summary = crmPipelineReviewState.summary || {};
    const warnings = crmPipelineReviewState.managementWarnings || {};
    const dateRange = crmPipelineReviewState.dateRange || getCrmPipelineReviewRange("this_week");
    const reviewErrorClass = crmPipelineReviewState.errorClassification || "";
    const isIndexReviewError =
      reviewErrorClass === "index_building" || reviewErrorClass === "index_required";
    const hasReviewActivity =
      Number(summary.newLeads || 0) +
        Number(summary.contactedLeads || 0) +
        Number(summary.qualifiedLeads || 0) +
        Number(summary.siteVisitsScheduled || 0) +
        Number(summary.measurementsCompleted || 0) +
        Number(summary.quotationsSent || 0) +
        Number(summary.ordersWon || 0) +
        Number(summary.advancePendingOrders || 0) +
        Number(summary.openLeads || 0) +
        Number(summary.openOpportunityValue || 0) +
        Number(summary.quoteValue || 0) +
        Number(summary.quotationSentValue || 0) +
        Number(summary.negotiationValue || 0) +
        Number(summary.paymentPendingBalance || 0) +
        Number(summary.activeProjects || 0) +
        Number(summary.activeProjectValue || 0) +
        Number(summary.projectsReadyToClose || 0) +
        Number(summary.lostOpportunities || 0) +
        Number(summary.overdueTasks || 0) +
        Number(summary.leadsWithoutNextAction || 0) +
        Number((crmAttentionState.paymentDueTodayTasks || []).length) +
        Number((crmAttentionState.overduePaymentTasks || []).length) +
        Number((crmAttentionState.projectsReadyToClose || []).length) +
        Number((crmPipelineReviewState.targets || []).length) >
      0;
    const ownerOptions = getCrmPipelineReviewOwnerOptions();
    const selectedTargetOwner =
      ownerOptions.find((option) => option.uid === crmWeeklyTargetDraft.userId) || ownerOptions[0] || {};
    const updateFilter = (patch) => {
      setCrmPipelineFilters((prev) => {
        const next = { ...prev, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, "dateRangePreset")) {
          if (patch.dateRangePreset !== "custom") {
            const range = getCrmPipelineReviewRange(patch.dateRangePreset);
            next.startDate = range.startDate;
            next.endDate = range.endDate;
          }
        }
        return next;
      });
    };
    const metricCards = [
      { key: "newLeads", label: "New leads", value: summary.newLeads || 0, accent: "#2563eb" },
      { key: "contactedLeads", label: "Contacted leads", value: summary.contactedLeads || 0, accent: "#0ea5e9" },
      { key: "qualifiedLeads", label: "Qualified leads", value: summary.qualifiedLeads || 0, accent: "#6366f1" },
      { key: "siteVisitsScheduled", label: "Site visits scheduled", value: summary.siteVisitsScheduled || 0, accent: "#14b8a6" },
      { key: "measurementsCompleted", label: "Measurements completed", value: summary.measurementsCompleted || 0, accent: "#10b981" },
      { key: "quotationsSent", label: "Quotations sent", value: summary.quotationsSent || 0, accent: "#f59e0b" },
      { key: "quoteValue", label: "Quote value", value: formatCurrencyDisplay(summary.quoteValue || 0, currency), accent: "#d97706" },
      { key: "ordersWon", label: "Orders won", value: summary.ordersWon || 0, accent: "#16a34a" },
      { key: "orderValue", label: "Order value", value: formatCurrencyDisplay(summary.orderValue || 0, currency), accent: "#15803d" },
      { key: "advancePendingOrders", label: "Advance Pending", value: summary.advancePendingOrders || 0, accent: "#f59e0b" },
      { key: "advancePendingValue", label: "Advance Pending Value", value: formatCurrencyDisplay(summary.advancePendingValue || 0, currency), accent: "#d97706" },
      { key: "paymentPendingBalance", label: "Payment Pending", value: formatCurrencyDisplay(summary.paymentPendingBalance || 0, currency), accent: "#b45309" },
      { key: "projectsReadyToClose", label: "Projects Ready To Close", value: (crmAttentionState.projectsReadyToClose || []).length || summary.projectsReadyToClose || 0, accent: "#16a34a" },
      { key: "lostOpportunities", label: "Lost opportunities", value: summary.lostOpportunities || 0, accent: "#ef4444" },
      { key: "overdueTasks", label: "Overdue tasks", value: summary.overdueTasks || 0, accent: "#dc2626" },
      { key: "leadsWithoutNextAction", label: "Leads without next action", value: summary.leadsWithoutNextAction || 0, accent: "#f97316" },
    ];
    const targetDisplay = (actual, target, isMoney = false) =>
      `${isMoney ? formatCurrencyDisplay(actual || 0, currency) : Number(actual || 0)} / ${
        isMoney ? formatCurrencyDisplay(target || 0, currency) : Number(target || 0)
      }`;

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <section style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Pipeline Review</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Review weekly sales activity, targets, follow-ups, and orders for manager meetings.
              </div>
            </div>
            <button
              type="button"
              onClick={() => loadCrmPipelineReview(workspaceUid, crmPipelineFilters)}
              disabled={crmPipelineReviewState.loading}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              {crmPipelineReviewState.loading ? "Refreshing..." : "Refresh Review"}
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Date range
              <select
                value={crmPipelineFilters.dateRangePreset}
                onChange={(event) => updateFilter({ dateRangePreset: event.target.value })}
                style={input}
              >
                {CRM_PIPELINE_REVIEW_RANGE_PRESETS.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              From
              <input
                type="date"
                value={crmPipelineFilters.startDate}
                disabled={crmPipelineFilters.dateRangePreset !== "custom"}
                onChange={(event) => updateFilter({ startDate: event.target.value, dateRangePreset: "custom" })}
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              To
              <input
                type="date"
                value={crmPipelineFilters.endDate}
                disabled={crmPipelineFilters.dateRangePreset !== "custom"}
                onChange={(event) => updateFilter({ endDate: event.target.value, dateRangePreset: "custom" })}
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Salesperson
              <select
                value={crmPipelineFilters.salesperson}
                onChange={(event) => updateFilter({ salesperson: event.target.value })}
                style={input}
              >
                <option value="all">All salespeople</option>
                {ownerOptions.map((option) => (
                  <option key={option.uid} value={option.uid}>{option.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Stage
              <select
                value={crmPipelineFilters.stage}
                onChange={(event) => updateFilter({ stage: event.target.value })}
                style={input}
              >
                <option value="all">All stages</option>
                {CRM_OPPORTUNITY_STAGES.map((stage) => (
                  <option key={stage.key} value={stage.key}>{stage.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Source
              <input
                value={crmPipelineFilters.source === "all" ? "" : crmPipelineFilters.source}
                onChange={(event) => updateFilter({ source: event.target.value.trim() || "all" })}
                style={input}
                placeholder="Any source"
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Product interest
              <input
                value={crmPipelineFilters.productInterest === "all" ? "" : crmPipelineFilters.productInterest}
                onChange={(event) => updateFilter({ productInterest: event.target.value.trim() || "all" })}
                style={input}
                placeholder="Any product"
              />
            </label>
            <div style={{ display: "grid", gap: 6, alignSelf: "end" }}>
              <button
                type="button"
                onClick={() => {
                  const range = getCrmPipelineReviewRange("this_week");
                  setCrmPipelineFilters({
                    ...CRM_PIPELINE_REVIEW_DEFAULT_FILTERS,
                    startDate: range.startDate,
                    endDate: range.endDate,
                  });
                }}
                style={{ ...btn("#64748b"), marginRight: 0 }}
              >
                Reset filters
              </button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Showing {formatPipelineDateLabel(dateRange.startDate)} to {formatPipelineDateLabel(dateRange.endDate)}
            {crmPipelineReviewState.loadedAt ? ` | Updated ${new Date(crmPipelineReviewState.loadedAt).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}` : ""}
          </div>
        </section>

        {crmSettings.enablePipelineReview === false ? (
          <section style={{ ...dashboardCardStyle, border: "1px solid rgba(245,158,11,0.28)", color: "#92400e", fontSize: 13, fontWeight: 800 }}>
            Pipeline Review is currently disabled in CRM Settings.
          </section>
        ) : null}

        {crmPipelineReviewState.error ? (
          <section
            style={{
              ...dashboardCardStyle,
              border: isIndexReviewError
                ? "1px solid rgba(245,158,11,0.30)"
                : "1px solid rgba(239,68,68,0.24)",
              color: isIndexReviewError ? "#92400e" : "#dc2626",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 900 }}>
                {isIndexReviewError ? "CRM reporting index is preparing" : crmPipelineReviewState.error}
              </div>
              <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>
                {crmPipelineReviewState.error}
              </div>
            </div>
            {isIndexReviewError ? (
              <button
                type="button"
                onClick={() => loadCrmPipelineReview(workspaceUid, crmPipelineFilters)}
                disabled={crmPipelineReviewState.loading}
                style={{ ...btn("#f59e0b"), marginRight: 0 }}
              >
                Retry
              </button>
            ) : null}
          </section>
        ) : null}

        {crmPipelineReviewState.loaded && !crmPipelineReviewState.error && !hasReviewActivity ? (
          <section style={{ ...dashboardCardStyle, textAlign: "center", display: "grid", gap: 6 }}>
            <div style={{ fontSize: 16, fontWeight: 900 }}>No CRM activity found for this period.</div>
            <div style={{ fontSize: 13, color: dashboardMuted }}>
              Try another date range or create leads, tasks, or quote follow-ups.
            </div>
          </section>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
          {metricCards.map((card) => (
            <section key={card.key} style={{ ...dashboardCardStyle, display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>{card.label}</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: card.accent }}>{card.value}</div>
            </section>
          ))}
        </div>

        <section style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900 }}>Management Warnings</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              Quick signals for records that need attention this review cycle.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            {[
              { label: "Leads without owner", value: warnings.missingOwnerCount || 0 },
              { label: "Leads without next action", value: warnings.missingNextActionCount || 0 },
              { label: "Overdue follow-ups", value: warnings.overdueTaskCount || 0 },
              { label: "Quote follow-up missing", value: warnings.missingQuoteFollowUpCount || 0 },
            ].map((warning) => (
              <div key={warning.label} style={{ padding: 14, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg }}>
                <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 800 }}>{warning.label}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: warning.value ? "#dc2626" : "#16a34a", marginTop: 6 }}>{warning.value}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...dashboardCardStyle, overflowX: "auto", display: "grid", gap: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900 }}>Salesperson Scorecard</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              Activity and results for the selected period.
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead>
              <tr>
                {["Salesperson", "Leads", "Follow-ups done", "Site visits", "Quotes sent", "Orders won", "Order value", "Overdue tasks", "Leads without next action"].map((heading) => (
                  <th key={heading} style={{ textAlign: "left", padding: "10px 8px", fontSize: 11, color: dashboardMuted, borderBottom: dashboardBorder, textTransform: "uppercase", letterSpacing: "0.04em" }}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(crmPipelineReviewState.salespersonRows || []).length ? (
                crmPipelineReviewState.salespersonRows.map((row) => (
                  <tr key={row.userId}>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, fontWeight: 900 }}>{row.userName}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.leads}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.followUpsDone}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.siteVisits}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.quotesSent}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{row.ordersWon}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{formatCurrencyDisplay(row.orderValue || 0, currency)}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, color: row.overdueTasks ? "#dc2626" : textColor }}>{row.overdueTasks}</td>
                    <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, color: row.leadsWithoutNextAction ? "#f97316" : textColor }}>{row.leadsWithoutNextAction}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ padding: 18, color: dashboardMuted, textAlign: "center" }}>
                    No CRM activity found for this review period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center" }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900 }}>Weekly Targets</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Target vs actual for the selected week.
              </div>
            </div>
            {!canEditCrmTargetsAccess ? (
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                You can view targets, but editing is restricted.
              </div>
            ) : null}
          </div>

          {canEditCrmTargetsAccess ? (
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12, padding: 14, borderRadius: 18, border: dashboardBorder, background: dashboardSoftBg }}>
              <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                Week starts
                <input
                  type="date"
                  value={crmWeeklyTargetDraft.weekStart}
                  onChange={(event) => {
                    const weekStart = event.target.value;
                    setCrmWeeklyTargetDraft((prev) => ({
                      ...prev,
                      weekStart,
                      weekEnd: addDaysToDateKey(weekStart, 6),
                    }));
                  }}
                  style={input}
                />
              </label>
              <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                Salesperson
                <select
                  value={crmWeeklyTargetDraft.userId}
                  onChange={(event) => {
                    const owner = ownerOptions.find((option) => option.uid === event.target.value) || {};
                    const target = (crmPipelineReviewState.targets || []).find((item) => item.userId === event.target.value && item.weekStart === crmWeeklyTargetDraft.weekStart);
                    setCrmWeeklyTargetDraft({
                      ...CRM_WEEKLY_TARGET_DEFAULTS,
                      ...(target || {}),
                      weekStart: crmWeeklyTargetDraft.weekStart || dateRange.weekStart,
                      weekEnd: crmWeeklyTargetDraft.weekEnd || dateRange.weekEnd,
                      userId: event.target.value,
                      userName: owner.name || target?.userName || "",
                    });
                  }}
                  style={input}
                >
                  <option value="">Choose salesperson</option>
                  {ownerOptions.map((option) => (
                    <option key={option.uid} value={option.uid}>{option.name}</option>
                  ))}
                </select>
              </label>
              {[
                ["targetFreshLeads", "Fresh leads"],
                ["targetQualifiedLeads", "Qualified leads"],
                ["targetSiteVisits", "Site visits"],
                ["targetQuotations", "Quotations"],
                ["targetFollowUps", "Follow-ups"],
                ["targetOrders", "Orders"],
                ["targetOrderValue", "Order value"],
              ].map(([key, label]) => (
                <label key={key} style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                  {label}
                  <input
                    type="number"
                    min="0"
                    value={crmWeeklyTargetDraft[key] ?? 0}
                    onChange={(event) => setCrmWeeklyTargetDraft((prev) => ({ ...prev, [key]: event.target.value }))}
                    style={input}
                  />
                </label>
              ))}
              <div style={{ display: "grid", gap: 6, alignSelf: "end" }}>
                <button
                  type="button"
                  onClick={saveCrmWeeklyTarget}
                  disabled={isSavingCrmWeeklyTarget || !selectedTargetOwner.uid}
                  style={{ ...btn("#16a34a"), marginRight: 0 }}
                >
                  {isSavingCrmWeeklyTarget ? "Saving..." : "Save Target"}
                </button>
              </div>
            </div>
          ) : null}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
              <thead>
                <tr>
                  {["Salesperson", "Fresh Leads", "Qualified Leads", "Site Visits", "Quotations", "Follow-ups", "Orders", "Order Value"].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", padding: "10px 8px", fontSize: 11, color: dashboardMuted, borderBottom: dashboardBorder, textTransform: "uppercase", letterSpacing: "0.04em" }}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(crmPipelineReviewState.salespersonRows || []).length ? (
                  crmPipelineReviewState.salespersonRows.map((row) => {
                    const target = row.target || {};
                    return (
                      <tr key={`target-${row.userId}`}>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder, fontWeight: 900 }}>{row.userName}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.leads, target.targetFreshLeads)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.targetQualifiedLeadsActual || 0, target.targetQualifiedLeads)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.siteVisits, target.targetSiteVisits)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.quotesSent, target.targetQuotations)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.followUpsDone, target.targetFollowUps)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.ordersWon, target.targetOrders)}</td>
                        <td style={{ padding: "12px 8px", borderBottom: dashboardBorder }}>{targetDisplay(row.orderValue, target.targetOrderValue, true)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} style={{ padding: 18, color: dashboardMuted, textAlign: "center" }}>
                      No targets or activity found for this week.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  };

  const renderCrmDataRepairPanel = () => {
    if (!canUseCrmDataRepairTools) return null;
    const selectedActivityCount = crmDataRepairState.activityResults.filter((row) => crmDataRepairState.activitySelected[row.key] && canRepairActivityDateRow(row)).length;
    const selectedProjectStageCount = Object.values(crmDataRepairState.projectStageSelected || {}).filter(Boolean).length;
    const currentRepairRoleLabel = isSuperAdmin
      ? "Superadmin"
      : currentCrmRole || "Workspace user";

    return (
      <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>Data Repair</div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
            Manual, workspace-scoped tools for old CRM records. Dry runs read bounded pages only and do not change quotation items or totals.
          </div>
        </div>

        {crmDataRepairState.activityConfirmation ? (
          <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(15,23,42,0.6)", display: "grid", placeItems: "center", padding: 20 }}>
            <section role="alertdialog" aria-modal="true" aria-labelledby="activity-repair-confirm-title" aria-describedby="activity-repair-confirm-warning" style={{ ...dashboardCardStyle, width: "min(600px, 100%)", maxHeight: "85vh", overflowY: "auto", padding: 24 }}>
              <h3 id="activity-repair-confirm-title">Confirm activity date repair</h3>
              <p id="activity-repair-confirm-warning">{ACTIVITY_SORT_WARNING}</p>
              {crmDataRepairState.activityResults.filter((row) => crmDataRepairState.activitySelected[row.key]).some((row) => row.repair_test_data !== true) ? <p style={{ color: "#b45309", fontWeight: 800 }}>You are repairing real business data. Review carefully. This action will be logged.</p> : <p>TEST DATA</p>}
              <p>Only the selected activity dates and repair metadata will change. Quote items, totals, created and updated dates will stay unchanged.</p>
              <div style={{ display: "grid", gap: 8 }}>
                {crmDataRepairState.activityResults.filter((row) => crmDataRepairState.activitySelected[row.key]).map((row) => <div key={row.key} style={{ fontSize: 12 }}><RepairDataBadge record={row} /> {row.quoteNo || row.recordId}<br />Current: {formatSafeDate(row.currentLastActivityAt, "Missing")} | Proposed: {row.proposedLastActivityAtMs != null ? formatCrmDate(new Date(row.proposedLastActivityAtMs)) : "None"} | Source: {row.source}</div>)}
              </div>
              <label style={{ display: "block", margin: "18px 0" }}><input type="checkbox" checked={crmDataRepairState.activityConfirmation.reviewed} disabled={crmDataRepairState.repairingActivity} onChange={(event) => setCrmDataRepairState((prev) => ({ ...prev, activityConfirmation: { ...prev.activityConfirmation, reviewed: event.target.checked } }))} /> I reviewed the current date, proposed date, and sorting impact.</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button autoFocus type="button" style={btn("#64748b")} disabled={crmDataRepairState.repairingActivity} onClick={() => setCrmDataRepairState((prev) => ({ ...prev, activityConfirmation: null }))}>Cancel</button>
                <button type="button" style={btn("#b45309")} disabled={!crmDataRepairState.activityConfirmation.reviewed || crmDataRepairState.repairingActivity} onClick={() => repairSelectedActivityDates({ confirmation: crmDataRepairState.activityConfirmation })}>{crmDataRepairState.repairingActivity ? "Verifying and repairing..." : "Confirm Update Activity Dates"}</button>
              </div>
            </section>
          </div>
        ) : null}

        <CrmRepairConsole
          key={workspaceUid}
          db={db}
          workspaceUid={workspaceUid}
          user={user}
          role={currentRepairRoleLabel}
          isOwner={Boolean(user?.uid) && (workspaceUid === user.uid || String(workspaceContext?.ownerUid || "") === user.uid)}
          isAdmin={["admin", "workspace_admin"].includes(normalizeCrmAccessRole(currentCrmRole))}
          isSuperAdmin={isSuperAdmin}
          allowed={canUseCrmDataRepairTools}
          fixturesAllowed={canUseCrmRepairTestDataTools}
          testModeEnabled={isCrmRepairTestModeEnabled}
          state={crmDataRepairState}
          getDoc={getDoc}
          getDocs={getDocs}
          invalidate={invalidateWorkspaceCollectionCache}
          onTestModeChange={(enabled) => {
            if (!canUseCrmDataRepairTools) return;
            try {
              setRepairTestMode(window.localStorage, enabled);
              setCrmDataRepairState((prev) => ({ ...prev }));
            } catch (error) {
              notify("Could not change repair test mode in this browser.", "warning");
            }
          }}
          onFixturePreview={(kind, group) => {
            if (!canUseCrmRepairTestDataTools) return;
            const resultsKey = kind === "deal" ? "duplicateDealResults" : "duplicateProjectResults";
            const selectedKey = kind === "deal" ? "duplicateDealSelected" : "duplicateProjectSelected";
            setCrmDataRepairState((prev) => ({ ...prev, [resultsKey]: [group], [selectedKey]: {} }));
          }}
          styles={{ card: { padding: 14, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder }, muted: dashboardMuted, button: { ...btn("#0f766e"), marginRight: 0 }, input }}
        />

        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: isDark ? "rgba(15,23,42,0.45)" : "#f8fafc",
            border: dashboardBorder,
            color: dashboardMuted,
            fontSize: 12,
            lineHeight: 1.55,
            fontWeight: 750,
          }}
        >
          Safe rules: exact phone/email/name matching only, no fuzzy customer merges, no automatic full scan, and every repair write is logged in repair logs.
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: isDark ? "rgba(22,101,52,0.18)" : "#ecfdf5",
            border: `1px solid ${isDark ? "rgba(74,222,128,0.35)" : "#bbf7d0"}`,
            color: isDark ? "#bbf7d0" : "#166534",
            fontSize: 12,
            lineHeight: 1.55,
            fontWeight: 800,
          }}
        >
          Current role: {currentRepairRoleLabel}. Repair access: allowed. Data Repair is visible only to owners/admins/superadmins; normal staff should not see this panel or trigger repair actions.
        </div>

        {canUseCrmRepairTestDataTools ? (
        <section
          style={{
            padding: 12,
            borderRadius: 14,
            background: isDark ? "rgba(15,23,42,0.45)" : "#f8fafc",
            border: dashboardBorder,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 900, color: textColor }}>Repair Test Data</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3, lineHeight: 1.5 }}>
                Superadmin or repair-mode fixtures for verifying repair writes. Records are tagged{" "}
                <strong>repair_test_data</strong> and use quote numbers TEST-REPAIR-001 / TEST-REPAIR-002.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={createCrmDataRepairTestData}
                disabled={crmDataRepairState.testDataBusy}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.testDataBusy ? 0.72 : 1 }}
              >
                {crmDataRepairState.testDataBusy ? "Working..." : "Create Repair Test Data"}
              </button>
              <button
                type="button"
                onClick={() => {
                  const confirmed =
                    typeof window === "undefined" ||
                    window.confirm(
                      "Delete only records tagged repair_test_data? Immutable repair logs will be kept."
                    );
                  if (confirmed) deleteCrmDataRepairTestData();
                }}
                disabled={crmDataRepairState.testDataBusy}
                style={{ ...btn("#dc2626"), marginRight: 0, opacity: crmDataRepairState.testDataBusy ? 0.72 : 1 }}
              >
                Delete Repair Test Data
              </button>
            </div>
          </div>
          {crmDataRepairState.testDataMessage ? (
            <div style={{ color: "#16a34a", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.testDataMessage}
            </div>
          ) : null}
          {crmDataRepairState.testDataError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.testDataError}
            </div>
          ) : null}
          {crmDataRepairState.testDataRecords?.length ? (
            <div style={{ color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              Records: {crmDataRepairState.testDataRecords.join(", ")}
            </div>
          ) : null}
          {crmDataRepairState.testDataVerification?.length ? (
            <div style={{ display: "grid", gap: 4, color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              {crmDataRepairState.testDataVerification.map((item) => (
                <div key={item.path}>
                  {item.path}: {item.existed ? "found" : "not found"}, tag{" "}
                  {item.tagged ? "verified" : "not verified"}, delete{" "}
                  {item.deleteQueued ? "queued" : "skipped"}, after cleanup{" "}
                  {item.gone ? "gone" : "still exists"}
                </div>
              ))}
            </div>
          ) : null}
          <div style={{ height: 1, background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 900, color: textColor }}>Quote Index Repair Fixtures</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3, lineHeight: 1.5 }}>
                Creates TEST-QI-001 with a correct full quotation and intentionally stale quote_index / CRM deal data.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={createQuoteIndexRepairTestData}
                disabled={crmDataRepairState.quoteIndexTestDataBusy}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.quoteIndexTestDataBusy ? 0.72 : 1 }}
              >
                {crmDataRepairState.quoteIndexTestDataBusy ? "Working..." : "Create Quote Index Test Data"}
              </button>
              <button
                type="button"
                onClick={() => {
                  const confirmed =
                    typeof window === "undefined" ||
                    window.confirm(
                      "Delete only quote index repair fixtures tagged repair_test_data? Immutable repair logs will be kept."
                    );
                  if (confirmed) deleteQuoteIndexRepairTestData();
                }}
                disabled={crmDataRepairState.quoteIndexTestDataBusy}
                style={{ ...btn("#dc2626"), marginRight: 0, opacity: crmDataRepairState.quoteIndexTestDataBusy ? 0.72 : 1 }}
              >
                Delete Quote Index Test Data
              </button>
            </div>
          </div>
          {crmDataRepairState.quoteIndexTestDataMessage ? (
            <div style={{ color: "#16a34a", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.quoteIndexTestDataMessage}
            </div>
          ) : null}
          {crmDataRepairState.quoteIndexTestDataError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.quoteIndexTestDataError}
            </div>
          ) : null}
          {crmDataRepairState.quoteIndexTestDataRecords?.length ? (
            <div style={{ color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              Quote index fixtures: {crmDataRepairState.quoteIndexTestDataRecords.join(", ")}
            </div>
          ) : null}
          {crmDataRepairState.quoteIndexTestDataVerification?.length ? (
            <div style={{ display: "grid", gap: 4, color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              {crmDataRepairState.quoteIndexTestDataVerification.map((item) => (
                <div key={item.path}>
                  {item.path}: {item.existed ? "found" : "not found"}, tag{" "}
                  {item.tagged ? "verified" : "not verified"}, delete{" "}
                  {item.deleteQueued ? "queued" : "skipped"}, after cleanup{" "}
                  {item.gone ? "gone" : "still exists"}
                </div>
              ))}
            </div>
          ) : null}
        </section>
        ) : null}

        {crmDataRepairState.repairLogChecks?.length ? (
          <section
            style={{
              padding: 12,
              borderRadius: 14,
              background: isDark ? "rgba(15,23,42,0.45)" : "#f8fafc",
              border: dashboardBorder,
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontWeight: 900, color: textColor }}>Latest Repair Log Check</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3, lineHeight: 1.5 }}>
                Verifies the immutable audit record written by the last repair action.
              </div>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {crmDataRepairState.repairLogChecks.map((check) => (
                <div
                  key={check.repairId}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    color: textColor,
                    fontSize: 12,
                    lineHeight: 1.55,
                  }}
                >
                  <div style={{ fontWeight: 900 }}>
                    {check.toolName || "Repair log"} / {check.recordId || check.quoteNo || check.quoteId || check.repairId}
                  </div>
                  <div style={{ color: check.exists && !check.missingFields?.length ? "#16a34a" : "#dc2626", fontWeight: 800 }}>
                    {check.exists && !check.missingFields?.length
                      ? "Audit log complete"
                      : `Audit log needs review${check.missingFields?.length ? `: ${check.missingFields.join(", ")}` : ""}`}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    before: {check.hasBefore ? "yes" : "missing"} | after: {check.hasAfter ? "yes" : "missing"} | performed by:{" "}
                    {check.hasPerformedBy ? "yes" : "missing"} | performed at: {check.hasPerformedAt ? "yes" : "missing"} | success:{" "}
                    {check.success ? "yes" : "missing"}
                  </div>
                  {check.readError ? (
                    <div style={{ color: "#dc2626", fontWeight: 800 }}>Read error: {check.readError}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section style={{ display: "grid", gap: 10 }}>
          <div id="repair-links" style={{ fontWeight: 900, color: textColor }}>Diagnose One Quote</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              value={crmDataRepairState.quoteLookup}
              onChange={(event) =>
                setCrmDataRepairState((prev) => ({
                  ...prev,
                  quoteLookup: event.target.value,
                }))
              }
              style={{ ...input, minWidth: isCompact ? "100%" : 260 }}
              placeholder="Quote no or quote ID"
            />
            <button
              type="button"
              onClick={runQuoteLinkDiagnosis}
              disabled={crmDataRepairState.quoteDiagnosticLoading}
              style={{ ...btn("#2563eb"), marginRight: 0, opacity: crmDataRepairState.quoteDiagnosticLoading ? 0.72 : 1 }}
            >
              {crmDataRepairState.quoteDiagnosticLoading ? "Checking..." : "Dry Run Diagnosis"}
            </button>
          </div>
          {crmDataRepairState.quoteDiagnosticError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.quoteDiagnosticError}
            </div>
          ) : null}
          {crmDataRepairState.quoteDiagnostic ? (
            <div style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8, fontSize: 12.5, color: textColor }}>
              <div style={{ fontWeight: 900 }}>
                {crmDataRepairState.quoteDiagnostic.quoteNo || crmDataRepairState.quoteDiagnostic.quoteId || "Quote"} diagnosis
              </div>
              <div>
                Full quote: {crmDataRepairState.quoteDiagnostic.fullQuote?.exists ? "found" : "missing"} | quote_index:{" "}
                {crmDataRepairState.quoteDiagnostic.quoteIndex?.exists ? "found" : "missing"} | deals:{" "}
                {crmDataRepairState.quoteDiagnostic.deals?.length || 0} | projects:{" "}
                {crmDataRepairState.quoteDiagnostic.projects?.length || 0}
              </div>
              <div>
                Customer: {crmDataRepairState.quoteDiagnostic.customer?.displayName || "Not linked"}{" "}
                {crmDataRepairState.quoteDiagnostic.customer?.matchedCustomerId
                  ? `(${crmDataRepairState.quoteDiagnostic.customer.matchedCustomerId})`
                  : ""}
              </div>
              <div style={{ color: crmDataRepairState.quoteDiagnostic.issues?.length ? "#b45309" : "#16a34a", fontWeight: 800 }}>
                Issues: {crmDataRepairState.quoteDiagnostic.issues?.length ? crmDataRepairState.quoteDiagnostic.issues.join(", ") : "None found"}
              </div>
            </div>
          ) : null}
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-quote-index" style={{ fontWeight: 900, color: textColor }}>Quote Index / Saved Quotes Repair</div>
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
                Dry run scans quote_index, 25 records at a time, and reads only the matching full quotation for each row.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => findQuoteIndexMismatches({ append: false })}
                disabled={crmDataRepairState.quoteIndexMismatchLoading}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.quoteIndexMismatchLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.quoteIndexMismatchLoading ? "Scanning..." : "Find Quote Index Mismatches"}
              </button>
              {crmDataRepairState.quoteIndexMismatchHasMore ? (
                <button
                  type="button"
                  onClick={() => findQuoteIndexMismatches({ append: true })}
                  disabled={crmDataRepairState.quoteIndexMismatchLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    quoteIndexMismatchResults: [],
                    quoteIndexMismatchCursor: null,
                    quoteIndexMismatchHasMore: false,
                    quoteIndexMismatchError: "",
                  }))
                }
                style={{ ...btn("#475569"), marginRight: 0 }}
              >
                Clear Results
              </button>
            </div>
          </div>
          {crmDataRepairState.quoteIndexMismatchError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.quoteIndexMismatchError}
            </div>
          ) : null}
          <div style={{ display: "grid", gap: 8 }}>
            {crmDataRepairState.quoteIndexMismatchResults.map((row) => {
              const crmBusyKey = `${row.quoteId || row.quoteNo}:crmDeal`;
              const canRepairFromFullQuote = row.risk !== "Manual Only" && Boolean(row.quoteId);
              return (
                <div key={row.key} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 900, color: textColor }}>
                        <RepairDataBadge record={row} /> {row.quoteNo || row.quoteId || row.quoteIndexId}
                      </div>
                      <div style={{ color: dashboardMuted, fontSize: 12 }}>
                        Full quote: {row.fullQuoteCustomerName || "missing"} / {row.fullQuoteSiteName || "No site"} | Index:{" "}
                        {row.indexCustomerName || "missing"} / {row.indexSiteName || "No site"}
                      </div>
                      <div style={{ color: dashboardMuted, fontSize: 12 }}>
                        Total full/index: {row.fullQuoteGrandTotal ?? "-"} / {row.indexGrandTotal ?? "-"} | Status full/index:{" "}
                        {row.fullQuoteStatus || "-"} / {row.indexStatus || "-"}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 900, color: row.risk === "Safe" ? "#16a34a" : row.risk === "Needs Review" ? "#b45309" : "#dc2626" }}>
                      {row.risk || "Needs Review"}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Issues: {row.issues.join(", ")}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Action: {row.recommendedAction}
                    {row.repaired ? ` | quote_index repaired${row.repairedMode ? ` (${row.repairedMode})` : ""}` : ""}
                    {row.crmDealRepaired ? " | CRM deal repaired" : ""}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {QUOTE_INDEX_REPAIR_MODES.map(([mode, label]) => {
                      const preview = row.modePreviews?.[mode];
                      const disabled = !canRepairFromFullQuote || Boolean(crmDataRepairState.repairingQuoteIndexKey) || !preview || Boolean(preview.warning) || !Object.keys(preview.patch || {}).length;
                      return (
                        <details key={mode} style={{ flexBasis: "100%" }}>
                          <summary>{label}{preview?.warning ? " - Manual Only" : ""}</summary>
                          {preview?.warning ? <p role="status">{preview.warning}</p> : null}
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", fontSize: 12 }}>
                              <thead><tr><th>Field</th><th>Current quote_index</th><th>Full quote source</th><th>Will update?</th></tr></thead>
                              <tbody>{(preview?.fields || []).map((field) => (
                                <tr key={field.field}><td>{field.field}</td><td>{repairValueText(field.before)}</td><td>{repairValueText(field.after)} ({field.source})</td><td>{field.changed && !preview.warning ? "Yes" : "No"}</td></tr>
                              ))}</tbody>
                            </table>
                          </div>
                          {row.repair_test_data !== true ? <p>You are repairing real business data. Review carefully. This action will be logged.</p> : null}
                          <p>{quoteIndexRepairConfirmation(mode)}</p>
                          <button type="button" disabled={disabled} onClick={() => repairQuoteIndexFromFullQuote({ row, mode })} style={{ ...btn(disabled ? "#94a3b8" : "#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                            {crmDataRepairState.repairingQuoteIndexKey === `${row.quoteIndexId}:${mode}` ? "Repairing..." : label}
                          </button>
                        </details>
                      );
                    })}
                    <button
                      type="button"
                      disabled={!canRepairFromFullQuote || crmDataRepairState.repairingCrmDealKey === crmBusyKey}
                      onClick={() => repairCrmDealQuoteFieldsFromQuote({ row })}
                      style={{ ...btn(canRepairFromFullQuote ? "#ea580c" : "#94a3b8"), marginRight: 0, padding: "8px 10px", opacity: !canRepairFromFullQuote ? 0.65 : 1 }}
                    >
                      {crmDataRepairState.repairingCrmDealKey === crmBusyKey ? "Repairing..." : "Repair CRM Deal Fields"}
                    </button>
                  </div>
                </div>
              );
            })}
            {!crmDataRepairState.quoteIndexMismatchLoading && !crmDataRepairState.quoteIndexMismatchResults.length ? (
              <div style={{ color: dashboardMuted, fontSize: 12 }}>No dry-run results yet.</div>
            ) : null}
          </div>
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 900, color: textColor }}>Find Unlinked Quotes</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>Dry run scans quote_index, 25 records at a time.</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select
                value={crmDataRepairState.unlinkedMode}
                onChange={(event) =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    unlinkedMode: event.target.value,
                  }))
                }
                style={input}
              >
                <option value="link_only">Link only</option>
                <option value="link_and_update_quote_customer">Also update quote customer</option>
              </select>
              <button
                type="button"
                onClick={() => findUnlinkedQuotesForRepair({ append: false })}
                disabled={crmDataRepairState.unlinkedLoading}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.unlinkedLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.unlinkedLoading ? "Scanning..." : "Dry Run Page"}
              </button>
              {crmDataRepairState.unlinkedHasMore ? (
                <button
                  type="button"
                  onClick={() => findUnlinkedQuotesForRepair({ append: true })}
                  disabled={crmDataRepairState.unlinkedLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
            </div>
          </div>
          {crmDataRepairState.unlinkedError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.unlinkedError}
            </div>
          ) : null}
          <div style={{ display: "grid", gap: 8 }}>
            {crmDataRepairState.unlinkedResults.map((row) => {
              const repairKey = `${row.quoteIndexId}:${row.suggestedCustomerId || ""}`;
              const canUseSuggestion = Boolean(row.suggestedCustomerId);
              return (
                <div key={row.key} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 900, color: textColor }}>
                        <RepairDataBadge record={row} /> {row.quoteNo || row.quoteId || row.quoteIndexId}
                      </div>
                      <div style={{ color: dashboardMuted, fontSize: 12 }}>
                        {row.quoteCustomerName || "No customer name"} {row.quotePhone ? `| ${row.quotePhone}` : ""}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 900, color: row.risk === "Safe" ? "#16a34a" : row.risk === "Needs Review" ? "#b45309" : "#dc2626" }}>
                      {row.risk || "Needs Review"}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Issues: {row.issues.join(", ")}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Suggested customer: {row.suggestedCustomerName || "No exact single match"}{" "}
                    {row.suggestedCustomerId ? `(${row.suggestedCustomerId})` : ""} | {row.reason}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      disabled={!canUseSuggestion || crmDataRepairState.repairingQuoteKey === repairKey || row.repaired}
                      onClick={() =>
                        repairQuoteCustomerLink({
                          repairRow: row,
                          targetCustomerId: row.suggestedCustomerId,
                          mode: crmDataRepairState.unlinkedMode,
                        })
                      }
                      style={{ ...btn(canUseSuggestion ? "#2563eb" : "#94a3b8"), marginRight: 0, padding: "8px 10px", opacity: !canUseSuggestion || row.repaired ? 0.65 : 1 }}
                    >
                      {row.repaired
                        ? "Repaired"
                        : crmDataRepairState.repairingQuoteKey === repairKey
                        ? "Repairing..."
                        : "Link Suggested"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const manualCustomerId =
                          typeof window !== "undefined"
                            ? window.prompt("Enter exact customer ID to link this quote:")
                            : "";
                        if (manualCustomerId) {
                          repairQuoteCustomerLink({
                            repairRow: row,
                            targetCustomerId: manualCustomerId,
                            mode: crmDataRepairState.unlinkedMode,
                          });
                        }
                      }}
                      disabled={Boolean(crmDataRepairState.repairingQuoteKey) || row.repaired}
                      style={{ ...btn("#475569"), marginRight: 0, padding: "8px 10px", opacity: row.repaired ? 0.65 : 1 }}
                    >
                      Use Customer ID
                    </button>
                  </div>
                </div>
              );
            })}
            {!crmDataRepairState.unlinkedLoading && !crmDataRepairState.unlinkedResults.length ? (
              <div style={{ color: dashboardMuted, fontSize: 12 }}>
                No dry-run results yet.
              </div>
            ) : null}
          </div>
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-activity" style={{ fontWeight: 900, color: textColor }}>Repair Missing Activity Dates</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Uses old updatedAt, createdAt, quoteDate, or date. Never uses the current time as the old activity date.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select
                value={crmDataRepairState.activityCollection}
                onChange={(event) =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    activityCollection: event.target.value,
                    activityResults: [],
                    activityCursor: null,
                    activitySelected: {},
                    activityError: "",
                  }))
                }
                style={input}
              >
                {CRM_ACTIVITY_REPAIR_COLLECTIONS.map((collectionName) => (
                  <option key={collectionName} value={collectionName}>
                    {collectionName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => findMissingActivityDatesForRepair({ append: false })}
                disabled={crmDataRepairState.activityLoading}
                style={{ ...btn("#7c3aed"), marginRight: 0, opacity: crmDataRepairState.activityLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.activityLoading ? "Scanning..." : "Dry Run Page"}
              </button>
              {crmDataRepairState.activityHasMore ? (
                <button
                  type="button"
                  onClick={() => findMissingActivityDatesForRepair({ append: true })}
                  disabled={crmDataRepairState.activityLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
            </div>
          </div>
          {crmDataRepairState.activityError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.activityError}
            </div>
          ) : null}
          {crmDataRepairState.activityResults.length ? (
            <div style={{ display: "grid", gap: 8 }}>
              {crmDataRepairState.activityResults.map((row) => (
                <label
                  key={row.key}
                  style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "flex", gap: 10, alignItems: "flex-start", color: textColor }}
                >
                  <input
                    type="checkbox"
                    checked={crmDataRepairState.activitySelected[row.key] === true}
                    disabled={!canRepairActivityDateRow(row)}
                    onChange={(event) =>
                      setCrmDataRepairState((prev) => ({
                        ...prev,
                        activitySelected: {
                          ...prev.activitySelected,
                          [row.key]: event.target.checked,
                        },
                      }))
                    }
                  />
                  <span style={{ display: "grid", gap: 3, fontSize: 12.5 }}>
                    <RepairDataBadge record={row} />
                    <strong>{row.collectionName}/{row.recordId}</strong>
                    <span>{row.customerName || "Customer unavailable"} | Risk: {row.risk}</span>
                    <span>Current lastActivityAt: {formatSafeDate(row.currentLastActivityAt, row.currentLastActivityAt == null || row.currentLastActivityAt === "" ? "Missing" : "Stored value (not missing)")}</span>
                    <span style={{ color: dashboardMuted }}>
                      Proposed lastActivityAt: {row.proposedLastActivityAtMs != null ? formatCrmDate(new Date(row.proposedLastActivityAtMs)) : "None"} | Proposed source: {row.source}
                      {row.quoteNo ? ` | Quote ${row.quoteNo}` : ""}
                      {row.repaired ? " | Repaired" : ""}
                    </span>
                    {row.warning ? <span style={{ color: "#b45309" }}>{row.warning}</span> : null}
                  </span>
                </label>
              ))}
              <button
                type="button"
                onClick={repairSelectedActivityDates}
                disabled={!selectedActivityCount || crmDataRepairState.repairingActivity}
                style={{ ...btn(selectedActivityCount ? "#16a34a" : "#94a3b8"), marginRight: 0, justifySelf: "start", opacity: !selectedActivityCount || crmDataRepairState.repairingActivity ? 0.7 : 1 }}
              >
                {crmDataRepairState.repairingActivity
                  ? "Repairing..."
                  : `Repair Selected (${selectedActivityCount})`}
              </button>
            </div>
          ) : !crmDataRepairState.activityLoading ? (
            <div style={{ color: dashboardMuted, fontSize: 12 }}>
              No dry-run results yet.
            </div>
          ) : null}
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-duplicate-deals" style={{ fontWeight: 900, color: textColor }}>Duplicate CRM Deals</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Dry run scans CRM deals 25 records at a time and groups exact quote ID / quote number matches only.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => findDuplicateDealsForRepair({ append: false })}
                disabled={crmDataRepairState.duplicateDealsLoading}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.duplicateDealsLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.duplicateDealsLoading ? "Scanning..." : "Dry Run"}
              </button>
              {crmDataRepairState.duplicateDealsHasMore ? (
                <button
                  type="button"
                  onClick={() => findDuplicateDealsForRepair({ append: true })}
                  disabled={crmDataRepairState.duplicateDealsLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    duplicateDealResults: [],
                    duplicateDealsCursor: null,
                    duplicateDealsHasMore: false,
                    duplicateDealsError: "",
                  }))
                }
                style={{ ...btn("#475569"), marginRight: 0 }}
              >
                Clear Results
              </button>
            </div>
          </div>
          {crmDataRepairState.duplicateDealsError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.duplicateDealsError}
            </div>
          ) : null}
          <div style={{ display: "grid", gap: 8 }}>
            {crmDataRepairState.duplicateDealResults.map((group) => (
              <div key={group.key} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900, color: textColor }}>
                      <RepairDataBadge record={group} /> {group.quoteNo || group.quoteId || group.key}
                    </div>
                    <div style={{ color: dashboardMuted, fontSize: 12 }}>
                      {group.customerName || "No customer name"} | Deals: {group.dealIds.join(", ")}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 900, color: group.risk === "Safe" ? "#16a34a" : "#b45309" }}>
                    {group.risk}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Primary: {group.recommendedPrimaryDealId || "Not selected"} | Archive: {group.duplicateDealIds.join(", ") || "None"} | {group.reason}
                </div>
                <button
                  type="button"
                  onClick={() => archiveDuplicateDealGroup(group)}
                  disabled={!crmDataRepairState.duplicateDealSelected[group.key] || !group.duplicateDealIds?.length || Boolean(crmDataRepairState.repairingDuplicateDealKey)}
                  style={{ ...btn("#dc2626"), marginRight: 0, justifySelf: "start", opacity: !group.duplicateDealIds?.length ? 0.65 : 1 }}
                >
                  {crmDataRepairState.repairingDuplicateDealKey === group.key ? "Archiving..." : "Repair Selected"}
                </button>
                <label style={{ fontSize: 12 }}><input type="checkbox" checked={crmDataRepairState.duplicateDealSelected[group.key] === true} onChange={(event) => setCrmDataRepairState((prev) => ({ ...prev, duplicateDealSelected: { [group.key]: event.target.checked } }))} /> I reviewed this group. Archive the listed duplicates and keep the primary active.</label>
              </div>
            ))}
            {!crmDataRepairState.duplicateDealsLoading && !crmDataRepairState.duplicateDealResults.length ? (
              <div style={{ color: dashboardMuted, fontSize: 12 }}>No dry-run results yet.</div>
            ) : null}
          </div>
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-projects" style={{ fontWeight: 900, color: textColor }}>Project Pipeline Repair</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Finds projects missing Orders pipeline fields and suggests a legacy-safe stage mapping.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => findProjectsMissingPipelineStageForRepair({ append: false })}
                disabled={crmDataRepairState.projectStageRepairLoading}
                style={{ ...btn("#7c3aed"), marginRight: 0, opacity: crmDataRepairState.projectStageRepairLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.projectStageRepairLoading ? "Scanning..." : "Dry Run"}
              </button>
              {crmDataRepairState.projectStageHasMore ? (
                <button
                  type="button"
                  onClick={() => findProjectsMissingPipelineStageForRepair({ append: true })}
                  disabled={crmDataRepairState.projectStageRepairLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    projectStageResults: [],
                    projectStageCursor: null,
                    projectStageHasMore: false,
                    projectStageSelected: {},
                    projectStageRepairError: "",
                  }))
                }
                style={{ ...btn("#475569"), marginRight: 0 }}
              >
                Clear Results
              </button>
            </div>
          </div>
          {crmDataRepairState.projectStageRepairError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.projectStageRepairError}
            </div>
          ) : null}
          {crmDataRepairState.projectStageResults.length ? (
            <div style={{ display: "grid", gap: 8 }}>
              {crmDataRepairState.projectStageResults.map((row) => (
                <label
                  key={row.key}
                  style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "flex", gap: 10, alignItems: "flex-start", color: textColor }}
                >
                  <input
                    type="checkbox"
                    checked={crmDataRepairState.projectStageSelected[row.key] === true}
                    disabled={row.repaired}
                    onChange={(event) =>
                      setCrmDataRepairState((prev) => ({
                        ...prev,
                        projectStageSelected: {
                          ...prev.projectStageSelected,
                          [row.key]: event.target.checked,
                        },
                      }))
                    }
                  />
                  <span style={{ display: "grid", gap: 3, fontSize: 12.5 }}>
                    <RepairDataBadge record={row} />
                    <strong>{row.customerName || row.projectId}</strong>
                    <span style={{ color: dashboardMuted }}>
                      {row.quoteNo ? `Quote ${row.quoteNo} | ` : ""}
                      Current: {row.projectStage || row.orderStage || row.currentStage || "missing"} | Suggested: {row.suggestedStageLabel || row.suggestedStage}
                      {row.repaired ? " | Repaired" : ""}
                    </span>
                    <span style={{ color: dashboardMuted }}>{row.reason}</span>
                  </span>
                </label>
              ))}
              <button
                type="button"
                onClick={repairSelectedProjectPipelineStages}
                disabled={!selectedProjectStageCount || crmDataRepairState.repairingProjectStage}
                style={{ ...btn(selectedProjectStageCount ? "#16a34a" : "#94a3b8"), marginRight: 0, justifySelf: "start", opacity: !selectedProjectStageCount || crmDataRepairState.repairingProjectStage ? 0.7 : 1 }}
              >
                {crmDataRepairState.repairingProjectStage
                  ? "Repairing..."
                  : `Repair Selected (${selectedProjectStageCount})`}
              </button>
            </div>
          ) : !crmDataRepairState.projectStageRepairLoading ? (
            <div style={{ color: dashboardMuted, fontSize: 12 }}>No dry-run results yet.</div>
          ) : null}
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-duplicate-projects" style={{ fontWeight: 900, color: textColor }}>Duplicate Projects</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Dry run scans projects 25 records at a time and groups exact quote/deal references only.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => findDuplicateProjectsForRepair({ append: false })}
                disabled={crmDataRepairState.duplicateProjectsLoading}
                style={{ ...btn("#0f766e"), marginRight: 0, opacity: crmDataRepairState.duplicateProjectsLoading ? 0.72 : 1 }}
              >
                {crmDataRepairState.duplicateProjectsLoading ? "Scanning..." : "Dry Run"}
              </button>
              {crmDataRepairState.duplicateProjectsHasMore ? (
                <button
                  type="button"
                  onClick={() => findDuplicateProjectsForRepair({ append: true })}
                  disabled={crmDataRepairState.duplicateProjectsLoading}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Load Next Page
                </button>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  setCrmDataRepairState((prev) => ({
                    ...prev,
                    duplicateProjectResults: [],
                    duplicateProjectsCursor: null,
                    duplicateProjectsHasMore: false,
                    duplicateProjectsError: "",
                  }))
                }
                style={{ ...btn("#475569"), marginRight: 0 }}
              >
                Clear Results
              </button>
            </div>
          </div>
          {crmDataRepairState.duplicateProjectsError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.duplicateProjectsError}
            </div>
          ) : null}
          <div style={{ display: "grid", gap: 8 }}>
            {crmDataRepairState.duplicateProjectResults.map((group) => (
              <div key={group.key} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900, color: textColor }}>
                      <RepairDataBadge record={group} /> {group.quoteNo || group.quoteId || group.dealId || group.opportunityId || group.key}
                    </div>
                    <div style={{ color: dashboardMuted, fontSize: 12 }}>
                      {group.customerName || "No customer name"} | Projects: {group.projectIds.join(", ")}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 900, color: group.risk === "Safe" ? "#16a34a" : "#b45309" }}>
                    {group.risk}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Primary: {group.recommendedPrimaryProjectId || "Not selected"} | Archive: {group.duplicateProjectIds.join(", ") || "None"} | {group.reason}
                </div>
                <button
                  type="button"
                  onClick={() => archiveDuplicateProjectGroup(group)}
                  disabled={!crmDataRepairState.duplicateProjectSelected[group.key] || !group.duplicateProjectIds?.length || Boolean(crmDataRepairState.repairingDuplicateProjectKey)}
                  style={{ ...btn("#dc2626"), marginRight: 0, justifySelf: "start", opacity: !group.duplicateProjectIds?.length ? 0.65 : 1 }}
                >
                  {crmDataRepairState.repairingDuplicateProjectKey === group.key ? "Archiving..." : "Repair Selected"}
                </button>
                <label style={{ fontSize: 12 }}><input type="checkbox" checked={crmDataRepairState.duplicateProjectSelected[group.key] === true} onChange={(event) => setCrmDataRepairState((prev) => ({ ...prev, duplicateProjectSelected: { [group.key]: event.target.checked } }))} /> I reviewed this group. Archive the listed duplicates and keep the primary active.</label>
              </div>
            ))}
            {!crmDataRepairState.duplicateProjectsLoading && !crmDataRepairState.duplicateProjectResults.length ? (
              <div style={{ color: dashboardMuted, fontSize: 12 }}>No dry-run results yet.</div>
            ) : null}
          </div>
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div id="repair-customer-search" style={{ fontWeight: 900, color: textColor }}>Customer Search Index Repair</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Search and select one exact customer, then rebuild only that customer's search index. Quote summaries are not scanned here.
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                setCrmDataRepairState((prev) => ({
                  ...prev,
                  customerIndexSearchQuery: "",
                  customerIndexSearchResults: [],
                  customerIndexSearchError: "",
                  customerIndexSearchSearched: false,
                  customerIndexSelectedCustomer: null,
                  customerIndexRepairId: "",
                  customerIndexRepairResult: null,
                  customerIndexRepairError: "",
                }))
              }
              style={{ ...btn("#475569"), marginRight: 0 }}
            >
              Clear Results
            </button>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <input
              value={crmDataRepairState.customerIndexSearchQuery}
              onChange={(event) =>
                setCrmDataRepairState((prev) => ({
                  ...prev,
                  customerIndexSearchQuery: event.target.value,
                  customerIndexSelectedCustomer: null,
                  customerIndexRepairId: "",
                  customerIndexRepairResult: null,
                  customerIndexRepairError: "",
                }))
              }
              style={{ ...input, width: "100%" }}
              placeholder="Search customer by name, phone, or email"
            />
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Uses bounded customer_search_index queries only. Select a customer result before dry-run or repair.
            </div>
          </div>

          {crmDataRepairState.customerIndexSearchError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.customerIndexSearchError}
            </div>
          ) : null}

          {crmDataRepairState.customerIndexSearchLoading ? (
            <div style={{ color: dashboardMuted, fontSize: 12, fontWeight: 800 }}>Searching customers...</div>
          ) : null}

          {crmDataRepairState.customerIndexSearchResults.length ? (
            <div style={{ display: "grid", gap: 8 }}>
              {crmDataRepairState.customerIndexSearchResults.map((customer) => {
                const isSelected =
                  crmDataRepairState.customerIndexSelectedCustomer?.customerId === customer.customerId;
                return (
                  <button
                    key={customer.customerId}
                    type="button"
                    onClick={() =>
                      setCrmDataRepairState((prev) => ({
                        ...prev,
                        customerIndexSelectedCustomer: customer,
                        customerIndexRepairId: customer.customerId,
                        customerIndexRepairResult: null,
                        customerIndexRepairError: "",
                      }))
                    }
                    style={{
                      textAlign: "left",
                      padding: 12,
                      borderRadius: 14,
                      border: isSelected ? "2px solid #2563eb" : dashboardBorder,
                      background: isSelected
                        ? isDark
                          ? "rgba(37,99,235,0.18)"
                          : "#eff6ff"
                        : dashboardSoftBg,
                      color: textColor,
                      display: "grid",
                      gap: 4,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <strong>{customer.displayName || "Unnamed customer"}</strong>
                      <span style={{ fontSize: 11, fontWeight: 900, color: "#2563eb" }}>
                        {customer.customerType || "Customer"}
                      </span>
                    </span>
                    <span style={{ fontSize: 12, color: dashboardMuted }}>
                      {customer.phone || "No phone"} | {customer.email || "No email"}
                    </span>
                    <span style={{ fontSize: 11, color: dashboardMuted }}>
                      Customer ID: {customer.customerId}
                      {customer.customerSearchIndexId ? ` | Index: ${customer.customerSearchIndexId}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : crmDataRepairState.customerIndexSearchSearched &&
            !crmDataRepairState.customerIndexSearchLoading &&
            normalizeCustomerSearchText(crmDataRepairState.customerIndexSearchQuery).length >= 2 ? (
            <div style={{ color: dashboardMuted, fontSize: 12 }}>
              No customer records found in the search index. Try a name, phone, or email from the customer ledger.
            </div>
          ) : null}

          {crmDataRepairState.customerIndexSelectedCustomer ? (
            <div style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 5, fontSize: 12.5, color: textColor }}>
              <div style={{ fontWeight: 900 }}>
                Selected: {crmDataRepairState.customerIndexSelectedCustomer.displayName || crmDataRepairState.customerIndexSelectedCustomer.customerId}
              </div>
              <div style={{ color: dashboardMuted }}>
                Phone: {crmDataRepairState.customerIndexSelectedCustomer.phone || "—"} | Email: {crmDataRepairState.customerIndexSelectedCustomer.email || "—"}
              </div>
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              value={crmDataRepairState.customerIndexRepairId}
              readOnly
              style={{ ...input, minWidth: isCompact ? "100%" : 260, opacity: 0.82 }}
              placeholder="Selected customer ID"
            />
            <button
              type="button"
              onClick={() => rebuildCustomerSearchIndexForRepair({ dryRun: true })}
              disabled={crmDataRepairState.customerIndexRepairLoading || !crmDataRepairState.customerIndexSelectedCustomer}
              style={{ ...btn(crmDataRepairState.customerIndexSelectedCustomer ? "#2563eb" : "#94a3b8"), marginRight: 0, opacity: crmDataRepairState.customerIndexRepairLoading ? 0.72 : 1 }}
            >
              Dry Run
            </button>
            <button
              type="button"
              onClick={() => rebuildCustomerSearchIndexForRepair({ dryRun: false })}
              disabled={
                crmDataRepairState.customerIndexRepairLoading ||
                !crmDataRepairState.customerIndexSelectedCustomer ||
                !crmDataRepairState.customerIndexRepairResult
              }
              style={{ ...btn(crmDataRepairState.customerIndexRepairResult ? "#16a34a" : "#94a3b8"), marginRight: 0, opacity: crmDataRepairState.customerIndexRepairLoading ? 0.72 : 1 }}
            >
              Repair This Customer Search Index
            </button>
          </div>
          {crmDataRepairState.customerIndexRepairError ? (
            <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
              {crmDataRepairState.customerIndexRepairError}
            </div>
          ) : null}
          {crmDataRepairState.customerIndexRepairResult ? (
            <div style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 5, fontSize: 12.5, color: textColor }}>
              <div style={{ fontWeight: 900 }}>
                {crmDataRepairState.customerIndexRepairResult.displayName || crmDataRepairState.customerIndexRepairResult.customerId}
                {" "}<RepairDataBadge record={crmDataRepairState.customerIndexRepairResult} />
              </div>
              <div style={{ color: dashboardMuted }}>
                Phone: {crmDataRepairState.customerIndexRepairResult.phone || "—"} | Email: {crmDataRepairState.customerIndexRepairResult.email || "—"}
              </div>
              <div style={{ color: dashboardMuted }}>
                Search tokens: {crmDataRepairState.customerIndexRepairResult.searchTokenCount} | Prefixes: {crmDataRepairState.customerIndexRepairResult.searchPrefixCount}
              </div>
              <div style={{ color: dashboardMuted }}>
                {crmDataRepairState.customerIndexRepairResult.quoteSummary}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                <div style={{ padding: 10, borderRadius: 12, border: dashboardBorder, background: isDark ? "rgba(15,23,42,0.35)" : "#fff" }}>
                  <div style={{ fontWeight: 900 }}>Before</div>
                  <div style={{ color: dashboardMuted }}>
                    {crmDataRepairState.customerIndexRepairResult.before?.exists
                      ? crmDataRepairState.customerIndexRepairResult.before.displayName || "Existing index found"
                      : "No existing customer_search_index doc at this customer ID"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Phone normalized: {crmDataRepairState.customerIndexRepairResult.before?.phoneNormalized || "—"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Email canonical: {crmDataRepairState.customerIndexRepairResult.before?.emailCanonical || "—"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Active: {crmDataRepairState.customerIndexRepairResult.before?.active ? "Yes" : "No"} | Deleted: {crmDataRepairState.customerIndexRepairResult.before?.deleted ? "Yes" : "No"} | Archived: {crmDataRepairState.customerIndexRepairResult.before?.archived ? "Yes" : "No"}
                  </div>
                </div>
                <div style={{ padding: 10, borderRadius: 12, border: dashboardBorder, background: isDark ? "rgba(15,23,42,0.35)" : "#fff" }}>
                  <div style={{ fontWeight: 900 }}>After</div>
                  <div style={{ color: dashboardMuted }}>
                    {crmDataRepairState.customerIndexRepairResult.after?.displayName || "Rebuilt customer index"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Phone normalized: {crmDataRepairState.customerIndexRepairResult.after?.phoneNormalized || "—"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Email canonical: {crmDataRepairState.customerIndexRepairResult.after?.emailCanonical || "—"}
                  </div>
                  <div style={{ color: dashboardMuted }}>
                    Active: {crmDataRepairState.customerIndexRepairResult.after?.active ? "Yes" : "No"} | Deleted: {crmDataRepairState.customerIndexRepairResult.after?.deleted ? "Yes" : "No"} | Archived: {crmDataRepairState.customerIndexRepairResult.after?.archived ? "Yes" : "No"}
                  </div>
                </div>
              </div>
              {crmDataRepairState.customerIndexRepairResult.readBack ? (
                <div style={{ color: "#16a34a", fontWeight: 900 }}>
                  Read-back confirmed: {crmDataRepairState.customerIndexRepairResult.readBack.exists ? "customer_search_index updated" : "customer_search_index read-back missing"}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
    );
  };

  const renderCrmSettings = () => {
    const pipeline = getCrmPipeline(crmPipelines, activeCrmPipelineType);
    const hasConfirmationStage = pipeline.stages.some((stage) => stage.confirmsQuote);
    const normalizedSalesPipeline = normalizeSalesPipeline(salesPipeline);
    const normalizedOrderPipeline = normalizeOrderPipeline(orderPipeline);

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>CRM Settings</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Configure the lightweight CRM workflow, follow-up prompts, and management review screens.
              </div>
            </div>
            <button
              type="button"
              onClick={saveCrmSettings}
              disabled={!canManageCrmSettings || isSavingCrmSettings}
              style={{
                ...btn(canManageCrmSettings ? "#16a34a" : "#94a3b8"),
                marginRight: 0,
                opacity: !canManageCrmSettings || isSavingCrmSettings ? 0.72 : 1,
              }}
            >
              {isSavingCrmSettings ? "Saving..." : "Save CRM Settings"}
            </button>
          </div>

          {crmSettingsLoadState.error ? (
            <div style={{ padding: 12, borderRadius: 14, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmSettingsLoadState.error}
            </div>
          ) : null}

          {!canManageCrmSettings ? (
            <div style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, color: dashboardMuted, fontSize: 13, fontWeight: 700 }}>
              You can view CRM settings, but only workspace owners/admins can edit them.
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            {[
              ["enableCrm", "Enable CRM features"],
              ["warnWhenNoNextAction", "Warn when active lead has no next action"],
              ["requireNextAction", "Require next action before saving active lead"],
              ["enableQuoteSentFollowUpPrompt", "Enable quote follow-up prompt"],
              ["autoCreateDealFromQuotation", "Automatically add saved quotations to CRM pipeline"],
              ["enableAttentionRequired", "Enable Attention Required"],
              ["enablePipelineReview", "Enable Pipeline Review"],
              ["enableOrderHandover", "Enable order handover"],
              ["enableOrderTaskTemplates", "Enable order task templates"],
              ["autoCreateOrderTasks", "Automatically create default order tasks"],
              ["enableDeliveryTracking", "Enable delivery/material tracking"],
              ["enableDailyTrackingReminder", "Enable daily tracking reminders"],
              ["enablePaymentTracking", "Enable payment tracking"],
              ["autoCreatePaymentFollowUp", "Automatically create payment follow-up"],
              ["autoCreateInstallationTask", "Automatically create installation task"],
              ["autoCreateReviewTaskAfterClose", "Automatically create review task after close"],
              ["requireInstallationBeforeClose", "Require installation before project close"],
              ["requireSnagResolutionBeforeClose", "Require snags resolved before close"],
              ["requireFinalPaymentBeforeClose", "Require final payment before close"],
              ["requireHandoverChecklistForWon", "Require checklist before won reporting"],
            ].map(([key, label]) => (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  borderRadius: 16,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 13,
                  fontWeight: 800,
                  color: textColor,
                }}
              >
                <input
                  type="checkbox"
                  checked={crmSettingsDraft[key] !== false}
                  disabled={!canManageCrmSettings}
                  onChange={(event) =>
                    setCrmSettingsDraft((prev) =>
                      normalizeCrmSettings({ ...prev, [key]: event.target.checked })
                    )
                  }
                />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default quotation follow-up after X days
              <input
                type="number"
                min="0"
                value={crmSettingsDraft.defaultQuoteFollowUpDays}
                disabled={!canManageCrmSettings}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultQuoteFollowUpDays: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              New lead first contact target in minutes
              <input
                type="number"
                min="0"
                value={crmSettingsDraft.firstContactSlaMinutes}
                disabled={!canManageCrmSettings}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, firstContactSlaMinutes: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default task owner
              <select
                value={crmSettingsDraft.defaultTaskOwnerMode}
                disabled={!canManageCrmSettings}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultTaskOwnerMode: event.target.value })
                  )
                }
                style={input}
              >
                <option value="current_user">Current user</option>
                <option value="lead_owner">Lead owner</option>
                <option value="workspace_owner">Workspace owner</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default project owner
              <select
                value={crmSettingsDraft.defaultProjectOwnerMode}
                disabled={!canManageCrmSettings}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultProjectOwnerMode: event.target.value })
                  )
                }
                style={input}
              >
                <option value="current_user">Current user</option>
                <option value="lead_owner">Lead owner</option>
                <option value="workspace_owner">Workspace owner</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Handover task due after X days
              <input
                type="number"
                min="0"
                value={crmSettingsDraft.defaultHandoverDueDays}
                disabled={!canManageCrmSettings}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultHandoverDueDays: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default order task template
              <select
                value={crmSettingsDraft.defaultOrderTaskTemplate || STANDARD_ORDER_TASK_TEMPLATE_KEY}
                disabled={!canManageCrmSettings || crmSettingsDraft.enableOrderTaskTemplates === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultOrderTaskTemplate: event.target.value })
                  )
                }
                style={input}
              >
                <option value={STANDARD_ORDER_TASK_TEMPLATE_KEY}>Standard furnishing order</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default daily tracking time
              <input
                type="time"
                value={crmSettingsDraft.defaultTrackingReminderTime || "10:30"}
                disabled={!canManageCrmSettings || crmSettingsDraft.enableDeliveryTracking === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultTrackingReminderTime: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default tracking owner
              <select
                value={crmSettingsDraft.defaultTrackingOwnerMode || "project_owner"}
                disabled={!canManageCrmSettings || crmSettingsDraft.enableDeliveryTracking === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultTrackingOwnerMode: event.target.value })
                  )
                }
                style={input}
              >
                <option value="project_owner">Project owner</option>
                <option value="current_user">Current user</option>
                <option value="workspace_owner">Workspace owner</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Payment follow-up after X days
              <input
                type="number"
                min="0"
                value={crmSettingsDraft.defaultPaymentFollowUpDays}
                disabled={!canManageCrmSettings || crmSettingsDraft.enablePaymentTracking === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultPaymentFollowUpDays: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Review request after close in X days
              <input
                type="number"
                min="0"
                value={crmSettingsDraft.defaultReviewRequestDaysAfterClose}
                disabled={!canManageCrmSettings || crmSettingsDraft.autoCreateReviewTaskAfterClose === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultReviewRequestDaysAfterClose: event.target.value })
                  )
                }
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Default payment follow-up owner
              <select
                value={crmSettingsDraft.defaultPaymentOwnerMode || "project_owner"}
                disabled={!canManageCrmSettings || crmSettingsDraft.enablePaymentTracking === false}
                onChange={(event) =>
                  setCrmSettingsDraft((prev) =>
                    normalizeCrmSettings({ ...prev, defaultPaymentOwnerMode: event.target.value })
                  )
                }
                style={input}
              >
                <option value="project_owner">Project owner</option>
                <option value="current_user">Current user</option>
                <option value="workspace_owner">Workspace owner</option>
              </select>
            </label>
          </div>
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Sales Pipeline</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Customize the workspace sales stages shown on the CRM Pipeline Board. Stage IDs stay stable, so renaming labels will not move existing deals.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={addSalesPipelineStage}
                disabled={!canManageCrmSettings}
                style={{
                  ...btn(canManageCrmSettings ? "#0f172a" : "#94a3b8"),
                  marginRight: 0,
                  opacity: canManageCrmSettings ? 1 : 0.7,
                }}
              >
                Add Custom Stage
              </button>
              <button
                type="button"
                onClick={restoreDefaultSalesPipeline}
                disabled={!canManageCrmSettings}
                style={{
                  ...btn(canManageCrmSettings ? "#64748b" : "#94a3b8"),
                  marginRight: 0,
                  opacity: canManageCrmSettings ? 1 : 0.7,
                }}
              >
                Restore Default
              </button>
              <button
                type="button"
                onClick={saveSalesPipelineSettings}
                disabled={!canManageCrmSettings || isSavingSalesPipeline}
                style={{
                  ...btn(canManageCrmSettings ? "#16a34a" : "#94a3b8"),
                  marginRight: 0,
                  opacity: !canManageCrmSettings || isSavingSalesPipeline ? 0.72 : 1,
                }}
              >
                {isSavingSalesPipeline ? "Saving..." : "Save Sales Pipeline"}
              </button>
            </div>
          </div>

          {salesPipelineLoadState.error ? (
            <div style={{ padding: 12, borderRadius: 14, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {salesPipelineLoadState.error}
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 12 }}>
            {normalizedSalesPipeline.stages.map((stage, index) => {
              const hasActiveDeals = salesPipelineStageHasActiveDeals(stage.stageId);
              return (
                <div
                  key={stage.stageId}
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact
                        ? "1fr"
                        : "54px minmax(180px, 1fr) 76px minmax(150px, 0.7fr) auto",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 900, color: dashboardMuted }}>
                      #{index + 1}
                    </div>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                      Stage label
                      <input
                        value={stage.label || ""}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateSalesPipelineStageDraft(stage.stageId, {
                            label: event.target.value,
                          })
                        }
                        style={input}
                        placeholder="Stage label"
                      />
                    </label>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                      Color
                      <input
                        type="color"
                        value={stage.color || "#64748b"}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateSalesPipelineStageDraft(stage.stageId, {
                            color: event.target.value,
                          })
                        }
                        style={{
                          width: 54,
                          height: 40,
                          padding: 4,
                          borderRadius: 12,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "transparent",
                        }}
                      />
                    </label>
                    <label
                      title={hasActiveDeals && stage.active !== false ? "This stage has active deals. Move them before deactivating." : undefined}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 12,
                        fontWeight: 800,
                        color: textColor,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={stage.active !== false}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateSalesPipelineStageDraft(stage.stageId, {
                            active: event.target.checked,
                          })
                        }
                      />
                      Active
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: isCompact ? "flex-start" : "flex-end" }}>
                      <button
                        type="button"
                        onClick={() => moveSalesPipelineStage(stage.stageId, -1)}
                        disabled={!canManageCrmSettings || index === 0}
                        style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px", opacity: !canManageCrmSettings || index === 0 ? 0.65 : 1 }}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSalesPipelineStage(stage.stageId, 1)}
                        disabled={!canManageCrmSettings || index === normalizedSalesPipeline.stages.length - 1}
                        style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px", opacity: !canManageCrmSettings || index === normalizedSalesPipeline.stages.length - 1 ? 0.65 : 1 }}
                      >
                        Down
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 11, color: dashboardMuted }}>
                    <span style={{ padding: "4px 8px", borderRadius: 999, background: `${stage.color || "#64748b"}18`, color: stage.color || "#64748b", fontWeight: 900 }}>
                      {stage.stageId}
                    </span>
                    {stage.systemStage ? <span>Default stage</span> : <span>Custom stage</span>}
                    {hasActiveDeals ? <span>Active deals in this stage</span> : null}
                    {stage.isWon ? <span>Counts as won</span> : null}
                    {stage.isLost ? <span>Counts as lost</span> : null}
                    {stage.isNurture ? <span>Counts as nurture</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Orders Pipeline</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Customize execution order stages shown on the Orders board. Stage IDs stay stable, so renaming labels will not move existing projects.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={addOrderPipelineStage}
                disabled={!canManageCrmSettings}
                style={{
                  ...btn(canManageCrmSettings ? "#0f172a" : "#94a3b8"),
                  marginRight: 0,
                  opacity: canManageCrmSettings ? 1 : 0.7,
                }}
              >
                Add Custom Stage
              </button>
              <button
                type="button"
                onClick={restoreDefaultOrderPipeline}
                disabled={!canManageCrmSettings}
                style={{
                  ...btn(canManageCrmSettings ? "#64748b" : "#94a3b8"),
                  marginRight: 0,
                  opacity: canManageCrmSettings ? 1 : 0.7,
                }}
              >
                Restore Default
              </button>
              <button
                type="button"
                onClick={saveOrderPipelineSettings}
                disabled={!canManageCrmSettings || isSavingOrderPipeline}
                style={{
                  ...btn(canManageCrmSettings ? "#16a34a" : "#94a3b8"),
                  marginRight: 0,
                  opacity: !canManageCrmSettings || isSavingOrderPipeline ? 0.72 : 1,
                }}
              >
                {isSavingOrderPipeline ? "Saving..." : "Save Orders Pipeline"}
              </button>
            </div>
          </div>

          {orderPipelineLoadState.error ? (
            <div style={{ padding: 12, borderRadius: 14, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {orderPipelineLoadState.error}
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 12 }}>
            {normalizedOrderPipeline.stages.map((stage, index) => {
              const hasActiveProjects = orderPipelineStageHasActiveProjects(stage.stageId);
              return (
                <div
                  key={stage.stageId}
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact
                        ? "1fr"
                        : "54px minmax(180px, 1fr) 76px minmax(150px, 0.7fr) auto",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 900, color: dashboardMuted }}>
                      #{index + 1}
                    </div>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                      Stage label
                      <input
                        value={stage.label || ""}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateOrderPipelineStageDraft(stage.stageId, {
                            label: event.target.value,
                          })
                        }
                        style={input}
                        placeholder="Stage label"
                      />
                    </label>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                      Color
                      <input
                        type="color"
                        value={stage.color || "#64748b"}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateOrderPipelineStageDraft(stage.stageId, {
                            color: event.target.value,
                          })
                        }
                        style={{
                          width: 54,
                          height: 40,
                          padding: 4,
                          borderRadius: 12,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "transparent",
                        }}
                      />
                    </label>
                    <label
                      title={hasActiveProjects && stage.active !== false ? "This stage has active orders. Move them before deactivating." : undefined}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 12,
                        fontWeight: 800,
                        color: textColor,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={stage.active !== false}
                        disabled={!canManageCrmSettings}
                        onChange={(event) =>
                          updateOrderPipelineStageDraft(stage.stageId, {
                            active: event.target.checked,
                          })
                        }
                      />
                      Active
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: isCompact ? "flex-start" : "flex-end" }}>
                      <button
                        type="button"
                        onClick={() => moveOrderPipelineStage(stage.stageId, -1)}
                        disabled={!canManageCrmSettings || index === 0}
                        style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px", opacity: !canManageCrmSettings || index === 0 ? 0.65 : 1 }}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        onClick={() => moveOrderPipelineStage(stage.stageId, 1)}
                        disabled={!canManageCrmSettings || index === normalizedOrderPipeline.stages.length - 1}
                        style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px", opacity: !canManageCrmSettings || index === normalizedOrderPipeline.stages.length - 1 ? 0.65 : 1 }}
                      >
                        Down
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 11, color: dashboardMuted }}>
                    <span style={{ padding: "4px 8px", borderRadius: 999, background: `${stage.color || "#64748b"}18`, color: stage.color || "#64748b", fontWeight: 900 }}>
                      {stage.stageId}
                    </span>
                    {stage.systemStage ? <span>Default stage</span> : <span>Custom stage</span>}
                    {hasActiveProjects ? <span>Active orders in this stage</span> : null}
                    {stage.isClosed ? <span>Closed stage</span> : null}
                    {stage.isCancelled ? <span>Cancelled stage</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                {getQuoteTypeLabel(activeCrmPipelineType)} Pipeline Settings
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Stage names, confirmation rules, completion flags, and default checklists are fully customizable.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => addPipelineStage(activeCrmPipelineType)}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Add Stage
              </button>
              <button
                type="button"
                onClick={saveCrmPipelinesSettings}
                style={{ ...btn("#16a34a"), marginRight: 0 }}
              >
                Save Pipeline Settings
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(260px, 1fr) auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Pipeline Label
              </div>
              <input
                value={pipeline.label || ""}
                onChange={(e) =>
                  setCrmPipelines((prev) => ({
                    ...normalizeCrmPipelines(prev),
                    [activeCrmPipelineType]: {
                      ...getCrmPipeline(prev, activeCrmPipelineType),
                      label: e.target.value,
                    },
                  }))
                }
                style={input}
                placeholder="Pipeline name"
              />
            </div>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 14,
                background: hasConfirmationStage ? "#dcfce7" : "#fef3c7",
                color: hasConfirmationStage ? "#166534" : "#92400e",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {hasConfirmationStage
                ? "A stage is set to confirm the quote."
                : "Choose a confirmation stage to mark quotes as confirmed orders."}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 14 }}>
          {pipeline.stages.map((stage, index) => (
            <div
              key={stage.id}
              style={{
                ...dashboardCardStyle,
                display: "grid",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GripVertical size={16} color={dashboardMuted} />
                  </div>
                  <div style={{ minWidth: 90, fontSize: 13, fontWeight: 800 }}>
                    Stage {index + 1}
                  </div>
                  <input
                    value={stage.label}
                    onChange={(e) =>
                      updatePipelineStage(activeCrmPipelineType, stage.id, {
                        label: e.target.value,
                      })
                    }
                    style={{ ...input, flex: 1 }}
                    placeholder="Stage label"
                  />
                  <input
                    type="color"
                    value={stage.color}
                    onChange={(e) =>
                      updatePipelineStage(activeCrmPipelineType, stage.id, {
                        color: e.target.value,
                      })
                    }
                    style={{
                      width: 54,
                      height: 40,
                      padding: 4,
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.35)",
                      background: "transparent",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => movePipelineStage(activeCrmPipelineType, stage.id, -1)}
                    style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => movePipelineStage(activeCrmPipelineType, stage.id, 1)}
                    style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePipelineStage(activeCrmPipelineType, stage.id)}
                    style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  { key: "confirmsQuote", label: "Marks quote as confirmed order" },
                  { key: "marksComplete", label: "Marks the deal as complete" },
                  { key: "isCancelled", label: "Marks the deal as cancelled" },
                ].map((toggle) => (
                  <label
                    key={toggle.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 14,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={stage[toggle.key] === true}
                      onChange={(e) =>
                        updatePipelineStage(activeCrmPipelineType, stage.id, {
                          [toggle.key]: e.target.checked,
                        })
                      }
                    />
                    {toggle.label}
                  </label>
                ))}
              </div>

              <div
                style={{
                  padding: 16,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>Default Checklist</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      These tasks are copied into each deal that enters this stage.
                    </div>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => addPipelineChecklistItem(activeCrmPipelineType, stage.id)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      Add Checklist Item
                    </button>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {(stage.defaultChecklist || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      No default checklist items for this stage yet.
                    </div>
                  ) : (
                    (stage.defaultChecklist || []).map((item) =>
                      renderPipelineChecklistNode(stage, item, 1)
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {renderCrmDataRepairPanel()}

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Execution Templates</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Execution templates are now managed from their own dedicated workspace so teams can create, edit, search,
                and reuse them without crowding the CRM settings screen.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setRoutedTab("executionTemplates")}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              Open Execution Templates
            </button>
          </div>

          <div
            style={{
              padding: 18,
              borderRadius: 18,
              border: dashboardBorder,
              background: dashboardSoftBg,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 800 }}>
              Dedicated template manager
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              Create template names, normalized item types, and default field/value pairs from the dedicated
              <strong style={{ color: textColor }}> Execution Templates</strong> page. Those templates then appear in the
              Execution Details workspace and auto-populate item fields the moment a matching template is selected or the
              item type matches automatically.
            </div>
            <div
              style={{
                fontSize: 12,
                color: dashboardMuted,
                padding: "10px 12px",
                borderRadius: 14,
                background: isDark ? "rgba(37,99,235,0.08)" : "#eff6ff",
                border: "1px solid rgba(37,99,235,0.18)",
              }}
            >
              Current templates available: {executionTemplateCatalog.length}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmLeadModal = () => {
    if (!isCrmLeadModalOpen || !crmLeadDraft) return null;

    const stageOptions = getStageSelectOptions(
      crmPipelines,
      crmLeadDraft.quoteType,
      crmLeadDraft.stageId
    );
    const leadCustomerMatch = findCustomerLedgerMatch(
      getCustomerIdentityFromLeadDraft(crmLeadDraft)
    );

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 120,
          background: "rgba(15,23,42,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onClick={closeNewCrmLeadModal}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onBlurCapture={() => persistCrmLeadDraftLocally("blur")}
          style={{
            width: "100%",
            maxWidth: 880,
            maxHeight: "90vh",
            overflowY: "auto",
            ...dashboardCardStyle,
            display: "grid",
            gap: 18,
            padding: isCompact ? 18 : 24,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                CRM
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                New Lead
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8 }}>
                Create a customer or project in CRM first, then create a curtain or general quotation only when the lead is ready.
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 8 }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.CRM_LEAD)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={closeNewCrmLeadModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNewCrmLead}
                disabled={isSavingCrmLead}
                style={{ ...btn("#2563eb"), marginRight: 0, opacity: isSavingCrmLead ? 0.7 : 1 }}
              >
                {isSavingCrmLead ? "Saving..." : "Create Lead"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 16,
            }}
          >
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Customer / Contact</div>
                {leadCustomerMatch.customer ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
                      border: "1px solid rgba(37,99,235,0.20)",
                      display: "grid",
                      gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb" }}>
                      Existing Customer Found
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>
                      {leadCustomerMatch.customer.customerName}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted }}>
                      {[leadCustomerMatch.customer.phone, leadCustomerMatch.customer.address]
                        .filter(Boolean)
                        .join(" | ") || "Linked by customer history"}
                    </div>
                  </div>
                ) : leadCustomerMatch.possibleMatches?.length ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: "#fef3c7",
                      border: "1px solid rgba(245,158,11,0.30)",
                      color: "#92400e",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Possible repeat customer found. Check Customer Ledger before creating a separate record.
                  </div>
                ) : null}
                <input
                  value={crmLeadDraft.contact?.name || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, name: e.target.value },
                    }))
                  }
                  style={input}
                  placeholder="Contact Name"
                />
                <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <input
                    value={crmLeadDraft.contact?.phone || ""}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, phone: e.target.value },
                      }))
                    }
                    style={input}
                    placeholder="Phone"
                  />
                  <input
                    value={crmLeadDraft.contact?.whatsapp || ""}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, whatsapp: e.target.value },
                      }))
                    }
                    style={input}
                    placeholder="WhatsApp"
                  />
                </div>
                <input
                  value={crmLeadDraft.contact?.email || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value },
                    }))
                  }
                  style={input}
                  placeholder="Email"
                />
                <textarea
                  value={crmLeadDraft.contact?.address || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, address: e.target.value },
                    }))
                  }
                  style={{ ...input, minHeight: 84, resize: "vertical" }}
                  placeholder="Address"
                />
              </div>

              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Deal / Project</div>
                <input
                  value={crmLeadDraft.title || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Project / Deal Title"
                />
                <input
                  value={crmLeadDraft.companyOrProject || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      companyOrProject: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Company / Project Name"
                />
                <input
                  value={crmLeadDraft.source || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      source: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Source"
                />
                <input
                  value={crmLeadDraft.tags || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      tags: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Tags (comma separated)"
                />
                <textarea
                  value={crmLeadDraft.notes || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  style={{ ...input, minHeight: 110, resize: "vertical" }}
                  placeholder="Notes"
                />
              </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>CRM Routing</div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Quote Type</div>
                  <select
                    value={crmLeadDraft.quoteType}
                    onChange={(e) => {
                      const nextType =
                        e.target.value === GENERAL_QUOTE_TYPE
                          ? GENERAL_QUOTE_TYPE
                          : CURTAIN_QUOTE_TYPE;
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        quoteType: nextType,
                        pipelineType: nextType,
                        stageId: getDefaultCrmStageId(crmPipelines, nextType),
                      }));
                    }}
                    style={selectInput}
                  >
                    {canAccessCurtainQuotes ? (
                      <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
                    ) : null}
                    {canAccessGeneralQuotes ? (
                      <option value={GENERAL_QUOTE_TYPE}>General Quotation</option>
                    ) : null}
                  </select>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Pipeline</div>
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: dashboardBorder,
                      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {getCrmPipeline(crmPipelines, crmLeadDraft.quoteType).label}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Initial Stage</div>
                  <select
                    value={crmLeadDraft.stageId}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        stageId: e.target.value,
                      }))
                    }
                    style={selectInput}
                  >
                    {stageOptions.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Business Details</div>
                <input
                  type="number"
                  value={crmLeadDraft.expectedValue || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      expectedValue: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Expected Value"
                />
                <input
                  value={crmLeadDraft.nextAction || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      nextAction: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Next Action"
                />
                <input
                  type="date"
                  value={crmLeadDraft.nextActionDate || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      nextActionDate: e.target.value,
                    }))
                  }
                  style={input}
                />
                <select
                  value={crmLeadDraft.assignedToUserId || ""}
                  onChange={(e) => {
                    if (!canAssignLeadOwnerAccess) {
                      notify("Only managers/admins can assign owners.", "warning");
                      return;
                    }
                    const member = getTeamMemberById(e.target.value);
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      assignedToUserId: e.target.value,
                      assignedToNameSnapshot: member?.name || "",
                    }));
                  }}
                  disabled={!canAssignLeadOwnerAccess}
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(crmLeadDraft.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id} disabled={Boolean(member.exclusionReason)}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                      {member.exclusionReason ? " | Needs reassignment" : ""}
                    </option>
                  ))}
                </select>
                {renderCrmMemberRosterStatus()}
                {!canAssignLeadOwnerAccess ? (
                  <div style={{ fontSize: 11, color: dashboardMuted }}>
                    Only managers/admins can assign owners.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDraftRecoveryModal = () => {
    if (!draftRecoveryPrompt) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 140,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            ...dashboardCardStyle,
            display: "grid",
            gap: 16,
            padding: isCompact ? 18 : 24,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 8,
              }}
            >
              Unsaved work found
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>
              {draftRecoveryPrompt.title}
            </div>
            <div
              style={{
                fontSize: 13,
                color: dashboardMuted,
                marginTop: 10,
                lineHeight: 1.7,
              }}
            >
              {draftRecoveryPrompt.description}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 18,
              background: dashboardSoftBg,
              border: dashboardBorder,
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Draft saved
              {draftRecoveryPrompt.savedAt
                ? ` ${new Date(draftRecoveryPrompt.savedAt).toLocaleString("en-IN")}`
                : " recently"}
            </div>
            {draftRecoveryPrompt.serverIsNewer ? (
              <div
                style={{
                  fontSize: 12,
                  color: isDark ? "#fde68a" : "#92400e",
                  padding: "10px 12px",
                  borderRadius: 14,
                  background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
                  border: "1px solid rgba(245,158,11,0.18)",
                }}
              >
                {draftRecoveryPrompt.serverNewerDescription}
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={draftRecoveryPrompt.onDiscard}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              {draftRecoveryPrompt.discardLabel || "Discard"}
            </button>
            <button
              type="button"
              onClick={draftRecoveryPrompt.onRestore}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              {draftRecoveryPrompt.restoreLabel || "Restore"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFabricPhotoQrModal = () => {
    if (!fabricPhotoQrModal.open) return null;

    const minutes = Math.floor(fabricPhotoQrCountdownMs / 60000);
    const seconds = Math.floor((fabricPhotoQrCountdownMs % 60000) / 1000);
    const countdownLabel =
      fabricPhotoQrCountdownMs > 0
        ? `${minutes}:${String(seconds).padStart(2, "0")}`
        : fabricPhotoQrModal.expiresAt
        ? "Expired"
        : "";
    const isReady = fabricPhotoQrModal.status === "pending" && fabricPhotoQrModal.uploadUrl;
    const qrRenderSize = isCompact ? 280 : 320;
    const qrRenderMargin = 4;
    const qrErrorCorrectionLevel = "H";
    const statusCopy =
      fabricPhotoQrModal.status === "uploaded"
        ? "Photo received. The Fabric Summary has been updated."
        : fabricPhotoQrModal.status === "expired"
        ? "This phone upload link has expired. Create a new QR code when needed."
        : fabricPhotoQrModal.status === "failed"
        ? fabricPhotoQrModal.error || "Could not create this phone upload link."
        : "Scan this QR code with your phone to take or upload a fabric photo.";

    const copyUploadLink = async () => {
      if (!fabricPhotoQrModal.uploadUrl) return;
      try {
        await navigator.clipboard.writeText(fabricPhotoQrModal.uploadUrl);
        notify("Phone upload link copied.", "success");
      } catch (error) {
        if (IS_DEV_UI_ENABLED) {
          devWarn("FABRIC_PHOTO_QR_COPY_LINK_FAILED", {
            message: error?.message,
            sessionId: fabricPhotoQrModal.sessionId,
          });
        }
        notify("Could not copy link. Please scan the QR code instead.", "warning");
      }
    };
    const openUploadLink = () => {
      if (!fabricPhotoQrModal.uploadUrl || !isReady) return;
      if (IS_DEV_UI_ENABLED) {
        devLog("FABRIC_QR_OPEN_UPLOAD_LINK", {
          uploadUrl: fabricPhotoQrModal.uploadUrl,
          sessionId: fabricPhotoQrModal.sessionId,
          origin:
            typeof window !== "undefined" && window.location?.origin
              ? window.location.origin
              : "",
        });
      }
      window.open(fabricPhotoQrModal.uploadUrl, "_blank", "noopener,noreferrer");
    };
    const sendUploadLinkToWhatsApp = () => {
      if (!fabricPhotoQrModal.uploadUrl || !isReady) return;
      const message = `FabriQuote fabric photo upload link:\n${fabricPhotoQrModal.uploadUrl}`;
      if (IS_DEV_UI_ENABLED) {
        devLog("FABRIC_QR_SEND_LINK_TO_WHATSAPP", {
          uploadUrl: fabricPhotoQrModal.uploadUrl,
          sessionId: fabricPhotoQrModal.sessionId,
          shortCode: fabricPhotoQrModal.shortCode || "",
        });
      }
      window.open(
        `https://wa.me/?text=${encodeURIComponent(message)}`,
        "_blank",
        "noopener,noreferrer"
      );
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 146,
          background: "rgba(15,23,42,0.58)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 26px 72px rgba(2,6,23,0.7)"
              : "0 26px 72px rgba(15,23,42,0.2)",
            padding: isCompact ? 20 : 28,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: isDark ? "#93c5fd" : "#2563eb",
                }}
              >
                Upload from Phone
              </div>
              <div style={{ marginTop: 5, fontSize: 24, fontWeight: 950 }}>
                Fabric Photo QR
              </div>
              <div
                style={{
                  marginTop: 5,
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.55,
                }}
              >
                {fabricPhotoQrModal.fabricDisplayName || "Selected fabric"}
              </div>
            </div>
            <button
              type="button"
              onClick={closeFabricPhotoQrModal}
              aria-label="Close phone upload QR"
              title="Close"
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: dashboardBorder,
                background: isDark ? "rgba(15,23,42,0.72)" : "#f8fafc",
                color: isDark ? "#e2e8f0" : "#334155",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={18} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "360px 1fr",
              gap: 18,
              alignItems: "center",
            }}
          >
            <div
              style={{
                borderRadius: 24,
                padding: 14,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                display: "grid",
                placeItems: "center",
              }}
            >
              {fabricPhotoQrModal.loading ? (
                <div
                  style={{
                    width: qrRenderSize,
                    height: qrRenderSize,
                    borderRadius: 18,
                    background: "#f1f5f9",
                    display: "grid",
                    placeItems: "center",
                    color: "#475569",
                    fontSize: 13,
                    fontWeight: 900,
                  }}
                >
                  Creating QR...
                </div>
              ) : isReady ? (
                <QrCodeSvg
                  value={fabricPhotoQrModal.uploadUrl}
                  size={qrRenderSize}
                  margin={qrRenderMargin}
                  errorCorrectionLevel={qrErrorCorrectionLevel}
                  foreground="#000000"
                  background="#ffffff"
                  label="Phone fabric photo upload QR code"
                />
              ) : (
                <div
                  role="alert"
                  style={{
                    width: qrRenderSize,
                    minHeight: qrRenderSize,
                    borderRadius: 18,
                    background:
                      fabricPhotoQrModal.status === "uploaded" ? "#ecfdf5" : "#fef2f2",
                    color:
                      fabricPhotoQrModal.status === "uploaded" ? "#047857" : "#991b1b",
                    display: "grid",
                    placeItems: "center",
                    textAlign: "center",
                    padding: 16,
                    fontSize: 13,
                    fontWeight: 900,
                    lineHeight: 1.5,
                  }}
                >
                  {fabricPhotoQrModal.status === "uploaded"
                    ? "Upload complete"
                    : fabricPhotoQrModal.error || "QR link unavailable"}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: isDark ? "rgba(15,23,42,0.48)" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.22)"
                    : "1px solid #e2e8f0",
                  color:
                    fabricPhotoQrModal.status === "failed" ||
                    fabricPhotoQrModal.status === "expired"
                      ? "#dc2626"
                      : isDark
                      ? "#e2e8f0"
                      : "#334155",
                  fontSize: 13,
                  fontWeight: 800,
                  lineHeight: 1.65,
                }}
              >
                {statusCopy}
              </div>

              {countdownLabel ? (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: dashboardMuted,
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  <Clock3 size={15} />
                  Expires in {countdownLabel}
                </div>
              ) : null}

              {isReady ? (
                <div
                  style={{
                    padding: 10,
                    borderRadius: 14,
                    background: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
                    border: isDark
                      ? "1px solid rgba(96,165,250,0.24)"
                      : "1px solid #bfdbfe",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    fontSize: 11,
                    fontWeight: 800,
                    lineHeight: 1.55,
                    wordBreak: "break-all",
                  }}
                >
                  <span style={{ fontWeight: 950 }}>Actual upload URL: </span>
                  {fabricPhotoQrModal.uploadUrl}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={copyUploadLink}
                  disabled={!isReady}
                  style={{
                    ...btn(isReady ? "#2563eb" : "#94a3b8"),
                    marginRight: 0,
                    opacity: isReady ? 1 : 0.72,
                    cursor: isReady ? "pointer" : "not-allowed",
                  }}
                >
                  <Copy size={16} style={{ marginRight: 8 }} />
                  Copy Link
                </button>
                <button
                  type="button"
                  onClick={openUploadLink}
                  disabled={!isReady}
                  style={{
                    ...btn(isReady ? "#0f172a" : "#94a3b8"),
                    marginRight: 0,
                    opacity: isReady ? 1 : 0.72,
                    cursor: isReady ? "pointer" : "not-allowed",
                  }}
                >
                  <ExternalLink size={16} style={{ marginRight: 8 }} />
                  Open Upload Link
                </button>
                <button
                  type="button"
                  onClick={sendUploadLinkToWhatsApp}
                  disabled={!isReady}
                  style={{
                    ...btn(isReady ? "#16a34a" : "#94a3b8"),
                    marginRight: 0,
                    opacity: isReady ? 1 : 0.72,
                    cursor: isReady ? "pointer" : "not-allowed",
                  }}
                >
                  <Phone size={16} style={{ marginRight: 8 }} />
                  Send Link to WhatsApp
                </button>
              </div>

              {IS_DEV_UI_ENABLED ? (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: isDark ? "rgba(15,23,42,0.72)" : "#f1f5f9",
                    color: dashboardMuted,
                    fontSize: 11,
                    fontWeight: 800,
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                  }}
                >
                  <div>sessionId: {fabricPhotoQrModal.sessionId || "-"}</div>
                  <div>shortCode: {fabricPhotoQrModal.shortCode || "-"}</div>
                  <div>uploadUrl: {fabricPhotoQrModal.uploadUrl || "-"}</div>
                  <div>legacyUploadUrl: {fabricPhotoQrModal.legacyUploadUrl || "-"}</div>
                  <div>qrSize: {qrRenderSize}px</div>
                  <div>qrMargin: {qrRenderMargin}</div>
                  <div>errorCorrectionLevel: {qrErrorCorrectionLevel}</div>
                  <div>expiresAt: {fabricPhotoQrModal.expiresAt || "-"}</div>
                  <div>status: {fabricPhotoQrModal.status || "-"}</div>
                  <div>
                    storagePath:{" "}
                    {fabricPhotoQrModal.storagePathPrefix
                      ? `${fabricPhotoQrModal.storagePathPrefix}...`
                      : "generated after upload"}
                  </div>
                  <div>lastError: {fabricPhotoQrModal.error || "-"}</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmDialog) return null;

    const toneColor =
      confirmDialog.tone === "danger"
        ? "#dc2626"
        : confirmDialog.tone === "warning"
        ? "#f59e0b"
        : "#2563eb";

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 145,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(520px, 100%)",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: toneColor,
              }}
            >
              Please Confirm
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>{confirmDialog.title}</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              {confirmDialog.description}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={async () => {
                const action = confirmDialog.onCancel;
                setConfirmDialog(null);
                if (typeof action === "function") {
                  await action();
                }
              }}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              {confirmDialog.cancelLabel || "Cancel"}
            </button>
            {(confirmDialog.extraActions || []).map((action, index) => (
              <button
                key={`${action.label || "action"}-${index}`}
                type="button"
                onClick={async () => {
                  setConfirmDialog(null);
                  if (typeof action.onClick === "function") {
                    await action.onClick();
                  }
                }}
                style={{
                  ...btn(action.tone === "danger" ? "#dc2626" : "#475569"),
                  marginRight: 0,
                }}
              >
                {action.label || "Action"}
              </button>
            ))}
            {confirmDialog.secondaryLabel ? (
              <button
                type="button"
                onClick={async () => {
                  const action = confirmDialog.onSecondary;
                  setConfirmDialog(null);
                  if (typeof action === "function") {
                    await action();
                  }
                }}
                style={{
                  ...btn(isDark ? "#334155" : "#64748b"),
                  marginRight: 0,
                }}
              >
                {confirmDialog.secondaryLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={async () => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(null);
                if (typeof action === "function") {
                  await action();
                }
              }}
              style={{
                ...btn(confirmDialog.tone === "danger" ? "#dc2626" : "#2563eb"),
                marginRight: 0,
              }}
            >
              {confirmDialog.confirmLabel || "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboardDrilldownModal = () => {
    if (!dashboardDrilldownModal) return null;

    const drilldown = getDashboardMetricRecords(
      dashboardDrilldownModal.key,
      dashboardDrilldownModal.context || {}
    );
    const records = Array.isArray(drilldown.records) ? drilldown.records : [];
    const query = normalizeSearchText(dashboardDrilldownSearch);
    const filteredRecords = records.filter((record) => {
      if (!query) return true;
      if (drilldown.recordType === "deals") {
        return doesSearchBlobMatch(buildDealSearchBlob(record), query);
      }
      if (drilldown.recordType === "customers") {
        return buildCustomerLedgerSearchText(record).includes(query);
      }
      return doesSearchBlobMatch(
        [
          record.customerLabel,
          record.customerName,
          record.quoteNo,
          record.status,
          record.workStageResolved,
          record.source,
          getQuoteTypeLabel(resolveQuoteType(record)),
          formatCurrencyDisplay(record.totalValue || record.expectedValue || 0, currency),
        ].join(" "),
        query
      );
    });

    const closeModal = () => {
      setDashboardDrilldownModal(null);
      setDashboardDrilldownSearch("");
    };
    const openQuoteRecord = (quote) => {
      if (!quote?.id) return;
      closeModal();
      loadFullQuote(quote);
    };
    const openDealRecord = (deal) => {
      if (!deal?.id) return;
      closeModal();
      setCrmView("list");
      setRoutedTab("crm");
      openCrmDealDetails(deal, { crmView: "list" });
    };
    const openQuoteDeal = (quote) => {
      const linkedLead =
        findLinkedCrmLeadForQuote(
          quote,
          quote?.crm?.leadId || quote?.linkedLeadId || ""
        ) || null;
      if (linkedLead?.id) {
        openDealRecord(linkedLead);
      } else {
        notify("This quote is not linked to a CRM deal yet.", "info");
      }
    };
    const openRecordCustomer = (record = {}) => {
      closeModal();
      openDashboardCustomer({
        customerId: record.customerId || record.crm?.customerId || record.customer?.id,
        name: record.customerLabel || record.customerName || record.crmContact?.name,
        phone: record.customerPhone || record.crm?.customerPhone || record.crmContact?.phone,
      });
    };
    const renderQuoteLikeRecord = (record, index, isInstallation = false) => (
      <div
        key={`${record.id || record.quoteNo || index}-${isInstallation ? "install" : "quote"}`}
        style={{
          padding: 14,
          borderRadius: 18,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 10,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, overflowWrap: "anywhere" }}>
              {record.customerLabel || record.customerName || "Unnamed Customer"}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: dashboardMuted }}>
              #{record.quoteNo || "-"} |{" "}
              {getQuoteTypeLabel(resolveQuoteType(record))}
              {isInstallation && record.installationMillis
                ? ` | Install ${new Date(record.installationMillis).toLocaleDateString("en-IN")}`
                : ""}
            </div>
          </div>
          <div style={{ fontWeight: 950, whiteSpace: "nowrap" }}>
            {formatCurrencyDisplay(record.totalValue || 0, currency)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              padding: "4px 9px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 850,
              background:
                resolveQuoteType(record) === GENERAL_QUOTE_TYPE
                  ? "#dbeafe"
                  : "#ede9fe",
              color:
                resolveQuoteType(record) === GENERAL_QUOTE_TYPE
                  ? "#1d4ed8"
                  : "#7c3aed",
            }}
          >
            {getQuoteTypeLabel(resolveQuoteType(record))}
          </span>
          <span
            style={{
              padding: "4px 9px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 850,
              background: record.isConfirmed ? "#dcfce7" : "#fef3c7",
              color: record.isConfirmed ? "#166534" : "#92400e",
            }}
          >
            {record.isConfirmed ? "Confirmed" : record.status || "Draft"}
          </span>
          {record.workStageResolved ? (
            <span
              style={{
                padding: "4px 9px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 850,
                background: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0",
                color: isDark ? "#cbd5e1" : "#475569",
              }}
            >
              {getStageLabelForQuote(record)}
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => openQuoteRecord(record)}
            style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 11px" }}
          >
            Open Quote
          </button>
          <button
            type="button"
            onClick={() => openQuoteDeal(record)}
            style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 11px" }}
          >
            Open CRM Deal
          </button>
          <button
            type="button"
            onClick={() => openRecordCustomer(record)}
            style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 11px" }}
          >
            Open Customer
          </button>
        </div>
      </div>
    );
    const renderDealRecord = (deal, index) => {
      const primaryQuote = getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal));
      return (
        <div
          key={`${deal.id || index}-deal`}
          style={{
            padding: 14,
            borderRadius: 18,
            border: dashboardBorder,
            background: dashboardSoftBg,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, overflowWrap: "anywhere" }}>
                {deal.crmTitle || deal.customerLabel || deal.crmContact?.name || "Unnamed Deal"}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: dashboardMuted }}>
                {primaryQuote?.quoteNo ? `#${primaryQuote.quoteNo} | ` : ""}
                {deal.crmStageLabel || "CRM Deal"}
              </div>
            </div>
            <div style={{ fontWeight: 950, whiteSpace: "nowrap" }}>
              {formatCurrencyDisplay(deal.expectedValue || 0, currency)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => openDealRecord(deal)}
              style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 11px" }}
            >
              Details
            </button>
            {primaryQuote?.quoteId ? (
              <button
                type="button"
                onClick={() => {
                  closeModal();
                  loadFullQuote(primaryQuote.quoteId);
                }}
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 11px" }}
              >
                Open Quote
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => openRecordCustomer(deal)}
              style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 11px" }}
            >
              Open Customer
            </button>
          </div>
        </div>
      );
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 155,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(980px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflow: "hidden",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.72)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            display: "grid",
            gridTemplateRows: "auto auto minmax(0, 1fr)",
          }}
        >
          <div style={{ padding: isCompact ? 18 : 24, borderBottom: dashboardBorder }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#2563eb",
                  }}
                >
                  Dashboard Drill-down
                </div>
                <div style={{ marginTop: 6, fontSize: isCompact ? 22 : 28, fontWeight: 950 }}>
                  {drilldown.title}
                </div>
                {drilldown.subtitle ? (
                  <div style={{ marginTop: 6, fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                    {drilldown.subtitle}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  alignSelf: "start",
                }}
              >
                Close
              </button>
            </div>
          </div>
          <div
            style={{
              padding: isCompact ? 14 : 18,
              borderBottom: dashboardBorder,
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <input
              value={dashboardDrilldownSearch}
              onChange={(event) => setDashboardDrilldownSearch(event.target.value)}
              style={input}
              placeholder="Search within these records..."
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 850 }}>
                {filteredRecords.length} of {records.length} shown
              </span>
              {drilldown.summaryValue != null ? (
                <span style={{ fontSize: 12, color: textColor, fontWeight: 950 }}>
                  Total {formatCurrencyDisplay(drilldown.summaryValue || 0, currency)}
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ overflowY: "auto", padding: isCompact ? 14 : 18 }}>
            {drilldown.filters?.length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {drilldown.filters.map((filter) => (
                  <span
                    key={filter}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                      color: "#2563eb",
                      fontSize: 11,
                      fontWeight: 850,
                    }}
                  >
                    {filter}
                  </span>
                ))}
              </div>
            ) : null}
            {filteredRecords.length === 0 ? (
              <div
                style={{
                  padding: 28,
                  borderRadius: 20,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  textAlign: "center",
                  color: dashboardMuted,
                  fontWeight: 800,
                }}
              >
                No records found for this drill-down.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {filteredRecords.map((record, index) =>
                  drilldown.recordType === "deals"
                    ? renderDealRecord(record, index)
                    : renderQuoteLikeRecord(
                        record,
                        index,
                        drilldown.recordType === "installations"
                      )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmFollowUpModal = () => {
    if (!crmFollowUpModalState?.followUp) return null;

    const followUp = crmFollowUpModalState.followUp;
    const attachmentUploadKey = `${selectedCrmDeal?.id || ""}:${followUp.id}`;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 150,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(760px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
              }}
            >
              Follow-up
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>
              {crmFollowUpModalState.mode === "edit" ? "Edit Follow-up" : "Add Follow-up"}
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              Keep each task focused with its own due date, repeat cadence, notes, and attachments.
            </div>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>Title</div>
              <input
                value={followUp.title ?? ""}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({ title: event.target.value })
                }
                style={input}
                placeholder="Client callback, site visit, material approval..."
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Description
              </div>
              <textarea
                value={followUp.description ?? ""}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({ description: event.target.value })
                }
                style={{ ...input, minHeight: 110, resize: "vertical" }}
                placeholder="Add context, promised work, or what the team needs to do next."
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 12,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Due Date
                </div>
                <input
                  type="date"
                  value={followUp.dueDate ?? ""}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({
                      dueDate: event.target.value,
                      reminderEnabled: event.target.value
                        ? followUp.reminderEnabled !== false
                        : false,
                    })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Due Time
                </div>
                <input
                  type="time"
                  value={followUp.dueTime ?? DEFAULT_CRM_REMINDER_TIME}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({
                      dueTime: parseTimeInputValue(event.target.value),
                    })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Repeat
                </div>
                <select
                  value={followUp.repeat ?? "none"}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ repeat: event.target.value })
                  }
                  style={{ ...selectInput, width: "100%", minWidth: 0 }}
                >
                  {CRM_FOLLOW_UP_REPEAT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Priority
                </div>
                <select
                  value={followUp.priority ?? CRM_FOLLOW_UP_PRIORITY_MEDIUM}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ priority: event.target.value })
                  }
                  style={{ ...selectInput, width: "100%", minWidth: 0 }}
                >
                  {CRM_FOLLOW_UP_PRIORITY_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Order
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={followUp.order || ""}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ order: event.target.value })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                  placeholder="1"
                />
              </div>
            </div>

            <label
              style={{
                padding: 14,
                borderRadius: 18,
                background: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
                border: "1px solid rgba(37,99,235,0.18)",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={followUp.reminderEnabled === true}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({
                    reminderEnabled: event.target.checked,
                  })
                }
                style={{ marginTop: 3 }}
              />
              <span style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 900 }}>
                  Reminder Enabled
                </span>
                <span style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                  When enabled, this task can show in-app reminders until it is marked done.
                </span>
              </span>
            </label>

            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>Attachments</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                    Add photos, PDFs, or site notes directly to this follow-up.
                  </div>
                </div>
                <label
                  style={{
                    ...btn("#0f172a"),
                    marginRight: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <UploadCloud size={14} style={{ marginRight: 6 }} />
                  Add Attachment
                  <input
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []);
                      uploadCrmFollowUpAttachments(files);
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>

              {crmFollowUpAttachmentUploadKey === attachmentUploadKey ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>Uploading attachment...</div>
              ) : null}

              {normalizeCrmAttachments(followUp.attachments || []).length === 0 ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  No attachments added yet for this follow-up.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {normalizeCrmAttachments(followUp.attachments || []).map((attachment) => {
                    const isImage = String(attachment.contentType || "").startsWith("image/");
                    return (
                      <div
                        key={attachment.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          flexWrap: "wrap",
                          padding: 12,
                          borderRadius: 16,
                          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                          border: dashboardBorder,
                        }}
                      >
                        {isImage && attachment.url ? (
                          <img
                            src={attachment.url}
                            alt={attachment.name}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 14,
                              objectFit: "cover",
                              border: dashboardBorder,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 14,
                              display: "grid",
                              placeItems: "center",
                              background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                              border: dashboardBorder,
                            }}
                          >
                            <Paperclip size={16} />
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, wordBreak: "break-word" }}>
                            {attachment.name}
                          </div>
                          <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4 }}>
                            {attachment.uploadedAt
                              ? `Uploaded ${new Date(attachment.uploadedAt).toLocaleString("en-IN")}`
                              : "Uploaded"}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              ...btn("#2563eb"),
                              marginRight: 0,
                              textDecoration: "none",
                            }}
                          >
                            View
                          </a>
                          <a
                            href={attachment.url}
                            download={attachment.filename || attachment.name}
                            style={{
                              ...btn("#0f172a"),
                              marginRight: 0,
                              textDecoration: "none",
                            }}
                          >
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => removeCrmFollowUpAttachment(attachment.id)}
                            style={{ ...btn("#ef4444"), marginRight: 0 }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={closeCrmFollowUpModal}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCrmFollowUpModal}
              style={{ ...btn("#16a34a"), marginRight: 0 }}
            >
              Save Follow-up
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteDealChoiceModal = () => {
    if (!quoteDealChoiceState) return null;
    const recommendedDeal = quoteDealChoiceState.openDeals?.[0] || null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,.38)",
          backdropFilter: "blur(8px)",
          zIndex: 10040,
          display: "grid",
          placeItems: "center",
          padding: 18,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 28,
            background: isDark ? "#0f172a" : "#fff",
            border: dashboardBorder,
            boxShadow: "0 28px 80px rgba(15,23,42,.28)",
            padding: 24,
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", letterSpacing: ".08em", textTransform: "uppercase" }}>
            CRM deal already open
          </div>
          <h3 style={{ margin: "8px 0 8px", fontSize: 22, lineHeight: 1.15 }}>
            This customer already has an open deal. What do you want to do?
          </h3>
          <p style={{ margin: 0, color: dashboardMuted, lineHeight: 1.55 }}>
            {quoteDealChoiceState.customerName}
            {quoteDealChoiceState.quoteNo ? ` | Quote #${quoteDealChoiceState.quoteNo}` : ""}.
            Linking to the existing deal keeps the customer pipeline clean.
          </p>

          {recommendedDeal ? (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>{recommendedDeal.title || recommendedDeal.customerName || "Open CRM Deal"}</strong>
                <span style={{ color: "#2563eb", fontSize: 12, fontWeight: 900 }}>
                  Recommended
                </span>
              </div>
              <div style={{ color: dashboardMuted, fontSize: 12 }}>
                {getQuoteTypeLabel(recommendedDeal.pipelineType || recommendedDeal.quoteType)} | {recommendedDeal.stageNameSnapshot || recommendedDeal.stageId || "Open"} | {formatCurrencyDisplay(recommendedDeal.expectedValue || recommendedDeal.finalValue || 0, currency)}
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("linkExisting")}
              disabled={!recommendedDeal}
              style={{
                ...btn("#2563eb"),
                marginRight: 0,
                justifyContent: "center",
                opacity: recommendedDeal ? 1 : 0.55,
              }}
            >
              Link this quote to existing deal
            </button>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("create")}
              style={{
                ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
                justifyContent: "center",
              }}
            >
              Create separate new deal
            </button>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("saveOnly")}
              style={{
                ...btn("transparent", dashboardMuted),
                border: dashboardBorder,
                marginRight: 0,
                justifyContent: "center",
              }}
            >
              Save quote only
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteSentPromptModal = () => {
    if (!quoteSentPromptState.open) return null;

    const quoteContext = quoteSentPromptState.quoteContext || {};
    const ownerOptions = getCrmQuoteOwnerOptions();
    const quoteLabel = quoteContext.quoteNo || quoteContext.quoteId || "this quotation";

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 154,
          background: "rgba(15,23,42,0.58)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeQuoteSentPrompt();
        }}
      >
        <div
          style={{
            width: "min(620px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                }}
              >
                Customer PDF
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                Schedule a quotation follow-up?
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: dashboardMuted,
                  marginTop: 7,
                  lineHeight: 1.7,
                }}
              >
                Download {quoteLabel} and optionally schedule a follow-up.
                Downloading does not mark the quotation as sent or create a new deal.
              </div>
            </div>
            <button
              type="button"
              onClick={closeQuoteSentPrompt}
              disabled={quoteSentPromptState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: quoteSentPromptState.loading ? "not-allowed" : "pointer",
                opacity: quoteSentPromptState.loading ? 0.65 : 1,
              }}
              title="Close"
              aria-label="Close quote sent prompt"
            >
              <X size={16} />
            </button>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 18,
              background: dashboardSoftBg,
              border: dashboardBorder,
              display: "grid",
              gap: 6,
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 900, color: textColor }}>
              {quoteContext.customerName || "Customer"}
            </div>
            <div style={{ color: dashboardMuted }}>
              {[quoteContext.quoteNo ? `#${quoteContext.quoteNo}` : "", quoteContext.siteName]
                .filter(Boolean)
                .join(" | ") || "Quotation"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 14,
            }}
          >
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
              Follow-up date
              <input
                type="date"
                value={quoteSentPromptState.followUpDate}
                aria-label="Follow-up date"
                onChange={(event) => updateQuoteSentPromptField("followUpDate", event.target.value)}
                disabled={quoteSentPromptState.loading}
                style={{ ...input, marginTop: 0 }}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
              Follow-up owner
              <select
                value={quoteSentPromptState.ownerUid}
                aria-label="Follow-up owner"
                onChange={(event) => {
                  const owner = ownerOptions.find(
                    (option) => String(option.value || "") === String(event.target.value || "")
                  );
                  setQuoteSentPromptState((prev) => ({
                    ...prev,
                    ownerUid: event.target.value,
                    ownerName: owner?.label || "",
                    error: "",
                  }));
                }}
                disabled={quoteSentPromptState.loading}
                style={{ ...selectInput, marginTop: 0 }}
              >
                {ownerOptions.length === 0 ? (
                  <option value="">Workspace User</option>
                ) : null}
                {ownerOptions.map((option) => (
                  <option key={option.value || option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
            Note
            <textarea
              value={quoteSentPromptState.note}
              onChange={(event) => updateQuoteSentPromptField("note", event.target.value)}
              disabled={quoteSentPromptState.loading}
              style={{
                ...input,
                minHeight: 82,
                resize: "vertical",
                marginTop: 0,
              }}
              placeholder="Optional follow-up note"
            />
          </label>


          {quoteSentPromptState.error ? (
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(127,29,29,0.2)" : "#fef2f2",
                border: "1px solid rgba(239,68,68,0.22)",
                color: "#dc2626",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {quoteSentPromptState.error}
            </div>
          ) : null}

          {quoteSentPromptState.pdfGenerated && quoteContext.customerId ? (
            <button type="button" disabled={quoteSentPromptState.loading}
              onClick={() => { closeQuoteSentPrompt(); openCustomerLedger(quoteContext.customerId); }}
              style={{ ...btn("#334155"), marginRight: 0 }}>
              Open Customer
            </button>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={handleQuoteSentDownloadOnly}
              disabled={quoteSentPromptState.loading}
              style={{
                ...btn("#0f172a"),
                marginRight: 0,
                opacity: quoteSentPromptState.loading ? 0.72 : 1,
              }}
            >
              {quoteSentPromptState.loading ? "Working..." : quoteSentPromptState.pdfGenerated ? "Download PDF Again" : "Download PDF Only"}
            </button>
            <button
              type="button"
              onClick={handleQuoteSentCreateFollowUp}
              disabled={quoteSentPromptState.loading}
              style={{
                ...btn("#2563eb"),
                marginRight: 0,
                opacity: quoteSentPromptState.loading ? 0.72 : 1,
              }}
            >
              {quoteSentPromptState.loading ? "Working..." : quoteSentPromptState.pdfGenerated ? "Retry Follow-up" : "Download PDF + Schedule Follow-up"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmPaymentModal = () => {
    if (!crmPaymentModalState.open) return null;
    const draft = crmPaymentModalState.draft || {};
    const selectedProject =
      crmProjectRecords.find(
        (project) =>
          String(project.projectId || project.id || "") === String(crmPaymentModalState.projectId || "")
      ) || null;
    const ownerOptions = getCrmPipelineReviewOwnerOptions();

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 159,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !crmPaymentModalState.loading) {
            closeCrmPaymentModal();
          }
        }}
      >
        <div
          style={{
            width: "min(720px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#16a34a" }}>
                Payment
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                Record payment
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                {selectedProject?.projectTitle || selectedProject?.customerName || "Selected order"}
              </div>
            </div>
            <button
              type="button"
              onClick={closeCrmPaymentModal}
              disabled={crmPaymentModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: crmPaymentModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close payment modal"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Payment type
              <select value={draft.paymentType || "advance"} onChange={(event) => updateCrmPaymentDraft({ paymentType: event.target.value })} disabled={crmPaymentModalState.loading} style={selectInput}>
                {PAYMENT_TYPES.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Amount
              <input type="number" min="0" value={draft.amount || ""} onChange={(event) => updateCrmPaymentDraft({ amount: event.target.value })} disabled={crmPaymentModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Payment date
              <input type="date" value={draft.paymentDate || ""} onChange={(event) => updateCrmPaymentDraft({ paymentDate: event.target.value })} disabled={crmPaymentModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Payment mode
              <select value={draft.paymentMode || "cash"} onChange={(event) => updateCrmPaymentDraft({ paymentMode: event.target.value })} disabled={crmPaymentModalState.loading} style={selectInput}>
                {PAYMENT_MODES.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Reference number
              <input value={draft.referenceNo || ""} onChange={(event) => updateCrmPaymentDraft({ referenceNo: event.target.value })} disabled={crmPaymentModalState.loading} style={input} placeholder="UPI / cheque / bank ref" />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Collected by
              <select
                value={draft.collectedByUid || ""}
                onChange={(event) => {
                  const owner = ownerOptions.find(
                    (option) => String(option.uid || "") === String(event.target.value || "")
                  );
                  updateCrmPaymentDraft({
                    collectedByUid: event.target.value,
                    collectedByName: owner?.name || owner?.userName || owner?.label || "",
                  });
                }}
                disabled={crmPaymentModalState.loading}
                style={selectInput}
              >
                {ownerOptions.map((option) => (
                  <option key={option.uid} value={option.uid}>{option.name || option.userName || "Team member"}</option>
                ))}
              </select>
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Notes
            <textarea value={draft.notes || ""} onChange={(event) => updateCrmPaymentDraft({ notes: event.target.value })} disabled={crmPaymentModalState.loading} style={{ ...input, minHeight: 82, resize: "vertical" }} placeholder="Optional payment note" />
          </label>

          {crmPaymentModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmPaymentModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={closeCrmPaymentModal} disabled={crmPaymentModalState.loading} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>
              Cancel
            </button>
            <button type="button" onClick={saveCrmPayment} disabled={crmPaymentModalState.loading} style={{ ...btn("#16a34a"), marginRight: 0, opacity: crmPaymentModalState.loading ? 0.72 : 1 }}>
              {crmPaymentModalState.loading ? "Saving..." : "Save Payment"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentFollowUpModal = () => {
    if (!crmPaymentFollowUpModalState.open) return null;
    const draft = crmPaymentFollowUpModalState.draft || {};
    const selectedProject =
      crmProjectRecords.find(
        (project) =>
          String(project.projectId || project.id || "") === String(crmPaymentFollowUpModalState.projectId || "")
      ) || null;
    const ownerOptions = getCrmPipelineReviewOwnerOptions();

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 158,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !crmPaymentFollowUpModalState.loading) {
            closePaymentFollowUpModal();
          }
        }}
      >
        <div
          style={{
            width: "min(640px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                Payment follow-up
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                Create payment follow-up
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                {selectedProject?.projectTitle || selectedProject?.customerName || "Selected order"}
              </div>
            </div>
            <button
              type="button"
              onClick={closePaymentFollowUpModal}
              disabled={crmPaymentFollowUpModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: crmPaymentFollowUpModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close payment follow-up modal"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Due amount
              <input type="number" min="0" value={draft.dueAmount || ""} onChange={(event) => updatePaymentFollowUpDraft({ dueAmount: event.target.value })} disabled={crmPaymentFollowUpModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Follow-up date
              <input type="date" value={draft.dueDate || ""} onChange={(event) => updatePaymentFollowUpDraft({ dueDate: event.target.value })} disabled={crmPaymentFollowUpModalState.loading} style={input} />
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Owner
            <select
              value={draft.ownerUid || ""}
              onChange={(event) => {
                const owner = ownerOptions.find(
                  (option) => String(option.uid || "") === String(event.target.value || "")
                );
                updatePaymentFollowUpDraft({
                  ownerUid: event.target.value,
                  ownerName: owner?.name || owner?.userName || owner?.label || "",
                });
              }}
              disabled={crmPaymentFollowUpModalState.loading}
              style={selectInput}
            >
              {ownerOptions.map((option) => (
                <option key={option.uid} value={option.uid}>{option.name || option.userName || "Team member"}</option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Note
            <textarea value={draft.note || ""} onChange={(event) => updatePaymentFollowUpDraft({ note: event.target.value })} disabled={crmPaymentFollowUpModalState.loading} style={{ ...input, minHeight: 82, resize: "vertical" }} placeholder="What should the team follow up about?" />
          </label>

          {crmPaymentFollowUpModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmPaymentFollowUpModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={closePaymentFollowUpModal} disabled={crmPaymentFollowUpModalState.loading} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>
              Cancel
            </button>
            <button type="button" onClick={savePaymentFollowUp} disabled={crmPaymentFollowUpModalState.loading} style={{ ...btn("#2563eb"), marginRight: 0, opacity: crmPaymentFollowUpModalState.loading ? 0.72 : 1 }}>
              {crmPaymentFollowUpModalState.loading ? "Saving..." : "Create Follow-up"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmMaterialModal = () => {
    if (!crmMaterialModalState.open) return null;
    const draft = crmMaterialModalState.draft || {};
    const isEdit = Boolean(crmMaterialModalState.materialId);

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 157,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !crmMaterialModalState.loading) {
            closeCrmMaterialModal();
          }
        }}
      >
        <div
          style={{
            width: "min(860px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                Tracking
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                {isEdit ? "Edit tracking item" : "Add tracking item"}
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                Track order materials, supplier, courier/LR, expected arrival, and received status.
              </div>
            </div>
            <button
              type="button"
              onClick={closeCrmMaterialModal}
              disabled={crmMaterialModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: crmMaterialModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close tracking item modal"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Item type
              <select value={draft.itemType || draft.materialType || "fabric"} onChange={(event) => updateCrmMaterialDraft({ itemType: event.target.value, materialType: event.target.value })} disabled={crmMaterialModalState.loading} style={selectInput}>
                {TRACKING_ITEM_TYPES.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Status
              <select value={draft.status || "not_ordered"} onChange={(event) => updateCrmMaterialDraft({ status: event.target.value })} disabled={crmMaterialModalState.loading} style={selectInput}>
                {TRACKING_STATUSES.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Priority
              <select value={draft.priority || "normal"} onChange={(event) => updateCrmMaterialDraft({ priority: event.target.value })} disabled={crmMaterialModalState.loading} style={selectInput}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Item name
              <input value={draft.itemName || ""} onChange={(event) => updateCrmMaterialDraft({ itemName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} placeholder="Main curtain fabric, living room track..." />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Fabric / Code
              <input value={draft.fabricCode || ""} onChange={(event) => updateCrmMaterialDraft({ fabricCode: event.target.value })} disabled={crmMaterialModalState.loading} style={input} placeholder="Fabric code, color, or SKU" />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Brand
              <input value={draft.brandName || ""} onChange={(event) => updateCrmMaterialDraft({ brandName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Catalogue
              <input value={draft.catalogueName || ""} onChange={(event) => updateCrmMaterialDraft({ catalogueName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              SR number
              <input value={draft.srNumber || ""} onChange={(event) => updateCrmMaterialDraft({ srNumber: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Supplier name
              <input value={draft.supplierName || ""} onChange={(event) => updateCrmMaterialDraft({ supplierName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Supplier phone
              <input value={draft.supplierPhone || ""} onChange={(event) => updateCrmMaterialDraft({ supplierPhone: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Quantity
              <input type="number" min="0" value={draft.quantity || ""} onChange={(event) => updateCrmMaterialDraft({ quantity: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Unit
              <select value={draft.unit || "meter"} onChange={(event) => updateCrmMaterialDraft({ unit: event.target.value })} disabled={crmMaterialModalState.loading} style={selectInput}>
                {CRM_TRACKING_UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Ordered quantity
              <input type="number" min="0" value={draft.orderedQuantity || ""} onChange={(event) => updateCrmMaterialDraft({ orderedQuantity: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Received quantity
              <input type="number" min="0" value={draft.receivedQuantity || ""} onChange={(event) => updateCrmMaterialDraft({ receivedQuantity: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Order date
              <input type="date" value={draft.orderDate || ""} onChange={(event) => updateCrmMaterialDraft({ orderDate: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Expected arrival
              <input type="date" value={draft.expectedArrivalDate || ""} onChange={(event) => updateCrmMaterialDraft({ expectedArrivalDate: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Received date
              <input type="date" value={draft.receivedDate || ""} onChange={(event) => updateCrmMaterialDraft({ receivedDate: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Courier name
              <input value={draft.courierName || ""} onChange={(event) => updateCrmMaterialDraft({ courierName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Transport name
              <input value={draft.transportName || ""} onChange={(event) => updateCrmMaterialDraft({ transportName: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Tracking number
              <input value={draft.trackingNumber || ""} onChange={(event) => updateCrmMaterialDraft({ trackingNumber: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              LR number
              <input value={draft.lrNumber || ""} onChange={(event) => updateCrmMaterialDraft({ lrNumber: event.target.value })} disabled={crmMaterialModalState.loading} style={input} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Tracking URL
              <input value={draft.trackingUrl || ""} onChange={(event) => updateCrmMaterialDraft({ trackingUrl: event.target.value })} disabled={crmMaterialModalState.loading} style={input} placeholder="https://..." />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Last update note
              <input value={draft.lastUpdateNote || ""} onChange={(event) => updateCrmMaterialDraft({ lastUpdateNote: event.target.value })} disabled={crmMaterialModalState.loading} style={input} placeholder="Called supplier, reached hub, expected tomorrow..." />
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Notes
            <textarea value={draft.notes || ""} onChange={(event) => updateCrmMaterialDraft({ notes: event.target.value })} disabled={crmMaterialModalState.loading} style={{ ...input, minHeight: 82, resize: "vertical" }} placeholder="Any supplier, order, courier, or received notes" />
          </label>

          {crmMaterialModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmMaterialModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={closeCrmMaterialModal} disabled={crmMaterialModalState.loading} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>
              Cancel
            </button>
            <button type="button" onClick={saveCrmMaterial} disabled={crmMaterialModalState.loading} style={{ ...btn("#2563eb"), marginRight: 0, opacity: crmMaterialModalState.loading ? 0.72 : 1 }}>
              {crmMaterialModalState.loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmInstallationModal = () => {
    if (!crmInstallationModalState.open) return null;
    const draft = crmInstallationModalState.draft || {};
    const isCompleteMode = crmInstallationModalState.mode === "complete";
    const selectedProject =
      crmProjectRecords.find(
        (project) =>
          String(project.projectId || project.id || "") === String(crmInstallationModalState.projectId || "")
      ) || null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 160,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !crmInstallationModalState.loading) {
            closeCrmInstallationModal();
          }
        }}
      >
        <div
          style={{
            width: "min(620px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: isCompleteMode ? "#16a34a" : "#2563eb" }}>
                Installation
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                {isCompleteMode ? "Mark installation completed" : "Schedule installation"}
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                {selectedProject?.projectTitle || selectedProject?.customerName || "Selected order"}
              </div>
            </div>
            <button
              type="button"
              onClick={closeCrmInstallationModal}
              disabled={crmInstallationModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: crmInstallationModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close installation modal"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Scheduled date
              <input
                type="date"
                value={draft.scheduledDate || ""}
                onChange={(event) => updateCrmInstallationDraft({ scheduledDate: event.target.value })}
                disabled={crmInstallationModalState.loading || isCompleteMode}
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Completion date
              <input
                type="date"
                value={draft.completedDate || ""}
                onChange={(event) => updateCrmInstallationDraft({ completedDate: event.target.value })}
                disabled={crmInstallationModalState.loading || !isCompleteMode}
                style={input}
              />
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Notes
            <textarea
              value={draft.notes || ""}
              onChange={(event) => updateCrmInstallationDraft({ notes: event.target.value })}
              disabled={crmInstallationModalState.loading}
              style={{ ...input, minHeight: 82, resize: "vertical" }}
              placeholder="Optional installation note"
            />
          </label>

          {crmInstallationModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmInstallationModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={closeCrmInstallationModal} disabled={crmInstallationModalState.loading} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>
              Cancel
            </button>
            <button type="button" onClick={saveCrmInstallation} disabled={crmInstallationModalState.loading} style={{ ...btn(isCompleteMode ? "#16a34a" : "#2563eb"), marginRight: 0, opacity: crmInstallationModalState.loading ? 0.72 : 1 }}>
              {crmInstallationModalState.loading ? "Saving..." : isCompleteMode ? "Mark Completed" : "Schedule"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmSnagModal = () => {
    if (!crmSnagModalState.open) return null;
    const draft = crmSnagModalState.draft || {};
    const ownerOptions = getCrmPipelineReviewOwnerOptions();
    const isEdit = Boolean(crmSnagModalState.snagId);

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 161,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !crmSnagModalState.loading) {
            closeCrmSnagModal();
          }
        }}
      >
        <div
          style={{
            width: "min(720px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#f97316" }}>
                Snag / Correction
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                {isEdit ? "Edit snag" : "Add snag"}
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                Track installation corrections and assign a clear owner.
              </div>
            </div>
            <button
              type="button"
              onClick={closeCrmSnagModal}
              disabled={crmSnagModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: crmSnagModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close snag modal"
            >
              <X size={16} />
            </button>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Title
            <input value={draft.title || ""} onChange={(event) => updateCrmSnagDraft({ title: event.target.value })} disabled={crmSnagModalState.loading} style={input} placeholder="Example: Bedroom track alignment correction" />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Due date
              <input type="date" value={draft.dueDate || ""} onChange={(event) => updateCrmSnagDraft({ dueDate: event.target.value })} disabled={crmSnagModalState.loading} style={input} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Priority
              <select value={draft.priority || "normal"} onChange={(event) => updateCrmSnagDraft({ priority: event.target.value })} disabled={crmSnagModalState.loading} style={selectInput}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Owner
              <select
                value={draft.ownerUid || ""}
                onChange={(event) => {
                  const owner = ownerOptions.find(
                    (option) => String(option.uid || "") === String(event.target.value || "")
                  );
                  updateCrmSnagDraft({
                    ownerUid: event.target.value,
                    ownerName: owner?.name || owner?.userName || owner?.label || "",
                  });
                }}
                disabled={crmSnagModalState.loading}
                style={selectInput}
              >
                {ownerOptions.map((option) => (
                  <option key={option.uid} value={option.uid}>{option.name || option.userName || "Team member"}</option>
                ))}
              </select>
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Description
            <textarea value={draft.description || ""} onChange={(event) => updateCrmSnagDraft({ description: event.target.value })} disabled={crmSnagModalState.loading} style={{ ...input, minHeight: 96, resize: "vertical" }} placeholder="What needs correction?" />
          </label>

          {crmSnagModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {crmSnagModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={closeCrmSnagModal} disabled={crmSnagModalState.loading} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>
              Cancel
            </button>
            <button type="button" onClick={saveCrmSnag} disabled={crmSnagModalState.loading} style={{ ...btn("#f97316"), marginRight: 0, opacity: crmSnagModalState.loading ? 0.72 : 1 }}>
              {crmSnagModalState.loading ? "Saving..." : isEdit ? "Save Snag" : "Add Snag"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderHandoverModal = () => {
    if (!orderHandoverModalState.open) return null;
    const draft = orderHandoverModalState.draft || {};
    const ownerOptions = getCrmPipelineReviewOwnerOptions();
    const balanceAmount = calculateBalanceAmount(
      Number(draft.orderValue || 0),
      Number(draft.advanceAmount || 0)
    );

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 156,
          background: "rgba(15,23,42,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !orderHandoverModalState.loading) {
            closeOrderHandoverModal();
          }
        }}
      >
        <div
          style={{
            width: "min(820px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.68)"
              : "0 24px 70px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c3aed" }}>
                Order handover
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                Create order handover
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                Confirm the basic order details and create a handover task for the team.
              </div>
            </div>
            <button
              type="button"
              onClick={closeOrderHandoverModal}
              disabled={orderHandoverModalState.loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: orderHandoverModalState.loading ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close order handover modal"
            >
              <X size={16} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {[
              ["Customer", draft.customerName || "Not set"],
              ["Quote", draft.quoteNo || draft.quoteId || "Not set"],
              ["Site", draft.siteName || draft.siteAddress || "Not set"],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: 12, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                <div style={{ fontSize: 13, color: textColor, fontWeight: 900, marginTop: 5 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Order value
              <input
                type="number"
                min="0"
                value={draft.orderValue || ""}
                onChange={(event) => updateOrderHandoverDraft({ orderValue: event.target.value })}
                disabled={orderHandoverModalState.loading}
                style={input}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Advance amount
              <input
                type="number"
                min="0"
                value={draft.advanceAmount || ""}
                onChange={(event) => updateOrderHandoverDraft({ advanceAmount: event.target.value })}
                disabled={orderHandoverModalState.loading}
                style={input}
              />
            </label>
            <div style={{ padding: 12, borderRadius: 16, border: dashboardBorder, background: dashboardSoftBg }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>Balance amount</div>
              <div style={{ fontSize: 16, color: textColor, fontWeight: 950, marginTop: 7 }}>
                {formatCurrencyDisplay(balanceAmount, currency)}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Payment status
              <select
                value={draft.paymentStatus || PROJECT_PAYMENT_STATUS_ADVANCE_PENDING}
                onChange={(event) => updateOrderHandoverDraft({ paymentStatus: event.target.value })}
                disabled={orderHandoverModalState.loading}
                style={selectInput}
              >
                {PROJECT_PAYMENT_STATUS.filter(
                  (option) =>
                    ![PROJECT_PAYMENT_STATUS_OVERDUE, PROJECT_PAYMENT_STATUS_CANCELLED].includes(option.key)
                ).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Project owner
              <select
                value={draft.projectOwnerUid || ""}
                onChange={(event) => {
                  const owner = ownerOptions.find(
                    (option) => String(option.uid || "") === String(event.target.value || "")
                  );
                  updateOrderHandoverDraft({
                    projectOwnerUid: event.target.value,
                    projectOwnerName: owner?.name || owner?.userName || owner?.label || "",
                    nextActionOwnerUid: event.target.value,
                    nextActionOwnerName: owner?.name || owner?.userName || owner?.label || "",
                  });
                }}
                disabled={orderHandoverModalState.loading}
                style={selectInput}
              >
                {ownerOptions.map((option) => (
                  <option key={option.uid} value={option.uid}>
                    {option.name || option.userName || "Team member"}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
              Expected installation date
              <input
                type="date"
                value={draft.expectedInstallationDate || ""}
                onChange={(event) => updateOrderHandoverDraft({ expectedInstallationDate: event.target.value })}
                disabled={orderHandoverModalState.loading}
                style={input}
              />
            </label>
          </div>

          <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
            Handover notes
            <textarea
              value={draft.handoverNotes || ""}
              onChange={(event) => updateOrderHandoverDraft({ handoverNotes: event.target.value })}
              disabled={orderHandoverModalState.loading}
              style={{ ...input, minHeight: 82, resize: "vertical" }}
              placeholder="Any production, payment, or installation notes for the team"
            />
          </label>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: textColor }}>
              Handover checklist
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              {PROJECT_HANDOVER_CHECKLIST_KEYS.map((key) => (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    padding: 12,
                    borderRadius: 16,
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    fontSize: 13,
                    fontWeight: 800,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draft.handoverChecklist?.[key] === true}
                    disabled={orderHandoverModalState.loading}
                    onChange={(event) =>
                      updateOrderHandoverDraft({
                        handoverChecklist: {
                          ...(draft.handoverChecklist || {}),
                          [key]: event.target.checked,
                        },
                      })
                    }
                    style={{ marginTop: 2 }}
                  />
                  {PROJECT_HANDOVER_CHECKLIST_LABELS[key] || key}
                </label>
              ))}
            </div>
          </div>

          {crmSettings.enableDeliveryTracking !== false ? (
            <div style={{ display: "grid", gap: 12, padding: 14, borderRadius: 18, border: dashboardBorder, background: dashboardSoftBg }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: isCompact ? "stretch" : "center", flexDirection: isCompact ? "column" : "row" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 950, color: textColor }}>
                    Delivery / Material Tracking
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.55 }}>
                    Add materials or shipments the team should check daily. You can skip now and add items later from the Tracking tab.
                  </div>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, fontWeight: 900, color: textColor }}>
                  <input
                    type="checkbox"
                    checked={draft.enableDeliveryTracking !== false}
                    disabled={orderHandoverModalState.loading}
                    onChange={(event) => updateOrderHandoverDraft({ enableDeliveryTracking: event.target.checked })}
                  />
                  Enable tracking
                </label>
              </div>

              {draft.enableDeliveryTracking !== false ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                      Tracking owner
                      <select
                        value={draft.trackingOwnerUid || draft.projectOwnerUid || ""}
                        onChange={(event) => {
                          const owner = ownerOptions.find(
                            (option) => String(option.uid || "") === String(event.target.value || "")
                          );
                          updateOrderHandoverDraft({
                            trackingOwnerUid: event.target.value,
                            trackingOwnerName: owner?.name || owner?.userName || owner?.label || "",
                          });
                        }}
                        disabled={orderHandoverModalState.loading}
                        style={selectInput}
                      >
                        {ownerOptions.map((option) => (
                          <option key={option.uid} value={option.uid}>
                            {option.name || option.userName || "Team member"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                      Daily tracking reminder time
                      <input
                        type="time"
                        value={draft.dailyTrackingReminderTime || "10:30"}
                        onChange={(event) => updateOrderHandoverDraft({ dailyTrackingReminderTime: event.target.value })}
                        disabled={orderHandoverModalState.loading || crmSettings.enableDailyTrackingReminder === false}
                        style={input}
                      />
                    </label>
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    {(Array.isArray(draft.trackingItems) ? draft.trackingItems : []).map((item) => (
                      <div key={item.clientId} style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1.1fr 1.4fr 1.1fr 0.75fr 0.8fr 1fr auto", gap: 8, alignItems: "end" }}>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Type
                          <select
                            value={item.itemType || "fabric"}
                            onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { itemType: event.target.value })}
                            disabled={orderHandoverModalState.loading}
                            style={selectInput}
                          >
                            {TRACKING_ITEM_TYPES.map((option) => (
                              <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Item
                          <input value={item.itemName || ""} onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { itemName: event.target.value })} disabled={orderHandoverModalState.loading} style={input} />
                        </label>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Supplier
                          <input value={item.supplierName || ""} onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { supplierName: event.target.value })} disabled={orderHandoverModalState.loading} style={input} />
                        </label>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Qty
                          <input type="number" min="0" value={item.quantity || ""} onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { quantity: event.target.value })} disabled={orderHandoverModalState.loading} style={input} />
                        </label>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Unit
                          <select value={item.unit || "meter"} onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { unit: event.target.value })} disabled={orderHandoverModalState.loading} style={selectInput}>
                            {CRM_TRACKING_UNITS.map((unit) => (
                              <option key={unit} value={unit}>{unit}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ display: "grid", gap: 5, fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                          Expected
                          <input type="date" value={item.expectedArrivalDate || ""} onChange={(event) => updateOrderHandoverTrackingItem(item.clientId, { expectedArrivalDate: event.target.value })} disabled={orderHandoverModalState.loading} style={input} />
                        </label>
                        <button
                          type="button"
                          onClick={() => removeOrderHandoverTrackingItem(item.clientId)}
                          disabled={orderHandoverModalState.loading}
                          style={{ ...btn("#dc2626"), marginRight: 0, padding: "8px 10px" }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addOrderHandoverTrackingItem}
                      disabled={orderHandoverModalState.loading}
                      style={{ ...btn("#2563eb"), marginRight: 0, justifySelf: "start", padding: "8px 12px" }}
                    >
                      Add tracking item
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>
                  Tracking setup will be skipped for now.
                </div>
              )}
            </div>
          ) : null}

          {orderHandoverModalState.warnings?.length ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(245,158,11,0.12)", color: "#b45309", fontSize: 13, fontWeight: 800 }}>
              {orderHandoverModalState.warnings.join(" ")}
            </div>
          ) : null}

          {orderHandoverModalState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {orderHandoverModalState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={closeOrderHandoverModal}
              disabled={orderHandoverModalState.loading}
              style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitOrderHandover}
              disabled={orderHandoverModalState.loading}
              style={{ ...btn("#7c3aed"), marginRight: 0, opacity: orderHandoverModalState.loading ? 0.72 : 1 }}
            >
              {orderHandoverModalState.loading ? "Creating..." : "Create Order Handover"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteLinkModal = () => {
    if (!quoteLinkModalState || !quoteLinkTargetLead) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 152,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(980px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                }}
              >
                Link Quote
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                Link Existing Quote
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                {quoteLinkTargetLead.customerLabel || quoteLinkTargetLead.crmTitle || "Selected Lead"}
              </div>
            </div>
            <button
              type="button"
              onClick={closeQuoteLinkModal}
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: "pointer",
                justifySelf: isCompact ? "end" : "auto",
              }}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1.3fr) minmax(150px, 0.8fr) repeat(2, minmax(130px, 0.7fr))",
              gap: 12,
            }}
          >
            <input
              value={quoteLinkSearch}
              onChange={(event) => setQuoteLinkSearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search customer, phone, quote no, or status"
            />
            <select
              value={quoteLinkTypeFilter}
              onChange={(event) => setQuoteLinkTypeFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Types</option>
              <option value={GENERAL_QUOTE_TYPE}>General</option>
              <option value={CURTAIN_QUOTE_TYPE}>Curtain</option>
            </select>
            <input
              type="number"
              min="0"
              value={quoteLinkMinAmount}
              onChange={(event) => setQuoteLinkMinAmount(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Min amount"
            />
            <input
              type="number"
              min="0"
              value={quoteLinkMaxAmount}
              onChange={(event) => setQuoteLinkMaxAmount(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Max amount"
            />
          </div>

          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Showing {quoteLinkResults.length} matching quotation
            {quoteLinkResults.length === 1 ? "" : "s"}
          </div>

          {quoteLinkResults.length === 0 ? (
            <div
              style={{
                padding: 20,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>No quotes found</div>
              <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                Try a customer name, phone number, quote number, or wider amount range.
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {quoteLinkResults.map(({ quote, meta }) => {
                const linkedLead = findLinkedCrmLeadForQuote(quote);
                const linkedToThisLead = linkedLead?.id === quoteLinkTargetLead.id;
                const linkedElsewhere = linkedLead?.id && !linkedToThisLead;
                const badgeColor = linkedToThisLead
                  ? "#16a34a"
                  : linkedElsewhere
                  ? "#f59e0b"
                  : "#2563eb";

                return (
                  <div
                    key={quote.id}
                    style={{
                      padding: 16,
                      borderRadius: 20,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        flexDirection: isCompact ? "column" : "row",
                        alignItems: isCompact ? "stretch" : "flex-start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 900 }}>
                          #{meta.quoteNo || "-"} | {meta.customerName || "Unnamed Customer"}
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                          {meta.phone || "No phone"} | {meta.quoteDateLabel || "-"} |{" "}
                          {currencySymbol(currency)} {Number(meta.totalValue || 0).toFixed(2)}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: `${badgeColor}18`,
                            color: badgeColor,
                            fontSize: 11,
                            fontWeight: 800,
                          }}
                        >
                          {linkedToThisLead
                            ? "Linked to this lead"
                            : linkedElsewhere
                            ? "Linked elsewhere"
                            : "Unlinked"}
                        </span>
                        <span
                          style={{
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
                            color: isDark ? "#e2e8f0" : "#334155",
                            fontSize: 11,
                            fontWeight: 800,
                          }}
                        >
                          {meta.quoteTypeLabel}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Customer", meta.customerName || "-"],
                        ["Phone", meta.phone || "-"],
                        ["Status", meta.status || "-"],
                        [
                          "Linked To",
                          linkedLead
                            ? linkedLead.title || linkedLead.contact?.name || linkedLead.id
                            : "Not linked",
                        ],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            padding: 11,
                            borderRadius: 16,
                            background: dashboardSoftBg,
                            border: dashboardBorder,
                          }}
                        >
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6, wordBreak: "break-word" }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => requestLinkExistingQuote(quoteLinkTargetLead, quote)}
                        disabled={isLinkingQuote || linkedToThisLead}
                        style={{
                          ...btn(linkedToThisLead ? "#94a3b8" : "#16a34a"),
                          marginRight: 0,
                        }}
                      >
                        <Link2 size={14} style={{ marginRight: 6 }} />
                        {linkedToThisLead ? "Already Linked" : "Link this Quote"}
                      </button>
                      {canViewQuotes ? (
                        <button
                          type="button"
                          onClick={() => openQuoteFromLinkModal(quote)}
                          style={{ ...btn("#2563eb"), marginRight: 0 }}
                        >
                          <ExternalLink size={14} style={{ marginRight: 6 }} />
                          Open Quote
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomerLedgerEditModal = () => {
    if (!customerLedgerEditState.open) return null;
    const draft = customerLedgerEditState.draft || createCustomerLedgerEditDraft();
    const original = customerLedgerEditState.original || {};
    const ownerOptions = getCrmQuoteOwnerOptions();
    const oldName = String(original.customerName || "").trim();
    const newName = String(draft.customerName || "").trim();
    const nameChanged =
      oldName &&
      newName &&
      normalizeCustomerLedgerName(oldName) !== normalizeCustomerLedgerName(newName);
    const fieldLabelStyle = {
      display: "grid",
      gap: 6,
      fontSize: 12,
      color: dashboardMuted,
      fontWeight: 850,
    };
    const renderField = (label, field, props = {}) => (
      <label style={fieldLabelStyle}>
        {label}
        <input
          value={draft[field] || ""}
          onChange={(event) => updateCustomerLedgerEditDraft(field, event.target.value)}
          disabled={customerLedgerEditState.saving}
          style={input}
          {...props}
        />
      </label>
    );

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 158,
          background: "rgba(15,23,42,0.62)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !customerLedgerEditState.saving) {
            closeCustomerLedgerEditModal();
          }
        }}
      >
        <div
          style={{
            width: "min(880px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.72)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            padding: isCompact ? 20 : 28,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                Customer master
              </div>
              <div style={{ fontSize: 24, fontWeight: 950, marginTop: 6 }}>
                Edit Customer
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 7, lineHeight: 1.7 }}>
                Update the Customer Ledger and search record. Existing quotation snapshots stay unchanged unless you choose to update active quotations.
              </div>
            </div>
            <button
              type="button"
              onClick={closeCustomerLedgerEditModal}
              disabled={customerLedgerEditState.saving}
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: customerLedgerEditState.saving ? "not-allowed" : "pointer",
              }}
              title="Close"
              aria-label="Close edit customer modal"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            {renderField("Customer Name", "customerName", { placeholder: "Customer name" })}
            <label style={fieldLabelStyle}>
              Customer Type
              <select
                value={draft.customerType || ""}
                onChange={(event) => updateCustomerLedgerEditDraft("customerType", event.target.value)}
                disabled={customerLedgerEditState.saving}
                style={selectInput}
              >
                {CUSTOMER_LEDGER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {renderField("Primary Phone", "primaryPhone", { placeholder: "Primary phone" })}
            {renderField("Alternate Phone", "alternatePhone", { placeholder: "Comma-separated alternate numbers" })}
            {renderField("Email", "email", { type: "email", placeholder: "customer@example.com" })}
            {renderField("Location", "location", { placeholder: "Area / city" })}
          </div>

          <label style={fieldLabelStyle}>
            Address
            <textarea
              value={draft.address || ""}
              onChange={(event) => updateCustomerLedgerEditDraft("address", event.target.value)}
              disabled={customerLedgerEditState.saving}
              style={{ ...input, minHeight: 72, resize: "vertical" }}
              placeholder="Full billing/customer address"
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            {renderField("Source", "source", { placeholder: "Referral, walk-in, Instagram..." })}
            {renderField("Tags", "tags", { placeholder: "Comma-separated tags" })}
            <label style={fieldLabelStyle}>
              Owner / Salesperson
              <select
                value={draft.ownerUid || ""}
                onChange={(event) => updateCustomerLedgerEditDraft("ownerUid", event.target.value)}
                disabled={customerLedgerEditState.saving}
                style={selectInput}
              >
                <option value="">No owner assigned</option>
                {ownerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {renderField("Contact Person Name", "contactPersonName", { placeholder: "Person to speak with" })}
            {renderField("Contact Person Phone", "contactPersonPhone", { placeholder: "Contact person's phone" })}
          </div>

          <label style={fieldLabelStyle}>
            Notes
            <textarea
              value={draft.notes || ""}
              onChange={(event) => updateCustomerLedgerEditDraft("notes", event.target.value)}
              disabled={customerLedgerEditState.saving}
              style={{ ...input, minHeight: 84, resize: "vertical" }}
              placeholder="Internal customer notes"
            />
          </label>

          {nameChanged ? (
            <div style={{ padding: 14, borderRadius: 18, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", color: isDark ? "#fbbf24" : "#92400e", display: "grid", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 900 }}>
                You are changing this customer name from {oldName} to {newName}.
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                This updates the customer ledger and search results. Existing quotations keep their original customer snapshot unless you choose to update active quotations.
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, fontWeight: 850 }}>
                <input
                  type="radio"
                  checked={!customerLedgerEditState.updateActiveQuotes}
                  onChange={() =>
                    setCustomerLedgerEditState((prev) => ({
                      ...prev,
                      updateActiveQuotes: false,
                      includeClosedQuotes: false,
                    }))
                  }
                  disabled={customerLedgerEditState.saving}
                />
                Update customer only
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, fontWeight: 850 }}>
                <input
                  type="radio"
                  checked={customerLedgerEditState.updateActiveQuotes === true}
                  onChange={() =>
                    setCustomerLedgerEditState((prev) => ({
                      ...prev,
                      updateActiveQuotes: true,
                    }))
                  }
                  disabled={customerLedgerEditState.saving}
                />
                Also update active/open quotations and CRM deals linked by this customer ID
              </label>
              {customerLedgerEditState.updateActiveQuotes ? (
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, fontWeight: 750 }}>
                  <input
                    type="checkbox"
                    checked={customerLedgerEditState.includeClosedQuotes === true}
                    onChange={(event) =>
                      setCustomerLedgerEditState((prev) => ({
                        ...prev,
                        includeClosedQuotes: event.target.checked,
                      }))
                    }
                    disabled={customerLedgerEditState.saving}
                  />
                  Include cancelled/lost/closed quotations and deals too
                </label>
              ) : null}
            </div>
          ) : null}

          {customerLedgerEditState.error ? (
            <div style={{ padding: 12, borderRadius: 16, background: "rgba(239,68,68,0.10)", color: "#dc2626", fontSize: 13, fontWeight: 800 }}>
              {customerLedgerEditState.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={closeCustomerLedgerEditModal}
              disabled={customerLedgerEditState.saving}
              style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCustomerLedgerEdit}
              disabled={customerLedgerEditState.saving}
              style={{ ...btn("#2563eb"), marginRight: 0, opacity: customerLedgerEditState.saving ? 0.72 : 1 }}
            >
              {customerLedgerEditState.saving ? "Saving..." : "Save Customer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerLedgerQuoteLinkModal = () => {
    if (!customerLedgerQuoteLinkState.open) return null;
    const targetCustomer =
      customerRecords.find(
        (customer) =>
          String(customer.id || "") ===
          String(customerLedgerQuoteLinkState.customerId || "")
      ) ||
      (String(selectedCustomerLedger?.id || "") ===
      String(customerLedgerQuoteLinkState.customerId || "")
        ? selectedCustomerLedger
        : null);

    if (!targetCustomer) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 153,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(920px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                }}
              >
                Customer Ledger
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                Link Existing Quotation
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                Link a saved quote to {targetCustomer.customerName || "this customer"}. This updates the quote listing, customer ledger, and CRM pipeline link together.
              </div>
            </div>
            <button
              type="button"
              onClick={closeCustomerLedgerQuoteLinkModal}
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: "pointer",
                justifySelf: isCompact ? "end" : "auto",
              }}
              title="Close"
              aria-label="Close link quotation"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <input
              value={customerLedgerQuoteLinkState.search}
              onChange={(event) =>
                setCustomerLedgerQuoteLinkState((prev) => ({
                  ...prev,
                  search: event.target.value,
                }))
              }
              style={{ ...input, width: "100%" }}
              placeholder="Search quote number, customer name, phone, site, or amount"
            />
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks saved quotation index records only. Full quote data is touched only when you confirm a link.
            </div>
          </div>

          {customerLedgerQuoteLinkState.error ? (
            <div style={{ padding: 12, borderRadius: 14, background: "#fef2f2", color: "#b91c1c", fontSize: 13, fontWeight: 800 }}>
              {customerLedgerQuoteLinkState.error}
            </div>
          ) : null}

          {customerLedgerQuoteLinkState.loading ? (
            <div style={{ color: dashboardMuted }}>Searching quotations...</div>
          ) : null}

          {!customerLedgerQuoteLinkState.loading &&
          customerLedgerQuoteLinkState.search.trim().length >= 2 &&
          customerLedgerQuoteLinkState.results.length === 0 ? (
            <div style={{ padding: 18, borderRadius: 18, background: dashboardSoftBg, border: dashboardBorder, color: dashboardMuted }}>
              No matching quotations found. Try quote number, phone number, or exact customer name.
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10 }}>
            {customerLedgerQuoteLinkState.results.map((quote) => {
              const quoteIdentity = String(
                quote.quoteIndexId || quote.indexId || quote.id || quote.quoteId || ""
              );
              const isLinking =
                customerLedgerQuoteLinkState.linkingQuoteId === quote.id ||
                customerLedgerQuoteLinkState.linkingQuoteId === quoteIdentity;
              const alreadyLinked =
                String(quote.customerId || "") === String(targetCustomer.id || "");
              return (
                <div
                  key={quoteIdentity}
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                    border: dashboardBorder,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 17, fontWeight: 900, overflowWrap: "anywhere" }}>
                        #{quote.quoteNo || quote.id || "-"} | {quote.customerName || "Unnamed Customer"}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                        {getQuoteTypeLabel(resolveQuoteType(quote))} | {quote.customerPhone || "No phone"} | {resolveQuoteDateForInput(quote) || toDateInputValue(quote.createdAt) || "-"}
                      </div>
                      {quote.siteName || quote.projectName ? (
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                          Site: {quote.siteName || quote.projectName}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <strong>
                        {currencySymbol(currency)} {Number(getNormalizedQuoteTotal(quote) || 0).toFixed(2)}
                      </strong>
                      <button
                        type="button"
                        disabled={isLinking || alreadyLinked}
                        onClick={() => linkExistingQuoteToCustomerLedger(quote)}
                        style={{
                          ...btn(alreadyLinked ? "#94a3b8" : "#16a34a"),
                          marginRight: 0,
                          opacity: isLinking ? 0.72 : 1,
                        }}
                      >
                        <Link2 size={14} style={{ marginRight: 6 }} />
                        {alreadyLinked ? "Already Linked" : isLinking ? "Linking..." : "Link Quote"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerLedgerQuoteLinkConfirmModal = () => {
    if (!customerLedgerQuoteLinkConfirm) return null;
    const confirmState = customerLedgerQuoteLinkConfirm;
    const quote = confirmState.quote || {};
    const quoteIdentity = String(
      quote.quoteIndexId || quote.indexId || quote.id || quote.quoteId || ""
    );
    const isLinking =
      customerLedgerQuoteLinkState.linkingQuoteId === quote.id ||
      customerLedgerQuoteLinkState.linkingQuoteId === quoteIdentity;
    const oldCustomerName =
      confirmState.previousCustomer?.customerName ||
      quote.customerName ||
      "No customer on quote";
    const newCustomerName =
      confirmState.nextCustomerName ||
      confirmState.targetCustomer?.customerName ||
      "selected customer";

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 170,
          background: "rgba(15,23,42,0.64)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 24,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.7)"
              : "0 24px 60px rgba(15,23,42,0.18)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Link quote #{confirmState.quoteNo || confirmState.quoteId || "-"} to customer
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8, lineHeight: 1.7 }}>
              You are linking this quotation to {newCustomerName}. Choose whether to only
              attach it to the ledger, or also update the customer details stored on the quote.
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                Current quote customer
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, marginTop: 5 }}>
                {oldCustomerName}
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                New linked customer
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, marginTop: 5 }}>
                {newCustomerName}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={isLinking}
              onClick={() => setCustomerLedgerQuoteLinkConfirm(null)}
              style={{ ...btn("#64748b"), marginRight: 0 }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isLinking}
              onClick={() =>
                linkExistingQuoteToCustomerLedger(quote, {
                  skipConfirm: true,
                  updateQuoteCustomer: false,
                })
              }
              style={{ ...btn("#0f172a"), marginRight: 0, opacity: isLinking ? 0.72 : 1 }}
            >
              Link Only
            </button>
            <button
              type="button"
              disabled={isLinking}
              onClick={() =>
                linkExistingQuoteToCustomerLedger(quote, {
                  skipConfirm: true,
                  updateQuoteCustomer: true,
                })
              }
              style={{ ...btn("#16a34a"), marginRight: 0, opacity: isLinking ? 0.72 : 1 }}
            >
              {isLinking ? "Linking..." : "Link and Update Quote Customer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerCreateModal = () => {
    if (!customerCreateState) return null;

    const draft = customerCreateState.draft || {};
    const updateDraft = (field, value) => {
      setCustomerCreateState((prev) => ({
        ...prev,
        draft: {
          ...(prev?.draft || {}),
          [field]: value,
        },
        duplicateWarning: false,
      }));
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 18,
          background: "rgba(15,23,42,0.46)",
          backdropFilter: "blur(10px)",
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setCustomerCreateState(null);
        }}
      >
        <div
          style={{
            width: "min(680px, 100%)",
            maxHeight: "92vh",
            overflowY: "auto",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 30px 90px rgba(2,6,23,0.62)"
              : "0 30px 90px rgba(15,23,42,0.22)",
            padding: 22,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              alignItems: "flex-start",
              marginBottom: 18,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: textColor,
                }}
              >
                Create Customer
              </div>
              <div style={{ marginTop: 5, color: dashboardMuted, fontSize: 13 }}>
                Add this customer to the ledger and link it to the quotation.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCustomerCreateState(null)}
              style={{
                border: "none",
                width: 38,
                height: 38,
                borderRadius: 14,
                background: dashboardSoftBg,
                color: textColor,
                cursor: "pointer",
                fontWeight: 900,
              }}
            >
              x
            </button>
          </div>

          {customerCreateState.duplicateWarning ? (
            <div
              style={{
                display: "grid",
                gap: 10,
                padding: 14,
                borderRadius: 20,
                border: isDark
                  ? "1px solid rgba(251,191,36,0.32)"
                  : "1px solid rgba(245,158,11,0.32)",
                background: isDark
                  ? "rgba(120,53,15,0.22)"
                  : "rgba(255,251,235,0.95)",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
                Possible existing customer found
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                Use an existing customer if this is the same person. Create a new
                customer only when it is genuinely separate.
              </div>
              {(customerCreateState.duplicateMatches || []).map(({ customer, reason }) => (
                <div
                  key={customer.id}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                    border: dashboardBorder,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 900,
                        color: textColor,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {customer.customerName || "Unnamed Customer"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3 }}>
                      {[customer.phone || customer.whatsapp, customer.address]
                        .filter(Boolean)
                        .join(" | ") || "No contact details"}{" "}
                      - {reason} - {Number(customer.totals?.totalQuotes || 0)} quotes
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      applyCustomerToQuoteContext(customerCreateState.context, customer);
                      setCustomerCreateState(null);
                      notify("Customer selected", "success");
                    }}
                    style={{
                      ...btn("#2563eb"),
                      minHeight: 36,
                      padding: "8px 12px",
                    }}
                  >
                    Use Existing
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
              gap: 14,
            }}
          >
            <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Customer Name
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.customerName ?? ""}
                onChange={(event) => updateDraft("customerName", event.target.value)}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Phone
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.phone ?? ""}
                onChange={(event) => updateDraft("phone", event.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                WhatsApp
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.whatsapp ?? ""}
                onChange={(event) => updateDraft("whatsapp", event.target.value)}
                placeholder="WhatsApp number"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Email
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.email ?? ""}
                onChange={(event) => updateDraft("email", event.target.value)}
                placeholder="Email address"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Company / Project
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.companyName ?? ""}
                onChange={(event) => updateDraft("companyName", event.target.value)}
                placeholder="Optional company or project"
              />
            </div>
            <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Address
              </label>
              <textarea
                style={{ ...input, marginTop: 6, minHeight: 86, resize: "vertical" }}
                value={draft.address ?? ""}
                onChange={(event) => updateDraft("address", event.target.value)}
                placeholder="Address or location"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Source
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.source ?? ""}
                onChange={(event) => updateDraft("source", event.target.value)}
                placeholder="Walk-in, Instagram, Referral..."
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Tags
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.tags ?? ""}
                onChange={(event) => updateDraft("tags", event.target.value)}
                placeholder="VIP, repeat, priority"
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <button
              type="button"
              onClick={() => setCustomerCreateState(null)}
              style={{
                ...btn(isDark ? "#1e293b" : "#e2e8f0", textColor),
                minHeight: 42,
              }}
            >
              Cancel
            </button>
            {customerCreateState.duplicateWarning ? (
              <button
                type="button"
                onClick={() => createCustomerFromQuote({ force: true })}
                disabled={isCreatingCustomerFromQuote}
                style={{
                  ...btn("#f59e0b"),
                  minHeight: 42,
                  opacity: isCreatingCustomerFromQuote ? 0.72 : 1,
                }}
              >
                {isCreatingCustomerFromQuote ? "Creating..." : "Create New Anyway"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => createCustomerFromQuote()}
              disabled={isCreatingCustomerFromQuote}
              style={{
                ...btn("#2563eb"),
                minHeight: 42,
                opacity: isCreatingCustomerFromQuote ? 0.72 : 1,
              }}
            >
              {isCreatingCustomerFromQuote ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAppToasts = () => {
    if (appToasts.length === 0) return null;

    const toneStyles = {
      success: {
        accent: "#16a34a",
        background: isDark ? "rgba(22,163,74,0.16)" : "#ecfdf5",
        color: isDark ? "#bbf7d0" : "#166534",
      },
      error: {
        accent: "#dc2626",
        background: isDark ? "rgba(220,38,38,0.18)" : "#fef2f2",
        color: isDark ? "#fecaca" : "#991b1b",
      },
      warning: {
        accent: "#f59e0b",
        background: isDark ? "rgba(245,158,11,0.16)" : "#fffbeb",
        color: isDark ? "#fde68a" : "#92400e",
      },
      info: {
        accent: "#2563eb",
        background: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
      },
    };

    return (
      <div
        style={{
          position: "fixed",
          top: isCompact ? 12 : 18,
          right: isCompact ? 12 : 18,
          zIndex: 150,
          width: isCompact ? "calc(100vw - 24px)" : 360,
          maxWidth: "calc(100vw - 24px)",
          display: "grid",
          gap: 10,
        }}
      >
        {appToasts.map((toast) => {
          const tone = toneStyles[toast.tone] || toneStyles.info;

          return (
            <div
              key={toast.id}
              style={{
                borderRadius: 18,
                border: dashboardBorder,
                background: dashboardCardBg,
                boxShadow: isDark
                  ? "0 18px 40px rgba(2,6,23,0.55)"
                  : "0 18px 40px rgba(15,23,42,0.12)",
                padding: 14,
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: tone.accent,
                  marginTop: 5,
                }}
              />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: tone.color }}>
                {toast.message}
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                style={{
                  border: "none",
                  background: tone.background,
                  color: tone.color,
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                  padding: 0,
                }}
                aria-label="Dismiss notification"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExecutionWorkspace = () => {
    const selectedDeal = selectedCrmDeal && isGeneralExecutionEligibleDeal(selectedCrmDeal)
      ? selectedCrmDeal
      : null;
    const executionItems = Array.isArray(crmDetailDraft?.executionSpecs)
      ? crmDetailDraft.executionSpecs
      : [];
    const selectedExecutionItem =
      executionItems.find((item) => item.id === selectedExecutionItemId) ||
      executionItems[0] ||
      null;
    const selectedItemAttachments = normalizeExecutionSpecAttachments(
      selectedExecutionItem?.attachments || []
    );
    const selectedItemUploadKey = selectedDeal && selectedExecutionItem
      ? `${selectedDeal.id}:${selectedExecutionItem.id}`
      : "";
    const selectedItemMatchingTemplate = selectedExecutionItem
      ? getExecutionTemplateForType(selectedExecutionItem.itemType)
      : null;
    const hasExecutionFilters =
      executionWorkspaceSearch.trim() ||
      executionWorkspaceSourceFilter !== "all" ||
      executionWorkspaceStageFilter !== "all" ||
      executionWorkspaceItemTypeFilter !== "all" ||
      executionWorkspaceAttachmentFilter !== "all" ||
      executionWorkspaceSortBy !== "updated";
    const finderResults = executionWorkspaceFilteredDeals.slice(0, 18);
    const executionWorkspaceItemTypeListId = "execution-workspace-item-type-options";
    const executionWorkspaceSelectedAttachmentCount = executionItems.reduce(
      (sum, item) =>
        sum +
        normalizeExecutionSpecAttachments(item.attachments || []).length,
      0
    );

    return (
      <div
        style={dashboardPanelStyle}
        onBlurCapture={() => persistExecutionDetailsDraftLocally("blur")}
      >
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #eff6ff, #ffffff)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              flexDirection: isCompact ? "column" : "row",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 10,
                }}
              >
                Execution Workspace
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isCompact ? 24 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                Manage real job specs, files, and production-ready details outside the CRM drawer
              </h2>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: dashboardMuted,
                }}
              >
                Execution Specs now have a dedicated operating surface for General Quotations. This workspace keeps the
                live job details on the linked CRM lead while letting the original quotation seed the first version when
                needed.
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS)}
              </div>
            </div>

            <div
              style={{
                minWidth: isCompact ? "100%" : 320,
                display: "grid",
                gap: 10,
                alignContent: "start",
              }}
            >
              <button
                type="button"
                onClick={() => returnToCrmFromExecutionWorkspace()}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                }}
              >
                Back to CRM
              </button>
              {selectedDeal && (selectedDeal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
                <button
                  type="button"
                  onClick={() =>
                    selectedDeal.hasQuote
                      ? openCrmQuoteForDeal(selectedDeal)
                      : startQuoteFromCrmLead(selectedDeal)
                  }
                  style={{
                    ...btn("#2563eb"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                  }}
                >
                  {selectedDeal.hasQuote ? "Open Quote" : "Create Quote"}
                </button>
              ) : null}
              {selectedDeal ? (
                <button
                  type="button"
                  onClick={persistCrmDetailDraft}
                  style={{
                    ...btn("#16a34a"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                  }}
                >
                  Save Execution Details
                </button>
              ) : null}
              {selectedDeal && canUseWorkshopPdf ? (
                <button
                  type="button"
                  onClick={() => downloadExecutionWorkshopPdf(selectedDeal)}
                  style={{
                    ...btn("#7c3aed"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                    display: "grid",
                    gap: 3,
                    justifyItems: "start",
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Download size={15} />
                    Download Workshop PDF
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.86 }}>
                    Production / installation copy
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 14, marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Deal Finder</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Search general-work deals by client, source, quote number, stage, item type, or attachment coverage
                before opening a full execution workspace.
              </div>
            </div>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 16,
                border: dashboardBorder,
                background: dashboardSoftBg,
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 700,
              }}
            >
              {executionWorkspaceFilteredDeals.length} matching deal
              {executionWorkspaceFilteredDeals.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1.2fr) repeat(5, minmax(120px, 1fr))",
              gap: 10,
            }}
          >
            <input
              value={executionWorkspaceSearch}
              onChange={(event) => setExecutionWorkspaceSearch(event.target.value)}
              style={input}
              placeholder="Search all execution deals by client, source, quote number, stage..."
            />
            <select
              value={executionWorkspaceSourceFilter}
              onChange={(event) => setExecutionWorkspaceSourceFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {executionWorkspaceSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceStageFilter}
              onChange={(event) => setExecutionWorkspaceStageFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {executionWorkspaceStageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceItemTypeFilter}
              onChange={(event) => setExecutionWorkspaceItemTypeFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Item Types</option>
              {executionTemplateItemTypeOptions.map((itemType) => (
                <option key={itemType} value={itemType}>
                  {itemType}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceAttachmentFilter}
              onChange={(event) => setExecutionWorkspaceAttachmentFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Attachments</option>
              <option value="with">With Attachments</option>
              <option value="without">Without Attachments</option>
            </select>
            <select
              value={executionWorkspaceSortBy}
              onChange={(event) => setExecutionWorkspaceSortBy(event.target.value)}
              style={selectInput}
            >
              <option value="updated">Newest Updated</option>
              <option value="oldest">Oldest Updated</option>
              <option value="client">Client Name</option>
            </select>
          </div>
          {executionWorkspaceSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all CRM execution deals, including older records.
              {globalSearchStatus.executionWorkspaceSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.executionWorkspaceSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.executionWorkspaceSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}

          {hasExecutionFilters ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setExecutionWorkspaceSearch("");
                  setExecutionWorkspaceSourceFilter("all");
                  setExecutionWorkspaceStageFilter("all");
                  setExecutionWorkspaceItemTypeFilter("all");
                  setExecutionWorkspaceAttachmentFilter("all");
                  setExecutionWorkspaceSortBy("updated");
                }}
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
              >
                Clear Filters
              </button>
            </div>
          ) : null}

          {finderResults.length === 0 ? (
            <div
              style={{
                padding: 18,
                borderRadius: 18,
                border: dashboardBorder,
                background: dashboardSoftBg,
                color: dashboardMuted,
                fontSize: 13,
              }}
            >
              No deals match the current execution filters.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              {finderResults.map((deal) => {
                const executionItemsForDeal = normalizeExecutionSpecs(deal.executionSpecs || []);
                const dealAttachmentCount = executionItemsForDeal.reduce(
                  (sum, item) => sum + normalizeExecutionSpecAttachments(item.attachments || []).length,
                  0
                );
                const opening = executionWorkspaceOpeningDealId === deal.id;
                const active = selectedDeal?.id === deal.id;

                return (
                  <button
                    key={`execution-deal-${deal.id}`}
                    type="button"
                    onClick={() => openExecutionWorkspaceForDeal(deal)}
                    disabled={opening}
                    style={{
                      ...dashboardCardStyle,
                      textAlign: "left",
                      width: "100%",
                      cursor: opening ? "progress" : "pointer",
                      padding: 16,
                      display: "grid",
                      gap: 10,
                      border:
                        active
                          ? "1px solid rgba(37,99,235,0.4)"
                          : dashboardBorder,
                      background:
                        active
                          ? isDark
                            ? "rgba(37,99,235,0.12)"
                            : "#eff6ff"
                          : dashboardCardBg,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: isDark ? "rgba(255,255,255,0.06)" : "#f8fafc",
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {getQuoteTypeLabel(deal.pipelineType)}
                      </div>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {opening ? "Opening..." : active ? "Selected" : "Open"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>
                        {deal.crmTitle || deal.customerLabel}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                        {deal.hasQuote
                          ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                          : "Lead only"}
                        {" | "}
                        {deal.customerLabel || "Unnamed Client"}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, color: dashboardMuted }}>
                      <span>{deal.crmStageLabel}</span>
                      <span>{executionItemsForDeal.length} item{executionItemsForDeal.length === 1 ? "" : "s"}</span>
                      <span>{dealAttachmentCount} attachment{dealAttachmentCount === 1 ? "" : "s"}</span>
                      <span>{deal.source || deal.crmContact?.source || "No source"}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!selectedDeal || !crmDetailDraft ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "grid",
              gap: 10,
              textAlign: "center",
              padding: isCompact ? 24 : 36,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900 }}>Select a general quotation deal to view execution details.</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, maxWidth: 720, margin: "0 auto" }}>
              Once a General Quotation lead is selected, this page opens a full execution workspace for structured job
              specs, dynamic fields, and attachments without forcing your team to work inside the CRM drawer.
            </div>
          </div>
        ) : selectedDeal.crmRecordType === "quote" ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "grid",
              gap: 12,
              padding: isCompact ? 22 : 28,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              Promote this quote-backed record into the canonical execution workspace
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, maxWidth: 760 }}>
              Execution Specs stay canonical on CRM lead documents. This quotation still uses the legacy quote-backed
              CRM record, so opening the full workspace will create or link the lead-backed execution owner first and
              then continue from there.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => openExecutionWorkspaceForDeal(selectedDeal)}
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                {executionWorkspaceOpeningDealId === selectedDeal.id
                  ? "Preparing Workspace..."
                  : "Open Execution Workspace"}
              </button>
              {selectedDeal.hasQuote && canViewQuotes ? (
                <button
                  type="button"
                  onClick={() => openCrmQuoteForDeal(selectedDeal)}
                  style={{ ...btn("#0f172a"), marginRight: 0 }}
                >
                  Open Quote
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div style={{ ...dashboardCardStyle, display: "grid", gap: 16, marginBottom: 18 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                  flexDirection: isCompact ? "column" : "row",
                }}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>
                    {selectedDeal.crmTitle || selectedDeal.customerLabel}
                  </div>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                    {selectedDeal.customerLabel || "Unnamed Client"}
                    {selectedDeal.hasQuote
                      ? ` | Quote #${selectedDeal.primaryLinkedQuote?.quoteNo || selectedDeal.quoteNo || "-"}`
                      : " | No quote linked yet"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => returnToCrmFromExecutionWorkspace(selectedDeal.id)}
                    style={{
                      ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                      marginRight: 0,
                    }}
                  >
                    Open CRM Deal
                  </button>
                  {selectedDeal.hasQuote ? (
                    <button
                      type="button"
                      onClick={() => openCrmQuoteForDeal(selectedDeal)}
                      style={{ ...btn("#2563eb"), marginRight: 0 }}
                    >
                      Open Quote
                    </button>
                  ) : canCreateQuotes ? (
                    <button
                      type="button"
                      onClick={() => startQuoteFromCrmLead(selectedDeal)}
                      style={{ ...btn("#2563eb"), marginRight: 0 }}
                    >
                      Create Quote
                    </button>
                  ) : null}
                  {selectedDeal.hasQuote ? (
                    <button
                      type="button"
                      onClick={() => convertDealQuoteToExecutionSpecs(selectedDeal)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      Convert Quote to Execution Specs
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={persistCrmDetailDraft}
                    style={{ ...btn("#16a34a"), marginRight: 0 }}
                  >
                    Save Execution Details
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(5, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["Stage", selectedDeal.crmStageLabel || "-"],
                  ["Source", selectedDeal.source || selectedDeal.crmContact?.source || "-"],
                  [
                    "Assigned",
                    getTeamMemberLabel(
                      crmDetailDraft.assignedToUserId,
                      crmDetailDraft.assignedToNameSnapshot
                    ),
                  ],
                  [
                    "Execution Items",
                    `${executionItems.length} item${executionItems.length === 1 ? "" : "s"}`,
                  ],
                  [
                    "Execution Attachments",
                    `${executionWorkspaceSelectedAttachmentCount} file${
                      executionWorkspaceSelectedAttachmentCount === 1 ? "" : "s"
                    }`,
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "grid",
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "minmax(300px, 0.9fr) minmax(0, 1.5fr)",
                gap: 16,
                alignItems: "start",
              }}
            >
              <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Execution Items</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                      Select an item to manage dynamic fields and execution attachments.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addCrmExecutionSpec}
                    style={{ ...btn("#0f172a"), marginRight: 0 }}
                  >
                    + Add Item
                  </button>
                </div>

                {executionItems.length === 0 ? (
                  <div
                    style={{
                      padding: 18,
                      borderRadius: 18,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 800 }}>No execution items yet</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      Add an item manually or seed the workspace from the linked quotation.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {executionItems.map((item, index) => {
                      const active = selectedExecutionItem?.id === item.id;
                      const attachmentCount = normalizeExecutionSpecAttachments(item.attachments || []).length;
                      const fieldCount = Array.isArray(item.attributeRows) ? item.attributeRows.length : 0;

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedExecutionItemId(item.id)}
                          style={{
                            padding: 14,
                            borderRadius: 18,
                            border: active
                              ? "1px solid rgba(37,99,235,0.38)"
                              : dashboardBorder,
                            background: active
                              ? isDark
                                ? "rgba(37,99,235,0.12)"
                                : "#eff6ff"
                              : dashboardSoftBg,
                            display: "grid",
                            gap: 8,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                            <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                              Item {index + 1}
                            </div>
                            <div
                              style={{
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: isDark ? "rgba(255,255,255,0.06)" : "#ffffff",
                                color: dashboardMuted,
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {item.itemType || "Custom"}
                            </div>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>
                            {item.itemName || "Untitled execution item"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                            {fieldCount} field{fieldCount === 1 ? "" : "s"} | {attachmentCount} attachment
                            {attachmentCount === 1 ? "" : "s"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
                {!selectedExecutionItem ? (
                  <div
                    style={{
                      padding: 24,
                      borderRadius: 18,
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      color: dashboardMuted,
                      fontSize: 13,
                    }}
                  >
                    Select an execution item to edit its fields and attachments.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 14,
                        flexDirection: isCompact ? "column" : "row",
                        alignItems: isCompact ? "stretch" : "flex-start",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          Selected Item
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, marginTop: 6 }}>
                          {selectedExecutionItem.itemName || "Untitled execution item"}
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                          {selectedExecutionItem.itemType || "Custom type"} |{" "}
                          {(selectedExecutionItem.attributeRows || []).length} field
                          {(selectedExecutionItem.attributeRows || []).length === 1 ? "" : "s"} |{" "}
                          {selectedItemAttachments.length} attachment
                          {selectedItemAttachments.length === 1 ? "" : "s"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => deleteCrmExecutionSpec(selectedExecutionItem.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete Item
                      </button>
                    </div>

                    <datalist id={executionWorkspaceItemTypeListId}>
                        {executionTemplateItemTypeOptions.map((option) => (
                          <option key={option} value={option} />
                        ))}
                    </datalist>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "minmax(180px, 0.9fr) minmax(220px, 1.1fr)",
                        gap: 12,
                      }}
                    >
                      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Item Type
                        </div>
                        <input
                          value={selectedExecutionItem.itemType || ""}
                          onChange={(event) =>
                            syncCrmExecutionSpecType(selectedExecutionItem.id, event.target.value)
                          }
                          style={{ ...input, width: "100%", minWidth: 0 }}
                          placeholder="Sofa, Wardrobe, Interior..."
                          list={executionWorkspaceItemTypeListId}
                        />
                      </div>
                      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Item Name
                        </div>
                        <input
                          value={selectedExecutionItem.itemName || ""}
                          onChange={(event) =>
                            updateCrmExecutionSpec(selectedExecutionItem.id, {
                              itemName: event.target.value,
                            })
                          }
                          style={{ ...input, width: "100%", minWidth: 0 }}
                          placeholder="L Shape Sofa, Bedroom Wardrobe..."
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Template Actions</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            Matching item types can auto-populate standard fields, while still keeping every deal fully customizable.
                          </div>
                          {selectedItemMatchingTemplate ? (
                            <div style={{ fontSize: 12, color: "#2563eb", marginTop: 8, fontWeight: 700 }}>
                              Suggested template: {selectedItemMatchingTemplate.name} ({selectedItemMatchingTemplate.itemType})
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={() => setRoutedTab("executionTemplates")}
                            style={{
                              ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                              marginRight: 0,
                              padding: "8px 12px",
                            }}
                          >
                            Manage Templates
                          </button>
                          {selectedItemMatchingTemplate ? (
                            <button
                              type="button"
                              onClick={() =>
                                applyExecutionTemplateToSpec(
                                  selectedExecutionItem.id,
                                  selectedItemMatchingTemplate.id
                                )
                              }
                              style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 12px" }}
                            >
                              Apply Suggested Template
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <select
                          value={selectedExecutionTemplateId ?? ""}
                          onChange={(event) => {
                            const nextTemplateId = event.target.value;
                            setSelectedExecutionTemplateId(nextTemplateId);
                            if (!nextTemplateId) return;
                            applyExecutionTemplateToSpec(
                              selectedExecutionItem.id,
                              nextTemplateId
                            );
                          }}
                          style={{ ...selectInput, width: "100%", minWidth: 0 }}
                        >
                          <option value="">Select a template...</option>
                          {executionWorkspaceTemplateOptions.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} | {template.itemType}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedExecutionTemplateId) return;
                            applyExecutionTemplateToSpec(
                              selectedExecutionItem.id,
                              selectedExecutionTemplateId
                            );
                          }}
                          disabled={!selectedExecutionTemplateId}
                          style={{
                            ...btn(selectedExecutionTemplateId ? "#2563eb" : "#94a3b8"),
                            marginRight: 0,
                            padding: "8px 12px",
                            opacity: selectedExecutionTemplateId ? 1 : 0.7,
                          }}
                        >
                          Apply Again
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        Selecting a template fills in matching fields right away. Reapplying stays safe and avoids repeating the same field twice.
                      </div>
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Dynamic Fields</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                            Add or edit any structured key/value fields for the actual execution plan.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addCrmExecutionSpecField(selectedExecutionItem.id)}
                          style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                        >
                          + Add Field
                        </button>
                      </div>

                      {(selectedExecutionItem.attributeRows || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: dashboardMuted }}>
                          No fields yet for this item. Add a field or apply a template.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {(selectedExecutionItem.attributeRows || []).map((field) => (
                            <div
                              key={field.id}
                              style={{
                                display: "grid",
                                gridTemplateColumns: isCompact
                                  ? "1fr"
                                  : "minmax(180px, 0.9fr) minmax(220px, 1.1fr) auto",
                                gap: 10,
                                alignItems: "center",
                              }}
                            >
                              <input
                                value={field.key ?? ""}
                                onChange={(event) =>
                                  updateCrmExecutionSpecField(selectedExecutionItem.id, field.id, {
                                    key: event.target.value,
                                  })
                                }
                                style={{ ...input, minWidth: 0 }}
                                placeholder="Field name"
                              />
                              <input
                                value={field.value ?? ""}
                                onChange={(event) =>
                                  updateCrmExecutionSpecField(selectedExecutionItem.id, field.id, {
                                    value: event.target.value,
                                  })
                                }
                                style={{ ...input, minWidth: 0 }}
                                placeholder="Field value"
                              />
                              <button
                                type="button"
                                onClick={() => removeCrmExecutionSpecField(selectedExecutionItem.id, field.id)}
                                style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Attachments</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            Upload production references, site photos, measurements, PDFs, and vendor documents for this execution item.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            id={`execution-workspace-attachment-${selectedExecutionItem.id}`}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={(event) => {
                              const files = Array.from(event.target.files || []);
                              uploadCrmExecutionSpecAttachments(selectedExecutionItem.id, files);
                              event.target.value = "";
                            }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              document
                                .getElementById(`execution-workspace-attachment-${selectedExecutionItem.id}`)
                                ?.click()
                            }
                            style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                          >
                            <UploadCloud size={14} style={{ marginRight: 6 }} />
                            Upload File
                          </button>
                        </div>
                      </div>

                      {crmExecutionAttachmentUploadKey === selectedItemUploadKey ? (
                        <div style={{ fontSize: 12, color: dashboardMuted }}>
                          Uploading execution attachment...
                        </div>
                      ) : null}

                      {selectedItemAttachments.length === 0 ? (
                        <div style={{ fontSize: 13, color: dashboardMuted }}>
                          No execution attachments added yet for this item.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {selectedItemAttachments.map((attachment) => {
                            const visual = getAttachmentVisual(attachment);
                            const Icon = visual.icon;

                            return (
                              <div
                                key={attachment.id}
                                style={{
                                  padding: 12,
                                  borderRadius: 16,
                                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                                  border: dashboardBorder,
                                  display: "grid",
                                  gap: 10,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    alignItems: "flex-start",
                                    flexDirection: isCompact ? "column" : "row",
                                  }}
                                >
                                  <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
                                    <div
                                      style={{
                                        width: 40,
                                        height: 40,
                                        minWidth: 40,
                                        borderRadius: 14,
                                        display: "grid",
                                        placeItems: "center",
                                        background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                        color: visual.accent,
                                      }}
                                    >
                                      <Icon size={18} />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: 14, fontWeight: 800, wordBreak: "break-word" }}>
                                        {attachment.name}
                                      </div>
                                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                                        {visual.label}
                                        {formatAttachmentSize(attachment.sizeBytes)
                                          ? ` | ${formatAttachmentSize(attachment.sizeBytes)}`
                                          : ""}
                                        {attachment.uploadedAt
                                          ? ` | Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                                          : ""}
                                        {attachment.uploadedByName
                                          ? ` | By ${attachment.uploadedByName}`
                                          : ""}
                                      </div>
                                    </div>
                                  </div>

                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (attachment.url) {
                                          window.open(attachment.url, "_blank", "noopener,noreferrer");
                                        }
                                      }}
                                      style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      <ExternalLink size={14} style={{ marginRight: 6 }} />
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!attachment.url) return;
                                        const anchor = document.createElement("a");
                                        anchor.href = attachment.url;
                                        anchor.download = attachment.name || "execution-attachment";
                                        anchor.target = "_blank";
                                        anchor.rel = "noopener noreferrer";
                                        anchor.click();
                                      }}
                                      style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeCrmExecutionSpecAttachment(
                                          selectedExecutionItem.id,
                                          attachment.id
                                        )
                                      }
                                      style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderExecutionTemplatesPage = () => {
    const executionTemplatesReady =
      executionTemplatesLoadState.loaded &&
      executionTemplatesLoadState.workspaceUid === workspaceUid;
    const executionTemplatesLoading =
      executionTemplatesLoadState.loading &&
      executionTemplatesLoadState.workspaceUid === workspaceUid;
    const selectedTemplate =
      executionTemplateCatalog.find(
        (template) => template.id === selectedExecutionTemplateManagerId
      ) || null;
    if (isDevRuntime) {
      executionTemplatesRenderCountRef.current += 1;
      console.count("ExecutionTemplates render");
      if (
        executionTemplatesRenderCountRef.current <= 10 ||
        executionTemplatesRenderCountRef.current % 25 === 0
      ) {
        devLog("EXECUTION TEMPLATE RENDER AUDIT", {
          renderCount: executionTemplatesRenderCountRef.current,
          workspaceUid,
          loadedWorkspace: executionTemplatesLoadedWorkspaceRef.current,
          loadingWorkspace: executionTemplatesLoadingWorkspaceRef.current,
          ready: executionTemplatesReady,
          loading: executionTemplatesLoading,
          localTemplateCount: crmExecutionTemplates.length,
          catalogCount: executionTemplateCatalog.length,
          filteredCount: filteredExecutionTemplates.length,
          selectedExecutionTemplateManagerId,
          hasSelectedTemplate: Boolean(selectedTemplate),
        });
      }
    }

    return (
      <div
        style={dashboardPanelStyle}
        onBlurCapture={() => persistExecutionTemplatesDraftLocally("blur")}
      >
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #eff6ff, #ffffff)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              flexDirection: isCompact ? "column" : "row",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 10,
                }}
              >
                Execution Templates
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isCompact ? 24 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                Build reusable execution field packs for sofas, wardrobes, kitchens, custom interiors, and other general work
              </h2>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: dashboardMuted,
                }}
              >
                Templates stay shared across your workspace and are ready to use in Execution Details whenever a matching item type is selected.
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE)}
              </div>
            </div>

            <div
              style={{
                minWidth: isCompact ? "100%" : 320,
                display: "grid",
                gap: 10,
                alignContent: "start",
              }}
            >
              <button
                type="button"
                onClick={addExecutionTemplate}
                disabled={
                  !executionTemplatesReady ||
                  executionTemplatesLoading ||
                  isSavingExecutionTemplates
                }
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                  opacity:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? 0.62
                      : 1,
                  cursor:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {executionTemplatesLoading ? "Loading Templates..." : "+ Create Template"}
              </button>
              <button
                type="button"
                onClick={saveCrmExecutionTemplates}
                disabled={
                  !executionTemplatesReady ||
                  executionTemplatesLoading ||
                  isSavingExecutionTemplates
                }
                style={{
                  ...btn("#16a34a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                  opacity:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? 0.62
                      : 1,
                  cursor:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isSavingExecutionTemplates ? "Saving Templates..." : "Save Template Changes"}
              </button>
              <button
                type="button"
                onClick={() => setRoutedTab("executionSpecs")}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                }}
              >
                Open Execution Details
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(320px, 0.92fr) minmax(0, 1.48fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Template Library</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                  Search, sort, and choose a reusable template to edit.
                </div>
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 12,
                  fontWeight: 700,
                  color: dashboardMuted,
                }}
              >
                {executionTemplateCatalog.length} template{executionTemplateCatalog.length === 1 ? "" : "s"}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(160px, 0.55fr)",
                gap: 10,
              }}
            >
              <input
                value={executionTemplateSearch}
                onChange={(event) => setExecutionTemplateSearch(event.target.value)}
                style={input}
                placeholder="Search name, item type, field labels, defaults..."
              />
              <select
                value={executionTemplateSortBy}
                onChange={(event) => setExecutionTemplateSortBy(event.target.value)}
                style={selectInput}
              >
                <option value="name">Sort by Name</option>
                <option value="updated">Newest Updated</option>
                <option value="oldest">Oldest Created</option>
              </select>
            </div>

            {filteredExecutionTemplates.length === 0 ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 13,
                  lineHeight: 1.7,
                }}
              >
                {executionTemplateCatalog.length === 0
                  ? executionTemplatesLoading
                    ? "Loading execution templates..."
                    : executionTemplatesLoadState.error
                    ? "Could not load execution templates. Please try again."
                    : "No execution templates yet. Create one to start auto-populating execution item fields."
                  : "No templates match the current search."}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {filteredExecutionTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setSelectedExecutionTemplateManagerId(template.id)}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      border:
                        selectedExecutionTemplateManagerId === template.id
                          ? "1px solid rgba(37,99,235,0.38)"
                          : dashboardBorder,
                      background:
                        selectedExecutionTemplateManagerId === template.id
                          ? isDark
                            ? "rgba(37,99,235,0.12)"
                            : "#eff6ff"
                          : dashboardSoftBg,
                      display: "grid",
                      gap: 8,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {template.name || "Untitled Template"}
                      </div>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {template.itemType || "no type"}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      {(template.fields || []).length} field{(template.fields || []).length === 1 ? "" : "s"}
                      {template.updatedAt
                        ? ` | Updated ${new Date(template.updatedAt).toLocaleDateString("en-IN")}`
                        : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
            {!selectedTemplate ? (
              <div
                style={{
                  padding: 24,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 13,
                }}
              >
                Select a template to edit its name, normalized item type, and reusable fields.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      Template Editor
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                      {selectedTemplate.name || "Untitled Template"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                      Item type <strong style={{ color: textColor }}>{selectedTemplate.itemType || "-"}</strong> will be used for auto-matching inside Execution Details.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => deleteExecutionTemplate(selectedTemplate.id)}
                    style={{ ...btn("#ef4444"), marginRight: 0 }}
                  >
                    Delete Template
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1fr) minmax(220px, 1fr)",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                      Template Name
                    </div>
                    <input
                      value={selectedTemplate.name ?? ""}
                      onChange={(event) =>
                        updateExecutionTemplate(selectedTemplate.id, {
                          name: event.target.value,
                        })
                      }
                      style={{ ...input, width: "100%", minWidth: 0 }}
                      placeholder="Sofa, Wardrobe, Interior..."
                    />
                  </div>
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                      Item Type
                    </div>
                    <input
                      value={selectedTemplate.itemType ?? ""}
                      onChange={(event) =>
                        updateExecutionTemplate(selectedTemplate.id, {
                          itemType: event.target.value,
                        })
                      }
                      style={{ ...input, width: "100%", minWidth: 0 }}
                      placeholder="sofa, wardrobe, interior..."
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexDirection: isCompact ? "column" : "row",
                      alignItems: isCompact ? "stretch" : "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>Template Fields</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                        Every field stays generic and reusable. Default values are optional and will auto-fill when the template is applied.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => addExecutionTemplateField(selectedTemplate.id)}
                      style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                    >
                      + Add Field
                    </button>
                  </div>

                  {(selectedTemplate.fields || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      No fields yet for this template.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {(selectedTemplate.fields || []).map((field) => (
                        <div
                          key={field.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "1fr"
                              : "minmax(220px, 0.95fr) minmax(220px, 1.05fr) auto",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          <input
                            value={field.key ?? ""}
                            onChange={(event) =>
                              updateExecutionTemplateField(selectedTemplate.id, field.id, {
                                key: event.target.value,
                              })
                            }
                            style={{ ...input, width: "100%", minWidth: 0 }}
                            placeholder="Field label"
                          />
                          <input
                            value={field.defaultValue ?? ""}
                            onChange={(event) =>
                              updateExecutionTemplateField(selectedTemplate.id, field.id, {
                                defaultValue: event.target.value,
                              })
                            }
                            style={{ ...input, width: "100%", minWidth: 0 }}
                            placeholder="Optional default value"
                          />
                          <button
                            type="button"
                            onClick={() => removeExecutionTemplateField(selectedTemplate.id, field.id)}
                            style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMaintenancePage = () => {
    if (!isSuperAdmin) {
      return (
        <div style={{ ...dashboardCardStyle, padding: 24 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>
            Page unavailable
          </h2>
          <p style={{ margin: "10px 0 0", color: dashboardMuted }}>
            This area is reserved for platform administrators.
          </p>
        </div>
      );
    }

    const maintenanceActions = [
      {
        key: "repairQuoteSearch",
        title: "Repair Saved Quote Search",
        description:
          "Use this when saved quote search is missing older quotations for this workspace.",
        buttonLabel: quoteSearchRepairState.running
          ? `Repairing ${quoteSearchRepairState.processed}`
          : "Repair Saved Quote Search",
        color: "#475569",
        disabled: quoteSearchRepairState.running,
        confirmTitle: "Repair Saved Quote Search?",
        confirmDescription:
          "This will repair quote search/index metadata for this workspace. It may take some time. Continue?",
        confirmLabel: "Repair Search",
        onConfirm: repairQuotationSearchIndex,
      },
      {
        key: "recoverSavedQuoteListings",
        title: "Repair Saved Quote Listings",
        description:
          "Normalize lightweight quote_index cards, recover missing cards from full quotations, and repair Recycle Bin flags.",
        buttonLabel: savedQuoteIndexRecoveryState.running
          ? `Recovering ${savedQuoteIndexRecoveryState.checked}`
          : "Repair Saved Quote Listings",
        color: "#0369a1",
        disabled: savedQuoteIndexRecoveryState.running,
        confirmTitle: "Repair Saved Quote Listings?",
        confirmDescription:
          "This scans a capped batch, normalizes quote_index metadata, recreates missing lightweight cards, and does not delete or overwrite original quotations. Continue?",
        confirmLabel: "Repair Listings",
        onConfirm: recoverMissingSavedQuoteIndexes,
      },
      {
        key: "repairQuoteContentConsistency",
        title: "Repair Quote Content Aliases",
        description:
          "Use this if deleted quote rows/items reappear because old content aliases disagree with the current canonical rows.",
        buttonLabel: quoteContentAuditState.running
          ? `Checking ${quoteContentAuditState.checked}`
          : "Repair Quote Content Aliases",
        color: "#b45309",
        disabled: quoteContentAuditState.running,
        confirmTitle: "Repair Quote Content Aliases?",
        confirmDescription:
          "This checks a capped batch and copies each quote's current canonical content into its legacy item/row aliases. It does not recalculate totals or delete quotations. Continue?",
        confirmLabel: "Repair Content",
        onConfirm: repairQuoteContentConsistency,
      },
      {
        key: "rebuildAnalytics",
        title: "Rebuild Dashboard & Calendar Data",
        description:
          "Use this when dashboard totals, top clients, or installation calendar dates are incorrect.",
        buttonLabel: analyticsRebuildState.running
          ? `Rebuilding ${analyticsRebuildState.scanned}`
          : "Rebuild Dashboard & Calendar Data",
        color: "#0f766e",
        disabled: analyticsRebuildState.running,
        confirmTitle: "Rebuild Dashboard & Calendar Data?",
        confirmDescription:
          "This will rebuild dashboard and calendar summary indexes for this workspace. Continue?",
        confirmLabel: "Rebuild Data",
        onConfirm: rebuildAnalyticsAndCalendarIndexes,
      },
      {
        key: "auditWorkspaceData",
        title: "Audit Workspace Data",
        description:
          "Run a diagnostic report when search or dashboard data looks inconsistent.",
        buttonLabel: adminDataAuditState.running ? "Auditing Data..." : "Audit Workspace Data",
        color: "#7c3aed",
        disabled: adminDataAuditState.running,
        confirmTitle: "Audit Workspace Data?",
        confirmDescription: "This will run a diagnostic audit. Continue?",
        confirmLabel: "Run Audit",
        onConfirm: runTemporaryFullDataAudit,
      },
    ];

    return (
      <div
        style={{
          ...dashboardCardStyle,
          padding: isCompact ? 18 : 24,
          display: "grid",
          gap: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#b45309",
            }}
          >
            Superadmin Maintenance
          </div>
          <h2
            style={{
              margin: "6px 0 4px",
              fontSize: isCompact ? 24 : 30,
              fontWeight: 900,
              letterSpacing: "-0.04em",
            }}
          >
            Admin Tools
          </h2>
          <p style={{ margin: 0, color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
            These tools are for system maintenance only. Use them only when search,
            dashboard totals, or quote indexes are incorrect.
          </p>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 16,
            background: isDark ? "rgba(120,53,15,0.24)" : "#fffbeb",
            border: "1px solid rgba(245,158,11,0.32)",
            color: isDark ? "#fde68a" : "#92400e",
            fontSize: 13,
            lineHeight: 1.55,
            fontWeight: 750,
          }}
        >
          Maintenance actions may take time and should only be run when the workspace
          needs repair. Normal quotation search, loading, deletion, and stage updates
          do not require these tools.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {maintenanceActions.map((action) => (
            <div
              key={action.key}
              style={{
                padding: 16,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 12,
                alignContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: textColor }}>
                  {action.title}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12.5,
                    color: dashboardMuted,
                    lineHeight: 1.55,
                  }}
                >
                  {action.description}
                </div>
              </div>
              <button
                type="button"
                disabled={action.disabled}
                onClick={() =>
                  openConfirmDialog({
                    title: action.confirmTitle,
                    description: action.confirmDescription,
                    confirmLabel: action.confirmLabel,
                    onConfirm: action.onConfirm,
                  })
                }
                style={{
                  ...btn(action.color),
                  marginRight: 0,
                  opacity: action.disabled ? 0.72 : 1,
                  cursor: action.disabled ? "not-allowed" : "pointer",
                }}
              >
                {action.buttonLabel}
              </button>
            </div>
          ))}
        </div>

        {(savedQuoteIndexRecoveryState.running ||
          savedQuoteIndexRecoveryState.report ||
          savedQuoteIndexRecoveryState.error) ? (
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: dashboardSoftBg,
              border: dashboardBorder,
              color: dashboardMuted,
              fontSize: 12.5,
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: textColor }}>Saved quote recovery:</strong>{" "}
            {savedQuoteIndexRecoveryState.running ? "running" : "idle"} | checked{" "}
            {savedQuoteIndexRecoveryState.checked} | missing{" "}
            {savedQuoteIndexRecoveryState.missing} | recovered{" "}
            {savedQuoteIndexRecoveryState.recovered} | bin flags{" "}
            {savedQuoteIndexRecoveryState.binFlagRepairCount} | in bin{" "}
            {savedQuoteIndexRecoveryState.inBinCount}
            {savedQuoteIndexRecoveryState.report ? (
              <>
                {" "} | normalized{" "}
                {Number(savedQuoteIndexRecoveryState.report.normalizedCount || 0)}
                {" "} | hidden by old type filter{" "}
                {Number(savedQuoteIndexRecoveryState.report.hiddenByQuoteTypeCount || 0)}
              </>
            ) : null}
            {savedQuoteIndexRecoveryState.error ? (
              <span style={{ color: "#dc2626" }}>
                {" "}Error: {savedQuoteIndexRecoveryState.error}
              </span>
            ) : null}
          </div>
        ) : null}

        {(quoteContentAuditState.running ||
          quoteContentAuditState.report ||
          quoteContentAuditState.error) ? (
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: dashboardSoftBg,
              border: dashboardBorder,
              color: dashboardMuted,
              fontSize: 12.5,
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: textColor }}>Quote content aliases:</strong>{" "}
            {quoteContentAuditState.running ? "running" : "idle"} | checked{" "}
            {quoteContentAuditState.checked} | conflicts{" "}
            {quoteContentAuditState.conflicts} | repaired{" "}
            {quoteContentAuditState.repaired}
            {quoteContentAuditState.error ? (
              <span style={{ color: "#dc2626" }}>
                {" "}Error: {quoteContentAuditState.error}
              </span>
            ) : null}
          </div>
        ) : null}

        {(analyticsRebuildState.running ||
          analyticsRebuildState.scanned > 0 ||
          analyticsRebuildState.error) ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 14,
              background: isDark ? "rgba(15,118,110,0.16)" : "#ecfdf5",
              border: "1px solid rgba(15,118,110,0.22)",
              color: textColor,
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            <strong>Dashboard rebuild:</strong>{" "}
            {analyticsRebuildState.running ? "running" : "idle"} | scanned{" "}
            {analyticsRebuildState.scanned} | indexed {analyticsRebuildState.indexedQuotes} |
            monthly updates {analyticsRebuildState.monthlyUpdated} | client updates{" "}
            {analyticsRebuildState.clientUpdated} | installation records{" "}
            {analyticsRebuildState.installationsUpdated} | stale cleared{" "}
            {analyticsRebuildState.staleCleared || 0} | skipped {analyticsRebuildState.skipped} |
            failed {analyticsRebuildState.failed}
            {analyticsRebuildState.error ? (
              <span style={{ color: "#dc2626", fontWeight: 800 }}>
                {" "}Error: {analyticsRebuildState.error}
              </span>
            ) : null}
          </div>
        ) : null}

        {(adminDataAuditState.running ||
          adminDataAuditState.checkedAt ||
          adminDataAuditState.error) ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 14,
              background: isDark ? "rgba(124,58,237,0.14)" : "#f5f3ff",
              border: "1px solid rgba(124,58,237,0.22)",
              color: textColor,
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 8 }}>
              Workspace Data Audit{" "}
              {adminDataAuditState.running
                ? "(running)"
                : adminDataAuditState.checkedAt
                ? `(${new Date(adminDataAuditState.checkedAt).toLocaleString()})`
                : ""}
            </div>
            {adminDataAuditState.progress?.collection && adminDataAuditState.running ? (
              <div style={{ marginBottom: 8, color: dashboardMuted }}>
                Scanning {adminDataAuditState.progress.collection}:{" "}
                {adminDataAuditState.progress.scanned} documents checked
              </div>
            ) : null}
            {adminDataAuditState.error ? (
              <div style={{ marginBottom: 8, color: "#dc2626", fontWeight: 800 }}>
                Audit error: {adminDataAuditState.error}
              </div>
            ) : null}
            {adminDataAuditState.report ? (
              <div
                style={{
                  marginBottom: 10,
                  padding: 10,
                  borderRadius: 12,
                  background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 4 }}>
                  Source documents: quotations{" "}
                  {adminDataAuditState.report.sourceDocumentCounts?.quotations || 0},
                  CRM leads {adminDataAuditState.report.sourceDocumentCounts?.crm_leads || 0},
                  customers {adminDataAuditState.report.sourceDocumentCounts?.customers || 0}
                </div>
                <div>
                  Derived docs: quote_index{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.quote_index || 0},
                  analytics_monthly{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.analytics_monthly || 0},
                  client_monthly{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.client_monthly || 0},
                  installation_index{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.installation_index || 0}
                </div>
                <div style={{ marginTop: 4 }}>
                  Mismatches / repair issues:{" "}
                  <strong>
                    {adminDataAuditState.report.mismatchSummary?.totalMismatchCount || 0}
                  </strong>{" "}
                  | invalid amounts{" "}
                  {adminDataAuditState.report.issueCounts?.invalidAmountCount || 0}
                  {" "} | confirmed without confirmedAt{" "}
                  {adminDataAuditState.report.issueCounts?.confirmedWithoutConfirmedAtCount || 0}
                </div>
                {adminDataAuditState.report.mismatchSummary?.rootCauseHints?.length ? (
                  <div style={{ marginTop: 6, color: dashboardMuted }}>
                    {adminDataAuditState.report.mismatchSummary.rootCauseHints.join(" ")}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={downloadAdminDataAuditReport}
                  style={{ ...btn("#6d28d9"), marginTop: 8 }}
                >
                  Download Audit JSON
                </button>
              </div>
            ) : null}
            <div style={{ display: "grid", gap: 8 }}>
              {Object.entries(adminDataAuditState.collections || {}).map(([name, result]) => (
                <div
                  key={name}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                    border: dashboardBorder,
                  }}
                >
                  <strong>{name}</strong>:{" "}
                  {result.ok ? (
                    <>
                      total {result.totalCount} | batches {result.batches || 0} |
                      sample docs {result.sampleCount}
                      {Array.isArray(result.samples) && result.samples.length ? (
                        <div style={{ marginTop: 6, color: dashboardMuted }}>
                          Samples:{" "}
                          {result.samples
                            .map((sample) =>
                              [
                                sample.id,
                                sample.quoteNo ? `#${sample.quoteNo}` : "",
                                sample.customerName,
                                sample.monthKey,
                                sample.installationDateKey,
                              ]
                                .filter(Boolean)
                                .join(" / ")
                            )
                            .join(" | ")}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span style={{ color: "#dc2626", fontWeight: 800 }}>
                      failed {result.errorCode ? `(${result.errorCode})` : ""}:{" "}
                      {result.errorMessage || "Unknown error"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAccountSecurityPage = () => {
    const currentUser = auth.currentUser || user;
    const providerSummary = getSecurityProviderSummary(currentUser);
    const passwordStrength = getPasswordStrength(passwordForm.newPassword);
    const lastPasswordChange = securityActivity.find(
      (activity) => activity.type === "password_changed"
    );
    const currentSession = securitySessions.find(
      (session) => session.id === securitySessionId || session.sessionId === securitySessionId
    );
    const otherSessionCount = securitySessions.filter(
      (session) => session.id !== securitySessionId && session.sessionId !== securitySessionId
    ).length;
    const securityCard = {
      ...dashboardCardStyle,
      padding: isCompact ? 18 : 22,
      display: "grid",
      gap: 14,
    };
    const sectionTitleStyle = {
      fontSize: 18,
      fontWeight: 900,
      margin: 0,
      letterSpacing: "-0.02em",
    };
    const tinyLabelStyle = {
      fontSize: 11,
      fontWeight: 850,
      color: dashboardMuted,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    };
    const securityPill = (background, color) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 10px",
      borderRadius: 999,
      background,
      color,
      fontSize: 11,
      fontWeight: 900,
      whiteSpace: "nowrap",
    });
    const passwordInputType = passwordForm.showPasswords ? "text" : "password";
    const activityLabels = {
      password_changed: "Password changed",
      session_created: "New device signed in",
      session_revoked: "Session revoked",
      session_logged_out: "Session logged out",
      logout_everywhere: "Logged out everywhere",
    };

    return (
      <div
        style={{
          display: "grid",
          gap: 18,
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            borderRadius: 28,
            padding: isCompact ? 20 : 28,
            color: "#ffffff",
            background:
              "radial-gradient(circle at 15% 20%, rgba(56,189,248,0.30), transparent 28%), linear-gradient(135deg, #0f172a 0%, #1e3a8a 52%, #0f766e 100%)",
            boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
            display: "grid",
            gap: 18,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#bfdbfe" }}>
                Account Security
              </div>
              <h1 style={{ fontSize: isCompact ? 28 : 38, lineHeight: 1.05, margin: 0, fontWeight: 950, letterSpacing: "-0.055em" }}>
                Protect your FabriQuote login
              </h1>
              <div style={{ fontSize: 14, lineHeight: 1.7, color: "#dbeafe", maxWidth: 720 }}>
                Change your password, review active devices, and sign out sessions you do not recognize. Sensitive writes go through secure Cloud Functions.
              </div>
            </div>

            <div
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                fontSize: 12,
                fontWeight: 900,
                alignSelf: isCompact ? "flex-start" : "center",
              }}
            >
              Security status: Active
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {[
              ["Email", currentUser?.email || "Not available"],
              ["Provider", providerSummary.label],
              ["Last sign-in", currentUser?.metadata?.lastSignInTime || "Not available"],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: 16,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(16px)",
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 900, color: "#bfdbfe", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {label}
                </div>
                <div style={{ fontSize: 15, fontWeight: 850, marginTop: 8, overflowWrap: "anywhere" }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 0.95fr) minmax(0, 1.05fr)",
            gap: 18,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 18 }}>
            <div style={securityCard}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
                <div>
                  <div style={tinyLabelStyle}>Overview</div>
                  <h2 style={sectionTitleStyle}>Account details</h2>
                </div>
                <span style={securityPill(isDark ? "rgba(22,163,74,0.18)" : "#dcfce7", isDark ? "#86efac" : "#166534")}>
                  Protected
                </span>
              </div>

              {[
                ["Email address", currentUser?.email || "Not available"],
                ["Sign-in provider", providerSummary.label],
                ["Last password change", lastPasswordChange ? formatSecurityRelativeTime(lastPasswordChange.createdAt) : "Not recorded yet"],
                ["Current device", currentSession?.display?.label || "This browser"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    borderTop: dashboardBorder,
                    paddingTop: 12,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: dashboardMuted, fontWeight: 750 }}>{label}</span>
                  <span style={{ fontWeight: 850, textAlign: "right", overflowWrap: "anywhere" }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={securityCard}>
              <div>
                <div style={tinyLabelStyle}>Password</div>
                <h2 style={sectionTitleStyle}>Change password</h2>
                <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, marginTop: 6 }}>
                  Choose a strong password you do not use elsewhere.
                </div>
              </div>

              {!providerSummary.hasPassword ? (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    border: isDark ? "1px solid rgba(96,165,250,0.22)" : "1px solid rgba(37,99,235,0.16)",
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  Your account uses {providerSummary.label} sign-in. Password changes are managed by that provider.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {[
                    ["Current password", "currentPassword"],
                    ["New password", "newPassword"],
                    ["Confirm new password", "confirmPassword"],
                  ].map(([label, key]) => (
                    <label key={key} style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>{label}</span>
                      <input
                        type={passwordInputType}
                        value={passwordForm[key] || ""}
                        onChange={(event) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            [key]: event.target.value,
                            error: "",
                            message: "",
                          }))
                        }
                        style={{ ...input, padding: "11px 12px", borderRadius: 14 }}
                        autoComplete={key === "currentPassword" ? "current-password" : "new-password"}
                      />
                    </label>
                  ))}

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                      <div style={{ height: 8, borderRadius: 999, background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0", overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${Math.min(100, Math.max(8, (passwordStrength.score / 5) * 100))}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: passwordStrength.color,
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, color: passwordStrength.color, fontWeight: 850 }}>
                        Strength: {passwordStrength.label}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          showPasswords: !prev.showPasswords,
                        }))
                      }
                      style={{
                        ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                        marginRight: 0,
                      }}
                    >
                      {passwordForm.showPasswords ? "Hide" : "Show"}
                    </button>
                  </div>

                  <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: dashboardMuted, lineHeight: 1.5 }}>
                    <input
                      type="checkbox"
                      checked={passwordForm.logoutOtherSessions}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          logoutOtherSessions: event.target.checked,
                        }))
                      }
                      style={{ marginTop: 3 }}
                    />
                    <span>Sign out other app sessions after password change. Recommended.</span>
                  </label>

                  {passwordForm.error ? (
                    <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 750 }}>
                      {passwordForm.error}
                    </div>
                  ) : null}
                  {passwordForm.message ? (
                    <div style={{ color: "#16a34a", fontSize: 13, fontWeight: 750 }}>
                      {passwordForm.message}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={securityActionState.password}
                    style={{
                      ...btn("#2563eb"),
                      marginRight: 0,
                      opacity: securityActionState.password ? 0.72 : 1,
                    }}
                  >
                    {securityActionState.password ? "Updating password..." : "Update Password"}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendAccountPasswordReset}
                    disabled={securityActionState.passwordReset}
                    style={{
                      ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                      marginRight: 0,
                      opacity: securityActionState.passwordReset ? 0.72 : 1,
                    }}
                  >
                    {securityActionState.passwordReset
                      ? "Sending reset email..."
                      : "Email Password Reset Link"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 18 }}>
            <div style={securityCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={tinyLabelStyle}>Active sessions</div>
                  <h2 style={sectionTitleStyle}>Signed-in devices</h2>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, marginTop: 6 }}>
                    These are recent app sessions for your account. Only safe device labels are stored.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loadAccountSecurityData}
                  disabled={securityCenterState.loading}
                  style={{
                    ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                    marginRight: 0,
                    opacity: securityCenterState.loading ? 0.72 : 1,
                  }}
                >
                  {securityCenterState.loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              {securityCenterState.error ? (
                <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 750 }}>
                  {securityCenterState.error}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out this device?",
                      description: "This browser will be signed out immediately.",
                      confirmLabel: "Log Out This Device",
                      tone: "danger",
                      onConfirm: handleRevokeCurrentSession,
                    })
                  }
                  disabled={securityActionState.currentSession}
                  style={{
                    ...btn("#dc2626"),
                    marginRight: 0,
                    opacity: securityActionState.currentSession ? 0.72 : 1,
                  }}
                >
                  {securityActionState.currentSession ? "Signing out..." : "Log out this device"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out other app sessions?",
                      description:
                        "Other FabriQuote browser sessions will sign out when they receive the session update. This does not expose or store tokens.",
                      confirmLabel: "Log Out Other Devices",
                      tone: "warning",
                      onConfirm: () => handleRevokeOtherSessions(),
                    })
                  }
                  disabled={securityActionState.otherSessions || otherSessionCount === 0}
                  style={{
                    ...btn("#f59e0b"),
                    marginRight: 0,
                    opacity: securityActionState.otherSessions || otherSessionCount === 0 ? 0.62 : 1,
                  }}
                >
                  {securityActionState.otherSessions ? "Signing out..." : "Log out other devices"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out everywhere?",
                      description:
                        "This is the strongest option. Firebase refresh tokens are revoked and you will need to sign in again.",
                      confirmLabel: "Log Out Everywhere",
                      tone: "danger",
                      onConfirm: handleRevokeAllSessions,
                    })
                  }
                  disabled={securityActionState.allSessions}
                  style={{
                    ...btn("#7f1d1d"),
                    marginRight: 0,
                    opacity: securityActionState.allSessions ? 0.72 : 1,
                  }}
                >
                  {securityActionState.allSessions ? "Securing..." : "Log out everywhere"}
                </button>
              </div>

              {securitySessions.length === 0 && !securityCenterState.loading ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    border: "1px dashed rgba(148,163,184,0.42)",
                    color: dashboardMuted,
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  No active sessions are listed yet. Refresh once if you just signed in.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {securitySessions.map((session) => {
                    const sessionId = session.sessionId || session.id;
                    const isCurrent =
                      sessionId === securitySessionId || session.id === securitySessionId;
                    const isTargetLoading =
                      securityActionState.targetSessionId &&
                      securityActionState.targetSessionId === sessionId;
                    return (
                      <div
                        key={session.id || sessionId}
                        style={{
                          padding: 16,
                          borderRadius: 20,
                          border: isCurrent
                            ? "1px solid rgba(37,99,235,0.42)"
                            : dashboardBorder,
                          background: isCurrent
                            ? isDark
                              ? "rgba(37,99,235,0.12)"
                              : "#eff6ff"
                            : dashboardSoftBg,
                          display: "grid",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexDirection: isCompact ? "column" : "row",
                            alignItems: isCompact ? "stretch" : "center",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 900 }}>
                              {session.display?.label || "Browser session"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5 }}>
                              {session.device?.deviceType || "unknown"} - {session.device?.os || "Unknown OS"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {isCurrent ? (
                              <span style={securityPill(isDark ? "rgba(37,99,235,0.20)" : "#dbeafe", isDark ? "#bfdbfe" : "#1d4ed8")}>
                                Current device
                              </span>
                            ) : null}
                            <span style={securityPill(isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0", isDark ? "#cbd5e1" : "#475569")}>
                              {formatSecurityRelativeTime(session.lastSeenAt)}
                            </span>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                            gap: 10,
                            fontSize: 12,
                            color: dashboardMuted,
                          }}
                        >
                          <div>First signed in: <strong style={{ color: textColor }}>{formatSecurityRelativeTime(session.createdAt)}</strong></div>
                          <div>Location: <strong style={{ color: textColor }}>{session.display?.approximateLocation || "Not stored"}</strong></div>
                          <div>Browser: <strong style={{ color: textColor }}>{session.device?.browser || "Browser"}</strong></div>
                        </div>

                        {!isCurrent ? (
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                openConfirmDialog({
                                  title: "Log out this device?",
                                  description: "This app session will be marked revoked. The device will sign out when it next checks its session.",
                                  confirmLabel: "Log Out Device",
                                  tone: "warning",
                                  onConfirm: () => handleRevokeOtherSessions(sessionId),
                                })
                              }
                              disabled={Boolean(isTargetLoading)}
                              style={{
                                ...btn("#f59e0b"),
                                marginRight: 0,
                                opacity: isTargetLoading ? 0.72 : 1,
                              }}
                            >
                              {isTargetLoading ? "Signing out..." : "Log out device"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={securityCard}>
              <div>
                <div style={tinyLabelStyle}>Security activity</div>
                <h2 style={sectionTitleStyle}>Recent events</h2>
              </div>

              {securityActivity.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    border: "1px dashed rgba(148,163,184,0.42)",
                    color: dashboardMuted,
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  Security events such as new sessions, password changes, and session revokes will appear here.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {securityActivity.map((activity) => {
                    const tone =
                      activity.severity === "critical"
                        ? { bg: isDark ? "rgba(127,29,29,0.34)" : "#fee2e2", color: "#dc2626" }
                        : activity.severity === "warning"
                        ? { bg: isDark ? "rgba(245,158,11,0.16)" : "#fffbeb", color: "#d97706" }
                        : { bg: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff", color: "#2563eb" };
                    return (
                      <div
                        key={activity.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 12,
                          padding: 12,
                          borderRadius: 16,
                          border: dashboardBorder,
                          background: dashboardSoftBg,
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background: tone.color,
                            marginTop: 5,
                            boxShadow: `0 0 0 6px ${tone.bg}`,
                          }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 900 }}>
                            {activityLabels[activity.type] || "Security event"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            {activity.deviceLabel || "FabriQuote"} - {formatSecurityRelativeTime(activity.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamMemberInviteModal = () => {
    if (!isTeamMemberModalOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 140,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onClick={closeTeamMemberInviteModal}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 920,
            maxHeight: "90vh",
            overflowY: "auto",
            ...dashboardCardStyle,
            padding: isCompact ? 18 : 24,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Team Members
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                Invite Team Member
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8 }}>
                Invite staff into this shared FabriQuote workspace with their own login and tailored feature access.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={closeTeamMemberInviteModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendTeamMemberInvite()}
                disabled={isSendingTeamInvite}
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  opacity: isSendingTeamInvite ? 0.7 : 1,
                }}
              >
                {isSendingTeamInvite ? "Sending Invite..." : "Send Invite"}
              </button>
              {isDevRuntime ? (
                <button
                  type="button"
                  onClick={sendTeamInviteTestEmail}
                  disabled={isTestingTeamInviteEmail || isSendingTeamInvite}
                  style={{
                    ...btn("#f59e0b"),
                    marginRight: 0,
                    opacity: isTestingTeamInviteEmail ? 0.7 : 1,
                  }}
                >
                  {isTestingTeamInviteEmail ? "Testing..." : "Test Invite Email"}
                </button>
              ) : null}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(0, 1.15fr)",
              gap: 16,
            }}
          >
            <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Team Member Details</div>
              <input
                value={teamMemberDraft.name || ""}
                onChange={(event) => updateTeamMemberDraftField("name", event.target.value)}
                style={input}
                placeholder="Full name"
              />
              <input
                value={teamMemberDraft.email || ""}
                onChange={(event) => updateTeamMemberDraftField("email", event.target.value)}
                style={input}
                placeholder="Email address"
                type="email"
              />
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 10 }}>
                <input
                  value={teamMemberDraft.phone || ""}
                  onChange={(event) => updateTeamMemberDraftField("phone", event.target.value)}
                  style={input}
                  placeholder="Phone"
                />
                {process.env.REACT_APP_EMPLOYEE_PROVISIONING_V2 === 'true' ? (
                  <select aria-label="Employee role" value={teamMemberDraft.role || 'staff'}
                    onChange={event => updateTeamMemberDraftField('role', event.target.value)} style={input}>
                    <option value="staff">Staff / Salesperson</option><option value="manager">Manager</option>
                    <option value="accounts">Accounts</option><option value="project_coordinator">Project Coordinator</option>
                    <option value="admin">Administrator</option>
                  </select>
                ) : <input
                  value={teamMemberDraft.role || ""}
                  onChange={(event) => updateTeamMemberDraftField("role", event.target.value)}
                  style={input}
                  placeholder="Role"
                />}
              </div>
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: dashboardBorder,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.6,
                }}
              >
                {remainingStaffSlots > 0
                  ? `${remainingStaffSlots} of ${accountStaffLimit} staff slots remaining in this workspace.`
                  : "You have reached your staff limit."}
              </div>
              {teamMemberModalError ? (
                <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
                  {teamMemberModalError}
                </div>
              ) : null}
            </div>

            <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Permissions</div>
              <div style={{ display: "grid", gap: 10 }}>
                {TEAM_PERMISSION_DEFINITIONS.map((permission) => (
                  <label
                    key={permission.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: 12,
                      borderRadius: 14,
                      border: dashboardBorder,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={teamMemberDraft.permissions?.[permission.key] === true}
                      onChange={(event) =>
                        updateTeamMemberDraftPermission(permission.key, event.target.checked)
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{permission.label}</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                        {permission.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamMembersManager = () => (
    <div
      style={{
        padding: 20,
        borderRadius: 20,
        background: isDark ? "#1b1b27" : "#f8fafc",
        border: "1px solid rgba(148,163,184,0.16)",
        display: "grid",
        gap: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexDirection: isCompact ? "column" : "row",
          alignItems: isCompact ? "stretch" : "center",
        }}
      >
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
            Workspace Team Members
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
            Manage invites, feature permissions, and CRM assignment availability for this shared FabriQuote workspace.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {occupiedStaffSlots}/{accountStaffLimit} staff slots used
          </div>
          {isWorkspaceOwner ? (
            <Button
              icon={Plus}
              variant="primary"
              onClick={openTeamMemberInviteModal}
              disabled={remainingStaffSlots <= 0}
            >
              Invite Team Member
            </Button>
          ) : null}
        </div>
      </div>

      {teamMembers.length === 0 ? (
        <div
          style={{
            padding: 16,
            borderRadius: 16,
            background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
            border: "1px dashed rgba(148,163,184,0.4)",
            fontSize: 13,
            color: dashboardMuted,
            lineHeight: 1.7,
          }}
        >
          No team members have been invited yet. Add staff here to share this workspace, unlock CRM assignments, and control what each person can access.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {teamMembers.map((member) => (
            <div
              key={member.id}
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: "1px solid rgba(148,163,184,0.18)",
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {member.name || "Unnamed Team Member"}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.72, marginTop: 6 }}>
                    {member.email || "No email added yet"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background:
                        member.status === "active"
                          ? isDark
                            ? "rgba(16,185,129,0.16)"
                            : "#dcfce7"
                          : member.status === "invited"
                          ? isDark
                            ? "rgba(37,99,235,0.18)"
                            : "#dbeafe"
                          : isDark
                          ? "rgba(148,163,184,0.14)"
                          : "#e2e8f0",
                      color:
                        member.status === "active"
                          ? "#166534"
                          : member.status === "invited"
                          ? "#1d4ed8"
                          : isDark
                          ? "#cbd5e1"
                          : "#475569",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {getTeamMemberStatusLabel(member)}
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background: isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {member.role || "Team Member"}
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background:
                        getTeamMemberLoginStatusLabel(member) === "Ready" ||
                        getTeamMemberLoginStatusLabel(member) === "Invite Sent"
                          ? isDark
                            ? "rgba(16,185,129,0.16)"
                            : "#dcfce7"
                          : getTeamMemberLoginStatusLabel(member) === "Disabled"
                          ? isDark
                            ? "rgba(148,163,184,0.14)"
                            : "#e2e8f0"
                          : isDark
                          ? "rgba(245,158,11,0.18)"
                          : "#fef3c7",
                      color:
                        getTeamMemberLoginStatusLabel(member) === "Ready" ||
                        getTeamMemberLoginStatusLabel(member) === "Invite Sent"
                          ? "#166534"
                          : getTeamMemberLoginStatusLabel(member) === "Disabled"
                          ? isDark
                            ? "#cbd5e1"
                            : "#475569"
                          : "#92400e",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {getTeamMemberLoginStatusLabel(member)}
                  </div>
                  {isWorkspaceOwner ? <div style={{ fontSize: 12, marginTop: 6, color: dashboardMuted }}>
                    Auth linked: {member.authLinked ? "Yes" : "No"}<br />
                    Last setup email sent: {member.lastSetupEmailSentAt ? formatCrmDate(member.lastSetupEmailSentAt) : "Not sent"}<br />
                    Last setup email status: {member.lastSetupEmailStatus || "Not sent"}
                    {member.emailFailureCode ? <div role="status">{staffPasswordSetupError({ emailErrorCode: member.emailFailureCode, emailErrorMessage: member.emailFailureMessage })}</div> : null}
                  </div> : null}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact
                    ? "1fr"
                    : "minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 0.8fr)",
                  gap: 12,
                }}
              >
                <input
                  style={input}
                  value={member.name}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, name: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Team member name"
                  disabled={!isWorkspaceOwner}
                />
                <input
                  style={input}
                  value={member.phone}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, phone: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Phone"
                  disabled={!isWorkspaceOwner}
                />
                <input
                  style={input}
                  value={member.role}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, role: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Role"
                  disabled={!isWorkspaceOwner}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
                  gap: 12,
                  alignItems: "start",
                }}
              >
                <div style={{ display: "grid", gap: 10 }}>
                  <input
                    style={input}
                    value={member.email}
                    onChange={(e) =>
                      setTeamMembers((prev) =>
                        prev.map((current) =>
                          current.id === member.id && !current.uid
                            ? { ...current, email: e.target.value }
                            : current
                        )
                      )
                    }
                    placeholder="Email"
                    disabled={!isWorkspaceOwner || Boolean(member.uid)}
                  />

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    {TEAM_PERMISSION_DEFINITIONS.map((permission) => (
                      <label
                        key={`${member.id}-${permission.key}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 10,
                          alignItems: "start",
                          padding: 12,
                          borderRadius: 14,
                          border: "1px solid rgba(148,163,184,0.18)",
                          background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={member.permissions?.[permission.key] === true}
                          onChange={(e) =>
                            updateSavedTeamMemberPermission(
                              member.id,
                              permission.key,
                              e.target.checked
                            )
                          }
                          disabled={!isWorkspaceOwner}
                          style={{ marginTop: 3 }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            {permission.label}
                          </div>
                          <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                            {permission.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignItems: isCompact ? "stretch" : "flex-end",
                  }}
                >
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={member.status === 'identity_pending' || (member.status !== "disabled" && member.isActive !== false)}
                      disabled={!isWorkspaceOwner}
                      onChange={(e) =>
                        setTeamMembers((prev) =>
                          normalizeTeamMembers(
                            prev.map((current) =>
                              current.id === member.id
                                ? {
                                    ...current,
                                    isActive: e.target.checked,
                                    status: e.target.checked
                                      ? current.uid
                                        ? "active"
                                        : "invited"
                                      : "disabled",
                                  }
                                : current
                            )
                          )
                        )
                      }
                    />
                    {member.status === 'identity_pending' ? 'Enabled - email verification required' : member.status === "disabled" ? "Disabled" : "Enabled"}
                  </label>

                  {member.email && isWorkspaceOwner && member.status !== "disabled" ? (
                    <Button
                      icon={Mail}
                      variant="secondary"
                      onClick={() =>
                        sendTeamMemberInvite(member, {
                          closeAfter: false,
                          resend: true,
                        })
                      }
                      disabled={isSendingTeamInvite || Boolean(resendingTeamInviteId)}
                    >
                      {resendingTeamInviteId === member.id
                        ? "Sending..."
                        : member.inviteEmailSent
                        ? "Resend Password Setup Link"
                        : "Send Password Setup Link"}
                    </Button>
                  ) : null}

                  {member.email && isWorkspaceOwner ? (
                    <Button
                      icon={CheckCircle2}
                      variant="secondary"
                      onClick={() => diagnoseTeamMemberLogin(member)}
                      disabled={Boolean(diagnosingTeamMemberId)}
                    >
                      {diagnosingTeamMemberId === member.id ? "Checking..." : "Check Login"}
                    </Button>
                  ) : null}

                  {member.email && isWorkspaceOwner && member.status !== "disabled" ? (
                    <Button
                      icon={RotateCcw}
                      variant="secondary"
                      onClick={() => repairTeamMemberLogin(member)}
                      disabled={Boolean(repairingTeamMemberId)}
                    >
                      {repairingTeamMemberId === member.id ? "Repairing..." : "Repair Staff Login"}
                    </Button>
                  ) : null}

                  {member.email && isWorkspaceOwner && member.status !== "disabled" ? (
                    <Button
                      icon={X}
                      variant="danger"
                      onClick={() => disableTeamMemberAccess(member)}
                      disabled={Boolean(disablingTeamMemberId)}
                    >
                      {disablingTeamMemberId === member.id ? "Disabling..." : "Disable Access"}
                    </Button>
                  ) : null}

                  <Button
                    icon={Trash2}
                    variant="danger"
                    onClick={() =>
                      setTeamMembers((prev) =>
                        prev.filter((current) => current.id !== member.id)
                      )
                    }
                    disabled={!isWorkspaceOwner}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          paddingTop: 4,
        }}
      >
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
          Permission, status, and profile edits are saved back to this workspace when you save team changes.
        </div>
        {isWorkspaceOwner ? (
          <Button
            icon={Save}
            variant="primary"
            onClick={saveSettings}
            disabled={isSavingSettings}
          >
            {isSavingSettings ? "Saving..." : "Save Team Changes"}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const renderTodayTasks = () => {
    const summaryCards = [
      { label: "Total Tasks", value: dailyTaskSummary.total, accent: "#2563eb" },
      { label: "Pending", value: dailyTaskSummary.pending, accent: "#0f172a" },
      { label: "Overdue", value: dailyTaskSummary.overdue, accent: "#ef4444" },
      { label: "Installations", value: dailyTaskSummary.installations, accent: "#f97316" },
      { label: "Completed", value: dailyTaskSummary.completed, accent: "#16a34a" },
    ];
    const dateModeOptions = [
      { key: "today", label: "Today" },
      { key: "tomorrow", label: "Tomorrow" },
      { key: "week", label: "This Week" },
      { key: "custom", label: "Custom Range" },
      { key: "overdue", label: "Overdue" },
    ];
    const groupDefinitions = [
      ["overdue", "Overdue", "Pending work already past its due date or time.", dailyTaskGroups.overdue, "#ef4444"],
      ["today", dailyTaskDateMode === "week" ? "This Week" : dailyTaskDateMode === "tomorrow" ? "Tomorrow" : dailyTaskDateMode === "custom" ? "Selected Range" : "Today", "Follow-ups, checklist items, and customer actions scheduled for the selected window.", dailyTaskGroups.today, "#2563eb"],
      ["installations", "Installations", "Installation work scheduled for this date.", dailyTaskGroups.installations, "#f97316"],
      ["completed", "Completed", "Tasks completed on or scheduled for this date.", dailyTaskGroups.completed, "#16a34a"],
    ];

    const renderTaskCard = (task) => {
      const primaryActionLabel =
        resolveTaskTarget(task).label;
      const isSelected = selectedDailyTask?.id === task.id;
      const priorityColor =
        task.priority === CRM_FOLLOW_UP_PRIORITY_HIGH
          ? "#ef4444"
          : task.priority === CRM_FOLLOW_UP_PRIORITY_MEDIUM
          ? "#f97316"
          : "#64748b";
      const typeColor =
        task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
          ? "#f97316"
          : task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP
          ? "#2563eb"
          : "#0f172a";

      return (
        <div
          key={task.id}
          onClick={() => setSelectedDailyTaskId(task.id)}
          style={{
            padding: 16,
            borderRadius: 20,
            border: isSelected ? "1px solid rgba(37,99,235,0.42)" : dashboardBorder,
            background: isSelected
              ? isDark
                ? "rgba(37,99,235,0.12)"
                : "#eff6ff"
              : dashboardCardBg,
            boxShadow: isDark ? "none" : "0 12px 26px rgba(15,23,42,0.06)",
            display: "grid",
            gap: 12,
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ padding: "4px 8px", borderRadius: 999, background: `${typeColor}18`, color: typeColor, fontSize: 11, fontWeight: 800 }}>
                  {task.typeLabel}
                </span>
                <span style={{ padding: "4px 8px", borderRadius: 999, background: `${priorityColor}16`, color: priorityColor, fontSize: 11, fontWeight: 800, textTransform: "capitalize" }}>
                  {task.priority || "medium"}
                </span>
                {task.isOverdue && task.status !== "completed" ? (
                  <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(239,68,68,0.12)", color: "#ef4444", fontSize: 11, fontWeight: 800 }}>
                    Overdue
                  </span>
                ) : null}
                {task.status === "completed" ? (
                  <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(22,163,74,0.12)", color: "#16a34a", fontSize: 11, fontWeight: 800 }}>
                    Done
                  </span>
                ) : null}
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: textColor }}>{task.title}</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                {task.customerName}
                {task.quoteNo ? ` | #${task.quoteNo}` : ""}
                {task.source ? ` | ${task.source}` : ""}
              </div>
            </div>
            <div style={{ minWidth: 82, textAlign: "right", color: task.isOverdue ? "#ef4444" : dashboardMuted, fontSize: 12, fontWeight: 800 }}>
              {task.dueTime || "-"}
            </div>
          </div>

          {task.description ? (
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>{task.description}</div>
          ) : null}
          {task.locationLabel || task.locationAddress ? (
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Location: <strong style={{ color: textColor }}>{task.locationLabel || "Site"}</strong>
              {task.locationAddress ? ` | ${task.locationAddress}` : ""}
            </div>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, color: dashboardMuted }}>
            <span>{formatReminderDateTime(task.dueDateTime || task.dueDate)}</span>
            <span>{task.assignedTo || "Unassigned"}</span>
            {task.stageLabel ? <span>{task.stageLabel}</span> : null}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={(event) => { event.stopPropagation(); openDailyTaskDeal(task); }} style={{ ...btn("#0f172a"), padding: "8px 12px", marginRight: 0 }}>
              {primaryActionLabel}
            </button>
            {task.locationUrl || task.siteLocation ? (
              <button type="button" onClick={(event) => { event.stopPropagation(); openSiteLocation(task.siteLocation || task); }} style={{ ...btn("#2563eb"), padding: "8px 12px", marginRight: 0 }}>
                Navigate
              </button>
            ) : null}
            {task.status !== "completed" ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(markingDoneKey)}
                  onClick={(event) => { event.stopPropagation(); markDailyTaskDone(task); }}
                  style={{
                    ...btn("#16a34a"),
                    padding: "8px 12px",
                    marginRight: 0,
                    opacity: markingDoneKey ? 0.7 : 1,
                  }}
                >
                  {markingDoneKey ? "Saving..." : "Mark as Done"}
                </button>
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); openDailyTaskReschedule(task); }}
                  style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), padding: "8px 12px", marginRight: 0 }}
                >
                  Snooze / Reschedule
                </button>
              </>
            ) : null}
          </div>
        </div>
      );
    };

    return (
      <div style={dashboardPanelStyle}>
        <div style={{ ...dashboardCardStyle, marginBottom: 18, background: isDark ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.96))" : "linear-gradient(135deg, #eff6ff, #ffffff)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 18, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "flex-start" }}>
            <div style={{ maxWidth: 760 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb", marginBottom: 10 }}>
                Today's Tasks
              </div>
              <h2 style={{ margin: 0, fontSize: isCompact ? 24 : 32, fontWeight: 900, letterSpacing: "-0.04em", color: isDark ? "#f8fafc" : "#0f172a" }}>
                Start the day with every follow-up, action, and installation in one place
              </h2>
              <div style={{ marginTop: 10, fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                This dashboard combines follow-ups, customer actions, overdue work, and installation dates from the same CRM records your team already uses.
              </div>
            </div>
            <div style={{ minWidth: isCompact ? "100%" : 340, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {dateModeOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setDailyTaskDateMode(option.key);
                      if (option.key === "today") setDailyTaskDate(todayISO());
                      if (option.key === "tomorrow") {
                        const tomorrow = parseDateInputValue(todayISO()) || new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setDailyTaskDate(toDateInputValue(tomorrow));
                      }
                    }}
                    style={{
                      ...btn(
                        dailyTaskDateMode === option.key
                          ? "#2563eb"
                          : isDark
                          ? "#1e293b"
                          : "#e2e8f0",
                        dailyTaskDateMode === option.key || isDark ? "#fff" : "#0f172a"
                      ),
                      marginRight: 0,
                      padding: "8px 10px",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {dailyTaskDateMode === "custom" ? (
                <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 8 }}>
                  <input type="date" value={dailyTaskRangeStart} onChange={(event) => setDailyTaskRangeStart(event.target.value || todayISO())} style={{ ...input, width: "100%", minWidth: 0 }} />
                  <input type="date" value={dailyTaskRangeEnd} onChange={(event) => setDailyTaskRangeEnd(event.target.value || todayISO())} style={{ ...input, width: "100%", minWidth: 0 }} />
                </div>
              ) : (
                <input type="date" value={dailyTaskDateKey} onChange={(event) => { setDailyTaskDate(event.target.value || todayISO()); setDailyTaskDateMode("custom"); setDailyTaskRangeStart(event.target.value || todayISO()); setDailyTaskRangeEnd(event.target.value || todayISO()); }} style={{ ...input, width: "100%", minWidth: 0 }} />
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 18 }}>
          {summaryCards.map((card) => (
            <div key={card.label} style={{ ...dashboardCardStyle, display: "grid", gap: 6, border: `1px solid ${card.accent}22` }}>
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>{card.label}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: card.accent }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1.3fr) repeat(6, minmax(120px, 1fr))", gap: 10 }}>
            <input value={dailyTaskSearch} onChange={(event) => setDailyTaskSearch(event.target.value)} style={{ ...input, width: "100%", minWidth: 0 }} placeholder="Search all tasks by customer, quote no, task, source..." />
            <select value={dailyTaskAssignedFilter} onChange={(event) => setDailyTaskAssignedFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Assignees</option>
              {dailyTaskAssignedOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select value={dailyTaskTypeFilter} onChange={(event) => setDailyTaskTypeFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Types</option>
              <option value={DAILY_TASK_SOURCE_FOLLOW_UP}>Follow-ups</option>
              <option value={DAILY_TASK_SOURCE_CRM_TASK}>CRM Tasks</option>
              <option value={DAILY_TASK_SOURCE_DEAL_ACTION}>Customer Actions</option>
              <option value={DAILY_TASK_SOURCE_CHECKLIST}>Checklist</option>
              <option value={DAILY_TASK_SOURCE_INSTALLATION}>Installations</option>
            </select>
            <select value={dailyTaskPriorityFilter} onChange={(event) => setDailyTaskPriorityFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Priority</option>
              {CRM_FOLLOW_UP_PRIORITY_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
            </select>
            <select value={dailyTaskStatusFilter} onChange={(event) => setDailyTaskStatusFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
            </select>
            <select value={dailyTaskSourceFilter} onChange={(event) => setDailyTaskSourceFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Sources</option>
              {dailyTaskSourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
            </select>
            <select value={dailyTaskQuoteTypeFilter} onChange={(event) => setDailyTaskQuoteTypeFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Quote Types</option>
              <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
              <option value={GENERAL_QUOTE_TYPE}>General</option>
            </select>
          </div>
          {deferredDailyTaskSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search finds searchable leads across pages, then filters loaded tasks for the selected date/range.
              {globalSearchStatus.todayTaskSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.todayTaskSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.todayTaskSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {renderDailyWorkQueue()}

        {filteredDailyTasks.length === 0 ? (
          <div style={{ ...dashboardCardStyle, textAlign: "center", padding: isCompact ? 26 : 42, display: "grid", gap: 10 }}>
            <div style={{ fontSize: 22, fontWeight: 900 }}>No tasks due for this day</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              Follow-ups, installations, payment reminders, and project tasks will appear here when they are scheduled.
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.35fr) minmax(300px, 0.65fr)", gap: 16, alignItems: "start" }}>
            <div style={{ display: "grid", gap: 16 }}>
              {groupDefinitions.filter(([, , , tasks]) => tasks.length > 0).map(([key, title, helper, tasks, accent]) => (
                <section key={key} style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: accent }}>{title}</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>{helper}</div>
                    </div>
                    <div style={{ padding: "6px 10px", borderRadius: 999, background: `${accent}14`, color: accent, fontSize: 12, fontWeight: 900 }}>{tasks.length}</div>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>{tasks.map(renderTaskCard)}</div>
                </section>
              ))}
            </div>

            <aside style={{ ...dashboardCardStyle, position: isCompact ? "static" : "sticky", top: 16, display: "grid", gap: 12 }}>
              {selectedDailyTask ? (
                <>
                  <div style={{ fontSize: 12, color: "#2563eb", fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>Selected Task</div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{selectedDailyTask.title}</div>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                    {selectedDailyTask.customerName}{selectedDailyTask.quoteNo ? ` | Quote #${selectedDailyTask.quoteNo}` : ""}
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {[
                      ["Type", selectedDailyTask.typeLabel],
                      ["Due", formatReminderDateTime(selectedDailyTask.dueDateTime || selectedDailyTask.dueDate)],
                      ["Assigned", selectedDailyTask.assignedTo || "Unassigned"],
                      ["Source", selectedDailyTask.source || "-"],
                      ["Stage", selectedDailyTask.stageLabel || "-"],
                      ["Location", selectedDailyTask.locationLabel || selectedDailyTask.locationAddress || "-"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 }}>
                        <span style={{ color: dashboardMuted, fontWeight: 700 }}>{label}</span>
                        <span style={{ fontWeight: 800, textAlign: "right" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  {selectedDailyTask.description ? <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>{selectedDailyTask.description}</div> : null}
                  <div style={{ display: "grid", gap: 8 }}>
                    <button type="button" onClick={() => openDailyTaskDeal(selectedDailyTask)} style={{ ...btn("#0f172a"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>
                      {resolveTaskTarget(selectedDailyTask).label}
                    </button>
                    {selectedDailyTask.locationUrl || selectedDailyTask.siteLocation ? (
                      <button type="button" onClick={() => openSiteLocation(selectedDailyTask.siteLocation || selectedDailyTask)} style={{ ...btn("#2563eb"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>Navigate</button>
                    ) : null}
                    {selectedDailyTask.status !== "completed" ? (
                      <>
                        <button
                          type="button"
                          disabled={Boolean(markingDoneKey)}
                          onClick={() => markDailyTaskDone(selectedDailyTask)}
                          style={{
                            ...btn("#16a34a"),
                            width: "100%",
                            justifySelf: "stretch",
                            marginRight: 0,
                            opacity: markingDoneKey ? 0.7 : 1,
                          }}
                        >
                          {markingDoneKey ? "Saving..." : "Mark as Done"}
                        </button>
                        <button type="button" onClick={() => openDailyTaskReschedule(selectedDailyTask)} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>Snooze / Reschedule</button>
                      </>
                    ) : null}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>Select a task to see more details and quick actions.</div>
              )}
            </aside>
          </div>
        )}
      </div>
    );
  };

  const renderDailyTaskRescheduleModal = () => {
    if (!dailyTaskRescheduleState?.task) return null;

    const task = dailyTaskRescheduleState.task;

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 190, display: "grid", placeItems: "center", padding: 18 }}>
        <div style={{ ...dashboardCardStyle, width: "min(520px, 100%)", display: "grid", gap: 14, boxShadow: "0 28px 80px rgba(15,23,42,0.28)" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Reschedule Task</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>Update the original CRM source for {task.title}.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(150px, 1fr) minmax(150px, 1fr)", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
              Date
              <input type="date" value={dailyTaskRescheduleState.dueDate} onChange={(event) => setDailyTaskRescheduleState((prev) => prev ? { ...prev, dueDate: event.target.value } : prev)} style={{ ...input, width: "100%", minWidth: 0 }} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
              Time
              <input type="time" value={dailyTaskRescheduleState.dueTime} onChange={(event) => setDailyTaskRescheduleState((prev) => prev ? { ...prev, dueTime: event.target.value } : prev)} style={{ ...input, width: "100%", minWidth: 0 }} />
            </label>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setDailyTaskRescheduleState(null)} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>Cancel</button>
            <button type="button" onClick={saveDailyTaskReschedule} style={{ ...btn("#2563eb"), marginRight: 0 }}>Save New Time</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmReminderPopups = () => {
    if (!canAccessCrm || dueCrmReminders.length === 0) return null;

    const visibleReminders = dueCrmReminders.slice(0, 3);

    return (
      <div
        style={{
          position: "fixed",
          right: isCompact ? 12 : 20,
          bottom: isCompact ? 12 : 20,
          zIndex: 160,
          width: isCompact ? "calc(100vw - 24px)" : 380,
          maxWidth: "calc(100vw - 24px)",
          display: "grid",
          gap: 12,
        }}
        >
          {visibleReminders.map((reminder) => {
            const isFollowUpReminder =
              reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP;
            const headline =
              (isFollowUpReminder
                ? reminder.taskTitle
                : reminder.dealTitle || reminder.clientName) || "CRM Deal";
            const secondaryLine = [
              reminder.clientName || "",
              reminder.quoteNo ? `#${reminder.quoteNo}` : "",
            ]
              .filter(Boolean)
              .join(" | ");
            const repeatLabel = isFollowUpReminder
              ? CRM_FOLLOW_UP_REPEAT_OPTIONS.find(
                  (option) => option.key === reminder.repeatKey
                )?.label
              : CRM_REMINDER_REPEAT_OPTIONS.find(
                  (option) => option.key === reminder.repeatKey
                )?.label;

            return (
              <div
                key={reminder.id}
                style={{
                  ...dashboardCardStyle,
                  padding: 16,
                  borderRadius: 20,
                  background: isDark
                    ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
                    : "linear-gradient(135deg, #ffffff, #eff6ff)",
                  border: "1px solid rgba(37,99,235,0.18)",
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ display: "flex", gap: 10 }}>
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        minWidth: 42,
                        borderRadius: 14,
                        display: "grid",
                        placeItems: "center",
                        background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                        color: "#2563eb",
                      }}
                    >
                      <BellRing size={18} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: "#2563eb",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        {reminder.reminderLabel || "Reminder"}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4 }}>
                        {headline}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: dashboardMuted,
                          marginTop: 4,
                          lineHeight: 1.6,
                        }}
                      >
                        {secondaryLine || "Unknown client"}
                      </div>
                      {isFollowUpReminder && reminder.taskDescription ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: dashboardMuted,
                            marginTop: 4,
                            lineHeight: 1.6,
                          }}
                        >
                          {reminder.taskDescription}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => dismissCrmReminder(reminder)}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      borderRadius: 10,
                      border: "none",
                      display: "grid",
                      placeItems: "center",
                      background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                      color: textColor,
                      cursor: "pointer",
                    }}
                    title="Dismiss reminder"
                  >
                    <X size={15} />
                  </button>
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                    border: dashboardBorder,
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                    Due Date & Time
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>
                    {formatReminderDateTime(reminder.scheduledAt || reminder.nextTriggerAt)}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                    {reminder.repeatKey === "none"
                      ? "This reminder will stay active until you mark it done."
                      : `Repeats ${repeatLabel?.toLowerCase() || "daily"} until you mark it done.`}
                  </div>
                </div>

                {reminder.locationLabel || reminder.locationAddress || reminder.locationUrl ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                      Site Location
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>
                      {reminder.locationLabel || "Site"}
                    </div>
                    {reminder.locationAddress ? (
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        {reminder.locationAddress}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => openSiteLocation(reminder.siteLocation || reminder)}
                      style={{ ...btn("#2563eb"), marginRight: 0, justifySelf: "start" }}
                    >
                      Open Map
                    </button>
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openReminderDeal(reminder)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    Open Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissCrmReminder(reminder)}
                    style={{ ...btn("#0f172a"), marginRight: 0 }}
                  >
                    Close / Dismiss
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(markingDoneKey)}
                    onClick={() => markCrmReminderDone(reminder)}
                    style={{
                      ...btn("#16a34a"),
                      marginRight: 0,
                      opacity: markingDoneKey ? 0.7 : 1,
                    }}
                  >
                    <CheckCircle2 size={14} style={{ marginRight: 6 }} />
                    {markingDoneKey ? "Saving..." : "Mark as Done"}
                  </button>
                </div>
              </div>
            );
          })}

        {dueCrmReminders.length > visibleReminders.length ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
              border: dashboardBorder,
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            {dueCrmReminders.length - visibleReminders.length} more reminder
            {dueCrmReminders.length - visibleReminders.length === 1 ? "" : "s"} waiting.
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmLeads = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Leads</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                A clean lead-first workspace for customer follow-ups, assignment, and quote creation without mixing everything into the pipeline board.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(240px, 1.3fr) repeat(5, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            <input
              value={crmLeadSearch}
              onChange={(event) => setCrmLeadSearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search all leads by customer, phone, quote no, or source"
            />
            <select
              value={crmLeadSourceFilter}
              onChange={(event) => setCrmLeadSourceFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Sources</option>
              {crmLeadSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={crmLeadStageFilter}
              onChange={(event) => setCrmLeadStageFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Stages / Status</option>
              {crmLeadStageOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmLeadAssignedFilter}
              onChange={(event) => setCrmLeadAssignedFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Assignees</option>
              {crmLeadAssignedOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={crmLeadFromDate}
              onChange={(event) => setCrmLeadFromDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
            <input
              type="date"
              value={crmLeadToDate}
              onChange={(event) => setCrmLeadToDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
          </div>
          {deferredCrmLeadSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search finds searchable leads across pages. Older records may also need legacy loading.
              {globalSearchStatus.crmLeadSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmLeadSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmLeadSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div style={{ fontSize: 13, color: dashboardMuted }}>
              Showing {visibleCrmLeadDeals.length} of {crmFilteredLeadDeals.length} loaded matching lead
              {crmFilteredLeadDeals.length === 1 ? "" : "s"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Click a lead to open the details panel and keep follow-ups moving.
            </div>
          </div>

          {crmFilteredLeadDeals.length === 0 ? (
            <div
              style={{
                padding: 22,
                borderRadius: 20,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 8,
                justifyItems: "start",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>No leads match these filters</div>
              <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                Try widening the date range or clearing a filter to bring more leads back into view.
              </div>
            </div>
          ) : (
            visibleCrmLeadDeals.map((cardRecord) => {
              const deal = getLatestCrmLeadForCard(cardRecord);
              const active = selectedCrmDealId === deal.id;
              const stageColor = deal.crmStageColor || "#64748b";
              const checklistSummary = getChecklistSummary(deal);
              const followUpSummary = getFollowUpSummary(deal, { nowMillis: Date.now() });
              const installationSummary = getInstallationSummary(deal);
              const hasOverdueTasks = Number(followUpSummary.overdue || 0) > 0;

              return (
                <div
                  key={deal.id}
                  onClick={() => {
                    openCrmDealDetails(deal, { crmView: "leads" });
                  }}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: active
                      ? `1px solid ${isDark ? "rgba(96,165,250,0.6)" : "#93c5fd"}`
                      : dashboardBorder,
                    background: active
                      ? isDark
                        ? "rgba(37,99,235,0.1)"
                        : "#eff6ff"
                      : dashboardSoftBg,
                    display: "grid",
                    gap: 14,
                    cursor: "pointer",
                    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                    boxShadow: active
                      ? isDark
                        ? "0 18px 32px rgba(2,6,23,0.35)"
                        : "0 18px 32px rgba(37,99,235,0.08)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 14,
                      flexDirection: isCompact ? "column" : "row",
                    }}
                  >
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 18, fontWeight: 900 }}>
                        {deal.customerLabel || deal.crmTitle}
                      </div>
                      <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                        {deal.crmTitle}
                        {deal.companyOrProject ? ` | ${deal.companyOrProject}` : ""}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        {[deal.crmContact?.phone, deal.crmContact?.email]
                          .filter(Boolean)
                          .join(" | ") || "No phone or email added yet"}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div
                        style={{
                          padding: "7px 11px",
                          borderRadius: 999,
                          background: `${stageColor}18`,
                          color: stageColor,
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {deal.crmStageLabel}
                      </div>
                      <div
                        style={{
                          padding: "7px 11px",
                          borderRadius: 999,
                          background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
                          color: isDark ? "#e2e8f0" : "#334155",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {deal.source || "No Source"}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact
                        ? "1fr"
                        : "repeat(auto-fit, minmax(130px, 1fr))",
                      gap: 12,
                    }}
                    >
                      {[
                        ["Assigned", getTeamMemberLabel(deal.assignedToUserId, deal.assignedToNameSnapshot)],
                        ["Next Action", followUpSummary.nextFollowUpLabel || "Not scheduled"],
                        ["Checklist", checklistSummary.label],
                        [
                          "Install",
                          installationSummary.installationLabel || "-",
                        ],
                        ["Last Updated", deal.updatedMillis ? new Date(deal.updatedMillis).toLocaleString() : "-"],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                        style={{
                          padding: 12,
                          borderRadius: 16,
                          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                          border: dashboardBorder,
                        }}
                      >
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                          {value}
                        </div>
                      </div>
                    ))}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: hasOverdueTasks
                          ? isDark
                            ? "rgba(127,29,29,0.2)"
                            : "#fff1f2"
                          : isDark
                          ? "rgba(255,255,255,0.03)"
                          : "#ffffff",
                        border: hasOverdueTasks
                          ? `1px solid ${isDark ? "rgba(248,113,113,0.42)" : "#fecdd3"}`
                          : dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        Tasks
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 900,
                          marginTop: 6,
                          color: hasOverdueTasks ? (isDark ? "#fecdd3" : "#be123c") : textColor,
                        }}
                      >
                        {followUpSummary.label}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                        border: dashboardBorder,
                        display: "grid",
                        gap: 8,
                        alignContent: "space-between",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Linked Quote
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                          {deal.hasQuote
                            ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                            : "No quote yet"}
                        </div>
                      </div>
                      {deal.crmRecordType === "lead" ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openQuoteLinkModal(deal);
                          }}
                          style={{
                            ...btn(deal.hasQuote ? "#0f172a" : "#2563eb"),
                            marginRight: 0,
                            justifySelf: "start",
                            padding: "7px 10px",
                          }}
                        >
                          <Link2 size={13} style={{ marginRight: 6 }} />
                          {deal.hasQuote ? "Change" : "Link Quote"}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexDirection: isCompact ? "column" : "row",
                      alignItems: isCompact ? "stretch" : "center",
                    }}
                  >
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      Value: {currencySymbol(currency)} {Number(deal.expectedValue || 0).toFixed(2)}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <select
                        value={deal.assignedToUserId || ""}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          event.stopPropagation();
                          updateCrmDealAssignment(deal.id, event.target.value);
                        }}
                        disabled={!canAssignLeadOwnerAccess}
                        style={{
                          ...selectInput,
                          minWidth: 170,
                        }}
                      >
                        <option value="">Assign user</option>
                        {getAssignableTeamMembers(deal.assignedToUserId).map((member) => (
                          <option key={member.id} value={member.id} disabled={Boolean(member.exclusionReason)}>
                            {member.name || "Unnamed Team Member"}
                            {member.exclusionReason ? " | Needs reassignment" : ""}
                          </option>
                        ))}
                      </select>
                      {renderCrmMemberRosterStatus()}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCrmDealDetails(deal, { crmView: "leads" });
                        }}
                        style={{ ...btn(isDark ? "#1e293b" : "#0f172a", "#fff"), marginRight: 0 }}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCrmDealDetails(deal, { crmView: "leads" });
                        }}
                        style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        Mark Follow-up
                      </button>
                      {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (deal.hasQuote) {
                              openCrmQuoteForDeal(deal);
                            } else {
                              startQuoteFromCrmLead(deal);
                            }
                          }}
                          style={{ ...btn("#2563eb"), marginRight: 0 }}
                        >
                          {deal.hasQuote ? "Open Quote" : "Create Quote"}
                        </button>
                      ) : null}
                      {deal.crmRecordType === "lead" && deal.hasQuote ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openQuoteLinkModal(deal);
                          }}
                          style={{ ...btn("#0f172a"), marginRight: 0 }}
                        >
                          Change Quote
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {hasMoreCrmLeadDeals || crmLeadPaging.hasMore || crmLeadPaging.error ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
              <button
                type="button"
                disabled={crmLeadPaging.loading}
                onClick={() => hasMoreCrmLeadDeals
                  ? setCrmLeadsVisibleCount((prev) => prev + CRM_LEADS_PAGE_SIZE)
                  : crmLeadPagerRef.current?.loadPage(crmLeadPaging.error ? crmLeadPaging.retryMode : "recent")}
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                {crmLeadPaging.loading ? "Loading..." : crmLeadPaging.error ? "Retry CRM Page" : "Load More Leads"}
              </button>
            </div>
          ) : null}
          {renderCrmLegacyPageControl()}
        </div>
      </div>
    </div>
  );

  const renderCustomerLedger = () => {
    const selected = selectedCustomerLedger;
    const selectedQuotes = selected?.quotes || [];
    const selectedLeads = selected?.leads || [];
    const selectedProjects = selected?.id
      ? customerLedgerProjectsByCustomer[selected.id] || []
      : [];
    const selectedFollowUps = selectedLeads.flatMap((lead) =>
      normalizeCrmFollowUps(lead.followUps).map((followUp) => ({
        ...followUp,
        lead,
      }))
    );
    const selectedInstallations = selectedLeads.filter((lead) => lead.installationDate);
    const selectedCustomerSubcollectionSites = (
      selected?.id ? customerSitesByCustomerId[selected.id] || [] : []
    ).map((site) =>
      createSiteLocationDraft({
        id: site.siteId || site.id,
        label: site.siteName,
        addressText: site.siteAddress,
        siteContactName: site.siteContactName,
        siteContactPhone: site.siteContactPhone,
        notes: site.siteNotes,
      })
    );
    const selectedSiteLocations = Array.from(
      normalizeSiteLocations([
        ...(selected?.siteLocations || []),
        ...selectedCustomerSubcollectionSites,
      ])
        .reduce((map, location) => {
          const key = String(location.id || location.label || location.addressText || "").trim();
          if (!key) return map;
          map.set(key, { ...(map.get(key) || {}), ...location });
          return map;
        }, new Map())
        .values()
    );
    const activeSiteQuoteFilter =
      selected?.id && customerLedgerQuoteSiteFilter?.customerId === selected.id
        ? customerLedgerQuoteSiteFilter
        : null;
    const normalizeSiteMatchText = (value) => normalizeSearchCompact(value || "");
    const quoteMatchesSiteLocation = (quote, location) => {
      if (!quote || !location) return false;
      const quoteSiteId = String(
        quote.siteId || quote.customerSiteId || quote.projectSiteId || ""
      ).trim();
      const locationId = String(location.id || location.siteId || "").trim();
      if (quoteSiteId && locationId && quoteSiteId === locationId) return true;
      const quoteSiteName = normalizeSiteMatchText(
        quote.siteName || quote.projectName || ""
      );
      const locationName = normalizeSiteMatchText(location.label || location.siteName || "");
      const quoteSiteAddress = normalizeSiteMatchText(quote.siteAddress || "");
      const locationAddress = normalizeSiteMatchText(
        location.addressText || location.siteAddress || ""
      );
      return Boolean(
        (quoteSiteName && locationName && quoteSiteName === locationName) ||
          (quoteSiteAddress && locationAddress && quoteSiteAddress === locationAddress)
      );
    };
    const selectedQuotesForDisplay = activeSiteQuoteFilter
      ? selectedQuotes.filter((quote) => quoteMatchesSiteLocation(quote, activeSiteQuoteFilter))
      : selectedQuotes;
    const selectedSiteQuoteSummaries = selectedSiteLocations.reduce((map, location) => {
      const quotesForSite = selectedQuotes
        .filter((quote) => quoteMatchesSiteLocation(quote, location))
        .sort(
          (a, b) =>
            safeTimeMillis(b.updatedAt || b.createdAt) -
            safeTimeMillis(a.updatedAt || a.createdAt)
        );
      map.set(String(location.id || ""), {
        count: quotesForSite.length,
        latestQuote: quotesForSite[0] || null,
      });
      return map;
    }, new Map());
    const selectedLeadSiteLocations = selectedLeads.flatMap((lead) =>
      normalizeSiteLocations(lead.siteLocations || []).map((location) => ({
        ...location,
        leadId: lead.id,
        leadTitle: lead.title || lead.customerName || "CRM Deal",
      }))
    );
    const detailTabs = [
      { key: "overview", label: "Overview" },
      { key: "quotations", label: "Quotations" },
      { key: "deals", label: "CRM Deals" },
      { key: "projects", label: "Projects" },
      { key: "tasks", label: "Follow-ups" },
      { key: "locations", label: "Locations" },
      { key: "installations", label: "Installations" },
      { key: "payments", label: "Payments" },
      { key: "notes", label: "Notes & Activity" },
    ];
    const statCards = selected
      ? [
          ["Total Quotations", selected.totals?.totalQuotes || 0],
          [
            "Total Quoted Value",
            `${currencySymbol(currency)} ${Number(selected.totals?.totalQuoteValue || 0).toFixed(2)}`,
          ],
          ["Confirmed Quotes", selected.totals?.confirmedQuotes || 0],
          ["Open Deals", selected.openDealsCount || 0],
          ["Completed Jobs", selected.completedJobsCount || 0],
          ["Pending Follow-ups", selected.pendingFollowUpsCount || 0],
        ]
      : [];

    const createQuoteForCustomer = (quoteType = GENERAL_QUOTE_TYPE) => {
      if (!selected) return;
      if (quoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes) {
        setGeneralQuote((prev) =>
          normalizeGeneralQuote({
            ...prev,
            customerId: selected.id || "",
            customerKey: selected.customerKey || selected.id || "",
            customerName: selected.customerName || "",
            customerPhone: selected.phone || selected.whatsapp || "",
            customerWhatsapp: selected.whatsapp || "",
            customerEmail: selected.email || "",
            customerAddress: selected.address || "",
            customerCompanyName: selected.companyName || "",
            customerLedgerLinked: Boolean(selected.id),
            quoteFor: {
              ...(prev.quoteFor || {}),
              name: selected.customerName || "",
              company: selected.companyName || "",
              phone: selected.phone || selected.whatsapp || "",
              email: selected.email || "",
              address: selected.address || "",
            },
          })
        );
        setQuoteSubTab(GENERAL_QUOTE_TYPE);
        setRoutedTab("quote", GENERAL_QUOTE_TYPE);
        return;
      }

      setCustomerName(selected.customerName || "");
      setQuoteCustomerLink({
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        phone: selected.phone || "",
        whatsapp: selected.whatsapp || "",
        email: selected.email || "",
        address: selected.address || "",
        companyName: selected.companyName || "",
      });
      setQuoteSubTab(CURTAIN_QUOTE_TYPE);
      setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
    };

    const createLeadForCustomer = () => {
      if (!selected) return;
      const nextDraft = createCrmLeadDraftState(GENERAL_QUOTE_TYPE);
      setCrmLeadDraft({
        ...nextDraft,
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        customerPhone: selected.phone || selected.whatsapp || "",
        customerWhatsapp: selected.whatsapp || "",
        customerEmail: selected.email || "",
        customerAddress: selected.address || "",
        customerCompanyName: selected.companyName || "",
        title: selected.companyName || selected.customerName || "",
        companyOrProject: selected.companyName || "",
        source: selected.source || "",
        tags: (selected.tags || []).join(", "),
        contact: {
          ...nextDraft.contact,
          name: selected.customerName || "",
          phone: selected.phone || "",
          whatsapp: selected.whatsapp || "",
          email: selected.email || "",
          address: selected.address || "",
        },
        siteLocations: normalizeSiteLocations(selected.siteLocations || []),
      });
      setIsCrmLeadModalOpen(true);
    };

    const createLeadForCustomerLocation = (location) => {
      if (!selected) return;
      const nextDraft = createCrmLeadDraftState(GENERAL_QUOTE_TYPE);
      setCrmLeadDraft({
        ...nextDraft,
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        customerPhone: selected.phone || selected.whatsapp || "",
        customerWhatsapp: selected.whatsapp || "",
        customerEmail: selected.email || "",
        customerAddress: location.addressText || selected.address || "",
        customerCompanyName: selected.companyName || "",
        title: selected.companyName || selected.customerName || location.label || "",
        companyOrProject: selected.companyName || "",
        source: selected.source || "",
        tags: (selected.tags || []).join(", "),
        contact: {
          ...nextDraft.contact,
          name: selected.customerName || "",
          phone: selected.phone || "",
          whatsapp: selected.whatsapp || "",
          email: selected.email || "",
          address: location.addressText || selected.address || "",
        },
        siteLocations: normalizeSiteLocations([{ ...location, isPrimary: true }]),
      });
      setIsCrmLeadModalOpen(true);
    };

    const saveCustomerLocationDraft = async () => {
      if (!selected?.id) return;
      const hasLocationContent = [
        customerLocationDraft.label,
        customerLocationDraft.addressText,
        customerLocationDraft.siteContactName,
        customerLocationDraft.siteContactPhone,
        customerLocationDraft.googleMapsUrl,
        customerLocationDraft.notes,
      ].some((value) => String(value || "").trim());

      if (!hasLocationContent) {
        notify("Add a label, address, or map link before saving.", "warning");
        return;
      }

      const nextLocation = normalizeSiteLocation({
        ...customerLocationDraft,
        id: customerLocationDraft.id || createClientId("customer-site-location"),
        createdAt: customerLocationDraft.createdAt || new Date().toISOString(),
        createdBy: customerLocationDraft.createdBy || user?.uid || user?.email || "",
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || user?.email || "",
      });
      const nextLocations = selectedSiteLocations.some(
        (location) => location.id === nextLocation.id
      )
        ? selectedSiteLocations.map((location) =>
            location.id === nextLocation.id
              ? nextLocation
              : nextLocation.isPrimary
              ? { ...location, isPrimary: false }
              : location
          )
        : [
            ...selectedSiteLocations.map((location) =>
              nextLocation.isPrimary ? { ...location, isPrimary: false } : location
            ),
            nextLocation,
          ];
      const saved = await saveCustomerSiteLocations(selected.id, nextLocations);
      if (saved) {
        notify("Site location saved", "success");
        setCustomerLocationDraft(createSiteLocationDraft());
      }
    };

    const editCustomerLocation = (location) => {
      setCustomerLocationDraft(createSiteLocationDraft(location));
      setCustomerLedgerDetailTab("locations");
    };

    const deleteCustomerLocation = async (locationId) => {
      if (!selected?.id) return;
      const saved = await saveCustomerSiteLocations(
        selected.id,
        selectedSiteLocations.filter((location) => location.id !== locationId)
      );
      if (saved) {
        try {
          await setDoc(
            doc(db, "users", workspaceUid, "customers", selected.id, "sites", locationId),
            {
              active: false,
              deleted: true,
              updatedAt: serverTimestamp(),
              updatedBy: user?.uid || user?.email || "",
            },
            { merge: true }
          );
          setCustomerSitesByCustomerId((prev) => ({
            ...prev,
            [selected.id]: (prev[selected.id] || []).filter(
              (site) => String(site.siteId || site.id) !== String(locationId)
            ),
          }));
          invalidateFirestoreReadCacheByPrefix(
            `users/${workspaceUid}/customers/${selected.id}/sites`
          );
        } catch (siteDeleteError) {
          devWarn("CUSTOMER_SITE_SUBCOLLECTION_DELETE_SKIPPED", {
            customerId: selected.id,
            locationId,
            errorCode: siteDeleteError?.code || "",
            errorMessage: siteDeleteError?.message || String(siteDeleteError),
          });
        }
        notify("Site location deleted", "success");
      }
    };

    const copyLeadLocationToCustomer = async (location) => {
      if (!selected?.id) return;
      const saved = await saveCustomerSiteLocations(selected.id, [
        ...selectedSiteLocations,
        normalizeSiteLocation({
          ...location,
          id: createClientId("customer-site-location"),
          isPrimary: selectedSiteLocations.length === 0 || location.isPrimary === true,
          createdAt: new Date().toISOString(),
          createdBy: user?.uid || user?.email || "",
          updatedAt: new Date().toISOString(),
          updatedBy: user?.uid || user?.email || "",
        }),
      ]);
      if (saved) notify("Location saved to customer ledger", "success");
    };

    const createOrLinkDealFromQuote = async (quote, mode = "link") => {
      if (!quote?.id || !selected) return;
      try {
        await ensureSavedQuoteCrmDeal(
          {
            ...quote,
            customerId: selected.id,
            customerKey: selected.customerKey || selected.id || "",
            customerName: selected.customerName,
            customerPhone: selected.phone || selected.whatsapp || "",
            customerWhatsapp: selected.whatsapp || "",
            customerEmail: selected.email || "",
            customerAddress: selected.address || "",
            customerCompanyName: selected.companyName || "",
          },
          {
            quoteNo: quote.quoteNo || quote.id,
            quoteType: resolveQuoteType(quote),
            totalValue: Number(getNormalizedQuoteTotal(quote) || 0),
            customerFields: {
              customerId: selected.id,
              customerKey: selected.customerKey || selected.id || "",
              customerName: selected.customerName,
              customerPhone: selected.phone || selected.whatsapp || "",
              customerWhatsapp: selected.whatsapp || "",
              customerEmail: selected.email || "",
              customerAddress: selected.address || "",
              customerCompanyName: selected.companyName || "",
            },
            forceCreate: mode === "create",
            forceLinkExisting: mode === "link",
            respectSaveQuoteOnly: false,
            showToast: true,
          }
        );
        setCustomerLedgerDetailTab("deals");
        Object.keys(customerLedgerCrmDealsLoadRef.current || {}).forEach((key) => {
          if (key.includes(`:${selected.id}:`)) {
            delete customerLedgerCrmDealsLoadRef.current[key];
          }
        });
        await loadCustomerLedgerCrmDeals(selected.id);
      } catch (error) {
        devError("Failed to create or link CRM deal from quote:", error);
        notify("Could not create CRM deal from quote. Please try again.", "error");
      }
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div
          style={{
            ...dashboardCardStyle,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.94))"
              : "linear-gradient(135deg, #f8fafc, #ffffff)",
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                CRM Customer Ledger
              </div>
              <div style={{ fontSize: isCompact ? 24 : 32, fontWeight: 900, letterSpacing: "-0.04em", marginTop: 6 }}>
                One customer, every quote and job history.
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8, lineHeight: 1.7 }}>
                Permanent customer records connect quotations, CRM deals, tasks, installations, payments, and activity without creating duplicate leads for repeat customers.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => selected && createQuoteForCustomer(GENERAL_QUOTE_TYPE)} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                Create Quote
              </button>
              <button type="button" onClick={createLeadForCustomer} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                Create Lead
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1fr) 180px 180px",
              gap: 10,
            }}
          >
            <input
              value={customerLedgerSearch}
              onChange={(event) => setCustomerLedgerSearch(event.target.value)}
              style={input}
              placeholder="Search all customers by name, phone, email, source, tag, or location"
            />
            <select
              value={customerLedgerSourceFilter}
              onChange={(event) => setCustomerLedgerSourceFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {customerLedgerSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={customerLedgerSortBy}
              onChange={(event) => setCustomerLedgerSortBy(event.target.value)}
              style={selectInput}
            >
              <option value="latest">Latest activity</option>
              <option value="value">Highest value</option>
              <option value="quotes">Most quotes</option>
              <option value="name">Name</option>
            </select>
          </div>
          {deferredCustomerLedgerSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all customer ledger records, including older customers.
              {globalSearchStatus.customerLedgerSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.customerLedgerSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.customerLedgerSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(280px, 0.85fr) minmax(0, 1.6fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
            {filteredCustomerLedgerSummaries.length === 0 ? (
              <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
                No customers yet. Create a quotation or lead to start building your customer ledger.
              </div>
            ) : (
              filteredCustomerLedgerSummaries.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => {
                    setSelectedCustomerLedgerId(customer.id);
                    setCustomerLedgerDetailTab("overview");
                    setCustomerLedgerQuoteSiteFilter(null);
                    loadCustomerLedgerQuotes(customer.id);
                    loadCustomerLedgerCrmDeals(customer.id);
                  }}
                  style={{
                    ...dashboardCardStyle,
                    textAlign: "left",
                    cursor: "pointer",
                    border:
                      customer.id === selected?.id
                        ? "1px solid rgba(37,99,235,0.45)"
                        : dashboardBorder,
                    boxShadow:
                      customer.id === selected?.id
                        ? "0 18px 42px rgba(37,99,235,0.12)"
                        : dashboardCardStyle.boxShadow,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 900, overflowWrap: "anywhere" }}>
                        {customer.customerName}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, overflowWrap: "anywhere" }}>
                        {[customer.phone || customer.whatsapp, customer.email, customer.address].filter(Boolean).join(" | ") || "No contact details yet"}
                      </div>
                    </div>
                    {customer.duplicateWarning ? (
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#b45309", background: "#fef3c7", borderRadius: 999, padding: "4px 8px", alignSelf: "flex-start" }}>
                        Possible duplicate
                      </span>
                    ) : null}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                    {[
                      ["Quotes", customer.totals?.totalQuotes || 0],
                      ["Value", `${currencySymbol(currency)} ${Number(customer.totals?.totalQuoteValue || 0).toFixed(0)}`],
                      ["Open Deals", customer.openDealsCount || 0],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: 10, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted }}>{label}</div>
                        <div style={{ fontSize: 14, fontWeight: 900, overflowWrap: "anywhere" }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </button>
              ))
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            {!selected ? (
              <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
                Select a customer to view the full ledger.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexDirection: isCompact ? "column" : "row" }}>
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.04em" }}>
                        {selected.customerName}
                      </div>
                      <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                        {[selected.phone || selected.whatsapp, selected.email, selected.address].filter(Boolean).join(" | ") || "No contact details yet"}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                        {(selected.tags || []).map((tag) => (
                          <span key={tag} style={{ padding: "4px 9px", borderRadius: 999, background: dashboardSoftBg, border: dashboardBorder, fontSize: 11, fontWeight: 800 }}>
                            {tag}
                          </span>
                        ))}
                        {selected.source ? (
                          <span style={{ padding: "4px 9px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800 }}>
                            {selected.source}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignSelf: isCompact ? "stretch" : "flex-start" }}>
                      <button type="button" onClick={() => openCustomerLedgerEditModal(selected)} style={{ ...btn("#7c3aed"), marginRight: 0 }}>
                        Edit Customer
                      </button>
                      <button type="button" onClick={() => createQuoteForCustomer(GENERAL_QUOTE_TYPE)} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                        General Quote
                      </button>
                      <button type="button" onClick={() => createQuoteForCustomer(CURTAIN_QUOTE_TYPE)} style={{ ...btn("#0f172a"), marginRight: 0 }}>
                        Curtain Quote
                      </button>
                      <button type="button" onClick={createLeadForCustomer} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                        Create Lead
                      </button>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr 1fr" : "repeat(6, minmax(0, 1fr))", gap: 10 }}>
                    {statCards.map(([label, value]) => (
                      <div key={label} style={{ padding: 14, borderRadius: 18, background: dashboardSoftBg, border: dashboardBorder, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 900, marginTop: 5, overflowWrap: "anywhere" }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {detailTabs.map((detailTab) => (
                      <button
                        key={detailTab.key}
                        type="button"
                        onClick={() => {
                          setCustomerLedgerDetailTab(detailTab.key);
                          if (detailTab.key === "quotations") {
                            setCustomerLedgerQuoteSiteFilter(null);
                            loadCustomerLedgerQuotes(selected.id);
                          }
                          if (detailTab.key === "locations") {
                            loadCustomerSitesForQuote(selected.id);
                            loadCustomerLedgerQuotes(selected.id);
                          }
                          if (["deals", "tasks", "installations"].includes(detailTab.key)) {
                            loadCustomerLedgerCrmDeals(selected.id);
                          }
                          if (detailTab.key === "projects") {
                            loadCustomerLedgerProjects(selected.id);
                          }
                        }}
                        style={{
                          ...btn(
                            customerLedgerDetailTab === detailTab.key ? "#2563eb" : isDark ? "#0f172a" : "#e2e8f0",
                            customerLedgerDetailTab === detailTab.key || isDark ? "#fff" : "#0f172a"
                          ),
                          marginRight: 0,
                          padding: "8px 11px",
                        }}
                      >
                        {detailTab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {customerLedgerDetailTab === "overview" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Customer Timeline</div>
                    {[...selectedQuotes, ...selectedLeads]
                      .sort((a, b) => getRecordSortTime(b) - getRecordSortTime(a))
                      .slice(0, 12)
                      .map((record) => (
                        <div key={`${record.quoteNo || record.id}-${record.crmRecordType || "record"}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                          <div style={{ fontWeight: 800 }}>
                            {record.quoteNo ? `Quote #${record.quoteNo}` : record.title || record.crmTitle || "CRM Deal"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                            {record.quoteNo ? getQuoteTypeLabel(resolveQuoteType(record)) : record.stageNameSnapshot || record.status || "Lead"} | {new Date(getRecordSortTime(record) || Date.now()).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "quotations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 900 }}>
                          {activeSiteQuoteFilter
                            ? `Quotations for ${activeSiteQuoteFilter.label || activeSiteQuoteFilter.siteName || "Site"}`
                            : "Quotations"}
                        </div>
                        {activeSiteQuoteFilter?.addressText ? (
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                            {activeSiteQuoteFilter.addressText}
                          </div>
                        ) : null}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => openCustomerLedgerQuoteLinkModal(selected)}
                          style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}
                        >
                          <Link2 size={14} style={{ marginRight: 6 }} />
                          Link Existing Quotation
                        </button>
                        {activeSiteQuoteFilter ? (
                          <button
                            type="button"
                            onClick={() => setCustomerLedgerQuoteSiteFilter(null)}
                            style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Show All Quotes
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {customerLedgerQuotesLoadingId === selected.id ? (
                      <div style={{ color: dashboardMuted }}>Loading quotations for this customer...</div>
                    ) : null}
                    {selectedQuotesForDisplay.length === 0 && customerLedgerQuotesLoadingId !== selected.id ? (
                      <div style={{ color: dashboardMuted }}>
                        {activeSiteQuoteFilter
                          ? "No quotations linked to this site yet. Create a quote for this site to keep the history together."
                          : "No linked quotations yet. Create a quote or save an existing quote against this customer."}
                      </div>
                    ) : selectedQuotesForDisplay.map((quote) => {
                      const isUnlinkedQuoteMatch =
                        quote.customerLedgerUnlinkedMatch === true ||
                        String(quote.customerLedgerMatchType || "").startsWith("legacy_exact") ||
                        String(quote.customerLedgerMatchType || "") === "customerKey";
                      return (
                      <div key={quote.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div>
                          <div style={{ fontWeight: 900 }}>#{quote.quoteNo || quote.id}</div>
                          <div style={{ fontSize: 12, color: dashboardMuted }}>
                            {getQuoteTypeLabel(resolveQuoteType(quote))} | {resolveQuoteDateForInput(quote) || toDateInputValue(quote.createdAt) || "-"} | {quote.status || "draft"}
                          </div>
                          {isUnlinkedQuoteMatch ? (
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 7, padding: "5px 9px", borderRadius: 999, background: isDark ? "rgba(251,146,60,0.14)" : "#fff7ed", color: "#c2410c", fontSize: 11, fontWeight: 900 }}>
                              Unlinked quote match
                            </div>
                          ) : null}
                          {quote.siteName || quote.siteAddress ? (
                            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5 }}>
                              Site: {[quote.siteName, quote.siteAddress].filter(Boolean).join(" - ")}
                            </div>
                          ) : null}
                          <div style={{ fontSize: 11, color: quote.crmLeadId || quote.leadId || quote.crm?.leadId ? "#16a34a" : "#f97316", fontWeight: 800, marginTop: 5 }}>
                            {quote.crmLeadId || quote.leadId || quote.crm?.leadId ? "Linked to CRM deal" : "Quote only - not in pipeline yet"}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <strong>{currencySymbol(currency)} {Number(getNormalizedQuoteTotal(quote) || 0).toFixed(2)}</strong>
                          <button type="button" onClick={() => loadFullQuote(quote)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                            Open Quote
                          </button>
                          {isUnlinkedQuoteMatch ? (
                            <button
                              type="button"
                              onClick={() => linkExistingQuoteToCustomerLedger(quote)}
                              style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}
                            >
                              <Link2 size={14} style={{ marginRight: 6 }} />
                              Link to customer
                            </button>
                          ) : null}
                          {!(quote.crmLeadId || quote.leadId || quote.crm?.leadId) ? (
                            <>
                              <button
                                type="button"
                                onClick={() => createOrLinkDealFromQuote(quote, "link")}
                                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Link to Deal
                              </button>
                              <button
                                type="button"
                                onClick={() => createOrLinkDealFromQuote(quote, "create")}
                                style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Create CRM Deal
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "deals" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>CRM Deals</div>
                    {customerLedgerCrmDealsLoadingId === selected.id ? (
                      <div style={{ color: dashboardMuted }}>Loading CRM deals for this customer...</div>
                    ) : null}
                    {selectedLeads.length === 0 && customerLedgerCrmDealsLoadingId !== selected.id ? (
                      <div style={{ color: dashboardMuted, lineHeight: 1.6 }}>
                        No CRM deals linked yet. Create a lead or link a quotation to start the sales pipeline.
                      </div>
                    ) : selectedLeads.map((leadRecord) => {
                      const lead = getLatestCrmLeadForCard(leadRecord);
                      const checklistSummary = getChecklistSummary(lead);
                      const followUpSummary = getFollowUpSummary(lead, { nowMillis: Date.now() });
                      const installationSummary = getInstallationSummary(lead);

                      return (
                        <div key={lead.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                          <div style={{ display: "grid", gap: 5 }}>
                            <div style={{ fontWeight: 900 }}>{lead.title || lead.customerName || "CRM Lead"}</div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              {getQuoteTypeLabel(lead.pipelineType)} | {isCrmRecordUnavailable(lead) ? "Archived / unavailable" : lead.stageNameSnapshot || lead.status || "-"} | {lead.assignedToNameSnapshot || "Unassigned"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              Checklist {checklistSummary.label} | Tasks {followUpSummary.label} | Install {installationSummary.installationLabel || "-"}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setRoutedTab("crm");
                              openCrmDealDetails(lead, { crmView: "leads" });
                            }}
                            style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Open Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "projects" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Projects / Orders</div>
                    {customerLedgerProjectsLoadingId === selected.id ? (
                      <div style={{ color: dashboardMuted }}>Loading projects for this customer...</div>
                    ) : null}
                    {selectedProjects.length === 0 && customerLedgerProjectsLoadingId !== selected.id ? (
                      <div style={{ color: dashboardMuted, lineHeight: 1.6 }}>
                        No projects yet. Projects are created after a quote is confirmed as an order.
                      </div>
                    ) : selectedProjects.map((project) => {
                      const balance = Number(project.paymentSummary?.balanceAmount ?? project.balanceAmount ?? 0) || 0;
                      const openSnags = Number(project.openSnagCount ?? project.snagSummary?.openCount ?? 0) || 0;
                      const installationLabel =
                        project.installationStatusLabel ||
                        getProjectInstallationStatusLabel(project.installationStatus);
                      const latestActivity =
                        project.lastActivityLabel ||
                        project.stageLabel ||
                        getProjectStageLabel(project.stage);

                      return (
                        <div
                          key={project.projectId || project.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                            padding: 12,
                            borderRadius: 16,
                            background: dashboardSoftBg,
                            border: dashboardBorder,
                          }}
                        >
                          <div style={{ display: "grid", gap: 5 }}>
                            <div style={{ fontWeight: 900 }}>
                              {project.projectTitle || project.quoteNo || "Project"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              {getProjectStageLabel(project.stage)} | {project.status || "open"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              Installation {installationLabel}
                              {project.installationScheduledAt
                                ? ` | ${formatCrmDate(project.installationScheduledAt)}`
                                : ""}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              Balance {currencySymbol(currency)} {balance.toFixed(2)} | Open snags {openSnags}
                            </div>
                            <div style={{ fontSize: 11, color: dashboardMuted }}>
                              Latest: {latestActivity}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setRoutedTab("crm");
                              setCrmView("projects");
                              setSelectedCrmProjectId(project.projectId || project.id || "");
                              setCrmProjectDrawerTab("overview");
                              setCrmProjectRecords((prev) => {
                                const projectId = String(project.projectId || project.id || "");
                                if (!projectId) return prev;
                                const exists = prev.some(
                                  (item) => String(item.projectId || item.id || "") === projectId
                                );
                                return exists
                                  ? prev.map((item) =>
                                      String(item.projectId || item.id || "") === projectId
                                        ? { ...item, ...project }
                                        : item
                                    )
                                  : [project, ...prev];
                              });
                            }}
                            style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Open Project
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "tasks" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Follow-ups / Tasks</div>
                    {selectedFollowUps.length === 0 ? (
                      <div style={{ color: dashboardMuted, lineHeight: 1.6 }}>
                        No follow-ups yet. Add one from CRM so this customer is not forgotten.
                      </div>
                    ) : selectedFollowUps.map((followUp) => (
                      <div key={`${followUp.lead.id}-${followUp.id}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <strong>{followUp.title || "Follow-up"}</strong>
                          <span style={{ fontSize: 12, fontWeight: 800, color: followUp.status === CRM_FOLLOW_UP_STATUS_DONE ? "#16a34a" : "#f97316" }}>
                            {followUp.status === CRM_FOLLOW_UP_STATUS_DONE ? "Done" : "Pending"}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5 }}>
                          {followUp.lead.title || followUp.lead.customerName || "CRM Deal"} | Due {followUp.dueDate || "-"} {followUp.dueTime || ""}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "locations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexDirection: isCompact ? "column" : "row" }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 900 }}>Locations</div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                          Customer-level site links stay available across future quotes, leads, and installations.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCustomerLocationDraft(createSiteLocationDraft())}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Add Location
                      </button>
                    </div>

                    {selectedSiteLocations.length === 0 ? (
                      <div
                        style={{
                          padding: 16,
                          borderRadius: 18,
                          background: dashboardSoftBg,
                          border: dashboardBorder,
                          color: dashboardMuted,
                          lineHeight: 1.7,
                        }}
                      >
                        <strong style={{ color: textColor }}>No customer locations saved yet.</strong>
                        <br />
                        Add a location here, or copy one from a linked CRM deal below.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 10 }}>
                        {selectedSiteLocations.map((location) => {
                          const siteQuoteSummary =
                            selectedSiteQuoteSummaries.get(String(location.id || "")) || {};
                          const latestSiteQuote = siteQuoteSummary.latestQuote || null;
                          const latestSiteQuoteLabel = latestSiteQuote
                            ? `Latest #${latestSiteQuote.quoteNo || latestSiteQuote.id || "-"}`
                            : "No linked quote yet";
                          const latestSiteQuoteStatus = latestSiteQuote
                            ? latestSiteQuote.stageLabel ||
                              latestSiteQuote.stageNameSnapshot ||
                              latestSiteQuote.status ||
                              "draft"
                            : "";

                          return (
                            <div
                              key={location.id}
                              style={{
                                padding: 14,
                                borderRadius: 18,
                                background: dashboardSoftBg,
                                border: dashboardBorder,
                                display: "grid",
                                gap: 10,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                <div>
                                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                    <strong>{location.label || "Site Location"}</strong>
                                    {location.isPrimary ? (
                                      <span style={{ padding: "4px 8px", borderRadius: 999, background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 800 }}>
                                        Primary
                                      </span>
                                    ) : null}
                                  </div>
                                  {location.addressText ? (
                                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                                      {location.addressText}
                                    </div>
                                  ) : null}
                                  {location.notes ? (
                                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                                      Notes: {location.notes}
                                    </div>
                                  ) : null}
                                </div>
                                <div
                                  style={{
                                    padding: "8px 10px",
                                    borderRadius: 14,
                                    background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                                    border: dashboardBorder,
                                    minWidth: 150,
                                  }}
                                >
                                  <div style={{ fontSize: 11, color: dashboardMuted }}>Linked quotes</div>
                                  <div style={{ fontSize: 16, fontWeight: 900, marginTop: 3 }}>
                                    {siteQuoteSummary.count || 0}
                                  </div>
                                  <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                                    {latestSiteQuoteLabel}
                                    {latestSiteQuoteStatus ? ` | ${latestSiteQuoteStatus}` : ""}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button type="button" onClick={() => openSiteLocation(location)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                  Open Map
                                </button>
                                <button type="button" onClick={() => copySiteLocationLink(location)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                                  Copy Link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCustomerLedgerQuoteSiteFilter({
                                      ...location,
                                      customerId: selected.id,
                                    });
                                    setCustomerLedgerDetailTab("quotations");
                                    loadCustomerLedgerQuotes(selected.id);
                                  }}
                                  style={{ ...btn("#0ea5e9"), marginRight: 0, padding: "8px 10px" }}
                                >
                                  View Quotes
                                </button>
                                <button type="button" onClick={() => editCustomerLocation(location)} style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}>
                                  Edit
                                </button>
                                <button type="button" onClick={() => createLeadForCustomerLocation(location)} style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}>
                                  Create Lead Using This Location
                                </button>
                                <button type="button" onClick={() => deleteCustomerLocation(location.id)} style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                        border: dashboardBorder,
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 850 }}>
                        {customerLocationDraft.id ? "Edit Customer Location" : "Add Customer Location"}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                        <input
                          value={customerLocationDraft.label}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              label: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Location label"
                        />
                        <input
                          value={customerLocationDraft.googleMapsUrl}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              googleMapsUrl: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Google Maps / WhatsApp location link"
                        />
                        <input
                          value={customerLocationDraft.siteContactName}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              siteContactName: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Site contact name"
                        />
                        <input
                          value={customerLocationDraft.siteContactPhone}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              siteContactPhone: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Site contact phone"
                        />
                      </div>
                      <textarea
                        value={customerLocationDraft.addressText}
                        onChange={(event) =>
                          setCustomerLocationDraft((prev) => ({
                            ...prev,
                            addressText: event.target.value,
                          }))
                        }
                        style={{ ...input, minHeight: 72, resize: "vertical" }}
                        placeholder="Address / landmark"
                      />
                      <textarea
                        value={customerLocationDraft.notes}
                        onChange={(event) =>
                          setCustomerLocationDraft((prev) => ({
                            ...prev,
                            notes: event.target.value,
                          }))
                        }
                        style={{ ...input, minHeight: 62, resize: "vertical" }}
                        placeholder="Notes"
                      />
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                        <input
                          type="checkbox"
                          checked={customerLocationDraft.isPrimary === true}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              isPrimary: event.target.checked,
                            }))
                          }
                        />
                        Primary Location
                      </label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={saveCustomerLocationDraft} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                          Save Location
                        </button>
                        <button type="button" onClick={() => setCustomerLocationDraft(createSiteLocationDraft())} style={{ ...btn("#64748b"), marginRight: 0 }}>
                          Clear
                        </button>
                      </div>
                    </div>

                    {selectedLeadSiteLocations.length > 0 ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 850 }}>
                          Locations saved on linked CRM deals
                        </div>
                        {selectedLeadSiteLocations.map((location) => (
                          <div key={`${location.leadId}-${location.id}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <div>
                              <strong>{location.label || "Site Location"}</strong>
                              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                                {location.leadTitle} {location.addressText ? `| ${location.addressText}` : ""}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button type="button" onClick={() => openSiteLocation(location)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                Open Map
                              </button>
                              <button type="button" onClick={() => copyLeadLocationToCustomer(location)} style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 10px" }}>
                                Save to Customer Ledger
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "installations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Installations</div>
                    {selectedInstallations.length === 0 ? (
                      <div style={{ color: dashboardMuted }}>No installation dates linked yet.</div>
                    ) : selectedInstallations.map((lead) => {
                      const installationLocation =
                        getPrimarySiteLocation(lead) || getPrimarySiteLocation(selected);
                      return (
                        <div key={lead.id} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                          <strong>{lead.title || lead.customerName || "Installation"}</strong>
                          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                            {toDateInputValue(lead.installationDate) || "-"} | {installationLocation?.addressText || lead.contact?.address || lead.crmContact?.address || selected.address || "No address"}
                          </div>
                          {installationLocation ? (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button type="button" onClick={() => openSiteLocation(installationLocation)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                Navigate
                              </button>
                              <button type="button" onClick={() => copySiteLocationLink(installationLocation)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                                Copy Link
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "payments" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Payments / Ledger</div>
                    <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 160px 2fr auto", gap: 10, alignItems: "center" }}>
                      <input type="number" value={customerLedgerPaymentDraft.amount} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, amount: event.target.value }))} style={input} placeholder="Payment amount" />
                      <input type="date" value={customerLedgerPaymentDraft.date} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, date: event.target.value }))} style={input} />
                      <input value={customerLedgerPaymentDraft.notes} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, notes: event.target.value }))} style={input} placeholder="Notes" />
                      <button type="button" onClick={addCustomerLedgerPayment} disabled={isSavingCustomerPayment} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                        {isSavingCustomerPayment ? "Saving..." : "Add Payment"}
                      </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Quoted</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.totalQuoteValue || 0).toFixed(2)}</strong>
                      </div>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Received</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.receivedAmount || 0).toFixed(2)}</strong>
                      </div>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Pending</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.pendingAmount || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                ) : null}

                {customerLedgerDetailTab === "notes" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Notes & Activity</div>
                    <div style={{ color: dashboardMuted, lineHeight: 1.7 }}>
                      {selected.notes || "No customer notes yet. Quote, lead, task, and installation activity appears across the other tabs."}
                    </div>
                    {selected.duplicateWarning ? (
                      <div style={{ padding: 12, borderRadius: 16, background: "#fef3c7", color: "#92400e", fontWeight: 700 }}>
                        Possible duplicate customer detected by {selected.duplicateWarning}. Open matching records before merging manually.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmTaskHistory = () => {
    const formatTaskHistoryDateTime = (value) => {
      const millis = safeTimeMillis(value);
      if (!millis) return "-";
      return new Date(millis).toLocaleString();
    };

    const selectedRecord = selectedCrmTaskHistoryRecord;

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Task History</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Completed reminders, checklist items, and installation follow-ups stay permanently recorded here for this workspace.
              </div>
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              {crmFilteredTaskHistoryRecords.length} visible
              {" / "}
              {crmTaskHistoryRecords.length} total completed item
              {crmTaskHistoryRecords.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(240px, 1.4fr) repeat(4, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            <input
              value={crmTaskHistorySearch}
              onChange={(event) => setCrmTaskHistorySearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search all task history by task, customer, quote no, or completed by"
            />
            <select
              value={crmTaskHistoryTypeFilter}
              onChange={(event) => setCrmTaskHistoryTypeFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Task Types</option>
              {Object.entries(TASK_HISTORY_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={crmTaskHistoryCompletedByFilter}
              onChange={(event) =>
                setCrmTaskHistoryCompletedByFilter(event.target.value)
              }
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Team Members</option>
              {crmTaskHistoryCompletedByOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={crmTaskHistoryFromDate}
              onChange={(event) => setCrmTaskHistoryFromDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
            <input
              type="date"
              value={crmTaskHistoryToDate}
              onChange={(event) => setCrmTaskHistoryToDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
          </div>
          {deferredCrmTaskHistorySearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks indexed task history beyond the recent page.
              {globalSearchStatus.crmTaskHistorySearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmTaskHistorySearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmTaskHistorySearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(320px, 0.95fr) minmax(0, 1.25fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
            {crmFilteredTaskHistoryRecords.length === 0 ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.7,
                }}
              >
                No completed task history matches these filters yet.
              </div>
            ) : (
              crmFilteredTaskHistoryRecords.map((record) => {
                const active = selectedRecord?.id === record.id;
                const typeLabel =
                  TASK_HISTORY_TYPE_LABELS[record.taskType] || "Completed Task";

                return (
                  <button
                    key={record.id}
                    type="button"
                    onClick={() => setSelectedCrmTaskHistoryId(record.id)}
                    style={{
                      ...dashboardCardStyle,
                      padding: 16,
                      background: active
                        ? isDark
                          ? "rgba(37,99,235,0.14)"
                          : "#eff6ff"
                        : dashboardSoftBg,
                      border: active
                        ? `1px solid ${isDark ? "rgba(96,165,250,0.6)" : "#93c5fd"}`
                        : dashboardBorder,
                      textAlign: "left",
                      display: "grid",
                      gap: 10,
                      cursor: "pointer",
                      boxShadow: active
                        ? isDark
                          ? "0 16px 30px rgba(2,6,23,0.35)"
                          : "0 16px 30px rgba(37,99,235,0.08)"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{record.taskTitle}</div>
                      <div
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          background: isDark ? "rgba(22,163,74,0.2)" : "#dcfce7",
                          color: isDark ? "#86efac" : "#166534",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {typeLabel}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      {record.customerName || "Unnamed Customer"}
                      {record.quoteNo ? ` | Quote #${record.quoteNo}` : ""}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                        gap: 8,
                        fontSize: 12,
                        color: dashboardMuted,
                      }}
                    >
                      <div>Due: {formatTaskHistoryDateTime(record.dueDate)}</div>
                      <div>Completed: {formatTaskHistoryDateTime(record.completedAt)}</div>
                      <div>
                        By:{" "}
                        {record.completedByName ||
                          record.completedByEmail ||
                          record.completedByUid ||
                          "Workspace User"}
                      </div>
                      <div>Deal: {record.dealId || record.leadId || record.quoteId || "-"}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
            {!selectedRecord ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.7,
                }}
              >
                Select a completed task to review the full audit trail and jump back into the related customer record.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                      {TASK_HISTORY_TYPE_LABELS[selectedRecord.taskType] || "Completed Task"}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>
                      {selectedRecord.taskTitle}
                    </div>
                    <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                      {selectedRecord.taskDescription || "No extra description was stored for this completion."}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCrmTaskHistoryDeal(selectedRecord)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    {resolveTaskTarget(selectedRecord).label}
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {[
                    ["Customer / Deal", selectedRecord.customerName || "Unnamed Customer"],
                    ["Quote No", selectedRecord.quoteNo || "No linked quote"],
                    ["Due Date", formatTaskHistoryDateTime(selectedRecord.dueDate)],
                    ["Completed At", formatTaskHistoryDateTime(selectedRecord.completedAt)],
                    [
                      "Completed By",
                      selectedRecord.completedByName ||
                        selectedRecord.completedByEmail ||
                        selectedRecord.completedByUid ||
                        "Workspace User",
                    ],
                    [
                      "Area",
                      TASK_HISTORY_SOURCE_LABELS[selectedRecord.sourceModule] ||
                        TASK_HISTORY_SOURCE_LABELS[TASK_HISTORY_SOURCE_CRM],
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmRestrictedSection = (title, message) => (
    <section style={{ ...dashboardCardStyle, display: "grid", gap: 8 }}>
      <div style={{ fontSize: 18, fontWeight: 950, color: textColor }}>{title}</div>
      <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
        {message}
      </div>
    </section>
  );

  const renderCrmWorkspace = () => (
    <div style={dashboardPanelStyle}>
      <div
        style={{
          ...dashboardCardStyle,
          marginBottom: 18,
          background: isDark
            ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.92))"
            : "linear-gradient(135deg, #eff6ff, #ffffff)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 18,
            flexDirection: isCompact ? "column" : "row",
          }}
        >
          <div style={{ maxWidth: 700 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 10,
              }}
            >
              CRM Workspace
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: isCompact ? 24 : 30,
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              Tailored sales, production, and installation operations for furnishing projects
            </h2>
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                lineHeight: 1.6,
                color: dashboardMuted,
              }}
            >
              Curtains and general quotations stay on their own pipelines, while Saved Quotes, checklist progress, and installation scheduling all remain connected to the same CRM state.
            </div>
          </div>

          <div
            style={{
              minWidth: isCompact ? "100%" : 320,
              display: "grid",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() => openNewCrmLeadModal()}
              style={{
                ...btn("#2563eb"),
                marginRight: 0,
                justifySelf: "stretch",
                width: "100%",
              }}
            >
              + New Lead
            </button>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.75)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                CRM Scope
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>
                {crmScopeLabel}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {crmScopeSubtitle}
              </div>
            </div>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.75)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                {crmScope === CRM_SCOPE_ALL ? "CRM deals loaded" : "Pipeline deals loaded"}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>{crmScopeDeals.length}</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Loaded: {crmOpenDeals.length} open | {crmPipelineSummary.completed} complete | {crmPipelineSummary.cancelled} cancelled
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        {[CRM_SCOPE_ALL, ...CRM_PIPELINE_TYPES.filter((type) =>
          type === GENERAL_QUOTE_TYPE ? canAccessGeneralQuotes : canAccessCurtainQuotes
        )].map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => {
              setCrmScope(type);
              if (type !== CRM_SCOPE_ALL) {
                setCrmPipelineType(type);
              }
              setCrmStageFilter("all");
              setCrmSourceFilter("all");
            }}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                crmScope === type
                  ? "#2563eb"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                crmScope === type
                  ? "#ffffff"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {type === CRM_SCOPE_ALL ? "All" : getQuoteTypeLabel(type)}
          </button>
        ))}
      </div>

      {crmView === "settings" && crmScope === CRM_SCOPE_ALL ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 16,
            padding: isCompact ? 16 : 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            flexDirection: isCompact ? "column" : "row",
            alignItems: isCompact ? "stretch" : "center",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
              Choose the pipeline to manage
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              The Sales Pipeline is universal. These legacy quote workflow pipeline settings remain type-specific for quotation operations.
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {CRM_PIPELINE_TYPES.filter((type) =>
              type === GENERAL_QUOTE_TYPE ? canAccessGeneralQuotes : canAccessCurtainQuotes
            ).map((type) => (
              <button
                key={`pipeline-${type}`}
                type="button"
                onClick={() => {
                  setCrmPipelineType(type);
                  setCrmStageFilter("all");
                  setCrmSourceFilter("all");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: dashboardBorder,
                  background:
                    crmPipelineType === type
                      ? isDark
                        ? "rgba(37,99,235,0.22)"
                        : "#dbeafe"
                      : isDark
                      ? "rgba(255,255,255,0.04)"
                      : "#ffffff",
                  color:
                    crmPipelineType === type
                      ? "#2563eb"
                      : isDark
                      ? "#cbd5e1"
                      : "#334155",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {getQuoteTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        {crmNavItems.map((item) => {
          const Icon = item.icon;
          const active = crmView === item.key;
          const navBadgeCount =
            item.key === "overview"
              ? crmNotificationCounts.today
              : item.key === "attention" && canViewAttentionRequiredWorkspace
              ? crmNotificationCounts.attention
              : 0;
          const navBadgeValue = formatInAppBadgeCount(navBadgeCount);
          const navBadgeLabel = navBadgeValue
            ? `${item.label}: ${navBadgeValue} ${navBadgeValue === "1" ? "item" : "items"} need attention`
            : "";

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setCrmView(item.key)}
              title={navBadgeLabel || undefined}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 14,
                border: dashboardBorder,
                background: active
                  ? isDark
                    ? "rgba(37,99,235,0.2)"
                    : "#dbeafe"
                  : isDark
                  ? "rgba(255,255,255,0.04)"
                  : "#ffffff",
                color: active
                  ? "#2563eb"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              <Icon size={16} strokeWidth={2} />
              <span>{item.label}</span>
              {navBadgeValue ? (
                <span
                  aria-label={navBadgeLabel}
                  style={{
                    minWidth: 24,
                    height: 22,
                    padding: "0 8px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: item.key === "attention" ? "#dc2626" : "#2563eb",
                    color: "#ffffff",
                    fontSize: 11,
                    fontWeight: 900,
                    lineHeight: 1,
                    boxShadow: "0 8px 18px rgba(15,23,42,0.14)",
                  }}
                >
                  {navBadgeValue}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {crmNotificationChips.length ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: -10,
            marginBottom: 20,
            alignItems: "center",
          }}
        >
          {crmNotificationChips.map((chip) => {
            const chipBadgeValue = formatInAppBadgeCount(chip.count);
            const chipLabel = `${chip.label}: ${chipBadgeValue} ${chipBadgeValue === "1" ? "item" : "items"} need attention`;
            return (
              <button
                key={chip.key}
                type="button"
                title={chipLabel}
                aria-label={chipLabel}
                onClick={() => {
                  if (chip.targetBucket) {
                    setCrmAttentionActiveBucket(chip.targetBucket);
                  }
                  setCrmView(chip.targetView || "overview");
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 10px",
                  borderRadius: 999,
                  border: dashboardBorder,
                  background: isDark ? "rgba(15,23,42,0.58)" : "#ffffff",
                  color: isDark ? "#cbd5e1" : "#334155",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 850,
                  boxShadow: isDark
                    ? "0 10px 24px rgba(2,6,23,0.24)"
                    : "0 10px 24px rgba(15,23,42,0.06)",
                }}
              >
                <span>{chip.label}</span>
                <span
                  style={{
                    minWidth: 24,
                    height: 22,
                    padding: "0 8px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: chip.accent,
                    color: "#ffffff",
                    fontSize: 11,
                    fontWeight: 900,
                    lineHeight: 1,
                  }}
                >
                  {chipBadgeValue}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {crmView === "overview"
        ? renderCrmOverview()
        : crmView === "board"
        ? canViewAllCrmRecords
          ? renderCrmBoard()
          : renderCrmRestrictedSection(
              "You do not have permission to view this section.",
              "Your assigned customer work is shown in Today."
            )
        : crmView === "leads"
        ? canViewAllCrmRecords
          ? renderCrmLeads()
          : renderCrmRestrictedSection(
              "You do not have permission to view this section.",
              "Your assigned customer work is shown in Today."
            )
        : crmView === "list"
        ? canViewAllCrmRecords
          ? renderCrmList()
          : renderCrmRestrictedSection(
              "You do not have permission to view this section.",
              "Your assigned customer work is shown in Today."
            )
        : crmView === "projects"
        ? renderCrmProjects()
        : crmView === "attention"
        ? canViewAttentionRequiredWorkspace
          ? renderCrmAttentionRequired()
          : renderCrmRestrictedSection(
              "This section is available to managers and admins.",
              "Your assigned work is shown in Today."
            )
        : crmView === CRM_PIPELINE_REVIEW_TAB
        ? canViewPipelineReview
          ? renderCrmPipelineReview()
          : renderCrmRestrictedSection(
              "Pipeline Review is available to managers and admins.",
              "Please ask an admin or manager if you need access to workspace review reports."
            )
        : crmView === "history"
        ? renderCrmTaskHistory()
        : crmView === "settings"
        ? canManageCrmSettings
          ? renderCrmSettings()
          : renderCrmRestrictedSection(
              "CRM settings are only available to admins.",
              "Please ask an admin to change CRM settings."
            )
        : renderCrmOverview()}

      {!isCompact && (crmView === "board" || crmView === "list" || crmView === "leads")
        ? renderCrmDetailDrawer()
        : null}

      {renderCrmLeadModal()}
    </div>
  );



  if (workspaceBootstrapError) {
    const debugText = JSON.stringify(workspaceBootstrapDebug || {}, null, 2);
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 640,
            textAlign: "center",
            display: "grid",
            gap: 14,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#dc2626" }}>
            Workspace Load Error
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            We could not load your workspace
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.65 }}>
            {workspaceBootstrapError.message ||
              "Please refresh the page. If this continues, contact support."}
          </div>
          {IS_DEV_UI_ENABLED ? (
            <details
              style={{
                textAlign: "left",
                background: isDark ? "rgba(15,23,42,0.58)" : "#f8fafc",
                border: dashboardBorder,
                borderRadius: 14,
                padding: 14,
                fontSize: 12,
                lineHeight: 1.6,
                color: isDark ? "#cbd5e1" : "#334155",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 900 }}>
                Startup debug details
              </summary>
              <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0" }}>
                {debugText}
              </pre>
            </details>
          ) : null}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              icon={RotateCcw}
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Reload FabriQuote
            </Button>
            {IS_DEV_UI_ENABLED ? (
              <Button
                icon={FileText}
                variant="secondary"
                onClick={() => {
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(debugText);
                  }
                }}
              >
                Copy Error Details
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (isResolvingWorkspace || accountLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 520,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Workspace
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            Loading your FabriQuote workspace
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            We&apos;re connecting your login to the correct company data, permissions, and shared team workspace.
          </div>
        </div>
      </div>
    );
  }

  if (!hasActiveSubscription || accessibleTabs.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 560,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Subscription Access
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            This FabriQuote account is not active
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            {account?.name || "This workspace"} is currently {account?.subscriptionStatus || "inactive"}. Plan-based access is locked until the subscription is active.
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Button icon={RotateCcw} variant="secondary" onClick={() => signOut(auth)}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isWorkspaceAccessRevoked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 560,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Team Access
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            Your workspace access is currently disabled
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            Please contact the workspace owner if you need access restored. Your sign-in still works, but FabriQuote is respecting the latest team permissions for this company.
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Button icon={RotateCcw} variant="secondary" onClick={() => signOut(auth)}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div
      className="fabriquote-main-shell"
      style={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        background: pageBg,
        color: textColor,
        fontFamily:
          "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <Sidebar
        activeTab={tab}
        onTabChange={(nextTab) => {
          setRoutedTab(nextTab);
          if (isMobile) {
            setIsNavOpen(false);
          }
        }}
        isDark={isDark}
        onToggleDarkMode={() => setIsDark((value) => !value)}
        onSignOut={() => signOut(auth)}
        isMobile={isMobile}
        mobileOpen={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        forceExpanded={isTablet}
        brandName={businessName || "FabriQuote"}
        visibleItemIds={accessibleTabs}
        itemBadges={sidebarItemBadges}
      />

      {/* ---------------- MAIN PANEL ---------------- */}
      <main
        className="fabriquote-main-content"
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          padding: isMobile ? 14 : isTablet ? 18 : 24,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            position: isMobile ? "sticky" : "static",
            top: isMobile ? 0 : "auto",
            zIndex: 10,
            background: isMobile ? pageBg : "transparent",
            paddingBottom: isMobile ? 8 : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isMobile ? (
              <button
                type="button"
                onClick={() => setIsNavOpen(true)}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background: isDark ? "#0f172a" : "#ffffff",
                  color: textColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                }}
              >
                <Menu size={18} strokeWidth={2.25} />
              </button>
            ) : null}
            {logoURL ? (
              <img
                src={logoURL}
                alt="logo"
                style={{
                  height: isMobile ? 48 : isTablet ? 72 : 130,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: isMobile ? 14 : 18,
                  fontWeight: 700,
                  opacity: 0.5,
                }}
              >
                {businessName || "Upload Logo"}
              </div>
            )}
          </div>
        </div>

        {IS_DEV_UI_ENABLED && firebaseHealthWarning ? (
          <div
            style={{
              border: "1px solid rgba(245, 158, 11, 0.45)",
              background: isDark ? "rgba(120, 53, 15, 0.28)" : "#fffbeb",
              color: isDark ? "#fde68a" : "#78350f",
              borderRadius: 16,
              padding: "12px 14px",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
            }}
          >
            <div>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                Firebase quota/billing issue detected
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.45 }}>
                {firebaseHealthWarning.message} Check Firebase Usage and Billing.
              </div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                {firebaseHealthWarning.code || "no-code"} ·{" "}
                {firebaseHealthWarning.detectedAt}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setFirebaseHealthWarning(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => {
                  clearFirestoreReadBackoffState();
                  setFirebaseHealthWarning(null);
                  notify("Firebase quota warning state cleared for this session.", "success");
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Clear warning state
              </button>
            </div>
          </div>
        ) : null}

        {IS_DEV_UI_ENABLED ? (() => {
          const monitor =
            typeof window !== "undefined"
              ? window.__FQ_READ_MONITOR__ || window.__FQ_READ_COUNT__
              : null;
          const startupTrace =
            monitor?.startupTrace ||
            (typeof window !== "undefined"
              ? window.__FQ_STARTUP_READ_TRACE__ || []
              : []);
          const byPath = monitor?.byPath || {};
          const activeListeners = monitor?.activeListeners || {};
          const topEntries = Object.entries(byPath)
            .sort(([, a], [, b]) => Number(b.reads || 0) - Number(a.reads || 0))
            .slice(0, 10);
          const activeListenerCount = Object.keys(activeListeners).length;

          return (
            <details
              style={{
                border: "1px solid rgba(59,130,246,0.24)",
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                borderRadius: 16,
                padding: 12,
                boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                Firebase Read Health · {Number(monitor?.total || 0)} reads ·{" "}
                {activeListenerCount} active listener
                {activeListenerCount === 1 ? "" : "s"}
              </summary>
              <div
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 8,
                  fontSize: 12,
                  color: isDark ? "#cbd5e1" : "#475569",
                }}
              >
                <div>
                  Startup trace rows: {startupTrace.length} · refresh tick:{" "}
                  {firebaseReadHealthTick}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      clearFirestoreReadBackoffState();
                      setFirebaseHealthWarning(null);
                      notify("Firebase quota warning state cleared for this session.", "success");
                    }}
                    style={{
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      color: textColor,
                      borderRadius: 10,
                      padding: "7px 10px",
                      cursor: "pointer",
                      fontWeight: 800,
                    }}
                  >
                    Clear Firebase quota warning state
                  </button>
                </div>
                {topEntries.length === 0 ? (
                  <div>No tracked Firestore reads in this session yet.</div>
                ) : (
                  topEntries.map(([path, entry]) => (
                    <div
                      key={path}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "1fr auto",
                        gap: 8,
                        padding: 8,
                        borderRadius: 10,
                        background: isDark ? "rgba(30,41,59,0.75)" : "#f8fafc",
                      }}
                    >
                      <span style={{ wordBreak: "break-word" }}>{path}</span>
                      <span>
                        {Number(entry.reads || 0)} reads ·{" "}
                        {Number(entry.cacheHits || 0)} cache ·{" "}
                        {Number(entry.listenerEvents || 0)} snapshots
                      </span>
                    </div>
                  ))
                )}
                {activeListenerCount > 0 ? (
                  <div>
                    Active listeners: {Object.keys(activeListeners).join(", ")}
                  </div>
                ) : null}
              </div>
            </details>
          );
        })() : null}

              {/* ---------------- DASHBOARD TAB ---------------- */}
{tab === "dashboard" && (
  <>
    <div style={dashboardPanelStyle}>
      <div
        style={{
          ...dashboardCardStyle,
          marginBottom: 20,
          background: isDark
            ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
            : "linear-gradient(135deg, #eff6ff, #ffffff)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            justifyContent: "space-between",
            gap: 18,
          }}
        >
          <div style={{ maxWidth: 620 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 10,
              }}
            >
              Executive Dashboard
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: isCompact ? 24 : 32,
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              Trusted quote intelligence for curtains and general quotations
            </h2>
          </div>

          <div
            style={{
              minWidth: isCompact ? "100%" : 280,
              display: "grid",
              gap: 10,
              alignSelf: "stretch",
            }}
          >
            <button
              type="button"
              onClick={() => openBusinessDashboardDrilldown("activeQuotes")}
              style={{
                width: "100%",
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                border: dashboardBorder,
                cursor: "pointer",
                textAlign: "left",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Recent Active Quotes
              </div>
              <div style={{ fontSize: 28, fontWeight: 900 }}>
                <CountUp end={dashboardMetricData.activeQuotes.metricValue} duration={1.2} />
              </div>
            </button>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Selected Period
              </div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{dashboardPeriodLabel}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { key: "business", label: "Business Overview" },
          { key: "crm", label: "CRM Overview" },
        ].map((mode) => (
          <button
            key={mode.key}
            type="button"
            onClick={() => setDashboardMode(mode.key)}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                dashboardMode === mode.key
                  ? isDark
                    ? "rgba(37,99,235,0.2)"
                    : "#dbeafe"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                dashboardMode === mode.key
                  ? "#2563eb"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {dashboardMode === "business" ? (
      <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { k: "today", l: "Today" },
          { k: "thisWeek", l: "This Week" },
          { k: "thisMonth", l: "This Month" },
          { k: "lastMonth", l: "Last Month" },
          { k: "thisYear", l: "This Year" },
          { k: "custom", l: "Custom" },
        ].map((period) => (
          <button
            key={period.k}
            onClick={() => setPeriodType(period.k)}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                periodType === period.k
                  ? "#2563eb"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                periodType === period.k
                  ? "#ffffff"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {period.l}
          </button>
        ))}
      </div>

      {periodType === "custom" && (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <input
            type="date"
            value={customRange.start || ""}
            onChange={(e) =>
              setCustomRange((prev) => ({
                ...prev,
                start: e.target.value,
              }))
            }
            style={input}
          />
          <input
            type="date"
            value={customRange.end || ""}
            onChange={(e) =>
              setCustomRange((prev) => ({
                ...prev,
                end: e.target.value,
              }))
            }
            style={input}
          />
        </div>
      )}

      {IS_DEV_UI_ENABLED ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            fontSize: 12,
            lineHeight: 1.6,
            background: isDark ? "rgba(15,23,42,0.92)" : "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 6 }}>
            Dashboard Analytics Diagnostics
          </div>
          <div>
            KPI source: <strong>{dashboardAnalyticsState.diagnostics?.source || "unknown"}</strong>
            {" "} | monthKey: <strong>{dashboardAnalyticsState.diagnostics?.monthKey || "-"}</strong>
            {" "} | calendar: <strong>{dashboardAnalyticsState.diagnostics?.calendarMonthKey || "-"}</strong>
          </div>
          <div>
            Reads: analytics_monthly{" "}
            {dashboardAnalyticsState.diagnostics?.reads?.analyticsMonthly ?? 0},
            client_monthly {dashboardAnalyticsState.diagnostics?.reads?.clientMonthly ?? 0},
            installation_index {dashboardAnalyticsState.diagnostics?.reads?.installationIndex ?? 0}
          </div>
          <div>
            Loaded: {dashboardAnalyticsState.diagnostics?.loadedAt || "-"} | summary updated:{" "}
            {dashboardAnalyticsState.diagnostics?.lastUpdated
              ? new Date(dashboardAnalyticsState.diagnostics.lastUpdated).toLocaleString()
              : "-"}
          </div>
          {dashboardAnalyticsState.diagnostics?.source !== "analytics_indexes" ? (
            <div style={{ color: "#dc2626", fontWeight: 900 }}>
              Warning: dashboard KPI source is not analytics/index documents.
            </div>
          ) : null}
          {adminDataAuditState.checkedAt || adminDataAuditState.running ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 900 }}>Workspace Data Audit</div>
              {adminDataAuditState.report?.mismatchSummary ? (
                <div>
                  Mismatches:{" "}
                  <strong>
                    {adminDataAuditState.report.mismatchSummary.totalMismatchCount}
                  </strong>{" "}
                  | source quotes:{" "}
                  <strong>{adminDataAuditState.report.sourceDocumentCounts?.quotations || 0}</strong>
                </div>
              ) : adminDataAuditState.progress?.collection ? (
                <div>
                  Scanning {adminDataAuditState.progress.collection}:{" "}
                  {adminDataAuditState.progress.scanned}
                </div>
              ) : null}
              {Object.entries(adminDataAuditState.collections || {}).map(([name, result]) => (
                <div key={name}>
                  {name}:{" "}
                  {result.ok
                    ? `total ${result.totalCount}, sample ${result.sampleCount}`
                    : `failed ${result.errorCode || ""} ${result.errorMessage || ""}`}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {dashboardAnalyticsError ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark ? "rgba(220,38,38,0.14)" : "#fef2f2",
            border: "1px solid rgba(220,38,38,0.24)",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 6 }}>
            Could not load dashboard right now
          </div>
          <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
            Try again in a moment. Your quotations and customer data are still safe.
          </div>
          {IS_DEV_UI_ENABLED ? (
            <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626", fontWeight: 800 }}>
              {dashboardAnalyticsState.diagnostics?.errorCode || "error"}:{" "}
              {dashboardAnalyticsState.diagnostics?.errorMessage || "Unknown dashboard analytics error"}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : isTablet
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(3, minmax(0, 1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {dashboardKpis.map((kpi) => (
          <button
            key={kpi.key}
            type="button"
            onClick={kpi.clickable ? kpi.onClick : undefined}
            style={{
              ...dashboardCardStyle,
              width: "100%",
              cursor: kpi.clickable ? "pointer" : "default",
              position: "relative",
              overflow: "hidden",
              textAlign: "left",
              transition: "transform 0.18s ease, box-shadow 0.18s ease",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, ${kpi.accent}18, transparent 58%)`,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                {kpi.label}
              </div>
              <div
                style={{
                  fontSize: isCompact ? 26 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                {typeof kpi.value === "number" ? (
                  <CountUp end={kpi.value} duration={1.1} />
                ) : (
                  kpi.value
                )}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>{kpi.note}</div>
            </div>
          </button>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.65fr) minmax(280px, 0.95fr)",
          gap: 16,
          marginBottom: 20,
          alignItems: "start",
        }}
      >
        <div style={dashboardCardStyle}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Quote Creation Trend</div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Created vs confirmed over the last 6 months
            </div>
          </div>
          <MeasuredChartFrame
            active={hasQuoteCreationTrendData}
            height={quoteTrendChartHeight}
            style={getChartShellStyle(quoteTrendChartHeight)}
            fallback={renderChartEmptyState("No quote trend data yet")}
          >
            {({ width, height }) => (
                <AreaChart
                  width={width}
                  height={height}
                  data={monthlyChartData}
                  margin={chartMargin}
                  onClick={(state) => {
                    const entry = state?.activePayload?.[0]?.payload;
                    if (entry?.key) {
                      openBusinessDashboardDrilldown("monthCreated", {
                        monthKey: entry.key,
                        monthLabel: entry.month,
                      });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <defs>
                    <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="confirmedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#243041" : "#e2e8f0"} />
                  <XAxis
                    dataKey="month"
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={8}
                  />
                  <YAxis
                    allowDecimals={false}
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    width={isMobile ? 32 : 45}
                  />
                  <Tooltip contentStyle={dashboardTooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="#2563eb"
                    fill="url(#createdFill)"
                    strokeWidth={2.5}
                    name="Created"
                  />
                  <Area
                    type="monotone"
                    dataKey="confirmed"
                    stroke="#10b981"
                    fill="url(#confirmedFill)"
                    strokeWidth={2.5}
                    name="Confirmed"
                  />
                </AreaChart>
            )}
          </MeasuredChartFrame>
          {hasQuoteCreationTrendData
            ? renderInlineLegend([
                { label: "Created", color: "#2563eb" },
                { label: "Confirmed", color: "#10b981" },
              ])
            : null}
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
              Business Health
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {dashboardInsights.map((insight) => (
                <button
                  key={insight.key}
                  type="button"
                  onClick={insight.onClick}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    color: textColor,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                    {insight.label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{insight.value}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {insight.description}
                  </div>
                  <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 8 }}>
                    Click to view records
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
              Quote Type Split
            </div>
            {hasQuoteTypeDistributionData
              ? renderQuoteTypeSplit(quoteTypeDistributionChartData, (entry) =>
                  openBusinessDashboardDrilldown("quoteType", {
                    quoteType:
                      entry.name === "General Quotation"
                        ? GENERAL_QUOTE_TYPE
                        : CURTAIN_QUOTE_TYPE,
                  })
                )
              : renderChartEmptyState("No quote type data yet")}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr)",
          gap: 16,
          marginBottom: 20,
          alignItems: "start",
        }}
      >
        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
            Stage Distribution
          </div>
          <div
            style={{
              width: "100%",
              minWidth: 0,
            }}
          >
            {hasStageDistributionData ? (
              renderHorizontalMetricBars(stageDistributionChartData, {
                valueKey: "value",
                valueLabel: "quotes",
                emptyTitle: "No stage data yet",
                onItemClick: (entry) =>
                  openBusinessDashboardDrilldown("stage", {
                    stageKey: entry.key,
                    stageLabel: entry.stageLabel,
                    title: entry.fullLabel,
                  }),
              })
            ) : (
              renderChartEmptyState("No stage data yet")
            )}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
            Revenue Trend
          </div>
          <MeasuredChartFrame
            active={hasRevenueTrendData}
            height={revenueChartHeight}
            style={getChartShellStyle(revenueChartHeight)}
            fallback={renderChartEmptyState("No revenue data yet")}
          >
            {({ width, height }) => (
                <BarChart
                  width={width}
                  height={height}
                  data={monthlyChartData}
                  margin={chartMargin}
                  onClick={(state) => {
                    const entry = state?.activePayload?.[0]?.payload;
                    if (entry?.key) {
                      openBusinessDashboardDrilldown("monthRevenue", {
                        monthKey: entry.key,
                        monthLabel: entry.month,
                      });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#243041" : "#e2e8f0"} />
                  <XAxis
                    dataKey="month"
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={8}
                  />
                  <YAxis
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    width={isMobile ? 34 : 58}
                    tickFormatter={(value) =>
                      Number(value) >= 100000
                        ? `${Math.round(Number(value) / 100000)}L`
                        : Number(value) >= 1000
                        ? `${Math.round(Number(value) / 1000)}k`
                        : value
                    }
                  />
                  <Tooltip
                    contentStyle={dashboardTooltipStyle}
                    formatter={(value) => [formatCurrencyDisplay(value, currency), "Revenue"]}
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#2563eb"
                    name="Revenue"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={42}
                    minPointSize={3}
                  />
                </BarChart>
            )}
          </MeasuredChartFrame>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : isTablet
            ? "repeat(2, minmax(0, 1fr))"
            : "1.15fr 0.95fr 0.9fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div style={dashboardCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Recent Activity</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Most recently updated quotations
              </div>
            </div>
            <button
              onClick={() => setTab("saved")}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              View All
            </button>
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {recentQuotes.map((quote) => (
              <button
                key={quote.id}
                type="button"
                onClick={() => loadFullQuote(quote)}
                style={{
                  border: dashboardBorder,
                  background: dashboardSoftBg,
                  borderRadius: 16,
                  padding: 14,
                  textAlign: "left",
                  cursor: "pointer",
                  minWidth: 0,
                }}
              >
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: 12, minWidth: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{quote.customerLabel}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      #{quote.quoteNo || "-"} | {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ textAlign: isMobile ? "left" : "right", minWidth: 0 }}>
                    <div style={{ fontWeight: 800 }}>
                      {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {quote.updatedMillis
                        ? new Date(quote.updatedMillis).toLocaleDateString()
                        : "-"}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span
                    style={{
                      flex: "0 0 auto",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background:
                        quote.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
                      color:
                        quote.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                    }}
                  >
                    {quote.quoteTypeResolved === GENERAL_QUOTE_TYPE
                      ? "General Quotation"
                      : "Curtains"}
                  </span>
                  <span
                    style={{
                      flex: "0 0 auto",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background: quote.isConfirmed ? "#dcfce7" : "#fef3c7",
                      color: quote.isConfirmed ? "#166534" : "#92400e",
                    }}
                  >
                    {quote.isConfirmed ? "Confirmed" : "Draft"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
            Top Customers
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {dashboardAnalyticsNotReady ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No customers found for this period.
              </div>
            ) : topCustomers.length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No customers found for this period.
              </div>
            ) : (
            topCustomers.map((customer, index) => (
              <button
                key={customer.name}
                type="button"
                onClick={() => openDashboardCustomer(customer)}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: 14,
                  borderRadius: 16,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  minWidth: 0,
                  color: textColor,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {index + 1}. {customer.name}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {customer.count} quote{customer.count === 1 ? "" : "s"}
                  </div>
                  <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 4 }}>
                    Open Customer
                  </div>
                </div>
                <div style={{ fontWeight: 900, flex: "0 0 auto", textAlign: "right", whiteSpace: "nowrap", fontSize: isMobile ? 13 : 14 }}>
                  {formatCurrencyDisplay(customer.value || 0, currency)}
                </div>
              </button>
            ))
            )}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
            Upcoming Installations
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {dashboardAnalyticsNotReady ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No upcoming installations.
              </div>
            ) : upcomingInstallations.length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No upcoming installations.
              </div>
            ) : (
              upcomingInstallations.map((quote) => (
                <button
                  key={quote.id}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    textAlign: "left",
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{quote.customerLabel}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    #{quote.quoteNo || "-"}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    {new Date(quote.installationMillis).toLocaleDateString()}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={dashboardCardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isCompact ? "flex-start" : "center",
            flexDirection: isCompact ? "column" : "row",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Installation Calendar</div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Scheduled jobs remain connected to the same live saved-quote data.
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => changeCalendarMonth(-1)}
              style={{
                border: dashboardBorder,
                background: dashboardSoftBg,
                borderRadius: 12,
                padding: "8px 12px",
                cursor: "pointer",
                color: textColor,
              }}
            >
              Prev
            </button>
            <div style={{ fontWeight: 700, minWidth: 150, textAlign: "center" }}>
              {MONTH_NAMES[calendarMonth.month]} {calendarMonth.year}
            </div>
            <button
              type="button"
              onClick={() => changeCalendarMonth(1)}
              style={{
                border: dashboardBorder,
                background: dashboardSoftBg,
                borderRadius: 12,
                padding: "8px 12px",
                cursor: "pointer",
                color: textColor,
              }}
            >
              Next
            </button>
          </div>
        </div>

        {(() => {
          const { year, month } = calendarMonth;
          const firstDay = new Date(year, month, 1);
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const startWeekday = firstDay.getDay();
          const weeks = [];
          let current = 1 - startWeekday;

          for (let w = 0; w < 6; w++) {
            const row = [];
            for (let d = 0; d < 7; d++, current++) {
              if (current < 1 || current > daysInMonth) {
                row.push(null);
              } else {
                const cellDate = new Date(year, month, current);
                const key =
                  cellDate.getFullYear() +
                  "-" +
                  String(cellDate.getMonth() + 1).padStart(2, "0") +
                  "-" +
                  String(cellDate.getDate()).padStart(2, "0");

                row.push({
                  day: current,
                  key,
                  installs: installationByDate[key] || [],
                });
              }
            }
            weeks.push(row);
          }

          return (
            <div style={{ display: "grid", gap: 8 }}>
              {weeks.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  {row.map((cell, cellIndex) =>
                    cell ? (
                      <button
                        key={cellIndex}
                        type="button"
                        onClick={() => {
                          if (!cell.installs.length) return;
                          setSelectedInstallDate(cell.key);
                          openBusinessDashboardDrilldown("installationDate", {
                            dateKey: cell.key,
                          });
                        }}
                        style={{
                          minHeight: isCompact ? 62 : 76,
                          padding: 10,
                          borderRadius: 16,
                          border:
                            cell.key === selectedInstallDate
                              ? "2px solid #2563eb"
                              : dashboardBorder,
                          background:
                            cell.key === selectedInstallDate
                              ? "rgba(37,99,235,0.16)"
                              : cell.installs.length > 0
                              ? isDark
                                ? "rgba(16,185,129,0.14)"
                                : "#ecfdf5"
                              : dashboardSoftBg,
                          color: textColor,
                          cursor: cell.installs.length ? "pointer" : "default",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ fontWeight: 800 }}>{cell.day}</div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 8 }}>
                          {cell.installs.length
                            ? `${cell.installs.length} install${cell.installs.length === 1 ? "" : "s"}`
                            : "No jobs"}
                        </div>
                      </button>
                    ) : (
                      <div key={cellIndex} />
                    )
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {false && selectedInstallDate &&
          installationByDate[selectedInstallDate]?.length > 0 && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                Installations on {selectedInstallDate}
              </div>
              {installationByDate[selectedInstallDate].map((quote) => (
                <button
                  key={quote.id}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>{quote.customerLabel || "Unnamed Site"}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      #{quote.quoteNo || "-"} | {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800 }}>
                      {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                  </div>
                </button>
              ))}
            </div>
          )}
        {selectedInstallDate &&
          installationByDate[selectedInstallDate]?.length > 0 && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                Installations on {selectedInstallDate}
              </div>
              {installationByDate[selectedInstallDate].map((quote) => (
                <button
                  key={`${quote.id}-clean`}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      {quote.customerLabel || "Unnamed Site"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: dashboardMuted,
                        marginTop: 4,
                      }}
                    >
                      #{quote.quoteNo || "-"} |{" "}
                      {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800 }}>
                    {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>
      </>
    ) : (
      <>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : isTablet
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {crmSummaryCards.map((card) => (
            <div
              key={card.key}
              style={{
                ...dashboardCardStyle,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${card.accent}16, transparent 58%)`,
                }}
              />
              <div style={{ position: "relative", display: "grid", gap: 8 }}>
                <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: isCompact ? 26 : 30, fontWeight: 900 }}>
                  {typeof card.value === "number" ? (
                    <CountUp end={card.value} duration={1.1} />
                  ) : (
                    card.value
                  )}
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>{card.note}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "minmax(0, 1.4fr) minmax(320px, 1fr)",
            gap: 16,
            marginBottom: 20,
            alignItems: "start",
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>CRM Pipeline Health</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Stage occupancy for the selected {getQuoteTypeLabel(crmPipelineType).toLowerCase()} pipeline
              </div>
            </div>
            {hasCrmStageOverviewData
              ? renderHorizontalMetricBars(crmStageOverviewChartData, {
                  valueKey: "count",
                  valueLabel: "deals",
                  emptyTitle: "No pipeline stage data yet",
                })
              : renderChartEmptyState("No pipeline stage data yet")}
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Checklist Completion
              </div>
              {renderChecklistProgress(
                crmChecklistChartData,
                crmSummary,
                "No checklist data yet"
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {crmSummary.checklistCompletion}% of tracked checklist items are complete across open deals.
              </div>
            </div>

            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Top CRM Clients
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {crmTopClients.map((client, index) => (
                  <div
                    key={client.name}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {index + 1}. {client.name}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {client.count} active quote{client.count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, flex: "0 0 auto", whiteSpace: "nowrap", textAlign: "right", fontSize: isMobile ? 13 : 14 }}>
                      {formatCurrencyDisplay(client.value || 0, currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Overdue Follow-ups
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmDeals
                .filter(
                  (deal) =>
                    deal.nextActionMillis &&
                    deal.nextActionMillis < Date.now() &&
                    !deal.isCrmClosed &&
                    !deal.isCrmCancelled
                )
                .slice(0, 5)
                .map((deal) => (
                  <button
                    key={deal.id}
                    type="button"
                    onClick={() => {
                      setCrmView("list");
                      setTab("crm");
                      openCrmDealDetails(deal, { crmView: "list" });
                    }}
                    style={{
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      borderRadius: 16,
                      padding: 14,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{deal.crmTitle}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {deal.nextAction || "Follow-up pending"} |{" "}
                      {toDateInputValue(deal.nextActionDate) || "-"}
                    </div>
                  </button>
                ))}
              {crmSummary.overdueFollowUps === 0 && (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No overdue follow-ups right now.
                </div>
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Upcoming Installations
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {upcomingInstallations.map((deal) => (
                <button
                  key={`${deal.id}-crm-overview`}
                  type="button"
                  onClick={() => {
                    setCrmView("list");
                    setTab("crm");
                    openCrmDealDetails(deal, { crmView: "list" });
                  }}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                  >
                  <div style={{ fontWeight: 800 }}>{deal.customerLabel}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {deal.hasQuote
                      ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                      : "Lead"}{" "}
                    | {new Date(deal.installationMillis).toLocaleDateString()}
                  </div>
                </button>
              ))}
              {upcomingInstallations.length === 0 && (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No upcoming installations scheduled.
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )}
    </div>
  </>
)}

{/* ================= DASHBOARD LIST ================= */}
{tab === "dashboardList" && (
  <div
    style={{
      ...dashboardPanelStyle,
      minHeight: "80vh",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isCompact ? "flex-start" : "center",
        flexDirection: isCompact ? "column" : "row",
        gap: 12,
        marginBottom: 20,
      }}
    >
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>
          {activeDashboardListView?.label || `Quotes ${dashboardPeriodLabel}`}
        </h2>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
          {dashboardListQuotes.length} quotation{dashboardListQuotes.length === 1 ? "" : "s"} in this view
          {activeDashboardListView?.summaryValue != null
            ? ` | Total ${formatCurrencyDisplay(
                activeDashboardListView.summaryValue || 0,
                currency
              )}`
            : ""}
        </div>
      </div>

      <button
        onClick={() => {
          setDashView("home");
          setTab("dashboard");
        }}
        style={{
          padding: "8px 16px",
          borderRadius: 10,
          border: "none",
          background: "#2563eb",
          color: "#fff",
          cursor: "pointer",
        }}
      >
         Back to Dashboard
      </button>
    </div>

    <div style={{ display: "grid", gap: 12 }}>
      {dashboardListQuotes.map((q) => (
        <div
          key={q.id}
          onClick={() => {
            if (!q?.id) {
              devError("Invalid quotation reference:", q);
              return;
            }

            try {
              loadFullQuote(q);

            } catch (err) {
              devError("Dashboard load error:", err);
            }
          }}
          style={{
            ...dashboardCardStyle,
            cursor: "pointer",
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div>
            <strong>{q.customerLabel || "-"}</strong>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              #{q.quoteNo || "-"} | {getStageLabelForQuote(q)}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background:
                    q.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
                  color:
                    q.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                }}
              >
                {q.quoteTypeResolved === GENERAL_QUOTE_TYPE
                  ? "General Quotation"
                  : "Curtains"}
              </span>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: q.isConfirmed ? "#dcfce7" : "#fef3c7",
                  color: q.isConfirmed ? "#166534" : "#92400e",
                }}
              >
                {q.isConfirmed ? "Confirmed" : "Draft"}
              </span>
            </div>
          </div>

          <div style={{ textAlign: isCompact ? "left" : "right" }}>
            <strong>
              {formatCurrencyDisplay(q.totalValue || 0, currency)}
            </strong>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
              Created {q.createdMillis ? new Date(q.createdMillis).toLocaleDateString() : "-"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 2 }}>
              Updated {q.updatedMillis ? new Date(q.updatedMillis).toLocaleDateString() : "-"}
            </div>
          </div>
        </div>
      ))}
      {dashboardListQuotes.length === 0 && (
        <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
          No quotations found for this dashboard view.
        </div>
      )}
    </div>
  </div>
)}


{/* ================= CRM TAB ================= */}
{tab === "crm" && renderCrmWorkspace()}
{tab === "customerLedger" && renderCustomerLedger()}
{tab === "todayTasks" && renderTodayTasks()}
{tab === "executionSpecs" && renderExecutionWorkspace()}
{tab === "executionTemplates" && renderExecutionTemplatesPage()}
{tab === "products" && renderProductCalculationsPage()}
{tab === "accountSecurity" && renderAccountSecurityPage()}
{tab === "maintenance" && renderMaintenancePage()}

        {/* === QUOTE TAB === */}
      {/* === QUOTE TAB === */}
      {tab === "teamAccess" && canAccessTeamAccess ? (
        <div
          style={{
            ...dashboardCardStyle,
            padding: isCompact ? 18 : 24,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                Team & Access
              </div>
              <h2 style={{ margin: "6px 0 4px", fontSize: isCompact ? 24 : 30, fontWeight: 900, letterSpacing: "-0.04em" }}>
                Manage account members and feature visibility
              </h2>
              <p style={{ margin: 0, color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
                Super admins, owners, and account admins can review the active plan, member status, and feature toggles. Existing quotation data remains under the legacy workspace path.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {isSuperAdmin ? (
                <a href="/super-admin" style={{ ...btn("#2563eb"), textDecoration: "none", marginRight: 0 }}>
                  Open Super Admin
                </a>
              ) : null}
              <button
                type="button"
                style={{ ...btn("#0f172a"), marginRight: 0 }}
                onClick={openTeamMemberInviteModal}
              >
                Add team member
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {[
              ["Account", account?.name || "Not linked"],
              ["Plan", accountUser?.plan || account?.plan || "full"],
              ["Status", account?.isActive ? "active" : "inactive"],
              ...(isTrialAccount
                ? [[
                    "Free Trial",
                    `${trialQuotationsUsed || 0} / ${trialQuotationLimit || 10} used${
                      Number(trialQuotationsRemaining || 0) > 0
                        ? ` | ${trialQuotationsRemaining} left`
                        : ""
                    }`,
                  ]]
                : []),
            ].map(([label, value]) => (
              <div key={label} style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 900 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 10px", minWidth: 860 }}>
              <thead>
                <tr>
                  {["Name", "Email", "Role", "Plan", "Status", "Account", "Actions"].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", fontSize: 12, color: dashboardMuted, padding: "0 12px" }}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamAccessRows.map((member) => (
                  <tr key={member.id}>
                    <td style={teamCellStyle}>{member.name}</td>
                    <td style={teamCellStyle}>{member.email || "No email"}</td>
                    <td style={teamCellStyle}>{member.role}</td>
                    <td style={teamCellStyle}>{member.plan}</td>
                    <td style={teamCellStyle}>
                      <span style={{ ...statusPillStyle, background: member.isActive ? "#dcfce7" : "#fee2e2", color: member.isActive ? "#166534" : "#991b1b" }}>
                        {member.isActive ? "active" : "inactive"}
                      </span>
                    </td>
                    <td style={teamCellStyle}>{member.accountName}</td>
                    <td style={teamCellStyle}>
                      {isSuperAdmin ? (
                        <a href="/super-admin" style={{ color: "#2563eb", fontWeight: 800, textDecoration: "none" }}>
                          Edit access
                        </a>
                      ) : (
                        <span style={{ color: dashboardMuted }}>Owner settings</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <EmployeeActivityHistory functions={functions} accountId={account?.id || accountUser?.activeAccountId || accountUser?.accountId}
            workspaceUid={workspaceUid} allowed={canAccessTeamAccess} />
          <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <UserCog size={20} color="#2563eb" />
              <div style={{ fontSize: 18, fontWeight: 900 }}>Current enabled features</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              {accessFeatureLabels.map(([key, label]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: dashboardBorder,
                    borderRadius: 14,
                    padding: "12px 14px",
                    background: isDark ? "rgba(15,23,42,0.65)" : "#ffffff",
                  }}
                >
                  <span style={{ fontWeight: 800 }}>{label}</span>
                  <span style={{ ...statusPillStyle, background: features?.[key] ? "#dbeafe" : "#f1f5f9", color: features?.[key] ? "#1d4ed8" : "#64748b" }}>
                    {features?.[key] ? "enabled" : "locked"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {renderTeamMembersManager()}
        </div>
      ) : null}

      <div
        style={{
          display: tab === "quote" ? "block" : "none",
    background: isDark ? "#151520" : "#ffffff",
    padding: 24,
    borderRadius: 16,
    boxShadow: isDark
      ? "0px 0px 20px rgba(0,0,0,0.4)"
      : "0px 4px 18px rgba(0,0,0,0.06)",
  }}
>
  <div
    style={{
      display: "inline-flex",
      gap: 8,
      padding: 6,
      borderRadius: 18,
      marginBottom: 22,
      background: isDark ? "#0f172a" : "#f8fafc",
      border: isDark
        ? "1px solid rgba(148,163,184,0.14)"
        : "1px solid rgba(226,232,240,0.9)",
    }}
  >
    {[
      { key: CURTAIN_QUOTE_TYPE, label: "Curtains" },
      { key: GENERAL_QUOTE_TYPE, label: "General Quotation" },
    ]
      .filter((item) =>
        item.key === CURTAIN_QUOTE_TYPE
          ? canAccessCurtainQuotes
          : canAccessGeneralQuotes
      )
      .map((item) => {
      const active = quoteSubTab === item.key;
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => setRoutedTab("quote", item.key)}
          style={{
            border: "none",
            cursor: "pointer",
            borderRadius: 14,
            padding: "10px 16px",
            fontSize: 14,
            fontWeight: 700,
            background: active
              ? isDark
                ? "rgba(37,99,235,0.22)"
                : "#dbeafe"
              : "transparent",
            color: active
              ? "#2563eb"
              : isDark
              ? "#cbd5e1"
              : "#475569",
            transition: "all 0.2s ease",
          }}
        >
          {item.label}
        </button>
      );
    })}
  </div>

  {quoteEditorReadOnly ? (
    <div
      style={{
        marginBottom: 16,
        padding: "12px 14px",
        borderRadius: 14,
        background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
        border: "1px solid rgba(37,99,235,0.18)",
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#bfdbfe" : "#1d4ed8",
      }}
    >
      View-only access: you can review quotations in this workspace, but saving changes is disabled for your role.
    </div>
  ) : null}

  <div
    style={{
      pointerEvents: quoteEditorReadOnly ? "none" : "auto",
      opacity: quoteEditorReadOnly ? 0.84 : 1,
      transition: "opacity 0.2s ease",
    }}
  >
  {quoteSubTab === CURTAIN_QUOTE_TYPE ? (

          <div
            onBlurCapture={() => persistCurtainQuoteDraft("blur")}
            style={{
              background: isDark ? "#151520" : "#ffffff",
              padding: 24,
              borderRadius: 16,
              boxShadow: isDark
                ? "0px 0px 20px rgba(0,0,0,0.4)"
                : "0px 4px 18px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                marginBottom: 18,
                padding: isCompact ? 16 : 18,
                borderRadius: 20,
                background: isDark
                  ? "linear-gradient(135deg, rgba(15,23,42,0.94), rgba(30,41,59,0.82))"
                  : "linear-gradient(135deg, #ffffff, #f8fafc)",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.18)"
                  : "1px solid rgba(226,232,240,0.95)",
                boxShadow: isDark
                  ? "0 18px 36px rgba(2,6,23,0.28)"
                  : "0 16px 34px rgba(15,23,42,0.06)",
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 14,
              }}
            >
              <div style={{ display: "grid", gap: 7 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color:
                      quoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
                        ? isDark
                          ? "#bfdbfe"
                          : "#1d4ed8"
                        : isDark
                        ? "#bbf7d0"
                        : "#166534",
                  }}
                >
                  {quoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
                    ? "Editing existing quotation"
                    : "New quotation"}
                </div>
                <div
                  style={{
                    fontSize: isCompact ? 22 : 26,
                    fontWeight: 900,
                    letterSpacing: "-0.04em",
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Curtain Quotation
                </div>
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: dashboardMuted,
                    fontWeight: 700,
                  }}
                >
                  {quoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING
                    ? `Currently editing ${quoteNo || selectedQuote?.quoteNo || "a saved quotation"}. Use New Quote to clear the editor without changing the saved quotation.`
                    : "Fresh quotation ready. Quote number will be generated when you save."}
                </div>
              </div>
              <Button
                type="button"
                icon={Plus}
                variant="ghost"
                onClick={(event) => requestStartNewQuote(event, CURTAIN_QUOTE_TYPE)}
                disabled={!canCreateQuotes || isSaving}
                style={{ alignSelf: isCompact ? "stretch" : "center" }}
              >
                New Quote
              </Button>
            </div>
{!selectedQuote && (
  <div style={{ padding: 20, opacity: 0.7 }}>
   
  </div>
)}

            {legacyRecalculationModalOpen && legacyRecalculationPreview ? (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Review calculation changes"
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  background: "rgba(15,23,42,0.58)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isCompact ? 12 : 24,
                }}
              >
                <div
                  style={{
                    width: "min(1100px, 96vw)",
                    maxHeight: "90vh",
                    overflow: "auto",
                    borderRadius: 24,
                    background: isDark ? "#111827" : "#ffffff",
                    color: isDark ? "#f8fafc" : "#111827",
                    boxShadow: "0 28px 90px rgba(15,23,42,0.38)",
                    border: isDark
                      ? "1px solid rgba(148,163,184,0.24)"
                      : "1px solid rgba(226,232,240,0.9)",
                  }}
                >
                  <div
                    style={{
                      padding: isCompact ? 18 : 24,
                      borderBottom: isDark
                        ? "1px solid rgba(148,163,184,0.18)"
                        : "1px solid #e2e8f0",
                      display: "flex",
                      flexDirection: isCompact ? "column" : "row",
                      justifyContent: "space-between",
                      gap: 14,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          letterSpacing: "0.09em",
                          textTransform: "uppercase",
                          color:
                            legacyRecalculationPreview.recalculationImpact ===
                            LEGACY_CALCULATION_IMPACT.NO_IMPACT
                              ? isDark
                                ? "#86efac"
                                : "#15803d"
                              : isDark
                              ? "#fbbf24"
                              : "#b45309",
                        }}
                      >
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Calculation check"
                          : "Legacy curtain calculation"}
                      </div>
                      <h2 style={{ margin: "4px 0 6px", fontSize: isCompact ? 22 : 28 }}>
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Calculation Check"
                          : "Review Calculation Changes"}
                      </h2>
                      <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 700 }}>
                        Quote {legacyRecalculationPreview.quoteNo || quoteNo || "-"} ·{" "}
                        {legacyRecalculationPreview.customerName || customerName || "Customer"} ·{" "}
                        rows changed: {legacyRecalculationPreview.affectedRowCount}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setLegacyRecalculationModalOpen(false);
                        setLegacyRecalculationPreview(null);
                      }}
                      style={{ ...btn(isDark ? "#1f2937" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                    >
                      Cancel
                    </button>
                  </div>

                  <div style={{ padding: isCompact ? 18 : 24, display: "grid", gap: 18 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                          ? "1fr"
                          : "repeat(3, minmax(0, 1fr))",
                        gap: 12,
                      }}
                    >
                      {[
                        {
                          label: "Saved subtotal",
                          oldValue: legacyRecalculationPreview.oldTotals?.subtotalBeforeGst,
                          newValue: legacyRecalculationPreview.newTotals?.subtotalBeforeGst,
                          diff: legacyRecalculationPreview.difference?.subtotal,
                        },
                        {
                          label: "GST",
                          oldValue:
                            legacyRecalculationPreview.oldTotals?.totalGstAmount ??
                            legacyRecalculationPreview.oldTotals?.gstAmount,
                          newValue:
                            legacyRecalculationPreview.newTotals?.totalGstAmount ??
                            legacyRecalculationPreview.newTotals?.gstAmount,
                          diff: legacyRecalculationPreview.difference?.gst,
                        },
                        {
                          label: "Grand total",
                          oldValue: legacyRecalculationPreview.oldTotals?.grandTotal,
                          newValue: legacyRecalculationPreview.newTotals?.grandTotal,
                          diff: legacyRecalculationPreview.difference?.grandTotal,
                          highlight: true,
                        },
                      ].map((card) => (
                        <div
                          key={card.label}
                          style={{
                            padding: 16,
                            borderRadius: 18,
                            background: card.highlight
                              ? isDark
                                ? "linear-gradient(135deg, rgba(30,64,175,0.5), rgba(15,23,42,0.96))"
                                : "linear-gradient(135deg, #eff6ff, #ffffff)"
                              : isDark
                              ? "rgba(15,23,42,0.72)"
                              : "#f8fafc",
                            border: isDark
                              ? "1px solid rgba(148,163,184,0.18)"
                              : "1px solid #e2e8f0",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 900, color: dashboardMuted }}>
                            {card.label}
                          </div>
                          <div style={{ display: "grid", gap: 5, marginTop: 8 }}>
                            <strong>Saved: {formatCurrencyDisplay(card.oldValue || 0, currency)}</strong>
                            <strong>New: {formatCurrencyDisplay(card.newValue || 0, currency)}</strong>
                            <span
                              style={{
                                color: Number(card.diff || 0) >= 0 ? "#16a34a" : "#dc2626",
                                fontWeight: 900,
                              }}
                            >
                              Difference: {Number(card.diff || 0) >= 0 ? "+" : ""}
                              {formatCurrencyDisplay(card.diff || 0, currency)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "rgba(22,101,52,0.2)"
                              : "#f0fdf4"
                            : isDark
                            ? "rgba(120,53,15,0.24)"
                            : "#fffbeb",
                        color:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "#bbf7d0"
                              : "#166534"
                            : isDark
                            ? "#fde68a"
                            : "#78350f",
                        border:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "1px solid rgba(74,222,128,0.25)"
                              : "1px solid #bbf7d0"
                            : isDark
                            ? "1px solid rgba(245,158,11,0.28)"
                            : "1px solid #fde68a",
                        fontSize: 13,
                        fontWeight: 750,
                        lineHeight: 1.55,
                      }}
                    >
                      {legacyRecalculationPreview.recalculationImpact ===
                      LEGACY_CALCULATION_IMPACT.NO_IMPACT
                        ? "No changes found. Saved values and the latest formula match, so no updated revision is needed."
                        : "This will not change the original quote unless you save a new revision. Customer and workshop PDFs continue to use the saved values."}
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "separate",
                          borderSpacing: 0,
                          minWidth: 860,
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr>
                            {[
                              "Item",
                              "Product",
                              "Fabric / Code",
                              "Field changed",
                              "Old value",
                              "New value",
                              "Difference",
                              "Reason",
                            ].map((heading) => (
                              <th
                                key={heading}
                                style={{
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  background: isDark ? "#1f2937" : "#f1f5f9",
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  fontSize: 11,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                }}
                              >
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(legacyRecalculationPreview.rowDiffRows || []).map((entry, index) => (
                            <tr key={`${entry.rowId}-${entry.field}-${index}`}>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", fontWeight: 800 }}>
                                {entry.itemName}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {entry.product || "-"}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {entry.fabric || "-"}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", fontWeight: 900 }}>
                                {entry.field}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {formatLegacyRecalculationValue(entry, entry.oldValue)}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {formatLegacyRecalculationValue(entry, entry.newValue)}
                              </td>
                              <td
                                style={{
                                  padding: "10px 12px",
                                  borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0",
                                  color:
                                    entry.difference == null
                                      ? dashboardMuted
                                      : Number(entry.difference || 0) >= 0
                                      ? "#16a34a"
                                      : "#dc2626",
                                  fontWeight: 900,
                                }}
                              >
                                {entry.difference == null ? (
                                  "Not comparable"
                                ) : (
                                  <>
                                    {Number(entry.difference || 0) >= 0 ? "+" : ""}
                                    {formatLegacyRecalculationValue(
                                      { ...entry, oldExists: true },
                                      entry.difference
                                    )}
                                  </>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", maxWidth: 260 }}>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {(entry.reasonCodes || []).map((reason) => (
                                    <span
                                      key={reason}
                                      style={{
                                        padding: "3px 7px",
                                        borderRadius: 999,
                                        background: isDark
                                          ? "rgba(148,163,184,0.14)"
                                          : "#f1f5f9",
                                        color: isDark ? "#e2e8f0" : "#334155",
                                        fontSize: 11,
                                        fontWeight: 800,
                                      }}
                                    >
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!legacyRecalculationPreview.rowDiffRows?.length ? (
                            <tr>
                              <td colSpan={8} style={{ padding: 18, textAlign: "center", color: dashboardMuted }}>
                                No row-level numeric differences were found.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        gap: 10,
                      }}
                    >
                      <button
                        type="button"
                        onClick={downloadLegacyRecalculationReport}
                        style={{ ...btn(isDark ? "#334155" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        Download Difference Report
                      </button>
                      {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT && canEditQuotes ? (
                        <button
                          type="button"
                          onClick={() => markLegacyCalculationNoImpactReviewed()}
                          style={{ ...btn("#16a34a", "#ffffff"), marginRight: 0 }}
                        >
                          Mark as Reviewed
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setLegacyRecalculationModalOpen(false);
                          setLegacyRecalculationPreview(null);
                        }}
                        style={{ ...btn(isDark ? "#1f2937" : "#cbd5e1", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Close"
                          : "Cancel"}
                      </button>
                      {legacyRecalculationPreview.recalculationImpact !==
                      LEGACY_CALCULATION_IMPACT.NO_IMPACT ? (
                        <button
                          type="button"
                          disabled={!canCreateQuotes || isSaving}
                          onClick={() => saveLegacyRecalculationAsNewRevision()}
                          style={{
                            ...btn("#2563eb", "#ffffff"),
                            marginRight: 0,
                            opacity: !canCreateQuotes || isSaving ? 0.65 : 1,
                          }}
                        >
                          Create Updated Revision
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {activeCurtainRecalculationAudit ? (
              <div
                style={{
                  marginBottom: 18,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(59,130,246,0.35)"
                    : "1px solid rgba(37,99,235,0.18)",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30,64,175,0.28), rgba(15,23,42,0.82))"
                    : "linear-gradient(135deg, #eff6ff, #ffffff)",
                  display: "flex",
                  flexDirection: isCompact ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isCompact ? "stretch" : "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "grid", gap: 5 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                    Calculation updated from older formula
                  </div>
                  <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 700 }}>
                    Previous grand total{" "}
                    {formatCurrencyDisplay(activeCurtainRecalculationAudit.oldGrandTotal || 0, currency)}
                    {" "}→{" "}
                    {formatCurrencyDisplay(activeCurtainRecalculationAudit.newGrandTotal || 0, currency)}
                    {" "}· rows changed: {activeCurtainRecalculationAudit.changedRows?.length || 0}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const preview = {
                        id: `audit-${Date.now()}`,
                        quoteNo,
                        customerName,
                        oldTotals: {
                          subtotalBeforeGst: activeCurtainRecalculationAudit.oldSubtotal,
                          totalGstAmount: activeCurtainRecalculationAudit.oldGst,
                          grandTotal: activeCurtainRecalculationAudit.oldGrandTotal,
                        },
                        newTotals: {
                          subtotalBeforeGst: activeCurtainRecalculationAudit.newSubtotal,
                          totalGstAmount: activeCurtainRecalculationAudit.newGst,
                          grandTotal: activeCurtainRecalculationAudit.newGrandTotal,
                        },
                        difference: {
                          subtotal: round2(
                            Number(activeCurtainRecalculationAudit.newSubtotal || 0) -
                              Number(activeCurtainRecalculationAudit.oldSubtotal || 0)
                          ),
                          gst: round2(
                            Number(activeCurtainRecalculationAudit.newGst || 0) -
                              Number(activeCurtainRecalculationAudit.oldGst || 0)
                          ),
                          grandTotal: Number(activeCurtainRecalculationAudit.difference || 0),
                        },
                        changedRows: activeCurtainRecalculationAudit.changedRows || [],
                        rowDiffRows: (activeCurtainRecalculationAudit.changedRows || []).map((row) => ({
                          ...row,
                          field: "Row total",
                          type: "money",
                          oldValue: row.oldTotal,
                          newValue: row.newTotal,
                          difference: row.difference,
                        })),
                        affectedRowCount:
                          activeCurtainRecalculationAudit.changedRows?.length || 0,
                      };
                      setLegacyRecalculationPreview(preview);
                      setLegacyRecalculationModalOpen(true);
                    }}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    View Recalculation Details
                  </button>
                  {canRevertLegacyCurtainRecalculation &&
                  activeCurtainLegacyCalculationSnapshot?.rows?.length ? (
                    <button
                      type="button"
                      onClick={revertLegacyCurtainRecalculation}
                      style={{ ...btn("#dc2626"), marginRight: 0 }}
                    >
                      Revert to Previous Calculation
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {shouldShowLegacyCalculationBanner ? (
              <div
                style={{
                  marginBottom: 18,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(245,158,11,0.38)"
                    : "1px solid rgba(245,158,11,0.42)",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(120,53,15,0.28), rgba(15,23,42,0.78))"
                    : "linear-gradient(135deg, #fffbeb, #ffffff)",
                  boxShadow: isDark
                    ? "0 18px 34px rgba(2,6,23,0.24)"
                    : "0 16px 32px rgba(245,158,11,0.12)",
                  display: "flex",
                  flexDirection: isCompact ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isCompact ? "stretch" : "center",
                  gap: 14,
                }}
              >
                <div style={{ display: "grid", gap: 5 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 900,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: isDark ? "#fbbf24" : "#92400e",
                    }}
                  >
                    Older calculation version detected
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.6,
                      color: isDark ? "#fde68a" : "#78350f",
                    }}
                  >
                    This quote was created with an older calculation version. We are showing
                    the saved values exactly as originally saved. To update it with the latest
                    formula, create a new revision.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 12,
                      color: dashboardMuted,
                      fontWeight: 700,
                    }}
                  >
                    <span>
                      Rows affected:{" "}
                      {legacyRecalculationImpactPreview?.actualChangedRows || 0}
                    </span>
                    <button
                      type="button"
                      onClick={openLegacyRecalculationPreview}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: isDark ? "#fbbf24" : "#b45309",
                        fontWeight: 900,
                        padding: 0,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View Changes
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!canCreateQuotes || isSaving}
                  onClick={() => {
                    const preview =
                      legacyRecalculationImpactPreview ||
                      buildLegacyCurtainRecalculationPreview();
                    if (!preview) return;
                    saveLegacyRecalculationAsNewRevision(preview);
                  }}
                  style={{
                    ...btn("#f59e0b", "#111827"),
                    marginRight: 0,
                    whiteSpace: "nowrap",
                    opacity: !canCreateQuotes || isSaving ? 0.65 : 1,
                  }}
                >
                  Create New Revision
                </button>
              </div>
            ) : null}

            {/* CUSTOMER INFO */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "repeat(3, 1fr)",
                gap: 16,
                marginBottom: 16,
              }}
            >
              {renderCustomerLedgerCombobox({
                context: CURTAIN_QUOTE_TYPE,
                label: "Customer Name",
                value: customerName,
                selectedCustomerId:
                  quoteCustomerLink.customerId ||
                  selectedQuote?.customerId ||
                  selectedQuote?.crm?.customerId ||
                  "",
                seed: quoteCustomerLink,
                onTextChange: (nextValue) => {
                  setCustomerName(nextValue);
                  setQuoteCustomerLink((prev) => ({
                    ...prev,
                    customerId: "",
                    customerName: nextValue,
                  }));
                },
              })}

              {renderReferralPartnerSelector({
                context: CURTAIN_QUOTE_TYPE,
              })}

            <div>
  <label style={{ fontSize: 13, opacity: 0.7 }}>
  Quote Number
</label>
{editingQuoteId ? <QuotationAttribution quote={selectedQuote} members={crmMemberRoster.rows} /> : null}

<div style={{ position: "relative", width: "100%" }}>
  <input
    style={{
      ...input,
      paddingRight: 0, // space for refresh button
    }}
    value={quoteNo}
    onChange={(e) => setQuoteNo(e.target.value)}
    placeholder="Quote number will be generated when saving"
    pattern=".*[0-9].*"   // must contain a number
  />

 {/*  Quote number generated automatically on save */}
<button
  type="button"
  title="Quote number will be generated when saving"
  disabled
  style={{
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",

    width: 28,
    height: 28,
    borderRadius: 8,

    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#94a3b8",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "not-allowed",
    fontSize: 14,
  }}
>

</button>
</div>
</div>


              <div>
                <label style={{ fontSize: 13, opacity: 0.7 }}>Date</label>
                <input
                  style={input}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              {renderQuoteSiteProjectPanel({
                context: CURTAIN_QUOTE_TYPE,
                selectedCustomerId:
                  quoteCustomerLink.customerId ||
                  selectedQuote?.customerId ||
                  selectedQuote?.crm?.customerId ||
                  "",
              })}
            </div>

    

            {/* TABLE */}
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "6px 0 14px",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Curtains & Blinds</div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Measurement rows use width, height, fabric, stitching, and fabric demand.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button icon={Plus} variant="success" onClick={addCurtainMeasurementRow}>
                  Add Curtain/Blind Row
                </Button>
                <Button icon={Plus} variant="primary" onClick={addCurtainAccessoryRow}>
                  Add Accessory Row
                </Button>
              </div>
            </div>
            {isCompact ? (
              <div style={{ display: "grid", gap: 14 }}>
                {curtainMeasurementEntries.map(({ row: r, index: i }) =>
                  renderCurtainCompactRow(r, i)
                )}
                {curtainMeasurementEntries.length === 0 ? (
                  <div style={{ ...compactCardStyle, color: dashboardMuted }}>
                    No curtain/blind measurement rows yet. Add a measurement row or use
                    accessories only for hardware/service quotations.
                  </div>
                ) : null}
              </div>
            ) : (
            <div className="quote-table-wrapper" style={{ overflowX: "auto", paddingBottom: 8 }}>
    <table
  style={{
    width: "max-content",
    minWidth: curtainTableMinWidth,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  }}
>

  {/* COLUMN WIDTH CONTROL */}
  <colgroup>
    <col style={colItem} />
    <col style={colProduct} />
    <col style={colFabric} />

    <col style={colXS} />   {/* Fab W */}
    <col style={colWH} />   {/* W */}
    <col style={colWH} />   {/* H */}
    <col style={colM} />    {/* MRP/m */}
    <col style={colXS} />   {/* Disc */}
    <col style={colS} />    {/* Rate/sqft */}

    <col style={colStitch} />   {/* Stitch */}
    <col style={colLining} />   {/* Lining */}

    <col style={colFabricM} />  {/* Ordered Fabric (m) */}
    <col style={colTotal} />    {/* Total */}

    <col style={colActions} />  {/* Actions */}
  </colgroup>

  <thead>
    <tr>
      <th>Item</th>
      <th>Product</th>
      <th>Fabric / Code</th>
     <th>Fab W (in)</th>

<th>W ({unitLabel(unit)})</th>
<th>H ({unitLabel(unit)})</th>
      <th>MRP/m</th>
      <th>Disc %</th>
      <th>Rate</th>
      <th>Stitch</th>
      <th>Lining</th>
      <th>Fabric (m)</th>
      <th style={stickyTotalHeaderStyle}>Total</th>
      <th style={stickyActionsHeaderStyle}>Actions</th>
    </tr>
  </thead>


                <tbody>
                  {curtainMeasurementEntries.map(({ row: r, index: i }) => {
                    const c = getCurtainProductionAdjustedRowCalc(r, i);
                    const rowRules = getRowProductRules(r);
                    const orderedFabricAudit = getCurtainOrderedFabricDisplayTrace(r, i, c);
                    const orderedFabricTooltip = getOrderedFabricTooltipText(orderedFabricAudit);
                    const requiredFields = { ...rowRules.requiredFields,
                      mrpPerMeter: getProductPricingRequirements(rowRules.config).requiresMrpPerMeter };
                    const rateMeta = getRowRateFieldMeta(r);
                    const fabricInputVisible = shouldShowFabricInput(r, rowRules.config);
                    const fabricInputEnabled = shouldEnableFabricInput(r, rowRules.config);
                    const fabricInputLabel = getFabricInputLabel(rowRules.config);
                    const rowId = getWorkshopRowStableId(r) || `measurement-${i}`;
                    const rowSurface =
                      i % 2 === 0
                        ? isDark
                          ? "#1e1e26"
                          : "#ffffff"
                        : isDark
                        ? "#2a2a34"
                        : "#f9fafb";
                    const rowValidationMatches =
                      quoteRowValidationError?.quoteType === CURTAIN_QUOTE_TYPE &&
                      (String(quoteRowValidationError?.rowId || "") === String(rowId) ||
                        Number(quoteRowValidationError?.rowIndex) === i);
                    const validationFieldName = String(
                      quoteRowValidationError?.failedField || ""
                    ).toLowerCase();
                    const fieldMatchesValidation = (fieldNames = []) =>
                      rowValidationMatches &&
                      fieldNames.some((fieldName) =>
                        validationFieldName.includes(String(fieldName || "").toLowerCase())
                      );
                    const invalidInputAccent = {
                      border: "2px solid #dc2626",
                      boxShadow: "0 0 0 3px rgba(220,38,38,0.16)",
                    };
                    const inputStyleForValidation = (baseStyle, fieldNames = []) =>
                      fieldMatchesValidation(fieldNames)
                        ? { ...baseStyle, ...invalidInputAccent }
                        : baseStyle;
                    const cellStyleForValidation = (baseStyle, fieldNames = []) =>
                      fieldMatchesValidation(fieldNames)
                        ? {
                            ...baseStyle,
                            background: isDark
                              ? "rgba(127,29,29,0.26)"
                              : "#fff1f2",
                          }
                        : baseStyle;
                    const effectiveRowSurface = rowValidationMatches
                      ? isDark
                        ? "rgba(127,29,29,0.22)"
                        : "#fff7f7"
                      : rowSurface;
                    const rowTrackConfig = getCurtainTrackConfigForUi(r, c);
                    const showTrackConfigPanel = shouldShowCurtainTrackConfigPanel(r, c);
                    const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
                      hasFabricMeterComponent: Boolean(c.isCurtain || c.isRoman),
                      fabricGrossAmount: c.fabricGrossAmount || 0,
                    });
                    const discountInputEnabled = rowDiscountScope !== "none";
                    const discountTooltip =
                      rowDiscountScope === "fabric_only"
                        ? "Applies only to fabric amount."
                        : rowDiscountScope === "line_total"
                        ? "Applies to this line amount."
                        : "Discount is disabled for this product.";

    // --- RAW INPUT SYNC (fix typing issues) ---

                    return (
                      <React.Fragment key={getWorkshopRowStableId(r) || `measurement-${i}`}>
                      <tr
                        data-quote-row-id={rowId}
                        data-quote-row-index={i}
                        style={{
                          background: effectiveRowSurface,
                          boxShadow: rowValidationMatches
                            ? "inset 4px 0 0 #dc2626"
                            : "none",
                        }}
                      >
                       {/* ITEM */}
<td style={{ padding: 8, ...colItem }}>
  <input
    style={inputStyleForValidation(input, ["item", "name", "description"])}
    value={r.item}
    onChange={(e) => updateField(rowId, "item", e.target.value)}
  />
</td>

{/* PRODUCT */}
<td style={cellStyleForValidation({ padding: 8, ...colProduct }, ["product"])}>
  {(() => {
    const savedProductOption = getCurtainRowSavedProductOption(r);
    return (
  <select
    style={inputStyleForValidation(selectInput, ["product"])}
    value={getCurtainRowProductSelectValue(r)}
    onChange={(e) => updateField(rowId, "productType", e.target.value)}
  >
    {savedProductOption ? (
      <option value={savedProductOption.value}>{savedProductOption.label}</option>
    ) : null}
    {curtainProductOptions.map((product) => (
      <option key={product.id} value={product.id}>
        {product.name}
      </option>
    ))}
  </select>
    );
  })()}
</td>

{/* FABRIC */}
<td style={cellStyleForValidation({ padding: 8, ...colFabric }, ["fabric", "materialCode", "fabricCode"])}>
  {fabricInputVisible ? (
    renderFabricCatalogueCombobox({
      row: r,
      rowId,
      rowIndex: i,
      label: fabricInputLabel || "Fabric / Code",
      disabled: !fabricInputEnabled,
    })
  ) : null}
</td>


{/* FAB W (in) */}
<td style={cellStyleForValidation({ padding: 8, ...colXS }, ["fabricWidthIn", "fabric width"])}>
  <div style={{ display: "grid", gap: 4 }}>
    <input
      type="text"
      style={inputStyleForValidation(input, ["fabricWidthIn", "fabric width"])}
      placeholder="in"
      value={
        r.fabricWidthIn === "" || r.fabricWidthIn == null
          ? ""
          : r.fabricWidthIn
      }
      onChange={(e) =>
        updateCurtainRowFabricWidthManual(rowId, e.target.value)
      }
      disabled={!requiredFields.fabricWidth}
    />
    {r.fabricWidthManuallyOverridden ? (
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: isDark ? "#bfdbfe" : "#1d4ed8",
          whiteSpace: "nowrap",
        }}
      >
        Manual width
      </span>
    ) : null}
  </div>
</td>


{/* WIDTH */}
<td style={cellStyleForValidation({ padding: 8, ...colWH }, ["width"])}>
  <input
    type="text"
    inputMode="decimal"
    step="0.01"
    style={inputStyleForValidation(input, ["width"])}
    placeholder={unitLabel(unit)}
    aria-label={`Width for ${r.item || 'window'}`}
    value={getDimensionInputValueForUnit(r.width, unit)}
    onChange={(e) => {
      const v = e.target.value;

      const storedValue = getStoredDimensionValueFromInput(v, unit);
      if (storedValue == null) return;

      updateField(rowId, "width", storedValue);
    }}
  />
</td>


{/* HEIGHT */}
<td style={cellStyleForValidation({ padding: 8, ...colWH }, ["height"])}>
  <input
    type="text"
    inputMode="decimal"
    step="0.01"
    style={inputStyleForValidation(input, ["height"])}
    placeholder={unitLabel(unit)}
    aria-label={`Height for ${r.item || 'window'}`}
    value={getDimensionInputValueForUnit(r.height, unit)}
    onChange={(e) => {
      const v = e.target.value;

      const storedValue = getStoredDimensionValueFromInput(v, unit);
      if (storedValue == null) return;

      updateField(rowId, "height", storedValue);
    }}
  />
</td>


{/* MRP/m */}
<td style={cellStyleForValidation({ padding: 8, ...colM }, ["mrp", "rrpPerMeter"])}>
  <input
    type="text"
    inputMode="decimal"
    disabled={!requiredFields.mrpPerMeter}
    style={{
      ...input,
      ...(fieldMatchesValidation(["mrp", "rrpPerMeter"]) ? invalidInputAccent : {}),
      opacity: requiredFields.mrpPerMeter ? 1 : 0.35,
    }}
    value={r.mrp ?? ""}
    onChange={(e) => updateField(rowId, "mrp", e.target.value)}
    onBlur={(e) =>
      handleCurtainRowMrpBlur(rowId, {
        ...r,
        mrp: e.target.value,
        mrpPerMeter: e.target.value,
      })
    }
  />
</td>

{/* DISCOUNT */}
<td style={cellStyleForValidation({ padding: 8, ...colXS }, ["discount"])}>
  <input
    type="text"
    inputMode="decimal"
    disabled={!discountInputEnabled}
    title={discountTooltip}
    style={{
      ...input,
      ...(fieldMatchesValidation(["discount"]) ? invalidInputAccent : {}),
      opacity: discountInputEnabled ? 1 : 0.45,
    }}
    value={r.discount}
    onChange={(e) => updateField(rowId, "discount", e.target.value)}
  />
</td>

{/* RATE/SQFT */}
<td style={cellStyleForValidation({ padding: 8, ...colS }, ["rate", "sqftRate", "ratePerSqft", "ratePerPiece", "ratePerFeet"])}>
  {rateMeta.hidden ? (
    <div
      title="Not applicable"
      style={{
        minHeight: 36,
        borderRadius: input.borderRadius,
        border: input.border,
        background: isDark ? "rgba(15,23,42,0.42)" : "#f8fafc",
        opacity: 0.45,
      }}
    />
  ) : (
    <input
      type="text"
      inputMode="decimal"
      disabled={rateMeta.disabled}
      title={rateMeta.label}
      placeholder={rateMeta.label}
      style={{
        ...input,
        ...(fieldMatchesValidation(["rate", "sqftRate", "ratePerSqft", "ratePerPiece", "ratePerFeet"])
          ? invalidInputAccent
          : {}),
        opacity: rateMeta.disabled ? 0.4 : 1,
      }}
      value={rateMeta.field ? r[rateMeta.field] ?? "" : ""}
      onChange={(e) => {
        if (!rateMeta.field) return;
        updateField(rowId, rateMeta.field, e.target.value);
      }}
    />
  )}
  {renderProductRateStatus(r, rowId)}
</td>

{/* STITCH */}
{/* STITCH */}
{requiredFields.stitching ? (
  <td style={cellStyleForValidation({ padding: 8, ...colStitch }, ["stitchingStyle", "customStitchRate"])}>
    <select
      style={inputStyleForValidation(selectInput, ["stitchingStyle"])}
      value={r.stitchingStyle || ""}
      onChange={(e) => {
        const val = e.target.value;
        updateField(rowId, "stitchingStyle", val);

        //  Clear custom rate if not custom
        if (val !== "Eyelet (Custom)") {
          updateField(rowId, "customStitchRate", "");
        }
      }}
    >
      <option value="">Select</option>
      {Object.keys(stitchingRates).map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
    {isCurtain(r.productType) && isEyeletCustom(r) ? (
      <input
        type="text"
        inputMode="decimal"
        style={{
          ...input,
          ...(fieldMatchesValidation(["customStitchRate"]) ? invalidInputAccent : {}),
          marginTop: 6,
        }}
        value={r.customStitchRate ?? ""}
        placeholder="Custom stitch rate"
        onChange={(e) => updateField(rowId, "customStitchRate", e.target.value)}
      />
    ) : null}
  </td>
) : (
  // keep column alignment without showing UI
  <td style={{ padding: 8 }}></td>
)}


{/* LINING */}
<td style={cellStyleForValidation({ padding: 8, ...colLining }, ["lining"])}>
  {renderCurtainLiningToggle(r, c, rowId)}
</td>

{/* FABRIC (m) */}
<td style={{ padding: 8, textAlign: "center", ...colFabricM }}>
  <span
    title={orderedFabricTooltip}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      whiteSpace: "nowrap",
    }}
  >
    <span>{c.fabricMeters.toFixed(2)}</span>
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: "50%",
        background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
        fontSize: 10,
        fontWeight: 900,
      }}
    >
      i
    </span>
  </span>
</td>

{/* TOTAL */}
<td style={{
  padding: 8,
  textAlign: "center",
  fontWeight: 700,
  color: isDark ? "#93c5fd" : "#1d4ed8",
  ...getStickyTotalCellStyle(effectiveRowSurface),
  ...colTotal
}}>
  <div>{currencySymbol(currency)} {c.rowTotal.toFixed(2)}</div>
  {c.lining?.enabled ? (
    <div
      style={{
        marginTop: 4,
        fontSize: 10.5,
        fontWeight: 800,
        color: dashboardMuted,
        lineHeight: 1.25,
      }}
    >
      Includes lining {currencySymbol(currency)}{" "}
      {Number(c.lining.amount || 0).toFixed(2)}
    </div>
  ) : null}
  {rowTrackConfig?.bendAmount > 0 ? (
    <div
      style={{
        marginTop: 4,
        fontSize: 10.5,
        fontWeight: 800,
        color: dashboardMuted,
        lineHeight: 1.25,
      }}
    >
      Bend {currencySymbol(currency)}{" "}
      {Number(rowTrackConfig.bendAmount || 0).toFixed(2)}
    </div>
  ) : null}
</td>


                        {/* ACTIONS */}
              {/* ACTIONS */}
<td style={{ padding: 6, ...getStickyActionsCellStyle(effectiveRowSurface), ...colActions }}>
  <div style={rowActionCellStyle}>
    {renderRowActionIconButton({
      label: "Add row below",
      title: "Add row below",
      color: "#16a34a",
      icon: Plus,
      onClick: () => insertRowAfter(i),
    })}
    {renderRowActionIconButton({
      label: "Delete row",
      title: "Delete row",
      color: "#dc2626",
      icon: Trash2,
      onClick: () => {
        removeQuoteRow(getWorkshopRowStableId(r));
      },
    })}
  </div>
</td>

                      </tr>
                      {(showTrackConfigPanel ||
                        (curtainRowSupportsLining(r, c) && c.lining?.enabled) ||
                        rowValidationMatches) ? (
                        <tr>
                          <td
                            colSpan={14}
                            style={{
                              padding: "0 8px 12px",
                              background: effectiveRowSurface,
                            }}
                          >
                            <div style={{ display: "grid", gap: 12 }}>
                              {rowValidationMatches ? (
                                <div
                                  role="alert"
                                  style={{
                                    borderRadius: 14,
                                    border: isDark
                                      ? "1px solid rgba(248,113,113,0.4)"
                                      : "1px solid #fecaca",
                                    background: isDark
                                      ? "rgba(127,29,29,0.34)"
                                      : "#fef2f2",
                                    color: isDark ? "#fecaca" : "#991b1b",
                                    padding: "10px 12px",
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  <div>{quoteRowValidationError.message}</div>
                                  <div style={{ marginTop: 4, opacity: 0.78 }}>
                                    Field: {quoteRowValidationError.failedFieldLabel || quoteRowValidationError.failedField || "Field"}
                                    {" "}· Current value:{" "}
                                    {String(quoteRowValidationError.failedValue ?? "") || "empty"}
                                  </div>
                                </div>
                              ) : null}
                              {showTrackConfigPanel ? renderCurtainTrackConfigPanel(r, c, rowId) : null}
                              {curtainRowSupportsLining(r, c) && c.lining?.enabled
                                ? renderCurtainLiningPanel(r, c, rowId)
                                : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            <div style={{ marginTop: 26, display: "grid", gap: 14 }}>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>Add-ons / Accessories</div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Motors, tracks, remotes, brackets, freight, and installation
                    charges stay out of fabric summary and optimizer.
                  </div>
                </div>
                <Button icon={Plus} variant="primary" onClick={addCurtainAccessoryRow}>
                  Add Accessory Row
                </Button>
              </div>
              {curtainAccessoryEntries.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {curtainAccessoryEntries.map(({ row: accessoryRow, index }, displayIndex) =>
                    renderCurtainAccessoryRow(accessoryRow, index, displayIndex)
                  )}
                </div>
              ) : (
                <div
                  style={{
                    borderRadius: 18,
                    border: isDark
                      ? "1px dashed rgba(96,165,250,0.35)"
                      : "1px dashed rgba(37,99,235,0.35)",
                    background: isDark ? "rgba(15,23,42,0.5)" : "#eff6ff",
                    color: dashboardMuted,
                    padding: 16,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  No accessories added yet. Add motors, tracks, remotes, or service
                  charges here when the project needs hardware.
                </div>
              )}
            </div>

            {/* Curtain Quote bottom flow: actions, summaries, totals */}
<div
  style={{
    marginTop: 30,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 18,
    alignItems: "stretch",
  }}
>

  {/* Save/PDF actions immediately after Accessories */}
  <div
    style={{
      display: "flex",
      gap: 14,
      alignItems: "center",
      flexWrap: "wrap",
      width: "100%",
      position: isMobile ? "sticky" : "static",
      bottom: isMobile ? 12 : "auto",
      zIndex: isMobile ? 8 : "auto",
      background: isMobile
        ? isDark
          ? "rgba(2,6,23,0.92)"
          : "rgba(255,255,255,0.96)"
        : isDark
        ? "rgba(15,23,42,0.66)"
        : "rgba(248,250,252,0.96)",
      padding: isMobile ? 12 : 16,
      borderRadius: 18,
      border: isDark
        ? "1px solid rgba(148,163,184,0.2)"
        : "1px solid rgba(226,232,240,0.95)",
      boxShadow: isMobile
        ? "0 14px 28px rgba(15,23,42,0.16)"
        : "0 12px 28px rgba(15,23,42,0.06)",
    }}
  >
    {(canEditQuotes || canCreateQuotes) ? (
      <Button
        type="button"
        icon={Save}
        variant="success"
        onClick={saveQuotation}
        disabled={isSaving || (quoteSubTab === GENERAL_QUOTE_TYPE && Boolean(generalUploadingItemId))}
      >
        {isSaving
          ? "Saving..."
          : quoteSubTab === GENERAL_QUOTE_TYPE && generalUploadingItemId
          ? "Uploading..."
          : "Save"}
      </Button>
    ) : null}

    {quoteSubTab !== GENERAL_QUOTE_TYPE && <QuotationHistoryButton functions={functions} workspaceUid={workspaceUid}
      quoteId={editingQuoteId || selectedQuote?.id || ''} quoteType="curtain" />}

    {canCreateQuotes ? (
      <Button
        type="button"
        icon={Save}
        variant="primary"
        onClick={saveQuotationAsNew}
        disabled={isSaving || (quoteSubTab === GENERAL_QUOTE_TYPE && Boolean(generalUploadingItemId))}
      >
        Save As New
      </Button>
    ) : null}



{/* Apple-like autosave indicator */}
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      color:
        autoSaveStatus === "saving"
          ? "#2563eb"
          : autoSaveStatus === "saved"
          ? "#16a34a"
          : autoSaveStatus === "error"
          ? "#dc2626"
          : "#64748b",
      transition: "all 0.25s ease",
    }}
  >
    {autoSaveStatus === "saving" && "Saving..."}
    {autoSaveStatus === "error" && "Save failed"}
    {autoSaveStatus !== "saving" &&
      autoSaveStatus !== "error" &&
      getLocalDraftStatusText(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE)}
  </div>

    {isQuoteMutationAuditDevMode() ? (
      <div
        style={{
          borderRadius: 14,
          border: quoteDataIntegrityWatch?.suspicious
            ? "1px solid rgba(239,68,68,0.55)"
            : "1px solid rgba(245,158,11,0.35)",
          background: quoteDataIntegrityWatch?.suspicious
            ? isDark
              ? "rgba(127,29,29,0.38)"
              : "rgba(254,242,242,0.98)"
            : isDark
            ? "rgba(120,53,15,0.28)"
            : "rgba(255,251,235,0.95)",
          color: isDark ? "#fde68a" : "#92400e",
          padding: "9px 11px",
          fontSize: 11,
          lineHeight: 1.45,
          maxWidth: 420,
        }}
      >
        <strong>Data Integrity Watch</strong>
        <div>
          Last mutation:{" "}
          {quoteDataIntegrityWatch
            ? `${quoteDataIntegrityWatch.source} | ${quoteDataIntegrityWatch.field} | ${String(
                quoteDataIntegrityWatch.previousValue ?? ""
              )} -> ${String(quoteDataIntegrityWatch.nextValue ?? "")}`
            : "none"}
        </div>
        <div>Row: {quoteDataIntegrityWatch?.rowId || "-"}</div>
        <div>Autosave: {autoSaveStatus || "idle"} | Saving: {isSaving ? "yes" : "no"}</div>
        <div style={{ opacity: 0.75 }}>
          Signature:{" "}
          {typeof window !== "undefined"
            ? String(window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ || "").slice(0, 90)
            : "-"}
        </div>
      </div>
    ) : null}

    {canUseCustomerPdf ? (
      <Button
        type="button"
        icon={Download}
        variant="primary"
        onClick={exportCustomerPDF}
      >
        Customer PDF
      </Button>
    ) : null}

    {canUseWorkshopPdf ? (
      <Button
        type="button"
        icon={Download}
        variant="primary"
        onClick={exportWorkshopPDF}
        onMouseEnter={preloadWorkshopZipRuntime}
        onFocus={preloadWorkshopZipRuntime}
        style={{ background: "#7c3aed" }}
      >
        Workshop ZIP
      </Button>
    ) : null}

 

    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flex: isCompact ? "1 1 100%" : "0 1 auto",
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#cbd5e1" : "#334155",
      }}
    >
      <input
        type="checkbox"
        checked={useHalfPieceReuse}
        onChange={(e) => {
          useHalfPieceReuseRef.current = e.target.checked;
          setUseHalfPieceReuse(e.target.checked);
        }}
      />
      Use half-piece reuse in workshop and optimizer
    </label>

    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        flex: isCompact ? "1 1 100%" : "1 1 360px",
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#cbd5e1" : "#334155",
        maxWidth: 430,
      }}
    >
      <input
        type="checkbox"
        checked={passHalfPieceSavingsToCustomer}
        onChange={(event) => {
          passHalfPieceSavingsToCustomerRef.current = event.target.checked;
          setPassHalfPieceSavingsToCustomer(event.target.checked);
        }}
        style={{ marginTop: 2 }}
      />
      <span>
        Pass half-piece savings to customer
        <span
          style={{
            display: "block",
            marginTop: 3,
            fontSize: 11,
            fontWeight: 500,
            color: dashboardMuted,
            lineHeight: 1.35,
          }}
        >
          {passHalfPieceSavingsToCustomer
            ? "Customer billable fabric and customer PDF total use the reduced fabric requirement."
            : "Customer is billed using full ordered fabric. Half-piece reuse remains internal workshop information."}
        </span>
      </span>
    </label>

  </div>

{quoteFullnessOverrideRows.length > 0 ? (
  <div
    style={{
      marginTop: 18,
      padding: isCompact ? "18px 16px" : "22px 24px",
      borderRadius: 20,
      background: isDark
        ? "linear-gradient(135deg,rgba(15,23,42,0.88),rgba(30,41,59,0.76))"
        : "linear-gradient(135deg,#ffffff,#f8fafc)",
      border: isDark
        ? "1px solid rgba(45,212,191,0.24)"
        : "1px solid rgba(125,211,252,0.65)",
      boxShadow: isDark
        ? "0 18px 42px rgba(2,6,23,0.28)"
        : "0 18px 36px rgba(14,165,233,0.08)",
      display: "grid",
      gap: 16,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexDirection: isCompact ? "column" : "row",
        alignItems: isCompact ? "stretch" : "flex-start",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            letterSpacing: "-0.2px",
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          Fullness &amp; Track Rates for This Quote
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.45 }}>
          Leave blank to use the default fullness from Stitching & Tracks settings.
        </div>
      </div>
      <div
        style={{
          borderRadius: 999,
          padding: "7px 12px",
          background: isDark ? "rgba(45,212,191,0.12)" : "#ecfeff",
          color: isDark ? "#5eead4" : "#0f766e",
          fontSize: 12,
          fontWeight: 900,
          whiteSpace: "nowrap",
          alignSelf: isCompact ? "flex-start" : "center",
        }}
      >
        {quoteFullnessOverrideRows.length} stitch type
        {quoteFullnessOverrideRows.length === 1 ? "" : "s"}
      </div>
    </div>

    <div
      style={{
        overflowX: "auto",
        borderRadius: 16,
        border: "1px solid rgba(148,163,184,0.22)",
        background: isDark ? "rgba(2,6,23,0.38)" : "rgba(255,255,255,0.8)",
      }}
    >
      <div style={{ minWidth: 620 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px,1.3fr) minmax(130px,0.7fr) minmax(170px,0.8fr) minmax(120px,0.6fr)",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(148,163,184,0.2)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: dashboardMuted,
          }}
        >
          <div>Stitch / Track Type</div>
          <div>Default Fullness</div>
          <div>Quote Fullness</div>
          <div>Status</div>
        </div>
        {quoteFullnessOverrideRows.map((stitchRow, index) => {
          const inputValue =
            quoteFullnessOverrideInputs[stitchRow.stitchingKey] ??
            (stitchRow.quoteFullness ? String(stitchRow.quoteFullness) : "");
          const parsedInput = Number(inputValue);
          const isInvalidFullness =
            inputValue !== "" &&
            (!Number.isFinite(parsedInput) || parsedInput <= 0 || parsedInput > 10);
          const usingQuoteOverride =
            !isInvalidFullness &&
            inputValue !== "" &&
            Number.isFinite(parsedInput) &&
            parsedInput > 0;

          return (
            <div
              key={stitchRow.stitchingKey}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px,1.3fr) minmax(130px,0.7fr) minmax(170px,0.8fr) minmax(120px,0.6fr)",
                gap: 12,
                alignItems: "center",
                padding: "12px 14px",
                borderBottom:
                  index === quoteFullnessOverrideRows.length - 1
                    ? "none"
                    : "1px solid rgba(148,163,184,0.14)",
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  {stitchRow.stitchingLabel}
                </div>
                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 2 }}>
                  Used in {stitchRow.rowCount} row{stitchRow.rowCount === 1 ? "" : "s"}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: isDark ? "#cbd5e1" : "#334155" }}>
                {stitchRow.defaultFullness
                  ? Number(stitchRow.defaultFullness).toFixed(2)
                  : "-"}
              </div>
              <div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={{
                    ...input,
                    minHeight: 38,
                    borderColor: isInvalidFullness
                      ? "#ef4444"
                      : input.borderColor || "rgba(148,163,184,0.35)",
                  }}
                  value={inputValue}
                  placeholder={
                    stitchRow.defaultFullness
                      ? `Default ${Number(stitchRow.defaultFullness).toFixed(2)}`
                      : "Use default"
                  }
                  onChange={(event) =>
                    updateQuoteFullnessOverride(stitchRow, event.target.value)
                  }
                />
                {isInvalidFullness ? (
                  <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: "#dc2626" }}>
                    Enter a fullness between 0 and 10.
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  justifySelf: "start",
                  borderRadius: 999,
                  padding: "6px 10px",
                  background: usingQuoteOverride
                    ? isDark
                      ? "rgba(34,197,94,0.13)"
                      : "#ecfdf5"
                    : isDark
                    ? "rgba(148,163,184,0.12)"
                    : "#f1f5f9",
                  color: usingQuoteOverride
                    ? isDark
                      ? "#86efac"
                      : "#047857"
                    : dashboardMuted,
                  fontSize: 11,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                }}
              >
                {usingQuoteOverride ? "Quote override" : "Using default"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
) : null}

 <QuoteConstructionControls key={`tracks-${editingQuoteId || "new"}`} tracks={quoteTrackControlRows} onTrack={updateQuoteTrackOverride} />
 {selectedQuote?.id && !quoteConstructionSettings.quoteCalculationSettingsSnapshot?.version ? <p style={{ fontSize: 12 }}>
   Legacy quotation: original financial values remain preserved. Where historical defaults are unavailable, current Settings are shown only for an intentional calculation revision.
 </p> : null}
 {constructionRevisionReview ? <div role="status" style={{ padding: 12, border: "1px solid #d97706", borderRadius: 10 }}>
   Calculation revision preview: original total {Number(constructionRevisionReview.originalTotal).toFixed(2)}.
   Proposed total {Number(curtainSummaryTotalsRef.current?.grandTotal || 0).toFixed(2)}.
   Review the updated Fabric Summary and track charges before explicitly saving. The saved quotation is unchanged until Save.
 </div> : null}
 {/* FABRIC SUMMARY UI */}

{fabricSummaryUI &&
  typeof fabricSummaryUI === "object" &&
  Object.keys(fabricSummaryUI).length > 0 &&
  (() => {
    const fabricSummaryRows = curtainCanonicalFabricSummaryRows.map((row) => ({
      ...row,
      usedIn: row.usedInItems || [],
      fabricToOrderMeters: Number(
        row.fabricToOrderMeters ??
          row.workshopFabricRequiredMeters ??
          row.newFabricNeededMeters ??
          0
      ),
      halfReusedMeters: row.halfReusedMeters,
      billableMeters: row.customerBillableFabricMeters,
    }));

    if (fabricSummaryRows.length === 0) return null;

    const totalFabricToOrderMeters = roundMetersForDisplay(
      fabricSummaryRows.reduce(
        (acc, row) => acc + Number(row.fabricToOrderMeters || 0),
        0
      )
    );
    const totalBillableMeters = roundMetersForDisplay(
      fabricSummaryRows.reduce(
        (acc, row) => acc + Number(row.billableMeters || 0),
        0
      )
    );
    const totalHalfPieceSavingMeters = roundMetersForDisplay(
      fabricSummaryRows.reduce(
        (acc, row) => acc + Number(row.halfReusedMeters || 0),
        0
      )
    );
    const showWorkshopSavingsNote =
      useHalfPieceReuse &&
      !passHalfPieceSavingsToCustomer &&
      totalHalfPieceSavingMeters > 0.005;
    return (
      <div
        className="shimmer-bg"
        style={{
          marginTop: 0,
          padding: isCompact ? "20px 18px" : "26px 28px",
          borderRadius: "20px",
          background: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.72)",
          backdropFilter: "blur(18px) saturate(180%)",
          WebkitBackdropFilter: "blur(18px) saturate(180%)",
          border: isDark
            ? "1px solid rgba(148,163,184,0.24)"
            : "1px solid rgba(255,255,255,0.55)",
          boxShadow:
            "0 12px 36px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.25)",
          animation: "fadeSlideUp 0.45s ease both",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isCompact ? "flex-start" : "center",
            flexDirection: isCompact ? "column" : "row",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: "-0.35px",
              color: isDark ? "#f8fafc" : "#0f172a",
            }}
          >
            Fabric Summary
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {canViewHalfPieceAudit ? (
              <button
                type="button"
                onClick={() => setHalfPieceAuditOpen((open) => !open)}
                style={{
                  padding: "7px 12px",
                  borderRadius: "999px",
                  border: isDark
                    ? "1px solid rgba(251,191,36,0.32)"
                    : "1px solid rgba(217,119,6,0.22)",
                  background: isDark ? "rgba(251,191,36,0.12)" : "#fffbeb",
                  color: isDark ? "#fde68a" : "#92400e",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Half-Piece Audit
              </button>
            ) : null}
            {canViewHalfPieceAudit ? (
              <button
                type="button"
                onClick={copyCurtainRowCalculationAudit}
                style={{
                  padding: "7px 12px",
                  borderRadius: "999px",
                  border: isDark
                    ? "1px solid rgba(96,165,250,0.32)"
                    : "1px solid rgba(37,99,235,0.18)",
                  background: isDark ? "rgba(59,130,246,0.14)" : "#eff6ff",
                  color: isDark ? "#bfdbfe" : "#1d4ed8",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Copy Row Calculation Audit
              </button>
            ) : null}
            <div
              style={{
                padding: "7px 14px",
                borderRadius: "999px",
                background: isDark ? "rgba(59,130,246,0.16)" : "rgba(15,23,42,0.06)",
                border: isDark ? "1px solid rgba(96,165,250,0.18)" : "1px solid rgba(15,23,42,0.06)",
                fontSize: 12.5,
                fontWeight: 800,
                color: isDark ? "#bfdbfe" : "#334155",
                whiteSpace: "nowrap",
              }}
            >
              Fabric to Order {roundMetersForDisplay(totalFabricToOrderMeters).toFixed(2)} m
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 12px",
            marginBottom: 16,
            fontSize: 12.5,
            color: isDark ? "#cbd5e1" : "#334155",
          }}
        >
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(16,185,129,0.14)" : "rgba(236,253,245,0.94)",
              border: "1px solid rgba(16,185,129,0.22)",
              whiteSpace: "nowrap",
            }}
          >
            Fabric to Order: <strong>{roundMetersForDisplay(totalFabricToOrderMeters).toFixed(2)} m</strong>
          </span>
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(59,130,246,0.14)" : "rgba(239,246,255,0.94)",
              border: "1px solid rgba(59,130,246,0.22)",
              whiteSpace: "nowrap",
            }}
          >
            Customer Billable: <strong>{roundMetersForDisplay(totalBillableMeters).toFixed(2)} m</strong>
          </span>
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(251,191,36,0.14)" : "rgba(254,243,199,0.94)",
              border: "1px solid rgba(251,191,36,0.28)",
              whiteSpace: "nowrap",
            }}
          >
            Half-piece Saving: <strong>{roundMetersForDisplay(totalHalfPieceSavingMeters).toFixed(2)} m</strong>
          </span>
        </div>
        {showWorkshopSavingsNote ? (
          <div
            style={{
              marginBottom: 14,
              borderRadius: 12,
              padding: "9px 11px",
              background: isDark
                ? "rgba(148,163,184,0.1)"
                : "rgba(248,250,252,0.9)",
              color: isDark ? "#cbd5e1" : "#475569",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Customer is billed full fabric. Half-piece saving is used only for workshop/order planning.
            {halfPieceAuditResultText ? (
              <div style={{ marginTop: 6, fontWeight: 800 }}>
                Reuse result: {halfPieceAuditResultText}
              </div>
            ) : null}
          </div>
        ) : null}

        {canViewHalfPieceAudit && halfPieceAuditOpen && curtainHalfPieceAudit ? (
          <div
            style={{
              display: "grid",
              gap: 12,
              marginBottom: 14,
              padding: 14,
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.66)" : "#fff7ed",
              border: isDark
                ? "1px solid rgba(251,191,36,0.22)"
                : "1px solid rgba(251,146,60,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 950, color: isDark ? "#f8fafc" : "#7c2d12" }}>
                  Half-Piece Reuse Audit
                </div>
                <div style={{ marginTop: 3, fontSize: 12, color: dashboardMuted }}>
                  Same canonical workshop fabric plan used by the quote screen, optimizer, and Workshop PDF.
                </div>
              </div>
              <button
                type="button"
                onClick={copyHalfPieceAuditJson}
                style={{
                  padding: "7px 11px",
                  borderRadius: 10,
                  border: 0,
                  background: "#ea580c",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Copy Half-Piece Audit JSON
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(4, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              {[
                ["Reuse", curtainHalfPieceAudit.enabled ? "Enabled" : "Disabled"],
                ["Half Needs", `${Number(curtainHalfPieceAudit.totalHalfPanelNeeds || 0)} row(s)`],
                ["Pairs", `${Number(curtainHalfPieceAudit.totalPairsCreated || 0)} pair(s)`],
                ["Reused", `${Number(curtainHalfPieceAudit.totalHalfPiecesReused || 0)} half piece(s)`],
                ["Fabric Saved", `${Number(curtainHalfPieceAudit.totalMetersSaved || 0).toFixed(2)} m`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: "9px 10px",
                    borderRadius: 12,
                    background: isDark ? "rgba(2,6,23,0.44)" : "rgba(255,255,255,0.72)",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 900, color: dashboardMuted, textTransform: "uppercase" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: 3, fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {(curtainHalfPieceAudit.reasons || []).length > 0 ? (
              <div style={{ fontSize: 12, fontWeight: 800, color: isDark ? "#fed7aa" : "#9a3412" }}>
                {(curtainHalfPieceAudit.reasons || []).join(" ")}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 8 }}>
              {(curtainHalfPieceAudit.fabricGroups || []).map((group) => (
                <div
                  key={group.fabricKey}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: isDark ? "rgba(2,6,23,0.34)" : "rgba(255,255,255,0.68)",
                    border: "1px solid rgba(148,163,184,0.16)",
                    fontSize: 12,
                    color: isDark ? "#cbd5e1" : "#334155",
                  }}
                >
                  <div style={{ fontWeight: 950, color: isDark ? "#f8fafc" : "#111827" }}>
                    {group.fabricCode || group.fabricKey}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Rows {group.rowsEligible}/{group.rowsConsidered} eligible | Half needs {group.halfPanelNeeds || 0} | Pairs {group.pairsCreated || 0} | Reused {group.halfPiecesReused} | Saved {Number(group.metersSaved || 0).toFixed(2)} m
                  </div>
                  {(group.reasons || []).length > 0 ? (
                    <div style={{ marginTop: 4, color: dashboardMuted }}>
                      Reason: {(group.reasons || []).join(" ")}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "repeat(auto-fit, minmax(420px, 1fr))",
            gap: 14,
          }}
        >
          {fabricSummaryRows.map((row) => {
            const current = normalizeCurtainRepeatEntry(
              repeatSettings?.[row.normalizedKey] || row.repeatSettings || {}
            );
            const repeatProductTypeOptions = row.repeatProductTypeOptions || [];
            const repeatProductTypeKeys = repeatProductTypeOptions
              .map((option) => option.key)
              .filter(Boolean);
            const currentRepeatScope = normalizeRepeatProductTypeScope(
              current.productTypeScope || []
            );
            const effectiveRepeatScope =
              current.applyMode === REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES
                ? currentRepeatScope
                : repeatProductTypeKeys;
            const repeatScopeDisplay = getRepeatScopeSummaryDisplay(
              current,
              repeatProductTypeOptions
            );
            const usedInLabel =
              row.usedIn?.length > 0
                ? `${row.usedIn.slice(0, 5).join(", ")}${
                    row.usedIn.length > 5 ? ` +${row.usedIn.length - 5} more` : ""
                  }`
                : "Not linked to visible rows";

            return (
              <div
                key={row.normalizedKey || row.key}
                style={{
                  display: "grid",
                  gap: 14,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.2)"
                    : "1px solid rgba(226,232,240,0.95)",
                  background: isDark ? "rgba(2,6,23,0.36)" : "rgba(255,255,255,0.88)",
                  boxShadow: isDark
                    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                    : "0 10px 24px rgba(15,23,42,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 14,
                    flexDirection: isCompact ? "column" : "row",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      minWidth: 0,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 12,
                        height: 12,
                        flex: "0 0 12px",
                        marginTop: 4,
                        borderRadius: "50%",
                        background: row.color,
                        boxShadow: "0 0 8px rgba(0,0,0,0.16)",
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        title={row.displayName}
                        style={{
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          fontWeight: 900,
                          fontSize: 16,
                          lineHeight: 1.35,
                          color: isDark ? "#f8fafc" : "#0f172a",
                        }}
                      >
                        {row.displayName}
                      </div>
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 12,
                          color: dashboardMuted,
                          lineHeight: 1.45,
                        }}
                      >
                        Used in: {usedInLabel}
                      </div>
                    </div>
                  </div>

                  <select
                    style={{
                      ...selectInput,
                      width: isCompact ? "100%" : 180,
                      minHeight: 34,
                      padding: "7px 9px",
                      fontSize: 12,
                    }}
                    value={row.brandId || ""}
                    onChange={(event) =>
                      updateFabricSummaryBrand(row.normalizedKey, event.target.value)
                    }
                    aria-label={`Select brand for ${row.displayName}`}
                  >
                    <option value="">Select brand</option>
                    {fabricBrands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>

                {row.brandName ? (
                  <div
                    style={{
                      marginTop: -6,
                      fontSize: 11,
                      fontWeight: 800,
                      color: row.hasMixedBrands
                        ? isDark
                          ? "#fde68a"
                          : "#92400e"
                        : isDark
                        ? "#bfdbfe"
                        : "#1d4ed8",
                    }}
                  >
                    {row.hasMixedBrands
                      ? `Mixed brands: ${(row.mixedBrandNames || []).join(", ")}`
                      : row.brandSource === "row_inferred"
                      ? `Brand: ${row.brandName} - auto-selected from quote rows`
                      : `Brand: ${row.brandName}`}
                  </div>
                ) : null}

                <QuoteConstructionControls key={`hems-${editingQuoteId || "new"}-${row.normalizedKey}`} fabrics={getQuoteHemControlRows(rows.filter(item => getCurtainRowFabricKey(item) === row.normalizedKey))} onHem={updateQuoteHemOverride} />
                {(() => {
                  const photoUrl = getFabricPhotoDisplayUrl(row.primaryPhoto);
                  const fullPhotoUrl = getFabricPhotoFullUrl(row.primaryPhoto);
                  const isUploadingPhoto =
                    row.fabricLibraryKey && fabricPhotoUploadingKey === row.fabricLibraryKey;
                  const isRemovingPhoto =
                    row.fabricLibraryKey && fabricPhotoRemovingKey === row.fabricLibraryKey;
                  const isCreatingPhoneUpload =
                    row.fabricLibraryKey && fabricPhotoQrCreatingKey === row.fabricLibraryKey;
                  const isPhotoActionBusy = Boolean(
                    isUploadingPhoto || isRemovingPhoto || isCreatingPhoneUpload
                  );
                  const photoInputId = `fabric-photo-input-${String(
                    row.fabricLibraryKey || row.normalizedKey || row.key || "fabric"
                  ).replace(/[^a-zA-Z0-9_-]+/g, "-")}`;

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: isCompact ? "stretch" : "center",
                        flexDirection: isCompact ? "column" : "row",
                        gap: 10,
                        padding: 10,
                        borderRadius: 16,
                        background: isDark
                          ? "rgba(15,23,42,0.48)"
                          : "linear-gradient(135deg,#f8fafc,#ffffff)",
                        border: isDark
                          ? "1px solid rgba(148,163,184,0.2)"
                          : "1px solid rgba(226,232,240,0.95)",
                      }}
                    >
                      <div
                        style={{
                          width: 66,
                          height: 66,
                          flex: "0 0 66px",
                          borderRadius: 14,
                          overflow: "hidden",
                          background: isDark ? "rgba(2,6,23,0.6)" : "#e2e8f0",
                          border: "1px solid rgba(148,163,184,0.22)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={`${row.displayName || "Fabric"} reference`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : (
                          <FileImage size={24} color={isDark ? "#94a3b8" : "#64748b"} />
                        )}
                      </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 950,
                            color: isDark ? "#f8fafc" : "#0f172a",
                          }}
                        >
                          Fabric Photo
                        </div>
                        <div
                          style={{
                            marginTop: 3,
                            fontSize: 11.5,
                            lineHeight: 1.4,
                            color: dashboardMuted,
                          }}
                        >
                          {photoUrl
                            ? "Saved in fabric library and reused for the same fabric."
                            : "Upload once to identify this fabric in quotes and workshop PDFs."}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                          justifyContent: isCompact ? "flex-start" : "flex-end",
                        }}
                      >
                        <input
                          id={photoInputId}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={isPhotoActionBusy}
                          style={{ display: "none" }}
                          onChange={async (event) => {
                            const selectedFile = event.target.files?.[0] || null;
                            await handleFabricPhotoFileSelected(row, selectedFile);
                            event.target.value = "";
                          }}
                        />
                        {fullPhotoUrl ? (
                          <button
                            type="button"
                            onClick={() => window.open(fullPhotoUrl, "_blank", "noopener,noreferrer")}
                            style={{
                              minHeight: 34,
                              padding: "8px 11px",
                              borderRadius: 12,
                              border: isDark
                                ? "1px solid rgba(148,163,184,0.28)"
                                : "1px solid rgba(203,213,225,0.95)",
                              background: isDark ? "rgba(15,23,42,0.62)" : "#ffffff",
                              color: isDark ? "#e2e8f0" : "#334155",
                              fontSize: 12,
                              fontWeight: 900,
                              cursor: "pointer",
                            }}
                          >
                            View Larger
                          </button>
                        ) : null}
                        <label
                          htmlFor={photoInputId}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            minHeight: 34,
                            padding: "8px 11px",
                            borderRadius: 12,
                            border: "none",
                            background: isUploadingPhoto ? "#94a3b8" : "#2563eb",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: isPhotoActionBusy ? "wait" : "pointer",
                            pointerEvents: isPhotoActionBusy ? "none" : "auto",
                            boxShadow: "0 8px 18px rgba(37,99,235,0.18)",
                          }}
                          title={photoUrl ? "Replace fabric photo" : "Upload fabric photo"}
                        >
                          <UploadCloud size={15} />
                          {isUploadingPhoto
                            ? "Uploading..."
                            : photoUrl
                            ? "Replace Photo"
                            : "Upload Photo"}
                        </label>
                        {photoUrl ? (
                          <button
                            type="button"
                            onClick={() => handleRemoveFabricPhoto(row)}
                            disabled={isPhotoActionBusy}
                            style={{
                              minHeight: 34,
                              padding: "8px 11px",
                              borderRadius: 12,
                              border: "none",
                              background: isRemovingPhoto ? "#94a3b8" : "#dc2626",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 900,
                              cursor: isPhotoActionBusy ? "wait" : "pointer",
                              opacity: isPhotoActionBusy && !isRemovingPhoto ? 0.72 : 1,
                            }}
                            title="Remove fabric photo"
                          >
                            <Trash2 size={15} style={{ marginRight: 6 }} />
                            {isRemovingPhoto ? "Removing..." : "Remove Photo"}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openFabricPhotoQrUpload(row)}
                          disabled={isPhotoActionBusy}
                          style={{
                            minHeight: 34,
                            padding: "8px 11px",
                            borderRadius: 12,
                            border: isDark
                              ? "1px solid rgba(96,165,250,0.4)"
                              : "1px solid rgba(37,99,235,0.24)",
                            background: isCreatingPhoneUpload
                              ? "#94a3b8"
                              : isDark
                              ? "rgba(37,99,235,0.22)"
                              : "#eff6ff",
                            color: isDark ? "#bfdbfe" : "#1d4ed8",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: isPhotoActionBusy ? "wait" : "pointer",
                            opacity: isPhotoActionBusy && !isCreatingPhoneUpload ? 0.72 : 1,
                          }}
                          title="Upload fabric photo from phone"
                        >
                          <Smartphone size={15} style={{ marginRight: 6 }} />
                          {isCreatingPhoneUpload ? "Creating..." : "Upload from Phone"}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact
                      ? "repeat(2, minmax(0, 1fr))"
                      : "repeat(4, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  {[
                    [
                      "Fabric Width",
                      row.hasMixedFabricWidths
                        ? `${(row.fabricWidthValues || [])
                            .map((value) => getFabricWidthDisplayLabel(value))
                            .filter(Boolean)
                            .join(", ")}`
                        : getFabricWidthDisplayLabel(row.fabricWidthIn) || "54 in",
                    ],
                    ["Fabric to Order", `${roundMetersForDisplay(row.fabricToOrderMeters).toFixed(2)} m`],
                    ["Customer Billable", `${roundMetersForDisplay(row.billableMeters).toFixed(2)} m`],
                    ["Half-piece Saving", `${roundMetersForDisplay(row.halfReusedMeters).toFixed(2)} m`],
                  ].map(([label, value]) => {
                    const isCustomerBillableMetric = label === "Customer Billable";
                    const isFabricToOrderMetric = label === "Fabric to Order";
                    return (
                    <div
                      key={label}
                      style={{
                        borderRadius: 14,
                        padding: "10px 11px",
                        background:
                          isFabricToOrderMetric
                            ? isDark
                              ? "rgba(16,185,129,0.2)"
                              : "#ecfdf5"
                            : isCustomerBillableMetric
                            ? isDark
                              ? "rgba(37,99,235,0.2)"
                              : "#eff6ff"
                            : isDark
                            ? "rgba(15,23,42,0.52)"
                            : "#f8fafc",
                        border:
                          isFabricToOrderMetric
                            ? "1px solid rgba(16,185,129,0.26)"
                            : isCustomerBillableMetric
                            ? "1px solid rgba(37,99,235,0.24)"
                            : "1px solid rgba(148,163,184,0.18)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10.5,
                          fontWeight: 900,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: dashboardMuted,
                          marginBottom: 4,
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 900,
                          color:
                            isFabricToOrderMetric
                              ? "#059669"
                              : isCustomerBillableMetric
                              ? "#2563eb"
                              : isDark
                              ? "#f8fafc"
                              : "#0f172a",
                        }}
                      >
                        {value}
                        {label === "Fabric Width" && row.fabricWidthManuallyOverridden ? (
                          <span
                            style={{
                              display: "block",
                              marginTop: 3,
                              fontSize: 10.5,
                              fontWeight: 800,
                              color: isDark ? "#bfdbfe" : "#1d4ed8",
                            }}
                          >
                            Manual width
                          </span>
                        ) : null}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(15,23,42,0.5)" : "rgba(248,250,252,0.9)",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: dashboardMuted,
                      marginBottom: 10,
                    }}
                  >
                    Repeat Settings
                  </div>
                  {current.horizontalEnabled ? (
                    <div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 10, color: dashboardMuted }}>
                      HR uses conservative equal-repeat join widths, not a guaranteed minimum-waste layout.
                      Check pattern phase and seams before cutting. Physical cut lengths retain full precision;
                      displayed metres and pricing are rounded.
                    </div>
                  ) : null}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 12,
                          fontWeight: 700,
                          color: isDark ? "#cbd5e1" : "#334155",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={current.verticalEnabled || false}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              verticalEnabled: e.target.checked,
                              verticalRepeatValue: current.verticalRepeatValue || "",
                              verticalRepeatUnit: current.verticalRepeatUnit || "in",
                              verticalRepeatIn: current.verticalRepeatInch || "",
                              verticalRepeatInch: current.verticalRepeatInch || "",
                            })
                          }
                        />
                        Vertical Repeat
                      </label>

                      {current.verticalEnabled && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) 74px",
                            gap: 8,
                            marginTop: 7,
                          }}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder={`Repeat (${current.verticalRepeatUnit || "in"})`}
                            style={input}
                            value={current.verticalRepeatValue ?? ""}
                            onChange={(e) =>
                              updateFabricRepeatMeasurementValue(
                                row.normalizedKey,
                                "vertical",
                                e.target.value,
                                current
                              )
                            }
                          />
                          <select
                            style={selectInput}
                            value={current.verticalRepeatUnit || "in"}
                            onChange={(e) =>
                              updateFabricRepeatMeasurementUnit(
                                row.normalizedKey,
                                "vertical",
                                e.target.value,
                                current
                              )
                            }
                          >
                            <option value="in">in</option>
                            <option value="cm">cm</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 12,
                          fontWeight: 700,
                          color: isDark ? "#cbd5e1" : "#334155",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={current.horizontalEnabled || false}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              horizontalEnabled: e.target.checked,
                              horizontalRepeatValue: current.horizontalRepeatValue || "",
                              horizontalRepeatUnit: current.horizontalRepeatUnit || "in",
                              horizontalRepeatIn: current.horizontalRepeatInch || "",
                              horizontalRepeatInch: current.horizontalRepeatInch || "",
                            })
                          }
                        />
                        Horizontal Repeat
                      </label>

                      {current.horizontalEnabled && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) 74px",
                            gap: 8,
                            marginTop: 7,
                          }}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder={`Repeat (${current.horizontalRepeatUnit || "in"})`}
                            style={input}
                            value={current.horizontalRepeatValue ?? ""}
                            onChange={(e) =>
                              updateFabricRepeatMeasurementValue(
                                row.normalizedKey,
                                "horizontal",
                                e.target.value,
                                current
                              )
                            }
                          />
                          <select
                            style={selectInput}
                            value={current.horizontalRepeatUnit || "in"}
                            onChange={(e) =>
                              updateFabricRepeatMeasurementUnit(
                                row.normalizedKey,
                                "horizontal",
                                e.target.value,
                                current
                              )
                            }
                          >
                            <option value="in">in</option>
                            <option value="cm">cm</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {repeatProductTypeOptions.length > 0 ? (
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: "1px solid rgba(148,163,184,0.18)",
                        display: "grid",
                        gap: 9,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: dashboardMuted,
                        }}
                      >
                        Repeat Applies To
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "7px 10px",
                            borderRadius: 999,
                            background:
                              current.applyMode !== REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES
                                ? isDark
                                  ? "rgba(37,99,235,0.22)"
                                  : "#dbeafe"
                                : isDark
                                ? "rgba(15,23,42,0.48)"
                                : "#ffffff",
                            border: "1px solid rgba(148,163,184,0.22)",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name={`repeat-apply-mode-${row.normalizedKey}`}
                            checked={current.applyMode !== REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES}
                            onChange={() =>
                              updateFabricRepeatSettings(row.normalizedKey, {
                                applyMode: REPEAT_APPLY_MODES.ALL_FABRIC_PRODUCTS,
                                productTypeScope: [],
                              })
                            }
                          />
                          All fabric products
                        </label>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "7px 10px",
                            borderRadius: 999,
                            background:
                              current.applyMode === REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES
                                ? isDark
                                  ? "rgba(37,99,235,0.22)"
                                  : "#dbeafe"
                                : isDark
                                ? "rgba(15,23,42,0.48)"
                                : "#ffffff",
                            border: "1px solid rgba(148,163,184,0.22)",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name={`repeat-apply-mode-${row.normalizedKey}`}
                            checked={current.applyMode === REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES}
                            onChange={() =>
                              updateFabricRepeatSettings(row.normalizedKey, {
                                applyMode: REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES,
                                productTypeScope: repeatProductTypeKeys,
                              })
                            }
                          />
                          Selected product types
                        </label>
                      </div>

                      {current.applyMode === REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 7,
                          }}
                        >
                          {repeatProductTypeOptions.map((option) => {
                            const checked = effectiveRepeatScope.includes(option.key);
                            const preventEmptyScope =
                              checked && effectiveRepeatScope.length <= 1;
                            return (
                              <label
                                key={option.key}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 9px",
                                  borderRadius: 999,
                                  background: checked
                                    ? isDark
                                      ? "rgba(16,185,129,0.28)"
                                      : "#dcfce7"
                                    : isDark
                                    ? "rgba(15,23,42,0.42)"
                                    : "#f8fafc",
                                  border: checked
                                    ? "1px solid rgba(16,185,129,0.58)"
                                    : "1px solid rgba(148,163,184,0.18)",
                                  color: checked
                                    ? isDark
                                      ? "#bbf7d0"
                                      : "#065f46"
                                    : isDark
                                    ? "#e2e8f0"
                                    : "#334155",
                                  fontSize: 12,
                                  fontWeight: 900,
                                  cursor: preventEmptyScope ? "not-allowed" : "pointer",
                                  boxShadow: checked
                                    ? "0 0 0 2px rgba(16,185,129,0.12)"
                                    : "none",
                                }}
                                title={
                                  preventEmptyScope
                                    ? "At least one product type must stay selected"
                                    : `Apply repeat to ${option.label}`
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={preventEmptyScope}
                                  aria-label={`Apply repeat to ${option.label}`}
                                  style={{
                                    width: 14,
                                    height: 14,
                                    accentColor: "#059669",
                                    opacity: 1,
                                  }}
                                  onChange={(event) => {
                                    const nextScope = event.target.checked
                                      ? Array.from(new Set([...effectiveRepeatScope, option.key]))
                                      : effectiveRepeatScope.filter((key) => key !== option.key);
                                    updateFabricRepeatSettings(row.normalizedKey, {
                                      applyMode: REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES,
                                      productTypeScope: nextScope,
                                    });
                                  }}
                                />
                                {option.label}
                              </label>
                            );
                          })}
                        </div>
                      ) : null}

                      {current.applyMode === REPEAT_APPLY_MODES.SELECTED_PRODUCT_TYPES &&
                      currentRepeatScope.length === 0 ? (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: isDark ? "#fde68a" : "#92400e",
                          }}
                        >
                          Select at least one product type.
                        </div>
                      ) : null}

                      <div style={{ fontSize: 11, color: dashboardMuted }}>
                        Current scope: {repeatScopeDisplay}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              );
            })}
        </div>

        <div
          style={{
            marginTop: 22,
            height: 3,
            borderRadius: 4,
            background: "linear-gradient(90deg,transparent,#94a3b8,transparent)",
            opacity: 0.35,
            animation: "glowLine 5s ease-in-out infinite",
          }}
        />
      </div>
    );
  })()}

{curtainLiningSummaryRows.length > 0 ? (
  <div
    style={{
      marginTop: 18,
      padding: isCompact ? "18px 16px" : "22px 24px",
      borderRadius: 20,
      background: isDark
        ? "linear-gradient(135deg,rgba(15,23,42,0.88),rgba(30,41,59,0.76))"
        : "linear-gradient(135deg,#ffffff,#f8fafc)",
      border: isDark
        ? "1px solid rgba(96,165,250,0.22)"
        : "1px solid rgba(191,219,254,0.85)",
      boxShadow: isDark
        ? "0 18px 40px rgba(2,6,23,0.24)"
        : "0 18px 34px rgba(37,99,235,0.07)",
      display: "grid",
      gap: 14,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexDirection: isCompact ? "column" : "row",
        alignItems: isCompact ? "stretch" : "center",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          Lining Summary
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
          Separate from main curtain fabric demand and roll optimizer quantities.
        </div>
      </div>
      <div
        style={{
          borderRadius: 999,
          padding: "7px 12px",
          background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
          color: "#2563eb",
          fontSize: 12,
          fontWeight: 900,
          whiteSpace: "nowrap",
        }}
      >
        Total {currencySymbol(currency)} {curtainLiningTotalAmount.toFixed(2)}
      </div>
    </div>
    <div
      style={{
        overflowX: "auto",
        borderRadius: 16,
        border: "1px solid rgba(148,163,184,0.22)",
        background: isDark ? "rgba(2,6,23,0.42)" : "rgba(255,255,255,0.82)",
      }}
    >
      <div style={{ minWidth: 680 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(150px,1fr) minmax(180px,1fr) 100px 100px 110px",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(148,163,184,0.2)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: dashboardMuted,
          }}
        >
          <div>Type</div>
          <div>Fabric / Code</div>
          <div>Method</div>
          <div style={{ textAlign: "right" }}>Qty</div>
          <div style={{ textAlign: "right" }}>Amount</div>
        </div>
        {curtainLiningSummaryRows.map((row, index) => {
          const liningDisplayInfo = sanitizeCurtainFabricForDisplayAgainstSettings({
            brandName: row.brandName,
            fabricBrandName: row.brandName,
            fabricCode: row.fabricCode,
            fabric: row.fabricCode,
          });
          const liningFabricCode =
            liningDisplayInfo.displayFabricCode ||
            sanitizeFabricTextAgainstSavedBrandPrefix(row.fabricCode, row);
          const liningBrandName = liningDisplayInfo.isValidCatalogue
            ? liningDisplayInfo.displayBrandName
            : "";
          return (
          <div
            key={row.key}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(150px,1fr) minmax(180px,1fr) 100px 100px 110px",
              gap: 12,
              alignItems: "center",
              padding: "12px 14px",
              borderBottom:
                index === curtainLiningSummaryRows.length - 1
                  ? "none"
                  : "1px solid rgba(148,163,184,0.14)",
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 900 }}>{row.label}</div>
            <div style={{ color: dashboardMuted }}>
              {[liningBrandName, liningFabricCode].filter(Boolean).join(" / ") || "-"}
            </div>
            <div style={{ fontWeight: 800 }}>
              {row.chargeMethod === "sqft" ? "Per sqft" : "Per meter"}
            </div>
            <div style={{ textAlign: "right", fontWeight: 800 }}>
              {row.chargeMethod === "sqft"
                ? `${Number(row.sqft || 0).toFixed(2)} sqft`
                : `${Number(row.meters || 0).toFixed(2)} m`}
            </div>
            <div style={{ textAlign: "right", fontWeight: 900, color: "#2563eb" }}>
              {currencySymbol(currency)} {Number(row.amount || 0).toFixed(2)}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  </div>
) : null}

              <div
                style={{
                  maxWidth: isCompact ? "100%" : 420,
                  width: "100%",
                  marginLeft: isCompact ? 0 : "auto",
                  background: isDark ? "#0f172a" : "#f9fafb",
                  borderRadius: 14,
                  padding: 16,
                  border: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {/* Subtotal */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 14, opacity: 0.8 }}>Subtotal</span>
                  <strong style={{ fontSize: 16 }}>
                    {currencySymbol(currency)}{" "}
                    {curtainDisplaySummaryTotals.subTotal.toFixed(2)}
                  </strong>
                </div>

                {curtainLineFamilyTotals.accessorySubtotal > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 6,
                      padding: "10px 0 2px",
                      borderTop: "1px solid rgba(148,163,184,0.18)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.78,
                      }}
                    >
                      <span>Curtains & Blinds</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainLineFamilyTotals.measurementSubtotal.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.78,
                      }}
                    >
                      <span>Accessories</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainLineFamilyTotals.accessorySubtotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : null}

{/* Discount label */}
<div style={{ fontSize: 14, opacity: 0.7, marginTop: 6 }}>
  {summaryDiscountMode === "%" ? "Quotation Discount (%)" : "Quotation Discount (Rs.)"}
  <div style={{ fontSize: 12, opacity: 0.72, marginTop: 3 }}>
    Applied after GST to adjust the final payable amount.
  </div>
</div>

                {/* Discount input */}
                <div
                  style={{
                    background: isDark ? "#020617" : "#ffffff",
                    borderRadius: 10,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: "1px solid rgba(148,163,184,0.4)",
                  }}
                >
                  <select
                    style={{ ...selectInput, width: 120 }}
                    value={summaryDiscountMode}
                    onChange={(e) => {
                      const nextMode = e.target.value;
                      summaryDiscountModeRef.current = nextMode;
                      markCurtainTotalsSettingsEdited("summaryDiscountMode", nextMode);
                      setSummaryDiscountMode(nextMode);
                    }}
                  >
                    <option value="%">Percent</option>
                    <option value="">Flat</option>
                  </select>

                  <input
                    type="number"
                    style={{ ...input, flex: 1 }}
                    value={summaryDiscountValue}
                    onChange={(e) => {
                      summaryDiscountValueRef.current = e.target.value;
                      markCurtainTotalsSettingsEdited(
                        "summaryDiscountValue",
                        e.target.value
                      );
                      setSummaryDiscountValue(e.target.value);
                    }}
                    placeholder={
                      summaryDiscountMode === "%"
                        ? "Enter % for full quote"
                        : "Enter flat amount"
                    }
                  />
                </div>

                {/* GST selector */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "1fr 160px",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>GST</div>
                    <div style={{ fontSize: 12, opacity: 0.65 }}>
                      Calculated before the quotation discount.
                    </div>
                  </div>
                  <select
                    style={{ ...selectInput, width: "100%" }}
                    value={getCurtainGstSelectValue(curtainGstMode, curtainGstRate)}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      const nextGstMode =
                        nextValue === CURTAIN_GST_MODE_FABRIC_5_OTHER_18
                          ? CURTAIN_GST_MODE_FABRIC_5_OTHER_18
                          : CURTAIN_GST_MODE_FLAT;
                      const nextGstRate =
                        nextGstMode === CURTAIN_GST_MODE_FABRIC_5_OTHER_18
                          ? 0
                          : normalizeCurtainGstRate(nextValue);
                      curtainGstModeRef.current = nextGstMode;
                      curtainGstRateRef.current = nextGstRate;
                      markCurtainTotalsSettingsEdited("curtainGstMode", nextValue);
                      setCurtainGstMode(nextGstMode);
                      setCurtainGstRate(nextGstRate);
                    }}
                  >
                    <option value={0}>No GST / 0%</option>
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                    <option value={CURTAIN_GST_MODE_FABRIC_5_OTHER_18}>
                      Fabric 5% + Other 18%
                    </option>
                  </select>
                </div>

                <div
                  style={{
                    borderTop: "1px solid rgba(148,163,184,0.28)",
                    paddingTop: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.82,
                    }}
                  >
                    <span>Taxable Amount</span>
                    <strong>
                      {currencySymbol(currency)}{" "}
                      {curtainDisplaySummaryTotals.taxableAmount.toFixed(2)}
                    </strong>
                  </div>
                  {isMixedCurtainGstMode(curtainDisplaySummaryTotals.quoteGstMode) ? (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 13,
                          opacity: 0.82,
                        }}
                      >
                        <span>Fabric GST @ 5%</span>
                        <span>
                          {currencySymbol(currency)}{" "}
                          {Number(curtainDisplaySummaryTotals.fabricGstAmount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 13,
                          opacity: 0.82,
                        }}
                      >
                        <span>Other GST @ 18%</span>
                        <span>
                          {currencySymbol(currency)}{" "}
                          {Number(curtainDisplaySummaryTotals.otherGstAmount || 0).toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {curtainDisplaySummaryTotals.measurementSubtotalBeforeDiscount > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13,
                            opacity: 0.82,
                          }}
                        >
                          <span>
                            Curtains/Blinds GST @ {curtainDisplaySummaryTotals.quoteGstRate}%
                          </span>
                          <span>
                            {currencySymbol(currency)}{" "}
                            {curtainDisplaySummaryTotals.measurementGstAmount.toFixed(2)}
                          </span>
                        </div>
                      ) : null}
                      {curtainDisplaySummaryTotals.accessorySubtotalBeforeDiscount > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13,
                            opacity: 0.82,
                          }}
                        >
                          <span>
                            Accessories GST
                            {curtainDisplaySummaryTotals.accessoryGstRates?.length > 1
                              ? " (mixed)"
                              : curtainDisplaySummaryTotals.accessoryGstRates?.length === 1
                              ? ` @ ${curtainDisplaySummaryTotals.accessoryGstRates[0]}%`
                              : ""}
                          </span>
                          <span>
                            {currencySymbol(currency)}{" "}
                            {curtainDisplaySummaryTotals.accessoryGstAmount.toFixed(2)}
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                  {isMixedCurtainGstMode(curtainDisplaySummaryTotals.quoteGstMode) ||
                  curtainDisplaySummaryTotals.hasMixedGstRates ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.9,
                        fontWeight: 800,
                      }}
                    >
                      <span>Total GST</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainDisplaySummaryTotals.totalGstAmount.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.92,
                      fontWeight: 800,
                    }}
                  >
                    <span>Total After GST</span>
                    <span>
                      {currencySymbol(currency)}{" "}
                      {Number(
                        curtainDisplaySummaryTotals.totalAfterGst ??
                          curtainDisplaySummaryTotals.finalTotal ??
                          0
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.82,
                    }}
                  >
                    <span>Post-GST Discount</span>
                    <span>
                      - {currencySymbol(currency)}{" "}
                      {curtainDisplaySummaryTotals.discountAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Final total */}
                <div
                  style={{
                    marginTop: 4,
                    background: isDark ? "#1d4ed8" : "#2563eb",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "white",
                    fontWeight: 700,
                  }}
                >
                  <span>GRAND TOTAL</span>
                  <span>
                    {currencySymbol(currency)}{" "}
                    {hasUnresolvedLiveFabricPricing(rows || [], isFrozenLegacyCurtainQuote) ? "Pricing incomplete" : curtainDisplaySummaryTotals.grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
          {renderGeneralQuoteDebugPanel()}
          {generalEditingQuoteId ? <QuotationAttribution quote={selectedQuote || generalQuote} members={crmMemberRoster.rows} /> : null}
          <QuotationHistoryButton functions={functions} workspaceUid={workspaceUid}
            quoteId={generalEditingQuoteId || ''} quoteType="general" />
          <GeneralQuotationEditor
            quote={generalQuote}
            setQuote={setGeneralQuoteLive}
            totals={generalQuoteTotals}
            isDark={isDark}
            businessName={businessName}
            logoURL={logoURL}
            shopAddress={shopAddress}
            currencySymbol={currencySymbol(generalQuote.currency || currency)}
            isSaving={isSaving}
            autoSaveStatus={autoSaveStatus}
            localDraftStatus={localDraftStatuses[LOCAL_DRAFT_MODULES.GENERAL_QUOTE]}
            editorMode={generalQuoteEditorMode}
            editingQuoteLabel={
              generalQuote.quoteNo || selectedQuote?.quoteNo || generalEditingQuoteId || ""
            }
            onStartNewQuote={(event) => requestStartNewQuote(event, GENERAL_QUOTE_TYPE)}
            onSave={saveQuotation}
            onSaveAsNew={saveQuotationAsNew}
            onExportPdf={exportGeneralQuotationPDF}
            onExportExecutionPdf={exportGeneralExecutionPDF}
            canSave={canEditQuotes || canCreateQuotes}
            canChangeImages={canAccessGeneralQuotes && (generalQuoteEditorMode === QUOTE_EDITOR_MODE_EDITING_EXISTING ? canEditQuotes : canCreateQuotes)}
            canSaveAsNew={canCreateQuotes}
            canStartNewQuote={canCreateQuotes}
            canExportPdf={canUseCustomerPdf}
            canExportExecutionPdf={canUseWorkshopPdf}
            onUploadItemImage={uploadGeneralQuoteItemImage}
            onRemoveItemImage={removeGeneralQuoteItemImage}
            onRetryItemImage={retryGeneralQuoteItemImagePreview}
            onRepairItemImage={
              IS_DEV_UI_ENABLED && !generalRepairingImageKey ? repairGeneralQuoteItemImage : null
            }
            onReplaceItemImage={
              generalRepairingImageKey ? null : replaceGeneralQuoteItemImage
            }
            onCopyImageDebug={IS_DEV_UI_ENABLED ? copyGeneralQuoteImageDebugJson : null}
            imageRepairState={IS_DEV_UI_ENABLED ? generalImageRepairState : {}}
            imageRepairCandidates={IS_DEV_UI_ENABLED ? generalImageRepairCandidates : {}}
            onSelectRepairCandidate={
              IS_DEV_UI_ENABLED && !generalRepairingImageKey
                ? selectGeneralQuoteImageRepairCandidate
                : null
            }
            onRemoveItem={removeGeneralQuoteItem}
            onDraftBlur={() => persistGeneralQuoteDraft("blur")}
            uploadingItemId={generalUploadingItemId}
            executionTemplates={executionTemplateCatalog}
            validationError={
              quoteRowValidationError?.quoteType === GENERAL_QUOTE_TYPE
                ? quoteRowValidationError
                : null
            }
            customerSelector={renderCustomerLedgerCombobox({
              context: GENERAL_QUOTE_TYPE,
              label: "Customer / Company",
              value:
                generalQuote.customerName ||
                generalQuote.quoteFor?.company ||
                generalQuote.quoteFor?.name ||
                "",
              selectedCustomerId:
                generalQuote.customerId ||
                selectedQuote?.customerId ||
                selectedQuote?.crm?.customerId ||
                "",
              seed: {
                customerName:
                  generalQuote.customerName ||
                  generalQuote.quoteFor?.company ||
                  generalQuote.quoteFor?.name ||
                  "",
                phone: generalQuote.quoteFor?.phone || generalQuote.customerPhone || "",
                email: generalQuote.quoteFor?.email || generalQuote.customerEmail || "",
                address: generalQuote.quoteFor?.address || generalQuote.customerAddress || "",
                companyName:
                  generalQuote.quoteFor?.company ||
                  generalQuote.customerCompanyName ||
                  "",
              },
              onTextChange: (nextValue) => {
                setGeneralQuote((prev) => ({
                  ...prev,
                  customerId: "",
                  customerName: nextValue,
                  customerPhone: "",
                  customerWhatsapp: "",
                  customerEmail: "",
                  customerAddress: "",
                  customerCompanyName: nextValue,
                  customerLedgerLinked: false,
                  quoteDisplayTitle: buildQuoteDisplayTitleFromParts({
                    customerName: nextValue,
                    siteName: prev.siteName,
                  }),
                  quoteFor: {
                    ...(prev.quoteFor || {}),
                    name: nextValue,
                    company: nextValue,
                  },
                }));
                setCustomerName(nextValue);
              },
            })}
            siteSelector={renderQuoteSiteProjectPanel({
              context: GENERAL_QUOTE_TYPE,
              selectedCustomerId:
                generalQuote.customerId ||
                selectedQuote?.customerId ||
                selectedQuote?.crm?.customerId ||
                "",
            })}
            referralPartnerSelector={renderReferralPartnerSelector({
              context: GENERAL_QUOTE_TYPE,
            })}
          />
          </>
        )}
      </div>
  </div>


{/* ================= SAVED QUOTES TAB ================= */}
{tab === "saved" && (
  <div>
    <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 20 }}>
      Saved Quotations
    </h2>

    {/* ================= FILTER BAR ================= */}
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 14,
        alignItems: "center",
        marginBottom: 22,
        padding: 16,
        borderRadius: 16,
        background: isDark ? "#111827" : "#f8fafc",
        boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
      }}
    >
      <input
        style={{ ...input, minWidth: isCompact ? "100%" : 220, flex: isCompact ? "1 1 100%" : "0 1 220px" }}
        placeholder="Search all quotations by customer, quote no, phone, site..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Status</option>
        <option value="confirmed">Confirmed</option>
        <option value="draft">Draft</option>
      </select>

      <select
        value={stageFilter}
        onChange={(e) => setStageFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Stages</option>
        {savedStageOptions.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        value={quoteTypeFilter}
        onChange={(e) => setQuoteTypeFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Types</option>
        <option value={CURTAIN_QUOTE_TYPE}>Curtains Only</option>
        <option value={GENERAL_QUOTE_TYPE}>General Quotations Only</option>
      </select>

      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        style={selectInput}
      >
        <option value="repository">Repository order (complete browsing)</option>
        <option value="newest">Recently Updated (loaded)</option>
        <option value="created">Recently Created (loaded)</option>
        <option value="oldest">Oldest First (loaded)</option>
        <option value="highest">Highest Value (loaded)</option>
        <option value="lowest">Lowest Value (loaded)</option>
      </select>

    </div>

    {savedQuotesSearchText ? (
      <div style={{ marginBottom: 14, fontSize: 12, color: dashboardMuted }}>
        Search checks all saved quotations, including older records.
      </div>
    ) : null}

    {loadingSaved && <div>Loading...</div>}
    {savedQuotesAccessError ? (
      <div
        style={{
          marginBottom: 16,
          padding: "16px 18px",
          borderRadius: 16,
          background: isDark ? "rgba(127,29,29,0.22)" : "#fef2f2",
          border: "1px solid rgba(220,38,38,0.28)",
          color: isDark ? "#fecaca" : "#991b1b",
          fontSize: 13,
          lineHeight: 1.65,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 4 }}>
          Saved quotes cannot be loaded
        </div>
        <div>{savedQuotesAccessError.message}</div>
        {isDevRuntime ? (
          <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11 }}>
            {savedQuotesAccessError.code || "error"} | {savedQuotesAccessError.path || ""}
          </div>
        ) : null}
      </div>
    ) : null}
    {savedQuoteOpenError ? (
      <div
        style={{
          marginBottom: 16,
          padding: "16px 18px",
          borderRadius: 16,
          background: isDark ? "rgba(120,53,15,0.22)" : "#fff7ed",
          border: "1px solid rgba(249,115,22,0.28)",
          color: isDark ? "#fed7aa" : "#9a3412",
          fontSize: 13,
          lineHeight: 1.65,
          display: "grid",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 900, marginBottom: 4 }}>
            This quotation could not be opened
          </div>
          <div>
            {savedQuoteOpenError.message ||
              "Please try again. If the issue continues, contact your admin."}
          </div>
          {savedQuoteOpenError.quoteNo ? (
            <div style={{ marginTop: 4, opacity: 0.86 }}>
              Quote #{savedQuoteOpenError.quoteNo}
            </div>
          ) : null}
          {Array.isArray(savedQuoteOpenError.candidates) &&
          savedQuoteOpenError.candidates.length ? (
            <div style={{ marginTop: 6 }}>
              Matching candidates:{" "}
              {savedQuoteOpenError.candidates
                .map((candidate) =>
                  [candidate.quoteNo ? `#${candidate.quoteNo}` : candidate.id, candidate.customerName]
                    .filter(Boolean)
                    .join(" / ")
                )
                .join(" | ")}
            </div>
          ) : null}
          {isDevRuntime ? (
            <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11 }}>
              {savedQuoteOpenError.result || "error"}
              {savedQuoteOpenError.code ? ` | ${savedQuoteOpenError.code}` : ""}
              {Array.isArray(savedQuoteOpenError.attemptedPaths) &&
              savedQuoteOpenError.attemptedPaths.length
                ? ` | ${savedQuoteOpenError.attemptedPaths.join(", ")}`
                : ""}
            </div>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {savedQuoteOpenError.result !== "not-found" ? (
            <button
              type="button"
              onClick={() => loadFullQuote(savedQuoteOpenError.quoteIndex)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              Try Open Again
            </button>
          ) : null}
          {isDevRuntime ? (
            <button
              type="button"
              onClick={() => {
                devLog("SAVED_QUOTE_FAILED_OPEN_DETAILS", savedQuoteOpenError);
                notify("Saved quote open details were logged to the console.", "info");
              }}
              style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#fff" : "#0f172a"), marginRight: 0 }}
            >
              Log Details
            </button>
          ) : null}
          {savedQuoteOpenError.result === "not-found" ? (
            <button
              type="button"
              onClick={() => {
                const errorKeys = new Set(
                  [
                    savedQuoteOpenError.indexId,
                    savedQuoteOpenError.quoteIndex?.indexId,
                    savedQuoteOpenError.quoteIndex?.quoteIndexId,
                    savedQuoteOpenError.quoteIndex?.id,
                    savedQuoteOpenError.quoteId,
                    savedQuoteOpenError.sourceQuoteId,
                  ]
                    .filter(Boolean)
                    .map(String)
                );
                setSavedQuotes((prev) =>
                  safeSavedQuoteArray(prev).filter((quote) =>
                    [
                      quote.id,
                      quote.quoteId,
                      quote.sourceQuoteId,
                      quote.indexId,
                      quote.quoteIndexId,
                    ]
                      .filter(Boolean)
                      .every((key) => !errorKeys.has(String(key)))
                  )
                );
                setSavedQuoteOpenError(null);
              }}
              style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#fff" : "#0f172a"), marginRight: 0 }}
            >
              Remove From View
            </button>
          ) : null}
          {savedQuoteOpenError.result === "item-details-not-found" ||
          resolveQuoteType(savedQuoteOpenError.quoteIndex || savedQuoteOpenError) ===
            GENERAL_QUOTE_TYPE ? (
            <button
              type="button"
              onClick={() => findGeneralQuoteRecoveryOptions(savedQuoteOpenError)}
              disabled={generalQuoteRecoveryState.running}
              style={{ ...btn("#0f766e"), marginRight: 0 }}
            >
              {generalQuoteRecoveryState.running ? "Checking Recovery..." : "Find Recovery Options"}
            </button>
          ) : null}
          {savedQuoteOpenError.result === "item-details-not-found" ? (
            <button
              type="button"
              onClick={moveBrokenSavedQuoteListingToBin}
              disabled={Boolean(savedQuoteActionBusyKey)}
              style={{ ...btn("#b91c1c"), marginRight: 0 }}
            >
              Move Broken Listing to Bin
            </button>
          ) : null}
          </div>
        {generalQuoteRecoveryState.error ? (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 12,
              background: isDark ? "rgba(127,29,29,0.24)" : "#fef2f2",
              color: isDark ? "#fecaca" : "#991b1b",
              fontWeight: 800,
            }}
          >
            {generalQuoteRecoveryState.error}
          </div>
        ) : null}
        {generalQuoteRecoveryState.checkedAt &&
        !generalQuoteRecoveryState.running &&
        generalQuoteRecoveryState.sources.length === 0 ? (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 12,
              background: isDark ? "rgba(15,23,42,0.62)" : "#fff",
              border: "1px solid rgba(148,163,184,0.28)",
              color: isDark ? "#e2e8f0" : "#334155",
            }}
          >
            No item details were found in the quotation record, save attempts, server drafts, or
            drafts available to this browser. This quote cannot be fully recovered unless another
            device has a local draft.
          </div>
        ) : null}
        {generalQuoteRecoveryState.sources.length ? (
          <div
            style={{
              display: "grid",
              gap: 10,
              padding: "12px",
              borderRadius: 14,
              background: isDark ? "rgba(15,23,42,0.5)" : "#ffffff",
              border: "1px solid rgba(148,163,184,0.28)",
            }}
          >
            <div style={{ fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
              Recovery sources
            </div>
            {generalQuoteRecoveryState.sources.map((source, index) => {
              const canRecover = source.canOpenAsDraft && Number(source.itemCount || 0) > 0;
              const sourceKey = `${source.sourceType || "source"}:${source.path || source.id || index}`;
              const isOpeningRecoverySource = openingGeneralQuoteRecoveryKey === sourceKey;
              const isRepairingLegacySource = repairingLegacyGeneralQuoteKey === sourceKey;
              const previewItems = Array.isArray(source.itemPreview)
                ? source.itemPreview.slice(0, 3)
                : [];
              const canRepairFormat =
                canRecover &&
                canRepairLegacyGeneralQuoteFormat &&
                source.sourceType === "full_quote" &&
                String(source.path || "").includes("/quotations/");
              return (
                <div
                  key={sourceKey}
                  style={{
                    display: "grid",
                    gap: 8,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: isDark ? "rgba(2,6,23,0.5)" : "#f8fafc",
                    border: canRecover
                      ? "1px solid rgba(20,184,166,0.35)"
                      : "1px solid rgba(148,163,184,0.25)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                      {source.sourceType === "save_attempt"
                        ? "Save attempt"
                        : source.sourceType === "server_draft"
                        ? "Server draft"
                        : source.sourceType === "local_draft"
                        ? "This device draft"
                        : source.sourceType === "full_quote"
                        ? "Full quotation"
                        : source.sourceType === "quote_index"
                        ? "Quote index"
                        : source.sourceType || "Recovery source"}
                    </strong>
                    <span style={{ fontWeight: 900 }}>
                      {Number(source.itemCount || 0)} item
                      {Number(source.itemCount || 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                    {source.itemSourceField
                      ? `Rows found in ${source.itemSourceField}.`
                      : "No item rows found in this source."}{" "}
                    {source.recommendedAction || ""}
                  </div>
                  {previewItems.length ? (
                    <div
                      style={{
                        display: "grid",
                        gap: 6,
                        padding: "8px 10px",
                        borderRadius: 10,
                        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                        border: "1px solid rgba(148,163,184,0.18)",
                      }}
                    >
                      {previewItems.map((item, previewIndex) => (
                        <div
                          key={item.id || `${sourceKey}-preview-${previewIndex}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact ? "1fr" : "1.6fr repeat(3, auto)",
                            gap: isCompact ? 2 : 10,
                            alignItems: "center",
                            fontSize: 12,
                            color: isDark ? "#e2e8f0" : "#334155",
                          }}
                        >
                          <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                            {item.name || `Item ${previewIndex + 1}`}
                          </strong>
                          <span>Qty {Number(item.qty || 0) || 0}</span>
                          <span>Rate {Number(item.rate || 0).toLocaleString("en-IN")}</span>
                          <span>Amount {Number(item.amount || 0).toLocaleString("en-IN")}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {isDevRuntime ? (
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: isDark ? "#94a3b8" : "#64748b",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {source.path || ""}
                    </div>
                  ) : null}
                  {canRecover ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => openGeneralQuoteRecoverySourceAsDraft(source)}
                        disabled={Boolean(openingGeneralQuoteRecoveryKey || repairingLegacyGeneralQuoteKey)}
                        style={{ ...btn("#0f766e"), marginRight: 0 }}
                      >
                        {isOpeningRecoverySource ? "Opening..." : "Open Recovered Draft"}
                      </button>
                      {canRepairFormat ? (
                        <button
                          type="button"
                          onClick={() => repairLegacyGeneralQuoteFormat(source)}
                          disabled={Boolean(openingGeneralQuoteRecoveryKey || repairingLegacyGeneralQuoteKey)}
                          style={{ ...btn("#2563eb"), marginRight: 0 }}
                        >
                          {isRepairingLegacySource ? "Repairing..." : "Repair Format"}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    ) : null}
    {activeBusinessDashboardDrilldown ? (
      <div
        style={{
          marginBottom: 16,
          padding: "14px 16px",
          borderRadius: 16,
          background: isDark ? "rgba(37,99,235,0.18)" : "#eff6ff",
          border: "1px solid rgba(37,99,235,0.22)",
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isCompact ? "stretch" : "center",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#2563eb" }}>
            Showing: {activeBusinessDashboardDrilldown.label}
          </div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
            {filteredSavedQuotesForDisplay.length} quotation
            {filteredSavedQuotesForDisplay.length === 1 ? "" : "s"}
            {activeBusinessDashboardDrilldown.summaryValue != null
              ? ` | Total ${currencySymbol(currency)} ${Number(
                  activeBusinessDashboardDrilldown.summaryValue || 0
                ).toFixed(2)}`
              : ""}
          </div>
        </div>

        <button
          type="button"
          onClick={clearBusinessDashboardDrilldown}
          style={btn("#2563eb")}
        >
          Clear Filter
        </button>
      </div>
    ) : null}
    <>
      {/* ================= ACTIVE QUOTES ================= */}
      <div
        style={{
          borderRadius: 18,
          overflow: "hidden",
          background: isDark ? "#151520" : "#ffffff",
          boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
        }}
      >
        {filteredSavedQuotesForDisplay.length === 0 && (
          <div style={{ padding: 24, opacity: 0.6 }}>
            {savedQuotesAccessError ? "Fix workspace permissions, then refresh Saved Quotes."
              : savedQuotesPageError || savedSearchResult.error || (loadingSaved || savedSearchResult.loading ? "Loading quotations..."
              : canLoadMoreSavedQuoteIndex ? "No matches in loaded pages. Continue loading to check remaining quotations." : "No matching quotations")}
          </div>
        )}

        {filteredSavedQuotesForDisplay.length > 0 && (
          <div
            style={{
              padding: "12px 20px",
              borderBottom: "1px solid rgba(148,163,184,0.16)",
              fontSize: 12,
              fontWeight: 700,
              color: dashboardMuted,
              background: isDark ? "rgba(15,23,42,0.62)" : "#f8fafc",
            }}
          >
            Showing {visibleSavedQuotesDisplay.length} of{" "}
            {filteredSavedQuotesForDisplay.length} loaded matching quotations
          </div>
        )}

        {visibleSavedQuotesDisplay.map((q) => {
    const computedTotals = getSavedQuoteTotals(q);
    const quoteIndexRepairReasons = getSavedQuoteRepairBadgeReasons(q);
    const quoteIndexHasDevRepairState = quoteIndexRepairReasons.length > 0;
    const showQuoteIndexHealthNote = isDevRuntime && quoteIndexHasDevRepairState;
    const displayTotal = getSavedQuoteDisplayTotal(q, computedTotals);
    const amountLabel = displayTotal.hasAmount
      ? `${currencySymbol(currency)} ${Number(displayTotal.amount || 0).toFixed(2)}`
      : "Open quote to view total";
    const savedQuoteCardTitleMeta = buildSavedQuoteCardTitleMeta(q);
    const savedQuoteCardTitle = savedQuoteCardTitleMeta.renderedTitle;
    if (isDevRuntime) {
      const titleTraceKey = [
        q.quoteIndexId || q.indexId || q.id || q.quoteId || q.quoteNo || "unknown",
        savedQuoteCardTitle,
        savedQuoteCardTitleMeta.customerName,
        savedQuoteCardTitleMeta.siteName,
        savedQuoteCardTitleMeta.titleSource,
      ].join("::");
      if (!savedQuoteCardTitleTraceRef.current.has(titleTraceKey)) {
        savedQuoteCardTitleTraceRef.current.add(titleTraceKey);
        devLog("SAVED_QUOTE_CARD_TITLE_TRACE", {
          quoteNo: savedQuoteCardTitleMeta.quoteNo,
          quoteIndexId: q.quoteIndexId || q.indexId || "",
          customerName: savedQuoteCardTitleMeta.customerName,
          contactPersonName: savedQuoteCardTitleMeta.contactPersonName,
          siteName: savedQuoteCardTitleMeta.siteName,
          projectName: savedQuoteCardTitleMeta.projectName,
          companyProject: savedQuoteCardTitleMeta.companyProject,
          quoteDisplayTitle: savedQuoteCardTitleMeta.quoteDisplayTitle,
          displayTitle: savedQuoteCardTitleMeta.displayTitle,
          customerDisplayName: savedQuoteCardTitleMeta.customerDisplayName,
          renderedTitle: savedQuoteCardTitleMeta.renderedTitle,
          titleSource: savedQuoteCardTitleMeta.titleSource,
        });
      }
    }
    if (isDevRuntime && quoteIndexHasDevRepairState) {
      const traceKey = [
        q.quoteIndexId || q.indexId || q.id || q.quoteId || q.quoteNo || "unknown",
        quoteIndexRepairReasons.join("|"),
      ].join("::");
      if (!savedQuoteRepairBadgeTraceRef.current.has(traceKey)) {
        savedQuoteRepairBadgeTraceRef.current.add(traceKey);
        devLog("SAVED_QUOTE_REPAIR_BADGE_TRACE", {
          quoteIndexId: q.quoteIndexId || q.indexId || "",
          quoteNo: q.quoteNo || q.quoteNoString || "",
          customerName: q.customerName || "",
          quoteType: q.quoteType || q.quotationType || q.quoteTypeResolved || "",
          sourcePath: q.sourcePath || "",
          sourceQuoteId: q.sourceQuoteId || "",
          quoteId: q.quoteId || q.id || "",
          grandTotal: q.grandTotal ?? null,
          total: q.total ?? null,
          finalTotal: q.finalTotal ?? null,
          totalAmount: q.totalAmount ?? null,
          displayTotalSource: displayTotal.source || "",
          reasonNeedsRepair: quoteIndexRepairReasons,
        });
      }
    }
    const quoteType = resolveQuoteType(q);
    const typeLabel =
      quoteType === GENERAL_QUOTE_TYPE ? "General Quotation" : "Curtains";
    const stageLabel = getStageLabelForQuote(q);
    const stageColor = getStageColorForQuote(q);
    const checklistProgress = getChecklistProgress(
      buildQuoteCrmData(q).checklistItems
    );
    const stageOptions = getStageSelectOptions(
      crmPipelines,
      quoteType,
      q.workStage ?? q.crm?.stageId
    );
    const confirmBusyKey = getSavedQuoteWorkflowBusyKey(q, "confirmQuote", workspaceUid);
    const unconfirmBusyKey = getSavedQuoteWorkflowBusyKey(q, "unconfirmQuote", workspaceUid);
    const deleteBusyKey = getSavedQuoteWorkflowBusyKey(q, "moveToBin", workspaceUid);
    const stageBusyKey = getSavedQuoteWorkflowBusyKey(q, "updateStage", workspaceUid);
    const installationDateBusyKey = getSavedQuoteWorkflowBusyKey(
      q,
      "updateInstallationDate",
      workspaceUid
    );
    const isConfirmBusy = savedQuoteActionBusyKey === confirmBusyKey;
    const isUnconfirmBusy = savedQuoteActionBusyKey === unconfirmBusyKey;
    const isDeleteBusy = savedQuoteActionBusyKey === deleteBusyKey;
    const isStageBusy = savedQuoteActionBusyKey === stageBusyKey;
    const isInstallationDateBusy =
      savedQuoteActionBusyKey === installationDateBusyKey;

          return (
            <div
              key={q.id}
              style={{
                padding: 24,
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isCompact ? "stretch" : "flex-start",
                transition: "all 0.2s ease",
                gap: 16,
              }}
            >
        {/* LEFT SIDE */}
        <div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>
            {savedQuoteCardTitle}
          </div>
          <QuotationAttribution quote={q} members={crmMemberRoster.rows} compact />

          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4, display: "grid", gap: 4 }}>
            {q.siteName || q.siteAddress ? (
              <span>
                Site: {[q.siteName, q.siteAddress].filter(Boolean).join(" - ")}
              </span>
            ) : null}
            <span>
              Created: {getCreatedMillis(q) ? new Date(getCreatedMillis(q)).toLocaleDateString() : "-"}
            </span>
            <span>
              Updated: {getUpdatedMillis(q) ? new Date(getUpdatedMillis(q)).toLocaleDateString() : "-"}
            </span>
            <span>Stage: {stageLabel}</span>
            <span>
              Checklist: {checklistProgress.completed}/{checklistProgress.total || 0}
            </span>
          </div>

          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              marginTop: 8,
              color: displayTotal.hasAmount ? "#2563eb" : dashboardMuted,
            }}
          >
            {amountLabel}
          </div>

          {/* STATUS BADGE */}
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {showQuoteIndexHealthNote ? (
              <span
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 800,
                  color: isDark ? "#fef3c7" : "#92400e",
                  background: isDark ? "rgba(180,83,9,0.28)" : "#fef3c7",
                  border: "1px solid rgba(180,83,9,0.28)",
                }}
                title={quoteIndexRepairReasons.join(", ")}
              >
                Index health note
              </span>
            ) : null}
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background:
                  q.status === "confirmed"
                    ? "#16a34a"
                    : "#f59e0b",
              }}
            >
              {q.status === "confirmed" ? "Final" : "Draft"}
            </span>
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                color: quoteType === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                background:
                  quoteType === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
              }}
            >
              {typeLabel}
            </span>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: isCompact ? "stretch" : "flex-end",
            minWidth: isCompact ? "100%" : 260,
          }}
        >
          {/* CONFIRM / UNCONFIRM */}
          {canAccessCrm && (canEditQuotes || (canCreateQuotes &&
            (q.createdByUid || q.createdBy) === user?.uid)) ? (
            <Button
              variant="secondary"
              disabled={Boolean(customerHistoryRetryBusyId)}
              onClick={() => retrySavedQuoteCustomerHistory(q)}
              title="Retry customer history only. Does not save or reprice this quotation."
            >
              {customerHistoryRetryBusyId === getSavedQuoteWorkflowIdentity(q, workspaceUid).fullQuoteId
                ? "Updating Customer History..." : "Update Customer History"}
            </Button>
          ) : null}
          {canEditQuotes && q.status !== "confirmed" ? (
                        <Button
              icon={Check}
              variant="success"
              disabled={isConfirmBusy}
              onClick={async () => {
                setSavedQuoteActionBusyKey(confirmBusyKey);
                try {
                  const result = await performSavedQuoteAction({
                    quote: q,
                    action: "confirmQuote",
                    updates: { totals: computedTotals },
                  });
                  notify(result.successMessage || "Quote confirmed", "success");
                } catch (err) {
                  if (isDevRuntime) {
                    const identity = getSavedQuoteWorkflowIdentity(q, workspaceUid);
                    devError("SAVED_QUOTE_CONFIRM_FAILED_EXACT", {
                      code: err?.code,
                      message: err?.message,
                      stack: err?.stack,
                      quoteIndexPath: identity.quoteIndexId
                        ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
                        : "",
                      fullQuotePath: identity.sourcePath,
                    });
                  }
                  notify(
                    getSavedQuoteWorkflowFriendlyError(
                      err,
                      "Could not confirm quotation. Please try again."
                    ),
                    "error"
                  );
                } finally {
                  setSavedQuoteActionBusyKey("");
                }
              }}
            >
              {isConfirmBusy ? "Confirming..." : "Confirm"}
            </Button>
          ) : canEditQuotes ? (
                        <Button
              icon={RotateCcw}
              variant="warning"
              disabled={isUnconfirmBusy}
              onClick={async () => {
                setSavedQuoteActionBusyKey(unconfirmBusyKey);
                try {
                  const result = await performSavedQuoteAction({
                    quote: q,
                    action: "unconfirmQuote",
                  });
                  notify(result.successMessage || "Quote reverted to draft", "success");
                } catch (err) {
                  if (isDevRuntime) {
                    const identity = getSavedQuoteWorkflowIdentity(q, workspaceUid);
                    devError("SAVED_QUOTE_UNCONFIRM_FAILED_EXACT", {
                      code: err?.code,
                      message: err?.message,
                      stack: err?.stack,
                      quoteIndexPath: identity.quoteIndexId
                        ? `users/${workspaceUid}/quote_index/${identity.quoteIndexId}`
                        : "",
                      fullQuotePath: identity.sourcePath,
                    });
                  }
                  notify(
                    getSavedQuoteWorkflowFriendlyError(
                      err,
                      "Could not revert quotation. Please try again."
                    ),
                    "error"
                  );
                } finally {
                  setSavedQuoteActionBusyKey("");
                }
              }}
            >
              {isUnconfirmBusy ? "Reverting..." : "Unconfirm"}
            </Button>
          ) : null}

          {/* LOAD + DELETE */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: isCompact ? "100%" : "auto" }}>
            {canViewQuotes ? (
              <button
                type="button"
                style={btn("#1d4ed8")}
                onClick={() => {
                  loadFullQuote(q);
                }}
              >
                Load
              </button>
            ) : null}

            {canEditQuotes ? (
              <button
                type="button"
                disabled={isDeleteBusy}
                style={{
                  ...btn("#ef4444"),
                  opacity: isDeleteBusy ? 0.65 : 1,
                  cursor: isDeleteBusy ? "not-allowed" : "pointer",
                }}
                onClick={() => deleteQuotation(q)}
              >
                {isDeleteBusy ? "Moving..." : "Delete"}
              </button>
            ) : null}

            {canEditQuotes && crmSettings.enableOrderHandover !== false ? (
              q.projectId ? (
                <button
                  type="button"
                  style={btn("#0f766e")}
                  onClick={() => {
                    loadCrmProjectById(q.projectId, {
                      navigate: true,
                      notifyMissing: true,
                    });
                  }}
                  title="Order handover already created"
                >
                  Order Created
                </button>
              ) : (
                <button
                  type="button"
                  style={btn("#7c3aed")}
                  onClick={() =>
                    openOrderHandoverModal({
                      source: "saved_quote",
                      quoteIndex: q,
                    })
                  }
                >
                  Create Order
                </button>
              )
            ) : null}

          </div>

          {/* WORK STAGE */}
          {canEditQuotes ? (
            <select
              value={q.workStage ?? "quote_given"}
              disabled={isStageBusy}
              onChange={(e) =>
                updateWorkStage(q, e.target.value)
              }
              style={{
                ...selectInput,
                padding: "6px 14px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.6)",
                fontSize: 12,
                fontWeight: 700,
                background: stageColor,
                color: "#fff",
                WebkitTextFillColor: "#fff",
                cursor: isStageBusy ? "not-allowed" : "pointer",
                opacity: isStageBusy ? 0.72 : 1,
                width: isCompact ? "100%" : "auto",
              }}
            >
              {stageOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <div
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                background: stageColor,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                alignSelf: isCompact ? "stretch" : "auto",
              }}
            >
              {stageLabel}
            </div>
          )}

          {/* INSTALLATION DATE (Editable Anytime) */}
          {canEditQuotes ? (
            <input
              type="date"
              value={toDateInputValue(q.installationDate)}
              disabled={isInstallationDateBusy}
              onChange={async (e) => {
                try {
                  await updateInstallationDate(q, e.target.value);
                } catch (error) {
                  devError("Failed to update installation date:", error);
                }
              }}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid rgba(148,163,184,0.4)",
                fontSize: 12,
                width: isCompact ? "100%" : "auto",
                cursor: isInstallationDateBusy ? "not-allowed" : "pointer",
                opacity: isInstallationDateBusy ? 0.72 : 1,
              }}
            />
          ) : (
            <div
              style={{
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 700,
              }}
            >
              Installation: {toDateInputValue(q.installationDate) || "-"}
            </div>
          )}
        </div>
            </div>
          );
        })}

        {hasMoreSavedQuotesDisplay || canLoadMoreSavedQuoteIndex ? (
          <div
            style={{
              padding: 18,
              display: "flex",
              justifyContent: "center",
              background: isDark ? "#151520" : "#ffffff",
            }}
          >
            <button
              type="button"
              style={btn("#0f172a")}
              disabled={loadingSaved || loadingMoreSavedQuotes || savedSearchResult.loading}
              onClick={() => {
                if (hasMoreSavedQuotesDisplay) {
                  setSavedQuotesVisibleCount((prev) => prev + SAVED_QUOTES_PAGE_SIZE);
                  return;
                }
                if (savedQuotesSearchText) searchSavedQuotesRemote(workspaceUid, search, true);
                else loadMoreSavedQuoteIndex();
              }}
            >
              {loadingMoreSavedQuotes || savedSearchResult.loading ? "Loading..." : savedQuotesPageError || savedSearchResult.error ? "Retry Load More" : "Load More"}
            </button>
          </div>
        ) : null}
        {!activeBusinessDashboardDrilldown ? (
          <div role="status" style={{ padding: 12, color: dashboardMuted, fontSize: 12 }}>
            {savedQuotesSearchText
              ? savedSearchResult.error || (savedSearchResult.loading ? "Searching..." : savedSearchResult.hasMore ? "More matching pages are available." : "All matching quotations loaded.")
              : savedQuotesPageError || (loadingSaved ? "Loading..." : hasMoreSavedQuoteIndex ? "More results are available." : "All matching quotations loaded.")}
            {" "}Compatibility browsing includes quotations without historical dates. Sorting applies to loaded records until reviewed date normalization is complete.
            <button type="button" style={{ ...btn("#64748b"), marginLeft: 8 }}
              onClick={() => {
                invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
              }}>Refresh Latest</button>
          </div>
        ) : null}
        </div>

          {/* ================= RECYCLE BIN ================= */}
          {!activeBusinessDashboardDrilldown ? (
            <div
              style={{
                marginTop: 24,
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 12,
                padding: 14,
                borderRadius: 14,
                background: isDark ? "#111827" : "#f8fafc",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.18)"
                  : "1px solid #e2e8f0",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  Recycle Bin
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Deleted quotations are loaded only when you open this section.
                </div>
              </div>
              <button
                type="button"
                style={btn(recycleBinOpen ? "#64748b" : "#b91c1c")}
                disabled={loadingRecycleBinQuotes}
                onClick={() => {
                  if (recycleBinOpen) {
                    setRecycleBinOpen(false);
                    return;
                  }
                  openRecycleBin();
                }}
              >
                {loadingRecycleBinQuotes
                  ? "Loading Recycle Bin..."
                  : recycleBinOpen
                  ? "Hide Recycle Bin"
                  : "Open Recycle Bin"}
              </button>
            </div>
          ) : null}

          {recycleBinOpen && !loadingRecycleBinQuotes && recycleBinQuotesForDisplay.length === 0 ? (
            <div
              style={{
                marginTop: 12,
                padding: 14,
                borderRadius: 14,
                background: isDark ? "#0f172a" : "#fff",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.14)"
                  : "1px solid #e2e8f0",
                color: dashboardMuted,
                fontSize: 13,
              }}
            >
              No deleted quotations found in the first recycle-bin page.
            </div>
          ) : null}

          {recycleBinQuotesForDisplay.length > 0 ? (
            <>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  marginTop: 36,
                  marginBottom: 12,
                  color: "#b91c1c",
                }}
              >
                 Recycle Bin
              </h3>

              {recycleBinQuotesForDisplay.map((q) => {
                const restoreBusyKey = getSavedQuoteWorkflowBusyKey(
                  q,
                  "restoreFromBin",
                  workspaceUid
                );
                const isRestoreBusy = savedQuoteActionBusyKey === restoreBusyKey;

                return (
            <div
              key={q.id}
              style={{
                padding: 16,
                borderRadius: 14,
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                marginBottom: 10,
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isCompact ? "stretch" : "center",
                gap: 12,
              }}
            >
              <div>
                {buildSavedQuoteCardTitle(q)}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {canEditQuotes ? (
                  <>
                    <button
                      disabled={isRestoreBusy}
                      style={{
                        ...btn("#16a34a"),
                        opacity: isRestoreBusy ? 0.65 : 1,
                        cursor: isRestoreBusy ? "not-allowed" : "pointer",
                      }}
                      onClick={() => restoreQuotation(q)}
                    >
                      {isRestoreBusy ? "Restoring..." : "Restore"}
                    </button>

                    <button
                      style={btn("#dc2626")}
                      onClick={() => {
                        openConfirmDialog({
                          title: "Delete quotation permanently?",
                          description:
                            "This action cannot be undone. The quotation will be removed from Recycle Bin permanently.",
                          confirmLabel: "Delete Forever",
                          cancelLabel: "Cancel",
                          tone: "danger",
                          onConfirm: async () => {
                            await deleteDoc(
                              doc(
                                db,
                                "users",
                                workspaceUid,
                                "quotations",
                                q.id
                              )
                            );
                            invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
                            setSavedQuotes((prev) =>
                              safeSavedQuoteArray(prev).filter((item) => item.id !== q.id)
                            );
                            notify("Quotation deleted permanently.", "success");
                          },
                        });
                      }}
                    >
                      Delete Forever
                    </button>
                  </>
                ) : null}
              </div>
            </div>
                );
              })}
            </>
          ) : null}
    </>
  </div>
)}

       {/* ================= SETTINGS TAB ================= */}
{tab === "settings" && (
  <div
    style={{
      background: isDark ? "#151520" : "#ffffff",
      padding: 32,
      borderRadius: 20,
      maxWidth: 900,
      margin: "0 auto",
      boxShadow: isDark
        ? "0px 0px 25px rgba(0,0,0,0.45)"
        : "0px 6px 20px rgba(0,0,0,0.08)",
    }}
  >
    <h2
      style={{
        fontSize: 24,
        fontWeight: 800,
        marginBottom: 24,
        letterSpacing: "-0.3px",
      }}
    >
      Settings
    </h2>

    {/* ================= SECTION WRAPPER ================= */}
    <div style={{ display: "grid", gap: 28 }}>

      {/* ------------------ Currency & Units ------------------ */}
      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>
          General Settings
        </h3>

        {/* CURRENCY */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Currency</label>
        <select
          style={{ ...selectInput, marginBottom: 16 }}
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          {CURRENCY_LIST.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        {/* MEASUREMENT UNIT */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Measurement Unit</label>
        <select
          style={{ ...selectInput, marginBottom: 8 }}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        >
          <option value="in">Inches</option>
          <option value="cm">Centimeters</option>
        </select>
      </div>

     

     {/* ------------------ Blind Calculation Settings ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#111827" : "#f8fafc",
    border: "1px solid rgba(0,0,0,0.08)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>
    Blind Calculation Settings
  </h3>
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 700,
      color: isDark ? "#e5e7eb" : "#111827",
    }}
  >
    <input
      type="checkbox"
      checked={
        resolveBlindSqftRoundingMode(fabricCalcSettings, "exact") === "ceil"
      }
      onChange={(e) =>
        setFabricCalcSettings((prev) =>
          normalizeFabricCalcSettingsForState({
            ...prev,
            blindSqftRoundingMode: e.target.checked ? "ceil" : "exact",
          })
        )
      }
      style={{ marginTop: 2 }}
    />
    <span>
      Round blind sqft up to next full sqft
      <span
        style={{
          display: "block",
          marginTop: 4,
          fontSize: 12,
          lineHeight: 1.5,
          fontWeight: 500,
          color: isDark ? "#94a3b8" : "#64748b",
        }}
      >
        When enabled, 14.09 sqft will be billed as 15 sqft. When disabled,
        exact sqft will be used.
      </span>
    </span>
  </label>
</div>

     {/* ------------------ Minimum Sqft ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#1b1b27" : "#f8fafc",
    border: "1px solid rgba(0,0,0,0.08)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
    Minimum SQFT for Each Blind Type
  </h3>

  {Object.keys(minSqft).map((k) => (
    <div
      key={k}
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 12,
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 14 }}>{k}</span>

      <input
        type="number"
        style={{ ...input, width: 120 }}
        value={minSqft[k]}
        onChange={(e) =>
          setMinSqft({ ...minSqft, [k]: e.target.value })
        }
      />
    </div>
  ))}
</div>

{/* ------------------ Product Calculation Handoff ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#101827" : "#eef6ff",
    border: isDark
      ? "1px solid rgba(96,165,250,0.24)"
      : "1px solid rgba(37,99,235,0.18)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
    Fabric calculation rules moved
  </h3>
  <p
    style={{
      margin: "0 0 14px",
      color: isDark ? "#bfdbfe" : "#1e3a8a",
      fontSize: 13,
      lineHeight: 1.55,
    }}
  >
    Curtain and blind hems, side allowances, usable fabric width, fullness, and
    meter formulas are now managed per product in Products & Calculations.
    Legacy values remain available only as fallback data for old quotations.
  </p>
  <button
    type="button"
    onClick={() => setRoutedTab("products")}
    disabled={!canManageProductConfigs}
    style={{
      padding: "10px 14px",
      borderRadius: 12,
      border: 0,
      background: canManageProductConfigs ? "#2563eb" : "#94a3b8",
      color: "#fff",
      fontWeight: 800,
      cursor: canManageProductConfigs ? "pointer" : "not-allowed",
    }}
  >
    Manage Product Calculations
  </button>
  {!canManageProductConfigs ? (
    <div style={{ marginTop: 10, fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
      You need product/settings access to edit calculation rules.
    </div>
  ) : null}
</div>

{/* ------------------ Fabric Brands ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#111827" : "#f8fafc",
    border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
    display: "grid",
    gap: 16,
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
      flexWrap: "wrap",
    }}
  >
    <div>
      <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
        Fabric Brands
      </h3>
      <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
        Manage reusable fabric brand names for the Curtain Quotation fabric summary.
      </div>
    </div>
    <Button
      icon={RotateCcw}
      variant="secondary"
      onClick={() => loadFabricBrands({ force: true })}
      disabled={fabricBrandsLoading}
    >
      {fabricBrandsLoading ? "Loading..." : "Refresh"}
    </Button>
  </div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
      gap: 12,
    }}
  >
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
        Brand Name
      </span>
      <input
        style={input}
        value={fabricBrandDraft.name}
        placeholder="D'Decor, GM Fabrics, Vista"
        onChange={(event) =>
          setFabricBrandDraft((prev) => ({ ...prev, name: event.target.value }))
        }
      />
    </label>
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
        Supplier / Notes
      </span>
      <input
        style={input}
        value={fabricBrandDraft.supplier}
        placeholder="Optional supplier"
        onChange={(event) =>
          setFabricBrandDraft((prev) => ({
            ...prev,
            supplier: event.target.value,
          }))
        }
      />
    </label>
  </div>

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
      Notes
    </span>
    <input
      style={input}
      value={fabricBrandDraft.notes}
      placeholder="Optional brand note"
      onChange={(event) =>
        setFabricBrandDraft((prev) => ({ ...prev, notes: event.target.value }))
      }
    />
  </label>

  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
    <Button icon={Plus} variant="success" onClick={saveFabricBrandDraft}>
      Add Fabric Brand
    </Button>
    <span style={{ alignSelf: "center", fontSize: 12, color: dashboardMuted }}>
      Brands are loaded once per workspace and reused in quotation summaries.
    </span>
  </div>

  <div style={{ display: "grid", gap: 8 }}>
    {fabricBrands.length ? (
      fabricBrands.map((brand) => {
        const isExpanded = expandedFabricBrandId === brand.id;
        return (
          <div
            key={brand.id}
            style={{
              display: "grid",
              gap: 10,
              padding: "12px 12px",
              borderRadius: 14,
              background: isDark ? "#0f172a" : "#ffffff",
              border: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 900 }}>{brand.name}</div>
                {brand.supplier || brand.notes ? (
                  <div style={{ marginTop: 3, fontSize: 11, color: dashboardMuted }}>
                    {[brand.supplier, brand.notes].filter(Boolean).join(" | ")}
                  </div>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (isExpanded) {
                      setExpandedFabricBrandId("");
                      return;
                    }
                    setExpandedFabricBrandId(brand.id);
                    await loadFabricCatalogueLibrary(brand.id);
                  }}
                  style={{
                    border: 0,
                    background: isDark ? "#1e293b" : "#dbeafe",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                    padding: "7px 10px",
                    borderRadius: 10,
                  }}
                >
                  {isExpanded ? "Hide catalogues" : "Catalogues & SR RRP"}
                </button>
                <button
                  type="button"
                  onClick={() => archiveFabricBrand(brand.id)}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "#dc2626",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                    padding: "7px 0",
                  }}
                >
                  Archive
                </button>
              </div>
            </div>
            {isExpanded ? renderFabricBrandCatalogueManager(brand) : null}
          </div>
        );
      })
    ) : (
      <div style={{ fontSize: 12, color: dashboardMuted }}>
        No fabric brands added yet.
      </div>
    )}
  </div>
</div>
          

      

      {/* ------------------ Shop Address + Logo ------------------ */}
      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
          Business Details
        </h3>

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Name</label>
        <input
          style={{ ...input, marginBottom: 12 }}
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Enter your business name"
        />

        {/* SHOP ADDRESS */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Shop Address</label>
        <textarea
          style={{ ...input, marginBottom: 16, height: 80 }}
          value={shopAddress}
          onChange={(e) => setShopAddress(e.target.value)}
        />

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Phone</label>
        <input
          style={{ ...input, marginBottom: 12 }}
          value={businessPhone}
          onChange={(e) => setBusinessPhone(e.target.value)}
          placeholder="Enter business phone"
        />

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Email</label>
        <input
          style={{ ...input, marginBottom: 16 }}
          value={businessEmail}
          onChange={(e) => setBusinessEmail(e.target.value)}
          placeholder="Enter business email"
        />

        {/* LOGO UPLOAD */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Brand Logo</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ marginBottom: 16 }}
          onChange={(e) => setSelectedLogoFile(e.target.files[0])}
        />

        {logoURL && (
          <img
            src={logoURL}
            alt="logo"
            style={{
              height: 70,
              marginBottom: 16,
              borderRadius: 10,
              objectFit: "contain",
              }}
            />
        )}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#111827" : "#f8fafc",
          border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
          display: "grid",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
              Storage Manager
            </h3>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
              Review optimized uploads from this workspace. Files are archived first;
              permanent deletion is admin-only and requires confirmation.
            </div>
          </div>
          <Button
            icon={FileImage}
            variant="secondary"
            onClick={loadStorageManagerFiles}
            disabled={storageManagerState.loading}
          >
            {storageManagerState.loading ? "Loading..." : "Refresh Storage"}
          </Button>
        </div>

        {storageManagerState.usage ? (() => {
          const usedBytes = Number(storageManagerState.usage.usedBytes || 0);
          const includedBytes = Number(
            storageManagerState.usage.includedBytes || 1024 * 1024 * 1024
          );
          const percent = includedBytes
            ? Math.min(100, Math.round((usedBytes / includedBytes) * 100))
            : 0;
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 10,
              }}
            >
              {[
                ["Used", formatStorageBytes(usedBytes)],
                ["Included", formatStorageBytes(includedBytes)],
                ["Remaining", formatStorageBytes(Math.max(0, includedBytes - usedBytes))],
                ["Images", Number(storageManagerState.usage.imageCount || 0).toLocaleString("en-IN")],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: isDark ? "#0f172a" : "#ffffff",
                    border: isDark ? "1px solid rgba(148,163,184,0.15)" : "1px solid #e2e8f0",
                  }}
                >
                  <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                    {label}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 17, fontWeight: 900 }}>
                    {value}
                  </div>
                </div>
              ))}
              <div
                style={{
                  gridColumn: "1 / -1",
                  height: 8,
                  borderRadius: 999,
                  background: isDark ? "#1f2937" : "#e2e8f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    background: percent > 85 ? "#dc2626" : percent > 65 ? "#f59e0b" : "#16a34a",
                  }}
                />
              </div>
            </div>
          );
        })() : (
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Click Refresh Storage to load the manifest. This does not scan Firebase Storage.
          </div>
        )}

        {storageManagerState.error ? (
          <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
            {storageManagerState.error}
          </div>
        ) : null}

        {storageManagerState.files.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {storageManagerState.files.map((file) => {
              const previewUrl =
                file.thumbnailDownloadURL || file.downloadURL || file.url || "";
              const linkedRecord =
                (Array.isArray(file.linkedRecords) && file.linkedRecords[0]) || {};
              return (
                <div
                  key={file.fileId || file.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "56px minmax(0, 1fr)",
                    gap: 12,
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "#0f172a" : "#ffffff",
                    border: isDark ? "1px solid rgba(148,163,184,0.15)" : "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      overflow: "hidden",
                      background: isDark ? "#1f2937" : "#e2e8f0",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={file.filename || "Stored file"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <FileImage size={20} color={isDark ? "#94a3b8" : "#64748b"} />
                    )}
                  </div>
                  <div style={{ minWidth: 0, display: "grid", gap: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={file.filename || file.fullPath}
                        >
                          {file.originalFilename || file.filename || "Stored image"}
                        </div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                          {file.ownerType || "image"} · {formatStorageBytes(
                            Number(file.sizeBytes || 0) + Number(file.thumbnailSizeBytes || 0)
                          )} · {file.compressionStatus || "legacy"}
                        </div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                          Linked: {linkedRecord.type || file.ownerType || "-"}{" "}
                          {linkedRecord.id || file.ownerId || file.quoteId || file.leadId || ""}
                        </div>
                      </div>
                      <span
                        style={{
                          alignSelf: "flex-start",
                          padding: "5px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          background: file.deleted
                            ? "#fee2e2"
                            : file.archived
                            ? "#fef3c7"
                            : "#dcfce7",
                          color: file.deleted
                            ? "#991b1b"
                            : file.archived
                            ? "#92400e"
                            : "#166534",
                        }}
                      >
                        {file.deleted ? "Deleted" : file.archived ? "Archived" : "Active"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {file.downloadURL || file.url ? (
                        <a
                          href={file.downloadURL || file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}
                        >
                          View
                        </a>
                      ) : null}
                      {!file.archived && !file.deleted ? (
                        <button
                          type="button"
                          onClick={() => archiveStorageManagerFile(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: "#b45309",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Archive
                        </button>
                      ) : null}
                      {canManageStorageFiles && !file.deleted ? (
                        <button
                          type="button"
                          onClick={() => permanentlyDeleteStorageManagerFile(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: "#dc2626",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Delete permanently
                        </button>
                      ) : null}
                      {IS_DEV_UI_ENABLED ? (
                        <button
                          type="button"
                          onClick={() => copyStorageManagerFileDebug(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: isDark ? "#93c5fd" : "#1d4ed8",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Copy JSON
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : storageManagerState.loadedAt && !storageManagerState.loading ? (
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            No storage manifest files found yet. New uploads will appear here.
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            Team members moved
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>
            Team invites, permissions, and assignment access now live only in the dedicated Team &amp; Access tab so there is a single place to manage staff.
          </div>
        </div>
        {canAccessTeamAccess ? (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              icon={Users}
              variant="secondary"
              onClick={() => setRoutedTab("teamAccess")}
            >
              Open Team &amp; Access
            </Button>
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#101827" : "#eff6ff",
          border: isDark
            ? "1px solid rgba(96,165,250,0.24)"
            : "1px solid rgba(37,99,235,0.18)",
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            Account Security
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>
            Change your password, review signed-in devices, and secure your account from one place.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            icon={UserCog}
            variant="secondary"
            onClick={() => setRoutedTab("accountSecurity")}
          >
            Open Account Security
          </Button>
        </div>
      </div>
    </div>

    {/* ------------------ SINGLE UNIVERSAL SAVE BUTTON ------------------ */}
        <Button
      icon={Save}
      variant="primary"
      onClick={saveSettings}
      disabled={isSavingSettings}
      style={{ marginLeft: "auto", marginTop: 30 }}
    >
      {isSavingSettings ? "Saving..." : "Save All Settings"}
    </Button>
  </div>
)}


        {/* ================= STITCHING TAB ================= */}
        {/* ================= STITCHING TAB ================= */}
{tab === "stitching" && (
  <div
    style={{
      display: "grid",
      gap: 18,
      maxWidth: 1120,
      margin: "0 auto",
    }}
  >
    <div
      style={{
        background: isDark
          ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,64,175,0.42))"
          : "linear-gradient(135deg, #eff6ff, #ffffff)",
        padding: 24,
        borderRadius: 22,
        border: isDark ? "1px solid rgba(96,165,250,0.25)" : "1px solid #dbeafe",
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.34)"
          : "0 18px 45px rgba(15,23,42,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>
            Stitching & Track Pricing
          </h2>
          <div style={{ marginTop: 8, fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
            Set per-running-foot rates, fullness, and default track length for each
            stitching/track style.
          </div>
        </div>
        {isStitchingSettingsDirty ? (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: isDark ? "rgba(245,158,11,0.18)" : "#fffbeb",
              border: isDark ? "1px solid rgba(251,191,36,0.3)" : "1px solid #fde68a",
              color: isDark ? "#fde68a" : "#92400e",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Unsaved changes
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: 12,
          marginTop: 18,
        }}
      >
        {[
          ["Rate / Running ft", STITCHING_SETTING_HELP.rate],
          ["Fullness Multiplier", STITCHING_SETTING_HELP.fullness],
          ["Default Track Length", STITCHING_SETTING_HELP.trackLength],
        ].map(([label, copy]) => (
          <div
            key={label}
            style={{
              padding: 14,
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.62)" : "rgba(255,255,255,0.82)",
              border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 900 }}>{label}</div>
            <div style={{ marginTop: 5, color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              {copy}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div
      style={{
        background: isDark ? "#151520" : "#ffffff",
        padding: 22,
        borderRadius: 20,
        border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
        boxShadow: isDark
          ? "0px 18px 40px rgba(0,0,0,0.35)"
          : "0px 14px 32px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : "minmax(180px, 1.2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(170px, 1fr) 96px",
          gap: 12,
          alignItems: "center",
          color: dashboardMuted,
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          padding: "0 4px 10px",
        }}
      >
        <span>Style Name</span>
        <span>Rate / Running ft (₹)</span>
        <span>Fullness Multiplier</span>
        <span>Default Track Length (ft)</span>
        <span>Actions</span>
    </div>

    {Object.entries(stitchingRates || {}).map(([name, cfg]) => {
      const settingCfg = normalizeStitchingSettingForState(cfg);
      const bendSettings = settingCfg.bendSettings || DEFAULT_BEND_SETTINGS;
      const rateValue = parseStitchingSettingNumber(settingCfg?.rate, 0);
      const trackLengthValue = parseStitchingSettingNumber(settingCfg?.trackLengthFt, 0);
      const exampleCharge = Number.isFinite(rateValue * trackLengthValue)
        ? rateValue * trackLengthValue
        : 0;

      return (
        <div
          key={name}
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "minmax(180px, 1.2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(170px, 1fr) 96px",
            gap: 12,
            alignItems: "start",
            padding: 14,
            marginBottom: 10,
            borderRadius: 16,
            background: isDark ? "rgba(15,23,42,0.72)" : "#f8fafc",
            border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
              {name}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
              Example: {trackLengthValue || 0} ft x{" "}
              {formatCurrencyDisplay(rateValue || 0, currency)} ={" "}
              {formatCurrencyDisplay(exampleCharge, currency)} track/stitch charge
            </div>
          </div>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Rate / Running ft (₹)</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="200"
              type="number"
              min="0"
              step="0.01"
              value={getInputValue(settingCfg.rate)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    rate: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.rate}
            </span>
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Fullness Multiplier</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="3"
              type="number"
              min="0.01"
              step="0.01"
              value={getInputValue(settingCfg.fullness)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    fullness: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.fullness}
            </span>
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Default Track Length (ft)</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="16"
              type="number"
              min="0"
              step="0.01"
              value={getInputValue(settingCfg.trackLengthFt)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    trackLengthFt: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.trackLength}
            </span>
          </label>

          <details
            style={{
              gridColumn: isCompact ? "auto" : "1 / -1",
              borderRadius: 14,
              border: isDark ? "1px solid rgba(20,184,166,0.18)" : "1px solid #ccfbf1",
              background: isDark ? "rgba(15,118,110,0.08)" : "#f0fdfa",
              padding: 12,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 900,
                color: isDark ? "#99f6e4" : "#0f766e",
              }}
            >
              Bended Track Settings
            </summary>
            <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                  <input
                    type="checkbox"
                    checked={settingCfg.includesTrack === true}
                    onChange={(event) => {
                      setStitchingRates((prev) => ({
                        ...prev,
                        [name]: {
                          ...prev[name],
                          includesTrack: event.target.checked,
                        },
                      }));
                      setIsStitchingSettingsDirty(true);
                    }}
                  />
                  This rate includes track/channel
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                  <input
                    type="checkbox"
                    checked={settingCfg.supportsBendedTrack === true}
                    onChange={(event) => {
                      setStitchingRates((prev) => ({
                        ...prev,
                        [name]: {
                          ...prev[name],
                          includesTrack: event.target.checked ? true : prev[name]?.includesTrack,
                          supportsBendedTrack: event.target.checked,
                          bendSettings: {
                            ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                            enabled: event.target.checked,
                          },
                        },
                      }));
                      setIsStitchingSettingsDirty(true);
                    }}
                  />
                  Allow bended track for this package
                </label>
              </div>

              {settingCfg.supportsBendedTrack ? (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800 }}>Bend Pricing Method</span>
                      <select
                        style={selectInput}
                        value={bendSettings.pricingMethod}
                        onChange={(event) => {
                          setStitchingRates((prev) => ({
                            ...prev,
                            [name]: {
                              ...prev[name],
                              bendSettings: {
                                ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                pricingMethod: event.target.value,
                                enabled: true,
                              },
                            },
                          }));
                          setIsStitchingSettingsDirty(true);
                        }}
                      >
                        {BENDED_TRACK_PRICING_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {bendSettings.pricingMethod === "fixed_per_bend" ? (
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 800 }}>Default bend charge / bend</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          style={input}
                          value={getInputValue(bendSettings.defaultBendCharge)}
                          onChange={(event) => {
                            setStitchingRates((prev) => ({
                              ...prev,
                              [name]: {
                                ...prev[name],
                                bendSettings: {
                                  ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                  defaultBendCharge: event.target.value,
                                  enabled: true,
                                },
                              },
                            }));
                            setIsStitchingSettingsDirty(true);
                          }}
                        />
                      </label>
                    ) : null}

                    {bendSettings.pricingMethod === "by_angle" ? (
                      ["90", "120", "135", "custom"].map((angle) => (
                        <label key={angle} style={{ display: "grid", gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 800 }}>
                            {angle === "custom" ? "Custom angle default" : `${angle} deg bend charge`}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={input}
                            value={getInputValue(bendSettings.angleCharges?.[angle])}
                            onChange={(event) => {
                              setStitchingRates((prev) => ({
                                ...prev,
                                [name]: {
                                  ...prev[name],
                                  bendSettings: {
                                    ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                    angleCharges: {
                                      ...((prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS).angleCharges || {}),
                                      [angle]: event.target.value,
                                    },
                                    enabled: true,
                                  },
                                },
                              }));
                              setIsStitchingSettingsDirty(true);
                            }}
                          />
                        </label>
                      ))
                    ) : null}
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>
                      Allowed Bend Types / Angles
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {BENDED_TRACK_SHAPES.filter((shape) => shape.shape !== "straight").map((shape) => (
                        <label
                          key={shape.shape}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800 }}
                        >
                          <input
                            type="checkbox"
                            checked={normalizeAllowedBendShapes(bendSettings.allowedAngles).includes(shape.shape)}
                            onChange={(event) => {
                              const current = normalizeAllowedBendShapes(
                                (stitchingRates[name]?.bendSettings || DEFAULT_BEND_SETTINGS).allowedAngles
                              );
                              const nextAllowed = event.target.checked
                                ? Array.from(new Set([...current, shape.shape]))
                                : current.filter((entry) => entry !== shape.shape);
                              setStitchingRates((prev) => ({
                                ...prev,
                                [name]: {
                                  ...prev[name],
                                  bendSettings: {
                                    ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                    allowedAngles: nextAllowed,
                                    enabled: true,
                                  },
                                },
                              }));
                              setIsStitchingSettingsDirty(true);
                            }}
                          />
                          {shape.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800 }}>Optional notes</span>
                    <input
                      style={input}
                      value={bendSettings.notes || ""}
                      placeholder="Charge extra for ceiling bend / bay window bend."
                      onChange={(event) => {
                        setStitchingRates((prev) => ({
                          ...prev,
                          [name]: {
                            ...prev[name],
                            bendSettings: {
                              ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                              notes: event.target.value,
                              enabled: true,
                            },
                          },
                        }));
                        setIsStitchingSettingsDirty(true);
                      }}
                    />
                  </label>
                </>
              ) : null}
            </div>
          </details>

          <Button
            icon={Trash2}
            variant="danger"
            disabled={isSavingSettings}
            onClick={async () => {
              if (!workspaceUid) {
                notify("Workspace is not ready. Please refresh and try again.", "warning");
                return;
              }

              try {
                const ref = doc(db, "users", workspaceUid, "settings", "main");

                await updateDoc(ref, {
                  [`stitching.${name}`]: deleteField(),
                });

                const updated = { ...stitchingRates };
                delete updated[name];
                setStitchingRates(updated);
                invalidateFirestoreReadCache(ref);
                mainSettingsLoadedWorkspaceRef.current = "";
                commitCurtainRows(
                  (prevRows) => prevRows.map((row) => ({ ...row })),
                  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY
                );
                setEffectiveRows((prevRows) => prevRows.map((row) => ({ ...row })));
                notify("Stitching / track style deleted.", "success");
              } catch (err) {
                devError("Delete stitching style failed:", err);
                notify("Failed to delete stitching / track style.", "error", 5200);
              }
            }}
          >
            Delete
          </Button>
        </div>
      );
    })}
    </div>

    <div
      style={{
        background: isDark ? "#151520" : "#ffffff",
        padding: 22,
        borderRadius: 20,
        border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
        boxShadow: isDark
          ? "0px 18px 40px rgba(0,0,0,0.35)"
          : "0px 14px 32px rgba(15,23,42,0.06)",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
        Add New Stitching / Track Style
      </h3>
      <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
        Add a reusable style with its running-foot rate, fabric fullness, and fallback
        track length.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Style Name</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="Ripple - Motorized"
            value={newStitch}
            onChange={(e) => setNewStitch(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Rate / Running ft (₹)</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="200"
            type="number"
            min="0"
            step="0.01"
            value={newRate === null || newRate === undefined ? "" : newRate}
            onChange={(e) => setNewRate(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Fullness Multiplier</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="3"
            type="number"
            min="0.01"
            step="0.01"
            value={
              newFullness === null || newFullness === undefined
                ? ""
                : newFullness
            }
            onChange={(e) => setNewFullness(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Default Track Length (ft)</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="16"
            type="number"
            min="0"
            step="0.01"
            value={
              newTrackLengthFt === null || newTrackLengthFt === undefined
                ? ""
                : newTrackLengthFt
            }
            onChange={(e) => setNewTrackLengthFt(e.target.value)}
          />
        </label>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 18,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Button
          icon={Plus}
          variant="success"
          disabled={isSavingSettings}
          onClick={() => {
            const name = String(newStitch || "").trim();
            const rate = parseStitchingSettingNumber(newRate, 0);
            const fullness = parseStitchingSettingNumber(newFullness, Number.NaN);
            const trackLengthFt = parseStitchingSettingNumber(newTrackLengthFt, 0);

            if (!name) {
              notify("Style name is required.", "error");
              return;
            }
            if (!Number.isFinite(rate) || rate < 0) {
              notify("Rate / Running ft must be 0 or greater.", "error");
              return;
            }
            if (!Number.isFinite(fullness) || fullness <= 0) {
              notify("Fullness Multiplier must be greater than 0.", "error");
              return;
            }
            if (!Number.isFinite(trackLengthFt) || trackLengthFt < 0) {
              notify("Default Track Length must be 0 or greater.", "error");
              return;
            }

            setStitchingRates((prev) => ({
              ...prev,
              [name]: {
                ...(prev[name] || {}),
                rate,
                fullness,
                trackLengthFt,
                trackRateFt: parseStitchingSettingNumber(prev[name]?.trackRateFt, 0),
              },
            }));
            setIsStitchingSettingsDirty(true);
            setNewStitch("");
            setNewRate("");
            setNewFullness("3");
            setNewTrackLengthFt("16");
          }}
        >
          + Add Stitching / Track Style
        </Button>

        <Button
          icon={Save}
          variant="primary"
          disabled={isSavingSettings}
          onClick={() =>
            saveSettings({ successMessage: "Stitching & track settings saved." })
          }
        >
          {isSavingSettings ? "Saving..." : "Save Stitching & Track Settings"}
        </Button>
      </div>
    </div>
  </div>
)}

{tab === "fco" && (
  <div
    style={{
      background: isDark ? "#0f172a" : "#ffffff",
      padding: 26,
      borderRadius: 16,
      maxWidth: 1100,
      margin: "0 auto",
      boxShadow: isDark
        ? "0 12px 40px rgba(0,0,0,0.6)"
        : "0 10px 30px rgba(2,6,23,0.06)",
    }}
  >

    {/* ================= HEADER ================= */}

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
      }}
    >
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>
          Roll Optimizer
        </div>
        <div style={{ fontSize: 13, opacity: 0.6 }}>
          Panel-based fabric cutting optimization
        </div>
        <div style={{ fontSize: 12, marginTop: 6 }}>
          Planning only. Saving, optimizing and exporting do not reserve or deduct stock. Stock commitment and reversal are not provided by this release.
        </div>
      </div>

            <div style={{ display: "flex", gap: 10 }}>
        <Button
          icon={RotateCcw}
          variant="ghost"
          onClick={() => {
            setOptimizerQuoteId("");
            setOptimizerQuote(null);
            setOptimizerFabric("");
            setRollOptions([]);
            setCutPlan([]);
            setOptimizerFinalDemands([]);
            setSelectedSolutionIndex(0);
            setOptimizerQuoteSearch("");
            setOptimizerQuoteSearchState({
              loading: false,
              error: "",
              results: [],
              searched: false,
            });
          }}
        >
          Reset
        </Button>

        <Button
          icon={Download}
          variant="success"
          disabled={
            !cutPlan.length ||
            !cutPlan[0]?.solutions?.[selectedSolutionIndex]
          }
          onClick={() => {
            const sol =
              cutPlan?.[0]?.solutions?.[selectedSolutionIndex];

            if (!sol) return;

            exportCuttingPDF(
              sol,
              optimizerResultSourceRef.current?.quote || optimizerQuote,
              optimizerResultSourceRef.current?.fabricKey || "",
              optimizerFinalDemands
            );
          }}
        >
          Download Selected
        </Button>
      </div>
    </div>

    <div
      style={{
        padding: 20,
        borderRadius: 22,
        marginBottom: 20,
        background: isDark
          ? "radial-gradient(circle at top left, rgba(37,99,235,0.24), transparent 34%), linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.94))"
          : "radial-gradient(circle at top left, rgba(37,99,235,0.13), transparent 34%), linear-gradient(135deg, #ffffff, #f8fafc)",
        border: isDark
          ? "1px solid rgba(148,163,184,0.18)"
          : "1px solid rgba(37,99,235,0.12)",
        boxShadow: isDark
          ? "0 22px 55px rgba(0,0,0,0.38)"
          : "0 22px 55px rgba(15,23,42,0.08)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1.1fr 1fr", gap: 18 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Start by loading a quotation
          </div>
          <div style={{ marginTop: 8, fontSize: isCompact ? 21 : 26, fontWeight: 950, letterSpacing: "-0.04em" }}>
            Optimize from a real saved curtain quote
          </div>
          <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7, color: isDark ? "#cbd5e1" : "#475569" }}>
            To optimize fabric cutting, first open the quotation in the editor, then come here. You can also search below and load a saved quotation directly.
          </div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {[
            "Search customer / quote number",
            "Select the correct quote",
            "Choose fabric",
            "Add available roll lengths",
            "Optimize cutting",
          ].map((step, index) => (
            <div
              key={step}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 11px",
                borderRadius: 14,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.72)",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.12)"
                  : "1px solid rgba(15,23,42,0.06)",
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {index + 1}
              </span>
              <span style={{ fontSize: 13, fontWeight: 750 }}>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ================= MAIN GRID ================= */}

    <div
      style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) 360px",
        gap: 20,
      }}
    >

      {/* ================= LEFT COLUMN ================= */}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Quote Search */}

        <div
          style={{
            padding: 18,
            borderRadius: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #f8fafc, #ffffff)",
            border: isDark
              ? "1px solid rgba(148,163,184,0.18)"
              : "1px solid rgba(15,23,42,0.08)",
            boxShadow: isDark
              ? "0 18px 45px rgba(0,0,0,0.35)"
              : "0 18px 45px rgba(15,23,42,0.08)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 850 }}>Find a saved quotation</div>
              <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", marginTop: 4 }}>
                Search is debounced and only loads full quotation details after you select a result.
              </div>
            </div>
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 800,
                alignSelf: "start",
                whiteSpace: "nowrap",
              }}
            >
              Low-read search
            </div>
          </div>

          {currentEditorOptimizerQuote ? (
            <div
              style={{
                padding: 14,
                borderRadius: 16,
                background: isDark ? "rgba(34,197,94,0.10)" : "#ecfdf5",
                border: isDark
                  ? "1px solid rgba(34,197,94,0.24)"
                  : "1px solid rgba(34,197,94,0.18)",
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", marginBottom: 5 }}>
                Currently loaded quotation
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 850 }}>
                    #{currentEditorOptimizerQuote.quoteNo || currentEditorOptimizerQuote.id} · {currentEditorOptimizerQuote.customerName || "Customer"}
                  </div>
                  <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#475569", marginTop: 3 }}>
                    {Array.isArray(currentEditorOptimizerQuote.rows)
                      ? `${currentEditorOptimizerQuote.rows.length} row${currentEditorOptimizerQuote.rows.length === 1 ? "" : "s"} ready`
                      : "Quotation rows ready"}
                  </div>
                </div>
                <Button
                  variant="success"
                  onClick={() => {
                    const nextQuote = cloneOptimizerQuoteForState(currentEditorOptimizerQuote);
                    optimizerUsesLiveRef.current = true;
                    setOptimizerQuote(nextQuote);
                    setOptimizerQuoteId(nextQuote.id || "");
                    setOptimizerFabric("");
                    setRollOptions([]);
                    setCutPlan([]);
                    setOptimizerFinalDemands([]);
                    setSelectedSolutionIndex(0);
                  }}
                >
                  Use Loaded Quote
                </Button>
              </div>
            </div>
          ) : null}
          {optimizerUsesLiveRef.current && effectiveOptimizerQuote?.id === currentEditorOptimizerQuote?.id ? <>
            <QuoteConstructionControls key={`optimizer-fullness-${editingQuoteId || "new"}`} fullness={quoteFullnessOverrideRows}
              onFullness={updateQuoteFullnessOverride} />
            <div style={{ fontSize: 12 }}>Fullness changes also update the quotation. Cutting plans are cleared when fullness or hems change; run Optimize Cutting again.</div>
            {getQuoteHemControlRows((effectiveOptimizerQuote?.rows || []).filter(row => !optimizerFabric || optimizerFabricIdentity(row, getCurtainRowFabricKey) === optimizerFabric)).map(fabric => <p key={fabric.key}>
              {fabric.label}: top {fabric.effective.topHemIn} in, bottom {fabric.effective.bottomHemIn} in, each side {fabric.effective.sideAllowancePerSideIn} in
              {fabric.effective.noBottomHem ? " - NO BOTTOM HEM" : ""}. Edit hems in Fabric Summary.
            </p>)}
          </> : null}
          {effectiveOptimizerQuote && !optimizerUsesLiveRef.current ? <p>To change this quotation's fullness, open it in the quotation editor first, then choose Use Loaded Quote here.</p> : null}
          <input
            value={optimizerQuoteSearch}
            onChange={(event) => setOptimizerQuoteSearch(event.target.value)}
            style={{ ...input, width: "100%" }}
            placeholder="Search customer, quote number, phone, or project..."
          />

          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {optimizerQuoteSearch.trim().length > 0 && optimizerQuoteSearch.trim().length < 2 ? (
              <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
                Type at least 2 characters to search saved quotations.
              </div>
            ) : null}

            {optimizerQuoteSearchState.loading ? (
              <div style={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#475569" }}>
                Searching quotations...
              </div>
            ) : null}

            {optimizerQuoteSearchState.error ? (
              <div style={{ fontSize: 13, color: "#ef4444" }}>
                {optimizerQuoteSearchState.error}
              </div>
            ) : null}

            {!optimizerQuoteSearchState.loading &&
            optimizerQuoteSearchState.searched &&
            optimizerQuoteSearchState.results.length === 0 ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 14,
                  background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.15)"
                    : "1px solid rgba(15,23,42,0.08)",
                  color: isDark ? "#cbd5e1" : "#475569",
                  fontSize: 13,
                }}
              >
                No curtain quotations found for this search. Try quote number, customer name, or phone.
              </div>
            ) : null}

            {optimizerQuoteSearchState.results.map((result) => (
              <button
                key={result.id}
                type="button"
                onClick={() => loadOptimizerQuoteById(result.id)}
                disabled={optimizerQuoteLoadingId === result.id}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  border:
                    optimizerQuoteId === result.id
                      ? "1px solid rgba(37,99,235,0.45)"
                      : isDark
                      ? "1px solid rgba(148,163,184,0.15)"
                      : "1px solid rgba(15,23,42,0.08)",
                  background:
                    optimizerQuoteId === result.id
                      ? isDark
                        ? "rgba(37,99,235,0.18)"
                        : "#eff6ff"
                      : isDark
                      ? "rgba(15,23,42,0.72)"
                      : "#ffffff",
                  color: isDark ? "#f8fafc" : "#0f172a",
                  textAlign: "left",
                  cursor: optimizerQuoteLoadingId === result.id ? "wait" : "pointer",
                  display: "grid",
                  gap: 7,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <strong>#{result.quoteNo || result.id}</strong>
                  <span style={{ fontSize: 12, color: "#2563eb", fontWeight: 800 }}>
                    {optimizerQuoteLoadingId === result.id ? "Loading..." : getQuoteTypeLabel(result.quoteType)}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 750 }}>
                  {result.customerName || "Customer"}
                </div>
                <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
                  {[result.customerPhone, result.projectName, result.date]
                    .filter(Boolean)
                    .join(" · ") || "No extra details"}
                </div>
                {result.grandTotal ? (
                  <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#334155" }}>
                    Total: {currency} {Number(result.grandTotal || 0).toLocaleString("en-IN")}
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {optimizerQuote ? (
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
              border: isDark
                ? "1px solid rgba(37,99,235,0.24)"
                : "1px solid rgba(37,99,235,0.16)",
              display: "grid",
              gap: 7,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Selected quotation
            </div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              #{optimizerQuote.quoteNo || optimizerQuote.id} · {optimizerQuote.customerName || "Customer"}
            </div>
            <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#475569" }}>
              {[
                optimizerQuote.date || optimizerQuote.quoteDate,
                optimizerQuote.customerPhone || optimizerQuote.customerWhatsapp,
                Array.isArray(optimizerQuote.rows)
                  ? `${optimizerQuote.rows.length} row${optimizerQuote.rows.length === 1 ? "" : "s"}`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
              border: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid rgba(15,23,42,0.08)",
              color: isDark ? "#cbd5e1" : "#475569",
              fontSize: 13,
              lineHeight: 1.7,
            }}
          >
            No quotation selected yet. Use the loaded editor quote or search a saved curtain quotation above.
          </div>
        )}

        {/* Fabric Selector */}

        <div>
          <label>Select Fabric</label>

          <select
            value={optimizerFabric || ""}
            disabled={!optimizerQuote}
            onChange={(e) => {
              setOptimizerFabric(e.target.value);
              setCutPlan([]);
              setOptimizerFinalDemands([]);
            }}
            style={{ ...selectInput, marginTop: 6 }}
          >

            <option value="">Choose fabric...</option>

            {optimizerFabricOptions.map((fab) => (
                <option key={fab.key} value={fab.key}>
                  {[fab.brand, fab.label].filter(Boolean).join(" / ")}
                </option>
              ))}

          </select>
          {optimizerQuote && !optimizerFabricOptions.length ? (
            <div style={{ marginTop: 8, fontSize: 12, color: "#ef4444" }}>
              This quotation has no fabric demand rows for optimizer yet.
            </div>
          ) : null}
        </div>

        {/* Rolls */}

        <div>

          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Usable physical rolls (meters; one entry per roll)
          </div>

          {rollOptions.map((len, i) => (

            <div
              key={i}
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8
              }}
            >

              <input
                type="number"
                step="0.01"
                value={len}
                aria-label={`Roll length ${i + 1}`}
                onChange={(e) => {

                  const v = [...rollOptions];
                  v[i] = e.target.value;

                  setRollOptions(v);
                  setCutPlan([]);
                  setOptimizerFinalDemands([]);

                }}
                style={{ ...input, flex: 1 }}
              />

              <Button
                icon={Trash2}
                variant="danger"
                onClick={() => {
                  setRollOptions(
                    rollOptions.filter((_, idx) => idx !== i)
                  );

                  setCutPlan([]);
                  setOptimizerFinalDemands([]);
                }}
              >
                Remove
              </Button>

            </div>

          ))}

          <Button
            icon={Plus}
            variant="primary"
            onClick={() => {
              setRollOptions([...rollOptions, ""]);
              setCutPlan([]);
              setOptimizerFinalDemands([]);
            }}
          >
            Add Roll
          </Button>

        </div>

    <label style={{ display: "block", marginTop: 10 }}>
      Supplier ordering increment (m; 0 = exact)
      <input type="number" min="0" step="0.01" value={supplierRollIncrement}
        onChange={e => { setSupplierRollIncrement(e.target.value); setCutPlan([]); setOptimizerFinalDemands([]); }}
        style={input} />
    </label>
    {/* ================= OPTIMIZE BUTTON ================= */}
<Button
  icon={Scissors}
  variant="success"
  style={{ marginTop: 10 }}
  onClick={() => {

    if (!optimizerQuote || !optimizerFabric) {
      alert("Select quote and fabric");
      return;
    }

    const demandInput = getCurrentOptimizerDemand();
    const { demands, rows: optimizerRows, fabricKey,
      repeatSettings: activeOptimizerRepeatSettings } = demandInput;
    devLog("ROLL_OPTIMIZER_DEMAND_TRACE", { ...demandInput.trace,
      optimizerInputSource: optimizerUsesLiveRef.current ? "live_editor" : "selected_saved_quote" });
    if (demandInput.failureReason) {
      alert(demandInput.failureReason);
      return;
    }

    setOptimizerFinalDemands(demands);

    const cleanRolls =
      rollOptions
        .map((x, i) => ({ id: `roll-${i + 1}`, usableLengthMeters: Number(x) }))
        .filter((r) => Number.isFinite(r.usableLengthMeters) && r.usableLengthMeters > 0);

    if (!cleanRolls.length) {
      alert("Enter valid roll lengths.");
      return;
    }

    const result =
      optimizeCutsGGMulti({
        rolls: cleanRolls,
        demands,
        fabricKey,
        sourceRows: optimizerRows,
        activeRepeatSettings: activeOptimizerRepeatSettings,
        supplierIncrementMeters: Math.max(0, Number(supplierRollIncrement) || 0),
        manualWarnings: demandInput.manualWarnings,
      });

    devLog("DEMANDS:", demands);
    devLog("ROLLS:", cleanRolls);
    devLog("OPTIMIZER RESULT:", result);

    const rawSolutions =
      Array.isArray(result)
        ? result
        : result?.solutions || [];

    const normalizedSolutions =
      rawSolutions
        .filter(Boolean)
        .map((sol) => {

          if (sol?.rollResults) return sol;

          if (
            sol?.patternInfo &&
            Array.isArray(sol?.comboRolls) &&
            sol.comboRolls.length
          ) {

            const rollResults =
              sol.comboRolls.map((rollLen) => ({
                rollLen,
                patternInfo: {
                  pattern:
                    sol.patternInfo?.pattern || {},
                  used:
                    sol.patternInfo.used || 0,
                  waste:
                    sol.patternInfo.waste || 0
                }
              }));

            return {
              ...sol,
              rollResults
            };
          }

          return {
            ...sol,
            rollResults: []
          };

        })
        .map((sol) => {
          // Drop rounding and the separate per-roll reserve must not be added twice.
          const vrPenalty = Number(sol.alignmentAllowanceMeters || 0);
          const effectiveUsed =
            Number(sol.totalUsedMeters || 0);

          return {
            ...sol,
            optimizerExtraVrMeters: vrPenalty,
            effectiveUsedMeters: effectiveUsed,
            requiresExtraCut:
              Boolean(sol?.requiresExtraCut) ||
              Number(sol?.finalRemainingMeters || 0) > 0,
            isCompleteFromAvailable:
              sol?.isCompleteFromAvailable === true &&
              Number(sol?.finalRemainingMeters || 0) <= 0,
            totalWasteMeters: Number(sol?.totalWasteMeters ?? sol?.totalWaste ?? 0),
            rollCount: Number(sol?.rollCount ?? sol?.totalRollsUsed ?? 0),
            totalRollLengthMeters: Number(
              sol?.totalRollLengthMeters ??
              (Array.isArray(sol?.comboRolls)
                ? sol.comboRolls.reduce(
                    (sum, rollLen) => sum + Number(rollLen || 0),
                    0
                  ) + Number(sol?.finalRemainingMeters || 0)
                : 0)
            ),
          };
        });

    //  REMOVE DUPLICATES (STABLE KEY)
    const seen = new Set();

    const uniqueSolutions = normalizedSolutions.filter((s) => {
      const key = JSON.stringify({
        rolls: s.rollResults.map(r => [r.rollId, r.allocations]),
        waste: Number(s.totalWaste || 0).toFixed(2),
        remaining: Number(s.finalRemainingMeters || 0).toFixed(2),
      });

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    uniqueSolutions.sort(compareOptimizerSolutions);

    if (!uniqueSolutions.length) {
      setCutPlan([]);
      alert("The available rolls cannot fit these cutting panels. Add a longer roll or more fabric.");
      return;
    }
    optimizerResultSourceRef.current = { quote: { ...effectiveOptimizerQuote, rows: optimizerRows }, fabricKey };

    setSelectedSolutionIndex(0);

    setCutPlan([
      {
        solutions: uniqueSolutions
      }
    ]);

  }}
>
  Optimize Cutting
</Button>

      </div>

      {/* ================= RIGHT COLUMN ================= */}

     <div
  style={{
    padding: 16,
    borderRadius: 12,
    background: isDark ? "#0b1220" : "#f8fafc",
  }}
>

  <div style={{ fontWeight: 800, marginBottom: 8 }}>
    Demand Summary
  </div>

  {optimizerQuote && optimizerFabric ? (

    (() => {

      let total = 0;
      let panels = 0;

      const { plan, failureReason } = getCurrentOptimizerDemand();

      total = round2(
        (plan.demands || []).reduce(
          (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
          0
        )
      );

      panels = plan.rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.totalPieces || 0), 0);

      const reusedPieces = plan.rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.reusedHalfCount || 0), 0);

      return (
        <>
          <div>
            Total Required: {total.toFixed(2)} m
          </div>
          <div>
            Panels: {panels}
          </div>
          <div>
            Reused pieces: {reusedPieces}
          </div>
          {failureReason ? <div role="alert" style={{ color: "#b45309", marginTop: 8 }}>{failureReason}</div> : null}
        </>
      );

    })()

  ) : (
    <div>Select quote and fabric</div>
  )}

</div>
    </div>
{/* ================= RESULTS ================= */}

{cutPlan?.[0]?.solutions?.length > 0 && (

  <div style={{ marginTop: 30 }}>

    <h3 style={{ marginBottom: 12 }}>
      Optimization Results  {optimizerFabricOptions.find(option => option.key === optimizerFabric)?.label || ""}
    </h3>

    {[...cutPlan[0].solutions]
      .slice(0, 3)
      .map((sol, idx) => {

        const rollResults = sol.rollResults || [];
        const requiresExtraCut =
          Boolean(sol?.requiresExtraCut) ||
          Number(sol?.finalRemainingMeters || 0) > 0;
        const rollCount = Number(sol?.rollCount ?? sol?.totalRollsUsed ?? 0);
        const totalRollLengthMeters = Number(
          sol?.totalRollLengthMeters ??
          (Array.isArray(sol?.comboRolls)
            ? sol.comboRolls.reduce(
                (sum, rollLen) => sum + Number(rollLen || 0),
                0
              ) + Number(sol?.finalRemainingMeters || 0)
            : 0)
        );

        //  SAFE USED CALCULATION (from pattern, not stored value)
        const getUsed = (pattern = {}) =>
          Object.entries(pattern).reduce(
            (s, [len, qty]) => s + Number(len) * Number(qty),
            0
          );

        const totalWaste = rollResults.reduce(
          (s, r) => s + (r?.patternInfo?.waste || 0),
          0
        );

        const totalUsed = rollResults.reduce(
          (s, r) =>
            s + getUsed(r?.patternInfo?.pattern || {}),
          0
        );

        const remainingMeters =
          sol.finalRemainingMeters || 0;

        return (

          <div
            key={idx}
            style={{
              border:
                selectedSolutionIndex === idx
                  ? "2px solid #22c55e"
                  : "1px solid #ddd",
              padding: 14,
              borderRadius: 10,
              marginBottom: 14,
              background:
                selectedSolutionIndex === idx
                  ? "rgba(34,197,94,0.06)"
                  : "transparent",
            }}
          >

            {/* HEADER */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >

              <strong>Option {idx + 1}</strong>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: requiresExtraCut ? "#fef3c7" : "#dcfce7",
                    color: requiresExtraCut ? "#92400e" : "#166534",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {sol.manualReviewRequired ? "Manual Verification Required" : requiresExtraCut ? "Requires Extra Cut" : "Capacity Fits - Workshop Alignment Required"}
                </span>

              <button
                style={btn(
                  selectedSolutionIndex === idx
                    ? "#22c55e"
                    : "#9ca3af"
                )}
                onClick={() => setSelectedSolutionIndex(idx)}
              >
                {selectedSolutionIndex === idx
                  ? "Selected"
                  : "Select"}
              </button>
              </div>

            </div>

            {/* ROLLS */}
            {rollResults.map((rr, i) => {

              const pattern =
                rr?.patternInfo?.pattern || {};

              const used = getUsed(pattern);
              const waste =
                rr?.patternInfo?.waste || 0;

              return (

                <div key={i} style={{ marginBottom: 6 }}>

                  <strong>
                    {rr.rollId || `Roll ${i + 1}`} - {rr?.rollLen}m | Cuts{" "}
                    {used.toFixed(4)}m | Reserve {Number(rr.alignmentReserveMeters || 0).toFixed(4)}m | Residual{" "}
                    {waste.toFixed(2)}m
                  </strong>

                  <div style={{ marginLeft: 12 }}>

                    <div>Pattern reference roll: {[...new Set((rr.allocations || []).map(a => a.referenceRollId))].join(', ') || 'manual check'}</div>

                    {Object.entries(pattern).map(
                      ([len, qty]) => (

                        <div key={len}>
                           {Number(len).toFixed(2)}m  {qty}
                        </div>

                      )
                    )}

                  </div>

                </div>

              );

            })}

            {/* SUMMARY */}
            <div style={{ marginTop: 10, fontSize: 13 }}>

              <div>
                <strong>Total Used:</strong>{" "}
                {totalUsed.toFixed(2)} m
              </div>

              <div>
                <strong>Residual on allocated rolls:</strong>{" "}
                {totalWaste.toFixed(2)} m
              </div>

              <div>
                <strong>Roll Count:</strong>{" "}
                {rollCount}
              </div>

              <div>
                <strong>Total Roll Length:</strong>{" "}
                {totalRollLengthMeters.toFixed(2)} m
              </div>

              <div>
                <strong>{requiresExtraCut ? "Extra Cut Required" : "Remaining" }:</strong>{" "}
                {remainingMeters.toFixed(2)} m
              </div>

              {rollAlignmentSummaryLines(sol).map((line, index) => <div key={index} style={{ marginTop: 5 }}>{line}</div>)}

            </div>

          </div>

        );

      })}

    {(() => {
      const selectedSolution =
        cutPlan?.[0]?.solutions?.[selectedSolutionIndex] || null;
      const visualRolls = buildRollVisualization(
        selectedSolution?.rollResults
      );

      return <RollVisualizer rolls={visualRolls} />;
    })()}

  </div>

)}

 </div> )}


{/* ====================== STOCK TAB ====================== */}
{tab === "stock" && (
  <div
    style={{
      background: isDark ? "#0f172a" : "#ffffff",
      padding: 28,
      borderRadius: 18,
      maxWidth: 1150,
      margin: "0 auto",
      boxShadow: isDark
        ? "0 14px 50px rgba(0,0,0,0.65)"
        : "0 12px 32px rgba(2,6,23,0.08)",
    }}
  >
    {/* ================= HEADER ================= */}
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 24, fontWeight: 800 }}>
        Fabric Stock
      </div>
      <div style={{ fontSize: 13, color: "#6b7280" }}>
        Legacy stock viewer. Quotation plans do not reserve or consume inventory.
      </div>
    </div>

    {/* ================= ADD STOCK CARD ================= */}
    <div
      style={{
        background: isDark ? "#0b1220" : "#f9fafb",
        borderRadius: 16,
        padding: 22,
        marginBottom: 30,
        boxShadow: isDark
          ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
          : "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 14 }}>
        Add Fabric to Stock
      </div>

      {/* QUOTATION SELECT */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>
          Quotation / Stock Purpose
        </label>
        <select
          value={stockQuoteId}
          onChange={(e) => setStockQuoteId(e.target.value)}
          style={{ ...selectInput, marginTop: 6 }}
        >
          <option value="">Select quotation</option>
          <option value="__GENERAL__">
            General / Bulk Stock
          </option>
          {savedQuotes
            .filter((q) => resolveQuoteType(q) !== GENERAL_QUOTE_TYPE)
            .map((q) => (
            <option key={q.id} value={q.id}>
              {buildSavedQuoteCardTitle(q)}
            </option>
          ))}
        </select>
      </div>

      {/* FABRIC NAME */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>Fabric Name</label>
        <input
          type="text"
          value={stockFabricName}
          onChange={(e) => setStockFabricName(e.target.value)}
          placeholder="Eg: Chroma Aqua, Milano Sheer"
          style={{ ...input, marginTop: 6 }}
        />
      </div>

      {/* METERS */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>
          Purchased Fabric
        </label>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 6,
          }}
        >
          <input
            type="text"
            placeholder="Rolls (e.g. 84, 42)"
            value={stockRollMeters}
            onChange={(e) => setStockRollMeters(e.target.value)}
            style={{ ...input, maxWidth: 260 }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="Loose (m)"
            value={stockLooseMeters}
            onChange={(e) => setStockLooseMeters(e.target.value)}
            style={{ ...input, maxWidth: 160 }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="Wastage (m)"
            value={stockWastageMeters}
            onChange={(e) => setStockWastageMeters(e.target.value)}
            style={{ ...input, maxWidth: 160 }}
          />
        </div>
      </div>

      <div style={{ textAlign: "right" }}>
        <button
          style={{
            ...btn("#2563eb"),
            padding: "10px 20px",
            fontWeight: 700,
          }}
onClick={async () => {
  try {
    if (!user?.uid) throw new Error("Not logged in");
    if (!stockFabricName.trim())
      throw new Error("Enter fabric name");

    const fabricKey = stockFabricName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    const rolls = String(stockRollMeters || "")
      .split(",")
      .map((r) => Number(r.trim()))
      .filter((n) => n > 0);

    const loose = Number(stockLooseMeters) || 0;
    const wastage = Number(stockWastageMeters) || 0;

    const totalMeters =
      rolls.reduce((a, b) => a + b, 0) +
      loose -
      wastage;

    if (totalMeters <= 0)
      throw new Error("Enter valid fabric meters");

    await applyStockTransaction({
      uid: workspaceUid,
      fabricKey,
      meters: totalMeters,
      type: "PURCHASE",
      quoteId:
        stockQuoteId === "__GENERAL__"
          ? null
          : stockQuoteId,
    });

    setStockFabricName("");
    setStockRollMeters("");
    setStockLooseMeters("");
    setStockWastageMeters("");
    setStockQuoteId("");

    alert("Stock added successfully");
  } catch (err) {
    alert(err.message);
  }
}}


        >
           Add to Stock
        </button>
      </div>
    </div>

   {/* ================= FABRIC STOCK VIEWER ================= */}
<div
  style={{
    background: "#ffffff",
    borderRadius: 16,
    padding: 20,
    marginBottom: 30,
    border: "1px solid #e5e7eb",
  }}
>
  {/* Header */}
  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
    Current Stock
  </div>

  {/*  Search */}
  <input
    type="text"
    placeholder="Search fabric name"
    value={stockSearch}
    onChange={(e) => setStockSearch(e.target.value)}
    style={{
      width: "100%",
      padding: "10px 12px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      marginBottom: 16,
      fontSize: 14,
    }}
  />

  {loadingFabricStock ? (
    <div style={{ opacity: 0.6 }}>Loading stock</div>
  ) : filteredFabricStock.length === 0 ? (
    <div style={{ opacity: 0.6 }}>No matching fabrics found.</div>
  ) : (
    filteredFabricStock.map((fab) => {

      const buckets = Array.isArray(fab.buckets)
        ? fab.buckets.filter((b) => Number(b.length) !== 0)
        : [];

      if (!buckets.length) return null;

      const rolls = buckets.filter((b) => b.type === "ROLL");
      const leftovers = buckets.filter((b) => b.type === "LEFTOVER");
      const negatives = buckets.filter((b) => b.type === "NEGATIVE");

      const totalRolls = rolls.reduce((a, b) => a + Number(b.length || 0), 0);
      const totalLeftovers = leftovers.reduce((a, b) => a + Number(b.length || 0), 0);
      const totalNegative = negatives.reduce((a, b) => a + Number(b.length || 0), 0);

      const totalAvailable = totalRolls + totalLeftovers;
      const netStock = totalAvailable + totalNegative;

      return (
        <div
          key={fab.fabricKey}
          style={{
            marginBottom: 22,
            padding: 18,
            borderRadius: 16,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* Fabric Name */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {(fab.displayName || fab.fabricKey)
                .toString()
                .replace(/_/g, " ")}
            </div>

            <div style={{ fontSize: 13, marginTop: 4 }}>
              Available:{" "}
              <strong>{totalAvailable.toFixed(2)} m</strong>
              &nbsp; | &nbsp;
              Negative:{" "}
              <strong style={{ color: "#dc2626" }}>
                {totalNegative.toFixed(2)} m
              </strong>
              &nbsp; | &nbsp;
              Net:{" "}
              <strong>{netStock.toFixed(2)} m</strong>
            </div>
          </div>

          {/* Rolls */}
          {rolls.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Rolls
              </div>
              {rolls.map((r, i) => (
                <div key={i} style={{ fontSize: 13 }}>
                   {Number(r.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}

          {/* Leftovers */}
          {leftovers.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Leftovers
              </div>
              {leftovers.map((l, i) => (
                <div key={i} style={{ fontSize: 13 }}>
                   {Number(l.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}

          {/* Negative */}
          {negatives.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#dc2626",
                }}
              >
                Negative Balance
              </div>
              {negatives.map((n, i) => (
                <div
                  key={i}
                  style={{ fontSize: 13, color: "#dc2626" }}
                >
                   {Number(n.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })
  )}
</div>

    {/* ================= STOCK HISTORY (LEDGER STYLE) ================= */}
    <div
      style={{
        background: "#ffffff",
        borderRadius: 18,
        padding: 20,
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700 }}>
        Stock History
      </div>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
        Immutable audit trail
      </div>

      <input
        type="text"
        placeholder="Search fabric / customer / quote"
        value={stockHistorySearch}
        onChange={(e) => setStockHistorySearch(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #d1d5db",
          marginBottom: 14,
        }}
      />

      {loadingStockHistory ? (
        <div>Loading history</div>
      ) : (
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "#6b7280", textAlign: "left" }}>
              <th>Date</th>
              <th>Fabric</th>
              <th>Action</th>
              <th>Meters</th>
              <th>Customer</th>
              <th>Quote</th>
            </tr>
          </thead>
          <tbody>
            {filteredStockHistory.flatMap((h) =>
              (h.fabrics || []).map((f, i) => (
                <tr key={`${h.id}-${i}`}>
                  <td>{h.triggeredAt?.toDate?.().toLocaleString()}</td>
                  <td>{f.fabricKey.replace(/_/g, " ")}</td>
                  <td
                    style={{
                      fontWeight: 700,
                      color:
                        h.direction === "DEDUCT"
                          ? "#dc2626"
                          : h.direction === "REVERSE"
                          ? "#16a34a"
                          : "#2563eb",
                    }}
                  >
                    {h.direction}
                  </td>
                  <td>{Number(f.meters).toFixed(2)}</td>
                  <td>{h.customerName || "-"}</td>
                  <td>{h.quoteNo || h.quoteId || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  </div>
)}

      </main>
      {renderDraftRecoveryModal()}
      {renderFabricPhotoQrModal()}
      {renderConfirmDialog()}
      {renderDashboardDrilldownModal()}
      {renderQuoteDealChoiceModal()}
      {renderQuoteSentPromptModal()}
      {renderCrmPaymentModal()}
      {renderPaymentFollowUpModal()}
      {renderCrmMaterialModal()}
      {renderCrmInstallationModal()}
      {renderCrmSnagModal()}
      {renderOrderHandoverModal()}
      {renderCrmFollowUpModal()}
      {renderDailyTaskRescheduleModal()}
      {renderQuoteLinkModal()}
      {renderCustomerLedgerEditModal()}
      {renderCustomerLedgerQuoteLinkModal()}
      {renderCustomerLedgerQuoteLinkConfirmModal()}
      {renderCustomerCreateModal()}
      {renderReferralPartnerModal()}
      {renderAppToasts()}
      {renderCrmReminderPopups()}
      {renderTeamMemberInviteModal()}
    </div>
  );
};











